/* =========================================================================
   configurator-ui.js
   Shared UI layer for LEVA configurators.

   Contains, in this order:
     1. UI widgets  -- slider, drag handles, dimension formatting   (ES5)
     2. map-manager -- MapManager (Mapbox) + ConfiguratorStage      (ES6)

   Part 2 is assets/map-manager.js pasted in VERBATIM, not rewritten. It ships
   to production today and works; retyping 1000 lines of ES6 into ES5 would
   risk a regression in the one thing that holds the viewport together, with no
   way to test it. The two IIFEs sit side by side: separate scopes, no
   interleaving, no shared names. ConfiguratorStage and MapManager are exported
   exactly as before, so any consumer sees no difference.

   Mixed ES5/ES6 in one file is the deliberate price of a byte-identical merge.

   MIGRATION: a configurator on this asset must NOT also load map-manager.js --
   it would define ConfiguratorStage twice. Configurators still on map-manager.js
   are untouched and keep working; that file stays until the last one moves.
   ========================================================================= */

(function (global) {
  'use strict';

  /* ---------------------------------------------------------------------
     configurator-ui.js
     Shared UI widgets for all LEVA configurators.

     Loads AFTER map-manager.js and BEFORE the configurator lib. It has no
     dependency on ConfiguratorStage today, but it will once drag handles and
     dimension sprites move in here, so the order is fixed from the start.

     Design rule that differs from the current bench pattern: every widget
     builds its own markup and injects its own CSS. The bench keeps its slider
     markup in configurator-bench.liquid, which means adding a widget is a
     two-file change -- across thirteen configurators that is thirteen liquids.
     Here a lib asks for a slider in one call and the liquid never learns it
     exists.

     Contract with the host (same as map-manager.css):
       - a container with id="cf-root"
       - Montserrat available
     --------------------------------------------------------------------- */

  var VERSION = 'ui-v419-fl';   /* ui-v419: core-v817, a storey's floor build-up over its deck (__PROD._floorLift, H.upLift; TWO STORY thread); ui-v418: core-v816, the gutters' near-miss corners snapped where a product asks (spec.gutters.snap) and the gable test's middle off the ridge line (T house thread); ui-v417: core-v815, a wall another layer builds (p.own / p.t, DUPLEX thread); ui-v416: core-v814, the downspout swept piece by piece -- a straight piece two rings, a bend six, the section 4 points a corner (T house thread for the perf line: the T house's six pipes 389k -> 33k vertices); ui-v415: core-v813, the fixes carried across (shared-modules thread); ui-v414: core-v812, a library design without a mirror button (it.noMirror, TWO STORY thread); ui-v413: core-v811, an upper storey's partition inside its own outline, the render's plinth on the ground storey only (TWO STORY thread); ui-v412: core-v810, the lightning protection, the fitout picker and the snapshot in the core (shared-modules thread); ui-v411: core-v809, a round light drawn round on the house line, the default project waited for (TWO STORY thread); ui-v410: core-v808, the few-variant helpers (shared-modules thread); ui-v409: core-v807, the small shared helpers (shared-modules thread); ui-v408: core-v806, the flat roof module's coping overhang (shared-modules thread, clean-up); ui-v407: core-v805, a product's own first view may give its rise, a distance factor and rail: true -- framed in the canvas left of the right-hand rail (RUSSWORX SHED, Marek's phone); ui-v406: core-v804, RUSSWORX SHED -- stacked walls in THE STUD WALL (H.tiers, H.dropNear) and a product's own first view / drawings isometric (o.viewDir / o.isoDir from __PROD._viewDir / _isoDir), opt-in; ui-v405: core-v803, the pitch's sample dashboard drawn as the real page, the subtitles over the item drawer; ui-v404: core-v802, the creator pitch's two new steps, dashboard (a sample royalty card) and editlist (a link typed into a budget item, never synced); ui-v403: core-v801, an opening over another cuts the lower one's jacks and kings (no timber in the upper glass; RUSSWORX SHED, Marek: v oknech jsou framing lumber); ui-v402: core-v800, the joinery's opt-in H.glazed(o) (a full-glass door leaf) and H.secPitch(o) (a roll-up's slats), and the stud wall's opt-in __PROD._studStep (RUSSWORX SHED round 3); ui-v401: core-v799, 'leva:model-ready' once the first model is on screen, the configurator's Mapbox SDK and map after it (perf thread, status/perf.md); ui-v400: core-v798, a unit of its own colour in the joinery (H.colOf) and a raked eave-post run (the rake of a mono falling along z, a turned gable) -- each post to its own soffit, the beam along the roof (RUSSWORX SHED thread); ui-v399: core-v797, Reset goes to the product's own fresh page, not to the core's bare defaults; ui-v398: core-v796, the corner trim climbs to the lower of the two walls' own tops (a wall with its own height, STATE.segH -- RUSSWORX SHED, shed-v132); ui-v397: core-v795, optional plan site bounds; ui-v396: core-v794, optional rigid assembly spread; ui-v395: core-v793, opt-in stable Bunker cameras and own-library upload control; ui-v394: core-v792, no blind over a round window or an opening marked blind: false (TWO STORY thread, status/twostory.md R5); ui-v393: core-v791, opt-in screen-sized deck targets (houseboat thread); ui-v392: core-v791, THE FITOUT MODULE (shared-modules thread); ui-v391: core-v789, THE FLAT ROOF MODULE (shared-modules thread); ui-v390: core-v788, houseRoof's span direction hook (TWO STORY thread, status/twostory.md R4b); ui-v389: core-v787, THE ROOF SKIN MODULE (shared-modules thread); ui-v388: core-v786, the serving windows in the shared offer (KIOSK thread); ui-v387: core-v785, a storey's own outline (__PROD._storeyPoly; TWO STORY thread, status/twostory.md R4a); ui-v386: core-v784, THE ROOF FRAME MODULE (shared-modules thread); ui-v385: core-v783, THE JOINERY MODULE and THE DOOR SET (shared-modules thread); ui-v384: core-v782, THE PARTITIONS MODULE (shared-modules thread); ui-v383: core-v781, the houseboat's marine controls (houseboat thread, status/houseboat.md); ui-v382: core-v780, THE CEILING MODULE (shared-modules thread); ui-v381: core-v779, THE FLOOR MODULE (shared-modules thread); ui-v380: core-v778, THE WALL KIT -- the gables, eaves and soffits (shared-modules thread); ui-v379: core-v777, the houseboat's deck (houseboat thread, status/houseboat.md); ui-v378: core-v776, THE WALL KIT -- the outside of the wall (shared-modules thread); ui-v377: core-v775, THE WALL KIT -- the inside of the wall (shared-modules thread); ui-v376: core-v774, CABIN / TWO STORY / ANY HOUSE ENGINE in the drawings page's package (blueprints thread); ui-v375: core-v773, THE STUD WALL MODULE (shared-modules thread); ui-v374: core-v772, the opt-in modeled-only assembly, per-body fly direction (bunker thread, status/bunker.md); ui-v373: core-v771, several floor openings on the plan (list / setAt) and the 3200 spiral and loft kits (T house thread); ui-v372: core-v770, the PDF icon waits for the prices (blueprints thread); ui-v371: core-v769, TAURUS and GARDEN BAR in the drawings page's package (the original in front, the purchase for a royalty product; blueprints thread); ui-v370: core-v768, the tour's product step (__PROD._tourRow, GARDEN BAR thread); ui-v369: core-v767, ?bppkg=1 -- the drawings page's package made here and the visitor sent back (blueprints thread); ui-v367: core-v765, the truss module (API.trusses); ui-v366: core-v764, the foundation module (API.foundation); ui-v365: core-v763, every ?ig page's PLAY bottom centre, hidden at the tap, the reel 2 s later; ui-v364: core-v762, the eave posts module (API.eavePosts); ui-v363: core-v761, the PDF package (drawings page) for BUNKER and HOUSEBOAT too; ui-v362: core-v760, the gutter hangs from the drip edge as drawn; ui-v361: core-v759, the gutters module (API.gutters), the first shared module of the house line; ui-v360: core-v758, an author's royalty product's PDF only with the purchase, no tokens (GARDEN BAR thread); ui-v359: core-v757, the hipped L's flush side one plane (ANY HOUSE thread); ui-v358: core-v756, the pitch's login step opens the stats button (aff-v1.4 keeps it shut by default) and shuts it after; ui-v357: core-v755, the creator pitch's step login after the budget -- the drawer open at its top on the sample stats panel (theme aff-v1.3), clip pitch-login (Marek 2026-10-23 16:25); ui-v356: core-v754, THE CREATOR PITCH -- a page with window.LEVA_PITCH (TAURUS III, the router from custom.creator_pitch) gets a big PLAY in the middle that shrinks into its corner on the first tap, and the tour's pitch cut with its own pitch-* clips, the author drawer as a step (marketing thread, Marek 2026-10-23); ui-v355: core-v753, an L as a wing flush with one end of the bar (STATE.wing.flush, ANY HOUSE thread); ui-v354: core-v752, ?recipe= from ANY HOUSE opens the product set to the visitor's recipe (any-house thread, Marek 2026-10-22); ui-v353: core-v751, the CORNER WINDOW -- a tile second in the windows picker, two catalogue units meeting at an outside corner, twelve sets on the cycler icon at the corner, the corner post kept, sets that leave no braced panel near the corner skipped (corner window thread, Marek 2026-10-16 / 2026-10-22); ui-v352: every footprint as the ADU's roof -- gold outline, 3 % fill, smaller pucks (handles() STYLES + _radius; bench thread, Marek 2026-10-22 00:25); ui-v351: core-v750, the homepage hero's camera is not framed again when its reel shuts the drawer at a loop's end (it jumped back to the framing angle); ui-v350: core-v749, the tour's assembly walk -- exploded, back together, four steps in one pass; ui-v349: core-v748, __PROD._tourBudget2 + __PROD._reelAsm (TAURUS ?ig); ui-v348: core-v747, the tour's subtitle over any open drawer (bench thread, Marek 2026-10-21: titulky jsou schovane za drawers); ui-v347: core-v746, window.LEVA_HERO_TURN (the hero's turn in degrees a second, for recording its video); ui-v346: core-v745, the hero's turn by the clock at 8 degrees a second (the exploded view's pace), 90 ms of frames between the eaves' builds, no second build of an unchanged project, the explode's extra turn 10 degrees; ui-v345: core-v744, the hero: the picture off centre on a frame the whole section wide (data-shift), the exploded view's zoom measured on the exploded house, the facade boards -> render and back, the cladding's (timber) colour, the infill off and on; ui-v344: core-v743, the hero's reel on one house: base and roof walked round to the house's own, colours, the eaves out into a porch with posts, slats between them, exploded view (1.2 x out and back to the same place, a gentle extra turn), then infill off, eaves in, colours back -- the loop ends on the house it began with; ui-v343: core-v742, the hero frames the house it goes on with after the intro; ui-v342: core-v741, the hero frames every design to its own size and again after the exploded view; ui-v341: core-v740, the hero's exploded view held 3.4 s and turned round, then back together; ui-v340: core-v739, the hero's reel: only the trees round the house, the faded buildings gone; ui-v339: core-v738, the hero's intro waits for its whole show (every design + the exploded view, 20 s at most); ui-v338: core-v737, the hero's reel: the exploded view out and back in 2 s after the designs; ui-v337: core-v736, the hero's reel: the roof's overhangs stretched and back, a slow turn the whole time (no camera jumps), faded buildings round the house and half the trees; ui-v336: core-v735, window.LEVA_FIT_K (a page's own camera distance factor, the hero's frame 1.3) and the hero's general intro clip (hero-intro, every language); ui-v335: core-v734, the homepage hero's own cut of the reel (designs, roof, base, colours, looped), muted = on the shows' own time, never the slow turn; ui-v334: core-v733, the package opens on the drawings page before it is paid (the page shows five pages sharp, takes the tokens on download), the record carries the token key and the owner flag; ui-v333: core-v732, ?ig&autoplay starts the reel by itself, muted, and loops it -- LEVA_TOUR_SOUND / LEVA_TOUR_RUN for the homepage hero's frame, no short link from it; ui-v332: core-v732, the paid PDF of a product with drawings opens the drawings page with the whole package (indexedDB leva-pkg, _pdfArgs shared with the PDF made here); ui-v331: core-v730, the ?ig subtitles larger; ui-v330: core-v729, the infill's on / off dots smaller (360 across, 400 out), __PROD._tourOut (a product's steps out of every tour), a product's own clip in every language; ui-v329: core-v728, the PDF opens with the project's original documentation where the product hands one (__PROD._pdfOriginal, TAURUS: Matt's drawings for the buyer); ui-v328: core-v727, the roof drawer lays battens with no counter-batten when a product hands counter: null (TAURUS, Matt's corrugated roof on battens); ui-v327: core-v726, Infill / Planks labels, a softer pick, ghost fill + band, a product's own plan-library designs, the tour's infill demo and product clips (TAURUS); ui-v326: core-v725, blueprintPackage().layers carry label (the layer's name in the page's language) for the drawings page; ui-v325: core-v724, the tour's highlight a soft grey pulse, no red; ui-v324: core-v723, per-bay infill kinds for TAURUS (opt-in setPostWallMode: a tap picks a bay, magenta, the arrows set its own kind; the kinds follow the facade's direction) and a product's override of a core kind; ui-v323: core-v722, the plain bar's plan icon opens the floorplan (the plan strip) and closes it, the bar never opening out; ui-v322: core-v721, three opt-ins for TAURUS: __PROD._cmdPlain (the bar's plan icon only switches the plan, no field), API.setPostWallKinds (a product's own infill kind in the Slats cycle), kit codes (the openings picker narrowed); ui-v321: core-v720, the ?ig reel under 60 s (designs, colours, plan library, roof, base, budget, AI) and the catchy hook as every English tour's intro; ui-v314: core-v714, ?ig = the highlights only; ui-v257-owner: the paid owner's PDF; ui-v258: core-v658, the exploded view; ui-v259: core-v659, spec.castLintel (RANCH); ui-v260: core-v660, the PDF price in the black token box in the middle; ui-v261: core-v661, __PROD._paintTileRe + __PROD._coatByLayer (RANCH); ui-v262: core-v662, framed again until the camera is touched; ui-v263: core-v663, the qty audit reads userData.qtyN / qtyV of a baked body (RANCH); ui-v264: core-v664, and userData.qtyRun of a baked profile (RANCH); ui-v265: core-v665, the tag audit reads the bill buckets that are not layers, a square section measured as one (RANCH); ui-v266: core-v666, a shingle course cut on the valley / hip line, tab by tab (RANCH thread, every product); ui-v267: core-v667, the mono's high eave gets its fascia + the fascia's nails (opt-in spec.edges.fascia.top / .nail, GARAGE APT); ui-v268: core-v668, the tour speaks Spanish on a Spanish page (-es clips, English as the fallback); ui-v269: core-v669, a verge tagged its own code (opt-in spec.edges.vergeTag, RANCH); ui-v270: core-v670, the English tour spoken from the leva-voice worker (ElevenLabs, Roger), Shopify's clips as the fallback; ui-v271: core-v671, the Spanish tour from the worker too (Roger); ui-v272: core-v672, the tour on a German page (Theo Teacher); ui-v273: core-v673, the tour on a French page (Thierry); ui-v274: core-v674, the tour on a Czech page (Zazy); ui-v275: core-v675, the tour's library step takes a plan unlike the house on screen; ui-v276: core-v676, the tour on a Portuguese page (Paulo); ui-v277: core-v677, the qty audit tests a square section first (RANCH thread, audit only); ui-v278: core-v678, the tour on a Chinese page (Adrian); ui-v279: core-v679, the tour on an Arabic page (Abdullah); ui-v280: core-v680, the tour on a Japanese page (Kaito); ui-v281: core-v681, the tour's clips fetched ahead (no long silence after play); ui-v282: core-v682, the qty audit measures what is under grade whole + buildStripSlab's opt-in o.marks (RANCH thread, opt-in __PROD._qtyFull); ui-v283: core-v683, the tour on a Turkish page (Emrullah Tuna); ui-v284: core-v684, buildStripSlab's opt-in o.marks.dig -- the dig drawn as billed (RANCH thread); ui-v285: core-v685, the PLAY button comes in with a zoom and three big pulses, then the soft pulse; ui-v286: core-v686, the tour on a Korean page (Sani); ui-v287: core-v687, the qty audit counts what is billed by the piece (RANCH thread, opt-in __PROD._qtyPieces); ui-v288: core-v688, the PLAY button first shows at 200 %, then settles to its size -- no red ring; ui-v289: core-v689, the tour on an Italian page (Antonio); ui-v290: core-v690, the scroll lane on the left, the strip lets the page scroll; ui-v291: core-v691, the tour goes on with the page in the background (the steps follow the voice), media controls; ui-v292: core-v692, the tour on a Polish page (Peter); ui-v293: core-v693, the scroll lane's glyph fixed in the middle of the section, 20 / 70 %; ui-v294: core-v694, the tour's panel in the phone's notifications: picture, name, step, play / pause / next / previous; ui-v295: core-v695, one stream for the whole tour (MediaSource): the player never ends between the clips, the panel stays; ui-v296: core-v696, the tour on a Romanian page (Liviu Mihai); ui-v297: core-v697, the tour in the background: the voice of the steps left queued at once, the shows catch up when the page comes back; the tour on a Dutch page (Finn); ui-v298: core-v698, the PLAY entrance: from 200 % at 10 % opacity to its size at full opacity in one move; ui-v299: core-v699, the tour's stream kept fed with silence on screen too (no stall, click or dip between two clips); ui-v300: core-v700, the tour's EN subtitles on every product (a cross to close, ?ig / ?subs=1 shows them); ui-v301: core-v701, the tour on a Greek page (Eugene); ui-v302: core-v702, the AR export carries the project, the prompt and the named rooms for the walk-through's Gemini snapshot; ui-v303: core-v703, the tour on a Hungarian page (Peter); ui-v304: core-v704, the walk-through prompt's English fallback product-neutral (any configurator, not only a house); ui-v305: core-v705, the tour on a Swedish page (Kim); ui-v306: core-v706, every change of the model's size framed again (3D from the visitor's angle, the plan too), the subtitles 15 px over the bottom bar, the soft highlight off the right-hand icons; ui-v307: core-v707, the tour: the roof's type changed as well as its pitch, the base walked through its types, the scissors zoom in on the piece, cut it, zoom out and put it back; ui-v308: core-v708, the tour shows every roof and every foundation; ui-v309: core-v709, the tour's pergola and pool outside the roof's footprint, placed, moved once, made bigger; ui-v310: core-v710, the tour on a Hebrew page (Omer); ui-v311: core-v711, the tour on a Finnish page (Christoffer Weiss); ui-v312: core-v712, the tour on a Norwegian page (Olaf) -- every store language has its voice; ui-v313: core-v713, a ?ig page (English) opens the tour with the product's own catchy intro for social posts */

  /* ---- BRAND ------------------------------------------------------------
     The LEVA UX manual (sections/ux.liquid) in one object. Every hex and
     radius in this file reads from here, so a brand change is one edit rather
     than a hunt through CSS strings. Exported as ConfiguratorUI.UX for the libs.

     ACCENT stays separate from sand: the slider shipped with #c79a4a from
     configurator-bench.liquid and the two are close but not equal. Aliasing
     them would silently restyle every existing slider, so the difference is
     kept until someone decides on purpose. */
  var UX = {
    sand:    '#D2A878',   /* add / primary / active accent */
    dark:    '#2C2D2E',   /* remove / negative */
    red:     '#D8232A',   /* destructive, chat, ON state */
    ink:     '#1A1C1D',   /* headings */
    body:    '#2C2D2E',
    muted:   '#9CA3AF',   /* labels, hints */
    line:    '#DADCE0',   /* dividers, borders */
    surface: '#FFFFFF',
    bg:      '#F5F5F5',
    brown:   '#463529',   /* icon glyph on light buttons */
    font:    "'Montserrat', sans-serif",
    radius:  { btn: '25px', card: '16px', input: '6px', round: '50%' },
    icon:    '44px',      /* circular target, manual section 7 */
    shadow:  { soft: '0 4px 10px rgba(0,0,0,0.08)', red: '0 4px 10px rgba(216,35,42,0.3)' }
  };

  /* ---- BRAND COMPONENTS -------------------------------------------------
     The classes from the UX manual that a configurator actually uses. Built
     from UX above, so there is one definition of a LEVA button and it lives in
     the thing that ships.

     The direction matters: sections/ux.liquid is a page, and a page cannot be
     read at runtime -- so the manual cannot be the source. It calls
     injectBrand() and renders what the configurator renders, which makes it
     incapable of showing something the product does not do.

     Page furniture -- the manual's wrapper, headings, swatch grid -- stays in
     the liquid. No configurator needs it and every configurator would download
     it. */
  function brandCss() {
    return [
      /* section 7: round icon buttons, 44px circular targets */
      '.leva-icon-btn{width:' + UX.icon + ';height:' + UX.icon + ';border-radius:' + UX.radius.round + ';',
      'display:inline-flex;align-items:center;justify-content:center;border:none;',
      'cursor:pointer;font:700 18px ' + UX.font + ';}',
      '.leva-icon-btn--dark{background:' + UX.dark + ';color:' + UX.surface + ';}',
      '.leva-icon-btn--sand{background:' + UX.sand + ';color:' + UX.surface + ';}',
      '.leva-icon-btn--light{background:' + UX.surface + ';color:' + UX.brown + ';',
      'border:1px solid ' + UX.line + ';box-shadow:' + UX.shadow.soft + ';}',
      '.leva-icon-btn--red{background:' + UX.red + ';color:' + UX.surface + ';box-shadow:' + UX.shadow.red + ';}',

      /* section 7: nav arrows -- bare chevron, no disc, outside the panel */
      '.leva-nav-arrow{width:' + UX.icon + ';height:' + UX.icon + ';padding:0;border:none;',
      'background:none;color:' + UX.body + ';cursor:pointer;display:inline-flex;',
      'align-items:center;justify-content:center;transition:color .12s,transform .12s;}',
      '.leva-nav-arrow svg{width:24px;height:24px;}',
      '.leva-nav-arrow:hover{color:' + UX.sand + ';}',
      '.leva-nav-arrow:active{transform:scale(0.88);}',
      '.leva-nav-arrow:disabled{opacity:0.25;cursor:default;transform:none;color:' + UX.body + ';}',
      '.leva-nav-arrow--light{color:' + UX.surface + ';}',
      '.leva-nav-arrow--light:hover{color:' + UX.sand + ';}',

      /* section 7: range slider -- the one the configurators drive */
      '.leva-range input[type=range],.cfg-ui-slider{-webkit-appearance:none;appearance:none;',
      'width:110px;height:4px;border-radius:2px;outline:none;cursor:pointer;margin:0 8px;',
      'vertical-align:middle;background:linear-gradient(to right,' + ACCENT + ' 0%,' + ACCENT + ' 50%,',
      TRACK + ' 50%,' + TRACK + ' 100%);}',
      '.leva-range input[type=range]::-webkit-slider-thumb,.cfg-ui-slider::-webkit-slider-thumb{',
      '-webkit-appearance:none;appearance:none;width:16px;height:16px;border-radius:' + UX.radius.round + ';',
      'background:' + ACCENT + ';border:2px solid ' + UX.surface + ';',
      'box-shadow:0 1px 3px rgba(0,0,0,0.25);cursor:grab;}',
      '.leva-range input[type=range]::-moz-range-thumb,.cfg-ui-slider::-moz-range-thumb{',
      'width:16px;height:16px;border-radius:' + UX.radius.round + ';background:' + ACCENT + ';',
      'border:2px solid ' + UX.surface + ';box-shadow:0 1px 3px rgba(0,0,0,0.25);cursor:grab;}'
    ].join('');
  }

  /* --------------------------------------------------------------------------
     THE PICKER. The asset drawer, copied from the tiny lib.

     It is the SAME drawer the EDIT button opens: side-panel, in from the RIGHT,
     over the step panel. I built it twice out of my own head first -- a sheet
     up from the bottom, then a shorter sheet up from the bottom -- when the
     answer was already shipping in another product and I had been pointed at
     it. That cost three rounds.

     WIDTH CHIPS ACROSS THE TOP, because the list is a matrix and width is the
     axis anyone actually shops on: the wall gives you so much room and no more.
     All is a chip too, so there is a way back.

     THUMBNAILS, NOT NAMES. Each tile draws the unit at its own proportions, so
     a 2400 x 600 ribbon and a 600 x 2100 light are told apart by looking rather
     than by reading two numbers in a different order.

     IT CARRIES NO THEME CLASSES. I gave it tiny's -- side-panel,
     configurator-asset-drawer -- to get the theme's styling for free, and got
     the theme's display rules with it: on a page where that stylesheet is
     loaded but the drawer is driven by something else, .side-panel is hidden
     until a class this code never sets, so the panel was built correctly and
     never appeared. Borrowed classes are borrowed behaviour. It styles itself.

     Fixed to the viewport rather than absolute inside the configurator, because
     absolute needs a positioned ancestor and there is no promise of one.
     -------------------------------------------------------------------------- */
  /* --------------------------------------------------------------------------
     THE OPENING CATALOGUE. Every window and door the central BoQ actually
     stocks, with its code and its real size.

     IT LIVES HERE BECAUSE EVERY PRODUCT NEEDS THE SAME ONE. A door is a door
     whether it goes in an ADU, a sauna or a garage -- the list is a fact about
     the catalogue, not about any one building. Each product filters it to what
     it will actually sell: it names the groups it wants and the rest stay put, and
     nothing else has to be maintained twice.

     THE SIZES ARE THE CATALOGUE'S OWN, read off the DIMENSIONS columns. Where a
     row has no size in the sheet it is not here, because a tile that cannot say
     how big it is cannot be chosen -- and inventing one would put a unit in a
     budget at a size the supplier never quoted.

     HAND IS PART OF THE PRODUCT, NOT A DETAIL. The catalogue carries LH and RH
     as separate codes with separate prices, so they are separate items here and
     the drawer filters on them. Getting the hand wrong is a door that opens
     into the room it was meant to open out of.
     -------------------------------------------------------------------------- */
  var OPENING_CATALOG = {
    window: {
      label: 'Windows', sill: 0.90,
      items: [
        /* THE FULL-WALL PAIR COMES FIRST, and their size is not on the tile.
           Every other item carries the width and height it will be built at;
           these carry zero, because the WALL decides. w and h are resolved at
           placement from the wall actually tapped and written onto the opening,
           so from that moment it is an ordinary opening and the framing, the
           bill and the drawing need to know nothing about it. */
        { code: 'FIXF0001', w: 0, h: 0, name: 'Fixed window, full wall', full: true, fixed: true },
        { code: 'FIXH0001', w: 0, h: 0, name: 'Fixed window, half wall', full: 'half', fixed: true },
        /* THE FULL-HEIGHT FIXED UNITS (core-v270): glass from the floor to 2100 mm
           in three widths, the catalogue's WINF rows with their own sizes. Asked
           for on the sauna, where a hot room looks out through one of these
           (Marek); they are catalogue items like any other, so every product
           that sells windows gets them. */
        { code: 'WINF0101', w: 0.60, h: 2.10, sill: 0, name: 'Fixed window, full height', fixed: true },
        { code: 'WINF0201', w: 1.20, h: 2.10, sill: 0, name: 'Fixed window, full height', leaves: 2, fixed: true },
        { code: 'WINF0301', w: 1.80, h: 2.10, sill: 0, name: 'Fixed window, full height', leaves: 3, fixed: true },
        { code: 'OWR04001', w: 0.40, h: 0.60, name: 'Single-leaf window' },
        { code: 'OWR05001', w: 0.50, h: 0.70, name: 'Single-leaf window' },
        { code: 'OWR09002', w: 0.90, h: 0.50, name: 'Single-leaf window' },
        { code: 'OWR07501', w: 0.75, h: 0.90, name: 'Single-leaf window' },
        { code: 'OWR07001', w: 0.70, h: 1.15, name: 'Single-leaf window' },
        { code: 'OWR08001', w: 0.80, h: 1.15, name: 'Single-leaf window' },
        { code: 'OWR09001', w: 0.90, h: 1.35, name: 'Single-leaf window' },
        { code: 'OWR12001', w: 1.20, h: 1.50, name: 'Single-leaf window', leaves: 2 },
        { code: 'OWR12002', w: 1.20, h: 2.00, name: 'Single-leaf window', leaves: 2 },
        { code: 'OWR15001', w: 1.50, h: 1.65, name: 'Single-leaf window', leaves: 2 },
        { code: 'OWR25917', w: 2.59, h: 1.70, name: 'Fixed window', leaves: 3, fixed: true },
        /* THE ROUND LIGHTS (core-v518, Marek: doplnit do moznosti vkladani
           oken i tyto dve kulata okna). Two fixed circular units, the
           catalogue's OWC rows by the piece, w and h both the diameter. The
           wall takes them as a square rough opening the size of the circle
           (the blocks are cut square and get their lintel as for any hole);
           the unit is drawn round in it and the four corners between the
           square and the circle are closed by the wall -- see buildOpenings.
           A porthole sits higher than a window: its sill puts the middle at
           about 1.6 and 1.5 m. */
        { code: 'OWC06001', w: 0.60, h: 0.60, name: 'Circular window', fixed: true, round: true, sill: 1.30 },
        { code: 'OWC10001', w: 1.00, h: 1.00, name: 'Circular window', fixed: true, round: true, sill: 1.00 }
      ]
    },
    door: {
      label: 'Entry doors', sill: 0,
      items: [
        { code: 'ODE08001', w: 0.80, h: 2.00, name: 'Exterior door', hand: 'L', solid: true },
        { code: 'ODE08002', w: 0.80, h: 2.00, name: 'Exterior door', hand: 'R', solid: true },
        { code: 'ODE09001', w: 0.90, h: 2.08, name: 'Exterior door', hand: 'L', solid: true },
        { code: 'ODE09002', w: 0.90, h: 2.08, name: 'Exterior door', hand: 'R', solid: true },
        /* A DOUBLE DOOR HAS NO HAND. Two leaves meet in the middle and open
           away from each other, so neither side is the hinge side -- there is
           nothing for left or right to mean. The sheet writes RH on this row
           and the code keeps it, because the code is what gets ordered, but it
           is not offered as a handed item and the left/right filter passes it
           by: filtering to Left and being shown a pair of doors would be
           telling somebody something untrue about what they are buying.

           This is also why the tab was two left and three right and could never
           be even. There is no ODE18001; there was never a missing twin. */
        { code: 'ODE18002', w: 1.50, h: 2.08, name: 'Double exterior door',
          solid: true, leaves: 2 }
      ]
    },
    french: {
      label: 'French & sliding', sill: 0,
      items: [
        { code: 'FULL0002', w: 0, h: 0, name: 'Sliding glazed door, full wall',
          full: true, leaves: 2 },
        { code: 'OWR06001', w: 0.60, h: 2.59, name: 'French door' },
        { code: 'OWR10001', w: 1.00, h: 2.59, name: 'French door' },
        { code: 'OWR15002', w: 1.50, h: 2.59, name: 'HS sliding door', leaves: 2 },
        { code: 'OWR17001', w: 1.70, h: 2.59, name: 'HS sliding door', leaves: 2 },
        { code: 'OWR20001', w: 2.00, h: 2.59, name: 'HS sliding door', leaves: 2 },
        { code: 'OWR25001', w: 2.50, h: 2.59, name: 'HS sliding door', leaves: 2 },
        { code: 'OWR30001', w: 3.00, h: 2.59, name: 'HS sliding door', leaves: 3 },
        { code: 'OWR40001', w: 4.00, h: 2.59, name: 'HS sliding door', leaves: 3 },
        { code: 'OWR50001', w: 5.00, h: 3.39, name: 'HS sliding door', leaves: 4 }
      ]
    },
    garage: {
      label: 'Garage doors', sill: 0,
      items: [
        /* core-v618 (RANCH thread): the single-car 9 x 7 ft door the sheet
           carries now (ODG27001, 2740 x 2130); the plan reader takes the unit
           nearest the width a garage wall offers, so a narrow garage gets it */
        { code: 'ODG27001', w: 2.74, h: 2.13, name: 'Sectional garage door', sectional: true },
        { code: 'ODG48001', w: 4.80, h: 2.08, name: 'Sectional garage door', sectional: true }
      ]
    },
    interior: {
      label: 'Interior doors', sill: 0,
      items: [
        { code: 'ODI06001', w: 0.60, h: 1.97, name: 'Interior door', hand: 'L', solid: true },
        { code: 'ODI06002', w: 0.60, h: 1.97, name: 'Interior door', hand: 'R', solid: true },
        { code: 'ODI07001', w: 0.75, h: 1.97, name: 'Sauna door', hand: 'R', solid: true },
        { code: 'ODI08001', w: 0.80, h: 1.97, name: 'Interior door', hand: 'L', solid: true },
        { code: 'ODI08002', w: 0.80, h: 1.97, name: 'Interior door', hand: 'R', solid: true },
        { code: 'ODI09001', w: 0.90, h: 1.97, name: 'Interior door', hand: 'L', solid: true },
        { code: 'ODI09002', w: 0.90, h: 1.97, name: 'Interior door', hand: 'R', solid: true }
      ]
    },
    /* THE SERVING WINDOWS (core-v786, KIOSK thread, kiosk-v5): the container's
       serving hatches, now in the shared offer -- 4, 6 and 8 ft wide, 40 in.
       high, the counter at 40 in. so somebody outside can lean on it. The
       sheet sells them by the square metre (SRV rows, M2 / FT2), so m2: true
       bills the unit's own area, not the piece. No name: the tab already
       says what they are, a tile shows only its size. The full-wall SRVW0001
       waits until a product asks for it. */
    serve: {
      label: 'Serving windows', sill: 1.02,
      items: [
        { code: 'SRVF0001', w: 1.22, h: 1.02, name: '', m2: true },
        { code: 'SRVL0001', w: 1.83, h: 1.02, name: '', m2: true },
        { code: 'SRVX0001', w: 2.44, h: 1.02, name: '', m2: true }
      ]
    },
    roof: {
      label: 'Roof windows', sill: 0,
      items: [
        { code: 'OWR55118', w: 0.55, h: 1.18, name: 'Roof window' },
        { code: 'OWR78118', w: 0.78, h: 1.18, name: 'Roof window' },
        { code: 'OWR78140', w: 0.78, h: 1.40, name: 'Roof window' },
        { code: 'OWR11118', w: 1.14, h: 1.18, name: 'Roof window' }
      ]
    }
  };

  /* The group, with each item carrying its group's default sill so a product
     never has to remember which way round that goes. */
  function openingGroup(kind) {
    var G = OPENING_CATALOG[kind];
    if (!G) return null;
    var out = { kind: kind, label: G.label, sill: G.sill, items: [] };
    for (var i = 0; i < G.items.length; i++) {
      var it = G.items[i], row = {};
      for (var k in it) if (it.hasOwnProperty(k)) row[k] = it[k];
      if (row.sill == null) row.sill = G.sill;
      row.kind = kind;
      out.items.push(row);
    }
    return out;
  }

  /* --------------------------------------------------------------------------
     THE ICON SET. One drawing per name, for every product.

     Each configurator grew its own map of little SVGs, and the maps had drifted
     apart: seven names existed in both the ADU and the container with a
     DIFFERENT drawing behind each. A door meant one thing in one product and
     another in the next, on a site whose whole premise is that a glyph means
     the same everywhere.

     THESE ARE PATHS, NOT ELEMENTS. The wrapper -- viewBox, stroke, weight --
     belongs to whoever is drawing, because the same shape serves a 22 px rail
     button and a 44 px tile at different weights, and baking the wrapper in is
     what forced every product to keep its own copy in the first place.

     A product that needs something nobody else does still defines it locally.
     This is the common set, not a rule against having any others.
     -------------------------------------------------------------------------- */
  var ICON_PATHS = {
    back:      '<path d="M15 5l-7 7 7 7"/>',
    base:      '<path d="M3 20h18"/><path d="M7 20v-6M17 20v-6"/><path d="M5 14h14"/>',
    bath:      '<path d="M4 11h16v3a4 4 0 0 1-4 4H8a4 4 0 0 1-4-4z"/><path d="M7 11V6a2 2 0 0 1 4 0"/><path d="M7 18l-1 2M17 18l1 2"/>',
    bed:       '<path d="M3 18v-7h18v7"/><path d="M3 11V7h7v4"/><path d="M3 20v-2M21 20v-2"/>',
    bolt:      '<path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z"/>',
    pool:      '<path d="M3 15c2-2 4-2 6 0s4 2 6 0 4-2 6 0"/><path d="M3 19c2-2 4-2 6 0s4 2 6 0 4-2 6 0"/><path d="M8 15V5a2 2 0 0 1 4 0M13 15V5a2 2 0 0 1 4 0"/>',
    brush:     '<path d="M4 20c2 0 3-1 3-3 0-1.5 1-2.5 2.5-2.5S12 15.5 12 17"/><path d="M9 14L18 5a2 2 0 0 1 3 3l-9 9"/>',
    cladding:  '<path d="M3 7h18M3 12h18M3 17h18"/><path d="M8 7v5M15 12v5"/>',
    /* a shutter: its case on the head, the guides down the reveals, and four
       slats hanging in them -- the same one-stroke language as the rest */
    blinds:    '<rect x="3" y="3" width="18" height="3" rx="1"/><path d="M5 6v15M19 6v15"/>' +
               '<path d="M7 9h10M7 12h10M7 15h10M7 18h10"/>',
    color:     '<path d="M12 3s6 6.5 6 10a6 6 0 0 1-12 0c0-3.5 6-10 6-10z"/>',
    cut:       '<rect x="3" y="4" width="18" height="16" rx="1.5" opacity="0.45"/><rect x="8" y="8" width="8" height="8" rx="1"/><path d="M8 8l8 8" opacity="0.7"/>',
    door:      '<path d="M4 21V3h12v18"/><path d="M16 21H2"/><circle cx="13" cy="12" r="1" fill="currentColor"/>',
    envelope:  '<rect x="3" y="3" width="18" height="18" rx="1.5"/><rect x="7" y="7" width="10" height="10" rx="1"/>',
    facade:    '<rect x="3" y="3" width="18" height="18" rx="1.5"/><path d="M3 9h18M3 15h18"/>',
    finish:    '<path d="M3 6h18"/><path d="M6 6v4M18 6v4" opacity="0.6"/><rect x="3" y="13" width="18" height="7" rx="1"/><path d="M3 16h18M3 18.5h18" opacity="0.5"/>',
    fitout:    '<path d="M4 11V8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3"/><path d="M2 13a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v4H2z"/><path d="M5 17v2M19 17v2"/>',
    flat:      '<path d="M2 9h20"/><path d="M5 9v11h14V9"/>',
    floor:     '<rect x="3" y="4" width="18" height="7" rx="1"/><rect x="3" y="13" width="18" height="7" rx="1" opacity="0.4"/>',
    footing:   '<path d="M4 10h16"/><path d="M7 10v7M12 10v7M17 10v7"/><path d="M3 20h18"/>',
    french:    '<rect x="3" y="3" width="18" height="18" rx="1"/><path d="M12 3v18"/><circle cx="10" cy="12" r="0.9" fill="currentColor"/><circle cx="14" cy="12" r="0.9" fill="currentColor"/>',
    gable:     '<path d="M2 13L12 5l10 8"/><path d="M5 13v7h14v-7"/>',
    garage:    '<path d="M3 20V9l9-5 9 5v11"/><path d="M7 20v-7h10v7"/><path d="M7 16h10"/>',
    kitchen:   '<rect x="3" y="8" width="18" height="12" rx="1"/><path d="M3 13h18M9 8V4h6v4"/>',
    layers:    '<path d="M12 3l9 5-9 5-9-5z"/><path d="M3 13l9 5 9-5"/>',
    lock:      '<rect x="4" y="10" width="16" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/>',
    minus:     '<path d="M5 12h14"/>',
    /* core-v592: the plan turned over left to right */
    /* core-v595: the glyph Marek sent -- two triangles across a dashed line, the turned one shaded */
    mirror:    '<path d="M12 2.5v19" stroke-dasharray="2 2"/><path d="M10 5.5L3.5 19H10z"/><path d="M14 5.5L20.5 19H14z" fill="currentColor" fill-opacity="0.35"/>',
    pad:       '<rect x="6" y="9" width="12" height="6" rx="1"/><path d="M4 18h16"/>',
    parapet:   '<path d="M3 7h18"/><path d="M5 7v3h14V7"/><path d="M5 10h14v10H5z"/>',
    pergola:   '<path d="M3 7h18M3 11h18"/><path d="M6 7v13M18 7v13"/><path d="M9 7v4M12 7v4M15 7v4" opacity="0.5"/>',
    pin:       '<path d="M12 21s7-6.2 7-11a7 7 0 1 0-14 0c0 4.8 7 11 7 11z"/><circle cx="12" cy="10" r="2.5"/>',
    plan:      '<rect x="3" y="3" width="18" height="18" rx="1"/><path d="M3 10h18M10 10v11"/>',
    play:      '<circle cx="12" cy="12" r="9"/><path d="M10 8.5l6 3.5-6 3.5z" fill="currentColor"/>',
    plus:      '<path d="M12 5v14M5 12h14"/>',
    raked:     '<path d="M2 12L22 5"/><path d="M5 11v9h14V7"/>',
    rooms:     '<rect x="3" y="4" width="18" height="16" rx="1.5"/><path d="M12 4v16"/>',
    scenery:   '<path d="M12 4l4 7h-8z"/><path d="M12 9l5 8H7z"/><path d="M12 17v4"/>',
    scissors:  '<circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M20 4L8.12 15.88M14.47 14.48L20 20M8.12 8.12L12 12"/>',
    screw:     '<path d="M12 3v14"/><path d="M8 6h8M8 9h8M8 12h8"/><path d="M12 17l-2 4h4z"/>',
    section:   '<path d="M4 4h16v16H4z"/><path d="M4 10h16"/><path d="M8 4v6"/><path d="M14 4v6"/>',
    slab:      '<path d="M2 9h20v5H2z"/><path d="M2 14v4M22 14v4"/>',
    sofa:      '<path d="M4 12V8a2 2 0 0 1 4 0v4"/><path d="M16 12V8a2 2 0 0 1 4 0v4"/><path d="M3 12h18v6H3z"/>',
    strip:     '<path d="M3 10h18v5H3z"/><path d="M3 18h18"/>',
    surface:   '<rect x="3" y="5" width="18" height="14" rx="1"/><path d="M3 9h18M3 13h18M3 17h18" opacity="0.55"/>',
    terrace:   '<rect x="3" y="8" width="18" height="12" rx="1"/><path d="M7 8v12M11 8v12M15 8v12"/>',
    trees:     '<path d="M7 4l3 6H4z"/><path d="M7 8l4 7H3z"/><path d="M7 15v5"/>' +
                   '<path d="M16 7l3 6h-6z"/><path d="M16 13v7"/>',
    upload:    '<path d="M12 16V4"/><path d="M7 9l5-5 5 5"/><path d="M4 20h16"/>',
    wallH:     '<path d="M3 12h18"/>',
    wallV:     '<path d="M12 3v18"/>',
    /* a load-bearing wall is the same wall drawn heavy (core-v346) */
    bearH:     '<path d="M3 12h18" stroke-width="4.5"/>',
    bearV:     '<path d="M12 3v18" stroke-width="4.5"/>',
    window:    '<rect x="3" y="4" width="18" height="16" rx="1"/><path d="M12 4v16M3 12h18"/>',
  };

  /* The full element. Size and weight are the caller's. */
  function icon(name, opts) {
    opts = opts || {};
    var d = ICON_PATHS[name];
    if (!d) return '';
    var sz = opts.size || 22;
    return '<svg viewBox="0 0 24 24" width="' + sz + '" height="' + sz + '" ' +
           'fill="' + (opts.fill || 'none') + '" stroke="' + (opts.stroke || 'currentColor') + '" ' +
           'stroke-width="' + (opts.weight || 2) + '" stroke-linecap="round" ' +
           'stroke-linejoin="round" aria-hidden="true">' + d + '</svg>';
  }

  /* Every name wrapped once, so a product that has always read a plain map can
     go on doing exactly that -- one line instead of thirty. */
  function iconSet(opts) {
    var out = {}, k;
    for (k in ICON_PATHS) if (ICON_PATHS.hasOwnProperty(k)) out[k] = icon(k, opts);
    return out;
  }

  /* --------------------------------------------------------------------------
     THE SHARED VOCABULARY.

     Every product was naming its own controls, so the same button was
     roof_gable in one file and roof_v1 in another and container_roof_1
     in a third -- three locale keys, three translations, three chances to
     disagree about what a gable is. Remapping the ADU by hand saved 23
     duplicates and would have to be done again for every product after it.

     THIS IS THE NAMING, NOT THE TEXT. Each entry is the locale KEY the site
     already uses plus the English fallback, and the product's own translator
     resolves it -- because only the product can reach Shopify's locale files.
     The asset decides WHAT a control is called; the theme decides how it reads
     in Czech.

     A product takes what it needs and nothing else: it never sees a key for a
     control it does not have, and it cannot invent a second name for one it
     does.
     -------------------------------------------------------------------------- */
  var STRINGS = {
    /* wizard steps -- the same steps in every house product, so the same words */
    step_place:      ['sauna_wiz_1',  'PLACE'],
    step_place_h:    ['sauna_wiz_h1', 'Tap the pin to find your garden'],
    step_layout_h:   ['sauna_wiz_h3', 'Size, walls, doors and rooms'],
    step_openings_h: ['sauna_wiz_h4', 'Pick one, then tap a wall'],
    step_roof:       ['sauna_wiz_6',  'ROOF & BASE'],
    step_roof_h:     ['sauna_wiz_h6', 'Shape, pitch, footings'],
    step_exterior:   ['sauna_wiz_7',  'EXTERIOR'],
    step_exterior_h: ['sauna_wiz_h7', 'Cladding and colour'],
    step_assembly:   ['sauna_wiz_8',  'ASSEMBLY'],
    step_assembly_h: ['sauna_wiz_h8', 'What the walls are made of'],

    /* roof shapes */
    roof_gable:  ['roof_v1', 'Gable'],
    roof_hip:    ['roof_v2', 'Hip'],
    roof_flat:   ['roof_v3', 'Flat'],
    roof_mono:   ['roof_v4', 'Mono-pitch'],
    roof_mansard:['roof_v5', 'Mansard'],

    /* foundations */
    base_slab:   ['base_v1', 'Concrete slab'],
    base_strips: ['base_v2', 'Strip footing'],
    base_pads:   ['base_v3', 'Concrete pads'],
    base_piers:  ['base_v4', 'Precast blocks'],
    base_screws: ['base_v5', 'Ground screws'],

    /* controls that appear in more than one product */
    address:   ['sauna_ui_address',  'Address'],
    scenery:   ['sauna_ui_scenery',  'Scenery'],
    cladding:  ['sauna_ui_cladding', 'Cladding'],
    envelope:  ['sauna_ui_envelope', 'Envelope'],
    wall_v:    ['sauna_ui_wall_v',   'Vertical wall'],
    wall_h:    ['sauna_ui_wall_h',   'Horizontal wall'],
    cuts:      ['sauna_ui_cuts',     'Cut handles'],
    all:       ['floorplan_filter_any', 'Any'],

    /* openings, whose catalogue lives here too */
    op_left:   ['view_left',  'Left'],
    op_right:  ['view_right', 'Right'],
    op_full:   ['op_full_wall', 'Full wall'],
    op_half:   ['op_half_wall', 'Half wall'],
    op_corner: ['op_corner',    'Corner'],
    op_hole:    ['op_hole',    'Hole'],
    op_opening: ['op_opening', 'Opening'],
    viz_focus:  ['viz_focus',  'Piece under review'],
    viz_rebar:  ['viz_rebar',  'Reinforcement'],

    /* THE SHORT WORD ON AN OPENING TILE. The catalogue's English name is the
       lookup, so a unit carries its own word and the drawer stays a menu --
       nothing here reaches the BoQ. The two wall-sized lights reuse the keys
       they already had. */
    'Single-leaf window':            ['op_name_single_leaf',       'Single leaf'],
    'Fixed window':                  ['op_name_fixed',             'Fixed'],
    'Fixed window, full height':     ['op_name_fixed_full_height', 'Fixed full height'],
    'Circular window':               ['ax_round',                  'Round'],
    'Fixed window, full wall':       ['op_full_wall',              'Full wall'],
    'Fixed window, half wall':       ['op_half_wall',              'Half wall'],
    'Roof window':                   ['op_name_roof_window',       'Roof window'],
    'Exterior door':                 ['op_name_exterior_door',     'Exterior'],
    'Double exterior door':          ['op_name_double_door',       'Double'],
    'Interior door':                 ['op_name_interior_door',     'Interior'],
    'Sauna door':                    ['rooms.sauna',               'Sauna'],
    'French door':                   ['op_name_french_door',       'French'],
    'HS sliding door':               ['op_name_hs_sliding',        'HS sliding'],
    'Sliding glazed door, full wall':['op_name_sliding_full_wall', 'Sliding full wall'],
    'Sectional garage door':         ['op_name_sectional_door',    'Sectional'],

    /* the interior fitout, which is the asset's catalogue as well */
    fit_kitchen: ['rooms.kitchen',    'Kitchen'],
    fit_bath:    ['rooms.bathroom',   'Bathroom'],
    fit_bedroom: ['rooms.bedroom',    'Bedroom'],
    fit_living:  ['rooms.livingroom', 'Living room'],
    fit_office:  ['rooms.office',     'Office'],
    fit_storage: ['rooms.storage',    'Storage']
  };

  /* Resolve one name through the product's translator. The product passes its
     own t() once, into kit(); nothing here reaches a locale file directly,
     because nothing here can. */
  function str(t, name) {
    var e = STRINGS[name];
    if (!e) return name;
    /* THE KEY GOES OVER BARE. I prefixed it with "configurator." here, which
       is how it reads in the locale FILE -- but a product's t() is handed a
       flat map already scoped to that section, so every lookup missed and
       every label fell back to English. The section is the caller's business;
       this passes the name it was given. */
    return (typeof t === 'function') ? t(e[0], e[1]) : e[1];
  }

  /* --------------------------------------------------------------------------
     COLOUR SCHEMES. The cycling, shared; the palette, a default.

     Every product grew its own version of the same control: an index in the
     state, a button that steps it, a badge with a number, a title saying which
     one of how many. The ADU never got one at all -- its rail carries the
     button and the snapshot has been reporting cf-btn-colors as NOT WIRED.

     WHAT IS SHARED IS THE MECHANISM, and it is worth being exact about why.
     Stepping an index and lighting a button is identical everywhere. A palette
     is not: shou-sugi-ban is charred cedar cladding and deck is a terrace
     colour, and a product without a terrace carries that field for nothing. So
     the table below is a DEFAULT, not a rule -- a product with its own passes
     it in and this steps that instead.

     THE FIRST SCHEME IS ON FROM THE START. A colour control that begins in no
     state at all makes the first press look like it did nothing: the building
     was already that colour. It starts at index 0, showing 1.
     -------------------------------------------------------------------------- */
  var SCHEMES_DEFAULT = [
    { key: 'nordic',        facade: 0xB9855A, wall: 0xD9D2C4, boardW: 0.190,
      pattern: 'wood1', deck: 0x9A7A57, roof: 0x33363b, roofPat: 'plain',   facCol: 'larch' },
    { key: 'shou-sugi-ban', facade: 0x1E1512, wall: 0xC9A6A0, boardW: 0.115,
      pattern: 'wood1', deck: 0x6B5A46, roof: 0x18181c, roofPat: 'shingle', facCol: 'shou', shou: true },
    { key: 'ivory',         facade: 0xE7E0CF, wall: 0xF0EADB, boardW: 0.160,
      pattern: 'wood2', deck: 0xC98A44, roof: 0x9a6242, roofPat: 'plain',   facCol: 'larch' }
  ];

  function schemes(opts) {
    opts = opts || {};
    var list = (opts.list && opts.list.length) ? opts.list : SCHEMES_DEFAULT;
    var idx  = Math.max(0, Math.min((opts.index | 0), list.length - 1));

    var api = {
      count:   function () { return list.length; },
      index:   function () { return idx; },
      current: function () { return list[idx]; },
      key:     function () { return list[idx].key; },

      /* one step on, wrapping, and the button told about it */
      next: function () {
        idx = (idx + 1) % list.length;
        api.paint();
        if (opts.onChange) opts.onChange(list[idx], idx);
        return api;
      },

      set: function (i) {
        idx = Math.max(0, Math.min(i | 0, list.length - 1));
        api.paint();
        return api;
      },

      /* THE BADGE IS THE ONLY THING THAT SAYS WHICH ONE.
         The tile stays the active colour rather than taking the scheme's own --
         a tile that changes colour with the scheme tells you a scheme changed
         and never which, and on a dark scheme it reads as switched off. */
      paint: function () {
        /* A PRODUCT MAY OWN THAT BUTTON FOR SOMETHING ELSE (the ADU's rail
           slot carries the section tool): button: false keeps the cycler's
           hands off it -- no aria-pressed, no badge, no title (ui-v107). */
        if (opts.button === false) return api;
        var b = $('cf-btn-colors');
        if (!b) return api;
        b.setAttribute('aria-pressed', 'true');
        var n = b.querySelector ? b.querySelector('.cf-viz-num') : null;
        if (n) n.textContent = String(idx + 1);
        if (opts.title) b.title = opts.title(list[idx], idx + 1, list.length);
        return api;
      }
    };

    api.paint();
    return api;
  }

  var PICKER_ID = 'cfg-ui-picker';

  function _pickerCss() {
    if (document.getElementById('cfg-ui-picker-css')) return;
    var st = document.createElement('style');
    st.id = 'cfg-ui-picker-css';
    st.appendChild(document.createTextNode([
      /* PINNED WITH INTENT, the same way the step drawer had to be. Every one
         of these carries !important because the last one that did not lost to a
         theme rule and spent three rounds looking like a bug in this file. */
      /* THE WIDTH IS MEASURED, NOT GUESSED.

         Eight tenths of the step drawer is the brief, and the step drawer's
         width comes from the theme's own stylesheet -- so any number written
         here is a guess about somebody else's CSS. I guessed 380 and shipped a
         drawer NARROWER than the one it sits over, which is the opposite of
         what was asked. It is measured when it opens and set inline; this rule
         only covers the case where there is nothing to measure. */
      '#' + PICKER_ID + '{position:fixed !important;top:0 !important;right:0 !important;',
      '  bottom:0 !important;height:100% !important;max-height:100% !important;',
      '  width:min(460px,92%) !important;z-index:100002 !important;',
      '  background:' + UX.surface + ' !important;display:flex !important;',
      '  flex-direction:column !important;visibility:visible !important;opacity:1 !important;',
      '  box-shadow:-8px 0 28px rgba(0,0,0,0.18);',
      '  transform:translateX(100%);transition:transform .24s ease;}',
      '#' + PICKER_ID + '.cfg-pk-open{transform:translateX(0) !important;}',
      '#' + PICKER_ID + ' .cfg-pk-chip{border-radius:999px;padding:6px 12px;cursor:pointer;',
      '  font:600 12px ' + UX.font + ';border:1px solid ' + UX.line + ';background:' + UX.surface + ';',
      '  color:' + UX.body + ';}',
      '#' + PICKER_ID + ' .cfg-pk-chip.on{background:' + UX.brown + ';border-color:' + UX.brown + ';color:#fff;}',
      '#' + PICKER_ID + ' .cfg-pk-tile{text-align:left;border:1px solid ' + UX.line + ';',
      '  border-radius:10px;background:' + UX.surface + ';padding:8px 10px 10px;cursor:pointer;}'
    ].join('\n')));
    document.head.appendChild(st);
  }

  function _pkThumb(it) {
    var TW = 116, TH = 76, pad = 8;
    /* A PIECE WITH ITS OWN DRAWING shows that drawing -- a top view of the
       thing, the same one the plan will show once it is placed -- instead of
       the blank rectangle an opening gets (ADU fitouts, ui-v109) */
    if (it.glyph) {
      /* the glyph keeps its own proportions (glyphVb), fitted into the thumb */
      var vb = (it.glyphVb || '24 24').split(' '), vw = parseFloat(vb[0]) || 24, vh = parseFloat(vb[1]) || 24;
      var maxW = TW - 2 * pad, maxH = TH - 2 * pad, sc = Math.min(maxW / vw, maxH / vh);
      var gw = vw * sc, gh = vh * sc, gx = (TW - gw) / 2, gy = (TH - gh) / 2;
      /* the stroke is set in the glyph's own units so it comes out the same
         on screen whatever the glyph's scale: one line weight for every tile */
      var swU = 1.3 / sc;
      return '<svg viewBox="0 0 ' + TW + ' ' + TH + '" width="100%" height="' + TH + '">' +
             '<svg x="' + gx + '" y="' + gy + '" width="' + gw + '" height="' + gh + '" viewBox="0 0 ' + vw + ' ' + vh + '" ' +
             'fill="none" stroke="' + UX.brown + '" stroke-width="' + swU.toFixed(3) + '" stroke-linecap="round" stroke-linejoin="round">' +
             it.glyph + '</svg></svg>';
    }
    /* zero by zero has no aspect: draw it as the wall it will become */
    /* the half-wall pane is TALL -- full height, half the length -- so its tile
       stands upright (core-v272; it was drawn as a wide strip, Marek) */
    var ar = it.full ? (it.full === 'half' ? 0.62 : 1.35)
                     : ((it.w || 1) / Math.max(0.05, it.h || 1));
    var w = TW - 2 * pad, h = TH - 2 * pad;
    if (w / h > ar) w = h * ar; else h = w / ar;
    var x = (TW - w) / 2, y = (TH - h) / 2;
    var ink = UX.brown, gl = '#cfe0e6';
    var g = '<svg viewBox="0 0 ' + TW + ' ' + TH + '" width="100%" height="' + TH + '">';
    if (it.sectional) {
      /* A SECTIONAL DOOR IS ITS PANELS. Drawn as one blank rectangle it looked
         like an oversized entry door, which is a different product entirely. */
      g += '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h +
           '" fill="#ffffff" stroke="' + ink + '" stroke-width="2"/>';
      var nP = 4, ph = h / nP, p2;
      for (p2 = 1; p2 < nP; p2++) {
        g += '<line x1="' + x + '" y1="' + (y + ph * p2) + '" x2="' + (x + w) +
             '" y2="' + (y + ph * p2) + '" stroke="' + ink + '" stroke-width="1.2"/>';
      }
    } else if (it.round) {
      /* A ROUND LIGHT IS A CIRCLE (core-v518): the glass in a ring, no
         mullions -- a porthole is one pane */
      var rr = Math.min(w, h) / 2;
      g += '<circle cx="' + (TW / 2) + '" cy="' + (TH / 2) + '" r="' + rr + '" fill="' + gl + '" stroke="' + ink + '" stroke-width="2"/>' +
           '<circle cx="' + (TW / 2) + '" cy="' + (TH / 2) + '" r="' + Math.max(1, rr - 5) + '" fill="none" stroke="' + ink + '" stroke-width="1.2"/>';
    } else if (it.solid) {
      g += '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h +
           '" fill="#ffffff" stroke="' + ink + '" stroke-width="2"/>' +
           '<rect x="' + (x + 4) + '" y="' + (y + 4) + '" width="' + Math.max(0, w - 8) +
           '" height="' + Math.max(0, h - 8) + '" fill="none" stroke="#b9ac97" stroke-width="1.2"/>' +
           '<rect x="' + x + '" y="' + (y + h - 3) + '" width="' + w + '" height="3" fill="' + ink + '"/>';
      /* THE HANDLES GO WHERE THE DOOR OPENS FROM. On a single leaf that is the
         side away from the hinge, which is what the hand names. On a pair it is
         the middle, both of them, either side of the meeting stile -- drawing
         one at an edge would be drawing a hand the door does not have. */
      if ((it.leaves || 1) > 1) {
        var cxm = x + w / 2;
        g += '<line x1="' + cxm + '" y1="' + y + '" x2="' + cxm + '" y2="' + (y + h) +
             '" stroke="' + ink + '" stroke-width="1.4"/>' +
             '<circle cx="' + (cxm - 4) + '" cy="' + (y + h * 0.55) + '" r="2.4" fill="' + ink + '"/>' +
             '<circle cx="' + (cxm + 4) + '" cy="' + (y + h * 0.55) + '" r="2.4" fill="' + ink + '"/>';
      } else {
        g += '<circle cx="' + ((it.hand === 'L') ? (x + w - 6) : (x + 6)) +
             '" cy="' + (y + h * 0.55) + '" r="2.6" fill="' + ink + '"/>';
      }
    } else {
      g += '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h +
           '" fill="' + gl + '" stroke="' + ink + '" stroke-width="2"/>';
      /* THE MULLIONS, WHERE THE UNIT HAS THEM. A 2.6 m fixed light and a
         three-part window are the same rectangle until the divisions are drawn,
         and they are not the same window or the same price. A unit with one
         leaf gets none, which is the whole point of saying so. */
      var nL = it.leaves || 1, q2;
      for (q2 = 1; q2 < nL; q2++) {
        var mx = x + (w / nL) * q2;
        g += '<line x1="' + mx + '" y1="' + y + '" x2="' + mx + '" y2="' + (y + h) +
             '" stroke="' + ink + '" stroke-width="1.4"/>';
      }
    }
    return g + '</svg>';
  }

  /* THE ICON STRIP'S LOOK, ONCE FOR EVERY PRODUCT (ui-v195): the core's
     strip and the strip a product on its own wizard gets (container,
     shelters, tiny) wear the same stylesheet. foot: the canvas bar's
     distance from the floor, which the arrows' banner shares.
     ui-v195 (Marek): the strip's cross on LEVA red; the colour dots in one
     line, shrinking to fit; the arrows' banner between two 70 px insets,
     so it is centred on the canvas axis and as wide as the canvas allows. */
  function stripCss(foot) {
    if ($('cf-sx-css')) return;
    var st = document.createElement('style');
    st.id = 'cf-sx-css';
    var M = "'Montserrat',sans-serif";
    /* the panels keep clear of the strip; the arrows' banner and its slider
       stand on the canvas axis, where the canvas bar stands (Marek) */
    var MID = 'calc(50% - 34px)';
    var INK = '#141516', RED = '#D8232A', GOLD = '#D2A878';
    st.textContent =
      '#cf-root.cf-sx-open .cf-sidebar{display:none !important;visibility:hidden !important;pointer-events:none !important;}' +
      /* AT ONCE (ui-v215, Marek: kdyz se zapne, je jeste chvili videt svetly
         pod nim): the rail's buttons carry transition:all 0.18s, visibility
         with it, so they lingered white round the dark strip for a moment --
         the rail and all inside it go with no transition */
      '.cf-sx-open .cf-sidebar,.cf-sx-open .cf-sidebar *{visibility:hidden !important;transition:none !important;}' +
      '#cf-root.cf-sx-open #cf-house-pitch-row,#cf-root.cf-sx-open #cf-house-height-row,#cf-root.cf-sx-open #cf-house-floor-row,' +
      '#cf-root.cf-sx-open #cf-house-storey-row,#cf-root.cf-sx-open #cf-postwall-gap-row{display:none !important;}' +
      /* the own-wizard products' floating sliders (shelter pitch, tiny pitch)
         would sit on the arrows' banner: the strip shows its own copy (ui-v200) */
      '#cf-root.cf-sx-open > .cfg-ui-slider-row{display:none !important;}' +
      /* the canvas bar beside the plan strip keeps clear of it on a phone --
         ON THE CANVAS AXIS as the arrows' banner is (core-v601, Marek: pri
         zapnuti edit floorplan banneru musi byt take na ose obrazovky): it
         stays centred and narrows by the strip's width on both sides */
      '@media (max-width:640px){#cf-root.cf-sx-plan #cf-cmdbar{max-width:calc(100% - 152px);}}' +
      /* THE STRIP IS INVERSE (core-v593): dark, the glyphs white */
      /* SHORTER, WITH ROOM AT BOTH ENDS (ui-v211, Marek: tmave banery kratsi
         a bude se scrollovat, at nekoliduje s otevrenym floorplan spodnim
         bannerem na mobilu, at neni od kraje section do konce): at most
         about eight icons tall (470 px), never closer than 24 px to the
         canvas's top and bottom; on a phone or a split screen (640 px and
         under) it keeps above the canvas bar and the arrows' banner -- 60 px
         from the top, 92 px from the bottom. More icons scroll, a fade at
         the end that has more. */
      '#cf-sx-strip{position:absolute;right:12px;top:50%;transform:translateY(-50%);z-index:13;display:flex;flex-direction:column;align-items:center;gap:6px;' +
      'padding:6px;border-radius:14px;background:' + INK + ';box-shadow:0 8px 28px rgba(0,0,0,0.30);' +
      'max-height:calc(100% - 48px);max-height:min(470px,calc(100% - 48px));box-sizing:border-box;font-family:' + M + ';}' +
      '@media (max-width:640px){#cf-sx-strip{top:calc(50% - 16px);max-height:calc(100% - 152px);max-height:min(470px,calc(100% - 152px));}}' +
      '#cf-sx-strip .cf-sx-list{display:flex;flex-direction:column;align-items:center;gap:6px;overflow-y:auto;overflow-x:hidden;min-height:0;scrollbar-width:none;' +
      /* core-v690 (Marek: cerne banery napravo se skroluji a nejde za nimi
         skrolovat stranka): the icons' own scroll hands the swipe on to the
         page at its end, or at once when every icon fits (was contain) */
      '-webkit-overflow-scrolling:touch;overscroll-behavior-y:auto;touch-action:pan-y;}' +
      '#cf-sx-strip .cf-sx-list::-webkit-scrollbar{display:none;}' +
      /* MORE BELOW: THE LAST ICON HALF SHOWN AND A WHITE ARROW UNDER IT
         (ui-v212, Marek: polovina ikony, at je videt ze banner pokracuje; dolu
         mala bila sipka, sem tam muze pulsovat) -- stripFit cuts the list
         through the middle of the last icon it can show; the arrow nudges
         now and then, a tap on it scrolls a screenful, at the end it fades */
      '#cf-sx-strip .cf-sx-dn{display:none;flex:0 0 auto;width:30px;height:16px;margin:-2px 0 0;border:0;padding:0;background:transparent;color:#ffffff;cursor:pointer;' +
      'align-items:center;justify-content:center;opacity:0.9;transition:opacity 200ms;-webkit-tap-highlight-color:transparent;touch-action:manipulation;}' +
      '#cf-sx-strip.cf-sx-over .cf-sx-dn{display:inline-flex;}' +
      '#cf-sx-strip.cf-sx-over:not(.cf-sx-more) .cf-sx-dn{opacity:0;pointer-events:none;}' +
      '#cf-sx-strip .cf-sx-dn svg{width:16px;height:16px;display:block;animation:cfSxNudge 4.5s ease-in-out infinite;}' +
      '@keyframes cfSxNudge{0%,76%,100%{transform:translateY(0);opacity:0.7;}82%{transform:translateY(4px);opacity:1;}88%{transform:translateY(0);opacity:0.85;}94%{transform:translateY(3px);opacity:1;}}' +
      '@media (prefers-reduced-motion:reduce){#cf-sx-strip .cf-sx-dn svg{animation:none;}}' +
      '#cf-sx-strip.cf-sx-less::before{content:"";position:absolute;z-index:1;left:6px;right:6px;top:44px;height:22px;pointer-events:none;' +
      'background:linear-gradient(to top,rgba(20,21,22,0),' + INK + ');}' +
      '#cf-sx-strip button{-webkit-tap-highlight-color:transparent;touch-action:manipulation;font-family:' + M + ';}' +
      /* THE CROSS ON WHITE (ui-v201-ghostoff, Marek: krizek bily background, at se
         odlisi od tlacitek s cervenym pozadim) -- red is what ON and a locked
         plan wear; the cross keeps the red in its stroke only */
      '#cf-sx-strip .cf-sx-x{flex:0 0 auto;width:32px;height:32px;margin:2px 0 4px;border-radius:50%;border:0;padding:0;background:#ffffff;color:' + RED + ';' +
      'box-shadow:0 2px 8px rgba(0,0,0,0.30);' +
      'display:inline-flex;align-items:center;justify-content:center;cursor:pointer;}' +
      '#cf-sx-strip .cf-sx-x svg{width:14px;height:14px;display:block;}' +
      '#cf-sx-strip .cf-sx-b{flex:0 0 auto;position:relative;width:44px;height:44px;border-radius:11px;border:0;padding:0;margin:0;background:rgba(255,255,255,0.06);color:#ffffff;' +
      'display:inline-flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:inset 0 0 0 1px rgba(255,255,255,0.14);transition:background 120ms,color 120ms,box-shadow 120ms,opacity 120ms;}' +
      '#cf-sx-strip .cf-sx-b svg{width:22px;height:22px;display:block;}' +
      '#cf-sx-strip .cf-sx-b img{width:36px;height:36px;object-fit:cover;border-radius:7px;display:block;}' +
      '#cf-sx-strip .cf-sx-num{font:700 16px/1 ' + M + ';}' +
      /* ON IS RED, OFF IS FADED WITH A HOLLOW DOT, THE PICKED ONE GOLD
         (ui-v212, Marek: ikony ktere spousti cycler banner a vypina je jina
         ikona -- roof atd. -- nase zlata; on / off -- bazen atd. -- cervena) */
      '#cf-sx-strip .cf-sx-b.cf-sx-tg svg{opacity:0.5;}' +
      '#cf-sx-strip .cf-sx-b.cf-sx-tg::after{content:"";position:absolute;right:4px;top:4px;width:7px;height:7px;border-radius:50%;box-sizing:border-box;border:1.5px solid rgba(255,255,255,0.55);background:transparent;}' +
      '#cf-sx-strip .cf-sx-b.cf-on{background:' + RED + ';color:#ffffff;box-shadow:none;}' +
      '#cf-sx-strip .cf-sx-b.cf-on svg{opacity:1;}' +
      '#cf-sx-strip .cf-sx-b.cf-on::after{border-color:#ffffff;background:#ffffff;}' +
      '#cf-sx-strip .cf-sx-b.cf-sel{background:' + GOLD + ';color:#ffffff;box-shadow:none;}' +
      '#cf-sx-strip .cf-sx-b.cf-sel svg{opacity:1;}' +
      '#cf-sx-strip .cf-sx-b.cf-on.cf-sel{background:' + RED + ';color:#ffffff;box-shadow:inset 0 0 0 2px #ffffff;}' +
      '#cf-sx-strip .cf-sx-b.cf-dim{opacity:0.38;}' +
      '#cf-sx-strip .cf-sx-sep{flex:0 0 auto;width:28px;height:1px;margin:2px 0;background:rgba(255,255,255,0.18);}' +
      '@media (hover:hover){#cf-sx-strip .cf-sx-b:hover{box-shadow:inset 0 0 0 1.5px #ffffff;}}' +
      '#cf-sx-tip{position:absolute;z-index:16;display:none;padding:7px 11px;border-radius:999px;background:#0A0A0A;color:#ffffff;white-space:nowrap;pointer-events:none;' +
      'font:700 11px/1 ' + M + ';letter-spacing:0.08em;text-transform:uppercase;transform:translateY(-50%);box-shadow:0 4px 14px rgba(0,0,0,0.22);}' +
      '#cf-sx-tip.cf-on{display:block;}' +
      '#cf-sx-tip::after{content:"";position:absolute;right:-5px;top:50%;width:10px;height:10px;margin-top:-5px;background:#0A0A0A;transform:rotate(45deg);border-radius:2px;}' +
      /* THE ARROWS ON THE CANVAS AXIS, BLACK (core-v593) */
      '#cf-sx-bar{position:absolute;left:70px;right:70px;bottom:' + (foot != null ? foot : 25) + 'px;z-index:12;display:flex;flex-direction:column;align-items:center;gap:8px;' +
      'pointer-events:none;font-family:' + M + ';}' +
      '#cf-sx-bar > *{pointer-events:auto;}' +
      '#cf-sx-bar .cf-sx-pill,#cf-sx-bar .cf-sx-dots{display:flex;align-items:center;gap:10px;padding:8px 10px;border-radius:999px;background:#ffffff;' +
      'box-shadow:0 6px 24px rgba(0,0,0,0.14),0 0 0 1px rgba(0,0,0,0.05);box-sizing:border-box;max-width:100%;}' +
      '#cf-sx-bar .cf-sx-dots{flex-wrap:nowrap;justify-content:center;gap:8px;padding:9px 12px;}' +
      '#cf-sx-bar button{-webkit-tap-highlight-color:transparent;touch-action:manipulation;}' +
      '#cf-sx-bar .cf-sx-arw{flex:0 0 auto;width:36px;height:36px;border-radius:50%;border:0;padding:0;margin:0;background:#1A1C1D;color:#ffffff;' +
      'display:inline-flex;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 4px 14px rgba(0,0,0,0.16);}' +
      '#cf-sx-bar .cf-sx-arw svg{width:18px;height:18px;display:block;}' +
      '#cf-sx-bar .cf-sx-lbl{flex:0 1 auto;min-width:76px;padding:0 4px;text-align:center;font:700 12px/1.2 ' + M + ';letter-spacing:0.08em;text-transform:uppercase;' +
      'color:#1A1C1D;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}' +
      /* the option over the arrows: a dark chip, so it reads over any model */
      '#cf-sx-bar .cf-sx-cur{max-width:100%;padding:7px 13px;border-radius:999px;background:#1A1C1D;color:#ffffff;box-sizing:border-box;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;' +
      'font:700 11px/1 ' + M + ';letter-spacing:0.08em;text-transform:uppercase;box-shadow:0 4px 14px rgba(0,0,0,0.18);}' +
      '#cf-sx-bar .cf-sx-cur:empty{display:none;}' +
      '#cf-sx-bar .cf-sx-dot{flex:0 1 26px;width:26px;min-width:12px;height:auto;aspect-ratio:1/1;border-radius:50%;border:1px solid rgba(0,0,0,0.14);padding:0;margin:0;cursor:pointer;}' +
      '#cf-sx-bar .cf-sx-dot.cf-on{box-shadow:0 0 0 3px #ffffff,0 0 0 4px #2C2D2E;}' +
      '#cf-sx-sl{position:absolute;left:70px;right:70px;bottom:3px;z-index:12;display:flex;justify-content:center;pointer-events:none;}' +
      '#cf-sx-sl > *{pointer-events:auto;}' +
      /* two thumbs stand over the title pill, their sizes between (core-v628) */
      '#cf-sx-sl.cf-sx-sl2{bottom:' + ((foot != null ? foot : 25) + 36) + 'px;}' +
      '.cf-sx-srow{position:relative;}' +
      '.cf-sx-srow.cf-off{opacity:0.38;}' +
      '.cf-sx-stt{font:700 10px/1.2 ' + M + ';letter-spacing:0.08em;text-transform:uppercase;color:#6B7280;margin:0 0 6px;}' +
      '.cf-sx-sline{display:flex;align-items:center;gap:8px;}' +
      '.cf-sx-range{width:min(220px,44vw);height:18px;margin:0;padding:0;-webkit-appearance:none;appearance:none;outline:none;border:0;cursor:pointer;touch-action:none;' +
      '-webkit-tap-highlight-color:transparent;background-color:transparent;background-repeat:no-repeat;background-position:center;background-size:100% 3px;' +
      'background-image:linear-gradient(to right,#D2A878 0%,#D2A878 50%,rgba(0,0,0,0.13) 50%,rgba(0,0,0,0.13) 100%);}' +
      '.cf-sx-range::-webkit-slider-runnable-track{background:transparent;border:0;height:18px;}' +
      '.cf-sx-range::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;box-sizing:border-box;width:16px;height:16px;margin-top:1px;border-radius:50%;background:#D2A878;' +
      'border:2px solid #ffffff;box-shadow:0 1px 3px rgba(0,0,0,0.25);cursor:grab;}' +
      '.cf-sx-range::-moz-range-track{background:transparent;border:0;height:3px;}' +
      '.cf-sx-range::-moz-range-thumb{box-sizing:border-box;width:16px;height:16px;border-radius:50%;background:#D2A878;border:2px solid #ffffff;box-shadow:0 1px 3px rgba(0,0,0,0.25);cursor:grab;}' +
      '.cf-sx-range:disabled{cursor:default;}' +
      /* two thumbs on one track (core-v628): each its own colour, its size under it */
      '.cf-sx-dual{position:relative;width:min(280px,56vw);height:62px;}' +
      '.cf-sx-dual .cf-sx-dtr{position:absolute;left:8px;right:8px;top:8px;height:3px;border-radius:2px;background:rgba(0,0,0,0.13);pointer-events:none;}' +
      '.cf-sx-dual .cf-sx-range{position:absolute;left:0;top:0;width:100%;background-image:none;pointer-events:none;z-index:2;}' +
      '.cf-sx-dual .cf-sx-range::-webkit-slider-thumb{pointer-events:auto;background:var(--cf-th,#D2A878);}' +
      '.cf-sx-dual .cf-sx-range::-moz-range-thumb{pointer-events:auto;background:var(--cf-th,#D2A878);}' +
      '.cf-sx-dual .cf-sx-range.cf-off{opacity:0.45;}' +
      '.cf-sx-dual .cf-sx-dv{position:absolute;top:22px;transform:translateX(-50%);padding:1px 7px;border-radius:6px;background:rgba(255,255,255,0.96);box-shadow:0 1px 4px rgba(0,0,0,0.12);' +
      'border-bottom:3px solid #D2A878;font:700 12px/1.35 ' + M + ';color:#1A1C1D;white-space:nowrap;pointer-events:none;}' +
      '.cf-sx-val{flex:0 0 auto;min-width:44px;padding:1px 7px;border-radius:6px;background:rgba(255,255,255,0.96);box-shadow:0 1px 4px rgba(0,0,0,0.12);' +
      'font:700 12px/1.35 ' + M + ';color:#1A1C1D;text-align:center;white-space:nowrap;}' +
      '#cf-sx-modal{position:absolute;z-index:14;top:16px;left:' + MID + ';transform:translateX(-50%);width:min(340px,calc(100% - 96px));max-height:calc(100% - 120px);' +
      'display:flex;flex-direction:column;background:#ffffff;border-radius:16px;box-shadow:0 12px 40px rgba(0,0,0,0.22),0 0 0 1px rgba(0,0,0,0.05);overflow:hidden;' +
      'font-family:' + M + ';box-sizing:border-box;}' +
      /* ON A PHONE THE PANEL STANDS IN THE LOWER THIRD, ON THE AXIS (ui-v214,
         Marek: plovouci modal na mobilu defaultne ve spodni tretine na stred);
         moved by its handle it stays where it was put */
      '@media (max-width:640px){#cf-sx-modal{top:auto;bottom:24px;max-height:calc(100% - 132px);}}' +
      /* THE ADDRESS IN THE PLACE PANEL (ui-v214, Marek: vlozeni adresy hned
         pri zapnuti location ikony, pod polem ikony exterior fitouts -- jeden
         modal): the map's own address dialog borrowed and laid flat, as the
         drawer's step panel did -- the geocoder, the suggestions and the load
         keep their wiring; no title, no cancel; a suggestion loads at once */
      '#cf-sx-modal .cf-sx-addr{flex:0 0 auto;padding:12px 14px 2px;}' +
      '#cf-sx-modal .cf-sx-addr .cf-map-modal-backdrop{position:static !important;display:block !important;background:none !important;opacity:1 !important;' +
      'inset:auto !important;padding:0 !important;z-index:auto !important;}' +
      '#cf-sx-modal .cf-sx-addr .cf-map-modal{position:static !important;transform:none !important;width:100% !important;max-width:100% !important;margin:0 !important;' +
      'box-shadow:none !important;border:none !important;padding:0 !important;background:none !important;border-radius:0 !important;}' +
      '#cf-sx-modal .cf-sx-addr .cf-map-modal h3,#cf-sx-modal .cf-sx-addr .cf-map-modal-btn-cancel{display:none !important;}' +
      '#cf-sx-modal .cf-sx-addr input{font-size:16px;border-radius:10px;}' +
      '#cf-sx-modal .cf-sx-addr input:focus{border-color:' + GOLD + ';}' +
      '#cf-sx-modal .cf-sx-addr .cf-map-modal-suggestions{position:static;margin-top:6px;max-height:180px;box-shadow:none;}' +
      '#cf-sx-modal .cf-sx-addr .cf-map-modal-buttons{margin-top:8px;}' +
      '#cf-sx-modal .cf-sx-addr .cf-map-modal-btn-ok{background:#1A1C1D;color:#ffffff;border-radius:10px;}' +
      '#cf-sx-modal .cf-sx-mh{flex:0 0 auto;display:flex;align-items:center;gap:10px;padding:10px 12px;border-bottom:1px solid rgba(0,0,0,0.08);cursor:grab;touch-action:none;user-select:none;-webkit-user-select:none;}' +
      '#cf-sx-modal .cf-sx-grip{flex:0 0 auto;width:34px;height:34px;border-radius:50%;border:0;padding:0;background:#1A1C1D;color:#ffffff;display:inline-flex;' +
      'align-items:center;justify-content:center;cursor:grab;touch-action:none;box-shadow:0 3px 10px rgba(0,0,0,0.14);}' +
      '#cf-sx-modal .cf-sx-grip svg{width:18px;height:18px;display:block;}' +
      '#cf-sx-modal .cf-sx-mt{flex:1 1 auto;min-width:0;font:700 12px/1.2 ' + M + ';letter-spacing:0.08em;text-transform:uppercase;color:#1A1C1D;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}' +
      '#cf-sx-modal .cf-sx-mx{flex:0 0 auto;width:30px;height:30px;border-radius:50%;border:0;padding:0;background:transparent;color:#6B7280;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;}' +
      '#cf-sx-modal .cf-sx-mx svg{width:14px;height:14px;display:block;}' +
      '#cf-sx-modal .cf-sx-mb{flex:1 1 auto;overflow-y:auto;padding:4px 14px 14px;-webkit-overflow-scrolling:touch;}' +
      '#cf-sx-modal .cf-sx-msl{display:flex;flex-direction:column;gap:14px;padding-top:10px;}' +
      '#cf-sx-modal .cf-sx-msl:empty{display:none;}' +
      '#cf-sx-modal .cf-sx-range{width:100%;}' +
      '#cf-sx-modal .cf-sx-sline .cf-sx-range{flex:1 1 auto;}' +
      /* the bin beside the window or door tapped (core-v593) */
      '#cf-op-nearbin{position:absolute;z-index:15;width:44px;height:44px;margin:-22px 0 0 -22px;border-radius:50%;border:2px solid #ffffff;padding:0;background:' + RED + ';color:#ffffff;' +
      'display:none;align-items:center;justify-content:center;cursor:pointer;box-shadow:0 4px 16px rgba(0,0,0,0.28);-webkit-tap-highlight-color:transparent;touch-action:manipulation;}' +
      '#cf-op-nearbin svg{width:20px;height:20px;display:block;}' +
      '#cf-op-nearbin.cf-on{display:flex;}';
    document.head.appendChild(st);
  }
  /* the fades and the arrow follow the scroll */
  function stripMore(el, list) {
    if (!el || !list) return;
    el.classList.toggle('cf-sx-more', list.scrollHeight - list.scrollTop - list.clientHeight > 4);
    el.classList.toggle('cf-sx-less', list.scrollTop > 4);
  }
  /* THE LIST CUT THROUGH THE MIDDLE OF AN ICON (ui-v212). When the icons
     do not all fit, the list's height is set so the last one it shows is
     half shown, and the arrow below it is let in. Measured again when the
     icons or the canvas's height change (a resize too); force to redo.
     (ui-v215 sized it to the light rail and opened the gaps; ui-v216 takes
     that back, Marek: zadne vetsi mezery, zadne prazdne misto -- the rail
     now hides at once, so nothing shows round the strip anyway.) */
  function stripFit(el, list, force) {
    if (!el || !list) return;
    var dn = el.querySelector('.cf-sx-dn');
    if (!dn) {
      dn = document.createElement('button');
      dn.type = 'button';
      dn.className = 'cf-sx-dn';
      dn.setAttribute('aria-hidden', 'true');
      dn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>';
      dn.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var by = Math.max(40, list.clientHeight - 30);
        try { list.scrollBy({ top: by, behavior: 'smooth' }); } catch (eS) { list.scrollTop += by; }
      });
      el.appendChild(dn);
    }
    var host = el.parentNode, key = list.children.length + ':' + (host ? host.clientHeight : 0) + ':' + (global.innerHeight || 0);
    if (!force && el.__cfFit === key) { stripMore(el, list); return; }
    el.__cfFit = key;
    list.style.maxHeight = '';
    el.classList.add('cf-sx-over');
    var avail = list.clientHeight;
    if (list.scrollHeight - avail <= 4) { el.classList.remove('cf-sx-over'); stripMore(el, list); return; }
    var kids = list.children, cut = 0, i;
    for (i = 0; i < kids.length; i++) {
      var k = kids[i], h = k.offsetHeight;
      if (!h || h < 20) continue;            /* a separator is no icon */
      var top = k.offsetTop - list.offsetTop;
      if (top + h / 2 <= avail) cut = top + h / 2; else break;
    }
    if (cut > 0) list.style.maxHeight = Math.round(cut) + 'px';
    stripMore(el, list);
  }
  (function () {
    var tR = 0;
    try {
      global.addEventListener('resize', function () {
        if (tR) global.clearTimeout(tR);
        tR = global.setTimeout(function () {
          tR = 0;
          var el = $('cf-sx-strip'), list = el ? el.querySelector('.cf-sx-list') : null;
          if (el && list) stripFit(el, list, true);
        }, 150);
      });
    } catch (eR) {}
  })();

  function picker(opts) {
    opts = opts || {};
    var items = opts.items || [];
    var host = document.getElementById('cf-root') || document.body;
    _pickerCss();

    var old = document.getElementById(PICKER_ID);
    if (old && old.parentNode) old.parentNode.removeChild(old);

    var d = document.createElement('div');
    d.id = PICKER_ID;
    d.className = 'cfg-ui-picker';
    d.setAttribute('tabindex', '-1');
    d.innerHTML =
      '<div style="display:flex;align-items:center;justify-content:space-between;padding:20px 22px 0;">' +
      '<div data-pk-title style="font:700 17px ' + UX.font + ';color:' + UX.ink + ';"></div>' +
      '<button type="button" data-pk-close style="border:none;background:transparent;' +
      'font-size:20px;cursor:pointer;color:' + UX.muted + ';">\u2715</button>' +
      '</div>' +
      '<div data-pk-filter style="display:flex;flex-wrap:wrap;gap:8px;padding:14px 22px 0;"></div>' +
      '<div style="padding:14px 22px 24px;overflow-y:auto;flex:1;">' +
      '<div data-pk-grid style="display:grid;grid-template-columns:repeat(auto-fill,minmax(132px,1fr));gap:10px;"></div>' +
      '</div>';
    /* the title node by id rather than by shape, so a change to the markup
       cannot silently leave the drawer untitled -- or throw and stop it opening */
    var tEl = d.querySelector('[data-pk-title]');
    if (tEl) tEl.textContent = opts.title || '';

    var catcher = null, closed = false;
    function close() {
      if (closed) return;
      closed = true;
      d.classList.remove('cfg-pk-open');
      if (catcher && catcher.parentNode) catcher.parentNode.removeChild(catcher);
      /* nothing to unset: the floating bar sits under both of these on
         z-index alone, so neither panel signals the page any more */
      setTimeout(function () {
        if (d.parentNode) d.parentNode.removeChild(d);
        if (opts.onClose) opts.onClose();
      }, 240);
    }
    d.querySelector('[data-pk-close]').addEventListener('click', close);

    var wSel = null, hSel = null;
    /* TABS, ONE PER KIND (ui-v192, the icon strip: jedna ikona openings, se
       zalozkami Windows / Doors / Garage doors). opts.tabs [{ label }] with
       opts.tab the one open; opts.onTab(i) answers { items, title } for the
       tab tapped, and the same drawer redraws with them -- no second drawer
       sliding in over the first. */
    var tabs = (opts.tabs && opts.tabs.length > 1) ? opts.tabs : null;
    var tabIdx = Math.max(0, Math.min(tabs ? tabs.length - 1 : 0, opts.tab | 0));
    function draw() {
      var fEl = d.querySelector('[data-pk-filter]');
      var grid = d.querySelector('[data-pk-grid]');
      fEl.innerHTML = '';
      grid.innerHTML = '';

      var i, hasHand = false;
      for (i = 0; i < items.length; i++) if (items[i].hand) hasHand = true;

      function chip(on, lab, click) {
        var c = document.createElement('button');
        c.type = 'button';
        c.className = 'cfg-pk-chip' + (on ? ' on' : '');
        c.textContent = lab;
        c.addEventListener('click', click);
        return c;
      }

      if (tabs) {
        var rowT = document.createElement('div');
        rowT.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;width:100%;margin-bottom:6px;padding-bottom:10px;border-bottom:1px solid rgba(0,0,0,0.08);';
        tabs.forEach(function (tb, ti) {
          rowT.appendChild(chip(ti === tabIdx, tb.label || '', function () {
            if (ti === tabIdx) return;
            var nx = null;
            try { nx = opts.onTab ? opts.onTab(ti) : null; } catch (eTb) { nx = null; }
            if (!nx) return;
            tabIdx = ti;
            items = nx.items || [];
            if (nx.title && tEl && !opts.keepTitle) tEl.textContent = nx.title;
            wSel = null; hSel = null;
            draw();
          }));
        });
        fEl.appendChild(rowT);
      }

      /* HAND FIRST, because on a door list it halves what you are looking at
         and every other filter is a refinement of that. It only appears where
         the catalogue actually carries handed items -- on windows there is
         nothing to hand and the row would be noise. */
      if (hasHand && !tabs) {
        var row1 = document.createElement('div');
        row1.style.cssText = 'display:flex;gap:6px;width:100%;margin-bottom:2px;';
        row1.appendChild(chip(hSel == null, opts.allLabel || 'All',
          function () { hSel = null; draw(); }));
        row1.appendChild(chip(hSel === 'L', opts.leftLabel || 'Left',
          function () { hSel = 'L'; draw(); }));
        row1.appendChild(chip(hSel === 'R', opts.rightLabel || 'Right',
          function () { hSel = 'R'; draw(); }));
        fEl.appendChild(row1);
      }

      /* AN ITEM WITH NO HAND IS NOT A MATCH FOR EITHER. A double door and a
         full-wall slider have no hinge side, so they belong under All and
         nowhere else -- showing them under Left would be answering a question
         they do not have an answer to. */
      var handed = items;
      if (hSel != null) handed = items.filter(function (it) { return it.hand === hSel; });

      var seen = {}, widths = [];
      for (i = 0; i < handed.length; i++) {
        var k = (handed[i].w || 0).toFixed(3);
        if (!seen[k]) { seen[k] = 1; widths.push(handed[i].w); }
      }
      widths.sort(function (a, b) { return a - b; });

      /* only worth a width row when there is something to filter -- and not
         at all when the caller says the list is short enough to just read */
      if (widths.length > 3 && !opts.noFilter && !tabs) {
        var row2 = document.createElement('div');
        row2.style.cssText = 'display:flex;flex-wrap:wrap;gap:6px;width:100%;';
        row2.appendChild(chip(wSel == null, opts.allLabel || 'All',
          function () { wSel = null; draw(); }));
        for (i = 0; i < widths.length; i++) {
          (function (wv) {
            row2.appendChild(chip(wSel != null && Math.abs(wv - wSel) < 1e-4,
              opts.fmt ? opts.fmt(wv) : ('' + wv),
              function () { wSel = wv; draw(); }));
          })(widths[i]);
        }
        fEl.appendChild(row2);
      }

      var list = handed;
      if (wSel != null) list = handed.filter(function (it) { return Math.abs(it.w - wSel) < 1e-4; });

      for (i = 0; i < list.length; i++) {
        (function (it) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'cfg-pk-tile';
          b.innerHTML = _pkThumb(it) +
            (it.name ? ('<div style="font:600 13px ' + UX.font + ';color:' + UX.ink +
                        ';line-height:1.25;">' + it.name + '</div>') : '') +
            (it.sub ? ('<div style="font:500 12px ' + UX.font + ';color:' + UX.muted +
                       ';margin-top:2px;">' + it.sub + '</div>') : '') +
            (it.code ? ('<div style="font:500 11px ' + UX.font + ';color:' + UX.muted +
                        ';margin-top:3px;">' + it.code + (it.hand ? ('  ' + it.hand) : '') + '</div>') : '');
          b.addEventListener('click', function () {
            close();
            if (opts.onPick) opts.onPick(it);
          });
          grid.appendChild(b);
        })(list[i]);
      }
    }
    draw();

    /* A TAP ANYWHERE ELSE CLOSES IT.

       A drawer that only closes by its own cross is a drawer people leave open
       and then work around, and on a tablet the cross is the smallest thing on
       screen. The catcher sits under the panel and over everything else, so a
       tap on the model, on the step drawer behind, or on the page dismisses it
       -- and taps inside the panel never reach it. */
    catcher = document.createElement('div');
    catcher.style.cssText =
      'position:fixed;inset:0;z-index:100001;background:transparent;';
    catcher.addEventListener('pointerdown', function (e) {
      e.preventDefault();
      e.stopPropagation();
      close();
    }, true);

    /* ON THE BODY, NOT IN THE CONFIGURATOR. A fixed element inside a
       transformed ancestor is fixed to that ancestor, not the window -- the
       same trap the address modal fell into. */
    (document.body || host).appendChild(catcher);
    (document.body || host).appendChild(d);

    /* eight tenths of whatever the step drawer actually came out at */
    try {
      var stepDr = document.getElementById('cfg-ui-drawer');
      var wpx = stepDr ? stepDr.offsetWidth : 0;
      if (wpx > 120) d.style.width = Math.round(wpx * 0.8) + 'px';
    } catch (eW) {}
    setTimeout(function () { d.classList.add('cfg-pk-open'); }, 16);
    return { close: close };
  }

  /* --------------------------------------------------------------------------
     THE KIT. One call, and a product has this interface.

     Everything a configurator needs from here was already available, but as
     four separate calls a new product had to know about and wire up in the
     right order -- which is a recipe, not a library. A recipe gets copied,
     copied recipes drift, and drift is the thing this file exists to stop.

     A product now says what it OFFERS and gets the interface back:

       var KIT = ConfiguratorUI.kit({
         t:        _t,                       translator, for the picker labels
         fmtLen:   fmtLen,                   how this product writes a size
         groups:   GROUPS,                   key -> [{ label, items }]
         openings: ['window','door'],        which catalogue groups to sell
         onPick:   function (spec) { ... }   what to do with the chosen unit
       });

     and uses three things:

       KIT.icons                the glyph set, filled and ready
       KIT.panel(pane, key)     the step's edit panel, labelled rows of tiles
       KIT.openPicker(kind)     the openings drawer for one catalogue group

     Nothing above is a style decision the product can get wrong, because there
     are no style decisions left in it -- only what is on offer, which is the
     one thing a product is actually for.
     -------------------------------------------------------------------------- */
  /* the corner window's tile: two panes meeting at the corner post, seen
     from outside the corner (core-v751) */
  var CORNER_GLYPH =
    '<path d="M3 9L24 4L24 29L3 25Z" fill="#cfe0e6"/><path d="M24 4L45 9L45 25L24 29Z" fill="#cfe0e6"/>' +
    '<path d="M13.5 6.5L13.5 27M34.5 6.5L34.5 27"/><path d="M24 2.5V30.5" stroke-width="1.4"/>';
  function kit(opts) {
    opts = opts || {};
    var t = opts.t || function (k, d) { return d; };
    var fmtLen = opts.fmtLen || function (m) { return m.toFixed(2) + ' m'; };

    /* THE GLYPHS ARE FILLED IN PLACE, NOT RETURNED FRESH.

       A product keeps this object for the life of the page and reads it from
       inside functions that run much later, so handing back a new object each
       time would leave the first one -- the one it kept -- empty. */
    var icons = {};
    (function () {
      var set = iconSet({ weight: opts.weight || 2 }), k;
      for (k in set) if (set.hasOwnProperty(k)) icons[k] = set[k];
    })();

    function panel(pane, key) {
      var groups = (typeof opts.groups === 'function') ? opts.groups(key) : (opts.groups || []);

      /* THE WIZARD IS LOOKED UP NOW, NOT WHEN THE KIT WAS MADE.

         Taking it once at construction assumed the kit is built after the
         wizard, and it is not: the icon set is wanted during boot, so the kit
         comes first and the reference was null forever. The panel then fell
         back to the wizardless tile, whose tap has nothing to ask for a
         redraw -- so the state changed and the row went on showing whatever it
         showed when it was opened. Exactly the stuck highlight. */
      var w = (typeof opts.wizard === 'function') ? opts.wizard() : opts.wizard;
      if (w && w.groupRows) return w.groupRows(pane, groups);

      /* a product with no wizard at all still gets rows that redraw */
      return _groupRowsInto(pane, groups, function () { panel(pane, key); });
    }

    /* extra (ui-v192): { tabs: [{ kind, label }], tab } -- one drawer for all
       the kinds, a tab each, the one opened being tabs[tab] */
    function openPicker(kind, title, extra) {
      var G = openingGroup(kind);
      if (!G) { _log('kit: no opening group "' + kind + '"'); return; }
      var tabs = (extra && extra.tabs && extra.tabs.length > 1) ? extra.tabs : null;
      picker({
        title: (extra && extra.title) || title || G.label,
        keepTitle: !!(extra && extra.title),
        items: _pickRows(kind, G),
        allLabel:   str(t, 'all'),
        leftLabel:  str(t, 'op_left'),
        rightLabel: str(t, 'op_right'),
        fmt: fmtLen,
        tabs: tabs ? tabs.map(function (tb) { return { label: tb.label }; }) : null,
        tab: extra ? extra.tab : 0,
        onTab: tabs ? function (i) {
          var G2 = openingGroup(tabs[i].kind);
          if (!G2) { _log('kit: no opening group "' + tabs[i].kind + '"'); return null; }
          return { items: _pickRows(tabs[i].kind, G2), title: tabs[i].label || G2.label };
        } : null,
        onPick: function (row) { if (opts.onPick) opts.onPick(row.spec, row); }
      });
    }
    function _pickRows(kind, G) {
      /* core-v721 (TAURUS, Marek: nabidku oken a dveri staci jen zakladni):
         opts.codes { kind: [codes] } narrows a kind to those units, in that
         order; a kind not named keeps the whole catalogue */
      var CO = opts.codes && opts.codes[kind], items = G.items;
      if (CO && CO.length) {
        items = [];
        CO.forEach(function (c) { var i9; for (i9 = 0; i9 < G.items.length; i9++) if (G.items[i9].code === c) { items.push(G.items[i9]); break; } });
        if (!items.length) items = G.items;
      }
      var rows = items.map(function (it) {
        return {
          w: it.w, h: it.h, solid: !!it.solid, sectional: !!it.sectional, round: !!it.round,
          full: it.full, leaves: it.leaves, hand: it.hand, pair: !!it.pair,
          code: it.code,
          name: it.full ? (it.full === 'half' ? str(t, 'op_half') : str(t, 'op_full'))
                        : (it.name ? str(t, it.name) : ''),
          /* a round light says its diameter, not a width by a height (core-v518) */
          sub: it.full ? '' : (it.round ? ('&#248; ' + fmtLen(it.w)) : (fmtLen(it.w) + ' x ' + fmtLen(it.h))),
          spec: { kind: kind, w: it.w, h: it.h, sill: it.sill, code: it.code,
                  hand: it.hand, full: it.full, leaves: it.leaves }
        };
      });
      /* THE CORNER WINDOW'S TILE (core-v751, Marek: na druhou pozici, kde to
         dava smysl): second among the windows, after the full wall window.
         It is not a catalogue row -- it is two of them, chosen on the wall by
         the cycler -- so it lives here and never in OPENING_CATALOG, where
         the plan reader and the products look units up by size. A product
         that narrows its windows (opts.codes) or says no (opts.corner) has
         no such tile. */
      var cwOn = false;
      try { cwOn = (kind === 'window') && !CO && typeof opts.corner === 'function' && !!opts.corner(); } catch (eCw) { cwOn = false; }
      if (cwOn) {
        var iF = -1, q9;
        for (q9 = 0; q9 < rows.length; q9++) if (rows[q9].full === true) { iF = q9; break; }
        rows.splice(iF + 1, 0, {
          w: 0, h: 0, corner: true, glyph: CORNER_GLYPH, glyphVb: '48 32', code: '',
          name: str(t, 'op_corner'), sub: '',
          spec: { kind: 'window', w: 0, h: 0, sill: 0, code: null, corner: true }
        });
      }
      return rows;
    }

    /* the groups a product offers, ready to drop in as category tiles */
    function openingTiles(onTap) {
      var kinds = opts.openings || ['window', 'door', 'french', 'garage'];
      var ICONS = { window: icons.window, door: icons.door,
                    french: icons.french, garage: icons.garage,
                    interior: icons.door, roof: icons.window };
      return kinds.map(function (k) {
        var G = openingGroup(k);
        return {
          label: G ? G.label : k,
          svg: ICONS[k] || icons.window,
          on: function () { return false; },
          tap: function () { (onTap || openPicker)(k, G ? G.label : k); }
        };
      });
    }

    /* the shared vocabulary, already resolved through this product's
       translator -- so a product writes KIT.s('roof_gable'), never a key */
    function s2(name) { return str(t, name); }

    return { icons: icons, panel: panel, openPicker: openPicker,
             openingTiles: openingTiles, s: s2, STRINGS: STRINGS };
  }

  /* ONE TILE, BUILT WITHOUT A WIZARD.

     _iconButton lives on the wizard instance because it calls refresh() after a
     tap. A product using only the panel has no instance to call, so the tap
     just runs and the panel redraws itself -- same markup, same classes, same
     states, so the two cannot look different. */
  function _soloTile(ic, redraw) {
    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'cfg-ui-wiz-ico';
    if (ic.kind === 'toggle' || typeof ic.on === 'function') {
      var on = ic.on ? !!ic.on() : false;
      b.className += on ? ' cfg-ui-wiz-ico--on' : ' cfg-ui-wiz-ico--off';
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    }
    if (ic.title) b.title = ic.title;
    b.innerHTML = '<span class="cfg-ui-wiz-ico-g">' + (ic.svg || '') + '</span>' +
                  '<span class="cfg-ui-wiz-ico-t">' + (ic.label || '') + '</span>';
    b.addEventListener('click', function () {
      if (ic.tap) { try { ic.tap(); } catch (e) {} }
      /* a tile that changes a state has to redraw the row it is in, or the
         lit one stays lit and the tap looks like it did nothing */
      if (redraw) { try { redraw(); } catch (e2) {} }
    });
    return b;
  }

  /* A SLIDER IN THE DRAWER (core-v539, Marek: posuvnik opacity vlozeneho
     pudorysu do draweru). A group may carry range instead of items:
       { label, range: { min, max, step, get(), set(v), fmt(v) } }
     It is drawn as the group's heading with the value at the right end and the
     LEVA slider under it, full width. Dragging calls set() only -- the panel is
     not rebuilt under the finger, or the drag would end at the first step. */
  function _rangeRow(grp) {
    var R = grp.range || {};
    var lo = (R.min != null) ? +R.min : 0, hi = (R.max != null) ? +R.max : 100;
    var fmt = (typeof R.fmt === 'function') ? R.fmt : function (v) { return String(Math.round(v)); };
    var v0 = 0;
    try { v0 = +R.get(); } catch (eG) { v0 = lo; }
    if (!isFinite(v0)) v0 = lo;
    var row = document.createElement('div');
    row.style.cssText = 'margin:10px 0 16px;';
    var head = document.createElement('div');
    head.style.cssText = 'display:flex;align-items:baseline;justify-content:space-between;margin-bottom:9px;';
    var lab = document.createElement('div');
    lab.textContent = grp.label || '';
    lab.style.cssText = 'font:700 10px/1.2 ' + UX.font + ';letter-spacing:.08em;text-transform:uppercase;color:' + UX.muted + ';';
    var val = document.createElement('div');
    val.style.cssText = 'font:700 12px/1.2 ' + UX.font + ';color:' + UX.ink + ';';
    head.appendChild(lab); head.appendChild(val);
    var inp = document.createElement('input');
    inp.type = 'range';
    inp.className = 'cfg-ui-slider';
    inp.min = String(lo); inp.max = String(hi); inp.step = String(R.step || 1);
    inp.value = String(Math.max(lo, Math.min(hi, v0)));
    inp.style.cssText = 'width:100%;margin:0;display:block;';
    function paint() {
      var v = parseFloat(inp.value), pct = (hi > lo) ? ((v - lo) / (hi - lo) * 100) : 0;
      inp.style.background = 'linear-gradient(to right,' + ACCENT + ' 0%,' + ACCENT + ' ' + pct + '%,' + TRACK + ' ' + pct + '%,' + TRACK + ' 100%)';
      val.textContent = fmt(v);
    }
    inp.addEventListener('input', function () {
      paint();
      try { R.set(parseFloat(inp.value)); } catch (eS) {}
    });
    /* the drawer and the plan under it both listen for drags: a gesture that
       starts on the slider is the slider's */
    var stop = function (e) { e.stopPropagation(); };
    inp.addEventListener('pointerdown', stop);
    inp.addEventListener('touchstart', stop, { passive: true });
    inp.addEventListener('touchmove', stop, { passive: true });
    paint();
    row.appendChild(head);
    row.appendChild(inp);
    return row;
  }

  /* groupRows without a wizard to hang it on -- the same drawing, so a product
     that only wants the panel does not have to build a wizard to get one */
  function _groupRowsInto(pane, groups, redraw) {
    if (!pane) return;
    /* CLEAR THE CHILDREN, NOT JUST THE MARKUP. Setting innerHTML empties a real
       DOM node's children too, so this is belt and braces in a browser -- but a
       redraw that leaves the old rows attached is exactly how a stale highlight
       survives, and that is the bug this path exists to fix. */
    while (pane.firstChild) pane.removeChild(pane.firstChild);
    pane.innerHTML = '';
    (groups || []).forEach(function (grp) {
      if (grp && grp.range) { pane.appendChild(_rangeRow(grp)); return; }
      if (!grp || !grp.items || !grp.items.length) return;
      var row = document.createElement('div');
      row.style.cssText = 'margin:10px 0 14px;';
      if (grp.label) {
        var lab = document.createElement('div');
        lab.textContent = grp.label;
        lab.style.cssText = 'font:700 10px/1.2 ' + UX.font + ';letter-spacing:.08em;' +
                            'text-transform:uppercase;color:' + UX.muted + ';margin-bottom:7px;';
        row.appendChild(lab);
      }
      var strip = document.createElement('div');
      strip.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;';
      grp.items.forEach(function (it) { strip.appendChild(_soloTile(it, redraw)); });
      row.appendChild(strip);
      pane.appendChild(row);
    });
  }

  function injectBrand() {
    if ($('cfg-ui-brand-css')) return;
    var st = document.createElement('style');
    st.id = 'cfg-ui-brand-css';
    st.textContent = brandCss();
    document.head.appendChild(st);
  }

  /* --------------------------------------------------------------------------
     THE RAIL, STYLED FROM ONE PLACE.

     Every product's liquid carries its own copy of the rail's CSS, and they
     have gone their own ways: pale round tiles with a dark glyph in some, black
     square tiles with a white glyph in others. It is the same rail doing the
     same job on the same site.

     Sharing the icon PATHS, which is what I did last, was the smaller half of
     this -- it made a door the same drawing everywhere and left it sitting on a
     different button in every product. This is the button.

     NOTHING CALLS THIS TODAY, ON PURPOSE.

     I wired it into the ADU and the container and it overrode rails that were
     already settled -- the container's coloured banner and the ADU's own,
     which we had deliberately put BACK to pale round tiles a few rounds
     earlier. Both times it broke hover: a tile darkening with nothing said
     about the glyph on it.

     A rail is a product's own surface until somebody asks for it to be shared,
     and nobody has. It stays here, unused, because the code is right and the
     decision was not mine to make; when a product wants it, it is one line.
     -------------------------------------------------------------------------- */
  function railCss() {
    var TILE = '#1A1C1D', TILE_ON = '#2C2F31';
    return [
      '.cf-icon-btn{width:44px;height:44px;border-radius:11px;border:none;',
      '  cursor:pointer;background:' + TILE + ';box-shadow:none;padding:0;position:relative;',
      '  display:flex;align-items:center;justify-content:center;transition:all .18s ease;}',

      /* WHITE IN EVERY STATE. The tile only ever goes from black to a lighter
         black, so there is no state in which a dark glyph would be readable --
         and naming only the resting one is how a hover rule elsewhere flips it
         back. Spans are in the list because half the glyphs on a rail are
         span[data-cfg-icon] filled in later, and a rule that names svg and img
         alone leaves those black on black: invisible, and indistinguishable
         from a button that failed to load. */
      '.cf-icon-btn svg,.cf-icon-btn img,.cf-icon-btn span[data-cfg-icon],',
      '.cf-icon-btn:hover svg,.cf-icon-btn:hover img,.cf-icon-btn:hover span[data-cfg-icon],',
      '.cf-icon-btn.active svg,.cf-icon-btn.active img,.cf-icon-btn.active span[data-cfg-icon],',
      '.cf-icon-btn[aria-pressed="true"] svg,.cf-icon-btn[aria-pressed="true"] img,',
      '.cf-icon-btn[aria-pressed="true"] span[data-cfg-icon]{',
      '  width:22px;height:22px;object-fit:contain;',
      '  filter:brightness(0) invert(1);pointer-events:none;}',

      /* A HOVER PROMISES A STATE, so only the buttons that hold one get it.
         Undo, unit and share happen once when pressed; lighting them up says
         they stay on, and on a tablet where hover is whatever the finger last
         touched it leaves a tile looking switched on for no reason. */
      '.cf-btn-viz:hover,.cf-btn-colors:hover,#cf-btn-body:hover,#cf-btn-ar:hover,',
      '#cf-btn-assembly:hover{background:' + TILE_ON + ';}',
      '.cf-icon-btn.active,.cf-icon-btn[aria-pressed="true"]{background:' + TILE_ON + ';}',
      '.cf-icon-btn[aria-pressed="false"]{background:' + TILE + ';}',

      /* THE COUNT IS THE ONLY GOLD. The stage button used to swap its whole
         tile between gold and black as it cycled, so the colour meant "an odd
         stage" rather than "this is on". The tile stays black; the badge says
         which stage, and it appears on the first press rather than sitting
         there reporting nothing. */
      '.cf-btn-viz,.cf-btn-colors{position:relative;}',
      '.cf-btn-viz .cf-viz-num,.cf-btn-colors .cf-viz-num{display:none;}',
      '.cf-btn-viz[aria-pressed="true"] .cf-viz-num,',
      '.cf-btn-colors[aria-pressed="true"] .cf-viz-num{display:block;position:absolute;',
      '  top:3px;right:3px;min-width:15px;height:15px;padding:0 4px;border-radius:8px;',
      '  background:' + UX.sand + ';color:#1A1C1D;font:800 9px/15px ' + UX.font + ';',
      '  text-align:center;box-shadow:0 0 0 1.5px ' + TILE + ';pointer-events:none;}',

      /* the assembly counter is a number instead of a glyph, so it is type */
      '.cf-btn-assembly .cf-assembly-num{font:600 13px ' + UX.font + ';color:#fff;',
      '  letter-spacing:-0.02em;line-height:1;}',

      '.cf-icon-btn.cart-btn{background:' + UX.sand + ';}',
      '.cf-icon-btn.cart-btn svg,.cf-icon-btn.cart-btn img,',
      '.cf-icon-btn.cart-btn span[data-cfg-icon]{filter:brightness(0) invert(1);}'
    ].join('');
  }

  function injectRail() {
    if ($('cfg-ui-rail-css')) return;
    var st = document.createElement('style');
    st.id = 'cfg-ui-rail-css';
    st.textContent = railCss();
    document.head.appendChild(st);
    _log('rail style injected');
  }

  var ACCENT = '#c79a4a';   /* slider gold, from configurator-bench.liquid */
  var TRACK = 'rgba(0,0,0,0.10)';

  function _log(msg) {
    try {
      if (global.Configurator && global.Configurator.log && global.Configurator.log.log) {
        global.Configurator.log.log('[cfg-ui] ' + msg);
      }
    } catch (e) {}
  }

  function $(id) { return document.getElementById(id); }
  function _clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

  function _root(ref) {
    if (ref && ref.nodeType === 1) return ref;
    if (typeof ref === 'string') { var el = $(ref); if (el) return el; }
    return $('cf-root') || document.body;
  }

  /* ---- CSS, injected once ---------------------------------------------
     Kept byte-identical in look to .cfg-slider-horiz in the bench liquid so
     migrating a configurator is a delete, never a redesign. */
  var _cssDone = false;
  function _ensureCss() {
    if (_cssDone || $('cfg-ui-css')) { _cssDone = true; return; }
    injectBrand();
    var st = document.createElement('style');
    st.id = 'cfg-ui-css';
    st.textContent =
      /* the slider's own look now comes from brandCss(); only its row is here */
      '.cfg-ui-slider-row{position:absolute;bottom:110px;width:120px;display:none;' +
      'align-items:center;justify-content:center;z-index:9;pointer-events:auto;}' +
      '.cfg-ui-slider-row.cfg-ui-on{display:flex;}' +
      '.cfg-ui-slider-row .cfg-ui-slider{width:100%;margin:0;}' +
      '.cfg-ui-slider-row.cfg-ui-disabled{opacity:0.35;pointer-events:none;}';
    document.head.appendChild(st);
    _cssDone = true;
  }

  /* ---- Slider ----------------------------------------------------------
     opts:
       id       required, unique per configurator
       root     container element or id, defaults to #cf-root
       over     element id to sit above and match in width -- measured, never
                hardcoded, because the column moves with the language. Omit to
                centre on the host instead.
       width    px, used when centred
       bottom   px above the container floor, default 110
       min/max/step/value
       onInput  fired continuously while dragging -- do cheap work only
       onChange fired once on release -- persist here
     returns a handle: set(), show(), hide(), position(), destroy(), el
  */
  function slider(opts) {
    if (!opts || !opts.id) { _log('slider() needs an id'); return null; }
    _ensureCss();

    var host = _root(opts.root);
    var rowId = opts.id + '-row';
    var row = $(rowId);
    if (!row) {
      row = document.createElement('div');
      row.id = rowId;
      row.className = 'cfg-ui-slider-row';
      if (opts.bottom != null) row.style.bottom = opts.bottom + 'px';
      var inp = document.createElement('input');
      inp.type = 'range';
      inp.id = opts.id;
      inp.className = 'cfg-ui-slider';
      row.appendChild(inp);
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      host.appendChild(row);
    }
    var input = $(opts.id);

    var api = {
      el: row,
      input: input,

      /* Repaints the filled portion of the track. Uses the live min/max, so a
         slider whose floor is not zero still fills from its own start. */
      _paint: function () {
        var lo = parseFloat(input.min) || 0;
        var hi = parseFloat(input.max) || 0;
        var v = parseFloat(input.value) || 0;
        var pct = (hi > lo) ? ((v - lo) / (hi - lo) * 100) : 0;
        input.style.background = 'linear-gradient(to right,' + ACCENT + ' 0%,' + ACCENT + ' ' +
          pct + '%,' + TRACK + ' ' + pct + '%,' + TRACK + ' 100%)';
      },

      set: function (o) {
        o = o || {};
        if (o.min != null) input.min = String(o.min);
        if (o.max != null) input.max = String(o.max);
        if (o.step != null) input.step = String(o.step);
        if (o.value != null) {
          input.value = String(_clamp(o.value, parseFloat(input.min) || 0, parseFloat(input.max) || 0));
        }
        api._paint();
        api.position();
        return api;
      },

      /* With a target, the row tracks that column and matches its width.
         Without one, it simply centres on the host.

         A hidden target measures zero, and a zero-width column used to make
         this return early and leave the row wherever it last was -- which is
         what happened the moment a host hid its info bar. Now a target that
         cannot be measured falls back to centred rather than to nothing. */
      position: function () {
        var hr = host.getBoundingClientRect();
        function centre() {
          if (opts.width) row.style.width = opts.width + 'px';
          row.style.left = '50%';
          row.style.transform = 'translateX(-50%)';
        }
        if (!opts.over) { centre(); return api; }
        var col = $(opts.over);
        if (!col) { centre(); return api; }
        var cr = col.getBoundingClientRect();
        if (!cr.width) { centre(); return api; }
        row.style.width = Math.max(90, Math.round(cr.width)) + 'px';
        row.style.left = (cr.left + cr.width / 2 - hr.left) + 'px';
        row.style.transform = 'translateX(-50%)';
        return api;
      },

      show: function () { row.classList.add('cfg-ui-on'); api.position(); return api; },
      hide: function () { row.classList.remove('cfg-ui-on'); return api; },
      enabled: function (on) { row.classList.toggle('cfg-ui-disabled', !on); return api; },
      destroy: function () { if (row.parentNode) row.parentNode.removeChild(row); }
    };

    api.set({
      min: opts.min != null ? opts.min : 0,
      max: opts.max != null ? opts.max : 100,
      step: opts.step != null ? opts.step : 1,
      value: opts.value != null ? opts.value : 0
    });

    if (!input.__cfgUiBound) {
      input.__cfgUiBound = true;
      input.addEventListener('input', function () {
        api._paint();
        if (opts.onInput) opts.onInput(parseFloat(input.value) || 0);
      });
      input.addEventListener('change', function () {
        if (opts.onChange) opts.onChange(parseFloat(input.value) || 0);
      });
      global.addEventListener('resize', function () { api.position(); });
      setTimeout(function () { api.position(); }, 200);
    }

    _log('slider ' + opts.id + ' ready');
    return api;
  }


  /* =====================================================================
     HANDLES -- ghost rectangles with corner and edge grips.

     Split of responsibility, and it is the whole point of this widget:
       UI   owns hit testing, pinning, snapping and drawing.
       HOST owns meaning. It hands over rectangles in world coords and gets
            back a proposed rectangle; what that rectangle means (footprint
            dimensions, a roof overhang, a terrace) is the host's business.
     The host clamps inside apply() and writes its own state. The UI never
     stores geometry, it re-reads rects() on every frame, so host clamping
     always wins and the two can never disagree.

     The one rule inherited from painful experience: hit zones are measured in
     SCREEN PIXELS, not metres. A grip sized in metres shrinks to a few pixels
     when the camera pulls back, which is what made dragging unusable on a
     tablet. Points are projected and compared in CSS px.
     ===================================================================== */

  var HIT_PX = 30;

  /* How much of a ghost survives behind the model. depthTest:false made the
     footprint float over the walls as if the cabin were not there, which reads
     as a bug rather than a hint. Two passes fix it without losing the plan:

       1. depthFunc LessEqualDepth (the default) -- full strength where nothing
          is in the way.
       2. depthFunc GreaterDepth -- only where geometry is in front, at a trace.

     GreaterDepth is what keeps them from stacking: each fragment passes exactly
     one of the two tests, never both, so the open ground stays at its own
     opacity instead of getting darker. */
  function _greaterDepth() {
    var T = global.THREE;
    return (T && T.GreaterDepth != null) ? T.GreaterDepth : 6;
  }

  /* A style is one hue from fill to grip, so whatever you grab tells you which
     rectangle it belongs to. Sand is the roof, olive is the building.

     Sand reads lighter than olive at the same opacity, which is why it went to
     the roof: the overhang should recede on its own, before opacity even gets
     involved. The building is the thing you are drawing, so it takes the
     heavier colour AND the heavier opacity -- the two agree instead of pulling
     against each other. The inherited grey is gone; it never meant anything.

     primary   -- solid mass: a building, a shed, a pool. You want to read the
                  plan under it, so it stays present in the open and shows
                  through the walls at 8 percent.
     secondary -- anything light overhead: a roof overhang, a pergola. An edge
                  condition, not a room. It is defined by its outline and its
                  grips; the fill only says which side is inside.

     xray     -- opacity behind geometry (the depthFunc GreaterDepth pass).
     xrayEdge -- multiplier for the outline, because a 1px line at the fill's
                 xray vanishes. Per style, so dropping a fill to 5 percent does
                 not silently take its outline with it. */
  var STYLES = {
    /* EVERY FOOTPRINT AS THE ADU'S ROOF (ui-v352, Marek 2026-10-22 00:25:
       Udelej footprint square stejny jako je footprint strechy u Adu (je tam
       jen linie okolo, jestli vypln tak opacity 3%. V nasi zlate barve, mensi
       drag pointy. Aplikuj to ... napric vsemi produkty): the building's
       footprint (shelter, sauna, ADU size step, container pergola, tiny, the
       bench lib's copy) is gold too -- the outline, a 3 % fill, a trace
       behind the model; the olive 50 % plane is gone. Both styles alike. */
    primary: {
      fill: 0xC49A4A, opacity: 0.03, xray: 0.015, xrayEdge: 24,
      edge: 0xC49A4A, corner: 0xC49A4A, mid: 0xffffff, order: 14
    },
    /* OUR GOLD (core-v511, Marek: drag pointy a ghost footprint v nasi zlate
       barve, at to mame vsude stejne): the roof, the wing, the terrace and the
       pool ghosts in the gold of the hole and stairwell grips, #C49A4A
       (ui-v352: its fill 8 -> 3 %) */
    secondary: {
      fill: 0xC49A4A, opacity: 0.03, xray: 0.015, xrayEdge: 24,
      edge: 0xC49A4A, corner: 0xC49A4A, mid: 0xffffff, order: 12
    }
  };
  function _xray(st) { return (st.xray != null) ? st.xray : 0.05; }
  function _xrayEdge(st) { return _xray(st) * ((st.xrayEdge != null) ? st.xrayEdge : 4); }

  var IN = 0.0254;

  /* US architectural dimension: feet, hyphen, whole inches, reduced fraction to
     the nearest 1/16 -- 5.00 m reads 16'-4 7/8", never 196.9". Zero inches still
     print (16'-0") because a bare 16' reads as an unfinished dimension. Under a
     foot the feet part is dropped. Metric side is whole millimetres. */
  /* Feet, dash, whole inches. Total inches are unreadable on site (196.9" means
     nothing to a carpenter) and sixteenths are more precision than a dragged
     handle can honestly claim -- they only made the label long and jittery.
     Zero inches is written out: 10'-0", never a bare 10'. */
  /* FEET, INCHES AND SIXTEENTHS, which is how an inch drawing is dimensioned.

     It used to round to the whole inch. On sizes that land on a foot that is
     invisible, which is exactly why it survived -- but the moment a wall is any
     other length the kota quietly reported a different building from the one on
     screen, up to half an inch out, with nothing to show it had rounded. A
     dimension that is silently wrong is worse than one that is awkward to read.

     Sixteenths is the shop convention and the practical floor: a sixteenth is
     about 1.6 mm, which is finer than anyone cuts. The fraction is reduced the
     way it would be written by hand -- 8/16 is a half, 12/16 three quarters --
     and dropped when the inch is whole, so a round building still reads
     24'-0" and not 24'-0 0/16". */
  function fmtDim(m, metric) {
    if (metric) return Math.round(m * 1000) + ' mm';
    var sx = m < 0 ? '-' : '';
    var sixteenths = Math.round(Math.abs(m) / IN * 16);
    var ft = Math.floor(sixteenths / 192);
    var rest = sixteenths - ft * 192;
    var inch = Math.floor(rest / 16);
    var frac = rest - inch * 16;

    var fs = '';
    if (frac) {
      var den = 16, num = frac;
      while (num % 2 === 0 && den > 1) { num /= 2; den /= 2; }
      fs = ' ' + num + '/' + den;
    }
    var out = (ft > 0) ? (ft + '\u2032-' + inch + fs) : (inch + fs);
    return sx + out + '\u2033';
  }

  function _stage() { return global.ConfiguratorStage || null; }

  /* Eight grips per rectangle. px/pz name the coordinates that must not move
     while this grip is dragged: corners pin the far corner, edge midpoints pin
     only their own axis and leave the other alone. */
  /* k 0-3 corners, 4-7 edge midpoints, 8 the body itself. The body is not a
     drawn puck -- it is the whole footprint, so a user can shove the object
     around without hunting for a handle. */
  var K_BODY = 8;

  /* THE dimension chip. One drawing, one cache, one place -- the handles below
     use it, and so does anything else that has a measurement to show, so a
     dimension looks the same wherever it appears.

     sizeAttenuation:false is a PERSPECTIVE-only flag. three.js only divides the
     sprite's scale by -mvPosition.z when the projection matrix is perspective:

         if ( isPerspectiveMatrix( projectionMatrix ) ) scale *= - mvPosition.z;

     In an orthographic plan there is no divide to undo, so the flag does nothing
     and scale.set(0.085, 0.028) means what it says: an 85 mm by 28 mm chip, laid
     flat on the ground, at a camera framing seven metres. That is not small, it
     is invisible -- which is why the size dimensions vanished the moment SIZE
     moved into the plan view. They were never drawn wrong; they were drawn at
     one thousandth of the size the number implies.

     In ortho the honest question is "what share of what the camera can see", so
     that is what it asks. */
  var _dimCache = { map: {}, n: 0 };
  function dimLabel(text, cfg) {
    var T = global.THREE, st = _stage();
    if (!T) return null;
    /* THE COLOUR IS PART OF THE KEY, OR THE CACHE HANDS BACK THE WRONG ONE.

       The cache is keyed on the string. A number at its limit and the same
       number below it are the same string, so whichever was drawn first would
       be served to both and the red would either never appear or never go
       away. */
    var hot = !!(cfg && cfg.max);
    var ck = (hot ? '!' : '') + text;
    var tex = _dimCache.map[ck];
    if (!tex) {
      /* An unbounded cache keyed on every distinct string is a leak with a
         friendly face: a drag produces a new number every few millimetres, each
         one a fresh canvas and a fresh GPU upload that nothing ever frees. */
      if (_dimCache.n >= 96) {
        for (var k in _dimCache.map) if (_dimCache.map.hasOwnProperty(k)) _dimCache.map[k].dispose();
        _dimCache = { map: {}, n: 0 };
      }
      /* THE HOUSE KOTA, AS THE UX SHEET DRAWS IT.

         White pill, dark number, Montserrat 600, fully rounded. Nothing else --
         no line, no leaders, no heads; those belong to the PDF kota, which is
         read slowly and can afford them. I had it inverted: a dark pill with
         white text, which is a different mark from the one the rest of the site
         uses, and this has to match across the web.

         THE PILL IS SIZED TO THE NUMBER. A fixed canvas gave every label the
         same width, so a short one floated in a wide empty box and the digits
         inside it shrank to fit the longest string the box was built for. The
         text is measured first and the pill is drawn round it, which is what
         makes it a pill rather than a plate -- and it is why the drawn size can
         grow without the number going soft.

         Twice the pixel height it was, so the extra size is extra detail rather
         than a bigger blur. */
      var PAD = 56, H = 160, FS = 88;
      var c = document.createElement('canvas');
      var mctx = c.getContext('2d');
      mctx.font = '600 ' + FS + 'px Montserrat, Arial';
      var tw = Math.ceil(mctx.measureText(text).width);
      c.width = Math.max(H * 1.6, tw + PAD * 2);
      c.height = H;
      var ctx = c.getContext('2d');
      ctx.fillStyle = '#FFFFFF';
      if (ctx.roundRect) { ctx.beginPath(); ctx.roundRect(0, 0, c.width, H, H / 2); ctx.fill(); }
      else ctx.fillRect(0, 0, c.width, H);
      /* AT THE LIMIT THE NUMBER GOES RED, AND ONLY THE NUMBER.

         The pill stays white. Inverting it would make a different mark from
         the one the rest of the site uses, and the point is not to shout -- it
         is to say which of the two dimensions is the one that stopped. */
      ctx.fillStyle = hot ? '#C8102E' : '#1A1C1D';
      ctx.font = '600 ' + FS + 'px Montserrat, Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, c.width / 2, H / 2 + 2);
      tex = new T.CanvasTexture(c);
      /* the sprite has to know how wide this particular number came out */
      tex.__aspect = c.width / c.height;
      tex.minFilter = T.LinearFilter;
      if (T.SRGBColorSpace) tex.colorSpace = T.SRGBColorSpace;
      _dimCache.map[ck] = tex;
      _dimCache.n++;
    }
    var cam = st && st.camera;
    var ortho = !!(cam && cam.isOrthographicCamera);
    var sp = new T.Sprite(new T.SpriteMaterial({
      map: tex, transparent: true, depthTest: false, sizeAttenuation: !ortho
    }));
    /* PURE WHITE HAS TO BE EXEMPT FROM TONE MAPPING.

       The pill is drawn #FFFFFF and still came out a soft grey, which is why it
       never quite matched the sheet. Everything in the scene goes through the
       renderer's tone curve on the way to the screen, and that curve pulls the
       top end down so that bright timber does not clip -- exactly what you want
       for a building and exactly what you do not want for a label, which is not
       lit by anything and is supposed to be paper.

       toneMapped false takes this one sprite out of that and hands the canvas
       to the screen as drawn. */
    sp.material.toneMapped = false;
    /* HEIGHT IS THE SIZE; WIDTH FOLLOWS THE NUMBER. Setting both from a fixed
       pair stretched a short label and squashed a long one, which is how the
       digits ended up different sizes from one drag to the next. */
    var asp = tex.__aspect || 3;

    /* THE KOTA IS A SCREEN ELEMENT, IN PLAN TOO.

       The UX sheet draws it as a pill about twenty pixels tall -- a thing that
       sits ON the drawing, not IN it. I had it as a size in metres, which made
       it grow with the building and shrink with the zoom: a metre-tall number
       on a small plan, unreadable on a large one. Sized to the viewport it is
       the same pill everywhere, which is what "must match across the web"
       means.

       AND AN ORTHOGRAPHIC CAMERA DOES NOT HAVE SIZE ATTENUATION. Turning it
       off there does nothing at all: an ortho sprite is always measured in
       world units, so asking for 0.043 asked for a pill four centimetres wide
       standing next to a ten-metre building -- the kota did not shrink, it
       disappeared.

       In plan the same fraction is worked out against the camera's own visible
       height, which is the world size that fills that share of the screen. It
       is read on every redraw, so it tracks the zoom instead of being fixed at
       whatever the zoom happened to be when the label was made. */
    if (cfg && cfg.screen) {
      var hh;
      if (ortho) {
        var vis = ((cam.top - cam.bottom) / (cam.zoom || 1));
        hh = vis * cfg.screen;
      } else {
        hh = cfg.screen;
        sp.material.sizeAttenuation = false;
      }
      sp.scale.set(hh * asp, hh, 1);
      return sp;
    }

    if (ortho) {
      /* Metres, fixed. Taking a share of the ortho span was the obvious thing
         and the wrong one: onResize re-runs setView('2d') with no focus, so the
         span is recomputed from the group's bounding box and the framing jumps.
         Anything scaled off that camera jumps too, and a dimension that changes
         size when you switch windows is not a dimension.

         opts.dimPlan is a product saying how big it wants its plan dimensions,
         as a multiple. Absent, this is the size it always was. */
      var k = (cfg && cfg.plan) ? cfg.plan : 1;
      var h = 0.36 * k;
      sp.scale.set(h * asp, h, 1);
    } else if (false) {
      /* SCREEN-SIZED, WHICH IS WHAT A DIMENSION WANTS TO BE.

         The default below is a world size -- 85 mm wide -- and that is fine on
         a garden bench a metre away. On a house seen from twenty metres it is a
         speck, and the number you are dragging is the one thing that has to
         stay readable at every zoom.

         Turning size attenuation off makes the scale a fraction of the
         viewport instead, so the label is the same size on screen however far
         out you are. Opt in per product: nothing that has not asked changes.
         The canvas is 3:1, so the width follows the height. */
      var hs = cfg.screen;
      sp.material.sizeAttenuation = false;
      sp.scale.set(hs * asp, hs, 1);
    } else {
      sp.scale.set(0.028 * asp, 0.028, 1);
    }
    sp.renderOrder = 40;
    return sp;
  }

  function _grips(r) {
    var mx = (r.x0 + r.x1) / 2, mz = (r.z0 + r.z1) / 2;
    /* A SIDE'S GRIP CAN BE LEFT OUT (core-v448): noGrip = ['z0', ...] on a rect
       drops that side's grip -- the wing's side on the bar, the bar's side
       under the wing */
    var no = r.noGrip || [];
    var gx0 = mx, gx1 = mx, gz0 = mz, gz1 = mz;
    var out = [
      { k: 0, edge: false, x: r.x0, z: r.z0, px: r.x1, pz: r.z1 },
      { k: 1, edge: false, x: r.x1, z: r.z0, px: r.x0, pz: r.z1 },
      { k: 2, edge: false, x: r.x1, z: r.z1, px: r.x0, pz: r.z0 },
      { k: 3, edge: false, x: r.x0, z: r.z1, px: r.x1, pz: r.z0 },
      { k: 4, edge: true, x: gx0, z: r.z0, px: null, pz: r.z1, side: 'z0' },
      { k: 5, edge: true, x: r.x1, z: gz1, px: r.x0, pz: null, side: 'x1' },
      { k: 6, edge: true, x: gx1, z: r.z1, px: null, pz: r.z0, side: 'z1' },
      { k: 7, edge: true, x: r.x0, z: gz0, px: r.x1, pz: null, side: 'x0' }
    ];
    if (no.length) out = out.filter(function (g) { return !g.side || no.indexOf(g.side) < 0; });
    /* edge grips only (core-v507): a corner would drag a side that must not move */
    if (r.edgeOnly) out = out.filter(function (g) { return g.edge; });
    return out;
  }

  function handles(opts) {
    if (!opts || typeof opts.rects !== 'function') { _log('handles() needs rects()'); return null; }

    var api = {
      _grp: null, _plane: null, _drag: null, _bound: false,
      _y: (opts.y != null) ? opts.y : 0.04,
      /* A RECTANGLE AT ITS OWN HEIGHT (ui-v179, RANCH: the cross gables' ghosts
         lay on the ground under the house -- hidden by the roof, their grips
         specks in odd places, and a tap on the gable seen on the roof landed on
         the ground behind it and turned the camera). A rect with y is drawn,
         hit and dragged on the level plane at that height; without y it stays
         on the set's own level, exactly as before. */
      _yOf: function (r) { return (r && r.y != null && isFinite(r.y)) ? +r.y : this._y; },

      _rects: function () { return opts.rects() || []; },
      _active: function () { return opts.active ? !!opts.active() : true; },

      _ensureGrp: function () {
        var T = global.THREE, st = _stage();
        if (!T || !st || !st.scene) return null;
        if (!this._grp) {
          this._grp = new T.Group();
          this._grp.userData.arSkip = true;
          st.scene.add(this._grp);
        }
        return this._grp;
      },
      _clear: function () {
        if (!this._grp) return;
        for (var i = this._grp.children.length - 1; i >= 0; i--) {
          var c = this._grp.children[i];
          if (c.geometry) c.geometry.dispose();
          this._grp.remove(c);
        }
      },

      _rect3: function (r, st) {
        var T = global.THREE;
        var w = Math.abs(r.x1 - r.x0), l = Math.abs(r.z1 - r.z0);
        var cx = (r.x0 + r.x1) / 2, cz = (r.z0 + r.z1) / 2;
        var self = this;
        function pass(op, behind) {
          var g = new T.Mesh(
            new T.PlaneGeometry(w, l),
            new T.MeshBasicMaterial({
              color: st.fill, transparent: true, opacity: op, side: T.DoubleSide,
              depthTest: true, depthWrite: false,
              depthFunc: behind ? _greaterDepth() : undefined
            })
          );
          g.rotation.x = -Math.PI / 2;
          g.position.set(cx, self._yOf(r), cz);
          g.renderOrder = st.order + (behind ? 0 : 1);
          self._grp.add(g);
        }
        /* NO FILL WHEN THE CALLER SAYS SO: the tinted plane lies over whatever
           is drawn under it -- on a house plan the rooms -- and changes their
           colours. fill: false keeps the outline and the pucks and draws no
           plane at all (ADU, ui-v105). */
        /* core-v726 (TAURUS, Marek: zvyraznit v pudorysu lepe i pristavbu,
           je tam jen tenky ramecek): a rect may ask for its fill (r.fill) and
           its strength (r.fillOp) where the page draws none */
        if (opts.fill === false && !r.fill) return;
        pass((r.fillOp != null) ? r.fillOp : st.opacity, false);
        pass(_xray(st), true);
      },
      _outline3: function (r, st) {
        var T = global.THREE, y = this._yOf(r), self = this;
        /* ONE OUTLINE FOR TWO RECTANGLES THAT TOUCH (core-v430, Marek: ma mit
           tvar stavby, T, ne dva obdelniky). Where an edge of this rect lies
           inside another rect of the same style -- the wing's roof on the bar's
           -- that edge is left out, so the two ghosts read as one T. The grips
           and the fills stay as they are. */
        var others = (this._rects() || []).filter(function (o) { return o !== r && o.key !== r.key && !o.bare && (STYLES[o.style] || STYLES.primary) === st; });
        function hidden(ax, az, bx, bz) {
          var mx = (ax + bx) / 2, mz = (az + bz) / 2, q;
          for (q = 0; q < others.length; q++) {
            var o = others[q];
            if (mx > o.x0 + 0.01 && mx < o.x1 - 0.01 && mz > o.z0 + 0.01 && mz < o.z1 - 0.01) return true;
          }
          return false;
        }
        var edges = [[r.x0, r.z0, r.x1, r.z0], [r.x1, r.z0, r.x1, r.z1], [r.x1, r.z1, r.x0, r.z1], [r.x0, r.z1, r.x0, r.z0]], pts = [], qe;
        for (qe = 0; qe < edges.length; qe++) {
          var e = edges[qe];
          if (hidden(e[0], e[1], e[2], e[3])) continue;
          pts.push(new T.Vector3(e[0], y, e[1]), new T.Vector3(e[2], y, e[3]));
        }
        if (!pts.length) return;
        function pass(op, behind) {
          var l = new T.LineSegments(new T.BufferGeometry().setFromPoints(pts),
            new T.LineBasicMaterial({
              color: st.edge, transparent: true, opacity: op,
              depthTest: true, depthWrite: false,
              depthFunc: behind ? _greaterDepth() : undefined
            }));
          l.renderOrder = st.order + (behind ? 2 : 3);
          self._grp.add(l);
        }
        pass(0.95, false);
        /* core-v726: a rect that asks for it (r.band, metres) gets its edges
           as flat bands too -- a line is a hair on a tablet */
        if (r.band > 0) {
          for (qe = 0; qe < edges.length; qe++) {
            var eb = edges[qe];
            if (hidden(eb[0], eb[1], eb[2], eb[3])) continue;
            var lenB = Math.hypot(eb[2] - eb[0], eb[3] - eb[1]);
            if (!(lenB > 0.01)) continue;
            var qb = new T.Mesh(new T.PlaneGeometry(lenB + r.band, r.band),
              new T.MeshBasicMaterial({ color: st.edge, transparent: true, opacity: 0.85, side: T.DoubleSide, depthTest: true, depthWrite: false }));
            qb.rotation.x = -Math.PI / 2;
            qb.rotation.z = -Math.atan2(eb[3] - eb[1], eb[2] - eb[0]);
            qb.position.set((eb[0] + eb[2]) / 2, y + 0.002, (eb[1] + eb[3]) / 2);
            qb.renderOrder = st.order + 3;
            self._grp.add(qb);
          }
        }
        pass(_xrayEdge(st), true);
      },
      _icons: function (L) {
        var T = global.THREE, q;
        if (!T || !T.Sprite) return;
        this._icoMat = this._icoMat || {};
        for (q = 0; q < L.length; q++) {
          var ic = L[q];
          if (!ic || !ic.map || !ic.at) continue;
          var id = ic.map.uuid || String(q), sm = this._icoMat[id];
          if (!sm) sm = this._icoMat[id] = new T.SpriteMaterial({ map: ic.map, transparent: true, depthTest: false, depthWrite: false, sizeAttenuation: false });
          var sp = new T.Sprite(sm), dd = ic.d || 0.04;
          sp.scale.set(dd, dd, 1); sp.renderOrder = 60;
          sp.position.set(ic.at[0], ic.at[1], ic.at[2]);
          if (ic.cx != null && sp.center && sp.center.set) sp.center.set(ic.cx, 0.5);
          this._grp.add(sp);
        }
      },
      _puck: function (x, z, rad, color, order, y) {
        var T = global.THREE;
        var m = new T.Mesh(new T.CircleGeometry(rad, 24),
          new T.MeshBasicMaterial({ color: color, transparent: true, opacity: 0.95, depthTest: false }));
        m.rotation.x = -Math.PI / 2;
        m.position.set(x, ((y != null) ? y : this._y) + 0.02, z);
        m.renderOrder = order + 6;
        this._grp.add(m);
      },
      _label: function (lab, x, z) {
        /* THE WHOLE LABEL, NOT JUST ITS TEXT.

           dims() has been returning max: true on the dimension that has
           stopped for some time and this took only the string, so the flag
           arrived and was dropped on the floor. */
        var text = (lab && lab.text != null) ? lab.text : lab;
        var hot = !!(lab && lab.max);
        /* opts.dimScreen is the product saying how big it wants its dimensions
           on screen. Absent, everything behaves exactly as it did. */
        var cfg = (opts.dimScreen || opts.dimPlan || hot)
          ? { screen: opts.dimScreen, plan: opts.dimPlan, max: hot } : null;
        var sp = dimLabel(text, cfg);
        if (!sp) return;
        /* ui-v179: a label may say its own height, as its rect does */
        var ly = (lab && lab.y != null && isFinite(lab.y)) ? +lab.y : this._y;
        sp.position.set(x, ly + 0.5, z);
        this._grp.add(sp);
        if (!hot) return;

        /* AND THE WORD UNDERNEATH, IN SCREEN SPACE.

           Offsetting it in the world would put it below the number in a plan
           view and beside it, or behind it, from anywhere else -- the model
           turns and the word wanders off. A sprite's centre is the point that
           lands on its position, so pushing the centre past its own height
           hangs the word under the number on the SCREEN, whatever the camera
           is doing.

           Same size and same face as the number: it is the second half of the
           same message, not a footnote to it. */
        var mx = dimLabel('MAX', cfg);
        if (!mx) return;
        mx.position.set(x, ly + 0.5, z);
        if (mx.center && mx.center.set) mx.center.set(0.5, 1.75);
        this._grp.add(mx);
      },

      /* Scales with the object so it reads right on a 3 m pergola and on a 12 m
         stack alike. The hit area stays HIT_PX either way -- that is what makes
         it usable on a tablet. Override per configurator with opts.puck. */
      _radius: function (r) {
        var p = opts.puck || {};
        var mn = Math.min(Math.abs(r.x1 - r.x0), Math.abs(r.z1 - r.z0));
        var lo = (p.min != null) ? p.min : 0.05;    /* ui-v352 smaller pucks (Marek: mensi drag pointy): 0.075 / 0.15 / 2.6 % -> 0.05 / 0.10 / 1.8 % */
        var hi = (p.max != null) ? p.max : 0.10;
        var fr = (p.frac != null) ? p.frac : 0.018;
        return Math.max(lo, Math.min(hi, mn * fr));
      },

      _screenOf: function (x, z, y) {
        var T = global.THREE, st = _stage();
        if (!T || !st || !st.camera || !st.renderer) return null;
        var v = new T.Vector3(x, (y != null) ? y : this._y, z);
        v.project(st.camera);
        if (v.z > 1) return null;
        var b = st.renderer.domElement.getBoundingClientRect();
        return { x: b.left + (v.x + 1) / 2 * b.width, y: b.top + (1 - v.y) / 2 * b.height };
      },

      _groundHit: function (cx, cy, y) {
        var T = global.THREE, st = _stage();
        if (!T || !st || !st.camera || !st.renderer) return null;
        var b = st.renderer.domElement.getBoundingClientRect();
        var ndc = new T.Vector2(((cx - b.left) / b.width) * 2 - 1, -((cy - b.top) / b.height) * 2 + 1);
        var rc = new T.Raycaster();
        rc.setFromCamera(ndc, st.camera);
        if (!this._plane) this._plane = new T.Plane(new T.Vector3(0, 1, 0), opts.planOverlay ? -this._y : 0);
        /* ui-v179: the level plane of a rect with its own height */
        var pl = (y != null && isFinite(y)) ? new T.Plane(new T.Vector3(0, 1, 0), -y) : this._plane;
        var hit = new T.Vector3();
        return rc.ray.intersectPlane(pl, hit) ? hit : null;
      },

      /* nearest grip across every rectangle, within its pixel radius */
      _hitAt: function (cx, cy) {
        var rs = this._rects(), best = null, bd = 1e9, i, j, gs, s, dx, dy, d2;
        /* core-v623: A RECT'S OWN ICONS BEFORE ANY GRIP (RANCH: a cross
           gable's icons near the roof ghost's grips lost the press to a grip
           within 30 px) -- the icons are drawn on top, so they answer first.
           hitScreen may answer 'tap': a press there is a tap only (an on / off
           icon), never a drag, however far the finger slides */
        for (i = rs.length - 1; i >= 0; i--) {
          var rI = rs[i], ownI = false;
          if (rI.movable === false || typeof rI.hitScreen !== 'function') continue;
          try { ownI = rI.hitScreen(cx, cy); } catch (eHI) { ownI = false; }
          if (!ownI) continue;
          var gI = (rI.y != null && isFinite(rI.y)) ? this._groundHit(cx, cy, +rI.y) : this._groundHit(cx, cy);
          if (!gI) gI = { x: (rI.x0 + rI.x1) / 2, z: (rI.z0 + rI.z1) / 2 };
          return { rect: rI, grip: { k: K_BODY, edge: false, body: true, x: gI.x, z: gI.z, tapOnly: ownI === 'tap' } };
        }
        for (i = 0; i < rs.length; i++) {
          gs = _grips(rs[i]);
          for (j = 0; j < gs.length; j++) {
            s = this._screenOf(gs[j].x, gs[j].z, rs[i].y);
            if (!s) continue;
            dx = cx - s.x; dy = cy - s.y;
            d2 = dx * dx + dy * dy;
            if (d2 <= HIT_PX * HIT_PX && d2 < bd) {
              bd = d2;
              best = { rect: rs[i], grip: gs[j] };
            }
          }
        }
        if (best) return best;

        /* Nothing under the thumb, so fall back to the footprint: grabbing the
           body moves the whole thing. Grips are tested first, otherwise a
           corner sitting inside its own rect could never be picked. Last rect
           wins, matching the draw order.

           movable:false opts a rect out. The base building of a configurator
           has nowhere to move to -- it IS the project origin -- and swallowing
           the press there costs the host every tap on the model, because this
           listener runs in the capture phase. Fitouts placed around the build
           (a pergola, a shed) leave it at the default and stay grabbable. */
        /* ui-v179: each rect on its own level -- the ground's hit for the rest */
        var g0 = this._groundHit(cx, cy), g;
        /* A RECT MAY CLAIM A PRESS BY ITS OWN SCREEN TEST (core-v588, RANCH:
           the cross gables' round icons stand side by side on the lid and,
           zoomed out, past its footprint). hitScreen(cx, cy) true = a body
           press on that rect; tested before the footprints, icons draw on top.
           Such a rect is hit ONLY by its own test, never by its footprint, so a
           press on the roof around it still turns the camera. A rect without
           it is hit exactly as before. */
        for (i = rs.length - 1; i >= 0; i--) {
          var rS = rs[i], own = false;
          if (rS.movable === false || typeof rS.hitScreen !== 'function') continue;
          try { own = !!rS.hitScreen(cx, cy); } catch (eHS) { own = false; }
          if (!own) continue;
          g = (rS.y != null && isFinite(rS.y)) ? this._groundHit(cx, cy, +rS.y) : g0;
          if (!g) g = { x: (rS.x0 + rS.x1) / 2, z: (rS.z0 + rS.z1) / 2 };
          return { rect: rS, grip: { k: K_BODY, edge: false, body: true, x: g.x, z: g.z } };
        }
        for (i = rs.length - 1; i >= 0; i--) {
          var r = rs[i];
          if (r.movable === false || typeof r.hitScreen === 'function') continue;
          g = (r.y != null && isFinite(r.y)) ? this._groundHit(cx, cy, +r.y) : g0;
          if (!g) continue;
          if (g.x >= Math.min(r.x0, r.x1) && g.x <= Math.max(r.x0, r.x1) &&
              g.z >= Math.min(r.z0, r.z1) && g.z <= Math.max(r.z0, r.z1)) {
            return { rect: r, grip: { k: K_BODY, edge: false, body: true, x: g.x, z: g.z } };
          }
        }
        return null;
      },

      /* Opt-in floating products: project the SAME native grips above plan ink.
         Pointer events remain on the renderer and keep the normal drag logic. */
      overlayRefresh: function () {
        if(typeof opts.planOverlay!=='function')return;
        var show=opts.planOverlay()&&this._active(),host=global.document.getElementById('cf-root');
        if(!host)return;
        if(!this._ov){var ns='http://www.w3.org/2000/svg';this._ov=global.document.createElementNS(ns,'svg');this._ov.setAttribute('class','cf-native-plan-grips');this._ov.style.cssText='position:absolute;inset:0;width:100%;height:100%;pointer-events:none;z-index:9;overflow:visible';host.appendChild(this._ov);}
        this._ov.style.display=show?'':'none';this._ov.innerHTML='';if(!show)return;
        var self=this,hr=host.getBoundingClientRect(),ns='http://www.w3.org/2000/svg';
        this._rects().forEach(function(r){var style=STYLES[r.style]||STYLES.primary;
          _grips(r).forEach(function(g){var pt=self._screenOf(g.x,g.z,self._yOf(r));if(!pt)return;
            var c=global.document.createElementNS(ns,'circle');c.setAttribute('cx',pt.x-hr.left);c.setAttribute('cy',pt.y-hr.top);c.setAttribute('r',7);c.setAttribute('fill','#'+(g.edge?style.mid:style.corner).toString(16).padStart(6,'0'));c.setAttribute('stroke','#fff');c.setAttribute('stroke-width',2);self._ov.appendChild(c);
          });
        });
      },
      refresh: function () {
        this.overlayRefresh();
        if (!this._ensureGrp()) return;
        this._clear();
        if (!this._active()) return;
        var rs = this._rects(), i, j, gs, st, rad, r, on;
        for (i = 0; i < rs.length; i++) {
          r = rs[i];
          st = STYLES[r.style] || STYLES.primary;
          /* bare (core-v588): no fill and no outline -- the product draws its
             own marks (RANCH cross gables: the line over the gable is gone) */
          if (!r.bare) {
            this._rect3(r, st);
            this._outline3(r, st);
          }
          rad = this._radius(r);
          gs = _grips(r);
          for (j = 0; j < gs.length; j++) {
            on = this._drag && this._drag.key === r.key && this._drag.k === gs[j].k;
            this._puck(gs[j].x, gs[j].z, rad * (on ? 1.25 : 1),
                       gs[j].edge ? st.mid : st.corner, st.order, this._yOf(r));
          }
          /* A RECT'S OWN ICONS (core-v623, RANCH: the cross gables' move /
             project / on-off icons were drawn by the build -- on screen while
             the handles were off (the strip on the covering or the base), so a
             tap fell through to the camera). Drawn here they are on screen
             exactly when the rect takes presses: r.icons = [{ at: [x, y, z],
             map: texture, d: diameter as a share of the view, cx: the sprite's
             centre x (pushes it across the screen) }], facing the camera */
          if (r.icons && r.icons.length) this._icons(r.icons);
        }
        var d = this._drag;
        if (d && d.moved && opts.dims) {
          var labels = opts.dims(d.key, d.k) || [];
          for (i = 0; i < labels.length; i++) this._label(labels[i], labels[i].x, labels[i].z);
        } else if (opts.dims && opts.dimsHold && opts.dimsHold()) {
          /* A DIMENSION AT ITS LIMIT STAYS ON SCREEN. The labels are drawn
             while a grip moves and cleared when it is let go, so a size dragged
             to its maximum and released showed nothing red and nothing that
             said MAX. When the product says a limit is standing (dimsHold),
             its maxed labels are kept up between drags, over whatever the
             plan draws there itself (ADU, ui-v106). */
          var held = opts.dims(null, -1) || [];
          for (i = 0; i < held.length; i++) {
            if (held[i] && held[i].max) this._label(held[i], held[i].x, held[i].z);
          }
        }
      },

      bind: function () {
        if (this._bound) return api;
        var st = _stage();
        if (!st || !st.renderer || !st.renderer.domElement) return api;
        this._bound = true;
        var self = this;

        st.renderer.domElement.addEventListener('pointerdown', function (e) {
          if (!self._active()) return;
          var h = self._hitAt(e.clientX, e.clientY);
          if (!h) return;
          e.preventDefault(); e.stopPropagation();
          if (st.controls) st.controls.enabled = false;
          self._drag = { key: h.rect.key, k: h.grip.k, edge: h.grip.edge,
                         px: h.grip.px, pz: h.grip.pz, snap: h.rect.snap || 0.1, moved: false,
                         /* core-v623: a press on a tap-only icon never drags */
                         tapOnly: !!h.grip.tapOnly,
                         /* ui-v184: where the press landed, for a tap */
                         tap: { x: h.grip.x, z: h.grip.z, y: (h.rect.y != null && isFinite(h.rect.y)) ? +h.rect.y : self._y, cx: e.clientX, cy: e.clientY },
                         /* ui-v179: dragged on its own level */
                         y: (h.rect.y != null && isFinite(h.rect.y)) ? +h.rect.y : null };
          if (h.grip.body) {
            /* remember the grab point and the size, so the rect slides under the
               thumb instead of jumping its corner to the cursor */
            self._drag.body = true;
            self._drag.w = h.rect.x1 - h.rect.x0;
            self._drag.l = h.rect.z1 - h.rect.z0;
            self._drag.ox = h.grip.x - h.rect.x0;
            self._drag.oz = h.grip.z - h.rect.z0;
          }
          self.refresh();
        }, true);

        global.addEventListener('pointermove', function (e) {
          var d = self._drag;
          if (!d || !self._active()) return;
          /* A FINGER THAT WOBBLES IS STILL A TAP (ui-v224, Marek: the cross
             gables' on / off on the RANCH mostly did nothing). Any pointermove
             made the press a drag, and a finger on a tablet always sends a
             few -- so the tap on the icon went out as a body drag of a few
             millimetres and the icon never heard it. A press is a drag only
             once it has gone 10 px (touch) / 5 px (mouse, pen) from where it
             landed. */
          if (!d.moved && d.tapOnly) return;   /* core-v623: an on / off icon is a tap however the finger slides */
          if (!d.moved && d.tap) {
            var lim = (e.pointerType === 'touch') ? 10 : 5, ddx = e.clientX - d.tap.cx, ddy = e.clientY - d.tap.cy;
            if (ddx * ddx + ddy * ddy <= lim * lim) return;
          }
          var g = self._groundHit(e.clientX, e.clientY, d.y);
          if (!g) return;
          d.moved = true;
          var sn = d.snap;
          var prop = {};
          if (d.body) {
            /* whole-rect move: size is fixed, both edges travel together */
            var bx = Math.round((g.x - d.ox) / sn) * sn;
            var bz = Math.round((g.z - d.oz) / sn) * sn;
            prop.x0 = bx; prop.x1 = bx + d.w;
            prop.z0 = bz; prop.z1 = bz + d.l;
            try { _log('grip     ' + d.key + ' grip ' + d.k + ' -> ' + ['x0', 'x1', 'z0', 'z1'].filter(function (kk) { return prop && prop[kk] != null; }).map(function (kk) { return kk + ' ' + Math.round(prop[kk] * 1000); }).join(', ')); } catch (eGL) {}
          if (opts.apply) opts.apply(d.key, prop, d.k);
            self.refresh();
            return;
          }
          /* pin the far side, snap the moving side; a null pin means that axis
             is not this grip's business and stays untouched */
          if (d.px != null) {
            var nx = Math.round(g.x / sn) * sn;
            prop.x0 = Math.min(d.px, nx); prop.x1 = Math.max(d.px, nx);
          }
          if (d.pz != null) {
            var nz = Math.round(g.z / sn) * sn;
            prop.z0 = Math.min(d.pz, nz); prop.z1 = Math.max(d.pz, nz);
          }
          try { _log('grip     ' + d.key + ' grip ' + d.k + ' -> ' + ['x0', 'x1', 'z0', 'z1'].filter(function (kk) { return prop && prop[kk] != null; }).map(function (kk) { return kk + ' ' + Math.round(prop[kk] * 1000); }).join(', ')); } catch (eGL) {}
          if (opts.apply) opts.apply(d.key, prop, d.k);
          self.refresh();
        }, true);

        global.addEventListener('pointerup', function () {
          var d = self._drag;
          if (!d) return;
          self._drag = null;
          if (st.controls) st.controls.enabled = true;
          if (d.moved && opts.onCommit) opts.onCommit();
          /* A press that never moved is a tap, not a drag. Without this the
             host loses the gesture entirely once the body becomes grabbable. */
          if (!d.moved && opts.onTap) opts.onTap(d.key, d.k, d.tap);
          self.refresh();
        }, true);

        _log('handles ' + (opts.id || '?') + ' bound');
        return api;
      },

      destroy: function () {
        this._clear();
        var st = _stage();
        if (this._grp && st && st.scene) st.scene.remove(this._grp);
        this._grp = null;
      }
    };
    return api;
  }


  /* =====================================================================
     WIZARD -- the guided path for someone who does not know what a pitch is.

     Deliberately DOM, not a 3D floor tag. BBQ puts its edit pencil on the
     ground in front of the first cabinet, which works because a run of
     cabinets has an obvious front. A garden building is a rectangle that
     spins with the map, so "in front" stops meaning anything the moment the
     user rotates. A fixed button under the model is always there, always the
     same size, and needs no raycast.

     Split of responsibility, same as slider and handles:
       UI   owns the button, the bar, the arrows, the dots, the state.
       HOST owns the steps -- what they are called and what each one does.
     A sauna's steps are nothing like a BBQ's, so the list stays in the lib.
     ===================================================================== */

  var _wizCss = false;
  /* the drawer, the step rows and the narrowed canvas -- nothing here changes
     the phone layout, it all hangs off .cfg-ui-desk */
  function _ensureDeskCss() {
    if ($('cfg-ui-desk-css')) return;
    var st = document.createElement('style');
    st.id = 'cfg-ui-desk-css';
    /* STOP STYLING THE DRAWER. Every round of this came from me adding one
       more rule -- a width, a background, a font size -- to a panel the theme
       already dresses. That is the different white, the smaller type, and the
       bottom edge showing while dragging: my rules were fighting the
       stylesheet that gives a side-panel its full height.
       Only the step rows are left, which the theme has never heard of. The
       panel itself -- width, surface, height, slide, type -- is the theme's,
       exactly as it is for the cart drawer (Marek). */
    st.textContent =
      '.cfg-ui-step{border-bottom:1px solid var(--color-border,' + UX.line + ');' +
        'padding:14px 18px;cursor:pointer;}' +
      '.cfg-ui-step:last-child{border-bottom:none;}' +
      '.cfg-ui-step-hd{display:flex;align-items:baseline;gap:10px;}' +
      /* THE MANUAL'S TYPE SCALE. A step title is an H3 -- semi-bold, 24 on a
         desktop and 20 on a phone -- and the hint is body, 16 and 15. Not upper
         case: nothing in section 2 is, and shouting the step names was mine
         (Marek). */
      '.cfg-ui-step-no{opacity:.5;min-width:22px;font-size:16px;font-weight:600;}' +
      '.cfg-ui-step-lbl{font-weight:600;font-size:20px;letter-spacing:0;' +
        'text-transform:none;color:' + UX.ink + ';}' +
      '.cfg-ui-step.on .cfg-ui-step-no,.cfg-ui-step.on .cfg-ui-step-lbl{color:' + UX.sand + ';}' +
      '.cfg-ui-step-hint{display:none;opacity:.85;font-size:16px;font-weight:400;' +
        'line-height:1.5;margin:8px 0 0 34px;}' +
      '@media (max-width:900px){' +
        '.cfg-ui-step-lbl{font-size:18px;}' +
        '.cfg-ui-step-hint{font-size:15px;}}' +
      '.cfg-ui-step.on .cfg-ui-step-hint{display:block;}' +
      '.cfg-ui-step-icons{display:none;flex-wrap:wrap;gap:10px;margin:12px 0 2px 26px;}' +
      '.cfg-ui-step.on .cfg-ui-step-icons{display:flex;}' +
      /* ALIGNED WITH THE ICONS, AND THE READING UNDER IT. The row was dropped
         into the step at whatever indent it happened to inherit, and the value
         bubble stayed pinned over the model where the product had put it -- so
         the one number the slider exists to show was in the other two thirds of
         the screen (Marek). */
      /* CENTRED ON WHAT IS LEFT OF THE VIEW. The slider centres itself on the
         canvas, and the canvas still runs the full width under the drawer -- so
         with the drawer out it sat a third of the way under it. It is shifted
         left by half the drawer, which puts it in the middle of the strip a
         person can actually see. Same reasoning as the camera: the canvas is
         not touched, only what sits on top of it moves (Marek). */
      '.cfg-ui-desk-open .cfg-ui-slider-row{margin-left:-' + (DESK_W / 2) + 'vw;}' +
      /* A BORROWED MODAL, LAID FLAT. The address dialog is moved into the step
         rather than rebuilt -- so the geocoder, the suggestion list and the
         climate select keep the wiring they already have, and there is no
         second address field to keep in step with the first. All that changes
         is that it stops being a dialog: no backdrop, no centring, no shadow,
         no fixed width (Marek). */
      '.cfg-ui-step-panel .cf-map-modal-backdrop{position:static !important;' +
        'display:block !important;background:none !important;opacity:1 !important;' +
        'inset:auto !important;padding:0 !important;z-index:auto !important;}' +
      '.cfg-ui-step-panel .cf-map-modal{position:static !important;transform:none !important;' +
        'width:100% !important;max-width:100% !important;margin:0 !important;' +
        'box-shadow:none !important;border:none !important;padding:0 !important;' +
        'background:none !important;}' +
      '.cfg-ui-step-panel{display:none;margin:12px 0 2px 26px;}' +
      '.cfg-ui-step.on .cfg-ui-step-panel{display:block;}' +
      '.cfg-ui-tipwrap{position:relative;display:inline-flex;}' +
      '.cfg-ui-tip{position:absolute;bottom:calc(100% + 6px);left:50%;transform:translateX(-50%);' +
        'background:' + UX.ink + ';color:' + UX.surface + ';font-size:11px;font-weight:600;' +
        'white-space:nowrap;padding:4px 8px;border-radius:6px;opacity:0;pointer-events:none;' +
        'transition:opacity .12s;z-index:60;}' +
      '.cfg-ui-tipwrap:hover .cfg-ui-tip,.cfg-ui-tip.show{opacity:1;}' +
      '.cfg-ui-desk .cfg-ui-wiz{display:none !important;}' +
      /* THE THEME IS NOT DRESSING THIS ONE, AND I SHOULD HAVE CHECKED.
         Stripping my rules was right in principle -- the cart drawer gets its
         white and its full height from the theme -- but that panel is a
         <cart-drawer> custom element and its rules are written against the
         elements the theme knows. A plain div with the same class names is not
         one of them, so it inherited nothing: hence a bottom edge appearing on
         scroll, which no other drawer here does, and a surface the colour of
         whatever is behind it.
         These four lines are what the theme would have given it and nothing
         more -- pinned top to bottom, white, above the page, scrolling inside
         itself (Marek). */
      /* AND IT HAS TO WIN. The theme's own .side-panel rule is more specific
         than mine, so my geometry lost and the panel stayed absolute -- which
         is exactly a drawer whose bottom edge rides up as the page scrolls.
         Pinned with intent: fixed, full height, and it scrolls INSIDE itself,
         so there is no bottom to find (Marek). */
      /* NO SCRIM, AND NOT BY FIGHTING ONE.

         I first tried to neutralise the theme's overlay by naming every class
         a theme might call it -- which is guessing, and the guess was wrong.
         The overlay is not something to switch off: it belongs to the theme's
         drawer, and this simply is not the theme's drawer any more. Nothing
         shows it, so there is nothing to hide.

         THE SLIDE IS OURS TOO, since the classes that used to animate it are
         gone. Closed is parked off the right edge; open is home. */
      '.cfg-ui-drawer{transform:translate3d(100%,0,0);' +
      '  transition:transform .24s ease;visibility:visible;}' +
      '.cfg-ui-drawer.active,.cfg-ui-drawer.open{transform:translate3d(0,0,0);}' +

      /* ONE WIDTH, SET BY THE WIDEST STEP.

         Without a width the panel took its size from whatever was in it, so it
         grew and shrank as you moved between steps -- the model jumped sideways
         each time, and on a step with one tile the drawer was a sliver. A panel
         that changes shape as you walk through it reads as something going
         wrong rather than as a step changing.

         394 px is five tiles across: 5 x 62 plus four 8 px gaps plus the
         panel's own padding. Five is the widest row any step here has, so
         nothing wraps that did not wrap before and nothing is left with a
         half-empty row.

         The percentage cap is for a narrow window in side mode -- the phone
         does not use this rule at all, it gets the bottom variant. */
      '.cfg-ui-drawer{position:fixed !important;top:0 !important;right:0 !important;' +
        'bottom:0 !important;height:100% !important;max-height:100% !important;' +
        /* WIDTH ALONE DOES NOT HOLD IT. A flex or grid child wider than its
           box still pushes the box out unless the maximum is stated too, so a
           step with a long label or a wide moved-in control could stretch the
           panel and the next step would snap it back. All three are pinned, and
           anything that does not fit scrolls or wraps instead of resizing the
           drawer. */
        'width:min(394px,92vw) !important;' +
        'min-width:min(394px,92vw) !important;' +
        'max-width:min(394px,92vw) !important;' +
        'box-sizing:border-box !important;' +
        'background:' + UX.surface + ' !important;z-index:60;overflow-y:auto;' +
        '-webkit-overflow-scrolling:touch;}' +

      /* THE SAME DRAWER, LYING DOWN.

         On a phone the wizard used to be a set of absolutely positioned pieces
         floating over the model at fixed offsets from the bottom -- a label at
         104 px, icons at 158, a strip at 150. Every one of those was a number
         guessed against a screen height, and on the wrong screen they collided
         or drifted apart. Worse, it was a SECOND renderer: the same steps
         written twice, which is the thing this file exists to prevent.

         It is one drawer now, in from the right where there is width and up
         from the bottom where there is not. The content is identical -- the
         steps, the active one opened, its icons -- because it is the same code.
         Only the edge it is attached to changes. */
      '.cfg-ui-drawer.cfg-ui-drawer-bottom{top:auto !important;left:0 !important;' +
        'right:0 !important;bottom:0 !important;width:100% !important;' +
        'min-width:0 !important;max-width:100% !important;' +
        'height:auto !important;' +
        /* A THIRD OF THE SCREEN, AND IT SCROLLS INSIDE THAT.
           Any taller and the drawer is the page: you lose sight of the building
           you are changing, which is the one thing a phone has least of. A third
           leaves two thirds of the model visible while you work on it, and the
           steps that do not fit are a scroll rather than a bigger panel. */
        'max-height:33vh !important;overflow-y:auto !important;' +
        '-webkit-overflow-scrolling:touch;' +
        'border-radius:16px 16px 0 0;box-shadow:0 -8px 28px rgba(0,0,0,0.18);' +
        /* AND IT COMES UP, NOT ACROSS.
           The theme animates a side-panel by sliding it in from the right, and
           that rule does not care which edge I have pinned this one to -- so a
           drawer sitting along the bottom still flew in sideways. The transform
           is stated for this variant on both axes: nothing horizontal, and the
           whole of its own height vertically. */
        'transform:translate3d(0,100%,0) !important;' +
        'transition:transform .24s ease !important;}' +
      '.cfg-ui-drawer.cfg-ui-drawer-bottom.active,' +
      '.cfg-ui-drawer.cfg-ui-drawer-bottom.open{transform:translate3d(0,0,0) !important;}' +
      '.cfg-ui-drawer .side-panel-inner{min-height:100%;display:flex;flex-direction:column;' +
        'background:' + UX.surface + ';}' +
      '.cfg-ui-drawer .side-panel-content{flex:1 1 auto;background:' + UX.surface + ';}';
    document.head.appendChild(st);
  }
  function _ensureWizCss() {
    if (_wizCss || $('cfg-ui-wiz-css')) { _wizCss = true; return; }
    var st = document.createElement('style');
    st.id = 'cfg-ui-wiz-css';
    st.textContent =
      /* No surface. The wizard is chrome over a 3D scene, and a white card
         under it just hides the thing being configured. Everything here is a
         glyph or a disc on transparent.

         Four rows, each anchored to the bottom on its own. That is deliberate:
         in one flex column, adding an icon would grow the stack and shove the
         step name upward, so the title would jitter between steps. Anchored
         separately, the name sits at one height for the whole wizard.

         Bottom to top: close, dots, name, icons. The cross lands exactly where
         the pencil was, so the way in and the way out are the same spot -- your
         thumb never has to learn a second place.

         The whole stack sits 40px off the floor rather than 14. Raising only the
         pencil would have split it from the cross, and the two sharing one spot
         is the point. */

      '.cfg-ui-wiz{position:absolute;left:0;right:0;bottom:0;height:0;' +
      'z-index:12;pointer-events:none;}' +
      '.cfg-ui-wiz *{pointer-events:auto;}' +

      /* IN DRAWER SHAPE THE BAR IS JUST THE PENCIL.

         Everything else it holds -- the step name, the arrows, the dots, the
         icon row, the close -- is the drawer's job at this width, and it was
         being left on screen underneath it. Named one by one rather than
         "hide the children and show the pencil back", because the pencil is the
         one thing here that must never disappear and a blanket rule is how it
         would. */
      '.cfg-ui-wiz-hidden .cfg-ui-wiz-head,' +
      '.cfg-ui-wiz-hidden .cfg-ui-wiz-icons,' +
      '.cfg-ui-wiz-hidden .cfg-ui-wiz-dots,' +
      '.cfg-ui-wiz-hidden .cfg-ui-wiz-close{display:none !important;}' +

      /* Pencil: the only thing here with a body -- it has to be findable on a
         busy scene. The shadow is a cast one and nothing more: a single soft
         drop, no inner rim, no bevel. Those were reading as a heavy 3D chrome
         button sitting on top of a 3D scene, which is one dimension too many.
         The disc keeps its gradient so it still looks lit, not printed. */
      '.cfg-ui-wiz-pencil{position:absolute;left:50%;bottom:54px;' +
      'transform:translateX(-50%);width:52px;height:52px;border:0;border-radius:' + UX.radius.round + ';' +
      'background:radial-gradient(circle at 50% 32%,#e8434e 0%,' + UX.red + ' 62%,#c01c26 100%);' +
      'color:' + UX.surface + ';cursor:pointer;display:flex;align-items:center;justify-content:center;' +
      'box-shadow:0 4px 12px rgba(0,0,0,0.16);' +
      'transition:transform .12s,box-shadow .12s;}' +
      '.cfg-ui-wiz-pencil:active{transform:translateX(-50%) translateY(1px);' +
      'box-shadow:0 2px 6px rgba(0,0,0,0.14);}' +
      '.cfg-ui-wiz.cfg-ui-on .cfg-ui-wiz-pencil{display:none;}' +

      /* icons -- above the name, own anchor, so their count never moves it */
      '.cfg-ui-wiz-icons{position:absolute;left:50%;bottom:158px;transform:translateX(-50%);' +
      'display:none;align-items:center;gap:10px;justify-content:center;max-width:96vw;}' +
      '.cfg-ui-wiz.cfg-ui-on .cfg-ui-wiz-icons{display:flex;}' +
      '.cfg-ui-wiz-icons:empty{display:none;}' +

      /* arrows flanking the step name */
      '.cfg-ui-wiz-head{position:absolute;left:50%;bottom:104px;transform:translateX(-50%);' +
      'display:none;align-items:center;gap:4px;max-width:96vw;}' +
      '.cfg-ui-wiz.cfg-ui-on .cfg-ui-wiz-head{display:flex;}' +

      /* manual section 7: bare chevron, no disc, no background */
      '.cfg-ui-wiz-arrow{width:' + UX.icon + ';height:' + UX.icon + ';flex:0 0 ' + UX.icon + ';padding:0;' +
      'border:0;background:none;color:' + UX.body + ';cursor:pointer;display:flex;' +
      'align-items:center;justify-content:center;transition:color .12s,transform .12s;}' +
      '.cfg-ui-wiz-arrow svg{width:24px;height:24px;}' +
      '.cfg-ui-wiz-arrow:active{transform:scale(0.88);}' +
      '.cfg-ui-wiz-arrow:disabled{opacity:0.25;cursor:default;transform:none;}' +

      '.cfg-ui-wiz-mid{display:flex;flex-direction:column;align-items:center;gap:2px;' +
      'padding:0 6px;min-width:150px;}' +
      '.cfg-ui-wiz-lbl{font:800 17px ' + UX.font + ';letter-spacing:.05em;text-transform:uppercase;' +
      'color:' + UX.ink + ';white-space:nowrap;line-height:1.1;' +
      'text-shadow:0 1px 3px rgba(255,255,255,0.85);}' +
      '.cfg-ui-wiz-hint{font:600 11px ' + UX.font + ';color:' + UX.muted + ';white-space:nowrap;' +
      'line-height:1;text-shadow:0 1px 3px rgba(255,255,255,0.85);}' +
      '.cfg-ui-wiz-hint:empty{display:none;}' +

      /* The exit: bottom of the stack, on the pencil's own spot. Small on
         purpose -- leaving is not the thing you came to do. */
      '.cfg-ui-wiz-close{position:absolute;left:50%;bottom:54px;' +
      'transform:translateX(-50%);width:26px;height:26px;border:0;' +
      'border-radius:' + UX.radius.round + ';background:' + UX.dark + ';color:' + UX.surface + ';' +
      'cursor:pointer;display:none;align-items:center;justify-content:center;' +
      'box-shadow:0 2px 8px rgba(0,0,0,0.16);}' +
      '.cfg-ui-wiz.cfg-ui-on .cfg-ui-wiz-close{display:flex;}' +
      '.cfg-ui-wiz-close svg{width:12px;height:12px;}' +
      '.cfg-ui-wiz-close:active{transform:translateX(-50%) scale(0.9);}' +

      /* manual section 7: 44px circular. Colour says ON, and nothing else --
         a cycler or a drawer opener has no state to read off a circle. */
      /* SQUARE WITH ROUNDED CORNERS, the way the ADU's guide draws them.

         These were circles, and a circle is the wrong container for an icon
         that is mostly straight lines -- a wall, a slab, a door all sit inside
         a rectangle and a round tile crops their corners or floats them in
         space. The ADU's drawer has been square-with-rounded-corners for a
         while and looks settled; this is the same tile, so the guide matches
         across products instead of per product.

         THE ON STATE IS THE ACCENT, NOT RED. Red is the destructive colour in
         this file's own palette -- it is what the chat button and the delete
         actions use -- so an option that is merely SELECTED was wearing the
         colour that elsewhere means "this removes something". */
      '.cfg-ui-wiz-ico{width:' + UX.icon + ';height:' + UX.icon + ';flex:0 0 ' + UX.icon + ';padding:0;' +
      'border-radius:9px;cursor:pointer;display:inline-flex;align-items:center;' +
      'justify-content:center;background:' + UX.surface + ';color:' + UX.brown + ';' +
      'border:1px solid ' + UX.line + ';box-shadow:' + UX.shadow.soft + ';' +
      'transition:background .12s,color .12s,transform .1s,box-shadow .12s;}' +
      '.cfg-ui-wiz-ico svg{width:20px;height:20px;}' +
      '.cfg-ui-wiz-ico:active{transform:scale(0.92);}' +

      /* EVERY TILE THE SAME BOX, CAPTION OR NOT.

         Sizing the tile off whether it had a name gave a row with two shapes in
         it -- 44 px squares next to 62 px columns -- and the ones that stayed
         small were exactly the ones whose meaning was hardest to guess. A row
         of controls is a set; a set with two sizes in it reads as two sets.

         So the column IS the tile. An item with no name gets the same box with
         an empty line under the glyph, which keeps the row straight and leaves
         an obvious hole where a name is missing rather than hiding it. */
      /* THE SAME TILE THE ADU DRAWS, because the ADU is the one that has been
         looked at and settled: 62 wide, 19 px glyph, 9 px name, 9 px corner. */
      '.cfg-ui-wiz-ico{width:62px;height:auto;flex:0 0 62px;flex-direction:column;' +
      'gap:4px;padding:7px 3px;border-radius:9px;' +
      'font:600 9px/1.2 ' + UX.font + ';text-align:center;}' +
      '.cfg-ui-wiz-ico-g{display:block;width:19px;height:19px;}' +
      '.cfg-ui-wiz-ico.cfg-ui-wiz-ico--sm{width:34px;height:34px;flex:0 0 34px;border-radius:8px;padding:0;}' +
      /* PICTURE TILES (core-v421, Marek: vetsi, bez background, obrazek 50 az
         100 procent): twice the size of a glyph tile, no card behind the
         picture at all; the picture itself is faded at rest, brighter under
         the finger and full when it is the choice. */
      '.cfg-ui-wiz-ico.cfg-ui-wiz-ico--img{width:72px;height:72px;flex:0 0 72px;padding:0;overflow:visible;' +
        'background:transparent;box-shadow:none;border:0;}' +
      '.cfg-ui-wiz-ico--img.cfg-ui-wiz-ico--on,.cfg-ui-wiz-ico--img.cfg-ui-wiz-ico--off{background:transparent;box-shadow:none;}' +
      '.cfg-ui-wiz-ico-p{display:block;width:100%;height:100%;object-fit:contain;pointer-events:none;opacity:0.2;' +
        'transition:opacity .15s ease,transform .15s ease;}' +
      '.cfg-ui-wiz-ico--img:hover .cfg-ui-wiz-ico-p{opacity:0.6;transform:scale(1.04);}' +
      '.cfg-ui-wiz-ico--img.cfg-ui-wiz-ico--on .cfg-ui-wiz-ico-p{opacity:1;}' +
      '.cfg-ui-wiz-ico--sm .cfg-ui-wiz-ico-g{display:none;}' +
      '.cfg-ui-wiz-ico--sm .cfg-ui-wiz-ico-t{font-size:14px;font-weight:700;line-height:34px;margin:0;}' +
      '.cfg-ui-wiz-ico svg{width:19px;height:19px;}' +
      '.cfg-ui-wiz-ico-t{display:block;min-height:11px;max-width:100%;' +
      'overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}' +

      /* A HOVER HAS TO LEAVE THE GLYPH READABLE. Darkening the tile without
         saying anything about what is on it is how an icon disappears on the
         way in -- which is what the rail did, and it is the same mistake one
         layer up. The colour is stated for both together, always. */
      '.cfg-ui-wiz-ico:hover{background:' + UX.ink + ';color:' + UX.surface + ';' +
      'border-color:transparent;}' +

      /* OFF IS NOT GREY. Grey is how this palette says unavailable, so every
         toggle that simply was not switched on looked disabled -- a row of
         dead buttons with one live one. Off is the resting tile: white card,
         dark glyph, same as anything that is not a toggle at all. On is the
         accent. Two states, and neither of them says broken. */
      '.cfg-ui-wiz-ico--off{background:' + UX.surface + ';color:' + UX.brown + ';' +
      'border:1px solid ' + UX.line + ';}' +
      '.cfg-ui-wiz-ico--off:hover{background:' + UX.ink + ';color:' + UX.surface + ';' +
      'border-color:transparent;}' +
      /* gold tile, WHITE glyph -- the ADU settled on this because a dark glyph
         on gold reads as disabled next to a white tile */
      '.cfg-ui-wiz-ico--on{background:' + UX.sand + ';color:' + UX.surface + ';' +
      'border:1.5px solid ' + UX.sand + ';box-shadow:' + UX.shadow.soft + ';}' +
      '.cfg-ui-wiz-ico--on:hover{background:' + UX.sand + ';color:' + UX.surface + ';}' +

      /* progress, between the name and the exit */
      '.cfg-ui-wiz-dots{position:absolute;left:50%;bottom:88px;transform:translateX(-50%);' +
      'display:none;gap:6px;}' +
      '.cfg-ui-wiz.cfg-ui-on .cfg-ui-wiz-dots{display:flex;}' +
      '.cfg-ui-wiz-dot{width:7px;height:7px;border:0;border-radius:' + UX.radius.round + ';padding:0;' +
      'background:rgba(0,0,0,0.16);cursor:pointer;box-shadow:0 0 0 2px rgba(255,255,255,0.5);}' +
      '.cfg-ui-wiz-dot.cfg-ui-on{background:' + UX.red + ';}' +
      '.cfg-ui-wiz-dot.cfg-ui-done{background:rgba(216,35,42,0.4);}' +

      /* hideBar retires the host's own info bar: where the wizard is the
         banner, a second one saying the same things only drifts apart. */
      '.cfg-ui-wiz-host .cf-info-bar{bottom:124px;}' +
      '.cfg-ui-wiz-hidebar .cf-info-bar{display:none !important;}';
    document.head.appendChild(st);
    _wizCss = true;
  }

  var CHEV_L = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>';
  var CHEV_R = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 18l6-6-6-6"/></svg>';
  var CROSS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" aria-hidden="true"><path d="M18 6L6 18M6 6l12 12"/></svg>';

  var PENCIL_SVG =
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"' +
    ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4z"/></svg>';

  /* opts:
       steps    [{ key, label, hint, enter, exit, skip }]  -- skip() true hides it
       onStep   fired on every change, (key, index)
       icons    per step: [{ svg, kind:'toggle'|'cycle'|'drawer', on, tap, title }]
                colour means on/off and nothing else -- cyclers and drawer
                openers stay neutral
                down(e, btn) instead of tap: the icon is a SOURCE, and the host
                takes the raw pointerdown to run its own drag. Mutually
                exclusive with tap -- click is not bound when down is present,
                so the host alone decides what a tap that never moved meant.
       onEnter  wizard opened
       onExit   wizard closed
       root     container, defaults to #cf-root
       lift     keep .cf-info-bar clear of the row (default true)
       hideBar  retire .cf-info-bar entirely -- the wizard replaces it
     returns: open(), close(), toggle(), goto(i), next(), prev(), refresh(), isOpen()
  */
  /* ===================================================================
     THE DESKTOP LAYOUT -- one wizard, two shapes
     ===================================================================
     On a phone the wizard is a bar across the bottom, and that is right: the
     screen is the model and the controls get out of its way. On a desktop it
     leaves two thirds of the window empty and the steps are a carousel you
     have to walk through to see.
     So the same wizard renders as a drawer down the right instead: the steps
     listed one under another, numbered, with a rule between them, and only the
     ACTIVE one opened out -- which matters because a step can change what the
     viewport shows, the floorplan most of all.
     There is ONE set of step definitions and two renderers over it. Two
     definitions would drift apart inside a month, which is the thing this file
     exists to prevent (Marek). */
  /* ANYTHING WIDER THAN A PHONE GETS THE DRAWER.

     1024 is a desktop breakpoint and this is not a desktop question. A tablet
     held upright is 768 and has room for the drawer twice over; it was getting
     the bar, which is the shape built for a screen that has no room at all. The
     line is drawn where a phone ends. */
  var DESK_MIN = 600;              /* below this it stays a bar */
  var DESK_W   = 33;               /* per cent of the window    */
  function _isDesk() {
    try { return (global.innerWidth || 0) >= DESK_MIN; } catch (e) { return false; }
  }
  /* THE CANVAS IS LEFT ALONE. HIS IDEA, AND IT IS THE RIGHT ONE.
     I was narrowing the canvas and asking the engine to resize: the renderer
     kept its own aspect for a frame, the 2D overlay kept the old width, and
     every rotation tore between the two. All of that came from touching the
     thing every projection depends on.
     Nothing is resized now. The canvas stays full width under the drawer and
     the CAMERA'S CENTRE is shifted left instead, so the model sits in the
     middle of the strip that is still visible. setViewOffset does exactly
     that -- it moves which part of the frustum lands on the canvas, changes no
     aspect and no pixel dimension, and screen-to-world keeps working because
     nothing about the canvas changed (Marek). */
  function _deskShell(on) {
    var host = _root();
    if (!host) return;
    if (on) host.classList.add('cfg-ui-desk');
    else host.classList.remove('cfg-ui-desk');
    /* the shift only applies while the drawer is actually out */
    try {
      if (on) document.body.classList.add('cfg-ui-desk-open');
      else document.body.classList.remove('cfg-ui-desk-open');
    } catch (eB) {}
    try {
      var eng = global.ConfiguratorStage || global.SaunaEngine;
      var rnd = eng && eng.renderer;
      if (!eng || !rnd || !rnd.domElement) return;
      var el = rnd.domElement;
      var w = el.clientWidth || el.width, h = el.clientHeight || el.height;
      if (!w || !h) return;
      /* EVERY CAMERA THE STAGE HAS, not just the one that happens to be live.
         The shift used to be set on eng.camera alone, and a configurator swaps
         cameras all the time -- the plan step goes to the ortho one by itself.
         Open the drawer in 3D and the PERSPECTIVE camera takes the shift; step
         into the plan, close the drawer, and the clear lands on the ORTHO one.
         The perspective camera keeps a shift nobody asked for, so the model
         comes back off centre and stays there (Marek: the middle of the canvas
         is always the middle of the building).
         Both cameras carry the same drawer state now, so whichever one is on
         screen is framed the same way and a swap changes nothing. */
      var cams = [], seen = [], i;
      function add(c) {
        if (!c || typeof c.setViewOffset !== 'function') return;
        for (var q = 0; q < seen.length; q++) if (seen[q] === c) return;
        seen.push(c); cams.push(c);
      }
      add(eng.camera); add(eng.persCam); add(eng.orthoCam); add(eng.orthCam);
      for (i = 0; i < cams.length; i++) {
        if (on) {
          /* half the drawer's width: the centre moves by that much, so what is
             left visible is centred on it */
          cams[i].setViewOffset(w, h, Math.round(w * DESK_W / 200), 0, w, h);
        } else if (typeof cams[i].clearViewOffset === 'function') {
          cams[i].clearViewOffset();
        }
        cams[i].updateProjectionMatrix();
      }
      /* core-v744: the homepage hero's own offset (its frame the whole section
         wide, the house right of the text) is put straight back */
      if (!on && typeof global.LEVA_HERO_SHIFT === 'function') global.LEVA_HERO_SHIFT();
    } catch (e) {}
  }

  /* ===================================================================
     TAP OR DRAG -- one answer for every product
     ===================================================================
     A finger is never still: on glass a tap always carries a few pixels, so
     "did the pointer move at all" reads every tap as a drag. Each product was
     about to grow its own threshold and its own number, and the moment two of
     them differ the same gesture means different things in different
     configurators -- which is the exact class of drift this file exists to
     stop.
     One threshold, one helper. A gesture becomes a drag only once it has
     travelled far enough to have been meant; below that it stays a tap
     (Marek). */
  var DRAG_SLOP = 10;               /* px -- about a millimetre of skin */
  function gesture(slop) {
    var x = 0, y = 0, past = false, s2 = (slop == null) ? DRAG_SLOP : slop;
    return {
      start: function (e) { x = e.clientX; y = e.clientY; past = false; return this; },
      /* true once the pointer has gone far enough to be a drag, and true for
         the rest of the gesture after that -- a drag does not become a tap
         again by coming back */
      far: function (e) {
        if (past) return true;
        past = Math.abs(e.clientX - x) > s2 || Math.abs(e.clientY - y) > s2;
        return past;
      },
      moved: function () { return past; }
    };
  }

  function wizard(opts) {
    opts = opts || {};
    var steps = opts.steps || [];
    if (!steps.length) { _log('wizard() needs steps'); return null; }
    _ensureWizCss();
    _ensureDeskCss();

    var host = _root(opts.root);
    var open = false, idx = 0;
    /* A SECOND TAP ON THE OPEN STEP FOLDS IT (core-v519, Marek: pri druhem
       kliknuti na step v edit mode by se zalozka mela zase zavrit). The
       drawer is an accordion with the current step opened out; tapping its
       heading again used to do nothing. Now it folds the step shut -- the
       step stays current, so the model keeps showing what the step shows,
       only the drawer's row closes -- and the next tap on any heading opens
       that one. Moving by the arrows or by goto() always unfolds. */
    var folded = false;
    /* AND THE TAP IS TIMED (core-v520, Marek: docela dlouho trva, nez se
       zalozka pri druhem kliku zavre). The fold itself is a class change and
       a redraw of a few rows, so a slow fold is either the finger's tap
       waiting for the main thread (a rebuild and a publish from the tap
       before it) or the redraw. Both are measured and said on the snapshot's
       wizard line, so the next report says which. */
    var _tapT0 = 0, _tapNote = 'no step heading tapped yet';
    /* THE DRAWER OPENS BEFORE THE MODEL IS REBUILT (core-v521, Marek: zavreni
       zalozky funguje rychle, ale pak trva dlouho nez se otevre dalsi). A
       step change runs the product's onStep, which rebuilds the whole model
       (every layer reads the step it is on), and only then was the drawer
       drawn -- so the tap seemed to do nothing for as long as the rebuild
       took. The drawer is drawn and painted first now, and the step's work
       runs on the next tick; the drawer is drawn once more after it, in
       case the work changed what it shows. A newer goto cancels an older
       one's pending work, so arrows tapped quickly rebuild once. The time
       the work took is said on the snapshot's wizard line. */
    var _gotoGen = 0, _stepNote = 'no step opened yet';
    function _nowMs() { try { return (global.performance && global.performance.now) ? global.performance.now() : Date.now(); } catch (e) { return Date.now(); } }

    var row = $('cfg-ui-wiz');
    if (!row) {
      row = document.createElement('div');
      row.id = 'cfg-ui-wiz';
      row.className = 'cfg-ui-wiz';
      row.innerHTML =
        '<button class="cfg-ui-wiz-pencil" id="cfg-ui-wiz-pencil" type="button" aria-label="Edit">' + PENCIL_SVG + '</button>' +
        '<div class="cfg-ui-wiz-head">' +
        '<button class="cfg-ui-wiz-arrow" id="cfg-ui-wiz-prev" type="button" aria-label="Back">' + CHEV_L + '</button>' +
        '<div class="cfg-ui-wiz-mid">' +
        '<span class="cfg-ui-wiz-lbl" id="cfg-ui-wiz-lbl"></span>' +
        '<span class="cfg-ui-wiz-hint" id="cfg-ui-wiz-hint"></span>' +
        '</div>' +
        '<button class="cfg-ui-wiz-arrow" id="cfg-ui-wiz-next" type="button" aria-label="Next">' + CHEV_R + '</button>' +
        '</div>' +
        '<div class="cfg-ui-wiz-icons" id="cfg-ui-wiz-icons"></div>' +
        '<div class="cfg-ui-wiz-dots" id="cfg-ui-wiz-dots"></div>' +
        '<button class="cfg-ui-wiz-close" id="cfg-ui-wiz-close" type="button" aria-label="Close">' + CROSS + '</button>';
      if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
      host.appendChild(row);
    }
    /* a window that crosses the threshold has to re-render, or it keeps the
       shape it happened to open in */
    if (!wizard._sizeBound) {
      wizard._sizeBound = true;
      try {
        global.addEventListener('resize', function () {
          try { if (wizard._live) wizard._live.refresh(); } catch (e) {}
        });
      } catch (e) {}
    }
    if (opts.lift !== false) host.classList.add('cfg-ui-wiz-host');
    if (opts.hideBar) host.classList.add('cfg-ui-wiz-hidebar');

    /* a step can opt out of itself -- a shed has no benches to place */
    function live() {
      var out = [], i;
      for (i = 0; i < steps.length; i++) {
        if (!steps[i].skip || !steps[i].skip()) out.push(steps[i]);
      }
      return out;
    }

    /* ------------------------------------------------------------------------
       THE ICON STRIP FOR A PRODUCT ON ITS OWN WIZARD (ui-v195, Marek: nasadit u
       vsech konfiguratoru postavenych nad UI asset). The house line's strip is
       drawn by the core from its rows; a product that builds its wizard here
       directly (the container, the shelter, the tiny house) gets the same dark
       strip with ONE ICON PER STEP: a tap enters the step and opens its
       content -- the tiles and the panel the drawer used to show -- in the
       same floating panel, moved by its handle; a second tap puts the panel
       away. The cross closes the wizard as the drawer's did. ?ui=drawer on
       the address, or strip: false from the product, keeps the drawer.
       ------------------------------------------------------------------------ */
    var _g = { el: null, list: null, modal: null, sel: null, sig: '', tip: null, tipT: 0, mPos: null, items: [], bar: null, sl: null, barKey: '', colorIdx: 0 };
    var G_GLYPH = { place: 'pin', base: 'footing', foundation: 'footing', size: 'envelope', layout: 'plan',
                    floorplan: 'plan', structure: 'layers', roof: 'gable', openings: 'window', doors: 'door',
                    colors: 'color', color: 'color', look: 'facade', exterior: 'facade', facade: 'facade',
                    fitout: 'fitout', fitouts: 'fitout', interior: 'sofa', assembly: 'layers', video: 'play',
                    terrace: 'terrace', pergola: 'pergola', benches: 'sofa', heater: 'bolt', rooms: 'rooms' };
    function _gOn() {
      var q = '';
      try { q = String((global.location && global.location.search) || ''); } catch (e) { q = ''; }
      return !/[?&]ui=drawer(&|$)/.test(q);
    }
    function _gWord(st) {
      return String((st && st.label) || '').replace(/([^\s]+)/g, function (w) {
        return (w.length > 2 && w === w.toUpperCase()) ? w.charAt(0) + w.slice(1).toLowerCase() : w;
      });
    }
    function _gBtn(cls, html, label) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = cls;
      b.innerHTML = html;
      if (label) b.setAttribute('aria-label', label);
      return b;
    }
    var G_X = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M6 6l12 12"/><path d="M18 6L6 18"/></svg>';
    var G_MOVE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18M3 12h18"/><path d="M9 6l3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3"/></svg>';
    function _gTipShow(btn, txt, ms) {
      var h = _root(opts.root);
      if (!_g.tip || !h || !btn || !txt) return;
      _g.tip.textContent = txt;
      var rr = h.getBoundingClientRect(), br = btn.getBoundingClientRect(), sr = _g.el ? _g.el.getBoundingClientRect() : br;
      _g.tip.style.right = Math.round(rr.right - sr.left + 10) + 'px';
      _g.tip.style.top = Math.round(br.top + br.height / 2 - rr.top) + 'px';
      _g.tip.classList.add('cf-on');
      if (_g.tipT) { clearTimeout(_g.tipT); _g.tipT = 0; }
      if (ms) _g.tipT = setTimeout(function () { _g.tipT = 0; if (_g.tip) _g.tip.classList.remove('cf-on'); }, ms);
    }
    function _gTipHide() {
      if (_g.tipT) { clearTimeout(_g.tipT); _g.tipT = 0; }
      if (_g.tip) _g.tip.classList.remove('cf-on');
    }
    /* a product's own element borrowed into the panel goes back to the page
       before the panel is cleared (the drawer's rule) */
    function _gPark(box) {
      if (!box) return;
      var kept = box.querySelectorAll('.cfg-ui-in-step'), k;
      for (k = 0; k < kept.length; k++) { kept[k].style.display = 'none'; document.body.appendChild(kept[k]); }
    }
    function _gModalDrop() {
      if (_g.modal) { _gPark(_g.modal); if (_g.modal.parentNode) _g.modal.parentNode.removeChild(_g.modal); }
      _g.modal = null;
    }
    function _gTeardown() {
      _gModalDrop();
      _gTipHide();
      if (_g.el && _g.el.parentNode) _g.el.parentNode.removeChild(_g.el);
      _g.el = null; _g.list = null; _g.sig = ''; _g.sel = null;
    }
    /* the floating panel of one icon: a step's tiles (all of them, or only
       the drag sources when the others are icons of their own) and its panel */
    function _gModal(I, st) {
      var h = _root(opts.root);
      if (!h || !st) return;
      if (!_g.modal || _g.modal.__key !== I.key) {
        _gModalDrop();
        var m = document.createElement('div');
        m.id = 'cf-sx-modal';
        m.setAttribute('data-cf-nopinch', '1');
        var hd = document.createElement('div');
        hd.className = 'cf-sx-mh';
        var grip = _gBtn('cf-sx-grip', G_MOVE, 'Move');
        var tt = document.createElement('span');
        tt.className = 'cf-sx-mt';
        var mx = _gBtn('cf-sx-mx', G_X, 'Close');
        hd.appendChild(grip); hd.appendChild(tt); hd.appendChild(mx);
        var body = document.createElement('div');
        body.className = 'cf-sx-mb';
        m.appendChild(hd); m.appendChild(body);
        mx.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _g.sel = null; api.refresh(); });
        hd.addEventListener('pointerdown', function (e) {
          if (mx.contains(e.target)) return;
          e.preventDefault(); e.stopPropagation();
          var mr = m.getBoundingClientRect(), off = { x: e.clientX - mr.left, y: e.clientY - mr.top, id: e.pointerId };
          try { hd.setPointerCapture(e.pointerId); } catch (eC) {}
          function mv(ev) {
            if (ev.pointerId !== off.id) return;
            var R = h.getBoundingClientRect();
            var x = Math.max(4, Math.min(R.width - m.offsetWidth - 4, ev.clientX - R.left - off.x));
            var y = Math.max(4, Math.min(R.height - 48, ev.clientY - R.top - off.y));
            m.style.left = Math.round(x) + 'px'; m.style.top = Math.round(y) + 'px'; m.style.bottom = 'auto'; m.style.transform = 'none';
            _g.mPos = { x: Math.round(x), y: Math.round(y) };
          }
          function up() {
            hd.removeEventListener('pointermove', mv);
            hd.removeEventListener('pointerup', up);
            hd.removeEventListener('pointercancel', up);
          }
          hd.addEventListener('pointermove', mv);
          hd.addEventListener('pointerup', up);
          hd.addEventListener('pointercancel', up);
        });
        m.addEventListener('pointerdown', function (e) { e.stopPropagation(); });
        if (_g.mPos) { m.style.left = _g.mPos.x + 'px'; m.style.top = _g.mPos.y + 'px'; m.style.bottom = 'auto'; m.style.transform = 'none'; }
        m.__key = I.key; m.__tt = tt; m.__body = body;
        h.appendChild(m);
        _g.modal = m;
      }
      var M2 = _g.modal;
      M2.__tt.textContent = _gWord(st);
      _gPark(M2.__body);
      M2.__body.innerHTML = '';
      var list = _gTiles(st), q;
      if (I.downOnly) list = list.filter(function (t) { return t && t.down; });
      if (list && list.length) {
        var row = document.createElement('div');
        row.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;padding:10px 0 4px;';
        for (q = 0; q < list.length; q++) if (list[q]) row.appendChild(api._iconButton(list[q]));
        M2.__body.appendChild(row);
      }
      var pane = document.createElement('div');
      pane.className = 'cf-sx-pane';
      M2.__body.appendChild(pane);
      if (typeof st.panel === 'function') {
        try { st.panel(pane, api); } catch (ePn) { _log('strip: the "' + st.key + '" panel threw ' + ((ePn && ePn.message) || ePn)); }
      }
    }

    /* ------------------------------------------------------------------------
       EVERY TILE ITS OWN ICON (ui-v200, Marek: rozepsat na sipky a on/off u
       vsech config -- container, shelter ...). A step's tiles are read by what
       they say they are:
         kind 'cycle'    an icon with the arrows on the canvas; the tile may
                         hand options() -> [{ label, on(), go() }] so the
                         arrows go both ways and the lit option's word stands
                         over them, and slider: { core(), sync(), read(), on(),
                         title } for a slider under them (the product's own
                         ConfiguratorUI.slider, driven as the core drives its
                         own); a cycle without options walks forward on both
                         arrows
         word            the tooltip's and the banner's word when the title
                         says the value instead
         group: 'x'      tiles sharing a group are ONE choice with the arrows
                         (boards / slats, the assembly views)
         kind 'toggle'   with on(): an on/off icon
         down()          a drag source: stays in the step's panel
         anything else   an action icon (the address, upload)
       A step's colorSlots() -> [{ key, label, dots: [{ key, name, hex }],
       cur, set(k) }] makes its icon the colours' arrows and dots. PLACE and
       FLOORPLAN stay one icon with their panel, as on the house line; a
       step's panel (and its drag sources) is a + icon of its own, unless
       the step says stripPanel: false. A group's slider is its first tile's.
       ------------------------------------------------------------------------ */
    function _gTiles(st) {
      var L = [];
      try { L = (typeof st.icons === 'function') ? st.icons() : (st.icons || []); } catch (e) { L = []; }
      return (L || []).filter(function (t) { return !!t; });
    }
    function _gTileWord(t) {
      var w = '';
      try { w = t.title || t.label || ''; } catch (e) { w = ''; }
      return String(w || '');
    }
    function _gItems() {
      var L = live(), out = [], i;
      for (i = 0; i < L.length; i++) (function (st, n) {
        var k = st.key, tiles = _gTiles(st);
        var hasPanel = (typeof st.panel === 'function');
        if (k === 'place' || k === 'layout' || k === 'floorplan') {
          out.push({ key: k, n: n, st: st, kind: 'modal', svg: icon(G_GLYPH[k] || 'layers', { size: 22 }), tip: _gWord(st) });
          return;
        }
        if (typeof st.colorSlots === 'function') {
          out.push({ key: k, n: n, st: st, kind: 'colors', svg: icon('color', { size: 22 }), tip: _gWord(st) });
          return;
        }
        var made = 0, groups = {}, q;
        /* A STEP OF VIEWS: three or more switches and nothing else, exactly
           one of them lit, is one choice -- the arrows walk it (the tiny
           house's assembly views) */
        if (_gOneOf(tiles)) {
          out.push({ key: k + ':g:*', n: n, st: st, kind: 'cycle', group: '*',
                     svgFn: function () { var G = _gGroup(st, '*'), z; for (z = 0; z < G.length; z++) { try { if (G[z].on()) return G[z].svg; } catch (e) {} } return G[0] ? G[0].svg : ''; },
                     tip: _gWord(st) });
          if (hasPanel && st.stripPanel !== false) out.push({ key: k + ':panel', n: n, st: st, kind: 'modal', downOnly: true, svg: icon('plus', { size: 22 }), tip: _gWord(st) });
          return;
        }
        for (q = 0; q < tiles.length; q++) (function (t, ti) {
          if (t.down) return;
          /* word: the section's own word when the tile's title follows its
             value (the shelter's covering) -- the value goes on the chip */
          var tip = (t.word ? String(t.word) : '') || _gTileWord(t) || _gWord(st);
          if (t.group) {
            if (groups[t.group]) return;
            groups[t.group] = 1;
            made++;
            out.push({ key: k + ':g:' + t.group, n: n, st: st, kind: 'cycle', group: t.group, svgFn: function () { var G = _gGroup(st, t.group), z; for (z = 0; z < G.length; z++) { try { if (G[z].on()) return G[z].svg; } catch (e) {} } return G[0] ? G[0].svg : ''; },
                       tip: t.groupTitle || _gWord(st) });
            return;
          }
          made++;
          var base = { key: k + ':' + (t.key || ti), n: n, st: st, ti: ti, tkey: t.key, svgFn: function () { var T = _gTileOf(st, base); return T ? T.svg : ''; }, tip: tip };
          if (t.kind === 'cycle') base.kind = 'cycle';
          else if (typeof t.on === 'function' && t.kind !== 'drawer') base.kind = 'toggle';
          else base.kind = 'action';
          out.push(base);
        })(tiles[q], q);
        var downs = tiles.some(function (t) { return t.down; });
        /* stripPanel: false -- the step's panel holds nothing the icons do not
           (the shelter's slat slider rides its own arrows) */
        if (st.stripPanel === false && made) hasPanel = false;
        if (hasPanel || downs) {
          out.push({ key: k + ':panel', n: n, st: st, kind: 'modal', downOnly: made > 0,
                     svg: icon(made ? 'plus' : (G_GLYPH[k] || 'layers'), { size: 22 }), tip: _gWord(st) });
        } else if (!made) {
          out.push({ key: k, n: n, st: st, kind: 'modal', svg: icon(G_GLYPH[k] || 'layers', { size: 22 }), tip: _gWord(st) });
        }
      })(L[i], i);
      return out;
    }
    function _gGroup(st, g) { return _gTiles(st).filter(function (t) { return (g === '*') ? !t.down : t.group === g; }); }
    function _gOneOf(tiles) {
      if (!tiles || tiles.length < 3) return false;
      var lit = 0, z;
      for (z = 0; z < tiles.length; z++) {
        var t = tiles[z];
        if (t.down || t.group || t.kind === 'cycle' || t.kind === 'drawer' || typeof t.on !== 'function') return false;
        try { if (t.on()) lit++; } catch (e) { return false; }
      }
      return lit === 1;
    }
    function _gTileOf(st, I) {
      var T = _gTiles(st), z;
      if (I.tkey) { for (z = 0; z < T.length; z++) if (T[z].key === I.tkey) return T[z]; }
      return T[I.ti] || null;
    }
    function _gItemBy(key) {
      for (var z = 0; z < _g.items.length; z++) if (_g.items[z].key === key) return _g.items[z];
      return null;
    }
    /* the options a cycle walks: its own list, its group, or forward only */
    function _gOptions(I) {
      if (I.group) return _gGroup(I.st, I.group).map(function (t) { return { label: _gTileWord(t), on: t.on, go: t.tap }; });
      var T = _gTileOf(I.st, I);
      if (!T) return [];
      if (typeof T.options === 'function') { try { return T.options() || []; } catch (e) { return []; } }
      return null;
    }
    function _gValue(I) {
      if (I.kind === 'colors') { var S = _gSlot(I); return S ? S.label : ''; }
      var O = _gOptions(I), z;
      if (O && O.length) { for (z = 0; z < O.length; z++) { try { if (O[z].on && O[z].on()) return O[z].label || ''; } catch (e) {} } return ''; }
      var T = _gTileOf(I.st, I);
      try { return (T && typeof T.value === 'function') ? (T.value() || '') : ''; } catch (e2) { return ''; }
    }
    function _gSlots(I) { try { return I.st.colorSlots() || []; } catch (e) { return []; } }
    function _gSlot(I) {
      var S = _gSlots(I);
      if (!S.length) return null;
      var n = S.length;
      return S[((_g.colorIdx % n) + n) % n];
    }
    function _gEnter(I) {
      var c = api.current();
      if (!c || c.key !== I.st.key) api.goto(I.n);
    }
    function _gStep(key, dir) {
      var I = _gItemBy(key);
      if (!I) return;
      if (I.kind === 'colors') { _g.colorIdx += dir; api.refresh(); return; }
      _gEnter(I);
      var O = _gOptions(I);
      if (O && O.length) {
        var n = O.length, cur = -1, z;
        for (z = 0; z < n; z++) { try { if (O[z].on && O[z].on()) { cur = z; break; } } catch (eO) {} }
        var nx = (cur < 0) ? (dir > 0 ? 0 : n - 1) : ((cur + dir + n) % n);
        try { O[nx].go(); } catch (eG) { _log('strip: ' + key + ' threw ' + ((eG && eG.message) || eG)); }
      } else {
        var T = _gTileOf(I.st, I);
        try { if (T && T.tap) T.tap(); } catch (eT) { _log('strip: ' + key + ' threw ' + ((eT && eT.message) || eT)); }
      }
      api.refresh();
      _log('strip: ' + key + ' ' + (dir > 0 ? 'next' : 'back') + ' -> "' + _gValue(I) + '"');
    }
    function _gTap(I, btn, e) {
      if (!e || e.pointerType !== 'mouse') _gTipShow(btn, I.tip, 5000);
      if (I.kind === 'toggle' || I.kind === 'action') {
        _gEnter(I);
        var T = _gTileOf(I.st, I);
        try { if (T && T.tap) T.tap(); } catch (eT) { _log('strip: ' + I.key + ' threw ' + ((eT && eT.message) || eT)); }
        _g.sel = null;
        api.refresh();
        _log('strip: ' + I.key + ' tapped');
        return;
      }
      if (_g.sel === I.key) { _g.sel = null; api.refresh(); return; }
      _g.sel = I.key;
      _gEnter(I);
      api.refresh();
      _log('strip -> ' + I.key);
    }
    /* the arrows' banner, built like the core's */
    function _gBarDrop() {
      if (_g.bar && _g.bar.parentNode) _g.bar.parentNode.removeChild(_g.bar);
      if (_g.sl && _g.sl.parentNode) _g.sl.parentNode.removeChild(_g.sl);
      _g.bar = null; _g.sl = null; _g.barKey = '';
    }
    var G_L = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>';
    var G_R = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>';
    function _gPaint(inp) {
      var lo = parseFloat(inp.min) || 0, hi = parseFloat(inp.max) || 0, v = parseFloat(inp.value) || 0;
      var pct = (hi > lo) ? Math.max(0, Math.min(100, (v - lo) / (hi - lo) * 100)) : 0;
      inp.style.backgroundImage = 'linear-gradient(to right,#D2A878 0%,#D2A878 ' + pct + '%,rgba(0,0,0,0.13) ' + pct + '%,rgba(0,0,0,0.13) 100%)';
    }
    function _gSlider(D) {
      var row = document.createElement('div');
      row.className = 'cf-sx-srow';
      var line = document.createElement('div');
      line.className = 'cf-sx-sline';
      var inp = document.createElement('input');
      inp.type = 'range'; inp.className = 'cf-sx-range';
      inp.setAttribute('data-cf-nopinch', '1');
      var val = document.createElement('span');
      val.className = 'cf-sx-val';
      line.appendChild(inp); line.appendChild(val); row.appendChild(line);
      var drag = false;
      inp.addEventListener('pointerdown', function (e) { e.stopPropagation(); drag = true; });
      inp.addEventListener('pointerup', function () { setTimeout(function () { drag = false; }, 60); });
      inp.addEventListener('input', function () {
        drag = true;
        var S = D.core && D.core();
        if (!S || !S.input) return;
        S.input.value = inp.value;
        try { S.input.dispatchEvent(new Event('input')); } catch (eI) {}
        _gPaint(inp);
        try { val.textContent = D.read ? D.read() : inp.value; } catch (eR) {}
      });
      inp.addEventListener('change', function () {
        var S = D.core && D.core();
        if (S && S.input) { try { S.input.dispatchEvent(new Event('change')); } catch (eC) {} }
        drag = false;
      });
      row.__upd = function () {
        var S = D.core && D.core();
        if (!S || !S.input) { row.style.display = 'none'; return; }
        row.style.display = '';
        var on = true;
        try { on = D.on ? !!D.on() : true; } catch (eO) { on = true; }
        if (!drag) {
          try { if (D.sync) D.sync(S); } catch (eS) {}
          inp.min = S.input.min; inp.max = S.input.max; inp.step = S.input.step; inp.value = S.input.value;
        }
        inp.disabled = !on;
        row.classList.toggle('cf-off', !on);
        _gPaint(inp);
        try { val.textContent = D.read ? D.read() : inp.value; } catch (eR2) {}
      };
      return row;
    }
    function _gBarSync() {
      var I = _g.sel ? _gItemBy(_g.sel) : null;
      if (!I || (I.kind !== 'cycle' && I.kind !== 'colors')) { _gBarDrop(); return; }
      var h = _root(opts.root);
      if (!h) return;
      var T = (I.kind === 'cycle' && !I.group) ? _gTileOf(I.st, I) : null;
      /* a group's slider is the one its first tile with a slider carries
         (the shelter's slat gap under boards / slats) */
      if (I.kind === 'cycle' && I.group) T = _gGroup(I.st, I.group).filter(function (t) { return !!t.slider; })[0] || null;
      var D = (T && T.slider) ? T.slider : null;
      var sig = I.key + (D ? ':s' : '');
      if (!_g.bar || _g.barKey !== sig) {
        _gBarDrop();
        var bar = document.createElement('div');
        bar.id = 'cf-sx-bar';
        bar.setAttribute('data-cf-nopinch', '1');
        if (I.kind === 'colors') { var dw = document.createElement('div'); dw.className = 'cf-sx-dots'; bar.appendChild(dw); bar.__dots = dw; }
        var cur = document.createElement('div'); cur.className = 'cf-sx-cur'; bar.appendChild(cur); bar.__cur = cur;
        var pill = document.createElement('div'); pill.className = 'cf-sx-pill';
        var pv = _gBtn('cf-sx-arw', G_L, 'Back'), nx = _gBtn('cf-sx-arw', G_R, 'Next');
        var lb = document.createElement('span'); lb.className = 'cf-sx-lbl';
        var key = I.key;
        pv.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _gStep(key, -1); });
        nx.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _gStep(key, 1); });
        pill.appendChild(pv); pill.appendChild(lb); pill.appendChild(nx);
        bar.appendChild(pill); bar.__lbl = lb;
        h.appendChild(bar);
        _g.bar = bar; _g.barKey = sig;
        if (D) {
          var sw = document.createElement('div');
          sw.id = 'cf-sx-sl';
          sw.setAttribute('data-cf-nopinch', '1');
          var rr = _gSlider(D);
          sw.appendChild(rr); sw.__row = rr;
          h.appendChild(sw);
          _g.sl = sw;
        }
      }
      var B = _g.bar;
      B.__lbl.textContent = I.tip || '';
      var v = _gValue(I);
      if (B.__cur.textContent !== v) B.__cur.textContent = v;
      if (B.__dots && I.kind === 'colors') {
        var S = _gSlot(I);
        B.__dots.innerHTML = '';
        if (S) (S.dots || []).forEach(function (d) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'cf-sx-dot' + (d.key === S.cur ? ' cf-on' : '');
          var hx = d.hex;
          b.style.background = (typeof hx === 'number') ? ('#' + ('000000' + hx.toString(16)).slice(-6)) : String(hx || '#888888');
          b.setAttribute('aria-label', d.name || d.key);
          b.addEventListener('click', function (e) {
            e.preventDefault(); e.stopPropagation();
            _gEnter(I);
            try { S.set(d.key); } catch (eS) { _log('strip: colour threw ' + ((eS && eS.message) || eS)); }
            api.refresh();
            _log('strip: colour ' + (S.key || '?') + ' -> ' + d.key);
          });
          B.__dots.appendChild(b);
        });
      }
      if (_g.sl && _g.sl.__row) _g.sl.__row.__upd();
    }
    function _gRender(isOpen) {
      var h = _root(opts.root);
      if (!h) return;
      if (typeof stripCss === 'function') stripCss(25);
      h.classList.toggle('cf-sx-open', !!isOpen);
      if (!isOpen) { if (_g.el) _log('strip closed'); _gBarDrop(); _gTeardown(); return; }
      if (!_g.el) {
        var el = document.createElement('nav');
        el.id = 'cf-sx-strip';
        el.setAttribute('data-cf-nopinch', '1');
        var x = _gBtn('cf-sx-x', G_X, 'Close');
        x.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _gTipHide(); api.close(); });
        var lst = document.createElement('div');
        lst.className = 'cf-sx-list';
        lst.addEventListener('scroll', function () {
          _gTipHide();
          /* the fades and the arrow follow the scroll (ui-v211 / v212) */
          try { stripMore(_g.el, lst); } catch (eF) {}
        });
        el.appendChild(x); el.appendChild(lst);
        if (getComputedStyle(h).position === 'static') h.style.position = 'relative';
        h.appendChild(el);
        _g.el = el; _g.list = lst;
        if (!_g.tip) {
          _g.tip = document.getElementById('cf-sx-tip');
          if (!_g.tip) { _g.tip = document.createElement('div'); _g.tip.id = 'cf-sx-tip'; h.appendChild(_g.tip); }
        }
        _log('strip open -- every tile its own icon (the product\'s own wizard)');
      }
      var items = _gItems(), i;
      _g.items = items;
      if (_g.sel && !_gItemBy(_g.sel)) _g.sel = null;
      var sig = items.map(function (I) { return I.key; }).join(',');
      if (sig !== _g.sig) {
        _g.sig = sig;
        _g.list.innerHTML = '';
        items.forEach(function (I) {
          var b = _gBtn('cf-sx-b' + (I.kind === 'toggle' ? ' cf-sx-tg' : ''), I.svg || '', I.tip);
          b.setAttribute('data-k', I.key);
          b.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); var I2 = _gItemBy(I.key); if (I2) _gTap(I2, b, e); });
          b.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') _gTipShow(b, I.tip, 0); });
          b.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') _gTipHide(); });
          _g.list.appendChild(b);
        });
        _log('strip ' + items.length + ' icon(s): ' + sig.slice(0, 300));
      }
      var bs = _g.list.querySelectorAll('.cf-sx-b');
      for (i = 0; i < bs.length; i++) {
        var I3 = _gItemBy(bs[i].getAttribute('data-k'));
        if (!I3) continue;
        if (I3.svgFn) { var html = ''; try { html = I3.svgFn() || ''; } catch (eS) { html = ''; } if (html && bs[i].__html !== html) { bs[i].innerHTML = html; bs[i].__html = html; } }
        var on = false;
        if (I3.kind === 'toggle') { var T = _gTileOf(I3.st, I3); try { on = !!(T && T.on && T.on()); } catch (eO) { on = false; } }
        bs[i].classList.toggle('cf-on', on);
        bs[i].classList.toggle('cf-sel', _g.sel === I3.key);
      }
      stripFit(_g.el, _g.list);
      _gBarSync();
      var M = _g.sel ? _gItemBy(_g.sel) : null;
      if (M && M.kind === 'modal') _gModal(M, M.st); else _gModalDrop();
    }

    var api = {
      isOpen: function () { return open; },
      /* a product asks this to decide whether to open its own drawer or to
         render into the step's panel instead */
      /* ONE SHAPE. It is a drawer at every width -- what changes is which edge
         it comes from. Products asked this to decide whether to draw their own
         bar strip; there is nothing for them to draw any more. */
      _shape: function () { return 'drawer'; },
      shape: function () { return 'drawer'; },
      /* the strip is neither edge: a product that moves a dialog into the
         side drawer keeps its dialog (ui-v195) */
      edge: function () { if (api.strip()) return 'strip'; return _isDesk() ? 'right' : 'bottom'; },
      current: function () { var L = live(); return L[Math.min(idx, L.length - 1)] || null; },
      /* the steps on offer right now, in order (ui-v192, the icon strip) */
      steps: function () { return live(); },
      /* true when the host draws the icon strip instead of this drawer */
      strip: function () {
        if (opts.strip === false) return false;
        if (typeof opts.strip === 'function') { try { return !!opts.strip(); } catch (e) { return false; } }
        return _gOn();
      },

      refresh: function () {
        var L = live();
        if (idx > L.length - 1) idx = Math.max(0, L.length - 1);
        row.classList.toggle('cfg-ui-on', open);
        /* WHICH SHAPE, DECIDED HERE AND NOWHERE ELSE. The window is asked on
           every refresh rather than once at boot, so turning a tablet or
           dragging a window between screens lands in the right one -- and the
           canvas is narrowed only while the drawer is actually open, so a
           closed configurator uses the whole width as before (Marek). */
        /* ONE SHAPE AT A TIME, AND THE OTHER PUT AWAY PROPERLY.

           Switching to the drawer never hid the bar -- it just stopped updating
           it -- so at any width where the shape changed, both were on screen at
           once. And going the other way removed only 'open' from the drawer,
           leaving 'active' behind, which is the class the theme actually reads.
           Each branch now closes the other before it opens itself. */
        var desk = _isDesk();
        var barEl = $('cfg-ui-wiz');
        var drEl  = $('cfg-ui-drawer');
        /* THE ICON STRIP INSTEAD OF THE DRAWER (ui-v192, Marek: misto sirokeho
           draweru uzky drawer s ikonami). The steps stay the steps -- enter,
           exit, onStep, editing(key) all mean what they meant -- only the
           drawer is not drawn: the host draws its narrow strip of icons and
           puts each step's controls on the canvas. The canvas is not shifted,
           the strip lies over the rail. */
        if (api.strip()) {
          _deskShell(false);
          if (drEl) { drEl.classList.remove('active'); drEl.classList.remove('open'); }
          if (barEl) barEl.classList.add('cfg-ui-wiz-hidden');
          try { if (opts.renderStrip) opts.renderStrip(open, api); else _gRender(open); }
          catch (eStr) { _log('strip render threw: ' + ((eStr && eStr.message) || eStr)); }
          return api;
        }
        _deskShell(desk && open);

        /* THE BAR IS THE PENCIL AND NOTHING ELSE, AT EVERY WIDTH.

           The pencil lives inside the bar's row because that is where it was
           drawn, and it is what OPENS the wizard -- so the row stays and wears
           a class that hides the panel parts. Hiding the row wholesale, which I
           tried first, takes the pencil with it and leaves a configurator that
           cannot be opened at all. */
        if (barEl) barEl.classList.add('cfg-ui-wiz-hidden');

        api._drawer();
        return api;
      },

      /* THE SAME STEPS, LISTED. Numbered rows with a rule between them, and
         only the active one opened out to show its hint and its icons -- a step
         can change what the viewport shows, so having several open at once
         would be claiming things about the plan that are not true.
         It builds the SAME icon buttons as the bar, through the same
         _iconButton, so a toggle behaves identically in both shapes and there
         is no second place for an icon's contract to be got wrong (Marek). */
      _drawer: function () {
        var host = _root(opts.root);
        if (!host) return api;
        /* THE THEME'S DRAWER, BONE FOR BONE. The cart drawer is the pattern
           every side panel here follows: side-panel on the outside, then
           side-panel-inner, a side-panel-header holding an h6 title and a
           side-panel-close, and side-panel-content for the body. Its stylesheet
           already gives it the width, the surface, the slide and the backdrop.
           My leva-icon-btn with a cross in it was a close button I made up --
           side-panel-close is the one this theme listens to, so the drawer even
           closes by the theme's own handler (Marek). */
        var dr = $('cfg-ui-drawer');
        if (!dr) {
          dr = document.createElement('div');
          dr.id = 'cfg-ui-drawer';
          /* NO THEME CLASSES. THIRD TIME THIS HAS BITTEN.

             side-panel gave us the theme's width, slide and surface for free --
             and with them its overlay, which dims the model, and its
             close-on-outside-click, which shut the drawer the moment you
             touched the building. Both are correct for a cart drawer and both
             are wrong here, because the thing behind this panel is the thing
             being edited.

             The picker learned this, the rail learned this, and I left the
             wizard's own drawer carrying them. It styles itself now; the rules
             below already set every property that matters. */
          dr.className = 'cfg-ui-drawer';
          dr.setAttribute('tabindex', '-1');
          document.body.appendChild(dr);
        }
        /* which edge it comes from is decided here and nowhere else */
        dr.classList.toggle('cfg-ui-drawer-bottom', !_isDesk());
        dr.classList.toggle('active', open);
        dr.classList.toggle('open', open);

        /* TELL THE PAGE A DRAWER IS OPEN.

           The floating icon bar sits at z-index 2147483647 -- the largest there
           is -- so nothing can be layered over it, and on a phone it lands
           exactly where this drawer now comes up. Racing it with a bigger
           number is not available and would be the wrong move anyway: two
           things fighting for the top of the same screen is not a stacking
           problem, it is a question of which one the person is using.

           It was told with body.open-cc for a while, which worked and brought
           the theme's overlay with it. Pinning the bar to z-index 50 does the
           same job without saying anything to the theme at all. */
        /* AND open-cc IS NOT SET EITHER.

           That class is how the theme is told a drawer is open, and showing its
           overlay is exactly what it does about it. I added it to make the
           floating icon bar stand down -- but the bar was then pinned to
           z-index 50, under this drawer, so it no longer needs telling. Setting
           it now buys nothing and costs the scrim. */
        if (!open) return api;
        /* PARK THE BORROWED BITS BEFORE WIPING THE DRAWER. The slider is the
           product's own element, moved in here -- and rewriting the drawer to
           redraw it was DELETING it. That is why it went from sitting outside
           the drawer to being nowhere at all: the first refresh after it moved
           in destroyed it. Anything borrowed goes back to the body first and is
           taken again on the way through (Marek). */
        var kept = dr.querySelectorAll('.cfg-ui-in-step'), kq;
        for (kq = 0; kq < kept.length; kq++) {
          kept[kq].style.display = 'none';
          document.body.appendChild(kept[kq]);
        }
        var L = live(), i;
        /* THE CLOSE ICON IS THE THEME'S, COPIED OFF THE PAGE. It comes from a
           Liquid snippet that this file cannot render, and my own cross drew
           nothing -- which is why the header was a bare white band with no way
           out of it. Another side panel is already in the document, so its
           close element is cloned: same markup, same glyph, same size, and it
           cannot go out of step with the theme because it IS the theme's
           (Marek). */
        dr.innerHTML =
          '<div class="side-panel-inner">' +
            '<div class="side-panel-header"><div>' +
              '<span class="h6"></span>' +
            '</div></div>' +
            '<div class="side-panel-content"></div>' +
          '</div>';
        dr.querySelector('.h6').textContent = opts.title || 'EDIT';
        var hdIn = dr.querySelector('.side-panel-header > div');
        /* the theme's own close, from snippets/svg-icons.liquid: 14px, stroked
           at 1.5 in var(--color-body), so it takes the theme's colour rather
           than a value written here */
        var xEl = document.createElement('side-panel-close');
        xEl.className = 'side-panel-close';
        xEl.innerHTML =
          '<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
          '<path d="M13 1L1 13M13 13L1 1" stroke="var(--color-body)" stroke-width="1.5" ' +
          'stroke-linecap="round" stroke-linejoin="round"/></svg>';
        xEl.addEventListener('click', function () { api.close(); });
        hdIn.appendChild(xEl);
        var body = dr.querySelector('.side-panel-content');
        for (i = 0; i < L.length; i++) {
          (function (st, n) {
            var row2 = document.createElement('div');
            row2.className = 'cfg-ui-step' + ((n === idx && !folded) ? ' on' : '');
            var hd = document.createElement('div');
            hd.className = 'cfg-ui-step-hd';
            hd.innerHTML = '<span class="cfg-ui-step-no">' + (n + 1) + '</span>' +
                           '<span class="cfg-ui-step-lbl"></span>';
            /* the labels arrive shouted from the products; the manual sets them
               in sentence case, so they are put back into it here rather than
               every product having to remember */
            hd.querySelector('.cfg-ui-step-lbl').textContent =
              String(st.label || '').replace(/([^\s]+)/g, function (w) {
                return (w.length > 2 && w === w.toUpperCase())
                  ? w.charAt(0) + w.slice(1).toLowerCase() : w;
              });
            row2.appendChild(hd);
            var hint = document.createElement('div');
            hint.className = 'cfg-ui-step-hint';
            hint.textContent = st.hint || '';
            row2.appendChild(hint);
            var box = document.createElement('div');
            box.className = 'cfg-ui-step-icons';
            row2.appendChild(box);
            var pane = document.createElement('div');
            pane.className = 'cfg-ui-step-panel';
            row2.appendChild(pane);
            if (n === idx && !folded) {
              var list = (typeof st.icons === 'function') ? st.icons() : (st.icons || []), q;
              var capped = 0;
              for (q = 0; q < list.length; q++) {
                if (list[q] && list[q].label) capped++;
                box.appendChild(api._iconButton(list[q]));
              }
              /* WHAT WAS ACTUALLY DRAWN. Two rounds were spent on captions that
                 never appeared -- once because they went into a renderer
                 nothing calls, once because the product had not named the
                 items. Neither was visible from a log, so both were reported
                 as done. */
              _log('step "' + (st.key || n) + '": ' + list.length + ' tile(s), ' +
                   capped + ' captioned' +
                   (list.length && !capped ? ' -- the product passes no label on any of them' : ''));
              /* A SLIDER IS A CONTROL OF THIS STEP, so it belongs beside the
                 step's icons, not floating over the model. It is not rebuilt --
                 the live element is MOVED here, so whatever the product bound
                 to it keeps working and there is no second slider to keep in
                 step with the first (Marek). */
              /* IT WAS LOOKING TOO EARLY, AND ONLY ONCE. The slider is built by
                 the product during boot, which can land after the drawer has
                 already drawn its first step -- so the element did not exist
                 when we went for it and nothing ever went back for it. It is
                 tried again on the next frame and once more shortly after, and
                 the product's own hiding rule is overridden, because in the
                 drawer it is a row like any other (Marek). */
              /* THE SLIDER STAYS UNDER THE MODEL, and you offered that yourself
                 after I had spent five versions on it. Moving a live control
                 out of the product and into a panel it was never styled for
                 means fighting its own positioning on every refresh -- and it
                 showed: flush to the drawer edge, its readout stretched across
                 the width. Under the model it is already right, and the pitch
                 is a thing you watch the ROOF change for, so beside the roof is
                 not a bad place for it.
                 The hook is left in: a step may still name a slider, and if we
                 ever want it in here it is one line. It is simply not worth
                 what it was costing (Marek). */
              /* the step fills its own panel, and only in this shape: on a
                 phone the same content is still a drawer over the model */
              if (typeof st.panel === 'function') {
                try { st.panel(pane, api); } catch (ePn) {}
              }
            }
            hd.addEventListener('pointerdown', function () { _tapT0 = _nowMs(); });
            hd.addEventListener('click', function () {
              var t1 = _nowMs(), what;
              if (n !== idx) { folded = false; api.goto(n); what = 'step "' + (st.key || n) + '" opened by a tap on its heading'; }
              else { folded = !folded; api.refresh(); what = 'step "' + (st.key || n) + '" ' + (folded ? 'folded shut by a second tap' : 'opened again'); }
              var t2 = _nowMs();
              _tapNote = what + ' -- finger to handler ' + Math.round(t1 - _tapT0) + ' ms, work ' + Math.round(t2 - t1) + ' ms';
              _log('wizard ' + _tapNote);
            });
            body.appendChild(row2);          /* in the document before anything moves into it */
          })(L[i], i);
        }
        return api;
      },

      /* --------------------------------------------------------------------
         LABELLED ROWS OF TILES -- the edit panel, drawn once.

         A step whose options fall into groups wants headings, not one long
         row: ROOF, then CEILING, then BASE. The ADU grew that for itself and
         it is the shape the guide has settled on, so it belongs here rather
         than in whichever product happened to need it first.

         The tiles are _iconButton, so a tile in a group row and a tile in the
         step's icon row are the same object with the same states -- which is
         the whole point, and is why the ADU's own copy could drift from it.

         groups is [{ label, items:[{ svg, label, on, tap }] }], the same shape
         a step's icons already use. A group with no items is skipped rather
         than drawing a heading over nothing.
         -------------------------------------------------------------------- */
      groupRows: function (pane, groups) {
        if (!pane) return api;
        pane.innerHTML = '';
        (groups || []).forEach(function (grp) {
          if (grp && grp.range) { pane.appendChild(_rangeRow(grp)); return; }
          if (!grp || !grp.items || !grp.items.length) return;
          var row = document.createElement('div');
          row.style.cssText = 'margin:10px 0 14px;';
          if (grp.label) {
            var lab = document.createElement('div');
            lab.textContent = grp.label;
            lab.style.cssText =
              'font:700 10px/1.2 ' + UX.font + ';letter-spacing:.08em;' +
              'text-transform:uppercase;color:' + UX.muted + ';margin-bottom:7px;';
            row.appendChild(lab);
          }
          var strip = document.createElement('div');
          strip.style.cssText = 'display:flex;flex-wrap:wrap;gap:8px;';
          grp.items.forEach(function (it) { strip.appendChild(api._iconButton(it)); });
          row.appendChild(strip);
          pane.appendChild(row);
        });
        return api;
      },

      /* one button, one contract, both shapes */
      _iconButton: function (ic) {
        var b = document.createElement('button');
        b._tipText = ic.title || '';
        b.type = 'button';
        b.className = 'cfg-ui-wiz-ico';
        /* A TILE THAT REPORTS A STATE IS A TOGGLE, whether or not it said so.

           kind:'toggle' was the only way to get the lit state, so a group tile
           that has an on() -- which every one-of-several choice does -- came
           out unlit and every option in the row looked unselected. If it can
           answer on(), it is asked. */
        if (ic.kind === 'toggle' || typeof ic.on === 'function') {
          var on2 = ic.on ? !!ic.on() : false;
          b.className += on2 ? ' cfg-ui-wiz-ico--on' : ' cfg-ui-wiz-ico--off';
          b.setAttribute('aria-pressed', on2 ? 'true' : 'false');
        }
        if (ic.title) b.title = ic.title;

        /* THE GLYPH AND WHAT IT MEANS.

           A row of bare icons is a quiz: a pin, a tree and a digit are
           guessable one at a time and not as a set. The name used to hang off
           title=, which is a hover tooltip and never fires on the tablet these
           are used from -- so it was there and invisible.

           IT GOES HERE AND NOWHERE ELSE. I put it in the bar's renderer first,
           which has been dead code since the wizard became one drawer, so it
           changed nothing and I reported it as done. This is the button both
           shapes actually build. */
        /* THE NAME, WHEREVER THE PRODUCT PUT IT.

           A tile carries its name as label if the product was written for this,
           and as title if it predates it -- and title was a hover tooltip, which
           never fires on the tablet these are used from. So the older products
           had the words all along and showed none of them: tiny's drawer is a
           row of bare glyphs while the ADU's is captioned, from the same code.

           Taking title as the caption gets every one of them right without a
           line changed in their libs. Long titles are the risk -- the rail
           carries some that run to a sentence -- so anything past a short label
           is left as a tooltip rather than crammed under a glyph. */
        var cap = ic.label || '';
        if (!cap && ic.title && ic.title.length <= 22) cap = ic.title;
        /* a compact tile, a digit and nothing else: the storey cards (core-v412) */
        if (ic.small) b.className += ' cfg-ui-wiz-ico--sm';
        /* A PICTURE INSTEAD OF A GLYPH (core-v417, Marek: dej tyto obrazky misto
           dlazdic): a tile may carry an image URL; it fills the tile, no caption,
           the picture is the name. */
        if (ic.img) {
          b.className += ' cfg-ui-wiz-ico--img';
          b.innerHTML = '<img class="cfg-ui-wiz-ico-p" src="' + ic.img + '" alt="' + cap + '" draggable="false">';
        } else {
          b.innerHTML = '<span class="cfg-ui-wiz-ico-g">' + (ic.svg || '') + '</span>' +
                        '<span class="cfg-ui-wiz-ico-t">' + cap + '</span>';
        }

        if (ic.down) {
          b.style.touchAction = 'none';
          b.addEventListener('pointerdown', function (e) { try { ic.down(e, b); } catch (e2) {} });
        } else {
          b.addEventListener('click', function () {
            if (ic.tap) { try { ic.tap(); } catch (e) {} }
            api.refresh();
          });
        }
        /* a tile that carries its name does not need the tap-tooltip too */
        if (!ic.title || cap) return b;
        /* wrapped, so the label can sit above the button without the row
           having to make space for it */
        var wrap = document.createElement('span');
        wrap.className = 'cfg-ui-tipwrap';
        var tip = document.createElement('span');
        tip.className = 'cfg-ui-tip';
        tip.textContent = ic.title;
        wrap.appendChild(b);
        wrap.appendChild(tip);
        b.addEventListener('pointerdown', function (e) {
          if (e.pointerType === 'mouse') return;      /* a mouse has hover */
          tip.classList.add('show');
          setTimeout(function () { tip.classList.remove('show'); }, 1400);
        });
        return wrap;
      },

      /* Icons belong to the step, so they are rebuilt on every refresh: a
         toggle's on() is re-read each time and cannot go stale after the host
         changes state some other way. */
      _icons: function (st) {
        var box = $('cfg-ui-wiz-icons');
        box.innerHTML = '';
        var list = (st && st.icons) || [], i;
        for (i = 0; i < list.length; i++) {
          (function (ic) {
            var b = document.createElement('button');
            b.type = 'button';
            b.className = 'cfg-ui-wiz-ico';
            if (ic.kind === 'toggle') {
              var on = ic.on ? !!ic.on() : false;
              b.className += on ? ' cfg-ui-wiz-ico--on' : ' cfg-ui-wiz-ico--off';
              b.setAttribute('aria-pressed', on ? 'true' : 'false');
            }
            if (ic.title) b.title = ic.title;

            /* THE GLYPH AND WHAT IT MEANS.

               A row of bare icons is a quiz. A pin, a tree and a digit are
               guessable one at a time and not as a set, and the tooltip that
               used to carry the answer never fires on the tablet these are
               used from -- so the label was there and invisible.

               The caption is the item's own label, which the product supplies
               already translated through its own locale file. Nothing here
               needs a string of its own: this file knows how to draw a tile,
               not what the tile is called.

               An item with no label still draws, as a glyph on its own -- a
               product that has not got round to naming its icons gets exactly
               what it had before rather than a row of empty captions. */
            var cap = ic.label || ic.title || '';
            if (cap) {
              b.className += ' cfg-ui-wiz-ico--cap';
              b.innerHTML = '<span class="cfg-ui-wiz-ico-g">' + (ic.svg || '') + '</span>' +
                            '<span class="cfg-ui-wiz-ico-t">' + cap + '</span>';
            } else {
              b.innerHTML = ic.svg || '';
            }
            /* An icon that starts a gesture cannot also be a click. The host
               needs the pointerdown itself -- to hand to its own drag stack --
               and click would fire again on release and do the thing twice.
               down() opts out of tap entirely: whatever it starts owns the rest
               of the gesture, including deciding that a gesture which never
               moved was a tap.

               touch-action is set here rather than in the stylesheet because it
               is a property of THIS icon's contract, not of the row. */
            if (ic.down) {
              b.style.touchAction = 'none';
              b.addEventListener('pointerdown', function (e) {
                try { ic.down(e, b); } catch (e2) {}
              });
            } else {
              b.addEventListener('click', function () {
                if (ic.tap) { try { ic.tap(); } catch (e) {} }
                api.refresh();
              });
            }
            box.appendChild(b);
          })(list[i]);
        }
        return api;
      },

      /* every jump runs exit() on the old step and enter() on the new one, so
         a step can rely on being torn down even when skipped past by a dot */
      goto: function (i) {
        var L = live();
        i = Math.max(0, Math.min(L.length - 1, i | 0));
        if (open && L[idx] && L[idx].exit && i !== idx) { try { L[idx].exit(); } catch (e) {} }
        idx = i;
        folded = false;
        if (L[idx] && L[idx].enter) { try { L[idx].enter(); } catch (e2) {} }
        api.refresh();
        var key = L[idx] ? L[idx].key : null, gen = ++_gotoGen;
        var fire = function () {
          if (gen !== _gotoGen || !open) return;
          var t0 = _nowMs();
          if (opts.onStep) opts.onStep(key, idx);
          api.refresh();
          _stepNote = 'step "' + key + '" work ' + Math.round(_nowMs() - t0) + ' ms after the drawer was drawn';
        };
        if (opts.deferStep === false) fire(); else setTimeout(fire, 20);
        return api;
      },
      next: function () { return api.goto(idx + 1); },
      prev: function () { return api.goto(idx - 1); },
      /* a step by its key (ui-v140): the canvas bar opens the Floorplan step
         to show a plan it has read, whatever step the drawer was on */
      gotoKey: function (k) {
        var L = live(), i;
        for (i = 0; i < L.length; i++) if (L[i] && L[i].key === k) return api.goto(i);
        return api;
      },
      tapNote: function () { return _tapNote + ' | ' + _stepNote; },

      open: function () {
        if (open) return api;
        open = true;
        /* the strip opens with nothing picked, on the first step (ui-v192):
           the step left over from last time would bring its own grips and
           sliders onto a canvas where no icon says why */
        if (api.strip()) {
          idx = 0; folded = false; _g.sel = null;
          /* the plan strip opens on its own step (ui-v194) */
          var sk = null;
          try { sk = opts.stripStart ? opts.stripStart() : null; } catch (eSk) { sk = null; }
          if (sk) { var Ls = live(), qs; for (qs = 0; qs < Ls.length; qs++) if (Ls[qs] && Ls[qs].key === sk) idx = qs; }
        }
        if (opts.onEnter) opts.onEnter();
        api.goto(idx);
        _log('wizard open');
        return api;
      },
      close: function () {
        if (!open) return api;
        var L = live();
        if (L[idx] && L[idx].exit) { try { L[idx].exit(); } catch (e) {} }
        open = false;
        api.refresh();
        if (opts.onExit) opts.onExit();
        _log('wizard closed');
        return api;
      },
      /* SLIDE AWAY WITHOUT ENDING THE SESSION.

         close() is "I am done": it runs the step's exit and the host's onExit,
         which in a house product means the finished view, a refitted camera and
         mode 5. That is the right thing for the cross in the corner and the
         wrong thing for every case where the panel is simply in the way -- you
         picked a window and now you need to see the wall.

         dismiss() puts the panel away and leaves everything else alone. The
         step is still the step; opening again lands exactly where you were. */
      dismiss: function () {
        if (!open) return api;
        open = false;
        api.refresh();
        _log('wizard dismissed -- step kept');
        return api;
      },

      toggle: function () { return open ? api.close() : api.open(); },
      destroy: function () {
        host.classList.remove('cfg-ui-wiz-host');
        host.classList.remove('cfg-ui-wiz-hidebar');
        if (row.parentNode) row.parentNode.removeChild(row);
      }
    };

    if (!row.__cfgUiBound) {
      row.__cfgUiBound = true;
      $('cfg-ui-wiz-pencil').addEventListener('click', function () { api.open(); });
      $('cfg-ui-wiz-close').addEventListener('click', function () { api.close(); });
      $('cfg-ui-wiz-prev').addEventListener('click', function () { api.prev(); });
      $('cfg-ui-wiz-next').addEventListener('click', function () { api.next(); });
      $('cfg-ui-wiz-dots').addEventListener('click', function (e) {
        var t = e.target;
        if (t && t.getAttribute && t.getAttribute('data-i') != null) api.goto(parseInt(t.getAttribute('data-i'), 10));
      });
    }

    api.refresh();
    _log('wizard ready, ' + steps.length + ' steps');
    /* the resize listener needs to find the live wizard; there is only ever one */
    wizard._live = api;
    return api;
  }


  /* =====================================================================
     UNDO BUTTON

     The glyph and the behaviour are lifted from configurator-aframe.liquid and
     configurator-fence-lib.js, which had worked this out already: an arrow that
     turns back on itself, and a button that never disables -- once the history
     runs out it becomes Restart instead of going grey. A dead control tells you
     nothing; a changed one tells you where you are.

     Fence toggles a cf-undo-reset-mode class and its comment says "the theme
     should swap the SVG". No theme ever did, so the icon has been lying about
     which mode it is in. Here the swap happens where the class does.

     Host supplies: canUndo() -> bool, onUndo(), onReset().
     Host does NOT supply the markup -- one button, one definition.
     ===================================================================== */

  var UNDO_SVG =
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"' +
    ' stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M9 14L4 9l5-5"/><path d="M4 9h11a5 5 0 0 1 0 10h-4"/></svg>';

  /* Restart: the same arrow closed into a full turn. Same weight, same joins --
     it reads as the sibling of the undo arrow rather than a different family. */
  var RESTART_SVG =
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"' +
    ' stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M3 12a9 9 0 1 0 2.6-6.4"/><path d="M3 4v5h5"/></svg>';

  function undoButton(opts) {
    opts = opts || {};
    var el = $(opts.id || 'cf-btn-undo');
    if (!el) { _log('undoButton: #' + (opts.id || 'cf-btn-undo') + ' not in the page'); return null; }

    var api = {
      el: el,
      refresh: function () {
        var can = opts.canUndo ? !!opts.canUndo() : false;
        /* never disabled: out of history is a different job, not a dead end */
        el.disabled = false;
        el.style.opacity = '';
        el.style.cursor = 'pointer';
        el.innerHTML = can ? UNDO_SVG : RESTART_SVG;
        el.title = can ? (opts.undoLabel || 'Undo') : (opts.resetLabel || 'Reset to default');
        el.setAttribute('aria-label', el.title);
        el.classList.toggle('cf-undo-reset-mode', !can);
        return api;
      }
    };

    if (!el.__cfgUiUndo) {
      el.__cfgUiUndo = true;
      el.addEventListener('click', function () {
        if (opts.canUndo && opts.canUndo()) { if (opts.onUndo) opts.onUndo(); }
        else if (opts.onReset) opts.onReset();
        api.refresh();
      });
    }
    api.refresh();
    _log('undo button ready');
    return api;
  }

  function cutPlanes(opts) {
    opts = opts || {};
    opts.log = opts.log || function () {};
    opts.fmtLen = opts.fmtLen || function (m) { return Math.round(m * 1000) + ' mm'; };
  /* --------------------------------------------------------------------------
       CUT PLANES -- shared. A product calls api.cutPlanes({ stage, rootId, buttonId,
       unitBtnId, fmtLen, log, controls, view }) once and gets { toggle, on,
       apply, planes }. The rail's colours button becomes the section tool: tapping it
       puts three buttons at the bottom of the canvas -- X, Y, Z -- a slider that
       moves the cut across the whole building along the chosen axis, and a
       flip that keeps the other side. The cut is the renderer's own clipping
       plane, so every layer is cut at once and the bodies show hollow where the
       plane passes through them; nothing is rebuilt and nothing is billed
       differently. Tap the rail button again and the cut is gone (Marek: cut
       planes in the right rail in place of the colours, three icons x / y / z
       at the bottom centre with the section and a handle to drag it).
       -------------------------------------------------------------------------- */
    var _cut = { on: false, axis: 'x', t: 0.5, flip: false, panel: null, timer: 0, box: null,
                 mesh: null, handle: null, plane: null, stencil: null, stencilKey: '' };

    function _cutBounds() {
      var T = global.THREE, st = opts.stage();
      if (!T || !st || !st.group) return null;
      try {
        var bx = new T.Box3().setFromObject(st.group);
        if (!isFinite(bx.min.x) || !isFinite(bx.max.x)) return null;
        return bx;
      } catch (e) { return null; }
    }

    /* the plane of the cut, drawn: a light grey translucent sheet across the
       building at the cut, on the kept side by a millimetre so it is not cut
       itself; sized to the building's own extent across the axis */
    /* THE CUT IS CAPPED. A body cut open is a body with one wall missing, and
       through the hole you see its inside -- a wool batt reads full because it
       is thin, an attic full of blown fill reads as a room. So the section gets
       a lid: every body the plane passes through is counted into the stencil
       (its back faces add, its front faces take away -- inside a body the count
       is not zero), and one plane at the cut is painted, opaque, wherever the
       count says there is a body. Every cut solid shows a flat grey section
       face, the way a drawing hatches it; where nothing is cut the plane is a
       faint tint (Marek: a volume must read solid on the cut). */
    function _cutMesh(bx, c) {
      var T = global.THREE, st = opts.stage();
      if (!T || !st || !st.scene) return;
      if (_cut.mesh) { try { st.scene.remove(_cut.mesh); } catch (e0) {} _cut.mesh = null; }
      if (!_cut.on || !bx) return;
      var ax = _cut.axis, sz = new T.Vector3().subVectors(bx.max, bx.min);
      var mid = new T.Vector3().addVectors(bx.min, bx.max).multiplyScalar(0.5);
      var w = ax === 'x' ? sz.z : sz.x, h = ax === 'y' ? sz.z : sz.y;
      var G = new T.Group();
      /* the faint tint over the whole section, for the eye to find the plane */
      var tint = new T.Mesh(new T.PlaneGeometry(w * 1.05, h * 1.05),
        new T.MeshBasicMaterial({ color: 0x777777, transparent: true, opacity: 0.10,
                                  side: T.DoubleSide, depthWrite: false }));
      tint.renderOrder = 30;
      G.add(tint);
      /* ONE CAP PER COLOUR, IN THAT COLOUR. The stencil is counted one colour
         group at a time and its cap painted in the group's own colour before
         the next group counts -- so wood is capped in wood, wool in wool, the
         blown fill in its own beige, and the section reads as the bodies it
         cuts, not as one grey (Marek). The cap also resets the stencil where it
         draws, so the next group starts clean. */
      var groups = _cut.capGroups || [], gq;
      for (gq = 0; gq < groups.length; gq++) {
        var capM = new T.MeshStandardMaterial({ color: groups[gq].col, roughness: 0.95, metalness: 0.0, side: T.DoubleSide });
        capM.stencilWrite = true;
        capM.stencilRef = 0;
        capM.stencilFunc = T.NotEqualStencilFunc;
        capM.stencilFail = T.ReplaceStencilOp;
        capM.stencilZFail = T.ReplaceStencilOp;
        capM.stencilZPass = T.ReplaceStencilOp;
        var cap = new T.Mesh(new T.PlaneGeometry(w * 1.05, h * 1.05), capM);
        cap.renderOrder = groups[gq].order + 1;   /* right after this group's stencil pass */
        G.add(cap);
      }
      var keepDir = _cut.flip ? 1 : -1;   /* the kept side is below c unless flipped */
      if (ax === 'x') { G.rotation.y = Math.PI / 2; G.position.set(c + keepDir * 0.0005, mid.y, mid.z); }
      else if (ax === 'y') { G.rotation.x = -Math.PI / 2; G.position.set(mid.x, c + keepDir * 0.0005, mid.z); }
      else { G.position.set(mid.x, mid.y, c + keepDir * 0.0005); }
      G.userData.arSkip = true;
      st.scene.add(G);
      _cut.mesh = G;
      if (_cut.showPlane === false) G.visible = false;
    }

    /* THE STENCIL PASSES: for every mesh of the house a back-face clone that
       adds to the stencil and a front-face clone that subtracts, both clipped by
       the same plane and drawing no colour. Built once per model (rebuilt when
       the group changes), sharing the originals' geometry and world transform,
       so moving the plane costs nothing. */
    /* OPEN SHEETS ARE LEFT OUT OF THE SECTION'S FILL (core-v504, moved from
       the A-frame lib, aframe-v52/v53, Marek: pri rezu je videt seda plocha,
       ktera se natacela). The cap fills a body by a stencil count of its
       faces, and only a body that is closed and wound one way round closes
       the count: a sheet with no thickness (a board drawn as a plane, a film,
       a folded flashing, a skin piece cut round a window) or a box with faces
       wound some in, some out, left the stencil odd and the fill spread over
       the cut plane wherever the eye looked through it. Every mesh is checked
       here, for every product: each directed edge must be walked exactly once
       and its reverse exactly once; anything else is skipped as if it were
       flagged isEdge. The answer is kept per geometry. */
    var _openMemo = {};
    function _cutIsOpen(geo) {
      if (!geo || !geo.attributes || !geo.attributes.position) return true;
      if (_openMemo[geo.uuid] != null) return _openMemo[geo.uuid];
      var pos = geo.attributes.position, idx = geo.index, n = idx ? idx.count : pos.count, E = {}, i, k;
      var key = function (j) { return Math.round(pos.getX(j) * 1e4) + ',' + Math.round(pos.getY(j) * 1e4) + ',' + Math.round(pos.getZ(j) * 1e4); };
      for (i = 0; i + 2 < n; i += 3) {
        var a = key(idx ? idx.getX(i) : i), b = key(idx ? idx.getX(i + 1) : i + 1), c = key(idx ? idx.getX(i + 2) : i + 2);
        if (a === b || b === c || c === a) continue;          /* a sliver of no area closes nothing */
        var es = [[a, b], [b, c], [c, a]];
        for (k = 0; k < 3; k++) { var e = es[k][0] + '>' + es[k][1]; E[e] = (E[e] || 0) + 1; }
      }
      var open = false, ek;
      for (ek in E) {
        if (!E.hasOwnProperty(ek)) continue;
        var pr = ek.split('>'), back = pr[1] + '>' + pr[0];
        if (E[ek] !== 1 || E[back] !== 1) { open = true; break; }
      }
      _openMemo[geo.uuid] = open;
      return open;
    }
    function _cutStencil(st, planes) {
      var T = global.THREE;
      if (!T || !st || !st.scene) return;
      if (_cut.stencil) { try { st.scene.remove(_cut.stencil); } catch (e0) {} _cut.stencil = null; }
      if (!planes || !st.group) return;
      var G = new T.Group(), n = 0, nOpen = 0;
      _openMemo = {};
      var byCol = {}, order = [];
      st.group.updateMatrixWorld(true);
      st.group.traverse(function (node) {
        if (!node.isMesh || !node.geometry || !node.material) return;
        if (node.userData && node.userData.isEdge) return;
        var opn = true; try { opn = _cutIsOpen(node.geometry); } catch (eOp) { opn = true; }
        if (opn) { nOpen++; return; }
        /* only what is on screen: a body hidden by its layer (the drawings
           page) or by itself has no cap -- walk the parents */
        var pv = node, shown = true;
        while (pv) { if (pv.visible === false) { shown = false; break; } pv = pv.parent; }
        if (!shown) return;
        var m0 = Array.isArray(node.material) ? node.material[0] : node.material;
        if (!m0 || m0.visible === false || m0.transparent && m0.opacity < 0.5) return;
        /* the group's colour: the material's own, or a mid tone for a textured
           one (a print or a tile shows white as its colour); a body drawn in
           ink (the drawings page, emissive white) caps white */
        var inked = m0.emissive && m0.emissive.r > 0.6 && m0.emissive.g > 0.6 && m0.emissive.b > 0.6;
        var col = inked ? 0xFFFFFF
                : (m0.userData && m0.userData.capCol != null) ? m0.userData.capCol
                : (m0.map ? 0xC2B49A : (m0.color ? m0.color.getHex() : 0xC9C7C2));
        var key = String(col);
        if (!byCol[key]) { byCol[key] = { col: col, nodes: [] }; order.push(key); }
        byCol[key].nodes.push(node);
      });
      var capGroups = [], gq, ro = 900;
      for (gq = 0; gq < order.length; gq++) {
        var grp = byCol[order[gq]], q;
        for (q = 0; q < grp.nodes.length; q++) {
          var node = grp.nodes[q];
          var base = { depthWrite: false, depthTest: false, colorWrite: false,
                       stencilWrite: true, stencilFunc: T.AlwaysStencilFunc, clippingPlanes: planes };
          var mb = new T.MeshBasicMaterial(base);
          mb.side = T.BackSide;
          mb.stencilFail = T.IncrementWrapStencilOp; mb.stencilZFail = T.IncrementWrapStencilOp; mb.stencilZPass = T.IncrementWrapStencilOp;
          var mf = new T.MeshBasicMaterial(base);
          mf.side = T.FrontSide;
          mf.stencilFail = T.DecrementWrapStencilOp; mf.stencilZFail = T.DecrementWrapStencilOp; mf.stencilZPass = T.DecrementWrapStencilOp;
          var a = new T.Mesh(node.geometry, mb), b = new T.Mesh(node.geometry, mf);
          a.matrixAutoUpdate = false; b.matrixAutoUpdate = false;
          a.matrix.copy(node.matrixWorld); b.matrix.copy(node.matrixWorld);
          a.renderOrder = ro; b.renderOrder = ro;
          a.userData.arSkip = true; b.userData.arSkip = true;
          G.add(a); G.add(b);
          n++;
        }
        capGroups.push({ col: grp.col, order: ro });
        ro += 2;
      }
      G.userData.arSkip = true;
      st.scene.add(G);
      _cut.stencil = G;
      _cut.nStencil = n;
      _cut.capGroups = capGroups;
      _cut.nOpen = nOpen;
      opts.log('section  ' + n + ' bod(ies) filled when cut, ' + nOpen + ' open sheet(s) left unfilled (core check, every product)');
    }

    /* THE CUT IS ON THE LAYERS' MATERIALS, NOT ON THE RENDERER. A global plane
       cuts everything drawn, the handle and the arrow included -- the arrow
       points into the removed side and was simply clipped away. So the plane
       goes onto every material of the building group (local clipping), and
       the overlay, the cross and the arrow stay whole. The planes are also
       kept on the renderer under a name of their own for the AR export. */
    /* AND THE BODIES STAY BODIES. A cut box is a box with one side open: the
       renderer drops the faces that look away, so through the cut you saw out
       the back of every wool batt, joist and fill and read them as planes with
       nothing inside (Marek). While the cut is on every material of the house
       is drawn double-sided, so the inside walls of a cut body show in the
       body's own colour and the batt is full, the joist is full, the blown fill
       is full. Off, the sides go back to what each material had. */
    function _cutMaterials(st, planes) {
      if (!st || !st.group) return 0;
      var T = global.THREE, n = 0;
      st.group.traverse(function (node) {
        if (!node.isMesh || !node.material) return;
        var mats = Array.isArray(node.material) ? node.material : [node.material], k;
        for (k = 0; k < mats.length; k++) {
          var m = mats[k];
          if (!m) continue;
          if (planes) {
            if (m.userData.__side === undefined) m.userData.__side = m.side;
            m.side = T.DoubleSide;
          } else if (m.userData.__side !== undefined) {
            m.side = m.userData.__side;
            delete m.userData.__side;
          }
          m.clippingPlanes = planes;
          m.needsUpdate = true;
          n++;
        }
      });
      return n;
    }

    function _cutApply() {
      var T = global.THREE, st = opts.stage();
      if (!T || !st || !st.renderer) return;
      if (!_cut.on) {
        st.renderer.clippingPlanes = [];
        st.renderer.__sectionPlanes = [];
        _cutMaterials(st, null);
        _cutStencil(st, null);
        _cut.stencilKey = '';
        _cut.capGroups = null;
        _cutMesh(null, 0);
        _cutHandle(null, 0);
        return;
      }
      var bx = _cutBounds();
      if (!bx) return;
      _cut.box = bx;
      var ax = _cut.axis, lo = bx.min[ax], hi = bx.max[ax];
      var c = lo + (hi - lo) * _cut.t;
      /* keep the side BELOW c on the axis: n.p + d >= 0 with n = -e, d = c */
      var n = new T.Vector3(ax === 'x' ? -1 : 0, ax === 'y' ? -1 : 0, ax === 'z' ? -1 : 0);
      var d = c;
      if (_cut.flip) { n.multiplyScalar(-1); d = -c; }
      if (!_cut.plane) _cut.plane = new T.Plane(n, d);
      else _cut.plane.set(n, d);
      var planes = [_cut.plane];
      st.renderer.localClippingEnabled = true;
      st.renderer.clippingPlanes = [];
      st.renderer.__sectionPlanes = planes;
      _cut.nMat = _cutMaterials(st, planes);
      /* the stencil clones follow the model, not the plane: rebuilt only when
         the group's make-up changes */
      var key = String(st.group ? st.group.children.length : 0) + ':' + (_cut.nMat | 0);
      if (!_cut.stencil || key !== _cut.stencilKey) { _cutStencil(st, planes); _cut.stencilKey = key; }
      _cutMesh(bx, c);   /* builds one cap per colour group the stencil found */
      _cutHandle(bx, c);
      _cutLabel(c - lo);
    }

    function _cutLabel(dist) {
      var el = document.getElementById('cf-house-cut-val');
      if (!el) return;
      el.textContent = opts.fmtLen(Math.max(0, dist));
    }

    function _cutButtonsSync() {
      var p = _cut.panel;
      if (p) {
        var k, b = p.querySelectorAll('[data-cut-axis]');
        for (k = 0; k < b.length; k++) {
          var on = b[k].getAttribute('data-cut-axis') === _cut.axis;
          b[k].style.background = on ? '#E8442A' : '#EEF0F2';
          b[k].style.color = on ? '#fff' : '#1A1C1D';
        }
      }
    }

    /* THE CONTROLS: round buttons like the floating menu at the bottom -- X, Y,
       Z and the side switch, the active one red, the others grey -- standing at
       about 15 percent from the top of the canvas so they never meet the edit
       button, and the shared slider under them, the same one the roof pitch
       uses, with the cut's distance from the building's edge riding on its
       thumb in the project's units (Marek). */
    function _cutPanel(show) {
      var root = document.getElementById(opts.rootId || 'cf-root');
      if (!root || opts.noPanel) return;
      if (!show) {
        if (_cut.panel && _cut.panel.parentNode) _cut.panel.parentNode.removeChild(_cut.panel);
        _cut.panel = null;
        var un = $('cf-house-cut-under');
        if (un && un.parentNode) un.parentNode.removeChild(un);
        return;
      }
      if (!_cut.panel) {
        /* the look of the right rail, laid on its side: a white rounded
           rectangle, rounded-square buttons of the rail's size, the active
           axis red and the others light grey (Marek) */
        var p = document.createElement('div');
        p.id = 'cf-cut-panel';
        p.style.cssText = 'position:absolute;left:50%;top:12%;transform:translateX(-50%);' +
          'display:flex;align-items:center;gap:6px;padding:6px;' +
          'background:#fff;border-radius:14px;' +
          'box-shadow:0 2px 10px rgba(0,0,0,0.16);z-index:40;';
        /* the buttons say what the world says: Z is up. three.js has y up, so
           the button Y drives the scene's z and the button Z the scene's y */
        var keys = ['x', 'z', 'y'], names = ['X', 'Y', 'Z'], k;
        for (k = 0; k < 3; k++) {
          var bt = document.createElement('button');
          bt.type = 'button';
          bt.setAttribute('data-cut-axis', keys[k]);
          bt.textContent = names[k];
          bt.style.cssText = 'width:36px;height:36px;border-radius:10px;border:0;' +
            'font:700 13px/1 Montserrat,sans-serif;cursor:pointer;background:#EEF0F2;color:#1A1C1D;';
          bt.addEventListener('click', function (ev) {
            _cut.axis = ev.currentTarget.getAttribute('data-cut-axis');
            _cutButtonsSync(); _cutApply();
            opts.log('cut plane axis -> ' + _cut.axis);
          });
          p.appendChild(bt);
        }
        root.appendChild(p);
        _cut.panel = p;
      }
      _cutButtonsSync();

      /* under the panel: the distance of the cut from the building's edge, and
         under that the side switch -- the rail's own unit-switch glyph, no
         background, static (Marek) */
      if (!$('cf-house-cut-under')) {
        var under = document.createElement('div');
        under.id = 'cf-house-cut-under';
        under.style.cssText = 'position:absolute;left:50%;top:calc(12% + 56px);transform:translateX(-50%);' +
          'display:flex;flex-direction:column;align-items:center;gap:4px;white-space:nowrap;z-index:40;';
        var lab = document.createElement('div');
        lab.id = 'cf-house-cut-val';
        lab.style.cssText = 'padding:2px 8px;border-radius:6px;' +
          'background:rgba(255,255,255,0.96);box-shadow:0 1px 4px rgba(0,0,0,0.12);' +
          'font:700 12px/1.35 Montserrat,sans-serif;color:#1A1C1D;';
        var fl = document.createElement('button');
        fl.type = 'button';
        fl.id = 'cf-house-cut-flip';
        fl.style.cssText = 'width:34px;height:28px;border:0;background:transparent;' +
          'color:#1A1C1D;cursor:pointer;padding:0;display:flex;align-items:center;justify-content:center;';
        var unitBtn = document.getElementById(opts.unitBtnId || 'cf-btn-unit');
        if (unitBtn && unitBtn.innerHTML) fl.innerHTML = unitBtn.innerHTML;
        else fl.textContent = '<->';
        fl.addEventListener('click', function () {
          _cut.flip = !_cut.flip;
          _cutButtonsSync(); _cutApply();
          opts.log('cut plane side flipped');
        });
        under.appendChild(lab);
        under.appendChild(fl);
        root.appendChild(under);
      }
      _cutBindDrag();
    }

    /* THE HANDLE IN THE MODEL: a thin cross at the middle of the cut plane with
       an arrow along the axis, drawn light; take it and drag, and the plane
       follows the finger along the axis. The pointer delta is turned into a
       movement along the axis by projecting the axis into screen space. */
    function _cutHandle(bx, c) {
      var T = global.THREE, st = opts.stage();
      if (!T || !st || !st.scene) return;
      if (_cut.handle) { try { st.scene.remove(_cut.handle); } catch (e0) {} _cut.handle = null; }
      if (!_cut.on || !bx) return;
      var ax = _cut.axis, mid = new T.Vector3().addVectors(bx.min, bx.max).multiplyScalar(0.5);
      var pos = mid.clone(); pos[ax] = c;
      var G = new T.Group();
      var mat = new T.LineBasicMaterial({ color: 0x2b2b2b, transparent: true, opacity: 0.85, depthTest: false });
      var arm = 0.35;
      function seg(a, b) {
        var g2 = new T.BufferGeometry().setFromPoints([a, b]);
        var l = new T.Line(g2, mat); l.renderOrder = 50; G.add(l);
      }
      /* the cross lies in the plane: the two axes that are not the cut's */
      var others = ['x', 'y', 'z'].filter(function (k) { return k !== ax; });
      others.forEach(function (k) {
        var a = new T.Vector3(), b = new T.Vector3(); a[k] = -arm; b[k] = arm; seg(a, b);
      });
      /* THE ARROW ALONG THE AXIS, BOTH WAYS, TWICE AS LONG AS THE CROSS ARMS
         and with heads at both ends -- the shape that was on screen first and
         read best (Marek). Nothing here is clipped any more: the cut sits on
         the building's materials, not on the renderer. */
      var e = new T.Vector3(); e[ax] = 1;
      var tip = arm * 2.6, hd = 0.09;
      seg(e.clone().multiplyScalar(-tip), e.clone().multiplyScalar(tip));
      others.forEach(function (k) {
        var s1 = new T.Vector3(); s1[k] = hd;
        seg(e.clone().multiplyScalar(tip), e.clone().multiplyScalar(tip - hd * 1.4).add(s1));
        seg(e.clone().multiplyScalar(tip), e.clone().multiplyScalar(tip - hd * 1.4).sub(s1));
        seg(e.clone().multiplyScalar(-tip), e.clone().multiplyScalar(-tip + hd * 1.4).add(s1));
        seg(e.clone().multiplyScalar(-tip), e.clone().multiplyScalar(-tip + hd * 1.4).sub(s1));
      });
      /* an invisible ball to grab */
      var grab = new T.Mesh(new T.SphereGeometry(0.45, 12, 8), new T.MeshBasicMaterial({ visible: false }));
      grab.userData.cutGrab = true;
      G.add(grab);
      G.position.copy(pos);
      G.userData.arSkip = true;
      st.scene.add(G);
      _cut.handle = G;
      if (_cut.showHandle === false) G.visible = false;
    }

    function _cutBindDrag() {
      var st = opts.stage(), T = global.THREE;
      if (!st || !st.renderer || !st.renderer.domElement || !T) return;
      var cv = st.renderer.domElement;
      if (cv.__cutDrag) return;
      cv.__cutDrag = true;
      var drag = null, ray = new T.Raycaster(), ndc = new T.Vector2();
      function cam() { return ((opts.view ? opts.view() : '3d') === '2d' && st.orthoCam) ? st.orthoCam : st.camera; }
      function toNdc(ev) {
        var r = cv.getBoundingClientRect();
        ndc.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1);
        return r;
      }
      cv.addEventListener('pointerdown', function (ev) {
        if (!_cut.on || !_cut.handle || !_cut.box) return;
        var r = toNdc(ev);
        ray.setFromCamera(ndc, cam());
        var hits = ray.intersectObject(_cut.handle, true);
        if (!hits.length) return;
        ev.preventDefault(); ev.stopPropagation();
        var ctl = (opts.controls ? opts.controls() : null); if (ctl) ctl.enabled = false;
        try { cv.setPointerCapture(ev.pointerId); } catch (eP) {}
        /* the axis on screen: where the handle projects and where one metre
           along the axis projects */
        var C = cam(), p0 = _cut.handle.position.clone(), p1 = p0.clone(); p1[_cut.axis] += 1;
        var s0 = p0.project(C), s1 = p1.project(C);
        var vx = (s1.x - s0.x) * r.width / 2, vy = -(s1.y - s0.y) * r.height / 2;
        drag = { id: ev.pointerId, x: ev.clientX, y: ev.clientY, t0: _cut.t, vx: vx, vy: vy,
                 range: _cut.box.max[_cut.axis] - _cut.box.min[_cut.axis] };
      }, true);
      cv.addEventListener('pointermove', function (ev) {
        if (!drag || ev.pointerId !== drag.id) return;
        ev.preventDefault(); ev.stopPropagation();
        var dx = ev.clientX - drag.x, dy = ev.clientY - drag.y;
        var vv = drag.vx * drag.vx + drag.vy * drag.vy;
        if (!(vv > 1e-6) || !(drag.range > 0)) return;
        var metres = (dx * drag.vx + dy * drag.vy) / vv;   /* pointer travel along the axis, in metres */
        _cut.t = Math.max(0, Math.min(1, drag.t0 + metres / drag.range));
        _cutApply();
      }, true);
      function end(ev) {
        if (!drag || (ev && ev.pointerId !== drag.id)) return;
        drag = null;
        var ctl = (opts.controls ? opts.controls() : null); if (ctl) ctl.enabled = true;
        opts.log('cut plane at ' + Math.round(_cut.t * 100) + '% along ' + _cut.axis);
      }
      cv.addEventListener('pointerup', end, true);
      cv.addEventListener('pointercancel', end, true);
    }


    /* the rail button's glyph and its black-when-pressed are the liquid's
       (house-liquid-v39): the lib only sets aria-pressed */
    function _cutIcon() {
      try {
        var elC = document.getElementById(opts.buttonId || 'cf-btn-colors');
        if (elC) elC.setAttribute('aria-pressed', _cut.on ? 'true' : 'false');
      } catch (eI) {}
    }

    function cutPlanesToggle() {
      _cut.on = !_cut.on;
      _cutIcon();
      _cutPanel(_cut.on);
      _cutApply();
      /* the model is rebuilt under the tool all the time -- a step change, a
         drag -- so while it is on the plane is re-measured against the fresh
         group a couple of times a second; off, nothing runs */
      if (_cut.timer) { try { global.clearInterval(_cut.timer); } catch (e2) {} _cut.timer = 0; }
      if (_cut.on) _cut.timer = global.setInterval(function () { try { _cutApply(); } catch (e3) {} }, 500);
      opts.log('cut planes ' + (_cut.on ? 'ON  axis=' + _cut.axis : 'off'));
    }


    return {
      toggle: cutPlanesToggle,
      on: function () { return _cut.on; },
      apply: _cutApply,
      /* set the cut from outside -- the drawings page's section views */
      set: function (o) {
        o = o || {};
        if (o.rebuild) { _cut.stencilKey = ''; }
        if (typeof o.handle === 'boolean') { _cut.showHandle = o.handle; if (_cut.handle) _cut.handle.visible = o.handle; }
        /* the grey plane itself, off for a drawing */
        if (typeof o.plane === 'boolean') { _cut.showPlane = o.plane; if (_cut.mesh) _cut.mesh.visible = o.plane; }
        if (o.axis) _cut.axis = o.axis;
        if (typeof o.t === 'number') _cut.t = Math.max(0, Math.min(1, o.t));
        if (typeof o.flip === 'boolean') _cut.flip = o.flip;
        if (o.on === true && !_cut.on) cutPlanesToggle();
        else if (o.on === false && _cut.on) cutPlanesToggle();
        else { _cutButtonsSync(); _cutApply(); }
      },
      planes: function () { var st = opts.stage(); return (st && st.renderer && st.renderer.__sectionPlanes) || []; },
      state: function () { return { on: _cut.on, axis: _cut.axis, t: _cut.t, flip: _cut.flip, nMat: _cut.nMat | 0, nStencil: _cut.nStencil | 0, caps: (_cut.capGroups || []).length, box: _cut.box }; }
    };
  }


  /* --------------------------------------------------------------------------
     TREES -- shared scenery for every configurator (ui-v116). Three kinds,
     each a low-poly paper tree fetched from the CDN once, when its switch is
     first turned on, and drawn as an InstancedMesh: one draw call a kind. A
     tree has a size, a turn and a shade of white, and fades into the
     background with its distance from the building (in the instance colour,
     no transparency). Each kind is placed once from its own seed, well away
     from the building, and kept in the product's state; after that the only
     thing a user can do with a tree is take it and drag it, anywhere but
     onto the building (footprint plus 300 mm). The tree in hand blushes
     red and goes back two seconds after the drop.

     api.trees({ stage, controls, log, origin, footprint, state, save })
       state()  -> the product's tree object { on: {a,b,c}, list: [...] }
       origin() -> { x, z }; footprint() -> { w, l }
     returns { KINDS, build, bindDrag, refresh, toggle, isOn, count }
     -------------------------------------------------------------------------- */
  /* the kinds, in the order the products show them: key, the model, and
     the number a product puts after the translated word Scenery */
  var TREE_KINDS = [
    { key: 'a', n: 1, url: 'https://cdn.shopify.com/3d/models/302bf12dc2946fa5/tree_without_leaves_low_poly.glb' },
    { key: 'b', n: 2, url: 'https://cdn.shopify.com/3d/models/37adec23b7f3a085/low-poly_game-ready_spruce_tree.glb' },
    { key: 'c', n: 3, url: 'https://cdn.shopify.com/3d/models/2649db4fd52fcab0/jungle_tree.glb' }
  ];
  var TREE_GLBS = {};
  TREE_KINDS.forEach(function (k) { TREE_GLBS[k.key] = k.url; });
  var TREE_TINTS = [0xFFFFFF, 0xF6F1E7, 0xF1EBDD, 0xEDE7DB, 0xF9F6EE];
  var _treeGeoCache = {};
  function _treeRng(seed) {
    var s = seed >>> 0 || 1;
    return function () { s = (s * 1664525 + 1013904223) >>> 0; return s / 4294967296; };
  }
  function _treeLoad(kind) {
    var url = TREE_GLBS[kind];
    if (_treeGeoCache[url] && _treeGeoCache[url].geo) return Promise.resolve(_treeGeoCache[url].geo);
    if (_treeGeoCache[url] && _treeGeoCache[url].p) return _treeGeoCache[url].p;
    var T = global.THREE;
    var p = import('https://esm.sh/three@0.160.0/examples/jsm/loaders/GLTFLoader.js')
      .then(function (mod) {
        var Loader = mod.GLTFLoader || (mod.default && mod.default.GLTFLoader) || mod.default;
        return new Promise(function (resolve, reject) { new Loader().load(url, resolve, undefined, reject); });
      })
      .then(function (gltf) {
        var pos = [];
        gltf.scene.updateMatrixWorld(true);
        gltf.scene.traverse(function (o) {
          if (!o.isMesh || !o.geometry) return;
          var gq = o.geometry.clone();
          if (gq.index) gq = gq.toNonIndexed();
          gq.applyMatrix4(o.matrixWorld);
          var a = gq.getAttribute('position').array, k;
          for (k = 0; k < a.length; k++) pos.push(a[k]);
        });
        if (!pos.length) throw new Error('no mesh in the tree glb');
        var g = new T.BufferGeometry();
        g.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
        g.computeBoundingBox();
        var bb = g.boundingBox, h = Math.max(0.001, bb.max.y - bb.min.y);
        g.translate(-(bb.min.x + bb.max.x) / 2, -bb.min.y, -(bb.min.z + bb.max.z) / 2);
        g.scale(1 / h, 1 / h, 1 / h);
        g.computeVertexNormals();
        _treeGeoCache[url] = { geo: g };
        return g;
      })
      .catch(function (err) { delete _treeGeoCache[url]; throw err; });
    _treeGeoCache[url] = { p: p };
    return p;
  }
  function trees(opts) {
    var T = global.THREE;
    var KINDS = ['a', 'b', 'c'], SEEDS = { a: 1337, b: 4242, c: 9001 };
    var _inst = {}, _grp = null, _sel = { kind: null, i: -1 }, _selTimer = 0, _drag = null;
    var log = opts.log || function () {};
    function S() {
      var s = opts.state();
      if (!s || Array.isArray(s)) s = null;
      return s;
    }
    function clearing() {
      var f = opts.footprint();
      return { w: f.w * 1.5, l: f.l * 1.5, reach: Math.max(f.w, f.l) * 3.5 };
    }
    function seed(kind) {
      var C = clearing(), O = opts.origin(), rnd = _treeRng(SEEDS[kind]), out = [], tries = 0, n = 8;
      while (out.length < n && tries < n * 60) {
        tries++;
        var x = (rnd() * 2 - 1) * C.reach, z = (rnd() * 2 - 1) * C.reach;
        if (Math.abs(x) < C.w && Math.abs(z) < C.l) continue;
        if (Math.hypot(x, z) > C.reach) continue;
        var ok = true, q;
        for (q = 0; q < out.length; q++) if (Math.hypot(out[q].x - (O.x + x), out[q].z - (O.z + z)) < 2.6) { ok = false; break; }
        if (!ok) continue;
        out.push({ x: Math.round((O.x + x) * 100) / 100, z: Math.round((O.z + z) * 100) / 100,
                   /* 2.4 to 6.8 m tall: a fifth smaller than before, saplings to modest trees */
                   s: Math.round((2.4 + Math.pow(rnd(), 1.4) * 4.4) * 100) / 100,
                   r: Math.round(rnd() * 6.283 * 100) / 100, t: Math.floor(rnd() * TREE_TINTS.length) });
      }
      return out;
    }
    function listOf(kind) {
      var s = S(); if (!s) return [];
      s.list = s.list || {};
      if (!s.list[kind] || !s.list[kind].length) { s.list[kind] = seed(kind); try { opts.save(); } catch (e) {} }
      /* trees placed by an earlier version are brought to the new size range
         once: half the count, sizes scaled to 80 percent */
      if (!s.v || s.v < 2) {
        var k, L;
        for (k in s.list) if (s.list.hasOwnProperty(k) && s.list[k]) {
          L = s.list[k];
          if (L.length > 8) L.splice(8, L.length - 8);
          L.forEach(function (t) { t.s = Math.round(Math.max(2.4, Math.min(6.8, t.s * 0.8)) * 100) / 100; });
        }
        s.v = 2;
        try { opts.save(); } catch (e2) {}
      }
      /* SAPLINGS ALL THE SAME HEIGHT ARE NOT A SCENE (core-v322). A project saved
         by the old sauna kept its trees as a scale factor of about one, not a
         height in metres; the pass above clamped every one of them to the
         2.4 m floor, and the sauna stood among a row of identical bushes
         (Marek: stromy ve scenery 1 jsou u sauny mensi). A list with every tree
         on the floor cannot have been seeded here -- the sizes are drawn at
         random -- so it gets its heights drawn again, from the kind's own seed,
         and keeps its places. */
      if (!s.v || s.v < 3) {
        var k3, L3, all3, q3, rng3;
        for (k3 in s.list) if (s.list.hasOwnProperty(k3) && s.list[k3] && s.list[k3].length) {
          L3 = s.list[k3]; all3 = true;
          for (q3 = 0; q3 < L3.length; q3++) if (!(L3[q3].s <= 2.45)) { all3 = false; break; }
          if (!all3) continue;
          rng3 = _treeRng((SEEDS[k3] || 1) + 7);
          for (q3 = 0; q3 < L3.length; q3++) L3[q3].s = Math.round((2.4 + Math.pow(rng3(), 1.4) * 4.4) * 100) / 100;
        }
        s.v = 3;
        try { opts.save(); } catch (e3) {}
      }
      return s.list[kind];
    }
    function colorOf(tree, out, kind, idx) {
      var C = clearing(), O = opts.origin();
      var d = Math.hypot(tree.x - O.x, tree.z - O.z), near = Math.max(C.w, C.l);
      var fade = Math.max(0, Math.min(0.82, (d - near) / (C.reach * 1.15 - near)));
      var c = new T.Color(TREE_TINTS[tree.t % TREE_TINTS.length]);
      c.lerp(new T.Color(0xFFFFFF), fade);
      if (_sel.kind === kind && _sel.i === idx) c.lerp(new T.Color(0xE8442A), 0.75);
      out.copy(c);
    }
    function refresh() {
      var m = new T.Matrix4(), c = new T.Color(), k, i;
      for (k = 0; k < KINDS.length; k++) {
        var kind = KINDS[k], inst = _inst[kind]; if (!inst) continue;
        var L = listOf(kind);
        for (i = 0; i < L.length && i < inst.count; i++) {
          m.compose(new T.Vector3(L[i].x, 0, L[i].z), new T.Quaternion().setFromAxisAngle(new T.Vector3(0, 1, 0), L[i].r || 0), new T.Vector3(L[i].s, L[i].s, L[i].s));
          inst.setMatrixAt(i, m); colorOf(L[i], c, kind, i); inst.setColorAt(i, c);
        }
        inst.instanceMatrix.needsUpdate = true;
        if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
      }
    }
    function build() {
      var st = opts.stage(); if (!st || !st.scene) return;
      if (_grp) { st.scene.remove(_grp); _grp = null; _inst = {}; }
      var s = S(); if (!s) return;
      var on = s.on || {}, any = false, k;
      for (k = 0; k < KINDS.length; k++) if (on[KINDS[k]]) any = true;
      if (!any) return;
      var g = new T.Group();
      g.userData.partType = 'scenery'; g.userData.arSkip = true;
      st.scene.add(g);
      _grp = g;
      var grp = g;
      KINDS.forEach(function (kind) {
        if (!on[kind]) return;
        /* the list is read once here, before the geometry comes back, so any
           migration of an old list has happened by the time it is drawn */
        listOf(kind);
        _treeLoad(kind).then(function (geo) {
          if (_grp !== grp) return;
          var L = listOf(kind);
          var mat = new T.MeshStandardMaterial({ color: 0xFFFFFF, roughness: 1.0, metalness: 0.0, flatShading: true });
          var inst = new T.InstancedMesh(geo, mat, Math.max(1, L.length));
          inst.name = 'trees-' + kind; inst.userData.arSkip = true; inst.userData.kind = kind;
          grp.add(inst);
          _inst[kind] = inst;
          refresh();
          log('trees ' + kind + ': ' + L.length + ' placed (one model, instanced)');
        }).catch(function (err) { log('trees ' + kind + ': model failed -- ' + (err && err.message)); });
      });
    }
    function toggle(kind) {
      var s = S(); if (!s) return false;
      s.on = s.on || {};
      s.on[kind] = !s.on[kind];
      try { opts.save(); } catch (e) {}
      build();
      return !!s.on[kind];
    }
    function isOn(kind) { var s = S(); return !!(s && s.on && s.on[kind]); }
    function count() { var s = S(), n = 0, k; if (!s) return 0; for (k = 0; k < KINDS.length; k++) if (s.on && s.on[KINDS[k]]) n += (s.list && s.list[KINDS[k]] ? s.list[KINDS[k]].length : 0); return n; }
    function select(kind, i) {
      if (_selTimer) { try { global.clearTimeout(_selTimer); } catch (e) {} _selTimer = 0; }
      _sel = { kind: kind, i: i }; refresh();
    }
    function release() {
      if (_selTimer) { try { global.clearTimeout(_selTimer); } catch (e) {} }
      _selTimer = global.setTimeout(function () { _selTimer = 0; _sel = { kind: null, i: -1 }; refresh(); }, 2000);
    }
    /* the pointer: a tap on a tree (or close by its trunk) takes it */
    function bindDrag(canvas) {
      if (!canvas || canvas.__treeDrag) return;
      canvas.__treeDrag = true;
      var ray = new T.Raycaster(), ndc = new T.Vector2(), ground = new T.Plane(new T.Vector3(0, 1, 0), 0), hit = new T.Vector3();
      function cam() { var st = opts.stage(); return st ? st.camera : null; }
      function toNdc(ev) { var r = canvas.getBoundingClientRect(); ndc.set(((ev.clientX - r.left) / r.width) * 2 - 1, -((ev.clientY - r.top) / r.height) * 2 + 1); }
      function treeAt(ev) {
        var c = cam(); if (!c || !_grp) return null;
        toNdc(ev); ray.setFromCamera(ndc, c);
        var k, best = null;
        for (k = 0; k < KINDS.length; k++) {
          var inst = _inst[KINDS[k]]; if (!inst) continue;
          var hits = ray.intersectObject(inst, false);
          if (hits.length && (!best || hits[0].distance < best.d)) best = { kind: KINDS[k], i: hits[0].instanceId | 0, d: hits[0].distance };
        }
        if (best) return best;
        /* a little more than the twigs: the trunk line with some room round it */
        var bd = 1e9;
        for (k = 0; k < KINDS.length; k++) {
          var inst2 = _inst[KINDS[k]]; if (!inst2) continue;
          var L = listOf(KINDS[k]), i;
          for (i = 0; i < L.length; i++) {
            var t = L[i], a = new T.Vector3(t.x, 0, t.z), b = new T.Vector3(t.x, t.s * 0.6, t.z);
            var seg = new T.Line3(a, b), p1 = new T.Vector3(), p2 = new T.Vector3();
            /* closest points between the ray and the trunk segment */
            var rd = ray.ray.direction, ro = ray.ray.origin, sd = new T.Vector3().subVectors(b, a);
            var w0 = new T.Vector3().subVectors(ro, a), A = rd.dot(rd), B = rd.dot(sd), Cq = sd.dot(sd), D = rd.dot(w0), E = sd.dot(w0);
            var den = A * Cq - B * B, sc = den > 1e-9 ? (B * E - Cq * D) / den : 0, tc = den > 1e-9 ? (A * E - B * D) / den : 0;
            tc = Math.max(0, Math.min(1, tc)); if (sc < 0) sc = 0;
            p1.copy(ro).addScaledVector(rd, sc); p2.copy(a).addScaledVector(sd, tc);
            var dist = p1.distanceTo(p2), tol = 0.25 + t.s * 0.06;
            if (dist < tol && dist < bd) { bd = dist; best = { kind: KINDS[k], i: i, d: sc }; }
          }
        }
        return best;
      }
      canvas.addEventListener('pointermove', function (ev) {
        if (!_drag) return;
        ev.preventDefault(); ev.stopPropagation();
        var c = cam(); if (!c) return;
        toNdc(ev); ray.setFromCamera(ndc, c);
        if (!ray.ray.intersectPlane(ground, hit)) return;
        var L = listOf(_drag.kind), t = L[_drag.i], f = opts.footprint(), O = opts.origin();
        var nx = hit.x - _drag.dx, nz = hit.z - _drag.dz;
        /* never onto the building: the footprint and 300 mm round it */
        var kw = f.w / 2 + 0.3, kl = f.l / 2 + 0.3, lx = nx - O.x, lz = nz - O.z;
        if (Math.abs(lx) < kw && Math.abs(lz) < kl) {
          if (kw - Math.abs(lx) < kl - Math.abs(lz)) lx = (lx < 0 ? -1 : 1) * kw; else lz = (lz < 0 ? -1 : 1) * kl;
        }
        t.x = Math.round((O.x + lx) * 100) / 100; t.z = Math.round((O.z + lz) * 100) / 100;
        refresh();
      }, true);
      canvas.addEventListener('pointerdown', function (ev) {
        if (!_grp || (opts.view && opts.view() === '2d')) return;
        var h = treeAt(ev); if (!h) return;
        ev.preventDefault(); ev.stopPropagation();
        if (ev.stopImmediatePropagation) ev.stopImmediatePropagation();
        try { canvas.setPointerCapture(ev.pointerId); } catch (e) {}
        var ctl = opts.controls ? opts.controls() : null; if (ctl) ctl.enabled = false;
        var c = cam(); toNdc(ev); ray.setFromCamera(ndc, c);
        var t = listOf(h.kind)[h.i], dx = 0, dz = 0;
        if (ray.ray.intersectPlane(ground, hit)) { dx = hit.x - t.x; dz = hit.z - t.z; }
        _drag = { kind: h.kind, i: h.i, id: ev.pointerId, dx: dx, dz: dz };
        select(h.kind, h.i);
      }, true);
      function end(ev) {
        if (!_drag || (ev && ev.pointerId !== _drag.id)) return;
        var ctl = opts.controls ? opts.controls() : null; if (ctl) ctl.enabled = true;
        try { if (ev) canvas.releasePointerCapture(ev.pointerId); } catch (e) {}
        var t = listOf(_drag.kind)[_drag.i];
        log('tree ' + _drag.kind + '/' + _drag.i + ' moved to ' + t.x + ',' + t.z);
        _drag = null;
        release();
        try { opts.save(); } catch (e2) {}
      }
      canvas.addEventListener('pointerup', end, true);
      canvas.addEventListener('pointercancel', end, true);
    }
    return { KINDS: KINDS, kinds: TREE_KINDS, build: build, bindDrag: bindDrag, refresh: refresh, toggle: toggle, isOn: isOn, count: count };
  }

  /* A tap selects an opening; orbit, pinch and cancelled gestures never do.
     Keep orbit controls in charge until release, including on touch screens. */
  function bindOpeningTap(canvas, opts) {
    if (!document.getElementById('cf-opening-edit-css')) {
      var style = document.createElement('style');
      style.id = 'cf-opening-edit-css';
      style.textContent = '#cf-root:has(#cf-op-done) #cf-cmdbar{display:none!important;}';
      document.head.appendChild(style);
    }
    var pending = null, pointers = {};
    canvas.addEventListener('pointerdown', function (e) {
      pointers[e.pointerId] = true;
      if (Object.keys(pointers).length !== 1 || e.isPrimary === false ||
          e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) { pending = null; return; }
      if (!opts.enabled()) return;
      var rec = opts.pick(e);
      pending = rec == null ? null : { id: e.pointerId, x: e.clientX, y: e.clientY, rec: rec };
    }, true);
    document.addEventListener('pointermove', function (e) {
      if (pending && e.pointerId === pending.id &&
          Math.hypot(e.clientX - pending.x, e.clientY - pending.y) > DRAG_SLOP) pending = null;
    }, true);
    document.addEventListener('pointerup', function (e) {
      var tap = pending;
      delete pointers[e.pointerId];
      if (!tap || tap.id !== e.pointerId) return;
      pending = null;
      if (e.target !== canvas || !opts.enabled() ||
          Math.hypot(e.clientX - tap.x, e.clientY - tap.y) > DRAG_SLOP || opts.pick(e) !== tap.rec) return;
      /* Let the renderer finish releasing its pointer before changing steps. */
      global.setTimeout(function () { if (opts.enabled()) opts.open(tap.rec); }, 0);
    }, true);
    document.addEventListener('pointercancel', function (e) { delete pointers[e.pointerId]; pending = null; }, true);
    canvas.addEventListener('lostpointercapture', function () { pending = null; });
    canvas.addEventListener('wheel', function () { pending = null; }, { passive: true });
    global.addEventListener('blur', function () { pending = null; pointers = {}; });
  }

  /* Invisible grab volumes are intentional targets, hidden scene layers are
     not. Stop at opaque geometry instead of selecting through the building. */
  function openingHit(hits, resolve) {
    for (var i = 0; i < (hits || []).length; i++) {
      var hit = hits[i], ob = hit.object, node = ob, hidden = false, helper = false;
      while (node) {
        if (!node.visible) hidden = true;
        if (node.userData && node.userData.arSkip) helper = true;
        node = node.parent;
      }
      if (hidden) continue;
      var rec = resolve(ob);
      if (rec != null) return { record: rec, hit: hit };
      if (helper || !ob.isMesh) continue;
      var mat = Array.isArray(ob.material) ? ob.material[hit.face ? hit.face.materialIndex : 0] : ob.material;
      if (!mat || mat.visible === false || (mat.transparent && mat.opacity < 0.5)) continue;
      return null;
    }
    return null;
  }

    var _CMD_SVG = {
      up: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15V4"/><path d="M7.5 8.5L12 4l4.5 4.5"/><path d="M4 14v4.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V14"/></svg>',
      mic: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="3" width="6" height="11" rx="3"/><path d="M5.5 11a6.5 6.5 0 0 0 13 0"/><path d="M12 17.5V21"/></svg>',
      send: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M21 3L10 14"/><path d="M21 3l-6.5 18-4.5-7-7-4.5z"/></svg>',
      edit: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M4 20h4.5L19.5 9a2.1 2.1 0 0 0-3-3L5.5 17v3z"/><path d="M14.5 7.5l2.9 2.9"/></svg>',
      /* reset: an arrow going back round */
      reset: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M4 12a8 8 0 1 0 2.4-5.7"/><path d="M4 4.5v4.3h4.3"/></svg>',
      /* the idea's cross (core-v551) */
      x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M6 6l12 12"/><path d="M18 6L6 18"/></svg>',
      /* the classic loader: twelve rounded spokes, darkest first */
      spin: (function () {
        var g = '', i;
        for (i = 0; i < 12; i++) g += '<rect x="11" y="1.5" width="2.2" height="6.2" rx="1.1" fill="#707070" opacity="' + (Math.round((1 - i * 0.075) * 100) / 100) + '" transform="rotate(' + (-i * 30) + ' 12 12)"/>';
        return '<svg class="cf-spin" viewBox="0 0 24 24">' + g + '</svg>';
      })(),
      /* a plan: the outline, a wall with a door gap, and the door's swing */
      plan: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3.5" y="3.5" width="17" height="17" rx="1.2"/><path d="M12 3.5v6.5"/><path d="M12 15v5.5"/><path d="M12 10h3.5"/><path d="M12 15a5 5 0 0 1 5-5"/><path d="M3.5 12.5H8"/></svg>'
    };
    var _PLIB_SVG = {
      cam: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M4 8h3l1.6-2.4h6.8L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z"/><circle cx="12" cy="13" r="3.6"/></svg>',
      web: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="8.5"/><path d="M3.5 12h17"/><path d="M12 3.5c2.6 2.6 3.6 5.4 3.6 8.5s-1 5.9-3.6 8.5c-2.6-2.6-3.6-5.4-3.6-8.5s1-5.9 3.6-8.5z"/></svg>',
      bed: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 18V7"/><path d="M3 13h18v5"/><path d="M21 13v-1.5A3.5 3.5 0 0 0 17.5 8H11v5"/><circle cx="7" cy="10.4" r="1.7"/></svg>',
      /* core-v572: the bathrooms beside the bedrooms, and the similar plans' fold */
      bath: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12h18v2a5 5 0 0 1-5 5H8a5 5 0 0 1-5-5v-2z"/><path d="M6 12V6a2 2 0 0 1 3.6-1.2"/><path d="M7 19l-1 2M17 19l1 2"/></svg>',
      down: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>',
      up: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M6 15l6-6 6 6"/></svg>',
      x: '<svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M13 1L1 13M13 13L1 1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
      /* core-v595: the mirror (the glyph Marek sent) and the plan taken into the project */
      mir: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2.5v19" stroke-dasharray="2 2"/><path d="M10 5.5L3.5 19H10z"/><path d="M14 5.5L20.5 19H14z" fill="currentColor" fill-opacity="0.35"/></svg>',
      take: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4v11"/><path d="M7 10.5l5 5 5-5"/><path d="M5 19.5h14"/></svg>'
    };
    function planBarCss(foot) {
      if ($('cf-cmdbar-css')) return;
      var st = global.document.createElement('style');
      st.id = 'cf-cmdbar-css';
      var M = "'Montserrat',sans-serif";
      st.textContent =
        /* EIGHTY PER CENT (core-v552, Marek: cely banner tak na 80 % velikosti,
           je zbytecne veliky): every button, gap, pad and the field */
        '#cf-cmdbar{position:absolute;left:50%;bottom:' + (foot == null ? 25 : foot) + 'px;transform:translateX(-50%);z-index:12;display:flex;align-items:center;gap:10px;' +
        'padding:8px 11px;border-radius:999px;background:#ffffff;box-shadow:0 6px 24px rgba(0,0,0,0.14),0 0 0 1px rgba(0,0,0,0.05);' +
        'max-width:calc(100% - 24px);box-sizing:border-box;font-family:' + M + ';transition:opacity 140ms ease-in-out,visibility 140ms,margin-left 200ms;}' +
        'body.cfg-ui-desk-open #cf-cmdbar,body.cfg-ui-desk-open #cf-cmd-bub{margin-left:-16.5vw;}' +
        'body.cfg-ui-desk-open #cf-cmdbar{max-width:calc(67vw - 24px);}' +
        '#cf-cmdbar.cf-cmd-off{opacity:0;visibility:hidden;pointer-events:none;}' +
        '#cf-cmdbar .cf-cmd-b{flex:0 0 auto;width:36px;height:36px;border-radius:50%;border:0;padding:0;margin:0;display:inline-flex;align-items:center;' +
        'justify-content:center;color:#ffffff;cursor:pointer;box-shadow:0 4px 14px rgba(0,0,0,0.16);-webkit-tap-highlight-color:transparent;touch-action:manipulation;}' +
        '#cf-cmdbar .cf-cmd-b svg{width:18px;height:18px;display:block;}' +
        '#cf-cmdbar .cf-cmd-up,#cf-cmdbar .cf-cmd-plan{background:#D2A878;}' +
        /* the edit mode (core-v550): the field in sand. THE SAME ICONS IN
           BOTH MODES (core-v557, Marek: tlacitko restore misto upload je
           blbost; pokazde kdyz rozbalim ikonu floorplan, mam koukat na tu
           stejnou sadu ikon): upload, voice, the field, send -- a plan there
           changes only what the field says, the designer's suggestion */
        '#cf-cmdbar.cf-cmd-change .cf-cmd-in{background:#F4EADB;}' +
        /* folded (core-v543): the plan icon, the divider and the pencil */
        '#cf-cmdbar .cf-cmd-plan{display:none;}' +
        '#cf-cmdbar.cf-cmd-mini .cf-cmd-plan{display:inline-flex;}' +
        /* FOLDED, THE BAR IS DARK (ui-v214, Marek: spodni banner se dvema
           ikonami cerne pozadi, rozbaleny muze zustat -- v kontrastu s plovoucim
           menu); opened with the field it stays white */
        '#cf-cmdbar.cf-cmd-mini{background:#141516;box-shadow:0 8px 28px rgba(0,0,0,0.30);}' +
        '#cf-cmdbar.cf-cmd-mini .cf-cmd-div{background:rgba(255,255,255,0.30);}' +
        '#cf-cmdbar.cf-cmd-mini .cf-cmd-up,#cf-cmdbar.cf-cmd-mini .cf-cmd-mic,#cf-cmdbar.cf-cmd-mini .cf-cmd-fld,#cf-cmdbar.cf-cmd-mini .cf-cmd-send{display:none !important;}' +
        '#cf-cmdbar .cf-cmd-mic{background:#2C2D2E;}' +
        '#cf-cmdbar .cf-cmd-mic.cf-on{background:#D8232A;animation:cf-cmd-pulse 1.2s ease-out infinite;}' +
        '#cf-cmdbar .cf-cmd-send{background:#463529;}' +
        '#cf-cmdbar .cf-cmd-send[disabled]{opacity:0.35;cursor:default;box-shadow:none;}' +
        '#cf-cmdbar .cf-cmd-edit{background:radial-gradient(circle at 50% 32%,#e8434e 0%,#D8232A 62%,#c01c26 100%);}' +
        '#cf-cmdbar .cf-cmd-edit.cf-on{box-shadow:0 0 0 3px rgba(216,35,42,0.28),0 4px 14px rgba(0,0,0,0.16);}' +
        /* the field and its cross (core-v552): the cross clears what is in
           it, or puts the designer's suggestion away */
        '#cf-cmdbar .cf-cmd-fld{flex:1 1 auto;position:relative;display:flex;align-items:center;min-width:88px;width:224px;transition:width 160ms ease-out;}' +
        '#cf-cmdbar .cf-cmd-fld.cf-hinted{width:320px;}' +
        '#cf-cmdbar .cf-cmd-in{flex:1 1 auto;width:100%;min-width:0;height:36px;border:0;border-radius:999px;background:#F5F5F5;padding:0 13px;margin:0;' +
        'font:500 13px/1 ' + M + ';color:#1A1C1D;outline:none;box-sizing:border-box;-webkit-appearance:none;appearance:none;text-overflow:ellipsis;}' +
        '#cf-cmdbar .cf-cmd-fld.cf-x .cf-cmd-in{padding-right:34px;}' +
        /* THE SUGGESTION IN THE FIELD reads as text, a shade lighter */
        '#cf-cmdbar .cf-cmd-in.cf-hint::placeholder{color:#7A5A36;opacity:1;}' +
        '#cf-cmdbar .cf-cmd-clr{position:absolute;right:6px;top:50%;transform:translateY(-50%);width:24px;height:24px;border-radius:50%;border:0;padding:0;margin:0;' +
        'display:none;align-items:center;justify-content:center;background:rgba(70,53,41,0.12);color:#463529;cursor:pointer;-webkit-tap-highlight-color:transparent;}' +
        '#cf-cmdbar .cf-cmd-clr svg{width:12px;height:12px;display:block;}' +
        '#cf-cmdbar .cf-cmd-fld.cf-x .cf-cmd-clr{display:inline-flex;}' +
        '#cf-cmdbar .cf-cmd-div{flex:0 0 auto;width:2px;height:27px;border-radius:1px;background:#A3A7AD;}' +
        '@keyframes cf-cmd-pulse{0%{box-shadow:0 0 0 0 rgba(216,35,42,0.45);}100%{box-shadow:0 0 0 12px rgba(216,35,42,0);}}' +
        '#cf-cmd-bub{position:absolute;left:50%;transform:translateX(-50%);z-index:13;display:none;align-items:center;justify-content:center;gap:10px;flex-wrap:wrap;' +
        'max-width:min(92%,560px);box-sizing:border-box;padding:9px 13px;border-radius:14px;background:#0A0A0A;color:#ffffff;font:600 12px/1.3 ' + M + ';' +
        'letter-spacing:0.4px;text-transform:uppercase;text-align:center;box-shadow:0 6px 20px rgba(0,0,0,0.28);}' +
        '#cf-cmd-bub.cf-on{display:flex;}' +
        '#cf-cmd-bub.cf-tail::after{content:"";position:absolute;left:50%;bottom:-6px;width:12px;height:12px;background:#0A0A0A;transform:translateX(-50%) rotate(45deg);border-radius:2px;}' +
        '#cf-cmd-bub button{font:700 11px/1 ' + M + ';letter-spacing:0.06em;text-transform:uppercase;padding:8px 12px;border-radius:999px;border:1px solid #ffffff;' +
        'background:transparent;color:#ffffff;cursor:pointer;}' +
        '#cf-cmd-bub button.cf-pri{background:#D8232A;border-color:#D8232A;}' +
        /* THE QUESTION (core-v549): a sentence, not a label -- as written */
        '#cf-cmd-bub.cf-q{flex-direction:column;align-items:stretch;text-align:left;text-transform:none;letter-spacing:0;font:600 14px/1.35 ' + M + ';padding:12px 14px;}' +
        '#cf-cmd-bub .cf-ask{display:flex;align-items:center;gap:8px;}' +
        '#cf-cmd-bub .cf-ask-in{flex:1 1 auto;min-width:140px;height:38px;border:0;border-radius:999px;background:#ffffff;color:#1A1C1D;padding:0 14px;margin:0;font:500 14px/1 ' + M + ';outline:none;-webkit-appearance:none;appearance:none;text-transform:none;}' +
        '#cf-cmd-bub button.cf-ask-go{flex:0 0 auto;width:38px;height:38px;padding:0;border:0;border-radius:50%;background:#463529;display:inline-flex;align-items:center;justify-content:center;}' +
        '#cf-cmd-bub button.cf-ask-go svg{width:18px;height:18px;display:block;}' +
        '#cf-cmd-bub button.cf-ask-no{flex:0 0 auto;}' +
        /* THE IDEA OVER THE PLAN ICON (core-v552, Marek: bublina stejna jako
           jinde, v plovoucim baneru u agenta AI -- cerny background; jen na
           zacatku na 2 vteriny, jinak zbytecne koliduje s obrazem): the AI
           bubble's black, its type and its tail, fading in and out */
        '#cf-cmd-idea{position:absolute;z-index:13;display:block;max-width:min(300px,calc(100% - 24px));box-sizing:border-box;margin:0;' +
        'padding:9px 13px;border:0;border-radius:14px;background:#0A0A0A;color:#ffffff;font:600 12px/1.3 ' + M + ';letter-spacing:0.4px;text-transform:uppercase;' +
        'text-align:left;box-shadow:0 6px 20px rgba(0,0,0,0.28);cursor:pointer;-webkit-tap-highlight-color:transparent;' +
        'opacity:0;visibility:hidden;pointer-events:none;transform:translateY(6px);transition:opacity 260ms ease-out,transform 260ms ease-out,visibility 260ms;}' +
        '#cf-cmd-idea.cf-on{opacity:1;visibility:visible;pointer-events:auto;transform:translateY(0);}' +
        '#cf-cmd-idea::after{content:"";position:absolute;left:var(--cf-tail,34px);bottom:-6px;width:12px;height:12px;margin-left:-6px;background:#0A0A0A;' +
        'transform:rotate(45deg);border-radius:2px;}' +
        /* ONE LINE (core-v553, Marek: maximalne jeden radek se tremi teckami
           na konci, cely text az pri otevreni banneru) */
        '#cf-cmd-idea .cf-idea-t{display:block;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}' +
        '@media (min-width:641px){#cf-cmd-idea{font-size:13px;padding:10px 15px;max-width:360px;}}' +
        '@media (max-width:419px){#cf-cmd-idea{font-size:11px;letter-spacing:0.3px;padding:8px 12px;max-width:240px;}}' +
        /* THE RIGHTS NOTICE (core-v559): red words in the foot under the bar,
           no box; a white halo keeps them readable over the plan and the map */
        '#cf-cmd-note{position:absolute;left:12px;right:12px;bottom:4px;z-index:12;margin:0;padding:0;text-align:center;white-space:nowrap;overflow:hidden;' +
        'font:600 11px/1.2 ' + M + ';letter-spacing:0.2px;color:#D8232A;pointer-events:none;' +
        'text-shadow:0 0 3px #ffffff,0 0 2px #ffffff,0 0 1px #ffffff;opacity:0;visibility:hidden;transition:opacity 600ms ease-out,visibility 600ms;}' +
        '#cf-cmd-note.cf-on{opacity:1;visibility:visible;}' +
        '#cf-cmd-note.cf-aside{opacity:0;visibility:hidden;}' +
        '#cf-cmd-note.cf-gone{display:none;}' +
        '#cf-cmd-note span{display:inline-block;}' +
        /* THE PICTURE'S SLIDER UNDER THE BAR (core-v562): where the notice
           stands, no words, the bar's sand */
        '#cf-cmd-pic{position:absolute;left:50%;bottom:3px;transform:translateX(-50%);z-index:12;width:min(240px,56%);height:18px;margin:0;padding:0;' +
        'display:none;-webkit-appearance:none;appearance:none;outline:none;border:0;cursor:pointer;touch-action:none;-webkit-tap-highlight-color:transparent;' +
        /* THINNER, AND LIGHT WHERE IT IS NOT FILLED (core-v563, Marek: udelej
           linii slideru tensi o neco, a nevyplnenou cast svetlejsi -- je moc
           tmava). The browser's own track was a thick dark bar; the line is
           drawn here, 3 px, the bar's sand up to the value and a light grey
           after it, the knob the LEVA slider's */
        'background-color:transparent;background-repeat:no-repeat;background-position:center;background-size:100% 3px;' +
        'background-image:linear-gradient(to right,#D2A878 0%,#D2A878 10%,rgba(0,0,0,0.13) 10%,rgba(0,0,0,0.13) 100%);}' +
        '#cf-cmd-pic::-webkit-slider-runnable-track{background:transparent;border:0;height:18px;}' +
        '#cf-cmd-pic::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;box-sizing:border-box;width:16px;height:16px;margin-top:1px;border-radius:50%;background:#D2A878;' +
        'border:2px solid #ffffff;box-shadow:0 1px 3px rgba(0,0,0,0.25);cursor:grab;}' +
        '#cf-cmd-pic::-moz-range-track{background:transparent;border:0;height:3px;}' +
        '#cf-cmd-pic::-moz-range-thumb{box-sizing:border-box;width:16px;height:16px;border-radius:50%;background:#D2A878;border:2px solid #ffffff;box-shadow:0 1px 3px rgba(0,0,0,0.25);cursor:grab;}' +
        '#cf-cmd-pic.cf-on{display:block;}' +
        '@media (min-width:641px){#cf-cmd-pic{width:min(280px,46%);}}' +
        /* THE SPINNER IN THE PLAN ICON'S PLACE (core-v547) */
        '#cf-cmdbar .cf-cmd-plan.cf-load{background:#ffffff;box-shadow:none;cursor:default;}' +
        '#cf-cmdbar .cf-cmd-plan.cf-load svg{width:24px;height:24px;}' +
        '#cf-cmdbar .cf-spin{animation:cf-spin 0.9s steps(12) infinite;transform-origin:50% 50%;}' +
        '@keyframes cf-spin{to{transform:rotate(360deg);}}' +
        '@media (min-width:641px){#cf-cmdbar .cf-cmd-plan.cf-load svg{width:27px;height:27px;}}' +
        '@media (min-width:641px){#cf-cmdbar{gap:12px;padding:10px 13px;}#cf-cmdbar .cf-cmd-b{width:42px;height:42px;}' +
        '#cf-cmdbar .cf-cmd-b svg{width:20px;height:20px;}#cf-cmdbar .cf-cmd-in{height:42px;font-size:14px;}#cf-cmdbar .cf-cmd-div{height:30px;}#cf-cmd-bub{font-size:13px;padding:10px 15px;}' +
        '#cf-cmdbar .cf-cmd-fld{width:260px;}#cf-cmdbar .cf-cmd-fld.cf-hinted{width:400px;}#cf-cmdbar .cf-cmd-clr{width:28px;height:28px;right:7px;}#cf-cmdbar .cf-cmd-fld.cf-x .cf-cmd-in{padding-right:40px;}}' +
        '@media (max-width:420px){#cf-cmdbar{gap:6px;padding:6px 8px;}#cf-cmdbar .cf-cmd-b{width:32px;height:32px;}' +
        '#cf-cmdbar .cf-cmd-b svg{width:16px;height:16px;}#cf-cmdbar .cf-cmd-in{height:32px;padding:0 10px;}#cf-cmdbar .cf-cmd-fld{min-width:72px;}#cf-cmdbar .cf-cmd-div{height:22px;}' +
        '#cf-cmdbar .cf-cmd-clr{width:22px;height:22px;right:5px;}#cf-cmdbar .cf-cmd-fld.cf-x .cf-cmd-in{padding-right:30px;}}' +
        /* the pencil now lives in the bar; a slider over the canvas foot steps
           up past the bar only while the bar is showing (core-v542: with the
           drawer out the bar is gone and the slider keeps its own place) */
        '.cf-cmdbar-on .cfg-ui-wiz-pencil{display:none !important;}' +
        '.cf-cmdbar-vis .cfg-ui-slider-row{margin-bottom:14px;}';
      global.document.head.appendChild(st);
    }
    function planLibraryCss() {
      if ($('cf-plib-css')) return;
      var st = global.document.createElement('style');
      st.id = 'cf-plib-css';
      var M = "'Montserrat',sans-serif";
      st.textContent =
        /* WIDER (core-v566, Marek: muzes ho rozsirit -- na mobilu 95 % sirky,
           na desktopu taky muze mit vic mista; pro uzivatele je to pri
           otevreni hlavni prostor pro hledani a cteni): in from the right on
           every screen, 95 % of a phone's width, on a wider screen 58 % of
           it between 420 and 880 px; the plans in as many columns of 150 px
           as fit */
        '#cf-plib{position:fixed;top:0;right:0;bottom:0;width:95vw;background:#ffffff;z-index:61;overflow-y:auto;-webkit-overflow-scrolling:touch;' +
        'box-sizing:border-box;transform:translate3d(100%,0,0);transition:transform .24s ease;box-shadow:-8px 0 28px rgba(0,0,0,0.14);font-family:' + M + ';color:#2C2D2E;}' +
        '#cf-plib.cf-open{transform:translate3d(0,0,0);}' +
        /* core-v569 (Marek: drawer nakonec zuzit o 20 %): 46 % of a wide
           screen, 336 to 704 px; a phone keeps its 95 % */
        '@media (min-width:600px){#cf-plib{width:clamp(336px,46vw,704px);}}' +
        '#cf-plib.cf-bottom{top:auto;left:0;right:0;width:100%;max-height:50vh;border-radius:16px 16px 0 0;box-shadow:0 -8px 28px rgba(0,0,0,0.18);transform:translate3d(0,100%,0);}' +
        '#cf-plib.cf-bottom.cf-open{transform:translate3d(0,0,0);}' +
        /* THE HEADER AS THE AI AGENT'S (core-v569, Marek: floorplan library
           vetsi font, normalni text, ne upper case; camera a upload jen kulate
           ikony na urovni nazvu, doprava; divider pod headerem; ne dark mode) */
        '#cf-plib .cf-plib-hd{position:sticky;top:0;z-index:1;display:flex;align-items:center;gap:10px;padding:16px 14px 14px 20px;background:#ffffff;border-bottom:1px solid #E6E7E8;}' +
        '#cf-plib .cf-plib-ti{flex:1 1 auto;min-width:0;font:700 19px/1.2 ' + M + ';letter-spacing:0;color:#1A1C1D;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}' +
        '#cf-plib .cf-plib-ics{flex:0 0 auto;display:flex;align-items:center;gap:10px;}' +
        '#cf-plib .cf-plib-ic{width:44px;height:44px;border:0;padding:0;margin:0;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;color:#ffffff;' +
        'cursor:pointer;box-shadow:0 4px 14px rgba(0,0,0,0.14);-webkit-tap-highlight-color:transparent;}' +
        '#cf-plib .cf-plib-ic svg{width:20px;height:20px;display:block;}' +
        '#cf-plib .cf-plib-ic.cf-cam{background:#1A1C1D;}' +
        '#cf-plib .cf-plib-ic.cf-up{background:#D2A878;}' +
        '#cf-plib .cf-plib-ic:active{transform:scale(0.94);}' +
        '#cf-plib .cf-plib-cl{flex:0 0 auto;width:36px;height:36px;border:0;padding:0;margin:0;border-radius:50%;background:transparent;color:#2C2D2E;display:inline-flex;' +
        'align-items:center;justify-content:center;cursor:pointer;-webkit-tap-highlight-color:transparent;}' +
        '#cf-plib .cf-plib-bd{padding:4px 20px 20px;}' +
        '#cf-plib .cf-plib-acts{display:flex;gap:8px;}' +
        '#cf-plib .cf-plib-act{flex:1 1 0;min-width:0;height:40px;border:0;border-radius:999px;background:#D2A878;color:#ffffff;display:inline-flex;align-items:center;justify-content:center;' +
        'gap:6px;padding:0 10px;font:700 11px/1 ' + M + ';letter-spacing:0.06em;text-transform:uppercase;cursor:pointer;box-shadow:0 4px 14px rgba(0,0,0,0.12);' +
        '-webkit-tap-highlight-color:transparent;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}' +
        '#cf-plib .cf-plib-act.cf-dark{background:#2C2D2E;}' +
        '#cf-plib .cf-plib-act svg{flex:0 0 auto;width:16px;height:16px;display:block;}' +
        '#cf-plib .cf-plib-act span{overflow:hidden;text-overflow:ellipsis;}' +
        '#cf-plib .cf-plib-flt{display:flex;flex-wrap:wrap;align-items:center;gap:6px;margin:16px 0 12px;}' +
        /* core-v572: the bathrooms' row right under the bedrooms' */
        '#cf-plib .cf-plib-flt.cf-baths{margin-top:-4px;}' +
        /* core-v573: one grid for both rows, the same number under the same number */
        '#cf-plib .cf-plib-flt.cf-grid{display:grid;align-items:center;justify-items:center;column-gap:6px;row-gap:8px;overflow-x:auto;}' +
        '#cf-plib .cf-plib-flt.cf-grid .cf-plib-fl{justify-self:start;}' +
        '#cf-plib .cf-plib-flt.cf-grid .cf-plib-chip{min-width:40px;}' +
        '#cf-plib .cf-plib-flt:empty{display:none;}' +
        '#cf-plib .cf-plib-sim{display:inline-flex;align-items:center;gap:4px;height:26px;padding:0 10px;margin-top:1px;border-radius:999px;border:1px solid #D2A878;background:#ffffff;' +
        'color:#463529;font:700 11px/1 ' + M + ';letter-spacing:0;cursor:pointer;-webkit-tap-highlight-color:transparent;}' +
        '#cf-plib .cf-plib-sim svg{width:12px;height:12px;display:block;}' +
        '#cf-plib .cf-plib-sim.cf-on{background:#D2A878;color:#ffffff;}' +
        '#cf-plib .cf-plib-t.cf-mem{border-style:dashed;border-color:#D2A878;background:#FBF8F3;}' +
        /* core-v569 (Marek: mivame vetsinu drawers s normalnim textem, ne
           upper case): the filter's labels and chips in normal case */
        '#cf-plib .cf-plib-fl{font:600 13px/1 ' + M + ';letter-spacing:0;color:#6B7280;margin-right:4px;}' +
        '#cf-plib .cf-plib-chip{height:32px;min-width:32px;padding:0 13px;border-radius:999px;border:1px solid #DADCE0;background:#ffffff;color:#2C2D2E;' +
        'font:600 12.5px/1 ' + M + ';letter-spacing:0;cursor:pointer;-webkit-tap-highlight-color:transparent;}' +
        '#cf-plib .cf-plib-chip.cf-on{background:#1A1C1D;border-color:#1A1C1D;color:#ffffff;}' +
        /* core-v569 (Marek: pudorysy zvetsit, nahledy jsou moc male): a tile
           is 190 px and up -- two across on a tablet (about 230 to 290 px
           each, half as big again as the four of before), one on a phone */
        '#cf-plib .cf-plib-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:12px;}' +
        /* core-v569 (Marek: note udelej cernou barvou, mirne vetsi font) */
        '#cf-plib .cf-plib-note{margin:14px 2px 0;font:500 12.5px/1.5 ' + M + ';letter-spacing:0;color:#1A1C1D;}' +
        '#cf-plib .cf-plib-note a{color:#1A1C1D;font-weight:700;text-decoration:underline;white-space:nowrap;}' +
        '#cf-plib .cf-plib-msg.cf-err{color:#1A1C1D;cursor:pointer;text-decoration:underline;}' +
        '#cf-plib .cf-plib-ar{margin:16px 0 0;}' +
        '#cf-plib .cf-plib-rng{position:relative;height:46px;margin:8px 4px 0;}' +
        /* core-v569 (Marek: posuvnik nema stejny vzhled jako jine nase -- ty
           maji zlaty puntik s bilym okrajem): the picture slider's look, a 3 px
           line, sand to the value and light grey beyond, 16 px gold knobs */
        '#cf-plib .cf-plib-tr,#cf-plib .cf-plib-fill{position:absolute;top:9.5px;height:3px;border-radius:2px;}' +
        '#cf-plib .cf-plib-tr{left:0;right:0;background:rgba(0,0,0,0.13);}' +
        '#cf-plib .cf-plib-fill{background:#D2A878;}' +
        '#cf-plib .cf-plib-rng input[type=range]{position:absolute;left:0;top:0;width:100%;height:22px;margin:0;padding:0;background:transparent;pointer-events:none;' +
        '-webkit-appearance:none;appearance:none;outline:none;}' +
        '#cf-plib .cf-plib-rng input[type=range]::-webkit-slider-runnable-track{height:22px;background:transparent;border:0;}' +
        '#cf-plib .cf-plib-rng input[type=range]::-moz-range-track{height:22px;background:transparent;border:0;}' +
        '#cf-plib .cf-plib-rng input[type=range]::-webkit-slider-thumb{pointer-events:auto;-webkit-appearance:none;appearance:none;width:16px;height:16px;margin-top:3px;border-radius:50%;' +
        'background:#D2A878;border:2px solid #ffffff;box-shadow:0 1px 3px rgba(0,0,0,0.25);cursor:grab;box-sizing:border-box;}' +
        '#cf-plib .cf-plib-rng input[type=range]::-moz-range-thumb{pointer-events:auto;width:16px;height:16px;border-radius:50%;background:#D2A878;border:2px solid #ffffff;' +
        'box-shadow:0 1px 3px rgba(0,0,0,0.25);cursor:grab;box-sizing:border-box;}' +
        '#cf-plib .cf-plib-rv{position:absolute;top:25px;transform:translateX(-50%);font:700 11px/1 ' + M + ';color:#1A1C1D;white-space:nowrap;}' +
        '#cf-plib .cf-plib-t{position:relative;display:flex;flex-direction:column;align-items:center;gap:6px;padding:8px 8px 7px;border:1px solid #DADCE0;border-radius:12px;' +
        'background:#ffffff;cursor:pointer;-webkit-tap-highlight-color:transparent;}' +
        '#cf-plib .cf-plib-t:active{border-color:#D2A878;}' +
        '#cf-plib .cf-plib-svg{display:block;width:100%;height:auto;aspect-ratio:1/1;}' +
        '#cf-plib .cf-plib-area{align-self:center;text-align:center;font:800 13px/1.1 ' + M + ';color:#1A1C1D;letter-spacing:0.02em;}' +
        '#cf-plib .cf-plib-cap{display:flex;align-items:center;gap:4px;font:600 11px/1.2 ' + M + ';color:#2C2D2E;white-space:nowrap;}' +
        '#cf-plib .cf-plib-cap svg{width:14px;height:14px;display:block;}' +
        /* core-v595 (Marek: mirror ikonu na stred kazdeho floorplan v horni
           polovine; klikem se v nahledu ozrcadli, dalsim klikem na floorplan se
           implementuje; ikona download na stred floorplan): the picture in a
           box of its own, the mirror button in the middle of its upper half,
           the download badge in its middle once the picture is turned over
           (with a mouse, on hover too); the numbers in the rooms stay readable */
        '#cf-plib .cf-plib-pv{position:relative;display:block;width:100%;}' +
        '#cf-plib .cf-plib-pv .cf-plib-svg{transition:transform 0.25s ease;}' +
        '#cf-plib .cf-plib-t.cf-mir .cf-plib-svg{transform:scaleX(-1);}' +
        '#cf-plib .cf-plib-t.cf-mir .cf-plib-svg text{transform-box:fill-box;transform-origin:center;transform:scaleX(-1);}' +
        '#cf-plib .cf-plib-mi{position:absolute;top:25%;left:50%;transform:translate(-50%,-50%);width:34px;height:34px;padding:0;margin:0;border-radius:50%;' +
        'border:1px solid #DADCE0;background:#ffffff;color:#2C2D2E;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;' +
        'box-shadow:0 1px 4px rgba(0,0,0,0.12);-webkit-tap-highlight-color:transparent;}' +
        '#cf-plib .cf-plib-mi svg{width:20px;height:20px;display:block;}' +
        /* core-v600 (Marek: download videt i na planech bez zrcadleni, v
           zakladni pozici ve stejne barve jako zakladni mirror; zrcadleny
           mirror i download inverzni black and white, bez zlate): the badge
           always shows -- white as the mirror button while the plan is as
           saved, both black with a white glyph once it is turned over */
        '#cf-plib .cf-plib-t.cf-mir .cf-plib-mi,#cf-plib .cf-plib-t.cf-mir .cf-plib-dl{background:#1A1C1D;border-color:#1A1C1D;color:#ffffff;}' +
        '#cf-plib .cf-plib-mi:active{transform:translate(-50%,-50%) scale(0.92);}' +
        '#cf-plib .cf-plib-dl{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:44px;height:44px;border-radius:50%;box-sizing:border-box;' +
        'border:1px solid #DADCE0;background:#ffffff;color:#2C2D2E;' +
        'display:inline-flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.18);pointer-events:auto;cursor:pointer;padding:0;transition:background 0.2s ease,color 0.2s ease;}' +
        '#cf-plib .cf-plib-dl svg{width:22px;height:22px;display:block;}' +
        '#cf-plib .cf-plib-x{position:absolute;top:4px;right:4px;width:24px;height:24px;border:0;padding:0;border-radius:50%;background:rgba(70,53,41,0.12);color:#463529;' +
        'display:inline-flex;align-items:center;justify-content:center;cursor:pointer;-webkit-tap-highlight-color:transparent;}' +
        '#cf-plib .cf-plib-x svg{width:10px;height:10px;}' +
        '#cf-plib .cf-plib-t.cf-del{opacity:0.75;}' +
        '#cf-plib .cf-plib-t.cf-del .cf-plib-x{background:#D8232A;color:#ffffff;}' +
        '#cf-plib .cf-plib-msg{padding:18px 4px;text-align:center;font:500 12px/1.45 ' + M + ';color:#9CA3AF;}' +
        '#cf-plib .cf-plib-msg svg{width:26px;height:26px;display:inline-block;}';
      global.document.head.appendChild(st);
    }

  function planBar(t) {
    var _t = t, _cmdBtn = function (cls, svg, label) {
      var b = document.createElement('button');
      b.type = 'button'; b.className = 'cf-cmd-b ' + cls;
      b.innerHTML = svg; b.setAttribute('aria-label', label);
      return b;
    };
    planBarCss();
      var bar = global.document.createElement('div');
      bar.id = 'cf-cmdbar';
      /* two fingers on the bar are not a pinch of the plan (core-v540) */
      bar.setAttribute('data-cf-nopinch', '1');
      var pl = _cmdBtn('cf-cmd-plan', _CMD_SVG.plan, _t('view_plan', 'Floor plan'));
      var up = _cmdBtn('cf-cmd-up', _CMD_SVG.up, _t('floorplan_upload', 'Upload a floor plan'));
      var mic = _cmdBtn('cf-cmd-mic', _CMD_SVG.mic, 'Voice');
      var fld = global.document.createElement('span');
      fld.className = 'cf-cmd-fld';
      var inp = global.document.createElement('input');
      inp.type = 'text';
      inp.className = 'cf-cmd-in';
      inp.setAttribute('enterkeyhint', 'send');
      inp.placeholder = _t('cmd_placeholder', 'Describe your floor plan...');
      var send = _cmdBtn('cf-cmd-send', _CMD_SVG.send, 'Send');
      send.disabled = true;
      var dv = global.document.createElement('span');
      dv.className = 'cf-cmd-div';
      var edit = _cmdBtn('cf-cmd-edit', _CMD_SVG.edit, _t('edit', 'Edit'));
      bar.appendChild(pl);
      bar.appendChild(up);
      /* the cross at the end of the field (core-v552) */
      var clr = global.document.createElement('button');
      clr.type = 'button'; clr.className = 'cf-cmd-clr'; clr.innerHTML = _CMD_SVG.x;
      clr.setAttribute('aria-label', _t('delete', 'Delete'));
      fld.appendChild(inp); fld.appendChild(clr);
      bar.appendChild(mic); bar.appendChild(fld); bar.appendChild(send); bar.appendChild(dv); bar.appendChild(edit);

    inp.setAttribute('aria-label', inp.placeholder);
    return { el: bar, plan: pl, upload: up, mic: mic, field: fld, input: inp,
             send: send, divider: dv, edit: edit, clear: clr };
  }
  function planLibraryShell(opts) {
    var _t = opts.t;
    planLibraryCss();
      var d = global.document, el = d.createElement('div');
      el.id = 'cf-plib';
      el.setAttribute('data-cf-nopinch', '1');
      el.setAttribute('role', 'dialog');
      var hd = d.createElement('div'); hd.className = 'cf-plib-hd';
      var ti = d.createElement('span'); ti.className = 'cf-plib-ti'; ti.textContent = _t('floorplan_drawer_title', 'Floorplan library');
      var cl = d.createElement('button'); cl.type = 'button'; cl.className = 'cf-plib-cl'; cl.innerHTML = _PLIB_SVG.x; cl.setAttribute('aria-label', _t('close', 'Close'));
      cl.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); opts.close(); });
      /* the camera black, the upload gold, round, beside the cross (core-v569) */
      var ics = d.createElement('div'); ics.className = 'cf-plib-ics';
      function act(svg, label, cls, fn) {
        var b = d.createElement('button');
        b.type = 'button'; b.className = 'cf-plib-ic ' + cls;
        b.innerHTML = svg;
        b.setAttribute('aria-label', label);
        b.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); fn(); });
        ics.appendChild(b);
      }
      /* core-v726: a product with its own designs and no upload shows neither */
      if (!opts.noUpload) {
        act(_PLIB_SVG.cam, _t('floorplan_camera', 'Camera'), 'cf-cam', function () { opts.camera(); });
        act(_CMD_SVG.up, _t('underlay_upload', 'Upload'), 'cf-up', function () { opts.upload(); });
      }
      hd.appendChild(ti); hd.appendChild(ics); hd.appendChild(cl);
      var bd = d.createElement('div'); bd.className = 'cf-plib-bd';
      /* NO SEARCH ON THE WEB (core-v566, Marek: na jednu stranu chceme, aby
         k nahranemu pudorysu meli prava, na druhou stranu tim navadime k
         hledani; nechat camera a upload jak je) -- and the rights under the
         two buttons, with the terms, instead of the red line on the canvas
         after a picture is chosen (Marek: note z canvas pro nahravani zmizi) */
      var note = d.createElement('div'); note.className = 'cf-plib-note';
      note.appendChild(d.createTextNode(_t('floorplan_rights', 'Upload only floor plans you own or have the right to use.') + ' '));
      /* THE TERMS IN THE PAGE'S LANGUAGE (core-v568, Marek: odkaz je
         /pages/terms-and-conditions plus lokalizacni zkratky). The store's own
         path for this language -- /cs/, /de/, or / for English -- as the pool's
         link takes it (core-v289); no setting in the admin to keep */
      var tRoot = '/';
      try { tRoot = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/'; } catch (eTR) { tRoot = '/'; }
      if (tRoot.charAt(tRoot.length - 1) !== '/') tRoot += '/';
      var tl = d.createElement('a'); tl.href = tRoot + 'pages/terms-and-conditions'; tl.target = '_blank'; tl.rel = 'noopener';
      tl.textContent = _t('terms_link', 'Terms & Conditions');
      note.appendChild(tl);

    if (!opts.noUpload) bd.appendChild(note);
    el.appendChild(hd); el.appendChild(bd);
    ti.id = 'cf-plib-title'; el.setAttribute('aria-labelledby', ti.id);
    return { el: el, body: bd, close: cl };
  }

  /* Shared library cards and filters: house line and native container models. */
  function planLibraryView(opts) {
    var _plib = opts.state, STATE = opts.units, _t = opts.t, fmtArea = opts.fmtArea;
    var _log = opts.log || function () {}, _plibPick = opts.pick, _PLIB_SVG = global.ConfiguratorUI.planLibraryIcons;
    function _plibBathKey(it) { var v = Number(it && it.baths) || 0; return v >= 1 ? Math.floor(v) : 0; }
    function _plibSetOn(S) { for (var k in S) if (S && S.hasOwnProperty(k) && S[k]) return true; return false; }
    /* a number no plan has any more goes off */
    function _plibPrune() {
      var a = _plib.selBd || {}, b = _plib.selBa || {}, k;
      for (k in a) if (a.hasOwnProperty(k) && !(_plib.counts || {})[k]) delete a[k];
      for (k in b) if (b.hasOwnProperty(k) && !(_plib.bcounts || {})[k]) delete b[k];
      _plib.selBd = a; _plib.selBa = b;
    }
    function _plibCount() {
      var C = {}, B = {}, i;
      for (i = 0; i < (_plib.items || []).length; i++) {
        if (_plib.items[i].cap) continue;   /* core-v726: a product's design has no bedrooms */
        var b = _plib.items[i].beds | 0; C[b] = (C[b] || 0) + 1;
        var k = _plibBathKey(_plib.items[i]); if (k > 0) B[k] = (B[k] || 0) + 1;
      }
      _plib.counts = C; _plib.bcounts = B;
    }
    /* A PLAN'S INNER FLOOR, as its tile writes it (core-v564): the sum of its
       rooms, each as this page writes it, where its picture has them */
    function _plibFloor(it) {
      var fl = Number(it.floor) || Number(it.thumb && it.thumb.a) || 0, TT = it.thumb || {}, ri;
      if (TT.v >= 2 && TT.r && TT.r.length) {
        var sm = 0;
        for (ri = 0; ri < TT.r.length; ri++) { var ar9 = Number(TT.r[ri][1]) || 0; sm += (STATE.isMetric !== false) ? Number(ar9.toFixed(1)) : Math.round(ar9 * 10.7639); }
        if (sm > 0) fl = (STATE.isMetric !== false) ? sm : sm / 10.7639;
      }
      return fl;
    }
    /* the same in the units the page counts in: m2 to a tenth, whole sqft */
    function _plibDv(m2) { return (STATE.isMetric !== false) ? Math.round(m2 * 10) / 10 : Math.round(m2 * 10.7639); }
    function _plibDvTxt(v) { return (STATE.isMetric !== false) ? (v.toFixed(1) + ' m2') : (Math.round(v) + ' sqft'); }
    /* THE INNER FLOOR AREA FILTER (core-v566, Marek: nad bedrooms dat jeste
       jeden filtr -- inner floor area -- se dvema body jako posuvniky, pod
       body presny numericky udaj, cele stopy nebo m2 s jednim desetinnym
       mistem; rozpeti je dle ulozenych pudorysu). From the smallest plan in
       the list to the largest, in the page's units; none with fewer than
       two plans of different sizes. The chosen span holds while the drawer
       is reopened, as long as the list still reaches it. */
    function _plibAreaRange() {
      var vs = [], i;
      for (i = 0; i < (_plib.items || []).length; i++) { var v = _plibDv(_plibFloor(_plib.items[i])); if (v > 0) vs.push(v); }
      if (vs.length < 2) return null;
      var metric = STATE.isMetric !== false, step = metric ? 0.1 : 1;
      var lo = Math.min.apply(null, vs), hi = Math.max.apply(null, vs);
      if (hi - lo < step - 1e-9) return null;
      lo = metric ? Math.floor(lo * 10) / 10 : Math.floor(lo); hi = metric ? Math.ceil(hi * 10) / 10 : Math.ceil(hi);
      var unit = metric ? 'm' : 'i';
      if (_plib.aunit !== unit || _plib.alo == null || _plib.ahi == null || _plib.alo < lo - 1e-9 || _plib.ahi > hi + 1e-9 || _plib.alo > _plib.ahi) {
        _plib.alo = lo; _plib.ahi = hi; _plib.aunit = unit;
      }
      return { lo: lo, hi: hi, step: step };
    }
    function _plibAreaBuild() {
      var d = global.document, af = _plib.af;
      if (!af) return null;
      af.innerHTML = ''; _plib.apaint = null;
      var FA = (_plib.items && _plib.items.length) ? _plibAreaRange() : null;
      _plib.fa = FA;
      if (!FA) { af.style.display = 'none'; return null; }
      af.style.display = '';
      var lb = d.createElement('span'); lb.className = 'cf-plib-fl'; lb.textContent = _t('inner_floor_area', 'Inner Floor Area');
      var rg = d.createElement('div'); rg.className = 'cf-plib-rng';
      var tr = d.createElement('div'); tr.className = 'cf-plib-tr';
      var fi = d.createElement('div'); fi.className = 'cf-plib-fill';
      rg.appendChild(tr); rg.appendChild(fi);
      var I = [d.createElement('input'), d.createElement('input')], V = [d.createElement('span'), d.createElement('span')], q;
      for (q = 0; q < 2; q++) {
        I[q].type = 'range'; I[q].min = String(FA.lo); I[q].max = String(FA.hi); I[q].step = String(FA.step);
        I[q].setAttribute('aria-label', _t('inner_floor_area', 'Inner Floor Area'));
        rg.appendChild(I[q]);
        V[q].className = 'cf-plib-rv';
      }
      I[0].value = String(_plib.alo); I[1].value = String(_plib.ahi);
      rg.appendChild(V[0]); rg.appendChild(V[1]);
      /* the thumb's middle runs from 11 px in at one end to 11 px in at the other */
      /* the knob's middle runs from 8 px in at one end to 8 px in at the other (16 px knobs, core-v569) */
      function pos(v) { var p = (v - FA.lo) / Math.max(1e-9, FA.hi - FA.lo); return 'calc(' + (p * 100).toFixed(3) + '% + ' + (8 - p * 16).toFixed(2) + 'px)'; }
      function paint() {
        var a = +I[0].value, b = +I[1].value, span = Math.max(1e-9, FA.hi - FA.lo);
        fi.style.left = pos(a); fi.style.width = 'calc(' + pos(b) + ' - ' + pos(a) + ')';
        V[0].textContent = _plibDvTxt(a); V[1].textContent = _plibDvTxt(b);
        /* each number under its thumb, inside the drawer and never over the
           other: in the middle under its thumb where there is room, pushed
           apart and in from the ends where there is not */
        var W = rg.clientWidth, wa = V[0].offsetWidth, wb = V[1].offsetWidth;
        if (W > 0 && wa > 0 && wb > 0) {
          var G = 8, E = -4, xa = 8 + (a - FA.lo) / span * (W - 16) - wa / 2, xb = 8 + (b - FA.lo) / span * (W - 16) - wb / 2;
          xa = Math.max(E, Math.min(xa, W - E - wa)); xb = Math.max(E, Math.min(xb, W - E - wb));
          var ov = xa + wa + G - xb;
          if (ov > 0) {
            xa -= ov / 2; xb += ov / 2;
            if (xa < E) { xb += E - xa; xa = E; }
            if (xb + wb > W - E) { xa -= xb + wb - (W - E); xb = W - E - wb; }
          }
          V[0].style.transform = 'none'; V[1].style.transform = 'none';
          V[0].style.left = xa.toFixed(1) + 'px'; V[1].style.left = xb.toFixed(1) + 'px';
        } else { V[0].style.left = pos(a); V[1].style.left = pos(b); }
        /* where the two thumbs meet, the one that can still move away takes
           the touch: the lower one in the upper half, the upper in the lower */
        I[0].style.zIndex = ((a - FA.lo) / span > 0.5) ? '3' : '2'; I[1].style.zIndex = '2';
      }
      _plib.apaint = paint;
      function on(e) {
        var a = +I[0].value, b = +I[1].value;
        if (a > b) { if (e && e.target === I[0]) { a = b; I[0].value = String(a); } else { b = a; I[1].value = String(b); } }
        _plib.alo = a; _plib.ahi = b;
        paint();
        _plibGrid();
      }
      function said() { _log('cmdbar   library: inner floor area ' + _plibDvTxt(_plib.alo) + ' to ' + _plibDvTxt(_plib.ahi) + ' -- ' + (_plib.shown || 0) + ' plan(s)'); }
      for (q = 0; q < 2; q++) { I[q].addEventListener('input', on); I[q].addEventListener('change', said); }
      af.appendChild(lb); af.appendChild(rg);
      paint();
      return FA;
    }
    /* the plans that pass the filters */
    /* PLANS VERY LIKE EACH OTHER UNDER ONE TILE (core-v572, worker w2.09,
       Marek: schovat jen ty, ktere jsou hodne podobne). The worker says which
       plans are very like each other (g, the group's leader) and their order
       in the group (gr). Of those that pass the filters the first in that
       order shows, with +N for the rest; a tap on it lays the rest out after
       it, dashed, and a second tap folds them again. Nothing is taken out. */
    function _plibGrid() {
      var grid = _plib.grid, msg = _plib.msg, i;
      if (!grid || !_plib.items) return;
      grid.innerHTML = ''; msg.innerHTML = '';
      var shown = 0, admin = false, FA = _plib.fa, vis = [], best = {}, cnt = {}, hid = 0;
      _plib.gopen = _plib.gopen || {};
      try { var LP = global.LEVA_PRODUCT || {}; admin = (LP.isAdmin === true || LP.isAdmin === 'true'); } catch (eA) { admin = false; }
      for (i = 0; i < _plib.items.length; i++) {
        var it = _plib.items[i];
        if (_plibSetOn(_plib.selBd) && !_plib.selBd[it.beds | 0]) continue;
        if (_plibSetOn(_plib.selBa) && !_plib.selBa[_plibBathKey(it)]) continue;
        if (FA) { var dv = _plibDv(_plibFloor(it)); if (dv > 0 && (dv < _plib.alo - 1e-6 || dv > _plib.ahi + 1e-6)) continue; }
        if ((_plib.selRooms || []).some(function(key) { return !(it.thumb && it.thumb.r || []).some(function(r) { return r[2] === key; }); })) continue;
        vis.push(it);
        if (it.g) { cnt[it.g] = (cnt[it.g] || 0) + 1; if (!best[it.g] || (it.gr | 0) < (best[it.g].gr | 0)) best[it.g] = it; }
      }
      for (i = 0; i < vis.length; i++) {
        var v = vis[i], gk = v.g, more = gk ? (cnt[gk] - 1) : 0;
        if (gk && best[gk] !== v) continue;
        grid.appendChild(_plibTile(v, admin, more > 0 ? { n: more, open: !!_plib.gopen[gk], g: gk } : null));
        shown++;
        if (more > 0) {
          if (!_plib.gopen[gk]) { hid += more; continue; }
          var mem = vis.filter(function (q) { return q.g === gk && q !== v; }).sort(function (a, b) { return (a.gr | 0) - (b.gr | 0); });
          for (var m = 0; m < mem.length; m++) { grid.appendChild(_plibTile(mem[m], admin, { member: true })); shown++; }
        }
      }
      _plib.shown = shown; _plib.hid = hid;
      if (!shown) {
        msg.textContent = (_plib.items.length && (_plibSetOn(_plib.selBd) || _plibSetOn(_plib.selBa) || (_plib.selRooms || []).length || FA)) ? _t('floorplan_no_results', 'No floorplans match your filters.')
                                                                            : _t('floorplan_library_empty', 'No floor plans here yet. Upload one or describe it in words.');
      }
    }
    function _plibRender() {
      var d = global.document, flt = _plib.flt, grid = _plib.grid, msg = _plib.msg, i;
      if (!flt) return;
      flt.innerHTML = ''; grid.innerHTML = ''; msg.innerHTML = '';
      if (_plib.flt2) _plib.flt2.innerHTML = '';
      if (_plib.af) { _plib.af.innerHTML = ''; _plib.af.style.display = 'none'; }
      if (_plib.items == null) { msg.innerHTML = _CMD_SVG.spin; return; }
      _plibAreaBuild();
      /* THE CHIPS, ON AND OFF (core-v573, Marek: zrusit Any -- kdyz nebude
         zaskrtle cislo, bude to znamenat any; cisla on / off; nepouzivat plus,
         vzdy jen cela cisla; sloupce s cisly zarovnat -- jednicky pod sebou,
         dvojky atd.). One grid for both rows: the labels in the first column
         and every number in a column of its own, the same number under the
         same number. A tap turns a number on or off, several may be on, none
         on is every plan. The bathrooms are whole ones. */
      _plibPrune();
      var bdK = Object.keys(_plib.counts || {}).map(Number).sort(function (a, b) { return a - b; });
      var baK = Object.keys(_plib.bcounts || {}).map(Number).sort(function (a, b) { return a - b; });
      flt.classList.remove('cf-grid'); flt.style.gridTemplateColumns = '';
      if (bdK.length || baK.length) {
        var allK = bdK.concat(baK), lo = Math.min.apply(null, allK), hi = Math.max.apply(null, allK), rowN = 0;
        flt.classList.add('cf-grid');
        flt.style.gridTemplateColumns = 'max-content repeat(' + (hi - lo + 1) + ', max-content)';
        var row = function (label, keys, S, what) {
          rowN++;
          var lb = d.createElement('span'); lb.className = 'cf-plib-fl'; lb.textContent = label;
          lb.style.gridRow = String(rowN); lb.style.gridColumn = '1';
          flt.appendChild(lb);
          keys.forEach(function (v) {
            var c = d.createElement('button');
            c.type = 'button'; c.className = 'cf-plib-chip' + (S[v] ? ' cf-on' : '');
            c.textContent = (what === 'bd' && v === 0) ? _t('floorplan_studio', 'Studio') : String(v);
            c.style.gridRow = String(rowN); c.style.gridColumn = String(v - lo + 2);
            c.setAttribute('aria-pressed', S[v] ? 'true' : 'false');
            c.addEventListener('click', function (e) {
              e.preventDefault(); e.stopPropagation();
              if (S[v]) delete S[v]; else S[v] = 1;
              _plibRender();
              _log('cmdbar   library: ' + (what === 'bd' ? 'bedrooms ' : 'bathrooms ') + ((what === 'bd' && v === 0) ? 'studio' : v) + (S[v] ? ' on' : ' off') + ' -- on now: ' +
                   (Object.keys(S).join(', ') || 'none, so every plan') + ' -- ' + (_plib.shown || 0) + ' tile(s), ' + (_plib.hid || 0) + ' similar folded (core-v573)');
            });
            flt.appendChild(c);
          });
        };
        if (bdK.length) row(_t('floorplan_filter_bedrooms', 'Bedrooms'), bdK, _plib.selBd, 'bd');
        if (baK.length) row(_t('floorplan_filter_bathrooms', 'Bathrooms'), baK, _plib.selBa, 'ba');
      }
      if (_plib.flt2) (opts.roomFilters || []).forEach(function(f) {
        var selected = _plib.selRooms || (_plib.selRooms = []), on = selected.indexOf(f.key) >= 0;
        var chip = d.createElement('button'); chip.type = 'button';
        chip.className = 'cf-plib-chip' + (on ? ' cf-on' : '');
        chip.textContent = _t(f.key, f.label); chip.setAttribute('data-room-filter', f.key);
        chip.setAttribute('aria-pressed', on ? 'true' : 'false');
        chip.addEventListener('click', function(e) { e.preventDefault(); e.stopPropagation();
          if (on) selected.splice(selected.indexOf(f.key), 1); else selected.push(f.key); _plibRender(); });
        _plib.flt2.appendChild(chip);
      });
      _plibGrid();
    }
    /* one plan, drawn from its outline and its walls, with its bedrooms and its size */
    /* ONE PLAN, DRAWN AS OUR PLAN IS (core-v561, Marek: pudorysy vizualizovat
       v draweru stejne jako je mame v pudorysu -- barevne rozliseni mistnosti,
       okna a dvere, mohou byt vymery mistnosti, nad obrazkem celkova podlahova
       plocha, v jednotkach stranky). The worker (w1.99) hands over the page's
       own picture of each plan as it was built (floorplan v137): the rooms in
       the plan's colours, the grey shell, the dark partitions and the dashed
       room lines, every door with its leaf and its arc, the windows and the
       outer doors in the shell, the terrace. Over it the inner floor area, in
       the units of this page; in the rooms their areas, where they fit. A
       plan saved before v137 has only its outline and walls, drawn as before. */
    var _PLIB_COL = { wall: '#8A8F94', part: '#374151', line: '#1f2937', terr: '#C6CBB9' };
    function _plibSvg(it, pxW) {
      var T = it.thumb || {}, o = T.o || [], i, k, bw = 0, bh = 0, pts = [];
      for (i = 0; i + 1 < o.length; i += 2) { bw = Math.max(bw, o[i]); bh = Math.max(bh, o[i + 1]); pts.push(o[i] + ',' + o[i + 1]); }
      if (!(bw > 0)) bw = it.w || 1;
      if (!(bh > 0)) bh = it.d || 1;
      var ex0 = 0, ey0 = 0, ex1 = bw, ey1 = bh;
      if (T.t) { ex0 = Math.min(ex0, T.t[0]); ey0 = Math.min(ey0, T.t[1]); ex1 = Math.max(ex1, T.t[2]); ey1 = Math.max(ey1, T.t[3]); }
      /* core-v567: a door that opens out of the building reaches out of the
         outline by its leaf, and the picture takes it in */
      for (i = 0; i < (T.e || []).length; i++) {
        var eo = T.e[i];
        if (!eo || (eo[6] | 0) !== 1) continue;
        var nOx = -eo[4], nOy = eo[3], dI = ((bw / 2 - eo[0]) * nOx + (bh / 2 - eo[1]) * nOy >= 0) ? 1 : -1;
        var Lo = (((eo[5] | 0) === 1) ? eo[2] / 2 : eo[2]) + 0.05, hxO = eo[3] * eo[2] / 2, hyO = eo[4] * eo[2] / 2, sO;
        for (sO = -1; sO <= 1; sO += 2) {
          var qxO = eo[0] + sO * hxO - dI * nOx * Lo, qyO = eo[1] + sO * hyO - dI * nOy * Lo;
          ex0 = Math.min(ex0, qxO); ey0 = Math.min(ey0, qyO); ex1 = Math.max(ex1, qxO); ey1 = Math.max(ey1, qyO);
        }
      }
      var pad = Math.max(ex1 - ex0, ey1 - ey0) * 0.04, vw = ex1 - ex0 + 2 * pad, vh = ey1 - ey0 + 2 * pad;
      var s = '<svg class="cf-plib-svg" viewBox="' + _plibN(ex0 - pad) + ' ' + _plibN(ey0 - pad) + ' ' + _plibN(vw) + ' ' + _plibN(vh) + '" preserveAspectRatio="xMidYMid meet">';
      /* a plan saved before the picture: the outline and the walls only */
      if (!T.v) {
        s += '<polygon points="' + pts.join(' ') + '" fill="#F5F5F5" stroke="#1A1C1D" stroke-width="2.2" vector-effect="non-scaling-stroke"/>';
        for (i = 0; i < (T.p || []).length; i++) { var q0 = T.p[i]; if (q0 && q0.length >= 4) s += '<line x1="' + q0[0] + '" y1="' + q0[1] + '" x2="' + q0[2] + '" y2="' + q0[3] + '" stroke="#2C2D2E" stroke-width="1.2" vector-effect="non-scaling-stroke"/>'; }
        return s + '</svg>';
      }
      var wt = T.wt || 0.16, pt = T.pt || 0.1, ppm = pxW / Math.max(vw, vh * 1.0);
      /* the terrace under everything */
      if (T.t) s += '<rect x="' + T.t[0] + '" y="' + T.t[1] + '" width="' + _plibN(T.t[2] - T.t[0]) + '" height="' + _plibN(T.t[3] - T.t[1]) + '" fill="' + _PLIB_COL.terr + '" fill-opacity="0.5" stroke="' + _PLIB_COL.part + '" stroke-width="' + _plibN(0.02) + '" stroke-dasharray="' + _plibN(0.15) + ' ' + _plibN(0.1) + '"/>';
      /* the rooms in their colours, a closet in its grey */
      s += '<polygon points="' + pts.join(' ') + '" fill="#ffffff"/>';
      for (i = 0; i < (T.r || []).length; i++) {
        var R = T.r[i], cl = (R[2] === 'leva.closet'), c = R[3] || [];
        for (k = 0; k + 3 < c.length; k += 4) s += '<rect x="' + c[k] + '" y="' + c[k + 1] + '" width="' + _plibN(c[k + 2] - c[k]) + '" height="' + _plibN(c[k + 3] - c[k + 1]) + '" fill="' + R[0] + '" fill-opacity="' + (cl ? 1 : 0.4) + '"/>';
      }
      /* the shell: a band the wall's thickness inside the outline */
      if (T.b) {
        for (i = 0; i < T.b.length; i++) { var box = T.b[i]; s += '<rect x="' + _plibN(box[0] + wt / 2) + '" y="' + _plibN(box[1] + wt / 2) + '" width="' + _plibN(box[2] - box[0] - wt) + '" height="' + _plibN(box[3] - box[1] - wt) + '" fill="none" stroke="' + _PLIB_COL.wall + '" stroke-width="' + wt + '"/>'; }
      } else if (o.length === 8) s += '<rect x="' + _plibN(wt / 2) + '" y="' + _plibN(wt / 2) + '" width="' + _plibN(bw - wt) + '" height="' + _plibN(bh - wt) + '" fill="none" stroke="' + _PLIB_COL.wall + '" stroke-width="' + wt + '"/>';
      else s += '<polygon points="' + pts.join(' ') + '" fill="none" stroke="' + _PLIB_COL.wall + '" stroke-width="' + wt + '"/>';
      /* Native modular plans can open a joint across two separate shells. */
      for (i = 0; i < (T.j || []).length; i++) {
        var j = T.j[i]; s += '<line x1="' + j[0] + '" y1="' + j[1] + '" x2="' + j[2] + '" y2="' + j[3] + '" stroke="#ffffff" stroke-width="' + _plibN(wt * 2 + 0.04) + '"/>';
      }
      /* the partitions, and the room lines dashed */
      for (i = 0; i < (T.p || []).length; i++) {
        var p = T.p[i];
        if (p[4]) s += '<line x1="' + p[0] + '" y1="' + p[1] + '" x2="' + p[2] + '" y2="' + p[3] + '" stroke="' + _PLIB_COL.part + '" stroke-width="' + _plibN(0.025) + '" stroke-dasharray="' + _plibN(0.16) + ' ' + _plibN(0.12) + '"/>';
        else s += '<line x1="' + p[0] + '" y1="' + p[1] + '" x2="' + p[2] + '" y2="' + p[3] + '" stroke="' + _PLIB_COL.part + '" stroke-width="' + pt + '" stroke-linecap="square"/>';
      }
      var LW = _plibN(Math.max(0.018, 1 / ppm));
      /* a door: the gap white across its wall, then its leaves. c the middle,
         u the way along the wall, n the side the leaves open to */
      function door(cx, cy, w, ux, uy, nx, ny, sw, kd, thick) {
        var hx = ux * w / 2, hy = uy * w / 2, tx = -uy * (thick / 2 + 0.01), ty = ux * (thick / 2 + 0.01);
        var g = '<polygon points="' + _plibN(cx - hx - tx) + ',' + _plibN(cy - hy - ty) + ' ' + _plibN(cx + hx - tx) + ',' + _plibN(cy + hy - ty) + ' ' +
                _plibN(cx + hx + tx) + ',' + _plibN(cy + hy + ty) + ' ' + _plibN(cx - hx + tx) + ',' + _plibN(cy - hy + ty) + '" fill="#ffffff"/>';
        function leaf(ax, ay, bx, by, L) {
          /* hinge a, the far jamb b: the leaf square to the wall, the arc back to b */
          var tipx = ax + nx * L, tipy = ay + ny * L, cr = (tipx - ax) * (by - ay) - (tipy - ay) * (bx - ax);
          return '<line x1="' + _plibN(ax) + '" y1="' + _plibN(ay) + '" x2="' + _plibN(tipx) + '" y2="' + _plibN(tipy) + '" stroke="' + _PLIB_COL.line + '" stroke-width="' + LW + '"/>' +
                 '<path d="M' + _plibN(tipx) + ' ' + _plibN(tipy) + ' A' + _plibN(L) + ' ' + _plibN(L) + ' 0 0 ' + (cr > 0 ? 1 : 0) + ' ' + _plibN(bx) + ' ' + _plibN(by) + '" fill="none" stroke="' + _PLIB_COL.line + '" stroke-width="' + LW + '"/>';
        }
        var sgn = (sw & 1) ? 1 : -1, ax = cx + sgn * hx, ay = cy + sgn * hy, bx = cx - sgn * hx, by = cy - sgn * hy;
        if (kd === 0) g += leaf(ax, ay, bx, by, w);
        else if (kd === 1) { g += leaf(cx + hx, cy + hy, cx, cy, w / 2) + leaf(cx - hx, cy - hy, cx, cy, w / 2); }
        else if (kd === 2) {
          var q4x = nx * w / 4, q4y = ny * w / 4;
          g += '<polyline points="' + _plibN(cx - hx) + ',' + _plibN(cy - hy) + ' ' + _plibN(cx - hx / 2 + q4x) + ',' + _plibN(cy - hy / 2 + q4y) + ' ' + _plibN(cx) + ',' + _plibN(cy) + ' ' +
               _plibN(cx + hx / 2 + q4x) + ',' + _plibN(cy + hy / 2 + q4y) + ' ' + _plibN(cx + hx) + ',' + _plibN(cy + hy) + '" fill="none" stroke="' + _PLIB_COL.line + '" stroke-width="' + LW + '"/>';
        } else if (kd === 3 || kd === 4) {
          var off = thick / 4;
          g += '<line x1="' + _plibN(cx - hx - tx / 2) + '" y1="' + _plibN(cy - hy - ty / 2) + '" x2="' + _plibN(cx + (kd === 3 ? hx * 0.1 : hx) - tx / 2) + '" y2="' + _plibN(cy + (kd === 3 ? hy * 0.1 : hy) - ty / 2) + '" stroke="' + _PLIB_COL.line + '" stroke-width="' + LW + '"/>';
          if (kd === 3) g += '<line x1="' + _plibN(cx - hx * 0.1 + tx / 2) + '" y1="' + _plibN(cy - hy * 0.1 + ty / 2) + '" x2="' + _plibN(cx + hx + tx / 2) + '" y2="' + _plibN(cy + hy + ty / 2) + '" stroke="' + _PLIB_COL.line + '" stroke-width="' + LW + '"/>';
          void off;
        }
        return g;
      }
      for (i = 0; i < (T.d || []).length; i++) {
        var d = T.d[i], v = d[3] === 1, s2 = d[4] | 0;
        /* the asset's sw: bit 0 the hinge at the high end; bit 1 opens to +z on
           an 'h' wall, to -x on a 'v' one */
        if (v) s += door(d[0], d[1], d[2], 0, 1, (s2 & 2) ? -1 : 1, 0, s2, d[5] | 0, pt);
        else s += door(d[0], d[1], d[2], 1, 0, 0, (s2 & 2) ? 1 : -1, s2, d[5] | 0, pt);
      }
      for (i = 0; i < (T.g || []).length; i++) {
        var gd = T.g[i], gs = gd[5] | 0, gn = (gs & 2) ? 1 : -1;
        s += door(gd[0], gd[1], gd[2], gd[3], gd[4], -gd[4] * gn, gd[3] * gn, gs, gd[6] | 0, pt);
      }
      /* windows and outer doors sit in the shell band: the inward side found
         by asking which side of the edge the middle of the house is */
      var mx = bw / 2, my = bh / 2;
      function inward(x, y, ex, ey) { var nx = -ey, ny = ex; return ((mx - x) * nx + (my - y) * ny >= 0) ? [nx, ny] : [-nx, -ny]; }
      for (i = 0; i < (T.w || []).length; i++) {
        var wq = T.w[i], nI = wq.length >= 7 ? [wq[5], wq[6]] : inward(wq[0], wq[1], wq[3], wq[4]), hw = wq[2] / 2, ox = wq[0], oy = wq[1];
        var cA = [ox - wq[3] * hw, oy - wq[4] * hw], cB = [ox + wq[3] * hw, oy + wq[4] * hw];
        s += '<polygon points="' + _plibN(cA[0]) + ',' + _plibN(cA[1]) + ' ' + _plibN(cB[0]) + ',' + _plibN(cB[1]) + ' ' + _plibN(cB[0] + nI[0] * wt) + ',' + _plibN(cB[1] + nI[1] * wt) + ' ' +
             _plibN(cA[0] + nI[0] * wt) + ',' + _plibN(cA[1] + nI[1] * wt) + '" fill="#ffffff" stroke="' + _PLIB_COL.line + '" stroke-width="' + LW + '"/>';
        s += '<line x1="' + _plibN(cA[0] + nI[0] * wt / 2) + '" y1="' + _plibN(cA[1] + nI[1] * wt / 2) + '" x2="' + _plibN(cB[0] + nI[0] * wt / 2) + '" y2="' + _plibN(cB[1] + nI[1] * wt / 2) + '" stroke="' + _PLIB_COL.line + '" stroke-width="' + LW + '"/>';
      }
      for (i = 0; i < (T.e || []).length; i++) {
        /* core-v567: the seventh number 1 is a door that opens out of the
           building (a picture of floorplan v149 or later) -- its leaves outside */
        var eq = T.e[i], nE = eq.length >= 9 ? [eq[7], eq[8]] : inward(eq[0], eq[1], eq[3], eq[4]), sE = ((eq[6] | 0) === 1) ? -1 : 1;
        s += door(eq[0] + nE[0] * wt / 2, eq[1] + nE[1] * wt / 2, eq[2], eq[3], eq[4], sE * nE[0], sE * nE[1], 0, eq[5] | 0, wt);
      }
      /* the areas, where a number fits: in the biggest cell of each room */
      var fs = 9 / ppm;
      if (pxW >= 140) {
        for (i = 0; i < (T.r || []).length; i++) {
          var R2 = T.r[i], c2 = R2[3] || [], bi = -1, ba = 0;
          /* core-v564: a closet by its area too, where it fits (a picture of v2) */
          if ((R2[2] === 'leva.closet' && !(T.v >= 2)) || !(R2[1] > 0)) continue;
          for (k = 0; k + 3 < c2.length; k += 4) { var aa = (c2[k + 2] - c2[k]) * (c2[k + 3] - c2[k + 1]); if (aa > ba) { ba = aa; bi = k; } }
          if (bi < 0) continue;
          var txt = _plibArea(R2[1], false), cw = c2[bi + 2] - c2[bi], ch = c2[bi + 3] - c2[bi + 1];
          if (cw < txt.length * fs * 0.62 + 0.1 || ch < fs * 1.6) continue;
          s += '<text x="' + _plibN(c2[bi] + cw / 2) + '" y="' + _plibN(c2[bi + 1] + ch / 2 + fs * 0.35) + '" font-size="' + _plibN(fs) + '" font-family="Montserrat,sans-serif" font-weight="700" fill="#1A1C1D" text-anchor="middle">' + txt + '</text>';
        }
      }
      return s + '</svg>';
    }
    function _plibN(v) { return String(Math.round(v * 1000) / 1000); }
    /* an area in this page's units, as the plan writes it (fmtArea); in a
       room the number alone */
    function _plibArea(m2, unit) {
      if (unit) return fmtArea(m2);
      return (STATE.isMetric !== false) ? (Math.round(m2 * 10) / 10).toFixed(1) : String(Math.round(m2 * 10.7639));
    }
    function _plibTile(it, admin, grp) {
      var d = global.document, t = d.createElement('div');
      t.className = 'cf-plib-t';
      t.setAttribute('role', 'button'); t.tabIndex = 0;
      if (it.name) t.setAttribute('aria-label', it.name);
      t.setAttribute('data-plib', String(it.id));   /* core-v675: the tour finds a plan by it */
      var px = 150;
      try { var gw = _plib.grid && _plib.grid.clientWidth; if (gw > 0) { var nc = Math.max(1, Math.floor((gw + 12) / 202)); px = Math.max(120, (gw - 12 * (nc - 1)) / nc - 18); } } catch (eW) {}
      var metric = STATE.isMetric !== false, a = Math.min(it.w || 0, it.d || 0), b = Math.max(it.w || 0, it.d || 0);
      var size = metric ? (a.toFixed(1) + ' x ' + b.toFixed(1) + ' m') : (Math.round(a / 0.3048) + '\' x ' + Math.round(b / 0.3048) + '\'');
      /* THE ROOMS ADD UP TO IT (core-v564, Marek: celkovou vymeru dej na
         stred nad floorplan; vymera nad floorplanem musi sedet za vsechny
         mistnosti) -- core-v566: the one helper the area filter counts with */
      var beds = it.beds | 0, fl = _plibFloor(it), baths = Number(it.baths) || 0;
      var top = '<span class="cf-plib-area">' + (fl > 0 ? _plibArea(fl, true) : '&nbsp;') + '</span>';
      /* core-v572: the bathrooms after the bedrooms, as the US reads a plan (2 bd, 1.5 ba) */
      var cap = '<span class="cf-plib-cap">' + (beds ? (_PLIB_SVG.bed + '<b>' + beds + '</b>') : '<b class="cf-plib-st"></b>') +
                (baths > 0 ? ('<span>&middot;</span>' + _PLIB_SVG.bath + '<b>' + (Math.round(baths * 10) / 10) + '</b>') : '') + '<span>&middot;&nbsp;' + size + '</span></span>';
      /* core-v726: a product's design says what it is instead of its bedrooms */
      if (it.cap) cap = '<span class="cf-plib-cap"><b class="cf-plib-nm"></b><span>&middot;&nbsp;' + size + '</span></span>';
      t.innerHTML = top + '<span class="cf-plib-pv">' + (opts.svg ? opts.svg(it, px) : _plibSvg(it, px)) + '</span>' + cap;
      if (it.cap) t.querySelector('.cf-plib-nm').textContent = String(it.cap);
      else if (!beds) t.querySelector('.cf-plib-st').textContent = _t('floorplan_studio', 'Studio');
      if (grp && grp.member) t.classList.add('cf-mem');
      if (grp && grp.n > 0) {
        var sb = d.createElement('button');
        sb.type = 'button'; sb.className = 'cf-plib-sim' + (grp.open ? ' cf-on' : '');
        sb.innerHTML = '<span></span>' + (grp.open ? _PLIB_SVG.up : _PLIB_SVG.down);
        sb.firstChild.textContent = _t('floorplan_similar', '+{n} similar').replace('{n}', String(grp.n));
        sb.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          _plib.gopen = _plib.gopen || {};
          _plib.gopen[grp.g] = !_plib.gopen[grp.g];
          _plibGrid();
          _log('cmdbar   library: ' + grp.n + ' plan(s) very like ' + it.id + ' ' + (_plib.gopen[grp.g] ? 'laid out after it' : 'folded under it again') + ' (core-v572)');
        });
        t.appendChild(sb);
      }
      /* core-v595: a tap on the tile takes the plan as its picture shows it --
         turned over when the mirror button turned the picture */
      t.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _plibPick(it, !!_plib.mir[it.id]); });
      t.addEventListener('keydown', function (e) { if (e.target === t && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); _plibPick(it, !!_plib.mir[it.id]); } });
      var pv = t.querySelector('.cf-plib-pv');
      if (pv) {
        if (_plib.mir[it.id]) t.classList.add('cf-mir');
        /* core-v812 (TWO STORY thread): a design that cannot be turned over
           (it.noMirror -- a two-storey house) has no mirror button */
        if (!it.noMirror) {
        var mb = d.createElement('button');
        mb.type = 'button'; mb.className = 'cf-plib-mi';
        mb.innerHTML = _PLIB_SVG.mir;
        mb.setAttribute('aria-pressed', _plib.mir[it.id] ? 'true' : 'false');
        mb.title = _t('mirror', 'Mirror'); mb.setAttribute('aria-label', mb.title);
        mb.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          _plib.mir[it.id] = !_plib.mir[it.id];
          t.classList.toggle('cf-mir', !!_plib.mir[it.id]);
          mb.setAttribute('aria-pressed', _plib.mir[it.id] ? 'true' : 'false');
          _log('cmdbar   library: plan ' + it.id + ' shown ' + (_plib.mir[it.id] ? 'MIRRORED -- a tap on the plan takes it so' : 'as saved again') + ' (core-v595)');
        });
        pv.appendChild(mb);
        }
        var dl = d.createElement('button'); dl.type = 'button';
        dl.className = 'cf-plib-dl';
        dl.setAttribute('aria-label', _t('floorplan_use', 'Use floor plan'));
        dl.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _plibPick(it, !!_plib.mir[it.id]); });
        dl.innerHTML = _PLIB_SVG.take;
        pv.appendChild(dl);
      }
      /* NO CROSS ON A PLAN (core-v564, Marek: nerozumim, proc je tam krizek
         na floorplan; ma se mezi nimi dat filtrovat, neni duvod je zavirat).
         The library keeps one plan for each picture, the latest (worker
         w2.03), so nothing needs taking out by hand here */
      void admin;
      return t;
    }
    return { count: _plibCount, prune: _plibPrune, render: _plibRender, grid: _plibGrid, svg: _plibSvg, floor: _plibFloor };
  }

  global.ConfiguratorUI = {
    planBarCss: planBarCss,
    planLibraryCss: planLibraryCss,
    planIcons: _CMD_SVG,
    planLibraryIcons: _PLIB_SVG,
    planBar: planBar,
    planLibraryShell: planLibraryShell,
    planLibraryView: planLibraryView,
    VERSION: VERSION,
    bindOpeningTap: bindOpeningTap,
    openingHit: openingHit,
    dimLabel: dimLabel,
    UX: UX,
    ACCENT: ACCENT,
    STYLES: STYLES,
    slider: slider,
    handles: handles,
    wizard: wizard,
    stripCss: stripCss,
    stripFit: stripFit,
    stripMore: stripMore,
    gesture: gesture,
    DRAG_SLOP: DRAG_SLOP,
    undoButton: undoButton,
    brandCss: brandCss,
    injectBrand: injectBrand,
    injectRail: injectRail,
    railCss: railCss,
    picker: picker,
    cutPlanes: cutPlanes,
    trees: trees,
    treeGeometry: _treeLoad,
    kit: kit,
    schemes: schemes,
    SCHEMES_DEFAULT: SCHEMES_DEFAULT,
    STRINGS: STRINGS,
    str: str,
    ICON_PATHS: ICON_PATHS,
    icon: icon,
    iconSet: iconSet,
    ICON_PATHS: ICON_PATHS,
    icon: icon,
    iconSet: iconSet,
    OPENING_CATALOG: OPENING_CATALOG,
    openingGroup: openingGroup,
    fmtDim: fmtDim
  };

  _log('loaded ' + VERSION);

})(window);


/* =======================================================================
   PART 2 -- assets/map-manager.js, verbatim. Do not edit here; if this ever
   needs a change while other configurators still load map-manager.js, change
   both or they drift apart.
   ======================================================================= */

/* -------------------------------------------------------------------------
 *  map-manager.js
 *  ---------------------------------------------------------------
 *  Shared Mapbox layer for LEVA configurators (fence, pool, main, ...).
 *
 *  USAGE
 *  -----
 *    1. Page must already have loaded Mapbox GL JS (window.mapboxgl).
 *    2. Page must include the markup contract (#cf-mapbox, #cf-btn-map,
 *       #cf-map-controls + children, #cf-map-modal-backdrop + children).
 *       See README in the lib folder or copy from configurator-fence.liquid.
 *    3. Call:
 *
 *         window.MapManager.init({
 *           token:         '<MAPBOX_TOKEN>',                // required
 *           i18n:          { rotation: 'Rotation', ... },   // optional, all keys have English fallbacks
 *           storageKey:    'levaMap:p123',                  // shared persistence key for this project
 *           engine: {                                       // refs into host Three.js setup
 *             camera, controls, orthoCamera, orthoControls
 *           },
 *           getView:       () => state.view,                // returns '2d-top' | '3d'
 *           getRootSize:   () => ({ w, h }),                // returns canvas wrapper size
 *           onChange:      () => host.scheduleAutoSave(),   // called on user-driven map change
 *           requestRender: () => host.requestFrame()        // optional, no-op if absent
 *         });
 *
 *  V2 OPTIONS (all optional; omitting them reproduces v1 behaviour exactly)
 *  -----------------------------------------------------------------------
 *    autoMarkup:     true (default) - inject any missing map markup into
 *                    #cf-root. Elements already present in the host Liquid are
 *                    never touched, so existing hosts are unaffected. Pass
 *                    false to opt out entirely.
 *    rootId:         id of the wrapper to inject into      (default 'cf-root')
 *    canvasId:       map is inserted before this element   (default 'cf-canvas')
 *    pad:            include the 4-way pan pad in injected markup. Default
 *                    false - panning by finger/arrow is enough. Hosts that
 *                    already have the pad in their Liquid keep it either way,
 *                    since existing markup is never touched.
 *    lock:           show the lock button                  (default false)
 *    getLocked:      () => bool   - host supplies initial lock state
 *    onLockChange:   (locked) => {} - host stores the new lock state
 *    modalExtraHTML: string - extra markup injected inside the address modal
 *                    (e.g. a climate-zone select); host wires it up itself
 *    onMarkupReady:  (root) => {} - called after injection so the host can bind
 *                    whatever it added via modalExtraHTML
 *
 *  All DOM binding is defensive: a missing element is skipped, never thrown on.
 *
 *  WHAT IT DOES
 *  ------------
 *    - Adds an address-search modal that geocodes via Mapbox Geocoding API.
 *    - Loads a Mapbox map *under* the host's Three.js canvas at a zoom level
 *      that locks 1 m of map = 1 m of grid.
 *    - 4-direction pan pad + vertical rotation slider + 2-style switch.
 *    - In 3D view: tilts the map to follow the host's perspective camera
 *      (bearing, pitch, zoom recomputed from camera state) and adds 3D
 *      buildings + sky layers.
 *    - Persists center/bearing/style under storageKey; restore() replays it.
 *    - Cross-tab sync: a window 'storage' event handler reloads the map
 *      state when another tab writes the same storageKey.
 *
 *  NOT IN THIS FILE
 *  ----------------
 *    - The HTML markup (kept in host Liquid for {{ ... | t }} translations).
 *    - The .cf-map-modal-backdrop class -- that base class is reused by
 *      non-map modals (e.g. trial gate) and stays in the host CSS.
 * ------------------------------------------------------------------------- */

(function (global) {
  'use strict';

  // Defaults for i18n keys -- host can override any via init({i18n: {...}}).
  const I18N_DEFAULTS = {
    rotation:           'Rotation',
    streets:            'Roadmap',
    satellite:          'Satellite',
    addressPlaceholder: 'Address, city or place...',
    load:               'Load',
    cancel:             'Cancel',
    modalTitle:         'Map location',
    addBackground:      'Add map background',
    changeLocation:     'Change map location',
    lockMap:            'Lock map position',
    streetsShort:       'MAP',
    satelliteShort:     'SAT'
  };

  const MM = {
    // Bind-time config (filled by init)
    _deps:        null,
    _i18n:        I18N_DEFAULTS,
    _storageKey:  null,
    MAPBOX_TOKEN: null,
    STYLE_STREETS:   'mapbox://styles/mapbox/light-v11',
    STYLE_SATELLITE: 'mapbox://styles/mapbox/satellite-streets-v12',

    // Runtime state
    map:          null,
    currentStyle: 'streets',
    center:       null,     // [lng, lat]
    zoomLevel:    null,
    bearing:      0,
    address:      '',

    _geocodeAbort:    null,
    _suggestionItems: [],

    _opts:   {},
    _locked: false,

  _el(id) { return document.getElementById(id); },

  _on(id, ev, fn) {
    const el = document.getElementById(id);
    if (el) el.addEventListener(ev, fn);
    return el;
  },

  _esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
  },

  _ensureMarkup() {
    const o = this._opts || {};
    if (o.autoMarkup === false) return;
    const root = document.getElementById(o.rootId || 'cf-root');
    if (!root) return;
    const t = this._i18n;
    const esc = this._esc;
    const canvas = document.getElementById(o.canvasId || 'cf-canvas');
    const put = (node) => { if (canvas) root.insertBefore(node, canvas); else root.appendChild(node); };

    if (!document.getElementById('cf-mapbox')) {
      const d = document.createElement('div');
      d.id = 'cf-mapbox';
      put(d);
    }
    if (!root.querySelector('.cf-map-fade')) {
      const f = document.createElement('div');
      f.className = 'cf-map-fade';
      put(f);
    }
    if (!document.getElementById('cf-map-credit')) {
      const c = document.createElement('div');
      c.id = 'cf-map-credit';
      c.className = 'cf-map-credit';
      c.textContent = '\u00A9 Mapbox \u00A9 OpenStreetMap';
      root.appendChild(c);
    }
    if (!document.getElementById('cf-map-controls')) {
      const padHTML = (o.pad !== true) ? '' :
        '<div class="cf-map-pad">' +
        '  <button id="cf-map-pad-up" class="cf-map-pad-btn cf-map-pad-up" type="button">\u25B2</button>' +
        '  <button id="cf-map-pad-left" class="cf-map-pad-btn cf-map-pad-left" type="button">\u25C0</button>' +
        '  <button id="cf-map-pad-right" class="cf-map-pad-btn cf-map-pad-right" type="button">\u25B6</button>' +
        '  <button id="cf-map-pad-down" class="cf-map-pad-btn cf-map-pad-down" type="button">\u25BC</button>' +
        '</div>';
      const lockHTML = !o.lock ? '' :
        '<button id="cf-map-lock" class="cf-map-style-btn cf-map-lock-btn" type="button" title="' + esc(t.lockMap) + '" aria-pressed="false">' +
        '  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '    <rect x="5" y="11" width="14" height="9" rx="2"/>' +
        '    <path d="M8 11V7a4 4 0 0 1 8 0v4" id="cf-map-lock-shackle"/>' +
        '  </svg>' +
        '</button>';
      const ctrls = document.createElement('div');
      ctrls.id = 'cf-map-controls';
      ctrls.className = 'cf-map-controls cf-map-stacked';
      ctrls.innerHTML =
        '<div class="cf-map-rotate-vert">' +
        '  <input type="range" id="cf-map-rotate" class="cf-map-rotate-slider" min="0" max="360" step="1" value="0" aria-label="' + esc(t.rotation) + '">' +
        '</div>' +
        '<div class="cf-map-right-col">' +
        '  <div class="cf-map-style-row">' +
        '    <button id="cf-map-style-streets" class="cf-map-style-btn active" type="button">' + esc(t.streetsShort || 'MAP') + '</button>' +
        '    <button id="cf-map-style-satellite" class="cf-map-style-btn" type="button">' + esc(t.satelliteShort || 'SAT') + '</button>' +
        lockHTML +
        '  </div>' +
        padHTML +
        '</div>';
      root.appendChild(ctrls);
    }
    if (!document.getElementById('cf-map-modal-backdrop')) {
      const m = document.createElement('div');
      m.id = 'cf-map-modal-backdrop';
      m.className = 'cf-map-modal-backdrop';
      m.innerHTML =
        '<div class="cf-map-modal" role="dialog" aria-modal="true">' +
        '  <h3>' + esc(t.modalTitle) + '</h3>' +
        '  <div class="cf-map-modal-input-wrap">' +
        '    <input id="cf-map-address-input" type="text" placeholder="' + esc(t.addressPlaceholder) + '" autocomplete="off">' +
        '    <div id="cf-map-suggestions" class="cf-map-modal-suggestions"></div>' +
        '  </div>' +
        (o.modalExtraHTML || '') +
        '  <div class="cf-map-modal-buttons">' +
        '    <button id="cf-map-modal-cancel" class="cf-map-modal-btn cf-map-modal-btn-cancel" type="button">' + esc(t.cancel) + '</button>' +
        '    <button id="cf-map-modal-ok" class="cf-map-modal-btn cf-map-modal-btn-ok" type="button" disabled>' + esc(t.load) + '</button>' +
        '  </div>' +
        '</div>';
      root.appendChild(m);
    }
    if (typeof o.onMarkupReady === 'function') o.onMarkupReady(root);
  },

  _hidePadUnlessWanted() {
    if (this._opts && this._opts.pad === true) return;
    ['cf-map-pad-up', 'cf-map-pad-down', 'cf-map-pad-left', 'cf-map-pad-right'].forEach((id) => {
      const el = this._el(id);
      if (el && el.style) el.style.display = 'none';
    });
    const grid = document.querySelector ? document.querySelector('.cf-map-pad') : null;
    if (grid && grid.style) grid.style.display = 'none';
  },

  setLocked(v) {
    this._locked = !!v;
    const b = document.getElementById('cf-map-lock');
    if (b) {
      b.classList.toggle('locked', this._locked);
      b.setAttribute('aria-pressed', this._locked ? 'true' : 'false');
    }
    const sh = document.getElementById('cf-map-lock-shackle');
    if (sh) sh.setAttribute('d', this._locked ? 'M8 11V7a4 4 0 0 1 8 0v4' : 'M8 11V7a4 4 0 0 1 8 0');
  },

  isLocked() { return !!this._locked; },

  init() {
    if (!window.mapboxgl) {
      console.warn('[MapManager] mapbox-gl not loaded');
      return;
    }
    mapboxgl.accessToken = this.MAPBOX_TOKEN;

    this._ensureMarkup();
    this._hidePadUnlessWanted();

    this._on('cf-btn-map', 'click', () => {
      this._openModal();
    });

    const backdrop = this._el('cf-map-modal-backdrop');
    const input = this._el('cf-map-address-input');
    const okBtn = this._el('cf-map-modal-ok');
    const sugg = this._el('cf-map-suggestions');

    if (backdrop) {
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) this._closeModal();
      });
    }
    this._on('cf-map-modal-cancel', 'click', () => this._closeModal());

    let debounceT = null;
    if (input) {
      input.addEventListener('input', () => {
        clearTimeout(debounceT);
        const q = input.value.trim();
        if (q.length < 3) {
          if (sugg) sugg.classList.remove('open');
          if (okBtn) okBtn.disabled = true;
          return;
        }
        debounceT = setTimeout(() => this._geocode(q), 300);
      });
    }

    this._on('cf-map-modal-ok', 'click', () => {
      const selected = this._suggestionItems.find(s => s.selected) || this._suggestionItems[0];
      if (!selected) return;
      this._loadMap(selected.center, selected.label);
      this._closeModal();
    });

    this._on('cf-map-style-streets', 'click', () => this._setStyle('streets'));
    this._on('cf-map-style-satellite', 'click', () => this._setStyle('satellite'));

    this._on('cf-map-lock', 'click', () => {
      this.setLocked(!this._locked);
      const cb = this._opts && this._opts.onLockChange;
      if (typeof cb === 'function') cb(this._locked);
      if (this._deps && this._deps.onChange) this._deps.onChange();
    });
    if (this._opts && typeof this._opts.getLocked === 'function') this.setLocked(this._opts.getLocked());

    // Directional pan pad (4 arrows). Step size comes from the host (typically
    // ~5 % of the land extent) so the meters-per-click scales with the scene.
    // Fallback to 2 m if host didn't provide a callback.
    const padStep = () => {
      if (typeof this._deps.getPadStep === 'function') return this._deps.getPadStep();
      return 2;
    };
    this._on('cf-map-pad-up', 'click',    () => this._panMeters(0, -padStep()));
    this._on('cf-map-pad-down', 'click',  () => this._panMeters(0,  padStep()));
    this._on('cf-map-pad-left', 'click',  () => this._panMeters(-padStep(), 0));
    this._on('cf-map-pad-right', 'click', () => this._panMeters( padStep(), 0));

    // Rotate slider -- silent (no value label)
    const rotateInput = this._el('cf-map-rotate');
    const updateRotateFill = () => {
      if (!rotateInput) return;
      const pct = (parseFloat(rotateInput.value) / 360) * 100;
      rotateInput.style.setProperty('--cf-fill', pct + '%');
    };
    if (rotateInput) {
      rotateInput.addEventListener('input', () => {
        const deg = parseInt(rotateInput.value, 10);
        this.bearing = deg;
        if (this.map) {
          if (this._deps.getView() === '3d') {
            // In 3D the bearing is composed of baseBearing + scene azimuth;
            // recompute via sync rather than overwriting with raw slider value.
            this._syncMapTo3D(false);
          } else {
            this.map.setBearing(deg);
          }
        }
        updateRotateFill();
      });
    }
    updateRotateFill();

    // Initial button state
    this._updateSidebarButton();
  },

  _openModal() {
    const bd = this._el('cf-map-modal-backdrop');
    if (bd) bd.classList.add('open');
    const input = document.getElementById('cf-map-address-input');
    input.value = this.address || '';
    setTimeout(() => input.focus(), 50);
  },
  _closeModal() {
    const bd2 = this._el('cf-map-modal-backdrop');
    if (bd2) bd2.classList.remove('open');
    const sg2 = this._el('cf-map-suggestions');
    if (sg2) sg2.classList.remove('open');
  },

  _geocode(query) {
    if (this._geocodeAbort) this._geocodeAbort.abort();
    this._geocodeAbort = new AbortController();
    const url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
      encodeURIComponent(query) + '.json?access_token=' + this.MAPBOX_TOKEN +
      '&autocomplete=true&limit=5&types=address,place,locality,neighborhood,region';
    fetch(url, { signal: this._geocodeAbort.signal })
      .then(r => r.json())
      .then(data => {
        const sugg = document.getElementById('cf-map-suggestions');
        sugg.innerHTML = '';
        this._suggestionItems = (data.features || []).map((f, i) => ({
          label: f.place_name,
          center: f.center,  // [lng, lat]
          selected: i === 0
        }));
        if (!this._suggestionItems.length) {
          sugg.classList.remove('open');
          document.getElementById('cf-map-modal-ok').disabled = true;
          return;
        }
        this._suggestionItems.forEach((s, i) => {
          const div = document.createElement('div');
          div.className = 'cf-map-modal-sugg-item';
          div.textContent = s.label;
          div.addEventListener('click', () => {
            this._suggestionItems.forEach(it => it.selected = false);
            s.selected = true;
            const ai = this._el('cf-map-address-input');
            if (ai) ai.value = s.label;
            sugg.classList.remove('open');
            document.getElementById('cf-map-modal-ok').disabled = false;
          });
          sugg.appendChild(div);
        });
        sugg.classList.add('open');
        document.getElementById('cf-map-modal-ok').disabled = false;
      })
      .catch(e => {
        if (e.name !== 'AbortError') console.warn('[MapManager] geocode failed', e);
      });
  },

  // Compute the Mapbox zoom level that matches our Three.js ortho camera scale,
  // so that 1 m on the grid = 1 m on the satellite imagery.
  //
  // Mapbox metersPerPixel = 156543.03392 x cos(lat) / 2^zoom
  // We need: ourMetersPerPixel = Mapbox metersPerPixel
  //   ->  zoom = log2(156543.03392 x cos(lat) / ourMetersPerPixel)
  _root() {
    /* Every other place honours opts.rootId; these two used to hardcode
       'cf-root', which is only correct for hosts that happen to use that id.
       A host on a different wrapper got null here -- _computeZoomForLat threw
       on clientWidth and the caller never reached _closeModal(). */
    return document.getElementById((this._opts && this._opts.rootId) || 'cf-root');
  },

  /* Mapbox refuses to zoom past MAX_MAP_ZOOM and says nothing about it -- it
     just clamps. This function is what glues a metre in the scene to a metre on
     the ground, so when its answer is silently ignored the two drift apart by
     2^(asked - given). Ask for 25.7, get 22, and the ground is 13x the scale of
     the model: pan the building a few metres and it lands two hundred away.

     Small footprints are what push it over. The tighter the ortho frustum, the
     higher the zoom needed to match, so this only starts happening once a
     configurator is used for something small -- which is why it never showed up
     on a container and does on a sauna.

     Returning the cap is not enough -- that only makes the lie honest. The scene
     is the side that has to give: if the map cannot go past 24, the ortho camera
     must not either, so the frustum is widened until the two agree. The user
     loses the last bit of zoom they cannot have anyway, and a metre stays a
     metre. */
  MAX_MAP_ZOOM: 24,

  _computeZoomForLat(lat) {
    const wrap = this._root();
    /* THE CANVAS'S OWN WIDTH, not the wrap's: with the drawer open the canvas
       is the narrower one, and the frustum spans the canvas -- measuring the
       wrap put the map a drawer's worth too far out (ui-v124) */
    const rd = this._deps.renderer && this._deps.renderer.domElement;
    const canvasW = (rd && rd.clientWidth) || (wrap ? wrap.clientWidth : 800);
    this._lastCanvasW = canvasW;
    const cam = this._deps.orthoCamera;
    const K = 156543.03392 * Math.cos(lat * Math.PI / 180);
    const span = cam.right - cam.left;

    // Three.js ortho frustum width in world meters:
    const frustumW = span / cam.zoom;
    const raw = Math.log2(K / (frustumW / canvasW));
    const cap = this.MAX_MAP_ZOOM;
    if (raw <= cap) { this._zoomClamped = null; return raw; }

    /* Pull the camera back to the tightest frustum the map can match, then
       recompute from the camera we actually have rather than the one we wanted.
       Mutating the camera from a function named compute is not tidy; leaving the
       scene at a scale the ground cannot hold is worse. */
    const maxMpp = K / Math.pow(2, cap);
    cam.zoom = span / (maxMpp * canvasW);
    cam.updateProjectionMatrix();
    if (this._deps.orthoControls) this._deps.orthoControls.update();
    this._zoomClamped = { asked: raw, got: cap, pulledBack: Math.pow(2, raw - cap) };
    return cap;
  },

  _loadMap(center, label) {
    this.center = center;
    this.address = label;
    this.zoomLevel = this._computeZoomForLat(center[1]);

    if (this.map) {
      // Already exists -- just recenter
      this._selfMove++;
      this.map.jumpTo({ center, zoom: this.zoomLevel, bearing: this.bearing });
    } else {
      const mapDiv = document.getElementById('cf-mapbox');
      mapDiv.classList.add('cf-map-visible');
      const cr = this._el('cf-map-credit');
      if (cr) cr.classList.add('cf-map-visible');
      const rt = this._el('cf-root');
      if (rt) rt.classList.add('cf-has-map');
      const fade = document.querySelector('.cf-map-fade');
      if (fade) fade.classList.add('cf-map-visible');

      this.map = new mapboxgl.Map({
        container: 'cf-mapbox',
        style: this.STYLE_STREETS,
        center,
        /* default is 22; v3 goes to 24, and every step buys back a factor of
           two of the scale error above */
        maxZoom: this.MAX_MAP_ZOOM,
        zoom: this.zoomLevel,
        bearing: this.bearing,
        pitch: 0,
        attributionControl: false,
        interactive: false  // user controls map ONLY via arrow pad + rotate slider
      });
      /* core-v589: how often the map under the model repaints, for the perf lines */
      try {
        const mmSelf = this;
        mmSelf._pfRenders = 0;
        mmSelf._pfSince = (global.performance && global.performance.now) ? global.performance.now() : Date.now();
        this.map.on('render', function () { mmSelf._pfRenders++; });
      } catch (ePfM) {}
      // Disable every interaction handler explicitly (belt + suspenders)
      /* Whatever moves the map, the map has to say so.
         center was a cache that only _panMeters and _loadMap ever wrote, on the
         assumption that the arrow pad is the only way to move -- interactive is
         false right below. But a host can hand control back (the sauna's lock
         toggle calls dragPan.enable() whenever the map is unlocked), and then a
         finger drags mapbox directly. getCenter() moves; this.center does not.
         persist() then writes the geocoded address it has always held, restore()
         puts it back, and the building appears to forget where it stood.

         moveend is mapbox's own word for "I have been moved, by anyone". Reading
         the centre from the map instead of from our memory of it means there is
         one truth, and no new way of panning can quietly bypass it. */
      /* mapbox fires moveend and rotateend for ITS moves and for ours alike, and
         it cannot tell us which. _syncMapTo3D turns the map to follow the camera
         -- so listening naively to rotateend meant: sync turns the map, mapbox
         reports a rotation, we treat it as the user's, save it, the scene reacts,
         sync turns the map again. The model span like a top.
         So every programmatic move is announced first, and the listener ignores
         exactly one event per announcement. */
      this._selfMove = 0;
      this.map.on('moveend', () => {
        if (!this.map) return;
        if (this._selfMove > 0) { this._selfMove--; return; }
        const c0 = this.map.getCenter();
        /* the view panned off the building (ui-v137): the building stands that
           far back from the middle of the map */
        const vo2 = (this._deps && this._deps.viewOffset && this._deps.viewOffset()) || null;
        const cc = vo2 ? this._offsetCenter([c0.lng, c0.lat], -vo2.x, -vo2.z) : [c0.lng, c0.lat];
        const c = { lng: cc[0], lat: cc[1] };
        this._viewCtr = vo2 ? [c0.lng, c0.lat] : null;
        if (!this.center ||
            Math.abs(c.lng - this.center[0]) > 1e-9 || Math.abs(c.lat - this.center[1]) > 1e-9) {
          this.center = [c.lng, c.lat];
          if (this._deps && this._deps.onChange) this._deps.onChange();   // persist
        }
      });
      /* Bearing is not read back at all. The scene owns it -- rotate the camera
         and _syncMapTo3D turns the map to match -- so taking it back from the
         map is what closed the loop. It is already saved from this.bearing,
         which the rotate slider sets. */

      this.map.dragPan.disable();
      this.map.scrollZoom.disable();
      this.map.boxZoom.disable();
      this.map.dragRotate.disable();
      this.map.keyboard.disable();
      this.map.doubleClickZoom.disable();
      this.map.touchZoomRotate.disable();

      this._updateSidebarButton();

      // Canvas always captures pointer events -- map is purely a visual layer
      // controlled by the arrow pad and rotate slider.
      const cv = this._el('cf-canvas');
      if (cv) cv.style.pointerEvents = 'auto';

      // Keep the map in lock-step with the ortho camera: 1 grid m = 1 map m, and
      // the same patch of ground under the model.
      //
      // This used to be called syncZoom and did only that -- it read the ortho
      // camera's scale and ignored where it was pointing. But mapboxgl runs with
      // interactive:false, so a finger on the 2D view is not moving the map at
      // all: it moves the ortho camera over a map that never budges. The scene
      // followed, the ground did not, and this.center stayed on the geocoded
      // address forever. persist() then dutifully saved the address, restore()
      // put it back, and the building appeared to forget where it was standing.
      //
      // So the target's travel is the map's travel. Convert the metres the
      // camera moved into a new centre, and the map has a memory again.
      let lastTgt = null;
      const syncOrtho = () => {
        if (!this.map || !this.center) return;
        if (this._deps.getView() !== '2d-top') return;   // 3D has its own sync path

        const t = this._deps.orthoControls.target;
        /* A LOOK ROUND IS NOT A MOVE (ui-v137, Marek: neda se zoomovat, nehybeme
           pudorysem mimo stred). The 2D view now pans and zooms where the fingers
           are, by moving the ortho camera over the plan. That travel is the view's,
           not the building's: the map follows it on screen and the place the
           building stands on (this.center, what is saved) stays where it is. */
        const vo = (this._deps.viewOffset && this._deps.viewOffset()) || null;
        if (vo) {
          const vc = this._offsetCenter(this.center, vo.x, vo.z);
          /* compared with where the map really is, not a remembered value: the
             3D view moves the map too, and a cache would miss that */
          const mc = this.map.getCenter();
          if (!mc || Math.abs(mc.lng - vc[0]) > 1e-9 || Math.abs(mc.lat - vc[1]) > 1e-9) {
            this._viewCtr = vc;
            this._selfMove++;
            this.map.setCenter(vc);
          }
          lastTgt = { x: t.x, y: t.y, z: t.z };
        } else if (lastTgt) {
          const dx = t.x - lastTgt.x;   // east
          const dz = t.z - lastTgt.z;   // south (scene north = -Z)
          if (Math.abs(dx) > 1e-4 || Math.abs(dz) > 1e-4) {
            /* metres -> degrees at this latitude. The map is unrotated in 2D
               apart from the user's own bearing, so undo that first. */
            const br = (this.bearing || 0) * Math.PI / 180;
            const ex = dx * Math.cos(br) - dz * Math.sin(br);
            const ez = dx * Math.sin(br) + dz * Math.cos(br);
            const lat = this.center[1];
            const mPerDegLat = 111132.92 - 559.82 * Math.cos(2 * lat * Math.PI / 180);
            const mPerDegLng = 111412.84 * Math.cos(lat * Math.PI / 180);
            this.center = [
              this.center[0] + ex / mPerDegLng,
              this.center[1] - ez / mPerDegLat
            ];
            this._selfMove++;
            this.map.setCenter(this.center);
            this._deps.onChange();      // persist()
          }
        }
        lastTgt = { x: t.x, y: t.y, z: t.z };

        const z = this._computeZoomForLat(this.center[1]);
        if (Math.abs(z - this.zoomLevel) > 0.005) {
          this.zoomLevel = z;
          this.map.setZoom(z);
        }
      };
      /* the first event after a view switch must not be read as a pan: the
         target jumps to wherever 2D starts, and that is not the user moving */
      this._resetOrthoPanRef = () => { lastTgt = null; };
      this._deps.orthoControls.addEventListener('change', syncOrtho);
      // Also after fitCamera (which changes zoom programmatically without orthoControls event)
      window.addEventListener('resize', () => setTimeout(syncOrtho, 50));
      // Initial sync (after first frame, ortho camera is fitted)
      setTimeout(syncOrtho, 100);

      // --- 3D sync: when perspective camera orbits, mirror the move on the map.
      // Fires only when controls actually move (no idle ticks). Cheap enough to
      // run unthrottled -- map.jumpTo is a transform update, not a re-render of tiles.
      this._deps.controls.addEventListener('change', () => {
        if (this._deps.getView() === '3d' && this.map && this.center) this._syncMapTo3D(false);
      });

      // Install 3D layers as soon as the style is ready (also re-fires after _setStyle).
      // If we're already in 3D when the map loads or the style swaps, also push the
      // current Three.js camera state to Mapbox right away.
      this.map.on('style.load', () => {
        this._install3DLayers();
        if (this._deps.getView() === '3d') this._syncMapTo3D(false);
      });

      // Re-fit map size when canvas resizes
      new ResizeObserver(() => {
        if (this.map) {
          this.map.resize();
          syncOrtho();
        }
      }).observe(mapDiv);
    }

    const mc = this._el('cf-map-controls');
    if (mc) mc.classList.add('open');
    this._deps.onChange();
  },

  _setStyle(s) {
    if (!this.map) return;
    this.currentStyle = s;
    const url = s === 'satellite' ? this.STYLE_SATELLITE : this.STYLE_STREETS;
    this.map.setStyle(url);
    const bs = this._el('cf-map-style-streets');
    if (bs) bs.classList.toggle('active', s === 'streets');
    const bt = this._el('cf-map-style-satellite');
    if (bt) bt.classList.toggle('active', s === 'satellite');
    this._deps.onChange();
  },

  // --- 3D extensions ------------------------------------------
  // Install 3D buildings + sky layer. Terrain is intentionally NOT enabled
  // even though the DEM source could be added -- Mapbox terrain renders ground
  // at real elevation above sea level (e.g. ~200 m in Brno), but the Three.js
  // scene's fence/footprint sits at y=0. The two reference frames mismatch
  // and the fence appears to float above the elevated map surface. Without
  // terrain, both the map ground and the scene plane share sea level, so the
  // fence visually rests on the ground. 3D buildings extrude from this flat
  // base which is fine for context.
  _install3DLayers() {
    if (!this.map) return;
    const m = this.map;
    // 1. Sky layer -- visible only at high pitch, but harmless otherwise
    try {
      if (!m.getLayer('cf-sky')) {
        m.addLayer({
          id: 'cf-sky',
          type: 'sky',
          paint: {
            'sky-type': 'atmosphere',
            'sky-atmosphere-sun-intensity': 5
          }
        });
      }
    } catch (e) { console.warn('[MapManager] sky layer add failed', e); }
    // 2. 3D buildings -- both Light and Satellite-streets styles expose
    // 'composite' source with 'building' source-layer.
    try {
      if (!m.getLayer('cf-3d-buildings') && m.getSource('composite')) {
        m.addLayer({
          id: 'cf-3d-buildings',
          source: 'composite',
          'source-layer': 'building',
          filter: ['==', 'extrude', 'true'],
          type: 'fill-extrusion',
          minzoom: 14,
          paint: {
            'fill-extrusion-color': '#d6cfc2',
            'fill-extrusion-height': ['get', 'height'],
            'fill-extrusion-base': ['get', 'min_height'],
            'fill-extrusion-opacity': 0.85
          }
        });
      }
    } catch (e) { console.warn('[MapManager] 3D buildings layer add failed', e); }

    // Apply current view's layer visibility (newly-added layers start visible)
    this._set3DLayerVisibility(this._deps.getView() === '3d');
  },

  // Compute Mapbox pitch/bearing/zoom that match the current Three.js
  // perspective camera state. Center is left untouched -- the map stays anchored
  // wherever the user placed it; only the viewing angle follows the orbit.
  _computeMapStateFor3D() {
    if (!this._deps.camera || !this._deps.controls || !this.center) return null;
    const target = this._deps.controls.target;
    const pos = this._deps.camera.position;
    const offX = pos.x - target.x;
    const offY = pos.y - target.y;
    const offZ = pos.z - target.z;
    const horiz = Math.sqrt(offX*offX + offZ*offZ);
    const dist = Math.sqrt(horiz*horiz + offY*offY);
    if (dist < 0.01) return null;

    // Pitch -- Three.js elevation above horizon vs. Mapbox tilt-from-top-down.
    // Mapbox max pitch is 85 deg; clamp to 0..85 to avoid jitter near zenith.
    const elev = Math.atan2(offY, horiz);
    const pitch = Math.max(0, Math.min(85, 90 - elev * 180 / Math.PI));

    // Bearing -- compass direction the camera is facing.
    // Scene convention: -Z = north, +X = east. Camera look direction = -offset.
    // East component of look = -offX. North component of look = offZ (since
    // north = -Z, the north-component of (x,z) is -z, and z here is -offZ).
    // bearingDeg = atan2(eastComp, northComp). Add user's slider baseBearing
    // (which rotated the satellite imagery to align with scene north in 2D).
    const sceneBearingDeg = Math.atan2(-offX, offZ) * 180 / Math.PI;
    const bearing = (this.bearing || 0) + sceneBearingDeg;

    // Zoom -- match Three.js perspective frustum at the target plane so 1 m on
    // scene ~ 1 m on map at the center of screen.
    const wrap = this._root();
    const canvasH = wrap ? wrap.clientHeight : 600;
    const fovRad = this._deps.camera.fov * Math.PI / 180;
    const heightAtTarget = 2 * dist * Math.tan(fovRad / 2);
    const mpp = heightAtTarget / canvasH;
    const lat = this.center[1];
    const zoom = Math.log2(156543.03392 * Math.cos(lat * Math.PI / 180) / mpp);

    return { pitch, bearing, zoom };
  },

  _syncMapTo3D(animate) {
    if (!this.map || !this.center) return;
    const s = this._computeMapStateFor3D();
    if (!s) return;
    if (animate) {
      this._selfMove++;
      this.map.easeTo({ pitch: s.pitch, bearing: s.bearing, zoom: s.zoom, duration: 400 });
    } else {
      this._selfMove++;
      this.map.jumpTo({ pitch: s.pitch, bearing: s.bearing, zoom: s.zoom });
    }
  },

  _syncMapTo2D(animate) {
    if (!this.map || !this.center) return;
    /* Entering 2D moves the ortho target somewhere of its own accord; that jump
       is not a pan and must not be charged to the map. */
    if (this._resetOrthoPanRef) this._resetOrthoPanRef();
    // Flat top-down view; restore the user's base bearing and ortho-matched zoom.
    const z = this._computeZoomForLat(this.center[1]);
    this.zoomLevel = z;
    if (animate) {
      this._selfMove++;
      this.map.easeTo({ pitch: 0, bearing: this.bearing || 0, zoom: z, duration: 400 });
    } else {
      this._selfMove++;
      this.map.jumpTo({ pitch: 0, bearing: this.bearing || 0, zoom: z });
    }
  },

  // Toggle the 3D-only layers (buildings, sky) so the 2D top-down view looks
  // identical to the original flat map.
  _set3DLayerVisibility(visible) {
    if (!this.map) return;
    const v = visible ? 'visible' : 'none';
    for (const id of ['cf-3d-buildings', 'cf-sky']) {
      try {
        if (this.map.getLayer(id)) this.map.setLayoutProperty(id, 'visibility', v);
      } catch(e){}
    }
  },

  _updateSidebarButton() {
    const btn = document.getElementById('cf-btn-map');
    if (!btn) return;
    // Always shows the map icon. Olive background indicates a map is currently loaded.
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6l6-2 6 2 6-2v14l-6 2-6-2-6 2z"/><path d="M9 4v16"/><path d="M15 6v16"/></svg>';
    if (this.map) {
      btn.classList.add('cf-map-btn-active');
      btn.title = this._i18n.changeLocation;
    } else {
      btn.classList.remove('cf-map-btn-active');
      btn.title = this._i18n.addBackground;
    }
  },

  // Pan map by N meters in screen-space direction (uses current bearing).
  // Used by 4 directional arrow buttons on the controls panel.
  _panMeters(dirX, dirZ) {
    if (!this.map) return;
    // Convert meters to pixels at the current zoom + latitude.
    // metersPerPixel = 156543.03392 x cos(lat) / 2^zoom
    const lat = this.center[1];
    const zoom = this.map.getZoom();
    const metersPerPixel = 156543.03392 * Math.cos(lat * Math.PI / 180) / Math.pow(2, zoom);
    const dx = dirX / metersPerPixel;
    const dy = dirZ / metersPerPixel;
    // map.panBy uses screen-space pixel offset and handles bearing internally,
    // so arrows always match what's visible on screen regardless of rotation.
    this.map.panBy([dx, dy], { animate: false });
    // Update our cached center from the map's new state
    const c = this.map.getCenter();
    const vo3 = (this._deps && this._deps.viewOffset && this._deps.viewOffset()) || null;
    this.center = vo3 ? this._offsetCenter([c.lng, c.lat], -vo3.x, -vo3.z) : [c.lng, c.lat];
    this._deps.onChange();
  },

  /* a centre moved by x metres east and z metres south, the user's bearing
     undone first -- the arithmetic the ortho sync has always used */
  _offsetCenter(center, x, z) {
    if (!center) return center;
    if (!x && !z) return [center[0], center[1]];
    const br = (this.bearing || 0) * Math.PI / 180;
    const ex = x * Math.cos(br) - z * Math.sin(br);
    const ez = x * Math.sin(br) + z * Math.cos(br);
    const lat = center[1];
    const mPerDegLat = 111132.92 - 559.82 * Math.cos(2 * lat * Math.PI / 180);
    const mPerDegLng = 111412.84 * Math.cos(lat * Math.PI / 180);
    return [center[0] + ex / mPerDegLng, center[1] - ez / mPerDegLat];
  },

  _removeMap() {
    if (this.map) { this.map.remove(); this.map = null; }
    this.center = null;
    this.zoomLevel = null;
    this.bearing = 0;
    this.address = '';
    const mb = this._el('cf-mapbox');
    if (mb) mb.classList.remove('cf-map-visible');
    const cr2 = this._el('cf-map-credit');
    if (cr2) cr2.classList.remove('cf-map-visible');
    const rt2 = this._el('cf-root');
    if (rt2) rt2.classList.remove('cf-has-map');
    const fadeEl = document.querySelector('.cf-map-fade');
    if (fadeEl) fadeEl.classList.remove('cf-map-visible');
    const mc2 = this._el('cf-map-controls');
    if (mc2) mc2.classList.remove('open');
    const rotateInput = this._el('cf-map-rotate');
    if (rotateInput) rotateInput.value = 0;
    const cv2 = this._el('cf-canvas');
    if (cv2) cv2.style.pointerEvents = 'auto';
    this._updateSidebarButton();
    if (this._deps && this._deps.onChange) this._deps.onChange();
  },

  // Serialization payload -- host calls MapManager.serialize() inside its own
  // save-to-storage routine and stores the result alongside its other state.
  // IMPORTANT: bearing is the slider value (this.bearing), NOT map.getBearing().
  // In 3D the live map bearing is base + scene azimuth -- saving the composed
  // value and restoring it as base would double-rotate. Zoom is intentionally
  // omitted; _loadMap recomputes it from the recipient's ortho frustum so the
  // 1m-grid-equals-1m-map invariant holds across different screen sizes.
  serialize() {
    if (!this.map || !this.center) return null;
    return {
      center: this.center,
      bearing: this.bearing || 0,
      style: this.currentStyle,
      address: this.address
    };
  },
  restore(data) {
    if (!data || !data.center) return;
    this.bearing = data.bearing || 0;
    const rotateInput = document.getElementById('cf-map-rotate');
    if (rotateInput) {
      rotateInput.value = Math.round(this.bearing);
      // Refresh the slider's --cf-fill so the rotated knob position matches
      const pct = (this.bearing / 360) * 100;
      rotateInput.style.setProperty('--cf-fill', pct + '%');
    }
    this._loadMap(data.center, data.address || '');
    if (data.style === 'satellite') this._setStyle('satellite');
  },

  // --- Persistence ---------------------------------------------
  // Storage is the lib's own concern when storageKey is set. Hosts can still
  // also include serialize() output in their own state blob, but persist()
  // makes the map self-contained for cross-tab sync.
  _suppressStorageEvent: false,
  persist() {
    if (!this._storageKey) return;
    const data = this.serialize();
    if (!data) return;
    try {
      this._suppressStorageEvent = true;
      localStorage.setItem(this._storageKey, JSON.stringify(data));
      // Cookie-style reset on next tick -- the storage event fires only in
      // OTHER tabs, but this guards against listeners we attach later.
      setTimeout(() => { this._suppressStorageEvent = false; }, 0);
    } catch (e) { /* quota or unavailable */ }
  },
  loadFromStorage() {
    if (!this._storageKey) return false;
    try {
      const raw = localStorage.getItem(this._storageKey);
      if (!raw) return false;
      const data = JSON.parse(raw);
      this.restore(data);
      return true;
    } catch (e) { return false; }
  },
  _onStorageEvent(e) {
    // Only react to changes to OUR key from another tab. The browser fires
    // 'storage' only in tabs other than the writer, so the suppression flag
    // is belt-and-suspenders for unusual environments (e.g. extensions).
    if (this._suppressStorageEvent) return;
    if (!this._storageKey || e.key !== this._storageKey) return;
    if (!e.newValue) return;
    try {
      const data = JSON.parse(e.newValue);
      // If center changed, do a full restore. Otherwise apply only bearing/style
      // to avoid the map flickering through a full reload.
      const oldCenter = this.center;
      if (!oldCenter || data.center[0] !== oldCenter[0] || data.center[1] !== oldCenter[1]) {
        this.restore(data);
      } else {
        if (typeof data.bearing === 'number') {
          this.bearing = data.bearing;
          if (this.map) this.map.setBearing(data.bearing);
          const ri = document.getElementById('cf-map-rotate');
          if (ri) {
            ri.value = Math.round(data.bearing);
            ri.style.setProperty('--cf-fill', (data.bearing / 360 * 100) + '%');
          }
        }
        if (data.style && data.style !== this.currentStyle) this._setStyle(data.style);
      }
    } catch (err) { /* malformed payload from other tab */ }
  }
  };

  // --- Public API ----------------------------------------------
  // init() validates dependencies and wires every DOM listener exactly once.
  // Safe to call again with new options (e.g. when storageKey changes); we
  // simply update internal refs without re-binding listeners.
  MM.init = (function (originalInit) {
    let initialized = false;
    return function (opts) {
      opts = opts || {};
      const hostOnChange = (opts.onChange || (() => {}));
      MM._deps         = opts.engine ? Object.assign({}, opts.engine, {
        getView:       opts.getView       || (() => '2d-top'),
        /* ui-v137: how far the 2D view is panned off the building, in metres
           ({x, z}), or null where the host does not say */
        viewOffset:    opts.viewOffset    || (() => null),
        getRootSize:   opts.getRootSize   || (() => ({ w: window.innerWidth, h: window.innerHeight })),
        // Wrap host's onChange so persist() always runs first -- host can rely
        // on storage being current when its own save logic runs.
        onChange:      () => { MM.persist(); hostOnChange(); },
        requestRender: opts.requestRender || (() => {})
      }) : null;
      MM._i18n         = Object.assign({}, I18N_DEFAULTS, opts.i18n || {});
      MM._storageKey   = opts.storageKey || 'levaMap:default';
      MM.MAPBOX_TOKEN  = opts.token || MM.MAPBOX_TOKEN;
      MM._opts         = opts;
      if (initialized) return;            // listeners already bound
      initialized = true;
      window.addEventListener('storage', e => MM._onStorageEvent(e));
      originalInit.call(MM);
    };
  }(MM.init));

  /* ---------------------------------------------------------------------
   *  ConfiguratorStage
   *  -----------------
   *  The Three.js viewport every map-backed configurator sets up the same
   *  way: renderer, perspective + orthographic cameras, orbit controls with
   *  the 2D hard-lock, grid, recentre/fit, and 2D<->3D switching. Lives in
   *  this file because it is the half the map has to stay glued to -- the
   *  map only lines up with the scene because the camera behaves predictably.
   *
   *  It touches no Mapbox code, so a configurator with no map can still use
   *  it; the map simply stays dormant unless MapManager.init() is called.
   *
   *  USAGE
   *    ConfiguratorStage.init({
   *      rootId, canvasId,                 // default 'cf-root' / 'cf-canvas'
   *      fov, near, far,                   // perspective camera
   *      cameraPos: [x, y, z],
   *      target:    [x, y, z],
   *      minDistance, maxDistance,
   *      maxPolarAngle,                    // > PI/2 lets the user look from below
   *      enablePan, enableDamping, dampingFactor,
   *      lights: true,                     // false to light the scene yourself
   *      log: msg => {},
   *      onResize: () => {}
   *    });
   *
   *  Every option is optional; the defaults reproduce the container setup.
   * ------------------------------------------------------------------- */
  /* core-v804: A PRODUCT'S OWN VIEW DIRECTION for the stage -- the first
     framing (o.viewDir) and the drawings' isometric (o.isoDir). fn() answers
     null (the default stays) or { x, z, eye } / { x, y, z }: the side the
     camera stands on, on plan, and either the eye's height over the ground in
     metres (the elevation worked out at the distance the model is framed
     from) or the direction's own rise; k a distance factor, rail: true the
     first framing in the canvas left of the right-hand rail. */
  function _stageViewDir(fn, center, dist) {
    const T = global.THREE;
    if (typeof fn !== 'function' || !T || !center || !(dist > 0)) return null;
    let v = null;
    try { v = fn(); } catch (eV) { v = null; }
    if (!v || !isFinite(v.x) || !isFinite(v.z)) return null;
    const h = Math.hypot(v.x, v.z);
    if (!(h > 1e-6)) return null;
    let sn = 0.45 / Math.sqrt(2 + 0.45 * 0.45);
    if (isFinite(v.eye)) sn = Math.max(-0.35, Math.min(0.9, (v.eye - center.y) / dist));
    else if (isFinite(v.y)) sn = Math.max(-0.35, Math.min(0.9, v.y / Math.hypot(h, v.y)));
    const cs = Math.sqrt(1 - sn * sn);
    const out = new T.Vector3(v.x / h * cs, sn, v.z / h * cs);
    /* { k }: the product's own distance factor over the fit (0.8 .. 1.5) */
    if (isFinite(v.k) && v.k > 0) out.k = Math.max(0.8, Math.min(1.5, v.k));
    /* { rail: true }: framed in the canvas left of the right-hand rail */
    if (v.rail) out.rail = true;
    return out;
  }

  const Stage = {
    scene: null, camera: null, renderer: null, controls: null, group: null,
    persCam: null, persControls: null, orthoCam: null, orthoControls: null,
    view: '3d',
    _gridHelper: null, _gridAxes: null,
    _opts: {},

    init(opts) {
      const o = this._opts = Object.assign({
        rootId: 'cf-root', canvasId: 'cf-canvas',
        fov: 40, near: 0.1, far: 200,
        cameraPos: [7, 4, 8], target: [0, 1.2, 0],
        minDistance: 1.5, maxDistance: 40,
        maxPolarAngle: Math.PI * 0.85,
        enablePan: false, enableDamping: true, dampingFactor: 0.08,
        lights: true,
        log: function () {},
        onResize: null
      }, opts || {});

      const T = global.THREE;
      const root = document.getElementById(o.rootId);
      const canvas = document.getElementById(o.canvasId);
      if (!T || !root || !canvas) { o.log('stage init failed: THREE/canvas/root missing'); return false; }

      this.scene = new T.Scene();

      const W = root.clientWidth || 800;
      const H = root.clientHeight || 600;

      this.camera = new T.PerspectiveCamera(o.fov, W / H, o.near, o.far);
      this.camera.position.set(o.cameraPos[0], o.cameraPos[1], o.cameraPos[2]);
      this.camera.lookAt(o.target[0], o.target[1], o.target[2]);

      this.renderer = new T.WebGLRenderer({
        canvas: canvas, antialias: true, alpha: true,
        stencil: true, preserveDrawingBuffer: true
      });
      this.renderer.setClearColor(0x000000, 0);
      this.renderer.localClippingEnabled = true;
      this.renderer.setSize(W, H, false);
      /* core-v605 (Marek: priorita je maximalni vykon i na slabych
         zarizenich, model na displeji mobilu nemusi byt top -- dulezite je AR
         a VR): a touch screen draws at 1.25 pixels per CSS pixel (it was up to
         2: on the tablet 896 x 1450 px for a 448 x 725 canvas); a mouse
         screen keeps up to 2. ?pr=N sets it by hand. The AR / VR export does
         not go through this canvas. */
      let _prCap = 2;
      try {
        const coarse = !!(global.matchMedia && global.matchMedia('(pointer: coarse)').matches);
        if (coarse) _prCap = 1.25;
        const mPr = /[?&]pr=([\d.]+)/.exec(String(global.location.search || ''));
        if (mPr) _prCap = Math.max(0.5, Math.min(3, parseFloat(mPr[1]) || _prCap));
      } catch (ePr) {}
      this._prCap = _prCap;
      this.renderer.setPixelRatio(Math.min(global.devicePixelRatio || 1, _prCap));
      this.renderer.toneMapping = T.ACESFilmicToneMapping;
      this.renderer.toneMappingExposure = 1.0;

      try {
        if (global.RoomEnvironment && T.PMREMGenerator) {
          const pm = new T.PMREMGenerator(this.renderer);
          this.scene.environment = pm.fromScene(new global.RoomEnvironment(), 0.04).texture;
          o.log('environment map ready (RoomEnvironment/PMREM)');
        } else {
          /* Not cosmetic. ACESFilmicToneMapping above expects an environment to
             lift the image; without one the whole scene reads dark and
             MeshStandardMaterial goes nearly black. Used to skip in silence,
             which cost an afternoon of looking for the fault in the model. */
          o.log('WARNING: RoomEnvironment global missing -- ACES tone mapping will render dark. Import it in the host boot module.');
        }
      } catch (envErr) { o.log('env map skipped: ' + envErr.message); }

      this.controls = new global.OrbitControls(this.camera, canvas);
      this.controls.target.set(o.target[0], o.target[1], o.target[2]);
      this.controls.enablePan = o.enablePan;
      this.controls.enableDamping = o.enableDamping;
      this.controls.dampingFactor = o.dampingFactor;
      this.controls.minDistance = o.minDistance;
      this.controls.maxDistance = o.maxDistance;
      this.controls.maxPolarAngle = o.maxPolarAngle;
      this.controls.update();

      this.persCam = this.camera;
      this.persControls = this.controls;
      this.orthoCam = null;
      this.orthoControls = null;
      this.view = '3d';
      this._ensureOrtho();

      if (o.lights) {
        this.scene.add(new T.AmbientLight(0xffffff, 0.25));
        const sun = new T.DirectionalLight(0xfff4e0, 1.1);
        sun.position.set(6, 9, 5);
        this.scene.add(sun);
        const fill = new T.DirectionalLight(0xdfe8ff, 0.35);
        fill.position.set(-5, 5, -4);
        this.scene.add(fill);
      }

      this.group = new T.Group();
      this.scene.add(this.group);

      const self = this;
      /* FRAMED UNTIL SOMEBODY MOVES THE CAMERA (core-v662, Marek: nekdy pri
         nacteni stranky neni dobre zoom -- prilis velky zoom in, levy okraj
         shed je mimo obrazovku). The model was framed ONCE, at boot, for the
         canvas as it was at that moment. A canvas that settles to another size
         afterwards (the page still laying itself out, a panel coming in, the
         tablet turned) kept the old distance with the new aspect, so a
         narrower canvas cut the building at the sides; and a model that grew
         after that one framing stood too close. Now the framing is redone on
         every resize, and when the host says the model changed, for as long as
         nobody has dragged, pinched or wheeled the 3D camera -- once they have,
         it is theirs and is left alone. The host can veto a moment (o.autoFitOk:
         the voice tour, the sample view). */
      this._camTouched = false;
      /* the last few framing events, for the snapshot's 'frame' line */
      this._frameNotes = [];
      this._frameNote = function (msg) {
        try { self._frameNotes.push(msg); if (self._frameNotes.length > 6) self._frameNotes.shift(); o.log('stage    ' + msg); } catch (eFn) {}
      };
      try {
        this.persControls.addEventListener('start', function () {
          if (self._camTouched) return;
          self._camTouched = true;
          self._frameNote('camera moved by hand -- the automatic framing stops');
        });
      } catch (eCT) {}
      /* core-v706 (Marek: model vzdy pri zmenach fit to canvas, plati pro
         vsechny configuratory): force -- after a build that changed the model's
         size, framed again even when the camera was moved by hand, from the
         angle the visitor left it at */
      this.autoFrame = function (why, force) {
        const T3 = global.THREE;
        if ((self._camTouched && !force) || self.view !== '3d' || self._preset || !(self._fitR > 0) || !self.persCam || !T3) return false;
        if (typeof o.autoFitOk === 'function') { try { if (!o.autoFitOk()) return false; } catch (eOk) { return false; } }
        let sp;
        try {
          const bx = new T3.Box3().setFromObject(self.group);
          if (bx.isEmpty() || !isFinite(bx.min.x)) return false;
          sp = bx.getBoundingSphere(new T3.Sphere());
        } catch (eBx) { return false; }
        const a = self.persCam.aspect || 1;
        const dR = Math.abs(sp.radius - self._fitR) / self._fitR;
        const dC = self._fitC ? sp.center.distanceTo(self._fitC) / self._fitR : 1;
        const dA = self._fitA ? Math.abs(a - self._fitA) / self._fitA : 1;
        if (dR < 0.03 && dC < 0.03 && dA < 0.02) return false;
        const was = 'radius ' + self._fitR.toFixed(2) + ' m, aspect ' + (self._fitA || 0).toFixed(2);
        const keep = !!self._camTouched;
        try { self.fitCameraToFootprint(keep); } catch (eFt) { return false; }
        self._frameNote('framed again -- ' + (why || '?') + '; ' + (keep ? 'from the visitor\'s own angle' : 'nobody had moved the camera') + ' (' + was +
                         ' -> radius ' + sp.radius.toFixed(2) + ' m, aspect ' + a.toFixed(2) + ')');
        return true;
      };
      /* WHAT A FRAME COSTS (core-v589, Marek: na zoom in / out uz pudorys
         reaguje jen s velkymi obtizemi). Each frame keeps how long since the
         last one (dt -- the main thread's rhythm) and how long the render call
         itself took (cpu); frames while a finger or the mouse is busy on the
         canvas go into their own list, so the snapshot can say what a pinch
         costs apart from a model standing still. Nothing here changes what is
         drawn. */
      const PF = this._perf = { idle: [], busy: [], last: 0, busyUntil: 0, calls: 0, tris: 0 };
      const _pfNow = function () { try { return global.performance.now(); } catch (e) { return Date.now(); } };
      const _pfBusy = function () { PF.busyUntil = _pfNow() + 400; };
      try {
        canvas.addEventListener('pointerdown', _pfBusy, { passive: true, capture: true });
        canvas.addEventListener('pointermove', function (e) { if (e.buttons || e.pointerType === 'touch') _pfBusy(); }, { passive: true, capture: true });
        canvas.addEventListener('wheel', _pfBusy, { passive: true, capture: true });
        canvas.addEventListener('touchmove', _pfBusy, { passive: true, capture: true });
      } catch (ePf) {}
      /* A FRAME ONLY WHEN SOMETHING CHANGED (core-v603, Marek: co v
         konfiguratorech bere hodne vykonu -- D1 #117 / #118, the bench). The
         loop drew the whole model sixty times a second standing still: on the
         RANCH 14 000 draw calls, 200 ms a frame, the tablet frozen while the
         house only stood there. A frame is now drawn when the camera moved
         (its matrix, its zoom, another camera), the controls are still
         settling, anything on the page asked for an animation frame (every
         animation here -- explode, the assembly fly, the near bin, a drag --
         runs on one), a finger, the mouse, the wheel or a key was busy within
         the last two seconds, the stage was woken (a build), and every two
         seconds whatever else happens. Standing still the model costs nothing;
         ?render=always brings back the old loop. */
      const RAF0 = (global.requestAnimationFrame ? global.requestAnimationFrame.bind(global) : function (fn) { return global.setTimeout(function () { fn(_pfNow()); }, 16); });
      if (!global.__levaRafWrapped && global.requestAnimationFrame) {
        global.__levaRafWrapped = true;
        global.__levaRafExt = 0;
        const RAFW = global.requestAnimationFrame;
        global.__levaRaf0 = RAFW;
        /* core-v604: every 20th asker is sampled by where it was called from,
           so the perf lines can say who keeps the stage awake */
        global.__levaRafWho = {};
        /* core-v605: ONLY OUR OWN ANIMATIONS WAKE THE STAGE. The theme's
           animations.min.js and Shopify's pixel manager ask for a frame every
           frame for ever (D1 #121 / #122), so 'someone asked for a frame' woke
           the model all the time. The caller is read off the stack: a
           configurator-*.js asking (the assembly fly, a product's own
           animation) wakes it; anything else does not. */
        global.__levaRafOurs = 0;
        global.requestAnimationFrame = function (cb) {
          const n = global.__levaRafExt = (global.__levaRafExt | 0) + 1;
          try {
            const L = String(new Error().stack || '').split('\n');
            const c = String(L[2] || L[1] || '');
            if (/configurator-[\w-]+\.js/.test(c)) global.__levaRafOurs = (global.__levaRafOurs | 0) + 1;
            if (n % 20 === 0) {
              const w = c.replace(/^\s*at\s+/, '').replace(/\(?https?:\/\/[^)\s]*\/([^\/?)\s]+)(\?[^:)\s]*)?/, '$1').replace(/\)$/, '').slice(0, 90);
              global.__levaRafWho[w] = (global.__levaRafWho[w] || 0) + 1;
            }
          } catch (eW) {}
          return RAFW.call(global, cb);
        };
      }
      const RD = this._rd = { until: _pfNow() + 3000, last: 0, sig: '', ext: -1, drawn: 0, skipped: 0, always: false };
      try { RD.always = /[?&]render=always(&|$)/.test(String(global.location.search || '')); } catch (eRA) {}
      this.wake = function (ms) { const u = _pfNow() + (ms || 1500); if (u > RD.until) RD.until = u; };
      const _wakeIn = function () { self.wake(2000); };
      try {
        ['pointerdown', 'pointermove', 'pointerup', 'wheel', 'touchstart', 'touchmove', 'touchend', 'keydown', 'input', 'change'].forEach(function (ev) {
          global.document.addEventListener(ev, _wakeIn, { passive: true, capture: true });
        });
      } catch (eWk) {}
      try { this.controls.addEventListener('change', function () { self.wake(600); }); } catch (eCh) {}
      const _camSig = function () {
        const c = self.camera;
        if (!c) return '';
        const p = c.position, q = c.quaternion, m = c.projectionMatrix.elements;
        return c.uuid + ':' + p.x.toFixed(5) + ',' + p.y.toFixed(5) + ',' + p.z.toFixed(5) + ':' + q.x.toFixed(6) + ',' + q.y.toFixed(6) + ',' + q.z.toFixed(6) + ',' + q.w.toFixed(6) +
               ':' + (c.zoom || 1).toFixed(5) + ':' + m[0].toFixed(6) + ',' + m[5].toFixed(6) + ',' + m[10].toFixed(6) + ',' + m[14].toFixed(6);
      };
      const loop = function () {
        RAF0(loop);
        const t0 = _pfNow();
        /* core-v605: the controls answer 'moved' on the tablet every frame
           (D1 #121 / #122: 'controls 312') -- a float that never settles; the
           camera's own signature (1e-5) decides, the answer is only counted */
        let moved = false;
        try { moved = !!self.controls.update(); } catch (eU) { moved = false; }
        const ext = global.__levaRafOurs | 0, extNew = (ext !== RD.ext);
        RD.ext = ext;
        const sig = _camSig();
        const camMoved = (sig !== RD.sig);
        const go = RD.always || camMoved || extNew || t0 < RD.until || (t0 - RD.last) > 2000;
        const W = RD.why || (RD.why = { always: 0, controls: 0, frame: 0, input: 0, camera: 0, beat: 0, noise: 0 });
        if (moved && !camMoved) W.noise++;
        if (go) {
          if (RD.always) W.always++; else if (camMoved) { if (moved) W.controls++; else W.camera++; } else if (extNew) W.frame++; else if (t0 < RD.until) W.input++; else W.beat++;
        }
        let cpu = 0;
        if (go) {
          self.renderer.setClearColor(0x000000, 0);
          self.renderer.render(self.scene, self.camera);
          RD.last = t0; RD.sig = _camSig(); RD.drawn++;
          cpu = _pfNow() - t0;
          try { PF.calls = self.renderer.info.render.calls; PF.tris = self.renderer.info.render.triangles; } catch (eI) {}
        } else RD.skipped++;
        if (PF.last) {
          const L = (t0 < PF.busyUntil) ? PF.busy : PF.idle;
          L.push([t0 - PF.last, cpu, go ? 1 : 0]);
          if (L.length > 240) L.shift();
        }
        PF.last = t0;
      };
      RAF0(loop);

      function onResize() {
        const w = root.clientWidth, h = root.clientHeight;
        self.renderer.setSize(w, h, false);
        if (self.persCam) { self.persCam.aspect = w / h; self.persCam.updateProjectionMatrix(); }
        if (self.view === '2d') self.setView('2d');
        if (self._preset) { try { self.viewPreset(self._preset); } catch (eP) {} }
        if (typeof o.onResize === 'function') o.onResize();
        /* core-v662: the new size framed again, once it has stopped changing */
        try {
          if (self._afT) global.clearTimeout(self._afT);
          self._afT = global.setTimeout(function () { self._afT = 0; self.autoFrame('the canvas is ' + w + 'x' + h + ' now'); }, 160);
        } catch (eAf) {}
      }
      global.addEventListener('resize', onResize);
      if (global.ResizeObserver) new ResizeObserver(onResize).observe(root);

      o.log('stage ready (' + W + 'x' + H + ')');
      return true;
    },

    fitCameraToFootprint(keepDir) {
      const T = global.THREE;
      if (!this.persCam || !this.group) return;
      let box;
      try { box = new T.Box3().setFromObject(this.group); }
      catch (eB) { _log('fitCameraToFootprint: cannot measure the group'); return; }
      if (box.isEmpty() || !isFinite(box.min.x)) return;
      const sphere = box.getBoundingSphere(new T.Sphere());
      const r = sphere.radius;
      if (!(r > 0)) return;
      const cam = this.persCam;
      const vFov = cam.fov * Math.PI / 180;
      const aspect = cam.aspect || 1.4;
      const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect);
      /* THE WIDTH IS ASKED ON ITS OWN (core-v662, Marek: levy okraj shed je mimo
         obrazovku). One distance came from the narrower of the two angles with
         0.85 of the sphere -- right for the height, which is well inside the
         sphere, but a long building seen corner-on is nearly the whole sphere
         ACROSS, and its near end looks bigger still. On a canvas not much wider
         than tall (and on any portrait one) the ends went off the sides. So the
         height keeps its 0.85 and the width gets 0.95 of the sphere, and the
         camera stands at the further of the two. Wider than about 8:7 nothing
         changes -- the height decides there as before. */
      const distV = (r / Math.sin(vFov / 2)) * 0.85;
      const distH = (r / Math.sin(hFov / 2)) * 0.95;
      /* core-v735: a page's own distance factor (the homepage hero's frame) */
      const kFit = (+global.LEVA_FIT_K > 0) ? Math.min(3, Math.max(0.5, +global.LEVA_FIT_K)) : 1;
      let dist = Math.max(distV, distH) * kFit;
      let dir = new T.Vector3(1, 0.45, 1).normalize();
      /* core-v804: A PRODUCT'S OWN FIRST VIEW (RUSSWORX SHED, Marek: the main
         canvas as the picture Nick promotes). o.viewDir() -> null, or
         { x, z, eye }: the side the camera stands on (on plan) and its height
         over the ground in metres -- the elevation is worked out at the
         distance that fits. Nothing else changes; a turned camera keeps its own. */
      const _vdir = _stageViewDir(this._opts && this._opts.viewDir, sphere.center, dist);
      /* { rail: true }: framed in the canvas LEFT OF THE RIGHT-HAND RAIL (the
         icon column lies over the canvas; on a phone it hid the model's right
         end) -- fitted to that width, the rig moved sideways so the model's
         middle is the middle of what is free; orbiting still turns round it */
      let railShift = 0, railTxt = '';
      if (_vdir) {
        dir = _vdir;
        if (_vdir.rail) {
          try {
            const cvR = this.renderer.domElement, rlR = global.document.querySelector('#cf-root .cf-sidebar');
            const cR = cvR.getBoundingClientRect(), aR = rlR ? rlR.getBoundingClientRect() : null;
            if (aR && aR.width > 0 && cR.width > 0 && cR.height > 0 && aR.left > cR.left + cR.width * 0.5 && aR.left < cR.right) {
              const freeW = Math.max(cR.width * 0.5, aR.left - cR.left - 8);
              const hFovF = 2 * Math.atan(Math.tan(vFov / 2) * (freeW / cR.height));
              dist = Math.max(dist, (r / Math.sin(hFovF / 2)) * 0.95 * kFit);
              railShift = (cR.width - freeW) / 2 / cR.width;
              railTxt = ', in the ' + Math.round(freeW) + ' px left of the rail';
            }
          } catch (eRl) { railShift = 0; }
        }
        if (_vdir.k) dist *= _vdir.k;
      }
      /* core-v706: framed again after the visitor turned it -- from where they look */
      if (keepDir && this.persControls && this.persControls.target) {
        const d0 = cam.position.clone().sub(this.persControls.target);
        if (d0.lengthSq() > 1e-9) dir = d0.normalize();
      }
      const aim = sphere.center.clone();
      if (railShift > 0) {
        const fwd = dir.clone().negate(), right = new T.Vector3().crossVectors(fwd, new T.Vector3(0, 1, 0));
        if (right.lengthSq() > 1e-9) aim.add(right.normalize().multiplyScalar(railShift * 2 * dist * Math.tan(hFov / 2)));
      }
      cam.position.copy(aim.clone().add(dir.multiplyScalar(dist)));
      cam.near = Math.max(0.05, dist / 200);
      cam.far = dist * 6;
      cam.updateProjectionMatrix();
      if (this.persControls) {
        this.persControls.target.copy(aim);
        this.persControls.minDistance = dist * 0.3;
        this.persControls.maxDistance = dist * 3;
        this.persControls.update();
      } else {
        cam.lookAt(aim);
      }
      /* core-v662: what was framed, so autoFrame can tell whether it changed */
      this._fitR = r; this._fitC = sphere.center.clone(); this._fitA = aspect;
      try {
        const cv = this.renderer && this.renderer.domElement;
        if (this._frameNote) this._frameNote('framed: radius ' + r.toFixed(2) + ' m, canvas ' +
          (cv ? (cv.clientWidth + 'x' + cv.clientHeight) : '?') + ' (aspect ' + aspect.toFixed(2) + '), camera ' +
          dist.toFixed(2) + ' m out, set by the ' + (distH > distV ? 'width' : 'height') + railTxt);
      } catch (eFl) {}
    },

    /* The 2D camera is deliberately immobile: the model stays centred and the
       map underneath it cannot drift. enabled is pinned to false through a
       non-configurable property so nothing can switch it back on by accident. */
    _ensureOrtho() {
      const T = global.THREE;
      if (this.orthoCam || !this.renderer || !T || !global.OrbitControls) return;
      const canvas = this.renderer.domElement;
      this.orthoCam = new T.OrthographicCamera(-1, 1, 1, -1, 0.01, 2000);
      this.orthoCam.up.set(0, 0, -1);
      this.orthoCam.position.set(0, 40, 0);
      this.orthoCam.lookAt(0, 0, 0);
      this.orthoControls = new global.OrbitControls(this.orthoCam, canvas);
      this.orthoControls.enableRotate = false;
      this.orthoControls.enableDamping = true;
      this.orthoControls.dampingFactor = 0.1;
      this.orthoControls.screenSpacePanning = true;
      this.orthoControls.mouseButtons = { LEFT: T.MOUSE.PAN, MIDDLE: T.MOUSE.DOLLY, RIGHT: T.MOUSE.PAN };
      this.orthoControls.touches = { ONE: T.TOUCH.PAN, TWO: T.TOUCH.DOLLY_PAN };

      try {
        Object.defineProperty(this.orthoControls, 'enabled', {
          get: function () { return false; },
          set: function () {},
          configurable: false
        });
      } catch (eOD) { this.orthoControls.enabled = false; }
    },

    setView(mode, focus) {
      const T = global.THREE;
      if (!this.renderer || !T) return;
      const canvas = this.renderer.domElement;
      /* This box used to be measured here, unconditionally, on the line before
         the branch -- and the 3d branch never reads it. It measured the whole
         model and threw the answer away.

         Which cost nothing until setFromObject started throwing, and then it
         cost everything: expandByObject recurses over the graph, and on a graph
         it cannot finish it raises RangeError right here, three lines before
         "this.camera = this.persCam". So the camera never swapped, this.view
         never updated, fitCameraToFootprint never ran -- the plan stayed on
         screen through every step after the plan, the model stayed framed too
         close, and the only thing that DID switch was whatever the host had
         already decided for itself.

         A view switch is a camera swap. It must not depend on being able to
         measure anything: measuring is for framing, and framing is allowed to
         fail on its own. */
      let ctr = null, size = null;
      if (focus) {
        ctr = new T.Vector3(focus.cx, focus.cy || 1.2, focus.cz);
        size = new T.Vector3(focus.spanX, 3, focus.spanZ);
      } else if (mode === '2d') {
        try {
          const box = new T.Box3().setFromObject(this.group);
          ctr = box.getCenter(new T.Vector3());
          size = box.getSize(new T.Vector3());
        } catch (eB) {
          _log('setView: cannot measure the group (' + (eB && eB.message ? eB.message : eB) + ')');
        }
        if (!ctr || !isFinite(ctr.x) || !size || !isFinite(size.x)) { ctr = new T.Vector3(0, 1.2, 0); size = new T.Vector3(8, 3, 8); }
      }
      if (mode === '2d') {
        this._ensureOrtho();
        const aspect = (canvas.clientWidth || 1) / (canvas.clientHeight || 1);

        const reachX = Math.abs(ctr.x) + size.x / 2;
        const reachZ = Math.abs(ctr.z) + size.z / 2;
        const span = Math.max(reachX, reachZ) * 1.15 + 0.6;
        this.orthoCam.left = -span * aspect; this.orthoCam.right = span * aspect;
        this.orthoCam.top = span; this.orthoCam.bottom = -span;
        const up = Math.max(size.x, size.z) + 10;
        this.orthoCam.near = 0.01; this.orthoCam.far = up * 4;
        this.orthoCam.zoom = 1;
        this.orthoCam.position.set(0, up, 0.0001);
        this.orthoCam.up.set(0, 0, -1);
        this.orthoCam.lookAt(0, 0, 0);
        this.orthoCam.updateProjectionMatrix();
        this.orthoControls.target.set(0, 0, 0);
        this.orthoControls.update();
        this.persControls.enabled = false;
        this.orthoControls.enabled = false;
        this.camera = this.orthoCam;
        this.controls = this.orthoControls;
      } else {
        if (this.orthoControls) this.orthoControls.enabled = false;
        this.persControls.enabled = true;
        this.camera = this.persCam;
        this.controls = this.persControls;
        /* the swap above is done and committed before this is allowed to fail:
           a camera you can see through beats a camera framed perfectly */
        try { this.fitCameraToFootprint(); }
        catch (eF) { _log('setView: fit failed (' + (eF && eF.message ? eF.message : eF) + ')'); }
      }
      this.view = mode;
    },

    /* A DRAWING VIEW: an orthographic camera framing the model from a set
       direction -- front, back, left, right, top, or an isometric -- with the
       controls off, for the blueprints page (ui-v114). 'free' hands the stage
       back to the perspective camera. The frustum follows the model's own
       box, padded a little; a resize keeps the framing. */
    viewPreset(name) {
      const T = global.THREE;
      if (!T || !this.renderer) return;
      if (name === 'free' || !name) {
        this._preset = null;
        if (this.orthoControls) this.orthoControls.enabled = false;
        this.persControls.enabled = true;
        this.camera = this.persCam; this.controls = this.persControls;
        try { this.fitCameraToFootprint(); } catch (e) {}
        this.view = '3d';
        return;
      }
      const box = new T.Box3().setFromObject(this.group);
      if (!isFinite(box.min.x)) return;
      const size = new T.Vector3().subVectors(box.max, box.min);
      const mid = new T.Vector3().addVectors(box.min, box.max).multiplyScalar(0.5);
      /* THE ISOMETRIC IS A PERSPECTIVE: a true axonometric reads as bent to
         an eye used to photographs (Marek). The perspective camera stands on
         the 45 / 30 degree line at the distance that fits the model, the
         controls off so it stays there. */
      if (name === 'iso' || name === 'isoBack') {
        const sgn = (name === 'iso') ? 1 : -1;
        let dir = new T.Vector3(sgn * 1, 0.7, sgn * -1).normalize();
        const cam = this.persCam;
        const fov = (cam.fov || 45) * Math.PI / 180;
        const radius = size.length() / 2;
        const dist = radius / Math.sin(fov / 2) * 1.05;
        /* core-v804: the drawings' first isometric from the product's own
           first view (o.isoDir, RUSSWORX SHED: the promo picture's side and
           eye height); isoBack and every other product as before */
        if (name === 'iso') { const _idir = _stageViewDir(this._opts && this._opts.isoDir, mid, dist); if (_idir) dir = _idir; }
        cam.position.copy(mid).addScaledVector(dir, dist);
        cam.up.set(0, 1, 0);
        cam.lookAt(mid);
        cam.updateProjectionMatrix();
        if (this.orthoControls) this.orthoControls.enabled = false;
        this.persControls.enabled = false;
        this.persControls.target.copy(mid);
        this.camera = cam;
        this._preset = name;
        this.view = '3d';
        return;
      }
      const dirs = {
        front: [0, 0, -1], back: [0, 0, 1], left: [-1, 0, 0], right: [1, 0, 0], top: [0, 1, 0],
        iso: [1, 0.85, -1], isoBack: [-1, 0.85, 1]
      };
      const d = new T.Vector3().fromArray(dirs[name] || dirs.front).normalize();
      if (!this.presetCam) this.presetCam = new T.OrthographicCamera(-1, 1, 1, -1, 0.01, 4000);
      const cam = this.presetCam;
      const dist = Math.max(size.x, size.y, size.z) * 4 + 20;
      cam.position.copy(mid).addScaledVector(d, dist);
      cam.up.set(0, 1, 0);
      if (name === 'top') { cam.up.set(0, 0, -1); cam.position.set(mid.x, mid.y + dist, mid.z + 0.0001); }
      cam.lookAt(mid);
      cam.updateMatrixWorld(true);
      /* the extent across the view: project the box corners into the camera frame */
      const inv = new T.Matrix4().copy(cam.matrixWorld).invert();
      let minX = 1e9, maxX = -1e9, minY = 1e9, maxY = -1e9;
      for (let i = 0; i < 8; i++) {
        const c = new T.Vector3(i & 1 ? box.max.x : box.min.x, i & 2 ? box.max.y : box.min.y, i & 4 ? box.max.z : box.min.z).applyMatrix4(inv);
        minX = Math.min(minX, c.x); maxX = Math.max(maxX, c.x); minY = Math.min(minY, c.y); maxY = Math.max(maxY, c.y);
      }
      const root = this.renderer.domElement.parentElement || this.renderer.domElement;
      const W = root.clientWidth || 800, H = root.clientHeight || 600, aspect = W / H;
      const pad = 1.18, halfW = (maxX - minX) / 2 * pad, halfH = (maxY - minY) / 2 * pad;
      const spanX = Math.max(halfW, halfH * aspect), spanY = spanX / aspect;
      const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
      cam.left = cx - spanX; cam.right = cx + spanX; cam.top = cy + spanY; cam.bottom = cy - spanY;
      cam.near = 0.01; cam.far = dist * 3;
      cam.zoom = 1;
      cam.updateProjectionMatrix();
      if (this.orthoControls) this.orthoControls.enabled = false;
      this.persControls.enabled = false;
      this.camera = cam;
      this._preset = name;
      this.view = '3d';
    },

    /* Ground grid + centre axes. Call again when the unit system flips. */
    rebuildGrid(opts) {
      const T = global.THREE;
      if (!T || !this.scene) return;
      const o = Object.assign({
        size: 25, step: 1.0,
        color1: 0xb9bfc6, color2: 0xe5e5e5,
        axes: true, axisColor: 0x8a93a0
      }, opts || {});

      if (this._gridHelper) {
        this.scene.remove(this._gridHelper);
        if (this._gridHelper.geometry) this._gridHelper.geometry.dispose();
        if (this._gridHelper.material) this._gridHelper.material.dispose();
        this._gridHelper = null;
      }
      const divisions = Math.round(o.size / o.step);
      this._gridHelper = new T.GridHelper(o.size, divisions, o.color1, o.color2);
      this._gridHelper.position.y = 0.005;
      this._gridHelper.rotation.set(0, 0, 0);
      this.scene.add(this._gridHelper);
      /* core-v735: a page without the ground grid (the homepage hero's frame) */
      if (global.LEVA_NO_GRID) this._gridHelper.visible = false;

      if (this._gridAxes) {
        this.scene.remove(this._gridAxes);
        if (this._gridAxes.geometry) this._gridAxes.geometry.dispose();
        if (this._gridAxes.material) this._gridAxes.material.dispose();
        this._gridAxes = null;
      }
      if (!o.axes || global.LEVA_NO_GRID) return;
      const ax = new T.BufferGeometry();
      ax.setAttribute('position', new T.Float32BufferAttribute([
        -o.size / 2, 0.008, 0, o.size / 2, 0.008, 0,
        0, 0.008, -o.size / 2, 0, 0.008, o.size / 2
      ], 3));
      this._gridAxes = new T.LineSegments(ax, new T.LineBasicMaterial({ color: o.axisColor }));
      this.scene.add(this._gridAxes);
    }
  };

  global.ConfiguratorStage = Stage;
  global.MapManager = MM;
}(typeof window !== 'undefined' ? window : globalThis));

/* =============================================================================
   CONFIGURATOR CORE -- the house line's shared engine.

   This is the CORE BLOCK that used to be copied into every product lib. It is
   read once, by every product, and createCore() gives each of them its own
   closure: its own STATE, its own layers, its own stage. Nothing is shared
   between two products at run time except the code itself.

   THE BOUNDARY RUNS BOTH WAYS AND BOTH ARE EXPLICIT. The product reads the
   core through C, handed to its build(); the core reads the product through P,
   which the product fills at the end of that build. Twelve names go up, about
   a hundred come down, and nothing crosses by scope.
   ============================================================================= */
(function (global) {

  /* Shared interaction controller. Product adapters own geometry and saved
     state; both HouseCore and modular products use this view/tool/strip path. */
  function createControls(adapter) {
    function root() { return adapter.root ? adapter.root() : global.document.getElementById('cf-root'); }
    function syncMap(animate) {
      var map = adapter.map && adapter.map();
      if (!map || !map.map || !map.center) return;
      var plan = adapter.view() === '2d';
      try {
        map._set3DLayerVisibility(!plan);
        if (plan) map._syncMapTo2D(!!animate); else map._syncMapTo3D(!!animate);
      } catch (e) { if (adapter.log) adapter.log('map view sync: ' + (e.message || e)); }
    }
    function view(mode, focus) {
      mode = mode === '2d' ? '2d' : '3d';
      var stage = adapter.stage(), r = root();
      if (stage) stage.setView(mode, focus);
      if (r) { r.classList.toggle('cf-view-2d', mode === '2d'); r.classList.toggle('cf-view-3d', mode === '3d'); }
      syncMap(false);
    }
    function frame(open, plan) {
      var r = root(); if (!r) return;
      r.classList.toggle('cf-sx-open', !!open);
      r.classList.toggle('cf-sx-plan', !!open && !!plan);
    }
    function strip(close, scroll, label, svg) {
      var d = global.document, r = root();
      var el = d.createElement('nav'), x = d.createElement('button'), list = d.createElement('div');
      el.id = 'cf-sx-strip'; el.setAttribute('data-cf-nopinch', '1');
      x.type = 'button'; x.className = 'cf-sx-x'; x.innerHTML = svg;
      x.setAttribute('aria-label', label);
      x.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); close(); });
      list.className = 'cf-sx-list'; list.addEventListener('scroll', scroll);
      el.appendChild(x); el.appendChild(list); r.appendChild(el);
      var tip = d.getElementById('cf-sx-tip');
      if (!tip) { tip = d.createElement('div'); tip.id = 'cf-sx-tip'; r.appendChild(tip); }
      return { el: el, list: list, tip: tip };
    }
    function tools(list, items) {
      // The event resolves the CURRENT item; repainting state never leaves a
      // handler pointing at a plan/storey that has since been replaced.
      list.__items = items;
      var sig = items.map(function (i) { return i.key + ':' + !!i.separator; }).join('|');
      function current(key) { return (list.__items || []).filter(function (i) { return i.key === key; })[0]; }
      if (list.__sig !== sig) {
        list.__sig = sig; list.innerHTML = '';
        items.forEach(function (i) {
          var b = global.document.createElement('button'); b.type = 'button';
          b.setAttribute('data-k', i.key);
          b.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); var it = current(i.key); if (it && it.tap) it.tap(e, b); });
          b.addEventListener('pointerdown', function (e) { var it = current(i.key); if (it && it.down) it.down(e, b); });
          b.addEventListener('pointerenter', function (e) { var it = current(i.key); if (e.pointerType === 'mouse' && it && it.enter) it.enter(b); });
          b.addEventListener('pointerleave', function (e) { var it = current(i.key); if (e.pointerType === 'mouse' && it && it.leave) it.leave(); });
          list.appendChild(b);
          if (i.separator) { var sep = global.document.createElement('span'); sep.className = 'cf-sx-sep'; list.appendChild(sep); }
        });
      }
      var buttons = list.querySelectorAll('button[data-k]');
      for (var n = 0; n < buttons.length; n++) {
        var b = buttons[n], it = current(b.getAttribute('data-k'));
        if (!it) continue;
        b.classList.add('cf-sx-b');
        b.classList.toggle('cf-sx-tg', !!it.toggle); b.classList.toggle('cf-on', !!it.on);
        b.classList.toggle('cf-sel', !!it.selected); b.classList.toggle('cf-dim', !!it.dim);
        b.setAttribute('aria-label', it.label || ''); b.setAttribute('aria-pressed', String(!!it.pressed));
        b.style.touchAction = it.down ? 'none' : '';
        if (b.__html !== it.html) { b.innerHTML = it.html || ''; b.__html = it.html; }
      }
    }
    function endTools(items, except, active, stop) {
      items.forEach(function (it) { if (it && it.key !== except && active(it)) stop(it); });
    }
    return { view: view, syncMap: syncMap, frame: frame, strip: strip, tools: tools, endTools: endTools };
  }

  function createCore(build) {
    /* ==========================================================================
       ===== CORE BLOCK core-v225 =================================================

       Shared by every house product. Copy whole, edit in one place, never fork.
       Nothing below this line until END CORE BLOCK may name a product.

       THE BLOCK EXPECTS TWO NAMES DECLARED ABOVE IT, and nothing else:
           VERSION   the product's own version string, e.g. '<product>-v1'
           LOG_TAG   the product's log prefix, e.g. '<product>'
       A lib that copies the block without declaring both will throw on its first
       log line. That is deliberate -- a loud failure on paste beats a quiet one
       three versions later.
       ========================================================================== */

    var PRODUCT = '';
    var _controls = createControls({ stage: function () { return _stage(); },
      map: function () { return _MM; }, view: function () { return STATE.view; }, log: _log });
    var DEFAULT_PROJECT = '';
    var LOG_TAG = '';

    /* core-v73 (Sep 2026, ADU): STATE.product bag saved whole in payload();
       the project travels -- #c= deflated hash, ?p= short id on leva-share,
       _ProjectStore bridge; hideInPlan meshes hidden in the plan; the drawer
       re-centres the model on open; the ortho camera is squared on every move
       and the plan icons follow it; AR export clips by the renderer's section
       planes; fixtures (fitout pieces) -- drag, bar with plus/rotate/mirror,
       red plus per room, confine when walls move; room rename only on the
       name; bearing supports at equal spacing; isNew() takes a list. */
    /* core-v658: THE EXPLODED VIEW REBUILT (Marek 2026-10-12: rozklad od teziste
       podlahy, steny do stran, strop a vys nahoru, nic se nema prolinat) --
       the block above setExplode says how; the T house's outer masonry one
       mesh per wall (_masByWall). core-v657 was the two-tier PDF. */
    /* core-v667 (GARAGE APARTMENT thread): the roof skin's edges -- a mono's
       high eave gets the fascia the low eave has, opt-in by the product's
       spec.edges.fascia.top; the fascia's nails into the rafter tails, opt-in
       by spec.edges.fascia.nail (+ nailStep, nailPer). core-v666 was the
       shingle course cut on the valley / hip line (RANCH thread). */
    /* core-v668 (voice tour thread, Marek: spanelstina nahrana): the tour
       speaks Spanish on a Spanish page -- the -es clips first, the English
       clip if one is missing; the command step types the Spanish sentence.
       core-v667 was the mono's high eave fascia (GARAGE APARTMENT). */
    /* core-v670 (voice tour thread, Marek: Roger, nahrej ted): an English page
       speaks the clips the leva-voice worker serves (ElevenLabs, voice Roger,
       made by the leva-workers voice workflow), Shopify's clips as the
       fallback. core-v669 was the RANCH thread's audit tag. */
    /* core-v671 (voice tour thread, Marek: dodelej spanelstinu): a Spanish
       page speaks the worker's es clips too (Roger, all 73), the Shopify -es
       clips as the fallback. */
    /* core-v672 (voice tour thread, Marek: de-theo je nejlepsi): the tour on a
       German page too -- the worker's de clips (voice Theo Teacher, all 73),
       English as the fallback; the command step types the German sentence. */
    /* core-v673 (voice tour thread, Marek: Thierry nejlepsi, nasad fr): the
       tour on a French page -- the worker's fr clips (voice Thierry, all 73). */
    /* core-v674 (voice tour thread, Marek: udelej cestinu): the tour on a
       Czech page -- the worker's cs clips (voice Zazy, all 73). */
    /* core-v675 (voice tour thread, Marek: nahrat vzdy jiny floorplan nez je
       na webu): the library step takes a plan unlike the house on screen
       (_tourLibTile; tiles carry data-plib). */
    /* core-v676 (voice tour thread, Marek: Paulo, prosim na web): the tour on
       a Portuguese page (pt-PT) -- the worker's pt clips (voice Paulo, all
       73); the command step types the Portuguese sentence. */
    /* core-v678 (voice tour thread, Marek: zh-adrian): the tour on a Chinese
       page (zh-CN) -- the worker's zh clips (voice Adrian, Mandarin, all 73);
       the command step types the Chinese sentence. core-v677 was the RANCH
       thread's square-section audit. */
    /* core-v679 (voice tour thread, Marek: ar-abdullah): the tour on an
       Arabic page (ar, modern standard) -- the worker's ar clips (voice
       Abdullah - Dramatic Bass, all 73); the command step types the Arabic
       sentence. */
    /* core-v680 (voice tour thread, Marek: Kaito): the tour on a Japanese
       page (ja) -- the worker's ja clips (voice Kaito, all 73); the command
       step types the Japanese sentence. */
    /* core-v681 (voice tour thread, Marek: po zmacknuti play byva nekdy
       dlouho ticho): the clips are fetched ahead -- the first step's a few
       seconds after the button shows, the next two while a step plays --
       and played from memory (_tourPre). The log says when the first sound
       came and whether its clip was there ahead. */
    /* core-v682 (RANCH thread, opt-in __PROD._qtyFull): the qty audit
       measures what is under grade whole -- a body _box / buildStripSlab
       cut at grade keeps its whole box (userData.qtyBox), one left out
       under grade is held on its layer group (_qtyHeld) with the item
       current then; buildStripSlab's opt-in o.marks { strip, slab, bed }
       tags each part as billed. Nothing drawn or billed changes. */
    /* core-v683 (voice tour thread, Marek: tr-emrullah-tuna): the tour on a
       Turkish page (tr) -- the worker's tr clips (voice Emrullah Tuna, all
       73); the command step types the Turkish sentence. */
    /* core-v684 (RANCH thread, opt-in buildStripSlab o.marks.dig): the
       strips' trenches and the slab's dig drawn as billed (translucent
       earth, under grade: the base step; held for the qty audit). */
    /* core-v685 (voice tour thread, Marek: tlacitko play na zacatku vice
       zduraznit -- vetsi puls, zoom in, pak pulsovat jako ted): the PLAY
       button's entrance (cf-intro) -- zoomed in from nothing, then three big
       pulses with a red ring, then the soft fade it had (cf-idle). Played
       once the button is on screen. */
    /* core-v686 (voice tour thread, Marek: ko-sani): the tour on a Korean
       page (ko) -- the worker's ko clips (voice Sani, all 73); the command
       step types the Korean sentence. */
    /* core-v687 (RANCH thread, opt-in __PROD._qtyPieces): the qty audit
       counts what is billed by the piece -- a body's qtyPiece, else touching
       bodies are one piece, baked qtyN, held under grade one each; qtyCode
       (billed as another code) and qtyWith (what comes with it) -- and
       prints what agrees, differs, is drawn not billed, billed none drawn. */
    /* core-v688 (voice tour thread, Marek: nedelat puls do cervene barvy,
       na zacatku play tlacitko zvetsit vic nez pak pulsuje -- prvni
       zobrazeni 200 % jeho finalniho zobrazeni): the entrance is one move --
       the button shows at twice its size, holds a moment and settles to its
       own size, then the soft fade as before; no red ring, no big pulses. */
    /* core-v689 (voice tour thread, Marek: it-antonio-2): the tour on an
       Italian page (it) -- the worker's it clips (voice Antonio - Grumpy
       Grandpa, all 73); the command step types the Italian sentence. The
       container lib's copy of the tour lists the language too. */
    /* core-v691 (voice tour thread, Marek: slo by to spustit i na pozadi,
       pokud odejdu z okna?): with the page in the background the browser
       draws no frames, so a step whose show waits on frames (the intro's
       turn) never called back and the tour stood still. Now a hidden page
       skips the shows and each step ends when its voice ends; back on screen
       the shows run again from the step it is on. The phone's media controls
       (lock screen, notification) pause and play it (navigator.mediaSession).
       core-v690 was the scroll lane. */
    /* core-v692 (voice tour thread, Marek: pl-peter): the tour on a Polish
       page (pl) -- the worker's pl clips (voice Peter, all 73); the command
       step types the Polish sentence. The container's copy lists pl too. */
    /* core-v694 (voice tour thread, Marek: nechat bezet na pozadi, mit v
       scroll mobil oznaceni banner s ovladanim prehravani jako Spotify,
       klikem se vratim na stranku): the media session carries the product's
       picture and name and the step's label; next and previous move a step
       (previous: the last one shown); the panel says paused when the tour
       stops. A tap on the panel brings the page back (the browser's own). */
    /* core-v695 (voice tour thread, Marek: tim ze jsou to samostatne stopy,
       tak se to vzdy po dojeti stopy zastavi a ze scroll oznameni to zmizi):
       the tour plays ONE stream -- a MediaSource the clips are appended to --
       so the player never ends between two steps, the panel in the
       notifications stays and a page in the background keeps running; with
       the page hidden a 0.5 s silence fills the gaps. See _TS / _tourStream*.
       A browser without MediaSource for MP3 plays one clip at a time. */
    /* core-v696 (voice tour thread, Marek: ro-liviu-mihai): the tour on a
       Romanian page (ro) -- the worker's ro clips (voice Liviu Mihai, all
       73); the command step types the Romanian sentence. */
    /* core-v697 (voice tour thread, Marek: kdyz odejdu z okna, tak se nikdy
       nespusti dalsi stopa a je ticho; stopy se pak musi prehravat dal a dal,
       pri otevreni se vizual musi prizpusobit aktualni stope; nl-finn): with
       the page in the background the voice of all the steps left is queued
       in the stream at once (_tourAheadGo) -- the player plays the rest of
       the tour with no timer of the page; when the page comes back the shows
       start again at the step whose voice plays (_tourCatchUp). The tour on
       a Dutch page (nl, voice Finn). */
    /* core-v698 (voice tour thread, Marek: tlacitko play na startu animaci
       opacity od 10 % pri 200 % velikosti po 100 % opacity pri 100 %
       velikosti; 200 % pri plne opacite nevypada dobre): the PLAY entrance
       is one move -- 200 % at 10 % opacity to its own size at full opacity,
       1.8 s, no hold at the big size. */
    /* core-v699 (CONTAINER thread for the tour, Marek: pri nabehu nove
       stopy vzdy slysim cinknuti a ztlumeni zvuku -- slo by aby zvuk
       nekolisal ani na mobilu?): the tour's one stream is kept fed with the
       0.5 s silence on screen too, not only in the background -- between two
       clips it ran dry, the player stalled, held its last frames back and
       let them out with the next clip, and a phone restarts its audio output
       after a stall. The next clip follows the silence already queued (at
       most about 1 s later). */
    /* core-v700 (CONTAINER thread for the tour, Marek: Ano -- titulky vsude;
       odkaz s ?ig zobrazi titulky a zaroven vypne vsechny admin buttons /
       logs): the tour's EN subtitles in the lower third on every product of
       the core, the container's block as it is (search "THE TOUR'S
       SUBTITLES"): words from leva-voice /texts/en, a small cross closes
       them for the visitor, ?ig / ?subs=1 shows them, ?subs=0 hides them. */
    /* core-v701 (voice tour thread, Marek: el-eugene): the tour on a Greek
       page (el) -- the worker's el clips (voice Eugene, all 73); the command
       step types the Greek sentence; the fitout tiles by their Greek names. */
    /* core-v703 (voice tour thread, Marek: hu-peter): the tour on a
       Hungarian page (hu) -- the worker's hu clips (voice Peter, all 73); the
       command step types the Hungarian sentence; the fitout tiles by their
       Hungarian names. */
    /* core-v705 (voice tour thread, Marek: sv-kim): the tour on a Swedish
       page (sv) -- the worker's sv clips (voice Kim, all 73); the command
       step types the Swedish sentence; the fitout tiles by their Swedish
       names. */
    /* core-v710 (voice tour thread, Marek: he-omer): the tour on a Hebrew
       page (he) -- the worker's he clips (voice Omer, all 73, plural
       address); the command step types the Hebrew sentence; the fitout tiles
       by their Hebrew names. */
    /* core-v711 (voice tour thread, Marek: fi-christoffer-weiss): the tour
       on a Finnish page (fi) -- the worker's fi clips (voice Christoffer
       Weiss, all 73); the command step types the Finnish sentence; the
       fitout tiles by their Finnish names. */
    /* core-v712 (voice tour thread, Marek: no-olaf): the tour on a
       Norwegian page (no) -- the worker's no clips (voice Olaf, all 73); the
       command step types the Norwegian sentence; the fitout tiles by their
       Norwegian names. With it every language the store publishes (21) has
       its voice. */
    /* core-v713 (voice tour thread, Marek: odkaz s ?ig bude mit u kazdeho
       projektu jiny uvod -- pro posts na socialni site, catchy introduction;
       jen en verze): on an English page opened with ?ig (or window.LEVA_IG)
       the tour's intro speaks the product's own hook, leva-voice
       en/ig-intro-<slug> (Roger), before the usual intro clips in the list
       -- so a product without one keeps its usual intro; the subtitles read
       the hook's own words. Every other page is as before. */
    /* core-v720 (voice tour thread, Marek 2026-10-19: ig prezentace ve vsech
       projektech, highlights, video do 60 vterin -- na zacatku rychle par
       designs i barevne varianty, nahrani floorplanu z databaze nebo slovni
       popis, u stavebnich projektu strecha / zaklady, rozpocet a AI agent;
       zacatek / prvni stopa stejny i na ig i na webu, catchy): the ?ig reel
       (_TOUR_IG) is now intro, colours, plan library, roof, base (trailer
       on the THOW), budget, AI, outro -- 250 ms between
       the steps, the intro four designs at 1.5 s with no turn, three colour
       slots, the library takes a plan without the scroll and the mirror,
       roof and base three choices at 0.7 s (no pitch sweep), the budget
       scrolled in 2.5 s, a shorter outro line, the strip steps' leads at
       most 1.5 s and their icon tapped after 0.7 s;
       the log says the reel's length at its end. Every English tour (not only
       ?ig) opens with the product's hook en/ig-intro-<slug>. */
    /* core-v723 (TAURUS, Marek 2026-10-20: vypln kazdeho pole zvlast --
       klikem na pole se vybere, magenta, pruvodcem dole jeho vypln;
       orientace vyplne podle fasady): opt-in API.setPostWallMode({ perBay,
       orient }) -- a kind per bay (STATE.postWall.kinds), a tap on a bay's
       infill picks it (magenta) and the Slats tile / the strip's arrows then
       change it alone; the core's four kinds walked only in the facade's
       direction. setPostWallKinds may now override one of the core's kinds
       with the product's own draw. buildPostWalls draws each run in its own
       kind and adds to the rows the product's kinds billed. Without the
       call every product draws and bills as before. And the ?ig reel's
       intro on a page with no reference designs turns the house once under
       the hook (it stood still). */
    /* core-v724 (voice tour thread, Marek 2026-10-20: vsude jen jemne
       zvyrazneni, o neco tmavsi nez background f5f5f5, jemny puls, nikde
       cerveny nadech): the tour's highlight is one soft grey halo everywhere
       (rgba 0,0,0 0.10 swelling to 8 px and back, 1.6 s); on the dark strip
       and the folded bar a light one (rgba 255,255,255 0.18, 6 px). */
    /* core-v729 (TAURUS thread, Marek: terciky pro on / off vyplni mensi; u
       taurus neni mozne nahravat vlastni floorplan): the post-wall dots 360
       across, 400 out from the post line (PW_DOT); __PROD._tourOut -- steps
       a product leaves out of its tour, the reel and the full one alike. */
    /* core-v728 (TAURUS, Marek: kupujici stahne PDF puvodni rozsirene o to, co
       dodava konfigurator): __PROD._pdfOriginal() -> promise of { url, label,
       why }; the exporter (pdf-merge-v1) puts that PDF first, ours after. */
    /* core-v727 (TAURUS, Marek: rozpocet musi sedet s puvodnim projektem):
       roofSkin's battens take counter: null -- the battens then lie on the
       rafters at the covering's gauge, nothing counter-battened or billed as
       such (Matt's corrugated roof has no deck and no underlay). */
    /* core-v726 (TAURUS, Marek after v10): the infill row and strip say
       Infill, its kinds Planks / Slats (no H / V where the facade decides the
       direction); the picked bay 40 % magenta over its own colour; a ghost
       rect may ask for its fill and a band outline (r.fill, r.fillOp,
       r.band); a product's own designs in the plan library
       (__PROD._libraryItems / _libraryPick); the tour's infill step fills,
       picks, cycles and opens bays where a bay is picked, and plays on the
       reel there (ig-infill); a product's own clips (__PROD._tourClips, the
       name with -<slug>) and steps left out of its reel (__PROD._reelSkip). */
    /* core-v730 (voice tour thread, Marek 2026-10-21: zvetsit titulky u
       pruvodcu s ig): the tour's subtitles on a ?ig page about 4 % of the
       page's width (clamp 24 .. 56 px, bold); other pages unchanged. */
    /* core-v732 (homepage hero): ?ig&autoplay starts the reel by itself,
       muted, and loops it; window.LEVA_TOUR_SOUND / LEVA_TOUR_RUN /
       LEVA_TOUR_ON for the page around it; no short link from such a page.
       Other pages unchanged. */
    /* core-v734 (homepage hero, Marek 2026-10-21 11:36: "Pudorys se
       nezobrazi, prazdny canvas. Rotace moc dlouha, na zacatku by se mely
       rychleji vizualizovat ruzne typy (jina strecha, zaklady, rozmery,
       barvy)... pudorysy mozna vynechat... Opakovat celou smycku"): a
       ?ig&autoplay page plays its own cut -- intro (the reference designs,
       their sizes), roof, base, colours (trailer / infill where a product
       has them) -- no plan library, budget, AI or outro (their drawers are
       hidden in the hero's frame: an empty canvas). Muted it runs without
       the voice, each step as long as its show (the designs 1.1 s each,
       never the slow turn -- the embed template now carries the reference
       table the intro takes its designs from); the sound icon goes on from
       the step with the voice, off again without it. Other pages unchanged. */
    /* core-v735 (homepage hero, Marek 2026-10-21 12:04: "Udelej trosku zoom
       out, lepsi bude kdyz objekt bude mensi. Chce to lepsi uvod - neni to
       je o ADU - vice obecne"; 12:07 "Ve vsech jazycich musi byt voice
       over"): window.LEVA_FIT_K, when a page sets it (the hero's embed
       layout: 1.3), multiplies the camera distance of every framing
       (fitCameraToFootprint, the tour's _tourFit); and on ?ig&autoplay the
       intro's first clip is voice:<lang>/hero-intro (LEVA in general: real
       projects in 3D, the price in real time, every item from real e-shops)
       in every language, the product's hook after it as the fallback.
       12:09 "bez spodniho grid ... v okoli faded stromy": LEVA_NO_GRID hides
       the ground grid and its axes; LEVA_SCENERY_FADE (0..1) puts the
       scenery's spruces round the house at that opacity on ?ig&autoplay,
       and the reel's reference designs keep them.
       Pages without them unchanged. */
    /* core-v736 (homepage hero, Marek 2026-10-21 13:49: "behem animace:
       natahovat previ strechy; celou dobu mirne rotovat; vyzkousej spise
       faded zastavbu okolo (staci jednoduche boxy ruzne vysoke a velke,
       jakoby naznacene okna / dvere), stromy na polovinu"), ?ig&autoplay
       only: after the roof types the roof's four overhangs stretch 1.2 m
       out and back (_tourEaves); the camera turns slowly the whole time
       (autoRotate 0.7, kept on by a 1 s watcher while the reel runs) and is
       never put back between steps or at the loop's end; with
       LEVA_SCENERY_FADE the trees are halved (core-v739, Marek 15:36: "Nechej
       tam jen stromy, bez budov" -- the faded buildings of core-v736 are gone). */
    var CORE_VERSION = 'core-v817';   /* core-v817 (TWO STORY thread, Marek 2026-11-04: "Steny na OSB"): A STOREY'S FLOOR BUILD-UP OVER ITS DECK -- __PROD._floorLift(st) (metres): the walls stay on the deck, the upper rooms' finish and skirting, the partitions' doors (their header and threshold strip) and the furniture stand on the build-up; houseFloor's opt-in H.upLift draws its layers under every upper room's finish (round the stairwell) and hands the area back (H.upLiftA). No hook: nothing moves. core-v816 (T house thread, Marek 2026-11-04: udelat to jak je to v projektu): the gutters' near-miss corners -- two runs at one height whose ends miss by up to spec.gutters.snap are cut / run on to where their lines cross and joined; opt-in, nothing changes without it; and a gable wall's middle that falls on the ridge line read a little to either side (the masonry's gable test). core-v815 (DUPLEX thread, Marek 2026-11-04: dalsi krok): A WALL ANOTHER LAYER BUILDS -- a partition with p.own (the layer that builds it) is a wall for the plan, the rooms, the services and the skirting, and the partitions module builds and bills nothing of it; p.t its own thickness in the floorplan contract, the spec and the plan's area (DUPLEX's party wall, 257 mm). core-v814 core-v814 (T house thread for the perf line, Marek 2026-11-04: zrychlit i na mobilu): the downspout swept piece by piece (_rsgPieces) -- the rings where the route bends, not spread evenly along it, and a 4-point corner in the section; ?dsp=even as before. core-v813 (shared-modules thread, Marek 2026-11-04 00:10: pokracuj v opravach preneseni): THE FIXES CARRIED ACROSS -- houseLps carries the cable 90 mm over the roof edge for every product (barndo-v34), houseFinTex draws the tile joint in grout grey for every product (sauna-v53), houseSkirtCuts (the skirting's doorways, the ADU's text) also for SAUNA; status/modules.md 14; core-v812 (TWO STORY thread, Marek 2026-11-03: two-storey designs in the plan library): THE PLAN LIBRARY's tile has no mirror button for an item that says it.noMirror (a two-storey design opens as drawn) */
    /* core-v811 (TWO STORY thread, Marek 2026-11-03: "Na balkone / terase jsou nepruhledne dvere" and "bez tech horizontalnich ... prvku"): THE PARTITIONS MODULE keeps a wall of a storey with its own outline (__PROD._storeyPoly) inside that outline, the wall's thickness in (_lineInOutline) -- a partition laid before the roof terrace stood on it, in the facade, and filled the terrace doors; THE RENDER's dark plinth band only at the foot of the ground storey (an upper storey had one at the floor between) */
    /* core-v810 (shared-modules thread, Marek 2026-11-03 18:36: dalsi krok): houseLps (the lightning protection, BARNDO edgeOut), houseOpenFitouts (the FITOUT picker), houseSnapshot (the snapshot's product part, SAUNA head, TAURUS scene); status/modules.md 13; core-v809 (TWO STORY thread, Marek 2026-11-03): THE JOINERY MODULE draws a round light (OWC) round on the house line too (ring, disc of glass, the corners closed by the wall and the facade), the wall kit puts no sill under it; and a product's DEFAULT PROJECT is waited for behind the loading cover (no flash of the plain defaults); core-v807 (shared-modules thread, Marek 2026-11-03 16:05: pokracuj): THE SMALL SHARED HELPERS -- ten functions the house-line products carried word for word (houseWebPlane, houseFootOnChord, houseSeatOnHip, houseRafterSteps, houseRoofPlaneYAt, houseRoofYAtXZ, houseUnderRoof, houseMembTex, houseSpaceBuild, houseDiagWalls), the ADU's text, a product's own sizes as the last argument; status/modules.md 11; core-v806 (shared-modules thread, Marek 2026-11-03 14:18: dalsi krok): THE CLEAN-UP after the shared modules -- houseFlatRoof's coping overhang gets its own name (overC; the KIOSK thread's find, kiosk-v9: a second 'var over' lifted the outlet domes and the gravel by the facade's width); status/modules.md 10; core-v805 (RUSSWORX SHED thread, Marek 2026-11-03 14:56 on his phone: default view moc napravo, kameru trosku vys, trosicku zoom out, at je jako jine projekty): the stage's o.viewDir may answer { x, y, z } (the direction's own rise -- RUSSWORX takes every product's 1 : 0.45 : 1), k (a distance factor) and rail: true (fitted to and centred in the canvas left of the '#cf-root .cf-sidebar' rail; the rig moved sideways, orbiting still round the model). No hook, nothing changes; core-v804 (RUSSWORX SHED thread, Marek 2026-11-03: the main canvas as Nick's promo picture, the PDF's first isometric the same, the frame profiles far too many -- the frame is two storeys): opt-in only -- THE STUD WALL frames a wall as walls stacked one on another where the product answers H.tiers(seg) (tierCap / tierUp / tierNoSole / tierFrom / tierTo on the copies), and leaves out a module stud closer than H.dropNear to a new jack or king; the stage frames first from __PROD._viewDir() and draws the drawings page's 'iso' from __PROD._isoDir() ({ x, z, eye }). No hook, nothing changes; core-v803 (Marek 2026-11-02 on his screenshots): the pitch's dashboard card is the real /royalties page with invented numbers, scrolled in step with the voice; the tour's subtitles over the drawers at 2147483000 (leva-profile raises the item drawer to 100006, they were under it); core-v802 (marketing thread, Marek 2026-11-02): the creator pitch only -- steps 'dashboard' and 'editlist' between login and outro (_tourDash*, _tourEdit*); core-v801 (RUSSWORX SHED thread, Marek 2026-11-02 18:59: v oknech jsou framing lumber): THE STUD WALL's opening framing -- a jack or a king standing under another opening's hole (his three upper windows over the two lower windows and the door) is cut by that hole like a module stud: the piece up to its sill, the cripple over its header; the pair's stitch nails follow. No opening over another, nothing changes; core-v800 (RUSSWORX SHED thread, round 3, Marek 2026-11-02: az do posledniho sroubku): opt-in only -- THE JOINERY MODULE's H.glazed(o) draws a hinged leaf as stiles and rails round one pane, H.secPitch(o) sets a sectional / roll-up door's slat pitch; the wall layout's studs step at __PROD._studStep() where a product says so (his 16 in.). No hook, nothing changes; core-v799 (perf thread, status/perf.md): window.__levaModelReady + the event 'leva:model-ready' two frames after the first build (15 s at most) -- the budget strip's map (bs-v61) and the configurator's own Mapbox SDK + map (initMap) wait for it; the model and the bill unchanged; core-v798 (RUSSWORX SHED thread, Marek 2026-11-02: bugs u podpurnych sloupu): THE EAVE POSTS MODULE's run of posts along x0 / x1 where the roof is not level along it (a mono falling along z, a gable with the ridge turned) -- the beam was level at the lower end's soffit, the high end's post stopped a rise short, the middle posts ran up past the beam and braced into air; now every post reaches its own soffit, the beam follows the roof post to post and the braces along it land on it. A level run is built exactly as before (every product's default unchanged); and THE JOINERY MODULE's opt-in H.colOf(o) -- a unit's leaf and frame in a colour of its own (his white roll-up door); core-v797 (Marek 2026-11-02 on GARDEN BAR: reset skoci na uplne jinou konstrukci, ma vest primo k Adamovu baru): reset() restored DEFAULTS, the core's own STATE copied before any product had set its design -- so every product's Reset landed on the bare box. boot() now keeps the STATE the product hands over, the fresh page's, and reset() returns to that, in the units the visitor is in. core-v796 (RUSSWORX SHED thread, shed-v132): the wall kit's corner trim climbs to the lower of the two walls' TOPS, each wall's rise over its own head -- identical wherever the walls are the same height (no product set STATE.segH before); a mono's high wall of its own height keeps its corner trim to the eave; core-v795: optional plan site bounds; core-v794: optional product rigid assembly spread; core-v792 (TWO STORY thread, status/twostory.md R5): the blinds pass over a round light (OWC / round) and over an opening marked blind: false; core-v791 (shared-modules thread, Marek 2026-10-30 16:26: dalsi krok): THE FITOUT MODULE -- API.houseFitout(ctx, hooks), the plan's pieces written once (built in parts at the plan's size, the kitchen runs and their wall units, islands, tables, the stacked dryer, beds, sofas, the bathroom; billed with companion rows, screws and silicone; GA / ANY HOUSE's storeys and US practice, BARNDO's loft, RANCH's qty marks, SAUNA's heater and benches behind hooks); status/modules.md 9; core-v789 (shared-modules thread, Marek 2026-10-30 15:05: dalsi krok): THE FLAT ROOF MODULE -- API.houseFlatRoof(ctx, planes, hooks), the warm flat roof written once (deck, vapour barrier, tapered EPS + PIR, membrane, parapet upstand and coping, scupper and downspout, ballast; TWO STORY's cells behind flatCut); status/modules.md 8c; core-v788 (TWO STORY thread, status/twostory.md R4b): houseRoof's H.spanAxis(pl) -- a product may say which way a flat roof's rafters run; opt-in; core-v787 (shared-modules thread, Marek 2026-10-30 14:07: dalsi krok): THE ROOF SKIN MODULE -- API.houseRoofSkin(ctx, hooks), the roof over the frame written once (the hip end framed: centre truss, hips, heels, jacks, side slopes, plates, billed and recorded for the truss sheets; the outline shell; the build-up steps deck, underlay, battens, edges, covering, ridge; RANCH's cross gables, Matt's bare roof, the ANY HOUSE L's flush hip end behind hooks); status/modules.md 8b; core-v786 (KIOSK thread, kiosk-v5, Marek 2026-10-30: dalsi krok): THE SERVING WINDOWS -- no window sill inside or out under a serve unit (it has a counter); OPENING_CATALOG.serve (SRVF0001 / SRVL0001 / SRVX0001, 4 / 6 / 8 ft x 40 in., sill 40 in.), its tile key mc_op_serving_window, and the joinery bills a unit marked m2 by its area; nothing changes for a product that does not offer serve. core-v785 (TWO STORY thread, status/twostory.md R4a): a storey's own outline -- __PROD._storeyPoly(s, P), polyOf(s), storeys()[s].poly and segments() per storey; opt-in, every product that does not answer unchanged; core-v784 (shared-modules thread, Marek 2026-10-30 13:27: dalsi krok): THE ROOF FRAME MODULE -- API.houseRoof(ctx, hooks), what carries the roof written once (the hand-over to the trusses, the rafters of a mono / flat roof and their set-out, the ridge board, the ties, then posts, build-up and gutters in order, the truss stage of the frame view); status/modules.md 8a; core-v783 (shared-modules thread, Marek 2026-10-30 12:02: dalsi krok): THE JOINERY MODULE -- API.houseJoinery(ctx, hooks), the windows and exterior doors written once (frame, threshold, leaves, panes, the walk-through, the bill and the fixings), and API.houseDoorSet(...), a partition's doorway drawn (lining, casing, leaf, slider, closet doors); the AR leaf helpers shared; status/modules.md 7; core-v782 (shared-modules thread, Marek 2026-10-30 10:41: dalsi krok): THE PARTITIONS MODULE -- API.houseParts(ctx, hooks), the inside walls written once (frame, wool, board face by face, thresholds, doorways and doors; GA's apartment walls, BARNDO's loft, RANCH / GA's house-garage line, SAUNA's sauna room, the shed family's OSB, Matt's T&G behind hooks); see THE PARTITIONS MODULE; core-v781 (houseboat thread): the houseboat's opt-in marine controls -- _postWallOnly, _postWallDeck.out, the plan overlay's grips, API.eavePosts H.deckFoot (status/houseboat.md); core-v780 (shared-modules thread, Marek 2026-10-30 10:08: pokracuj): THE CEILING MODULE -- API.houseCeiling(ctx, hooks), the ceiling written once (blown fill under trusses, the mono-pitch's joists and ledgers, barrier, battens, plasterboard room by room; RANCH's masonry gable and garage, SAUNA's sauna room, GA / ANY HOUSE's attic hatch, the L's seam behind hooks); see THE CEILING MODULE; core-v779 (shared-modules thread, Marek 2026-10-30 09:05: zkontrolovano, dalsi krok): THE FLOOR MODULE -- API.houseFloor(ctx, hooks), the timber floor written once (frame, deck, the build-up in and over the bays, the rooms' finishes and skirting; GA / ANY HOUSE's apartment floor, RANCH's slab rooms, block house and one body a layer, SAUNA's sauna room, Matt's plywood store and deck behind hooks); see THE FLOOR MODULE; core-v778 (shared-modules thread, Marek 2026-10-29 23:57: dalsi krok): THE WALL KIT, THE GABLES, EAVES AND SOFFITS -- gable sheathing and skin, eave blocks (closure, vent strip, baffles), soffits, hip soffit, corner beads, the coating note, the openings scan written once (RANCH's porch walls and garage bays, GA's upper storey, the hipped L, Matt's T&G behind hooks); the walls' layers are all in the kit now; see THE WALL KIT; core-v777 (houseboat thread): the houseboat's deck (status/houseboat.md); core-v776 (shared-modules thread, Marek 2026-10-29 22:59: dalsi krok prosim): THE WALL KIT, THE OUTSIDE -- sheathing, breather, facade battens, boards, the contact facade, skirt, outside sills, corner trims and reveals written once (the shed family's panel siding and counter battens, TAURUS's T&G, RANCH's porch walls, the L's kink, GA's band, vent strip and head flashing behind hooks); see THE WALL KIT; core-v775 (shared-modules thread, Marek 2026-10-29 20:45: dalsi krok): THE WALL KIT -- API.wallKit(hooks), the wall's layers written once, starting with the inside (service battens, board, wool, vapour barrier, batts, interior sills; SAUNA's hot room, RANCH's one body a layer, GA's casing behind hooks); see THE WALL KIT; core-v774 (blueprints thread, Marek 2026-10-29 15:06: muzes i neverejne): the drawings page's package for CABIN and the ANY HOUSE engine's presets (TWO STORY HOUSE, ANY HOUSE ENGINE) too; core-v773 (shared-modules thread, Marek 2026-10-29 14:25: pokracuj any house konsolidace): THE STUD WALL MODULE -- API.studWall(ctx, hooks), the timber frame of every wall written once (the ADU's text as ANY HOUSE carries it; GARAGE APT / ANY HOUSE's storeys, portal frames and nailing, RANCH's L ends, the treated plate on the slab and the LVL headers, TAURUS's portal beam and the shed family's siding behind hooks); see THE STUD WALL MODULE; core-v772 (bunker thread): the opt-in modeled-only assembly, per-body fly direction (status/bunker.md); core-v771 (T house thread, Marek 2026-10-27: druhy otvor; 2026-10-29: zapsat oba): SEVERAL FLOOR OPENINGS -- a product whose _floorHole() hands list() and setAt(i, R) gets the corners and the move handle on every opening (_fhAll, _fhTick1); index 0 is get() / set() as before, a product without list() is unchanged; STAIR_KITS spiral + SIS32016 and loft + SIS32070 (3200, the catalogue's new rows); core-v770 (blueprints thread, Marek 2026-10-29 13:18: nestihly se vygenerovat cisla - ceny na hlavni stranku; ikona se nema aktivovat driv, dokud nebudou nacteny hodnoty): the PDF icon stays grey until the bill has its prices (the catalogue in, a priced row of the building's bill, the strip's total not nought) -- 45 s at most; core-v769 (blueprints thread, Marek 2026-10-29 12:36: chtel bych to takto u vsech produktu - i Taurusy, firewood racks, benches atd.): TAURUS (I and III) and GARDEN BAR in the drawings page's package too -- the original drawings (__PROD._pdfOriginal) ride along as args.prependPdf, an author's royalty product carries rec.buy (the purchase instead of tokens) instead of exporting on the page; core-v768 (GARDEN BAR thread, Marek 2026-10-29: voice over ve 21 jazycich): the tour's PRODUCT step -- a product's own rows (__PROD._tourRow) opened and walked on the arrows after the base, its own clip tour-product-<slug>; on the ?ig reel after the designs (ig-product); passed by where a product names none (opt-in, every other product unchanged); core-v767 (blueprints thread, Marek 2026-10-29: chci vizualizaci kompletniho pdf na blueprints -- main page s product picture, cut lists, shopping lists, BoQ): ?bppkg=1 -- the drawings page sends a visitor with no package here; the page covers itself, keeps the PDF tap's package once the catalogue and the prices are in (no tokens) and goes back to the drawings (_bpAutoRun); core-v766: core-v765 (shared-modules thread, Marek 2026-10-27 11:36: dalsi krok): THE TRUSS MODULE -- API.trusses(ctx, planes, hooks), the common trusses written once (the ADU's text; RANCH's truss check, ANY HOUSE's L girders, the gable ladder, BARNDO's post-frame heels behind hooks); two fixes carried to every product: the ridge ends off the roof's planes (adu-v1419) and a ridge along x turning the truss the right way (taurus-v16); see THE TRUSS MODULE; core-v764 (shared-modules thread, Marek 2026-10-27 10:31: dalsi krok): THE FOUNDATION MODULE -- API.foundation(ctx, hooks), the base written once (the ADU's text; RANCH's dug ground, the L's skipped sides and seam, the products' own slabs, TAURUS's no steps behind hooks), SHED's two fixes for everyone (the access door off a terrace side, a vent sliding past the door); see THE FOUNDATION MODULE; core-v763 (AI reel thread, Marek 2026-10-27 10:38: "Tlacitko play dej na stred dolu, ale po zmacknuti, dve vteriny nic, pak spustit. Tlacitko mezitim schovat - plati pro vsechny prispevky pro ig, ktere budeme delat"): on a ?ig page (not the hero's autoplay, not the creator pitch) the tour's PLAY sits in the middle of the canvas's bottom edge above the command bar; a tap hides it and the reel starts 2 s later; it comes back when the reel ends. core-v762 (shared-modules thread, Marek 2026-10-27: pokracuj s dalsi konsolidaci dle seznamu): THE EAVE POSTS MODULE -- API.eavePosts(ctx, hooks), the posts under a deep overhang written once (the ADU's text; the shed family's framing behind H.shed, the THOW porch behind H.porch), fourteen builds' own copies gone; see THE EAVE POSTS MODULE; core-v761 (blueprints thread): BUNKER and HOUSEBOAT in the PDF package (PKG_SLUGS); core-v760 (shared-modules thread, Marek 2026-10-27: ADU, okap by to chtelo posunout vys = bliz k profilu okapnice): the gutters module hangs each run from the eave drip the edges drawer really drew (ctx._rsDrip) -- lapped 12 mm into the trough where the drip's face stands over it, 5 mm under the kick where the face is at the fascia (a hip); the old deck rule only where no drip was drawn; core-v759 (shared-modules thread, Marek 2026-10-27: zacnes ze stavajicich konfiguratoru tvorit centralni asset, at se nemusi stejne veci opakovat?): THE GUTTERS MODULE -- API.gutters(ctx, hooks), the gutters, outlets and downspouts of the house line written once (the ANY HOUSE text: the ADU's own plus the L behind hooks), the twelve libs' own copies gone; see THE GUTTERS MODULE; core-v758 (GARDEN BAR thread, Marek 2026-10-26 20:59: rovnou to muzes prehodit na beta): AN AUTHOR'S ROYALTY PRODUCT (window.LEVA_PRODUCT.vendor not LEVA -- GARDEN BAR, Man Cave Bartender) takes no tokens for its PDF: the tap goes straight to the export and the purchase gate of configurator-core.js decides -- a buyer gets it, anyone else the gate's Purchase card; no token price said (the bench lib's bench-v356 rule, carried to the house line); every LEVA product unchanged; core-v757 (ANY HOUSE thread, Marek 2026-10-26: muzes pokracovat -- ROAD 3b, the hipped L): an L's flush end hipped -- the bar's hip apex on the wing's ridge (no 0.3 m keep) and that hip end merged with the wing's slope on the same side into one plane (_roofMergeFlush), its valley dropped; opt-in by STATE.wing.flush + hip; core-v756 (marketing thread, Marek 2026-10-23 17:17: statistika sbalena na jedno tlacitko): the login step opens the panel with LEVA_AFF_PANEL(true), shuts it at its end; core-v755 (marketing thread, Marek 2026-10-23 16:25: rozsirit ukazku na Taurus 3 -- pri prihlaseni uvidite na nakupnim seznamu statistiku, muzete menit svuj affiliate kod): the creator pitch's step login, after the budget; core-v754 (marketing thread, Marek 2026-10-23 15:27-15:30): THE CREATOR PITCH, opt-in by the page (window.LEVA_PITCH) -- see _tourPitch; core-v753 (ANY HOUSE thread, Marek 2026-10-23: any house -- the L): STATE.wing.flush 'lo' / 'hi', a wing flush with one end of the bar (an L, no return of wall there; poly() drops the corner that falls in a straight run, _wingRect and the wing's drag keep it flush) -- opt-in by the state, the T unchanged; core-v752 (ANY HOUSE thread, Marek 2026-10-22): ?recipe= from the any-house page -- the product's default project, then the recipe through the product's own setters, see ANY HOUSE HANDS OVER A RECIPE; core-v751 (corner window thread, Marek 2026-10-16: rohove okno -- sestava dvou katalogovych oken s ikonou prepinani, max 12 kroku, konstrukcne spravne; 2026-10-22: realizovat): the corner window, see THE CORNER WINDOW; core-v750 (homepage hero thread): the hero's camera kept when its reel shuts the drawer; core-v749 (TAURUS thread, Marek 2026-10-21): the tour's assembly step in one pass -- exploded, back together and four steps in quick succession (only the first step showed); core-v748 (TAURUS thread, Marek 2026-10-21: udelej na tento projekt ?IG prezentaci): two opt-ins for a house-line product's tour -- __PROD._tourBudget2 (the budget step as the container's: dashboard, price bar 3 stores + cheapest, the budget's SECOND item's card held until loaded, its offers, shut) and __PROD._reelAsm (the exploded view and a few assembly steps on the ?ig reel, before the budget); core-v747 (bench thread, Marek 2026-10-21: titulky jsou schovane za drawers): the tour's subtitle over any open drawer -- in the page's body, fixed, 100001 */
    /* core-v808 (shared-modules thread, Marek 2026-11-03 18:04: pokracuj): THE FEW-VARIANT HELPERS -- houseFinTex, houseStudPositions / houseStudPositionsWorld, houseGableFaces, houseTrussStations, houseTerraceDeck: one text each, a product's own rule as a hook (SAUNA grout, RANCH masonry / faces / its terrace, BARNDO first, GA / ANY HOUSE endStop, ANY HOUSE runRidge); status/modules.md 12; core-v807 (shared-modules thread, Marek 2026-11-03 16:05: pokracuj): THE SMALL SHARED HELPERS -- ten functions the house-line products carried word for word (houseWebPlane, houseFootOnChord, houseSeatOnHip, houseRafterSteps, houseRoofPlaneYAt, houseRoofYAtXZ, houseUnderRoof, houseMembTex, houseSpaceBuild, houseDiagWalls), the ADU's text, a product's own sizes as the last argument; status/modules.md 11; core-v806 (shared-modules thread, Marek 2026-11-03 14:18: dalsi krok): THE CLEAN-UP after the shared modules -- houseFlatRoof's coping overhang gets its own name (overC; the KIOSK thread's find, kiosk-v9: a second 'var over' lifted the outlet domes and the gravel by the facade's width); status/modules.md 10; core-v805 (RUSSWORX SHED thread, Marek 2026-11-03 14:56 on his phone: default view moc napravo, kameru trosku vys, trosicku zoom out, at je jako jine projekty): the stage's o.viewDir may answer { x, y, z } (the direction's own rise -- RUSSWORX takes every product's 1 : 0.45 : 1), k (a distance factor) and rail: true (fitted to and centred in the canvas left of the '#cf-root .cf-sidebar' rail; the rig moved sideways, orbiting still round the model). No hook, nothing changes; core-v804 (RUSSWORX SHED thread, Marek 2026-11-03: the main canvas as Nick's promo picture, the PDF's first isometric the same, the frame profiles far too many -- the frame is two storeys): opt-in only -- THE STUD WALL frames a wall as walls stacked one on another where the product answers H.tiers(seg) (tierCap / tierUp / tierNoSole / tierFrom / tierTo on the copies), and leaves out a module stud closer than H.dropNear to a new jack or king; the stage frames first from __PROD._viewDir() and draws the drawings page's 'iso' from __PROD._isoDir() ({ x, z, eye }). No hook, nothing changes; core-v803 (Marek 2026-11-02 on his screenshots): the pitch's dashboard card is the real /royalties page with invented numbers, scrolled in step with the voice; the tour's subtitles over the drawers at 2147483000 (leva-profile raises the item drawer to 100006, they were under it); core-v802 (marketing thread, Marek 2026-11-02): the creator pitch only -- steps 'dashboard' and 'editlist' between login and outro (_tourDash*, _tourEdit*); core-v801 (RUSSWORX SHED thread, Marek 2026-11-02 18:59: v oknech jsou framing lumber): THE STUD WALL's opening framing -- a jack or a king standing under another opening's hole (his three upper windows over the two lower windows and the door) is cut by that hole like a module stud: the piece up to its sill, the cripple over its header; the pair's stitch nails follow. No opening over another, nothing changes; core-v800 (RUSSWORX SHED thread, round 3, Marek 2026-11-02: az do posledniho sroubku): opt-in only -- THE JOINERY MODULE's H.glazed(o) draws a hinged leaf as stiles and rails round one pane, H.secPitch(o) sets a sectional / roll-up door's slat pitch; the wall layout's studs step at __PROD._studStep() where a product says so (his 16 in.). No hook, nothing changes; core-v799 (perf thread, status/perf.md): window.__levaModelReady + the event 'leva:model-ready' two frames after the first build (15 s at most) -- the budget strip's map (bs-v61) and the configurator's own Mapbox SDK + map (initMap) wait for it; the model and the bill unchanged; core-v798 (RUSSWORX SHED thread, Marek 2026-11-02: bugs u podpurnych sloupu): THE EAVE POSTS MODULE's run of posts along x0 / x1 where the roof is not level along it (a mono falling along z, a gable with the ridge turned) -- the beam was level at the lower end's soffit, the high end's post stopped a rise short, the middle posts ran up past the beam and braced into air; now every post reaches its own soffit, the beam follows the roof post to post and the braces along it land on it. A level run is built exactly as before (every product's default unchanged); and THE JOINERY MODULE's opt-in H.colOf(o) -- a unit's leaf and frame in a colour of its own (his white roll-up door); core-v797 (Marek 2026-11-02 on GARDEN BAR: reset skoci na uplne jinou konstrukci, ma vest primo k Adamovu baru): reset() restored DEFAULTS, the core's own STATE copied before any product had set its design -- so every product's Reset landed on the bare box. boot() now keeps the STATE the product hands over, the fresh page's, and reset() returns to that, in the units the visitor is in. core-v796 (RUSSWORX SHED thread, shed-v132): the wall kit's corner trim climbs to the lower of the two walls' TOPS, each wall's rise over its own head -- identical wherever the walls are the same height (no product set STATE.segH before); a mono's high wall of its own height keeps its corner trim to the eave; core-v795: optional plan site bounds; core-v794: optional product rigid assembly spread; core-v792 (TWO STORY thread, status/twostory.md R5): the blinds pass over a round light (OWC / round) and over an opening marked blind: false; core-v791 (shared-modules thread, Marek 2026-10-30 16:26: dalsi krok): THE FITOUT MODULE -- API.houseFitout(ctx, hooks), the plan's pieces written once (built in parts at the plan's size, the kitchen runs and their wall units, islands, tables, the stacked dryer, beds, sofas, the bathroom; billed with companion rows, screws and silicone; GA / ANY HOUSE's storeys and US practice, BARNDO's loft, RANCH's qty marks, SAUNA's heater and benches behind hooks); status/modules.md 9; core-v789 (shared-modules thread, Marek 2026-10-30 15:05: dalsi krok): THE FLAT ROOF MODULE -- API.houseFlatRoof(ctx, planes, hooks), the warm flat roof written once (deck, vapour barrier, tapered EPS + PIR, membrane, parapet upstand and coping, scupper and downspout, ballast; TWO STORY's cells behind flatCut); status/modules.md 8c; core-v788 (TWO STORY thread, status/twostory.md R4b): houseRoof's H.spanAxis(pl) -- a product may say which way a flat roof's rafters run; opt-in; core-v787 (shared-modules thread, Marek 2026-10-30 14:07: dalsi krok): THE ROOF SKIN MODULE -- API.houseRoofSkin(ctx, hooks), the roof over the frame written once (the hip end framed: centre truss, hips, heels, jacks, side slopes, plates, billed and recorded for the truss sheets; the outline shell; the build-up steps deck, underlay, battens, edges, covering, ridge; RANCH's cross gables, Matt's bare roof, the ANY HOUSE L's flush hip end behind hooks); status/modules.md 8b; core-v786 (KIOSK thread, kiosk-v5, Marek 2026-10-30: dalsi krok): THE SERVING WINDOWS -- no window sill inside or out under a serve unit (it has a counter); OPENING_CATALOG.serve (SRVF0001 / SRVL0001 / SRVX0001, 4 / 6 / 8 ft x 40 in., sill 40 in.), its tile key mc_op_serving_window, and the joinery bills a unit marked m2 by its area; nothing changes for a product that does not offer serve. core-v785 (TWO STORY thread, status/twostory.md R4a): a storey's own outline -- __PROD._storeyPoly(s, P), polyOf(s), storeys()[s].poly and segments() per storey; opt-in, every product that does not answer unchanged; core-v784 (shared-modules thread, Marek 2026-10-30 13:27: dalsi krok): THE ROOF FRAME MODULE -- API.houseRoof(ctx, hooks), what carries the roof written once (the hand-over to the trusses, the rafters of a mono / flat roof and their set-out, the ridge board, the ties, then posts, build-up and gutters in order, the truss stage of the frame view); status/modules.md 8a; core-v783 (shared-modules thread, Marek 2026-10-30 12:02: dalsi krok): THE JOINERY MODULE -- API.houseJoinery(ctx, hooks), the windows and exterior doors written once (frame, threshold, leaves, panes, the walk-through, the bill and the fixings), and API.houseDoorSet(...), a partition's doorway drawn (lining, casing, leaf, slider, closet doors); the AR leaf helpers shared; status/modules.md 7; core-v782 (shared-modules thread, Marek 2026-10-30 10:41: dalsi krok): THE PARTITIONS MODULE -- API.houseParts(ctx, hooks), the inside walls written once (frame, wool, board face by face, thresholds, doorways and doors; GA's apartment walls, BARNDO's loft, RANCH / GA's house-garage line, SAUNA's sauna room, the shed family's OSB, Matt's T&G behind hooks); see THE PARTITIONS MODULE; core-v781 (houseboat thread): the houseboat's opt-in marine controls -- _postWallOnly, _postWallDeck.out, the plan overlay's grips, API.eavePosts H.deckFoot (status/houseboat.md); core-v780 (shared-modules thread, Marek 2026-10-30 10:08: pokracuj): THE CEILING MODULE -- API.houseCeiling(ctx, hooks), the ceiling written once (blown fill under trusses, the mono-pitch's joists and ledgers, barrier, battens, plasterboard room by room; RANCH's masonry gable and garage, SAUNA's sauna room, GA / ANY HOUSE's attic hatch, the L's seam behind hooks); see THE CEILING MODULE; core-v779 (shared-modules thread, Marek 2026-10-30 09:05: zkontrolovano, dalsi krok): THE FLOOR MODULE -- API.houseFloor(ctx, hooks), the timber floor written once (frame, deck, the build-up in and over the bays, the rooms' finishes and skirting; GA / ANY HOUSE's apartment floor, RANCH's slab rooms, block house and one body a layer, SAUNA's sauna room, Matt's plywood store and deck behind hooks); see THE FLOOR MODULE; core-v778 (shared-modules thread, Marek 2026-10-29 23:57: dalsi krok): THE WALL KIT, THE GABLES, EAVES AND SOFFITS -- gable sheathing and skin, eave blocks (closure, vent strip, baffles), soffits, hip soffit, corner beads, the coating note, the openings scan written once (RANCH's porch walls and garage bays, GA's upper storey, the hipped L, Matt's T&G behind hooks); the walls' layers are all in the kit now; see THE WALL KIT; core-v777 (houseboat thread): the houseboat's deck (status/houseboat.md); core-v776 (shared-modules thread, Marek 2026-10-29 22:59: dalsi krok prosim): THE WALL KIT, THE OUTSIDE -- sheathing, breather, facade battens, boards, the contact facade, skirt, outside sills, corner trims and reveals written once (the shed family's panel siding and counter battens, TAURUS's T&G, RANCH's porch walls, the L's kink, GA's band, vent strip and head flashing behind hooks); see THE WALL KIT; core-v775 (shared-modules thread, Marek 2026-10-29 20:45: dalsi krok): THE WALL KIT -- API.wallKit(hooks), the wall's layers written once, starting with the inside (service battens, board, wool, vapour barrier, batts, interior sills; SAUNA's hot room, RANCH's one body a layer, GA's casing behind hooks); see THE WALL KIT; core-v774 (blueprints thread, Marek 2026-10-29 15:06: muzes i neverejne): the drawings page's package for CABIN and the ANY HOUSE engine's presets (TWO STORY HOUSE, ANY HOUSE ENGINE) too; core-v773 (shared-modules thread, Marek 2026-10-29 14:25: pokracuj any house konsolidace): THE STUD WALL MODULE -- API.studWall(ctx, hooks), the timber frame of every wall written once (the ADU's text as ANY HOUSE carries it; GARAGE APT / ANY HOUSE's storeys, portal frames and nailing, RANCH's L ends, the treated plate on the slab and the LVL headers, TAURUS's portal beam and the shed family's siding behind hooks); see THE STUD WALL MODULE; core-v772 (bunker thread): the opt-in modeled-only assembly, per-body fly direction (status/bunker.md); core-v771 (T house thread, Marek 2026-10-27: druhy otvor; 2026-10-29: zapsat oba): SEVERAL FLOOR OPENINGS -- a product whose _floorHole() hands list() and setAt(i, R) gets the corners and the move handle on every opening (_fhAll, _fhTick1); index 0 is get() / set() as before, a product without list() is unchanged; STAIR_KITS spiral + SIS32016 and loft + SIS32070 (3200, the catalogue's new rows); core-v770 (blueprints thread, Marek 2026-10-29 13:18: nestihly se vygenerovat cisla - ceny na hlavni stranku; ikona se nema aktivovat driv, dokud nebudou nacteny hodnoty): the PDF icon stays grey until the bill has its prices (the catalogue in, a priced row of the building's bill, the strip's total not nought) -- 45 s at most; core-v769 (blueprints thread, Marek 2026-10-29 12:36: chtel bych to takto u vsech produktu - i Taurusy, firewood racks, benches atd.): TAURUS (I and III) and GARDEN BAR in the drawings page's package too -- the original drawings (__PROD._pdfOriginal) ride along as args.prependPdf, an author's royalty product carries rec.buy (the purchase instead of tokens) instead of exporting on the page; core-v768 (GARDEN BAR thread, Marek 2026-10-29: voice over ve 21 jazycich): the tour's PRODUCT step -- a product's own rows (__PROD._tourRow) opened and walked on the arrows after the base, its own clip tour-product-<slug>; on the ?ig reel after the designs (ig-product); passed by where a product names none (opt-in, every other product unchanged); core-v767 (blueprints thread, Marek 2026-10-29: chci vizualizaci kompletniho pdf na blueprints -- main page s product picture, cut lists, shopping lists, BoQ): ?bppkg=1 -- the drawings page sends a visitor with no package here; the page covers itself, keeps the PDF tap's package once the catalogue and the prices are in (no tokens) and goes back to the drawings (_bpAutoRun); core-v766: core-v765 (shared-modules thread, Marek 2026-10-27 11:36: dalsi krok): THE TRUSS MODULE -- API.trusses(ctx, planes, hooks), the common trusses written once (the ADU's text; RANCH's truss check, ANY HOUSE's L girders, the gable ladder, BARNDO's post-frame heels behind hooks); two fixes carried to every product: the ridge ends off the roof's planes (adu-v1419) and a ridge along x turning the truss the right way (taurus-v16); see THE TRUSS MODULE; core-v764 (shared-modules thread, Marek 2026-10-27 10:31: dalsi krok): THE FOUNDATION MODULE -- API.foundation(ctx, hooks), the base written once (the ADU's text; RANCH's dug ground, the L's skipped sides and seam, the products' own slabs, TAURUS's no steps behind hooks), SHED's two fixes for everyone (the access door off a terrace side, a vent sliding past the door); see THE FOUNDATION MODULE; core-v763 (AI reel thread, Marek 2026-10-27 10:38: "Tlacitko play dej na stred dolu, ale po zmacknuti, dve vteriny nic, pak spustit. Tlacitko mezitim schovat - plati pro vsechny prispevky pro ig, ktere budeme delat"): on a ?ig page (not the hero's autoplay, not the creator pitch) the tour's PLAY sits in the middle of the canvas's bottom edge above the command bar; a tap hides it and the reel starts 2 s later; it comes back when the reel ends. core-v762 (shared-modules thread, Marek 2026-10-27: pokracuj s dalsi konsolidaci dle seznamu): THE EAVE POSTS MODULE -- API.eavePosts(ctx, hooks), the posts under a deep overhang written once (the ADU's text; the shed family's framing behind H.shed, the THOW porch behind H.porch), fourteen builds' own copies gone; see THE EAVE POSTS MODULE; core-v761 (blueprints thread): BUNKER and HOUSEBOAT in the PDF package (PKG_SLUGS); core-v760 (shared-modules thread, Marek 2026-10-27: ADU, okap by to chtelo posunout vys = bliz k profilu okapnice): the gutters module hangs each run from the eave drip the edges drawer really drew (ctx._rsDrip) -- lapped 12 mm into the trough where the drip's face stands over it, 5 mm under the kick where the face is at the fascia (a hip); the old deck rule only where no drip was drawn; core-v759 (shared-modules thread, Marek 2026-10-27: zacnes ze stavajicich konfiguratoru tvorit centralni asset, at se nemusi stejne veci opakovat?): THE GUTTERS MODULE -- API.gutters(ctx, hooks), the gutters, outlets and downspouts of the house line written once (the ANY HOUSE text: the ADU's own plus the L behind hooks), the twelve libs' own copies gone; see THE GUTTERS MODULE; core-v758 (GARDEN BAR thread, Marek 2026-10-26 20:59: rovnou to muzes prehodit na beta): AN AUTHOR'S ROYALTY PRODUCT (window.LEVA_PRODUCT.vendor not LEVA -- GARDEN BAR, Man Cave Bartender) takes no tokens for its PDF: the tap goes straight to the export and the purchase gate of configurator-core.js decides -- a buyer gets it, anyone else the gate's Purchase card; no token price said (the bench lib's bench-v356 rule, carried to the house line); every LEVA product unchanged; core-v757 (ANY HOUSE thread, Marek 2026-10-26: muzes pokracovat -- ROAD 3b, the hipped L): an L's flush end hipped -- the bar's hip apex on the wing's ridge (no 0.3 m keep) and that hip end merged with the wing's slope on the same side into one plane (_roofMergeFlush), its valley dropped; opt-in by STATE.wing.flush + hip; core-v756 (marketing thread, Marek 2026-10-23 17:17: statistika sbalena na jedno tlacitko): the login step opens the panel with LEVA_AFF_PANEL(true), shuts it at its end; core-v755 (marketing thread, Marek 2026-10-23 16:25: rozsirit ukazku na Taurus 3 -- pri prihlaseni uvidite na nakupnim seznamu statistiku, muzete menit svuj affiliate kod): the creator pitch's step login, after the budget; core-v754 (marketing thread, Marek 2026-10-23 15:27-15:30): THE CREATOR PITCH, opt-in by the page (window.LEVA_PITCH) -- see _tourPitch; core-v753 (ANY HOUSE thread, Marek 2026-10-23: any house -- the L): STATE.wing.flush 'lo' / 'hi', a wing flush with one end of the bar (an L, no return of wall there; poly() drops the corner that falls in a straight run, _wingRect and the wing's drag keep it flush) -- opt-in by the state, the T unchanged; core-v752 (ANY HOUSE thread, Marek 2026-10-22): ?recipe= from the any-house page -- the product's default project, then the recipe through the product's own setters, see ANY HOUSE HANDS OVER A RECIPE; core-v751 (corner window thread, Marek 2026-10-16: rohove okno -- sestava dvou katalogovych oken s ikonou prepinani, max 12 kroku, konstrukcne spravne; 2026-10-22: realizovat): the corner window, see THE CORNER WINDOW; core-v750 (homepage hero thread): the hero's camera kept when its reel shuts the drawer; core-v749 (TAURUS thread, Marek 2026-10-21): the tour's assembly step in one pass -- exploded, back together and four steps in quick succession (only the first step showed); core-v748 (TAURUS thread, Marek 2026-10-21: udelej na tento projekt ?IG prezentaci): two opt-ins for a house-line product's tour -- __PROD._tourBudget2 (the budget step as the container's: dashboard, price bar 3 stores + cheapest, the budget's SECOND item's card held until loaded, its offers, shut) and __PROD._reelAsm (the exploded view and a few assembly steps on the ?ig reel, before the budget); core-v747 (bench thread, Marek 2026-10-21: titulky jsou schovane za drawers): the tour's subtitle over any open drawer -- in the page's body, fixed, 100001 */

    /* Magenta marks what this version introduces. The guider mass is the new
       thing, so the guider is magenta until a real layer claims its part. */
    var GUIDER_COL = 0xFF00FF;

    /* THE ONE-CONSTANT GATE, NOW A HANDLE. Layers register in order and only
       those at or below this index are built, so a new assembly can be judged
       with everything after it hidden. 99 means build everything.

       It was a constant edited by hand, which meant the one thing every layer
       wants -- see it with nothing standing in front of it -- needed a redeploy.
       It is state now, and cf-btn-assembly walks it. Session only: a configurator
       that reopens half built would look broken rather than instructive. */
    /* -------------------------------------------------------------------------
       WHAT A LAYER IS FOR, IN SEVEN WORDS THAT NEVER CHANGE.

       A layer's id names the thing that builds it -- adu-stud-wall, barndo-purlin
       -- so it is different in every product, and anything keyed by it dies at the
       first fork. Twelve products are about to come out of this one.

       The role is the shared word. A drawing template that says "structure at 50
       per cent" means the same in every house in the line, and a saved sheet keeps
       working after a layer is renamed, reordered or split in two.

       Fixed list, defined once, here. A product classifies its own layers into it
       and adds nothing to it.
       ------------------------------------------------------------------------- */
    var LAYER_ROLES = ['nosna', 'oplasteni', 'pricky', 'otvory', 'strecha', 'tzb', 'fitout'];

    function isRole(r) {
      for (var i = 0; i < LAYER_ROLES.length; i++) if (LAYER_ROLES[i] === r) return true;
      return false;
    }


    /* the order the assembly walk takes through this product's layers */
    /* THE ASSEMBLY TABLES ARE PRODUCT DATA -- they are nothing but this
       product's own layer ids, in this product's own build order, so they are
       assigned under END CORE BLOCK. The core keeps the four empty vars above
       and reads whatever it is given. */

    var IN = 0.0254, FT = 0.3048;

    /* -------------------------------------------------------------------------
       THE HEIGHT STACK, bottom up, and every product inherits it. A number here
       is a zone, not a construction: SLAB is the ground floor structure whatever
       it turns out to be made of, INTER is the zone between two storeys.
       ------------------------------------------------------------------------- */
    var STACK = {
      slab:  0.35,          /* grade to top of ground floor deck */
      inter: 0.40,          /* floor zone between storey n and n+1 */
      plate: 0.14,          /* top plate / ring beam zone at each wall head */
      parapet: 0.55         /* flat roof only */
    };

    var CLEARS = { metric: [2.60, 2.80, 3.00], imperial: [8 * FT, 9 * FT, 10 * FT] };

    /* WHAT AN ADU IS, AND WHERE THE BUNGALOW BEGINS.

       I first proposed 10 x 20 m. That is 200 square metres -- not an accessory
       dwelling but a house, and he is right that it would want a different
       heating system, a different plan and different engineering. Two products,
       not one stretched.

       An ADU is the garage conversion, the garden annexe, the flat for a parent:
       one or two bedrooms, 40 to 90 square metres. So:

         WIDTH  4 to 8 m    -- 8 m spans comfortably on the truss the catalogue
                               stocks, and is about as wide as a single-storey
                               annexe gets before it reads as a house
         LENGTH 4 to 12 m   -- 96 m2 at the extreme, inside the 1200 sq ft
                               (111 m2) that California caps an ADU at

       AND THE SECTIONS STAY AS THEY ARE. I was about to step them down for the
       small end, and the catalogue says otherwise: below 60 x 160 the next
       stocked size is 60 x 120, which is a 120 mm wall -- too thin to insulate a
       dwelling properly. Today's 60 x 160 is the right section for THIS size of
       building; it only looked generous while the limits pretended a 22 m span
       was possible.

       That is the honest version of "slabsi profily": there is nothing to take
       away here, because the sizes were already matched to a building this size.
       What was wrong was the envelope around them (Marek).

       HEATING follows the same line: up to about 90 m2 a split with two or three
       indoor units does the whole dwelling, which is the simple system an ADU
       wants. Past that it needs zoning and recovery -- and that is the bungalow. */
    var LIMITS = { minW: 4.0, maxW: 8.0, minL: 4.0, maxL: 12.0 };
    /* core-v75: THE CLEAR HEIGHT IS A PRODUCT NUMBER TOO, and it was five
       literals in the height slider until the first fork asked for a shed at
       2.0 to 2.6. The default is the ADU's own range, so a product that says
       nothing behaves exactly as before. */
    /* up to five metres (core-v356, Marek): a hall, a shop or the storey a
       masonry house gets beams or steel joists in instead of a ring beam */
    /* the wall may go to six metres of masonry over two storeys (core-v407):
       each storey's clear height is set on the Floorplan step, and this is
       the ceiling of the one-storey slider in Roof and Base */
    var CLEAR_LIM = { min: 2.40, max: 6.00 };

    var STATE = {
      origin:   { x: 0, z: 0 },
      footprint: { w: 9.0, l: 12.0 },
      /* A SECOND RECTANGLE, FOR THE PLANS THAT ARE NOT ONE (core-v308).

         An L or a T is two rectangles meeting, not a new kind of outline, so
         the core keeps the second one beside the first and poly() puts them
         together. Off by default, which is every product built so far; a
         product turns it on and the rest of the core -- the wall segments, the
         rooms, the plan, the roof planes -- sees the shape it makes, because
         all of them already read poly() rather than the rectangle. Built for
         the T house (Marek: T house, vzdy jen T). */
      wing:     { on: false, w: 5.0, l: 4.0, side: 'z1', at: 0 },
      polygon:  null,

      /* STOREYS AND ROOF ARE PARAMETERS, NOT PRODUCTS. They live in the core from
         day one so that the two-storey product and the flat-roof product are a
         different answer to the same question rather than a rewrite of it. */
      storeys:  1,
      clearH:   [2.80, 2.60],
      /* openings cut into any wall, kept with the project (core-v348) */
      holes:    [],
      /* which storey the plan shows (core-v401) */
      planStorey: 0,
      /* THE FINISHED FLOOR ABOVE GRADE, when the product lets it be set
         (core-v336): null is the product's own default, a number is what the
         floor-level slider on Roof & Base left there. Saved with the project. */
      floorLevel: null,

      roof: {
        type:  'gable',        /* gable | flat */
        pitch: 26,
        axis:  'l',            /* ridge runs along the length */
        eave:  0.60,

        /* AND THE SAME REACH ON EACH SIDE SEPARATELY.

           One eave number gives the same overhang all the way round, so the deep
           side that earns a post and the shallow side that does not cannot both
           exist -- and a post could never appear, because 450 mm carries itself
           and 450 mm was all there was.

           Four numbers, dragged by the corners of a second rectangle, the way
           the shed does it. eave stays as what they all start at. */
        over:  { x0: 0.60, x1: 0.60, z0: 0.60, z1: 0.60 },
        rake:  0.40,
        /* IS THERE A PARAPET. Off means a plain overhanging eave with a drip and
           a gutter, which is cheaper and fails less. On means the wall studs run
           past the roof and the covering turns up against them.
           It is a roof option because it is a roof decision, but it is BUILT by
           the wall layer -- see there for why. */
        parapet: true,

        /* WHERE THE ROOF SPRINGS FROM. null means the head of the topmost wall,
           which is every ordinary house. A number puts the eave anywhere -- at
           floor level the roof reaches the ground, part way up it stands on a
           knee wall. One field, and the roof stops assuming there is a wall
           under it. */
        eaveY: null
      },

      openings:  [],
      /* PER-SEGMENT WALL HEIGHT, keyed by segment id. Empty means every segment
         takes its storey's clear height, which is what a plain house does. A knee
         wall, or a building where two of the four walls are not walls at all, is
         this map with two entries -- not a second product.
         It is in the contract from the first version so the segment API never has
         to be rewritten to gain it. */
      segH:      {},
      parts:     [],
      colorView: 0,
      /* THE PRODUCT'S OWN SETTINGS, IN ONE BAG. Every product field used to need
         a line here and a line in payload(), and one of the two was forgotten
         every time (the covering, then the cladding). A product writes into
         STATE.product and the bag is saved and restored whole. */
      product: {},
      doors:     [],
      roomNames: [],
      cutsOn:    false,
      doorTool:  false,

      /* WHERE IT IS BEING BUILT. climateZone is the frost depth in metres, which
         is what sets how deep the footings go; mapLocked pins the map so scene
         interaction cannot drag the ground out from under it. */
      climateZone: 0,
      mapLocked:   false,

      /* HOW FAR THE FINISHED FACE STANDS OUT FROM THE FRAME. Battens, cladding
         and, on an insulated wall, the outboard insulation. The plan polygon is
         the outer face of the FRAME, so nothing that reads as the outside of the
         building sits on that line -- and an eave measured from it stops short of
         the wall it is supposed to shelter. */
      facadeThk:   0.145,

      /* WHICH WAY THE CLADDING RUNS. It is a real choice with a real cost, not a
         skin: vertical boards need a horizontal counter-batten under them,
         because the battens they would otherwise land on run the same way they
         do. So the wall gets 40 mm thicker and facadeThk moves with it -- which
         is exactly what facadeThk is for, and why the eave is measured from it
         rather than from the frame. */
      /* UNSET UNTIL CHOSEN (core-v497): a product names its own default in
         __PROD._claddingDefault -- a masonry house starts rendered ('off'),
         a boarded house starts horizontal -- and cladKind() reads it; a
         saved project keeps what it saved */
      cladding:    null,   /* off | horizontal | vertical, or null = the product's default */

      /* WHAT IT STANDS ON. Five, because that is what the market asks for and no
         one of them covers it: screws, pads, piers, strips, slab.

         SLAB IS NOT JUST ANOTHER FOOTING. The other four hold a timber floor up
         in the air; a slab IS the floor, so choosing it takes the joists away.
         The floor layer has to answer that, which is why this lives in the core
         and not in whichever layer builds the concrete. */
      foundation:  'piers',

      /* A flat ceiling under a mono-pitch. Off by default: the raked soffit is
         the reason that roof gets chosen. */
      flatCeiling: false,

      /* WHAT IS ON THE ROOF. It was never declared here at all -- the picker set
         STATE.covering and every reader found undefined, fell back to shingle,
         and the choice did not survive the first rebuild. Nothing threw, the
         button lit up, and the battens simply never appeared.

         A field the product reads has to be a field the product HAS: declared
         here it is saved, restored, and defaulted in one place. */
      covering:    'shingle',

      /* THE WALLS BETWEEN THE POSTS. Declared here for the reason spelled out
         just above: a field the product reads has to be a field the state HAS,
         or _applyPayload has nowhere to put it back and every reload hands the
         reader the default. sides is keyed by the side that has posts, kind is
         one of clad_h / clad_v / slat_h / slat_v, gap is the slat spacing in
         metres, and an empty colour means "the same as the facade". */
      postWall:    { sides: {}, kind: 'clad_v', gap: 0.04, color: '' },

      /* external roller shutters, on or off for the whole house; in the state
         AND in the payload, which is the lesson two fields above paid for */
      blinds:      false,

      /* What the building is standing in. 'none' leaves the aerial photograph
         showing; the other two replace it with trees. */
      scenery:     'none',
      /* the trees of the scenery: where each stands, its size, turn and
         shade; made once from a seed, then only ever moved by hand */
      trees:       {},

      /* THE WIDEST THE ROOF CAN CROSS. Zero means no limit -- a trussed gable
         spans whatever it is made to span. A rafter cannot: it is one piece of
         timber, and past its span it sags whatever is drawn on the screen.
         The product sets this when the roof changes. */
      maxSpan:     0,

      /* WHICH STAGE THE EYE IS ON. Stage 0 is the finished build; the stages
         after it are ways of looking INTO it, and each one may name the layers it
         wants left on. The list itself is the product's -- a sauna and a house
         have nothing to look at in common -- so the core only carries the index. */
      vizStage: 0,

      /* The scale figure. Its position is remembered because where somebody
         stood the person is part of the picture they made. */
      bodyVisible: false,
      bodyX:       null,
      bodyZ:       null,

      view:     '3d',
      mode:     1,
      isMetric: false
    };

    var DEFAULTS = JSON.parse(JSON.stringify(STATE));

    function _log(msg) {
      try {
        if (global.Configurator && global.Configurator.log && global.Configurator.log.log) {
          global.Configurator.log.log('[' + LOG_TAG + '] ' + msg);
        }
      } catch (e) {}
    }
    function $(id) { return document.getElementById(id); }
    function clamp(v, lo, hi) { return v < lo ? lo : (v > hi ? hi : v); }

    var _i18n = function (k, f) { return f; };
    /* A LABEL IN SHOUTING CAPS, TURNED BACK INTO A LABEL.

       configurator.edit is stored as "EDIT" while place, close and cancel beside
       it are "Place", "Close", "Cancel" -- so every translation inherited the
       caps and the panel read UPRAVIT next to buttons that did not shout.

       Fixing the English string would leave every other language still shouting,
       and there are twenty of them. Normalising here fixes all of them at once
       and keeps working if a translator sends caps back.

       A string is only reshaped when it is ALL caps. "Roof covering" is left
       alone, and so is a genuine acronym in the middle of a sentence, because
       the test is that the whole thing has no lower case in it. */
    /* a log line that only appears when the reasoning is wanted */
    function _why(ctx, msg) { if (!TERSE) ctx.log(msg); }

    function _tCap(key, def) {
      var v = _t(key, def);
      if (typeof v !== 'string' || !v) return v;
      if (v !== v.toUpperCase() || v.length < 3) return v;
      if (!/[A-Za-z]/.test(v)) return v;
      /* AND NOT A SHORT TOKEN. "2D" is a label, not shouting, and lower-casing it
         to "2d" would be a fault introduced by the fix -- which the test caught
         before it shipped. Three letters or more, and it has to be all letters:
         an acronym like AR or a code like R60 keeps its case. */
      if (!/^[A-Za-z]+$/.test(v.replace(/\s+/g, ''))) return v;
      return v.charAt(0) + v.slice(1).toLowerCase();
    }

    /* core-v573: Shopify's t filter escapes a text it hands over (don&#39;t,
       Windows &amp; doors); shown as text the entity stood there as it was.
       The apostrophe and the ampersand only, decoded once -- a quote or a
       bracket stays as it came, so a text built into an attribute or into
       markup is never opened. */
    function _tDeent(v) {
      if (typeof v !== 'string' || v.indexOf('&') < 0) return v;
      return v.replace(/&#0*39;|&#x0*27;|&apos;/gi, "'").replace(/&amp;/g, '&');
    }
    function _t(k, f) { try { return _tDeent(_i18n(k, f) || f); } catch (e) { return f; } }

    /* Room labels are drawn UPPER CASE. The asset asks its host for the words, so
       the case is decided here -- room keys only, nothing else. Copied from tiny. */
    function _planT(k, f) {
      var v = _t(k, f);
      if (typeof v === 'string' && (/^rooms\./.test(k) || /^container_room_\d+$/.test(k))) {
        return v.toUpperCase();
      }
      return v;
    }

    function _stage() { return global.ConfiguratorStage || null; }
    function _UI() { return global.ConfiguratorUI || null; }
    function _FL() { return global.FloorplanLib || null; }

    /* -------------------------------------------------------------------------
       GRID AND SNAPPING. One structural grammar, two moduli: metric steps 600 for
       the 1200 sheet, imperial steps 24 inches on centre for the 4x8 sheet.
       Everything that steps asks this, never a literal.
       ------------------------------------------------------------------------- */
    /* THE SHEET IS THE MODULE, AND EVERYTHING ELSE FOLLOWS FROM IT.

       Framing is not spaced at a round number for its own sake -- it is spaced so
       that the edge of every board lands on a member. Get that wrong by 25 mm and
       every sheet on the building has to be ripped, which costs both the offcut
       and the cut.

       So the sheet width sets the spacing, and it also sets what the plan SNAPS
       to. That second half is what was missing: the plan snapped to half a bay,
       which let the building end mid-sheet and threw away a strip down two whole
       elevations. Snapping to the sheet means a wall is always a whole number of
       boards wide.

         imperial   4 x 8 ft, framing at 24 in -- two bays to a sheet
         metric     1250 x 2500, framing at 625 -- the same two bays

       THE METRIC BOARD IS 1250 WIDE, NOT 1200.

       It was set to 1200, which makes the module 600 -- and 600 is a round number
       rather than a sheet fraction. Every board then ended 50 mm past a stud, and
       after four of them the error is 200 mm: a wall of 3680 mm came out with a
       22 mm sliver at the corner, which is not a bay, not buildable and not worth
       drawing. 1250 and 625 put the board's edge back on the stud, which is the
       entire reason the module exists (Marek: aby se stavba prizpusobila zase
       metrickemu systemu).

       A tighter 16 in / 400 module is the other common choice and divides the
       same sheet; it belongs to a product that needs the extra stiffness, not
       here. */
    var SHEET = {
      metric:   { w: 1.250, l: 2.500 },
      imperial: { w: 48 * IN, l: 96 * IN }
    };
    function sheet() { return STATE.isMetric ? SHEET.metric : SHEET.imperial; }
    function GRID() { return sheet().w / 2; }
    /* said once at load, because every remainder on every wall traces back here */
    try {
      _say('truss module sheet ' + Math.round(sheet().w * 1000) + ' x ' +
           Math.round(sheet().l * 1000) + ' mm, framing at ' +
           Math.round(GRID() * 1000) + ' mm, two bays to a board');
    } catch (eMOD) {}
    /* THE DRAG SNAPS TO THE FRAMING MODULE, NOT THE WHOLE BOARD.

       Snapping to the board meant the only sizes on offer were 6.10, 7.32, 8.54 --
       a metre and a fifth apart. The building jumped a whole board at a time and
       the number under your finger jumped with it, which is what looked like the
       dimension lagging.

       Half a board is the framing module, so every board edge still lands on a
       member; what a half-board size costs is one ripped board at the end of each
       run, not a ripped strip down the whole elevation. The snapshot says which
       of the two you have, so the choice is visible rather than made for you. */
    /* THE STEP IS HALF A BAY, so the number moves every time you do.

       A whole bay is 2 ft, and a plan that only lands on 22, 24, 26 makes a drag
       feel broken: you move, nothing happens, you move again and it jumps two
       feet. The dimension was right both times -- there was simply no size
       between them to show.

       Half a bay costs nothing structural. Studs still run at full bay centres
       from one end; the wall just ends on a short last bay, which is what the end
       stud is for anyway. What it does cost is a ripped board where the length is
       an odd number of half-bays, and the snapshot says so on the plan line, so
       the trade is visible rather than made quietly. */
    /* =======================================================================
       WALL_LAYOUT -- ONE PLACE THAT DECIDES WHERE THINGS STAND.

       The stud spacing, the plan's step and the position of an opening are three
       answers to the same question, and they were being worked out in three
       different files. That is why a window could be snapped by the core, moved by
       the product and drawn from a third number: nothing was wrong on its own and
       nothing agreed.

       The rules, in full:

         1  the sheet sets the module -- 1250 board, 625 module; 4 ft board, 24 in
         2  a wall's studs: one at each end, half a thickness in, and the run
            between them divided into the whole number of bays nearest the module.
            Every bay is identical. No exceptions anywhere, ever.
         3  the plan steps by one bay, so one step is one stud more or less
         4  an opening is pushed until one of its edges touches the face of one of
            those studs, from whichever side moves it less

       Products do not compute any of this. They ask, and they build what they are
       told (Marek: melo by se to cele stavet, nebo predat do ui.js). */
    var WALL_LAYOUT = {
      studT: 0.06,
      /* THE UNIT IS FITTED INTO ITS HOLE WITH A GAP EACH SIDE.

         A window is drawn -- and built -- 10 mm narrower than the structural
         opening, because that is the room it is fitted with. Snapping the OPENING
         to a stud therefore leaves the visible frame 10 mm off the timber every
         time, which is the gap he has been pointing at all night. Worse, it is
         always the same 10 mm on the same side, so the product's correction always
         pulled the same way and an opening could only ever attach on its left.

         So what is brought against the stud is the frame, not the hole. */
      frameTol: 0.010,

      module: function () { return GRID(); },

      /* every stud line on a wall of this length, in its own coordinates */
      studs: function (len) {
        /* core-v800: a product may frame closer than the sheet module (RUSSWORX
           SHED's 2x4 at 16 in., __PROD._studStep) -- a sheet edge still lands
           on a stud, 4 ft being three bays of 16 in.; no hook, the module */
        var g = GRID(), t = this.studT, out = [];
        try { if (__PROD && typeof __PROD._studStep === 'function') { var gS = __PROD._studStep(); if (gS > 0.2 && gS < g) g = gS; } } catch (eSS) {}
        if (!(len > 0.001)) return out;
        var lo = t / 2, hi = len - t / 2, span = hi - lo;
        if (!(span > 0.001)) return [lo];
        var n = Math.max(1, Math.round(span / g)), step = span / n, k;
        for (k = 0; k <= n; k++) out.push(lo + step * k);
        return out;
      },

      /* the bay this layout actually comes out at -- the module exactly when the
         wall is a whole number of bays, and the nearest even division when it is
         not */
      bay: function (len) {
        var p = this.studs(len);
        return (p.length > 1) ? (p[1] - p[0]) : 0;
      },

      /* a plan dimension steps by one bay: one step, one stud */
      snapLen: function (v) {
        /* a product may step its plan its own way -- the A-frame goes by
           50 mm and 2 in, not by half a sheet (core-v337) */
        var g = (__PROD && typeof __PROD._planStep === 'function') ? (__PROD._planStep() || GRID()) : GRID();
        return Math.max(g, Math.round(v / g) * g);
      },

      /* an opening's centre, brought up against the nearest stud face -- of the
         studs this wall really has, when the segment is given */
      openingAt: function (len, at, w, seg) {
        var lines = seg ? this.studsFor(seg) : this.studs(len), t = this.studT, q;
        /* the frame is what has to touch, so the frame is what is measured; the
           hole then sits symmetrically around it as it always did */
        var wf = Math.max(0, (w || 0) - this.frameTol * 2);
        var edgeL = at - wf / 2, edgeR = at + wf / 2;
        var best = at, bestD = 1e9;
        for (q = 0; q < lines.length; q++) {
          var c = lines[q];
          var faceR = c + t / 2, faceL = c - t / 2;
          if (Math.abs(edgeL - faceR) < bestD) { bestD = Math.abs(edgeL - faceR); best = faceR + wf / 2; }
          if (Math.abs(edgeR - faceL) < bestD) { bestD = Math.abs(edgeR - faceL); best = faceL - wf / 2; }
        }
        return best;
      },

      /* A WALL THAT BUTTS INTO ANOTHER DOES NOT OWN THE CORNER.

         Its frame runs between the inner faces of the two walls it butts into,
         so its end studs stand half a thickness inside THAT span, not inside the
         segment's outer ends -- where the other wall's end stud already is. The
         studs in between keep the whole-segment layout, because the sheathing
         is set out from the outer corner and its board edges have to land on
         them (Marek: panel ma koncit mezi stenovymi prvky dlouhe strany; roztec
         aby byly co nejlepe oplastene).

         Which walls butt is the product's decision, so the product answers it:
         endInset(seg) returns [from start, from end] in metres, or null for a
         wall that runs through. Without it every wall is laid out as before. */
      endInset: null,

      studsFor: function (seg) {
        if (!seg) return [];
        var out = this.studs(seg.len), t = this.studT, ins = null, q;
        if (typeof this.endInset === 'function') {
          try { ins = this.endInset(seg); } catch (eIn) { ins = null; }
        }
        if (!ins || out.length < 2) return out;
        var e0 = (+ins[0] || 0) + t / 2, e1 = seg.len - (+ins[1] || 0) - t / 2;
        if (!(e1 - e0 > t)) return out;
        var res = [e0];
        for (q = 1; q < out.length - 1; q++) {
          if (out[q] >= e0 + t - 1e-6 && out[q] <= e1 - t + 1e-6) res.push(out[q]);
        }
        res.push(e1);
        return res;
      },

      /* which stud an opening is leaning on, for anything that wants to show it */
      openingStud: function (len, at, w, seg) {
        var lines = seg ? this.studsFor(seg) : this.studs(len), t = this.studT, q;
        var best = null, bestD = 1e9;
        for (q = 0; q < lines.length; q++) {
          var c = lines[q];
          var d = Math.min(Math.abs(c + t / 2 + (w || 0) / 2 - at),
                           Math.abs(c - t / 2 - (w || 0) / 2 - at));
          if (d < bestD) { bestD = d; best = c; }
        }
        return best;
      },

      /* ===================================================================
         THE FRAME OF A WALL -- the doubled posts round an opening, its header
         and sill, what is left of the studs it crosses, and the frames the
         wall travels as. Moved here from the ADU lib (core-v252) so the shed
         and the sauna frame a wall exactly as the ADU does, from one copy of
         the arithmetic. Pure: stud lines, opening records and the product's
         own section in; positions out. Nothing here draws or bills.

         The product passes its stud thickness t (and for the header its ply
         and plies); the numbers below are his rules and are the same for
         every product of the line. */
      frame: {
        take:   0.050,   /* an existing stud this close to the frame edge IS the jack / king (Marek: bliz nez 5 cm) */
        seam:   0.006,   /* the drawn gap that shows a doubled member is two (Marek: spary) */
        stitch: 0.400,   /* a doubled member is nailed through along its length at this spacing */
        len:    3.500,   /* what goes on the lorry (Marek: prepravni delka cca 3,5 m) */
        tol:    0.175,   /* "about": up to 5 percent over when that saves a whole frame */
        fit:    0.010    /* the unit's frame sits this far inside its structural opening, each side */
      },

      /* THE DOUBLED POST EACH SIDE OF AN OPENING -- WHERE ITS TWO MEMBERS STAND.

         Per side the frame edge looks outward for the nearest stud face (gap g).
         jack: an existing stud within take of the edge IS the jack (the rough
         opening is then that much wider, which is allowed); otherwise a new one
         against the frame. king: outside the jack, face to face; an existing
         stud within take of that place IS the king, otherwise a new one. A new
         member needs a stud's width of room, so below t the existing stud is
         taken too. Nothing stands past the wall's own end studs. */
      dblPlan: function (len, studs, at, wf, tIn, kTakeIn) {
        var t = tIn || this.studT, take = Math.max(this.frame.take, t), out = {}, dir, q;
        /* core-v804: a product may borrow a layout stud as a KING only when it
           stands that close to the king's place (RUSSWORX SHED: touching --
           a king a few centimetres off its jack is not nailed to it); the jack
           keeps the take */
        var kTake = (typeof kTakeIn === 'number' && kTakeIn >= 0) ? kTakeIn : take;
        for (dir = -1; dir <= 1; dir += 2) {
          var edge = at + dir * wf / 2, best = null, g = 1e9;
          for (q = 0; q < studs.length; q++) {
            var face = studs[q] - dir * t / 2;
            var d = dir * (face - edge);
            if (d > -0.0005 && d < g) { g = Math.max(0, d); best = studs[q]; }
          }
          var sd = { gap: (best === null) ? null : g, jack: null, king: null };
          if (best !== null && g < take) {
            sd.jack = { x: best, isNew: false, gap: g };
            sd.king = { x: best + dir * t, isNew: true, gap: 0 };
          } else {
            var jx = edge + dir * t / 2;
            sd.jack = { x: jx, isNew: true, gap: 0 };
            var h = (best === null) ? 1e9 : (g - t);
            if (best !== null && h < kTake) sd.king = { x: best, isNew: false, gap: h };
            else sd.king = { x: jx + dir * t, isNew: true, gap: 0 };
          }
          /* THE FRAME ROUND AN OPENING ALWAYS CLOSES (core-v297).
             A jack or a king that came out past the LAST MODULE STUD was thrown
             away, and with a full-height glazing sitting hard against a corner
             that is exactly where both of them land: the panel then had the
             glazing, the two plates and nothing standing at its end -- an open
             frame, in the drawing and in the model (Marek: vzdycky tam ma byt
             uzavreny ram kolem okna, chybi i v modelu).
             A member that falls outside is now brought back to the last place it
             fits INSIDE THE WALL ITSELF -- half a stud in from the end -- rather
             than dropped. Only a king with no room left beside its own jack goes,
             because two posts cannot stand in one place; the jack, the one that
             carries the header, always stands. */
          var wLo = t / 2, wHi = len - t / 2;
          if (sd.jack.isNew && (sd.jack.x < wLo - 1e-6 || sd.jack.x > wHi + 1e-6)) {
            sd.jack.x = Math.min(wHi, Math.max(wLo, sd.jack.x));
            sd.jack.clamped = true;
          }
          if (sd.king.isNew && (sd.king.x < wLo - 1e-6 || sd.king.x > wHi + 1e-6)) {
            sd.king.x = Math.min(wHi, Math.max(wLo, sd.king.x));
            sd.king.clamped = true;
          }
          /* AND IT IS A NEW MEMBER, NOT A MODULE STUD BORROWED (core-v298).
             Handing the role to a module stud standing at that place looked
             tidy, but at a wall end that stud is inside the glazing and gets cut
             into cripples -- so the side that was open before lost its post too,
             and the panel came out with two plates and nothing between them
             (Marek: misto aby se pridalo, tak se ubralo). A clamped jack or king
             is always built. */
          if (sd.king && sd.jack && Math.abs(sd.king.x - sd.jack.x) < t - 1e-6) sd.king.out = true;
          out[dir < 0 ? 'L' : 'R'] = sd;
        }
        return out;
      },

      /* THE WHOLE FRAME ROUND EACH OPENING OF A WALL. Jacks and kings full
         height; header and sill BETWEEN the jacks (never notched into them),
         each opt.plies plies of opt.ply laid flat; the header's underside on the
         unit's frame top, the sill's top under its bottom. A door has no sill and
         the sole plate runs through under it. topAt(x) is the underside of the
         top plates at x, base the top of the sole plate. */
      framePlan: function (seg, studs, topAt, base, opt) {
        opt = opt || {};
        var t = opt.t || this.studT, ply = opt.ply || t, plies = opt.plies || 2;
        var tol = this.frame.fit, out = [], q;
        var ops = seg.openings || [];
        for (q = 0; q < ops.length; q++) {
          var o = ops[q], wf = (o.w || 0) - tol * 2;
          var P = this.dblPlan(seg.len, studs, o.at, wf, t, opt.kingTake);
          var f = { o: o, floor: openingOnFloor(o), plan: P, wf: wf,
                    eL: o.at - wf / 2, eR: o.at + wf / 2 };
          var jL = P.L.jack, jR = P.R.jack;
          var okJL = jL && !jL.out, okJR = jR && !jR.out;
          f.jiL = okJL ? jL.x + t / 2 : f.eL;
          f.jiR = okJR ? jR.x - t / 2 : f.eR;
          f.xh0 = f.jiL;
          f.xh1 = f.jiR;
          f.yFB = seg.y0 + (o.sill || 0) + tol;
          f.yFT = seg.y0 + (o.sill || 0) + (o.h || 0) - tol;
          var hMax = Math.min(topAt(f.xh0), topAt(f.xh1));
          /* A BEAM OVER A WIDE OPENING (core-v606, RANCH thread): the product
             may hand a header depth per opening (opt.headD(o, seg) -> metres,
             or 0 for the plies laid flat). A beam is never replaced by the
             plate -- it sits on the jacks over the unit. Where the wall leaves
             less room over the unit than the beam is deep, no beam is built
             (it would cut into the unit): the opening keeps the header it had
             and f.beamShort says by how much the beam did not fit, for the
             product to report. */
          var hdB = 0;
          if (typeof opt.headD === 'function') { try { hdB = +opt.headD(o, seg) || 0; } catch (eHD) { hdB = 0; } }
          f.beam = hdB > 0 ? hdB : 0;
          f.beamShort = 0;
          if (f.beam && (hMax - f.yFT) < f.beam - 1e-6) { f.beamShort = f.beam - Math.max(0, hMax - f.yFT); f.beamWant = f.beam; f.beam = 0; }
          f.yHB = f.yFT;
          f.yHT = f.yHB + (f.beam || ply * plies);
          f.headShort = 0;
          f.plateHead = false;
          if (f.beam) {
            if (f.yHT > hMax + 1e-6) { f.headShort = f.yHT - hMax; f.yHT = hMax; f.yHB = hMax - f.beam; }
          } else if (!f.floor && (hMax - f.yFT) < ply * plies - 1e-6) {
            /* THE TOP PLATE IS THE HEADER: the window stands right under it (or
               the rule above put it there), so no header of its own is built and
               nothing stands above the hole but the plate */
            f.plateHead = true;
            f.yHB = f.yHT = hMax;
            f.xh1 = f.xh0;                    /* no header span: the product draws none */
          } else if (f.yHT > hMax + 1e-6) {
            f.headShort = f.yHT - hMax;
            f.yHT = hMax;
            f.yHB = hMax - ply * plies;
          }
          if (f.floor) { f.sillPlies = 0; f.ySB = -1e9; }
          else {
            var room = f.yFB - base;
            f.sillPlies = (room >= ply * 2 - 1e-6) ? 2 : ((room >= ply - 1e-6) ? 1 : 0);
            f.ySB = f.sillPlies ? (f.yFB - ply * f.sillPlies) : -1e9;
          }
          out.push(f);
        }
        return out;
      },

      /* WHAT IS LEFT OF A MODULE STUD AT x ONCE THE OPENINGS HAVE TAKEN THEIR
         PART. In the hole: a cripple under the sill and one over the header (a
         door: only the one over). A stud the unit's frame reaches into gives way
         to the full-height jack beside it. Anything else stays full height. */
      studPieces: function (x, base, top, frames, tIn) {
        var t = tIn || this.studT, bands = [], role = 'full', q, sq, sides = ['L', 'R'];
        for (q = 0; q < frames.length; q++) {
          var f = frames[q];
          var inHole = (x + t / 2 > f.eL + 0.0005) && (x - t / 2 < f.eR - 0.0005);
          if (inHole) {
            var underJack = false;
            for (sq = 0; sq < 2; sq++) {
              var jk = f.plan[sides[sq]].jack;
              if (jk && jk.isNew && !jk.out && Math.abs(jk.x - x) < t - 0.0005) underJack = true;
            }
            bands.push(underJack ? [-1e9, 1e9] : [f.ySB, f.yHT]);
            role = underJack ? 'gave way' : 'hole';
            continue;
          }
        }
        var pcs = [[base, top]], b, p, nx;
        for (b = 0; b < bands.length; b++) {
          nx = [];
          for (p = 0; p < pcs.length; p++) {
            var lo = pcs[p][0], hi = pcs[p][1], b0 = bands[b][0], b1 = bands[b][1];
            if (b1 <= lo || b0 >= hi) { nx.push(pcs[p]); continue; }
            if (b0 > lo) nx.push([lo, b0]);
            if (b1 < hi) nx.push([b1, hi]);
          }
          pcs = nx;
        }
        return { pcs: pcs, role: role };
      },

      /* THE FRAMES THE WALL TRAVELS AS. The fewest frames that fit on the lorry
         (up to tol over when that saves a frame), then the most even. A joint is
         a module stud doubled by a new one beside it, or an opening's own jack
         and king -- never inside an opening's frame, never where the second stud
         has no room. Every way through the candidates is weighed at once. */
      frameJoints: function (len, from, to, studs, frames, tIn) {
        var t = tIn || this.studT, F = this.frame;
        var L = to - from, n = Math.ceil(L / (F.len + F.tol) - 1e-6), out = [], q, k;
        if (!(n > 1)) return out;
        var busy = [], sides = ['L', 'R'], roles = ['jack', 'king'], f, a2, b2;
        for (q = 0; q < frames.length; q++) {
          for (a2 = 0; a2 < 2; a2++) {
            for (b2 = 0; b2 < 2; b2++) {
              var mm2 = frames[q].plan[sides[a2]][roles[b2]];
              if (mm2 && !mm2.out) busy.push(mm2.x);
            }
          }
        }
        function clear(x2, skip) {
          if (x2 < studs[0] - 1e-6 || x2 > studs[studs.length - 1] + 1e-6) return false;
          var r;
          for (r = 0; r < studs.length; r++) {
            if (r !== skip && Math.abs(studs[r] - x2) < t - 0.0005) return false;
          }
          for (r = 0; r < busy.length; r++) if (Math.abs(busy[r] - x2) < t - 0.0005) return false;
          for (r = 0; r < frames.length; r++) {
            if (x2 + t / 2 > frames[r].jiL - 0.0005 && x2 - t / 2 < frames[r].jiR + 0.0005) return false;
          }
          return true;
        }
        var cand = [];
        for (q = 1; q < studs.length - 1; q++) {
          var c = studs[q], taken = false;
          if (c <= from + t || c >= to - t) continue;
          for (k = 0; k < busy.length; k++) if (Math.abs(busy[k] - c) < 1e-4) taken = true;
          for (k = 0; k < frames.length; k++) {
            f = frames[k];
            if (c + t / 2 > f.jiL - 0.0005 && c - t / 2 < f.jiR + 0.0005) taken = true;
          }
          if (taken) continue;
          if (clear(c + t, q)) cand.push({ c: c, side: 1, x2: c + t, joint: c + t / 2 });
          if (clear(c - t, q)) cand.push({ c: c, side: -1, x2: c - t, joint: c - t / 2 });
        }
        for (q = 0; q < frames.length; q++) {
          for (a2 = 0; a2 < 2; a2++) {
            var pj = frames[q].plan[sides[a2]], dirP = a2 ? 1 : -1;
            if (!pj.jack || !pj.king || pj.jack.out || pj.king.out) continue;
            var jtP = (pj.jack.x + pj.king.x) / 2;
            if (jtP <= from + t || jtP >= to - t) continue;
            cand.push({ c: pj.jack.x, side: dirP, x2: pj.king.x, joint: jtP, pair: true });
          }
        }
        cand.sort(function (u, v) { return u.joint - v.joint; });
        var P = [{ joint: from }].concat(cand, [{ joint: to }]), N = P.length, best = [], i2, j2;
        var MIN_FRAME = t * 5;
        function cost(len2) {
          var over = len2 - F.len, pen = 0;
          if (over > F.tol + 1e-6) pen = 1e6 + over * 1e4;
          else if (over > 1e-6) pen = over * 2e3;
          return pen + 1e3 + len2 * len2;
        }
        best[0] = { c: 0, prev: -1 };
        for (j2 = 1; j2 < N; j2++) {
          best[j2] = null;
          for (i2 = 0; i2 < j2; i2++) {
            if (!best[i2]) continue;
            var sgm = P[j2].joint - P[i2].joint;
            if (sgm < MIN_FRAME && !(i2 === 0 && j2 === N - 1)) continue;
            if (sgm < 1e-6) continue;
            var cc = best[i2].c + cost(sgm);
            if (!best[j2] || cc < best[j2].c) best[j2] = { c: cc, prev: i2 };
          }
        }
        var path = [], at2 = best[N - 1] ? best[N - 1].prev : -1;
        while (at2 > 0) { path.unshift(P[at2]); at2 = best[at2].prev; }
        out = path;
        return out;
      },

      /* WHICH FRAME A PIECE BELONGS TO, AND WHAT IT IS IN IT (core-v253).

         Moved from the ADU lib, where it was _panelMark and a dozen loose
         counters. A product makes one marker and, while it builds a wall,
         tells it the wall's frames (spans along the wall) and the role of the
         piece it is about to place; every placed body is then marked with its
         frame tag (W<wall>P<n>), its role and its cut section -- the marks the
         frame colours, the frame sheets and the plate check all read.

         The frame is decided by WHERE THE PIECE STANDS, nudged 2 mm toward the
         side it is built on so a doubled pair at a joint splits the way it is
         built (the jack with the opening, the king with its neighbour). A piece
         that runs out past its frame -- the end frame's top plate under an
         extended eave -- is marked at markAt() instead (Marek: jeden z panelu
         nema horni profil). Marking is off while the cap plate is placed: it
         belongs to no frame.

         opt.sect is the product's frame section ('60x160'); roles in opt.noSect
         (ledger, seal) are not that section. mark() returns the frame's running
         number (0-based across the building) or -1, so the product can paint
         the panels view with its own palette. */
      frameMarker: function (opt) {
        opt = opt || {};
        var sect = opt.sect || '', noSect = opt.noSect || ['ledger', 'seal'];
        var on = false, spans = [], base = 0, wall = 0, role = null, at = null;
        var S = null;
        function reset() {
          S = { bodies: 0, seen: 0, off: 0, noHit: 0, missAt: [], offWhat: [], stat: {}, roles: {} };
        }
        reset();
        return {
          beginBuild: function () { reset(); on = false; role = null; at = null; },
          beginWall: function (wallIdx, sp, firstNo) { wall = wallIdx; spans = sp || []; base = firstNo || 0; on = true; },
          setSpans: function (sp) { spans = sp || []; },
          on: function () { on = true; },
          off: function () { on = false; },
          role: function (r) { role = r || null; },
          markAt: function (x) { at = (x == null) ? null : x; },
          mark: function (m, along2, biasR) {
            S.seen++;
            if (!on) {
              S.off++;
              try {
                var pr = m && m.geometry && m.geometry.parameters;
                if (pr && S.offWhat.length < 6) {
                  S.offWhat.push(Math.round(pr.width * 1000) + 'x' + Math.round(pr.height * 1000) +
                                 'x' + Math.round(pr.depth * 1000));
                }
              } catch (eOW) {}
              return -1;
            }
            if (!m) return -1;
            if (at !== null) along2 = at;
            var t2 = (along2 == null) ? null : (along2 + (biasR ? 0.002 : -0.002)), j2, hit = -1;
            if (t2 != null) {
              for (j2 = 0; j2 < spans.length; j2++) {
                if (t2 >= spans[j2][0] - 0.001 && t2 <= spans[j2][1] + 0.001) { hit = j2; break; }
              }
            }
            if (hit < 0) {
              S.noHit++;
              if (S.missAt.length < 6) S.missAt.push(Math.round((along2 == null ? -1 : along2) * 1000));
              return -1;
            }
            var tagP = 'W' + wall + 'P' + (base + hit + 1), r = role || 'other';
            m.userData.panelTag = tagP;
            m.userData.frameRole = r;
            if (!S.roles[tagP]) S.roles[tagP] = {};
            S.roles[tagP][r] = 1;
            if (sect && noSect.indexOf(role) < 0) m.userData.cutSect = sect;
            S.bodies++;
            if (!S.stat[tagP]) S.stat[tagP] = { n: 0, lo: 1e9, hi: -1e9, span: null };
            var stp = S.stat[tagP];
            stp.n++;
            if (along2 < stp.lo) stp.lo = along2;
            if (along2 > stp.hi) stp.hi = along2;
            stp.span = spans[hit];
            return base + hit;
          },
          /* every frame has both its plates, or which one does not */
          check: function () {
            var k = Object.keys(S.roles), bad = [], q;
            for (q = 0; q < k.length; q++) {
              var rr = S.roles[k[q]], miss = [];
              if (!rr.sole) miss.push('bottom plate');
              if (!rr.top) miss.push('top plate');
              if (miss.length) bad.push(k[q] + ' has no ' + miss.join(' and no '));
            }
            return { frames: k.length, bad: bad };
          },
          stats: function () { return S; }
        };
      }
    };

    function snapDim(v, kind, lim) {
      /* A PRODUCT CAN SNAP ITS PLAN ITSELF (core-v341). A step is a lattice
         from zero, and a masonry wall's is not: its runs come out in whole
         blocks only at half a module past a whole one, so the T house hands
         over its own snap. kind names the dimension -- 'w' and 'l' of the bar,
         'wing-w', 'wing-l' and 'wing-at' of the wing -- and lim the limits it
         has to stay inside; anything that is not a number keeps the step. */
      if (kind && __PROD && typeof __PROD._planSnap === 'function') {
        var sv = null;
        try { sv = __PROD._planSnap(v, kind, lim || null); } catch (ePS) { sv = null; }
        if (typeof sv === 'number' && isFinite(sv)) return sv;
      }
      return WALL_LAYOUT.snapLen(v);
    }

    /* How many boards a run of wall or floor takes, and what is left over. The
       offcut is the number worth showing: it is the part that gets paid for and
       thrown away. */
    function sheetsFor(lengthM, heightM) {
      var sh = sheet();
      var acrossN = Math.ceil(lengthM / sh.w - 1e-6);
      var upN = Math.ceil(heightM / sh.l - 1e-6);
      var used = lengthM * heightM;
      var bought = acrossN * upN * sh.w * sh.l;
      return { n: acrossN * upN, used: used, bought: bought, waste: bought - used };
    }
    function snapClear(h) {
      var list = STATE.isMetric ? CLEARS.metric : CLEARS.imperial;
      var best = list[0], bd = 1e9, i, d;
      for (i = 0; i < list.length; i++) {
        d = Math.abs(list[i] - h);
        if (d < bd) { bd = d; best = list[i]; }
      }
      return best;
    }
    function fmtLen(m) { var U = _UI(); return U ? U.fmtDim(m, STATE.isMetric) : (m.toFixed(2) + ' m'); }
    function fmtArea(a) {
      if (STATE.isMetric) return a.toFixed(1) + ' m2';
      return Math.round(a * 10.7639) + ' sqft';
    }

    /* -------------------------------------------------------------------------
       THE PLAN POLYGON. A rectangle is the default and an arbitrary ring is
       accepted, because more than one product in the line will want one and the
       segment builder below does not care how many corners there are.
       Order is clockwise seen from above: x right, z down.
       ------------------------------------------------------------------------- */
    function poly() {
      if (STATE.polygon && STATE.polygon.length >= 3) return STATE.polygon;
      var hw = STATE.footprint.w / 2, hl = STATE.footprint.l / 2;
      var ox = STATE.origin.x, oz = STATE.origin.z;
      var R = [
        [ox - hw, oz - hl],
        [ox + hw, oz - hl],
        [ox + hw, oz + hl],
        [ox - hw, oz + hl]
      ];
      var WG = STATE.wing;
      if (!WG || !WG.on || !(WG.w > 0.3) || !(WG.l > 0.3)) return R;
      /* WHICH EDGE THE WING STANDS ON, and the wing let into it.

         The edge is walked as it is: the wing's four points are made from the
         edge's own direction and the way out of the building, so one piece of
         code does all four sides and the outline keeps its winding. The wing is
         kept inside the edge it stands on, with a return of wall left at each
         end, so the shape stays a T and does not become a cross or a step. */
      var SIDE = { 'z0': 0, 'x1': 1, 'z1': 2, 'x0': 3 };
      var e = SIDE[WG.side];
      if (e === undefined) e = 2;
      var out = [], i;
      for (i = 0; i < R.length; i++) {
        out.push(R[i]);
        if (i !== e) continue;
        var a = R[i], b = R[(i + 1) % R.length];
        var dx = b[0] - a[0], dz = b[1] - a[1], L = Math.sqrt(dx * dx + dz * dz) || 1;
        var ux = dx / L, uz = dz / L;         /* along the edge */
        var nx = uz, nz = -ux;                /* out of the building, for this winding */
        var wW = Math.min(WG.w, L - 0.6);     /* a return of wall at each end */
        /* THE WING'S PLACE IS MEASURED ON THE WORLD AXIS, NOT ALONG THE EDGE
           (core-v332). The edge the wing stands on is walked in the outline's
           own direction -- on the far side that is backwards -- so an offset
           counted along the edge put the walls on one side of the middle while
           _wingRect(), which counts on the axis, put the roof and the grips on
           the other. The two agreed only at zero; slid, the walls left the roof
           and the grip ran against the finger (Marek: zrcadlene drag pointy,
           strecha mimo side run). One convention now: the wing's centre is the
           bar's centre plus the offset, on the axis, wherever the edge starts. */
        var sideZ0 = (WG.side === 'z0' || WG.side === 'z1');
        var cAxis = (sideZ0 ? ox : oz) + (WG.at || 0);
        var aAxis = sideZ0 ? a[0] : a[1], uAxis = sideZ0 ? ux : uz;
        var half = wW / 2, mid = (cAxis - aAxis) * uAxis;
        mid = Math.max(half + 0.3, Math.min(L - half - 0.3, mid));
        /* AN L (core-v753, ANY HOUSE thread): a wing FLUSH with one end of the
           bar's edge -- STATE.wing.flush 'lo' / 'hi', the end with the lower or
           the higher coordinate on the world axis -- has no return of wall at
           that end, so the bar's end wall and the wing's side wall are one
           wall. Opt-in by the state: a product that never sets it (the T) is
           built exactly as before. */
        var flushW = (WG.flush === 'lo' || WG.flush === 'hi') ? WG.flush : null;
        if (flushW) {
          var axA = aAxis, axB = aAxis + uAxis * L, axLo = Math.min(axA, axB), axHi = Math.max(axA, axB);
          mid = (((flushW === 'lo') ? axLo + half : axHi - half) - aAxis) * uAxis;
        }
        var s = mid - half, t = mid + half, d = WG.l;
        out.push([a[0] + ux * s, a[1] + uz * s]);
        out.push([a[0] + ux * s + nx * d, a[1] + uz * s + nz * d]);
        out.push([a[0] + ux * t + nx * d, a[1] + uz * t + nz * d]);
        out.push([a[0] + ux * t, a[1] + uz * t]);
      }
      if (WG.flush === 'lo' || WG.flush === 'hi') out = _polyClean(out);
      return out;
    }
    /* the outline without a point twice over or a point in the middle of a
       straight run (core-v753: the L's corner where the wing stands flush) */
    function _polyClean(P) {
      var q = [], i, n;
      for (i = 0; i < P.length; i++) {
        var p = P[i], l0 = q.length ? q[q.length - 1] : null;
        if (l0 && Math.abs(l0[0] - p[0]) < 1e-6 && Math.abs(l0[1] - p[1]) < 1e-6) continue;
        q.push(p);
      }
      if (q.length > 1 && Math.abs(q[0][0] - q[q.length - 1][0]) < 1e-6 && Math.abs(q[0][1] - q[q.length - 1][1]) < 1e-6) q.pop();
      var again = true;
      while (again && q.length > 3) {
        again = false; n = q.length;
        for (i = 0; i < n; i++) {
          var A = q[(i + n - 1) % n], B = q[i], C = q[(i + 1) % n];
          if (Math.abs((B[0] - A[0]) * (C[1] - B[1]) - (B[1] - A[1]) * (C[0] - B[0])) < 1e-9) { q.splice(i, 1); again = true; break; }
        }
      }
      return q;
    }

    function polyArea(P) {
      var a = 0, i, j;
      for (i = 0, j = P.length - 1; i < P.length; j = i++) {
        a += (P[j][0] + P[i][0]) * (P[j][1] - P[i][1]);
      }
      return Math.abs(a / 2);
    }

    function inPoly(x, z, P) {
      P = P || poly();
      var inside = false, i, j, xi, zi, xj, zj;
      for (i = 0, j = P.length - 1; i < P.length; j = i++) {
        xi = P[i][0]; zi = P[i][1]; xj = P[j][0]; zj = P[j][1];
        if (((zi > z) !== (zj > z)) && (x < (xj - xi) * (z - zi) / (zj - zi) + xi)) inside = !inside;
      }
      return inside;
    }

    /* -------------------------------------------------------------------------
       STOREYS. y0 is the top of that storey's floor structure, y1 the underside
       of its head. Storey 1 stacks on storey 0 through the INTER zone, so a
       product that only ever draws one storey costs nothing for the second.
       ------------------------------------------------------------------------- */
    /* THE STOREY'S OWN OUTLINE (core-v785, TWO STORY thread, Marek
       2026-10-30: the Rohatec house -- the upper floor set back behind a
       roof terrace). A product may answer __PROD._storeyPoly(s, P) with the
       outline of storey s (s >= 1) -- P is the house's own -- and the storey,
       its walls (segments) and everything built from them follow it; the ids
       stay S<s>-W<i> in the outline's own order. A product that never
       answers is built exactly as before: every storey on poly(). */
    /* core-v811: WHERE A LINE x = at (vert) or z = at RUNS INSIDE AN
       OUTLINE, its wall's thickness wt in from every side, between lo and hi
       along it: [from, to, other pieces] of the longest stretch, or null. The
       outline is the walls' outer faces (as poly()). Exact for square-cornered
       outlines; a slanted side is stepped at 20 mm. */
    function _lineInOutline(P, vert, at, lo, hi, wt) {
      if (!P || P.length < 3 || !(hi > lo)) return null;
      var Q = [], i, n = P.length, br = [lo, hi], k;
      for (i = 0; i < n; i++) Q.push(vert ? [P[i][0], P[i][1]] : [P[i][1], P[i][0]]);   /* [across, along] */
      for (i = 0; i < n; i++) {
        var A = Q[i], B = Q[(i + 1) % n], dc, h2;
        if (Math.abs(A[1] - B[1]) < 1e-6) {   /* across the line: crosses at its along */
          var c0 = Math.min(A[0], B[0]), c1 = Math.max(A[0], B[0]);
          dc = Math.max(0, c0 - at, at - c1);
          br.push(A[1]);
          if (dc < wt) { h2 = Math.sqrt(wt * wt - dc * dc); br.push(A[1] - h2, A[1] + h2); }
        } else if (Math.abs(A[0] - B[0]) < 1e-6) {   /* along the line */
          dc = Math.abs(A[0] - at);
          if (dc < wt) { h2 = Math.sqrt(wt * wt - dc * dc); br.push(Math.min(A[1], B[1]) - h2, Math.max(A[1], B[1]) + h2); }
          br.push(A[1], B[1]);
        } else {
          for (k = lo; k <= hi; k += 0.02) br.push(k);
        }
      }
      function inside(c, t) {
        var a, b, inn = false;
        for (a = 0, b = n - 1; a < n; b = a++) {
          if (((Q[a][1] > t) !== (Q[b][1] > t)) && (c < (Q[b][0] - Q[a][0]) * (t - Q[a][1]) / (Q[b][1] - Q[a][1]) + Q[a][0])) inn = !inn;
        }
        if (!inn) return false;
        for (a = 0; a < n; a++) {
          var U = Q[a], V = Q[(a + 1) % n], vx = V[0] - U[0], vy = V[1] - U[1], L2 = vx * vx + vy * vy;
          var tt = L2 > 0 ? Math.max(0, Math.min(1, ((c - U[0]) * vx + (t - U[1]) * vy) / L2)) : 0;
          var dx = U[0] + vx * tt - c, dy = U[1] + vy * tt - t;
          if (dx * dx + dy * dy < (wt - 0.002) * (wt - 0.002)) return false;
        }
        return true;
      }
      br = br.filter(function (v) { return v >= lo - 1e-9 && v <= hi + 1e-9; }).sort(function (x, y) { return x - y; });
      var runs = [], cur = null;
      for (i = 0; i + 1 < br.length; i++) {
        var u0 = br[i], u1 = br[i + 1];
        if (u1 - u0 < 1e-6) continue;
        if (inside(at, (u0 + u1) / 2)) { if (cur && Math.abs(cur[1] - u0) < 1e-6) cur[1] = u1; else { cur = [u0, u1]; runs.push(cur); } }
        else cur = null;
      }
      runs = runs.filter(function (r) { return r[1] - r[0] > 0.05; });
      if (!runs.length) return null;
      runs.sort(function (x, y) { return (y[1] - y[0]) - (x[1] - x[0]); });
      return [runs[0][0], runs[0][1], runs.length - 1];
    }
    function polyOf(s) {
      var P = poly();
      if (!(s > 0) || !__PROD || typeof __PROD._storeyPoly !== 'function') return P;
      var r = null;
      try { r = __PROD._storeyPoly(s, P); } catch (eSP) { r = null; }
      return (Array.isArray(r) && r.length >= 3) ? r : P;
    }
    function storeys() {
      var out = [], n = clamp(STATE.storeys | 0, 1, 2), i, y = STACK.slab, h;
      /* THE TOP STOREY IS TRIMMED TO THE COURSE, HERE, EVERY TIME (core-v442,
         Marek: logika ma byt jednoducha, pridava se rows, skladba pod se
         nemeni, strecha jde s vencem). The slider values are left exactly as
         set; the masonry product gives, from constants alone, how much of the
         top storey falls above its last course, and that much is taken off in
         the derivation. Nothing is written back, so nothing drifts, and the
         wall below never moves. */
      var trim = 0;
      try { if (__PROD && __PROD._trimTop) trim = __PROD._trimTop(n, STACK) || 0; } catch (eT) { trim = 0; }
      for (i = 0; i < n; i++) {
        h = STATE.clearH[i] || STATE.clearH[0] || 2.8;
        if (i === n - 1 && trim > 0.0005 && trim < h) h = h - trim;
        out.push({
          i:    i,
          y0:   y,
          y1:   y + h,
          h:    h,
          head: y + h + STACK.plate,
          poly: polyOf(i),   /* core-v785 */
          area: polyArea(polyOf(i))
        });
        y = y + h + STACK.plate + STACK.inter;
      }
      return out;
    }

    function wallTop() {
      var S = storeys();
      return S[S.length - 1].head;
    }

    /* core-v817 (TWO STORY thread): A STOREY'S FLOOR BUILD-UP OVER ITS DECK.
       A product whose upper rooms have a build-up between the walls (a dry
       underfloor heating system on the subfloor) hands its height for that
       storey (__PROD._floorLift(st), metres); the walls stand on the deck as
       before, the rooms' finish, their skirting, the doors in the partitions
       and the furniture stand on the build-up. No hook, 0: nothing moves. */
    function _flLift(st) {
      try { return (__PROD && typeof __PROD._floorLift === 'function') ? (+__PROD._floorLift(st | 0) || 0) : 0; } catch (eFL) { return 0; }
    }

    /* -------------------------------------------------------------------------
       NAMED WALL SEGMENTS -- the primitive the whole line rests on.

       Every product asks for these and answers ONE question about each: how is
       this segment built. That is what makes "replace the mass with KVH stud
       panels" a sentence the code can act on rather than a wish.

       id is stable across a resize: storey index and edge index, not a running
       counter. A window pinned to S0-W2 is still on the south wall after the
       footprint is dragged.
       ------------------------------------------------------------------------- */
    function segments() {
      var out = [], S = storeys(), P = poly(), s, i, a, b, dx, dz, len, nx, nz;
      for (s = 0; s < S.length; s++) {
        P = S[s].poly || poly();   /* core-v785: the storey's own outline */
        for (i = 0; i < P.length; i++) {
          a = P[i];
          b = P[(i + 1) % P.length];
          dx = b[0] - a[0];
          dz = b[1] - a[1];
          len = Math.sqrt(dx * dx + dz * dz);
          if (len < 0.01) continue;
          /* outward normal of a clockwise ring seen from above */
          nx = dz / len;
          nz = -dx / len;
          var sid = 'S' + s + '-W' + i;
          var sh = (STATE.segH && STATE.segH[sid] != null) ? STATE.segH[sid] : S[s].h;
          out.push({
            id:      sid,
            storey:  s,
            edge:    i,
            x0: a[0], z0: a[1],
            x1: b[0], z1: b[1],
            len:     len,
            nx:      nx,
            nz:      nz,
            y0:      S[s].y0,
            y1:      S[s].y0 + sh,
            h:       sh,
            full:    S[s].h,
            head:    S[s].y0 + sh + STACK.plate,
            area:    len * sh,
            openings: openingsOn(sid)
          });
        }
      }
      return out;
    }

    function segment(id) {
      var L = segments(), i;
      for (i = 0; i < L.length; i++) if (L[i].id === id) return L[i];
      L = _prodSegs();
      for (i = 0; i < L.length; i++) if (L[i].id === id) return L[i];
      return null;
    }
    /* WALLS A PRODUCT BUILDS BESIDE THE OUTLINE (core-v525, A-frame side
       wing: okna a dvere ve stenach kridla jako ve stitech). A product may
       answer __PROD._extraSegs() with more walls in the segments' own shape
       ({ id, storey, x0, z0, x1, z1, len, nx, nz, y0, y1, h, full, head, area });
       their ids are the product's own (never S<n>-W<i>). An opening records
       its seg as usual, so picking, dragging, the bin and the plan work on
       them as on any wall; they are not in segments(), so no core layer
       builds a wall there -- the product builds and bills them. */
    function _prodSegs() {
      if (!__PROD || typeof __PROD._extraSegs !== 'function') return [];
      var r = null;
      try { r = __PROD._extraSegs(); } catch (eXS) { r = null; }
      return Array.isArray(r) ? r : [];
    }

    /* -------------------------------------------------------------------------
       AN OPENING, AND WHAT THE CORE PROMISES ABOUT IT.

         { seg, at, w, h, sill, kind }

       seg   the segment id it belongs to, 'S0-W1'
       at    the centre, measured along the wall from its start
       w, h  the CLEAR size -- the hole you can see through, not the rough opening.
             The frame that makes it is the product's business; what everything
             else needs to agree on is the hole.
       sill  height of the bottom of the hole above the floor. Zero is a door.
       kind  'door' | 'window' | 'french' | 'garage', for pricing and for symbols

       Nothing here knows what a header is. That belongs to whichever layer frames
       the wall, because a stud wall, a masonry wall and a CLT panel make the same
       hole out of entirely different things.
       ------------------------------------------------------------------------- */
    /* A FULL-WALL UNIT MEETS ITS WALL HERE, and once through this it is an
       ordinary opening -- the framing, the joinery, the bill and the drawing all
       see a normal width and height and none of them has to know it started as a
       zero. The margins are the same the clamp below would apply anyway: timber
       either side for the header to land in. */
    function _sizeToWall(seg, o) {
      if (!o.full || !seg) return o;
      /* A WALL THAT IS NOT A RECTANGLE SIZES ITS OWN FULL UNIT (core-v491,
         R3, Marek: stejny zpusob vkladani vsude, i kdyz ma sikmou stenu). A
         product whose wall is a gable triangle hands back the unit it fits
         there -- the whole triangle, or the band at its top -- with rake: true
         when the unit is not a rectangle; the core then leaves drawing and
         billing that unit to the product (R4). */
      if (typeof __PROD._opFull === 'function') {
        try {
          var pf = __PROD._opFull(seg, o);
          if (pf) { _say('opening  full unit ' + (o.code || o.kind) + ' on ' + seg.id + ' sized by the product' + (pf.rake ? ' (rake: drawn and billed by the product)' : '')); return pf; }
        } catch (ePF) { _say('opening  ERROR __PROD._opFull threw: ' + ((ePF && ePF.message) || ePF)); }
      }
      /* a full-wall unit gets its leaf count from the wall too, for the same
         reason: nobody makes a nine-metre pane */
      var wantLeaves = o.leaves || 0;
      /* FULL: CORNER TO CORNER, SOLE PLATE TO THE DOUBLED TOP PLATE (core-v271).
         Across: from one corner to the other, less the wall that meets it and a
         jack and a king at each end -- the same keep every opening is held to.
         Up: from the top of the sole plate to the underside of the doubled top
         plate, which is its header (the window-under-the-plate rule).
         HALF: the same height, half the length -- a tall pane filling half the
         wall, not a strip along the whole of it (Marek). It goes where it was
         put and can be slid along the wall like any other. */
      var t0 = WALL_LAYOUT.studT, fit0 = WALL_LAYOUT.frame.fit, keep0 = _openingKeep();
      var runW = Math.max(0.6, seg.len - 2 * keep0);
      o.sill = t0;
      o.h = Math.max(0.6, (seg.h - t0) - t0 + fit0);
      if (o.full === 'half') {
        o.w = Math.max(0.6, runW / 2);
        var atH = (+o.at > 0) ? +o.at : seg.len / 2;
        o.at = Math.max(keep0 + o.w / 2, Math.min(seg.len - keep0 - o.w / 2, atH));
      } else {
        o.w = runW;
        o.at = seg.len / 2;
      }
      o.leaves = Math.max(wantLeaves, Math.round(o.w / 1.4)) || 1;
      /* AND IT KEEPS SAYING SO ON THE PLAN. full is cleared here so the fitting
         is not applied twice, which left nothing behind to tell the drawing what
         this unit is -- a nine-metre glazed portal came out with a plain window
         mark. The asset draws SLIDE for fullW and halfW; this is the flag it
         looks for, and unlike full it survives. */
      if (o.full === 'half') o.halfW = true; else o.fullW = true;
      o.full = null;
      return o;
    }

    function addOpening(o) {
      if (!o || !o.seg) return null;
      var rec = {
        seg: o.seg,
        at: +o.at || 0,
        w: Math.max(0.3, +o.w || 0.9),
        h: Math.max(0.3, +o.h || 2.0),
        sill: Math.max(0, +o.sill || 0),
        kind: o.kind || 'window',
        /* THE CATALOGUE CODE TRAVELS WITH THE OPENING. Without it the bill has a
           hole where the biggest bought item on the wall should be, and the model
           would have to guess afterwards which unit this was from its size --
           which two different units can share. */
        code: o.code || null,
        hand: o.hand || null,
        /* WHICH WAY AN ENTRY DOOR OPENS (core-v567, Marek: vstup do storage --
           dvoukridle dvere ven). null is into the building, as every door was
           drawn until now; true is out of it -- a store's pair of doors, where
           the leaves would take the room they are meant to fill. The catalogue
           unit is the same either way, so the bill does not change with it. */
        out: o.out ? true : null,
        fullW: o.fullW || null,
        halfW: o.halfW || null,
        /* a unit the product draws itself, e.g. a triangle in a gable (R3/R4) */
        rake: o.rake || null,
        /* HOW MANY LEAVES, because a 4 m opening is not one sheet of glass.

           The catalogue says so per unit and it travels with the opening; where
           it does not, it is worked out from the width. Anything wider than about
           a metre and a half in one pane does not exist -- it cannot be lifted,
           it cannot be sealed, and a portal that size is made of leaves with
           mullions between them. Drawing it as one pane is the difference between
           a window and a shop front. */
        /* AND A PORTAL DIVIDES SOONER THAN A WINDOW. A sliding leaf carries its
           own weight on a track and is walked through; a fixed window pane only
           has to stay in its frame. The catalogue's own count wins whenever the
           unit came from it -- this is only for openings placed without one. */
        leaves: o.leaves || Math.max(1, Math.round((+o.w || 1) /
                  (((o.kind === 'french') || (o.kind === 'garage')) ? 1.0 : 1.4)))
      };
      var sg = segment(rec.seg);
      if (sg) {
        /* placed by the same single rule it is moved by */
        rec.at = snapOpeningAt(rec, rec.at);
        if (rec.w > sg.len - OP_KEEP * 2) rec.w = sg.len - OP_KEEP * 2;
        if (_opUnderHook(sg, rec) != null) {
          /* UNDER A RAKE (core-v491, R2): the head is held under the product's
             line at the place it was tapped; a unit on the floor that does not
             fit there is not placed at all */
          var topR = _plateUnder(sg, rec) + WALL_LAYOUT.frame.fit;
          if (openingOnFloor(rec) && !rec.rake && rec.h > topR + 0.0005) {
            _say('opening  ' + rec.kind + ' ' + Math.round(rec.h * 1000) + ' high does not fit under the rake at ' + Math.round(rec.at * 1000) +
                 ' on ' + rec.seg + ' (room ' + Math.round(topR * 1000) + ') -- not placed, tap nearer the middle');
            _log('opening: no room for that ' + rec.kind + ' there -- tap nearer the middle of the wall');
            return null;
          }
        } else if (rec.sill + rec.h > sg.h) rec.h = Math.max(0.3, sg.h - rec.sill - 0.05);
        var snapA = _snapUnderPlate(rec);
        if (snapA) {
          _say('frame window under the top plate: new ' + rec.kind + ' on ' + rec.seg + ' gap ' +
               Math.round(snapA.gap * 1000) + ' mm -> sill ' + Math.round(snapA.sill * 1000) + ' -> ' +
               Math.round(rec.sill * 1000) + ', the plate is its header');
        }
      }
      STATE.openings.push(rec);
      savePref();
      /* opEdit rebuilds once with the new unit already editable. Building
         before it duplicated the work and delayed the glazing's controls. */
      opEdit(true);
      _log('opening ' + rec.kind + ' ' + fmtLen(rec.w) + ' x ' + fmtLen(rec.h) +
           ' on ' + rec.seg + ' at ' + fmtLen(rec.at));
      return rec;
    }

    /* WHERE THIS OPENING MAY SLIDE TO. Its own wall, clear of the corners, and
       clear of whatever else is already in that wall -- two openings that overlap
       are not an opening, they are a hole with a stud in the middle of it. */
    function openingRange(rec) {
      var sg = segment(rec.seg);
      if (!sg) return { lo: rec.at, hi: rec.at };
      var margin = OP_KEEP + rec.w / 2;
      var lo = margin, hi = sg.len - margin, i;
      for (i = 0; i < STATE.openings.length; i++) {
        var o = STATE.openings[i];
        if (o === rec || o.seg !== rec.seg) continue;
        var gap = rec.w / 2 + o.w / 2 + 0.12;   /* a king and a jack between them */
        if (o.at < rec.at) lo = Math.max(lo, o.at + gap);
        else hi = Math.min(hi, o.at - gap);
      }
      return { lo: lo, hi: Math.max(lo, hi) };
    }

    /* AN OPENING STEPS FROM STUD TO STUD, NOT BY THE MILLIMETRE.

       A window landing anywhere means the framing has to make a bay for it
       wherever it stopped, and the studs either side of it end up at whatever
       spacing is left over. Landed on the module, the jack takes the place of a
       stud that was going to be there anyway -- which is how it is built, and it
       is why nobody sizes a rough opening to the nearest centimetre.

       The stud line runs from the start of the wall, so the opening is centred on
       the midpoint of a bay: that puts a jack hard against a stud on both sides
       and leaves the module untouched either way along the wall. */
    /* THE OPENING IS PUSHED UP AGAINST A STUD. THE OLD RULE IS GONE.

       It used to step the CENTRE of the hole along a grid of its own, with the
       half-width rounded to whole bays -- a rule that had nothing to do with where
       the studs actually stand, so an opening came to rest wherever that
       arithmetic left it and never touched a member. Every attempt to line the two
       up from the product side was overwritten by this the moment the opening was
       dragged (Marek: musis to pravidlo ktere je krokuje zrusit, smazat, a
       postavit znovu).

       The studs stand at the two ends of the wall, half a thickness in, with the
       run between them divided into the whole number of bays nearest the module --
       every bay identical. An opening's edge is brought onto the face of whichever
       of those studs is nearest, from whichever side moves it less. It is the same
       arithmetic the wall itself uses, in the one place every product reads. */
    /* KEPT ON ITS WALL, CLEAR OF BOTH CORNERS (core-v271). A dragged window
       followed the nearest stud face wherever the finger went, and at a wall's
       end that face is the end stud -- inside the thickness of the wall that
       meets it, so the unit hung out past the corner (Marek: okna dvere by
       nemelo byt mozne tahnout mimo steny). An opening's edge now stays at least
       one wall thickness from each outer corner plus a jack and a king, the room
       its frame needs; within that it still lands on a stud face. */
    function _openingKeep() { return (_wallThk || 0.30) + 2 * WALL_LAYOUT.studT; }
    function snapOpeningAt(rec, at) {
      var sg = segment(rec.seg);
      if (!sg) return at;
      var w = (rec && rec.w) || 0, keep = _openingKeep();
      var lo = keep + w / 2, hi = sg.len - keep - w / 2;
      if (hi < lo) { lo = hi = sg.len / 2; }
      var a = Math.max(lo, Math.min(hi, at));
      var s = WALL_LAYOUT.openingAt(sg.len, a, w, sg);
      if (s < lo - 1e-6 || s > hi + 1e-6) s = Math.max(lo, Math.min(hi, s));
      return s;
    }

    function moveOpening(rec, at) {
      if (!rec) return null;
      /* ONE RULE, AND IT IS THE ONLY ONE.

         An opening is brought up against the face of a stud. That is the whole
         of it. The keep-off-the-corner margin, the gap kept between two openings
         and the clamp into a legal range were each reasonable on their own, and
         together they meant a window came to rest wherever the last of them left
         it -- between studs, and short of where it was dragged. Every one of them
         is gone: what the framing needs at a corner or between two holes is the
         framing's business, and it will be built around where the opening IS
         (Marek: musis zrusit veskera pravidla pro horizontalni posun, bude
         existovat jedine -- primknout ke stene svislice). */
      /* A CORNER UNIT STAYS IN ITS CORNER (core-v751): the pair is one window;
         it is changed on the cycler at the corner, not slid apart */
      if (rec.cw) {
        var sgCw = segment(rec.seg);
        if (sgCw) rec.at = _cwAt(sgCw, rec.w, rec.cwEnd);
        return rec;
      }
      var atWas = rec.at;
      rec.at = snapOpeningAt(rec, at);
      /* A PRODUCT MAY PULL A UNIT ONTO A LINE OF ITS OWN (core-v522, Marek:
         mozna chytat na osu stitu, pod hreben, pokud je blizko):
         __PROD._opSnapAt(sg, rec, at) answers with the place to use, or null */
      try {
        if (typeof __PROD._opSnapAt === 'function') {
          var sgS = segment(rec.seg), aS = sgS ? __PROD._opSnapAt(sgS, rec, rec.at) : null;
          if (typeof aS === 'number' && isFinite(aS)) rec.at = aS;
        }
      } catch (eSA) {}
      /* UNDER A RAKE THE HEAD FOLLOWS THE SLOPE (core-v491, R2): sliding a
         window towards the low end of a gable brings it down, and shortens it
         only when a sill of nought is not enough; a door or a product-drawn
         unit does not go where it would not fit -- it stays where it last fitted */
      var sgM = segment(rec.seg);
      if (sgM && _opUnderHook(sgM, rec) != null && !rec.rake) {
        var topM = _plateUnder(sgM, rec) + WALL_LAYOUT.frame.fit;
        if (openingOnFloor(rec)) {
          if (rec.h > topM + 0.0005) { rec.at = atWas; _sillNote = 'rake: the ' + rec.kind + ' would not fit there, kept at ' + Math.round(atWas * 1000); }
        } else _snapUnderPlate(rec);
      }
      return rec;
    }

    /* HOW HIGH IT SITS, WHICH NOTHING COULD CHANGE UNTIL NOW.

       A window could slide along its wall and no further: the sill was whatever
       it was given when it was placed, and the only way to raise one was to
       delete it and put another in. Dragging it up and down is the obvious thing
       to try, so that is what it does.

       It steps -- 20 mm metric, one inch imperial -- because a sill is a set-out
       dimension somebody has to build to, and a number like 843 mm is not one.

       A DOOR STAYS ON THE FLOOR. french and garage are doors that reach the deck
       as much as a door is; raising one off the floor is not a thing to allow
       because a finger wobbled. */
    var SILL_HEAD_RESERVE = 0.12;   /* the header over it, laid flat, two plies */

    /* A WINDOW CLOSE UNDER THE TOP PLATE GOES UP TO IT (core-v263).

       Above every window stands a doubled header, 120 mm, and above that the
       lower top plate, 60 mm. On a low wall -- the shed's is 2200 mm -- a
       window can come so close to the plate that the header no longer fits,
       and the header ended up drawn into the window. His rule: where the gap
       between the top of the window's frame and the underside of the top plate
       is 150 mm or less, the window goes up until its frame meets the plate,
       and the doubled plate is its header -- no separate header is built
       (Marek: G <= 150 mm -> okno nahoru k pozednici, bez prekladu). A window
       that reaches into the plate comes down to it, and only if even a sill of
       nought does not bring it clear is it shortened. Doors stand on the floor
       and are not moved. Applied wherever a window is set: added, dragged, and
       on every build, so a lower wall brings its windows along. */
    var HEAD_GAP_MAX = 0.150;
    /* THE LINE A UNIT'S HEAD STAYS UNDER, WHERE THE WALL IS NOT LEVEL
       (core-v491, R2). A product whose wall top is a rake -- a gable -- hands
       __PROD._opUnder(sg, rec): the height over the wall's foot of the
       underside of the member over the opening, for that opening's own place
       and width (the lower of its two top corners under the slope). Without
       the hook it is the level top plate, as it always was. */
    function _opUnderHook(sg, rec) {
      if (!sg || !rec || typeof __PROD._opUnder !== 'function') return null;
      try {
        var u = __PROD._opUnder(sg, rec);
        return (typeof u === 'number' && isFinite(u)) ? u : null;
      } catch (eU) { return null; }
    }
    function _plateUnder(sg, rec) {
      var u = _opUnderHook(sg, rec);
      return (u != null) ? u : (sg.h - WALL_LAYOUT.studT);
    }
    function _snapUnderPlate(rec) {
      if (!rec) return null;
      var sgR = rec.rake ? null : segment(rec.seg), uR = sgR ? _opUnderHook(sgR, rec) : null;
      if (uR != null) {
        /* under a rake nothing is pulled UP to the line -- a sloped member is
           no header -- a unit reaching into it only comes down, and is cut
           only when a sill of nought does not clear it */
        var fitR = WALL_LAYOUT.frame.fit, topR = uR + fitR;
        if (rec.sill + rec.h <= topR + 0.0005) return null;
        var wasR = { sill: rec.sill, h: rec.h, gap: uR - (rec.sill + rec.h - fitR) };
        if (!openingOnFloor(rec)) rec.sill = Math.max(0, topR - rec.h);
        if (rec.sill + rec.h > topR + 0.0005) rec.h = Math.max(0.3, topR - rec.sill);
        return wasR;
      }
      if (openingOnFloor(rec)) return null;
      var sg = segment(rec.seg);
      if (!sg || !(sg.h > 0)) return null;
      var fit = WALL_LAYOUT.frame.fit, under = _plateUnder(sg);
      var gap = under - (rec.sill + rec.h - fit);
      if (gap > HEAD_GAP_MAX + 1e-6) return null;
      if (Math.abs(gap) < 0.0005) return null;
      var was = { sill: rec.sill, h: rec.h, gap: gap };
      rec.sill = Math.max(0, under + fit - rec.h);
      if (rec.sill + rec.h - fit > under + 0.0005) rec.h = Math.max(0.3, under + fit - rec.sill);
      return was;
    }
    function _fitOpeningsToPlates() {
      var i, ch, n = 0, notes = [];
      for (i = 0; i < STATE.openings.length; i++) {
        ch = _snapUnderPlate(STATE.openings[i]);
        if (ch) {
          n++;
          if (notes.length < 4) {
            notes.push((STATE.openings[i].kind || 'window') + ' on ' + STATE.openings[i].seg + ' gap ' +
                       Math.round(ch.gap * 1000) + ' mm -> sill ' + Math.round(ch.sill * 1000) + ' -> ' +
                       Math.round(STATE.openings[i].sill * 1000) +
                       (Math.abs(ch.h - STATE.openings[i].h) > 0.0005 ? (', height cut to ' + Math.round(STATE.openings[i].h * 1000)) : ''));
          }
        }
      }
      /* and, every build, how many stand right under the plate with it as their
         header -- said on each build because the build log starts over */
      var atP = 0, sgA, oA, gA;
      for (i = 0; i < STATE.openings.length; i++) {
        oA = STATE.openings[i];
        if (!oA || openingOnFloor(oA)) continue;
        sgA = segment(oA.seg);
        if (!sgA) continue;
        gA = _plateUnder(sgA, oA) - (oA.sill + oA.h - WALL_LAYOUT.frame.fit);
        if (Math.abs(gA) < 0.0005) atP++;
      }
      if (n || atP) {
        _say('frame window under the top plate: ' + atP + ' window(s) stand right under it and use it as their header' +
             (n ? (', ' + n + ' moved there this build -- ' + notes.join(' | ')) : '') +
             ' (rule: a gap of ' + Math.round(HEAD_GAP_MAX * 1000) + ' mm or less closes)');
      }
      return n;
    }

    function openingOnFloor(rec) {
      var k = rec && rec.kind;
      return k === 'door' || k === 'french' || k === 'garage';
    }

    function sillStep() { return STATE.isMetric ? 0.020 : IN; }

    function moveOpeningSill(rec, y) {
      if (!rec || openingOnFloor(rec)) return rec;
      var sg = segment(rec.seg);
      if (!sg) return rec;
      var st = sillStep();
      /* up to the plate itself: the frame may touch its underside */
      var hi = _plateUnder(sg, rec) + WALL_LAYOUT.frame.fit - rec.h;
      if (!(hi > 0)) return rec;
      rec.sill = clamp(Math.round(y / st) * st, 0, hi);
      _snapUnderPlate(rec);
      return rec;
    }

    /* WHERE THE FINGER IS ON THE WALL ITSELF, along it and up it, in one answer.

       The drag used to ask nearestSegment(x, z), which only knows the ground
       plane -- so there was never a height to read. This strikes the ray against
       the vertical plane the wall stands in, which gives both numbers and does
       not need anything to be under the pointer to hit. */
    /* THE SILL HEIGHT, READ WHILE IT IS BEING SET AND NOT AFTERWARDS.

       A number that only appears once the finger lets go is a number you set by
       guessing and then check. It stands on the glass at its bottom edge, with
       the line running down to the floor it is measured from, and it goes when
       the drag does -- nothing left on the model to tidy up later. */
    var _sillDrag = null;
    var _sillNote = 'no opening dragged yet';
    var _grabDiag = '';
    function _grabDescribe(e) {
      try {
        var hits = _hitGroup(e.clientX, e.clientY) || [], i, first = '', boxAt = -1, boxY = '';
        for (i = 0; i < hits.length; i++) {
          var ob = hits[i].object, lay = '', u = ob && ob.userData || {};
          var p = ob; while (p) { if (p.userData && p.userData.layer) { lay = p.userData.layer; break; } p = p.parent; }
          if (i === 0) first = (lay || '?') + ':' + (u.frameRole || u.hotLayer || u.trussPart || (u.opSeg ? 'grabbox' : (ob.type || 'mesh'))) +
                               ' at y ' + hits[i].point.y.toFixed(2);
          var q = ob; while (q) { if (q.userData && q.userData.opSeg) break; q = q.parent; }
          if (q && boxAt < 0) {
            boxAt = i;
            var T = global.THREE, bb = new T.Box3().setFromObject(q);
            boxY = ' y ' + bb.min.y.toFixed(2) + '..' + bb.max.y.toFixed(2);
          }
        }
        var near = '', pt = hits.length ? hits[0].point : null;
        if (pt) {
          var best = null, bd = 1e9, k;
          for (k = 0; k < STATE.openings.length; k++) {
            var o = STATE.openings[k], sg = segment(o.seg);
            if (!sg || !(sg.len > 0)) continue;
            var ux = (sg.x1 - sg.x0) / sg.len, uz = (sg.z1 - sg.z0) / sg.len;
            var al = (pt.x - sg.x0) * ux + (pt.z - sg.z0) * uz, ac = Math.abs((pt.x - sg.x0) * uz - (pt.z - sg.z0) * ux);
            var d = Math.hypot(Math.max(0, Math.abs(al - o.at) - (o.w || 0.9) / 2), ac);
            if (d < bd) { bd = d; best = { o: o, sg: sg }; }
          }
          if (best) {
            var lo = best.sg.y0 + best.o.sill, hi = lo + best.o.h;
            near = '; nearest ' + best.o.kind + ' on ' + best.o.seg + ' spans y ' + lo.toFixed(2) + '..' + hi.toFixed(2) +
                   ', the tap is ' + (pt.y < lo ? ('BELOW it by ' + (lo - pt.y).toFixed(2)) : (pt.y > hi ? ('ABOVE it by ' + (pt.y - hi).toFixed(2)) : 'on it'));
          }
        }
        return hits.length + ' hit(s), first ' + (first || 'none') +
               '; grab box ' + (boxAt < 0 ? 'NOT among the hits' : ('hit #' + (boxAt + 1) + boxY)) + near;
      } catch (eG) { return 'describe threw: ' + (eG && eG.message); }
    }

    function _sillKotBuild() {
      var d = _sillDrag;
      if (!d) return;
      var st = _stage(), T = global.THREE;
      if (!st || !T || !st.group) return;
      var rec = d.rec, sg = segment(rec.seg);
      if (!sg || openingOnFloor(rec)) return;
      /* a glazing the product draws to its own outline has no sill to speak
         of -- its foot is the floor zone and its top the rake (core-v503,
         Marek: ghost overlay ukazuje nesmyslnou vysku parapetu) */
      if (_opOutlineOf(rec)) return;

      var y0 = storeys()[0].y0;
      var sill = rec.sill || 0;
      if (!(sill > 0.001)) return;

      var t = rec.at / sg.len;
      var cx = sg.x0 + (sg.x1 - sg.x0) * t;
      var cz = sg.z0 + (sg.z1 - sg.z0) * t;
      var nx = (typeof sg.nx === 'number') ? sg.nx : (sg.n ? sg.n[0] : 0);
      var nz = (typeof sg.nz === 'number') ? sg.nz : (sg.n ? sg.n[1] : 0);
      var off = 0.02;

      var px = cx + nx * off, pz = cz + nz * off;

      /* THE TWO POINTS IT IS MEASURING BETWEEN, so the number is not a claim.

         A pill on its own says 640 mm and nothing about from where to where. Two
         small dots -- one on the floor, one at the sill -- and the number stops
         being something to trust and becomes something to read. */
      var dotA = _kotDot(T);
      var dotB = _kotDot(T);
      if (dotA) { dotA.position.set(px, y0, pz); st.group.add(dotA); }
      if (dotB) { dotB.position.set(px, y0 + sill, pz); st.group.add(dotB); }

      var sp = _kotSprite(T, fmtLen(sill));
      if (!sp) return;
      /* halfway up what it measures, which is where the plan puts it */
      sp.position.set(px, y0 + sill / 2, pz);
      st.group.add(sp);
    }

    /* a black dot, the same size at any zoom, like everything else on this mark */
    var KOT_DOT = 7;           /* screen pixels across */
    var _kotDotTex = null;

    function _kotDot(T) {
      try {
        var st = _stage();
        if (!_kotDotTex) {
          var n = 64, cv = global.document.createElement('canvas');
          cv.width = n; cv.height = n;
          var c = cv.getContext('2d');
          c.fillStyle = '#1A1C1D';
          c.beginPath();
          c.arc(n / 2, n / 2, n / 2 - 2, 0, Math.PI * 2);
          c.fill();
          _kotDotTex = new T.CanvasTexture(cv);
          if (T.SRGBColorSpace) _kotDotTex.colorSpace = T.SRGBColorSpace;
        }
        var sp = new T.Sprite(new T.SpriteMaterial({
          map: _kotDotTex, depthTest: false, transparent: true, sizeAttenuation: false
        }));
        var vh = 728;
        try {
          var el = st && st.renderer && st.renderer.domElement;
          if (el && el.clientHeight > 0) vh = el.clientHeight;
        } catch (e2) {}
        var sy = KOT_DOT / vh;
        sp.scale.set(sy, sy, 1);
        sp.renderOrder = 1001;
        return sp;
      } catch (e) { return null; }
    }

    /* A KOT IS A NUMBER, AND THAT IS THE WHOLE OF IT.

       I had built a dimension line with a tick at each end and a label off to one
       side. The plan does not do that. DRAW.kot is a white pill with dark text,
       halfway between the two points it measures, and NOTHING else -- its own
       comment says so: no dimension line, no leaders, no arrowheads. Same mark
       here, same colours, same weight of text, drawn on a canvas with the same
       rounded box, so it IS the plan mark rather than something resembling it.

       AND IT DOES NOT GROW OR SHRINK. sizeAttenuation false takes the sprite out
       of perspective altogether: its size is a fraction of the viewport, not a
       function of how far away it is. Zoom in, zoom out, the number is the same
       size -- which is what it is in the plan, and the only way a number stays
       readable. */
    var KOT_H = 22;            /* the pill, in screen pixels */

    function _kotSprite(T, txt) {
      try {
        var st = _stage();
        var doc = global.document;
        txt = String(txt == null ? '' : txt);

        var pad = 14, ch = 7.2;
        var W = Math.max(48, Math.round(txt.length * ch + pad * 2));
        var H = 26, r = 11;
        var sc = 3;                                    /* drawn oversize, so it stays crisp */
        var cv = doc.createElement('canvas');
        cv.width = W * sc; cv.height = H * sc;
        var c = cv.getContext('2d');
        c.scale(sc, sc);

        var bx = 0.5, by = 0.5, bw = W - 1, bh = H - 1;
        c.fillStyle = '#ffffff';                       /* DRAW.DIM_BG */
        c.beginPath();
        c.moveTo(bx + r, by);
        c.lineTo(bx + bw - r, by); c.quadraticCurveTo(bx + bw, by, bx + bw, by + r);
        c.lineTo(bx + bw, by + bh - r); c.quadraticCurveTo(bx + bw, by + bh, bx + bw - r, by + bh);
        c.lineTo(bx + r, by + bh); c.quadraticCurveTo(bx, by + bh, bx, by + bh - r);
        c.lineTo(bx, by + r); c.quadraticCurveTo(bx, by, bx + r, by);
        c.closePath();
        c.fill();

        c.fillStyle = '#1A1C1D';                       /* DRAW.DIM_TXT */
        c.font = '600 12px system-ui,-apple-system,sans-serif';
        c.textAlign = 'center';
        c.textBaseline = 'middle';
        c.fillText(txt, W / 2, H / 2);

        var tex = new T.CanvasTexture(cv);
        if (T.SRGBColorSpace) tex.colorSpace = T.SRGBColorSpace;
        tex.minFilter = T.LinearFilter;
        var sp = new T.Sprite(new T.SpriteMaterial({
          map: tex, depthTest: false, transparent: true, sizeAttenuation: false
        }));

        /* with sizeAttenuation off a scale of 1 is the height of the viewport, so
           the pill is asked for in pixels and turned into that here */
        var vh = 728;
        try {
          var el = st && st.renderer && st.renderer.domElement;
          if (el && el.clientHeight > 0) vh = el.clientHeight;
        } catch (e2) {}
        var sy = KOT_H / vh;
        sp.scale.set(sy * (W / H), sy, 1);
        sp.renderOrder = 1000;
        return sp;
      } catch (e) { return null; }
    }

    /* how far the finger is above the sill at the moment it takes hold, so the
       window does not jump to it on the first frame */
    function _grabDy(e, rec) {
      try {
        var h = openingHitOnWall(e, segment(rec.seg));
        return h ? (h.y - rec.sill) : null;
      } catch (eg) { return null; }
    }

    /* THE NORMAL IS TWO FIELDS, NOT AN ARRAY, AND THAT IS THE WHOLE OF IT.

       segments() pushes  nx: nx, nz: nz.  This asked for sg.n[0] and sg.n[1], so
       sg.n was undefined, the guard sent it home, and every drag got dy=NULL --
       which is a window that cannot be raised by a millimetre.

       What made it hard to see: the snapshot prints the pair as  n=[0.00,-1.00],
       because the line that formats it builds those brackets out of the two
       fields. It reads like an array in the one place anybody looks. */
    function openingHitOnWall(e, sg) {
      var st = _stage(), T = global.THREE;
      if (!st || !T || !st.camera || !st.renderer || !sg) return null;
      var nx = (typeof sg.nx === 'number') ? sg.nx : (sg.n ? sg.n[0] : null);
      var nz = (typeof sg.nz === 'number') ? sg.nz : (sg.n ? sg.n[1] : null);
      if (nx === null || nz === null) return null;
      var rc = _aim(e.clientX, e.clientY);
      if (!rc) return null;
      var pl = new T.Plane(new T.Vector3(nx, 0, nz), -(nx * sg.x0 + nz * sg.z0));
      var hit = rc.ray.intersectPlane(pl, new T.Vector3());
      if (!hit) return null;
      var ux = (sg.x1 - sg.x0) / sg.len, uz = (sg.z1 - sg.z0) / sg.len;
      return { along: (hit.x - sg.x0) * ux + (hit.z - sg.z0) * uz, y: hit.y };
    }

    /* The opening nearest a point in plan, if the point is close enough to mean
       it. Measured to the CENTRE, so a wide door is no easier to grab than a
       narrow window -- the target is the thing, not its size. */
    /* WHICH UNIT THE RAY ACTUALLY HIT, read off the thing it hit.

       Copied from tiny, which has never had this problem: the grab boxes carry
       the wall and the position, so a hit IS the answer. No reach, no tolerance,
       no way to pick a unit the finger is not on. */
    function openingRayHit(e) {
      var found = _UI().openingHit(_hitGroup(e.clientX, e.clientY), function (ob) {
        while (ob) {
          var u = ob.userData || {};
          if (u.opRoof) {
            var roof = (STATE.openings || [])[u.opIndex | 0];
            return roof && roof.host === 'roof' ? roof : null;
          }
          if (u.opSeg) {
            var ops = STATE.openings || [];
            for (var q = 0; q < ops.length; q++) {
              if (ops[q].seg === u.opSeg && Math.abs(ops[q].at - u.opAt) < 0.001) return ops[q];
            }
          }
          ob = ob.parent;
        }
        return null;
      });
      return found ? found.record : null;
    }

    /* A WINDOW IS AS BIG AS IT IS, AND THIS WAS MEASURING TO ITS MIDDLE.

       The distance was taken to the opening's CENTRE POINT, so on a 1.8 m unit a
       tap 900 mm from the middle -- which is still on the glass, at its edge --
       came out 0.9 m away and missed a reach of 0.8. Tapping the middle worked,
       tapping the window did not, and it got worse the wider the unit.

       It measures to the opening's EXTENT now: along the wall, how far outside
       its own width the tap is, and zero anywhere on it. Across the wall it is
       the same distance it always was. Nothing else changes -- the reach still
       decides, it is just measured from the right thing. */
    function openingNear(px, pz, tol) {
      var best = null, bestD = tol || 0.8, i;
      for (i = 0; i < STATE.openings.length; i++) {
        var o = STATE.openings[i];
        var sg = segment(o.seg);
        if (!sg || !(sg.len > 0)) continue;
        var ux = (sg.x1 - sg.x0) / sg.len, uz = (sg.z1 - sg.z0) / sg.len;
        var dx = px - sg.x0, dz = pz - sg.z0;
        var along = dx * ux + dz * uz;            /* how far along the wall the tap is */
        var across = Math.abs(dx * uz - dz * ux); /* and how far off it */
        var half = (o.w || 0.9) / 2;
        var out = Math.abs(along - o.at) - half;  /* outside the unit, or 0 on it */
        if (out < 0) out = 0;
        var d = Math.sqrt(out * out + across * across);
        if (d < bestD) { bestD = d; best = o; }
      }
      return best;
    }

    function removeOpening(rec) {
      var i = STATE.openings.indexOf(rec);
      if (i < 0) return false;
      STATE.openings.splice(i, 1);
      /* a corner window goes as one: the bin on either unit takes both (core-v751) */
      if (rec && rec.cw) {
        var nCw = 0, j;
        for (j = STATE.openings.length - 1; j >= 0; j--) if (STATE.openings[j] && STATE.openings[j].cw === rec.cw) { STATE.openings.splice(j, 1); nCw++; }
        _say('corner   removed ' + rec.cw + ' (both units, ' + (nCw + 1) + ')');
      }
      savePref();
      rebuild();
      try { _opSwingSync(); } catch (eSw) {}
      return true;
    }

    function openingsOn(segId) {
      /* AN OPENING STORED UNDER AN OLDER RULE IS BROUGHT TO THE CURRENT ONE.

         Snapping happened when an opening was dragged or placed, and nowhere else
         -- so a project saved before the rule changed kept whatever the old rule
         left behind, and only the one window that had been touched since sat on a
         stud. That is why three of four were out by exactly a stud thickness and
         the fourth was perfect: the fourth was the one he had moved.

         Every opening is brought onto a stud as it is handed out, so what the wall
         builds and what the drag produces are the same thing whatever the project
         was saved from (Marek: okna maji odstup, je tam mezera). */
      /* AND IT DOES NOT SNAP ANYTHING HERE.

         Normalising each opening as it was handed out looked harmless and was
         fatal: snapping asks for the segment, asking for a segment rebuilds the
         list of segments, and building that list hands out the openings again.
         The page never finished loading. Whatever brings an old project onto the
         current rule has to run somewhere that is not inside this. */
      var out = [], i, o;
      for (i = 0; i < STATE.openings.length; i++) {
        o = STATE.openings[i];
        if (o && o.seg === segId) out.push(o);
      }
      return out;
    }

    /* -------------------------------------------------------------------------
       ROOF PLANES. A set of planes with a named eave and ridge edge, never a
       single roof object -- that is what lets the flat-roof product and the gable
       product share every line above this one.
       ------------------------------------------------------------------------- */

    /* THE ZONE STARTS HERE BECAUSE THE ROOF GEOMETRY IS HERE.

       It used to start four thousand lines lower, which put roofPlanes above
       every zone -- so a fix to the roof SURFACE could only ever travel in a
       whole file, and three zone patches in a row quietly left it behind. The
       frame moved and the plane did not, and the log had to spell it out:

         frame tips -4645 / 7145 | plane eaves -5926 / 7145

       -5926 is x0 minus the DEEPER eave, which is the symmetric formula that was
       replaced once and then kept coming back with every patch.

       Anything a patch might need to reach has to be inside a zone. This is the
       line the roof is drawn from, so the zone starts above it. */

    /* ===== ZONE A -- replace from here to the END of the file ============ */

    /* Everything: the roof surface, all the product layers, and the boot. Above
       this line is the shared core. Use it only when a change spans the file */

    /* THE ROOF OVER A T (core-v310).

       Two roofs, not one: the bar's, and the wing's standing across it. A roof
       worked out over the outline's bounding box covered the empty corners of
       the T as well, which is the one magenta slab over the whole plan (Marek).

       The bar is roofed on its own rectangle with its ridge PARALLEL to the wall
       the wing stands on -- that is what makes it a T and not a longer bar, so
       it is not left to STATE.roof.axis. The wing is roofed with its ridge out
       of that wall, and its rectangle is run INTO the bar as far as the bar's
       ridge: a wing narrower than the bar has the lower ridge, so its roof dies
       into the bar's slopes, and the two lines where it does are the valleys.
       Only the wing's outer end can be hipped; its inner end is inside the bar's
       roof. Every wing plane carries wing: true and an id starting with W, so
       whatever reads the planes can tell the two roofs apart. */
    function roofPlanes() {
      var WG = STATE.wing, R = STATE.roof;
      if (!WG || !WG.on || R.type === 'flat' || R.type === 'mono') return _roofPlanesBox(null);
      var B = _barRect(), WR = _wingRect();
      var sideZ = (WG.side === 'z0' || WG.side === 'z1');
      var mainAxis = sideZ ? 'w' : 'l';
      var WR0 = _wingRect();
      var barHip = null;
      if (WR0 && R.type === 'hip') {
        /* the coordinate along the bar's ridge where the wing's ridge meets it */
        var wrAt = sideZ ? ((WR0.x0 + WR0.x1) / 2) : ((WR0.z0 + WR0.z1) / 2), keep = 0.30;
        barHip = { lo: !!hipEnds().lo, hi: !!hipEnds().hi, limLo: wrAt - keep, limHi: wrAt + keep };
        /* AN L'S FLUSH END (core-v757, ANY HOUSE thread): there the bar's hip
           end and the wing's side slope share one eave line, so the hip's
           apex lands ON the wing's ridge (no 0.3 m keep -- that is for a T,
           where a hip end would otherwise run into the wing's ridge) and the
           two become one plane below (_roofMergeFlush) */
        if (WG.flush === 'hi') barHip.limHi = wrAt;
        if (WG.flush === 'lo') barHip.limLo = wrAt;
      }
      var main = _roofPlanesBox(B, mainAxis, barHip);
      if (!WR) return main;
      var OG = _roofOver(), FG = facadeThk();
      var box = { x0: WR.x0, x1: WR.x1, z0: WR.z0, z1: WR.z1 };
      /* to the bar's ridge line, less the overhang the box will be given back
         at that end -- so the wing's own rake lands on the ridge, not past it */
      var rz = (B.z0 + B.z1) / 2, rx = (B.x0 + B.x1) / 2;
      /* the wing's own even overhang (core-v331) is what the box will be given
         back at its inner end, so that end lands on the bar's ridge line */
      var e0 = R.eave || 0.4;
      if (WG.side === 'z1') box.z0 = rz + (e0 + FG);
      if (WG.side === 'z0') box.z1 = rz - (e0 + FG);
      if (WG.side === 'x1') box.x0 = rx + (e0 + FG);
      if (WG.side === 'x0') box.x1 = rx - (e0 + FG);
      var outerHi = (WG.side === 'z1' || WG.side === 'x1');
      var wingHip = (R.type === 'hip') && !!hipEnds().wing;
      var hipOver = wingHip ? { lo: !outerHi, hi: outerHi } : { lo: false, hi: false };
      /* THE RIDGES LEVEL, WHEN ASKED (core-v324). Off, both arms have the roof's
         pitch and the narrower wing's ridge is the lower one. On, the wing takes
         whatever pitch brings its ridge up to the bar's: the bar's rise over the
         wing's half width. A wing as wide as the bar gets the same pitch either
         way, which is why this is a switch and not the rule (Marek). */
      var wingPitch = null;
      /* the slopes start at the eave, which is the overhang past the wall, so
         the half widths carry their overhang or the ridges miss by it */
      /* THE BAR'S RISE FROM THE BAR'S PLANES (core-v483, from the T house
         thread, Marek: kdyz protahuju eave hranu na main run, hreben side run
         se meni). A tails product keeps the bar's planes on its minimum eave
         and only lengthens the rafters, so the bar's ridge does not move --
         but the rise the wing's pitch is matched to was worked out from the
         DRAGGED reaches, and every drag of the bar's eave steepened the wing.
         The reaches are read the way the bar's planes are built. */
      var OGp = _roofOverForPlanes(OG, mainAxis);
      var ovBar = (sideZ ? (OGp.z0 + OGp.z1) : (OGp.x0 + OGp.x1)) / 2 + FG;
      var ovWing = e0 + FG;
      var barHalf = (sideZ ? (B.z1 - B.z0) : (B.x1 - B.x0)) / 2 + ovBar;
      var wingHalf = (sideZ ? (WR.x1 - WR.x0) : (WR.z1 - WR.z0)) / 2 + ovWing;
      var tanP = Math.tan((R.pitch || 0) * Math.PI / 180);
      var barRise = barHalf * tanP, wingRise = wingHalf * tanP;
      /* THE WING'S RIDGE NEVER RISES ABOVE THE BAR'S (core-v334). A wing wider
         than the bar would, at the roof's pitch, put its ridge higher and run
         its slopes over the bar's far side; instead its pitch drops by itself
         until its ridge is the bar's, whatever the level-ridge switch says
         (Marek: pokud je side run sirsi, hreben nesmi presahovat hreben main
         run a automaticky se meni sklon). The switch still lifts a narrower
         wing's ridge up to the bar's when it is on. */
      _roofWingHeld = false;
      var wingDy = 0;
      if (wingHalf > 0.05 && (R.sameRidge || wingRise > barRise + 0.005)) {
        wingPitch = Math.max(1, Math.min(75, Math.atan(barRise / wingHalf) * 180 / Math.PI));
        _roofWingHeld = (wingRise > barRise + 0.005);
        /* LEVEL ONCE SEATED (core-v484, from the T house thread, Marek: side
           run ma mirne vyssi hreben / main run 167 do pozednice). A product
           that seats its rafters on the plate moves each roof down by a drop
           that depends on the pitch, so two ridges level as planes come apart
           once seated. Such a product hands over __PROD._levelSeat(pWing,
           pBar): how much lower the wing's eave must stand than the bar's for
           the two roofs to seat by the same drop. The wing's pitch is then
           found so its ridge meets the bar's with the eave that much lower --
           the ridges are level as planes AND once seated, and neither roof
           goes deeper into its plate than its own birdsmouth. */
        try {
          if (__PROD && typeof __PROD._levelSeat === 'function') {
            var pBar = R.pitch || 0, lo = 1, hi = 75, it;
            var gLv = function (p) { return (+__PROD._levelSeat(p, pBar) || 0) + wingHalf * Math.tan(p * Math.PI / 180) - barRise; };
            if (gLv(lo) < 0 && gLv(hi) > 0) {
              for (it = 0; it < 50; it++) { var md = (lo + hi) / 2; if (gLv(md) > 0) hi = md; else lo = md; }
              wingPitch = (lo + hi) / 2;
              wingDy = +__PROD._levelSeat(wingPitch, pBar) || 0;
              _roofLevelNote = 'wing ' + (Math.round(wingPitch * 10) / 10) + ' deg, its eave ' + Math.round(wingDy * 1000) + ' mm off the bar\'s, both seat by one drop';
            }
          }
        } catch (eLS) {}
      }
      /* THE WING'S OVERHANG IS ITS OWN AND THE SAME ON BOTH SIDES (core-v331).
         The box roofer read the bar's four eaves, one per side of the plan, and
         gave the wing the bar's left and right reaches as its own two sides. A
         bar whose eaves had been dragged unequal put the wing's ridge off the
         middle of the wing's walls, and the rafters on the shorter side came
         down under the plate (Marek: side run neni symetricky hreben). The wing
         takes the roof's plain eave all round. */
      var wingOver = { x0: e0, x1: e0, z0: e0, z1: e0 };
      /* AND ITS OWN ONCE DRAGGED (core-v427): the wing's ghost rect on the roof
         step writes four reaches of its own into STATE.wing.over; until then
         the plain eave all round, as before */
      try { if (WG.over) { var kk, tlW = !!(__PROD && __PROD._roofTails), hiWO = tlW ? 5.0 : OVER_LIM.max, loWO = tlW ? Math.min(OVER_LIM.min, _tailsRake()) : OVER_LIM.min; for (kk in wingOver) if (wingOver.hasOwnProperty(kk) && typeof WG.over[kk] === 'number') wingOver[kk] = clamp(WG.over[kk], loWO, hiWO); } } catch (eWO) {}
      var wing = _roofPlanesBox(box, sideZ ? 'l' : 'w', hipOver, wingPitch, wingOver, wingDy) || [];
      var k;
      for (k = 0; k < wing.length; k++) { wing[k].id = 'W' + (wing[k].id || ('P' + k)); wing[k].wing = true; }
      var trimmed = _roofTrimT(main, wing, WG.side);
      if ((WG.flush === 'lo' || WG.flush === 'hi') && R.type === 'hip') trimmed = _roofMergeFlush(trimmed, WG.flush === 'hi' ? 'H1' : 'H0');
      return trimmed;
    }

    /* THE HIPPED L'S FLUSH SIDE IS ONE PLANE (core-v757, ANY HOUSE thread).
       With the wing flush with one end of the bar, that end's hip and the
       wing's slope on the same side rise from one eave line at one pitch:
       they are one plane, and drawn as two they had a hip cap and a valley
       on a line where the roof does not fold. So the two are merged (their
       shared edge dropped from the outline, the eave the whole side, the
       ridge the wing's), the bar's slivers that the merge leaves at no area
       are dropped, and so is the valley that ran along the shared edge --
       the L has one valley, at its inside corner. Only when the two really
       are one plane (same eave line, same normal); otherwise nothing changes. */
    var _roofMergeNote = '';
    function _roofMergeFlush(planes, hipId) {
      _roofMergeNote = '';
      var H = null, Wp = null, i;
      for (i = 0; i < planes.length; i++) if (!planes[i].wing && planes[i].id === hipId) H = planes[i];
      if (!H || !H.eave || !H.n) { _roofMergeNote = 'no ' + hipId; return planes; }
      var e0 = H.eave[0], e1 = H.eave[1], ex = e1[0] - e0[0], ez = e1[2] - e0[2], eL = Math.hypot(ex, ez) || 1;
      for (i = 0; i < planes.length; i++) {
        var P = planes[i];
        if (!P.wing || P.kind !== 'slope' || !P.eave || !P.n) continue;
        var d = (P.n[0] * H.n[0] + P.n[1] * H.n[1] + P.n[2] * H.n[2]) / ((Math.hypot(P.n[0], P.n[1], P.n[2]) * Math.hypot(H.n[0], H.n[1], H.n[2])) || 1);
        if (d < 0.9995 || Math.abs(P.eave[0][1] - e0[1]) > 0.005) continue;
        var off = Math.abs(((P.eave[0][0] - e0[0]) * -ez + (P.eave[0][2] - e0[2]) * ex) / eL);
        if (off > 0.005) continue;
        Wp = P; break;
      }
      if (!Wp) { _roofMergeNote = hipId + ' and the wing\'s slope are not one plane (the wing as deep as the bar is wide makes them one)'; return planes; }
      /* the outline of the two: every edge of both, the one they share
         (it runs both ways) dropped, the rest chained */
      function cleanP(Q) {
        var o = [], k;
        for (k = 0; k < Q.length; k++) {
          var q = Q[k], l = o.length ? o[o.length - 1] : null;
          if (l && Math.hypot(l[0] - q[0], l[1] - q[1], l[2] - q[2]) < 1e-4) continue;
          o.push(q);
        }
        if (o.length > 1 && Math.hypot(o[0][0] - o[o.length - 1][0], o[0][1] - o[o.length - 1][1], o[0][2] - o[o.length - 1][2]) < 1e-4) o.pop();
        var a2 = 0;
        for (k = 0; k < o.length; k++) { var u = o[k], v = o[(k + 1) % o.length]; a2 += u[0] * v[2] - v[0] * u[2]; }
        if (a2 < 0) o.reverse();
        return o;
      }
      var A = cleanP(H.pts), B = cleanP(Wp.pts), E = [], k1, k2;
      [A, B].forEach(function (Q) { for (var q = 0; q < Q.length; q++) E.push([Q[q], Q[(q + 1) % Q.length]]); });
      var same = function (p, q) { return Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]) < 0.005; };
      var gone = {}, shared = null;
      for (k1 = 0; k1 < E.length; k1++) for (k2 = k1 + 1; k2 < E.length; k2++) {
        if (gone[k1] || gone[k2]) continue;
        if (same(E[k1][0], E[k2][1]) && same(E[k1][1], E[k2][0])) { gone[k1] = gone[k2] = 1; shared = E[k1]; }
      }
      if (!shared) { _roofMergeNote = hipId + ' and ' + Wp.id + ' share no edge'; return planes; }
      var rest = [];
      for (k1 = 0; k1 < E.length; k1++) if (!gone[k1]) rest.push(E[k1]);
      var loop = [rest[0][0]], cur = rest[0][1], used = { 0: 1 }, guard = 0;
      while (!same(cur, loop[0]) && guard++ < 64) {
        loop.push(cur);
        var nx = -1;
        for (k1 = 0; k1 < rest.length; k1++) if (!used[k1] && same(rest[k1][0], cur)) { nx = k1; break; }
        if (nx < 0) break;
        used[nx] = 1; cur = rest[nx][1];
      }
      if (!same(cur, loop[0])) { _roofMergeNote = 'the outline of ' + hipId + ' + ' + Wp.id + ' did not close'; return planes; }
      var M = _polyClean(loop.map(function (p) { return [p[0], p[2], p[1]]; })).map(function (p) { return [p[0], p[2], p[1]]; });
      /* the eave: the two eaves end to end */
      var ev = [H.eave[0], H.eave[1], Wp.eave[0], Wp.eave[1]], tMin = 1e9, tMax = -1e9, pMin = null, pMax = null;
      for (k1 = 0; k1 < ev.length; k1++) { var t = ((ev[k1][0] - e0[0]) * ex + (ev[k1][2] - e0[2]) * ez) / eL; if (t < tMin) { tMin = t; pMin = ev[k1]; } if (t > tMax) { tMax = t; pMax = ev[k1]; } }
      var MP = {}, kk;
      for (kk in Wp) if (Wp.hasOwnProperty(kk)) MP[kk] = Wp[kk];
      MP.id = hipId + Wp.id; MP.pts = M; MP.eave = [pMin, pMax]; MP.area = _polyArea3(M); MP.flushMerge = true;
      var out = [];
      for (i = 0; i < planes.length; i++) {
        var Pq = planes[i];
        if (Pq === H) { out.push(MP); continue; }
        if (Pq === Wp) continue;
        /* a piece the merge leaves with no area */
        if (Pq.piece && Pq.pts && _polyArea3(Pq.pts) < 0.01) continue;
        out.push(Pq);
      }
      /* the valley along the shared edge is no fold */
      var nV = _roofValleys.length;
      var keepV = _roofValleys.filter(function (v) {
        return !((same(v.a, shared[0]) && same(v.b, shared[1])) || (same(v.a, shared[1]) && same(v.b, shared[0])));
      });
      keepV.over = _roofValleys.over;
      _roofValleys = keepV;
      _roofMergeNote = hipId + ' + ' + Wp.id + ' -> one plane ' + MP.id + ' (' + M.length + ' corners, ' + (Math.round(MP.area * 10) / 10) + ' m2), ' + (nV - keepV.length) + ' valley dropped, ' + (planes.length - out.length) + ' plane(s) gone';
      return out;
    }

    /* THE VALLEYS ARE EDGES, NOT AN OVERLAP (core-v326).

       Two roofs laid through each other look like one roof from outside and are
       two from every other point of view: the bill counted covering twice
       where they cross, and a rafter has nowhere to stop. So the wing's slopes
       are cut where they meet the bar's slope that faces them, and that slope of
       the bar loses the triangle the wing roof covers. What is left meets along
       two lines, and those lines are the valleys; they are kept, with their
       length, for the carpenter's roof to put its valley rafters on.

       The cuts are half-space clips (Sutherland-Hodgman): a wing slope keeps
       what is above the bar's facing slope; the bar's facing slope is split in
       three -- outside the wing on either hand, and between them only what is
       above both wing slopes. Every piece is convex, so it draws as a fan. A
       wing whose ridge rises above the bar's would reach over its ridge onto the
       far slope; that case is not cut here, and is said so in the log. */
    var _roofValleys = [], _roofWingHeld = false, _roofLevelNote = '';
    function roofEdges() { return { valleys: _roofValleys.slice(), wingHeld: _roofWingHeld }; }
    function _planeEq(pts) {
      var a = pts[0], b = null, c, i, j;
      /* three points that are not on one line -- and the second not the first
         again, which a polygon trimmed to a corner can carry (core-v329) */
      for (j = 1; j < pts.length; j++) {
        if (Math.abs(pts[j][0] - a[0]) + Math.abs(pts[j][1] - a[1]) + Math.abs(pts[j][2] - a[2]) > 1e-6) { b = pts[j]; break; }
      }
      if (!b) return null;
      for (i = 1; i < pts.length; i++) {
        c = pts[i];
        var ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2], vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2];
        var nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx, L = Math.sqrt(nx * nx + ny * ny + nz * nz);
        if (L > 1e-9) {
          nx /= L; ny /= L; nz /= L;
          if (ny < 0) { nx = -nx; ny = -ny; nz = -nz; }
          return { n: [nx, ny, nz], d: -(nx * a[0] + ny * a[1] + nz * a[2]) };
        }
      }
      return null;
    }
    function _sideOf(p, E) { return E.n[0] * p[0] + E.n[1] * p[1] + E.n[2] * p[2] + E.d; }
    function _clipAbove(poly, E, eps) {
      var out = [], i, n = poly.length;
      eps = eps || 1e-6;
      for (i = 0; i < n; i++) {
        var P = poly[i], Q = poly[(i + 1) % n], sp = _sideOf(P, E), sq = _sideOf(Q, E);
        var inP = sp >= -eps, inQ = sq >= -eps;
        if (inP) out.push(P);
        if (inP !== inQ) {
          var t = sp / (sp - sq);
          out.push([P[0] + (Q[0] - P[0]) * t, P[1] + (Q[1] - P[1]) * t, P[2] + (Q[2] - P[2]) * t]);
        }
      }
      /* a cut through a corner leaves the corner twice; a polygon with the same
         point twice has no plane of its own for whoever reads it next */
      var dd = [], q;
      for (q = 0; q < out.length; q++) {
        var prev = dd.length ? dd[dd.length - 1] : null;
        if (prev && Math.abs(prev[0] - out[q][0]) + Math.abs(prev[1] - out[q][1]) + Math.abs(prev[2] - out[q][2]) < 1e-6) continue;
        dd.push(out[q]);
      }
      if (dd.length > 1) {
        var f0 = dd[0], l0 = dd[dd.length - 1];
        if (Math.abs(f0[0] - l0[0]) + Math.abs(f0[1] - l0[1]) + Math.abs(f0[2] - l0[2]) < 1e-6) dd.pop();
      }
      return dd;
    }
    function _clipAxis(poly, ax, val, keepGreater) {
      /* a vertical plane x = val (ax 0) or z = val (ax 2) */
      var E = { n: [0, 0, 0], d: 0 };
      E.n[ax] = keepGreater ? 1 : -1;
      E.d = keepGreater ? -val : val;
      return _clipAbove(poly, E);
    }
    function _polyArea3(pts) {
      var a = [0, 0, 0], i, n = pts.length;
      for (i = 1; i + 1 < n; i++) {
        var ux = pts[i][0] - pts[0][0], uy = pts[i][1] - pts[0][1], uz = pts[i][2] - pts[0][2];
        var vx = pts[i + 1][0] - pts[0][0], vy = pts[i + 1][1] - pts[0][1], vz = pts[i + 1][2] - pts[0][2];
        a[0] += uy * vz - uz * vy; a[1] += uz * vx - ux * vz; a[2] += ux * vy - uy * vx;
      }
      return Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2]) / 2;
    }
    function _roofTrimT(main, wing, side) {
      _roofValleys = [];
      var sideZ = (side === 'z0' || side === 'z1'), sgn = (side === 'z1' || side === 'x1') ? 1 : -1;
      var axis = sideZ ? 2 : 0, across = sideZ ? 0 : 2;
      /* the bar's slope that faces the wing: the one whose normal leans that way */
      var near = null, ni = -1, i, best = 0;
      for (i = 0; i < main.length; i++) {
        if (main[i].kind !== 'slope' || !main[i].n) continue;
        var lean = main[i].n[axis] * sgn;
        if (lean > best) { best = lean; near = main[i]; ni = i; }
      }
      if (!near) return main.concat(wing);
      var NE = _planeEq(near.pts);
      if (!NE) return main.concat(wing);
      /* the wing's slopes, cut to what stands above that slope */
      var wingOut = [], wEq = [], wSpan = [1e9, -1e9], k, j;
      for (k = 0; k < wing.length; k++) {
        var W = wing[k], pts = W.pts;
        for (j = 0; j < pts.length; j++) { wSpan[0] = Math.min(wSpan[0], pts[j][across]); wSpan[1] = Math.max(wSpan[1], pts[j][across]); }
        var cut = _clipAbove(pts, NE);
        if (cut.length < 3) continue;
        var edgeA = null, edgeB = null;
        for (j = 0; j < cut.length; j++) {
          var P = cut[j], Q = cut[(j + 1) % cut.length];
          if (Math.abs(_sideOf(P, NE)) < 1e-4 && Math.abs(_sideOf(Q, NE)) < 1e-4) { edgeA = P; edgeB = Q; }
        }
        if (edgeA && W.kind === 'slope') {
          var dxv = edgeB[0] - edgeA[0], dyv = edgeB[1] - edgeA[1], dzv = edgeB[2] - edgeA[2];
          _roofValleys.push({ a: edgeA, b: edgeB, len: Math.sqrt(dxv * dxv + dyv * dyv + dzv * dzv), wing: W.id, bar: near.id });
        }
        var W2 = {}; for (var kk in W) if (W.hasOwnProperty(kk)) W2[kk] = W[kk];
        W2.pts = cut; W2.area = _polyArea3(cut);
        if (W.kind === 'slope') wEq.push(_planeEq(W.pts));
        wingOut.push(W2);
      }
      /* the bar's facing slope: outside the wing on either hand, and between only
         what rises above both wing slopes */
      var mainOut = [];
      /* THE WING'S SURFACE IS THE LOWER OF ITS TWO PLANES, and a point of the
         bar's slope is covered only when it is under that surface. Clipping the
         middle strip above BOTH wing planes cut it away almost whole, because
         each wing plane keeps rising past the wing's ridge, extended -- the
         bar's slope over the far half of the wing was found below the near
         wing plane and thrown out (Marek: strecha nad main run jakoby vyrizla
         cela). So the strip is split at the wing's ridge and each half is
         clipped above its own wing plane only. */
      var ridgeAt = null, wl = null, wr = null;
      for (k = 0; k < wing.length; k++) if (wing[k].kind === 'slope' && wing[k].ridge) { ridgeAt = wing[k].ridge[0][across]; break; }
      for (k = 0; k < wing.length; k++) {
        if (wing[k].kind !== 'slope' || !wing[k].eave) continue;
        var eaveAt = wing[k].eave[0][across];
        if (ridgeAt != null && eaveAt < ridgeAt) wl = _planeEq(wing[k].pts); else wr = _planeEq(wing[k].pts);
      }
      for (i = 0; i < main.length; i++) {
        if (i !== ni) { mainOut.push(main[i]); continue; }
        var Q0 = near.pts;
        var left = _clipAxis(Q0, across, wSpan[0], false), right = _clipAxis(Q0, across, wSpan[1], true);
        var strip = _clipAxis(_clipAxis(Q0, across, wSpan[0], true), across, wSpan[1], false);
        var midL, midR;
        if (ridgeAt != null && wl && wr) {
          midL = _clipAbove(_clipAxis(strip, across, ridgeAt, false), wl);
          midR = _clipAbove(_clipAxis(strip, across, ridgeAt, true), wr);
        } else {
          midL = strip; midR = [];
          for (j = 0; j < wEq.length; j++) if (wEq[j]) midL = _clipAbove(midL, wEq[j]);
        }
        var parts = [[left, 'a'], [midL, 'b'], [midR, 'c'], [right, 'd']], p;
        for (p = 0; p < parts.length; p++) {
          if (parts[p][0].length < 3) continue;
          var M2 = {}; for (var kk2 in near) if (near.hasOwnProperty(kk2)) M2[kk2] = near[kk2];
          M2.pts = parts[p][0]; M2.area = _polyArea3(parts[p][0]); M2.id = near.id + parts[p][1]; M2.piece = true;
          mainOut.push(M2);
        }
      }
      /* the wing's ridge above the bar's: the far slope would be reached too */
      var barTop = -1e9, wingTop = -1e9;
      for (i = 0; i < near.pts.length; i++) barTop = Math.max(barTop, near.pts[i][1]);
      for (k = 0; k < wing.length; k++) for (j = 0; j < wing[k].pts.length; j++) wingTop = Math.max(wingTop, wing[k].pts[j][1]);
      _roofValleys.over = wingTop > barTop + 0.01;
      return mainOut.concat(wingOut);
    }

    /* ONE RECTANGLE, ONE ROOF (core-v310). This is the whole of what
       roofPlanes() used to be, with three things it can now be told instead of
       reading them off the building: the rectangle to roof, which way its ridge
       runs, and which of its ends are hipped. Left unsaid, it behaves exactly as
       it always did -- the building's own outline, STATE.roof.axis, hipAt(). */
    function _roofPlanesBox(box, axisOver, hipOver, pitchOver, overOver, yOver) {
      var R = STATE.roof, top = (R.eaveY != null) ? R.eaveY : wallTop(), P = poly(), i;
      /* a wing's eave may stand off the bar's (core-v484, _levelSeat) */
      if (typeof yOver === 'number' && isFinite(yOver)) top += yOver;
      /* THE PLANE IS THE RAFTER'S TOP, SO IT SITS A RAFTER ABOVE THE PLATE
         (core-v399): a product that lays its rafters under the plane tells the
         guider how much higher the eave is than the wall top -- the rafter's
         depth along the slope -- and the planes, the deck and the covering all
         rise by that, while the plate stays on the wall and the rafter's
         underside meets it. Products that say nothing keep the eave on the
         wall top as before. */
      try { if (typeof __PROD._eaveLift === 'function') { var lf = +__PROD._eaveLift(); if (isFinite(lf) && lf > 0) top += lf; } } catch (eLf) {}
      /* the pitch is the roof's unless the caller has one of its own -- the wing
         of a T with its ridge held level with the bar's (core-v324) */
      var PITCH = (pitchOver != null) ? pitchOver : R.pitch;
      var x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9;
      if (box) { x0 = box.x0; x1 = box.x1; z0 = box.z0; z1 = box.z1; }
      else for (i = 0; i < P.length; i++) {
        x0 = Math.min(x0, P[i][0]); x1 = Math.max(x1, P[i][0]);
        z0 = Math.min(z0, P[i][1]); z1 = Math.max(z1, P[i][1]);
      }
      var AXIS = axisOver || R.axis;

      /* THREE ROOFS, NOT TWO.

         flat   dead level, and it has a parapet -- that is what it is for. A fall
                is made in the covering build-up, not by tilting the frame.
         mono   one plane with a real fall and an eave. This is what used to be
                called flat, and tilting it was how the parapet got broken.
         gable  two planes and a ridge.

         They were two, with a flag on the flat one, and the flag could not carry
         the difference: the parapet variant ended up tilted with everything else.
         A roof that is level and a roof that falls are not one roof with a
         setting. */
      if (R.type === 'flat') {
        /* TO THE OUTER FACE OF THE PANELS. The deck used to stop at the inside
           face of the parapet studs, which left the rafters ending in mid-wall
           with nothing over the top plate. They run the full width and finish
           flush with the outside of the panel, passing between the studs that
           carry on past them. */
        var ovF = 0;
        return [{
          id: 'R0', kind: 'flat', pitch: 0,
          pts: [
            [x0 - ovF, top, z0 - ovF], [x1 + ovF, top, z0 - ovF],
            [x1 + ovF, top, z1 + ovF], [x0 - ovF, top, z1 + ovF]
          ],
          eave:  [[x0 - ovF, top, z0 - ovF], [x1 + ovF, top, z0 - ovF]],
          ridge: null,
          area:  (x1 - x0 + 2 * ovF) * (z1 - z0 + 2 * ovF),
          n:     [0, 1, 0]
        }];
      }

      if (R.type === 'mono') {
        /* A PARAPET AND AN OVERHANG ARE THE SAME DECISION MADE TWICE, and they
           cannot both be true. Either the deck runs out past the wall and is
           finished with a drip and a gutter, or it stops INSIDE a parapet that
           the covering turns up against. Having both put the joists out beyond
           the wall they are supposed to bear on -- which is exactly the rafters
           you saw hanging off the plate.

           With a parapet the plane is inset by the wall thickness, because that
           is where the inside face of the parapet studs is. */
        /* A FLAT ROOF IS A MONO-PITCH, AND IT IS ONE PLANE OF THE SAME KIND THE
           GABLE IS MADE OF.

           Nothing drains off level, so this always has a fall. Giving it a low
           edge and a high edge, named the way the gable names its eave and its
           ridge, means everything downstream stops caring which roof it is: the
           rafters, the sheathing and the bearing all read the two edges. That
           deleted the whole second branch that used to handle the flat case.

           THE OVERHANG IS PAST THE FINISHED FACE. R.eave is how far the roof
           reaches beyond the cladding; the facade's own thickness is added here,
           so a change of cladding cannot silently eat the overhang. */
        /* EACH SIDE REACHES ITS OWN DISTANCE. This read R.eave -- one number for
           all four -- so dragging the rect stored four and the roof used the
           smallest of them: the drag made the roof SHRINK. The four are the
           truth; R.eave is only what an untouched project starts at. */
        var F0 = facadeThk(), OV = _roofOverForPlanes(overOver || _roofOver(), axisOver || R.axis);
        var oX0 = OV.x0 + F0, oX1 = OV.x1 + F0, oZ0 = OV.z0 + F0, oZ1 = OV.z1 + F0;

        /* AND THE RAKE COVERS THE LAST RAFTER, INSTEAD OF STOPPING ON IT.

           The plane ended on the rake overhang exactly, and the outermost rafter
           is CENTRED on that line -- so half of it stuck out past the boarding
           with its end grain in the weather. At the minimum overhang the rafter
           is right on the corner of the building and there is nothing over it at
           all.

           Half a rafter clears the timber; the 30 mm past that is what makes it a
           drip rather than a flush edge, and it is what the verge flashing folds
           over. It is added to the RAKE only -- the eave has its own detail with
           the gutter hanging off it, and moving that would drop the trough.

           The fall runs along x here, so the rake sides are the z pair; the other
           branch has it the other way round and says so where it happens. */
        var rkT = __PROD.RAFTER.t / 2 + 0.030;
        var alongL = (AXIS === 'l');
        var lowA, lowB, hiA, hiB, span;
        if (alongL) {
          /* fall along x -> the rake sides are z0 and z1 */
          span = (x1 - x0) + oX0 + oX1;
          lowA = [x0 - oX0, top, z0 - oZ0 - rkT]; lowB = [x0 - oX0, top, z1 + oZ1 + rkT];
          hiA  = [x1 + oX1, top, z0 - oZ0 - rkT]; hiB  = [x1 + oX1, top, z1 + oZ1 + rkT];
        } else {
          /* fall along z -> the rake sides are x0 and x1 */
          span = (z1 - z0) + oZ0 + oZ1;
          lowA = [x0 - oX0 - rkT, top, z0 - oZ0]; lowB = [x1 + oX1 + rkT, top, z0 - oZ0];
          hiA  = [x0 - oX0 - rkT, top, z1 + oZ1]; hiB  = [x1 + oX1 + rkT, top, z1 + oZ1];
        }
        var fall = span * Math.tan(PITCH * Math.PI / 180);
        hiA[1] += fall; hiB[1] += fall;
        var runL = Math.hypot(lowB[0] - lowA[0], lowB[2] - lowA[2]);
        return [{
          id: 'R0', kind: 'slope', pitch: PITCH,
          pts:   [lowA, hiA, hiB, lowB],
          eave:  [lowA, lowB],
          ridge: [hiA, hiB],
          area:  runL * Math.hypot(span, fall),

          /* THE MONO'S NORMAL WAS [0, 1, 0] -- THE NORMAL OF A LEVEL PLANE.

             The gable publishes a real one, with the sine and cosine of the pitch
             in it. This one was a placeholder that nobody had needed until the
             build-up started asking the plane which way it faced -- and then
             everything derived from it collapsed: the eave reach came out as
             d * sin(0) = 0, and the vertical lift as d / cos(0) = d.

             So the layers stood still while the rafters tilted away from them,
             and the error grew with the pitch exactly as he described. The gable
             did not have it because the gable's normal was right all along.

             Derived from the plane's own fall rather than written out again: the
             slope runs from the low edge to the high one, and the normal leans
             back against it. */
          n: (function () {
            var ux2 = (hiA[0] - lowA[0]), uz2 = (hiA[2] - lowA[2]);
            var uL2 = Math.hypot(ux2, uz2) || 1;
            var pr = PITCH * Math.PI / 180;
            return [-(ux2 / uL2) * Math.sin(pr), Math.cos(pr), -(uz2 / uL2) * Math.sin(pr)];
          })()
        }];
      }

      /* gable: the ridge runs along the axis named in STATE.roof.axis, the slope
         falls across the other one */
      var alongL = (AXIS === 'l');
      var FG = facadeThk(), OG = _roofOverForPlanes(overOver || _roofOver(), axisOver || R.axis);
      /* the eave sides are the two the slope falls to; the rake sides are the
         gable ends. Which pair is which follows the ridge axis. */
      var eaveA = (alongL ? OG.x0 : OG.z0) + FG, eaveB = (alongL ? OG.x1 : OG.z1) + FG;
      var rakeA = (alongL ? OG.z0 : OG.x0) + FG, rakeB = (alongL ? OG.z1 : OG.x1) + FG;
      var ro = Math.max(rakeA, rakeB);

      /* THE PLANE HAS TO STAND WHERE THE TIMBER STANDS.

         This took the DEEPER of the two eaves and used it both ways, so the roof
         surface was symmetric about the middle of the building. The trusses have
         not been symmetric since the day they got two tails and a ridge that
         shifts between them -- so the plane and the frame under it were two
         different roofs, and anything reading the plane got the wrong answer.

         The gutter is what showed it: it holds the published eave line, and on
         the tight side that line was floating out where no rafter ended.

         Both edges are their own now, and the ridge sits midway between them --
         the same rule the truss uses, written once on each side of the same
         building rather than twice differently. */
      var edgeA = alongL ? (x0 - eaveA) : (z0 - eaveA);
      var edgeB = alongL ? (x1 + eaveB) : (z1 + eaveB);
      var half = (edgeB - edgeA) / 2;
      var rise = half * Math.tan(PITCH * Math.PI / 180);
      var ridgeY = top + rise;
      var cx = alongL ? ((edgeA + edgeB) / 2) : ((x0 + x1) / 2);
      var cz = alongL ? ((z0 + z1) / 2) : ((edgeA + edgeB) / 2);

      /* A HIP IS THE SAME ROOF WITH ITS ENDS FOLDED IN. */
      if (R.type === 'hip') {
        /* THE HIP KEEPS THE GABLE'S DIRECTION, AND ITS ENDS STAY WHERE THEY ARE
           (core-v269).

           The ridge used to run the longer way of whatever the footprint was at
           the moment, so dragging the plan past square turned the whole roof and
           moved the hipped ends to the other pair of walls, while a gable keeps
           the direction set in STATE.roof.axis however the plan is dragged
           (Marek: sedlova ma vzdy stejny smer, valba to nerespektuje; strana kde
           se valba implementuje se nesmi menit). Now the ridge runs where the
           gable's does, and the hipped ends are always the two walls across it.

           An end as deep as the side run (equal pitch all round) needs room for
           it; when the end wall is the wider one there is not enough length along
           the ridge, so the end is made shorter and steeper instead -- down to a
           10 cm ridge, never a flipped roof. The overhangs are read by the side
           they are on (x or z), not by eave/rake, so they stay on their walls. */
        /* A HIPPED END IS AN EAVE, NEVER A RAKE (core-v333). The end of a gable
           roof is a rake and may be dragged in to next to nothing; the same end
           hipped has rafters coming down to it and needs the eave's reach. A
           project saved with the rake at 100 mm kept that number when its end
           was hipped, and the hip's jacks stopped on the plate (Marek: cela
           valby nepresahuji pozednici, natoz zed). A hipped end reaches at
           least as far as the eave sides do. */
        var alongZ = alongL;                                   /* the gable's own direction */
        var hipLo = hipOver ? !!hipOver.lo : hipAt('lo'), hipHi = hipOver ? !!hipOver.hi : hipAt('hi');
        /* BUT ONLY WHERE THE END'S VALUE CAN BE A LEFTOVER (core-v337). The lift
           was for the T house, whose saved projects kept a 100 mm rake that the
           old truss cap had crushed. On a sauna with a hip all round, dragging
           an end in to its minimum is a choice, and the lift threw it back to
           the sides' reach -- 500 instead of 150 (Marek). A product that has
           set its own overhang cap is the one that can carry a crushed
           leftover; every other product's end stays where it was dragged. */
        var liftHip = (typeof __PROD._productOverCap === 'function');
        var eaveSide = alongZ ? (OG.x0 + OG.x1) / 2 : (OG.z0 + OG.z1) / 2;
        var oX0h = (liftHip && !alongZ && hipLo) ? Math.max(OG.x0, eaveSide) : OG.x0;
        var oX1h = (liftHip && !alongZ && hipHi) ? Math.max(OG.x1, eaveSide) : OG.x1;
        var oZ0h = (liftHip && alongZ && hipLo) ? Math.max(OG.z0, eaveSide) : OG.z0;
        var oZ1h = (liftHip && alongZ && hipHi) ? Math.max(OG.z1, eaveSide) : OG.z1;
        var hEx0 = x0 - (oX0h + FG), hEx1 = x1 + (oX1h + FG);
        var hRz0 = z0 - (oZ0h + FG), hRz1 = z1 + (oZ1h + FG);
        var hCx = (hEx0 + hEx1) / 2, hCz = (hRz0 + hRz1) / 2;
        var hHalfX = (hEx1 - hEx0) / 2, hHalfZ = (hRz1 - hRz0) / 2;
        var hRunS = alongZ ? hHalfX : hHalfZ;                  /* eave to ridge, across it */
        var hSpan = alongZ ? (hRz1 - hRz0) : (hEx1 - hEx0);    /* end eave to end eave, along it */
        var nHip = (hipLo ? 1 : 0) + (hipHi ? 1 : 0);
        var hDepth = hRunS;
        if (nHip && hDepth * nHip > hSpan - 0.10) hDepth = Math.max(0.05, (hSpan - 0.10) / nHip);
        var hRise = hRunS * Math.tan(PITCH * Math.PI / 180);
        var hY = top + hRise;
        var sn = Math.sin(PITCH * Math.PI / 180), cs = Math.cos(PITCH * Math.PI / 180);
        var dLo = hipLo ? hDepth : 0, dHi = hipHi ? hDepth : 0;
        /* A HIP NEVER RUNS PAST THE WING'S RIDGE (core-v328). On a T the bar's
           hipped end sets its apex back by the roof's depth; on a short arm that
           apex would land past the line where the wing's ridge meets the bar's
           and the hip would eat the junction. The caller may name a coordinate
           along the ridge the apex must not pass; the hip is then shorter and
           steeper, each end with its own pitch (Marek: vrchol valby nikdy nesmi
           jit dal nez je hreben side run -- pak musi mit jiny uhel). */
        var limLo = (hipOver && hipOver.limLo != null) ? hipOver.limLo : null;
        var limHi = (hipOver && hipOver.limHi != null) ? hipOver.limHi : null;
        var end0 = alongZ ? hRz0 : hEx0, end1 = alongZ ? hRz1 : hEx1;
        if (hipLo && limLo != null) dLo = Math.max(0.10, Math.min(dLo, limLo - end0));
        if (hipHi && limHi != null) dHi = Math.max(0.10, Math.min(dHi, end1 - limHi));
        var peLo = Math.atan2(hRise, Math.max(0.05, dLo)), peHi = Math.atan2(hRise, Math.max(0.05, dHi));
        var snLo = Math.sin(peLo), csLo = Math.cos(peLo), snHi = Math.sin(peHi), csHi = Math.cos(peHi);
        var rA, rB;
        if (alongZ) {
          rA = [hCx, hY, hRz0 + dLo];
          rB = [hCx, hY, hRz1 - dHi];
        } else {
          rA = [hEx0 + dLo, hY, hCz];
          rB = [hEx1 - dHi, hY, hCz];
        }
        var slopeL = Math.sqrt(hRunS * hRunS + hRise * hRise);
        var slopeLo = Math.sqrt(dLo * dLo + hRise * hRise), slopeHi = Math.sqrt(dHi * dHi + hRise * hRise);
        var ridgeLen = alongZ ? (rB[2] - rA[2]) : (rB[0] - rA[0]);
        var longSide = hSpan;
        var endSide = alongZ ? (hEx1 - hEx0) : (hRz1 - hRz0);
        var trapA = ((longSide + Math.max(0, ridgeLen)) / 2) * slopeL;

        if (alongZ) {
          var outZ = [
            { id: 'R0', kind: 'slope', pitch: PITCH, pts: [[hEx0, top, hRz0], rA, rB, [hEx0, top, hRz1]],
              eave: [[hEx0, top, hRz0], [hEx0, top, hRz1]], ridge: [rA, rB],
              area: trapA, n: [-sn, cs, 0] },
            { id: 'R1', kind: 'slope', pitch: PITCH, pts: [[hEx1, top, hRz0], rA, rB, [hEx1, top, hRz1]],
              eave: [[hEx1, top, hRz0], [hEx1, top, hRz1]], ridge: [rA, rB],
              area: trapA, n: [sn, cs, 0] }
          ];
          if (hipLo) outZ.push({ id: 'H0', kind: 'hip', pitch: peLo * 180 / Math.PI, pts: [[hEx0, top, hRz0], rA, rA, [hEx1, top, hRz0]],
              eave: [[hEx0, top, hRz0], [hEx1, top, hRz0]], ridge: [rA, rA],
              area: (endSide / 2) * slopeLo, n: [0, csLo, -snLo] });
          if (hipHi) outZ.push({ id: 'H1', kind: 'hip', pitch: peHi * 180 / Math.PI, pts: [[hEx0, top, hRz1], rB, rB, [hEx1, top, hRz1]],
              eave: [[hEx0, top, hRz1], [hEx1, top, hRz1]], ridge: [rB, rB],
              area: (endSide / 2) * slopeHi, n: [0, csHi, snHi] });
          return outZ;
        }
        return [
          { id: 'R0', kind: 'slope', pitch: PITCH, pts: [[hEx0, top, hRz0], rA, rB, [hEx1, top, hRz0]],
            eave: [[hEx0, top, hRz0], [hEx1, top, hRz0]], ridge: [rA, rB],
            area: trapA, n: [0, cs, -sn] },
          { id: 'R1', kind: 'slope', pitch: PITCH, pts: [[hEx0, top, hRz1], rA, rB, [hEx1, top, hRz1]],
            eave: [[hEx0, top, hRz1], [hEx1, top, hRz1]], ridge: [rA, rB],
            area: trapA, n: [0, cs, sn] },
          ].concat(hipLo ? [{ id: 'H0', kind: 'hip', pitch: peLo * 180 / Math.PI, pts: [[hEx0, top, hRz0], rA, rA, [hEx0, top, hRz1]],
            eave: [[hEx0, top, hRz0], [hEx0, top, hRz1]], ridge: [rA, rA],
            area: (endSide / 2) * slopeLo, n: [-snLo, csLo, 0] }] : [])
           .concat(hipHi ? [{ id: 'H1', kind: 'hip', pitch: peHi * 180 / Math.PI, pts: [[hEx1, top, hRz0], rB, rB, [hEx1, top, hRz1]],
            eave: [[hEx1, top, hRz0], [hEx1, top, hRz1]], ridge: [rB, rB],
            area: (endSide / 2) * slopeHi, n: [snHi, csHi, 0] }] : []);
      }

      if (alongL) {
        var rz0 = z0 - rakeA, rz1 = z1 + rakeB;
        return [
          { id: 'R0', kind: 'slope', pitch: PITCH,
            pts: [[cx - half, top, rz0], [cx, ridgeY, rz0], [cx, ridgeY, rz1], [cx - half, top, rz1]],
            eave:  [[cx - half, top, rz0], [cx - half, top, rz1]],
            ridge: [[cx, ridgeY, rz0], [cx, ridgeY, rz1]],
            area:  Math.sqrt(half * half + rise * rise) * (rz1 - rz0),
            n:     [-Math.sin(PITCH * Math.PI / 180), Math.cos(PITCH * Math.PI / 180), 0] },
          { id: 'R1', kind: 'slope', pitch: PITCH,
            pts: [[cx + half, top, rz0], [cx, ridgeY, rz0], [cx, ridgeY, rz1], [cx + half, top, rz1]],
            eave:  [[cx + half, top, rz0], [cx + half, top, rz1]],
            ridge: [[cx, ridgeY, rz0], [cx, ridgeY, rz1]],
            area:  Math.sqrt(half * half + rise * rise) * (rz1 - rz0),
            n:     [Math.sin(PITCH * Math.PI / 180), Math.cos(PITCH * Math.PI / 180), 0] }
        ];
      }

      /* the rake ends are their own too -- the same fix as the eaves, on the
         other pair of sides. ro is only kept for anything still asking for a
         single number. */
      var rx0 = x0 - rakeA, rx1 = x1 + rakeB;
      return [
        { id: 'R0', kind: 'slope', pitch: PITCH,
          pts: [[rx0, top, cz - half], [rx0, ridgeY, cz], [rx1, ridgeY, cz], [rx1, top, cz - half]],
          eave:  [[rx0, top, cz - half], [rx1, top, cz - half]],
          ridge: [[rx0, ridgeY, cz], [rx1, ridgeY, cz]],
          area:  Math.sqrt(half * half + rise * rise) * (rx1 - rx0),
          n:     [0, Math.cos(PITCH * Math.PI / 180), -Math.sin(PITCH * Math.PI / 180)] },
        { id: 'R1', kind: 'slope', pitch: PITCH,
          pts: [[rx0, top, cz + half], [rx0, ridgeY, cz], [rx1, ridgeY, cz], [rx1, top, cz + half]],
          eave:  [[rx0, top, cz + half], [rx1, top, cz + half]],
          ridge: [[rx0, ridgeY, cz], [rx1, ridgeY, cz]],
          area:  Math.sqrt(half * half + rise * rise) * (rx1 - rx0),
          n:     [0, Math.cos(PITCH * Math.PI / 180), Math.sin(PITCH * Math.PI / 180)] }
      ];
    }

    /* HOW HIGH THE ROOF BEARS OVER A GIVEN PLAN POINT.

       On a gable the trusses all bear on the wall head, so it is the same
       everywhere. On a mono-pitch the roof climbs, so the wall under it has to
       climb with it -- and the two walls it crosses have to be RAKED, stud by
       stud. One function answers that for every wall and every stud, so nothing
       downstream needs to know which roof is overhead. */
    function bearingY(x, z) {
      var R = STATE.roof;
      if (R.type !== 'mono') return wallTop();
      var pl = roofPlanes()[0];
      if (!pl || !pl.ridge) return wallTop();
      var lo = pl.eave[0], hi = pl.ridge[0];
      var dx = hi[0] - lo[0], dz = hi[2] - lo[2];
      var len2 = dx * dx + dz * dz;
      if (len2 < 1e-9) return wallTop();
      var t = ((x - lo[0]) * dx + (z - lo[2]) * dz) / len2;
      t = clamp(t, 0, 1);
      return lo[1] + (hi[1] - lo[1]) * t;
    }

    function apexY() {
      var R = roofPlanes(), y = 0, i, j;
      for (i = 0; i < R.length; i++) for (j = 0; j < R[i].pts.length; j++) y = Math.max(y, R[i].pts[j][1]);
      return y;
    }

    /* WHAT A ROOF IS ACTUALLY BUILT AT.

       A gable below about 15 degrees stops shedding properly for most coverings
       and starts needing a membrane; above 45 it costs a great deal of extra
       timber and covering for headroom nobody uses in a single-storey unit. The
       old floor of 8 let you build something that would leak.

       A flat roof is never flat: it needs a fall to drain, and 2 to 15 degrees is
       the range a mono-pitch is built at. Its geometry does not tilt yet -- that
       is the next step -- so the slider is still gable-only. */
    function pitchMin() {
      if (STATE.roof.type === 'flat') return 0;
      if (STATE.roof.type === 'mono') return 2;
      /* a product may have its own range for a pitched roof -- an A-frame
         lives between 50 and 70 degrees, which no other house does (core-v337) */
      if (__PROD._pitchLim && __PROD._pitchLim.min > 0) return __PROD._pitchLim.min;
      return 15;
    }
    function pitchMax() {
      if (STATE.roof.type === 'flat') return 0;
      if (STATE.roof.type === 'mono') return 15;
      /* 35 not 45: past about 35 a gable of this span buys headroom nobody uses
         in a single-storey unit and pays for it in timber and covering twice. */
      if (__PROD._pitchLim && __PROD._pitchLim.max > 0) return __PROD._pitchLim.max;
      return 35;
    }
    function clampPitch() { STATE.roof.pitch = clamp(STATE.roof.pitch, pitchMin(), pitchMax()); }

    /* -------------------------------------------------------------------------
       LAYER REGISTRY. The only supported way to add construction to a product.
       ------------------------------------------------------------------------- */
    var _layers = [];
    var _lastBuild = { built: [], skipped: [], claimed: {} };

    /* THE VIEWING STAGES. Supplied by the product; the core only steps through
       them and tells every layer which one is showing.

         { key: 'panels', label: 'Wall panels', only: ['<product>-stud-wall'], paint: 'panel' }

       only   the layer ids to build. Everything else, INCLUDING THE GUIDER, is
              left out -- a stage that shows one assembly has to hide what
              surrounds it or there is nothing to judge.
       paint  a word the layer understands. The core never reads it. */
    var _vizStages = [{ key: 'full', label: 'Full build' }];

    function setVizStages(list) {
      _vizStages = (list && list.length) ? list : [{ key: 'full', label: 'Full build' }];
      /* ONE STAGE FOR WHATEVER IS UNDER REVIEW (core-v391): the eye cycled
         through the stages a product declared and none of them was this, so
         the first press showed something else and the reinforcement stayed
         buried. Every product now gets this stage at the end of its own list:
         the piece drawn in magenta stays solid and the building drops to a
         whisper behind it. */
      var hasF = false, q, wants = false;
      try { wants = !!(__PROD && __PROD._focusStage); } catch (eF) { wants = false; }
      for (q = 0; q < _vizStages.length; q++) if (_vizStages[q].key === 'focus') hasF = true;
      /* only the product that asks for it gets this stage -- the others keep
         the eye exactly as it was (core-v392) */
      if (wants && !hasF) _vizStages = _vizStages.concat([{ key: 'focus', label: _uiStr('viz_focus', 'Piece under review') }]);
      if (STATE.vizStage >= _vizStages.length) STATE.vizStage = 0;
      return _vizStages;
    }
    /* THE FOCUS STAGE IS ADDED WHEN THE EYE LOOKS, NOT WHEN A LIST IS SET
       (core-v411, Marek: porad nefunguje stage view). Nobody on the house line
       ever calls setVizStages, so the stage that was only appended there was
       never there and the eye had one stage to cycle through. It is appended
       here, the moment the stages are read, for the product that asks. */
    function _vizWithFocus() {
      /* THE REINFORCEMENT STAGE (core-v474, A-frame): a product with
         __PROD._rebarStage gets it as the first press of the eye -- only the
         bars, stirrups and meshes (userData.rebar) solid, everything else at
         3 per cent, and the base drawn whole */
      var wantsR = false, hasR = false, qr;
      try { wantsR = !!(__PROD && __PROD._rebarStage); } catch (eR) { wantsR = false; }
      for (qr = 0; qr < _vizStages.length; qr++) if (_vizStages[qr].key === 'rebar') hasR = true;
      if (wantsR && !hasR) _vizStages = [_vizStages[0]].concat([{ key: 'rebar', label: _uiStr('viz_rebar', 'Reinforcement') }], _vizStages.slice(1));
      var wants = false, has = false, q;
      try { wants = !!(__PROD && __PROD._focusStage); } catch (eF) { wants = false; }
      for (q = 0; q < _vizStages.length; q++) if (_vizStages[q].key === 'focus') has = true;
      if (wants && !has) _vizStages = _vizStages.concat([{ key: 'focus', label: _uiStr('viz_focus', 'Piece under review') }]);
      return _vizStages;
    }
    function vizStages() { return _vizWithFocus(); }
    function viz() { var L = _vizWithFocus(); return L[STATE.vizStage % L.length] || L[0]; }
    function setViz(n) {
      var L = _vizWithFocus();
      STATE.vizStage = ((n | 0) % L.length + L.length) % L.length;
      savePref();
      rebuild();
      _log('viz -> ' + STATE.vizStage + ' ' + viz().key);
      return viz();
    }
    function nextViz() { return setViz(STATE.vizStage + 1); }

    /* =========================================================================
       TWO MORE WAYS OF LOOKING, BOTH ACROSS THE WHOLE BUILDING (core-v101).

       A stage could already hide LAYERS -- that is what 'only' does, and it is
       right for the wall panels, which are one layer. The two new ones are not
       about layers at all:

         paint: 'timber'   every piece of structural timber in the building,
                           whatever layer drew it, each PROFILE in its own
                           colour; everything else hidden. What somebody orders
                           from the sawmill, seen at once.
         ghost: 0.05       everything faded to almost nothing EXCEPT the layers
                           named in 'keep' -- the services, left solid inside a
                           glass house.

       Both work on the finished group rather than on the build, so no layer has
       to know a stage exists and the bill is untouched either way (Marek). */

    /* which codes are timber. Asked of the catalogue NAME rather than a list of
       prefixes kept here, because the catalogue is the thing that grows. */
    /* THE CATALOGUE ROW FOR A CODE, FROM WHERE ANYONE CAN REACH IT.

       There is a _pickRow already, and it lives inside the block that handles
       tapping. Everything outside that block -- the coating, the timber test --
       called it inside a try/catch, got a ReferenceError, swallowed it, and
       carried on as though the catalogue held nothing. The coating said
       "nothing coatable" for four rounds because of it, and the try/catch is
       what hid it: 149 codes in the bill, none with a row (Marek's log).

       The same lookup, declared where the rest of the file can see it. */
    function _catRow(code) {
      var pd = null;
      try { pd = global.productData; } catch (eP) { pd = null; }
      if (!pd || !pd.length || !code) return null;
      var i;
      for (i = 0; i < pd.length; i++) if (pd[i] && pd[i][0] === code) return pd[i];
      return null;
    }

    /* a dimension cell from the catalogue, in millimetres */
    function _mmOf(v) {
      var s = String(v == null ? '' : v).trim();
      if (!s) return 0;
      var m = s.match(/([\d.]+)\s*(mm|cm|m)?/i);
      if (!m) return 0;
      var n = parseFloat(m[1]);
      if (!isFinite(n)) return 0;
      var u = (m[2] || 'mm').toLowerCase();
      return u === 'm' ? n * 1000 : (u === 'cm' ? n * 10 : n);
    }

    function _isTimber(code) {
      var row = null;
      try { row = _catRow(code); } catch (e) {}
      /* THE CATEGORY DECIDES FIRST, AND IT IS NOT A GUESS.

         Leaving the fit-out out by LAYER was one defence and it leaned on the
         layer being named something recognisable. The catalogue already sorts
         these properly: a bed and a dining table are FURNITURE, a sink is
         SANITARY, and no reading of a name should be able to pull them into a
         view meant for the order to the sawmill. So the category is asked
         first, and only then the name (Marek: porad tam mas fitouts). */
      var cat = String((row && row[1]) || '').toUpperCase();
      if (/FURNITURE|SANITARY|APPLIANCE|KITCHEN|LIGHTING|ELECTRIC/.test(cat)) return false;
      var nm = String((row && row[3]) || '').toUpperCase();
      if (!nm) return /^(KVH|KVR|KVL|TST)/.test(String(code || ''));
      return /LUMBER|BATTEN|FURRING|DECKING|TIMBER|STUD|RAFTER|JOIST|PLATE|BEAM|POST/.test(nm) &&
             !/CONNECTOR|TIE|SCREW|NAIL|PLATE 1|PLATE 2/.test(nm);
    }

    /* A PALETTE, NOT A HASH.

       Hashing the code to a hue put a truss and a stud two degrees apart --
       telling them apart was the entire point of the view. These are picked to
       be distinguishable from each other, not generated, and they are handed
       out in the order the profiles first appear, so the biggest groups get the
       clearest colours. Sorted by code first, so the same building always gets
       the same assignment (Marek: kazdy profil ma mit jinou vyraznou barvu). */
    var PROFILE_COLOURS = [
      0xE6194B, 0x3CB44B, 0x4363D8, 0xF58231, 0x911EB4,
      0x42D4F4, 0xF032E6, 0xBFEF45, 0xFABED4, 0x469990,
      0x9A6324, 0xFFE119, 0x800000, 0x000075, 0xA9A9A9,
      0xAAFFC3, 0xDCBEFF, 0x808000, 0xFFD8B1, 0x00FF7F
    ];
    function _profileColour(T, idx) {
      return new T.Color(PROFILE_COLOURS[idx % PROFILE_COLOURS.length]);
    }

    /* THE LOAD-BEARING TIMBER OF THE ROOF, EACH PIECE IN ITS ROLE'S COLOUR.

       The stage for judging a truss the way the PDF sheet shows it: the roof
       layer keeps only its truss members and plates, everything else of it --
       sheathing, battens, covering, fascia -- is hidden, and every member wears
       the colour of what it does, the same colours and the same grouping as the
       sheet's legend: top chord, bottom chord, post, vertical web, diagonal web,
       connector plate (Marek: stage view, barevne rozlisene prvky na vaznicich,
       nosna drevena konstrukce). Lit, so the depth still reads in 3D. The walls
       stay as they are built, so the roof has something to stand on. */
    function _vizTruss(T, st, P) {
      var mats = {}, count = {}, hidden = 0;
      function matOf(role) {
        if (!mats[role]) mats[role] = new T.MeshStandardMaterial({ color: TRUSS_ROLE_COLOR[role], roughness: 0.7, metalness: 0.0 });
        return mats[role];
      }
      st.group.traverse(function (o) {
        if (!o.isMesh) return;
        var u = o.userData || {}, part = u.trussPart, role = null;
        if (part != null && part !== '') role = TRUSS_PART_ROLE[part] || null;
        if (!role) {
          var mk = u.asm, kk = mk ? (typeof mk === 'string' ? mk : (mk.key || '')) : '';
          if (kk === 'hip_chord' || kk === 'hip_web' || (part != null && part !== '')) {
            var bx = new T.Box3().setFromObject(o), sz = bx.getSize(new T.Vector3());
            var horiz = Math.hypot(sz.x, sz.z);
            if (kk === 'hip_web' || part === 'web') role = (sz.y > horiz) ? 'vweb' : 'dweb';
            else role = (sz.y < 0.25 * horiz) ? 'bchord' : 'tchord';
          }
        }
        if (role) {
          o.material = matOf(role);
          count[role] = (count[role] || 0) + 1;
          return;
        }
        var lay = '';
        try { lay = _layerOf(o) || ''; } catch (eL) { lay = ''; }
        if (/-roof$/.test(lay)) { o.visible = false; hidden++; }
      });
      var parts = [], r;
      for (r = 0; r < TRUSS_ROLE_ORDER.length; r++) {
        if (count[TRUSS_ROLE_ORDER[r]]) parts.push(TRUSS_ROLE_ORDER[r] + ' ' + count[TRUSS_ROLE_ORDER[r]]);
      }
      if (P) P('truss stage  members by role: ' + (parts.length ? parts.join(', ') : 'NONE -- no truss in this roof') +
               '; ' + hidden + ' other roof bodies hidden');
    }

    /* THE SAME THREE STAGE VIEWS IN EVERY PRODUCT (core-v299).

       Each product had grown its own list -- the sauna six, the ADU six, the
       shed five, in different orders and with a review view still among them.
       One line, one set (Marek: pojdme je vymyslet pro vsechny projekty
       stejne): the build itself, then

         1  the frame -- wall panels each in its own colour, with the roof
            structure standing on them, both at once,
         2  timber profiles, every piece in the colour of its section,
         3  services.

       A product passes its own prefix and, where a layer is not named after it,
       that layer's name. Nothing else about the list is decided in a product. */
    function stageViews(prefix, opt) {
      opt = opt || {};
      var wall = opt.wall || (prefix + '-stud-wall');
      var roof = opt.roof || (prefix + '-roof');
      var elec = opt.elec || (prefix + '-elec');
      var fit = opt.fitout || (prefix + '-fitout');
      var frameOnly = [wall, roof].concat(opt.frameAlso || []);
      return [
        { key: 'full', label: _t('viz_full', 'Full build') },
        { key: 'frame', label: _t('structure_v2', 'Timber frame'), only: frameOnly, paint: 'frame' },
        { key: 'timber', label: _t('viz_timber', 'Timber profiles'), paint: 'timber', except: [fit] },
        { key: 'services', label: _t('viz_services', 'Services'), ghost: 0.02, keep: [elec] }
      ];
    }

    function _vizAfter(P) {
      var V = viz(), st = _stage(), T = global.THREE;
      if (!st || !st.group || !T) return;
      if (V.paint === 'timber') return _vizTimber(T, st, P);
      /* 'frame' is the two together (core-v299): the product paints each wall
         panel its own colour while it builds, and the roof is painted here by
         what each member does -- one stage instead of two */
      if (V.paint === 'truss' || V.paint === 'frame') return _vizTruss(T, st, P);
      if (V.ghost) return _vizGhost(T, st, V, P);
    }

    /* THE FURNITURE IS NOT STRUCTURE.

       A bed frame and a dining table are timber by any reading of their name,
       and they were turning up in a view whose whole purpose is the order to
       the sawmill. They come from the fit-out, so the fit-out is left out --
       by layer, which is what a layer is for (Marek: jsou tam fitouts, ty tam
       byt nemaji). */
    function _vizStructural(layerId) {
      return !/fitout|furnit|appli|kitchen|sanit/i.test(String(layerId || ''));
    }

    /* WHICH LAYER A BODY BELONGS TO, WHEREVER IT SITS.

       The pass walked the stage group's CHILDREN and treated each as a layer.
       That covered the wall and the roof -- they are layer groups -- and missed
       the fit-out entirely, which is why the furniture came through unhidden
       and unpainted while everything around it was recoloured: the pass never
       reached it. Walking the whole scene and asking each body which layer it
       is under answers the question for a body anywhere, including one an
       asset parked outside the group (Marek: porad jsou tam fitouts). */
    function _layerOf(o) {
      var p = o;
      while (p) {
        if (p.userData && p.userData.layer) return p.userData.layer;
        p = p.parent;
      }
      return '';
    }
    /* the scenery is not the building: lights, the grid, the ground and the
       helpers are left exactly as they are by every stage */
    function _isScenery(o, st) {
      /* anything that is neither in the building's group nor carries a layer or
         a catalogue tag is somebody else's object -- the sun, the person, the
         shadow plane -- and no stage touches it */
      if (st && st.group) {
        var inGroup = false, q = o;
        while (q) { if (q === st.group) { inGroup = true; break; } q = q.parent; }
        if (!inGroup && !_layerOf(o) && !(o.userData && o.userData.asm)) return true;
      }
      var p = o;
      while (p) {
        var n = String((p.name || '')).toLowerCase();
        if (n.indexOf('grid') >= 0 || n.indexOf('helper') >= 0 || n.indexOf('shadow') >= 0 ||
            n.indexOf('ground') >= 0 || n.indexOf('scenery') >= 0 || n.indexOf('guider') >= 0 ||
            n.indexOf('asm-leader') >= 0) return true;
        p = p.parent;
      }
      return false;
    }

    function _vizTimber(T, st, P) {
      var shown = 0, hidden = 0, byCode = {}, offLayers = {}, root = st.scene || st.group;
      function codeOf(o) {
        var m = o.userData && o.userData.asm;
        var tag = m ? (typeof m === 'string' ? m : (m.code || '')) : '';
        return tag ? (((CATALOG[tag] && CATALOG[tag].code) ? CATALOG[tag].code : tag)) : '';
      }
      /* the codes first, so the palette is handed out in a stable order rather
         than in whatever order the traverse happens to run */
      root.traverse(function (o) {
        if (!o.isMesh || _isScenery(o, st)) return;
        if (!_vizStructural(_layerOf(o))) return;
        var code = codeOf(o);
        if (code && _isTimber(code)) byCode[code] = (byCode[code] || 0) + 1;
      });
      var codes = [], k;
      for (k in byCode) if (byCode.hasOwnProperty(k)) codes.push(k);
      codes.sort();

      /* ONE PROFILE, ONE COLOUR -- AND THE SECTION COMES FROM THE CATALOGUE.

         Three earlier goes at this failed, and the reason each time was where
         the section came from. Per CODE put two codes of one section in two
         colours. Per section READ OFF THE GEOMETRY was worse: a truss member's
         geometry is rotated before use, so its own box is the diagonal's
         footprint -- a 1999 x 160 x 60 web at 44 degrees measures 1549 x 1504 --
         and half the "sections" in the log were lengths.

         The catalogue knows the section of every code it sells. That is the only
         source used here; a code with no row falls back to its own name, which
         may split a colour but can never invent a section out of a length.
         Battens and slats are in it like everything else (Marek: ted vsechny
         profily vcetne battens / slats). */
      /* WHAT THIS VIEW SHOWS: PROFILES, NOT BOARDS.

         A slat is a profile and belongs here -- it was falling out because the
         fallback prefix list is KVH, KVR, KVL and TST, and a slat is FVF. When
         the catalogue has not arrived that test is all there is, so the slats
         vanished with it.

         Cladding is the other way round: it is timber, but it is a BOARD, and a
         wall of boards in a profile colour hides the frame this view exists to
         show. Left out on purpose, by its own code (Marek: chybi slats, ty tam
         maji byt take. Nedelat ale cladding, at je to prehledne). */
      function _vizProfile(code) {
        var c = String(code || '').toUpperCase();
        if (/^FVW/.test(c)) return false;              /* cladding and trim: boards */
        if (/^(KVH|KVR|KVL|TST|FVF|ROT)/.test(c)) return true;
        var row = null;
        try { row = _catRow(c); } catch (eP) {}
        var nm = String((row && row[3]) || '').toUpperCase();
        if (/CLADDING|SIDING|SHIPLAP|PANEL|SHEET|BOARD/.test(nm)) return false;
        return _isTimber(c);
      }

      function _sectKey(o) {
        var c2 = codeOf(o);
        if (!c2) return '?';
        var row = null;
        try { row = _catRow(c2); } catch (eR) {}
        if (row) {
          var a1 = _mmOf(row[4]), b1 = _mmOf(row[5]);
          if (a1 > 0 && b1 > 0) {
            return Math.round(Math.max(a1, b1)) + 'x' + Math.round(Math.min(a1, b1));
          }
        }
        return c2;
      }

      var sects = {}, sKeys = [];
      root.traverse(function (o) {
        if (!o.isMesh || _isScenery(o, st)) return;
        if (!_vizStructural(_layerOf(o))) return;
        var c3 = codeOf(o);
        if (!c3 || !_vizProfile(c3)) return;
        var sk = _sectKey(o);
        if (!sects.hasOwnProperty(sk)) { sects[sk] = 0; sKeys.push(sk); }
        sects[sk]++;
      });
      /* biggest first, so the pieces there is most of take the strongest colours
         and two neighbours are never adjacent in the ramp */
      sKeys.sort(function (a, b) { return sects[b] - sects[a]; });
      var slotOfSect = {};
      for (k = 0; k < sKeys.length; k++) slotOfSect[sKeys[k]] = k;

      try {
        var _sl = [];
        for (k = 0; k < sKeys.length; k++) _sl.push(sKeys[k] + ' x' + sects[sKeys[k]]);
        _say('stage2  ' + sKeys.length + ' section(s): ' + _sl.join(' | '));
      } catch (eS2) {}

      /* THE COLOURS ARE BACK IN STAGE 2.

         They were handed out per code, then per section read off the geometry,
         then per section read off the catalogue. Every version put the roof in
         a different set of colours and none of them satisfied the rule, which
         was simply that one profile should look like one profile.

         The reason the last two failed is worth keeping: a truss member's
         geometry is ROTATED before it is used, so its own bounding box is the
         diagonal's footprint -- a 1999 x 160 x 60 web at 44 degrees measures
         1549 x 1504 x 60. Half of those twenty-eight "sections" were lengths.

         So the palette is out. Everything structural is drawn in one timber
         colour and the view does what it is actually for: showing what is there
         (Marek: odstranit ve stage 2 vsechny barvy, zahodit je). */

      root.traverse(function (o) {
        if (!o.isMesh || _isScenery(o, st)) return;
        var id = _layerOf(o);
        var structural = _vizStructural(id);
        var code = codeOf(o);
        if (!structural || !code || !_vizProfile(code)) {
          o.visible = false; hidden++;
          if (!structural) offLayers[id || '(no layer)'] = (offLayers[id || '(no layer)'] || 0) + 1;
          return;
        }
        o.visible = true; shown++;
        try {
          if (!o.userData.__vizMat) { o.material = o.material.clone(); o.userData.__vizMat = 1; }
          var _slot = slotOfSect[_sectKey(o)];
          if (_slot == null) _slot = 0;
          if (o.material.color) o.material.color.copy(_profileColour(T, _slot));
          if (o.material.map) o.material.map = null;
          /* flat, so two neighbouring colours are not shaded into each other */
          if (o.material.roughness != null) o.material.roughness = 0.95;
          if (o.material.metalness != null) o.material.metalness = 0.0;
          o.material.needsUpdate = true;
        } catch (e) {}
      });
      var list = [], off = [];
      for (k = 0; k < codes.length; k++) list.push(codes[k] + ':' + byCode[codes[k]] + '=#' +
        PROFILE_COLOURS[k % PROFILE_COLOURS.length].toString(16));
      for (k in offLayers) if (offLayers.hasOwnProperty(k)) off.push(k + ' x' + offLayers[k]);
      if (P) P('viz      timber -- ' + codes.length + ' profile(s), ' + shown + ' piece(s) shown, ' +
               hidden + ' hidden' +
               (off.length ? (' | not structural: ' + off.join(', ')) : '') +
               ' | ' + list.join(' '));
    }

    function _vizGhost(T, st, V, P) {
      var keep = V.keep || [], solid = 0, faded = 0, root = st.scene || st.group;
      root.traverse(function (o) {
        if (!o.isMesh || _isScenery(o, st)) return;
        var isKeep = keep.indexOf(_layerOf(o)) >= 0;
        o.visible = true;
        if (isKeep) { solid++; return; }
        faded++;
        try {
          if (!o.userData.__vizMat) { o.material = o.material.clone(); o.userData.__vizMat = 1; }
          o.material.transparent = true;
          o.material.opacity = V.ghost;
          o.material.depthWrite = false;
          o.material.needsUpdate = true;
        } catch (e) {}
      });
      if (P) P('viz      ' + V.key + ' -- ' + solid + ' body(ies) left solid in ' +
               (keep.join(', ') || 'nothing') + ', ' + faded + ' faded to ' +
               Math.round(V.ghost * 100) + ' pct');
    }

    /* EVERY LAYER IS CHECKED FOR A VERTEX THAT IS NOT A NUMBER (core-v498,
       the T house's guard of thouse-v256 moved here, Marek: zapne se navigacni
       panel, ale rezy neexistuji). The section tool measures the building's
       box from every mesh at once; one NaN anywhere makes the box NaN, and
       with no box the tool shows its panel and cuts nothing. So each layer is
       walked as soon as it is built: a bad coordinate is zeroed so the box is
       finite, and the layer, the count and the kind of body are said in the
       log, so the source can be fixed rather than hidden. */
    function finiteGuard(g, T, id, log) {
      if (!g || !T || !g.traverse) return 0;
      var nM = 0, nV = 0, nMesh = 0, nPos = 0, kinds = {};
      g.traverse(function (node) {
        if (!node.isMesh || !node.geometry) return;
        nMesh++;
        var k0 = (node.geometry.type || 'geometry') + (node.userData && node.userData.asm ? (' ' + node.userData.asm) : ''), bad = 0, q;
        var pa = node.geometry.attributes && node.geometry.attributes.position;
        if (pa && pa.array) {
          var arr = pa.array, sum0 = 0;
          /* core-v621: one sum says whether any coordinate is not a number
             (NaN and Infinity carry through it); only then one by one */
          for (q = 0; q < arr.length; q++) sum0 += arr[q];
          if (!isFinite(sum0)) for (q = 0; q < arr.length; q++) if (!isFinite(arr[q])) { arr[q] = 0; bad++; }
          if (bad) { pa.needsUpdate = true; node.geometry.boundingBox = null; node.geometry.boundingSphere = null; }
        }
        var p = node.position, sc = node.scale, badP = 0;
        if (!isFinite(p.x) || !isFinite(p.y) || !isFinite(p.z)) { p.set(0, 0, 0); badP++; }
        if (!isFinite(sc.x) || !isFinite(sc.y) || !isFinite(sc.z)) { sc.set(1, 1, 1); badP++; }
        if (!isFinite(node.rotation.x) || !isFinite(node.rotation.y) || !isFinite(node.rotation.z)) { node.rotation.set(0, 0, 0); badP++; }
        if (node.isInstancedMesh && node.instanceMatrix && node.instanceMatrix.array) {
          var im = node.instanceMatrix.array, badI = 0;
          for (q = 0; q < im.length; q++) if (!isFinite(im[q])) { im[q] = 0; badI++; }
          if (badI) { node.instanceMatrix.needsUpdate = true; bad += badI; }
        }
        if (bad || badP) { nM++; nV += bad; nPos += badP; kinds[k0] = (kinds[k0] || 0) + 1; }
      });
      if (nM && log) {
        var ks = Object.keys(kinds), q2, parts = [];
        for (q2 = 0; q2 < ks.length; q2++) parts.push(ks[q2] + ' x' + kinds[ks[q2]]);
        log('cut      ' + id + ': ' + nV + ' coordinate(s) not a number' + (nPos ? (' and ' + nPos + ' placement(s)') : '') + ' in ' + nM + ' of ' + nMesh + ' mesh(es) [' + parts.join(', ') + '] -- zeroed so the section tool has a box; the source is to be fixed');
      }
      return nM;
    }
    function registerLayer(def) {
      if (!def || !def.id) { _log('registerLayer: needs an id'); return null; }
      var i;
      for (i = 0; i < _layers.length; i++) {
        if (_layers[i].id === def.id) { _layers[i] = def; _log('layer replaced: ' + def.id); return def; }
      }
      _layers.push(def);
      _log('layer registered: ' + def.id + ' ' + (def.version || '?') +
           ' claims=' + ((def.claims || []).join(',') || 'none') +
           ' role=' + (def.role || 'NONE -- it will not survive a fork or a saved sheet') +
           (def.role && !isRole(def.role) ? '  <-- NOT ONE OF THE SEVEN' : ''));
      return def;
    }

    function layers() {
      var o = [], i;
      for (i = 0; i < _layers.length; i++) {
        o.push({ id: _layers[i].id, version: _layers[i].version || '?', claims: _layers[i].claims || [] });
      }
      return o;
    }

    function _claimed() {
      var c = {}, i, j, cl;
      for (i = 0; i < _layers.length; i++) {
        cl = _layers[i].claims || [];
        for (j = 0; j < cl.length; j++) c[cl[j]] = _layers[i].id;
      }
      return c;
    }

    /* -------------------------------------------------------------------------
       THE GUIDER. Plain mass, magenta, drawn only where nothing has claimed the
       part. This is what a product's prompt replaces.
       ------------------------------------------------------------------------- */
    function _mat(T, col, op) {
      return new T.MeshStandardMaterial({
        color: col, roughness: 0.9, metalness: 0.0,
        transparent: op < 1, opacity: op
      });
    }

    function _box(T, g, w, h, d, x, y, z, ry, col, op) {
      var m = new T.Mesh(new T.BoxGeometry(w, h, d), _mat(T, col, op));
      m.position.set(x, y, z);
      if (ry) m.rotation.y = ry;
      g.add(m);
      return m;
    }

    function _poly3d(T, g, pts, col, op) {
      if (pts.length === 4) return _quad(T, g, pts, col, op);
      var arr = [], i;
      for (i = 1; i + 1 < pts.length; i++) {
        arr.push(pts[0][0], pts[0][1], pts[0][2], pts[i][0], pts[i][1], pts[i][2], pts[i + 1][0], pts[i + 1][1], pts[i + 1][2]);
      }
      var geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.BufferAttribute(new Float32Array(arr), 3));
      geo.computeVertexNormals();
      var m = new T.Mesh(geo, new T.MeshStandardMaterial({ color: col, roughness: 0.9, side: T.DoubleSide, transparent: op < 1, opacity: op }));
      g.add(m);
      return m;
    }
    function _quad(T, g, pts, col, op) {
      var geo = new T.BufferGeometry();
      var v = new Float32Array([
        pts[0][0], pts[0][1], pts[0][2], pts[1][0], pts[1][1], pts[1][2], pts[2][0], pts[2][1], pts[2][2],
        pts[0][0], pts[0][1], pts[0][2], pts[2][0], pts[2][1], pts[2][2], pts[3][0], pts[3][1], pts[3][2]
      ]);
      geo.setAttribute('position', new T.BufferAttribute(v, 3));
      geo.computeVertexNormals();
      var m = new T.Mesh(geo, new T.MeshStandardMaterial({
        color: col, roughness: 0.9, side: T.DoubleSide,
        transparent: op < 1, opacity: op
      }));
      g.add(m);
      return m;
    }

    /* THE PLACEHOLDER WALL IS 300 mm, WHICH IS NOT A CONSTRUCTION -- it is a
       thickness chosen so the mass reads as a wall on screen. The moment a layer
       builds the real thing it calls setWallThickness() with what it actually
       framed, and the plan, the koty and the room areas follow. Until then
       everything downstream is honestly working off a placeholder. */
    var GUIDER_THK = 0.30;
    var _wallThk = GUIDER_THK;

    /* THE PLATE ZONE IS WHAT SITS BETWEEN THE HEAD OF THE STUDS AND THE ROOF, and
       until a layer says what it framed there it is a guess. The wall layer calls
       setPlateZone() with the real cap plate, and from then on the storey stack,
       the wall head and everything the roof bears on agree with the timber that
       is actually drawn. Left as a guess it shows up as a gap. */
    function setPlateZone(t) {
      if (!(t > 0)) return STACK.plate;
      STACK.plate = t;
      _log('plate zone -> ' + Math.round(t * 1000) + ' mm');
      return STACK.plate;
    }
    function wallThickness() { return _wallThk; }
    /* live, from the cladding that is on -- a stored number would be whatever
       an older version wrote into the preference */
    function facadeThk() { return _cladThk(cladKind()); }
    function setWallThickness(t) {
      if (!(t > 0)) return _wallThk;
      _wallThk = t;
      _log('wall thickness -> ' + Math.round(t * 1000) + ' mm');
      return _wallThk;
    }

    /* AND THE SAME HANDSHAKE FOR THE PARTITION, for the same reason. The plan was
       drawing partitions 120 mm because 120 was typed into the host contract,
       while the layer framed a 100 mm stud -- two numbers for one wall, neither
       aware of the other. The layer says what it framed and the drawing follows,
       exactly as it already does for the shell. 100 mm until one does, so the
       placeholder is a plausible stud rather than a made-up wall. */
    var _partThk = 0.100;
    var _partFramed = false;   /* set by whichever layer actually frames a partition */
    function partThickness() { return _partThk; }
    function setPartThickness(t) {
      if (!(t > 0)) return _partThk;
      _partThk = t;
      _log('partition thickness -> ' + Math.round(t * 1000) + ' mm');
      return _partThk;
    }

    function _buildGuider(T, g, claimed, P) {
      var S = storeys(), SEG = segments(), R = roofPlanes(), i, s, y, cx, cz, ang;
      var PG = poly();

      if (!claimed.floor) {
        /* the bar's slab stands under the bar, and a wing gets its own -- the
           average of a T's corners is not the middle of either */
        var BR0 = _barRect(), WR0 = _wingRect();
        for (i = 0; i < S.length; i++) {
          s = S[i];
          var sy = i === 0 ? STACK.slab / 2 : s.y0 - STACK.inter / 2, sh = i === 0 ? STACK.slab : STACK.inter;
          _box(T, g, BR0.x1 - BR0.x0, sh, BR0.z1 - BR0.z0,
               (BR0.x0 + BR0.x1) / 2, sy, (BR0.z0 + BR0.z1) / 2, 0, GUIDER_COL, 0.55);
          if (WR0) _box(T, g, WR0.x1 - WR0.x0, sh, WR0.z1 - WR0.z0,
                        (WR0.x0 + WR0.x1) / 2, sy, (WR0.z0 + WR0.z1) / 2, 0, GUIDER_COL, 0.55);
        }
        P('guider  floor    ' + S.length + ' slab(s), mass');
      }

      if (!claimed.wall) {
        for (i = 0; i < SEG.length; i++) {
          var sg = SEG[i];
          ang = Math.atan2(sg.x1 - sg.x0, sg.z1 - sg.z0);
          _box(T, g, GUIDER_THK, sg.h + STACK.plate, sg.len,
               (sg.x0 + sg.x1) / 2, sg.y0 + (sg.h + STACK.plate) / 2, (sg.z0 + sg.z1) / 2,
               ang, GUIDER_COL, 0.55);
        }
        P('guider  wall     ' + SEG.length + ' segment(s), mass at ' + Math.round(GUIDER_THK * 1000) + ' mm');
      }

      /* no roof mass over the plan of a storey above the ground floor
         (core-v425, W8, Marek: v pudoryse porad vidim magenta plochu strechy) */
      var planUp = false;
      try { planUp = (STATE.view === '2d' && (STATE.planStorey | 0) > 0 && editing('layout')); } catch (ePU) { planUp = false; }
      if (!claimed.roof && !planUp) {
        /* thin enough to see the frame and the walls under it (core-v329) */
        for (i = 0; i < R.length; i++) _poly3d(T, g, R[i].pts, GUIDER_COL, 0.28);
        var nW = 0, VL = roofEdges().valleys, vs = [];
        for (i = 0; i < R.length; i++) if (R[i].wing) nW++;
        for (i = 0; i < VL.length; i++) vs.push(Math.round(VL[i].len * 100) / 100 + ' m');
        P('guider  roof     ' + R.length + ' plane(s), mass' +
          (nW ? (' -- ' + (R.length - nW) + ' on the bar, ' + nW + ' on the wing; stage ' + roofStageLabel() +
                 (STATE.roof.sameRidge ? ', ridges level' : (_roofWingHeld ? ', wing wider -- its ridge held to the bar, pitch eased' : ', wing ridge lower')) +
                 '; valleys ' + (vs.length ? vs.join(' and ') : 'NONE') +
                 (_roofValleys.over ? ' -- the wing ridge is above the bar ridge, the far slope is not cut' : '')) : ''));
      }

      if (STATE.roof.type === 'flat' && !claimed.parapet) {
        y = wallTop();
        for (i = 0; i < SEG.length; i++) {
          if (SEG[i].storey !== S.length - 1) continue;
          var pg = SEG[i];
          ang = Math.atan2(pg.x1 - pg.x0, pg.z1 - pg.z0);
          _box(T, g, GUIDER_THK, STACK.parapet, pg.len,
               (pg.x0 + pg.x1) / 2, y + STACK.parapet / 2, (pg.z0 + pg.z1) / 2,
               ang, GUIDER_COL, 0.55);
        }
        P('guider  parapet  ring at ' + Math.round(STACK.parapet * 1000) + ' mm');
      }
    }

    /* -------------------------------------------------------------------------
       REBUILD
       ------------------------------------------------------------------------- */
    /* WHERE A LINE HAS TO GO TO REACH THE SNAPSHOT. The snapshot prints
       _buildLog; inside a build a P pushes there. Everywhere else I reached for
       _log, which goes to the console and nowhere else -- so two rounds of
       diagnostics ran and were never seen, and I read their absence as "the
       code never runs". */
    var _buildLog = [];

    function _say(line) {
      try { _buildLog.push(line); } catch (e) {}
      _log(line);
    }

    /* WHEN THINGS ARRIVED, IN MILLISECONDS. Loading is sometimes quick and
       sometimes falls over, and every diagnosis of it was an impression. These
       four moments decide it: the lib starting, the first model, the bill
       published, and the catalogue coming back from the page (Marek). */
    var _T0 = (function () {
      try { return (global.performance && global.performance.now) ? global.performance.now() : Date.now(); }
      catch (e) { return 0; }
    })();
    /* LONG TASKS AND SLOW TAPS (core-v602, Marek: mereni, co nam v
       konfiguratorech bere hodne vykonu). The browser reports every stretch
       of 50 ms or more in which the page could not answer a finger (a long
       task), and every tap or key whose answer took 40 ms or more to reach
       the screen; both are counted from the page's start for the perf lines
       and the bench. Nothing here changes what the page does. */
    var _pfT = { n: 0, sum: 0, max: 0 }, _pfE = { n: 0, slow: 0, max: 0, maxName: '' }, _pfLF = { on: false, n: 0, ms: 0, layout: 0, by: {} };
    (function () {
      try {
        var PO = global.PerformanceObserver;
        if (!PO) return;
        try {
          new PO(function (l) {
            l.getEntries().forEach(function (e) { _pfT.n++; _pfT.sum += e.duration; if (e.duration > _pfT.max) _pfT.max = e.duration; });
          }).observe({ type: 'longtask', buffered: true });
        } catch (eL) {}
        try {
          new PO(function (l) {
            l.getEntries().forEach(function (e) {
              if (!e.interactionId) return;
              _pfE.n++;
              if (e.duration >= 200) _pfE.slow++;
              if (e.duration > _pfE.max) {
                _pfE.max = e.duration;
                var tg = e.target, tn = '';
                try { tn = tg ? (tg.id ? ('#' + tg.id) : (String(tg.className || tg.nodeName || '').split(' ')[0])) : ''; } catch (eT) { tn = ''; }
                _pfE.maxName = e.name + (tn ? (' on ' + tn) : '');
              }
            });
          }).observe({ type: 'event', buffered: true, durationThreshold: 40 });
        } catch (eE) {}
        /* core-v604: WHO the long frames belong to -- the browser names the
           script (file, function, what called it) and the time it spent on
           forced style and layout; summed per script for the perf lines */
        try {
          new PO(function (l) {
            l.getEntries().forEach(function (e) {
              _pfLF.n++; _pfLF.ms += e.duration;
              try { if (e.styleAndLayoutStart) _pfLF.layout += Math.max(0, e.startTime + e.duration - e.styleAndLayoutStart); } catch (eS) {}
              (e.scripts || []).forEach(function (sc) {
                var f = String(sc.sourceURL || '').split('/').pop().split('?')[0] || '(inline)';
                var k = f + ' ' + (sc.sourceFunctionName || '?') + ' <' + String(sc.invokerType || '') + (sc.invoker ? (' ' + String(sc.invoker).slice(0, 40)) : '') + '>';
                var A = _pfLF.by[k] || (_pfLF.by[k] = { ms: 0, lay: 0, n: 0 });
                A.ms += sc.duration || 0; A.lay += sc.forcedStyleAndLayoutDuration || 0; A.n++;
              });
            });
          }).observe({ type: 'long-animation-frame', buffered: true });
          _pfLF.on = true;
        } catch (eLF) {}
      } catch (e) {}
    })();
    var _marks = { lib: 0 };

    function _mark(name) {
      if (_marks[name] != null) return;
      try {
        _marks[name] = Math.round(((global.performance && global.performance.now)
          ? global.performance.now() : Date.now()) - _T0);
      } catch (e) { _marks[name] = 0; }
    }

    function _loadLine() {
      var pd = 0;
      try { pd = (global.productData && global.productData.length) || 0; } catch (e) {}
      if (pd) _mark('catalogue');
      var order = ['lib', 'build', 'publish', 'catalogue'], parts = [], k;
      for (k = 0; k < order.length; k++) {
        parts.push(order[k] + ' ' + (_marks[order[k]] == null ? 'NEVER' : (_marks[order[k]] + ' ms')));
      }
      return 'load  ' + parts.join(' | ') + ' | catalogue rows ' + pd +
             (pd ? '' : ' -- the page has not handed the catalogue back') +
             ' | budget ' + _pubNote + '; loadVariantCSV asked ' + _pubCalls + 'x' +
             (_pubFirstAt != null ? (' (first at ' + _pubFirstAt + ' ms)') : '') +
             (_pubRetries ? (', ' + _pubRetries + ' of them because no catalogue came back') : '') +
             (_pubMiss ? (', not on the page yet ' + _pubMiss + 'x' +
                          (_pubMiss > 20 ? ' -- GAVE UP, the page never defined it' : '')) : '') +
             (_pubThrew ? (', ERROR it threw: ' + _pubThrew) : '');
    }

    var _fitGeomSig = '';
    /* THE STRUCTURE STEP SHOWS A SAMPLE, NOT THE HOUSE (core-v339).

       What a wall is built of cannot be judged on a whole house seen from
       twenty metres: a block is a quarter of a metre. So when a product hands
       over __PROD.structureSample(ctx) and the drawer is on STRUCTURE, the
       build draws the product's sample INSTEAD of the building -- the way the
       container draws its build-up, not the house hidden or emptied (Marek:
       udelat visualne podobny blok misto stavby primo ve 3D). The layers are
       still built, into groups that are thrown away, so the bill is exactly
       what it was; the guider, the person, the props and the pool are left
       out of the picture the same way. The camera is framed on the sample when
       it appears and on the house when it goes.

         __PROD.structureSample(ctx)   draws into ctx.group, standing on grade
                                       at STATE.origin; ctx.log reaches the
                                       snapshot. A product without it keeps
                                       its house on this step.
         __PROD.sampleSize()           { x, z, l, w, h } of the sample, centred
                                       on x, z, standing on grade (core-v340)
         __PROD.sampleSet(key, m)      stores 'l' | 'w' | 'h' as the product
                                       allows it and returns what it stored;
                                       with both, the sample gets its drag
                                       points and its dimension lines */
    var _sampleShown = false;
    var _afUntil = 0;   /* core-v662: until when a changed model is framed again (boot + 20 s, or the first tap) */
    /* ALWAYS FRAMED AFTER A CHANGE (core-v706, Marek: pokud zmenim floorplan
       z nabidky, musi se fit to canvas, stejne tak model vzdy pri zmenach fit
       to canvas; plati pro vsechny configuratory). After a build whose model
       is more than 3 % bigger, smaller or moved -- a plan from the library,
       the size, the roof, a storey -- the 3D camera frames it again from the
       visitor's own angle (autoFrame force), the plan fits the canvas again.
       A change of colour or a window moved leaves the camera where it is.
       Never under a finger: a build while one is down (a slider, a drag) is
       framed 250 ms after the last finger lifts. The voice tour and the
       sample view frame by themselves (autoFitOk, _T.on). */
    var _fitPtrs = {}, _fitPend = '';
    try {
      global.document.addEventListener('pointerdown', function (e) { if (e.isPrimary) _fitPtrs = {}; _fitPtrs[e.pointerId] = 1; }, { capture: true, passive: true });
      var _fitUp = function (e) {
        delete _fitPtrs[e.pointerId];
        if (!_fitPend || Object.keys(_fitPtrs).length) return;
        var w = _fitPend; _fitPend = '';
        global.setTimeout(function () { try { _fitAfterBuild(w + ', after the finger lifted'); } catch (eF) {} }, 250);
      };
      global.document.addEventListener('pointerup', _fitUp, { capture: true, passive: true });
      global.document.addEventListener('pointercancel', _fitUp, { capture: true, passive: true });
    } catch (eFp) {}
    function _fitAfterBuild(why) {
      // Product opt-in: preserve perspective and plan cameras across model edits.
      if (__PROD && __PROD._preserveCameraOnBuild) return;
      if (Object.keys(_fitPtrs).length) { _fitPend = why; return; }
      var st = _stage();
      if (!st) return;
      if (STATE.view === '2d') { _planFitIfChanged(why); return; }
      if (st.autoFrame) st.autoFrame(why, true);
    }
    /* the plan's own: the building's rectangle it was last framed on */
    var _planFitB = null;
    function _planFitSig() {
      var f = _planFocus();
      return f ? { cx: f.cx, cz: f.cz, sx: f.spanX, sz: f.spanZ } : null;
    }
    function _planFitIfChanged(why) {
      if (STATE.view !== '2d') return false;
      try { if (typeof _T !== 'undefined' && _T && _T.on) return false; } catch (eT) {}
      var f = _planFitSig(), L = _planFitB;
      if (!f) return false;
      if (!L) { _planFitB = f; return false; }
      var s = Math.max(L.sx, L.sz, 1);
      if (Math.abs(f.sx - L.sx) / s < 0.03 && Math.abs(f.sz - L.sz) / s < 0.03 && Math.hypot(f.cx - L.cx, f.cz - L.cz) / s < 0.03) return false;
      try { _planKeep = null; } catch (eK) {}
      setView('2d');
      _log('frame    the plan framed again -- ' + why + ': ' + L.sx.toFixed(1) + ' x ' + L.sz.toFixed(1) + ' -> ' + f.sx.toFixed(1) + ' x ' + f.sz.toFixed(1) + ' m (core-v706)');
      return true;
    }
    /* WHAT A BUILD COSTS (core-v589): every rebuild's whole time, the last 30
       kept, and each layer's own time in the last one -- the snapshot's perf
       lines read them. Nothing is changed by measuring. */
    var _pfB = { n: 0, hist: [], layers: [], firstEnd: null };
    /* THE FIRST MODEL IS ON SCREEN, SAID ONCE (core-v799, perf thread
       2026-11-02, status/perf.md): window.__levaModelReady and the event
       'leva:model-ready', two animation frames after the first build -- the
       frame that draws it has been drawn by then. The budget strip's map
       (bs-v61) waits for it on the house line, and so does the configurator's
       own map (initMap: the Mapbox SDK, ~300 kB and ~0.3 s of a phone's main
       thread, and the map's set-up used to land between the first build and
       its first frame). The model has a phone's first seconds to itself. If
       no first model comes, whatever waits goes after 15 s. */
    var _mrQ = [], _mrDone = false, _mrTimer = null;
    function _mrFlush(why) {
      if (_mrDone) return;
      _mrDone = true;
      if (_mrTimer) { clearTimeout(_mrTimer); _mrTimer = null; }
      var q = _mrQ; _mrQ = [];
      for (var i = 0; i < q.length; i++) { try { q[i](); } catch (eQ) { _log('model-ready: a held step threw ' + ((eQ && eQ.message) || eQ)); } }
      try { global.__levaModelReady = true; global.dispatchEvent(new global.CustomEvent('leva:model-ready', { detail: { why: why } })); } catch (eMr) {}
    }
    function _whenModelReady(fn) {
      if (_mrDone) { fn(); return; }
      _mrQ.push(fn);
      if (!_mrTimer) _mrTimer = setTimeout(function () { _mrFlush('no first model in 15 s'); }, 15000);
    }
    function _modelReady() {
      var raf = global.requestAnimationFrame ? function (f) { global.requestAnimationFrame(f); } : function (f) { setTimeout(f, 16); };
      raf(function () { raf(function () { _mrFlush('the first model on screen'); }); });
    }
    function _pfNow() { try { return (global.performance && global.performance.now) ? global.performance.now() : Date.now(); } catch (e) { return Date.now(); } }
    /* A FOLLOWER'S FRAME DOES NOT WAKE THE MODEL (core-v603): the trackers
       that keep the drag handles, the pool link and the sample's points on
       the model run every frame for ever; through the page's own animation
       frame they told the stage something was animating, so it drew sixty
       frames a second for nothing. They ask the browser directly. */
    function _rafQuiet(fn) { var R0 = global.__levaRaf0 || global.requestAnimationFrame; return R0.call(global, fn); }
    /* A DRAG BUILDS LIGHT, THE RELEASE BUILDS WHOLE (core-v617, Marek: prace
       s konfiguratory co nejplynulejsi; the work bench #131-#134: every move
       of a finger rebuilt the whole house -- ADU 0.3 s, RANCH 2 s a move).
       While a finger drags:
         on the plan  -- nothing is built, the plan is repainted from the
                         state (partition, door, doorway end, window, the
                         outline's and the products' grips);
         in 3D        -- only the layers the drag can change are built again
                         (the pitch and the roof's grips: roof, walls,
                         ceiling...; a window: walls and joinery), the rest
                         keep their bodies; no bill, no audits, no count-only
                         builds of hidden layers;
         a slider or grip with no set -- the whole house, but light.
       When the finger lifts (the handler's own release, or any pointerup with
       no finger left down, or 2.5 s with nothing moving) the whole house is
       built once, with the bill. ?drag=full keeps the old behaviour. */
    var _drag = { on: false, kind: '', set: null, t0: 0, last: 0, n: 0, wd: 0, raf: 0, frame: false };
    var DRAG_SETS = {
      roof: ['roof', 'wall', 'stud-wall', 'ceiling', 'lps', 'gutter', 'gable', 'porch', 'facade', 'cladding', 'eave', 'fascia', 'soffit'],
      opening: ['wall', 'stud-wall', 'joinery', 'facade', 'cladding', 'lintel', 'header', 'trim']
    };
    var _dragFullQ = null;
    function _dragOff() {
      if (_dragFullQ === null) { try { _dragFullQ = /[?&]drag=full(&|$)/.test(String((global.location && global.location.search) || '')); } catch (e) { _dragFullQ = false; } }
      return _dragFullQ;
    }
    function _dragHas(id) {
      if (!_drag.set) return true;
      var suf = String(id || '').replace(/^[^-]+-/, ''), i;
      for (i = 0; i < _drag.set.length; i++) {
        var k = _drag.set[i];
        if (suf === k || suf.indexOf(k + '-') === 0) return true;
      }
      return false;
    }
    var _ptrs = {};
    function _ptrDown() { for (var k in _ptrs) if (_ptrs.hasOwnProperty(k)) return true; return false; }
    function _dragBegin(kind, setKey) {
      if (_dragOff()) return;
      var now = Date.now();
      if (!_drag.on) {
        _drag.on = true; _drag.kind = kind; _drag.set = setKey ? ((typeof setKey === 'object' && setKey.length) ? setKey : (DRAG_SETS[setKey] || null)) : null; _drag.t0 = now; _drag.n = 0;
        /* Product layers need not have the shared roof/wall suffixes. Resolve
           their dependencies together, and coalesce their moves per frame. */
        _drag.frame = false;
        if (setKey && typeof __PROD._dragLayers === 'function') {
          try {
            var own = __PROD._dragLayers(kind, setKey);
            if (own && own.length) { _drag.set = own; _drag.frame = true; }
          } catch (eDL) { _log('drag layers: ' + (eDL && eDL.message)); }
        }
        _log('drag     ' + kind + ' -- ' + (_drag.set ? ('only ' + _drag.set.slice(0, 5).join(', ') + '... built while it moves') : (_planWanted() ? 'the plan repainted, nothing built while it moves' : 'light builds while it moves (no bill, no audits)')) + '; the whole house when the finger lifts (core-v617)');
      }
      _drag.last = now; _drag.n++;
      if (!_drag.wd) _drag.wd = global.setTimeout(_dragWatch, 600);
    }
    function _dragWatch() {
      _drag.wd = 0;
      if (!_drag.on) return;
      if (_ptrDown() || Date.now() - _drag.last < 2500) { _drag.wd = global.setTimeout(_dragWatch, 600); return; }
      if (_dragStop('nothing moved for 2.5 s')) { try { rebuild(); savePref(); } catch (eW) {} }
    }
    function _dragStop(why) {
      if (!_drag.on) return false;
      _drag.on = false;
      if (_drag.raf) { global.cancelAnimationFrame(_drag.raf); _drag.raf = 0; }
      if (_drag.wd) { global.clearTimeout(_drag.wd); _drag.wd = 0; }
      _log('drag     ' + _drag.kind + ' let go (' + (why || '') + ') after ' + _drag.n + ' move(s) in ' + ((Date.now() - _drag.t0) / 1000).toFixed(1) + ' s -- the whole house built once');
      return true;
    }
    /* a move on the plan: the plan only; in 3D a light build of the set */
    function _dragMove(kind, setKey) {
      if (_dragOff()) { rebuild(); if (_planWanted()) drawPlan(); return; }
      if (_planWanted()) { _dragBegin(kind); drawPlan(); return; }
      _dragBegin(kind, setKey);
      if (_drag.frame) {
        if (!_drag.raf) _drag.raf = global.requestAnimationFrame(function () {
          _drag.raf = 0;
          if (_drag.on) rebuild();
        });
        return;
      }
      rebuild();
    }
    (function () {
      try {
        global.addEventListener('pointerdown', function (e) { _ptrs[e.pointerId] = 1; }, true);
        var up = function (e) {
          delete _ptrs[e.pointerId];
          if (!_drag.on) return;
          /* after the handlers of this event, which build on their own release */
          global.setTimeout(function () {
            if (_drag.on && !_ptrDown() && _dragStop('the finger lifted')) { try { rebuild(); if (_planWanted()) drawPlan(); savePref(); } catch (eR) {} }
          }, 0);
        };
        global.addEventListener('pointerup', up, true);
        global.addEventListener('pointercancel', up, true);
      } catch (eP) {}
    })();
    /* WHERE A LAYER'S TIME GOES (core-v621, D1 #137: RANCH floor 260-340 ms,
       partitions 220, roof 230 a build). While a layer builds, the three.js
       geometry constructors are timed and counted for it; 'perf   geometry'
       says, for the last whole build, how much of each layer was making
       geometry and of which kind. The constructors are wrapped once (the
       page's own copy of THREE), the objects they make are the same. */
    var _pfG = { on: '', by: {}, wrapped: false };
    var PF_GEO = ['BoxGeometry', 'ExtrudeGeometry', 'CylinderGeometry', 'ShapeGeometry', 'PlaneGeometry', 'SphereGeometry', 'TubeGeometry', 'LatheGeometry', 'EdgesGeometry'];
    /* A BOX FROM A TEMPLATE (core-v627, D1 #145: the RANCH makes ~7 600
       boxes a build, 55-66 us each on the tablet, ~0.45 s). three.js builds
       every box face by face into plain arrays and copies them; a box of one
       segment each way with positive sizes is always the unit box scaled, so
       it is copied from a unit box made once: the same positions (0.5 x w is
       w / 2 exactly), normals, uvs, index, groups and parameters -- checked
       equal on 20 000 random boxes -- three times faster. Any other box
       (segments, a size not above zero) is made by three.js. ?box=three
       makes every box the old way. */
    var _bxT = null, _bxQ = null;
    function _bxOff() {
      if (_bxQ === null) { try { _bxQ = /[?&]box=three(&|$)/.test(String((global.location && global.location.search) || '')); } catch (e) { _bxQ = false; } }
      return _bxQ;
    }
    function _bxSeg(v) { return v === undefined || Math.floor(v) === 1; }
    function _bxFast(T, Base, a, nt) {
      var w = (a[0] === undefined) ? 1 : a[0], h = (a[1] === undefined) ? 1 : a[1], d = (a[2] === undefined) ? 1 : a[2];
      if (typeof w !== 'number' || typeof h !== 'number' || typeof d !== 'number' || !(w > 0) || !(h > 0) || !(d > 0) ||
          !isFinite(w) || !isFinite(h) || !isFinite(d) || !_bxSeg(a[3]) || !_bxSeg(a[4]) || !_bxSeg(a[5])) return null;
      if (!_bxT) {
        var u = Reflect.construct(Base, [1, 1, 1], Base);
        _bxT = { p: u.attributes.position.array, n: u.attributes.normal.array, uv: u.attributes.uv.array, i: u.index.array,
                 g: u.groups.map(function (q) { return [q.start, q.count, q.materialIndex]; }) };
      }
      var g = Reflect.construct(T.BufferGeometry, [], nt), P = new Float32Array(_bxT.p.length), TP = _bxT.p, i;
      g.type = 'BoxGeometry';
      g.parameters = { width: w, height: h, depth: d, widthSegments: (a[3] === undefined) ? 1 : a[3], heightSegments: (a[4] === undefined) ? 1 : a[4], depthSegments: (a[5] === undefined) ? 1 : a[5] };
      for (i = 0; i < TP.length; i += 3) { P[i] = TP[i] * w; P[i + 1] = TP[i + 1] * h; P[i + 2] = TP[i + 2] * d; }
      for (i = 0; i < _bxT.g.length; i++) g.addGroup(_bxT.g[i][0], _bxT.g[i][1], _bxT.g[i][2]);
      g.setIndex(new T.Uint16BufferAttribute(_bxT.i, 1));
      g.setAttribute('position', new T.BufferAttribute(P, 3));
      g.setAttribute('normal', new T.Float32BufferAttribute(_bxT.n, 3));
      g.setAttribute('uv', new T.Float32BufferAttribute(_bxT.uv, 2));
      return g;
    }
    function _pfGeoWrap(T) {
      if (_pfG.wrapped || !T || typeof Reflect === 'undefined' || !Reflect.construct) return;
      _pfG.wrapped = true;
      PF_GEO.forEach(function (nm) {
        var Base = T[nm];
        if (typeof Base !== 'function' || Base.__levaTimed) return;
        try {
          var W = function () {
            var on = _pfG.on, t0 = on ? _pfNow() : 0, o = null;
            if (nm === 'BoxGeometry' && !_bxOff()) { try { o = _bxFast(T, Base, arguments, new.target || W); } catch (eBx) { o = null; } }
            if (!o) o = Reflect.construct(Base, arguments, new.target || W);
            if (on) {
              var L = _pfG.by[on] || (_pfG.by[on] = { ms: 0, n: 0, k: {} });
              var d = _pfNow() - t0, K = L.k[nm] || (L.k[nm] = [0, 0]);
              L.ms += d; L.n++; K[0]++; K[1] += d;
            }
            return o;
          };
          W.prototype = Base.prototype;
          Object.setPrototypeOf(W, Base);
          W.__levaTimed = true;
          T[nm] = W;
        } catch (e) {}
      });
    }
    function _pfGeoLine() {
      var by = _pfG.by, rows = [], k;
      for (k in by) if (by.hasOwnProperty(k)) rows.push([k, by[k]]);
      if (!rows.length) return 'perf   geometry -- nothing timed yet (a whole build times it)';
      rows.sort(function (a, b) { return b[1].ms - a[1].ms; });
      return 'perf   geometry made while the layers built (the last whole build): ' + rows.slice(0, 6).map(function (x) {
        var ks = [], q;
        for (q in x[1].k) if (x[1].k.hasOwnProperty(q)) ks.push([q, x[1].k[q]]);
        ks.sort(function (a, b) { return b[1][1] - a[1][1]; });
        return String(x[0]).replace(/^[a-z]+-/, '') + ' ' + Math.round(x[1].ms) + ' ms / ' + x[1].n + ' (' + ks.slice(0, 3).map(function (y) { return y[0].replace('Geometry', '') + ' ' + y[1][0] + ' x ' + Math.round(y[1][1]) + ' ms'; }).join(', ') + ')';
      }).join(' | ');
    }
    /* THE OLD HOUSE GOES AFTER THE NEW ONE IS ON SCREEN (core-v620). A build
       threw the old bodies' materials away before the new ones had been drawn
       once -- and a shader nobody uses any more is deleted, so the first frame
       of every build compiled the house's shaders again (RANCH: 9 of its 15,
       headless 0.8 - 2.7 s; the tablet's 1.7 s first frame). The old
       geometries and materials now wait in a queue and are let go just after
       the first frame of the new house, minus anything the new house still
       uses (a shared material stays alive, a shared geometry stays uploaded).
       ?dispose=now lets them go at once, as before. */
    var _old = { g: [], m: [], t: 0, f: 0, n: 0, freed: 0, kept: 0, ms: 0, q: null };
    function _oldNow() {
      if (_old.q === null) { try { _old.q = /[?&]dispose=now(&|$)/.test(String((global.location && global.location.search) || '')); } catch (e) { _old.q = false; } }
      return _old.q;
    }
    function _oldPut(o) {
      var m = o.material, k;
      if (_oldNow()) {
        if (o.geometry) o.geometry.dispose();
        if (m && m.dispose) m.dispose();
        return;
      }
      if (o.geometry && o.geometry.dispose) _old.g.push(o.geometry);
      if (m) { if (Array.isArray(m)) { for (k = 0; k < m.length; k++) if (m[k] && m[k].dispose) _old.m.push(m[k]); } else if (m.dispose) _old.m.push(m); }
      if (!_old.t) _old.t = global.setTimeout(function () { _old.t = 0; _oldFlush(); }, 4000);   /* no frame came: let go anyway */
    }
    function _oldFlush() {
      if (_old.t) { global.clearTimeout(_old.t); _old.t = 0; }
      if (!_old.g.length && !_old.m.length) return;
      var t0 = _pfNow(), st = _stage(), use = new Set(), seen = new Set(), keepG = [], keepM = [], i, nF = 0, x;
      if (st && st.scene) st.scene.traverse(function (o) {
        if (o.geometry) use.add(o.geometry);
        var m = o.material;
        if (m) { if (Array.isArray(m)) { for (var k = 0; k < m.length; k++) use.add(m[k]); } else use.add(m); }
      });
      for (i = 0; i < _old.g.length; i++) {
        x = _old.g[i]; if (seen.has(x)) continue; seen.add(x);
        if (use.has(x)) { keepG.push(x); continue; }
        try { x.dispose(); } catch (e1) {}
        nF++;
      }
      for (i = 0; i < _old.m.length; i++) {
        x = _old.m[i]; if (seen.has(x)) continue; seen.add(x);
        if (use.has(x)) { keepM.push(x); continue; }
        try { x.dispose(); } catch (e2) {}
        nF++;
      }
      /* still in use: asked again after the next build */
      _old.g = keepG; _old.m = keepM;
      _old.n++; _old.freed += nF; _old.kept = keepG.length + keepM.length; _old.ms = _pfNow() - t0;
    }
    /* ONE DRAW PER LAYER AND MATERIAL (core-v620, Marek: maximalni vykon i
       na slabych zarizenich). Every body was a draw call of its own: the
       RANCH drew 5 000 - 14 000 of them a frame (80 ms on the tablet) and the
       first frame after a build sent every one of them to the GPU (1.7 s).

       Now, at the first frame after a build, the bodies of one layer that
       share a look (the same kind of material with the same colour, maps and
       settings) are copied into ONE body per layer and look -- a proxy, kept
       in a group of its own beside the house, never inside it. While a frame
       is drawn the proxies stand in for their bodies; between frames the
       bodies are the house exactly as before: picking, the bill, the
       drawings, the section, the AR / VR export and every tool see the very
       same objects, and the proxies are hidden, skipped by the AR export and
       cannot be hit.

       NOTHING STALE IS EVER DRAWN. Before every frame each layer is compared
       with what it was when it was merged -- every body and group: shown or
       hidden, where it stands, its children, its material and geometry -- and
       the bodies sharing a proxy must still look alike. A layer that changed
       (a hidden body, a highlight, the assembly walk, the explode) is drawn
       body by body, as it always was, and merged again once it has stood still
       for a second. A drag merges nothing: the layers it rebuilds are drawn
       body by body until the whole house is built on the release.
       Left apart: see-through bodies that write depth (they are drawn far to
       near), bodies with several materials (a window: frame + glass), lines
       that are not segments, instanced bodies, a body alone in its look.
       ?merge=0 draws every body on its own, as before. */
    var _mg = { q: null, R: null, root: null, units: [], frames: 0, framesM: 0, merges: 0, re: 0, ms: 0, lastMs: 0, lastAt: 0, hid: [], skp: [], dead: '', apart: {}, alone: {}, gs: null };
    function _mgOff() {
      if (_mg.q === null) { try { _mg.q = /[?&]merge=0(&|$)/.test(String((global.location && global.location.search) || '')); } catch (e) { _mg.q = false; } }
      return _mg.q || !!_mg.dead;
    }
    function _mgNoop() {}
    var _mgIds = (typeof WeakMap === 'function') ? new WeakMap() : null, _mgIdN = 0;
    function _mgId(o) {
      if (!_mgIds) return '#' + (++_mgIdN);
      var v = _mgIds.get(o);
      if (!v) { v = ++_mgIdN; _mgIds.set(o, v); }
      return '#' + v;
    }
    var MG_PROPS = ['color', 'emissive', 'emissiveIntensity', 'roughness', 'metalness', 'opacity', 'transparent', 'depthWrite', 'depthTest',
                    'side', 'shadowSide', 'flatShading', 'vertexColors', 'wireframe', 'polygonOffset', 'polygonOffsetFactor', 'polygonOffsetUnits',
                    'alphaTest', 'alphaToCoverage', 'colorWrite', 'blending', 'blendSrc', 'blendDst', 'blendEquation', 'premultipliedAlpha',
                    'toneMapped', 'fog', 'dithering', 'envMapIntensity', 'reflectivity', 'refractionRatio', 'combine', 'shininess', 'specular',
                    'map', 'normalMap', 'normalMapType', 'normalScale', 'roughnessMap', 'metalnessMap', 'aoMap', 'aoMapIntensity', 'emissiveMap',
                    'alphaMap', 'bumpMap', 'bumpScale', 'envMap', 'lightMap', 'lightMapIntensity', 'displacementMap', 'specularMap',
                    'stencilWrite', 'clippingPlanes', 'clipIntersection', 'clipShadows', 'forceSinglePass', 'linewidth'];
    var MG_MAPS = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap', 'alphaMap', 'bumpMap', 'lightMap', 'displacementMap', 'specularMap'];
    function _mgVal(v) {
      if (v === undefined) return '';
      if (v === null) return '-';
      var t = typeof v;
      if (t === 'number' || t === 'boolean' || t === 'string') return String(v);
      if (v.isColor) return v.r + ',' + v.g + ',' + v.b;
      if (v.isVector2) return v.x + ',' + v.y;
      return _mgId(v);   /* a texture, a list of planes: the very same one */
    }
    /* A TEXTURE BY ITS PICTURE (core-v621, D1 #137: RANCH 682 bodies alone in
       their look -- the finishes and the OSB clone their texture per piece
       for its own repeat, so floor 609 materials, ceiling 582). Two clones of
       one picture with the same sampling are one texture to the GPU; where
       each piece's copy is placed (offset, repeat, rotation) goes into the
       proxy's uvs instead, so they merge. */
    function _mgTexSig(t) {
      return _mgId(t.source || t) + ':' + t.wrapS + ',' + t.wrapT + ',' + t.magFilter + ',' + t.minFilter + ',' + t.anisotropy + ',' + t.colorSpace + ',' +
             t.flipY + ',' + t.generateMipmaps + ',' + t.premultiplyAlpha + ',' + t.unpackAlignment + ',' + t.format + ',' + t.type + ',' + (t.internalFormat || '');
    }
    function _mgTex0(m) {
      for (var i = 0; i < MG_MAPS.length; i++) if (m[MG_MAPS[i]] && m[MG_MAPS[i]].isTexture) return m[MG_MAPS[i]];
      return null;
    }
    function _mgTexPlace(t) {
      if (!t.matrixAutoUpdate) return 'm' + t.matrix.elements.join(',');
      return t.offset.x + ',' + t.offset.y + ',' + t.repeat.x + ',' + t.repeat.y + ',' + t.rotation + ',' + t.center.x + ',' + t.center.y;
    }
    /* the placement as numbers, for the check before every frame */
    var MG_TP = 10;
    function _mgTexPut(t, A, o) {
      var e = t.matrixAutoUpdate ? null : t.matrix.elements;
      A[o] = t.matrixAutoUpdate ? 1 : 0;
      A[o + 1] = e ? e[0] : t.offset.x; A[o + 2] = e ? e[1] : t.offset.y; A[o + 3] = e ? e[3] : t.repeat.x; A[o + 4] = e ? e[4] : t.repeat.y;
      A[o + 5] = e ? e[6] : t.rotation; A[o + 6] = e ? e[7] : t.center.x; A[o + 7] = e ? 0 : t.center.y; A[o + 8] = 1; A[o + 9] = 0;
    }
    function _mgTexEq(t, A, o) {
      if (!t) return A[o + 8] === 0;
      if ((t.matrixAutoUpdate ? 1 : 0) !== A[o] || A[o + 8] !== 1) return false;
      if (!t.matrixAutoUpdate) { var e = t.matrix.elements; return e[0] === A[o + 1] && e[1] === A[o + 2] && e[3] === A[o + 3] && e[4] === A[o + 4] && e[6] === A[o + 5] && e[7] === A[o + 6]; }
      return t.offset.x === A[o + 1] && t.offset.y === A[o + 2] && t.repeat.x === A[o + 3] && t.repeat.y === A[o + 4] &&
             t.rotation === A[o + 5] && t.center.x === A[o + 6] && t.center.y === A[o + 7];
    }
    /* the uv matrix three.js gives the texture (a copy) */
    function _mgTexMat(T, t) {
      if (t.matrixAutoUpdate) t.updateMatrix();
      return new T.Matrix3().copy(t.matrix);
    }
    /* the look a material gives, as a key: two materials with one key draw
       alike; null -- never merged */
    function _mgMatKey(m, T) {
      if (!m || Array.isArray(m) || m.visible === false) return null;
      if (m.transparent && m.depthWrite) return null;
      /* only the plain kinds: anything else may read more off the geometry
         than a proxy carries (a shader's own attributes, a dashed line's
         distances, the physical material's extra maps) */
      var std = (m.isMeshStandardMaterial && !m.isMeshPhysicalMaterial) || m.isMeshBasicMaterial || m.isMeshLambertMaterial || m.isMeshPhongMaterial ||
                (m.isLineBasicMaterial && !m.isLineDashedMaterial);
      if (!std || m.isShaderMaterial) return null;
      if (m.onBeforeCompile !== T.Material.prototype.onBeforeCompile ||
          m.customProgramCacheKey !== T.Material.prototype.customProgramCacheKey) return 'id' + _mgId(m);
      /* the maps' placement goes into the uvs only when every map of the
         material sits the same way; otherwise the very same textures */
      var s = m.type, i, pl = null, exact = false, t;
      for (i = 0; i < MG_MAPS.length; i++) {
        t = m[MG_MAPS[i]];
        if (!t || !t.isTexture) continue;
        var pi = _mgTexPlace(t);
        if (pl === null) pl = pi; else if (pi !== pl) { exact = true; break; }
      }
      for (i = 0; i < MG_PROPS.length; i++) {
        var v = m[MG_PROPS[i]];
        s += '|' + ((v && v.isTexture && !exact && MG_MAPS.indexOf(MG_PROPS[i]) >= 0) ? _mgTexSig(v) : _mgVal(v));
      }
      if (exact) s += '|exact';
      return s;
    }
    /* a material's key once per merge pass (5 000 bodies, 150 materials) */
    var _mgKC = null;
    function _mgKeyOf(m, T) {
      if (!_mgKC || !m || Array.isArray(m)) return _mgMatKey(m, T);
      if (_mgKC.has(m)) return _mgKC.get(m);
      var k = _mgMatKey(m, T);
      _mgKC.set(m, k);
      return k;
    }
    /* what a material reads off the geometry */
    function _mgNeed(m, line) {
      var uv = false, i, t;
      for (i = 0; i < MG_MAPS.length; i++) { t = m[MG_MAPS[i]]; if (t) { if (t.channel) return null; uv = true; } }
      return { uv: uv, nrm: !line && !(m.isMeshBasicMaterial && !m.envMap), col: !!m.vertexColors };
    }
    function _mgAttrOk(a, size) {
      return !!(a && !a.isInterleavedBufferAttribute && !a.normalized && a.array instanceof Float32Array && (size ? a.itemSize === size : true));
    }
    /* why a body cannot join a merge, or '' */
    function _mgWhyNot(o, T) {
      if (!(o.isMesh || o.isLineSegments)) return (o.isLine || o.isPoints || o.isSprite) ? 'line / points' : 'x';
      if (o.isInstancedMesh || o.isSkinnedMesh || o.isBatchedMesh) return 'instanced';
      if (o.userData && o.userData.noMerge) return 'kept apart by its builder';
      if (Array.isArray(o.material)) return 'several materials';
      if (o.onBeforeRender !== T.Object3D.prototype.onBeforeRender || o.onAfterRender !== T.Object3D.prototype.onAfterRender) return 'a draw callback';
      if (o.layers.mask !== 1) return 'other layers';
      var g = o.geometry;
      if (!g || !g.isBufferGeometry || !g.attributes) return 'no geometry';
      if (g.morphAttributes && Object.keys(g.morphAttributes).length) return 'morphs';
      if (g.drawRange.start !== 0 || g.drawRange.count !== Infinity) return 'a draw range';
      if (!_mgAttrOk(g.attributes.position, 3) || g.attributes.position.count < 2) return 'its positions';
      if (g.index && (g.index.isInterleavedBufferAttribute || !g.index.array)) return 'its index';
      return '';
    }
    var MG_W = 13;   /* floats a node: shown, children, position, quaternion, scale, layers */
    function _mgSnap(u, A, R) {
      var N = u.nodes, k, b, o, p, q, s;
      for (k = 0, b = 0; k < N.length; k++, b += MG_W) {
        o = N[k]; p = o.position; q = o.quaternion; s = o.scale;
        A[b] = o.visible ? 1 : 0; A[b + 1] = o.children.length;
        A[b + 2] = p.x; A[b + 3] = p.y; A[b + 4] = p.z;
        A[b + 5] = q._x; A[b + 6] = q._y; A[b + 7] = q._z; A[b + 8] = q._w;
        A[b + 9] = s.x; A[b + 10] = s.y; A[b + 11] = s.z; A[b + 12] = o.layers.mask;
        R[k * 3] = o.parent; R[k * 3 + 1] = o.material || null; R[k * 3 + 2] = o.geometry || null;
      }
    }
    /* has anything in the layer changed since the last frame? if so the last
       seen state is taken over */
    function _mgDiff(u) {
      var N = u.nodes, A = u.S, R = u.RS, k, b, o, p, q, s;
      for (k = 0, b = 0; k < N.length; k++, b += MG_W) {
        o = N[k];
        if ((o.visible ? 1 : 0) !== A[b] || o.children.length !== A[b + 1] || R[k * 3] !== o.parent || R[k * 3 + 1] !== (o.material || null) || R[k * 3 + 2] !== (o.geometry || null)) break;
        p = o.position; if (p.x !== A[b + 2] || p.y !== A[b + 3] || p.z !== A[b + 4]) break;
        q = o.quaternion; if (q._x !== A[b + 5] || q._y !== A[b + 6] || q._z !== A[b + 7] || q._w !== A[b + 8]) break;
        s = o.scale; if (s.x !== A[b + 9] || s.y !== A[b + 10] || s.z !== A[b + 11] || o.layers.mask !== A[b + 12]) break;
      }
      if (k === N.length) return false;
      _mgSnap(u, u.S, u.RS);
      return true;
    }
    /* is the last seen state the state it was merged in? */
    function _mgAsMerged(u) {
      var A = u.S, B = u.M, R = u.RS, Q = u.RM, k;
      for (k = 0; k < A.length; k++) if (A[k] !== B[k]) return false;
      for (k = 0; k < R.length; k++) if (R[k] !== Q[k]) return false;
      return true;
    }
    function _mgCol(a, b) { return (!a && !b) || (a && b && a.r === b.r && a.g === b.g && a.b === b.b); }
    /* the bodies of one proxy still look alike (a material changed in place
       on one of them -- a dim, a ghost -- and they do not) */
    function _mgTexSame(a, b) {
      return a === b || (!!a && !!b && a.source === b.source && a.wrapS === b.wrapS && a.wrapT === b.wrapT && a.magFilter === b.magFilter &&
                         a.minFilter === b.minFilter && a.colorSpace === b.colorSpace && a.flipY === b.flipY);
    }
    function _mgSetOk(S) {
      var a = S.mats[0], i, b, t;
      if (a.visible === false || (a.transparent && a.depthWrite)) return false;
      for (i = 0; i < S.mats.length; i++) {
        b = S.mats[i];
        /* each body's map still placed as when its uvs were baked */
        if (S.tp && !_mgTexEq(_mgTex0(b), S.tp, i * MG_TP)) return false;
        if (!i) continue;
        if (b.opacity !== a.opacity || b.transparent !== a.transparent || b.depthWrite !== a.depthWrite || b.depthTest !== a.depthTest ||
            b.visible !== a.visible || b.side !== a.side || b.colorWrite !== a.colorWrite || b.clippingPlanes !== a.clippingPlanes ||
            !_mgTexSame(a.map, b.map) || b.wireframe !== a.wireframe || b.polygonOffset !== a.polygonOffset || b.emissiveIntensity !== a.emissiveIntensity ||
            !_mgCol(a.color, b.color) || !_mgCol(a.emissive, b.emissive)) return false;
      }
      return true;
    }
    /* one proxy from the bodies of one look, in the scene's frame, standing
       at the bodies' middle (so the see-through ones sort sensibly) */
    function _mgBuild(T, B) {
      var L = B.list, n = L.length, V = 0, I = 0, i, j, k, line = !!B.line, need = B.need;
      var cx = 0, cy = 0, cz = 0;
      for (i = 0; i < n; i++) {
        var g0 = L[i].geometry, c0 = g0.attributes.position.count;
        V += c0; I += g0.index ? g0.index.count : c0;
        var e0 = L[i].matrixWorld.elements; cx += e0[12]; cy += e0[13]; cz += e0[14];
      }
      cx /= n; cy /= n; cz /= n;
      var P = new Float32Array(V * 3), N = need.nrm ? new Float32Array(V * 3) : null, U = need.uv ? new Float32Array(V * 2) : null;
      var CS = B.cs || 3, CL = need.col ? new Float32Array(V * CS) : null;
      var X = (V > 65535) ? new Uint32Array(I) : new Uint16Array(I);
      var nm = new T.Matrix3(), v0 = 0, i0 = 0;
      /* the proxy is drawn with the first body's material, so its map's
         placement T0 applies to every vertex: a body placed Ti gets its uvs
         as inv(T0) * Ti * uv (bodies placed like the first one: as they are) */
      var tx0 = U ? _mgTex0(B.mats[0]) : null, T0 = null, I0 = null, P0 = tx0 ? _mgTexPlace(tx0) : '';
      if (tx0) {
        T0 = _mgTexMat(T, tx0);
        if (Math.abs(T0.determinant()) < 1e-12) return null;
        I0 = T0.clone().invert();
      }
      for (i = 0; i < n; i++) {
        var o = L[i], g = o.geometry, e = o.matrixWorld.elements, pa = g.attributes.position.array, c = g.attributes.position.count, x, y, z, w;
        for (j = 0, k = v0 * 3; j < c; j++, k += 3) {
          x = pa[j * 3]; y = pa[j * 3 + 1]; z = pa[j * 3 + 2];
          P[k] = e[0] * x + e[4] * y + e[8] * z + e[12] - cx;
          P[k + 1] = e[1] * x + e[5] * y + e[9] * z + e[13] - cy;
          P[k + 2] = e[2] * x + e[6] * y + e[10] * z + e[14] - cz;
        }
        if (N) {
          nm.getNormalMatrix(o.matrixWorld);
          var ne = nm.elements, na = g.attributes.normal.array;
          for (j = 0, k = v0 * 3; j < c; j++, k += 3) {
            x = na[j * 3]; y = na[j * 3 + 1]; z = na[j * 3 + 2];
            var nx = ne[0] * x + ne[3] * y + ne[6] * z, ny = ne[1] * x + ne[4] * y + ne[7] * z, nz = ne[2] * x + ne[5] * y + ne[8] * z;
            w = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;
            N[k] = nx / w; N[k + 1] = ny / w; N[k + 2] = nz / w;
          }
        }
        if (U) {
          var txi = tx0 ? _mgTex0(o.material) : null, bk = null;
          if (txi && _mgTexPlace(txi) !== P0) bk = I0.clone().multiply(_mgTexMat(T, txi)).elements;
          var ua = g.attributes.uv ? g.attributes.uv.array : null;
          if (!bk) { if (ua) U.set(ua.subarray(0, c * 2), v0 * 2); }
          else {
            for (j = 0, k = v0 * 2; j < c; j++, k += 2) {
              x = ua ? ua[j * 2] : 0; y = ua ? ua[j * 2 + 1] : 0;
              U[k] = bk[0] * x + bk[3] * y + bk[6];
              U[k + 1] = bk[1] * x + bk[4] * y + bk[7];
            }
          }
        }
        if (CL) CL.set(g.attributes.color.array.subarray(0, c * CS), v0 * CS);
        var flip = !line && o.matrixWorld.determinant() < 0, ic;
        if (g.index) {
          var ia = g.index.array; ic = g.index.count;
          for (j = 0; j < ic; j++) X[i0 + j] = ia[j] + v0;
        } else {
          ic = c;
          for (j = 0; j < ic; j++) X[i0 + j] = v0 + j;
        }
        if (flip) for (j = i0; j + 2 < i0 + ic; j += 3) { var t = X[j + 1]; X[j + 1] = X[j + 2]; X[j + 2] = t; }
        v0 += c; i0 += ic;
      }
      var geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.BufferAttribute(P, 3));
      if (N) geo.setAttribute('normal', new T.BufferAttribute(N, 3));
      if (U) geo.setAttribute('uv', new T.BufferAttribute(U, 2));
      if (CL) geo.setAttribute('color', new T.BufferAttribute(CL, CS));
      geo.setIndex(new T.BufferAttribute(X, 1));
      var f = L[0], px = line ? new T.LineSegments(geo, B.mats[0]) : new T.Mesh(geo, B.mats[0]);
      px.name = 'merged ' + n + ' bodies';
      px.position.set(cx, cy, cz);
      px.updateMatrix();
      px.matrixAutoUpdate = false;
      px.castShadow = f.castShadow; px.receiveShadow = f.receiveShadow; px.renderOrder = f.renderOrder; px.frustumCulled = f.frustumCulled;
      px.raycast = _mgNoop;
      px.userData.arSkip = true; px.userData.mergeProxy = true;
      px.visible = false;
      return { px: px, verts: V };
    }
    /* a layer (one child of the stage group) merged: its nodes noted, its
       bodies sorted by look, a proxy for every look shared by two or more */
    function _mgMake(C, T, now) {
      var u = { C: C, nodes: [], sets: [], S: null, M: null, RS: null, RM: null, clean: true, changed: now, bodies: 0, verts: 0, ms: 0 };
      var t0 = _pfNow(), byK = {}, keys = [], byC = {};
      var lname = String((C.userData && C.userData.layer) || C.name || C.type).replace(/^[a-z]+-/, '');
      C.updateWorldMatrix(true, true);
      (function walk(o, vis) {
        u.nodes.push(o);
        var v = vis && o.visible, i;
        if (v && (o.isMesh || o.isLine || o.isPoints || o.isSprite)) {
          var why = _mgWhyNot(o, T), key = null, need = null, m = o.material;
          if (!why) {
            key = _mgKeyOf(m, T);
            if (key === null) why = (m && m.visible === false) ? 'x' : ((m && m.transparent && m.depthWrite) ? 'see-through, writes depth' : 'its kind of material');
            else {
              need = _mgNeed(m, !!o.isLineSegments);
              var at = o.geometry.attributes;
              if (!need) why = 'a second uv';
              /* a map on a body with no uv reads uv 0,0 -- the proxy carries zeros for it */
              else if ((need.nrm && !_mgAttrOk(at.normal, 3)) || (need.uv && at.uv && !_mgAttrOk(at.uv, 2)) || (need.col && !_mgAttrOk(at.color))) why = 'its attributes';
            }
          }
          if (why) { if (why !== 'x') _mg.apart[why] = (_mg.apart[why] || 0) + 1; }
          else {
            var cs = need.col ? o.geometry.attributes.color.itemSize : 0;
            var k0 = (o.isLineSegments ? 'L' : 'M') + (o.castShadow ? 1 : 0) + (o.receiveShadow ? 1 : 0) + (o.frustumCulled ? 1 : 0) + '|' + o.renderOrder + '|' +
                     (need.nrm ? 'n' : '') + (need.uv ? 'u' : '') + (cs ? ('c' + cs) : '') + '|';
            var k = k0 + key;
            var B = byK[k];
            if (!B) {
              /* the same key without the colour: says later whether a body
                 alone in its look differs only by its colour */
              var c1 = key.indexOf('|'), c2 = (c1 >= 0) ? key.indexOf('|', c1 + 1) : -1;
              B = byK[k] = { list: [], mats: [], ms: (typeof Set === 'function') ? new Set() : null, need: need, cs: cs, line: !!o.isLineSegments,
                             kc: k0 + ((c2 > 0 && key.indexOf('id') !== 0) ? (key.slice(0, c1) + key.slice(c2)) : key) };
              keys.push(k);
              byC[B.kc] = (byC[B.kc] || 0) + 1;
            }
            B.list.push(o);
            if (B.ms ? !B.ms.has(m) : B.mats.indexOf(m) < 0) { B.mats.push(m); if (B.ms) B.ms.add(m); }
          }
        }
        for (i = 0; i < o.children.length; i++) walk(o.children[i], v);
      })(C, true);
      var i;
      for (i = 0; i < keys.length; i++) {
        var B2 = byK[keys[i]];
        if (B2.list.length < 2) {
          _mg.apart['alone in its look'] = (_mg.apart['alone in its look'] || 0) + 1;
          var al = _mg.alone[lname] || (_mg.alone[lname] = [0, 0]);
          al[0]++; if (byC[B2.kc] > 1) al[1]++;
          continue;
        }
        /* a proxy of at most 65 535 vertices: a 16-bit index and no huge
           upload in one go (headless, one 13 MB buffer stalled the GPU
           process for seconds) */
        var ch = [], cv = 0, q2;
        for (q2 = 0; q2 <= B2.list.length; q2++) {
          var o2 = B2.list[q2], c2 = o2 ? o2.geometry.attributes.position.count : 0;
          if (!o2 || (ch.length && cv + c2 > 65535)) {
            if (ch.length >= 2) {
              var mm = [], q3, ms3 = (typeof Set === 'function') ? new Set() : null;
              for (q3 = 0; q3 < ch.length; q3++) {
                var m3 = ch[q3].material;
                if (ms3 ? !ms3.has(m3) : mm.indexOf(m3) < 0) { mm.push(m3); if (ms3) ms3.add(m3); }
              }
              var r = _mgBuild(T, { list: ch, mats: mm, need: B2.need, cs: B2.cs, line: B2.line });
              if (!r) { _mg.apart['a map squeezed to nothing'] = (_mg.apart['a map squeezed to nothing'] || 0) + ch.length; }
              else {
                /* where each material's map sat when the uvs were baked */
                var tp = null;
                if (B2.need.uv && _mgTex0(mm[0])) {
                  tp = new Float64Array(mm.length * MG_TP);
                  for (q3 = 0; q3 < mm.length; q3++) { var t3 = _mgTex0(mm[q3]); if (t3) _mgTexPut(t3, tp, q3 * MG_TP); }
                }
                _mg.root.add(r.px);
                u.sets.push({ px: r.px, mem: ch, mats: mm, tp: tp });
                u.bodies += ch.length; u.verts += r.verts;
              }
            } else if (ch.length === 1) _mg.apart['too big to pair'] = (_mg.apart['too big to pair'] || 0) + 1;
            ch = []; cv = 0;
          }
          if (o2) { ch.push(o2); cv += c2; }
        }
      }
      var nn = u.nodes.length;
      u.S = new Float64Array(nn * MG_W); u.RS = new Array(nn * 3);
      _mgSnap(u, u.S, u.RS);
      u.M = new Float64Array(u.S); u.RM = u.RS.slice();
      u.ms = _pfNow() - t0;
      C.__levaMg = u;
      return u;
    }
    function _mgDrop(u) {
      var i;
      for (i = 0; i < u.sets.length; i++) {
        var px = u.sets[i].px;
        if (px.parent) px.parent.remove(px);
        try { px.geometry.dispose(); } catch (e) {}
      }
      u.sets = [];
      if (u.C.__levaMg === u) u.C.__levaMg = null;
    }
    function _mgGSig(G) {
      var p = G.position, q = G.quaternion, s = G.scale;
      return [p.x, p.y, p.z, q._x, q._y, q._z, q._w, s.x, s.y, s.z, G.matrixAutoUpdate ? 1 : 0];
    }
    /* before a frame of the stage's scene: merge what is new, swap in the
       proxies of every layer still as it was merged */
    function _mgBefore(st) {
      var G = st.group, T = global.THREE, sc = st.scene;
      if (!G || !T || !sc || G.parent !== sc || !G.visible) return false;
      var now = _pfNow(), i, j, u, made = 0, madeB = 0, madeP = 0, madeMs = 0, re = 0;
      var se = sc.matrixWorld.elements;
      if (se[0] !== 1 || se[5] !== 1 || se[10] !== 1 || se[12] !== 0 || se[13] !== 0 || se[14] !== 0) return false;
      _mgKC = (typeof Map === 'function') ? new Map() : null;
      if (!_mg.root || _mg.root.parent !== sc) {
        if (!_mg.root) {
          _mg.root = new T.Group();
          _mg.root.name = 'merged bodies';
          _mg.root.userData.arSkip = true;
          _mg.root.raycast = _mgNoop;
        }
        sc.add(_mg.root);
      }
      /* the stage group moved: every layer is drawn body by body until it is merged again */
      var gs = _mgGSig(G), gch = !_mg.gs;
      if (_mg.gs) for (i = 0; i < gs.length; i++) if (gs[i] !== _mg.gs[i]) { gch = true; break; }
      if (gch) { _mg.gs = gs; for (i = 0; i < _mg.units.length; i++) { _mg.units[i].clean = false; _mg.units[i].changed = now; } }
      for (i = _mg.units.length - 1; i >= 0; i--) {
        u = _mg.units[i];
        if (u.C.parent !== G) { _mgDrop(u); _mg.units.splice(i, 1); }
      }
      if (!_mg.units.length) { _mg.apart = {}; _mg.alone = {}; }   /* a new house: the tally starts over */
      /* Coalesced product drags replace complete layer groups once per frame.
         Merge each new group too: drawing their individual boards while the
         finger is down otherwise turns the live roof into thousands of calls. */
      var busy = !!_drag.on && !_drag.frame;
      for (i = 0; i < G.children.length; i++) {
        var C = G.children[i];
        u = C.__levaMg;
        if (!u) {
          if (busy || !C.visible) continue;
          u = _mgMake(C, T, now);
          _mg.units.push(u);
          made++; madeB += u.bodies; madeP += u.sets.length; madeMs += u.ms;
          continue;
        }
        if (_mgDiff(u)) { u.changed = now; u.clean = _mgAsMerged(u) && !gch; }
        if (!u.clean && !busy && now - u.changed > 1000) {
          _mgDrop(u);
          var k2 = _mg.units.indexOf(u);
          u = _mgMake(C, T, now);
          if (k2 >= 0) _mg.units[k2] = u; else _mg.units.push(u);
          re++; madeMs += u.ms;
        }
      }
      if (made || re) {
        _mg.ms += madeMs; _mg.lastMs = madeMs; _mg.lastAt = now;
        if (made) _mg.merges++;
        _mg.re += re;
        if (made) _log('merge    ' + made + ' layer(s): ' + madeB + ' bodies drawn as ' + madeP + ' in ' + Math.round(madeMs) + ' ms' + (re ? (', ' + re + ' merged again') : '') + ' (' + CORE_VERSION + ')');
      }
      /* the swap */
      var hid = _mg.hid, skp = _mg.skp, all = true;
      hid.length = 0; skp.length = 0;
      for (i = 0; i < _mg.units.length; i++) {
        u = _mg.units[i];
        if (!u.clean) { all = false; continue; }
        for (j = 0; j < u.sets.length; j++) {
          var S = u.sets[j];
          if (!_mgSetOk(S)) { all = false; continue; }
          S.px.visible = true;
          for (var q = 0; q < S.mem.length; q++) { S.mem[q].visible = false; hid.push(S.mem[q]); }
        }
        /* nothing in it moved: its world matrices are the ones it was merged with */
        if (!u.C.hasOwnProperty('updateMatrixWorld')) { u.C.updateMatrixWorld = _mgNoop; skp.push(u.C); }
      }
      _mgKC = null;
      _mg.frames++;
      if (all && _mg.units.length) _mg.framesM++;
      return true;
    }
    function _mgAfter() {
      var i, j, hid = _mg.hid, skp = _mg.skp;
      for (i = 0; i < hid.length; i++) hid[i].visible = true;
      for (i = 0; i < skp.length; i++) delete skp[i].updateMatrixWorld;
      hid.length = 0; skp.length = 0;
      for (i = 0; i < _mg.units.length; i++) for (j = 0; j < _mg.units[i].sets.length; j++) _mg.units[i].sets[j].px.visible = false;
    }
    /* the stage's renderer draws through this from the first build on */
    function _mgHook(st) {
      if (!st || !st.renderer || _mg.R === st.renderer) return;
      var R = st.renderer, base = R.render;
      _mg.R = R;
      global.__levaMg = _mg;   /* for a look from the console */
      R.render = function (scene, cam) {
        var on = false;
        if (scene === st.scene && !_mgOff()) {
          var tB = _pfNow();
          try { on = _mgBefore(st); _mg.tB = _pfNow() - tB; }
          catch (e) {
            _mg.dead = (e && e.message) || 'threw';
            try { _mgAfter(); } catch (e2) {}
            try { for (var i = 0; i < _mg.units.length; i++) _mgDrop(_mg.units[i]); _mg.units = []; } catch (e3) {}
            try { while (_mg.root && _mg.root.children.length) { var c = _mg.root.children[0]; _mg.root.remove(c); if (c.geometry) c.geometry.dispose(); } } catch (e5) {}
            _log('merge    threw (' + _mg.dead + ') -- every body drawn on its own from now on');
            on = false;
          }
        }
        try { return base.apply(this, arguments); }
        finally {
          if (on) { try { _mgAfter(); } catch (e4) {} }
          /* the old house goes once this frame is out */
          if (scene === st.scene && (_old.g.length || _old.m.length) && !_old.f) {
            _old.f = global.setTimeout(function () { _old.f = 0; try { _oldFlush(); } catch (e6) {} }, 30);
          }
        }
      };
    }
    function _mgLine() {
      var od = ' | old bodies let go after the frame ' + _old.n + 'x, ' + _old.freed + ' freed, ' + _old.kept + ' still in use, last ' + Math.round(_old.ms) + ' ms' + (_old.q ? ' (?dispose=now: at once)' : '');
      if (_mg.q) return 'perf   merged: off (?merge=0) -- every body is a draw call of its own' + od;
      if (_mg.dead) return 'perf   merged: stopped -- ' + _mg.dead + od;
      if (!_mg.R) return 'perf   merged: not started (no build yet)' + od;
      var nU = _mg.units.length, nB = 0, nP = 0, nV = 0, nC = 0, i, ap = [], k;
      for (i = 0; i < nU; i++) { var u = _mg.units[i]; nB += u.bodies; nP += u.sets.length; nV += u.verts; if (u.clean) nC++; }
      for (k in _mg.apart) if (_mg.apart.hasOwnProperty(k)) ap.push([k, _mg.apart[k]]);
      ap.sort(function (a, b) { return b[1] - a[1]; });
      /* alone in their look, by layer -- and how many differ only by colour */
      var alL = [], al = '';
      for (k in _mg.alone) if (_mg.alone.hasOwnProperty(k)) alL.push([k, _mg.alone[k]]);
      alL.sort(function (a, b) { return b[1][0] - a[1][0]; });
      if (alL.length) al = ' | alone by layer: ' + alL.slice(0, 6).map(function (x) { return x[0] + ' ' + x[1][0] + (x[1][1] ? (' (' + x[1][1] + ' only by colour)') : ''); }).join(', ');
      return 'perf   merged (' + CORE_VERSION + '): ' + nB + ' bodies drawn as ' + nP + ' in ' + nU + ' layer(s), ' + Math.round(nV / 1000) + 'k vertices, ' + nC + ' layer(s) as merged now' +
             ' | merges ' + _mg.merges + ' + ' + _mg.re + ' again, ' + Math.round(_mg.ms) + ' ms in all, last ' + Math.round(_mg.lastMs) + ' ms' +
             ' | frames ' + _mg.frames + ', ' + _mg.framesM + ' with every layer merged, the work before the last one (check + any merge) ' + (_mg.tB || 0).toFixed(1) + ' ms' +
             ' | left apart: ' + (ap.length ? ap.slice(0, 6).map(function (x) { return x[0] + ' ' + x[1]; }).join(', ') : 'none') + al + od;
    }
    function rebuild() {
      var _pfA = _pfNow();
      _pfB.layers = [];
      /* WHERE THE REST OF A BUILD GOES (core-v609): the time between named
         points of rebuild(), so the bench can say which pass outside the
         layers is the slow one. Names in brackets are counted elsewhere. */
      _pfB.after = {};
      var _pfS = _pfA;
      /* core-v617: a drag on -- DS the layers built (null: all), DL light */
      var DL = !!_drag.on, DS = (DL && _drag.set) ? _drag.set : null;
      function _pfCut(n) { var t = _pfNow(); _pfB.after[n] = (_pfB.after[n] || 0) + (t - _pfS); _pfS = t; }
      _planRb++;
      var st = _stage(), T = global.THREE;
      if (!st || !st.group || !T) { _log('rebuild skipped -- stage or THREE missing'); return; }
      try { _mgHook(st); } catch (eMH) {}   /* core-v620: the stage draws merged bodies */
      try { _pfGeoWrap(T); } catch (eGW) {}
      if (!DL) _pfG.by = {};
      /* A PRODUCT MAY SET ITS STATE STRAIGHT BEFORE ANYTHING IS DRAWN
         (core-v337): the A-frame puts its eave line where the rafters' feet
         meet the deck, which moves with the pitch and the overhang, and the
         guider reads the roof before any product layer has run. */
      try { if (__PROD && typeof __PROD.beforeBuild === 'function') __PROD.beforeBuild(); } catch (eBB) { _log('beforeBuild: ' + (eBB && eBB.message)); }
      _wopSaid = 0;
      /* one storey stays one storey, whatever a saved project or the slider left */
      try { _foldOneStorey(); } catch (eFO) { _log('one-storey fold threw ' + (eFO && eFO.message)); }
      var _oneSayLater = function () { try {
        if (_oneStorey()) {
          var S1 = storeys();
          _say('frame height one storey: wall ' + Math.round(S1[0].h * 1000) + ' clear, plate to ' + Math.round((S1[0].head - S1[0].y0) * 1000) +
               ' above the floor -- the wall height slider stretches the studs, the inside layers and the facade, no second storey' +
               (_oneStoreyNote ? (' | ' + _oneStoreyNote) : ''));
          _oneStoreyNote = '';
        }
      } catch (eOS) { _say('frame height one-storey check threw ' + (eOS && eOS.message)); } };
      try { var hsn = snapHoles(); if (hsn) _say('hole     ' + hsn); } catch (eHS) {}
      /* THE PHASE COMES FROM THE WALL AS IT WAS LAID (core-v372): on the first
         build there is none yet, so the jambs were put on a grid starting in
         the corner and both sides came out cut. One more pass after the build
         puts them on the joints the wall actually has. */
      if (!_holeRepass) {
        _holeRepass = 1;
        global.setTimeout(function () {
          var before = JSON.stringify(STATE.holes || []);
          try { snapHoles(); } catch (eR) {}
          _holeRepass = 0;
          if (JSON.stringify(STATE.holes || []) !== before) { _say('hole     put back on the joints the wall was laid with'); rebuild(); }
        }, 0);
      }
      setOutReset();   /* the set-out points are collected while this build runs */
      /* THE WALLS PUSH THE PIECES, AND ONLY THE WALLS. confine() ran on every
         rebuild -- so on the rebuild right after a piece was let go, which is
         where the piece kept jumping from the spot it was put (Marek). It runs
         only when the outline or the partitions have actually changed since the
         last build; a piece dragged by hand is left exactly where it was put. */
      try {
        var PLc = _P();
        if (PLc && PLc.fixtures && PLc.fixtures.confine) {
          /* EVERY BUILD, BUT ONLY WHAT IS OUTSIDE. confine() moves nothing that
             already sits inside its room's faces, so a piece put by hand stays
             put; a wall or partition dragged over it pushes it back in on the
             same build (Marek: the walls must keep the pieces in the room). */
          var nC = PLc.fixtures.confine();
          if (nC) _log('fitout: ' + nC + ' piece(s) pushed back inside their room by a wall or partition');
        }
      } catch (eC) { _log('fitout confine threw: ' + (eC && eC.message)); }

      for (var i = st.group.children.length - 1; i >= 0; i--) {
        var c = st.group.children[i];
        if (DS && c.userData && c.userData.layer && !_dragHas(c.userData.layer)) continue;
        /* a layer's own group holds its meshes; dispose through it -- once
           the new house is on screen (core-v620, _oldPut) */
        c.traverse(_oldPut);
        st.group.remove(c);
      }

      _buildLog = [];
      _oneSayLater();
      /* THE CORNER WINDOWS FOLLOW THEIR CORNERS (core-v751): pinned to the
         corner, the set checked against the wall as it is now */
      try { _cwSync(DL); } catch (eCwS) { _say('corner   sync threw: ' + ((eCwS && eCwS.message) || eCwS)); }
      /* windows close under the top plate go up to it -- after the log starts
         over, so the line saying so reaches the snapshot */
      try { _fitOpeningsToPlates(); } catch (eFP) { _say('frame window fit threw: ' + (eFP && eFP.message)); }
      _mark('build');
      function P(line) { _buildLog.push(line); }

      var claimed = _claimed();
      var V = viz();
      var only = V.only || null;
      /* THE OTHER HALF OF only, AND THE ONE THAT WORKS.

         A stage could say which layers to keep. The timber view needs the
         opposite -- everything EXCEPT the fit-out -- and three attempts at
         hiding the furniture after the build all failed, because something
         downstream keeps turning bodies back on. The first stage has never had
         that problem for one reason: with 'only' the other layers are never
         BUILT. A body that does not exist cannot be switched on by anybody.

         So the timber view says what to leave out and gets the same treatment:
         the layer is built into a throwaway group, so the bill is untouched and
         nothing lands in the scene (Marek: v prvnim stage neni, tak se podivej
         tam -- necim ho ve stage 2 zapinas). */
      var except = V.except || null;
      var sampleOn = (typeof __PROD.structureSample === 'function') && STATE.mode !== 5 && editing('structure');
      var ctx = {
        THREE:   T,
        group:   st.group,
        STATE:   STATE,
        storeys: storeys(),
        seg:     segments(),
        roof:    roofPlanes(),
        poly:    poly(),
        stack:   STACK,
        grid:    GRID(),
        claimed: claimed,
        viz:     V,
        log:     P
      };

      /* A STAGE THAT SHOWS ONE ASSEMBLY HIDES EVERYTHING ELSE, the guider
         included. Magenta mass standing around the thing under review is exactly
         the clutter the stage exists to remove. */
      if (sampleOn) P('sample   structure step -- the house is counted but not drawn; the product sample stands in its place');
      else if (only) P('viz      stage ' + STATE.vizStage + ' ' + V.key + ' -- only ' + only.join(', '));
      else if (except) P('viz      stage ' + STATE.vizStage + ' ' + V.key + ' -- everything except ' + except.join(', '));
      else _buildGuider(T, st.group, claimed, P);

      _lastBuild = { built: [], skipped: [], claimed: claimed };
      _notBilled = [];
      _pfCut('clear+guider');
      for (i = 0; i < _layers.length; i++) {
        if (typeof _layers[i].build3d !== 'function') {
          _lastBuild.skipped.push(_layers[i].id + ' (no build3d)');
          continue;
        }
        if (DS && !_dragHas(_layers[i].id)) { _lastBuild.skipped.push(_layers[i].id + ' (kept as it was -- a drag is on)'); continue; }
        if (sampleOn ||
            (only && only.indexOf(_layers[i].id) < 0) ||
            (except && except.indexOf(_layers[i].id) >= 0)) {
          if (DL) { _lastBuild.skipped.push(_layers[i].id + ' (hidden, not counted while a drag is on)'); continue; }
          /* HIDDEN, BUT STILL COUNTED.

             A layer left out of a viz stage used to be skipped outright -- and
             because every layer counts its quantities WHILE it builds, skipping
             it emptied its share of the bill. Open the panel view on a fresh load
             and the budget read zero: the foundation, the floor and the roof had
             never run, so nothing had ever been counted for them.

             A way of LOOKING at the building must not change what the building
             costs. So it is built exactly as before, into a group that is thrown
             away instead of one that is drawn. Its log goes nowhere too, or the
             snapshot would describe parts that are not on screen. */
          var ghost = new T.Group();
          var gctx = {}, gk;
          for (gk in ctx) if (ctx.hasOwnProperty(gk)) gctx[gk] = ctx[gk];
          gctx.group = ghost;
          gctx.log = function () {};
          var _pfL0 = _pfNow();
          try { _layers[i].build3d(gctx); }
          catch (eG) { _log('count-only build threw in ' + _layers[i].id + ': ' + (eG && eG.message)); }
          _pfB.layers.push({ id: _layers[i].id, ms: _pfNow() - _pfL0, hidden: true });
          _lastBuild.skipped.push(_layers[i].id + (sampleOn ? ' (hidden by the structure sample, still counted)' : (' (hidden by viz stage ' + V.key + ', still counted)')));
          continue;
        }
        try {
          /* EACH LAYER IN ITS OWN GROUP, named after it, so a page can show,
             dim or hide a layer on its own (the blueprints); the guider and
             the scenery stay on the stage group itself */
          var lg = new T.Group();
          lg.name = 'layer:' + _layers[i].id;
          lg.userData.layer = _layers[i].id;
          ctx.group = lg;
          ctx.marks = [];   /* the assembly's phase marks, written by the builder */
          ctx.item = null;  /* the item every body added from now on belongs to */
          lg._qtyHeld = []; _qtyLayer = { g: lg, ctx: ctx };   /* core-v682: what is under grade, held for the qty audit */
          /* TAGGED AS IT GOES IN: whatever a builder adds to its group carries
             the item current at that moment (a mark sets it), so a builder that
             interleaves plates, studs and headers still yields one step per
             item -- the walk groups by the tag, in order of first appearance */
          (function (grp, c) {
            var baseAdd = T.Group.prototype.add;
            grp.add = function () {
              var q;
              for (q = 0; q < arguments.length; q++) if (arguments[q] && arguments[q].userData && c.item && !arguments[q].userData.asm) arguments[q].userData.asm = c.item;
              return baseAdd.apply(this, arguments);
            };
          })(lg, ctx);
          var _pfL1 = _pfNow();
          if (!DL) _pfG.on = _layers[i].id;   /* core-v621: its geometry timed (whole builds) */
          try { _layers[i].build3d(ctx); }
          finally { _pfG.on = ''; _pfB.layers.push({ id: _layers[i].id, ms: _pfNow() - _pfL1 }); }
          try { finiteGuard(lg, T, _layers[i].id, ctx.log); } catch (eFG) { P('guard threw in ' + _layers[i].id + ' -- ' + (eFG && eFG.message)); }
          lg.userData.marks = ctx.marks;
          st.group.add(lg);
          ctx.group = st.group;
          _lastBuild.built.push(_layers[i].id);
        } catch (eL) {
          ctx.group = st.group;
          /* AND INTO THE SNAPSHOT, not only into _lastBuild.

             A layer that throws was recorded somewhere nobody can read from a
             tablet, so it disappeared from the model in silence -- which is
             indistinguishable from a layer that was never written, or one whose
             patch did not arrive. Every wrong turn today started there. */
          _lastBuild.skipped.push(_layers[i].id + ' THREW ' + (eL && eL.message ? eL.message : eL));
          P('THREW ' + _layers[i].id + ' -- ' + (eL && eL.message ? eL.message : eL));
        }
      }
      if (sampleOn) {
        try {
          var sgp = new T.Group();
          sgp.name = 'structure-sample';
          sgp.userData.layer = 'structure-sample';
          ctx.group = sgp; ctx.marks = []; ctx.item = null;
          __PROD.structureSample(ctx);
          sgp.position.set(_smp.off.x, 0, _smp.off.z);
          st.group.add(sgp);
          var nSm = 0;
          sgp.traverse(function (o) { if (o.isMesh) nSm++; });
          P('sample   ' + nSm + ' body(ies) drawn by the product' + (nSm ? '' : ' -- NOTHING: the sample builder drew no mesh'));
          _smpRead();
          /* THE CAMERA FRAMES THE SAMPLE (core-v432, Marek: kamera pri step
             structure neni idealne nastavena, bud jsou bloky male nebo velke).
             Each block system has its own size, so on the STRUCTURE step the
             sample's own box sets the distance: the camera looks at the
             block's centre from the usual angle, far enough that the whole
             block fills the view, close enough that it does not shrink. Done
             once per sample, then the finger owns the camera again. */
          try {
            var key = _sysKey ? _sysKey() : '';
            /* not while a grip is held (core-v455, Marek: pri editaci tvary
               odskakuji): the size is in the key, so every drag step asked for
               a new frame; the frame waits until the finger lifts */
            /* framed whether or not the sample has a size box (core-v456, Marek:
               sdk se neprizpusobi): the drywall gives no size, so it had no box
               and was never framed; the group's own bounds are enough */
            if (st.persCam && _smp.camKey !== key && !_smp.drag) {
              var bx = new T.Box3().setFromObject(sgp);
              if (!bx.isEmpty()) {
                var sph = bx.getBoundingSphere(new T.Sphere()), camS = st.persCam, vF = camS.fov * Math.PI / 180, asp = camS.aspect || 1.4;
                /* a little further back than the sphere asks (core-v454, Marek:
                   cihelny blok a concrete vyplnuji obrazovku moc): the block
                   samples stand with their long side to the camera and ran
                   past the edges; 1.25 leaves air round every element */
                var hF = 2 * Math.atan(Math.tan(vF / 2) * asp), mF = Math.min(vF, hF), distS = (sph.radius / Math.sin(mF / 2)) * 1.25;
                var dirS = new T.Vector3(1, 0.5, 1).normalize();
                camS.position.copy(sph.center.clone().add(dirS.multiplyScalar(distS)));
                camS.near = Math.max(0.02, distS / 200); camS.far = Math.max(camS.far, distS * 6); camS.updateProjectionMatrix();
                var ctlS = _navControls();
                if (ctlS && ctlS.target) { ctlS.target.copy(sph.center); if (ctlS.update) ctlS.update(); }
                _smp.camKey = key;
                P('sample   camera framed on the block, radius ' + Math.round(sph.radius * 1000) + ', distance ' + Math.round(distS * 1000));
              }
            }
          } catch (eCam) { P('sample   camera not framed: ' + ((eCam && eCam.message) || eCam)); }
          if (_smp.box) _smpKoty(T, sgp);
          P('sample   drag points ' + ((_smp.box && _smp.set) ? ('length / width / height, step ' + (STATE.isMetric ? '10 mm' : '1 in') +
            ' -- now ' + _smpFmt(_smp.box.l) + ' x ' + _smpFmt(_smp.box.w) + ' x ' + _smpFmt(_smp.box.h)) :
            'none -- the product gives no sampleSize / sampleSet') + (_smp.last ? (' | last drag: ' + _smp.last) : ''));
          _smpTrack();
        } catch (eSm) { P('sample   THREW ' + (eSm && eSm.message ? eSm.message : eSm)); }
        ctx.group = st.group;
      } else {
        _smp.box = null;
        _smp.off.x = 0; _smp.off.z = 0;
      }
      _pfCut('(layers)');
      try { _applyLayerModes(); } catch (eLM) {}
      if (!DL) { try { _tagAudit(P); } catch (eTA) { P('tag audit threw: ' + ((eTA && eTA.message) || eTA)); } }
      try { _asmRebuilt(); } catch (eAS) {}
      _pfCut('modes+tags+assembly');
      /* AFTER THE ASSEMBLY, NOT BEFORE IT.

         The timber view hid the fit-out and then _asmApply turned every body in
         the building back on -- at walk step 0 it does exactly that, and the
         rebuild calls it. So the view was correct for a fraction of a second
         and then undone, which is indistinguishable from a filter that does not
         work (Marek: porad jsou tam fitouts). It goes last now, and _asmApply
         calls it again whenever it makes things visible. */
      /* WHAT THE AFTER-BUILD PASSES COST, IN MILLISECONDS.

         Three of these passes walk every body in the model and I added two of
         them today. "It feels slow" cannot be argued with; a number can. Each
         one is timed and the line is printed, so if one of them is the reason
         it says so instead of us guessing (Marek: zase pomale nacteni). */
      var _t0 = 0, _tim = [];
      function _timed(name, fn) {
        _pfCut('other passes');
        try { _timedIn(name, fn); } finally { _pfCut(name); }
      }
      function _timedIn(name, fn) {
        var a = 0, b = 0;
        try { a = (global.performance && global.performance.now) ? global.performance.now() : Date.now(); } catch (eT1) {}
        try { fn(); } catch (eF) { P(name + ' threw: ' + ((eF && eF.message) || eF)); }
        try { b = (global.performance && global.performance.now) ? global.performance.now() : Date.now(); } catch (eT2) {}
        _tim.push(name + ' ' + Math.round(b - a) + ' ms');
      }
      if (!DL) {
      _timed('split', function () { _matSplit(P); });
      _timed('audit', function () { _matAudit(P); });
      _timed('qty', function () { _qtyAudit(P); });
      /* the truss sheet's own count, so it can be checked from the log rather
         than by opening a PDF every time */
      try {
        var TSx = _pdfTrusses();
        var tl2 = [], q6;
        for (q6 = 0; q6 < TSx.length; q6++) {
          tl2.push(TSx[q6].label + ' x' + TSx[q6].qty +
                   ' span ' + Math.round(TSx[q6].span * 1000) +
                   ' rise ' + Math.round(TSx[q6].rise * 1000));
        }
        /* AND HOW MANY OF THOSE MEMBERS CARRY THEIR CUT SHAPE.

           The line I added for this counted itself into a log line of its own,
           which the snapshot's FOCUS filter drops on its prefix -- so it never
           reached him and two rounds went by on a drawing nobody could explain.
           It belongs on the line that already prints and already passes:
           outline 0 means the product is not sending shapes, any other number
           means it is and the drawing is at fault (Marek: kam se ten radek
           vypise). */
        var nM2 = 0, nP2 = 0, o1, o2;
        for (o1 = 0; o1 < TSx.length; o1++) {
          for (o2 = 0; o2 < (TSx[o1].members || []).length; o2++) {
            nM2++;
            if (TSx[o1].members[o2].poly) nP2++;
          }
        }
        /* A LOG HE CANNOT PASTE IS A LOG THAT DOES NOT EXIST.

           This printed all eleven shapes with their spans on one line, which is a
           paragraph; the shapes are already listed by the product on its own truss
           list line, so here only the counts are news (Marek: delej mensi logy at
           je muzu kopirovat). */
        P('truss sheet  ' + TSx.length + ' sheet(s), ' + nM2 + ' member(s), ' +
          nP2 + ' with a cut outline');

        /* AND WHETHER EACH SHEET HAS A BODY TO PHOTOGRAPH, counted HERE.

           The product counts this too, but it counts it while the roof is still
           being built -- so it reported two shapes when there are eleven, and
           said nothing at all about the hip end, which is added after. This runs
           at the report, when the scene is finished and every sheet exists. */
        var stB = _stage(), nB = 0, tagSet = {}, q7, okS = 0;
        if (stB && stB.scene) {
          stB.scene.traverse(function (o) {
            if (!o || !o.isMesh || !o.userData) return;
            var tg = o.userData.trussSig || o.userData.trussGrp;
            if (!tg) return;
            nB++; tagSet[tg] = 1;
          });
        }
        /* AND IT NAMES THE ONES THAT HAVE NONE.

           Ten sheets of eleven had bodies and I had no way to tell which one was
           short of guessing, which is how the last several rounds went. A count
           that says WHICH is a count worth printing. */
        var missS = [];
        for (q7 = 0; q7 < TSx.length; q7++) {
          var tgs7 = TSx[q7].tags || [], hit7 = false, q8;
          for (q8 = 0; q8 < tgs7.length; q8++) if (tagSet[tgs7[q8]]) { hit7 = true; break; }
          if (hit7) okS++;
          else missS.push('T' + (q7 + 1) + (tgs7.length ? '' : ' (no tag)'));
        }
        P('truss bodies ' + nB + ' marked, ' + Object.keys(tagSet).length +
          ' distinct truss(es), ' + okS + ' of ' + TSx.length +
          ' sheet(s) have bodies to photograph' +
          (missS.length ? (' -- without: ' + missS.join(', ')) : ''));
      } catch (eTS) { P('truss sheet threw: ' + ((eTS && eTS.message) || eTS)); }
      try { _postWallReport(P); } catch (ePW) { P('post bays threw: ' + ((ePW && ePW.message) || ePW)); }
      }
      /* the discs are rebuilt with the model: a bay that has just appeared or
         gone needs its plus to appear or go with it */
      try { if (_plusWanted) _plusBuild(); } catch (ePP) {}
      if (!DL) _timed('coating', function () { _coatingBill(P); });
      P('timing  ' + _tim.join(' | ') + ' -- the passes that walk every body after a build');
      try { _vizAfter(P); } catch (eVA) { P('viz pass threw: ' + ((eVA && eVA.message) || eVA)); }
      /* whoever wants to know a build finished (the drawings page re-inks) */
      try { var hq; for (hq = 0; hq < (API.afterBuild || []).length; hq++) { try { API.afterBuild[hq](); } catch (eH) {} } } catch (eH2) {}
      _pfCut('viz+hooks');

      /* STAGE VIEW ONE: ONLY WHAT IS UNDER REVIEW (core-v388, Marek: zamereno
         jen na aktualni prvky, ostatni prvky opacity 5 per cent). With the eye
         on its first stage, everything the building is made of drops to a
         whisper and the piece being judged -- the one a product draws in
         magenta while it is the subject -- stays solid. Move on to the next
         piece and the previous one fades with the rest, because the product
         takes the magenta off it. */
      try {
        if (viz().key === 'rebar') {
          var nR = 0;
          st.group.traverse(function (o) {
            var m = o && o.material;
            if (!m || !m.color || o.userData.arSkip) return;
            if (o.userData && o.userData.rebar) { nR++; m.transparent = false; m.opacity = 1; m.depthWrite = true; return; }
            m.transparent = true; m.opacity = 0.03; m.depthWrite = false;
          });
          _say('view     reinforcement: ' + nR + ' bar(s) solid, everything else at 3 per cent');
        }
      } catch (eVr) {}
      try {
        if (viz().key === 'focus') {
          var hasFocus = false;
          st.group.traverse(function (o) {
            var m = o && o.material;
            if (!m || !m.color) return;
            if (m.color.getHex() === 0xFF00FF) hasFocus = true;
          });
          if (hasFocus) {
            st.group.traverse(function (o) {
              var m = o && o.material;
              if (!m || !m.color || o.userData.arSkip) return;
              if (m.color.getHex() === 0xFF00FF) { m.transparent = false; m.opacity = 1; m.depthWrite = true; return; }
              /* the guider's own see-through masses -- the roof planes -- go
                 away entirely, or they sit as a veil over the review
                 (core-v416, Marek: je tam strecha planes overlay) */
              /* the see-through masses are not hidden any more -- that took
                 the below-grade footings with them (core-v419, Marek: neni videt
                 zaklad domu); they go to a whisper like everything else */
              m.transparent = true;
              m.opacity = (m.opacity < 0.99) ? 0.02 : 0.05;
              m.depthWrite = false;
            });
            _say('view     piece under review: the magenta piece is solid, everything else is at 5 per cent');
          } else {
            _say('view     piece under review: nothing is drawn in magenta right now, so the model is left as it is');
          }
        }
      } catch (eVz) {}
      /* THE SELECTED OPENING SHOWS AS A GHOST (core-v355), so it is clear
         which one the corners belong to and what the bin would take away */
      try {
        var edOn = false;
        try { edOn = !!opEdit() && !!editing('openings'); } catch (eE0) { edOn = false; }
        /* EVERY OPENING IS GHOSTED, ONLY THE TAPPED ONE HAS GRIPS
           (core-v366): with the edit on, the windows, the doors and the plain
           holes all show their fill, so it is clear what can be touched; the
           corner dots and the numbers belong to whichever one was tapped. */
        var HL = (edOn || _hh.on) ? (STATE.holes || []).concat(edOn ? wallOpenings() : []) : [], qg;
        _holeTrack();
        for (qg = 0; qg < HL.length && !sampleOn; qg++) {
          /* THE GHOST IS THE GLASS, NOT THE BOX ROUND IT (core-v503, Marek:
             overlay ghost pres half wall window je ctverec, ma byt tvar
             zaskleni): a product that answers __PROD._opOutline for the unit
             gets its own outline extruded through the wall instead of the
             rectangle -- the triangle of a full glazing, the band of a half */
          var OLg = HL[qg].rec ? _opOutlineOf(HL[qg].rec) : null;
          if (OLg) {
            var nO = 0, pqO, matO = _mat(T, 0xC49A4A, 0.18);
            for (pqO = 0; pqO < OLg.polys.length; pqO++) {
              var gmO = _opOutlineMesh(T, OLg.sg, OLg.polys[pqO], _opGhostDepth(), matO);
              if (gmO) { gmO.userData.arSkip = true; gmO.userData.opGhost = true; gmO.renderOrder = 40; st.group.add(gmO); nO++; }
            }
            if (nO) { _opOutlineSay(HL[qg].rec, OLg.polys.map(function (P) { return P.length; }).join('+')); continue; }
          }
          var Gh = _holeGeom(HL[qg]);
          if (!Gh) continue;
          var sel = !!(_hh.on && _hh.on.id === HL[qg].id);
          var gm = new T.Mesh(new T.BoxGeometry(Math.abs(Gh.p1[0] - Gh.p0[0]) + 0.04, Gh.y1 - Gh.y0, Math.abs(Gh.p1[2] - Gh.p0[2]) + 0.04),
                              _mat(T, 0xC49A4A, sel ? 0.30 : 0.18));
          gm.position.set((Gh.p0[0] + Gh.p1[0]) / 2, (Gh.y0 + Gh.y1) / 2, (Gh.p0[2] + Gh.p1[2]) / 2);
          gm.userData.arSkip = true;
          gm.renderOrder = 40;
          st.group.add(gm);
        }
      } catch (eGh) {}
      /* Some products draw their own joinery without grab volumes. Give those
         units the same hit target, using their real outline (including raked
         glazing), even before the openings step has been opened. */
      if (!sampleOn && STATE.view !== '2d') {
        var grabbed = {};
        st.group.traverse(function (ob) {
          var u = ob.userData || {};
          if (u.opSeg) grabbed[u.opSeg + ':' + u.opAt] = true;
        });
        (STATE.openings || []).forEach(function (rec) {
          if (rec.host === 'roof' || grabbed[rec.seg + ':' + rec.at]) return;
          var sg = segment(rec.seg);
          if (!sg || !(sg.len > 0)) return;
          var outline = _opOutlineOf(rec), polys;
          if (outline) polys = outline.polys;
          else {
            var low = rec.sill || 0, half = rec.w / 2;
            if (typeof __PROD._wallBaseY === 'function') low += __PROD._wallBaseY() - storeys()[0].y0;
            polys = [[[rec.at - half, low], [rec.at + half, low],
                      [rec.at + half, low + rec.h], [rec.at - half, low + rec.h]]];
            var item = _openingItem(rec.code);
            if (item && item.round) {
              var circle = [];
              for (var ci = 0; ci < 40; ci++) {
                var angle = ci * Math.PI / 20;
                circle.push([rec.at + half * Math.cos(angle), low + rec.h / 2 + rec.h / 2 * Math.sin(angle)]);
              }
              polys = [circle];
            }
          }
          polys.forEach(function (poly) {
            var mat = new T.MeshBasicMaterial({ visible: false });
            var mesh = _opOutlineMesh(T, sg, poly, _opGhostDepth(), mat);
            if (!mesh) { mat.dispose(); return; }
            mesh.userData.opSeg = rec.seg; mesh.userData.opAt = rec.at;
            mesh.userData.arSkip = true;
            st.group.add(mesh);
          });
        });
      }
      /* beside the sample these are built where nobody sees them, so whatever
         they count is still counted */
      _pfCut('view+ghosts');
      var gOut = sampleOn ? new T.Group() : st.group;
      try { _buildPerson(T, gOut); } catch (eP) { _log('person: ' + (eP && eP.message ? eP.message : eP)); }
      try { _buildProps(T, gOut, P); } catch (ePr) { P('prop threw: ' + ((ePr && ePr.message) || ePr)); }
      try { _propHandles(); } catch (ePh) {}
      try { _buildPool(T, gOut, P); } catch (ePo) { P('pool threw: ' + ((ePo && ePo.message) || ePo)); }
      try { _poolLink(); } catch (ePl) {}
      try {
        var fxs = (STATE.openings || []).filter(function (o) { return _openingFixed(o); });
        P('openings: ' + (STATE.openings || []).length + ', ' + fxs.length + ' fixed -- drawn in the plan as glazing, no sash' +
          (fxs.length ? (': ' + fxs.map(function (o) { return (o.code || o.kind) + ' on ' + o.seg; }).join(', ')) : ''));
      } catch (eFx) { P('openings: threw ' + (eFx && eFx.message)); }
      try {
        var TRl = _terraceRect();
        P('terrace: ' + (TRl ? (fmtLen(TRl.x1 - TRl.x0) + ' x ' + fmtLen(TRl.z1 - TRl.z0) + ' at ' + TRl.x0.toFixed(2) + ', ' + TRl.z0.toFixed(2) +
          ', ' + (_terrace().side ? ('attached to wall ' + _terrace().side) : 'free-standing') + (_terrSettleNote ? (' (last drag: ' + _terrSettleNote + ')') : '')) : 'off'));
      } catch (eTl) {}
      /* the bill changes with the model, so it is sent every time the model is */
      _pfCut('person+props+pool');
      var _pfQ0 = _pfNow();
      if (!DL) { try { publishBoq(); } catch (eB) { _log('budget: ' + (eB && eB.message ? eB.message : eB)); } }
      _pfB.boq = _pfNow() - _pfQ0;
      _pfCut('(bill)');
      /* the sill dimension rides on the model, so it goes in with it and is gone
         the moment the drag that asked for it is */
      try { _sillKotBuild(); } catch (eK) {}

      /* AND THE FRUSTUM, IF THE STAGE MEASURED NOTHING. The group is full again by
         here, which is the only moment it is safe to ask for a re-measure. */
      try { _refitIfCollapsed(); } catch (eF) {}
      _pfCut('sill+refit');

      /* the drawing is a second surface onto the same state: one rebuild, both */
      var _pfD0 = _pfNow();
      try { drawPlan(); } catch (eD) { _log('plan: ' + (eD && eD.message)); }
      _pfB.plan = _pfNow() - _pfD0;
      _pfCut('(plan)');

      if (sampleOn !== _sampleShown) {
        _sampleShown = sampleOn;
        try { if (STATE.view === '3d') { st.fitCameraToFootprint(); if (sampleOn) _smpBackOff(st); } } catch (eSF) {}
        P('sample   camera ' + (sampleOn ? ('framed on the sample, backed off x' + SMP_BACKOFF + ' so the drag points and the dimensions fit') : 'back on the whole house'));
      }

      _hRefresh();
      _pfCut('handles');
      /* core-v662: a model that changed after the boot framing is framed again
         while nobody has touched anything yet; core-v706: after every build
         that changed its size, whoever moved the camera, in 3D and on the plan */
      try { _fitAfterBuild((_afUntil && Date.now() < _afUntil) ? 'the model changed after the first framing' : 'the model changed'); } catch (eAfR) {}
      /* Put a new glazing's control on the same model update. Raycasting
         before the next render needs the new bodies' world matrices. */
      try {
        if (_opcOn()) { st.group.updateMatrixWorld(true); if (st.camera) st.camera.updateMatrixWorld(true); }
        _opcTick();
      } catch (eOpc) { _log('opening cycle: ' + (eOpc && eOpc.message)); }
      /* core-v589: the build's whole time, kept for the perf lines */
      try {
        var _pfMs = _pfNow() - _pfA;
        _pfB.n++; _pfB.hist.push(_pfMs); if (_pfB.hist.length > 30) _pfB.hist.shift();
        if (_pfB.firstEnd == null) { _pfB.firstEnd = _pfNow(); try { _benchMaybe(); } catch (eBn) {} try { _modelReady(); } catch (eMR) {} }
        /* core-v607: the work bench counts this build into its part */
        try { _benchBuilt(_pfMs); } catch (eBb) {}
      } catch (ePfB) {}
      /* core-v603: a new model is a reason to draw a frame */
      try { var stW = _stage(); if (stW && stW.wake) stW.wake(1500); } catch (eWk) {}
    }

    /* THE STAGE REFIT AN EMPTY GROUP -- the note in every resize line for weeks,
       and nothing was ever done about it.

       rebuild() empties st.group and fills it again. The stage's own resize sits
       on a ResizeObserver, so when it fires inside that window setView('2d')
       measures a Box3 of nothing and leaves the frustum at its default 1.76 m.
       rebuild does not call setView, so once it collapses it stays collapsed --
       and the next resize lands in the same window, which is why the number never
       recovered and read 1.76 m under a 19 m building in every snapshot.

       Everything still LOOKED right because zoom absorbed it: 0.09 instead of
       about 1. What did not look right is the range around it -- the 0.02..40
       limits then mean the widest view is 88 m and the closest is 44 mm, so
       zooming out ran out of room long before it should and zooming in went far
       past anything useful.

       Measured again here, where the group is full, and the remembered framing
       put straight back on top so the picture does not jump. The test is the
       building's own size, so once it is right this costs nothing. */
    function _refitIfCollapsed() {
      if (STATE.view !== '2d') return false;
      var st = _stage(), cam = st && st.orthoCam;
      if (!st || !cam || !st.setView) return false;
      var was = cam.right - cam.left;
      var b = _planBounds();
      var wide = Math.max(b.ax1 - b.ax0, b.az1 - b.az0);
      if (!(wide > 1) || !(was > 0) || was > wide * 0.5) return false;
      st.setView('2d', _planFocus());
      try { _apply2DControls(); } catch (e2c) {}
      _planRestore(true);
      _resizeLog('refit    frustum was ' + was.toFixed(2) + ' m under a ' + wide.toFixed(2) +
                 ' m building -- the group was empty when the stage last measured it; ' +
                 'now ' + (cam.right - cam.left).toFixed(2) + ' m');
      return true;
    }

    /* --------------------------------------------------------------------------
       SCENERY. Trees, so the building has something to be the size of.

       A model floating on a grey plane has no scale and no place; the eye reads
       it as a drawing. Two or three trees round it and it reads as a building in
       a garden -- which is what somebody is actually buying.

       THEY ARE CROSSED CARDS, not models. Two flat silhouettes at right angles
       read as a tree from every direction, cost two dozen triangles each, and
       never pull attention away from the thing being configured. Modelled trees
       would do the opposite on both counts.

       White and unlit, with a hairline edge: the drawing is the CONTOUR. Shading
       them would turn every face away from the light into pencil grey, which is
       physics rather than paper, and the model would start competing with its own
       background. Lifted from the tiny lib, where this was already settled.
       -------------------------------------------------------------------------- */
    /* ONE SWITCH, NOT TWO. Meadow and forest were the same idea at two
       densities, and nobody wants to choose a density -- they want to see the
       building in a garden or not. */
    var SCENERY = ['none', 'trees'];
    var _sceneryGrp = null;

    /* Deterministic, so the garden does not rearrange itself on every rebuild. */
    function _rng(seed) {
      var t = seed >>> 0;
      return function () { t = (t * 1664525 + 1013904223) >>> 0; return t / 4294967296; };
    }

    /* --------------------------------------------------------------------------
       A TREE, drawn as a silhouette worth looking at.

       The old one was a stack of cones and a rectangle: unmistakably a diagram,
       and next to a building drawn to the millimetre it read as a placeholder
       somebody forgot to finish. Scenery does not have to be accurate, but it
       does have to look like it was meant.

       TWO SHAPES, FOUR CROWNS EACH, AND THE REST IS SCALE AND ROTATION. A crown
       is a radius that varies with the angle -- three harmonics, seeded per
       variant -- so no two outlines repeat, and the trunk forks into the crown
       rather than stopping at it. Eight geometries are built once and every tree
       in the garden is one of them turned and sized differently, which is why
       ninety trees still cost about what nine did.

       Broadleaf, both of them, because that is what stands round a house.
       -------------------------------------------------------------------------- */
    var TREE_KINDS = [
      /* spread: a wide low crown on a short trunk, the garden tree */
      { crownY: 0.60, r: 0.36, stretch: 0.92, trunk: 0.34, taper: 0.45, forks: 3 },
      /* upright: narrower and taller, the one at the back of the plot */
      { crownY: 0.58, r: 0.25, stretch: 1.45, trunk: 0.40, taper: 0.55, forks: 2 }
    ];
    /* THE TREES ARE THE KIT'S (ui-v116): three kinds, each a switch in the
       Place step, all instanced and draggable; the lib only hands the kit the
       stage, the footprint and its state and asks it to build. */
    var _treesKit = null;
    function _treesState() {
      if (!STATE.trees || Array.isArray(STATE.trees)) STATE.trees = { on: { a: STATE.scenery === 'trees' }, list: {} };
      return STATE.trees;
    }
    function _trees() {
      if (_treesKit) return _treesKit;
      var U = _UI(); if (!U || !U.trees) return null;
      _treesKit = U.trees({
        stage: _stage, controls: _navControls, log: _log,
        view: function () { return STATE.view; },
        origin: function () { return { x: STATE.origin.x, z: STATE.origin.z }; },
        footprint: function () { return { w: STATE.footprint.w, l: STATE.footprint.l }; },
        state: _treesState, save: savePref
      });
      return _treesKit;
    }
    /* THE MAP UNDER THE TREES: in 3D the trees stand in for the site and the
       map goes; in 2D the map is the plan's ground and stays whatever the
       trees do (Marek: one finger could not pan the map under the house) */
    function _mapUnderTrees() {
      var mapDiv = $('cf-map'); if (!mapDiv) return;
      var any = (STATE.scenery === 'trees');
      mapDiv.style.visibility = (any && STATE.view !== '2d') ? 'hidden' : '';
    }
    function _buildScenery() {
      var st = _stage();
      if (!st) return;
      var K = _trees();
      var s = _treesState(), any = !!(s.on && (s.on.a || s.on.b || s.on.c));
      STATE.scenery = any ? 'trees' : 'none';
      _mapUnderTrees();
      if (!K) { _log('scenery: ui-v116 or newer needed for the trees'); return; }
      K.build();
      try { if (st.renderer) K.bindDrag(st.renderer.domElement); } catch (e) {}
      _log('scenery -> ' + (any ? ('trees ' + K.count()) : 'none'));
    }
    function toggleTrees(kind) {
      var K = _trees(); if (!K) return;
      K.toggle(kind);
      _buildScenery();
    }
    function cycleScenery() {
      var i = SCENERY.indexOf(STATE.scenery || 'none');
      STATE.scenery = SCENERY[(i + 1) % SCENERY.length];
      savePref();
      _buildScenery();
      return STATE.scenery;
    }

    /* -------------------------------------------------------------------------
       NAVIGATING THE SCENE.

       Two things, and the second one is the point:

         TWO FINGERS SLIDE THE WHOLE SCENE. Not the model against the scene -- the
         camera and what it is looking at, together. The building stays where it
         was put on the plot; the view moves.

         ZOOM GOES WHERE YOU ARE POINTING. It used to pull towards the middle of
         the scene, which is exactly why the edge of the building could never be
         reached: every notch dragged the view back to the centre of the plan. Now
         the ground point under the cursor -- or under the middle of the pinch --
         is the point that stays still, so zooming at a corner walks the view to
         that corner. That also removes most of the need to pan at all.

       THE MAP IS RESYNCED AFTER EVERY MOVE, because it only agrees with the scene
       as long as it is told where the camera went.
       ------------------------------------------------------------------------- */
    var _navBound = false;
    var _rcNav = null, _ndcNav = null, _planeNav = null, _hitNav = null;

    /* AIMING THE RAY, ONCE.

       Five copies of the same seven lines -- the canvas rect, the two lazy
       singletons, the two NDC formulas, setFromCamera -- one per thing that
       wanted to know what was under the finger. Five chances for one of them to
       drift, and no way to fix an aiming bug in fewer than five places.

       It answers the raycaster or null, and the caller does its own asking. */
    function _aim(px, py, cam) {
      /* handed up to the outer scope on the first call, so the post-wall plus
         can aim with this one and not a copy of it */
      if (!_aimRef) { try { _aimRef = _aim; } catch (eA) {} }
      var st = _stage(), T = global.THREE;
      cam = cam || (st && st.camera);
      if (!st || !T || !cam || !st.renderer) return null;
      var r = st.renderer.domElement.getBoundingClientRect();
      if (!(r.width > 0) || !(r.height > 0)) return null;
      if (!_rcNav) _rcNav = new T.Raycaster();
      if (!_ndcNav) _ndcNav = new T.Vector2();
      _ndcNav.x = ((px - r.left) / r.width) * 2 - 1;
      _ndcNav.y = -((py - r.top) / r.height) * 2 + 1;
      _rcNav.setFromCamera(_ndcNav, cam);
      return _rcNav;
    }

    /* what the model has under a point, or null -- the other half of every copy */
    function _hitGroup(px, py) {
      var rc = _aim(px, py), st = _stage();
      if (!rc || !st || !st.group) return null;
      try { return rc.intersectObjects(st.group.children, true) || null; } catch (x) { return null; }
    }

    function _canvasEl() {
      var st = _stage();
      return (st && st.renderer && st.renderer.domElement) || null;
    }

    function _navCam() {
      var st = _stage();
      if (!st) return null;
      return (STATE.view === '2d' && st.orthoCam) ? st.orthoCam : st.camera;
    }
    function _navControls() {
      var st = _stage();
      if (!st) return null;
      return (STATE.view === '2d' && st.orthoControls) ? st.orthoControls : st.controls;
    }

    /* Where a screen point lands on the ground. That is the anchor a zoom holds
       still, and for a building sitting on a plot the ground is the honest plane
       to measure against. */
    function _groundPoint(px, py) {
      var T = global.THREE, rc = _aim(px, py, _navCam());
      if (!rc) return null;
      if (!_planeNav) _planeNav = new T.Plane(new T.Vector3(0, 1, 0), 0);
      if (!_hitNav) _hitNav = new T.Vector3();
      return rc.ray.intersectPlane(_planeNav, _hitNav);
    }

    function _afterNav() {
      var st = _stage();
      var c = _navControls();
      try { if (c && c.update) c.update(); } catch (e) {}
      try { if (st && st.renderer && st.camera) st.renderer.render(st.scene, st.camera); } catch (e) {}
      _syncMapView(false);
      /* and the plan comes with the camera */
      try { repaintPlan(); } catch (eP) {}
    }

    /* world units per screen pixel, so a hundred pixels of drag is a hundred
       pixels of building at any zoom */
    function _worldPerPixel() {
      var st = _stage(), cam = _navCam();
      if (!st || !cam || !st.renderer) return 0.01;
      var h = st.renderer.domElement.clientHeight || 600;
      if (!cam.fov) return ((cam.top - cam.bottom) / (cam.zoom || 1)) / h;
      var c = _navControls();
      var tgt = (c && c.target) || { x: 0, y: 0, z: 0 };
      var dx = cam.position.x - tgt.x, dy = cam.position.y - tgt.y, dz = cam.position.z - tgt.z;
      var dist = Math.sqrt(dx * dx + dy * dy + dz * dz) || 10;
      return (2 * dist * Math.tan((cam.fov * Math.PI / 180) / 2)) / h;
    }

    function panScene(dxPx, dyPx) {
      var T = global.THREE, cam = _navCam(), c = _navControls();
      if (!T || !cam || !cam.matrixWorld) return;
      var k = _worldPerPixel();
      var right = new T.Vector3(), up = new T.Vector3();
      cam.matrixWorld.extractBasis(right, up, new T.Vector3());
      /* the scene comes with the finger, so the camera goes the other way */
      var mx = (-right.x * dxPx + up.x * dyPx) * k;
      var my = (-right.y * dxPx + up.y * dyPx) * k;
      var mz = (-right.z * dxPx + up.z * dyPx) * k;
      cam.position.set(cam.position.x + mx, cam.position.y + my, cam.position.z + mz);
      if (c && c.target && c.target.set) c.target.set(c.target.x + mx, c.target.y + my, c.target.z + mz);
      _afterNav();
    }

    /* ZOOM ABOUT A POINT. Hold the ground point under the cursor still and pull
       everything else towards it: camera and target both move to p + (v - p)/f.
       One line of arithmetic, and it is the whole difference between a zoom that
       can reach a corner and one that cannot. */
    function zoomAt(px, py, factor) {
      var cam = _navCam(), c = _navControls();
      if (!cam || !(factor > 0)) return;
      var p = _groundPoint(px, py);

      if (cam.fov) {
        if (!p) {
          var tg = (c && c.target) || { x: 0, y: 0, z: 0 };
          p = { x: tg.x, y: tg.y, z: tg.z };
        }
        var nx = p.x + (cam.position.x - p.x) / factor;
        var ny = p.y + (cam.position.y - p.y) / factor;
        var nz = p.z + (cam.position.z - p.z) / factor;
        var d = Math.sqrt((nx - p.x) * (nx - p.x) + (ny - p.y) * (ny - p.y) + (nz - p.z) * (nz - p.z));
        /* both ends of the leash, so a hard zoom cannot end up inside a stud or
           out in orbit */
        if (d < 2 || d > 200) return;
        cam.position.set(nx, ny, nz);
        if (c && c.target && c.target.set) {
          c.target.set(p.x + (c.target.x - p.x) / factor,
                       p.y + (c.target.y - p.y) / factor,
                       p.z + (c.target.z - p.z) / factor);
        }
      } else {
        var z0 = cam.zoom || 1;
        var z = Math.max(0.15, Math.min(12, z0 * factor));
        var applied = z / z0;
        cam.zoom = z;
        if (cam.updateProjectionMatrix) cam.updateProjectionMatrix();
        if (p && c && c.target) {
          /* in plan the camera looks straight down, so holding a point still is a
             move of the target alone */
          var tx = p.x + (c.target.x - p.x) / applied;
          var tz = p.z + (c.target.z - p.z) / applied;
          var upY = cam.position.y || 40;
          c.target.x = tx; c.target.z = tz;
          cam.position.set(tx, upY, tz + 0.0001);
          if (cam.up && cam.up.set) cam.up.set(0, 0, -1);
          if (cam.lookAt) cam.lookAt(tx, 0, tz);
          if (cam.updateProjectionMatrix) cam.updateProjectionMatrix();
        }
      }
      _afterNav();
    }

    /* -------------------------------------------------------------------------
       PLACING AN OPENING BY POINTING AT THE WALL.

       Tapping a tile used to drop the opening on whichever wall had the most room
       left. That is a reasonable guess and it is still the fallback, but it is a
       guess: nobody wants a window somewhere sensible, they want it on THAT wall,
       there. So the tile now ARMS a kind, and the next tap on the model places
       it.

       The pick is a ray into the scene and then the nearest wall in plan to
       wherever it landed. Going via the model rather than a set of invisible
       panels means it works on whatever the walls happen to be made of, and keeps
       working when they change -- there is nothing to keep in step.

       Armed state is deliberately loud: the log says what is armed and the next
       tap says where it went, because an armed tool that looks like an unarmed
       one turns the next orbit into an unwanted window.
       ------------------------------------------------------------------------- */
    var _armed = null;

    /* --------------------------------------------------------------------------
       EDIT MODE FOR OPENINGS.

       Dragging a window used to be live whenever the DOORS step was open, which
       means the step could not be read without the risk of moving something: a
       scroll that starts on a window is a drag. Editing is now a state you turn
       on, and the tile says so.

       IT TURNS ITSELF ON WHEN YOU PLACE ONE. Nobody puts a window in and then
       goes looking for a switch before nudging it -- placing IS the start of
       editing, and the first thing anyone does after is move it.

       And it clears the panel out of the way for the same reason picking a unit
       does: the thing being edited is the building, not the drawer.
       -------------------------------------------------------------------------- */
    var _opEdit = false;
    /* the fitout tool: on from the moment a piece is picked, off by the cross;
       while it is on a plan tap is about fitouts only -- no room renamed, no
       partition dragged (Marek). The bin acts on the piece last touched. */
    var _fitEdit = false, _fitLast = null, _fitDiag = '', _fitDiagRef = null;
    var _fitRoom = null;   /* the room whose plus was tapped: { x, z, name } */
    /* the product's own pieces of the fitout (the picker, the room grouping)
       are optional: a product without them keeps the plan and the bar */
    function _fitOpen(groups) { if (typeof __PROD._openFitouts === 'function') __PROD._openFitouts(groups); }
    /* core-v76: A PRODUCT WITHOUT A FITOUT CATALOGUE HAS NO PLUS. core-v73 put
       typeof guards on the two calls so a fork without fitouts would run, and it
       does -- but the red plus was still drawn in every room and did nothing
       when it was tapped (Marek). The button asks first now. */
    function _fitHasCatalog() { return typeof __PROD._openFitouts === 'function'; }
    function _fitGroups(name) { return (typeof __PROD._fitGroupOf === 'function') ? __PROD._fitGroupOf(name) : null; }
    function fitEdit(on, why) {
      if (on === undefined) return _fitEdit;
      if (_fitEdit !== !!on) _log('fitout mode -> ' + (on ? 'ON' : 'off') + (why ? (' (' + why + ')') : ' (unnamed caller)'));
      _fitEdit = !!on;
      if (!_fitEdit) { _fitLast = null; _fitAt = null; }
      /* rebuild the bar so the fitout buttons come and go with the mode */
      if (_opBarEl && _opBarEl.parentNode) { _opBarEl.parentNode.removeChild(_opBarEl); _opBarEl = null; }
      _opBar();
      try { var w = API.wizard(); if (w && w.refresh) w.refresh(); } catch (eW) {}
      _log('fitout edit ' + (_fitEdit ? 'ON -- drag a piece to move it, tap it to turn it, the bin removes the last one touched; a tap on the floor picks a new piece for that spot' : 'off'));
      return _fitEdit;
    }
    var _fitAt = null;   /* where the next picked piece lands, from a tap on the floor */

    /* --------------------------------------------------------------------------
       THE EDIT BAR: a bin and a way out.

       Edit mode dismisses the drawer, so while it is on there is nothing on
       screen that says it is on or how to leave -- and the pencil reopens the
       wizard, which is not the same thing as finishing what you are doing.

       The cross is the way out and it goes where the bin is, because those are
       the two things you reach for during editing and they are the same kind of
       act: one ends a window, the other ends the session. The bin only appears
       while something is actually moving; the cross is there the whole time.
       -------------------------------------------------------------------------- */
    var _opBarEl = null;
    var _opBarMode = 'exit';

    var _fitBarEls = [];
    var _FIT_PLUS = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>';
    var _FIT_ROT = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12a8 8 0 1 1-2.3-5.7"/><path d="M20 4v5h-5"/></svg>';
    var _FIT_MIR = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18"/><path d="M9 7L4 12l5 5"/><path d="M15 7l5 5-5 5"/></svg>';
    var _OP_CROSS =
      '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" ' +
      'stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
    var _OP_BIN =
      '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" ' +
      'stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M4 7h16"/><path d="M9 7V5h6v2"/><path d="M6 7l1 13h10l1-13"/>' +
      '<path d="M10 11v6M14 11v6"/></svg>';

    /* TWO BUTTONS, NOT ONE WITH TWO MEANINGS. */
    var BIN_LINGER = 5000;
    var _opBinEl = null;
    /* the last opening the finger was on, which is what the bin acts upon */
    var _opLast = null;
    var _opSel = null;   /* the opening shown as selected (red wash) */
    var _opBinTimer = 0;

    function _opBarSet(mode, hot) {
      if (!_opBarEl) return;
      _opBarMode = mode;
      if (_opBarEl.innerHTML !== _OP_CROSS) _opBarEl.innerHTML = _OP_CROSS;
      _opBarEl.style.background = '#1A1C1D';
      _opBarEl.style.color = '#ffffff';
      _opBinHot(mode === 'bin' && hot);
    }

    function _opBinHot(hot) {
      if (!_opBinEl) return;
      /* red with a white glyph, always; a piece over it only makes it bigger */
      _opBinEl.style.background = '#D8232A';
      _opBinEl.style.color = '#ffffff';
      _opBinEl.style.transform = 'scale(' + (hot ? 1.12 : 1) + ')';
    }

    function _opBinShow() {
      if (!_opBinEl) return;
      if (_opBinTimer) { try { global.clearTimeout(_opBinTimer); } catch (e) {} _opBinTimer = 0; }
      _opBinEl.style.display = 'flex';
      _opBinEl.style.opacity = '1';
    }

    /* it does not vanish under the finger that just let go */
    function _opBinLinger() {
      if (!_opBinEl) return;
      if (_opEdit && _opLast && STATE.openings.indexOf(_opLast) >= 0) { _opBinShow(); return; }
      if (_opBinTimer) { try { global.clearTimeout(_opBinTimer); } catch (e) {} }
      _opBinTimer = global.setTimeout(function () {
        _opBinTimer = 0;
        if (_opBinEl) { _opBinEl.style.opacity = '0'; _opBinEl.style.display = 'none'; }
      }, BIN_LINGER);
    }

    /* IN OR OUT, FOR THE ENTRY DOOR IN HAND (core-v567). An entry door opened
       into the building and nothing could say otherwise; a store's pair of
       doors 1.5 m wide then swept the whole store. With a hinged entry door in
       hand in the edit mode a button stands left of the cross: a tap turns the
       door to open the other way, the plan and the bill follow. Windows,
       sliders, garage doors and the picked holes do not get it. */
    var _opSwingEl = null;
    var _OP_SWING = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M3 12h18"/><path d="M12 4v5"/><path d="M9 6.5l3-3 3 3"/><path d="M12 20v-5"/><path d="M9 17.5l3 3 3-3"/></svg>';
    function _opSwingRec() {
      var o = _opLast, i;
      if (!o || (STATE.openings || []).indexOf(o) < 0) {
        o = null;
        if (_opSel) for (i = 0; i < (STATE.openings || []).length; i++) {
          var q = STATE.openings[i];
          if (q && q.seg === _opSel.seg && Math.abs((q.at || 0) - _opSel.at) < 0.01) { o = q; break; }
        }
      }
      if (!o || o.kind !== 'door' || o.host === 'roof') return null;
      var it = null;
      try { it = _openingItem(o.code); } catch (eI) { it = null; }
      if (it && (it.sectional || it.full)) return null;
      return o;
    }
    function _opSwingSync() {
      var host = $('cf-root'), o = (_opEdit && !_fitEdit) ? _opSwingRec() : null;
      if (!host || !o) { if (_opSwingEl) _opSwingEl.style.display = 'none'; return null; }
      if (!_opSwingEl || !_opSwingEl.parentNode) {
        _opSwingEl = document.createElement('button');
        _opSwingEl.id = 'cf-op-swing';
        _opSwingEl.type = 'button';
        _opSwingEl.style.cssText =
          'position:absolute;left:calc(50% - 84px);bottom:26px;' +
          'z-index:15;width:48px;height:48px;border-radius:50%;border:none;cursor:pointer;' +
          'display:none;align-items:center;justify-content:center;background:#fff;color:#1A1C1D;' +
          'box-shadow:0 4px 16px rgba(0,0,0,0.22);';
        _opSwingEl.innerHTML = _OP_SWING;
        _opSwingEl.setAttribute('aria-label', _t('mirror', 'Mirror'));
        _opSwingEl.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _opSwingFlip(); });
        host.appendChild(_opSwingEl);
      }
      _opSwingEl.style.display = 'flex';
      _opSwingEl.setAttribute('aria-pressed', o.out ? 'true' : 'false');
      return o;
    }
    function _opSwingFlip() {
      var o = _opSwingRec();
      if (!o) { _log('door swing: touch an entry door first'); return; }
      if (o.out) o.out = null; else o.out = true;
      _log('door ' + (o.code || o.kind) + ' on ' + o.seg + ' now opens ' + (o.out ? 'OUT of the building' : 'INTO the building') + ' -- the swing button');
      _say('opening  ' + (o.code || o.kind) + ' on ' + o.seg + ' at ' + Math.round((o.at || 0) * 1000) + ', ' + (o.leaves || 1) + ' leaf(s): opens ' +
           (o.out ? 'OUT' : 'IN') + ' (core-v567) -- the same catalogue unit, the bill unchanged; the plan draws the leaves on that side');
      try { savePref(); } catch (eS) {}
      try { rebuild(); } catch (eR) {}
      try { drawPlan(); } catch (eD) {}
      _opSwingSync();
    }

    function _opBar() {
      var host = $('cf-root');
      if (!host) return;

      /* THE PENCIL STAYS. It was hidden here while edit mode dismissed the
         drawer -- with the drawer gone the pencil was the one button that undid
         the state you had just entered. The drawer no longer goes anywhere, so
         the pencil is just the pencil again. */

      var qf;
      for (qf = 0; qf < _fitBarEls.length; qf++) { try { if (_fitBarEls[qf].parentNode) _fitBarEls[qf].parentNode.removeChild(_fitBarEls[qf]); } catch (eF) {} }
      _fitBarEls = [];
      if (!_opEdit && !_fitEdit) {
        if (_opSwingEl && _opSwingEl.parentNode) _opSwingEl.parentNode.removeChild(_opSwingEl);
        _opSwingEl = null;
        if (_opBarEl && _opBarEl.parentNode) _opBarEl.parentNode.removeChild(_opBarEl);
        _opBarEl = null;
        if (_opBinEl && _opBinEl.parentNode) _opBinEl.parentNode.removeChild(_opBinEl);
        _opBinEl = null;
        if (_opBinTimer) { try { global.clearTimeout(_opBinTimer); } catch (e0) {} _opBinTimer = 0; }
        return;
      }
      _opSwingSync();
      if (_opBarEl) return;

      /* Closing ends the editing step; the separate bin acts on the selected
         opening and remains available after the pointer is released. */
      _opBarEl = document.createElement('button');
      _opBarEl.id = 'cf-op-done';
      _opBarEl.type = 'button';
      _opBarEl.setAttribute('aria-label', _t('close', 'Close'));
      _opBarEl.style.cssText =
        'position:absolute;left:50%;bottom:26px;transform:translateX(-50%);' +
        'z-index:15;width:56px;height:56px;border-radius:50%;border:none;cursor:pointer;' +
        'display:flex;align-items:center;justify-content:center;' +
        'box-shadow:0 4px 16px rgba(0,0,0,0.22);' +
        'transition:background .12s,color .12s,transform .12s;';
      _opBarEl.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        /* only the cross acts on a click; in bin mode a drag is in progress and
           the drop is what decides */
        if (_opBarMode === 'exit') { if (_fitEdit) fitEdit(false, 'the cross'); else { opEdit(false); if (_wiz && _wiz.isOpen()) _wiz.close(); } }
      });
      host.appendChild(_opBarEl);

      /* THE FITOUT BUTTONS, LEFT OF THE CROSS, ONLY IN FITOUT MODE: plus opens
         the picker (the only way it opens), rotate turns the piece in hand a
         quarter, mirror flips an L or a U to the other hand (Marek) */
      _fitBarEls = [];
      if (_fitEdit) {
        var defs = [
          { id: 'cf-fit-plus', svg: _FIT_PLUS, off: -196, skip: !_fitHasCatalog(), act: function () { _fitOpen(_fitRoom ? _fitGroups(_fitRoom.key || _fitRoom.name) : null); } },
          { id: 'cf-fit-rot', svg: _FIT_ROT, off: -140, act: function () {
              var PLr = _P(); if (!_fitLast || !PLr || !PLr.fixtures) { _log('rotate: touch a piece first'); return; }
              PLr.fixtures.rotate(_fitLast); _sugOwn(_fitLast, 'turned'); savePref(); rebuild(); drawPlan(); } },
          { id: 'cf-fit-mir', svg: _FIT_MIR, off: -84, act: function () {
              var PLm = _P(); if (!_fitLast || !PLm || !PLm.fixtures || !PLm.fixtures.mirror) { _log('mirror: touch a piece first'); return; }
              PLm.fixtures.mirror(_fitLast); _sugOwn(_fitLast, 'mirrored'); savePref(); rebuild(); drawPlan(); } }
        ], q;
        for (q = 0; q < defs.length; q++) {
          if (defs[q].skip) continue;
          (function (dfn) {
            var b = document.createElement('button');
            b.id = dfn.id; b.type = 'button';
            b.style.cssText =
              'position:absolute;left:calc(50% + ' + dfn.off + 'px);bottom:26px;' +
              'z-index:15;width:48px;height:48px;border-radius:50%;border:none;cursor:pointer;' +
              'display:flex;align-items:center;justify-content:center;background:#fff;color:#1A1C1D;' +
              'box-shadow:0 4px 16px rgba(0,0,0,0.22);';
            b.innerHTML = dfn.svg;
            b.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); dfn.act(); });
            host.appendChild(b);
            _fitBarEls.push(b);
          })(defs[q]);
        }
      }

      /* THE BIN, BESIDE IT, DOWN UNTIL SOMETHING IS IN YOUR HAND */
      _opBinEl = document.createElement('button');
      _opBinEl.id = 'cf-op-bin';
      _opBinEl.type = 'button';
      _opBinEl.setAttribute('aria-label', _t('delete', 'Delete'));
      _opBinEl.style.cssText =
        'position:absolute;left:calc(50% + 40px);bottom:26px;' +
        'z-index:15;width:56px;height:56px;border-radius:50%;border:none;cursor:pointer;' +
        'display:none;opacity:0;align-items:center;justify-content:center;' +
        'box-shadow:0 4px 16px rgba(0,0,0,0.22);' +
        'transition:background .12s,color .12s,transform .12s,opacity .18s;';
      _opBinEl.innerHTML = _OP_BIN;
      /* Delete only the selected element. */
      _opBinEl.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        /* a picked opening goes the same way a window does (core-v362) */
        if (_hh.on) {
          delHole(_hh.on.id);
          _opBinLinger();
          return;
        }
        if (_fitEdit) {
          var PLb = _P();
          if (_fitLast && PLb && PLb.fixtures) {
            PLb.fixtures.remove(_fitLast);
            _log('fitout removed: ' + _fitLast.kind + ' -- bin tapped');
            _fitLast = null;
            savePref(); rebuild(); drawPlan();
          } else _log('bin tapped with no fitout in hand -- touch one first');
          _opBinLinger();
          return;
        }
        var rec = _opLast;
        if (!rec) {
          _log('bin tapped with nothing in hand -- touch a window first');
          return;
        }
        undoPush(); // Include a freshly loaded shared project before deleting.
        _log('removed ' + rec.kind + ' from ' + rec.seg + ' -- bin tapped');
        _opLast = null;
        _opSel = null;
        removeOpening(rec);
        undoPush();
        _opBinEl.style.display = 'none';
      });
      host.appendChild(_opBinEl);
      _opBinHot(false);

      _opBarSet('exit', false);
      _opSwingSync();
    }

    /* which storey the wall slider moves: the lit one on the plan, the
       ground floor anywhere else */
    function _heightTarget() {
      var onPlan = false;
      try { onPlan = !!editing('layout'); } catch (e) { onPlan = false; }
      return onPlan ? (STATE.planStorey | 0) : 0;
    }
    /* the wall total, every storey added up */
    function _wallTotal() {
      var S = storeys(), t = 0, i;
      for (i = 0; i < S.length; i++) t += S[i].h;
      return t;
    }
    /* A SECOND STOREY EXISTS WHEN THERE IS HEIGHT FOR ONE (core-v410, Marek:
       druhe patro je definovane vyskou zdi a vyskou prvniho patra). 600 is
       the least a knee wall can be; under that the height is one storey. */
    /* A ONE-STOREY PRODUCT: THE WALL HEIGHT STRETCHES THE WALL (core-v481).

       The slider held the ground floor and gave everything above it to a
       second storey, and from 100 mm up that storey was built: a floor zone,
       a sole plate and a top plate right round the building at the old wall
       head, the inside layers stopping there -- which is where the window
       that stood under the plate had its head. That is the masonry house's
       rule (a storey from two courses) and it had reached a product that has
       one storey. A product that says __PROD._oneStorey keeps one storey
       whatever the slider does: the studs, the inside layers and the facade
       just get taller (Marek: wall height nema pridavat dalsi vodorovny
       profil, maji se natahovat svisle profily i s vrstvami fasady). */
    function _oneStorey() {
      try { return !!(__PROD && __PROD._oneStorey); } catch (e) { return false; }
    }
    var _oneStoreyNote = '';
    function _foldOneStorey() {
      if (!_oneStorey()) return;
      STATE.clearH = STATE.clearH || [];
      var h0 = STATE.clearH[0] || 2.6, h1 = STATE.clearH[1] || 0, was = STATE.storeys | 0;
      if (was > 1 || h1 > 0) {
        /* a project saved with the split keeps the wall it showed: storey two
           was standing, so its height joins the wall; one that never split
           had a remainder nobody saw, and it goes */
        var tot = (was > 1) ? (h0 + h1) : h0;
        STATE.clearH[0] = Math.round(clamp(tot, CLEAR_LIM.min, CLEAR_LIM.max) * 10000) / 10000;
        STATE.clearH[1] = 0;
        STATE.storeys = 1;
        _oneStoreyNote = 'folded ' + was + ' storey(s) ' + Math.round(h0 * 1000) + ' + ' + Math.round(h1 * 1000) + ' into one wall of ' + Math.round(STATE.clearH[0] * 1000);
        try { savePref(); } catch (eSP) {}
      }
    }
    function _storeysFromHeights() {
      if (_oneStorey()) { _foldOneStorey(); return; }
      /* two courses of block make a storey (core-v415, Marek: storey 2 od 2
         rows bloku, muze byt i mala nadezdivka) */
      var h2 = (STATE.clearH && typeof STATE.clearH[1] === 'number') ? STATE.clearH[1] : 0;
      var st2 = _heightStep(), want = (h2 >= 2 * st2 - 0.001) ? 2 : 1;
      if ((STATE.storeys | 0) !== want) { STATE.storeys = want; _log('storeys -> ' + want + ' from the heights (storey 2 ' + Math.round(h2 * 1000) + ')'); }
    }
    function _heightStep() {
      var c = 0;
      try { if (typeof __PROD._courseH === 'function') c = +__PROD._courseH() || 0; } catch (e) { c = 0; }
      return (c > 0.02) ? c : 0.05;
    }
    function opEdit(on) {
      if (on === false) { _opSel = null; _opLast = null; _nearBinHide(); }
      /* EDIT OFF TAKES THE GHOSTS WITH IT (core-v409, Marek: kdyz odkliknu
         edit, maji zmizet overlay ghosts): the picked opening is let go, the
         fills and the corner dots leave with the mode. */
      if (on === false && _hh.on) { _hh.on = null; try { rebuild(); } catch (eG) {} }
      if (on === undefined) return _opEdit;
      var was = _opEdit;
      _opEdit = !!on;
      /* EDIT MODE DOES NOT TOUCH THE DRAWER.

         It used to dismiss it, which meant entering the mode took away the panel
         holding the controls for the mode. The drawer and the model are two
         halves of one screen and both stay live; the mode changes what a drag on
         the model does, and nothing else. */
      var w = API.wizard();
      if (w && w.refresh) w.refresh();
      _opBar();
      rebuild();
      _log('opening edit ' + (_opEdit ? 'ON -- drag one to move it, drop it on the bin to remove it'
                                      : 'off -- drawer back'));
      return _opEdit;
    }

    function armOpening(spec) {
      _armed = spec || null;
      if (_armed && _armed.remove) _log('armed: remove -- tap an opening to take it out');
      else if (_armed) _log('armed: ' + _armed.kind + ' ' + fmtLen(_armed.w) + ' x ' + fmtLen(_armed.h) +
                            ' -- tap a wall to place it');
      else _log('opening tool disarmed');
      return _armed;
    }
    function armedOpening() { return _armed; }

    /* The wall nearest a point in plan, and how far along it that point is. */
    /* WHICH WALLS TAKE A UNIT (core-v491, R1): with a kind, a segment the
       product turns down through __PROD._opHost(seg, kind) === false is not a
       place for it -- the long sides of an A-frame are roof, not wall. Without
       a kind, or without the hook, every segment counts, as before. */
    function nearestSegment(px, pz, kind) {
      var SEG = segments().concat(_prodSegs()), best = null, bestD = 1e9, i;
      var hostFn = (kind && typeof __PROD._opHost === 'function') ? __PROD._opHost : null;
      for (i = 0; i < SEG.length; i++) {
        var s = SEG[i];
        if (hostFn) { var okH = true; try { okH = (hostFn(s, kind) !== false); } catch (eH) { okH = true; } if (!okH) continue; }
        var vx = s.x1 - s.x0, vz = s.z1 - s.z0;
        var len2 = vx * vx + vz * vz;
        if (len2 < 1e-9) continue;
        var t = ((px - s.x0) * vx + (pz - s.z0) * vz) / len2;
        t = clamp(t, 0, 1);
        var cxp = s.x0 + vx * t, czp = s.z0 + vz * t;
        var d = Math.hypot(px - cxp, pz - czp);
        if (d < bestD) { bestD = d; best = { seg: s, at: t * s.len, dist: d }; }
      }
      return best;
    }

    function _initOpeningPick() {
      var canvas = _canvasEl();
      if (!canvas) { global.setTimeout(_initOpeningPick, 120); return; }
      /* HAND THE AIM HELPER UP NOW, NOT ON ITS FIRST USE.

         It was assigned inside _aim, so it only existed once something else had
         already aimed at something. Tap the very first dot on a fresh page and
         nothing happened -- the helper was still null and the tap was declined
         in silence. This runs at boot, which is when it should have been done
         (Marek: a hlavne nereaguji). */
      try { _aimRef = _aim; } catch (eAR) {}

      var drag = null;
      _UI().bindOpeningTap(canvas, {
        enabled: function () { return !_opEdit && !_armed && !_fitEdit && STATE.view !== '2d'; },
        pick: openingRayHit,
        open: function (rec) {
          if (STATE.openings.indexOf(rec) < 0) return;
          _sxEditOpen();
          _sxGoto('openings');
          _SX.sel = 'openings';
          _hh.on = null;
          _opLast = rec;
          _opSel = { seg: rec.seg, at: rec.at };
          opEdit(true);
          _opBarMode = 'exit';
          _opBinShow();
          _opSwingSync();
        }
      });

      /* --------------------------------------------------------------------
         THE BIN, AND IT ONLY EXISTS WHILE SOMETHING IS MOVING.

         Removing used to be a separate armed tool: pick the scissors, then tap
         the thing. That is two decisions for one act, and the tool stays armed
         afterwards waiting to eat the next tap. Dragging the window onto a bin
         is the act itself -- you already have hold of the thing you mean.

         It appears on the first movement rather than on the grab, because a tap
         that turns out not to be a drag should not flash a bin at you.
         -------------------------------------------------------------------- */
      /* the exit button becomes the bin while something is being dragged */
      var _fixDrag = null;
      function S_cutsOn() { return !!STATE.cutsOn; }
      _fitDiagRef = function () { return _fitDiag; };
      function binShow() { _opBarMode = 'bin'; _opBinShow(); }
      function binHide() { _opBarMode = 'exit'; _opBinHot(false); _opBinLinger(); }
      function binHot(on) { if (_opBarMode === 'bin') _opBinHot(!!on); }

      function overBin(e) {
        if (!_opBinEl || _opBinEl.style.display === 'none') return false;
        var r = _opBinEl.getBoundingClientRect();
        if (!(r.width > 0)) return false;
        return e.clientX >= r.left && e.clientX <= r.right &&
               e.clientY >= r.top && e.clientY <= r.bottom;
      }

      function planPoint(e) {
        var hits = _hitGroup(e.clientX, e.clientY);
        return (hits && hits.length) ? hits[0].point : _groundPoint(e.clientX, e.clientY);
      }

      /* MOVING ONE, WHICH IS THE COMMON CASE ONCE IT EXISTS.

         An opening is almost never right the first time -- it is placed, then
         nudged until it looks right from inside. Without this the only way to
         move a window was to delete it and place it again, which loses its size
         and is why nobody bothers.

         It slides along its own wall and nowhere else: dragging it round a corner
         would mean deciding what happens to the header at the corner post, and
         the answer is that it does not go there. */
      canvas.addEventListener('pointermove', function (e) {
        if (!drag) return;
        /* THE GROUND HIT IS NOT NEEDED ANY MORE, AND IT WAS KILLING THE DRAG.

           planPoint strikes the model, and where it misses it falls back to the
           GROUND plane -- so it answers null whenever the ray hits neither. Push
           a window UP and the finger leaves the building and goes into the sky
           within a few hundred millimetres: no model, no ground, null, and this
           handler returned before it did anything.

           That is why sliding a window ALONG its wall worked and raising it did
           not. The finger stays on the building going sideways and leaves it
           going up, and the guard was the first line of the function.

           The wall's own plane needs nothing under the pointer. The ground hit is
           asked for only where it is used, in the fallback. */
        var p = null;
        e.preventDefault();
        e.stopPropagation();
        if (!drag.moved && typeof drag.x0 === 'number' && Math.hypot(e.clientX - drag.x0, e.clientY - drag.y0) < 8) return;
        if (!drag.moved) { try { _nearBinHide('the unit dragged'); } catch (eNb) {} }
        drag.moved = true;

        /* THE BIN CANNOT BE ALLOWED TO TAKE THE DRAG DOWN WITH IT.

           binShow rewrites the button's innerHTML on every frame of the move. If
           the button is not there, or that throws for any reason, it throws HERE
           -- ahead of everything this handler exists to do -- and the window does
           not move a millimetre while nothing anywhere says why.

           Whether the finger is over the bin is a question that can fail safely:
           if it cannot be answered, the answer is no. */
        var onBin = false;
        try { binShow(); onBin = overBin(e); binHot(onBin); }
        catch (eB) { onBin = false; _sillNote = 'bin threw: ' + (eB && eB.message); }

        /* OVER THE BIN, NOTHING MOVES. Letting it keep sliding while it is being
           aimed at the bin means the drop lands somewhere it was never going, and
           if you change your mind the window is left wherever it drifted to. */
        if (onBin) return;

        /* A ROOF WINDOW MOVES IN ITS SLOPE (core-v491, R5): the product reads
           the finger's ray against the plane and keeps it between the rafters,
           __PROD._roofMove(rec, ray, drag) -- drag carries the grab offset the
           product may keep on it */
        if (drag.rec && drag.rec.host === 'roof') {
          if (typeof __PROD._roofMove === 'function') {
            var rcM = _aim(e.clientX, e.clientY);
            try { __PROD._roofMove(drag.rec, rcM ? rcM.ray : null, drag); }
            catch (eRM) { _sillNote = 'roof move threw: ' + ((eRM && eRM.message) || eRM); }
            _sillNote = 'roof window on plane ' + drag.rec.plane + ' at ' + Math.round((drag.rec.along || 0) * 1000) + ' along, ' + Math.round((drag.rec.up || 0) * 1000) + ' up';
          }
          _dragMove('roof window', 'roof');
          return;
        }

        /* ALONG THE WALL AND UP IT, FROM ONE HIT. The wall's own vertical plane
           answers both; the ground plane only ever answered one, which is why the
           sill could not be dragged at all.

           The grab offset is taken on the way down, so the window does not jump
           to the finger the moment it starts moving. */
        var sgD = segment(drag.rec.seg);
        /* ON THE PLAN, ALONG THE WALL UNDER THE FINGER (core-v283). The wall-plane
           hit is aimed through the 3D camera, which on the plan is not the camera
           the drawing is seen through -- so the finger's point came out anywhere
           and the unit would not follow (Marek: v pudorysu nejdou posouvat okna /
           dvere). On the plan the point is read off the ground through the plan's
           own camera, and the unit slides along its wall to it, kept on the wall
           by the same rules as in 3D. The sill is not a plan question. */
        if (drag.plan || _planWanted()) {
          var pG = _groundPoint(e.clientX, e.clientY);
          if (!pG) { _sillNote = 'plan drag: no ground point under the finger'; return; }
          if (!sgD || !(sgD.len > 0)) return;
          /* projected on its OWN wall, so near a corner it does not jump to the next one */
          var uxP = (sgD.x1 - sgD.x0) / sgD.len, uzP = (sgD.z1 - sgD.z0) / sgD.len;
          var atP = clamp((pG.x - sgD.x0) * uxP + (pG.z - sgD.z0) * uzP, 0, sgD.len);
          moveOpening(drag.rec, atP + (drag.du || 0));
          _sillNote = 'plan drag along ' + drag.rec.seg + ' to ' + drag.rec.at.toFixed(3);
          _dragMove('a window or door', 'opening');
          return;
        }
        var wh = openingHitOnWall(e, sgD);
        if (wh) {
          moveOpening(drag.rec, wh.along);
          /* a glazing drawn to the product's outline has no sill to drag
             (core-v503): its foot and its top are the gable's */
          if (_opOutlineOf(drag.rec)) {
            _sillNote = 'glazing on ' + drag.rec.seg + ' -- the product sets its shape, no sill';
          } else if (drag.dy != null && !isNaN(drag.dy)) {
            moveOpeningSill(drag.rec, wh.y - drag.dy);
            _sillDrag = { rec: drag.rec };
            _sillNote = 'hit y=' + wh.y.toFixed(3) + ' dy=' + drag.dy.toFixed(3) +
                        ' -> sill ' + (drag.rec.sill || 0).toFixed(3) +
                        ' | step ' + Math.round(sillStep() * 1000) + ' mm' +
                        (openingOnFloor(drag.rec) ? ' | DOOR, stays on the floor' : '');
          } else {
            _sillNote = 'no grab offset -- ' +
                        (drag.dy == null ? 'openingHitOnWall gave nothing on the way down'
                                         : 'sill was not a number');
          }
        } else {
          p = planPoint(e);
          if (!p) { _sillNote = 'no wall-plane hit and no ground hit -- nothing to read'; return; }
          var n = nearestSegment(p.x, p.z);
          if (!n || n.seg.id !== drag.rec.seg) return;
          moveOpening(drag.rec, n.at);
        }
        /* the plan is where it is being dragged now, so the plan has to keep up */
        _dragMove('a window or door', 'opening');
      }, true);

      function endDrag(e) {
        if (!drag) return;
        var st = _stage();
        /* BOTH SETS OF CONTROLS, because the grab can start in either view now:
           the orbit in 3D, the ortho pan on the plan. Re-enabling only the one
           that was switched off left the other dead for the rest of the session. */
        if (st && st.controls) st.controls.enabled = true;
        if (st && st.orthoControls) st.orthoControls.enabled = true;

        var binned = !!(e && e.type === 'pointerup' && drag.moved && overBin(e));
        binHide();
        /* A tap keeps the selected unit's bottom action visible. */
        if (!binned && !drag.moved && drag.rec && drag.P && e && e.type === 'pointerup') {
          _opBinShow();
        }

        if (binned) {
          undoPush();
          _log('removed ' + drag.rec.kind + ' from ' + drag.rec.seg + ' -- dropped on the bin');
          _opLast = null;
          _opSel = null;
          removeOpening(drag.rec);
          undoPush();
          if (_opBinEl) _opBinEl.style.display = 'none';
        } else if (drag.moved && drag.rec.host === 'roof') {
          savePref();
          _say('opening  roof window moved to ' + Math.round((drag.rec.along || 0) * 1000) + ' along, ' + Math.round((drag.rec.up || 0) * 1000) + ' up plane ' + drag.rec.plane);
        } else if (drag.moved) {
          savePref();
          _log('opening moved to ' + fmtLen(drag.rec.at) + ' along ' + drag.rec.seg);
        }
        if (!binned && _opLast) _opSel = { seg: _opLast.seg, at: _opLast.at };
        drag = null;
        _sillDrag = null;
        _dragStop('the unit let go');
        rebuild();
        if (_planWanted()) drawPlan();
      }
      /* the partition follows the pointer, and the button at the bottom becomes
         the bin exactly as it does for an opening */
      canvas.addEventListener('pointermove', function (e) {
        if (!_partDrag) return;
        var pp = planPoint(e);
        var PL2 = _P();
        if (!pp) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        /* A TAP IS NOT A DRAG (core-v537). Every pointermove used to count as
           a drag, and a finger never lands without trembling, so there was no
           such thing as a tap on a partition. Under 8 px it is still a tap --
           and a tap now does something (see endPartDrag). */
        if (!_partDrag.moved && typeof _partDrag.x0 === 'number' &&
            Math.hypot(e.clientX - _partDrag.x0, e.clientY - _partDrag.y0) < 8) return;
        _partDrag.moved = true;
        binShow();
        binHot(overBin(e));
        if (overBin(e)) return;
        var pr = (STATE.parts || [])[_partDrag.i];
        if (!pr) return;
        var want = ((pr.o || pr.dir) === 'v') ? pp.x : pp.z;
        var was = pr.c;
        /* the asset rebuilds after its move -- on the plan a repaint while
           the drag is on (core-v617) */
        if (_planWanted() && !_dragOff()) _dragBegin('a partition');
        _ask(PL2, 'movePartTo', _partDrag.i, want);
        _normParts();
        /* IT EITHER MOVED OR IT DID NOT, and that is a fact this can check
           rather than assume. Where the asset declined, the partition is this
           file's own and moves here. */
        pr = (STATE.parts || [])[_partDrag.i];
        if (pr && Math.abs(pr.c - was) < 1e-6) { _movePartLocal(_partDrag.i, want); _normParts(); }
        /* AND THE NAMES TRAVEL WITH THE ROOMS. A pin is a fixed point, so a wall
           dragged PAST one hands that room's name to the room on the other side.
           Small moves never reach a pin, which is why this only showed on the big
           ones. The wall pushes them ahead of it, the same rule as for a door. */
        if (pr) _pushRoomPins(pr.o || pr.dir, was, pr.c);
        _dragMove('a partition', null);
        if (!_planWanted()) drawPlan();
      }, true);

      function endPartDrag(e) {
        if (!_partDrag) return;
        var st1 = _stage();
        if (st1 && st1.orthoControls) st1.orthoControls.enabled = true;
        var binned = !!(e && _partDrag.moved && overBin(e));
        binHide();
        var PL3 = _P();
        if (binned) {
          /* A LOAD-BEARING WALL BINNED ON A STOREY ABOVE ITS OWN IS CUT AWAY
             THERE ONLY (core-v403, Marek: pokud ji odstranim cut ve vyssich
             patrech, zmizi jen v danem patru, v patrech pod zustava). It is
             the same wall on every storey it stands on; on the storey it was
             drawn on the bin takes it entirely, on any storey above it the
             storey is added to its cut list and the wall stays below. */
          var pv = _normParts()[_partDrag.i], sv2 = STATE.planStorey | 0;
          if (pv && pv.bear && (pv.st | 0) < sv2) {
            pv.cutOn = (pv.cutOn || []).slice();
            if (pv.cutOn.indexOf(sv2) < 0) pv.cutOn.push(sv2);
            _log('partition load-bearing #' + pv.id + ' cut away on storey ' + (sv2 + 1) + ' only -- it still stands on storey ' + ((pv.st | 0) + 1) + (sv2 - (pv.st | 0) > 1 ? ' and between' : ''));
            _partDrag = null;
            savePref(); rebuild(); drawPlan();
            return;
          }
          /* the asset's own cut knows what a partition meeting another one
             means; where it will not take it, the partition goes, because a bin
             that does nothing is worse than no bin */
          var before = (STATE.parts || []).length;
          _ask(PL3, 'cutPartSeg', _partDrag.i);
          _normParts();
          if ((STATE.parts || []).length === before) {
            _cutPartLocal(_partDrag.i);
            _log('partition removed here -- the asset would not cut it');
          } else {
            _log('partition cut -- dropped on the bin');
          }
          savePref(); rebuild(); drawPlan();
        } else if (_partDrag.moved) {
          if (_dragStop('the partition let go')) rebuild();
          savePref();
          drawPlan();
          _log('partition moved');
        } else if (e && e.type === 'pointerup' && editing('layout') && (Date.now() - (_partDrag.t0 || 0)) < 700) {
          /* WALL OR ROOM LINE (core-v537, Marek: delici cara -- ano). A tap on a
             partition that did not move it turns it into a room line -- two
             rooms with no wall between them, nothing built or billed -- and a
             tap on a room line makes it a wall again. The asset does the work
             (plan.toggleSep, floorplan-lib v105 and later) and says so. */
          var tgR;
          try { tgR = (PL3 && PL3.toggleSep) ? PL3.toggleSep(_partDrag.i) : undefined; }
          catch (eTg) { tgR = undefined; _log('plan toggleSep(): ' + (eTg && eTg.message)); }
          if (tgR === undefined) _log('partition tap: this floorplan asset has no room line yet (it needs v105 or later)');
          else if (tgR === null) _log('partition tap: nothing to turn at index ' + _partDrag.i);
          else if (tgR === false && (_normParts()[_partDrag.i] || {}).bear) _log('partition tap: a load-bearing wall stays a wall');
          else {
            _normParts();
            savePref(); rebuild(); drawPlan();
            _log('partition tapped -> ' + (tgR ? 'ROOM LINE (no wall, nothing built or billed)' : 'WALL'));
          }
        }
        _partDrag = null;
        try { var stPe = _stage(); if (stPe && stPe.orthoControls) stPe.orthoControls.enabled = true; } catch (ePe) {}
      }
      /* SLIDING A DOOR ALONG ITS WALL. moveDoorTo is the asset's; it keeps the
         door inside the free stretch it is in and will not push it through its
         neighbours. Copied from tiny, including the rule that it is still a tap
         until the finger has actually travelled. */
      canvas.addEventListener('pointermove', function (e) {
        if (!_doorDrag) return;
        var pm = planPoint(e), PLm = _P();
        if (!pm || !PLm || !PLm.moveDoorTo) return;
        e.preventDefault(); e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        if (!_farEnough(e)) return;
        _doorDrag.moved = true;
        try { PLm.moveDoorTo(_doorDrag.d, pm.x, pm.z); }
        catch (eM) { _log('plan moveDoorTo(): ' + (eM && eM.message)); }
        _normDoors();
        _dragMove('a door', null);
        if (!_planWanted()) drawPlan();
      }, true);

      function endDoorDrag() {
        if (!_doorDrag) return;
        var PLu = _P();
        if (!_doorDrag.moved) {
          /* a door tapped and not dragged walks its widths and swings, and out */
          try { if (PLu && PLu.stepDoor) PLu.stepDoor(_doorDrag.d); }
          catch (eS2) { _log('plan stepDoor(): ' + (eS2 && eS2.message)); }
          _log('door cycled');
        } else {
          _log('door moved');
        }
        _doorDrag = null;
        _dragFromSet(null);
        _normDoors();
        _dragStop('the door let go');
        savePref(); rebuild(); drawPlan();
      }
      canvas.addEventListener('pointerup', endDoorDrag, true);
      canvas.addEventListener('pointercancel', endDoorDrag, true);

      /* dragging one end of a doorway: the end you hold moves, the other stays */
      canvas.addEventListener('pointermove', function (e) {
        if (!_doorEnd) return;
        var pd = planPoint(e); var PLd = _P();
        if (!pd || !PLd || !PLd.resizeDoorTo) return;
        e.preventDefault(); e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        _doorEnd.moved = true;
        if (_planWanted() && !_dragOff()) _dragBegin('a doorway end');
        try { PLd.resizeDoorTo(_doorEnd.g, pd.x, pd.z, true); } catch (eR) {}
        _dragMove('a doorway end', null);
        if (!_planWanted()) drawPlan();
      }, true);
      function endDoorEnd() {
        if (!_doorEnd) return;
        if (_doorEnd.moved) { _dragStop('the doorway end let go'); _normDoors(); savePref(); rebuild(); drawPlan(); _log('doorway resized'); }
        _doorEnd = null;
      }
      canvas.addEventListener('pointerup', endDoorEnd, true);
      canvas.addEventListener('pointercancel', endDoorEnd, true);

      /* THE IMAGE FOLLOWS THE FINGER, and a corner resizes it about its own
         centre -- which is what scaleTo does, so nothing is worked out twice. */
      canvas.addEventListener('pointermove', function (e) {
        if (!_traceDrag) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        var TR = _trace();
        var tp = planPoint(e);
        if (!TR || !tp) return;
        if (_traceDrag.mode === 'move') TR.moveTo(tp.x, tp.z);
        else if (_traceDrag.mode === 'scale') TR.scaleTo(tp.x, tp.z);
        _traceDrag.moved = true;
        _traceRepaint();
      }, true);

      canvas.addEventListener('pointerup', function (e) {
        if (!_traceDrag) return;
        if (e) { e.preventDefault(); e.stopPropagation(); }
        try { if (e) canvas.releasePointerCapture(e.pointerId); } catch (eR) {}
        STATE.mapLocked = !!_traceDrag.wasLocked;
        var st2 = _stage();
        if (st2 && st2.orthoControls) st2.orthoControls.enabled = true;
        _log('trace ' + _traceDrag.mode + (_traceDrag.moved ? ' done' : ' -- no move'));
        _traceDrag = null;
        _traceRepaint();
        _traceIcon();
      }, true);

      canvas.addEventListener('pointerup', endPartDrag, true);
      canvas.addEventListener('pointercancel', function () { binHide(); endPartDrag(null); }, true);

      /* the fixture drag, in the same shape the partition drag has */
      canvas.addEventListener('pointermove', function (e) {
        if (!_fixDrag || e.pointerId !== _fixDrag.id) return;
        e.preventDefault(); e.stopPropagation();
        if (Math.hypot(e.clientX - _fixDrag.x0, e.clientY - _fixDrag.y0) > 6) _fixDrag.moved = true;
        if (!_fixDrag.moved) return;
        var pm = planPoint(e), PLm = _P();
        if (!pm || !PLm || !PLm.fixtures) return;
        _fixDrag.last = { x: pm.x + _fixDrag.dx, z: pm.z + _fixDrag.dz };
        if (PLm.fixtures.dragTo) PLm.fixtures.dragTo(_fixDrag.f, _fixDrag.last.x, _fixDrag.last.z);
        else PLm.fixtures.moveTo(_fixDrag.f, _fixDrag.last.x, _fixDrag.last.z);
        _opBinHot(overBin(e));
        drawPlan();
      }, true);
      function endFixDrag(e) {
        if (!_fixDrag || (e && e.pointerId !== _fixDrag.id)) return;
        var PLe = _P(), binned = !!(e && _fixDrag.moved && overBin(e));
        binHide();
        try { if (e) canvas.releasePointerCapture(e.pointerId); } catch (eR) {}
        var stE = _stage(); if (stE && stE.orthoControls) stE.orthoControls.enabled = true;
        if (PLe && PLe.fixtures) {
          if (binned) { PLe.fixtures.remove(_fixDrag.f); _log('fitout removed: ' + _fixDrag.f.kind); }
          else if (!_fixDrag.moved) { _log('fitout in hand: ' + _fixDrag.f.kind + ' -- rotate, mirror and the bin act on it'); }
          else {
            if (PLe.fixtures.endDrag && _fixDrag.last) PLe.fixtures.endDrag(_fixDrag.f, _fixDrag.last.x, _fixDrag.last.z);
            _sugOwn(_fixDrag.f, 'moved');
            _fitDiag = _fixDrag.f.kind + ' finger ' + (_fixDrag.last ? (_fixDrag.last.x.toFixed(2) + ',' + _fixDrag.last.z.toFixed(2)) : '?') +
                       ' -> dropped ' + _fixDrag.f.x.toFixed(2) + ',' + _fixDrag.f.z.toFixed(2);
            _log('fitout moved: ' + _fitDiag);
          }
        }
        var fDone = _fixDrag ? _fixDrag.f : null;
        _fixDrag = null;
        savePref(); rebuild(); drawPlan();
        if (fDone && _fitDiag) { _fitDiag += ' -> after rebuild ' + fDone.x.toFixed(2) + ',' + fDone.z.toFixed(2); }
      }
      canvas.addEventListener('pointerup', endFixDrag, true);
      canvas.addEventListener('pointercancel', function () { binHide(); endFixDrag(null); }, true);

      /* THE NAME WALKS ONLY IF NOTHING ELSE WANTED THE GESTURE. Any drag that
         started clears the tap, so grabbing the outline, a partition, a door or
         an opening cannot rename anything -- and neither can a finger that
         travelled, which is a pan and not a tap. */
      function endRoomTap(e) {
        var rt = _roomTap;
        _roomTap = null;
        if (!rt) return;
        /* the drag enders are bound before this one and have already cleared
           their own state by now, so asking them is worthless -- what can be
           asked is whether the finger travelled, and whether it landed on the
           shell, which is where the outline's handles are */
        if (_farEnough(e)) { _planTaps.none++; _planTaps.last += ' -> travelled, no rename'; return; }
        if (_nearShell(rt.x, rt.z)) { _planTaps.none++; _planTaps.last += ' -> on the shell, no rename'; return; }
        var PLr = _P();
        /* THE NAME, NOT THE ROOM: only a tap on the name itself renames, and
           never while the fitouts are being edited (Marek) */
        if (_fitEdit) { _planTaps.none++; _planTaps.last += ' -> fitout mode, no rename'; return; }
        /* THE PLUS COMES ON THIS TAP (core-v539, Marek: klepnutim do mistnosti
           se neobjevuji plus). core-v538 listened for the tap on its own, and
           this handler swallows every tap on the plan before anyone else hears
           it -- so the plus never came. It is asked here, where the tap is
           already known to be a tap on a room and not on its name. */
        /* core-v541 (Marek: plus automaticky pri zoom in, kliknuti do
           mistnosti si nechame na jinou funkci): the plus comes only with the
           zoom now, and a tap in a room off its name does nothing yet */
        if (PLr && PLr.roomNameHit && !PLr.roomNameHit(rt.x, rt.z)) {
          _planTaps.none++; _planTaps.last += ' -> not on the name, no rename';
          return;
        }
        if (PLr && PLr.cycleRoomNameAt) {
          _planTaps.room++;
          _planTaps.last += ' -> room name';
          _ask(PLr, 'cycleRoomNameAt', rt.x, rt.z);
          /* THE NAME CHANGES AT ONCE, THE MODEL LATER (core-v476, Marek: nejde
             plynule preklikavat mistnosti, jakmile je jich vic, kouslo se to):
             every tap rebuilt the whole house -- 1.7 s on the T house -- before
             the plan was repainted. The plan is repainted now; the model is
             rebuilt once, 1.2 s after the last tap */
          savePref(); drawPlan(); _rebuildSoon(1200, 'room names');
        } else {
          _planTaps.none++;
          _planTaps.last += ' -> nothing under it';
        }
      }
      canvas.addEventListener('pointerup', endRoomTap, true);
      canvas.addEventListener('pointercancel', function () { _roomTap = null; }, true);

      /* TWO FINGERS ARE THE VIEW'S, WHATEVER THE FIRST ONE TOUCHED (core-v540,
         Marek: dva prsty jsou jen zoom a posouvani; pri zoomu dvema prsty se
         objevuje spodni UI se zrcadlenim a otacenim). The first finger lands on
         a piece, a wall, a door or a room before the second one arrives, and
         each of them took it: a piece switched the fitout mode on and brought
         its bar up, a wall got ready to move. When the second finger comes
         down all of that is let go -- whatever has not travelled yet -- and the
         fitout mode goes off again if this very finger switched it on. Then the
         two fingers zoom and slide the plan. Something already being dragged
         stays in hand, and the second finger leaves the view alone. */
      _planTakeover = function () {
        var said = [];
        if (_fixDrag && !_fixDrag.moved) { _fixDrag = null; try { binHide(); } catch (eB) {} said.push('piece let go'); }
        if (_fitEditBy !== null) {
          _fitEditBy = null;
          if (_fitEdit && !_fixDrag) { fitEdit(false, 'two fingers'); said.push('fitout mode off again'); }
        }
        if (_partDrag && !_partDrag.moved) { _partDrag = null; said.push('wall let go'); }
        if (_doorDrag && !_doorDrag.moved) { _doorDrag = null; try { _dragFromSet(null); } catch (eD) {} said.push('door let go'); }
        if (_doorEnd && !_doorEnd.moved) { _doorEnd = null; said.push('doorway end let go'); }
        if (_traceDrag) { STATE.mapLocked = !!_traceDrag.wasLocked; _traceDrag = null; said.push('picture let go'); }
        if (_roomTap) { _roomTap = null; said.push('room tap dropped'); }
        if (drag && !drag.moved) { drag = null; said.push('opening let go'); }
        var busy = !!(_fixDrag || _partDrag || _doorDrag || _doorEnd || drag);
        return { said: said.length ? said.join(', ') : 'nothing in hand', busy: busy };
      };

      canvas.addEventListener('pointerup', endDrag, true);
      canvas.addEventListener('pointercancel', function () { binHide(); endDrag(null); }, true);

      /* OBJECTS TAKEN BY THEMSELVES (core-v288). A bathtub, the sauna's heater,
         the pool and the terrace are moved by putting a finger on the thing
         itself and dragging -- no handle over them -- in 3D and on the plan,
         outside edit mode as much as in it. The house is not one of them: it is
         edited in its own edit mode, and a tap on it still turns the view.
         In 3D the nearest thing under the finger decides; on the plan the
         point on the ground does (the heater's square, the tub, inside the pool
         or the terrace). A tap that does not move turns a piece a quarter. While
         the pool or the terrace moves, its drawing is carried along and the
         model is rebuilt once, when it is let go. In the Exterior step the pool
         and the terrace keep their edge handles, which do this there. */
      canvas.addEventListener('pointerdown', function (e) {
        if (e && e.button != null && e.button !== 0) return;
        var took = false;
        try { took = _objGrab(e); } catch (eOG) { _log('object grab: ' + (eOG && eOG.message)); }
        if (took) {
          e.preventDefault(); e.stopPropagation();
          if (e.stopImmediatePropagation) e.stopImmediatePropagation();
          try { canvas.setPointerCapture(e.pointerId); } catch (eCp) {}
        }
      }, true);
      canvas.addEventListener('pointermove', function (e) {
        if (_objPending && e.pointerId === _objPending.id && Math.abs(e.clientX - _objPending.sx) + Math.abs(e.clientY - _objPending.sy) > 8) _objPending = null;
        if (!_objDrag || (_objDrag.id != null && e.pointerId !== _objDrag.id)) return;
        e.preventDefault();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        try { _objMove(e); } catch (eOM) { _log('object move: ' + (eOM && eOM.message)); }
      }, true);
      canvas.addEventListener('pointerup', function (e) {
        if (_objPending && e.pointerId === _objPending.id) _objPending = null;
        if (!_objDrag) return;
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        try { _objEnd(e); } catch (eOE) { _log('object end: ' + (eOE && eOE.message)); _objDrag = null; _objControls(true); }
      }, true);
      canvas.addEventListener('pointercancel', function (e) {
        _objPending = null;
        if (!_objDrag) return;
        try { _objEnd(e); } catch (eOC) { _objDrag = null; _objControls(true); }
      }, true);

      /* THE PLUS ON THE FLOOR GETS THE FIRST LOOK.

         The discs are small, they sit on the ground where nothing else is
         pickable, and they only exist while the Exterior step is open -- so
         asking them first costs one raycast on a step where little else is
         being picked, and asking them last would mean the ground or a terrace
         board took the tap instead (Marek: nevidim plus kterym bych pridal). */
      canvas.addEventListener('pointerdown', function (e) {
        if (STATE.view === '2d') return;
        if (e && e.button != null && e.button !== 0) return;
        var took = false;
        try { took = postWallTap(e.clientX, e.clientY); } catch (ePW) {}
        if (took) { e.preventDefault(); e.stopImmediatePropagation(); }
      }, true);

      canvas.addEventListener('pointerdown', function (e) {
        /* THE PLAN COMES FIRST WHEN THE PLAN IS WHAT YOU ARE LOOKING AT.

           In 2D on the FLOORPLAN step a drag is about a partition, and the
           openings handler below would otherwise take the tap first. The two
           never overlap -- one is the plan step, the other the openings step --
           so the order is a statement of which surface owns the pointer rather
           than a race. */
        /* THE POINTER COMES BACK HERE, because the old module no longer holds
           it. This block was written, then dead-ended behind if (false) with the
           note that the module owned the plan -- which was true while the module
           drew it. The asset draws it now, and the asset does not bind anything:
           it answers questions about a point. So the product turns a tap into a
           point and asks. */
        /* THE PADLOCK (core-v591, the icon strip): a locked plan is looked
           at -- pinch and pan go to the camera, nothing on the plan answers */
        if (_planWanted() && _stripPlanLocked()) { _planTaps.taps++; _planTaps.last = 'the plan is locked -- the padlock in the bar opens it'; }
        else if (_planWanted()) {
          /* THE TRACE OWNS THE POINTER WHILE IT IS ARMED, AND WE TOOK IT.

             Uploading a plan to trace turns plan editing OFF -- that is the
             point of it -- and the image is then dragged and scaled by the
             ASSET's own handlers, which sit on this same canvas element.

             This block calls stopImmediatePropagation, and the comment three
             lines down says why: to stop the map's pan and the opening grab,
             which are listeners on this very element. The trace handler is a
             listener on this very element too. So it died with them, and the
             image went stiff: upload worked, opacity worked, nothing moved.
             That is the regression, and it arrived the day this block was
             un-dead-ended rather than in anything about the trace.

             Armed means the image is the subject. We hand the pointer over and
             touch nothing -- no preventDefault, no stopping -- so the asset gets
             the event it has always been listening for. */
          /* AND NOBODY WAS LISTENING ON THE OTHER SIDE.

             Last version handed the pointer to the asset. The asset does not
             want it: TRACE is written the way partAt and movePartTo are -- it
             answers a question about a point and exposes the verbs, and the
             PRODUCT drives them. Its own comment says so: the object is here and
             exported and nothing is wired to it yet.

             So the image had handles drawn on it and not one listener behind
             them. Upload worked because that is a file input, opacity worked
             because that slider is the asset's own DOM. Everything that needed a
             gesture -- move, scale, turn, bin, lock -- was never connected in
             this product at all, and handing the pointer over made it worse:
             before, the tap at least did something to the plan.

             Wired here, in the same shape the partitions use. */
          /* COPIED FROM TINY, WHERE IT WORKS, INSTEAD OF WRITTEN AGAIN.

             I wired the verbs last version and it still did nothing, and the
             reason is everything AROUND the verbs. Tiny does five things here
             that I did not:

               the whole gesture is claimed at the TOP, before the hit test, and
               a MISS returns without falling through -- armed means the picture
               is the only thing on the plan that answers;

               the pointer is CAPTURED, so a finger that leaves the picture keeps
               sending its moves to this element instead of to whatever it slid
               over;

               the map is locked, or a drag pans the world under the picture;

               the controls are turned off for the duration;

               and the plan is repainted from the move, not from a rebuild.

             Written out again from tiny's own block rather than adapted, which
             is what I should have done the first time (Marek). */
          var TR = _trace();
          if (TR && TR.armed && TR.has()) {
            e.preventDefault();
            e.stopPropagation();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();

            var tp = planPoint(e);
            var hit = tp ? TR.at(tp.x, tp.z) : null;
            _planTaps.taps++;
            _planTaps.last = 'trace armed -> ' + (hit || 'miss') +
                             (tp ? (' at ' + tp.x.toFixed(2) + ',' + tp.z.toFixed(2)) : ' NO POINT');
            _log('trace down: ' + _planTaps.last);
            if (!hit) return;

            /* A QUARTER TURN AND A BIN ARE 2D EVENTS, so they repaint the
               overlay and nothing else. They were going through drawPlan and a
               full rebuild -- the whole building, every layer, for a picture
               that is not in the 3D model at all. That is the delay he can feel
               on these two and not on the drag. */
            if (hit === 'freeze') { _traceOff('locked from its own handle'); return; }
            if (hit === 'rot90')  { TR.turn90(); _traceRepaint(); _traceIcon(); return; }
            if (hit === 'bin') {
              TR.clear();
              _traceDrag = null;
              _log('trace binned');
              _traceRepaint();
              _traceIcon();
              return;
            }

            if (hit === 'move') TR.grab(tp.x, tp.z);
            try { canvas.setPointerCapture(e.pointerId); } catch (eTC) {}
            var wasL = !!STATE.mapLocked;
            STATE.mapLocked = true;
            var st0 = _stage();
            if (st0 && st0.orthoControls) st0.orthoControls.enabled = false;
            _traceDrag = { mode: hit, wasLocked: wasL };
            return;
          }

          var pp = planPoint(e);
          var PL = _P();
          _planTaps.taps++;
          _planTaps.last = pp ? (pp.x.toFixed(2) + ',' + pp.z.toFixed(2)) : 'NO POINT';
          if (!PL) _planTaps.last += ' (no asset)';
          /* A FITOUT PIECE UNDER THE FINGER IS GRABBED FIRST. Drag moves it
             (snapped to 50 mm), a tap with no movement turns it a quarter, a
             drop on the bin removes it. Only on the plan step, only with no
             tool armed, so a door tap is still a door tap. */
          /* a tap on a piece outside the mode turns the mode on and takes it in
             hand (Marek), with no other tool armed */
          if (pp && PL && _fitPlanOn() && !_fitEdit && !STATE.doorTool && !S_cutsOn() && PL.fixtures) {
            if (_ask(PL, 'fixtureAt', pp.x, pp.z)) { fitEdit(true); _fitEditBy = e.pointerId; }
          }
          if (pp && PL && _fitPlanOn() && _fitEdit && PL.fixtures) {
            var fxHit = _ask(PL, 'fixtureAt', pp.x, pp.z);
            if (fxHit) {
              /* a piece: the gesture is ours -- the camera does not get it */
              e.preventDefault(); e.stopPropagation();
              if (e.stopImmediatePropagation) e.stopImmediatePropagation();
              try { canvas.setPointerCapture(e.pointerId); } catch (eCap) {}
              var stF = _stage(); if (stF && stF.orthoControls) stF.orthoControls.enabled = false;
              _fixDrag = { f: fxHit, id: e.pointerId, x0: e.clientX, y0: e.clientY, moved: false,
                           dx: fxHit.x - pp.x, dz: fxHit.z - pp.z, last: null };
              _fitLast = fxHit;
              if (PL.fixtures.beginDrag) PL.fixtures.beginDrag(fxHit);
              /* THE BIN COMES UP AT ONCE, the way the openings step shows it:
                 drop the piece on it, or tap it, and the piece is gone */
              binShow();
              _opBinHot(false);
              _planTaps.last += ' -> fixture ' + fxHit.kind;
            } else {
              /* A PARTITION UNDER THE FINGER IS STILL A PARTITION, fitout mode or
                 not -- the mode comes on by a tap on a piece and stays, and it
                 was swallowing every drag of a wall (Marek). A miss that lands
                 on a partition falls through to the partition handler below;
                 the floor remembers the spot for the next piece. */
              var grabF = 0.17;
              try { var mF = OV.map ? OV.map(_stage()) : null; if (mF && mF.ppm > 0) grabF = Math.max(0.17, 28 / mF.ppm); } catch (eGF) {}
              var pOn = _ask(PL, 'partAt', pp.x, pp.z, grabF);
              if (pOn !== null && pOn >= 0) {
                _planTaps.last += ' -> fitout mode, but a partition is under the finger: to the partition';
              } else {
                _fitAt = { x: pp.x, z: pp.z };
                _planTaps.last += ' -> fitout mode, next piece at ' + pp.x.toFixed(2) + ',' + pp.z.toFixed(2);
                return;
              }
            }
            if (!_fixDrag) { /* fell through: the partition takes it */ } else return;
          }
          if (pp && PL) {
            /* AND NOTHING ELSE ON THIS CANVAS GETS THE SAME TAP. stopPropagation
               stops it climbing; it does not stop the other listeners sitting on
               this very element, which is where the map's pan and the opening
               grab both live. */
            e.preventDefault();
            e.stopPropagation();
            if (e.stopImmediatePropagation) e.stopImmediatePropagation();

            /* THE DOOR TOOL OWNS THE DOORS -- ALL OF IT, AND ONLY IT.

               It used to mean one thing only: this tap ADDS a door. So with the
               tool up every tap on a wall made another door, including a tap on
               a door that was already there, and the cycling and sliding lived
               on the OTHER side of the switch, where the tool was down. Backwards
               in both directions.

               With the tool up: a doorway's grip resizes, a door that is already
               there is taken hold of -- tap cycles it, drag slides it -- and only
               a tap with NO door under it puts a new one in. That last order is
               what stops a second door landing on top of the first.

               With the tool down, a door cannot be touched at all: the tap falls
               through to the wall and the room name. */
            /* AND ON THE OPENINGS STEP THE PLAN IS A PLAN OF THE OPENINGS.

               v233 drew the plan on that step so a window could be moved there,
               and brought the whole floorplan toolkit with it: partition grips,
               the scissors, the door tool and room naming, on a step that is
               about the shell. The drawing stays; the tools belonging to the
               other step stand down, and the only thing the finger can take hold
               of here is an opening. */
            if (STATE.doorTool && editing('layout')) {
              var geT = _ask(PL, 'doorEndAt', pp.x, pp.z);
              if (geT !== null) {
                _planTaps.end++;
                _planTaps.last += ' -> doorway end';
                _doorEnd = { g: geT, moved: false };
                try { canvas.setPointerCapture(e.pointerId); } catch (eET) {}
                return;
              }

              var dT = _ask(PL, 'doorAt', pp.x, pp.z);
              if (dT) {
                _planTaps.door++;
                _planTaps.last += ' -> door held';
                _doorDrag = { d: dT, moved: false };
                _dragFromSet(e);
                try { canvas.setPointerCapture(e.pointerId); } catch (eDT) {}
                return;
              }

              var added = PL.addDoorAt ? PL.addDoorAt(pp.x, pp.z) : null;
              _planTaps.door++;
              _planTaps.last += added ? ' -> door placed' : ' -> nothing to put a door in';
              _normDoors();
              savePref(); rebuild(); drawPlan();
              _log(added ? 'door placed' : 'door: nothing to put it in there');
              return;
            }

            /* armed: place one, and that is this product's job */
            if (_armedPart && editing('layout')) {
              placePartition(_armedPart, pp.x, pp.z);
              _armedPart = null;
              var wA = API.wizard();
              if (wA && wA.refresh) wA.refresh();
              return;
            }

            /* EVERY ONE OF THESE IS ASKED BEHIND ITS OWN GUARD, and that is the
               whole of this version.

               The count said it: three taps arrived, the point resolved, and not
               one outcome was recorded -- not a partition, not a door, not even
               "nothing under it". There is no path through this block that
               reaches the end without recording something, so the block was not
               reaching the end: one of these calls was THROWING, and it took the
               pointerdown handler down with it. Nothing dragged, nothing cut,
               nothing was named, and the console it complained to is not
               something a tablet hands back.

               Asked separately, one bad answer costs only its own question. And
               whichever it is says so by name in the snapshot. */

            /* THE PARTITION. -1 IS A MISS, NOT AN ANSWER. partAt returns the
               index or -1, and -1 is neither null nor false, so _ask handed it
               back as a hit and the drag started on wall number minus one. That
               is the one partition that moved and then nothing ever again: it
               worked only when the first probe happened to find something. */
            /* A PARTITION IS TAKEN AS EASILY AS A PIECE: the grab is a finger's
               reach at the plan's zoom (28 px), not a fixed 170 mm (Marek) */
            var grabP = 0.17;
            try { var mG = OV.map ? OV.map(_stage()) : null; if (mG && mG.ppm > 0) grabP = Math.max(0.17, 28 / mG.ppm); } catch (eG) {}
            /* THE PARTITIONS ARE THE PLAN STRIP'S AGAIN (core-v593, Marek: dej
               tam cele menu pro editaci pudorysu) -- behind its padlock, which
               stops this whole block while locked */
            var partsLive = editing('layout');
            var pi = partsLive ? _ask(PL, 'partAt', pp.x, pp.z, grabP) : null;
            var byUs = false;
            if (partsLive && (pi === null || pi < 0)) {
              var li = _partAtLocal(pp.x, pp.z);
              pi = (li >= 0) ? li : null;
              byUs = (li >= 0);
            }
            if (pi !== null && pi >= 0) {
              _planTaps.part++;
              _planTaps.last += ' -> partition ' + pi + (byUs ? ' (found here, not by the asset)' : '');
              _partDrag = { i: pi, moved: false, x0: e.clientX, y0: e.clientY, t0: Date.now() };
              /* the finger is the partition's: the camera does not pan under it */
              e.preventDefault(); e.stopPropagation();
              if (e.stopImmediatePropagation) e.stopImmediatePropagation();
              try { var stPd = _stage(); if (stPd && stPd.orthoControls) stPd.orthoControls.enabled = false; } catch (ePd) {}
              try { canvas.setPointerCapture(e.pointerId); } catch (eC) {}
              return;
            }

            /* A ROOM IS NAMED ON THE WAY UP, NOT ON THE WAY DOWN.

               The outline's own handles live on this same canvas, and an exterior
               wall is not a partition -- so grabbing one fell straight through to
               here and renamed the room under the finger before the drag had even
               started. A partition missed by a hair did the same. Nine stored
               names for two rooms is what that leaves behind.

               Nothing is decided here now: the point is remembered, and the name
               walks on pointerup only if no drag claimed the gesture in between
               and the finger never travelled. The tap is not swallowed either --
               it is left to fall through to whatever else wants it. */
            /* AN OPENING IN THE SHELL, WHICH THE PLAN COULD NOT TOUCH AT ALL.

               Grabbing a window or a door in an exterior wall lives further down
               this handler, past the point where the plan claims the tap -- so in
               2D every path returned before reaching it and nothing on the shell
               could be moved. In 3D it always worked, which is why it read as a
               plan problem rather than a missing branch.

               Same grab, same bin, same rule that it only takes over within a
               hand's width of an opening. It does NOT need edit mode here: on the
               plan there is nothing else within that reach to compete with it. */
            /* A FINGER IS A FINGER, NOT SIX HUNDRED MILLIMETRES.

               v223 asked for an opening within a fixed 0.6 m of the tap. On a
               plan zoomed out to fit a 20 m house that is about twenty pixels --
               a target smaller than the fingertip aiming at it, so most taps
               missed and the branch looked dead. The reach is a SCREEN distance
               now, turned into metres through the same projection the drawing is
               placed with, and never less than 0.6 m so it does not shrink to
               nothing when zoomed right in.

               And when it misses it says so, with the distance, because "nothing
               happened" is the one answer this file has been unable to explain
               twice in a row. */
            var opReach = 0.6;
            try {
              var mR = OV.map ? OV.map(_stage()) : null;
              if (mR && mR.ppm > 0) opReach = Math.max(0.6, 26 / mR.ppm);
            } catch (eR2) {}
            var opHit = openingNear(pp.x, pp.z, opReach);
            if (!opHit) {
              var nearest = openingNear(pp.x, pp.z, 1e9);
              if (nearest) {
                var sgN = segment(nearest.seg);
                if (sgN) {
                  var tN = nearest.at / sgN.len;
                  var dN = Math.hypot(pp.x - (sgN.x0 + (sgN.x1 - sgN.x0) * tN),
                                      pp.z - (sgN.z0 + (sgN.z1 - sgN.z0) * tN));
                  _planTaps.last += ' | nearest opening ' + nearest.seg + ' at ' +
                                    dN.toFixed(2) + ' m, reach ' + opReach.toFixed(2) + ' m';
                }
              } else {
                _planTaps.last += ' | no openings placed';
              }
            }
            if (opHit) {
              _planTaps.door++;
              _planTaps.last += ' -> opening on ' + opHit.seg;
              var stOp = _stage();
              if (stOp && stOp.orthoControls) stOp.orthoControls.enabled = false;
              /* on the plan the grab offset is ALONG the wall: where on the unit
                 the finger went down, so it does not jump its middle to the finger */
              var nG = nearestSegment(pp.x, pp.z);
              drag = { rec: opHit, moved: false, dy: _grabDy(e, opHit), plan: true,
                       du: (nG && nG.seg && nG.seg.id === opHit.seg) ? (opHit.at - nG.at) : 0,
                       P: { x: pp.x, y: 0, z: pp.z } };
              _hh.on = null;
              _opLast = opHit;   /* the bin acts on whatever was last in hand */
              /* THE ONE IN HAND IS THE ONE IN RED. A tap on a unit in edit mode
                 marks it -- a red wash over the whole opening, 10 per cent, so
                 it is obvious which one the bar's buttons will act on -- and
                 brings up the BIM button for it (Marek). */
              /* THE BIN COMES UP ON THE TAP, not only on a drag: a unit is in
                 hand the moment it is touched, and that is when you want to be
                 able to throw it away (Marek). */
              _opSel = { seg: opHit.seg, at: opHit.at };
              /* core-v617: on the plan the 3D wash waits for the release, which
                 builds the house anyway -- the press was a whole build */
              if (_dragOff()) { try { rebuild(); } catch (eSel) {} }
              else { _dragBegin('a window or door on the plan'); try { drawPlan(); } catch (eSel2) {} }
              try { _opBarMode = 'bin'; _opBinShow(); } catch (eBar) {}
              try { _opSwingSync(); } catch (eSw) {}
              try { canvas.setPointerCapture(e.pointerId); } catch (eO) {}
              return;
            }

            if (editing('layout')) {
              _roomTap = { x: pp.x, z: pp.z };
              _dragFromSet(e);
            } else {
              _planTaps.none++;
              _planTaps.last += ' -> openings step, nothing but openings here';
            }
            return;
          }
        }

        /* PICKING UP AN EXISTING ONE COMES FIRST. If a tool is armed the tap is a
           placement, but with nothing armed a tap on a window means that window --
           which is what anyone would expect, and it costs the orbit nothing
           because the grab only takes over within a hand's width of one. */
        if (!_armed) {
          /* EDIT MODE IS THE WHOLE TEST.

             It used to also require editing('openings') -- which asks whether the
             DOORS step is open -- and edit mode dismisses the drawer, so turning
             it on turned the drag off in the same breath. Placing a window left
             you in edit mode with nothing editable, which is exactly what you
             saw.

             The mode is the state. It is entered deliberately and left
             deliberately, and while it is on the drawer has no bearing on it. */
          if (!_opEdit) { _sillNote = 'tap ignored -- edit mode is off'; return; }
          _grabDiag = _grabDescribe(e);
          var hit = openingRayHit(e);
          if (!hit) return;
          _sillNote = 'grabbed ' + hit.kind + ' on ' + hit.seg + ' by ray';
          e.preventDefault();
          e.stopPropagation();
          var st0 = _stage();
          if (st0 && st0.controls) st0.controls.enabled = false;
          var gdy = _grabDy(e, hit);
          _sillNote += ' | dy=' + (gdy == null ? 'NULL -- no wall plane hit' : gdy.toFixed(3));
          drag = { rec: hit, moved: false, dy: gdy, x0: e.clientX, y0: e.clientY };
          /* where the finger met the unit, for the bin beside it (core-v593) */
          try { var hP = _hitGroup(e.clientX, e.clientY); drag.P = (hP && hP.length) ? { x: hP[0].point.x, y: hP[0].point.y, z: hP[0].point.z } : planPoint(e); } catch (eHP) { drag.P = null; }
          _hh.on = null;
          _opLast = hit;
          _opSel = { seg: hit.seg, at: hit.at };
          _opBinShow();
          try { _opSwingSync(); } catch (eSw2) {}
          return;
        }

        /* THE SCISSORS ARE GONE. Removing was an armed tool -- pick it, then tap
           the thing -- which is two decisions for one act, and it stayed armed
           afterwards waiting to eat the next tap. Dragging onto the bin is the
           act itself. The branch is kept only so an older saved state that still
           has it armed cannot leave the tap doing nothing. */
        if (_armed.remove) { armOpening(null); return; }
        var hits = _hitGroup(e.clientX, e.clientY);
        /* A ROOF WINDOW GOES ON THE ROOF (core-v491, R5). It has no wall: the
           product reads the tap against its own roof slopes through
           __PROD._roofHit(ray, hits) -> { plane, along, up } and keeps the unit
           in the rafters' bays through __PROD._roofClamp(rec). The record lives
           in STATE.openings with host 'roof' and no seg, so no wall ever sees it. */
        if (_armed.kind === 'roof') {
          if (typeof __PROD._roofHit !== 'function') { _log('roof window: this product has no roof to put one in'); _say('opening  roof window refused -- no __PROD._roofHit'); return; }
          var rcR = _aim(e.clientX, e.clientY), hR = null;
          try { hR = __PROD._roofHit(rcR ? rcR.ray : null, hits || []); } catch (eRH) { hR = null; _say('opening  ERROR __PROD._roofHit threw: ' + ((eRH && eRH.message) || eRH)); }
          if (!hR) { _log('roof window: tap the roof slope itself'); return; }
          e.preventDefault();
          e.stopPropagation();
          var recR = { host: 'roof', kind: 'roof', code: _armed.code || null, w: +_armed.w || 0.78, h: +_armed.h || 1.18,
                       sill: 0, plane: hR.plane, along: +hR.along || 0, up: +hR.up || 0 };
          try { if (typeof __PROD._roofClamp === 'function') __PROD._roofClamp(recR); } catch (eRC) {}
          STATE.openings.push(recR);
          savePref();
          rebuild();
          opEdit(true);
          _say('opening  roof window ' + (recR.code || '') + ' ' + Math.round(recR.w * 1000) + ' x ' + Math.round(recR.h * 1000) +
               ' placed on roof plane ' + recR.plane + ' at ' + Math.round(recR.along * 1000) + ' along, ' + Math.round(recR.up * 1000) + ' up the slope');
          armOpening(null);
          if (_wiz && _wiz.refresh) _wiz.refresh();
          return;
        }
        var p = (hits && hits.length) ? hits[0].point : _groundPoint(e.clientX, e.clientY);
        if (!p) { _log('opening: nothing under that tap'); return; }

        var n = nearestSegment(p.x, p.z, _armed.kind);
        /* A TAP THAT IS NOT NEAR A WALL IS NOT A PLACEMENT. Without this an orbit
           that begins on the roof drops a door on the far side of the building. */
        if (!n || n.dist > 1.5) {
          if (!n && typeof __PROD._opHost === 'function') {
            _say('opening  no wall here takes a ' + _armed.kind + ' (product host rule)');
            _log('opening: no wall here takes a ' + _armed.kind);
            return;
          }
          _log('opening: that is ' + fmtLen(n ? n.dist : 99) + ' from the nearest wall -- ' +
               'tap the wall itself');
          return;
        }

        e.preventDefault();
        e.stopPropagation();
        /* THE CORNER WINDOW (core-v751): two units at the outside corner of
           the wall that was tapped, the end nearer the tap */
        if (_armed.corner) {
          _cwPlace(n.seg, n.at);
          armOpening(null);
          if (_wiz && _wiz.refresh) _wiz.refresh();
          return;
        }
        addOpening(_sizeToWall(n.seg, {
          seg: n.seg.id, at: n.at, w: _armed.w, h: _armed.h,
          sill: _armed.sill, kind: _armed.kind,
          code: _armed.code, hand: _armed.hand, full: _armed.full,
          leaves: _armed.leaves
        }));
        armOpening(null);
        var w = _wiz;
        if (w && w.refresh) w.refresh();
      }, true);

      _log('opening pick bound');
    }

    function _initNav() {
      if (_navBound) return;
      var canvas = _canvasEl();
      if (!canvas) { global.setTimeout(_initNav, 120); return; }
      _navBound = true;

      /* 3D ONLY. In 2D the centre stays put and the camera stays square:
         the zoom is the ortho controls' pinch and wheel, about the centre,
         and one finger is the map's. This navigation moved the ortho camera
         off its axis (rot -2.03 in the snapshot) and its centre 20 m away. */
      canvas.addEventListener('wheel', function (e) {
        if (STATE.view === '2d') return;
        e.preventDefault();
        e.stopPropagation();
        zoomAt(e.clientX, e.clientY, e.deltaY > 0 ? (1 / 1.12) : 1.12);
      }, { passive: false, capture: true });

      /* TWO FINGERS DO BOTH AT ONCE, which is how a pinch actually behaves: the
         distance between them is the zoom and the midpoint between them is the
         slide, and both are read every frame. */
      var g2 = { on: false, d: 0, cx: 0, cy: 0 };
      function mid(e) {
        return { x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
                 y: (e.touches[0].clientY + e.touches[1].clientY) / 2 };
      }
      function gap(e) {
        return Math.sqrt(Math.pow(e.touches[0].clientX - e.touches[1].clientX, 2) +
                         Math.pow(e.touches[0].clientY - e.touches[1].clientY, 2));
      }
      canvas.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 2) return;
        if (STATE.view === '2d') return;
        var m = mid(e);
        g2.on = true; g2.d = gap(e); g2.cx = m.x; g2.cy = m.y;
        var c = _navControls();
        if (c) c.enabled = false;
      }, { passive: true, capture: true });

      canvas.addEventListener('touchmove', function (e) {
        if (!g2.on || e.touches.length !== 2) return;
        e.preventDefault();
        var m = mid(e), d = gap(e);
        if (Math.abs(m.x - g2.cx) > 0.5 || Math.abs(m.y - g2.cy) > 0.5) {
          panScene(m.x - g2.cx, m.y - g2.cy);
        }
        if (g2.d > 0 && Math.abs(d - g2.d) > 0.5) {
          zoomAt(m.x, m.y, d / g2.d);
        }
        g2.cx = m.x; g2.cy = m.y; g2.d = d;
      }, { passive: false, capture: true });

      function endG(e) {
        if (!g2.on) return;
        if (e.touches && e.touches.length >= 2) return;
        g2.on = false;
        var c = _navControls();
        if (c) c.enabled = true;
      }
      canvas.addEventListener('touchend', endG, true);
      canvas.addEventListener('touchcancel', endG, true);

      /* the same slide on a mouse, on the buttons nothing else is using */
      var m2 = { on: false, x: 0, y: 0, id: null };
      canvas.addEventListener('contextmenu', function (e) { e.preventDefault(); });
      canvas.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'mouse' || (e.button !== 1 && e.button !== 2)) return;
        if (STATE.view === '2d') return;
        e.preventDefault();
        m2.on = true; m2.x = e.clientX; m2.y = e.clientY; m2.id = e.pointerId;
        var c = _navControls();
        if (c) c.enabled = false;
        try { canvas.setPointerCapture(e.pointerId); } catch (x) {}
      }, true);
      canvas.addEventListener('pointermove', function (e) {
        if (!m2.on || e.pointerId !== m2.id) return;
        panScene(e.clientX - m2.x, e.clientY - m2.y);
        m2.x = e.clientX; m2.y = e.clientY;
      }, true);
      function endM() {
        if (!m2.on) return;
        m2.on = false; m2.id = null;
        var c = _navControls();
        if (c) c.enabled = true;
      }
      canvas.addEventListener('pointerup', endM, true);
      canvas.addEventListener('pointercancel', endM, true);

      _log('navigation bound -- zoom at the pointer, two fingers or right/middle drag to slide');
    }

    /* -------------------------------------------------------------------------
       THE SCALE FIGURE. A 175 cm human, ghosted, standing wherever it is put.

       It is the cheapest way to make a screenshot readable: a box of unknown size
       with a person beside it is a building, without one it is a box. Lifted from
       the tiny lib, where the material and the drag handle were already settled.

       Three things it must never be part of: the bill, the AR export, and the
       footprint handles. All three key on userData -- partType 'person' and
       arSkip -- so nothing has to remember to exclude it by name.
       ------------------------------------------------------------------------- */
    var PERSON_GLB_URL = 'https://cdn.shopify.com/3d/models/a185bdffeca46290/human.glb';
    var PERSON_H = 1.75;
    var _personScene = null;
    var _personLoading = null;
    var _personNote = 'not loaded';

    function _getGLTFLoader() {
      return new Promise(function (resolve, reject) {
        var T = global.THREE;
        /* the shared liquid already imports it onto the window; the rest is for a
           host that does not */
        if (global.GLTFLoader) { resolve(global.GLTFLoader); return; }
        if (T && T.GLTFLoader) { resolve(T.GLTFLoader); return; }
        if (!T) { reject(new Error('THREE not on window')); return; }
        var rev = T.REVISION || '160';
        var url = 'https://esm.sh/three@0.' + rev + '/examples/jsm/loaders/GLTFLoader.js';
        try {
          import(url).then(function (mod) {
            if (mod && mod.GLTFLoader) resolve(mod.GLTFLoader);
            else reject(new Error('no GLTFLoader in module'));
          }).catch(reject);
        } catch (e) { reject(e); }
      });
    }

    function _loadPersonScene() {
      if (_personScene) return Promise.resolve(_personScene);
      if (_personLoading) return _personLoading;
      _personNote = 'loading';
      _personLoading = _getGLTFLoader().then(function (Loader) {
        return new Promise(function (resolve, reject) {
          var T = global.THREE;
          new Loader().load(PERSON_GLB_URL, function (gltf) {
            var scene = gltf.scene;
            /* fitted to a real height, then stood on the ground rather than
               centred on it -- a figure sunk to its waist reads as a bug */
            var b1 = new T.Box3().setFromObject(scene);
            var size = b1.getSize(new T.Vector3());
            if (size.y > 0) scene.scale.setScalar(PERSON_H / size.y);
            var b2 = new T.Box3().setFromObject(scene);
            var c = b2.getCenter(new T.Vector3());
            scene.position.set(-c.x, -b2.min.y, -c.z);
            /* Ghosted, not solid. The figure is a ruler, and a ruler that hides
               the wall behind it is no use -- depthWrite off so it never occludes
               the building it is there to measure. */
            var mat = new T.MeshStandardMaterial({
              /* A GREY FIGURE, NOT A WHITE ONE (core-v642, Marek: postava je
                 stale extremne pruhledna -- najit, kde se ridi opacity). The
                 opacity was never the whole of it: a WHITE body with a white
                 glow on a pale grey ground disappears at any opacity. Slate
                 grey at 65 %, no glow -- it reads against the ground and the
                 walls, and the house still shows through it (depthWrite off).
                 core-v649 (Marek: postava je moc tmava, muze byt o dost
                 svetlejsi, spise snizit opacity): the same grey at 40 %. */
              color: 0x5a5f66, transparent: true, opacity: 0.40,
              depthWrite: false, roughness: 1.0,
              emissive: 0x000000, emissiveIntensity: 0
            });
            scene.traverse(function (ch) {
              if (ch.isMesh) { ch.material = mat; ch.castShadow = false; ch.receiveShadow = false; }
            });
            _personScene = scene;
            _personNote = 'loaded';
            _log('scale figure GLB loaded and fitted to ' + PERSON_H + ' m');
            resolve(scene);
          }, null, function (err) {
            _personNote = 'GLB FAILED: ' + (err && err.message ? err.message : err);
            _log('person: ' + _personNote);
            reject(err);
          });
        });
      }).catch(function (err) {
        _personLoading = null;
        _personNote = 'loader FAILED: ' + (err && err.message ? err.message : err);
        throw err;
      });
      return _personLoading;
    }

    /* Beside the building, off its long side, where somebody would stand to look
       at it. */
    /* -------------------------------------------------------------------------
       MODELS TO PLACE (core-v273). A GLB from the store's CDN, put down and
       moved about the way the scale figure is -- a handle that follows it on
       screen, dragged across the ground -- but solid, in its own materials, and
       standing on whatever floor is under it: the building's floor inside the
       footprint, the deck or the tiles on the terrace, the ground elsewhere
       (Marek: umistit a posouvat na libovolne misto, reflektuje vysku podlahy).
       A tap on the handle without dragging turns it a quarter.

       Each is one entry here: the file and the length to fit its longest side
       to, because a GLB carries no promise about units. What is on, and where,
       lives in the product's settings bag, so it saves and shares with the rest.
       ------------------------------------------------------------------------- */
    var PROP_DEFS = {
      tub: { url: 'https://cdn.shopify.com/3d/models/f7f023a7db64da4b/Bath_tub.glb', len: 1.70, name: 'bathtub' }
    };
    var _propScene = {}, _propLoading = {}, _propNote = {};
    function _propBag() {
      STATE.product = STATE.product || {};
      var B = STATE.product.props || (STATE.product.props = {});
      return B;
    }
    function _prop(key) {
      var B = _propBag();
      if (!B[key]) B[key] = { on: false, x: null, z: null, rot: 0 };
      return B[key];
    }
    /* A PRODUCT'S OWN PIECES (core-v277). Besides a GLB, a piece can be drawn
       by the product -- build(T) returns the model -- and can carry its own
       rules: auto() switches it on whenever it applies (the sauna's heater is
       in the sauna room whenever there is one), place() is where it starts,
       clamp(x, z) keeps it where it may stand, floorY(x, z) is the floor under
       it. Everything else -- the handle, the drag, the quarter turn on a tap,
       the saving -- is the tub's. */
    function defineProp(key, def) {
      if (!key || !def) return false;
      PROP_DEFS[key] = def;
      _propScene[key] = null;
      return true;
    }
    function propOn(key) {
      var D = PROP_DEFS[key];
      if (!D) return false;
      if (typeof D.auto === 'function') { try { return !!D.auto(); } catch (eA) { return false; } }
      return !!_prop(key).on;
    }
    function propPos(key) {
      if (!propOn(key)) return null;
      var p = _propXZ(key);
      return { x: p.x, z: p.z, rot: p.rot || 0 };
    }
    function _propXZ(key) {
      var p = _prop(key), D = PROP_DEFS[key] || {};
      if ((p.x == null || p.z == null) && typeof D.place === 'function') {
        try { var pl = D.place(); if (pl) { p.x = pl.x; p.z = pl.z; } } catch (eP) {}
      }
      if (p.x != null && typeof D.clamp === 'function') {
        try { var cl = D.clamp(p.x, p.z); if (cl) { p.x = cl.x; p.z = cl.z; } } catch (eC) {}
      }
      if (p.x == null || p.z == null) {
        var B = _buildRect();
        p.x = B.x1 + 1.6;
        p.z = (B.z0 + B.z1) / 2;
      }
      return p;
    }
    /* the floor under a point: the building's, the terrace's, or the ground */
    function _floorYAt(x, z) {
      var B = _buildRect(), y0 = 0.35;
      try { y0 = storeys()[0].y0; } catch (e) {}
      if (x > B.x0 && x < B.x1 && z > B.z0 && z < B.z1) return { y: y0 + 0.012, on: 'floor' };
      var TR = null;
      try { TR = _terraceRect(); } catch (eT) { TR = null; }
      if (TR && x > TR.x0 && x < TR.x1 && z > TR.z0 && z < TR.z1) {
        var surf = (_terrace() || {}).surface;
        return surf === 'pavers' ? { y: 0.10, on: 'terrace tiles' } : { y: y0 - 0.02, on: 'terrace deck' };
      }
      return { y: 0, on: 'ground' };
    }
    function _loadProp(key) {
      var D = PROP_DEFS[key];
      if (!D) return Promise.reject(new Error('no such model ' + key));
      if (_propScene[key]) return Promise.resolve(_propScene[key]);
      if (typeof D.build === 'function') {
        try {
          var wrapB = new global.THREE.Group();
          wrapB.add(D.build(global.THREE));
          _propScene[key] = wrapB;
          _propNote[key] = 'drawn by the product';
          return Promise.resolve(wrapB);
        } catch (eB) {
          _propNote[key] = 'build FAILED: ' + (eB && eB.message);
          return Promise.reject(eB);
        }
      }
      if (_propLoading[key]) return _propLoading[key];
      _propNote[key] = 'loading';
      _propLoading[key] = _getGLTFLoader().then(function (Loader) {
        return new Promise(function (resolve, reject) {
          var T = global.THREE;
          new Loader().load(D.url, function (gltf) {
            var scene = gltf.scene;
            var b1 = new T.Box3().setFromObject(scene), s1 = b1.getSize(new T.Vector3());
            /* ITS OWN SIZE, NOT A GUESS (core-v274). Fitting the longest side to
               1.70 m counted the stove and its flue as tub, so the tub itself came
               out about a metre across (Marek: zda se o dost mensi). A model for
               the store's 3D viewer is drawn in metres, because that is what puts
               it at true size in AR; so it is used as it comes. Only a file that
               is plainly in centimetres or millimetres is brought to metres, and
               only one that fits none of them falls back to the stated length. */
            var dMax = Math.max(s1.x, s1.y, s1.z), how = 'metres as drawn';
            function ok(d) { return d >= 0.2 && d <= 20; }
            if (!ok(dMax)) {
              if (ok(dMax / 100)) { scene.scale.setScalar(0.01); how = 'drawn in cm, scaled to metres'; }
              else if (ok(dMax / 1000)) { scene.scale.setScalar(0.001); how = 'drawn in mm, scaled to metres'; }
              else { var side = Math.max(s1.x, s1.z); if (side > 0) scene.scale.setScalar(D.len / side); how = 'no usable unit, fitted to ' + Math.round(D.len * 1000) + ' mm'; }
            }
            var b2 = new T.Box3().setFromObject(scene), c2 = b2.getCenter(new T.Vector3()), s2 = b2.getSize(new T.Vector3());
            scene.position.set(-c2.x, -b2.min.y, -c2.z);
            scene.traverse(function (ch) { if (ch.isMesh) { ch.castShadow = true; ch.receiveShadow = true; } });
            var wrap = new T.Group();
            wrap.add(scene);
            _propScene[key] = wrap;
            _propNote[key] = 'loaded, ' + how + ': ' + Math.round(s2.x * 1000) + ' x ' + Math.round(s2.z * 1000) +
                             ', ' + Math.round(s2.y * 1000) + ' high (file ' + s1.x.toFixed(2) + ' x ' + s1.z.toFixed(2) + ' x ' + s1.y.toFixed(2) + ')';
            _log('prop ' + key + ' GLB ' + _propNote[key]);
            resolve(wrap);
          }, null, function (err) {
            _propNote[key] = 'GLB FAILED: ' + (err && err.message ? err.message : err);
            reject(err);
          });
        });
      }).catch(function (err) {
        _propLoading[key] = null;
        if (!/GLB FAILED/.test(_propNote[key] || '')) _propNote[key] = 'loader FAILED: ' + (err && err.message ? err.message : err);
        throw err;
      });
      return _propLoading[key];
    }
    function _findProp(key) {
      var st = _stage();
      if (!st || !st.group) return null;
      for (var i = 0; i < st.group.children.length; i++) {
        var c = st.group.children[i];
        if (c.userData && c.userData.partType === 'prop:' + key) return c;
      }
      return null;
    }
    function _placeProp(key, grp) {
      var p = _propXZ(key), f = _floorYAt(p.x, p.z), D = PROP_DEFS[key] || {};
      if (typeof D.floorY === 'function') {
        try { var fy = D.floorY(p.x, p.z); if (typeof fy === 'number' && isFinite(fy)) f = { y: fy, on: D.floorName || 'its own floor' }; } catch (eF) {}
      }
      grp.position.set(p.x, f.y, p.z);
      grp.rotation.y = (p.rot || 0) * Math.PI / 2;
      return f;
    }
    function _buildProps(T, g, P) {
      var k;
      for (k in PROP_DEFS) {
        if (!PROP_DEFS.hasOwnProperty(k)) continue;
        if (!propOn(k)) { if (P) P('prop ' + k + ': off'); continue; }
        var grp = new T.Group();
        grp.userData.partType = 'prop:' + k;
        var f = _placeProp(k, grp);
        g.add(grp);
        /* a drawn piece is drawn again on every build: the product may have
           changed it (its colour, the review magenta) */
        if (typeof PROP_DEFS[k].build === 'function') _propScene[k] = null;
        if (_propScene[k]) grp.add(_propScene[k]);
        else if (typeof PROP_DEFS[k].build === 'function') {
          try { _loadProp(k).then(function (sc) { grp.add(sc); }); } catch (eL) {}
        }
        else {
          (function (key) {
            _loadProp(key).then(function (scene) {
              var q = _findProp(key);
              if (q && q.children.length === 0) q.add(scene);
            }).catch(function () {});
          })(k);
        }
        var pp = _prop(k);
        if (P) P('prop ' + k + ': on at ' + pp.x.toFixed(2) + ', ' + pp.z.toFixed(2) + ', standing on the ' + f.on +
                 ' at ' + f.y.toFixed(3) + ', turned ' + ((pp.rot || 0) % 4) * 90 + ' deg; model ' + (_propNote[k] || 'not asked for yet') +
                 '; handle ' + _propHandleFrom);
      }
    }
    function toggleProp(key) {
      if (!PROP_DEFS[key]) return false;
      var p = _prop(key);
      p.on = !p.on;
      _propXZ(key);
      savePref();
      rebuild();
      _propHandles();
      _log('prop ' + key + ' ' + (p.on ? 'on' : 'off'));
      return p.on;
    }
    /* the handle: made here rather than in the page, one per model */
    var _propDrag = null, _propTracking = false, _propHandleFrom = 'not made yet';
    /* ---- the object drag itself (core-v288) ---- */
    var _objDrag = null, _objNote = 'no object moved yet', _objPrev = null, _objPending = null;
    /* THE CONTROLS GO BACK EXACTLY AS THEY WERE (core-v289). Letting go switched
       BOTH navigations on -- the 3D orbit and the plan's -- so after the first
       grab the two fought over every drag and the map would not turn until the
       page was reloaded (Marek: zacyklilo se rotovani mapy). */
    function _objControls(on) {
      var st = _stage();
      if (!st) return;
      if (!on) {
        _objPrev = { c: st.controls ? st.controls.enabled : null, o: st.orthoControls ? st.orthoControls.enabled : null };
        if (st.controls) st.controls.enabled = false;
        if (st.orthoControls) st.orthoControls.enabled = false;
      } else {
        var pv = _objPrev || {};
        if (st.controls && pv.c != null) st.controls.enabled = pv.c;
        if (st.orthoControls && pv.o != null) st.orthoControls.enabled = pv.o;
        _objPrev = null;
      }
    }
    function _objTarget(e, g0) {
      var k, plan = _planWanted();
      if (!plan) {
        var hits = _hitGroup(e.clientX, e.clientY) || [];
        if (!hits.length) return null;
        var o = hits[0].object;
        while (o) {
          var ud = o.userData || {};
          if (ud.partType && ud.partType.indexOf('prop:') === 0) return { kind: 'prop', key: ud.partType.slice(5), grp: o };
          if (ud.partType === 'pool') return { kind: 'pool', grp: o };
          if (ud.layer && /-terrace$/.test(ud.layer) && _terrace().on) return { kind: 'terrace', grp: o };
          o = o.parent;
        }
        return null;
      }
      /* the plan: the point on the ground */
      for (k in PROP_DEFS) {
        if (!PROP_DEFS.hasOwnProperty(k) || !propOn(k)) continue;
        var pp = _prop(k), reach = (k === 'heater') ? 0.32 : 0.80;
        if (pp.x != null && Math.abs(g0.x - pp.x) < reach && Math.abs(g0.z - pp.z) < reach) return { kind: 'prop', key: k, grp: _findProp(k) };
      }
      var PR = _poolRect();
      if (PR && g0.x > PR.x0 - POOL.cope && g0.x < PR.x1 + POOL.cope && g0.z > PR.z0 - POOL.cope && g0.z < PR.z1 + POOL.cope) return { kind: 'pool', grp: _findPartGroup('pool') };
      var TR = _terraceRect();
      if (TR && g0.x > TR.x0 && g0.x < TR.x1 && g0.z > TR.z0 && g0.z < TR.z1) return { kind: 'terrace', grp: _findTerraceGroup() };
      return null;
    }
    function _findPartGroup(pt) {
      var st = _stage(), i;
      if (!st || !st.group) return null;
      for (i = 0; i < st.group.children.length; i++) if (st.group.children[i].userData && st.group.children[i].userData.partType === pt) return st.group.children[i];
      return null;
    }
    function _findTerraceGroup() {
      var st = _stage(), found = null;
      if (!st || !st.group) return null;
      st.group.traverse(function (o) { if (!found && o.userData && o.userData.layer && /-terrace$/.test(o.userData.layer)) found = o; });
      return found;
    }
    function _objGrab(e) {
      /* a drag left behind by a lost pointerup is ended first, never kept */
      if (_objDrag) { try { _objEnd(null); } catch (eSt) { _objDrag = null; _objControls(true); } }
      _objPending = null;
      if (_opEdit) return false;
      var g0 = _groundPoint(e.clientX, e.clientY);
      if (!g0) return false;
      var t = _objTarget(e, g0);
      if (!t) return false;
      if ((t.kind === 'pool' || t.kind === 'terrace') && editing('exterior')) return false;
      /* AND THE BIG ONES ARE TAKEN STRAIGHT AWAY TOO (core-v295). They were on a
         350 ms hold so a finger landing on them could still turn the view, but
         waiting for a pool or a terrace to "catch" reads as nothing happening
         (Marek: pergola s terasou a bazen nejde hned tahnout). Everything is
         taken on touch now; the view still turns anywhere the finger does not
         land on one of these pieces. */
      _objStart(t, g0, e.pointerId);
      return true;
    }
    function _objStart(t, g0, pid) {
      var start = null;
      if (t.kind === 'prop') { var P0 = _propXZ(t.key); start = { x: P0.x, z: P0.z }; }
      else if (t.kind === 'pool') { var Q = _pool(); _poolRect(); start = { x: Q.x0, z: Q.z0 }; }
      else { var R0 = _terraceRect(); start = { x: R0.x0, z: R0.z0 }; }
      _objDrag = { t: t, g0: { x: g0.x, z: g0.z }, start: start, moved: false, dx: 0, dz: 0, id: pid };
      _objControls(false);
      _objNote = 'grabbed ' + t.kind + (t.key ? (' ' + t.key) : '') + (_planWanted() ? ' on the plan' : ' in 3D');
      return true;
    }
    function _objMove(e) {
      var D = _objDrag;
      if (!D || (D.id != null && e.pointerId != null && e.pointerId !== D.id)) return;
      var g = _groundPoint(e.clientX, e.clientY);
      if (!g) return;
      var dx = g.x - D.g0.x, dz = g.z - D.g0.z;
      if (Math.abs(dx) + Math.abs(dz) > 0.02) D.moved = true;
      if (!D.moved) return;
      D.dx = dx; D.dz = dz;
      if (D.t.kind === 'prop') {
        var S = _prop(D.t.key);
        S.x = D.start.x + dx; S.z = D.start.z + dz;
        var grp = _findProp(D.t.key);
        if (grp) _placeProp(D.t.key, grp);
        if (_planWanted()) { try { drawPlan(); } catch (eDp) {} }
      } else if (D.t.grp) {
        D.t.grp.position.set(dx, D.t.grp.position.y, dz);
      }
    }
    function _objEnd(e) {
      var D = _objDrag;
      _objDrag = null;
      _objControls(true);
      if (!D) return;
      if (!D.moved) {
        if (D.t.kind === 'prop') {
          var S = _prop(D.t.key);
          S.rot = ((S.rot || 0) + 1) % 4;
          _objNote = D.t.key + ' turned to ' + (S.rot * 90) + ' deg';
          savePref(); rebuild();
          if (_planWanted()) { try { drawPlan(); } catch (eDp0) {} }
        }
        return;
      }
      if (D.t.kind === 'pool') {
        var Q = _pool();
        Q.x0 = Math.round((D.start.x + D.dx) * 100) / 100; Q.z0 = Math.round((D.start.z + D.dz) * 100) / 100;
        _poolLinkFor(5000);
      } else if (D.t.kind === 'terrace') {
        var T = _terrace();
        T.x0 = Math.round((D.start.x + D.dx) * 100) / 100; T.z0 = Math.round((D.start.z + D.dz) * 100) / 100;
        _terraceSettle();
      }
      if (D.t.grp && D.t.kind !== 'prop') D.t.grp.position.set(0, D.t.grp.position.y, 0);
      _objNote = 'moved ' + D.t.kind + (D.t.key ? (' ' + D.t.key) : '') + ' by ' + D.dx.toFixed(2) + ', ' + D.dz.toFixed(2) +
                 (D.t.kind === 'terrace' ? (' -- ' + _terrSettleNote) : '');
      _log('object: ' + _objNote);
      savePref(); rebuild();
      if (_planWanted()) { try { drawPlan(); } catch (eDp2) {} }
    }

    function _propHandles() {
      /* NO HANDLE OVER A PIECE ANY MORE (core-v288): the piece itself is taken.
         Any handle an earlier build made is hidden; nothing new is made. */
      var hk;
      for (hk in PROP_DEFS) {
        if (!PROP_DEFS.hasOwnProperty(hk)) continue;
        var hOld = document.getElementById('cf-prop-handle-' + hk);
        if (hOld) hOld.style.display = 'none';
      }
      _propHandleFrom = 'none -- the piece itself is dragged';
      return;
    }
    function _propHandlesOld() {
      var k, st = _stage();
      var host = (($('cf-body-handle') || {}).parentNode) || (st && st.renderer && st.renderer.domElement.parentNode);
      if (!host) return;
      for (k in PROP_DEFS) {
        if (!PROP_DEFS.hasOwnProperty(k)) continue;
        var id = 'cf-prop-handle-' + k, h = document.getElementById(id);
        if (!h) {
          /* THE SAME HANDLE AS THE SCALE FIGURE'S (core-v274): copied off the
             page's own, children and all, with the look it is drawn with carried
             across by value -- its styles are written against its id, which the
             copy cannot share (Marek: drag point vypada jinak nez u postavy) */
          var src = $('cf-body-handle');
          if (src) {
            h = src.cloneNode(true);
            h.id = id;
            h.classList.add('active');
            try {
              var cs = global.getComputedStyle(src);
              ['width', 'height', 'marginLeft', 'marginTop', 'borderRadius', 'backgroundColor', 'backgroundImage',
               'backgroundSize', 'backgroundPosition', 'backgroundRepeat', 'borderTop', 'borderRight', 'borderBottom',
               'borderLeft', 'boxShadow', 'color', 'transform', 'boxSizing', 'alignItems', 'justifyContent',
               'fontSize', 'lineHeight', 'textAlign', 'zIndex'].forEach(function (pn) {
                try { if (cs[pn]) h.style[pn] = cs[pn]; } catch (eS) {}
              });
            } catch (eC) {}
            _propHandleFrom = 'copied from the scale figure\'s';
          } else {
            h = document.createElement('div');
            h.id = id;
            h.style.cssText = 'width:40px;height:40px;margin:-20px 0 0 -20px;border-radius:50%;background:#111;' +
                              'border:4px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.3);z-index:25';
            _propHandleFrom = 'its own (no scale figure handle on the page)';
          }
          h.setAttribute('role', 'button');
          h.style.position = 'absolute';
          h.style.touchAction = 'none';
          h.style.cursor = 'grab';
          h.style.pointerEvents = 'auto';
          host.appendChild(h);
          _bindPropHandle(h, k);
        }
        h.style.display = propOn(k) ? '' : 'none';
        if (!propOn(k)) continue;
        if (propOn(k) && global.getComputedStyle && global.getComputedStyle(h).display === 'none') h.style.display = 'block';
      }
      if (!_propTracking) _startPropTracker();
    }
    function _bindPropHandle(h, key) {
      var T = global.THREE, plane = null, rc = null, ndc = null, hit = null;
      function toWorld(cx, cy) {
        var st = _stage();
        if (!st || !st.camera || !st.renderer || !T) return null;
        var r = st.renderer.domElement.getBoundingClientRect();
        if (!plane) plane = new T.Plane(new T.Vector3(0, 1, 0), 0);
        if (!rc) rc = new T.Raycaster();
        if (!ndc) ndc = new T.Vector2();
        if (!hit) hit = new T.Vector3();
        ndc.x = ((cx - r.left) / r.width) * 2 - 1;
        ndc.y = -((cy - r.top) / r.height) * 2 + 1;
        rc.setFromCamera(ndc, st.camera);
        plane.constant = -_bodyY();
        return rc.ray.intersectPlane(plane, hit);
      }
      h.addEventListener('pointerdown', function (e) {
        if (!propOn(key)) return;
        e.preventDefault(); e.stopPropagation();
        _propDrag = { key: key, x0: e.clientX, y0: e.clientY, moved: false };
        try { h.setPointerCapture(e.pointerId); } catch (x) {}
        var st = _stage();
        if (st && st.controls) st.controls.enabled = false;
      });
      h.addEventListener('pointermove', function (e) {
        if (!_propDrag || _propDrag.key !== key) return;
        if (Math.abs(e.clientX - _propDrag.x0) + Math.abs(e.clientY - _propDrag.y0) > 6) _propDrag.moved = true;
        if (!_propDrag.moved) return;
        var p = toWorld(e.clientX, e.clientY);
        if (!p) return;
        var S = _prop(key);
        S.x = p.x; S.z = p.z;
        var grp = _findProp(key);
        if (grp) _placeProp(key, grp);   /* _propXZ inside clamps it */
      });
      function up(e) {
        if (!_propDrag || _propDrag.key !== key) return;
        var turned = !_propDrag.moved;
        _propDrag = null;
        try { h.releasePointerCapture(e.pointerId); } catch (x) {}
        var st = _stage();
        if (st && st.controls) st.controls.enabled = true;
        if (turned) {
          var S = _prop(key);
          S.rot = ((S.rot || 0) + 1) % 4;
          var grp = _findProp(key);
          if (grp) _placeProp(key, grp);
        }
        savePref();
        rebuild();
      }
      h.addEventListener('pointerup', up);
      h.addEventListener('pointercancel', up);
    }
    function _startPropTracker() {
      _propTracking = true;
      var T = global.THREE;
      if (!T) return;
      var v = new T.Vector3();
      function tick() {
        var st = _stage(), k;
        for (k in PROP_DEFS) {
          if (!PROP_DEFS.hasOwnProperty(k)) continue;
          var h = document.getElementById('cf-prop-handle-' + k);
          if (!h || !st || !st.camera || !st.renderer || !propOn(k)) continue;
          var p = _prop(k), f = _floorYAt(p.x, p.z);
          v.set(p.x, f.y + 0.9, p.z).project(st.camera);
          var cr = st.renderer.domElement.getBoundingClientRect();
          var par = h.offsetParent;
          var pr = par ? par.getBoundingClientRect() : { left: 0, top: 0 };
          h.style.left = (cr.left - pr.left + (v.x + 1) * 0.5 * cr.width) + 'px';
          h.style.top = (cr.top - pr.top + (1 - (v.y + 1) * 0.5) * cr.height) + 'px';
          h.style.opacity = (v.z > -1 && v.z < 1) ? '' : '0';
        }
        _rafQuiet(tick);
      }
      _rafQuiet(tick);
    }

    /* -------------------------------------------------------------------------
       THE POOL (core-v275). A place for one, shown the way it would look from
       the garden: the coping around the edge in pool tiles, a light blue liner
       going down into the ground, and water in it. Dragged and sized on the
       site like the terrace, in the Exterior step, and a small note on a drag
       points to the pool configurator, where the pool itself is designed and
       priced -- here it is a picture of where it could go and is not billed.

       IT GOES DOWN, NOT UP. The liner is a box drawn from the inside only, so
       seen from above it reads as a hole in the ground over the map. AR is cut
       at the ground like everything else (core-v276) -- a viewer stands a model
       on its lowest point, and a pool floor 1.4 m down lifted the house off the
       ground -- so AR gets the coping and a sheet of water at ground level. The
       coping stands 50 mm proud, the water 120 mm under its top.

       In the core so every product can place one; a product only adds the tile.
       ------------------------------------------------------------------------- */
    var POOL = { cope: 0.30, copeT: 0.05, depth: 1.40, water: 0.12, minW: 2.0, maxW: 15.0, minL: 1.5, maxL: 8.0,
                 copeCol: 0xE6DFD0, linerCol: 0x9FD8F2, waterCol: 0x2E9ED8 };
    function _pool() {
      var P = STATE.product = STATE.product || {};
      if (!P.pool) P.pool = { on: false, x0: null, z0: null, w: 6.0, l: 3.0 };
      return P.pool;
    }
    function poolOn() { return !!_pool().on; }
    function _poolRect() {
      var Q = _pool();
      if (!Q.on) return null;
      if (Q.x0 == null || Q.z0 == null) {
        var B = _buildRect();
        Q.x0 = B.x1 + 2.0;
        Q.z0 = (B.z0 + B.z1) / 2 - Q.l / 2;
      }
      return { x0: Q.x0, x1: Q.x0 + Q.w, z0: Q.z0, z1: Q.z0 + Q.l };
    }
    function togglePool() {
      var Q = _pool();
      Q.on = !Q.on;
      _poolRect();
      if (Q.on) _poolLinkFor(5000);
      savePref();
      rebuild();
      _log('pool ' + (Q.on ? 'on' : 'off'));
      return Q.on;
    }
    var _poolTex = null;
    function _poolCopeTex(T) {
      if (_poolTex !== null) return _poolTex;
      try {
        var n = 256, cv = global.document.createElement('canvas');
        cv.width = n; cv.height = n;
        var c = cv.getContext('2d');
        c.fillStyle = '#E6DFD0'; c.fillRect(0, 0, n, n);
        c.fillStyle = 'rgba(255,255,255,0.35)'; c.fillRect(8, 8, n - 16, n / 2 - 16);
        c.strokeStyle = '#A9A294'; c.lineWidth = 6;
        c.strokeRect(0, 0, n, n / 2); c.strokeRect(0, n / 2, n, n / 2);
        var tx = new T.CanvasTexture(cv);
        if (T.SRGBColorSpace) tx.colorSpace = T.SRGBColorSpace;
        tx.wrapS = tx.wrapT = T.RepeatWrapping;
        _poolTex = tx;
      } catch (e) { _poolTex = false; }
      return _poolTex;
    }
    function _buildPool(T, g, P) {
      var R = _poolRect();
      if (!R) { if (P) P('pool: off'); return; }
      var w = R.x1 - R.x0, l = R.z1 - R.z0, cx = (R.x0 + R.x1) / 2, cz = (R.z0 + R.z1) / 2;
      var grp = new T.Group();
      grp.userData.partType = 'pool';
      g.add(grp);
      /* the liner, seen from inside: walls and floor */
      var liner = new T.Mesh(new T.BoxGeometry(w, POOL.depth, l),
                             new T.MeshStandardMaterial({ color: POOL.linerCol, roughness: 0.55, metalness: 0.0, side: T.BackSide }));
      liner.position.set(cx, -POOL.depth / 2, cz);
      liner.name = 'pool-liner';
      grp.add(liner);
      /* the water */
      var wat = new T.Mesh(new T.BoxGeometry(w, 0.004, l),
                           new T.MeshStandardMaterial({ color: POOL.waterCol, roughness: 0.08, metalness: 0.1,
                                                        transparent: true, opacity: 0.45, depthWrite: false }));
      wat.position.set(cx, POOL.copeT - POOL.water, cz);
      wat.name = 'pool-water';
      wat.renderOrder = 2;
      grp.add(wat);
      /* AR cuts everything at the ground, the water with it -- so AR gets its own
         sheet of water just above the ground, hidden here */
      var watAr = new T.Mesh(new T.BoxGeometry(w, 0.004, l), wat.material);
      watAr.position.set(cx, 0.004, cz);
      watAr.name = 'pool-water-ar';
      watAr.visible = false;
      watAr.userData.arOnly = true;
      grp.add(watAr);
      /* the coping, four strips round the edge in 300 x 600 pool tiles */
      var cw = POOL.cope, ct = POOL.copeT, tex = _poolCopeTex(T);
      function strip(sw, sl, x, z, along) {
        var mat = new T.MeshStandardMaterial({ color: tex ? 0xFFFFFF : POOL.copeCol, roughness: 0.8, metalness: 0.0 });
        if (tex) {
          var t2 = tex.clone(); t2.needsUpdate = true; t2.wrapS = t2.wrapT = T.RepeatWrapping;
          t2.repeat.set(along ? sw / 0.60 : sw / 0.30, along ? sl / 0.30 : sl / 0.60);
          mat.map = t2;
        }
        var m = new T.Mesh(new T.BoxGeometry(sw, ct, sl), mat);
        m.position.set(x, ct / 2, z);
        m.name = 'pool-coping';
        grp.add(m);
      }
      strip(w + 2 * cw, cw, cx, R.z0 - cw / 2, true);
      strip(w + 2 * cw, cw, cx, R.z1 + cw / 2, true);
      strip(cw, l, R.x0 - cw / 2, cz, false);
      strip(cw, l, R.x1 + cw / 2, cz, false);
      /* THE LADDER, stainless (core-v279): two handrails rising from the deck
         behind the coping, arching over it and dropping down the pool wall,
         three treads between them in the water. On the long side nearest the
         house, a metre in from the pool's corner. */
      var lad = 0;
      try { lad = _poolLadder(T, grp, R); } catch (eLd) { if (P) P('pool ladder threw: ' + (eLd && eLd.message)); }
      if (P) P('pool: on, ' + fmtLen(w) + ' x ' + fmtLen(l) + ' of water at ' + R.x0.toFixed(2) + ', ' + R.z0.toFixed(2) +
               ', ' + Math.round(POOL.depth * 1000) + ' deep, coping ' + Math.round(cw * 1000) + ' wide ' + Math.round(ct * 1000) +
               ' proud, a stainless ladder (' + lad + ' parts) -- a picture of where it could go, not billed; the pool configurator designs and prices it');
    }
    function _poolLadder(T, grp, R) {
      var B = _buildRect();
      /* which long side faces the house: the one whose line is nearer to it */
      var alongX = (R.x1 - R.x0) >= (R.z1 - R.z0);
      var side, edge, inward;
      if (alongX) {
        var dz0 = Math.abs(R.z0 - (B.z0 + B.z1) / 2), dz1 = Math.abs(R.z1 - (B.z0 + B.z1) / 2);
        side = (dz0 <= dz1) ? 'z0' : 'z1'; edge = (side === 'z0') ? R.z0 : R.z1; inward = (side === 'z0') ? 1 : -1;
      } else {
        var dx0 = Math.abs(R.x0 - (B.x0 + B.x1) / 2), dx1 = Math.abs(R.x1 - (B.x0 + B.x1) / 2);
        side = (dx0 <= dx1) ? 'x0' : 'x1'; edge = (side === 'x0') ? R.x0 : R.x1; inward = (side === 'x0') ? 1 : -1;
      }
      var aLo = alongX ? R.x0 : R.z0, aHi = alongX ? R.x1 : R.z1;
      var at = Math.max(aLo + 0.5, Math.min(aHi - 0.5, aHi - 1.0));
      var steel = new T.MeshStandardMaterial({ color: 0xC9CED3, roughness: 0.22, metalness: 0.9 });
      var yTop = POOL.copeT, span = 0.50, n = 0;
      /* the rail's line in the ladder's own frame: u across the edge (+ = into
         the pool), y up; the deck anchor is behind the coping */
      var pts = [[-0.42, yTop], [-0.40, yTop + 0.55], [-0.24, yTop + 0.86], [0.02, yTop + 0.78], [0.10, yTop + 0.35],
                 [0.10, yTop - 0.20], [0.10, -0.95]];
      function world(u, y, s) {
        var a = at + s, d = edge + inward * u;
        return alongX ? new T.Vector3(a, y, d) : new T.Vector3(d, y, a);
      }
      [-span / 2, span / 2].forEach(function (s) {
        var cur = new T.CatmullRomCurve3(pts.map(function (p) { return world(p[0], p[1], s); }));
        var tube = new T.Mesh(new T.TubeGeometry(cur, 48, 0.021, 10, false), steel);
        tube.name = 'pool-ladder';
        grp.add(tube);
        n++;
      });
      /* three treads in the water */
      [-0.25, -0.52, -0.79].forEach(function (y) {
        var c = world(0.14, y, 0);
        var tr = new T.Mesh(new T.BoxGeometry(alongX ? span : 0.09, 0.022, alongX ? 0.09 : span), steel);
        tr.position.copy(c);
        tr.name = 'pool-ladder';
        grp.add(tr);
        n++;
      });
      return n;
    }
    /* THE LINK BESIDE THE POOL (core-v281): a small round icon at its corner,
       always there while the pool is, that opens the pool configurator -- no
       note, no 'read more' (Marek). The same will stand by the pergola. It is
       placed on screen each frame from the pool's corner, like the figure's
       handle. */
    var _poolLinkOn = false, _poolLinkUntil = 0, _poolTitleAsked = false;
    /* SHOWN FOR FIVE SECONDS, when the pool is put down, dragged or edited
       (core-v289, Marek) -- not standing there all the time */
    function _poolLinkFor(ms) { _poolLinkUntil = Date.now() + (ms || 5000); }
    function _poolLink() {
      var st = _stage(), host = (($('cf-body-handle') || {}).parentNode) || (st && st.renderer && st.renderer.domElement.parentNode);
      if (!host) return;
      var el = document.getElementById('cf-pool-link');
      if (!el) {
        /* A RED PILL WITH THE PRODUCT'S NAME (core-v285): POOL / BAZEN / ... in
           the page's own language, then a round icon at the pill's end -- red,
           the arrow white (Marek). The name is the rooms.pool key, which every
           language already has. */
        el = document.createElement('a');
        el.id = 'cf-pool-link';
        /* the store's own path for this language, and the product's own title in
           it -- the name follows the product, not a label of ours (core-v289) */
        var root0 = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/';
        el.href = root0 + 'products/pool';
        el.target = '_blank';
        el.rel = 'noopener';
        el.style.cssText = 'position:absolute;height:34px;transform:translate(-50%,-50%);border-radius:17px;background:#E4312B;' +
                           'box-shadow:0 2px 8px rgba(0,0,0,0.28);display:none;align-items:center;gap:8px;padding:0 3px 0 14px;' +
                           'z-index:24;text-decoration:none;color:#fff;font:700 13px/1 Montserrat, Arial, sans-serif;' +
                           'letter-spacing:0.04em;text-transform:uppercase;white-space:nowrap';
        var lab = document.createElement('span');
        lab.textContent = _t('rooms.pool', 'Pool');
        if (!_poolTitleAsked && global.fetch) {
          _poolTitleAsked = true;
          try {
            global.fetch(root0 + 'products/pool.js').then(function (r) { return r.ok ? r.json() : null; })
              .then(function (p) { if (p && p.title) { lab.textContent = p.title; _log('pool link: the product is called ' + p.title); } })
              .catch(function () {});
          } catch (eF) {}
        }
        var ico = document.createElement('span');
        ico.style.cssText = 'width:28px;height:28px;border-radius:50%;background:#C4211C;display:flex;align-items:center;justify-content:center';
        ico.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="#fff" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">' +
                        '<path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"/></svg>';
        el.appendChild(lab); el.appendChild(ico);
        host.appendChild(el);
      }
      el.style.display = (poolOn() && Date.now() < _poolLinkUntil) ? 'flex' : 'none';
      if (_poolLinkOn) return;
      _poolLinkOn = true;
      var T = global.THREE, v = T ? new T.Vector3() : null;
      function tick() {
        var st2 = _stage(), R = _poolRect(), el2 = document.getElementById('cf-pool-link');
        if (el2) el2.style.display = (R && Date.now() < _poolLinkUntil) ? 'flex' : 'none';
        if (el2 && v && st2 && st2.camera && st2.renderer && R && Date.now() < _poolLinkUntil) {
          v.set(R.x1 + POOL.cope + 0.35, 0.40, R.z0 - POOL.cope - 0.35).project(st2.camera);
          var cr = st2.renderer.domElement.getBoundingClientRect(), par = el2.offsetParent;
          var pr = par ? par.getBoundingClientRect() : { left: 0, top: 0 };
          el2.style.left = (cr.left - pr.left + (v.x + 1) * 0.5 * cr.width) + 'px';
          el2.style.top = (cr.top - pr.top + (1 - (v.y + 1) * 0.5) * cr.height) + 'px';
          el2.style.opacity = (v.z > -1 && v.z < 1) ? '' : '0';
        }
        _rafQuiet(tick);
      }
      _rafQuiet(tick);
    }
    /* the note on a drag, retired for the icon above; kept for reference */
    var _poolPopAt = 0;
    function _poolPop() {
      var now = Date.now();
      if (now - _poolPopAt < 5000) return;
      _poolPopAt = now;
      try {
        var st = _stage(), host = st && st.renderer && st.renderer.domElement.parentNode;
        if (!host) return;
        var el = document.getElementById('cf-pool-pop');
        if (!el) {
          el = document.createElement('div');
          el.id = 'cf-pool-pop';
          el.style.cssText = 'position:absolute;left:50%;bottom:96px;transform:translateX(-50%);z-index:40;background:#fff;' +
                             'border-radius:14px;box-shadow:0 4px 16px rgba(0,0,0,0.22);padding:10px 14px;display:flex;' +
                             'align-items:center;gap:12px;font:600 14px/1.2 Montserrat, Arial, sans-serif;color:#111';
          host.appendChild(el);
        }
        el.innerHTML = '';
        var tx = document.createElement('span');
        tx.textContent = _t('rooms.pool', 'Pool');
        var a = document.createElement('a');
        a.href = '/products/pool';
        a.target = '_blank';
        a.rel = 'noopener';
        a.textContent = _t('read_more', 'Read more');
        a.style.cssText = 'color:#fff;background:#111;border-radius:10px;padding:7px 12px;text-decoration:none';
        var x = document.createElement('span');
        x.textContent = '\u00D7';
        x.style.cssText = 'cursor:pointer;font-size:18px;padding:0 2px;color:#666';
        x.addEventListener('click', function () { el.style.display = 'none'; });
        el.appendChild(tx); el.appendChild(a); el.appendChild(x);
        el.style.display = 'flex';
        global.setTimeout(function () { if (Date.now() - _poolPopAt >= 5900) el.style.display = 'none'; }, 6000);
        _log('pool: the note to the pool configurator shown');
      } catch (e) { _log('pool pop-up: ' + (e && e.message)); }
    }

    /* -------------------------------------------------------------------------
       THE PERGOLA (core-v284), timber only, one for every product. Built the way
       the posts under a deep roof overhang are: 4x4 posts standing in black steel
       post bases, a ring of 4x4 beams on them joined by black steel connectors --
       a corner connector on each corner post, a tee where a cross beam meets the
       long beam over an intermediate post -- and slats across on top. The three
       connectors are the catalogue's (FAT00017 base, FAT00018 corner, FAT00019
       tee), drawn as the sleeves and open arms they are.

       Posts along the two long sides no more than 3 m apart; opposite
       intermediate posts carry a cross beam. The product calls this from its own
       terrace layer and bills the rows it gets back, so the bill stays with the
       product; the drawing is the same everywhere.
       ------------------------------------------------------------------------- */
    var PERG = { post: 0.100, h: 2.40, beam: 0.100, slat: [0.040, 0.060], slatGap: 0.40, postMax: 3.0,
                 timber: 'KVH10100', slatCode: 'KVH40060',   /* 4x4 dimensional lumber (core-v286) */ base: 'FAT00017', corner: 'FAT00018', tee: 'FAT00019',
                 screw: 'FAS60100', screwsPerConnector: 8, screwsPerSlat: 4, steel: 0x1D1E20 };
    function buildPergola(ctx, R, yTop, opt) {
      var T = ctx.THREE, g = ctx.group, P = PERG, o = opt || {};
      var w = R.x1 - R.x0, l = R.z1 - R.z0;
      var along = (o.side) ? (o.side === 'z0' || o.side === 'z1') : (w >= l);   /* the long beams run along x when true */
      var wood = new T.MeshStandardMaterial({ color: o.color != null ? o.color : 0xC9A06A, roughness: 0.85, metalness: 0.0 });
      var steel = new T.MeshStandardMaterial({ color: P.steel, roughness: 0.55, metalness: 0.45 });
      function box(sx, sy, sz, x, y, z, m) { var mm = new T.Mesh(new T.BoxGeometry(sx, sy, sz), m); mm.position.set(x, y, z); g.add(mm); return mm; }
      var L = along ? w : l, S = along ? l : w;             /* long and short spans */
      var n = Math.max(2, Math.ceil((L - P.post) / P.postMax) + 1), i, k;
      /* the posts: (a, b) in along/across terms, turned into x/z */
      function xz(a, b) { return along ? [a, b] : [b, a]; }
      var a0 = (along ? R.x0 : R.z0) + P.post / 2, a1 = (along ? R.x1 : R.z1) - P.post / 2;
      var b0 = (along ? R.z0 : R.x0) + P.post / 2, b1 = (along ? R.z1 : R.x1) - P.post / 2;
      var yPostTop = yTop + P.h, yBeam = yPostTop - P.beam / 2;
      var posts = [], corners = 0, tees = 0;
      for (i = 0; i < n; i++) {
        var a = a0 + (a1 - a0) * (i / (n - 1));
        [b0, b1].forEach(function (b) { posts.push({ a: a, b: b, end: (i === 0 || i === n - 1) }); });
      }
      /* THE POSTS STAND ON THE BUILDING'S OWN KIND OF FOOTING (core-v287): a
         ground screw, a precast pad or a cast pier, the same as the posts under a
         deep roof overhang, with the steel post base on top of it -- at the
         ground, under the deck, so the post passes through the terrace; the posts
         and their footings are part of the terrace's foundation (Marek). */
      var F = STATE.foundation || 'screws', groundY = 0, grey = new T.MeshStandardMaterial({ color: 0x9A9EA3, roughness: 0.6, metalness: 0.3 });
      var footTop = (F === 'pads') ? groundY + 0.05 : ((F === 'piers' || F === 'strips') ? groundY + 0.10 : groundY + 0.09);
      var PIERP = { w: 0.30, d: 0.90 }, feet = { screws: 0, pads: 0, piers: 0 };
      /* below the ground only while the base is being looked at, the way the
         house's footings are (core-v289, Marek: podzemni cast jen v edit mode) */
      function cutBox(sx, sy, sz, x, y, z, m) {
        var lo = y - sy / 2, hi = y + sy / 2;
        if (!_footFull()) { if (hi <= 0.001) return; if (lo < 0) { sy = hi; y = hi / 2; } }
        box(sx, sy, sz, x, y, z, m);
      }
      var yFoot = footTop + 0.036, postH = yPostTop - yFoot;
      posts.forEach(function (p) {
        var c = xz(p.a, p.b);
        setOutPoint((F === 'pads') ? 'pad' : ((F === 'piers' || F === 'strips') ? 'pier' : 'screw'), c[0], c[1]);
        if (F === 'pads') {
          cutBox(0.40, 0.15, 0.40, c[0], footTop - 0.075, c[1], grey); feet.pads++;
        } else if (F === 'piers' || F === 'strips') {
          cutBox(PIERP.w, PIERP.d + 0.10, PIERP.w, c[0], footTop - (PIERP.d + 0.10) / 2, c[1], grey); feet.piers++;
        } else {
          /* the house's own ground screw, flight and all, drawn by the product;
             the shortest the catalogue has, FGR00001 1200 x 76 (Marek) */
          if (typeof o.screw === 'function') o.screw(c[0], c[1], footTop);
          else cutBox(0.076, 1.20, 0.076, c[0], footTop - 0.60, c[1], grey);
          feet.screws++;
        }
        box(P.post, postH, P.post, c[0], yFoot + postH / 2, c[1], wood);
        /* the base: a plate and a short sleeve round the foot */
        box(0.150, 0.006, 0.150, c[0], footTop + 0.003, c[1], steel);
        box(P.post + 0.006, 0.180, P.post + 0.006, c[0], footTop + 0.006 + 0.090, c[1], steel);
        /* the head: a sleeve up the post's top 200, and open arms along the beams it takes */
        box(P.post + 0.006, 0.200, P.post + 0.006, c[0], yPostTop - 0.100, c[1], steel);
        var inward = (p.b === b0) ? 1 : -1, aDir = [];
        if (p.end) { aDir.push((p.a === a0) ? 1 : -1); corners++; }
        else { aDir.push(1, -1); tees++; }
        aDir.forEach(function (d) {
          var ca = xz(p.a + d * (P.post / 2 + 0.050), p.b);
          box(along ? 0.100 : P.beam + 0.006, P.beam + 0.006, along ? P.beam + 0.006 : 0.100, ca[0], yBeam, ca[1], steel);
        });
        var cb = xz(p.a, p.b + inward * (P.post / 2 + 0.050));
        box(along ? P.beam + 0.006 : 0.100, P.beam + 0.006, along ? 0.100 : P.beam + 0.006, cb[0], yBeam, cb[1], steel);
      });
      /* the beams: the two long ones, the two ends, and a cross beam at each pair of intermediate posts */
      var beamM = 0;
      [b0, b1].forEach(function (b) { var c = xz((a0 + a1) / 2, b); box(along ? L : P.beam, P.beam, along ? P.beam : L, c[0], yBeam, c[1], wood); beamM += L; });
      for (i = 0; i < n; i++) {
        var aC = a0 + (a1 - a0) * (i / (n - 1)), cC = xz(aC, (b0 + b1) / 2), spanC = (b1 - b0) - P.post;
        box(along ? P.beam : spanC, P.beam, along ? spanC : P.beam, cC[0], yBeam, cC[1], wood);
        beamM += spanC;
      }
      /* THE SLATS ACROSS THE TOP, CLEAR OF THE STEEL (core-v285): they sat on the
         beams at the post tops, straight through the connectors' sleeves and
         arms (Marek: pricne lamely koliduji se spoji a hranoly 4x4). They now
         rest above the arms' top edge and keep out of the length each connector
         takes along the beam -- a post's width plus its arms -- so none lands on
         a post head; the spacing is kept as near 400 as the free lengths allow. */
      var clearA = P.post / 2 + 0.100 + 0.015, yS = yPostTop + 0.004 + P.slat[1] / 2, slatL = S, slatM = 0, nS = 0, slatA = [];
      var postA = [];
      for (i = 0; i < n; i++) postA.push(a0 + (a1 - a0) * (i / (n - 1)));
      for (i = 0; i < n - 1; i++) {
        var fa = postA[i] + clearA + P.slat[0] / 2, fb = postA[i + 1] - clearA - P.slat[0] / 2;
        if (!(fb > fa)) continue;
        var cnt = Math.max(1, Math.round((fb - fa) / (P.slat[0] + P.slatGap)) + 1);
        for (k = 0; k < cnt; k++) {
          var aS = (cnt === 1) ? (fa + fb) / 2 : fa + (fb - fa) * (k / (cnt - 1)), cS = xz(aS, (b0 + b1) / 2);
          box(along ? P.slat[0] : slatL, P.slat[1], along ? slatL : P.slat[0], cS[0], yS, cS[1], wood);
          slatM += slatL; nS++;
          slatA.push(aS);
        }
      }
      /* THE FABRIC WOVEN THROUGH THE SLATS (core-v290): strips of a light beige
         cloth, half see-through, running across the slats -- over one, under the
         next, over the one after -- and each strip starting the other way from
         its neighbour, so the weave shows (Marek: latka cik cak mezi lamelami).
         Drawn as ribbons that hug each slat; its area is reported, and billed
         once the catalogue has a row for it. */
      var fabricM2 = 0, fabricStrips = 0;
      if (o.fabric && slatA.length >= 2) {
        slatA.sort(function (p, q) { return p - q; });
        var fMat = new T.MeshStandardMaterial({ color: 0xE6D9BC, roughness: 0.9, metalness: 0.0, transparent: true,
                                                opacity: 0.80, side: T.DoubleSide, depthWrite: false });   /* 80 percent cover (core-v294, Marek) */
        var fw = 0.45, fg = 0.10, span2 = (b1 - b0) - 0.20;
        var nF = Math.max(1, Math.floor((span2 + fg) / (fw + fg))), used = nF * fw + (nF - 1) * fg, bStart = (b0 + b1) / 2 - used / 2;
        var yUp = yS + P.slat[1] / 2 + 0.004, yDn = yS - P.slat[1] / 2 - 0.004, hug = P.slat[0] / 2 + 0.012;
        var kf, jf;
        for (kf = 0; kf < nF; kf++) {
          var bLo = bStart + kf * (fw + fg), bHi = bLo + fw, path = [];
          for (jf = 0; jf < slatA.length; jf++) {
            var yy = ((jf + kf) % 2 === 0) ? yUp : yDn;
            path.push([slatA[jf] - hug, yy]); path.push([slatA[jf] + hug, yy]);
          }
          var pos = [], idx = [], pl = 0, pi2;
          for (pi2 = 0; pi2 < path.length; pi2++) {
            var pa2 = path[pi2][0], py2 = path[pi2][1];
            var v1 = xz(pa2, bLo), v2 = xz(pa2, bHi);
            pos.push(v1[0], py2, v1[1], v2[0], py2, v2[1]);
            if (pi2 > 0) {
              var a0i = (pi2 - 1) * 2, b0i = pi2 * 2;
              idx.push(a0i, a0i + 1, b0i, a0i + 1, b0i + 1, b0i);
              pl += Math.hypot(path[pi2][0] - path[pi2 - 1][0], path[pi2][1] - path[pi2 - 1][1]);
            }
          }
          var geo = new T.BufferGeometry();
          geo.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
          geo.setIndex(idx);
          geo.computeVertexNormals();
          var fm = new T.Mesh(geo, fMat);
          fm.renderOrder = 3;
          fm.name = 'pergola-fabric';
          g.add(fm);
          fabricM2 += pl * fw; fabricStrips++;
        }
      }
      var postM = posts.length * postH, conn = posts.length + corners + tees;
      var rows = {};
      rows[P.timber] = Math.round((postM + beamM) * 100) / 100;
      rows[P.slatCode] = Math.round(slatM * 100) / 100;
      rows[P.base] = posts.length;
      rows[P.corner] = corners;
      if (tees) rows[P.tee] = tees;
      /* THE STEEL IS FIXED, AND THE BILL SAYS WITH WHAT (core-v293). The plain
         6x100 wood screw went into the steel before: 100 mm through a 100 mm
         post comes out the far side, and a wood screw is not made for shear in a
         connector. Now: a structural connector screw in every hole of a corner
         (8) and a tee (12); two bolts through each post and its base sleeve,
         which is what holds a post down when the wind lifts the roof; and under
         each base four fixings into what it stands on -- concrete screw anchors
         in a pad or a pier, bolts through the ground screw's flange. The slats
         keep the 6x100: that is wood into wood. */
      rows[P.screw] = nS * P.screwsPerSlat;
      rows['FAS50040'] = corners * 8 + tees * 12;
      rows['FAT10002'] = posts.length * 2;
      if (feet.screws) rows['FAT10001'] = feet.screws * 4;
      if (feet.pads || feet.piers) rows['FAS10075'] = (feet.pads + feet.piers) * 4;
      /* the footings, as the product's own keys for them */
      if (feet.screws) rows['FGR00001'] = feet.screws;
      if (feet.pads) rows['grd.padBlock'] = feet.pads;
      if (feet.piers) rows['grd.concrete'] = Math.round(feet.piers * PIERP.w * PIERP.w * (PIERP.d + 0.10) * 1000) / 1000;
      var log = 'pergola timber: ' + posts.length + ' posts 4x4 x ' + postH.toFixed(2) + ' (from the footing, through the terrace) + beams ' + (Math.round(beamM * 10) / 10) +
                ' m (' + P.timber + ' ' + (Math.round((postM + beamM) * 10) / 10) + ' m), ' + nS + ' slats ' + (Math.round(slatM * 10) / 10) + ' m ' + P.slatCode +
                '; steel ' + posts.length + ' x ' + P.base + ' post base, ' + corners + ' x ' + P.corner + ' corner, ' + tees + ' x ' + P.tee +
                ' tee; fixings: ' + rows['FAS50040'] + ' x FAS50040 connector screw (8 a corner, 12 a tee), ' +
                rows['FAT10002'] + ' x FAT10002 bolt through the posts (2 each), ' +
                (feet.screws ? (rows['FAT10001'] + ' x FAT10001 bolt into the screw flanges (4 a base)') : (rows['FAS10075'] + ' x FAS10075 anchor into the concrete (4 a base)')) +
                ', ' + rows[P.screw] + ' x ' + P.screw + ' (' + P.screwsPerSlat + ' a slat)' +
                '; footings on ' + F + ': ' + (feet.screws ? (feet.screws + ' x ground screw FGR00001 1200x76, no U holder') : '') +
                (feet.pads ? (feet.pads + ' x precast pad FCO00009') : '') + (feet.piers ? (feet.piers + ' cast pier(s) FCO00002') : '');
      if (fabricStrips) {
        rows['TST00003'] = Math.round(fabricM2 * 100) / 100;
        log += '; fabric TST00003 ' + fabricStrips + ' strip(s) woven through the slats, ' + (Math.round(fabricM2 * 10) / 10) + ' m2 billed';
      }
      return { rows: rows, log: log, posts: posts.length, fabricM2: fabricM2 };
    }

    function _bodyY() { return typeof __PROD._bodyFloor === 'function' ? Number(__PROD._bodyFloor(_bodyX(), _bodyZ())) || 0 : 0; }
    function _bodyPlace(x,z) { return typeof __PROD._bodyPlace === 'function' ? __PROD._bodyPlace(x,z) : {x:x,z:z}; }
    function _defaultBodyX() { return STATE.origin.x + STATE.footprint.w / 2 + 0.8; }
    function _defaultBodyZ() { return STATE.origin.z + STATE.footprint.l / 2 - 0.5; }
    function _bodyX() { return (STATE.bodyX != null) ? STATE.bodyX : _defaultBodyX(); }
    function _bodyZ() { return (STATE.bodyZ != null) ? STATE.bodyZ : _defaultBodyZ(); }

    function _findPerson() {
      var st = _stage();
      if (!st || !st.group) return null;
      for (var i = 0; i < st.group.children.length; i++) {
        var c = st.group.children[i];
        if (c.userData && c.userData.partType === 'person') return c;
      }
      return null;
    }

    function _buildPerson(T, g) {
      var person = new T.Group();
      person.position.set(_bodyX(), _bodyY(), _bodyZ());
      person.userData.partType = 'person';
      person.userData.arSkip = true;
      person.visible = (STATE.bodyVisible === true);
      g.add(person);

      if (_personScene) { person.add(_personScene); return; }
      if (!STATE.bodyVisible) return;
      /* THE GLB ARRIVES AFTER THE BUILD IT WAS ASKED FOR. Rather than force
         another rebuild, the loader hunts down whichever empty person group is in
         the scene when it resolves -- there may have been three rebuilds by then
         and the one that started this is long gone. */
      _loadPersonScene().then(function (scene) {
        var p = _findPerson();
        if (p && p.children.length === 0) { p.add(scene); }
      }).catch(function () {});
    }

    function toggleBody() {
      STATE.bodyVisible = !STATE.bodyVisible;
      var btn = $('cf-btn-body');
      if (btn) {
        btn.setAttribute('aria-pressed', STATE.bodyVisible ? 'true' : 'false');
        /* NO FADING. Half opacity is how this rail says "not available yet", so a
           figure that was merely switched off looked like a button that had
           failed to load -- one appearance for two different states. Whether the
           model has arrived is a question for the log, which answers it on the
           figure line. */
      }
      var h = $('cf-body-handle');
      if (h) h.classList.toggle('active', STATE.bodyVisible);
      rebuild();
      savePref();
      _log('scale figure ' + (STATE.bodyVisible ? 'on' : 'off') +
           ' at ' + _bodyX().toFixed(2) + ', ' + _bodyZ().toFixed(2));
      return STATE.bodyVisible;
    }

    /* THE HANDLE IS A DOM ELEMENT OVER THE CANVAS, not a 3D object. A ghost at
       18% opacity is almost impossible to grab in the scene, and it would fight
       the orbit controls for the same drag. A disc tracked to the figure's
       position each frame is grabbable at any zoom. */
    var _bodyBound = false;
    function _initBodyHandle() {
      var h = $('cf-body-handle');
      if (!h || _bodyBound) return;
      _bodyBound = true;
      var dragging = false, plane = null, rc = null, ndc = null, hit = null;

      function toWorld(cx, cy) {
        var st = _stage(), T = global.THREE;
        if (!st || !st.camera || !st.renderer || !T) return null;
        var r = st.renderer.domElement.getBoundingClientRect();
        if (!plane) plane = new T.Plane(new T.Vector3(0, 1, 0), 0);
        if (!rc) rc = new T.Raycaster();
        if (!ndc) ndc = new T.Vector2();
        if (!hit) hit = new T.Vector3();
        ndc.x = ((cx - r.left) / r.width) * 2 - 1;
        ndc.y = -((cy - r.top) / r.height) * 2 + 1;
        rc.setFromCamera(ndc, st.camera);
        return rc.ray.intersectPlane(plane, hit);
      }

      h.addEventListener('pointerdown', function (e) {
        if (!STATE.bodyVisible) return;
        e.preventDefault();
        dragging = true;
        try { h.setPointerCapture(e.pointerId); } catch (x) {}
        var st = _stage();
        if (st && st.controls) st.controls.enabled = false;
      });
      h.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var p = toWorld(e.clientX, e.clientY);
        if (!p) return;
        p = _bodyPlace(p.x, p.z);
        STATE.bodyX = p.x;
        STATE.bodyZ = p.z;
        var person = _findPerson();
        if (person) person.position.set(p.x, _bodyY(), p.z);
      });
      function up(e) {
        if (!dragging) return;
        dragging = false;
        try { h.releasePointerCapture(e.pointerId); } catch (x) {}
        var st = _stage();
        if (st && st.controls) st.controls.enabled = true;
        savePref();
      }
      h.addEventListener('pointerup', up);
      h.addEventListener('pointercancel', up);
    }

    var _bodyTracking = false;
    function _startBodyTracker() {
      if (_bodyTracking) return;
      _bodyTracking = true;
      var T = global.THREE;
      if (!T) return;
      var v = new T.Vector3();
      function tick() {
        var h = $('cf-body-handle'), st = _stage();
        /* no figure beside a sample, so no handle for one (core-v339) */
        if (h && _sampleShown) {
          h.style.opacity = '0'; h.style.pointerEvents = 'none'; h.__cfSampleHid = true;
          _rafQuiet(tick);
          return;
        }
        if (h && h.__cfSampleHid) { h.style.pointerEvents = ''; h.__cfSampleHid = false; }
        if (h && st && st.camera && st.renderer && STATE.bodyVisible) {
          v.set(_bodyX(), _bodyY() + 1.20, _bodyZ()).project(st.camera);
          var cr = st.renderer.domElement.getBoundingClientRect();
          var par = h.offsetParent;
          var pr = par ? par.getBoundingClientRect() : { left: 0, top: 0 };
          h.style.left = (cr.left - pr.left + (v.x + 1) * 0.5 * cr.width) + 'px';
          h.style.top  = (cr.top - pr.top + (1 - (v.y + 1) * 0.5) * cr.height) + 'px';
          h.style.opacity = (v.z > -1 && v.z < 1) ? '' : '0';
        }
        _rafQuiet(tick);
      }
      _rafQuiet(tick);
    }

    /* -------------------------------------------------------------------------
       THE SAMPLE'S DRAG POINTS AND DIMENSIONS (core-v340).

       The block on the STRUCTURE step is sized by dragging it, not by sliders
       (Marek: vizualne podobny blok ... s drag pointy, zmena rozmeru po 10 mm /
       1 inch). Three points: the end face for the length, the side for the
       width, the top for the height; each moves along its own axis only. The
       block stays centred, so a point dragged out by 10 mm makes the block 20
       mm longer -- the point stays on the face it belongs to. The height grows
       from the ground up. The camera looks from the +x, +z side, so the points
       and the lines are put on the faces it sees.

       The points are the scale figure's grip, the same class and so the same
       look (copied, not redrawn: the look was settled on the tiny house). The
       dimension lines are drawn in the scene beside the block, with the figure
       on each in a label, and they follow while dragging. While a point is
       held only the sample is redrawn -- rebuilding the whole house on every
       move would stall the tablet -- and the full rebuild runs once, on
       release, which also saves the size with the project.
       ------------------------------------------------------------------------- */
    var SMP_BACKOFF = 1.8;
    var _smp = { box: null, set: null, drag: null, last: '', tracking: false, raf: 0, v: null, off: { x: 0, z: 0 } };
    /* EACH SIDE ON ITS OWN, FROM ITS CORNER (core-v343). The points sat on the
       middles of the edges and the block grew both ways from its centre, so a
       drag moved two faces (Marek: kazdy meni jen jeden rozmer, dat je na rohy
       bloku; stale se neda tahnout kazda strana zvlast). Now each point sits on
       a corner and moves ONE face: the length point the right end, the width
       point the back face (core-v345), the height point the top. The opposite face stays
       where it is -- the sample is shifted under the product's centre by what
       the moving face took, and the shift holds while the step is open. */
    var SMP_KEYS = ['l', 'w', 'h'];
    /* WHICH SIZES THE FINGER MAY CHANGE (core-v584, RANCH: a timber section
       is chosen by its depth only). __PROD.sampleKeys -- a list or a function
       giving one -- names the points shown; every size keeps its dimension
       line and its label. Without it all three, as before. */
    function _smpPts() {
      var K = null;
      try { K = (typeof __PROD.sampleKeys === 'function') ? __PROD.sampleKeys() : __PROD.sampleKeys; } catch (e) { K = null; }
      return (K && K.length) ? K : SMP_KEYS;
    }
    /* THE ONE STEP (core-v344): 10 mm, or 2 in in imperial units -- and it is
       taken here only, on the size itself (Marek: jen jedine pravidlo) */
    function _smpStep() { return STATE.isMetric ? 0.010 : 0.0508; }
    function _smpFmt(v) { return STATE.isMetric ? (Math.round(v * 1000) + ' mm') : fmtLen(v); }
    function _smpName(k) { return k === 'l' ? 'length' : (k === 'w' ? 'width' : 'height'); }
    function _sysKey() {
      try { return (__PROD._sampleKey && __PROD._sampleKey()) || ''; } catch (e) { return ''; }
    }
    function _smpRead() {
      _smp.box = null; _smp.set = null;
      try {
        if (typeof __PROD.sampleSize === 'function') {
          var b = __PROD.sampleSize();
          if (b && b.l > 0 && b.w > 0 && b.h > 0) _smp.box = { x: (b.x || 0) + _smp.off.x, z: (b.z || 0) + _smp.off.z, cx: b.x || 0, cz: b.z || 0, l: b.l, w: b.w, h: b.h };
        }
        if (typeof __PROD.sampleSet === 'function') _smp.set = __PROD.sampleSet;
      } catch (eR) { _smp.box = null; }
    }
    function _smpGap() { var b = _smp.box; return b ? Math.max(0.03, 0.10 * Math.max(b.l, b.w, b.h)) : 0.05; }
    /* where a point sits, the point it is measured from, and its axis */
    function _smpAnchor(k, gap) {
      var b = _smp.box, g = (gap != null) ? gap : _smpGap();
      /* the length and width points ride the top edge, so they stand clear of
         the dimension lines along the bottom; each is measured from a centre
         at its own height, or the tilt of the view would bias the drag */
      /* on the corners the camera sees, each measured from the face that
         stays: the length from the left end, the width from the back face,
         the height from the ground */
      /* EXACTLY ON THE CORNER (core-v344): no gap out along the axis any more
         -- a point off the corner read as belonging to nothing (Marek: drag
         pointy nejsou presne na rozich) */
      /* AT THE END OF ITS OWN DIMENSION LINE (core-v345, Marek: drag pointy
         ke kotam, na rohy bloku, ktere dany point meni): the length line runs
         along the front at the bottom, so the length point is its right end,
         the bottom front right corner; the width line runs along the right
         side, so the width point is its far end, the bottom back right
         corner, and it moves the back face; the height line stands at the
         front left, so the height point is its top, the top front left
         corner. The faces that stay: the left end, the front, the bottom. */
      var x0 = b.x - b.l / 2, x1 = b.x + b.l / 2, z0 = b.z - b.w / 2, z1 = b.z + b.w / 2;
      g = 0;
      if (k === 'l') return { p: [x1, 0, z1], c: [x0, 0, z1], ax: [1, 0, 0] };
      if (k === 'w') return { p: [x1, 0, z0], c: [x1, 0, z1], ax: [0, 0, -1] };
      return { p: [x0, b.h, z1], c: [x0, 0, z1], ax: [0, 1, 0] };
    }
    /* a dimension line: its two ends, and the way back to the block */
    function _smpLine(k) {
      var b = _smp.box, o = _smpGap() * 1.3;
      var x0 = b.x - b.l / 2, x1 = b.x + b.l / 2, z0 = b.z - b.w / 2, z1 = b.z + b.w / 2;
      if (k === 'l') return { a: [x0, 0, z1 + o], b: [x1, 0, z1 + o], back: [0, 0, -o] };
      if (k === 'w') return { a: [x1 + o, 0, z0], b: [x1 + o, 0, z1], back: [-o, 0, 0] };
      return { a: [x0 - o, 0, z1], b: [x0 - o, b.h, z1], back: [o, 0, 0] };
    }
    function _smpKoty(T, g, shifted) {
      if (!_smp.box) return;
      var pts = [], i;
      for (i = 0; i < SMP_KEYS.length; i++) {
        var L = _smpLine(SMP_KEYS[i]), A = L.a, B = L.b, bk = L.back, tk = 0.25;
        pts.push(A[0], A[1], A[2], B[0], B[1], B[2]);
        /* the extension lines, from the block out to the dimension line */
        pts.push(A[0] + bk[0], A[1] + bk[1], A[2] + bk[2], A[0] - bk[0] * tk, A[1] - bk[1] * tk, A[2] - bk[2] * tk);
        pts.push(B[0] + bk[0], B[1] + bk[1], B[2] + bk[2], B[0] - bk[0] * tk, B[1] - bk[1] * tk, B[2] - bk[2] * tk);
      }
      var geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.BufferAttribute(new Float32Array(pts), 3));
      var ln = new T.LineSegments(geo, new T.LineBasicMaterial({ color: 0x1A1C1D, transparent: true, opacity: 0.85, depthTest: false }));
      ln.renderOrder = 60;
      ln.name = 'structure-sample-koty';
      /* the lines are made in world terms; inside a shifted sample group they
         are shifted back, so they stay on the block */
      if (shifted || (g.position && (g.position.x || g.position.z))) ln.position.set(-_smp.off.x, 0, -_smp.off.z);
      ln.userData.arSkip = true;
      g.add(ln);
    }
    function _smpScreen(p) {
      var st = _stage(), T = global.THREE;
      if (!st || !st.camera || !st.renderer || !T) return null;
      if (!_smp.v) _smp.v = new T.Vector3();
      _smp.v.set(p[0], p[1], p[2]).project(st.camera);
      var cr = st.renderer.domElement.getBoundingClientRect();
      return { x: cr.left + (_smp.v.x + 1) * 0.5 * cr.width, y: cr.top + (1 - (_smp.v.y + 1) * 0.5) * cr.height, z: _smp.v.z };
    }
    function _smpHost() {
      var st = _stage();
      return (($('cf-body-handle') || {}).parentNode) || (st && st.renderer && st.renderer.domElement.parentNode) || null;
    }
    function _smpEl(k) {
      var id = 'cf-smp-h-' + k, h = document.getElementById(id), host = _smpHost();
      if (h || !host) return h;
      h = document.createElement('div');
      h.id = id;
      h.className = 'cf-body-handle active';
      h.setAttribute('role', 'button');
      h.style.position = 'absolute';
      h.style.touchAction = 'none';
      h.style.pointerEvents = 'auto';
      h.style.display = 'none';
      host.appendChild(h);
      _smpBind(h, k);
      return h;
    }
    function _smpLab(k) {
      var id = 'cf-smp-lab-' + k, el = document.getElementById(id), host = _smpHost();
      if (el || !host) return el;
      el = document.createElement('div');
      el.id = id;
      el.style.cssText = 'position:absolute;transform:translate(-50%,-50%);padding:1px 7px;border-radius:6px;white-space:nowrap;' +
        'background:rgba(255,255,255,0.96);box-shadow:0 1px 4px rgba(0,0,0,0.12);z-index:8;display:none;' +
        'font:700 12px/1.35 Montserrat,sans-serif;color:#1A1C1D;pointer-events:none;';
      host.appendChild(el);
      return el;
    }
    function _smpBackOff(st) {
      var cam = st.persCam, ctl = st.persControls;
      if (!cam || !ctl) return;
      cam.position.sub(ctl.target).multiplyScalar(SMP_BACKOFF).add(ctl.target);
      ctl.maxDistance = ctl.maxDistance * SMP_BACKOFF;
      cam.far = cam.far * SMP_BACKOFF;
      cam.updateProjectionMatrix();
      ctl.update();
    }
    /* only the sample, redrawn while a point is held */
    function _smpRedraw() {
      _smp.raf = 0;
      var st = _stage(), T = global.THREE;
      if (!st || !st.group || !T) return;
      var sg = st.group.getObjectByName('structure-sample');
      if (!sg) return;
      var i;
      for (i = sg.children.length - 1; i >= 0; i--) {
        var c = sg.children[i];
        c.traverse(function (o) { if (o.geometry) o.geometry.dispose(); if (o.material && o.material.dispose) o.material.dispose(); });
        sg.remove(c);
      }
      try { __PROD.structureSample({ THREE: T, group: sg, STATE: STATE, log: function () {}, marks: [], item: null }); } catch (eD) { _log('sample redraw threw: ' + (eD && eD.message)); }
      sg.position.set(_smp.off.x, 0, _smp.off.z);
      _smpRead();
      _smpKoty(T, sg, true);
    }
    /* WHERE THE FINGER IS ALONG THE POINT'S AXIS: the ray under the finger
       meets a plane that holds the axis and faces the camera, and the hit is
       read off along the axis -- exact at any tilt, where a screen-space guess
       drifts as the block comes towards the camera */
    function _smpAxisT(e, A) {
      var st = _stage(), T = global.THREE;
      if (!st || !st.camera || !st.renderer || !T) return null;
      var r = st.renderer.domElement.getBoundingClientRect();
      var ndc = new T.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
      var rc = new T.Raycaster();
      rc.setFromCamera(ndc, st.camera);
      var ax = new T.Vector3(A.ax[0], A.ax[1], A.ax[2]), c = new T.Vector3(A.c[0], A.c[1], A.c[2]);
      var vd = rc.ray.direction.clone();
      var n = vd.sub(ax.clone().multiplyScalar(vd.dot(ax)));
      if (n.lengthSq() < 1e-8) return null;
      n.normalize();
      var pl = new T.Plane().setFromNormalAndCoplanarPoint(n, c);
      var hit = rc.ray.intersectPlane(pl, new T.Vector3());
      if (!hit) return null;
      return hit.sub(c).dot(ax);
    }
    function _smpBind(h, k) {
      h.addEventListener('pointerdown', function (e) {
        if (!_smp.box || !_smp.set) return;
        e.preventDefault(); e.stopPropagation();
        var gp = 0, A0 = _smpAnchor(k, gp);
        _smp.drag = { k: k, from: _smp.box[k], gap: gp, moved: false, A: A0,
                      fix: (k === 'l') ? (_smp.box.x - _smp.box.l / 2) : ((k === 'w') ? (_smp.box.z + _smp.box.w / 2) : 0) };
        try { h.setPointerCapture(e.pointerId); } catch (x) {}
        var st = _stage();
        if (st && st.controls) st.controls.enabled = false;
      });
      h.addEventListener('pointermove', function (e) {
        var D = _smp.drag;
        if (!D || D.k !== k || !_smp.box) return;
        var t = _smpAxisT(e, D.A);
        if (t == null) return;
        var dim = t - D.gap, step = _smpStep();
        dim = Math.round(dim / step) * step;
        if (!(dim > 0) || Math.abs(dim - _smp.box[k]) < 0.0005) return;
        var got = dim;
        try { got = _smp.set(k, dim); } catch (eS) { return; }
        if (!(got > 0) || Math.abs(got - _smp.box[k]) < 0.0005) return;
        _smp.box[k] = got;
        /* the face that stays, stays: the sample slides by half of what the
           moving face took */
        if (k === 'l') { _smp.off.x = D.fix + got / 2 - _smp.box.cx; _smp.box.x = D.fix + got / 2; }
        if (k === 'w') { _smp.off.z = D.fix - got / 2 - _smp.box.cz; _smp.box.z = D.fix - got / 2; }
        D.moved = true;
        if (!_smp.raf) _smp.raf = (global.requestAnimationFrame || global.setTimeout)(_smpRedraw, 16);
      });
      function up(e) {
        var D = _smp.drag;
        if (!D || D.k !== k) return;
        _smp.drag = null;
        try { h.releasePointerCapture(e.pointerId); } catch (x) {}
        var st = _stage();
        if (st && st.controls) st.controls.enabled = true;
        if (!D.moved) return;
        _smp.last = _smpName(k) + ' ' + _smpFmt(D.from) + ' -> ' + _smpFmt(_smp.box ? _smp.box[k] : D.from);
        savePref();
        rebuild();
      }
      h.addEventListener('pointerup', up);
      h.addEventListener('pointercancel', up);
    }
    /* the points and the labels ride on the model, every frame, while the
       sample is up; with no sample they are gone */
    function _smpTrack() {
      if (_smp.tracking) return;
      _smp.tracking = true;
      function tick() {
        var on = !!(_sampleShown && _smp.box && _smp.set && STATE.view === '3d'), i;
        for (i = 0; i < SMP_KEYS.length; i++) {
          var k = SMP_KEYS[i], h = _smpEl(k), lb = _smpLab(k);
          if (!h || !lb) continue;
          if (!on) { h.style.display = 'none'; lb.style.display = 'none'; continue; }
          var par = h.offsetParent || h.parentNode, pr = par ? par.getBoundingClientRect() : { left: 0, top: 0 };
          var s = _smpScreen(_smpAnchor(k, _smp.drag && _smp.drag.k === k ? _smp.drag.gap : null).p);
          if (_smpPts().indexOf(k) < 0) { h.style.display = 'none'; s = null; }
          if (s) {
            h.style.display = 'block';
            h.style.left = (s.x - pr.left) + 'px';
            h.style.top = (s.y - pr.top) + 'px';
            h.style.opacity = (s.z > -1 && s.z < 1) ? '' : '0';
          }
          var L = _smpLine(k), m = _smpScreen([(L.a[0] + L.b[0]) / 2, (L.a[1] + L.b[1]) / 2, (L.a[2] + L.b[2]) / 2]);
          if (m) {
            lb.style.display = 'block';
            lb.textContent = _smpFmt(_smp.box[k]);
            lb.style.left = (m.x - pr.left) + 'px';
            lb.style.top = (m.y - pr.top) + 'px';
          }
        }
        _rafQuiet(tick);
      }
      _rafQuiet(tick);
    }

    /* -------------------------------------------------------------------------
       AR. Lifted from the tiny lib, minus the parts that belonged to that
       product. The chain is: merge everything on screen into one GLB, PUT it to
       the worker, turn the viewer URL it hands back into a QR code, and show that
       QR in a modal for the customer to scan with their phone.

       WHY IT MERGES RATHER THAN EXPORTS THE SCENE. A configurator scene is
       thousands of small meshes -- every stud its own box -- and a GLB with that
       many nodes is slow to write, slow to upload and slow to open on a phone.
       Meshes are bucketed by material, baked into world space and written as one
       mesh per bucket, which turns thousands of nodes into a handful.

       EVERY AR SURFACE IS DOUBLE-SIDED. Single-sided walls render from outside
       only, so standing inside the model you cannot see out through a window --
       the far wall is culled and the opening reads as solid.

       WHAT IS LEFT OUT: the scale figure and anything marked arSkip. A ghost at
       18% opacity exported into AR is a grey smear standing in the customer's
       garden.
       ------------------------------------------------------------------------- */
    var AR_WORKER_FALLBACK = 'https://soft-art-55b9.long-cloud-d006.workers.dev';
    var AR_TONE = 0.7;
    var _arBusy = false;
    var _arNote = 'not started';

    function _deent(s) {
      if (!s) return s;
      var d = document.createElement('textarea');
      d.innerHTML = String(s);
      return d.value || String(s);
    }

    function _ensureGLTFExporter() {
      if (global.GLTFExporter) return Promise.resolve();
      if (_ensureGLTFExporter._p) return _ensureGLTFExporter._p;
      _ensureGLTFExporter._p = import('https://esm.sh/three@0.160.0/examples/jsm/exporters/GLTFExporter.js')
        .then(function (mod) {
          global.GLTFExporter = mod.GLTFExporter || (mod.default && mod.default.GLTFExporter) || mod.default;
          if (!global.GLTFExporter) throw new Error('GLTFExporter import returned no export');
          _log('[ar] GLTFExporter loaded dynamically');
        })
        .catch(function (e) {
          _ensureGLTFExporter._p = null;
          throw new Error('Could not load GLTFExporter: ' + (e && e.message ? e.message : e));
        });
      return _ensureGLTFExporter._p;
    }

    /* WHAT THE RENDERER CUTS, AR CUTS. A clipping plane on the renderer is a
       screen effect; the exporter walks the geometry and would hand AR the whole
       building with the section gone. So every triangle bound for the GLB is
       clipped by the same planes first: a triangle wholly outside is dropped,
       wholly inside kept, and one the plane crosses becomes the one or two
       triangles of its kept part, the new corners interpolated on the edges.
       The bodies stay open where the plane passes through them, as they are on
       screen. Nothing here knows what product asked for the cut. */
    var _arGroundPl = null, _arGroundCut = 0;
    function _arClipTris(pos, nrm, planes) {
      if (!planes || !planes.length) return { pos: pos, nrm: nrm };
      var pq;
      for (pq = 0; pq < planes.length; pq++) {
        var pl = planes[pq], nx = pl.normal.x, ny = pl.normal.y, nz = pl.normal.z, dd = pl.constant;
        var outP = [], outN = [], i;
        function side(i0) { return nx * pos[i0] + ny * pos[i0 + 1] + nz * pos[i0 + 2] + dd; }
        function lerp(i0, i1, t) {
          var P = [pos[i0] + (pos[i1] - pos[i0]) * t, pos[i0 + 1] + (pos[i1 + 1] - pos[i0 + 1]) * t,
                   pos[i0 + 2] + (pos[i1 + 2] - pos[i0 + 2]) * t];
          var N = [nrm[i0] + (nrm[i1] - nrm[i0]) * t, nrm[i0 + 1] + (nrm[i1 + 1] - nrm[i0 + 1]) * t,
                   nrm[i0 + 2] + (nrm[i1 + 2] - nrm[i0 + 2]) * t];
          return { p: P, n: N };
        }
        function push(v) { outP.push(v.p[0], v.p[1], v.p[2]); outN.push(v.n[0], v.n[1], v.n[2]); }
        function at(i0) { return { p: [pos[i0], pos[i0 + 1], pos[i0 + 2]], n: [nrm[i0], nrm[i0 + 1], nrm[i0 + 2]] }; }
        for (i = 0; i + 8 < pos.length; i += 9) {
          var a = i, b = i + 3, c = i + 6;
          var sa = side(a), sb = side(b), sc = side(c);
          var ins = (sa >= 0 ? 1 : 0) + (sb >= 0 ? 1 : 0) + (sc >= 0 ? 1 : 0);
          if (ins === 3) { push(at(a)); push(at(b)); push(at(c)); continue; }
          if (ins === 0) continue;
          /* the kept polygon: walk the edges, keep inside corners, add the
             crossings -- three or four corners, fanned into triangles */
          var idx = [a, b, c], sd = [sa, sb, sc], poly = [], k;
          for (k = 0; k < 3; k++) {
            var i0 = idx[k], i1 = idx[(k + 1) % 3], s0 = sd[k], s1 = sd[(k + 1) % 3];
            if (s0 >= 0) poly.push(at(i0));
            if ((s0 >= 0) !== (s1 >= 0)) poly.push(lerp(i0, i1, s0 / (s0 - s1)));
          }
          for (k = 1; k + 1 < poly.length; k++) { push(poly[0]); push(poly[k]); push(poly[k + 1]); }
        }
        pos = outP; nrm = outN;
      }
      return { pos: pos, nrm: nrm };
    }

    function _arMerge(sourceGroup, options) {
      options = options || {};
      return new Promise(function (resolve, reject) {
        var T = global.THREE, Exporter = global.GLTFExporter;
        if (!T || !Exporter || !sourceGroup) { reject(new Error('THREE / exporter / source missing')); return; }

        var buckets = {}, nrmMat = new T.Matrix3(), v = new T.Vector3();
        /* A LEAF THAT MOVES STAYS A PIECE OF ITS OWN (core-v629, Marek: muzou
           se automaticky otevirat dvere? I HS portaly? Pri prochazeni). Merged
           by material, a door leaf was welded into the same body as every
           other board of its colour and nothing could ever swing it. A mesh
           (or a group above it) that carries userData.arLeaf goes out as a node
           of its own instead, its origin on the hinge, with what the walk-
           through page needs to open it in the node's extras. Nothing changes
           on screen or in the AR viewers: the leaf is drawn where it was.
             arLeaf, in the coordinates of the parent the product adds it to:
               { type: 'swing', hinge: [x, y, z], dir: +1 | -1 (the turn about
                 +Y that opens it further), ajar: rad drawn open now, max: rad }
               { type: 'slide', dir: [x, y, z] (the way it opens), at: m drawn
                 open now, travel: m, shift: [x, y, z] (stepped aside first,
                 an HS sash clearing the fixed pane) }
               { type: 'lift', hinge: [x, y, z] the top of the curtain, min:
                 the share of its drawn drop left when rolled up } (core-v630,
                 Marek: at se vytahuji i zaluzie pri prochazeni)
               ext: true and out: [x, y, z] (outward) on a unit in an outer wall
           Meshes that share one arLeaf object move as one (a leaf and its
           astragal). */
        var matCache = {}, leafSets = [], leafMap = (typeof Map === 'function') ? new Map() : null;

        function bucketFor(mat, into) {
          into = into || buckets;
          var col = (mat.color ? '#' + mat.color.getHexString() : 'fff');
          var key = col + '|' + (mat.transparent ? 'a' + (mat.opacity || 1) : 'o') +
                    '|' + (mat.roughness != null ? mat.roughness : 0.8) +
                    '|' + (mat.metalness != null ? mat.metalness : 0) +
                    '|' + (mat.side === T.BackSide ? 'back' : 'both');
          if (!into[key] && matCache[key]) {
            into[key] = { mat: matCache[key].mat, pos: [], nrm: [], glass: matCache[key].glass };
          }
          if (!into[key]) {
            /* GLASS STAYS GLASS IN AR: a transparent material goes out as
               alphaMode BLEND with its opacity in the base colour's alpha and no
               depth write, which is what the viewers need to draw the room
               behind a window (Marek: through the glass you must see out) */
            var seeThrough = !!mat.transparent && (mat.opacity != null ? mat.opacity : 1) < 0.95;
            var c = new T.MeshStandardMaterial({
              color: mat.color ? mat.color.clone().multiplyScalar(AR_TONE) : 0xaaaaaa,
              roughness: (mat.roughness != null ? mat.roughness : 0.8),
              metalness: (mat.metalness != null ? mat.metalness : 0),
              transparent: seeThrough,
              opacity: seeThrough ? (mat.opacity != null ? mat.opacity : 0.3) : 1,
              depthWrite: !seeThrough
            });
            /* A FACE DRAWN FROM ONE SIDE STAYS ONE-SIDED (core-v275): the pool's
               liner is seen from inside only, which is what makes it a hole in
               the ground rather than a box standing under it */
            c.side = (mat.side === T.BackSide) ? T.BackSide : T.DoubleSide;
            c.name = seeThrough ? 'glass' : ('m_' + col.replace('#', ''));
            into[key] = { mat: c, pos: [], nrm: [], glass: seeThrough };
            matCache[key] = { mat: c, glass: seeThrough };
          }
          return into[key];
        }
        function v3(a) { return (a && a.length >= 3) ? new T.Vector3(+a[0] || 0, +a[1] || 0, +a[2] || 0) : null; }
        function leafSetFor(owner) {
          var info = owner.userData.arLeaf, set = leafMap ? leafMap.get(info) : null, k;
          if (!set && !leafMap) for (k = 0; k < leafSets.length; k++) if (leafSets[k].info === info) { set = leafSets[k]; break; }
          if (set) return set;
          var par = owner.parent || owner, Mp = par.matrixWorld;
          set = { info: info, buckets: {}, pivot: null, dirW: null, shiftW: null, outW: null };
          if (info.type === 'swing' || info.type === 'lift') set.pivot = (v3(info.hinge) || new T.Vector3()).applyMatrix4(Mp);
          else set.pivot = new T.Vector3().setFromMatrixPosition(owner.matrixWorld);
          if (info.type === 'slide' && v3(info.dir)) set.dirW = v3(info.dir).transformDirection(Mp);
          if (v3(info.shift)) { var shL = v3(info.shift), shN = shL.length(); set.shiftW = shN > 1e-6 ? shL.transformDirection(Mp).multiplyScalar(shN) : null; }
          if (v3(info.out)) set.outW = v3(info.out).transformDirection(Mp);
          leafSets.push(set);
          if (leafMap) leafMap.set(info, set);
          return set;
        }

        sourceGroup.updateMatrixWorld(true);
        var arCut = false;
        _arGroundCut = 0;
        sourceGroup.traverse(function (node) {
          if (!node.isMesh || !node.geometry || !node.material) return;
          /* a mesh made for AR only is hidden on screen and goes to the export */
          if (!node.visible && !(node.userData && node.userData.arOnly)) return;
          var m0 = Array.isArray(node.material) ? node.material[0] : node.material;
          if (m0 && m0.visible === false) return;   /* an invisible material is an invisible mesh */
          /* the exclusion walks UP the tree: a mesh inside the person group is
             skipped even though the flag is on the group, not on the mesh */
          var p = node, leafOwner = null;
          while (p && p !== sourceGroup) {
            var pd = p.userData || {};
            if (pd.partType === 'person' || pd.arSkip || pd.ctl || pd.cPick) return;
            if (!leafOwner && pd.arLeaf && (pd.arLeaf.type === 'swing' || pd.arLeaf.type === 'slide' || pd.arLeaf.type === 'lift')) leafOwner = p;
            p = p.parent;
          }
          var into = leafOwner ? leafSetFor(leafOwner).buckets : buckets;
          var src = node.geometry.index ? node.geometry.toNonIndexed() : node.geometry;
          var posA = src.attributes.position;
          if (!posA || posA.count < 3) return;
          var nrmA = src.attributes.normal;
          if (!nrmA) { src.computeVertexNormals(); nrmA = src.attributes.normal; }
          /* A UNIT IS FRAME AND GLASS. A window is one mesh with two materials
             and groups; everything went into the FIRST material's bucket, so in
             AR the whole opening was a pale grey panel -- no frame, no seeing
             through (Marek). Each group goes to its own material's bucket. */
          var mats = Array.isArray(node.material) ? node.material : [node.material];
          if (options.solid) mats = mats.map(function (m) {
            var copy = m.clone(), alpha = options.walk ? node.userData.walkOpacity : node.userData.arOpacity;
            copy.opacity = Number.isFinite(alpha) ? Math.max(0, Math.min(1, alpha)) : 1;
            copy.transparent = copy.opacity < 1; copy.depthWrite = !copy.transparent;
            return copy;
          });
          var grps = (mats.length > 1 && src.groups && src.groups.length) ? src.groups : null;
          var b = bucketFor(mats[0], into);
          nrmMat.getNormalMatrix(node.matrixWorld);
          var tmpP = [], tmpN = [], bad = false, i;
          for (i = 0; i < posA.count; i++) {
            v.fromBufferAttribute(posA, i).applyMatrix4(node.matrixWorld);
            if (!isFinite(v.x) || !isFinite(v.y) || !isFinite(v.z)) { bad = true; break; }
            tmpP.push(v.x, v.y, v.z);
            v.fromBufferAttribute(nrmA, i).applyMatrix3(nrmMat).normalize();
            if (!isFinite(v.x) || !isFinite(v.y) || !isFinite(v.z)) { bad = true; break; }
            tmpN.push(v.x, v.y, v.z);
          }
          if (bad) return;
          var stR = _stage(), rr = stR && stR.renderer;
          var cutPl = (rr && ((rr.__sectionPlanes && rr.__sectionPlanes.length) ? rr.__sectionPlanes : rr.clippingPlanes)) || [];
          if (cutPl.length) arCut = true;
          /* CUT AT THE GROUND, ALWAYS (core-v276). The viewers stand a model on
             its lowest point, so anything below the ground -- a pool's floor, a
             footing -- lifted the whole building off the floor and the phone
             showed its underside (Marek: AR ma slouzit pro realisticke
             prochazeni stavbou). Everything is cut at the ground plane, the same
             way a section cuts it, and each material part of a mesh is cut on
             its own so a window keeps its frame and its glass. */
          var planes = options.ignoreSections ? [] : cutPl.slice();
          if (options.groundClip !== false) {
            var groundY = Number.isFinite(options.groundY) ? options.groundY : 0;
            planes.push(new T.Plane(new T.Vector3(0, 1, 0), -groundY));
          }
          if (grps) {
            var gq;
            for (gq = 0; gq < grps.length; gq++) {
              var gr = grps[gq], mIx = gr.materialIndex || 0, bg = bucketFor(mats[mIx] || mats[0], into);
              var from = gr.start * 3, to = Math.min(tmpP.length, (gr.start + gr.count) * 3), gi;
              var subP = tmpP.slice(from, to), subN = tmpN.slice(from, to);
              var clg = _arClipTris(subP, subN, planes);
              if (clg.pos.length !== subP.length) _arGroundCut++;
              for (gi = 0; gi < clg.pos.length; gi++) { bg.pos.push(clg.pos[gi]); bg.nrm.push(clg.nrm[gi]); }
            }
          } else {
            var cl = _arClipTris(tmpP, tmpN, planes);
            if (cl.pos.length !== tmpP.length) _arGroundCut++;
            for (i = 0; i < cl.pos.length; i++) { b.pos.push(cl.pos[i]); b.nrm.push(cl.nrm[i]); }
          }
          if (src !== node.geometry) src.dispose();
        });
        if (arCut) _log('[ar] the section on screen was applied to the export -- the model goes to AR cut');
        _log('[ar] cut at the ground: ' + _arGroundCut + ' mesh part(s) had something below ground, left out of AR');

        /* CENTRED ON X AND Z, SITTING ON Y. AR places the model at the point the
           phone picks on the floor, so the origin has to be the middle of the
           footprint at ground level -- otherwise the building lands off to one
           side or half sunk into the lawn. */
        var mnX = Infinity, mxX = -Infinity, mnY = Infinity, mxY = -Infinity, mnZ = Infinity, mxZ = -Infinity;
        var key, bk, i0, ls, allB = [];
        for (key in buckets) allB.push(buckets[key]);
        for (ls = 0; ls < leafSets.length; ls++) for (key in leafSets[ls].buckets) allB.push(leafSets[ls].buckets[key]);
        for (key = 0; key < allB.length; key++) {
          bk = allB[key];
          for (i0 = 0; i0 < bk.pos.length; i0 += 3) {
            if (bk.pos[i0] < mnX) mnX = bk.pos[i0];
            if (bk.pos[i0] > mxX) mxX = bk.pos[i0];
            if (bk.pos[i0 + 1] < mnY) mnY = bk.pos[i0 + 1];
            if (bk.pos[i0 + 1] > mxY) mxY = bk.pos[i0 + 1];
            if (bk.pos[i0 + 2] < mnZ) mnZ = bk.pos[i0 + 2];
            if (bk.pos[i0 + 2] > mxZ) mxZ = bk.pos[i0 + 2];
          }
        }
        if (!isFinite(mnX)) { reject(new Error('No exportable meshes')); return; }
        if (Number.isFinite(options.originY)) mnY = options.originY;
        var cX = (mnX + mxX) / 2, cZ = (mnZ + mxZ) / 2;
        for (key in buckets) {
          bk = buckets[key];
          for (i0 = 0; i0 < bk.pos.length; i0 += 3) {
            bk.pos[i0] -= cX;
            bk.pos[i0 + 1] -= mnY;
            bk.pos[i0 + 2] -= cZ;
          }
        }
        /* a leaf's corners are measured from its own hinge (world), so the
           node can turn about its origin */
        for (ls = 0; ls < leafSets.length; ls++) {
          var LS = leafSets[ls], pv = LS.pivot;
          for (key in LS.buckets) {
            bk = LS.buckets[key];
            for (i0 = 0; i0 < bk.pos.length; i0 += 3) {
              bk.pos[i0] -= pv.x;
              bk.pos[i0 + 1] -= pv.y;
              bk.pos[i0 + 2] -= pv.z;
            }
          }
        }
        _log('[ar] bbox ' + (mxX - mnX).toFixed(1) + ' x ' + (mxY - mnY).toFixed(1) +
             ' x ' + (mxZ - mnZ).toFixed(1) + ' m');

        var root = new T.Group(), tris = 0, ki, keys = Object.keys(buckets).sort();
        function meshOf(bk, name) {
          var triVerts = Math.floor(bk.pos.length / 9) * 9;
          if (triVerts < 9) return null;
          /* vertices are welded on position AND normal, so a corner shared by two
             faces stays two vertices and the edge does not smooth out */
          var map = {}, outP = [], outN = [], index = [], vi;
          for (vi = 0; vi < triVerts; vi += 3) {
            var hk = Math.round(bk.pos[vi] * 5000) + ',' + Math.round(bk.pos[vi + 1] * 5000) + ',' +
                     Math.round(bk.pos[vi + 2] * 5000) + '|' +
                     Math.round(bk.nrm[vi] * 100) + ',' + Math.round(bk.nrm[vi + 1] * 100) + ',' +
                     Math.round(bk.nrm[vi + 2] * 100);
            var id = map[hk];
            if (id === undefined) {
              id = outP.length / 3;
              map[hk] = id;
              outP.push(bk.pos[vi], bk.pos[vi + 1], bk.pos[vi + 2]);
              outN.push(bk.nrm[vi], bk.nrm[vi + 1], bk.nrm[vi + 2]);
            }
            index.push(id);
          }
          var g = new T.BufferGeometry();
          g.setAttribute('position', new T.Float32BufferAttribute(outP, 3));
          g.setAttribute('normal', new T.Float32BufferAttribute(outN, 3));
          g.setIndex(index);
          var m = new T.Mesh(g, bk.mat);
          m.name = name;
          tris += triVerts / 9;
          return m;
        }
        for (ki = 0; ki < keys.length; ki++) {
          var mPart = meshOf(buckets[keys[ki]], 'part_' + root.children.length);
          if (mPart) root.add(mPart);
        }
        function r4(x) { return Math.round((+x || 0) * 10000) / 10000; }
        var nSw = 0, nSl = 0, nLf = 0, nExt = 0;
        for (ls = 0; ls < leafSets.length; ls++) {
          var LZ = leafSets[ls], IZ = LZ.info, node = new T.Group(), lk = Object.keys(LZ.buckets).sort(), lq;
          for (lq = 0; lq < lk.length; lq++) {
            var mL = meshOf(LZ.buckets[lk[lq]], 'leaf_' + ls + '_' + lq);
            if (mL) node.add(mL);
          }
          if (!node.children.length) continue;
          node.name = 'leaf_' + ls;
          node.position.set(LZ.pivot.x - cX, LZ.pivot.y - mnY, LZ.pivot.z - cZ);
          var X = { leaf: IZ.type };
          if (IZ.type === 'swing') {
            X.dir = (IZ.dir < 0) ? -1 : 1; X.ajar = r4(IZ.ajar); X.max = r4(IZ.max || 1.45); nSw++;
          } else if (IZ.type === 'lift') {
            X.min = r4(IZ.min != null ? IZ.min : 0.08); nLf++;
          } else {
            var dW = LZ.dirW || new T.Vector3(1, 0, 0);
            X.ux = r4(dW.x); X.uy = r4(dW.y); X.uz = r4(dW.z); X.at = r4(IZ.at); X.travel = r4(IZ.travel); nSl++;
            if (LZ.shiftW) { X.sx = r4(LZ.shiftW.x); X.sy = r4(LZ.shiftW.y); X.sz = r4(LZ.shiftW.z); }
          }
          if (IZ.ext) { X.ext = 1; nExt++; if (LZ.outW) { X.ox = r4(LZ.outW.x); X.oz = r4(LZ.outW.z); } }
          node.userData = X;
          root.add(node);
        }
        if (leafSets.length) _log('[ar] moving leaves kept as nodes of their own (core-v629): ' + nSw + ' swing, ' + nSl + ' slide, ' + nLf + ' blind curtain(s) (core-v630), ' + nExt + ' in an outer wall -- the walk-through opens them');
        else _log('[ar] moving leaves: none tagged (arLeaf) -- doors stay merged and do not open in the walk-through');
        var nGlass = 0, kb;
        for (kb in buckets) if (buckets[kb].glass) nGlass++;
        _log('[ar] merged ' + root.children.length + ' mesh(es), ' + tris + ' tris, ' + nGlass +
             ' see-through material(s) as alphaMode BLEND');
        if (root.children.length === 0) { reject(new Error('No exportable meshes')); return; }
        /* WHAT THE WALK-THROUGH SAYS ABOUT WHERE YOU STAND (core-v702, Marek:
           ikona Gemini v prochazeni -- snimek a predvyplneny text, projekt
           "xxx", stojim "v loznici"; texty podle projektu a mista). Only the
           configurator can translate, so the words travel in the GLB: the
           product's title and size, the prompt in the buyer's language, and
           the rooms of the plan with their names, in the GLB's own frame. */
        try {
          root.userData = { leva: _arWalkInfo(sourceGroup, cX, mnY, cZ) };
          if (options.siteBounds) {
            var sb = options.siteBounds, corners = [[sb.x0,sb.z0],[sb.x1,sb.z0],[sb.x0,sb.z1],[sb.x1,sb.z1]].map(function (q) { return new T.Vector3(q[0],0,q[1]).applyMatrix4(sourceGroup.matrixWorld); });
            root.userData.leva.site = { groundHole: [Math.min.apply(null,corners.map(function(q){return q.x;}))-cX,Math.min.apply(null,corners.map(function(q){return q.z;}))-cZ,Math.max.apply(null,corners.map(function(q){return q.x;}))-cX,Math.max.apply(null,corners.map(function(q){return q.z;}))-cZ] };
          }
          _log('[ar] walk info: ' + root.userData.leva.project + ' | ' + root.userData.leva.rooms.length + ' room(s) named for the walk-through prompt (core-v702)');
        } catch (eWI) { _log('[ar] walk info THREW ' + ((eWI && eWI.message) || eWI) + ' -- the walk-through falls back to English words'); }

        if (options.returnScene) { resolve(root); return; }
        new Exporter().parse(root, function (glb) {
          if (glb instanceof ArrayBuffer) resolve(glb);
          else reject(new Error('Expected GLB ArrayBuffer, got JSON'));
        }, function (err) { reject(err); }, { binary: true });
      });
    }

    /* the project, the prompt and the rooms for the walk-through page (core-v702).
       The prompt is the locale's walk_ai_prompt with {project} and {place};
       outside is the Exterior step's word, the hint the shared 'copied' line.
       Rooms: every named room of the plan's storey, each rect taken to world
       by the model's matrix and to the GLB's frame by the export's centring. */
    function _arWalkInfo(src, cX, mnY, cZ) {
      var T = global.THREE, B = _boot(), F = _FL(), M = src.matrixWorld, i, j;
      var P = poly() || [], x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9;
      for (i = 0; i < P.length; i++) { x0 = Math.min(x0, P[i][0]); x1 = Math.max(x1, P[i][0]); z0 = Math.min(z0, P[i][1]); z1 = Math.max(z1, P[i][1]); }
      var size = (P.length && x1 > x0) ? (fmtLen(x1 - x0) + ' x ' + fmtLen(z1 - z0)) : '';
      var title = _deent(String(B.productTitle || ''));
      var out = {
        v: 1,
        project: (title + (size ? (' ' + size) : '')).replace(/\s+/g, ' ').trim(),
        prompt: _deent(_t('walk_ai_prompt', 'Please create a maximally photorealistic visualisation of this view. Keep the design, its shape, details and proportions exactly as they are. Landscaped surroundings. Project: {project}. Place: {place}.')),
        outside: _deent(_t('wiz_exterior', 'Exterior')),
        copied: _deent(_t('copied_to_clipboard', 'Copied to clipboard')),
        rooms: []
      };
      var R = (F && F.plan && F.plan.rooms) ? (F.plan.rooms() || []) : [];
      var yF = 0, st = STATE.planStorey | 0;
      try { yF = +storeys()[st].y0 || 0; } catch (eY) { yF = 0; }
      var a = new T.Vector3(), b = new T.Vector3();
      for (i = 0; i < R.length; i++) {
        if (!R[i] || R[i].closet) continue;
        var nm = '';
        try { nm = (F.draw && F.draw.roomName && F.plan.roomNameIdx) ? String(F.draw.roomName(F.plan.roomNameIdx(R[i], i), _t) || '') : ''; } catch (eN) { nm = ''; }
        nm = _deent(nm);
        if (!nm) continue;
        var rs = [];
        for (j = 0; j < (R[i].rects || []).length; j++) {
          var q = R[i].rects[j];
          a.set(q.x0, yF, q.z0).applyMatrix4(M); b.set(q.x1, yF, q.z1).applyMatrix4(M);
          rs.push([Math.round((Math.min(a.x, b.x) - cX) * 1000) / 1000, Math.round((Math.min(a.z, b.z) - cZ) * 1000) / 1000,
                   Math.round((Math.max(a.x, b.x) - cX) * 1000) / 1000, Math.round((Math.max(a.z, b.z) - cZ) * 1000) / 1000]);
        }
        if (rs.length) out.rooms.push({ n: nm, y: Math.round((a.y - mnY) * 1000) / 1000, r: rs });
      }
      return out;
    }

    function _arUpload(glb) {
      var B = _boot();
      var workerUrl = B.arWorkerUrl || AR_WORKER_FALLBACK;
      _log('[ar] worker ' + workerUrl);
      return fetch(workerUrl + '/upload-url').then(function (r1) {
        if (!r1.ok) throw new Error('upload-url failed: HTTP ' + r1.status);
        return r1.json();
      }).then(function (info) {
        if (!info || !info.uploadUrl) throw new Error('upload-url response invalid');
        _log('[ar] PUT ' + (glb.byteLength / 1024).toFixed(1) + ' KB');
        return fetch(info.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'model/gltf-binary' },
          body: glb
        }).then(function (r2) {
          if (!r2.ok) return r2.text().then(function (t) { throw new Error('PUT failed: HTTP ' + r2.status + ' ' + t); });
          return info;
        });
      });
    }

    function _arQR(url) {
      return new Promise(function (resolve) {
        var fallback = 'https://api.qrserver.com/v1/create-qr-code/?size=400x400&data=' + encodeURIComponent(url);
        function tryLib() {
          if (global.QRCode && global.QRCode.toDataURL) {
            global.QRCode.toDataURL(url, { width: 400, margin: 1, errorCorrectionLevel: 'M' }, function (err, d) {
              resolve((err || !d) ? fallback : d);
            });
            return true;
          }
          return false;
        }
        if (tryLib()) return;
        var n = 0, iv = setInterval(function () {
          if (tryLib() || ++n > 20) { clearInterval(iv); if (n > 20) resolve(fallback); }
        }, 100);
      });
    }

    function _arSet(id, fn) { var el = $(id); if (el) fn(el); }
    function _arStatus(msg) { _arSet('cf-ar-modal-status', function (el) { el.textContent = msg || ''; }); }
    function _arShowQR(src) {
      _arSet('cf-ar-modal-qr', function (el) {
        el.innerHTML = '';
        if (!src) return;
        var img = document.createElement('img');
        img.src = src;
        img.alt = 'AR QR code';
        img.style.cssText = 'width:200px;height:200px;border-radius:8px;background:#fff;padding:8px;';
        el.appendChild(img);
      });
    }
    function _arShowLink(url) {
      _arSet('cf-ar-modal-link', function (el) {
        if (!url) { el.style.display = 'none'; el.innerHTML = ''; return; }
        el.style.display = 'block';
        el.innerHTML = '<a href="' + _esc(url) + '" target="_blank" rel="noopener noreferrer" ' +
                       'style="color:inherit;text-decoration:underline;">' + _esc(url) + '</a>';
      });
    }

    function _ensureArModal() {
      if ($('cf-ar-modal')) return;
      if (!$('cf-ar-modal-style')) {
        var st = document.createElement('style');
        st.id = 'cf-ar-modal-style';
        st.textContent =
          '#cf-ar-modal{position:fixed;inset:0;display:flex;align-items:center;justify-content:center;z-index:100000;}' +
          '#cf-ar-modal[hidden]{display:none !important;}' +
          '#cf-ar-modal .cf-arm-backdrop{position:absolute;inset:0;background:rgba(0,0,0,.55);backdrop-filter:blur(2px);}' +
          '#cf-ar-modal .cf-arm-card{position:relative;background:#fff;border-radius:16px;padding:32px 28px 24px;max-width:380px;width:calc(100% - 32px);box-shadow:0 20px 60px rgba(0,0,0,.25);text-align:center;}' +
          '#cf-ar-modal .cf-arm-close{position:absolute;top:12px;right:12px;width:32px;height:32px;border:none;background:transparent;cursor:pointer;padding:0;display:flex;align-items:center;justify-content:center;color:#888;border-radius:8px;}' +
          '#cf-ar-modal .cf-arm-close:hover{background:#f4f4f4;color:#222;}' +
          '#cf-ar-modal .cf-arm-icon{width:56px;height:56px;margin:0 auto 14px;border-radius:50%;background:linear-gradient(135deg,#d2a878 0%,#b88752 100%);display:flex;align-items:center;justify-content:center;color:#fff;}' +
          '#cf-ar-modal .cf-arm-title{font:700 18px/1.3 var(--cf-font-display,sans-serif);color:#1a1a1a;margin:0 24px 6px;}' +
          '#cf-ar-modal .cf-arm-subtitle{font:500 13px/1.5 var(--cf-font-display,sans-serif);color:#555;margin:0 0 20px;}' +
          '#cf-ar-modal #cf-ar-modal-status{font:500 12px/1.4 var(--cf-font-display,sans-serif);color:#888;margin-bottom:12px;min-height:16px;}' +
          '#cf-ar-modal #cf-ar-modal-qr{display:flex;justify-content:center;align-items:center;min-height:200px;margin:8px 0 14px;}' +
          '#cf-ar-modal #cf-ar-modal-link{font:500 11px/1.4 var(--cf-font-display,sans-serif);color:#b88752;word-break:break-all;margin-bottom:14px;display:none;}' +
          '#cf-ar-modal .cf-arm-btn{border:none;border-radius:10px;padding:12px 18px;font:600 13px/1.2 var(--cf-font-display,sans-serif);cursor:pointer;min-width:110px;background:#f4f4f4;color:#333;}';
        document.head.appendChild(st);
      }
      var closeTxt = _deent(_t('close', 'Close'));
      var w = document.createElement('div');
      w.id = 'cf-ar-modal';
      w.hidden = true;
      w.setAttribute('role', 'dialog');
      w.setAttribute('aria-modal', 'true');
      w.innerHTML =
        '<div class="cf-arm-backdrop" data-cf-ar-close></div>' +
        '<div class="cf-arm-card">' +
          '<button class="cf-arm-close" type="button" data-cf-ar-close aria-label="' + _esc(closeTxt) + '">' +
            '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">' +
            '<line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>' +
          '</button>' +
          '<div class="cf-arm-icon" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>' +
            '<polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>' +
            '<line x1="12" y1="22.08" x2="12" y2="12"></line></svg>' +
          '</div>' +
          '<h3 class="cf-arm-title">' + _esc(_deent(_t('scan_to_view_in_ar', 'Scan to view in AR'))) + '</h3>' +
          '<p class="cf-arm-subtitle">' + _esc(_deent(_t('point_your_phone_camera_at_t', 'Point your phone camera at the QR code to place this model in your space.'))) + '</p>' +
          '<div id="cf-ar-modal-status"></div>' +
          '<div id="cf-ar-modal-qr"></div>' +
          '<div id="cf-ar-modal-link"></div>' +
          '<div class="cf-arm-actions"><button class="cf-arm-btn" type="button" data-cf-ar-close>' + _esc(closeTxt) + '</button></div>' +
        '</div>';
      document.body.appendChild(w);
      w.addEventListener('click', function (e) {
        if (e.target && e.target.closest && e.target.closest('[data-cf-ar-close]')) {
          e.preventDefault();
          w.hidden = true;
        }
      });
    }

    // Products may request a surface-only AR scene and a complete walk scene.
    // Existing products retain the exact single-scene export path.
    function arExport() {
      return _ensureGLTFExporter().then(function () {
        return typeof __PROD._arReady === 'function' ? __PROD._arReady() : null;
      }).then(function () {
        var st = _stage();
        if (!st || !st.group) throw new Error('stage not ready');
        var opts = typeof __PROD._arOptions === 'function' ? __PROD._arOptions() : null;
        if (!opts || !opts.splitWalk) return _arMerge(st.group);
        st.group.updateMatrixWorld(true);
        var grade = new global.THREE.Vector3(0,opts.groundY,0).applyMatrix4(st.group.matrixWorld).y;
        var base = Object.assign({},opts,{groundY:grade,originY:grade,returnScene:true,ignoreSections:true});
        return Promise.all([_arMerge(st.group,base),_arMerge(st.group,Object.assign({},base,{walk:true,groundClip:false}))]).then(function (roots) {
          var scenes = roots.map(function(root,i){var scene=new global.THREE.Scene();scene.name=i?'LEVA_WALK':'LEVA_AR';scene.userData.levaWalk=!!i;scene.add(root);return scene;});
          return new Promise(function(resolve,reject){new global.GLTFExporter().parse(scenes,resolve,reject,{binary:true});});
        });
      });
    }

    function arOpen() {
      _ensureArModal();
      var modal = $('cf-ar-modal');
      if (!modal) return false;
      modal.hidden = false;
      if (_arBusy) return true;
      _arBusy = true;
      _arShowQR(null);
      _arShowLink(null);
      _arNote = 'preparing';
      _arStatus(_deent(_t('arPreparing', 'Preparing scene...')));
      arExport()
        .then(function (glb) {
          _arNote = 'uploading ' + (glb.byteLength / 1024).toFixed(1) + ' KB';
          _arStatus(_deent(_t('arUploading', 'Uploading to cloud...')));
          return _arUpload(glb);
        })
        .then(function (info) {
          _log('[ar] uploaded uuid=' + info.uuid);
          return _arQR(info.viewerUrl).then(function (qr) {
            _arShowQR(qr);
            _arShowLink(info.viewerUrl);
            _arNote = 'ready ' + info.uuid;
            _arStatus(_deent(_t('arReady', 'Ready -- scan with your phone')));
          });
        })
        .catch(function (e) {
          _arNote = 'FAILED: ' + (e && e.message ? e.message : e);
          _log('[ar] ' + _arNote);
          _arStatus(_deent(_t('ar_export_failed', 'AR export failed: ')) + (e && e.message ? e.message : e));
        })
        .then(function () { _arBusy = false; });
      return true;
    }

    /* -------------------------------------------------------------------------
       PDF. The document itself is NOT written here and never will be: the router
       renders snippets/configurator-pdf.liquid on every configurator page, which
       puts ConfiguratorPdfExporter on the window, and that is where the socials
       sheet and the purchase gate already live. Rewriting either would give us a
       second one to keep in step.

       So this is only the button's behaviour, which does have to be the same for
       all twelve:

         - it is DIM until the price data has landed. The bill is what the
           document is mostly made of, so offering the download before the numbers
           exist produces a document that is wrong rather than late.
         - a click opens the socials sheet FIRST and downloads second. That order
           is the point of the sheet.
         - the drawing itself comes from the product, through exportPdf below. A
           product with nothing to draw yet says so out loud instead of handing
           over an empty file.
       ------------------------------------------------------------------------- */
    var PDF_READY_BG = '#1A1C1D';
    var _pdfNote = 'not bound';
    var _pdfExport = null;

    function _initPdfButton() {
      /* THE CHIP IS NOT THE BUTTON'S BUSINESS.

         It was mounted from inside here, below the line that gives up when
         there is no #cf-btn-pdf -- and on this line the PDF button lives in the
         floating bar, not in the page, so the function returned before the chip
         was ever made. Capturing views has nothing to do with where the
         download button sits, so it is mounted first and on its own
         (Marek: nevidim na canvas pill button + PDF). */
      try { _shotChip(); } catch (eSC) { _log('pdf: chip -- ' + ((eSC && eSC.message) || eSC)); }

      var btn = $('cf-btn-pdf');
      if (!btn) { _pdfNote = 'no #cf-btn-pdf in the page'; return; }
      if (btn.__cfPdfBound) return;
      btn.__cfPdfBound = true;
      /* + PDF hidden now that the rail's PDF icon is known (core-v645) */
      try { _shotSync(); } catch (eSy) {}

      if (!$('cf-house-pdf-css')) {
        var st = document.createElement('style');
        st.id = 'cf-house-pdf-css';
        st.textContent =
          '#cf-btn-pdf.cf-pdf-loading{opacity:.4;pointer-events:none;cursor:default;filter:grayscale(1);}' +
          '#cf-btn-pdf.cf-pdf-ready{background:' + PDF_READY_BG + ' !important;}' +
          '#cf-btn-pdf.cf-pdf-ready > *{filter:brightness(0) invert(1) !important;}';
        document.head.appendChild(st);
      }

      btn.addEventListener('click', function (ev) {
        ev.preventDefault();
        /* THE FIRST TAP SHOWS + PDF (core-v645, Marek: +PDF button se objevi
           po prvnim kliknuti na ikonu PDF v pravem banneru, jeji opetovne
           kliknuti pak spusti generovani PDF; jinak bude skryty): while the
           chip is hidden a tap only brings it up; with the chip shown the
           tap makes the document as before */
        if (_shotHidden()) {
          _shotShow(true, 'the first tap on the PDF icon');
          /* core-v650: the price is said on the tap that brings + PDF up */
          _pdfPriceNote();
          return;
        }
        /* THE PDF BUTTON OPENS THE DRAWINGS. The blueprints page draws the
           project from its own model; the old assembly export stays behind
           the exporter asset for products that still ship it. The project's
           short id is made sure of first (leva-share), then the page opens
           with it (Marek). */
        /* the rail button is the assembly PDF again; the drawings open from
           the Assembly step (a proof of concept for now) or with
           HOUSE_BOOT.pdfOpensBlueprints = true */
        var B0 = null; try { B0 = BOOTDATA(); } catch (e0) {}
        if (B0 && B0.pdfOpensBlueprints) { openBlueprints(); return; }
        /* core-v758: an author's royalty product -- no tokens, no package page:
           the export straight away, the purchase gate decides */
        /* core-v769: the royalty product's package goes to the drawings page
           too -- no tokens there either, a buyer sees it all, anyone else the
           purchase (rec.buy) on the PDF icon and the locked pages */
        if (_pdfRoyalty() && !_pdfToBlueprints()) {
          _log('pdf: royalty product (vendor ' + _pdfRoyalty() + ') -- the package goes to the drawings page, ' + (_pdfOwner() ? 'bought' : 'the purchase offered there') + ' (core-v769)');
          _pdfPackage();
          return;
        }
        if (_pdfRoyalty()) {
          _pdfNote = 'royalty product -- the purchase decides';
          _log('pdf: royalty product (vendor ' + _pdfRoyalty() + ') -- no tokens, the purchase gate decides (' + (_pdfOwner() ? 'bought' : 'not bought') + ', core-v758)');
          _pdfRun();
          return;
        }
        /* SEEN BEFORE IT IS PAID (core-v733, Marek: PDF jsou pod registraci /
           zakoupenim produktu, chci aby bylo PDF videt, ale ostre jen prvnich
           5 stranek, ostatni faded): a product with drawings opens its whole
           package on the drawings page straight away; the page shows the
           first five pages sharp, the rest faded, and takes the tokens when
           the PDF is downloaded there */
        if (!_pdfToBlueprints()) { _log('pdf: the package goes to the drawings page, paid there on download (core-v733)'); _pdfPackage(); return; }
        /* THE PDF IS PAID IN TOKENS (core-v650, Marek: PDF za 10 tokenu,
           pokryje kredit pro registraci): the worker takes them first, and
           only then the document is made */
        /* NO FOLLOW-US SHEET (core-v654, Marek: modal muzes cely zrusit):
           paid -> the document is made at once */
        _pdfPay(function () {
          _log('pdf: paid -- made straight away, no socials sheet (core-v654)');
          /* core-v656: the purchase gate in configurator-core.js lets a paid
             PDF through (core-v9-credits) */
          try { if (global.ConfiguratorPdfExporter) global.ConfiguratorPdfExporter.__levaPaidAt = Date.now(); } catch (eM) {}
          _log('pdf: paid -- made here, ' + (_pdfToBlueprints() || 'no package'));
          _pdfRun();
        });
      });

      var bridge = global.ConfiguratorBudgetBridge || null;
      /* core-v770 (Marek 2026-10-29 13:18: nestihly se vygenerovat cisla -
         ceny na hlavni stranku. Melo by se to osetrit tim, ze se ikona
         neaktivuje drive, dokud nebudou nacteny hodnoty): this line's pages
         carry no bridge any more (core-v479), so the icon went live at once
         and an early tap made the cover with $0. It waits for the prices now:
         the catalogue in, a priced row in the building's bill (the PDF's own
         _pickMoney) and, where the strip is on the page, its total not
         nought. */
      function priced() {
        try {
          if (!_catRows()) return false;
          var rows = _pdfBoq('main'), i, any = false;
          for (i = 0; i < rows.length; i++) if (rows[i].total > 0) { any = true; break; }
          if (!any) return false;
          var tx = _pdfTexts();
          return !(tx && tx.total && !/[1-9]/.test(tx.total));
        } catch (e) { return false; }
      }
      function ready() { return (!(bridge && typeof bridge.isReady === 'function') || bridge.isReady()) && priced(); }
      function promote() {
        btn.classList.remove('cf-pdf-loading');
        btn.classList.add('cf-pdf-ready');
        _pdfNote = 'ready';
      }
      if (ready()) { promote(); return; }

      btn.classList.add('cf-pdf-loading');
      _pdfNote = 'waiting for price data';
      _log('pdf: the icon waits for the prices (core-v770)');
      if (bridge && typeof bridge.onReady === 'function') { try { bridge.onReady(promote); } catch (e) {} }
      /* AND A CEILING ON THE WAIT. The bridge may never report, and a button that
         stays grey forever is indistinguishable from a broken one. Thirty seconds
         and it goes live regardless. */
      var waited = 0;
      var iv = global.setInterval(function () {
        if (ready()) { global.clearInterval(iv); promote(); _log('pdf: the prices are in -- the icon live after ' + Math.round(waited / 1000) + ' s (core-v770)'); }
        else if ((waited += 1000) >= 45000) {
          global.clearInterval(iv);
          promote();
          _pdfNote = 'ready (no prices in 45 s, promoted on timeout)';
          _log('pdf: ' + _pdfNote);
        }
      }, 1000);
    }

    /* =========================================================================
       THE PDF IS PAID IN TOKENS (core-v650, worker leva-ai w2.34+, Marek: PDF
       za 10 tokenu, pri registraci 50 tokenu, kredit = produkt LEVA CREDITS).

       The worker decides everything -- the price, the balance, the sign-up
       gift, the free repeat of the same configuration -- and the page only
       says it. Signed out: sign in (the gift is named). Not enough: the
       bubble with Buy tokens, LEVA CREDITS in a new tab so the project stays
       open. The worker unreachable: the PDF is made anyway and the log says
       so -- a broken check must not take the download away.
       ========================================================================= */
    var PDF_SPEND_URL = '/apps/leva-ai/tokens/spend', PDF_TOKENS_URL = '/apps/leva-ai/tokens';
    function _pdfCreditsUrl() {
      var root = '/';
      try { root = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/'; } catch (e) { root = '/'; }
      return root + 'products/credits';
    }
    /* the same configuration is downloaded again for free: its fingerprint is
       the saved project without the lib's own VERSION, so a new release of
       the lib does not make an unchanged house a new one */
    function _pdfKey() {
      var s = '';
      try {
        var p = _slim(payload());
        if (p && typeof p === 'object') { p = Object.assign({}, p); delete p.v; }
        s = String(PRODUCT || (BOOTDATA() || {}).slug || '') + '|' + JSON.stringify(p);
      } catch (e) { return ''; }
      var h1 = 0x811c9dc5, h2 = 7, i, c;
      for (i = 0; i < s.length; i++) {
        c = s.charCodeAt(i);
        h1 = Math.imul(h1 ^ c, 16777619) >>> 0;
        h2 = (Math.imul(h2, 31) + c) >>> 0;
      }
      return h1.toString(16) + h2.toString(16) + s.length.toString(16);
    }
    /* the command bar's bubble when the bar is there, the browser's own
       question otherwise -- the answer must reach the buyer either way */
    function _pdfAsk(text, btns) {
      if (_cmd && _cmd.bub) { _cmdBubble(text, btns); return; }
      var pri = null;
      (btns || []).forEach(function (b) { if (b && b.pri) pri = b; });
      try { if (global.confirm(text) && pri) pri.fn(); } catch (e) {}
      _log('pdf: no command bar -- asked in a browser dialog');
    }
    /* the shared Not enough tokens card (configurator-pdf.liquid pdf-pay-v4);
       false when the page has not got it */
    function _lowTokens(line) {
      var X = global.ConfiguratorPdfExporter;
      if (!X || typeof X.lowTokens !== 'function') return false;
      try { _cmdBubble(null); } catch (eB) {}
      try { X.lowTokens(line); return true; } catch (e) { _log('low tokens card THREW ' + ((e && e.message) || e)); return false; }
    }
    function _pdfCancelBtn() {
      return { t: _t('cancel', 'Cancel'), fn: function () { _cmdBubble(null); _log('pdf: the bubble closed, nothing made'); } };
    }
    /* core-v657 (two tiers, Marek: projekty za 50 / 20 USD vcetne tokenu): a
       customer who bought this project at a real price has its PDF free
       (window.LEVA_PRODUCT.paidOwner, configurator-router) */
    function _pdfOwner() {
      try { var LP = global.LEVA_PRODUCT || {}; return LP.paidOwner === true || LP.paidOwner === 'true'; } catch (e) { return false; }
    }
    /* core-v758 (GARDEN BAR, as the bench lib's bench-v356 and the firewood
       rack): AN AUTHOR'S ROYALTY PRODUCT -- its vendor is the author, not
       LEVA (window.LEVA_PRODUCT.vendor, configurator-router). The author is
       paid from the sale, so tokens never open its PDF: the export is asked
       for straight away and the purchase gate decides. '' for LEVA's own. */
    function _pdfRoyalty() {
      try { var v = String((global.LEVA_PRODUCT || {}).vendor || '').trim().toUpperCase(); return (v && v !== 'LEVA') ? v : ''; } catch (e) { return ''; }
    }
    /* core-v660 (Marek: modal s informacemi o tokenech na stred zobrazeni,
       black background a white font): the token line goes into the shared
       black box in the middle of the screen (pdf-pay-v7 tokenInfo), the red
       line under the bar only when that box is not on the page */
    function _pdfSay(txt, what) {
      var X = global.ConfiguratorPdfExporter;
      if (X && typeof X.tokenInfo === 'function') {
        try { X.tokenInfo(txt, 6000); _log(what + ' -- the black token box in the middle, 6 s'); return; } catch (e) { _log('token box THREW ' + ((e && e.message) || e)); }
      }
      if (!(_cmd && _cmd.note)) { _log(what + ' -- no token box and no line under the bar to say it on'); return; }
      _cmdNoteShow(txt, what);
    }
    /* the price, said once + PDF comes up (not while the voice tour plays) */
    function _pdfPriceNote() {
      try { if (typeof _T !== 'undefined' && _T && _T.on) { _log('pdf: price not said -- the tour is playing'); return; } } catch (eT) {}
      if (_pdfOwner()) {
        _pdfSay(_t('pdf_owned', 'The PDF of this project comes with your purchase. Tap the PDF icon again to download it.'), 'pdf: bought this project -- the PDF is free (core-v657)');
        return;
      }
      if (_pdfRoyalty()) { _log('pdf: royalty product (vendor ' + _pdfRoyalty() + ') -- no token price, the purchase decides (core-v758)'); return; }
      if (!global.fetch) return;
      global.fetch(PDF_TOKENS_URL, { credentials: 'same-origin', headers: { accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (j) {
          var tk = (j && j.tokens) || {}, n = (tk.cost && tk.cost.pdf) || 0;
          if (!n) { _log('pdf: price unknown -- the worker sent no pdf cost (' + ((j && j.error) || 'old worker?') + ')'); return; }
          var signedIn = (j.customer === 'yes'), txt;
          if (signedIn) txt = _t('pdf_price', 'The PDF takes [n] tokens, you have [b]. Tap the PDF icon again to download it.').replace('[n]', String(n)).replace('[b]', String(tk.balance || 0));
          else txt = _t('pdf_price_signup', 'The PDF takes [n] tokens. Sign up and you get [w] tokens free.').replace('[n]', String(n)).replace('[w]', String(j.welcome || 0));
          _pdfSay(txt, 'pdf: the price, ' + n + ' tokens, ' + (signedIn ? ('balance ' + (tk.balance || 0)) : ('signed out, sign-up gift ' + (j.welcome || 0))));
        })
        .catch(function (e) { _log('pdf: price check FAILED ' + ((e && e.message) || e)); });
    }
    function _pdfPay(go) {
      if (_pdfOwner()) { _pdfNote = 'bought this project'; _log('pdf: bought this project -- free, no tokens taken (core-v657)'); go(); return; }
      if (!global.fetch) { _log('pdf: no fetch -- made without the token check'); go(); return; }
      var key = _pdfKey();
      _pdfNote = 'paying';
      global.fetch(PDF_SPEND_URL, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        body: JSON.stringify({ what: 'pdf', key: key })
      })
        .then(function (r) { return r.json().then(function (j) { return j || {}; }, function () { return { error: 'http_' + r.status }; }); })
        .then(function (j) {
          if (j.ok) {
            _pdfNote = 'paid (' + j.how + ')';
            _log('pdf: paid -- ' + j.how + ', ' + (j.spent || 0) + ' tokens, ' + j.balance + ' left, key ' + (key || 'none') + ' (' + (j.version || '?') + ')');
            go();
          } else if (j.error === 'sign_in') {
            _pdfNote = 'sign in asked';
            _log('pdf: signed out -- the sign-in bubble, ' + j.need + ' tokens, sign-up gift ' + j.welcome + ', nothing made');
            _pdfAsk(_t('pdf_price_signup', 'The PDF takes [n] tokens. Sign up and you get [w] tokens free.').replace('[n]', String(j.need || '')).replace('[w]', String(j.welcome || '')),
                    [{ t: _t('trial_login_btn', 'Sign in'), pri: true, fn: _cmdGo(j.loginUrl || '/account/login') }, _pdfCancelBtn()]);
          } else if (j.error === 'pdf_tokens') {
            _pdfNote = 'not enough tokens';
            var lowLine = _t('pdf_tokens', 'The PDF takes [n] tokens from your balance. You have [b].').replace('[n]', String(j.need || '')).replace('[b]', String(j.balance || 0));
            /* core-v655: the shared Not enough tokens card (pdf-pay-v4), the
               bubble only when the card is not on the page */
            if (_lowTokens(lowLine)) { _log('pdf: not enough tokens -- needs ' + j.need + ', has ' + j.balance + ', the Not enough tokens card, nothing made'); return; }
            _log('pdf: not enough tokens -- needs ' + j.need + ', has ' + j.balance + ', the Buy tokens bubble, nothing made');
            _pdfAsk(_t('pdf_tokens', 'The PDF takes [n] tokens from your balance. You have [b].').replace('[n]', String(j.need || '')).replace('[b]', String(j.balance || 0)),
                    [{ t: _t('buy_tokens', 'Buy tokens'), pri: true, fn: function () {
                        var u = _pdfCreditsUrl();
                        _cmdBubble(null);
                        _log('pdf: LEVA CREDITS opened in a new tab -- ' + u);
                        var w = null; try { w = global.open(u, '_blank'); } catch (eO) { w = null; }
                        if (!w) global.location.href = u;
                      } }, _pdfCancelBtn()]);
          } else {
            _pdfNote = 'token check answered ' + (j.error || '?');
            _log('pdf: the token check answered ' + (j.error || '?') + ' -- made anyway (CHECK THE WORKER)');
            go();
          }
        }, function (e) {
          _pdfNote = 'token check FAILED';
          _log('pdf: the token check FAILED ' + ((e && e.message) || e) + ' -- made anyway (CHECK THE WORKER)');
          go();
        });
    }

    function _pdfRun() {
      /* the product's own, if it has one; otherwise the one above */
      if (typeof _pdfExport !== 'function') _pdfExport = _pdfDefaultExport;
      if (typeof _pdfExport === 'function') {
        try {
          _pdfNote = 'exporting';
          var r = _pdfExport();
          if (r && r.then) r.then(function () { _pdfNote = 'exported'; },
                                  function (e) { _pdfNote = 'export FAILED: ' + (e && e.message); _log('pdf: ' + _pdfNote); });
          else _pdfNote = 'exported';
        } catch (e) {
          _pdfNote = 'export THREW: ' + (e && e.message ? e.message : e);
          _log('pdf: ' + _pdfNote);
        }
        return;
      }
      _pdfNote = 'NOTHING TO DRAW -- this product has no exportPdf yet';
      _log('pdf: ' + _pdfNote + ' (the model is still guider mass, so a document ' +
           'would show a magenta box and an empty bill)');
    }

    function setPdfExport(fn) { _pdfExport = (typeof fn === 'function') ? fn : null; }

    /* =========================================================================
       THE DOCUMENT ONE PRODUCT ASKS FOR IS THE DOCUMENT THEY ALL ASK FOR.

       The button and the exporter have both been here for months; what was
       missing is the data, and every product was expected to assemble it for
       itself. None of the three ever did, so the button said "NOTHING TO DRAW".
       Everything it needs is already in this file -- the bill, the catalogue,
       the assembly steps, the stage -- so it is built once (Marek).

       Three lists, and each answers a different question:

         BILL OF QUANTITIES   what the building is made of: every code with its
                              quantity, in the units on screen, priced the way
                              the budget prices it
         CUT LIST             what to cut: the linear items, body by body, by
                              their longest dimension, grouped by profile. Read
                              off the model itself, so it cannot disagree with
                              what is drawn
         SHOPPING LIST        what to buy and from whom: the product assigned to
                              each code, its vendor and the line total, with the
                              unassigned lines still printed so the gaps show

       A product that wants something else still calls setPdfExport and this is
       never used. This is the default, not a rule. */

    /* =========================================================================
       THE VIEWS SOMEBODY CHOSE, AT THE FRONT OF THE DOCUMENT (core-v102).

       Ported from the container, where it already works, rather than designed
       again: a chip beside the PDF button takes the frame AS IT STANDS, so what
       lands in the document is the angle the person turned the model to when
       they decided they liked it -- not a view this file picked for them.

       Six at most: a document is a document, not an album. A long press empties
       the queue, because undoing one at a time is the wrong shape for "no,
       start again" (Marek: tlacitko +pdf, ktery zachytava pohledy co chce user
       a pak je da na zacatek pdf). */
    var _pdfShots = [];
    var PDF_SHOTS_MAX = 6;

    /* THE SHOT IS OF THE BUILDING, NOT OF THE SCREEN.

       The canvas is transparent so the map shows through it, and the grid is
       drawn for working, not for looking at. Both landed in the document and
       made the page read as a screenshot. So for the one frame that is
       captured: the grid off, the canvas filled with the same #F5F5F5 the
       exporter paints its snapshot pages with -- so the image and the page are
       one surface -- and everything put back exactly as it was afterwards, in
       a finally, because a shot must not leave the model looking different
       (Marek: bez pozadi, pozadi klasika f5f5f5, bez gridu). */
    var SHOT_BG = 0xF5F5F5;

    /* THE SHOT AT FULL SHARPNESS (core-v605): a touch screen draws the model
       at 1.25 pixels per CSS pixel now; a picture for the PDF is taken at the
       sharpness it always had (up to 2) and the canvas goes back after */
    function _shotPrUp(st) {
      try {
        var pr = st.renderer.getPixelRatio(), want = Math.min(global.devicePixelRatio || 1, 2);
        if (want > pr + 0.01) { st.renderer.setPixelRatio(want); return pr; }
      } catch (e) {}
      return null;
    }
    function _shotPrDown(st, pr) {
      if (pr == null) return;
      try { st.renderer.setPixelRatio(pr); if (st.wake) st.wake(500); } catch (e) {}
    }

    function _shotTake() {
      var st = _stage();
      if (!st || !st.renderer || !st.scene) return false;
      var gridWas = null, gh = null, clearWas = null, alphaWas = 1, bgWas, prWas = null;
      try {
        var T = global.THREE;
        gh = st._gridHelper || null;
        if (gh) { gridWas = gh.visible; gh.visible = false; }
        try {
          clearWas = st.renderer.getClearColor ? st.renderer.getClearColor(new T.Color()) : null;
          alphaWas = st.renderer.getClearAlpha ? st.renderer.getClearAlpha() : 1;
        } catch (eC) {}
        bgWas = st.scene.background;
        st.renderer.setClearColor(SHOT_BG, 1);
        st.scene.background = new T.Color(SHOT_BG);

        /* drawn and read in the same breath -- the buffer is not kept between
           frames, so a toDataURL on its own comes back blank */
        var cam = (STATE.view === '2d' && st.orthoCam) ? st.orthoCam : st.camera;
        if (STATE.view !== '2d') prWas = _shotPrUp(st);
        st.renderer.render(st.scene, cam);
        /* THE PLAN SHOT CARRIES THE PLAN (core-v291). On the plan the drawing --
           walls, rooms, openings, names -- is not in the 3D canvas at all: it is
           painted on the floorplan asset's own layer above it, and the sauna's
           benches and heater on a layer above that. A shot of the 3D canvas
           alone came out as the model seen from above with none of the plan on
           it. So on the plan the shot is laid up from all three, in their
           places on screen, before it goes to the PDF. */
        if (STATE.view === '2d') {
          var glc = st.renderer.domElement;
          _shotPlanComposite(glc).then(function (u2) {
            if (!u2 || u2.length < 2048) { _log('pdf shot: the plan could not be laid up'); return; }
            _pdfShots.push(u2);
            while (_pdfShots.length > PDF_SHOTS_MAX) _pdfShots.shift();
            _log('pdf: plan view ' + _pdfShots.length + ' of ' + PDF_SHOTS_MAX + ' captured with the plan drawing and its extras');
          }).catch(function (eP) { _log('pdf plan shot: ' + ((eP && eP.message) || eP)); });
          return true;
        }
        var url = st.renderer.domElement.toDataURL('image/jpeg', 0.92);
        if (!url || url.length < 2048) return false;
        _pdfShots.push(url);
        while (_pdfShots.length > PDF_SHOTS_MAX) _pdfShots.shift();
        _log('pdf: view ' + _pdfShots.length + ' of ' + PDF_SHOTS_MAX +
             ' captured on a plain background' + (gh ? ', grid off' : ''));
        return true;
      } catch (e) { _log('pdf shot: ' + ((e && e.message) || e)); return false; }
      finally {
        try {
          _shotPrDown(st, prWas);
          if (gh && gridWas !== null) gh.visible = gridWas;
          st.scene.background = bgWas;
          if (clearWas) st.renderer.setClearColor(clearWas, alphaWas);
          else st.renderer.setClearColor(0x000000, 0);
          var cam2 = (STATE.view === '2d' && st.orthoCam) ? st.orthoCam : st.camera;
          st.renderer.render(st.scene, cam2);   /* put the map back on screen */
        } catch (eR) {}
      }
    }

    /* the 3D frame, the asset's plan layer and the product's plan extras, laid
       up on one canvas in their places on screen */
    function _shotPlanComposite(glc) {
      return new Promise(function (resolve, reject) {
        try {
          var W = glc.width, H = glc.height, gr = glc.getBoundingClientRect();
          var sx = W / Math.max(1, gr.width), sy = H / Math.max(1, gr.height);
          var cv = global.document.createElement('canvas');
          cv.width = W; cv.height = H;
          var c = cv.getContext('2d');
          c.fillStyle = '#F5F5F5'; c.fillRect(0, 0, W, H);
          c.drawImage(glc, 0, 0);
          function place(el) {
            var r = el.getBoundingClientRect();
            return { x: (r.left - gr.left) * sx, y: (r.top - gr.top) * sy, w: r.width * sx, h: r.height * sy };
          }
          var F = _FL(), OV = F && F.plan && F.plan.overlay, jobs = [];
          function svgJob(svgEl) {
            return new Promise(function (res) {
              try {
                var pr = place(svgEl), xml = new XMLSerializer().serializeToString(svgEl);
                if (xml.indexOf('xmlns=') < 0) xml = xml.replace('<svg', '<svg xmlns="http://www.w3.org/2000/svg"');
                var img = new Image();
                img.onload = function () { try { c.drawImage(img, pr.x, pr.y, pr.w, pr.h); } catch (eD) {} res(); };
                img.onerror = function () { res(); };
                img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml);
              } catch (eS) { res(); }
            });
          }
          if (OV && OV.el) {
            var tag = (OV.el.tagName || '').toUpperCase();
            if (tag === 'CANVAS') { var po = place(OV.el); c.drawImage(OV.el, po.x, po.y, po.w, po.h); }
            else if (tag === 'SVG') jobs.push(svgJob(OV.el));
            else {
              var inner = OV.el.querySelectorAll ? OV.el.querySelectorAll('canvas, svg') : [], k;
              for (k = 0; k < inner.length; k++) {
                if ((inner[k].tagName || '').toUpperCase() === 'CANVAS') { var pi = place(inner[k]); c.drawImage(inner[k], pi.x, pi.y, pi.w, pi.h); }
                else jobs.push(svgJob(inner[k]));
              }
            }
          }
          var ex = $('cf-plan-extra-host'), exSvg = ex && ex.style.display !== 'none' ? ex.querySelector('svg') : null;
          if (exSvg) jobs.push(svgJob(exSvg));
          Promise.all(jobs).then(function () { resolve(cv.toDataURL('image/jpeg', 0.92)); });
        } catch (e) { reject(e); }
      });
    }

    /* the rail is about 56 px wide and sits 12 px in, so 78 px clears it with
       a little air; a product whose rail is elsewhere can set its own */
    var SHOT_CHIP_RIGHT = 78;
    /* OUR RED, NOT ADOBE'S.

       #D8232A is the asset drawer's action colour and reads as the Acrobat
       badge. The budget uses #dc3545 for everything that acts -- the download
       tab, the refresh button, the add button -- and the chip belongs with
       those, not with somebody else's product (Marek: pouzij nasi cervenou z
       budget button). #c12838 is the same button's pressed state. */
    var SHOT_CHIP_RED = '#dc3545';
    var SHOT_CHIP_RED_DOWN = '#c12838';

    /* HIDDEN UNTIL THE PDF ICON IS TAPPED (core-v645). Only where the rail
       has its PDF icon -- a page without one keeps the chip shown, or
       nothing could ever bring it up. Views already taken keep it shown. */
    var _shotOn = false;
    function _shotHidden() {
      var el = $('cf-shot-chip');
      return !!(el && el.classList.contains('cf-shot-off'));
    }
    function _shotShow(on, why) {
      _shotOn = !!on;
      _shotSync();
      _log('pdf: + PDF ' + (_shotOn ? 'shown' : 'hidden') + ' -- ' + (why || '?') + (_shotOn ? ' (the next tap on the PDF icon makes the document)' : ''));
    }
    function _shotSync() {
      var el = $('cf-shot-chip');
      if (!el) return;
      el.classList.toggle('cf-shot-off', !(_shotOn || _pdfShots.length || !$('cf-btn-pdf')));
      var n = _pdfShots.length, lbl = el.querySelector('.cf-shot-n');
      if (lbl) lbl.textContent = n ? String(n) : '';
      /* ALWAYS THE FULL RED.

         The empty state was the same red at 62 per cent, and over the white
         fade the canvas draws at its top edge that came out pale pink -- not a
         softer version of the button, a different colour. The COUNT says
         whether anything is in the queue; the colour does not have to say it
         too (Marek: mixujete ji s bilou fade out barvou). */
      el.style.background = SHOT_CHIP_RED;
    }

    function _shotChip() {
      if ($('cf-shot-chip')) return;
      /* the canvas's own container if there is one, the root otherwise -- the
         chip has to sit over the model, not over the page */
      var st = _stage();
      var root = (st && st.renderer && st.renderer.domElement && st.renderer.domElement.parentNode) ||
                 $('cf-root') || $('cf-canvas-wrap');
      if (!root) { _log('pdf: chip has nowhere to sit -- no canvas and no #cf-root'); return; }
      try {
        /* an absolutely positioned child needs a positioned parent, and the
           canvas wrapper is not always one */
        var cs = global.getComputedStyle ? global.getComputedStyle(root) : null;
        if (cs && cs.position === 'static') root.style.position = 'relative';
      } catch (e) {}
      if (!$('cf-shot-chip-css')) {
        var st = document.createElement('style');
        st.id = 'cf-shot-chip-css';
        st.textContent =
          /* TOP RIGHT, CLEAR OF THE RAIL.

             Bottom right put it under the rail and only a sliver showed. The
             rail runs down the right edge, so the chip sits at the top and far
             enough left to miss it (Marek: idealne do praveho horniho rohu,
             ale nesmi byt pres banner, tak mirne doleva).

             PDF red, because that is what it is for and the eye should find it
             without reading it. */
          '#cf-shot-chip{position:absolute;right:' + SHOT_CHIP_RIGHT + 'px;top:14px;z-index:9;height:34px;' +
          'padding:0 14px;border:0;border-radius:17px;color:#fff;display:flex;align-items:center;' +
          'gap:6px;font:800 12px Montserrat,sans-serif;letter-spacing:.04em;cursor:pointer;' +
          'background:' + SHOT_CHIP_RED + ';box-shadow:0 2px 8px rgba(0,0,0,.18);' +
          '-webkit-tap-highlight-color:transparent;touch-action:manipulation;}' +
          '#cf-shot-chip .cf-shot-n{font:800 12px Montserrat,sans-serif;opacity:0.8;}' +
          '#cf-shot-chip.cf-shot-off{display:none !important;}';
        document.head.appendChild(st);
      }
      var b = document.createElement('button');
      b.id = 'cf-shot-chip';
      b.type = 'button';
      b.innerHTML = '+ PDF <span class="cf-shot-n"></span>';
      var timer = 0, held = false;
      b.addEventListener('pointerdown', function () {
        b.style.background = SHOT_CHIP_RED_DOWN;
        held = false;
        timer = global.setTimeout(function () {
          held = true; _pdfShots = []; _shotSync();
          _log('pdf: captured views cleared');
        }, 600);
      });
      b.addEventListener('pointerup', function (e) {
        global.clearTimeout(timer);
        if (held) { e.preventDefault(); _shotSync(); return; }
        _shotTake();
        _shotSync();
      });
      b.addEventListener('pointercancel', function () { global.clearTimeout(timer); });
      root.appendChild(b);
      _shotSync();
      _log('pdf: + PDF chip mounted on ' + (root.id ? ('#' + root.id) : (root.tagName || '?')));
    }

    /* =========================================================================
       THE COVER: THE PRODUCT'S OWN PICTURE, AND THE FIGURES OFF THE SCREEN.

       The exporter can read the totals as PRE-FORMATTED STRINGS, and that is
       the right way round: the four numbers on the page are already in the
       shop's currency, in the visitor's units, with the visitor's labels. Handing
       over the strings means the document cannot disagree with the page it was
       made from -- and the labels come along for free, in whatever language the
       page is in (Marek: preklady dle stranek).

       The picture is the product's own image. og:image is what the shop puts on
       a product page for exactly this purpose, so it is the featured image
       without this file needing to know anything about the theme. */
    function _pdfCoverImage() {
      var pick = function (sel, attr) {
        try {
          var el = global.document.querySelector(sel);
          var v = el && (attr ? el.getAttribute(attr) : el.src);
          return (v && String(v).indexOf('http') === 0) ? String(v) : '';
        } catch (e) { return ''; }
      };
      return pick('meta[property="og:image"]', 'content') ||
             pick('meta[name="twitter:image"]', 'content') ||
             pick('.product__media img') ||
             pick('.product-media img') || '';
    }

    /* the four figures exactly as the strip prints them, or nothing */
    function _pdfTexts() {
      var read = function (id) {
        try {
          var el = $(id);
          var t = el && (el.textContent || '').trim();
          return (t && /[0-9]/.test(t)) ? t : '';
        } catch (e) { return ''; }
      };
      var t = {
        total:     read('cf-bw-total'),
        material:  read('cf-bw-material'),
        execution: read('cf-bw-execution'),
        time:      read('cf-bw-time')
      };
      return (t.total || t.material || t.execution) ? t : null;
    }

    /* WHAT THE PDF FONT CAN ACTUALLY WRITE.

       Imperial dimensions are formatted with the typographic prime and double
       prime -- 10\u2032-0\u2033 -- which is correct on screen and unprintable in
       jsPDF's standard font: every one of them came out as a question mark, so
       the cover of an imperial project read "10?-0? x 15?-0?". The plain
       apostrophe and quote say the same thing and exist in the font.

       Everything the document prints goes through here, not just the title:
       names, units and notes all come from the same formatters (Marek). */
    function _pdfSafe(s) {
      return String(s == null ? '' : s)
        .replace(/[\u2032\u02B9\u2035]/g, "'")      /* prime -> foot */
        .replace(/[\u2033\u02BA\u2036]/g, '"')      /* double prime -> inch */
        .replace(/[\u2010-\u2015]/g, '-')           /* dashes */
        .replace(/[\u2018\u2019]/g, "'")
        .replace(/[\u201C\u201D]/g, '"')
        .replace(/\u00D7/g, 'x')                    /* multiplication sign */
        .replace(/\u00F8/g, 'd')                    /* diameter slashed o */
        .replace(/\u2013/g, '-')
        .replace(/\u2026/g, '...')
        .replace(/\u00A0/g, ' ');
    }

    function _pdfTitle() {
      try {
        var b = BOOTDATA();
        var name = (b && (b.productTitle || b.title)) || (b && b.slug) || 'Project';
        /* the size on the second line, which is what the cover's big type is
           for -- and in the units on screen */
        var dim = '';
        try {
          dim = fmtLen(STATE.footprint.w) + ' x ' + fmtLen(STATE.footprint.l);
        } catch (e) {}
        return _pdfSafe(dim ? (name + '\n' + dim) : name);
      } catch (e) { return 'Project'; }
    }

    /* every code the project bills, once, with the quantity summed */
    /* WHICH BILL A LAYER'S ITEMS BELONG TO (core-v300). Three bills now, in the
       order they are built: the building, then the terrace under it, then the
       pergola over that (Marek: rozdelit main construction a pergola a
       samostatne i terasa; prvni main house, pak terasa, pak pergola). */
    function _billPart(lay) {
      var s = String(lay || '');
      if (/-pergola$/.test(s)) return 'pergola';
      if (/-terrace$/.test(s)) return 'terrace';
      return 'main';
    }
    function _pdfCodes(part) {
      var out = {}, lay, b, k;
      for (lay in _bill) {
        if (!_bill.hasOwnProperty(lay)) continue;
        if (part && _billPart(lay) !== part) continue;
        b = _bill[lay];
        for (k in b) {
          if (!b.hasOwnProperty(k)) continue;
          var c = (CATALOG[k] && CATALOG[k].code) ? CATALOG[k].code : k;
          out[c] = (out[c] || 0) + b[k];
        }
      }
      return out;
    }

    function _pdfBoq(part) {
      var codes = _pdfCodes(part), rows = [], c;
      for (c in codes) {
        if (!codes.hasOwnProperty(c)) continue;
        var qtyM = Math.round(codes[c] * 100) / 100;
        var row = null;
        try { row = _catRow(c); } catch (e) {}
        var shown = qtyM, unit = '';
        try { unit = _pickUnit(row, null); } catch (e) {}
        try { if (!_pickMetric() && row) shown = Math.round(qtyM * _pickConv(row[12], row[13]) * 100) / 100; } catch (e) {}
        var money = null;
        try { money = _pickMoney(c, qtyM); } catch (e) {}
        var nm = '';
        try { nm = _asmName(c, '', c) || c; } catch (e) { nm = c; }
        rows.push({
          code: c, name: _pdfSafe(nm), qty: shown, unit: _pdfSafe(unit),
          unitPrice: (money && shown > 0) ? (money.total / shown) : null,
          total: money ? money.total : null,
          execution: money ? money.execution : 0,
          from: money ? money.from : ''
        });
      }
      rows.sort(function (a, b2) { return a.code < b2.code ? -1 : 1; });
      /* AND SAY HOW MANY OF THEM HAVE A PRICE. A bill of quantities where every
         line reads nought is not a document, and it used to say so nowhere. */
      var priced = 0, viaBridge = 0, j;
      for (j = 0; j < rows.length; j++) {
        if (rows[j].total > 0) priced++;
        if (rows[j].from === 'bridge') viaBridge++;
      }
      if (part) return rows;
      _log('pdf bill: ' + priced + ' of ' + rows.length + ' row(s) priced' +
           (viaBridge ? (' (' + viaBridge + ' through the bridge)') : '') +
           (priced ? '' : ' -- LevaBudgetCore ' + (global.LevaBudgetCore ? 'present' : 'MISSING') +
                          ', bridge ' + (global.ConfiguratorBudgetBridge ? 'present' : 'MISSING') +
                          ', user_prices ' + (function () { try { return Object.keys(global.user_prices || {}).length; } catch (e) { return '?'; } })() + ' key(s)'));
      return rows;
    }

    /* the same bill, cut into its three parts, each with its own title from the
       keys the web already uses; a part with nothing in it is left out */
    /* THE PLAN AS NUMBERS, FOR A DRAWING THAT CAN BE BUILT FROM (core-v302).

       The PDF carries a picture of the plan and not one dimension on it, so
       nobody can set a building out from it (Marek). This hands the plan over
       as geometry instead of pixels: the outline, each wall with the openings
       on it and how far along they sit, the partitions, and the rooms with
       their names. Everything in metres, from the model's own numbers; the
       sheet does the drawing and the unit. */
    /* WHERE EVERY FOOTING GOES (core-v304). A plan says how big the building is;
       it does not say where to put the screws. What the base layer drew is read
       back off the model -- every body whose assembly mark is the foundation's
       own -- and anything a product puts down itself (a pergola post, a terrace
       support) adds its point here as it builds. The sheet dimensions them from
       the building's corner (Marek: vytycovaci vykres zakladu). */
    var _setOut = [];
    function setOutReset() { _setOut = []; }
    function setOutPoint(kind, x, z) {
      if (!isFinite(x) || !isFinite(z)) return;
      _setOut.push({ kind: String(kind || 'point'), x: x, z: z });
    }
    /* THE PLACE STEP, THE SAME IN EVERY PRODUCT (core-v312).

       It was written out three times -- in the ADU, the sauna and the shed --
       and the three copies were the same character for character except that
       the shed leaves the trees out. The step is about the plot, not about the
       building, so it belongs here with the map it opens (Marek: place bude
       vzdy stejne, tam by to spravne patrilo).

       placeStepDef() is the step's words, mode and view, from the line's own
       keys; placeGroups(opts) is what its panel shows -- the address, and one
       switch per kind of tree unless opts.trees is false. A product puts the
       first in its step list and returns the second for the 'place' key, and
       has nothing of its own to keep in step with the others. */
    function _uiStr(name, def) {
      try { var U = _UI(); if (U && U.str) { var s = U.str(_t, name); if (s && s !== name) return s; } } catch (e) {}
      return def;
    }
    function _placeAddrInline() {
      try { return !!(_stripMode() && $('cf-map-modal-backdrop')); } catch (e) { return false; }
    }
    function placeStepDef() {
      return {
        key: 'place', mode: 1, view: '3d',
        label: _t('sauna_wiz_1', 'PLACE'),
        hint: _t('wiz_place_hint', 'Find your lot on the map')
      };
    }
    function placeGroups(opts) {
      opts = opts || {};
      var items = [];
      /* the strip's place panel carries the address field itself (ui-v214),
         so its tile would only open the same field a second time */
      if (!_placeAddrInline()) {
        items.push({ label: _uiStr('address', 'Address'), svg: ICO.pin,
          on: function () { return mapReady(); },
          tap: function () { if (!mapOpen()) _log('place: map not ready yet'); } });
      }
      /* THE LOCK IS GONE (carried over from the ADU). It froze the map so a
         stray drag could not nudge it, which guards against a gesture people use
         on purpose: in 2D dragging the ground IS how the building gets placed on
         the plot. Its tile goes to the surroundings, which answer the question
         the step is asking -- what is this thing standing in. One switch per
         kind of tree the kit offers; all on is a dense scene. A product that is
         bought by the square foot rather than pictured in a garden says
         trees: false and gets the address alone. */
      if (opts.trees !== false) {
        var K = _trees(), kinds = (K && K.kinds) ? K.kinds : [{ key: 'a', n: 1 }, { key: 'b', n: 2 }, { key: 'c', n: 3 }];
        kinds.forEach(function (kd) {
          items.push({ label: _uiStr('scenery', 'Scenery') + ' ' + kd.n, svg: ICO.trees,
                       on: function () { var K2 = _trees(); return !!(K2 && K2.isOn(kd.key)); },
                       tap: function () { toggleTrees(kd.key); var w = _wiz; if (w && w.refresh) w.refresh(); } });
        });
      }
      return [{ key: 'lot', label: _t('grp_lot', 'Lot'), items: items }];
    }

    /* -------------------------------------------------------------------------
       TEXTURES DRAWN ONCE, FOR EVERY PRODUCT (core-v498, step 2 of the T house
       hand-over, Marek: prevest do sdileneho UI to, co budeme pouzivat v jinych
       produktech). The ADU drew its film and its net with the print on them and
       its speckle for the render; the T house copied all four. One copy here,
       cached by everything that shapes the picture, so two products asking for
       the same thing get the same canvas and a product asking for its own
       colours gets its own. Every one returns null where there is no document
       (a test harness) and says so in texLog(), which a builder can print.
         texSheet(T, key, bg, ink)    a film in bg with the worn print in ink
         texMesh(T, key, ink, weave)  a net of strands in weave on a clear
                                      ground with the print in ink
         texGrain(T, hex, G)          the speckle in a colour: G = { rep, dmin,
                                      dvar, amp, count }, tiles rep a metre
         texStucco(T, G)              the same speckle as grey relief, for a
                                      bump map
       ------------------------------------------------------------------------- */
    var _TEX = {}, _TEX_LOG = [];
    var _TEX_PRINT = 'www.theleva.com';
    function _texRnd(seed) { var gs = seed; return function () { gs = (gs * 1103515245 + 12345) & 0x7fffffff; return gs / 0x7fffffff; }; }
    function _texPrint(c, n, ink) {
      var txt = _TEX_PRINT, ang = Math.PI / 6, ca = Math.cos(ang), sa = Math.sin(ang), base = 64;
      c.font = '700 ' + base + 'px Montserrat, Arial';
      var tw = c.measureText(txt).width || (txt.length * base * 0.55), th = base * 1.05;
      var boxW = tw * ca + th * sa, boxH = tw * sa + th * ca;
      var fit = (n * 0.97) / Math.max(boxW, boxH), size = Math.max(10, base * fit);
      c.save();
      c.translate(n / 2, n / 2);
      c.rotate(-ang);
      c.fillStyle = ink;
      c.font = '700 ' + size.toFixed(1) + 'px Montserrat, Arial';
      c.textAlign = 'center';
      c.textBaseline = 'middle';
      c.fillText(txt, 0, 0);
      return { hw: (tw * fit) / 2, hh: (th * fit) / 2 };
    }
    function _texDone(T, cv, srgb) {
      var tx = new T.CanvasTexture(cv);
      if (srgb && T.SRGBColorSpace) tx.colorSpace = T.SRGBColorSpace;
      tx.wrapS = tx.wrapT = T.RepeatWrapping;
      return tx;
    }
    function texSheet(T, key, bg, ink) {
      var k = 'sheet:' + key + ':' + bg + ':' + ink;
      if (_TEX[k]) return _TEX[k];
      try {
        var n = 512, cv = document.createElement('canvas');
        cv.width = n; cv.height = n;
        var c = cv.getContext('2d');
        c.fillStyle = bg;
        c.fillRect(0, 0, n, n);
        var rnd = _texRnd(7311901), gi;
        var box = _texPrint(c, n, ink);
        for (gi = 0; gi < 520; gi++) {
          var sx = (rnd() * 2 - 1) * box.hw * 1.05, sy = (rnd() * 2 - 1) * box.hh * 1.15, sr = 0.6 + rnd() * 3.2;
          c.fillStyle = bg;
          c.globalAlpha = 0.35 + rnd() * 0.5;
          c.beginPath();
          c.arc(sx, sy, sr, 0, Math.PI * 2);
          c.fill();
        }
        c.globalAlpha = 1;
        c.restore();
        _TEX[k] = _texDone(T, cv, true);
        _TEX_LOG.push(key + ' ok bg=' + bg);
        return _TEX[k];
      } catch (e) {
        _TEX_LOG.push(key + ' FAILED ' + (e && e.message));
        return null;
      }
    }
    function texMesh(T, key, ink, weave) {
      var k = 'mesh:' + key + ':' + ink + ':' + weave;
      if (_TEX[k]) return _TEX[k];
      try {
        var n = 512, cv = document.createElement('canvas');
        cv.width = n; cv.height = n;
        var c = cv.getContext('2d');
        var wq, wn = 36;
        c.strokeStyle = weave;
        c.lineWidth = 3.6;
        for (wq = 0; wq <= wn; wq++) {
          var wp = wq * (n / wn);
          c.beginPath(); c.moveTo(wp, 0); c.lineTo(wp, n); c.stroke();
          c.beginPath(); c.moveTo(0, wp); c.lineTo(n, wp); c.stroke();
        }
        _texPrint(c, n, ink);
        c.restore();
        _TEX[k] = _texDone(T, cv, true);
        _TEX_LOG.push(key + ' ok weave=' + weave);
        return _TEX[k];
      } catch (e) {
        _TEX_LOG.push(key + ' FAILED ' + (e && e.message));
        return null;
      }
    }
    function _texGrainOf(G) {
      G = G || {};
      return { rep: (G.rep != null) ? G.rep : 3, dmin: (G.dmin != null) ? G.dmin : 1.5, dvar: (G.dvar != null) ? G.dvar : 2.5, amp: (G.amp != null) ? G.amp : 0.20, count: (G.count != null) ? G.count : 6000 };
    }
    function texGrain(T, hex, G0) {
      var G = _texGrainOf(G0), k = 'grain:' + hex + ':' + G.rep + ':' + G.dmin + ':' + G.dvar + ':' + G.amp + ':' + G.count;
      if (_TEX[k]) return _TEX[k];
      try {
        var n = 256, cv = document.createElement('canvas');
        cv.width = n; cv.height = n;
        var c = cv.getContext('2d');
        var r0 = (hex >> 16) & 255, g0 = (hex >> 8) & 255, b0 = hex & 255;
        c.fillStyle = 'rgb(' + r0 + ',' + g0 + ',' + b0 + ')'; c.fillRect(0, 0, n, n);
        var rnd = _texRnd(4242), i;
        for (i = 0; i < G.count; i++) {
          var lum = 1 + (rnd() - 0.5) * 2 * G.amp;
          var rr = Math.max(0, Math.min(255, Math.round(r0 * lum)));
          var gg = Math.max(0, Math.min(255, Math.round(g0 * lum)));
          var bb = Math.max(0, Math.min(255, Math.round(b0 * lum)));
          c.fillStyle = 'rgb(' + rr + ',' + gg + ',' + bb + ')';
          var d = G.dmin + rnd() * G.dvar;
          c.fillRect(rnd() * n, rnd() * n, d, d);
        }
        var tx = _texDone(T, cv, true);
        tx.repeat.set(G.rep, G.rep);
        _TEX[k] = tx;
        return tx;
      } catch (e) { _TEX_LOG.push('grain FAILED ' + (e && e.message)); return null; }
    }
    function texStucco(T, G0) {
      var G = _texGrainOf(G0), k = 'stucco:' + G.rep + ':' + G.dmin + ':' + G.dvar + ':' + G.count;
      if (_TEX[k]) return _TEX[k];
      try {
        var n = 256, cv = document.createElement('canvas');
        cv.width = n; cv.height = n;
        var c = cv.getContext('2d');
        c.fillStyle = 'rgb(128,128,128)'; c.fillRect(0, 0, n, n);
        var rnd = _texRnd(9137), i;
        for (i = 0; i < G.count; i++) {
          var v = Math.round(128 + (rnd() - 0.5) * 240), d = G.dmin + rnd() * G.dvar;
          c.fillStyle = 'rgb(' + v + ',' + v + ',' + v + ')';
          c.fillRect(rnd() * n, rnd() * n, d, d);
        }
        var tx = _texDone(T, cv, false);
        tx.repeat.set(G.rep, G.rep);
        _TEX[k] = tx;
        return tx;
      } catch (e) { _TEX_LOG.push('stucco FAILED ' + (e && e.message)); return null; }
    }
    function texLog() { return _TEX_LOG.slice(); }

    /* -------------------------------------------------------------------------
       MASONRY BLOCKS (core-v499, step 3 of the T house hand-over, Marek: hlavne
       masonry logiku, ta bude na mnoha dalsich projektech). The block systems a
       masonry house is built of, the block as the person sizes it on the
       STRUCTURE step, the bond rule, the wall's thickness, the module and the
       course that follow from it, and the block as it looks -- the clay block
       with its slots and teeth, the two-core concrete stretcher, the EPS form
       -- with the chamfered piece body the courses are drawn as. Moved out of
       configurator-thouse-lib.js word for word; the choice and the sizes stay
       in STATE.product (masonry, block) where the project keeps them. The
       product reads it all through API.masonry.
       ------------------------------------------------------------------------- */
    var MAS_SYSTEMS = [
      { id: 'clay', i18n: 'clay_block', def: 'Clay block', t: 0.44, col: 0xC96F4A,
        img: 'https://cdn.shopify.com/s/files/1/0803/9815/6119/files/Clay_block.png?v=1790168236' },
      { id: 'cmu',  i18n: 'concrete',   def: 'Concrete', t: 0.30, col: 0xA8A29A,
        img: 'https://cdn.shopify.com/s/files/1/0803/9815/6119/files/Concrete_block.png?v=1790168236' },
      /* THE THIRD: insulated concrete forms, EPS blocks stacked dry and filled
         with concrete (thouse-v36). The word is the site's own EPS layer key,
         already translated. */
      { id: 'eps',  i18n: 'layer_eps',  def: 'EPS', t: 0.30, col: 0xF2F2EE,
        img: 'https://cdn.shopify.com/s/files/1/0803/9815/6119/files/Eps_block.png?v=1790168236' }
    ];
    /* THE CHOICE GOES WITH THE PROJECT (thouse-v32). It lived in a variable,
       so a reload or a shared link came back in clay whatever had been picked
       -- and with it another wall, another slab edge and another bill. It is
       kept in the product's own bag, STATE.product, which the core saves and
       restores whole; anything unknown there falls back to clay. */
    function masSysId() {
      var P = STATE.product || {}, v = P.masonry, i;
      for (i = 0; i < MAS_SYSTEMS.length; i++) if (MAS_SYSTEMS[i].id === v) return v;
      return MAS_SYSTEMS[0].id;
    }
    function masSysSaved() { var P = STATE.product || {}; return masSysId() === P.masonry; }
    function masSys() {
      var i, id = masSysId();
      for (i = 0; i < MAS_SYSTEMS.length; i++) if (MAS_SYSTEMS[i].id === id) return MAS_SYSTEMS[i];
      return MAS_SYSTEMS[0];
    }

    /* -------------------------------------------------------------------------
       THE BLOCK ITSELF (thouse-v33).

       Its size is the user's, not a catalogue format: there are far too many
       formats across three materials to offer them as a list, so the block is
       drawn on the STRUCTURE step in place of the house and its length, width
       and height are dragged on it (Marek: vizualne podobny blok misto stavby
       primo ve 3D, zmena rozmeru po 10 mm / 1 inch). It is billed by the piece
       without a size, so a free size costs nothing in the bill.

       Each material keeps its own size, so switching clay -> concrete -> clay
       comes back to the clay block that was set. Stored in the product's bag
       with the project; nothing stored means the material's usual block.

         length  along the wall     width  across it = the wall's thickness
         height  one course

       The joints belong to the material, not to the drag: a clay block is laid
       on a thin bed with its ends dry in a tongue and groove, a concrete block
       on 10 mm of mortar both ways. They set the module the plan and the
       courses will step by (B6).
       ------------------------------------------------------------------------- */
    var MAS_BLOCK_DEF = {
      clay: { l: 0.250, w: 0.440, h: 0.249, bed: 0.001, head: 0.000 },
      cmu:  { l: 0.390, w: 0.190, h: 0.190, bed: 0.010, head: 0.010 },
      eps:  { l: 1.000, w: 0.300, h: 0.250, bed: 0.000, head: 0.000, shell: 0.060 }
    };
    /* the limits are the material's: an EPS form is a metre long where a
       clay block is a quarter of one, and a US form is 48 x 16 in */
    var MAS_BLOCK_LIMS = {
      clay: { l: [0.10, 0.60], w: [0.10, 0.60], h: [0.05, 0.40] },
      cmu:  { l: [0.10, 0.60], w: [0.10, 0.60], h: [0.05, 0.40] },
      eps:  { l: [0.40, 1.25], w: [0.20, 0.45], h: [0.20, 0.45] }
    };
    function masBlockLim(id) { return MAS_BLOCK_LIMS[id] || MAS_BLOCK_LIMS.clay; }
    function masBlockClamp(v, k, id) { var L = masBlockLim(id)[k]; return Math.max(L[0], Math.min(L[1], v)); }
    function masBlock(id) {
      id = id || masSysId();
      var D = MAS_BLOCK_DEF[id] || MAS_BLOCK_DEF.clay, P = STATE.product || {}, S = (P.block && P.block[id]) || null, k, out = {};
      for (k in D) if (D.hasOwnProperty(k)) out[k] = D[k];
      if (S) {
        if (typeof S.l === 'number' && isFinite(S.l)) out.l = masBlockClamp(S.l, 'l', id);
        if (typeof S.w === 'number' && isFinite(S.w)) out.w = masBlockClamp(S.w, 'w', id);
        if (typeof S.h === 'number' && isFinite(S.h)) out.h = masBlockClamp(S.h, 'h', id);
      }
      /* EVERY SIZE IS FREE AGAIN (thouse-v42): each drag point moves its own
         size only, by 10 mm or 2 in, so the real products can be set -- a
         440 clay block 250 long, a 12 in concrete block. Where a block's width
         does not bond whole at a corner, the corner takes the corner piece the
         maker sells for it (below, in the laying); everything else stays
         whole. */
      out.saved = !!S;
      return out;
    }
    /* THE BOND RULE (thouse-v39, Marek: vsechny cihly cele, zadne pulky; blok
       drzi pravidlo vazby). Every block whole, corners bonded, is possible only
       when a block's width and joint make half its length and joint -- or one
       and a half, two and a half: then the wall running through a corner and
       the wall butting on it differ by a whole number of blocks, and the head
       joints of one course fall half a block over those of the next. So the
       width is held to width + joint = (k + 1/2) x (length + joint). */
    function masBondWidths(id, l, j) {
      var L = masBlockLim(id).w, m = l + (j || 0), out = [], k;
      for (k = 0; k < 8; k++) {
        var w = Math.round(((k + 0.5) * m - (j || 0)) * 10000) / 10000;
        if (w >= L[0] - 1e-9 && w <= L[1] + 1e-9) out.push(w);
      }
      return out;
    }
    /* and the other way round: the lengths a given width bonds with */
    function masBondLengths(id, w, j) {
      var L = masBlockLim(id).l, out = [], k;
      for (k = 0; k < 8; k++) {
        var l = Math.round(((w + (j || 0)) / (k + 0.5) - (j || 0)) * 10000) / 10000;
        if (l >= L[0] - 1e-9 && l <= L[1] + 1e-9) out.push(l);
      }
      return out;
    }
    function masNearest(list, v) {
      var best = list[0], i;
      for (i = 1; i < list.length; i++) if (Math.abs(list[i] - v) < Math.abs(best - v)) best = list[i];
      return best;
    }
    /* THE BLOCK SETS THE WALL (thouse-v37): the wall is as thick as the block
       is wide, the plan steps by the block and its head joint, and the
       masonry rises by whole courses of block and bed joint. The figures in
       the systems list were stand-ins until the block could be sized. */
    function masWallT() { return masBlock(masSysId()).w; }
    function masModule() { var B = masBlock(masSysId()); return B.l + B.head; }
    function masCourse() { var B = masBlock(masSysId()); return B.h + B.bed; }

    /* THE CLAY BLOCK AS IT LOOKS: a plan section extruded to its height. Its
       ends carry the tongue and groove it is laid dry by, its body the rows of
       narrow slots that make it insulate -- long along the wall, thin across
       it, every other row shifted half a slot -- and two grip pockets in the
       middle. The slots are counted from the size, so a dragged block keeps
       looking like a block and not like a scaled picture of one. */
    function masClayGeo(T, l, w, h) {
      var SH = 0.012, WEB = 0.008, SU = 0.040, SV = 0.010, TOOTH = 0.008, PITCH = 0.034;
      var hl = l / 2, hw = w / 2, i, r, c;
      function endProfile(xOut, xIn, shift) {
        var pts = [], n = Math.max(0, Math.floor((w - 2 * SH) / PITCH)), a = PITCH * 0.12;
        var v0 = -n * PITCH / 2 + shift, k;
        pts.push([xOut, -hw]);
        for (k = 0; k < n; k++) {
          var v = v0 + k * PITCH;
          if (v < -hw + SH || v + PITCH > hw - SH) continue;
          pts.push([xOut, v], [xIn, v + a], [xIn, v + PITCH / 2 - a], [xOut, v + PITCH / 2]);
        }
        pts.push([xOut, hw]);
        return pts;
      }
      var R = endProfile(hl, hl - TOOTH, 0), Lp = endProfile(-hl, -hl + TOOTH, PITCH / 2);
      var shape = new T.Shape();
      shape.moveTo(-hl, -hw);
      shape.lineTo(hl, -hw);
      for (i = 1; i < R.length - 1; i++) shape.lineTo(R[i][0], R[i][1]);
      shape.lineTo(hl, hw);
      shape.lineTo(-hl, hw);
      for (i = Lp.length - 2; i >= 1; i--) shape.lineTo(Lp[i][0], Lp[i][1]);
      shape.lineTo(-hl, -hw);
      /* the inside: slots and grip pockets, inside a shell and clear of the teeth */
      var uIn = hl - TOOTH - SH, vIn = hw - SH, nSlot = 0, nGrip = 0;
      var grips = [];
      if (uIn > 0.03 && vIn > 0.06) {
        var gU = Math.min(0.090, uIn * 0.9), gV = Math.min(0.040, vIn * 0.35);
        grips.push({ u0: -gU / 2, u1: gU / 2, v0: vIn / 2 - gV / 2, v1: vIn / 2 + gV / 2 });
        grips.push({ u0: -gU / 2, u1: gU / 2, v0: -vIn / 2 - gV / 2, v1: -vIn / 2 + gV / 2 });
      }
      function hole(u0, u1, v0, v1) {
        var hp = new T.Path();
        hp.moveTo(u0, v0); hp.lineTo(u1, v0); hp.lineTo(u1, v1); hp.lineTo(u0, v1); hp.lineTo(u0, v0);
        shape.holes.push(hp);
      }
      for (i = 0; i < grips.length; i++) { hole(grips[i].u0, grips[i].u1, grips[i].v0, grips[i].v1); nGrip++; }
      var pV = SV + WEB, pU = SU + WEB;
      var nRow = Math.max(0, Math.floor((2 * vIn + WEB) / pV)), nCol = Math.max(0, Math.floor((2 * uIn + WEB) / pU));
      var vStart = -(nRow * pV - WEB) / 2;
      for (r = 0; r < nRow; r++) {
        var v0 = vStart + r * pV, v1 = v0 + SV, odd = (r % 2 === 1);
        var nc = odd ? nCol - 1 : nCol;
        if (nc < 1) continue;
        var uStart = -(nc * pU - WEB) / 2;
        for (c = 0; c < nc; c++) {
          var u0 = uStart + c * pU, u1 = u0 + SU, clash = false, q;
          for (q = 0; q < grips.length; q++) {
            var G = grips[q];
            if (u1 > G.u0 - WEB / 2 && u0 < G.u1 + WEB / 2 && v1 > G.v0 - WEB / 2 && v0 < G.v1 + WEB / 2) { clash = true; break; }
          }
          if (clash) continue;
          hole(u0, u1, v0, v1); nSlot++;
        }
      }
      var geo = new T.ExtrudeGeometry(shape, { depth: h, bevelEnabled: false, steps: 1, curveSegments: 1 });
      geo.rotateX(-Math.PI / 2);
      geo.computeVertexNormals();
      return { geo: geo, slots: nSlot, grips: nGrip, teeth: Math.max(0, R.length - 2) / 4, tooth: TOOTH, pitch: PITCH };
    }

    /* THE CONCRETE BLOCK AS IT LOOKS (thouse-v35): the two-core stretcher a
       US wall is laid from -- two face shells along the wall, webs across it at
       the ends and between the cores, the cores open top to bottom with their
       corners rounded. The number of cores follows the length (one on a short
       block, two on the usual 390, three from about 550), and a narrow block
       gets thinner shells and webs, so a dragged block stays a real one. */
    function masCmuGeo(T, l, w, h) {
      var FS = Math.min(0.032, w * 0.16), WEB = Math.min(0.025, l * 0.07);
      var n = Math.max(1, Math.round((l - WEB) / 0.195));
      var cw = (l - (n + 1) * WEB) / n, cd = w - 2 * FS, hl = l / 2, hw = w / 2, i;
      var shape = new T.Shape();
      shape.moveTo(-hl, -hw); shape.lineTo(hl, -hw); shape.lineTo(hl, hw); shape.lineTo(-hl, hw); shape.lineTo(-hl, -hw);
      var nCore = 0;
      if (cw > 0.02 && cd > 0.02) {
        var r = Math.min(0.020, 0.25 * Math.min(cw, cd));
        for (i = 0; i < n; i++) {
          var u0 = -hl + WEB + i * (cw + WEB), u1 = u0 + cw, v0 = -cd / 2, v1 = cd / 2;
          var hp = new T.Path();
          hp.moveTo(u0 + r, v0);
          hp.lineTo(u1 - r, v0); hp.quadraticCurveTo(u1, v0, u1, v0 + r);
          hp.lineTo(u1, v1 - r); hp.quadraticCurveTo(u1, v1, u1 - r, v1);
          hp.lineTo(u0 + r, v1); hp.quadraticCurveTo(u0, v1, u0, v1 - r);
          hp.lineTo(u0, v0 + r); hp.quadraticCurveTo(u0, v0, u0 + r, v0);
          shape.holes.push(hp);
          nCore++;
        }
      }
      var geo = new T.ExtrudeGeometry(shape, { depth: h, bevelEnabled: false, steps: 1, curveSegments: 4 });
      geo.rotateX(-Math.PI / 2);
      geo.computeVertexNormals();
      return { geo: geo, cores: nCore, shell: FS, web: WEB };
    }

    /* THE EPS FORM AS IT LOOKS (thouse-v36): two white EPS panels, the core
       between them left open for the concrete, plastic ties across the core at
       even centres, and the studs along the top of each panel that lock the
       next course on when the forms are stacked dry. Drawn as a few bodies into
       the sample's group; the ties and studs follow the length. */
    function masEpsBlock(T, g, B, ox, oz, col) {
      var SH = Math.min(B.shell || 0.060, B.w * 0.3), core = B.w - 2 * SH, l = B.l, h = B.h, i, s2;
      var foam = new T.MeshStandardMaterial({ color: col, roughness: 1, metalness: 0 });
      var tieM = new T.MeshStandardMaterial({ color: 0x55585C, roughness: 0.6, metalness: 0 });
      var edM = new T.LineBasicMaterial({ color: 0x3A2A22, transparent: true, opacity: 0.35 });
      function box(w, hh, d, x, y, z, m, edge) {
        var geo = new T.BoxGeometry(w, hh, d);
        var mm = new T.Mesh(geo, m);
        mm.position.set(ox + x, y, oz + z);
        g.add(mm);
        if (edge) { try { var e = new T.LineSegments(new T.EdgesGeometry(geo, 30), edM); e.position.copy(mm.position); g.add(e); } catch (eE) {} }
        return mm;
      }
      for (s2 = -1; s2 <= 1; s2 += 2) box(l, h, SH, 0, h / 2, s2 * (B.w / 2 - SH / 2), foam, true);
      var nTie = Math.max(2, Math.round(l / 0.15)), pT = l / nTie;
      for (i = 0; i < nTie; i++) box(0.004, h * 0.8, core, -l / 2 + pT * (i + 0.5), h / 2, 0, tieM, false);
      var nTooth = Math.max(2, Math.floor(l / 0.10)), pS = l / nTooth, tw = Math.min(0.045, pS * 0.5), td = Math.min(0.030, SH * 0.5), th = 0.018;
      for (s2 = -1; s2 <= 1; s2 += 2) {
        for (i = 0; i < nTooth; i++) box(tw, th, td, -l / 2 + pS * (i + 0.5), h + th / 2, s2 * (B.w / 2 - SH / 2), foam, true);
      }
      return { shell: SH, core: core, ties: nTie, teeth: 2 * nTooth };
    }

    var _MAS_CHB = null;
    /* a chamfered box as 44 triangles, each corner written as which side it
       is on and whether the chamfer pulls it in, so one list serves any size */
    function masChbTemplate() {
      if (_MAS_CHB) return _MAS_CHB;
      var tris = [], F = [], ax, sgn;
      function V(sx, kx, sy, ky, sz, kz) { return [sx, kx, sy, ky, sz, kz]; }
      function at(v, hx, hy, hz, c) { return [v[0] * (hx - v[1] * c), v[2] * (hy - v[3] * c), v[4] * (hz - v[5] * c)]; }
      function add(a, b, c2, n) {
        /* wound so the face looks outwards: tested on a sample size once */
        var A = at(a, 1, 1, 1, 0.2), B2 = at(b, 1, 1, 1, 0.2), C = at(c2, 1, 1, 1, 0.2);
        var ux = B2[0] - A[0], uy = B2[1] - A[1], uz = B2[2] - A[2], vx = C[0] - A[0], vy = C[1] - A[1], vz = C[2] - A[2];
        var cx = uy * vz - uz * vy, cy = uz * vx - ux * vz, cz = ux * vy - uy * vx;
        if (cx * n[0] + cy * n[1] + cz * n[2] < 0) tris.push([a, c2, b, n]); else tris.push([a, b, c2, n]);
      }
      function quad(a, b, c2, d, n) { add(a, b, c2, n); add(a, c2, d, n); }
      var r2 = Math.SQRT1_2, r3 = 1 / Math.sqrt(3);
      /* the six faces, each inset by the chamfer on its four edges */
      for (sgn = -1; sgn <= 1; sgn += 2) {
        quad(V(sgn, 0, -1, 1, -1, 1), V(sgn, 0, 1, 1, -1, 1), V(sgn, 0, 1, 1, 1, 1), V(sgn, 0, -1, 1, 1, 1), [sgn, 0, 0]);
        quad(V(-1, 1, sgn, 0, -1, 1), V(1, 1, sgn, 0, -1, 1), V(1, 1, sgn, 0, 1, 1), V(-1, 1, sgn, 0, 1, 1), [0, sgn, 0]);
        quad(V(-1, 1, -1, 1, sgn, 0), V(1, 1, -1, 1, sgn, 0), V(1, 1, 1, 1, sgn, 0), V(-1, 1, 1, 1, sgn, 0), [0, 0, sgn]);
      }
      /* the twelve chamfers along the edges */
      var s1, s2;
      for (s1 = -1; s1 <= 1; s1 += 2) for (s2 = -1; s2 <= 1; s2 += 2) {
        /* edges along z: between the x and y faces */
        quad(V(s1, 0, s2, 1, -1, 1), V(s1, 0, s2, 1, 1, 1), V(s1, 1, s2, 0, 1, 1), V(s1, 1, s2, 0, -1, 1), [s1 * r2, s2 * r2, 0]);
        /* edges along y: between the x and z faces */
        quad(V(s1, 0, -1, 1, s2, 1), V(s1, 0, 1, 1, s2, 1), V(s1, 1, 1, 1, s2, 0), V(s1, 1, -1, 1, s2, 0), [s1 * r2, 0, s2 * r2]);
        /* edges along x: between the y and z faces */
        quad(V(-1, 1, s1, 0, s2, 1), V(1, 1, s1, 0, s2, 1), V(1, 1, s1, 1, s2, 0), V(-1, 1, s1, 1, s2, 0), [0, s1 * r2, s2 * r2]);
      }
      /* the eight corners */
      var s3;
      for (s1 = -1; s1 <= 1; s1 += 2) for (s2 = -1; s2 <= 1; s2 += 2) for (s3 = -1; s3 <= 1; s3 += 2) {
        add(V(s1, 0, s2, 1, s3, 1), V(s1, 1, s2, 0, s3, 1), V(s1, 1, s2, 1, s3, 0), [s1 * r3, s2 * r3, s3 * r3]);
      }
      _MAS_CHB = tris;
      return tris;
    }
    /* the pieces of one run, laid from both ends: [start, length, kind] */
    function masLayRun(R, sA, sB, L, j, smin) {
      var out = [], u = 0, lens = [], kinds = [], q;
      var inner = R - (sA > 0 ? sA + j : 0) - (sB > 0 ? sB + j : 0);
      if (inner < smin) return [[0, R, 'cut']];
      var m = L + j, n, cutLen = 0, nx = Math.round((inner + j) / m);
      if (nx >= 1 && Math.abs(nx * m - j - inner) < 0.003) n = nx;
      else {
        n = Math.max(0, Math.floor((inner - smin) / m));
        cutLen = inner - n * m;
        if (cutLen > L + 0.0005) { n += 1; cutLen = inner - n * m; }
        if (cutLen < 0.003) cutLen = 0;
      }
      if (sA > 0) { lens.push(sA); kinds.push('starter'); }
      var nA = Math.floor(n / 2);
      for (q = 0; q < nA; q++) { lens.push(L); kinds.push('whole'); }
      if (cutLen > 0) { lens.push(cutLen); kinds.push('cut'); }
      for (q = nA; q < n; q++) { lens.push(L); kinds.push('whole'); }
      if (sB > 0) { lens.push(sB); kinds.push('starter'); }
      for (q = 0; q < lens.length; q++) { out.push([u, lens[q], kinds[q]]); u += lens[q] + j; }
      return out;
    }
    /* the pieces into one body: centre, half sizes, chamfer */
    function masChbMesh(T, pcs, col) {
      var tp = masChbTemplate(), nV = pcs.length * tp.length * 3;
      var pos = new Float32Array(nV * 3), nrm = new Float32Array(nV * 3), o = 0, i, k, q;
      for (i = 0; i < pcs.length; i++) {
        var P = pcs[i], hx = P[3], hy = P[4], hz = P[5], c = P[6], C = P[7] || null;
        for (k = 0; k < tp.length; k++) {
          var tr = tp[k], n = tr[3], o0 = o;
          for (q = 0; q < 3; q++) {
            var v = tr[q];
            /* A CHAMFER PER SIDE (thouse-v254): a piece may carry six chamfers
               [x-, x+, y-, y+, z-, z+] instead of one, so a block notched for
               a joist pocket is drawn in parts that meet without a joint */
            var cx = C ? (v[0] < 0 ? C[0] : C[1]) : c, cy = C ? (v[2] < 0 ? C[2] : C[3]) : c, cz = C ? (v[4] < 0 ? C[4] : C[5]) : c;
            pos[o] = P[0] + v[0] * (hx - v[1] * cx);
            pos[o + 1] = P[1] + v[2] * (hy - v[3] * cy);
            pos[o + 2] = P[2] + v[4] * (hz - v[5] * cz);
            nrm[o] = n[0]; nrm[o + 1] = n[1]; nrm[o + 2] = n[2];
            o += 3;
          }
          if (C) {
            /* a suppressed chamfer flattens its strip into the face: give the
               strip the face's normal, from the triangle itself */
            var ax = pos[o0 + 3] - pos[o0], ay = pos[o0 + 4] - pos[o0 + 1], az = pos[o0 + 5] - pos[o0 + 2];
            var bx = pos[o0 + 6] - pos[o0], by = pos[o0 + 7] - pos[o0 + 1], bz = pos[o0 + 8] - pos[o0 + 2];
            var nx9 = ay * bz - az * by, ny9 = az * bx - ax * bz, nz9 = ax * by - ay * bx, nl9 = Math.hypot(nx9, ny9, nz9);
            if (nl9 > 1e-12) {
              if (nx9 * n[0] + ny9 * n[1] + nz9 * n[2] < 0) { nx9 = -nx9; ny9 = -ny9; nz9 = -nz9; }
              for (q = 0; q < 3; q++) { nrm[o0 + q * 3] = nx9 / nl9; nrm[o0 + q * 3 + 1] = ny9 / nl9; nrm[o0 + q * 3 + 2] = nz9 / nl9; }
            }
          }
        }
      }
      var geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.BufferAttribute(pos, 3));
      geo.setAttribute('normal', new T.BufferAttribute(nrm, 3));
      geo.computeBoundingSphere();
      return new T.Mesh(geo, new T.MeshStandardMaterial({ color: col, roughness: 0.9, metalness: 0 }));
    }

    /* PLAIN BOXES IN ONE BODY (thouse-v258): [cx, cy, cz, hx, hy, hz] each,
       36 vertices a box, for the many small strips a joint pattern needs */
    function masBoxBatch(T, pcs, col) {
      var n = pcs.length, pos = new Float32Array(n * 36 * 3), nrm = new Float32Array(n * 36 * 3), o = 0, i, f, q;
      var F = [[1, 0, 0], [-1, 0, 0], [0, 1, 0], [0, -1, 0], [0, 0, 1], [0, 0, -1]];
      for (i = 0; i < n; i++) {
        var P = pcs[i], hx = P[3], hy = P[4], hz = P[5];
        for (f = 0; f < 6; f++) {
          var N = F[f], ax = Math.abs(N[0]) ? 0 : (Math.abs(N[1]) ? 1 : 2), u = (ax === 0) ? 1 : 0, v = (ax === 2) ? 1 : 2;
          var sgnN = N[ax], H = [hx, hy, hz], c = [P[0], P[1], P[2]], corners = [], k;
          for (k = 0; k < 4; k++) {
            var su = (k === 1 || k === 2) ? 1 : -1, sv = (k >= 2) ? 1 : -1, pt = [0, 0, 0];
            pt[ax] = c[ax] + sgnN * H[ax]; pt[u] = c[u] + su * H[u]; pt[v] = c[v] + sv * H[v];
            corners.push(pt);
          }
          /* wound outwards: the cross product of the first two edges along the normal */
          var e1 = [corners[1][0] - corners[0][0], corners[1][1] - corners[0][1], corners[1][2] - corners[0][2]];
          var e2 = [corners[2][0] - corners[0][0], corners[2][1] - corners[0][1], corners[2][2] - corners[0][2]];
          var cr = [e1[1] * e2[2] - e1[2] * e2[1], e1[2] * e2[0] - e1[0] * e2[2], e1[0] * e2[1] - e1[1] * e2[0]];
          var flip = (cr[0] * N[0] + cr[1] * N[1] + cr[2] * N[2]) < 0;
          var tri = flip ? [0, 2, 1, 0, 3, 2] : [0, 1, 2, 0, 2, 3];
          for (q = 0; q < 6; q++) {
            var cp = corners[tri[q]];
            pos[o] = cp[0]; pos[o + 1] = cp[1]; pos[o + 2] = cp[2];
            nrm[o] = N[0]; nrm[o + 1] = N[1]; nrm[o + 2] = N[2];
            o += 3;
          }
        }
      }
      var geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.BufferAttribute(pos, 3));
      geo.setAttribute('normal', new T.BufferAttribute(nrm, 3));
      geo.computeBoundingSphere();
      return new T.Mesh(geo, new T.MeshStandardMaterial({ color: col, roughness: 0.9, metalness: 0 }));
    }

    /* -------------------------------------------------------------------------
       THE MASONRY WALLS (core-v500, step 4 of the T house hand-over, Marek:
       hlavne masonry logiku). configurator-thouse-lib.js's _buildMass moved
       here word for word as masonry.build(ctx, spec): the bearing walls laid
       course by course from the block, corners bonded as a pinwheel, the
       openings cut out with their lintels and the courses over them, the
       partitions with their own block, the joist pockets of the bearing
       course, the ring beam with its cage, the gables cut under the rafters,
       and every piece into the bill. The product hands over what is its own
       in spec -- the floor level and the slab top, the facade's thickness for
       the sill, the partition systems, its constants and its helpers, the
       joist pockets the floor recorded, the plan's wall starts -- and the
       drawer reads the block, the outline, the storeys and the roof itself.
       ------------------------------------------------------------------------- */
    function masMod(v, m) { return ((v % m) + m) % m; }
    function _masCutBox(piece, planes, joint, out) {
      var cx = piece[0], cy = piece[1], cz = piece[2], hx = piece[3] - joint, hy = piece[4] - joint, hz = piece[5] - joint;
      if (hx <= 0.002 || hy <= 0.002 || hz <= 0.002) return;
      var V = [
        [cx - hx, cy - hy, cz - hz], [cx + hx, cy - hy, cz - hz], [cx + hx, cy + hy, cz - hz], [cx - hx, cy + hy, cz - hz],
        [cx - hx, cy - hy, cz + hz], [cx + hx, cy - hy, cz + hz], [cx + hx, cy + hy, cz + hz], [cx - hx, cy + hy, cz + hz]
      ];
      var faces = [[0, 3, 2, 1], [4, 5, 6, 7], [0, 1, 5, 4], [2, 3, 7, 6], [1, 2, 6, 5], [0, 4, 7, 3]], polys = [], f;
      for (f = 0; f < faces.length; f++) polys.push([V[faces[f][0]], V[faces[f][1]], V[faces[f][2]], V[faces[f][3]]]);
      var q;
      for (q = 0; q < planes.length; q++) {
        var pl = planes[q], n = pl.normal, c = pl.constant, dist = function (p) { return n.x * p[0] + n.y * p[1] + n.z * p[2] + c; };
        /* nothing to do when the whole block is one side of the plane */
        var allIn = true, allOut = true, k;
        for (k = 0; k < 8; k++) { var dk = dist(V[k]); if (dk < -1e-6) allIn = false; if (dk > 1e-6) allOut = false; }
        if (allOut) return;
        if (allIn) continue;
        var nextPolys = [], capPts = [], pi;
        for (pi = 0; pi < polys.length; pi++) {
          var poly = polys[pi], outP = [], j;
          for (j = 0; j < poly.length; j++) {
            var A = poly[j], B = poly[(j + 1) % poly.length], dA = dist(A), dB = dist(B);
            if (dA >= 0) outP.push(A);
            if ((dA >= 0) !== (dB >= 0)) {
              var t = dA / (dA - dB), X = [A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t];
              outP.push(X); capPts.push(X);
            }
          }
          if (outP.length >= 3) nextPolys.push(outP);
        }
        polys = nextPolys;
        if (capPts.length >= 3) {
          /* the cap: the cut points ordered round their centre in the plane */
          var mx = 0, my = 0, mz = 0, m;
          for (m = 0; m < capPts.length; m++) { mx += capPts[m][0]; my += capPts[m][1]; mz += capPts[m][2]; }
          mx /= capPts.length; my /= capPts.length; mz /= capPts.length;
          var ax = Math.abs(n.x) < 0.9 ? [1, 0, 0] : [0, 0, 1];
          var u = [n.y * ax[2] - n.z * ax[1], n.z * ax[0] - n.x * ax[2], n.x * ax[1] - n.y * ax[0]], ul = Math.hypot(u[0], u[1], u[2]) || 1;
          u = [u[0] / ul, u[1] / ul, u[2] / ul];
          var v = [n.y * u[2] - n.z * u[1], n.z * u[0] - n.x * u[2], n.x * u[1] - n.y * u[0]];
          capPts.sort(function (P1, P2) {
            var a1 = Math.atan2((P1[0] - mx) * v[0] + (P1[1] - my) * v[1] + (P1[2] - mz) * v[2], (P1[0] - mx) * u[0] + (P1[1] - my) * u[1] + (P1[2] - mz) * u[2]);
            var a2 = Math.atan2((P2[0] - mx) * v[0] + (P2[1] - my) * v[1] + (P2[2] - mz) * v[2], (P2[0] - mx) * u[0] + (P2[1] - my) * u[1] + (P2[2] - mz) * u[2]);
            return a1 - a2;
          });
          /* the cap faces away from the kept side: wound so its normal is -n */
          capPts.reverse();
          polys.push(capPts);
        }
      }
      for (q = 0; q < polys.length; q++) {
        var pg = polys[q], t2;
        for (t2 = 1; t2 + 1 < pg.length; t2++) out.push(pg[0][0], pg[0][1], pg[0][2], pg[t2][0], pg[t2][1], pg[t2][2], pg[t2 + 1][0], pg[t2 + 1][1], pg[t2 + 1][2]);
      }
    }
    /* WHERE A BLOCK STANDS AGAINST THE PLANES (core-v532): wholly under them
       ('in'), wholly over one of them ('out'), or crossed by one ('cut') */
    function _masBoxState(piece, planes) {
      var cx = piece[0], cy = piece[1], cz = piece[2], hx = piece[3], hy = piece[4], hz = piece[5], q, k, anyCut = false;
      for (q = 0; q < planes.length; q++) {
        var n = planes[q].normal, c = planes[q].constant, allIn = true, allOut = true;
        for (k = 0; k < 8; k++) {
          var px = cx + ((k & 1) ? hx : -hx), py = cy + ((k & 2) ? hy : -hy), pz = cz + ((k & 4) ? hz : -hz);
          var d = n.x * px + n.y * py + n.z * pz + c;
          if (d < -1e-6) allIn = false; if (d > 1e-6) allOut = false;
        }
        if (allOut) return 'out';
        if (!allIn) anyCut = true;
      }
      return anyCut ? 'cut' : 'in';
    }
    function _masCutMesh(T, pieces, planes, col, joint) {
      var pos = [], i, jt = (joint == null) ? 0.004 : joint;
      for (i = 0; i < pieces.length; i++) _masCutBox(pieces[i], planes, jt, pos);
      if (!pos.length) return null;
      var geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
      geo.computeVertexNormals();
      return new T.Mesh(geo, new T.MeshStandardMaterial({ color: col, roughness: 0.9, metalness: 0 }));
    }
    function masPlanPoly(pts) { var o = [], q; for (q = 0; q < pts.length; q++) o.push([pts[q][0], pts[q][2]]); return o; }
    function masPtIn(pp, x, z) {
      var inside = false, i, j;
      for (i = 0, j = pp.length - 1; i < pp.length; j = i++) {
        var xi = pp[i][0], zi = pp[i][1], xj = pp[j][0], zj = pp[j][1];
        if (((zi > z) !== (zj > z)) && (x < (xj - xi) * (z - zi) / ((zj - zi) || 1e-9) + xi)) inside = !inside;
      }
      return inside;
    }
    function masPlaneEq(pts) {
      var a = pts[0], b = null, c = null, q;
      for (q = 1; q < pts.length && !b; q++) if (Math.hypot(pts[q][0] - a[0], pts[q][1] - a[1], pts[q][2] - a[2]) > 1e-6) b = pts[q];
      for (q = 1; q < pts.length && !c; q++) {
        if (!b || pts[q] === b) continue;
        var ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2], vx = pts[q][0] - a[0], vy = pts[q][1] - a[1], vz = pts[q][2] - a[2];
        var nx = uy * vz - uz * vy, ny = uz * vx - ux * vz, nz = ux * vy - uy * vx;
        if (Math.hypot(nx, ny, nz) > 1e-6) c = pts[q];
      }
      if (!b || !c) return null;
      var ux2 = b[0] - a[0], uy2 = b[1] - a[1], uz2 = b[2] - a[2], vx2 = c[0] - a[0], vy2 = c[1] - a[1], vz2 = c[2] - a[2];
      var n = [uy2 * vz2 - uz2 * vy2, uz2 * vx2 - ux2 * vz2, ux2 * vy2 - uy2 * vx2];
      if (Math.abs(n[1]) < 1e-9) return null;
      return { n: n, d: -(n[0] * a[0] + n[1] * a[1] + n[2] * a[2]) };
    }

    /* -------------------------------------------------------------------------
       THE WALL, BLOCK BY BLOCK (thouse-v38).

       Every block is laid, simply: a box with its edges chamfered so each
       joint reads, and no slots, cores or teeth -- those are the sample's
       (Marek: modeloval bych vsechny bloky, ale zjednodusene, zkosene hrany at
       jsou videt spoje). Thousands of them go into ONE body per colour, built
       straight into its buffers, so the tablet draws the wall in one call and
       AR takes it like any other body.

       How they are laid:
         - course by course from the slab, whole courses only (thouse-v37);
         - at every corner one wall runs through to the far face and the other
           butts against it, and that swaps course by course: walls along x run
           through on even courses, walls along z on odd ones. A T has only
           such corners -- the wing meets the bar at two inner corners -- so the
           one rule covers the whole plan;
         - a butting end starts with a cut piece sized so the head joints of
           the next course fall half a block over -- that is what makes the
           bond at the corner (for a 440 clay block, a 185 starter gives 125);
         - each run is laid from both ends towards its middle, and whatever
           does not come out whole is one cut piece in the middle, never at a
           corner.
       Cut pieces are drawn a shade darker so the bond can be read; the log
       counts whole blocks and cuts, and checks the overlap against 0.4 of the
       block's height (at least 40 mm).
       ------------------------------------------------------------------------- */
    /* the chamfered piece body, the run layer and the box batch are the core's
       too (thouse-v277, core-v499): the same names, one call each */
    function _chbTemplate() { return MAS.chbTemplate(); }
    function _layRun(R, sA, sB, L, j, smin) { return MAS.layRun(R, sA, sB, L, j, smin); }
    function _chbMesh(T, pcs, col) { return MAS.chbMesh(T, pcs, col); }
    /* core-v658: THE OUTER WALL'S BLOCKS GO ON ONE MESH PER WALL (Marek, the
       exploded view: steny odjizdi do strany, prvky se nemaji prolinat). All
       the courses of every outer wall were one mesh, and one mesh cannot go
       out square to four walls: it stayed in the middle and the plaster, the
       buffer tank and the rafters went out through it. Each block goes with
       the outline's wall it is nearest; the same blocks, the same bill. */
    function _masByWall(T, g, pcs, col) {
      var P = poly(), by = {}, order = [], i, s;
      for (i = 0; i < pcs.length; i++) {
        var p = pcs[i], best = -1, bd = Infinity;
        for (s = 0; s < P.length; s++) {
          var A = P[s], Bp = P[(s + 1) % P.length], dx = Bp[0] - A[0], dz = Bp[1] - A[1], L2 = (dx * dx + dz * dz) || 1;
          var t = Math.max(0, Math.min(1, ((p[0] - A[0]) * dx + (p[2] - A[1]) * dz) / L2));
          var d = Math.hypot(p[0] - (A[0] + dx * t), p[2] - (A[1] + dz * t));
          if (d < bd) { bd = d; best = s; }
        }
        var key = (bd < 1.0) ? ('w' + best) : 'in';
        if (!by[key]) { by[key] = []; order.push(key); }
        by[key].push(p);
      }
      for (i = 0; i < order.length; i++) g.add(masChbMesh(T, by[order[i]], col));
    }
    function _boxBatch(T, pcs, col) { return MAS.boxBatch(T, pcs, col); }

    function masBuild(ctx, spec) {
      /* what the product owns, handed over once per build; the block, the
         courses, the bodies and the core's own outline, storeys, roof and
         bill are this file's */
      spec = spec || {};
      var slabTop = spec.slabTop, floorLevel = spec.floorLevel, facadeT = spec.facadeT,
          PART = spec.PART, PART_BLOCK = spec.PART_BLOCK, _partId = spec.partId,
          FLOOR = spec.FLOOR, PLATE = spec.PLATE, REBAR = spec.REBAR,
          _box = spec.box, sg = spec.sg, _mm = spec.mm, bondPeriod = spec.bondPeriod, _offs = spec.offs,
          _pockets = spec.pockets || [], _wallU0 = spec.wallU0 || {}, _lastDrop = spec.lastDrop || 0,
          /* how far under the roof plane a gable stops (core-v587): the product's
             spec.gableUnder, else its rafter's depth as before */
          _gableUnder = function () { return (typeof spec.gableUnder === 'number' && spec.gableUnder >= 0) ? spec.gableUnder : __PROD.RAFTER.d; },
          _replanNote = spec.replanNote || '',
          _stageOf = spec.stageOf,
          _sysId = masSysId, BLOCK = masBlock, SYS = masSys, _sysSaved = masSysSaved, wallT = masWallT, blockModule = masModule, blockCourse = masCourse,
          _chbMesh = masChbMesh, _boxBatch = masBoxBatch, _cutMesh = _masCutMesh, planPolyM = masPlanPoly, ptInM = masPtIn, planeEqM = masPlaneEq,
          _mod = masMod, _overPass = 0, _pocketSaid = 0, LAYER = spec.layer || 'thouse-mass';
      var T = ctx.THREE, g = ctx.group, P = poly(), i;
      /* the masonry stops a plate's height under the wall top: the plate sits
         there, and the roof plane leaves from the wall top (thouse-v28) */
      /* AND IT STARTS ON THE SLAB, not on the ground (thouse-v30) -- and where
         the slab is under grade, what of the wall is in the ground is drawn on
         the base step only (thouse-v31) */
      var th = wallT(), y0 = slabTop(), yTop = wallTop() - PLATE.h, col = SYS().col;
      /* BY WHOLE COURSES (thouse-v37): the masonry stops at the last whole
         course under the plate. What is left between it and the plate is where
         the ring beam will go (step 5); until then it is drawn as a concrete
         band, so the wall reads closed, and it is not billed. */
      /* THE RING BEAM IS ONE COURSE HIGH, ALWAYS (thouse-v122, Marek: venec je
         extremne nizky a meni vysku s vyskou steny). It was whatever was left
         over between the last whole course and the plate, 40 one day and 240
         the next. Now the beam is a course high, the masonry is the whole
         courses that fit under it, and what little is left over the beam is
         said in the log rather than drawn as a sliver. */
      var crs = blockCourse(), RING_H = crs;
      var nCrs = Math.max(0, Math.floor((yTop - RING_H - y0) / crs + 1e-6)), yMas = y0 + nCrs * crs, rest = RING_H, over = yTop - yMas - RING_H;
      var buried = (y0 < 0) ? -y0 : 0, yDraw = (buried > 0 && !_footFull()) ? 0 : y0, h = yMas - yDraw;
      /* THE WALL ENDS ON A COURSE, WHATEVER THE SLIDERS SAID (thouse-v135, W2,
         Marek: pozednice jsou zase o vysku bloku nad vencem). The floor
         build-ups between the storeys put the wall top off the course grid,
         and the plate waited a course above the beam. Whatever is left over
         is taken off the top storey once, and the wall is built again so the
         beam meets the plate. */
      if (false && over > 0.005 && _overPass < 2) {
        var S9 = storeys(), kTop = S9.length - 1;
        STATE.clearH = STATE.clearH || [];
        STATE.clearH[kTop] = Math.round(((STATE.clearH[kTop] || S9[kTop].h) - over) * 10000) / 10000;
        _overPass++;
        ctx.log('mass  ' + _mm(over) + ' was left between the ring beam and the plate -- storey ' + (kTop + 1) + ' taken down to ' + _mm(STATE.clearH[kTop]) + ' so the wall ends on a course; rebuilding once');
        try { setTimeout(function () { API.rebuild(); }, 0); } catch (eRb2) {}
      } else if (over > 0.005) {
        ctx.log('mass  ' + _mm(over) + ' left between the ring beam and the plate after two passes -- the storey slider steps do not fit the course');
      } else { _overPass = 0; }
      _bill[LAYER] = {};      /* the bucket this layer bills into, emptied each build */
      _asmMark(ctx, 'layer_masonry', 'side', 'Masonry wall', '');
      /* THE RING BEAM STOPS AT A FULL-HEIGHT OPENING (thouse-v108, Marek:
         pokud jsou pres celou vysku, nema nad nimi byt ani venec, ani preklad,
         proste nic, nechat pres nej jen pozednici). An opening that reaches the
         top of the wall has nothing to carry a beam or a lintel: the band is
         laid in the pieces between such openings on each wall, and the plate
         alone spans the gap. Everywhere else the band runs as before. */
      /* A RING BEAM AT EVERY STOREY (thouse-v112): the masonry runs through,
         and at the top of each storey below the last a band of the same
         concrete goes round, the floor's bearing. It takes one course's place,
         the courses above it start again on top of it. */
      /* ONE WALL FROM THE SLAB TO THE TOP RING BEAM (thouse-v115, Z2, Marek:
         jeden venec, uplne nahore). The masonry runs through both storeys as
         one wall; there is no band between them. Whether a second ring beam
         is needed is decided by the wall's total height, not by the storeys,
         and that is the next step. The line below says what the wall is. */
      var STs = storeys(), secondBeam = null, secondBeamLen = 0;
      (function () {
        var tot = yTop - y0, parts = [];
        for (var qs = 0; qs < STs.length; qs++) parts.push('storey ' + (qs + 1) + ' ' + _mm(STs[qs].h));
        /* A SECOND RING BEAM OVER 4500 OF MASONRY (thouse-v116, Z3, Marek:
           pocitat celkovou vysku a v urcite vysce doplnit druhy venec pro
           ztuzeni na urovni stropu). It sits at the floor of storey two, on
           the bed joint the storey's height put there, one course high, the
           same concrete round the outer walls and over the load-bearing ones;
           its cage is billed with the top ring's. The floor will bear on it. */
        /* from 4500 inclusive (thouse-v165, Marek: druhy venec az od vysky
           wall 4500) */
        /* the figure that decides is the wall slider's own, the storeys added
           up, not the masonry from the slab (thouse-v166, Marek: druhy venec
           az od vysky wall 4500) */
        var wallSl = 0; for (var qw = 0; qw < STs.length; qw++) wallSl += STs[qw].h;
        if (wallSl >= 4.5 - 0.001 && STs.length > 1) {
          var yF = STs[1].y0 - FLOOR.build, kF = Math.round((yF - y0) / crs), yBeam = y0 + kF * crs;
          secondBeam = { y: yBeam, course: kF };
          var q, r;
          for (r = 0; r < P.length; r++) {
            var aS = P[r], bS = P[(r + 1) % P.length], dxS = bS[0] - aS[0], dzS = bS[1] - aS[1], LS = Math.hypot(dxS, dzS);
            if (!(LS > 0.05)) continue;
            _box(T, g, LS + th, crs, th, (aS[0] + bS[0]) / 2, yBeam + crs / 2, (aS[1] + bS[1]) / 2, Math.atan2(dxS, dzS) + Math.PI / 2, 0x9A9A96, 1);
            secondBeamLen += LS;
          }
        }
        ctx.log('mass  wall height ' + _mm(tot) + ' in one piece from the slab to the top ring beam (' + parts.join(', ') + ')' +
                (secondBeam ? (' -- over 4500: a second ring beam at ' + sg(secondBeam.y) + ' on course ' + secondBeam.course + ', one course high, the floor bears on it')
                            : (wallSl >= 4.5 - 0.001 ? ' -- over 4500 but one storey, so no second ring beam' : ' -- under 4500, one ring beam at the top is enough')));
      })();
      /* AN OPENING IS OPEN TO THE ROOF, OR IT IS NOT (core-v531, Marek: porad
         se nad kulatym oknem nevytvareji vrstvy fasady v nekterych mistech).
         A hole that starts under the ring beam and reaches it is open to the
         roof on an EAVE wall, where the plate is the top and nothing is
         over it. On a GABLE wall the masonry goes on over the ring beam, so
         there it is open to the roof only when its head reaches the rafters'
         underside; a window straddling the ring beam line keeps its head,
         the courses over it stay and the ring beam runs on over it. */
      var _toTopOf = (function () {
        var PLt = roofPlanes() || [], PT = [], q;
        for (q = 0; q < PLt.length; q++) {
          var plt = PLt[q]; if (!plt || !plt.pts || plt.pts.length < 3 || (plt.kind !== 'slope' && plt.kind !== 'hip')) continue;
          var Et = planeEqM(plt.pts); if (!Et) continue;
          var cst = Math.cos((plt.pitch || 0) * Math.PI / 180) || 1;
          PT.push({ poly: planPolyM(plt.pts), E: Et, down: _gableUnder() / cst + (_lastDrop || 0) });
        }
        var underAt = function (x, z) {
          var best = -Infinity, k;
          for (k = 0; k < PT.length; k++) {
            if (!ptInM(PT[k].poly, x, z)) continue;
            var e = PT[k].E, y = -(e.n[0] * x + e.n[2] * z + e.d) / e.n[1] - PT[k].down;
            if (y > best) best = y;
          }
          return best;
        };
        return function (h) {
          if (!h || h.kind !== 'wall') return false;
          var foot = y0 + (h.sill || 0), head = foot + (h.h || 0);
          if (!(head >= yMas - 0.002 && foot < yMas - 0.002)) return false;
          var A = P[h.ref], B = P[(h.ref + 1) % P.length];
          if (!A || !B) return true;
          var L = Math.hypot(B[0] - A[0], B[1] - A[1]) || 1, ux = (B[0] - A[0]) / L, uz = (B[1] - A[1]) / L;
          var at = function (u) { return underAt(A[0] + ux * u, A[1] + uz * u); };
          var yA = at(L * 0.02), yB = at(L * 0.98), yM = at(L * 0.5);
          if (!isFinite(yA) || !isFinite(yB) || !isFinite(yM)) return true;
          if (Math.max(yA, yB, yM) - Math.min(yA, yB, yM) <= 0.30) return true;
          var u0 = Math.max(0, Math.min(L, h.u || 0)), u1 = Math.max(0, Math.min(L, (h.u || 0) + (h.w || 0)));
          var ua = at(u0), ub = at(u1);
          if (!isFinite(ua) || !isFinite(ub)) return true;
          return head >= Math.min(ua, ub) - 0.05;
        };
      })();
      var fullTop = [], ringSaid = [], ringRuns = [];
      (function () {
        var HS0 = (STATE.holes || []).concat(API.wallOpenings ? API.wallOpenings() : []), q0;
        for (q0 = 0; q0 < HS0.length; q0++) {
          var h0 = HS0[q0];
          if (!h0 || h0.kind !== 'wall') continue;
          if (_toTopOf(h0)) fullTop.push(h0);
        }
      })();
      for (i = 0; i < P.length; i++) {
        var a = P[i], b = P[(i + 1) % P.length];
        var dx = b[0] - a[0], dz = b[1] - a[1], L = Math.sqrt(dx * dx + dz * dz);
        if (!(L > 0.05)) continue;
        if (!(rest > 0.001)) continue;
        var ux0 = dx / L, uz0 = dz / L, segs0 = [[-th / 2, L + th / 2]], q1, q2;
        for (q1 = 0; q1 < fullTop.length; q1++) {
          if (fullTop[q1].ref !== i) continue;
          var o0 = fullTop[q1].u, o1 = fullTop[q1].u + fullTop[q1].w, nx0 = [];
          for (q2 = 0; q2 < segs0.length; q2++) {
            var s0 = segs0[q2][0], s1 = segs0[q2][1];
            if (o1 <= s0 + 1e-6 || o0 >= s1 - 1e-6) { nx0.push([s0, s1]); continue; }
            if (o0 > s0 + 1e-6) nx0.push([s0, o0]);
            if (o1 < s1 - 1e-6) nx0.push([o1, s1]);
          }
          segs0 = nx0;
        }
        for (q2 = 0; q2 < segs0.length; q2++) {
          var Ls = segs0[q2][1] - segs0[q2][0];
          if (Ls < 0.02) continue;
          var mid0 = (segs0[q2][0] + segs0[q2][1]) / 2;
          _box(T, g, Ls, rest, th, a[0] + ux0 * mid0, yMas + rest / 2, a[1] + uz0 * mid0,
               Math.atan2(dx, dz) + Math.PI / 2, 0x9A9A96, 1);
          ringSaid.push('wall ' + i + ' ' + _mm(Ls));
          ringRuns.push([a[0] + ux0 * segs0[q2][0], a[1] + uz0 * segs0[q2][0], a[0] + ux0 * segs0[q2][1], a[1] + uz0 * segs0[q2][1]]);
        }
      }
      if (fullTop.length) ctx.log('mass  ' + fullTop.length + ' opening(s) reach the wall top: no ring beam and no lintel over them, the plate alone spans them');
      /* every run the top ring beam was laid on, so a run that is missing --
         a wing wall, say -- names itself (thouse-v138, W6 diagnosis) */
      ctx.log('mass  top ring beam at ' + sg(yMas) + ' to ' + sg(yMas + rest) + ' on ' + ringSaid.length + ' run(s) of ' + P.length + ' walls: ' + ringSaid.join(', '));
      /* THE RING BEAM RUNS OVER THE LOAD-BEARING WALLS TOO (thouse-v109,
         Marek: nosna zed by mela byt spojena vencem s obvodovou). A wall that
         carries the floor is tied into the outer ring at both ends: its own
         band, in the same concrete at the same height, from outer wall to
         outer wall, with its share of bars, stirrups, spacers and wire billed
         with the ring's. The band is drawn here; its cage joins the ring's
         cage below. */
      var bearBeamLen = 0;
      /* THE BLOCKS (thouse-v38), THE WALLS INSIDE TOOTHED INTO THEM
         (thouse-v49).

         The outer walls are laid as one grid per run: whole blocks from the
         run's start, the pinwheel deciding which end runs through the corner.
         Where a load-bearing wall meets an outer wall, it ENTERS it in every
         other course: the outer wall's grid is left untouched and only the
         width of the inner wall and its two joints is taken out of it, so the
         two pieces beside the junction are cut and everything above and below
         them keeps running as before (Marek: nevadi ze se preusi obvodova
         stena, kdyz tam hned dame dorezy aby stridani pokracovalo stejne).
         In the other course the inner wall stops at the outer wall's face.
         That is a real bond for ANY block, not only one whose width is half
         its length; the cuts move from the inner wall into the outer one and
         there are about as many.

         A bearing wall that ends on a partition, on another bearing wall or
         free in a room butts there with a joint and is tied. */
      var BK = BLOCK(_sysId()), bL = BK.l, bj = BK.head, bm = bL + bj, bh = BK.h;
      var smin = Math.max(0.04, 0.2 * bL), need = Math.max(0.4 * bh, 0.04);
      var whole = [], cuts = [], bWhole = [], bCut = [], nWhole = 0, nCut = 0, kc, full = _footFull(), cutAt = [], holeCut = 0, nLintel = 0, linLen = 0, headUp = [], linInfo = {}, linOrder = [], nPocketed = 0;
      (function () { var HZ = STATE.holes || [], z; for (z = 0; z < HZ.length; z++) if (HZ[z]) { HZ[z]._lin = 0; HZ[z]._hSaid = 0; } })();
      _pocketSaid = 0;
      /* every piece that is not a whole block, so the blocks can be counted
         with the offcuts used up (thouse-v51) */
      var cutLens = [];
      var jOff = _mod(th + bj, bm), overlap = Math.min(jOff, bm - jOff);
      var chm = Math.min(0.008, 0.12 * Math.min(bh, th)), PART_T = 0.10;
      var PT = STATE.parts || [];
      /* WHICH WAY IS IN (thouse-v255, Marek: zapustene kousky zdiva v rade
         nad prekladem). The joist pockets asked the joist's end point which
         side of the wall the room was on -- but that point lies ON the wall
         line (the plan's rectangles are cut on the wall lines), so the answer
         was a coin toss and on the long wall the notch was turned outwards:
         100 of the block's face missing at every joist. The room side is
         read from the outline's own winding instead, once for every wall. */
      var oriM = 0, qo;
      for (qo = 0; qo < P.length; qo++) { var pO = P[qo], pN = P[(qo + 1) % P.length]; oriM += pO[0] * pN[1] - pN[0] * pO[1]; }
      oriM = (oriM >= 0) ? 1 : -1;

      /* what the bearing walls are, before anything is laid */
      var BSPEC = [], bearSkip = [];
      (function () {
        var qp;
        for (qp = 0; qp < PT.length; qp++) {
          var pt = PT[qp];
          if (!pt || !pt.bear) continue;
          var isV = ((pt.o || pt.dir) === 'v'), cc = (typeof pt.c === 'number') ? pt.c : pt.at;
          if (typeof cc !== 'number' || !isFinite(cc)) { bearSkip.push('#' + (pt.id || qp) + ' no position'); continue; }
          var mid = (typeof pt.a === 'number' && typeof pt.b === 'number') ? (pt.a + pt.b) / 2 : null, qe, cr = [];
          for (qe = 0; qe < P.length; qe++) {
            var e0 = P[qe], e1 = P[(qe + 1) % P.length];
            if (isV) {
              if (Math.abs(e1[1] - e0[1]) > 1e-6) continue;
              if (cc > Math.min(e0[0], e1[0]) + th / 2 && cc < Math.max(e0[0], e1[0]) - th / 2) cr.push(e0[1]);
            } else {
              if (Math.abs(e1[0] - e0[0]) > 1e-6) continue;
              if (cc > Math.min(e0[1], e1[1]) + th / 2 && cc < Math.max(e0[1], e1[1]) - th / 2) cr.push(e0[0]);
            }
          }
          cr.sort(function (u, v) { return u - v; });
          if (cr.length < 2) { bearSkip.push('#' + (pt.id || qp) + ' not between two outer walls'); continue; }
          if (mid == null) mid = (cr[0] + cr[cr.length - 1]) / 2;
          var lo = null, hi = null, qc;
          for (qc = 0; qc < cr.length; qc++) { if (cr[qc] <= mid) lo = cr[qc]; if (cr[qc] > mid && hi == null) hi = cr[qc]; }
          if (lo == null || hi == null) { lo = cr[0]; hi = cr[cr.length - 1]; }
          var a0 = (typeof pt.a === 'number') ? pt.a : lo, b0 = (typeof pt.b === 'number') ? pt.b : hi;
          var ea = Math.max(lo, Math.min(a0, b0)), eb = Math.min(hi, Math.max(a0, b0));
          var endInfo = function (pos, atLo) {
            if (Math.abs(pos - (atLo ? lo : hi)) < 0.005) return { g: th / 2 + bj, what: 'outer wall', tooth: true, tie: false };
            var qq;
            for (qq = 0; qq < PT.length; qq++) {
              var o2 = PT[qq];
              if (!o2 || o2 === pt || o2.sep || (o2.o || o2.dir) === (pt.o || pt.dir)) continue;
              var c2 = (typeof o2.c === 'number') ? o2.c : o2.at;
              if (Math.abs(c2 - pos) > 0.005) continue;
              if (typeof o2.a === 'number' && typeof o2.b === 'number' && (cc < Math.min(o2.a, o2.b) - 0.005 || cc > Math.max(o2.a, o2.b) + 0.005)) continue;
              return o2.bear ? { g: th / 2 + bj, what: 'load-bearing wall', tooth: false, tie: true }
                             : { g: PART_T / 2 + bj, what: 'partition', tooth: false, tie: false };
            }
            return { g: 0, what: 'free end', tooth: false, tie: false };
          };
          var eA = endInfo(ea, true), eB = endInfo(eb, false);
          if (!(eb - ea - eA.g - eB.g > smin)) { bearSkip.push('#' + (pt.id || qp) + ' too short'); continue; }
          /* THE STOREYS A WALL INSIDE STANDS ON (thouse-v113): from the floor
             of the storey it was drawn on, up through every storey above it
             unless it was cut away there. Its courses are laid inside that
             range and nowhere else. */
          var STw = storeys(), stW = pt.st | 0, yLoW = (STw[stW] ? STw[stW].y0 : y0) - FLOOR.build, yHiW = yTop, qsW;
          for (qsW = stW; qsW < STw.length; qsW++) {
            var gone = (pt.cutOn && pt.cutOn.indexOf(qsW) >= 0);
            if (gone) { yHiW = STw[qsW].y0 - FLOOR.build; break; }
          }
          BSPEC.push({ id: (pt.id || qp), isV: isV, cc: cc, lo: lo, hi: hi, ea: ea, eb: eb, A: eA, B: eB, whole: 0, cut: 0, ties: 0, yLo: yLoW, yHi: yHiW, st: stW });
        }
      })();

      /* the grid of a run, with the junction bands taken out of it */
      function layGrid(R, bands, off) {
        /* A RUNNING BOND OF HALF THIS BLOCK (thouse-v66, Marek: musi se
           vychazet z definice rozmeru bloku ve structure step). The corner used
           to set where each course started, and with a 350 wall on a 250 block
           that moved the head joints 100 at a time -- a pattern that only
           repeats after five courses, so no opening could end on a joint in
           every other course. The course now starts half a module over from the
           one below, whatever the block is, and the piece at the corner takes
           up the difference. */
        var out = [], u = 0, q, segs, k;
        if (off) { u = -(off % bm); if (u > -0.002) u -= bm; }
        /* the corner keeps setting where a course starts -- that is what makes
           the two joint lattices the opening ends on (thouse-v67) */
        while (u < R - 0.002) {
          var e = Math.min(u + bL, R);
          /* a lattice pushed over starts before the run; the first piece is
             cut at the corner, not laid outside it (thouse-v175) */
          segs = [[Math.max(u, 0), e]];
          for (q = 0; q < bands.length; q++) {
            var B0 = bands[q][0], B1 = bands[q][1], nx = [];
            for (k = 0; k < segs.length; k++) {
              var s0 = segs[k][0], s1 = segs[k][1];
              if (B1 <= s0 + 1e-9 || B0 >= s1 - 1e-9) { nx.push([s0, s1]); continue; }
              if (B0 > s0 + 1e-9) nx.push([s0, B0]);
              if (B1 < s1 - 1e-9) nx.push([B1, s1]);
            }
            segs = nx;
          }
          for (k = 0; k < segs.length; k++) {
            var L2 = segs[k][1] - segs[k][0];
            if (L2 > 0.02) out.push([segs[k][0], L2, (Math.abs(L2 - bL) < 0.002) ? 'whole' : 'cut']);
          }
          u += bm;
        }
        return out;
      }
      /* A PIECE CUT ON ITS HEIGHT, LAID AS A BLOCK (thouse-v254, Marek: nad
         prekladem jakysi monoblok, mely by tam byt rezane masonry blocks dle
         volby blocks, a spravne uctovat). The make-up over a lintel, the
         course under a sill and the blocks trimmed under a lintel's bearing
         were drawn as plain boxes and counted apart from the blocks. They are
         pieces of the chosen block like any other cut now: drawn by the same
         block mesh, and their lengths go into the offcut count the blocks are
         billed from. u is along the wall from its start point a2. */
      function cutPiece(a2, alongX, dir, uMid, len, yMid, hgt, tag, ext) {
        var ch2 = Math.min(chm, 0.12 * Math.max(0.01, hgt)), lD = len + (ext || 0);
        var pc2 = alongX ? [a2[0] + dir * uMid, yMid, a2[1], lD / 2, hgt / 2, th / 2, ch2, null, len]
                         : [a2[0], yMid, a2[1] + dir * uMid, th / 2, hgt / 2, lD / 2, ch2, null, len];
        push(pc2, 'cut', tag, len);
      }
      /* THE OFFCUT IS MEASURED ALONG THE WALL (thouse-v254): the length of a
         cut piece was taken as the larger of its two plan sizes, which on a
         400 wall of 250 blocks was the wall's thickness -- longer than the
         block, so every cut opened a block it could not have come from and the
         offcut pool ran negative. The length is handed in with the piece. */
      function push(piece, kind, tag, len, parts) {
        var list = (kind === 'whole') ? whole : cuts, q;
        if (parts) { for (q = 0; q < parts.length; q++) list.push(parts[q]); } else list.push(piece);
        if (kind === 'whole') { nWhole++; return; }
        nCut++;
        cutLens.push((len != null && len > 0) ? len : Math.min(piece[3], piece[5]) * 2);
        if (tag && cutAt.length < 6) cutAt.push(tag);
      }

      /* THE GABLES ARE THE SAME MASONRY, CARRIED ON (thouse-v147, Marek: vzit
         to stejne co je pod vencem, dat do stitu a orezat podle vrchni plochy
         krokve). A wall is a gable wall when the roof over it climbs along it.
         Those walls keep laying their courses above the top ring beam, the
         same grid, the same bond, into a body of their own -- and that body is
         clipped by the roof planes over it, so every block the rafters cross
         is cut exactly on the rafters' plane. */
      var PLg = roofPlanes() || [], gableWall = [], nExtra = 0, gWallP = [], gPlanes = [];
      (function () {
        var roofAt2 = function (x, z) {
          var best = -Infinity, q, pl;
          for (q = 0; q < PLg.length; q++) {
            pl = PLg[q]; if (!pl || !pl.pts || pl.pts.length < 3) continue;
            if (!ptInM(planPolyM(pl.pts), x, z)) continue;
            var E2 = planeEqM(pl.pts); if (!E2) continue;
            var y = -(E2.n[0] * x + E2.n[2] * z + E2.d) / E2.n[1];
            if (y > best) best = y;
          }
          return best;
        };
        var q, top9 = -Infinity;
        for (q = 0; q < P.length; q++) {
          var A9 = P[q], B9 = P[(q + 1) % P.length];
          var yA = roofAt2(A9[0] * 0.98 + B9[0] * 0.02, A9[1] * 0.98 + B9[1] * 0.02), yB = roofAt2(A9[0] * 0.02 + B9[0] * 0.98, A9[1] * 0.02 + B9[1] * 0.98);
          var yM = roofAt2((A9[0] + B9[0]) / 2, (A9[1] + B9[1]) / 2);
          /* THE MIDDLE OF A GABLE IS UNDER ITS RIDGE (core-v816, T house thread):
             on a symmetric gable the wall's midpoint lies on the line where the
             two slopes meet, and whether either plane's outline held it was a
             matter of rounding -- the T house's wing gable lost its courses
             over the ring beam on one build and had them on the next. A middle
             no plane holds is read a little to either side. */
          if (!isFinite(yM)) yM = Math.max(roofAt2(A9[0] * 0.53 + B9[0] * 0.47, A9[1] * 0.53 + B9[1] * 0.47), roofAt2(A9[0] * 0.47 + B9[0] * 0.53, A9[1] * 0.47 + B9[1] * 0.53));
          gableWall[q] = (isFinite(yA) && isFinite(yB) && isFinite(yM) && (Math.max(yA, yB, yM) - Math.min(yA, yB, yM) > 0.30));
          if (gableWall[q]) top9 = Math.max(top9, yA, yB, yM);
        }
        if (isFinite(top9)) nExtra = Math.max(0, Math.ceil((top9 - (yMas + RING_H)) / crs));
        /* A TRUSSED PRODUCT KEEPS ITS GABLES IN TIMBER (core-v577, RANCH:
           stejne vazniky na vsech systemech): spec.gables === false lays no
           course above the ring beam -- the gable end truss stands on the
           plate there. Every other product passes nothing and is unchanged. */
        if (spec.gables === false) nExtra = 0;
        /* WHICH WALLS ARE GABLES IS THE PRODUCT'S TO SAY TOO (core-v587, RANCH:
           u cihly neni stit masonry -- zdeny stit): spec.gableWall(i) may turn
           a wall the roof climbs along back into a plain wall (a porch wall
           under the roof); the courses are counted for the walls kept. */
        if (typeof spec.gableWall === 'function' && !(spec.gables === false)) {
          var top10 = -Infinity, q10g;
          for (q10g = 0; q10g < P.length; q10g++) {
            if (gableWall[q10g] && !spec.gableWall(q10g)) gableWall[q10g] = false;
            if (gableWall[q10g]) {
              var A10 = P[q10g], B10 = P[(q10g + 1) % P.length];
              top10 = Math.max(top10, roofAt2(A10[0] * 0.98 + B10[0] * 0.02, A10[1] * 0.98 + B10[1] * 0.02), roofAt2(A10[0] * 0.02 + B10[0] * 0.98, A10[1] * 0.02 + B10[1] * 0.98), roofAt2((A10[0] + B10[0]) / 2, (A10[1] + B10[1]) / 2));
            }
          }
          nExtra = isFinite(top10) ? Math.max(0, Math.ceil((top10 - (yMas + RING_H)) / crs)) : 0;
        }
        /* the planes that clip the gables: every slope, its normal turned to
           point down so the side under the roof is the side that is kept */
        for (q = 0; q < PLg.length; q++) {
          var pl9 = PLg[q]; if (!pl9 || !pl9.pts || pl9.pts.length < 3 || (pl9.kind !== 'slope' && pl9.kind !== 'hip')) continue;
          var E9 = planeEqM(pl9.pts); if (!E9) continue;
          var nx = E9.n[0], ny = E9.n[1], nz = E9.n[2], dd = E9.d, nl = Math.hypot(nx, ny, nz) || 1;
          if (ny > 0) { nx = -nx; ny = -ny; nz = -nz; dd = -dd; }
          /* THE CUT IS THE RAFTERS' UNDERSIDE, NOT THE ROOF SURFACE (thouse-v172,
             Marek: objekt presahuje pres vrchni plochy krokvi). The guider's
             plane is the top of the rafters; the masonry stops under them, a
             rafter's depth along the slope plus the drop the rafters took onto
             the plate. The plane is let down by that much. */
          var csP = Math.cos((pl9.pitch || 0) * Math.PI / 180) || 1, downP = _gableUnder() / csP + (_lastDrop || 0);
          var nyN = ny / nl, cN = dd / nl + nyN * downP;
          gPlanes.push({ plane: new T.Plane(new T.Vector3(nx / nl, nyN, nz / nl), cN), poly: planPolyM(pl9.pts) });
        }
      })();
      var lastK = nCrs + (nExtra > 0 ? 1 + nExtra : 0);
      for (kc = 0; kc < lastK; kc++) {
        var yb = y0 + kc * crs, yt = yb + bh;
        var gableCourse = (kc > nCrs);
        if (kc === nCrs) continue;
        if (secondBeam && kc === secondBeam.course) continue;
        /* THE POCKETS FOR THE JOISTS (thouse-v120): in the course the floor
           bears in, wherever a joist end meets an outer wall, the block gives
           way 100 deep from the inside and stays whole on the outside -- a
           piece cut on its depth, counted with the cuts. Only when there is a
           storey above and the wall is under 4500, so no ring beam carries
           the floor. */
        var pocketCourse = (!secondBeam && storeys().length > 1 && kc === Math.round(((storeys()[1].y0 - FLOOR.build) - y0) / crs));
        if (!full && yt <= 0) continue;
        if (!full && yb < 0) yb = 0;
        /* THE JOINTS ARE FILLED (thouse-v258, Marek: v rezu tenke bile profily
           pres celou zed). A block was drawn its own height and the bed joint
           over it was left as air -- a slit right through the wall, which the
           section tool cannot cap, so every course showed a white line across
           the whole wall in the cut (and a CMU wall, with its 10 mm joints,
           had daylight through it). The body is drawn a course high and, where
           the block has a head joint, a module long: the mortar is part of the
           block's body and the chamfer still reads as the joint. The counts and
           the offcuts keep the block's own size. */
        var bedJ = crs - bh, cy = (yb + yt + bedJ) / 2, hy = (yt + bedJ - yb) / 2, toothC = (kc % 2 === 1);
        /* the outer walls */
        for (i = 0; i < P.length; i++) {
          if (gableCourse && !gableWall[i]) continue;
          var a2 = P[i], b2 = P[(i + 1) % P.length];
          var ddx = b2[0] - a2[0], ddz = b2[1] - a2[1], alongX = Math.abs(ddx) >= Math.abs(ddz);
          var len = alongX ? Math.abs(ddx) : Math.abs(ddz), dir = alongX ? (ddx >= 0 ? 1 : -1) : (ddz >= 0 ? 1 : -1);
          if (!(len > 0.05)) continue;
          var tS = (kc % 2 === 0), tE = (kc % 2 === 1);
          /* THE GABLE OWNS BOTH ITS CORNERS (thouse-v174, Marek: na prave
             strane nejsou bloky az do spicky, na leve ano). Below the beam a
             corner belongs to one wall or the other course by course, the
             pinwheel; above the beam the eave wall has no courses, so the
             corner it would have laid was empty every other course. Up here
             the gable wall runs corner to corner in every course. */
          if (gableCourse) { tS = true; tE = true; }
          var u0 = tS ? -th / 2 : (th / 2 + bj), u1 = tE ? (len + th / 2) : (len - th / 2 - bj), R = u1 - u0;
          /* WHERE THIS WALL'S FIRST HEAD JOINT SITS (thouse-v62): the run does
             not start in the corner, it starts half a wall in or out, so an
             opening measured from the corner never met a joint and both jambs
             were cut in every course. The core asks for this offset and puts
             the jambs on the joints of it. */
          /* RECORDED FROM THE FIRST COURSE ONLY (thouse-v170, Marek: otvory se
             prepocitavaji pri wall height, odskoci a vrati se). Every course
             overwrote this, so the wall reported the start of whichever course
             was laid last -- and that flips between the two starts with every
             row the slider adds. The core then put the jambs on the other
             lattice, rebuilt, and the wall reported the first lattice again.
             The first course is always the same course. */
          if (kc === 0) _wallU0[i] = u0;
          var bands = [], q2;
          if (toothC) {
            for (q2 = 0; q2 < BSPEC.length; q2++) {
              var sp = BSPEC[q2];
              /* NO POCKET WHERE THE WALL IS NOT (thouse-v128, X4, Marek: po
                 oriznuti nosne zdi zustavaji po ni diry po zazubeni): the
                 outer wall opens for a tooth only in the courses the inner
                 wall actually stands in. Above a storey it was cut away on, or
                 below the storey it starts on, the outer run stays whole. */
              if (yt <= sp.yLo + 0.001 || yb >= sp.yHi - 0.001) continue;
              /* an inner wall enters this run where it ends on this very edge */
              var cross = alongX ? (sp.isV ? sp.cc : null) : (sp.isV ? null : sp.cc);
              if (cross == null) continue;
              var edgePos = alongX ? a2[1] : a2[0];
              var hits = ((sp.A.tooth && Math.abs(sp.lo - edgePos) < 0.005) || (sp.B.tooth && Math.abs(sp.hi - edgePos) < 0.005));
              if (!hits) continue;
              var uW = dir * (cross - (alongX ? a2[0] : a2[1])) - u0;
              if (uW < -th || uW > R + th) continue;
              bands.push([uW - th / 2 - bj, uW + th / 2 + bj]);
            }
          }
          /* AN OPENING TAKES ITS COURSES OUT OF THE WALL (thouse-v56): the
             grid is left alone and only the opening is subtracted, so the
             blocks beside it are cut at the jamb and everything above and
             below keeps running. The head joints fall on the jamb wherever
             the opening sits on the module, so only every other course is cut
             there. The lintel over it comes next. */
          /* a window and a door are cut out of the masonry the same way a
             plain hole is, each with its lintel over it (thouse-v61) */
          var HSW = (STATE.holes || []).concat(API.wallOpenings ? API.wallOpenings() : []), qh, nHoleC = 0;
          for (qh = 0; qh < HSW.length; qh++) {
            var hw = HSW[qh];
            if (!hw || hw.kind !== 'wall' || hw.ref !== i) continue;
            /* A WINDOW'S HEAD GOES ON A COURSE (thouse-v72, V3): the lintel
               bears on the masonry, so its underside has to be a bed joint and
               its top the one above. The head is therefore taken up to the
               next bed joint before anything is cut, and what that adds sits
               between the frame and the lintel, where the reveal closes it. */
            /* THE UNIT MEETS THE LINTEL WITH A JOINT, NOT A HOLE (thouse-v79,
               S2): the head was rounded up to the next bed joint, which left
               daylight over the frame; and the sill was left wherever the unit
               asked, which left daylight under it. Both now close to a 10 mm
               joint: the head goes up to the bed joint the lintel bears on and
               the frame meets its underside, while the course below the sill is
               made up in blocks cut on their height. */
            /* on the floor means on the finished floor, a build-up above the
               wall's foot (thouse-v96) */
            var onFloor0 = ((hw.sill || 0) < FLOOR.build + 0.02);
            var JNT = 0.010;
            /* the head is already on a bed joint -- the core puts it there on
               the record itself now, so the wall and the unit agree and no gap
               is left over the frame (thouse-v92) */
            var hRaw = (hw.h || 0);
            var hy0 = y0 + (hw.sill || 0), hy1 = hy0 + hRaw, hy2 = hy1 + crs;
            /* AN OPENING TO THE WALL TOP GOES THROUGH THE GABLE TOO (thouse-v149,
               Q4, Marek: pokud je pod stitem otvor az pres celou stenu, ma byt
               vyrizly i stit): its band runs on up through every gable course
               over it, with no lintel, so the wall is open to the roof. */
            /* AN OPENING WHOLLY IN THE GABLE IS NOT ONE TO THE WALL TOP
               (core-v522, Marek: nad oknem se ve stite tvori dira ve vrstvach
               fasady). A hole whose head was at or over the ring beam was
               read as open to the roof and cut on up through every gable
               course -- true for a full-height light under the gable, false
               for a window sitting IN the gable, which can be there since
               thouse-v295. To the top means it starts under the top. */
            var toTop0 = _toTopOf(hw);
            if (toTop0) hy2 = 1e9;
            /* A ROUND LIGHT HAS NO LINTEL (core-v521, Marek: kulate okno
               nepotrebuje preklad pri vsazeni do cihly): its band stops at
               its head, no course over it gives way, and the blocks over it
               are cut on the circle and bear on it. */
            var roundH = false;
            try { var itRd = _openingItem(hw.code); roundH = !!(itRd && itRd.round); } catch (eRd) { roundH = false; }
            if (roundH && !toTop0) hy2 = hy1;
            if (yt <= hy0 + 0.001 || yb >= hy2 - 0.001) continue;
            if (toTop0 && gableCourse) { bands.push([hw.u - u0, hw.u + hw.w - u0]); continue; }
            /* the course over the opening belongs to the lintel, and it
               reaches 125 into the wall on each side (thouse-v57) */
            /* A DOOR CARRIES ITS LINTEL AT ITS OWN HEIGHT (thouse-v70, Marek:
               pokud je vlozeny prvek ktery zacina na podlaze, musi se preklad
               uzpusobit vysce dveri). A door and a portal start on the floor,
               so their head is set by the leaf, not by a course: the lintel sits
               straight on top of them and the masonry above it makes up the
               difference. A window keeps its lintel in the course, because its
               head was put on a course in the first place. */
            var onFloor = ((hw.sill || 0) < FLOOR.build + 0.02);
            /* A HEAD OFF THE BED JOINT IS A DOOR'S HEAD, WHATEVER STANDS IN IT
               (thouse-v253, Marek: preklady dveri, HS portalu). Only a door was
               given its lintel at its own head; a portal or a unit whose sill is
               a little off the floor fell to the window rule, which puts the
               lintel on the NEXT bed joint -- leaving air under it and the
               bearings trimmed to a height nothing stood on. Wherever the head
               is not on a bed joint the lintel sits on the head. */
            var kHead = (hy1 - y0) / crs, offJoint = Math.abs(kHead - Math.round(kHead)) * crs > 0.002;
            var headFree = onFloor || offJoint;
            var LK = (hw.id != null ? hw.id : qh) + '/' + i;
            if (!linInfo[LK]) { linInfo[LK] = { id: (hw.id != null ? hw.id : qh), wall: i, head: hy1, off: (kHead - Math.floor(kHead + 1e-6)) * crs, u0: hw.u, w: hw.w, door: onFloor, round: roundH, trim: 0, trimH: 0, mk: 0, mkH: 0, lin: null }; linOrder.push(LK); }
            var toTop = toTop0;
            if (toTop) { hw._lin = 1; }
            /* A ROUND LIGHT IS CUT WHERE IT WAS DROPPED (core-v522, Marek:
               proste volne tazeni a vyrezavani vrstev fasady az po drop,
               zadny preklad). Its square takes the courses it crosses over
               its own width only -- no bearing, no lintel -- and the course
               its head falls in is made up from the head to the course top,
               the course its sill falls in from the course bottom to the
               sill, in pieces of the same block cut on their height, so the
               wall closes on the square on all four sides wherever it sits. */
            if (roundH && !toTop0) {
              bands.push([hw.u - u0, hw.u + hw.w - u0]);
              nHoleC++;
              var mkR = function (yLo, yHi, tag) {
                var gR = yHi - yLo; if (!(gR > 0.012)) return 0;
                var phR = _mod(u0 + ((kc % 2) ? _mod(th + bj, bm) : 0), bm), uR = hw.u, uE = hw.u + hw.w, nR = 0, gd = 0;
                while (uR < uE - 0.002 && gd++ < 400) {
                  var nxR = Math.min(uE, Math.ceil((uR - phR) / bm + 1e-6) * bm + phR);
                  if (nxR <= uR + 0.002) nxR = Math.min(uE, uR + bm);
                  var wR = nxR - uR;
                  if (wR > 0.02) { cutPiece(a2, alongX, dir, uR + wR / 2, wR - bj, (yLo + yHi) / 2, gR, tag, bj); nR++; }
                  uR = nxR;
                }
                return nR;
              };
              if (yb < hy1 - 0.001 && yt > hy1 + 0.001) { var nHR = mkR(hy1, yt + bedJ, 'over a round light on wall ' + i); linInfo[LK].mk += nHR; linInfo[LK].mkH = yt + bedJ - hy1; }
              if (yb < hy0 - 0.001 && yt > hy0 + 0.001) { var nSR = mkR(yb, hy0, 'under a round light on wall ' + i); linInfo[LK].trim += nSR; linInfo[LK].trimH = hy0 - yb; }
              continue;
            }
            /* THE LINTEL SITS IN A POCKET (thouse-v93): its bearing runs 125
               into the wall on each side, so the course it lies in gives way
               over the opening AND over that bearing -- otherwise its ends run
               straight through the blocks, which is the collision. */
            /* A COURSE CAN MEET BOTH (thouse-v94). The lintel band runs from
               the head up one course, and the course that CONTAINS the head
               was being treated as if it were still below it: it gave way over
               the opening only, and its blocks stayed in the very place the
               lintel and its bearing occupy. That is the collision, and it is
               why nothing changed when the make-up was moved. A course now
               gives way over the bearing whenever it touches the lintel band at
               all, and over the opening whenever it touches the opening. */
            var over = (yb >= hy1 - 0.001);
            /* THE COURSE A DOOR'S HEAD FALLS IN (thouse-v95): its blocks beside
               the opening are not removed, they are cut down to the lintel's
               underside over the bearing, so the lintel lands on masonry and
               nothing stands where it lies. Windows never come here, their
               head is a bed joint. */
            var inHead = (!over) && (yb < hy1 - 0.001) && (yt > hy1 + 0.001);
            if (inHead) {
              var trimH = hy1 - yb, e9;
              for (e9 = 0; e9 < 2; e9++) {
                var b0e = e9 ? (hw.u + hw.w) : (hw.u - 0.125), b1e = e9 ? (hw.u + hw.w + 0.125) : hw.u;
                bands.push([b0e - u0, b1e - u0]);
                var mide = (b0e + b1e) / 2;
                cutPiece(a2, alongX, dir, mide, b1e - b0e - bj, yb + trimH / 2, trimH, 'under a lintel bearing on wall ' + i, bj);
                linInfo[LK].trim++; linInfo[LK].trimH = trimH;
              }
            }
            var bu0 = over ? (hw.u - 0.125) : hw.u, bu1 = over ? (hw.u + hw.w + 0.125) : (hw.u + hw.w);
            if (!onFloor0 && !over && !hw._hSaid) { hw._hSaid = 1; if (Math.abs(hRaw - (hw.h || 0)) > 0.002) headUp.push('#' + hw.id + ' on wall ' + i + ' ' + _mm(hRaw - (hw.h || 0))); }
            bands.push([bu0 - u0, bu1 - u0]);
            if (!over) nHoleC++;
            if (over && !hw._lin) {
              hw._lin = 1;
              var lc = (a2[0] + dir * (u0 + (bu0 - u0 + bu1 - u0) / 2)), lz = a2[1];
              var ly = headFree ? (hy1 + crs / 2) : (yb + crs / 2);
              linInfo[LK].lin = [ly - crs / 2, ly + crs / 2, bu0, bu1];
              if (alongX) _box(T, g, bu1 - bu0, crs, th, lc, ly, lz, 0, 0x9A9A96, 1);
              else _box(T, g, th, crs, bu1 - bu0, a2[0], ly, a2[1] + dir * ((bu0 + bu1) / 2), 0, 0x9A9A96, 1);
              /* AND THE GAP OVER IT IS BRICKED UP (thouse-v71, V2): a door's
                 head is set by its leaf, so its lintel ends between two
                 courses and left a slot of air under the next one. A cut
                 course of the same blocks fills it, over the opening only, and
                 those pieces go into the offcuts like any other cut. */
              /* THE SILL COURSE (thouse-v73, V4): a window's sill is set by
                 the unit, not by a bed joint, so the course under it was cut
                 short and left a slot of air right where the sill board has to
                 bear. The remainder is laid in pieces of the same block, under
                 the opening only, and they go into the offcuts like any other
                 cut. */
              /* THE SILL COURSE IS BLOCKS TOO (thouse-v87, T3): laid like the
                 make-up over a door, in pieces of the same block cut on their
                 height and bonded with the course they stand in, so the wall
                 under the window reads as masonry and the sill board has
                 something to bear on all the way across. */
              if (!onFloor) {
                var sBot = Math.max(yb, Math.floor((hy0 - y0) / crs) * crs + y0);
                var gapS = hy0 - sBot;
                if (gapS > 0.012 && sBot >= yb - 0.001 && sBot < yt) {
                  var sYc = sBot + gapS / 2, phS = _mod(u0 + ((kc % 2) ? _mod(th + bj, bm) : 0), bm);
                  var us = hw.u, uEnd = hw.u + hw.w, nSk = 0, gS = 0;
                  while (us < uEnd - 0.002 && gS++ < 400) {
                    var nxS = Math.min(uEnd, Math.ceil((us - phS) / bm + 1e-6) * bm + phS);
                    if (nxS <= us + 0.002) nxS = Math.min(uEnd, us + bm);
                    var wS2 = nxS - us;
                    if (wS2 > 0.02) {
                      var midS = us + wS2 / 2;
                      cutPiece(a2, alongX, dir, midS, wS2 - bj, sYc, gapS, 'under a window sill on wall ' + i, bj);
                      nSk++;
                    }
                    us = nxS;
                  }
                  cutAt.push('under a window sill on wall ' + i + ': ' + nSk + ' piece(s) ' + _mm(gapS) + ' deep, cut on their height and bonded with the course');
                }
              }
              /* THE MAKE-UP OVER A DOOR IS BLOCKS, NOT A SLAB (thouse-v86,
                 T2): it was drawn as one lump of clay the width of the opening,
                 which read as a second lintel. It is laid instead as pieces of
                 the same block, cut on their height, bonded like the course
                 they stand in -- and only over the opening itself, so the
                 lintel's ends no longer run into the blocks beside them. */
              var gapT = headFree ? (yt + bedJ - (hy1 + crs)) : 0;
              if (gapT > 0.012) {
                /* AND THE MAKE-UP OVER IT BELONGS TO THE COURSE IT STANDS IN:
                   it was set out on the phase of the lintel's course, but it
                   sits in the one above, half a bond over, so it met the blocks
                   beside it edge to edge instead of on a joint. */
                /* OVER THE WHOLE LINTEL, BEARINGS INCLUDED (thouse-v253, Marek:
                   nad konci prekladu byla dira). The course the lintel reaches
                   into gives way over the opening and both bearings, so the
                   make-up has to close all of it -- it was laid over the
                   opening only and left a slot of air over each end. */
                var gy = (hy1 + crs) + gapT / 2, mkU0 = bu0, mkU1 = bu1;
                var phase = _mod(u0 + ((kc % 2) ? _mod(th + bj, bm) : 0), bm);
                var uu2 = mkU0, nMk = 0, guard = 0;
                while (uu2 < mkU1 - 0.002 && guard++ < 400) {
                  var nxt = Math.min(mkU1, Math.ceil((uu2 - phase) / bm + 1e-6) * bm + phase);
                  if (nxt <= uu2 + 0.002) nxt = Math.min(mkU1, uu2 + bm);
                  var wSeg = nxt - uu2;
                  if (wSeg > 0.02) {
                    var mid3 = uu2 + wSeg / 2;
                    cutPiece(a2, alongX, dir, mid3, wSeg - bj, gy, gapT, 'made up over a lintel on wall ' + i, bj);
                    nMk++;
                  }
                  uu2 = nxt;
                }
                linInfo[LK].mk += nMk; linInfo[LK].mkH = gapT;
                cutAt.push('made up over a door on wall ' + i + ': ' + nMk + ' piece(s) ' + _mm(gapT) + ' deep, cut on their height and bonded with the course');
              }
              nLintel++;
              linLen += (bu1 - bu0);
            }
          }
          if (nHoleC) holeCut += nHoleC;
          /* THE POCKETS ARE NOTCHES IN THE BLOCKS, NOT PIECES BESIDE THEM
             (thouse-v254, Marek: tvarnice se skladaji divne, jakoby polovina
             z nich byla zapustena). The bearing course used to take a band out
             of its grid at every joist and stand a flat box in it, so from
             outside the course read as a row of alternating pieces. Now the
             grid is laid as in any course, and a block a joist lands in is
             drawn in parts -- the outer part 100 shallower behind the joist,
             the rest full depth -- that meet without a joint, so from outside
             it is one block and from inside the pocket is there. The block is
             bought once, like any block; the pockets are said in the log. */
          var PKR = [];
          if (pocketCourse && _pockets.length) {
            var qpk;
            for (qpk = 0; qpk < _pockets.length; qpk++) {
              var pk = _pockets[qpk];
              var du = alongX ? (pk[0] - a2[0]) * dir : (pk[1] - a2[1]) * dir;
              var dv = alongX ? Math.abs(pk[1] - a2[1]) : Math.abs(pk[0] - a2[0]);
              if (dv > th * 0.6 || du < -0.01 || du > len + 0.01) continue;
              PKR.push({ u0: du - 0.03, u1: du + 0.075, side: alongX ? (oriM * dir) : (-oriM * dir) });
            }
            if (PKR.length && !_pocketSaid) { _pocketSaid = 1; cutAt.push('joist pockets 100 deep in the bearing course: ' + _pockets.length + ' block(s) notched on their depth, bought whole'); }
          }
          /* a grid piece [s, e] (wall coordinates) with the pockets in it, as
             the parts it is drawn in */
          var notched = function (piece, s, e, pks) {
            var parts = [], iv = [], q7, lo = s, pd = th - 0.10, offOut = 0.05, loI = alongX ? (dir > 0 ? 0 : 1) : (dir > 0 ? 4 : 5), hiI = alongX ? (dir > 0 ? 1 : 0) : (dir > 0 ? 5 : 4);
            for (q7 = 0; q7 < pks.length; q7++) iv.push([Math.max(s, pks[q7].u0), Math.min(e, pks[q7].u1), pks[q7].side]);
            iv.sort(function (p1, p2) { return p1[0] - p2[0]; });
            var mk = function (ua, ub, side, chLo, chHi) {
              if (ub - ua < 0.003) return;
              var um = (ua + ub) / 2, hl = (ub - ua) / 2, C6 = [chm, chm, chm, chm, chm, chm], pc9;
              C6[loI] = chLo; C6[hiI] = chHi;
              if (side) {
                if (alongX) pc9 = [a2[0] + dir * um, cy, a2[1] - side * offOut, hl, hy, pd / 2, chm, C6];
                else pc9 = [a2[0] - side * offOut, cy, a2[1] + dir * um, pd / 2, hy, hl, chm, C6];
              } else {
                if (alongX) pc9 = [a2[0] + dir * um, cy, a2[1], hl, hy, th / 2, chm, C6];
                else pc9 = [a2[0], cy, a2[1] + dir * um, th / 2, hy, hl, chm, C6];
              }
              parts.push(pc9);
            };
            for (q7 = 0; q7 < iv.length; q7++) {
              var n0 = Math.max(lo, iv[q7][0]), n1 = Math.max(n0, iv[q7][1]);
              if (n1 - n0 < 0.003) continue;
              mk(lo, n0, 0, (lo === s) ? chm : 0, 0);
              mk(n0, n1, iv[q7][2], (n0 === s) ? chm : 0, (n1 === e) ? chm : 0);
              lo = n1;
            }
            mk(lo, e, 0, (lo === s) ? chm : 0, chm);
            return parts;
          };
          /* THE GABLE KEEPS THE BOND (thouse-v175, Marek: i tam musi byt cikcak
             vazba). The run goes corner to corner in every course, but on the
             odd courses the joint lattice is pushed over by the wall's own
             start, th + joint, the same shift the corner gives below the beam:
             the first piece is a cut of that length and the whole blocks sit
             half a module over from the course below. */
          var run = layGrid(R, bands, (gableCourse && (kc % 2 === 1)) ? (th + bj) : 0), r;
          for (r = 0; r < run.length; r++) {
            var pc = run[r], piece;
            var extR = (bj > 0.0005 && run[r + 1] && Math.abs(run[r + 1][0] - (pc[0] + pc[1]) - bj) < 0.0015) ? bj : 0;
            var mid2 = u0 + pc[0] + (pc[1] + extR) / 2, hlR = (pc[1] + extR) / 2;
            if (alongX) piece = [a2[0] + dir * mid2, cy, a2[1], hlR, hy, th / 2, chm, null, pc[1]];
            else piece = [a2[0], cy, a2[1] + dir * mid2, th / 2, hy, hlR, chm, null, pc[1]];
            if (gableCourse) { (gWallP[i] = gWallP[i] || { w: [], c: [] }); (pc[2] === 'whole' ? gWallP[i].w : gWallP[i].c).push(piece); }
            else {
              var partsP = null;
              if (PKR.length) {
                var sP = u0 + pc[0], eP = sP + pc[1] + extR, inP = [], q8;
                for (q8 = 0; q8 < PKR.length; q8++) if (PKR[q8].u1 > sP + 0.003 && PKR[q8].u0 < eP - 0.003) inP.push(PKR[q8]);
                if (inP.length) { partsP = notched(piece, sP, eP, inP); nPocketed++; }
              }
              push(piece, pc[2], 'outer wall ' + (i + 1) + ' course ' + (kc + 1) + ' ' + _mm(pc[1]) + ' beside a junction', pc[1], partsP);
            }
          }
        }
        /* the walls inside */
        for (i = 0; i < BSPEC.length; i++) {
          var sp2 = BSPEC[i];
          if (gableCourse) break;
          if (yt <= sp2.yLo + 0.001 || yb >= sp2.yHi - 0.001) continue;
          var sPos = (sp2.A.tooth && toothC) ? (sp2.lo - th / 2) : (sp2.ea + sp2.A.g);
          var ePos = (sp2.B.tooth && toothC) ? (sp2.hi + th / 2) : (sp2.eb - sp2.B.g);
          var R3 = ePos - sPos;
          if (!(R3 > 0.02)) continue;
          /* its own running bond: where entering and stopping do not already
             shift the head joints by enough, the stopping course starts with
             a half block */
          /* of the two ways to start the stopping course -- on the grid or
             half a block over -- take the one that shifts the head joints
             further from the course below */
          var shift = _mod(th + bj, bm), half = Math.max(0.04, bm / 2 - bj);
          var ov0 = Math.min(shift, bm - shift), sh2 = _mod(shift + half + bj, bm), ovH = Math.min(sh2, bm - sh2);
          sp2.ov = Math.max(ov0, ovH);
          var u3 = 0, runB = [];
          if (!toothC && ovH > ov0 + 1e-6 && half < R3) { runB.push([0, half, 'cut']); u3 = half + bj; }
          while (u3 + bL <= R3 + 1e-6) { runB.push([u3, bL, 'whole']); u3 += bm; }
          if (R3 - u3 > 0.02) runB.push([u3, R3 - u3, 'cut']);
          /* WHERE TWO WALLS INSIDE CROSS (thouse-v53): one of them runs
             through the crossing and the other stops at its face, and that
             swaps course by course -- the walls along x pass on the odd
             courses, along z on the even ones. The one that stops has the
             crossing taken out of its grid and the pieces beside it are cut,
             so its bond runs on past the crossing unchanged. */
          var passes = sp2.isV ? (kc % 2 === 0) : (kc % 2 === 1), q5, bandsX = [];
          if (!passes) {
            for (q5 = 0; q5 < BSPEC.length; q5++) {
              var sp5 = BSPEC[q5];
              if (sp5 === sp2 || sp5.isV === sp2.isV) continue;
              if (!(sp5.cc > sPos - 0.001 && sp5.cc < ePos + 0.001)) continue;
              /* only a wall that really runs across, not one that ends here */
              if (!(sp2.cc > Math.min(sp5.ea, sp5.eb) + th / 2 && sp2.cc < Math.max(sp5.ea, sp5.eb) - th / 2)) continue;
              bandsX.push([sp5.cc - th / 2 - bj - sPos, sp5.cc + th / 2 + bj - sPos]);
            }
          }
          var HSB = (STATE.holes || []).concat(API.wallOpenings ? API.wallOpenings() : []), qh2;
          for (qh2 = 0; qh2 < HSB.length; qh2++) {
            var hb = HSB[qh2];
            if (!hb || hb.ref !== sp2.id || (hb.kind !== 'bearing' && hb.kind !== 'partition')) continue;
            var by0 = y0 + (hb.sill || 0), by1 = by0 + (hb.h || 0);
            if (yt <= by0 + 0.001 || yb >= by1 - 0.001) continue;
            bandsX.push([sp2.ea + hb.u - sPos, sp2.ea + hb.u + hb.w - sPos]);
            holeCut++;
          }
          if (bandsX.length) {
            var cutB = [], q6, q7;
            for (q6 = 0; q6 < runB.length; q6++) {
              var segsB = [[runB[q6][0], runB[q6][0] + runB[q6][1]]];
              for (q7 = 0; q7 < bandsX.length; q7++) {
                var B0 = bandsX[q7][0], B1 = bandsX[q7][1], nxB = [], q8;
                for (q8 = 0; q8 < segsB.length; q8++) {
                  var s8 = segsB[q8][0], e8 = segsB[q8][1];
                  if (B1 <= s8 + 1e-9 || B0 >= e8 - 1e-9) { nxB.push([s8, e8]); continue; }
                  if (B0 > s8 + 1e-9) nxB.push([s8, B0]);
                  if (B1 < e8 - 1e-9) nxB.push([B1, e8]);
                }
                segsB = nxB;
              }
              for (q8 = 0; q8 < segsB.length; q8++) {
                var LB = segsB[q8][1] - segsB[q8][0];
                if (LB > 0.02) cutB.push([segsB[q8][0], LB, (Math.abs(LB - bL) < 0.002) ? 'whole' : 'cut']);
              }
            }
            runB = cutB;
            sp2.cross = (sp2.cross || 0) + bandsX.length;
          }
          var r3;
          for (r3 = 0; r3 < runB.length; r3++) {
            var pb = runB[r3], pcB;
            var extB = (bj > 0.0005 && runB[r3 + 1] && Math.abs(runB[r3 + 1][0] - (pb[0] + pb[1]) - bj) < 0.0015) ? bj : 0;
            var mb = sPos + pb[0] + (pb[1] + extB) / 2, hlB = (pb[1] + extB) / 2;
            pcB = sp2.isV ? [sp2.cc, cy, mb, th / 2, hy, hlB, chm, null, pb[1]] : [mb, cy, sp2.cc, hlB, hy, th / 2, chm, null, pb[1]];
            if (pb[2] === 'whole') { bWhole.push(pcB); nWhole++; sp2.whole++; }
            else { bCut.push(pcB); nCut++; sp2.cut++; cutLens.push(pb[1]); }
          }
        }
      }
      /* ties where an inner wall is not toothed in */
      for (i = 0; i < BSPEC.length; i++) {
        BSPEC[i].ties = ((BSPEC[i].A.tie ? 1 : 0) + (BSPEC[i].B.tie ? 1 : 0)) * Math.floor(nCrs / 2);
      }
      /* the ring beam's place over the inner walls */
      if (rest > 0.001) {
        for (i = 0; i < BSPEC.length; i++) {
          var sp3 = BSPEC[i], s3 = sp3.ea + sp3.A.g, e3 = sp3.eb - sp3.B.g;
          if (e3 - s3 <= 0.02) continue;
          if (sp3.isV) _box(T, g, th, rest, e3 - s3, sp3.cc, yMas + rest / 2, (s3 + e3) / 2, 0, 0x9A9A96, 1);
          else _box(T, g, e3 - s3, rest, th, (s3 + e3) / 2, yMas + rest / 2, sp3.cc, 0, 0x9A9A96, 1);
        }
      }
      if (BSPEC.length || bearSkip.length) {
        var bl = [];
        for (i = 0; i < BSPEC.length; i++) {
          var sp4 = BSPEC[i];
          bl.push('#' + sp4.id + ' ' + _mm(sp4.eb - sp4.ea) + ' ' + sp4.A.what + (sp4.A.tooth ? ' (toothed in)' : (sp4.A.tie ? ' (tied)' : ' (butted)')) +
                  ' to ' + sp4.B.what + (sp4.B.tooth ? ' (toothed in)' : (sp4.B.tie ? ' (tied)' : ' (butted)')) +
                  ': ' + sp4.whole + ' whole + ' + sp4.cut + ' cut, ' + sp4.ties + ' ties' +
                  (sp4.cross ? (', stops at ' + Math.round(sp4.cross / Math.max(1, Math.floor(nCrs / 2))) + ' crossing(s) every other course') : '') +
                  ', head joints shift ' + _mm(sp4.ov || 0) +
                  ' course to course (needs ' + _mm(need) + ') ' + ((sp4.ov || 0) + 0.0005 >= need ? 'ok' : '-- BOND SHORT, as on the outer walls of this block'));
        }
        (function () {
        if (!(rest > 0.001)) return;
        var q;
        for (q = 0; q < BSPEC.length; q++) {
          var b0 = BSPEC[q], Lb = b0.hi - b0.lo;
          if (!(Lb > 0.2)) continue;
          /* THE TOP BEAM ONLY OVER A WALL THAT REACHES IT (thouse-v152, Marek:
             zustal venec po odrezani nosne zdi ve druhem patre). A wall cut
             away on the storey above stops at the floor; nothing of it is
             under the roof, so no beam runs there. */
          if (b0.yHi >= yMas - 0.01) {
            if (b0.isV) _box(T, g, th, rest, Lb, b0.cc, yMas + rest / 2, (b0.lo + b0.hi) / 2, 0, 0x9A9A96, 1);
            else _box(T, g, Lb, rest, th, (b0.lo + b0.hi) / 2, yMas + rest / 2, b0.cc, 0, 0x9A9A96, 1);
            bearBeamLen += Lb;
          }
          /* THE FLOOR BEAM RUNS OVER EVERY WALL THAT REACHES IT (thouse-v127,
             X3, Marek: pokud je v prvnim patre venec, ma byt i na nosne zdi, i
             kdyby byla jen v prvnim patre): a wall that stops at the floor
             carries the beam on its last course just as one that goes on. */
          if (secondBeam && b0.yLo < secondBeam.y && b0.yHi >= secondBeam.y - 0.01) {
            if (b0.isV) _box(T, g, th, crs, Lb, b0.cc, secondBeam.y + crs / 2, (b0.lo + b0.hi) / 2, 0, 0x9A9A96, 1);
            else _box(T, g, Lb, crs, th, (b0.lo + b0.hi) / 2, secondBeam.y + crs / 2, b0.cc, 0, 0x9A9A96, 1);
            secondBeamLen += Lb;
          }
        }
        if (bearBeamLen > 0) ctx.log('mass  ring beam over the load-bearing wall(s): ' + Math.round(bearBeamLen * 10) / 10 + ' m, tied into the outer ring at both ends');
      })();
      ctx.log('mass  bearing walls ' + BSPEC.length + ' | ' + bl.join(' | ') +
                '; toothed walls enter the outer wall in every other course and the outer grid keeps running past the junction' +
                (bearSkip.length ? (' | SKIPPED: ' + bearSkip.join('; ')) : ''));
      }
      /* THE WALLS INSIDE IN MAGENTA while they are being judged (thouse-v51,
         Marek: at muzu lepe valisovat to jak prostupuji obvodovou stenou).
         Off again once they are accepted. */
      /* the load-bearing walls in their own block colour now, the magenta of
         the review is off (thouse-v107, Marek: magenta z bloku nosne zdi muzes
         odstranit); cut pieces a shade darker, like the outer wall's */
      if (bWhole.length) g.add(_chbMesh(T, bWhole, col));
      if (bCut.length) g.add(_chbMesh(T, bCut, new T.Color(col).multiplyScalar(0.86).getHex()));
      /* ONE BODY PER GABLE WALL, CLIPPED BY ITS OWN ROOF (thouse-v171, Marek:
         stity vubec nejsou videt). One body for all the gables was clipped by
         every slope at once, and the wing's slopes, carried on past the wing,
         pass under the bar's gable and took it away. Now each gable wall is
         clipped only by the slopes whose outline it stands under. */
      var nGw = 0, nGp = 0, q10, stR = global.ConfiguratorStage, gMat = 0, gWedges = 0, gJoint = { whole: 0, cut: 0 };
      try { if (stR && stR.renderer) stR.renderer.localClippingEnabled = true; } catch (eLC) {}
      try { var stI = (typeof _stageOf === 'function') ? _stageOf() : null; if (stI && stI.renderer) stI.renderer.localClippingEnabled = true; } catch (eLC2) {}
      for (q10 = 0; q10 < P.length; q10++) {
        var GW = gWallP[q10]; if (!GW || (!GW.w.length && !GW.c.length)) continue;
        var Aq = P[q10], Bq = P[(q10 + 1) % P.length], mine = [], qp;
        var probes = [[Aq[0] * 0.9 + Bq[0] * 0.1, Aq[1] * 0.9 + Bq[1] * 0.1], [(Aq[0] + Bq[0]) / 2, (Aq[1] + Bq[1]) / 2], [Aq[0] * 0.1 + Bq[0] * 0.9, Aq[1] * 0.1 + Bq[1] * 0.9]];
        for (qp = 0; qp < gPlanes.length; qp++) {
          var hit = false, qq2;
          for (qq2 = 0; qq2 < probes.length && !hit; qq2++) if (ptInM(gPlanes[qp].poly, probes[qq2][0], probes[qq2][1])) hit = true;
          if (hit) mine.push(gPlanes[qp].plane);
        }
        if (!mine.length) { for (qp = 0; qp < gPlanes.length; qp++) mine.push(gPlanes[qp].plane); }
        /* the blocks are cut as geometry, solid, capped (thouse-v173); no
           picture clipping, so the renderer setting no longer matters */
        /* THE GABLE'S JOINTS ARE FILLED LIKE THE WALL'S (core-v532, Marek:
           jaksi se generuji spary kolem cihel i mimo steny, u stitu). Every
           gable block went through the cutter, which draws a block 4 mm
           short of its cell on every side -- a gap round each one, the slit
           the wall below lost in thouse-v258, and daylight through the
           gable. A block the roof does not cross is drawn as the courses
           below draw theirs, a course high with its chamfers reading as the
           joint; only a block the roof line crosses is cut, and it is cut
           flush, its mortar in its body. A block wholly over the roof is
           dropped as before. */
        var GWin = [], GWcut = [], GCin = [], GCcut = [], qs, stq;
        for (qs = 0; qs < GW.w.length; qs++) { stq = _masBoxState(GW.w[qs], mine); if (stq === 'in') GWin.push(GW.w[qs]); else if (stq === 'cut') GWcut.push(GW.w[qs]); }
        for (qs = 0; qs < GW.c.length; qs++) { stq = _masBoxState(GW.c[qs], mine); if (stq === 'in') GCin.push(GW.c[qs]); else if (stq === 'cut') GCcut.push(GW.c[qs]); }
        var colC = new T.Color(col).multiplyScalar(0.86).getHex();
        if (GWin.length) g.add(_chbMesh(T, GWin, col));
        if (GCin.length) g.add(_chbMesh(T, GCin, colC));
        var gm1 = GWcut.length ? _cutMesh(T, GWcut, mine, col, 0) : null, gm2 = GCcut.length ? _cutMesh(T, GCcut, mine, colC, 0) : null;
        if (gm1) g.add(gm1);
        if (gm2) g.add(gm2);
        gJoint.whole += GWin.length + GCin.length; gJoint.cut += GWcut.length + GCcut.length;
        nGw++; nGp += GW.w.length + GW.c.length;
        /* WHAT THE GABLE REALLY USES (thouse-v176, Marek: presne uctovat,
           rezane kusy s co nejmensim odpadem, s prorezy a malou rezervou).
           Every piece is measured against the roof line: a block wholly under
           it counts one; a block the line crosses counts the part that stays,
           read at its two ends (a straight cut, so the mean of the two); a
           corner piece counts its length. The offcut of one wedge is the wedge
           of the mirrored course, so the material adds up over the pieces
           rather than block by block. */
        var alongXw = Math.abs(Bq[0] - Aq[0]) >= Math.abs(Bq[1] - Aq[1]), allP = GW.w.concat(GW.c), qm, matG = 0, nWedge = 0;
        var yUnder = function (x, z) {
          var best = Infinity, qq3;
          for (qq3 = 0; qq3 < mine.length; qq3++) { var pn3 = mine[qq3].normal, c3 = mine[qq3].constant; if (Math.abs(pn3.y) < 1e-6) continue; var yv = -(pn3.x * x + pn3.z * z + c3) / pn3.y; if (yv < best) best = yv; }
          return best;
        };
        for (qm = 0; qm < allP.length; qm++) {
          var pcm = allP[qm], bhm = bh, blm = bL, ybm = pcm[1] - pcm[4];
          var eA = alongXw ? [pcm[0] - pcm[3], pcm[2]] : [pcm[0], pcm[2] - pcm[5]], eB = alongXw ? [pcm[0] + pcm[3], pcm[2]] : [pcm[0], pcm[2] + pcm[5]];
          var hA = Math.max(0, Math.min(bhm, yUnder(eA[0], eA[1]) - ybm)), hB = Math.max(0, Math.min(bhm, yUnder(eB[0], eB[1]) - ybm));
          var lenF = ((pcm[8] != null) ? pcm[8] : (alongXw ? 2 * pcm[3] : 2 * pcm[5])) / blm, hF = ((hA + hB) / 2) / bhm;
          if (hA < bhm - 0.002 || hB < bhm - 0.002) nWedge++;
          matG += lenF * hF;
        }
        GW.mat = matG; GW.wedges = nWedge;
        gMat += matG; gWedges += nWedge;
      }
      /* the buy: the material with a kerf on every wedge cut (5 mm of a 250
         block, two per pairing) and a reserve of 5 per cent, rounded up */
      var kerfG = gWedges * (0.005 / bL), buyG = Math.ceil((gMat + kerfG) * 1.05);
      if (nGw) API.billAdd(LAYER, (_sysId() === 'cmu') ? 'WWB03003' : (_sysId() === 'eps' ? 'WWB03002' : 'WWB03001'), buyG);
      if (nGw) ctx.log('mass  gables: ' + nGw + ' wall(s) carried on ' + nExtra + ' course(s) over the ring beam in the same bond, ' + nGp + ' piece(s): ' + gJoint.whole + ' drawn whole with the chamfer as the joint, ' + gJoint.cut + ' cut flush on the rafters\' underside by the roof planes over its wall (core-v532, no gap round a gable block any more) | material ' + (Math.round(gMat * 10) / 10) + ' block(s) with ' + gWedges + ' wedge(s) paired from their offcuts, kerf 5 on each, reserve 5 per cent -> buy ' + buyG + ' (would be ' + nGp + ' by the piece)');
      else ctx.log('mass  gables: none -- the roof sits on the ring beam all round');
      if (whole.length) _masByWall(T, g, whole, col);
      if (cuts.length) {
        var dk = new T.Color(col).multiplyScalar(0.86);
        _masByWall(T, g, cuts, dk.getHex());
      }
      var PL = roofPlanes() || [], S0 = storeys(), fl = (S0 && S0[0]) ? S0[0].y0 : 0;
      ctx.log('mass  ' + P.length + ' wall(s) of ' + Math.round(th * 1000) + ' mm ' + SYS().id +
              (_sysSaved() ? ' (saved with the project)' : ' (the default -- nothing chosen in this project yet)') +
              ', from ' + sg(y0) + ' (on the slab) to ' + sg(yTop) +
              ' (under the wall plate): ' + nCrs + ' courses of ' + _mm(crs) + ' (block ' + _mm(BLOCK(_sysId()).h) + ' + bed ' + _mm(BLOCK(_sysId()).bed) +
              ') = ' + _mm(yMas - y0) + ' mm of masonry up to ' + sg(yMas) +
              (rest > 0.001 ? (', ' + _mm(rest) + ' left to the plate -- the ring beam\'s place, in concrete WWR03001 with formwork WWR03009 (billed with its cage)') : ', on the plate exactly') +
              (buried > 0 ? (', its foot ' + Math.round(buried * 1000) + ' in the ground (no plinth' +
                             (_footFull() ? ', drawn on the base step)' : ', drawn from grade outside the base step)')) : '') +
              '; finished floor ' + sg(fl) + (Math.abs(fl - floorLevel()) > 0.001 ? (' -- NOT the ' + sg(floorLevel()) +
              ' this house asks for: the core did not take it (configurator-ui.js older than core-v335?)') : '') +
              ', clear height ' + Math.round((STATE.clearH[0] || 0) * 1000) +
              ', wall top ' + sg(wallTop()) + ', ' +
              Math.round(polyArea(P) * 10) / 10 + ' m2 footprint, ' +
              PL.length + ' roof plane(s) over it');
      /* THE BLOCKS THAT HAVE TO BE BOUGHT (thouse-v51, Marek: pocitej
         bezezbytkove, odecist reznou sparu a vyuzit odrezky): each cut is taken
         from the smallest offcut it still fits in, a saw cut of 5 mm gone with
         it; only when none fits is another block opened. */
      var KERF = 0.005, pool = [], blkCuts = 0, reused = 0, qz, qw;
      cutLens.sort(function (u, v) { return v - u; });
      for (qz = 0; qz < cutLens.length; qz++) {
        var need2 = cutLens[qz] + KERF, bestI = -1;
        for (qw = 0; qw < pool.length; qw++) if (pool[qw] >= need2 - 1e-9 && (bestI < 0 || pool[qw] < pool[bestI])) bestI = qw;
        if (bestI >= 0) { pool[bestI] -= need2; reused++; }
        else { blkCuts++; pool.push(bL - need2); }
      }
      var waste = 0;
      for (qw = 0; qw < pool.length; qw++) waste += pool[qw];
      ctx.log('mass  buy ' + (nWhole + blkCuts) + ' blocks: ' + nWhole + ' laid whole + ' + blkCuts + ' cut up for ' + nCut +
              ' pieces (' + reused + ' of them out of offcuts, saw cut ' + _mm(KERF) + '); ' + Math.round(waste * 100) / 100 +
              ' m of offcut left over, ' + (nCut ? Math.round(waste / (blkCuts * bL || 1) * 100) : 0) + ' per cent of the blocks cut up');
      if (nPocketed) ctx.log('mass  joist pockets: ' + nPocketed + ' block(s) of the bearing course notched 100 deep from the inside for a joist end, whole from the outside, each bought as one block; drawn in parts that meet without a joint');
      ctx.log('mass  blocks ' + nWhole + ' whole + ' + nCut + ' cut' +
              (nCut ? (' (at the junctions and the ends of the walls inside: ' + cutAt.join('; ') + (nCut > cutAt.length ? ' ...' : '') + ')') :
                      ' -- nothing cut, every block whole') +
              ', ' + nCrs + ' courses; corners ' +
              'BONDED, pinwheel: at each corner the wall leaving it runs through on even courses, the wall arriving on odd; head joints shift ' +
              _mm(overlap) + ' course to course (width ' + _mm(th) + ' + joint ' + _mm(bj) + ' round a ' + _mm(bm) + ' module; needs ' + _mm(need) + ') ' +
              (overlap + 0.0005 >= need ? 'ok' : '-- BOND SHORT: this block overlaps less than 0.4 of its height') +
              '; ' + (whole.length + cuts.length) * 132 + ' vertices in 2 bodies');
      /* the plan against the block module: how many blocks each run is, and
         whether it comes out whole */
      /* WHAT THE OPENINGS COST (thouse-v58): the lintels by the piece and by
         the metre, and the reveals by the square metre for the plaster and the
         insulation that will wrap them. No catalogue code exists for a lintel
         yet, so the line says so instead of billing it into the wrong row. */
      (function () {
        var HB = STATE.holes || [], q9, jamb = 0;
        for (q9 = 0; q9 < HB.length; q9++) {
          var hb2 = HB[q9];
          if (!hb2) continue;
          jamb += (2 * (hb2.h || 0) + (hb2.w || 0)) * th;
        }
        if (HB.length) ctx.log('BILL  openings: ' + nLintel + ' lintel(s), ' + Math.round(linLen * 100) / 100 + ' m at ' +
          _mm(th) + ' wide and one course high -- NOT BILLED, no catalogue code yet (step 4); reveals ' +
          Math.round(jamb * 100) / 100 + ' m2 for plaster and insulation, also to come');
      })();
      var md = blockModule(), O = _offs();
      var nBear = 0, nPart = 0, qb;
      for (qb = 0; qb < PT.length; qb++) { if (PT[qb] && PT[qb].bear) nBear++; else if (PT[qb]) nPart++; }
      /* the windows and the doors themselves, built by the shared asset so
         every product has the same unit (thouse-v68) */
      try {
        /* THE SILL REACHES OVER THE FACADE (thouse-v265): the asset draws the
           outer sill board 30 past the masonry, which since the facade came
           was inside the insulation; it goes 30 past the finished face now */
        var sillOutT = 0.03;
        try { sillOutT = facadeT() + 0.03; } catch (eSO) {}
        var nUn = API.buildOpenings ? API.buildOpenings(T, g, { t: wallT(), inset: 0.08, layer: LAYER, floorUp: FLOOR.build, sillOut: sillOutT }) : 0;
        if (nUn) ctx.log('mass  ' + nUn + ' window(s) and door(s) built with frame, glass and both sills -- from the shared asset; the outer sill ' + _mm(sillOutT) + ' past the masonry, 30 past the facade');
      } catch (eU) { ctx.log('mass  openings not built: ' + (eU && eU.message)); }
      /* THE BLOCKS AND WHAT LAYS THEM, ON THE CODES WE ALREADY HAVE
         (thouse-v69). Clay goes up on thin bed mortar, the concrete unit on
         type S mortar with grout in its reinforced cores, and the foam form is
         filled with concrete and held by a braced system while it sets. Each
         is billed on the quantity the wall actually has: the blocks by the
         piece, the rest by the area of wall laid. */
      (function () {
        var sysB = _sysId(), P2 = poly(), qw, per = 0;
        for (qw = 0; qw < P2.length; qw++) {
          var a3 = P2[qw], b3 = P2[(qw + 1) % P2.length];
          per += Math.hypot(b3[0] - a3[0], b3[1] - a3[1]);
        }
        var areaB = per * (wallTop() - PLATE.h - slabTop());
        /* AND THE GABLES' AREA (core-v587): the courses over the ring beam take
           the same mortar (or grout, or ICF fill) as the wall under it; read
           off the material the gables count, a block's face with its joints */
        var areaG = (typeof gMat === 'number' && gMat > 0) ? gMat * bm * crs : 0;
        areaB += areaG;
        var nBuyB = nWhole + blkCuts;
        var code = (sysB === 'cmu') ? 'WWB03003' : (sysB === 'eps' ? 'WWB03002' : 'WWB03001');
        API.billAdd(LAYER, code, nBuyB);
        if (sysB === 'clay') {
          API.billAdd(LAYER, 'WWM00001', areaB * 4.0);
        } else if (sysB === 'cmu') {
          API.billAdd(LAYER, 'WWM00003', areaB * 12.0);
          API.billAdd(LAYER, 'WWR03002', areaB * 0.02);
        } else {
          API.billAdd(LAYER, 'WWR03003', areaB * wallT() * 0.55);
          API.billAdd(LAYER, 'WWM00004', areaB * 0.05);
          API.billAdd(LAYER, 'WWR03010', Math.max(1, Math.ceil(areaB / 25)));
        }
        ctx.log('BILL  mass: ' + nBuyB + ' block(s) on ' + code + ' by the piece, over ' + Math.round(areaB * 10) / 10 +
                ' m2 of wall' + (areaG > 0 ? (' (gables ' + Math.round(areaG * 10) / 10 + ' m2 of it)') : '') + (sysB === 'clay' ? ' with thin bed mortar WWM00001 at 4 kg per m2'
                  : (sysB === 'cmu' ? ' with type S mortar WWM00003 at 12 kg per m2 and grout WWR03002 at 0.02 m3 per m2'
                  : ' filled with ICF concrete WWR03003, foam adhesive WWM00004 and a braced system WWR03010')));
      })();
      /* THE LINTELS ON THE CODES THE CATALOGUE ALREADY HAS (thouse-v74, V6).
         Four lengths are stocked -- 1000, 1250 and 2500 -- so each opening
         takes the shortest that spans it with its bearing, and a span past the
         longest takes as many of the longest as the wall is thick enough to
         carry side by side. One lintel per opening in the thin wall, two in a
         wall of 300 or more, which is how they are laid in practice. */
      (function () {
        var LIN = [{ c: 'WSC10001', l: 1.0 }, { c: 'WSC12001', l: 1.25 }, { c: 'WSC25001', l: 2.5 }];
        var HL2 = (STATE.holes || []).concat(API.wallOpenings ? API.wallOpenings() : []), q6, per = (wallT() >= 0.30) ? 2 : 1, said = [];
        for (q6 = 0; q6 < HL2.length; q6++) {
          var o6 = HL2[q6];
          if (!o6 || o6.kind !== 'wall') continue;
          /* a round light takes no lintel and buys none (core-v521) */
          var rd6 = false;
          try { var it6 = _openingItem(o6.code); rd6 = !!(it6 && it6.round); } catch (e6) { rd6 = false; }
          if (rd6) { said.push('#' + o6.id + ' round -- no lintel'); continue; }
          var need = o6.w + 0.25, pick = null, z6;
          for (z6 = 0; z6 < LIN.length; z6++) if (LIN[z6].l >= need - 0.001) { pick = LIN[z6]; break; }
          /* A SPAN PAST THE LONGEST LINTEL IS CAST (core-v659, RANCH thread,
             spec.castLintel): two 2500 lintels side by side over a 4800 garage
             door span 2500, not 5050 -- lying through the wall's thickness
             they never meet over the middle. With spec.castLintel such an
             opening gets a lintel cast in place, one course deep, the wall's
             width, 250 bearing a side, under the ring beam: concrete WWR03001,
             formwork WWR03009 by the metre, four bars WWL00141 with 6 per cent
             of laps, a stirrup WWR00061 every 200, spacers FFM00004 4 a metre,
             tie wire FEC00026 -- the ring beam's own rows. Without it, as
             before. */
          if (!pick && spec.castLintel) {
            var Lc = o6.w + 0.50, stC = Math.ceil(Lc / 0.20) + 1, m3C = Lc * wallT() * RING_H;
            if (!CATALOG.WWR03001) CATALOG.WWR03001 = { code: 'WWR03001', unit: 'm3', label: 'Ring beam concrete C20/25' };
            if (!CATALOG.WWR03009) CATALOG.WWR03009 = { code: 'WWR03009', unit: 'm', label: 'Ring beam formwork' };
            API.billAdd(LAYER, 'WWR03001', m3C);
            API.billAdd(LAYER, 'WWR03009', Lc);
            API.billAdd(LAYER, 'WWL00141', Lc * 4 * 1.06);
            API.billAdd(LAYER, 'WWR00061', stC);
            API.billAdd(LAYER, 'FFM00004', Math.ceil(Lc * 4));
            API.billAdd(LAYER, 'FEC00026', stC * 2 * 0.5 * 0.010);
            said.push('#' + o6.id + ' ' + _mm(o6.w) + ' -> CAST in place, ' + _mm(Lc) + ' x ' + _mm(wallT()) + ' x ' + _mm(RING_H) + ' (' + (Math.round(m3C * 1000) / 1000) +
                      ' m3 WWR03001, ' + (Math.round(Lc * 100) / 100) + ' m WWR03009, 4 bars WWL00141, ' + stC + ' x WWR00061, ' + Math.ceil(Lc * 4) + ' x FFM00004, FEC00026) -- past the longest stocked lintel');
            continue;
          }
          if (!pick) { pick = LIN[LIN.length - 1]; }
          API.billAdd(LAYER, pick.c, per);
          said.push('#' + o6.id + ' ' + _mm(o6.w) + ' -> ' + per + ' x ' + pick.c);
        }
        if (said.length) ctx.log('BILL  lintels: ' + said.join(', ') + ' (a bearing of 125 each side, ' + per + ' per opening in a ' + _mm(wallT()) + ' wall)');
      })();
      /* THE RING BEAM'S CAGE (thouse-v76, R1): four bars round the building
         with a stirrup every 300, lapped where the bars meet, and the cover
         the trade works to -- 25 mm to the stirrup all round, which sets how
         far in the cage sits. The spacers that hold it off the formwork are
         the catalogue's rebar spacer, four to the metre. The tie wire has no
         row yet and is the only piece of this reported rather than billed. */
      (function () {
        var P3 = poly(), q7, per3 = 0, skipped = 0;
        for (q7 = 0; q7 < P3.length; q7++) {
          var a7 = P3[q7], b7 = P3[(q7 + 1) % P3.length];
          per3 += Math.hypot(b7[0] - a7[0], b7[1] - a7[1]);
        }
        /* NO CAGE OVER A FULL-HEIGHT OPENING (thouse-v122, Marek: zelezo ve
           venci musis odstranit, pokud je otvor pres celou vysku): the beam
           stops there, so its bars and stirrups stop there too */
        for (q7 = 0; q7 < fullTop.length; q7++) skipped += (fullTop[q7].w || 0);
        per3 = Math.max(0, per3 - skipped);
        if (!(per3 > 1)) return;
        per3 += (typeof bearBeamLen === 'number' ? bearBeamLen : 0);
        per3 += (typeof secondBeamLen === 'number' ? secondBeamLen : 0);
        var bars = 4, lap = 1.06, barM = per3 * bars * lap;
        var stir = Math.ceil(per3 / 0.30);
        var spac = Math.ceil(per3 * 4);
        /* AND NOW IT IS DRAWN (thouse-v81, R4): four bars round the building
           with a stirrup every 300, set 25 in from every face of the ring beam,
           so the cage can be looked at instead of taken on trust. Magenta while
           it is the thing under review. */
        (function () {
          /* INSIDE THE BEAM, NOT OVER IT (thouse-v129, X5, Marek: armovani
             horniho vence je nad, ne v nem): the beam is the course over the
             masonry, yMas up one course, and the cage sits in that, 25 in. */
          var cov = 0.025, bw = 0.014, ringH = RING_H, yBotR = yMas, yTopR = yMas + ringH;
          var inY0 = yBotR + cov + bw / 2, inY1 = yTopR - cov - bw / 2, q8;
          /* THE CAGE FOLLOWS THE CONCRETE, PIECE BY PIECE (thouse-v145, Marek:
             armovani horniho vence porad nekopiruje horni venec, kde je beton
             tam ma byt armovani). The beam is laid in the runs between the
             full-height openings; the cage was skipping the whole wall as soon
             as one opening touched it. It now takes exactly the runs the beam
             took, and nothing where the beam is not. */
          var cageRun = function (ax, az, bx, bz) {
            var dx8 = bx - ax, dz8 = bz - az, L8 = Math.hypot(dx8, dz8);
            if (!(L8 > 0.2)) return;
            var ux8 = dx8 / L8, uz8 = dz8 / L8, nx8 = -uz8, nz8 = ux8;
            var inset = wallT() / 2 - cov - bw / 2, mx8 = (ax + bx) / 2, mz8 = (az + bz) / 2, s8, t8;
            for (s8 = -1; s8 <= 1; s8 += 2) {
              for (t8 = 0; t8 < 2; t8++) {
                var yB = t8 ? inY1 : inY0;
                if (Math.abs(dx8) > Math.abs(dz8)) _box(T, g, L8, bw, bw, mx8 + nx8 * inset * s8, yB, mz8 + nz8 * inset * s8, 0, REBAR, 1);
                else _box(T, g, bw, bw, L8, mx8 + nx8 * inset * s8, yB, mz8 + nz8 * inset * s8, 0, REBAR, 1);
              }
            }
            var nSt = Math.max(1, Math.floor(L8 / 0.30)), k8;
            for (k8 = 0; k8 <= nSt; k8++) {
              var u8 = (k8 / nSt) * L8, px8 = ax + ux8 * u8, pz8 = az + uz8 * u8;
              var sw = wallT() - 2 * cov, sh8 = Math.max(0.05, ringH - 2 * cov), sl = 0.008, ym = (yBotR + yTopR) / 2;
              if (Math.abs(dx8) > Math.abs(dz8)) {
                _box(T, g, sl, sl, sw, px8, yBotR + cov + sl / 2, pz8, 0, REBAR, 1);
                _box(T, g, sl, sl, sw, px8, yTopR - cov - sl / 2, pz8, 0, REBAR, 1);
                _box(T, g, sl, sh8, sl, px8, ym, pz8 - sw / 2 + sl / 2, 0, REBAR, 1);
                _box(T, g, sl, sh8, sl, px8, ym, pz8 + sw / 2 - sl / 2, 0, REBAR, 1);
              } else {
                _box(T, g, sw, sl, sl, px8, yBotR + cov + sl / 2, pz8, 0, REBAR, 1);
                _box(T, g, sw, sl, sl, px8, yTopR - cov - sl / 2, pz8, 0, REBAR, 1);
                _box(T, g, sl, sh8, sl, px8 - sw / 2 + sl / 2, ym, pz8, 0, REBAR, 1);
                _box(T, g, sl, sh8, sl, px8 + sw / 2 - sl / 2, ym, pz8, 0, REBAR, 1);
              }
            }
          };
          for (q8 = 0; q8 < ringRuns.length; q8++) cageRun(ringRuns[q8][0], ringRuns[q8][1], ringRuns[q8][2], ringRuns[q8][3]);
        })();
        API.billAdd(LAYER, 'WWL00141', barM);
        API.billAdd(LAYER, 'WWR00061', stir);
        API.billAdd(LAYER, 'FFM00004', spac);
        /* THE WIRE THAT HOLDS THE CAGE TOGETHER (thouse-v77, R3): two ties at
           every stirrup, about half a metre of wire each, and the catalogue
           sells it by weight -- a millimetre and a quarter of annealed wire
           runs near ten grams to the metre. It was the last piece of this
           assembly that was drawn or needed and never billed. */
        var wireKg = stir * 2 * 0.5 * 0.010;
        API.billAdd(LAYER, 'FEC00026', wireKg);
        /* THE RING BEAM ITSELF (core-v586, Marek: jestli ti chybi polozky tak
           je dopln): its concrete and its formwork were drawn and never
           billed. The catalogue has both rows: WWR03001, concrete C20/25 for
           the ring beam, by the m3, and WWR03009, the ring beam's formwork,
           labour by the metre. The concrete is the band drawn -- one course
           high, a wall thick, along the runs the cage takes (top ring, the
           beams over the bearing walls, the floor beam); the outline's axis
           times the thickness is the band's plan area exactly at square
           corners -- and the formwork is the metres of beam. A product whose
           catalogue lacks the two rows gets them here under the catalogue's
           own codes (never invented). */
        var ringM3 = per3 * wallT() * RING_H;
        if (!CATALOG.WWR03001) CATALOG.WWR03001 = { code: 'WWR03001', unit: 'm3', label: 'Ring beam concrete C20/25' };
        if (!CATALOG.WWR03009) CATALOG.WWR03009 = { code: 'WWR03009', unit: 'm', label: 'Ring beam formwork' };
        API.billAdd(LAYER, 'WWR03001', ringM3);
        API.billAdd(LAYER, 'WWR03009', per3);
        /* THE FLOOR BEAM HAS THE SAME CAGE (thouse-v130, X6): drawn in it,
           bars and stirrup loops, over the outer walls and over the bearing
           walls that carry it; billed above in the same totals. */
        if (secondBeam) (function () {
          var cov2 = 0.025, bw2 = 0.014, yB0 = secondBeam.y, yB1 = secondBeam.y + crs, inA = yB0 + cov2 + bw2 / 2, inB = yB1 - cov2 - bw2 / 2;
          var drawRun = function (ax, az, bx, bz) {
            var dx = bx - ax, dz = bz - az, L = Math.hypot(dx, dz); if (!(L > 0.2)) return;
            var ux = dx / L, uz = dz / L, nx = -uz, nz = ux, mx = (ax + bx) / 2, mz = (az + bz) / 2, ins = wallT() / 2 - cov2 - bw2 / 2, sgn, t2;
            for (sgn = -1; sgn <= 1; sgn += 2) for (t2 = 0; t2 < 2; t2++) {
              var yy = t2 ? inB : inA;
              if (Math.abs(dx) > Math.abs(dz)) _box(T, g, L, bw2, bw2, mx + nx * ins * sgn, yy, mz + nz * ins * sgn, 0, REBAR, 1);
              else _box(T, g, bw2, bw2, L, mx + nx * ins * sgn, yy, mz + nz * ins * sgn, 0, REBAR, 1);
            }
            var nS = Math.max(1, Math.floor(L / 0.30)), k, sw = wallT() - 2 * cov2, sh = Math.max(0.05, crs - 2 * cov2), sl = 0.008, ym = (yB0 + yB1) / 2;
            for (k = 0; k <= nS; k++) {
              var u = (k / nS) * L, px = ax + ux * u, pz = az + uz * u;
              if (Math.abs(dx) > Math.abs(dz)) {
                _box(T, g, sl, sl, sw, px, yB0 + cov2 + sl / 2, pz, 0, REBAR, 1); _box(T, g, sl, sl, sw, px, yB1 - cov2 - sl / 2, pz, 0, REBAR, 1);
                _box(T, g, sl, sh, sl, px, ym, pz - sw / 2 + sl / 2, 0, REBAR, 1); _box(T, g, sl, sh, sl, px, ym, pz + sw / 2 - sl / 2, 0, REBAR, 1);
              } else {
                _box(T, g, sw, sl, sl, px, yB0 + cov2 + sl / 2, pz, 0, REBAR, 1); _box(T, g, sw, sl, sl, px, yB1 - cov2 - sl / 2, pz, 0, REBAR, 1);
                _box(T, g, sl, sh, sl, px - sw / 2 + sl / 2, ym, pz, 0, REBAR, 1); _box(T, g, sl, sh, sl, px + sw / 2 - sl / 2, ym, pz, 0, REBAR, 1);
              }
            }
          };
          var qf;
          for (qf = 0; qf < P3.length; qf++) { var af = P3[qf], bf = P3[(qf + 1) % P3.length]; drawRun(af[0], af[1], bf[0], bf[1]); }
          for (qf = 0; qf < BSPEC.length; qf++) {
            var bb = BSPEC[qf];
            if (!(bb.yLo < secondBeam.y && bb.yHi >= secondBeam.y - 0.01)) continue;
            if (bb.isV) drawRun(bb.cc, bb.lo, bb.cc, bb.hi); else drawRun(bb.lo, bb.cc, bb.hi, bb.cc);
          }
        })();
        ctx.log('BILL  ring beam cage: ' + bars + ' bars round ' + Math.round(per3 * 10) / 10 + ' m = ' +
                Math.round(barM * 10) / 10 + ' m WWL00141 with 6 per cent of laps, ' + stir +
                ' stirrups WWR00061 every 300, ' + spac + ' spacers FFM00004 at 4 per m, cover 25 to the stirrup' +
                (skipped > 0.01 ? ('; ' + Math.round(skipped * 100) / 100 + ' m left out over full-height openings') : '') + '; ' +
                'tie wire FEC00026 ' + Math.round(wireKg * 100) / 100 + ' kg at 2 ties per stirrup -- everything in this cage is billed; ' +
                'the beam: concrete WWR03001 ' + Math.round(ringM3 * 1000) / 1000 + ' m3 (' + Math.round(per3 * 10) / 10 + ' m x ' + _mm(wallT()) + ' x ' + _mm(RING_H) +
                '), formwork WWR03009 ' + Math.round(per3 * 10) / 10 + ' m');
      })();
      if (headUp.length) ctx.log('mass  window heads taken up to the next bed joint so the lintel bears on masonry: ' + headUp.join(', ') + ' mm');
      /* one line per opening: where its head fell in the course, where its
         lintel went, what was cut under the bearings and laid over it
         (thouse-v253, the lintel check) */
      (function () {
        var q9;
        for (q9 = 0; q9 < linOrder.length; q9++) {
          var LI = linInfo[linOrder[q9]];
          ctx.log('mass  lintel #' + LI.id + ' wall ' + LI.wall + (LI.door ? ' door/portal' : ' window') + ' ' + _mm(LI.w) + ' wide: head ' + sg(LI.head) +
                  (LI.off > 0.002 && LI.off < crs - 0.002 ? (', ' + _mm(LI.off) + ' over the bed joint below (mid course)') : ', on a bed joint') +
                  (LI.round ? (', round light -- no lintel; ' + LI.mk + ' piece(s) ' + _mm(LI.mkH) + ' made up over its head, ' + LI.trim + ' piece(s) ' + _mm(LI.trimH) + ' under its sill, cut on their height')
                            : ((LI.lin ? (', lintel ' + sg(LI.lin[0]) + '..' + sg(LI.lin[1]) + ' over ' + _mm(LI.lin[3] - LI.lin[2]) + ' (125 bearing each side)') : ', NO LINTEL DRAWN') +
                               (LI.trim ? (', ' + LI.trim + ' block(s) under the bearings cut to ' + _mm(LI.trimH)) : '') +
                               (LI.mk ? (', ' + LI.mk + ' make-up piece(s) ' + _mm(LI.mkH) + ' over the lintel') : ', no make-up over it'))));
        }
      })();
      var BP = bondPeriod();
      var wOK = [], wq;
      for (wq = 1; wq <= 3; wq++) { wOK.push(_mm(wq * BP.m)); if (BP.shift > 0.001) wOK.push(_mm(BP.shift + wq * BP.m)); }
      wOK.sort(function (x, y) { return parseFloat(x) - parseFloat(y); });
      ctx.log('mass  openings end whole on ' + wOK.join(' / ') + ' and so on -- a run of whole blocks, or a run plus the shift');
      ctx.log('mass  bond from the block ' + _mm(BLOCK(_sysId()).l) + ' x ' + _mm(wallT()) +
              ': module ' + _mm(BP.m) + ', head joints move ' + _mm(BP.shift) + ' every course, the pattern repeats after ' +
              BP.n + ' course(s), so a joint falls in the same place once in ' + BP.n +
              ' and the finest step an opening can end on is ' + _mm(BP.step));
      var HS = STATE.holes || [];
      if (HS.length) ctx.log('mass  openings ' + HS.length + ': ' + HS.map(function (h2) {
        return '#' + h2.id + ' in ' + h2.kind + ' ' + h2.ref + ' at ' + _mm(h2.u) + ' wide ' + _mm(h2.w) + ', sill ' + _mm(h2.sill || 0) + ', ' + _mm(h2.h || 0) + ' high';
      }).join(', ') + ' -- cut out in ' + holeCut + ' course(s), ' + nLintel + ' lintel(s) of one course over them reaching 125 into the wall each side, not billed yet (codes in step 4)');
      /* THE PARTITIONS, ON THEIR OWN STOREY ONLY (thouse-v123, Y4): a partition
         is a thin masonry wall, 115 thick, from the finished floor of the
         storey it was drawn on to the underside of that storey's ceiling --
         never carried up. Drawn as a wall body in the block's colour; the
         thin partition block has no catalogue row yet, so it is reported by
         area and not billed into anything else. */
      (function () {
        /* A PRODUCT THAT BUILDS ITS OWN PARTITIONS (core-v577, RANCH: timber
           stud partitions in a block house) passes spec.partitions === false
           and the masonry lays none; every other product is unchanged */
        if (spec.partitions === false) { ctx.log('mass  partitions: none here -- the product builds its own (spec.partitions false)'); return; }
        var PTp = STATE.parts || [], STp = storeys(), q, areaP = 0, nP = 0, PT_T = (PART_BLOCK(_partId()) || {}).w || PART().t, pSaid = [], dryRuns = [];
        for (q = 0; q < PTp.length; q++) {
          var pp = PTp[q];
          if (!pp || pp.bear) continue;
          var isVp = ((pp.o || pp.dir) === 'v'), cp = (typeof pp.c === 'number') ? pp.c : pp.at;
          if (typeof cp !== 'number' || typeof pp.a !== 'number' || typeof pp.b !== 'number') continue;
          /* THE FULL STOREY HEIGHT (thouse-v246, Marek: pricky musi byt na vysku
             celeho patra, at uz prvniho nebo druheho). A partition is masonry
             and is laid before the floors: it stands on the structure, not on
             the finished floor -- the slab on the ground floor, the floor deck
             upstairs -- and goes up to the structure above: the underside of
             the joists over a lower storey (the course the joists bear on, as
             the floor layer sets them), the wall head under the roof on the
             top storey. */
          var stp = pp.st | 0, S1 = STp[stp] || STp[0];
          var yA = (stp === 0) ? y0 : (S1.y0 - FLOOR.build), yB;
          if (STp[stp + 1]) { var yFn = STp[stp + 1].y0 - FLOOR.build; yB = y0 + Math.round((yFn - y0) / crs) * crs + crs; }
          else yB = (typeof S1.head === 'number') ? S1.head : S1.y1;
          /* CLIPPED BY THE OUTER WALLS (thouse-v137, W4, Marek: pricka tazena
             do side run si zachovava stejnou delku, musi se orezat obvodovymi
             stenami): the partition's own line is cut where it crosses the
             outline, so in the narrower wing it ends at the wing's wall. */
          var crs2 = [], qe2, PP = poly();
          for (qe2 = 0; qe2 < PP.length; qe2++) {
            var e0p = PP[qe2], e1p = PP[(qe2 + 1) % PP.length];
            if (isVp) { if (Math.abs(e1p[1] - e0p[1]) > 1e-6) continue; if (cp > Math.min(e0p[0], e1p[0]) - 1e-6 && cp < Math.max(e0p[0], e1p[0]) + 1e-6) crs2.push(e0p[1]); }
            else { if (Math.abs(e1p[0] - e0p[0]) > 1e-6) continue; if (cp > Math.min(e0p[1], e1p[1]) - 1e-6 && cp < Math.max(e0p[1], e1p[1]) + 1e-6) crs2.push(e0p[0]); }
          }
          crs2.sort(function (u, v) { return u - v; });
          var aC = pp.a, bC = pp.b, qc2;
          for (qc2 = 0; qc2 + 1 < crs2.length; qc2 += 2) {
            var lo2 = crs2[qc2] + th / 2, hi2 = crs2[qc2 + 1] - th / 2;
            if (pp.a < hi2 && pp.b > lo2) { aC = Math.max(pp.a, lo2); bC = Math.min(pp.b, hi2); break; }
          }
          var Lp = bC - aC;
          if (!(Lp > 0.2) || !(yB > yA)) continue;
          /* IN THE PARTITION'S OWN MATERIAL (thouse-v248, Marek: vizualne vidim
             porad cihlove pricky). The body was drawn in the outer wall's colour,
             so every partition looked like the wall. It now takes the colour of
             the system chosen on STRUCTURE, and a masonry partition shows its
             courses and staggered head joints on both faces, a drywall one its
             board joints every 1200. */
          var pCol = PART().col;
          if (isVp) _box(T, g, PT_T, yB - yA, Lp, cp, (yA + yB) / 2, (aC + bC) / 2, 0, pCol, 1);
          else _box(T, g, Lp, yB - yA, PT_T, (aC + bC) / 2, (yA + yB) / 2, cp, 0, pCol, 1);
          (function () {
            /* THE JOINT LINES ARE BODIES, NOT LINES (thouse-v258, Marek: v rezu
               zustavaji tenke draty). They were line segments, which the section
               tool neither cuts nor caps -- so a cut that took the partition away
               left its joints hanging in the air as thin wires. Each joint is a
               thin strip 3 mm proud of the face, in one body per partition. */
            var pidJ = _partId(), PBj = PART_BLOCK(pidJ), vj = [], yy, uu, kc = 0, fS, JW = 0.0015;
            var seg = function (u0, y0j, u1, y1j) {
              for (fS = -1; fS <= 1; fS += 2) {
                var fc = cp + fS * (PT_T / 2 + JW), um = (u0 + u1) / 2, ym = (y0j + y1j) / 2, hu = Math.max(JW, Math.abs(u1 - u0) / 2), hyj = Math.max(JW, Math.abs(y1j - y0j) / 2);
                vj.push(isVp ? [fc, ym, um, JW, hyj, hu] : [um, ym, fc, hu, hyj, JW]);
              }
            };
            if (pidJ === 'drywall') {
              for (uu = aC + 1.2; uu < bC - 0.05; uu += 1.2) seg(uu, yA, uu, yB);
            } else if (PBj) {
              var ch = PBj.h + (pidJ === 'brick' ? 0.010 : 0.001), cl = PBj.l + (pidJ === 'brick' ? 0.010 : 0.001);
              for (yy = yA + ch; yy < yB - 0.01; yy += ch) seg(aC, yy, bC, yy);
              for (yy = yA; yy < yB - 0.01; yy += ch, kc++) {
                var yTopC = Math.min(yB, yy + ch);
                for (uu = aC + ((kc % 2) ? cl / 2 : cl); uu < bC - 0.02; uu += cl) seg(uu, yy, uu, yTopC);
              }
            }
            if (!vj.length) return;
            g.add(_boxBatch(T, vj, (pidJ === 'brick') ? 0x7A3A2A : 0x9A958C));
          })();
          areaP += Lp * (yB - yA); nP++;
          dryRuns.push({ L: Lp, H: yB - yA });
          pSaid.push('#' + (pp.id || q) + ' storey ' + (stp + 1) + ' ' + sg(yA) + ' to ' + sg(yB) + ' (' + _mm(yB - yA) + ', ' + (stp === 0 ? 'slab' : 'floor deck') + ' to ' + (STp[stp + 1] ? 'joist underside' : 'wall head') + ')');
        }
        /* THE PARTITIONS ARE BILLED BY THE PIECE IN THE SIZE CHOSEN (thouse-v203,
           Marek: vykazujeme na ks podle toho co si uzivatel nadefinuje). The
           brick goes on WWB03004 with 10 mm beds, the clay block on WWB03005
           with tongue-and-groove ends and a thin bed, both from the area of
           the partitions and the element's own size on the STRUCTURE sample,
           plus 3 per cent for cuts. The block's thin bed mortar goes on
           WWM00001 in proportion to its thickness; the brick's full bed and
           the drywall have no catalogue row yet. */
        if (nP) {
          var pidB = _partId(), PBb = PART_BLOCK(pidB), billNote = '';
          if (PBb && pidB === 'brick') {
            var perB = (PBb.l + 0.010) * (PBb.h + 0.010), nBr = Math.ceil(areaP / perB * 1.03);
            API.billAdd(LAYER, 'WWB03004', nBr);
            billNote = nBr + ' brick(s) WWB03004 of ' + _mm(PBb.l) + ' x ' + _mm(PBb.w) + ' x ' + _mm(PBb.h) + ' with 10 mm beds and 3 per cent for cuts, BILLED; the full bed mortar has no code yet';
          } else if (PBb && pidB === 'aac') {
            var perC = PBb.l * (PBb.h + 0.001), nBl = Math.ceil(areaP / perC * 1.03), kgM = 4.0 * (PBb.w / 0.40);
            API.billAdd(LAYER, 'WIB01151', nBl);
            API.billAdd(LAYER, 'WWM00001', areaP * kgM);
            billNote = nBl + ' AAC block(s) WIB01151 of ' + _mm(PBb.l) + ' x ' + _mm(PBb.w) + ' x ' + _mm(PBb.h) + ' with 3 per cent for cuts and ' + Math.round(areaP * kgM * 10) / 10 + ' kg of thin bed mortar WWM00001, BILLED';
          } else {
            /* THE DRYWALL PARTITION, BILLED WHOLE (thouse-v247): metal studs
               WDD00501 at 625 the full height, floor and head tracks WDD00503
               fixed into the slab and the structure with concrete screw anchors
               FAS10075 every 600, mineral wool INS00601 in the bays, gypsum
               board WDD24001 on both faces with 5 per cent for cuts, 20 screws
               WDS00001 a square metre of board, the joints taped WDF00008 and
               filled WDF00009 at the ADU's rates. */
            var bA = areaP * 2, nStud = 0, stLen = 0, trLen = 0, qd;
            for (qd = 0; qd < dryRuns.length; qd++) { var ns = Math.ceil(dryRuns[qd].L / 0.625) + 1; nStud += ns; stLen += ns * dryRuns[qd].H; trLen += 2 * dryRuns[qd].L; }
            var nAnc = Math.ceil(trLen / 0.6), dTape = Math.round(bA / 2.88 * 3.6 * 10) / 10, dFill = Math.round(bA * 0.45 * 10) / 10;
            API.billAdd(LAYER, 'WDD00501', stLen * 1.05);
            API.billAdd(LAYER, 'WDD00503', trLen * 1.05);
            API.billAdd(LAYER, 'FAS10075', nAnc);
            API.billAdd(LAYER, 'INS00601', areaP * 1.05);
            API.billAdd(LAYER, 'WDD24001', bA * 1.05);
            API.billAdd(LAYER, 'WDS00001', Math.round(bA * 20));
            API.billAdd(LAYER, 'WDF00008', dTape);
            API.billAdd(LAYER, 'WDF00009', dFill);
            billNote = 'drywall: ' + nStud + ' studs = ' + Math.round(stLen * 10) / 10 + ' m WDD00501 at 625, ' + Math.round(trLen * 10) / 10 + ' m track WDD00503, ' + nAnc + ' anchors FAS10075, ' +
                       Math.round(areaP * 10) / 10 + ' m2 wool INS00601, ' + Math.round(bA * 1.05 * 10) / 10 + ' m2 board WDD24001 (both faces + 5 pct), ' + Math.round(bA * 20) + ' screws WDS00001, ' +
                       dTape + ' m tape WDF00008, ' + dFill + ' kg filler WDF00009, BILLED';
          }
          ctx.log('mass  partitions ' + nP + ' built ' + _mm(PT_T) + ' thick on their own storey (' + pSaid.join(', ') + '), ' + Math.round(areaP * 10) / 10 + ' m2 -- ' + billNote);
        }
      })();
      ctx.log('mass  walls inside: ' + nBear + ' load-bearing, ' + nPart + ' partition(s)' +
              (nBear ? ' -- the load-bearing ones are laid in blocks (see the bearing line)' : ''));
      ctx.log('mass  plan on the block, module ' + _mm(md) + ' (block ' + _mm(BLOCK(_sysId()).l) + ' + head ' + _mm(BLOCK(_sysId()).head) + '), ' +
              'every run of wall a whole number of modules on its wall line' +
              ' | bar ' + _mm(STATE.footprint.w) + ' x ' + _mm(STATE.footprint.l) +
              (STATE.wing && STATE.wing.on ? (', wing ' + _mm(STATE.wing.w) + ' x ' + _mm(STATE.wing.l) + ' offset ' + _mm(STATE.wing.at || 0)) : '') +
              (nCut ? ' -- NOT all whole, see the blocks line' : ' -- every block whole') +
              (_replanNote ? (' | ' + _replanNote) : ''));
    }

    /* THE PLAN ON THE BLOCK (core-v501, step 5 of the T house hand-over):
       the bond period and the course shift the openings read, the phase of
       a wall's first head joint, the lattice the plan snaps to while it is
       dragged and before every build, and the bearing walls moved onto the
       outer wall's grid -- the T house's, moved here so a masonry product
       gets them from the block alone. The product's hooks are one-liners. */
    function masBondPeriod() {
      var B = masBlock(masSysId()), bm = B.l + B.head, sh = masMod(masWallT() + (B.head || 0), bm);
      if (!(bm > 0.05)) return { m: 0, shift: 0, n: 0, step: 0 };
      /* the courses alternate between two starts, the corner sets them, so
         there are two lattices of head joints and an opening ends whole on a
         run of blocks or a run plus that one shift (thouse-v67) */
      return { m: bm, shift: sh, n: 2, step: Math.min(sh, bm - sh) || bm };
    }
    function masCourseShift() {
      var B = masBlock(masSysId()), bm = B.l + B.head;
      if (!(bm > 0.05)) return 0;
      return masMod(masWallT() + (B.head || 0), bm);
    }
    function masWallPhase(ref, kind, wallU0) {
      var m = masModule();
      if (!(m > 0.05)) return 0;
      var u0 = (kind === 'wall' && wallU0 && wallU0[ref] != null) ? wallU0[ref] : 0;
      var ph = u0 % m;
      return (ph < 0) ? (ph + m) : ph;
    }
    function masOffs() {
      var B = masBlock(masSysId()), m = B.l + B.head;
      return { m: m, w: 0, l: 0, ww: 0, wl: 0, seg: 0 };
    }
    function masLatK(v, kind, lo, hi) {
      var O = masOffs(), m = O.m, off = O[kind];
      if (!(m > 0.01) || off == null) return null;
      var n = Math.round((v - off) / m), c = n * m + off, guard = 0;
      while (hi != null && c > hi + 1e-6 && guard++ < 500) { n--; c = n * m + off; }
      while (lo != null && c < lo - 1e-6 && guard++ < 1000) { n++; c = n * m + off; }
      if ((hi != null && c > hi + 1e-6) || (lo != null && c < lo - 1e-6)) return null;
      return Math.round(c * 10000) / 10000;
    }
    function masSegMin() { return masLatK(0.3, 'seg', 0.3, null); }
    function masLatAt(at, along, w) {
      var sm = masSegMin();
      if (sm == null) return null;
      var seg = along / 2 + at - w / 2, lo = sm, hi = along - w - sm;
      if (hi < lo - 1e-6) return null;
      var sA = masLatK(seg, 'seg', lo, hi);
      return (sA == null) ? null : Math.round((sA - along / 2 + w / 2) * 10000) / 10000;
    }
    function masPlanSnap(v, kind, lim) {
      lim = lim || {};
      if (kind === 'w') return masLatK(v, 'w', lim.min, lim.max);
      if (kind === 'l') return masLatK(v, 'l', lim.min, lim.max);
      if (kind === 'wing-l') return masLatK(v, 'wl', lim.min, lim.max);
      if (kind === 'wing-w') {
        var sm = masSegMin(), along = (lim.max != null) ? lim.max + 0.6 : null;
        var hi = (along != null && sm != null) ? Math.min(lim.max, along - 2 * sm) : lim.max;
        return masLatK(v, 'ww', lim.min, hi);
      }
      if (kind === 'wing-at') return masLatAt(v, lim.along, lim.w);
      return null;
    }
    /* THE INNER WALL SITS WHERE THE OUTER WALL'S BLOCKS LET IT (thouse-v50,
       Marek: pri zmene rozmeru bloku posunout osu nosne zdi tak, aby se vazala
       na cele bloky obvodove zdi, co nejmene prorezu).

       Where a bearing wall enters an outer wall, it takes its own width and two
       joints out of that course's grid. Land the band on a joint and one side
       of it stays whole and only the other is cut; land it anywhere else and
       both sides are cut. So the wall's line is moved to the nearest place
       where the band starts -- or ends -- on a joint, whichever leaves the
       bigger piece, and never one under 60 mm. It runs on a new block, a new
       material, a load and after the wall has been dragged. */
    function masBearSnap(mm) {
      var B = masBlock(masSysId()), m = B.l + B.head, t = B.w, j = B.head, PTs = STATE.parts || [], moved = [];
      if (!(m > 0.01)) return '';
      var Pp = poly(), qp;
      for (qp = 0; qp < PTs.length; qp++) {
        var pt = PTs[qp];
        if (!pt || !pt.bear) continue;
        var isV = ((pt.o || pt.dir) === 'v'), cc = (typeof pt.c === 'number') ? pt.c : pt.at;
        if (typeof cc !== 'number' || !isFinite(cc)) continue;
        var ends = [], qe;
        for (qe = 0; qe < Pp.length; qe++) {
          var e0 = Pp[qe], e1 = Pp[(qe + 1) % Pp.length];
          var horiz = Math.abs(e1[1] - e0[1]) < 1e-6, vert = Math.abs(e1[0] - e0[0]) < 1e-6;
          if (isV && !horiz) continue;
          if (!isV && !vert) continue;
          var lo2 = isV ? Math.min(e0[0], e1[0]) : Math.min(e0[1], e1[1]);
          var hi2 = isV ? Math.max(e0[0], e1[0]) : Math.max(e0[1], e1[1]);
          if (!(cc > lo2 + t / 2 && cc < hi2 - t / 2)) continue;
          var dir = isV ? ((e1[0] - e0[0]) >= 0 ? 1 : -1) : ((e1[1] - e0[1]) >= 0 ? 1 : -1);
          ends.push({ o: isV ? e0[0] : e0[1], dir: dir });
        }
        if (!ends.length) continue;
        var u0 = t / 2 + j, band = t + 2 * j, best = null, qq, kk, form;
        for (qq = 0; qq < ends.length; qq++) {
          for (form = 0; form < 2; form++) {
            var base = ends[qq].o + ends[qq].dir * (u0 + (form === 0 ? (t / 2 + j) : (-t / 2 - j)));
            kk = Math.round((cc - base) / (ends[qq].dir * m));
            var cand = base + ends[qq].dir * kk * m, qe2, score = 0, minCut = 9, nCuts = 0;
            for (qe2 = 0; qe2 < ends.length; qe2++) {
              var s0 = masMod(ends[qe2].dir * (cand - ends[qe2].o) - u0 - t / 2 - j, m);
              var e2 = masMod(s0 + band, m), left = s0, right = (e2 < 0.002) ? 0 : (m - e2);
              if (left > 0.002) { nCuts++; minCut = Math.min(minCut, left); }
              if (right > 0.002) { nCuts++; minCut = Math.min(minCut, right); }
            }
            score = nCuts * 10 + ((minCut < 0.06) ? 6 : 0) + Math.abs(cand - cc);
            if (!best || score < best.score - 1e-9) best = { c: cand, score: score, cuts: nCuts, min: minCut };
          }
        }
        if (best && Math.abs(best.c - cc) > 0.0005) {
          moved.push('#' + (pt.id || qp) + ' ' + mm(cc) + ' -> ' + mm(best.c) + ' (' + best.cuts + ' cut at the junctions' +
                     (best.min < 9 ? (', smallest ' + mm(best.min)) : '') + ')');
          pt.c = Math.round(best.c * 10000) / 10000;
          pt.at = pt.c;
        }
      }
      return moved.length ? ('bearing walls moved onto the outer grid: ' + moved.join(', ')) : '';
    }
    /* the plan onto the block before every build (the T house's beforeBuild
       of thouse-v39 .. v45): the bar's width and depth, the wing's width,
       length and offset, then the bearing walls; the note says what moved */
    function masReplan(LIMITS, mm) {
      var _replanNote = '';
      var m = masModule(), F = STATE.footprint, WG = STATE.wing, ch = [], v;
      if (!(m > 0.01)) return '';
      v = masLatK(F.w, 'w', LIMITS.minW, LIMITS.maxW);
      if (v != null && Math.abs(v - F.w) > 0.0005) { ch.push('bar ' + mm(F.w) + ' -> ' + mm(v)); F.w = v; }
      var wingOn = !!(WG && WG.on);
      var maxLBar = wingOn ? Math.max(LIMITS.minL, LIMITS.maxL - WG.l) : LIMITS.maxL;
      v = masLatK(F.l, 'l', LIMITS.minL, maxLBar);
      if (v != null && Math.abs(v - F.l) > 0.0005) { ch.push('depth ' + mm(F.l) + ' -> ' + mm(v)); F.l = v; }
      if (wingOn) {
        var along = F.w, sm = masSegMin(), wHi = Math.min(along - 0.6, sm != null ? along - 2 * sm : along - 0.6);
        /* the width and the offset are chosen together: of the lattice widths
           next to the old one, the one that keeps the wing where it was -- a
           centred T stays centred, which only every other width allows */
        var at0 = WG.at || 0, w0 = WG.w, v0 = masLatK(w0, 'ww', 2.0, wHi), bestW = null, bestA = null, bestC = 1e9, q2;
        if (v0 != null) {
          var cands = [v0, v0 - m, v0 + m];
          for (q2 = 0; q2 < cands.length; q2++) {
            var wc = Math.round(cands[q2] * 10000) / 10000;
            if (wc < 2.0 - 1e-6 || wc > wHi + 1e-6) continue;
            var ac = masLatAt(at0, along, wc);
            if (ac == null) continue;
            var cost = Math.abs(wc - w0) + 3 * Math.abs(ac - at0);
            if (cost < bestC - 1e-9) { bestC = cost; bestW = wc; bestA = ac; }
          }
        }
        if (bestW != null) {
          if (Math.abs(bestW - w0) > 0.0005) { ch.push('wing width ' + mm(w0) + ' -> ' + mm(bestW)); WG.w = bestW; }
          if (Math.abs(bestA - at0) > 0.0005) { ch.push('wing offset ' + mm(at0) + ' -> ' + mm(bestA)); WG.at = bestA; }
        }
        v = masLatK(WG.l, 'wl', 1.5, Math.max(1.5, LIMITS.maxL - F.l));
        if (v != null && Math.abs(v - WG.l) > 0.0005) { ch.push('wing length ' + mm(WG.l) + ' -> ' + mm(v)); WG.l = v; }
      }
      var bs = '';
      try { bs = masBearSnap(mm); } catch (eBS) { bs = 'bearing snap threw: ' + (eBS && eBS.message); }
      if (ch.length) { STATE.polygon = null; _replanNote = 'plan moved onto the block: ' + ch.join(', '); }
      else _replanNote = '';
      if (bs) _replanNote = (_replanNote ? (_replanNote + ' | ') : '') + bs;
      return _replanNote;
    }
    var masonry = {
      build: masBuild,
      bondPeriod: masBondPeriod, courseShift: masCourseShift, wallPhase: masWallPhase, planSnap: masPlanSnap, bearSnap: masBearSnap, replan: masReplan, offs: masOffs,
      planStep: function () { var m = masModule(); return (m > 0.05) ? m : 0; }, courseH: function () { var c = masCourse(); return (c > 0.02) ? c : 0; },
      systems: function () { return MAS_SYSTEMS; },
      sysId: masSysId, sysSaved: masSysSaved, sys: masSys,
      blockDef: function (id) { return MAS_BLOCK_DEF[id] || null; }, blockLim: masBlockLim, blockClamp: masBlockClamp, block: masBlock,
      bondWidths: masBondWidths, bondLengths: masBondLengths, nearest: masNearest,
      wallT: masWallT, module: masModule, course: masCourse,
      clayGeo: masClayGeo, cmuGeo: masCmuGeo, epsBlock: masEpsBlock,
      chbTemplate: masChbTemplate, layRun: masLayRun, chbMesh: masChbMesh, boxBatch: masBoxBatch
    };

    /* THE COLOR STEP, THE SAME IN EVERY PRODUCT (core-v313).

       The machinery was written out three times, in the ADU, the sauna and the
       shed, and the three copies were the same line for line: where a choice is
       kept, how a slot falls back to the first colour of its own list, the
       rows of dots with the ring round the chosen one. What differs between
       products is only WHICH slots there are -- the shed has panel siding and
       no blinds, the sauna has no facade render -- and one grey. So the
       machinery and the palette are here, and a product hands over its slots
       once with colorSlots(list, paletteChanges) and nothing else
       (Marek: colors taky -- do UI).

       A slot is { key, labelKey, def, pal: [...] or function, when?, label2Key?,
       def2? }, exactly the shape the products already wrote. The choice is kept
       in STATE.product.colors, where it always was, so saved projects open with
       the colours they were saved with. */
    var COLOR_PALETTE = {
      anthracite: 0x3A3D40, graphite: 0x5A5D60, terracotta: 0xA5553B, moss: 0x5C6B4E,
      cedar: 0xB98B57, oak: 0xC8A46E, larch: 0xD3B07A, white: 0xF2F0EA, cream: 0xEDE6D3,
      sand: 0xCFC3A8, stone: 0x9EA2A0, charcoal: 0x2B2B2B, ivory: 0xF4EFE1, greige: 0xC9C4B8,
      vinylOak: 0xC9AA78, vinylGrey: 0x9A9691, walnut: 0x7A5A3A, zinc: 0xA7ABAF, copper: 0xB87333,
      lightGreen: 0xD7E8DC, lightBlue: 0xD6E4EE, pink: 0xF1D9DC,
      /* the light grey both the timber and the joinery were missing (Marek) */
      lightGrey: 0x8E918E,
      /* a copper roof, new and weathered (Marek) */
      patina: 0x6E9C8F
    };
    var _colorSlotList = [];
    function colorSlots(list, paletteChanges) {
      _colorSlotList = list || [];
      if (paletteChanges) { for (var k in paletteChanges) if (paletteChanges.hasOwnProperty(k)) COLOR_PALETTE[k] = paletteChanges[k]; }
      _log('colors  ' + _colorSlotList.length + ' slot(s): ' + _colorSlotList.map(function (s) { return s.key; }).join(', ') +
           (paletteChanges ? (' | palette changed: ' + Object.keys(paletteChanges).join(', ')) : ''));
    }
    function _colorChoices() {
      var P = STATE.product = STATE.product || {};
      return P.colors = P.colors || {};
    }
    function _colorPalOf(s) { return (typeof s.pal === 'function') ? s.pal() : s.pal; }
    function _colorSlot(slot) {
      for (var i = 0; i < _colorSlotList.length; i++) if (_colorSlotList[i].key === slot) return _colorSlotList[i];
      return null;
    }
    function colorSlotKey(slot) {
      var s = _colorSlot(slot);
      if (!s) return null;
      var C = _colorChoices(), pal = _colorPalOf(s);
      return (C[slot] && pal.indexOf(C[slot]) >= 0) ? C[slot] : pal[0];
    }
    function colorSlotOf(slot) {
      var k = colorSlotKey(slot);
      return k ? COLOR_PALETTE[k] : 0x888888;
    }
    function colorSlotNext(slot) {
      var s = _colorSlot(slot);
      if (!s) return;
      var pal = _colorPalOf(s), at = pal.indexOf(colorSlotKey(slot));
      _colorChoices()[slot] = pal[(at + 1) % pal.length];
      savePref();
      _log('colour ' + slot + ' -> ' + _colorChoices()[slot]);
      rebuild();
    }
    function colorSlotSet(slot, key) {
      if (COLOR_PALETTE[key] == null) return;
      _colorChoices()[slot] = key;
      savePref();
      _log('colour ' + slot + ' -> ' + key);
      rebuild();
    }
    function colorHex(v) { return '#' + ('000000' + v.toString(16)).slice(-6); }
    function colorsStepDef() {
      return {
        key: 'colors', mode: 3, view: '3d',
        label: _t('toggle_colors', 'Color'),
        hint: _t('texture_colour', 'Texture / Colour')
      };
    }
    /* THE COLOURS PANE: the drawer scrolls, so nothing folds. Every slot is its
       name and, under it, all of its dots open at once; the chosen one wears a
       thin ring with a clear gap to the dot. Plain text names, no boxes, no
       thumbnail of the choice -- the ring says it (Marek). Carried over from the
       products unchanged. */
    function colorsPanel(pane) {
      if (!pane) return;
      pane.innerHTML = '';
      var wrap = document.createElement('div');
      wrap.style.cssText = 'padding:6px 2px 12px;display:flex;flex-direction:column;gap:14px;';
      _colorSlotList.forEach(function (s) {
        if (s.when && !s.when()) return;
        var block = document.createElement('div');
        var name = document.createElement('div');
        name.textContent = _t(s.labelKey, s.def) + (s.label2Key ? (' / ' + _t(s.label2Key, s.def2)) : '');
        /* the same label the kit puts on every row: small caps, light, muted */
        name.style.cssText = 'font:700 10px/1.2 Montserrat,sans-serif;letter-spacing:.08em;' +
          'text-transform:uppercase;color:#9CA3AF;margin:0 0 7px;';
        var dots = document.createElement('div');
        dots.style.cssText = 'display:flex;flex-wrap:wrap;gap:12px;';
        var cur = colorSlotKey(s.key), i, palS = _colorPalOf(s);
        for (i = 0; i < palS.length; i++) {
          (function (k) {
            var b = document.createElement('button');
            b.type = 'button';
            b.title = k;
            var on = (k === cur);
            /* the ring: a box-shadow outside a clear gap, so it reads as a thin
               line standing off the dot */
            b.style.cssText = 'width:26px;height:26px;border-radius:50%;cursor:pointer;padding:0;' +
              'background:' + colorHex(COLOR_PALETTE[k]) + ';border:1px solid rgba(0,0,0,0.14);' +
              (on ? 'box-shadow:0 0 0 3px #fff, 0 0 0 4px #2C2D2E;' : '');
            b.addEventListener('click', function () { colorSlotSet(s.key, k); colorsPanel(pane); });
            dots.appendChild(b);
          })(palS[i]);
        }
        block.appendChild(name);
        block.appendChild(dots);
        wrap.appendChild(block);
      });
      pane.appendChild(wrap);
    }

    /* THE DOORS & WINDOWS STEP, THE SAME IN EVERY PRODUCT (core-v314).

       The tiles were written out three times and the ADU's and the sauna's were
       the same line for line; the shed's differed only in where a tile got its
       fallback word. What a product really decides is WHICH kinds it sells --
       the ADU four, the sauna three, the shed two -- so that is the one thing
       it hands over (Marek: doors & windows -- jen se meni nabidka oken a dveri
       dle projektu).

       openingsGroups({ kinds, labelOf? }):
         kinds    the kinds on offer, in the order the tiles stand
         labelOf  the fallback word for a kind when the theme has no key for it
                  (default: the shared kit's own label for that group)
       Each kind opens the product's own picker (its KIT), so what is IN a kind
       is still the product's catalogue. */
    var OP_TILE_KEY = {
      window: 'windows',
      door:   'mc_op_exterior_door',
      french: 'mc_op_french_door',
      garage: 'mc_op_garage_door',
      roof:   'roof_windows',
      serve:  'mc_op_serving_window'   /* core-v786 */
    };
    function openingsStepDef() {
      return {
        key: 'openings', mode: 4, view: '3d',
        label: _t('wiz_openings', 'DOORS & WINDOWS'),
        hint: _uiStr('step_openings_h', 'Pick one, then tap a wall')
      };
    }
    function openingsGroups(opts) {
      opts = opts || {};
      var kinds = opts.kinds || ['window', 'door'];
      var ICONS = { window: ICO.window, door: ICO.door, french: ICO.french, garage: ICO.garage, roof: ICO.roof || ICO.window, serve: ICO.window };
      /* THE ICONS ARE THE CATEGORIES; THE DRAWER IS THE CHOICE. A kind opens the
         shared picker -- a sheet listing that kind's units, each drawn at its own
         proportions -- because a 2400 x 600 ribbon and a 600 x 2100 light are the
         same two numbers in a different order and reading them off a list is
         work. Every caption goes through the translator: the key first, the
         kit's word only as the fallback. */
      var cats = kinds.map(function (k) {
        var U2 = _UI();
        var G2 = (U2 && U2.openingGroup) ? U2.openingGroup(k) : null;
        var def = (typeof opts.labelOf === 'function') ? (opts.labelOf(k) || k) : (G2 ? G2.label : k);
        var label = _t(OP_TILE_KEY[k] || ('op_' + k), def);
        return { kind: k, label: label, svg: ICONS[k],
                 on: function () { var a = armedOpening(); return !!(a && a.kind === k); },
                 tap: function () {
                   var K = (typeof __PROD.KIT === 'function') ? __PROD.KIT() : null;
                   if (!K || !K.openPicker) { _log('picker: shared kit missing, needs ui-v94 or newer'); return; }
                   K.openPicker(k, label);
                 } };
      });
      /* ANYWHERE IS GONE (core-v323): a unit is placed by tapping the wall it goes
         in, and nothing else (Marek). */
      /* THE PLAIN HOLE BELONGS HERE, WITH THE DOORS AND THE WINDOWS
         (core-v353): the tile arms the placing and the next tap on the model
         drops it in the wall, sized on the blocks; it is edited on the wall
         like a window and its bin takes it away. */
      if (opts.holes) cats = cats.concat([{ label: _uiStr('op_hole', 'Hole'), svg: ICO.wall,
        on: function () { return !!_holeMode; },
        tap: function () { holeMode(!_holeMode); } }]);
      return [
        { key: 'openings', label: _t('grp_openings', 'What goes in the wall'), items: cats },
        { key: 'opedit', label: _t('place', 'Place'), items: [
          /* EDIT IS A STATE, SO IT LOOKS LIKE ONE -- lit while it is on, which is
             what the tile colour is for everywhere else in this guide. */
          /* the bin of this step takes the selected opening away (core-v355) */
          { label: _t('edit', 'Edit'), svg: ICO.brush,
            on: function () { return opEdit(); },
            tap: function () { opEdit(!opEdit()); } }
        ] }
      ];
    }

    /* THE FLOORPLAN STEP, THE SAME IN EVERY PRODUCT (core-v315).

       The ADU's, the sauna's and the shed's plan tiles were the same line for
       line, and so were the five small functions behind them -- trace, the door
       tool, the cut tool and the two that tidy up after them. The one real
       difference was the sauna's own row for its benches, standing between the
       plan tools and the walls. So the tools and their tiles are here, and a
       product hands over only rows that are its own (Marek: dlazdice s klici
       locales -- do UI).

       layoutGroups({ before?, middle?, after? }):
         before  rows above the plan tools (the T house: material and wing)
         middle  rows between the plan tools and the walls (the sauna: benches)
         after   rows under the walls */
    function _planTraceDraw() {
      try { drawPlan(); } catch (e1) {}
      _traceIcon();
    }
    /* THE BUTTON SAYS WHICH OF THE THREE IT IS, and it is tiny's paint, copied.
       The asset offers syncTraceButton, but it swaps in a CROSS -- and he wants
       the FREEZE glyph the tiny house uses, because that is what the button does
       once a picture is in: it puts the picture to sleep. */
    function planTraceTap() {
      var F = global.FloorplanLib;
      if (!F || !F.trace) { _log('floorplan: this asset has no trace'); return; }
      if (F.trace.has()) {
        F.trace.armed = !F.trace.armed;
        _traceRepaint();
        _traceIcon();
        _log('trace ' + (F.trace.armed ? 'armed -- handles live' : 'locked -- it is scenery'));
        return;
      }
      F.trace.pick(function () {
        /* the picture arrives awake, and the button has to say so */
        var r = poly(), bx0 = 1e9, bx1 = -1e9, bz0 = 1e9, bz1 = -1e9, q;
        for (q = 0; q < r.length; q++) {
          bx0 = Math.min(bx0, r[q][0]); bx1 = Math.max(bx1, r[q][0]);
          bz0 = Math.min(bz0, r[q][1]); bz1 = Math.max(bz1, r[q][1]);
        }
        F.trace.cx = (bx0 + bx1) / 2;
        F.trace.cz = (bz0 + bz1) / 2;
        F.trace.w = (bx1 - bx0) * 1.2;
        F.trace.armed = true;
        rebuild();
        _planTraceDraw();
      });
    }
    function _planToolsChanged(name) {
      var S = STATE;
      savePref();
      rebuild();
      try { drawPlan(); } catch (e1) {}
      if (_wiz && _wiz.refresh) _wiz.refresh();
      _log('tool ' + name + ' ' + ((name === 'cut' ? S.cutsOn : S.doorTool) ? 'ON' : 'off') +
           '  | cut=' + (S.cutsOn ? 'on' : 'off') + ' door=' + (S.doorTool ? 'on' : 'off'));
    }
    /* the two plan tools: each a switch, never both -- a tap cannot be a cut and
       a door at the same time */
    function planCutTap() {
      var S = STATE;
      S.cutsOn = !S.cutsOn;
      if (S.cutsOn) { S.doorTool = false; if (fitEdit()) fitEdit(false, 'the scissors'); }
      _planToolsChanged('cut');
    }
    function planMirrorTap() {
      var IU = _cmdUser();
      if (!IU || typeof IU.mirror !== 'function') { _log('plan     mirror: this floorplan asset has none (floorplan v199 and up) (core-v592)'); return; }
      _cmd.lastSt = '';
      _cmd.asked = true;
      _cmd.bwArm = false;
      var how = false;
      try { how = IU.mirror(_cmdOnPlan); } catch (eM) { how = false; _log('plan     mirror THREW ' + ((eM && eM.message) || eM) + ' (core-v592)'); }
      /* the project turned over in place: no plan comes to be written */
      if (how !== 'plan') _cmd.asked = false;
      if (how === 'state') { try { drawPlan(); } catch (eD) {} if (_wiz && _wiz.refresh) _wiz.refresh(); }
      _log('plan     mirror tapped -- ' + (how === 'state' ? 'the project turned over left to right' : (how ? 'the plan turned over, being built and written' : 'NOT started (no plan to turn over)')) + ' (core-v592)');
    }
    function planDoorTap() {
      var S = STATE;
      S.doorTool = !S.doorTool;
      if (S.doorTool) { S.cutsOn = false; if (fitEdit()) fitEdit(false, 'the door tool'); }
      _planToolsChanged('door');
    }
    function layoutStepDef() {
      return {
        key: 'layout', mode: 3, view: '2d',
        label: _t('wiz_layout', 'FLOORPLAN'),
        hint: _uiStr('step_layout_h', 'Size, walls, doors and rooms')
      };
    }
    function layoutGroups(opts) {
      opts = opts || {};
      var S = STATE;
      var plan = { label: _t('grp_plan', 'Plan'), items: [
        /* THE TILE CARRIES A TITLE AS WELL AS A LABEL: the panel sets the
           button's title attribute only when the item supplies one, and the
           trace glyph is found by it. The glyph is chosen here too, not only
           repainted afterwards -- the panel is rebuilt on every tap, so a rebuilt
           tile has to come out already right. */
        { label: _traceTitle(), title: _traceTitle(), act: true,
          svg: (_trace() && _trace().has()) ? ICO_FREEZE_G : ICO.upload,
          on: function () { var F = global.FloorplanLib; return !!(F && F.trace && F.trace.has()); },
          tap: planTraceTap },
        /* the fitouts have no tile: a room zoomed into shows its red plus,
           which opens the drawer for that room (Marek, core-v541) */
        { label: _t('doors', 'Doors'), svg: ICO.door, fn: true,
          on: function () { return !!S.doorTool; }, tap: planDoorTap },
        { label: _uiStr('cuts', 'Cut handles'), svg: ICO.scissors, fn: true,
          on: function () { return !!S.cutsOn; }, tap: planCutTap },
        /* MIRROR (core-v592, floorplan v199, Marek: do edit floorplan udelat
           tlacitko zrcadleni planu): an action, not a switch -- the plan as
           built turned over left to right, built again and written, one Undo */
        { label: _t('mirror', 'Mirror'), svg: ICO.mirror, act: true,
          on: function () { return false; }, tap: planMirrorTap }
      ] };
      /* SUG: THE SUGGESTED FURNITURE ON AND OFF (core-v541). Only while the
         project has a piece the plan reader suggested (sug) or one put aside */
      if (_sugCount() > 0) {
        plan.items.push({ label: _t('suggested', 'Suggested'), svg: _SUG_SVG,
          on: function () { return !(STATE.product && STATE.product.sugOff); }, tap: sugToggle });
      }
      /* THE WALL BUTTONS ARE ACTIONS, NOT SWITCHES. They divide the biggest
         room straight away, the way tiny's do. There is nothing to light up
         because nothing stays armed. */
      var walls = { label: _t('walls', 'Walls'), items: [
        { label: _uiStr('wall_v', 'Vertical wall'), svg: ICO.wallV, act: true,
          on: function () { return false; },
          tap: function () { addPartition('v'); } },
        { label: _uiStr('wall_h', 'Horizontal wall'), svg: ICO.wallH, act: true,
          on: function () { return false; },
          tap: function () { addPartition('h'); } }
      ] };
      /* LOAD-BEARING WALLS (core-v346): the same two tiles, drawn heavy, for a
         product that asks for them -- a masonry house. They add a wall of the
         house's own masonry across the biggest room, marked bear: true, which
         the product lays in blocks and bonds; to the plan it is a wall like any
         other until the plan draws it at its thickness. Under the structure
         label, so the two pairs read apart. */
      /* AN OPENING IN ANY WALL (core-v348). One tile on the Floorplan step
         drops an opening into the longest wall that has room for it -- an outer
         wall, a load-bearing wall or a partition alike -- and it is kept in
         STATE.holes as the wall it belongs to and how far along it starts and
         ends. In a masonry wall the width and the position step by the block's
         module, through the product's own plan step, so the blocks around it
         stay whole. The drag points on its two ends, and the hole cut through
         the blocks with a lintel over it, are the next steps. */
      /* THE STOREY CARDS COME FIRST (core-v401, Marek: prepinac floorplanu
         ve step floorplan, jako prvni linie pas karet s cislovanim pater). A
         product that has more than one storey, or asks for the row, gets a
         card per storey at the top of the Floorplan step; the lit one is the
         storey the plan shows and edits. STATE.planStorey remembers it with
         the project. */
      /* HOW STRONGLY THE PICTURE SHOWS (core-v539, Marek: posuvnik opacity
         vlozeneho pudorysu do draweru, objevi se jen kdyz je obrazek vlozeny).
         Under the plan tools, only while a picture lies under the plan, 0 to
         100 % -- at 0 the picture is gone and the row stays to bring it back.
         The asset's own slider that hung off the picture is hidden from now on:
         one control, in the drawer. */
      /* core-v562 (Marek: slider s picture fp opacity dej pod otevreny banner
         pudorysu, na misto hlasky o vlastnictvi, bez popisu -- nebude v
         draweru): the row is no longer built; the slider lives under the bar */
      var picRow = [];
      var TRp = _trace();
      if (false && TRp && TRp.has && TRp.has()) {
        _traceSliderHide();
        picRow.push({ label: _t('floorplan_opacity', 'Floorplan opacity'), range: {
          min: 0, max: 100, step: 5,
          get: function () { var T2 = _trace(); return T2 ? Math.round((+T2.opacity || 0) * 100) : 0; },
          set: function (v) {
            var T3 = _trace(); if (!T3) return;
            T3.opacity = Math.max(0, Math.min(1, (+v || 0) / 100));
            _traceRepaint();
          },
          fmt: function (v) { return Math.round(v) + ' %'; }
        } });
      }
      var groups = [plan].concat(picRow, opts.middle || [], [walls]);
      if (opts.storeys || storeys().length > 1) {
        var nS = Math.max(storeys().length, opts.storeys | 0), cards = [], si;
        for (si = 0; si < nS; si++) (function (k) {
          cards.push({ label: String(k + 1), svg: ICO.floor, small: true,
            on: function () { return ((STATE.planStorey | 0) === k); },
            tap: function () {
              STATE.planStorey = k;
              _log('plan     showing storey ' + (k + 1));
              try { if (typeof opts.onStorey === 'function') opts.onStorey(k); } catch (eOS) {}
              savePref(); rebuild(); drawPlan();
              /* core-v472 (Marek: ukazatel vysky patra jen ve step Roof &
                 Base): the storey card no longer brings the height slider up
                 on the plan */
            } });
        })(si);
        groups.unshift({ label: _t('storeys', 'Storeys'), items: cards });
      }
      if (opts.holes) {
        groups.push({ label: _t('grp_openings', 'Openings'), items: [
          { label: _uiStr('op_opening', 'Opening'), svg: ICO.door, fn: true,
            on: function () { return !!_holeMode; },
            tap: function () { holeMode(!_holeMode); } }
        ] });
      }
      if (opts.bearing) {
        groups.push({ label: _t('bearing_wall', 'Load-bearing wall'), items: [
          { label: _uiStr('wall_v', 'Vertical wall'), svg: ICO.bearV, act: true,
            on: function () { return false; },
            tap: function () { addPartition('v', true); } },
          { label: _uiStr('wall_h', 'Horizontal wall'), svg: ICO.bearH, act: true,
            on: function () { return false; },
            tap: function () { addPartition('h', true); } }
        ] });
      }
      return [].concat(opts.before || [], groups, opts.after || []);
    }

    /* THE RAIL AND UNDO, THE SAME IN EVERY PRODUCT (core-v316).

       The rail buttons on the right -- 2D/3D, units, the view stages, the section
       tool, AR, the person, the assembly walk -- and the Undo button were wired
       by each product in a function called bindUI, and the three copies were the
       same line for line. A product that did not copy it -- the T house -- had
       the buttons on the page and none of them did anything. So they are wired
       here, once, by railBind(), and a product only calls it.

       UNDO keeps a history of whole states. A state is pushed when the building
       has loaded and after every change that pushes one -- a drag pushes on its
       own, from the handles' commit. Undo steps back one state; with one left it
       goes back to that one, the state the page loaded with; with none it resets
       to the default house. With no pushes but the first, which is where the
       ADU, the sauna and the shed stood, that is exactly what their Undo did. */
    var _undoStack = [], _undoBtn = null;
    function undoPush() {
      try {
        var j = JSON.stringify(payload());
        /* a state that is the last one already is not a step: a tile that
           opened a drawer and changed nothing must not cost an Undo */
        if (_undoStack.length && _undoStack[_undoStack.length - 1] === j) return;
        _undoStack.push(j);
        if (_undoStack.length > 30) _undoStack.shift();
      } catch (e) {}
      if (_undoBtn) _undoBtn.refresh();
    }
    function _undoApply(json) {
      var p = JSON.parse(json), k;
      for (k in p) { if (k !== 'v' && p.hasOwnProperty(k) && STATE.hasOwnProperty(k)) STATE[k] = p[k]; }
      savePref();
      rebuild();
    }
    function undoPop() {
      try {
        if (_undoStack.length >= 2) { _undoStack.pop(); _undoApply(_undoStack[_undoStack.length - 1]); }
        else if (_undoStack.length === 1) { _undoApply(_undoStack.pop()); }
        else { reset(); }
        _log('undo -> ' + _undoStack.length + ' state(s) left');
      } catch (e) { _log('undo threw: ' + (e && e.message)); }
      railCounters();
      if (_undoBtn) _undoBtn.refresh();
    }
    function undoState() { return { steps: _undoStack.length, bound: !!_undoBtn }; }

    function _railBtn(id, fn) {
      var el = document.getElementById(id);
      if (!el) { _log('rail: #' + id + ' not in the page'); return; }
      if (el.__cfBound) return;
      el.__cfBound = true;
      el.addEventListener('click', function () { try { fn(el); } catch (e) { _log('rail ' + id + ' threw: ' + (e && e.message)); } });
    }
    function railCounters() {
      var a = $('cf-stat-viz');
      if (a) a.textContent = String((STATE.vizStage | 0) + 1);
      /* THE ASSEMBLY COUNTER COUNTS LAYERS, so it is honest from the first
         version: 0/0 means nothing is built yet, which is exactly true while the
         model is still guider mass. */
      var b = $('cf-stat-assembly');
      if (b) b.textContent = '0/' + layers().length;
    }
    function railBind(opts) {
      opts = opts || {};
      (opts.hide || []).forEach(function (id) { var el = $(id); if (el) el.classList.add('cf-hide'); });
      /* THE RAIL'S 2D / 3D BUTTON IS THE BAR'S, WHILE NO DRAWER IS OPEN
         (core-v556, Marek: po nacteni floorplanu ma zustat videt floorplan,
         prepinani mezi 2d a 3d ma byt pomoci tohoto tlacitka). With the bar on
         the canvas, 2D is the plan the bar holds -- drawn, the pieces and the
         plus working -- and 3D is the whole model, rebuilt and framed. It only
         set the view before, so the plan's own stage was left behind: back in
         2D nothing drew the plan, and the model under it stayed veiled. */
      _railBtn('cf-btn-view', function () {
        var to2d = STATE.view !== '2d';
        var drawerOut = false;
        try { drawerOut = !!(typeof wizardOpen === 'function' && wizardOpen()); } catch (eWo) { drawerOut = false; }
        if (typeof _cmd !== 'undefined' && _cmd && _cmd.el && !drawerOut) {
          /* the plan to look at, never the plan strip (core-v600) */
          _cmdPlanView(to2d, 'the 2D / 3D button on the rail', true);
          if (to2d && STATE.view !== '2d') setView('2d');
          if (!to2d && STATE.view === '2d') setView('3d');
          return;
        }
        setView(to2d ? '2d' : '3d');
      });
      _railBtn('cf-btn-unit', function () { toggleUnits(); railCounters(); });
      _railBtn('cf-btn-viz', function (el) {
        var v = nextViz();
        railCounters();
        try {
          el.setAttribute('aria-pressed', STATE.vizStage ? 'true' : 'false');
          el.title = _t('viz_prefix', 'Visualisation:') + ' ' + v.label;
          el.blur();   /* focus styling must not outlive the tap */
        } catch (e) {}
      });
      /* THE SECTION TOOL IS THE KIT'S (ui-v113): the rail only hands it the
         stage, the button and its units, and calls toggle */
      _railBtn('cf-btn-colors', function () {
        var U = _UI();
        if (!_cutTool && U && U.cutPlanes) {
          _cutTool = U.cutPlanes({
            stage: _stage, rootId: 'cf-root', buttonId: 'cf-btn-colors', unitBtnId: 'cf-btn-unit',
            fmtLen: fmtLen, log: _log, controls: _navControls, view: function () { return STATE.view; }
          });
        }
        if (_cutTool) _cutTool.toggle(); else _log('cut planes: ui-v113 or newer needed');
        try { var elB = $('cf-btn-colors'); if (elB) elB.blur(); } catch (eB) {}
      });
      _railBtn('cf-btn-ar', function () { arOpen(); });
      _railBtn('cf-btn-body', function () { toggleBody(); });
      /* THE ASSEMBLY WALK: one more step of the building per tap, flown in,
         named; past the last one the whole building is back (Marek) */
      _railBtn('cf-btn-assembly', function () { asmNext(); });
      try {
        var U2 = global.ConfiguratorUI;
        if (U2 && U2.undoButton && !_undoBtn) {
          _undoBtn = U2.undoButton({
            canUndo: function () { return _undoStack.length > 0; },
            onUndo: undoPop,
            onReset: function () { reset(); _undoStack = []; railCounters(); },
            undoLabel: _t('undo', 'Undo'),
            resetLabel: _t('reset', 'Reset to default')
          });
        }
      } catch (e) {}
      railCounters();
      _log('rail    bound | undo ' + (_undoBtn ? 'on' : 'MISSING -- ConfiguratorUI.undoButton not found'));
    }

    /* A STEP, ITS PANEL, AND THE WIZARD MOUNTED (core-v317).

       Three more pieces every product wrote the same way: the helper that turns
       a key, a label and a mode into a step; the panel that draws a step's tiles
       and seats the sliders under them; and the mounting of the wizard with
       what happens on entering a step and on closing the drawer. The T house
       had to copy all three by hand to get a drawer at all, which is the sign
       they were the core's.

       stepDef(key, label, hint, mode, view, extra)  a step; extra overrides
       stepPanel(pane, key)   the product's tiles for the key, through its KIT,
                              then the sliders the key seats (roof: height and
                              pitch; exterior: the slat gap)
       wizardMount(steps, opts)  the wizard, Edit as its title; opts.holdStep()
                              returning true keeps a step from rebuilding (the
                              shed's wall sample); opts.onExit runs before the
                              core's own exit work

       AND EVERY TILE TAP GOES INTO THE HISTORY: the panel listens for taps on
       its own pane and pushes the state after each; undoPush() drops a state
       that is the same as the last, so a tap that only opened a drawer costs
       nothing. Before this a tile's change could only be undone together with
       the drag before it. */
    function stepDef(key, label, hint, mode, view, extra) {
      var st = {
        key: key, mode: mode, view: view, label: label, hint: hint,
        panel: function (pane) { stepPanel(pane, key); },
        /* the built-in icon row stays empty: the drawer has the rows below it */
        icons: function () { return []; }
      };
      if (extra) { for (var k in extra) if (extra.hasOwnProperty(k)) st[k] = extra[k]; }
      return st;
    }
    function stepPanel(pane, key) {
      if (!pane) return;
      var K = (typeof __PROD.KIT === 'function') ? __PROD.KIT() : null;
      if (!K) { _log('panel: shared kit missing, needs ui-v94 or newer'); return; }
      K.panel(pane, key);
      if (!pane.__cfUndoBound) {
        pane.__cfUndoBound = true;
        pane.addEventListener('click', function () { global.setTimeout(undoPush, 0); });
      }
      /* THE SLIDER GOES UNDER ITS OWN TILE. K.panel has just laid the rows out,
         so appending here puts it after them (Marek). */
      if (key === 'exterior') {
        try { seatPostWallGap(pane, _t); }
        catch (ePW) { _log('post wall slider: ' + (ePW && ePW.message)); }
      }
      if (key === 'roof') {
        try {
          if (!_heightSl) _initHeight();
          if (!_pitchSl) _initPitch();
          if (!_floorSl && _floorRange()) _initFloor();
          var host = document.createElement('div');
          host.id = 'cf-roof-sliders';
          host.style.cssText = 'display:flex;flex-direction:column;gap:10px;padding:18px 12px 8px;width:100%;box-sizing:border-box;';
          function seat(sl, titleKey, def) {
            if (!sl || !sl.el) return;
            var wrap = document.createElement('div');
            wrap.style.cssText = 'position:relative;padding-top:4px;padding-bottom:26px;';
            var tt = document.createElement('div');
            tt.style.cssText = 'font:700 10px Montserrat,sans-serif;letter-spacing:.08em;text-transform:uppercase;color:#6B7280;margin-bottom:6px;';
            tt.textContent = _t(titleKey, def);
            wrap.appendChild(tt);
            var el = sl.el;
            el.style.position = 'relative'; el.style.left = 'auto'; el.style.right = 'auto'; el.style.bottom = 'auto'; el.style.top = 'auto';
            el.style.transform = 'none'; el.style.width = '100%'; el.style.maxWidth = 'none'; el.style.margin = '0'; el.style.boxSizing = 'border-box';
            try { var inp2 = el.querySelector('input'); if (inp2) { inp2.style.width = '100%'; inp2.style.maxWidth = 'none'; } } catch (eI) {}
            try { if (sl.input) { sl.input.style.width = '100%'; sl.input.style.maxWidth = 'none'; } } catch (eI2) {}
            wrap.appendChild(el);
            host.appendChild(wrap);
            sl.show();
          }
          /* bottom up, as the house is built: the floor, the walls, the roof */
          if (_floorSl && _floorRange()) { _floorSl.set({ min: _floorRange().min, max: _floorRange().max, value: _floorNow() }); seat(_floorSl, 'floor_label', 'Floor'); }
          /* A PRODUCT WITH NO WALL TO RAISE (core-v506, Marek: u A-frame
             wall height nedava smysl): __PROD._noWallHeight leaves the slider
             out of the drawer and off the canvas */
          if (_noWallH()) { try { _heightSl.hide(); } catch (eHh) {} _log('roof     wall height slider left out -- the product has no wall to raise (__PROD._noWallHeight)'); }
          else seat(_heightSl, 'mc_wall_height', 'Wall height');
          /* THE STOREY SLIDER GOES IN THE DRAWER TOO, UNDER THE WALL HEIGHT
             (core-v439, Marek: porad je posuvnik vysky prvniho podlazi v
             canvas, ma byt pod wall height). It was seated under the model
             alone while the other three sat in here. */
          try {
            var wantsSt = !!(__PROD && __PROD._storeySlider);
            if (wantsSt) {
              if (!_stSl) _initStorey();
              if (_stSl) { var RS = _stRange(); _stSl.set({ min: RS.min, max: RS.max, step: RS.step, value: _stNow() }); seat(_stSl, 'storeys', 'Storey'); _log('storey slider seated in the drawer under the wall height'); }
            }
          } catch (eSt) { _log('storey slider in the drawer: ' + (eSt && eSt.message)); }
          seat(_pitchSl, 'roof_pitch', 'Roof pitch');
          /* A PRODUCT'S OWN SLIDERS UNDER THE ROOF'S (core-v583, RANCH: one
             width for every cross gable). __PROD._roofSliders(seat, U) makes its
             sliders with U.slider and seats them like these */
          try { if (typeof __PROD._roofSliders === 'function') __PROD._roofSliders(seat, _UI()); } catch (ePS) { _log('roof     product sliders: ' + (ePS && ePS.message)); }
          pane.appendChild(host);
          /* the labels are placed after the slider has a width; a width of 0
             put it at the left edge on opening */
          var relab = function () { _heightLabel(); _pitchLabel(); _floorLabel(); try { _stLabel(); } catch (eL) {} };
          relab();
          try { global.requestAnimationFrame(relab); } catch (eR) {}
          global.setTimeout(relab, 350);
          global.setTimeout(relab, 900);
        } catch (eS) { _log('roof sliders: ' + (eS && eS.message)); }
      }
    }
    function wizardMount(steps, opts) {
      opts = opts || {};
      var w = initWizard(steps, {
        title: _tCap('edit', 'Edit'),
        /* the model is rebuilt on every step because what is drawn depends on
           which step is open: the foundation shows its full depth on BASE and
           stops at the ground everywhere else */
        onStep: function () {
          railCounters();
          if (typeof opts.holdStep === 'function' && opts.holdStep()) return;
          rebuild();
        },
        onExit: function () {
          try { if (typeof opts.onExit === 'function') opts.onExit(); } catch (e0) {}
          try { if (fitEdit()) fitEdit(false, 'the drawer closed'); } catch (e1) {}
          try { if (opEdit()) opEdit(false); } catch (e2) {}
          try { _asmLeave(); } catch (e3) {}
          try { showExplode(false); } catch (e4) {}
          railCounters(); rebuild();
        }
      });
      railCounters();
      undoPush();
      _log('wizard  ' + steps.length + ' step(s): ' + steps.map(function (s) { return s.key; }).join(', '));
      return w;
    }

    /* THE LINE'S OWN STEP LIST, AND THE KIT (core-v319).

       Every product listed the same seven steps in the same order with the same
       keys, and built the same kit from the same seven settings. Both are here
       now. A product hands over only what is its own:

       wizardSteps({ videoId?, structure?, override?, skip?, extra? })
         videoId   the YouTube id of the product's tutorial; empty and the VIDEO
                   step is not there at all -- no dot, no arrow stop
         structure true when the product offers a choice of what it is built
                   of; its GROUPS('structure') fills the step
         override  { key: { ...fields } } to change a step's label, hint, panel,
                   enter, exit -- anything stepDef takes
         skip      keys to leave out
         extra     steps of the product's own, appended before video

       kitDefault({ groups, kinds, onPick? })
         groups    the product's GROUPS(key) -- what each step offers
         kinds     the opening kinds the product sells
         onPick    what happens when a unit is chosen in the picker; the default
                   arms it and refreshes the wizard, which is what every product
                   did */
    function videoUrl(id) {
      return id ? ('https://www.youtube.com/embed/' + id + '?rel=0&modestbranding=1') : '';
    }
    function wizardSteps(opts) {
      opts = opts || {};
      _sxVideoId = opts.videoId || '';
      var P = placeStepDef(), L = layoutStepDef(), O = openingsStepDef(), K = colorsStepDef();
      var list = [
        stepDef('place',    P.label, P.hint, P.mode, P.view),
        stepDef('layout',   L.label, L.hint, L.mode, L.view),
        /* STRUCTURE: what the walls, floors and roof are built of, when a product
           lets that be chosen -- the masonry system of the T house, one day the
           frame of a timber house. Between the plan and the roof, because the wall
           comes before what stands on it. A product without the choice has no
           such step (Marek: skladbu sten a dalsi parametry -- separatni step
           STRUCTURE, idealne v UI central). */
        stepDef('structure', _t('structure', 'STRUCTURE'), _t('materials', 'Materials'), 3, '3d', {
          skip: function () { return !opts.structure; }
        }),
        stepDef('roof',     _uiStr('step_roof', 'ROOF & BASE'), _uiStr('step_roof_h', 'Shape, pitch, footings'), 2, '3d'),
        stepDef('openings', O.label, O.hint, O.mode, O.view),
        stepDef('colors',   K.label, K.hint, K.mode, K.view, { panel: colorsPanel }),
        stepDef('exterior', _uiStr('step_exterior', 'EXTERIOR'), _uiStr('step_exterior_h', 'Cladding and colour'), 5, '3d', {
          /* the wall between the posts: its plus signs stand on the model only
             while somebody is deciding this, and go the moment they are not */
          enter: function () { try { showPostWallPlus(true); } catch (e) {} },
          exit:  function () { try { showPostWallPlus(false); } catch (e) {} }
        })
      ].concat(opts.extra || []);
      list.push(stepDef('video', _t('wiz_video', 'VIDEO TUTORIAL'), _t('wiz_video_hint', 'Watch how the configurator works'), 5, '3d', {
        skip: function () { return !opts.videoId; },
        panel: function (pane) {
          stepPanel(pane, 'video');
          if (!opts.videoId || !pane) return;
          var fr = document.createElement('iframe');
          fr.src = videoUrl(opts.videoId);
          fr.setAttribute('allow', 'accelerometer; encrypted-media; picture-in-picture; fullscreen');
          fr.setAttribute('allowfullscreen', '');
          fr.style.cssText = 'width:100%;aspect-ratio:16/9;border:0;border-radius:8px;display:block;margin-top:10px;';
          pane.appendChild(fr);
        }
      }));
      var skip = opts.skip || [], ov = opts.override || {}, out = [], i, k;
      for (i = 0; i < list.length; i++) {
        if (skip.indexOf(list[i].key) >= 0) continue;
        if (ov[list[i].key]) { for (k in ov[list[i].key]) if (ov[list[i].key].hasOwnProperty(k)) list[i][k] = ov[list[i].key][k]; }
        out.push(list[i]);
      }
      return out;
    }
    /* the tutorial, full screen, from the Watch tile: one overlay, made once */
    function videoOpen(id) {
      if (!id) { _log('video: no VIDEO_ID set for this product'); return; }
      var oid = 'cf-video-overlay';
      var ov = document.getElementById(oid);
      if (ov) { ov.style.display = 'flex'; return; }
      ov = document.createElement('div');
      ov.id = oid;
      ov.style.cssText = 'position:fixed;inset:0;z-index:10000;background:rgba(0,0,0,0.82);' +
        'display:flex;align-items:center;justify-content:center;padding:24px;';
      var box = document.createElement('div');
      box.style.cssText = 'position:relative;width:min(960px,100%);aspect-ratio:16/9;' +
        'background:#000;border-radius:12px;overflow:hidden;';
      var fr = document.createElement('iframe');
      fr.src = videoUrl(id);
      fr.setAttribute('allow', 'accelerometer; encrypted-media; picture-in-picture; fullscreen');
      fr.setAttribute('allowfullscreen', '');
      fr.style.cssText = 'width:100%;height:100%;border:0;display:block;';
      var x = document.createElement('button');
      x.type = 'button';
      x.textContent = '\u2715';
      x.setAttribute('aria-label', _t('close', 'Close'));
      x.style.cssText = 'position:absolute;top:8px;right:8px;z-index:2;width:36px;height:36px;' +
        'border:0;border-radius:50%;background:rgba(255,255,255,0.92);color:#111;' +
        'font:700 16px Montserrat,sans-serif;cursor:pointer;';
      function shut() {
        ov.style.display = 'none';
        try { fr.src = fr.src; } catch (e) {}
      }
      x.addEventListener('click', shut);
      ov.addEventListener('click', function (e) { if (e.target === ov) shut(); });
      box.appendChild(fr);
      box.appendChild(x);
      ov.appendChild(box);
      document.body.appendChild(ov);
      _log('video opened: ' + id);
    }
    function videoGroups(id) {
      return [
        { label: _t('grp_video', 'Tutorial'), items: [
          { label: _t('watch', 'Watch'), svg: ICO.play,
            on: function () { return false; }, tap: function () { videoOpen(id); } }
        ] }
      ];
    }
    var _kitDefault = null;
    /* the product's own GROUPS(key), kept so the core can ask a step's rows
       without drawing them -- roofOffer reads the Roof & Base row (core-v551) */
    var _kitGroups = null;
    function kitDefault(opts) {
      if (!_kitGroups && opts && typeof opts.groups === 'function') _kitGroups = opts.groups;
      if (opts && opts.kinds && !_cwKinds) _cwKinds = opts.kinds.slice();
      if (_kitDefault) return _kitDefault;
      opts = opts || {};
      var U = _UI();
      if (!U || !U.kit) return null;
      _kitDefault = U.kit({
        t: _t,
        fmtLen: fmtLen,
        weight: 2.0,
        groups: opts.groups || function () { return []; },
        openings: opts.kinds || ['window', 'door'],
        codes: opts.codes || null,   /* core-v721: the units a product offers, per kind */
        corner: function () { return _cwOffered(); },   /* core-v751: the corner window's tile */
        wizard: function () { return _wiz; },
        onPick: (typeof opts.onPick === 'function') ? opts.onPick : function (spec) {
          armOpening(spec);
          /* the panel you chose from goes and the panel you are working in
             stays: both halves are meant to be worked in at once */
          if (_wiz && _wiz.refresh) _wiz.refresh();
          _log('armed ' + spec.kind + ' -- tap the wall to place it');
        }
      });
      return _kitDefault;
    }

    /* THE ROOF & BASE STEP, THE SAME IN EVERY PRODUCT (core-v320).

       The coverings table and its four helpers were the same in all three
       products, line for line, and the tiles differed in one thing: which roof
       shapes a product sells. The ADU and the sauna cycle the pitched tile from
       a gable through the hip stages and offer a flat roof with a parapet; the
       shed has a gable and a mono-pitch and nothing else. So the table, the
       helpers and the tiles are here, and a product says which shapes, its
       eave, and its default pitch (Marek: roof & base -- do UI).

       roofGroups({ hip?, mono?, parapet?, eave, pitch?, coverings?, foundations? })
         hip         true: the pitched tile cycles gable -> hip stages (default)
                     false: a gable only
         mono        the mono-pitch tile (default true)
         parapet     the flat roof with a parapet (default true)
         eave        the product's eave, set back when a pitched roof is chosen
         pitch       the pitch put on when the one kept is out of range (26)
         coverings   keys from ROOF_COVERINGS to offer (default all three)
         foundations kinds to offer (default screws, pads, piers, strips) */
    var ROOF_COVERINGS = [
      { key: 'shingle', i18n: 'mc_cov_shingle', def: 'Asphalt shingle',
        code: 'ROR00001', minPitch: 9.5 },
      { key: 'trapez', i18n: 'mc_cov_trapez', def: 'Trapezoidal sheet',
        code: 'ROR12004', minPitch: 5, batten: 1.00 },
      { key: 'seam', i18n: 'mc_cov_seam', def: 'Standing seam',
        code: 'ROR12003', minPitch: 3, batten: 0.60 },
      /* THE CLAY TILE (core-v475, Marek: asfaltovou krytinu nahradit
         klasickou taskou, uctujeme na m2): ROR00001 ROOF TILES / COVERING by
         the square metre, battens at the tile's gauge, 22 deg at least. Only
         a product that lists it in roofGroups({ coverings }) offers it */
      { key: 'tile', i18n: 'mc_cov_clay', def: 'Clay tile',
        code: 'ROR00001', minPitch: 22, batten: 0.33 }
    ];
    /* what the product offers and what it starts on (core-v475): set by
       roofGroups from opts.coverings, and __PROD._covDefault */
    var _covOffer = ['shingle', 'trapez', 'seam'];
    /* THE OFFER IS KNOWN BEFORE THE ROOF STEP IS OPENED (core-v483, from the
       T house thread, Marek: porad se pri reloadu vraci asfaltova krytina).
       roofGroups sets the offer only when the roof step's panel is made, so a
       reload built the house on the core's own list first and a tiled
       product came back in shingles. A product that names its coverings in
       __PROD._coverings has them from the first build. */
    try { if (__PROD && __PROD._coverings && __PROD._coverings.length) _covOffer = __PROD._coverings.slice(); } catch (eCO) {}
    function _covOfferNow() {
      try { if (__PROD && __PROD._coverings && __PROD._coverings.length && _covOffer.join() !== __PROD._coverings.join()) _covOffer = __PROD._coverings.slice(); } catch (eCO2) {}
      return _covOffer;
    }
    function _covDefault() {
      var d = null, off = _covOfferNow();
      try { d = (__PROD && __PROD._covDefault) ? String(__PROD._covDefault) : null; } catch (e) { d = null; }
      return (d && off.indexOf(d) >= 0) ? d : off[0];
    }
    function covKey(k) {
      var i;
      if (k === 'metal') return 'trapez';
      for (i = 0; i < ROOF_COVERINGS.length; i++) if (ROOF_COVERINGS[i].key === k && _covOfferNow().indexOf(k) >= 0) return k;
      return _covDefault();
    }
    function covAllowed(key) {
      var i, pit = (STATE.roof && STATE.roof.pitch) || 0;
      for (i = 0; i < ROOF_COVERINGS.length; i++) {
        if (ROOF_COVERINGS[i].key === key) return pit + 1e-6 >= ROOF_COVERINGS[i].minPitch;
      }
      return false;
    }
    /* the steepest covering this pitch still allows, or the least demanding one
       when none does */
    function covFallback() {
      var i, c, best = null, low = ROOF_COVERINGS[0];
      for (i = 0; i < ROOF_COVERINGS.length; i++) {
        c = ROOF_COVERINGS[i];
        if (_covOfferNow().indexOf(c.key) < 0) continue;
        if (c.minPitch < low.minPitch) low = c;
        if (!covAllowed(c.key)) continue;
        if (!best || c.minPitch > best.minPitch) best = c;
      }
      return best || low;
    }
    function covDef() {
      var i, want = covKey(STATE.covering || _covDefault());
      var pit = (STATE.roof && STATE.roof.pitch) || 0;
      for (i = 0; i < ROOF_COVERINGS.length; i++) {
        if (ROOF_COVERINGS[i].key !== want) continue;
        return (pit + 1e-6 >= ROOF_COVERINGS[i].minPitch) ? ROOF_COVERINGS[i] : covFallback();
      }
      return covFallback();
    }
    function roofGroups(opts) {
      opts = opts || {};
      var S = STATE, eave = (opts.eave != null) ? opts.eave : (S.roof.eave || 0.4), pitch0 = opts.pitch || 26;
      var types = [];
      /* the strip's roof cycler walks the same stages with the same eave and
         pitch the tile uses (core-v591) */
      _sxRoofOpts = { eave: eave, pitch: pitch0 };
      /* WHAT THIS PRODUCT SELLS, remembered for roofOffer (core-v551) */
      _roofOfferSeen = ['gable'].concat(opts.hip !== false ? ['hip'] : [], opts.mono !== false ? ['mono'] : [], opts.parapet !== false ? ['flat'] : []);
      if (opts.hip !== false) {
        /* THE PITCHED TILE CYCLES: gable, hip at one end, at the other, at both
           -- and round again. The label says which stage is on. */
        types.push({
          key: 'pitched',
          label: roofStageLabel(),
          svg: ICO.gable,
          on: function () { return S.roof.type === 'gable' || S.roof.type === 'hip'; },
          tap: function () {
            var was = (S.roof.type === 'gable' || S.roof.type === 'hip');
            var k = was ? (pitchedStage() + 1) % roofStageCount() : 0;
            S.roof.parapet = false;
            S.roof.eave = eave;
            if (S.roof.pitch < 15 || S.roof.pitch > 35) S.roof.pitch = pitch0;
            setPitchedStage(k);
            showPitch(true);
            if (_wiz && _wiz.refresh) _wiz.refresh();
          } });
      } else {
        types.push({
          key: 'gable',
          label: _uiStr('roof_gable', 'Gable'), svg: ICO.gable,
          on: function () { return S.roof.type === 'gable'; },
          tap: function () {
            setRoofType('gable');
            S.roof.parapet = false;
            S.roof.eave = eave;
            if (S.roof.pitch < 15 || S.roof.pitch > 35) S.roof.pitch = pitch0;
            setPitchedStage(0);
            showPitch(true);
            savePref(); rebuild();
            if (_wiz && _wiz.refresh) _wiz.refresh();
          } });
      }
      if (opts.mono !== false) {
        types.push({
          key: 'mono',
          label: _uiStr('roof_mono', 'Mono-pitch'), svg: ICO.flat,
          on: function () { return S.roof.type === 'mono'; },
          tap: function () {
            setRoofType('mono');
            S.roof.parapet = false;
            S.roof.eave = eave;
            if (S.roof.pitch < 2 || S.roof.pitch > 15) S.roof.pitch = 8;
            showPitch(true);
            savePref(); rebuild();
          } });
      }
      if (opts.parapet !== false) {
        types.push({
          key: 'flat',
          label: _t('roof_parapet', 'Parapet'), svg: ICO.parapet,
          on: function () { return S.roof.type === 'flat'; },
          tap: function () {
            /* a flat roof is flat: its fall is made in the covering above the
               frame, not by tilting the structure */
            setRoofType('flat');
            S.roof.parapet = true;
            S.roof.eave = 0;
            S.roof.pitch = 0;
            showPitch(false);
            savePref(); rebuild();
          } });
      }
      /* THE COVERINGS: three buttons that show which one is on. ONE IS GREYED
         OUT RATHER THAN MISSING when the pitch is below its minimum -- removing
         it would say the product has two coverings; showing it off says this
         roof is too flat for that one, which is also a nudge toward the pitch
         slider. */
      /* on a T: the ridges held level, or the wing's lower -- a switch, lit when
         level (core-v324) */
      if (S.wing && S.wing.on && opts.sameRidge !== false) {
        types.push({ key: 'ridge', label: _t('roof_ridge', 'Ridge'), svg: ICO.roof || ICO.gable,
          on: function () { return !!S.roof.sameRidge; },
          tap: function () { S.roof.sameRidge = !S.roof.sameRidge; savePref(); rebuild(); _log('ridges -> ' + (S.roof.sameRidge ? 'level' : 'each arm its own')); if (_wiz && _wiz.refresh) _wiz.refresh(); } });
      }
      var COV_ICON = { shingle: ICO.flat, trapez: ICO.raked, seam: ICO.parapet,
        /* rows of round-backed tiles, staggered (core-v475) */
        tile: '<path d="M3 8c1.5-2 3-2 4.5 0s3 2 4.5 0 3-2 4.5 0 3 2 4.5 0"/><path d="M3 13c1.5-2 3-2 4.5 0s3 2 4.5 0 3-2 4.5 0 3 2 4.5 0"/><path d="M3 18c1.5-2 3-2 4.5 0s3 2 4.5 0 3-2 4.5 0 3 2 4.5 0"/>' };
      var covKeys = opts.coverings || ['shingle', 'trapez', 'seam'];
      _covOffer = covKeys.slice();
      var covs = (S.roof.type === 'flat') ? [] : covKeys.map(function (k) {
        var D = null, q;
        for (q = 0; q < ROOF_COVERINGS.length; q++) if (ROOF_COVERINGS[q].key === k) D = ROOF_COVERINGS[q];
        if (!D) return null;
        var ok = covAllowed(k);
        return {
          key: k,
          label: _t(D.i18n, D.def), svg: COV_ICON[k],
          on: function () { return covKey(S.covering || _covDefault()) === k; },
          dim: !ok,
          tap: function () {
            if (!ok) {
              _log('covering ' + k + ' needs ' + D.minPitch + ' deg, this roof is at ' + Math.round(S.roof.pitch || 0) + ' -- tap ignored');
              return;
            }
            S.covering = k;
            _log('covering picked ' + k + ' -> STATE.covering=' + S.covering);
            savePref();
            rebuild();
          }
        };
      }).filter(function (x) { return !!x; });
      var FND_ICON = { screws: ICO.screw, pads: ICO.pad, piers: ICO.footing, strips: ICO.strip };
      var FND_LAB = { screws: ['base_screws', 'Ground screws'], pads: ['base_pads', 'Concrete pads'],
                      piers: ['base_piers', 'Precast blocks'], strips: ['base_strips', 'Strip footing'] };
      var fnd = (opts.foundations || ['screws', 'pads', 'piers', 'strips']).map(function (kind) {
        return { key: kind, label: _uiStr(FND_LAB[kind][0], FND_LAB[kind][1]), svg: FND_ICON[kind],
                 on: function () { return S.foundation === kind; },
                 tap: function () { setFoundation(kind); } };
      });
      var rows = [
        { key: 'roof', label: _t('roof', 'Roof'), items: types },
        { key: 'covering', label: _t('mc_cov_group', 'Roof covering'), items: covs }
      ];
      /* a product with one kind of footing has no base row: nothing to choose */
      if (fnd.length > 1) rows.push({ key: 'base', label: _t('base', 'Base'), items: fnd });
      return rows;
    }

    /* THE EXTERIOR STEP, THE SAME IN EVERY PRODUCT (core-v321).

       The last step to move, and the least alike: the ADU and the sauna share
       it almost whole, the shed shares a third. What every product has is the
       facade tile, the terrace, the pool, the wall between the posts with its
       slat gap under it. What differs is a row here and there -- the sauna's
       hot tub, the ADU's blinds, the shed's own terrace that comes with its
       post bays -- and the facade's own words. So the common rows are here and
       a product switches rows off, adds tiles of its own, or hands over a row
       whole (Marek: exterior -- do UI).

       exteriorGroups({ facade?, facadeLabel?, terrace?, terraceRow?, pergola?,
                        pool?, elements?, blinds?, slats?, after? })
         facade       the facade row (default true)
         facadeLabel  function (cladding) -> the tile's word; the default names
                      Render / Vertical / Horizontal
         terrace      the terrace row: on/off and, when on, its surface (default true)
         terraceRow   a product's own terrace row, used instead of the default
         pergola      the pergola tile among the elements (default true)
         pool         the pool tile (default true)
         elements     tiles of the product's own, after the pool
         blinds       the blinds row (default false -- the ADU says true)
         slats        the wall between the posts, last so its slider lands under
                      it (default true)
         after        rows appended after everything */
    function exteriorGroups(opts) {
      opts = opts || {};
      var S = STATE, rows = [];
      var refresh = function () { if (_wiz && _wiz.refresh) _wiz.refresh(); };
      if (opts.facade !== false) {
        /* THE TILE SAYS THE VARIANT AND NOTHING ELSE: Horizontal / Vertical /
           Render. The third state is render, not off, so the tile is a three-way
           switch and always lit (Marek). */
        var ck = cladKind();
        var lab = (typeof opts.facadeLabel === 'function') ? opts.facadeLabel(ck)
                : (ck === 'off' ? _t('layer_render', 'Render')
                  : (ck === 'vertical' ? _t('cladding_vertical', 'Vertical') : _t('cladding_horizontal', 'Horizontal')));
        rows.push({ key: 'facade', label: _t('grp_facade', 'Facade'), items: [
          { key: 'facade', label: lab, svg: ICO.cladding, on: function () { return true; }, tap: function () { nextCladding(); } }
        ] });
      }
      if (typeof opts.terraceRow === 'function') {
        var tr = opts.terraceRow();
        if (tr) rows.push(tr);
      } else if (opts.terrace !== false) {
        /* the terrace: on or off, and once it is on, the surface -- boards or
           pavers. Its size and place are dragged on the plan (Marek). */
        var T = _terrace(), items = [
          { key: 'terrace', label: _t('rooms.terrace', 'Terrace'), svg: ICO.terrace,
            on: function () { return !!_terrace().on; },
            tap: function () {
              var T2 = _terrace();
              /* core-v597: a pergola over the terrace stays where it stood
                 when the terrace goes -- it takes the terrace's place as its own */
              if (_pgFree() && T2.on && pergolaOn() && !_pgOwn(T2) && !_terraceSheltered()) {
                var TRk = _terraceRect();
                if (TRk) {
                  var ksx = Math.round(clamp(TRk.x1 - TRk.x0, PERG_LIM.min, PERG_LIM.max) * 100) / 100;
                  var ksz = Math.round(clamp(TRk.z1 - TRk.z0, PERG_LIM.min, PERG_LIM.max) * 100) / 100;
                  T2.pg = { x0: Math.round(TRk.x0 * 100) / 100, z0: Math.round(TRk.z0 * 100) / 100, sx: ksx, sz: ksz };
                  _log('pergola  kept where it stood -- the terrace under it went (core-v597)');
                }
              }
              T2.on = !T2.on; savePref(); rebuild(); refresh();
            } }
        ];
        if (T.on) items.push({ key: 'terraceSurface', label: _t('terrace_surface_toggle', 'Terrace surface'),
          svg: T.surface === 'pavers' ? (ICO.tiles || ICO.floor) : (ICO.floor || ICO.terrace),
          on: function () { return _terrace().surface === 'pavers'; },
          tap: function () { var T2 = _terrace(); T2.surface = (T2.surface === 'pavers') ? 'deck' : 'pavers'; _terraceSnapToTiles(); savePref(); rebuild(); refresh(); } });
        rows.push({ key: 'terrace', label: _t('rooms.terrace', 'Terrace'), items: items });
      }
      var el = [];
      if (opts.pergola !== false) {
        el.push({
          key: 'pergola',
          label: (function () { var k = _terrace().pergola; return (k === 'wood' || k === 'alu') ? _t('sauna_perg_1', 'Wooden slats') : (k === 'fabric' ? _t('perg_fabric', 'Fabric') : _cap(_t('pergola', 'PERGOLA'))); })(),
          svg: ICO.pergola || ICO.roof,
          on: function () { return pergolaOn() && !_pergolaSheltered(); },
          tap: function () {
            /* core-v597: off -> wooden slats -> fabric -> off, on the terrace
               or on a place of its own (togglePergola finds it one) */
            var T2 = _terrace();
            if (!pergolaOn()) { togglePergola(); refresh(); return; }
            if (T2.pergola === 'fabric') { togglePergola(); refresh(); return; }
            T2.pergola = 'fabric'; savePref(); rebuild(); refresh();
          } });
      }
      if (opts.pool !== false) {
        /* a picture of where a pool could go, placed and sized like the
           terrace; its note links to the pool configurator */
        el.push({ key: 'pool', label: _t('rooms.pool', 'Pool'), svg: ICO.pool || ICO.terrace,
          on: function () { return poolOn(); },
          tap: function () { togglePool(); refresh(); } });
      }
      el = el.concat(opts.elements || []);
      if (el.length) rows.push({ key: 'elements', label: _t('exterior_elements', 'Exterior elements'), items: el });
      if (opts.blinds) rows.push({ key: 'blinds', label: _t('blinds', 'Blinds'), items: [ blindsTile(_t, ICO) ] });
      /* THE SLATS ROW IS LAST: the slat gap slider is appended to the pane after
         the rows, so whatever group is last is the one it sits under */
      /* core-v726 (Marek: nazev slats v ovladacim panelu pro vsechny je
         blbost -- ma tam byt neco jako vypln, cyklovat vinyl / slats /
         planks): the row and the strip say Infill */
      if (opts.slats !== false) rows.push({ key: 'slats', label: _t('post_wall_infill', 'Infill'), items: [ postWallTile(_t, ICO) ] });
      return rows.concat(opts.after || []);
    }

    /* ---------------------------------------------------------------------
       THE ROOF SKIN CUT ROUND ITS WINDOWS, AND THE FLASHING KIT (core-v506,
       moved from the A-frame lib, aframe-v51 / v54 / v56, Marek: krytina je i
       pres vlozena okna; lem kolem oken). The shared drawer lays the deck,
       the underlay, the battens and the covering over the whole plane; here
       every piece it drew (the group's children from n0 on) is cut round the
       windows afterwards -- each triangle that reaches into an opening is
       clipped to what lies outside it, so any piece, flat or thick, loses
       exactly the opening and keeps its own colours and texture.

         API.roofOpenings(ctx, n0, holes, opt)
           holes  [{ o: [x,y,z], s: [..], a: [..], n: [..], s0, s1, a0, a1 }]
                  o a point on the rafters' top face, s down the slope, a along
                  the eave, n out of the roof (unit vectors); the opening is
                  s0..s1 x a0..a1 -- the unit plus its flashing gap
           opt    { billKey, takeOff: [{ code, f }], cov: covering key,
                    flash: true, flashCol, review, kitCode }
         -> { cut, meshes, tris, area, tops: [mm over the rafters, a window] }

       The openings' area comes off the named bill rows (times each f), and
       the kit is drawn round each opening: four upstands in the gap from the
       deck up past the covering and a 100 wide apron lying on the covering
       as measured round that window. Any product with a pitched roof and a
       roof window uses it.
       --------------------------------------------------------------------- */
    function roofOpenings(ctx, n0, holes, opt) {
      opt = opt || {};
      var T = ctx.THREE || global.THREE, res = { cut: 0, meshes: 0, tris: 0, area: 0, tops: [] };
      if (!T || !holes || !holes.length) { ctx.log('roofwin  skin: no roof window, nothing cut'); return res; }
      var dot = function (w, o, u) { return (w[0] - o[0]) * u[0] + (w[1] - o[1]) * u[1] + (w[2] - o[2]) * u[2]; };
      var H = holes.map(function (h) { return { h: h, top: 0, nTop: 0 }; });
      ctx.group.updateMatrixWorld(true);
      var V = new T.Vector3(), meshes = [], ch;
      for (ch = n0 || 0; ch < ctx.group.children.length; ch++) ctx.group.children[ch].traverse(function (o) { if (o.isMesh && o.geometry && o.geometry.attributes && o.geometry.attributes.position) meshes.push(o); });
      var clipPoly = function (poly, f) {
        var out = [], i;
        for (i = 0; i < poly.length; i++) {
          var P = poly[i], Q = poly[(i + 1) % poly.length], fP = f(P.w), fQ = f(Q.w);
          if (fP <= 0) out.push(P);
          if ((fP <= 0) !== (fQ <= 0)) {
            var t = fP / (fP - fQ), k, wv = [], av = [];
            for (k = 0; k < 3; k++) wv.push(P.w[k] + t * (Q.w[k] - P.w[k]));
            for (k = 0; k < P.a.length; k++) av.push(P.a[k] + t * (Q.a[k] - P.a[k]));
            out.push({ w: wv, a: av });
          }
        }
        return out;
      };
      var mi;
      for (mi = 0; mi < meshes.length; mi++) {
        var M = meshes[mi], geo = M.geometry.index ? M.geometry.toNonIndexed() : M.geometry, pos = geo.attributes.position;
        res.meshes++;
        var names = [], sizes = [], an;
        for (an in geo.attributes) if (geo.attributes.hasOwnProperty(an)) { names.push(an); sizes.push(geo.attributes[an].itemSize); }
        var vert = function (i) {
          var a = [], k, j;
          for (k = 0; k < names.length; k++) { var A = geo.attributes[names[k]]; for (j = 0; j < sizes[k]; j++) a.push(A.array[i * sizes[k] + j]); }
          V.fromBufferAttribute(pos, i).applyMatrix4(M.matrixWorld);
          return { w: [V.x, V.y, V.z], a: a };
        };
        var outV = [], touched = false, i3;
        for (i3 = 0; i3 + 2 < pos.count; i3 += 3) {
          var tri = [vert(i3), vert(i3 + 1), vert(i3 + 2)], hq, vq;
          /* the skin's real top round each window, measured on what was laid */
          for (hq = 0; hq < H.length; hq++) {
            var h6 = H[hq].h, sa6 = 1e9, sb6 = -1e9, za6 = 1e9, zb6 = -1e9, om6 = -1e9, on6 = 1e9;
            for (vq = 0; vq < 3; vq++) {
              var w6 = tri[vq].w, s6 = dot(w6, h6.o, h6.s), a6 = dot(w6, h6.o, h6.a), o6 = dot(w6, h6.o, h6.n);
              sa6 = Math.min(sa6, s6); sb6 = Math.max(sb6, s6); za6 = Math.min(za6, a6); zb6 = Math.max(zb6, a6); om6 = Math.max(om6, o6); on6 = Math.min(on6, o6);
            }
            if (on6 < -0.03 || om6 > 0.45) continue;
            if (sb6 < h6.s0 - 0.12 || sa6 > h6.s1 + 0.12 || zb6 < h6.a0 - 0.12 || za6 > h6.a1 + 0.12) continue;
            if (om6 > H[hq].top && om6 < 0.40) { H[hq].top = om6; H[hq].nTop++; }
          }
          var pieces = [tri], h;
          for (h = 0; h < H.length; h++) {
            var HH = H[h].h;
            var fs = function (w) { return dot(w, HH.o, HH.s); }, fa = function (w) { return dot(w, HH.o, HH.a); }, fo = function (w) { return dot(w, HH.o, HH.n); };
            var np = [], pc;
            for (pc = 0; pc < pieces.length; pc++) {
              var P0 = pieces[pc], s0 = 1e9, s1 = -1e9, a0 = 1e9, a1 = -1e9, o0 = 1e9, o1 = -1e9, v2;
              for (v2 = 0; v2 < P0.length; v2++) { var sv = fs(P0[v2].w), av2 = fa(P0[v2].w), ov = fo(P0[v2].w); s0 = Math.min(s0, sv); s1 = Math.max(s1, sv); a0 = Math.min(a0, av2); a1 = Math.max(a1, av2); o0 = Math.min(o0, ov); o1 = Math.max(o1, ov); }
              if (s1 <= HH.s0 || s0 >= HH.s1 || a1 <= HH.a0 || a0 >= HH.a1 || o0 < -0.02 || o1 > 0.45) { np.push(P0); continue; }
              touched = true;
              /* the four regions outside the rectangle, each convex */
              var rA = clipPoly(P0, function (w) { return fs(w) - HH.s0; });
              var rB = clipPoly(P0, function (w) { return HH.s1 - fs(w); });
              var mid = clipPoly(clipPoly(P0, function (w) { return HH.s0 - fs(w); }), function (w) { return fs(w) - HH.s1; });
              var rC = clipPoly(mid, function (w) { return fa(w) - HH.a0; });
              var rD = clipPoly(mid, function (w) { return HH.a1 - fa(w); });
              [rA, rB, rC, rD].forEach(function (rg) { if (rg.length >= 3) np.push(rg); });
            }
            pieces = np;
          }
          var pp, tv;
          for (pp = 0; pp < pieces.length; pp++) for (tv = 1; tv + 1 < pieces[pp].length; tv++) outV.push(pieces[pp][0], pieces[pp][tv], pieces[pp][tv + 1]);
        }
        if (!touched) continue;
        res.cut++;
        /* the cut piece is in world coordinates now: its own transform goes */
        var G2 = new T.BufferGeometry(), k2, offs = [], acc = 0;
        for (k2 = 0; k2 < names.length; k2++) { offs.push(acc); acc += sizes[k2]; }
        for (k2 = 0; k2 < names.length; k2++) {
          var arr2 = new Float32Array(outV.length * sizes[k2]), vj, jj;
          if (names[k2] === 'position') { for (vj = 0; vj < outV.length; vj++) for (jj = 0; jj < 3; jj++) arr2[vj * 3 + jj] = outV[vj].w[jj]; }
          else for (vj = 0; vj < outV.length; vj++) for (jj = 0; jj < sizes[k2]; jj++) arr2[vj * sizes[k2] + jj] = outV[vj].a[offs[k2] + jj];
          G2.setAttribute(names[k2], new T.BufferAttribute(arr2, sizes[k2]));
        }
        var inv = new T.Matrix4().copy(M.matrixWorld).invert();
        G2.applyMatrix4(inv);
        M.geometry = G2; res.tris += outV.length / 3;
      }
      var q;
      for (q = 0; q < holes.length; q++) res.area += (holes[q].s1 - holes[q].s0) * (holes[q].a1 - holes[q].a0);
      /* the openings come off the rows the drawer billed over the whole plane */
      var taken = [], BL = opt.billKey ? (_bill[opt.billKey] || {}) : null;
      (opt.takeOff || []).forEach(function (r) {
        var qty = res.area * (r.f || 1);
        if (BL && r.code && BL[r.code] > qty) { BL[r.code] -= qty; taken.push(r.code + ' -' + qty.toFixed(2) + ' m2'); }
      });
      if (opt.flash !== false) {
        var ck = opt.cov || 'shingle', hTop = (ck === 'shingle') ? 0.050 : (0.018 + 0.030 + 0.030 + 0.014 + (ck === 'tile' ? 0.028 : (ck === 'trapez' ? 0.038 : 0.025)));
        var colF = opt.review ? 0xFF00FF : ((opt.flashCol != null) ? opt.flashCol : 0x3A3D40);
        var fMat = new T.MeshStandardMaterial({ color: colF, roughness: 0.45, metalness: 0.55 });
        var PW = function (h9, s9, o9, a9) { return [h9.o[0] + s9 * h9.s[0] + o9 * h9.n[0] + a9 * h9.a[0], h9.o[1] + s9 * h9.s[1] + o9 * h9.n[1] + a9 * h9.a[1], h9.o[2] + s9 * h9.s[2] + o9 * h9.n[2] + a9 * h9.a[2]]; };
        /* a closed box in the slope's own axes, wound outward, so the section fills it */
        var slab = function (h9, sa, sb, aa, ab, oa, ob) {
          var c = [PW(h9, sa, oa, aa), PW(h9, sb, oa, aa), PW(h9, sb, oa, ab), PW(h9, sa, oa, ab), PW(h9, sa, ob, aa), PW(h9, sb, ob, aa), PW(h9, sb, ob, ab), PW(h9, sa, ob, ab)];
          var F = [[0, 2, 1], [0, 3, 2], [4, 5, 6], [4, 6, 7], [0, 1, 5], [0, 5, 4], [1, 2, 6], [1, 6, 5], [2, 3, 7], [2, 7, 6], [3, 0, 4], [3, 4, 7]];
          var cr = function (u, v) { return [u[1] * v[2] - u[2] * v[1], u[2] * v[0] - u[0] * v[2], u[0] * v[1] - u[1] * v[0]]; };
          var hand = dot(cr(h9.s, h9.a), [0, 0, 0], h9.n) > 0 ? 1 : -1;      /* s x a along n: the faces as listed wind outward */
          var arr = [], f9, k9;
          for (f9 = 0; f9 < F.length; f9++) {
            var tri9 = (hand > 0) ? F[f9] : [F[f9][0], F[f9][2], F[f9][1]];
            for (k9 = 0; k9 < 3; k9++) arr.push(c[tri9[k9]][0], c[tri9[k9]][1], c[tri9[k9]][2]);
          }
          var gB = new T.BufferGeometry(); gB.setAttribute('position', new T.BufferAttribute(new Float32Array(arr), 3)); gB.computeVertexNormals();
          ctx.group.add(new T.Mesh(gB, fMat));
        };
        var ft = 0.004, apr = 0.100;
        for (q = 0; q < H.length; q++) {
          var h5 = H[q].h, hTopW = (H[q].top > 0.005) ? H[q].top + 0.002 : hTop;
          res.tops.push(Math.round(hTopW * 1000));
          slab(h5, h5.s0 - ft, h5.s0, h5.a0 - ft, h5.a1 + ft, 0, hTopW + 0.015);
          slab(h5, h5.s1, h5.s1 + ft, h5.a0 - ft, h5.a1 + ft, 0, hTopW + 0.015);
          slab(h5, h5.s0, h5.s1, h5.a0 - ft, h5.a0, 0, hTopW + 0.015);
          slab(h5, h5.s0, h5.s1, h5.a1, h5.a1 + ft, 0, hTopW + 0.015);
          slab(h5, h5.s0 - apr, h5.s1 + apr, h5.a0 - apr, h5.a0, hTopW, hTopW + 0.002);
          slab(h5, h5.s0 - apr, h5.s1 + apr, h5.a1, h5.a1 + apr, hTopW, hTopW + 0.002);
          slab(h5, h5.s0 - apr, h5.s0, h5.a0, h5.a1, hTopW, hTopW + 0.002);
          slab(h5, h5.s1, h5.s1 + apr, h5.a0, h5.a1, hTopW, hTopW + 0.002);
        }
        ctx.log('roofwin  flashing kit drawn round ' + H.length + ' window(s) (core-v506): upstands from the deck past the ' + ck + ' covering, a 100 apron laid on its measured top round each window: ' + res.tops.join(' / ') + ' over the rafters (fallback by kind ' + Math.round(hTop * 1000) + ')' + (opt.review ? ' -- MAGENTA, under review' : '') + (opt.kitCode ? ('; billed ' + H.length + ' x ' + opt.kitCode + ' by the product') : ''));
      }
      ctx.log('roofwin  skin cut round ' + holes.length + ' roof window(s) (core-v506): ' + res.cut + ' of ' + res.meshes + ' skin piece(s) cut (' + res.tris + ' triangles after), openings ' + res.area.toFixed(2) + ' m2 taken off the bill: ' + (taken.length ? taken.join(', ') : 'nothing found to take off'));
      return res;
    }

    /* BILLING A CODE, FOR ANY PRODUCT (core-v325). The three products carry the
       same _add: a quantity into a layer's bucket under a catalogue key, and a
       loud NOT BILLED line when the key has no catalogue row -- because a thing
       drawn and priced against nothing is how a product gets sold for less than
       it costs (Marek). Here so the T house and whatever comes after it bill the
       same way without copying it. */
    function billAdd(layer, key, qty) {
      if (!(qty > 0)) return;
      if (!CATALOG[key]) {
        _log('BILL  NOT BILLED -- no catalogue entry for ' + key + ' (drawn and priced against nothing)');
        if (_notBilled.indexOf(key) < 0) _notBilled.push(key);
        return;
      }
      if (!_bill[layer]) _bill[layer] = {};
      _bill[layer][key] = (_bill[layer][key] || 0) + qty;
    }

    /* STRIP FOOTINGS WITH A SLAB, UNDER ANY OUTLINE (core-v325).

       A masonry building stands on a strip footing under every wall and a
       ground slab between them. The strips follow the outline the core keeps --
       poly(), so a T gets eight of them, closed at every corner -- down to frost
       depth, with a spread footing at the bottom; the slab covers the plan on a
       bed of pea gravel over geotextile. Everything below the slab shows only
       where the footings are being decided (the base step) or the assembly walk
       is on, the way the line treats what is under ground.

       buildStripSlab(ctx, { layer, stripW?, footH?, slabT?, gravelT?, minDepth?, out?, floorT? })
       draws it and bills it into the layer given, with the catalogue codes the
       ADU already bills its footings with. Returns the tally. */
    /* -------------------------------------------------------------------------
       TWO DRAWERS, EMPTY (core-v458, K2, Marek: v UI asset supliky, z kterych
       si dany config bere co potrebuje). What is written here is the CONTRACT
       of each drawer -- what a product hands in and what the drawer hands back
       -- so the products can be written against it while the drawer fills up
       in the steps that follow. Neither builds anything yet; a product that
       calls one gets a log line saying so and its own build goes on as before.

       ROOF SKIN -- everything on top of the roof structure, whatever that
       structure is (trusses in ADU, shed and sauna, rafters in the T house):
       deck, underlay, battens, edges, covering, ridge and hips, gutters. It is
       built on the roof PLANES and their eave lines, which the core owns, so
       the product only says what the layers are made of and where the top of
       its structure lies. Filled from the ADU's own roof skin, layer by layer
       (K3 to K7), the ADU switching to the drawer at each step.

         API.roofSkin(ctx, {
           layer:     'adu-roof',            the layer the rows are billed into
           planes:    roofPlanes(),          the planes to cover (a subset is fine)
           lift:      function (plane)       height of the structure's top above the
                                             plane's datum, so the skin stacks from it
           deck:      { code, t } | null     sheathing, or none (battens straight on rafters)
           underlay:  { code }
           battens:   { counter: { code, w, h }, batten: { code, w, h }, screw, col }
           members:   [[x, z, dx, dz], ...] | null   the structure's members in plan, a
                                             counter-batten goes up each one
           spacing:   m                      counter-batten spacing where no member lands
           edges:     { drip: { code, face, top, t }, verge: { code, face, top, t, rake, eaveOut },
                        fix, fixSpacing, color, fascia: { code, h, t, color } | null }
           covering:  { kind: 'shingle' | 'trapez' | 'seam', picked, gauge, code, def, color, table? }
           ridge:     { code, vent, width, t, stand, fix, fixSpacing }
           gutters:   { code, downpipe: code, spacing } | null
           only:      ['deck', 'underlay'] | null   build these layers only (a product
                                             moves over one layer at a time)
           sheet:     { l, w }               the deck's sheet size, for the joints drawn
           isNew:     function (name) | null the magenta marker, by layer.name
           tex:       function (kind) | null a texture for a layer ('osb')
           why:       function (line) | null the explanatory log lines
           mark:      function (id, side, label, code) | null   the assembly-step marker
         })
         -> { built: [layer names], area: m2, pending: [layers not in the drawer yet] }
            and a log line per layer

       STAIRS -- a stair in a floor opening, for every product with a storey or
       a loft. Filled in core-v465 (A2); its contract sits over stairPlan()
       and stairs() themselves, next to the code.
       ------------------------------------------------------------------------- */
    /* the roof skin's own small geometry, copied from the ADU (core-v459, K3) */
    function _rsQuad(v, a, b, c, d) {
      v.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]);
      v.push(a[0], a[1], a[2], c[0], c[1], c[2], d[0], d[1], d[2]);
    }
    /* THE DECK PLANE: the published plane lifted PLUMB by the structure's depth,
       so every layer's corner stands over the plane's corner (ADU _deckPlane) */
    function _rsDeckPlane(pl, lift) {
      if (!pl) return null;
      var n = pl.n || [0, 1, 0];
      var d = lift / Math.max(0.2, Math.abs(n[1]));
      var off = function (a) { return [a[0], a[1] + d, a[2]]; };
      var offEdge = function (e) { return e ? [off(e[0]), off(e[1])] : null; };
      var pts = [], q;
      for (q = 0; q < (pl.pts || []).length; q++) pts.push(off(pl.pts[q]));
      return { pts: pts, eave: offEdge(pl.eave), ridge: offEdge(pl.ridge), n: n, area: pl.area, pitch: pl.pitch };
    }
    /* the sheet joints scored on the deck: long joints across, cross joints per
       course with a half-board stagger (ADU _deckGrid) */
    function _rsDeckGrid(T, g, m, Q, n, lift, pitchL, pitchU, AX) {
      var ex = AX ? (AX.eB[0] - AX.eA[0]) : (Q[3][0] - Q[0][0]), ez = AX ? (AX.eB[2] - AX.eA[2]) : (Q[3][2] - Q[0][2]);
      var eL = Math.hypot(ex, ez);
      if (!(eL > 0.05)) return 0;
      ex /= eL; ez /= eL;
      var ux = ex, uy = 0, uz = ez;
      var vx = uy * n[2] - uz * n[1], vy = uz * n[0] - ux * n[2], vz = ux * n[1] - uy * n[0];
      var vl = Math.hypot(vx, vy, vz) || 1;
      vx /= vl; vy /= vl; vz /= vl;
      var O = Q[0], q, lo2 = { u: 1e9, v: 1e9 }, hi2 = { u: -1e9, v: -1e9 };
      var UV = [];
      for (q = 0; q < Q.length; q++) {
        var dx = Q[q][0] - O[0], dy = Q[q][1] - O[1], dz = Q[q][2] - O[2];
        var uu = dx * ux + dy * uy + dz * uz;
        var vvv = dx * vx + dy * vy + dz * vz;
        UV.push([uu, vvv]);
        if (uu < lo2.u) lo2.u = uu;
        if (uu > hi2.u) hi2.u = uu;
        if (vvv < lo2.v) lo2.v = vvv;
        if (vvv > hi2.v) hi2.v = vvv;
      }
      var toWorld = function (a, b) {
        return [O[0] + ux * a + vx * b + n[0] * lift, O[1] + uy * a + vy * b + n[1] * lift, O[2] + uz * a + vz * b + n[2] * lift];
      };
      var span = function (fixed, alongV) {
        var lo3 = 1e9, hi3 = -1e9, e;
        for (e = 0; e < UV.length; e++) {
          var A = UV[e], B = UV[(e + 1) % UV.length];
          var a0 = alongV ? A[0] : A[1], b0 = alongV ? B[0] : B[1];
          if ((a0 - fixed) * (b0 - fixed) > 0) continue;
          var t = (fixed - a0) / ((b0 - a0) || 1);
          var w = alongV ? (A[1] + (B[1] - A[1]) * t) : (A[0] + (B[0] - A[0]) * t);
          if (w < lo3) lo3 = w;
          if (w > hi3) hi3 = w;
        }
        return (hi3 - lo3 > 0.05) ? [lo3, hi3] : null;
      };
      var span2 = function (fixed, v0, v1) {
        var sp3 = span(fixed, true);
        if (!sp3) return null;
        var a3 = Math.max(sp3[0], v0), b3 = Math.min(sp3[1], v1);
        return (b3 - a3 > 0.05) ? [a3, b3] : null;
      };
      var bar = function (p1, p2) {
        var L = Math.hypot(p2[0] - p1[0], p2[1] - p1[1], p2[2] - p1[2]);
        if (L < 0.05) return;
        var w = 0.006, vvv2 = [];
        var ax = (p2[0] - p1[0]) / L, ay = (p2[1] - p1[1]) / L, az = (p2[2] - p1[2]) / L;
        var sx = ay * n[2] - az * n[1], sy = az * n[0] - ax * n[2], sz = ax * n[1] - ay * n[0];
        var sl = Math.hypot(sx, sy, sz) || 1;
        sx = sx / sl * w / 2; sy = sy / sl * w / 2; sz = sz / sl * w / 2;
        _rsQuad(vvv2, [p1[0] - sx, p1[1] - sy, p1[2] - sz], [p1[0] + sx, p1[1] + sy, p1[2] + sz],
                      [p2[0] + sx, p2[1] + sy, p2[2] + sz], [p2[0] - sx, p2[1] - sy, p2[2] - sz]);
        var geo = new T.BufferGeometry();
        geo.setAttribute('position', new T.Float32BufferAttribute(vvv2, 3));
        geo.computeVertexNormals();
        g.add(new T.Mesh(geo, m));
      };
      var row = 0, k, sp, uk;
      for (k = Math.floor(lo2.v / pitchU) * pitchU; k < hi2.v; k += pitchU) {
        if (k > lo2.v + 0.01) {
          sp = span(k, false);
          if (sp) bar(toWorld(sp[0], k), toWorld(sp[1], k));
        }
        var off7 = (row % 2) ? pitchL / 2 : 0;
        var vMid = k + pitchU / 2;
        /* a board as long as its bay has no cross joint: pitchL 0 means none (core-v515) */
        if (vMid < hi2.v && pitchL > 0.01) {
          for (uk = Math.ceil((lo2.u - off7) / pitchL) * pitchL + off7; uk < hi2.u; uk += pitchL) {
            var s1 = span2(uk, k, Math.min(k + pitchU, hi2.v));
            if (s1) bar(toWorld(uk, s1[0]), toWorld(uk, s1[1]));
          }
        }
        row++;
      }
    }
    /* THE PLANE'S OWN AXES, FROM ITS EAVE AND RIDGE (core-v464, Marek: battens
       na side run jsou nahoru mimo strechu). The ADU's planes are four corners
       in the order eave A, ridge A, ridge B, eave B, and the deck grid, the
       underlay and the battens read the eave direction and the slope off that
       order. A T house plane is a clipped piece whose corners come in another
       order, so the grids stood in the wrong plane. The eave and the ridge
       are published on every plane whatever its outline; the axes come from
       them now, and the ADU's come out exactly as before. */
    function _rsAxes(DP) {
      var Q = DP.pts, E = DP.eave, R = DP.ridge;
      var eA = (E && E[0]) || Q[0], eB = (E && E[1]) || Q[3], rA = (R && R[0]) || Q[1], rB = (R && R[1]) || Q[2];
      return { eA: eA, eB: eB, rA: rA, rB: rB };
    }
    /* the underlay's lap follows the pitch: 150 over 30 degrees, 200 from 22, 250 under */
    function _rsUlLap(pitch) { var p = pitch || 26; return (p >= 30) ? 0.15 : ((p >= 22) ? 0.20 : 0.25); }

    /* STEP 1, THE DECK (copied from the ADU's _skinDeck): one slab per plane on
       the same four corners the shell draws, lifted clear of the structure, the
       sheet joints scored on its face; billed in square metres plus a cutting
       allowance, and the nails on the measured area */
    /* INSIDE THE WALLS ONLY (core-v517, from the T house thread, Marek:
       skladba stropu druheho patra). A lining or a ceiling hangs under the
       deck, and the deck runs out over the eave -- so both ran outdoors with
       it, boards and plasterboard under the rafter tails and in the bill. A
       product hands over its inner outline in plan (spec.inner, the wall
       faces the room sees); every piece of lining and ceiling is cut to it.
       The outline may be a T, so it is sliced into rectangles first and the
       piece cut against each; the rectangles tile, so nothing is drawn
       twice. Without spec.inner nothing changes. */
    function _rsInnerPieces(P3, inner) {
      if (!inner || inner.length < 3 || !P3 || P3.length < 3) return [P3];
      var rects = _decomposeToRects(inner), out = [], r, piece;
      for (r = 0; r < rects.length; r++) {
        var R = rects[r];
        piece = _clipAxis(P3, 0, R.x0, true);
        if (piece.length < 3) continue;
        piece = _clipAxis(piece, 0, R.x1, false);
        if (piece.length < 3) continue;
        piece = _clipAxis(piece, 2, R.z0, true);
        if (piece.length < 3) continue;
        piece = _clipAxis(piece, 2, R.z1, false);
        if (piece.length < 3) continue;
        if (_polyArea3(piece) > 0.0005) out.push(piece);
      }
      return out;
    }
    /* a slab on an n-gon of the plane: the polygon offset h0 and h1 along the normal, fanned, with its sides */
    function _rsPrism(T, g, mat, P3, n, h0, h1, name) {
      var at = function (pt, h) { return [pt[0] + n[0] * h, pt[1] + n[1] * h, pt[2] + n[2] * h]; };
      var lo = [], hi = [], vv = [], q, m = P3.length;
      for (q = 0; q < m; q++) { lo.push(at(P3[q], h0)); hi.push(at(P3[q], h1)); }
      for (q = 1; q + 1 < m; q++) {
        vv.push(hi[0][0], hi[0][1], hi[0][2], hi[q][0], hi[q][1], hi[q][2], hi[q + 1][0], hi[q + 1][1], hi[q + 1][2]);
        vv.push(lo[0][0], lo[0][1], lo[0][2], lo[q + 1][0], lo[q + 1][1], lo[q + 1][2], lo[q][0], lo[q][1], lo[q][2]);
      }
      for (q = 0; q < m; q++) { var r = (q + 1) % m; _rsQuad(vv, lo[q], hi[q], hi[r], lo[r]); }
      var geo = new T.BufferGeometry(); geo.setAttribute('position', new T.Float32BufferAttribute(vv, 3)); geo.computeVertexNormals();
      var mesh = new T.Mesh(geo, mat); if (name) mesh.name = name; g.add(mesh);
      return mesh;
    }
    function _rsDeck(ctx, spec, planes) {
      var T = ctx.THREE, g = ctx.group, i, D = spec.deck, layer = spec.layer, MAG = 0xFF00FF;
      var isNewL = function (nm) { try { return !!(spec.isNew && spec.isNew(layer + '.' + nm)); } catch (e) { return false; } };
      var mat = new T.MeshStandardMaterial({ color: isNewL('deck') ? MAG : 0xFFFFFF, roughness: 0.9, metalness: 0.0, side: T.DoubleSide });
      var otx = null;
      try { otx = spec.tex ? spec.tex('osb') : null; } catch (eT) { otx = null; }
      if (otx && !isNewL('deck')) {
        mat.map = otx.clone();
        mat.map.wrapS = mat.map.wrapT = T.RepeatWrapping;
        mat.map.repeat.set(3, 3);
      }
      var SS = spec.sheet || { l: 2.44, w: 1.22 };
      var LIN = spec.lining || null, CLEAR = 0.001 + _rsLinT(spec), area = 0, made = 0, linBays = 0, linNet = 0;
      var linM = LIN ? new T.MeshStandardMaterial({ color: LIN.color || 0xD8B887, roughness: 0.75, metalness: 0.0, side: T.DoubleSide }) : null;
      var linJ = LIN ? new T.MeshBasicMaterial({ color: 0x9C7B4E, side: T.DoubleSide }) : null;
      for (i = 0; i < planes.length; i++) {
        var DP = _rsDeckPlane(planes[i], spec.lift(planes[i]));
        var Q = DP && DP.pts;
        if (!Q || Q.length < 4) continue;
        var n = DP.n || [0, 1, 0];
        var at = function (pt, h) { return [pt[0] + n[0] * h, pt[1] + n[1] * h, pt[2] + n[2] * h]; };
        var lo = [at(Q[0], CLEAR), at(Q[1], CLEAR), at(Q[2], CLEAR), at(Q[3], CLEAR)];
        var hi = [at(Q[0], CLEAR + D.t), at(Q[1], CLEAR + D.t), at(Q[2], CLEAR + D.t), at(Q[3], CLEAR + D.t)];
        var vv = [], q;
        _rsQuad(vv, hi[0], hi[1], hi[2], hi[3]);
        _rsQuad(vv, lo[3], lo[2], lo[1], lo[0]);
        for (q = 0; q < 4; q++) { var r = (q + 1) % 4; _rsQuad(vv, lo[q], hi[q], hi[r], lo[r]); }
        var geo = new T.BufferGeometry();
        geo.setAttribute('position', new T.Float32BufferAttribute(vv, 3));
        geo.computeVertexNormals();
        g.add(new T.Mesh(geo, mat));
        /* THE LINING UNDER THE DECK (core-v487, Marek: T&G palubky primo na
           OSB zespodu, krokve viditelne v interieru): tongue-and-groove boards
           fixed to the deck's underside in the bays between the rafters, so
           the rafters stay in sight and the deck stays on them. Drawn as one
           board thick under the deck; inside a rafter it is hidden by the
           rafter. The boards run along the eave, joints scored underneath. */
        if (LIN) {
          var l0 = -LIN.t, l1 = 0, baysDone = 0;
          /* THE BOARDS LIE BETWEEN THE RAFTERS (core-v515, from the T house
             thread, Marek: T&G obklad mezi krokvemi primo na OSB zaklop,
             kolmo ke krokvim). The lining was one slab under the whole plane,
             running through the top of every rafter -- right on the bill
             (the rafters were taken off) and wrong in a section, where the
             board showed inside the timber. A product that publishes its
             rafters (spec.members) and their width (lining.memberW) now gets
             one strip per bay: from the side of one rafter to the side of
             the next, the plane's own outline at the ends, the boards along
             the eave with no cross joint, since a board is as long as its
             bay. A product without members keeps the slab. */
          if (spec.members && spec.members.length > 1 && LIN.memberW > 0) {
            var AXl = _rsAxes(DP), exl = AXl.eB[0] - AXl.eA[0], ezl = AXl.eB[2] - AXl.eA[2], eLl = Math.hypot(exl, ezl);
            if (eLl > 0.05) {
              exl /= eLl; ezl /= eLl;
              var planQ = [], qp, us = [], qm;
              for (qp = 0; qp < Q.length; qp++) planQ.push([Q[qp][0], Q[qp][2]]);
              /* this plane's own rafters: those that run up its slope and
                 stand inside its outline in plan, by their station along the eave */
              for (qm = 0; qm < spec.members.length; qm++) {
                var MPl = spec.members[qm];
                if (!MPl || MPl.length < 4) continue;
                var mdl2 = Math.hypot(MPl[2], MPl[3]) || 1, along2 = Math.abs((MPl[2] * (-ezl) + MPl[3] * exl) / mdl2);
                if (along2 < 0.7) continue;
                if (!inPoly(MPl[0], MPl[1], planQ)) continue;
                us.push((MPl[0] - AXl.eA[0]) * exl + (MPl[1] - AXl.eA[2]) * ezl);
              }
              us.sort(function (a9, b9) { return a9 - b9; });
              var half = LIN.memberW / 2, qb;
              for (qb = 0; qb + 1 < us.length; qb++) {
                var uA = us[qb] + half, uB = us[qb + 1] - half;
                if (!(uB - uA > 0.05)) continue;
                /* the bay's piece of the plane: the outline cut on the two rafter sides */
                var S1 = _clipAbove(Q, { n: [exl, 0, ezl], d: -(AXl.eA[0] * exl + AXl.eA[2] * ezl) - uA });
                if (S1.length < 3) continue;
                var S2 = _clipAbove(S1, { n: [-exl, 0, -ezl], d: (AXl.eA[0] * exl + AXl.eA[2] * ezl) + uB });
                if (S2.length < 3) continue;
                /* and inside the walls only (core-v517): the strip cut to the inner outline */
                var pcs = _rsInnerPieces(S2, spec.inner || null), qpc;
                for (qpc = 0; qpc < pcs.length; qpc++) {
                  _rsPrism(T, g, linM, pcs[qpc], n, l0, l1, 'lining');
                  _rsDeckGrid(T, g, linJ, pcs[qpc], n, l0 - 0.0006, 0, LIN.w || 0.146, AXl);
                  linNet += _polyArea3(pcs[qpc]);
                }
                if (pcs.length) baysDone++;
              }
            }
          }
          if (!baysDone) {
            var llo = [at(Q[0], l0), at(Q[1], l0), at(Q[2], l0), at(Q[3], l0)], lhi = [at(Q[0], l1), at(Q[1], l1), at(Q[2], l1), at(Q[3], l1)];
            var lv = [];
            _rsQuad(lv, lhi[0], lhi[1], lhi[2], lhi[3]);
            _rsQuad(lv, llo[3], llo[2], llo[1], llo[0]);
            for (q = 0; q < 4; q++) { var r2 = (q + 1) % 4; _rsQuad(lv, llo[q], lhi[q], lhi[r2], llo[r2]); }
            var lg = new T.BufferGeometry(); lg.setAttribute('position', new T.Float32BufferAttribute(lv, 3)); lg.computeVertexNormals();
            var lm = new T.Mesh(lg, linM); lm.name = 'lining'; g.add(lm);
            _rsDeckGrid(T, g, linJ, Q, n, l0 - 0.0006, LIN.len || 4.0, LIN.w || 0.146, _rsAxes(DP));
          }
          linBays += baysDone;
        }
        var jm = new T.MeshBasicMaterial({ color: 0x8A7A5C });
        _rsDeckGrid(T, g, jm, Q, n, CLEAR + D.t, SS.l, SS.w, _rsAxes(DP));
        var tri = function (a, b, c) {
          var ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2];
          var wx = c[0] - a[0], wy = c[1] - a[1], wz = c[2] - a[2];
          var cx = uy * wz - uz * wy, cy = uz * wx - ux * wz, cz = ux * wy - uy * wx;
          return Math.hypot(cx, cy, cz) / 2;
        };
        area += tri(Q[0], Q[1], Q[2]) + tri(Q[0], Q[2], Q[3]);
        made++;
      }
      if (!made) { ctx.log('skin     deck NONE DRAWN -- ' + planes.length + ' plane(s) offered'); return { area: 0 }; }
      /* the cutting allowance is the product's (core-v463): the shed keeps it
         in its own SHEET_WASTE, the ADU writes 1.05 */
      if (LIN) {
        /* billed on the bays only: the rafters' own width is taken off -- or,
           laid bay by bay, on the strips as drawn, inside the walls (core-v517) */
        var net = linBays ? linNet : (area * ((typeof LIN.net === 'number') ? LIN.net : 1)), lArea = net * ((typeof LIN.waste === 'number') ? LIN.waste : 1.10);
        var nNail = Math.ceil(net / (LIN.w || 0.146) / (LIN.nailSpacing || 0.30));
        if (LIN.code) billAdd(layer, LIN.code, lArea);
        if (LIN.nail) billAdd(layer, LIN.nail, nNail);
        ctx.log('skin     lining on the deck\'s underside between the rafters' + (linBays ? (' -- ' + linBays + ' bay strip(s) from rafter side to rafter side, boards across the bay, no cross joints (core-v515)' + (spec.inner ? ', cut to the inner outline of the walls (core-v517)' : '')) : ' -- one slab per plane, the product publishes no rafters') + ': ' + Math.round(LIN.t * 1000) + ' mm tongue-and-groove boards ' + Math.round((LIN.w || 0.146) * 1000) + ' wide along the eave, ' +
                fmtArea(lArea) + ' of ' + (LIN.code || 'NO CODE') + ' (' + fmtArea(net) + ' of bays, the rafters taken off, + ' + Math.round((((typeof LIN.waste === 'number') ? LIN.waste : 1.10) - 1) * 100) + ' pct) | ' +
                nNail + ' x ' + (LIN.nail || 'NO FIXING CODE -- NOT BILLED') + ' through the tongue into the deck every ' + Math.round((LIN.nailSpacing || 0.30) * 1000) + ' mm; the rafters stay in sight, nothing above moves');
      }
      var withCuts = area * ((typeof D.waste === 'number') ? D.waste : 1.05);
      var sheets = Math.ceil(withCuts / (SS.l * SS.w));
      billAdd(layer, D.code, withCuts);
      if (D.nail && D.nailPerM2) {
        billAdd(layer, D.nail, Math.round(area * D.nailPerM2));
        ctx.log('FIX  deck ' + Math.round(area * 10) / 10 + ' m2 -> ' + Math.round(area * D.nailPerM2) + ' x ' + D.nail +
                ' at ' + D.nailPerM2 + '/m2 (150 at the edges, 300 in the field) -- charged on the measured area, not on the cutting allowance');
      }
      ctx.log('skin     deck ' + fmtArea(withCuts) + ' over ' + made + ' plane(s) (' + fmtArea(area) + ' net + 8 pct), joints at ' +
              Math.round(SS.l * 1000) + 'x' + Math.round(SS.w * 1000) + ', about ' + sheets + ' sheet(s) of ' + Math.round(D.t * 1000) + ' mm');
      return { area: area };
    }


    /* THE INSULATION ABOVE THE RAFTERS (core-v485, from the T house thread,
       Marek: nadkrokevni izolace z PIR desek 100 x 2 = 200 mm). A product that
       puts its insulation over the rafters hands over
         insulation: { code, t, layers, board: { l, w }, waste?,
                       vcl?: { code?, t? }, screw?: { code?, spacing? } }
       and it is laid on the deck: the vapour control layer first (on the
       warm side), then the boards in their layers, each layer's joints
       scored; the underlay, the counter-battens and everything above sit
       that much higher (_rsInsT). Billed: the boards by the square metre of
       each layer with the cutting allowance, the vapour layer by the square
       metre with 10 per cent of laps, the long screws that hold the
       counter-battens down through the boards into the rafters -- each only
       when the product names its code; the log says what was not billed. */
    function _rsInsT(spec) { var I = spec && spec.insulation; return I ? ((I.t || 0) + 0.002) : 0; }
    /* the lining hangs UNDER the deck between the rafters (core-v487), so it
       raises nothing */
    function _rsLinT(spec) { return 0; }
    function _rsInsulation(ctx, spec, planes) {
      var T = ctx.THREE, g = ctx.group, i, I = spec.insulation, D = spec.deck, layer = spec.layer;
      var nL = Math.max(1, I.layers || 1), tL = (I.t || 0) / nL, B = I.board || { l: 1.2, w: 0.6 };
      var matB = new T.MeshStandardMaterial({ color: 0xC9CCD0, roughness: 0.35, metalness: 0.55, side: T.DoubleSide });
      var matV = new T.MeshStandardMaterial({ color: 0x4A5A6A, roughness: 0.8, metalness: 0.0, side: T.DoubleSide });
      var jm = new T.MeshBasicMaterial({ color: 0x8C9096 });
      var base = 0.001 + _rsLinT(spec) + (D ? D.t : 0), area = 0, made = 0, mitreN = 0;
      for (i = 0; i < planes.length; i++) {
        var DP = _rsDeckPlane(planes[i], spec.lift(planes[i]));
        var Q = DP && DP.pts;
        if (!Q || Q.length < 4) continue;
        var n = DP.n || [0, 1, 0];
        var at = function (pt, h) { return [pt[0] + n[0] * h, pt[1] + n[1] * h, pt[2] + n[2] * h]; };
        /* THE BOARDS MEET AT THE RIDGE, THE HIPS AND THE VALLEYS (core-v489, Marek:
           PIR nevyplnuje klin na hrebeni -- serezat v hrebeni ve svisle rovine,
           aby na sebe dosedaly). Lifted along its own normal, each side's slab
           left a V open over the ridge. A corner that lies on another plane's
           outline too is moved instead to where the two offset surfaces meet:
           the point m with n_A . m = h and n_B . m = h, square to the shared
           edge -- m = h (n_A + n_B) / (1 + n_A . n_B). The cut face between the
           two sides then stands in the bisecting plane, which on a ridge of two
           equal slopes is the vertical plane through the ridge. */
        var nbN = [], P0 = planes[i].pts || [], qn, jn, kn;
        for (qn = 0; qn < 4; qn++) {
          var own = P0[qn], got = [];
          if (own) for (jn = 0; jn < planes.length; jn++) {
            if (jn === i || !planes[jn].pts) continue;
            var nj = planes[jn].n || [0, 1, 0];
            if (n[0] * nj[0] + n[1] * nj[1] + n[2] * nj[2] > 0.999) continue;
            for (kn = 0; kn < planes[jn].pts.length; kn++) {
              var o2 = planes[jn].pts[kn];
              if (Math.abs(o2[0] - own[0]) < 0.003 && Math.abs(o2[1] - own[1]) < 0.003 && Math.abs(o2[2] - own[2]) < 0.003) { got.push(nj); break; }
            }
          }
          nbN.push(got.length ? got[0] : null);
        }
        var atM = function (qi, h) {
          var nb = nbN[qi], pt = Q[qi];
          if (!nb) return at(pt, h);
          var c = n[0] * nb[0] + n[1] * nb[1] + n[2] * nb[2], f = h / (1 + c);
          return [pt[0] + (n[0] + nb[0]) * f, pt[1] + (n[1] + nb[1]) * f, pt[2] + (n[2] + nb[2]) * f];
        };
        var mitred = 0; for (qn = 0; qn < 4; qn++) if (nbN[qn]) mitred++;
        mitreN += mitred;
        /* the vapour control layer, on the deck */
        var vV = [];
        _rsQuad(vV, at(Q[0], base + 0.001), at(Q[1], base + 0.001), at(Q[2], base + 0.001), at(Q[3], base + 0.001));
        var gV = new T.BufferGeometry(); gV.setAttribute('position', new T.Float32BufferAttribute(vV, 3)); gV.computeVertexNormals();
        g.add(new T.Mesh(gV, matV));
        var k;
        for (k = 0; k < nL; k++) {
          var h0 = base + 0.002 + k * tL, h1 = h0 + tL;
          var lo = [atM(0, h0), atM(1, h0), atM(2, h0), atM(3, h0)], hi = [atM(0, h1), atM(1, h1), atM(2, h1), atM(3, h1)];
          var vv = [], q;
          _rsQuad(vv, hi[0], hi[1], hi[2], hi[3]);
          _rsQuad(vv, lo[3], lo[2], lo[1], lo[0]);
          for (q = 0; q < 4; q++) { var r = (q + 1) % 4; _rsQuad(vv, lo[q], hi[q], hi[r], lo[r]); }
          var geo = new T.BufferGeometry(); geo.setAttribute('position', new T.Float32BufferAttribute(vv, 3)); geo.computeVertexNormals();
          var m = new T.Mesh(geo, matB); m.name = 'insulation'; g.add(m);
          /* the board joints on each layer's face; the layers' joints are
             staggered by laying the second one the other way round */
          _rsDeckGrid(T, g, jm, Q, n, h1, (k % 2) ? B.w : B.l, (k % 2) ? B.l : B.w, _rsAxes(DP));
        }
        var tri = function (a, b, c) {
          var ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2], wx = c[0] - a[0], wy = c[1] - a[1], wz = c[2] - a[2];
          return Math.hypot(uy * wz - uz * wy, uz * wx - ux * wz, ux * wy - uy * wx) / 2;
        };
        area += tri(Q[0], Q[1], Q[2]) + tri(Q[0], Q[2], Q[3]);
        made++;
      }
      if (!made) { ctx.log('skin     insulation NONE LAID -- ' + planes.length + ' plane(s) offered'); return { area: 0 }; }
      var waste = (typeof I.waste === 'number') ? I.waste : 1.05, boards = area * nL * waste;
      if (I.code) billAdd(layer, I.code, boards);
      var V = I.vcl || {}, S = I.screw || {}, sp = spec.spacing || 0.9, nScr = Math.ceil(area / (sp * ((S.spacing) || 0.6)));
      if (V.code) billAdd(layer, V.code, area * 1.10);
      /* its laps taped, one roll width apart (core-v487) */
      var vTape = V.tape ? area / (V.roll || 1.5) : 0;
      if (V.tape) billAdd(layer, V.tape, vTape);
      if (S.code) billAdd(layer, S.code, nScr);
      /* the boards' joints on the top layer taped, 2.5 m of joint a square
         metre for 1200 x 600 boards (core-v488) */
      var jTape = I.tape ? area * 2.5 : 0;
      if (I.tape) billAdd(layer, I.tape, jTape);
      ctx.log('skin     insulation above the rafters: ' + nL + ' layer(s) of ' + Math.round(tL * 1000) + ' mm = ' + Math.round((I.t || 0) * 1000) + ' mm, ' +
              fmtArea(boards) + ' of ' + (I.code || 'NO CODE') + ' (' + fmtArea(area) + ' x ' + nL + ' + ' + Math.round((waste - 1) * 100) + ' pct), boards ' +
              Math.round(B.l * 1000) + ' x ' + Math.round(B.w * 1000) + ', second layer turned so the joints do not line up');
      ctx.log('skin     under it a vapour control layer ' + fmtArea(area * 1.10) + (V.code ? (' ' + V.code) : ' -- NOT BILLED, no catalogue code yet') +
              (V.tape ? (', laps taped ' + (Math.round(vTape * 10) / 10) + ' m ' + V.tape) : ', laps NOT taped (no tape code)') +
              ' | the counter-battens held down through it by about ' + nScr + ' long screw(s), one per ' + Math.round(((S.spacing) || 0.6) * 1000) + ' mm of batten' +
              (S.code ? (' ' + S.code) : ' -- NOT BILLED, no catalogue code yet'));
      ctx.log('skin     insulation cut where the planes meet: ' + mitreN + ' corner(s) moved onto the line where the two sides\' boards meet (ridge, hips, valleys) -- no open wedge');
      ctx.log('skin     top layer joints taped ' + (I.tape ? ((Math.round(jTape * 10) / 10) + ' m ' + I.tape) : 'NOT billed, no tape code'));
      ctx.log('skin     everything above the deck sits ' + Math.round(_rsInsT(spec) * 1000) + ' mm higher: underlay, battens, covering, flashings, ridge, gutters');
      return { area: area };
    }


    /* THE INSULATION BETWEEN THE RAFTERS AND THE CEILING UNDER THEM (core-v488,
       from the T house thread; the layers and their rates are the ADU's
       ceiling, adu-v1396: CEIL_VB, CEIL_BATTEN, CEIL_BOARD). A product hands
       ceiling: { rafterD, net?, wool: { code, t, waste? },
                  vcl: { code, tape?, roll? },
                  battens: { code, w, h, spacing, screw? },
                  board: { code, t, screw?, perM2?, tape?, filler?, fillerKg? } }
       and under every plane, following its slope: mineral wool in the bays
       from the rafters' underside up (an air gap left under the deck), the
       vapour control layer across the rafters' underside, service battens
       across the rafters at their spacing, the gypsum board on them. Billed:
       the wool on the bays, the barrier with 10 per cent of laps and its
       tape, the battens by the metre and their screws (a run every 500 plus
       two an end, the ADU's count), the board with 5 per cent and its 20
       screws a square metre, the joints (area / 2.88 x 3.6 m of tape) and
       the filler by the kilogram. */
    function _rsCeiling(ctx, spec, planes) {
      var T = ctx.THREE, g = ctx.group, i, CE = spec.ceiling, layer = spec.layer;
      var W = CE.wool || {}, V = CE.vcl || {}, BT = CE.battens || { w: 0.05, h: 0.03, spacing: 0.4 }, BD = CE.board || { t: 0.0125 };
      var rD = CE.rafterD || 0.18, net = (typeof CE.net === 'number') ? CE.net : 1;
      var mW = new T.MeshStandardMaterial({ color: 0xE3C66A, roughness: 1.0, metalness: 0.0, side: T.DoubleSide });
      var mV = new T.MeshStandardMaterial({ color: 0x4A5A6A, roughness: 0.8, metalness: 0.0, side: T.DoubleSide });
      var mB = new T.MeshStandardMaterial({ color: 0xB98B57, roughness: 0.9, metalness: 0.0, side: T.DoubleSide });
      var mG = new T.MeshStandardMaterial({ color: 0xEDEAE3, roughness: 0.95, metalness: 0.0, side: T.DoubleSide });
      var jm = new T.MeshBasicMaterial({ color: 0xC9C5BC, side: T.DoubleSide });
      var area = 0, made = 0, bLen = 0, nRuns = 0;
      for (i = 0; i < planes.length; i++) {
        var DP = _rsDeckPlane(planes[i], spec.lift(planes[i]));
        var Q0 = DP && DP.pts;
        if (!Q0 || Q0.length < 3) continue;
        var n = DP.n || [0, 1, 0], AX = _rsAxes(DP);
        var wT = W.t || 0.16;
        /* INSIDE THE WALLS ONLY (core-v517): the plane's piece cut to the
           product's inner outline -- the wool, the barrier, the battens and
           the board stop at the wall, nothing hangs under the eave */
        var pcs = _rsInnerPieces(Q0, spec.inner || null), qc;
        for (qc = 0; qc < pcs.length; qc++) {
          var Q = pcs[qc];
          _rsPrism(T, g, mW, Q, n, -rD, -rD + wT, 'ceiling-wool');
          _rsPrism(T, g, mV, Q, n, -rD - 0.0012, -rD - 0.0002, 'ceiling-vcl');
          var yB = -rD - 0.0012 - (BT.h || 0.03);
          var bl = _rsBatGrid(T, g, mB, Q, n, yB, { w: BT.w || 0.05, h: BT.h || 0.03 }, BT.spacing || 0.4, false, null, null, AX);
          var eLn = Math.hypot(AX.eB[0] - AX.eA[0], AX.eB[2] - AX.eA[2]) || 5;
          bLen += bl; nRuns += Math.max(1, Math.round(bl / Math.max(0.5, eLn)));
          var yG = yB - (BD.t || 0.0125);
          _rsPrism(T, g, mG, Q, n, yG, yB, 'ceiling-board');
          _rsDeckGrid(T, g, jm, Q, n, yG - 0.0006, 2.4, 1.2, AX);
          area += _polyArea3(Q);
          made++;
        }
      }
      if (!made) { ctx.log('skin     ceiling NONE BUILT -- ' + planes.length + ' plane(s) offered'); return { area: 0 }; }
      var woolA = area * net * ((typeof W.waste === 'number') ? W.waste : 1.05);
      if (W.code) billAdd(layer, W.code, woolA);
      var vA = area * 1.10, vT = area / (V.roll || 1.5);
      if (V.code) billAdd(layer, V.code, vA);
      if (V.tape) billAdd(layer, V.tape, vT);
      var bScr = Math.round(bLen / 0.5) + nRuns * 2;
      if (BT.code) billAdd(layer, BT.code, bLen);
      if (BT.screw) billAdd(layer, BT.screw, bScr);
      var gA = area * 1.05, gScr = Math.round(area * (BD.perM2 || 20)), gTape = Math.round(area / 2.88 * 3.6 * 10) / 10, gFill = Math.round(area * (BD.fillerKg || 0.45) * 10) / 10;
      if (BD.code) billAdd(layer, BD.code, gA);
      if (BD.screw) billAdd(layer, BD.screw, gScr);
      if (BD.tape) billAdd(layer, BD.tape, gTape);
      if (BD.filler) billAdd(layer, BD.filler, gFill);
      ctx.log('skin     between the rafters: ' + Math.round((W.t || 0.16) * 1000) + ' mm mineral wool ' + fmtArea(woolA) + ' ' + (W.code || 'NO CODE') + ' (bays only, ' + Math.round((rD - (W.t || 0.16)) * 1000) + ' mm of air left under the deck)');
      ctx.log('skin     under the rafters: vapour barrier ' + fmtArea(vA) + ' ' + (V.code || 'NO CODE') + ', laps ' + (Math.round(vT * 10) / 10) + ' m ' + (V.tape || 'NOT taped') +
              ' | battens ' + (Math.round(bLen * 10) / 10) + ' m ' + (BT.code || 'NO CODE') + ' at ' + Math.round((BT.spacing || 0.4) * 1000) + ' across the rafters, ' + bScr + ' x ' + (BT.screw || 'NO SCREW') +
              ' | gypsum board ' + fmtArea(gA) + ' ' + (BD.code || 'NO CODE') + ', ' + gScr + ' x ' + (BD.screw || 'NO SCREW') + ', ' + gTape + ' m ' + (BD.tape || 'no tape') + ', ' + gFill + ' kg ' + (BD.filler || 'no filler'));
      ctx.log('skin     the ceiling follows the slope under every plane, over ' + fmtArea(area) + ' of roof' + (spec.inner ? ' inside the walls (core-v517)' : ''));
      return { area: area };
    }

    /* STEP 2, THE UNDERLAY (copied from the ADU's _skinUnderlay): courses from the
       eave up, each lapping the one below by a lap that follows the pitch, the
       lap drawn as a raised band; bought from the plane's area times the lap
       factor, with staples */
    /* THE COURSES END ON THE OUTLINE (core-v514, with _rsCovFrame): each
       course was a quad from the eave's ends to the ridge's, so on a piece
       cut at a valley the underlay ran on past the valley like the covering
       did. A course is now a quad on the frame's limits at its bottom and at
       its top, and the courses climb the frame's own slope. */
    function _rsUnderlay(ctx, spec, planes) {
      var T = ctx.THREE, g = ctx.group, i, U = spec.underlay, D = spec.deck, layer = spec.layer, MAG = 0xFF00FF;
      var isNewL = function (nm) { try { return !!(spec.isNew && spec.isNew(layer + '.' + nm)); } catch (e) { return false; } };
      var why = function (s) { try { if (spec.why) spec.why(s); } catch (e) {} };
      var mat = new T.MeshStandardMaterial({ color: isNewL('underlay') ? MAG : 0x3E4A56, roughness: 0.95, metalness: 0.0, side: T.DoubleSide });
      var pitch = (STATE.roof && STATE.roof.pitch) || 26, ulLap = _rsUlLap(pitch);
      var bought = 0, courses = 0, covered = 0;
      for (i = 0; i < planes.length; i++) {
        var DPu = _rsDeckPlane(planes[i], spec.lift(planes[i]));
        var E = DPu && DPu.eave, RD = DPu && DPu.ridge;
        if (!E || !RD || E.length < 2 || RD.length < 2) continue;
        var F = _rsCovFrame(spec, planes, i);
        if (!F) continue;
        var slope = F.slope;
        var lift = 0.001 + _rsLinT(spec) + (D ? D.t : 0) + 0.002 + _rsInsT(spec);
        var cover = Math.max(0.3, U.roll - ulLap);
        var rows2 = Math.max(1, Math.ceil(slope / cover));
        var lapMat = new T.MeshStandardMaterial({ color: isNewL('underlay') ? MAG : 0x38434D, roughness: 0.95, metalness: 0.0, side: T.DoubleSide });
        var r2;
        for (r2 = 0; r2 < rows2; r2++) {
          var v0 = r2 * cover;
          var v1 = Math.min(slope, v0 + U.roll);
          if (v1 - v0 < 0.05) continue;
          var hh2 = lift + r2 * 0.0004;
          var l0 = F.uMinRaw(v0), h0 = F.uMaxRaw(v0), l1 = F.uMinRaw(v1), h1 = F.uMaxRaw(v1);
          if (!(h0 - l0 > 0.002) && !(h1 - l1 > 0.002)) continue;
          var vv = [];
          _rsQuad(vv, F.P(l0, v0, hh2), F.P(l1, v1, hh2), F.P(h1, v1, hh2), F.P(h0, v0, hh2));
          var geo = new T.BufferGeometry();
          geo.setAttribute('position', new T.Float32BufferAttribute(vv, 3));
          geo.computeVertexNormals();
          g.add(new T.Mesh(geo, mat));
          if (v1 < slope - 0.02) {
            var vl0 = v0 + cover, vl1 = Math.min(slope, v0 + U.roll);
            var ll0 = F.uMinRaw(vl0), lh0 = F.uMaxRaw(vl0), ll1 = F.uMinRaw(vl1), lh1 = F.uMaxRaw(vl1);
            if ((lh0 - ll0 > 0.002) || (lh1 - ll1 > 0.002)) {
              var lv = [], up = [0, 0.004, 0];
              var eL = function (u, v) { var p = F.P(u, v, hh2); return [p[0] + up[0], p[1] + up[1], p[2] + up[2]]; };
              _rsQuad(lv, eL(ll0, vl0), eL(ll1, vl1), eL(lh1, vl1), eL(lh0, vl0));
              var lg = new T.BufferGeometry();
              lg.setAttribute('position', new T.Float32BufferAttribute(lv, 3));
              lg.computeVertexNormals();
              g.add(new T.Mesh(lg, lapMat));
            }
          }
        }
        var runLen = Math.hypot(E[1][0] - E[0][0], E[1][2] - E[0][2]);
        var n2 = Math.max(1, Math.ceil(slope / Math.max(0.3, U.roll - ulLap)));
        var lapFactor = U.roll / Math.max(0.3, U.roll - ulLap);
        bought += (planes[i].area || (runLen * slope / 2)) * lapFactor;
        courses += n2;
        covered += planes[i].area || 0;
      }
      if (!courses) return { area: 0 };
      billAdd(layer, U.code, bought);
      var st = Math.round(bought * 12);
      if (U.staple) billAdd(layer, U.staple, st);
      ctx.log('skin     underlay in courses of ' + Math.round(U.roll * 1000) + ' mm lapped ' + Math.round(ulLap * 1000) +
              ', so each covers ' + Math.round((U.roll - ulLap) * 1000) + ' -- laid from the eave up, water crosses every lap from above');
      ctx.log('skin     underlay ' + fmtArea(bought) + ' bought for ' + fmtArea(covered) + ' of roof -- the laps ARE in the bill, at ' +
              Math.round((U.roll / (U.roll - ulLap) - 1) * 100) + ' pct, and drawn as a raised band at every one');
      why('skin     underlay ' + courses + ' course(s) of ' + Math.round(U.roll * 1000) + ' mm lapped ' + Math.round(ulLap * 1000) +
          ' = ' + fmtArea(bought) + ' bought for ' + fmtArea(covered) + ' of roof');
      why('skin     laid EAVE UP, each course over the one below -- lap them the other way and every joint faces uphill and collects what it is meant to shed');
      why('skin     courses stepped along the PLANE, so the roll width is already the slope measurement -- no cosine, which is the thing that leaves a steep roof short of the layer it needs most');
      why('skin     it lies straight under the SHINGLE -- there is nothing between them. Only the sheet coverings get battens, and those go on TOP of this');
      ctx.log('skin     ' + st + ' staple(s), no tape -- a cold roof holds its laps under what goes on top; taping is for a sealed warm deck and this is not one');
      return { area: covered };
    }

    /* where the batten grid starts above the deck plane: the deck, the
       underlay's courses, a hair (ADU _batBase) */
    function _rsBatBase(spec, slope) {
      var D = spec.deck, U = spec.underlay, pitch = (STATE.roof && STATE.roof.pitch) || 26;
      var ulTop = 0.001 + _rsLinT(spec) + (D ? D.t : 0) + 0.002 + _rsInsT(spec) + ((U && U.t) || 0);
      var courses = U ? Math.max(1, Math.ceil((slope || 4) / (U.roll - _rsUlLap(pitch)))) : 0;
      return ulTop + courses * 0.0012 + 0.001;
    }
    /* a counter-batten on every published member: the member's vertical plane
       cut against the roof quad gives its two ends in world coordinates (ADU _batCut) */
    function _rsBatCut(T, g, m, Q, n, lift, sect, members, ctx, AX) {
      if (!members || members.length < 2) return 0;
      var ex = AX ? (AX.eB[0] - AX.eA[0]) : (Q[3][0] - Q[0][0]), ez = AX ? (AX.eB[2] - AX.eA[2]) : (Q[3][2] - Q[0][2]);
      var eL = Math.hypot(ex, ez);
      if (!(eL > 0.05)) return 0;
      ex /= eL; ez /= eL;
      var total = 0, q, drawn = [], seen = [];
      for (q = 0; q < members.length; q++) {
        var MP = members[q];
        if (MP.length >= 4) {
          var mdl = Math.hypot(MP[2], MP[3]) || 1, along = Math.abs((MP[2] * (-ez) + MP[3] * ex) / mdl);
          if (along < 0.7) continue;
        }
        var hit = [], e;
        for (e = 0; e < Q.length; e++) {
          var A = Q[e], B = Q[(e + 1) % Q.length];
          var da = (A[0] - MP[0]) * ex + (A[2] - MP[1]) * ez;
          var db = (B[0] - MP[0]) * ex + (B[2] - MP[1]) * ez;
          if (da === db) continue;
          if ((da > 0) === (db > 0)) continue;
          var t = da / (da - db);
          hit.push([A[0] + (B[0] - A[0]) * t, A[1] + (B[1] - A[1]) * t, A[2] + (B[2] - A[2]) * t]);
        }
        if (hit.length < 2) continue;
        var p1 = hit[0], p2 = hit[1];
        var L = Math.hypot(p2[0] - p1[0], p2[1] - p1[1], p2[2] - p1[2]);
        if (L < 0.15) continue;
        if (Math.abs(p2[1] - p1[1]) < L * 0.05) continue;
        var key = Math.round(((p1[0] + p2[0]) / 2) * 1000) + ':' + Math.round(((p1[2] + p2[2]) / 2) * 1000);
        if (seen.indexOf(key) >= 0) continue;
        seen.push(key);
        var ax = (p2[0] - p1[0]) / L, ay = (p2[1] - p1[1]) / L, az = (p2[2] - p1[2]) / L;
        var sx = ay * n[2] - az * n[1], sy = az * n[0] - ax * n[2], sz = ax * n[1] - ay * n[0];
        var sl = Math.hypot(sx, sy, sz) || 1;
        sx = sx / sl * sect.w / 2; sy = sy / sl * sect.w / 2; sz = sz / sl * sect.w / 2;
        var lo = [], hi = [], k;
        lo.push([p1[0] - sx + n[0] * lift, p1[1] - sy + n[1] * lift, p1[2] - sz + n[2] * lift]);
        lo.push([p1[0] + sx + n[0] * lift, p1[1] + sy + n[1] * lift, p1[2] + sz + n[2] * lift]);
        lo.push([p2[0] + sx + n[0] * lift, p2[1] + sy + n[1] * lift, p2[2] + sz + n[2] * lift]);
        lo.push([p2[0] - sx + n[0] * lift, p2[1] - sy + n[1] * lift, p2[2] - sz + n[2] * lift]);
        for (k = 0; k < 4; k++) hi.push([lo[k][0] + n[0] * sect.h, lo[k][1] + n[1] * sect.h, lo[k][2] + n[2] * sect.h]);
        var vv = [];
        _rsQuad(vv, hi[0], hi[1], hi[2], hi[3]);
        _rsQuad(vv, lo[3], lo[2], lo[1], lo[0]);
        for (k = 0; k < 4; k++) { var f2 = (k + 1) % 4; _rsQuad(vv, lo[k], hi[k], hi[f2], lo[f2]); }
        var geo = new T.BufferGeometry();
        geo.setAttribute('position', new T.Float32BufferAttribute(vv, 3));
        geo.computeVertexNormals();
        g.add(new T.Mesh(geo, m));
        total += L;
        drawn.push(Math.round(((p1[0] + p2[0]) / 2) * 1000) + '/' + Math.round(((p1[2] + p2[2]) / 2) * 1000));
      }
      if (ctx) ctx.log('BAT cut n=' + drawn.length + ' at ' + drawn.slice(0, 6).join(' '));
      return (drawn.length > 1) ? total : 0;
    }
    /* bars on a square grid in the plane's own axes, clipped by its outline;
       up the slope on the published members where there are any, else evenly
       from the middle; across at the covering's gauge (ADU _batGrid) */
    function _rsBatGrid(T, g, m, Q, n, lift, sect, pitch, upSlope, members, ctx, AX) {
      var ux = AX ? (AX.eB[0] - AX.eA[0]) : (Q[3][0] - Q[0][0]), uy = 0, uz = AX ? (AX.eB[2] - AX.eA[2]) : (Q[3][2] - Q[0][2]);
      var ul = Math.hypot(ux, uz);
      if (!(ul > 0.05) || !(pitch > 0.05)) return 0;
      ux /= ul; uz /= ul;
      var vx = uy * n[2] - uz * n[1], vy = uz * n[0] - ux * n[2], vz = ux * n[1] - uy * n[0];
      var vl = Math.hypot(vx, vy, vz) || 1;
      vx /= vl; vy /= vl; vz /= vl;
      var O = Q[0], q, UV = [], lo = { u: 1e9, v: 1e9 }, hi = { u: -1e9, v: -1e9 };
      for (q = 0; q < Q.length; q++) {
        var dx = Q[q][0] - O[0], dy = Q[q][1] - O[1], dz = Q[q][2] - O[2];
        var a = dx * ux + dy * uy + dz * uz, b = dx * vx + dy * vy + dz * vz;
        UV.push([a, b]);
        if (a < lo.u) lo.u = a;
        if (a > hi.u) hi.u = a;
        if (b < lo.v) lo.v = b;
        if (b > hi.v) hi.v = b;
      }
      var W = function (a, b, h) { return [O[0] + ux * a + vx * b + n[0] * h, O[1] + uy * a + vy * b + n[1] * h, O[2] + uz * a + vz * b + n[2] * h]; };
      var span = function (fixed, alongV) {
        var l3 = 1e9, h3 = -1e9, e;
        for (e = 0; e < UV.length; e++) {
          var A = UV[e], B = UV[(e + 1) % UV.length];
          var a0 = alongV ? A[0] : A[1], b0 = alongV ? B[0] : B[1];
          if ((a0 - fixed) * (b0 - fixed) > 0) continue;
          var t = (fixed - a0) / ((b0 - a0) || 1);
          var w = alongV ? (A[1] + (B[1] - A[1]) * t) : (A[0] + (B[0] - A[0]) * t);
          if (w < l3) l3 = w;
          if (w > h3) h3 = w;
        }
        return (h3 - l3 > 0.05) ? [l3, h3] : null;
      };
      var total = 0, k, sp, ks = [];
      var from = upSlope ? lo.u : lo.v, to = upSlope ? hi.u : hi.v;
      if (upSlope && members && members.length) {
        var mq;
        for (mq = 0; mq < members.length; mq++) {
          var MP = members[mq];
          var du = (MP[0] - O[0]) * ux + (MP[1] - O[2]) * uz;
          if (du < from - 0.01 || du > to + 0.01) continue;
          var dup = false, dq;
          for (dq = 0; dq < ks.length; dq++) { if (Math.abs(ks[dq] - du) < 0.05) { dup = true; break; } }
          if (!dup) ks.push(du);
        }
        ks.sort(function (a, b) { return a - b; });
      }
      if (ks.length < 2) {
        ks = [];
        var mid = (from + to) / 2;
        for (k = mid; k >= from - 0.01; k -= pitch) ks.push(k);
        for (k = mid + pitch; k <= to + 0.01; k += pitch) ks.push(k);
        ks.sort(function (a, b) { return a - b; });
      }
      if (!upSlope && (STATE.roof || {}).type === 'mono' && ks.length) {
        var kTop = to - sect.w / 2;
        if (kTop - ks[ks.length - 1] > sect.w) ks.push(kTop);
      }
      if (ctx) {
        var rd = function (v) { return Math.round(v * 1000); };
        ctx.log('BAT ' + (upSlope ? 'up  ' : 'across') + ' pitch ' + rd(pitch) + ' n=' + ks.length + ' bars ' + ks.slice(0, 8).map(rd).join(','));
      }
      var kq;
      for (kq = 0; kq < ks.length; kq++) {
        k = ks[kq];
        sp = span(k, upSlope);
        if (!sp) continue;
        var p1 = upSlope ? W(k, sp[0], lift) : W(sp[0], k, lift);
        var p2 = upSlope ? W(k, sp[1], lift) : W(sp[1], k, lift);
        var L = Math.hypot(p2[0] - p1[0], p2[1] - p1[1], p2[2] - p1[2]);
        if (L < 0.1) continue;
        var vv = [], e2;
        var ax = (p2[0] - p1[0]) / L, ay = (p2[1] - p1[1]) / L, az = (p2[2] - p1[2]) / L;
        var sxn = ay * n[2] - az * n[1], syn = az * n[0] - ax * n[2], szn = ax * n[1] - ay * n[0];
        var sn = Math.hypot(sxn, syn, szn) || 1;
        sxn = sxn / sn * sect.w / 2; syn = syn / sn * sect.w / 2; szn = szn / sn * sect.w / 2;
        var lo4 = [[p1[0] - sxn, p1[1] - syn, p1[2] - szn], [p1[0] + sxn, p1[1] + syn, p1[2] + szn],
                   [p2[0] + sxn, p2[1] + syn, p2[2] + szn], [p2[0] - sxn, p2[1] - syn, p2[2] - szn]];
        var hi4 = [];
        for (e2 = 0; e2 < 4; e2++) hi4.push([lo4[e2][0] + n[0] * sect.h, lo4[e2][1] + n[1] * sect.h, lo4[e2][2] + n[2] * sect.h]);
        _rsQuad(vv, hi4[0], hi4[1], hi4[2], hi4[3]);
        _rsQuad(vv, lo4[3], lo4[2], lo4[1], lo4[0]);
        for (e2 = 0; e2 < 4; e2++) { var f2 = (e2 + 1) % 4; _rsQuad(vv, lo4[e2], hi4[e2], hi4[f2], lo4[f2]); }
        var geo = new T.BufferGeometry();
        geo.setAttribute('position', new T.Float32BufferAttribute(vv, 3));
        geo.computeVertexNormals();
        g.add(new T.Mesh(geo, m));
        total += L;
      }
      return total;
    }

    /* STEP 3, THE BATTENS (copied from the ADU's _skinBattens): nothing under a
       shingle; under a sheet a counter-batten up every published member and
       cross battens at the covering's gauge, both cut to the plane's outline */
    function _rsBattens(ctx, spec, planes) {
      var T = ctx.THREE, g = ctx.group, i, B = spec.battens, C = spec.covering || {}, layer = spec.layer, MAG = 0xFF00FF;
      var isNewL = function (nm) { try { return !!(spec.isNew && spec.isNew(layer + '.' + nm)); } catch (e) { return false; } };
      var mat = new T.MeshStandardMaterial({ color: isNewL('battens') ? MAG : (B.col || 0xB98B57), roughness: 0.9, metalness: 0.0, side: T.DoubleSide });
      ctx.log('skin     battens in: covering=' + (C.kind || '-') + ' planes=' + planes.length);
      if (C.kind === 'shingle') { ctx.log('skin     no battens: shingles are nailed straight to the deck'); return { len: 0 }; }
      var cLen = 0, bLen = 0, made = 0, members = spec.members || null, spacing = spec.spacing || 0.6;
      for (i = 0; i < planes.length; i++) {
        var DP = _rsDeckPlane(planes[i], spec.lift(planes[i]));
        var Q = DP && DP.pts;
        if (!Q || Q.length < 4) continue;
        var nrm = DP.n || [0, 1, 0], AXb = _rsAxes(DP);
        var sx = AXb.rA[0] - AXb.eA[0], sy = AXb.rA[1] - AXb.eA[1], sz = AXb.rA[2] - AXb.eA[2];
        var slope = Math.sqrt(sx * sx + sy * sy + sz * sz);
        if (!(slope > 0.2)) continue;
        var base = _rsBatBase(spec, slope);
        var CB = B.counter, BB = B.batten;
        /* NO COUNTER-BATTEN WITHOUT A DECK (core-v727, TAURUS -- Matt's roof):
           a product may hand counter: null, and the battens then go straight
           across the rafters at the covering's gauge, as a sheet laid with no
           deck and no underlay is built */
        if (CB) {
          /* the assembly marks are the product's to place (core-v463): the ADU
             and the shed mark both batten runs, the sauna marks neither */
          if (spec.mark) { try { spec.mark('roof_counter_battens', 'top', 'Counter battens', CB.code); } catch (eM1) {} }
          var cut = _rsBatCut(T, g, mat, Q, nrm, base, CB, members, ctx, AXb);
          if (cut > 0) cLen += cut;
          else cLen += _rsBatGrid(T, g, mat, Q, nrm, base, CB, spacing, true, null, ctx, AXb);
        }
        if (spec.mark) { try { spec.mark('roof_battens', 'top', 'Roof battens', BB.code); } catch (eM2) {} }
        bLen += _rsBatGrid(T, g, mat, Q, nrm, base + (CB ? CB.h : 0), BB, C.gauge, false, null, null, AXb);
        made++;
      }
      if (!made) { ctx.log('skin     battens NONE DRAWN -- ' + planes.length + ' plane(s) offered'); return { len: 0 }; }
      if (B.counter) billAdd(layer, B.counter.code, cLen);
      billAdd(layer, B.batten.code, bLen);
      var scr = Math.round((cLen + bLen) / 0.6);
      if (B.screw) billAdd(layer, B.screw, scr);
      ctx.log('skin     counter-battens ' + (B.counter ? (Math.round(cLen * 10) / 10 + ' m at ' + Math.round(spacing * 1000)) : 'NONE (no deck -- the battens on the rafters)') + ', battens ' +
              Math.round(bLen * 10) / 10 + ' m at ' + Math.round((C.gauge || 0) * 1000) + ' -- both cut to the plane outline, ' + scr + ' screw(s)');
      return { len: cLen + bLen };
    }

    /* THE THREE LIGHT COVERINGS' OWN NUMBERS (copied from the ADU): a product
       may hand its own in spec.covering.table; these are the defaults */
    var RS_COV = {
      gap: 0.014, ridgeGap: 0.030, monoOver: 0.050,
      shingle: { exposure: 0.143, tab: 0.333, slot: 0.006, t: 0.005, nail: 'FAS00318', nailsPerM2: 20 },
      trapez:  { pitch: 0.333, ribW: 0.075, ribH: 0.038, t: 0.0007, screw: 'FAS60100', screwPerM2: 7 },
      seam:    { pan: 0.530, seamW: 0.022, seamH: 0.025, t: 0.0006, clip: 'FAS60100', clipPerM2: 5 },
      /* the clay tile (core-v475): a course per batten gauge, a tile 300
         wide with a 4 mm joint, 28 thick; the courses are staggered half a
         tile like the shingle's tabs. Its fixing (a tile clip) has no row in
         the catalogue yet, so nail: null -- the log says so */
      tile:    { exposure: 0.330, tab: 0.300, slot: 0.004, t: 0.028, nail: null, nailsPerM2: 0 }
    };
    var RS_TILE_SHADES = [[0.92, 1.00], [0.94, 0.98], [0.955, 1.02], [0.97, 0.99], [1.00, 1.00],
                          [1.015, 1.02], [1.035, 0.985], [1.05, 1.01], [1.07, 0.995]];
    function _rsCovTable(spec) { return (spec.covering && spec.covering.table) || RS_COV; }
    function _rsBatTop(spec, slope) {
      var B = spec.battens, C = spec.covering || {};
      if (C.kind === 'shingle') return (spec.deck ? spec.deck.t : 0) + _rsLinT(spec) + _rsInsT(spec) + 0.004;
      return _rsBatBase(spec, slope) + ((B && B.counter) ? B.counter.h : 0) + ((B && B.batten) ? B.batten.h : 0);
    }
    function _rsCovBase(spec, slope) {
      var C = spec.covering || {};
      if (C.kind === 'shingle') return _rsBatBase(spec, slope);
      return _rsBatTop(spec, slope) + _rsCovTable(spec).gap;
    }
    function _rsCovOut(spec) {
      var C = spec.covering || {}, B = spec.battens;
      return (C.kind === 'shingle') ? 0.020 : ((B && B.counter) ? B.counter.w : 0.05) / 2 + 0.015;
    }
    function _rsCovOutV(spec) {
      var C = spec.covering || {}, B = spec.battens;
      return (C.kind === 'shingle') ? 0.020 : ((B && B.batten) ? B.batten.w : 0.05) / 2 + 0.015;
    }
    function _rsCovTopV(spec, F) {
      var mono = (STATE.roof || {}).type === 'mono', TB = _rsCovTable(spec);
      return F.slope + F.lapV + (mono ? TB.monoOver : -TB.ridgeGap);
    }
    function _rsBatMesh(T, g, mat, v) {
      if (!v.length) return;
      var geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.Float32BufferAttribute(v, 3));
      var uv = new Float32Array((v.length / 3) * 2), q;
      for (q = 0; q < v.length / 3; q++) { uv[q * 2] = v[q * 3] * 2; uv[q * 2 + 1] = v[q * 3 + 2] * 2; }
      geo.setAttribute('uv', new T.BufferAttribute(uv, 2));
      geo.computeVertexNormals();
      g.add(new T.Mesh(geo, mat));
    }
    /* THE FRAME OF A PLANE: eave direction u, true up-slope v (the eave's
       sideways part taken out of it), limits at any height, and the run past
       the folds on a hip so the sheets meet where their surfaces cross (ADU _covFrame) */
    /* THE LIMITS COME FROM THE OUTLINE, NOT FROM THE EAVE AND THE RIDGE
       (core-v514, from the T house thread, Marek: vrstvy krytiny pokracuji i
       tam kde nemaji -- krytina main run do side run a obracene, maji koncit
       v uzlabi). A plane cut at a valley keeps the eave and the ridge of the
       whole slope it was cut from (roofPlanes copies them onto every piece),
       and the frame read its width off those two lines alone -- so every
       piece of the bar's slope was covered as the whole slope, four times
       over, and the wing's slopes ran on into the bar up to its ridge. The
       width at any height is now the outline's own: the plane's corners in
       the frame's (u, v), and the span of the edges the line v crosses. A
       rectangle or a hip trapezoid gives exactly what the eave and the ridge
       gave, so every other product's roof is unchanged. The hip lap goes only
       on a side that narrows going up -- a hip; a side that widens is a valley
       and a plumb side is a rake or a cut, and neither gets it. */
    function _rsCovFrame(spec, planes, i) {
      var DP = _rsDeckPlane(planes[i], spec.lift(planes[i]));
      var E = DP && DP.eave, RD = DP && DP.ridge;
      if (!E || !RD) return null;
      var n = DP.n;
      var ax = E[1][0] - E[0][0], ay = E[1][1] - E[0][1], az = E[1][2] - E[0][2];
      var runLen = Math.hypot(ax, ay, az);
      var bx = RD[0][0] - E[0][0], by = RD[0][1] - E[0][1], bz = RD[0][2] - E[0][2];
      var slope = Math.hypot(bx, by, bz);
      if (!(runLen > 0.2) || !(slope > 0.2)) return null;
      ax /= runLen; ay /= runLen; az /= runLen;
      var bDot = bx * ax + by * ay + bz * az;
      bx -= ax * bDot; by -= ay * bDot; bz -= az * bDot;
      slope = Math.hypot(bx, by, bz);
      if (!(slope > 0.2)) return null;
      bx /= slope; by /= slope; bz /= slope;
      var R0 = RD[0], R1 = RD[1];
      var dL = (R0[0] - E[0][0]) * ax + (R0[1] - E[0][1]) * ay + (R0[2] - E[0][2]) * az;
      var dR = (R1[0] - E[1][0]) * ax + (R1[1] - E[1][1]) * ay + (R1[2] - E[1][2]) * az;
      var hipCov = !!(STATE.roof && STATE.roof.type === 'hip');
      var lapU = 0, lapV = 0;
      if (hipCov) {
        var hCov = _rsCovBase(spec, slope);
        var cPc = Math.abs(n[1]), sPc = Math.hypot(n[0], n[2]);
        lapV = (cPc > 1e-6) ? (hCov * sPc / cPc) : 0;
        var dihC = Math.acos(Math.max(-1, Math.min(1, cPc * cPc)));
        lapU = hCov * Math.tan(dihC / 2);
      }
      /* the outline in the frame's own (u, v) */
      var UV = [], q, PT = DP.pts || [], vLo = 1e9, vHi = -1e9, uLoAll = 1e9, uHiAll = -1e9;
      for (q = 0; q < PT.length; q++) {
        var dx = PT[q][0] - E[0][0], dy = PT[q][1] - E[0][1], dz = PT[q][2] - E[0][2];
        var uu = dx * ax + dy * ay + dz * az, vv = dx * bx + dy * by + dz * bz;
        UV.push([uu, vv]);
        if (vv < vLo) vLo = vv;
        if (vv > vHi) vHi = vv;
        if (uu < uLoAll) uLoAll = uu;
        if (uu > uHiAll) uHiAll = uu;
      }
      var hasPoly = (UV.length >= 3) && (vHi - vLo > 0.05);
      /* the outline's span at a height v up the slope: the edges the line
         crosses; below the lowest corner or above the highest the nearest
         span holds, so a course past the eave still runs the eave's width.
         Each side says whether it narrows going up (a hip, which gets the lap). */
      var spanAt = function (v) {
        var vc = Math.max(vLo, Math.min(vHi, v)), lo = 1e9, hi = -1e9, loHip = false, hiHip = false, e, m = UV.length;
        for (e = 0; e < m; e++) {
          var A = UV[e], B = UV[(e + 1) % m], a = A[1] - vc, b = B[1] - vc;
          if (a * b > 1e-12) continue;
          var dv = B[1] - A[1], u1, u2, lean = 0;
          if (Math.abs(dv) < 1e-9) { u1 = A[0]; u2 = B[0]; }
          else { u1 = u2 = A[0] + (B[0] - A[0]) * ((vc - A[1]) / dv); lean = (B[0] - A[0]) / dv; }
          var lowU = Math.min(u1, u2), highU = Math.max(u1, u2);
          /* at a corner two edges meet on one u: the lap is the hip's whichever comes first */
          if (lowU < lo - 1e-9) { lo = lowU; loHip = lean > 1e-6; } else if (lowU < lo + 1e-9) { loHip = loHip || lean > 1e-6; }
          if (highU > hi + 1e-9) { hi = highU; hiHip = lean < -1e-6; } else if (highU > hi - 1e-9) { hiHip = hiHip || lean < -1e-6; }
        }
        if (lo > hi) return null;
        return { lo: lo, hi: hi, loHip: loHip, hiHip: hiHip };
      };
      var tAt = function (v) { var t = (slope > 0) ? (v / slope) : 0; return Math.max(0, Math.min(1, t)); };
      return {
        runLen: runLen, slope: slope, n: n, lapU: lapU, lapV: lapV,
        vLo: hasPoly ? vLo : 0, vHi: hasPoly ? vHi : slope,
        uLoAll: hasPoly ? uLoAll : Math.min(0, dL), uHiAll: hasPoly ? uHiAll : (runLen + Math.max(0, dR)),
        uv: hasPoly ? UV : null,
        uMin: function (v) {
          if (hasPoly) { var s = spanAt(v); return s ? (s.lo - (s.loHip ? lapU : 0)) : 1e9; }
          return dL * tAt(v) - lapU;
        },
        uMax: function (v) {
          if (hasPoly) { var s2 = spanAt(v); return s2 ? (s2.hi + (s2.hiHip ? lapU : 0)) : -1e9; }
          return runLen + dR * tAt(v) + lapU;
        },
        /* the same limits without the hip lap, for a layer cut on the line */
        uMinRaw: function (v) { if (hasPoly) { var s3 = spanAt(v); return s3 ? s3.lo : 1e9; } return dL * tAt(v); },
        uMaxRaw: function (v) { if (hasPoly) { var s4 = spanAt(v); return s4 ? s4.hi : -1e9; } return runLen + dR * tAt(v); },
        P: function (u, v, h) { return [E[0][0] + ax * u + bx * v + n[0] * h, E[0][1] + ay * u + by * v + n[1] * h, E[0][2] + az * u + bz * v + n[2] * h]; }
      };
    }
    /* shingles: courses at the exposure, tabs staggered half a tab, a 6 mm slot
       between tabs (ADU _covShingle) */
    function _rsCovShingle(ctx, spec, planes, tk) {
      var T = ctx.THREE, g = ctx.group, i, C = spec.covering, K = _rsCovTable(spec)[tk || 'shingle'] || _rsCovTable(spec).shingle, layer = spec.layer, MAG = 0xFF00FF;
      var mag = false; try { mag = !!(spec.isNew && spec.isNew(layer + '.covering')); } catch (e) {}
      var granTx = null; try { granTx = (mag || tk === 'tile') ? null : (spec.tex ? spec.tex('shingle', C.color) : null); } catch (e2) { granTx = null; }
      var mat = new T.MeshStandardMaterial({ color: mag ? MAG : (granTx ? 0xFFFFFF : C.color), map: granTx || null, roughness: (tk === 'tile') ? 0.8 : 0.96, metalness: 0.0, side: T.DoubleSide });
      var area = 0, courses = 0;
      for (i = 0; i < planes.length; i++) {
        var F = _rsCovFrame(spec, planes, i);
        if (!F) continue;
        var base = _rsCovBase(spec, F.slope);
        var vRidge = _rsCovTopV(spec, F);
        var nC = Math.max(1, Math.ceil((F.slope + F.lapV) / K.exposure));
        var oV = _rsCovOutV(spec);
        for (var c = 0; c < nC; c++) {
          var v0 = (c === 0) ? -oV : c * K.exposure;
          var v1 = Math.min(vRidge, c * K.exposure + K.exposure + 0.004);
          if (c === nC - 1) v1 = vRidge;
          var up = base + c * 0.0012;
          var vv = [];
          var off = (c % 2) ? K.tab / 2 : 0;
          /* THE COURSE IS CUT ON THE VALLEY AND THE HIP, NOT SQUARE (core-v666,
             RANCH thread, from the tile's core-v490): a course ran between
             the NARROWER of its two ends, so where the outline widens going
             up -- a valley, a cross gable's two valleys -- every course
             stopped short at its top and the felt showed in a saw-tooth up
             the valley. Now the course runs from its outermost end and each
             tab is clipped on the two sides' lines between the course's
             bottom and top (the tab a polygon, not a box): the shingles end
             on the slanting line like cut shingles do. The billed area is
             the plane's, as before. */
          var lo0 = F.uMin(v0), hi0 = F.uMax(v0), lo1 = F.uMin(v1), hi1 = F.uMax(v1);
          if (!(hi0 >= lo0)) { lo0 = lo1; hi0 = hi1; }
          if (!(hi1 >= lo1)) { lo1 = lo0; hi1 = hi0; }
          var uLo = Math.min(lo0, lo1), uHi = Math.max(hi0, hi1), dV = v1 - v0;
          if (!(uHi - uLo > 0.01) || !(uLo > -1e4) || !(uHi < 1e4) || !(dV > 0.002)) { courses++; continue; }
          var cut = function (Pg, a0, a1, sg) {
            var out = [], n = Pg.length, k2;
            for (k2 = 0; k2 < n; k2++) {
              var A2 = Pg[k2], B2 = Pg[(k2 + 1) % n];
              var fa = sg * (A2[0] - (a0 + (a1 - a0) * (A2[1] - v0) / dV)), fb = sg * (B2[0] - (a0 + (a1 - a0) * (B2[1] - v0) / dV));
              if (fa >= 0) out.push(A2);
              if ((fa >= 0) !== (fb >= 0)) { var t2 = fa / (fa - fb); out.push([A2[0] + (B2[0] - A2[0]) * t2, A2[1] + (B2[1] - A2[1]) * t2]); }
            }
            return out;
          };
          var uS = -off;
          while (uS > uLo) uS -= K.tab;
          for (var u0 = uS; u0 < uHi; u0 += K.tab) {
            var ua = u0, ub = u0 + K.tab - K.slot;
            if (ub <= uLo || ua >= uHi) continue;
            var pg = cut(cut([[ua, v0], [ub, v0], [ub, v1], [ua, v1]], lo0, lo1, 1), hi0, hi1, -1), np = pg.length;
            if (np < 3) continue;
            var ar = 0, q;
            for (q = 0; q < np; q++) { var r0 = (q + 1) % np; ar += pg[q][0] * pg[r0][1] - pg[r0][0] * pg[q][1]; }
            if (Math.abs(ar) / 2 < 0.0005) continue;
            var lo = [], hi = [];
            for (q = 0; q < np; q++) { lo.push(F.P(pg[q][0], pg[q][1], up)); hi.push(F.P(pg[q][0], pg[q][1], up + K.t)); }
            for (q = 1; q + 1 < np; q++) {
              vv.push(lo[0][0], lo[0][1], lo[0][2], lo[q + 1][0], lo[q + 1][1], lo[q + 1][2], lo[q][0], lo[q][1], lo[q][2]);
              vv.push(hi[0][0], hi[0][1], hi[0][2], hi[q][0], hi[q][1], hi[q][2], hi[q + 1][0], hi[q + 1][1], hi[q + 1][2]);
            }
            for (q = 0; q < np; q++) { var r = (q + 1) % np; _rsQuad(vv, lo[q], lo[r], hi[r], hi[q]); }
          }
          _rsBatMesh(T, g, mat, vv);
          courses++;
        }
        area += planes[i].area || 0;
      }
      if (!area) return { area: 0 };
      billAdd(layer, C.code, area);
      var nails = K.nail ? Math.round(area * K.nailsPerM2) : 0;
      if (K.nail) billAdd(layer, K.nail, nails);
      ctx.log('skin     covering ' + C.def + ' (' + C.code + ') ' + fmtArea(area) + ' in ' + courses + ' course(s) at ' +
              Math.round(K.exposure * 1000) + ' mm ' + (tk === 'tile' ? 'gauge' : 'exposure') + ', ' + (tk === 'tile' ? 'tiles ' : 'tabs ') + Math.round(K.tab * 1000) + ' mm, ' +
              (K.nail ? (nails + ' nail(s)') : 'fixing NOT BILLED -- no tile clip row in the catalogue yet'));
      try { if (spec.why) spec.why('skin     courses staggered half a tab, so no two slots line up -- which is how they are laid, not a pattern chosen to look busy'); } catch (e3) {}
      return { area: area };
    }
    /* THE CLAY TILE AS IT LOOKS (core-v476, Marek: tasky maximalne
       realisticke). Interlocking tiles lie in straight columns, not staggered:
       every tile is a wave across -- a roll and a pan, 300 a tile -- a course
       per batten gauge, each tile 80 longer than the gauge so it laps the
       course above, and tilted: its lower edge rests on the course below, so
       every course shows a shadowed front edge. Three shades of fired clay
       from course to course, so the roof does not read as one plastic sheet.
       Cut to the plane's outline course by course, like the shingle, so hips
       and valleys stay clean. */
    function _rsCovTile(ctx, spec, planes) {
      var T = ctx.THREE, g = ctx.group, i, C = spec.covering, K = _rsCovTable(spec).tile || RS_COV.tile, layer = spec.layer;
      var base0 = (C.color != null) ? C.color : 0xA4502E;
      var shade = function (f) {
        var r = ((base0 >> 16) & 255) * f, gg = ((base0 >> 8) & 255) * f, b = (base0 & 255) * f;
        return (Math.min(255, Math.round(r)) << 16) | (Math.min(255, Math.round(gg)) << 8) | Math.min(255, Math.round(b));
      };
      /* NINE SHADES, SCATTERED TILE BY TILE (core-v483, migrated from the T
         house, thouse-v230, Marek: barvy tasek ne v pruzich ale chaoticky
         rozmistene, i vice velice blizkych odstinu). The three shades went
         course by course and read as stripes. Now nine close shades round
         the picked colour -- 8 per cent darker to 8 lighter, the old three
         among them, each a hair warmer or cooler -- and each tile takes one
         by a hash of where it lies: scattered, and the same on every build.
         One mesh per shade per plane. */
      var TS = RS_TILE_SHADES, mats = TS.map(function (S) {
        var r = ((base0 >> 16) & 255) * S[0] * (2 - S[1]), gg = ((base0 >> 8) & 255) * S[0] * S[1], b = (base0 & 255) * S[0];
        var cl = function (x) { return Math.max(0, Math.min(255, Math.round(x))); };
        return new T.MeshStandardMaterial({ color: (cl(r) << 16) | (cl(gg) << 8) | cl(b), roughness: 0.78, metalness: 0.0, side: T.DoubleSide });
      });
      var hash3 = function (a, b, c) { var h = Math.sin(a * 127.1 + b * 311.7 + c * 74.7) * 43758.5453; return h - Math.floor(h); };
      var A = 0.022, SEG = 6, head = 0.080, area = 0, courses = 0, tiles = 0;
      for (i = 0; i < planes.length; i++) {
        var F = _rsCovFrame(spec, planes, i);
        if (!F) continue;
        var base = _rsCovBase(spec, F.slope), vRidge = _rsCovTopV(spec, F), oV = _rsCovOutV(spec);
        var nC = Math.max(1, Math.ceil((F.slope + F.lapV) / K.exposure));
        var bk = TS.map(function () { return []; }), kb;
        for (var c = 0; c < nC; c++) {
          var v0 = (c === 0) ? -oV : c * K.exposure;
          var v1 = Math.min(vRidge, c * K.exposure + K.exposure + head);
          if (c === nC - 1) v1 = vRidge;
          if (v1 - v0 < 0.02) continue;
          /* THE COURSE IS CUT ALONG THE HIP, NOT SQUARE (core-v490, Marek: tasky
             dorezane az k hrebenum a narozi, jsou tam videt nedodelky). A course
             took the narrower of its two ends and stopped square there, so at
             every hip each course left a triangle of bare battens. Now the
             course runs from the outermost end, and every step of it is
             clamped to the plane's own edge at its bottom and at its top, so
             the tiles end on the slanting hip line like cut tiles do. */
          var lo0 = F.uMin(v0), hi0 = F.uMax(v0), lo1 = F.uMin(v1), hi1 = F.uMax(v1);
          var uLo = Math.min(lo0, lo1), uHi = Math.max(hi0, hi1);
          if (uHi - uLo < 0.02) continue;
          var hAt = function (u, v) {
            var s2 = (v - v0) / (v1 - v0);
            var ph = ((u % K.tab) + K.tab) % K.tab / K.tab;
            return base + 0.004 + K.t * (1 - s2) + A * (0.5 - 0.5 * Math.cos(2 * Math.PI * ph));
          };
          var cl = function (x, a, b) { return Math.max(a, Math.min(b, x)); };
          var du = K.tab / SEG, u = uLo, un;
          while (u < uHi - 1e-6) {
            un = Math.min(uHi, (Math.floor(u / du + 1e-6) + 1) * du);
            var uA0 = cl(u, lo0, hi0), uB0 = cl(un, lo0, hi0), uA1 = cl(u, lo1, hi1), uB1 = cl(un, lo1, hi1);
            if (uB0 - uA0 < 0.002 && uB1 - uA1 < 0.002) { u = un; continue; }
            var vv = bk[Math.floor(hash3(i + 1, c, Math.floor(((u + un) / 2) / K.tab)) * TS.length) % TS.length];
            var a0 = F.P(uA0, v0, hAt(uA0, v0)), b0 = F.P(uB0, v0, hAt(uB0, v0)), b1 = F.P(uB1, v1, hAt(uB1, v1)), a1 = F.P(uA1, v1, hAt(uA1, v1));
            _rsQuad(vv, a0, b0, b1, a1);
            /* the front edge of the tile, down to the course below */
            if (uB0 - uA0 > 0.002) _rsQuad(vv, F.P(uA0, v0, hAt(uA0, v0) - 0.016), F.P(uB0, v0, hAt(uB0, v0) - 0.016), b0, a0);
            u = un;
          }
          courses++;
          tiles += Math.ceil((uHi - uLo) / K.tab);
        }
        for (kb = 0; kb < bk.length; kb++) _rsBatMesh(T, g, mats[kb], bk[kb]);
        area += planes[i].area || 0;
      }
      if (!area) return { area: 0 };
      billAdd(layer, C.code, area);
      /* ONE CLIP PER TILE (core-v478, merged from the T house thread; the
         catalogue's ROA00003 ROOF TILE CLIP: one clip per tile): the product
         names the clip in covering.clip */
      if (C.clip) billAdd(layer, C.clip, tiles);
      ctx.log('skin     covering ' + C.def + ' (' + C.code + ') ' + fmtArea(area) + ': ' + courses + ' course(s) at ' + Math.round(K.exposure * 1000) + ' gauge, about ' + tiles +
              ' interlocking tiles 300 wide in columns, lapped ' + Math.round(head * 1000) + ', ' + TS.length + ' shades scattered tile by tile -- ' +
              (C.clip ? (tiles + ' tile clips ' + C.clip + ', one per tile') : 'fixing NOT BILLED, the product names no clip'));
      return { area: area };
    }
    /* a sheet covering, trapezoidal or standing seam: one pan per plane with the
       ribs or seams up the slope, each cut where it leaves the plane on a hip
       (ADU _covSheet) */
    /* THE PAN FOLLOWS THE OUTLINE AND A RIB MAY START ABOVE THE EAVE
       (core-v514, with _rsCovFrame): the pan was one quad from the eave's
       limits to the ridge's, and a rib was scanned from the eave up to where
       it left the plane. A piece cut at a valley is no quad, and a rib on it
       can begin on the valley, above the eave, and end on the ridge. The pan
       is laid in bands between the heights of the outline's corners, each
       band on the frame's own limits; a rib is scanned for where it enters
       the outline as well as where it leaves it. */
    function _rsCovSheet(ctx, spec, planes, seam) {
      var T = ctx.THREE, g = ctx.group, i, C = spec.covering, TB = _rsCovTable(spec), K = seam ? TB.seam : TB.trapez, layer = spec.layer, MAG = 0xFF00FF;
      var mag = false; try { mag = !!(spec.isNew && spec.isNew(layer + '.covering')); } catch (e) {}
      var mat = new T.MeshStandardMaterial({ color: mag ? MAG : C.color, roughness: 0.45, metalness: 0.6, side: T.DoubleSide });
      var area = 0, ribs = 0;
      var step = seam ? K.pan : K.pitch;
      for (i = 0; i < planes.length; i++) {
        var F = _rsCovFrame(spec, planes, i);
        if (!F) continue;
        var base = _rsCovBase(spec, F.slope);
        var vv = [];
        var oV = _rsCovOutV(spec);
        var vTop = _rsCovTopV(spec, F);
        /* the pan, in bands: the eave band runs oV past the eave on the eave's
           width, then a band from every corner height to the next, the last
           one to the top */
        var vs = [F.vLo], q, vq;
        if (F.uv) for (q = 0; q < F.uv.length; q++) { var vc = F.uv[q][1]; if (vc > F.vLo + 1e-6 && vc < vTop - 1e-6) vs.push(vc); }
        vs.push(vTop);
        vs.sort(function (a1, b1) { return a1 - b1; });
        var band = function (va, vb, vaLim, vbLim) {
          var l0 = F.uMin(vaLim), h0 = F.uMax(vaLim), l1 = F.uMin(vbLim), h1 = F.uMax(vbLim);
          if (!(h0 - l0 > 0.002) && !(h1 - l1 > 0.002)) return;
          _rsQuad(vv, F.P(l0, va, base), F.P(h0, va, base), F.P(h1, vb, base), F.P(l1, vb, base));
        };
        band(F.vLo - oV, F.vLo, F.vLo, F.vLo);
        for (q = 0; q + 1 < vs.length; q++) { if (vs[q + 1] - vs[q] > 1e-6) band(vs[q], vs[q + 1], vs[q], vs[q + 1]); }
        var inside = function (a, b, vt) { return !(a < F.uMin(vt) || b > F.uMax(vt)); };
        for (var u = Math.max(step, Math.ceil((F.uLoAll - F.lapU - step) / step) * step); u < F.uHiAll + F.lapU - 0.01; u += step) {
          var w = seam ? K.seamW : K.ribW, h = seam ? K.seamH : K.ribH;
          var a = u - w / 2, b = u + w / 2;
          var t0 = base + K.t, t1 = base + h;
          /* the samples the rib is inside on, its first and its last */
          var first = -1, last = -1, vt;
          for (vq = 0; vq <= 24; vq++) {
            vt = (vTop + oV) * (vq / 24) - oV;
            if (inside(a, b, vt)) { if (first < 0) first = vq; last = vq; }
            else if (first >= 0) break;
          }
          if (first < 0) continue;
          var vStart = (vTop + oV) * (first / 24) - oV, vEnd = (vTop + oV) * (last / 24) - oV, bi, bMid;
          if (first > 0) {
            var sLo = (vTop + oV) * ((first - 1) / 24) - oV, sHi = vStart;
            for (bi = 0; bi < 30; bi++) { bMid = (sLo + sHi) / 2; if (inside(a, b, bMid)) sHi = bMid; else sLo = bMid; }
            vStart = sHi;
          }
          if (last < 24) {
            var eLo = vEnd, eHi = (vTop + oV) * ((last + 1) / 24) - oV;
            for (bi = 0; bi < 30; bi++) { bMid = (eLo + eHi) / 2; if (inside(a, b, bMid)) eLo = bMid; else eHi = bMid; }
            vEnd = eLo;
          }
          if (vEnd - vStart < 0.02) continue;
          var p0 = [F.P(a, vStart, t0), F.P(b, vStart, t0), F.P(b, vEnd, t0), F.P(a, vEnd, t0)];
          var p1 = [F.P(a, vStart, t1), F.P(b, vStart, t1), F.P(b, vEnd, t1), F.P(a, vEnd, t1)];
          _rsQuad(vv, p1[0], p1[1], p1[2], p1[3]);
          _rsQuad(vv, p0[0], p1[0], p1[3], p0[3]);
          _rsQuad(vv, p0[1], p0[2], p1[2], p1[1]);
          ribs++;
        }
        _rsBatMesh(T, g, mat, vv);
        area += planes[i].area || 0;
      }
      if (!area) return { area: 0 };
      billAdd(layer, C.code, area);
      var fix = Math.round(area * (seam ? K.clipPerM2 : K.screwPerM2));
      billAdd(layer, seam ? K.clip : K.screw, fix);
      ctx.log('skin     covering ' + C.def + ' (' + C.code + ') ' + fmtArea(area) + ', ' + ribs + ' ' + (seam ? 'seam' : 'rib') + '(s) at ' +
              Math.round(step * 1000) + ' mm, ' + fix + ' ' + (seam ? 'clip' : 'screw') + '(s)');
      try { if (spec.why) spec.why('skin     they run UP the slope -- a sheet joined across the fall would put a lap facing the water at every course'); } catch (e3) {}
      ctx.log('skin     the sheet runs ' + fmtLen(_rsCovOut(spec)) + ' past each verge and ' + fmtLen(_rsCovOutV(spec)) +
              ' past the eave -- the outermost bars are CENTRED on those lines, so stopping on the line leaves half a bar outside');
      ctx.log('skin     but it stops ' + fmtLen(TB.ridgeGap) + ' SHORT of the ridge on each side. That gap is the ventilation outlet, not a fitting error -- two ' +
              'sheets meeting there would seal the cavity the eave feeds, and the cap covers it');
      return { area: area };
    }
    /* STEP 5, THE COVERING (copied from the ADU's _skinCovering): the frame of
       every plane logged, the pick against the pitch, then shingle or sheet */
    function _rsCovering(ctx, spec, planes) {
      var C = spec.covering || {}, TB = _rsCovTable(spec), fq, fLog = [];
      for (fq = 0; fq < planes.length; fq++) {
        var FF = _rsCovFrame(spec, planes, fq);
        fLog.push('p' + fq + ' ' + (FF ? (Math.round(FF.runLen * 1000) + 'x' + Math.round(FF.slope * 1000) + ' top ' + Math.round(FF.uMin(FF.slope) * 1000) + '..' + Math.round(FF.uMax(FF.slope) * 1000)) : 'NO FRAME'));
      }
      ctx.log('COV  ' + (STATE.roof || {}).type + ' ' + C.kind + ': ' + fLog.join('  '));
      var F0c = _rsCovFrame(spec, planes, 0);
      if (F0c) {
        ctx.log('COV  ribs cut on the hip by halving the interval, not at the next of 24 samples -- one sample is about 220 mm of slope and the hip moves 190 mm sideways over it, which is the tip that showed on the cap');
        ctx.log('COV  run past the fold: ridge ' + Math.round(F0c.lapV * 1000) + ' mm less the ' + Math.round(TB.ridgeGap * 1000) + ' mm vent gap, hip ' +
                Math.round(F0c.lapU * 1000) + ' mm each side -- both off this plane normal, so the sheets meet where their own surfaces cross instead of on the fold');
      }
      if (C.picked && C.kind !== C.picked) {
        ctx.log('skin     covering PICKED ' + C.picked + ' but BUILT ' + C.kind + ' -- at ' + Math.round((STATE.roof.pitch || 0)) + ' deg the pick is below its minimum');
      }
      if (C.kind === 'trapez') return _rsCovSheet(ctx, spec, planes, false);
      if (C.kind === 'seam') return _rsCovSheet(ctx, spec, planes, true);
      if (C.kind === 'tile') return _rsCovTile(ctx, spec, planes);
      return _rsCovShingle(ctx, spec, planes);
    }

    function _rsCovTop(spec, slope) {
      var C = spec.covering || {}, TB = _rsCovTable(spec);
      if (C.kind === 'shingle') return _rsCovBase(spec, slope) + TB.shingle.t;
      if (C.kind === 'tile') return _rsCovBase(spec, slope) + (TB.tile || RS_COV.tile).t + 0.022;
      if (C.kind === 'seam') return _rsCovBase(spec, slope) + TB.seam.seamH;
      return _rsCovBase(spec, slope) + TB.trapez.ribH;
    }
    /* STEP 4, THE EDGES (copied from the ADU's _skinEdges): the drip at every
       eave, a verge down every rake of a gable, none on a hip whose sloping
       edges are capped instead; on a mono a drip at the top too; and the fascia
       board under the drip that the gutter brackets go into */
    function _rsEdges(ctx, spec, planes) {
      var T = ctx.THREE, g = ctx.group, i, ED = spec.edges, layer = spec.layer, MAG = 0xFF00FF;
      var isNewL = function (nm) { try { return !!(spec.isNew && spec.isNew(layer + '.' + nm)); } catch (e) { return false; } };
      var why = function (line) { try { if (spec.why) spec.why(line); } catch (e) {} };
      var mat = new T.MeshStandardMaterial({ color: isNewL('edges') ? MAG : (ED.color || 0x8A8F94), roughness: 0.4, metalness: 0.65, side: T.DoubleSide });
      var isHipE = !!(STATE.roof && STATE.roof.type === 'hip');
      var mDrip = 0, mVerge = 0, eLog = [], _vergeExt = 0, _vergeOnCov = null;
      var liftS = spec.lift(planes[0]);
      /* THE VERGE LIES ON THE COVERING, NOT ON THE BATTENS (core-v535, Marek:
         zavetrne listy mas pod battens, mely by byt nad nimi). Every edge
         piece was laid at the battens' top with its leg running OUTWARD from
         the roof's edge and a 60 face hanging off that -- right for the drip
         under the first course, wrong for a verge: under a clay tile's 18 to
         64 of profile it was buried, and what showed at the gable was the
         raw edge of the tiles with the battens' ends under it. A verge now
         takes onCov: it lies on the covering's top, its leg runs INWARD over
         the last tiles by K.top, and its face hangs down the roof's edge from
         the tiles' top to 10 under the counter-battens' foot, over the ends
         of the battens, the counter-battens and the tiles. A shingle roof
         keeps the thin verge as it was. The metres are the same. */
      var angle = function (a0, b0e, up, K, outward, nrm, onCov, rec) {
        var run = Math.hypot(b0e[0] - a0[0], b0e[2] - a0[2]);
        if (!(run > 0.2)) return 0;
        var ex = (b0e[0] - a0[0]) / run, ez = (b0e[2] - a0[2]) / run;
        var ey = (b0e[1] - a0[1]) / run;
        var over = isHipE ? (liftS * Math.hypot((nrm || [0, 1, 0])[0], (nrm || [0, 1, 0])[2]) + 0.010) : 0.040;
        var ovA = over, ovB = over;
        if (K.rake) {
          if (a0[1] <= b0e[1]) { ovA = K.eaveOut; ovB = 0; }
          else { ovB = K.eaveOut; ovA = 0; }
        }
        var a = [a0[0] - ex * ovA, a0[1] - ey * ovA, a0[2] - ez * ovA];
        var b = [b0e[0] + ex * ovB, b0e[1] + ey * ovB, b0e[2] + ez * ovB];
        var ox2 = outward[0], oz2 = outward[1];
        var N = nrm || [0, 1, 0];
        if (onCov) up = onCov.up;
        if (isHipE && !onCov) {
          a = [a[0] + ox2 * 0.010, a[1] + 0.030, a[2] + oz2 * 0.010];
          b = [b[0] + ox2 * 0.010, b[1] + 0.030, b[2] + oz2 * 0.010];
        }
        var v = [];
        var dhx = ox2 - N[0] * (ox2 * N[0] + oz2 * N[2]);
        var dhz = oz2 - N[2] * (ox2 * N[0] + oz2 * N[2]);
        var dhy = -(N[1] ? (ox2 * N[0] + oz2 * N[2]) / 1 : 0) * 0 - (N[0] * ox2 + N[2] * oz2) * N[1];
        var dhL = Math.hypot(dhx, dhy, dhz) || 1;
        dhx /= dhL; dhy /= dhL; dhz /= dhL;
        var pt = function (e, out, along, down) {
          return [e[0] + dhx * out + N[0] * along, e[1] + dhy * out + N[1] * along - down, e[2] + dhz * out + N[2] * along];
        };
        var legA = (isHipE || onCov) ? -K.top : 0;
        var legB = (isHipE || onCov) ? 0 : K.top;
        var faceL = onCov ? onCov.face : K.face;
        _rsQuad(v, pt(a, legA, up, 0), pt(b, legA, up, 0), pt(b, legB, up, 0), pt(a, legB, up, 0));
        _rsQuad(v, pt(a, legB, up, 0), pt(b, legB, up, 0), pt(b, legB, up - K.t, 0), pt(a, legB, up - K.t, 0));
        _rsQuad(v, pt(a, legB, up, 0), pt(b, legB, up, 0), pt(b, legB, up, faceL), pt(a, legB, up, faceL));
        _rsQuad(v, pt(a, legB, up, faceL), pt(b, legB, up, faceL), pt(b, legB + 0.016, up, faceL + 0.004), pt(a, legB + 0.016, up, faceL + 0.004));
        var geo = new T.BufferGeometry();
        geo.setAttribute('position', new T.Float32BufferAttribute(v, 3));
        geo.computeVertexNormals();
        g.add(new T.Mesh(geo, mat));
        /* core-v760: WHERE THE DRIP REALLY ENDS, for the gutter to hang from --
           the face's foot, the kick's foot and the face's line on plan */
        if (rec) {
          var fA = pt(a, legB, up, faceL), fB = pt(b, legB, up, faceL);
          var kA = pt(a, legB + 0.016, up, faceL + 0.004), kB = pt(b, legB + 0.016, up, faceL + 0.004);
          rec.yFace = Math.min(fA[1], fB[1]); rec.yLow = Math.min(kA[1], kB[1]);
          rec.fx = (fA[0] + fB[0]) / 2; rec.fz = (fA[2] + fB[2]) / 2;
        }
        return Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]);
      };
      var lift = 0;
      /* THE DRIP AND THE FASCIA RUN THE EAVE THE PLANE REALLY HAS (core-v534,
         Marek: dovnitr do vnitrniho prostoru se natahuji i okapy, maji koncit
         na vnejsi obvodove stene). A T's bar slope facing the wing is cut
         into pieces, and every piece keeps the WHOLE slope's eave record;
         the wing's slopes keep an eave record that starts on the bar's
         ridge line. The drip and the fascia were laid along those records,
         so each bar piece carried a full-length fascia through the wing,
         and the wing's fascias ran on into the bar up to its ridge -- the
         eave boards inside the house that looked like gutters (the gutters
         themselves read the outline since thouse-v225). Here the eave is
         the record cut to the outline: the outline's vertices on the eave's
         height, spanned along the record and kept within it. A piece whose
         low edge is a valley has no eave edge and gets no drip and no
         fascia; the record still steers the verge and the covering. */
      var eaveOnPlane = function (P9, E9) {
        if (!P9 || !E9 || E9.length < 2) return null;
        var ex9 = E9[1][0] - E9[0][0], ey9 = E9[1][1] - E9[0][1], ez9 = E9[1][2] - E9[0][2], L9 = Math.hypot(ex9, ez9);
        if (!(L9 > 0.05)) return null;
        var ux9 = ex9 / L9, uz9 = ez9 / L9, yE9 = Math.min(E9[0][1], E9[1][1]), lo9 = 1e9, hi9 = -1e9, n9 = 0, q9;
        for (q9 = 0; q9 < P9.length; q9++) {
          if (Math.abs(P9[q9][1] - yE9) > 0.01) continue;
          var t9 = (P9[q9][0] - E9[0][0]) * ux9 + (P9[q9][2] - E9[0][2]) * uz9;
          n9++; if (t9 < lo9) lo9 = t9; if (t9 > hi9) hi9 = t9;
        }
        if (n9 < 2) return null;
        lo9 = Math.max(lo9, 0); hi9 = Math.min(hi9, L9);
        if (!(hi9 - lo9 > 0.05)) return null;
        var at9 = function (t) { return [E9[0][0] + ux9 * t, E9[0][1] + ey9 * (t / L9), E9[0][2] + uz9 * t]; };
        return [at9(lo9), at9(hi9)];
      };
      var eCut = [];
      /* core-v669 (RANCH thread, opt-in spec.edges.vergeTag): a verge is
         drawn under the drip's mark, so the qty audit read every verge as
         drip; with the flag each verge is tagged its own code (ED.verge.code)
         and the drip's code put back before the next drip */
      var tagV = false;
      for (i = 0; i < planes.length; i++) {
        var DPe = _rsDeckPlane(planes[i], spec.lift(planes[i]));
        var P = DPe && DPe.pts, E = DPe && DPe.eave, RD = DPe && DPe.ridge;
        if (!E || E.length < 2) continue;
        var mx2 = (E[0][0] + E[1][0]) / 2, mz2 = (E[0][2] + E[1][2]) / 2;
        var rx2 = RD ? (RD[0][0] + RD[1][0]) / 2 : mx2;
        var rz2 = RD ? (RD[0][2] + RD[1][2]) / 2 : mz2;
        var dx2 = mx2 - rx2, dz2 = mz2 - rz2, L2 = Math.hypot(dx2, dz2) || 1;
        var slopeE = RD ? Math.hypot(RD[0][0] - E[0][0], RD[0][1] - E[0][1], RD[0][2] - E[0][2]) : 4;
        lift = (isHipE ? _rsBatBase(spec, slopeE) : _rsBatTop(spec, slopeE)) + 0.0005;
        var Ee = eaveOnPlane(P, E), eFull = Math.hypot(E[1][0] - E[0][0], E[1][2] - E[0][2]);
        if (!Ee) { eLog.push('p' + i + ' no eave edge (a valley or a point), no drip'); eCut.push(String(planes[i].id || ('p' + i)) + ' none of ' + Math.round(eFull * 1000)); }
        else {
          var eOwn = Math.hypot(Ee[1][0] - Ee[0][0], Ee[1][2] - Ee[0][2]);
          if (eOwn < eFull - 0.01) eCut.push(String(planes[i].id || ('p' + i)) + ' ' + Math.round(eOwn * 1000) + ' of ' + Math.round(eFull * 1000));
        }
        if (tagV) { _asmCode(ctx, ED.drip.code, 'top'); tagV = false; }
        var dRec = Ee ? {} : null;
        var runE = Ee ? angle(Ee[0], Ee[1], lift, ED.drip, [dx2 / L2, dz2 / L2], DPe.n, null, dRec) : 0;
        /* core-v760: each eave drip as drawn, on this build's ctx -- the gutter
           module (API.gutters) hangs its trough from it */
        if (dRec && runE && dRec.yFace != null) {
          if (!ctx._rsDrip) ctx._rsDrip = [];
          ctx._rsDrip.push({ id: planes[i].id, a: Ee[0], b: Ee[1], yFace: dRec.yFace, yLow: dRec.yLow, fx: dRec.fx, fz: dRec.fz });
        }
        mDrip += runE;
        if (Ee) eLog.push('p' + i + ' ' + Math.round(runE * 1000));
        if (ED.fascia && Ee) {
          try {
            var eLen = Math.hypot(Ee[1][0] - Ee[0][0], Ee[1][2] - Ee[0][2]);
            if (eLen > 0.2) {
              _asmCode(ctx, ED.fascia.code, 'side');
              var fasT = ED.fascia.t || 0.025, fasH = ED.fascia.h || 0.200, fx = (Ee[0][0] + Ee[1][0]) / 2, fz = (Ee[0][2] + Ee[1][2]) / 2;
              var yFas = Math.min(Ee[0][1], Ee[1][1]) + lift - fasH / 2 - 0.004;
              var alongX9 = Math.abs(Ee[1][0] - Ee[0][0]) > Math.abs(Ee[1][2] - Ee[0][2]);
              var oxF = dx2 / L2, ozF = dz2 / L2;
              var mFas = new T.MeshStandardMaterial({ color: isNewL('edges') ? MAG : (ED.fascia.color || 0xB98B57), roughness: 0.9, metalness: 0.0 });
              var fas = new T.Mesh(alongX9 ? new T.BoxGeometry(eLen, fasH, fasT) : new T.BoxGeometry(fasT, fasH, eLen), mFas);
              fas.position.set(fx - oxF * fasT / 2, yFas, fz - ozF * fasT / 2);
              fas.name = 'fascia'; g.add(fas);
              billAdd(layer, ED.fascia.code, eLen);
              /* core-v667: its nails into the rafter tails, where the product
                 names them (opt-in spec.edges.fascia.nail) */
              if (ED.fascia.nail) billAdd(layer, ED.fascia.nail, (Math.floor(eLen / (ED.fascia.nailStep || 0.6)) + 1) * (ED.fascia.nailPer || 2));
              eLog.push('fascia ' + Math.round(eLen * 1000));
              _asmCode(ctx, ED.drip.code, 'top');
            }
          } catch (eFas) { ctx.log('skin     fascia THREW ' + (eFas && eFas.message)); }
        }
        if (((STATE.roof || {}).type === 'mono') && RD && RD.length >= 2) {
          var runT = angle(RD[0], RD[1], lift, ED.drip, [-dx2 / L2, -dz2 / L2], DPe.n);
          mDrip += runT;
          eLog.push('p' + i + ' top ' + Math.round(runT * 1000));
          /* core-v667 (GARAGE APARTMENT thread, opt-in spec.edges.fascia.top):
             THE HIGH EAVE GETS ITS FASCIA TOO -- under the top drip, closing the
             rafter tails the way the low eave's board does (US practice); the
             same board, billed by its length */
          if (ED.fascia && ED.fascia.top) {
            try {
              var tLen = Math.hypot(RD[1][0] - RD[0][0], RD[1][2] - RD[0][2]);
              if (tLen > 0.2) {
                _asmCode(ctx, ED.fascia.code, 'side');
                var tT = ED.fascia.t || 0.025, tH = ED.fascia.h || 0.200, tx = (RD[0][0] + RD[1][0]) / 2, tz = (RD[0][2] + RD[1][2]) / 2;
                var yTf = Math.min(RD[0][1], RD[1][1]) + lift - tH / 2 - 0.004;
                var tAlongX = Math.abs(RD[1][0] - RD[0][0]) > Math.abs(RD[1][2] - RD[0][2]);
                var mTf = new T.MeshStandardMaterial({ color: isNewL('edges') ? MAG : (ED.fascia.color || 0xB98B57), roughness: 0.9, metalness: 0.0 });
                var tf = new T.Mesh(tAlongX ? new T.BoxGeometry(tLen, tH, tT) : new T.BoxGeometry(tT, tH, tLen), mTf);
                tf.position.set(tx + (dx2 / L2) * tT / 2, yTf, tz + (dz2 / L2) * tT / 2);
                tf.name = 'fascia'; g.add(tf);
                billAdd(layer, ED.fascia.code, tLen);
                if (ED.fascia.nail) billAdd(layer, ED.fascia.nail, (Math.floor(tLen / (ED.fascia.nailStep || 0.6)) + 1) * (ED.fascia.nailPer || 2));
                eLog.push('fascia top ' + Math.round(tLen * 1000));
                _asmCode(ctx, ED.drip.code, 'top');
              }
            } catch (eTf) { ctx.log('skin     top fascia THREW ' + (eTf && eTf.message)); }
          }
        }
        /* AN EDGE TWO PLANES SHARE IS READ ON THE PLANES AS BUILT (core-v534).
           The test compared this plane's LIFTED outline (the deck plane,
           raised off the rafters by the build-up) with the other planes' RAW
           outlines, so with any lift at all no edge was ever found shared:
           a verge went down every valley, up every cut line where the bar's
           slope is split by the wing, and over every hip of a hipped roof,
           all billed as ROR00006. A product with no lift never saw it. Both
           sides are now the raw outlines; the verge itself still stands on
           the lifted one. And every edge of the outline is walked, not the
           first four. */
        var PR = planes[i].pts || [];
        var onSeg3 = function (p, c, d) {
          var ex = d[0] - c[0], ey = d[1] - c[1], ez = d[2] - c[2], L = Math.hypot(ex, ey, ez) || 1;
          var t = ((p[0] - c[0]) * ex + (p[1] - c[1]) * ey + (p[2] - c[2]) * ez) / L;
          if (t < -0.02 || t > L + 0.02) return false;
          var fx = c[0] + ex / L * t, fy = c[1] + ey / L * t, fz = c[2] + ez / L * t;
          return Math.hypot(p[0] - fx, p[1] - fy, p[2] - fz) < 0.02;
        };
        if (P && P.length >= 3 && PR.length === P.length) {
          var q;
          for (q = 0; q < P.length; q++) {
            var a3 = P[q], b3 = P[(q + 1) % P.length], a3r = PR[q], b3r = PR[(q + 1) % PR.length];
            var isEave = (Math.abs(a3[1] - E[0][1]) < 0.01 && Math.abs(b3[1] - E[1][1]) < 0.01);
            var isRidge = RD && (Math.abs(a3[1] - RD[0][1]) < 0.01 && Math.abs(b3[1] - RD[1][1]) < 0.01);
            if (isEave || isRidge) continue;
            if (Math.hypot(b3[0] - a3[0], b3[1] - a3[1], b3[2] - a3[2]) < 0.05) continue;
            var shared = false, p2, q2;
            for (p2 = 0; p2 < planes.length && !shared; p2++) {
              if (p2 === i || !planes[p2].pts) continue;
              var P2 = planes[p2].pts;
              for (q2 = 0; q2 < P2.length; q2++) {
                var c3 = P2[q2], d3 = P2[(q2 + 1) % P2.length];
                if (Math.hypot(d3[0] - c3[0], d3[1] - c3[1], d3[2] - c3[2]) < 0.05) continue;
                /* shared when one edge lies along the other: the hip of a hip
                   end is one edge, and on the bar's side the same hip is two
                   pieces' edges (core-v534) */
                var same = (onSeg3(a3r, c3, d3) && onSeg3(b3r, c3, d3)) || (onSeg3(c3, a3r, b3r) && onSeg3(d3, a3r, b3r));
                if (same) { shared = true; break; }
              }
            }
            if (shared) continue;
            var cxq = (a3[0] + b3[0]) / 2 - (mx2 + rx2) / 2;
            var czq = (a3[2] + b3[2]) / 2 - (mz2 + rz2) / 2;
            var Lq = Math.hypot(cxq, czq) || 1;
            /* THE VERGE MEETS ITS PARTNER AT THE APEX (core-v527, Marek:
               zavetrne listy protahnout az k hrebeni, je tam mezera). Lifted off
               the deck by the build-up, the two rakes' ends stood apart at the
               ridge by lift x tan(pitch) each -- a notch at the top of every
               gable, the wider the thicker the roof. The end on the ridge is
               carried on up the slope by that much, to where the two lifted
               lines cross. Not on a mono: its top edge has a drip of its own. */
            /* on a tiled or sheeted roof the verge sits on the covering (core-v535) */
            var vMode = null, liftV = lift;
            if (spec.covering && spec.covering.kind !== 'shingle') {
              liftV = _rsCovTop(spec, slopeE) + 0.0005;
              vMode = { up: liftV, face: Math.max(ED.verge.face || 0.06, liftV - _rsBatBase(spec, slopeE) + 0.010) };
              _vergeOnCov = Math.round((liftV - lift) * 1000);
            }
            if (RD && ((STATE.roof || {}).type !== 'mono')) {
              var aR = Math.abs(a3[1] - RD[0][1]) < 0.01, bR = Math.abs(b3[1] - RD[0][1]) < 0.01;
              if (aR !== bR) {
                var tp = aR ? a3 : b3, lw = aR ? b3 : a3, ddx = tp[0] - lw[0], ddy = tp[1] - lw[1], ddz = tp[2] - lw[2];
                var dL = Math.hypot(ddx, ddy, ddz) || 1, hL = Math.hypot(ddx, ddz) || 1, ext = liftV * Math.abs(ddy) / hL;
                var tp2 = [tp[0] + ddx / dL * ext, tp[1] + ddy / dL * ext, tp[2] + ddz / dL * ext];
                if (aR) a3 = tp2; else b3 = tp2;
                _vergeExt = ext;
              }
            }
            if (ED.vergeTag && ED.verge.code && !tagV) { _asmCode(ctx, ED.verge.code, 'top'); tagV = true; }
            mVerge += angle(a3, b3, lift, ED.verge, [cxq / Lq, czq / Lq], DPe.n, vMode);
          }
        }
      }
      if (tagV) { _asmCode(ctx, ED.drip.code, 'top'); tagV = false; }
      if (mDrip) billAdd(layer, ED.drip.code, mDrip);
      if (mVerge) billAdd(layer, ED.verge.code, mVerge);
      if (_vergeExt) ctx.log('EDG  verge carried ' + Math.round(_vergeExt * 1000) + ' mm on past the ridge line at each rake, to meet its partner over the apex (core-v527)');
      var mFix = Math.ceil((mDrip + mVerge) / ED.fixSpacing);
      if (mFix) billAdd(layer, ED.fix, mFix);
      ctx.log('FIX  flashings ' + Math.round((mDrip + mVerge) * 1000) + ' mm -> ' + mFix + ' x ' + ED.fix + ' at ' + Math.round(ED.fixSpacing * 1000) + ' mm through the seating leg');
      ctx.log('EDG  ' + (isHipE ? 'hip' : (STATE.roof || {}).type) + ': ' + planes.length + ' plane(s), drip ' + eLog.join(' ') + ' = ' + Math.round(mDrip * 1000) + ' mm total, ' + ED.drip.code);
      ctx.log('EDG  verge ' + Math.round(mVerge * 1000) + ' mm' + (isHipE ? ' -- none on a hip: the four sloping edges are HIPS, roof on both sides, and a cap goes OVER them in the ridge step' : ' at the rakes, ' + ED.verge.code) +
              (_vergeOnCov != null ? (' -- on the covering (core-v535): ' + _vergeOnCov + ' over the battens, leg ' + Math.round((ED.verge.top || 0) * 1000) + ' in over the tiles, face down the edge over the battens and the tiles') : ''));
      ctx.log('EDG  eave lap ' + (isHipE ? '0 -- the pieces butt on the corner, because the next eave starts there' : '40 mm past each end, for the verge to lap'));
      ctx.log('gutter   eave drip cut to each plane\'s own eave edge (core-v534, the eave record of a cut piece ran on through the house): ' + (eCut.length ? eCut.join(', ') : 'none -- every eave record is the plane\'s own edge'));
      var pDegE = (STATE.roof && STATE.roof.pitch) || 0;
      var sPE = Math.sin(pDegE * Math.PI / 180), cPE = Math.cos(pDegE * Math.PI / 180);
      ctx.log('EDG  horizontally past the plane eave at ' + Math.round(pDegE) + ' deg: deck ' + Math.round(liftS * sPE * 1000) + ' + build-up ' + Math.round(lift * sPE * 1000) +
              ' + leg ' + Math.round((isHipE ? 0 : ED.drip.top) * cPE * 1000) + ' + kick ' + Math.round(0.016 * cPE * 1000) + ' mm');
      ctx.log('EDG  metal sits ' + Math.round(lift * 1000) + ' mm off the deck plane' + (isHipE ? (', on the underlay -- ' + Math.round((_rsBatTop(spec, 4) - _rsBatBase(spec, 4)) * 1000) + ' mm lower than the batten grid it was on') : ', on the batten grid'));
      ctx.log('skin     drip edge ' + Math.round(mDrip * 10) / 10 + ' m at the eaves, verge ' + Math.round(mVerge * 10) / 10 + ' m at the rakes');
      why('skin     both sit ON the underlay and UNDER the covering -- neither first nor last, which is why this list is in laying order and not a section');
      why('skin     at the eave the metal goes OVER the underlay, so water running down the underlay lands on it and is thrown into the gutter; under it and the underlay drains behind the fascia');
      why('skin     built from the edge POINTS, so a rake slopes with the roof -- it was three axis-aligned boxes at the average height, which is right on a level eave and one straight bar across the gable everywhere else');
      why('skin     deck, underlay, both flashings and the gutter all come off ONE offset plane -- they each had their own route to it, and the routes only agreed at nought degrees');
      ctx.log('skin     the rake runs ' + fmtLen(ED.verge.eaveOut) + ' PAST the eave and all the way TO the ridge -- the ridge piece laps it there, so holding it back as well only made it short');
      ctx.log('skin     the offset is the normal x ' + fmtLen(liftS) + ' -- horizontal AND vertical at once, d*sin and d*cos. A vertical shear gives d/cos, which is the same only at nought degrees and 161 mm out at 35');
      why('skin     drawn as an ANGLE with the down leg and the kick -- a flat band would bill the same and look like a painted line, and the leg IS the reason it exists');
      return { len: mDrip + mVerge };
    }

    /* STEP 6, THE RIDGE AND THE HIPS (copied from the ADU's _skinRidge): a
       folded cap on the published ridge over a vent strip, and the same cap on
       every fold two planes share on a hip; cap shingles under a shingle roof */
    /* THE CLAY RIDGE TILES, PIECE BY PIECE (core-v484, migrated from the T
       house, thouse-v224/v230, Marek: doplnit hrebenace pro tasky / hrebenac
       na kusy). A tiled roof is capped with round ridge tiles, not the metal
       strip: 420 long laid 380 apart, each its own shade of the tiles' nine,
       its wide end over the piece below, on the ridge and on every hip of the
       planes handed in; spec.hipPlanes may name the planes the hips are read
       from (a product that stretches its eaves hands the unstretched ones).
       Billed as the ridge's code by the metre, the vent strip on the ridge,
       the ridge fixings as the metal cap counts them. */
    function _rsRidgeOrder(list) {
      if (!list || !list.length) return null;
      var RD = null, i, a = null, b = null, rest = [];
      for (i = 0; i < list.length; i++) if (list[i].ridge && list[i].kind !== 'hip') { RD = list[i].ridge; break; }
      if (!RD) for (i = 0; i < list.length; i++) if (list[i].ridge) { RD = list[i].ridge; break; }
      if (!RD) return null;
      var same = function (r) {
        if (!r) return false;
        var d1 = Math.hypot(r[0][0] - RD[0][0], r[0][2] - RD[0][2]) + Math.hypot(r[1][0] - RD[1][0], r[1][2] - RD[1][2]);
        var d2 = Math.hypot(r[0][0] - RD[1][0], r[0][2] - RD[1][2]) + Math.hypot(r[1][0] - RD[0][0], r[1][2] - RD[0][2]);
        return Math.min(d1, d2) < 0.01;
      };
      for (i = 0; i < list.length; i++) {
        var pl = list[i], n = pl.n || [0, 1, 0];
        if (pl.kind !== 'hip' && same(pl.ridge)) {
          if (!a) { a = pl; continue; }
          var na = a.n || [0, 1, 0];
          if (!b && (n[0] * na[0] + n[2] * na[2]) < 0) { b = pl; continue; }
        }
        rest.push(pl);
      }
      var outL = [a];
      if (b) outL.push(b);
      return outL.concat(rest);
    }
    function _rsTileShade(col, k) {
      var S = RS_TILE_SHADES[k % RS_TILE_SHADES.length], f = S[0], w = S[1];
      var r = ((col >> 16) & 255) * f * (2 - w), gg = ((col >> 8) & 255) * f * w, b = (col & 255) * f;
      var cl = function (x) { return Math.max(0, Math.min(255, Math.round(x))); };
      return (cl(r) << 16) | (cl(gg) << 8) | cl(b);
    }
    function _rsHash3(a, b, c) {
      var h = Math.sin(a * 127.1 + b * 311.7 + c * 74.7) * 43758.5453;
      return h - Math.floor(h);
    }
    var RS_RT = { len: 0.420, cover: 0.380, half: 0.140, flare: 1.10, t: 0.016, seg: 12 };
    function _rsRidgeTile(ctx, spec, planes) {
      var T = ctx.THREE, g = ctx.group, i, j, RG = spec.ridge || {}, layer = spec.layer;
      var grpX = _rsRidgeOrder(planes) || planes, grp0 = spec.hipPlanes || planes, tag = spec.ridgeTag || '';
      var lift = spec.lift(grpX[0]), colRoof = ((spec.covering || {}).color != null) ? spec.covering.color : 0xA4502E;
      var rCode = RG.code || 'ROR00005', rVent = RG.vent || null, rFix = RG.fix || null, rSp = RG.fixSpacing || 0.300;
      if (!grp0 || !grp0.length) return { ridge: 0, hip: 0, n: 0 };
      /* THE CAP LIES ON THE TOP COURSE'S UPPER PART (core-v489, Marek: hrebenace
         jsou posunute vys nad hreben, nedolehaji). _rsCovTop is the tile's
         thickest point, the nose of a course; near the ridge the cap lies on
         the course's upper end, which is 4 mm off the battens plus half the
         wave -- 35 mm lower, which is what the caps floated by. */
      var covTop = _rsCovBase(spec, 4) + 0.004 + 0.011;
      var Nt = lift + covTop + 0.004;
      var byId = {};
      for (i = 0; i < grpX.length; i++) byId[grpX[i].id] = grpX[i];
      var same = function (a, b) { return Math.abs(a[0] - b[0]) < 0.003 && Math.abs(a[1] - b[1]) < 0.003 && Math.abs(a[2] - b[2]) < 0.003; };
      var lines = [], RD = null;
      var ord = _rsRidgeOrder(grpX);
      if (ord && ord[0] && ord[0].ridge) {
        RD = ord[0].ridge;
        var sides = [ord[0]];
        if (ord[1] && ord[1].ridge) sides.push(ord[1]);
        if (Math.hypot(RD[1][0] - RD[0][0], RD[1][2] - RD[0][2]) > 0.2 && sides.length === 2) lines.push({ a: RD[0], b: RD[1], p: sides, ridge: true });
      }
      /* THE HIPS ARE FOUND BY THEIR EDGES, NOT BY TWO SHARED CORNERS (core-v490,
         Marek: sem tam chybi hrebenovy profil na narozi, jedna strana main run).
         The bar's slope on the wing's side is cut in pieces, and a piece meets
         the hip plane along an edge whose corners are not both the hip's --
         two identical corners were asked for and the hip was dropped. Now any
         two planes whose edges lie on one sloping line for more than 200 mm
         share a hip there, over the length they share; a line where the other
         plane rises above this one is a valley and gets no cap. */
      var hipSeen = [], nHipPairs = 0;
      var onLine = function (P, A0, A1) {
        var dx = A1[0] - A0[0], dy = A1[1] - A0[1], dz = A1[2] - A0[2], L2 = dx * dx + dy * dy + dz * dz;
        if (!(L2 > 1e-6)) return null;
        var t = ((P[0] - A0[0]) * dx + (P[1] - A0[1]) * dy + (P[2] - A0[2]) * dz) / L2;
        var qx = A0[0] + dx * t - P[0], qy = A0[1] + dy * t - P[1], qz = A0[2] + dz * t - P[2];
        return (Math.hypot(qx, qy, qz) < 0.02) ? t : null;
      };
      for (i = 0; i < grp0.length; i++) for (j = 0; j < grp0.length; j++) {
        if (i === j) continue;
        var A = grp0[i], B = grp0[j], na = A.n || [0, 1, 0], nb = B.n || [0, 1, 0];
        if (!A.pts || !B.pts || na[0] * nb[0] + na[1] * nb[1] + na[2] * nb[2] > 0.999) continue;
        var pa, pb;
        for (pa = 0; pa < A.pts.length; pa++) {
          var A0 = A.pts[pa], A1 = A.pts[(pa + 1) % A.pts.length];
          var Ld = Math.hypot(A1[0] - A0[0], A1[1] - A0[1], A1[2] - A0[2]);
          if (!(Ld > 0.2) || Math.abs(A1[1] - A0[1]) < 0.05) continue;
          for (pb = 0; pb < B.pts.length; pb++) {
            var B0 = B.pts[pb], B1 = B.pts[(pb + 1) % B.pts.length];
            var t0 = onLine(B0, A0, A1), t1 = onLine(B1, A0, A1);
            if (t0 == null || t1 == null) continue;
            var lo = Math.max(0, Math.min(t0, t1)), hi = Math.min(1, Math.max(t0, t1));
            if ((hi - lo) * Ld < 0.2) continue;
            /* a valley, not a hip: the other plane's middle lies above this one */
            var cB = [0, 0, 0], kq;
            for (kq = 0; kq < B.pts.length; kq++) { cB[0] += B.pts[kq][0]; cB[1] += B.pts[kq][1]; cB[2] += B.pts[kq][2]; }
            cB[0] /= B.pts.length; cB[1] /= B.pts.length; cB[2] /= B.pts.length;
            if (na[0] * (cB[0] - A0[0]) + na[1] * (cB[1] - A0[1]) + na[2] * (cB[2] - A0[2]) > 0.01) continue;
            var P0 = [A0[0] + (A1[0] - A0[0]) * lo, A0[1] + (A1[1] - A0[1]) * lo, A0[2] + (A1[2] - A0[2]) * lo];
            var P1 = [A0[0] + (A1[0] - A0[0]) * hi, A0[1] + (A1[1] - A0[1]) * hi, A0[2] + (A1[2] - A0[2]) * hi];
            /* the low end follows the plane's stretched eave when it was stretched */
            var AX = byId[A.id] || A, BX = byId[B.id] || B, ends = [P0, P1], e9;
            for (e9 = 0; e9 < 2; e9++) {
              var src = e9 ? A1 : A0, tE = e9 ? hi : lo, idx = e9 ? (pa + 1) % A.pts.length : pa;
              if (Math.abs(tE - (e9 ? 1 : 0)) < 1e-3 && AX.pts && AX.pts[idx] && AX.pts[idx][1] < src[1] - 0.005) ends[e9] = AX.pts[idx];
            }
            if (ends[0][1] > ends[1][1]) ends.reverse();
            var dupH = false, dh;
            for (dh = 0; dh < hipSeen.length; dh++) if (Math.hypot(hipSeen[dh][0] - (ends[0][0] + ends[1][0]) / 2, hipSeen[dh][1] - (ends[0][2] + ends[1][2]) / 2) < 0.05) dupH = true;
            if (dupH) continue;
            hipSeen.push([(ends[0][0] + ends[1][0]) / 2, (ends[0][2] + ends[1][2]) / 2]);
            lines.push({ a: ends[0], b: ends[1], p: [AX, BX], ridge: false });
            nHipPairs++;
          }
        }
      }
      var shade = function (f) { var r = (colRoof >> 16) & 255, gg = (colRoof >> 8) & 255, b = colRoof & 255; return (Math.min(255, Math.round(r * f)) << 16) | (Math.min(255, Math.round(gg * f)) << 8) | Math.min(255, Math.round(b * f)); };
      /* EACH RIDGE TILE A PIECE OF ITS OWN (thouse-v230, Marek: hrebenac
         nakreslit jakoby na kusy, tak jak se k taskam dava): every piece takes
         its own shade from the tiles' nine, darkened a touch, and shows its
         rim at both ends -- the wide lapping end over the piece below and the
         plain end under the next -- so the run reads piece by piece */
      var buckets = [], kb;
      for (kb = 0; kb < RS_TILE_SHADES.length; kb++) buckets.push([]);
      var vv = null, lenR = 0, lenH = 0, nPc = 0, L9;
      var tri = function (a, b, c) { vv.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]); };
      for (L9 = 0; L9 < lines.length; L9++) {
        var ln = lines[L9], a0 = ln.a, b0 = ln.b;
        var ux = b0[0] - a0[0], uy = b0[1] - a0[1], uz = b0[2] - a0[2], L = Math.hypot(ux, uy, uz);
        if (!(L > 0.2)) continue;
        ux /= L; uy /= L; uz /= L;
        var wx = -uz, wz = ux, wl = Math.hypot(wx, wz) || 1; wx /= wl; wz /= wl;
        var upx = -uy * wz, upy = uz * wx - ux * wz, upz = uy * wx;
        if (upy < 0) { upx = -upx; upy = -upy; upz = -upz; }
        /* the covering's top either side, in the section across the line */
        var yAt = [0, 0], y0 = -1e9, sI;
        for (sI = 0; sI < ln.p.length; sI++) {
          var n = ln.p[sI].n || [0, 1, 0], c = [0, 0, 0], m, pts = ln.p[sI].pts;
          for (m = 0; m < pts.length; m++) { c[0] += pts[m][0]; c[2] += pts[m][2]; }
          c[0] /= pts.length; c[2] /= pts.length;
          var sideP = ((c[0] - a0[0]) * wx + (c[2] - a0[2]) * wz) >= 0 ? 1 : -1;
          var nw = n[0] * wx + n[2] * wz, nu = n[0] * upx + n[1] * upy + n[2] * upz;
          if (!(Math.abs(nu) > 0.05)) nu = 0.05;
          var yHere = (Nt - nw * sideP * RS_RT.half) / nu;
          yAt[sideP > 0 ? 1 : 0] = yHere;
          y0 = Math.max(y0, Nt / nu);
        }
        if (ln.p.length < 2) yAt[0] = yAt[1];
        var yc = Math.min(yAt[0], yAt[1]), bb = Math.max(0.055, y0 + 0.012 - yc);
        var nP = Math.max(1, Math.ceil((L - (RS_RT.len - RS_RT.cover)) / RS_RT.cover)), k2, s9;
        for (k2 = 0; k2 < nP; k2++) {
          var t0 = Math.min(L - 0.05, k2 * RS_RT.cover), t1 = Math.min(L, t0 + RS_RT.len);
          var ring = function (t, f) {
            var out = [], th;
            for (th = 0; th <= RS_RT.seg; th++) {
              var an = Math.PI * th / RS_RT.seg, xx = -Math.cos(an) * RS_RT.half * f, yy = yc + (Math.sin(an) * bb) * f + (f - 1) * 0.02;
              if (th === 0) yy = yAt[0] + (f - 1) * 0.02; else if (th === RS_RT.seg) yy = yAt[1] + (f - 1) * 0.02;
              out.push([a0[0] + ux * t + wx * xx + upx * yy, a0[1] + uy * t + upy * yy, a0[2] + uz * t + wz * xx + upz * yy]);
            }
            return out;
          };
          vv = buckets[Math.floor(_rsHash3(L9 + 1, k2, a0[0] + a0[2]) * RS_TILE_SHADES.length) % RS_TILE_SHADES.length];
          var R0 = ring(t0, RS_RT.flare), R1 = ring(t1, 1.0);
          for (s9 = 0; s9 < RS_RT.seg; s9++) { tri(R0[s9], R1[s9], R1[s9 + 1]); tri(R0[s9], R1[s9 + 1], R0[s9 + 1]); }
          /* the lapping end shows its thickness: a thin band inside the flare */
          var Ri = ring(t0 + 0.004, RS_RT.flare - RS_RT.t / RS_RT.half);
          for (s9 = 0; s9 < RS_RT.seg; s9++) { tri(R0[s9], Ri[s9 + 1], Ri[s9]); tri(R0[s9], R0[s9 + 1], Ri[s9 + 1]); }
          /* and the plain end's rim, where it slides under the next piece */
          var Re = ring(t1 - 0.004, 1.0 - RS_RT.t / RS_RT.half);
          for (s9 = 0; s9 < RS_RT.seg; s9++) { tri(R1[s9], Re[s9], Re[s9 + 1]); tri(R1[s9], Re[s9 + 1], R1[s9 + 1]); }
          nPc++;
        }
        if (ln.ridge) lenR += L; else lenH += L;
      }
      for (kb = 0; kb < buckets.length; kb++) {
        if (!buckets[kb].length) continue;
        var geo = new T.BufferGeometry();
        geo.setAttribute('position', new T.Float32BufferAttribute(buckets[kb], 3));
        geo.computeVertexNormals();
        var mR = new T.MeshStandardMaterial({ color: _rsTileShade(shade(0.93), kb), roughness: 0.80, metalness: 0.0, side: T.DoubleSide });
        var meR = new T.Mesh(geo, mR); meR.name = 'ridge-tiles';
        g.add(meR);
      }
      if (lenR + lenH > 0) {
        billAdd(layer, rCode, lenR + lenH);
        if (lenR > 0 && rVent) billAdd(layer, rVent, lenR);
        if (rFix) billAdd(layer, rFix, Math.ceil((lenR + lenH) / rSp) * 2);
      }
      ctx.log('skin     ridge tiles ' + tag + ': ' + nHipPairs + ' hip line(s) found by their edges | ' + nPc + ' pcs, ridge ' + Math.round(lenR * 1000) + ' mm + hips ' + Math.round(lenH * 1000) + ' mm -> ' + rCode + ' ' + (Math.round((lenR + lenH) * 100) / 100) + ' m, ' + (rVent || 'no vent') + ' ' + (Math.round(lenR * 100) / 100) + ' m, ' + (rFix || 'no fix') + ' ' + Math.ceil((lenR + lenH) / rSp) * 2 + ' pcs' + (lines.length ? '' : ' -- no ridge or hip found in these planes'));
      return { ridge: lenR, hip: lenH, n: nPc };
    }

    function _rsRidge(ctx, spec, planes) {
      var T = ctx.THREE, g = ctx.group, i, RG = spec.ridge, C = spec.covering || {}, TB = _rsCovTable(spec), layer = spec.layer, MAG = 0xFF00FF;
      var R = STATE.roof || {};
      var why = function (line) { try { if (spec.why) spec.why(line); } catch (e) {} };
      var isNewL = function (nm) { try { return !!(spec.isNew && spec.isNew(layer + '.' + nm)); } catch (e) { return false; } };
      if (R.type === 'flat') { why('skin     ridge none -- a parapet roof has no apex, it turns up against a wall'); return { len: 0 }; }
      if (R.type === 'mono') { why('skin     ridge none -- a mono-pitch has one slope, so there is nothing for a cap to sit over. Its top edge is a different detail and belongs to the wall'); return { len: 0 }; }
      if (C.kind === 'tile') { var rTl = _rsRidgeTile(ctx, spec, planes); return { len: (rTl.ridge || 0) + (rTl.hip || 0) }; }
      var shingleCap = (C.kind === 'shingle'), SH = TB.shingle;
      var mat = shingleCap
        ? new T.MeshStandardMaterial({ color: isNewL('ridge') ? MAG : C.color, roughness: 0.92, metalness: 0.0, side: T.DoubleSide })
        : new T.MeshStandardMaterial({ color: isNewL('ridge') ? MAG : C.color, roughness: 0.42, metalness: 0.62, side: T.DoubleSide });
      var capT = shingleCap ? SH.t : RG.t;
      var capRun = function (vvOut, Pf, len, halfW, liftAt) {
        if (!shingleCap) {
          _rsQuad(vvOut, Pf(0, 0, liftAt), Pf(len, 0, liftAt), Pf(len, halfW, liftAt), Pf(0, halfW, liftAt));
          _rsQuad(vvOut, Pf(0, halfW, liftAt), Pf(len, halfW, liftAt), Pf(len, halfW, liftAt - capT), Pf(0, halfW, liftAt - capT));
          return 1;
        }
        var e = SH.exposure, tab = SH.tab, n9 = Math.max(1, Math.ceil(len / e)), q9, pieces = 0;
        for (q9 = n9 - 1; q9 >= 0; q9--) {
          var a0 = q9 * e, a1 = Math.min(len, a0 + tab), up9 = liftAt + (q9 % 2 ? capT : 0);
          if (a1 - a0 < 0.02) continue;
          _rsQuad(vvOut, Pf(a0, 0, up9), Pf(a1, 0, up9), Pf(a1, halfW, up9), Pf(a0, halfW, up9));
          _rsQuad(vvOut, Pf(a0, halfW, up9), Pf(a1, halfW, up9), Pf(a1, halfW, up9 - capT), Pf(a0, halfW, up9 - capT));
          _rsQuad(vvOut, Pf(a0, 0, up9), Pf(a0, halfW, up9), Pf(a0, halfW, up9 - capT), Pf(a0, 0, up9 - capT));
          pieces++;
        }
        return pieces;
      };
      var RD = null;
      for (i = 0; i < planes.length; i++) if (planes[i].ridge) { RD = planes[i].ridge; break; }
      if (!RD) return { len: 0 };
      var run = Math.hypot(RD[1][0] - RD[0][0], RD[1][2] - RD[0][2]);
      var hasRidge = run > 0.2;
      var ux = hasRidge ? (RD[1][0] - RD[0][0]) / run : 1;
      var uz = hasRidge ? (RD[1][2] - RD[0][2]) / run : 0;
      var half = shingleCap ? (SH.tab / 2) : (RG.width / 2);
      var slopeR = 4;
      for (i = 0; i < planes.length; i++) {
        var DPr = _rsDeckPlane(planes[i], spec.lift(planes[i]));
        if (DPr && DPr.eave && DPr.ridge) { slopeR = Math.hypot(DPr.ridge[0][0] - DPr.eave[0][0], DPr.ridge[0][1] - DPr.eave[0][1], DPr.ridge[0][2] - DPr.eave[0][2]); break; }
      }
      var lift = spec.lift(planes[0]) + _rsCovTop(spec, slopeR) + RG.stand;
      if (shingleCap) lift += SH.t * 2 + 0.006;
      var vv = [], q;
      for (q = 0; hasRidge && q < planes.length && q < 2; q++) {
        var DP = _rsDeckPlane(planes[q], spec.lift(planes[q]));
        var n = DP.n, E = DP.eave, RG2 = DP.ridge;
        if (!E || !RG2) continue;
        var dx = E[0][0] - RG2[0][0], dy = E[0][1] - RG2[0][1], dz = E[0][2] - RG2[0][2];
        var dl = Math.hypot(dx, dy, dz) || 1;
        dx /= dl; dy /= dl; dz /= dl;
        var P = (function (dx, dy, dz, n) {
          return function (alongU, downSlope, up) {
            /* THE CAP'S APEX WHERE THE TWO LIFTED SLOPES MEET (core-v524, A-frame:
               hreben musi byt prekryty krycim profilem -- over 160 of PIR at 60
               degrees the apex was lifted straight up by the slope's offset and
               stood 200 under the covering): a point lifted by 'up' off both
               slopes sits up / cos higher on the ridge line */
            if (downSlope === 0) return [RD[0][0] + ux * alongU, RD[0][1] + up / Math.max(0.25, n[1]), RD[0][2] + uz * alongU];
            return [RD[0][0] + ux * alongU + dx * downSlope + n[0] * up, RD[0][1] + dy * downSlope + n[1] * up, RD[0][2] + uz * alongU + dz * downSlope + n[2] * up];
          };
        })(dx, dy, dz, n);
        capRun(vv, P, run, half, lift);
      }
      if (hasRidge) {
        _rsBatMesh(T, g, mat, vv);
        if (shingleCap) {
          billAdd(layer, C.code, run * (SH.tab * SH.tab / SH.exposure));
          billAdd(layer, SH.nail, Math.ceil(run / SH.exposure) * 4);
        } else billAdd(layer, RG.code, run);
        billAdd(layer, RG.vent, run);
      } else {
        run = 0;
        ctx.log('RDG  ridge none -- the apex is a point or under 200 mm, so there is nothing to cap; the hips below are drawn on their own');
      }
      var capLen = 0;
      if (R.type === 'hip') {
        var same = function (p1, p2) { return Math.abs(p1[0] - p2[0]) < 0.002 && Math.abs(p1[1] - p2[1]) < 0.002 && Math.abs(p1[2] - p2[2]) < 0.002; };
        var cent = function (pl) {
          var c0 = 0, c1 = 0, c2 = 0, k;
          for (k = 0; k < pl.pts.length; k++) { c0 += pl.pts[k][0]; c1 += pl.pts[k][1]; c2 += pl.pts[k][2]; }
          var m = pl.pts.length || 1;
          return [c0 / m, c1 / m, c2 / m];
        };
        var caps = 0, ci, cj, cp, cq2, ds;
        for (ci = 0; ci < planes.length; ci++) {
          for (cj = ci + 1; cj < planes.length; cj++) {
            var shared = [];
            for (cp = 0; cp < planes[ci].pts.length; cp++) {
              for (cq2 = 0; cq2 < planes[cj].pts.length; cq2++) {
                if (!same(planes[ci].pts[cp], planes[cj].pts[cq2])) continue;
                var dupS = false;
                for (ds = 0; ds < shared.length; ds++) { if (same(shared[ds], planes[ci].pts[cp])) { dupS = true; break; } }
                if (!dupS) shared.push(planes[ci].pts[cp]);
              }
            }
            if (shared.length !== 2) continue;
            if ((same(shared[0], RD[0]) && same(shared[1], RD[1])) || (same(shared[0], RD[1]) && same(shared[1], RD[0]))) continue;
            var A2 = shared[0], B2 = shared[1];
            var hl = Math.hypot(B2[0] - A2[0], B2[1] - A2[1], B2[2] - A2[2]);
            if (!(hl > 0.2)) continue;
            var hux = (B2[0] - A2[0]) / hl, huy = (B2[1] - A2[1]) / hl, huz = (B2[2] - A2[2]) / hl;
            var mx = (A2[0] + B2[0]) / 2, my = (A2[1] + B2[1]) / 2, mz = (A2[2] + B2[2]) / 2;
            var cosH = Math.abs((planes[ci].n || [0, 1, 0])[1]) || 1;
            var creaseUp = lift / cosH;
            var vvH = [], side;
            for (side = 0; side < 2; side++) {
              var PL2 = side ? planes[cj] : planes[ci];
              var nn = PL2.n || [0, 1, 0];
              var wx = huy * nn[2] - huz * nn[1];
              var wy = huz * nn[0] - hux * nn[2];
              var wz = hux * nn[1] - huy * nn[0];
              var wl = Math.hypot(wx, wy, wz) || 1;
              wx /= wl; wy /= wl; wz /= wl;
              var C2 = cent(PL2);
              if ((C2[0] - mx) * wx + (C2[1] - my) * wy + (C2[2] - mz) * wz < 0) { wx = -wx; wy = -wy; wz = -wz; }
              var PH = (function (ax, ay, az, bx2, by2, bz2, nx, ny, nz) {
                return function (al, dn, up) {
                  if (dn === 0) return [A2[0] + hux * al, A2[1] + huy * al + creaseUp, A2[2] + huz * al];
                  return [A2[0] + hux * al + bx2 * dn + nx * up, A2[1] + huy * al + by2 * dn + ny * up, A2[2] + huz * al + bz2 * dn + nz * up];
                };
              })(hux, huy, huz, wx, wy, wz, nn[0], nn[1], nn[2]);
              capRun(vvH, PH, hl, half, lift);
            }
            _rsBatMesh(T, g, mat, vvH);
            caps++; capLen += hl;
          }
        }
        if (capLen) {
          if (shingleCap) {
            billAdd(layer, C.code, capLen * (SH.tab * SH.tab / SH.exposure));
            billAdd(layer, SH.nail, Math.ceil(capLen / SH.exposure) * 4);
          } else billAdd(layer, RG.code, capLen);
        }
        ctx.log('RDG  ' + caps + ' hip cap(s), ' + Math.round(capLen * 1000) + ' mm of ' + Math.round(RG.width * 1000) + ' mm ' + RG.code + ' -- same piece as the ridge, no vent strip: the outlet is the ridge, a hip is not one');
        ctx.log('RDG  hip crease ' + Math.round((lift / (Math.abs((planes[0].n || [0, 1, 0])[1]) || 1)) * 1000) + ' mm above the hip line, not ' + Math.round(lift * 1000) +
                ' -- the coverings cross at H / cos, and that is the height that lies in both offset planes, so the legs stay on the covering');
      }
      var rFix = shingleCap ? 0 : Math.ceil((run + capLen) / RG.fixSpacing) * 2;
      if (rFix) billAdd(layer, RG.fix, rFix);
      ctx.log('FIX  caps ' + Math.round(run * 1000) + ' mm ridge + ' + Math.round(capLen * 1000) + ' mm hip -> ' + rFix + ' x ' + RG.fix + ' at ' + Math.round(RG.fixSpacing * 1000) + ' mm, both legs, so twice the run');
      ctx.log('RDG  ridge cap ' + Math.round(run * 1000) + ' mm of ' + Math.round(RG.width * 1000) + ' mm ' + RG.code + ', leg ' + Math.round(half * 1000) + ' mm each side, crease ' + Math.round(lift * 1000) + ' mm above the ridge line');
      ctx.log('skin     ridge cap ' + Math.round(run * 10) / 10 + ' m of ' + Math.round(RG.width * 1000) + ' mm (' + RG.code + '), a leg of ' + Math.round(half * 1000) + ' mm down each slope');
      why('skin     ONE crease: both legs start on the ridge line lifted straight up and fall away down their own slope. Lifted along their own normals they came out 77 mm apart and the cap was two halves with a V between them');
      why('skin     the crease is on the PUBLISHED ridge -- each plane offset along its own normal puts its copy 125 mm off the apex, and taking whichever came first put the cap that far off centre');
      ctx.log('skin     it sits on the covering\'s own top (' + Math.round(_rsCovTop(spec, slopeR) * 1000) + ' mm) plus ' + fmtLen(RG.stand) + ', not on the battens');
      ctx.log('skin     the leg laps the covering by ' + fmtLen(RG.width / 2 - TB.ridgeGap) + ' -- the sheet stops ' + fmtLen(TB.ridgeGap) + ' below the ridge for the vent and the cap covers it');
      ctx.log('skin     ' + Math.round(run * 10) / 10 + ' m of vent strip under it -- a cold roof takes air in at the eave and lets it out here, and a cap bedded tight would seal the outlet and stop the whole cavity working');
      return { len: run + capLen };
    }

    /* =========================================================================
       THE GUTTERS AND THE DOWNSPOUTS IN THE SHARED DRAWER (core-v484, K7,
       migrated from the T house, thouse-v225..v235, which had them from the
       ADU's _gutters, adu-v1396). roofSkin builds them when the spec has
         gutters: { layer?, color?, posts?: [{ x, z, half }], walls?: [[x, z]],
                    faceOff?, reachNote? }
       posts are what a pipe may come down (a post under the eave wins), walls
       the plan outline whose corners are the other places (the house's own
       outline when not given), faceOff how far the wall's face stands off
       that outline. Planes flagged _split are skipped (their eave strip
       carries the gutter). Everything below is the T house's code, renamed.
       ========================================================================= */
    var _rsgG = null, _rsgLayer = null, _rsgColor = 0x6B6F73, _rsgIns = 0;
    /* =========================================================================
       THE GUTTERS (thouse-v225, G1 -- copied from the ADU's _gutters, adu-v1396,
       Marek: okapy a jejich logiku vzit z ADU).

       COPIED: the K section drawn as a 1.5 mm sheet (RSG_KOUT / RSG_KIN), the trough
       growing out from the fascia line (rotOut), its top just under the drip
       edge, the hidden hangers across the trough with their tab down the back,
       the stop end as a filled K plate, the external corner as two mitred
       wedges of the K section (_rsgCorner), and the rows: ROG01251 by the
       metre, ROA01251 per hanger, ROG00001 per stop end, ROG00003 per corner.

       ADAPTED, because a T is not a box:
         - each run is read off the plane's own outline, not off pl.eave: the
           bar's slope on the wing's side is cut in pieces and every piece
           still carries the WHOLE eave, so the ADU's way drew one gutter four
           times over. Here a run is the plane's vertices that lie on its
           lowest line, end to end -- a piece above the wing has none and gets
           no gutter;
         - the planes are the skin's (_skinPlanes), so a dragged eave takes
           its gutter with it;
         - which ends meet is read from the runs themselves: two ends on one
           point are an EXTERNAL corner (a hip; ADU fitting) or an INTERNAL
           one (where the wing's eave meets the bar's -- no fitting yet, the
           runs just stop at the point and the log counts it);
         - outward is the plane's own fall, not the side away from the middle.
       The pipes are the next step (G2).
       ========================================================================= */
    var RSG = { h: 0.127, w: 0.127, board: 0.025, bracket: 0.60, standoff: 0.05,
                gutter: 'ROG01251', brk: 'ROA01251', cap: 'ROG00001', corner: 'ROG00003', icorner: 'ROG00002',
                hopper: 'ROG00005', pipe: 'ROG01252', elbow: 'ROG00006', shoe: 'ROG00009', clip: 'ROG00011' };
    var RSG_KOUT = [[0, 0.127], [0, 0.008], [0.012, 0], [0.086, 0], [0.086, 0.020],
                 [0.112, 0.045], [0.096, 0.075], [0.127, 0.097], [0.127, 0.115], [0.108, 0.115]];
    var RSG_KIN = [[0.1065, 0.1130], [0.1225, 0.1130], [0.1225, 0.0975], [0.0945, 0.0755],
                [0.1100, 0.0455], [0.0845, 0.0205], [0.0845, 0.0015], [0.0130, 0.0015],
                [0.0015, 0.0090], [0.0015, 0.127]];
    function _rsgShell(T) {
      var sh = new T.Shape(), i;
      sh.moveTo(RSG_KOUT[0][0], RSG_KOUT[0][1]);
      for (i = 1; i < RSG_KOUT.length; i++) sh.lineTo(RSG_KOUT[i][0], RSG_KOUT[i][1]);
      for (i = 0; i < RSG_KIN.length; i++) sh.lineTo(RSG_KIN[i][0], RSG_KIN[i][1]);
      sh.lineTo(RSG_KOUT[0][0], RSG_KOUT[0][1]);
      return sh;
    }
    function _rsgPlate(T) {
      var sh = new T.Shape(), i;
      sh.moveTo(RSG_KOUT[0][0], RSG_KOUT[0][1]);
      for (i = 1; i < RSG_KOUT.length; i++) sh.lineTo(RSG_KOUT[i][0], RSG_KOUT[i][1]);
      sh.lineTo(RSG_KOUT[0][0], RSG_KOUT[0][1]);
      return sh;
    }
    /* the ADU's corner, unchanged: run A ends on the plane of run B's back, run
       B starts on the plane of run A's back, two wedges of the K section between */
    function _rsgCorner(T, C, outA, outB, yBase, mat) {
      var pts = _rsgShell(T).extractPoints(1), rings = [pts.shape].concat(pts.holes || []), r, k;
      var geo = new T.BufferGeometry(), pos = [];
      var board = RSG.board;
      var Cx = C[0] + outA[0] * board + outB[0] * board, Cz = C[1] + outA[1] * board + outB[1] * board;
      function V(p, plane) {
        var x = p.x, y = p.y, fx = (plane === 2) ? 0 : x, fz = (plane === 0) ? 0 : x;
        return [Cx + outA[0] * fx + outB[0] * fz, yBase + y, Cz + outA[1] * fx + outB[1] * fz];
      }
      for (r = 0; r < rings.length; r++) {
        var ring = rings[r], n = ring.length;
        for (k = 0; k < n; k++) {
          var p0 = ring[k], p1 = ring[(k + 1) % n], s;
          for (s = 0; s < 2; s++) {
            var a0 = V(p0, s), a1 = V(p1, s), b0 = V(p0, s + 1), b1 = V(p1, s + 1);
            pos.push(a0[0], a0[1], a0[2], a1[0], a1[1], a1[2], b1[0], b1[1], b1[2]);
            pos.push(a0[0], a0[1], a0[2], b1[0], b1[1], b1[2], b0[0], b0[1], b0[2]);
          }
        }
      }
      geo.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
      geo.computeVertexNormals();
      var m = new T.Mesh(geo, mat);
      m.name = 'gutter-corner';
      return m;
    }
    /* one run per plane: its vertices on the lowest line, end to end along the
       eave, with the outward fall and the height the trough hangs at */
    /* A GUTTER STOPS AT THE OUTER WALL (core-v533, Marek: dovnitr do
       vnitrniho prostoru se natahuji i okapy, maji koncit na vnejsi
       obvodove stene). The bar's eave on the wing's side runs on under the
       wing's roof -- through the house -- and its gutter ran with it. Every
       run is now cut where it crosses the finished face of the walls (the
       outline pushed out by half the wall and the facade): what lies inside
       is dropped, what is left keeps its own ends and a cut end gets a stop
       end. A plain rectangle never has a gutter inside itself, so nothing
       changes there. */
    function _rsgGrow(P, d) {
      var n = P.length, ar = 0, i, out = [];
      for (i = 0; i < n; i++) { var pa = P[i], pb = P[(i + 1) % n]; ar += pa[0] * pb[1] - pb[0] * pa[1]; }
      var ori = (ar >= 0) ? 1 : -1;
      var line = function (i0) {
        var a = P[i0], b = P[(i0 + 1) % n], dx = b[0] - a[0], dz = b[1] - a[1], L = Math.hypot(dx, dz) || 1, ux = dx / L, uz = dz / L;
        var nx = ori * uz, nz = -ori * ux;
        return { px: a[0] + nx * d, pz: a[1] + nz * d, ux: ux, uz: uz };
      };
      for (i = 0; i < n; i++) {
        var A = line((i - 1 + n) % n), B = line(i);
        var den = A.ux * B.uz - A.uz * B.ux;
        if (Math.abs(den) < 1e-9) { out.push([B.px, B.pz]); continue; }
        var t = ((B.px - A.px) * B.uz - (B.pz - A.pz) * B.ux) / den;
        out.push([A.px + A.ux * t, A.pz + A.uz * t]);
      }
      return out;
    }
    var _rsgCutNote = '';
    function _rsgClipRuns(runs) {
      _rsgCutNote = '';
      var P = null; try { P = poly(); } catch (eP) { P = null; }
      if (!P || P.length < 4) return runs;
      var d = 0.15; try { d = wallThickness() / 2 + (facadeThk() || 0); } catch (eD) { d = 0.15; }
      var PO = _rsgGrow(P, d), out = [], notes = [], i, k;
      var inside = function (x, z) { return masPtIn(PO, x, z); };
      for (i = 0; i < runs.length; i++) {
        var r = runs[i];
        if (r.skip) { out.push(r); continue; }
        var at = function (t) { return [r.a[0] + (r.b[0] - r.a[0]) * t, r.a[1] + (r.b[1] - r.a[1]) * t]; };
        var n = Math.max(2, Math.ceil(r.run / 0.025)), segs = [], cur = null;
        for (k = 0; k < n; k++) {
          var t0 = k / n, t1 = (k + 1) / n, pm = at((t0 + t1) / 2);
          if (!inside(pm[0], pm[1])) { if (cur) cur[1] = t1; else { cur = [t0, t1]; segs.push(cur); } }
          else cur = null;
        }
        if (segs.length === 1 && segs[0][0] <= 1e-9 && segs[0][1] >= 1 - 1e-9) { out.push(r); continue; }
        if (!segs.length) { out.push({ id: r.id, skip: 'inside the house, under the wing' }); notes.push(r.id + ' dropped (inside)'); continue; }
        /* the cut ends sharpened onto the wall face */
        var sharpen = function (tOut, tIn) { var q; for (q = 0; q < 10; q++) { var tm = (tOut + tIn) / 2, pq = at(tm); if (inside(pq[0], pq[1])) tIn = tm; else tOut = tm; } return tOut; };
        for (k = 0; k < segs.length; k++) {
          var s0 = segs[k][0], s1 = segs[k][1];
          if (s0 > 1e-9) s0 = sharpen(s0, s0 - 1 / n);
          if (s1 < 1 - 1e-9) s1 = sharpen(s1, s1 + 1 / n);
          var L2 = (s1 - s0) * r.run;
          if (L2 < 0.30) { notes.push(r.id + ' piece ' + Math.round(L2 * 1000) + ' dropped (too short)'); continue; }
          var r2 = {}, kk;
          for (kk in r) if (r.hasOwnProperty(kk)) r2[kk] = r[kk];
          r2.a = at(s0); r2.b = at(s1); r2.run = L2;
          r2.id = r.id + (segs.length > 1 ? String.fromCharCode(97 + k) : '');
          r2.extA = 0; r2.extB = 0; r2.capA = true; r2.capB = true; r2.cA = null; r2.cB = null;
          out.push(r2);
          notes.push(r2.id + ' ' + Math.round(L2 * 1000) + ' of ' + Math.round(r.run * 1000) + ' kept, cut at the wall face');
        }
      }
      if (notes.length) _rsgCutNote = notes.join(', ') + ' (outline pushed out ' + Math.round(d * 1000) + ')';
      return out;
    }
    function _rsgRuns(PLs, lift) {
      var runs = [], i, k;
      for (i = 0; i < PLs.length; i++) {
        var pl = PLs[i], n = pl.n, P = pl.pts;
        if (!n || !P || P.length < 3) continue;
        /* a split plane's gutter hangs off its strip (thouse-v235) */
        if (pl._split) continue;
        var hl = Math.hypot(n[0], n[2]);
        if (!(hl > 0.05) || !(Math.abs(n[1]) > 0.05)) continue;
        var nx = n[0] / hl, nz = n[2] / hl, ux = -nz, uz = nx;
        var eY = 1e9;
        for (k = 0; k < P.length; k++) eY = Math.min(eY, P[k][1]);
        var tMin = 1e9, tMax = -1e9, pA = null, pB = null, nOn = 0;
        for (k = 0; k < P.length; k++) {
          if (Math.abs(P[k][1] - eY) > 0.005) continue;
          nOn++;
          var t = P[k][0] * ux + P[k][2] * uz;
          if (t < tMin) { tMin = t; pA = P[k]; }
          if (t > tMax) { tMax = t; pB = P[k]; }
        }
        if (nOn < 2 || !(tMax - tMin > 0.6)) { runs.push({ id: pl.id, skip: nOn < 2 ? 'no eave (its low edge is a valley)' : 'eave under 600' }); continue; }
        var dyDeck = lift(pl) / Math.abs(n[1]);
        runs.push({ id: pl.id, wing: !!pl.wing, a: [pA[0], pA[2]], b: [pB[0], pB[2]], y: eY + dyDeck,
                    ux: ux, uz: uz, nx: nx, nz: nz, run: tMax - tMin, extA: 0, extB: 0, capA: true, capB: true, cA: null, cB: null });
      }
      return runs;
    }

    /* =========================================================================
       THE OUTLETS AND THE DOWNSPOUTS (thouse-v226, G2 -- copied from the ADU's
       _outletPlan, _dspTarget and the pipe in _gutters, adu-v1396).

       COPIED: the downspout section (60 x 87 rounded, hollow, 1.2 mm wall --
       what a dia 80 holds, the US rectangular pipe), the swept pipe with small
       arcs at the bends (_rsgPath / _rsgSweep), the outlet as a tapered collar
       in the trough floor, the neck (two bends, a cut length between when the
       offset is long), the shoe kicking out 130 at the bottom, the brackets at
       2 m at most with the top one just under the lower bend and a leg back to
       what the pipe hangs on; and the ADU's rules for where the pipe goes:
         - A POST ON THIS EAVE WINS: where the roof was dragged out far enough
           to stand on posts, the pipe comes down the post (Marek) -- here the
           brick post, on its outer face;
         - otherwise it comes down a WALL, not a corner: on the face under
           this eave, 300 in from the arris;
         - a closed ring of gutters (a hip) takes two pipes on opposite corners,
           and a target is not used twice.
       ADAPTED for the T: the runs that join (at a hip corner or where the
       wing's eave meets the bar's) are one DRAINAGE GROUP, and each group
       gets its pipes -- one, two for a closed ring, and one more for every
       12 m of gutter past that -- spread as far apart as the candidates
       allow. The candidates are the brick posts published by the frame and
       the corners of the T's own walls (the inner corners of the T too); the
       wall face is the centre line plus half the wall and 20 of render.
       ========================================================================= */
    var RSG_DSP = { w: 0.060, d: 0.087, r: 0.010, wall: 0.0012 };
    function _rsgRound(T, w, d, r) {
      var sh = new T.Shape(), hw = w / 2, hd = d / 2;
      sh.moveTo(-hw + r, -hd); sh.lineTo(hw - r, -hd); sh.quadraticCurveTo(hw, -hd, hw, -hd + r);
      sh.lineTo(hw, hd - r); sh.quadraticCurveTo(hw, hd, hw - r, hd); sh.lineTo(-hw + r, hd);
      sh.quadraticCurveTo(-hw, hd, -hw, hd - r); sh.lineTo(-hw, -hd + r); sh.quadraticCurveTo(-hw, -hd, -hw + r, -hd);
      return sh;
    }
    /* THE PIPE SAMPLED WHERE IT BENDS (core-v814, T house thread for the perf
       line, Marek 2026-11-04: zrychlit i na mobilnim telefonu). The sweep's
       steps were spread evenly along the whole route -- max(24, 14 a point) --
       so a straight 2.5 m drop carried dozens of rings that a straight prism
       needs two of, and the section was 52 points outside and 100 in the
       1.2 mm bore (24 a corner of a 10 mm radius): the T house's six pipes
       were 389k vertices, a quarter of the house, and the sweep its slowest
       piece of geometry (0.67 s of the first build at 4x CPU). Now the route
       is handed to the extrusion piece by piece (_rsgPieces): a straight piece
       gets its two ends, a bend RSG_BEND rings (it had about 3.5); the frames
       are the same parallel transport over the same route, a straight run the
       same prism; the section 4 points a corner outside and in (a facet of
       2.6 mm on the 10 mm radius). The pipe's length, the bill and the route
       are untouched. ?dsp=even sweeps as before. */
    var RSG_BEND = 6, RSG_DSP_SEG = 4, _rsgEvenQ = null;
    function _rsgEven() {
      if (_rsgEvenQ === null) { try { _rsgEvenQ = /[?&]dsp=even(&|$)/.test(String((global.location && global.location.search) || '')); } catch (e) { _rsgEvenQ = false; } }
      return _rsgEvenQ;
    }
    function _rsgPieces(T, path) {
      var cs = (path && path.curves) || [], segs = [], n = 0, i;
      for (i = 0; i < cs.length; i++) {
        var k = (cs[i].isLineCurve3 || cs[i].isLineCurve) ? 1 : RSG_BEND;
        segs.push({ c: cs[i], k: k, s0: n });
        n += k;
      }
      if (!n || typeof T.Curve !== 'function') return null;
      var at = function (u) {
        var x = Math.max(0, Math.min(1, u)) * n, j = segs.length - 1;
        while (j > 0 && x < segs[j].s0 - 1e-9) j--;
        return { c: segs[j].c, t: Math.max(0, Math.min(1, (x - segs[j].s0) / segs[j].k)) };
      };
      var C = new T.Curve();
      C.getPointAt = function (u, out) { var a = at(u); return a.c.getPointAt(a.t, out); };
      C.getPoint = C.getPointAt;
      C.getTangentAt = function (u, out) { var a = at(u); return a.c.getTangentAt(a.t, out); };
      C.getTangent = C.getTangentAt;
      C.getLength = function () { return path.getLength(); };
      return { curve: C, steps: n };
    }
    function _rsgDspShell(T) {
      var out = _rsgRound(T, RSG_DSP.w, RSG_DSP.d, RSG_DSP.r), wall = RSG_DSP.wall;
      var inner = _rsgRound(T, RSG_DSP.w - wall * 2, RSG_DSP.d - wall * 2, Math.max(0.001, RSG_DSP.r - wall));
      out.holes.push(new T.Path(inner.getPoints(_rsgEven() ? 24 : RSG_DSP_SEG)));
      return out;
    }
    function _rsgPath(T, pts, r) {
      var path = new T.CurvePath(), i;
      var V = function (p) { return new T.Vector3(p[0], p[1], p[2]); };
      if (pts.length < 2) return null;
      var cur = V(pts[0]);
      for (i = 1; i < pts.length - 1; i++) {
        var a = V(pts[i - 1]), b = V(pts[i]), c = V(pts[i + 1]);
        var ab = new T.Vector3().subVectors(b, a), bc = new T.Vector3().subVectors(c, b);
        var rr = Math.min(r, ab.length() * 0.45, bc.length() * 0.45);
        var pIn = new T.Vector3().copy(b).addScaledVector(ab.clone().normalize(), -rr);
        var pOut = new T.Vector3().copy(b).addScaledVector(bc.clone().normalize(), rr);
        if (cur.distanceTo(pIn) > 1e-4) path.add(new T.LineCurve3(cur.clone(), pIn));
        path.add(new T.QuadraticBezierCurve3(pIn, b, pOut));
        cur = pOut;
      }
      var last = V(pts[pts.length - 1]);
      if (cur.distanceTo(last) > 1e-4) path.add(new T.LineCurve3(cur.clone(), last));
      return path;
    }
    function _rsgSweep(T, g, mat, pts) {
      var path = _rsgPath(T, pts, 0.075);
      if (!path) return 0;
      var PC = _rsgEven() ? null : _rsgPieces(T, path);
      var geo = PC ? new T.ExtrudeGeometry(_rsgDspShell(T), { extrudePath: PC.curve, steps: PC.steps, curveSegments: RSG_DSP_SEG, bevelEnabled: false })
                   : new T.ExtrudeGeometry(_rsgDspShell(T), { extrudePath: path, steps: Math.max(24, pts.length * 14), bevelEnabled: false });
      var m = new T.Mesh(geo, mat); m.name = 'downspout';
      g.add(m);
      return path.getLength();
    }
    /* where a pipe can come down, for one run: the posts standing under it and
       the wall corners under it, each as a spot along the gutter (t from the
       run's middle) and the point the pipe is fixed to */
    function _rsgCands(r, far) {
      var out = [], i, P = [], faceOff = (_rsgG && typeof _rsgG.faceOff === 'number') ? _rsgG.faceOff : (facadeThk() + 0.020), POSTS = (_rsgG && _rsgG.posts) || [];
      try { P = (_rsgG && _rsgG.walls) || poly() || []; } catch (eP) { P = []; }
      var half = r.run / 2, lineOff = RSG.board + RSG.w / 2;
      var mx = r.bx - r.nx * RSG.board, mz = r.bz - r.nz * RSG.board;
      var clampT = function (t) { return Math.max(-half + 0.12, Math.min(half - 0.12, t)); };
      for (i = 0; i < POSTS.length; i++) {
        var pp = POSTS[i], t = (pp.x - mx) * r.hdX + (pp.z - mz) * r.hdZ, o = (pp.x - mx) * r.nx + (pp.z - mz) * r.nz;
        if (Math.abs(t) > half + 0.05 || o > 0.3 || lineOff - o > (far ? 8.0 : 2.0)) continue;
        /* the pipe on the post's outer face, square to this eave */
        var st = pp.half + RSG.standoff + RSG_DSP.d / 2, tc = clampT(t);
        out.push({ what: 'post', t: tc, x: pp.x + r.nx * st, z: pp.z + r.nz * st, ox: r.nx, oz: r.nz, stand: st, key: 'p' + i, px: pp.x, pz: pp.z });
      }
      for (i = 0; i < P.length; i++) {
        var c = P[i], tC = (c[0] - mx) * r.hdX + (c[1] - mz) * r.hdZ, oC = (c[0] - mx) * r.nx + (c[1] - mz) * r.nz;
        if (Math.abs(tC) > half + 0.05 || oC > 0.05 || lineOff - oC > (far ? 8.0 : 3.0)) continue;
        /* 300 in from the arris along the face under this eave, towards the
           middle of the run, standing off the render */
        var tIn = tC + (tC > 0 ? -0.30 : 0.30), tH = clampT(tIn), st2 = faceOff + RSG.standoff + RSG_DSP.d / 2;
        var wx = c[0] + r.hdX * (tH - tC) + r.nx * st2, wz = c[1] + r.hdZ * (tH - tC) + r.nz * st2;
        out.push({ what: 'wall', t: tH, x: wx, z: wz, ox: r.nx, oz: r.nz, stand: st2, key: 'c' + i, px: c[0], pz: c[1] });
      }
      return out;
    }
    function _rsgPipes(ctx, runs, links, metal) {
      var T = ctx.THREE, g = ctx.group, logs = [], i, k;
      /* the drainage groups: runs joined end to end */
      var par = [];
      for (i = 0; i < runs.length; i++) par.push(i);
      var find = function (x) { while (par[x] !== x) { par[x] = par[par[x]]; x = par[x]; } return x; };
      for (i = 0; i < links.length; i++) { var ra = find(links[i][0]), rb = find(links[i][1]); if (ra !== rb) par[ra] = rb; }
      var groups = {}, gk;
      for (i = 0; i < runs.length; i++) { gk = find(i); (groups[gk] = groups[gk] || []).push(i); }
      var tot = { hop: 0, pipe: 0, elb: 0, shoe: 0, clip: 0 }, used = {};
      for (gk in groups) {
        if (!groups.hasOwnProperty(gk)) continue;
        var G = groups[gk], L = 0, closed = true, ids = [], cands = [];
        for (k = 0; k < G.length; k++) {
          var r = runs[G[k]];
          L += r.run; ids.push(r.id);
          if (r.capA || r.capB) closed = false;
          var cs = _rsgCands(r), c9;
          for (c9 = 0; c9 < cs.length; c9++) { cs[c9].run = r; cands.push(cs[c9]); }
        }
        var want = Math.max(closed ? 2 : 1, Math.ceil(L / 12.0));
        var chosen = [], nPost = 0;
        for (k = 0; k < cands.length; k++) if (cands[k].what === 'post') nPost++;
        var hopXZ = function (c) { var rr = c.run, lo = RSG.w / 2; return [rr.bx + rr.nx * lo + rr.hdX * c.t, rr.bz + rr.nz * lo + rr.hdZ * c.t]; };
        var bendOf = function (c) { var h = hopXZ(c); return Math.hypot(c.x - h[0], c.z - h[1]); };
        while (chosen.length < want) {
          var best = null, bestS = -1e9;
          for (k = 0; k < cands.length; k++) {
            var cc = cands[k];
            if (used[cc.key + '@' + cc.run.id] || chosen.indexOf(cc) >= 0) continue;
            var h1 = hopXZ(cc), dmin = 1e9, q;
            for (q = 0; q < chosen.length; q++) { var h2 = hopXZ(chosen[q]); dmin = Math.min(dmin, Math.hypot(h1[0] - h2[0], h1[1] - h2[1])); if (cc.key === chosen[q].key) dmin = -1e9; }
            if (!chosen.length) dmin = 0;
            if (chosen.length && dmin < 1.0) continue;
            var sc = dmin + (cc.what === 'post' ? 3.0 : 0) - bendOf(cc) * 0.5;
            if (sc > bestS) { bestS = sc; best = cc; }
          }
          if (!best) break;
          chosen.push(best); used[best.key + '@' + best.run.id] = 1;
        }
        /* NOTHING NEAR: look further before hanging a pipe on nothing (core-v484).
           A run far out on a deep reach has its wall or post more than the
           usual 2 to 3 m in; the pipe then takes a long neck back to it, which
           the log shows, rather than dropping free */
        if (!chosen.length) {
          var farC = [], kf, cf;
          for (kf = 0; kf < G.length; kf++) { cf = _rsgCands(runs[G[kf]], true); for (var qf = 0; qf < cf.length; qf++) { cf[qf].run = runs[G[kf]]; farC.push(cf[qf]); } }
          var bestF = null, bdF = 1e9;
          for (kf = 0; kf < farC.length; kf++) { var bF = bendOf(farC[kf]) - (farC[kf].what === 'post' ? 1.0 : 0); if (bF < bdF) { bdF = bF; bestF = farC[kf]; } }
          if (bestF) chosen.push(bestF);
        }
        if (!chosen.length) {
          /* nothing under this group to hang a pipe on: the outlet goes at a
             free end of its first run and the pipe drops straight, and the log
             says it has nothing behind it */
          var r0 = runs[G[0]], tE = r0.run / 2 - 0.12, hx0 = r0.bx + r0.nx * RSG.w / 2 + r0.hdX * tE, hz0 = r0.bz + r0.nz * RSG.w / 2 + r0.hdZ * tE;
          chosen.push({ what: 'free', t: tE, x: hx0, z: hz0, ox: r0.nx, oz: r0.nz, stand: 0, run: r0, key: 'free' });
        }
        var said = [];
        for (k = 0; k < chosen.length; k++) {
          var ch = chosen[k], rr2 = ch.run, hp = hopXZ(ch), yTop = rr2.yTop;
          /* the collar in the trough floor */
          var col = new T.Mesh(new T.CylinderGeometry(0.086 / 2, Math.max(RSG_DSP.w, RSG_DSP.d) / 2, 0.10, 8, 1, true), metal);
          col.position.set(hp[0], yTop - RSG.h - 0.05 + 0.004, hp[1]); col.rotation.y = Math.PI / 8;
          g.add(col); tot.hop++;
          var yHop = yTop - RSG.h - 0.10, yStub = yHop - 0.08, yBendTop = yStub;
          var route = [[hp[0], yHop, hp[1]], [hp[0], yStub, hp[1]]];
          var offX = ch.x - hp[0], offZ = ch.z - hp[1], off = Math.hypot(offX, offZ);
          if (off > 0.04) {
            var neck = Math.max(0.12, off * 0.8), yNeck = yStub - neck;
            route.push([ch.x, yNeck, ch.z]); tot.elb += 2; yBendTop = yNeck;
          }
          route.push([ch.x, 0.16, ch.z]);
          route.push([ch.x + ch.ox * 0.13, 0.085, ch.z + ch.oz * 0.13]);
          tot.pipe += _rsgSweep(T, g, metal, route);
          tot.shoe++; tot.elb++;
          /* the brackets: at 2 m at most, the top one under the lower bend, a
             strap round the section and a leg back as long as the standoff */
          var face = Math.atan2(ch.ox, ch.oz), yLow = 0.45, ySpan = yBendTop - yLow, nC = Math.max(1, Math.ceil(ySpan / 2.0)), qc, nClip = 0;
          if (ch.what !== 'free') {
            for (qc = 0; qc <= nC; qc++) {
              var yc = yBendTop - 0.14 - (ySpan - 0.14) * (qc / Math.max(1, nC));
              if (yc < yLow - 0.2) continue;
              var stp = new T.Mesh(new T.BoxGeometry(RSG_DSP.w + 0.016, 0.016, RSG_DSP.d + 0.016), metal);
              stp.position.set(ch.x, yc, ch.z); stp.rotation.y = face; g.add(stp);
              var lg = new T.Mesh(new T.BoxGeometry(0.014, 0.012, RSG.standoff), metal);
              lg.position.set(ch.x - ch.ox * (RSG_DSP.d / 2 + RSG.standoff / 2), yc, ch.z - ch.oz * (RSG_DSP.d / 2 + RSG.standoff / 2));
              lg.rotation.y = face; g.add(lg);
              tot.clip++; nClip++;
            }
          }
          said.push(rr2.id + ' at ' + Math.round(hp[0] * 1000) + ',' + Math.round(hp[1] * 1000) + ' down the ' + ch.what +
                    (ch.what === 'free' ? ' (NOTHING BEHIND IT -- no post or wall corner under this group)' : (' at ' + Math.round(ch.px * 1000) + ',' + Math.round(ch.pz * 1000))) +
                    ', neck ' + Math.round(off * 1000) + ', ' + nClip + ' bracket(s)');
        }
        logs.push('pipe     group ' + ids.join('+') + ' (' + (Math.round(L * 10) / 10) + ' m, ' + (closed ? 'closed ring' : 'open') + ', ' + nPost + ' post candidate(s)): ' +
                  chosen.length + ' pipe(s) -- ' + said.join(' | '));
      }
      if (tot.hop) {
        billAdd(_rsgLayer, RSG.hopper, tot.hop);
        billAdd(_rsgLayer, RSG.pipe, tot.pipe);
        billAdd(_rsgLayer, RSG.elbow, tot.elb);
        billAdd(_rsgLayer, RSG.shoe, tot.shoe);
        if (tot.clip) billAdd(_rsgLayer, RSG.clip, tot.clip);
      }
      logs.push('pipe     ' + tot.hop + ' x ' + RSG.hopper + ' outlet(s), ' + (Math.round(tot.pipe * 100) / 100) + ' m ' + RSG.pipe + ', ' + tot.elb + ' x ' + RSG.elbow + ' elbow(s), ' +
                tot.shoe + ' x ' + RSG.shoe + ' shoe(s), ' + tot.clip + ' x ' + RSG.clip + ' bracket(s) -- a post under the eave wins, else a wall 300 in from the corner');
      return logs;
    }

    function _rsGutters(ctx, spec, planes) {
      var T = ctx.THREE, g = ctx.group, G = spec.gutters || {};
      _rsgG = G; _rsgLayer = G.layer || spec.layer; _rsgIns = _rsInsT(spec) + _rsLinT(spec);
      _rsgColor = (G.color != null) ? G.color : colorSlotOf('metal');
      var runs = _rsgClipRuns(_rsgRuns(planes, spec.lift)), i, j, e1, e2;
      var ok = [], skipped = [];
      for (i = 0; i < runs.length; i++) (runs[i].skip ? skipped : ok).push(runs[i]);
      /* NEAR-MISS CORNERS SNAPPED (core-v816, T house thread, opt-in
         spec.gutters.snap, a distance). Two runs at one height whose ends miss
         each other by up to that much -- a wing's eave run on past the bar's by
         its rafter tails, the two eaves level, so one run stops short of the
         corner and the other overshoots it -- are cut or run on to the point
         where their lines cross; they then meet there and take their corner
         piece like any other pair. Runs at different heights are left as they
         were (stop ends, v227). Without spec.gutters.snap nothing changes. */
      var _snapD = (typeof G.snap === 'number' && G.snap > 0) ? G.snap : 0, _snapSaid = [];
      if (_snapD > 0) (function () {
        var i2, j2, a1, a2;
        for (i2 = 0; i2 < ok.length; i2++) for (j2 = i2 + 1; j2 < ok.length; j2++) {
          var A = ok[i2], B = ok[j2];
          if (Math.abs(A.y - B.y) > 0.05) continue;
          var cr = A.ux * B.uz - A.uz * B.ux; if (Math.abs(cr) < 0.5) continue;
          for (a1 = 0; a1 < 2; a1++) for (a2 = 0; a2 < 2; a2++) {
            var pa = a1 ? A.b : A.a, pb = a2 ? B.b : B.a, d0 = Math.hypot(pa[0] - pb[0], pa[1] - pb[1]);
            if (!(d0 > 0.05 && d0 <= _snapD)) continue;
            var dx = pb[0] - pa[0], dz = pb[1] - pa[1], sA = (dx * B.uz - dz * B.ux) / cr;
            var X = [pa[0] + A.ux * sA, pa[1] + A.uz * sA];
            if (Math.hypot(X[0] - pa[0], X[1] - pa[1]) > _snapD || Math.hypot(X[0] - pb[0], X[1] - pb[1]) > _snapD) continue;
            var nA = { a: a1 ? A.a : X, b: a1 ? X : A.b }, nB = { a: a2 ? B.a : X, b: a2 ? X : B.b };
            var rA = (nA.b[0] - nA.a[0]) * A.ux + (nA.b[1] - nA.a[1]) * A.uz, rB = (nB.b[0] - nB.a[0]) * B.ux + (nB.b[1] - nB.a[1]) * B.uz;
            if (!(rA > 0.30) || !(rB > 0.30)) continue;
            A.a = nA.a; A.b = nA.b; A.run = rA; B.a = nB.a; B.b = nB.b; B.run = rB;
            _snapSaid.push(A.id + '/' + B.id + ' ' + Math.round(d0 * 1000) + ' apart -> met at ' + Math.round(X[0] * 1000) + ',' + Math.round(X[1] * 1000));
          }
        }
      })();
      if (_snapSaid.length) ctx.log('gutter   near-miss corners snapped (core-v816, spec.gutters.snap ' + Math.round(_snapD * 1000) + '): ' + _snapSaid.join(', '));
      /* which ends meet: same point in plan, same height */
      var corners = [], nIn = 0, inSaid = [], links = [];
      var endPt = function (r, e) { return e ? r.b : r.a; };
      for (i = 0; i < ok.length; i++) for (j = i + 1; j < ok.length; j++) {
        for (e1 = 0; e1 < 2; e1++) for (e2 = 0; e2 < 2; e2++) {
          var A = ok[i], B = ok[j], pa = endPt(A, e1), pb = endPt(B, e2);
          if (Math.hypot(pa[0] - pb[0], pa[1] - pb[1]) > 0.05 || Math.abs(A.y - B.y) > 0.05) continue;
          /* B's run leaves the point along +u from its a end, -u from its b end */
          var dBx = e2 ? -B.ux : B.ux, dBz = e2 ? -B.uz : B.uz;
          var external = (dBx * A.nx + dBz * A.nz) < 0;
          if (e1) A.capB = false; else A.capA = false;
          if (e2) B.capB = false; else B.capA = false;
          links.push([i, j]);
          /* THE INTERNAL CORNER (thouse-v227, G3, ROG00002): the same fitting
             as the external one, drawn by the same mitre between the two
             backs -- inside the angle each run stops on the OTHER run's back
             line, a fascia board short of the point, and the fitting fills
             the square between them. Outside the angle each run goes on a
             board to the other's back. Either way the metres are billed from
             corner point to corner point and the fitting as a piece, so no
             length is counted twice and none is lost. */
          var ext9 = external ? RSG.board : -RSG.board;
          if (e1) A.extB = ext9; else A.extA = ext9;
          if (e2) B.extB = ext9; else B.extA = ext9;
          corners.push({ c: pa, nA: [A.nx, A.nz], nB: [B.nx, B.nz], y: Math.min(A.y, B.y), ids: A.id + '/' + B.id, inner: !external });
          if (!external) { nIn++; inSaid.push(A.id + '/' + B.id + ' at ' + Math.round(pa[0] * 1000) + ',' + Math.round(pa[1] * 1000)); }
        }
      }
      /* ends that nearly meet and do not (thouse-v227, G3): where the wing's
         eave was dragged and the bar's eave on the wing's side cannot be, the
         wing's run ends lower and further out than the bar's -- they get stop
         ends instead of a corner, and the log names them */
      var near = [];
      for (i = 0; i < ok.length; i++) for (e1 = 0; e1 < 2; e1++) {
        if (!(e1 ? ok[i].capB : ok[i].capA)) continue;
        var pI = endPt(ok[i], e1);
        for (j = 0; j < ok.length; j++) {
          if (j === i) continue;
          for (e2 = 0; e2 < 2; e2++) {
            var pJ = endPt(ok[j], e2), dd = Math.hypot(pI[0] - pJ[0], pI[1] - pJ[1]);
            if (dd > 0.05 && dd < 1.5 && i < j) near.push(ok[i].id + '/' + ok[j].id + ' ' + Math.round(dd * 1000) + ' apart, ' + Math.round(Math.abs(ok[i].y - ok[j].y) * 1000) + ' in height');
          }
        }
      }
      var metal = new T.MeshStandardMaterial({ color: _rsgColor, roughness: 0.45, metalness: 0.6, side: T.DoubleSide });
      var shp = _rsgShell(T), tot = { run: 0, brk: 0, caps: 0 }, said = [], q;
      for (i = 0; i < ok.length; i++) {
        var r = ok[i], nx = r.nx, nz = r.nz;
        var rotOut = Math.atan2(nx, nz) - Math.PI / 2, hdX = Math.sin(rotOut), hdZ = Math.cos(rotOut);
        /* the ends in the extrusion's own direction: the minus end is the one
           further back along hd */
        var ta = r.a[0] * hdX + r.a[1] * hdZ, tb = r.b[0] * hdX + r.b[1] * hdZ;
        var eMinus = (ta <= tb) ? r.extA : r.extB, ePlus = (ta <= tb) ? r.extB : r.extA;
        var capMinus = (ta <= tb) ? r.capA : r.capB, capPlus = (ta <= tb) ? r.capB : r.capA;
        var mx = (r.a[0] + r.b[0]) / 2, mz = (r.a[1] + r.b[1]) / 2, run = r.run;
        var yTop = r.y + 0.018 + _rsgIns - 0.055 - 0.012;
        var bx = mx + nx * RSG.board, bz = mz + nz * RSG.board;
        r.bx = bx; r.bz = bz; r.hdX = hdX; r.hdZ = hdZ; r.yTop = yTop;
        var geo = new T.ExtrudeGeometry(shp, { depth: run + eMinus + ePlus, bevelEnabled: false });
        geo.translate(0, 0, -run / 2);
        geo.rotateY(rotOut);
        var mesh = new T.Mesh(geo, metal);
        mesh.position.set(bx - hdX * eMinus, yTop - RSG.h, bz - hdZ * eMinus);
        mesh.name = 'gutter';
        g.add(mesh);
        tot.run += run;
        /* the hidden hangers, 600 apart */
        var nB = Math.max(2, Math.round(run / RSG.bracket));
        for (q = 0; q <= nB; q++) {
          var t2 = -run / 2 + run * (q / nB), yBar = yTop - 0.020;
          var bg = new T.BoxGeometry(RSG.w - 0.008, 0.004, 0.022); bg.rotateY(rotOut);
          var bm = new T.Mesh(bg, metal); bm.position.set(bx + nx * (RSG.w / 2) + hdX * t2, yBar, bz + nz * (RSG.w / 2) + hdZ * t2); g.add(bm);
          var tg = new T.BoxGeometry(0.0015, 0.045, 0.022); tg.rotateY(rotOut);
          var tm = new T.Mesh(tg, metal); tm.position.set(bx + hdX * t2, yBar - 0.022, bz + hdZ * t2); g.add(tm);
          tot.brk++;
        }
        /* a stop end wherever the run meets nothing */
        var sg2;
        for (sg2 = -1; sg2 <= 1; sg2 += 2) {
          if (!(sg2 < 0 ? capMinus : capPlus)) continue;
          var cg = new T.ExtrudeGeometry(_rsgPlate(T), { depth: 0.0015, bevelEnabled: false }); cg.rotateY(rotOut);
          var cm = new T.Mesh(cg, metal);
          cm.position.set(bx + hdX * (run / 2) * sg2, yTop - RSG.h, bz + hdZ * (run / 2) * sg2);
          g.add(cm);
          tot.caps++;
        }
        said.push(r.id + ' ' + Math.round(run * 1000) + (eMinus || ePlus ? ' +' + Math.round((eMinus + ePlus) * 1000) : '') +
                  ' (' + (capMinus ? 'end' : 'joins') + '/' + (capPlus ? 'end' : 'joins') + ')');
      }
      var cMat = new T.MeshStandardMaterial({ color: _rsgColor, roughness: 0.4, metalness: 0.7, side: T.DoubleSide });
      for (i = 0; i < corners.length; i++) {
        var yB = corners[i].y + 0.018 + _rsgIns - 0.055 - 0.012 - RSG.h;
        g.add(_rsgCorner(T, corners[i].c, corners[i].nA, corners[i].nB, yB, cMat));
      }
      var pipeLog = _rsgPipes(ctx, ok, links, metal);
      var nOut = corners.length - nIn;
      if (tot.run > 0) {
        billAdd(_rsgLayer, RSG.gutter, tot.run);
        billAdd(_rsgLayer, RSG.brk, tot.brk);
        if (tot.caps) billAdd(_rsgLayer, RSG.cap, tot.caps);
        if (nOut) billAdd(_rsgLayer, RSG.corner, nOut);
        if (nIn) billAdd(_rsgLayer, RSG.icorner, nIn);
      }
      ctx.log('gutter   runs ' + ok.length + ': ' + (said.length ? said.join(', ') : 'none') + (skipped.length ? (' | no gutter on ' + skipped.map(function (x) { return x.id + ' (' + x.skip + ')'; }).join(', ')) : ''));
      ctx.log('gutter   ' + (Math.round(tot.run * 100) / 100) + ' m ' + RSG.gutter + ' (corner point to corner point), ' + tot.brk + ' x ' + RSG.brk + ' hangers at ' + Math.round(RSG.bracket * 1000) + ', ' + tot.caps + ' x ' + RSG.cap + ' stop end(s)');
      ctx.log('gutter   corners: ' + nOut + ' x ' + RSG.corner + ' external' + (nOut ? ' (' + corners.filter(function (c) { return !c.inner; }).map(function (c) { return c.ids; }).join(', ') + ')' : '') +
              ', ' + nIn + ' x ' + RSG.icorner + ' internal' + (nIn ? ' (' + inSaid.join(', ') + ')' : '') + ' -- each a piece, the runs end on it');
      ctx.log('gutter   eave reach under the gutters: ' + (G.reachNote || 'none') + ' | posts under the eaves: ' + ((G.posts || []).length));
      ctx.log('gutter   runs against the outer walls: ' + (_rsgCutNote || 'none crosses the house -- nothing cut'));
      ctx.log('gutter   ends that nearly meet: ' + (near.length ? near.join(', ') + ' -- NOT JOINED, each has a stop end (the two eaves were dragged by different reaches)' : 'none'));
      for (i = 0; i < pipeLog.length; i++) ctx.log(pipeLog[i]);
    }

    /* THE DRAWER (core-v459, K3): the layers asked for in spec.only, or every
       layer present in the spec, in build order. Deck and underlay are in;
       battens, edges, covering, ridge and gutters still say they are not. */
    /* =========================================================================
       THE WALL KIT (core-v775 the inside, core-v776 the outside, core-v778 the
       gables, eaves and soffits -- the fifth
       shared module of the house line, its second part, status/modules.md). The wall's layers are many small
       builders run in the product's own order by its _buildWallBattens; this
       is where they are written once. It starts with the inside of the wall:
       the service battens, the board (plain, green, the tiled wet band, the
       reveals), the wool between the battens, the vapour barrier, the batts
       between the studs and the interior sills -- with the helpers they share
       (_wallNew, _wallStepOn, _wallInner, _battenRuns, _battenCourses,
       _wallFaces, _wallTileBill). core-v776 adds the outside: sheathing
       (the shed family's panel siding), breather, facade battens, boards,
       EPS, mesh, coat, render, skirt, outside sills, corner trims, reveals,
       with _wallSkin, _gableOf, _wallLift, _facadeFace, _skirtDrop.

         API.wallKit({
           api, layer ('adu-wall'), MAGENTA, VB_T, WALL_BATTEN, WOOD,
           FILLER_KG_M2, JOINERY, PART, STUD, WALL_BATT, WALL_BOARD,
           WALL_ISILL, WALL_TILE, WALL_VB, WALL_WOOL, WET_TILE_H,
           boardCol, roomRects, showerOnFace, tileMat, wetAt, runsThrough,
           magenta(), step(), frameBodies(), wallStuds()  -- this build's
           add:    function (code, qty)
           paint:  function (m2) -- the board's area for the interior paint
           hot:    { step(), runs, split, at } -- SAUNA / HOUSEBOAT
           batch:  { boxes, baked } -- RANCH's one body a layer
           lin, bareAt -- RANCH / GA: a garage wall boarded on the studs
           ga:     { roomSt, backList(), caseW(), caseT(), CASE } -- GA / ANY HOUSE
         }) -> { battens, board, wool, vb, batt, intSills, sheathing, memb,
              facadeBattens, cladding, eps, mesh, coat, render, skirt,
              extSills, cornerTrim, revealLining }, each f(ctx)

       Carried: the board's plasterboard and tile marks for the assembly
       (the ADU's) now also on SAUNA and HOUSEBOAT.
       core-v776 carried: the facade battens' end post (ADU) to the shed
       family and SAUNA / HOUSEBOAT; the skirt's battens off the corners and
       its marks to SAUNA / HOUSEBOAT; the mesh not drawn under a wet coat to
       BARNDO; TAURUS's boards over a head within 3 mm of a course (taurus-v9)
       and over a door reaching the wall head (taurus-v20) to everyone.
       The outside's hooks (H): CHORD, JOIST, ... WALL_* (the product's),
       core, finTex, grainTex, membTex, polyArea, revealBack, roofYAtXZ,
       underRoof, vertRuns, colorOf, roofSection, cladOff, cladVertical,
       cladQty, show(), cladShow(), skirtShow(), texLog(), extCoat(m2),
       panel { on, grooved, shade }, tg { on }, sillByTrim, noSkirt
       (the shed family), atRoofEdge (RANCH), kink { on, at } (ANY HOUSE),
       ga { under, CVENT, HEADF } (GA).
       ========================================================================= */
    function wallKit(H) {
      /* the product's own pieces, under the names the text has always used */
      var API = H.api, MAGENTA = H.MAGENTA, VB_T = H.VB_T, WALL_BATTEN = H.WALL_BATTEN, WOOD = H.WOOD,
          FILLER_KG_M2 = H.FILLER_KG_M2, JOINERY = H.JOINERY, PART = H.PART, STUD = H.STUD,
          WALL_BATT = H.WALL_BATT, WALL_BOARD = H.WALL_BOARD, WALL_ISILL = H.WALL_ISILL,
          WALL_TILE = H.WALL_TILE, WALL_VB = H.WALL_VB, WALL_WOOL = H.WALL_WOOL, WET_TILE_H = H.WET_TILE_H;
      /* the bill: everything here goes in the product's wall category */
      var LAYER = H.layer, _add = function (cat, code, q) { H.add(code, q); };
      var _boardCol = H.boardCol, _roomRects = H.roomRects, _showerOnFace = H.showerOnFace,
          _tileMat = H.tileMat, _wetAt = H.wetAt, _runsThrough = H.runsThrough;
      /* read when the kit is made, so they are this build's */
      var WALL_MAGENTA = H.magenta(), WALL_STEP = H.step();
      var _frameBodies = H.frameBodies() || [], _wallStuds = H.wallStuds() || [];
      /* the products' own build-ups, each behind its hook (status/modules.md 5):
           HOT  SAUNA / HOUSEBOAT -- the sauna room lined, not battened, wooled,
                barriered or boarded
           RSB  RANCH -- one body a layer (ranch-v61)
           lin, bareAt  RANCH / GA -- a garage wall boarded on the studs
           GA   GARAGE APT / ANY HOUSE -- the storey's rooms, the backings in
                the wool, the casing and the corner bead */
      var HOT = H.hot || null, HOT_STEP = HOT ? HOT.step() : -1;
      var _hotRuns = HOT ? HOT.runs : null, _splitByRuns = HOT ? HOT.split : null, _hotAt = HOT ? HOT.at : null;
      var RSB = H.batch || null;
      var _lin = H.lin || function () { return VB_T + WALL_BATTEN.d; };
      var _bareAt = H.bareAt || null;
      var GA = H.ga || null, GA_CASE = GA ? GA.CASE : null;

      /* THE OUTSIDE (core-v776): the sheathing, the breather, the facade
         battens and the boards, the contact facade (EPS, mesh, coat, render),
         the skirt, the outside sills, the corner trims and the reveals */
      var CHORD = H.CHORD, JOIST = H.JOIST, PARAPET_UPSTAND = H.PARAPET_UPSTAND, RAFTER = H.RAFTER,
          SHEATH_NAIL = H.SHEATH_NAIL, WALL_CLAD = H.WALL_CLAD, WALL_COAT = H.WALL_COAT,
          WALL_CTRIM = H.WALL_CTRIM, WALL_EAVE = H.WALL_EAVE, WALL_EPS = H.WALL_EPS,
          WALL_EPS_REV = H.WALL_EPS_REV, WALL_ESILL = H.WALL_ESILL, WALL_FBATT = H.WALL_FBATT,
          WALL_MEMB = H.WALL_MEMB, WALL_MESH = H.WALL_MESH, WALL_REND = H.WALL_REND,
          WALL_SKIRT = H.WALL_SKIRT, WALL_PANEL = H.WALL_PANEL, __CORE = H.core;
      var _finTex = H.finTex, _grainTex = H.grainTex, _membTex = H.membTex, _polyArea = H.polyArea,
          _revealBack = H.revealBack, _roofYAtXZ = H.roofYAtXZ, _underRoof = H.underRoof,
          _vertRuns = H.vertRuns, colorOf = H.colorOf, roofSection = H.roofSection;
      var WALL_SHOW = H.show ? H.show() : 'all', CLAD_SHOW = H.cladShow ? H.cladShow() : true,
          SKIRT_SHOW = H.skirtShow ? H.skirtShow() : 'all', WALL_TEX_LOG = H.texLog ? H.texLog() : [];
      /* the product's own facade choices, as its tile reads them */
      var _cladOff = H.cladOff || function () { return (API.STATE && API.STATE.cladding) === 'off'; };
      var _cladVertical = H.cladVertical || function () { return (API.STATE && API.STATE.cladding) === 'vertical'; };
      var _cladQty = H.cladQty || function (a) { return a; };
      var _extCoat = function (a) { if (H.extCoat) H.extCoat(a); };
      /*   PANEL, TG  the shed family's panel siding / TAURUS's T&G (no sheathing,
                      no breather, no battens behind the panel)
           sillByTrim, noSkirt  the shed family's sills past the jamb trim, no
                      skirt
           atRoofEdge RANCH: the L's porch walls carry no gable and no lift
           kink      ANY HOUSE: the L's roof line kinking over a wall
           GA_CVENT, GA_HEADF  GA: the rainscreen vent strip, the head flashing */
      var PANEL = H.panel || null, TG = H.tg || null, _atRoofEdge = H.atRoofEdge || null;
      var _kinkOn = (H.kink && H.kink.on) || function () { return false; }, _ahKink = H.kink ? H.kink.at : null;
      var GA_CVENT = GA ? (GA.CVENT || null) : null, GA_HEADF = GA ? (GA.HEADF || null) : null;

      /* the hooks the layers read, taken out of H here: several layers keep a
         local H of their own (a height), which hides the parameter */
      var _hkPaint = H.paint || null, _hkSillByTrim = !!H.sillByTrim,
          _hkNoSkirt = !!H.noSkirt;

      /* THE GABLES, THE EAVES AND THE SOFFITS (core-v778): the gable's
         sheathing and skin, the eave blocks (closure, vent strip, baffles),
         the soffits and the hip soffit, the corner beads, the outside
         coating's note, the openings scan */
      var EAVE_POST = H.EAVE_POST, WALL_BEAD = H.WALL_BEAD, WALL_SOFF = H.WALL_SOFF;
      var _plateEndReach = H.plateEndReach, _trussStations = H.trussStations;
      var _memberPts = H.memberPts ? H.memberPts() : null;
      var H_hipJacks = H.hipJacks || null, _hipSideJacks = H_hipJacks ? (H_hipJacks.get() || []) : [];
      /*   garageAt  RANCH: a bay over the garage keeps its closure, no baffle
           kink.hipEnds  ANY HOUSE: the hipped L's ends for the hip soffit */
      var H_garageAt = H.garageAt || null, H_hipEndsL = (H.kink && H.kink.hipEnds) || null;
      var _extCoatNow = H.extCoatA ? H.extCoatA() : 0;
      var bayNote = [];

      function _wallNew(id) {
        if (!WALL_MAGENTA) return false;
        return (',' + WALL_MAGENTA + ',').indexOf(',' + id + ',') >= 0;
      }

      function _wallStepOn(n) { return WALL_STEP < 0 || WALL_STEP >= n; }

      function _wallInner(s) {
        var t = API.wallThickness();
        return { x0: s.x0 - s.nx * t, z0: s.z0 - s.nz * t,
                 x1: s.x1 - s.nx * t, z1: s.z1 - s.nz * t };
      }

      function _battenRuns(s, yLo, yHi, inner, noHot) {
        var cuts = [], q, o, ops = s.openings || [];
        for (q = 0; q < ops.length; q++) {
          o = ops[q];
          var oLo = s.y0 + o.sill, oHi = oLo + o.h;
          if (yHi <= oLo + 0.001 || yLo >= oHi - 0.001) continue;
          /* AT IS THE CENTRE OF THE OPENING, NOT ITS NEAR EDGE.

             Read as an edge, every hole was cut half a width late: the batten ran
             on into the opening by half of it and then stopped short of the far
             reveal by the same amount, which is the shift he saw. Everything else
             in this file already reads it as a centre -- the trimmers at
             at - w/2, the jack spacing, the drag clamp -- so this was the one
             place with a second opinion about the same number. */
          cuts.push([o.at - o.w / 2, o.at + o.w / 2]);
        }
        cuts.sort(function (a, b) { return a[0] - b[0]; });
        var out = [], at = 0;
        for (q = 0; q < cuts.length; q++) {
          if (cuts[q][0] > at + 0.05) out.push([at, Math.min(cuts[q][0], s.len)]);
          at = Math.max(at, cuts[q][1]);
        }
        if (s.len > at + 0.05) out.push([at, s.len]);
        /* THE INNER FACE STOPS AT THE INNER CORNER. The runs were the segment's
           full length, corner to outer corner, and every inside layer -- battens,
           cavity wool, vapour barrier, board -- ran on past the inner corner by
           the next wall's thickness, through it (Marek). A closed outline meets
           another wall at both ends, so both ends come in by the thickness. */
        if (!inner) return out;   /* the outside layers run corner to corner */
        var tIn = API.wallThickness(), q2, clipped = [];
        for (q2 = 0; q2 < out.length; q2++) {
          var a2 = Math.max(out[q2][0], tIn), b2 = Math.min(out[q2][1], s.len - tIn);
          if (b2 - a2 > 0.05) clipped.push([a2, b2]);
        }
        /* garageapt-v22 (3d): a split wall's part keeps to its own runs */
        if (s.gaRuns) {
          var cl2 = [], q3, q4;
          for (q3 = 0; q3 < clipped.length; q3++) for (q4 = 0; q4 < s.gaRuns.length; q4++) {
            var a3 = Math.max(clipped[q3][0], s.gaRuns[q4][0]), b3 = Math.min(clipped[q3][1], s.gaRuns[q4][1]);
            if (b3 - a3 > 0.05) cl2.push([a3, b3]);
          }
          return cl2;
        }
        /* the house service battens and their wool ask with noHot: not in the
           sauna room, where the sauna battens stand instead (sauna-v44) */
        if (noHot && HOT_STEP >= 2) {
          var hr = _hotRuns(s);
          if (hr.length) {
            var dryR = [], q3h, d3;
            for (q3h = 0; q3h < clipped.length; q3h++) {
              var sp3 = _splitByRuns(clipped[q3h][0], clipped[q3h][1], hr).dry;
              for (d3 = 0; d3 < sp3.length; d3++) if (sp3[d3][1] - sp3[d3][0] > 0.05) dryR.push(sp3[d3]);
            }
            return dryR;
          }
        }
        return clipped;
      }

      function _battenCourses(s) {
        var out = [], c;
        var nC = Math.max(2, Math.round(s.h / WALL_BATTEN.spacing) + 1);
        var step = s.h / (nC - 1);
        for (c = 0; c < nC; c++) {
          var yC = s.y0 + step * c;
          if (c === 0) yC += WALL_BATTEN.f / 2;
          if (c === nC - 1) yC -= WALL_BATTEN.f / 2;
          out.push(yC);
        }
        return out;
      }

      function _wallFaces(s, inner) {
        var out = [], ops = s.openings || [], q, c, r;
        var ys = [s.y0, s.y0 + s.h];
        for (q = 0; q < ops.length; q++) {
          ys.push(s.y0 + ops[q].sill);
          ys.push(s.y0 + ops[q].sill + ops[q].h);
        }
        ys.sort(function (p1, p2) { return p1 - p2; });
        for (c = 0; c < ys.length - 1; c++) {
          var yLo = Math.max(ys[c], s.y0), yHi = Math.min(ys[c + 1], s.y0 + s.h);
          if (!(yHi - yLo > 0.02)) continue;
          var runs = _battenRuns(s, yLo + 0.001, yHi - 0.001, inner);
          for (r = 0; r < runs.length; r++) {
            if (runs[r][1] - runs[r][0] > 0.05) {
              out.push({ yLo: yLo, yHi: yHi, a: runs[r][0], b: runs[r][1] });
            }
          }
        }
        return out;
      }

      function _wallTileBill(layer, area, ctx, where) {
        if (!(area > 0.01)) return;
        /* grout at a fixed rate (Marek): 0.3 kg/m2 is the makers' table figure
           for a 300 x 600 tile with a 3 mm joint 8 deep */
        var grout = area * WALL_TILE.groutKgM2;
        _add(layer, WALL_TILE.code, area);
        _add(layer, WALL_TILE.glue, Math.ceil(area * WALL_TILE.kgM2));
        _add(layer, WALL_TILE.grout, Math.round(grout * 10) / 10);
        ctx.log('TIL  ' + where + ': ' + API.fmtArea(area) + ' of ' + WALL_TILE.code +
                ' (' + Math.round(WALL_TILE.w * 1000) + 'x' + Math.round(WALL_TILE.h * 1000) +
                ' laid across, ' + Math.round(WALL_TILE.gap * 1000) + ' mm joints, ivory) to ' +
                Math.round(WET_TILE_H * 1000) + ' mm, ' + Math.ceil(area * WALL_TILE.kgM2) +
                ' kg ' + WALL_TILE.glue + ', ' + (Math.round(grout * 10) / 10) + ' kg ' +
                WALL_TILE.grout + ' -- on the unpainted band of the board, ' +
                Math.round((WALL_TILE.t + WALL_TILE.bed) * 1000) + ' mm proud');
      }

      function _wallBattens(ctx) {
        if (!_wallStepOn(1)) return;
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, c, r;
        var mat = new T.MeshStandardMaterial({
          color: _wallNew('battens') ? MAGENTA : WOOD,
          roughness: 0.9, metalness: 0.0 });

        var run = 0, pieces = 0, screws = 0, sp = API.grid();

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2) || !(s.h > 0.2)) continue;
          var inn = _wallInner(s);
          var ux = (inn.x1 - inn.x0) / s.len, uz = (inn.z1 - inn.z0) / s.len;
          /* inward, away from the frame and into the room */
          var ix = -s.nx, iz = -s.nz;

          /* EVENLY UP THE WALL, WHOLE COURSES, one at the floor and one at the
             head -- the board has to be caught top and bottom or its edge waves */
          var CRS = _battenCourses(s);
          for (c = 0; c < CRS.length; c++) {
            var yC = CRS[c];
            var runs = _battenRuns(s, yC - WALL_BATTEN.f / 2, yC + WALL_BATTEN.f / 2, true, true);
            for (r = 0; r < runs.length; r++) {
              var a = runs[r][0], b = runs[r][1], L = b - a;
              if (!(L > 0.05)) continue;
              var mxp = inn.x0 + ux * (a + L / 2) + ix * (VB_T + WALL_BATTEN.d / 2);
              var mzp = inn.z0 + uz * (a + L / 2) + iz * (VB_T + WALL_BATTEN.d / 2);
              var bm = new T.Mesh(
                new T.BoxGeometry(Math.abs(ux) > Math.abs(uz) ? L : WALL_BATTEN.d,
                                  WALL_BATTEN.f,
                                  Math.abs(ux) > Math.abs(uz) ? WALL_BATTEN.d : L), mat);
              bm.position.set(mxp, yC, mzp);
              g.add(bm);
              run += L;
              pieces++;
              /* one screw at every stud it crosses, and never fewer than two --
                 a piece caught once is a pivot, not a fixing */
              screws += Math.max(2, Math.round(L / sp) + 1);
            }
          }
        }

        if (!pieces) { ctx.log('WAL  battens none -- no wall segments'); return; }
        _add(LAYER, WALL_BATTEN.code, run);
        _add(LAYER, WALL_BATTEN.screw, screws);
        ctx.log('WAL  battens ' + pieces + ' piece(s), ' + Math.round(run * 10) / 10 +
                ' m of ' + Math.round(WALL_BATTEN.f * 1000) + 'x' +
                Math.round(WALL_BATTEN.d * 1000) + ' ' + WALL_BATTEN.code +
                ', ' + screws + ' x ' + WALL_BATTEN.screw +
                ' -- horizontal, cut at every opening, ' +
                Math.round(WALL_BATTEN.d * 1000) + ' mm service void behind the board');
        _why(ctx, 'wall     the battens run ACROSS the studs, so the board is caught ' +
                'every ' + Math.round(WALL_BATTEN.spacing * 1000) + ' mm and the ' +
                'cables run behind it without a hole in the barrier');
      }

      function _wallBoard(ctx) {
        if (!_wallStepOn(2)) return;
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, c, r, q;
        var done = { dry: 0, wet: 0 }, tiledA = 0;
        var rev = { n: 0, doors: 0, dry: 0, wet: 0, dep: 0 };

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2) || !(s.h > 0.2)) continue;
          var inn = _wallInner(s);
          var ux = (inn.x1 - inn.x0) / s.len, uz = (inn.z1 - inn.z0) / s.len;
          var ix = -s.nx, iz = -s.nz;
          if (GA) GA.roomSt(s.storey | 0);   /* garageapt-v6: the rooms of this wall's own storey */
          /* the board sits on the face of the battens, so its own face is one
             batten depth plus one board thickness in from the frame */
          var off = _lin(s) + WALL_BOARD.t / 2;   /* garageapt-v3: on the studs on a garage wall */

          var FC = _wallFaces(s, true);
          for (c = 0; c < FC.length; c++) {
            var yLo = FC[c].yLo, yHi = FC[c].yHi, bh = yHi - yLo;
            {
              var a = FC[c].a, b = FC[c].b, L = b - a;

              /* THE BREAK COMES OFF THE ROOMS, NOT OFF A SAMPLE EVERY 100 mm.

                 Walking the run and asking every 100 mm put the changeover on the
                 nearest sample, which is up to a whole step away from the wall it
                 belongs to. And the room rectangles meet on the partition's CENTRE
                 LINE, so even a perfect answer ran the green half a partition into
                 the room next door -- which is what he saw.

                 Both are fixed by asking the geometry instead. Every room corner
                 is projected onto this wall's own axis and those coordinates are
                 the breaks; each piece between two of them is then asked ONCE, at
                 its middle, where a tolerance cannot reach it. Then the boundary
                 between a wet piece and a dry one is pulled back to the wet FACE
                 of the partition: nothing green is left showing next door, and the
                 joint lands where the partition covers it. */
              var brk = [a, b], RMB = _roomRects(), q6, r6, c6;
              for (q6 = 0; q6 < RMB.length; q6++) {
                var RS6 = RMB[q6].rects ||
                          (typeof RMB[q6].x0 === 'number' ? [RMB[q6]] : []);
                for (r6 = 0; r6 < RS6.length; r6++) {
                  var RC = RS6[r6];
                  if (typeof RC.x0 !== 'number') continue;
                  var CX = [RC.x0, RC.x1, RC.x1, RC.x0], CZ = [RC.z0, RC.z0, RC.z1, RC.z1];
                  for (c6 = 0; c6 < 4; c6++) {
                    var uu = (CX[c6] - inn.x0) * ux + (CZ[c6] - inn.z0) * uz;
                    if (uu > a + 0.02 && uu < b - 0.02) brk.push(uu);
                  }
                }
              }
              brk.sort(function (p1, p2) { return p1 - p2; });

              /* one answer per piece, then neighbours that agree are joined so a
                 wall through four rooms is not four sheets where two will do */
              var segs6 = [];
              for (c6 = 0; c6 < brk.length - 1; c6++) {
                var s0 = brk[c6], s1 = brk[c6 + 1];
                if (!(s1 - s0 > 0.02)) continue;
                var mu = (s0 + s1) / 2;
                var pxP = inn.x0 + ux * mu + ix * 0.25, pzP = inn.z0 + uz * mu + iz * 0.25;
                var wetP = _wetAt(pxP, pzP);
                /* the sauna room is lined, not boarded (sauna-v41) */
                var hotP = (HOT_STEP >= 3) && _hotAt(pxP, pzP);
                if (segs6.length && segs6[segs6.length - 1].wet === wetP && segs6[segs6.length - 1].hot === hotP) {
                  segs6[segs6.length - 1].b = s1;
                } else {
                  segs6.push({ a: s0, b: s1, wet: wetP, hot: hotP });
                }
              }

              /* the joint goes onto the partition's wet face */
              var half6 = PART.d / 2;
              for (c6 = 0; c6 < segs6.length - 1; c6++) {
                if (segs6[c6].wet === segs6[c6 + 1].wet) continue;
                var shift = segs6[c6].wet ? -half6 : half6;
                segs6[c6].b += shift;
                segs6[c6 + 1].a += shift;
              }

              for (c6 = 0; c6 < segs6.length; c6++) {
                var pa = segs6[c6].a, PL = segs6[c6].b - pa;
                if (!(PL > 0.05)) continue;
                if (segs6[c6].hot) { done.hotSkipped = (done.hotSkipped || 0) + PL * bh; continue; }
                /* THE BOARD WEARS THE KIND IT IS, AND IT SAYS SO BEFORE IT IS
                   DRAWN. A mark names everything added AFTER it, and I put this
                   one at the end of the block -- so it named the NEXT segment's
                   bodies, not this one's. The bill splits plain from moisture-
                   resistant here and always has (Marek). */
                var K = segs6[c6].wet ? WALL_BOARD.wet : WALL_BOARD.dry;
                _asmMark(ctx, 'layer_plasterboard', 'in', 'Plasterboard', K.code);
                var px = inn.x0 + ux * (pa + PL / 2) + ix * off;
                var pz = inn.z0 + uz * (pa + PL / 2) + iz * off;
                /* a wet piece is tiled to WET_TILE_H and painted above: two
                   boxes, the band unpainted (green), the rest in paint white */
                var tileTop = s.y0 + WET_TILE_H, parts = [];
                /* a shower against this piece of wall: tiles to the top */
                if (segs6[c6].wet && _showerOnFace(inn.x0 + ux * pa + ix * off, inn.z0 + uz * pa + iz * off,
                                                   inn.x0 + ux * (pa + PL) + ix * off, inn.z0 + uz * (pa + PL) + iz * off, 0.25)) {
                  tileTop = yHi; done.showerFull = (done.showerFull || 0) + 1;
                }
                if (segs6[c6].wet && yLo < tileTop - 0.005) {
                  parts.push([yLo, Math.min(yHi, tileTop), true]);
                  if (yHi > tileTop + 0.005) parts.push([tileTop, yHi, false]);
                } else {
                  parts.push([yLo, yHi, false]);
                }
                var pq;
                for (pq = 0; pq < parts.length; pq++) {
                  var hP = parts[pq][1] - parts[pq][0];
                  if (!(hP > 0.005)) continue;
                  var m = new T.MeshStandardMaterial({
                    color: _wallNew('board') ? MAGENTA : _boardCol(K, parts[pq][2]),
                    roughness: 0.92, metalness: 0.0 });
                  var bm = new T.Mesh(
                    new T.BoxGeometry(Math.abs(ux) > Math.abs(uz) ? PL : WALL_BOARD.t,
                                      hP,
                                      Math.abs(ux) > Math.abs(uz) ? WALL_BOARD.t : PL), m);
                  bm.position.set(px, (parts[pq][0] + parts[pq][1]) / 2, pz);
                  g.add(bm);
                  if (parts[pq][2]) {
                    tiledA += PL * hP;
                    /* the tile on the band: bed and tile, proud of the board face */
                    var tT2 = WALL_TILE.t + WALL_TILE.bed;
                    var mT2 = _tileMat(T, _wallNew('tile'), PL, hP, pa, parts[pq][0] - s.y0);
                    /* AND THE TILE IS A TILE. It had no mark, so it took the
                       board's -- 14.8 m2 of ceramic counted as moisture-resistant
                       board, which is the whole of the last discrepancy. */
                    _asmMark(ctx, 'wall_tile', 'in', 'Wall tile', WALL_TILE.code);
                    var tm2 = new T.Mesh(
                      new T.BoxGeometry(Math.abs(ux) > Math.abs(uz) ? PL : tT2, hP,
                                        Math.abs(ux) > Math.abs(uz) ? tT2 : PL), mT2);
                    tm2.position.set(px + ix * (WALL_BOARD.t / 2 + tT2 / 2),
                                     (parts[pq][0] + parts[pq][1]) / 2,
                                     pz + iz * (WALL_BOARD.t / 2 + tT2 / 2));
                    tm2.name = 'wall-tile';
                    g.add(tm2);
                    _asmMark(ctx, 'layer_plasterboard', 'in', 'Plasterboard', K.code);
                  }
                }
              done[segs6[c6].wet ? 'wet' : 'dry'] += PL * bh;
              }
            }
          }

          /* THE REVEAL, RETURNED TO THE FRAME -- THE DRYWALL WRAP.

             The board stopped on the face of the wall and the hole was left as
             bare frame from the lining back to the window. On a stud wall lined
             in board the reveal is the same board turned into the opening: a
             jamb each side and a head, from the face of the lining back to the
             frame, with the window board (OWW00001) already doing the sill. That
             is the ordinary US finish and it costs one row that is already in
             the bill -- the board -- so it comes in here rather than as a layer
             of its own, and its area rides the same screws, tape and compound.

             HOW DEEP: the joinery stands the unit on the middle of the wall, so
             the frame's room-side face is half a wall less half a frame in from
             the inner stud face; the lining stands the barrier, the batten and
             the board out from that face into the room. Frame to lining is the
             return. Wet or plain follows the room the window is in, the same
             question the face asked.

             WINDOWS ONLY. A door in an exterior wall gets its reveal from the
             door's own casing, which is the door layer's to draw. And no corner
             bead yet: there is no drywall corner bead row in the catalogue, so the
             arris is drawn as the board's own edge and nothing is invented. */
          (function () {
            var ops = s.openings || [], q9;
            /* SAYS WHAT IT SEES, EVERY WALL. His snapshot had no reveal line at
               all while the sills found five windows in the same segments, so the
               first thing this block does is report what it was given. */
            var nS9 = 0;
            for (q9 = 0; q9 < ops.length; q9++) if (ops[q9].sill > 0.05) nS9++;
            var liningR = _lin(s) + WALL_BOARD.t;   /* garageapt-v3 */
            var backR = API.wallThickness() / 2 - JOINERY.depth / 2;
            var depR = liningR + backR;
            rev.dep = depR;
            var t9 = WALL_BOARD.t;
            for (q9 = 0; q9 < ops.length; q9++) {
              var o9 = ops[q9];
              /* DOORS TOO. The entrance and the patio door were left to a casing
                 that nothing draws, so their reveals were bare studs from the
                 floor up. Same wrap: jambs from the floor to the head, head across;
                 the threshold is the door's. */
              var isDoor9 = !(o9.sill > 0.05);
              if (isDoor9) rev.doors++;
              var oW = o9.w, oY0 = s.y0 + (isDoor9 ? 0 : o9.sill), oY1 = oY0 + o9.h;
              if (!(oW > 0.1) || !(oY1 - oY0 > 0.1)) continue;
              var wet9 = _wetAt(inn.x0 + ux * o9.at + ix * 0.25, inn.z0 + uz * o9.at + iz * 0.25);
              var K9 = wet9 ? WALL_BOARD.wet : WALL_BOARD.dry;
              var m9 = new T.MeshStandardMaterial({
                color: _wallNew('ireveal') ? MAGENTA : _boardCol(K9), roughness: 0.92, metalness: 0.0 });
              /* from the frame (backR into the wall) to the lining face (liningR
                 into the room): centred that far in from the inner stud face */
              var cN = (liningR - backR) / 2;
              var alongW = Math.abs(ux) > Math.abs(uz);
              /* the two jambs, inside the opening by a board, from the top of the
                 window board to the head */
              var jY0 = oY0 + (isDoor9 ? 0.001 : WALL_ISILL.t), jY1 = oY1 - t9;
              var jH = jY1 - jY0, jq9;
              for (jq9 = 0; jq9 < 2; jq9++) {
                var sg9 = jq9 ? 1 : -1;
                var at9 = o9.at + sg9 * (oW / 2 - t9 / 2);
                var jm9 = new T.Mesh(
                  new T.BoxGeometry(alongW ? t9 : depR, jH, alongW ? depR : t9), m9);
                jm9.position.set(inn.x0 + ux * at9 + ix * cN, (jY0 + jY1) / 2,
                                 inn.z0 + uz * at9 + iz * cN);
                jm9.name = 'reveal-int';
                g.add(jm9);
                _asmMark(ctx, 'layer_plasterboard', 'in', 'Plasterboard',
                       (wet9 ? WALL_BOARD.wet : WALL_BOARD.dry).code);
              done[wet9 ? 'wet' : 'dry'] += jH * depR;
                rev[wet9 ? 'wet' : 'dry'] += jH * depR;
              }
              /* the head, the full width, capping the jambs */
              var hm9 = new T.Mesh(
                new T.BoxGeometry(alongW ? oW : depR, t9, alongW ? depR : oW), m9);
              hm9.position.set(inn.x0 + ux * o9.at + ix * cN, oY1 - t9 / 2,
                               inn.z0 + uz * o9.at + iz * cN);
              hm9.name = 'reveal-int';
              g.add(hm9);
              _asmMark(ctx, 'layer_plasterboard', 'in', 'Plasterboard',
                       (wet9 ? WALL_BOARD.wet : WALL_BOARD.dry).code);
              done[wet9 ? 'wet' : 'dry'] += oW * depR;
              rev[wet9 ? 'wet' : 'dry'] += oW * depR;
              rev.n++;
              /* garageapt-v29 (17a, COPIED from barndo-v26): the return's arris
                 gets a drywall corner bead (jambs + head); a door its casing on
                 the room's face, a window an apron under its stool. Not round
                 the overhead door (its jamb boards and seal are 3c's). */
              if (GA && o9.kind !== 'garage') {
                rev.bead = (rev.bead || 0) + 2 * (oY1 - oY0) + oW;
                var cwC = GA.caseW(), ctC = GA.caseT(), cRm = liningR + ctC / 2;
                var mC = rev.mC || (rev.mC = new T.MeshStandardMaterial({ color: 0xF4F3F0, roughness: 0.55, metalness: 0.0 }));
                var cBox = function (len, h, along, y) {
                  var cm = new T.Mesh(new T.BoxGeometry(alongW ? len : ctC, h, alongW ? ctC : len), mC);
                  cm.position.set(inn.x0 + ux * along + ix * cRm, y, inn.z0 + uz * along + iz * cRm);
                  cm.name = 'casing';
                  g.add(cm);
                };
                _asmMark(ctx, 'ga_casing', 'in', 'Casing', GA_CASE.code);
                if (isDoor9) {
                  cBox(cwC, oY1 - oY0, o9.at - oW / 2 - cwC / 2, (oY0 + oY1) / 2);
                  cBox(cwC, oY1 - oY0, o9.at + oW / 2 + cwC / 2, (oY0 + oY1) / 2);
                  cBox(oW + 2 * cwC, cwC, o9.at, oY1 + cwC / 2);
                  rev.casing = (rev.casing || 0) + 2 * (oY1 - oY0) + oW + 2 * cwC;
                } else {
                  cBox(oW, cwC, o9.at, oY0 - cwC / 2);
                  rev.casing = (rev.casing || 0) + oW;
                }
                _asmMark(ctx, 'layer_plasterboard', 'in', 'Plasterboard', (wet9 ? WALL_BOARD.wet : WALL_BOARD.dry).code);
              }
            }
          })();
        }

        if (rev.n || rev.doors) {
          ctx.log('WAL  reveal-int ' + rev.n + ' opening(s) wrapped in board (' + rev.doors +
                  ' of them doors, from the floor), ' +
                  Math.round(rev.dep * 1000) + ' mm from the lining face back to the frame (' +
                  Math.round((VB_T + WALL_BATTEN.d + WALL_BOARD.t) * 1000) + ' lining + ' +
                  Math.round((API.wallThickness() / 2 - JOINERY.depth / 2) * 1000) +
                  ' to the frame), ' + API.fmtArea(rev.dry) + ' plain + ' + API.fmtArea(rev.wet) +
                  ' green, counted with the wall board below; ' + (GA ? ('corner bead ' + GA_CASE.bead + ' ' + (Math.round((rev.bead || 0) * 10) / 10) + ' m on the returns\' arrises (garageapt-v29)') :
                  'NO corner bead -- no drywall corner bead row in the catalogue, nothing invented'));
          /* garageapt-v29 (17a, COPIED from barndo-v26): the bead, the casing and the aprons */
          if (GA && rev.bead > 0) _add(LAYER, GA_CASE.bead, Math.round(rev.bead * 100) / 100);
          if (GA && rev.casing > 0) {
            _add(LAYER, GA_CASE.code, Math.round(rev.casing * 100) / 100);
            _add(LAYER, GA_CASE.nail, GA_CASE.rows * Math.ceil(rev.casing / GA_CASE.step));
            ctx.log('WAL  casing (garageapt-v29) the exterior doors cased on the room\'s face, an apron under every window stool: ' + (Math.round(rev.casing * 10) / 10) + ' m ' + GA_CASE.code + ', ' +
                    (GA_CASE.rows * Math.ceil(rev.casing / GA_CASE.step)) + ' x ' + GA_CASE.nail + ' (two rows every 12 in.)');
          }
        }

        var total = done.dry + done.wet;
        if (!(total > 0.01)) { ctx.log('WAL  board none -- no wall segments'); return; }
        if (done.hotSkipped) ctx.log('hot        plasterboard left off ' + API.fmtArea(done.hotSkipped) + ' of the sauna room\'s walls -- the lining is there instead');

        if (done.dry > 0) _add(LAYER, WALL_BOARD.dry.code, done.dry);
        if (done.wet > 0) _add(LAYER, WALL_BOARD.wet.code, done.wet);
        var scr = Math.round(total * 20);
        var tape = Math.round(total / 2.88 * 3.6 * 10) / 10;
        _add(LAYER, WALL_BOARD.screw, scr);
        _add(LAYER, WALL_BOARD.tape, tape);
        _add(LAYER, WALL_BOARD.filler, Math.round(total * FILLER_KG_M2 * 10) / 10);
        if (_hkPaint) _hkPaint(total - tiledA);
        if (tiledA > 0) _wallTileBill(LAYER, tiledA, ctx, 'wall wet band');

        ctx.log('WAL  board ' + API.fmtArea(done.dry) + ' plain' +
                (done.wet > 0
                  ? (' + ' + API.fmtArea(done.wet) + ' MOISTURE-RESISTANT, drawn green' +
                     (done.showerFull ? (', ' + done.showerFull + ' piece(s) tiled to the top behind a shower') : ''))
                  : ' -- no wet room on an exterior wall, so no green board') +
                ', ' + Math.round(WALL_BOARD.t * 1000) + ' mm on the batten face');
        ctx.log('WAL  board ' + scr + ' x ' + WALL_BOARD.screw + ', ' + tape + ' m ' +
                WALL_BOARD.tape + ', ' + API.fmtArea(total) + ' ' + WALL_BOARD.filler +
                ' -- all three off the area, at the ceiling own rates');
        _why(ctx, 'wall     the sheet is chosen ALONG the wall, not once per segment: a ' +
                'bathroom at one end and a bedroom at the other meet on their partition, ' +
                'not at the corner of the building');
      }

      function _wallWool(ctx) {
        if (!_wallStepOn(3)) return;
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, c, r;
        var mat = new T.MeshStandardMaterial({
          color: _wallNew('wool') ? MAGENTA : 0xE8C86E,
          roughness: 1.0, metalness: 0.0 });
        var area = 0, pieces = 0, WB = RSB ? RSB.boxes() : null;   /* ranch-v61: one body */

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2) || !(s.h > 0.2)) continue;
          var inn = _wallInner(s);
          var ux = (inn.x1 - inn.x0) / s.len, uz = (inn.z1 - inn.z0) / s.len;
          var ix = -s.nx, iz = -s.nz;
          var CRS = _battenCourses(s);

          /* THE GAP IS SPLIT AT THE HEADS AND SILLS AS WELL AS AT THE COURSES.

             A course gap that straddles a window head was cut for its WHOLE height
             -- the run function is asked one question about the band and answers
             it for the band -- so the strip between the head and the next course
             came out empty. Sometimes 40 mm of it, sometimes 300, depending where
             the head happened to fall between two courses, which is exactly the
             narrow-and-wide he is looking at.

             Splitting the gap at every head and sill first makes each piece either
             wholly beside the opening or wholly clear of it, and a piece that is
             clear comes back whole. Nothing about the openings is worked out here:
             the heights are the openings' own and the cutting is still the one run
             function. */
          var ops3 = s.openings || [], q3;
          /* garageapt-v32 (17d): the backing behind the kitchen / vanity is in
             this void too -- the wool is split at its edges and stops at it */
          var BKw = [], qb;
          var GBL = GA ? (GA.backList() || []) : [];
          for (qb = 0; qb < GBL.length; qb++) if (GBL[qb].id === s.id) BKw.push({ a: GBL[qb].a, b: GBL[qb].b, lo: GBL[qb].y - WALL_BATTEN.f / 2, hi: GBL[qb].y + WALL_BATTEN.f / 2 });
          for (c = 0; c < CRS.length - 1; c++) {
            var gLo = CRS[c] + WALL_BATTEN.f / 2;
            var gHi = CRS[c + 1] - WALL_BATTEN.f / 2;
            if (!(gHi - gLo > 0.02)) continue;

            var cut3 = [gLo, gHi];
            for (q3 = 0; q3 < ops3.length; q3++) {
              var hSill = s.y0 + ops3[q3].sill, hHead = hSill + ops3[q3].h;
              if (hSill > gLo + 0.005 && hSill < gHi - 0.005) cut3.push(hSill);
              if (hHead > gLo + 0.005 && hHead < gHi - 0.005) cut3.push(hHead);
            }
            cut3.sort(function (p1, p2) { return p1 - p2; });

            for (q3 = 0; q3 < cut3.length - 1; q3++) {
              var yLo = cut3[q3], yHi = cut3[q3 + 1];
              var bh = yHi - yLo;
              if (!(bh > 0.02)) continue;
              var runs = _battenRuns(s, yLo + 0.001, yHi - 0.001, true, true);
              /* garageapt-v32: each run a rectangle of the band; a backing in
                 it takes its own place out -- the wool beside it full height,
                 under and over it in its length */
              var RC = [], rq;
              for (rq = 0; rq < runs.length; rq++) RC.push([runs[rq][0], runs[rq][1], yLo, yHi]);
              for (qb = 0; qb < BKw.length; qb++) {
                var K = BKw[qb], RC2 = [];
                for (rq = 0; rq < RC.length; rq++) {
                  var R0 = RC[rq];
                  if (!(K.a < R0[1] - 0.001 && K.b > R0[0] + 0.001 && K.lo < R0[3] - 0.001 && K.hi > R0[2] + 0.001)) { RC2.push(R0); continue; }
                  var ka = Math.max(K.a, R0[0]), kb = Math.min(K.b, R0[1]);
                  if (ka - R0[0] > 0.001) RC2.push([R0[0], ka, R0[2], R0[3]]);
                  if (R0[1] - kb > 0.001) RC2.push([kb, R0[1], R0[2], R0[3]]);
                  if (K.lo - R0[2] > 0.002) RC2.push([ka, kb, R0[2], K.lo]);
                  if (R0[3] - K.hi > 0.002) RC2.push([ka, kb, K.hi, R0[3]]);
                }
                RC = RC2;
              }
              for (r = 0; r < RC.length; r++) {
                var a = RC[r][0], L = RC[r][1] - a;
                if (!(L > 0.05)) continue;
                yLo = RC[r][2]; yHi = RC[r][3]; bh = yHi - yLo;
                /* the wool is drawn one millimetre thinner so it starts on the
                   FACE of the barrier rather than in it; the row is still the
                   catalogue's 40 and is bought by area */
                var wT = WALL_WOOL.t - VB_T;
                var px = inn.x0 + ux * (a + L / 2) + ix * (VB_T + wT / 2);
                var pz = inn.z0 + uz * (a + L / 2) + iz * (VB_T + wT / 2);
                if (WB) WB.add(Math.abs(ux) > Math.abs(uz) ? L : wT, bh, Math.abs(ux) > Math.abs(uz) ? wT : L, px, (yLo + yHi) / 2, pz, null);
                else {
                  var wm = new T.Mesh(
                    new T.BoxGeometry(Math.abs(ux) > Math.abs(uz) ? L : wT,
                                      bh,
                                      Math.abs(ux) > Math.abs(uz) ? wT : L), mat);
                  wm.position.set(px, (yLo + yHi) / 2, pz);
                  g.add(wm);
                }
                area += L * bh;
                pieces++;
              }
            }
          }
        }

        if (WB) RSB.baked(T, g, WB, mat, 'm2');
        if (!pieces) { ctx.log('WAL  wool none -- no cavity to fill'); return; }
        _add(LAYER, WALL_WOOL.code, area);
        ctx.log('WAL  wool ' + pieces + ' piece(s), ' + API.fmtArea(area) + ' of ' +
                Math.round(WALL_WOOL.t * 1000) + ' mm ' + WALL_WOOL.code +
                ' -- the GAPS between the battens, not a blanket over them, and cut ' +
                'at every opening');
        _why(ctx, 'wall     a 40 mm cavity left empty is a convection loop behind the ' +
                'board; wool rather than the 40 PIR because this void has cables and ' +
                'boxes in it and wool takes a cut round a box');
      }

      function _wallVB(ctx) {
        if (!_wallStepOn(4)) return;
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, c;
        var mat = new T.MeshStandardMaterial({
          color: _wallNew('vb') ? MAGENTA : 0x4A5560,
          roughness: 0.85, metalness: 0.0, side: T.DoubleSide });
        var vbT = VB_T, drawn = 0, pieces = 0, VBB = RSB ? RSB.boxes() : null;   /* ranch-v61: one body */

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2) || !(s.h > 0.2)) continue;
          var inn = _wallInner(s);
          var ux = (inn.x1 - inn.x0) / s.len, uz = (inn.z1 - inn.z0) / s.len;
          var ix = -s.nx, iz = -s.nz;
          var FC = _wallFaces(s, true);
          /* in the hot room the foil is the barrier -- the plain one stays off */
          var hotR = (HOT_STEP >= 1) ? _hotRuns(s) : [];
          for (c = 0; c < FC.length; c++) {
            var dryP = hotR.length ? _splitByRuns(FC[c].a, FC[c].b, hotR).dry : [[FC[c].a, FC[c].b]], dq;
            for (dq = 0; dq < dryP.length; dq++) {
            var L = dryP[dq][1] - dryP[dq][0], bh = FC[c].yHi - FC[c].yLo;
            if (HOT && !(L > 0.02)) continue;
            var mu = dryP[dq][0] + L / 2;
            var px = inn.x0 + ux * mu + ix * (vbT / 2);
            var pz = inn.z0 + uz * mu + iz * (vbT / 2);
            if (VBB) VBB.add(Math.abs(ux) > Math.abs(uz) ? L : vbT, bh, Math.abs(ux) > Math.abs(uz) ? vbT : L, px, (FC[c].yLo + FC[c].yHi) / 2, pz, null);
            else {
              var vm = new T.Mesh(
                new T.BoxGeometry(Math.abs(ux) > Math.abs(uz) ? L : vbT,
                                  bh,
                                  Math.abs(ux) > Math.abs(uz) ? vbT : L), mat);
              vm.position.set(px, (FC[c].yLo + FC[c].yHi) / 2, pz);
              g.add(vm);
            }
            drawn += L * bh;
            pieces++;
            }
          }
        }
        if (VBB) RSB.baked(T, g, VBB, mat, 'm2');

        if (!pieces) { ctx.log('WAL  barrier none -- no wall face'); return; }
        var bought = drawn * WALL_VB.roll / (WALL_VB.roll - WALL_VB.lap);
        var tapeM = (bought - drawn) / WALL_VB.lap;
        _add(LAYER, WALL_VB.code, bought);
        _add(LAYER, WALL_VB.tape, tapeM);
        ctx.log('WAL  barrier ' + pieces + ' piece(s), drawn ' + API.fmtArea(drawn) +
                ', bought ' + API.fmtArea(bought) + ' ' + WALL_VB.code +
                ' on a ' + Math.round(WALL_VB.roll * 1000) + ' roll lapped ' +
                Math.round(WALL_VB.lap * 1000) + ', ' + Math.round(tapeM * 10) / 10 +
                ' m ' + WALL_VB.tape + ' -- the tape run IS the lap, so it cannot ' +
                'disagree with the area');
        ctx.log('WAL  barrier drawn ' + Math.round(vbT * 1000) + ' mm thick on the ' +
                'frame face; the cavity wool is drawn ' +
                Math.round((WALL_WOOL.t - VB_T) * 1000) + ' and still billed as the ' +
                'catalogue ' + Math.round(WALL_WOOL.t * 1000) + ' -- it is bought by ' +
                'area, so no two faces share a plane and nothing is lost');
        _why(ctx, 'wall     barrier on the WARM face of the studs with the battens over ' +
                'it, so the board screws and the cables stay in front of it');
      }

      function _wallBatt(ctx) {
        if (!_wallStepOn(5)) return;
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, c, k;
        var mat = new T.MeshStandardMaterial({
          color: _wallNew('frame-wool') ? MAGENTA : 0xD8B25A,
          roughness: 1.0, metalness: 0.0 });
        var area = 0, pieces = 0, bays = 0, cutBy = 0, BTB = RSB ? RSB.boxes() : null;   /* ranch-v61: one body */
        var ST = (API.__wallStuds ? API.__wallStuds() : _wallStuds) || [];

        /* THE WOOL FILLS WHAT THE TIMBER LEAVES, NOT THE GRID BETWEEN MODULE STUDS
           (adu-v1352).

           The bays were read between the module studs the frame published, and
           nothing else: the jack and the king beside every opening, the header
           over it and the sill under it, the doubled stud at a transport joint
           and the lower top plate -- all of it timber the full depth of the wall
           -- had wool drawn through them and billed with it (Marek: vata nepocita
           s jack/king/preklad/parapet). So the wool is now laid out from the
           frame's own bodies: every piece of the wall frame that stands in the
           cavity is taken as the rectangle it covers on the wall, the wall is cut
           into upright strips at every edge of every piece, and in each strip the
           wool fills the heights no piece covers. The corner of a butting wall
           still belongs to the through wall, as before. If the frame was not built
           this time (a stage that shows the wool alone), the module studs are used
           as before, and the log says so. */
        /* the frame's bodies, as its builder set them down (it runs before the
           wall layer) */
        var bodies = [], fb;
        try { if (g) g.updateMatrixWorld(true); } catch (eUM) {}
        for (fb = 0; fb < _frameBodies.length; fb++) {
          var ob = _frameBodies[fb];
          if (!ob || !ob.isMesh || !ob.parent) continue;
          bodies.push(new T.Box3().setFromObject(ob));
        }
        var fromFrame = bodies.length > 0;

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2) || !(s.h > 0.2)) continue;
          var inn = _wallInner(s);
          var ux = (inn.x1 - inn.x0) / s.len, uz = (inn.z1 - inn.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var endB = _runsThrough(s) ? STUD.t / 2 : (STUD.d - STUD.t / 2);
          var uMin = endB + STUD.t / 2, uMax = s.len - endB - STUD.t / 2;
          var yLoF = s.y0 + STUD.t;
          var yHiF = s.y0 + s.h;
          var FC = _wallFaces(s, true);

          /* the frame's pieces in this wall, as rectangles on it */
          var rects = [];
          if (fromFrame) {
            for (k = 0; k < bodies.length; k++) {
              var bx = bodies[k];
              var cxB = (bx.min.x + bx.max.x) / 2, czB = (bx.min.z + bx.max.z) / 2;
              var nC = (cxB - inn.x0) * ox + (czB - inn.z0) * oz;
              if (nC < -0.02 || nC > STUD.d + 0.02) continue;
              var uA = (bx.min.x - inn.x0) * ux + (bx.min.z - inn.z0) * uz;
              var uB = (bx.max.x - inn.x0) * ux + (bx.max.z - inn.z0) * uz;
              var u0 = Math.min(uA, uB), u1 = Math.max(uA, uB);
              if (u1 < -0.05 || u0 > s.len + 0.05) continue;
              if (bx.max.y < yLoF - 0.001 || bx.min.y > yHiF + 0.001) continue;
              rects.push({ u0: u0, u1: u1, y0: bx.min.y, y1: bx.max.y });
            }
          } else {
            var ats = [];
            for (k = 0; k < ST.length; k++) {
              if (ST[k].seg !== s.id) continue;
              if (ST[k].at > 0.001 && ST[k].at < s.len - 0.001) ats.push(ST[k].at);
            }
            for (k = 0; k < ats.length; k++) rects.push({ u0: ats[k] - STUD.t / 2, u1: ats[k] + STUD.t / 2, y0: -1e9, y1: 1e9 });
          }

          for (c = 0; c < FC.length; c++) {
            var fa = Math.max(uMin, FC[c].a), fb = Math.min(uMax, FC[c].b);
            var fyLo = Math.max(yLoF, FC[c].yLo), fyHi = Math.min(yHiF, FC[c].yHi);
            if (!(fb - fa > 0.05) || !(fyHi - fyLo > 0.05)) continue;
            /* every edge of every piece inside this face is a strip boundary */
            var brk = [fa, fb];
            for (k = 0; k < rects.length; k++) {
              if (rects[k].u0 > fa && rects[k].u0 < fb) brk.push(rects[k].u0);
              if (rects[k].u1 > fa && rects[k].u1 < fb) brk.push(rects[k].u1);
            }
            brk.sort(function (p1, p2) { return p1 - p2; });
            var run = null;       /* a batt run grows across strips with the same free heights */
            var flush = function () {
              if (!run) return;
              var L = run.b - run.a, q;
              for (q = 0; q < run.free.length; q++) {
                var yLo = run.free[q][0], yHi = run.free[q][1], bh = yHi - yLo;
                if (!(L > 0.05) || !(bh > 0.05)) continue;
                var bt = WALL_BATT.t - VB_T;
                var px = inn.x0 + ux * (run.a + L / 2) + ox * (VB_T + bt / 2);
                var pz = inn.z0 + uz * (run.a + L / 2) + oz * (VB_T + bt / 2);
                if (BTB) BTB.add(Math.abs(ux) > Math.abs(uz) ? L : bt, bh, Math.abs(ux) > Math.abs(uz) ? bt : L, px, (yLo + yHi) / 2, pz, null);
                else {
                  var bm = new T.Mesh(
                    new T.BoxGeometry(Math.abs(ux) > Math.abs(uz) ? L : bt,
                                      bh,
                                      Math.abs(ux) > Math.abs(uz) ? bt : L), mat);
                  bm.position.set(px, (yLo + yHi) / 2, pz);
                  g.add(bm);
                }
                area += L * bh;
                pieces++;
              }
              bays++;
              run = null;
            };
            for (k = 0; k < brk.length - 1; k++) {
              var sa = brk[k], sb = brk[k + 1];
              if (!(sb - sa > 0.002)) continue;
              var mid = (sa + sb) / 2, cov = [], r2;
              for (r2 = 0; r2 < rects.length; r2++) {
                if (rects[r2].u0 < mid && rects[r2].u1 > mid) cov.push([Math.max(fyLo, rects[r2].y0), Math.min(fyHi, rects[r2].y1)]);
              }
              cov.sort(function (p1, p2) { return p1[0] - p2[0]; });
              var free = [], yCur = fyLo, cq;
              for (cq = 0; cq < cov.length; cq++) {
                if (cov[cq][1] <= cov[cq][0]) continue;
                if (cov[cq][0] > yCur + 0.001) free.push([yCur, cov[cq][0]]);
                if (cov[cq][1] > yCur) yCur = cov[cq][1];
              }
              if (fyHi > yCur + 0.001) free.push([yCur, fyHi]);
              if (cov.length) cutBy++;
              var key = free.map(function (fq) { return Math.round(fq[0] * 1000) + ':' + Math.round(fq[1] * 1000); }).join(',');
              if (run && run.key === key && Math.abs(run.b - sa) < 0.002) { run.b = sb; continue; }
              flush();
              if (free.length && (sb - sa) > 0.002) run = { a: sa, b: sb, free: free, key: key };
            }
            flush();
          }
        }

        if (BTB) RSB.baked(T, g, BTB, mat, 'm2');
        if (!pieces) { ctx.log('WAL  batts none -- the frame published no studs'); return; }
        _add(LAYER, WALL_BATT.code, area);
        ctx.log('frame wool ' + pieces + ' batt(s) in ' + bays + ' run(s), ' +
                API.fmtArea(area) + ' of ' + Math.round(WALL_BATT.t * 1000) + ' mm ' +
                WALL_BATT.code + ' -- ' + (fromFrame
                  ? ('laid round ' + bodies.length + ' frame piece(s): studs, jacks, kings, cripples, headers, sills and plates')
                  : 'FROM THE MODULE STUDS ONLY, the frame was not built this time'));
        _why(ctx, 'wall     a batt laid out on the grid would be laid out on a wall that ' +
                'does not exist: kings, cripples and corner studs all move the module');
      }

      function _wallIntSills(ctx) {
        if (!_wallStepOn(2)) return;
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, q;
        var mat = new T.MeshStandardMaterial({
          color: _wallNew('isill') ? MAGENTA : 0xE8E4DA,
          roughness: 0.6, metalness: 0.0 });

        var lining = WALL_BATTEN.d + WALL_BOARD.t;
        var run = 0, n = 0;

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2)) continue;
          var IN = _wallInner(s);
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var along = Math.abs(ux) > Math.abs(uz);
          var ops = s.openings || [];
          for (q = 0; q < ops.length; q++) {
            /* core-v786: a serving window has a counter, not a sill (the container's rule) */
            if (!(ops[q].sill > 0.05) || ops[q].kind === 'serve') continue;
            if (_roundOp(ops[q])) continue;   /* core-v809: a porthole has no sill */
            if (_bareAt) lining = (_bareAt(s, ops[q].at) ? 0 : WALL_BATTEN.d) + WALL_BOARD.t;   /* garageapt-v3: no battens on a garage wall; v22: at the window */
            var len = ops[q].w + 2 * WALL_ISILL.side;
            var dep = lining + WALL_ISILL.out;
            var y = s.y0 + ops[q].sill;

            var sm = new T.Mesh(
              new T.BoxGeometry(along ? len : dep, WALL_ISILL.t,
                                along ? dep : len), mat);
            /* ON the rough sill, so its BOTTOM is the sill line -- and a
               millimetre INTO it, because a bottom face laid exactly on a top face
               is two surfaces in one plane, which is the flicker every other layer
               in this wall has already had to be moved off. */
            sm.position.set(
              IN.x0 + ux * ops[q].at - ox * (dep / 2),
              y + WALL_ISILL.t / 2 - 0.001,
              IN.z0 + uz * ops[q].at - oz * (dep / 2));
            sm.name = 'window-sill-int';
            g.add(sm);
            run += len;
            n++;
          }
        }

        if (!n) { ctx.log('WAL  interior sills none -- no window with a sill'); return; }
        _add(LAYER, WALL_ISILL.code, run);
        _add(LAYER, 'ODP00001', Math.ceil(run / 12));
        ctx.log('WAL  interior sills ' + n + ' window(s), ' +
                Math.round(run * 100) / 100 + ' m of ' + WALL_ISILL.code + ' -- ' +
                Math.round(WALL_ISILL.side * 1000) + ' mm past each jamb, ' +
                Math.ceil(run / 12) + ' x ODP00001 bedding it, ' +
                Math.round(WALL_ISILL.out * 1000) + ' proud of the lining, depth ' +
                Math.round((lining + WALL_ISILL.out) * 1000) + ' mm');
        _why(ctx, 'wall     the window board sits ON the rough sill and tucks UNDER the ' +
                'frame -- hung with its top on the sill line it is buried inside the ' +
                'timber and barely visible, which is what tiny had to learn once');
      }

      function _wallGhost() { return WALL_SHOW === 'ghost'; }

      function _wallNet()   { return WALL_SHOW !== 'eps'; }

      function _wallClad()  { return WALL_SHOW !== 'frame' && WALL_SHOW !== 'eps'; }

      function _wallWet()   { return WALL_SHOW === 'all' || WALL_SHOW === 'coat'; }

      function _wallDry()   { return WALL_SHOW === 'all'; }

      function _skirtDrop() {
        if (API.STATE.foundation === 'slab') return 0;
        return JOIST.deck + JOIST.d + WALL_SKIRT.clear;
      }

      function _facadeFace() {
        return _cladThk((API.STATE && API.STATE.cladding) || 'horizontal');
      }

      function _wallLift(s) {
        var R = API.STATE.roof || {};
        if (_atRoofEdge && !_atRoofEdge(s)) return 0;   /* ranch-v5: the L's porch walls carry no roof edge */
        /* a gable end gets the lift -- on a gable roof, a mono, and on the gable
           end of a half-hip (Marek: the truss profile showed above the facade
           there); a hipped end has no gable to lift */
        if (R.type !== 'gable' && R.type !== 'mono' && R.type !== 'hip') return 0;
        if (R.type === 'hip') {
          var endH = API.ridgeEndOf ? API.ridgeEndOf(s) : null;
          if (!endH || API.hipAt(endH)) return 0;
        }
        var rake = (R.type === 'hip') ? true : ((R.axis === 'l') ? (Math.abs(s.nz) > 0.5) : (Math.abs(s.nx) > 0.5));
        if (!rake) return 0;
        var sect = _isTrussed() ? CHORD.d : roofSection().d;
        return sect / Math.cos((R.pitch || 0) * Math.PI / 180);
      }

      function _gableOf(s) {
        var R = API.STATE.roof || {};
        if (!(s.len > 0.2)) return null;
        /* garageapt-v5: A WALL WITH A STOREY OVER IT HAS NO ROOF OVER IT --
           no gable and no eave lift: its skins (OSB, wrap, battens, cladding,
           corner trims) run up over the floor zone -- the top plates, the
           rim and the subfloor -- to where the storey above starts, as a band
           the whole length of the wall, and stop there. They ran up to the
           ridge over the apartment's own before, twice in the bill. */
        if (GA && GA.under(s)) {
          var STb = API.storeys(), nxt = STb[(s.storey | 0) + 1], topB = s.y0 + s.h, band = nxt ? (nxt.y0 - topB) : 0;
          if (!(band > 0.005)) return null;
          return { top: topB, rise: band, at: s.len / 2, band: true,
                   hAt: function () { return band; },
                   spanAtH: function (h) { return (h <= band + 0.0005) ? [0, s.len] : null; } };
        }

        /* A PARAPET IS A WALL THAT CARRIES ON, AND IT GETS WHAT A WALL GETS.

           On a flat roof the studs run past the head to hold the coping, and until
           now the facade stopped at the head and left them bare -- render or
           boarding to the head and raw timber above it, which is not a detail
           anybody builds.

           Its profile is the simplest of all: the same height everywhere, right
           round the building. So it comes back through the same door as the gable
           and the mono, and every layer above follows it without knowing what it
           is standing on.

           The rise is the roof's own section plus its deck plus the upstand -- the
           same three numbers the frame used to make the studs, asked again rather
           than written down twice. */
        if (R.type === 'flat') {
          if (!R.parapet) return null;
          var pTop = s.y0 + s.h;
          var pRise = roofSection().d + RAFTER.deck + PARAPET_UPSTAND;
          if (!(pRise > 0.05)) return null;
          return { top: pTop, rise: pRise, at: s.len / 2, parapet: true,
                   hAt: function () { return pRise; },
                   spanAtH: function (h) {
                     return (h <= pRise + 0.001) ? [0, s.len] : null;
                   } };
        }

        /* THE PROFILE IS READ OFF THE ROOF, POINT BY POINT.

           The old version knew one shape: an apex, a half span, and a straight run
           to each eave. That is a gable and nothing else, which is why a MONO had
           no triangle anywhere -- not on the raked sides and not on the tall front
           -- and why every fix to the apex had to be made twice.

           There is no shape here now. The roof is asked how high it is at a point,
           the wall is walked, and whatever comes back is the profile. A gable
           gives a triangle, a mono gives a wedge on the sides and a rectangle on
           the high wall, and a hip will give what a hip gives. Nothing in the
           layers above needs to know which. */
        /* a wall of the porch stands under the roof, not at its edge: no gable (ranch-v5) */
        if (_atRoofEdge && !_atRoofEdge(s)) return null;
        var top = s.y0 + s.h;
        var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;

        function hAt(u) {
          /* past the wall's own end the profile is whatever the roof does THERE,
             so a layer carried round the corner is still cut to the roof */
          var uu = Math.max(-0.5, Math.min(s.len + 0.5, u));
          var y = _roofYAtXZ(s.x0 + ux * uu, s.z0 + uz * uu);
          if (y === null) {
            y = _roofYAtXZ(s.x0 + ux * Math.max(0, Math.min(s.len, u)),
                           s.z0 + uz * Math.max(0, Math.min(s.len, u)));
          }
          if (y === null) return 0;
          return Math.max(0, y - top);
        }

        /* the tallest point, and where it is -- sampled, because a ridge or a
           hip line can land anywhere along the wall */
        var N = 96, q, best = 0, bestAt = 0;
        for (q = 0; q <= N; q++) {
          var u = s.len * q / N, h = hAt(u);
          if (h > best) { best = h; bestAt = u; }
        }
        if (!(best > 0.05)) return null;

        /* how far along the wall the roof is at least this high */
        function spanAtH(h) {
          var a = null, b = null, k;
          for (k = 0; k <= N; k++) {
            var uu = s.len * k / N;
            if (hAt(uu) >= h - 0.0005) { if (a === null) a = uu; b = uu; }
          }
          if (a === null) return null;
          return [Math.max(0, a - s.len / N), Math.min(s.len, b + s.len / N)];
        }

        return { top: top, rise: best, at: bestAt, hAt: hAt, spanAtH: spanAtH };
      }

      function _wallSkin(s) {
        var G = _gableOf(s);
        if (!G || G.parapet || G.band) return G;   /* garageapt-v5: a band under a storey takes no lift */
        var lift = _wallLift(s);
        /* AND AN EAVE WALL HAS A RISE TOO. Over an eave the roof's plane sits
           barely above the wall head, so the rise came out near zero and every
           finish above the mesh -- the base coat, the render, the primer --
           stopped at the head, which on screen is the ceiling line, while the
           cladding and the EPS carried on (Marek). The finish goes up to the
           top of the members over the wall, where the fascia and the drip edge
           are: the same lift a rake gets, measured plumb. */
        if (!(lift > 0) && !(G.rise > 0.02)) {
          var R9 = API.STATE.roof || {};
          if (R9.type !== 'flat') {
            var sect9 = _isTrussed() ? CHORD.d : roofSection().d;
            lift = sect9 / Math.cos((R9.pitch || 0) * Math.PI / 180);
          }
        }
        if (!(lift > 0)) return G;
        var S = {}, k;
        for (k in G) { if (G.hasOwnProperty(k)) S[k] = G[k]; }
        S.lift = lift;
        S.rise = G.rise + lift;
        S.hAt = function (u) { return G.hAt(u) + lift; };
        S.spanAtH = function (h) {
          return (h <= lift + 0.001) ? [0, s.len] : G.spanAtH(h - lift);
        };
        return S;
      }

      function _wallSheathing(ctx) {
        if (!_wallStepOn(6)) return;
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, c, k;
        /* ONE PASS, TWO MATERIALS (the shed family). On the panel variant this
           sheet is not sheathing under a facade -- it IS the facade, painted, so
           it takes the panel colour and no wood grain. */
        var panel = !!(PANEL && PANEL.on());
        var tex = panel ? null : _finTex(T, 'osb');
        var mat = new T.MeshStandardMaterial({
          color: _wallNew('sheathing') ? MAGENTA
               : (panel ? colorOf('facade') : (tex ? 0xFFFFFF : 0xC8A063)),
          map: tex || null, roughness: panel ? 0.85 : 0.95, metalness: 0.0 });

        /* THE JOINT IS A PIECE, NOT A GAP.

           Left as a gap it was 6 mm of whatever happens to be behind it, seen
           edge-on from anywhere but square, which is why the division still did
           not read. A dark strip in the gap reads from every angle -- and it is
           its own mesh rather than a line painted on a board, so the assembly
           animation can bring the boards in one at a time and the joints with
           them. Named, for the same reason: something has to be able to ask for
           them later. */
        var jmat = new T.MeshStandardMaterial({
          color: 0x14161A, roughness: 0.95, metalness: 0.0 });

        /* the groove reads as a shadow line, so it is the panel colour taken
           down rather than a black strip: a pressed groove is the same paint */
        var panelGroove = panel && PANEL.grooved();
        var gmat = panelGroove ? new T.MeshStandardMaterial({
          color: _wallNew('sheathing') ? MAGENTA : PANEL.shade(colorOf('facade'), 0.62),
          roughness: 0.9, metalness: 0.0 }) : null;
        var grooves = 0;
        var SH = sheet(), bw = panel ? WALL_PANEL.w : SH.w;
        var joint = panel ? WALL_PANEL.gap : 0.006;
        var t = panel ? WALL_PANEL.t : 0.018;
        var drawn = 0, boards = 0, whole = 0, jn = 0;

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2) || !(s.h > 0.2)) continue;
          /* the OUTER face of the frame: the plan polygon is that face, so the
             board sits on it rather than in it */
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var FC = _wallFaces(s);
          var top = s.y0 + s.h;

          /* THE BOARD DOES NOT STOP AT THE CEILING EITHER.

             Above the head a second pass drew its own boards, under their own
             name, on their own set-out, one millimetre further out -- and that
             millimetre is the joint he could see at the storey line all the way
             round the parapet. It is one sheet of 18 mm standing on the frame; it
             is cut at the top by the roof and nowhere else.

             Where the roof leaves the same height across a whole board -- which
             is a parapet, and every board below the head -- the board is ONE box
             straight through. Where the two edges of a board come out at
             different heights, which is a rake, the part above the head is a
             trapezium cut at both its own corners: the same shape the old pass
             made, drawn from the piece it belongs to. */
          var Gs = null;
          try { Gs = _wallSkin(s); } catch (eS) { Gs = null; }
          function riseS(u) {
            if (!Gs || !Gs.hAt) return 0;
            var v = 0;
            try { v = Gs.hAt(Math.max(0, Math.min(s.len, u))); } catch (e2) { v = 0; }
            return (v > 0) ? v : 0;
          }

          for (var xB = 0; xB < s.len - 0.005; xB += bw) {
            var xE = Math.min(xB + bw - joint, s.len);
            if (!(xE - xB > 0.02)) continue;
            whole++;
            var rA = riseS(xB), rB = riseS(xE);
            var flatS = Math.abs(rA - rB) < 0.002;
            var upS = flatS ? rA : 0;
            /* THE SAME TURN _wallGable HAS WRITTEN UP, TAKEN WHOLE THIS TIME.

               A shape is built in its own x. On the walls whose direction runs
               against that x the board is started at its FAR edge and the shape
               runs back to the near one; anything else sends it off the wrong way,
               outside the building, with its two heights read at the ends they do
               not belong to. That is the board that flew off on the opposite
               elevations and the cut that did not follow the roof. */
            var fwdS = (oz * ux - ox * uz) > 0;
            for (c = 0; c < FC.length; c++) {
              var a = Math.max(xB, FC[c].a), b = Math.min(xE, FC[c].b);
              var lo = Math.max(s.y0, FC[c].yLo), hi = Math.min(top, FC[c].yHi);
              var L = b - a;
              if (!(L > 0.02)) continue;

              /* THE BOARD THE ROOF CUTS IS ONE SHAPE FROM ITS OWN BOTTOM.

                 It was a box to the head with a wedge stuck on top of it, which is
                 the storey joint back again -- and the wedge was the piece that
                 flew off. Where the roof leaves the two edges of this board at
                 different heights the WHOLE board is the shape, cut at both its
                 own corners. Where they come out level -- a parapet, or no roof
                 above this wall at all -- a box says everything and stays a box. */
              /* on the L the roof over a wall can kink inside one board -- where
                 a valley crosses the wing's eave wall the bar's slope comes down
                 to the wing's level eave -- and a straight cut between the two
                 edges stood 30 mm up through the covering. There the top is the
                 roof line itself, sampled (anyhouse-lib-v11). */
              var kinkS = (hi >= top - 0.005 && (rA > 0.005 || rB > 0.005) && _kinkOn()) ? _ahKink(riseS, a, b) : null;
              if (hi >= top - 0.005 && (!flatS || kinkS) && (rA > 0.005 || rB > 0.005)) {
                var e0S = fwdS ? a : b;
                var hLo = riseS(a), hHi = riseS(b);
                var x0S = fwdS ? 0 : (b - a), x1S = fwdS ? (b - a) : 0;
                var shB = new T.Shape();
                shB.moveTo(x0S, 0);
                shB.lineTo(x1S, 0);
                shB.lineTo(x1S, (top - lo) + hHi);
                if (kinkS) {
                  var qk;
                  for (qk = kinkS.length - 2; qk >= 1; qk--) shB.lineTo(fwdS ? (kinkS[qk][0] - a) : (b - kinkS[qk][0]), (top - lo) + kinkS[qk][1]);
                }
                shB.lineTo(x0S, (top - lo) + hLo);
                shB.lineTo(x0S, 0);
                var bmS = new T.Mesh(
                  new T.ExtrudeGeometry(shB, { depth: t, bevelEnabled: false }), mat);
                bmS.rotation.y = Math.atan2(ox, oz);
                bmS.position.set(s.x0 + ux * e0S, lo, s.z0 + uz * e0S);
                bmS.name = 'osb-board';
                g.add(bmS);
                drawn += L * ((top - lo) + (hLo + hHi) / 2);
                boards++;
                continue;
              }

              /* the piece that reaches the head carries the parapet with it */
              if (hi >= top - 0.005 && upS > 0.005) hi = top + upS;
              var H = hi - lo;
              if (!(H > 0.02)) continue;
              var px = s.x0 + ux * (a + L / 2) + ox * (t / 2);
              var pz = s.z0 + uz * (a + L / 2) + oz * (t / 2);
              var bm = new T.Mesh(
                new T.BoxGeometry(Math.abs(ux) > Math.abs(uz) ? L : t,
                                  H,
                                  Math.abs(ux) > Math.abs(uz) ? t : L), mat);
              bm.position.set(px, (lo + hi) / 2, pz);
              bm.name = 'osb-board';
              g.add(bm);
              drawn += L * H;
              boards++;
            }

            /* and the joint on this board's far edge, unless the wall ends here */
            if (xE >= s.len - 0.02) continue;
            for (c = 0; c < FC.length; c++) {
              var ja = Math.max(xE, FC[c].a), jb = Math.min(xE + joint, FC[c].b);
              var jlo = Math.max(s.y0, FC[c].yLo), jhi = Math.min(top, FC[c].yHi);
              /* THE JOINT IS AT THE BOARD'S FAR EDGE, SO ITS HEIGHT IS THE RAKE
                 THERE -- not the mean of the board's two edges, which is half a
                 board's rise wrong: on the downhill side of every board the strip
                 stood up to 300 mm above the cut, the black wires over the roof
                 he could see from one side and not the other. */
              /* NO STRIP OBJECT. The joint is the 6 mm the boards leave between
                 them; a dark strip drawn into it kept standing out over the roof
                 on the rake walls whatever height it was given, and he asked for
                 them gone rather than chased again. What shows in the gap is
                 what is behind the sheathing, which is what a joint shows. */
              var jL = jb - ja, jH = jhi - jlo;
              if (!(jL > 0.001) || !(jH > 0.02)) continue;
              jn++;
            }
          }

          /* THE GROOVES, once per segment rather than once per sheet, because the
             rhythm belongs to the WALL: set out from the corner at 203.2 mm it
             runs through the sheet joints without a broken bay, and a groove that
             lands on a joint is simply not drawn -- the joint is already that
             line. They are clipped to the same face spans the boards use, so an
             opening cuts them exactly as it cuts the sheet. */
          if (panelGroove) {
            var gstep = WALL_PANEL.groove, gwid = WALL_PANEL.grooveW, gdep = WALL_PANEL.grooveD;
            var gq, gc;
            for (gq = gstep; gq < s.len - 0.02; gq += gstep) {
              /* on a sheet edge the joint IS the groove */
              if (Math.abs((gq % bw) - 0) < 0.012 || Math.abs((gq % bw) - bw) < 0.012) continue;
              var gRise = 0;
              for (gc = 0; gc < FC.length; gc++) {
                if (gq < FC[gc].a || gq > FC[gc].b) continue;
                var glo = Math.max(s.y0, FC[gc].yLo), ghi = Math.min(top, FC[gc].yHi);
                if (ghi >= top - 0.005) { gRise = riseS(gq); ghi = top + gRise; }
                if (!(ghi - glo > 0.02)) continue;
                var gpx = s.x0 + ux * gq + ox * (t - gdep / 2);
                var gpz = s.z0 + uz * gq + oz * (t - gdep / 2);
                var gm = new T.Mesh(new T.BoxGeometry(
                  Math.abs(ux) > Math.abs(uz) ? gwid : gdep, ghi - glo,
                  Math.abs(ux) > Math.abs(uz) ? gdep : gwid), gmat);
                gm.position.set(gpx, (glo + ghi) / 2, gpz);
                gm.name = 'panel-groove';
                g.add(gm);
                grooves++;
              }
            }
          }
        }

        if (!boards) { ctx.log('WAL  sheathing none -- no wall face'); return; }
        ctx.log('WAL  sheathing ' + boards + ' piece(s) from ' + whole + ' board(s), ' +
                Math.round(bw * 1000) + ' wide x full height x ' + Math.round(t * 1000) +
                ', STANDING, joint drawn ' + Math.round(joint * 1000) +
                ' mm -- the side edges land on studs, and there is no horizontal ' +
                'joint to block');
        ctx.log('WAL  sheathing ' + jn + ' joint(s) -- the 6 mm gap the boards leave, no ' +
                'strip object in it any more (they stood out over the roof on the rake ' +
                'walls); the division reads as the gap itself');
        ctx.log('WAL  sheathing drawn ' + API.fmtArea(drawn) +
                ' -- the ROW is still the frame own sheet.osb18 by area; when the two ' +
                'agree it moves here and is bought in whole boards');
        if (panel) {
          ctx.log('WAL  PANEL SIDING ' + (panelGroove
                  ? (grooves + ' groove(s) at ' + Math.round(WALL_PANEL.groove * 1000) +
                     ' mm -- 6 to a sheet exactly, so the rhythm runs through the joints; ' +
                     'the groove is DRAWING and is in the price of the panel, no row of its own')
                  : 'plain face, no grooves') +
                  ' -- sheet ' + Math.round(WALL_PANEL.w * 1000) + ' x ' +
                  Math.round(WALL_PANEL.h * 1000) + ' x ' + Math.round(WALL_PANEL.t * 1000));
        }
        ctx.log('WAL  sheathing drawn ' + API.fmtArea(drawn) +
                ' -- the ROW is still the frame own sheet.osb18 by area; when the two ' +
                'agree it moves here and is bought in whole boards');
        _why(ctx, 'wall     the stud module is half a board so the board edges land on ' +
                'studs -- laid flat that is thrown away and the shear joint hangs in a bay');
      }

      function _wallMemb(ctx) {
        /* NOT BEHIND PANEL SIDING (the shed family). There is no cavity to drain
           into and nothing to protect: the panel is the weather face. */
        if (PANEL && PANEL.on()) { ctx.log('WAL  breather none -- PANEL SIDING is the weather face, no cavity behind it'); return; }
        if (!_wallStepOn(7)) return;
        /* NOT ON A CONTACT FACADE. The EPS is stuck straight onto the sheathing,
           so a breather between the two is a vapour-open film sealed under a
           vapour-closed board -- and in the model it was the grey printed sheet he
           could see instead of the mesh. */
        if (_cladOff()) {
          ctx.log('WAL  breather SKIPPED -- contact facade: the EPS goes straight ' +
                  'on the sheathing and there is no cavity to drain');
          return;
        }
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, c;
        var mtex = _wallNew('memb') ? null : _membTex(T);
        /* the tile IS the course, so one row of print to a strip */
        var MEMB_TILE = WALL_MEMB.roll - WALL_MEMB.lap;
        /* A SHADE DARKER WHERE IT DOUBLES, which is what a lap IS.

           The rolls run across the wall and each one covers the top of the last by
           100 mm, so every 1400 up the face there is a band of two thicknesses.
           Drawn in one flat colour none of that is visible and the wall reads as
           one enormous sheet, which is the same thing that was wrong with the
           boards. Only a shade -- it is a lap, not a feature. */
        var lapMat = new T.MeshStandardMaterial({
          color: _wallNew('memb') ? MAGENTA : 0xC6CAC2,
          roughness: 0.9, metalness: 0.0, side: T.DoubleSide });
        /* the bands CUT to a short piece wore magenta for one round; the rule is
           accepted and they are the same film as the rest */
        var lapCutMat = lapMat;
        var lapsTrim = 0;

        /* AND IT SITS A MILLIMETRE OFF THE BOARDS.

           The joint strips stand 1 mm proud of the sheathing so the division reads
           from any angle -- and the membrane was drawn in exactly that millimetre,
           so the two shared a face and flickered. His fix, and the right one: the
           film goes outside everything under it rather than into the one gap that
           was deliberately left occupied. */
        var osbT = 0.018 + 0.001, mt = WALL_MEMB.t, drawn = 0, pieces = 0, laps = 0;
        var runW = WALL_MEMB.roll - WALL_MEMB.lap;

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2) || !(s.h > 0.2)) continue;
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var ops2 = s.openings || [], oq;

          /* STRIPS FIRST, AND THE HOLES SLIT OUT OF THEM.

             Both halves of this matter and I have had one or the other wrong four
             times. The COURSES belong to the wall: a roll is hung from the bottom
             and every one after it laps the one below, so the joint is at the same
             height on every elevation and nothing about a window may move it.
             Within a course the film IS cut at the openings, because that is what
             happens on site once the roll is on.

             AND THE PRINT IS ANCHORED TO THE COURSE, NOT TO THE PIECE. That is the
             part I took out last time and should not have: with the pattern
             measured from the wall's start, a piece to the right of a window
             carries on where the piece to the left stopped, so the only string
             that is ever cut is the one the reveal actually cuts -- which is what
             he is describing when he says the ones away from openings sit right.
             Started at each piece instead, every piece began mid-tile and split a
             string that nothing had cut.

             The cutter takes the full width of any opening that touches the
             course, so where a head or a sill lands INSIDE a course the film above
             or below the hole would go missing. Those slivers are put back. */
          /* THE FILM DOES NOT STOP AT THE CEILING.

             It ran to the wall head and a second pass started another one above
             it, under another name, on its own row. A breather runs up the
             sheathing to the top of whatever it is protecting; the storey line is
             not a roll edge and there is nothing to stop at.

             So the coursing carries on to the top of the profile. Where the
             profile still has the whole wall at the top of a course -- a parapet,
             or anything below the head -- the course is ONE piece straight
             through. Where it narrows, which is a rake, the part above the head
             is cut to the profile at its own two heights and drawn as a shape:
             the roll really is cut there, so a joint there is the truth. */
          var Gm = null;
          try { Gm = _wallSkin(s); } catch (eM) { Gm = null; }
          var headM = s.y0 + s.h;
          var topM = headM + (Gm ? Math.max(0, Gm.rise) : 0);

          /* AND ABOVE THE HEAD THE FILM FOLLOWS THE BOARDS.

             Coursing it up a rake was wrong twice over: a horizontal course has no
             single width where the roof is cutting it, and my own shape ran off
             the wrong way and out of the building. The sheathing under it does
             build correctly, column by column, each board cut at its own two top
             corners -- so the film is cut the same way, on the same columns, with
             the same edge heights and the same turn. One piece per column, full
             height of what the roof leaves there. */
          if (topM > headM + 0.01) {
            var SHm = sheet(), bwm = SHm.w, xm;
            var fwdF = (oz * ux - ox * uz) > 0;
            for (xm = 0; xm < s.len - 0.005; xm += bwm) {
              var xa9 = xm, xb9 = Math.min(xm + bwm, s.len);
              if (!(xb9 - xa9 > 0.02)) continue;
              var ha9 = 0, hb9 = 0;
              try { ha9 = Math.max(0, Gm.hAt(xa9)); } catch (e9) { ha9 = 0; }
              try { hb9 = Math.max(0, Gm.hAt(xb9)); } catch (e9b) { hb9 = 0; }
              if (!(Math.max(ha9, hb9) > 0.03)) continue;
              var e9x = fwdF ? xa9 : xb9;
              var x0f = fwdF ? 0 : (xb9 - xa9), x1f = fwdF ? (xb9 - xa9) : 0;
              var shF = new T.Shape();
              shF.moveTo(x0f, 0);
              shF.lineTo(x1f, 0);
              shF.lineTo(x1f, Math.max(0.001, hb9));
              /* the roof line where it kinks over this column (the L's valleys, anyhouse-lib-v11) */
              var kinkF = _kinkOn() ? _ahKink(function (u) { var v9 = 0; try { v9 = Math.max(0, Gm.hAt(u)); } catch (e9k) { v9 = 0; } return v9; }, xa9, xb9) : null;
              if (kinkF) {
                var qf;
                for (qf = kinkF.length - 2; qf >= 1; qf--) shF.lineTo(fwdF ? (kinkF[qf][0] - xa9) : (xb9 - kinkF[qf][0]), Math.max(0.001, kinkF[qf][1]));
              }
              shF.lineTo(x0f, Math.max(0.001, ha9));
              shF.lineTo(x0f, 0);
              var mF;
              if (mtex) {
                var txF = mtex.clone();
                txF.needsUpdate = true;
                txF.wrapS = txF.wrapT = T.RepeatWrapping;
                txF.repeat.set(1 / MEMB_TILE, 1 / MEMB_TILE);
                txF.offset.set(xa9 / MEMB_TILE, (headM - s.y0) / MEMB_TILE);
                mF = new T.MeshStandardMaterial({
                  map: txF, roughness: 0.9, metalness: 0.0, side: T.DoubleSide });
              } else {
                mF = new T.MeshStandardMaterial({
                  color: 0xDCDFD8, roughness: 0.9, metalness: 0.0, side: T.DoubleSide });
              }
              var mshF = new T.Mesh(
                new T.ExtrudeGeometry(shF, { depth: mt, bevelEnabled: false }), mF);
              mshF.rotation.y = Math.atan2(ox, oz);
              mshF.position.set(s.x0 + ux * e9x + ox * osbT, headM,
                                s.z0 + uz * e9x + oz * osbT);
              mshF.name = 'wall-breather';
              g.add(mshF);
              drawn += (xb9 - xa9) * (ha9 + hb9) / 2;
              pieces++;
            }
          }

          var kMax = Math.ceil((headM - s.y0) / runW), kq;
          for (kq = 0; kq < kMax; kq++) {
            var cLo = s.y0 + runW * kq;
            var cHi = Math.min(cLo + runW, headM);
            if (!(cHi - cLo > 0.02)) continue;

            var cHiB = cHi;

            var strips = _battenRuns(s, cLo + 0.001, cHiB - 0.001);
            for (oq = 0; oq < ops2.length; oq++) {
              var oLo2 = s.y0 + ops2[oq].sill, oHi2 = oLo2 + ops2[oq].h;
              var oa = ops2[oq].at - ops2[oq].w / 2, ob = ops2[oq].at + ops2[oq].w / 2;
              if (oHi2 > cLo + 0.005 && oHi2 < cHiB - 0.005) strips.push([oa, ob, oHi2, cHiB]);
              if (oLo2 > cLo + 0.005 && oLo2 < cHi - 0.005) strips.push([oa, ob, cLo, oLo2]);
            }

            for (c = 0; c < strips.length; c++) {
              var a2 = Math.max(0, strips[c][0]), b2 = Math.min(s.len, strips[c][1]);
              var yA = (strips[c].length > 2) ? strips[c][2] : cLo;
              var yB2 = (strips[c].length > 3) ? strips[c][3] : cHiB;
              var L = b2 - a2, ph = yB2 - yA;
              if (!(L > 0.05) || !(ph > 0.02)) continue;
              var mu = a2 + L / 2;

              var px = s.x0 + ux * mu + ox * (osbT + mt / 2);
              var pz = s.z0 + uz * mu + oz * (osbT + mt / 2);
              var pm;
              if (mtex) {
                var tx2 = mtex.clone();
                tx2.needsUpdate = true;
                tx2.wrapS = tx2.wrapT = T.RepeatWrapping;
                /* WHICH END THE FACE COUNTS FROM -- AND NOT BY MIRRORING IT.

                   A box's two big faces run their U in OPPOSITE directions. On the
                   walls whose outward face is the one where U runs backwards,
                   anchoring at a2 measures the pattern off b2 instead, and b2
                   moves the instant a window is dragged: the print slid, on some
                   walls and not others.

                   A negative repeat fixes the anchor and mirrors the letters,
                   which is the wrong cure -- and the mirroring was never needed.
                   That face is on the far side of the building and is looked at
                   from the far side, so its backwards U reads left to right for
                   whoever is standing in front of it. The text is already the
                   right way round; only the anchor was wrong.

                   So the repeat stays positive and the anchor is taken from the
                   end that face counts from. len - b2 is a function of the WALL and
                   of nothing else, exactly as a2 is on the other side, which is
                   what makes the print a property of the building. An opening can
                   then only ever clip it. */
                var flip = (Math.abs(ux) > Math.abs(uz))
                  ? ((oz > 0 ? 1 : -1) !== (ux > 0 ? 1 : -1))
                  : ((ox > 0 ? -1 : 1) !== (uz > 0 ? 1 : -1));
                tx2.repeat.set(L / MEMB_TILE, ph / MEMB_TILE);
                tx2.offset.set((flip ? (s.len - b2) : a2) / MEMB_TILE,
                               (yA - cLo) / MEMB_TILE);
                pm = new T.MeshStandardMaterial({
                  map: tx2, roughness: 0.9, metalness: 0.0, side: T.DoubleSide });
              } else {
                pm = new T.MeshStandardMaterial({
                  color: MAGENTA, roughness: 0.9, metalness: 0.0, side: T.DoubleSide });
              }
              var mm = new T.Mesh(
                new T.BoxGeometry(Math.abs(ux) > Math.abs(uz) ? L : mt,
                                  ph,
                                  Math.abs(ux) > Math.abs(uz) ? mt : L), pm);
              mm.position.set(px, (yA + yB2) / 2, pz);
              mm.name = 'wall-breather';
              g.add(mm);
              drawn += L * ph;
              pieces++;

              /* a lap only at a real roll edge: the top of a course, and never at
                 the very top of the film, where nothing laps over it */
              if (yB2 < cHiB - 0.005 || cHiB >= topM - 0.005) continue;
              /* THE LAP IS THE TOP OF THIS PIECE, NOT A BAND HUNG OFF THE COURSE
                 LINE -- the mesh's rule, copied. It was drawn a full 100 down from
                 cHiB whatever was under it, so over a head where the piece is
                 thirty tall the other seventy hung into the opening. A doubling
                 can only exist where there is film underneath it to double: cut
                 to the piece, and a piece too short to carry one gets none. */
              var lapH = Math.min(WALL_MEMB.lap, yB2 - yA);
              if (!(lapH > 0.005)) continue;
              var cut = (lapH < WALL_MEMB.lap - 0.0005);
              if (cut) lapsTrim++;
              var lm = new T.Mesh(
                new T.BoxGeometry(Math.abs(ux) > Math.abs(uz) ? L : mt * 0.6,
                                  lapH,
                                  Math.abs(ux) > Math.abs(uz) ? mt * 0.6 : L),
                cut ? lapCutMat : lapMat);
              lm.position.set(
                s.x0 + ux * mu + ox * (osbT + mt + mt * 0.3),
                cHiB - lapH / 2,
                s.z0 + uz * mu + oz * (osbT + mt + mt * 0.3));
              lm.name = 'wall-breather-lap';
              g.add(lm);
              laps++;
            }
          }
        }

        if (!pieces) { ctx.log('WAL  breather none -- no wall face'); return; }
        var bought = drawn * WALL_MEMB.roll / (WALL_MEMB.roll - WALL_MEMB.lap);
        var tapeM = (bought - drawn) / WALL_MEMB.lap;
        _add(LAYER, WALL_MEMB.code, bought);
        _add(LAYER, WALL_MEMB.tape, tapeM);
        ctx.log('WAL  breather ' + pieces + ' piece(s), drawn ' + API.fmtArea(drawn) +
                ', bought ' + API.fmtArea(bought) + ' ' + WALL_MEMB.code +
                ' on a ' + Math.round(WALL_MEMB.roll * 1000) + ' roll lapped ' +
                Math.round(WALL_MEMB.lap * 1000) + ', ' + Math.round(tapeM * 10) / 10 +
                ' m ' + WALL_MEMB.tape + ' -- same lap arithmetic as the barrier');
        ctx.log('WAL  breather ' + laps + ' lap band(s) drawn every ' +
                Math.round(runW * 1000) + ' mm, ' + Math.round(WALL_MEMB.lap * 1000) +
                ' mm tall and a shade darker, named wall-breather-lap; the film sits 1 mm ' +
                'outside the joint strips so nothing shares a face');
        ctx.log('WAL  breather ' + lapsTrim + ' lap band(s) CUT to a short piece over a ' +
                'head -- a lap hangs off the piece it doubles, not off the ' +
                'course line; the same rule the mesh got, and a band under 5 mm is not drawn');
        _why(ctx, 'wall     breather on the COLD face: the barrier keeps vapour OUT of ' +
                'the insulation, this one lets what got in anyway back out and keeps ' +
                'the weather off the boards');
      }

      function _wallFacadeBattens(ctx) {
        if (PANEL && PANEL.on()) { ctx.log('WAL  facade battens none -- PANEL SIDING is fixed straight to the studs'); return; }
        if (!_wallStepOn(8)) return;

        /* NOT ON A CONTACT FACADE. THERE ARE NO BATTENS THERE.

           He is right and this one has no defence: the batten pass has run on
           every wall since the day it was written, whatever facade was chosen. On
           a contact wall the EPS is stuck to the sheathing with low-expansion foam
           and held with plates -- there is no cavity, nothing to hold anything off
           anything, and a batten in that build-up is a piece of wood buried in
           insulation.

           Worse than wrong: they are drawn at 19 mm out and 40 deep, which is
           inside the EPS, so on the ghost view the timber reads through the board
           and the wall looks like sheathing and battens with the insulation
           nowhere. That is exactly what he is looking at. */
        if (_cladOff()) {
          ctx.log('WAL  facade battens none -- CONTACT facade: the EPS goes onto the ' +
                  'sheathing on foam and plates, there is no cavity and nothing to ' +
                  'batten. They have been drawn here on every rebuild until now');
          return;
        }
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, k, r;
        var mat = new T.MeshStandardMaterial({
          color: _wallNew('facade-battens') ? MAGENTA : colorOf('timber'),
          roughness: 0.9, metalness: 0.0 });
        /* clear of the sheathing, its joint strips and the film on top of them */
        var base = 0.018 + 0.001 + WALL_MEMB.t + 0.0005;
        var run = 0, pieces = 0, screws = 0, lines = 0;
        var ST = (API.__wallStuds ? API.__wallStuds() : _wallStuds) || [];
        /* garageapt-v30 (17b): the cavity's foot (the ground storey, less the
           doorways) and head (the top storey: the battens' tops, up the rake) */
        var nStC = 1, vFoot = 0, vTop = 0, vGeo = [];
        try { nStC = API.storeys().length; } catch (eNs) { nStC = 1; }

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2) || !(s.h > 0.2)) continue;
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;

          /* WHAT THE ROOF LEAVES ABOVE THIS WALL, asked here so the batten can be
             ONE PIECE from the sole plate to the top of the parapet. It used to
             stop at the ceiling and a second pass started another one above it,
             under another name -- two objects, two rows in the bill, and a butt
             joint in the middle of a length of 60x40 that nobody would ever cut. */
          var Gf = null;
          try { Gf = _wallSkin(s); } catch (eF) { Gf = null; }

          var ats = [];
          for (k = 0; k < ST.length; k++) {
            if (ST[k].seg !== s.id) continue;
            if (ST[k].at > 0.001 && ST[k].at < s.len - 0.001) ats.push(ST[k].at);
          }
          ats.sort(function (p1, p2) { return p1 - p2; });
          /* ONE BATTEN PER LINE OF STUDS. Two studs side by side -- a panel joint,
             a king beside a jack -- are one line for the cladding, so a pair
             closer than 130 mm becomes one batten between them (Marek: doubles at
             the joints, and the spacing looked uneven for it). */
          var atsM = [], km;
          for (km = 0; km < ats.length; km++) {
            if (atsM.length && ats[km] - atsM[atsM.length - 1] < 0.13) atsM[atsM.length - 1] = (atsM[atsM.length - 1] + ats[km]) / 2;
            else atsM.push(ats[km]);
          }
          ats = atsM;
          /* and one at each end, because the cladding has to finish on something */
          /* a butting wall's end stud stands a frame depth in; it is still the
             end of the run, so the corner batten takes its place as before */
          var endF = Math.max(0.13, STUD.d + STUD.t + 0.005);
          if (!ats.length || ats[0] > endF) ats.unshift(WALL_FBATT.f / 2); else ats[0] = WALL_FBATT.f / 2;
          if (ats[ats.length - 1] < s.len - endF) ats.push(s.len - WALL_FBATT.f / 2); else ats[ats.length - 1] = s.len - WALL_FBATT.f / 2;

          var topPts = [];   /* garageapt-v30: the cavity's head line */
          for (k = 0; k < ats.length; k++) {
            if (k > 0 && ats[k] - ats[k - 1] < 0.05) continue;
            lines++;
            var runs = _vertRuns(s, ats[k]);
            /* the run that reaches the head carries on to whatever the roof leaves
               over this station -- the full upstand on a parapet, the wedge on a
               mono, nothing where the roof says there is nothing above this wall */
            if (Gf && runs.length) {
              var upF = 0;
              try { upF = Math.max(0, Gf.hAt(ats[k])); } catch (eH) { upF = 0; }
              var lastF = runs[runs.length - 1];
              if (upF > 0.005 && lastF[1] > s.y0 + s.h - 0.005) lastF[1] = Gf.top + upF;
            }
            if (runs.length) topPts.push([ats[k], runs[runs.length - 1][1]]);
            for (r = 0; r < runs.length; r++) {
              var yA = runs[r][0], H = runs[r][1] - yA;
              if (!(H > 0.05)) continue;
              var px = s.x0 + ux * ats[k] + ox * (base + WALL_FBATT.d / 2);
              var pz = s.z0 + uz * ats[k] + oz * (base + WALL_FBATT.d / 2);
              var bm = new T.Mesh(
                new T.BoxGeometry(Math.abs(ux) > Math.abs(uz) ? WALL_FBATT.f : WALL_FBATT.d,
                                  H,
                                  Math.abs(ux) > Math.abs(uz) ? WALL_FBATT.d : WALL_FBATT.f), mat);
              bm.position.set(px, yA + H / 2, pz);
              bm.name = 'facade-batten';
              g.add(bm);
              run += H;
              pieces++;
              screws += Math.max(2, Math.round(H / WALL_FBATT.screwStep) + 1);
            }
          }
          /* garageapt-v30 (17b): the vent strip at the cavity's foot -- the
             ground storey's wall less its doorways -- and at its head */
          if (GA_CVENT && (s.storey | 0) === 0) {
            var cutsV = (s.openings || []).filter(function (o) { return o && !((o.sill || 0) > 0.05); }).map(function (o) { return [o.at - o.w / 2, o.at + o.w / 2]; }).sort(function (a2, b2) { return a2[0] - b2[0]; });
            var curV = 0, segV = [], qv;
            for (qv = 0; qv < cutsV.length; qv++) { if (cutsV[qv][0] > curV + 0.02) segV.push([curV, cutsV[qv][0]]); curV = Math.max(curV, cutsV[qv][1]); }
            if (s.len > curV + 0.02) segV.push([curV, s.len]);
            for (qv = 0; qv < segV.length; qv++) {
              var Lv = segV[qv][1] - segV[qv][0], mv = (segV[qv][0] + segV[qv][1]) / 2;
              vGeo.push({ s: s, ux: ux, uz: uz, ox: ox, oz: oz, at: mv, L: Lv, y: s.y0 + GA_CVENT.h / 2 });
              vFoot += Lv;
            }
          }
          if (GA_CVENT && (s.storey | 0) === nStC - 1 && topPts.length > 1) {
            for (k = 1; k < topPts.length; k++) vTop += Math.hypot(topPts[k][0] - topPts[k - 1][0], topPts[k][1] - topPts[k - 1][1]);
          }
        }
        if (vGeo.length) {
          _asmMark(ctx, 'ga_cavity_vent', 'side', 'Rainscreen vent strip', GA_CVENT.code);
          var mVn = new T.MeshStandardMaterial({ color: _wallNew('cavityvent') ? MAGENTA : 0x2E3236, roughness: 0.8, metalness: 0.0 });
          vGeo.forEach(function (v) {
            var vm = new T.Mesh(new T.BoxGeometry(Math.abs(v.ux) > Math.abs(v.uz) ? v.L : WALL_FBATT.d, GA_CVENT.h, Math.abs(v.ux) > Math.abs(v.uz) ? WALL_FBATT.d : v.L), mVn);
            vm.position.set(v.s.x0 + v.ux * v.at + v.ox * (base + WALL_FBATT.d / 2), v.y, v.s.z0 + v.uz * v.at + v.oz * (base + WALL_FBATT.d / 2));
            vm.name = 'cavity-vent';
            g.add(vm);
          });
        }
        if (vFoot + vTop > 0) {
          var vLen = Math.round((vFoot + vTop) * 100) / 100, vSt = Math.ceil(vFoot / GA_CVENT.step) + Math.ceil(vTop / GA_CVENT.step) + vGeo.length;
          _add(LAYER, GA_CVENT.code, vLen);
          _add(LAYER, GA_CVENT.staple, vSt);
          ctx.log('WAL  cavity vent (garageapt-v30) the rainscreen cavity closed by ' + GA_CVENT.code + ' (an insect screen, air and water through): the foot ' + (Math.round(vFoot * 10) / 10) + ' m (the ground storey less its doorways), the head ' +
                  (Math.round(vTop * 10) / 10) + ' m (under the eaves and up the rakes) -- ' + vLen + ' m, ' + vSt + ' x ' + GA_CVENT.staple + ' every 16 in.');
        }

        /* NO SECOND LAYER HERE. Under vertical boards the horizontal counter
           battens are the BOARDS' pass (_wallCladding: drawn as
           facade-counter-batten and billed by its cbRun), as on the ADU. The
           shed family had them here as well and both were drawn and billed --
           67 m of KVH40060 twice on the default shed (core-v776). */
        if (!pieces) { ctx.log('WAL  facade battens none'); return; }
        _add(LAYER, WALL_FBATT.code, run);
        _add(LAYER, WALL_FBATT.screw, screws);
        ctx.log('WAL  facade battens ' + pieces + ' piece(s) on ' + lines + ' line(s), ' +
                Math.round(run * 10) / 10 + ' m of ' + Math.round(WALL_FBATT.f * 1000) +
                'x' + Math.round(WALL_FBATT.d * 1000) + ' ' + WALL_FBATT.code + ', ' +
                screws + ' x ' + WALL_FBATT.screw + ' at ' +
                Math.round(WALL_FBATT.screwStep * 1000) + ' mm');
        ctx.log('WAL  facade battens VERTICAL, on the studs, ' +
                Math.round(WALL_FBATT.d * 1000) + ' mm cavity, broken at every ' +
                'opening they pass behind -- 18 mm of sheathing will not hold a facade');
      }

      function _wallCladding(ctx) {
        /* THE PANEL WAS ALREADY DRAWN, as the sheathing pass, because on this
           variant they are the same sheet (the shed family). */
        if (PANEL && PANEL.on()) return;
        if (!_wallStepOn(9)) return;
        if (!_wallClad()) {
          ctx.log('WAL  cladding HIDDEN -- WALL_SHOW is frame; still billed');
          return;
        }
        if (_cladOff()) {
          ctx.log('WAL  cladding OFF -- this is the CONTACT facade: EPS on the sheathing, ' +
                  'mesh, base coat and render; no boards, no battens, no cavity');
          return;
        }
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, c, r;
        var vert = _cladVertical();
        /* THE VERTICAL BOARDS GO SEE-THROUGH SO HE CAN LOOK BEHIND THEM.

           Three rounds on "short OSB under the vertical cladding" and the numbers
           say the board is drawn 1390 mm above the head. Either the sheathing is
           not where the log says it is, or something in front of it is telling a
           different story -- and the only way to tell those apart is to see
           through the thing that is in front.

           Twenty per cent, on the VERTICAL case only, because that is where the
           fault is reported and the horizontal one is fine. It goes back to solid
           the moment we know what we are looking at: it is a way of looking, not
           a finish, and a facade you can see through is not a facade. */
        var CLAD_XRAY = 0.50;
        var xray = _wallGhost();
        var mat = new T.MeshStandardMaterial({
          color: _wallNew('cladding') ? MAGENTA : colorOf('timber'),
          roughness: 0.86, metalness: 0.0,
          transparent: xray, opacity: xray ? CLAD_XRAY : 1,
          depthWrite: !xray, side: T.DoubleSide });
        var cbMat = new T.MeshStandardMaterial({
          color: _wallNew('cladding') ? MAGENTA : colorOf('timber'),
          roughness: 0.9, metalness: 0.0 });

        var battTop = (TG && TG.on()) ? 0 : (0.018 + 0.001 + WALL_MEMB.t + 0.0005 + WALL_FBATT.d);   /* taurus-v12: Matt's T&G on the studs */
        var cbT = vert ? WALL_FBATT.d : 0;
        var base = battTop + cbT;
        var pitch = WALL_CLAD.w + WALL_CLAD.gap;
        var area = 0, boards = 0, nails = 0, cbRun = 0;
        var upNv = 0, upAv = 0;

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2) || !(s.h > 0.2)) continue;
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var along = Math.abs(ux) > Math.abs(uz);

          /* WHAT THE ROOF LEAVES ABOVE THIS WALL, so the counter-battens carry on
             over the ceiling line instead of a second pass starting its own set
             above it under its own name. Step two of the same job the vertical
             battens have already had done to them. */
          var Gc = null;
          try { Gc = _wallSkin(s); } catch (eC) { Gc = null; }
          var topC = s.y0 + s.h + (Gc ? Math.max(0, Gc.rise) : 0);

          /* the horizontal counter-batten, only when the boards stand up */
          if (vert) {
            var cy;
            for (cy = s.y0 + WALL_FBATT.f / 2; cy < topC; cy += WALL_FBATT.screwStep) {
              var cr;
              if (cy <= s.y0 + s.h) {
                cr = _battenRuns(s, cy - WALL_FBATT.f / 2, cy + WALL_FBATT.f / 2);
              } else {
                /* above the head there are no openings, only the roof: the bar is
                   cut to the width the profile still has at ITS OWN height, which
                   is the whole wall on a parapet and a shorter cut up a rake --
                   the same length a joiner would cut it to standing there */
                var spC = null;
                try { spC = Gc && Gc.spanAtH ? Gc.spanAtH(cy - Gc.top) : null; }
                catch (eS) { spC = null; }
                if (!spC || !(spC[1] - spC[0] > 0.05)) continue;
                cr = [[spC[0], spC[1]]];
              }
              for (r = 0; r < cr.length; r++) {
                var ca2 = cr[r][0], cl = cr[r][1] - ca2;
                if (!(cl > 0.05)) continue;
                var cbm = new T.Mesh(
                  new T.BoxGeometry(along ? cl : WALL_FBATT.d,
                                    WALL_FBATT.f,
                                    along ? WALL_FBATT.d : cl), cbMat);
                cbm.position.set(
                  s.x0 + ux * (ca2 + cl / 2) + ox * (battTop + WALL_FBATT.d / 2),
                  cy,
                  s.z0 + uz * (ca2 + cl / 2) + oz * (battTop + WALL_FBATT.d / 2));
                cbm.name = 'facade-counter-batten';
                g.add(cbm);
                cbRun += cl;
              }
            }
          }

          if (!vert) {
            /* boards across, struck from the bottom of the wall -- AND ON PAST
               THE HEAD TO THE ROOF. The courses used to stop at the wall head and
               the gable skin laid its own courses above it; now the same rhythm
               carries on to the roof line less the eaves intake, and a board the
               roof crosses is the whole board cut on the rake (_underRoof), as
               the sheathing behind it is. Above the head the run turns the corner
               by the facade's own depth at both ends, because the corner trim
               follows the lower wall and stops at the head. */
            var headH = s.y0 + s.h;
            var ventH = (Gc && (Gc.parapet || Gc.band)) ? 0 : WALL_EAVE.vent;   /* garageapt-v5: no eave vent at the floor band -- the boards run on */
            var roofYc = function (u) { return headH + (Gc ? Gc.hAt(u) : 0) - ventH; };
            var topH = Gc ? Math.max(headH, headH + Gc.rise - ventH) : headH;
            var fwdH = (oz * ux - ox * uz) > 0, cextH = _facadeFace();
            var upN = 0, upA = 0;
            var yB;
            for (yB = s.y0; yB < topH - 0.005; yB += pitch) {
              var bh2 = Math.min(WALL_CLAD.w, topH - yB);
              if (!(bh2 > 0.02)) continue;

              /* a course above the head: one piece, corner to corner, cut by the roof */
              if (Gc && yB + bh2 > headH + 0.005) {
                var yA9 = yB, yB9 = yB + bh2;
                var polyH = _underRoof(-cextH, s.len + cextH, yA9, yB9, roofYc, [Gc.at]);
                var aH = polyH.length >= 3 ? _polyArea(polyH) : 0;
                if (!(aH > 0.002)) continue;
                var e0H = fwdH ? -cextH : (s.len + cextH), kH;
                var shH = new T.Shape();
                for (kH = 0; kH < polyH.length; kH++) {
                  var lxH = fwdH ? (polyH[kH][0] + cextH) : (s.len + cextH - polyH[kH][0]);
                  if (kH === 0) shH.moveTo(lxH, polyH[kH][1] - yA9);
                  else shH.lineTo(lxH, polyH[kH][1] - yA9);
                }
                shH.lineTo(fwdH ? (polyH[0][0] + cextH) : (s.len + cextH - polyH[0][0]), polyH[0][1] - yA9);
                var bmH = new T.Mesh(
                  new T.ExtrudeGeometry(shH, { depth: WALL_CLAD.t, bevelEnabled: false }), mat);
                bmH.rotation.y = Math.atan2(ox, oz);
                bmH.position.set(s.x0 + ux * e0H + ox * base, yA9, s.z0 + uz * e0H + oz * base);
                bmH.name = 'cladding-board';
                if (CLAD_SHOW) g.add(bmH);
                var wH = 0, kW;
                for (kW = 0; kW < polyH.length; kW++) {
                  wH = Math.max(wH, Math.abs(polyH[kW][0] - polyH[(kW + 1) % polyH.length][0]));
                }
                area += aH;
                boards++;
                upN++; upA += aH;
                nails += Math.max(2, Math.round(wH / WALL_CLAD.nailStep) + 1) * 2;
                continue;
              }
              /* AND ACROSS, THE SAME FAULT ONE AXIS OVER. The cutter takes the
                 full width of any opening the course touches, so a board crossing
                 a head lost the window's whole width over its own height and left
                 bare batten above the glass. The pieces that belong above a head
                 and below a sill are put back. */
              var rr = _battenRuns(s, yB + 0.001, yB + bh2 - 0.001);
              var oq4, opsH = s.openings || [];
              for (oq4 = 0; oq4 < opsH.length; oq4++) {
                var hLo = s.y0 + opsH[oq4].sill, hHi = hLo + opsH[oq4].h;
                var ha = opsH[oq4].at - opsH[oq4].w / 2;
                var hb = opsH[oq4].at + opsH[oq4].w / 2;
                /* taurus-v9: a head (or a sill) within 3 mm of the course's own
                   edge still gets its board back -- the cutter takes the course
                   for a 1 mm overlap, and the 2.4 m portal's head fell 3 mm into
                   one: a whole course was missing over it, the film showing */
                if (hHi > yB + 0.0005 && hHi < yB + bh2 - 0.003) rr.push([ha, hb, hHi, yB + bh2]);
                if (hLo > yB + 0.003 && hLo < yB + bh2 - 0.0005) rr.push([ha, hb, yB, hLo]);
              }
              for (r = 0; r < rr.length; r++) {
                var a3 = rr[r][0], L3 = rr[r][1] - a3;
                var yA3 = (rr[r].length > 2) ? rr[r][2] : yB;
                var yB3 = (rr[r].length > 3) ? rr[r][3] : (yB + bh2);
                var hh3 = yB3 - yA3;
                if (!(L3 > 0.05) || !(hh3 > 0.02)) continue;
                var bm2 = new T.Mesh(
                  new T.BoxGeometry(along ? L3 : WALL_CLAD.t,
                                    hh3,
                                    along ? WALL_CLAD.t : L3), mat);
                bm2.position.set(
                  s.x0 + ux * (a3 + L3 / 2) + ox * (base + WALL_CLAD.t / 2),
                  (yA3 + yB3) / 2,
                  s.z0 + uz * (a3 + L3 / 2) + oz * (base + WALL_CLAD.t / 2));
                bm2.name = 'cladding-board';
                if (CLAD_SHOW) g.add(bm2);
                area += L3 * hh3;
                boards++;
                nails += Math.max(2, Math.round(L3 / WALL_CLAD.nailStep) + 1) * 2;
              }
            }
            if (Gc && topH > headH + 0.02) {
            }
          } else {
            /* boards up, struck from the start of the wall.

               A BOARD IS RIPPED TO THE REVEAL, NOT DROPPED FOR MISSING ITS CENTRE.

               The runs were asked for at the MIDDLE of each board, so a board
               whose centre fell inside a window vanished over its whole width,
               and a board whose centre missed it by a millimetre ran straight
               across the glass. Either way the last board before a reveal could be
               a whole 135 short of it -- the gap he can see beside his windows,
               and it moved when the window moved, because it depended on where the
               centre happened to land.

               On the wall a board that meets an opening is RIPPED and carries on
               at whatever width is left. So each board is split at the reveal
               lines it crosses and every piece is asked about on its own: beside
               the jamb that gives a narrow full-height board, and over the head
               and under the sill a short one -- which is what closes the wall
               under the sill. */
            var xB;
            for (xB = 0; xB < s.len - 0.005; xB += pitch) {
              var bw2 = Math.min(WALL_CLAD.w, s.len - xB);
              if (!(bw2 > 0.02)) continue;

              var cuts = [xB, xB + bw2], oq2, opsC = s.openings || [];
              for (oq2 = 0; oq2 < opsC.length; oq2++) {
                var ca = opsC[oq2].at - opsC[oq2].w / 2;
                var cb = opsC[oq2].at + opsC[oq2].w / 2;
                if (ca > xB + 0.002 && ca < xB + bw2 - 0.002) cuts.push(ca);
                if (cb > xB + 0.002 && cb < xB + bw2 - 0.002) cuts.push(cb);
              }
              cuts.sort(function (p1, p2) { return p1 - p2; });

              var ci;
              for (ci = 0; ci < cuts.length - 1; ci++) {
                var pa = cuts[ci], pw = cuts[ci + 1] - pa;
                if (!(pw > 0.005)) continue;
                var vr = _vertRuns(s, pa + pw / 2);
                for (r = 0; r < vr.length; r++) {
                  var y3 = vr[r][0], H3 = vr[r][1] - y3;
                  if (!(H3 > 0.05)) continue;

                  /* AND A BOARD THAT REACHES THE HEAD CARRIES ON PAST IT.

                     A vertical board stopped at the wall head and a second one
                     started there, so there was a joint straight across the
                     facade at ceiling level -- on a parapet, where the wall
                     simply keeps going for another half metre, that joint is in
                     nothing and covers nothing. Boards are cut to length; there
                     is no reason for that one.

                     It grows by the LOWEST the roof is anywhere across this
                     board's own width, so a square-topped board can never poke
                     through a slope. On a parapet that is the whole rise and the
                     board is one piece to the coping, which is what he asked for.
                     Where the roof does slope across it, the wedge above this
                     line is the gable skin's, and the two meet on a line that is
                     a real cut rather than an arbitrary one. */
                  /* AND NOW THE WHOLE WAY: the board is one shape from its own
                     bottom to the roof line, tip cut on the rake (_underRoof), so
                     the wedge the gable skin used to add above the square part is
                     gone and there is no line across the board where the two met.
                     Same intake trim as the boards across; none on a parapet. */
                  if (vr[r][1] >= s.y0 + s.h - 0.005 && Gc && Gc.rise > 0.01) {
                    var headV = s.y0 + s.h, ventV = (Gc.parapet || Gc.band) ? 0 : WALL_EAVE.vent;
                    var roofYv = function (u) { return headV + Gc.hAt(u) - ventV; };
                    var polyV = _underRoof(pa, pa + pw, y3, headV + Gc.rise, roofYv, [Gc.at]);
                    var aV = polyV.length >= 3 ? _polyArea(polyV) : 0;
                    if (aV > 0.002) {
                      var fwdV = (oz * ux - ox * uz) > 0, e0V = fwdV ? pa : (pa + pw), kV;
                      var shV = new T.Shape();
                      for (kV = 0; kV < polyV.length; kV++) {
                        var lxV = fwdV ? (polyV[kV][0] - pa) : (pa + pw - polyV[kV][0]);
                        if (kV === 0) shV.moveTo(lxV, polyV[kV][1] - y3);
                        else shV.lineTo(lxV, polyV[kV][1] - y3);
                      }
                      shV.lineTo(fwdV ? (polyV[0][0] - pa) : (pa + pw - polyV[0][0]), polyV[0][1] - y3);
                      var bmV = new T.Mesh(
                        new T.ExtrudeGeometry(shV, { depth: WALL_CLAD.t, bevelEnabled: false }), mat);
                      bmV.rotation.y = Math.atan2(ox, oz);
                      bmV.position.set(s.x0 + ux * e0V + ox * base, y3, s.z0 + uz * e0V + oz * base);
                      bmV.name = 'cladding-board';
                      if (CLAD_SHOW) g.add(bmV);
                      var hV = 0, kT;
                      for (kT = 0; kT < polyV.length; kT++) hV = Math.max(hV, polyV[kT][1] - y3);
                      area += aV;
                      boards++;
                      upNv++; upAv += aV - pw * H3;
                      nails += Math.max(2, Math.round(hV / WALL_CLAD.nailStep) + 1) * 2;
                      continue;
                    }
                  }
                  var bm3 = new T.Mesh(
                    new T.BoxGeometry(along ? pw : WALL_CLAD.t,
                                      H3,
                                      along ? WALL_CLAD.t : pw), mat);
                  bm3.position.set(
                    s.x0 + ux * (pa + pw / 2) + ox * (base + WALL_CLAD.t / 2),
                    y3 + H3 / 2,
                    s.z0 + uz * (pa + pw / 2) + oz * (base + WALL_CLAD.t / 2));
                  bm3.name = 'cladding-board';
                  if (CLAD_SHOW) g.add(bm3);
                  area += pw * H3;
                  boards++;
                  nails += Math.max(2, Math.round(H3 / WALL_CLAD.nailStep) + 1) * 2;
                }

                /* taurus-v20: AND OVER A DOOR THAT REACHES THE WALL HEAD. The
                   runs stop at the head, so a board over an opening whose head
                   is at or above it had nothing to carry on from and the wedge
                   from the door to the roof was left open -- Matt's 74 inch
                   doors at the low end of his raked walls. The board starts on
                   the head and is cut on the rake like the others. */
                if (Gc && Gc.rise > 0.01) {
                  var reachV = false, rq5;
                  for (rq5 = 0; rq5 < vr.length; rq5++) if (vr[rq5][1] >= s.y0 + s.h - 0.005) reachV = true;
                  var opTop = -1, oq5, mid5 = pa + pw / 2;
                  if (!reachV) {
                    for (oq5 = 0; oq5 < opsC.length; oq5++) {
                      var o5 = opsC[oq5];
                      if (mid5 <= o5.at - o5.w / 2 || mid5 >= o5.at + o5.w / 2) continue;
                      var t5 = s.y0 + o5.sill + o5.h;
                      if (t5 >= s.y0 + s.h - 0.005) opTop = Math.max(opTop, t5);
                    }
                  }
                  if (opTop > 0) {
                    var head5 = s.y0 + s.h, vent5 = Gc.parapet ? 0 : WALL_EAVE.vent;
                    var roofY5 = function (u) { return head5 + Gc.hAt(u) - vent5; };
                    var poly5 = _underRoof(pa, pa + pw, opTop, head5 + Gc.rise, roofY5, [Gc.at]);
                    var a5 = poly5.length >= 3 ? _polyArea(poly5) : 0;
                    if (a5 > 0.002) {
                      var fwd5 = (oz * ux - ox * uz) > 0, e05 = fwd5 ? pa : (pa + pw), k5;
                      var sh5 = new T.Shape();
                      for (k5 = 0; k5 < poly5.length; k5++) {
                        var lx5 = fwd5 ? (poly5[k5][0] - pa) : (pa + pw - poly5[k5][0]);
                        if (k5 === 0) sh5.moveTo(lx5, poly5[k5][1] - opTop);
                        else sh5.lineTo(lx5, poly5[k5][1] - opTop);
                      }
                      sh5.lineTo(fwd5 ? (poly5[0][0] - pa) : (pa + pw - poly5[0][0]), poly5[0][1] - opTop);
                      var bm5 = new T.Mesh(
                        new T.ExtrudeGeometry(sh5, { depth: WALL_CLAD.t, bevelEnabled: false }), mat);
                      bm5.rotation.y = Math.atan2(ox, oz);
                      bm5.position.set(s.x0 + ux * e05 + ox * base, opTop, s.z0 + uz * e05 + oz * base);
                      bm5.name = 'cladding-board';
                      if (CLAD_SHOW) g.add(bm5);
                      var h5 = 0, kq5;
                      for (kq5 = 0; kq5 < poly5.length; kq5++) h5 = Math.max(h5, poly5[kq5][1] - opTop);
                      area += a5;
                      boards++;
                      upNv++; upAv += a5;
                      nails += Math.max(2, Math.round(h5 / WALL_CLAD.nailStep) + 1) * 2;
                    }
                  }
                }
              }
            }
          }
        }

        if (!boards) { ctx.log('WAL  cladding none'); return; }
        if (vert && upNv) {
          ctx.log('WAL  cladding VERTICAL: ' + upNv + ' board(s) run through the head to the ' +
                  'roof in one piece, tip cut on the rake, ' + API.fmtArea(upAv) +
                  ' of that above the head -- no gable-cladding column any more');
        }
        if (!CLAD_SHOW) {
          ctx.log('WAL  cladding BOARDS HIDDEN -- CLAD_SHOW is off. The boards on the ' +
                  'wall AND the ones carrying on above the head are struck, ripped to ' +
                  'their reveals, nailed and billed exactly as before; they do not ' +
                  'reach the scene. Battens, counter-battens, film, sheathing and the ' +
                  'corner trims are all untouched, so what is left is what the boards ' +
                  'were hanging on');
        }
        _add(LAYER, WALL_CLAD.code, _cladQty(area));
        _add(LAYER, WALL_CLAD.nail, nails);
        _extCoat(area);
        if (cbRun > 0) {
          _add(LAYER, WALL_FBATT.code, cbRun);
          _add(LAYER, WALL_FBATT.screw,
               Math.round(cbRun / WALL_FBATT.screwStep) + 1);
        }
        if (xray) {
          ctx.log('WAL  cladding DRAWN AT 20 PER CENT so the sheathing behind it can be ' +
                  'seen -- a way of looking, not a finish; CLAD_XRAY goes back to 1 as ' +
                  'soon as we know what the short board actually is');
        }
        ctx.log('WAL  cladding ' + (vert ? 'VERTICAL' : 'HORIZONTAL') + ', ' +
                boards + ' board(s), ' + API.fmtArea(area) + ' of ' +
                Math.round(WALL_CLAD.w * 1000) + 'x' + Math.round(WALL_CLAD.t * 1000) +
                ' ' + WALL_CLAD.code + ', gap ' + Math.round(WALL_CLAD.gap * 1000) +
                ' mm, ' + nails + ' x ' + WALL_CLAD.nail);
        if (vert && TG && TG.on()) {
          ctx.log('WAL  cladding Matt\'s T&G VERTICAL on horizontal rails: ' +
                  Math.round(cbRun * 10) / 10 + ' m of ' + WALL_FBATT.code +
                  ' straight on the studs at ' + Math.round(WALL_FBATT.screwStep * 1000) +
                  ' mm, what the boards are nailed to; still no OSB and no breather (taurus-v20)');
        } else if (vert) {
          ctx.log('WAL  cladding vertical needs a counter-batten: ' +
                  Math.round(cbRun * 10) / 10 + ' m of ' + WALL_FBATT.code +
                  ' horizontal over the vertical ones, and the wall is ' +
                  Math.round(WALL_FBATT.d * 1000) + ' mm thicker for it');
        }
        _why(ctx, 'wall     the module is struck from the BUILDING, not from each piece, ' +
                'so two walls at a corner line their boards up and the courses do not ' +
                'step at a reveal');
      }

      function _wallEps(ctx) {
        if (!_wallStepOn(9)) return;
        if (!_cladOff()) return;
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, c, r;
        var mat = new T.MeshStandardMaterial({
          color: _wallNew('eps') ? MAGENTA : 0xF0EFE6,
          roughness: 0.98, metalness: 0.0 });
        var trkMat = new T.MeshStandardMaterial({
          color: _wallNew('eps') ? MAGENTA : 0xB9BDC2,
          roughness: 0.5, metalness: 0.6 });

        /* THE JOINTS GO BOLD WHEN THE INSULATION IS WHAT YOU CAME TO LOOK AT.

           At 6 mm and a shade off white they are honest and nearly invisible,
           which is right on a finished wall and useless for checking a bond. In
           eps view they are drawn twice as wide and properly dark, so the half
           board offset reads across a whole elevation and the plates sitting on
           the Ts read with it.

           The BILL does not move: a joint is a gap between boards, and how boldly
           it is drawn is a question about eyesight, not about materials. */
        /* the bond stays legible with the mesh back on: ghost and eps are both
           views for looking at what is under the render, so both get the wide
           joint. Only the finished wall draws it as the hairline it really is. */
        var jBold = (WALL_SHOW !== 'all');
        /* the boards the roof cuts wore magenta for one round; the clip is
           accepted and they are the same board as the rest */
        var cutMat = mat;
        var cutN = 0;
        /* THE PLATE, AS TINY DRAWS IT: a grey disc with a ring of holes punched
           on a circle inside it. The holes are what the render keys into and they
           are the reason the plate is not just a washer, so they are drawn. Bigger
           than before -- 90 mm, which is the plate you actually buy. */
        var anchMat = new T.MeshStandardMaterial({
          color: _wallNew('eps') ? MAGENTA : 0x9AA0A4,
          roughness: 0.65, metalness: 0.15 });
        var anchHole = new T.MeshStandardMaterial({
          color: _wallNew('eps') ? MAGENTA : 0x53585C,
          roughness: 0.9, metalness: 0.0 });
        var anchR = 0.045, anchT = 0.006;
        var anchGeo = new T.CylinderGeometry(anchR, anchR, anchT, 20);
        var holeGeo = new T.CylinderGeometry(0.006, 0.006, anchT + 0.002, 8);
        var bossGeo = new T.CylinderGeometry(0.011, 0.011, anchT + 0.003, 12);
        var anchSeen = {}, anchors = 0, anchFail = null;
        var anchLines = [], anchField = [], anchDiag = [];

        var osbT = 0.018 + 0.001;
        var area = 0, boards = 0, trackM = 0, whole = 0, jn3 = 0;  /* jn3 unused now */

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2) || !(s.h > 0.2)) continue;
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var along = Math.abs(ux) > Math.abs(uz);

          /* ONE COURSING, TRACK TO TOP -- THE HEAD IS NOT A JOINT.

             The wall stopped its boards at the ceiling and the gable started its
             own above it, so the whole building had a horizontal break at head
             height, both halves setting out their own courses from their own ends.
             That is why the bond stopped reading across the storey and why every
             piece cut under a truss came out as its own little board.

             There is no joint there. The insulation runs from the starter track to
             the top of the facade in one set of courses, and the frame, the
             trusses and the openings are all just things it is cut around.

             So the top is the head PLUS whatever the roof leaves over this wall,
             and the gable skin no longer lays EPS -- it would be laying it twice. */
          var gTop = 0, gProf = null, gApex = null, gHead = s.y0 + s.h;
          try {
            var GE = _wallSkin(s);
            if (GE) { gTop = GE.rise; gProf = GE.hAt; gApex = GE.at; }
          } catch (eE) { gTop = 0; gProf = null; gApex = null; }
          var top = gHead + gTop;

          /* THE TRACK GOES AT THE FOOT OF THE INSULATION, WHEREVER THAT IS.

             It was pinned to the wall base, and then the skirt carried the EPS 288
             mm below that -- so the track ended up as a bar across the middle of
             the insulation instead of the thing holding its first course. It sits
             at the bottom of the skirt now, and where there is no skirt (a slab)
             it stays where it was. */
          var trkY = s.y0 - _skirtDrop() + 0.015;
          var tm = new T.Mesh(
            new T.BoxGeometry(along ? s.len : WALL_EPS.t, 0.030,
                              along ? WALL_EPS.t : s.len), trkMat);
          tm.position.set(
            s.x0 + ux * (s.len / 2) + ox * (osbT + WALL_EPS.t / 2),
            trkY,
            s.z0 + uz * (s.len / 2) + oz * (osbT + WALL_EPS.t / 2));
          tm.name = 'eps-starter-track';
          g.add(tm);
          trackM += s.len;

          /* THE COURSES BELONG TO THE WALL, NOT TO THE PIECES.

             _wallFaces splits at every sill and head across the whole segment, so
             a window put a horizontal seam right along the elevation at its own
             head height -- the same fault the membrane had, one layer out. The
             courses are struck from the track and the openings are cut OUT of
             them, which is the way the boards actually go on.

             AND THE RUN CARRIES ROUND THE CORNER. Each wall's boards stopped on
             its own end, and the two boards meeting at a corner are each offset
             outward by their own thickness, so the outside corner came back with a
             square notch in it. The run is extended by one board thickness at the
             far end: that fills the notch exactly, and exactly once, because every
             wall does it at the same end and the polygon runs one way. Butted, not
             mitred, which is how the board is cut on site. */
          var ops3 = s.openings || [], oq3;
          /* AND THE FIRST COURSE STARTS WHERE THE SKIRT HANDS OVER.

             It used to start 30 mm up, which was the height of the track. With the
             track moved to the foot of the skirt that 30 mm became a strip of bare
             sheathing right at the floor line -- the one place water gets at. Where
             there is no skirt the track is still there and the offset still is. */
          var epsY0 = s.y0 + (_skirtDrop() > 0 ? 0 : 0.030);
          var kMax3 = Math.ceil((top - epsY0) / WALL_EPS.h), kq3;
          var course, yB;
          for (kq3 = 0; kq3 < kMax3; kq3++) {
            course = kq3;
            yB = epsY0 + WALL_EPS.h * kq3;
            var cHi3 = Math.min(yB + WALL_EPS.h, top);
            if (!(cHi3 - yB > 0.02)) continue;

            var strips3 = _battenRuns(s, yB + 0.001, cHi3 - 0.001);
            for (oq3 = 0; oq3 < ops3.length; oq3++) {
              var oLo3 = s.y0 + ops3[oq3].sill, oHi3 = oLo3 + ops3[oq3].h;
              var oa3 = ops3[oq3].at - ops3[oq3].w / 2;
              var ob3 = ops3[oq3].at + ops3[oq3].w / 2;
              if (oHi3 > yB + 0.005 && oHi3 < cHi3 - 0.005) strips3.push([oa3, ob3, oHi3, cHi3]);
              if (oLo3 > yB + 0.005 && oLo3 < cHi3 - 0.005) strips3.push([oa3, ob3, yB, oLo3]);
            }

            for (r = 0; r < strips3.length; r++) {
              var ra = strips3[r][0], rb = strips3[r][1];
              if (strips3[r].length <= 2) {
                /* a full-height course strip: its two edges are joint lines, and
                   its own middle carries the field plates */
                anchLines.push([yB, ra, rb]);
                if (cHi3 >= top - 0.005) anchLines.push([cHi3, ra, rb]);
                anchField.push([(yB + cHi3) / 2, ra, rb,
                                (course % 2) ? -WALL_EPS.w / 2 : 0]);
              }
              var rLo = (strips3[r].length > 2) ? strips3[r][2] : yB;
              var rHi = (strips3[r].length > 3) ? strips3[r][3] : cHi3;

              /* WHETHER AN EDGE IS A JOINT IS KNOWN RIGHT HERE, AND I KEPT
                 DERIVING IT SOMEWHERE ELSE.

                 A strip that came from the course loop has yB and cHi3 as its
                 edges: those are course lines, so they are joints. A strip pushed
                 on by the opening loop carries its own two numbers: those are
                 cuts round a hole.

                 Three times now I have thrown the T plates away by working this
                 out again from a grid I invented -- a base struck from the track,
                 a modulus, a tolerance -- and every time the arithmetic missed and
                 every edge came back "not a joint". The answer was never a
                 calculation. It is which of two lists the strip came from, and it
                 is available on this line. */
              var tLo = Math.abs(rLo - yB) < 0.0005;
              var tHi = Math.abs(rHi - cHi3) < 0.0005;
            /* A LAYER TURNS THE CORNER BY ITS OWN FACE, NOT BY THE BOARD'S.

               Every layer was extended by the EPS thickness, because the EPS was
               the first one that needed it. But the notch a layer has to fill is
               as deep as ITS OWN outer face stands from the wall line -- the coat
               sits on the board and so reaches further than the board, and the
               render sits on the coat and reaches further still. Short by six
               millimetres, then by twelve: the two centimetres he can see at every
               corner, on every roof, wherever a layer stopped where the one
               underneath it stopped.

               So each one carries on by its own depth and they close in sequence,
               the way they sit in section. */
              /* CORNER TO CORNER. BOTH ENDS. That is the whole rule and I have
                 been writing it as half of one.

                 A layer sitting d out from the wall lives on an outline pushed out
                 by d, and that outline's corner is d PAST the plan corner -- at
                 each end, not at one. Extending only the far end leaves every
                 layer short at its near end, which is the same side of every
                 corner on the whole building: his four bare EPS ends, top and
                 bottom, always on the left.

                 Both ends. The overlap at a corner is the mitre and is billed
                 once, on the plan length. */
              var extE = 0.018 + 0.001 + WALL_EPS.t;
              if (rb >= s.len - 0.01) rb = s.len + extE;
              if (ra <= 0.01) ra = -extE;
              if (!(rb - ra > 0.03) || !(rHi - rLo > 0.03)) continue;

              var start = (course % 2) ? -WALL_EPS.w / 2 : 0;
              var xB;
              for (xB = start; xB < rb - 0.005; xB += WALL_EPS.w) {
                var xS = Math.max(xB, ra), xE = Math.min(xB + WALL_EPS.w, rb);
                var L = xE - xS, H = rHi - rLo;
                if (!(L > 0.03) || !(H > 0.03)) continue;
                whole++;
                /* AND UP IN THE GABLE THE BOARD IS CUT TO THE ROOF.

                   Carrying the coursing to the top was right; letting it carry on
                   as full boards was not. The top of the wall is not a straight
                   line on a gable or a mono, so the last course stood out over the
                   roof right across the elevation.

                   A board is cut on site and it is cut here: where the roof line
                   crosses it, it becomes a trapezium with that line as its top
                   edge, exactly as the sheathing behind it does. Below the line it
                   is a whole board and nothing changes. Above it there is no
                   board at all. */
                var ch = WALL_EPS.joint * (jBold ? 2 : 1);
                var Ld = Math.max(0.02, L - ch), Hd = Math.max(0.02, H - ch);
                var yB0 = rLo + ch / 2;

                var cutA = null, cutB = null;
                if (gProf) {
                  cutA = gHead + gProf(xS);
                  cutB = gHead + gProf(xE);
                }
                var bm;
                if (cutA !== null &&
                    (cutA < rHi - 0.005 || cutB < rHi - 0.005)) {
                  /* THE BOARD IS CLIPPED BY THE ROOF, NOT DRAWN BETWEEN TWO GUESSED
                     CORNERS.

                     The old cut took the roof height at the board's two edges and
                     joined them -- and where the roof passed BELOW the course at
                     one edge that height was clamped to zero, so the line ran
                     from the board's bottom corner to the roof at the other edge:
                     flatter than the roof, with a wedge of board standing over
                     the tiles. The last board of every course on the rake did it,
                     which is the row of odd wedges he can see, and the board
                     that straddles the apex got one straight line across the
                     peak. The cladding above the head had the same fault and got
                     the same answer: cut the shape by the roof, all of it.

                     So the whole board rectangle is clipped by the roof line
                     sampled every 150 mm across it. A board the roof crosses
                     through its top, its side, its bottom, or over the apex all
                     come out of the same three lines, and the area is the area
                     of what is left. The log carries every corner. */
                  var fwdE = (oz * ux - ox * uz) > 0;
                  var Hmax = rHi - yB0;
                  var hAtLocal = function (lx) {
                    var uL = fwdE ? (xS + lx) : (xE - lx);
                    return gHead + gProf(uL) - yB0;
                  };
                  /* the band under the roof, built directly (the half-plane clip
                     cut the level stretch over a corner off -- see _underRoof);
                     the ridge, when it falls inside this board, is its own corner */
                  var lxA = (gApex !== null) ? (fwdE ? (gApex - xS) : (xE - gApex)) : null;
                  var poly = _underRoof(0, Ld, 0, Hmax, hAtLocal, lxA !== null ? [lxA] : null);
                  var ks;
                  var pArea = (poly.length >= 3) ? _polyArea(poly) : 0;
                  if (!(pArea > 0.002)) {
                    ctx.log('EPS  W' + i + ' c' + course + ' x ' + Math.round(xS * 1000) + '..' +
                            Math.round(xE * 1000) + ' y ' + Math.round((rLo - gHead) * 1000) +
                            '..' + Math.round((rHi - gHead) * 1000) + ' -- nothing under the roof, ' +
                            'no board');
                    continue;
                  }
                  var shE = new T.Shape();
                  shE.moveTo(poly[0][0], poly[0][1]);
                  for (ks = 1; ks < poly.length; ks++) shE.lineTo(poly[ks][0], poly[ks][1]);
                  shE.lineTo(poly[0][0], poly[0][1]);
                  var e0 = fwdE ? xS : xE;
                  bm = new T.Mesh(
                    new T.ExtrudeGeometry(shE, { depth: WALL_EPS.t, bevelEnabled: false }),
                    cutMat);
                  bm.rotation.y = Math.atan2(ox, oz);
                  bm.position.set(s.x0 + ux * e0 + ox * osbT, yB0,
                                  s.z0 + uz * e0 + oz * osbT);
                  bm.name = 'eps-board';
                  g.add(bm);
                  area += pArea;
                  boards++;
                  cutN++;
                  /* every corner, in the wall's own frame: along the wall from its
                     start, and height above the wall head, both in mm */
                  var pts9 = [], kp;
                  for (kp = 0; kp < poly.length; kp++) {
                    var uW = fwdE ? (xS + poly[kp][0]) : (xE - poly[kp][0]);
                    pts9.push(Math.round(uW * 1000) + ',' +
                              Math.round((yB0 + poly[kp][1] - gHead) * 1000));
                  }
                  ctx.log('EPS  W' + i + ' c' + course + ' x ' + Math.round(xS * 1000) + '..' +
                          Math.round(xE * 1000) + ' y ' + Math.round((rLo - gHead) * 1000) + '..' +
                          Math.round((rHi - gHead) * 1000) + ' roof ' +
                          Math.round((cutA - gHead) * 1000) + '/' + Math.round((cutB - gHead) * 1000) +
                          ' -> ' + poly.length + ' corners [' + pts9.join(' ') + '] ' +
                          (Math.round(pArea * 1000) / 1000) + ' m2');
                  continue;
                }
                if (cutA !== null && cutA <= rLo + 0.005 && cutB <= rLo + 0.005) continue;

                /* AND A SQUARE BOARD NEVER GOES PAST THE LOWER OF ITS TWO CUTS.

                   The trapezium above handles a board the roof line crosses. This
                   is the one it does not -- and it was drawn to the full course
                   height on trust. Where the roof is only just above the course,
                   trust is how a board ends up standing over the tiles. It is
                   clamped to whichever of its two ends the roof is lower over, so
                   it cannot pass the roof even if the branch above misses. */
                /* AND NO SECOND OPINION ON THE CUT.

                   I clamped the square board to the lower of its two roof heights
                   as a belt against the trapezium branch missing. Where the roof
                   is only just above a course that clamp takes a bite out of a
                   board the roof never touched -- the notches he can see along the
                   rake. The trapezium branch owns the cut; this one draws the
                   board it was given. */

                bm = new T.Mesh(
                  new T.BoxGeometry(along ? Ld : WALL_EPS.t, Hd,
                                    along ? WALL_EPS.t : Ld), mat);
                bm.position.set(
                  s.x0 + ux * (xS + L / 2) + ox * (osbT + WALL_EPS.t / 2),
                  (rLo + rHi) / 2,
                  s.z0 + uz * (xS + L / 2) + oz * (osbT + WALL_EPS.t / 2));
                bm.name = 'eps-board';
                g.add(bm);
                area += L * H;
                boards++;

                /* THE ANCHORS, AND THEY GO IN THE JOINTS.

                   They have been billed since the day the EPS was drawn and never
                   drawn once -- five a board, a number with nothing behind it.

                   Where they go is not decoration. A plate set in the FIELD of a
                   board holds that board and nothing else; a plate set on a JOINT
                   holds two, and one on a junction of joints holds three or four.
                   That is why tiny puts them there and why the pattern is worth
                   drawing rather than assuming: it is the difference between five
                   fixings and five fixings that each do double duty.

                   So: one in the middle of the board, and one at the middle of
                   each of its four edges. The edge ones land exactly where the
                   neighbour's do, so they are counted ONCE -- rounded to the
                   millimetre and kept in a set -- and the bill is what is actually
                   drilled rather than five times the number of boards. */
                /* AND THE TWO ON THE TOP AND BOTTOM EDGES ARE THE T-JOINTS.

                   He asked whether the boards are laid in half bond so that one
                   plate at a T holds three of them. They are -- the course offset
                   below is half a board, which is what puts every vertical joint
                   over the MIDDLE of the board underneath instead of over the
                   joint underneath. A cross joint would give four boards meeting
                   at a point that nothing can hold; a T gives three that one plate
                   does hold.

                   The middle of this board's top edge IS that T, and so is the
                   middle of its bottom edge. So the pattern below is not five
                   anchors in convenient places: it is one in the field, two on
                   joints holding two boards each, and two on Ts holding three
                   each -- eleven board-holds out of five plates. */
                /* AND THE ANCHORS CANNOT BE ALLOWED TO KILL THE INSULATION.

                   He is looking at ONE board on a whole facade. A layer that draws
                   a hundred and stops after the first is a layer that threw on the
                   second, and the only thing added between the version that worked
                   and this one is the anchor block below. Whatever it is throwing
                   on, an anchor is a 60 mm disc and the EPS is the wall: the
                   insulation must not depend on it.

                   So the anchors get their own try, and if they throw, they say so
                   once and the boards carry on. A missing plate is a line in the
                   log; a missing wall is a morning. */
                /* THE NAMES WERE WRONG AND THE LOG SAID SO: "a2 is not defined".
                   The board runs xS..xE between rLo and rHi in this loop; I wrote
                   it from memory of a different one. */
                /* THE T-JOINTS FIRST, AND A SMALL PIECE GETS ONE.

                   Five a board put three plates round every little offcut under a
                   window -- an eighth of a board with more fixings than a whole
                   one, which is nobody's idea of how it is done.

                   So the pattern follows what the plate is FOR. The two on the top
                   and bottom edge midpoints are the Ts, where the course above
                   breaks over the middle of this board and one plate holds three.
                   Those go in whatever the board is. The one in the field is for
                   holding the board itself and is only worth a hole on a board big
                   enough to lift off the wall -- half a full one. The side edges
                   get nothing: the T two hundred millimetres away is already
                   holding that joint. */
                /* AND A PIECE TOO SMALL TO HOLD A PLATE GETS NONE.

                   Round a window the boards are cut into narrow strips, and giving
                   each strip its two T plates stacked them into a thicket -- on
                   his screen they are shoulder to shoulder and some of them are
                   sitting in the opening and on the sill.

                   A plate is 90 mm across and it needs board around it, not just
                   under it. So a piece has to be 300 in both directions before it
                   takes one at all, and every plate has to sit a clear radius plus
                   20 inside the piece it is fixed to. What that leaves round an
                   opening is what a fixer would actually put there: the Ts on the
                   full boards either side, and nothing on the slivers. */
                /* THE T PLATES ARE SUPPOSED TO BE ON THE EDGE. I THREW THEM AWAY.

                   Last version I gave every plate a clear radius inside the piece
                   it is fixed to. That is right for the one in the field and
                   exactly wrong for the two on the joints: a T plate sits ON the
                   edge, because the edge is the joint and holding it is the whole
                   job. My test asked them to stand clear of the thing they exist
                   to hold, so all of them went. He is right and it is the third
                   time I have removed the T plates by fixing something else.

                   So the clearance is now the one that means something: ALONG the
                   wall, so no plate hangs over a reveal. Up the wall it applies to
                   the field plate only.

                   AND THE FIELD PLATE GOES ON A SHORT PIECE. On a course cut down
                   under a truss, the middle of the piece is a hand's width from
                   the joint above it -- two plates almost touching, which is what
                   he can see. Under 400 tall there is no field to hold: the two Ts
                   are already doing it. */
                /* AND A T PLATE NEEDS A T. An edge that is a COURSE line is a
                   joint and gets one. An edge where the board was cut round a hole
                   is a reveal: there is no board on the other side of it to hold,
                   and a plate there ends up in the opening or on the sill, which
                   is what he keeps finding above and below his windows.

                   The course lines are struck from the track at the board height,
                   so an edge is a joint when it sits on that grid and a cut when
                   it does not. */
                var full = (L * H) > (WALL_EPS.w * WALL_EPS.h * 0.5);
                var fits = (L > 0.30);
                var clear = anchR + 0.020;
                /* A BOARD HAS FOUR Ts, NOT TWO, AND I WAS ONLY PLACING TWO.

                   In a half bond every board meets the course below at its own two
                   bottom CORNERS -- that is where its vertical joint runs into the
                   horizontal one, three boards at a point -- and it meets the
                   course above at the middles of its top and bottom edges, where
                   the board up there breaks over it.

                   I was placing the mid-edge pair and nothing else, so along the
                   lowest course and up the gable, where a board has no neighbour
                   above to contribute its own mid-edge plate, whole T-joints went
                   unfixed. He spotted both.

                   All four go in. They are shared with the neighbours and the
                   de-duplication collapses them, so the count is one plate per
                   junction rather than one per board that touches it.

                   AND A T IS NEVER MOVED FOR CLEARANCE. It is on the joint because
                   the joint is the job; only the field plate has to stand clear of
                   anything. */
                /* HIS RULE, AND IT IS SIMPLER THAN EITHER OF MINE: ONE AT EACH
                   CORNER OF THE BOARD AND ONE IN THE MIDDLE, NEVER DUPLICATED.

                   I had corners and mid-edges both, which at the course where the
                   wall runs into the gable put plates on top of each other -- the
                   nonsense count he can see along that line. A corner is already
                   shared by up to four boards, so the corner set alone gives every
                   junction its plate; the mid-edge ones added nothing but density.

                   Corners are Ts and stay on the joint. The middle one is the
                   field plate and only goes on a board still most of its own size,
                   so an offcut round an opening carries its corners and no more. */
                /* THE PLATES ARE NOT PLACED FROM THE BOARDS ANY MORE.

                   Every board offered its own corners and its own mid-edge, and
                   the wall then de-duplicated whatever landed in the same spot.
                   That works while the courses either side of a line are exactly
                   half a board apart. At the line where the wall runs into the
                   gable they are not: the strips above it come from a different
                   subdivision, so their corners land a few tens of millimetres off
                   the ones below -- close enough to look like a pair, far enough
                   that the de-duplication keeps both. Two plates touching in one
                   place and a missing one where a cut board had no corner to
                   offer: exactly the two symptoms he described on that one edge.

                   A T-joint is a property of the COURSE LINE, not of a board. So
                   the lines are recorded here and the plates are set out along
                   them afterwards, at the module, from one origin. Nothing can be
                   half a board out because nothing is measured from a board. */

                /* NO JOINT OBJECTS. A CHAMFER ON THE BOARD INSTEAD.

                   I was drawing each joint as its own slab, which is a solid the
                   size of a gap -- and being a little thicker than the insulation
                   it stuck out past the wall on both faces. Boxes standing in the
                   air where there should be nothing at all.

                   A board on site has an arris, and what you see between two of
                   them is the shadow in that little bevel. So the board is drawn
                   back by half a joint on every edge and the gap IS the joint: no
                   extra object, nothing proud of the face, and the module reads
                   as a shadow line the way it does on a real wall. */
              }

              /* the course joint is the chamfer too -- the board above is already
                 drawn back by half of it, so there is nothing to add here */
            }
          }

          /* THE PLATES, SET OUT ALONG THE COURSE LINES.

             One origin for the whole wall, one step of half a board, and every
             line gets the same set of positions. A T-joint is where a vertical
             joint meets a horizontal one, and in a half bond those are half a
             board apart along every course line -- so this IS the T pattern,
             written as what it is instead of collected from whichever boards
             happened to offer a corner.

             Each one still has to pass the same two tests as before: it must have
             board under it at that height, and it must be clear of every opening.
             Nothing else decides. */
          /* TWO IN THE FIELD, ON THE THIRDS -- HIS CALL AND THE ARITHMETIC AGREES.

             The T-joints alone give one plate per 600 x 600, which is 2.8 per
             square metre. One in the middle of each board takes it to 4.2 and the
             makers ask for 6. Two on the thirds gives 5.6: still a shade under,
             and defensible on a low building with a light board, because it also
             means no part of a board is more than 300 mm from a fixing.

             They are set out from the module like everything else here, so they
             cannot drift against the joints the way the old per-board placement
             did. The edge zone -- where the pull is greatest and the number has to
             go up regardless -- is its own step and needs one decision from him:
             how wide. */
          var alq, apx, apy;
          for (alq = 0; alq < anchLines.length; alq++) {
            apy = anchLines[alq][0];
            var la = anchLines[alq][1], lb = anchLines[alq][2];
            var step = WALL_EPS.w / 2;
            var n0 = Math.ceil(la / step);
            for (apx = n0 * step; apx <= lb + 0.001; apx += step) {
              /* A PLATE SITS ON BOARD, THE WHOLE OF IT. A plate centred on the end
                 of a strip -- the wall's end, the corner return, the edge by an
                 opening -- had half of itself over nothing, which is the anchor he
                 kept finding half off the board. Its own radius plus 20 mm inside
                 every edge of the piece it fixes, and under the roof on both of
                 its sides, not just at its centre (Marek). */
              if (apx < la + anchR + 0.020 || apx > lb - anchR - 0.020) continue;
              /* board under it: the roof must still be above this line here */
              if (gProf && (gHead + gProf(apx)) < apy - 0.005) continue;
              if (gProf && ((gHead + gProf(apx - anchR)) < apy + anchR - 0.005 ||
                            (gHead + gProf(apx + anchR)) < apy + anchR - 0.005)) continue;

              var bad = false, oq9;
              for (oq9 = 0; oq9 < ops3.length; oq9++) {
                var q = ops3[oq9];
                var qa = q.at - q.w / 2 - (anchR + 0.020);
                var qb = q.at + q.w / 2 + (anchR + 0.020);
                var ql = s.y0 + q.sill - (anchR + 0.020);
                var qh = s.y0 + q.sill + q.h + (anchR + 0.020);
                if (apx > qa && apx < qb && apy > ql && apy < qh) { bad = true; break; }
              }
              if (bad) continue;

              var kk = s.id + ':' + Math.round(apx * 1000) + ':' + Math.round(apy * 1000);
              if (anchSeen[kk]) continue;
              anchSeen[kk] = 1;

              var pg = new T.Group();
              pg.add(new T.Mesh(anchGeo, anchMat));
              var hq2;
              for (hq2 = 0; hq2 < 8; hq2++) {
                var ha2 = hq2 * Math.PI / 4;
                var hm2 = new T.Mesh(holeGeo, anchHole);
                hm2.position.set(Math.cos(ha2) * anchR * 0.62, 0,
                                 Math.sin(ha2) * anchR * 0.62);
                pg.add(hm2);
              }
              pg.add(new T.Mesh(bossGeo, anchHole));
              pg.rotation.x = along ? Math.PI / 2 : 0;
              pg.rotation.z = along ? 0 : Math.PI / 2;
              pg.position.set(
                s.x0 + ux * apx + ox * (osbT + WALL_EPS.t - anchT / 2),
                apy,
                s.z0 + uz * apx + oz * (osbT + WALL_EPS.t - anchT / 2));
              pg.name = 'eps-anchor';
              g.add(pg);
              anchors++;

              if (gProf && Math.abs(apy - (s.y0 + s.h)) < 0.35) {
                anchDiag.push(Math.round(apx * 1000));
              }
            }
          }
          var afq;
          for (afq = 0; afq < anchField.length; afq++) {
            var fy = anchField[afq][0], fa = anchField[afq][1];
            var fb = anchField[afq][2], fs = anchField[afq][3];
            var t3;
            for (t3 = 1; t3 <= 2; t3++) {
              var fx = fs + WALL_EPS.w * (t3 / 3);
              while (fx < fa - 0.001) fx += WALL_EPS.w;
              for (; fx <= fb + 0.001; fx += WALL_EPS.w) {
                if (fx < fa + anchR + 0.020 || fx > fb - anchR - 0.020) continue;
                if (gProf && (gHead + gProf(fx)) < fy - 0.005) continue;
                if (gProf && ((gHead + gProf(fx - anchR)) < fy + anchR - 0.005 ||
                              (gHead + gProf(fx + anchR)) < fy + anchR - 0.005)) continue;

                var fbad = false, fq;
                for (fq = 0; fq < ops3.length; fq++) {
                  var qf = ops3[fq];
                  var fa2 = qf.at - qf.w / 2 - (anchR + 0.020);
                  var fb2 = qf.at + qf.w / 2 + (anchR + 0.020);
                  var fl2 = s.y0 + qf.sill - (anchR + 0.020);
                  var fh2 = s.y0 + qf.sill + qf.h + (anchR + 0.020);
                  if (fx > fa2 && fx < fb2 && fy > fl2 && fy < fh2) { fbad = true; break; }
                }
                if (fbad) continue;

                var fk = s.id + ':' + Math.round(fx * 1000) + ':' + Math.round(fy * 1000);
                if (anchSeen[fk]) continue;
                anchSeen[fk] = 1;

                var fg = new T.Group();
                fg.add(new T.Mesh(anchGeo, anchMat));
                var fh3;
                for (fh3 = 0; fh3 < 8; fh3++) {
                  var fa3 = fh3 * Math.PI / 4;
                  var fm3 = new T.Mesh(holeGeo, anchHole);
                  fm3.position.set(Math.cos(fa3) * anchR * 0.62, 0,
                                   Math.sin(fa3) * anchR * 0.62);
                  fg.add(fm3);
                }
                fg.add(new T.Mesh(bossGeo, anchHole));
                fg.rotation.x = along ? Math.PI / 2 : 0;
                fg.rotation.z = along ? 0 : Math.PI / 2;
                fg.position.set(
                  s.x0 + ux * fx + ox * (osbT + WALL_EPS.t - anchT / 2),
                  fy,
                  s.z0 + uz * fx + oz * (osbT + WALL_EPS.t - anchT / 2));
                fg.name = 'eps-anchor';
                g.add(fg);
                anchors++;
              }
            }
          }

          anchLines = [];
          anchField = [];
        }

        if (anchDiag.length) {
          ctx.log('WAL  eps anchors on the TRANSITION line ' + anchDiag.join(' ') +
                  ' -- every plate on that one course line, in mm along the wall. ' +
                  'They are struck from one origin at half a board, so a gap or a ' +
                  'pair touching is now impossible rather than something to hunt');
        }
        if (anchFail) {
          ctx.log('WAL  eps ANCHORS THREW -- ' + anchFail + '. The boards are drawn and ' +
                  'billed; the plates are not. This is caught on purpose: an anchor is a ' +
                  'disc, the EPS is the wall, and the wall must not depend on it');
        }
        /* THE REVEALS: a strip of the same board on each jamb and on the head,
           WALL_EPS_REV thick, from the frame's outer face to the face of the EPS.
           Glued, no plates (nothing to plate through), billed as board and foam. */
        var revEps = 0, revN = 0, revDoors = 0, iR, qR;
        var mRev = new T.MeshStandardMaterial({
          color: _wallNew('eps-rev') ? MAGENTA : 0xF0EFE6, roughness: 0.98, metalness: 0.0 });
        for (iR = 0; iR < SEG.length; iR++) {
          var sR = SEG[iR];
          if (!(sR.len > 0.2) || !(sR.h > 0.2)) continue;
          var uxR = (sR.x1 - sR.x0) / sR.len, uzR = (sR.z1 - sR.z0) / sR.len;
          var oxR = sR.nx, ozR = sR.nz;
          var alongR = Math.abs(uxR) > Math.abs(uzR);
          var opsR = sR.openings || [];
          var backR = _revealBack();                      /* frame outer face, from the plan line */
          var epsFaceR = osbT + WALL_EPS.t;               /* outer face of the board */
          var depRv = epsFaceR - backR;
          var cNR = (epsFaceR + backR) / 2;
          for (qR = 0; qR < opsR.length; qR++) {
            var oR = opsR[qR];
            var oWR = oR.w, oY0R = sR.y0 + oR.sill, oY1R = oY0R + oR.h;
            if (!(oWR > 0.1) || !(oY1R - oY0R > 0.1)) continue;
            if (!(oR.sill > 0.05)) revDoors++;
            var jY0R = oY0R + (oR.sill > 0.05 ? 0.010 : 0.001), jY1R = oY1R - WALL_EPS_REV;
            var jHR = jY1R - jY0R, jqR;
            for (jqR = 0; jqR < 2; jqR++) {
              var sgR = jqR ? 1 : -1;
              var atR = oR.at + sgR * (oWR / 2 - WALL_EPS_REV / 2);
              var jmR = new T.Mesh(
                new T.BoxGeometry(alongR ? WALL_EPS_REV : depRv, jHR, alongR ? depRv : WALL_EPS_REV), mRev);
              jmR.position.set(sR.x0 + uxR * atR + oxR * cNR, (jY0R + jY1R) / 2,
                               sR.z0 + uzR * atR + ozR * cNR);
              jmR.name = 'eps-reveal';
              g.add(jmR);
              revEps += jHR * depRv;
            }
            var hmR = new T.Mesh(
              new T.BoxGeometry(alongR ? oWR : depRv, WALL_EPS_REV, alongR ? depRv : oWR), mRev);
            hmR.position.set(sR.x0 + uxR * oR.at + oxR * cNR, oY1R - WALL_EPS_REV / 2,
                             sR.z0 + uzR * oR.at + ozR * cNR);
            hmR.name = 'eps-reveal';
            g.add(hmR);
            revEps += oWR * depRv;
            revN++;
          }
        }
        if (revN) {
          ctx.log('WAL  eps reveals ' + revN + ' opening(s) (' + revDoors + ' doors): a ' +
                  Math.round(WALL_EPS_REV * 1000) + ' mm strip of ' + WALL_EPS.code +
                  ' on both jambs and the head, ' + Math.round((osbT + WALL_EPS.t - _revealBack()) * 1000) +
                  ' mm deep from the frame face to the board face, ' + API.fmtArea(revEps) +
                  ' -- glued, no plates; the coat, the render and the reveal beads sit on it');
        }

        if (!boards) { ctx.log('WAL  eps none -- no wall face'); return; }
        area += revEps;
        var foam = Math.ceil(area * WALL_EPS.foamPerM2);
        /* WHAT IS DRAWN IS WHAT IS BILLED, AND THE PLATES ARE COUNTED WHERE THEY
           ARE PLACED. anchors is incremented once per plate that survives every
           test -- the roof, the openings, the de-duplication -- so the row is the
           number of holes drilled and not a rate multiplied by an area. */
        var anch = anchors;
        _add(LAYER, WALL_EPS.code, area);
        _add(LAYER, WALL_EPS.foam, foam);
        _add(LAYER, WALL_EPS.anchor, anch);
        _add(LAYER, WALL_EPS.track, trackM);
        ctx.log('WAL  eps ' + boards + ' piece(s) from ' + whole + ' board(s) of ' +
                Math.round(WALL_EPS.w * 1000) + 'x' + Math.round(WALL_EPS.h * 1000) +
                'x' + Math.round(WALL_EPS.t * 1000) + ', ' + API.fmtArea(area) + ' ' +
                WALL_EPS.code + ', courses offset half a board, ' + jn3 +
                ' board(s) with a chamfered edge instead of a joint object' +
                (jBold ? ', drawn wide for this view' : '') + ', run carried ' +
                Math.round(WALL_EPS.t * 1000) + ' mm past the corner to butt');
        ctx.log('WAL  eps ' + foam + ' x ' + WALL_EPS.foam + ' and ' + anch + ' x ' +
                WALL_EPS.anchor + ' at ' + WALL_EPS.anchorPerBoard +
                ' a board, DRAWN and de-duplicated at the joints (' +
                Math.round(anch / Math.max(area, 0.01) * 10) / 10 +
                '/m2 -- the recommendation is 6), plus ' +
                Math.round(trackM * 10) / 10 + ' m ' + WALL_EPS.track);
        ctx.log('WAL  eps anchors clear ' + Math.round((anchR + 0.020) * 1000) +
                ' mm from each end of the piece so none hangs over a reveal; the T ' +
                'plates sit ON the joint, which is what they hold, and only the field ' +
                'plate has to stand clear of the courses. No field plate on a piece ' +
                'under 400 tall -- there its middle is a hand from the joint above it');
        ctx.log('WAL  eps anchors a T plate only where the edge is a COURSE line; an ' +
                'edge cut round an opening has no board on the far side to hold, so it ' +
                'gets none -- that is what was putting plates in the reveals and on ' +
                'the sills');
        ctx.log('EPS  ' + cutN + ' board(s) cut by the roof on the rake -- each one ' +
                'is the whole board clipped by the roof line, corners listed above; x is ' +
                'along the wall from its start, y is above the wall head, mm');
        ctx.log('WAL  eps BILLED: ' + WALL_EPS.code + ' ' + API.fmtArea(area) + ', ' +
                WALL_EPS.foam + ' ' + foam + ', ' + WALL_EPS.anchor + ' ' + anch +
                ' (every plate drawn is one billed), ' + WALL_EPS.track + ' ' +
                Math.round(trackM * 10) / 10 + ' m');
        ctx.log('WAL  eps anchors a protection zone round every opening -- the hole ' +
                'grown by the plate radius plus ' + Math.round(0.020 * 1000) + ' mm, and ' +
                'nothing may sit in it. It is checked last and overrules the bond, ' +
                'because a plate half over a reveal holds nothing at all');
        ctx.log('WAL  eps anchors on the T-JOINTS -- the middles of the top and bottom ' +
                'edges, where the course above breaks over this board and one plate ' +
                'holds three. A field plate only on a board over half size, and nothing ' +
                'on the side edges, so an offcut under a window no longer collects more ' +
                'fixings than a whole board');
        ctx.log('WAL  eps bond HALF A BOARD per course, so a vertical joint always lands ' +
                'over the MIDDLE of the board below -- which is where the top and bottom ' +
                'edge anchors already sit, so each of those is a T and holds THREE ' +
                'boards; the two side ones hold two and only the field one holds a ' +
                'single board');
        _why(ctx, 'wall     foam and anchors are both billed because neither does the ' +
                'other job: the foam grabs the board and closes the joint, the anchors ' +
                'are what keep it there in a gale');
      }

      function _wallMesh(ctx) {
        if (!_wallStepOn(9)) return;
        if (!_cladOff()) return;
        if (!_wallNet()) {
          ctx.log('WAL  mesh HIDDEN -- WALL_SHOW is eps, so the boards, their joints ' +
                  'and the plates in those joints are what is left to look at');
          return;
        }
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, c;
        /* adu-v1430 (Marek: kontaktni fasadou prosvita sklotextilni armovaci
           tkanina; sterky a omitky musi byt nad). The net lies 3.5 mm off the
           board inside a 5.5 mm coat with 6 mm of render over that -- right in
           the drawing, and still it came through: on a tablet's coarse depth
           buffer a few millimetres at ten metres is one step, and a see-through
           net drawn after the walls wins that step. So once the coat is drawn
           the net is buried for good and is not drawn at all; it is drawn only
           in the steps that stop at it. Measured and billed the same either
           way. */
        var gM = _wallWet() ? { add: function () {} } : g;
        if (_wallWet()) ctx.log('WAL  mesh under the coat -- billed, not drawn (adu-v1430)');
        /* THE COLOUR IS ON THE STRANDS AND NOWHERE ELSE. */
        var mtex = _wallNew('mesh') ? null
          : _membTex(T, 'mesh', false, '#1A1A1A', 'rgba(196,44,36,0.95)');
        var TILE = WALL_MESH.roll - WALL_MESH.lap;
        /* THE LAP IS A SECOND LAYER OF MESH, NOT A RED BAR.

           Drawn as a solid stripe it read as a painted band across the wall, which
           is what he is asking about -- there is no such thing on a facade. What
           is actually there is 100 mm where two nets lie on top of each other, so
           it is drawn as exactly that: the same net again, offset a hair, and the
           doubling is what makes the line. */
        function lapMatFor(len, hgt) {
          if (!mtex) {
            return new T.MeshStandardMaterial({
              color: MAGENTA, roughness: 0.95, metalness: 0.0, side: T.DoubleSide });
          }
          var lt = mtex.clone();
          lt.needsUpdate = true;
          lt.wrapS = lt.wrapT = T.RepeatWrapping;
          lt.repeat.set(len / TILE, hgt / TILE);
          return new T.MeshStandardMaterial({
            map: lt, roughness: 0.95, metalness: 0.0, side: T.DoubleSide,
            transparent: true, depthWrite: false });
        }

        var baseT = 0.018 + 0.001 + WALL_EPS.t + 0.0035;
        var mt = WALL_MESH.t, runW = TILE, drawn = 0, pieces = 0, laps = 0, lapsTrim = 0;

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2) || !(s.h > 0.2)) continue;
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var ops4 = s.openings || [], oq4;

          /* ONE NET FROM THE TRACK TO THE ROOF. The courses used to stop at the
             wall head and the gable skin laid its own net above it, on its own
             coursing -- two objects, two laps that never lined through, and a
             seam at the storey line. The coursing carries on past the head to
             whatever the roof leaves above this wall, and a piece the roof
             crosses is the whole piece cut by the roof line (_underRoof), as the
             EPS boards under it already are. */
          var Gm4 = null;
          try { Gm4 = _wallSkin(s); } catch (eM4) { Gm4 = null; }
          var head4 = s.y0 + s.h;
          var top4 = head4 + (Gm4 ? Math.max(0, Gm4.rise) : 0);
          var roofY4 = function (u) { return Gm4 ? (head4 + Gm4.hAt(u)) : head4; };
          var fwd4 = (oz * ux - ox * uz) > 0;
          var upN4 = 0, upA4 = 0;

          var kMax4 = Math.ceil((top4 - s.y0) / runW), kq4;
          for (kq4 = 0; kq4 < kMax4; kq4++) {
            var cLo = s.y0 + runW * kq4;
            var cHi = Math.min(cLo + runW, top4);
            if (!(cHi - cLo > 0.02)) continue;

            var strips = _battenRuns(s, cLo + 0.001, cHi - 0.001);
            for (oq4 = 0; oq4 < ops4.length; oq4++) {
              var oLo4 = s.y0 + ops4[oq4].sill, oHi4 = oLo4 + ops4[oq4].h;
              var oa4 = ops4[oq4].at - ops4[oq4].w / 2;
              var ob4 = ops4[oq4].at + ops4[oq4].w / 2;
              if (oHi4 > cLo + 0.005 && oHi4 < cHi - 0.005) strips.push([oa4, ob4, oHi4, cHi]);
              if (oLo4 > cLo + 0.005 && oLo4 < cHi - 0.005) strips.push([oa4, ob4, cLo, oLo4]);
            }

            for (c = 0; c < strips.length; c++) {
              var a2 = Math.max(0, strips[c][0]), b2 = Math.min(s.len, strips[c][1]);
              /* AND ROUND THE CORNER, like the board and the coat over it. The net
                 stopped on the plan corner while the EPS carried 140 past it, so
                 the last stretch of board at every corner had render on it and no
                 reinforcement under the render -- the one place that must never
                 happen. */
              if (b2 >= s.len - 0.01) b2 = s.len + WALL_EPS.t;
              var yA = (strips[c].length > 2) ? strips[c][2] : cLo;
              var yB2 = (strips[c].length > 3) ? strips[c][3] : cHi;
              var L = b2 - a2, ph = yB2 - yA;
              /* AND NOTHING GETS TO CROSS AN OPENING, WHATEVER THE STRIPS SAY.

                 One window out of four -- his words, and the clue I kept stepping
                 over. A fault in the geometry would show on all four; a fault in a
                 TEST shows on the one opening whose edges happen to fall the wrong
                 side of it. The strip builder above splits a course at a head or a
                 sill only when the split lands more than 5 mm inside the course,
                 so an opening whose head sits within that of a course line keeps
                 its full-width piece and the mesh runs straight across the hole.

                 Rather than move the tolerance and hope, the piece is checked
                 against the openings here, where it is about to be drawn. If it
                 overlaps one it is cut into the parts that do not, and those go
                 back on the list to be drawn instead. A test that asks the actual
                 question cannot be defeated by where a window happens to sit. */
              var segs4 = [[a2, b2]], oc4;
              for (oc4 = 0; oc4 < ops4.length; oc4++) {
                var o4 = ops4[oc4];
                var y0o = s.y0 + o4.sill, y1o = y0o + o4.h;
                if (yB2 <= y0o + 0.001 || yA >= y1o - 0.001) continue;
                var xa4 = o4.at - o4.w / 2, xb4 = o4.at + o4.w / 2;
                var next4 = [], sq4;
                for (sq4 = 0; sq4 < segs4.length; sq4++) {
                  var pa4 = segs4[sq4][0], pb4 = segs4[sq4][1];
                  if (xb4 <= pa4 + 0.001 || xa4 >= pb4 - 0.001) {
                    next4.push([pa4, pb4]); continue;
                  }
                  if (xa4 > pa4 + 0.001) next4.push([pa4, xa4]);
                  if (xb4 < pb4 - 0.001) next4.push([xb4, pb4]);
                }
                segs4 = next4;
              }
              if (segs4.length !== 1 ||
                  Math.abs(segs4[0][0] - a2) > 0.001 ||
                  Math.abs(segs4[0][1] - b2) > 0.001) {
                var rq4;
                for (rq4 = 0; rq4 < segs4.length; rq4++) {
                  if (segs4[rq4][1] - segs4[rq4][0] > 0.05) {
                    strips.push([segs4[rq4][0], segs4[rq4][1], yA, yB2]);
                  }
                }
                continue;
              }

              if (!(L > 0.05) || !(ph > 0.02)) continue;
              var mu = a2 + L / 2;

              /* ABOVE THE HEAD THE PIECE IS CUT BY THE ROOF: the band from a2 to
                 b2 between yA and yB2, clipped by the roof line, extruded, with
                 the same print anchored the same way the boxes below anchor it. */
              if (Gm4 && yB2 > head4 + 0.005) {
                var polyM = _underRoof(a2, b2, yA, yB2, roofY4, [Gm4.at]);
                var aM = polyM.length >= 3 ? _polyArea(polyM) : 0;
                if (!(aM > 0.002)) continue;
                var e0M = fwd4 ? a2 : b2, kM;
                var shM = new T.Shape();
                for (kM = 0; kM < polyM.length; kM++) {
                  var lxM = fwd4 ? (polyM[kM][0] - a2) : (b2 - polyM[kM][0]);
                  if (kM === 0) shM.moveTo(lxM, polyM[kM][1] - yA);
                  else shM.lineTo(lxM, polyM[kM][1] - yA);
                }
                shM.lineTo(fwd4 ? (polyM[0][0] - a2) : (b2 - polyM[0][0]), polyM[0][1] - yA);
                var pmM;
                if (mtex) {
                  var txM = mtex.clone();
                  txM.needsUpdate = true;
                  txM.wrapS = txM.wrapT = T.RepeatWrapping;
                  txM.repeat.set(1 / TILE, 1 / TILE);
                  txM.offset.set((fwd4 ? a2 : (s.len - b2)) / TILE, (yA - cLo) / TILE);
                  pmM = new T.MeshStandardMaterial({
                    map: txM, roughness: 0.95, metalness: 0.0, side: T.FrontSide,
                    transparent: true, depthWrite: false });
                } else {
                  pmM = new T.MeshStandardMaterial({
                    color: MAGENTA, roughness: 0.95, metalness: 0.0, side: T.DoubleSide });
                }
                var mmM = new T.Mesh(
                  new T.ExtrudeGeometry(shM, { depth: mt, bevelEnabled: false }), pmM);
                mmM.rotation.y = Math.atan2(ox, oz);
                mmM.position.set(s.x0 + ux * e0M + ox * baseT, yA, s.z0 + uz * e0M + oz * baseT);
                mmM.name = 'wall-mesh';
                gM.add(mmM);
                drawn += aM;
                pieces++;
                upN4++; upA4 += aM;

                /* and its lap, the top 100 of the same shape, cut the same way */
                if (cHi < top4 - 0.005) {
                  var lapM = _underRoof(a2, b2, Math.max(yA, cHi - WALL_MESH.lap), cHi, roofY4, [Gm4.at]);
                  if (lapM.length >= 3 && _polyArea(lapM) > 0.001) {
                    var shL = new T.Shape(), kL, yL0 = Math.max(yA, cHi - WALL_MESH.lap);
                    for (kL = 0; kL < lapM.length; kL++) {
                      var lxL = fwd4 ? (lapM[kL][0] - a2) : (b2 - lapM[kL][0]);
                      if (kL === 0) shL.moveTo(lxL, lapM[kL][1] - yL0);
                      else shL.lineTo(lxL, lapM[kL][1] - yL0);
                    }
                    shL.lineTo(fwd4 ? (lapM[0][0] - a2) : (b2 - lapM[0][0]), lapM[0][1] - yL0);
                    var lmM = new T.Mesh(
                      new T.ExtrudeGeometry(shL, { depth: mt * 0.6, bevelEnabled: false }),
                      mtex ? pmM : lapMatFor(L, WALL_MESH.lap));
                    lmM.rotation.y = Math.atan2(ox, oz);
                    lmM.position.set(s.x0 + ux * e0M + ox * (baseT + mt + mt * 0.3), yL0,
                                     s.z0 + uz * e0M + oz * (baseT + mt + mt * 0.3));
                    lmM.name = 'wall-mesh-lap';
                    gM.add(lmM);
                    laps++;
                  }
                }
                continue;
              }

              var pm;
              if (mtex) {
                var tx2 = mtex.clone();
                tx2.needsUpdate = true;
                tx2.wrapS = tx2.wrapT = T.RepeatWrapping;
                var flip = (Math.abs(ux) > Math.abs(uz))
                  ? ((oz > 0 ? 1 : -1) !== (ux > 0 ? 1 : -1))
                  : ((ox > 0 ? -1 : 1) !== (uz > 0 ? 1 : -1));
                tx2.repeat.set(L / TILE, ph / TILE);
                tx2.offset.set((flip ? (s.len - b2) : a2) / TILE, (yA - cLo) / TILE);
                /* FRONT FACE ONLY. On a double-sided box you see the print on the
                   near face AND, through the holes, the mirrored one on the far
                   face -- two nets and two sets of letters where there is one. */
                pm = new T.MeshStandardMaterial({
                  map: tx2, roughness: 0.95, metalness: 0.0, side: T.FrontSide,
                  transparent: true, depthWrite: false });
              } else {
                pm = new T.MeshStandardMaterial({
                  color: MAGENTA, roughness: 0.95, metalness: 0.0, side: T.DoubleSide });
              }
              var mm = new T.Mesh(
                new T.BoxGeometry(Math.abs(ux) > Math.abs(uz) ? L : mt, ph,
                                  Math.abs(ux) > Math.abs(uz) ? mt : L), pm);
              mm.position.set(
                s.x0 + ux * mu + ox * (baseT + mt / 2),
                (yA + yB2) / 2,
                s.z0 + uz * mu + oz * (baseT + mt / 2));
              mm.name = 'wall-mesh';
              gM.add(mm);
              drawn += L * ph;
              pieces++;

              if (yB2 < cHi - 0.005 || cHi >= top4 - 0.005) continue;
              /* THE LAP IS THE TOP OF THIS PIECE. IT IS NOT A BAND HUNG OFF THE
                 COURSE LINE.

                 It was drawn a full 100 downwards from cHi whatever was under it.
                 Over a head the piece is only what is left of the course above the
                 opening -- thirty millimetres on the window he found -- so the
                 other seventy hung straight down into the hole. That is the strip
                 of textile over one opening, and it is why it was one and not the
                 other four: their heads leave a piece taller than the lap, so the
                 band lands inside it and nobody sees anything.

                 The scan says the same thing in numbers: wall-mesh-lap, 892 wide,
                 66 mm down into a 900 window whose head is 30 below the course.

                 A doubling can only exist where there is net underneath it to
                 double. So it is cut to the piece, and a piece too short to carry
                 a lap does not get one at all. The magenta that marked the cut
                 bands is off again -- he has seen them and they are right. */
              var lapH = Math.min(WALL_MESH.lap, yB2 - yA);
              if (!(lapH > 0.005)) continue;
              if (lapH < WALL_MESH.lap - 0.0005) lapsTrim++;
              var lm = new T.Mesh(
                new T.BoxGeometry(Math.abs(ux) > Math.abs(uz) ? L : mt * 0.6,
                                  lapH,
                                  Math.abs(ux) > Math.abs(uz) ? mt * 0.6 : L),
                lapMatFor(L, lapH));
              lm.position.set(
                s.x0 + ux * mu + ox * (baseT + mt + mt * 0.3),
                cHi - lapH / 2,
                s.z0 + uz * mu + oz * (baseT + mt + mt * 0.3));
              lm.name = 'wall-mesh-lap';
              gM.add(lm);
              laps++;
            }
          }
          if (Gm4 && top4 > head4 + 0.02) {
          }
        }

        if (!pieces) { ctx.log('WAL  mesh none -- no wall face'); return; }
        var bought = drawn * WALL_MESH.roll / (WALL_MESH.roll - WALL_MESH.lap);
        _add(LAYER, WALL_MESH.code, bought);
        ctx.log('WAL  mesh ' + pieces + ' piece(s), drawn ' + API.fmtArea(drawn) +
                ', bought ' + API.fmtArea(bought) + ' ' + WALL_MESH.code + ' on a ' +
                Math.round(WALL_MESH.roll * 1000) + ' roll lapped ' +
                Math.round(WALL_MESH.lap * 1000) + ', ' + laps + ' lap band(s), ' +
                lapsTrim + ' of them CUT to a short piece over a head -- a lap ' +
                'hangs off the piece it doubles, not off the course line');
        ctx.log('WAL  mesh tex=' + (mtex ? 'built' : 'NULL -- magenta fallback') +
                ' [' + WALL_TEX_LOG.join(' | ') + '], strand drawn 6px on a ' +
                Math.round(TILE * 1000) + ' mm tile at 30 lines, transparent ' +
                'background so the EPS shows between the holes');
        _why(ctx, 'wall     the mesh is not a covering, it is what stops the render ' +
                'cracking where two boards move -- so the LAP is the whole point of it');
      }

      function _wallCoat(ctx) {
        if (!_wallStepOn(9)) return;
        if (!_cladOff()) return;
        if (!_wallWet()) {
          ctx.log('WAL  base coat HIDDEN -- WALL_SHOW is ' + WALL_SHOW + ', so the EPS, ' +
                  'its joints, the mesh and the anchors can be seen with nothing over them');
          return;
        }
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, c, q6, jq6;
        var mat = new T.MeshStandardMaterial({
          color: _wallNew('coat') ? MAGENTA : 0xCFCCC2,
          roughness: 0.96, metalness: 0.0 });

        /* A HAIR OFF THE BOARD, AND THICK ENOUGH TO SWALLOW THE MESH.

           Started exactly on the face of the EPS the coat and the board shared a
           plane and flickered -- the same fault as every other film in this wall.
           And at a flat 5 it finished level with the top of the mesh rather than
           over it, so the net kept showing through the render. Half a millimetre
           off the board, five and a half thick: nothing shares a face and the
           outer surface is clear of everything underneath. */
        var epsFace = 0.018 + 0.001 + WALL_EPS.t;
        var coatFace = epsFace + WALL_COAT.gap + WALL_COAT.t;
        /* the whole width of the reveal: this layer's own face, back to the frame */
        var revD = coatFace - _revealBack();
        if (!(revD > 0.02)) revD = WALL_EPS.t;
        var area = 0, revA = 0, pieces = 0;

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2) || !(s.h > 0.2)) continue;
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var along = Math.abs(ux) > Math.abs(uz);
          var FC = _wallFaces(s);

          for (c = 0; c < FC.length; c++) {
            /* AND IT RUNS TO THE CORNER, BECAUSE THE BOARD DOES.

               The faces stop on the plan corner, but the EPS was deliberately
               carried one board thickness past it so the two walls butt. The coat
               was still stopping at the plan corner, so the last 140 mm of board,
               the bead and its wings had no render on them at all -- which is the
               uncovered corner he can see. Same extension, same reason, same end:
               every wall does it once and the polygon runs one way. */
            var cA = FC[c].a, cB = FC[c].b;
            var extC = epsFace + WALL_COAT.gap + WALL_COAT.t;
            if (cB >= s.len - 0.01) cB = s.len + extC;
            /* AND BACK ROUND THE NEAR CORNER AS WELL.

               Extending only the far end covers one side of every corner and
               leaves the other bare -- which is the side he can still see mesh on.
               A troweled coat turns both ways at a corner; the far extension was
               enough for the BOARD only because boards butt and coat does not.

               The near extension is DRAWN and NOT BILLED. It lands on the corner
               the neighbour's far extension already covered, so it is the same
               coat seen from the other side, and coat bought twice is coat bought
               wrong. The billed length stays the run between the two plan
               corners. */
            var drawA = (cA <= 0.01) ? -extC : cA;
            var L = cB - drawA, Lbill = cB - cA;
            var H = FC[c].yHi - FC[c].yLo;
            if (!(L > 0.03) || !(H > 0.03)) continue;
            var cm = new T.Mesh(
              new T.BoxGeometry(along ? L : WALL_COAT.t, H,
                                along ? WALL_COAT.t : L), mat);
            cm.position.set(
              s.x0 + ux * (drawA + L / 2) + ox * (epsFace + WALL_COAT.gap + WALL_COAT.t / 2),
              (FC[c].yLo + FC[c].yHi) / 2,
              s.z0 + uz * (drawA + L / 2) + oz * (epsFace + WALL_COAT.gap + WALL_COAT.t / 2));
            cm.name = 'eps-base-coat';
            g.add(cm);
            area += Lbill * H;
            pieces++;
          }

          /* AND ON OVER THE HEAD TO THE ROOF, THE SAME COAT.

             The gable skin used to trowel its own coat above the head in 400 mm
             courses -- a second object on a second set-out. One band from the
             head to the roof line, corner return to corner return, cut by the
             roof (_underRoof), on the same face at the same offset. Billed
             between the plan corners plus the far return, as the faces below are:
             the near return is drawn and not billed. */
          var Gc6 = null;
          try { Gc6 = _wallSkin(s); } catch (eC6) { Gc6 = null; }
          /* A PARAPET IS RENDERED TOO. Its band was skipped here and in the
             render below, so on a flat roof the coat and the render stopped at
             the ceiling line and the EPS stood bare up to the coping (Marek).
             The profile is level, so the piece is simply a rectangle. */
          if (Gc6 && Gc6.rise > 0.02) {
            var head6 = s.y0 + s.h, top6 = head6 + Gc6.rise;
            var roofY6 = function (u) { return head6 + Gc6.hAt(u); };
            var extC6 = epsFace + WALL_COAT.gap + WALL_COAT.t;
            var polyC = _underRoof(-extC6, s.len + extC6, head6, top6, roofY6, [Gc6.at]);
            var aDraw6 = polyC.length >= 3 ? _polyArea(polyC) : 0;
            if (aDraw6 > 0.002) {
              var fwd6 = (oz * ux - ox * uz) > 0, e06 = fwd6 ? -extC6 : (s.len + extC6), k6;
              var shC = new T.Shape();
              for (k6 = 0; k6 < polyC.length; k6++) {
                var lx6 = fwd6 ? (polyC[k6][0] + extC6) : (s.len + extC6 - polyC[k6][0]);
                if (k6 === 0) shC.moveTo(lx6, polyC[k6][1] - head6);
                else shC.lineTo(lx6, polyC[k6][1] - head6);
              }
              shC.lineTo(fwd6 ? (polyC[0][0] + extC6) : (s.len + extC6 - polyC[0][0]), polyC[0][1] - head6);
              var cmU = new T.Mesh(
                new T.ExtrudeGeometry(shC, { depth: WALL_COAT.t, bevelEnabled: false }), mat);
              cmU.rotation.y = Math.atan2(ox, oz);
              cmU.position.set(s.x0 + ux * e06 + ox * (epsFace + WALL_COAT.gap), head6,
                               s.z0 + uz * e06 + oz * (epsFace + WALL_COAT.gap));
              cmU.name = 'eps-base-coat';
              g.add(cmU);
              var polyB = _underRoof(0, s.len + extC6, head6, top6, roofY6, [Gc6.at]);
              var aBill6 = polyB.length >= 3 ? _polyArea(polyB) : 0;
              area += aBill6;
              pieces++;
            }
          }

          /* and into every reveal: two jambs and a soffit, the depth of the board */
          var ops6 = s.openings || [];
          for (q6 = 0; q6 < ops6.length; q6++) {
            var oW = ops6[q6].w;
            var oY0 = s.y0 + ops6[q6].sill, oY1 = oY0 + ops6[q6].h;
            var oH = oY1 - oY0;
            if (!(oH > 0.05) || !(oW > 0.05)) continue;
            var rd = revD;

            /* THE END OF THE REVEAL STRIP IS FACE, AND IT IS COATED. The strip
               stands 30 mm inside the opening line; its outer end is 30 mm of
               board face level with the wall, and the face pieces stop at the
               opening line -- so the arris the bead sits on had no coat under it
               and the bead hung over bare board. Three small pieces per opening:
               both jambs and the head, on the face plane. */
            var capC = WALL_EPS_REV, jqC;
            for (jqC = 0; jqC < 2; jqC++) {
              var atC = ops6[q6].at + (jqC ? 1 : -1) * (oW / 2 - capC / 2);
              var cmC = new T.Mesh(
                new T.BoxGeometry(along ? capC : WALL_COAT.t, oH,
                                  along ? WALL_COAT.t : capC), mat);
              cmC.position.set(s.x0 + ux * atC + ox * (coatFace - WALL_COAT.t / 2),
                               oY0 + oH / 2, s.z0 + uz * atC + oz * (coatFace - WALL_COAT.t / 2));
              cmC.name = 'eps-base-coat';
              g.add(cmC);
              revA += oH * capC;
            }
            var chC = new T.Mesh(
              new T.BoxGeometry(along ? oW : WALL_COAT.t, capC, along ? WALL_COAT.t : oW), mat);
            chC.position.set(s.x0 + ux * ops6[q6].at + ox * (coatFace - WALL_COAT.t / 2),
                             oY1 - capC / 2, s.z0 + uz * ops6[q6].at + oz * (coatFace - WALL_COAT.t / 2));
            chC.name = 'eps-base-coat';
            g.add(chC);
            revA += oW * capC;

            /* AND THE REVEAL FACES, ON THEM RATHER THAN THROUGH THEM.

               The jamb pieces were centred on the jamb LINE, so half of each one
               was inside the board and half in the opening -- a plate straddling
               the face it is supposed to be troweled onto. It goes on the face:
               half a coat thickness into the opening, which is where a trowel can
               reach. Same for the soffit. */
            for (jq6 = 0; jq6 < 2; jq6++) {
              var sgn6 = jq6 ? -1 : 1;
              var at6 = ops6[q6].at + (jq6 ? oW / 2 : -oW / 2)
                        + sgn6 * (WALL_EPS_REV + WALL_COAT.t / 2 + WALL_COAT.gap);
              var jm = new T.Mesh(
                new T.BoxGeometry(along ? WALL_COAT.t : rd, oH,
                                  along ? rd : WALL_COAT.t), mat);
              jm.position.set(
                s.x0 + ux * at6 + ox * (coatFace - rd / 2),
                (oY0 + oY1) / 2,
                s.z0 + uz * at6 + oz * (coatFace - rd / 2));
              jm.name = 'eps-base-coat';
              g.add(jm);
              revA += oH * rd;
              pieces++;
            }

            var sm = new T.Mesh(
              new T.BoxGeometry(along ? (oW - 2 * WALL_EPS_REV) : rd, WALL_COAT.t,
                                along ? rd : (oW - 2 * WALL_EPS_REV)), mat);
            sm.position.set(
              s.x0 + ux * ops6[q6].at + ox * (coatFace - rd / 2),
              oY1 - WALL_EPS_REV - WALL_COAT.t / 2 - WALL_COAT.gap,
              s.z0 + uz * ops6[q6].at + oz * (coatFace - rd / 2));
            sm.name = 'eps-base-coat';
            g.add(sm);
            revA += oW * rd;
            pieces++;
          }
        }

        if (!pieces) { ctx.log('WAL  base coat none'); return; }

        /* the unit is whatever the catalogue says this code is sold in */
        var tot = area + revA;
        var unit = (__CORE.CATALOG[WALL_COAT.code] && __CORE.CATALOG[WALL_COAT.code].unit) || 'm2';
        var byWeight = (unit === 'kg' || unit === 'lb' || unit === 'lbs');
        _add(LAYER, WALL_COAT.code, byWeight ? (tot * WALL_COAT.kgPerM2) : tot);

        ctx.log('WAL  base coat ' + pieces + ' piece(s), ' + API.fmtArea(area) +
                ' on the elevations + ' + API.fmtArea(revA) + ' round the reveals = ' +
                API.fmtArea(tot) + ' of ' + Math.round(WALL_COAT.t * 1000) + ' mm ' +
                WALL_COAT.code + ' -- drawn ' + Math.round(WALL_EPS.t * 1000) +
                ' mm round BOTH sides of every corner, billed once: the near return ' +
                'is the same coat the neighbour already carried');
        ctx.log('WAL  base coat billed in ' + unit + ' -- ' +
                (byWeight
                  ? (Math.round(tot * WALL_COAT.kgPerM2) + ' at ' + WALL_COAT.kgPerM2 +
                     ' per m2 over the mesh')
                  : 'the sheet sells it by area, so no conversion') +
                '; change the row unit and this follows it without a code change');
        ctx.log('WAL  base coat reveals ' + Math.round(revD * 1000) + ' mm deep -- the ' +
                'WHOLE width of the reveal, from the coat own face back to the OUTER ' +
                'face of the frame, which the joinery stands on the middle of a ' +
                Math.round(API.wallThickness() * 1000) + ' wall. It used to return ' +
                Math.round(WALL_EPS.t * 1000) + ', the thickness of the board, and ' +
                'left a bare band down every jamb and across every head');
        ctx.log('WAL  base coat buries the mesh and the beads: everything under it was ' +
                'pulled inside ' + Math.round(WALL_COAT.t * 1000) + ' mm, because a bead ' +
                'standing proud of the render is a defect and not a detail');
        _why(ctx, 'wall     the reveal returns are troweled by hand and a takeoff that ' +
                'measures only the elevations misses every one of them');
      }

      function _wallRender(ctx) {
        if (!_wallStepOn(9)) return;
        if (!_cladOff()) return;
        if (!_wallDry()) { ctx.log('WAL  render HIDDEN -- WALL_SHOW is ' + WALL_SHOW); return; }
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, c, q7, jq7;
        var pMat = new T.MeshStandardMaterial({
          color: _wallNew('render') ? MAGENTA : 0xD9D6CC,
          roughness: 0.99, metalness: 0.0 });
        var rMat = new T.MeshStandardMaterial({
          /* GREY, ON HIS CALL. The near-white render was the same value as the
             base coat and the EPS under it, so there was nothing to judge -- three
             layers and one colour. A mid grey separates it until the colour
             choices are a real setting rather than a guess. */
          color: _wallNew('render') ? MAGENTA : 0xFFFFFF,
          map: _wallNew('render') ? null : (function () {
            var tx = _grainTex(T, 'render', colorOf('facade'));
            if (tx) { tx = tx.clone(); tx.needsUpdate = true; tx.wrapS = tx.wrapT = T.RepeatWrapping; tx.repeat.set(3, 3); }
            return tx;
          })(),
          roughness: 0.93, metalness: 0.0 });
        var plMat = new T.MeshStandardMaterial({
          color: _wallNew('render') ? MAGENTA : 0x8C857A,
          roughness: 0.88, metalness: 0.0 });

        var coatFace = 0.018 + 0.001 + WALL_EPS.t + WALL_COAT.gap + WALL_COAT.t;
        var rendFace = coatFace + WALL_REND.pt + WALL_REND.t;
        /* the whole width of the reveal, taken back from THIS layer's face -- so
           in the reveal the render lies on the coat exactly as it does outside */
        var revD = rendFace - _revealBack();
        if (!(revD > 0.02)) revD = WALL_EPS.t;
        var primA = 0, rendA = 0, pliA = 0, revA = 0, pieces = 0;

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2) || !(s.h > 0.2)) continue;
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var along = Math.abs(ux) > Math.abs(uz);
          var FC = _wallFaces(s);
          /* core-v811 (TWO STORY thread, Marek 2026-11-03: "bez tech
             horizontalnich ... prvku"): the plinth is the ground's, at the
             foot of the ground storey only -- an upper storey's walls had a
             dark band of their own at the floor between */
          var pliTop = ((s.storey | 0) === 0) ? s.y0 + WALL_REND.plinthH : -1e9;

          function slab(a, L, yLo, yHi, off, th, m, nm) {
            var H = yHi - yLo;
            if (!(L > 0.03) || !(H > 0.02)) return 0;
            var mesh = new T.Mesh(
              new T.BoxGeometry(along ? L : th, H, along ? th : L), m);
            mesh.position.set(
              s.x0 + ux * (a + L / 2) + ox * (off + th / 2),
              (yLo + yHi) / 2,
              s.z0 + uz * (a + L / 2) + oz * (off + th / 2));
            mesh.name = nm;
            g.add(mesh);
            pieces++;
            return L * H;
          }

          for (c = 0; c < FC.length; c++) {
            var cA = FC[c].a, cB = FC[c].b;
            var extR = coatFace + WALL_REND.pt + WALL_REND.t;
            if (cB >= s.len - 0.01) cB = s.len + extR;
            var drawA = (cA <= 0.01) ? -extR : cA;
            var L = cB - drawA, Lbill = cB - cA;
            var yLo = FC[c].yLo, yHi = FC[c].yHi;
            if (!(L > 0.03) || !(yHi - yLo > 0.02)) continue;

            /* the primer is under everything, plinth included */
            slab(drawA, L, yLo, yHi, coatFace, WALL_REND.pt, pMat, 'eps-primer');
            primA += Lbill * (yHi - yLo);

            /* and the finish, split at the plinth line */
            var rLo = Math.max(yLo, Math.min(yHi, pliTop));
            if (rLo > yLo + 0.005) {
              slab(drawA, L, yLo, rLo, coatFace + WALL_REND.pt,
                   WALL_REND.t + WALL_REND.plinthProud, plMat, 'eps-plinth-render');
              pliA += Lbill * (rLo - yLo);
            }
            if (yHi > rLo + 0.005) {
              slab(drawA, L, rLo, yHi, coatFace + WALL_REND.pt,
                   WALL_REND.t, rMat, 'eps-render');
              rendA += Lbill * (yHi - rLo);
            }
          }

          /* AND ON OVER THE HEAD TO THE ROOF, PRIMER AND RENDER BOTH.

             The gable skin used to render its own 400 mm courses above the head;
             now one band each, head to roof line, corner return to corner return,
             cut by the roof (_underRoof), on the same faces at the same offsets
             as the slabs below. Billed between the plan corners plus the far
             return, the near return drawn only, as the faces below are. */
          var Gr7 = null;
          try { Gr7 = _wallSkin(s); } catch (eR7) { Gr7 = null; }
          if (Gr7 && Gr7.rise > 0.02) {
            var head7 = s.y0 + s.h, top7 = head7 + Gr7.rise;
            var roofY7 = function (u) { return head7 + Gr7.hAt(u); };
            var extR7 = coatFace + WALL_REND.pt + WALL_REND.t;
            var polyR = _underRoof(-extR7, s.len + extR7, head7, top7, roofY7, [Gr7.at]);
            var aDraw7 = polyR.length >= 3 ? _polyArea(polyR) : 0;
            if (aDraw7 > 0.002) {
              var fwd7r = (oz * ux - ox * uz) > 0, e07 = fwd7r ? -extR7 : (s.len + extR7);
              var shR = function () {
                var sh = new T.Shape(), k7;
                for (k7 = 0; k7 < polyR.length; k7++) {
                  var lx7 = fwd7r ? (polyR[k7][0] + extR7) : (s.len + extR7 - polyR[k7][0]);
                  if (k7 === 0) sh.moveTo(lx7, polyR[k7][1] - head7);
                  else sh.lineTo(lx7, polyR[k7][1] - head7);
                }
                sh.lineTo(fwd7r ? (polyR[0][0] + extR7) : (s.len + extR7 - polyR[0][0]),
                          polyR[0][1] - head7);
                return sh;
              };
              var bandR = function (off, th, m, nm) {
                var mesh7 = new T.Mesh(
                  new T.ExtrudeGeometry(shR(), { depth: th, bevelEnabled: false }), m);
                mesh7.rotation.y = Math.atan2(ox, oz);
                mesh7.position.set(s.x0 + ux * e07 + ox * off, head7, s.z0 + uz * e07 + oz * off);
                mesh7.name = nm;
                g.add(mesh7);
                pieces++;
              };
              bandR(coatFace, WALL_REND.pt, pMat, 'eps-primer');
              bandR(coatFace + WALL_REND.pt, WALL_REND.t, rMat, 'eps-render');
              var polyRb = _underRoof(0, s.len + extR7, head7, top7, roofY7, [Gr7.at]);
              var aBill7 = polyRb.length >= 3 ? _polyArea(polyRb) : 0;
              primA += aBill7;
              rendA += aBill7;
            }
          }

          /* and into the reveals, primer and render both */
          var ops7 = s.openings || [];
          for (q7 = 0; q7 < ops7.length; q7++) {
            var oW = ops7[q7].w;
            var oY0 = s.y0 + ops7[q7].sill, oY1 = oY0 + ops7[q7].h;
            var oH = oY1 - oY0, rd = revD;
            if (!(oH > 0.05) || !(oW > 0.05)) continue;

            /* the end face of the reveal strip, primed and rendered like the wall
               face it is level with -- see the coat pass */
            var capR = WALL_EPS_REV, jqR;
            for (jqR = 0; jqR < 2; jqR++) {
              var atR = ops7[q7].at + (jqR ? 1 : -1) * (oW / 2 - capR / 2);
              var pmR = new T.Mesh(
                new T.BoxGeometry(along ? capR : WALL_REND.pt, oH, along ? WALL_REND.pt : capR), pMat);
              pmR.position.set(s.x0 + ux * atR + ox * (coatFace + WALL_REND.pt / 2),
                               oY0 + oH / 2, s.z0 + uz * atR + oz * (coatFace + WALL_REND.pt / 2));
              pmR.name = 'eps-primer';
              g.add(pmR);
              var rmR = new T.Mesh(
                new T.BoxGeometry(along ? capR : WALL_REND.t, oH, along ? WALL_REND.t : capR), rMat);
              rmR.position.set(s.x0 + ux * atR + ox * (rendFace - WALL_REND.t / 2),
                               oY0 + oH / 2, s.z0 + uz * atR + oz * (rendFace - WALL_REND.t / 2));
              rmR.name = 'eps-render';
              g.add(rmR);
              revA += oH * capR;
            }
            var phR = new T.Mesh(
              new T.BoxGeometry(along ? oW : WALL_REND.pt, capR, along ? WALL_REND.pt : oW), pMat);
            phR.position.set(s.x0 + ux * ops7[q7].at + ox * (coatFace + WALL_REND.pt / 2),
                             oY1 - capR / 2, s.z0 + uz * ops7[q7].at + oz * (coatFace + WALL_REND.pt / 2));
            phR.name = 'eps-primer';
            g.add(phR);
            var rhR = new T.Mesh(
              new T.BoxGeometry(along ? oW : WALL_REND.t, capR, along ? WALL_REND.t : oW), rMat);
            rhR.position.set(s.x0 + ux * ops7[q7].at + ox * (rendFace - WALL_REND.t / 2),
                             oY1 - capR / 2, s.z0 + uz * ops7[q7].at + oz * (rendFace - WALL_REND.t / 2));
            rhR.name = 'eps-render';
            g.add(rhR);
            revA += oW * capR;

            for (jq7 = 0; jq7 < 2; jq7++) {
              var sgn7 = jq7 ? -1 : 1;
              var at7 = ops7[q7].at + (jq7 ? oW / 2 : -oW / 2)
                        + sgn7 * (WALL_EPS_REV + WALL_COAT.t + WALL_REND.t);
              var jm = new T.Mesh(
                new T.BoxGeometry(along ? WALL_REND.t : rd, oH,
                                  along ? rd : WALL_REND.t), rMat);
              jm.position.set(
                s.x0 + ux * at7 + ox * (rendFace - rd / 2),
                (oY0 + oY1) / 2,
                s.z0 + uz * at7 + oz * (rendFace - rd / 2));
              jm.name = 'eps-render';
              g.add(jm);
              revA += oH * rd;
              pieces++;
            }

            var sm = new T.Mesh(
              new T.BoxGeometry(along ? (oW - 2 * WALL_EPS_REV) : rd, WALL_REND.t,
                                along ? rd : (oW - 2 * WALL_EPS_REV)), rMat);
            sm.position.set(
              s.x0 + ux * ops7[q7].at + ox * (rendFace - rd / 2),
              oY1 - WALL_EPS_REV - WALL_COAT.t - WALL_REND.t / 2,
              s.z0 + uz * ops7[q7].at + oz * (rendFace - rd / 2));
            sm.name = 'eps-render';
            g.add(sm);
            revA += oW * rd;
            pieces++;
          }
        }

        if (!pieces) { ctx.log('WAL  render none'); return; }
        _add(LAYER, WALL_REND.prim, primA + revA);
        _add(LAYER, WALL_REND.code, rendA + revA);
        if (pliA > 0) _add(LAYER, WALL_REND.plinth, pliA);
        ctx.log('WAL  render ' + pieces + ' piece(s): primer ' +
                API.fmtArea(primA + revA) + ' ' + WALL_REND.prim + ', render ' +
                API.fmtArea(rendA + revA) + ' ' + WALL_REND.code + ', plinth ' +
                API.fmtArea(pliA) + ' ' + WALL_REND.plinth + ' over the bottom ' +
                Math.round(WALL_REND.plinthH * 1000) + ' mm');
        ctx.log('WAL  render reveals ' + API.fmtArea(revA) + ' at ' +
                Math.round(revD * 1000) + ' mm deep -- the WHOLE width of the reveal, ' +
                'from the render own face back to the frame, the same line the coat ' +
                'under it now stops on. Both used to return ' +
                Math.round(WALL_EPS.t * 1000) + ' and stop in mid air. Counted into ' +
                'both the primer and the render, and the plinth is its own product ' +
                'because that band is splashed and kicked');
        _why(ctx, 'wall     the primer is the layer nobody sees and the one that decides ' +
                'whether the render stays on: it keys the coats together and stops the ' +
                'base coat drinking the render dry before it sets');
      }

      function _wallSkirt(ctx) {
        if (!_wallStepOn(6)) return;
        /* on a slab there is nothing under the wall to close */
        if (API.STATE.foundation === 'slab') {
          ctx.log('WAL  skirt none -- the slab is the floor, so there is no gap');
          return;
        }
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i;
        var drop = _skirtDrop();
        var contact = _cladOff();

        var osbM = new T.MeshStandardMaterial({
          color: _wallNew('skirt') ? MAGENTA : (_finTex(T, 'osb') ? 0xFFFFFF : 0xC8A063),
          map: _wallNew('skirt') ? null : (_finTex(T, 'osb') || null),
          roughness: 0.95, metalness: 0.0 });
        var epsM = new T.MeshStandardMaterial({
          color: _wallNew('skirt') ? MAGENTA : 0xF0EFE6, roughness: 0.98, metalness: 0.0 });
        var coatM = new T.MeshStandardMaterial({
          color: _wallNew('skirt') ? MAGENTA : 0xCFCCC2, roughness: 0.96, metalness: 0.0 });
        var pliM = new T.MeshStandardMaterial({
          color: _wallNew('skirt') ? MAGENTA : 0x8C857A, roughness: 0.88, metalness: 0.0 });
        var cbM = new T.MeshStandardMaterial({
          color: _wallNew('skirt') ? MAGENTA : 0xB6B3AB, roughness: 0.95, metalness: 0.0 });
        var battM = new T.MeshStandardMaterial({
          color: _wallNew('skirt') ? MAGENTA : colorOf('timber'), roughness: 0.9, metalness: 0.0 });

        var meshM = new T.MeshStandardMaterial({
          color: _wallNew('skirt') ? MAGENTA : 0x6B6F72,
          roughness: 0.6, metalness: 0.5, side: T.DoubleSide,
          transparent: true, opacity: 0.75 });

        var osbA = 0, epsA = 0, coatA = 0, pliA = 0, cbA = 0, battRun = 0, pieces = 0;
        var ventM = 0;
        var skin = (SKIRT_SHOW !== 'frame');
        var ST5 = (API.__wallStuds ? API.__wallStuds() : _wallStuds) || [];

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2)) continue;
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var along = Math.abs(ux) > Math.abs(uz);
          var yLo = s.y0 - drop, yHi = s.y0;
          var L = s.len;                      /* billed on the wall, drawn past it */

          /* AND DOWN HERE EACH LAYER TURNS THE CORNER BY ITS OWN FACE TOO.

             The skirt ran from half a board before the wall to half a board past
             it -- the extension split between the two ends. That closes neither
             corner and doubles at both: half a lap is not a lap.

             Same rule as the storey above, which is the point of having a rule:
             start at the wall, carry on past the far end by the distance this
             layer's own outer face stands from the wall line, and let every wall
             do it at the same end. The EPS then reaches the outside corner of the
             building instead of stopping short of it, which is the gap he can see
             at the bottom. */
          function band(off, th, m, nm) {
            /* both ends, for the reason spelled out at the EPS above: the layer's
               own outline turns the corner d past the plan corner at EACH end */
            var ext = off + th;
            var bl = s.len + 2 * ext;
            var bm2 = bl / 2 - ext;
            var mesh = new T.Mesh(
              new T.BoxGeometry(along ? bl : th, drop, along ? th : bl), m);
            mesh.position.set(
              s.x0 + ux * bm2 + ox * (off + th / 2), (yLo + yHi) / 2,
              s.z0 + uz * bm2 + oz * (off + th / 2));
            mesh.name = nm;
            g.add(mesh);
            pieces++;
            return bl;
          }

          /* the sheathing, carried down over the rim on both kinds of wall */
          /* EVERY BAND WEARS ITS OWN CODE.

             One 'skirt' mark covered this whole block, so the sheathing, the
             insulation, the coat and the render all counted as CEMENT BOARD --
             74 m of band in the model against 24 m of wall in the bill, on a
             building whose perimeter is 38. Two bands per segment, one code.

             Fifth time this exact shape has turned up, and the last: a mark per
             item, set where the item is drawn (Marek). */
          _asmMark(ctx, 'skirt_sheathing', 'side', 'Skirt sheathing', 'sheet.osb18');
          band(0, 0.018, osbM, 'skirt-sheathing');
          osbA += L * drop;

          if (contact) {
            var f = 0.018 + 0.001;
            if (skin) {
              _asmMark(ctx, 'skirt_eps', 'side', 'Skirt insulation', WALL_EPS.code);
              band(f, WALL_EPS.t, epsM, 'skirt-eps');
              _asmMark(ctx, 'skirt_coat', 'side', 'Skirt coat', WALL_COAT.code);
              band(f + WALL_EPS.t + WALL_COAT.gap, WALL_COAT.t, coatM, 'skirt-coat');
              _asmMark(ctx, 'skirt_render', 'side', 'Skirt render', WALL_REND.plinth);
              band(f + WALL_EPS.t + WALL_COAT.gap + WALL_COAT.t + WALL_REND.pt,
                   WALL_REND.t + WALL_REND.plinthProud, pliM, 'skirt-plinth-render');
            }
            epsA += L * drop;
            coatA += L * drop;
            pliA += L * drop;

            /* AND UNDER THE BOTTOM EDGE OF THE BOARD, WHICH IS THE SAME FAULT AS
               THE ONE UNDER THE COPING, TWO STOREYS DOWN.

               He spotted that himself: the skirt's insulation ends in mid-air at
               the foot of the building and its cut face -- 140 mm of open foam,
               the width of the board -- has nothing on it. Up at the parapet the
               coat and the render now turn over the top edge; down here they have
               to turn UNDER the bottom one, and for the better reason: this is the
               edge that faces the ground, catches the splash off it and is the
               first thing a strimmer finds.

               Same two layers, same wrap, the other way up. */
            if (skin) {
              var bw = WALL_EPS.t + WALL_COAT.t;
              var bmid = f + bw / 2;
              var bl2 = s.len + 2 * (f + bw);
              var bcm = new T.Mesh(
                new T.BoxGeometry(along ? bl2 : bw, WALL_COAT.t,
                                  along ? bw : bl2), coatM);
              bcm.position.set(
                s.x0 + ux * (bl2 / 2 - (f + bw)) + ox * bmid,
                yLo - WALL_COAT.t / 2,
                s.z0 + uz * (bl2 / 2 - (f + bw)) + oz * bmid);
              bcm.name = 'skirt-coat';
              g.add(bcm);
              coatA += bl2 * bw;

              var brm = new T.Mesh(
                new T.BoxGeometry(along ? bl2 : bw + 2 * WALL_REND.t, WALL_REND.t,
                                  along ? bw + 2 * WALL_REND.t : bl2), pliM);
              brm.position.set(
                s.x0 + ux * (bl2 / 2 - (f + bw)) + ox * bmid,
                yLo - WALL_COAT.t - WALL_REND.pt - WALL_REND.t / 2,
                s.z0 + uz * (bl2 / 2 - (f + bw)) + oz * bmid);
              brm.name = 'skirt-plinth-render';
              g.add(brm);
              pliA += bl2 * bw;
            }
          } else {
            /* THE BATTENS ARE BATTENS, NOT A BOARD 40 MM THICK.

               I drew this band as one continuous slab the length of the wall,
               which is not what is under a cement board and not what he asked to
               look at. They are the SAME battens as the ones above, carried down:
               same section, same lines, and the lines are the frame's own studs.
               Drawn one by one, the gap between them is the cavity, and that is
               the thing worth seeing. */
            var fb = 0.018 + 0.001 + WALL_MEMB.t;
            var ats5 = [], k5;
            for (k5 = 0; k5 < ST5.length; k5++) {
              if (ST5[k5].seg !== s.id) continue;
              if (ST5[k5].at > 0.001 && ST5[k5].at < s.len - 0.001) ats5.push(ST5[k5].at);
            }
            ats5.sort(function (p1, p2) { return p1 - p2; });
            /* a butting wall's end stud stands a frame depth in and the corner
               batten already covers it, the same as the facade battens above */
            var endS5 = STUD.d + STUD.t + 0.005;
            ats5 = ats5.filter(function (v5) { return v5 > endS5 && v5 < s.len - endS5; });
            ats5.unshift(WALL_FBATT.f / 2);
            ats5.push(s.len - WALL_FBATT.f / 2);
            for (k5 = 0; k5 < ats5.length; k5++) {
              if (k5 > 0 && ats5[k5] - ats5[k5 - 1] < 0.05) continue;
              /* THE BATTEN IS NOT THE BOARD. One 'skirt' mark covered this whole
                 section, so the battens behind the cement board were counted as
                 cement board -- 16.2 m2 in the model against 7 m2 billed. Fourth
                 time this shape of fault has turned up: one mark over two items
                 (Marek). */
              _asmMark(ctx, 'skirt_battens', 'side', 'Skirt battens', WALL_FBATT.code);
              var bm5 = new T.Mesh(
                new T.BoxGeometry(along ? WALL_FBATT.f : WALL_FBATT.d, drop,
                                  along ? WALL_FBATT.d : WALL_FBATT.f), battM);
              bm5.position.set(
                s.x0 + ux * ats5[k5] + ox * (fb + WALL_FBATT.d / 2),
                (yLo + yHi) / 2,
                s.z0 + uz * ats5[k5] + oz * (fb + WALL_FBATT.d / 2));
              bm5.name = 'skirt-batten';
              g.add(bm5);
              pieces++;
              battRun += drop;
            }
            if (skin) {
              _asmMark(ctx, 'skirt', 'side', 'Skirt', WALL_SKIRT.board);
              band(fb + WALL_FBATT.d, WALL_SKIRT.boardT, cbM, 'skirt-cement-board');
            }
            cbA += L * drop;

            /* AND THE CAVITY IS OPEN AT THE BOTTOM, WHICH IS THE POINT AND THE
               PROBLEM.

               A ventilated facade draws its air in at the foot: that 40 mm slot
               runs round the whole building and it is what makes the cavity a
               cavity rather than a warm damp gap. It is also a door for mice, and
               tiny says so in as many words -- it has billed FEC00034 for this
               since long before I started drawing skirts.

               So the slot stays open and gets the same mesh the eaves intake got.
               Same code, same reason, the other end of the same air path: in at
               the bottom, out at the top. */
            var vm5 = new T.Mesh(
              new T.BoxGeometry(along ? (s.len + fb + WALL_FBATT.d) : WALL_FBATT.d,
                                0.020,
                                along ? WALL_FBATT.d : (s.len + fb + WALL_FBATT.d)),
              meshM);
            vm5.position.set(
              s.x0 + ux * ((s.len + fb + WALL_FBATT.d) / 2) + ox * (fb + WALL_FBATT.d / 2),
              yLo + 0.010,
              s.z0 + uz * ((s.len + fb + WALL_FBATT.d) / 2) + oz * (fb + WALL_FBATT.d / 2));
            vm5.name = 'cavity-vent-mesh';
            g.add(vm5);
            ventM += s.len;
          }
        }

        if (!pieces) { ctx.log('WAL  skirt none'); return; }
        _add(LAYER, 'sheet.osb18', osbA);
        _add(LAYER, SHEATH_NAIL.code, Math.round(osbA * SHEATH_NAIL.perM2));
        if (contact) {
          _add(LAYER, WALL_EPS.code, epsA);
          _add(LAYER, WALL_EPS.foam, Math.ceil(epsA * WALL_EPS.foamPerM2));
          _add(LAYER, WALL_EPS.anchor,
               Math.ceil(epsA / (WALL_EPS.w * WALL_EPS.h) * WALL_EPS.anchorPerBoard));
          _add(LAYER, WALL_COAT.code, coatA);
          /* AND THIS ONE IS THE SKIRT'S, NOT THE GABLE'S. I meant to point the
             gable's mesh at its own area and edited the first match instead --
             which was here, where meshA has never existed. The whole wall layer has
             been throwing on it ever since, which is why everything above the head
             vanished and why he kept seeing the layers stop at the ceiling: they
             were not short, they were never drawn. The skirt's mesh follows the
             skirt's coat, as it always did. */
          _add(LAYER, WALL_MESH.code,
               coatA * WALL_MESH.roll / (WALL_MESH.roll - WALL_MESH.lap));
          _add(LAYER, WALL_REND.prim, pliA);
          _add(LAYER, WALL_REND.plinth, pliA);
          ctx.log('WAL  skirt CONTACT, ' + Math.round(drop * 1000) + ' mm deep: ' +
                  API.fmtArea(osbA) + ' sheathing carried over the rim, then the same ' +
                  'EPS, coat, mesh and PLINTH render as the wall above -- no battens ' +
                  'and no cavity, so there is nothing for a cement board to hang on');
        } else {
          _add(LAYER, WALL_FBATT.code, battRun);
          _add(LAYER, WALL_FBATT.screw,
               Math.max(2, Math.round(battRun / WALL_FBATT.screwStep)));
          _add(LAYER, WALL_SKIRT.board, cbA);
          _add(LAYER, WALL_SKIRT.screw, Math.round(cbA * WALL_SKIRT.screwPerM2));
          _add(LAYER, WALL_EAVE.mesh, ventM);
          ctx.log('WAL  skirt VENTILATED, ' + Math.round(drop * 1000) + ' mm deep: ' +
                  API.fmtArea(cbA) + ' ' + WALL_SKIRT.board + ' on the battens carried ' +
                  'down, the same way tiny closes it -- the board IS the cladding of ' +
                  'that band and the cavity behind it drains with the rest');
          ctx.log('WAL  skirt cavity intake ' + Math.round(ventM * 10) / 10 + ' m of ' +
                  WALL_EAVE.mesh + ' along the foot -- the slot is what makes the ' +
                  'cavity a cavity, and it is also a door for mice; tiny has billed ' +
                  'this row for it all along');
          ctx.log('WAL  skirt battens ' + Math.round(battRun * 10) / 10 + ' m of ' +
                  WALL_FBATT.code + ', drawn one by one on the frame own stud lines ' +
                  'so the cavity between them is visible');
        }
        if (!skin) {
          ctx.log('WAL  skirt SHOW=frame -- the board, the EPS, the coat and the ' +
                  'render are hidden so the substrate can be checked. LOOKING only: ' +
                  'every one of them is still billed. Set SKIRT_SHOW back to all.');
        }
        if (contact) {
          ctx.log('WAL  skirt the BOTTOM EDGE of the board is coated and rendered too, ' +
                  'the same wrap the parapet gets over its top -- this is the edge that ' +
                  'faces the ground, takes the splash off it and meets the strimmer');
        }
        if (contact) {
          ctx.log('WAL  skirt the starter track sits at the FOOT of it, ' +
                  Math.round(drop * 1000) + ' mm below the wall base, and the first ' +
                  'EPS course now starts on the floor line with no bare strip under it');
        }
      }

      function _wallExtSills(ctx) {
        if (!_wallStepOn(9)) return;
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, q;
        var mat = new T.MeshStandardMaterial({
          color: _wallNew('sill') ? MAGENTA : 0xC9CCCE,
          roughness: 0.5, metalness: 0.35 });

        var face = _facadeFace();
        var run = 0, n = 0;

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2)) continue;
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var along = Math.abs(ux) > Math.abs(uz);
          var ops = s.openings || [];

          for (q = 0; q < ops.length; q++) {
            /* core-v786: a serving window has a counter, not a sill (the container's rule) */
            if (!(ops[q].sill > 0.05) || ops[q].kind === 'serve') continue;
            if (_roundOp(ops[q])) continue;   /* core-v809: a porthole has no sill */          /* a door has a threshold */
            /* THE SHED FAMILY'S CLEAR THE TRIM, NOT JUST THE CLADDING (shed-v117,
               tiny's rule): 25 past the jamb trim each side, its depth counting
               the trim's thickness before the 50 it stands proud */
            var len = _hkSillByTrim ? (ops[q].w + 2 * (WALL_CTRIM.leg + 0.025)) : (ops[q].w + 2 * WALL_ESILL.side);
            var dep = _hkSillByTrim ? (face + WALL_CTRIM.t + WALL_ESILL.out) : (face + WALL_ESILL.out);
            var y = s.y0 + ops[q].sill;

            var sm = new T.Mesh(
              new T.BoxGeometry(along ? len : dep, WALL_ESILL.t,
                                along ? dep : len), mat);
            /* IT FALLS OUTWARD. A flat sill is a shelf for water, and the tilt has
               to know which way out is: turning about the wall's own axis sends
               the far edge down on one side of the building and up on the other,
               so the sign comes from the outward normal and not from the axis. */
            if (along) sm.rotation.x = (oz > 0 ? 1 : -1) * WALL_ESILL.fall;
            else       sm.rotation.z = (ox > 0 ? -1 : 1) * WALL_ESILL.fall;
            /* AND THE TILT LIFTS THE BACK EDGE, WHICH IS WHERE THE LINE CAME FROM.

               The board is turned about its own centre, so the fall that drops the
               front by half a depth raises the BACK by the same half -- and the
               back is the edge against the wall. Sitting with its top on the sill
               line, the raised corner floated a few millimetres clear of the
               facade under it and the render showed through in a hairline right
               under the window. He read it exactly: it is a touch too high.

               So it comes down by what the turn lifted -- half the depth times the
               fall -- and then a further 2 mm, so it BEDS on what is under it
               rather than balancing on it. */
            /* AND HE IS RIGHT ABOUT THE SIGN: the whole board was a thickness low.

               I put its TOP face on the opening's sill line, on tiny's reasoning
               that the frame lands on top of it. Looked at, that is a board sunk
               into the wall with the frame standing in its place -- the top edge
               sitting where the bottom belongs, exactly as he says. The board goes
               ON the line and the frame behind it, so its BOTTOM is the sill line
               and it stands a thickness proud of it.

               The drop for the tilt stays: the turn still lifts the back edge and
               that is what opened the hairline over the facade. */
            var lift = (dep / 2) * Math.sin(WALL_ESILL.fall) + 0.002;
            sm.position.set(
              s.x0 + ux * ops[q].at + ox * (dep / 2),
              y + WALL_ESILL.t / 2 - lift,
              s.z0 + uz * ops[q].at + oz * (dep / 2));
            sm.name = 'window-sill-ext';
            g.add(sm);
            run += len;
            n++;
          }
        }

        if (!n) { ctx.log('WAL  exterior sills none -- no window with a sill'); return; }
        _add(LAYER, WALL_ESILL.code, run);
        /* AND WHAT HOLDS IT DOWN. A sill laid on nothing rattles in the wind and
           lets water under itself, so it is bedded in foam over its whole length.
           One can does about 12 m of bead. */
        _add(LAYER, 'ODP00001', Math.ceil(run / 12));
        ctx.log('WAL  exterior sills ' + n + ' window(s), ' +
                Math.round(run * 100) / 100 + ' m of ' + WALL_ESILL.code + ' -- ' +
                (_hkSillByTrim ? (Math.round((WALL_CTRIM.leg + 0.025) * 1000) + ' mm past each jamb (25 past the trim), ') : (Math.round(WALL_ESILL.side * 1000) + ' mm past each jamb, ')) +
                Math.ceil(run / 12) + ' x ODP00001 bedding it, ' +
                Math.round(WALL_ESILL.out * 1000) + ' proud, depth ' +
                Math.round((face + WALL_ESILL.out) * 1000) + ' mm through the ' +
                (_cladOff() ? 'contact' : 'ventilated') + ' facade, dropped by the ' +
                'lift the fall puts on its back edge so it beds on the facade ' +
                'instead of floating over it');
        _why(ctx, 'wall     the sill runs PAST the reveal on both sides, because water ' +
                'that stops at the jamb runs straight back into it');
      }

      function _wallCornerTrim(ctx) {
        if (!_wallStepOn(9)) return;
        /* SAY WHY IT IS NOT THERE. Three of the four he is looking for are behind
           a facade choice or a step gate, and a layer that returns in silence is a
           layer that looks broken. */
        if (_cladOff()) {
          ctx.log('WAL  corner trim none -- contact facade has a bead at every corner');
          return;
        }
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i;
        var P = ctx.poly || [];
        var mat = new T.MeshStandardMaterial({
          color: _wallNew('ctrim') ? MAGENTA : colorOf('timber'),
          roughness: 0.86, metalness: 0.0 });

        /* A CENTIMETRE MORE ON EACH LEG, ON HIS CALL.

           At 70 the profile did not quite reach round the corner: the boards' cut
           ends showed at the arris on one side or the other, which is the one job
           it has. 80 covers it with something to spare.

           THE ROW DOES NOT MOVE. FVW02660 is billed by the METRE and the run is
           the height of the corner, so a wider leg is the same number of metres of
           the same product -- it is a drawing figure, not a quantity. If the wider
           profile is genuinely a different article, that is a new row and he will
           say so; until then the catalogue is right as it stands. */
        var LEG_W = WALL_CTRIM.leg + 0.010;

        var face = _facadeFace();
        var drop = _hkNoSkirt ? 0 : _skirtDrop();   /* the shed family: no skirt, the boards run to the frame's foot */
        var run = 0, n = 0, skipped = 0;

        function nextAt2(x, z, self) {
          var q;
          for (q = 0; q < SEG.length; q++) {
            if (q === self || !SEG[q] || !(SEG[q].len > 0.2)) continue;
            if (Math.abs(SEG[q].x0 - x) < 0.01 && Math.abs(SEG[q].z0 - z) < 0.01) return SEG[q];
          }
          return null;
        }

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!s || !(s.len > 0.2)) continue;
          var nx2 = nextAt2(s.x1, s.z1, i);
          if (!nx2) continue;

          /* convex or not -- asked of the outline, not guessed from the winding */
          var bx = s.nx + nx2.nx, bz = s.nz + nx2.nz;
          var bl = Math.hypot(bx, bz);
          if (bl < 1e-6) continue;                       /* a straight run */
          bx /= bl; bz /= bl;
          if (P.length > 2 && inPoly(s.x1 + bx * 0.05, s.z1 + bz * 0.05, P)) {
            skipped++;
            continue;                                    /* an internal corner */
          }

          /* AND IT CLIMBS WITH THE FACADE, WHERE THE FACADE CLIMBS.

             The trim stopped at the wall head. Below that line the corner is
             covered and the boards' cut ends are hidden; above it, on a mono, the
             cladding carries on to the roof with nothing over the arris -- which
             is the corner he says the upper part does not reach.

             It follows the LOWER of the two walls' profiles at that corner. Taking
             the higher one would put the trim through the roof on the other side,
             which is the mistake the gable boards already made once. */
          /* AND IT CLIMBS ON EVERY ROOF THAT HAS ANYTHING TO CLIMB.

             The condition said mono, because a mono was what I had in front of me.
             A parapet is a wall that carries on for half a metre round the whole
             building, and its corners had the trim stopping at the ceiling with
             the cladding running past it to the coping -- the cut ends showing at
             eye level on the one part of the facade nothing else covers.

             There is no type test now. Both walls are asked how high they go at
             that corner and the trim follows the LOWER of the two, which on a
             parapet is the same number all round, on a mono is the rake, and on a
             gable is the eave -- above which there is no vertical corner left to
             cover anyway. */
          var GA = _wallSkin(s), GB = _wallSkin(nx2);
          var hA2 = GA ? GA.hAt(s.len) : 0;
          var hB2 = GB ? GB.hAt(0) : 0;
          var climb = Math.min(hA2, hB2);
          /* core-v796 (RUSSWORX SHED, shed-v132): each wall's rise is over ITS
             OWN head, so the lower of the two TOPS is taken -- the same number
             as before wherever the two walls are the same height (every
             product without STATE.segH); where one wall is its own height (a
             mono's high wall) the corner no longer stops a rise short */
          var y0 = s.y0 - drop, y1 = Math.min(s.y0 + s.h + hA2, nx2.y0 + nx2.h + hB2), H = y1 - y0;
          if (!(H > 0.05)) continue;

          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var vx = (nx2.x1 - nx2.x0) / nx2.len, vz = (nx2.z1 - nx2.z0) / nx2.len;

          /* the outer corner of the CLADDING, not of the frame -- both legs start
             from that one point, which is the lesson the beads already taught */
          var cX = s.x1 + s.nx * face + nx2.nx * face;
          var cZ = s.z1 + s.nz * face + nx2.nz * face;
          var leg = LEG_W, th = WALL_CTRIM.t;

          var mA = new T.Mesh(
            new T.BoxGeometry(Math.abs(ux) > Math.abs(uz) ? leg : th, H,
                              Math.abs(ux) > Math.abs(uz) ? th : leg), mat);
          mA.position.set(cX - ux * (leg / 2) + s.nx * (th / 2),
                          (y0 + y1) / 2,
                          cZ - uz * (leg / 2) + s.nz * (th / 2));
          mA.name = 'cladding-corner-trim';
          g.add(mA);

          var mB = new T.Mesh(
            new T.BoxGeometry(Math.abs(vx) > Math.abs(vz) ? leg : th, H,
                              Math.abs(vx) > Math.abs(vz) ? th : leg), mat);
          mB.position.set(cX + vx * (leg / 2) + nx2.nx * (th / 2),
                          (y0 + y1) / 2,
                          cZ + vz * (leg / 2) + nx2.nz * (th / 2));
          mB.name = 'cladding-corner-trim';
          g.add(mB);

          run += H;
          n++;
        }

        if (!n) { ctx.log('WAL  corner trim none -- no external corner found'); return; }
        _add(LAYER, WALL_CTRIM.code, run);
        /* a trim shows two legs, so its painted surface is twice the leg */
        _extCoat(run * LEG_W * 2);
        _add(LAYER, WALL_CLAD.nail,
             Math.max(4, Math.round(run / WALL_CLAD.nailStep) * 2));
        ctx.log('WAL  corner trim ' + n + ' external corner(s)' +
                (skipped ? (', ' + skipped + ' internal skipped') : '') + ', ' +
                Math.round(run * 10) / 10 + ' m of ' + WALL_CTRIM.code + ' (' +
                Math.round(LEG_W * 1000) + ' legs drawn, ' +
                Math.round(WALL_CTRIM.t * 1000) + ' thick), foot of the skirt to the ' +
                'wall head -- and it carries on wherever the wall does: up the rake ' +
                'on a mono, to the coping on a parapet, to the eave on a gable, ' +
                'following the LOWER of the two walls at each corner so it cannot come ' +
                'through on the other side');
        _why(ctx, 'wall     without it a corner shows the end grain of the boards and the ' +
                'cavity behind them -- the trim covers the joint from both sides and ' +
                'shuts the cavity');
      }

      function _wallRevealLining(ctx) {
        if (!_wallStepOn(9)) return;
        if (_cladOff()) {
          ctx.log('WAL  reveal lining none -- contact facade: the reveal is rendered');
          return;
        }
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, q, jq;
        var mat = new T.MeshStandardMaterial({
          color: _wallNew('lining') ? MAGENTA : colorOf('timber'),
          roughness: 0.86, metalness: 0.0 });

        /* THE REVEAL IS THE WHOLE WAY FROM THE FRAME TO THE FINISHED FACE.

           The lining was cut to _facadeFace -- 75 on the boarded wall -- which
           is the distance from the plan line OUT to the cladding, and the reveal
           does not start at the plan line. The joinery stands the unit on the
           middle of the wall, so the frame's outer face is _revealBack() BEHIND
           that line: 40 mm on a 160 stud. The boards stopped there and left the
           sheathing edge and a strip of stud bare down every jamb and across the
           head. The wet layers had the same fault and got the same answer -- one
           function, asked once, each layer measuring from its own face. */
        var face = _facadeFace();
        var back = _revealBack();
        var dep = face - back;
        var t = WALL_CLAD.t;
        var area = 0, n = 0, pieces = 0, trimRun = 0;

        /* AND A CORNER PROFILE ROUND EVERY OPENING, NOT ONLY AT THE BUILDING'S
           OWN CORNERS.

           He asked for it and it is the same argument one scale down: where the
           face boards stop and the reveal lining turns in, there is an arris made
           of two cut edges meeting. On the building's corner that gets a trim; at
           a window it is the same joint, seen closer, and touched more.

           Same product, FVW02660, and the same shape -- one leg on the face, one
           down the reveal. Two jambs and a head; the sill keeps its metal. */
        var trimMat = new T.MeshStandardMaterial({
          color: _wallNew('rtrim') ? MAGENTA : colorOf('timber'),
          roughness: 0.86, metalness: 0.0 });
        var TL = WALL_CTRIM.leg, TT = WALL_CTRIM.t;
        var headFMat = null, headFRun = 0, headFNails = 0;   /* garageapt-v30 */

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2)) continue;
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var along = Math.abs(ux) > Math.abs(uz);
          var ops = s.openings || [];

          for (q = 0; q < ops.length; q++) {
            var oW = ops[q].w;
            var oY0 = s.y0 + ops[q].sill, oY1 = oY0 + ops[q].h;
            var oH = oY1 - oY0;
            if (!(oH > 0.05) || !(oW > 0.05)) continue;

            /* the two jambs, each set a board thickness inside the reveal line */
            for (jq = 0; jq < 2; jq++) {
              var sgn = jq ? -1 : 1;
              var at = ops[q].at + (jq ? oW / 2 : -oW / 2) + sgn * (t / 2);
              var jm = new T.Mesh(
                new T.BoxGeometry(along ? t : dep, oH, along ? dep : t), mat);
              jm.position.set(
                s.x0 + ux * at + ox * (back + dep / 2),
                (oY0 + oY1) / 2,
                s.z0 + uz * at + oz * (back + dep / 2));
              jm.name = 'reveal-lining';
              g.add(jm);
              area += oH * dep;
              pieces++;
            }

            /* and the head, run the full width so it caps the jambs */
            var hm = new T.Mesh(
              new T.BoxGeometry(along ? oW : dep, t, along ? dep : oW), mat);
            hm.position.set(
              s.x0 + ux * ops[q].at + ox * (back + dep / 2),
              oY1 - t / 2,
              s.z0 + uz * ops[q].at + oz * (back + dep / 2));
            hm.name = 'reveal-lining';
            g.add(hm);
            area += oW * dep;
            pieces++;

            /* the trims: a leg on the face and a leg into the reveal, at each
               jamb and along the head */
            for (jq = 0; jq < 2; jq++) {
              var sg2 = jq ? 1 : -1;
              var edge = ops[q].at + (jq ? oW / 2 : -oW / 2);

              var fA = new T.Mesh(
                new T.BoxGeometry(along ? TL : TT, oH, along ? TT : TL), trimMat);
              fA.position.set(
                s.x0 + ux * (edge + sg2 * TL / 2) + ox * (face + TT / 2),
                (oY0 + oY1) / 2,
                s.z0 + uz * (edge + sg2 * TL / 2) + oz * (face + TT / 2));
              fA.name = 'reveal-trim';
              g.add(fA);

              var rA = new T.Mesh(
                new T.BoxGeometry(along ? TT : TL, oH, along ? TL : TT), trimMat);
              rA.position.set(
                s.x0 + ux * (edge - sg2 * TT / 2) + ox * (face - TL / 2),
                (oY0 + oY1) / 2,
                s.z0 + uz * (edge - sg2 * TT / 2) + oz * (face - TL / 2));
              rA.name = 'reveal-trim';
              g.add(rA);
              trimRun += oH;
            }

            var hF = new T.Mesh(
              new T.BoxGeometry(along ? (oW + 2 * TL) : TT, TL,
                                along ? TT : (oW + 2 * TL)), trimMat);
            hF.position.set(
              s.x0 + ux * ops[q].at + ox * (face + TT / 2),
              oY1 + TL / 2,
              s.z0 + uz * ops[q].at + oz * (face + TT / 2));
            hF.name = 'reveal-trim';
            g.add(hF);

            var hR = new T.Mesh(
              new T.BoxGeometry(along ? oW : TL, TT, along ? TL : oW), trimMat);
            hR.position.set(
              s.x0 + ux * ops[q].at + ox * (face - TL / 2),
              oY1 - TT / 2,
              s.z0 + uz * ops[q].at + oz * (face - TL / 2));
            hR.name = 'reveal-trim';
            g.add(hR);
            trimRun += oW;

            /* garageapt-v30 (17b): the metal head flashing over the head trim */
            if (GA_HEADF) (function () {
              var HFl = GA_HEADF, lenF = oW + 2 * TL + 2 * HFl.over, yT = oY1 + TL, dW = 0.018 + 0.001 + WALL_MEMB.t, dF = face + TT + HFl.lip;
              var mF = headFMat || (headFMat = new T.MeshStandardMaterial({ color: _wallNew('headflash') ? MAGENTA : 0x9AA0A6, roughness: 0.4, metalness: 0.6 }));
              /* its own item for the bodies, then the wall's again (the coating
                 must not read the steel as timber) */
              var mkPrev = ctx.item || null;
              _asmMark(ctx, 'ga_head_flashing', 'side', 'Head flashing', HFl.code);
              function hfBox(dl, h, dd, y, dc) {
                var mm = new T.Mesh(new T.BoxGeometry(along ? dl : dd, h, along ? dd : dl), mF);
                mm.position.set(s.x0 + ux * ops[q].at + ox * dc, y, s.z0 + uz * ops[q].at + oz * dc);
                mm.name = 'head-flashing';
                g.add(mm);
              }
              hfBox(lenF, HFl.t, dF - dW, yT + HFl.t / 2, (dW + dF) / 2);          /* over the cavity and the trim */
              hfBox(lenF, HFl.up, HFl.t, yT + HFl.up / 2, dW + HFl.t / 2);         /* up the wrap */
              hfBox(lenF, HFl.lip, HFl.t, yT - HFl.lip / 2, dF - HFl.t / 2);      /* the drip */
              headFRun += lenF;
              headFNails += Math.ceil(lenF / HFl.step) + 1;
              if (mkPrev) _asmMark(ctx, mkPrev.key, mkPrev.dir, mkPrev.def, mkPrev.code, mkPrev.byKey);
            })();

            n++;
          }
        }

        if (!n) {
          ctx.log('WAL  reveal lining NONE -- ' + SEG.length + ' segment(s) and no ' +
                  'opening on any of them');
          return;
        }
        _add(LAYER, WALL_CLAD.code, area);
        _add(LAYER, WALL_CLAD.nail, Math.round(area / (WALL_CLAD.w * 0.3)) * 2);
        _extCoat(area);
        ctx.log('WAL  reveal lining ' + n + ' opening(s), ' + pieces + ' piece(s), ' +
                API.fmtArea(area) + ' more ' + WALL_CLAD.code + ' ripped to the ' +
                Math.round(dep * 1000) + ' mm reveal, from the frame at ' +
                Math.round(back * 1000) + ' to the finished face at ' + Math.round(face * 1000) +
                ' (it used to be ' + Math.round(face * 1000) + ', from the plan line, and ' +
                'left the strip back to the frame bare) -- two jambs and a head, no sill ' +
                'piece: the metal sill throws the water and boarding over it would dam it');
        _add(LAYER, WALL_CTRIM.code, trimRun);
        _extCoat(trimRun * WALL_CTRIM.leg * 2);
        /* garageapt-v30 (17b): the head flashings */
        if (GA_HEADF && headFRun > 0) {
          _add(LAYER, GA_HEADF.code, Math.round(headFRun * 100) / 100);
          _add(LAYER, GA_HEADF.nail, headFNails);
          ctx.log('WAL  head flashing (garageapt-v30, IRC R703.4 item 4) ' + n + ' head(s), ' + (Math.round(headFRun * 100) / 100) + ' m ' + GA_HEADF.code + ' -- up the wrap 2 in., across the cavity, over the head trim, a drip in front, 1 in. past each end; ' +
                  headFNails + ' x ' + GA_HEADF.nail + ' through the back leg every 300');
        }
        ctx.log('WAL  reveal trim ' + Math.round(trimRun * 10) / 10 + ' m of ' +
                WALL_CTRIM.code + ' round the openings -- two jambs and a head each, ' +
                'one leg on the face and one down the reveal, the same profile the ' +
                'building corners get and for the same reason');
        _why(ctx, 'wall     the same board rather than a separate profile: one product, ' +
                'one colour, one weathering, and the corner at the face is a joint ' +
                'between two pieces of the same thing');
      }

      function _wallPrism(T, ax, az, bx, bz, ox, oz, t, y0, hA, hB) {
        var nx = ox * t / 2, nz = oz * t / 2;
        var P = [
          [ax - nx, y0, az - nz], [bx - nx, y0, bz - nz], [bx - nx, y0 + hB, bz - nz], [ax - nx, y0 + hA, az - nz],
          [ax + nx, y0, az + nz], [bx + nx, y0, bz + nz], [bx + nx, y0 + hB, bz + nz], [ax + nx, y0 + hA, az + nz]
        ];
        var F = [[0, 1, 2], [0, 2, 3], [5, 4, 7], [5, 7, 6], [4, 0, 3], [4, 3, 7], [1, 5, 6], [1, 6, 2], [3, 2, 6], [3, 6, 7], [4, 5, 1], [4, 1, 0]];
        var pos = [], q;
        for (q = 0; q < F.length; q++) { var f = F[q], a = P[f[0]], b = P[f[1]], c = P[f[2]]; pos.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]); }
        var geo = new T.BufferGeometry();
        geo.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
        geo.computeVertexNormals();
        return geo;
      }

      function _wallGable(ctx) {
        /* taurus-v12: no sheathing anywhere under Matt's T&G -- the courses
           run on up the rake to the roof by themselves */
        if (TG && TG.on()) { ctx.log('WAL  gable sheathing none -- Matt\'s T&G runs up to the roof on its rails'); return; }
        if (!_wallStepOn(6)) return;
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, k;
        var mat = new T.MeshStandardMaterial({
          color: _wallNew('gable') ? MAGENTA : (_finTex(T, 'osb') ? 0xFFFFFF : 0xC8A063),
          map: _wallNew('gable') ? null : (_finTex(T, 'osb') || null),
          roughness: 0.95, metalness: 0.0 });
        var jMat = new T.MeshStandardMaterial({
          color: _wallNew('gable') ? MAGENTA : 0x6E5533, roughness: 0.95, metalness: 0.0 });

        var W = 1.200, TH = 0.018, JT = 0.006;
        var drawn = 0, sheets = 0, pieces = 0, walls = 0, gTops = [];
        var lifted = 0, liftMm = 0;

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          var G = null;
          try { G = _wallSkin(s); } catch (eG) {
            gTops.push('[PROFILE THREW ' + ((eG && eG.message) || '?') + ']');
            continue;
          }
          if (!G) {
            gTops.push('[' + Math.round((s.y0 + s.h) * 1000) + ' NO PROFILE]');
            continue;
          }
          walls++;

          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var along = Math.abs(ux) > Math.abs(uz);
          var y0 = G.top;

          /* the roof line over this wall, in the wall's own coordinate */
          /* AND IT STOPS SHORT OF THE DECK BY THE INTAKE.

             With the soffit gone the wall itself is what closes the building under
             the overhang, so the boarding runs all the way up to the top face of
             the trusses -- except for the slot the roof breathes through. That was
             a separate closure board with its own gap; it is the same board as
             this one and the same gap, so there is one of each now.

             The mesh over the slot is still drawn by the closure pass, which from
             here does nothing else. */
          /* AND A PARAPET HAS NOTHING TO VENTILATE AT ITS TOP.

             The 25 mm is the eaves intake -- air into the roof space, under the
             deck. A parapet has no roof space up there; it has a coping on it. So
             the boarding stopped 25 mm short of the top for a reason that does not
             exist there, and left a strip of bare stud under the cap. On a
             ventilated wall that strip is behind the cladding and on the vertical
             one, where the boards now run past the head in one piece, it is the
             thing he can see.

             The inset belongs to an eave. Under a coping the board goes to the
             top. */
          /* same as the skin: the slot belongs to a ventilated wall, so the
             sheathing runs to the top on a contact one */
          var vTop = (G.parapet || _cladOff()) ? 0 : WALL_EAVE.vent;
          function rakeY(u) { return y0 + Math.max(0, G.hAt(u) - vTop); }

          gTops.push('[head ' + Math.round(y0 * 1000) + ' rise ' +
                     Math.round(G.rise * 1000) + ' top ' +
                     Math.round(rakeY(G.at) * 1000) +
                     (G.lift ? (' lift ' + Math.round(G.lift * 1000)) : '') +
                     (G.parapet ? ' parapet' : '') + ']');

          /* the lift is accepted; the magenta dots that marked the old top are
             gone and only the count is kept for the log */
          if (G.lift > 0) { lifted++; liftMm = Math.round(G.lift * 1000); }

          /* which way this wall's own x runs against the shape's, worked out once
             for the wall rather than per board */
          var yaw0 = Math.atan2(ox, oz);
          var fwd0 = (oz * ux - ox * uz) > 0;

          /* AND THE PEAK IS A KINK, WHICH A STRAIGHT EDGE CANNOT DRAW.

             Every board's top is one straight line from its left edge height to
             its right edge height. That is exact on a slope and wrong at the
             apex: the board that straddles the ridge gets a line drawn straight
             ACROSS the peak, and the little triangle above that line is the gap he
             can still see through at the top.

             So the apex is a cut like any other. The board that would have
             straddled it becomes two, they meet under the ridge, and each one's
             top edge lies on a single slope where a straight line is the truth. */
          /* AND IT TURNS THE CORNER BY ITS OWN THICKNESS.

             The boarding stopped on the plan corner while everything over it
             carried on -- the EPS by a board, the render round both sides -- so at
             every corner there was a strip the width of the facade with the layers
             above it and no board under them. On the contact wall that is the bare
             patch he can see, always on the same side of each corner because the
             extension has always been at one end.

             Same rule, its own number: 18, so the two walls' boards butt exactly
             the way the walls' own sheathing does a storey below. */
          /* BOTH ENDS, LIKE EVERYTHING ELSE. This one still ran from the plan
             corner to one board past the far one, so above the head every corner
             was short at its NEAR end -- the same side of all four, which is the
             corner he keeps finding. The rule has been applied to the EPS, the
             coat, the render, the skirt and the skin; the sheathing above the head
             was the last one still doing it by halves. */
          var extG = TH;
          var cutsG = [], gq;
          for (gq = 0; -extG + gq * W < s.len + extG - 0.005; gq++) {
            cutsG.push(-extG + gq * W);
          }
          cutsG.push(s.len + extG);
          if (G.at > 0.02 && G.at < s.len - 0.02) cutsG.push(G.at);
          /* and wherever the profile kinks -- a hip line, a ridge that stops */
          cutsG.sort(function (p1, p2) { return p1 - p2; });

          var gi2;
          for (gi2 = 0; gi2 < cutsG.length - 1; gi2++) {
            var u0 = cutsG[gi2];
            var w = cutsG[gi2 + 1] - u0;
            if (!(w > 0.01)) continue;
            /* the tallest and the shortest edge of this board */
            var hA = rakeY(u0) - y0, hB = rakeY(u0 + w) - y0;
            var hi = Math.max(hA, hB), lo = Math.min(hA, hB);
            if (!(hi > 0.02)) continue;

            /* THE SHAPE IS MIRRORED ON HALF THE WALLS, AND ITS HEIGHTS WERE NOT.

               A shape is built in its own x, and on the walls whose direction runs
               against that x the whole board is turned round -- but I kept putting
               the tall corner at local x = 0 regardless. So on those walls the
               trapezium came out FLIPPED: high where the roof is low. That is the
               board standing through the roof on one end, and because the two
               gables face opposite ways it swaps sides between them, which is
               exactly what he described.

               Both edge heights are now read at the wall coordinates the shape's
               own edges land on, so the cut cannot disagree with the roof whatever
               way round the board is turned. */
            var e0 = fwd0 ? u0 : (u0 + w);
            var e1 = fwd0 ? (u0 + w - JT) : (u0 + JT);
            var h0 = Math.max(0.001, rakeY(e0) - y0);
            var h1 = Math.max(0.001, rakeY(e1) - y0);

            /* CUT ON THE RAKE, AND A BOX CANNOT BE.

               I drew each board as a box of the trapezium's MEAN height. The area
               was right and the shape was wrong: what came out was a row of
               rectangles stepping up the gable, no rake cut anywhere, and none of
               them sitting on the top edge of the truss. He read it exactly.

               So the board is a SHAPE -- four corners, two of them at different
               heights -- extruded by its own thickness. The sloping edge is then
               the roof line itself, and the whole triangle closes against the
               truss instead of approximating it in 1200 mm steps. */
            var h = (h0 + h1) / 2;
            var sh = new T.Shape();
            sh.moveTo(0, 0);
            sh.lineTo(w - JT, 0);
            sh.lineTo(w - JT, h1);
            sh.lineTo(0, h0);
            sh.lineTo(0, 0);
            var geo = new T.ExtrudeGeometry(sh, { depth: TH, bevelEnabled: false });
            var bm = new T.Mesh(geo, mat);
            /* the shape is built in its own XY with x along the wall, so it is
               turned to face out; where the wall's direction runs against the
               shape's x, it is turned the other way and started at the far end */
            /* AND THE TURN WAS THE WRONG CURE, WHICH IS WHY IT GOT WORSE.

               At the wall's own yaw the shape's +Z already points OUT of the wall
               -- that part was always right. Its +X points along the wall on half
               the walls and against it on the other half, and for those I added
               half a turn. That turn does put +X back along the wall, and it also
               spins +Z inward, and it makes the shape run FORWARD from a point
               chosen as if it ran backward. So every board landed a full board
               further along than it belonged: they piled into each other, the edges
               made teeth, and the whole triangle slid sideways -- one way on one
               gable and the other way on the other, because only one of the two is
               mirrored. That is his description exactly, and the extra turn is
               mine from the last version.

               No turn. The wall's yaw, always, so the face is out. Where +X runs
               against the wall the board simply starts at its FAR edge and the
               shape runs back to the near one -- which is what the two edge
               heights already allow for. */
            /* A MILLIMETRE CLEAR OF THE TIMBER, like every other layer here.

               The truss face and the board's back face were struck on the same
               plane -- the wall line -- so the two fought over every pixel. That
               is the collision he can see on the marked truss, and it is the same
               fault the coat, the film and the barrier all had to be moved off. */
            bm.rotation.y = yaw0;
            bm.position.set(s.x0 + ux * e0 + ox * 0.001, y0,
                            s.z0 + uz * e0 + oz * 0.001);
            /* NOT DRAWN HERE ANY MORE, AND NOT BILLED HERE EITHER.

               This board is the top of the wall sheathing now. The sheathing pass
               runs every column from the sole plate to whatever the roof leaves
               above it and cuts it on the rake where the two edges differ, so what
               this loop used to make is the upper half of a piece that already
               exists -- and the millimetre of offset between the two, which is the
               step at the storey line, cannot exist when there is only one.

               The loop is left standing because the CHECK lines below it are the
               only place the roof profile is read back and compared with the truss
               stations, and losing that would cost more than it saves. */
            bm.name = 'gable-sheathing';
            pieces++;

            /* AND NO JOINT STRIP EITHER. The board above is not added, but its
               joint still was -- a second set of dark strips above the head, on
               this pass's own set-out, drawn from the head to the LOWER of the
               board's two edge heights whatever the roof does at the strip. The
               sheathing pass draws the real joints with the real boards. */
          }
        }

        ctx.log('WAL  skin LIFTED ' + liftMm + ' mm on ' + lifted + ' rake wall(s): every ' +
                'facade layer on them now ends on the TOP edge of the truss chord / rafter ' +
                'instead of its underside (the chord depth measured plumb); the eave walls ' +
                'keep their blocks and vent, a hip gets nothing until its ends are framed');
        ctx.log('WAL  gable TOPS ' + (gTops.length ? gTops.join(' ') : 'none') +
                ' -- wall head, the rise the roof gives that wall, and the height the ' +
                'boarding was actually drawn to. Rise zero means the roof says there ' +
                'is nothing above that wall; top equal to head means the boards are ' +
                'being skipped after the profile said otherwise');
        if (!walls) {
          ctx.log('WAL  gable none -- ' +
                  (((API.STATE.roof || {}).type === 'gable')
                    ? 'no wall across the ridge'
                    : 'roof is ' + ((API.STATE.roof || {}).type || '?') +
                      ', which has no triangle over the wall head'));
          return;
        }
        if (!pieces) { ctx.log('WAL  gable none -- no rise over the wall head'); return; }
        _add(LAYER, SHEATH_NAIL.code, Math.round(drawn * SHEATH_NAIL.perM2));
        ctx.log('WAL  gable ' + walls + ' wall(s), ' + pieces + ' board(s) NOT DRAWN ' +
                'here: they are the top of the wall sheathing now, one piece each ' +
                'through the ceiling line, and the row went with them. What this pass ' +
                'still does is read the profile back and check it against the truss. ' +
                'It used to draw ' + API.fmtArea(drawn) + ' of 18 mm as ' +
                'extruded SHAPES cut on the rake -- standing, ' +
                Math.round(W * 1000) + ' wide on the wall own set-out so the joints ' +
                'line through from the head to the apex, run carried ' +
                Math.round(TH * 1000) + ' mm past the corner at BOTH ends so the two ' +
                'walls butt on either side');

        /* AND THE MEASUREMENT HE ASKED FOR, because I cannot see his screen.

           For each gable wall: where its plane is, and where the NEAREST truss
           station actually is. If those two do not agree the sheathing has nothing
           behind it, and this line says so in millimetres instead of leaving it to
           be argued about. */
        (function () {
          var ST = [], R2 = API.STATE.roof || {};
          try { ST = _trussStations() || []; } catch (eT) { ST = []; }
          var alongL2 = (R2.axis === 'l'), w2, k2;
          for (w2 = 0; w2 < SEG.length; w2++) {
            var sv = SEG[w2], Gv = _wallSkin(sv);
            if (!Gv) continue;
            var planeC = alongL2 ? sv.z0 : sv.x0;
            var best = null, bd = 1e9;
            for (k2 = 0; k2 < ST.length; k2++) {
              var d2 = Math.abs(ST[k2] - planeC);
              if (d2 < bd) { bd = d2; best = ST[k2]; }
            }
          }
        })();
        _why(ctx, 'wall     the area billed is the trapezium under the roof line, not the ' +
                'rectangle the board was cut from -- the offcut is the buyer waste, not ' +
                'the building');
      }

      function _wallGableSkin(ctx) {
        if (!_wallStepOn(9)) return;
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i;
        var contact = _cladOff();
        var osbT = 0.018;

        if (!contact) {
          return;
        }

        var mCoat = new T.MeshStandardMaterial({
          color: _wallNew('gskin') ? MAGENTA : 0xCFCCC2, roughness: 0.96, metalness: 0.0 });
        var mRend = new T.MeshStandardMaterial({
          color: _wallNew('gskin') ? MAGENTA : 0x9EA2A0, roughness: 0.93, metalness: 0.0 });
        var coatA = 0, rendA = 0, walls = 0, caps = 0;

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          var G = _wallSkin(s);
          if (!G) continue;
          walls++;
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var along = Math.abs(ux) > Math.abs(uz);
          var y0 = G.top, topY = y0 + G.rise;

          /* the level top edge, where there is one: on a parapet the whole wall,
             on a gable the width of the ridge tip; both ends carried round the
             corner by the facade's depth where they reach the wall ends */
          var sp = G.spanAtH(G.rise - 0.002);
          if (!sp) continue;
          var cext = _facadeFace();
          if (sp[1] >= s.len - 0.02) sp = [sp[0], s.len + cext];
          if (sp[0] <= 0.02) sp = [-cext, sp[1]];
          var capL = sp[1] - sp[0];
          var capW = WALL_EPS.t + WALL_COAT.t;
          var capMid = osbT + 0.001 + capW / 2;
          if (!(capL > 0.05) || !_wallWet()) continue;

          var cm5 = new T.Mesh(
            new T.BoxGeometry(along ? capL : capW, WALL_COAT.t, along ? capW : capL), mCoat);
          cm5.position.set(
            s.x0 + ux * (sp[0] + capL / 2) + ox * capMid,
            topY + WALL_COAT.t / 2,
            s.z0 + uz * (sp[0] + capL / 2) + oz * capMid);
          cm5.name = 'gable-coat';
          g.add(cm5);
          coatA += capL * capW;

          var rm5 = new T.Mesh(
            new T.BoxGeometry(along ? capL : capW + 2 * WALL_REND.t, WALL_REND.t,
                              along ? capW + 2 * WALL_REND.t : capL), mRend);
          rm5.position.set(
            s.x0 + ux * (sp[0] + capL / 2) + ox * capMid,
            topY + WALL_COAT.t + WALL_REND.pt + WALL_REND.t / 2,
            s.z0 + uz * (sp[0] + capL / 2) + oz * capMid);
          rm5.name = 'gable-render';
          g.add(rm5);
          rendA += capL * capW;
          caps++;
        }

        if (!walls) return;
        _add(LAYER, WALL_COAT.code, coatA);
        _add(LAYER, WALL_REND.prim, rendA);
        _add(LAYER, WALL_REND.code, rendA);
        ctx.log('WAL  gable skin CONTACT: EPS, mesh, base coat and render above the head are ' +
                'the wall passes\' own now (drawn and billed there, once); here only the cap ' +
                'over the top edge of the board: ' + caps + ' edge(s), ' + API.fmtArea(coatA) +
                ' coat, ' + API.fmtArea(rendA) + ' primer and render -- the EPS above the head ' +
                'was billed here as well as by _wallEps until v876, and is not any more');
      }

      function _wallEaveBlocks(ctx) {
        if (!_wallStepOn(6)) return;
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, k;
        var R = API.STATE.roof || {};
        if (R.type === 'flat') { ctx.log('WAL  eaves closure none -- flat roof'); return; }

        var ST = [];
        try { ST = _trussStations() || []; } catch (e) { ST = []; }
        if (ST.length < 2) { ctx.log('WAL  eaves closure none -- no truss set-out'); return; }

        var alongL = (R.axis === 'l');
        var mB = new T.MeshStandardMaterial({
          color: _wallNew('eaveblock') ? MAGENTA : (_finTex(T, 'osb') ? 0xFFFFFF : 0xC8A063),
          map: _wallNew('eaveblock') ? null : (_finTex(T, 'osb') || null),
          roughness: 0.95, metalness: 0.0 });
        /* NOT METALLIC. With no environment map a metalness of 0.5 rendered the
           strip near-black whatever its colour -- a black band in the shadow
           under the eave is a band nobody can see, magenta included. It is a
           mesh, not a mirror: matte, and it takes the light the closures take. */
        /* A NET, NOT A SHEET: the same open weave the facade mesh is drawn with,
           strands only and the holes clear, so the gap shows through it. Magenta
           strands while under review, dark grey after. */
        var mTexE = _membTex(T, _wallNew('eavestrip') ? 'eavestrip-m' : 'eavestrip', false,
                             _wallNew('eavestrip') ? '#FF00FF' : '#2E3134',
                             _wallNew('eavestrip') ? 'rgba(255,0,255,0.9)' : 'rgba(46,49,52,0.9)');
        if (mTexE) { mTexE.wrapS = mTexE.wrapT = T.RepeatWrapping; mTexE.repeat.set(5, 1); }
        var mM = mTexE
          ? new T.MeshStandardMaterial({
              map: mTexE, roughness: 0.9, metalness: 0.0, side: T.DoubleSide,
              transparent: true, depthWrite: false, alphaTest: 0.05 })
          : new T.MeshStandardMaterial({
              color: _wallNew('eavestrip') ? MAGENTA : 0x4F5357,
              roughness: 0.9, metalness: 0.0, side: T.DoubleSide });

        var wt = API.wallThickness();
        var area = 0, meshRun = 0, bays = 0, walls = 0, baffleM = 0, tallyGarBay = 0;
        /* SEE-THROUGH WHILE THE STRIP IS UNDER REVIEW: the dam stands on the
           inner face of the wall, full height, and from inside the attic it hides
           the closure and the strip that stand 160 mm further out -- which is why
           four rounds of magenta on the strip showed him nothing. */
        var mBaf = new T.MeshStandardMaterial({
          color: _wallNew('baffle') ? MAGENTA : 0x6E7B8B,
          roughness: 0.9, metalness: 0.02, side: T.DoubleSide });
        /* THE BAY GOES UP TO THE DECK. The closure filled the space from the wall
           head to the UNDERSIDE of the member -- the heel -- and stopped there,
           while the opening between two members carries on for the member's own
           depth up to the deck. That is the hole he was looking at, with the
           strip built below it. The member depth is the truss chord on a trussed
           roof and the rafter on a mono, measured plumb. */
        var sectD = _isTrussed() ? CHORD.d : roofSection().d;
        var chordUp = sectD / Math.cos((R.pitch || 0) * Math.PI / 180);

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2)) continue;
          if (GA && GA.under(s)) continue;   /* garageapt-v5: the eave is the top storey's */
          /* the porch's walls are under the roof: no eave there (ranch-v5) */
          if (_atRoofEdge && !_atRoofEdge(s)) continue;
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var along = Math.abs(ux) > Math.abs(uz);

          /* the trusses repeat along one axis; the walls they BEAR on are the ones
             that run along that same axis, so those are the ones with bays --
             AND ON A HIP THE END WALLS TOO. A hip end is carried by jack trusses
             set out from the middle of the end wall both ways at the truss step,
             so its bays run between those, corner to corner, and get the same
             blocking, strip and baffle the eaves do (Marek: the hip ends had no
             closures and no mesh). */
          var hipRoof = (R.type === 'hip');
          var bearing = alongL ? !along : along;
          /* NO BAYS WHERE THERE ARE NO MEMBERS. The hip ends are not framed yet
             (the trusses stop at the girders; jacks and hip rafters are still to
             come), so a closure set out there was closing bays between members
             that do not exist -- and it showed (Marek). Until the hip framing is
             modelled the ends get nothing, and the log says so. */
          var hipEnd = hipRoof && !bearing && !!API.hipAt(API.ridgeEndOf(s) || 'lo');
          if (!bearing && !hipEnd) continue;
          walls++;

          var y0 = s.y0 + s.h;
          var ox = s.nx, oz = s.nz;

          /* the stations in this wall's own coordinate */
          var STw = [], kw;
          if (hipRoof) {
            /* FROM THE MEMBERS THAT WERE ACTUALLY DRAWN. The roof publishes every
               member with its run (_memberPts: x, z, dirX, dirZ) -- commons, the
               side-slope jacks, each hip end's centre truss and jacks -- and the
               wall builds after the roof. A member that runs ACROSS this wall
               is a station on it, projected onto the wall's line; nothing is set
               out a second time by a rule of its own, which is what kept
               dividing the closures where the members were not (Marek). */
            var mq2, nAcross = 0;
            for (mq2 = 0; mq2 < (_memberPts || []).length; mq2++) {
              var MP = _memberPts[mq2]; if (!MP || MP.length < 4) continue;
              var mdl = Math.hypot(MP[2], MP[3]) || 1, alongWall = Math.abs((MP[2] * ux + MP[3] * uz) / mdl);
              if (alongWall > 0.3) continue;   /* runs along this wall: not a member on it */
              /* AND IT HAS TO CROSS THE WALL'S LINE. A side-slope jack out in
                 the overhang runs from the hip to the eave and never reaches the
                 wall -- the closures were being cut at it all the same (Marek).
                 With both ends published, the test is the sign of each end's
                 distance from the wall line; no ends published -> the old
                 half-depth test. */
              var crosses;
              if (MP.length >= 8) {
                var dA = (MP[4] - s.x0) * ox + (MP[5] - s.z0) * oz, dB = (MP[6] - s.x0) * ox + (MP[7] - s.z0) * oz;
                crosses = (Math.min(dA, dB) < 0.02 && Math.max(dA, dB) > -0.02);
              } else {
                var BB = _buildRect(), depthAcross = (Math.abs(s.z1 - s.z0) > Math.abs(s.x1 - s.x0)) ? (BB.x1 - BB.x0) : (BB.z1 - BB.z0);
                crosses = (Math.abs((MP[0] - s.x0) * ox + (MP[1] - s.z0) * oz) <= depthAcross / 2 + 0.3);
              }
              if (!crosses) continue;
              var uM = (MP[0] - s.x0) * ux + (MP[1] - s.z0) * uz;
              if (uM > 0.02 && uM < s.len - 0.02) { STw.push(uM); nAcross++; }
            }
            /* the corners close the end bays on a HIPPED end; at the gable end of
               a half-hip the end truss stands on the wall face and there is no bay past it */
            var alongZw = Math.abs(s.z1 - s.z0) > Math.abs(s.x1 - s.x0);
            var startLow = alongZw ? (s.z0 < s.z1) : (s.x0 < s.x1);
            if (hipEnd || API.hipAt(startLow ? 'lo' : 'hi')) STw.push(0);
            if (hipEnd || API.hipAt(startLow ? 'hi' : 'lo')) STw.push(s.len);
            STw.sort(function (p1, p2) { return p1 - p2; });
            var dd = [], sj;
            for (sj = 0; sj < STw.length; sj++) if (!dd.length || STw[sj] - dd[dd.length - 1] > 0.05) dd.push(STw[sj]);
            STw = dd;
            if (H_hipJacks) H_hipJacks.setUsed(_hipSideJacks.join(','));
            ctx.log('WAL  eaves ' + (hipEnd ? 'hip end' : 'hip side') + ' W' + i + ': ' + (STw.length - 1) + ' bay(s) on ' + nAcross + ' published member(s) across it' + (nAcross ? '' : ' -- NONE published, only the corners'));
          } else {
            for (kw = 0; kw < ST.length; kw++) {
              STw.push(along ? ((ST[kw] - s.x0) / (ux || 1)) : ((ST[kw] - s.z0) / (uz || 1)));
            }
            STw.sort(function (p1, p2) { return p1 - p2; });
          }

          for (k = 0; k < STw.length - 1; k++) {
            var u0 = STw[k], u1 = STw[k + 1];
            /* half a member off each station -- at a corner of a hip end there is
               no member on the station, so the bay runs to the wall's end */
            var atCorner0 = (hipEnd || hipRoof) && k === 0, atCorner1 = (hipEnd || hipRoof) && k === STw.length - 2;
            var a = u0 + (atCorner0 ? 0 : CHORD.t / 2);
            var b = u1 - (atCorner1 ? 0 : CHORD.t / 2);
            a = Math.max(a, 0); b = Math.min(b, s.len);
            var L = b - a;
            if (!(L > 0.05)) continue;

            var mid = a + L / 2;
            if (k === 0) bayNote = [];
            var rY = _roofYAtXZ(s.x0 + ux * mid, s.z0 + uz * mid);
            /* from the wall head to the underside of the DECK, less the slot --
               at BOTH ends of the bay, since the deck may climb along the wall */
            var H = (rY === null ? chordUp : (rY - y0 + chordUp)) - WALL_EAVE.vent;
            var rYa = _roofYAtXZ(s.x0 + ux * (a + 0.01), s.z0 + uz * (a + 0.01)), rYb = _roofYAtXZ(s.x0 + ux * (b - 0.01), s.z0 + uz * (b - 0.01));
            var Ha = (rYa === null ? H : (rYa - y0 + chordUp - WALL_EAVE.vent)), Hb = (rYb === null ? H : (rYb - y0 + chordUp - WALL_EAVE.vent));
            Ha = Math.max(0.03, Ha); Hb = Math.max(0.03, Hb);
            if (hipRoof) bayNote.push(k + ':' + Math.round(a * 100) / 100 + '-' + Math.round(b * 100) / 100 + ' H ' + Math.round(Ha * 1000) + '/' + Math.round(Hb * 1000) +
                                      (rYa === null || rYb === null ? ' (plane MISSING at an end)' : ''));
            if (k === 0) ctx.log('WAL  eave W' + i + ' first bay: roof plane at the wall line ' + (rY === null ? 'NONE' : Math.round(rY * 1000)) + ', wall head ' + Math.round(y0 * 1000) +
                                 ', chord up ' + Math.round(chordUp * 1000) + ', block H ' + Math.round(H * 1000) + ' (over ' + (R.over ? Math.round((R.over[along ? (oz > 0 ? 'z1' : 'z0') : (ox > 0 ? 'x1' : 'x0')] || 0) * 1000) : '?') + ' overhang)');
            if (!(H > 0.03)) continue;

            /* THE BOARD IS BACK, AND TAKING IT OUT WAS MY MISTAKE.

               I called it a duplicate of the wall sheathing. It is not. The
               sheathing is the SKIN, on the outside face of the wall plane; this
               closes the BAY, in the depth of the truss, between one truss and the
               next. They are at right angles to each other in the build and only
               looked alike because both are 18 mm of board at the same height.

               Taking it out left the eaves open and left him looking for an
               assembly that was no longer drawn. It sits behind the sheathing
               where it belongs. */
            /* the prism runs along the wall: local x is the wall's direction, so
               it is turned for a wall that runs along z */
            var inB = WALL_EAVE.boardT / 2 + 0.001;
            var bm = new T.Mesh(_wallPrism(T, s.x0 + ux * a - ox * inB, s.z0 + uz * a - oz * inB, s.x0 + ux * b - ox * inB, s.z0 + uz * b - oz * inB,
                                           ox, oz, WALL_EAVE.boardT, y0, Ha, Hb), mB);
            bm.name = 'eaves-closure';
            /* KEYED, NOT CODED (adu-v1402): the code is the wall OSB's too, so
               by code the walk could not put the closure anywhere but with the
               facade; by its own key it goes in after the blown insulation */
            _asmMark(ctx, 'eave_closure', 'in', 'Eave closure', 'OSB25002');
            g.add(bm);
            area += L * H;

            /* AND THE MESH IS DRAWN AT A THICKNESS THAT READS.

               Two millimetres of dark strip tucked under a roof is invisible from
               anywhere anyone stands, which is why he could not find it either. It
               is drawn 20 mm deep -- it is a mesh in a 40 mm slot, so that is
               nearer the truth than 2 was, and it can be seen. */
            /* THE STRIP IS A STRIP: 100 mm of expanded mesh on the outer face of
               the closure, over its top edge and the slot -- as tiny stands it in
               the top of every block -- not a 25 mm sliver in the slot that nobody
               could find. Opaque, so it reads. */
            /* IN THE SLOT, STANDING IN THE TOP OF THE CLOSURE -- the way tiny
               stands it in the top of every eave block. In the plane of the
               board: the top 25 mm fills the slot and shows from BOTH sides,
               inside the attic and outside under the eave; the rest is in the
               board. On the outer face it could only be seen from outside, and
               he was looking from inside at the closures. */
            /* IN THE GAP, STANDING IN THE TOP OF THE BLOCKING -- tiny's detail:
               a 100 mm strip in the plane of the board, its top half in the 50 mm
               gap under the deck where it shows from both sides, the rest on the
               board. The blocking stops 50 short of the deck (Marek: a 25 slot was
               neither a vent nor visible); the gap is the eave's intake and the
               strip is what keeps birds and mice out of it. */
            var sW = 0.100, sT = 0.003, sIn = WALL_EAVE.boardT / 2 + 0.001;
            /* the strip follows the same slope: its top is the block's top plus
               the slot at each end, its foot a strip's width under the lower end */
            var sBase = Math.min(Ha, Hb) + WALL_EAVE.vent - sW;
            var vm = new T.Mesh(_wallPrism(T, s.x0 + ux * a - ox * sIn, s.z0 + uz * a - oz * sIn, s.x0 + ux * b - ox * sIn, s.z0 + uz * b - oz * sIn,
                                           ox, oz, sT, y0 + sBase, Ha + WALL_EAVE.vent - sBase, Hb + WALL_EAVE.vent - sBase), mM);
            vm.name = 'eaves-vent-strip';
            _asmMark(ctx, 'eave_vent_strip', 'top', 'Eaves ventilation strip', 'ROV00100');
            g.add(vm);
            meshRun += L;

            /* THE BAFFLE: a dam at the inner face of the wall, a chute under the
               deck from the closure inward over the fill. The roof over this bay
               climbs inward square to the wall, so both are set out along the
               wall's own inward normal. */
            var px0 = s.x0 + ux * mid, pz0 = s.z0 + uz * mid;
            var yPlan = _roofYAtXZ(px0, pz0);
            var runIn = wt + WALL_EAVE.chuteRun;
            var yIn = _roofYAtXZ(px0 - ox * runIn, pz0 - oz * runIn);
            var yWall = _roofYAtXZ(px0 - ox * wt, pz0 - oz * wt);
            /* ranch-v39: over the garage no fill is blown, so the bay keeps its
               closure and vent strip and needs no dam or chute */
            var garBay = false;
            if (H_garageAt) { try { garBay = !!H_garageAt(px0 - ox * (wt + 0.40), pz0 - oz * (wt + 0.40)); } catch (eGB) { garBay = false; } }
            if (garBay) { tallyGarBay = (tallyGarBay || 0) + 1; bays++; continue; }
            if (yPlan !== null && yIn !== null && yWall !== null) {
              var bt = WALL_EAVE.baffleT;
              /* THE ROOF OVER THIS BAY MAY CLIMB ANY WAY -- inward under a side
                 plane, ALONG the wall under a hip end's plane in the corners. So
                 the dam and the chute are set out from the roof height at their
                 own corners, not tilted about the wall's axis (Marek: in the
                 corners the chute climbed inward into a roof that did not). */
              var chuteDrop = chordUp - WALL_EAVE.chute;   /* the chute's top: a channel under the deck */
              function rY(uu, inw) { var yy = _roofYAtXZ(s.x0 + ux * uu - ox * inw, s.z0 + uz * uu - oz * inw); return (yy === null) ? null : (yy + chuteDrop); }
              var aA = a + 0.005, bB = b - 0.005;
              var yDamA = rY(aA, wt), yDamB = rY(bB, wt);
              if (yDamA !== null && yDamB !== null) {
                /* the dam: on the inner face, foot at the wall head, top under the chute at each end */
                var dHA = yDamA - bt - y0, dHB = yDamB - bt - y0;
                if (dHA > 0.03 && dHB > 0.03) {
                  var dm = new T.Mesh(_wallPrism(T, s.x0 + ux * aA - ox * (wt + bt / 2), s.z0 + uz * aA - oz * (wt + bt / 2),
                                                 s.x0 + ux * bB - ox * (wt + bt / 2), s.z0 + uz * bB - oz * (wt + bt / 2), ox, oz, bt, y0, dHA, dHB), mBaf);
                  dm.name = 'eave-baffle-dam';
                  _asmMark(ctx, 'eave_baffle', 'top', 'Eave vent baffle', 'ROV00101');
                  g.add(dm);
                }
              }
              /* the chute: a sheet whose four corners sit a channel under the deck,
                 from the closure to chuteRun past the inner face */
              var c00 = rY(aA, 0), c10 = rY(bB, 0), c01 = rY(aA, runIn), c11 = rY(bB, runIn);
              if (c00 !== null && c10 !== null && c01 !== null && c11 !== null) {
                var Pq = [
                  [s.x0 + ux * aA, c00, s.z0 + uz * aA], [s.x0 + ux * bB, c10, s.z0 + uz * bB],
                  [s.x0 + ux * bB - ox * runIn, c11, s.z0 + uz * bB - oz * runIn], [s.x0 + ux * aA - ox * runIn, c01, s.z0 + uz * aA - oz * runIn]
                ];
                var posC = [], fq, top = [], bot = [];
                for (fq = 0; fq < 4; fq++) { top.push([Pq[fq][0], Pq[fq][1], Pq[fq][2]]); bot.push([Pq[fq][0], Pq[fq][1] - bt, Pq[fq][2]]); }
                function tri(A1, B1, C1) { posC.push(A1[0], A1[1], A1[2], B1[0], B1[1], B1[2], C1[0], C1[1], C1[2]); }
                tri(top[0], top[1], top[2]); tri(top[0], top[2], top[3]);
                tri(bot[2], bot[1], bot[0]); tri(bot[3], bot[2], bot[0]);
                for (fq = 0; fq < 4; fq++) { var n1 = (fq + 1) % 4; tri(top[fq], bot[fq], bot[n1]); tri(top[fq], bot[n1], top[n1]); }
                var geoC = new T.BufferGeometry();
                geoC.setAttribute('position', new T.Float32BufferAttribute(posC, 3));
                geoC.computeVertexNormals();
                var cm7 = new T.Mesh(geoC, mBaf);
                cm7.name = 'eave-baffle-chute';
                _asmMark(ctx, 'eave_baffle', 'top', 'Eave vent baffle', 'ROV00101');
                g.add(cm7);
                baffleM += L;
              }
            }
            bays++;
          }
          if (hipRoof && bayNote.length) ctx.log('WAL  eave W' + i + ' bays (u from-to, H at each end mm): ' + bayNote.join(' | '));
        }

        if (!bays) { ctx.log('WAL  eaves closure none -- no bay found on a bearing wall'); return; }
        if (R.type === 'hip') {
        }
        _add(LAYER, 'sheet.osb18', area);
        _add(LAYER, SHEATH_NAIL.code, Math.round(area * SHEATH_NAIL.perM2));
        ctx.log('WAL  eave blocking ' + bays + ' bay(s) on ' + walls + ' bearing wall(s), ' +
                API.fmtArea(area) + ' of ' + Math.round(WALL_EAVE.boardT * 1000) +
                ' mm OSB cut to the bay, wall head to the underside of the deck less the ' +
                'slot -- raised-heel eave blocking, sheet rather than 2x stock because the ' +
                'bay is taller than any block; behind the sheathing, at right angles to it');
        _add(LAYER, WALL_EAVE.strip, meshRun);
        ctx.log('WAL  eaves vent strip ' + Math.round(meshRun * 10) / 10 + ' m of ' +
                WALL_EAVE.strip + ' in the ' + Math.round(WALL_EAVE.vent * 1000) +
                ' mm gap between the blocking and the deck -- tiny\'s expanded-mesh strip, ' +
                'the catalogue row for exactly this; the rodent mesh ' + WALL_EAVE.mesh +
                ' stays over the cavity foot');
        _add(LAYER, WALL_EAVE.baffle, baffleM);
        if (tallyGarBay) ctx.log('GAR  eave baffles left out in ' + tallyGarBay + ' bay(s) over the garage -- no fill there to dam or vent over; closure and vent strip stay (ranch-v39)');
        ctx.log('WAL  eave baffles ' + bays + ' bay(s), ' + Math.round(baffleM * 10) / 10 +
                ' m of ' + WALL_EAVE.baffle + ' -- a dam ' + Math.round(WALL_EAVE.baffleT * 1000) +
                ' mm on the inner face of the wall from the head up to the chute, and a chute ' +
                'under the deck ' + Math.round(WALL_EAVE.chute * 1000) + ' mm clear of it from ' +
                'the closure to ' + Math.round(WALL_EAVE.chuteRun * 1000) + ' past the inner ' +
                'face, so the blown fill stops at the wall and the eave air gets over it');
        _why(ctx, 'wall     closed and not closed: solid it rots the deck from underneath, ' +
                'open it is a door for wind, birds and mice -- so a slot the whole ' +
                'length of the building, and a mesh over the slot');
      }

      function _wallSoffit(ctx) {
        if (!_wallStepOn(9)) return;
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, k;
        var R = API.STATE.roof || {};
        if (R.type === 'flat') { ctx.log('WAL  soffit none -- flat roof'); return; }

        /* NOT ON A GABLE, ANYWHERE, INCLUDING UNDER THE OVERHANG.

           A product decision and his: the trusses stay open from below. It is the
           look the house is for -- you stand under the eave and see the timber
           that holds the roof up, rather than a lid over it -- and it is one less
           assembly to build, ventilate and eventually replace.

           What closes the building is then the FACADE, carried up to the top face
           of the trusses, and that is the next step. Nothing is left open by this
           one; the closing line simply moves from under the overhang back to the
           wall where it belongs. */
        /* AND NOT ON A HIP EITHER, WHICH IS HIM CHANGING HIS MIND FOR A REASON.

           The hip soffit has cost four versions and the last one put boarding
           ABOVE the roof, which is as wrong as it gets. The gable already works
           the other way -- no soffit, trusses open from below, the facade carried
           up the wall to the top of them -- and it works because there is nothing
           to line up with: the wall closes itself and the timber is simply seen.

           So a hip does the same. What is left is the one place a hip really does
           overhang past a wall with nothing under it, and that gets boarded
           BETWEEN THE PLATES, across them, which is its own step -- and now its
           own function, _wallHipSoffit, written from his spec rather than from a
           fifth guess. The hip leaves here for it.

           A mono keeps its soffit: there the rafter tails are the eave and there
           is no wall under them to climb. */
        if (R.type === 'gable') {
          ctx.log('WAL  soffit none on a gable -- the trusses stay open from below ' +
                  'and the facade closes the wall up to their top face instead');
          return;
        }
        if (R.type === 'hip') {
          ctx.log('WAL  soffit none on the hip EAVES -- open trusses, as on a gable; ' +
                  'the projecting end is boarded between the plates below');
          _wallHipSoffit(ctx);
          return;
        }

        var O = _roofOver();
        var alongL = (R.axis === 'l');
        var isHip = (R.type === 'hip');
        var face = _facadeFace();

        var mBoard = new T.MeshStandardMaterial({
          color: _wallNew('soffit') ? MAGENTA : colorOf('timber'),
          roughness: 0.86, metalness: 0.0 });
        var mRail = new T.MeshStandardMaterial({
          color: _wallNew('soffit') ? MAGENTA : colorOf('timber'), roughness: 0.9, metalness: 0.0 });

        var mMesh = new T.MeshStandardMaterial({
          color: _wallNew('soffit') ? MAGENTA : 0x6B6F72,
          roughness: 0.6, metalness: 0.5, side: T.DoubleSide,
          transparent: true, opacity: 0.75 });
        var mTrim = new T.MeshStandardMaterial({
          color: _wallNew('soffit') ? MAGENTA : colorOf('timber'),
          roughness: 0.86, metalness: 0.0 });

        var area = 0, railRun = 0, walls = 0, skipped = 0, ventRun = 0, trimRun = 0;
        var corners = 0;

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!(s.len > 0.2)) continue;
          if (GA && GA.under(s)) continue;   /* garageapt-v5: the soffit is the top storey's */
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var ox = s.nx, oz = s.nz;
          var along = Math.abs(ux) > Math.abs(uz);

          if (_atRoofEdge && !_atRoofEdge(s)) { skipped++; continue; }   /* the porch's walls (ranch-v5) */
          /* an eave wall carries the trusses; a rake wall is a gable */
          var bearing = alongL ? !along : along;
          if (!isHip && !bearing) { skipped++; continue; }

          /* how far the roof reaches past this wall, on this wall's own side */
          var over = Math.abs(ox) > Math.abs(oz)
            ? (ox < 0 ? O.x0 : O.x1)
            : (oz < 0 ? O.z0 : O.z1);
          var dep = over - face;
          if (!(dep > 0.05)) { skipped++; continue; }
          walls++;

          /* AND ROUND THE CORNER, ONCE.

             Each wall boarded its own length and stopped on the plan corner, so
             the square of overhang where two eaves meet had nothing under it --
             the hole he can see in every corner of a hip. The soffit is one
             surface and it turns the corner.

             The far end carries on by the NEIGHBOUR's overhang, which is exactly
             the depth of the missing square. Every wall does it at the same end
             and the polygon runs one way, so each corner is boarded once: no gap
             and no double.

             It is billed at the extended length, because that square is real
             boarding that somebody cuts and fixes. */
          var nb = null, q8;
          for (q8 = 0; q8 < SEG.length; q8++) {
            if (q8 === i || !SEG[q8] || !(SEG[q8].len > 0.2)) continue;
            if (Math.abs(SEG[q8].x0 - s.x1) < 0.01 && Math.abs(SEG[q8].z0 - s.z1) < 0.01) {
              nb = SEG[q8]; break;
            }
          }
          var extB = 0;
          if (nb) {
            extB = Math.abs(nb.nx) > Math.abs(nb.nz)
              ? (nb.nx < 0 ? O.x0 : O.x1)
              : (nb.nz < 0 ? O.z0 : O.z1);
          }
          /* THE RUN STOPS AT THE WALL. THE CORNER IS ITS OWN PIECE.

             Carrying the long run past the corner boarded the square with planks
             that keep running the same way -- so at a corner the boarding met end
             grain to face and had nothing under it but air, because the trusses
             there run the other way.

             What a roofer does instead is board that square ACROSS: the planks sit
             on the members that are actually there, and the joint at the corner is
             a straight line between two directions rather than a lap between two
             lengths. It also means the square needs no battens of its own to hold
             it up, which is what he asked for.

             So the run is the wall, and each corner gets its own patch below. */
          var runL = s.len;

          /* THE SOFFIT FOLLOWS THE ROOF, IT DOES NOT HANG UNDER IT.

             It was drawn dead level at the wall head, which is two faults at once
             on a mono: on the HIGH side the roof is well above that line, so the
             boarding sat down at the ceiling instead of up under the rafters; on
             the LOW side the roof falls across the overhang while the boarding
             stayed flat, so the two crossed.

             A soffit under a pitched roof is raked -- it is fixed to the underside
             of the rafters and runs parallel to them. So the height is asked of
             the roof at each distance out, and the boards are tilted to match. On
             a hip and a gable eave that slope is the roof's own pitch; on a flat
             it would be nothing, and there is no soffit there anyway. */
          var mid = runL / 2;

          /* THE PLANE IS THE UNDERSIDE ALREADY. I KEPT SUBTRACTING FROM IT.

             I guessed twice -- first the span section, then the chord -- and both
             times the soffit went too low. He told me to stop guessing and find
             the rafter, so I did, and the answer was written in the roof's own
             comment forty lines from where I was looking:

               ON THE PLANE, NOT UNDER IT. The plane the core hands back is the
               BEARING line -- the top of the plate the rafter lands on -- so the
               rafter stands on it.

             The rafter sits ABOVE the plane. Its underside IS the plane, its top
             is a section higher and the deck is above that. So there is nothing to
             take off: the soffit goes on the plane, and every millimetre I
             subtracted was a millimetre of gap between the boarding and the timber
             it is supposed to be screwed to.

             Only the mono reaches here now -- the gable and the hip have no soffit
             -- and a mono is rafters, so this is the whole answer. */
          var underD = 0;

          function yAtD(d) {
            var px = s.x0 + ux * mid + ox * d, pz = s.z0 + uz * mid + oz * d;
            var ry = _roofYAtXZ(px, pz);
            if (ry === null) ry = _roofYAtXZ(s.x0 + ux * mid, s.z0 + uz * mid);
            return (ry === null ? (s.y0 + s.h + underD) : ry) - underD;
          }

          /* RAKED ON A MONO, LEVEL EVERYWHERE ELSE.

             I raked every soffit at once, which fixed the mono and broke the hip.
             A hip eave is boxed: the boarding is level and the triangle between it
             and the rafters is closed at the fascia. That is what he had and
             approved. A mono is not boxed -- there the soffit is fixed straight to
             the underside of the rafters and runs with them, which is what he
             asked for and what a level board there cannot do.

             So the pitch of the soffit is a decision per roof, not a formula for
             all of them. */
          var raked = (R.type === 'mono');
          var yIn = yAtD(face), yOut = yAtD(face + dep);
          var slope = raked ? Math.atan2(yOut - yIn, dep) : 0;
          /* which way a turn about the wall's own axis drops the far edge */
          var tilt = along ? ((oz > 0 ? -1 : 1) * slope) : ((ox > 0 ? 1 : -1) * slope);

          /* level ones sit at the rafter soffit where it meets the WALL, which is
             the high point of the eave and the line a boxed soffit is set from */
          function yUse(d) { return raked ? yAtD(d) : yIn; }

          ctx.log('WAL  soffit ' + (raked ? 'RAKED' : 'LEVEL') + ' on this wall: head ' +
                  Math.round((s.y0 + s.h) * 1000) + ', rafter soffit at the wall ' +
                  Math.round(yIn * 1000) + ', at the eave ' + Math.round(yOut * 1000) +
                  ' -- ON the plane, because the plane IS the bearing line and the ' +
                  'rafter stands on it; nothing is taken off (section ' +
                  Math.round(roofSection().d * 1000) + ', deck ' +
                  Math.round(RAFTER.deck * 1000) + ', both of them above this line)');

          /* the rails, out from the facade at a stride the board can span --
             their own assembly step, named from their catalogue row (adu-v1397:
             the soffit was one step with no code, the one name in the walk that
             was not the BoQ's) */
          _asmMark(ctx, 'soffit', 'top', 'Soffit', WALL_SOFF.rail);
          var d;
          for (d = WALL_SOFF.railF / 2; d < dep; d += WALL_SOFF.railStep) {
            var dd = Math.min(d, dep - WALL_SOFF.railF / 2);
            var rm = new T.Mesh(
              new T.BoxGeometry(along ? runL : WALL_SOFF.railF, WALL_SOFF.railD,
                                along ? WALL_SOFF.railF : runL), mRail);
            rm.position.set(
              s.x0 + ux * mid + ox * (face + dd),
              yUse(face + dd) - WALL_SOFF.railD / 2,
              s.z0 + uz * mid + oz * (face + dd));
            rm.name = 'soffit-rail';
            g.add(rm);
            railRun += runL;
          }

          /* AND THE AIR HAS TO GET IN SOMEWHERE.

             The closure over the wall plate has a slot in it, and a slot with a
             boarded soffit sealed across the front of it is a slot into a closed
             box. The intake is here, at the OUTER edge against the fascia, where
             wind pressure is and where rain is not: the boarding stops 25 mm short
             and the gap is meshed, exactly as the closure is.

             So the two belong together and are drawn together -- one path in at
             the fascia, one path on at the plate, and the roof breathes end to
             end. */
          var boarded = dep - WALL_SOFF.vent;

          /* and the boarding under them, running along the wall */
          _asmMark(ctx, 'soffit_boards', 'top', 'Soffit boards', WALL_CLAD.code);
          var pitch = WALL_CLAD.w + WALL_CLAD.gap, p;
          for (p = 0; p < boarded - 0.005; p += pitch) {
            var w = Math.min(WALL_CLAD.w, boarded - p);
            if (!(w > 0.02)) continue;
            var bm = new T.Mesh(
              new T.BoxGeometry(along ? runL : w, WALL_CLAD.t,
                                along ? w : runL), mBoard);
            if (along) bm.rotation.x = tilt; else bm.rotation.z = tilt;
            bm.position.set(
              s.x0 + ux * mid + ox * (face + p + w / 2),
              yUse(face + p + w / 2) - WALL_SOFF.railD - WALL_CLAD.t / 2,
              s.z0 + uz * mid + oz * (face + p + w / 2));
            bm.name = 'soffit-board';
            g.add(bm);
            area += runL * w;
          }

          /* THE CORNER PATCH, BOARDED ACROSS.

             It fills the square between this wall's overhang and the neighbour's,
             with the boards turned ninety degrees so they cross the members
             instead of lying between them. One patch per corner, on the same far
             end every wall extends everything else, so no corner is done twice. */
          /* AND NOT ON A MONO, WHICH IS WHERE HE IS SEEING THEM FLOAT.

             The corner patch belongs to a boxed eave: level boarding, two runs
             meeting at a right angle, a square between them. A mono has neither --
             its soffit is raked, and the two walls it meets at a corner are at
             different heights there, so the patch lands at one of them and hangs
             in the air beside the other. Top on one side, bottom on the other,
             exactly as he describes.

             There is no square to fill on a mono: the raked soffit runs out to the
             rake and the verge closes it. */
          if (extB > 0.05 && !raked) {
            var cw9 = extB, cd9 = dep, p9;
            for (p9 = 0; p9 < cd9 - 0.005; p9 += (WALL_CLAD.w + WALL_CLAD.gap)) {
              var w9 = Math.min(WALL_CLAD.w, cd9 - p9);
              if (!(w9 > 0.02)) continue;
              /* runs ACROSS the wall: long in the outward direction, stepping
                 along the wall instead of the other way round */
              var cm9 = new T.Mesh(
                new T.BoxGeometry(along ? w9 : cw9, WALL_CLAD.t,
                                  along ? cw9 : w9), mBoard);
              cm9.position.set(
                s.x0 + ux * (s.len + cw9 / 2) + ox * (face + p9 + w9 / 2),
                yUse(face + p9 + w9 / 2) - WALL_SOFF.railD - WALL_CLAD.t / 2,
                s.z0 + uz * (s.len + cw9 / 2) + oz * (face + p9 + w9 / 2));
              cm9.name = 'soffit-board-corner';
              g.add(cm9);
              area += cw9 * w9;
            }
            corners++;
          }

          /* the mesh over the intake */
          var vm2 = new T.Mesh(
            new T.BoxGeometry(along ? runL : WALL_SOFF.vent, 0.002,
                              along ? WALL_SOFF.vent : runL), mMesh);
          vm2.position.set(
            s.x0 + ux * mid + ox * (face + dep - WALL_SOFF.vent / 2),
            yUse(face + dep - WALL_SOFF.vent / 2) - WALL_SOFF.railD - 0.001,
            s.z0 + uz * mid + oz * (face + dep - WALL_SOFF.vent / 2));
          vm2.name = 'soffit-vent-mesh';
          g.add(vm2);
          ventRun += runL;

          /* AND A TRIM AT EACH END OF IT.

             Where the soffit meets the wall the boards' cut ends face down at head
             height, and where it meets the fascia there is the intake -- both want
             a profile over them, and it is the one the corners already use rather
             than a third article invented for the purpose. */
          var tq;
          for (tq = 0; tq < 2; tq++) {
            var at2 = tq ? (face + dep - WALL_CTRIM.t / 2) : (face + WALL_CTRIM.t / 2);
            var tm2 = new T.Mesh(
              new T.BoxGeometry(along ? runL : WALL_CTRIM.t, WALL_CTRIM.leg,
                                along ? WALL_CTRIM.t : runL), mTrim);
            tm2.position.set(
              s.x0 + ux * mid + ox * at2,
              yUse(at2) - WALL_SOFF.railD - WALL_CLAD.t - WALL_CTRIM.leg / 2,
              s.z0 + uz * mid + oz * at2);
            tm2.name = 'soffit-trim';
            g.add(tm2);
            trimRun += runL;
          }
        }

        if (!walls) {
          ctx.log('WAL  soffit none -- ' + skipped + ' wall(s) skipped: a rake has no ' +
                  'soffit, and an overhang inside the facade has nothing to board');
          return;
        }
        _add(LAYER, WALL_CLAD.code, area);
        _add(LAYER, WALL_CLAD.nail,
             Math.round(area / (WALL_CLAD.w * WALL_CLAD.nailStep)) * 2);
        _add(LAYER, WALL_SOFF.rail, railRun);
        _extCoat(area);
        _add(LAYER, WALL_SOFF.screw,
             Math.max(2, Math.round(railRun / WALL_SOFF.railStep)));
        ctx.log('WAL  soffit ' + walls + ' wall(s)' +
                (skipped ? (', ' + skipped + ' skipped') : '') + ', ' +
                API.fmtArea(area) + ' of ' + WALL_CLAD.code + ' on ' +
                Math.round(railRun * 10) / 10 + ' m of ' + WALL_SOFF.rail +
                ' at ' + Math.round(WALL_SOFF.railStep * 1000) + ' mm -- ' +
                (isHip ? 'hip: every side is an eave, so all four'
                       : 'eave walls only; the rake is closed by the verge, not from below') +
                '; the runs stop at the walls and ' + corners + ' corner square(s) are ' +
                'boarded ACROSS, so the planks cross the members that are there ' +
                'instead of lying between them and need no battens of their own');
        ctx.log('WAL  soffit RAKED -- its height is asked of the roof at each distance ' +
                'out and the boards are tilted to match, so on a mono the high side ' +
                'sits under the rafters and the low side falls with the roof instead ' +
                'of crossing it -- and LEVEL on a hip, which is a boxed eave and is ' +
                'what it was before I raked all of them at once');
        _add(LAYER, WALL_SOFF.trim, trimRun);
        _extCoat(trimRun * WALL_CTRIM.leg * 2);
        ctx.log('WAL  soffit depth is what stands past the FINISHED facade, not past the ' +
                'frame -- the facade already fills the first ' + Math.round(face * 1000) +
                ' mm of the overhang and boarding over that is boarding over the cladding');
        ctx.log('WAL  soffit trims ' + Math.round(trimRun * 10) / 10 + ' m of ' +
                WALL_SOFF.trim + ' -- one at the wall where the cut ends face down, one ' +
                'at the fascia over the intake; the corner profile, not a third article');
        _add(LAYER, WALL_EAVE.mesh, ventRun);
        ctx.log('WAL  soffit intake ' + Math.round(WALL_SOFF.vent * 1000) + ' mm along ' +
                Math.round(ventRun * 10) / 10 + ' m at the fascia edge, meshed with ' +
                WALL_EAVE.mesh + ' -- a slot at the plate with a sealed soffit in front ' +
                'of it is a slot into a closed box');
      }

      function _wallHipSoffit(ctx) {
        if (!_wallStepOn(9)) return;
        var T = ctx.THREE, g = ctx.group;
        var R = API.STATE.roof || {};
        if (R.type !== 'hip') return;

        var O = _roofOver();
        var B = _buildRect();
        var face = _facadeFace();
        var TR = EAVE_POST.trigger, PW = EAVE_POST.w;
        var deep = { x0: O.x0 > TR, x1: O.x1 > TR, z0: O.z0 > TR, z1: O.z1 > TR };

        var mBoard = new T.MeshStandardMaterial({
          color: colorOf('timber'), roughness: 0.86, metalness: 0.0 });

        var wallTop = API.wallTop();
        var plane = wallTop;
        var xIn0 = B.x0 + STUD.d, xIn1 = B.x1 - STUD.d;
        var runX = xIn1 - xIn0;
        var cx = (xIn0 + xIn1) / 2;
        var pitch = WALL_CLAD.w + WALL_CLAD.gap;

        var area = 0, ends = 0, boards = 0, sgz;
        /* anyhouse-lib-v13, THE HIPPED L: each hipped end boarded across its
           own rectangle -- the bar's two ends the bar's width, the wing's far
           end the wing's -- rather than the outline's box (which hung boards
           in the air past the bar's end) */
        var LE = (_kinkOn() && H_hipEndsL) ? H_hipEndsL() : null;
        if (LE) {
          LE.forEach(function (E) {
            var ovL = O[E.side], reachL = _plateEndReach(ovL), nearL = E.edge + E.sg * face, farL = E.edge + E.sg * reachL, depL = E.sg * (farL - nearL);
            if (deep[E.side]) ctx.log('WAL  hip soffit ' + E.side + ' (L): an overhang past the post trigger -- the posts are not set out on the L yet, CHECK');
            if (!(depL > 0.05)) { ctx.log('WAL  hip soffit ' + E.side + ' (L): none -- less than a board past the facade'); return; }
            ends++;
            var a0 = E.a0 + STUD.d, a1 = E.a1 - STUD.d, runA = a1 - a0, nL = 0, pL;
            for (pL = 0; pL < depL - 0.005; pL += pitch) {
              var wL = Math.min(WALL_CLAD.w, depL - pL);
              if (!(wL > 0.02)) continue;
              var bmL = new T.Mesh(E.x ? new T.BoxGeometry(wL, WALL_CLAD.t, runA) : new T.BoxGeometry(runA, WALL_CLAD.t, wL), mBoard);
              var cAl = nearL + E.sg * (pL + wL / 2), cAc = (a0 + a1) / 2;
              bmL.position.set(E.x ? cAl : cAc, plane - WALL_CLAD.t / 2, E.x ? cAc : cAl);
              bmL.name = 'hip-soffit-board';
              g.add(bmL);
              area += runA * wL; nL++;
            }
            boards += nL;
            ctx.log('WAL  hip soffit ' + E.side + ' (L, ' + E.what + '): ' + nL + ' boards of ' + WALL_CLAD.code + ', ' + Math.round(depL * 1000) + ' deep from the facade face, ' + Math.round(runA * 1000) + ' across between the plates');
          });
        }
        for (sgz = (LE ? 3 : -1); sgz <= 1; sgz += 2) {
          var tag = (sgz < 0) ? 'z0' : 'z1';
          /* a half-hip: the gable end keeps its gable, no hip soffit there */
          if (!API.hipAt(sgz < 0 ? 'lo' : 'hi')) { ctx.log('WAL  hip soffit ' + tag + ': none -- a gable end'); continue; }
          var ovv = (sgz < 0) ? O.z0 : O.z1;
          var edge = (sgz < 0) ? B.z0 : B.z1;
          var reach = _plateEndReach(ovv);
          var near = edge + sgz * face;
          var plateEnd = edge + sgz * reach;

          /* what stands at the far end: nothing, one post, or two posts with the
             double profile across them -- the same three answers _eavePosts gives */
          var deepZ = (sgz < 0) ? deep.z0 : deep.z1;
          var nPosts = deepZ ? ((deep.x0 ? 0 : 1) + (deep.x1 ? 0 : 1)) : 0;
          var stop = nPosts === 2 ? 'frame' : (nPosts === 1 ? 'post' : 'end');
          var frameOff = nPosts ? (PW / 2 + CHORD.d / 2) : 0;
          var far = plateEnd - sgz * frameOff;
          var dep = sgz * (far - near);

          if (!(dep > 0.05)) {
            ctx.log('WAL  hip soffit ' + tag + ': none -- the plates end ' +
                    Math.round(reach * 1000) + ' past the frame line, the facade ' +
                    'fills ' + Math.round(face * 1000) + ' of that' +
                    (frameOff ? (' and the ' + stop + ' another ' + Math.round(frameOff * 1000)) : '') +
                    ', which leaves ' + Math.round(dep * 1000) + ' mm: less than a board');
            continue;
          }
          ends++;

          var n = 0, p;
          for (p = 0; p < dep - 0.005; p += pitch) {
            var w = Math.min(WALL_CLAD.w, dep - p);
            if (!(w > 0.02)) continue;
            var bm = new T.Mesh(new T.BoxGeometry(runX, WALL_CLAD.t, w), mBoard);
            bm.position.set(cx, plane - WALL_CLAD.t / 2, near + sgz * (p + w / 2));
            bm.name = 'hip-soffit-board';
            g.add(bm);
            area += runX * w;
            n++;
          }
          boards += n;

          ctx.log('WAL  hip soffit ' + tag + ': z ' + Math.round(near * 1000) + ' -> ' +
                  Math.round(far * 1000) + ' (' + Math.round(dep * 1000) + ' deep), x ' +
                  Math.round(xIn0 * 1000) + ' -> ' + Math.round(xIn1 * 1000) +
                  ' (' + Math.round(runX * 1000) + ' wide, BETWEEN the plates, ' +
                  Math.round(STUD.d * 1000) + ' in from each frame line), ' +
                  n + ' boards of ' + WALL_CLAD.code + ' parallel to the end wall -- ' +
                  'starts at the facade face (' + Math.round(face * 1000) +
                  ' past the frame), stops at the ' +
                  (stop === 'frame' ? ('frame: inner face of the profile across the posts, ' +
                                       Math.round(frameOff * 1000) + ' inside the plate end ' +
                                       Math.round(plateEnd * 1000))
                   : stop === 'post' ? ('post line, ' + Math.round(frameOff * 1000) +
                                        ' inside the plate end ' + Math.round(plateEnd * 1000) +
                                        ' -- one post only at this end, no profile across')
                   : ('plate end ' + Math.round(plateEnd * 1000) + ', overhang ' +
                      Math.round(ovv * 1000) + ' under the post trigger ' +
                      Math.round(TR * 1000))));
        }

        ctx.log('WAL  hip soffit plane ' + Math.round(plane * 1000) + ' = wallTop, the top ' +
                'of the double plate and the underside of the trusses, level; boards ' +
                Math.round(WALL_CLAD.t * 1000) + ' below it, so they sit in the plate ' +
                'zone between the two plates (plate bottom ' +
                Math.round((wallTop - 2 * STUD.t) * 1000) + ', posts and braces all under ' +
                'or beside that)');

        if (!ends) {
          ctx.log('WAL  hip soffit none -- neither end projects past the facade');
          return;
        }
        var nails = Math.round(area / (WALL_CLAD.w * WALL_CLAD.nailStep)) * 2;
        _add(LAYER, WALL_CLAD.code, area);
        _add(LAYER, WALL_CLAD.nail, nails);
        _extCoat(area);
        ctx.log('WAL  hip soffit ' + ends + ' end(s), ' + boards + ' boards, ' +
                API.fmtArea(area) + ' of ' + WALL_CLAD.code + ' and ' + nails + ' x ' +
                WALL_CLAD.nail + ' (two per board every ' +
                Math.round(WALL_CLAD.nailStep * 1000) + ', the cladding rate) billed ' +
                'to ' + LAYER + ', area added to the exterior coating; no rail, trim or ' +
                'intake -- nothing between the plates to fix a rail to until the ' +
                'California fill, and the intake is the eave\'s and the eave stays open');
      }

      function _wallCornerBeads(ctx) {
        if (!_wallStepOn(9)) return;
        /* A VENTILATED WALL HAS NO BEADS, BUT IT STILL HAS CORNERS.

           The scan was hung on the end of this function and this function leaves
           on the first line when the facade is ventilated -- so on the very build
           he is looking at, it never ran and nothing was painted. It runs on the
           way out instead. */
        if (!_cladOff()) return;

        /* THE BEAD IS BEDDED ON THE BOARD, NOT STOOD ON THE FINISHED FACE.

           I gave it _facadeFace, which is the outside of the whole build-up --
           past the coat and past the render. So the angle and its wings were the
           outermost thing on the corner and the coat could never cover them; they
           showed through, which is what he can see.

           On site the angle beds on the insulation, its wings lie over it, and
           everything after that goes ON TOP. So it sits a millimetre and a half
           off the EPS face: the coat starts outside that and buries the lot, and
           no thickness has to be invented to hide anything. */
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, k;
        /* adu-v1430: the beads and their mesh wings are bedded under the coat
           like the field net -- buried once the coat is drawn, so not drawn */
        var gB = _wallWet() ? { add: function () {} } : g;
        var face = 0.018 + 0.001 + WALL_EPS.t + 0.0015;
        var drop = _skirtDrop();

        var galv = new T.MeshStandardMaterial({
          color: _wallNew('bead') ? MAGENTA : 0xD7DCDF,
          roughness: 0.45, metalness: 0.2 });
        var wingM = new T.MeshStandardMaterial({
          color: _wallNew('bead') ? MAGENTA : 0xC4402C,
          roughness: 0.9, metalness: 0.0,
          transparent: true, opacity: 0.85, depthWrite: true, side: T.DoubleSide });

        /* the reveals below still ask for a mesh face by size; they get the same
           wing material, which no longer needs a texture cloned per piece */
        function beadMat() { return wingM; }

        function riseAt(px, pz, headY) {
          var R = API.STATE.roof || {};
          if (R.type === 'flat') {
            if (!R.parapet) return 0;
            return roofSection().d + RAFTER.deck + PARAPET_UPSTAND;
          }
          var ry = _roofYAtXZ(px, pz);
          if (ry === null) return 0;
          return Math.max(0, ry - headY);
        }

        var run = 0, beads = 0, diag = [];

        for (i = 0; i < SEG.length; i++) {
          var a = SEG[i];
          if (!a || !(a.len > 0.2)) continue;

          var b = null;
          for (k = 0; k < SEG.length; k++) {
            if (k === i || !SEG[k] || !(SEG[k].len > 0.2)) continue;
            if (Math.abs(SEG[k].x0 - a.x1) < 0.01 &&
                Math.abs(SEG[k].z0 - a.z1) < 0.01) { b = SEG[k]; break; }
          }
          if (!b) continue;

          var aux = (a.x1 - a.x0) / a.len, auz = (a.z1 - a.z0) / a.len;
          var bux = (b.x1 - b.x0) / b.len, buz = (b.z1 - b.z0) / b.len;

          /* outward means the next wall leans away from this one's outside face */
          if ((aux * b.nx + auz * b.nz) < 0) continue;

          var headY = a.y0 + a.h;
          /* THE LOWER OF THE TWO WALLS, NOT THE HIGHER.

             A bead is an arris between two rendered faces, so it can only run as
             high as BOTH walls' insulation goes. Taking the higher one put a bead
             178 mm up the gable's corner where the eave wall beside it has no EPS
             above its head at all -- a bead with one face, standing out of the
             facade. The corner trim on the boarded wall has always followed the
             lower wall for the same reason. */
          var rise = Math.min(riseAt(a.x1 - aux * 0.15, a.z1 - auz * 0.15, headY) + _wallLift(a),
                              riseAt(b.x0 + bux * 0.15, b.z0 + buz * 0.15, headY) + _wallLift(b));
          var yLo = a.y0 - drop;
          var yHi = headY + rise;
          var H = yHi - yLo;
          if (!(H > 0.05)) continue;

          diag.push('[' + Math.round(yLo * 1000) + '..' + Math.round(yHi * 1000) + ']');

          /* the arris: the plan corner pushed out on both faces at once */
          var cx = a.x1 + a.nx * face + b.nx * face;
          var cz = a.z1 + a.nz * face + b.nz * face;

          var legs = [[aux, auz, WALL_BEAD.legA, -1],
                      [bux, buz, WALL_BEAD.legB, 1]];
          var q;
          for (q = 0; q < legs.length; q++) {
            var dx = legs[q][0], dz = legs[q][1], lg = legs[q][2], sg = legs[q][3];
            var alo = Math.abs(dx) > Math.abs(dz);

            var m = new T.Mesh(
              new T.BoxGeometry(alo ? lg : WALL_BEAD.metalT, H,
                                alo ? WALL_BEAD.metalT : lg), galv);
            m.position.set(cx + sg * dx * (lg / 2), (yLo + yHi) / 2,
                           cz + sg * dz * (lg / 2));
            m.name = 'eps-corner-bead';
            gB.add(m);

            var w = new T.Mesh(
              new T.BoxGeometry(alo ? WALL_BEAD.wing : 0.0015, H,
                                alo ? 0.0015 : WALL_BEAD.wing), wingM);
            w.position.set(cx + sg * dx * (WALL_BEAD.wing / 2), (yLo + yHi) / 2,
                           cz + sg * dz * (WALL_BEAD.wing / 2));
            w.name = 'eps-corner-bead-mesh';
            gB.add(w);
          }

          var nose = new T.Mesh(new T.BoxGeometry(0.008, H, 0.008), galv);
          nose.position.set(cx, (yLo + yHi) / 2, cz);
          nose.name = 'eps-corner-bead';
          gB.add(nose);

          run += H;
          beads++;
        }

        if (!beads) {
          ctx.log('WAL  corner beads NONE -- no outward junction on ' +
                  SEG.length + ' segment(s)');
        } else {
          _add(LAYER, WALL_BEAD.code, run);
          ctx.log('WAL  corner beads bedded ' + Math.round((face - (0.018 + 0.001 +
                WALL_EPS.t)) * 1000) + ' mm off the EPS face, so the base coat at ' +
                Math.round((WALL_COAT.gap + WALL_COAT.t) * 1000) +
                ' mm covers the angle and both wings -- it used to sit on the ' +
                'FINISHED face, outside everything, which is why it showed through');
        ctx.log('WAL  corner beads ' + beads + ' corner(s), ' +
                  Math.round(run * 10) / 10 + ' m of ' + WALL_BEAD.code + ' with a ' +
                  Math.round(WALL_BEAD.wing * 1000) + ' mm mesh wing on each leg -- ' +
                  'REBUILT: one pass, foot of the skirt to the top of the contact ' +
                  'facade, no climb worked out anywhere else');
          ctx.log('WAL  corner beads span per corner ' + diag.join(' ') +
                  ' mm -- bottom and top of each, so a short bead is a number now');
        }

        /* AND THE REVEALS, WHICH ARE CORNERS TOO.

           A 140 board turned into an opening makes three more arrises per hole --
           two jambs and a head -- and they take more knocks than the building's
           own corners do, because that is where things get carried through. Same
           profile and the same wings. The SILL is left out on purpose: that arris
           belongs to the window's own flashing, not to the render. */
        var rev = 0, jambRun = 0, headRun = 0, apuRun = 0, q5, jq;
        for (i = 0; i < SEG.length; i++) {
          var s2 = SEG[i];
          if (!s2 || !(s2.len > 0.2)) continue;
          var u2x = (s2.x1 - s2.x0) / s2.len, u2z = (s2.z1 - s2.z0) / s2.len;
          var alng = Math.abs(u2x) > Math.abs(u2z);
          var ops5 = s2.openings || [];
          for (q5 = 0; q5 < ops5.length; q5++) {
            var oW = ops5[q5].w;
            var oY0 = s2.y0 + ops5[q5].sill, oY1 = oY0 + ops5[q5].h;
            var oH = oY1 - oY0;
            if (!(oH > 0.05) || !(oW > 0.05)) continue;

            for (jq = 0; jq < 2; jq++) {
              var sgn = jq ? 1 : -1;
              var at5 = ops5[q5].at + sgn * (oW / 2 - WALL_EPS_REV);

              /* the angle on the arris, bedded on the board */
              var am = new T.Mesh(
                new T.BoxGeometry(alng ? WALL_BEAD.legA : WALL_BEAD.metalT, oH,
                                  alng ? WALL_BEAD.metalT : WALL_BEAD.legA), galv);
              am.position.set(
                s2.x0 + u2x * (at5 + sgn * WALL_BEAD.legA / 2) + s2.nx * face,
                (oY0 + oY1) / 2,
                s2.z0 + u2z * (at5 + sgn * WALL_BEAD.legA / 2) + s2.nz * face);
              am.name = 'eps-reveal-bead';
              gB.add(am);

              /* its wing OVER the top of it, on the face of the wall */
              var wm = new T.Mesh(
                new T.BoxGeometry(alng ? WALL_BEAD.wing : WALL_BEAD.t, oH,
                                  alng ? WALL_BEAD.t : WALL_BEAD.wing),
                beadMat(WALL_BEAD.wing, oH));
              wm.position.set(
                s2.x0 + u2x * (at5 + sgn * WALL_BEAD.wing / 2) + s2.nx * (face + 0.0013),
                (oY0 + oY1) / 2,
                s2.z0 + u2z * (at5 + sgn * WALL_BEAD.wing / 2) + s2.nz * (face + 0.0013));
              wm.name = 'eps-reveal-bead-mesh';
              gB.add(wm);

              /* AND THE OTHER WING, TURNED INTO THE REVEAL.

                 A bead on a jamb has two legs like any other, and the second one
                 goes down the return face of the board. Without it the mesh
                 stopped dead on the arris and the reveal itself -- the face you
                 actually look at when you stand in the opening -- had nothing on
                 it at all. Its depth is the board, because that is how deep the
                 reveal is. */
              /* THE TURN INTO THE REVEAL IS A TURN, NOT A LINING.

                 It was 140 -- the thickness of the insulation -- so the textile
                 ran the full depth of the reveal and out the other side of
                 wherever the frame is. That is the strip he keeps finding in his
                 openings, and it is on the jambs for the same reason the head one
                 was: I used the board thickness because it was the number to
                 hand.

                 What actually turns in is the bead's own wing, and it turns far
                 enough to hold the arris and no further. Thirty millimetres. One
                 constant, so if it is wrong it is wrong in one place and by a
                 number rather than by an assumption. */
              var rd = WALL_BEAD.turn;
              var rw = new T.Mesh(
                new T.BoxGeometry(alng ? WALL_BEAD.t : rd, oH,
                                  alng ? rd : WALL_BEAD.t),
                beadMat(rd, oH));
              rw.position.set(
                s2.x0 + u2x * (at5 - sgn * 0.0025) + s2.nx * (face - rd / 2),
                (oY0 + oY1) / 2,
                s2.z0 + u2z * (at5 - sgn * 0.0025) + s2.nz * (face - rd / 2));
              rw.name = 'eps-reveal-bead-mesh';
              gB.add(rw);

              jambRun += oH;
            }

            /* THE HEAD IS ABOVE THE HEAD LINE, NOT BELOW IT.

               Every piece of it was centred a leg or a wing DOWN from oY1, which
               put the metal and 225 mm of textile straight across the top of the
               opening -- the horizontal band over the glass he is looking at. The
               arris is AT oY1; the wall is above it and the soffit is behind it,
               so the wall pieces go up from that line and only the soffit return
               goes back from it. */
            var hm = new T.Mesh(
              new T.BoxGeometry(alng ? oW : WALL_BEAD.metalT, WALL_BEAD.legA,
                                alng ? WALL_BEAD.metalT : oW), galv);
            hm.position.set(
              s2.x0 + u2x * ops5[q5].at + s2.nx * face,
              oY1 + WALL_BEAD.legA / 2,
              s2.z0 + u2z * ops5[q5].at + s2.nz * face);
            hm.name = 'eps-reveal-bead';
            gB.add(hm);

            var hwm = new T.Mesh(
              new T.BoxGeometry(alng ? oW : WALL_BEAD.t, WALL_BEAD.wing,
                                alng ? WALL_BEAD.t : oW),
              beadMat(oW, WALL_BEAD.wing));
            hwm.position.set(
              s2.x0 + u2x * ops5[q5].at + s2.nx * (face + 0.0013),
              oY1 + WALL_BEAD.wing / 2,
              s2.z0 + u2z * ops5[q5].at + s2.nz * (face + 0.0013));
            hwm.name = 'eps-reveal-bead-mesh';
            gB.add(hwm);

            /* and the return along the soffit, which is the one piece that does
               go back from the line rather than up from it */
            /* THE SOFFIT RETURN AT THE HEAD IS OUT.

               Twice now I have tried to size it -- first the board thickness, then
               a guessed reveal depth -- and both times it came out as a shelf of
               textile hanging over the glass. It is real on site: the mesh does
               turn onto the underside of the head. But the model does not know
               where the frame sits, so every depth I give it is a guess, and a
               guess drawn 140 or 77 mm into an opening is worse than nothing.

               It comes out. The jambs still return, because their depth is the one
               the reveal profile already carries; the head waits until the opening
               tells us where its frame is, and then it is arithmetic rather than a
               number I chose. */
            headRun += oW;
            apuRun += oW + 2 * oH;
            rev++;
          }
        }

        if (rev) {
          _add(LAYER, WALL_BEAD.code, jambRun);
          _add(LAYER, WALL_BEAD.drip, headRun);
          _add(LAYER, WALL_BEAD.apu, apuRun);
          ctx.log('WAL  reveal profiles ' + rev + ' opening(s): jambs ' +
                  Math.round(jambRun * 10) / 10 + ' m ' + WALL_BEAD.code +
                  ', heads ' + Math.round(headRun * 10) / 10 + ' m ' + WALL_BEAD.drip +
                  ', frame junction ' + Math.round(apuRun * 10) / 10 + ' m ' +
                  WALL_BEAD.apu + ' -- three products, three jobs, three rows');
          ctx.log('WAL  reveal profiles no SILL bead: that arris belongs to the window ' +
                  'flashing. Angle bedded on the board, textile OVER it and turned ' +
                  Math.round(WALL_BEAD.turn * 1000) + ' mm into the reveal -- far ' +
                  'enough to hold the arris and no further. It used to be 140, the ' +
                  'thickness of the board, which ran the textile the whole depth of ' +
                  'the reveal and past wherever the frame sits');
        }
      }

      function _wallExtCoating(ctx) {
        if (!(_extCoatNow > 0.01)) {
          ctx.log('WAL  exterior coating none -- no bare timber outside');
          return;
        }
        /* THE CORE COUNTS THE PAINT NOW, off every timber body in the model
           -- the frame, the posts, the rafters, the terrace, not just what
           this one pass happened to draw. Billing here as well would charge
           the cladding twice (Marek). */
        /* _add(...MFP00002, _extCoatNow) -- moved to the core's coating pass */
        ctx.log('WAL  exterior coating ' + API.fmtArea(_extCoatNow) + ' of MFP00002 over ' +
                'the cladding, the reveal linings, the soffits and the trims -- every ' +
                'square metre of timber outdoors, billed by the SURFACE once; how many ' +
                'coats belongs in the row and not in the quantity');
        _why(ctx, 'wall     it counts what was DRAWN, so a rendered wall contributes ' +
                'nothing and only the soffit and the trims are left to paint');
      }

      function _wallOpeningScan(ctx) {
        if (!_wallStepOn(9)) return;
        if (!_cladOff()) return;
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg || [], i, q, k;
        if (!g || !g.children) return;
        g.updateMatrixWorld(true);

        var netN = [], coatN = [], kid;
        for (k = 0; k < g.children.length; k++) {
          kid = g.children[k];
          if (!kid || !kid.name) continue;
          if (kid.name.indexOf('vent') >= 0) continue;
          if (kid.name.indexOf('mesh') >= 0) netN.push(kid);
          else if (kid.name.indexOf('coat') >= 0) coatN.push(kid);
        }

        /* AN OBJECT IN THE WALL'S OWN FRAME. A world box says nothing about a
           wall that does not run north-south, so the box corners are projected
           onto the segment's own axes: how far along, how high, how far out. */
        function localBox(s, obj) {
          var b = new T.Box3().setFromObject(obj);
          if (!b || b.isEmpty()) return null;
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
          var xs = [b.min.x, b.max.x], zs = [b.min.z, b.max.z];
          var uLo = 1e9, uHi = -1e9, nLo = 1e9, nHi = -1e9, a, c2, px, pz, uu, nn;
          for (a = 0; a < 2; a++) {
            for (c2 = 0; c2 < 2; c2++) {
              px = xs[a] - s.x0; pz = zs[c2] - s.z0;
              uu = px * ux + pz * uz;
              nn = px * s.nx + pz * s.nz;
              if (uu < uLo) uLo = uu;
              if (uu > uHi) uHi = uu;
              if (nn < nLo) nLo = nn;
              if (nn > nHi) nHi = nn;
            }
          }
          return { uLo: uLo, uHi: uHi, nLo: nLo, nHi: nHi, yLo: b.min.y, yHi: b.max.y };
        }

        /* the hole, shrunk 4 mm all round, so a jamb bead ON the arris and a
           sill UNDER it are not reported as intruders */
        var IN = 0.004, OUT_LO = -0.05, OUT_HI = 0.45, seen = 0;

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          if (!s || !(s.len > 0.2)) continue;
          var ops = s.openings || [];
          for (q = 0; q < ops.length; q++) {
            var oA = ops[q].at - ops[q].w / 2 + IN;
            var oB = ops[q].at + ops[q].w / 2 - IN;
            var oY0 = s.y0 + ops[q].sill, oY1 = oY0 + ops[q].h;
            if (!(oB - oA > 0.02) || !(oY1 - oY0 > 0.05)) continue;
            var hit = [], lb, du, dy;
            for (k = 0; k < netN.length; k++) {
              lb = localBox(s, netN[k]);
              if (!lb) continue;
              if (lb.nHi < OUT_LO || lb.nLo > OUT_HI) continue;
              du = Math.min(lb.uHi, oB) - Math.max(lb.uLo, oA);
              dy = Math.min(lb.yHi, oY1 - IN) - Math.max(lb.yLo, oY0 + IN);
              if (!(du > 0.002) || !(dy > 0.002)) continue;
              hit.push(netN[k].name + ' ' + Math.round(du * 1000) + 'x' +
                       Math.round(dy * 1000) + ' from y ' +
                       Math.round((Math.max(lb.yLo, oY0) - s.y0) * 1000));
              if (!netN[k].__scanHit) {
                netN[k].__scanHit = true;
                netN[k].material = new T.MeshStandardMaterial({
                  color: MAGENTA, roughness: 0.9, metalness: 0.0,
                  side: T.DoubleSide, depthWrite: true });
              }
            }
            /* and whether anything wet is over the middle of the head at all */
            var pu = ops[q].at, py = oY1 + 0.05, cov = 0, cb;
            for (k = 0; k < coatN.length; k++) {
              cb = localBox(s, coatN[k]);
              if (!cb) continue;
              if (cb.nHi < OUT_LO || cb.nLo > OUT_HI) continue;
              if (pu > cb.uLo - 0.002 && pu < cb.uHi + 0.002 &&
                  py > cb.yLo - 0.002 && py < cb.yHi + 0.002) cov++;
            }
            ctx.log('WAL  scan ' + s.id + ' op' + q + ' ' + (ops[q].kind || 'unit') +
                    ' w ' + Math.round(ops[q].w * 1000) +
                    ' sill ' + Math.round(ops[q].sill * 1000) +
                    ' head ' + Math.round((oY1 - s.y0) * 1000) +
                    ' wall ' + Math.round(s.h * 1000) + ' -- ' +
                    (hit.length ? ('IN THE HOLE: ' + hit.join(' | '))
                                : 'textile clear') +
                    ', coat 50 over the head ' + (cov ? (cov + ' piece(s)') : 'NONE'));
            seen++;
          }
        }
        ctx.log('WAL  scan ' + seen + ' opening(s) measured against ' + netN.length +
                ' textile piece(s) and ' + coatN.length + ' wet piece(s) -- the hole ' +
                'is shrunk 4 mm all round, so a bead on the arris is not an intruder; ' +
                'anything reported IN THE HOLE is painted magenta');
      }

      return { battens: _wallBattens, board: _wallBoard, wool: _wallWool, vb: _wallVB, batt: _wallBatt,
               intSills: _wallIntSills,
               sheathing: _wallSheathing, memb: _wallMemb, facadeBattens: _wallFacadeBattens,
               cladding: _wallCladding, eps: _wallEps, mesh: _wallMesh, coat: _wallCoat, render: _wallRender,
               skirt: _wallSkirt, extSills: _wallExtSills, cornerTrim: _wallCornerTrim,
               revealLining: _wallRevealLining,
               gable: _wallGable, gableSkin: _wallGableSkin, eaveBlocks: _wallEaveBlocks,
               soffit: _wallSoffit, hipSoffit: _wallHipSoffit, cornerBeads: _wallCornerBeads,
               extCoating: _wallExtCoating, openingScan: _wallOpeningScan };
    }

    /* =========================================================================
       THE FLOOR MODULE (core-v779, the sixth shared module of the house line,
       its first part -- status/modules.md 6). The timber floor written once:
       the rim, the centre beam, the joists and the blocking, the deck (OSB
       sheets), the build-up in the bays (rails, cover board, breather tray,
       wool, vapour barrier) and over them, the rooms' finishes and skirting
       room by room (wet rooms tiled, the skirting cut at the doorways, the
       slanted walls), with the bill and the fixings. The ADU's text as ANY
       HOUSE carries it.

         API.houseFloor(ctx, {
           api, layer ('adu-floor'), wood (the timber colour), layers
           (FLOOR_LAYERS), show (FLOOR_SHOW), JOIST, RAIL, DECK_SCREW,
           FRAME_NAIL, MEM_EPS, MEM_FLAT, MEM_LAP, MAGENTA, FINISH, WET_ROOMS,
           TANK_UP, PART, PART_BOARD, billReset, finMat, finTex, osbMat,
           woolFill, roomRects, roomKey, roomLabel, sharedEdge, onRoomLine,
           polyArea,
           add:       function (code, qty)
           setTally:  function (tally) -- the product's _floorTally
           skirtCuts, slants { raw, spaceMap, wedgeRoomAt } -- the ADU family
           ga:        { floor2, groundFinish, hole, rectMinus, pass } -- GARAGE
                      APT / ANY HOUSE: the apartment's floor over the garage
           spanCheck, rIn, seamCuts -- ANY HOUSE (and RANCH): the IRC spacing,
                      the L built a rectangle at a time
           slabRooms, rectArea, garageBare, blk { on, slabTop, GFL, buildUp },
           batch { boxes, baked }, joists { reset, add } -- RANCH
           hot:       { step, key, floor, review } -- SAUNA's sauna room
           matt:      { ply, plyCol, split, TERR, deckCol } -- TAURUS / GARDEN
                      BAR: Matt's plywood store and the bar's deck
         })

       Carried: the skirting's shared edges taken off once, not twice (the
       ADU's fix, Marek's audit FFW00002) to SAUNA; the finish's and the
       skirting's own figures in the log.
       ========================================================================= */
    function houseFloor(ctx, H) {
      /* the product's own pieces, under the names the text has always used */
      var API = H.api, LAYER = H.layer, FLOOR_LAYERS = H.layers, FLOOR_SHOW = H.show, JOIST = H.JOIST,
          RAIL = H.RAIL, DECK_SCREW = H.DECK_SCREW, FRAME_NAIL = H.FRAME_NAIL, MEM_EPS = H.MEM_EPS,
          MEM_FLAT = H.MEM_FLAT, MEM_LAP = H.MEM_LAP, MAGENTA = H.MAGENTA, FINISH = H.FINISH,
          WET_ROOMS = H.WET_ROOMS, TANK_UP = H.TANK_UP, PART = H.PART, PART_BOARD = H.PART_BOARD;
      var _billReset = H.billReset, _add = function (cat, code, q) { H.add(code, q); },
          _finMat = H.finMat, _finTex = H.finTex, _osbMat = H.osbMat, woolFill = H.woolFill,
          _roomRects = H.roomRects, _roomKey = H.roomKey, _roomLabel = H.roomLabel,
          _sharedEdge = H.sharedEdge, _onRoomLine = H.onRoomLine, _polyArea = H.polyArea;
      /* the products' own floors, each behind its hook (status/modules.md 6):
         slants   the ADU family's slanted partitions (adu-v1411 / v1414):
                  slantRaw, spaceMap, wedgeRoomAt; none -> no slanted walls
         skirtCuts  the skirting stops at a doorway (adu-v1412); none -> the
                  full perimeter (SAUNA, the shed family)
         GA       GARAGE APT / ANY HOUSE -- the apartment's floor over the
                  garage (floor2, groundFinish, hole, rectMinus) and ANY
                  HOUSE's two passes on a crawl space (pass)
         spanCheck  ANY HOUSE -- a wide house closes the joists up (IRC)
         slabRooms, BLK, batch, joists, rIn, seamCuts, rectArea, garageBare
                  RANCH -- the rooms finished on the slab, the block house's
                  build-up, one body a layer, the joists' lines for the
                  services, the L built a rectangle at a time, the garage
                  left bare
         HOT      SAUNA -- the sauna room's floor is its own
         MATT     TAURUS / GARDEN BAR -- Matt's floor: marine plywood under the
                  store, a deck of boards in front of it */
      var _skirtCuts = H.skirtCuts || function () { return []; };
      var _slantRaw = (H.slants && H.slants.raw) || function () { return []; };
      var _spaceMap = (H.slants && H.slants.spaceMap) || function () { return null; };
      var _wedgeRoomAt = (H.slants && H.slants.wedgeRoomAt) || function () { return -1; };
      var GA = H.ga || null, PASS = (GA && GA.pass) ? GA.pass() : null;
      var _gaBuildFloor2 = GA ? GA.floor2 : null, _gaGroundFinish = GA ? GA.groundFinish : null,
          _gaHole = GA ? GA.hole : null, _gaRectMinus = GA ? GA.rectMinus : null;
      var BLK = H.blk || null, BLK_GFL = BLK ? BLK.GFL : null;
      var RSB = H.batch || null;
      var _rIn = H.rIn || function () { return 1; }, _seamCuts = H.seamCuts || null;
      var HOT = H.hot || null, MATT = H.matt || null, TERR = MATT ? MATT.TERR : null;
      var T = ctx.THREE, g = ctx.group, P = ctx.poly, i;
      /* A SLAB IS THE FLOOR. Building joists on top of one would be a timber
         floor standing on a concrete floor -- two floors, and a takeoff that pays
         for both. */
      /* garageapt-v8 (6c): UNDER TWO STOREYS THIS LAYER BUILDS THE
         APARTMENT'S FLOOR -- the floor between (===== THE FLOOR BETWEEN,
         _gaBuildFloor2) and then, as ranch-v31 lays its rooms on the slab,
         this function's own room pass on the subfloor's top: vinyl on its
         underlay, the wet rooms' tile on tanking, the skirting, room by room
         of the apartment's plan; no ADU frame, deck or bays. The garage's
         floor is the bare slab. */
      var GA_UP = false, GA_HOLE = null, UPL = null, UPT = 0, UPA = 0, UPM = null;
      /* ranch-v31: BUT THE ROOMS ARE STILL FINISHED. On the slab this layer
         builds no frame, no deck, no bays -- only the room finishes (vinyl on
         its underlay, the wet rooms' tile on tanking) and the skirting, laid
         on the slab's top by the same room-by-room pass as on the deck */
      /* ranch-v35: a block house finishes its rooms the same way, on the
         EPS and the anhydrite over its slab (_blkBuildUp) */
      var BLKMODE = !!(BLK && BLK.on());
      var SLABMODE = !!H.slabRooms && ((API.STATE.foundation === 'slab') || BLKMODE);
      if (SLABMODE) { H.setTally(null); if (H.joists) H.joists.reset(); }
      /* anyhouse-lib-v4: on a crawl space under two storeys the layer runs
         twice (_ahFloorPass): the ADU's floor of the ground storey, then the
         floor between with the upper rooms -- the second keeps the first's bill */
      else if (GA && (API.STATE.foundation === 'slab' || PASS === 'up')) {
        if (PASS !== 'up') H.setTally(null);
        if (API.storeys().length < 2) {
          _billReset(LAYER);
          _why(ctx, 'floor    none -- the slab is the floor');
          return;
        }
        GA_UP = true;
        GA_HOLE = _gaHole();   /* garageapt-v12: the stairwell, no finish over it */
        /* core-v817: the upper rooms' build-up (H.upLift: its layers, bottom
           to top, [{ t, col, key, label, code }]) under their finish */
        H.upLiftA = 0;
        if (H.upLift && H.upLift.length) { UPL = H.upLift; for (var uq = 0; uq < UPL.length; uq++) UPT += +UPL[uq].t || 0; }
      }
      else if (!GA && API.STATE.foundation === 'slab') {
        _why(ctx, 'floor    none -- the slab is the floor');
        H.setTally(null);
        return;
      }
      var mat = new T.MeshStandardMaterial({ color: H.wood, roughness: 0.85, metalness: 0.0 });
      var deckMat = new T.MeshStandardMaterial({ color: scheme().deck || 0xB9A88C, roughness: 0.95, metalness: 0.0 });

      var x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9;
      for (i = 0; i < P.length; i++) {
        x0 = Math.min(x0, P[i][0]); x1 = Math.max(x1, P[i][0]);
        z0 = Math.min(z0, P[i][1]); z1 = Math.max(z1, P[i][1]);
      }
      var w = x1 - x0, l = z1 - z0, cx = (x0 + x1) / 2, cz = (z0 + z1) / 2;

      /* THE DECK TOP IS THE DATUM, AND EVERYTHING UNDER IT MOVES TO SUIT.

         The wall plates land on 0.350, so that is where the deck's TOP face has
         to be -- not where its underside happens to fall. Putting the vapour
         barrier on the joists pushed the deck a millimetre proud of the datum and
         the walls were standing on nothing. Nobody would ever measure that
         millimetre on site, which is exactly why the model has to get it right:
         an error nobody can see is one nobody catches.

         So the frame drops by whatever the build-up puts BETWEEN the joist tops
         and the deck, and the deck top stays on the datum. */
      var deckTop = GA_UP ? API.storeys()[1].y0   /* garageapt-v8: the apartment's subfloor top */
                  : BLKMODE ? (BLK.slabTop() + BLK_GFL.epsT + BLK_GFL.screedT) : ctx.stack.slab;
      var underDeck = 0;
      (function () {
        var q0;
        for (q0 = 0; q0 < FLOOR_LAYERS.length; q0++) {
          if (!FLOOR_LAYERS[q0].on) continue;
          if (FLOOR_LAYERS[q0].where !== 'over') continue;
          if (FLOOR_LAYERS[q0].viz === 'layer_floor_osb') break;   /* the deck itself */
          underDeck += FLOOR_LAYERS[q0].t;
        }
      })();
      var deckY = deckTop - JOIST.deck / 2;
      var joistTop = deckTop - JOIST.deck - underDeck;
      var joistY = joistTop - JOIST.d / 2;

      var _boxN = 0;
      /* ranch-v60: A BATCH COLLECTS INSTEAD OF ADDING (the membrane's tray:
         1231 bodies of 1 mm sheet, 18 % of the house, now one) */
      var _boxBatch = null;
      function box(bw, bh, bd, px, py, pz, m) {
        _boxN++;
        if (_boxBatch) {
          _boxBatch.B.add(bw, bh, bd, px, py, pz, null);
          var dB = [bw, bh, bd].sort(function (a, b) { return b - a; });
          _boxBatch.qtyV += dB[0] * dB[1];
          _boxBatch.mat = m || mat;
          return null;
        }
        var mesh = new T.Mesh(new T.BoxGeometry(bw, bh, bd), m || mat);
        mesh.position.set(px, py, pz);
        g.add(mesh);
        return mesh;
      }
      function _boxFlush() {
        var B = _boxBatch;
        _boxBatch = null;
        if (!B || !B.B.n()) return null;
        var mB = B.B.mesh(T, B.mat);
        mB.userData.qtyV = B.qtyV;          /* what the pieces measured, for the audit (core-v663) */
        mB.userData.qtyN = B.B.n();
        g.add(mB);
        return mB;
      }

      if (PASS !== 'up') _billReset(LAYER);
      /* SAUNA: the hot room's review step is on while its viz is */
      if (HOT) HOT.review(HOT.step() > 0 && ctx.viz && ctx.viz.key === 'hot');
      var tally = { rim: 0, beam: 0, joists: 0, joistLen: 0, deck: 0 };
      /* garageapt-v8: the floor between first (its bill goes with this one) */
      if (GA_UP) _gaBuildFloor2(ctx, true);
      /* garageapt-v14: a heated room of the ground floor (the stair hall)
         finished on the slab -- the ADU's finish and skirting, room by room
         (anyhouse-lib-v4: on the slab only; on a crawl space the ground pass did it) */
      if (GA_UP && API.STATE.foundation === 'slab') { try { _gaGroundFinish(ctx); } catch (eGF) { ctx.log('GA floor 6c ground rooms THREW ' + ((eGF && eGF.message) || eGF)); } }
      /* garageapt-v8: the ADU's frame, deck and blocking -- not over the garage;
         ranch-v31: none on the slab */
      if (!GA_UP && !SLABMODE) {

      /* rim, all four sides, inside the outline */
      _asmMark(ctx, 'layer_floor_joists', 'top', 'Floor joists', JOIST.code);
      box(JOIST.t, JOIST.d, l, x0 + JOIST.t / 2, joistY, cz);
      box(JOIST.t, JOIST.d, l, x1 - JOIST.t / 2, joistY, cz);
      box(w - JOIST.t * 2, JOIST.d, JOIST.t, cx, joistY, z0 + JOIST.t / 2);
      box(w - JOIST.t * 2, JOIST.d, JOIST.t, cx, joistY, z1 - JOIST.t / 2);
      tally.rim = l * 2 + (w - JOIST.t * 2) * 2;

      /* centre beam down the long axis, doubled */
      var k;
      for (k = 0; k < 2; k++) {
        box(JOIST.t, JOIST.d, l, cx + (k ? JOIST.t / 2 : -JOIST.t / 2), joistY, cz);
      }
      tally.beam = l * 2;

      /* joists across the short direction, each side of the beam */
      var sp = ctx.grid, run = (w - JOIST.t * 2) / 2 - JOIST.t, spWhy = '';
      /* anyhouse-lib-v5: A WIDER HOUSE ON A CRAWL SPACE CLOSES THE JOISTS UP.
         The joist is the 2x10 (60 x 240); IRC Table R502.3.1(2), SPF No. 2,
         40 psf live / 10 dead, L/360: 12 ft 9 in. at 24 in., 15 ft 7 in. at
         16 in., 17 ft 2 in. at 12 in. A run past what the module carries
         takes the next closer spacing (24 ft wide stays at 2 ft) */
      if (H.spanCheck && run > 12.75 * FT && sp > 16 * IN + 1e-3) { sp = 16 * IN; spWhy = ' (16 in.: a ' + (Math.round(run / FT * 10) / 10) + ' ft run is past the 2x10\'s 12 ft 9 in. at 24 in., IRC R502.3.1(2))'; }
      if (H.spanCheck && run > 15.58 * FT && sp > 12 * IN + 1e-3) { sp = 12 * IN; spWhy = ' (12 in.: a ' + (Math.round(run / FT * 10) / 10) + ' ft run is past the 2x10\'s 15 ft 7 in. at 16 in., IRC R502.3.1(2))'; }
      if (H.spanCheck && run > 17.17 * FT) spWhy = ' -- OVER: a ' + (Math.round(run / FT * 10) / 10) + ' ft run is past the 2x10\'s 17 ft 2 in. even at 12 in.';
      var pos = [], z = z0 + JOIST.t;
      while (z < z1 - JOIST.t - JOIST.t) { pos.push(z); z += sp; }
      pos.push(z1 - JOIST.t - JOIST.t / 2);
      for (i = 0; i < pos.length; i++) {
        box(run, JOIST.d, JOIST.t, x0 + JOIST.t + run / 2, joistY, pos[i]);
        box(run, JOIST.d, JOIST.t, x1 - JOIST.t - run / 2, joistY, pos[i]);
        tally.joists += 2;
        tally.joistLen += run * 2;
      }
      tally.joistAt = pos;
      if (H.joists) H.joists.add(joistY, pos);   /* RANCH: the lines for the services */

      /* THE DECK IS A LAYER LIKE THE REST, AND IT IS THE LID.

         It was drawn unconditionally, so every layer going on underneath was
         under a solid sheet -- the build-up could be watched only by taking the
         screen apart. It is row 4 of the table, so it appears when the build-up
         reaches row 4 and not before.

         The bill is untouched: the deck is still bought at every step. */
      var DECK_ROW = 5;
      /* MATT'S FLOOR (taurus-v12, Marek: rozpocet musi sedet s puvodnim
         projektem): his model floors the STORE only, with marine plywood
         FFW00003; the open bar part is a deck of the same boards as under
         the lean-to (TST00001, by the metre), laid across the joists from
         the partition to the front. No OSB anywhere in the floor. */
      var zPF = MATT ? MATT.split() : null;
      if (!(zPF !== null && zPF > z0 + 0.3 && zPF < z1 - 0.3)) zPF = null;
      var zStore = (zPF === null) ? z1 : zPF;
      if (MATT) {
      _asmMark(ctx, 'layer_floor_subfloor', 'top', 'Marine plywood', MATT.ply);
      if (FLOOR_SHOW < 0 || FLOOR_SHOW >= DECK_ROW) {
        var mPly = (FLOOR_SHOW === DECK_ROW) ? new T.MeshStandardMaterial({ color: MAGENTA, roughness: 0.95 })
                 : new T.MeshStandardMaterial({ color: MATT.plyCol, roughness: 0.9, metalness: 0.0 });
        var SHP = sheet(), jtP = 0.004, nPl = 0, cP2, sXP;
        for (cP2 = 0; cP2 < Math.ceil((zStore - z0) / SHP.w - 1e-6); cP2++) {
          var zAP = z0 + cP2 * SHP.w, zBP = Math.min(zStore, zAP + SHP.w);
          for (sXP = x0 + ((cP2 % 2) ? -SHP.l / 2 : 0); sXP < x1; sXP += SHP.l) {
            var xAP = Math.max(x0, sXP), xBP = Math.min(x1, sXP + SHP.l);
            if (xBP - xAP < 0.05 || zBP - zAP < 0.05) continue;
            box(xBP - xAP - jtP, JOIST.deck, zBP - zAP - jtP, (xAP + xBP) / 2, deckY, (zAP + zBP) / 2, mPly);
            nPl++;
          }
        }
        ctx.log('floor    STORE ' + nPl + ' marine plywood sheet(s) ' + MATT.ply + ' z ' + Math.round(z0 * 1000) + '..' + Math.round(zStore * 1000));
      }
      } else {
      _asmMark(ctx, 'layer_floor_subfloor', 'top', 'Subfloor OSB', 'OSB25002');
      if (FLOOR_SHOW < 0 || FLOOR_SHOW >= DECK_ROW) {
        /* THE SAME OSB EVERYWHERE (Marek): the deck is laid as sheets with a
           joint between them and the flake texture the wall sheathing has --
           staggered courses, the long side across the joists */
        var otxF = _finTex(T, 'osb');
        var mOsb = (FLOOR_SHOW === DECK_ROW) ? new T.MeshStandardMaterial({ color: MAGENTA, roughness: 0.95 })
                 : _finMat(T, new T.MeshStandardMaterial({ color: otxF ? 0xFFFFFF : (scheme().deck || 0xB9A88C), roughness: 0.95, metalness: 0.0 }), 'osb', sheet().l, sheet().w);
        var SHF = sheet(), jt = 0.006, sL = SHF.l, sW = SHF.w, nSh = 0;
        var courses = Math.ceil(l / sW), cI, sX, rowStart;
        for (cI = 0; cI < courses; cI++) {
          var zA = z0 + cI * sW, zB = Math.min(z1, zA + sW);
          rowStart = (cI % 2) ? -sL / 2 : 0;   /* staggered by half a sheet */
          for (sX = x0 + rowStart; sX < x1; sX += sL) {
            var xA = Math.max(x0, sX), xB = Math.min(x1, sX + sL);
            if (xB - xA < 0.05 || zB - zA < 0.05) continue;
            box(xB - xA - jt, JOIST.deck, zB - zA - jt, (xA + xB) / 2, deckY, (zA + zB) / 2, mOsb);
            nSh++;
          }
        }
        ctx.log('floor    deck ' + nSh + ' OSB sheet(s) ' + Math.round(sL * 1000) + 'x' + Math.round(sW * 1000) + ' with ' + Math.round(jt * 1000) + ' joints, staggered');
      }
      }
      tally.deck = w * l;
      if (zPF !== null) {
        _asmMark(ctx, 'decking', 'top', 'Decking boards', TERR.board);
        var mDk = (FLOOR_SHOW === DECK_ROW) ? new T.MeshStandardMaterial({ color: MAGENTA, roughness: 0.9 })
                : new T.MeshStandardMaterial({ color: MATT.deckCol, roughness: 0.85 });
        var runD = z1 - zPF, nBD = Math.max(1, Math.floor((x1 - x0 + TERR.gap) / (TERR.boardW + TERR.gap)));
        var stD = x0 + ((x1 - x0) - (nBD * TERR.boardW + (nBD - 1) * TERR.gap)) / 2 + TERR.boardW / 2, kD;
        if (FLOOR_SHOW < 0 || FLOOR_SHOW >= DECK_ROW) {
          for (kD = 0; kD < nBD; kD++) box(TERR.boardW, TERR.boardT, runD - 0.004, stD + kD * (TERR.boardW + TERR.gap), deckTop - TERR.boardT / 2, (zPF + z1) / 2, mDk);
        }
        var jCross = (tally.joistAt || []).filter(function (zj) { return zj > zPF && zj < z1; }).length + 2;
        _add(LAYER, TERR.board, nBD * runD);
        _add(LAYER, 'FAS55050', nBD * jCross * 2);
        ctx.log('floor    PORCH decking ' + nBD + ' board(s) ' + TERR.board + ' ' + Math.round(runD * 1000) + ' long, ' + (Math.round(nBD * runD * 100) / 100) + ' m, ' + (nBD * jCross * 2) + ' x FAS55050 over ' + jCross + ' joist(s)');
      }

      /* BLOCKING BETWEEN THE JOISTS, AND IT IS THE SAME TIMBER THEY ARE.

         I had this as crossed diagonal strutting out of a thin offcut. Tiny does
         it the way it is actually built here: solid blocks, the FULL depth of the
         joist, laid horizontally between them. Full depth is the point -- a block
         shallower than the joist leaves the top and bottom edges free to roll,
         which is the whole thing it was put in to stop.

         AND THE ROWS ARE STAGGERED, NOT IN LINE. Consecutive blocks step past
         each other by a joist thickness, because a screw has to go through the
         joist into the END of the block: line them all up and every second one
         has a screw coming at it from both sides through the same piece of
         timber. Staggered, each is reachable.

         Rows about 1.6 m apart along the span, which is tiny's number.
         ------------------------------------------------------------------- */
      var blockRow = [], blockAt = [];
      /* the blocking is the joists' own item (it is drawn after the deck's mark) */
      _asmMark(ctx, 'layer_floor_joists', 'top', 'Floor joists', JOIST.code);
      (function () {
        var jz = tally.joistAt || [];
        if (jz.length < 2) return;
        var rows = Math.max(1, Math.round(run / 1.6));
        var halves = [x0 + JOIST.t, x1 - JOIST.t - run];   /* start of each run */
        var step = JOIST.t * 1.2;
        var n = 0;
        for (var r = 0; r < halves.length; r++) {
          for (var q = 1; q <= rows; q++) {
            var atX = halves[r] + run * (q / (rows + 1));
            for (var j = 1; j < jz.length; j++) {
              var a = jz[j - 1], b = jz[j];
              var gapZ = b - a - JOIST.t;
              if (gapZ < 0.15) continue;
              /* every other one steps forward, so no two ends meet head on */
              var off = (j % 2) ? step : -step;
              box(JOIST.t, JOIST.d, gapZ, atX + off, joistY, (a + b) / 2);
              blockRow.push(gapZ);
              /* WHERE IT IS, NOT JUST HOW MUCH OF IT. A block divides the bay it
                 sits in exactly as a joist does, and the build-up has to know
                 that or it lays a sheet straight through one. */
              blockAt.push({ x: atX + off, za: a + JOIST.t / 2, zb: b - JOIST.t / 2 });
              n++;
            }
          }
        }
        tally.blocks = n;
        tally.blockLen = blockRow.reduce(function (u, v) { return u + v; }, 0);
      })();

      H.setTally(tally);
      _add(LAYER, JOIST.code, tally.joistLen + tally.rim + tally.beam + (tally.blockLen || 0));
      /* SHEETS ARE CUT, AND THE OFFCUTS ARE PAID FOR. The roof deck has carried
         5 per cent since it was written; the floor deck and the wall sheathing
         were billed as the bare area, so every board cut round a corner or an
         opening was missing from the order (Marek). */
      /* RANCH: the rectangle built (ranch-v5: one of the L's two) */
      var fA = H.rectArea ? w * l : API.STATE.footprint.w * API.STATE.footprint.l;
      if (MATT) _add(LAYER, MATT.ply, (x1 - x0) * (zStore - z0) * 1.05);   /* taurus-v12: the store's marine plywood, not OSB under the whole floor */
      else _add(LAYER, 'sheet.osb18', fA * 1.05);

      /* AND WHAT FIXES THE FLOOR DOWN, WHICH WAS NOT BILLED.

         Same two counts as the wall: an area rate for the sheet and a junction
         count for the frame. The deck reads the shared sheathing rule so the
         three OSB layers in this building are all bought against one number.

         One substitution, and it is deliberate rather than hidden: a floor deck
         is SCREWED, not nailed, because a nailed deck squeaks as soon as the
         timber dries. There is no deck screw in the catalogue -- FAS60100 is a
         6x100 and would go through the joist and out the other side -- so the
         quantity is right and the code is the nail until a 4.2x55 countersunk
         is issued. It is on the specification list at the end of this revision.

         Three nails through the rim into each joist end, two ends: a joist that
         can rotate at its bearing takes the deck with it. */
      var fDeckA = MATT ? (x1 - x0) * (zStore - z0) : fA;   /* taurus-v12: the plywood is screwed, the porch deck is FAS55050 */
      var fSheath = Math.round(fDeckA * DECK_SCREW.perM2);
      var fFrame = tally.joists * 3 * 2;
      if (fSheath) _add(LAYER, DECK_SCREW.code, fSheath);
      if (fFrame) _add(LAYER, FRAME_NAIL.code, fFrame);
      ctx.log('FIX  floor deck ' + Math.round(fDeckA * 10) / 10 + ' m2 -> ' +
              fSheath + ' x ' + DECK_SCREW.code + ' at ' + DECK_SCREW.perM2 +
              '/m2 -- screwed, not nailed: a nailed deck squeaks once the timber dries');
      ctx.log('FIX  floor frame ' + tally.joists + ' joist(s) -> ' + fFrame + ' x ' +
              FRAME_NAIL.code + ', 3 through the rim at each of 2 ends');
      ctx.log('floor    joists ' + tally.joists + ' at ' + API.fmtLen(sp) + ' oc, ' +
              'span ' + API.fmtLen(run) + ' each side of a centre beam' + spWhy);
      ctx.log('floor    timber: joists ' + tally.joistLen.toFixed(1) + ' m, ' +
              'rim ' + tally.rim.toFixed(1) + ' m, beam ' + tally.beam.toFixed(1) + ' m, ' +
              'deck ' + API.fmtArea(tally.deck));
      var fq = H.rectArea ? API.sheetsFor(w, l) : API.sheetsFor(API.STATE.footprint.w, API.STATE.footprint.l);
      ctx.log('floor    deck ' + fq.n + ' boards, offcut ' + API.fmtArea(fq.waste) +
              ' -- joists at half a board so every edge lands on one');
      if (tally.blocks) {
        ctx.log('floor    ' + tally.blocks + ' blocks, ' + tally.blockLen.toFixed(1) +
                ' m of the same ' + Math.round(JOIST.t * 1000) + 'x' + Math.round(JOIST.d * 1000) +
                ' the joists are -- full depth, staggered so each can be screwed ' +
                'through the joist into its end');
      }
      }   /* !GA_UP && !SLABMODE */
      else if (BLKMODE) { try { BLK.buildUp(ctx, x0, x1, z0, z1); } catch (eBU) { ctx.log('BLK  floor build-up THREW ' + ((eBU && eBU.message) || eBU)); } }
      else if (SLABMODE) ctx.log('floor    on the slab (ranch-v31): no joists, no deck, no bays -- the rooms finished on the slab\'s top at +' + Math.round(deckTop * 1000));
      else ctx.log('GA floor 6c the apartment\'s rooms finished on the subfloor at +' + Math.round((deckTop - API.storeys()[0].y0) * 1000) + ' (the ADU\'s room pass; no ADU frame, deck or bays)');
      /* ------------------------------------------------------------------
         THE BUILD-UP, ONE LAYER AT A TIME.

         Step 1 is the cover board: a sheet fixed to the UNDERSIDE of the joists,
         closing the cassette from below. It is what the wool sits on, what keeps
         the wind out of the bays, and the only thing between the insulation and
         the open air under the building.

         Whatever this version put on last is MAGENTA, so it can be told from
         everything already accepted underneath it. */
      var lay = { n: 0, area: 0, rails: 0, railScrews: 0 };
      var yOver = null;      /* the stack above the joists, once the pockets are shut */

      /* THE BAYS, ONCE. Every layer that goes IN the floor reads the same list,
         so the rail, the board and the wool cannot disagree about where a bay is
         or drift apart at the beam. */
      function floorBays() {
        var out = [], q1, q2, q3;
        var runs = [[x0 + JOIST.t, x0 + JOIST.t + run], [x1 - JOIST.t - run, x1 - JOIST.t]];
        var jz = tally.joistAt || [];
        for (q1 = 0; q1 + 1 < jz.length; q1++) {
          var za = jz[q1] + JOIST.t / 2, zb = jz[q1 + 1] - JOIST.t / 2;
          if (zb - za < 0.05) continue;
          for (q2 = 0; q2 < 2; q2++) {
            /* A POCKET, NOT A BAY. The blocking crosses the bay and divides it as
               surely as a joist does, and it is in from the start -- it is what
               stops the joists rolling, so it cannot be an afterthought. A sheet
               laid the length of the bay would run straight through one. */
            var cuts = [runs[q2][0]];
            for (q3 = 0; q3 < blockAt.length; q3++) {
              var Bk = blockAt[q3];
              if (Bk.za < za - 0.01 || Bk.zb > zb + 0.01) continue;
              if (Bk.x < runs[q2][0] || Bk.x > runs[q2][1]) continue;
              cuts.push(Bk.x - JOIST.t / 2);
              cuts.push(Bk.x + JOIST.t / 2);
            }
            cuts.push(runs[q2][1]);
            cuts.sort(function (u, v) { return u - v; });
            for (q3 = 0; q3 + 1 < cuts.length; q3 += 2) {
              if (cuts[q3 + 1] - cuts[q3] < 0.05) continue;
              out.push({ x0: cuts[q3], x1: cuts[q3 + 1], z0: za, z1: zb });
            }
          }
        }
        return out;
      }

      var BAYS = floorBays();
      var railTop = joistTop - JOIST.d + RAIL.drop + RAIL.h;   /* what the board comes to rest on */

      (function () {
        var q, L, th, yBay = railTop;                  /* the build-up starts ON the rails */
        /* WHAT EACH ROW ACTUALLY PUT IN THE SCENE, counted rather than assumed.

           Three rounds of "I cannot see it" and three different guesses from me,
           none of them checked. A row either added meshes or it did not, and that
           is a number this can report. */
        lay.rows = [];
        for (q = 0; q < FLOOR_LAYERS.length; q++) {
          L = FLOOR_LAYERS[q];
          if (L.where === 'rail') {
            L.code = RAIL.code;
            L.t = RAIL.h;
            L.label = 'Rails ' + Math.round(RAIL.proj * 1000) + 'x' + Math.round(RAIL.h * 1000);
          }
          if (!L.on) { lay.rows.push({ q: q, lb: L.label, n: 0, why: 'switched off' }); continue; }
          /* garageapt-v8: over the garage only the apartment's rooms' finish and skirting */
          if (GA_UP && !L.room) { lay.rows.push({ q: q, lb: L.label, n: 0, why: 'the apartment: its own floor (5a-5d)' }); continue; }
          /* ranch-v31: on the slab only the rooms' finish and skirting */
          if (SLABMODE && !L.room) { lay.rows.push({ q: q, lb: L.label, n: 0, why: 'on the slab' }); continue; }
          /* one assembly step per row of the build-up, named as the row is */
          if (!L.room) _asmMark(ctx, ({ rail: 'layer_floor_rail', 'OSB25001': 'layer_floor_cover_osb', 'INS00002': 'layer_breather_membrane', '@wool': 'layer_mineral_wool',
                           'ROV00001': 'layer_vapour_barrier', 'OSB25002': 'layer_floor_subfloor' })[L.where === 'rail' ? 'rail' : L.code] || '', 'top', L.label,
                         (L.code && L.code.charAt(0) !== '@') ? L.code : (L.code === '@wool' ? (function () { try { return woolFill(JOIST.d - RAIL.h).mix[0].code; } catch (e) { return ''; } })() : ''));
          if (FLOOR_SHOW >= 0 && q > FLOOR_SHOW) {
            lay.rows.push({ q: q, lb: L.label, n: 0, why: 'past FLOOR_SHOW ' + FLOOR_SHOW });
            continue;
          }
          var _b0 = _boxN;
          th = L.t;
          /* a bay row with no thickness of its own takes what is left of the
             pocket -- that is the wool, and it is handled on its own below */
          if (th <= 0 && L.viz !== 'layer_floor_wool') continue;

          /* A MEMBRANE HAS TO BE SEEN THROUGH, or it repaints the frame.

             It wraps the joist on three sides, so an opaque one takes the timber
             away and the whole frame reads as being that colour -- which is what
             happened: a near-black breather made every joist look navy and the
             rails under it vanished. It is a thin sheet stretched over a frame,
             and it should look like one.

             And the colours were the wrong way round. A breather is white or
             light grey; a vapour barrier is the dark one. They were swapped. */
          var lm = new T.MeshStandardMaterial({
            color: (q === FLOOR_SHOW) ? MAGENTA : L.col, roughness: 0.95, metalness: 0.02 });
          /* an OSB row wears the OSB the walls wear (Marek: the same texture everywhere) */
          if (q !== FLOOR_SHOW && L.code && String(L.code).indexOf('OSB') === 0) lm = _osbMat(T, false);
          if (L.viz === 'layer_floor_membrane') {
            lm.transparent = true;
            lm.opacity = 0.55;
            lm.depthWrite = false;
          }

          if (L.where === 'rail') {
            /* DOWN BOTH SIDES OF EVERY JOIST, AND BROKEN AT EVERY BRACE. */
            /* ranch-v65: the rails one body, measured as their pieces (qtyRun,
               core-v664; the coating's coatA / coatRun) -- 416 bodies on the
               default house, the most of anything hidden */
            var rq, B, rl, RLB = RSB ? RSB.boxes() : null;
            for (rq = 0; rq < BAYS.length; rq++) {
              B = BAYS[rq]; rl = B.x1 - B.x0;
              if (RLB) {
                RLB.add(rl, RAIL.h, RAIL.proj, (B.x0 + B.x1) / 2, railTop - RAIL.h / 2, B.z0 + RAIL.proj / 2, null);
                RLB.add(rl, RAIL.h, RAIL.proj, (B.x0 + B.x1) / 2, railTop - RAIL.h / 2, B.z1 - RAIL.proj / 2, null);
                _boxN += 2;
              } else {
                box(rl, RAIL.h, RAIL.proj, (B.x0 + B.x1) / 2, railTop - RAIL.h / 2, B.z0 + RAIL.proj / 2, lm);
                box(rl, RAIL.h, RAIL.proj, (B.x0 + B.x1) / 2, railTop - RAIL.h / 2, B.z1 - RAIL.proj / 2, lm);
              }
              lay.rails += rl * 2;
            }
            if (RLB) RSB.baked(T, g, RLB, lm, 'm', 'floor-rails');
            _add(LAYER, L.code, lay.rails);
            /* AND WHAT HOLDS THEM ON, BY THE SAME ARITHMETIC AS THE TIMBER: one
               screw at 300 mm along, two at each rail end. */
            lay.railScrews = Math.round(lay.rails / 0.30) + BAYS.length * 4;
            _add(LAYER, RAIL.screw, lay.railScrews);
            lay.n++;
            lay.rows.push({ q: q, lb: L.label, n: _boxN - _b0 });
            continue;
          }

          if (L.viz === 'layer_floor_wool') {
            /* IT FILLS WHAT IS LEFT, POCKET BY POCKET. Not a slab over the floor
               -- that buys wool for the timber as well, about a fifth of it. The
               pockets are what the drawing actually fills, and the volume is
               theirs. */
            var wd = joistTop - yBay;
            if (wd < 0.02) continue;
            var wq, WB, wa = 0;
            if (RSB) _boxBatch = { B: RSB.boxes(), qtyV: 0, mat: lm };   /* ranch-v61: the bays' wool one body */
            for (wq = 0; wq < BAYS.length; wq++) {
              WB = BAYS[wq];
              var ww = WB.x1 - WB.x0 - th * 0, wl2 = WB.z1 - WB.z0;
              /* THE WOOL WEARS THE WOOL IT IS. The bay was drawn under whatever
                 mark came before it, which is how 26 m2 of floor insulation
                 ended up counted as the WALL's 160 mm batt. It is billed from
                 woolMix, so it is marked from woolMix too (Marek). */
              (function () {
                var wmx = woolFill(wd);
                var wc = (wmx && wmx.mix && wmx.mix[0] && wmx.mix[0].code) || 'INS01601';
                _asmMark(ctx, 'layer_floor_wool', 'top', 'Mineral wool', wc);
              })();
              box(ww, wd, wl2, (WB.x0 + WB.x1) / 2, yBay + wd / 2, (WB.z0 + WB.z1) / 2, lm);
              wa += ww * wl2;
            }
            _boxFlush();
            lay.woolArea = wa;
            lay.woolDepth = wd;
            lay.woolVol = wa * wd;
            lay.woolMix = woolFill(wd);
            var wm2;
            for (wm2 = 0; wm2 < lay.woolMix.mix.length; wm2++) {
              _add(LAYER, lay.woolMix.mix[wm2].code, wa * lay.woolMix.mix[wm2].n);
            }
            lay.area += wa;
            lay.n++;
            lay.rows.push({ q: q, lb: L.label, n: _boxN - _b0 });
            yBay = joistTop;
            continue;
          }

          /* EVERYTHING FROM HERE UP IS ONE SHEET OVER THE WHOLE FLOOR.

             The pockets are full, so there is nothing to work around any more:
             the vapour barrier, the deck, the underlay and the finish all cover
             the plan and stack on each other. They start at the joist tops and
             climb from there.

             The deck is the exception -- it is drawn by the frame, above, so that
             the walls have something to stand on -- so this skips it and only
             counts its thickness. */
          if (L.where === 'over') {
            if (yOver === null) yOver = (GA_UP || SLABMODE) ? deckTop + (GA_UP ? UPT : 0) : joistTop;   /* garageapt-v8: on the subfloor's top; ranch-v31: on the slab's top; core-v817: on the upper rooms' build-up */
            if (L.viz === 'layer_floor_osb') { yOver += th; continue; }   /* the deck, drawn above */

            /* A FINISH IS A ROOM'S, NOT THE BUILDING'S.

               Everything up to the deck is one continuous thing and is measured
               as one. Above it nothing is: a bathroom is tiled, a bedroom is
               vinyl, and somebody orders each of them by the room. Measured over
               the whole plan they come out as one number nobody can buy from --
               and it is the wrong number anyway, because it counts the ground
               under the partitions.

               The asset already cuts the plan into rooms at the partitions, so
               this asks it rather than dividing the floor up again. */
            if (L.room) {
              var RM = _roomRects(), rq2, ra = 0;
              if (!RM.length) {
                /* no partitions yet, so the building is one room -- say that,
                   rather than quietly falling back to the plan area */
                var wtF3 = API.wallThickness();
                RM = [{ rects: [{ x0: x0 + wtF3 * _rIn('x0'), x1: x1 - wtF3 * _rIn('x1'),
                                  z0: z0 + wtF3 * _rIn('z0'), z1: z1 - wtF3 * _rIn('z1') }], one: true }];   /* anyhouse-lib-v11: the L's seam */
              }
              /* A ROOM IS A SET OF RECTANGLES, NOT A RECTANGLE.

                 The asset hands back { rects: [...] } -- an L-shaped room is two
                 of them -- and this read r.x0 straight off the room. undefined
                 minus undefined is NaN, so every width, every area and every
                 quantity was NaN; the meshes were built at NaN and _add threw the
                 rows away, because NaN > 0 is false. Nothing said a word.

                 The ADU already reads rooms correctly forty lines up in
                 _pinRoomNames. I did not look. */
              lay.rooms = lay.rooms || [];
              /* THE CORNER PAST A SLANTED WALL TAKES THE FINISH OF THE ROOM IT
                 OPENS INTO (adu-v1414). The rectangle a slanted wall cuts a
                 corner off still belongs to the room behind the wall, so the
                 bathroom's tiles ran on past its own door into the hall. A
                 rectangle the wall cuts right across -- the wall's two ends on
                 its edges -- is split along the wall's line; a piece that lies
                 in another room's space is laid with that room's finish and
                 counted in its area, so the strip at the door is where the
                 floor really changes. */
              var SPL = {}, GETS = {};
              (function () {
                var SLw = _slantRaw(), r0, s0, w0, k0;
                if (!SLw.length || !_spaceMap()) return;
                var wtW = API.wallThickness();
                function chord(gW, bx0, bx1, bz0, bz1) {
                  var t0 = -1e9, t1 = 1e9, ax = gW.a[0], az = gW.a[1], ta, tb;
                  if (Math.abs(gW.ux) > 1e-9) { ta = (bx0 - ax) / gW.ux; tb = (bx1 - ax) / gW.ux; t0 = Math.max(t0, Math.min(ta, tb)); t1 = Math.min(t1, Math.max(ta, tb)); }
                  else if (ax < bx0 || ax > bx1) return null;
                  if (Math.abs(gW.uz) > 1e-9) { ta = (bz0 - az) / gW.uz; tb = (bz1 - az) / gW.uz; t0 = Math.max(t0, Math.min(ta, tb)); t1 = Math.min(t1, Math.max(ta, tb)); }
                  else if (az < bz0 || az > bz1) return null;
                  return (t1 > t0) ? [t0, t1] : null;
                }
                /* the part of a polygon on one side of the wall's line */
                function half(P, gW, sg) {
                  var out = [], i5, n5 = P.length;
                  function sd(pt) { return sg * ((pt[0] - gW.a[0]) * gW.nx + (pt[1] - gW.a[1]) * gW.nz); }
                  for (i5 = 0; i5 < n5; i5++) {
                    var A5 = P[i5], B5 = P[(i5 + 1) % n5], dA = sd(A5), dB = sd(B5);
                    if (dA >= 0) out.push(A5);
                    if ((dA >= 0) !== (dB >= 0)) { var t5 = dA / (dA - dB); out.push([A5[0] + (B5[0] - A5[0]) * t5, A5[1] + (B5[1] - A5[1]) * t5]); }
                  }
                  return out;
                }
                for (r0 = 0; r0 < RM.length; r0++) {
                  var RS0 = RM[r0].rects || (typeof RM[r0].x0 === 'number' ? [RM[r0]] : []);
                  for (s0 = 0; s0 < RS0.length; s0++) {
                    var R0 = RS0[s0];
                    if (typeof R0.x0 !== 'number') continue;
                    var bx0 = Math.max(R0.x0, x0 + wtW), bx1 = Math.min(R0.x1, x1 - wtW), bz0 = Math.max(R0.z0, z0 + wtW), bz1 = Math.min(R0.z1, z1 - wtW);
                    if (!(bx1 - bx0 > 0.05) || !(bz1 - bz0 > 0.05)) continue;
                    var polys = [[[bx0, bz0], [bx1, bz0], [bx1, bz1], [bx0, bz1]]], cutAny = false;
                    for (w0 = 0; w0 < SLw.length; w0++) {
                      var gW = SLw[w0], ch = chord(gW, bx0, bx1, bz0, bz1);
                      if (!ch || ch[1] - ch[0] < 0.3 || ch[0] < -0.25 || ch[1] > gW.len + 0.25) continue;
                      var np = [];
                      for (k0 = 0; k0 < polys.length; k0++) {
                        var hp = half(polys[k0], gW, 1), hm = half(polys[k0], gW, -1);
                        if (hp.length > 2 && _polyArea(hp) > 0.005) np.push(hp);
                        if (hm.length > 2 && _polyArea(hm) > 0.005) np.push(hm);
                      }
                      polys = np; cutAny = true;
                    }
                    if (!cutAny) continue;
                    var own = [], away = [];
                    for (k0 = 0; k0 < polys.length; k0++) {
                      var PK = polys[k0], cxK = 0, czK = 0, aK = _polyArea(PK), j0;
                      for (j0 = 0; j0 < PK.length; j0++) { cxK += PK[j0][0]; czK += PK[j0][1]; }
                      var wqK = (aK >= 0.03) ? _wedgeRoomAt(cxK / PK.length, czK / PK.length) : -1;
                      if (wqK >= 0 && wqK !== r0 && RM[wqK]) { away.push({ to: wqK, poly: PK, a: aK }); (GETS[wqK] = GETS[wqK] || []).push({ from: r0, poly: PK, a: aK }); }
                      else own.push(PK);
                    }
                    if (away.length) SPL[r0 + '|' + s0] = { own: own, away: away };
                  }
                }
              })();
              /* a flat piece of finish of any outline, its top at yTop */
              var finPoly = function (PK, hK, yTop, mK) {
                try {
                  var shK = new T.Shape(), j1;
                  shK.moveTo(PK[0][0], PK[0][1]);
                  for (j1 = 1; j1 < PK.length; j1++) shK.lineTo(PK[j1][0], PK[j1][1]);
                  shK.closePath();
                  var mshK = new T.Mesh(new T.ExtrudeGeometry(shK, { depth: hK, bevelEnabled: false }), mK);
                  mshK.rotation.x = Math.PI / 2;
                  mshK.position.y = yTop;
                  g.add(mshK);
                  _boxN++;
                } catch (ePk) {}
              };
              for (rq2 = 0; rq2 < RM.length; rq2++) {
                var ROOM = RM[rq2];
                var RS = ROOM.rects || (typeof ROOM.x0 === 'number' ? [ROOM] : []);
                if (!RS.length) continue;
                var rkey = _roomKey(ROOM, rq2);
                /* ranch-v32: a GARAGE is the bare slab -- no finish, no skirting */
                if (H.garageBare && rkey === 'rooms.garage') { lay.garageSkip = (lay.garageSkip || 0) + 1; continue; }
                /* THE SAUNA ROOM'S FLOOR IS ITS OWN (sauna-v50): no vinyl, no
                   underlay, no skirting -- its build-up goes on the deck instead,
                   layer by layer (Marek: ostatni vrstvy v potni mistnosti zrusit) */
                if (HOT && HOT.step() >= 7 && rkey === HOT.key) {
                  if (L.room === 'finish') {
                    var hotRs = [], sqh, wtH = API.wallThickness();
                    for (sqh = 0; sqh < RS.length; sqh++) {
                      var RH = RS[sqh];
                      if (typeof RH.x0 !== 'number') continue;
                      var hx0 = Math.max(RH.x0, x0 + wtH), hx1 = Math.min(RH.x1, x1 - wtH);
                      var hz0 = Math.max(RH.z0, z0 + wtH), hz1 = Math.min(RH.z1, z1 - wtH);
                      if (hx1 - hx0 > 0.05 && hz1 - hz0 > 0.05) hotRs.push({ x0: hx0, x1: hx1, z0: hz0, z1: hz1, src: sqh });
                    }
                    try { HOT.floor(ctx, hotRs, yOver, RS, { x0: x0 + wtH, x1: x1 - wtH, z0: z0 + wtH, z1: z1 - wtH }); }
                    catch (eHF2) { ctx.log('hot        !! floor threw: ' + (eHF2 && eHF2.message)); }
                  } else {
                    ctx.log('hot        floor: the house skirting is left out of the sauna room -- the tile plinth comes with the tiles');
                  }
                  continue;
                }
                var F2 = FINISH[WET_ROOMS[rkey] ? 'wet' : 'dry'];
                /* the room's finish is its own item (vinyl or tile), the skirting another */
                if (L.room === 'finish') _asmMark(ctx, 'layer_floor_finish', 'top', 'Floor finish', (function () { var fq2, best = ''; for (fq2 = 0; fq2 < F2.lays.length; fq2++) if (F2.lays[fq2].per === 'area') best = F2.lays[fq2].code; return best || F2.lays[0].code; })());
                else if (L.room === 'skirt') _asmMark(ctx, 'layer_floor_skirting', 'top', 'Skirting', F2.skirt.code);
                var fm = new T.MeshStandardMaterial({
                  color: (q === FLOOR_SHOW) ? MAGENTA : F2.col, roughness: 0.9, metalness: 0.02 });

                var ar = 0, pr = 0, sq;
                for (sq = 0; sq < RS.length; sq++) {
                  var RR = RS[sq];
                  if (typeof RR.x0 !== 'number') continue;

                  /* THE FINISH STOPS AT THE INSIDE FACE. */
                  /* wt WAS NEVER DECLARED HERE. I added this clip in v439 and used a
                     name that exists in the ceiling, not in the floor -- so the
                     whole floor layer threw on every build since. It threw into
                     the layer catch, which until today said nothing, and the
                     snapshot found it the moment that catch was made loud. */
                  var wtF2 = API.wallThickness();
                  /* anyhouse-lib-v11: no wall where two rectangles of the L meet */
                  var cx0 = Math.max(RR.x0, x0 + wtF2 * _rIn('x0')), cx1 = Math.min(RR.x1, x1 - wtF2 * _rIn('x1'));
                  var cz0 = Math.max(RR.z0, z0 + wtF2 * _rIn('z0')), cz1 = Math.min(RR.z1, z1 - wtF2 * _rIn('z1'));
                  var rw = cx1 - cx0, rl = cz1 - cz0;
                  if (!(rw > 0.05) || !(rl > 0.05)) continue;
                  var spK = (L.room === 'finish') ? SPL[rq2 + '|' + sq] : null;
                  ar += rw * rl;
                  pr += (rw + rl) * 2;

                  if (L.room === 'finish' && spK) {
                    /* cut along a slanted wall: this room keeps its own pieces,
                       the corner past the wall is laid by the room it opens into */
                    var fmP = (q === FLOOR_SHOW) ? fm : _finMat(T, fm, F2.piece, 1, 1), kq;
                    for (kq = 0; kq < spK.own.length; kq++) finPoly(spK.own[kq], F2.h, yOver + F2.h, fmP);
                    for (kq = 0; kq < spK.away.length; kq++) { ar -= spK.away[kq].a; lay.wedgeFin = (lay.wedgeFin || 0) + spK.away[kq].a; }
                  } else if (L.room === 'finish') {
                    var fmT = (q === FLOOR_SHOW) ? fm : _finMat(T, fm, F2.piece, rw, rl);
                    var hPc = GA_HOLE ? _gaRectMinus({ x0: cx0, x1: cx1, z0: cz0, z1: cz1 }, GA_HOLE) : null;
                    if (UPL) {
                      /* core-v817: the build-up under this room's finish, layer by
                         layer from the deck, round the stairwell as the finish */
                      if (!UPM) UPM = UPL.map(function (Lu) { return new T.MeshStandardMaterial({ color: Lu.col || 0xCCCCCC, roughness: 0.9, metalness: 0.02 }); });
                      var UPR = hPc || [{ x0: cx0, x1: cx1, z0: cz0, z1: cz1 }], uk, ur, yU0;
                      for (ur = 0; ur < UPR.length; ur++) {
                        var Ru = UPR[ur], wu = Ru.x1 - Ru.x0, lu = Ru.z1 - Ru.z0;
                        if (!(wu > 0.02) || !(lu > 0.02)) continue;
                        yU0 = deckTop;
                        for (uk = 0; uk < UPL.length; uk++) {
                          _asmMark(ctx, UPL[uk].key || 'layer_floor_finish', 'top', UPL[uk].label || 'Floor build-up', UPL[uk].code || '');
                          box(wu, UPL[uk].t, lu, (Ru.x0 + Ru.x1) / 2, yU0 + UPL[uk].t / 2, (Ru.z0 + Ru.z1) / 2, UPM[uk]);
                          yU0 += UPL[uk].t;
                        }
                        UPA += wu * lu;
                      }
                      H.upLiftA = UPA;   /* the area laid, for the product's bill */
                      _asmMark(ctx, 'layer_floor_finish', 'top', 'Floor finish', (function () { var fq3, best = ''; for (fq3 = 0; fq3 < F2.lays.length; fq3++) if (F2.lays[fq3].per === 'area') best = F2.lays[fq3].code; return best || F2.lays[0].code; })());
                    }
                    if (hPc && !(hPc.length === 1 && hPc[0].x0 === cx0 && hPc[0].x1 === cx1 && hPc[0].z0 === cz0 && hPc[0].z1 === cz1)) {
                      /* garageapt-v12: round the stairwell -- the pieces left, the hole off the area */
                      var hq, aPc = 0;
                      for (hq = 0; hq < hPc.length; hq++) {
                        box(hPc[hq].x1 - hPc[hq].x0, F2.h, hPc[hq].z1 - hPc[hq].z0, (hPc[hq].x0 + hPc[hq].x1) / 2, yOver + F2.h / 2, (hPc[hq].z0 + hPc[hq].z1) / 2, fmT);
                        aPc += (hPc[hq].x1 - hPc[hq].x0) * (hPc[hq].z1 - hPc[hq].z0);
                      }
                      ar -= rw * rl - aPc;
                      lay.wellCut = (lay.wellCut || 0) + (rw * rl - aPc);
                    } else box(rw, F2.h, rl, (cx0 + cx1) / 2, yOver + F2.h / 2, (cz0 + cz1) / 2, fmT);
                  } else {
                    /* ON the room's own finish -- yOver is already past the finish
                       row by here, so adding F2.h again floated it a centimetre up */
                    var sk = 0.070, skT = 0.018, skY = yOver;
                    /* SKIRTING GOES WHERE THERE IS A WALL, NOT ROUND EVERY BOX.

                       A room comes back as several rectangles -- the plan splits
                       it wherever a partition ABOVE it meets the through wall,
                       so a plain rectangular room can arrive in three pieces.
                       Running skirting round each piece then draws a board along
                       every internal seam: short lengths standing in the middle
                       of the floor exactly where the partitions upstairs would
                       have continued, which is what he is seeing.

                       An edge shared with another rectangle OF THE SAME ROOM has
                       no wall against it. The perimeter is counted the same way,
                       so the bill loses the same lengths the model does. */
                    var edges = [
                      [cx0, cx1, cz0, cz0, true],
                      [cx0, cx1, cz1, cz1, true],
                      [cx0, cx0, cz0, cz1, false],
                      [cx1, cx1, cz0, cz1, false]
                    ], eq;
                    for (eq = 0; eq < edges.length; eq++) {
                      var E = edges[eq];
                      if (_sharedEdge(RS, sq, E[0], E[1], E[2], E[3], E[4]) || _onRoomLine(E[0], E[1], E[2], E[3], E[4])) {
                        pr -= E[4] ? rw : rl;
                        continue;
                      }
                      /* AND IT STOPS AT A DOORWAY (adu-v1412). The board ran
                         straight across every door in the room -- interior
                         doors, the entrance, a glazed door down to the floor --
                         drawn there and bought there; the log owned up to it
                         (DOORWAYS ARE NOT DEDUCTED YET). The edge is cut where a
                         doorway crosses it and each piece between is a board. */
                      var aE = E[4] ? cx0 : cz0, bE = E[4] ? cx1 : cz1, lnE = E[4] ? E[2] : E[0];
                      var cutsE = _skirtCuts(E[4], lnE, aE, bE), curE = aE, keptE = 0, ce;
                      /* anyhouse-lib-v11: nor on the L's seam -- the room runs on there */
                      var smE = _seamCuts ? _seamCuts(E[4], lnE, aE, bE) : [];
                      if (smE.length) { cutsE = cutsE.concat(smE); cutsE.sort(function (c1, c2) { return c1[0] - c2[0]; }); }
                      var piecesE = [];
                      for (ce = 0; ce < cutsE.length; ce++) { if (cutsE[ce][0] > curE + 0.02) piecesE.push([curE, cutsE[ce][0]]); curE = Math.max(curE, cutsE[ce][1]); }
                      if (bE > curE + 0.02) piecesE.push([curE, bE]);
                      for (ce = 0; ce < piecesE.length; ce++) {
                        var pa = piecesE[ce][0], pb = piecesE[ce][1], pl = pb - pa;
                        if (pl < 0.05) continue;
                        keptE += pl;
                        if (E[4]) {
                          box(pl, sk, skT, (pa + pb) / 2, skY + sk / 2,
                              (E[2] === cz0) ? cz0 + skT / 2 : cz1 - skT / 2, fm);
                        } else {
                          box(skT, sk, pl, (E[0] === cx0) ? cx0 + skT / 2 : cx1 - skT / 2,
                              skY + sk / 2, (pa + pb) / 2, fm);
                        }
                      }
                      if (cutsE.length) {
                        pr -= (bE - aE) - keptE;
                        lay.skCut = (lay.skCut || 0) + ((bE - aE) - keptE);
                        /* a doorway once per room, however many of the room's
                           rectangles its edge is cut into */
                        lay.skCutK = lay.skCutK || {};
                        for (ce = 0; ce < cutsE.length; ce++) for (var ck = 0; ck < cutsE[ce][2].length; ck++) lay.skCutK[rq2 + '|' + cutsE[ce][2][ck]] = 1;
                        lay.skCutN = Object.keys(lay.skCutK).length;
                      }
                    }
                  }
                }
                /* THE SLANTED WALLS IN THIS ROOM (adu-v1411). The room's
                   rectangles know only the square partitions, so the ground a
                   slanted wall stands on was finished and its two faces had no
                   skirting. The finish loses the wall's footprint in the room
                   its middle stands in; each face gets its skirting in the room
                   it faces, run in the pieces between its doorways. */
                /* and the corners past slanted walls that are this room's floor */
                if (L.room === 'finish' && GETS[rq2]) {
                  var fmG = (q === FLOOR_SHOW) ? fm : _finMat(T, fm, F2.piece, 1, 1), kg;
                  for (kg = 0; kg < GETS[rq2].length; kg++) { finPoly(GETS[rq2][kg].poly, F2.h, yOver + F2.h, fmG); ar += GETS[rq2][kg].a; }
                }
                var SLf = _slantRaw(), sfq, sfs;
                var inRS = function (px, pz) {
                  for (var qR = 0; qR < RS.length; qR++) { var rR = RS[qR]; if (typeof rR.x0 === 'number' && px >= rR.x0 - 0.01 && px <= rR.x1 + 0.01 && pz >= rR.z0 - 0.01 && pz <= rR.z1 + 0.01) return true; }
                  return false;
                };
                for (sfq = 0; sfq < SLf.length; sfq++) {
                  var gF = SLf[sfq], runF = Math.max(0, gF.len - PART.d);
                  if (L.room === 'finish') {
                    if (inRS(gF.mx, gF.mz)) { ar -= runF * PART.d; lay.slantFin = (lay.slantFin || 0) + runF * PART.d; }
                    continue;
                  }
                  for (sfs = -1; sfs <= 1; sfs += 2) {
                    var offF = PART.d / 2 + 0.10, fxF = gF.mx + gF.nx * sfs * offF, fzF = gF.mz + gF.nz * sfs * offF;
                    /* the room the face really looks into: past a corner's
                       room line that is not the rectangle it stands in (adu-v1414) */
                    var wqF = _wedgeRoomAt(fxF, fzF);
                    if (wqF >= 0 ? (wqF !== rq2) : !inRS(fxF, fzF)) continue;
                    /* the face, less a doorway wherever a door stands in it */
                    var cutF = [], curF = PART.d / 2, dqF;
                    for (dqF = 0; dqF < gF.doors.length; dqF++) cutF.push([gF.doors[dqF].u - (gF.doors[dqF].w || 0.8) / 2, gF.doors[dqF].u + (gF.doors[dqF].w || 0.8) / 2]);
                    cutF.sort(function (c1, c2) { return c1[0] - c2[0]; });
                    var piecesF = [];
                    for (dqF = 0; dqF < cutF.length; dqF++) { if (cutF[dqF][0] > curF + 0.05) piecesF.push([curF, cutF[dqF][0]]); curF = Math.max(curF, cutF[dqF][1]); }
                    if (gF.len - PART.d / 2 > curF + 0.05) piecesF.push([curF, gF.len - PART.d / 2]);
                    var skF = 0.070, skTF = 0.018, faceF = PART.d / 2 + PART_BOARD.t + skTF / 2;
                    for (dqF = 0; dqF < piecesF.length; dqF++) {
                      var pl0 = piecesF[dqF][0], pl1 = piecesF[dqF][1], plM = (pl0 + pl1) / 2;
                      var mF = box(pl1 - pl0, skF, skTF, gF.a[0] + gF.ux * plM + gF.nx * sfs * faceF, yOver + skF / 2, gF.a[1] + gF.uz * plM + gF.nz * sfs * faceF, fm);
                      mF.rotation.y = -gF.ang;
                      pr += pl1 - pl0;
                      lay.slantSk = (lay.slantSk || 0) + (pl1 - pl0);
                    }
                  }
                }
                if (!(ar > 0)) continue;

                /* AND THIS TOOK THE SAME LENGTH OFF A SECOND TIME.

                   The edge loop above already drops a shared edge: it skips
                   the board AND subtracts that edge from pr, which is what
                   its own comment says -- the bill loses the same lengths the
                   model does. Then this line subtracted the shared run again,
                   so a whole ADU came out with 0.6 m of skirting against the
                   30.6 m actually drawn (Marek's audit: FFW00002 51x).

                   One subtraction, in the loop, where the board is skipped. */
                if (pr < 0) pr = 0;

                if (L.room === 'finish') {
                  for (var fq = 0; fq < F2.lays.length; fq++) {
                    var LY = F2.lays[fq];
                    _add(LAYER, LY.code,
                         LY.per === 'tank' ? Math.round((ar + pr * TANK_UP) * (LY.kgM2 || 1) * 10) / 10
                         : LY.per === 'kg' ? Math.round(ar * LY.kgM2 * 10) / 10 : ar);
                  }
                  ra += ar;
                  /* ranch-v35: a room of more than 40 m2 gets a joint across it, the shorter way */
                  if (BLKMODE && ar > BLK_GFL.field) {
                    var jx0 = 1e9, jx1 = -1e9, jz0 = 1e9, jz1 = -1e9, jq;
                    for (jq = 0; jq < RS.length; jq++) if (typeof RS[jq].x0 === 'number') { jx0 = Math.min(jx0, RS[jq].x0); jx1 = Math.max(jx1, RS[jq].x1); jz0 = Math.min(jz0, RS[jq].z0); jz1 = Math.max(jz1, RS[jq].z1); }
                    var jCuts = Math.ceil(ar / BLK_GFL.field) - 1, jLen = jCuts * Math.max(0, Math.min(jx1 - jx0, jz1 - jz0));
                    if (jLen > 0) { _add(LAYER, BLK_GFL.joint, jLen); lay.blkJoint = (lay.blkJoint || 0) + jLen; lay.blkJointN = (lay.blkJointN || 0) + jCuts; }
                  }
                } else {
                  _add(LAYER, F2.skirt.code, pr);
                  _add(LAYER, 'FAS50060', Math.ceil(pr / 0.6));   /* a screw every 600 */
                  ra += pr;
                  /* ranch-v35: the screed's edge strip runs where the skirting does */
                  if (BLKMODE && pr > 0) { _add(LAYER, BLK_GFL.strip, pr); lay.blkStrip = (lay.blkStrip || 0) + pr; }
                }

                if (q === FLOOR_SHOW || FLOOR_SHOW < 0) {
                  lay.rooms.push({ n: _roomLabel(ROOM, rq2), a: (L.room === 'finish') ? ar : pr,
                                   wet: !!WET_ROOMS[rkey], what: (L.room === 'finish') ? F2.label : F2.skirt.label,
                                   h: F2.h, per: (L.room === 'finish') ? 'area' : 'run',
                                   parts: RS.length });
                }
              }
              /* THE FINISH'S AREA, NOT THE SKIRTING'S RUN (adu-v1411). Both room
                 layers wrote here and the skirting came last, so the log read
                 its metres as square feet -- 1033 sqft of finish against 832 of
                 plan. Each keeps its own now. */
              if (L.room === 'finish') { lay.finArea = ra; lay.finPlan = w * l; }
              else lay.skRun = ra;
              lay.finKind = L.room;
              yOver += th;
              lay.area += ra;
              lay.n++;
              lay.rows.push({ q: q, lb: L.label, n: _boxN - _b0 });
              continue;
            }

            box(w, th, l, cx, yOver + th / 2, cz, lm);
            if (L.code) _add(LAYER, L.code, w * l);
            if (!L.code) lay.noCode = (lay.noCode || []).concat([L.label]);
            yOver += th;
            lay.area += w * l;
            lay.n++;
            lay.rows.push({ q: q, lb: L.label, n: _boxN - _b0 });
            continue;
          }

          if (L.where !== 'bay') continue;

          if (L.viz === 'layer_floor_membrane') {
            /* ONE SHEET, LAID OVER THE JOISTS AND PUSHED INTO EVERY POCKET.

               A mat cut to each bay stops dead at the joist and leaves the one
               line the draught actually gets through -- the joint between bay and
               joist. Laid over the tops and pressed down into each pocket, the
               whole floor is a single tray: nothing to cut to size, nothing to
               line up, and the only joints are where two rolls lap.

               AND IT CARRIES OUT OVER THE RIM. That tail is clamped under the
               wall's sole plate, which is what seals the floor to the wall. Stop
               it at the joist top inside and the perimeter is open, which is the
               one place the wind gets under the building.

               It costs about half as much again in material and it is worth it. */
            /* ALL FOUR SIDES OF EVERY POCKET, not two.

               I lined the joist faces and left the ends open, so a block had its
               top covered and its sides bare -- which is a tray with two walls
               missing. If the sheet goes over the top of a member it goes down
               both its faces, or it is not one sheet. */
            var upH = joistTop - yBay;
            var mq, MB, ma = 0;
            if (RSB) _boxBatch = { B: RSB.boxes(), qtyV: 0, mat: lm };
            for (mq = 0; mq < BAYS.length; mq++) {
              MB = BAYS[mq];
              var mw = MB.x1 - MB.x0, md = MB.z1 - MB.z0;
              /* INSIDE THE POCKET, NOT IN THE FACE OF THE TIMBER.

                 I had the upstands at MB.z0 - th/2, which is half a millimetre
                 INTO the joist -- two surfaces in the same plane, which is what
                 was flickering. A lining goes on the inside of what it lines. */
              box(mw, th, md, (MB.x0 + MB.x1) / 2, yBay + th / 2, (MB.z0 + MB.z1) / 2, lm);
              box(mw, upH, th, (MB.x0 + MB.x1) / 2, yBay + upH / 2, MB.z0 + th / 2, lm);
              box(mw, upH, th, (MB.x0 + MB.x1) / 2, yBay + upH / 2, MB.z1 - th / 2, lm);
              box(th, upH, md, MB.x0 + th / 2, yBay + upH / 2, (MB.z0 + MB.z1) / 2, lm);
              box(th, upH, md, MB.x1 - th / 2, yBay + upH / 2, (MB.z0 + MB.z1) / 2, lm);
              ma += mw * md + (mw + md) * upH * 2;
            }
            /* across the top of every joist, which is what makes it one sheet */
            var jt2 = tally.joistAt || [], jq2;
            for (jq2 = 0; jq2 < jt2.length; jq2++) {
              box(w - JOIST.t * 2, th, JOIST.t, cx, joistTop + th / 2 + MEM_EPS, jt2[jq2], lm);
              ma += (w - JOIST.t * 2) * JOIST.t;
            }
            /* AND OVER THE BLOCKING. It crosses the bay the same way a joist
               does; going round it would leave the tray cut in two at every row
               and an open seam along each one. */
            var bq2;
            for (bq2 = 0; bq2 < blockAt.length; bq2++) {
              box(JOIST.t, th, blockAt[bq2].zb - blockAt[bq2].za, blockAt[bq2].x,
                  joistTop + th / 2 + MEM_EPS, (blockAt[bq2].za + blockAt[bq2].zb) / 2, lm);
              ma += JOIST.t * (blockAt[bq2].zb - blockAt[bq2].za);
            }

            /* OUT OVER THE RIM, AND THE FOLD IS NOT ON THE ARRIS. A sheet does
               not crease exactly along the edge of the timber -- it runs on flat
               a little way past it and breaks there, then comes down the outside
               face. Folded on the corner it would be a drawing, not a detail. */
            var lap = MEM_LAP, flat = MEM_FLAT;
            box(w + flat * 2, th, JOIST.t + flat, cx, joistTop + th / 2 + MEM_EPS, z0 - flat / 2 + JOIST.t / 2, lm);
            box(w + flat * 2, th, JOIST.t + flat, cx, joistTop + th / 2 + MEM_EPS, z1 + flat / 2 - JOIST.t / 2, lm);
            box(JOIST.t + flat, th, l, x0 - flat / 2 + JOIST.t / 2, joistTop + th / 2 + MEM_EPS, cz, lm);
            box(JOIST.t + flat, th, l, x1 + flat / 2 - JOIST.t / 2, joistTop + th / 2 + MEM_EPS, cz, lm);
            /* the fold, hanging down the outside face of the rim */
            box(w + flat * 2, lap, th, cx, joistTop - lap / 2, z0 - th / 2, lm);
            box(w + flat * 2, lap, th, cx, joistTop - lap / 2, z1 + th / 2, lm);
            box(th, lap, l, x0 - th / 2, joistTop - lap / 2, cz, lm);
            box(th, lap, l, x1 + th / 2, joistTop - lap / 2, cz, lm);
            ma += (w + l) * 2 * (JOIST.t + flat + lap);
            _boxFlush();

            if (L.code) _add(LAYER, L.code, ma);
            lay.area += ma;
            lay.memArea = ma;
            yBay += th;      /* the board lands ON it, in the pocket */
          } else {
            /* ONE PIECE PER BAY, DROPPED IN FROM ABOVE ONTO THE RAILS. Not one
               sheet under everything -- that board would have to be held up
               against the joists from underneath while it is fixed. */
            var aBay = 0, bq;
            for (bq = 0; bq < BAYS.length; bq++) {
              var Bb = BAYS[bq];
              box(Bb.x1 - Bb.x0, th, Bb.z1 - Bb.z0,
                  (Bb.x0 + Bb.x1) / 2, yBay + th / 2, (Bb.z0 + Bb.z1) / 2, lm);
              aBay += (Bb.x1 - Bb.x0) * (Bb.z1 - Bb.z0);
            }
            if (L.code && L.code.charAt(0) !== '@') _add(LAYER, L.code, aBay);
            if (L.code === 'ROV00001') _add(LAYER, 'INS01005', aBay / 1.5 + 2 * Math.sqrt(aBay));   /* laps every roll, and round the edge */
            /* screwed down into the rails it is resting on */
            if (L.viz === 'layer_floor_osb') {
              lay.boardScrews = Math.round(aBay * 9);
              _add(LAYER, RAIL.screw, lay.boardScrews);
            }
            lay.area += aBay;
            yBay += th;
          }
          lay.n++;
          lay.rows.push({ q: q, lb: L.label, n: _boxN - _b0 });
        }
      })();

      ctx.log('floorstep  ' + (FLOOR_SHOW < 0 ? FLOOR_LAYERS.length : (FLOOR_SHOW + 1)) +
              ' of ' + FLOOR_LAYERS.length + ' -- drawn: ' +
              (lay.n ? FLOOR_LAYERS.slice(0, FLOOR_SHOW < 0 ? FLOOR_LAYERS.length : FLOOR_SHOW + 1)
                         .filter(function (L2) { return L2.on && (!SLABMODE || L2.room); })   /* ranch-v36: on a slab only the rooms' layers are drawn */
                         .map(function (L2) { return L2.label; }).join(' + ')
                     : 'nothing yet') +
              (FLOOR_SHOW < 0 ? '   -- all of them, none magenta'
                              : '   <-- the newest is MAGENTA'));
      if (lay.rails > 0) {
        ctx.log('floor    rails ' + Math.round(lay.rails * 10) / 10 + ' m of ' +
                Math.round(RAIL.proj * 1000) + 'x' + Math.round(RAIL.h * 1000) +
                ' down both sides of every joist -- ' + BAYS.length + ' pocket(s), the board ' +
                'drops in from above onto them, top at ' + railTop.toFixed(3) +
                ', held ' + Math.round(RAIL.drop * 1000) + ' mm up from the joist soffit so ' +
                'the two read apart from below' + (RSB ? ('; drawn as one body of ' + (BAYS.length * 2) + ' piece(s) (ranch-v65)') : ''));
      }
      if (lay.railScrews > 0) {
        ctx.log('floor    rail screws ' + lay.railScrews + ' -- one at 300 mm along ' +
                Math.round(lay.rails * 10) / 10 + ' m plus two at each rail end, ' + RAIL.screw);
      }
      if (lay.memArea) {
        ctx.log('floor    membrane ' + API.fmtArea(lay.memArea) + ' as ONE tray -- over every ' +
                'joist top, down into all ' + BAYS.length + ' pocket(s), and ' +
                Math.round(MEM_LAP * 1000) + ' mm out over the rim, turned down its outside ' +
                'face so the sole plate clamps it');
        ctx.log('floor    that is ' + Math.round(lay.memArea / (w * l) * 100) + '% of the ' +
                'floor area -- a tray is not a mat, and the difference is the upstands');
      }
      if (lay.boardScrews) {
        ctx.log('floor    board screws ' + lay.boardScrews + ' -- 9 a square metre into the ' +
                'rails it sits on, ' + RAIL.screw);
      }
      if (lay.woolMix) {
        var wmix = lay.woolMix.mix.map(function (M2) { return M2.n + ' x ' + M2.mm + ' mm'; }).join(' + ');
        ctx.log('floor    wool ' + wmix + ' = ' + Math.round(lay.woolMix.t * 1000) +
                ' mm nominal, squeezed into a ' + Math.round(lay.woolDepth * 1000) + ' mm pocket -- ' +
                Math.round((lay.woolMix.t - lay.woolDepth) * 1000) + ' mm of compression, ' +
                Math.round((lay.woolMix.t - lay.woolDepth) / lay.woolMix.t * 100) + '%');
        ctx.log('floor    wool ' + API.fmtArea(lay.woolArea) + ' of pocket, ' +
                (Math.round(lay.woolVol * 100) / 100) + ' m3 in place -- the POCKETS, not the ' +
                'floor: billing the plan area buys wool for the timber as well');
      }
      if (lay.noCode && lay.noCode.length) {
        ctx.log('floor    NO CODE for ' + lay.noCode.join(' and ') + ' -- drawn and measured, ' +
                'not buyable until the catalogue carries them');
      }
      if (lay.garageSkip) ctx.log('floor    the GARAGE: no finish, no skirting -- its floor is the bare slab (ranch-v32)');
      if (BLKMODE) ctx.log('BLK  floor edge strip ' + BLK_GFL.strip + ' ' + (Math.round((lay.blkStrip || 0) * 10) / 10) + ' m (where the skirting runs, doorways left out)' +
                           (lay.blkJoint ? (', ' + lay.blkJointN + ' field joint(s) ' + BLK_GFL.joint + ' ' + (Math.round(lay.blkJoint * 10) / 10) + ' m across room(s) over ' + BLK_GFL.field + ' m2') : ', no room over ' + BLK_GFL.field + ' m2') + ' (ranch-v35)');
      if (lay.rooms && lay.rooms.length) {
        var isRun = lay.rooms[0].per === 'run';
        var nFinR = lay.rooms.filter(function (R7) { return R7.per !== 'run'; }).length;
        ctx.log('floor    ' + (isRun ? 'skirting' : 'finish') + ' laid room by room -- ' +
                (isRun ? lay.rooms.length : nFinR) + ' room(s)' +
                (isRun ? '' : (', ' + API.fmtArea(lay.finArea) + ' against ' +
                               API.fmtArea(lay.finPlan) + ' of plan; the difference is what the ' +
                               'partitions stand on')));
        if (lay.skRun) ctx.log('floor    skirting laid room by room -- ' + (Math.round(lay.skRun * 10) / 10) + ' m' +
                               (lay.skCutN ? (', ' + lay.skCutN + ' doorway(s) left out, ' + (Math.round(lay.skCut * 10) / 10) + ' m (adu-v1412)') : ''));
        if (lay.slantFin) ctx.log('floor    slanted walls stand on ' + API.fmtArea(lay.slantFin) + ' -- taken off the finish of the room each stands in (adu-v1411)');
        if (lay.wedgeFin) ctx.log('floor    ' + API.fmtArea(lay.wedgeFin) + ' past slanted walls laid with the finish of the room it opens into, not the room behind the wall (adu-v1414)');
        if (lay.wellCut) ctx.log('GA floor 6c ' + API.fmtArea(lay.wellCut) + ' of finish left out over the stairwell (garageapt-v12)');
        if (lay.slantSk) ctx.log('floor    slanted walls take ' + (Math.round(lay.slantSk * 10) / 10) + ' m of skirting on their faces, doorways left out (adu-v1411)');
        for (var rl2 = 0; rl2 < lay.rooms.length; rl2++) {
          var RW = lay.rooms[rl2];
          ctx.log('  room     ' + RW.n + '  ' +
                  (RW.per === 'run' ? (Math.round(RW.a * 10) / 10 + ' m') : API.fmtArea(RW.a)) +
                  '  ' + RW.what + (RW.wet ? '   <-- WET' : '') +
                  (RW.per === 'run' ? '' : ('  ' + Math.round(RW.h * 1000) + ' mm')));
        }
        if (isRun && !H.skirtCuts) {
          _why(ctx, 'floor    skirting is the full room perimeter -- DOORWAYS ARE NOT ' +
                  'DEDUCTED YET, so every doorway is a door width over');
        } else if (isRun) {
          _why(ctx, 'floor    skirting is the room perimeter less its doorways -- ' +
                  (lay.skCutN ? (lay.skCutN + ' doorway(s), ' + (Math.round(lay.skCut * 10) / 10) + ' m left out (adu-v1412)') : 'no doorway crosses a wall here'));
        } else {
          _why(ctx, 'floor    a wet room finishes 3 mm higher than a dry one -- tile and ' +
                  'adhesive against vinyl and underlay -- so the threshold at the door ' +
                  'takes that up');
        }
      }
      if (lay.rows) {
        for (var rw2 = 0; rw2 < lay.rows.length; rw2++) {
          var RO = lay.rows[rw2];
          ctx.log('  layer ' + RO.q + '    ' + (RO.lb || '?') + ' -- ' + RO.n + ' mesh(es)' +
                  (RO.why ? ('   ' + RO.why) : (RO.n ? '' : '   <-- DREW NOTHING')));
        }
      }
      ctx.log('floor    build-up ' + lay.n + ' layer(s) drawn, ' + API.fmtArea(lay.area) +
              ' -- the bill carries every layer whatever is drawn');
      ctx.log('floor    deck top at ' + deckTop.toFixed(3) + ' m, which is where the wall plates sit');
      (function () {
        var fin = 0, q2;
        for (q2 = 0; q2 < FLOOR_LAYERS.length; q2++) {
          if (FLOOR_LAYERS[q2].on && FLOOR_LAYERS[q2].where === 'over' &&
              FLOOR_LAYERS[q2].code !== 'OSB25002') fin += FLOOR_LAYERS[q2].t;
        }
        if (fin > 0) {
          ctx.log('floor    finished floor ' + Math.round(fin * 1000) + ' mm above the deck -- ' +
                  'the walls stand on the DECK, so the clear height is that much less ' +
                  'and the skirting covers the joint');
        }
      })();
    }

    /* =========================================================================
       THE CEILING MODULE (core-v780, the sixth shared module of the house
       line, its second part -- status/modules.md 6b). The ceiling written
       once: under trusses the blown fill on the boards, cut by the roof
       above it; under a flat roof only the ceiling (the warm roof's boards
       are the roof's); under a mono-pitch the level joists on their ledgers
       beside the studs; and under every one the vapour barrier bought by the
       roll and taped, the battens across the members, the plasterboard room
       by room (moisture-resistant over the wet rooms), its screws, tape and
       filler. The ADU's text with the products' own parts behind hooks.

         API.houseCeiling(ctx, {
           api, layer ('adu-ceiling'), wood, MAGENTA, CEIL_JOIST, CHORD,
           LEDGER, STUD, BLOWN, CEIL_BOARD, CEIL_VB, CEIL_BATTEN, FILLER_KG_M2,
           INT_PAINT, WET_ROOMS, billReset, isNew, box2, boardCol, roomRects,
           roomKey, roomLabel, roofPlaneYAt, skinLift, trussStations,
           soffit (_ceilingSoffit), roofMembers,
           add:       function (code, qty)
           setTally:  function (tally) -- the product's _ceilTally
           paintTake: function (m2) -> the lib's interior-paint sum with this
                      ceiling's board, the sum set to nought
           rIn, seamTape  ANY HOUSE / RANCH -- the L a rectangle at a time
           blk:       { masonryGable, gableEdges, wallT } -- RANCH
           cold:      { coldRects, cells, dam, hasGarage } -- RANCH's garage
           hot:       { step, key, review, pdfReset, rectsIn, cellsBy, ceil }
                      -- SAUNA's sauna room
           hatch      GA_HATCH -- GARAGE APT / ANY HOUSE's attic hatch
         })

       Carried: the interior paint billed once, by the core from the board
       (SAUNA billed it a second time from its ceiling); the wet room's board
       marked under its own code for the assembly (ranch-v59) to everyone;
       the L's seam on a flat roof's ceiling (ANY HOUSE's) to RANCH. Not
       carried: RANCH's half-lap tape at the seam (seamTape) -- it halves the
       whole side of a rectangle, the stretch along a wall too.
       ========================================================================= */
    function houseCeiling(ctx, H) {
      /* the product's own pieces, under the names the text has always used */
      var API = H.api, LAYER = H.layer, MAGENTA = H.MAGENTA, WOOD = H.wood, CEIL_JOIST = H.CEIL_JOIST,
          CHORD = H.CHORD, LEDGER = H.LEDGER, STUD = H.STUD, BLOWN = H.BLOWN, CEIL_BOARD = H.CEIL_BOARD,
          CEIL_VB = H.CEIL_VB, CEIL_BATTEN = H.CEIL_BATTEN, FILLER_KG_M2 = H.FILLER_KG_M2,
          INT_PAINT = H.INT_PAINT, WET_ROOMS = H.WET_ROOMS;
      var _billReset = H.billReset, _add = function (cat, code, q) { H.add(code, q); }, isNew = H.isNew,
          box2 = H.box2, _boardCol = H.boardCol, _roomRects = H.roomRects, _roomKey = H.roomKey,
          _roomLabel = H.roomLabel, _roofPlaneYAt = H.roofPlaneYAt, _skinLift = H.skinLift,
          _trussStations = H.trussStations, _ceilingSoffit = H.soffit, _roofMembers = H.roofMembers;
      /* the products' own ceilings, each behind its hook (status/modules.md 6b):
         rIn      ANY HOUSE / RANCH -- the L built a rectangle at a time: no
                  wall at the seam; seamTape (RANCH) the barrier's tape half
                  a lap on a seam side
         blk      RANCH -- a masonry gable stops the fill at its inner face
         cold     RANCH -- no fill and no retarder over the garage, the dam
         hot      SAUNA -- the sauna room lined, not boarded (its own ceiling)
         hatch    GARAGE APT / ANY HOUSE -- the attic hatch (IRC R807.1) */
      var _rIn = H.rIn || function () { return 1; };
      var BLK = H.blk || null, GAR = H.cold || null, HOT = H.hot || null, HATCH = H.hatch || null;
      var _rsCeilBlown = false;   /* RANCH: the fill is blown, so the garage is outside the envelope */
      function _ceilBoard(ctx, yBatt, bx0, bx1, bz0, bz1) {
        _asmMark(ctx, 'layer_plasterboard', 'top', 'Plasterboard', CEIL_BOARD.dry.code);
        var T = ctx.THREE, g = ctx.group;
        var yB = yBatt - CEIL_BOARD.t;
        var RM = _roomRects();
        var done = { dry: 0, wet: 0 }, q5, hotOff = 0, sheetKind = 'dry';

        /* CLIPPED TO THE RECTANGLE THIS WAS GIVEN. */
        function sheet(kind, sx0, sx1, sz0, sz1) {
          if (sx0 < bx0) sx0 = bx0;
          if (sx1 > bx1) sx1 = bx1;
          if (sz0 < bz0) sz0 = bz0;
          if (sz1 > bz1) sz1 = bz1;
          var w5 = sx1 - sx0, l5 = sz1 - sz0;
          if (!(w5 > 0.05) || !(l5 > 0.05)) return;
          var K = CEIL_BOARD[kind];
          /* ranch-v59: a wet room's moisture-resistant board under its own code
             (it wore the plain board's: the audit had WDD24002 0 of 23 m2) */
          if (kind !== sheetKind) { _asmMark(ctx, 'layer_plasterboard', 'top', 'Plasterboard', K.code); sheetKind = kind; }
          var m5 = new T.MeshStandardMaterial({
            color: isNew(LAYER + '.board') ? MAGENTA : _boardCol(K), roughness: 0.92, metalness: 0.0 });
          box2(T, g, m5, w5, CEIL_BOARD.t, l5, (sx0 + sx1) / 2, yB + CEIL_BOARD.t / 2, (sz0 + sz1) / 2);
          done[kind] += w5 * l5;
        }

        if (!RM.length) {
          sheet('dry', bx0, bx1, bz0, bz1);
        } else {
          for (q5 = 0; q5 < RM.length; q5++) {
            var ROOM5 = RM[q5];
            var RS5 = ROOM5.rects || (typeof ROOM5.x0 === 'number' ? [ROOM5] : []);
            var wet5 = !!WET_ROOMS[_roomKey(ROOM5, q5)];
            var hot5 = !!HOT && (HOT.step() >= 4) && _roomKey(ROOM5, q5) === HOT.key;
            for (var r5 = 0; r5 < RS5.length; r5++) {
              var R5 = RS5[r5];
              if (typeof R5.x0 !== 'number') continue;
              if (hot5) {
                /* the sauna room is lined, not boarded -- and its area is not
                   handed back as a strip over the partitions either */
                hotOff += Math.max(0, Math.min(bx1, R5.x1) - Math.max(bx0, R5.x0)) *
                          Math.max(0, Math.min(bz1, R5.z1) - Math.max(bz0, R5.z0));
                continue;
              }
              sheet(wet5 ? 'wet' : 'dry', R5.x0, R5.x1, R5.z0, R5.z1);
            }
          }
          /* the rooms stop at the partitions; the strips over them are dry board */
          var covered = done.dry + done.wet, whole = (bx1 - bx0) * (bz1 - bz0) - hotOff;
          if (whole - covered > 0.2) done.dry += (whole - covered);
        }

        if (done.dry > 0) _add(LAYER, CEIL_BOARD.dry.code, done.dry);
        if (done.wet > 0) _add(LAYER, CEIL_BOARD.wet.code, done.wet);

        /* garageapt-v29 (17a, COPIED from barndo-v26): THE ATTIC HATCH, 22 x 30
           in. (IRC R807.1: an attic of 30 sq ft and 30 in. high wants one) --
           in a hall, the laundry or a closet when the plan has one (not a wet
           room), else in the biggest room; at the room's middle, its long side
           along the trusses so it falls between two of them */
        if (HATCH) try {
          var hR = null, hBest = -1, hq, hr;
          for (hq = 0; hq < RM.length; hq++) {
            var RMh = RM[hq], RSh = RMh.rects || (typeof RMh.x0 === 'number' ? [RMh] : []), keyH = '', nmH = '';
            try { keyH = _roomKey(RMh, hq) || ''; nmH = _roomLabel(RMh, hq) || ''; } catch (eKh) {}
            if (WET_ROOMS[keyH]) continue;
            for (hr = 0; hr < RSh.length; hr++) {
              var Rh = RSh[hr];
              if (typeof Rh.x0 !== 'number' || Rh.x1 - Rh.x0 < HATCH.l + 0.10 || Rh.z1 - Rh.z0 < HATCH.l + 0.10) continue;
              var sc = (Rh.x1 - Rh.x0) * (Rh.z1 - Rh.z0) + (/hall|corridor|landing|laundry|closet|wardrobe|utility/i.test(keyH + ' ' + nmH) ? 1000 : 0);
              if (sc > hBest) { hBest = sc; hR = { r: Rh, nm: nmH || keyH }; }
            }
          }
          if (!hR) hR = { r: { x0: bx0, x1: bx1, z0: bz0, z1: bz1 }, nm: 'the ceiling' };
          var alongXh = (API.STATE.roof && API.STATE.roof.axis === 'w');   /* trusses across the ridge */
          var hx = (hR.r.x0 + hR.r.x1) / 2, hz = (hR.r.z0 + hR.r.z1) / 2;
          _asmMark(ctx, 'ga_attic_hatch', 'top', 'Attic hatch', HATCH.code);
          var hm = new T.Mesh(new T.BoxGeometry(alongXh ? HATCH.w : HATCH.l, 0.010, alongXh ? HATCH.l : HATCH.w),
                              new T.MeshStandardMaterial({ color: 0xDCDAD3, roughness: 0.7, metalness: 0.0 }));
          hm.position.set(hx, yB - 0.005, hz); hm.name = 'ga-attic-hatch'; g.add(hm);
          _add(LAYER, HATCH.code, 1);
          ctx.log('ceiling attic hatch (garageapt-v29, IRC R807.1) 22 x 30 in. ' + HATCH.code + ' in ' + String(hR.nm).toUpperCase() + ' at ' + hx.toFixed(2) + ', ' + hz.toFixed(2));
        } catch (eHt) { ctx.log('ceiling attic hatch THREW ' + ((eHt && eHt.message) || eHt)); }

        var total = done.dry + done.wet;
        _add(LAYER, CEIL_BOARD.screw, Math.round(total * 20));
        /* a 2400 x 1200 sheet has 7.2 m of edge for 2.88 m2, and half of every
           joint is the neighbour's -- so the taped run is area / 2.88 * 3.6 */
        _add(LAYER, CEIL_BOARD.tape, Math.round(total / 2.88 * 3.6 * 10) / 10);
        _add(LAYER, CEIL_BOARD.filler, Math.round(total * FILLER_KG_M2 * 10) / 10);

        /* THE INTERIOR PAINT, ONE ROW FOR THE WHOLE INSIDE.

           Every board face that has just been put up -- partitions on both sides,
           the walls with their reveals, this ceiling -- is a face that gets
           painted, and it is painted once by the surface: how many coats belongs
           in the row, as it does for the exterior coating. The three layers add
           their area as they build and this one, the last of them, bills the sum.
           The code is INT_PAINT.code in the switch block at the end of the file,
           because the catalogue has no interior paint row yet and nothing is
           invented here: until he names one, the area is in the log and not in
           the bill. */
        var _intPaintA = H.paintTake(total);   /* the lib's running sum, handed back and set to nought */
        /* THE PAINT IS BILLED IN ONE PLACE NOW, AND IT IS NOT HERE.

           This added up the ceiling's boards and the partitions' and billed the
           lot from the ceiling -- which left the inside face of every external
           wall unpainted and made the partitions' share depend on which layer
           ran first. The core works it out from the plasterboard in the bill,
           where build order cannot reach it. Billing it here as well would pay
           for it twice (Marek). */
        if (false && INT_PAINT.code) {
          _add(LAYER, INT_PAINT.code, _intPaintA);
          ctx.log('PNT  interior paint ' + API.fmtArea(_intPaintA) + ' of ' + INT_PAINT.code +
                  ' -- partitions both faces + walls with reveals + ceiling, less the tiling ' +
                  'bands in the wet room(s), billed once by the surface; ' +
                  (INT_PAINT.show ? 'drawn as the white of every board it covers'
                                                      : 'NOT drawn (INT_PAINT.show off), boards in their own colours'));
        } else {
          /* garageapt-v29, ranch-v54: the row exists (WIS00001) and the core bills
             it from the plasterboard in the bill -- the old NOT BILLED line was stale */
          ctx.log('PNT  interior paint ' + API.fmtArea(_intPaintA) + ' of board faces drawn -- billed by the core from the ' +
                  'plasterboard in the bill as ' + (INT_PAINT.code || '-') + ' (partitions both faces + walls with reveals + ceiling)');
        }
        _intPaintA = 0;

        ctx.log('ceiling board ' + API.fmtArea(done.dry) + ' plain' +
                (done.wet > 0 ? (' and ' + API.fmtArea(done.wet) + ' MOISTURE-RESISTANT over the ' +
                                 'wet room(s), drawn green') : ' -- no wet room, so no green board') +
                ', ' + Math.round(CEIL_BOARD.t * 1000) + ' mm, under the battens at ' + yB.toFixed(3));
        ctx.log('ceiling board ' + Math.round(total * 20) + ' screw(s), ' +
                (Math.round(total / 2.88 * 3.6 * 10) / 10) + ' m of tape and ' +
                API.fmtArea(total) + ' of filler -- all three from the area, not added later');
        _why(ctx, 'ceiling wet rooms come from the same WET_ROOMS the floor tiles from -- ' +
                'a second list here would be a second opinion about the same bathroom');
      }

      function _ceilUnder(ctx, yDeck, ux0, ux1, uz0, uz1, membersAlongX) {
        var along = !membersAlongX;
        var T = ctx.THREE, g = ctx.group;
        var w2 = ux1 - ux0, l2 = uz1 - uz0;
        if (!(w2 > 0.2) || !(l2 > 0.2)) return;

        var vm = new T.MeshStandardMaterial({
          color: isNew(LAYER + '.barrier') ? MAGENTA : 0x4A5560,
          roughness: 0.9, metalness: 0.0, transparent: true, opacity: 0.55, depthWrite: false });
        var bm = new T.MeshStandardMaterial({
          color: isNew(LAYER + '.batten') ? MAGENTA : WOOD, roughness: 0.9, metalness: 0.0 });

        var vbT = 0.001;
        var yVB = yDeck - vbT;
        /* SAUNA: no barrier, no battens in the sauna room -- its own lining */
        var hotR = (HOT && HOT.step() >= 4) ? HOT.rectsIn(ux0, ux1, uz0, uz1) : [], hotA = 0, qh;
        for (qh = 0; qh < hotR.length; qh++) hotA += (hotR[qh].x1 - hotR[qh].x0) * (hotR[qh].z1 - hotR[qh].z0);
        var dryShare = Math.max(0, 1 - hotA / (w2 * l2));
        /* NONE OVER THE GARAGE (ranch-v34): under a blown ceiling the garage is
           outside the envelope, so the retarder covers only the cells that are
           not over it; the battens and the board carry on over it */
        var COLDu = (_rsCeilBlown && GAR) ? GAR.coldRects(ux0, ux1, uz0, uz1) : [];
        var CU = COLDu.length ? GAR.cells({ x0: ux0, x1: ux1, z0: uz0, z1: uz1 }, COLDu) : { keep: [{ x0: ux0, x1: ux1, z0: uz0, z1: uz1 }], inn: [] };
        var keepA = 0, kq;
        for (kq = 0; kq < CU.keep.length; kq++) keepA += (CU.keep[kq].x1 - CU.keep[kq].x0) * (CU.keep[kq].z1 - CU.keep[kq].z0);
        var vbFrac = Math.min(1, keepA / (w2 * l2));
        if (vbFrac > 0.01) {
          _asmMark(ctx, 'layer_vapour_barrier', 'top', 'Vapour barrier', CEIL_VB.code);
          if (!hotR.length) {
            for (kq = 0; kq < CU.keep.length; kq++) {
              var KU = CU.keep[kq];
              var m = new T.Mesh(new T.BoxGeometry(KU.x1 - KU.x0, vbT, KU.z1 - KU.z0), vm);
              m.position.set((KU.x0 + KU.x1) / 2, yVB + vbT / 2, (KU.z0 + KU.z1) / 2);
              g.add(m);
            }
          } else {
            var cellsV = HOT.cellsBy(ux0, ux1, uz0, uz1, hotR), cv;
            for (cv = 0; cv < cellsV.length; cv++) {
              if (cellsV[cv].hot) continue;
              var mc = new T.Mesh(new T.BoxGeometry(cellsV[cv].x1 - cellsV[cv].x0, vbT, cellsV[cv].z1 - cellsV[cv].z0), vm);
              mc.position.set((cellsV[cv].x0 + cellsV[cv].x1) / 2, yVB + vbT / 2, (cellsV[cv].z0 + cellsV[cv].z1) / 2);
              g.add(mc);
            }
          }
        }

        /* BOUGHT BY THE ROLL. Width less the lap is what a run actually covers. */
        var runW = CEIL_VB.roll - CEIL_VB.lap;
        var across = along ? l2 : w2;
        var runs = Math.max(1, Math.ceil(across / runW));
        var vbArea = runs * CEIL_VB.roll * (along ? w2 : l2) * dryShare * (vbFrac > 0.01 ? vbFrac : 0);
        if (vbArea > 0) _add(LAYER, CEIL_VB.code, vbArea);
        /* one taped lap between every pair of runs, plus the perimeter */
        /* on the L, the seam between two rectangles is one taped lap, half on each (ranch-v5) */
        var hS = function (sd) { return _rIn(sd) ? 1 : 0.5; };
        var tapeM = ((runs - 1) * (along ? w2 : l2) + (H.seamTape ? (w2 * (hS('z0') + hS('z1')) + l2 * (hS('x0') + hS('x1'))) : 2 * (w2 + l2))) * dryShare * (vbFrac > 0.01 ? vbFrac : 0);
        if (tapeM > 0) _add(LAYER, CEIL_VB.tape, tapeM);
        if (COLDu.length) ctx.log('GAR  ceiling vapour retarder ' + (vbFrac > 0.01 ? ('on ' + Math.round(vbFrac * 100) + ' % of this ceiling') : 'none on this ceiling') +
                                  ' -- not over the garage (' + API.fmtArea(w2 * l2 - keepA) + '); battens and 1/2 in. gypsum carry on over it, IRC R302.6 (ranch-v34)');

        /* the battens, across the members */
        _asmMark(ctx, 'layer_ceiling_battens', 'top', 'Ceiling battens', CEIL_BATTEN.code);
        var yB = yVB - CEIL_BATTEN.h;
        var span2 = along ? l2 : w2;
        var nB = Math.max(2, Math.round(span2 / CEIL_BATTEN.spacing) + 1);
        var len2 = along ? w2 : l2, q4, run2 = 0;
        for (q4 = 0; q4 < nB; q4++) {
          var t4 = (along ? uz0 : ux0) + span2 * (q4 / (nB - 1));
          /* the pieces of this line outside the sauna room */
          var pcs4 = [[along ? ux0 : uz0, along ? ux1 : uz1]], qr;
          for (qr = 0; qr < hotR.length; qr++) {
            var HR = hotR[qr];
            var inLine = along ? (t4 >= HR.z0 - 1e-6 && t4 <= HR.z1 + 1e-6) : (t4 >= HR.x0 - 1e-6 && t4 <= HR.x1 + 1e-6);
            if (!inLine) continue;
            var cA = along ? HR.x0 : HR.z0, cB = along ? HR.x1 : HR.z1, nxt = [], qp;
            for (qp = 0; qp < pcs4.length; qp++) {
              var pA = pcs4[qp][0], pB = pcs4[qp][1];
              if (cB <= pA || cA >= pB) { nxt.push([pA, pB]); continue; }
              if (cA > pA + 0.05) nxt.push([pA, cA]);
              if (cB < pB - 0.05) nxt.push([cB, pB]);
            }
            pcs4 = nxt;
          }
          for (qr = 0; qr < pcs4.length; qr++) {
            var pl4 = pcs4[qr][1] - pcs4[qr][0], pm4 = (pcs4[qr][0] + pcs4[qr][1]) / 2;
            if (!(pl4 > 0.05)) continue;
            if (along) {
              box2(T, g, bm, pl4, CEIL_BATTEN.h, CEIL_BATTEN.w, pm4, yB + CEIL_BATTEN.h / 2, t4);
            } else {
              box2(T, g, bm, CEIL_BATTEN.w, CEIL_BATTEN.h, pl4, t4, yB + CEIL_BATTEN.h / 2, pm4);
            }
            run2 += pl4;
          }
        }
        if (hotR.length) { try { HOT.ceil(ctx, hotR, yDeck, along); } catch (eHC) { ctx.log('hot        !! ceiling threw: ' + (eHC && eHC.message)); } }
        _add(LAYER, CEIL_BATTEN.code, run2);
        var scr = Math.round(run2 / 0.5) + nB * 2;
        _add(LAYER, CEIL_BATTEN.screw, scr);

        if (vbArea > 0) ctx.log('ceiling barrier UNDER the frame at ' + yVB.toFixed(3) + ' -- warm side. ' +
                runs + ' run(s) of ' + Math.round(CEIL_VB.roll * 1000) + ' mm lapped ' +
                Math.round(CEIL_VB.lap * 1000) + ' = ' + API.fmtArea(vbArea) + ' bought for ' +
                API.fmtArea(w2 * l2) + ' of ceiling');
        if (tapeM > 0) ctx.log('ceiling barrier tape ' + Math.round(tapeM * 10) / 10 + ' m -- every lap and the ' +
                'whole perimeter, counted from the runs rather than guessed at as a percentage');
        ctx.log('ceiling battens run along ' + (along ? 'X' : 'Z') + ', the members along ' +
                (membersAlongX ? 'X' : 'Z') + ' -- crossed, which is the whole point of them');
        ctx.log('ceiling battens ' + nB + ' at ' + Math.round(span2 / (nB - 1) * 1000) + ' mm oc, ' +
                Math.round(run2 * 10) / 10 + ' m of ' + Math.round(CEIL_BATTEN.w * 1000) + 'x' +
                Math.round(CEIL_BATTEN.h * 1000) + ', ' + scr + ' screw(s) -- across the members, ' +
                'and the ' + Math.round(CEIL_BATTEN.h * 1000) + ' mm they leave is the service void, ' +
                'so a cable never pierces the barrier');

        _ceilBoard(ctx, yB, ux0, ux1, uz0, uz1);
      }

      function _ceilFill(ctx, yBase, fx0, fx1, fz0, fz1, timberSectionLen, lines, memT, memD, along) {
        _asmMark(ctx, 'layer_ceiling_insulation', 'top', 'Blown insulation', BLOWN.code);
        var T = ctx.THREE, g = ctx.group;
        var dep = BLOWN.depth;
        var w2 = fx1 - fx0, l2 = fz1 - fz0;
        if (!(w2 > 0.2) || !(l2 > 0.2)) return;

        var area = w2 * l2, vol = area * dep;
        var bm = new T.MeshStandardMaterial({
          color: isNew(LAYER + '.blown') ? MAGENTA : 0xE8D9A0,
          roughness: 1.0, metalness: 0.0 });

        /* CUT BY THE ROOF, WHICH IS WHAT REALLY LIMITS IT.

           The fill was a flat 300 mm everywhere, and near the eaves the roof plane
           is nearly down on the ceiling -- so it came out THROUGH the rafters and
           above the covering. Nobody blows 300 mm into a space 80 mm high; the
           insulator fills what is there and the depth tapers away to the eave.

           Every slab is therefore clipped to the plane above it, sampled at its own
           corners and taken at the lowest, so nothing pokes through anywhere along
           it. A slab with no room left is not drawn at all rather than drawn thin.

           The BILL follows the drawing: what is clipped away is not blown and is
           not paid for. That is the whole reason to clip it rather than to leave a
           tidy number and an untidy model. */
        var clipped = 0, cutVol = 0;

        /* NOT OVER THE GARAGE (ranch-v34): it is outside the envelope, so the
           cells of a slab that lie over it are left out of the drawing and of
           the bill; the dam on the line follows below */
        _rsCeilBlown = true;
        var COLD = GAR ? GAR.coldRects(fx0, fx1, fz0, fz1) : [], coldA = 0, cq;
        for (cq = 0; cq < COLD.length; cq++) coldA += (COLD[cq].x1 - COLD[cq].x0) * (COLD[cq].z1 - COLD[cq].z0);
        function slab(cw, ch, cd, cx3, cy3, cz3) {
          if (!COLD.length) return slab0(cw, ch, cd, cx3, cy3, cz3);
          var CC = GAR.cells({ x0: cx3 - cw / 2, x1: cx3 + cw / 2, z0: cz3 - cd / 2, z1: cz3 + cd / 2 }, COLD), k6;
          for (k6 = 0; k6 < CC.keep.length; k6++) {
            var K6 = CC.keep[k6];
            slab0(K6.x1 - K6.x0, ch, K6.z1 - K6.z0, (K6.x0 + K6.x1) / 2, cy3, (K6.z0 + K6.z1) / 2);
          }
        }
        function slab0(cw, ch, cd, cx3, cy3, cz3) {
          var x0c = cx3 - cw / 2, x1c = cx3 + cw / 2;
          var z0c = cz3 - cd / 2, z1c = cz3 + cd / 2;
          var yb = cy3 - ch / 2;
          var room = null, q4;
          var pts4 = [[x0c, z0c], [x1c, z0c], [x1c, z1c], [x0c, z1c],
                      [cx3, cz3]];
          /* THE CEILING STOPS UNDER THE DECK, AND OFF THE PLANE'S EDGE TOO.

             Two things were letting the fill through. A corner that falls outside
             the roof planes returns null and was simply skipped, so where the roof
             runs past the wall -- an overhang -- no corner answered and the slab
             grew to its full height, straight through the boards above it.

             And the ceiling is not capped by the roof PLANE. The deck sits a lift
             above that, so its underside, not the plane, is what the insulation has
             to stop below.

             A corner that finds no plane now falls back to the nearest one that did
             -- there is always a roof above a ceiling slab, even where this test
             cannot see it (Marek). */
          var deckUnder = _skinLift() * Math.cos((STATE.roof.pitch || 0) * Math.PI / 180);
          var got = false;
          for (q4 = 0; q4 < pts4.length; q4++) {
            var py = _roofPlaneYAt(pts4[q4][0], pts4[q4][1]);
            if (py == null) continue;
            got = true;
            var r4 = (py + deckUnder) - 0.010 - yb;
            if (room === null || r4 < room) room = r4;
          }
          if (!got) {
            /* off every plane: use the centre's own height rather than no cap */
            var pc = _roofPlaneYAt(cx3, cz3);
            if (pc != null) room = (pc + deckUnder) - 0.010 - yb;
          }
          var use = (room === null) ? ch : Math.min(ch, room);
          if (use < 0.02) { clipped++; cutVol += cw * cd * ch; return; }
          if (use < ch - 0.001) { clipped++; cutVol += cw * cd * (ch - use); }
          var m = new T.Mesh(new T.BoxGeometry(cw, use, cd), bm);
          m.position.set(cx3, yb + use / 2, cz3);
          g.add(m);
        }

        /* IN THE BAYS UP TO THE TIMBER, THEN ONE SHEET OVER THE TOP OF IT.

           One solid the full depth was honest about the volume and wrong about the
           picture: it stood in the same space as every joist. Loose fill really
           does do both things -- it fills the bays and then keeps going over the
           members once it is deeper than they are -- so the drawing does the same,
           and neither part is inside the timber.

           Below the member tops it is drawn bay by bay from the member lines the
           caller passes; above them, whatever depth is left is one continuous
           sheet, because up there nothing is in the way. */
          var mt = memT || 0, md = memD || 0;
          var inBay = Math.min(dep, md);
          var over = dep - inBay;
          var nBay = 0;

        if (lines && lines.length && inBay > 0.001) {
          var L2 = lines.slice().sort(function (a, b) { return a - b; });
          var edges = [], q3;
          var lo3 = along ? fx0 : fz0, hi3 = along ? fx1 : fz1;
          edges.push(lo3);
          for (q3 = 0; q3 < L2.length; q3++) { edges.push(L2[q3] - mt / 2); edges.push(L2[q3] + mt / 2); }
          edges.push(hi3);
          for (q3 = 0; q3 + 1 < edges.length; q3 += 2) {
            var a3 = edges[q3], b3 = edges[q3 + 1];
            if (b3 - a3 < 0.02) continue;
            if (along) slab(b3 - a3, inBay, l2, (a3 + b3) / 2, yBase + inBay / 2, (fz0 + fz1) / 2);
            else slab(w2, inBay, b3 - a3, (fx0 + fx1) / 2, yBase + inBay / 2, (a3 + b3) / 2);
            nBay++;
          }
        } else if (inBay > 0.001) {
          slab(w2, inBay, l2, (fx0 + fx1) / 2, yBase + inBay / 2, (fz0 + fz1) / 2);
          nBay = 1;
        }

        if (over > 0.001) {
          slab(w2, over, l2, (fx0 + fx1) / 2, yBase + inBay + over / 2, (fz0 + fz1) / 2);
        }

        /* what the frame displaces, from its own measured length */
        var tim = (timberSectionLen || 0) * inBay;
        /* the garage's share comes off pro rata, timber and all (ranch-v34) */
        var coldVol = Math.max(0, vol - tim) * Math.min(1, coldA / area);
        var net = Math.max(0, vol - tim - coldVol - cutVol);
        if (net > 0) _add(LAYER, BLOWN.code, net);

        ctx.log('ceiling blown fill ' + Math.round(dep * 1000) + ' mm over ' +
                API.fmtArea(area) + ' = ' + (Math.round(vol * 100) / 100) + ' m3, less ' +
                (Math.round(tim * 100) / 100) + ' m3 of timber' +
                (coldVol > 0 ? (', less ' + (Math.round(coldVol * 100) / 100) + ' m3 over the garage') : '') + ' = ' +
                (Math.round(net * 100) / 100) + ' m3 blown, base at ' + yBase.toFixed(3));
        if (GAR && (COLD.length || GAR.hasGarage())) GAR.dam(ctx, COLD, yBase, dep, fx0, fx1, fz0, fz1, coldA);
        ctx.log('ceiling drawn ' + nBay + ' bay(s) ' + Math.round(inBay * 1000) + ' mm deep' +
                (over > 0.001 ? (' plus ' + Math.round(over * 1000) + ' mm over the tops of the ' +
                                 'members, where nothing is in the way') : '') +
                ' -- so it fills round the frame instead of standing in it');
        if (clipped) {
          ctx.log('ceiling ' + clipped + ' slab(s) cut by the roof above them, ' +
                  (Math.round(cutVol * 100) / 100) + ' m3 taken off -- near the eave the plane ' +
                  'is almost down on the ceiling and a flat ' + Math.round(dep * 1000) +
                  ' mm came out through the rafters. Clipped in the model AND in the bill');
        }
        ctx.log('ceiling R about ' + (Math.round(dep / BLOWN.lam * 10) / 10) +
                ' m2K/W where the full depth fits -- less toward the eave, which is real and ' +
                'is why the detail there is a baffle rather than more fill');
      }

        var T = ctx.THREE, g = ctx.group;
        /* SAUNA: the hot room's review step is on while its viz is */
        if (HOT) HOT.review(HOT.step() > 0 && ctx.viz && ctx.viz.key === 'hot');
        _billReset(LAYER);
        if (HOT) HOT.pdfReset();
        H.setTally(null);
        _rsCeilBlown = false;
        if (API.STATE.roof.type !== 'mono') {
          /* SAY WHERE THE CEILING CAME FROM. Silence here read as a missing layer;
             it is the opposite -- these roofs bring their own. */
          ctx.log('ceiling ' + (_isTrussed()
            ? 'is the truss bottom chord -- level, full width, already there'
            : 'is the underside of the roof itself, which on a flat roof is level'));

          if (_isTrussed()) {
            /* THE DECK IS THERE, SO THE FILL GOES ON IT -- AND THAT IS THE BEARING
               LINE, NOT A CHORD BELOW IT.

               I subtracted CHORD.d, which put the base a whole bottom chord low: the
               fill started under the timber and only its top 80 mm reached into the
               bays. The roof layer says where the chord is, in its own log line --
               "bottom chord bears at 3.210, which is the top of the wall plate --
               ON it, not in it" -- so the chord's UNDERSIDE is wallTop(), the board
               hangs off that, and the fill stands on the board.

               Twice in three versions I have subtracted a member depth from a line
               that was already the underside. The line the roof hands over is the
               soffit; nothing comes off it. */
            var P2 = API.poly(), gx0 = 1e9, gx1 = -1e9, gz0 = 1e9, gz1 = -1e9, q2;
            for (q2 = 0; q2 < P2.length; q2++) {
              gx0 = Math.min(gx0, P2[q2][0]); gx1 = Math.max(gx1, P2[q2][0]);
              gz0 = Math.min(gz0, P2[q2][1]); gz1 = Math.max(gz1, P2[q2][1]);
            }
            var wt3 = API.wallThickness();
            /* THE BOTTOM CHORDS, COMPUTED RATHER THAN GUESSED AT.

               _trussTally.chord is top AND bottom together and belongs to the roof
               layer, which builds AFTER this one -- so reading it here is last
               pass's number, halved by a rule of thumb. The bottom chord is one per
               truss across the span, and the truss spacing is the module: that is
               a number this layer can work out for itself, this pass. */
            var alongL2 = (API.STATE.roof.axis === 'l');
            var spanW = alongL2 ? (gx1 - gx0) : (gz1 - gz0);
            var runL2 = alongL2 ? (gz1 - gz0) : (gx1 - gx0);
            var nTr2 = Math.max(2, Math.round(runL2 / API.grid()) + 1);
            /* TO THE OUTER FACE OF THE WALLS. It is poured, so it runs out over the
               wall head and covers the plate -- stopping it at the inner face
               leaves the top of every wall bare, which is the one line the whole
               envelope leaks through. */
            /* the bottom chords, at the module along the run */
            /* THE FILL IS DIVIDED BY THE TRUSSES THAT ARE THERE. */
            var cl2 = [], cq2, c0 = alongL2 ? gz0 : gx0, c1 = alongL2 ? gz1 : gx1;
            var fromFrame = false;
            var ST2 = _trussStations(), mq2;
            for (mq2 = 0; mq2 < ST2.length; mq2++) {
              if (ST2[mq2] >= Math.min(c0, c1) - 0.01 && ST2[mq2] <= Math.max(c0, c1) + 0.01) {
                cl2.push(ST2[mq2]);
              }
            }
            fromFrame = cl2.length > 1;
            if (!fromFrame) {
              cl2 = [];
              for (cq2 = 0; cq2 < nTr2; cq2++) cl2.push(c0 + (c1 - c0) * (cq2 / (nTr2 - 1)));
            }
            ctx.log('ceiling fill split on ' + cl2.length + ' line(s) ' +
                    (fromFrame ? 'FROM THE FRAME' : 'spread evenly -- the frame published none'));
            /* A MASONRY GABLE IS SOLID TO THE ROOF (ranch-v20): the fill stops at
               its inner face; over the eaves it still runs out over the plate */
            var fx0 = gx0, fx1 = gx1, fz0 = gz0, fz1 = gz1;
            if (BLK && BLK.masonryGable()) {
              var GEc = BLK.gableEdges(), hc = BLK.wallT() / 2, qc;
              for (qc = 0; qc < P2.length; qc++) {
                if (!GEc[qc]) continue;
                var ca = P2[qc], cb = P2[(qc + 1) % P2.length];
                /* not on a seam of the L, where no wall stands (ranch-v37: the
                   fill stopped 220 short of it in the second rectangle) */
                if (Math.abs(ca[0] - cb[0]) < 0.01) { if (Math.abs(ca[0] - gx0) < 0.01 && _rIn('x0')) fx0 = gx0 + hc; else if (Math.abs(ca[0] - gx1) < 0.01 && _rIn('x1')) fx1 = gx1 - hc; }
                else if (Math.abs(ca[1] - cb[1]) < 0.01) { if (Math.abs(ca[1] - gz0) < 0.01 && _rIn('z0')) fz0 = gz0 + hc; else if (Math.abs(ca[1] - gz1) < 0.01 && _rIn('z1')) fz1 = gz1 - hc; }
              }
            }
            _ceilFill(ctx, API.wallTop(), fx0, fx1, fz0, fz1,
                      nTr2 * spanW * CHORD.t, cl2, CHORD.t, CHORD.d, !alongL2);
            /* the bottom chords span the way the trusses do */
            _ceilUnder(ctx, API.wallTop(), gx0 + wt3 * _rIn('x0'), gx1 - wt3 * _rIn('x1'), gz0 + wt3 * _rIn('z0'), gz1 - wt3 * _rIn('z1'), alongL2);   /* ranch-v5, anyhouse-lib-v11: the L's seam */
          } else {
            /* A PARAPET ROOF IS A WARM ROOF AND IS NOT BLOWN.

               There is no attic and no deck to pour onto: the insulation goes ABOVE
               the structural deck, under the covering, as boards -- which is also
               what gives the fall to the outlet. Loose fill up there would have
               nothing to sit on, nothing to vent to, and nothing to stop it drifting
               to the low corner.

               So this layer builds nothing on a flat roof and says so. The boards
               belong to the roof build-up, with the covering. */
            _why(ctx, 'ceiling flat roof -- NOT blown. A parapet roof is a warm roof: ' +
                    'rigid board ABOVE the deck, under the covering, which is where the ' +
                    'fall to the outlet comes from as well. That is the roof build-up, ' +
                    'not this layer');

            /* BUT IT STILL HAS A CEILING. */
            var fp = API.poly(), fq;
            var fx0 = 1e9, fx1 = -1e9, fz0 = 1e9, fz1 = -1e9;
            for (fq = 0; fq < fp.length; fq++) {
              if (fp[fq][0] < fx0) fx0 = fp[fq][0];
              if (fp[fq][0] > fx1) fx1 = fp[fq][0];
              if (fp[fq][1] < fz0) fz0 = fp[fq][1];
              if (fp[fq][1] > fz1) fz1 = fp[fq][1];
            }
            var wtF = API.wallThickness();
            _ceilUnder(ctx, API.wallTop(), fx0 + wtF * _rIn('x0'), fx1 - wtF * _rIn('x1'), fz0 + wtF * _rIn('z0'), fz1 - wtF * _rIn('z1'), true);   /* anyhouse-lib-v11: the L's seam */
          }
          return;
        }
        /* A MONO-PITCH ALWAYS GETS A LEVEL CEILING. */

        var P = API.poly(), i;
        var x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9;
        for (i = 0; i < P.length; i++) {
          x0 = Math.min(x0, P[i][0]); x1 = Math.max(x1, P[i][0]);
          z0 = Math.min(z0, P[i][1]); z1 = Math.max(z1, P[i][1]);
        }
        var wt = API.wallThickness();

        /* LEVEL AT THE LOW SIDE. Taking the high wall would put the ceiling above
           the head of the low one and leave a wedge of daylight over it. */
        var lowY = _ceilingSoffit() + CEIL_JOIST.d;
        var y = lowY - CEIL_JOIST.d / 2;

        /* THE JOISTS WERE HALF INSIDE THE WALLS, AT ALL FOUR EDGES.

           The outer joist sat at z0 + wt/2 -- the middle of the panel -- so half of
           its thickness was buried in the wall it is supposed to bear against. The
           span had the same fault along the other axis: (x1 - x0) - wt centred on
           the building runs from x0 + wt/2 to x1 - wt/2, which is again the middle
           of the panel at each end.

           A joist lands ON the inner face, not in it. The outer ones start where the
           wall stops, and the span is inner face to inner face, so the frame sits
           inside the room the walls make instead of overlapping it.

           MAGENTA THIS VERSION -- the joists moved, and the only way to see that
           they moved by the right amount is to see them against the panels. */
        /* THE CEILING IS FRAMED THE WAY THE ROOF OVER A PARAPET IS, and v238 was
           wrong about it. Not "on the inner face" -- that is where the LEDGER goes.

           Three things, and all three are copied from the product's roof in the same lib
           rather than worked out again:

             a plank fixed flat to the inside of the studs, its TOP the bearing
             line, its depth hanging below -- a bearer, not a hanger, so the load
             goes straight into timber and not through fasteners;

             a joist ON that top face, running the whole way to the OUTER face of
             the studs, so the boards have an edge to land on at both ends;

             one joist per stud, face to face with it, so it is fixed sideways into
             the vertical as well as down onto the bearer.

           v238 stopped them at the inner face and gave them nothing to bear on --
           a frame floating in the room. The end ones were right by accident,
           because at the ends there is a wall behind them either way. */
        var mat = new T.MeshStandardMaterial({ color: isNew(LAYER) ? MAGENTA : WOOD,
                                               roughness: 0.85, metalness: 0.0 });

        /* I TOOK "TO THE OUTER FACE" LITERALLY AND THE STUDS ARE IN THE WAY.

           On the roof that is right: the rafter is up at the bearing line, above
           the stud it lands on. Here the joist is BESIDE the studs, at their own
           height, so running it to the outer face drives it through the full 160 mm
           of every one it meets. It ends on the INNER face, resting on the ledger
           that is fixed there -- which is the same thing "face to face" means at
           the other end of the joist.

           And it goes BESIDE a stud, not through it. v244 put a joist on each stud
           centre line, so every one of them overlapped its stud by half a joist,
           and reading the wall's stud list instead of the module turned sixteen
           joists into twenty-four. That is the "thicker" -- not a bigger section, a
           denser frame with a clash at every line.

           Half a stud plus half a joist off the stud centre puts the two faces
           together with nothing shared, which is what the roof does with its
           rafters and is copied from there. */
        /* BOTH THINGS HE SAID ARE TRUE AND THEY ARE NOT IN CONFLICT.

           The joist runs the whole way to the OUTER face of the studs, like the
           rafter does. v244 did that and it drove through the studs -- because v244
           put a joist on each stud's CENTRE LINE. A joist that sits in the BAY
           between two studs crosses the wall through the gap and can go all the way
           out; a joist on a stud line cannot go anywhere.

           So the lines are the bay centres of the wall the joists cross, not the
           stud lines and not the module. The pitch that comes out is not the stud
           pitch and is not even -- near a corner the wall has extra studs and the
           bays there are narrower. That is the wall's own layout and it is the
           right answer.

           The outermost two are the exception: their FACE goes on the inner face of
           the end walls, because there is a wall behind them rather than a bay.

           AND NOTHING IS MERGED ANY MORE. The old dedupe dropped a line within one
           joist thickness of the last one -- which quietly turned two members 80 mm
           apart into one, and a pair 70 mm apart into what looks like a single
           member of twice the width. That is the "different widths": not a section,
           two joists all but touching. A bay centre that lands within a clear joist
           of a forced outer one is dropped outright instead. */
        var span = x1 - x0;                       /* outer face of the studs, both ends */
        var yBear = y - CEIL_JOIST.d / 2;         /* the bearing line: the joist underside */
        var g2 = API.grid();
        var tally = { joists: 0, len: 0, span: span, ledger: 0 };

        var jt = CEIL_JOIST.t / 2;
        var zA = z0 + wt + jt, zB = z1 - wt - jt;   /* the outer two: face on the inner face */

        /* THE STUD LINES OF THE WALLS THESE JOISTS CROSS, ASKED FOR BY WALL.

           This used to sift _bearStuds by coordinate -- keep the ones whose x is
           near x0 or x1 -- which only works if those happen to be the walls the
           roof bears on. When they are not, the sift keeps almost nothing, the code
           falls through to stepping the module, and a module pitch that is not the
           built pitch DRIFTS: the first joist lands right and every one after it is
           further off until it is inside a stud. First one fine, worse and worse
           going along, which is exactly what he saw.

           The wall now writes down every stud with its segment id, so this asks for
           a wall by name instead of guessing from a number. */
        /* THE SAME STATIONS THE RAFTER IS SET OUT FROM. Tiny's rule, in its own
           words: the rafters were always right and the ties always wrong for one
           reason -- the rafter is set out from the roof and the tie had a world of
           its own. Change anything and the two worlds disagree in a new way.

           _bearStuds is that world: it is the list the roof reads to put a rafter
           face to face with a stud. The ceiling reads the SAME list, so a joist can
           only land where a rafter lands, and the pitch is whatever the wall
           actually built -- uneven at a corner, which is correct.

           _wallStuds is the fallback, and only for a roof that bears on nothing. */
        /* ONE WALL'S STUDS, NOT TWO WALLS' PILED TOGETHER.

           The log gave it away and I read past it: 24 stations while the roof was
           building 15 rafters. _bearStuds holds the studs of BOTH walls the roof
           bears on, and those two walls do not lay out identically -- their panel
           joints fall in different places -- so their rows differ by a few
           millimetres and a dedupe to the millimetre keeps both. Twenty-four lines
           across 7.2 m is 300 mm: half the stud pitch, interleaved, and a joist on
           every one of them lands inside a stud every other time.

           So: group by the wall each stud came from, take the wall with the most,
           and use that one row. */
        var src = 'rafter stations', sz = [];
        var ws = API.__wallStuds ? API.__wallStuds() : [];
        var SEGS = API.segments ? API.segments() : [];
        var byWall = {}, wid;
        for (i = 0; i < SEGS.length; i++) {
          /* a joist runs along x, so it crosses the walls whose normal is along x */
          if (Math.abs(SEGS[i].nx) > 0.7) byWall[SEGS[i].id] = [];
        }
        for (i = 0; i < ws.length; i++) {
          if (!byWall[ws[i].seg]) continue;
          byWall[ws[i].seg].push(ws[i].z);
        }
        var bestId = null;
        for (wid in byWall) {
          if (!byWall.hasOwnProperty(wid)) continue;
          if (!bestId || byWall[wid].length > byWall[bestId].length) bestId = wid;
        }
        if (bestId && byWall[bestId].length > 1) {
          src = bestId;
          sz = byWall[bestId].slice();
          sz.sort(function (a2, b2) { return a2 - b2; });
          /* two studs closer together than a joist is wide cannot both carry one */
          var szk = [];
          for (i = 0; i < sz.length; i++) {
            if (i && sz[i] - szk[szk.length - 1] < STUD.t + CEIL_JOIST.t) continue;
            szk.push(sz[i]);
          }
          sz = szk;
        } else {
          src = 'nothing usable';
          sz = [];
        }

        /* ONE JOIST PER STUD, ITS FACE ON THAT STUD'S FACE.

           v252 put them in the middle of each bay -- which does clear the studs,
           and is why they stopped colliding, but it also means nothing is fixed to
           anything: a joist floating in a gap can only be nailed down onto the
           ledger, never sideways into the vertical. Beside the stud, faces
           touching, is what makes the two one thing.

           The stud decides where the joist goes, so the pitch is the STUD pitch and
           is not even: near a corner the wall has extra studs and the joists follow
           them there. That is not a fault to smooth out. */
        /* ONE SIDE, AND KEEP IT.

           This chose the side per joist -- "whichever keeps it inside the building"
           -- so everything left of the middle went one way and everything right of
           it went the other. At the changeover two neighbours jump apart by two
           offsets at once, and from there the whole row is out of step with the
           studs by a joist width in the opposite direction. The pitch in the log
           said it plainly: 600 600 600 600 540 540 600 600, and the 540s are the
           flip.

           A row that follows the studs has to follow them the same way the whole
           length. One side, chosen once. Anything that falls off the end is dropped
           -- the outer two are forced onto the inner faces regardless. */
        var side = (STUD.t + CEIL_JOIST.t) / 2;   /* centre to centre, faces touching */
        var zs = [], q;
        for (q = 0; q < sz.length; q++) zs.push(sz[q] + side);
        var fromStuds = zs.length > 0;
        if (!fromStuds) {
          /* nothing to read -- step the module and say so rather than pretend */
          var n = Math.max(1, Math.round((zB - zA) / g2));
          zs = [];
          for (i = 1; i < n; i++) zs.push(zA + (zB - zA) * i / n);
        }

        /* the outer two go in first; a bay centre too close to one of them is a
           second member in the same place and is dropped, not merged */
        var clearOf = CEIL_JOIST.t * 2;
        var zk = [zA, zB];
        _asmMark(ctx, 'layer_ceiling_joists', 'top', 'Ceiling joists', CEIL_JOIST.code);
        for (q = 0; q < zs.length; q++) {
          if (zs[q] < zA + clearOf || zs[q] > zB - clearOf) continue;
          zk.push(zs[q]);
        }
        zk.sort(function (a2, b2) { return a2 - b2; });
        zs = zk;

        for (i = 0; i < zs.length; i++) {
          var m = new T.Mesh(new T.BoxGeometry(span, CEIL_JOIST.d, CEIL_JOIST.t), mat);
          m.position.set((x0 + x1) / 2, yBear + CEIL_JOIST.d / 2, zs[i]);
          g.add(m);
          tally.joists++;
          tally.len += span;
        }

        /* THE LEDGER, on the two walls the joists cross, inside face of the studs */
        var lz = (z0 + z1) / 2, lspan = z1 - z0;
        for (i = 0; i < 2; i++) {
          var lx = (i ? x1 - wt - LEDGER.t / 2 : x0 + wt + LEDGER.t / 2);
          var led = new T.Mesh(new T.BoxGeometry(LEDGER.t, CEIL_JOIST.d, lspan), mat);
          led.position.set(lx, yBear - CEIL_JOIST.d / 2, lz);
          g.add(led);
          tally.ledger += lspan;
        }
        _add(LAYER, LEDGER.code, tally.ledger);

        /* the same: out to the outline, not to the inner face */
        _ceilFill(ctx, yBear, x0, x1, z0, z1,
                  tally.len * CEIL_JOIST.t + tally.ledger * LEDGER.t,
                  zs, CEIL_JOIST.t, CEIL_JOIST.d, false);
        /* the ceiling joists run along X */
        _ceilUnder(ctx, yBear, x0 + wt, x1 - wt, z0 + wt, z1 - wt, true);

        H.setTally(tally);
        _add(LAYER, CEIL_JOIST.code, tally.len);
        if (tally.joists) _add(LAYER, 'FAT00011', tally.joists * 2);   /* a hanger at each end */

        ctx.log('ceiling ledger ' + Math.round(LEDGER.t * 1000) + ' mm on the inside face of the ' +
                'studs, top at ' + API.fmtLen(yBear) + ' -- the joists sit ON it, ' +
                API.fmtLen(tally.ledger) + ' of it');
        /* THE NUMBERS, NOT A SUMMARY. "one beside each of 24 rafter stations" read
           as success and was the bug -- 24 where the roof builds 15. A count cannot
           be checked against the model; a list can. */
        ctx.log('ceiling source ' + src + ' -- ' + sz.length + ' stud line(s), ' +
                zs.length + ' joist(s), roof built ' + (_roofMembers || '?') + ' rafter(s) last pass' +
                (fromStuds ? '' : '   <-- NOTHING USABLE, stepped on the module, these WILL drift'));
        ctx.log('ceiling studs  ' + (sz.length ? sz.map(function (v) {
                  return Math.round(v * 1000);
                }).join(' ') : '(none)'));
        ctx.log('ceiling joists ' + zs.map(function (v) { return Math.round(v * 1000); }).join(' '));
        (function () {
          var gp = [], q2;
          for (q2 = 1; q2 < zs.length; q2++) gp.push(Math.round((zs[q2] - zs[q2 - 1]) * 1000));
          /* and whether the row is in step with itself: two neighbours that differ
             by more than a joist width mean something flipped */
          var evenNote = ' -- the stud pitch, uneven at a corner';
          var mn = 1e9, mx = -1e9, q5;
          for (q5 = 1; q5 + 1 < gp.length; q5++) { mn = Math.min(mn, gp[q5]); mx = Math.max(mx, gp[q5]); }
          if (gp.length > 3 && (mx - mn) > Math.round(CEIL_JOIST.t * 1000)) {
            evenNote = '   <-- OUT OF STEP by ' + (mx - mn) + ' mm somewhere in the middle';
          }
          ctx.log('ceiling pitch  ' + (gp.length ? gp.join(' ') : '(one joist)') + evenNote);
          /* and whether any of them is actually inside a stud, which is the thing
             he can see and this could not say */
          var bad = 0, q3, q4, need = (STUD.t + CEIL_JOIST.t) / 2 - 0.001;
          for (q3 = 0; q3 < zs.length; q3++) {
            for (q4 = 0; q4 < sz.length; q4++) {
              if (Math.abs(zs[q3] - sz[q4]) < need) { bad++; break; }
            }
          }
          ctx.log('ceiling clash  ' + bad + ' joist(s) inside a stud' +
                  (bad ? '   <-- STILL WRONG' : '   ok'));
        })();
        ctx.log('ceiling joists run ' + API.fmtLen(x0) + ' to ' + API.fmtLen(x1) +
                ' -- the OUTER face of the studs at both ends; the outer two on the ' +
                'inner face at ' + API.fmtLen(zA - jt) + ' and ' + API.fmtLen(zB + jt) +
                (isNew(LAYER) ? '   <-- MAGENTA this version' : ''));
        ctx.log('ceiling ' + Math.round(CEIL_JOIST.t * 1000) + 'x' + Math.round(CEIL_JOIST.d * 1000) +
                ' -- as wide as the ' + Math.round(STUD.t * 1000) + ' mm stud and deeper than the ' +
                Math.round(STUD.d * 1000) + ' mm wall, because it spans ' + API.fmtLen(span) +
                ' unsupported and the stud spans nothing');
        ctx.log('ceiling flat, ' + tally.joists + ' joist(s), span ' + API.fmtLen(span) +
                ', level at the LOW wall head ' +
                API.fmtLen(lowY) + ' -- above the high wall it would leave a wedge ' +
                'of daylight over the low one');
        ctx.log('ceiling ' + Math.round(CEIL_JOIST.t * 1000) + 'x' + Math.round(CEIL_JOIST.d * 1000) +
                ', ' + tally.len.toFixed(1) + ' m');
    }

    /* =========================================================================
       THE PARTITIONS MODULE (core-v782, the sixth shared module of the house
       line, its third part -- status/modules.md 6c). The inside walls written
       once: each wall cut to the faces it meets (T and L joints, a stud to
       receive the other wall), sole and single top plate, studs on the
       module and never in a doorway, the acoustic wool in the bays and over
       the doors, the board face by face (green and tiled where the face
       looks into a wet room, the joint moved onto the wet face), the
       thresholds where the floor changes, every doorway framed, lined and
       given its door, the slanted walls of an imported plan, and the bill
       with its screws, tape, filler, nails and door hardware. The ADU's
       text with the products' own parts behind hooks.

         API.houseParts(ctx, {
           api, layer ('adu-partitions'), wood, MAGENTA, PART, PART_BOARD,
           PART_WOOL, DOOR_W_DEF, DOOR_H_DEF, DOOR_STRIP, FINISH, WALL_TILE,
           WET_TILE_H, FRAME_NAIL, FILLER_KG_M2, billReset, isNew, boardCol,
           tileMat, roomRects, wetAt, showerOnFace, inHole, doorSet3D,
           wallTileBill, intDoorUnit, soffit, diagWalls,
           add, setTally, framed, doorListNew, paintReset, paintAdd, begin,
           said -- the product's own lists and tallies
           ga:     { groundCeil, roomSt } -- GARAGE APT / ANY HOUSE
           loft:   { geom, LF, partSt, roomSt, tag, planSaid, mm, partHead,
                     living, inShop, inLiving } -- BARNDO
           sep:    { kind, on, at, probe, wool, woolT, fireSay } -- RANCH / GA
           batch, timberMarks  RANCH, hot { step, at, faceY, face, partBill, doorCode,
           handleCode } SAUNA, osb { waste } the shed family, tg { on,
           vertical, TG, col } and full { at, tag } TAURUS / GARDEN BAR,
           casing { tally, CASE, tag } and closets BARNDO / GA
         })

       Carried: no stud where a room line meets a wall (sauna-v102) to the
       ADU family, RANCH, BARNDO, GA and ANY HOUSE; the tile on a partition
       under its own mark (the ADU's) to SAUNA. Not carried (candidates): the
       studs that receive a wall and the trimmers under the timber's mark
       (ranch-v59, timberMarks) -- the core's impregnation reads the marks,
       so SAUNA's lining would be impregnated; the sliding doors' track kits
       and the closet doors' hardware for SAUNA and the shed family -- their
       catalogue has no such rows.
       ========================================================================= */
    function houseParts(ctx, H) {
      /* the product's own pieces, under the names the text has always used */
      var API = H.api, LAYER = H.layer, MAGENTA = H.MAGENTA, PART = H.PART, PART_BOARD = H.PART_BOARD,
          PART_WOOL = H.PART_WOOL, DOOR_W_DEF = H.DOOR_W_DEF, DOOR_H_DEF = H.DOOR_H_DEF,
          DOOR_STRIP = H.DOOR_STRIP, FINISH = H.FINISH, WALL_TILE = H.WALL_TILE, WET_TILE_H = H.WET_TILE_H,
          FRAME_NAIL = H.FRAME_NAIL, FILLER_KG_M2 = H.FILLER_KG_M2;
      var _billReset = H.billReset, _add = function (cat, code, q) { H.add(code, q); }, isNew = H.isNew,
          _boardCol = H.boardCol, _tileMat = H.tileMat, _roomRects = H.roomRects, _inHole = H.inHole,
          _doorSet3D = H.doorSet3D, _wallTileBill = H.wallTileBill,
          _wetAt = H.wetAt || function () { return false; },
          _showerOnFace = H.showerOnFace || function () { return false; };
      /* SAUNA / the shed family have no unit table: the leaf by its width and
         hand, as their own copies billed it */
      var _intDoorUnit = H.intDoorUnit || function (H0) {
        var wmm = Math.round(((H0.a1 - H0.a0) || 0.8) * 1000), hand = (H0.hand === 'L') ? 1 : 2;
        return { code: (wmm >= 1450) ? 'ODI16001' : (wmm >= 1150) ? 'ODI12001'
                     : (wmm >= 850) ? ('ODI090' + '0' + hand) : (wmm >= 700) ? ('ODI080' + '0' + hand) : ('ODI060' + '0' + hand), n: 1 };
      };
      /* the products' own partitions, each behind its hook (status/modules.md 6c):
         diagWalls  the ADU family's slanted walls from an imported plan
         GA       GARAGE APT / ANY HOUSE -- the apartment's walls on its floor
         LOFT     BARNDO -- the loft's walls, a wall under the loft, the
                  shop / living door
         SEP      RANCH / GA -- the house-garage line: its wool, its rated door
         RSB      RANCH -- the wool one body a code
         HOT      SAUNA -- the sauna room lined, its door the sauna door
         OSB      the shed family -- OSB on one face, nailed, no wool, no wet
         TG       TAURUS / GARDEN BAR -- Matt's T&G instead of the OSB
         FULL     TAURUS / GARDEN BAR -- the store / porch wall floor to roof
         CASE     BARNDO / GA -- the doorways' casing; closets() their closets */
      var GA = H.ga || null, LOFT = H.loft || null, SEP = H.sep || null, RSB = H.batch || null,
          HOT = H.hot || null, OSB = H.osb || null, TG = H.tg || null, FULL = H.full || null,
          CASE = H.casing || null;
        var _partDoorList = H.doorListNew();   /* the product's list, new for this build */
        var T = ctx.THREE, g = ctx.group, i;
        _billReset(LAYER);
        /* the interior paint counts every square metre of board the three
           boarding layers put up -- partitions first, the walls, then the
           ceiling -- and the ceiling, which builds last, bills it once */
        if (H.paintReset) H.paintReset();
        H.setTally(null);
        /* SAUNA: the hot room's tally and review; BARNDO / GA: the casing's tally */
        if (H.begin) H.begin(ctx);

        var parts = API.STATE.parts || [];
        /* ranch-v37: what the L's inner edge moved, said where the build log sees it */
        if (H.said) H.said(ctx);
        var DGL = H.diagWalls ? H.diagWalls(ctx) : [];
        if (!parts.length && !DGL.length) {
          _why(ctx, 'partitions none -- add one in FLOORPLAN');
          return;
        }

        var mat = new T.MeshStandardMaterial({ color: H.wood, roughness: 0.85, metalness: 0.0 });
        var y0 = API.storeys()[0].y0 + PART.t;
        var dLift = 0;   /* core-v817: the floor build-up a door of this storey stands on */

        /* A PARTITION STOPS UNDER WHATEVER IS OVER IT, AND ON A MONO-PITCH THAT IS
           NOT THE WALL HEAD.

           The top rail went to the wall head on every roof. Under a parapet that is
           right, and under a gable the truss bottom chord is at the head anyway, so
           both looked fine -- but a mono-pitch with a flat ceiling hangs joists
           BELOW the head, and the rail ran up past their soffit to their top edge,
           through 240 mm of ceiling frame.

           The soffit is asked for rather than worked out here: two copies of that
           number is exactly how this happened. */
        /* NO CEILING ON A SHED, so there is nothing hanging below the wall head
           for a partition to stop under (the shed family hands no soffit) */
        var soffit = H.soffit ? H.soffit() : null;
        var head = (soffit != null ? soffit : API.wallTop()) - PART.t;
        var gaST = null, gaUp = false, gaTop = null;
        if (GA) {
          /* garageapt-v4: UNDER THE APARTMENT a ground-floor wall stops at the
             ground floor's ceiling line (its clear height), not under the roof */
          gaST = API.storeys(); gaUp = (gaST.length > 1);
          /* garageapt-v6: the apartment's walls stand on its subfloor and stop
             under the roof's ceiling -- the ADU's own top */
          gaTop = { y0: (gaUp ? gaST[gaST.length - 1].y0 : gaST[0].y0) + PART.t, head: head };
          if (gaUp) { soffit = GA.groundCeil(); head = soffit - PART.t; }   /* garageapt-v5: under the Type X */
        }
        var clear = head - y0;
        if (clear < 0.3) { _why(ctx, 'partitions no height to build in'); return; }
        var head0 = head, clear0 = clear;   /* barndo-v17, taurus-v10: each wall starts from these */
        /* what a wall is built from -- the storey's floor and head, which GA's
           loop moves for the apartment's walls */
        function _outer() { return { y0: y0, head: head }; }
        ctx.log('partitions top rail under ' +
                (soffit != null ? ('the ceiling frame at ' + API.fmtLen(soffit))
                                : ('the wall head at ' + API.fmtLen(API.wallTop()))) +
                ' -- rail top ' + API.fmtLen(head + PART.t));

        var grid = API.grid();
        var tally = { walls: 0, studs: 0, studLen: 0, plateLen: 0, area: 0, doors: 0 };

        /* THE ENDS ARE THE INNER FACES, NOT THE OUTLINE.

           A partition is stored end to end across the plan, and the plan is the
           OUTSIDE of the building -- so building it to those numbers ran it through
           the exterior panels and out to the cladding line. It butts against the
           inside face of the wall it meets: that is where the framing it fixes to
           actually is, and it is the length somebody cuts.

           Clamped here rather than where the partition is created, so the ones
           already saved come out right too. */
        var P2 = API.poly(), bx0 = 1e9, bx1 = -1e9, bz0 = 1e9, bz1 = -1e9;
        for (i = 0; i < P2.length; i++) {
          bx0 = Math.min(bx0, P2[i][0]); bx1 = Math.max(bx1, P2[i][0]);
          bz0 = Math.min(bz0, P2[i][1]); bz1 = Math.max(bz1, P2[i][1]);
        }
        var wt = API.wallThickness();

        var nSep = 0;
        /* ranch-v64: THE WOOL OF EVERY PARTITION IS ONE BODY a code (the
           acoustic 60, the house-garage line's 115), measured as its pieces
           (qtyN / qtyV, core-v663) -- it was a body a bay */
        var PWB = RSB ? { w: RSB.boxes(), s: RSB.boxes(),
                          mat: new T.MeshStandardMaterial({ color: isNew(LAYER + '.wool') ? MAGENTA : 0xE8D9A0, roughness: 1.0, metalness: 0.0 }) } : null;
        var G7 = null, LB7 = null, nLoft = 0, loftSaid = [];
        if (GA) {
          /* garageapt-v4: the walls drawn on the apartment's plan (card 2, st 1)
             are not built yet -- they stand on the floor of unit 5 (unit 6) */
          var gaUpN = 0, gaUpB = 0, gaSkip = 0;
          var gaG = { y0: y0, head: head, clear: clear };
          for (i = 0; i < parts.length; i++) if (parts[i] && (parts[i].st | 0) !== 0) gaUpN++;
          for (i = 0; i < parts.length; i++) {
            var stP = parts[i] ? (parts[i].st | 0) : 0;
            if (stP === 0) { _partOne(parts[i], i, null); continue; }
            /* garageapt-v6 (6b): A WALL DRAWN ON CARD 2 STANDS ON THE APARTMENT'S
               FLOOR -- its sole plate on the subfloor, its top rail under the
               apartment's ceiling, its faces, wet board and doors read the
               apartment's rooms; a storey the building has not got is left out */
            if (!gaUp || stP > gaST.length - 1) { gaSkip++; continue; }
            var gaPS = API.STATE.planStorey | 0;
            y0 = gaTop.y0; head = gaTop.head; clear = head - y0; GA.roomSt(stP); API.STATE.planStorey = stP;   /* its spans read on its own plan */
            dLift = _flLift(stP);
            try { if (clear > 0.3) { _partOne(parts[i], i, null); gaUpB++; } } finally { y0 = gaG.y0; head = gaG.head; clear = gaG.clear; GA.roomSt(0); API.STATE.planStorey = gaPS; dLift = 0; }
          }
          if (gaUp || gaUpN) ctx.log('GA part  ground floor ' + (parts.length - gaUpN) + ' wall(s), top rail under the ground ceiling at +' + Math.round(((gaUp ? GA.groundCeil() : gaST[0].y1) - gaST[0].y0) * 1000) +
                                     (gaUpN ? ('; the apartment ' + gaUpB + ' wall(s) on its subfloor at +' + Math.round((gaTop.y0 - PART.t - gaST[0].y0) * 1000) + ', top rail under its ceiling at +' + Math.round((gaTop.head + PART.t - gaST[0].y0) * 1000)) : '') +
                                     (gaSkip ? ('; ' + gaSkip + ' wall(s) of a storey this building has not got -- left out') : ''));
        } else if (LOFT) {
          /* barndo-v20 (7d1): A WALL DRAWN ON THE LOFT'S PLAN (card 2, st 1)
             STANDS ON THE LOFT -- its sole plate on the subfloor, its top rail
             under the ceiling over the loft, its ends inside the loft's room;
             its faces and doors read the loft's rooms. It was built on the
             slab under the loft before. A wall of a storey the barndo has not
             got (st 2+) is left out with a line. */
          try { G7 = LOFT.geom(); } catch (eG7) { G7 = null; }
          if (G7) {
            /* to the guard's inner face at the open edge: the guard's newel stands at the wall's end */
            var lu0 = G7.uG + G7.bt, lu1 = G7.u1 - LOFT.LF.newel;
            LB7 = G7.FR.L ? { x0: G7.f0, x1: G7.f1, z0: lu0, z1: lu1 } : { x0: lu0, x1: lu1, z0: G7.f0, z1: G7.f1 };
          }
          for (i = 0; i < parts.length; i++) {
            var stP = LOFT.partSt(parts[i]);
            if (stP === 0) { _partOne(parts[i], i, null, 0); continue; }
            if (stP !== 1 || !LB7) {
              ctx.log('PF part  7d1 SKIPPED ' + ((parts[i] || {}).dir === 'v' ? 'v@' : 'h@') + Math.round(((parts[i] || {}).at || 0) * 1000) + ' -- drawn on storey ' + (stP + 1) +
                      (stP === 1 ? ', and there is no loft (the LOFT switch on the Floorplan step)' : ', which the barndo has not got'));
              continue;
            }
            var tgP = g.children.length;
            LOFT.roomSt(1);
            try { _partOne(parts[i], i, null, 1); } finally { LOFT.roomSt(0); }
            LOFT.tag(g, tgP, '7d1');
            if (g.children.length > tgP) nLoft++;
          }
          if (LOFT.planSaid()) ctx.log('PF plan  ' + LOFT.planSaid());   /* barndo-v23: the default plan, on the page that put it in */
          if (nLoft || loftSaid.length) ctx.log('PF part  7d1 ' + nLoft + ' wall(s) on the loft, sole plate on the subfloor at +' + LOFT.mm(G7.yS1 - G7.F) + ', top rail under the ceiling at +' +
                                                LOFT.mm(head0 + PART.t - G7.F) + ' (' + loftSaid.join(' | ') + ')');
        } else for (i = 0; i < parts.length; i++) _partOne(parts[i], i, null);
        /* THE SLANTED WALLS (adu-v1410, Marek: zkusime to postavit -- jen pri
           prevodu pudorysu; dvere v sikme stene rovnou s kridlem). The plan
           import keeps them in the project (floorplan v125); each is framed,
           boarded, insulated, tiled and given its doors by the very code a
           square partition is, through one mapping from along-and-across to
           the plan -- so the bill and the model cannot tell them apart. */
        for (i = 0; i < DGL.length; i++) _partOne(DGL[i].p, -1, DGL[i]);
        if (PWB) {
          if (PWB.w.n()) { _asmMark(ctx, 'layer_mineral_wool', 'top', 'Mineral wool', PART_WOOL.code); RSB.baked(T, g, PWB.w, PWB.mat, 'm2', 'partition-wool'); }
          if (PWB.s.n()) { _asmMark(ctx, 'layer_mineral_wool', 'top', 'Mineral wool', SEP.wool); RSB.baked(T, g, PWB.s, PWB.mat, 'm2', 'partition-wool-garage'); }
          if (PWB.w.n() || PWB.s.n()) ctx.log('partitions wool drawn as ' + ((PWB.w.n() ? 1 : 0) + (PWB.s.n() ? 1 : 0)) + ' body(ies) for ' + (PWB.w.n() + PWB.s.n()) + ' piece(s) (ranch-v64)');
        }
        if (DGL.length) ctx.log('partitions ' + DGL.length + ' slanted wall(s) built from the imported plan -- not edited by hand: a new plan, Undo or RESTORE change them');

        function _partOne(p, i, DG, stP) {
          /* the storey it stands on: the floor and head GA's loop set, or
             BARNDO's loft floor for st 1 (barndo-v20) */
          var OU = _outer();
          var y0 = (LOFT && stP === 1 && G7) ? G7.yS1 + PART.t : OU.y0;
          var head = OU.head, clear = head - y0;
          /* A ROOM LINE IS NOT A WALL (adu-v1403, Marek: dve mistnosti, ktere
             nejsou oddelene prickou -- delici cara). It parts two rooms on the
             plan and nothing is built on it: no plates, studs, wool, board,
             screws, tile or paint, and no door. */
          if (p && p.sep) {
            nSep++;
            ctx.log('partitions ROOM LINE ' + (p.dir === 'v' ? 'v@' : 'h@') + Math.round(p.at * 1000) + ' -- no wall, nothing built or billed');
            return;
          }
          /* A WALL ANOTHER LAYER BUILDS (core-v815, DUPLEX's party wall):
             p.own names that layer. On the plan and for everything that asks
             about walls -- the rooms, the services' spaces, the skirting -- it
             is a wall, at its own thickness p.t; nothing of it is built or
             billed here. */
          if (p && p.own) {
            ctx.log('partitions ' + (p.dir === 'v' ? 'v@' : 'h@') + Math.round(p.at * 1000) + ' built by ' + p.own + ' -- nothing here');
            return;
          }
          var vert0 = (p.dir === 'v');
          var inLo = (vert0 ? bz0 : bx0) + wt;
          var inHi = (vert0 ? bz1 : bx1) - wt;
          /* THE MODEL AND THE DRAWING WERE MEASURING THE WALL DIFFERENTLY.

             The plan asks partSpan, which trims an end back to whatever the wall
             runs into. The model asked p.a and p.b, which are the building's inner
             faces -- so a partition that the drawing stops at a crossing was built
             straight through it in timber, and the two disagreed about the same
             wall. One answer for both now. */
          var spn = (!DG && API.partSpan) ? API.partSpan(p) : null;
          var lo = Math.max(spn ? spn[0] : Math.min(p.a, p.b), inLo);
          var hi = Math.min(spn ? spn[1] : Math.max(p.a, p.b), inHi);
          /* barndo-v20: a loft wall runs inside the loft's room -- from the
             gable's board to the open edge, between the long walls' boards */
          if (LOFT && stP === 1 && LB7 && !DG) {
            var cLo = vert0 ? LB7.x0 : LB7.z0, cHi = vert0 ? LB7.x1 : LB7.z1;
            if (p.at < cLo + PART.d / 2 || p.at > cHi - PART.d / 2) {
              ctx.log('PF part  7d1 SKIPPED ' + (vert0 ? 'v@' : 'h@') + Math.round(p.at * 1000) + ' -- its line is outside the loft (' + Math.round(cLo * 1000) + '..' + Math.round(cHi * 1000) + ')');
              return;
            }
            lo = Math.max(lo, vert0 ? LB7.z0 : LB7.x0);
            hi = Math.min(hi, vert0 ? LB7.z1 : LB7.x1);
          }

          /* core-v811 (TWO STORY thread, Marek 2026-11-03: "Na balkone /
             terase jsou nepruhledne dvere"): A WALL STAYS INSIDE ITS STOREY'S
             OWN OUTLINE. A storey with an outline of its own
             (__PROD._storeyPoly -- ANY HOUSE's set-back floor round a roof
             terrace) was cut against the building's box only, so a partition
             laid before the terrace stood out on the terrace in the facade
             and its board filled the terrace doors. Its span is now the
             longest stretch of its line inside that outline, the wall's
             thickness in from every side; nothing left, nothing built. */
          var sPo = DG ? 0 : ((typeof stP === 'number') ? stP : (p ? (p.st | 0) : 0));
          if (sPo >= 1 && __PROD && typeof __PROD._storeyPoly === 'function') {
            var POo = null;
            try { POo = __PROD._storeyPoly(sPo, poly()); } catch (ePOo) { POo = null; }
            if (Array.isArray(POo) && POo.length >= 3) {
              var cutO = _lineInOutline(POo, vert0, p.at, lo, hi, wt), tagO = (vert0 ? 'v@' : 'h@') + Math.round(p.at * 1000);
              if (!cutO) {
                ctx.log('partitions ' + tagO + ' SKIPPED -- storey ' + (sPo + 1) + ': its line lies outside the storey\'s own outline or inside its wall (core-v811), nothing built or billed');
                return;
              }
              if (cutO[0] > lo + 0.005 || cutO[1] < hi - 0.005) {
                ctx.log('partitions ' + tagO + ' cut to storey ' + (sPo + 1) + '\'s own outline (core-v811): ' + Math.round(lo * 1000) + '..' + Math.round(hi * 1000) + ' -> ' +
                        Math.round(cutO[0] * 1000) + '..' + Math.round(cutO[1] * 1000) + (cutO[2] ? (' (' + cutO[2] + ' other piece(s) of its line inside, the longest kept)') : ''));
                lo = cutO[0]; hi = cutO[1];
              }
            }
          }

          /* AND A T JOINT IS TWO WALLS THAT HAVE TO TOUCH.

             partSpan stops this wall at the CENTRE LINE of the one it meets, which
             is 50 mm inside it -- so the plates overlapped, the end stud sat buried
             in the other wall, and the corner where the boards meet had no timber
             in it at all. That is the hole: not a gap in the framing, a gap in what
             the sheathing can be fixed to.

             The end butts the FACE, and the wall it butts gets a stud at the
             crossing to receive it. Every joint says so in the log with both
             numbers, because a joint that is out by half a thickness looks right
             until somebody measures it. */
          var half = PART.d / 2;
          var jn = 0, jq, po;
          var lo0 = lo, hi0 = hi, jointNotes = [];
          /* what each end does at its joint, for the boards: through (covers the
             corner) or butt, and on which face the corner's inside lies */
          var endLo = { through: false, inner: 0 }, endHi = { through: false, inner: 0 };
          /* A SLANTED WALL (adu-v1410) runs between its own two ends, already
             taken to the faces it meets: no joint is worked out here */
          if (DG) { lo = 0; hi = DG.len; lo0 = 0; hi0 = DG.len; }
          for (jq = 0; !DG && jq < parts.length; jq++) {
            if (jq === i) continue;
            po = parts[jq];
            if (!po || po.sep || (po.dir === 'v') === vert0) continue;   /* parallel, or a room line: no joint */
            if (GA && (po.st | 0) !== (p.st | 0)) continue;   /* garageapt-v6: a wall of the other storey */
            if (LOFT && LOFT.partSt(po) !== (stP | 0)) continue;   /* barndo-v20: a wall of the other storey */
            var oc = po.at;
            /* AN L CORNER IS NOT A T. Where the other wall also ENDS at this one
               (an alcove corner), butting its face leaves the outer quarter of
               the corner uncovered by either -- the bite he sees on every corner
               (Marek). At an L the vertical wall runs through to the far face of
               the horizontal one and the horizontal butts it; at a T the end
               still butts the face. */
            var spo = API.partSpan ? API.partSpan(po) : null;
            var oLo = spo ? spo[0] : Math.min(po.a, po.b), oHi = spo ? spo[1] : Math.max(po.a, po.b);
            /* ONLY A WALL WE ACTUALLY REACH. Two walls on the same line -- one on
               each side of the house -- both answered for a joint at that
               height, and the far one, a T, overwrote the near one's L and left
               the corner bitten (Marek). A joint needs our line inside the other
               wall's span. */
            if (p.at < oLo - half - 0.02 || p.at > oHi + half + 0.02) continue;
            var oEndLo = Math.abs(oLo - p.at) < half + 0.02, oEndHi = Math.abs(oHi - p.at) < half + 0.02;
            var otherEndsHere = oEndLo || oEndHi;
            var runThrough = otherEndsHere && vert0;   /* the vertical one covers the corner */
            /* the other wall's body lies on our +side when its lo end is here
               (it runs off towards +), on our -side when its hi end is here */
            var innerSide = oEndLo ? 1 : (oEndHi ? -1 : 0);
            if (Math.abs(lo - oc) < half + 0.02) {
              lo = runThrough ? oc - half : oc + half; jn++;
              endLo = { through: runThrough, inner: innerSide, butt: !runThrough && otherEndsHere };
              jointNotes.push('lo meets ' + (po.dir === 'v' ? 'v@' : 'h@') + Math.round(oc * 1000) + ' (its span ' + Math.round(oLo * 1000) + '..' + Math.round(oHi * 1000) +
                              ', ends here: ' + (otherEndsHere ? 'yes' : 'no') + ') -> ' + (runThrough ? 'THROUGH' : (otherEndsHere ? 'BUTT (L, other runs through)' : 'BUTT (T)')) +
                              (innerSide ? (' inner ' + (innerSide > 0 ? '+' : '-')) : ''));
            }
            if (Math.abs(hi - oc) < half + 0.02) {
              hi = runThrough ? oc + half : oc - half; jn++;
              endHi = { through: runThrough, inner: innerSide, butt: !runThrough && otherEndsHere };
              jointNotes.push('hi meets ' + (po.dir === 'v' ? 'v@' : 'h@') + Math.round(oc * 1000) + ' (its span ' + Math.round(oLo * 1000) + '..' + Math.round(oHi * 1000) +
                              ', ends here: ' + (otherEndsHere ? 'yes' : 'no') + ') -> ' + (runThrough ? 'THROUGH' : (otherEndsHere ? 'BUTT (L, other runs through)' : 'BUTT (T)')) +
                              (innerSide ? (' inner ' + (innerSide > 0 ? '+' : '-')) : ''));
            }
          }
          if (DG) {
            var fqP = [DG.ax + DG.ux * DG.len / 2 + DG.nx * (PART.d / 2 + 0.10), DG.az + DG.uz * DG.len / 2 + DG.nz * (PART.d / 2 + 0.10)];
            var fqM = [DG.ax + DG.ux * DG.len / 2 - DG.nx * (PART.d / 2 + 0.10), DG.az + DG.uz * DG.len / 2 - DG.nz * (PART.d / 2 + 0.10)];
            var fwP = false, fwM = false;
            try { fwP = !!_wetAt(fqP[0], fqP[1]); fwM = !!_wetAt(fqM[0], fqM[1]); } catch (eFw) {}
            ctx.log('partitions SLANTED ' + DG.id + ' ' + API.fmtLen(DG.len) + ' at ' + Math.round(Math.abs(DG.ang) * 180 / Math.PI) + ' deg from ' +
                    API.fmtLen(DG.ax) + ',' + API.fmtLen(DG.az) + ' -- ' + DG.note + (DG.holes.length ? (' | ' + DG.holes.length + ' door(s)') : ' | no door') +
                    ' | faces ' + (fwP ? 'WET' : 'dry') + ' / ' + (fwM ? 'WET' : 'dry') + ' (asked at its middle)');
          }
          else ctx.log('partitions ' + (vert0 ? 'v@' : 'h@') + Math.round(p.at * 1000) + ' ends: span ' +
                  Math.round(lo0 * 1000) + '..' + Math.round(hi0 * 1000) + ' -> ' + Math.round(lo * 1000) + '..' + Math.round(hi * 1000) +
                  (jointNotes.length ? (' | ' + jointNotes.join(' | ')) : ' | free at both ends'));

          /* taurus-v10 (Marek: stena mezi shed a druhou casti ma jakesi
             zdvojene profily; ma tam byt zakladni ram od podlahy ke stropu, i
             bez dalsiho vodorovneho profilu): the partition between the store
             and the porch is ONE frame -- studs from the sole plate to a
             single plate under the rafters, no plate at the wall head and no
             second frame stood on it; the store top (_buildPartTop) keeps only
             the blocking between the rafters over it */
          var full9 = (FULL && !vert0) ? FULL.at(p, lo, hi) : null;
          if (full9) {
            head = full9.head; clear = head - y0;
            ctx.log(FULL.tag + '  partition h@' + Math.round(((typeof p.c === 'number') ? p.c : p.at) * 1000) + ' one frame floor to roof: studs ' + Math.round(clear * 1000) +
                    ', plate under the rafters at ' + Math.round(full9.top * 1000) + ' (no plate at the wall head ' + Math.round((head0 + PART.t) * 1000) + ')');
          }

          var len = hi - lo;
          if (len < 0.3) {
            /* A PARTITION IN THE PLAN AND NOT IN THE MODEL IS WORTH A LINE.
               Silence here reads as a bug in the drawing; it is usually a wall
               whose span got trimmed to nothing by the thing it runs into. */
            ctx.log('partitions SKIPPED ' + (vert0 ? 'v@' : 'h@') + Math.round(p.at * 1000) +
                    ' mm -- span came out ' + Math.round(len * 1000) + ' mm after trimming to ' +
                    'what it meets; under 300 mm there is no wall to build');
            return;
          }
          if (jn) {
            ctx.log('partition ' + (vert0 ? 'v' : 'h') + '@' + API.fmtLen(p.at) +
                    ' butts ' + jn + ' wall(s) face to face -- runs ' +
                    API.fmtLen(lo) + ' to ' + API.fmtLen(hi));
          }

          /* barndo-v17: A PARTITION UNDER THE LOFT stops at the loft's joists (7b) */
          if (LOFT && stP === 1) loftSaid.push((vert0 ? 'v@' : 'h@') + Math.round(p.at * 1000) + ' ' + Math.round((hi - lo) * 1000) + ' long, ' + Math.round(clear * 1000) + ' tall');
          if (LOFT && stP !== 1) try {
            var LPH = LOFT.partHead(p, DG, lo, hi, vert0);
            if (LPH && LPH.head < head) {
              head = LPH.head; clear = head - y0;
              ctx.log('PF part  5a ' + (DG ? DG.id : ((vert0 ? 'v@' : 'h@') + Math.round(p.at * 1000))) + ' under the loft (' + Math.round(LPH.under * 1000) + ' of ' + Math.round(LPH.len * 1000) +
                      ') -- top rail under its joists, ' + API.fmtLen(head + PART.t - (y0 - PART.t)) + ' over the slab' + (LPH.part ? '; it runs out from under the loft and stops there its whole length -- draw it as two walls for a full-height part' : ''));
            }
          } catch (eLPH) {}

          /* along the partition, and across it */
          var vert = vert0;
          /* A POINT OF THIS WALL IN THE PLAN (adu-v1410): along it from lo,
             and across it -- one mapping for the square walls and the
             slanted ones, so the framing, the board, the probes for the wet
             rooms and the doors all stand where the wall does */
          function W(along, off) {
            if (DG) return [DG.ax + DG.ux * along + DG.nx * off, DG.az + DG.uz * along + DG.nz * off];
            return vert ? [p.at + off, lo + along] : [lo + along, p.at + off];
          }
          var ROTY = DG ? (Math.PI / 2 - DG.ang) : (vert ? 0 : Math.PI / 2);
          function put(w, h, d, along, y, mOverride) {
            var m = new T.Mesh(new T.BoxGeometry(w, h, d), mOverride || mat);
            var q0 = W(along, 0);
            m.position.set(q0[0], y, q0[1]);
            if (ROTY) m.rotation.y = ROTY;
            g.add(m);
          }

          /* the same, but stood off the wall's centre line -- which is what a face
             board is: the wall's own plane plus half a stud plus half a board */
          function putOff(w, h, d, along, y, mOverride, off) {
            var m = new T.Mesh(new T.BoxGeometry(w, h, d), mOverride || mat);
            var q0 = W(along, off);
            m.position.set(q0[0], y, q0[1]);
            if (ROTY) m.rotation.y = ROTY;
            g.add(m);
          }

          /* sole and a SINGLE top plate -- nothing lands on this wall, so the
             doubled cap an exterior panel needs would be timber doing nothing */
          _asmCode(ctx, PART.code, 'top');
          put(PART.d, PART.t, len, len / 2, y0 - PART.t / 2);
          put(PART.d, PART.t, len, len / 2, head + PART.t / 2);
          tally.plateLen += len * 2;

          /* THE DOORS THIS WALL CARRIES, in the wall's own along-coordinate.
             The plan has had them for a while and the model had none: studs marched
             straight through every doorway and the framing was solid where the
             drawing showed a hole. */
          var holes = [];
          if (DG) { for (var hqD = 0; hqD < DG.holes.length; hqD++) { holes.push(DG.holes[hqD]); _partDoorList.push(DG.holes[hqD]); } }
          else (function () {
            var ds = API.STATE.doors || [], q, d, a0, a1;
            for (q = 0; q < ds.length; q++) {
              d = ds[q];
              if (!d || d.pid !== p.id) continue;
              var dw = d.w || DOOR_W_DEF;
              a0 = (d.u - dw / 2) - lo;
              a1 = (d.u + dw / 2) - lo;
              if (a1 <= 0.02 || a0 >= len - 0.02) continue;
              a0 = Math.max(0, a0); a1 = Math.min(len, a1);
              if (a1 - a0 < 0.30) continue;
              var hD = { a0: a0, a1: a1, pass: !!d.pass, sw: (d.sw | 0), slide: (d.slide | 0), kind: d.kind || '', w: dw, hand: (d.sw | 0) ? 'L' : 'R' };
              if (HOT) {
                /* a door in a partition of the sauna room is the SAUNA door (sauna-v43) */
                var midD = lo + (a0 + a1) / 2, prD = PART.d / 2 + 0.25;
                hD.hot = !!(vert0 ? (HOT.at(p.at + prD, midD) || HOT.at(p.at - prD, midD))
                                  : (HOT.at(midD, p.at + prD) || HOT.at(midD, p.at - prD)));
              }
              holes.push(hD);
            }
            holes.sort(function (m1, m2) { return m1.a0 - m2.a0; });
            for (var hq9 = 0; hq9 < holes.length; hq9++) _partDoorList.push(holes[hq9]);
          })();

          /* ranch-v34 / garageapt-v13: the house-garage line -- a garage on one
             side, a heated room on the other */
          var sepOn = SEP ? SEP.on(p, stP) : false;
          /* ranch-v38, garageapt-v13: a door through it is the 20-minute rated
             one; barndo-v19: the door between the shop and the living part */
          if (SEP && sepOn) try {
            for (var hqF = 0; hqF < holes.length; hqF++) {
              var HF = holes[hqF], amF = (HF.a0 + HF.a1) / 2, pA = W(amF, half + SEP.probe), pB = W(amF, -(half + SEP.probe));
              HF.fire = !HF.pass && SEP.at(pA[0], pA[1], pB[0], pB[1]);
              if (HF.fire) ctx.log(SEP.fireSay(HF, _intDoorUnit(HF)));
            }
          } catch (eFD) {}
          else if (LOFT && stP !== 1) try {
            var LVd = LOFT.living();
            for (var hqG = 0; hqG < holes.length; hqG++) {
              var HG = holes[hqG], amG = (HG.a0 + HG.a1) / 2, pAG = W(amG, 0.30), pBG = W(amG, -0.30);
              var sA = LOFT.inShop(LVd, pAG[0], pAG[1]), sB = LOFT.inShop(LVd, pBG[0], pBG[1]);
              HG.fire = !HG.pass && ((sA && LOFT.inLiving(LVd, pBG[0], pBG[1])) || (sB && LOFT.inLiving(LVd, pAG[0], pAG[1])));
              if (HG.fire) ctx.log('PF part  5a the door through the split (' + Math.round((HG.a1 - HG.a0) * 1000) + ', ' + (HG.hand || '') + ') is the fire rated one: ' + _intDoorUnit(HG).code +
                                   (_intDoorUnit(HG).fire ? '' : ' -- CHECK: the rated rows are 32 in. (ODI08008/9) and 36 in. (ODI09008/9) hinged doors, this one is neither'));
            }
          } catch (eFD2) {}

          /* studs on the same module as everything else, so a board edge and a
             door lining land where they are expected -- and none of them inside a
             doorway */
          var n = Math.max(1, Math.round(len / grid));
          for (var k = 0; k <= n; k++) {
            var at = len * (k / n);
            var off = (k === 0) ? PART.t / 2 : (k === n ? -PART.t / 2 : 0);
            if (_inHole(holes, at + off)) continue;
            put(PART.d, clear, PART.t, at + off, y0 + clear / 2);
            tally.studs++;
            tally.studLen += clear;
          }

          /* ACOUSTIC WOOL IN THE BAYS THE STUDS LEAVE.

             A partition is not insulated to keep heat in -- both sides are the same
             room temperature. It is insulated so a bathroom is not audible from a
             bedroom, which is the one thing people notice about a small house and
             the one thing a stud wall is bad at empty. An empty cavity is a drum
             with a skin on each face.

             It fills the bay, not the wall: what the studs take up is theirs, and
             the wool is 60 mm in a 100 mm stud so it stays friction-fitted without
             being squeezed against both boards, which would carry sound through it
             rather than stopping it.

             Nothing goes in a doorway. */
          /* THE SOLID RUNS -- the wall minus its openings. The wool fills them, the
             board covers them, and they are worked out ONCE so the two cannot
             disagree about where the wall is. */
          var runs5 = [], cur5 = 0, hq5;
          for (hq5 = 0; hq5 < holes.length; hq5++) {
            if (holes[hq5].a0 > cur5 + 0.02) runs5.push([cur5, holes[hq5].a0]);
            cur5 = Math.max(cur5, holes[hq5].a1);
          }
          if (len - cur5 > 0.02) runs5.push([cur5, len]);

          /* the shed family has no wool in its partitions */
          if (!OSB) (function () {
            /* garageapt-v13 (COPIED from ranch-v34): a bay with the GARAGE on
               one side and a heated room on the other is the thermal line --
               the line's wool (SEP.wool) fills it, not the acoustic 60 mm. Asked per bay at
               its middle, a probe each side. The pieces are listed first and
               drawn after, the line's under its own mark. */
            var isSep = function (along) {
              if (!sepOn) return false;
              var pa = W(along, half + SEP.probe), pb = W(along, -(half + SEP.probe));
              return SEP.at(pa[0], pa[1], pb[0], pb[1]);
            };
            var PCS = [], aSep = 0, bSep = 0;
            var put = function (w, h, d, along, y) { PCS.push({ w: w, h: h, d: d, along: along, y: y, sep: (w !== PART_WOOL.t) }); };
            var putS = function (h, d, along, y) { PCS.push({ w: Math.min(SEP ? SEP.woolT : PART.d, PART.d), h: h, d: d, along: along, y: y, sep: true }); };
            var wm = new T.MeshStandardMaterial({
              color: isNew(LAYER + '.wool') ? MAGENTA : 0xE8D9A0,
              roughness: 1.0, metalness: 0.0 });
            /* A BAY IS BOUNDED BY WHATEVER IS SOLID -- A STUD OR A DOOR JAMB.

               I bounded them by studs only, and threw away any stud that fell
               inside a doorway. So the bay between the last stud before a door and
               the first stud after it spanned the whole opening, failed the
               in-a-hole test, and was dropped -- taking with it the two strips
               BESIDE the jambs, which are wall and want filling like any other.
               That is why the wool went missing exactly around the doors.

               The solid runs come first: the wall minus the openings. Each run is
               then cut at the studs standing in it, and every piece is filled. A
               doorway is simply not one of the runs, so nothing has to be tested
               for being inside one. */
            var wq, bays = 0, aWool = 0;

            for (var rq5 = 0; rq5 < runs5.length; rq5++) {
              var r0 = runs5[rq5][0], r1 = runs5[rq5][1];
              /* the stud faces inside this run, in order */
              var cuts5 = [r0];
              for (wq = 0; wq <= n; wq++) {
                var atW = len * (wq / n);
                var offW = (wq === 0) ? PART.t / 2 : (wq === n ? -PART.t / 2 : 0);
                var c5 = atW + offW;
                if (c5 - PART.t / 2 <= r0 + 0.001 || c5 + PART.t / 2 >= r1 - 0.001) continue;
                cuts5.push(c5 - PART.t / 2);
                cuts5.push(c5 + PART.t / 2);
              }
              cuts5.push(r1);
              for (wq = 0; wq + 1 < cuts5.length; wq += 2) {
                var a5 = cuts5[wq], b5 = cuts5[wq + 1];
                if (b5 - a5 < 0.03) continue;
                if (isSep((a5 + b5) / 2)) {
                  putS(clear, b5 - a5, (a5 + b5) / 2, y0 + clear / 2);
                  bSep++; aSep += (b5 - a5) * clear;
                  continue;
                }
                put(PART_WOOL.t, clear, b5 - a5, (a5 + b5) / 2, y0 + clear / 2, wm);
                bays++;
                aWool += (b5 - a5) * clear;
              }
            }

            /* AND THE PANEL OVER EACH DOORWAY.

               Above the header is cripple studs and cavity -- wall, in every sense
               that matters here. It is also the piece of a partition that carries
               the most sound, because it is the shortest path from one room to the
               next: over the door, through 300 mm of nothing, and down again.

               The runs above skip the opening entirely, which is right for the
               height of the door and wrong for everything above it. */
            var dhW = Math.min(DOOR_H_DEF, clear - 0.10), hdrW = 0.14;
            if (dhW >= 1.2) {
              for (var oq = 0; oq < holes.length; oq++) {
                var Ho = holes[oq];
                var yTop = y0 + dhW + hdrW;
                var hOver = (y0 + clear) - yTop;
                var wOver = Ho.a1 - Ho.a0;
                if (hOver < 0.05 || wOver < 0.05) continue;
                if (isSep((Ho.a0 + Ho.a1) / 2)) {
                  putS(hOver, wOver, (Ho.a0 + Ho.a1) / 2, yTop + hOver / 2);
                  bSep++; aSep += wOver * hOver;
                  tally.sepDoors = (tally.sepDoors || 0) + 1;
                  continue;
                }
                put(PART_WOOL.t, hOver, wOver, (Ho.a0 + Ho.a1) / 2, yTop + hOver / 2, wm);
                bays++;
                aWool += wOver * hOver;
              }
            }
            /* drawn: the line's wool, then the acoustic -- each under its mark;
               RANCH: into the layer's batch (PWB), a piece a bay, turned with
               the wall (ROTY) -- the same box put() drew (ranch-v64) */
            var wmS = null, pq7;
            if (PWB) {
              var cR = Math.cos(ROTY || 0), sR = Math.sin(ROTY || 0);
              for (pq7 = 0; pq7 < PCS.length; pq7++) {
                var PQ = PCS[pq7], q0W = W(PQ.along, 0);
                (PQ.sep ? PWB.s : PWB.w).add(PQ.w, PQ.h, PQ.d, 0, 0, 0, [cR, 0, -sR, 0, 0, 1, 0, 0, sR, 0, cR, 0, q0W[0], PQ.y, q0W[1], 1]);
              }
            } else {
              if (bSep) {
                _asmMark(ctx, 'layer_mineral_wool', 'top', 'Mineral wool', SEP.wool);
                wmS = new T.MeshStandardMaterial({ color: isNew(LAYER + '.wool') ? MAGENTA : 0xE3CF8E, roughness: 1.0, metalness: 0.0 });
                for (pq7 = 0; pq7 < PCS.length; pq7++) if (PCS[pq7].sep) putOff(PCS[pq7].w, PCS[pq7].h, PCS[pq7].d, PCS[pq7].along, PCS[pq7].y, wmS, 0);
              }
              _asmMark(ctx, 'layer_mineral_wool', 'top', 'Mineral wool', PART_WOOL.code);
              for (pq7 = 0; pq7 < PCS.length; pq7++) if (!PCS[pq7].sep) putOff(PCS[pq7].w, PCS[pq7].h, PCS[pq7].d, PCS[pq7].along, PCS[pq7].y, wm, 0);
            }
            if (aWool > 0) {
              _add(LAYER, PART_WOOL.code, aWool);
              tally.wool = (tally.wool || 0) + aWool;
              tally.woolBays = (tally.woolBays || 0) + bays;
            }
            if (aSep > 0) {
              _add(LAYER, SEP.wool, aSep);
              tally.sepWool = (tally.sepWool || 0) + aSep;
              tally.sepBays = (tally.sepBays || 0) + bSep;
              (tally.sepWalls = tally.sepWalls || []).push((DG ? DG.id : ((vert0 ? 'v@' : 'h@') + Math.round(p.at * 1000))) + ' ' + bSep + ' bay(s)');
            }
          })();

          /* AND A STUD WHERE ANOTHER WALL LANDS ON THIS ONE, so the one that butts
             has something to fix to and the boards have a corner to stop at. */
          /* ranch-v59: under the partition timber's own mark -- the wool's was
             still set, so these studs were 'mineral wool' in the assembly walk,
             the exploded view and the review steps (RANCH; the others keep
             their marks -- the core's impregnation reads them) */
          if (H.timberMarks) _asmCode(ctx, PART.code, 'top');
          for (jq = 0; !DG && jq < parts.length; jq++) {
            if (jq === i) continue;
            po = parts[jq];
            if (!po || po.sep || (po.dir === 'v') === vert0) continue;   /* a room line lands on nothing (sauna-v102) */
            if (GA && (po.st | 0) !== (p.st | 0)) continue;   /* garageapt-v6: a wall of the other storey */
            var at2 = po.at - lo;
            if (at2 < PART.t || at2 > len - PART.t) continue;   /* it lands off the end */
            if (_inHole(holes, at2)) continue;                   /* a doorway is there */
            put(PART.d, clear, PART.t, at2, y0 + clear / 2);
            tally.studs++; tally.studLen += clear;
          }
          /* and where a SLANTED wall lands on this one (adu-v1410) */
          for (jq = 0; !DG && jq < DGL.length; jq++) {
            for (var lq = 0; lq < DGL[jq].lands.length; lq++) {
              var LDq = DGL[jq].lands[lq];
              if (LDq.po !== p) continue;
              var atG = (vert ? LDq.z : LDq.x) - lo;
              if (atG < PART.t || atG > len - PART.t) continue;
              if (_inHole(holes, atG)) { ctx.log('partitions ' + (vert ? 'v@' : 'h@') + Math.round(p.at * 1000) + ' slanted wall ' + DGL[jq].id + ' lands in a DOORWAY at ' + Math.round(atG * 1000) + ' mm -- no stud there; the plan has its end in the opening'); continue; }
              put(PART.d, clear, PART.t, atG, y0 + clear / 2);
              tally.studs++; tally.studLen += clear;
              ctx.log('partitions ' + (vert ? 'v@' : 'h@') + Math.round(p.at * 1000) + ' stud at ' + Math.round(atG * 1000) + ' mm for slanted wall ' + DGL[jq].id);
            }
          }

          /* ------------------------------------------------------------------
             THE BOARD, ONE FACE AT A TIME.

             A partition has two faces and they are two different rooms: the
             bathroom side takes the green sheet, the bedroom on the other side of
             the same studs takes the plain one. Boarding the WALL would give one
             answer where there are two, which is why the side is asked rather than
             the partition.

             The face follows the same solid runs the wool does -- wall minus the
             openings -- plus the panel over each door. It has to, or the board and
             the insulation behind it would disagree about where the wall is.
             ------------------------------------------------------------------ */
          (function () {
            var dhB = Math.min(DOOR_H_DEF, clear - 0.10), hdrB = 0.14;
            /* the shed family: ONE FACE, faces[0] is the side the sheet goes on */
            var faces = OSB ? [-1] : [-1, 1], fq;

            /* ASK PER PIECE, NOT PER WALL.

               One probe at the middle of the wall decided the whole face, so a
               partition that borders the bathroom along part of its length came out
               entirely plain or entirely green -- and if that midpoint happened to
               land in a doorway it was in no room at all, which is why the wall
               WITH the door was the one that missed its green board.

               A sheet belongs to the room the sheet faces. Each run asks for
               itself, and the panel over a door asks at the door. */
            for (fq = 0; fq < faces.length; fq++) {
              var sgB = faces[fq];
              var offB = sgB * (PART.d / 2 + PART_BOARD.t / 2);
              var probe = sgB * (PART.d / 2 + 0.25);
              /* THE BOARD REACHES THE OUTER CORNER. On a through end the board
                 on the corner's outside runs a board's thickness past the frame
                 to cover the end of the other wall's board, and the board on the
                 inside stops a thickness short so the two do not cross; on a
                 butting end the boards stop where the frame does (Marek: the
                 plasterboard must reach the outer corner too). */
              var extLo = 0, extHi = 0;
              if (endLo.through && endLo.inner) extLo = (sgB === endLo.inner) ? PART_BOARD.t : -PART_BOARD.t;
              if (endHi.through && endHi.inner) extHi = (sgB === endHi.inner) ? -PART_BOARD.t : PART_BOARD.t;
              /* and on a BUTTING end of an L the board on the corner's outside
                 runs on across the END of the through wall -- its whole width and
                 its board -- because nothing else covers that end grain (Marek:
                 the board has to reach the very corner) */
              var over = PART.d + PART_BOARD.t;
              if (endLo.butt && endLo.inner && sgB !== endLo.inner) extLo = -over;
              if (endHi.butt && endHi.inner && sgB !== endHi.inner) extHi = over;

              function faceAt(alongB) {
                /* NO WET ROOM IN A SHED, so no face asks: every sheet is the same sheet */
                if (OSB) return false;
                /* a slanted wall asks once, at its middle and close to the
                   face: near its ends the room it faces narrows to a point
                   and a probe a hand away lands in the room next door */
                var qB = DG ? W(DG.len / 2, sgB * (PART.d / 2 + 0.10)) : W(alongB, probe);
                return _wetAt(qB[0], qB[1]);
              }
              function sheetAt(wetB, wB, hB, alongB, yB2) {
                /* taurus-v12: on Matt's build-up the partition is boarded with
                   his T&G, course by course, like the facade -- no OSB */
                if (TG && TG.on()) {
                  _asmCode(ctx, TG.TG.code, 'top');
                  var tgM = new T.MeshStandardMaterial({ color: isNew(LAYER + '.board') ? MAGENTA : TG.col, roughness: 0.86, metalness: 0.0 });
                  var yLoT = yB2 - hB / 2, yHiT = yB2 + hB / 2, yc;
                  if (TG.vertical()) {
                    /* taurus-v20: standing up, like his walls */
                    var uLoT = alongB - wB / 2, uHiT = alongB + wB / 2, uc;
                    for (uc = uLoT; uc < uHiT - 0.005; uc += TG.TG.w + TG.TG.gap) {
                      var bwT = Math.min(TG.TG.w, uHiT - uc);
                      if (bwT > 0.01) putOff(PART_BOARD.t, hB, bwT, uc + bwT / 2, yB2, tgM, offB);
                    }
                  } else {
                    for (yc = yLoT; yc < yHiT - 0.005; yc += TG.TG.w + TG.TG.gap) {
                      var bhT = Math.min(TG.TG.w, yHiT - yc);
                      if (bhT > 0.01) putOff(PART_BOARD.t, bhT, wB, alongB, yc + bhT / 2, tgM, offB);
                    }
                  }
                  _add(LAYER, TG.TG.code, wB * hB / TG.TG.w);
                  tally.board = (tally.board || 0) + wB * hB;
                  return;
                }
                /* a face into the sauna room is foil and lining, not board */
                if (HOT) HOT.faceY(y0, y0 + clear);
                if (HOT && HOT.step() >= 1 && HOT.at(vert ? (p.at + probe) : (lo + alongB), vert ? (lo + alongB) : (p.at + probe))) {
                  HOT.face(T, g, vert, p.at, lo, sgB, alongB, wB, yB2 - hB / 2, yB2 + hB / 2);
                  return;
                }
                /* the shed family: OSB on one face, nailed, no wet board, no tile */
                if (OSB) {
                  var KO = PART_BOARD.dry;
                  _asmMark(ctx, 'layer_osb', 'top', 'OSB sheathing', KO.code);
                  var bmO = new T.MeshStandardMaterial({
                    color: isNew(LAYER + '.board') ? MAGENTA : KO.col,
                    roughness: 0.92, metalness: 0.0 });
                  putOff(PART_BOARD.t, hB, wB, alongB, yB2, bmO, offB);
                  _add(LAYER, KO.code, wB * hB * OSB.waste);
                  tally.board = (tally.board || 0) + wB * hB;
                  return;
                }
                var K = wetB ? PART_BOARD.wet : PART_BOARD.dry;
                /* a wet face is tiled to WET_TILE_H and painted above it, so the
                   sheet is drawn in two: the tiled band unpainted, the rest in
                   the paint's white; both are the same board in the bill */
                var yLoS = yB2 - hB / 2, yHiS = yB2 + hB / 2;
                var tileTop = y0 + WET_TILE_H;
                /* the face this sheet is on, as a line, and whether a shower
                   stands against it: then the tiles go to the top */
                var qP = W(alongB - wB / 2, sgB * PART.d / 2), qQ = W(alongB + wB / 2, sgB * PART.d / 2);
                var fxP = qP[0], fzP = qP[1], fxQ = qQ[0], fzQ = qQ[1];
                if (wetB && _showerOnFace(fxP, fzP, fxQ, fzQ, 0.20)) { tileTop = yHiS; tally.showerFull = (tally.showerFull || 0) + 1; }
                var parts = [];
                if (wetB && yLoS < tileTop - 0.005) {
                  parts.push([yLoS, Math.min(yHiS, tileTop), true]);
                  if (yHiS > tileTop + 0.005) parts.push([tileTop, yHiS, false]);
                } else {
                  parts.push([yLoS, yHiS, false]);
                }
                var pq;
                for (pq = 0; pq < parts.length; pq++) {
                  var hP = parts[pq][1] - parts[pq][0];
                  if (!(hP > 0.005)) continue;
                  _asmMark(ctx, 'layer_plasterboard', 'top', 'Plasterboard', K.code);
                  var bmB = new T.MeshStandardMaterial({
                    color: isNew(LAYER + '.board') ? MAGENTA : _boardCol(K, parts[pq][2]),
                    roughness: 0.92, metalness: 0.0 });
                  putOff(PART_BOARD.t, hP, wB, alongB, (parts[pq][0] + parts[pq][1]) / 2, bmB, offB);
                  if (parts[pq][2]) {
                    tally.tiled = (tally.tiled || 0) + wB * hP;
                    /* and the tile on it, bed and tile, proud of the board; the
                       joints set out from the partition's start and the floor */
                    var tT = WALL_TILE.t + WALL_TILE.bed;
                    /* THE TILE ON A PARTITION SAYS IT IS A TILE.

                       The partition's tile band is drawn here and billed as
                       SBC00001, but it carried the plasterboard mark set a few
                       lines up -- so seven square metres of ceramic was paid for
                       with no body of its own anywhere in the model. The wall's
                       tile was fixed a version ago; this is its twin, and it
                       only showed once the wall stopped hiding it (Marek). */
                    _asmMark(ctx, 'wall_tile', 'top', 'Wall tile', WALL_TILE.code);
                    var mT = _tileMat(T, isNew(LAYER + '.tile'), wB, hP,
                                      alongB - wB / 2, parts[pq][0] - y0);
                    putOff(tT, hP, wB, alongB, (parts[pq][0] + parts[pq][1]) / 2, mT,
                           offB + sgB * (PART_BOARD.t / 2 + tT / 2));
                    /* and back to the board for whatever follows */
                    _asmMark(ctx, 'layer_plasterboard', 'top', 'Plasterboard', K.code);
                  }
                }
                _add(LAYER, K.code, wB * hB);
                tally.board = (tally.board || 0) + wB * hB;
                if (wetB) tally.boardWet = (tally.boardWet || 0) + wB * hB;
              }

              /* A RUN IS SPLIT WHERE THE ROOMS CHANGE, NOT ONLY AT THE DOORS.

                 The solid run from the bathroom door to the far end of the wall
                 crossed the line where the bathroom stops and the kitchen starts
                 -- the partition standing on it -- and one probe in its middle
                 answered for all of it: kitchen, plain. So the piece between the
                 door and that corner was plain on the bathroom side, every time
                 a door was put in. The log said it in one line: run -442..1453,
                 probe 505, KITCHEN.

                 The exterior wall has split its pieces at every room corner
                 since the day the board followed the rooms; this is that rule
                 brought to the partitions. Each run is cut at the room corners
                 that fall along it, every piece asks for itself, neighbours that
                 agree are joined, and where a wet piece meets a dry one the joint
                 is moved onto the WET face of the partition standing on that
                 line -- so no green shows next door and the joint is covered. */
              var rq6, RM6 = _roomRects();
              for (rq6 = 0; rq6 < runs5.length; rq6++) {
                var a6 = runs5[rq6][0], b6 = runs5[rq6][1];
                if (rq6 === 0 && a6 < 0.001) a6 += extLo;
                if (rq6 === runs5.length - 1 && b6 > len - 0.001) b6 += extHi;
                var w6 = b6 - a6;
                if (w6 < 0.03) continue;

                var brk6 = [a6, b6], q7, r7, c7;
                /* a slanted wall is split by its probes only (adu-v1410) */
                for (q7 = 0; !DG && q7 < RM6.length; q7++) {
                  var RS7 = RM6[q7].rects || (typeof RM6[q7].x0 === 'number' ? [RM6[q7]] : []);
                  for (r7 = 0; r7 < RS7.length; r7++) {
                    var RC7 = RS7[r7];
                    if (typeof RC7.x0 !== 'number') continue;
                    var cs7 = vert ? [RC7.z0, RC7.z1] : [RC7.x0, RC7.x1];
                    for (c7 = 0; c7 < 2; c7++) {
                      var u7 = cs7[c7] - lo;
                      if (u7 > a6 + 0.02 && u7 < b6 - 0.02) brk6.push(u7);
                    }
                  }
                }
                brk6.sort(function (p1, p2) { return p1 - p2; });

                var pcs6 = [];
                for (c7 = 0; c7 < brk6.length - 1; c7++) {
                  var s7 = brk6[c7], e7 = brk6[c7 + 1];
                  if (!(e7 - s7 > 0.02)) continue;
                  var m7 = (s7 + e7) / 2;
                  var wet7 = faceAt(m7);
                  var px7 = vert ? (p.at + probe) : (lo + m7);
                  var pz7 = vert ? (lo + m7) : (p.at + probe);
                  if (pcs6.length && pcs6[pcs6.length - 1].wet === wet7) {
                    pcs6[pcs6.length - 1].b = e7;
                  } else {
                    pcs6.push({ a: s7, b: e7, wet: wet7 });
                  }
                }

                /* the joint onto the wet face of the partition standing there */
                for (c7 = 0; c7 < pcs6.length - 1; c7++) {
                  if (pcs6[c7].wet === pcs6[c7 + 1].wet) continue;
                  var sh7 = pcs6[c7].wet ? -(PART.d / 2) : (PART.d / 2);
                  pcs6[c7].b += sh7;
                  pcs6[c7 + 1].a += sh7;
                }

                for (c7 = 0; c7 < pcs6.length; c7++) {
                  var wp = pcs6[c7].b - pcs6[c7].a;
                  if (!(wp > 0.03)) continue;
                  sheetAt(pcs6[c7].wet, wp, clear, (pcs6[c7].a + pcs6[c7].b) / 2, y0 + clear / 2);
                }
              }

              /* THE BOARD RUNS OVER THE HEADER, IT DOES NOT STOP AT IT.

                 This started at the TOP of the header, so between the door lining
                 and the bottom of the panel there was a 140 mm strip of nothing --
                 the gap over every door, which is what kept showing up.

                 On site nobody boards up to a header and patches above it. The
                 sheet covers the wall and a hole is cut for the DOOR. So the panel
                 starts at the top of the opening and the header is behind it,
                 which is also why the header never needed a joint over it. */
              for (var oq6 = 0; oq6 < holes.length; oq6++) {
                var Hb = holes[oq6];
                var yT6 = y0 + dhB, h6 = (y0 + clear) - yT6, ww6 = Hb.a1 - Hb.a0;
                if (h6 < 0.03 || ww6 < 0.03) {
                  ctx.log('partitions OVER-DOOR PANEL SKIPPED at ' +
                          Math.round((Hb.a0 + Hb.a1) / 2 * 1000) + ' mm -- ' +
                          Math.round(ww6 * 1000) + ' x ' + Math.round(h6 * 1000) +
                          ' mm, head ' + Math.round(clear * 1000) + ', opening ' +
                          Math.round(dhB * 1000));
                  continue;
                }
                sheetAt(faceAt((Hb.a0 + Hb.a1) / 2), ww6, h6, (Hb.a0 + Hb.a1) / 2, yT6 + h6 / 2);
                tally.overDoor = (tally.overDoor || 0) + 1;
              }
            }
          })();

          /* THE THRESHOLD OF AN INTERIOR DOOR IS A TRANSITION STRIP WHERE THE FLOOR
             CHANGES. An interior door has no saddle; its lining is jambs and a head
             and the floor runs through. Where the two rooms are finished
             differently -- tile on one side, vinyl on the other, 13 mm against 10
             -- a transition strip (ODD00003, by the metre, the door's width) takes
             the step and covers the cut edges. Where both sides are the same
             finish there is nothing to cover and nothing is billed (Marek: the
             threshold was missing entirely, drawing and bill). */
          if (!OSB) (function () {
            var q8, Hb8, mid8, px8, pz8, wetA8, wetB8;
            var probe8 = PART.d / 2 + (DG ? 0.08 : 0.25);
            for (q8 = 0; q8 < holes.length; q8++) {
              Hb8 = holes[q8];
              mid8 = (Hb8.a0 + Hb8.a1) / 2;
              var qA8 = W(mid8, probe8), qB8 = W(mid8, -probe8), qM8 = W(mid8, 0);
              px8 = qM8[0]; pz8 = qM8[1];
              wetA8 = _wetAt(qA8[0], qA8[1]);
              wetB8 = _wetAt(qB8[0], qB8[1]);
              if (wetA8 === wetB8) continue;
              var ww8 = Hb8.a1 - Hb8.a0;
              var mS = new T.MeshStandardMaterial({
                color: isNew(LAYER + '.threshold') ? MAGENTA : 0xB8B8B4,
                roughness: 0.4, metalness: 0.6 });
              var sm8 = new T.Mesh(new T.BoxGeometry(DOOR_STRIP.w, DOOR_STRIP.t, ww8), mS);
              sm8.position.set(px8, y0 + dLift + FINISH.wet.h + DOOR_STRIP.t / 2, pz8);
              if (ROTY) sm8.rotation.y = ROTY;
              sm8.name = 'door-transition';
              g.add(sm8);
              tally.stripM = (tally.stripM || 0) + ww8;
              tally.stripN = (tally.stripN || 0) + 1;
            }
          })();

          /* every doorway: two trimmers, a header over it, and the cripple above */
          for (var hq = 0; hq < holes.length; hq++) {
            var H0 = holes[hq];
            var dh = Math.min(DOOR_H_DEF, clear - 0.10 - dLift);
            if (dh < 1.2) continue;
            var hdr = 0.14;
            /* ranch-v59: the trimmers and the header are partition timber, not
               the board or the door whose mark was set last (RANCH) */
            if (H.timberMarks) _asmCode(ctx, PART.code, 'top');
            put(PART.d, clear, PART.t, H0.a0 - PART.t / 2, y0 + clear / 2);
            put(PART.d, clear, PART.t, H0.a1 + PART.t / 2, y0 + clear / 2);
            tally.studs += 2; tally.studLen += clear * 2;
            put(PART.d, hdr, (H0.a1 - H0.a0), (H0.a0 + H0.a1) / 2, y0 + dLift + dh + hdr / 2);   /* core-v817: over the door on the build-up */
            tally.plateLen += (H0.a1 - H0.a0);
            /* THE DOORWAY IS A DOOR, NOT A BOARD -- AND THE MARK GOES ABOVE IT.

               Same fault the shed had: the leaf and the lining were drawn while
               the mark still said whatever the boarding set, so tapping an
               interior door in the exploded view opened a sheet of board, with
               its name and its price. A mark names everything added after it,
               so it stands before the first body it belongs to. The unit is the
               same rule the bill uses below (Marek: to stejne udelat s adu). */
            try {
              var wmmM = Math.round(((H0.a1 - H0.a0) || 0.8) * 1000);
              var handM = (H0.hand === 'L') ? 1 : 2;
              var codeM = H0.pass ? PART_BOARD.dry.code : (HOT && H0.hot) ? HOT.doorCode : _intDoorUnit(H0).code;
              _asmMark(ctx, H0.pass ? 'door_lining' : 'interior_door', 'side',
                       H0.pass ? 'Door lining' : 'Interior door', codeM);
            } catch (eDm) {}
            /* a slanted wall's door is built in the wall's own frame and the
               whole set turned onto it (adu-v1410) */
            if (DG) {
              var gD = new T.Group();
              gD.position.set(DG.ax, 0, DG.az);
              gD.rotation.y = -DG.ang;
              _doorSet3D(T, gD, { at: 0 }, false, 0, H0, dh, y0 + dLift);
              g.add(gD);
            } else _doorSet3D(T, g, p, vert, lo, H0, dh, y0 + dLift);

          /* AND A VISIBLE LINING ROUND THE OPENING.

             The kind that wraps the wall rather than sitting in it: two faces and
             the reveal between them, in one white piece. Until now the opening was
             a hole with the studs showing at its edge, which is what a doorway
             looks like before anyone has finished it. */
          (function () {
            var lw = 0.060, lt = 0.018;
            var faceOff = PART.d / 2 + PART_BOARD.t + lt / 2;
            var lm = new T.MeshStandardMaterial({ color: 0xF4F2ED, roughness: 0.55, metalness: 0.0 });
            var sgL, sideL;
            for (sgL = -1; sgL <= 1; sgL += 2) {
              /* the two jambs on this face, and the head across them */
              for (sideL = 0; sideL < 2; sideL++) {
                var atL = sideL ? H0.a1 : H0.a0;
                putOff(lt, dh + lw, lw, atL + (sideL ? lw / 2 : -lw / 2),
                       y0 + (dh + lw) / 2, lm, sgL * faceOff);
              }
              putOff(lt, lw, (H0.a1 - H0.a0) + lw * 2, (H0.a0 + H0.a1) / 2,
                     y0 + dh + lw / 2, lm, sgL * faceOff);
            }
            /* the reveal, wrapping through the wall */
            for (sideL = 0; sideL < 2; sideL++) {
              var atR = sideL ? H0.a1 : H0.a0;
              putOff(PART.d + PART_BOARD.t * 2, dh, lt, atR + (sideL ? lt / 2 : -lt / 2),
                     y0 + dh / 2, lm, 0);
            }
            putOff(PART.d + PART_BOARD.t * 2, lt, (H0.a1 - H0.a0), (H0.a0 + H0.a1) / 2,
                   y0 + dh - lt / 2, lm, 0);
            tally.lining = (tally.lining || 0) + 2 * (dh * 2 + (H0.a1 - H0.a0));
          })();
            tally.doors++;
          }

          tally.walls++;
          tally.area += len * clear;
        }

        API.setPartThickness(PART.d); if (H.framed) H.framed();
        H.setTally(tally);
        _add(LAYER, PART.code, tally.studLen + tally.plateLen);
        /* the board is billed face by face above, by its own code -- this was one
           lump on a made-up key, which is what has been sitting in NO CODE */

        /* AND THE FIXINGS FOR IT, ON THE CEILING'S OWN RULE.

           A partition is boarded twice, so the screws are counted on the area
           that was actually put up -- tally.board is both faces added together,
           which is the same number the screws are bought against.

           The rates are the ceiling's, not a second opinion: 20 screws to the
           square metre, 3.6 m of tape for every 2.88 m2 board, and filler by the
           square metre. A partition is denser in joints than a ceiling because it
           is cut round every doorway, so if either number moves later it moves in
           one place and both layers follow. */
        if (tally.board && OSB) {
          /* THE SAME NAIL THE SHEATHING USES, at the same rate: the sheet is the
             same sheet and the stud is the same timber. No tape, no filler, no
             screws -- there is no plasterboard here to joint. */
          var pNail = Math.round(tally.board * 16);
          _add(LAYER, PART_BOARD.nail, pNail);
          ctx.log('FIX  partitions board ' + Math.round(tally.board * 10) / 10 +
                  ' m2 -> ' + pNail + ' x ' + PART_BOARD.nail);
        } else if (tally.board) {
          _add(LAYER, PART_BOARD.screw, Math.round(tally.board * 20));
          _add(LAYER, PART_BOARD.tape,
               Math.round(tally.board / 2.88 * 3.6 * 10) / 10);
          _add(LAYER, PART_BOARD.filler, Math.round(tally.board * FILLER_KG_M2 * 10) / 10);
          ctx.log('FIX  partitions board ' + Math.round(tally.board * 10) / 10 +
                  ' m2 -> ' + Math.round(tally.board * 20) + ' x ' + PART_BOARD.screw +
                  ', ' + (Math.round(tally.board / 2.88 * 3.6 * 10) / 10) + ' m ' +
                  PART_BOARD.tape + ', ' + Math.round(tally.board * 10) / 10 + ' m2 ' +
                  PART_BOARD.filler + ' -- same rates as the ceiling');
        } else {
          ctx.log('FIX  partitions no board put up, so no fixings');
        }

        ctx.log('partitions run between the INNER faces, ' + Math.round(wt * 1000) +
                ' mm in from the outline at each end -- that is the piece somebody cuts');
        ctx.log('partitions ' + tally.doors + ' doorway(s) framed -- trimmers, header, ' +
                'lining and a leaf standing ajar' +
                (isNew(LAYER) ? '   <-- MAGENTA this version' : ' (white)') +
                (tally.doors ? '' : ' (none: no door sits on a partition yet)'));
        if (!tally.wool && !OSB) {
          ctx.log('partitions NO WOOL -- ' + (tally.walls ? 'the bays came out empty'
                                                          : 'there are no partitions to fill'));
        }
        if (tally.wool) {
          ctx.log('partitions wool ' + API.fmtArea(tally.wool) + ' of ' +
                  Math.round(PART_WOOL.t * 1000) + ' mm in ' + tally.woolBays + ' bay(s) -- ' +
                  'acoustic, not thermal: both sides are the same room temperature, and an ' +
                  'empty stud bay is a drum with a skin on each face');
          _why(ctx, 'partitions bays are bounded by whatever is SOLID -- a stud OR a door ' +
                  'jamb -- so the strips beside a doorway are filled like any other wall, ' +
                  'and the panel OVER each door is filled too: it is the shortest path ' +
                  'from one room to the next');
          ctx.log('partitions wool is ' + Math.round(PART_WOOL.t * 1000) + ' in a ' +
                  Math.round(PART.d * 1000) + ' stud, so it is not pressed against both boards -- ' +
                  'wool touching each face carries sound across instead of stopping it');
        }
        if (SEP && SEP.kind === 'ranch' && SEP.on(null, 0)) {
          ctx.log('GAR  house-garage partition(s): ' + (tally.sepWool
            ? (API.fmtArea(tally.sepWool) + ' of ' + SEP.wool + ' ' + Math.round(SEP.woolT * 1000) + ' mm friction-fitted into the ' + Math.round(PART.d * 1000) +
               ' mm stud in ' + tally.sepBays + ' bay(s) [' + (tally.sepWalls || []).join(', ') + '] -- the thermal line, instead of the acoustic ' + Math.round(PART_WOOL.t * 1000) +
               ' mm; 1/2 in. gypsum both faces as every partition (IRC R302.6)' +
               (tally.sepDoors ? (' | ' + tally.sepDoors + ' door(s) in it: the 20-minute rated ODI09008/9 (ranch-v38)') : ''))
            : 'none -- no partition has the garage on one side and a heated room on the other') + ' (ranch-v34)');
        }
        if (SEP && SEP.kind === 'ga' && (tally.sepWool || tally.sepDoors)) {
          ctx.log('GA hall  garage / dwelling partition(s): ' + API.fmtArea(tally.sepWool || 0) + ' of ' + SEP.wool + ' ' + Math.round(SEP.woolT * 1000) + ' mm friction-fitted into the ' + Math.round(PART.d * 1000) +
                  ' mm stud in ' + (tally.sepBays || 0) + ' bay(s) [' + (tally.sepWalls || []).join(', ') + '] -- the thermal line, instead of the acoustic ' + Math.round(PART_WOOL.t * 1000) +
                  ' mm; 1/2 in. gypsum both faces as every partition (IRC R302.6)' + (tally.sepDoors ? (' | ' + tally.sepDoors + ' door(s) in it: the 20-minute rated ODI09008/9') : ''));
        }
        ctx.log('partitions ' + tally.walls + ' wall(s), ' + tally.studs + ' studs at ' +
                API.fmtLen(grid) + ' oc, ' + Math.round(PART.t * 1000) + 'x' +
                Math.round(PART.d * 1000) + ' -- lighter than the shell and no sheathing, ' +
                'because nothing lands on them');
        if (tally.board) {
          if (tally.lining) {
            ctx.log('partitions lining ' + Math.round(tally.lining * 10) / 10 + ' m -- the wrapping ' +
                    'kind: two faces and the reveal in one white piece, so the opening is ' +
                    'finished rather than a hole with the studs showing');
          }
          ctx.log('partitions ' + (tally.overDoor || 0) + ' over-door panel(s) boarded from the ' +
                  'top of the OPENING to the head -- over the header, not starting above it, ' +
                  'which is where the strip of bare frame kept coming from');
          if (!OSB && H.paintAdd) H.paintAdd((tally.board || 0) - (tally.tiled || 0));
          if (tally.tiled) {
            _wallTileBill(LAYER, tally.tiled, ctx, 'partitions wet faces' +
                          (tally.showerFull ? (' (' + tally.showerFull + ' sheet(s) tiled to the top behind a shower)') : ''));
          }
          if (HOT) { try { HOT.partBill(ctx); } catch (eHP) { ctx.log('hot        !! partition bill threw: ' + (eHP && eHP.message)); } }
          /* AND THE DOOR ITSELF. A doorway that is not a plain pass-through gets
             a unit: the leaf by its width, the hand by which way it swings, with
             its handle set and three hinges. A pass-through (no leaf) gets only
             the lining, which is what it was getting before -- every interior
             door in the house was drawn and never billed (Marek). */
          /* A SLIDER IS NOT HUNG ON HINGES (adu-v1403, Marek: everything in the
             model and what fixes it, into the bill). A hinged leaf gets its
             handle set and three hinges as before. A one-leaf slider gets a wall
             track kit for its width and a flush cup pull; two leaves parting in
             the middle get the double track kit and a cup pull each. Neither
             has a handle set or a hinge. The units are the catalogue's own:
             ODD00010-12 single track by leaf 600/800/900, ODD00013-15 double
             by opening 1220/1520/1830, ODD00019 the cup pull. */
          (function () {
            var byCode = {}, dq, D9, hinged = 0, extra = {}, saunaDoors = 0, saunaNotes = [];
            function more(c, n) { extra[c] = (extra[c] || 0) + n; }
            var kinds = {};
            for (dq = 0; dq < (_partDoorList || []).length; dq++) {
              D9 = _partDoorList[dq];
              if (D9.pass) continue;
              /* INTO THE SAUNA ROOM: the sauna door and its handle set, from the
                 catalogue, whatever width was drawn (Marek) */
              if (HOT && D9.hot) {
                saunaDoors++;
                saunaNotes.push(Math.round((D9.w || 0.8) * 1000) + ' mm drawn' + (Math.abs(Math.round((D9.w || 0.8) * 1000) - 750) > 20 ? ' (the unit is 750)' : ''));
                continue;
              }
              var wmm = Math.round((D9.w || 0.8) * 1000), UN = _intDoorUnit({ a0: 0, a1: D9.w || 0.8, kind: D9.kind, hand: D9.hand, slide: D9.slide, fire: D9.fire });
              byCode[UN.code] = (byCode[UN.code] || 0) + UN.n;
              /* SAUNA / the shed family: no unit table and no track kits in
                 their catalogue -- every leaf hinged, as their own copies billed */
              if (!H.intDoorUnit) { hinged++; continue; }
              /* THE CLOSET DOORS AND WHAT HOLDS THEM (adu-v1404):
                   pair   -- six hinges, a dummy knob on each leaf, a ball catch
                             and a flush bolt on the leaf that stays shut
                   bifold -- a dummy knob on each unit, an aligner where two
                             units meet; the track and pivots come in the unit
                   bypass -- its track kit by width and a cup pull on each leaf */
              if (D9.kind === 'pair') {
                more('ODD00005', 6); more('ODD00016', 2); more('ODD00017', 1); more('ODD00018', 1);
                kinds.pair = (kinds.pair || 0) + 1;
              } else if (D9.kind === 'bifold') {
                more('ODD00016', UN.n);
                if (UN.n === 2) more('ODD00020', 1);
                kinds.bifold = (kinds.bifold || 0) + 1;
              } else if (D9.kind === 'bypass') {
                more((wmm >= 1680) ? 'ODD00009' : (wmm >= 1370) ? 'ODD00008' : 'ODD00007', 1);
                more('ODD00019', 2);
                kinds.bypass = (kinds.bypass || 0) + 1;
              } else if (D9.slide === 3) {
                more((wmm >= 1680) ? 'ODD00015' : (wmm >= 1370) ? 'ODD00014' : 'ODD00013', 1);
                more('ODD00019', 2);
              } else if (D9.slide) {
                more((wmm >= 850) ? 'ODD00012' : (wmm >= 700) ? 'ODD00011' : 'ODD00010', 1);
                more('ODD00019', 1);
              } else if (UN.fire) {
                kinds.fireRated = (kinds.fireRated || 0) + 1;   /* ranch-v38, barndo-v19, garageapt-v13: its closer hinges and lockset come with it */
              } else {
                hinged++;
              }
            }
            var kq, doors = 0;
            /* barndo-v26 / garageapt-v29: the doorways' casing */
            if (CASE) {
              var CT = CASE.tally(), CC = CASE.CASE;
              if (CT.len > 0) {
                _add(LAYER, CC.code, Math.round(CT.len * 100) / 100);
                _add(LAYER, CC.nail, CC.rows * Math.ceil(CT.len / CC.step));
                ctx.log('PART casing (' + CASE.tag + ') ' + CT.n + ' doorway(s) cased both faces, 2 1/4 in., ' + (Math.round(CT.len * 10) / 10) + ' m ' + CC.code + ', ' +
                        (CC.rows * Math.ceil(CT.len / CC.step)) + ' x ' + CC.nail + ' (two rows every 12 in.)');
              }
            }
            if (saunaDoors) {
              _add(LAYER, HOT.doorCode, saunaDoors);
              _add(LAYER, HOT.handleCode, saunaDoors);
              ctx.log('hot        ' + saunaDoors + ' door(s) into the sauna room billed as the sauna door ' + HOT.doorCode + ' + handle set ' + HOT.handleCode + ': ' + saunaNotes.join(', '));
            } else if (HOT) {
              ctx.log('hot        no door in a partition of the sauna room');
            }
            for (kq in byCode) if (byCode.hasOwnProperty(kq)) { _add(LAYER, kq, byCode[kq]); doors += byCode[kq]; }
            for (kq in extra) if (extra.hasOwnProperty(kq)) _add(LAYER, kq, extra[kq]);
            if (hinged) {
              _add(LAYER, 'ODD00001', hinged);
              _add(LAYER, 'ODD00005', hinged * 3);
            }
            if (doors) {
              ctx.log('PART ' + doors + ' interior door unit(s) billed: ' + Object.keys(byCode).map(function (c) { return c + ' x' + byCode[c]; }).join(', ') +
                      (hinged ? (' | ' + hinged + ' hinged: handles ODD00001 and hinges ODD00005 x3') : '') +
                      (Object.keys(kinds).length ? (' | closet doors: ' + Object.keys(kinds).map(function (c) { return c + ' x' + kinds[c]; }).join(', ')) : '') +
                      (Object.keys(extra).length ? (' | hardware: ' + Object.keys(extra).map(function (c) { return c + ' x' + extra[c]; }).join(', ')) : ''));
            } else ctx.log('PART interior doors: none with a leaf (pass-throughs are lining only)');
          })();
          /* the frame is nailed: every stud through the plates, both ends, two nails each */
          if (tally.studs) _add(LAYER, FRAME_NAIL.code, tally.studs * FRAME_NAIL.perStudEnd * FRAME_NAIL.ends);
          if (tally.stripM) {
            _add(LAYER, DOOR_STRIP.code, tally.stripM);
            ctx.log('partitions thresholds ' + tally.stripN + ' transition strip(s), ' +
                    (Math.round(tally.stripM * 100) / 100) + ' m of ' + DOOR_STRIP.code +
                    ' -- at every interior door where the floor changes (tile one side, vinyl ' +
                    'the other); doors between two rooms with the same floor get none');
          }
          if (OSB) ctx.log('partitions board ' + API.fmtArea(tally.board) +
                  ((TG && TG.on()) ? (' of Matt\'s T&G ' + TG.TG.code + ' (' + Math.round(tally.board / TG.TG.w * 10) / 10 + ' m)') : ' of OSB') +
                  ' on ONE face -- the other side is open studs');
          else ctx.log('partitions board ' + API.fmtArea(tally.board - (tally.boardWet || 0)) +
                  ' plain' + (tally.boardWet
                    ? (' and ' + API.fmtArea(tally.boardWet) + ' MOISTURE-RESISTANT on the wet ' +
                       'side(s), drawn green')
                    : ' -- no face looks into a wet room') +
                  ' -- face by face, because the two sides of one partition are two rooms');
        }
        _why(ctx, 'partitions single top plate: an exterior panel doubles it to lap the ' +
                'joints and carry the roof, and neither applies inside');
        ctx.log('partitions ' + (OSB ? (API.fmtArea(tally.area) + ' of board, one face') : (API.fmtArea(tally.area * 2) + ' of board, both faces')));
        /* barndo-v26 / garageapt-v29: the closets */
        if (H.closets) { try { H.closets(ctx); } catch (eCl) { ctx.log('PART closets THREW ' + ((eCl && eCl.message) || eCl)); } }
    }

    /* =========================================================================
       THE WALK-THROUGH'S LEAVES (core-v783, with the joinery module). What
       moves in the AR walk-through is said on the mesh itself: userData.arLeaf
       is a hinge or a track, and the leaf is drawn exactly where it was -- on
       screen and in AR nothing changes. The products' own copies were the same
       text word for word; the shared joinery and door set use these.
         Swing: hinge (x, z); c the way from the hinge to the far edge with the
         leaf shut; s the side it swings onto. A turn about +Y by a positive
         angle carries c towards (c.z, -c.x), so that is the sign that opens.
         Meshes sharing one arLeaf object move as one (a leaf, its handle and
         hinges).
       ========================================================================= */
    function _arSwing(m, hx, hz, cx, cz, sx, sz, ajar, ext, ox, oz) {
      if (!m) return null;
      var L = { type: 'swing', hinge: [hx, 0, hz], dir: ((sx * cz - sz * cx) >= 0) ? 1 : -1, ajar: ajar || 0, max: 1.45 };
      if (ext) { L.ext = true; L.out = [ox, 0, oz]; }
      m.userData.arLeaf = L;
      return L;
    }
    /* Slide: (ux, uz) the way it opens, at how far it is drawn open, travel
       fully open; (shx, shz) a step aside before it runs (an HS sash lifts
       and clears the fixed pane). */
    function _arSlide(m, ux, uz, at, travel, shx, shz, ext, ox, oz) {
      if (!m) return null;
      var L = { type: 'slide', dir: [ux, 0, uz], at: at || 0, travel: travel || 0 };
      if (shx || shz) L.shift = [shx || 0, 0, shz || 0];
      if (ext) { L.ext = true; L.out = [ox, 0, oz]; }
      m.userData.arLeaf = L;
      return L;
    }
    function _arWith(m, L) { if (m && L) m.userData.arLeaf = L; return m; }
    /* the unit's name from the offer by its code: a record placed from the
       picker carries the code and not the name (HS sliding door vs French) */
    /* core-v786: is this unit sold by the square metre? (the offer's m2 flag) */
    function _unitM2(o) {
      try {
        var U = window.ConfiguratorUI, G = (U && U.openingGroup) ? U.openingGroup(o.kind) : null, i;
        for (i = 0; G && G.items && i < G.items.length; i++) if (G.items[i].code === o.code) return !!G.items[i].m2;
      } catch (eM2) {}
      return false;
    }
    function _unitName(o) {
      if (o && o.name) return String(o.name);
      try {
        var U = window.ConfiguratorUI, G = (U && U.openingGroup) ? U.openingGroup(o.kind) : null, i;
        for (i = 0; G && G.items && i < G.items.length; i++) if (G.items[i].code === o.code) return String(G.items[i].name || '');
      } catch (eUN) {}
      return '';
    }

    /* =========================================================================
       THE JOINERY MODULE (core-v783, the seventh shared module of the house
       line, its first part -- status/modules.md 7a). The windows and the
       exterior doors written once: each unit in its clear opening with the
       fitting gap, its frame (a door's threshold instead of a bottom rail),
       its leaves, panes and mullions, what moves in the walk-through, its
       grab box for picking and the red wash when selected, the edit-mode
       gold, and the bill -- each unit against its code, the door's handle
       set, and what fixes a unit into its hole (anchors, foam, tape both
       faces, the head flashing). The ADU's text with the products' own
       parts behind hooks.

         API.houseJoinery(ctx, {
           api, layer ('adu-joinery'), MAGENTA, JOINERY, JOIN_FIX, frameCol,
           leafCol, glassCol, accent, opEdit, billReset, isNew, add,
           setTally, atSeen (the copies with the seat log)
           garage: { opener, kit, say } -- RANCH / BARNDO / GA / ANY HOUSE
           gaFall  GA / ANY HOUSE, hotAt SAUNA, after GA / ANY HOUSE
           shed:   { openable, bar, pane } -- the shed family's drawing and
                   ironmongery
           plank:  { isPortal, isPlank, draw, TG, tag } -- TAURUS / GARDEN BAR
         })

       Carried: the overhead door drawn as sections (barndo-v13) to the ADU,
       CABIN and BUNKER, which glazed it as a slider -- so its posts no
       longer count in the joint the fixings follow; the double door drawn
       as two leaves with the astragal (adu-v1416) to SAUNA. Not carried
       (candidates): the hinges billed with a hinged exterior door (the shed
       family's ODD00005) for the house line; the shed family's door and
       window drawing (handles, hinges, the pane grid) for the house line.
       ========================================================================= */
    function houseJoinery(ctx, H) {
      /* the product's own pieces, under the names the text has always used */
      var API = H.api, LAYER = H.layer, MAGENTA = H.MAGENTA, JOINERY = H.JOINERY, JOIN_FIX = H.JOIN_FIX;
      var _billReset = H.billReset, _add = function (cat, code, q) { H.add(code, q); }, isNew = H.isNew;
      /* the products' own joinery, each behind its hook (status/modules.md 7a):
         GAR    RANCH / BARNDO / GA / ANY HOUSE -- the overhead door's opener:
                a code per door, RANCH's garage kit, GA's line in the log
         SHED   the shed family -- the leaf in the joinery colour, handles and
                hinges on the leaf, a sectional door's ribs, the WIND pane
                grid, the openable sash, the hinges billed with the door
         PLANK  TAURUS / GARDEN BAR -- the portal is a hole, Matt's plank door
         gaFall GA / ANY HOUSE -- the overhead door's seal down to the fall
         hotAt  SAUNA -- a window in the sauna room is one pane
         after  GA / ANY HOUSE -- the canopy, the rooms' light, the brace scan */
      var GAR = H.garage || null, SHED = H.shed || null, PLANK = H.plank || null;
      var WIN_OPENABLE = SHED ? SHED.openable : 0, WIN_BAR = SHED ? SHED.bar : null, WIN_PANE = SHED ? SHED.pane : 0;
        var _joinChk = [], _joinSeat = [];
        var T = ctx.THREE, g = ctx.group, SEG = ctx.seg, i, q;
        var frameM = new T.MeshStandardMaterial({ color: H.frameCol, roughness: 0.7, metalness: 0.0 });
        /* reassigned below when edit mode is on */
        /* the shed family: THE LEAF TAKES THE JOINERY COLOUR TOO (Marek), a hair
           darker, so a panel still reads as a panel inside its frame */
        var leafM  = new T.MeshStandardMaterial({ color: H.leafCol, roughness: 0.8, metalness: 0.0 });
        /* YOU LOOK THROUGH A WINDOW. The pane was half milky, which in AR read as
           a solid panel; it goes out see-through, and the AR pass carries the same
           opacity rather than its own cap (Marek). */
        var glassM = new T.MeshStandardMaterial({
          color: H.glassCol, roughness: 0.05, metalness: 0.0,
          transparent: true, opacity: 0.18, depthWrite: false, side: T.DoubleSide
        });

        /* IN EDIT MODE THE UNITS SAY THEY ARE THE THING BEING EDITED.

           Without it the mode is invisible: the drawer has gone, the building looks
           exactly as it did, and the only way to find out whether dragging will do
           anything is to try it on something. Colouring what responds turns the
           question into a look.

           The FRAME carries it rather than the glass -- the frame is the outline of
           the unit, so a tinted frame reads as "this whole thing is selectable"
           where tinted glass just reads as different glazing. */
        var editing_ = (typeof H.opEdit === 'function') ? H.opEdit() : false;
        if (editing_) {
          frameM = new T.MeshStandardMaterial({
            color: H.accent, roughness: 0.55, metalness: 0.0,
            emissive: H.accent, emissiveIntensity: 0.18
          });
        }

        /* core-v798: A UNIT OF ITS OWN COLOUR (RUSSWORX SHED, his white roll-up
           door among black windows): H.colOf(o) -> a colour for that unit's
           leaf and frame, or null for the joinery colour. No hook, nothing
           changes. */
        var leafM0 = leafM, frameM0 = frameM, _colMats = {};
        _billReset(LAYER);
        if (H.atSeen) setTimeout(function () {
          try {
            if (_joinSeat.length) ctx.log('frame seat ' + _joinSeat.join('  ||  ') + ' mm');
          } catch (eJL) {}
        }, 0);
        var tally = { units: 0, glassArea: 0, frameLen: 0, mullions: 0, byKind: {} };

        for (i = 0; i < SEG.length; i++) {
          var s = SEG[i];
          var ops = s.openings || [];
          if (!ops.length) continue;
          var ang = Math.atan2(s.x1 - s.x0, s.z1 - s.z0);
          var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;

          for (q = 0; q < ops.length; q++) {
            var o = ops[q];
            if (H.colOf) {
              var cO = null;
              try { cO = H.colOf(o); } catch (eCO) { cO = null; }
              if (typeof cO === 'number') {
                if (!_colMats[cO]) _colMats[cO] = { l: new T.MeshStandardMaterial({ color: cO, roughness: 0.8, metalness: 0.0 }),
                                                     f: new T.MeshStandardMaterial({ color: cO, roughness: 0.7, metalness: 0.0 }) };
                leafM = _colMats[cO].l; frameM = editing_ ? frameM0 : _colMats[cO].f;
              } else { leafM = leafM0; frameM = frameM0; }
            }
            /* TAURUS: the portal is a hole, not a unit -- nothing in it to draw
               or to buy (taurus-v1) */
            if (PLANK && PLANK.isPortal(o)) { tally.portals = (tally.portals || 0) + 1; continue; }
            var w = o.w - JOINERY.tol * 2;
            /* WHAT THE WINDOW IS ACTUALLY DRAWN AS, MEASURED HERE.

               The opening's stored position sits on a stud -- the red stud proves
               it -- and there is still a gap on screen, so the gap is made
               somewhere between that number and this body. Two figures settle it:
               the edge of the piece as drawn, and the face of the stud it is meant
               to touch. If the edges differ by the fitting tolerance the drawing
               is right and the gap is the 10 mm a window is fitted with; if they
               differ by more, this body is not being drawn from the number that
               was snapped (Marek: je tam proste mezera). */
            try {
              var gL = API.grid(), tS = 0.06;
              var lo9 = tS / 2, hi9 = s.len - tS / 2, sp9 = hi9 - lo9;
              var n9 = Math.max(1, Math.round(sp9 / gL)), st9 = sp9 / n9, q9;
              var near9 = null, nd9 = 1e9;
              for (q9 = 0; q9 <= n9; q9++) {
                var c9 = lo9 + st9 * q9;
                var d9 = Math.min(Math.abs((o.at - o.w / 2) - (c9 + tS / 2)),
                                  Math.abs((o.at + o.w / 2) - (c9 - tS / 2)));
                if (d9 < nd9) { nd9 = d9; near9 = c9; }
              }
              _joinChk.push(o.kind + ' w' + Math.round(w * 1000) + ' drawn');
            } catch (eJC) {}
            var h = o.h - JOINERY.tol * 2;
            if (w < 0.2 || h < 0.2) continue;
            /* the unit is its own item, and it comes in from the OUTSIDE of its wall */
            _asmMark(ctx, 'windows_doors', 'side', 'Windows & doors', o.code || '');

            var cy = s.y0 + o.sill + o.h / 2;
            /* THE UNIT IS PUT AGAINST THE STUD, BY THE THING THAT DRAWS IT.

               Everything needed has been here all along: the stud lines are the
               wall's own layout, and the width is the one this body is built at.
               The position was taken from the stored centre instead, and that
               centre has been through a snap in another file which turned out to
               work on a different number -- so the unit landed near a stud and
               never on it, whatever either side did.

               So the centre is worked out here, from those two facts, and the
               frame's own edge is brought onto the face of the nearest stud. No
               tolerance, no offset, no second opinion (Marek: vis kde jsou
               svislice, znas vnejsi rozmer okna). */
            /* THE PRODUCT DRAWS THE UNIT WHERE THE CORE PUT IT.

               This worked the seat out a second time, from the frame's width, and
               it pulled every unit a few millimetres to the LEFT -- so whatever
               the core decided, the drawing ended up on the same side every time
               and a door dragged to the right came back. The core seats the frame
               against a stud now; here there is nothing left to correct (Marek:
               jsou blokovane svym primknutim doleva, musis to odblokovat). */
            var atUse = o.at, _atSeen = H.atSeen ? H.atSeen() : null;
            if (_atSeen) try {
              var seen9 = null, qs9;
              for (qs9 = 0; qs9 < _atSeen.length; qs9++) {
                if (_atSeen[qs9].o === o) { seen9 = _atSeen[qs9].at; break; }
              }
              _joinSeat.push('W' + i + ' ' + o.kind + ' drawn at ' + Math.round(atUse * 1000) +
                             (seen9 === null ? ', stud pass never saw it' :
                              (Math.abs(seen9 - atUse) < 0.0005 ? ', same as stud pass' :
                               ', STUD PASS SAW ' + Math.round(seen9 * 1000) + ' -- moved in between')));
            } catch (eJS) {}

            var px = s.x0 + ux * atUse - s.nx * (API.wallThickness() / 2);
            var pz = s.z0 + uz * atUse - s.nz * (API.wallThickness() / 2);

            function put(bw, bh, bd, dx, dy, mat) {
              var m = new T.Mesh(new T.BoxGeometry(bd, bh, bw), mat);
              m.position.set(px + ux * dx, cy + dy, pz + uz * dx);
              m.rotation.y = ang;
              g.add(m);
              return m;
            }
            /* the same piece, stood off the wall face: a handle, a hinge and a
               rib all sit ON the leaf rather than inside it */
            function putN(bw, bh, bd, dx, dy, mat, nOff) {
              var m = new T.Mesh(new T.BoxGeometry(bd, bh, bw), mat);
              m.position.set(px + ux * dx + s.nx * nOff, cy + dy, pz + uz * dx + s.nz * nOff);
              m.rotation.y = ang;
              g.add(m);
              return m;
            }
            /* a height ABOVE THE FLOOR of this wall, as an offset from the unit's
               centre -- the one conversion every piece of hardware needs */
            function atY(y) { return y - o.sill - o.h / 2; }
            /* the walk-through's leaves (adu-v1431): s.n points out of the
               house; a door opens in unless it was set to open out. The hand is
               read from outside looking in: L hangs it on the left jamb. */
            var inX = o.out ? s.nx : -s.nx, inZ = o.out ? s.nz : -s.nz;
            var hsHand = ((-s.nz * ux + s.nx * uz) >= 0 ? 1 : -1) * ((o.hand === 'L') ? 1 : -1);
            function swingAt(m, along, hs) {
              var hx = px + ux * along, hz = pz + uz * along;
              return _arSwing(m, hx, hz, -ux * hs, -uz * hs, inX, inZ, 0, true, s.nx, s.nz);
            }

            /* THE UNIT'S OWN GRAB BOX, WHICH IS HOW TINY PICKS ONE.

               Everything so far asked openingNear(x, z) -- how close the tap is to
               the opening MEASURED IN PLAN. That is a guess dressed as a test: it
               cannot tell the glass from the wall behind it, it has no idea how
               high up the finger is, and every reach it is given is either too
               small to hit or big enough to grab the wrong one.

               Tiny raycasts the unit itself and reads the index off what it hit.
               So each unit gets an invisible box the size of the hole, tagged, and
               picking becomes a fact rather than a distance. */
            var grab = new T.Mesh(new T.BoxGeometry(JOINERY.depth + 0.12, h, w),
                                  new T.MeshBasicMaterial({ visible: false }));
            grab.position.set(px, cy, pz);
            grab.rotation.y = ang;
            grab.userData.opAt = o.at;
            grab.userData.opSeg = s.id;
            /* NOT IN AR. The box is invisible because its MATERIAL is, which the
               renderer honours and the exporter does not -- so every opening went
               to AR as a solid pale box over the frame and the glass (Marek). */
            grab.userData.arSkip = true;
            /* the red wash over the selected unit */
            if (__CORE._opSel && __CORE._opSel.seg === s.id && Math.abs(__CORE._opSel.at - o.at) < 0.01) {
              var selM = new T.MeshBasicMaterial({ color: 0xE03A3A, transparent: true, opacity: 0.10, depthWrite: false, side: T.DoubleSide });
              var sel = new T.Mesh(new T.BoxGeometry(JOINERY.depth + 0.10, h, w), selM);
              sel.position.set(px, cy, pz);
              sel.rotation.y = ang;
              sel.name = 'opening-selected';
              sel.userData.arSkip = true;
              g.add(sel);
            }
            g.add(grab);

            /* taurus-v20: Matt's plank door -- its own leaves, no unit frame */
            if (PLANK && PLANK.isPlank(o)) {
              _asmMark(ctx, 'windows_doors', 'side', 'Windows & doors', PLANK.TG.board);
              PLANK.draw(T, g, s, o, px, pz, ang, ux, uz, cy, tally);
              tally.units++;
              tally.byKind[o.kind] = (tally.byKind[o.kind] || 0) + 1;
              continue;
            }

            /* core-v809 (TWO STORY thread, Marek 2026-11-03: "Nema dum v Rohatci
               i kulate okno?"): A ROUND LIGHT IS DRAWN ROUND on the house line too.
               The catalogue's OWC units came out as a square frame here; this is
               buildOpenings' masonry text carried over: the frame a ring the
               unit's depth, one disc of glass, and the four corners between the
               square rough opening and the circle closed by the wall from its
               inner face to the finished facade (the wall's shade, then the
               facade's). No sills (the wall kit passes it by), the joint the
               circle. Billed by the piece on its own row as before. */
            if (_roundOp(o)) {
              var RrJ = Math.min(w, h) / 2, rInJ = Math.max(0.05, RrJ - JOINERY.frame), dJ = JOINERY.depth / 2;
              var qnJ = new T.Quaternion().setFromUnitVectors(new T.Vector3(0, 1, 0), new T.Vector3(s.nx, 0, s.nz));
              var profJ = [new T.Vector2(rInJ, -dJ), new T.Vector2(RrJ, -dJ), new T.Vector2(RrJ, dJ), new T.Vector2(rInJ, dJ), new T.Vector2(rInJ, -dJ)];
              var ringJ = new T.Mesh(new T.LatheGeometry(profJ, 48), frameM);
              ringJ.position.set(px, cy, pz); ringJ.quaternion.copy(qnJ); g.add(ringJ);
              var paneJ = new T.Mesh(new T.CylinderGeometry(rInJ + 0.005, rInJ + 0.005, JOINERY.glass || 0.012, 48), glassM);
              paneJ.position.set(px, cy, pz); paneJ.quaternion.copy(qnJ); g.add(paneJ);
              var wtJ = API.wallThickness(), ftJ = 0, colFJ = 0xD9D6CC;
              try { ftJ = API.facadeThk() || 0; } catch (eFJ) { ftJ = 0; }
              try { colFJ = (cladKind() === 'off') ? colorSlotOf('facade') : colorSlotOf('timber'); } catch (eCJ) {}
              var W2J = o.w / 2, yLoJ = s.y0 + o.sill, yHiJ = yLoJ + o.h, cyJ = (yLoJ + yHiJ) / 2, qdJ, k2J;
              var prismJ = function (pts, o0, o1, col) {
                var sh = new T.Shape(), ar = 0, k3;
                for (k3 = 0; k3 < pts.length; k3++) { var p1 = pts[k3], p2 = pts[(k3 + 1) % pts.length]; ar += p1[0] * p2[1] - p2[0] * p1[1]; }
                var P2 = (ar < 0) ? pts.slice().reverse() : pts;
                for (k3 = 0; k3 < P2.length; k3++) { if (k3 === 0) sh.moveTo(P2[k3][0], P2[k3][1]); else sh.lineTo(P2[k3][0], P2[k3][1]); }
                var geo = new T.ExtrudeGeometry(sh, { depth: o1 - o0, bevelEnabled: false });
                /* a right-handed basis: s.n is the OUTWARD normal here (the masonry
                   module's is inward), so the run turns round -- a mirrored basis
                   lit the corners from behind (grey); the four corners are
                   symmetric, so the set is the same */
                var uJ = new T.Vector3(ux, 0, uz), nJ = new T.Vector3(s.nx, 0, s.nz);
                if (new T.Vector3().crossVectors(uJ, new T.Vector3(0, 1, 0)).dot(nJ) < 0) uJ.negate();
                var Mx = new T.Matrix4().makeBasis(uJ, new T.Vector3(0, 1, 0), nJ);
                Mx.setPosition(px + s.nx * o0, 0, pz + s.nz * o0);
                geo.applyMatrix4(Mx);
                g.add(new T.Mesh(geo, new T.MeshStandardMaterial({ color: col, roughness: 0.9, metalness: 0.0, side: T.DoubleSide })));
              };
              for (qdJ = 0; qdJ < 4; qdJ++) {
                var sxJ = (qdJ === 0 || qdJ === 3) ? 1 : -1, syJ = (qdJ < 2) ? 1 : -1, yEJ = (syJ > 0) ? yHiJ : yLoJ;
                var ptsJ = [[sxJ * RrJ, cyJ], [sxJ * W2J, cyJ], [sxJ * W2J, yEJ], [0, yEJ], [0, cyJ + syJ * RrJ]];
                for (k2J = 1; k2J < 16; k2J++) { var anJ = (Math.PI / 2) * (1 - k2J / 16); ptsJ.push([sxJ * RrJ * Math.cos(anJ), cyJ + syJ * RrJ * Math.sin(anJ)]); }
                prismJ(ptsJ, -wtJ / 2 - 0.015, wtJ / 2, 0xE9E4DA);
                if (ftJ > 0.001) prismJ(ptsJ, wtJ / 2, wtJ / 2 + ftJ, colFJ);
              }
              tally.frameLen += Math.PI * 2 * RrJ;
              tally.glassArea += Math.PI * rInJ * rInJ;
              tally.round = (tally.round || 0) + 1;
              tally.units++;
              tally.byKind[o.kind] = (tally.byKind[o.kind] || 0) + 1;
              continue;
            }
            /* the frame, four sections round the hole -- except that a DOOR has
               no bottom rail: it has a threshold. A 70 mm timber rail at the floor
               read as nothing at all (Marek: no threshold on the entrance door),
               so a door gets an aluminium sill instead, 25 mm high, the frame's
               depth plus a 20 mm nose to the outside, at floor level. It is part
               of the unit and billed with it; no row of its own. */
            var isDoorU = (o.kind === 'door');
            put(w, JOINERY.frame, JOINERY.depth, 0,  h / 2 - JOINERY.frame / 2, frameM);
            if (!isDoorU) {
              put(w, JOINERY.frame, JOINERY.depth, 0, -h / 2 + JOINERY.frame / 2, frameM);
            } else {
              var thM = new T.MeshStandardMaterial({
                color: isNew(LAYER + '.threshold') ? MAGENTA : 0xB8BABC,
                roughness: 0.35, metalness: 0.6 });
              /* from the inner face of the frame out to 20 mm past the finished
                 facade: the frame stands in the middle of the wall and the facade
                 is up to 170 outside it, so a sill the frame's own depth sat 190
                 mm back in the reveal where nobody could see it (Marek) */
              var thH = 0.040, thNose = 0.020;
              var thOut = API.wallThickness() / 2 + API.facadeThk() + thNose;   /* from the wall centre outward */
              var thIn = JOINERY.depth / 2;                                       /* to the frame's inner face */
              var thL = thOut + thIn;
              var thm = new T.Mesh(new T.BoxGeometry(thL, thH, w), thM);
              thm.position.set(px + s.nx * ((thOut - thIn) / 2), cy - h / 2 + thH / 2,
                               pz + s.nz * ((thOut - thIn) / 2));
              thm.rotation.y = ang;
              thm.name = 'door-threshold';
              g.add(thm);
              tally.thresholds = (tally.thresholds || 0) + 1;
            }
            /* the jambs run to the floor on a door (no bottom rail to stop on) */
            var jLo = isDoorU ? 0 : JOINERY.frame;
            var jambH = h - JOINERY.frame - jLo, jambDy = (jLo - JOINERY.frame) / 2;
            put(JOINERY.frame, jambH, JOINERY.depth, -(w / 2 - JOINERY.frame / 2), jambDy, frameM);
            put(JOINERY.frame, jambH, JOINERY.depth,  (w / 2 - JOINERY.frame / 2), jambDy, frameM);
            tally.frameLen += (w + h) * 2;
            tally.headM = (tally.headM || 0) + w + 0.30;   /* the head flashing */

            var iw = w - JOINERY.frame * 2, ih = h - JOINERY.frame * 2;
            if (iw > 0.1 && ih > 0.1) {
              /* A SOLID DOOR IS SOLID. Glazing every opening was the easy thing and
                 it made the entry read as a patio door -- which is a different
                 product at a different price, and the person choosing between them
                 has to be able to see which one they picked. */
              if (SHED) {
                var hardM = new T.MeshStandardMaterial({
                  color: isNew(LAYER + '.hw') ? MAGENTA : 0x6A6E71,
                  roughness: 0.35, metalness: 0.7 });
                var faceN = JOINERY.depth / 2;      /* the outside face of the unit */
              }
              if (o.kind === 'garage' && !SHED) {
                /* AN OVERHEAD DOOR IS SECTIONS, NOT GLASS (barndo-v13, ranch-v28, garageapt-v3;
                   the ADU glazed it as an HS slider): horizontal panels about 21 in. tall
                   with a seam between them */
                var nSec = Math.max(4, Math.round(ih / 0.53)), secH = ih / nSec, qs;
                /* in the walk-through it rolls up into its head like a blind
                   (garageapt-v35): every section shares one lift arLeaf hung from the top */
                var gLift = { type: 'lift', hinge: [px, cy + ih / 2, pz], min: 0.05, ext: true, out: [s.nx, 0, s.nz] };
                for (qs = 0; qs < nSec; qs++) put(iw, secH - 0.006, JOINERY.leaf, 0, -ih / 2 + secH * (qs + 0.5), leafM).userData.arLeaf = gLift;
                tally.leavesWalk = (tally.leavesWalk || 0) + 1;
                tally.sections = (tally.sections || 0) + nSec;
                /* garageapt-v24 (unit 15): the floor falls to the door -- its
                   bottom seal reaches down past the rail to the slab there */
                var fD = (H.gaFall && H.gaFall() && H.gaFall().drop) || 0;
                if (fD > 0.003) put(w, fD, JOINERY.leaf, 0, -h / 2 - fD / 2, new T.MeshStandardMaterial({ color: 0x1E1F21, roughness: 0.9, metalness: 0.0 }));
              } else if (o.kind === 'door') {
                if (SHED) {
                  var code = String(o.code || '');
                  var nLeaf = Math.max(1, o.leaves || 1);
                  var sect = /^ODG/.test(code);

                  /* THE LEAVES, AND THE JOINT BETWEEN THEM. A double door is two
                     leaves meeting in the middle, so it is drawn as two with the
                     shut between them -- one slab 1500 wide reads as a panel
                     (Marek: delici spara u dvoukridlych). */
                  var shut = (nLeaf > 1) ? 0.006 : 0;
                  var leafW = (iw - shut * (nLeaf - 1)) / nLeaf, Lq;
                  /* IN THE WALK-THROUGH (shed-v118) each leaf swings on the edge its
                     hinges are drawn on below (a single door on its left as seen
                     from outside, a pair on the outer jambs); a sectional door
                     rolls up into its head like a blind, ribs and all */
                  var leafL = [], secL = sect ? { type: 'lift', hinge: [px, cy + ih / 2, pz], min: 0.05, ext: true, out: [s.nx, 0, s.nz] } : null;
                  /* core-v800: A GLAZED LEAF (opt-in H.glazed(o), RUSSWORX SHED's
                     full-glass door): stiles and rails in the leaf's colour round
                     one pane, all on the leaf's swing */
                  var glz = false;
                  try { glz = !sect && !!(H.glazed && H.glazed(o)); } catch (eGz) { glz = false; }
                  for (Lq = 0; Lq < nLeaf; Lq++) {
                    var cxD = -iw / 2 + leafW / 2 + Lq * (leafW + shut);
                    var hsD = (nLeaf > 1 && Lq >= nLeaf / 2) ? 1 : -1;
                    if (glz && leafW > 0.4 && ih > 0.8) {
                      var stW = 0.110, rlT = 0.110, rlB = 0.200;
                      var mS = put(stW, ih, JOINERY.leaf, cxD - leafW / 2 + stW / 2, 0, leafM);
                      leafL[Lq] = swingAt(mS, cxD + hsD * leafW / 2, hsD);
                      put(stW, ih, JOINERY.leaf, cxD + leafW / 2 - stW / 2, 0, leafM).userData.arLeaf = leafL[Lq];
                      put(leafW - 2 * stW, rlT, JOINERY.leaf, cxD, ih / 2 - rlT / 2, leafM).userData.arLeaf = leafL[Lq];
                      put(leafW - 2 * stW, rlB, JOINERY.leaf, cxD, -ih / 2 + rlB / 2, leafM).userData.arLeaf = leafL[Lq];
                      put(leafW - 2 * stW, ih - rlT - rlB, 0.008, cxD, (rlB - rlT) / 2, glassM).userData.arLeaf = leafL[Lq];
                      tally.glassArea += (leafW - 2 * stW) * (ih - rlT - rlB);
                      tally.glazedDoors = (tally.glazedDoors || 0) + 1;
                      continue;
                    }
                    var mD = put(leafW, ih, JOINERY.leaf, cxD, 0, leafM);
                    if (secL) { mD.userData.arLeaf = secL; continue; }
                    leafL[Lq] = swingAt(mD, cxD + hsD * leafW / 2, hsD);
                  }
                  tally.leavesWalk = (tally.leavesWalk || 0) + 1;

                  if (sect) {
                    /* A SECTIONAL DOOR IS SECTIONS. Horizontal ribs at the joint
                       between panels, which is the only thing that tells a garage
                       door from a wall from any distance (Marek: vodorovne
                       prolisy). Panels about 500 mm, so the count follows the
                       height rather than being a number typed in. */
                    /* core-v800: a roll-up door's slats are a product's pitch
                       (H.secPitch(o), RUSSWORX SHED's 3 in. roll-up); 500 else */
                    var secP = 0.50;
                    try { if (H.secPitch) secP = H.secPitch(o) || 0.50; } catch (eSP) { secP = 0.50; }
                    var nPan = Math.max(2, Math.round(ih / secP)), pq, ribs = 0;
                    /* the slats of a light roll-up: its own colour a shade down, not the dark handle steel */
                    if (secP < 0.50 && leafM && leafM.color) _colMats.rib = new T.MeshStandardMaterial({ color: leafM.color.clone().multiplyScalar(0.82), roughness: 0.7, metalness: 0.0 });
                    for (pq = 1; pq < nPan; pq++) {
                      var yR = -ih / 2 + ih * (pq / nPan);
                      _arWith(putN(iw, 0.020, 0.014, 0, yR, (secP < 0.50 && _colMats.rib) ? _colMats.rib : hardM, faceN), secL);
                      ribs++;
                    }
                    tally.ribs = (tally.ribs || 0) + ribs;
                    tally.sectional = (tally.sectional || 0) + 1;
                  } else {
                    /* THE HANDLE AT 1050 AND THE HINGES ON THE OUTER EDGES. A
                       double door has a handle on each leaf and its hinges on the
                       two outer jambs, never in the middle -- the middle is the
                       shut (Marek). */
                    var yH = atY(1.050), hq2, hgq;
                    for (hq2 = 0; hq2 < nLeaf; hq2++) {
                      var cxL2 = -iw / 2 + leafW / 2 + hq2 * (leafW + shut);
                      /* toward the shut: a single door's handle is on the side away
                         from its hinge, a pair's are either side of the joint */
                      var toShut = (nLeaf > 1) ? ((hq2 === 0) ? 1 : -1) : 1;
                      var xH = cxL2 + toShut * (leafW / 2 - 0.060);
                      _arWith(putN(0.030, 0.030, 0.030, xH, yH, hardM, faceN + 0.015), leafL[hq2]);
                      _arWith(putN(0.130, 0.024, 0.024, xH - toShut * 0.055, yH, hardM, faceN + 0.030), leafL[hq2]);
                      /* the hinges: three per leaf, on the OUTER edge of that leaf */
                      var xHg = cxL2 - toShut * (leafW / 2 - 0.010);
                      for (hgq = 0; hgq < 3; hgq++) {
                        var yHg = atY(0.250 + hgq * ((o.h - 0.500) / 2));
                        _arWith(putN(0.024, 0.100, 0.024, xHg, yHg, hardM, faceN + 0.006), leafL[hq2]);
                      }
                      tally.hinges = (tally.hinges || 0) + 3;
                      tally.handles = (tally.handles || 0) + 1;
                    }
                  }
                } else {
                  /* A PAIR OF LEAVES (adu-v1416, Marek: vstup do storage je
                     prevedeny jako jednokridle dvere -- dvoukridle ven). The
                     catalogue's double door (ODE18002, two leaves) was one slab
                     1.4 m wide. Two leaves now, 6 mm apart, the astragal over the
                     joint in the frame's colour; shut, a pair that opens out
                     looks as one that opens in -- the plan shows which. */
                  if ((o.leaves | 0) >= 2 && iw > 0.9) {
                    var gapL = 0.006, lwL = (iw - gapL) / 2;
                    var lA = put(lwL, ih, JOINERY.leaf, -iw / 2 + lwL / 2, 0, leafM);
                    var lB = put(lwL, ih, JOINERY.leaf, iw / 2 - lwL / 2, 0, leafM);
                    var lC = put(0.04, ih, JOINERY.leaf + 0.012, 0, 0, frameM);
                    swingAt(lA, -iw / 2, -1);
                    /* the astragal is fixed to the second leaf and goes with it */
                    lC.userData.arLeaf = swingAt(lB, iw / 2, 1);
                    tally.pairs = (tally.pairs || 0) + 1;
                    if (o.out) tally.pairsOut = (tally.pairsOut || 0) + 1;
                  } else {
                    swingAt(put(iw, ih, JOINERY.leaf, 0, 0, leafM), hsHand * iw / 2, hsHand);
                  }
                  tally.leavesWalk = (tally.leavesWalk || 0) + 1;
                }
              } else {
                if (SHED) {
                  /* THE PANE GRID. Every WIND unit is a whole number of 250 mm
                     panes each way -- the index in the code plus one -- so the bars
                     land on the module and no column is cut (Marek). Up to
                     750 x 750 the unit opens and has no bars at all.

                     The glass is ONE sheet behind the bars rather than a pane per
                     cell: the bars are what is seen, and thirty-six little sheets
                     of transparent glass is thirty-six meshes for nothing. */
                  var wg = /^WIND(\d\d)(\d\d)$/.exec(String(o.code || ''));
                  var opens = (o.w <= WIN_OPENABLE && o.h <= WIN_OPENABLE);

                  if (wg && !opens) {
                    put(iw, ih, JOINERY.glass, 0, 0, glassM);
                    tally.glassArea += iw * ih;
                    var nC2 = parseInt(wg[1], 10) + 1, nR2 = parseInt(wg[2], 10) + 1, bq;
                    for (bq = 1; bq < nC2; bq++) {
                      put(WIN_BAR.w, ih, WIN_BAR.d, -iw / 2 + iw * (bq / nC2), 0, frameM);
                    }
                    for (bq = 1; bq < nR2; bq++) {
                      put(iw, WIN_BAR.w, WIN_BAR.d, 0, -ih / 2 + ih * (bq / nR2), frameM);
                    }
                    tally.bars = (tally.bars || 0) + (nC2 - 1) + (nR2 - 1);
                    tally.panes = (tally.panes || 0) + nC2 * nR2;
                    tally.fixed = (tally.fixed || 0) + 1;
                  } else if (wg) {
                    /* OPENABLE: a sash inside the frame, so it reads as a window
                       that opens rather than a hole with glass in it. */
                    var sashW = 0.045;
                    put(iw, sashW, JOINERY.depth * 0.7, 0,  ih / 2 - sashW / 2, frameM);
                    put(iw, sashW, JOINERY.depth * 0.7, 0, -ih / 2 + sashW / 2, frameM);
                    put(sashW, ih - sashW * 2, JOINERY.depth * 0.7, -(iw / 2 - sashW / 2), 0, frameM);
                    put(sashW, ih - sashW * 2, JOINERY.depth * 0.7,  (iw / 2 - sashW / 2), 0, frameM);
                    put(iw - sashW * 2, ih - sashW * 2, JOINERY.glass, 0, 0, glassM);
                    tally.glassArea += (iw - sashW * 2) * (ih - sashW * 2);
                    /* the handle on the shut stile, at half height */
                    putN(0.024, 0.090, 0.024, iw / 2 - sashW - 0.020, 0, hardM, faceN);
                    tally.openable = (tally.openable || 0) + 1;
                  } else {
                    /* ANYTHING PLACED BEFORE THIS LIST EXISTED still draws: leaves
                       with posts between them, the way the house line does it. */
                    var nL = Math.max(1, o.leaves || 1);
                    var mulls = nL - 1;
                    var paneW = (iw - mulls * JOINERY.frame) / nL;
                    if (paneW < 0.15) { nL = 1; mulls = 0; paneW = iw; }
                    /* a French door's end panes swing in the walk-through (shed-v118) */
                    var frenchW = (o.kind === 'french');
                    for (var L2 = 0; L2 < nL; L2++) {
                      var cxL = -iw / 2 + paneW / 2 + L2 * (paneW + JOINERY.frame);
                      var paneF = put(paneW, ih, JOINERY.glass, cxL, 0, glassM);
                      tally.glassArea += paneW * ih;
                      if (frenchW && (L2 === 0 || L2 === nL - 1)) {
                        if (nL === 1) swingAt(paneF, -iw / 2, -1);
                        else swingAt(paneF, (L2 === 0) ? -iw / 2 : iw / 2, (L2 === 0) ? -1 : 1);
                        tally.leavesWalk = (tally.leavesWalk || 0) + 1;
                      }
                    }
                    for (var M2 = 1; M2 <= mulls; M2++) {
                      var cxM = -iw / 2 + M2 * paneW + (M2 - 0.5) * JOINERY.frame;
                      put(JOINERY.frame, ih, JOINERY.depth, cxM, 0, frameM);
                      tally.frameLen += ih;
                    }
                    tally.mullions += mulls;
                  }
                } else {
                  /* MULLIONS, AND THE GLASS IN LEAVES BETWEEN THEM.

                     A four-metre opening was going in as ONE sheet of glass, which is
                     not a thing that exists: it cannot be lifted, glazed or sealed,
                     and an HS portal that size is leaves with posts between them. One
                     pane read as a shop front rather than a door.

                     The posts are the same section as the frame, so the unit looks
                     like one thing rather than a frame with something else inside
                     it. */
                  /* A WINDOW IN THE SAUNA ROOM IS ONE PANE (sauna-v41): no posts,
                     no bars -- a hot room's glass is one sheet (Marek: sklo vcelku) */
                  var hotWin = (o.kind === 'window') && !!H.hotAt && H.hotAt(px - s.nx * 0.30, pz - s.nz * 0.30);
                  var nL = hotWin ? 1 : Math.max(1, o.leaves || 1);
                  if (hotWin) tally.hotSingle = (tally.hotSingle || 0) + 1;
                  var mulls = nL - 1;
                  var paneW = (iw - mulls * JOINERY.frame) / nL;
                  if (paneW < 0.15) { nL = 1; mulls = 0; paneW = iw; }

                  /* WHICH PANES MOVE IN THE WALK-THROUGH (adu-v1431). A French
                     door swings in on its jambs; an HS or full-wall slider runs
                     its end sash over the fixed pane beside it (both ends from
                     four panes up), stepped in past the mullion first. A window
                     stays put. */
                  var slider = (o.kind === 'portal') || (o.kind === 'french' && (!!o.fullW || /slid/i.test(_unitName(o))));
                  var french = (o.kind === 'french') && !slider;
                  var hsEnd = (o.hand === 'L') ? 0 : nL - 1;
                  for (var L2 = 0; L2 < nL; L2++) {
                    var cxL = -iw / 2 + paneW / 2 + L2 * (paneW + JOINERY.frame);
                    var pane = put(paneW, ih, JOINERY.glass, cxL, 0, glassM);
                    tally.glassArea += paneW * ih;
                    if (french) {
                      if (nL === 1) swingAt(pane, hsHand * iw / 2, hsHand);
                      else if (L2 === 0) swingAt(pane, -iw / 2, -1);
                      else if (L2 === nL - 1) swingAt(pane, iw / 2, 1);
                      else continue;
                      tally.leavesWalk = (tally.leavesWalk || 0) + 1;
                    } else if (slider) {
                      var stepIn = JOINERY.depth / 2 + JOINERY.glass / 2 + 0.006;
                      if (nL === 1) {
                        /* one sash: over the wall beside the hole, on its inner face */
                        var sg1 = (o.hand === 'L') ? -1 : 1, stepW = API.wallThickness() / 2 + 0.03;
                        _arSlide(pane, ux * sg1, uz * sg1, 0, paneW * 0.9, -s.nx * stepW, -s.nz * stepW, true, s.nx, s.nz);
                      } else if ((nL >= 4 && (L2 === 0 || L2 === nL - 1)) || (nL < 4 && L2 === hsEnd)) {
                        var sgS = (L2 === 0) ? 1 : -1;
                        _arSlide(pane, ux * sgS, uz * sgS, 0, paneW + JOINERY.frame * 0.5, -s.nx * stepIn, -s.nz * stepIn, true, s.nx, s.nz);
                      } else continue;
                      tally.leavesWalk = (tally.leavesWalk || 0) + 1;
                    }
                  }
                  for (var M2 = 1; M2 <= mulls; M2++) {
                    var cxM = -iw / 2 + M2 * paneW + (M2 - 0.5) * JOINERY.frame;
                    put(JOINERY.frame, ih, JOINERY.depth, cxM, 0, frameM);
                    tally.frameLen += ih;
                  }
                  tally.mullions += mulls;
                }
              }
            }

            tally.units++;
            tally.byKind[o.kind] = (tally.byKind[o.kind] || 0) + 1;
          }
        }

        if (H.setTally) H.setTally(tally);
        if (tally.hotSingle) ctx.log('hot        ' + tally.hotSingle + ' window(s) in the sauna room glazed as one pane, no posts or bars');
        /* each unit is a piece against its own code, so two of the same window is
           two of one row rather than two rows saying the same thing */
        for (i = 0; i < SEG.length; i++) {
          (SEG[i].openings || []).forEach(function (o) {
            if (!o.code || (PLANK && (PLANK.isPortal(o) || PLANK.isPlank(o)))) return;
            if (!__CORE.CATALOG[o.code]) {
              __CORE.CATALOG[o.code] = { code: o.code, unit: 'pcs',
                                  label: 'Opening ' + o.code + (o.hand ? (' ' + o.hand) : '') };
            }
            /* core-v786: a unit the offer marks m2 (the serving windows) is sold by
               its area, the rest by the piece */
            _add(LAYER, o.code, _unitM2(o) ? Math.round(o.w * o.h * 1000) / 1000 : 1);
            /* the exterior door's handle set (the unit does not carry it).
               THE SHED FAMILY'S IRONMONGERY: a hinged exterior door takes the
               security handle set and three hinges per leaf; a sectional door
               brings its own track and springs in its row, so it gets neither.
               EXTE0101 is a hinged door like any ODE. */
            var cd9 = String(o.code);
            if (SHED ? /^(ODE|EXTE)/.test(cd9) : /^ODE/.test(cd9)) {
              _add(LAYER, 'ODD00002', 1);
              if (SHED) _add(LAYER, 'ODD00005', Math.max(1, o.leaves || 1) * 3);
            }
            /* and an overhead door's opener, as every US shop door has one
               (barndo-v13, garageapt-v3) */
            if (GAR && GAR.opener && o.kind === 'garage') { _add(LAYER, GAR.opener, 1); tally.openers = (tally.openers || 0) + 1; }
          });
        }
        /* RANCH: the garage kit (ranch-v25); GA: the openers said */
        if (GAR && GAR.kit) GAR.kit(ctx);
        if (GAR && GAR.say) GAR.say(ctx, tally);

        /* AND WHAT FIXES A UNIT INTO ITS HOLE -- NOT BILLED, AND NO CODE FOR IT.

           A window or a door is billed as one piece and set into an opening 10 mm
           bigger than itself all round. Nothing closes that gap and nothing holds
           the frame: no anchors, no foam, no sealing tape. On a house that has to
           show an air-tightness figure, the joint round the joinery is where it is
           lost, and it is currently free.

           tally.frameLen is the perimeter of every unit added up, which is the
           length the joint runs, so the quantity follows straight from it: an
           anchor about every 700 mm, and a 750 ml can of foam to roughly 10 m of a
           10 mm joint.

           The tape is counted on both faces, because the joint is taped twice:
           vapour-tight inside, weather-tight outside. A joint taped on one face
           only is the one that fails. */
        /* taurus-v20: Matt's plank doors -- boards, the Z, screws, hinges, handles */
        if (PLANK && tally.plank && tally.plank.doors) {
          var PK = tally.plank, TGD = PLANK.TG;
          _add(LAYER, TGD.board, PK.boardM);
          _add(LAYER, TGD.ledge.code, PK.ledgeM);
          _add(LAYER, TGD.screw, PK.screws);
          _add(LAYER, TGD.hinge, PK.leaves * TGD.hingesPerLeaf);
          _add(LAYER, TGD.handle, PK.doors);
          ctx.log(PLANK.tag + '  plank doors ' + PK.doors + ' (' + PK.leaves + ' leaves): ' + TGD.board + ' ' + (Math.round(PK.boardM * 10) / 10) + ' m, ' +
                  TGD.ledge.code + ' ' + (Math.round(PK.ledgeM * 100) / 100) + ' m (ledges + braces), ' + PK.screws + ' x ' + TGD.screw + ', ' +
                  (PK.leaves * TGD.hingesPerLeaf) + ' x ' + TGD.hinge + ', ' + PK.doors + ' x ' + TGD.handle + ' -- Matt\'s, no door unit');
        }
        if (tally.frameLen) {
          var jAnch = Math.ceil(tally.frameLen / JOIN_FIX.spacing);
          var jFoam = Math.ceil(tally.frameLen / JOIN_FIX.foamPerM);
          var jRoll = Math.ceil(tally.frameLen / JOIN_FIX.tapePerRoll);
          _add(LAYER, JOIN_FIX.anchor, jAnch);
          _add(LAYER, JOIN_FIX.foam, jFoam);
          _add(LAYER, JOIN_FIX.tapeOut, tally.frameLen);
          /* and a self-adhesive flashing over every head, the width plus 300 */
          if (tally.headM > 0) _add(LAYER, 'ROR00012', tally.headM);
          _add(LAYER, JOIN_FIX.tapeIn, jRoll);
          ctx.log('FIX  joinery joint ' + Math.round(tally.frameLen * 1000) +
                  ' mm round ' + tally.units + ' unit(s): ' + jAnch + ' x ' +
                  JOIN_FIX.anchor + ', ' + jFoam + ' x ' + JOIN_FIX.foam + ', ' +
                  Math.round(tally.frameLen * 10) / 10 + ' m ' + JOIN_FIX.tapeOut +
                  ' outside, ' + jRoll + ' x ' + JOIN_FIX.tapeIn + ' inside');
        }

        /* GA / ANY HOUSE: the canopy over the hall's door out (garageapt-v20),
           the rooms' light and egress, the brace scan */
        if (H.after) H.after(ctx);

        if (editing_) {
          _why(ctx, 'joinery EDIT MODE -- frames drawn in the guide gold: those are the ' +
                  'units that respond to a drag. Drop one on the bin to remove it, or ' +
                  'the cross to finish.');
        }
        if (!tally.units) { _why(ctx, 'joinery no units -- place an opening first'); return; }

        var kinds = [];
        for (var k in tally.byKind) if (tally.byKind.hasOwnProperty(k)) kinds.push(tally.byKind[k] + ' ' + k);
        ctx.log('joinery ' + tally.units + ' unit(s): ' + kinds.join(', '));
        ctx.log('joinery ' + (tally.leavesWalk || 0) + ' leaf / sash(es) open in the AR walk-through (arLeaf, adu-v1431, shed-v118): ' +
                (SHED ? 'hinged leaves swing with their handles and hinges, a sectional door rolls up'
                      : ('doors and French doors swing, HS / full-wall sliders run their end sash' + (tally.sections ? ', an overhead door rolls up' : ''))));
        if (tally.pairs) ctx.log('joinery ' + tally.pairs + ' double door(s) drawn as two leaves with the astragal between them' +
                                 (tally.pairsOut ? (', ' + tally.pairsOut + ' of them opening out of the house') : '') + ' (adu-v1416)');
        if (tally.mullions) {
          ctx.log('joinery ' + tally.mullions + ' mullion(s) -- wide units are made of leaves ' +
                  'with posts between them, not one sheet of glass');
        }
        if (SHED) {
          ctx.log('joinery windows ' + (tally.fixed || 0) + ' fixed with a ' +
                  Math.round(WIN_PANE * 1000) + ' mm pane grid (' + (tally.panes || 0) +
                  ' pane(s), ' + (tally.bars || 0) + ' bar(s)) and ' + (tally.openable || 0) +
                  ' openable up to ' + Math.round(WIN_OPENABLE * 1000) +
                  ' -- the grid is drawing and is in the price of the window, no row of its own');
          if (tally.handles || tally.sectional) {
            ctx.log('joinery doors ' + (tally.handles || 0) + ' handle(s) at 1050 and ' +
                    (tally.hinges || 0) + ' hinge(s) on the outer edges' +
                    (tally.sectional ? (', ' + tally.sectional + ' sectional door with ' +
                                        (tally.ribs || 0) + ' rib(s)') : '') +
                    ' -- billed ODD00002 + ODD00005 per hinged leaf, the sectional carries its own');
          }
        }
        ctx.log('joinery glazing ' + API.fmtArea(tally.glassArea) + ', frame ' +
                tally.frameLen.toFixed(1) + ' m -- each unit set in the CLEAR opening with ' +
                Math.round(JOINERY.tol * 1000) + ' mm all round for fitting');
        var coded = 0, uncoded = 0;
        for (i = 0; i < SEG.length; i++) {
          (SEG[i].openings || []).forEach(function (o) { if (o.code) coded++; else uncoded++; });
        }
        ctx.log('joinery ' + coded + ' unit(s) carry a catalogue code' +
                (uncoded ? (', ' + uncoded + ' placed before the codes arrived and cannot be ordered') : ''));
    }
    /* =========================================================================
       THE DOOR SET (core-v783, the joinery module's second part --
       status/modules.md 7b). A partition's doorway drawn: the lining (two
       jambs and a head proud of the stud both faces), the casing where the
       product has one, and its door -- a hinged leaf ajar on the jamb the
       plan hangs it on, a slider parked over the wall beside the hole, or a
       closet's pair, bifold or bypass the way the plan draws them. The ADU's
       text; the products call it from their own _doorSet3D.

         API.houseDoorSet(T, g, p, vert, lo, hole, dh, y0, {
           layer ('adu-partitions'), MAGENTA, PART, isNew,
           casing: { w, t, tally } -- BARNDO / GA / ANY HOUSE
         })

       Carried: the closet doors (adu-v1404) to SAUNA and the shed family,
       which drew a pair, a bifold or a bypass as one hinged leaf.
       ========================================================================= */
    function houseDoorSet(T, g, p, vert, lo, hole, dh, y0, H) {
      var MAGENTA = H.MAGENTA, PART = H.PART, isNew = H.isNew, CASE = H.casing || null;
        var col = isNew(H.layer) ? MAGENTA : 0xF4F3F0;
        var mat = new T.MeshStandardMaterial({ color: col, roughness: 0.55, metalness: 0.0 });
        var dw = hole.a1 - hole.a0;
        var jamb = 0.03, leafT = 0.042;
        var aCtr = (hole.a0 + hole.a1) / 2;

        function at3(along) { return vert ? { x: p.at, z: lo + along } : { x: lo + along, z: p.at }; }

        /* the lining: two jambs and a head, standing proud of the stud both sides */
        function lining(w, h, d, along, y) {
          var m = new T.Mesh(new T.BoxGeometry(w, h, d), mat);
          var q = at3(along);
          m.position.set(q.x, y, q.z);
          if (!vert) m.rotation.y = Math.PI / 2;
          g.add(m);
        }
        lining(PART.d + 0.01, dh, jamb, hole.a0 + jamb / 2, y0 + dh / 2);
        lining(PART.d + 0.01, dh, jamb, hole.a1 - jamb / 2, y0 + dh / 2);
        lining(PART.d + 0.01, jamb, dw, aCtr, y0 + dh - jamb / 2);
        /* barndo-v26 / garageapt-v29: the casing on both faces -- two legs and
           a head over them */
        if (CASE) {
          var cw = CASE.w(), ct = CASE.t(), cOff = PART.d / 2 + PART.board + ct / 2, sg;
          var casing = function (len, h, along, y, sgn) {
            var m = new T.Mesh(new T.BoxGeometry(ct, h, len), mat), q = at3(along);
            if (vert) m.position.set(q.x + sgn * cOff, y, q.z);
            else { m.position.set(q.x, y, q.z + sgn * cOff); m.rotation.y = Math.PI / 2; }
            g.add(m);
          };
          for (sg = -1; sg <= 1; sg += 2) {
            casing(cw, dh, hole.a0 - cw / 2, y0 + dh / 2, sg);
            casing(cw, dh, hole.a1 + cw / 2, y0 + dh / 2, sg);
            casing(dw + 2 * cw, cw, aCtr, y0 + dh + cw / 2, sg);
          }
          var cT = CASE.tally();
          cT.len += 2 * (2 * dh + dw + 2 * cw); cT.n++;
        }

        if (hole.pass) return;   /* a doorway has no leaf */

        /* THE CLOSET DOORS (adu-v1404, Marek: skrine dvojite dvere). Drawn the
           way the plan draws them, in the wall's own frame: u along the wall
           from lo, n across it towards the side the door opens to -- the same
           side DRAW.door sends a leaf (sw bit 1: a wall along x opens to -z
           when clear, a wall along z to +x). Each piece is a panel from one
           point to another, so no rotation case has to be worked out twice.
             pair   -- two leaves, each on its own jamb, ajar 30 degrees
             bifold -- panels folded out at 50 degrees, stacked at one jamb, or
                       at both for an opening over 1.1 m
             bypass -- two leaves at two depths in the opening, one slid half
                       over the other */
        if (hole.kind === 'pair' || hole.kind === 'bifold' || hole.kind === 'bypass') {
          var sideK = (hole.sw & 2) ? -1 : 1, nW = vert ? sideK : -sideK, shK = dh - jamb;
          var W3 = function (u, n) { return vert ? { x: p.at + nW * n, z: lo + u } : { x: lo + u, z: p.at + nW * n }; };
          var panel = function (u0, n0, u1, n1, t) {
            var A = W3(u0, n0), B = W3(u1, n1), dx = B.x - A.x, dz = B.z - A.z, L = Math.hypot(dx, dz);
            if (L < 0.05) return;
            var m = new T.Mesh(new T.BoxGeometry(L, shK, t), mat);
            m.position.set((A.x + B.x) / 2, y0 + shK / 2, (A.z + B.z) / 2);
            m.rotation.y = Math.atan2(-dz, dx);
            g.add(m);
            return m;
          };
          if (hole.kind === 'pair') {
            var lwP = dw / 2 - jamb, opP = 0.52;
            var mP1 = panel(hole.a0 + jamb, 0, hole.a0 + jamb + lwP * Math.cos(opP), lwP * Math.sin(opP), leafT);
            var mP2 = panel(hole.a1 - jamb, 0, hole.a1 - jamb - lwP * Math.cos(opP), lwP * Math.sin(opP), leafT);
            /* each leaf on its own jamb, shut along the wall towards the middle */
            var hP1 = W3(hole.a0 + jamb, 0), hP2 = W3(hole.a1 - jamb, 0), cP = W3(aCtr, 0), sP = W3(aCtr, 1);
            _arSwing(mP1, hP1.x, hP1.z, cP.x - hP1.x, cP.z - hP1.z, sP.x - cP.x, sP.z - cP.z, opP);
            _arSwing(mP2, hP2.x, hP2.z, cP.x - hP2.x, cP.z - hP2.z, sP.x - cP.x, sP.z - cP.z, opP);
          } else if (hole.kind === 'bifold') {
            var two = dw > 1.1, pw = (dw - 2 * jamb) / (two ? 4 : 2), thF = 0.87, tF = 0.028;
            var starts = two ? [[hole.a0 + jamb, 1], [hole.a1 - jamb, -1]]
                             : [[(hole.sw & 1) ? hole.a1 - jamb : hole.a0 + jamb, (hole.sw & 1) ? -1 : 1]];
            for (var sq = 0; sq < starts.length; sq++) {
              var u0 = starts[sq][0], dr = starts[sq][1];
              var u1 = u0 + dr * pw * Math.cos(thF), n1 = pw * Math.sin(thF), u2 = u1 + dr * pw * Math.cos(thF);
              panel(u0, 0, u1, n1, tF);
              panel(u1, n1, u2, 0, tF);
            }
          } else {
            var half2 = (dw - 2 * jamb) / 2, ovB = 0.03, dB = PART.d * 0.2, slid = 0.45 * half2;
            panel(hole.a0 + jamb, -dB, aCtr + ovB, -dB, 0.035);
            panel(aCtr - ovB - slid, dB, hole.a1 - jamb - slid, dB, 0.035);
          }
          return;
        }

        /* A SLIDER DOES NOT SWING, AND HANGING IT ON A HINGE WOULD SAY IT DOES.

           It runs in the plane of the wall and parks over the wall beside the
           opening, on one face. Parked at seven tenths of its travel: fully open it
           is a panel on a wall with nothing to connect it to the hole, and closed
           it is the filled panel this whole assembly exists to avoid.

           Two leaves part from the middle and go to opposite faces, which is the
           same thing the plan says by drawing them at different depths. */
        if (hole.slide) {
          var park = 0.70, face = PART.d / 2 + leafT / 2 + 0.004;
          var sh = dh - jamb;

          function slab(wid, alongCtr, offSide) {
            /* ACROSS THE WALL IS LOCAL X, ALONG IT LOCAL Z (adu-v1431), as for
               the lining above: the box was (wid, sh, leafT), so every sliding
               leaf stood square to its wall like a fin (seen in the walk-
               through: 42 mm along an 'h' wall, 900 across it) */
            var g2 = new T.Mesh(new T.BoxGeometry(leafT, sh, wid), mat);
            var q2 = at3(alongCtr);
            var nOff = offSide * face;
            g2.position.set(q2.x + (vert ? nOff : 0), y0 + sh / 2, q2.z + (vert ? 0 : nOff));
            if (!vert) g2.rotation.y = Math.PI / 2;
            g.add(g2);
            return g2;
          }

          /* along the wall in the group's coordinates: z on a 'v' wall, x on an 'h' one */
          var auX = vert ? 0 : 1, auZ = vert ? 1 : 0;
          if (hole.slide === 3) {
            var hw = dw / 2;
            var sA = slab(hw, aCtr - dw / 4 - park * hw, +1);
            var sB = slab(hw, aCtr + dw / 4 + park * hw, -1);
            _arSlide(sA, -auX, -auZ, park * hw, hw * 0.95);
            _arSlide(sB, auX, auZ, park * hw, hw * 0.95);
          } else {
            var dirS = (hole.slide === 2) ? 1 : -1;
            var sC = slab(dw, aCtr + dirS * park * dw, +1);
            _arSlide(sC, dirS * auX, dirS * auZ, park * dw, dw * 0.95);
          }
          return;
        }

        /* the leaf, hinged on one jamb and standing off the shut. Which jamb it
           hangs on is the swing the plan already records. */
        /* THIRTY DEGREES, up from twenty. At twenty the leaf still read as a panel
           sitting in its frame from a shallow angle; a third of a right angle is
           far enough to be unmistakably open and still short of swinging into the
           room in a way that hides what is behind it. */
        var open = 0.52;
        var right = (hole.sw & 1) === 1;
        /* AND WHICH WAY IT OPENS, WHICH v219 THREW AWAY. sw carries two bits: bit 0
           is the jamb it hangs on, bit 1 is the face it swings onto. Only the first
           was read, so half the doors in the model opened the opposite way to the
           arc drawn on the plan for the same door. */
        var side = (hole.sw & 2) ? -1 : 1;
        var lw = dw - jamb * 2;
        if (lw < 0.30) return;
        var geo = new T.BoxGeometry(lw, dh - jamb, leafT);
        geo.translate(lw / 2, 0, 0);                       /* hinge at the near edge */
        var leaf = new T.Mesh(geo, mat);
        var hingeAlong = right ? (hole.a1 - jamb) : (hole.a0 + jamb);
        var q0 = at3(hingeAlong);
        leaf.position.set(q0.x, y0 + (dh - jamb) / 2, q0.z);
        /* Rotating about Y sends the leaf's local +X to (cos, -sin) in world x,z.
           Closed, it has to lie along the wall from its hinge; ajar, it swings off
           it by open. Four cases, written out rather than derived, because a sign
           here is the difference between a door and a leaf inside the wall. */
        /* THE TWO CASES DO NOT SHARE A SIGN, WHICH IS WHY v224 FIXED HALF OF IT.

           DRAW.door works in the wall's own frame and gets to the vertical case by
           rotating the canvas a quarter turn, and that rotation flips the sense of
           the swing between the two. Screen y grows downward and Y(z) = oy + z*ppm,
           so screen +y is world +z:

             horizontal wall, sw&2 = 0 -> the leaf goes to local -y -> world -z
             vertical wall,   sw&2 = 0 -> local -y lands on screen -x -> world +x

           Reading it as one rule gave the horizontal doors the wrong side. Written
           out per case, with the world direction each one has to end up pointing. */
        var theta;
        if (vert) theta = right ? (Math.PI / 2 - side * open) : (-Math.PI / 2 + side * open);
        else      theta = right ? (Math.PI - side * open) : (side * open);
        leaf.rotation.y = theta;
        g.add(leaf);
        /* shut it is theta less the open, and every case above adds the open
           with the sign right ? -side : side -- that is the turn that opens it */
        leaf.userData.arLeaf = { type: 'swing', hinge: [q0.x, 0, q0.z], dir: right ? -side : side, ajar: open, max: 1.45 };
    }


    /* =========================================================================
       THE ROOF FRAME MODULE (core-v784, the eighth shared module of the house
       line, its first part -- status/modules.md 8a). What carries the roof,
       written once: a trussed gable or hip hands over to the truss module, a
       mono-pitch and a flat roof get rafters (the section chosen to cross the
       span in one piece, set out on the building's stations, one per stud
       under a flat roof, published for the battens above), a ridge board where
       two rafter slopes meet, the hurricane ties and their nails, and then the
       eave posts, the roof's build-up and the gutters in their order; the
       truss stage of the frame view (everything but the trusses hidden, the
       members measured in the log). The ADU's text with the products' own
       parts behind hooks.

         API.houseRoof(ctx, {
           api, layer ('adu-roof'), wood, RAFTER, STUD, TRUSS_TIE, EAVE_POST,
           GUT, show { trusses, posts, gutters }, billReset, add, TR, TK,
           trussSides, onPlane, rafterSteps, trussStations, roofSection,
           bearStuds, outletReset, memberPts, members, frameMark, buildTrusses,
           eavePosts, roofSkin, gutters,
           paint:  { on, isOn, ORDER, NAME, PLAIN } -- the truss
                   stage (the ADU family, RANCH, BARNDO, GA, ANY HOUSE)
           report: { hipGroup } -- SAUNA and the shed family say the truss
                   list to the PDF right after the trusses
           afterPosts (ANY HOUSE: the L's valley), afterGutters (GA / ANY
           HOUSE: the splash blocks), say (ANY HOUSE: the heavy rafter check)
         })

       Carried: the truss recorder started over on every roof (adu / sauna)
       to the shed family, whose PDF could keep the last gable's trusses. Not
       carried (candidates): the truss stage of the frame view for SAUNA and
       the shed family; the framing bodies named by the section the bill used
       (frameMark, the ADU's and the shed's) for SAUNA -- the core's coatings
       read that name, so SAUNA's mono / flat roof would bill a few hundred
       m2 of MFP00001 / 00002 / 00007 less.
       ========================================================================= */
    function houseRoof(ctx, H) {
      /* the product's own pieces, under the names the text has always used */
      var API = H.api, LAYER = H.layer, RAFTER = H.RAFTER, STUD = H.STUD, TRUSS_TIE = H.TRUSS_TIE,
          EAVE_POST = H.EAVE_POST, GUT = H.GUT;
      var SHOW_TRUSSES = !!(H.show && H.show.trusses), SHOW_POSTS = !!(H.show && H.show.posts),
          SHOW_GUTTERS = !!(H.show && H.show.gutters);
      var _billReset = H.billReset, _add = function (cat, code, q) { H.add(code, q); },
          _TR = H.TR, _TK = H.TK, _trussSides = H.trussSides, _onPlane = H.onPlane,
          _rafterStepsFromStuds = H.rafterSteps, _trussStations = H.trussStations, roofSection = H.roofSection,
          _buildTrusses = H.buildTrusses, _eavePosts = H.eavePosts, _roofSkin = H.roofSkin, _gutters = H.gutters;
      var _bearStuds = H.bearStuds() || [];
      /* the products' own roof, each behind its hook (status/modules.md 8a):
         PAINT   the ADU family, RANCH, BARNDO, GA, ANY HOUSE -- the truss
                 stage of the frame view
         REPORT  SAUNA, the shed family -- the truss list to the PDF said
                 right after the trusses */
      var PAINT = H.paint || null, REPORT = H.report || null;
        var T = ctx.THREE, g = ctx.group, R = ctx.roof, i, j;
        var mat = new T.MeshStandardMaterial({ color: H.wood, roughness: 0.85, metalness: 0.0 });
        var deckMat = new T.MeshStandardMaterial({ color: scheme().deck || 0xB9A88C, roughness: 0.95, metalness: 0.0 });
        var sp = ctx.grid;
        /* RESET BEFORE ANYTHING IS COUNTED. Without it the roof's quantities piled
           up across rebuilds -- four rebuilds gave four times the truss plates and
           four times the timber, and 1632 plates looked plausible enough to ship. */
        _billReset(LAYER);
        if (H.outletReset) H.outletReset();
        var tally = { planes: R.length, rafters: 0, rafterLen: 0, ridge: 0, sheath: 0 };

        /* A GABLE IS TRUSSED, A FLAT ROOF IS JOISTED. Both still get their
           sheathing laid the same way, on the planes the core hands back. */
        /* A HIP IS TRUSSED TOO -- the truss builder, the ceiling on the bottom
           chord, the depth the skin sits on. The shape differs, not the build. */
        var trussed = _isTrussed();
        /* THE STAGE IS ARMED BEFORE THE FIRST MEMBER IS MADE, so the members
           are born in their own colours instead of being repainted afterwards
           -- a repaint is the thing that has twice been undone by a later pass. */
        /* the stage that shows the roof structure is 'frame' now -- the same one
           that colours the wall panels under it (adu-v1376) */
        if (PAINT) PAINT.on(!!(ctx.viz && ctx.viz.key === 'frame'));
        /* THE MARK IS SET BEFORE THE SECTION IS CHOSEN, so it has to be told
           afterwards. A long span buys a deeper rafter and the bill follows it
           -- tally.section.code -- while this mark names the default 60x240 and
           never changes. 199 m of roof timber in the model under a code the bill
           had barely any of. Sixth time a body has worn the wrong name; this one
           hid behind a variable (Marek). */
        _asmMark(ctx, 'roof_framing', 'top', 'Roof framing', RAFTER.code);
        var _frameMark = ctx.item || null;
        /* every roof starts the truss sheets over -- a mono roof builds no truss,
           and without this the shapes of the last gable stayed in the recorder
           and went into the PDF of a roof that has none */
        try { _TR.reset(); } catch (eTRr) {}
        if (trussed && SHOW_TRUSSES) _buildTrusses(ctx, R);
        /* SAUNA / the shed family: the truss list to the PDF, said here */
        if (REPORT) try {
          if (trussed && SHOW_TRUSSES) REPORT.hipGroup();
          API.reportTrusses((trussed && SHOW_TRUSSES) ? _TR.shapes() : []);
          ctx.log('truss list  ' + ((trussed && SHOW_TRUSSES) ? _TR.shapes().length : 0) +
                  ' shape(s) to the PDF' + (trussed ? '' : ' -- a mono roof has rafters, no trusses') +
                  (_TR.missing ? ' -- ERROR the truss recorder is missing, configurator-ui.js is older than core-v254' : '') +
                  (_TK.missing ? ' -- ERROR the truss kit is missing, configurator-ui.js is older than core-v257: no trusses built' : ''));
        } catch (eRT) {}

        for (i = 0; i < R.length; i++) {
          var pl = R[i];
          var lo, hi;

          /* A LEVEL ROOF HAS NO HIGH EDGE. Gable and mono both arrive as a plane
             with an eave and a ridge, so they share every line below. The parapet
             roof is dead level and has only the one edge -- its rafters run from
             side to side, so the opposite side of the quad is the other bearing. */
          if (pl.ridge) {
            lo = { a: pl.eave[0], b: pl.eave[1] };
            hi = { a: pl.ridge[0], b: pl.ridge[1] };
          } else {
            var q = pl.pts;
            var sA = Math.hypot(q[1][0] - q[0][0], q[1][2] - q[0][2]);
            var sB = Math.hypot(q[2][0] - q[1][0], q[2][2] - q[1][2]);
            /* SPAN THE SHORT WAY, as any joist does -- which means bearing on the
               two LONG edges. I had picked the long edges' opposite pair, so the
               rafters were crossing 9.75 m instead of 7.32 and the whole roof was
               spanning the wrong way. The bearing edges are the ones the SHORTER
               dimension separates. */
            if (sA <= sB) { lo = { a: q[1], b: q[2] }; hi = { a: q[0], b: q[3] }; }
            else          { lo = { a: q[0], b: q[1] }; hi = { a: q[3], b: q[2] }; }
            /* THE PRODUCT MAY SAY WHICH WAY THE RAFTERS RUN (core-v787, TWO STORY
               R4b: the roof over a set-back storey is two rectangles, and the
               short way of one of them lands its rafters on a line with no wall
               under it). H.spanAxis(pl) -> 'x' | 'z': the rafters run along that
               axis, so they bear on the two edges running the other way. Nothing
               answered: the short way, as before. */
            var fsp = null;
            try { fsp = (typeof H.spanAxis === 'function') ? H.spanAxis(pl) : null; } catch (eSp) { fsp = null; }
            if (fsp === 'x' || fsp === 'z') {
              var e01z = Math.abs(q[1][2] - q[0][2]) > Math.abs(q[1][0] - q[0][0]);
              if (e01z === (fsp === 'x')) { lo = { a: q[0], b: q[1] }; hi = { a: q[3], b: q[2] }; }
              else                        { lo = { a: q[1], b: q[2] }; hi = { a: q[0], b: q[3] }; }
            }
          }

          /* the fall: from the middle of the low edge to the middle of the high one */
          var lmx = (lo.a[0] + lo.b[0]) / 2, lmz = (lo.a[2] + lo.b[2]) / 2, lmy = (lo.a[1] + lo.b[1]) / 2;
          var hmx = (hi.a[0] + hi.b[0]) / 2, hmz = (hi.a[2] + hi.b[2]) / 2, hmy = (hi.a[1] + hi.b[1]) / 2;
          var dx = hmx - lmx, dy = hmy - lmy, dz = hmz - lmz;
          var horiz = Math.hypot(dx, dz);
          var slope = Math.atan2(dy, horiz);
          var yaw = Math.atan2(-dz, dx);
          var span = Math.hypot(horiz, dy);
          var runLen = Math.hypot(lo.b[0] - lo.a[0], lo.b[2] - lo.a[2]);
          if (span < 0.05 || runLen < 0.05) continue;

          /* the section that actually crosses it, chosen per plane */
          if (!trussed) {
            var sec = roofSection();
            RAFTER.t = sec.t;
            RAFTER.d = sec.d;
            tally.section = sec;
          }

          /* NO BEAM DOWN THE MIDDLE. There was one, and it ran at rafter level
             straight through every rafter it was supposed to help -- a member
             nothing could be built around. A rafter either reaches or it does not;
             halving it with something in its own way is not a third option.

             So the span is capped instead, and the timber is deep enough to cross
             what is left. A trussed gable has no cap, because a truss is made to
             the span it is asked for.
          */

          /* rafters, stepped along the low edge and following it up -- skipped on a
             trussed roof, where the top chords already are the rafters */
          /* AGAINST THE STUD, NOT IN THE MIDDLE OF THE BAY.

             Half a bay off kept them apart, but a rafter floating in the middle of a
             gap can only ever be fixed downwards. Put face to face with the stud it
             gains a second fixing -- a full-depth face nailed straight into the
             vertical -- on top of bearing on the bearer.

             The offset is therefore half a stud plus half a rafter: exactly enough
             for the two to touch and no more. */
          var offs = (!trussed && API.STATE.roof.type === 'flat')
            ? (STUD.t / 2 + (STUD.t + RAFTER.t) / 2) : 0;
          var steps = [], t = offs;
          if (trussed) steps = [];
          else {
          var lastAt = runLen - offs;
          /* WHICH SET-OUT RAN, SAID BEFORE IT RUNS.

             Three conditions guard the stud-derived path and the log only spoke
             from INSIDE it -- so when it was skipped there was silence, and silence
             reads as a change that did not arrive. Two versions have now gone into
             wondering whether the code was delivered when the question was whether
             it was reached. */
          ctx.log('RAFT path ' + ((!trussed && API.STATE.roof.type === 'flat' && _bearStuds.length)
                    ? 'from STUDS' : 'from MODULE') +
                  '  trussed=' + (trussed ? 1 : 0) +
                  ' type=' + API.STATE.roof.type +
                  ' bearStuds=' + _bearStuds.length);

          if (!trussed && API.STATE.roof.type === 'flat' && _bearStuds.length) {
            /* ONE RAFTER PER STUD, PUT WHERE THE STUD ACTUALLY IS. Stepping by the
               module and hoping got the middle right and both ends wrong. */
            /* NO BLANKET OFFSET. I shifted every stud-derived rafter by half a stud
               plus half a rafter, reasoning that it should sit face-on against the... */
            steps = _rafterStepsFromStuds(lo, runLen);
            /* the stations, once, so a gap can be read rather than guessed at */
            var mm = [], sq2;
            for (sq2 = 0; sq2 < steps.length; sq2++) mm.push(Math.round(steps[sq2] * runLen * 1000));
            var gaps = [], sq3;
            for (sq3 = 1; sq3 < mm.length; sq3++) gaps.push(mm[sq3] - mm[sq3 - 1]);
            ctx.log('RAFT ' + mm.length + ' at ' + mm.join(' '));
            ctx.log('RAFT gaps ' + gaps.join(' ') + '  module ' + Math.round(sp * 1000));
          } else {
            /* ONE SET-OUT FOR THE MONO TOO. The rafters stepped the module from
               the rake EDGE of the roof, while the eaves closures, the baffles and
               the battens all step it from the wall FACES (_trussStations) -- so on
               a mono the closure of every bay landed under the middle of a rafter
               bay and read as a block split in two, and the last rafter and the
               plate end never agreed. The stations are the building's; the
               rafters take them like the trusses do, as fractions of this edge. */
            var STm = [], sq7;
            try { STm = _trussStations() || []; } catch (eSt) { STm = []; }
            var alongLm = (API.STATE.roof.axis === 'l');
            var a0m = alongLm ? lo.a[2] : lo.a[0];
            var dirm = alongLm ? ((lo.b[2] - lo.a[2]) >= 0 ? 1 : -1) : ((lo.b[0] - lo.a[0]) >= 0 ? 1 : -1);
            for (sq7 = 0; sq7 < STm.length; sq7++) {
              var fr7 = ((STm[sq7] - a0m) * dirm) / runLen;
              if (fr7 > -0.001 && fr7 < 1.001) steps.push(Math.max(0, Math.min(1, fr7)));
            }
            if (!steps.length) {
              while (t < lastAt - RAFTER.t) { steps.push(t / runLen); t += sp; }
              steps.push(lastAt / runLen);
            }
          }
          }
          var ex = (lo.b[0] - lo.a[0]), ez = (lo.b[2] - lo.a[2]);
          /* AND THESE ARE PUBLISHED TOO.

             Only the truss builder was saying where its members stand, so a
             mono-pitch published nothing and the battens above fell back to a
             derived module -- which could never have matched, because these steps
             are not a module at all: _rafterStepsFromStuds puts a rafter over every
             STUD. Guessing a spacing was hopeless against a set-out that follows
             the wall.

             Kept as FRACTIONS of the run. An absolute coordinate carries an origin
             and a direction with it and both have already been wrong once; a
             fraction of the same edge cannot be. */
          /* ONLY IF THIS PATH ACTUALLY PLACED ANY. On a trussed roof steps is set to
             [] a few lines above -- the trusses are built by their own function --
             so writing it here unconditionally wiped the fractions THEY had just
             published, three lines after they published them. The gable then fell
             back to a derived set-out and the battens went missing again.

             Publishing is not the same as publishing over someone. */
          /* the same, taken off the very edge these were set out along */
          if (steps.length) {
            var _memberPts = [];
            H.memberPts(_memberPts);
            for (var mq = 0; mq < steps.length; mq++) {
              _memberPts.push([lo.a[0] + (lo.b[0] - lo.a[0]) * steps[mq],
                               lo.a[2] + (lo.b[2] - lo.a[2]) * steps[mq],
                               -(lo.b[2] - lo.a[2]), (lo.b[0] - lo.a[0])]);   /* across the eave */
            }
          }

          for (j = 0; j < steps.length; j++) {
            var u = steps[j];
            /* ON THE PLANE, NOT UNDER IT. The plane the core hands back is the
               BEARING line -- the top of the plate the rafter lands on -- so the
               rafter stands on it. Hanging it below put every rafter inside the
               wall it was supposed to be carried by. */
            _onPlane(T, g, mat, span, RAFTER.t, RAFTER.d, slope, yaw,
                     (lmx + hmx) / 2 + (u - 0.5) * ex,
                     (lmy + hmy) / 2,
                     (lmz + hmz) / 2 + (u - 0.5) * ez,
                     RAFTER.d / 2);
            tally.rafters++;
            tally.rafterLen += span;
          }

          /* NO SHEATHING YET. It is not frame, it is the first board of the roof
             BUILD-UP -- sheathing, membrane, counter-batten, covering -- and those
             arrive together as their own layer. Leaving it here would hide the
             trusses under a lid on the very version we want to look at them. The
             area is still counted, because the build-up will want it. */
          tally.sheath += pl.area;
        }

        /* ONE RIDGE BOARD, NOT ONE PER PLANE. Both slopes land on the same timber,
           so building it inside the loop above would put two in the same place. */
        /* NO RIDGE BOARD ON A TRUSSED ROOF. The apex is a plated joint between two
           top chords; a board there would be a member nothing needs. */
        if (!trussed && R.length === 2 && R[0].ridge) {
          var ra = R[0].ridge[0], rb = R[0].ridge[1];
          var rl = Math.hypot(rb[0] - ra[0], rb[2] - ra[2]);
          var rgeo = new T.BoxGeometry(RAFTER.t, RAFTER.d, rl);
          rgeo.rotateY(Math.atan2(-(rb[2] - ra[2]), rb[0] - ra[0]) + Math.PI / 2);
          rgeo.translate((ra[0] + rb[0]) / 2, ra[1] - RAFTER.d / 2, (ra[2] + rb[2]) / 2);
          g.add(new T.Mesh(rgeo, mat));
          tally.ridge = rl;
        }

        _asmCode(ctx, EAVE_POST.code, 'side');
        try { if (SHOW_POSTS) _eavePosts(ctx); } catch (ePP) { ctx.log('posts    THREW ' + (ePP && ePP.message)); }
        /* ANY HOUSE: the L's California valley over the bar's slope */
        if (H.afterPosts) H.afterPosts(ctx, trussed);
        /* THE BUILD-UP GOES ON HERE, AND IT LIVES AT THE END OF THE FILE.

           One call, so that adding the next layer of the roof is a patch to the
           last zone instead of an edit up here. Every step of it is drawn in the
           order it is really laid, which is also the order it will animate in. */
        try { _roofSkin(ctx); } catch (eRS) { ctx.log('skin     THREW ' + (eRS && eRS.message)); }
        _asmMark(ctx, 'gutters', 'side', 'Gutters and downpipes', GUT.gutter);
        try { if (SHOW_GUTTERS) _gutters(ctx); } catch (eGG) { ctx.log('THREW gutter ' + (eGG && eGG.message)); }
        /* GA / ANY HOUSE: the splash blocks under the downspouts */
        if (H.afterGutters) H.afterGutters(ctx);
        if (H.members) H.members(tally.rafters);
        ctx.log('roof     ' + API.STATE.roof.type + ', ' + tally.planes + ' plane(s), ' +
                tally.rafters + ' member(s) at ' + API.fmtLen(sp) + ' oc');
        /* ANY HOUSE: the heavy rafter's check */
        if (H.say) H.say(ctx, tally);
        if (tally.section) {
          ctx.log('roof     rafters ' + tally.section.label + ' (' +
                  Math.round(tally.section.d * 1000) + ' mm deep) -- chosen to cross the span ' +
                  'in one piece, no beam under them');
        }
        ctx.log('roof     timber: rafters ' + tally.rafterLen.toFixed(1) + ' m' +
                (tally.ridge ? (', ridge ' + tally.ridge.toFixed(1) + ' m') : '') +

                ' | roof area ' + API.fmtArea(tally.sheath) + ' (not covered yet)');

        if (API.STATE.roof.type === 'flat') {
          _why(ctx, 'roof     rafters run to the outer face of the panels, bearing ON the ' +
                  'bearer at the inside face, each one face to face with a stud so it is ' +
                  'fixed sideways into the vertical as well as down onto the bearer');
          _why(ctx, 'roof     one at each end of the run against the inside face of the end ' +
                  'wall, so the deck has an edge to land on there');
        }
        if (tally.section) {
          /* and the bodies take the name the bill just used */
          if (H.frameMark && _frameMark && tally.section.code) _frameMark.code = tally.section.code;
          _add(LAYER, tally.section.code, tally.rafterLen);
          if (tally.rafters) { _add(LAYER, TRUSS_TIE.code, tally.rafters * 2); _add(LAYER, TRUSS_TIE.nail, tally.rafters * 2 * TRUSS_TIE.nailsPerTie); }
        } else {
          _add(LAYER, RAFTER.code, tally.rafterLen + tally.ridge);
        }
        _why(ctx, 'roof     every member tilted by the pitch and turned down the fall, ' +
                'so nothing rolls about its own length and the sheathing lies flush on top');
        if (trussed) _why(ctx, 'roof     no rafters and no ridge board -- the trusses are both');

        /* THE TRUSS STAGE, AND IT IS A HANDOVER AS MUCH AS A PICTURE.

           Nothing can be named while every member shares one timber brown and
           the sheathing stands in front of it. So the roof keeps its trusses and
           loses everything else, each component in its own colour, and the same
           components are listed in the log with what they measure -- the colour
           in the model and the line in the snapshot are the same word, so a
           member can be pointed at from either end (Marek: abych tu mohl presne
           rici ktery prvek musime resit).

           The hip members are marked by the hip framing rather than here, so
           they are recognised by the name they already carry. */
        if (PAINT && PAINT.isOn()) {
          var TP = {}, tpShown = 0, tpHidden = 0, tpPlates = 0, HIPB = [], CALB = [];
          g.traverse(function (o) {
            if (!o.isMesh) return;
            var part = (o.userData && o.userData.trussPart) || '';
            if (!part) {
              var mk = o.userData && o.userData.asm;
              var kk = mk ? (typeof mk === 'string' ? mk : (mk.key || '')) : '';
              if (kk === 'hip_chord' || kk === 'hip_web') part = kk;
            }
            if (!part) { o.visible = false; tpHidden++; return; }
            /* AND THE PLATES ARE BACK ON. They were hidden while the joints
               underneath were being cut; with those settled, the view should show
               the truss as it is built (Marek: spolu se zapnutim stycnikovych
               desek). */
            if (part === 'plate') tpPlates++;
            tpShown++;
            /* the hips are collected first and painted after: which two are the
               longest is only known once they have all been seen */
            /* the hip and the centre truss keep their timber now; they are still
               collected so the log can say how many of each were built */
            if (o.userData && o.userData.hipNo) HIPB.push(o);
            else if (o.userData && o.userData.calSeq != null) CALB.push(o);
            /* THE COLOURS ARE OFF AGAIN. They were a way of naming a piece while
               it was being cut; every piece named that way is cut. The table and
               the names stay where they are -- switching them back on is this one
               block, and it took one round each time (Marek: muzes strechu
               odbarvit). */
            var dd = _trussSides(o);
            if (!TP[part]) TP[part] = { n: 0, len: {}, sec: {} };
            TP[part].n++;
            var Lmm = Math.round(dd[0] * 1000);
            TP[part].len[Lmm] = (TP[part].len[Lmm] || 0) + 1;
            var smm = Math.round(dd[1] * 1000) + 'x' + Math.round(dd[2] * 1000);
            TP[part].sec[smm] = (TP[part].sec[smm] || 0) + 1;
          });

          /* THE TWO LONGEST HIPS, AND NOTHING ELSE.

             Every hip member carries its hip's number and its own place in it,
             so the two longest are picked here and each of their pieces gets its
             own colour off one list. Every other body keeps its timber, which is
             what makes the two that matter stand out at all. */
          /* THE HIP AND THE CENTRE TRUSS ARE DONE, so they are only counted now.
             Their legend served its purpose; leaving it on would fight with the
             short trusses, which are what is being looked at. */
          var HL = {}, hb2, hq8;
          for (hq8 = 0; hq8 < HIPB.length; hq8++) {
            hb2 = HIPB[hq8];
            if (HL[hb2.userData.hipNo] == null) HL[hb2.userData.hipNo] = hb2.userData.hipLen || 0;
          }
          var nHip = 0;
          for (hq8 in HL) { if (HL.hasOwnProperty(hq8)) nHip++; }
          if (nHip || CALB.length) {
            ctx.log('truss geom  settled and left in plain timber: ' + nHip + ' hip(s), ' +
                    CALB.length + ' member(s) of the centre truss(es)');
          }

          var FITNAME = { wvert: 'STRAIGHT TRUSS UPRIGHT', web: 'STRAIGHT TRUSS DIAGONAL',
                          post: 'STRAIGHT TRUSS KING POST' };
          var fq, FR, forder = ['wvert', 'web', 'post'];
          for (fq = 0; fq < forder.length; fq++) {
            FR = _TK.fit()[forder[fq]];
            if (!FR || !FR.n) continue;
            ctx.log('truss geom  FIT ' + FITNAME[forder[fq]] + '  n=' + FR.n +
                    '  into the chord ' + Math.round(FR.chord * 1000) +
                    ' mm (worst at p ' + Math.round(FR.at * 1000) +
                    '), through the tie ' + Math.round(FR.tie * 1000) +
                    ' mm, into the post ' + Math.round(FR.post * 1000) +
                    ' mm -- anything above nought is a collision');
          }
          var TKF = _TK.floorCut();
          if (TKF.n) {
            ctx.log('truss geom  TOP CHORD underside planed off on the tie top face: ' +
                    TKF.n + ' chord(s), deepest cut ' +
                    Math.round(TKF.max * 1000) + ' mm');
          }
          ctx.log('truss geom  STAGE TRUSSES -- ' + tpShown + ' truss body(s) shown (' +
                  tpPlates + ' connector plate(s) among them), ' +
                  tpHidden + ' other roof body(s) hidden, wall panels left standing');
          var tpi, tpk, TPR, tpLen, tpSec, tpv, tpList;
          for (tpi = 0; tpi < PAINT.ORDER.length; tpi++) {
            tpk = PAINT.ORDER[tpi];
            TPR = TP[tpk];
            if (!TPR) continue;
            tpLen = [];
            for (tpv in TPR.len) { if (TPR.len.hasOwnProperty(tpv)) tpLen.push(parseInt(tpv, 10)); }
            tpLen.sort(function (a, b) { return b - a; });
            tpList = [];
            for (tpv = 0; tpv < tpLen.length && tpv < 8; tpv++) {
              tpList.push(tpLen[tpv] + ' x' + TPR.len[tpLen[tpv]]);
            }
            if (tpLen.length > 8) tpList.push('and ' + (tpLen.length - 8) + ' more length(s)');
            tpSec = [];
            for (tpv in TPR.sec) { if (TPR.sec.hasOwnProperty(tpv)) tpSec.push(tpv + ' x' + TPR.sec[tpv]); }
            ctx.log('truss geom  ' + (PAINT.NAME[tpk] || tpk) + '  n=' + TPR.n +
                    '  section ' + tpSec.join(' + ') +
                    '  length ' + tpList.join(' | '));
          }
          for (tpk in TP) {
            if (!TP.hasOwnProperty(tpk) || PAINT.ORDER.indexOf(tpk) >= 0) continue;
            ctx.log('truss geom  ' + ((PAINT.PLAIN.indexOf(tpk) >= 0)
                    ? ('STRAIGHT TRUSS ' + tpk + ' (no colour)') : ('UNNAMED PART ' + tpk)) +
                    '  n=' + TP[tpk].n);
          }
          if (!tpShown) ctx.log('truss geom  nothing to show -- this roof has no trusses');
        }
    }

    /* =========================================================================
       THE ROOF SKIN MODULE (core-v787, the eighth shared module of the house
       line, its second part -- status/modules.md 8b). The roof over the frame,
       written once: a flat roof handed to the product's warm deck; on a hip,
       the hip end framed -- the centre truss, the hips from the same peaks to
       the corners, their heels, the jack trusses down the end and the side
       slopes, the plates, the last bays' diagonals, all recorded for the PDF's
       truss sheets and billed (ROT00001 / ROT00002, the plates by size); the
       outline shell when that review is on; then the build-up step by step --
       deck, underlay, battens, edges, covering, ridge -- with what each step
       did said in the log. The ANY HOUSE text (the ADU's plus the L's flush
       hip end) with the products' own parts behind hooks.

         API.houseRoofSkin(ctx, {
           api, layer ('adu-roof'), wood, RAFTER, CHORD, WEB, PLATE, STEEL,
           TRUSS_PANELS, TRUSS_PANEL_MAX, TRUSS_PANEL_MIN, HEEL_SET,
           HEEL_PLATE_DROP, HEEL_PLATE_OUT, HIP_FRAME, SHELL_ONLY, ROOF_STEP,
           ROOF_DECK, ROOF_UL, ROOF_BATTEN, ROOF_EDGE, ROOF_RIDGE, FLAT_ROOF,
           COVERINGS, roofPlanes, TR, TK, covDef, covKey, deckPlane,
           faceOutline, footOnChord, heelPost, hipGroup, hipRec, seatOnHip,
           webPlaneEnds, quad, trussStations, skinDeck, skinUnderlay,
           skinBattens, skinEdges, skinCovering, skinRidge, add,
           vars -- the product's counters and lists, read and written through
                   accessors (calSeq, hipNo, hipSeq, hipPostsDropped,
                   hipSideJacks, jackBare, footFlat, memberPts, trussTop, and
                   ANY HOUSE's ahCap)
           skinFlat   the warm flat roof (not the shed family)
           ridgeCode  RANCH / GA / ANY HOUSE -- the ridge's own name
           xg:  { batCut, covCut } -- RANCH, cut out under the cross gables
           bare       TAURUS / GARDEN BAR -- Matt's roof: no deck, no underlay
           ah:  { flushHipV, flushSaid, openCopies } -- the ANY HOUSE L's
                flush hip end, open on the wing's side
         })

       Carried: a ridge shorter than a bay keeps the module for the jacks
       (adu-v1419) to SAUNA and the shed family; the short-jack counter
       started over on every roof to the shed family; TRUSS_PANEL_MIN (600,
       the value every other product has) to TAURUS and GARDEN BAR, whose
       copy used it without declaring it -- their hip roof threw and drew no
       build-up at all.
       ========================================================================= */
    function houseRoofSkin(ctx, H) {
      /* the product's own pieces, under the names the text has always used */
      var API = H.api, LAYER = H.layer, STRUCT_WOOD = H.wood, RAFTER = H.RAFTER, CHORD = H.CHORD, WEB = H.WEB,
          PLATE = H.PLATE, STEEL = H.STEEL, TRUSS_PANELS = H.TRUSS_PANELS, TRUSS_PANEL_MAX = H.TRUSS_PANEL_MAX,
          TRUSS_PANEL_MIN = (H.TRUSS_PANEL_MIN != null) ? H.TRUSS_PANEL_MIN : 0.600,
          HEEL_SET = H.HEEL_SET, HEEL_PLATE_DROP = H.HEEL_PLATE_DROP, HEEL_PLATE_OUT = H.HEEL_PLATE_OUT,
          HIP_FRAME = H.HIP_FRAME, SHELL_ONLY = H.SHELL_ONLY, ROOF_STEP = H.ROOF_STEP, ROOF_DECK = H.ROOF_DECK,
          ROOF_UL = H.ROOF_UL, ROOF_BATTEN = H.ROOF_BATTEN, ROOF_EDGE = H.ROOF_EDGE, ROOF_RIDGE = H.ROOF_RIDGE,
          FLAT_ROOF = H.FLAT_ROOF, COVERINGS = H.COVERINGS;
      var roofPlanes = H.roofPlanes, _TR = H.TR, _TK = H.TK, _covDef = H.covDef, _covKey = H.covKey,
          _deckPlane = H.deckPlane, _faceOutline = H.faceOutline, _footOnChord = H.footOnChord,
          _heelPost = H.heelPost, _hipGroup = H.hipGroup, _hipRec = H.hipRec, _seatOnHip = H.seatOnHip,
          _webPlaneEnds = H.webPlaneEnds, quad = H.quad, _trussStations = H.trussStations,
          _skinDeck = H.skinDeck, _skinUnderlay = H.skinUnderlay, _skinBattens = H.skinBattens,
          _skinEdges = H.skinEdges, _skinCovering = H.skinCovering, _skinRidge = H.skinRidge,
          _add = function (cat, code, q) { H.add(code, q); };
      /* the product's counters and lists: its own variables, other parts of the
         product read them (the truss sheets, the battens, the ceiling) */
      var SV = H.vars;
      /* the products' own roofs, each behind its hook (status/modules.md 8b):
         XG  RANCH -- the main roof cut out under the cross gables
         AH  ANY HOUSE -- the L's flush hip end, open on the wing's side */
      var XG = H.xg || null, AH = H.ah || null;
        SV.jackBare = 0;
        var R = API.STATE.roof || {};
        /* TWO SILENT EXITS, AND BOTH COST A ROUND TODAY. */
        var planes = [];
        try {
          planes = roofPlanes() || [];
        } catch (e) {
          ctx.log('THREW roofPlanes -- ' + (e && e.message ? e.message : e));
          return;
        }
        if (!planes.length) {
          ctx.log('THREW roofPlanes gave NO planes for type ' + R.type);
          return;
        }
        ctx.log('HIP  type=' + R.type + ' planes=' + planes.length +
                ' shell=' + (SHELL_ONLY ? 'on' : 'off'));

        /* THE TYPE DECIDES, NOT A LEFTOVER FLAG. */
        SV.hipSideJacks = [];
        if (R.type === 'flat') {
          /* the shed family has no flat roof: the warm deck went with the fork, so
             a saved project that still says flat is drawn as nothing here */
          if (!H.skinFlat) { ctx.log('THREW roof type flat -- this product has no flat roof; set gable or mono'); return; }
          try { H.skinFlat(ctx, planes); }
          catch (eF) { ctx.log('THREW FLAT -- ' + (eF && eF.message)); }
          return;
        }
        if (R.parapet) {
          ctx.log('HIP  roof.parapet is set but type is ' + R.type +
                  ' -- ignoring the flag, the type decides');
        }

        /* EACH STEP GUARDED ON ITS OWN. One throw used to take the whole build-up
           with it and the log said 'skin THREW' once, which does not say which
           layer -- and a layer that is silently absent looks exactly like a layer
           that was never written. */
        function step(n, name, fn) {
          if (!(ROOF_STEP < 0 || ROOF_STEP >= n)) return;
          try { fn(ctx, planes); }
          catch (e) { ctx.log('skin     STEP ' + n + ' (' + name + ') THREW ' + (e && e.message)); }
          /* and a line when it did NOT throw, so a step that silently builds
             nothing is distinguishable from one that was never reached */
          ctx.log('skin STEP ' + n + ' ' + name + ' ran');
        }
        /* THE ORDER HAD A STEP MISSING, AND THE MISSING ONE CHANGED THE OTHERS.

           It went deck, underlay, flashings, covering -- straight from the felt to
           the metal. Tiny puts what the covering is FIXED TO in between, and that
           matters twice over: it is the first layer decided by the covering the
           person picked, and the flashings belong ON it rather than under it.
           Drawn on the underlay they were a layer too low and the battens would
           have run over the top of them. */
        /* THE SHELL, REWRITTEN FROM SCRATCH. */
        /* sT and sG were declared inside the shell block, so lifting the framing
           out left it without the two names it draws through. Declared here, once,
           for both. */
        var sT = ctx.THREE, sG = ctx.group;

        if (HIP_FRAME) {
        /* THE HIP FRAMING WAS INSIDE THE SHELL BLOCK. */
          /* THE CENTRE TRUSS, DRAWN HERE BECAUSE HERE DEMONSTRABLY WORKS. */
          /* BOTH HIP ENDS, FULL SECTION, FULL HEIGHT. */
          _asmMark(ctx, 'roof_framing', 'top', 'Roof framing', RAFTER.code);
          /* TIMBER IS TIMBER AND STEEL IS STEEL.

             One material was doing both, so the plates came out the colour of wood
             -- which is worse than magenta, because a wrong colour that looks
             plausible is not questioned. The truss builder already keeps two: WOOD
             for the members and STEEL for the plates. Same two here. */
          var cm3 = new sT.MeshStandardMaterial({ color: STRUCT_WOOD, roughness: 0.85,
                                                 metalness: 0.0 });
          var pm3 = new sT.MeshStandardMaterial({ color: STEEL, roughness: 0.4,
                                                 metalness: 0.8 });

          /* bar3 is gone: it aimed members with lookAt and left the roll to chance,
             which is what produced the notched joints and the post that would not
             sit against the girder. Everything here is built in the truss's own
             vertical plane now, so there is one way of drawing a member. */

          var hipEnds = 0, chordL = 0, webL = 0, plates = 0, platesBig = 0, hips = 0;
          var pLog = [], pTag = '?';
          /* anyhouse-lib-v13: the hipped L's flush end, a hip end open on the
             wing's side (_ahFlushHipV) */
          var hipPL = planes.slice();
          if (AH) try { var FHV = AH.flushHipV(planes); if (FHV) hipPL.push(FHV); else if (AH.flushSaid()) ctx.log(AH.flushSaid()); } catch (eFV) { ctx.log('AH L  flush hip end threw ' + ((eFV && eFV.message) || eFV)); }
          for (sq = 0; sq < hipPL.length; sq++) {
            var PP = hipPL[sq];
            if (!PP || !PP.ridge || !PP.eave) continue;
            var rr = Math.hypot(PP.ridge[1][0] - PP.ridge[0][0],
                                PP.ridge[1][2] - PP.ridge[0][2]);
            if (rr >= 0.05) continue;               /* not a hip end */

            /* IT SITS ON THE PLATE, LIKE EVERY OTHER TRUSS.

               The bars are centred on their axis, and I ran the axis along the eave
               line -- which is the BEARING line, the top of the plate. So the
               bottom face ended half a chord below it and the truss hung 80 mm
               lower than its neighbours. Their axis is wallTop + chord/2; this one
               is now the same. */
            /* THE PEAK IS THE TRUSSES' PEAK, NOT THE PLANE'S.

               And the foot stops short of the eave rather than at it: a truss dies
               on the plate, the tail beyond is the rafter's overhang, and running
               the chord all the way out is what put it through the gutter. */
            /* ONE SOURCE FOR BOTH ENDS.

               The peak came from the trusses (4360) and the foot from the plane
               (4280 at its ridge), so the member ran at a slope that was neither
               the roof's nor the frame's -- which is the different angle he can
               see. Both ends now come from SV.trussTop, and where that is missing
               both come from the plane. Mixed is the one thing they cannot be. */
            var TT = SV.trussTop || { ridgeY: PP.ridge[0][1], eaveY: PP.eave[0][1] };
            var half3 = CHORD.d / 2;
            /* A RIGHT TRIANGLE, MEETING AT ONE POINT. */
            var apex0 = [PP.ridge[0][0], TT.ridgeY, PP.ridge[0][2]];
            var eFull = [(PP.eave[0][0] + PP.eave[1][0]) / 2,
                         TT.eaveY,
                         (PP.eave[0][2] + PP.eave[1][2]) / 2];
            var avx = eFull[0] - apex0[0], avz = eFull[2] - apex0[2];
            var avL = Math.hypot(avx, avz) || 1;
            var axZ2 = Math.abs(avz) >= Math.abs(avx);   /* which way the run goes */

            /* THE FOOT REACHES THE EAVE, SO THE SLOPE IS THE SHELL'S. */
            /* THE JUNCTION POST IS A CHORD, SO IT STEPS BACK FURTHER. */
            /* THE CHORDS REACH THE DOUBLED TRUSS, AND STOP ON ITS FACE.

               The peak used to stand 126 mm inboard of it: 62 mm for half the
               doubled truss and another 64 mm of clearance for its diagonal. The
               chords therefore died in mid-air and needed a post under them. A
               hip end bears on that truss -- so the setback is now HALF THE
               DOUBLED TRUSS and nothing else, which puts the chord ends exactly
               on its face (Marek: pasnice musi byt az ke zdvojenemu vazniku). */
            var girderHalf = CHORD.t / 2 + (CHORD.t + 0.004) / 2;
            /* still wanted further down, where a jack steps clear of the hip's
               own diagonal -- only the PEAK stopped using it */
            var diagReach = (WEB.t + WEB.d) * Math.SQRT1_2;
            var clear0 = girderHalf;
            var apex = [apex0[0] + avx / avL * clear0,
                        TT.ridgeY + (TT.eaveY - TT.ridgeY) * (clear0 / avL),
                        apex0[2] + avz / avL * clear0];

            var eMid = [eFull[0], TT.eaveY, eFull[2]];

            /* the post drops straight from the peak; the chord runs from its foot */
            var under = [apex[0], TT.eaveY, apex[2]];
            /* anyhouse-lib-v13: the centre truss of an end open on one side is
               copied across that side -- what it adds is counted from here */
            var ahC0 = { n: sG.children.length, c: chordL, w: webL, p: plates, pb: platesBig, mk: (ctx.marks || []).length };
            SV.ahCap = PP.ahOpen ? [] : null;

            /* THE CHORDS ARE BUILT UPRIGHT TOO. */
            function inPlane(a, b, wid, dep, partH, plumbAt, floorH, topPl, capE) {
              var dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
              var runL2 = Math.hypot(dx, dz);
              var L = Math.hypot(runL2, dy);
              if (L < 0.05) return 0;
              /* EVERY HIP MEMBER IS RECORDED AS IT IS MADE.

                 A hip end has no truss object: the members are drawn one at a
                 time and their lengths added up, so there was no shape for the
                 sheet to draw and the short corner trusses never appeared on it.

                 Each piece is written down here with where it sits, and grouped
                 afterwards -- members standing over the same point along the
                 hip belong to the same jack truss, which is what makes them one
                 drawing (Marek: musi tam byt vsechny vazniky strechy, i ty
                 kratke, narozni). */
              /* EVERY MEMBER IS RECORDED ONCE, AND IT WAS BEING RECORDED TWICE.

                 The same push stood here in two identical copies -- a leftover
                 from an older edit that nobody tripped over while the records
                 were only used for lengths. The sheets read them as bodies, so
                 every member of the centre truss would have been drawn on top of
                 itself.

                 EVERY HIP MEMBER IS RECORDED AS IT IS MADE.

                 A hip end has no truss object: the members are drawn one at a
                 time and their lengths added up, so there was no shape for the
                 sheet to draw and the short corner trusses never appeared on it.

                 Each piece is written down here with where it sits, and grouped
                 afterwards -- members standing over the same point along the
                 hip belong to the same jack truss, which is what makes them one
                 drawing (Marek: musi tam byt vsechny vazniky strechy, i ty
                 kratke, narozni). */
              /* AND IT CARRIES ITS REAL OUTLINE ONTO THE SHEET. Every other
                 member of a hip end is recorded with the shape of its own face,
                 bevels and all; the centre truss's members were pushed straight
                 into the list with none, so its drawing was made of plain
                 rectangles laid on the members' axes -- which is why its
                 diagonals looked as if they stopped short of the chord and the
                 uprights (Marek: u T2 porad nejsou natazene sikme vzpery az ke
                 spoji). The outline is taken from the finished body below. */
              var recH = null;
              try {
                recH = {
                  x1: a[0], y1: a[1], z1: a[2],
                  x2: b[0], y2: b[1], z2: b[2],
                  d: dep, t: wid,
                  kind: (dep === WEB.d) ? 'web' : 'chord',
                  part: partH || 'cal_rafter',
                  code: (dep === WEB.d) ? 'ROT00002' : 'ROT00001',
                  /* the centre truss names itself as well, so no sheet is left to
                     the station guess */
                  grp: 'CTR' + (hipEnds + 1)
                };
                _TR.hipPush(recH);
                if (SV.ahCap) SV.ahCap.push({ rec: recH });
              } catch (eHM) {}
              _asmMark(ctx, (dep === WEB.d) ? 'hip_web' : 'hip_chord', 'top',
                       (dep === WEB.d) ? 'Hip truss web' : 'Hip truss chord',
                       (dep === WEB.d) ? 'ROT00002' : 'ROT00001');
              /* a break across the member is only needed where its underside has to
                 be planed off partway along */
              var segL = (floorH != null) ? 2 : 1;
              var segD = capE ? 2 : 1;              /* a corner row, for a two-face end */
              var m = new sT.Mesh(
                axZ2 ? new sT.BoxGeometry(wid, dep, L, 1, segD, segL)
                     : new sT.BoxGeometry(L, dep, wid, segL, segD, 1), cm3);
              m.position.set((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2);
              /* AND THIS BODY SAYS WHICH TRUSS IT IS, TOO.

                 Every other member of a hip end is written down through the
                 recorder, which marks the body on the way past. The centre
                 truss's members are made here and pushed straight into the list,
                 so they were the one family with a record and no mark -- and its
                 sheet came out as the single one of eleven with nothing to
                 photograph (Marek: T2 tag CTR1). */
              m.userData.trussGrp = 'CTR' + (hipEnds + 1);
              _TR.tag();
              /* tilt in the truss's own vertical plane, nothing else */
              var ang = Math.atan2(dy, runL2) * ((axZ2 ? dz : dx) < 0 ? 1 : -1);
              if (axZ2) m.rotation.x = ang; else m.rotation.z = -ang;

              /* AND THE RAFTER IS PLANED OFF ON THE BOTTOM CHORD'S TOP FACE.

                 The two chords of the end truss converge at the eave, so over the
                 last stretch the rafter's underside is BELOW the bottom chord's
                 top face and the two occupy the same timber -- the same thing the
                 straight truss had, and the same cut answers it. The rafter keeps
                 its full depth up to where its underside meets that face, and from
                 there its underside IS that face (Marek: magenta musi byt orezana
                 spodni acid green pasnici). */
              if (floorH != null) {
                var lenA2 = axZ2 ? 2 : 0;
                var sinF = Math.sin(ang), cosF = Math.cos(ang);
                if (Math.abs(sinF) > 1e-4 && cosF > 1e-4) {
                  var posF = m.geometry.attributes.position, iF;
                  var hD3 = dep / 2, hL3 = L / 2;
                  var cyF2 = (a[1] + b[1]) / 2;
                  /* where the member's own underside crosses that face */
                  var cBrk = (cyF2 - hD3 * cosF - floorH) / sinF;
                  var brkIn3 = (cBrk > -hL3 + 0.005 && cBrk < hL3 - 0.005);
                  if (brkIn3) {
                    for (iF = 0; iF < posF.count; iF++) {
                      var cc = (lenA2 === 2) ? posF.getZ(iF) : posF.getX(iF);
                      if (Math.abs(cc) > 1e-6) continue;          /* the middle ring */
                      if (lenA2 === 2) posF.setZ(iF, cBrk); else posF.setX(iF, cBrk);
                    }
                  }
                  /* AND IT ENDS WHERE ITS TOP FACE MEETS THAT FACE TOO.

                     Past that point the member is wholly below the other one --
                     there is no timber there to cut, only a sliver lying inside
                     the bottom chord. So the low end is brought back to where the
                     two faces cross and the member finishes in the corner they
                     make, which is what a rafter dying on a chord looks like. */
                  var lowEnd = (sinF > 0) ? hL3 : -hL3;
                  var cTop = (cyF2 + hD3 * cosF - floorH) / sinF;
                  /* AND NOT PAST THE END OF THE MEMBER IT LIES ON.

                     The taper runs out to a knife edge, and the last of it sits
                     beyond where the bottom chord itself stops -- a small spike
                     hanging in the air off the end of the eave. The bottom chord's
                     end is a plumb face; the rafter is cut on it too (Marek: jeji
                     spicku oriznout podle cela spodni acid green). */
                  var cEnd = hL3;
                  if (cTop > cEnd) cTop = cEnd;
                  /* THE SAME TWO CUTS AS _footOnChord: level on the chord's top face,
                     plumb on its end, the corner where they meet on both. Squashing
                     the end to one edge and squaring it afterwards lifted the
                     underside corner off the chord (Marek: nejsou orizle vodorovnou
                     plochou). */
                  var Hp3 = lowEnd * cosF, Dp3 = floorH - cyF2;
                  var yB3 = Dp3 * cosF + Hp3 * sinF, zB3 = (Hp3 - yB3 * sinF) / cosF;
                  var two3 = brkIn3 && (yB3 > -hD3 + 1e-4) && (yB3 < hD3 - 1e-4) &&
                             ((sinF > 0) ? (zB3 > cBrk) : (zB3 < cBrk));
                  if (two3) {
                    for (iF = 0; iF < posF.count; iF++) {
                      var ce3 = (lenA2 === 2) ? posF.getZ(iF) : posF.getX(iF);
                      if (Math.abs(ce3 - lowEnd) > 1e-6) continue;
                      var yE3 = posF.getY(iF), cN3;
                      if (yE3 <= yB3) { posF.setY(iF, yB3); cN3 = zB3; }
                      else cN3 = (Hp3 - yE3 * sinF) / cosF;
                      if (lenA2 === 2) posF.setZ(iF, cN3); else posF.setX(iF, cN3);
                    }
                    SV.footFlat.n++; SV.footFlat.ends++;
                  } else if ((sinF > 0 && cTop < hL3 - 0.002 && cTop > -hL3) ||
                      (sinF < 0 && cTop > -hL3 + 0.002 && cTop < hL3)) {
                    for (iF = 0; iF < posF.count; iF++) {
                      var ce = (lenA2 === 2) ? posF.getZ(iF) : posF.getX(iF);
                      if (Math.abs(ce - lowEnd) > 1e-6) continue;
                      if (lenA2 === 2) posF.setZ(iF, cTop); else posF.setX(iF, cTop);
                    }
                  }
                  for (iF = 0; iF < posF.count; iF++) {
                    /* AND IT IS THE WHOLE SECTION, NOT JUST THE UNDERSIDE.

                       At the eave the two chords meet on one point, so the LAST
                       part of the rafter lies entirely below the bottom chord's
                       top face -- top corner and all. Lifting only the underside
                       left that sliver inside the other member. Every point below
                       the face is brought up onto it, so the rafter thins out to
                       nothing exactly where the two faces cross and nothing of it
                       is left inside the chord. */
                    var yF = posF.getY(iF);
                    var cF = (lenA2 === 2) ? posF.getZ(iF) : posF.getX(iF);
                    var wantF = (floorH - cyF2 + cF * sinF) / cosF;
                    if (wantF > hD3) wantF = hD3;
                    if (wantF > yF) posF.setY(iF, wantF);
                  }
                  /* and squared to the plumb at the end, so no corner reaches past
                     the face the chord below it stops on */
                  var tnF = sinF / cosF;
                  for (iF = 0; iF < posF.count; iF++) {
                    var y3 = posF.getY(iF);
                    var c3 = (lenA2 === 2) ? posF.getZ(iF) : posF.getX(iF);
                    if (sinF > 0) {
                      var bA = hL3 - y3 * tnF;
                      if (c3 > bA) { if (lenA2 === 2) posF.setZ(iF, bA); else posF.setX(iF, bA); }
                    } else {
                      var bB = -hL3 - y3 * tnF;
                      if (c3 < bB) { if (lenA2 === 2) posF.setZ(iF, bB); else posF.setX(iF, bB); }
                    }
                  }
                  posF.needsUpdate = true;
                  m.geometry.computeVertexNormals();
                }
              }

              /* AND ITS HEAD IS CUT PLUMB WHERE IT LANDS ON THE DOUBLED TRUSS.

                 That truss stands across the run, so its face is a VERTICAL
                 plane. A member cut square to its own length meets a vertical
                 face along one arris only and gapes above or below it; a sloping
                 chord has to be cut at its own slope to bed flat. So the end
                 face is laid on the vertical: every point across the member's
                 depth is moved along its length by its own height times the
                 slope, which is what makes them all land on one plane. */
              if (plumbAt) {
                var lenAx = axZ2 ? 2 : 0;            /* the axis the length lies on */
                var runIx = axZ2 ? 2 : 0;            /* and the world axis it runs along */
                var endPt = (plumbAt === 'a') ? a : b;
                var othPt = (plumbAt === 'a') ? b : a;
                var tgt = ((endPt[runIx] > othPt[runIx]) ? 1 : -1) * (L / 2);
                var tanE = Math.tan(ang);
                var posI = m.geometry.attributes.position, iI;
                for (iI = 0; iI < posI.count; iI++) {
                  var cI = (lenAx === 2) ? posI.getZ(iI) : posI.getX(iI);
                  if (Math.abs(cI - tgt) > 1e-6) continue;
                  var yI = posI.getY(iI);
                  if (lenAx === 2) posI.setZ(iI, cI - yI * tanE);
                  else posI.setX(iI, cI - yI * tanE);
                }
                posI.needsUpdate = true;
                m.geometry.computeVertexNormals();
              }
              /* AND AN UPRIGHT DIES ON THE CHORD ABOVE IT, ON ITS UNDERSIDE.

                 The uprights ran from the bottom chord's AXIS to the rafter's
                 AXIS, so each one buried half a chord depth in the timber at
                 both ends. The foot is set on the bottom chord's top face by the
                 caller; the head is cut here, on the rafter's underside -- which
                 SLOPES, so the head is not a level cut: each point is carried up
                 the member until it meets that plane, and the 120 mm of depth
                 comes out as a bevel, not a step (Marek: orezane vrchni plochou
                 spodni pasnice a spodni plochou vrchni pasnice). */
              if (topPl) {
                var thT2 = Math.cos(ang), shT2 = Math.sin(ang);
                var lenT = axZ2 ? 2 : 0;
                var posT = m.geometry.attributes.position, iT;
                var cxT = (a[0] + b[0]) / 2, cyT2 = (a[1] + b[1]) / 2, czT = (a[2] + b[2]) / 2;
                /* where a local point lands in the world, for this tilt */
                function wT(lx2, ly2, lz2) {
                  if (axZ2) return [lx2, ly2 * thT2 - lz2 * shT2, ly2 * shT2 + lz2 * thT2];
                  return [lx2 * thT2 + ly2 * shT2, -lx2 * shT2 + ly2 * thT2, lz2];
                }
                var uAx = wT(0, 0, 1);
                if (!axZ2) uAx = wT(1, 0, 0);
                var denT = uAx[1] + (topPl.rx * uAx[0] + topPl.rz * uAx[2]) * topPl.s;
                var endT = (b[1] >= a[1]) ? 1 : -1;      /* the head is the higher end */
                var tgtT = endT * (L / 2) * ((axZ2 || true) ? 1 : 1);
                for (iT = 0; iT < posT.count; iT++) {
                  var lxT = posT.getX(iT), lyT = posT.getY(iT), lzT = posT.getZ(iT);
                  var cT = (lenT === 2) ? lzT : lxT;
                  if (Math.abs(cT - tgtT) > 1e-6) continue;
                  var wv = wT(lxT, lyT, cT);
                  var PT = [cxT + wv[0], cyT2 + wv[1], czT + wv[2]];
                  var rT = topPl.rx * (PT[0] - topPl.px) + topPl.rz * (PT[2] - topPl.pz);
                  var fT = PT[1] - (topPl.y - rT * topPl.s);
                  if (Math.abs(denT) < 1e-6) continue;
                  var dT2 = -fT / denT;
                  if (Math.abs(dT2) > L * 0.45) continue;
                  if (lenT === 2) posT.setZ(iT, cT + dT2); else posT.setX(iT, cT + dT2);
                }
                posT.needsUpdate = true;
                m.geometry.computeVertexNormals();
              }
              /* AND A WEB THAT DIES BETWEEN TWO FACES GETS THEM BOTH.

                 The caller works the ends out in the truss's own flat picture --
                 which face each point across the depth meets first, and where the
                 two cross -- and hands them over as three offsets along the
                 member's length plus the corner. All this does is write them into
                 the right end of the box. */
              if (capE) {
                /* AND WHICH WAY THE MEMBER RUNS INSIDE ITS OWN BOX DECIDES THE
                   SIGN OF EVERY ONE OF THOSE NUMBERS.

                   The solver hands the cuts back measured along the member's own
                   axis, from its middle, with the FOOT on the minus side and the
                   HEAD on the plus side. Every other member of a hip end is made
                   as a box turned to face its head, so that axis and the box's
                   own run the same way and the numbers go straight in. This one
                   is different: it is never turned in plan, only tilted, so its
                   box runs with the world -- and whenever the foot happens to sit
                   at the box's PLUS end, the member runs BACKWARDS inside it.

                   The cuts were written in anyway. The head then got the shape of
                   a foot and the corner landed on the wrong side of the depth,
                   which is why the sloping webs of the centre truss -- and only
                   of the centre truss -- died short of the corner between the top
                   chord and the upright, whatever the size of the building
                   (Marek: porad stredovy vaznik hip end nema natazene sikme
                   vzpery az do konce spoje). Running backwards, every offset and
                   the corner across the depth are negated. */
                var lenC = axZ2 ? 2 : 0;
                var runC = axZ2 ? 2 : 0;
                var aIsPlus = (a[runC] > b[runC]) ? 1 : -1;
                var revC = (aIsPlus === 1) ? -1 : 1;
                var hLC = L / 2, hDC = dep / 2;
                var posC = m.geometry.attributes.position, iC;
                for (iC = 0; iC < posC.count; iC++) {
                  var cC = (lenC === 2) ? posC.getZ(iC) : posC.getX(iC);
                  var yC = posC.getY(iC);
                  var kC0 = (yC > hDC * 0.5) ? 0 : ((yC < -hDC * 0.5) ? 2 : 1);
                  var kC = capE.flip ? (2 - kC0) : kC0;
                  var qSg = (capE.flip ? -1 : 1) * revC;
                  var set = null, qq = null;
                  if (Math.abs(cC - aIsPlus * hLC) < 1e-6 && capE.aOff) {
                    set = revC * capE.aOff[kC]; qq = capE.aQ;
                  } else if (Math.abs(cC + aIsPlus * hLC) < 1e-6 && capE.bOff) {
                    set = revC * capE.bOff[kC]; qq = capE.bQ;
                  }
                  if (set == null) continue;
                  if (lenC === 2) posC.setZ(iC, set); else posC.setX(iC, set);
                  if (kC0 === 1 && qq != null) posC.setY(iC, qSg * qq);
                }
                posC.needsUpdate = true;
                m.geometry.computeVertexNormals();
              }
              m.userData.trussPart = partH || 'cal_rafter';
              m.userData.calSeq = SV.calSeq++;
              /* the finished face, bevels and all, onto the sheet's record */
              if (recH) {
                try { recH.poly = _faceOutline(m.geometry, axZ2 ? 2 : 0, wid / 2); } catch (eFO2) {}
              }
              sG.add(m);
              return L;
            }

            /* THE HIP TRUSS WEARS WHAT IT IS BILLED AS.

               The whole block ran under the roof's 'roof_framing' mark, which
               names the rafter section -- so 199 m of hip truss was drawn as
               KVH60240 while the bill put it under ROT00001 and ROT00002.

               Two wrong fixes before this one. The first decided chord from web
               inside the drawing helper; the second put one mark before the
               first pair of chords. Both left the remaining fifteen meshes under
               whichever mark happened to be last, because this block draws in
               sixteen separate places.

               So every mesh says what it is, where it is made. The section is
               already in each BoxGeometry -- CHORD.d is 160 and WEB.d is 120 --
               so there is nothing to decide (Marek: chci to mit presne). */
            SV.calSeq = 0;
            chordL += inPlane(apex, eMid, CHORD.t, CHORD.d, 'cal_rafter', 'a',
                              TT.eaveY + CHORD.d / 2);                /* the rafter */
            chordL += inPlane(under, eMid, CHORD.t, CHORD.d, 'cal_bottom');   /* the bottom chord */
            /* THE POST IS BUILT UPRIGHT, NOT AIMED. */
            /* IT RUNS FACE TO FACE, NOT AXIS TO AXIS.

               Members are drawn on their axis with their depth either side, so a
               post that stops at the rafter's axis is 80 mm short of its top, and
               the same again at the bottom chord. That is the piece missing at both
               ends -- the outline never closed.

               From the TOP of the rafter down to the UNDERSIDE of the bottom chord,
               the perimeter is the right triangle he keeps asking for. The overlap
               inside the joint is what a nail plate is pressed over anyway. */
            /* THE POST NOW STANDS BETWEEN THE TWO CHORDS, CUT BY BOTH.

               It used to run from the TOP of the rafter to the BOTTOM of the
               bottom chord, straight through both of them -- which is why it
               read as the piece everything hung on. The chords are the pieces
               that bear; the post fills what is left between them. So its head
               is cut on the rafter's underside, sloping at the rafter's own
               slope across its 120 mm, and its foot on the bottom chord's top
               face. And it stands INBOARD of the doubled truss, its own face
               flush with it, not straddling it (Marek: sloupek mezi nimi
               orezavaji svymi kontaktnimi plochami). */
            var runIx2 = axZ2 ? 2 : 0;
            var dirE = ((eMid[runIx2] - apex[runIx2]) >= 0) ? 1 : -1;
            var runLen2 = Math.abs(eMid[runIx2] - apex[runIx2]) || 1;
            var tanS2 = (apex[1] - eMid[1]) / runLen2;
            var faceC = (CHORD.d / 2) / Math.cos(Math.atan(tanS2));
            var pBot = under[1] + CHORD.d / 2;
            var pTopHi = apex[1] - faceC;
            var postH = pTopHi - pBot;
            if (postH > 0.05) {
              _asmMark(ctx, 'hip_web', 'top', 'Hip truss web', 'ROT00002');
              var pgeo = axZ2 ? new sT.BoxGeometry(WEB.t, postH, WEB.d)
                              : new sT.BoxGeometry(WEB.d, postH, WEB.t);
              var posQ = pgeo.attributes.position, iQ;
              for (iQ = 0; iQ < posQ.count; iQ++) {
                if (posQ.getY(iQ) < postH / 2 - 1e-6) continue;      /* the head only */
                var cQ = (runIx2 === 2) ? posQ.getZ(iQ) : posQ.getX(iQ);
                var uQ = WEB.d / 2 + dirE * cQ;                      /* out from the face */
                posQ.setY(iQ, postH / 2 - uQ * tanS2);
              }
              posQ.needsUpdate = true;
              pgeo.computeVertexNormals();
              var pmesh = new sT.Mesh(pgeo, cm3);
              pmesh.position.set(apex[0] + ((runIx2 === 0) ? dirE * WEB.d / 2 : 0),
                                 (pTopHi + pBot) / 2,
                                 apex[2] + ((runIx2 === 2) ? dirE * WEB.d / 2 : 0));
              pmesh.userData.trussPart = 'cal_post';
              pmesh.userData.calSeq = SV.calSeq++;
              /* AND THE POST IS PART OF THE TRUSS, ON THE SHEET AS IN THE ROOF.

                 It is made here rather than through the member loop, so it
                 carried neither a record nor a mark: its sheet listed six pieces
                 and not the upright that stands against the doubled truss, and
                 the photograph left it out for the same reason. It is the piece
                 that carries the peak (Marek: v sestave chybi sloupek ktery ma
                 byt na styku s dvojtym vaznikem). */
              _hipRec([pmesh.position.x, pBot, pmesh.position.z],
                      [pmesh.position.x, pTopHi, pmesh.position.z],
                      WEB.d, WEB.t, 'CTR' + (hipEnds + 1), pmesh, axZ2 ? 'plumb' : 'plumbZ');
              sG.add(pmesh);
              webL += postH - (WEB.d / 2) * tanS2;
              ctx.log('HIP  centre post between the chords: ' +
                      Math.round((postH - WEB.d * tanS2) * 1000) + ' to ' +
                      Math.round(postH * 1000) + ' mm, head cut on the rafter underside (' +
                      Math.round(Math.atan(tanS2) * 180 / Math.PI) + ' deg), foot on the tie top face; ' +
                      'chords now reach the doubled truss, setback ' +
                      Math.round(clear0 * 1000) + ' mm from its centre');
            }

            /* THE PLATES ARE OFF UNTIL THE SHAPE IS RIGHT. */

            /* THE WEBS, ON THE PATTERN THE OTHER TRUSSES USE. */
            var nPan = Math.max(2, TRUSS_PANELS);
            var wq2, prevF = 0;
            /* the centre truss of the hip end gets the same last bay (adu-v1371) */
            var runT0 = Math.hypot(eMid[0] - apex[0], eMid[2] - apex[2]) || 1;
            var calFootMax = (tanS2 > 1e-6)
              ? ((apex[1] - faceC) - (under[1] + CHORD.d / 2) - WEB.d - 0.02) / tanS2
              : 0;
            var calEnd = 'none';
            for (wq2 = 1; wq2 <= nPan; wq2++) {
              var lastC = (wq2 === nPan);
              var f2 = wq2 / nPan;
              var bx2 = apex[0] + (eMid[0] - apex[0]) * f2;
              var bz2 = apex[2] + (eMid[2] - apex[2]) * f2;
              var topY2 = apex[1] + (eMid[1] - apex[1]) * f2;
              /* the upright, from the bottom chord's TOP FACE to the rafter's
                 UNDERSIDE -- faces, not axes */
              if (!lastC) webL += inPlane([bx2, under[1] + CHORD.d / 2, bz2],
                              [bx2, topY2 - faceC, bz2], WEB.t, WEB.d, 'cal_vert',
                              null, null,
                              { y: topY2 - faceC, s: tanS2,
                                rx: avx / avL, rz: avz / avL, px: bx2, pz: bz2 });
              /* and the diagonal back toward the peak, worked out in the truss's
                 own flat picture and then set down in the world */
              var runT2 = Math.hypot(eMid[0] - apex[0], eMid[2] - apex[2]) || 1;
              var Rx2 = (eMid[0] - apex[0]) / runT2, Rz2 = (eMid[2] - apex[2]) / runT2;
              /* the first one runs back to the post at the peak, which stands
                 from the doubled truss's face inward by its own depth */
              var footC2 = f2 * runT2;
              if (lastC) { calEnd = 'no diagonal -- the truss ends at its last upright'; prevF = f2; continue; }
              var WP = _webPlaneEnds(footC2, prevF * runT2, tanS2,
                                     apex[1] - faceC, under[1] + CHORD.d / 2,
                                     WEB.d / 2, WEB.d / 2,
                                     (prevF < 1e-6) ? WEB.d : null, true);
              if (WP) {
                var fPt = [apex[0] + Rx2 * WP.foot.d, WP.foot.y, apex[2] + Rz2 * WP.foot.d];
                var hPt = [apex[0] + Rx2 * WP.head.d, WP.head.y, apex[2] + Rz2 * WP.head.d];
                webL += inPlane(fPt, hPt, WEB.t, WEB.d, lastC ? 'cal_diag_end' : 'cal_diag', null, null, null,
                                { aOff: WP.eF.off, aQ: WP.eF.q,
                                  bOff: WP.eH.off, bQ: WP.eH.q, flip: WP.flip });
                if (lastC) calEnd = 'diagonal ' + Math.round(Math.hypot(hPt[0] - fPt[0], hPt[1] - fPt[1], hPt[2] - fPt[2]) * 1000) +
                  ' mm, its foot ' + Math.round((runT0 - footC2) * 1000) + ' mm short of the eave';
              }
              prevF = f2;
            }
            ctx.log('HIP  centre truss: run ' + Math.round(runT0 * 1000) + ' mm in ' + nPan + ' bay(s); last bay: ' + calEnd);

            /* AND THE PLATES, THIS TIME AS PLATES.

               The first attempt made them 64 mm thick, so each one read as a cube
               on the joint -- the odd square he found. A connector plate is a
               millimetre of steel pressed onto the FACE, one each side, and that is
               what these are. */
            /* A PLATE LIES ON THE FACE OF ITS OWN TRUSS.

               The centre truss runs one way and each hip runs diagonally, so a
               plate has to be turned into the plane it belongs to -- otherwise it
               sits across the joint edge-on and reads as a sliver. yaw is the same
               angle its members are drawn with, which is why it is passed in
               rather than worked out again here. */
            /* THE SAME PLATE THE REST OF THE ROOF USES. */
            /* ONE SIZE, THE SAME ONE THE REST OF THE ROOF USES. */
            /* THE MAIN RUN'S PLATE, COPIED RATHER THAN RE-INVENTED. */
            /* AT THE END OF A MEMBER THE PLATE STEPS INBOARD. */
            function plate3(at, big, yaw3, tilt3, isEnd, toward, sq, tall) {
              /* A STEEL PLATE IS NOT A TIMBER WEB.

                 Fifteen of these are drawn in this block and none of them said
                 what it was, so each inherited whichever mark came last -- after
                 my truss fix, that is the web. The audit read their steel as
                 123 m of timber against a bill of 65, and the ratio sat at 1.9
                 through three more rounds of me looking at the timber (Marek).

                 It is billed as PLATE.code, so that is what it wears. */
              _asmMark(ctx, 'truss_plate', 'top', 'Truss connector plate',
                       big ? PLATE.codeBig : PLATE.code);
              var pl3 = PLATE.l;
              var pw3 = Math.min(PLATE.w, CHORD.d - 0.004);
              /* A PLATE AT A PANEL POINT IS A SQUARE, AND IT STRADDLES THE JOINT.

                 Away from the peak the plates were laid on the chord's AXIS and
                 sized 250 x 150, so a 160 mm chord swallowed them whole: all of
                 the steel on one member and none of it on the pieces it is meant
                 to hold. A joint plate laps BOTH -- so at these nodes it is
                 square, and the caller puts it on the chord's FACE, half over the
                 chord and half over the webs standing on it (Marek: jsou skoro
                 cele na pasnici, takze neplni funkci spoje, spise ctverec). */
              if (sq) { pl3 = PLATE.sq; pw3 = PLATE.sq; }
              /* AND AT A HEEL IT STANDS ON END.

                 Lying down, the plate runs 250 mm along the rafter and only 150
                 across it -- so it reaches past the post toward the hip and still
                 laps barely more than the chord. Stood up, it is 150 along and 250
                 tall: it stays inside the post's own footprint along the jack and
                 covers the full depth of the rafter plus a good bite of the post
                 below it (Marek: otocit o 90 stupnu, na vysku). */
              if (tall) { pl3 = PLATE.w; pw3 = PLATE.l; }
              var off3 = CHORD.t / 2 + PLATE.t / 2;
              /* AN END PLATE IS LEVEL, WHEREVER IT IS. */
              if (isEnd) tilt3 = 0;
              /* THE STEP GOES TOWARD THE OTHER END OF THE TRUSS. */
              var shX = 0, shZ = 0;
              if (isEnd && toward) {
                var tdx = toward[0] - at[0], tdz = toward[2] - at[2];
                var tdL = Math.hypot(tdx, tdz) || 1;
                shX = tdx / tdL * (pl3 / 2);
                shZ = tdz / tdL * (pl3 / 2);
              }

              /* EVERY PLATE SAYS WHERE IT IS AND HOW IT IS TURNED. */
              pLog.push(pTag + ' ' + Math.round(at[0] * 1000) + ',' +
                        Math.round(at[1] * 1000) + ',' + Math.round(at[2] * 1000) +
                        ' yaw' + Math.round(yaw3 * 180 / Math.PI) +
                        ' tilt' + Math.round((tilt3 || 0) * 180 / Math.PI) +
                        (isEnd ? ' END' : ''));

              var side;
              for (side = -1; side <= 1; side += 2) {
                var pm = new sT.Mesh(new sT.BoxGeometry(PLATE.t, pw3, pl3), pm3);
                pm.position.set(at[0] + side * off3 * Math.cos(yaw3) + shX,
                                at[1],
                                at[2] - side * off3 * Math.sin(yaw3) + shZ);
                pm.rotation.order = 'YXZ';
                pm.rotation.y = yaw3;
                pm.rotation.x = tilt3 || 0;
                pm.userData.trussPart = 'plate';   /* hidden in the truss stage, like the rest */
                sG.add(pm);
              }
              if (big) platesBig++; else plates++;
            }

            var yawC = axZ2 ? 0 : Math.PI / 2;
            /* THE TILT CARRIES THE DIRECTION, LIKE THE MEMBER DOES. */
            /* THE CENTRE TRUSS IS THE ONE THAT DOES NEED A SIGN -- and the opposite
               one to what I gave it. Its members go through inPlane, whose yaw is
               fixed and which carries direction in the TILT: +1 when the run is
               negative. Mine was -1, so its plates were inverted against their own
               members at both ends.

               It is the only family whose yaw does not turn with the hip end, which
               is why it is the only one that needs this at all. */
            var dirC = ((axZ2 ? (eMid[2] - apex[2]) : (eMid[0] - apex[0])) < 0) ? 1 : -1;
            /* SUBTRACTED THE WAY THE MEMBER SUBTRACTS. */
            var tiltC = Math.atan2(eMid[1] - apex[1], avL - clear0) * dirC;
            pTag = 'CTR' + (hipEnds + 1);
            /* THE PLATE STARTS AT THE POST'S EDGE, NOT ON THE NODE.

               Centred on the peak it reached half its own length back past the
               doubled truss -- 125 mm of steel inside another truss. A plate is
               laid ON the joint it holds, so its edge lines up with the edge of
               the post and the rest of it runs INTO the truss, over the rafter,
               the post and the head of the first diagonal, which is the three
               pieces this joint is (Marek: aby licovala hrana desky a sloupku, a
               zaroven tak prichytnou i modrou vzperu). */
            var pcOff = HEEL_PLATE_OUT + PLATE.w / 2;
            var pcx = apex[0] + (eMid[0] - apex[0]) / avL * pcOff;
            var pcz = apex[2] + (eMid[2] - apex[2]) / avL * pcOff;
            plate3([pcx, apex[1] - pcOff * tanS2 + faceC - PLATE.l / 2 - HEEL_PLATE_DROP,
                    pcz], true, yawC, 0, false, null, false, true);
            plate3([pcx, under[1] - CHORD.d / 2 + PLATE.l / 2, pcz],
                   true, yawC, 0, false, null, false, true);
            /* levelling these was wrong: only the centre truss's was turned the
               wrong way, and that was the sign, fixed a version ago. Levelling all
               of them made the hips and the jacks worse than they were. */
            plate3(eMid, true, yawC, tiltC, true, apex);
            /* BOTH ENDS OF EVERY UPRIGHT, not just the foot.

               Every plate went on at bottom-chord level, so the top of each web --
               where it meets the sloping chord -- had nothing on it. Those are
               joints as much as the feet are, and on a real truss they are plated
               the same way. */
            for (wq2 = 1; wq2 < nPan; wq2++) {
              var f3 = wq2 / nPan;
              var jx = apex[0] + (eMid[0] - apex[0]) * f3;
              var jz = apex[2] + (eMid[2] - apex[2]) * f3;
              plate3([jx, under[1] + CHORD.d / 2, jz], false, yawC, 0, false, null, true);
              plate3([jx, apex[1] + (eMid[1] - apex[1]) * f3 - faceC, jz],
                     false, yawC, tiltC, false, null, (wq2 + 1 < nPan));
            }

            /* THE TWO HIP MEMBERS, FROM THE SAME PEAK TO THE TWO CORNERS.

               They start where the centre truss starts -- the same point, so the
               three meet in one joint rather than three near-misses -- and run
               diagonally down to the corners of the eave.

               A HIP IS SHALLOWER THAN THE ROOF. Its plan run is the diagonal, which
               is longer than the centre truss's straight one, over the same rise:
               21.6 degrees against the planes' 29.6. That is why the jacks are cut
               on the hip and not the other way round, and it is worth having the
               number in the model rather than in someone's head.

               Drawn in ITS OWN vertical plane -- the diagonal one -- so the section
               stands up in it, the same discipline the centre truss needed. */
            var ahC1 = { n: sG.children.length, c: chordL, w: webL, p: plates, pb: platesBig, cap: SV.ahCap };
            SV.ahCap = null;
            var corners = [PP.eave[0], PP.eave[1]], cq3;
            for (cq3 = 0; cq3 < corners.length; cq3++) {
              if (PP.ahOpen && cq3 === 1) continue;   /* the open corner: no hip there */
              var CN = [corners[cq3][0], TT.eaveY, corners[cq3][2]];
              var hdx = CN[0] - apex[0], hdz = CN[2] - apex[2];
              var hRun = Math.hypot(hdx, hdz);
              var hLen = Math.hypot(hRun, apex[1] - CN[1]);
              if (!(hLen > 0.3)) continue;
              SV.hipNo++; SV.hipSeq = 0;
              function hipTag(mesh) {
                mesh.userData.hipNo = SV.hipNo;
                mesh.userData.hipSeq = SV.hipSeq++;
                mesh.userData.hipLen = hLen;
                return mesh;
              }

              /* THE TILT SIGN. The box's length lies on its local Z, and after the
                 yaw that Z points from the peak toward the corner -- downhill. A
                 positive rotation about X lifts the far end, so the drop is
                 POSITIVE here; I had it negative and the member ran the other way,
                 rising out of the corner instead of falling into it. */
              _asmMark(ctx, 'hip_chord', 'top', 'Hip truss chord', 'ROT00001');
              var hm = new sT.Mesh(new sT.BoxGeometry(CHORD.t, CHORD.d, hLen, 1, 1, 2), cm3);
              hm.position.set((apex[0] + CN[0]) / 2, (apex[1] + CN[1]) / 2,
                              (apex[2] + CN[2]) / 2);
              hm.rotation.order = 'YXZ';
              hm.rotation.y = Math.atan2(hdx, hdz);
              hm.rotation.x = Math.atan2(apex[1] - CN[1], hRun);
              hm.userData.trussPart = 'hip_rafter';
              /* THE HIP GOES ON A SHEET TOO.

                 Only the centre truss and the jacks were writing themselves down,
                 so the drawing set had every short truss of a hip end and not the
                 hip itself -- the longest member in the roof, and the one every
                 jack is cut against. Each of its pieces is recorded under the
                 hip's own tag (Marek: narozni pak zellow a red pasnice spolu se
                 vzperami). */
              var hTag = 'HIP' + (hips + 1);
              /* AND THE HIP'S FOOT IS PLANED ON ITS OWN BOTTOM CHORD, TOO.

                 The jacks got this cut and the hip never did: its rafter ran down
                 to the corner still full depth, so over the last stretch it shared
                 timber with the chord below it and the end came out as a wedge
                 rather than a level face. Same cut, same helper, same reason
                 (Marek: ten konec byl opraveny jen u jack truss hip end). */
              _footOnChord(hm, CN[1] + CHORD.d / 2, (apex[1] + CN[1]) / 2,
                           hLen / 2, CHORD.d / 2);
              _hipRec(apex, CN, CHORD.d, CHORD.t, hTag, hm);

              /* THE HIP'S HEAD IS CUT ON THE CENTRE CHORD, SIDE AND TOP.

                 Two members leave the same peak: the centre chord runs straight
                 down the middle and the hip runs away from it diagonally. Cut
                 square, the hip's head stands inside the centre chord -- and
                 because that chord SLOPES, the corners of the hip that clear its
                 cheek still ride above its top face. On the bench a carpenter
                 takes those corners off; there is nothing else to do with them
                 (Marek: pri sklonu magenta pasnice se kousky zlute dostavaji nad
                 jeji horni plochu, tesar by je odstranil).

                 So each point of the head is carried along the hip until it is
                 clear of the cheek, and then dropped onto the top face if it is
                 still above it. Both are the centre chord's own planes, so the
                 two members meet along the arris they really share. */
              (function trimHipHead() {
                var yawT = hm.rotation.y, tilT = hm.rotation.x;
                var cy3 = Math.cos(tilT), sy3 = Math.sin(tilT);
                var cz3 = Math.cos(yawT), sz3 = Math.sin(yawT);
                var Rx3 = avx / avL, Rz3 = avz / avL;        /* down the run */
                var Sx3 = avz / avL, Sz3 = -avx / avL;       /* across it */
                var topY3 = apex[1] + (CHORD.d / 2) / Math.cos(Math.atan(tanS2));
                var pos3 = hm.geometry.attributes.position, i3;
                var cxM = (apex[0] + CN[0]) / 2, cyM = (apex[1] + CN[1]) / 2,
                    czM = (apex[2] + CN[2]) / 2;
                /* where a local direction lands in the world */
                function wof(x3, y3, z3) {
                  var ay = y3 * cy3 - z3 * sy3, az = y3 * sy3 + z3 * cy3;
                  return [x3 * cz3 + az * sz3, ay, -x3 * sz3 + az * cz3];
                }
                var uZ = wof(0, 0, 1), uY = wof(0, 1, 0);
                var dSdZ = Sx3 * uZ[0] + Sz3 * uZ[2];
                /* the whole head belongs on the side the hip runs to, not each
                   vertex to whichever cheek it happens to be nearer */
                var sideH = ((Sx3 * (CN[0] - apex[0]) + Sz3 * (CN[2] - apex[2])) >= 0) ? 1 : -1;
                var dFdY = uY[1] + (Rx3 * uY[0] + Rz3 * uY[2]) * tanS2;
                for (i3 = 0; i3 < pos3.count; i3++) {
                  var lx = pos3.getX(i3), ly = pos3.getY(i3), lz = pos3.getZ(i3);
                  if (lz > -hLen / 2 + 1e-6) continue;        /* the head ring only */
                  /* THE TWO MOVES PULL AGAINST EACH OTHER, so they are repeated.

                     Dropping a point onto the sloping top face also slides it
                     sideways -- the hip is turned in plan, so its depth direction
                     has a sideways component -- and that can put a point back
                     inside the cheek it had just cleared. Three passes settle it;
                     the second is already within a millimetre. */
                  var pass3, w, P3, r3, f3;
                  for (pass3 = 0; pass3 < 3; pass3++) {
                    w = wof(lx, ly, lz);
                    P3 = [cxM + w[0], cyM + w[1], czM + w[2]];
                    var sNow = Sx3 * (P3[0] - apex[0]) + Sz3 * (P3[2] - apex[2]);
                    if (Math.abs(dSdZ) > 1e-6) {
                      var want3 = sideH * (CHORD.t / 2);
                      var dz3 = (want3 - sNow) / dSdZ;
                      if (dz3 * sideH * dSdZ > -1e-9 && dz3 > 0 && lz + dz3 < hLen * 0.45) {
                        lz += dz3;
                        w = wof(lx, ly, lz);
                        P3 = [cxM + w[0], cyM + w[1], czM + w[2]];
                      }
                    }
                    r3 = Rx3 * (P3[0] - apex[0]) + Rz3 * (P3[2] - apex[2]);
                    f3 = P3[1] - (topY3 - r3 * tanS2);
                    if (f3 > 0.0002 && Math.abs(dFdY) > 1e-6) {
                      var dy3 = -f3 / dFdY;
                      if (Math.abs(ly + dy3) <= CHORD.d) ly += dy3;
                    } else if (pass3 > 0) break;
                  }
                  pos3.setZ(i3, lz);
                  pos3.setY(i3, ly);
                }
                pos3.needsUpdate = true;
                hm.geometry.computeVertexNormals();
              })();

              /* AND ITS FOOT IS PLANED ON ITS OWN BOTTOM CHORD.

                 The hip's two chords converge at the corner exactly as the centre
                 truss's do at the eave, so the same thing happens: over the last
                 stretch the rafter's underside is below the bottom chord's top
                 face, and the very end lies wholly inside it. The rafter keeps
                 full depth to where its underside meets that face, follows the
                 face from there, and finishes where its top face meets it too
                 (Marek: konce zlutych oriznout podle cervenych spodnich pasnic). */
              (function trimHipFoot() {
                var thT = hm.rotation.x, cyT = Math.cos(thT), syT = Math.sin(thT);
                if (!(Math.abs(syT) > 1e-4) || !(cyT > 1e-4)) return;
                var floorB = CN[1] + CHORD.d / 2;          /* the bottom chord's top */
                var cyB = (apex[1] + CN[1]) / 2;
                var hD4 = CHORD.d / 2, hL4 = hLen / 2;
                var posB = hm.geometry.attributes.position, iB;
                /* the break, where the underside crosses that face */
                var cBrkB = (cyB - hD4 * cyT - floorB) / syT;
                if (cBrkB > -hL4 + 0.005 && cBrkB < hL4 - 0.005) {
                  for (iB = 0; iB < posB.count; iB++) {
                    if (Math.abs(posB.getZ(iB)) > 1e-6) continue;
                    posB.setZ(iB, cBrkB);
                  }
                }
                /* and the end, where the top face crosses it */
                var lowB = (syT > 0) ? hL4 : -hL4;
                var cTopB = (cyB + hD4 * cyT - floorB) / syT;
                if ((syT > 0 && cTopB < hL4 - 0.002 && cTopB > -hL4) ||
                    (syT < 0 && cTopB > -hL4 + 0.002 && cTopB < hL4)) {
                  for (iB = 0; iB < posB.count; iB++) {
                    if (Math.abs(posB.getZ(iB) - lowB) > 1e-6) continue;
                    posB.setZ(iB, cTopB);
                  }
                }
                /* then nothing of it is left below the face */
                for (iB = 0; iB < posB.count; iB++) {
                  var lyB = posB.getY(iB), lzB = posB.getZ(iB);
                  var wantB = (floorB - cyB + lzB * syT) / cyT;
                  if (wantB > hD4) wantB = hD4;
                  if (wantB > lyB) posB.setY(iB, wantB);
                }
                posB.needsUpdate = true;
                hm.geometry.computeVertexNormals();
              })();

              sG.add(hipTag(hm));
              chordL += hLen;

              /* AND ITS BOTTOM CHORD, so the hip is a triangle like the rest.

                 Level, from under the shared peak out to the corner. The post at
                 the peak is the one the centre truss already stands on -- all four
                 hips and the centre truss hang off that single upright, which is
                 what makes the joint one joint. */
              var hbLen = hRun;
              _asmMark(ctx, 'hip_chord', 'top', 'Hip truss chord', 'ROT00001');
              var hb = new sT.Mesh(new sT.BoxGeometry(CHORD.t, CHORD.d, hbLen), cm3);
              hb.position.set((apex[0] + CN[0]) / 2, CN[1], (apex[2] + CN[2]) / 2);
              hb.rotation.order = 'YXZ';
              hb.rotation.y = Math.atan2(hdx, hdz);
              hb.userData.trussPart = 'hip_bottom';
              _hipRec([apex[0], CN[1], apex[2]], CN, CHORD.d, CHORD.t, hTag, hb);
              sG.add(hipTag(hb));
              chordL += hbLen;

              /* A HIP HAS NO POST AT ITS PEAK, AND IT NEVER SHOULD HAVE HAD ONE.

                 Each hip used to carry its own upright 120 mm out from the peak,
                 dropped from the rafter to the bottom chord. That is not how a
                 hip is framed: the end of the roof is carried by the DOUBLED
                 truss standing at the ridge end, and the hips bear on it. The
                 upright was there because the peak had been set back clear of
                 that truss and then needed something under it -- one made-up
                 piece holding up another (Marek: u tech naroznich bych sloupky
                 urcite odstranil).

                 Four of them are gone. The centre truss keeps its own post for
                 now; that one is a real question and he has not settled it. */
              SV.hipPostsDropped++;

              /* THE WEBS OF THE HIP TRIANGLE, on the same half-fink pattern.

                 The centre truss already divides its run into TRUSS_PANELS and puts
                 an upright at each division with a diagonal back toward the peak.
                 A hip is the same shape on a longer, shallower run, so it gets the
                 same rule rather than a second one -- and it is drawn in its own
                 diagonal plane, turned by the same yaw as its chords. */
              var hn = Math.max(2, TRUSS_PANELS);
              var hq3, hPrev = 0;
              /* AND THE LAST BAY GETS ITS DIAGONAL TOO (adu-v1371). The pattern put an
                 upright at each division and a diagonal from it back toward the
                 peak, but it stopped at the last division -- so the shallow bay
                 over the eave was left with nothing in it. Its foot stops where
                 the rafter's underside is still a web deep above the chord,
                 because at the eave the two meet and a 120 mm web has no room;
                 a bay shorter than 200 mm is left alone and the log says why. */
              var tanH0 = (apex[1] - CN[1]) / hRun;
              var faceH0 = (CHORD.d / 2) / Math.cos(Math.atan(tanH0));
              var dFootMax = (tanH0 > 1e-6)
                ? ((apex[1] - faceH0) - (CN[1] + CHORD.d / 2) - WEB.d - 0.02) / tanH0
                : 0;
              var endBay = 'none';
              for (hq3 = 1; hq3 <= hn; hq3++) {
                var last3 = (hq3 === hn);
                var hf = hq3 / hn;
                var wx = apex[0] + hdx * hf, wz = apex[2] + hdz * hf;
                var wyTop = apex[1] + (CN[1] - apex[1]) * hf;

                /* the hip's own fall and face offset, wanted by both the upright
                   and the diagonal below it */
                var tanH4 = (apex[1] - CN[1]) / hRun;
                var faceH4 = (CHORD.d / 2) / Math.cos(Math.atan(tanH4));
                var wUp = Math.abs(wyTop - CN[1]);
                if (wUp > 0.05) {
                  _asmMark(ctx, 'hip_web', 'top', 'Hip truss web', 'ROT00002');
                  /* THE SAME AT THE HIP: FACES, NOT AXES, AND A BEVELLED HEAD.

                     Foot on the bottom chord's top face, head on the hip
                     rafter's underside -- and since that underside falls along
                     the hip, the head is cut to the hip's own slope across the
                     upright's 120 mm rather than level. */
                  var wBot4 = CN[1] + CHORD.d / 2;
                  var wTop4 = wyTop - faceH4;
                  var wUp4 = wTop4 - wBot4;
                  if (!(wUp4 > 0.05)) { hPrev = hf; continue; }
                  var wgeo = new sT.BoxGeometry(WEB.t, wUp4, WEB.d);
                  var posW = wgeo.attributes.position, iW;
                  for (iW = 0; iW < posW.count; iW++) {
                    if (posW.getY(iW) < wUp4 / 2 - 1e-6) continue;    /* the head ring */
                    posW.setY(iW, wUp4 / 2 - posW.getZ(iW) * tanH4);
                  }
                  posW.needsUpdate = true;
                  wgeo.computeVertexNormals();
                  var wm = new sT.Mesh(wgeo, cm3);
                  wm.position.set(wx, (wTop4 + wBot4) / 2, wz);
                  wm.rotation.y = Math.atan2(hdx, hdz);
                  wm.userData.trussPart = 'hip_vert';
                  _hipRec([wx, wBot4, wz], [wx, wTop4, wz], WEB.d, WEB.t, hTag, wm, 'plumb');
                  sG.add(hipTag(wm));
                  webL += wUp4;
                }

                /* the diagonal, back to the node above the previous division */
                /* THE HIP'S DIAGONALS GO THROUGH THE SAME SOLVER.

                   A hip is the same flat picture as any other truss, only on a
                   longer and shallower run, so its sloping webs are worked out by
                   the same rule: both ends on the faces they land on, each point
                   of the end carried to whichever face it meets first, and the
                   head laid down until the two bearings are equal. */
                /* NO DIAGONAL IN THE LAST BAY ANYWHERE ON A HIP END. It was added
                   for the shallow bay over the eave and then taken off the jacks;
                   the centre truss and the hips want it off too -- they finish at
                   their last upright (Marek: ty koncove zelene sikme vzpery
                   odstranit i u T2 a T3). */
                var footD3 = hf * hRun;
                if (last3) { endBay = 'no diagonal -- the truss ends at its last upright'; hPrev = hf; continue; }
                /* into the corner, like the centre truss: the head of a sloping
                   web fills the corner between the chord above and the upright
                   beside it instead of being laid down for an even bearing */
                var HP = _webPlaneEnds(footD3, hPrev * hRun, tanH4,
                                       apex[1] - faceH4, CN[1] + CHORD.d / 2,
                                       WEB.d / 2, WEB.d / 2, null, true);
                var ex3 = hdx / hRun, ez3 = hdz / hRun;
                var px3 = HP ? (apex[0] + ex3 * HP.head.d) : (apex[0] + hdx * hPrev);
                var pz3 = HP ? (apex[2] + ez3 * HP.head.d) : (apex[2] + hdz * hPrev);
                var py3 = HP ? HP.head.y : (apex[1] + (CN[1] - apex[1]) * hPrev);
                var fx3 = HP ? (apex[0] + ex3 * HP.foot.d) : (apex[0] + ex3 * footD3);
                var fz3 = HP ? (apex[2] + ez3 * HP.foot.d) : (apex[2] + ez3 * footD3);
                var fy3 = HP ? HP.foot.y : CN[1];
                var ddx = px3 - fx3, ddz = pz3 - fz3, ddy = py3 - fy3;
                var dRun = Math.hypot(ddx, ddz), dLen = Math.hypot(dRun, ddy);
                if (dLen > 0.05) {
                  _asmMark(ctx, 'hip_web', 'top', 'Hip truss web', 'ROT00002');
                  var dgeo = new sT.BoxGeometry(WEB.t, WEB.d, dLen, 1, HP ? 2 : 1, 1);
                  if (HP) {
                    var posD = dgeo.attributes.position, iD, hDD = WEB.d / 2, hLD = dLen / 2;
                    for (iD = 0; iD < posD.count; iD++) {
                      var zD = posD.getZ(iD), yD = posD.getY(iD);
                      var kD0 = (yD > hDD * 0.5) ? 0 : ((yD < -hDD * 0.5) ? 2 : 1);
                      var kD = HP.flip ? (2 - kD0) : kD0;
                      var qS = HP.flip ? -1 : 1;
                      if (Math.abs(zD - hLD) < 1e-6) {
                        posD.setZ(iD, HP.eH.off[kD]);
                        if (kD0 === 1) posD.setY(iD, qS * HP.eH.q);
                      } else if (Math.abs(zD + hLD) < 1e-6) {
                        posD.setZ(iD, HP.eF.off[kD]);
                        if (kD0 === 1) posD.setY(iD, qS * HP.eF.q);
                      }
                    }
                    posD.needsUpdate = true;
                    dgeo.computeVertexNormals();
                  }
                  var dm = new sT.Mesh(dgeo, cm3);
                  dm.position.set((fx3 + px3) / 2, (fy3 + py3) / 2, (fz3 + pz3) / 2);
                  dm.rotation.order = 'YXZ';
                  dm.rotation.y = Math.atan2(ddx, ddz);
                  dm.rotation.x = Math.atan2(-ddy, dRun);
                  dm.userData.trussPart = last3 ? 'hip_diag_end' : 'hip_diag';
                  if (last3) endBay = 'diagonal ' + Math.round(dLen * 1000) + ' mm, its foot ' +
                    Math.round((hRun - footD3) * 1000) + ' mm short of the eave';
                  _hipRec([fx3, fy3, fz3], [px3, py3, pz3], WEB.d, WEB.t, hTag, dm);
                  sG.add(hipTag(dm));
                  webL += dLen;
                }
                hPrev = hf;
              }

              /* the hip's own joints: peak, the foot under it, the corner, and the
                 foot of each upright -- the same three-big-plus-one-per-division
                 the centre truss uses */
              var yawH = Math.atan2(hdx, hdz);
              /* the hips face four ways, so theirs is signed too */
              pTag = 'HIP' + (hips + 1);
              /* THE SAME EXPRESSION THE MEMBER USES, WITH NO SIGN OF ITS OWN. */
              var tiltH = Math.atan2(apex[1] - CN[1], hRun);
              /* NO PLATES AT THE PEAK OF A HIP.

                 Four hips and the centre truss meet over one point, and each hip
                 was putting a pair of plates there on its own -- so the peak grew
                 ten of them, in five different planes, running straight through
                 the chords of every hip but their own. A plate is pressed onto the
                 FACE of one truss; it cannot be pressed onto five at once.

                 That junction is not a plated node at all: the hips and the end
                 truss bear on the doubled truss at the ridge end, and the only
                 plates that belong there are the centre truss's own, which are
                 still made above. The hips keep the plate at their CORNER, where
                 the rafter really does meet its own bottom chord (Marek: tam maji
                 zustat jen desky na spoji stredoveho vazniku). */
              plate3(CN, true, yawH, tiltH, true, apex);
              ctx.log('HIP  centre profile: run ' + Math.round(hRun * 1000) + ' mm in ' + hn + ' bay(s); last bay: ' + endBay);
              for (hq3 = 1; hq3 < hn; hq3++) {
                var hf2 = hq3 / hn;
                var fcH2 = (CHORD.d / 2) / Math.cos(Math.atan((apex[1] - CN[1]) / hRun));
                plate3([apex[0] + hdx * hf2, CN[1] + CHORD.d / 2, apex[2] + hdz * hf2],
                       false, yawH, 0, false, null, true);
                plate3([apex[0] + hdx * hf2, apex[1] + (CN[1] - apex[1]) * hf2 - fcH2,
                        apex[2] + hdz * hf2], false, yawH, tiltH, false, null, (hq3 + 1 < hn));
              }

              hips++;

              ctx.log('HIP  hip member ' + hips + ': ' + Math.round(hLen * 1000) +
                      ' mm at ' + Math.round(Math.atan2(apex[1] - CN[1], hRun) * 180 / Math.PI) +
                      ' deg -- shallower than the roof, which is why jacks die on it');
            }

            /* THE JACK TRUSSES: THE FILL BETWEEN THE HIPS. */
            /* the fill is settled, so it is timber like the rest of the frame */
            var jm = cm3;

            /* ONE WEB RULE FOR EVERY JACK. */
            function jackWebs(hiX, hiY, hiZ, loX, loY, loZ, botY, yawJ, tagJ, hasPost) {
              /* A SHORT TRUSS HAS FEWER PANELS, NOT SMALLER ONES. */
              var runX = loX - hiX, runZ = loZ - hiZ;
              var runP = Math.hypot(runX, runZ);
              if (runP < 0.4) return;
              /* a panel has to be LONGER than a plate, not equal to it -- at exactly
                 one plate per panel the plates still touch end to end */
              /* HOW MANY PANELS A SHORT TRUSS GETS, AND WHY.

                 It used to be the plan run divided by one and a half plates, so a
                 1.13 m chord came out as two panels of 375 mm -- a 250 mm plate
                 inside a 375 mm bay. That is timber, not bracing. The measure is
                 the PANEL, not the plate: a panel longer than TRUSS_PANEL_MAX leaves
                 the chord unsupported and earns another division; one shorter
                 than TRUSS_PANEL_MIN has no room for the joint and gives one back. The
                 count is capped at the main truss's, so a long jack never ends up
                 busier than the truss it hangs off (Marek: delkou spodni pasnice,
                 1,5 m a 0,6 m, nejvys 3 pole).

                   bottom chord up to 1.5 m    no webs, just the triangle
                   1.5 to 3.0 m                one upright and one diagonal
                   over 3.0 m                  two of each */
              var n5 = Math.max(1, Math.min(TRUSS_PANELS,
                                            Math.ceil(runP / TRUSS_PANEL_MAX)));
              while (n5 > 1 && (runP / n5) < TRUSS_PANEL_MIN) n5--;
              /* A PLATE ONLY WHERE IT JOINS SOMETHING (adu-v1355).

                 The heel plates are the joint of the heel POST with the rafter
                 and with the bottom chord -- so they go on where that post
                 stands, and on the shortest jacks, whose post is too short to be
                 built (100 mm or less between the chords), there is nothing for
                 them to join and they stay off (Marek: desky jen tam, kde opravdu
                 neco spojuji). Everything else keeps its plates: every node where
                 an upright or a diagonal meets a chord, and the eave end, where
                 the rafter and the bottom chord meet -- that joint is always
                 plated (the previous version took those off too, wrongly). */
              /* (decided below, once the jack's fall and faces are known) */
              var q5, prev5 = 0;

              /* the high end is plated first, and every later node measures from it */
              /* likewise: the jack's rafter is atan2(rise, run) and its yaw does the
                 turning, so the plate needs no sign either */
              var tiltJ = Math.atan2(hiY - loY, runP);
              var lastPX = hiX, lastPZ = hiZ;
              /* the jack's own picture: its fall, the two faces its webs die on,
                 and the way out along its run */
              var tanJ = (hiY - loY) / runP;
              var faceJ = (CHORD.d / 2) / Math.cos(Math.atan(tanJ));
              var yTop0J = hiY - faceJ;
              var tieTopJ = botY + CHORD.d / 2;
              var ex5 = runX / runP, ez5 = runZ / runP;

              /* THE HEEL PLATES BELONG TO THE JACK, NOT TO THE HIP.

                 They were put on at the jack's start -- which is ON the hip's
                 line now -- so they stood in the hip and ran through the centre
                 truss beyond it, plating three trusses at once in one plane. A
                 plate is pressed on the face of ONE truss. They go where the
                 joint actually is: over the heel post, which stands its own
                 set-out along the jack, one under the rafter and one on the
                 bottom chord (Marek: maji byt jen soucasti tech kratkych
                 vazniku, nesmi trcet ven). */
              /* edge to edge with the post, so the plate covers the heel joint and
                 stops where the post stops -- nothing of it stands in the hip */
              var pOffJ = HEEL_SET + HEEL_PLATE_OUT + PLATE.w / 2;
              var hpxJ = hiX + ex5 * pOffJ, hpzJ = hiZ + ez5 * pOffJ;
              var faceJ2 = (CHORD.d / 2) / Math.cos(Math.atan(tanJ));
              /* AND AT THE HEEL THE PLATE STANDS SQUARE TO THE WORLD.

                 Laid parallel to the rafter its edges run with the slope, which
                 on a joint whose third piece is a PLUMB post reads as a lozenge.
                 Turned upright, one edge follows the post and the plate laps the
                 rafter, the post and the head of the diagonal at once (Marek:
                 natocit at hrana desky je svisla). */
              /* the caller built the heel post or did not -- the same test it
                 used, passed in, so the plates and the post cannot disagree */
              var plateJ = (hasPost !== false);
              if (!plateJ) SV.jackBare++;
              if (plateJ) plate3([hpxJ, hiY - pOffJ * tanJ + faceJ2 - PLATE.l / 2 - HEEL_PLATE_DROP,
                      hpzJ], true, yawJ, 0, false, null, false, true);
              /* AND THE LOWER ONE STANDS UP TOO. Lying down it ran along the
                 bottom chord and barely touched the post; on end it laps the whole
                 depth of the chord and bites into the post above it, which is the
                 joint it is there for (Marek: otoc o 90 stupnu ty spodni). */
              if (plateJ) plate3([hpxJ, botY - CHORD.d / 2 + PLATE.l / 2, hpzJ],
                     true, yawJ, 0, false, null, false, true);

              /* the jacks get the same last bay as the hips and the centre truss */
              var jackFootMax = (tanJ > 1e-6) ? ((yTop0J) - (tieTopJ) - WEB.d - 0.02) / tanJ : 0;
              var jackEnd = 'none', jackDiags = [];
              for (q5 = 1; q5 <= n5; q5++) {
                var last5 = (q5 === n5);
                var f5 = q5 / n5;
                var dNode5 = runP * f5;
                var wx5 = hiX + runX * f5, wz5 = hiZ + runZ * f5;
                var wy5 = hiY + (loY - hiY) * f5;

                /* A JACK IS A MONO TRUSS, AND ITS WEBS GET WHAT THE OTHERS GOT.

                   Upright from the bottom chord's TOP FACE to the rafter's
                   UNDERSIDE, bevelled at the rafter's own slope across its depth;
                   diagonal through the same solver as the centre truss and the
                   hips, so its foot beds on the chord and its head dies on the
                   cheek of whatever stands at the node above -- which for the
                   first one is the heel post, not a web. */
                var upTop5 = yTop0J - dNode5 * tanJ;
                var up5 = upTop5 - tieTopJ;
                if (up5 > 0.05 && !last5) {
                  _asmMark(ctx, 'hip_web', 'top', 'Hip truss web', 'ROT00002');
                  var ugeo5 = new sT.BoxGeometry(WEB.t, up5, WEB.d);
                  var posU5 = ugeo5.attributes.position, iU5;
                  for (iU5 = 0; iU5 < posU5.count; iU5++) {
                    if (posU5.getY(iU5) < up5 / 2 - 1e-6) continue;
                    posU5.setY(iU5, up5 / 2 - posU5.getZ(iU5) * tanJ);
                  }
                  posU5.needsUpdate = true;
                  ugeo5.computeVertexNormals();
                  var um = new sT.Mesh(ugeo5, jm);
                  um.position.set(wx5, (upTop5 + tieTopJ) / 2, wz5);
                  um.rotation.y = yawJ;
                  um.userData.trussPart = (q5 === 1) ? 'jack_vert1' : 'jack_vert2';
                  _hipRec([wx5, tieTopJ, wz5], [wx5, upTop5, wz5], WEB.d, WEB.t, tagJ, um, 'plumb');
                  sG.add(um);
                  webL += up5;
                }

                /* THE FIRST ONE DIES ON THE HEEL POST, AND THAT POST HAS MOVED.

                   It was given the post's far face as WEB.d from the hip, which
                   was true until the post was stood 30 mm further out to keep its
                   width. The face is where the post actually is -- its set-out
                   plus its own depth -- otherwise the diagonal runs straight into
                   it (Marek: konce oranzove koliduji s bilymi sloupky). */
                /* A JACK ENDS WITH ITS LAST UPRIGHT. The shallow bay over the
                   eave was given a diagonal like the hips and the centre truss,
                   but on a jack it has nothing to do: the truss finishes at the
                   last vertical web, or at the post on the short end pieces
                   (Marek: posledni sikme vzpery jsou zbytecne). */
                var footJ5 = dNode5;
                if (last5) { jackEnd = 'no diagonal -- a jack ends at its last upright'; prev5 = f5; continue; }
                var WJ = _webPlaneEnds(footJ5, runP * prev5, tanJ, yTop0J, tieTopJ,
                                       WEB.d / 2, WEB.d / 2,
                                       (q5 === 1) ? (HEEL_SET + WEB.d) : null, true);
                var px5 = hiX + ex5 * (WJ ? WJ.head.d : runP * prev5);
                var pz5 = hiZ + ez5 * (WJ ? WJ.head.d : runP * prev5);
                var py5 = WJ ? WJ.head.y : (hiY + (loY - hiY) * prev5);
                var fx5 = hiX + ex5 * (WJ ? WJ.foot.d : dNode5);
                var fz5 = hiZ + ez5 * (WJ ? WJ.foot.d : dNode5);
                var fy5 = WJ ? WJ.foot.y : botY;
                var ddx5 = px5 - fx5, ddz5 = pz5 - fz5, ddy5 = py5 - fy5;
                var dr5 = Math.hypot(ddx5, ddz5), dl5 = Math.hypot(dr5, ddy5);
                if (dl5 > 0.05) {
                  _asmMark(ctx, 'hip_web', 'top', 'Hip truss web', 'ROT00002');
                  var dgeo5 = new sT.BoxGeometry(WEB.t, WEB.d, dl5, 1, WJ ? 2 : 1, 1);
                  if (WJ) {
                    var posJ5 = dgeo5.attributes.position, iJ5;
                    var hDJ = WEB.d / 2, hLJ = dl5 / 2;
                    for (iJ5 = 0; iJ5 < posJ5.count; iJ5++) {
                      var zJ = posJ5.getZ(iJ5), yJ = posJ5.getY(iJ5);
                      var kJ0 = (yJ > hDJ * 0.5) ? 0 : ((yJ < -hDJ * 0.5) ? 2 : 1);
                      var kJ = WJ.flip ? (2 - kJ0) : kJ0, qJ = WJ.flip ? -1 : 1;
                      if (Math.abs(zJ - hLJ) < 1e-6) {
                        posJ5.setZ(iJ5, WJ.eH.off[kJ]);
                        if (kJ0 === 1) posJ5.setY(iJ5, qJ * WJ.eH.q);
                      } else if (Math.abs(zJ + hLJ) < 1e-6) {
                        posJ5.setZ(iJ5, WJ.eF.off[kJ]);
                        if (kJ0 === 1) posJ5.setY(iJ5, qJ * WJ.eF.q);
                      }
                    }
                    posJ5.needsUpdate = true;
                    dgeo5.computeVertexNormals();
                  }
                  var dm5 = new sT.Mesh(dgeo5, jm);
                  dm5.position.set((fx5 + px5) / 2, (fy5 + py5) / 2, (fz5 + pz5) / 2);
                  dm5.rotation.order = 'YXZ';
                  dm5.rotation.y = Math.atan2(ddx5, ddz5);
                  dm5.rotation.x = Math.atan2(-ddy5, dr5);
                  dm5.userData.trussPart = (q5 === 1) ? 'jack_diag1' : 'jack_diag2';
                  jackDiags.push('foot ' + Math.round(WJ ? WJ.foot.d * 1000 : dNode5 * 1000) + '/' + Math.round(fy5 * 1000) +
                                 ' head ' + Math.round(WJ ? WJ.head.d * 1000 : runP * prev5 * 1000) + '/' + Math.round(py5 * 1000) +
                                 ' (the station above it stands to ' + Math.round((yTop0J - (WJ ? WJ.head.d : runP * prev5) * tanJ) * 1000) + ')');
                  _hipRec([fx5, fy5, fz5], [px5, py5, pz5], WEB.d, WEB.t, tagJ, dm5);
                  sG.add(dm5);
                  webL += dl5;
                }
                /* A PLATE NEEDS ROOM. On a jack near the corner the whole truss is 640
                   mm long and its nodes are 213 apart, so seven 300 mm plates pile on top
                   of... */
                if (!last5 && Math.hypot(wx5 - lastPX, wz5 - lastPZ) >= PLATE.l) {
                  plate3([wx5, tieTopJ, wz5], false, yawJ, 0, false, null, true);
                  plate3([wx5, upTop5, wz5], false, yawJ, tiltJ, false, null, (q5 + 1 < n5));
                  lastPX = wx5; lastPZ = wz5;
                }
                prev5 = f5;
              }
              /* the low end always gets one -- it is the other end of the truss */
              if (Math.hypot(loX - lastPX, loZ - lastPZ) >= PLATE.l * 0.5) {
                plate3([loX, loY, loZ], true, yawJ, tiltJ, true, [hiX, hiY, hiZ]);
              }
              ctx.log('HIP  jack ' + tagJ + ': run ' + Math.round(runP * 1000) + ' mm in ' + n5 + ' bay(s); last bay: ' + jackEnd +
                      '; diagonals: ' + (jackDiags.join(' | ') || 'none'));
            }

            /* PARALLEL TO THE CENTRE TRUSS, STARTING ON THE HIP. */
            var jST = _trussStations();
            var jStep = (jST.length > 1) ? (jST[1] - jST[0]) : API.grid();
            /* A RIDGE SHORTER THAN A BAY IS NOT THE MODULE (adu-v1419, Marek: u
               celovalby jsou vazniky nesmyslne na husto). A hip roof on a square
               plan keeps a 100 mm ridge, its two girders stand 0.10 m apart, and
               that was taken for the spacing of the jacks -- some sixty a side.
               Under half the module, the module is used. */
            if (!(jStep > 0.5 * API.grid())) jStep = API.grid();

            /* the sideways direction: along the hip end's eave */
            var sdx = PP.eave[1][0] - PP.eave[0][0], sdz = PP.eave[1][2] - PP.eave[0][2];
            var sdL = Math.hypot(sdx, sdz) || 1;
            var halfW = sdL / 2;
            sdx /= sdL; sdz /= sdL;

            var jq, jacks = 0, sideJ, ahOpenN = 0;
            for (sideJ = -1; sideJ <= 1; sideJ += 2) {
              if (PP.ahOpen && sideJ === 1) {
                var ahCp = AH.openCopies(ctx, sG, PP, ahC0, ahC1, apex, eMid, sdx, sdz, jStep, 'CTR' + (hipEnds + 1));
                ahOpenN = ahCp.n; chordL += ahCp.c; webL += ahCp.w; plates += ahCp.p; platesBig += ahCp.pb;
                continue;
              }
              for (jq = 1; ; jq++) {
                var uOff = jq * jStep;
                if (uOff >= halfW - 0.15) break;

                /* MEASURED FROM THE PEAK THE TRUSS ACTUALLY STARTS AT. */
                var avE = avL - clear0;
                var uf = uOff / halfW;              /* how far out toward the corner */
                var hitD = avE * uf;                /* where the hip is, along the run */
                var runJ = avE - hitD;              /* and how far from there to the eave */
                if (runJ < 0.2) break;

                var topY = apex[1] + (TT.eaveY - apex[1]) * uf;
                var ax2 = apex[0] + avx / avL * hitD + sdx * uOff * sideJ;
                var az2 = apex[2] + avz / avL * hitD + sdz * uOff * sideJ;
                var bx3 = apex[0] + avx / avL * avE + sdx * uOff * sideJ;
                var bz3 = apex[2] + avz / avL * avE + sdz * uOff * sideJ;

                /* IT STARTS ON THE HIP'S LINE NOW, AND IS CUT BACK TO ITS CHEEK.

                   The old step-back was a guess at a compound cut: one number for
                   every jack whatever its plan angle, which is why the ends stood
                   off the hip by a different amount each time. The jack is built
                   up to the hip's own line and its head is then seated on the
                   cheek -- the cut does the work the guess was standing in for. */
                var hipClear = 0;
                var jdx = bx3 - ax2, jdz = bz3 - az2;
                var jdL = Math.hypot(jdx, jdz) || 1;
                ax2 += jdx / jdL * hipClear;
                az2 += jdz / jdL * hipClear;
                topY += (TT.eaveY - topY) * (hipClear / jdL);
                runJ -= hipClear;
                if (runJ < 0.2) break;

                var jLen = Math.hypot(runJ, topY - TT.eaveY);
                var jYaw = Math.atan2(bx3 - ax2, bz3 - az2);
                /* PUBLISHED, like every other member: the counter-battens on the hip end are cut where these jacks run, one each, as the commons set out the side planes (Marek: the end had two) */
                if (SV.memberPts) SV.memberPts.push([(ax2 + bx3) / 2, (az2 + bz3) / 2, bx3 - ax2, bz3 - az2, ax2, az2, bx3, bz3]);

                /* THIS END IS STILL CUT SQUARE, AND I HAVE STOPPED GUESSING AT IT.

                   Three attempts: a shear across the width, a shear down the
                   depth, and a plain pull-back. Two of them made exactly
                   thirteen bodies stop being counted -- the same thirteen, from
                   two completely different operations, with no throw in the log.
                   That the same number falls out of two unrelated changes says
                   the fault is not in the shear at all, and I have been fixing
                   the wrong thing three times over.

                   Left square, which is wrong but visible and stable, until the
                   cause of those thirteen is known (Marek). */
                _asmMark(ctx, 'hip_chord', 'top', 'Hip truss chord', 'ROT00001');
                var jr = new sT.Mesh(new sT.BoxGeometry(CHORD.t, CHORD.d, jLen, 1, 1, 2), jm);
                jr.position.set((ax2 + bx3) / 2, (topY + TT.eaveY) / 2, (az2 + bz3) / 2);
                jr.rotation.order = 'YXZ';
                jr.rotation.y = jYaw;
                jr.rotation.x = Math.atan2(topY - TT.eaveY, runJ);
                jr.userData.trussPart = 'jack_rafter';
                /* the hip this one runs off: from the peak out to its own corner */
                var hex = avx / avL * avE + sdx * halfW * sideJ;
                var hez = avz / avL * avE + sdz * halfW * sideJ;
                var heL = Math.hypot(hex, hez) || 1;
                hex /= heL; hez /= heL;
                var hTanJ2 = (apex[1] - TT.eaveY) / heL;
                var hTopA2 = apex[1] + (CHORD.d / 2) / Math.cos(Math.atan(hTanJ2));
                /* which cheek: the side the jack's own body lies on */
                var jSide = (((hez) * (bx3 - ax2) + (-hex) * (bz3 - az2)) >= 0) ? 1 : -1;
                _seatOnHip(jr, apex[0], apex[2], hex, hez, CHORD.t / 2,
                           hTopA2, hTanJ2, jSide, false);
                /* and planed off where it meets its own bottom chord at the eave */
                _footOnChord(jr, TT.eaveY + CHORD.d / 2, (topY + TT.eaveY) / 2,
                             jLen / 2, CHORD.d / 2);
                var jTag = 'JKA' + (jacks + 1);
                _hipRec([ax2, topY, az2], [bx3, TT.eaveY, bz3], CHORD.d, CHORD.t, jTag, jr);
                sG.add(jr);
                chordL += jLen;

                _asmMark(ctx, 'hip_chord', 'top', 'Hip truss chord', 'ROT00001');
                var jb = new sT.Mesh(new sT.BoxGeometry(CHORD.t, CHORD.d, runJ), jm);
                jb.position.set((ax2 + bx3) / 2, TT.eaveY, (az2 + bz3) / 2);
                jb.rotation.y = jYaw;
                jb.userData.trussPart = 'jack_bottom';
                _seatOnHip(jb, apex[0], apex[2], hex, hez, CHORD.t / 2,
                           null, null, jSide, false);
                _hipRec([ax2, TT.eaveY, az2], [bx3, TT.eaveY, bz3], CHORD.d, CHORD.t, jTag, jb);
                sG.add(jb);
                chordL += runJ;

                /* CUT UNDER THE RAFTER, NOT THROUGH IT.

                   I gave the post a whole chord of extra height, so its top ended
                   80 mm ABOVE the rafter's axis -- which is level with the rafter's
                   own top face and therefore out through the shell. It has to stop
                   at the rafter's UNDERSIDE and let the rafter cover it, the way
                   the centre truss's post does.

                   Top at topY - CHORD.d/2, foot down to the underside of the bottom
                   chord, so the triangle still closes at the bottom. */
                var jTopY = topY - CHORD.d / 2;
                /* THE HEEL POST OF A MONO TRUSS, BUILT LIKE ONE.

                   It ran from the rafter's axis to UNDER the bottom chord, so it
                   passed through both. On a mono truss this is the piece that ties
                   the two chords together at the high end -- it belongs between
                   their faces, cut by the rafter's underside above and standing on
                   the bottom chord's top face below, with its own face against the
                   hip rather than straddling the line (Marek: varianta A). */
                var jTanJ = (topY - TT.eaveY) / Math.max(0.2, runJ);
                var jBotY = TT.eaveY + CHORD.d / 2;
                /* AND IT STANDS CLEAR OF THE HIP, WHICH IS NOT A FIXED STEP.

                   The chords are cut back to the hip's cheek now, but the post is
                   a whole body: it has to START there. How far along the jack that
                   is depends on the angle the jack meets the hip at -- a jack near
                   the peak crosses it steeply and clears in 35 mm, one near the
                   corner runs shallow and needs 90. So it is measured, not
                   guessed: the rate at which the jack moves away from the hip's
                   line, divided into half the hip's thickness. Then the head is
                   cut on the rafter's underside AT THAT POINT, and the foot on the
                   bottom chord's top face (Marek: orezat pasnicemi magenta a acid
                   green, a nekolidovat s naroznim vaznikem). */
                var jRate = Math.abs(hez * (jdx / jdL) - hex * (jdz / jdL));
                var jClr = (CHORD.t / 2) / Math.max(0.15, jRate);
                /* the post starts ON the hip's line and is cut back to its cheek,
                   so its centre is half its own depth along the jack */
                var jPost = WEB.d / 2 + HEEL_SET;
                jTopY = (topY - jPost * jTanJ) -
                        (CHORD.d / 2) / Math.cos(Math.atan(jTanJ));
                var jpH = jTopY - jBotY;
                if (jpH > 0.1) {
                  /* ON THE OUTER FACE OF THE HIP, NOT ITS AXIS. Same fault the
                     centre truss's post had against the girder: a member centred on
                     the line it is supposed to sit against ends up half inside it.
                     Half a hip plus half a post, along the jack's own direction. */
                  /* the start is already clear of the hip, so the post sits on it */
                  _asmMark(ctx, 'hip_web', 'top', 'Hip truss web', 'ROT00002');
                  var jp = new sT.Mesh(
                    _heelPost(sT, jpH, jTanJ, jYaw,
                              ax2 + jdx / jdL * jPost, az2 + jdz / jdL * jPost,
                              apex[0], apex[2], hex, hez, CHORD.t / 2, jSide), jm);
                  jp.position.set(ax2 + jdx / jdL * jPost,
                                  (jTopY + jBotY) / 2,
                                  az2 + jdz / jdL * jPost);
                  jp.rotation.y = jYaw;
                  jp.userData.trussPart = 'jack_post';
                  _hipRec([ax2 + jdx / jdL * jPost, jBotY, az2 + jdz / jdL * jPost],
                          [ax2 + jdx / jdL * jPost, jTopY, az2 + jdz / jdL * jPost],
                          WEB.d, WEB.t, jTag, jp, true);
                  sG.add(jp);
                  webL += jpH;
                }
                pTag = 'JKA' + (jacks + 1);
                jackWebs(ax2, topY, az2, bx3, TT.eaveY, bz3, TT.eaveY, jYaw, jTag, jpH > 0.1);
                jacks++;
              }
            }
            /* AND THE OTHER SIDE OF THE HIPS: ON THE SIDE SLOPES. */
            var avE0 = avL - clear0;
            var xq, cross = 0, pubJ = [];
            for (xq = 1; ; xq++) {
              /* REVERTED TOO. Both of my offset attempts made the spacing worse
                 than it was four versions ago, when only the shortened jacks were
                 out. Putting a correction into the jack layout was the wrong lever
                 -- the jacks are where the frame wants them; it is the BATTENS that
                 have to find them (Marek). */
              var xd = xq * jStep;
              if (xd >= avE0 - 0.15) break;
              var xf = xd / avE0;
              var hipAt = halfW * xf;
              var xLen = halfW - hipAt;
              if (xLen < 0.2) break;

              var cx4 = apex[0] + avx / avL * xd;
              var cz4 = apex[2] + avz / avL * xd;
              var cyHip = apex[1] + (TT.eaveY - apex[1]) * xf;

              var builtB = cross;
              var eq4;
              for (eq4 = -1; eq4 <= 1; eq4 += 2) {
                if (PP.ahOpen && eq4 === 1) continue;   /* the open side has no side slope */
                var ix4 = cx4 + sdx * hipAt * eq4, iz4 = cz4 + sdz * hipAt * eq4;
                var ox4 = cx4 + sdx * halfW * eq4, oz4 = cz4 + sdz * halfW * eq4;
                /* the same clearance on this family: start at the hip's face */
                var hipClear2 = 0;     /* the seat below does this properly now */
                var xdx = ox4 - ix4, xdz = oz4 - iz4;
                var xdL = Math.hypot(xdx, xdz) || 1;
                ix4 += xdx / xdL * hipClear2;
                iz4 += xdz / xdL * hipClear2;
                cyHip += (TT.eaveY - cyHip) * (hipClear2 / xdL);
                xLen -= hipClear2;
                if (xLen < 0.2) continue;

                var xYaw = Math.atan2(ox4 - ix4, oz4 - iz4);
                var xSlope = Math.hypot(xLen, cyHip - TT.eaveY);

                /* the rafter, down the side plane from the hip to the eave */
                _asmMark(ctx, 'hip_chord', 'top', 'Hip truss chord', 'ROT00001');
                var xr = new sT.Mesh(new sT.BoxGeometry(CHORD.t, CHORD.d, xSlope, 1, 1, 2), jm);
                xr.position.set((ix4 + ox4) / 2, (cyHip + TT.eaveY) / 2, (iz4 + oz4) / 2);
                xr.rotation.order = 'YXZ';
                xr.rotation.y = xYaw;
                xr.rotation.x = Math.atan2(cyHip - TT.eaveY, xLen);
                xr.userData.trussPart = 'jack_rafter';
                /* the same seat on this family: its hip is the one on its own side */
                var hex4 = avx / avL * avE + sdx * halfW * eq4;
                var hez4 = avz / avL * avE + sdz * halfW * eq4;
                var heL4 = Math.hypot(hex4, hez4) || 1;
                hex4 /= heL4; hez4 /= heL4;
                var hTan4 = (apex[1] - TT.eaveY) / heL4;
                var hTop4 = apex[1] + (CHORD.d / 2) / Math.cos(Math.atan(hTan4));
                var xSide = (((hez4) * (ox4 - ix4) + (-hex4) * (oz4 - iz4)) >= 0) ? 1 : -1;
                _seatOnHip(xr, apex[0], apex[2], hex4, hez4, CHORD.t / 2,
                           hTop4, hTan4, xSide, false);
                _footOnChord(xr, TT.eaveY + CHORD.d / 2, (cyHip + TT.eaveY) / 2,
                             xSlope / 2, CHORD.d / 2);
                var xTag = 'JKB' + (cross + 1);
                _hipRec([ix4, cyHip, iz4], [ox4, TT.eaveY, oz4], CHORD.d, CHORD.t, xTag, xr);
                sG.add(xr);
                chordL += xSlope;

                /* its bottom chord */
                _asmMark(ctx, 'hip_chord', 'top', 'Hip truss chord', 'ROT00001');
                var xb = new sT.Mesh(new sT.BoxGeometry(CHORD.t, CHORD.d, xLen), jm);
                xb.position.set((ix4 + ox4) / 2, TT.eaveY, (iz4 + oz4) / 2);
                xb.rotation.y = xYaw;
                xb.userData.trussPart = 'jack_bottom';
                _seatOnHip(xb, apex[0], apex[2], hex4, hez4, CHORD.t / 2,
                           null, null, xSide, false);
                _hipRec([ix4, TT.eaveY, iz4], [ox4, TT.eaveY, oz4], CHORD.d, CHORD.t, xTag, xb);
                sG.add(xb);
                chordL += xLen;

                /* and the post at the hip end, under the rafter and on its face */
                /* the same heel post on this family */
                var xTanJ = (cyHip - TT.eaveY) / Math.max(0.2, xLen);
                var xBot = TT.eaveY + CHORD.d / 2;
                /* the same clearance on this family */
                var xRate = Math.abs(hez4 * (xdx / xdL) - hex4 * (xdz / xdL));
                var xClr = (CHORD.t / 2) / Math.max(0.15, xRate);
                var xPost = WEB.d / 2 + HEEL_SET;
                var xTop = (cyHip - xPost * xTanJ) -
                           (CHORD.d / 2) / Math.cos(Math.atan(xTanJ));
                var xpH = xTop - xBot;
                if (xpH > 0.1) {
                  _asmMark(ctx, 'hip_web', 'top', 'Hip truss web', 'ROT00002');
                  var xp = new sT.Mesh(
                    _heelPost(sT, xpH, xTanJ, xYaw,
                              ix4 + xdx / xdL * xPost, iz4 + xdz / xdL * xPost,
                              apex[0], apex[2], hex4, hez4, CHORD.t / 2, xSide), jm);
                  xp.position.set(ix4 + xdx / xdL * xPost,
                                  (xTop + xBot) / 2,
                                  iz4 + xdz / xdL * xPost);
                  xp.rotation.y = xYaw;
                  xp.userData.trussPart = 'jack_post';
                  _hipRec([ix4 + xdx / xdL * xPost, xBot, iz4 + xdz / xdL * xPost],
                          [ix4 + xdx / xdL * xPost, xTop, iz4 + xdz / xdL * xPost],
                          WEB.d, WEB.t, xTag, xp, true);
                  sG.add(xp);
                  webL += xpH;
                }
                pTag = 'JKB' + (cross + 1);
                jackWebs(ix4, cyHip, iz4, ox4, TT.eaveY, oz4, TT.eaveY, xYaw, xTag, xpH > 0.1);
                cross++;
              }

              /* AND THE SHORTENED JACK SAYS WHERE IT STANDS, LIKE THE TRUSSES DO.

                 The counter-battens sit on SV.memberPts, and until now only the main
                 run was in it -- so the side planes were battened between the ridge
                 ends and bare over both hip ends, which is exactly the corner he is
                 looking at. Nothing here moves: the jack keeps the position the
                 frame gave it, clear0 and all, and the batten comes to find it.

                 One entry per station, not per side: both jacks of a pair stand in
                 the same vertical plane, and that plane is what a batten is cut by.
                 The dedupe is there because a pass without SHOW_TRUSSES leaves the
                 list from the pass before. */
              if (cross > builtB && SV.memberPts) {
                var dupJ = false, dq4;
                for (dq4 = 0; dq4 < SV.memberPts.length; dq4++) {
                  if (Math.hypot(SV.memberPts[dq4][0] - cx4,
                                 SV.memberPts[dq4][1] - cz4) < 0.05) { dupJ = true; break; }
                }
                if (!dupJ) {
                  SV.memberPts.push([cx4, cz4, axZ2 ? 1 : 0, axZ2 ? 0 : 1, ix4, iz4, ox4, oz4]);
                  pubJ.push(Math.round((axZ2 ? cz4 : cx4) * 1000));
                  SV.hipSideJacks.push(axZ2 ? cz4 : cx4);
                }
              }
            }

            ctx.log('HIP  ' + jacks + ' jack(s) at ' + Math.round(jStep * 1000) +
                    ' mm each side, parallel to the centre truss and starting on the hip');
            ctx.log('HIP  ' + cross + ' jack(s) on the side slopes, hip to side eave');
            ctx.log('BAT  ' + pubJ.length + ' side-slope jack(s) published at ' +
                    pubJ.join(' ') + ' -- the corners of the side planes set out on these');
            /* THE WALL BUILDS BEFORE THE ROOF, so the closures read last build's
               jacks. When the list changed (the first build, a resize) the walls
               are built once more with the fresh one -- one re-run, never a loop. */
            /* the wall skin now builds after the roof, so it reads these fresh */
            var plq;
            for (plq = 0; plq < pLog.length; plq++) ctx.log('PLT ' + pLog[plq]);
            pLog = [];

            hipEnds++;

            /* THE TWO FACES THAT ARE SUPPOSED TO TOUCH, AS NUMBERS. */
            var dirS = (axZ2 ? avz : avx) < 0 ? -1 : 1;
            var ridgeAt = axZ2 ? apex0[2] : apex0[0];
            var trussFace = ridgeAt + dirS * girderHalf;   /* the GIRDER's outer face */
            var postC = axZ2 ? apex[2] : apex[0];
            var postFace = postC - dirS * (diagReach / 2);
            var gapMM = Math.round((postFace - trussFace) * dirS * 1000);
            ctx.log('HIP  face check: ridge end ' + Math.round(ridgeAt * 1000) +
                    ', truss face ' + Math.round(trussFace * 1000) +
                    ', post face ' + Math.round(postFace * 1000) +
                    ' -> ' + (gapMM === 0 ? 'TOUCHING' :
                              (gapMM > 0 ? ('gap ' + gapMM) : ('overlap ' + (-gapMM)))) + ' mm');

            /* the centre truss is a member of the end plane too: published with its
               run (apex to eave), so the end's counter-battens are the centre and
               one per jack either side, all at the same step */
            try { if (SV.memberPts) SV.memberPts.push([apex[0] + avx / avL * (avL - clear0) / 2, apex[2] + avz / avL * (avL - clear0) / 2, avx, avz, apex[0], apex[2], eMid[0], eMid[2]]); } catch (eCT) {}
            ctx.log('HIP  centre truss ' + hipEnds + ' triangle: peak ' +
                    Math.round(clear0 * 1000) + ' mm off the ridge end at ' +
                    Math.round(apex[1] * 1000) + ', apex ' +
                    Math.round(apex[0] * 1000) + ',' + Math.round(apex[1] * 1000) + ',' +
                    Math.round(apex[2] * 1000) + ' -> eave ' +
                    Math.round(eMid[0] * 1000) + ',' + Math.round(eMid[1] * 1000) + ',' +
                    Math.round(eMid[2] * 1000));
          }

          if (hipEnds) {
            _add(LAYER, 'ROT00001', chordL);
            _add(LAYER, 'ROT00002', webL);
            /* two plates at every joint, one on each face -- which is how they are
               pressed, and why the count is doubled here rather than in the loop */
            /* two per joint, one on each face -- and PLATE's code, so the hip end
               lands on the same line as every other truss plate on the roof */
            /* THE TWO SIZES GO ON THEIR OWN TWO LINES.

               They were added together and billed as one code, so a takeoff could
               not tell how many of the big ones to order -- and the big one is the
               expensive one. The loop above has always counted them separately;
               only the bill threw the distinction away. */
            _add(LAYER, PLATE.code, plates * 2);
            _add(LAYER, PLATE.codeBig, platesBig * 2);
            ctx.log('HIP  ' + hips + ' hip member(s) from the same peaks to the corners');
            /* WHETHER EACH SHEET HAS BODIES TO PHOTOGRAPH, said before any
               picture is attempted -- the step after this one renders one truss
               at a time, and a shape with no marked bodies would come back as an
               empty page. */
            try {
              var tgOK = 0, tgQ;
              var TRS2 = _TR.shapes();
              for (tgQ = 0; tgQ < TRS2.length; tgQ++) {
                if ((TRS2[tgQ].tags || []).length) tgOK++;
              }
              ctx.log('truss marks ' + _TR.tagged() + ' bodies marked, ' +
                      TRS2.length + ' shape(s), ' + tgOK +
                      ' of them with bodies to photograph');
            } catch (eTM) {}
            ctx.log('heel  ' + SV.footFlat.n + ' rafter(s) planed on a bottom chord, ' +
                    'worst point off that face ' + Math.round(SV.footFlat.worst * 1000) + ' mm; ' +
                    SV.footFlat.ends + ' heel(s) cut level + plumb, their corner off the chord by ' +
                    Math.round(SV.footFlat.endWorst * 1000) + ' mm' +
                    (SV.footFlat.oneCut ? (', ' + SV.footFlat.oneCut + ' too short for two cuts (single cut)') : ''));
            if (_TR.hipSheet().length) {
              ctx.log('HIP  truss sheets from the hip end: ' + _TR.hipSheet().length +
                      ' short truss(es), members each -- ' + _TR.hipSheet().join(' '));
            }
            if (SV.hipPostsDropped) {
              ctx.log('HIP  ' + SV.hipPostsDropped + ' hip peak post(s) not built -- a hip bears on the ' +
                      'doubled truss at the ridge end, not on an upright of its own; the centre truss keeps its post');
            }
            ctx.log('HIP  ' + hipEnds + ' centre truss(es), ' +
                    Math.round(chordL * 10) / 10 + ' m ROT00001, ' +
                    Math.round(webL * 10) / 10 + ' m ROT00002' +
                    ', ' + (plates * 2) + ' x ' + PLATE.code +
                    ' + ' + (platesBig * 2) + ' x ' + PLATE.codeBig);
          } else {
            ctx.log('HIP  no triangular plane -- no hip end to put a centre truss in');
          }

          /* THE SHELL'S OWN LOG LINE WENT WITH THE SHELL.

             It stayed behind in the framing when I lifted that out, and it reads
             sMade, eY and tY -- three variables that live in the shell block. So
             _roofSkin threw on every build and took ALL SIX skin layers with it,
             which is why nothing appeared at all rather than one layer missing
             (Marek). */
          /* the four plane dumps are gone -- the shape is settled and they were
             four of the longest lines in a snapshot he has to carry by hand */

        }

        if (SHELL_ONLY) {
          /* A SOLID THAT ENCLOSES THE WHOLE ROOF, not four loose faces. */
          var sMat = new sT.MeshStandardMaterial({
            color: 0xC2603A, roughness: 0.85, metalness: 0.0,
            transparent: true, opacity: 0.80, side: sT.DoubleSide });
          var sv = [], sq, sMade = 0;

          /* THE SLOPES RUN ON THE DECK PLANE, WHICH IS THE TOP OF THE TRUSS. */
          var eX0 = 1e9, eX1 = -1e9, eZ0 = 1e9, eZ1 = -1e9, eY = null, tY = null;
          for (sq = 0; sq < planes.length; sq++) {
            var EQ = (planes[sq] && planes[sq].eave) || [];
            for (var eq = 0; eq < EQ.length; eq++) {
              if (EQ[eq][0] < eX0) eX0 = EQ[eq][0];
              if (EQ[eq][0] > eX1) eX1 = EQ[eq][0];
              if (EQ[eq][2] < eZ0) eZ0 = EQ[eq][2];
              if (EQ[eq][2] > eZ1) eZ1 = EQ[eq][2];
              eY = EQ[eq][1];
            }
          }

          for (sq = 0; sq < planes.length; sq++) {
            var DK = _deckPlane(planes[sq]);
            var Q = DK && DK.pts;
            if (!Q || Q.length < 4) continue;
            quad(sv, Q[0], Q[1], Q[2], Q[3]);
            if (DK.eave && tY === null) tY = DK.eave[0][1];
            sMade++;
          }

          /* the box, from the eave up to the top of the bottom chord */
          if (eY !== null && tY !== null && eX1 > eX0) {
            var A = [eX0, eY, eZ0], B = [eX1, eY, eZ0],
                C = [eX1, eY, eZ1], D = [eX0, eY, eZ1];
            var a2 = [eX0, tY, eZ0], b2 = [eX1, tY, eZ0],
                c2 = [eX1, tY, eZ1], d2 = [eX0, tY, eZ1];
            quad(sv, A, B, b2, a2);
            quad(sv, B, C, c2, b2);
            quad(sv, C, D, d2, c2);
            quad(sv, D, A, a2, d2);
            quad(sv, A, B, C, D);
            sMade++;
          }

          var sGeo = new sT.BufferGeometry();
          sGeo.setAttribute('position', new sT.Float32BufferAttribute(sv, 3));
          sGeo.computeVertexNormals();
          sG.add(new sT.Mesh(sGeo, sMat));

          return;
        }

        /* EVERY STEP SAYS WHETHER IT RAN.

           The shell was hiding all six of these, and with it off the question is
           which of them actually build anything on a hip -- a step that quietly
           does nothing looks exactly like a step that is not called. */
        /* the hip end is drawn by now, so its jack trusses can be grouped and
           the whole set handed over for the sheet */
        try { _hipGroup(); } catch (eHG) {}
        try {
          API.reportTrusses(_TR.shapes());
          ctx.log('truss list  ' + _TR.shapes().length + ' shape(s) after the hip end' +
                  (SV.jackBare ? (', ' + SV.jackBare + ' short jack(s) too short for a heel post: no heel plates, the eave joint keeps its plate') : '') +
                  (_TR.missing ? ' -- ERROR the truss recorder is missing, configurator-ui.js is older than core-v254' : '') +
                  (_TK.missing ? ' -- ERROR the truss kit is missing, configurator-ui.js is older than core-v257: no trusses built' : ''));
        } catch (eRT) {}

        if (!(H.bare && H.bare())) {
          _asmMark(ctx, 'roof_sheathing', 'top', 'Roof sheathing', ROOF_DECK.code);
          step(1, 'deck', _skinDeck);
          _asmMark(ctx, 'roof_underlay', 'top', 'Roof underlay', ROOF_UL.code);
          step(2, 'underlay', _skinUnderlay);
        } else ctx.log('skin     MATT\'S ROOF: corrugated sheet on battens on the rafters -- no deck, no underlay, no counter-battens');
        _asmMark(ctx, 'roof_battens', 'top', 'Roof battens', ROOF_BATTEN.batten.code);
        var batFrom = ctx.group.children.length;
        step(3, 'battens', _skinBattens);
        /* RANCH (ranch-v70): and the battens under the cross gables, off the bill too */
        if (XG) try { XG.batCut(ctx, batFrom); } catch (eXB) { ctx.log('XG       main battens cut THREW ' + ((eXB && eXB.message) || eXB)); }
        _asmMark(ctx, 'roof_edges', 'top', 'Drip edges and verges', ROOF_EDGE.drip.code);
        step(4, 'edges', _skinEdges);
        _asmMark(ctx, 'roof_covering', 'top', 'Roof covering', (function () { try { return _covDef().code; } catch (e) { return ''; } })());
        var covFrom = ctx.group.children.length;
        step(5, 'covering', _skinCovering);
        /* RANCH (ranch-v68): the covering cut out under the cross gables, as the bill has it */
        if (XG) try { XG.covCut(ctx, covFrom); } catch (eXC) { ctx.log('XG       main covering cut THREW ' + ((eXC && eXC.message) || eXC)); }
        _asmMark(ctx, 'roof_ridge', 'top', 'Ridge', H.ridgeCode ? H.ridgeCode() : ROOF_RIDGE.code);
        step(6, 'ridge', _skinRidge);

        (function () {
          var out = [], q;
          for (q = 0; q < COVERINGS.length; q++) {
            var C2 = COVERINGS[q];
            var got = _t(C2.i18n, C2.def);
            out.push(C2.key + '=' + (got === C2.def ? 'EN fallback' : got));
          }
          var grp = _t('mc_cov_group', 'Roof covering');
          ctx.log('skin     covering captions: ' + out.join(' | ') + ' | group=' +
                  (grp === 'Roof covering' ? 'EN fallback' : grp));
        })();

        /* NAME THE COVERING AT THE TOP, ALWAYS.

           It was logged inside the shingle branch, so when the answer was "shingle,
           and therefore no battens" the line explaining it sat below the thing it
           explained -- and when the battens vanished the obvious reading was that
           they had broken. They had not: the covering had quietly gone back to the
           default when it became a real STATE field, because a saved project has no
           such key in it.

           Which covering is being built decides three of the six steps. It belongs
           in the first line, not in a branch. */
        (function () {
          var C0 = _covDef(), w0 = _covKey(API.STATE.covering || 'shingle');
          ctx.log('skin     covering ' + C0.key + (C0.key === w0 ? '' : ' (picked ' + w0 + ')') +
                  ' -- ' + (C0.key === 'shingle'
                    ? 'no battens on a shingle roof; pick trapez or seam to see them'
                    : 'battens at ' + ((C0.key === 'seam') ? '400' : '1000') + ' mm'));
        })();

        /* WHERE THE MAIN INSULATION IS, SAID OUT LOUD.

           The six steps here are deck, felt, battens, flashings, covering, ridge --
           and no insulation, which reads as a roof that has none. It has plenty; on
           a cold roof it is in the CEILING, under a vented cavity, and that is the
           whole reason this build-up has a cavity to vent.

           A warm deck is the other way round and the insulation is one of these
           steps. Two different places for the same thing is exactly the sort of
           fact that belongs in the log rather than in someone's head. */
        /* THE FOUR PLANES, ONE LINE EACH. What a hip needs checking against is the
           shape of each plane -- how long its eave is, how long its ridge is, and
           whether the two triangles really close to a point. Rectangles come out
           with eave and ridge equal; a trapezoid narrows; a hip end reads 0. */
        if (R.type === 'hip') {
          var hp = [], hq2;
          for (hq2 = 0; hq2 < planes.length; hq2++) {
            var PL = planes[hq2];
            var eL = PL.eave ? Math.hypot(PL.eave[1][0] - PL.eave[0][0],
                                          PL.eave[1][2] - PL.eave[0][2]) : 0;
            var rL = PL.ridge ? Math.hypot(PL.ridge[1][0] - PL.ridge[0][0],
                                           PL.ridge[1][2] - PL.ridge[0][2]) : 0;
            hp.push('e' + Math.round(eL * 1000) + '/r' + Math.round(rL * 1000) +
                    '=' + Math.round(PL.area || 0) + 'm2');
          }
          ctx.log('HIP  ' + planes.length + ' planes: ' + hp.join('  '));
        }

        ctx.log('skin     main insulation: ' +
                ((R.type === 'flat')
                  ? ('tapered EPS + PIR ' + Math.round(FLAT_ROOF.pir.t * 1000) +
                     ' mm ABOVE the deck -- a warm roof, so it is part of this build-up')
                  : ('blown fill in the CEILING layer, not here -- a cold roof, and the ' +
                     'cavity above it is what these battens ventilate')));

        ctx.log('skin     step ' + ROOF_STEP + ' of 6 -- deck' +
                (ROOF_STEP >= 2 ? ' + underlay' : '') +
                (ROOF_STEP >= 3 ? ' + battens' : '') +
                (ROOF_STEP >= 4 ? ' + flashings' : '') +
                (ROOF_STEP >= 5 ? ' + covering' : '') +
                (ROOF_STEP >= 6 ? ' + ridge' : '') +
                '   <-- the newest is MAGENTA');
    }

    /* =========================================================================
       THE FLAT ROOF MODULE (core-v789, the eighth shared module of the house
       line, its third part -- status/modules.md 8c). The warm flat roof,
       written once: the deck on the rafters, the vapour barrier, the tapered
       EPS falling to the outlet wall with the PIR over it, the membrane, the
       parapet's upstand and its coping all round, the scupper and its
       downspout through the parapet, the ballast, every layer under its own
       name and billed by the area it covers. The ANY HOUSE text (the ADU's
       plus TWO STORY's cells: one fall over every cell, no coping or
       upstand along a seam, no outlet where a cell's low side is not the
       outlet wall) with the products' own parts behind hooks.

         API.houseFlatRoof(ctx, planes, {
           api, layer ('adu-roof'), MAGENTA, FLAT_ROOF, FLAT_STEP,
           PARAPET_UPSTAND, RAFTER, STUD, GUT, isNew, add, quad,
           roofSection, flatBays, flatMembrane, flatTaper, facadeFace,
           dspSweep, wallStuds (getter),
           flatCut (getter) -- ANY HOUSE / TWO STORY: the cell being roofed
         })

       Carried: nothing -- the seven copies were the ADU's word for word; the
       cells' rules only act where a product hands in flatCut.
       ========================================================================= */
    function houseFlatRoof(ctx, planes, H) {
      /* the product's own pieces, under the names the text has always used */
      var API = H.api, LAYER = H.layer, MAGENTA = H.MAGENTA, FLAT_ROOF = H.FLAT_ROOF, FLAT_STEP = H.FLAT_STEP,
          PARAPET_UPSTAND = H.PARAPET_UPSTAND, RAFTER = H.RAFTER, STUD = H.STUD, GUT = H.GUT;
      var isNew = H.isNew, quad = H.quad, roofSection = H.roofSection, _flatBays = H.flatBays,
          _flatMembrane = H.flatMembrane, _flatTaper = H.flatTaper, _facadeFace = H.facadeFace,
          _dspSweep = H.dspSweep, _add = function (cat, code, q) { H.add(code, q); };
      var _wallStudsL = H.wallStuds() || [];
      /* ANY HOUSE / TWO STORY: the cell of the storey's L being roofed (seam
         sides, the one fall's origin, whether its low side has the outlet) */
      var _flatCut = H.flatCut ? H.flatCut() : null;
        var T = ctx.THREE, g = ctx.group;
        var P0 = planes && planes[0] && planes[0].pts;
        if (!P0 || P0.length < 4) { ctx.log('FLAT  no plane to build on'); return; }

        /* IT STOPS AT THE INSIDE FACE OF THE WALL, ON EVERY LAYER. */
        /* AND IT RUNS OUT TO THE OUTSIDE OF THE WALL PANELS. */
        var wtR = API.wallThickness();
        var x0 = Math.min(P0[0][0], P0[2][0]) + wtR, x1 = Math.max(P0[0][0], P0[2][0]) - wtR;
        var z0 = Math.min(P0[0][2], P0[2][2]) + wtR, z1 = Math.max(P0[0][2], P0[2][2]) - wtR;
        var top = P0[0][1];
        var w = x1 - x0, d = z1 - z0;
        if (!(w > 0.2) || !(d > 0.2)) return;
        var area = w * d;

        /* AND EACH LAYER ANSWERS TO ITS OWN NAME.

           The whole build-up shared one id, adu-roof.flat, so asking for the deck
           in magenta lit nothing -- the layer under review does not exist by that
           name here. The pitched roofs name their layers one by one; this one now
           does the same, so the same request works on all three. */
        var step = 0;
        function slab(t, yUp, col, rough, id) {
          step++;
          if (FLAT_STEP >= 0 && step > FLAT_STEP) return yUp + t;
          var m = new T.Mesh(new T.BoxGeometry(w, t, d),
            new T.MeshStandardMaterial({
              color: (isNew(id) || isNew(LAYER + '.flat')) ? MAGENTA : col,
              roughness: rough, metalness: 0.0, side: T.DoubleSide }));
          m.position.set((x0 + x1) / 2, yUp + t / 2, (z0 + z1) / 2);
          g.add(m);
          return yUp + t;
        }

        /* THE ORDER, AND THE HEIGHT EACH LAYER REALLY SITS AT.

           The plane is the BEARING line -- the top of the plate, where a rafter
           lands -- so the rafters occupy the 400 mm ABOVE it. I put the insulation
           bays 400 mm BELOW it instead, in the ceiling's space, which is the mass
           that flickered under the deck.

             plane          3210
             rafters        3210 .. 3610
             bays were      2810 .. 3210     a rafter depth too low
             deck           3610             on the rafters, not under them

           From inside out: board and battens (the ceiling layer), insulation
           between the rafters, deck on top of them, then barrier, geotextile and
           membrane. Only the parapet is touched -- the other two roofs are settled
           and are built by different code. */
        var y = top;
        _flatBays(ctx, x0, x1, z0, z1, top, wtR);
        y += RAFTER.d;

        /* NOTHING ABOVE THE INSULATION UNTIL THE INSULATION IS RIGHT.

           He asked for it plainly and the last version ignored it in two places:
           the geotextile and the coping were drawn outside the step gate, so a thin
           grey sheet and a metal frame floated over a roof whose layers were
           hidden. Everything now answers to the same gate. */
        var showAbove = (FLAT_STEP < 0 || FLAT_STEP >= 3);

        /* THE ORDER IS THE BUILD-UP: deck 1, barrier 2, flat EPS 3, taper 4,
           geotextile 5, membrane 6, ballast 7, coping 8. The flat board is a slab
           like the deck; the taper has a sloping top and counts its own step. */
        /* EVERYTHING OVER THE WEDGE LIES ON THE WEDGE.

           The geotextile, the membrane, the outlets and the gravel sat on one
           level line at the mean thickness, which put the sheet 77 mm above the
           outlets and 77 mm inside the wedge at the far wall. surf(x) is the top
           of the wedge at any x -- thin end plus the climb -- and each layer above
           it is drawn at its own offset over that line, tilted to the same fall.
           y, the level bookkeeping, keeps the MEAN for what still wants one number
           (the coping and the pipe run). */
        var tMean = 0, EP = FLAT_ROOF.eps, TP = FLAT_ROOF.taper;
        var yWedge = 0, over = 0;
        var fxS = (_flatCut && isFinite(_flatCut.fallX0)) ? _flatCut.fallX0 : x0;   /* anyhouse-lib-v31 */
        function surf(px) { return yWedge + TP.min + TP.fall * (px - fxS) + over; }
        var fallAng = Math.atan2(TP.fall, 1);
        function sloped(t, col, rough, id) {
          step++;
          if (FLAT_STEP < 0 || step <= FLAT_STEP) {
            var m2 = new T.Mesh(new T.BoxGeometry(w / Math.cos(fallAng), t, d),
              new T.MeshStandardMaterial({
                color: (isNew(id) || isNew(LAYER + '.flat')) ? MAGENTA : col,
                roughness: rough, metalness: 0.0, side: T.DoubleSide }));
            m2.position.set((x0 + x1) / 2, surf((x0 + x1) / 2) + t / 2, (z0 + z1) / 2);
            m2.rotation.z = fallAng;
            g.add(m2);
          }
          over += t;
        }
        if (showAbove) {
          y = slab(FLAT_ROOF.deck.t, y, 0xC9B48D, 0.9, LAYER + '.deck');
          y = slab(0.001, y, 0x3E4A56, 0.95, LAYER + '.underlay');
          y = slab(EP.t, y, 0xF2EFE8, 0.95, LAYER + '.eps');
          yWedge = y;
          step++;
          tMean = _flatTaper(ctx, x0, x1, z0, z1, y, (FLAT_STEP < 0 || step <= FLAT_STEP));
          y += tMean;
          sloped(0.001, 0x9AA0A6, 0.95, LAYER + '.geo');
          y += 0.001;
          step++;
          if (FLAT_STEP < 0 || step <= FLAT_STEP) {
            y = _flatMembrane(ctx, x0, x1, z0, z1, surf, top);
          } else {
            y += 0.0015;
          }
          over += 0.0015;
        } else {
          yWedge = y + FLAT_ROOF.deck.t + 0.001 + EP.t;
          tMean = _flatTaper(ctx, x0, x1, z0, z1, yWedge, false);
          y += FLAT_ROOF.deck.t + 0.0035 + EP.t + tMean;
          over += 0.0025;
        }
        ctx.log('FLAT  slope: the geotextile, the membrane, the outlets and the gravel lie ' +
                'on the wedge now -- surface ' + Math.round(surf(x0) * 1000) + ' at the ' +
                'outlet wall, ' + Math.round(surf(x1) * 1000) + ' at the far one; the ' +
                'level line at the mean (' + Math.round((yWedge + tMean) * 1000) +
                ') is gone from everything but the coping and the pipe run');
        _add(LAYER, EP.code, area);
        ctx.log('FLAT  eps ' + API.fmtArea(area) + ' of ' + EP.code + ', the flat ' +
                Math.round(EP.t * 1000) + ' board ' + Math.round(EP.w * 1000) + ' x ' +
                Math.round(EP.h * 1000) + ' on the vapour barrier, one slab for now, ' +
                'billed by area -- the constant layer the roof gets its R from; the ' +
                'wedge on it only makes the fall');
        _add(LAYER, FLAT_ROOF.deck.code, area);
        _add(LAYER, FLAT_ROOF.vb.code, area * 1.15);
        _add(LAYER, FLAT_ROOF.geo.code, area * 1.10);

        var cw = FLAT_ROOF.coping.w;
        var upToPar = top + roofSection().d + RAFTER.deck + PARAPET_UPSTAND;
        var SMc = (_flatCut && _flatCut.seam) || {};   /* anyhouse-lib-v31: no coping along a seam */
        var lenXc = (x1 - x0) + (SMc.x0 ? 0 : wtR) + (SMc.x1 ? 0 : wtR), lenZc = (z1 - z0) + (SMc.z0 ? 0 : wtR) + (SMc.z1 ? 0 : wtR);
        var per = (SMc.z0 ? 0 : lenXc) + (SMc.z1 ? 0 : lenXc) + (SMc.x0 ? 0 : lenZc) + (SMc.x1 ? 0 : lenZc);
        var showCap = showAbove && (FLAT_STEP < 0 || FLAT_STEP >= 8);
        /* AND IT HAS TO CLEAR THE FACADE, WHICH IS NOW ON THE PARAPET.

           The cap stood 20 mm past the timber. That was right while the parapet
           was bare -- and it is wrong the moment the facade climbs it, because the
           cladding or the render is 130 to 240 mm thick and the cap would end
           INSIDE it, tipping every drop of water it collects straight down behind
           the boarding. A coping that stops short of what it covers is worse than
           none: it is a funnel.

           So the projection is the facade plus a drip. It is measured, not
           written down: _facadeFace answers whichever build-up is on, so the same
           parapet gets a wider cap on the contact wall than on the ventilated one
           and neither is a special case.

           AND THE ROW IS CHECKED AGAINST IT. ROA00002 is a 300 mm coping; if the
           sheet has to reach further than that, the catalogue is short and the log
           says so with the number rather than quietly drawing something nobody
           sells. */
        var facePar = 0;
        try { facePar = _facadeFace(); } catch (eF) { facePar = 0; }
        /* THE COPING'S OVERHANG HAS ITS OWN NAME (core-v806, found by the KIOSK
           thread, kiosk-v9): it was a second 'var over' -- the same variable
           surf() adds for the build-up -- so every surface after the coping
           (the outlet domes, the gravel) stood the facade's width over the
           membrane (the kiosk's domes floated 103 mm) */
        var overC = (API.STATE.roof.parapet ? (facePar + 0.030) : 0.020);
        var outL = 0.070, inL = 0.060;
        var xo0 = SMc.x0 ? x0 : x0 - wtR - overC, xo1 = SMc.x1 ? x1 : x1 + wtR + overC;   /* anyhouse-lib-v31: a run stops at a seam */
        var zo0 = SMc.z0 ? z0 : z0 - wtR - overC, zo1 = SMc.z1 ? z1 : z1 + wtR + overC;
        /* ON the cap plate, not IN it.

           upToPar is the top of the wall, and the cap plate's own top face is at
           exactly that height -- so the coping was drawn in the same plane as the
           timber, the timber won, and all that showed was the 20 mm sticking out
           past the face. A strip along the edge instead of a cap over the parapet,
           which is what he is looking at.

           Four millimetres up puts the sheet on the wall rather than inside it. */
        /* AND THE WHOLE SHEET CLEARS THE MEMBRANE, not just its outer edge.

           The fall is inward, so the inner edge is the LOW one -- and I hung the
           high edge 4 mm over the wall top, which put the low edge 8 mm under it.
           The membrane is turned up to exactly that height, so it stood in front of
           the inner half of the cap and what showed was a strip of top and the
           outer face. Right shape, wrong height, and the wrong half hidden.

           The LOW edge is the one to clear: it sits 4 mm over the membrane, and the
           high edge is the fall above that. */
        var yIn = upToPar + 0.004, yTop = yIn + 0.012;   /* the inward fall */

        var cm2 = new T.MeshStandardMaterial({
          color: isNew(LAYER + '.coping') ? MAGENTA : 0x3A3E42,   /* anthracite */
          roughness: 0.42, metalness: 0.62, side: T.DoubleSide });

        if (showCap) {
          var vv2 = [];
          /* each side: outer pair on the outer rect, inner pair on the inner rect */
          var runs = [
            [[xo0, zo0], [xo1, zo0], [x1, z0], [x0, z0]],
            [[xo1, zo0], [xo1, zo1], [x1, z1], [x1, z0]],
            [[xo1, zo1], [xo0, zo1], [x0, z1], [x1, z1]],
            [[xo0, zo1], [xo0, zo0], [x0, z0], [x0, z1]]
          ], rq;
          for (rq = 0; rq < runs.length; rq++) {
            if ([SMc.z0, SMc.x1, SMc.z1, SMc.x0][rq]) continue;   /* anyhouse-lib-v31: none along a seam */
            var R2 = runs[rq];
            var oA = [R2[0][0], yTop, R2[0][1]], oB = [R2[1][0], yTop, R2[1][1]];
            var iB = [R2[2][0], yIn, R2[2][1]], iA = [R2[3][0], yIn, R2[3][1]];
            /* the top, falling inward */
            quad(vv2, oA, oB, iB, iA);
            /* the leg down the outer face, and its drip */
            quad(vv2, oA, oB,
                 [oB[0], yTop - outL, oB[2]], [oA[0], yTop - outL, oA[2]]);
            /* THE LEG DOWN INSIDE, IN FRONT OF THE MEMBRANE.

               It was drawn on exactly the line the membrane is turned up to, so
               the two shared a plane for the whole 60 mm and the leg was behind it
               -- present, billed, and invisible.

               It laps OVER the upstand, so it belongs a few millimetres further in:
               the membrane's own thickness plus enough not to fight it. Same fault
               as the cap had against the plate a version ago, one axis over. */
            var inOff = 0.005;
            var ax = (iA[0] === iB[0]) ? ((iA[0] > (x0 + x1) / 2) ? -inOff : inOff) : 0;
            var az = (iA[2] === iB[2]) ? ((iA[2] > (z0 + z1) / 2) ? -inOff : inOff) : 0;
            var jA = [iA[0] + ax, yIn, iA[2] + az];
            var jB = [iB[0] + ax, yIn, iB[2] + az];
            quad(vv2, jA, jB,
                 [jB[0], yIn - inL, jB[2]], [jA[0], yIn - inL, jA[2]]);
          }
          var geo2 = new T.BufferGeometry();
          geo2.setAttribute('position', new T.Float32BufferAttribute(vv2, 3));
          geo2.computeVertexNormals();
          g.add(new T.Mesh(geo2, cm2));
        }

        if (!(_flatCut && _flatCut.noOutlet)) {   /* anyhouse-lib-v31: a cell whose low side is not the outlet wall has none */
        /* TWO OUTLETS ON THE LOW SIDE, EACH WITH ITS OWN PIPE. */
        var D2 = FLAT_ROOF.drain;
        /* ANTHRACITE FOR THE PIPEWORK, GALVANISED FOR THE BRACKETS -- which is what
           they are, and it makes the fixings legible against the pipe instead of
           one dark shape running down the wall. */
        var dm = new T.MeshStandardMaterial({
          /* NO LONGER MAGENTA-AWARE. The pipework is settled, so it wears its own
             colour whatever is under review -- asking for the ballast and getting
             the downspouts as well is how a review loses its point. */
          color: 0x3A3E42, roughness: 0.45, metalness: 0.55, side: T.DoubleSide });
        var bm2 = new T.MeshStandardMaterial({
          color: 0x9A9EA3, roughness: 0.5, metalness: 0.6, side: T.DoubleSide });

        var lowX = x0 - wtR;                       /* the low edge, at the wall face */
        var stand = API.facadeThk() + GUT.standoff + D2.pipeD / 2;
        var runL = z1 - z0, dq;

        /* BETWEEN THE FIRST AND SECOND STUD, NOT AT A FRACTION OF THE WALL. */
        var wallZ = [], wq;
        for (wq = 0; wq < _wallStudsL.length; wq++) {
          var W2 = _wallStudsL[wq];
          if (Math.abs(W2.x - lowX) > STUD.d + 0.05) continue;
          wallZ.push(W2.z);
        }
        wallZ.sort(function (a, b) { return a - b; });

        /* AND A PAIR OF STUDS IS NOT A BAY.

           The last two on this wall are 130 mm apart -- a doubled corner post, not
           a cavity -- so the midpoint between them landed on the corner itself and
           the pipe came down outside the building, alongside the framing.

           A bay has to be wide enough to be one: half the module. Walking in from
           each end until that is true finds the real first cavity, and the corner
           double is stepped over rather than measured. */
        var minBay = API.grid() / 2, wi;

        function firstBay(fromStart) {
          var n = wallZ.length, k;
          for (k = 0; k + 1 < n; k++) {
            var a3 = fromStart ? wallZ[k] : wallZ[n - 2 - k];
            var b3 = fromStart ? wallZ[k + 1] : wallZ[n - 1 - k];
            if (b3 - a3 >= minBay) return (a3 + b3) / 2;
          }
          return null;
        }

        var slot = [];
        if (wallZ.length >= 3 && firstBay(true) !== null && firstBay(false) !== null) {
          slot.push(firstBay(true));
          slot.push(firstBay(false));
        } else {
          slot.push(z0 + runL * 0.14);
          slot.push(z0 + runL * 0.86);
          ctx.log('FLAT  outlets on FRACTIONS -- ' + wallZ.length + ' stud(s) found on the low ' +
                  'wall, so the first bay could not be located');
        }

        for (dq = 0; dq < 2; dq++) {
          var dz = slot[dq];
          var dx = x0 + 0.22;

          /* THE OUTLET AS IT IS MADE: a domed grate standing PROUD of the opening.

             A leaf guard is a cage over the hole, not a disc in it -- debris
             collects on the dome and the water goes round and under. Drawn as the
             raised cap plus the flange it clamps the membrane to. */
          /* ON THE SURFACE AT ITS OWN x, which is the thin end of the wedge plus
             220 mm of climb -- not the mean height, which stood it 70 mm in the air */
          var yO = surf(dx);
          var cap = new T.Mesh(new T.SphereGeometry(D2.d * 0.55, 14, 8, 0, Math.PI * 2,
                                                    0, Math.PI / 2), dm);
          cap.position.set(dx, yO + 0.010, dz);
          g.add(cap);
          var ring = new T.Mesh(new T.CylinderGeometry(D2.d * 0.55, D2.d * 0.55, 0.012, 20), dm);
          ring.position.set(dx, yO + 0.006, dz);
          g.add(ring);
          var fl = new T.Mesh(new T.CylinderGeometry(D2.d, D2.d, 0.008, 22), dm);
          fl.position.set(dx, yO + 0.002, dz);
          g.add(fl);

          /* ONE SWEPT PIPE, NOT A CHAIN OF BOXES.

             The gutter downspouts already solve this: _dspSweep runs a single
             extruded section along a path with real arcs at the bends, which is
             why they stopped showing daylight at their corners. Sharp elbows here
             would be the same fault knowingly repeated.

             Down through the build-up, out to the wall, down the face, and a kick
             at the bottom so the water leaves the wall. */
          var xw = lowX - stand;
          var yArm = y - RAFTER.d - 0.10;
          var pts = [
            [dx, yO - 0.02, dz],
            [dx, yArm, dz],
            [xw, yArm, dz],
            [xw, 0.32, dz],
            [xw - 0.16, 0.20, dz]
          ];
          var L2 = _dspSweep(T, g, dm, pts);

          /* THE ELBOW UNDER THE OUTLET. The drain drops vertically and the pipe
             runs horizontally away from it, so there is a bend there and it is a
             fitting, not a length of pipe. One per outlet, turned down. */
          _add(LAYER, D2.bend, 1);

          /* AND IT IS FIXED TO THE WALL. A pipe standing 235 mm off the face on
             nothing is a pipe that is not there in the morning. Brackets at 2 m,
             the same spacing the gutter downspouts use. */
          var dropH = yArm - 0.32;
          var nClip = Math.max(2, Math.ceil(dropH / 2.0));
          var cq;
          /* THE SAME BRACKET THE OTHER TWO ROOFS USE: a strap round the section and
             a thin leg back to the wall. Mine was a solid block the width of the
             whole standoff -- 235 mm of bracket for an 80 mm pipe, which reads as a
             shelf rather than a fixing. There was no reason to design a second one. */
          for (cq = 0; cq < nClip; cq++) {
            var cy = 0.32 + (dropH * (cq + 0.5)) / nClip;
            var st2 = new T.Mesh(new T.BoxGeometry(D2.pipeD + 0.016, 0.016,
                                                   D2.pipeD + 0.016), bm2);
            st2.position.set(lowX - stand, cy, dz);
            g.add(st2);
            var lg2 = new T.Mesh(new T.BoxGeometry(GUT.standoff, 0.012, 0.014), bm2);
            lg2.position.set(lowX - stand + D2.pipeD / 2 + GUT.standoff / 2, cy, dz);
            g.add(lg2);
          }
          _add(LAYER, D2.clip, nClip);
          _add(LAYER, D2.pipe, L2 || 3);
          _add(LAYER, D2.elbow, 1);
        }

        _add(LAYER, D2.code, 2);
        _add(LAYER, D2.tape, 4);
        ctx.log('FLAT  2 outlets in the FIRST BAY at each end of the low wall (z=' +
                Math.round(slot[0] * 1000) + ' and ' + Math.round(slot[1] * 1000) +
                ') -- as far into the corner as a pipe goes without meeting a stud');
        ctx.log('FLAT  domed grate proud of the opening, then ONE swept pipe -- the same ' +
                'sweep the gutter downspouts use, with arcs at the bends and a kick at the ' +
                'foot -- standing ' + Math.round(stand * 1000) + ' mm off the frame line');
        }

        /* THE BALLAST, WHICH IS WHAT KEEPS THE MEMBRANE ON THE ROOF. */
        var BAL = FLAT_ROOF.ballast;
        /* PEBBLES, NOT TILES.

           Two tones of big square patch is a chequerboard -- it reads as paving,
           which he said and which is exactly what it looked like. Gravel is loose
           stone: many small lumps, no two the same size, colours running from brown
           through to grey.

           So it is drawn as stones. Each one is an octahedron -- six vertices, the
           cheapest thing that still has facets to catch the light -- scattered on a
           jittered grid so there are no rows. They are collected into three colour
           buckets and each bucket is ONE mesh, so a thousand stones cost three
           objects rather than a thousand.

           THE SCATTER IS DETERMINISTIC. Seeded from the stone's own position, so
           the roof looks the same on every rebuild instead of reshuffling itself
           whenever anything else changes. */
        /* SIX TONES, MOSTLY GREY.

           Three tones and two of them brown read as one brown roof with a bit of
           variation. Washed gravel is mostly grey -- pale limestone through to
           nearly black basalt -- with a few warm stones in it, not the other way
           round.

           Six buckets is still six meshes, which costs nothing next to the stones
           themselves, and it is the difference between a heap of gravel and a
           coloured surface. */
        var bTones = [0xB4B7B4, 0x9BA0A2, 0x83888C, 0x6E7377, 0x9A8E7C, 0x7C6F5E];

        /* AND IT ANSWERS TO THE GATE LIKE EVERYTHING ELSE. It was the one layer
           drawn outside FLAT_STEP, so with the insulation under review the gravel
           floated over the low half of the wedge and the high half came up through
           it. Same step as the membrane it sits on; still billed either way. */
        step++;
        var showBal = showAbove && (FLAT_STEP < 0 || step <= FLAT_STEP);

        /* the bed the stones sit in -- on the slope, like the sheet under it */
        var bed = new T.Mesh(new T.BoxGeometry(w / Math.cos(fallAng), BAL.t * 0.65, d),
          new T.MeshStandardMaterial({
            color: 0x8C9094,   /* the bed, grey like the stones */
            roughness: 1.0, metalness: 0.0 }));
        bed.position.set((x0 + x1) / 2, surf((x0 + x1) / 2) + BAL.t * 0.325, (z0 + z1) / 2);
        bed.rotation.z = fallAng;
        if (showBal) g.add(bed);
        var bufs = [[], [], [], [], [], []], bq;
        /* 55 mm PITCH AND SMALLER STONES.

           I thinned it to 120 for the triangle count and it went the wrong way:
           widely spaced lumps on a flat surface read as debris scattered on grey
           paving, which is what he saw. Gravel has no visible ground between the
           stones -- that is the difference.

           At 55 mm it covered 39 percent and cost 125 000 triangles -- still not
           gravel, and more than the whole building. Chasing full coverage with
           stones alone is the wrong end of the problem.

           A BED UNDER THEM SOLVES IT. Real gravel shows no ground because there IS
           no ground: it is stones all the way down. So a slab in the same colour
           goes underneath and the stones only have to break the surface. Coverage
           reads as complete at a third of the count.

           That is honest about what is drawn -- the bed is gravel, not a floor
           showing through -- and the bill is the volume either way. */
        var pitch2 = 0.100;
        var px2, pz2, seed;

        function rnd(a, b) {
          seed = (seed * 1103515245 + 12345) & 0x7fffffff;
          return a + (seed / 0x7fffffff) * (b - a);
        }

        for (px2 = x0 + pitch2 / 2; px2 < x1; px2 += pitch2) {
          for (pz2 = z0 + pitch2 / 2; pz2 < z1; pz2 += pitch2) {
            seed = Math.abs(Math.round(px2 * 7919) * 104729 + Math.round(pz2 * 7919)) % 0x7fffffff;
            var r2 = rnd(0.018, 0.034);
            var cx2 = px2 + rnd(-pitch2 * 0.35, pitch2 * 0.35);
            var cz2 = pz2 + rnd(-pitch2 * 0.35, pitch2 * 0.35);
            var cy2 = surf(cx2) + BAL.t * 0.65 + rnd(-r2 * 0.3, r2 * 0.25);
            var sy2 = r2 * rnd(0.55, 0.85);
            /* weighted to the greys: four of the six draws land in 0..3 */
            var pick = rnd(0, 1);
            var bi = (pick < 0.72) ? Math.floor(rnd(0, 4)) % 4 : 4 + (Math.floor(rnd(0, 2)) % 2);
            var buf = bufs[bi];
            /* an octahedron: the six points, then its eight faces */
            var P = [[cx2 + r2, cy2, cz2], [cx2 - r2, cy2, cz2],
                     [cx2, cy2, cz2 + r2], [cx2, cy2, cz2 - r2],
                     [cx2, cy2 + sy2, cz2], [cx2, cy2 - sy2, cz2]];
            var F = [[0, 2, 4], [2, 1, 4], [1, 3, 4], [3, 0, 4],
                     [2, 0, 5], [1, 2, 5], [3, 1, 5], [0, 3, 5]];
            for (bq = 0; bq < F.length; bq++) {
              var f = F[bq];
              buf.push(P[f[0]][0], P[f[0]][1], P[f[0]][2],
                       P[f[1]][0], P[f[1]][1], P[f[1]][2],
                       P[f[2]][0], P[f[2]][1], P[f[2]][2]);
            }
          }
        }
        var bn = 0;
        for (bq = 0; bq < bufs.length; bq++) {
          if (!bufs[bq].length) continue;
          var bgeo = new T.BufferGeometry();
          bgeo.setAttribute('position', new T.Float32BufferAttribute(bufs[bq], 3));
          bgeo.computeVertexNormals();
          if (showBal) {
            g.add(new T.Mesh(bgeo, new T.MeshStandardMaterial({
              color: bTones[bq],
              roughness: 1.0, metalness: 0.0, flatShading: true })));
          }
          bn += bufs[bq].length / 24;
        }

        _add(LAYER, BAL.code, area * BAL.t);
        ctx.log('FLAT  ballast ' + Math.round(BAL.t * 1000) + ' mm over ' + API.fmtArea(area) +
                ' = ' + (Math.round(area * BAL.t * 100) / 100) + ' m3 of ' + BAL.code +
                ', drawn as ' + Math.round(bn) + ' stones in six tones, mostly grey -- it holds the ' +
                'membrane down and shades it, which is what actually ages these sheets');

        _add(LAYER, FLAT_ROOF.coping.code, per);
        _add(LAYER, FLAT_ROOF.coping.clip, Math.ceil(per / FLAT_ROOF.coping.spacing));

        /* the developed width the sheet actually needs: down the outside, across
           the parapet, and the turn-down on the roof side */
        var devW = overC + (2 * wtR) + inL;
        if (devW > cw + 0.001) {
          ctx.log('FLAT  coping THE ROW IS SHORT: this cap needs ' +
                  Math.round(devW * 1000) + ' mm developed (' + Math.round(overC * 1000) +
                  ' over the facade, ' + Math.round(2 * wtR * 1000) + ' across the ' +
                  'parapet, ' + Math.round(inL * 1000) + ' turned down inside) and ' +
                  FLAT_ROOF.coping.code + ' is ' + Math.round(cw * 1000) +
                  '. Widen the row or the sheet does not exist -- I have drawn what ' +
                  'the building needs, not what the catalogue sells');
        } else {
          ctx.log('FLAT  coping ' + Math.round(devW * 1000) + ' mm developed against a ' +
                  Math.round(cw * 1000) + ' mm row -- it fits');
        }
        /* THE DRAIN AND THE TAPE ARE BILLED WITH THE OUTLETS, where the count is
           two rather than one. These two lines were left behind from the version
           that had a single outlet -- and FLAT_ROOF.tape does not exist at all, so
           the second one threw every time this roof was built. It threw into the
           layer's catch, which logs and moves on, so what it cost was everything
           after it in the function rather than a message. */

        ctx.log('FLAT  layers run to the OUTSIDE of the wall panels -- the insulation carries ' +
                'over the wall head, which is where a parapet roof bridges if it stops short. ' +
                API.fmtArea(area) + ' of roof');
        ctx.log('FLAT  build-up: wool ' + Math.round(RAFTER.d * 1000) + ' between the rafters, ' +
                'deck ' + Math.round(FLAT_ROOF.deck.t * 1000) + ', membrane up to the parapet ' +
                'top, ballast ' + Math.round(FLAT_ROOF.ballast.t * 1000) + ' -- against a ' +
                Math.round(PARAPET_UPSTAND * 1000) + ' mm upstand');
        ctx.log('FLAT  coping ' + Math.round(per * 10) / 10 + ' m of ' + FLAT_ROOF.coping.code +
                ' at ' + Math.round((0.070 + (x0 - (x0 - wtR - 0.020)) + wtR + 0.060) * 1000) +
                ' mm developed, mitred at the corners, on clips at ' +
                Math.round(FLAT_ROOF.coping.spacing * 1000) + ' mm -- falling INWARD, onto a ' +
                'roof with a membrane and an outlet rather than down a rendered face');
        ctx.log('FLAT  stack: deck ' + Math.round(FLAT_ROOF.deck.t * 1000) + ' + VB + EPS ' +
                Math.round(FLAT_ROOF.eps.t * 1000) + ' + taper ' +
                Math.round(FLAT_ROOF.taper.min * 1000) + '..' +
                Math.round((FLAT_ROOF.taper.min + FLAT_ROOF.taper.fall * w) * 1000) +
                ' (mean ' + Math.round(tMean * 1000) + ') + geo + membrane over ' +
                API.fmtArea(area) + ', coping ' + Math.round(per * 10) / 10 + ' m, 2 outlets; ' +
                'wool ' + Math.round(FLAT_ROOF.pir.t * 1000) + ' x' + FLAT_ROOF.pir.plies +
                ' between the rafters under the deck');
        ctx.log('FLAT  MEMBRANE NOT BILLED -- no TPO/PVC/EPDM row in the catalogue. Drawn and ' +
                'named, not invented; everything else here is a real code');
        _why(ctx, 'skin     the fall is in the tapered boards, not the frame, so the ceiling ' +
                  'stays flat and the parapet is one height all round');
        _why(ctx, 'skin     the coping falls INWARD -- onto a roof with a membrane and an outlet, ' +
                  'rather than down a rendered face');
    }

    /* =========================================================================
       THE FITOUT MODULE (core-v791, the ninth shared module of the house line
       -- status/modules.md 9). The pieces placed on the plan's FITOUT,
       written once: each built in parts in its own frame at the size the plan
       draws it (an appliance at its US stock size, the compact one where the
       full size does not fit), the kitchen runs with their wall units left out
       where an opening is in the way, the L and U kitchens, the islands with
       their stools, the tables of four, six and eight, the washer with a dryer
       stacked on it, beds, sofas, desks, the bathroom; billed by the piece
       with its companion rows, the screws and silicone where they fix
       something, what has no catalogue row named once. The ANY HOUSE text (the
       ADU's plus GARAGE APT's storeys and US practice) with the products' own
       parts behind hooks.

         API.houseFitout(ctx, {
           api, layer ('adu-fitout'), MAGENTA, FINISH, isNew, billReset, add,
           begin (GA: the storey hook; SAUNA: the heater and the benches),
           noneSay / endSay (GA / ANY HOUSE: the plan's notes),
           ga:   { FIT, linAt, WALL_BOARD, WALL_BATTEN, VB_T, roomSt,
                   backList, backSkip, planNote, planSaid } -- GA / ANY HOUSE:
                   a piece on its storey's floor (the garage slab bare), its
                   back off the board, the kitchen run by the box with its
                   faucet, the vanity, the stacking kit
           loft: { fixHook, geom, mm } -- BARNDO: a piece on the loft
           qtyMarks -- RANCH: every body says what it is billed as
         })

       Carried to SAUNA (its copy was the ADU's of before adu-v1416): the
       appliances at their US stock size and standing free (no screws, no
       silicone), the stacked dryer, the island with stools, the tables of
       six and eight, the wall units left out over a tall opening (an HS
       door) and a lone one beside it, the not-billed pieces named once.
       Not carried (candidates): GA's US practice (the run by the box, the
       faucet, the vanity, the stacking kit -- its rows are PENDING) and
       RANCH's qtyMarks for the others.
       ========================================================================= */
    function houseFitout(ctx, H) {
      /* the product's own pieces, under the names the text has always used */
      var API = H.api, LAYER = H.layer, MAGENTA = H.MAGENTA, FINISH = H.FINISH, isNew = H.isNew;
      var _billReset = H.billReset, _add = function (cat, code, q) { H.add(code, q); };
      /* the products' own fitout, each behind its hook (status/modules.md 9):
         GA    GARAGE APT / ANY HOUSE -- storeys, the back off the board, US practice
         LOFT  BARNDO -- the loft's pieces */
      var GA = H.ga || null, LOFT = H.loft || null, GA_FIT = GA ? GA.FIT : null;
        /* one step for every piece, named as the group (Marek) */
        _asmMark(ctx, 'container_int_fitout', 'top', 'Fitout', '', true);

        _billReset(LAYER);
        /* GA: the plan's storey hook; SAUNA: the heater and the benches */
        if (H.begin) H.begin(ctx);
        var T = ctx.THREE, g = ctx.group, i;
        var PL = _P();
        var P = API.STATE.product || {}, L = P.fixtures || [];
        if (!PL || !PL.fixtures) { ctx.log('FIT  none -- floorplan asset without fixtures'); return; }
        if (!L.length) { ctx.log('FIT  none -- no piece placed (FITOUT in the floorplan)'); if (H.noneSay) H.noneSay(ctx); return; }
        var cat = PL.fixtures.catalog();
        /* the rows come from the asset's catalogue: registered here so _add
           knows them, by the piece (pcs) */
        var FIT_ROWS = { SKS08401: 'Kitchen sink', SFA00001: 'Washbasin faucet', SFA00002: 'Bathtub faucet set',
                         SFA00003: 'Shower faucet set', FMA00002: 'Mattress', FKB00004: 'Desk chair', FDI00002: 'Dining chair' };
        var kq, cq;
        for (kq in cat) {
          if (!cat.hasOwnProperty(kq)) continue;
          var Dq = cat[kq];
          if (Dq.code && !__CORE.CATALOG[Dq.code]) __CORE.CATALOG[Dq.code] = { code: Dq.code, unit: 'pcs', label: Dq.def };
          if (Dq.extra) for (cq = 0; cq < Dq.extra.length; cq++) {
            if (!__CORE.CATALOG[Dq.extra[cq]]) __CORE.CATALOG[Dq.extra[cq]] = { code: Dq.extra[cq], unit: 'pcs', label: FIT_ROWS[Dq.extra[cq]] || Dq.extra[cq] };
          }
        }
        /* garageapt-v8 (6d): A PIECE STANDS ON THE FLOOR OF ITS STOREY. The plan
           stamps the storey whose card was lit when the piece went in (the
           storey hook, _gaFixHook); a piece of card 2 stands on the
           apartment's floor finish, one of card 1 on the garage slab -- bare
           concrete, nothing laid on it, so nothing under the piece either.
           A piece without a storey is a ground one (as the services read it). */
        var y0 = API.storeys()[0].y0 + ((GA && API.STATE.foundation === 'slab') ? 0 : FINISH.dry.h);
        var gaNS = API.storeys().length, gaYF = [], gaQ;
        for (gaQ = 0; gaQ < gaNS; gaQ++) gaYF.push(gaQ ? (API.storeys()[gaQ].y0 + FINISH.dry.h + _flLift(gaQ)) : y0);   /* core-v817: on the build-up */
        /* BARNDO (barndo-v21, 7e1): A PIECE PLACED ON THE LOFT'S PLAN (card 2)
           STANDS ON THE LOFT -- the plan stamps the storey it was placed on
           (the fixtures' storey hook, as the T house), and it is built on the
           loft's finished floor. It stood on the slab under the loft before. */
        if (LOFT) { try { LOFT.fixHook(PL); } catch (eHk) {} }
        var GF = null, nUpF = 0;
        if (LOFT) { try { GF = LOFT.geom(); } catch (eGF) { GF = null; } }
        var mag = isNew(LAYER);
        function M(col, o) {
          o = o || {};
          return new T.MeshStandardMaterial({ color: mag ? MAGENTA : col, roughness: o.r != null ? o.r : 0.85,
            metalness: o.m || 0, transparent: !!o.a, opacity: o.a != null ? o.a : 1, side: o.a ? T.DoubleSide : T.FrontSide });
        }
        var mWood = M(0xB8956A), mFab = M(0x9A8F82), mFabD = M(0x7E746A), mWhite = M(0xF2F2EE, { r: 0.4 }),
            mSteel = M(0xB7BCC0, { r: 0.35, m: 0.6 }), mGlass = M(0xBFD4DC, { r: 0.1, a: 0.35 }),
            mDark = M(0x3B3D40, { r: 0.6 }), mLinen = M(0xE9E4DA, { r: 0.9 }), mTop = M(0xD9D2C3, { r: 0.5 });
        var n = 0, tally = {};

        /* THE PIECES ARE BUILT IN THEIR OWN FRAME -- x across, z into the room,
           back at -z -- and turned by rot; part() takes a box in that frame. */
        /* garageapt-v32 (17d): THE PLAN'S ROOM ENDS AT THE FRAME, THE ROOM AT
           THE BOARD. A piece with its back on an outer wall was built against
           the frame's face -- 54 into the lining (the vapour retarder, the
           service void, the board), its back through the wool. Built off the
           board's face instead: moved into the room by the lining (the board
           alone on a garage wall). A back that already stands off the frame
           is left where it is. */
        var SEGF = API.segments ? (API.segments() || []) : [], tWF = API.wallThickness(), nOff = 0;
        function backOff(f) {
          var Df = cat[f.kind] || {}, dF = Df.d || 0;
          try { var SZo = PL.fixtures.size ? PL.fixtures.size(f) : null; if (SZo && SZo.d > 0) dF = SZo.d; } catch (eSo) {}
          if (!(dF > 0) || !Df.wall) return null;
          var rot = (f.rot | 0) % 4, c = Math.cos(rot * Math.PI / 2), s = Math.sin(rot * Math.PI / 2);
          var bx = f.x + (dF / 2) * s, bz = f.z - (dF / 2) * c, q;
          for (q = 0; q < SEGF.length; q++) {
            var sg = SEGF[q];
            if (!sg || !(sg.len > 0.2) || (sg.storey | 0) !== (f.st | 0)) continue;
            var ux = (sg.x1 - sg.x0) / sg.len, uz = (sg.z1 - sg.z0) / sg.len;
            if (Math.abs(ux * s - uz * c) > 0.05) continue;   /* the wall runs along the piece's x */
            var din = (bx - sg.x0) * (-sg.nx) + (bz - sg.z0) * (-sg.nz);
            if (Math.abs(din - tWF) > 0.03) continue;
            var u = (bx - sg.x0) * ux + (bz - sg.z0) * uz;
            if (u < -0.05 || u > sg.len + 0.05) continue;
            var lin = GA.linAt(sg, u) + GA.WALL_BOARD.t;
            nOff++;
            return { x: -sg.nx * lin, z: -sg.nz * lin };
          }
          return null;
        }
        function partOf(f) {
          var rot = (f.rot | 0) % 4, c = Math.cos(rot * Math.PI / 2), s = Math.sin(rot * Math.PI / 2);
          var mir = f.mirror ? -1 : 1, OF = (GA && backOff(f)) || { x: 0, z: 0 };
          return function (w, h, d, lx, lz, ly, mat, geo) {
            lx = lx * mir;
            var rx = lx * c - lz * s, rz = lx * s + lz * c;
            var mesh = new T.Mesh(geo || new T.BoxGeometry(w, h, d), mat);
            /* GA (garageapt-v8): the floor of the piece's storey; BARNDO
               (barndo-v21): a piece of the loft on the loft's floor */
            var yP = (GF && f && (f.st | 0) === 1) ? GF.yLF : (GA ? gaYF[f.st | 0] : y0);
            if (typeof yP !== 'number') yP = y0;
            mesh.position.set(f.x + rx + OF.x, yP + (ly || 0) + h / 2, f.z + rz + OF.z);
            mesh.rotation.y = -rot * Math.PI / 2;
            mesh.name = 'fitout';
            mesh.userData.hideInPlan = true;
            mesh.visible = !_planWanted();
            g.add(mesh);
            return mesh;
          };
        }
        function cyl(r, h, seg) { return new T.CylinderGeometry(r, r, h, seg || 20); }

        function sofaRun(part, w, d, h) {
          var seat = 0.42, back = 0.22, arm = 0.2;
          part(w, seat, d, 0, 0, 0, mFab);                                  /* the seat */
          part(w, h - seat, back, 0, -d / 2 + back / 2, seat, mFabD);        /* the back */
          part(arm, 0.2, d - back, -w / 2 + arm / 2, back / 2, seat, mFabD); /* arms */
          part(arm, 0.2, d - back, w / 2 - arm / 2, back / 2, seat, mFabD);
          /* rounded edges: a roll along the top of the back and each arm, and
             cushions with softened corners */
          part(w, 0.06, 0.06, 0, -d / 2 + back / 2, h - 0.03, mFabD, cyl(0.055, w, 12).rotateZ(Math.PI / 2));
          part(0.06, 0.06, d - back, -w / 2 + arm / 2, back / 2, seat + 0.2 - 0.03, mFabD, cyl(0.055, d - back, 12).rotateX(Math.PI / 2));
          part(0.06, 0.06, d - back, w / 2 - arm / 2, back / 2, seat + 0.2 - 0.03, mFabD, cyl(0.055, d - back, 12).rotateX(Math.PI / 2));
          var nC = Math.max(1, Math.round((w - 2 * arm) / 0.7)), cw = (w - 2 * arm) / nC, q;
          for (q = 0; q < nC; q++) {
            part(cw - 0.03, 0.08, d - back - 0.06, -w / 2 + arm + cw * (q + 0.5), back / 2, seat, mFab);
            part(cw - 0.06, 0.05, d - back - 0.09, -w / 2 + arm + cw * (q + 0.5), back / 2, seat + 0.08, mFab);
          }
        }
        function bed(part, w, d, tall) {
          part(w, 0.3, d, 0, 0, 0, mWood);                                   /* frame */
          part(w - 0.08, 0.2, d - 0.08, 0, 0.02, 0.3, mLinen);               /* mattress */
          part(w, tall ? 0.9 : 0.5, 0.06, 0, -d / 2 + 0.03, 0.3, mWood);     /* headboard */
          var pw = (w - 0.2) / 2 - 0.05, q;
          for (q = 0; q < (w > 1.2 ? 2 : 1); q++) {
            var px = (w > 1.2) ? (-w / 4 + (q ? w / 2 : 0)) : 0;
            part(w > 1.2 ? pw : w - 0.3, 0.1, 0.45, px, -d / 2 + 0.35, 0.5, mWhite);
          }
        }
        /* THE UPPER ROW: along the back run between lx0 and lx1 in the piece's
           own x, 600 units, only where the back stands on a wall, and never
           across a window in that wall -- a unit that would sit on a window is
           left out (Marek). The window spans come from the exterior segment the
           back stands against, in the piece's own x. */
        function windowSpans(f, D, dReal) {
          var out = [], rot = (f.rot | 0) % 4, SEGS = API.segments ? API.segments() : [], q, o;
          /* the back line in world: its centre and its direction (local +x) --
             adu-v1427: at the depth the piece is built at (an L drawn 3.60 deep
             is not the catalogue's), or the back line missed its wall */
          var c = Math.cos(rot * Math.PI / 2), s = Math.sin(rot * Math.PI / 2), dB = (dReal > 0) ? dReal : D.d;
          var bx = f.x + (0 * c - (-dB / 2) * s), bz = f.z + (0 * s + (-dB / 2) * c);
          /* local +x in world -- mirrored with the piece, so the spans land on
             the same units part() puts the cabinets on */
          var mir = f.mirror ? -1 : 1;
          var dx = c * mir, dz = s * mir;
          for (q = 0; q < SEGS.length; q++) {
            var sg = SEGS[q]; if (!sg || !(sg.len > 0.2)) continue;
            /* garageapt-v8: the wall of the piece's own storey -- the garage's
               windows under the apartment's kitchen are not behind it */
            if (GA && (sg.storey | 0) !== (f.st | 0)) continue;
            var ux = (sg.x1 - sg.x0) / sg.len, uz = (sg.z1 - sg.z0) / sg.len;
            /* parallel and within 450 mm of the line -- adu-v1427: the segment is the
               wall's axis, and a block house's 440 block with its plaster puts
               the room's face 0.24 in from it */
            if (Math.abs(ux * dz - uz * dx) > 0.2) continue;
            var nx = -uz, nz = ux;
            var dist = Math.abs((bx - sg.x0) * nx + (bz - sg.z0) * nz);
            if (dist > 0.45) continue;
            var ops = sg.openings || [], k2;
            for (k2 = 0; k2 < ops.length; k2++) {
              o = ops[k2];
              /* WHAT REACHES UP TO THE CABINETS IS IN THEIR WAY (adu-v1427, Marek:
                 vrchni skrinky kuchynske linky jsou pres stavebni otvor / okno).
                 A sill above the slab was the test for a window, and a door was
                 never in the way of a cabinet at 1.45 -- so the 2.59 m HS sliding
                 door behind the kitchen of plan 1000076010 had its glass under a
                 row of wall units. Any opening whose head is above the units'
                 underside is in the way: a window, French or sliding doors, a
                 door; a low window under 1.45 is not. */
              if (((o.sill || 0) + (o.h || 0)) <= 1.45) continue;
              /* the window centre in world, then along the piece's x */
              var wx = sg.x0 + ux * o.at, wz = sg.z0 + uz * o.at;
              var lx = (wx - f.x) * dx + (wz - f.z) * dz;
              /* the frame plus the reveal lining and its trim on both jambs, and
                 a hand's clearance: a cabinet that touches the trim is one too
                 many (Marek: the cabinets ran into the window) */
              out.push([lx - o.w / 2 - 0.16, lx + o.w / 2 + 0.16]);
            }
          }
          return out;
        }
        function uppers(f, part, lx0, lx1, d) {
          var D = cat[f.kind];
          /* one step per piece, named as the catalogue names the piece (a
             kitchen is a kitchen, not the carcass line of its budget) -- Marek */

          if (!(PL.fixtures.backAtWall && PL.fixtures.backAtWall(f))) return { n: 0, joints: 0 };
          var spans = windowSpans(f, D, d), run = lx1 - lx0, drawn = [];
          var nU = Math.max(1, Math.round(run / 0.6)), uw = run / nU, qu, sq, skipped = 0, HIT = [];
          for (qu = 0; qu < nU; qu++) {
            var a = lx0 + uw * qu, b = a + uw, hit = false;
            for (sq = 0; sq < spans.length; sq++) if (a < spans[sq][1] && b > spans[sq][0]) { hit = true; break; }
            HIT.push(hit);
          }
          /* adu-v1427: the counter stays, the wall units go where the opening
             is -- and a single unit left alone beside it goes too (Marek:
             pokud je tam okno, muzes nechat kuchynskou linku ale bez vrchnich
             skrinek) */
          for (qu = 0; qu < nU; qu++) {
            if (HIT[qu]) { skipped++; drawn.push(false); continue; }
            var lone = (qu === 0 || HIT[qu - 1]) && (qu === nU - 1 || HIT[qu + 1]) && HIT.indexOf(true) >= 0;
            if (lone) { skipped++; drawn.push(false); continue; }
            var a2 = lx0 + uw * qu, b2 = a2 + uw;
            part(uw - 0.02, 0.7, 0.35, (a2 + b2) / 2, -d / 2 + 0.175, 1.45, mWood);
            drawn.push(true);
          }
          var jU = 0; for (qu = 1; qu < drawn.length; qu++) if (drawn[qu] && drawn[qu - 1]) jU++;
          ctx.log('FIT  uppers on ' + f.kind + ': ' + (nU - skipped) + ' of ' + nU + ' unit(s), ' + spans.length +
                  ' window span(s) behind' + (spans.length ? (' ' + spans.map(function (sp) { return Math.round(sp[0] * 1000) + '..' + Math.round(sp[1] * 1000); }).join(' ')) : ''));
          return { n: nU - skipped, joints: jU };
        }
        /* garageapt-v32 (17d): the kitchen run fixed and finished by the box */
        var KITS = [], VANS = 0, STACKS = 0;
        function kitBill(f, k, runLen, U) {
          var nB = Math.max(1, Math.round((runLen - GA_FIT.range - GA_FIT.dw) / GA_FIT.box));
          var nU = U ? U.n : 0, jU = U ? U.joints : 0;
          var wS = nB * 2 + nU * 4, jS = (nB - 1) * 2 + jU * 2, top = Math.round(Math.max(0, runLen - GA_FIT.range) * 100) / 100;
          _add(LAYER, GA_FIT.wallScrew, wS);
          if (jS > 0) _add(LAYER, GA_FIT.joinScrew, jS);
          if (top > 0) _add(LAYER, GA_FIT.top, top);
          _add(LAYER, GA_FIT.faucet, 1);
          KITS.push(k + ': ' + nB + ' base box(es) x 2 + ' + nU + ' wall unit(s) x 4 = ' + wS + ' x ' + GA_FIT.wallScrew + ', ' + jS + ' x ' + GA_FIT.joinScrew + ' at the joints; countertop ' + top.toFixed(2) + ' m (the run ' + runLen.toFixed(2) + ' less the range)');
        }

        function chair(part, lx, lz, back) {
          part(0.42, 0.05, 0.42, lx, lz, 0.42, mWood);
          part(0.42, 0.45, 0.04, lx, lz + back * 0.19, 0.47, mWood);
          var q, o = 0.17;
          for (q = 0; q < 4; q++) part(0.03, 0.42, 0.03, lx + (q % 2 ? o : -o), lz + (q > 1 ? o : -o), 0, mWood);
        }
        /* a chair at the end of a table: its back along the piece's own x
           (adu-v1416, the table of eight) */
        function chairEnd(part, lx, lz, back) {
          part(0.42, 0.05, 0.42, lx, lz, 0.42, mWood);
          part(0.04, 0.45, 0.42, lx + back * 0.19, lz, 0.47, mWood);
          var q, o = 0.17;
          for (q = 0; q < 4; q++) part(0.03, 0.42, 0.03, lx + (q % 2 ? o : -o), lz + (q > 1 ? o : -o), 0, mWood);
        }
        /* a stool at a counter: round seat, steel post, round foot */
        function stool(part, lx, lz, seat) {
          part(0.36, 0.05, 0.36, lx, lz, seat, mDark, cyl(0.18, 0.05, 20));
          part(0.05, seat, 0.05, lx, lz, 0, mSteel, cyl(0.025, seat, 12));
          part(0.4, 0.02, 0.4, lx, lz, 0, mSteel, cyl(0.2, 0.02, 20));
        }
        var noRow = {}, sizedUS = 0, nCompact = 0, stackedBilled = 0;

        /* garageapt-v8: each piece is read on its own storey -- its room, the
           wall at its back (the plan's rooms and partitions of that card) */
        var gaPs = API.STATE.planStorey | 0, gaRs = GA ? GA.roomSt() : 0, gaOn = [0, 0], gaOff = 0;
        try {
        for (i = 0; i < L.length; i++) {
          var f = L[i], D = cat[f.kind];
          if (!D) continue;
          if (f.ahStair) continue;   /* anyhouse-lib-v6: the stair's piece -- the stair layer builds and bills the flight */
          if (GA) {
            var gaSt = f.st | 0;
            if (gaSt >= gaNS) { gaOff++; continue; }   /* a piece of a storey the house no longer has */
            gaOn[gaSt ? 1 : 0]++;
            API.STATE.planStorey = gaSt; GA.roomSt(gaSt);
          }
          if (GF && (f.st | 0) === 1) nUpF++;
          var part = partOf(f), w = D.w, d = D.d, h = D.h, k = f.kind;
          var cP0 = g.children.length, cDry = -1;   /* ranch-v83: the piece's bodies, for the audit's count */
          /* AT THE SIZE THE PLAN DRAWS IT (adu-v1417): the asset gives an
             appliance its US stock size on an imperial page -- a 27 in washer
             and dryer, a 36 in fridge -- or the compact one where that does not
             fit (floorplan v151); the model was built from the catalogue's
             600 mm whatever the plan showed */
          try {
            var SZf = (PL.fixtures.size) ? PL.fixtures.size(f) : null;
            if (SZf && SZf.w > 0 && SZf.d > 0) { w = SZf.w; d = SZf.d; }
            if (PL.fixtures.height) { var Hf = +PL.fixtures.height(f); if (Hf > 0) h = Hf; }
          } catch (eSZ) {}
          if (D.appliance && D.wi > 0 && Math.abs(w - D.w) > 0.01) sizedUS = (sizedUS || 0) + 1;
          if (f.compact) nCompact++;
          if (/^kitchen(24|30|36)$/.test(k)) {
            /* base cabinets, a worktop, the sink and the hob in the top -- no
               tall column (Marek); the fridge is in the row */
            var run = w;
            part(run, 0.85, d - 0.02, 0, 0.01, 0, mWood);
            part(run, 0.04, d, 0, 0, 0.85, mTop);
            part(0.5, 0.02, 0.4, -w / 2 + 0.45, 0, 0.89, mSteel);
            part(0.58, 0.015, 0.5, w / 2 - 0.55, 0, 0.89, mDark);
            var q1; for (q1 = 0; q1 < 4; q1++) part(0.18, 0.005, 0.18, w / 2 - 0.55 + (q1 % 2 ? 0.14 : -0.14), (q1 > 1 ? 0.12 : -0.12), 0.905, mSteel, cyl(0.09, 0.005, 24));
            /* GA (garageapt-v32): the faucet behind the sink, its spout over the
               bowl, and the run billed by the box */
            if (GA) {
              part(0.03, 0.30, 0.03, -w / 2 + 0.45, -0.25, 0.89, mSteel, cyl(0.015, 0.30, 12));
              part(0.025, 0.025, 0.18, -w / 2 + 0.45, -0.16, 1.165, mSteel);
              kitBill(f, k, run, uppers(f, part, -w / 2, w / 2, d));
            } else uppers(f, part, -w / 2, w / 2, d);
          } else if (k === 'kitchenL' || k === 'kitchenU') {
            var leg = 0.6;
            part(w, 0.85, leg, 0, -d / 2 + leg / 2, 0, mWood); part(w, 0.04, leg, 0, -d / 2 + leg / 2, 0.85, mTop);
            part(leg, 0.85, d - leg, -w / 2 + leg / 2, leg / 2, 0, mWood); part(leg, 0.04, d - leg, -w / 2 + leg / 2, leg / 2, 0.85, mTop);
            if (k === 'kitchenU') { part(leg, 0.85, d - leg, w / 2 - leg / 2, leg / 2, 0, mWood); part(leg, 0.04, d - leg, w / 2 - leg / 2, leg / 2, 0.85, mTop); }
            part(0.5, 0.02, 0.4, 0, -d / 2 + leg / 2, 0.89, mSteel);
            part(0.58, 0.015, 0.5, -w / 2 + leg / 2, d / 2 - 0.5, 0.89, mDark);
            if (GA) {
              part(0.03, 0.30, 0.03, 0, -d / 2 + 0.05, 0.89, mSteel, cyl(0.015, 0.30, 12));
              part(0.025, 0.025, 0.18, 0, -d / 2 + 0.14, 1.165, mSteel);
              kitBill(f, k, w + (d - leg) * (k === 'kitchenU' ? 2 : 1), uppers(f, part, -w / 2, w / 2, d));
            } else uppers(f, part, -w / 2, w / 2, d);
          } else if (k === 'island') {
            part(w, 0.85, d, 0, 0, 0, mWood); part(w + 0.1, 0.04, d + 0.1, 0, 0, 0.85, mTop);
          } else if (k === 'island18' || k === 'island24') {
            /* THE ISLAND WITH ITS STOOLS (adu-v1416): the counter at the back of
               the set -- cabinets, the worktop running 0.25 m over their front
               for the knees -- and a stool to every 0.6 m in front of it */
            var itw = D.tw || w, itd = D.td || 0.9, ibz = -d / 2 + itd / 2, nSt = D.stools || Math.max(1, Math.round(itw / 0.6));
            part(itw - 0.05, 0.85, itd - 0.3, 0, ibz - 0.15, 0, mWood);
            part(itw, 0.04, itd, 0, ibz, 0.86, mTop);
            var qi; for (qi = 0; qi < nSt; qi++) stool(part, -itw / 2 + itw * (qi + 0.5) / nSt, d / 2 - 0.2, 0.62);
          } else if (/^bath/.test(k)) {
            part(w, h, d, 0, 0, 0, mWhite);
            part(w - 0.16, 0.02, d - 0.16, 0, 0, h - 0.02, mGlass);   /* the water line, the tub reads hollow */
            part(0.06, 0.15, 0.06, -w / 2 + 0.15, 0, h, mSteel, cyl(0.02, 0.15, 12));
          } else if (/^shower/.test(k)) {
            part(w, 0.04, d, 0, 0, 0, mWhite);
            part(w, h, 0.01, 0, d / 2 - 0.005, 0.04, mGlass);           /* glass front */
            part(0.01, h, d, w / 2 - 0.005, 0, 0.04, mGlass);           /* glass side */
            part(0.04, 1.1, 0.04, -w / 2 + 0.05, -d / 2 + 0.05, 1.0, mSteel, cyl(0.015, 1.1, 12));
            part(0.2, 0.02, 0.2, -w / 2 + 0.2, -d / 2 + 0.2, 2.05, mSteel, cyl(0.1, 0.02, 20));
          } else if (/^basin/.test(k)) {
            part(w, 0.7, d, 0, 0, 0, mWood);
            part(w, 0.02, d, 0, 0, 0.7, mTop);
            part(w * 0.7, 0.13, d * 0.75, 0, 0, 0.72, mWhite);
            part(0.04, 0.2, 0.04, 0, -d / 2 + 0.06, 0.85, mSteel, cyl(0.015, 0.2, 12));
          } else if (k === 'wc') {
            part(0.18, 0.4, 0.18, 0, d / 2 - 0.2, 0, mWhite, cyl(0.16, 0.4, 20));
            part(0.36, 0.05, 0.5, 0, d / 2 - 0.25, 0.4, mWhite);
            part(w, 0.35, 0.16, 0, -d / 2 + 0.08, 0.4, mWhite);
          } else if (k === 'bedDouble' || k === 'bedSingle') {
            bed(part, w, d, false);
          } else if (k === 'bunk') {
            bed(part, w, d, true);
            part(w - 0.08, 0.2, d - 0.08, 0, 0.02, 1.35, mLinen);
            part(w, 0.2, 0.06, 0, -d / 2 + 0.03, 1.15, mWood);
            var qb; for (qb = 0; qb < 4; qb++) part(0.06, 1.55, 0.06, (qb % 2 ? w / 2 - 0.03 : -w / 2 + 0.03), (qb > 1 ? d / 2 - 0.03 : -d / 2 + 0.03), 0, mWood);
          } else if (k === 'bedside') {
            part(w, 0.5, d, 0, 0, 0, mWood); part(0.2, 0.3, 0.2, 0, 0, 0.5, mWhite, cyl(0.1, 0.3, 16));
          } else if (k === 'wardrobe') {
            part(w, h, d, 0, 0, 0, mWood);
            part(0.02, h - 0.1, 0.02, 0, d / 2 + 0.005, 0.05, mDark);
            part(0.02, 0.3, 0.02, -0.06, d / 2 + 0.02, h / 2 - 0.15, mSteel); part(0.02, 0.3, 0.02, 0.06, d / 2 + 0.02, h / 2 - 0.15, mSteel);
          } else if (k === 'desk') {
            part(w, 0.03, d, 0, 0, 0.72, mWood);
            var qd; for (qd = 0; qd < 4; qd++) part(0.04, 0.72, 0.04, (qd % 2 ? w / 2 - 0.05 : -w / 2 + 0.05), (qd > 1 ? d / 2 - 0.05 : -d / 2 + 0.05), 0, mSteel);
            part(0.5, 0.35, 0.02, 0.15, -d / 2 + 0.15, 0.78, mDark);
            chair(part, 0, d / 2 + 0.3, 1);
          } else if (/^sofa[234]$/.test(k)) {
            sofaRun(part, w, d, h);
          } else if (k === 'sofaL' || k === 'sofaU') {
            var seatD = 0.9, seat = 0.42, back = 0.22;
            sofaRun(part, w, seatD, h);
            /* the legs: seat, back on the outer side, and the same cushions at the
               same height, divided by the same joints (Marek) */
            function legRun(side) {
              var lx = side * (w / 2 - seatD / 2), ld = d - seatD;
              part(seatD, seat, ld, lx, seatD / 2, 0, mFab);
              part(back, h - seat, ld, side * (w / 2 - back / 2), seatD / 2, seat, mFabD);
              part(0.06, 0.06, ld, side * (w / 2 - back / 2), seatD / 2, h - 0.03, mFabD, cyl(0.055, ld, 12).rotateX(Math.PI / 2));
              var nC = Math.max(1, Math.round(ld / 0.7)), cw = ld / nC, q;
              for (q = 0; q < nC; q++) {
                var lz = seatD / 2 - ld / 2 + cw * (q + 0.5);
                part(seatD - back - 0.06, 0.08, cw - 0.03, lx - side * back / 2, lz, seat, mFab);
                part(seatD - back - 0.09, 0.05, cw - 0.06, lx - side * back / 2, lz, seat + 0.08, mFab);
              }
            }
            legRun(-1);
            if (k === 'sofaU') legRun(1);
          } else if (k === 'armchair') {
            sofaRun(part, w, d, h);
          } else if (k === 'coffee') {
            part(w, 0.03, d, 0, 0, 0.42, mWood);
            var qc; for (qc = 0; qc < 4; qc++) part(0.04, 0.42, 0.04, (qc % 2 ? w / 2 - 0.06 : -w / 2 + 0.06), (qc > 1 ? d / 2 - 0.06 : -d / 2 + 0.06), 0, mDark);
          } else if (k === 'tvStand') {
            part(w, 0.5, d, 0, 0, 0, mWood);
            part(1.2, 0.7, 0.03, 0, -d / 2 + 0.05, 0.55, mDark);
          } else if (k === 'table' || k === 'table6' || k === 'table8') {
            /* the set's footprint is w x d; the top is tw x td in its middle and
               the chairs sit inside the footprint on both long sides -- two a
               side, three on the table of six and of eight, and one at each end
               of the eight (adu-v1416, Marek: jidelni stul pro 6+2 osoby) */
            var tw = D.tw || w, td = D.td || d, cz = td / 2 + 0.24, nSd = D.side || 2;
            part(tw, 0.03, td, 0, 0, 0.72, mWood);
            var qt; for (qt = 0; qt < 4; qt++) part(0.05, 0.72, 0.05, (qt % 2 ? tw / 2 - 0.08 : -tw / 2 + 0.08), (qt > 1 ? td / 2 - 0.08 : -td / 2 + 0.08), 0, mWood);
            for (qt = 0; qt < nSd; qt++) {
              var cxT = -tw / 2 + tw * (qt + 0.5) / nSd;
              chair(part, cxT, -cz, -1); chair(part, cxT, cz, 1);
            }
            if (D.ends) { chairEnd(part, -tw / 2 - 0.24, 0, -1); chairEnd(part, tw / 2 + 0.24, 0, 1); }
          } else if (k === 'table2' || k === 'table2r') {
            var tw2 = D.tw || w, td2 = D.td || d, cz2 = td2 / 2 + 0.24;
            if (D.round) { part(tw2, 0.03, td2, 0, 0, 0.72, mWood, cyl(tw2 / 2, 0.03, 32)); part(0.08, 0.72, 0.08, 0, 0, 0, mDark, cyl(0.04, 0.72, 16)); part(0.5, 0.02, 0.5, 0, 0, 0, mDark, cyl(0.25, 0.02, 24)); }
            else { part(tw2, 0.03, td2, 0, 0, 0.72, mWood); var q2; for (q2 = 0; q2 < 4; q2++) part(0.05, 0.72, 0.05, (q2 % 2 ? tw2 / 2 - 0.07 : -tw2 / 2 + 0.07), (q2 > 1 ? td2 / 2 - 0.07 : -td2 / 2 + 0.07), 0, mWood); }
            chair(part, 0, -cz2, -1); chair(part, 0, cz2, 1);
          } else if (k === 'barTable') {
            /* the top at the back of the footprint, the two stools in front of
               it inside the footprint (w x d is the whole set) */
            var bw = D.tw || w, bd = D.td || 0.6, bz = -d / 2 + bd / 2, sz3 = d / 2 - 0.2;
            part(bw, 0.04, bd, 0, bz, 1.01, mTop);
            part(bw - 0.1, 0.97, bd - 0.1, 0, bz, 0.04, mWood);
            var qs; for (qs = 0; qs < 2; qs++) {
              var sx2 = -bw / 4 + (qs ? bw / 2 : 0);
              part(0.36, 0.05, 0.36, sx2, sz3, 0.72, mDark, cyl(0.18, 0.05, 20));
              part(0.05, 0.72, 0.05, sx2, sz3, 0, mSteel, cyl(0.025, 0.72, 12));
              part(0.4, 0.02, 0.4, sx2, sz3, 0, mSteel, cyl(0.2, 0.02, 20));
            }
          } else if (k === 'fridge') {
            part(w, h, d, 0, 0, 0, mWhite);
            part(w, 0.02, d, 0, 0, h * 0.7, mSteel);
            part(0.03, 0.4, 0.02, w / 2 - 0.08, d / 2 + 0.01, h * 0.75, mSteel);
          } else if (k === 'washer') {
            part(w, h, d, 0, 0, 0, mWhite);
            part(0.44, 0.44, 0.02, 0, d / 2 + 0.01, 0.2, mDark, cyl(0.22, 0.02, 24).rotateX(Math.PI / 2));
            part(0.16, 0.16, 0.02, 0, d / 2 + 0.02, 0.34, mGlass, cyl(0.08, 0.02, 20).rotateX(Math.PI / 2));
            /* A DRYER ON TOP (adu-v1416, floorplan v150 stacks them where the
               laundry has no room for the two side by side) */
            if (f.stack === 'dryer') {
              cDry = g.children.length;
              part(w, 0.02, d, 0, 0, h, mSteel);
              part(w, h, d, 0, 0, h + 0.02, mWhite);
              part(0.4, 0.4, 0.02, 0, d / 2 + 0.01, h + 0.24, mSteel, cyl(0.2, 0.02, 24).rotateX(Math.PI / 2));
              /* adu-v1417: billed on the dryer's own row (FKI00012) */
              var Ddry = cat.dryer;
              if (Ddry && Ddry.code) { _add(LAYER, Ddry.code, 1); tally[Ddry.code] = (tally[Ddry.code] || 0) + 1; stackedBilled++; }
              else noRow['dryer (stacked on the washer)'] = (noRow['dryer (stacked on the washer)'] || 0) + 1;
              /* garageapt-v32: the plate between them is the maker's stacking kit */
              if (GA) { _add(LAYER, GA_FIT.stack, 1); STACKS++; }
            }
          } else if (k === 'dryer') {
            /* the dryer: the washer's box, a plain round door, the lint filter
               in the top front */
            part(w, h, d, 0, 0, 0, mWhite);
            part(0.4, 0.4, 0.02, 0, d / 2 + 0.01, 0.24, mSteel, cyl(0.2, 0.02, 24).rotateX(Math.PI / 2));
            part(0.3, 0.02, 0.06, 0, d / 2 - 0.05, h, mDark);
          } else {
            part(w, h, d, 0, 0, 0, mFab);
          }
          if (D.code) { _add(LAYER, D.code, 1); tally[D.code] = (tally[D.code] || 0) + 1; }
          /* RANCH (ranch-v83, core-v687): THE PIECE SAYS WHAT IT IS BILLED AS --
             the fitout stays one step of the walk (one mark, Marek), so every
             body of the piece carries its code (qtyCode) and the piece
             (qtyPiece), the first what comes with it (qtyWith: a faucet, a
             mattress, the chairs); a dryer stacked on the washer is its own piece */
          if (H.qtyMarks) (function () {
            var qe, cEnd = g.children.length, cA = (cDry >= 0) ? cDry : cEnd;
            for (qe = cP0; qe < cA; qe++) { var ub = g.children[qe].userData; if (D.code) ub.qtyCode = D.code; ub.qtyPiece = 'fit' + i; }
            if (cDry >= 0 && cat.dryer && cat.dryer.code) for (qe = cDry; qe < cEnd; qe++) { var ud = g.children[qe].userData; ud.qtyCode = cat.dryer.code; ud.qtyPiece = 'fit' + i + 'd'; }
            if (D.extra && D.extra.length && cP0 < cEnd) g.children[cP0].userData.qtyWith = D.extra.slice();
          })();
          /* what fixes it: four screws for every piece that stands on a wall, a
             tube of silicone for every wet piece (bath, shower, basin, wc, sink) */
          /* adu-v1417: an appliance (fridge, washer, dryer) stands free on its
             feet -- nothing screws it to the wall and nothing seals it */
          /* garageapt-v32: a kitchen run by the box (kitBill), a vanity by its
             back rail -- two screws, and the vanity itself */
          if (GA && /^basin/.test(k) && D.code) { _add(LAYER, GA_FIT.wallScrew, 2); _add(LAYER, GA_FIT.vanity, 1); VANS++; }
          else if (D.code && D.wall && !D.appliance && !(GA && /^kitchen(24|30|36|L|U)$/.test(k))) _add(LAYER, 'FAS60080', 4);
          if (D.code && !D.appliance && (D.grp === 'bath' || D.grp === 'wet' || D.grp === 'kitchen' || /shower|bath|basin|wc|sink|toilet/i.test(String(D.def || '')))) _add(LAYER, 'FEC00040', 1);
          /* adu-v1416: what has no catalogue row is counted and named once,
             below -- the line here hung off the silicone test and named every
             dry piece that IS billed (a bed, a sofa, a table) as not billed */
          if (!D.code) noRow[k] = (noRow[k] || 0) + 1;
          if (D.extra) { var eq; for (eq = 0; eq < D.extra.length; eq++) { _add(LAYER, D.extra[eq], 1); } }
          n++;
        }
        } finally { if (GA) { API.STATE.planStorey = gaPs; GA.roomSt(gaRs); } }
        var codes = [], kk;
        for (kk in tally) codes.push(kk + ' x' + tally[kk]);
        ctx.log('FIT  ' + n + ' piece(s): ' + codes.join(', ') + ' -- modelled in parts on the floor finish, billed with their companion rows');
        /* GARAGE APT / ANY HOUSE: the fitouts by US practice, the appliances,
           the storeys */
        if (GA) {
          var bkN = {}, bkL = 0, bq;
          for (bq = 0; bq < GA.backList().length; bq++) { bkN[GA.backList()[bq].kind] = (bkN[GA.backList()[bq].kind] || 0) + 1; bkL += GA.backList()[bq].b - GA.backList()[bq].a; }
          if (nOff) ctx.log('FIT  17d ' + nOff + ' piece(s) with the back on an outer wall built off the board\'s face (the lining, ' + Math.round((GA.VB_T + GA.WALL_BATTEN.d + GA.WALL_BOARD.t) * 1000) + ' on a heated wall), not the frame\'s (garageapt-v32)');
          if (KITS.length || VANS || STACKS) {
            ctx.log('FIT  17d the fitouts by US practice (garageapt-v32): ' + (KITS.length ? KITS.join('; ') : 'no kitchen run') +
                    (KITS.length ? (', ' + KITS.length + ' x ' + GA_FIT.faucet + ' (PENDING), the countertop ' + GA_FIT.top + ' (PENDING)') : '') +
                    (VANS ? ('; ' + VANS + ' basin(s) on a vanity ' + GA_FIT.vanity + ' (PENDING), 2 x ' + GA_FIT.wallScrew + ' each') : '') +
                    (STACKS ? ('; ' + STACKS + ' dryer(s) on the washer with the stacking kit ' + GA_FIT.stack + ' (PENDING)') : '') +
                    '; the backing in the service layer: ' + (GA.backList().length ? (GA.backList().length + ' piece(s), ' + bkL.toFixed(2) + ' m (' + Object.keys(bkN).map(function (kb) { return kb + ' ' + bkN[kb]; }).join(', ') + ') -- WAL  backing') : 'none') +
                    (GA.backSkip() ? (', ' + GA.backSkip() + ' rail(s) on a course already') : '') +
                    '; the range\'s anti-tip bracket comes in its box (UL 858), the dishwasher\'s brackets with it');
          }
          /* garageapt-v18 (unit 10, COPIED from barndo-v28 / v29): the kitchen
             run's appliances are its own rows in the shared fixture table
             (floorplan-lib-v206) -- the range FKI00013, the dishwasher FKI00014
             with its installation kit FKI00015, the hood FKI00016 (its duct is
             the ventilation's, mep-v39 fxUp); nothing smaller is billed (Marek's
             line, barndo-v28: no bathroom accessories, mirrors or curtain rods) */
          (function () {
            var FXa = ((API.STATE.product || {}).fixtures) || [], na = { kit: 0, wc: 0, basin: 0, tub: 0 }, ia;
            for (ia = 0; ia < FXa.length; ia++) {
              var kd = String((FXa[ia] && FXa[ia].kind) || '');
              if (/^kitchen(24|30|36|L|U)$/.test(kd)) na.kit++; else if (kd === 'wc') na.wc++; else if (/^basin/.test(kd)) na.basin++; else if (/^bath(15|18)$/.test(kd)) na.tub++;
            }
            ctx.log('FIT  appliances (garageapt-v18, as barndo-v29): ' + na.kit + ' kitchen run(s) -> FKI00013 range, FKI00014 dishwasher + FKI00015 its kit, FKI00016 hood with each run (the fixture table); ' +
                    'nothing smaller billed (Marek\'s line, barndo-v28): no bathroom accessories, mirrors or curtain rods (' + na.wc + ' toilet(s), ' + na.basin + ' basin(s), ' + na.tub + ' tub(s))');
          })();
          if (GA.planNote()) ctx.log(GA.planSaid());
          /* ANY HOUSE: its own notes (the ground, one storey, the module) */
          if (H.endSay) H.endSay(ctx);
          ctx.log('GA fitout 6d ' + gaOn[0] + ' piece(s) in the garage on the slab' +
                  (API.STATE.foundation === 'slab' ? ' (bare, nothing laid on it)' : '') +
                  (gaNS > 1 ? (', ' + gaOn[1] + ' in the apartment on its floor finish at +' +
                               Math.round((gaYF[1] - API.storeys()[0].y0) * 1000) + ' over the slab') : '') +
                  (gaOff ? (' -- ' + gaOff + ' piece(s) of storey 2 left out: the house has one storey now (kept in the plan)') : ''));
        }
        var nr = [], kr;
        for (kr in noRow) if (noRow.hasOwnProperty(kr)) nr.push(kr + ' x' + noRow[kr]);
        if (nr.length) ctx.log('FIT  NOT BILLED, no catalogue row yet: ' + nr.join(', ') + ' -- drawn, and nothing billed into another row in their place');
        if (nUpF) ctx.log('PF fit   7e1 ' + nUpF + ' piece(s) placed on the loft\'s plan, built on the loft floor at +' + LOFT.mm(GF.yLF - GF.F));
        if (sizedUS || nCompact || stackedBilled) ctx.log('FIT  appliances (adu-v1417): ' + sizedUS + ' at the US stock size (27 in washer and dryer, 36 in fridge)' +
                                                         (nCompact ? (', ' + nCompact + ' compact 24 in where the full size does not fit') : '') +
                                                         (stackedBilled ? (', ' + stackedBilled + ' stacked dryer(s) billed as FKI00012') : '') +
                                                         ' -- standing free: no screws, no silicone');
        /* BARNDO: the loft's pieces on the arrows with the loft's rooms (7d1) */
        if (GF) {
          for (var qU = 0; qU < g.children.length; qU++) {
            var cU = g.children[qU];
            if (cU && cU.name === 'fitout' && cU.position.y > GF.yS1) cU.userData.pfStep = '7d1';
          }
        }
    }

    /* =========================================================================
       THE SMALL SHARED HELPERS (core-v807, status/modules.md 11). Ten
       functions every house-line product carried word for word: the truss
       web's plane, a rafter's foot on the chord, a jack seated on its hip, a
       flat roof's rafters from the studs, the roof's height over a point (two
       ways) and a band under it, the membrane texture, the rooms on a grid and
       the slanted partitions. The text is the ADU's. What a product's copy
       read from its own scope comes in as the last argument -- its CHORD, STUD,
       RAFTER and partition sizes, the studs its walls reported, its texture
       cache, its rooms -- so each product's copy is a one-line call and its
       numbers stay its own. The roof's planes (roofPlanes) and the stud
       module (GRID) are this core's own; the products took them from here.
       ========================================================================= */
    /* tight: THE HEAD GOES INTO THE CORNER ITSELF.
       A sloping web is normally laid down until it beds by the same length on
       the chord above and on the upright's cheek beside it. On the centre
       truss of a hip end that leaves a wedge of daylight between the web, the
       upright and the chord -- the web meets the chord a hand's width past the
       upright instead of in the corner between them, and on the sheet it reads
       as a web that stops short of the joint (Marek: nejsou natazene sikme
       vzpery az ke spoji vrchni pasnice a svislych vyztuh). With tight the
       head's axis is aimed AT that corner, so the web fills it; it is cut by
       both faces as before. */
    function houseWebPlane(dF, dH, tanS, yTop0, tieTop, hu, hd, ckHAbs, drop, tight) {
      var sgn = (dF >= dH) ? 1 : -1;
      var ckF = dF - sgn * hu;                    /* its own upright's cheek */
      /* AND WHAT IT DIES ON AT THE TOP IS NOT ALWAYS A WEB.

         The web nearest the peak runs back to the POST, which is wider than a
         web and does not straddle its node -- it stands with one face on the
         doubled truss. Taking half a web's depth off the node put the head 60 mm
         inside it. The caller passes the real face when it is not a web (Marek:
         horni cast modre musi byt orezana svetle modrym svislym profilem). */
      var ckH = (ckHAbs != null) ? ckHAbs : (dH + sgn * hu);
      function under(d) { return yTop0 - d * tanS; }
      var F = { d: dF, y: tieTop - drop };
      var aimY = under(ckH), it, ux, uy, L2, sA, sB, qs, qx, qy, tt;
      var nrm = Math.sqrt(1 + tanS * tanS);
      for (it = 0; tight ? false : (it < 8); it++) {
        ux = ckH - F.d; uy = aimY - F.y;
        L2 = Math.sqrt(ux * ux + uy * uy);
        if (!(L2 > 0.05)) break;
        ux /= L2; uy /= L2;
        sA = Math.abs(ux * (-tanS) - uy) / nrm;   /* against the chord */
        sB = Math.abs(ux);                        /* against a plumb cheek */
        if (!(sA > 0.02) || !(sB > 0.02)) break;
        /* which side of the member the chord lies on flips with the way it
           leans, and the offset has to flip with it */
        qs = ((ux >= 0) ? 1 : -1) * hd * (sB - sA) / (sA + sB);
        /* the axis has to pass this far from the corner of the two faces */
        qx = ckH - qs * (-uy);
        qy = under(ckH) - qs * ux;
        ux = qx - F.d; uy = qy - F.y;
        L2 = Math.sqrt(ux * ux + uy * uy);
        if (!(L2 > 0.05) || Math.abs(ux) < 1e-6) break;
        tt = (ckH - F.d) / (ux / L2);
        aimY = F.y + (uy / L2) * tt;
        if (aimY > under(ckH) - 0.002) aimY = under(ckH) - 0.002;
        if (aimY < F.y + 0.05) aimY = F.y + 0.05;
      }
      var H = { d: ckH, y: aimY };
      var dxA = H.d - F.d, dyA = H.y - F.y, LA = Math.sqrt(dxA * dxA + dyA * dyA);
      if (!(LA > 0.05)) return null;
      var uxA = dxA / LA, uyA = dyA / LA;
      var cpd = (F.d + H.d) / 2, cpy = (F.y + H.y) / 2;
      function meetCheek(cd, q) {                 /* a plumb face at d = cd */
        if (Math.abs(uxA) < 1e-6) return null;
        return (cd - cpd + q * uyA) / uxA;
      }
      function meetLine(aa, cc, q) {              /* y = aa*d + cc */
        var den = uyA - aa * uxA;
        if (Math.abs(den) < 1e-9) return null;
        return (aa * cpd + cc - cpy - q * (uxA + aa * uyA)) / den;
      }
      function ends(isHead) {
        var cd = isHead ? ckH : ckF;
        var aa = isHead ? -tanS : 0, cc = isHead ? yTop0 : tieTop;
        var out = [], k, q, x1, x2, xv;
        for (k = 1; k >= -1; k--) {
          q = k * hd;
          x1 = meetCheek(cd, q);
          x2 = meetLine(aa, cc, q);
          if (x1 == null) x1 = x2;
          if (x2 == null) x2 = x1;
          if (x1 == null) return null;
          /* x runs from the foot to the head whichever way the member leans,
             so the head stops at the FIRST face going forward and the foot at
             the first one coming back -- the lean does not enter it */
          xv = isHead ? Math.min(x1, x2) : Math.max(x1, x2);
          out.push(xv);
        }
        /* the corner of the two, put where they really cross */
        var pcD = cd, pcY = aa * cd + cc;
        var qStar = (pcD - cpd) * (-uyA) + (pcY - cpy) * uxA;
        var xStar = (pcD - cpd) * uxA + (pcY - cpy) * uyA;
        if (Math.abs(qStar) < hd - 0.001) { out[1] = xStar; }
        else qStar = 0;
        return { off: out, q: qStar };
      }
      var eH = ends(true), eF = ends(false);
      if (!eH || !eF) return null;
      /* WHICH WAY ROUND THE DEPTH GOES.

         In the flat picture the offsets are measured off the member's LEFT
         normal, and for a web running back toward the peak that normal points
         DOWNWARD -- while in the model the same box has its own +Y pointing up.
         Handing the rows over in picture order therefore put the bottom edge's
         cut on the top edge: one corner hanging in the air and the other buried
         in the upright, which is exactly what it looked like. The flag says
         whether the two agree, and the caller turns the rows over when they do
         not (Marek: bile maji spodni konce ve vzduchu a koliduji s violet). */
      /* how much of the foot ends up lying on the chord, which is the number
         the drop above is chosen by */
      var bear8 = 0;
      (function () {
        var k8, P8 = [];
        var qs8 = [hd, eF.q, -hd];
        for (k8 = 0; k8 < 3; k8++) {
          P8.push([cpd + eF.off[k8] * uxA - qs8[k8] * uyA,
                   cpy + eF.off[k8] * uyA + qs8[k8] * uxA]);
        }
        var onT = (Math.abs(P8[0][1] - tieTop) < 0.0005) ? 0 : 2;
        bear8 = Math.sqrt((P8[onT][0] - P8[1][0]) * (P8[onT][0] - P8[1][0]) +
                          (P8[onT][1] - P8[1][1]) * (P8[onT][1] - P8[1][1]));
      })();
      return { foot: F, head: H, len: LA, half: LA / 2, eF: eF, eH: eH,
               ux: uxA, uy: uyA, flip: (uxA < 0), bear: bear8, drop: drop };
    }

    function houseFootOnChord(mesh, floorB, cyB, hLen, hDep, _footFlat) {
      var th = mesh.rotation.x, ct = Math.cos(th), st = Math.sin(th);
      if (!(Math.abs(st) > 1e-4) || !(ct > 1e-4)) return;
      var pos = mesh.geometry.attributes.position, i;
      var cBrk = (cyB - hDep * ct - floorB) / st;
      var brkIn = (cBrk > -hLen + 0.005 && cBrk < hLen - 0.005);
      if (brkIn) {
        for (i = 0; i < pos.count; i++) {
          if (Math.abs(pos.getZ(i)) > 1e-6) continue;
          pos.setZ(i, cBrk);
        }
      }
      var low = (st > 0) ? hLen : -hLen;
      /* THE END IS CUT BY TWO PLANES: LEVEL ON THE CHORD, PLUMB AT ITS END.

         It used to be squashed to a single edge where the top face meets the
         chord, and the plumb squaring further down then pulled that edge back
         -- with the underside corner still riding on it. So the last stretch of
         the underside climbed off the chord's top face, a thin wedge of air
         under every heel, and the drawing showed a rafter not cut level
         (Marek: horni pasnice nejsou orizle vodorovnou plochou spodni
         pasnice). Each end vertex now goes where the two cuts put it: below
         the line where the chord's top face meets the plumb end it sits ON that
         line, above it on the plumb face at its own height. */
      var Hp = low * ct;                 /* the plumb end, along the level from the centre */
      var Dp = floorB - cyB;             /* the chord's top face, from the centre */
      var yB = Dp * ct + Hp * st;        /* the two cuts meet here, in the member's own frame */
      var zB = (Hp - yB * st) / ct;
      var twoCuts = brkIn && (yB > -hDep + 1e-4) && (yB < hDep - 1e-4) &&
                    ((st > 0) ? (zB > cBrk) : (zB < cBrk));
      var endIdx = [];
      if (twoCuts) {
        for (i = 0; i < pos.count; i++) {
          if (Math.abs(pos.getZ(i) - low) > 1e-6) continue;
          var yE = pos.getY(i);
          if (yE <= yB) { pos.setY(i, yB); pos.setZ(i, zB); endIdx.push(i); }
          else pos.setZ(i, (Hp - yE * st) / ct);
        }
      } else {
        var cTop = (cyB + hDep * ct - floorB) / st;
        if (cTop > hLen) cTop = hLen;
        if (cTop < -hLen) cTop = -hLen;
        if ((st > 0 && cTop < hLen - 0.002) || (st < 0 && cTop > -hLen + 0.002)) {
          for (i = 0; i < pos.count; i++) {
            if (Math.abs(pos.getZ(i) - low) > 1e-6) continue;
            pos.setZ(i, cTop);
          }
        }
      }
      for (i = 0; i < pos.count; i++) {
        var ly = pos.getY(i), lz = pos.getZ(i);
        var want = (floorB - cyB + lz * st) / ct;
        if (want > hDep) want = hDep;
        if (want > ly) pos.setY(i, want);
      }
      /* AND NOTHING REACHES PAST THE BOTTOM CHORD'S OWN END.

         The chord below stops with a plumb face; the rafter above it is cut
         square to ITS length, which on a sloping member means the top corner
         pokes out beyond that face -- the little spikes hanging in the air off
         the eave. The end is squared to the plumb instead: every point pulled
         back onto the same vertical plane the chord ends on (Marek: podle cela
         acid green, drobne spicky ktere trci ven). */
      var tn = st / ct;
      for (i = 0; i < pos.count; i++) {
        var y2 = pos.getY(i), z2 = pos.getZ(i);
        if (st > 0) { var b1 = hLen - y2 * tn; if (z2 > b1) pos.setZ(i, b1); }
        else { var b2 = -hLen - y2 * tn; if (z2 < b2) pos.setZ(i, b2); }
      }
      /* AND IT SAYS HOW FLAT THE CUT CAME OUT.

         The planed stretch of a rafter's underside IS the chord's top face --
         level, and at one height. Measured on the finished body: every vertex
         below the axis that ends up past the break is compared with that face,
         and the worst is kept. A gap opening at the end of a truss shows up
         here as a number, which is the difference between knowing and guessing
         (Marek: na konci vazniku se rozevira skvira). */
      try {
        var wF = 0, nF = 0;
        for (i = 0; i < pos.count; i++) {
          var yF = pos.getY(i), zF = pos.getZ(i);
          if (yF >= hDep - 1e-6) continue;
          var hW = cyB + (yF * ct - zF * st);
          if (hW > floorB + 0.004) continue;      /* still above the chord: not planed */
          nF++;
          var dF = Math.abs(hW - floorB);
          if (dF > wF) wF = dF;
        }
        if (nF) {
          _footFlat.n++;
          if (wF > _footFlat.worst) _footFlat.worst = wF;
        }
        /* and the corner where the level cut meets the plumb one: it must sit on
           the chord's top face too -- this is the point that used to ride up */
        var eq;
        for (eq = 0; eq < endIdx.length; eq++) {
          var hE = cyB + (pos.getY(endIdx[eq]) * ct - pos.getZ(endIdx[eq]) * st) - floorB;
          if (Math.abs(hE) > _footFlat.endWorst) _footFlat.endWorst = Math.abs(hE);
        }
        if (endIdx.length) _footFlat.ends++;
        else _footFlat.oneCut++;
      } catch (eFF) {}
      pos.needsUpdate = true;
      mesh.geometry.computeVertexNormals();
    }

    /* SEATING A JACK ON THE HIP IT RUNS INTO.

       This is the cut that was given up on twice: the jack meets the hip at an
       angle in PLAN and at a different angle in SLOPE, so no single saw setting
       describes it and the end was left square, standing off the hip. But the
       face it has to meet is not complicated at all -- the hip's cheek is a
       VERTICAL plane containing the hip's plan line, because the hip is turned
       about its own length and its sides stay plumb. One plane, and every point
       of the jack's head carried along the jack until it lands on it.

       The second plane is the hip's top face, which does slope; any corner of
       the head still above it after the first move is dropped onto it, the same
       way the hip itself was seated against the centre chord.

       mesh is placed and rotated already; ex/ez is the hip's plan direction
       from (hx,hz); side says which cheek; topA and tanH describe its top face
       (Marek: magenta se musi primknout na horni pasnici naroznich vazniku). */
    function houseSeatOnHip(mesh, hx, hz, ex, ez, halfT, topA, tanH, side, headPlus, CHORD) {
      var g = mesh.geometry, pos = g.attributes.position, i;
      var bb = 0, k;
      for (k = 0; k < pos.count; k++) {
        var zz = pos.getZ(k);
        if (Math.abs(zz) > bb) bb = Math.abs(zz);
      }
      var hL = bb, tgt = headPlus ? hL : -hL;
      var cy2 = Math.cos(mesh.rotation.x), sy2 = Math.sin(mesh.rotation.x);
      var cz2 = Math.cos(mesh.rotation.y), sz2 = Math.sin(mesh.rotation.y);
      function wof(x, y, z) {
        var ay = y * cy2 - z * sy2, az = y * sy2 + z * cy2;
        return [x * cz2 + az * sz2, ay, -x * sz2 + az * cz2];
      }
      var sx = ez, sz = -ex;                    /* across the hip, in plan */
      var uZ = wof(0, 0, 1), uY = wof(0, 1, 0);
      var dSdZ = sx * uZ[0] + sz * uZ[2];
      var dFdY = uY[1] - (ex * uY[0] + ez * uY[2]) * tanH;
      var cx3 = mesh.position.x, cy3 = mesh.position.y, cz3 = mesh.position.z;
      for (i = 0; i < pos.count; i++) {
        var lx = pos.getX(i), ly = pos.getY(i), lz = pos.getZ(i);
        if (Math.abs(lz - tgt) > 1e-6) continue;
        var pass, w, P, sNow, tAlong, f2;
        for (pass = 0; pass < 3; pass++) {
          w = wof(lx, ly, lz);
          P = [cx3 + w[0], cy3 + w[1], cz3 + w[2]];
          sNow = sx * (P[0] - hx) + sz * (P[2] - hz);
          if (Math.abs(dSdZ) > 1e-6) {
            var want = side * halfT;
            var dz = (want - sNow) / dSdZ;
            /* BOTH WAYS, BECAUSE THIS IS A PROJECTION, NOT A CLAMP.

               It was only allowed to move the head FORWARD onto the cheek. But
               dropping a corner onto the hip's top face slides it along the jack
               as well, and a corner that ends up standing PROUD of the cheek
               then had no way back -- so the joint closed on the near edge and
               opened a little at the far one. Every point of the head belongs on
               that plane whichever side of it the last move left it (Marek: u
               vzdalenejsiho konce se magenta mirne rozvira). */
            if (Math.abs(dz) < hL * 0.9) {
              lz += dz;
              w = wof(lx, ly, lz);
              P = [cx3 + w[0], cy3 + w[1], cz3 + w[2]];
            }
          }
          if (tanH != null && topA != null) {
            tAlong = ex * (P[0] - hx) + ez * (P[2] - hz);
            f2 = P[1] - (topA - tAlong * tanH);
            if (f2 > 0.0002 && Math.abs(dFdY) > 1e-6) {
              var dy = -f2 / dFdY;
              if (Math.abs(ly + dy) <= CHORD.d) ly += dy;
            } else if (pass > 0) break;
          } else if (pass > 0) break;
        }
        pos.setZ(i, lz);
        pos.setY(i, ly);
      }
      pos.needsUpdate = true;
      g.computeVertexNormals();
    }

    /* Turn the recorded stud positions into rafter positions along the low edge:
       each one shifted just clear of its stud, and any that would then land on
       ANOTHER stud dropped -- which is what happens at the ends, where two studs
       stand 51 mm apart and only one of them can have a rafter beside it. */
    function houseRafterSteps(lo, runLen, H) {
      /* what the product's copy read from its own scope */
      var API = H.api, STUD = H.STUD, RAFTER = H.RAFTER, _bearStuds = H.bearStuds || [];
      var ax = lo.b[0] - lo.a[0], az = lo.b[2] - lo.a[2];
      var L = Math.hypot(ax, az) || 1;
      var ux = ax / L, uz = az / L;
      function along(p) { return (p.x - lo.a[0]) * ux + (p.z - lo.a[2]) * uz; }

      /* ONE WALL SETS THE RAFTERS OUT. */
      var us = [], i;
      var offMax = STUD.d + 0.05;
      for (i = 0; i < _bearStuds.length; i++) {
        var P = _bearStuds[i];
        var u = along(P);
        if (u < -0.01 || u > L + 0.01) continue;
        /* how far off the line it sits, across the run */
        var offx = (P.x - lo.a[0]) - ux * u, offz = (P.z - lo.a[2]) - uz * u;
        if (Math.hypot(offx, offz) > offMax) continue;
        us.push(u);
      }
      us.sort(function (a, b) { return a - b; });

      /* ONE RAFTER PER LINE OF TIMBER, NOT ONE PER STICK.

         This is the extra profile. The list is every stud the wall built, and the
         wall builds them in PAIRS wherever two things meet: a panel joint is two
         end studs 60 mm apart, a king stands right beside its jack, and both
         walls report into the same list so their studs land on the same
         positions. Each of those was getting its own rafter, so the run came out
         with twins squeezed together at every joint and beside every opening.

         Two studs touching are one line of timber to fix to. Collapse them and
         set out from what is left. */
      /* AND A COLLAPSED PAIR SITS AT ITS CENTRE, NOT ON ITS FIRST STICK.

         Collapsing was right -- two studs touching are one line of timber. Which
         one to keep was not thought about, and keeping the first throws the
         rafter to one side of a 90 mm double:

           studs   3578 3623        the panel joint
           kept    3578             spacing 507 then 654, either side of it
           centre  3600             spacing 529 then 632

         That lopsided pair is the rafter he reads as missing: the run has a wide
         gap on one side of every joint and a tight one on the other, and the eye
         finds the wide one first.

         The centre also puts the rafter's face on the middle of the doubled stud,
         which is what it is fixed to. Flat roofs only -- this is the only caller,
         and the parapet is the only roof that sets its rafters out from studs. */
      /* THE CENTRE INSIDE THE RUN, THE OUTERMOST STICK AT ITS ENDS. */
      var lines = [], li, group = [];
      function flush(first, last) {
        if (!group.length) return;
        if (first) { lines.push(group[0]); group = []; return; }
        if (last) { lines.push(group[group.length - 1]); group = []; return; }
        var sum = 0, gi;
        for (gi = 0; gi < group.length; gi++) sum += group[gi];
        lines.push(sum / group.length);
        group = [];
      }
      for (li = 0; li < us.length; li++) {
        if (group.length && us[li] - group[group.length - 1] >= STUD.t * 1.8) {
          flush(lines.length === 0, false);
        }
        group.push(us[li]);
      }
      flush(lines.length === 0, true);
      us = lines;

      var gap = (STUD.t + RAFTER.t) / 2;
      /* AND ONE AT EACH END OF THE RUN, hard against the inside face of the end
         wall. Without it the last bay of deck has nothing to land on at that edge
         -- the sheathing would be cantilevering off the second-to-last rafter. */
      /* AND NOT INSIDE THE END WALLS. The rafters run parallel to the two walls
         at the ends of this line, so a rafter placed in the last 140 mm is lying
         inside that wall's own frame rather than beside a stud. The last one
         stops against its inside face. */
      var endBand = API.wallThickness() + RAFTER.t / 2;
      var out = [], last = -1e9;
      for (i = 0; i < us.length; i++) {
        /* beside the stud, on whichever side has room */
        var cand = (us[i] + gap <= L - endBand) ? (us[i] + gap) : (us[i] - gap);
        if (cand < endBand || cand > L - endBand) continue;
        var clear = true, j;
        for (j = 0; j < us.length; j++) {
          if (Math.abs(cand - us[j]) < (STUD.t + RAFTER.t) / 2 - 0.001) { clear = false; break; }
        }
        if (!clear) continue;
        /* NOTHING CLOSER THAN HALF A BAY TO THE LAST ONE.

           This is the extra profile, and the test that let it through was mine:
           RAFTER.t is 45 mm, so anything more than a rafter's own width apart
           counted as a separate bay. The kings either side of an opening do not
           sit on the module -- they flank the hole, wherever the hole is -- so
           each one minted a rafter 200 mm from its neighbour and the run came out
           with pairs squeezed together beside every window.

           A king is a stud and a rafter may be fixed to it, but only if there is
           a bay there to fill. Half a bay is the line: past it the roof is doing
           nothing the neighbouring rafter is not already doing, and the deck sees
           no difference. */
        if (cand - last < GRID() * 0.5) continue;
        last = cand;
        out.push(cand / runLen);
      }

      /* TWO AT THE ENDS, AND ONLY TWO.

         They go on the outside of what is already there rather than into it: an
         end rafter is there to give the deck an edge at the end wall, not to
         change the spacing of the run. Adding them into the sorted list and then
         thinning by proximity let them push their neighbours out, so a request
         for two ended up rearranging the middle as well. */
      out.sort(function (a, b) { return a - b; });
      var first = out.length ? out[0] * runLen : L / 2;
      var last  = out.length ? out[out.length - 1] * runLen : L / 2;
      if (first - endBand > RAFTER.t) out.unshift(endBand / runLen);
      if ((L - endBand) - last > RAFTER.t) out.push((L - endBand) / runLen);
      return out;
    }

    /* WHERE THE ROOF'S UNDERSIDE IS AT A GIVEN POINT -- ASKED, NOT DERIVED.

       The header height was worked out from the pitch and the sign of the side:
       plateTop +/- reach*tan(pitch) - rafter. That is a guess about the shape,
       and on a mono-pitch it is the wrong guess -- the plane does not fall away
       from the plate in both directions, it runs UP from a low edge to a high
       one. So one side of the building had the beam riding above the rafters and
       the other had it buried under them, and the gable, where both sides really
       are the same, got two different answers as well.

       The plane knows its own height. Three of its corners give the plane, and
       the plane gives y anywhere -- inside the building or three metres past it,
       which is exactly where a post stands.

       RAFTER.d below that is the soffit: what a post has to reach to hold the
       roof rather than to hold the air under it. */
    function houseRoofPlaneYAt(px, pz) {
      var planes = [];
      try { planes = roofPlanes() || []; } catch (e) { return null; }
      var best = null, i;
      for (i = 0; i < planes.length; i++) {
        var P = planes[i].pts;
        if (!P || P.length < 3) continue;
        /* THREE CORNERS THAT ARE ACTUALLY THREE.

           This took the first three every time. A hip end is stored as a quad
           with its apex REPEATED -- [eave, apex, apex, eave] -- so corners 1 and
           2 are the same point, the cross product came out zero, and the test
           below discarded the plane.

           Both hip ends were therefore answering nothing at all, and a point over
           one of them got the long slope instead, which is higher there. That is
           why the insulation came through the boards at the hip and never did on
           a gable (Marek). */
        var ax = 0, ay = 0, az = 0, bx = 0, by = 0, bz = 0, nx = 0, ny = 0, nz = 0;
        var c1, c2b, ok = false;
        for (c1 = 1; c1 < P.length && !ok; c1++) {
          for (c2b = c1 + 1; c2b < P.length && !ok; c2b++) {
            ax = P[c1][0] - P[0][0]; ay = P[c1][1] - P[0][1]; az = P[c1][2] - P[0][2];
            bx = P[c2b][0] - P[0][0]; by = P[c2b][1] - P[0][1]; bz = P[c2b][2] - P[0][2];
            nx = ay * bz - az * by; ny = az * bx - ax * bz; nz = ax * by - ay * bx;
            if (Math.abs(ny) > 1e-6) ok = true;
          }
        }
        if (!ok) continue;
        var y = P[0][1] - (nx * (px - P[0][0]) + nz * (pz - P[0][2])) / ny;
        /* THE LOWEST ROOF OVER THE POINT, NOT THE NEAREST PLANE'S CENTRE.

           Picking by distance to a plane's centre works on a gable, where the two
           planes are equal and opposite. On a hip there are four, the trapezoids
           are much bigger than the triangles, and their centres sit closer to the
           middle of the house -- so a point out over a hip END was answered by
           the long slope, which is HIGHER there. The insulation was then cut to
           the wrong roof and came through the boards.

           Every plane is a full plane, so each one answers everywhere; what
           decides is which roof is actually lowest above that spot. That is the
           one the ceiling has to stop under, and on a gable it gives the same
           answer as before (Marek). */
        if (best === null || y < best) best = y;
      }
      return best;
    }

    /* HOW HIGH THE ROOF IS OVER A POINT ON THE GROUND.

       Every plane the roof publishes is a flat polygon, so three of its corners
       give its plane and the rest is arithmetic. The highest plane that covers
       the point wins, which is what a roof does. */
    function houseRoofYAtXZ(x, z) {
      var P = [], i;
      try { P = roofPlanes() || []; } catch (e) { return null; }
      var best = null;
      for (i = 0; i < P.length; i++) {
        var pts = P[i] && P[i].pts;
        if (!pts || pts.length < 3) continue;
        var A = pts[0], B = null, C = null, k;
        for (k = 1; k < pts.length && !C; k++) {
          var Q = pts[k];
          if (!B) { if (Math.hypot(Q[0] - A[0], Q[2] - A[2]) > 0.05) B = Q; continue; }
          var cr = (B[0] - A[0]) * (Q[2] - A[2]) - (B[2] - A[2]) * (Q[0] - A[0]);
          if (Math.abs(cr) > 0.01) C = Q;
        }
        if (!B || !C) continue;

        /* THE POINT HAS TO BE ON THIS PLANE, NOT ON ITS EQUATION.

           A roof plane is a POLYGON. I took its equation and evaluated it
           anywhere, then kept the highest answer -- so the far slope of a gable,
           extrapolated back over the near wall, came out metres above the roof,
           and on a hip every wall got a rise out of a plane that does not reach
           it. That is the sheathing standing over the gable and the whole
           triangle appearing on a hip, and it is one missing test.

           Inside its own outline or it does not count. */
        var inside = false, w9, u9, v9, cnt9 = 0;
        for (w9 = 0, u9 = pts.length - 1; w9 < pts.length; u9 = w9++) {
          var xi = pts[w9][0], zi = pts[w9][2], xj = pts[u9][0], zj = pts[u9][2];
          if (((zi > z) !== (zj > z)) &&
              (x < (xj - xi) * (z - zi) / ((zj - zi) || 1e-9) + xi)) cnt9++;
        }
        inside = (cnt9 % 2) === 1;
        if (!inside) continue;

        var d = (B[0] - A[0]) * (C[2] - A[2]) - (B[2] - A[2]) * (C[0] - A[0]);
        if (Math.abs(d) < 1e-9) continue;
        var w1 = ((x - A[0]) * (C[2] - A[2]) - (z - A[2]) * (C[0] - A[0])) / d;
        var w2 = ((B[0] - A[0]) * (z - A[2]) - (B[2] - A[2]) * (x - A[0])) / d;
        var y = A[1] + w1 * (B[1] - A[1]) + w2 * (C[1] - A[1]);
        /* THE LOWEST PLANE OVER THE POINT, NOT THE HIGHEST.

           A roof made of planes is their lower envelope: at a hip line two
           planes meet and the roof is whichever is LOWER past that line. Taking
           the highest gave the wall profile the wrong plane wherever two
           polygons overlap near a hip, and the EPS on a hip end climbed out
           through the covering there -- more with a longer extension, which
           pushes the hip lines out over the end wall. The roof's own
           _roofPlaneYAt has taken the lowest for the same reason all along; on
           a gable the two only meet at the ridge and nothing changes. */
        if (best === null || y < best) best = y;
      }
      return best;
    }

    /* A BAND UNDER A ROOF LINE, BUILT DIRECTLY.

       Clipping a rectangle by one half-plane per sampled piece of the roof
       (_clipBelow) is exact only while the roof line is a tent -- concave --
       and since the facade climbs to the top of the truss it is not: over the
       corner return the line is LEVEL (the lift) and then it rises, a convex
       kink, and the rising piece's line extended backwards cut the level part
       off. The band lost a triangle at every top corner.

       So the band is built, not clipped: its bottom from where the roof is
       above yLo to where it stops being, and its top the roof line itself,
       sampled every 150 mm plus any x the caller names (a ridge), each sample
       held down to yHi, with the crossings interpolated. Any profile shape,
       one polygon, corners listed in order. Coordinates are the caller's own:
       x along, y up. */
    function houseUnderRoof(uA, uB, yLo, yHi, roofY, extra) {
      var N = Math.max(2, Math.ceil((uB - uA) / 0.15)), k, us = [];
      for (k = 0; k <= N; k++) us.push(uA + (uB - uA) * k / N);
      if (extra) {
        for (k = 0; k < extra.length; k++) {
          if (extra[k] > uA + 0.0005 && extra[k] < uB - 0.0005) us.push(extra[k]);
        }
      }
      us.sort(function (p1, p2) { return p1 - p2; });
      var ys = [], first = null, last = null;
      for (k = 0; k < us.length; k++) {
        ys.push(roofY(us[k]));
        if (ys[k] > yLo + 0.0005) { if (first === null) first = k; last = k; }
      }
      if (first === null) return [];
      var u0 = us[first], u1 = us[last];
      if (first > 0) {
        u0 = us[first - 1] + (us[first] - us[first - 1]) *
             (yLo - ys[first - 1]) / ((ys[first] - ys[first - 1]) || 1e-9);
      }
      if (last < us.length - 1) {
        u1 = us[last] + (us[last + 1] - us[last]) *
             (yLo - ys[last]) / ((ys[last + 1] - ys[last]) || 1e-9);
      }
      var poly = [[u0, yLo], [u1, yLo]];
      var prevY = null, prevU = null;
      for (k = last; k >= first; k--) {
        var yk = Math.min(yHi, ys[k]);
        /* where the roof crosses the top of the band between two samples, the
           crossing is a corner of its own, not a chord across it */
        if (prevY !== null && ((ys[k] > yHi) !== (prevY > yHi))) {
          var t = (yHi - prevY) / ((ys[k] - prevY) || 1e-9);
          poly.push([prevU + (us[k] - prevU) * t, yHi]);
        }
        poly.push([us[k], yk]);
        prevY = ys[k]; prevU = us[k];
      }
      /* points closer than half a millimetre are one point, and a point on the
         straight line between its neighbours says nothing */
      var out = [], q;
      for (q = 0; q < poly.length; q++) {
        var P = poly[q], Pp = out.length ? out[out.length - 1] : null;
        if (Pp && Math.abs(Pp[0] - P[0]) < 0.0005 && Math.abs(Pp[1] - P[1]) < 0.0005) continue;
        out.push(P);
      }
      var thin = [];
      for (q = 0; q < out.length; q++) {
        var A = out[(q + out.length - 1) % out.length], Bq = out[q], C = out[(q + 1) % out.length];
        var cr = (Bq[0] - A[0]) * (C[1] - A[1]) - (Bq[1] - A[1]) * (C[0] - A[0]);
        if (Math.abs(cr) < 1e-7 && out.length > 3) continue;
        thin.push(Bq);
      }
      return thin.length >= 3 ? thin : [];
    }

    function houseMembTex(T, key, bg, ink, weave, WALL_TEX, WALL_TEX_LOG) {
      key = key || 'memb';
      if (bg === undefined || bg === null) bg = '#DCDFD8';
      if (ink === undefined || ink === null) ink = '#0E1113';
      if (WALL_TEX[key]) return WALL_TEX[key];
      try {
        var n = 512, cv = global.document.createElement('canvas');
        cv.width = n; cv.height = n;
        var c = cv.getContext('2d');
        /* A NET IS MOSTLY HOLE, SO THE TILE IS MOSTLY NOTHING.

           bg === false leaves the canvas clear and only the strands get colour.
           Filling it red and drawing pale strands on top made a red SHEET with a
           pattern on it, which is what he keeps seeing -- the wall behind was
           never going to show through a solid fill however fine the lines were.
           The breather still passes a colour, because a breather IS a sheet. */
        if (bg !== false) {
          c.fillStyle = bg;
          c.fillRect(0, 0, n, n);
        }

        /* THE WEAVE, on the mesh only: a reinforcing textile is a grid and that
           is the whole of what it looks like. Drawn under the ink, because the
           ink is printed onto it. */
        if (weave) {
          /* FINER, AND FAINTER. At 2 px on a 512 tile covering 900 mm of wall
             each strand read as about three and a half millimetres of rope --
             heavier than the board joints underneath it, which made the mesh the
             loudest thing on the elevation instead of the thinnest. A strand is
             about a millimetre and the grid is about 5, so it is drawn at that:
             a lot more lines, a lot less ink in each. */
          /* thin strands, and OPEN. 96 lines was a fabric; a reinforcing mesh
             is mostly hole, which is how the coat gets through it and grips the
             board behind. About 20 mm between strands at this tile. */
          /* A STRAND NEEDS A DRAWN THICKNESS, LIKE EVERY OTHER THIN THING HERE.

             A real strand is about a millimetre. Drawn at a millimetre on a wall
             seen from ten metres it is well under a pixel and the whole mesh
             averages away to nothing -- which is why only the opaque lap bands
             were left on screen. The same answer as the sheathing joint and the
             films: draw it at what it takes to READ, and say so. */
          var wq, wn = 36;
          c.strokeStyle = (bg === false) ? weave : 'rgba(255,255,255,0.26)';
          c.lineWidth = (bg === false) ? 3.6 : 0.7;
          for (wq = 0; wq <= wn; wq++) {
            var wp = wq * (n / wn);
            c.beginPath(); c.moveTo(wp, 0); c.lineTo(wp, n); c.stroke();
            c.beginPath(); c.moveTo(0, wp); c.lineTo(n, wp); c.stroke();
          }
        }

        /* ONE WHOLE STRING TO A TILE, AND IT MUST NOT TOUCH THE SEAM.

           Drawing it at the centre and again a tile away in each direction was
           meant to carry the text across the join. What it actually did was
           bring the neighbours' copies into this tile, where the edge cut them:
           every repeat showed a tail of one string, a whole one, and the head of
           the next -- the "n www.theleva.com v" he is looking at.

           A repeating texture cannot carry a word across its own seam without
           the word being cut somewhere. So the word does not go near the seam.
           The font is measured and shrunk until the ROTATED box fits inside the
           tile with a margin, and it is drawn once. Every instance is then whole,
           which is what he asked for, and the spacing between them is the tile.

           The rotated box is w*cos + h*sin across and w*sin + h*cos up; the
           wider of the two is what has to fit. */
        var txt = 'www.theleva.com';
        var ang = Math.PI / 6;
        var ca = Math.cos(ang), sa = Math.sin(ang);
        var base = 64, gi;

        /* A SEED OF ITS OWN, so the grunge is the same on every rebuild and he
           is not chasing a pattern that moved between screenshots. */
        var gs = 7311901;
        var rnd = function () {
          gs = (gs * 1103515245 + 12345) & 0x7fffffff;
          return gs / 0x7fffffff;
        };

        /* THE FILM IS CLEAN. THE PRINT IS NOT.

           I put the grunge on the membrane and he asked for it on the letters.
           A breather comes off the roll flat and even; what is worn on it is the
           INK -- it breaks up on the fibres as it is laid down and it scuffs
           against itself in the roll. So the film keeps its flat colour and
           everything below happens to the text. */
        /* BIGGER, on his call: the string takes almost the whole tile now. */
        c.font = '700 ' + base + 'px Montserrat, Arial';
        var tw = c.measureText(txt).width || (txt.length * base * 0.55);
        var th = base * 1.05;
        var boxW = tw * ca + th * sa;
        var boxH = tw * sa + th * ca;
        var fit = (n * 0.97) / Math.max(boxW, boxH);
        var size = Math.max(10, base * fit);

        c.save();
        c.translate(n / 2, n / 2);
        c.rotate(-ang);
        c.fillStyle = (ink === false) ? 'rgba(0,0,0,0)' : ink;
        c.font = '700 ' + size.toFixed(1) + 'px Montserrat, Arial';
        c.textAlign = 'center';
        c.textBaseline = 'middle';
        c.fillText(txt, 0, 0);

        /* AND THE PRINT IS WORN. Ink on a spun fabric never lays solid -- it
           breaks up on the fibres, and it scuffs in the roll. Specks of the
           film's own colour punched back over the letters do both jobs, which
           is cheaper than a second texture and reads the same. */
        var hw = (tw * fit) / 2, hh = (th * fit) / 2;
        for (gi = 0; (ink !== false) && (bg !== false) && gi < 520; gi++) {
          var sx = (rnd() * 2 - 1) * hw * 1.05;
          var sy = (rnd() * 2 - 1) * hh * 1.15;
          var sr = 0.6 + rnd() * 3.2;
          /* punched back in the FILM's own colour -- and on a net there is no
             film colour to punch with, so there is nothing to punch: fillStyle
             = false is not a colour and canvas silently kept the INK, which put
             520 dark specks over the letters instead of taking bites out of
             them. */
          c.fillStyle = (bg === false) ? 'rgba(0,0,0,0)' : bg;
          c.globalAlpha = 0.35 + rnd() * 0.5;
          c.beginPath();
          c.arc(sx, sy, sr, 0, Math.PI * 2);
          c.fill();
        }
        c.globalAlpha = 1;
        c.restore();

        var tx = new T.CanvasTexture(cv);
        if (T.SRGBColorSpace) tx.colorSpace = T.SRGBColorSpace;
        tx.wrapS = tx.wrapT = T.RepeatWrapping;
        WALL_TEX[key] = tx;
        WALL_TEX_LOG.push(key + ' ok bg=' + (bg === false ? 'clear' : bg) +
                          ' ink=' + (ink === false ? 'none' : ink) +
                          ' weave=' + (weave || 'none'));
        return tx;
      } catch (e) {
        WALL_TEX_LOG.push(key + ' FAILED ' + (e && e.message));
        return null;
      }
    }

    function houseSpaceBuild(SL, PS, H) {
      /* what the product's copy read from its own scope */
      var API = H.api, PART = H.PART, _roomLabel = H.roomLabel, _roomRects = H.roomRects;
      var RM = _roomRects(), B = [], X0 = 1e9, X1 = -1e9, Z0 = 1e9, Z1 = -1e9, i, j, q, kk, ii;
      for (i = 0; i < RM.length; i++) {
        var RS = RM[i].rects || (typeof RM[i].x0 === 'number' ? [RM[i]] : []);
        for (j = 0; j < RS.length; j++) {
          var r = RS[j];
          if (typeof r.x0 !== 'number') continue;
          B.push({ i: i, x0: r.x0, x1: r.x1, z0: r.z0, z1: r.z1 });
          X0 = Math.min(X0, r.x0); X1 = Math.max(X1, r.x1); Z0 = Math.min(Z0, r.z0); Z1 = Math.max(Z1, r.z1);
        }
      }
      if (!B.length || !(X1 > X0) || !(Z1 > Z0)) return null;
      var h = 0.05;
      if ((X1 - X0) * (Z1 - Z0) / (h * h) > 400000) h = Math.sqrt((X1 - X0) * (Z1 - Z0) / 400000);
      var NX = Math.max(1, Math.ceil((X1 - X0) / h)), NZ = Math.max(1, Math.ceil((Z1 - Z0) / h)), N = NX * NZ;
      var lab = new Int16Array(N), blk = new Uint8Array(N), comp = new Int32Array(N), room = new Int16Array(N), wedge = new Uint8Array(N), Qu = new Int32Array(N);
      for (q = 0; q < N; q++) { lab[q] = -1; comp[q] = -1; room[q] = -1; }
      /* the rooms as the plan cuts them, cell by cell (a cell is in a
         rectangle when its centre is) */
      for (q = 0; q < B.length; q++) {
        var b = B[q];
        var ia = Math.max(0, Math.ceil((b.x0 - X0) / h - 0.5)), ib = Math.min(NX - 1, Math.floor((b.x1 - X0) / h - 0.5));
        var ka = Math.max(0, Math.ceil((b.z0 - Z0) / h - 0.5)), kb = Math.min(NZ - 1, Math.floor((b.z1 - Z0) / h - 0.5));
        for (kk = ka; kk <= kb; kk++) for (ii = ia; ii <= ib; ii++) if (lab[kk * NX + ii] < 0) lab[kk * NX + ii] = b.i;
      }
      /* the real walls: a cell whose centre is in a wall's band is wall.
         Square ones run on past their ends by a stud's depth, slanted ones by
         100 mm, so a joint never leaves a gap a room could leak through */
      var half = PART.d / 2, bandS = half + 0.5 * h, bandG = half + 0.75 * h, cx, cz;
      /* the shell: the plan's rectangles run out to the outer face, and a
         partition stops at the inner one -- so the outer wall's own band is
         wall too, or every room would leak round every partition's end */
      var PO = [], wtS = 0;
      try { PO = API.poly() || []; wtS = API.wallThickness() || 0; } catch (eP) { PO = []; }
      if (PO.length > 2 && wtS > 0) {
        for (kk = 0; kk < NZ; kk++) for (ii = 0; ii < NX; ii++) {
          var cS = kk * NX + ii;
          if (lab[cS] < 0) continue;
          cx = X0 + (ii + 0.5) * h; cz = Z0 + (kk + 0.5) * h;
          for (q = 0; q < PO.length; q++) {
            var pa = PO[q], pb = PO[(q + 1) % PO.length], ex = pb[0] - pa[0], ez = pb[1] - pa[1], el = ex * ex + ez * ez;
            if (!(el > 1e-9)) continue;
            var tS = Math.max(0, Math.min(1, ((cx - pa[0]) * ex + (cz - pa[1]) * ez) / el)), dxS = cx - (pa[0] + ex * tS), dzS = cz - (pa[1] + ez * tS);
            if (dxS * dxS + dzS * dzS < wtS * wtS) { blk[cS] = 1; break; }
          }
        }
      }
      for (q = 0; q < PS.length; q++) {
        var P = PS[q], sp = null;
        if (!P || P.sep) continue;
        try { sp = API.partSpan ? API.partSpan(P) : null; } catch (eS) { sp = null; }
        var c = (typeof P.at === 'number') ? P.at : P.c, vv = (P.dir || P.o) === 'v';
        var lo = sp ? sp[0] : Math.min(P.a, P.b), hi = sp ? sp[1] : Math.max(P.a, P.b);
        if (typeof c !== 'number' || !(hi > lo)) continue;
        lo -= half + h; hi += half + h;
        var xa = vv ? c - bandS : lo, xb = vv ? c + bandS : hi, za = vv ? lo : c - bandS, zb = vv ? hi : c + bandS;
        var i0 = Math.max(0, Math.ceil((xa - X0) / h - 0.5)), i1 = Math.min(NX - 1, Math.floor((xb - X0) / h - 0.5));
        var k0 = Math.max(0, Math.ceil((za - Z0) / h - 0.5)), k1 = Math.min(NZ - 1, Math.floor((zb - Z0) / h - 0.5));
        for (kk = k0; kk <= k1; kk++) for (ii = i0; ii <= i1; ii++) blk[kk * NX + ii] = 1;
      }
      for (q = 0; q < SL.length; q++) {
        var g = SL[q], ext = 0.10;
        var gax = g.a[0] - g.ux * ext, gaz = g.a[1] - g.uz * ext, gbx = g.b[0] + g.ux * ext, gbz = g.b[1] + g.uz * ext, gL = g.len + 2 * ext;
        var gi0 = Math.max(0, Math.floor((Math.min(gax, gbx) - bandG - X0) / h)), gi1 = Math.min(NX - 1, Math.ceil((Math.max(gax, gbx) + bandG - X0) / h));
        var gk0 = Math.max(0, Math.floor((Math.min(gaz, gbz) - bandG - Z0) / h)), gk1 = Math.min(NZ - 1, Math.ceil((Math.max(gaz, gbz) + bandG - Z0) / h));
        for (kk = gk0; kk <= gk1; kk++) for (ii = gi0; ii <= gi1; ii++) {
          cx = X0 + (ii + 0.5) * h; cz = Z0 + (kk + 0.5) * h;
          var tG = (cx - gax) * g.ux + (cz - gaz) * g.uz, sG = (cx - gax) * g.nx + (cz - gaz) * g.nz;
          if (tG >= 0 && tG <= gL && Math.abs(sG) <= bandG) blk[kk * NX + ii] = 1;
        }
      }
      /* the spaces: floor reached from floor without crossing a wall */
      var nC = 0, qh, qt, cq, nb, dd4, DX4 = [1, -1, 0, 0], DZ4 = [0, 0, 1, -1];
      for (q = 0; q < N; q++) {
        if (comp[q] >= 0 || lab[q] < 0 || blk[q]) continue;
        qh = 0; qt = 0; Qu[qt++] = q; comp[q] = nC;
        while (qh < qt) {
          cq = Qu[qh++]; ii = cq % NX; kk = (cq - ii) / NX;
          for (dd4 = 0; dd4 < 4; dd4++) {
            var ni = ii + DX4[dd4], nk = kk + DZ4[dd4];
            if (ni < 0 || nk < 0 || ni >= NX || nk >= NZ) continue;
            nb = nk * NX + ni;
            if (comp[nb] >= 0 || lab[nb] < 0 || blk[nb]) continue;
            comp[nb] = nC; Qu[qt++] = nb;
          }
        }
        nC++;
      }
      /* each room's home: the space holding most of its floor */
      var cnt = [], home = [];
      for (i = 0; i < RM.length; i++) { cnt.push({}); home.push(-1); }
      for (q = 0; q < N; q++) if (comp[q] >= 0 && lab[q] < RM.length) cnt[lab[q]][comp[q]] = (cnt[lab[q]][comp[q]] || 0) + 1;
      for (i = 0; i < RM.length; i++) {
        var bn = 0, kc;
        for (kc in cnt[i]) if (cnt[i][kc] > bn) { bn = cnt[i][kc]; home[i] = kc | 0; }
      }
      /* the wedges take the room of the nearest floor at home in their space */
      var nW = 0;
      qh = 0; qt = 0;
      for (q = 0; q < N; q++) {
        if (comp[q] < 0) continue;
        if (comp[q] === home[lab[q]]) { room[q] = lab[q]; Qu[qt++] = q; }
        else { wedge[q] = 1; nW++; }
      }
      while (qh < qt) {
        cq = Qu[qh++]; ii = cq % NX; kk = (cq - ii) / NX;
        for (dd4 = 0; dd4 < 4; dd4++) {
          var mi = ii + DX4[dd4], mk = kk + DZ4[dd4];
          if (mi < 0 || mk < 0 || mi >= NX || mk >= NZ) continue;
          nb = mk * NX + mi;
          if (comp[nb] !== comp[cq] || room[nb] >= 0) continue;
          room[nb] = room[cq]; Qu[qt++] = nb;
        }
      }
      /* what moved, for the log */
      var mv = {}, notes = [], mk2;
      for (q = 0; q < N; q++) {
        if (!wedge[q]) continue;
        mk2 = lab[q] + '>' + room[q];
        mv[mk2] = (mv[mk2] || 0) + h * h;
      }
      for (mk2 in mv) {
        var pr2 = mk2.split('>'), oI = pr2[0] | 0, nI = pr2[1] | 0, aM = Math.round(mv[mk2] * 100) / 100;
        /* a pocket of a few cells where two walls meet is the grid, not a room */
        if (aM < 0.05) continue;
        if (nI < 0) notes.push(aM + ' m2 of ' + _roomLabel(RM[oI], oI) + ' closed off behind a slanted wall -- kept as ' + _roomLabel(RM[oI], oI));
        else notes.push(aM + ' m2 of ' + _roomLabel(RM[oI], oI) + '\'s rectangle lies past its slanted wall -- it is ' + _roomLabel(RM[nI], nI) + '\'s floor');
      }
      return { X0: X0, Z0: Z0, h: h, NX: NX, NZ: NZ, lab: lab, comp: comp, room: room, wedge: wedge, nW: nW, nC: nC,
               notes: (notes.length ? notes.join('; ') : 'no wedge -- every room in one piece') + ' (grid ' + Math.round(h * 1000) + ' mm, ' + nC + ' space(s), adu-v1414)' };
    }

    function houseDiagWalls(ctx, H) {
      /* what the product's copy read from its own scope */
      var API = H.api, DOOR_W_DEF = H.DOOR_W_DEF, PART = H.PART, PART_BOARD = H.PART_BOARD, _spaceMap = H.spaceMap;
      var L = (API.STATE.product && API.STATE.product.diagWalls) || [], out = [], i, k;
      var parts = API.STATE.parts || [];
      var half = PART.d / 2, tb = PART_BOARD.t;
      var P1 = API.poly() || [], wt1 = API.wallThickness(), fx0 = 1e9, fx1 = -1e9, fz0 = 1e9, fz1 = -1e9;
      for (i = 0; i < P1.length; i++) {
        fx0 = Math.min(fx0, P1[i][0]); fx1 = Math.max(fx1, P1[i][0]);
        fz0 = Math.min(fz0, P1[i][1]); fz1 = Math.max(fz1, P1[i][1]);
      }
      /* the outer walls' inner faces, each with the side the house is on */
      var FACES = P1.length ? [{ o: 'v', c: fx0 + wt1, sg: 1, lo: fz0, hi: fz1 }, { o: 'v', c: fx1 - wt1, sg: -1, lo: fz0, hi: fz1 },
                               { o: 'h', c: fz0 + wt1, sg: 1, lo: fx0, hi: fx1 }, { o: 'h', c: fz1 - wt1, sg: -1, lo: fx0, hi: fx1 }] : [];
      for (i = 0; i < L.length; i++) {
        var gw = L[i];
        if (!gw || !gw.a || !gw.b) continue;
        if ((gw.st | 0) !== 0) { ctx.log('partitions SLANTED ' + (gw.id || i) + ' on storey ' + ((gw.st | 0) + 1) + ' -- not built, ground floor only'); continue; }
        var dx = gw.b[0] - gw.a[0], dz = gw.b[1] - gw.a[1], L0 = Math.hypot(dx, dz);
        if (!(L0 > 0.3)) continue;
        var ux = dx / L0, uz = dz / L0, notes = [], lands = [];
        /* how far to move an end along the wall (+ shortens it), and what
           it meets; ix,iz point from the end into the wall */
        var endTrim = function (E, ix, iz, tag) {
          var q, best = null;
          for (q = 0; q < parts.length; q++) {
            var po = parts[q], sp = null;
            if (!po || po.sep) continue;
            try { sp = API.partSpan ? API.partSpan(po) : null; } catch (eS) { sp = null; }
            var lo2 = sp ? sp[0] : Math.min(po.a, po.b), hi2 = sp ? sp[1] : Math.max(po.a, po.b);
            var onV = (po.dir === 'v'), dp = onV ? ix : iz, c = Math.abs(dp), al = onV ? E[1] : E[0];
            if (c < 0.2 || al < lo2 - 0.05 || al > hi2 + 0.05) continue;
            var e0 = ((onV ? E[0] : E[1]) - po.at) * (dp > 0 ? 1 : -1);
            if (Math.abs(e0) > 0.04) continue;
            var s1 = Math.sqrt(Math.max(0, 1 - c * c)), t1 = (half * (1 - s1) - e0) / c;
            if (!best || t1 > best.t) best = { t: t1, po: po, how: tag + ' on partition ' + (onV ? 'v@' : 'h@') + Math.round(po.at * 1000) + ', far corner on its face' };
          }
          if (best) return best;
          for (q = 0; q < FACES.length; q++) {
            var F = FACES[q], dpF = (F.o === 'v') ? ix : iz, cF = Math.abs(dpF), alF = (F.o === 'v') ? E[1] : E[0];
            if (cF < 0.2 || dpF * F.sg <= 0 || alF < F.lo - 0.05 || alF > F.hi + 0.05) continue;
            var eF = ((F.o === 'v') ? E[0] : E[1]) - F.c;
            eF *= F.sg;
            if (Math.abs(eF) > 0.04) continue;
            var sF = Math.sqrt(Math.max(0, 1 - cF * cF)), tF = Math.max(-0.15, (-(half + tb) * sF - eF) / cF);
            return { t: tF, po: null, how: tag + ' on the outer wall, board corner on its inner face' };
          }
          return { t: 0, po: null, how: tag + ' free' };
        };
        var EA = endTrim(gw.a, ux, uz, 'start'), EB = endTrim(gw.b, -ux, -uz, 'end');
        var tA = EA.t, tB = EB.t;
        notes.push(EA.how + (tA ? (' (' + (tA > 0 ? 'in ' : 'out ') + Math.abs(Math.round(tA * 1000)) + ' mm)') : ''));
        notes.push(EB.how + (tB ? (' (' + (tB > 0 ? 'in ' : 'out ') + Math.abs(Math.round(tB * 1000)) + ' mm)') : ''));
        if (EA.po) lands.push({ po: EA.po, x: gw.a[0], z: gw.a[1] });
        if (EB.po) lands.push({ po: EB.po, x: gw.b[0], z: gw.b[1] });
        var len = L0 - tA - tB;
        if (len < 0.3) { ctx.log('partitions SLANTED ' + (gw.id || i) + ' SKIPPED -- ' + Math.round(len * 1000) + ' mm left after its ends'); continue; }
        var holes = [];
        for (k = 0; gw.doors && k < gw.doors.length; k++) {
          var d = gw.doors[k], w = d.w || DOOR_W_DEF, u = d.u - tA;
          var a0 = Math.max(0, u - w / 2), a1 = Math.min(len, u + w / 2);
          if (a1 - a0 < 0.30) continue;
          var kd = String(d.kind || 'hinged');
          holes.push({ a0: a0, a1: a1, pass: kd === 'pass', sw: (d.sw | 0), slide: (kd === 'slide') ? 1 : 0,
                       kind: (kd === 'pair' || kd === 'bifold' || kd === 'bypass') ? kd : '', w: w, hand: (d.sw | 0) ? 'L' : 'R' });
        }
        holes.sort(function (m1, m2) { return m1.a0 - m2.a0; });
        out.push({ id: gw.id || ('g' + (i + 1)), len: len, ux: ux, uz: uz, nx: -uz, nz: ux, ang: Math.atan2(uz, ux),
                   ax: gw.a[0] + ux * tA, az: gw.a[1] + uz * tA, holes: holes, lands: lands,
                   note: notes.join(', '),
                   p: { id: 'diag-' + (gw.id || i), dir: 'h', o: 'h', at: 0, a: 0, b: len } });
      }
      if (out.length) { try { var SMl = _spaceMap(); ctx.log('partitions SLANTED rooms either side: ' + (SMl ? SMl.notes : 'no room plan to read')); } catch (eSm) {} }
      return out;
    }

    /* =========================================================================
       THE FEW-VARIANT HELPERS (core-v808, status/modules.md 12). Five more
       functions of the house line, each in two to five versions that differed
       only by a product's own rule -- now one text with the rule as a hook:
         houseFinTex          the plank / tile / OSB texture (SAUNA: grout)
         houseStudPositions   the studs of a panel, every bay the same (the ADU
                              family); houseStudPositionsWorld on the world
                              grid (the shed family: SAUNA, TAURUS)
         houseGableFaces      where the gable walls stand for the trusses
                              (RANCH: masonry)
         houseTrussStations   where the trusses stand (BARNDO: first, ANY
                              HOUSE: runRidge + endStop, GA: endStop, RANCH:
                              faces)
         houseTerraceDeck     the terrace, deck or pavers, its bill (RANCH:
                              rect, td, qtyPiece)
       A product without the hook gets exactly its old text.
       ========================================================================= */
    function houseFinTex(T, kind, col, FIN_TEX, grout) {
      /* the plank and the tile are drawn in the colour asked for and cached
         under it; osb has one colour */
      var ckey = (kind === 'osb') ? kind : (kind + ':' + (col == null ? 'd' : col));
      if (FIN_TEX[ckey]) return FIN_TEX[ckey];
      function shade(hex, f) {
        var rr = Math.min(255, Math.round(((hex >> 16) & 255) * f)), gg = Math.min(255, Math.round(((hex >> 8) & 255) * f)),
            bb = Math.min(255, Math.round((hex & 255) * f));
        return 'rgb(' + rr + ',' + gg + ',' + bb + ')';
      }
      try {
        var n = 512, cv = global.document.createElement('canvas');
        cv.width = n; cv.height = n;
        var c = cv.getContext('2d'), i, j;

        if (kind === 'osb') {
          /* OSB IS FLAKES, NOT GRAIN.

             The board is a mat of long chips pressed flat, so the pattern is
             dozens of pale rectangles at every angle over a warmer base -- no
             courses, no joints, no direction. Drawn here rather than as a second
             texture mechanism, because this factory already exists and already
             caches (Marek). */
          c.fillStyle = '#C8A063'; c.fillRect(0, 0, n, n);
          var seed = 20260831;
          /* an expression: this sits inside the kind test, and a function
             statement in a block is the fault that has cost four layers today */
          var rnd = function () {
            seed = (seed * 1103515245 + 12345) & 0x7fffffff;
            return seed / 0x7fffffff;
          };
          for (i = 0; i < 900; i++) {
            var fx = rnd() * n, fy = rnd() * n;
            var fl = 18 + rnd() * 46, fw = 5 + rnd() * 9;
            var ang = rnd() * Math.PI;
            var lum = 0.72 + rnd() * 0.46;
            var rr = Math.min(255, Math.round(200 * lum));
            var gg = Math.min(255, Math.round(160 * lum));
            var bb = Math.min(255, Math.round(99 * lum));
            c.save();
            c.translate(fx, fy);
            c.rotate(ang);
            c.fillStyle = 'rgba(' + rr + ',' + gg + ',' + bb + ',0.75)';
            c.fillRect(-fl / 2, -fw / 2, fl, fw);
            c.restore();
          }
        } else if (kind === 'tile') {
          /* one 400 x 400 tile per texture, so the repeat IS the tile */
          var tc = (col == null) ? 0xF2F2EE : col;
          c.fillStyle = shade(tc, 1.0); c.fillRect(0, 0, n, n);
          /* THE JOINT IS GROUT, NOT A SHADE OF THE TILE (sauna-v53; every product
             since core-v813, grout false keeps the shaded joint): a 14% darker line on a white tile was lost at any distance,
             and on the magenta of a tile under review it vanished (Marek: nevidim
             spary). A grout grey, the same on every tile colour. */
          if (grout !== false) { c.strokeStyle = '#8E8E88'; c.lineWidth = 12; }
          else { c.strokeStyle = shade(tc, 0.86); c.lineWidth = 10; }
          c.strokeRect(0, 0, n, n);
          /* a faint sheen so it does not read as paper */
          var gr = c.createLinearGradient(0, 0, n, n);
          gr.addColorStop(0, 'rgba(255,255,255,0.35)');
          gr.addColorStop(1, 'rgba(220,220,214,0.25)');
          c.fillStyle = gr; c.fillRect(6, 6, n - 12, n - 12);
        } else {
          /* four courses of 800 x 180 plank, each course offset by a third,
             in the floor's own colour with a shade per course */
          var pc = (col == null) ? 0xC9A876 : col;
          c.fillStyle = shade(pc, 1.0); c.fillRect(0, 0, n, n);
          var rows = 4, hh = n / rows;
          for (i = 0; i < rows; i++) {
            var off = (i % 3) * (n / 3);
            c.fillStyle = shade(pc, [1.02, 0.96, 1.06, 0.99][i % 4]);
            c.fillRect(0, i * hh, n, hh - 2);
            /* the grain, a few long strokes down the plank */
            c.strokeStyle = 'rgba(150,116,72,0.30)'; c.lineWidth = 1;
            for (j = 0; j < 7; j++) {
              var yy = i * hh + 4 + j * (hh - 8) / 7;
              c.beginPath(); c.moveTo(0, yy); c.lineTo(n, yy + (j % 2 ? 1.5 : -1.5)); c.stroke();
            }
            /* the end joints, offset course to course */
            c.strokeStyle = 'rgba(120,92,56,0.55)'; c.lineWidth = 2;
            c.beginPath(); c.moveTo(off, i * hh); c.lineTo(off, i * hh + hh - 2); c.stroke();
            /* and the long joint under it */
            c.strokeStyle = 'rgba(120,92,56,0.40)';
            c.beginPath(); c.moveTo(0, i * hh + hh - 1); c.lineTo(n, i * hh + hh - 1); c.stroke();
          }
        }
        var tx = new T.CanvasTexture(cv);
        if (T.SRGBColorSpace) tx.colorSpace = T.SRGBColorSpace;
        tx.wrapS = tx.wrapT = T.RepeatWrapping;
        FIN_TEX[ckey] = tx;
        return tx;
      } catch (e) { return null; }
    }

    function houseStudPositions(len, datum, H) {
      /* what the product's copy read from its own scope */
      var API = H.api, STUD = H.STUD;
      /* datum is where this panel starts in the world, along its own run. The
         first module line is pulled back to the nearest step of the shared grid,
         so panels on parallel walls -- and successive panels on the same wall --
         all land on one set of lines. Without it each panel restarted its own
         count and nothing lined up with anything. */
      /* Studs from one end at the module, and ALWAYS one at the far end however
         short the last bay comes out. A wall that ends in a gap has nothing to
         nail the sheet to.

         THE LAST MODULE IS DROPPED IF IT LANDS ON THE END STUD. A 24 ft wall at
         24 in centres comes out 5 mm short of a whole bay, and stepping blindly
         put a stud 5 mm from the corner post -- two studs in the same place,
         counted twice in the timber and impossible to build. */
      /* AND EVERY STUD SITS INSIDE ITS OWN PANEL. They used to be measured from
         the panel edge, so the last stud of one panel and the first of the next
         landed on exactly the same 38 mm -- one shared stud between two panels,
         which is a wall that cannot be built in panels at all. Each panel now
         carries its own end stud, half a thickness in, so a joint between two
         panels is TWO studs face to face. That is what a panel joint is. */
      /* EVERY BAY THE SAME. NO EXCEPTIONS, INCLUDING THE ONES AT THE ENDS.

         Stepping the module from one end and forcing a post at the other leaves
         the last bay short -- 595 where the rest are 625 -- and I have called
         that an axis correction twice tonight. It is not: the rule is that the
         spacing is constant, and a wall whose length is not a whole number of
         bays does not get to break it.

         So the posts at the two ends are set first, flush with the wall, and the
         run between them is divided into the whole number of bays nearest the
         module. Every bay is then identical by construction. On a wall whose
         length IS a whole number of bays -- which is what stepping the plan will
         give -- that division comes out at exactly the module and the sheet
         edges land on studs (Marek: vsechny sloupky / pole musi mit stejnou
         roztec). */
      var sp = API.grid(), out = [], lo = STUD.t / 2, hi = len - STUD.t / 2;
      var span = hi - lo;
      if (!(span > 0.001)) return [lo];
      var n = Math.max(1, Math.round(span / sp));
      var step = span / n, k;
      for (k = 0; k <= n; k++) out.push(lo + step * k);
      return out;
    }

    function houseStudPositionsWorld(len, datum, H) {
      /* what the product's copy read from its own scope */
      var API = H.api, STUD = H.STUD;
      /* datum is where this panel starts in the world, along its own run. The
         first module line is pulled back to the nearest step of the shared grid,
         so panels on parallel walls -- and successive panels on the same wall --
         all land on one set of lines. Without it each panel restarted its own
         count and nothing lined up with anything. */
      /* Studs from one end at the module, and ALWAYS one at the far end however
         short the last bay comes out. A wall that ends in a gap has nothing to
         nail the sheet to.

         THE LAST MODULE IS DROPPED IF IT LANDS ON THE END STUD. A 24 ft wall at
         24 in centres comes out 5 mm short of a whole bay, and stepping blindly
         put a stud 5 mm from the corner post -- two studs in the same place,
         counted twice in the timber and impossible to build. */
      /* AND EVERY STUD SITS INSIDE ITS OWN PANEL. They used to be measured from
         the panel edge, so the last stud of one panel and the first of the next
         landed on exactly the same 38 mm -- one shared stud between two panels,
         which is a wall that cannot be built in panels at all. Each panel now
         carries its own end stud, half a thickness in, so a joint between two
         panels is TWO studs face to face. That is what a panel joint is. */
      var sp = API.grid(), out = [], end = len - STUD.t / 2, x = STUD.t / 2;

      /* THE PHASE IS ON THE WORLD AXIS, not mirrored by the wall's direction.

         I flipped the sign with the run and called it aligned. It is not: two
         walls facing each other then sit on grids offset by half a stud in
         OPPOSITE directions, so their studs miss by 45 mm -- close enough to look
         intended and far enough that no rafter lands on both.

           wall A  -3025      wall B  +3026      mine, 45 mm apart
           wall A  -3048      wall B  +3048      on the world grid, facing

         datum arrives already resolved to a world coordinate along the run, so
         the module lines are simply the multiples of the grid. No stud offset in
         here: half a stud is where a PANEL starts, which is a different thing
         and is added below. */
      if (typeof datum === 'number' && isFinite(datum)) {
        x = ((-datum % sp) + sp) % sp;
        while (x < STUD.t / 2) x += sp;
      }
      while (x < end - STUD.t) { out.push(x); x += sp; }

      /* THE END STUDS ARE NOT OPTIONAL. */
      var drop = STUD.t * 1.8, o2 = [], oq;
      for (oq = 0; oq < out.length; oq++) {
        if (out[oq] - STUD.t / 2 < drop) continue;
        if (end - out[oq] < drop) continue;
        o2.push(out[oq]);
      }
      out = o2;
      out.unshift(STUD.t / 2);
      out.push(end);
      return out;
    }

    /* WHERE THE TRUSSES STAND, ANSWERED WITHOUT BUILDING THEM. */
    /* THE GABLE'S SET-OUT LINES: the wall faces along the ridge (from the
       segments), the end truss on each face (sheathing plus half a chord in),
       and the last station out over the rake inside the facade. Shared by the
       gable and the gable end of a half-hip. */
    function houseGableFaces(H) {
      /* what the product's copy read from its own scope */
      var API = H.api, CHORD = H.CHORD, _rakeReach = H.rakeReach;
      var R = API.STATE.roof, alongL = (R.axis === 'l'), B = _buildRect();
      /* the rake overhang as _trussStations reads it: without the facade --
         adding it pushed the last station out in front of the cladding */
      var OG = API.roofOver();
      var rakeA = (alongL ? OG.z0 : OG.x0), rakeB = (alongL ? OG.z1 : OG.x1);
      var lo9 = null, hi9 = null, SG9 = [], q9;
      try { SG9 = segments() || []; } catch (eS9) { SG9 = []; }
      for (q9 = 0; q9 < SG9.length; q9++) {
        var c9a = alongL ? SG9[q9].z0 : SG9[q9].x0;
        var c9b = alongL ? SG9[q9].z1 : SG9[q9].x1;
        if (lo9 === null || c9a < lo9) lo9 = c9a;
        if (lo9 === null || c9b < lo9) lo9 = c9b;
        if (hi9 === null || c9a > hi9) hi9 = c9a;
        if (hi9 === null || c9b > hi9) hi9 = c9b;
      }
      if (lo9 === null) { lo9 = alongL ? B.z0 : B.x0; hi9 = alongL ? B.z1 : B.x1; }
      var SHTH = 0.018;
      /* RANCH: a masonry gable sets its own faces (ranch-v20, the hook masonry) */
      if (H.masonry) { var M9 = H.masonry(lo9, hi9, rakeA, rakeB, B, alongL); if (M9) return M9; }
      return { lo9: lo9, hi9: hi9,
               wA: lo9 + SHTH + CHORD.t / 2, wB: hi9 - SHTH - CHORD.t / 2,
               loEdge: (alongL ? B.z0 : B.x0) - _rakeReach(rakeA) + CHORD.t / 2,
               hiEdge: (alongL ? B.z1 : B.x1) + _rakeReach(rakeB) - CHORD.t / 2 };
    }

    function houseTrussStations(H) {
      /* what the product's copy read from its own scope, and its own rules:
           first     BARNDO -- on a gable the post frame's lines (_pfStations)
           runRidge  ANY HOUSE -- the L's hipped wing runs on its own ridge
           endStop   GARAGE APT / ANY HOUSE -- no truss past a laddered gable
                     end, nor past the L's wing at the seam
           faces     RANCH -- the block house's face stations, and none in a
                     masonry gable */
      var API = H.api, CHORD = H.CHORD, PLATE = H.PLATE, _gableFaces = H.gableFaces;
      var _endStop = function (lo) { return H.endStop ? !!H.endStop(lo) : false; };
      if (H.first) {
        try { var pfS = H.first(); if (pfS && pfS.length >= 2) return pfS; } catch (ePFS) {}
      }
      /* API.STATE, NOT STATE. This function sits in the PRODUCT section,
         where the state is only ever reached through API -- bare STATE is a name
         from... */
      var R = API.STATE.roof || {};
      var B = _buildRect(), O = _roofOver();
      var alongL = (R.axis === 'l');
      var rakeA = alongL ? O.z0 : O.x0, rakeB = alongL ? O.z1 : O.x1;
      /* THE RAKE RUN IS FROM THE FRAME, AND THAT IS HIS CALL.

         For one version this reached the roof edge facade included, so that the
         last truss stood under the verge; he sent it back. With a small
         overhang the end truss is meant to sit INSIDE the facade, hidden, and
         the roof build-up cantilevers past it -- what stuck out of the facade
         was the plates running on beyond that truss, and those are shortened
         to it instead (_plateEndReach). */
      var runFrom = (alongL ? B.z0 : B.x0) - rakeA;
      var runTo = (alongL ? B.z1 : B.x1) + rakeB;
      var sp = API.grid();
      var out = [], q;

      if (R.type === 'hip') {
        /* between the ridge ends, which the planes decide */
        var P = [];
        try { P = roofPlanes() || []; } catch (e) { P = []; }
        var lo = null, hi = null;
        for (q = 0; q < P.length; q++) {
          var RG = P[q] && P[q].ridge;
          if (!RG || RG.length < 2) continue;
          var dx = Math.abs(RG[1][0] - RG[0][0]), dz = Math.abs(RG[1][2] - RG[0][2]);
          if (Math.max(dx, dz) < 0.05) continue;
          var useZ = dz >= dx;
          var a = useZ ? RG[0][2] : RG[0][0], b = useZ ? RG[1][2] : RG[1][0];
          lo = Math.min(a, b); hi = Math.max(a, b);
          break;
        }
        /* anyhouse-lib-v13, THE L'S WING HIPPED: its run's ridge is the
           wing's own (the outline here is the wing's rectangle alone) */
        var WRg = H.runRidge ? H.runRidge() : null;
        if (WRg) { lo = WRg[0]; hi = WRg[1]; }
        if (lo === null) return [];
        /* A HALF-HIP: the gable end is set out the gable's way -- the end truss
           on the wall face, bays inward at the module, and stations outward
           over the rake to the last one inside the facade -- while the hipped
           end keeps the girder on the ridge end. Both ends hipped is as before.
           (Marek: the end truss stood out in front of the cladding.) */
        var hipLo = hipAt('lo'), hipHi = hipAt('hi');
        if (hipLo && hipHi) {
          var nR = Math.max(1, Math.round((hi - lo) / sp));
          for (q = 0; q <= nR; q++) out.push(lo + ((hi - lo) / nR) * q);
          return out;
        }
        var GF = _gableFaces();
        var a0 = hipLo ? lo : GF.wA, b0 = hipHi ? hi : GF.wB;
        var nH = Math.max(1, Math.round((b0 - a0) / sp)), pH = (b0 - a0) / nH;
        for (q = 0; q <= nH; q++) out.push(a0 + pH * q);
        var yH;
        /* garageapt-v9: no truss out over a laddered gable end (as v7) -- endStop */
        if (!hipLo && !_endStop(true)) { yH = a0 - pH; while (yH > GF.loEdge - 0.001) { out.unshift(yH); yH -= pH; } }
        if (!hipHi && !_endStop(false)) { yH = b0 + pH; while (yH < GF.hiEdge + 0.001) { out.push(yH); yH += pH; } }
        /* RANCH (faces): the block house's face stations, none in a masonry gable */
        if (H.faces) { out = H.faces(out, rakeA, rakeB, B, alongL, !!hipLo, !!hipHi); out.sort(function (p1, p2) { return p1 - p2; }); }
        return out;
      }

      /* THE MODULE BELONGS TO THE BUILDING, NOT TO THE ROOF EDGE.

         Last version forced a station onto each gable wall and left the rest of
         the set-out where it was -- divided evenly between the RAKE edges. One
         end then happened to agree and the other did not, which is exactly what
         he saw: a truss that does not come out on the second gable, and a short
         bay beside it. Forcing a member into a set-out that was struck from
         somewhere else can only ever work at one end.

         So the division is struck between the two WALL FACES. Both gables land
         on a station because both gables ARE stations, every bay in between is
         equal, and the overhang is then carried on at the same pitch by adding
         stations outward until the rake is covered. The module is the building's
         and the roof follows it, rather than the other way round. */
      /* AND THE WALL LINE COMES FROM THE WALLS, NOT FROM THE RECT.

         One gable landed on a station and the other did not, which is the tell
         that the two ends were being measured from different things: the truss
         from _buildRect, the sheathing from the wall SEGMENT it is drawn on. If
         those two disagree by even a few millimetres at one end, the board has
         nothing to land on there -- and it will always be one end, because a
         rect is symmetric and a rounding is not.

         Asked of the segments instead, so the timber and the boarding are set
         out from one source and cannot drift apart. */
      var inset = CHORD.t / 2 + PLATE.t;
      var GF2 = _gableFaces();
      var lo9 = GF2.lo9, hi9 = GF2.hi9, q9;
      var SHTH = 0.018;
      var wA = GF2.wA, wB = GF2.wB;
      if (!(wB - wA > 0.2)) {
        var span0 = (runTo - inset) - (runFrom + inset);
        var n0 = Math.max(1, Math.round(span0 / sp));
        for (q = 0; q <= n0; q++) out.push(runFrom + inset + (span0 / n0) * q);
        return out;
      }

      var spanB = wB - wA;
      var nB = Math.max(1, Math.round(spanB / sp));
      var pitch = spanB / nB;
      for (q = 0; q <= nB; q++) out.push(wA + pitch * q);

      /* garageapt-v6 / v7: AN ORDINARY RAKE IS A GABLE LADDER on the end truss
         (GA_LADDER, drawn in _buildTrusses) -- no truss past that wall. A
         roof PULLED OUT over the gable (a porch, a carport, past
         GA_LADDER_MAX) keeps the ADU's trusses out to its edge, on the
         plates that run out with them (Marek 2026-10-11: negeneruji se
         vazniky u natazene strechy ve smeru stitu, vcetne pozednice) --
         each end on its own */
      var ldLo = _endStop(true), ldHi = _endStop(false);
      /* and out over the rake at the same pitch, both ends -- to the end
         member, which stays inside the facade at a small overhang
         (_rakeReach, the same line the plates stop on) */
      var loEdge = GF2.loEdge, hiEdge = GF2.hiEdge;
      var y2 = wA - pitch, lastLo = wA, lastHi = wB;
      if (!ldLo) { while (y2 > loEdge - 0.001) { out.push(y2); lastLo = y2; y2 -= pitch; } }
      y2 = wB + pitch;
      if (!ldHi) { while (y2 < hiEdge + 0.001) { out.push(y2); lastHi = y2; y2 += pitch; } }

      /* AND THE VERY END OF THE OVERHANG GETS ONE, WHATEVER THE PITCH SAYS.

         Stepping out at the module's own pitch stops at the last WHOLE bay, so
         the last stretch of rake -- whatever the overhang has left over -- had
         no member under it at either end. On the roof that is the piece hanging
         in the air, which is what he can see.

         The answer is the ordinary one on site: the last bay is short. A truss
         goes on the rake edge itself and the gap before it is whatever is left,
         never more than a full bay and never nothing. The module between the
         gables is untouched -- it is only this last one that closes up. */
      if (!ldLo && lastLo - loEdge > 0.05) out.push(loEdge);
      if (!ldHi && hiEdge - lastHi > 0.05) out.push(hiEdge);
      if (H.faces) out = H.faces(out, rakeA, rakeB, B, alongL, false, false);

      out.sort(function (p1, p2) { return p1 - p2; });
      return out;
    }

    function houseTerraceDeck(ctx, H) {
      /* what the product's copy read from its own scope; RANCH also hands in
         a terrace of its own (rect, td) and marks each tile (qtyPiece) */
      var API = H.api, MAGENTA = H.MAGENTA, TERR = H.TERR, _add = H.add, isNew = H.isNew,
          colorOf = H.colorOf, _screwPile = H.screwPile;
      var Rov = H.rect || null, Tdov = H.td || null, TL = H.layer;
      var _qp = function (b, k) { if (H.qtyPiece && b) b.userData.qtyPiece = k; return b; };
      var T = ctx.THREE, g = ctx.group;
      var R = Rov || _terraceRect();
      if (!R) { ctx.log('TER  none -- terrace off'); return; }
      var Td = Tdov || _terrace(), mag = isNew(H.newKey || TL);
      var yTop = API.storeys()[0].y0 - 0.02;   /* a step below the finished floor */
      var w = R.x1 - R.x0, l = R.z1 - R.z0, cx = (R.x0 + R.x1) / 2, cz = (R.z0 + R.z1) / 2;
      /* boards run along the house wall; on a free-standing terrace along its longer side */
      var along = Td.side ? (Td.side === 'z0' || Td.side === 'z1') : ((R.x1 - R.x0) >= (R.z1 - R.z0));
      var mBoard = new T.MeshStandardMaterial({ color: mag ? MAGENTA : colorOf('terrDeck'), roughness: 0.85 });
      var mJoist = new T.MeshStandardMaterial({ color: mag ? MAGENTA : 0xB8956A, roughness: 0.9 });
      var mPad = new T.MeshStandardMaterial({ color: mag ? MAGENTA : 0x9A9A96, roughness: 0.9 });
      var mPaver = new T.MeshStandardMaterial({ color: mag ? MAGENTA : colorOf('terrPavers'), roughness: 0.95 });
      var n = 0, i, j;
      function box(bw, bh, bd, x, y, z, m) { var b = new T.Mesh(new T.BoxGeometry(bw, bh, bd), m); b.position.set(x, y, z); b.name = 'terrace'; g.add(b); return b; }
      _asmMark(ctx, 'rooms.terrace', 'top', 'Terrace', (Td.surface === 'pavers' ? 'SFF00002' : TERR.board));
      if (Td.surface === 'pavers') {
        /* pavers on a bed, on the ground */
        /* whole tiles only: the terrace is snapped to the tile (500 metric,
           20 in imperial), so the rows come out whole */
        var tS = _paverSize();
        var yP = TERR.bedT, nx = Math.max(1, Math.round(w / tS)), nz = Math.max(1, Math.round(l / tS));
        var pw = tS, pl = tS;
        _asmMark(ctx, 'terrace_tiles', 'top', 'Concrete tiles', 'SFF00002');
        /* a tile with a 10 joint and chamfered edges: a body and a smaller top slab */
        var fromX = (Td.side === 'x0'), fromZ = (Td.side === 'z0');   /* lay from the house side out */
        for (i = 0; i < nx; i++) for (j = 0; j < nz; j++) {
          var x0t = fromX ? (R.x1 - pw * (i + 1)) : (R.x0 + pw * i), z0t = fromZ ? (R.z1 - pl * (j + 1)) : (R.z0 + pl * j);
          var x1t = Math.min(R.x1, Math.max(R.x0, x0t + pw)), z1t = Math.min(R.z1, Math.max(R.z0, z0t + pl));
          x0t = Math.max(R.x0, x0t); z0t = Math.max(R.z0, z0t);
          var tw = x1t - x0t, tl = z1t - z0t; if (tw < 0.03 || tl < 0.03) continue;
          var tx = (x0t + x1t) / 2, tz = (z0t + z1t) / 2, jt = 0.010, ch = 0.012;
          /* ranch-v84 (RANCH, qtyPiece): a tile a piece -- the 10 joint is closer than the audit's touch */
          _qp(box(Math.max(0.01, tw - jt), TERR.paverT - ch, Math.max(0.01, tl - jt), tx, yP + (TERR.paverT - ch) / 2, tz, mPaver), 'tt' + i + '-' + j);
          _qp(box(Math.max(0.01, tw - jt - 2 * ch), ch, Math.max(0.01, tl - jt - 2 * ch), tx, yP + TERR.paverT - ch / 2, tz, mPaver), 'tt' + i + '-' + j);
        }
        /* the layers under the tiles, to the tile field exactly: 150 of
           compacted gravel below grade on a geotextile, the 40 bed above it,
           and a concrete edge restraint round the open sides holding the bed */
        var fieldW = nx * pw, fieldL = nz * pl, fcx = R.x0 + fieldW / 2, fcz = R.z0 + fieldL / 2;
        var mGravel = new T.MeshStandardMaterial({ color: mag ? MAGENTA : 0xA9A39A, roughness: 1.0 });
        _asmMark(ctx, 'terrace_base', 'top', 'Gravel base', 'FEC00002');
        box(fieldW + 0.20, 0.15, fieldL + 0.20, fcx, -0.075, fcz, mGravel);
        box(fieldW, TERR.bedT, fieldL, fcx, TERR.bedT / 2, fcz, mPad);
        _asmMark(ctx, 'terrace_edge', 'top', 'Edge restraint', 'grd.concrete');
        var mEdge = new T.MeshStandardMaterial({ color: mag ? MAGENTA : 0x9A9A96, roughness: 0.9 }), edgeM = 0;
        if (Td.side !== 'z0') { box(fieldW + 0.10, 0.20, 0.05, fcx, 0.0, R.z0 - 0.025, mEdge); edgeM += fieldW; }
        if (Td.side !== 'z1') { box(fieldW + 0.10, 0.20, 0.05, fcx, 0.0, R.z0 + fieldL + 0.025, mEdge); edgeM += fieldW; }
        if (Td.side !== 'x0') { box(0.05, 0.20, fieldL, R.x0 - 0.025, 0.0, fcz, mEdge); edgeM += fieldL; }
        if (Td.side !== 'x1') { box(0.05, 0.20, fieldL, R.x0 + fieldW + 0.025, 0.0, fcz, mEdge); edgeM += fieldL; }
        var gravel = (fieldW + 0.20) * (fieldL + 0.20) * 0.15;
        _add(TL, 'SFF00002', nx * nz);
        _add(TL, 'FEC00002', gravel + fieldW * fieldL * TERR.bedT);
        _add(TL, 'grd.geotextile', (fieldW + 0.20) * (fieldL + 0.20));
        _add(TL, 'grd.concrete', edgeM * 0.05 * 0.20);
        ctx.log('TER  pavers ' + API.fmtArea(fieldW * fieldL) + ' on side ' + (Td.side || 'none (free-standing)') + ': ' + nx + ' x ' + nz + ' = ' + (nx * nz) + ' whole tiles ' + Math.round(tS * 1000) + ' (SFF00002), ' +
                Math.round((gravel + fieldW * fieldL * TERR.bedT) * 100) / 100 + ' m3 gravel base 150 below grade and bed 40 (FEC00002), geotextile ' + API.fmtArea((fieldW + 0.20) * (fieldL + 0.20)) +
                ', edge restraint ' + Math.round(edgeM * 10) / 10 + ' m concrete on the open sides');
        return;
      }
      /* THE DECK'S FRAME, the way a deck is built: supports in a grid at no
         more than 1.8 m, bearers on them, joists across on the bearers at 400,
         a rim joist round, and the boards over the lot with a 40 overhang. The
         supports follow the house: ground screws under a house on screws (the
         small 1200 x 76 one), a small precast footing under the others; on a
         crawl space the row at the house is a ledger on the stem wall. */
      var GRID = 1.80, RIM = TERR.joistT, OVER = 0.040;
      var fx0 = R.x0 + OVER, fx1 = R.x1 - OVER, fz0 = R.z0 + OVER, fz1 = R.z1 - OVER;   /* the frame's outside */
      var fw = fx1 - fx0, fl = fz1 - fz0;
      var yJ = yTop - TERR.boardT - TERR.joistD / 2;               /* joists */
      var yB = yJ - TERR.joistD;                                     /* bearers under them */
      /* A TERRACE AGAINST THE HOUSE SHARES ITS FOUNDATION (adu-v1367): the row of
         the frame at the wall is a ledger fixed to the house's own sill and
         carried by the house's footings -- whatever they are, screws, pads,
         piers or strips -- so no second line of supports stands a hand's width
         from the house's own (Marek: spolecna zakladova konstrukce). A
         free-standing terrace stands on its own supports all round. */
      var found = STATE.foundation, screws = (found === 'screws'), ledger = !!Td.side;
      /* THE SUPPORTS ARE THE BUILDING'S OWN KIND (adu-v1364). On screws: the house's
         ground screw FGR00004 with its U holder FGR00002 taking the bearer --
         the classic way, not the small deck screw. On pads, and on a cast base
         too: a precast pad FCO00009 with a U post base FCO00004 and a short 4x4
         post up to the bearer, not the deck block (Marek: u prefa zase prefa). */
      var supCode = screws ? 'FGR00004' : 'FCO00009';
      var supPostM = 0;
      /* bearers run parallel to the house wall (joists then run out from it) */
      var bearerRun = along ? fw : fl, bearerAcross = along ? fl : fw;
      var nBr = Math.max(2, Math.ceil(bearerAcross / GRID) + 1), stepBr = (bearerAcross - TERR.joistT) / (nBr - 1);
      var nSup = Math.max(2, Math.ceil(bearerRun / GRID) + 1), stepSup = (bearerRun - 0.3) / (nSup - 1);
      /* WITH A PERGOLA THE SUPPORTS FOLLOW ITS POSTS: along each bearer they
         stand at the posts' stations and exactly half way between them. On the
         two edge bearers the post itself carries the bearer at its station, so
         there only the half-way support is added (Marek). The stations are the
         core pergola's own: posts at no more than 3 m, 50 in from the ends. */
      /* adu-v1430 / ranch-v24: only a pergola that stands over this terrace */
      var PGo = API.pergolaRect ? API.pergolaRect() : null;
      var pergOn = !!(PGo && !PGo.own && Td.pergola && Td.pergola !== 'none' && !_terraceSheltered());
      var vStations = null, postStations = {};
      if (pergOn) {
        var runLo = along ? R.x0 : R.z0, runHi = along ? R.x1 : R.z1, LL = runHi - runLo;
        var nP = Math.max(2, Math.ceil((LL - 0.10) / 3.0) + 1), pa0 = runLo + 0.05, pa1 = runHi - 0.05, qp;
        vStations = [];
        for (qp = 0; qp < nP; qp++) {
          var pa = pa0 + (pa1 - pa0) * (qp / (nP - 1));
          vStations.push({ v: pa, post: true });
          if (qp < nP - 1) vStations.push({ v: pa + (pa1 - pa0) / (nP - 1) / 2, post: false });
        }
      }
      var halfWay = 0, atPosts = 0;
      var bm = 0, sups = 0, mSup = screws ? new T.MeshStandardMaterial({ color: mag ? MAGENTA : 0x8E9296, roughness: 0.5, metalness: 0.7 }) : mPad;
      for (i = 0; i < nBr; i++) {
        var u = (along ? fz0 : fx0) + TERR.joistT / 2 + stepBr * i;
        var atHouse = ledger && ((Td.side === 'z1' && i === 0) || (Td.side === 'z0' && i === nBr - 1) || (Td.side === 'x1' && i === 0) || (Td.side === 'x0' && i === nBr - 1));
        _asmMark(ctx, 'terrace_frame', 'top', 'Terrace frame', TERR.joist);
        if (along) box(bearerRun, TERR.joistD, TERR.joistT, cx, yB + TERR.joistD / 2, u, mJoist); else box(TERR.joistT, TERR.joistD, bearerRun, u, yB + TERR.joistD / 2, cz, mJoist);
        bm += bearerRun;
        if (atHouse) continue;   /* the ledger side: bolted to the stem wall, no supports */
        var s2;
        _asmMark(ctx, 'terrace_supports', 'top', 'Terrace supports', supCode);
        var edgeBr = (i === 0 || i === nBr - 1);
        var nHere = vStations ? vStations.length : nSup;
        for (s2 = 0; s2 < nHere; s2++) {
          var st2 = vStations ? vStations[s2] : null;
          /* the pergola's post carries the edge bearer at its own station */
          if (st2 && st2.post && edgeBr) { atPosts++; continue; }
          var v = st2 ? st2.v : ((along ? fx0 : fz0) + 0.15 + stepSup * s2), sx = along ? v : u, sz = along ? u : v, topY = yB;
          if (screws) {
            /* the house's screw, with its U holder under the bearer */
            _screwPile(T, g, mSup, sx, sz, topY - 0.09, { r: 0.038, len: 1.20 });
            box(0.12, 0.09, 0.012, sx, topY - 0.045, sz - 0.06, mSup);
            box(0.12, 0.09, 0.012, sx, topY - 0.045, sz + 0.06, mSup);
          } else {
            /* the precast pad, 400 x 400, a U post base and a short 4x4 to the bearer */
            box(0.40, 0.15, 0.40, sx, 0.05 - 0.075, sz, mSup);
            box(0.12, 0.006, 0.12, sx, 0.053, sz, mSup);
            var postH = topY - 0.056;
            if (postH > 0.03) { box(0.10, postH, 0.10, sx, 0.056 + postH / 2, sz, mJoist); supPostM += postH; }
          }
          if (st2) halfWay += st2.post ? 0 : 1;
          sups++;
        }
      }
      /* joists at 400 across the bearers, and the rim round the frame */
      _asmMark(ctx, 'terrace_frame', 'top', 'Terrace frame', TERR.joist);
      var jRun = along ? fl : fw, jAcross = along ? fw : fl;
      var nJ = Math.max(2, Math.ceil((jAcross - TERR.joistT) / 0.40) + 1), stepJ = (jAcross - TERR.joistT) / (nJ - 1), jm = 0;
      for (i = 0; i < nJ; i++) {
        var uj = (along ? fx0 : fz0) + TERR.joistT / 2 + stepJ * i;
        if (along) box(TERR.joistT, TERR.joistD, jRun, uj, yJ, cz, mJoist); else box(jRun, TERR.joistD, TERR.joistT, cx, yJ, uj, mJoist);
        jm += jRun;
      }
      if (along) { box(fw, TERR.joistD, RIM, cx, yJ, fz0 + RIM / 2, mJoist); box(fw, TERR.joistD, RIM, cx, yJ, fz1 - RIM / 2, mJoist); jm += 2 * fw; }
      else { box(RIM, TERR.joistD, fl, fx0 + RIM / 2, yJ, cz, mJoist); box(RIM, TERR.joistD, fl, fx1 - RIM / 2, yJ, cz, mJoist); jm += 2 * fl; }
      /* the boards, along the wall, with a gap, over the whole terrace (the overhang) */
      _asmMark(ctx, 'decking', 'top', 'Decking boards', TERR.board);
      var bRun = along ? w : l, bAcross = along ? l : w, nB = Math.floor((bAcross + TERR.gap) / (TERR.boardW + TERR.gap));
      var start = (along ? R.z0 : R.x0) + (bAcross - (nB * TERR.boardW + (nB - 1) * TERR.gap)) / 2 + TERR.boardW / 2;
      for (i = 0; i < nB; i++) {
        var q = start + i * (TERR.boardW + TERR.gap);
        if (along) box(bRun, TERR.boardT, TERR.boardW, cx, yTop - TERR.boardT / 2, q, mBoard); else box(TERR.boardW, TERR.boardT, bRun, q, yTop - TERR.boardT / 2, cz, mBoard);
        n++;
      }
      _add(TL, TERR.board, w * l);
      _add(TL, TERR.joist, jm + bm);
      if (screws) { _add(TL, 'FGR00001', sups); _add(TL, 'steel.saddle', sups); }
      else { _add(TL, 'grd.padBlock', sups); _add(TL, 'FCO00004', sups); if (supPostM > 0) _add(TL, 'KVH10100', Math.round(supPostM * 100) / 100); }
      /* what holds it: two stainless screws at every board over every joist,
         two screws at every joist over every bearer, a hanger at every joist
         end on the ledger */
      _add(TL, 'FAS55050', Math.max(0, n) * Math.max(1, nJ) * 2);
      _add(TL, 'FAS60100', Math.max(1, nJ) * Math.max(1, nBr) * 2);
      if (ledger) {
        _add(TL, 'FAT00011', Math.max(1, nJ));
        /* the ledger's own fixing into the house sill: a washer head screw every 400 */
        _add(TL, 'FAS80200', Math.ceil(bearerRun / 0.40) + 1);
      }
      ctx.log('TER  decking ' + API.fmtArea(w * l) + ' on side ' + (Td.side || 'none (free-standing)') + ': ' + nB + ' boards ' + Math.round(TERR.boardW * 1000) + 'x' + Math.round(TERR.boardT * 1000) +
              ' (' + TERR.board + ' by the m2, 40 overhang), ' + nJ + ' joists at 400 + rim ' + Math.round(jm * 10) / 10 + ' m, ' + nBr + ' bearers ' + Math.round(bm * 10) / 10 + ' m (' + TERR.joist + '), ' +
              sups + ' support(s) ' + (screws ? 'FGR00001 ground screw 1200x76 + FGR00002 U holder' : 'FCO00009 precast pad + FCO00004 U post base + 4x4 post ' + (Math.round(supPostM * 100) / 100) + ' m KVH10100') +
              (pergOn ? ('; with the pergola: ' + halfWay + ' half way between its posts, ' + atPosts + ' station(s) carried by the posts themselves') : '') +
              (ledger ? (', ledger on the house sill at wall ' + Td.side + ' -- the house foundation carries that row, ' + (Math.ceil(bearerRun / 0.40) + 1) + ' x FAS80200 into the sill') : ', free-standing: supports all round'));
    }

    /* =========================================================================
       THE LIGHTNING PROTECTION, THE FITOUT PICKER AND THE SNAPSHOT (core-v810,
       status/modules.md 13). The last three of the house line's shared texts:
         houseLps          the lightning protection -- air terminals, the ridge
                           conductor, the downs at opposite corners, guards,
                           ground rods, the bill under the product's layer
                           (BARNDO: edgeOut, the cable over the roof edge)
         houseOpenFitouts  the FITOUT picker of the plan's pieces
         houseSnapshot     the snapshot's product part (SAUNA: head, the room
                           words; TAURUS: scene, what is on screen)
       Text: the ADU's; the lightning protection BARNDO's (the ADU's + the edge).
       ========================================================================= */
    function houseLps(ctx, H) {
      /* what the product's copy read from its own scope; its bill under its own
         layer (H.layer); the cable is carried 90 mm over the roof edge before it
         drops (barndo-v34, every product since core-v813; edgeOut: false keeps
         the plumb drop from the plane's corner) */
      var API = H.api, EAVE_POST = H.EAVE_POST, LPS = H.LPS, MAGENTA = H.MAGENTA, _add = H.add,
          _billReset = H.billReset, _covDef = H.covDef, _covTop = H.covTop, _lpsOn = H.lpsOn,
          _postPts = H.postPts, _skinLift = H.skinLift, _wallSkin = H.wallSkin, isNew = H.isNew, TL = H.layer;
      _billReset(TL);
      if (!_lpsOn()) { ctx.log('LPS  off'); return; }
      var T = ctx.THREE, g = ctx.group, R = API.STATE.roof || {}, i, k;
      var planes = ctx.roof || API.roofPlanes() || [];
      if (!planes.length) { ctx.log('LPS  none -- no roof planes'); return; }
      var mag = isNew(TL);
      var mCu = new T.MeshStandardMaterial({ color: mag ? MAGENTA : 0xB87333, roughness: 0.45, metalness: 0.7 });
      var mBr = new T.MeshStandardMaterial({ color: mag ? MAGENTA : 0x8A6A3A, roughness: 0.5, metalness: 0.6 });
      var mGd = new T.MeshStandardMaterial({ color: mag ? MAGENTA : 0x555A5E, roughness: 0.8, metalness: 0.2 });
      var tally = { term: 0, cable: 0, clips: 0, conn: 0, downs: 0, rods: 0, bond: 0, guard: 0 };

      function tube(a, b, r, m, nm) {
        var dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
        var L = Math.hypot(dx, dy, dz);
        if (!(L > 0.01)) return 0;
        var mesh = new T.Mesh(new T.CylinderGeometry(r, r, L, 8), m);
        mesh.quaternion.setFromUnitVectors(new T.Vector3(0, 1, 0), new T.Vector3(dx / L, dy / L, dz / L));
        mesh.position.set((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2);
        mesh.name = nm;
        g.add(mesh);
        return L;
      }
      function lerp(a, b, t) { return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t]; }
      /* a ball at every bend so the run reads as one wire with no gap at the corners */
      function joint(p) {
        var j = new T.Mesh(new T.SphereGeometry(LPS.cableD * 0.6, 8, 6), mCu);
        j.position.set(p[0], p[1], p[2]); j.name = 'lps-cable'; g.add(j);
      }
      function key(p) { return Math.round(p[0] * 100) + ',' + Math.round(p[1] * 100) + ',' + Math.round(p[2] * 100); }
      function same(p, q) { return Math.hypot(p[0] - q[0], p[1] - q[1], p[2] - q[2]) < 0.02; }

      /* the height of the finished roof over a plane point: deck lift and the
         covering, plumb, plus the cable's own stand-off */
      function lift(pl) {
        var pitch = (pl.pitch || 0) * Math.PI / 180;
        var slope = 1;
        return (_skinLift() + _covTop(slope) + LPS.cableLift) / Math.max(0.2, Math.cos(pitch));
      }

      /* THE LINES THE CABLE RUNS: ridges (terminals live on these) and the
         roof edges that join a ridge end to an eave corner (rakes, hips). On a
         flat roof the parapet top all round is the ridge. */
      var ridges = [], edges = [], seen = {};
      var flat = (R.type === 'flat');
      if (flat) {
        var B0 = _buildRect(), SEG0 = API.segments ? API.segments() : [];
        var rise = 0;
        try { var G0 = SEG0.length ? _wallSkin(SEG0[0]) : null; rise = G0 ? G0.rise : 0; } catch (eG) { rise = 0; }
        var yP = API.wallTop() + rise + LPS.cableLift;
        /* a flat roof: the conductor is a closed loop round the parapet, with
           a rod at every corner and one every 20 ft along the edges (NFPA 780) */
        var c4 = [[B0.x0, yP, B0.z0], [B0.x1, yP, B0.z0], [B0.x1, yP, B0.z1], [B0.x0, yP, B0.z1]];
        for (k = 0; k < 4; k++) ridges.push([c4[k], c4[(k + 1) % 4]]);
      } else {
        for (i = 0; i < planes.length; i++) {
          var pl = planes[i], lf = lift(pl), rd = pl.ridge, ev = pl.eave, P = pl.pts || [];
          if (rd && rd.length >= 2 && Math.hypot(rd[1][0] - rd[0][0], rd[1][2] - rd[0][2]) > 0.2) {
            var a = [rd[0][0], rd[0][1] + lf, rd[0][2]], b = [rd[1][0], rd[1][1] + lf, rd[1][2]];
            var kk = key(lerp(a, b, 0.5));
            if (!seen[kk]) { seen[kk] = 1; ridges.push([a, b]); }
          }
          /* the plane's edges that are neither the eave nor the ridge */
          for (k = 0; k < P.length; k++) {
            var p0 = P[k], p1 = P[(k + 1) % P.length];
            var isEave = ev && ((same(p0, ev[0]) && same(p1, ev[1])) || (same(p0, ev[1]) && same(p1, ev[0])));
            var isRidge = rd && rd.length >= 2 && ((same(p0, rd[0]) && same(p1, rd[1])) || (same(p0, rd[1]) && same(p1, rd[0])));
            if (isEave || isRidge) continue;
            if (Math.hypot(p1[0] - p0[0], p1[2] - p0[2]) < 0.2) continue;
            var e0 = [p0[0], p0[1] + lf, p0[2]], e1 = [p1[0], p1[1] + lf, p1[2]];
            var ke = key(lerp(e0, e1, 0.5));
            if (!seen[ke]) { seen[ke] = 1; edges.push([e0, e1]); }
          }
        }
      }

      /* AIR TERMINALS on the ridges: one 600 in from each end, the rest at most
         6 m apart between them */
      var termPts = [];
      for (i = 0; i < ridges.length; i++) {
        var ra = ridges[i][0], rb = ridges[i][1];
        var L = Math.hypot(rb[0] - ra[0], rb[1] - ra[1], rb[2] - ra[2]);
        var inner = Math.max(0, L - 2 * LPS.termEnd);
        var n = Math.max(1, Math.ceil(inner / LPS.termMax));
        for (k = 0; k <= n; k++) {
          var t = (LPS.termEnd + inner * k / n) / L;
          if (L < 2 * LPS.termEnd) t = 0.5;
          var p = lerp(ra, rb, t);
          var kt = key(p);
          if (seen['T' + kt]) continue;
          seen['T' + kt] = 1;
          termPts.push(p);
          if (L < 2 * LPS.termEnd) break;
        }
        _asmCode(ctx, LPS.cable, 'top');
        tally.cable += tube(ra, rb, LPS.cableD / 2, mCu, 'lps-cable');
        joint(ra); joint(rb);
      }
      for (i = 0; i < termPts.length; i++) {
        var tp = termPts[i];
        _asmCode(ctx, LPS.rod, 'top');
        var base = new T.Mesh(new T.BoxGeometry(LPS.baseW, 0.012, LPS.baseW), mBr);
        base.position.set(tp[0], tp[1] + 0.006, tp[2]);
        base.name = 'lps-terminal';
        g.add(base);
        _asmCode(ctx, LPS.rod, 'top');
        tube([tp[0], tp[1] + 0.012, tp[2]], [tp[0], tp[1] + 0.012 + LPS.rodH, tp[2]], LPS.rodD / 2, mCu, 'lps-terminal');
        tally.term++;
        tally.conn++;   /* a connector at every base */
      }

      /* TWO WAYS DOWN, AS FAR APART AS THE HOUSE ALLOWS (NFPA 780: not fewer
         than two down conductors). Of the roof edges that leave a ridge end
         (rakes, hips) the two that reach the most distant pair of eave corners
         carry the cable; the other edges carry nothing. On a flat roof the loop
         already reaches every corner and the two down conductors take opposite
         corners of it. */
      var cands = [], q;
      if (flat) {
        for (i = 0; i < 4; i++) cands.push({ edge: null, corner: ridges[i][0] });
      } else {
        for (i = 0; i < edges.length; i++) {
          var eA = edges[i][0], eB = edges[i][1];
          var hiE = eA[1] >= eB[1] ? eA : eB, loE = eA[1] >= eB[1] ? eB : eA;
          var atRidge = false, rq;
          for (rq = 0; rq < ridges.length && !atRidge; rq++) {
            if (same(hiE, ridges[rq][0]) || same(hiE, ridges[rq][1])) atRidge = true;
          }
          if (!atRidge) continue;
          cands.push({ edge: [hiE, loE], corner: loE });
        }
      }
      var pick = [];
      if (cands.length >= 2) {
        var best = -1, bi = 0, bj = 1;
        for (i = 0; i < cands.length; i++) for (q = i + 1; q < cands.length; q++) {
          var d2 = Math.hypot(cands[i].corner[0] - cands[q].corner[0], cands[i].corner[2] - cands[q].corner[2]);
          if (d2 > best) { best = d2; bi = i; bj = q; }
        }
        pick = [cands[bi], cands[bj]];
      } else if (cands.length === 1) pick = [cands[0]];

      var B = _buildRect();
      for (i = 0; i < pick.length; i++) {
        var c = pick[i].corner;
        if (pick[i].edge) {
          _asmCode(ctx, LPS.cable, 'side');
          tally.cable += tube(pick[i].edge[0], pick[i].edge[1], LPS.cableD / 2, mCu, 'lps-cable');
          joint(pick[i].edge[1]);
          tally.conn++;   /* the corner */
          if (H.edgeOut !== false) {
            /* barndo-v34: OVER THE EDGE, NOT THROUGH IT. The corner is the roof
               plane's own corner; the steel, the eave drip and the verge stand
               40 to 80 mm past it, and the cable dropped plumb from it through
               all three (the exploded view lifted the roof through the cable).
               It runs on 90 mm past the rake, square to it -- on a hip along
               the hip, past both eaves -- and drops there. */
            var ed = pick[i].edge, ex0 = ed[1][0] - ed[0][0], ez0 = ed[1][2] - ed[0][2], eL0 = Math.hypot(ex0, ez0) || 1;
            var nx0 = ex0 / eL0, nz0 = ez0 / eL0;
            if (R.type !== 'hip') {
              var Bc = _buildRect(), mx0 = (Bc.x0 + Bc.x1) / 2, mz0 = (Bc.z0 + Bc.z1) / 2;
              nx0 = -ez0 / eL0; nz0 = ex0 / eL0;
              if (nx0 * (c[0] - mx0) + nz0 * (c[2] - mz0) < 0) { nx0 = -nx0; nz0 = -nz0; }
            }
            var eO = (LPS.edgeOut != null) ? LPS.edgeOut : 0.090;   /* core-v813: BARNDO's 90 mm where a product's LPS has none */
            var cO = [c[0] + nx0 * eO, c[1], c[2] + nz0 * eO];
            tally.cable += tube(c, cO, LPS.cableD / 2, mCu, 'lps-cable');
            joint(cO);
            c = cO;
          }
        }
        /* STRAIGHT DOWN FROM THE ROOF CORNER. The cable used to jog in to the
           wall under the eave and the jog ran inside the roof edge, so the run
           read as broken there (Marek). It drops plumb from the corner of the
           roof itself -- down the post where an extended roof has one -- to
           the ground, guard over the bottom 2.4 m, then out to the rod 600
           further from the building, 2.4 m into the ground, drawn while the
           base is open like everything below grade. */
        var sx = (Math.abs(c[0] - B.x0) < Math.abs(c[0] - B.x1)) ? -1 : 1;
        var sz = (Math.abs(c[2] - B.z0) < Math.abs(c[2] - B.z1)) ? -1 : 1;
        var wx = c[0], wz = c[2];
        /* ALONG THE POST WHERE THERE IS ONE. The eave posts stand 150 in from
           the roof edge, so a cable dropping from the corner hung in the air
           beside the post (Marek). If a post stands within a metre of this
           corner the cable runs down its outer face instead: a short jog from
           the corner to the post, then plumb. */
        var pAt = null, pq2, pd = 1.0;
        if (_postPts && _postPts.length) {
          for (pq2 = 0; pq2 < _postPts.length; pq2++) {
            var dq = Math.hypot(_postPts[pq2].x - c[0], _postPts[pq2].z - c[2]);
            if (dq < pd) { pd = dq; pAt = _postPts[pq2]; }
          }
        }
        var top = c;
        if (pAt) {
          wx = pAt.x + sx * (EAVE_POST.w / 2 + LPS.cableD / 2);
          wz = pAt.z + sz * (EAVE_POST.w / 2 + LPS.cableD / 2);
          var jog = [wx, c[1] - 0.02, wz];
          tally.cable += tube(c, jog, LPS.cableD / 2, mCu, 'lps-cable');
          joint(jog);
          top = jog;
        } else {
          /* NO POST: the cable cannot hang in the air under an overhang. From
             the roof corner it drops to just under the eave, runs in along the
             underside of the overhang to the wall corner outside the facade,
             and only then goes down the wall (Marek). */
          var fT = API.facadeThk() + 0.03;
          var wxW = (sx < 0 ? B.x0 : B.x1) + sx * fT, wzW = (sz < 0 ? B.z0 : B.z1) + sz * fT;
          var under = c[1] - (_skinLift() + LPS.cableLift + 0.05) / Math.max(0.2, Math.cos(((planes[0] && planes[0].pitch) || 0) * Math.PI / 180));
          var overhang = Math.hypot(wxW - c[0], wzW - c[2]);
          if (overhang > 0.12) {
            var p1 = [c[0], under, c[2]], p2 = [wxW, under, wzW];
            tally.cable += tube(c, p1, LPS.cableD / 2, mCu, 'lps-cable');
            joint(p1);
            tally.cable += tube(p1, p2, LPS.cableD / 2, mCu, 'lps-cable');
            joint(p2);
            tally.conn++;
            wx = wxW; wz = wzW; top = p2;
          }
        }
        var foot = [wx, 0, wz];
        tally.cable += tube(top, foot, LPS.cableD / 2, mCu, 'lps-cable');
        joint(foot);
        var gTop = Math.min(top[1], LPS.guardL);
        tube([wx, 0, wz], [wx, gTop, wz], LPS.guardD / 2, mGd, 'lps-guard');
        tally.guard++;
        var rx = wx + sx * LPS.groundOut, rz = wz + sz * LPS.groundOut;
        _asmCode(ctx, LPS.ground, 'bottom');   /* the earth rod: from below */
        tally.cable += tube(foot, [rx, -0.02, rz], LPS.cableD / 2, mCu, 'lps-cable');
        if (_footFull()) tube([rx, 0.05, rz], [rx, -LPS.groundL, rz], LPS.groundD / 2, mCu, 'lps-ground');
        else tube([rx, 0.05, rz], [rx, -0.05, rz], LPS.groundD / 2, mCu, 'lps-ground');
        var clamp = new T.Mesh(new T.BoxGeometry(0.04, 0.03, 0.04), mBr);
        clamp.position.set(rx, 0.03, rz); clamp.name = 'lps-ground';
        g.add(clamp);
        tally.rods++;
        tally.downs++;
        tally.conn++;
      }
      if (!pick.length) ctx.log('LPS  no route down -- no roof edge leaves a ridge end');

      tally.clips = Math.ceil(tally.cable / LPS.clipStep);
      var covK = _covDef().key;
      tally.bond = (covK === 'trapez' || covK === 'seam') ? planes.length : 0;

      /* THE BILL, OR THE LOG WHERE THE ROW IS STILL TO COME */
      function row(code, qty, what, unit) {
        if (!(qty > 0)) return;
        if (code) { _add(TL, code, qty); }
        else ctx.log('LPS  NOT BILLED ' + what + ': ' + (unit === 'm' ? (Math.round(qty * 10) / 10) + ' m' : qty + ' pcs'));
      }
      row(LPS.rod, tally.term, 'air terminals', 'pcs');
      row(LPS.cable, tally.cable, 'conductor cable', 'm');
      row(LPS.clip, tally.clips, 'conductor clips', 'pcs');
      row(LPS.conn, tally.conn, 'connectors', 'pcs');
      row(LPS.ground, tally.rods, 'ground rods', 'pcs');
      row(LPS.gclamp, tally.rods, 'ground rod clamps', 'pcs');
      row(LPS.guard, tally.guard, 'down conductor guards', 'pcs');
      row(LPS.bond, tally.bond, 'bonding clamps', 'pcs');
      ctx.log('LPS  ' + tally.term + ' air terminal(s) on ' + ridges.length + ' ridge line(s), ' +
              (Math.round(tally.cable * 10) / 10) + ' m of conductor (ridge, one edge down at each end, ' +
              tally.downs + ' down conductor(s) at opposite corners -- NFPA 780), ' + tally.clips + ' clip(s) at ' +
              Math.round(LPS.clipStep * 1000) + ', ' + tally.conn + ' connector(s), ' + tally.rods +
              ' ground rod(s) ' + Math.round(LPS.groundL * 1000) + ' long ' + Math.round(LPS.groundOut * 1000) +
              ' out from the footing, ' + tally.guard + ' guard(s), ' + tally.bond + ' bond(s) (' + covK + ' roof)' +
              ' -- LPS00001..8 billed to ' + TL);
    }

    function houseOpenFitouts(groups, API) {
      var U = _UI(), PL = _P();
      if (!U || !U.picker) { _log('fitouts: shared picker missing'); return; }
      if (!PL || !PL.fixtures) { _log('fitouts: floorplan asset without fixtures (needs v60)'); return; }
      var FX = PL.fixtures, cat = FX.catalog(), keys = FX.order(), items = [], i;
      for (i = 0; i < keys.length; i++) {
        var D = cat[keys[i]];
        if (!D) continue;
        if (groups && groups.length) {
          var gD = D.grp === 'living' && /^table/.test(keys[i]) ? 'dining' : D.grp;
          if (groups.indexOf(gD) < 0 && groups.indexOf(D.grp) < 0) continue;
        }
        items.push({ key: keys[i], sub: _t(D.i18n, D.def) + (D.sub ? (' ' + D.sub) : ''),
                     w: D.w, h: D.d, glyph: FX.glyph(keys[i]), glyphVb: FX.glyphVb ? FX.glyphVb(keys[i]) : '24 24',
                     code: fmtLen(D.w) + ' x ' + fmtLen(D.d) });
      }
      U.picker({
        title: _cap(_t('container_int_fitout', 'FITOUT')),
        items: items,
        allLabel: _t('all', 'All'),
        fmt: fmtLen,
        noFilter: true,
        onPick: function (it) {
          var at = __CORE._fitAt; __CORE._fitAt = null;
          var rm = __CORE._fitRoom;
          var f = at ? FX.add(it.key, at.x, at.z)
                : (rm ? FX.add(it.key, null, null, rm.x, rm.z) : FX.add(it.key));
          if (f && at && FX.moveTo) FX.moveTo(f, f.x, f.z);   /* snap to the wall there */
          if (!f) { _log('fitout: nothing placed for ' + it.key); return; }
          _log('fitout placed: ' + it.key + ' at ' + f.x.toFixed(2) + ',' + f.z.toFixed(2) + ' rot ' + f.rot);
          __CORE._fitLast = f;
          savePref(); API.rebuild(); drawPlan();
          fitEdit(true);
        }
      });
    }

    /* --------------------------------------------------------------------------
       SNAPSHOT. The core's report, plus what only this product knows -- and above
       all, WHAT IS STILL A STUB. While most of the interface is scaffolding, the
       most useful line in the whole snapshot is the one saying which parts are
       scaffolding.
       -------------------------------------------------------------------------- */
    function houseSnapshot(H) {
      /* what the product's copy read from its own scope; head = SAUNA's room
         words, scene = TAURUS's probe of what is on screen (with state in FOCUS) */
      var API = H.api, VERSION = H.VERSION, LIQUID_MIN = H.LIQUID_MIN, VIDEO_ID = H.VIDEO_ID,
          _stubs = H.stubs, _stubNames = H.stubNames, wizSteps = H.wizSteps;
      var out = [], i, el;
      out.push('===== ' + VERSION + ' on ' + API.CORE_VERSION + ' =====');
      if (H.head) H.head(out);
      var B = BOOTDATA();
      /* LOUD WHEN THE TWO HALVES DISAGREE. Three rounds were spent fixing a card
         in a liquid that was not on the page, because the version was one quiet
         word in the middle of a long report. */
      var lq = String(B.liquidVersion || '');
      var lqn = parseInt((lq.match(/v(\d+)/) || [0, 0])[1], 10) || 0;
      if (lqn < LIQUID_MIN) {
        out.push('!! LIQUID TOO OLD: page has ' + (lq || 'nothing') +
                 ', this lib expects house-liquid-v' + LIQUID_MIN + ' or newer.');
        out.push('!! Anything below about the rail, the card or the wizard may be');
        out.push('!! describing a page that is not the one you deployed.');
      }
      try {
        var elCs = $('cf-btn-colors');
        out.push('rail cut   ' + (elCs ? ('pressed=' + elCs.getAttribute('aria-pressed') + ' glyph=' +
                 (elCs.firstElementChild ? elCs.firstElementChild.tagName.toLowerCase() + '.' +
                  (elCs.firstElementChild.className || '') : 'none')) : 'MISSING'));
      } catch (eCs) {}
      out.push('cut        ' + (function () {
        var cs = __CORE._cutTool ? __CORE._cutTool.state() : null;
        if (!cs || !cs.on) return 'off';
        return 'ON axis=' + cs.axis + ' t=' + Math.round(cs.t * 100) + '% ' + (cs.flip ? 'flipped' : '') +
               (cs.box ? (' box ' + Math.round((cs.box.max[cs.axis] - cs.box.min[cs.axis]) * 1000) + ' mm along ' + cs.axis) : '') +
               ' | ' + cs.nMat + ' material(s) clipped, ' + cs.nStencil + ' mesh(es) in the stencil, ' + cs.caps + ' colour cap(s)';
      })());
      out.push('share      ' + (__CORE._shareId ? ('?p=' + __CORE._shareId + ' on ' + _shareWorker()) : ('no short id yet (worker ' + _shareWorker() + ')')));
      /* THE SCALE AGAINST THE MAP: metres per pixel from the ortho camera and
         from the map's zoom at the site's latitude; equal is right */
      /* THE ASSEMBLY WALK, step by step: order, layer, item, direction, bodies */
      try {
        var SA = _asm2.steps.length ? _asm2.steps : _asmCollect(), sa;
        /* THE STEP LIST IS 79 LINES AND HE CANNOT SELECT PART OF A LOG.

           The snapshot is copied whole, so every line in it costs him. The
           step list is worth printing when the assembly is what we are working
           on and dead weight otherwise -- so it prints a count, and the whole
           list only when 'assembly' is in FOCUS (Marek: tak ho udelej kratsi).

           Set FOCUS to include the word assembly to get it back. */
        var wantSteps = (String(__CORE.FOCUS || '').indexOf('assembly') >= 0) || !__CORE.FOCUS;
        out.push('assembly   ' + (SA.length + 2) + ' step(s)' +
                 (wantSteps ? ': 1 = exploded view, 2 = whole building, then:' : ' (add assembly to FOCUS to list them)'));
        for (sa = 0; wantSteps && sa < SA.length; sa++) {
          out.push('  ' + (sa + 3) + '. [' + SA[sa].layer.replace(/^[a-z]+-/, '') + '] ' + _asmStepLabel(SA[sa]) + (SA[sa].code ? ' (' + SA[sa].code + ')' : ' (no code)') + ' -- ' + SA[sa].dir + ', ' + SA[sa].meshes.length + ' bodies');
        }
      } catch (eSA) { out.push('assembly   list threw ' + (eSA && eSA.message)); }
      out.push('NOT BILLED ' + (__CORE._notBilled.length ? (__CORE._notBilled.length + ' code(s) drawn and priced against nothing: ' + __CORE._notBilled.join(', ')) : 'none -- every billed code has a catalogue entry'));
      /* THE STATE BLOCK IS BEHIND A WORD NOW.
         Twenty-two lines of it print whatever FOCUS says -- the map, the
         wizard, the rail, the stubs -- and he copies the log whole, so every
         one of them costs him. They matter when the subject IS the viewport or
         the wizard; the rest of the time they are noise. Put 'state' in FOCUS
         to get them back (Marek: udelej logy kratsi at se daji posilat).
      */
      if (String(__CORE.FOCUS || '').indexOf('state') >= 0 || !__CORE.FOCUS) {
      out.push('plantaps   ' + (function () { try { return 'part ' + _planTaps.part + ', door ' + _planTaps.door + ', last: ' + _planTaps.last; } catch (e) { return '?'; } })() + ' | editing layout=' + editing('layout') + ' fitEdit=' + !!__CORE._fitEdit + ' doorTool=' + !!STATE.doorTool);
      out.push('pan2d      pointerdowns seen ' + _pan2D.downs + ', taken ' + _pan2D.taken + ', moves ' + _pan2D.moves + ' | last verdict: ' + _pan2D.why + ' | mapLocked=' + !!STATE.mapLocked);
      out.push('map        ' + (function () {
        try {
          var st = _stage(), cam = st && st.orthoCam, M = __CORE._MM;
          if (!M || !M.map || !cam) return 'no map or no ortho camera';
          var lat = (M.center && M.center[1]) || 0, K = 156543.03392 * Math.cos(lat * Math.PI / 180);
          var rd = st.renderer && st.renderer.domElement, cw = (rd && rd.clientWidth) || 0, ww = ($('cf-root') || {}).clientWidth || 0;
          var frustumW = (cam.right - cam.left) / (cam.zoom || 1), mppCam = cw ? frustumW / cw : 0;
          var z = M.map.getZoom(), mppMap = K / Math.pow(2, z);
          var cl = M._zoomClamped ? (' | CLAMPED asked z=' + M._zoomClamped.asked.toFixed(2) + ' got ' + M._zoomClamped.got + ' (pulled back x' + M._zoomClamped.pulledBack.toFixed(2) + ')') : '';
          var gb = '';
          try { var bx = new global.THREE.Box3().setFromObject(st.group); gb = ' | group box x ' + bx.min.x.toFixed(1) + '..' + bx.max.x.toFixed(1) + ' z ' + bx.min.z.toFixed(1) + '..' + bx.max.z.toFixed(1) + ' | target ' + st.orthoControls.target.x.toFixed(1) + ',' + st.orthoControls.target.z.toFixed(1); } catch (eG) {}
          try { gb += ' | rendering ' + (st.camera === st.orthoCam ? 'ORTHO' : (st.camera === st.persCam ? 'PERSPECTIVE' : 'other')) + ' | pers controls ' + (st.persControls && st.persControls.enabled ? 'ON' : 'off') + ', ortho controls ' + (st.orthoControls && st.orthoControls.enabled ? 'ON' : 'off') + ' | ortho pos y ' + st.orthoCam.position.y.toFixed(1) + ' rot ' + st.orthoCam.rotation.x.toFixed(2) + ',' + st.orthoCam.rotation.y.toFixed(2); } catch (eG2) {}
          return 'view=' + STATE.view + ' zoom=' + z.toFixed(2) + ' | camera ' + mppCam.toFixed(4) + ' m/px (frustum ' + frustumW.toFixed(1) + ' m over canvas ' + cw + ' px, root ' + ww + ' px) | map ' +
                 mppMap.toFixed(4) + ' m/px | ratio cam/map ' + (mppMap ? (mppCam / mppMap).toFixed(3) : '?') + cl + gb;
        } catch (e) { return 'threw ' + (e && e.message); }
      })());
      out.push('fitout     mode ' + (__CORE._fitEdit ? 'ON' : 'off') + ' | last drag: ' + (__CORE._fitDiag || 'none') +
               ' | rooms ' + (function () { try { var PLd = _P(); var R = PLd && PLd.rooms ? PLd.rooms() : []; return R.map(function (r) {
                 var rc = r.rects && r.rects[0]; return rc ? ('[' + rc.x0.toFixed(2) + '..' + rc.x1.toFixed(2) + ' x ' + rc.z0.toFixed(2) + '..' + rc.z1.toFixed(2) + ']') : '[]'; }).join(' '); } catch (e) { return '?'; } })());
      out.push('store      ' + (_store() ? ('project store live, ' + STORE_KEY + ' ' + (__CORE._storeSeen ? ('seen ' + __CORE._storeSeen) : 'not yet read'))
                                        : 'project store absent') + ' | link #cfg ' +
               (/[#&](c|cfg)=/.test(global.location.hash || '') ? ('present, ' + (global.location.hash.length) + ' chars' +
                 ((global.location.hash || '').indexOf('#c=') === 0 ? ' deflated' : ' plain')) : 'not in the url yet') +
               (__CORE._storeSeen === 'link' ? ' | this page was opened FROM a link' : ''));
      out.push('boot       liquid=' + (B.liquidVersion || 'MISSING') +
               ' slug=' + (B.slug || '?') +
               ' lib=' + (B.libAsset || '?') +
               ' i18n keys=' + (B.i18n ? Object.keys(B.i18n).length : 0));
      out.push('product    ' + (B.productTitle || '?') +
               ' | admin=' + (global.LEVA_PRODUCT ? global.LEVA_PRODUCT.isAdmin : '?') +
               ' | purchased=' + (global.LEVA_PRODUCT ? global.LEVA_PRODUCT.hasPurchased : '?'));

      /* NO cf-btn-storey. It left the rail in liquid v4 on purpose -- two numbers
         at the bottom stopped reading as numbers -- and this list kept expecting
         it, so every snapshot accused a correctly deployed liquid of being old. */
      var RAIL = ['cf-btn-view', 'cf-btn-viz', 'cf-btn-colors', 'cf-btn-undo', 'cf-btn-unit',
                  'cf-btn-share', 'cf-btn-pdf', 'cf-btn-ar', 'cf-btn-body',
                  'cf-btn-assembly'];
      var missing = [], hidden = [];
      for (i = 0; i < RAIL.length; i++) {
        el = document.getElementById(RAIL[i]);
        if (!el) missing.push(RAIL[i]);
        else if (el.classList && el.classList.contains('cf-hide')) hidden.push(RAIL[i]);
      }
      out.push('rail       ' + RAIL.length + ' expected | missing: ' +
               (missing.length ? missing.join(', ') + '  -- liquid older than v4?' : 'none') +
               ' | hidden: ' + (hidden.length ? hidden.join(', ') : 'none'));
      el = document.getElementById('cf-btn-budget');
      out.push('budget btn ' + (el ? 'STILL IN THE PAGE -- liquid older than v4' : 'gone (moved to the floating bar)'));
      if (H.scene) H.scene(out);

      var steps = wizSteps(), live = [], skipped = [];
      for (i = 0; i < steps.length; i++) {
        if (steps[i].skip && steps[i].skip()) skipped.push(steps[i].key);
        else live.push(steps[i].key + '(' + steps[i].icons().length + ')');
      }
      out.push('wizard     ' + live.length + ' live: ' + live.join(' '));
      out.push('wizard     skipped: ' + (skipped.length ? skipped.join(' ') : 'none'));
      out.push('video      ' + (VIDEO_ID ? ('id=' + VIDEO_ID) : 'NO VIDEO_ID -- tab hidden on purpose'));

      var keys = [], k;
      for (k in _stubs) if (_stubs.hasOwnProperty(k)) keys.push(k + ' x' + _stubs[k]);
      out.push('stubs hit  ' + (keys.length ? keys.join(' | ') : 'none pressed yet'));
      /* BUILT FROM THE STUBS THEMSELVES, not typed out. The hand-written version
         drifted every time something got wired and then lied about it. */
      var wired = {}, sk;
      for (sk in _stubNames) if (_stubNames.hasOwnProperty(sk)) wired[sk] = 1;
      out.push('NOT WIRED  ' + Object.keys(wired).sort().join(', '));

      out.push('undo       ' + API.undoState().steps + ' step(s) | button=' + (API.undoState().bound ? 'bound' : 'MISSING'));
      }

      var txt = out.join('\n') + '\n' + API._coreSnapshot();
      try {
        if (global.Configurator && global.Configurator.log) {
          for (i = 0; i < out.length; i++) global.Configurator.log.log('[' + __CORE.LOG_TAG + '] ' + out[i]);
        }
      } catch (e) {}
      return txt;
    }

    /* =========================================================================
       THE SKIRTING'S DOORWAYS (core-v813, status/modules.md 14): where a door in
       a partition, or an opening in an outer wall standing on the floor, crosses
       the run the skirting stops (adu-v1412) -- the ADU family's and RANCH's
       copies word for word, and SAUNA, which had none, gets it this way.
       ========================================================================= */
    /* THE DOORWAYS ACROSS A ROOM'S EDGE (adu-v1412), as stretches along it:
       a door in a partition standing on that line, and an opening in the
       outer wall there that reaches the floor -- the entrance, a glazed
       door, a pane down to the floor. Merged, in order, held to the edge. */
    function houseSkirtCuts(horiz, line, a, b, H) {
      /* what the product's copy read from its own scope */
      var API = H.api, PART = H.PART, DOOR_W_DEF = H.DOOR_W_DEF;
      var out = [], q, k, PS = API.STATE.parts || [], DS = API.STATE.doors || [];
      for (q = 0; q < DS.length; q++) {
        var d = DS[q], p = null;
        for (k = 0; k < PS.length; k++) if (PS[k] && PS[k].id === d.pid) { p = PS[k]; break; }
        if (!p || p.sep || (((p.dir || p.o) === 'h') !== !!horiz)) continue;
        var pc = (typeof p.at === 'number') ? p.at : p.c;
        if (Math.abs(pc - line) > PART.d / 2 + 0.06) continue;
        var w = d.w || DOOR_W_DEF;
        out.push([d.u - w / 2, d.u + w / 2, 'd' + q]);
      }
      var SG = [], wt = API.wallThickness();
      try { SG = API.segments ? (API.segments() || []) : []; } catch (eS) { SG = []; }
      for (q = 0; q < SG.length; q++) {
        var sg = SG[q];
        if (!sg || !sg.openings || !sg.len || (sg.id && String(sg.id).indexOf('S0-') !== 0)) continue;
        var hz = Math.abs(sg.z1 - sg.z0) < 0.02, vt = Math.abs(sg.x1 - sg.x0) < 0.02;
        if (horiz ? !hz : !vt) continue;
        if (Math.abs((horiz ? sg.z0 : sg.x0) - line) > wt + 0.08) continue;
        var ux = (sg.x1 - sg.x0) / sg.len, uz = (sg.z1 - sg.z0) / sg.len;
        for (k = 0; k < sg.openings.length; k++) {
          var o = sg.openings[k];
          if ((o.sill || 0) > 0.05) continue;
          var wx = sg.x0 + ux * o.at, wz = sg.z0 + uz * o.at, ow = o.w || 0.9, cc = horiz ? wx : wz;
          out.push([cc - ow / 2, cc + ow / 2, 'o' + q + '.' + k]);
        }
      }
      var C = [];
      for (q = 0; q < out.length; q++) {
        var c0 = Math.max(a, out[q][0]), c1 = Math.min(b, out[q][1]);
        if (c1 - c0 > 0.02) C.push([c0, c1, out[q][2]]);
      }
      C.sort(function (m1, m2) { return m1[0] - m2[0]; });
      var M = [];
      for (q = 0; q < C.length; q++) {
        if (M.length && C[q][0] <= M[M.length - 1][1] + 0.01) { M[M.length - 1][1] = Math.max(M[M.length - 1][1], C[q][1]); M[M.length - 1][2].push(C[q][2]); }
        else M.push([C[q][0], C[q][1], [C[q][2]]]);
      }
      return M;
    }

    /* =========================================================================
       THE STUD WALL MODULE (core-v773, the fifth shared module of the house
       line, its first half -- status/modules.md). The timber frame of every
       wall written once: the openings checked against the module, the studs,
       the plates (sole, top, cap), the jacks, kings, cripples, headers and
       sills, the parapet and the eave's run-out, the frames marked for the
       panels view, the sill seal, and the bill (frame timber, ledger,
       headers, sheathing, nails). The ADU's text as ANY HOUSE carries it.

         API.studWall(ctx, {
           api, layer ('adu-stud-wall'), wood (the timber colour),
           STUD, PLATE, HEADER, SILL_SEAL, LEDGER, RAFTER, EAVE_POST,
           SHEATH_NAIL, FRAME_NAIL, CHAMFER, DBL_SEAM, DBL_STITCH, FRAME_LEN,
           FRAME_TOL, PANEL_COLOURS, PANEL_MAX, PARAPET_UPSTAND, MAGENTA,
           FM, billReset, frameJoints, framePlan, openingAt, planeYAt,
           plateEndReach, plateTrussReach, runsThrough, studPieces, studsOf,
           wlHas, isNew, roofSection,
           add:       function (code, qty)
           publish:   function ({ frameBodies, bearStuds, wallStuds, atSeen,
                      portalHeadY }) -- the product's own arrays, filled here
           setTally:  function (tally)
           ga:        { under, pfFrames, pfCut, PF, LP } -- GARAGE APT /
                      ANY HOUSE: the storeys, the portal frames, the nailing
           slab:      { plate } -- RANCH / GA, the treated plate on the slab
           lvl:       { beam, beam2 }, hdrSaid: { get, reset } -- RANCH / GA
           studEnds, notch: { on, atRoofEdge } -- RANCH's L
           portal:    { def, tag } -- TAURUS / GARDEN BAR's front portal
           panel:     { code, waste, on }, tg: { code, on }, cladApply -- the
                      shed family's siding instead of the sheathing
         })

       Nothing is carried to other products this time: each one draws and
       bills as its own copy did (the candidates are in status/modules.md).
       ========================================================================= */
    function studWall(ctx, H) {
      /* the product's own pieces, under the names the text has always used */
      var API = H.api, STUD = H.STUD, PLATE = H.PLATE, HEADER = H.HEADER, SILL_SEAL = H.SILL_SEAL,
          LEDGER = H.LEDGER, RAFTER = H.RAFTER, EAVE_POST = H.EAVE_POST, SHEATH_NAIL = H.SHEATH_NAIL,
          FRAME_NAIL = H.FRAME_NAIL, CHAMFER = H.CHAMFER, DBL_SEAM = H.DBL_SEAM, DBL_STITCH = H.DBL_STITCH,
          FRAME_LEN = H.FRAME_LEN, FRAME_TOL = H.FRAME_TOL, PANEL_COLOURS = H.PANEL_COLOURS,
          PANEL_MAX = H.PANEL_MAX, PARAPET_UPSTAND = H.PARAPET_UPSTAND, MAGENTA = H.MAGENTA, LAYER = H.layer;
      var _FM = H.FM, _billReset = H.billReset, _frameJoints = H.frameJoints, _framePlan = H.framePlan,
          _openingAt = H.openingAt, _planeYAt = H.planeYAt, _plateEndReach = H.plateEndReach,
          _plateTrussReach = H.plateTrussReach, _runsThrough = H.runsThrough, _studPieces = H.studPieces,
          _studsOf = H.studsOf, _wlHas = H.wlHas, isNew = H.isNew, roofSection = H.roofSection;
      /* the products' own framing, each behind its hook (status/modules.md 5):
         GA      GARAGE APT / ANY HOUSE -- the storeys, the overhead doors'
                 portal frames (CS-PF), the load path's nailing
         SLAB    RANCH / GA -- the bottom plate on the slab pressure treated
                 and sawn out of the doorways (IRC R317.1)
         LVL     RANCH / GA -- the beam over a wide opening, by the check
         PORTAL  TAURUS / GARDEN BAR -- the beam on edge over the front portal
         PANEL, TG  the shed family's panel siding / Matt's T&G instead of the
                 sheathing */
      var GA = H.ga || null, GA_PF = GA ? GA.PF : null, GA_LP = GA ? GA.LP : null;
      var _gaUnder = GA ? GA.under : function () { return false; };
      var SLAB = H.slab || null;
      var HDR_BEAM = H.lvl ? H.lvl.beam : null, HDR_BEAM2 = H.lvl ? (H.lvl.beam2 || null) : null;
      var PORTAL = H.portal ? H.portal.def : null;
      var PANEL = H.panel || null, TG = H.tg || null;
      if (H.cladApply) H.cladApply();   /* taurus-v12: the facade's board for this build */
      /* the model is rebuilt many times in a session and these counters were
         adding up across all of them -- 490 kings on a wall with four windows */
      _FM.beginBuild();
      var _frameBodies = [];
      var T = ctx.THREE, g = ctx.group, SEG = ctx.seg, i, j;
      /* core-v804: STACKED WALLS (RUSSWORX SHED, Marek 2026-11-03 on Nick's
         build photos: the frame profiles were far too many, the frame is two
         storeys). A product may answer H.tiers(seg) with the wall as walls
         standing one on another -- copies of the segment, framed here as
         segments of their own: tierCap (the lower one: level at that y, no
         run-out, carries no rafter), tierUp (the one on top: no sill seal),
         tierNoSole (its studs stand on the cap below), tierFrom / tierTo (its
         run along the wall), area 0 on all but one (the skin is billed once).
         No hook, or null for a wall: framed as before. */
      var _tierNoSole = {};
      if (typeof H.tiers === 'function') {
        var SEGt = [], qTi, tTi, qTi2;
        for (qTi = 0; qTi < SEG.length; qTi++) {
          tTi = null;
          try { tTi = H.tiers(SEG[qTi]); } catch (eTi) { ctx.log('frame tiers ' + (SEG[qTi].id || qTi) + ' THREW ' + ((eTi && eTi.message) || eTi)); tTi = null; }
          if (tTi && tTi.length) { for (qTi2 = 0; qTi2 < tTi.length; qTi2++) SEGt.push(tTi[qTi2]); }
          else SEGt.push(SEG[qTi]);
        }
        SEG = SEGt;
      }
      /* the frame stage paints the panels too, with the roof over them */
      var byPanel = !!(ctx.viz && (ctx.viz.paint === 'panel' || ctx.viz.paint === 'frame'));
      var woodMat = new T.MeshStandardMaterial({ color: H.wood, roughness: 0.85, metalness: 0.0 });
      var treatedMat = null;   /* garageapt-v2: the bottom plate on the slab */
      var panelMats = [];
      for (i = 0; i < PANEL_COLOURS.length; i++) {
        panelMats.push(new T.MeshStandardMaterial({ color: PANEL_COLOURS[i], roughness: 0.85, metalness: 0.0 }));
      }

      var tally = { segments: 0, panels: 0, studs: 0, studLen: 0, plateLen: 0, area: 0,
                    through: 0, butted: 0, widths: [],
                    openings: 0, cripples: 0, headerLen: 0, sillLen: 0, jacks: 0, kings: 0 };
      var _bearStuds = [], _wallStuds = [], _atSeen = [], _portalHeadY = {};
      if (H.publish) H.publish({ frameBodies: _frameBodies, bearStuds: _bearStuds, wallStuds: _wallStuds,
                                 atSeen: _atSeen, portalHeadY: _portalHeadY });
      _billReset(LAYER);
      var panelNo = 0;

      for (i = 0; i < SEG.length; i++) {
        var s = SEG[i];
        /* THE OPENING STANDS ON THE MODULE, LIKE EVERYTHING ELSE.

           A window sat wherever the plan put it -- 1440, 2420, 3510 mm on a
           625 module -- so its kings landed between two sheet lines. The wall
           then had a module stud, a king a few centimetres away, and a bay
           between them too narrow to be a bay: those extra studs are not an
           extra rule, they are the module giving up at every hole.

           The centre of each opening moves to the nearest module line. It is
           the smallest move that puts the frame back in the rhythm, and it is
           done here, at the section, so every reader sees the same number --
           the panel breaks, the framing, the cladding and the bill (Marek:
           krokovani okna take musi byt podle sheet). */
        /* THE PRODUCT DOES NOT MOVE OPENINGS. THE CORE DOES.

           This moved every opening a second time, at build, after the core had
           already placed it -- two rules for one thing, and the wall was drawn
           from whichever ran last. That is what was breaking it however carefully
           either rule was written.

           What is left here is a CHECK, not a move: for each opening, the
           distance from its edge to the nearest stud face. Zero means the core
           put it where the wall builds a stud. Anything else is a disagreement
           between them and the number says how big (Marek: prizpusob tomu log
           jestli to nemuzes najit). */
        var leanAt = [];
        try {
          var posC = _studsOf(s), pC;
          var opsC = s.openings || [], oC, chk = [];
          for (oC = 0; oC < opsC.length; oC++) {
            /* MEASURED ON THE PIECE THAT IS DRAWN, NOT ON THE HOLE.

               The stud was picked using the structural opening, and the unit is
               seated using its own frame, which is 20 mm narrower -- so the red
               stud and the stud the window actually touches were two different
               members, and from the outside that reads as a window attaching to
               nothing. Both use the frame now (Marek: okno se neprimkne, jen se
               prebarvi profil). */
            /* AND IT MEASURES THE OPENING, NOT ITSELF.

               It used to find the nearest seat and then measure that seat
               against its own stud -- which is zero by construction, whatever
               the window's real position. Five zeros in a row proved nothing.
               Now both frame edges are measured against the real stud faces
               either side: the gap on the left, the gap on the right, and which
               one is closed. */
            var atC = opsC[oC].at, wC = (opsC[oC].w || 0) - 0.020;
            var eLc = atC - wC / 2, eRc = atC + wC / 2;
            var gLc = 1e9, gRc = 1e9, sLc = null, sRc = null;
            for (pC = 0; pC < posC.length; pC++) {
              var cC = posC[pC];
              var fRc = cC + STUD.t / 2, fLc = cC - STUD.t / 2;
              if (fRc <= eLc + 0.0005 && (eLc - fRc) < gLc) { gLc = eLc - fRc; sLc = cC; }
              if (fLc >= eRc - 0.0005 && (fLc - eRc) < gRc) { gRc = fLc - eRc; sRc = cC; }
            }
            var sideC = (sLc !== null && gLc < 0.002) ? 'LEFT' :
                        ((sRc !== null && gRc < 0.002) ? 'RIGHT' : 'NONE');
            if (sideC === 'LEFT') leanAt.push(sLc);
            else if (sideC === 'RIGHT') leanAt.push(sRc);
            _atSeen.push({ o: opsC[oC], at: atC });
            chk.push('w' + Math.round(wC * 1000) + ' at ' + Math.round(atC * 1000) +
                     ' gapL ' + (sLc === null ? '-' : Math.round(gLc * 1000)) +
                     ' gapR ' + (sRc === null ? '-' : Math.round(gRc * 1000)) +
                     ' -> ' + sideC);
          }
          if (chk.length) ctx.log('frame opening W' + i + ': ' + chk.join(' | ') + ' mm');

          /* THE TWO LISTS, SIDE BY SIDE.

             Three openings are out by 0, 7 and 60 mm. One rule applied wrongly
             would be out by the same amount everywhere; three different amounts
             mean two different lists of studs -- the core snapping to its own
             and the wall building another. Whose length, whose module and the
             first few positions of each, printed together, is the end of that
             question (Marek: asi uz na to nebere jine pravidlo, ale spatny
             vypocet). */
          var WL = (API && API.wallLayout) || null;
          if (WL) {
            var mine = posC.slice(0, 4).map(function (v) { return Math.round(v * 1000); });
            var theirs = WL.studsFor(s).slice(0, 4).map(function (v) { return Math.round(v * 1000); });
            ctx.log('truss lines wall ' + i + ' len ' + Math.round(s.len * 1000) +
                    ' | product ' + posC.length + ': ' + mine.join(',') +
                    ' | core ' + WL.studsFor(s).length + ': ' + theirs.join(',') +
                    ' | module ' + Math.round(WL.module() * 1000));
          } else {
            ctx.log('truss lines wall ' + i + ': the core exposes no wallLayout -- ' +
                    'it is older than core-v239 and snapping by its own rule');
          }
        } catch (eOS) {}

        /* NO OPENING RULE HERE AT ALL.

           Four versions of "move the window onto the module" were written from
           a description and judged from a photograph, and every one of them
           fought the spacing instead of following it. They are gone. A window
           sits where the plan puts it until there is a rule that has been proven
           on numbers first (Marek: vsechno ostatni pryc). */
        var ang = Math.atan2(s.x1 - s.x0, s.z1 - s.z0);
        var ux = (s.x1 - s.x0) / s.len, uz = (s.z1 - s.z0) / s.len;
        var through = _runsThrough(s);
        var from = through ? 0 : STUD.d;
        var to = through ? s.len : (s.len - STUD.d);
        /* per end, so the L's inside corner is framed (ranch-v5): a through
           wall runs a stud depth past it, a butting wall goes right to it */
        if (H.studEnds) { var EIs = H.studEnds(s); from = EIs[0]; to = s.len - EIs[1]; }
        /* core-v804: a stacked wall's own run (the raked one stops where its
           plates would meet the cap under it) */
        var tierClip = (typeof s.tierFrom === 'number') || (typeof s.tierTo === 'number');
        if (typeof s.tierFrom === 'number') from = Math.max(from, s.tierFrom);
        if (typeof s.tierTo === 'number') to = Math.min(to, s.tierTo);
        if (through) tally.through++; else tally.butted++;

        /* NO TWO VERTICALS IN THE SAME PLACE, WHOEVER PUT THEM THERE.

           The kings, the jacks, the module studs and the cripples are placed by
           four different pieces of code, and each one is right on its own. What
           none of them can see is another member already standing on the same
           line -- so a pair a couple of centimetres apart appears wherever two
           rules happen to agree, and it reads as a doubled post nobody designed.

           I could not find which two were colliding above the window from here,
           and guessing a second time is worse than fixing the class: anything
           closer than a stud thickness to something already up is the same
           member, and the first one wins.

           It is deliberately NOT a tolerance on the module. Two studs 300 mm
           apart are two studs; two 20 mm apart are one drawn twice. */
        var _vAt = [];

        /* POSITION AND HEIGHT BAND, NOT POSITION ALONE.

           The cripple above a header and the cripple below the sill share a
           position -- they are the same module line, interrupted by the hole. A
           check on position only would have thrown the lower one away and left a
           gap under every window, which is a worse fault than the one being
           fixed. So two members clash when they are on the same line AND their
           heights overlap. */
        function _vFree(at2, lo2, hi2, kind2) {
          for (var q3 = 0; q3 < _vAt.length; q3++) {
            var e = _vAt[q3];
            if (Math.abs(e.at - at2) >= STUD.t * 0.9) continue;
            if (hi2 <= e.lo + 0.01 || lo2 >= e.hi - 0.01) continue;
            return false;
          }
          _vAt.push({ at: at2, lo: lo2, hi: hi2, k: kind2 || '?' });
          return true;
        }

        /* IS ONE OF THIS SEGMENT'S KINGS CLOSE ENOUGH TO BE THIS STUD?

           It took the openings as an argument and the caller passed ops, which
           is not assigned until a hundred lines further down -- hoisted, so no
           error until it was read, and then ops2.length threw and took the whole
           stud wall with it. Most of the timber vanished; the base, floor,
           partitions and roof are their own layers and stood.

           The segment carries its own openings and always has. Asking it directly
           removes the ordering question rather than answering it. */
        function _kingNear(at2) {
          var ops2 = (s && s.openings) || [];
          var q2, sg2;
          for (q2 = 0; q2 < ops2.length; q2++) {
            for (sg2 = -1; sg2 <= 1; sg2 += 2) {
              var kAt = ops2[q2].at + sg2 * (ops2[q2].w / 2 + STUD.t * 1.5);
              if (Math.abs(kAt - at2) < STUD.t * 1.5) return true;
            }
          }
          return false;
        }

        function place(w, h, d, along, y, mat, biasR) {
          var px = s.x0 + ux * along - s.nx * (STUD.d / 2);
          var pz = s.z0 + uz * along - s.nz * (STUD.d / 2);
          var m = new T.Mesh(new T.BoxGeometry(w, h, d), mat);
          m.position.set(px, y, pz);
          m.rotation.y = ang;
          _panelMark(m, along, biasR);
          g.add(m);
          return m;
        }

        /* A STUD UNDER A RAKED PLATE IS CUT TO THE RAKE.

           Every stud was a plain box with a flat top, and on the two walls that
           cross the fall of a mono-pitch the plate above them climbs -- so each
           one met it on a corner and left a wedge of daylight at the other. Small,
           and on every stud in the wall:

             sklon  8 deg ->  6.3 mm
             sklon 15 deg -> 12.1 mm
             sklon 23 deg -> 19.1 mm

           The box is built as before and only its four TOP corners are moved, so
           the position, the rotation and the material are untouched -- what
           changes is the face that meets the plate. At zero rake it does nothing,
           which is why a level wall can call the same thing.

           The cut runs ALONG the wall, across the stud's 45 mm width, because
           that is the direction the plate rises in. */
        function placeCut(w, h, d, along, y, mat, tiltA, biasR) {
          var m = place(w, h, d, along, y, mat, biasR);
          if (!m || !tiltA || Math.abs(tiltA) < 1e-4) return m;
          var pos = m.geometry && m.geometry.attributes && m.geometry.attributes.position;
          if (!pos) return m;
          var half = h / 2, k;
          for (k = 0; k < pos.count; k++) {
            if (pos.getY(k) < half - 1e-6) continue;
            pos.setY(k, pos.getY(k) + pos.getZ(k) * Math.tan(tiltA));
          }
          pos.needsUpdate = true;
          m.geometry.computeVertexNormals();
          return m;
        }

        /* THE SAME, OFFSET ACROSS THE WALL. A doubled member is two boards side
           by side through the thickness of the wall, so each one needs pushing
           off the centreline along the wall's own normal. */
        function placeAcross(w, h, d, along, y, off, mat, biasR) {
          var px = s.x0 + ux * along - s.nx * (STUD.d / 2) + s.nx * off;
          var pz = s.z0 + uz * along - s.nz * (STUD.d / 2) + s.nz * off;
          var m = new T.Mesh(new T.BoxGeometry(w, h, d), mat);
          m.position.set(px, y, pz);
          m.rotation.y = ang;
          /* THE OTHER DOOR INTO THE WALL, and most of the wall comes through it.

             There are two placers here, one of them offset across the wall's
             thickness, and I marked only the first: 254 pieces came past the
             mark where the wall has many times that, so a panel's sheet held a
             plate and a stud or two and looked broken. A piece is a piece
             whichever helper set it down (Marek: porad je to rozbite na vice
             casti). */
          _panelMark(m, along, biasR);
          g.add(m);
        }

        /* THE SAME, BUT FOLLOWING A RAKE. A plate on a wall that climbs has to
           climb with it: tilted along its own length before it is turned onto the
           wall. Two rotations on the geometry, the same order the roof uses. */
        /* WHICH PANEL A PIECE BELONGS TO, AND THE PIECES THAT BELONG TO NONE.

           A panel is what gets stood up on site: its own sole plate, its studs,
           its kings and cripples, and the LOWER of the two top plates. The upper
           plate is not part of any panel -- it runs the whole wall in one piece
           over every panel joint, and tying the panels together is its entire
           job. So the mark is set while a panel is being built and cleared the
           moment the loop ends: whatever is made after it, the cap plate above
           all, stays unmarked and out of the panel's picture (Marek: kazdy
           zvlast, bez horni spojovaci pozednice). */
        function _panelMark(m, along2, biasR) {
          if (m) _frameBodies.push(m);
          var nP = _FM.mark(m, along2, biasR);
          /* AND IN THE WALL PANELS VIEW EACH FRAME WEARS ITS OWN COLOUR, so the
             joints can be seen at a glance; neighbours never share one */
          if (nP >= 0 && byPanel && panelMats.length) m.material = panelMats[nP % panelMats.length];
        }

        function placeRaked(w, h, len, along, y, tilt, mat) {
          var px = s.x0 + ux * along - s.nx * (STUD.d / 2);
          var pz = s.z0 + uz * along - s.nz * (STUD.d / 2);
          var geo = new T.BoxGeometry(w, h, len / Math.max(0.2, Math.cos(tilt)));
          /* MINUS, because rotateX takes the length axis DOWNWARD for a positive
             angle. Feeding it the rake straight made every raked plate fall
             exactly as far as the roof above it rose -- the two walls read as
             mirrored because they were both wrong, and against a roof going the
             other way it looked like one of them had been flipped. */
          geo.rotateX(-tilt);
          geo.rotateY(ang);
          geo.translate(px, y, pz);
          var mR = new T.Mesh(geo, mat);
          _panelMark(mR, along);
          g.add(mR);
          return mR;
        }

        /* WHERE THE ROOF BEARS OVER A POINT OF THIS WALL. On a gable it is the
           same all the way along; on a mono-pitch it climbs, and both the studs
           and the plates have to follow it. One question, asked per point, and
           every wall answers it the same way. */
        function bearAt(along) {
          /* garageapt-v26 (found with unit 16b): A WALL WITH A STOREY OVER IT
             ENDS AT ITS OWN STOREY'S HEAD -- the floor between bears on its
             plates (platform framing, the I-joists on the rim). The roof's
             bearing (wallTop, the top storey's head) had the ground storey's
             studs, jacks, kings and plates run on up through the floor and
             the whole apartment wall, beside that wall's own studs. */
          if (_gaUnder(s)) { try { var stU = API.storeys()[s.storey | 0]; if (stU && stU.head > 0) return stU.head; } catch (eU) {} }
          /* core-v804: the lower of two stacked walls stops level at its cap */
          if (s.tierCap > 0) return Math.min(s.tierCap, API.bearingY(s.x0 + ux * along, s.z0 + uz * along));
          return API.bearingY(s.x0 + ux * along, s.z0 + uz * along);
        }
        var bear0 = bearAt(0), bear1 = bearAt(s.len);
        /* the wall's own rake: zero on three roofs out of three at the low and
           high walls, and the real slope on the two that cross the fall */
        var tilt = Math.atan2(bear1 - bear0, s.len);

        /* THE PARAPET IS PART OF THE PANEL, NOT A THING BUILT ON TOP OF THE ROOF.

           The studs run in ONE PIECE from the sole plate past the roof to the
           coping, and the roof hangs off a ledger nailed to their inside face.
           That is the recommended detail: a curb sitting on the finished deck
           puts a hinge exactly where the covering turns up, which is the most
           failure-prone joint on a flat roof.

           It is also what makes the parapet a NUMBER rather than an assembly --
           how much taller the studs are than the bearing line. Zero is a plain
           eave. One field, one wall layer, and the modern-house product stops
           being a different engine from the bungalow.

           Only the top storey gets one; a wall with a floor above it has no roof
           to stand past. */
        var topStorey = (s.storey === ctx.storeys.length - 1);
        var wantPar = (API.STATE.roof.type === 'flat') && topStorey;
        var parRise = wantPar ? (roofSection().d + RAFTER.deck + PARAPET_UPSTAND) : 0;

        /* the two-direction layout report is gone with the rest */

        /* ONE WALL, ONE FRAME.

           The wall was cut into transport panels, each with its own plates, its
           own end posts and its own colour -- which is where every joint, every
           extra post and every colour boundary in the middle of a wall came
           from. There is no panelisation left in this wall: one span, corner to
           corner, one sole plate, one set of top plates, a post at each end.

           _panelSpans stays in the file and is not called. It comes back when
           the module underneath it is proven, not before (Marek: kazda stena je
           jeden ram). */
        var spans = [[from, to]];
        /* where this segment's colours start, so a later lookup lands on the
           same panel the loop drew */
        var panelBase = panelNo;

        /* the section the panel marks are resolved against, for as long as this
           section's panels are being built */
        _FM.beginWall(i, spans, panelNo);

          /* NO OPENING IS MOVED IN THIS FILE. NOT HERE, NOT ANYWHERE.

             This spot held the last surviving copy of "bring the LEFT king onto
             a stud line": it ran on every rebuild, after the core had already
             seated the frame against a stud, and moved the opening again -- up
             to half a bay, always from the left. The core offers two seats per
             bay (left edge on a stud, right edge on a stud); this collapsed both
             into one, which is why a window would only ever attach one way. Its
             log line began with 'wall' and FOCUS printed only 'truss', so it
             never reached the snapshot (Marek: porad se primykaji pri tazeni jen
             na jednu stranu). */

        /* WHAT LIVES IN WHICH ZONE, and this is the fix.

           The clear height y0..y1 holds the sole plate, the studs, and the LOWER
           top plate -- that is the panel, the thing that gets stood up.

           The CAP plate lives in the storey's plate zone, above y1. That zone was
           140 mm of nothing before, because all three plates were built inside the
           clear height and the zone the core reserves was never filled: the roof
           bore on a level with an empty gap under it. */
        /* THE TOP OF THE WALL IS WHATEVER THE ROOF SAYS. Under a gable that is one
           height; under a mono-pitch the high wall stands taller and the two walls
           that cross the fall are RAKED -- every stud its own length and the
           plates on the slope. It is the same three lines either way. */
        function headAt(along) { return bearAt(along) + parRise; }

        /* TO THE FAR FACE OF THE LAST RAFTER, NOT TO ITS CENTRELINE. The plane
           reaches the overhang; the rafter on that edge is centred on it, so half
           of it is still beyond. Same half-thickness the gable's barge rafter
           needed. */
        /* THE PLATES THAT ALREADY EXIST ARE THE ONES THAT RUN OUT.

           This was mono only, and it read the single eave. A gable with a deep
           rake had "plates stop at the wall" and a post standing under a plate
           that was not there -- so I had started building a separate member out
           at the roof edge, which is a new profile pretending to be a plate.

           There is no new profile. The plate is the plate; it is made longer.
           Per side, because the two ends can reach different distances. */
        /* AND THE END THAT REACHES IS THE END THE ROOF REACHES OVER.

           rakeExtA was "the z0 side" in WORLD terms and was applied at the
           segment's own START -- and a segment can run either way round. On one
           wall the plate therefore grew away from the overhang, which is what
           looked wrong: the roof out one side and the timber out the other.

           The two ends are worked out in the SEGMENT's direction: whichever world
           edge its start is nearer to is the reach that belongs to it. */
        /* A PARAPET HAS NO OVERHANG, SO NONE OF THIS APPEARS. */
        var flatRoof = (API.STATE.roof.type === 'flat' || API.STATE.roof.parapet);
        var OW = (!flatRoof && API.roofOver) ? API.roofOver() : null;
        var rakeExt = 0, rakeExtA = 0, rakeExtB = 0;
        if (through && OW) {
          var alongLW = (API.STATE.roof.axis === 'l');
          var oLo = alongLW ? OW.z0 : OW.x0, oHi = alongLW ? OW.z1 : OW.x1;
          /* does this segment START at the low world edge or the high one? */
          var sA = alongLW ? s.z0 : s.x0, sB = alongLW ? s.z1 : s.x1;
          var startsLow = (sA <= sB);
          /* to the outer face of the last truss -- the same reach the eave header
             uses, so plate and header end on the same line */
          rakeExtA = _plateEndReach(startsLow ? oLo : oHi);
          rakeExtB = _plateEndReach(startsLow ? oHi : oLo);
          /* ONLY WHERE A TRUSS STANDS OUT THERE (adu-v1401). Past a gable the
             last truss stands in the overhang and the plate runs out under
             it. Past a HIPPED end there is no truss -- the hip and its jacks
             close the corner -- and the plate stuck out of the facade with
             nothing on it (Marek: zkratit pozednice at netrci z fasady, jen
             pokud drzi vazniky). */
          /* AND ONLY AS FAR AS A TRUSS THAT IS REALLY THERE (adu-v1402). The
             reach was worked out from the overhang, as if a truss always
             stood at its edge. It does not: past a gable the stations go on
             outward at the module only while a whole bay still fits in the
             rake, so a 400 mm rake has no truss in it and the plate ran out
             378 mm under nothing (Marek: porad trci ty pozednice z fasady).
             The stations are asked: the plate goes to the outer face of the
             outermost truss past the frame, and stops at the frame when
             there is none -- which is also every hipped end. */
          var wasA = rakeExtA, wasB = rakeExtB;
          var tA = _plateTrussReach(startsLow), tB = _plateTrussReach(!startsLow);
          if (tA !== null) rakeExtA = Math.min(rakeExtA, tA);
          if (tB !== null) rakeExtB = Math.min(rakeExtB, tB);
          ctx.log('frame plates W' + i + ' (through): run-out ' + Math.round(rakeExtA * 1000) + ' at its start' +
                  (tA === null ? ' (rafters, the rake reach)' : (rakeExtA > 0 ? ' (under the last truss)' : (' (no truss past the frame' + (wasA > 0 ? (', was ' + Math.round(wasA * 1000)) : '') + ')'))) +
                  ' / ' + Math.round(rakeExtB * 1000) + ' at its end' +
                  (tB === null ? ' (rafters, the rake reach)' : (rakeExtB > 0 ? ' (under the last truss)' : (' (no truss past the frame' + (wasB > 0 ? (', was ' + Math.round(wasB * 1000)) : '') + ')'))));
          rakeExt = Math.max(rakeExtA, rakeExtB);
        }

        /* AND THE END WALLS' CAP RUNS OUT UNDER AN EXTENDED EAVE. When a side of
           the roof is pulled out far enough to stand on posts, the roof over
           that side needs a plate from the corner out to the post line -- the
           end walls' cap, carried on past the through wall to the eave header,
           the way the through walls' cap carries on past the gable under a
           rake extension (Marek: the roof parts had nothing to bear on). */
        var eaveExtA = 0, eaveExtB = 0;
        if (!through && OW) {
          var alongLE = (API.STATE.roof.axis === 'l');
          /* roofOver() is the OVERHANG per side, in metres -- not an outline */
          var ovLo = alongLE ? OW.x0 : OW.z0, ovHi = alongLE ? OW.x1 : OW.z1;   /* how far the roof goes past each end of this wall */
          var startsLowE = (alongLE ? s.x0 : s.z0) <= (alongLE ? s.x1 : s.z1);
          /* whenever the eave goes past a plain overhang -- posts or no posts
             (Marek): the plate carries the roof's edge out there either way.
             With posts it ends on the post's NEAR face; without, at the roof
             edge (the truss tails' reach) -- no gap either way. */
          var PLAIN_EAVE = 0.80;
          function runOut(ovv) {
            if (!(ovv > PLAIN_EAVE)) return 0;
            var tail = ovv + API.facadeThk() - PLATE.t;
            return (ovv > EAVE_POST.trigger) ? Math.max(0, tail - EAVE_POST.w) : tail;
          }
          var extLo = runOut(ovLo), extHi = runOut(ovHi);
          /* the cap of a butted wall starts a stud depth INSIDE the corner, so
             the run-out past the corner needs that depth added back (Marek:
             still short of the header by a stud) */
          eaveExtA = (startsLowE ? extLo : extHi); if (eaveExtA) eaveExtA += STUD.d;
          eaveExtB = (startsLowE ? extHi : extLo); if (eaveExtB) eaveExtB += STUD.d;
          /* AND A RAKED WALL KEEPS ITS PLATES ON ITS PANELS. What the roof needs
             out there is under the RAFTERS -- their tails cantilever, the eave
             plate ties their ends, and past the trigger the posts take over. A
             raked plate hanging past the last panel carries none of that; it
             just stuck out (shed-v11, Marek). Walls with level plates keep
             their run-out: the end rafter lies on it. */
          if (Math.abs(tilt) > 1e-4 && (eaveExtA || eaveExtB)) {
            ctx.log('wall     raked wall W' + i + ': plates STOP at the panels -- ' +
                    Math.round(eaveExtA * 1000) + ' / ' + Math.round(eaveExtB * 1000) +
                    ' mm of run-out dropped, it stood in the air and bore nothing');
            eaveExtA = 0; eaveExtB = 0;
          }
          /* AND AN END WALL UNDER A HIP CARRIES NO TRUSS PAST ITS CORNERS
             (adu-v1401): the run-out held the gable truss's tail; at a hip
             end that truss is not there, so the plate stops in the frame */
          var myEndE = (API.ridgeEndOf ? API.ridgeEndOf(s) : null);
          if (myEndE && hipAt(myEndE) && (eaveExtA || eaveExtB)) {
            ctx.log('frame plates W' + i + ' (end wall, hip end ' + myEndE + '): run-out ' + Math.round(eaveExtA * 1000) + ' / ' + Math.round(eaveExtB * 1000) +
                    ' mm dropped -- no truss bears on it past the corner, it only stuck out of the facade');
            eaveExtA = 0; eaveExtB = 0;
          }
          ctx.log('wall     end wall W' + i + ': roof past its ends ' + Math.round(ovLo * 1000) + ' / ' + Math.round(ovHi * 1000) + ' mm -> cap plate runs out ' + Math.round(eaveExtA * 1000) + ' / ' + Math.round(eaveExtB * 1000) + ' mm' + (OW ? '' : ' (no roof outline)'));
        }
        /* ON THE L A PLATE RUNS OUT ONLY AT A CORNER OF THE ROOF'S RECTANGLE
           (ranch-v5): past the porch's corner the porch beam carries the roof,
           and the porch's own walls are under it */
        if (H.notch && H.notch.on()) {
          var BBr = _buildRect();
          var atC = function (x, z) { return (Math.abs(x - BBr.x0) < 0.01 || Math.abs(x - BBr.x1) < 0.01) && (Math.abs(z - BBr.z0) < 0.01 || Math.abs(z - BBr.z1) < 0.01); };
          if (!atC(s.x0, s.z0) || !H.notch.atRoofEdge(s)) { rakeExtA = 0; eaveExtA = 0; }
          if (!atC(s.x1, s.z1) || !H.notch.atRoofEdge(s)) { rakeExtB = 0; eaveExtB = 0; }
          rakeExt = Math.max(rakeExtA, rakeExtB);
        }
        /* garageapt-v9: A WALL WITH A STOREY OVER IT CARRIES NO ROOF, so its
           plates stop at its corners. The rake reach was the mono's rafters'
           (no truss stations to stop it) and ran the garage's plates 588 past
           the corners into the floor band, under nothing. */
        if (_gaUnder(s) && (rakeExtA || rakeExtB || eaveExtA || eaveExtB)) {
          ctx.log('GA plates W' + i + ' (storey ' + ((s.storey | 0) + 1) + ', a storey over it): run-out ' +
                  Math.round(Math.max(rakeExtA, rakeExtB, eaveExtA, eaveExtB) * 1000) + ' dropped -- the roof is not over this wall');
          rakeExtA = 0; rakeExtB = 0; rakeExt = 0; eaveExtA = 0; eaveExtB = 0;
        }
        /* THE FRAME ROUND EVERY OPENING OF THIS WALL, before anything is drawn:
           the module studs below are cut to it, and the jacks, kings, header
           and sill are drawn from it after the cap plate. */
        /* core-v804: the lower of two stacked walls carries no roof -- its
           plates end at its corners, the wall on it runs out */
        if (s.tierCap > 0 && (rakeExtA || rakeExtB || eaveExtA || eaveExtB)) {
          ctx.log('frame tier W' + i + ' (' + (s.id || '') + ', the lower wall): run-out ' + Math.round(Math.max(rakeExtA, rakeExtB, eaveExtA, eaveExtB) * 1000) + ' dropped -- the wall on it carries the roof');
          rakeExtA = 0; rakeExtB = 0; rakeExt = 0; eaveExtA = 0; eaveExtB = 0;
        }
        var framesF = [], cutL = [];
        try {
          framesF = _framePlan(s, _studsOf(s),
                               function (x) { return headAt(x) - STUD.t * 2; }, s.y0 + STUD.t);
        } catch (eFP) { ctx.log('frame plan W' + i + ' threw: ' + (eFP && eFP.message)); framesF = []; }
        /* garageapt-v26 (unit 16b): the overhead doors' portal frames (CS-PF)
           -- where each header ends, its post, the studs that stop under it */
        if (GA) {
          var pfSaid = [];
          try { pfSaid = GA.pfFrames(s, framesF, from, to, _studsOf(s)); } catch (ePF) { pfSaid = ['THREW ' + ((ePF && ePF.message) || ePF)]; }
          tally.pfSaid = tally.pfSaid || [];
          for (var pfq = 0; pfq < pfSaid.length; pfq++) tally.pfSaid.push((s.id || ('W' + i)) + ' ' + pfSaid[pfq]);
        }
        if (i === 0 && !_wlHas('framePlan')) {
          ctx.log('frame ERROR the frame planners are missing -- configurator-ui.js is older than core-v252; ' +
                  'no opening frames and no transport frames this build');
        }

        /* AND THE FRAMES THIS WALL TRAVELS AS: the joints, then one span per
           frame. The plates below are laid per span, so they part at each
           joint; the marks are resolved against the same spans. */
        var jointsF = [];
        try {
          jointsF = _frameJoints(s.len, from, to, _studsOf(s), framesF);
        } catch (eFJ) { ctx.log('frame joints W' + i + ' threw: ' + (eFJ && eFJ.message)); jointsF = []; }
        /* what a module stud is in its frame, when it is more than a stud */
        var _modRole = function (x) {
          var q8, s8, sd8 = ['L', 'R'];
          for (q8 = 0; q8 < framesF.length; q8++) {
            for (s8 = 0; s8 < 2; s8++) {
              var P8 = framesF[q8].plan[sd8[s8]];
              if (P8.jack && !P8.jack.isNew && Math.abs(P8.jack.x - x) < 1e-4) return 'jack';
              if (P8.king && !P8.king.isNew && Math.abs(P8.king.x - x) < 1e-4) return 'king';
            }
          }
          for (q8 = 0; q8 < jointsF.length; q8++) {
            if (!jointsF[q8].pair && Math.abs(jointsF[q8].c - x) < 1e-4) return 'joint';
          }
          return 'stud';
        };
        if (jointsF.length) {
          spans = [];
          var jx0 = from, jq;
          for (jq = 0; jq < jointsF.length; jq++) { spans.push([jx0, jointsF[jq].joint]); jx0 = jointsF[jq].joint; }
          spans.push([jx0, to]);
          _FM.setSpans(spans);
        }
        try {
          var lensF = [], overF = false, jl;
          for (jl = 0; jl < spans.length; jl++) {
            var lenF = spans[jl][1] - spans[jl][0];
            lensF.push(Math.round(lenF * 1000) +
                       (lenF > FRAME_LEN + FRAME_TOL + 1e-6 ? ' OVER' :
                        (lenF > FRAME_LEN + 1e-6 ? (' (+' + Math.round((lenF - FRAME_LEN) * 1000) + ')') : '')));
            if (lenF > FRAME_LEN + FRAME_TOL + 1e-6) overF = true;
          }
          var stE = _studsOf(s);
          ctx.log('frame panels W' + i + (through ? ' through' : ' butting') + ' run ' +
                  Math.round(from * 1000) + '..' + Math.round(to * 1000) + ', end studs ' +
                  Math.round(stE[0] * 1000) + ' / ' + Math.round(stE[stE.length - 1] * 1000) +
                  ' (' + ((stE[0] > from && stE[stE.length - 1] < to) ? 'inside' : 'OUTSIDE') + ' the frame): ' + spans.length +
                  ' frame(s) ' + lensF.join(' / ') + ' (limit ' + Math.round(FRAME_LEN * 1000) + ')' +
                  (jointsF.length ? (' | joints ' + jointsF.map(function (jj) {
                    return jj.pair ? ('opening pair jack ' + Math.round(jj.c * 1000) + ' / king ' + Math.round(jj.x2 * 1000))
                                   : ('stud ' + Math.round(jj.c * 1000) + ' + new ' + Math.round(jj.x2 * 1000));
                  }).join(', ')) : '') +
                  (overF ? (' | NO CLEAR STUD NEAR ENOUGH -- a frame is past ' +
                            Math.round((FRAME_LEN + FRAME_TOL) * 1000)) : ''));
        } catch (eFL) {}

        for (j = 0; j < spans.length; j++) {
          var a = spans[j][0], b = spans[j][1], plen = b - a;
          var mid = (a + b) / 2;
          var mat = woodMat;   /* one wall, one timber, no panel colours */
          panelNo++;
          tally.panels++;
          tally.widths.push(plen);

          _asmCode(ctx, STUD.code, 'side');
          /* THE PANEL'S OWN SOLE PLATE, AND ITS TOP PLATE.

             AND ON THE END PANELS THAT TOP PLATE IS LONGER. The overhang wants a
             double plate the whole way out, and the lower of the two IS the
             panel's -- so it runs out as one board rather than the panel stopping
             at the wall and a separate stub carrying on past it. A splice at the
             wall face is the worst place to put one: that is where the
             cantilever starts.

             Only the first and last panel of a through wall grow, and only by the
             overhang at their own end. */
          /* BOTH PLATES RUN OUT, EACH ITS OWN END'S REACH. The lower one was
             taking the deeper of the two at whichever end it happened to be, so
             one of the pair projected further than the other and the doubled
             plate stopped being doubled where it matters. */

          /* THE JOINT STUD IS OUT AGAIN, AND THE REASON IS WORTH KEEPING.

             I added a stud a stud's width inside the panel that ends at a joint,
             so each panel would carry its own. Where the joint is near a window
             that put a THIRD vertical beside the jack and the king, with gaps
             between all three -- three studs and two slots where the drawing
             wants two studs face to face.

             The pair beside an opening IS the joint pair: one belongs to the
             opening's panel and the other to its neighbour, and nothing needs
             adding. What is still open is the plain joint in the middle of a
             run, where the wall has one stud and two panels want an end each.
             That is a framing decision, not a marking one, and it is not being
             guessed at a fourth time (Marek: ty tam ale pridavas dalsi sloupek). */

          var loExt = (through && j === 0 && typeof s.tierFrom !== 'number') ? rakeExtA : 0;   /* core-v804: not past a stacked wall's clipped end */
          var hiExt = (through && j === spans.length - 1 && typeof s.tierTo !== 'number') ? rakeExtB : 0;
          var topLen = plen + loExt + hiExt;
          var topMid = mid - loExt / 2 + hiExt / 2;

          /* THE SOLE PLATE IS THE PANEL'S AND IS CUT WITH IT -- that is what
             makes a panel a thing you can lift. */
          /* AND IT RUNS THROUGH UNDER A DOOR. The wall is built as a frame, so
             the plate stays whole under every door and portal (Marek: muze byt
             prubezny, stavime ramy). */
          /* where two frames meet, each plate stops half a seam short so the
             joint shows; the bill keeps the full length */
          var sjA = (j > 0) ? DBL_SEAM / 2 : 0, sjB = (j < spans.length - 1) ? DBL_SEAM / 2 : 0;
          _FM.role('sole');
          /* garageapt-v2: ON THE SLAB THE BOTTOM PLATE IS PRESSURE TREATED
             (IRC R317.1: a sill on concrete in contact with the ground) --
             KVP60160, drawn a treated green and billed apart from the frame */
          /* garageapt-v4: the slab is under storey 0 only -- the apartment's
             walls stand on its subfloor: their bottom plate is the frame's
             own timber, and no sill seal under it */
          /* ranch-v30 the same (copied from garageapt-v2 / v3); a product
             without SLAB never is on one here */
          var onSlab = !!SLAB && (API.STATE.foundation === 'slab') && (!GA || ((s.storey | 0) === 0));
          if (onSlab) _asmCode(ctx, SLAB.plate, 'side');
          /* garageapt-v3: ON THE SLAB THE PLATE IS SAWN OUT OF EVERY DOORWAY
             once the wall stands -- a 2x6 across a garage door is a kerb.
             Billed whole (it is bought and stood whole); the seal goes with it. */
          var gaPlA = a + sjA, gaPlB = b - sjB, gaRuns = [[gaPlA, gaPlB]];
          if (onSlab) {
            var gaDOps = s.openings || [], gaDq, gaDCuts = [];
            for (gaDq = 0; gaDq < gaDOps.length; gaDq++) {
              if (!gaDOps[gaDq] || (Number(gaDOps[gaDq].sill) || 0) > 0.05) continue;
              var gaC0 = Math.max(gaPlA, gaDOps[gaDq].at - gaDOps[gaDq].w / 2), gaC1 = Math.min(gaPlB, gaDOps[gaDq].at + gaDOps[gaDq].w / 2);
              if (gaC1 - gaC0 > 0.01) { gaDCuts.push([gaC0, gaC1]); tally.doorCut = (tally.doorCut || 0) + (gaC1 - gaC0); tally.doorCuts = (tally.doorCuts || 0) + 1; }
            }
            gaDCuts.sort(function (p, q2) { return p[0] - q2[0]; });
            if (gaDCuts.length) {
              gaRuns = []; var gaAtP = gaPlA;
              for (gaDq = 0; gaDq < gaDCuts.length; gaDq++) { if (gaDCuts[gaDq][0] - gaAtP > 0.01) gaRuns.push([gaAtP, gaDCuts[gaDq][0]]); gaAtP = Math.max(gaAtP, gaDCuts[gaDq][1]); }
              if (gaPlB - gaAtP > 0.01) gaRuns.push([gaAtP, gaPlB]);
            }
          }
          /* core-v804: a stacked wall whose studs stand on the cap below has no
             sole plate of its own (gable studs on the lower wall) */
          if (s.tierNoSole) { gaRuns = []; _tierNoSole['W' + i + 'P' + panelNo] = 1; }
          var gaSoleMat = (onSlab && !byPanel) ? (treatedMat || (treatedMat = new T.MeshStandardMaterial({ color: 0x9AA36E, roughness: 0.9, metalness: 0.0 }))) : mat;
          var soleM = null, gaPr;
          for (gaPr = 0; gaPr < gaRuns.length; gaPr++) {
            var gaSm = place(STUD.d, STUD.t, gaRuns[gaPr][1] - gaRuns[gaPr][0], (gaRuns[gaPr][0] + gaRuns[gaPr][1]) / 2, s.y0 + STUD.t / 2, gaSoleMat);
            if (!soleM) soleM = gaSm;
            if (gaSm) gaSm.userData.cutLen = (gaPr === 0) ? plen : 0;   /* the seam is drawn, not cut */
            if (gaSm && onSlab && GA) gaSm.userData.gaStep = '2d';
          }
          if (onSlab) tally.soleLen = (tally.soleLen || 0) + plen;
          /* garageapt-v27 (unit 16c): an upper storey's sole plate through the
             subfloor into the rim / end joist, 3 x 16d per 16 in. at braced
             wall panels (Table R602.3(1); every exterior wall is one) */
          if (GA_LP && !onSlab && (s.storey | 0) > 0) {
            tally.upSoleLen = (tally.upSoleLen || 0) + plen;
            tally.upSoleN = (tally.upSoleN || 0) + GA_LP.soleNails * (Math.floor(plen / GA_LP.sole) + 1);
          }
          _FM.role('seal');
          _asmCode(ctx, SILL_SEAL.code, 'side');
          /* AND THE SILL SEAL UNDER IT: INS01003, a 150 x 5 foam strip between
             the slab and the sole plate, air seal and damp break. It has been in
             the catalogue since the wall was written and was never laid or
             billed; one strip per panel, the plate's own length, by the metre. */
          var gaSealMat = new T.MeshStandardMaterial({
              color: isNew(LAYER + '.seal') ? MAGENTA : 0x9AB0C8, roughness: 0.9 });
          /* GA: the seal only on the slab (garageapt-v4, an upper storey stands
             on its subfloor); everyone else under every sole plate */
          var sealHere = (onSlab || !GA) && !s.tierUp;   /* core-v804: a wall on a wall stands on its cap, not on the floor */
          for (gaPr = 0; gaPr < (sealHere ? gaRuns.length : 0); gaPr++) {
            var sealM = place(SILL_SEAL.w, SILL_SEAL.t, gaRuns[gaPr][1] - gaRuns[gaPr][0], (gaRuns[gaPr][0] + gaRuns[gaPr][1]) / 2, s.y0 - SILL_SEAL.t / 2, gaSealMat);
            sealM.name = 'sill-seal';
            if (onSlab && GA) sealM.userData.gaStep = '2d';
          }
          if (sealHere) tally.sealLen = (tally.sealLen || 0) + plen;
          _asmCode(ctx, STUD.code, 'side');   /* the panel's top plate is the frame's, not the seal's */
          /* on a butted (end) wall under an extended eave the panel's own top
             plate runs out with the cap: the double plate goes the whole way */
          var topLenE = topLen, topMidE = topMid;
          if (!through && (eaveExtA || eaveExtB)) {
            var endA = (Math.abs(a - STUD.d) < 0.01), endB = (Math.abs(b - (s.len - STUD.d)) < 0.01);
            var addA = endA ? eaveExtA : 0, addB = endB ? eaveExtB : 0;
            topLenE = topLen + addA + addB; topMidE = topMid + (addB - addA) / 2;
          }
          /* THE HEIGHT IS READ WHERE THE PIECE IS SET OUT. The geometry went to
             topMidE (the middle after the run-out) while the height came from
             headAt(topMid), the middle BEFORE it -- on a raked wall under an
             extended eave that is half the run-out along the slope, 207 mm at
             15 degrees over a 1545 run-out, so the lower plate sat inside the
             panel (found in shed-v7, Marek). */
          var topY = headAt(topMidE) - STUD.t * 1.5;
          var topLenD = topLenE - sjA - sjB, topMidD = topMidE + (sjA - sjB) / 2;
          _FM.role('top');
          _FM.markAt(mid);   /* this frame's plate, however far it runs out */
          var topM = null;
          if (Math.abs(tilt) < 1e-4) {
            topM = place(STUD.d, STUD.t, topLenD, topMidD, topY, woodMat);
          } else {
            topM = placeRaked(STUD.d, STUD.t, topLenD, topMidD, topY, tilt, woodMat);
          }
          _FM.markAt(null);
          if (topM) topM.userData.cutLen = topLenE / Math.max(0.2, Math.cos(tilt));
          tally.plateLen += (s.tierNoSole ? 0 : plen) + topLenE;

          _asmCode(ctx, LEDGER.code, 'side');
          /* THE LEDGER, and only when the studs run past the roof. The joists
             cannot bear on a plate that is now 800 mm in the air, so they hang off
             this instead, nailed to the inside face at the old bearing line. */
          /* THE BEARER ONLY GOES ON THE WALLS THAT CARRY SOMETHING.

             It was run round all four, and the two the rafters lie PARALLEL to
             held nothing at all -- a timber picture frame inside the parapet,
             paid for and doing no work. The rafters land on the two walls they
             cross, and those are the through walls, so only those get a bearer. */
          /* ONLY UNDER A PARAPET. On a mono the studs are raked and the ceiling
             members land on the TOP plates -- the bearing line is up there, not
             down here, so a bearer on the inside face holds nothing and is just
             a lump in the room. It belongs where the wall carries on past the
             roof and the plate is out of reach, which is the flat roof with an
             upstand: there the ceiling members come off this and tie into the
             side of the studs right behind it. */
          var Rp9 = API.STATE.roof || {};
          if (parRise > 0 && through && Rp9.type === 'flat' && Rp9.parapet) {
            /* A LEDGER IS A PLANK, NOT A POST. This was 235 x 235 -- as thick as
               it was deep, five times the timber it needs and a lump running
               round every inside face. It is 50 thick and as deep as the joist,
               fixed flat to the studs, which is all a ledger ever is. */
            /* HOUSED IN THE WALL, NOT NAILED ON THE FACE OF IT.

               It was fixed flat to the inside face, so it stood 60 mm proud of
               the studs -- and the lining that goes over it is a 40 mm service
               void and a 13 mm board, which is 53. Seven millimetres of bearer
               came through the plasterboard right round the room, at the one
               height where a wall meets a ceiling and nobody would accept it.

               So it goes INSIDE the stud depth, its inner face flush with theirs
               and the whole of it inside the wall. The lining then covers it
               like any other stud and nothing shows.

               What it costs: the bearer is let in between the studs instead of
               lapping across them, so it is fixed through the stud sides at each
               end of every bay rather than face-nailed. It carries the same load
               on the same bearing line -- the rafters still sit ON its top face,
               which has not moved -- and it takes 60 mm out of the insulated
               bay over the depth of the joist. */
            var lx = s.x0 + ux * ((a + b) / 2) - s.nx * (STUD.d - LEDGER.t / 2);
            var lz = s.z0 + uz * ((a + b) / 2) - s.nz * (STUD.d - LEDGER.t / 2);
            /* back in timber -- the ledger was last version's change and it is
               accepted */
            /* THE BEARER, NOT A HANGER. The rafters sit ON its top face rather
               than hanging off its side on straps -- load straight into timber
               instead of through fasteners. Its top is the bearing line, so its
               depth hangs below. */
            var led = new T.Mesh(new T.BoxGeometry(LEDGER.t, roofSection().d, plen), mat);
            led.position.set(lx, bearAt(mid) - roofSection().d / 2, lz);
            led.rotation.y = ang;
            _FM.role('ledger');
            _panelMark(led, (a + b) / 2);
            g.add(led);
            tally.ledger = (tally.ledger || 0) + plen;
          }

          _asmCode(ctx, STUD.code, 'side');
          /* EVERY STUD ITS OWN LENGTH. On a raked wall no two are the same, and
             asking the roof over each one is the whole of it.

             AND A STUD THAT WOULD STAND IN A HOLE IS CUT INSTEAD. What is left of
             it above the head and below the sill is a cripple -- same timber,
             same line, shorter. That is what makes an opening frame rather than a
             gap: nothing moves off the module, the module is interrupted. */
          /* THE MODULE IS SET OUT FROM THE BUILDING, NOT FROM THIS PANEL. */
          /* where this panel starts in the world, resolved onto the axis it runs
             along and signed by the direction it runs in -- so a stud at panel
             position p lands on world (datum + p) whichever way the wall goes */
          var wx = s.x0 + ux * a, wz = s.z0 + uz * a;
          var alongX = Math.abs(ux) > Math.abs(uz);
          var datum = (alongX ? wx : wz) * (alongX ? (ux > 0 ? 1 : -1) : (uz > 0 ? 1 : -1));
          /* THE MODULE IS BACK, AND IT IS THE ONLY THING THAT DECIDES.

             Studs from corner to corner on the sheet module, and nothing else
             has a say -- not an opening, not a panel joint, not a king that is
             no longer built. That was the whole argument tonight and it is now
             one line: the positions come from the layout and the wall places
             them (Marek: vyramuj vsechny panely svislymi profily bez ohledu na
             otvory s rozteci podle sheets). */
          /* THE PERIMETER ONLY.

             Not one stud between the corners: the sole plate, the two top plates
             and a post at each end of the panel, and nothing else. Every rule
             about spacing, openings and panels has been taken out of this wall,
             so what is left cannot be wrong about any of them -- it is the frame
             and nothing but (Marek: ja chci jen prosty ram bez sloupku, jen
             obvod). The layout stays in the file, unused, for the module to be
             rebuilt on once it is proven on numbers. */
          /* THE LAYOUT IS THE WHOLE WALL, CORNER TO CORNER.

             It was laid out over the panel's run, which on a wall that butts
             into another starts a stud's depth in -- so the product's lines and
             the core's lines were drawn over two different lengths and came out
             53 to 182 mm apart. Both were internally right; they were answers
             about different walls.

             Corner to corner, over the section's own length, which is exactly
             what the core measures when it pushes an opening against a stud. The
             two cannot disagree because they are now the same sum (Marek: bud
             tam mas jeste jine pravidlo a nebo to primknuti nefunguje). */
          var pos = _studsOf(s);
          for (var k = 0; k < pos.length; k++) {
            var at = pos[k];
            /* each frame builds only its own studs; the first and last take
               everything past their outer end, as the single span did */
            if ((j > 0 && at < a - 1e-6) || (j < spans.length - 1 && at >= b - 1e-6)) continue;
            /* core-v804: a stacked wall cut short builds no stud past its run */
            if (tierClip && (at < from + STUD.t / 2 - 1e-6 || at > to - STUD.t / 2 + 1e-6)) continue;
            var hTop = headAt(at) - STUD.t * 2;      /* under both top plates */
            var base = s.y0 + STUD.t;
            var op = _openingAt(ctx, s, at);
            /* core-v804: A MODULE STUD A STUD'S WIDTH OFF A NEW KING OR JACK IS
               LEFT OUT (H.dropNear, the centre distance; RUSSWORX SHED). The
               layout stud and the opening's post stood side by side with a
               slot between them -- two members where a framer stands one. So
               is a layout cripple that slot's width off its hole's jack. A
               stud that IS a jack or a king stays. */
            if (H.dropNear > 0 && framesF.length && _modRole(at) === 'stud') {
              var dnRole = _studPieces(at, base, hTop, framesF).role, dropBy = null, qDn, sDn, rDn, mDn, sdsDn = ['L', 'R'], rlDn = ['jack', 'king'];
              if (dnRole === 'full' || dnRole === 'hole') {
                for (qDn = 0; qDn < framesF.length && !dropBy; qDn++) {
                  for (sDn = 0; sDn < 2 && !dropBy; sDn++) {
                    for (rDn = 0; rDn < 2 && !dropBy; rDn++) {
                      if (dnRole === 'hole' && rDn === 1) continue;   /* a cripple stands in the hole: only its jack */
                      mDn = framesF[qDn].plan[sdsDn[sDn]][rlDn[rDn]];
                      if (mDn && mDn.isNew && !mDn.out && Math.abs(mDn.x - at) > 1e-4 && Math.abs(mDn.x - at) < H.dropNear) dropBy = rlDn[rDn] + ' ' + Math.round(mDn.x * 1000);
                    }
                  }
                }
              }
              if (dropBy) {
                tally.dropped = (tally.dropped || 0) + 1;
                cutL.push(Math.round(at * 1000) + (dnRole === 'hole' ? ' cripples' : ' stud') + ' LEFT OUT beside the ' + dropBy);
                continue;
              }
            }

            /* AND A KING ALREADY STANDING HERE IS THIS STUD.

               The module stud and the opening's king were placed by two separate
               passes that never asked about each other, so wherever a king landed
               near a module line the wall got two full-height studs a few
               centimetres apart -- a doubled post with a slot down it. On the
               other side of the same window the module line fell INSIDE the hole
               instead, got cut into cripples, and there was no neighbour at all.
               Same window, two different frames, and neither of them chosen.

               A king within a stud and a half of the module IS the module stud,
               moved to suit the hole. It is not a second one. Further away than
               that they are genuinely two members and both stay. */
            /* THE KING TEST IS OUT WITH THE KINGS.

               It drops a post standing near where an opening's king would be --
               and the kings are gone, so it was dropping posts to make room for
               members that are never built. On two walls that happened to be the
               end post, which is why a wall came out with three sides (Marek: na
               dvou stenach chybi koncovy svisly profil). */

            /* AND THE HOLE DOES NOT GET A VOTE HERE EITHER.

               A post standing where an opening is was skipped -- the last place
               in this wall where a hole still decided what got built. It is why
               two walls came out with the end post missing: the opening reached
               it. The frame is the frame; where an opening falls is a question
               for the framing, and the framing is gone (Marek: nemas prece ted
               vubec brat v potaz otvory). */
            {
              var hLen = hTop - base;
              if (hLen < 0.05) continue;
              /* THE STUD STAYS ON ITS LINE AND AN OPENING TAKES ITS PART OUT.
                 What is left is one piece, or a jack and a cripple, or two
                 cripples -- _studPieces decides, from the frame plan. */
              var cutS = _studPieces(at, base, hTop, framesF), pcS, pq, anyPc = false, nPc = 0;
              /* garageapt-v26: a stud of a portal frame's panel stops under
                 the header; over it the pony wall's short stud */
              if (GA && cutS.role !== 'gave way') { var pfC = GA.pfCut(framesF, at, base, hTop); if (pfC) cutS = pfC; }
              pcS = cutS.pcs;
              /* THE STUD AN OPENING LEANS ON, IN COLOUR (the check above says
                 which one it is). */
              var leanM = mat, lq2;
              for (lq2 = 0; lq2 < leanAt.length; lq2++) {
                if (Math.abs(leanAt[lq2] - at) < STUD.t * 0.4) {
                  leanM = new T.MeshStandardMaterial({ color: 0xE6194B, roughness: 0.85, metalness: 0.0 });
                  break;
                }
              }
              _FM.role((cutS.role === 'hole') ? 'cripple' : _modRole(at));
              for (pq = 0; pq < pcS.length; pq++) {
                var p0 = pcS[pq][0], p1 = pcS[pq][1];
                if (p1 - p0 < 0.05) continue;
                if (!_vFree(at, p0, p1, 'stud')) continue;
                /* only a piece that reaches the plate is cut to its rake */
                placeCut(STUD.d, p1 - p0, STUD.t, at, (p0 + p1) / 2, leanM,
                         (p1 >= hTop - 1e-6) ? tilt : 0);
                tally.studs++;
                tally.studLen += p1 - p0;
                anyPc = true;
                nPc++;
                if (cutS.role === 'hole') tally.cripples++;
              }
              if (anyPc) {
                /* core-v804: the lower of two stacked walls carries no rafter,
                   and a stud line two stacked walls share is published once */
                if (through && !(s.tierCap > 0)) _bearStuds.push({ x: s.x0 + ux * at, z: s.z0 + uz * at });
                var wsSeen = false, wsq;
                if (s.tierUp || s.tierCap > 0) { for (wsq = 0; wsq < _wallStuds.length; wsq++) if (_wallStuds[wsq].seg === s.id && Math.abs(_wallStuds[wsq].at - at) < 1e-4) { wsSeen = true; break; } }
                if (!wsSeen) _wallStuds.push({ seg: s.id, at: at, x: s.x0 + ux * at, z: s.z0 + uz * at });
              }
              if (cutS.role === 'hole') cutL.push(Math.round(at * 1000) + ' ' + nPc + 'pc');
              if (cutS.role === 'gave way') {
                cutL.push(Math.round(at * 1000) + ' GAVE WAY to the jack, the frame reaches into it');
                tally.gaveWay = (tally.gaveWay || 0) + 1;
              }
              continue;
            }

            /* AND A STUD CROSSING A HOLE SIMPLY STOPS.

               The pieces over and under an opening were built here, which made
               the hole a partner in the set-out. It is not one: the module lays
               the studs, a hole interrupts whichever of them it crosses, and
               nothing is built in its place until the framing comes back
               (Marek: chci videt jen prazdny ram). */
          }
        }

        /* THE PANEL LOOP IS NOT WHERE THE WALL IS BUILT.

           I turned marking off the moment the span loop ended, taking that loop
           for the whole panel. It is not: the loop lays the sole plate, the
           lower top plate and the framing over and under an opening, while the
           STUDS -- the studs, the kings, the jacks, the header plies and the
           sills -- are placed further down, against the wall as a whole. So a
           panel sheet carried its plates and the two studs that happened to fall
           in the loop, which is the picture of half a panel that came back four
           times (Marek: neni tam jediny cely panel).

           Marking stays on for the whole section now. Which panel a piece is in
           is decided by where it stands, and a piece standing in no span is not
           marked, so the rule is safe wherever the piece is placed. It goes off
           once, just before the cap plate -- the one member that genuinely
           belongs to no panel. */

        _asmCode(ctx, STUD.code, 'side');
        /* THE UPPER TOP PLATE IS WHY WALLS HAVE TWO. It is not a second plate on
           the same panel -- it runs the WHOLE segment in one piece, over every
           panel joint below it, and past the corner on a through wall. That lap
           is what ties the panels into a wall. */
        /* THE PLATES STOP AT THE LAST TRUSS, WHICH IS THE WALL.

           They used to run out past the gable by the whole rake, and there is
           nothing out there for them to carry. That extension existed to hold a
           barge rafter -- and a trussed roof has no barge rafter. The last truss
           sits inside the wall, so the plate has nothing to reach for, and the
           overhang past the gable is made by the roof build-up cantilevering off
           the sheathing, not by timber sticking out of the wall.

           The posts that held the old extension up went with it. */
        /* WHO CARRIES THE RAFTERS THAT HANG PAST THE END WALLS.

           GABLE: nobody has to. Its trusses stop at the walls, the rake overhang
           is made by running the sheathing wide, and the plates therefore stop at
           the wall too -- which is the shortening you asked for.

           MONO: the opposite. Its roof plane really does reach 595 mm past every
           wall, so the outermost rafter is out there and something has to be
           under it. The plates on the two walls the rafters run along carry on by
           exactly that much.

           PARAPET: the deck stops at the outer face of the panels, so nothing
           hangs past anything and the plates stay put.

           It is one rule read three ways: the plate goes as far as the roof does,
           and on a gable the roof stops at the wall. */
        /* rakeExt is worked out before the panels are built, because the end
           panels need to know how far their top plate runs out. */
        var capFrom = through ? -rakeExtA : (STUD.d - eaveExtA);
        var capLen = (through ? s.len + rakeExtA + rakeExtB : s.len - STUD.d * 2 + eaveExtA + eaveExtB);
        /* core-v804: a stacked wall's cap stops where its run does */
        if (tierClip) {
          var capEnd = capFrom + capLen;
          if (typeof s.tierFrom === 'number') capFrom = Math.max(capFrom, s.tierFrom);
          if (typeof s.tierTo === 'number') capEnd = Math.min(capEnd, s.tierTo);
          capLen = Math.max(0, capEnd - capFrom);
        }
        var capY = headAt(capFrom + capLen / 2) - STUD.t / 2;

        /* the chamfer is what tells the upper plate from the lower one */
        /* off for this one member only: the kings, jacks, header plies and
           sills come AFTER it and every one of them belongs to a panel */
        _FM.off();
        if (Math.abs(tilt) < 1e-4) {
          place(STUD.d - CHAMFER * 2, STUD.t - CHAMFER, capLen, capFrom + capLen / 2, capY, woodMat);
        } else {
          placeRaked(STUD.d - CHAMFER * 2, STUD.t - CHAMFER, capLen,
                     capFrom + capLen / 2, capY, tilt, woodMat);
        }
        _FM.on();

        /* NO NOGGINGS. I put a row of horizontal blocking across every panel of
           a raked wall, because a mono-pitch raises one wall and a 60 mm stud
           over about three metres wants holding about its weak axis. The
           engineering is real, but it was my addition, not a request, and it put
           a profile across the middle of every panel in the model.

           It is out. Two things worth knowing if it ever comes back: the
           threshold fired at 3.00 m and the default wall is 3.05 m at the high
           side, so it appeared on the flattest pitch there is -- a rule that
           trips on a rounding is the wrong rule. And the cheaper answer to a tall
           wall is a deeper stud, which costs one line in the section table and
           nothing on screen. */

        /* THE OPENINGS IN THIS SEGMENT, framed after the studs so the jacks and
           kings land on top of the module rather than being interrupted by it. */
        (function () {
          /* WHAT ACTUALLY WENT UP IN THIS SEGMENT, AND WHAT ENDED UP NEAR WHAT.

             Three versions have now gone at a doubled post above the openings and
             I have been reading it off a screenshot each time. The four placers
             each believe they are right, so the only way to see which two agree
             is to list them: every vertical with its position, its kind and the
             band it covers, and then every pair closer together than a quarter of
             a metre.

             If a pair is listed, the two names in it are the two rules that
             collided, and that is the whole answer rather than a guess at it. */
          /* ONE LINE, AND ONLY WHEN THERE IS SOMETHING WRONG.

             The first version of this listed every vertical in every segment,
             which on a real building is hundreds of entries -- he cannot get that
             off a tablet and I would not read it either. A diagnostic nobody can
             carry is not a diagnostic.

             So it prints nothing at all unless two members are SIDE BY SIDE: on
             different lines, closer than 250 mm, and overlapping in height. A
             cripple over a header and a cripple under a sill share a line and do
             not overlap -- they are the same stud interrupted, and they are not
             reported. Two things at the same height a few centimetres apart are
             the fault, and the two names are the two rules that collided. */
          function _vReport() {
            var L = _vAt.slice().sort(function (a, b) { return a.at - b.at; });
            var bad = [], q4, r4;
            for (q4 = 0; q4 < L.length; q4++) {
              for (r4 = q4 + 1; r4 < L.length; r4++) {
                var d4 = L[r4].at - L[q4].at;
                if (d4 >= 0.25) break;
                if (d4 < 0.001) continue;
                if (L[r4].lo >= L[q4].hi - 0.05 || L[q4].lo >= L[r4].hi - 0.05) continue;
                /* KING BESIDE JACK IS THE FRAME, NOT A FAULT. They are meant to
                   be a stud apart on both sides of every opening, so reporting
                   them would bury the real finding under the thing that is
                   working -- which is how a diagnostic becomes noise. */
                var kk = L[q4].k + '+' + L[r4].k;
                if (kk === 'king+jack' || kk === 'jack+king' || kk === 'joint+joint') continue;
                bad.push(L[q4].k + '+' + L[r4].k + ' @' + Math.round(L[q4].at * 1000) +
                         ' gap=' + Math.round(d4 * 1000) +
                         ' y=' + Math.round(Math.max(L[q4].lo, L[r4].lo) * 1000) + '-' +
                         Math.round(Math.min(L[q4].hi, L[r4].hi) * 1000));
              }
            }
            if (bad.length) ctx.log('DBL      ' + s.id + '  ' + bad.join('  |  '));

            /* AND WHICH PANEL EACH VERTICAL ENDED UP IN.

               The colours are the only way he can see the panel split from
               outside, so when they look wrong the useful thing is not the split
               but which piece each stud was coloured as. Two studs at an opening
               edge that report the SAME panel is the fault, in one short line. */
            if (byPanel && (s.openings || []).length) {
              var seen = [], q6;
              for (q6 = 0; q6 < _vAt.length; q6++) {
                var e6 = _vAt[q6], pj = -1, j6;
                for (j6 = 0; j6 < spans.length; j6++) {
                  if (e6.at >= spans[j6][0] - 0.001 && e6.at <= spans[j6][1] + 0.001) { pj = j6; break; }
                }
                if (e6.k === 'king' || e6.k === 'jack') {
                  seen.push(Math.round(e6.at * 1000) + ':' + e6.k + '=P' + pj);
                }
              }
              if (seen.length) ctx.log('PNL      ' + s.id + '  ' + seen.join('  '));
            }
          }

          /* THE OPENING FRAMING COMES BACK, BUILT ON THE MODULE.

             A doubled post each side of every opening: a jack against the frame
             and a king outside it. Nothing that stands is moved -- the module
             studs stay where the layout put them and the opening stays where the
             core pushed it. Where a module stud already stands close enough it
             IS the jack or the king; everything else is added (_dblPlan says
             which). The hole itself is not framed yet: no header, no sill, the
             jacks run full height (Marek: kolem okna postavit zdvojeny profil). */
          var q, frL = [];
          function mmF(v) { return Math.round(v * 1000); }
          /* a module stud serving in the pair is renamed, so the side-by-side
             report reads it as frame and not as a clash */
          function _vTag(x2, k2) {
            for (var q7 = 0; q7 < _vAt.length; q7++) {
              if (Math.abs(_vAt[q7].at - x2) < 0.001 && _vAt[q7].k === 'stud') _vAt[q7].k = k2;
            }
          }
          /* a flat member along the wall, [x0, x1] long, [y0, y1] high; the
             outer one of a pair is drawn with the seam on the side given */
          function flat(x0, x1, y0f, y1f, seamSide) {
            var L = x1 - x0;
            if (L < 0.02 || y1f - y0f < 0.01) return;
            if (seamSide) {
              var hh = (y1f - y0f) - DBL_SEAM;
              var yc = (seamSide > 0) ? (y1f - hh / 2) : (y0f + hh / 2);
              place(STUD.d - CHAMFER * 2, hh, L, (x0 + x1) / 2, yc, woodMat);
            } else {
              place(STUD.d, y1f - y0f, L, (x0 + x1) / 2, (y0f + y1f) / 2, woodMat);
            }
          }
          /* THE SECOND STUD AT EACH FRAME JOINT, face to face with the module
             stud it doubles, drawn with the seam like every pair */
          var jn;
          for (jn = 0; jn < jointsF.length; jn++) {
            var JF = jointsF[jn], jBase = s.y0 + STUD.t, jTop = headAt(JF.x2) - STUD.t * 2, jH = jTop - jBase;
            if (JF.pair) { tally.pairJoints = (tally.pairJoints || 0) + 1; continue; }
            if (jH < 0.05) continue;
            _vTag(JF.c, 'joint');
            if (!_vFree(JF.x2, jBase, jTop, 'joint')) {
              ctx.log('frame joint W' + i + ' second stud at ' + mmF(JF.x2) + ' CLASH, dropped');
              continue;
            }
            _FM.role('joint');
            placeCut(STUD.d - CHAMFER * 2, jH, STUD.t - DBL_SEAM, JF.x2 + JF.side * DBL_SEAM / 2,
                     jBase + jH / 2, woodMat, tilt);
            tally.studs++;
            tally.studLen += jH;
            tally.jointStuds = (tally.jointStuds || 0) + 1;
            /* the two frames are nailed together through the pair on site */
            tally.stitch = (tally.stitch || 0) + Math.floor(jH / (GA_LP ? GA_LP.bwp : DBL_STITCH)) + 1;   /* garageapt-v28: 16d at 12 in. in a braced wall panel (was DBL_STITCH) */
            if (GA_LP) {
              tally.stitchWas = (tally.stitchWas || 0) + Math.floor(jH / DBL_STITCH) + 1;
              tally.stitchNow = (tally.stitchNow || 0) + Math.floor(jH / GA_LP.bwp) + 1;
            }
          }
          /* core-v801: AN OPENING ABOVE ANOTHER (RUSSWORX SHED's clerestory over
             his lower windows and door, Marek: v oknech jsou framing lumber).
             The jacks and kings ran full height, so the lower unit's pair stood
             in the upper unit's glass. Another opening's hole now cuts them as it
             cuts a module stud (studPieces): the piece under its sill and the
             cripple over its header. Nothing changes where no opening stands
             over another. */
          function _otherHolePcs(x9, lo9, hi9, fSelf) {
            var pcs9 = [[lo9, hi9]], q9, p9;
            for (q9 = 0; q9 < framesF.length; q9++) {
              var g9 = framesF[q9];
              if (g9 === fSelf) continue;
              if (!((x9 + STUD.t / 2 > g9.eL + 0.0005) && (x9 - STUD.t / 2 < g9.eR - 0.0005))) continue;
              var b09 = g9.ySB, b19 = g9.yHT, nx9 = [];
              for (p9 = 0; p9 < pcs9.length; p9++) {
                var l9 = pcs9[p9][0], h9 = pcs9[p9][1];
                if (b19 <= l9 || b09 >= h9) { nx9.push(pcs9[p9]); continue; }
                if (b09 > l9) nx9.push([l9, b09]);
                if (b19 < h9) nx9.push([b19, h9]);
              }
              pcs9 = nx9;
            }
            return pcs9;
          }
          for (q = 0; q < framesF.length; q++) {
            tally.openings++;
            var fF = framesF[q], oF = fF.o, wfF = fF.wf, planF = fF.plan;
            if (PORTAL && oF && oF.code === PORTAL.code && typeof fF.yHB === 'number' && isFinite(fF.yHB)) _portalHeadY[s.id] = fF.yHB;
            var sidesF = ['L', 'R'], txtF = [], sq;
            var baseF = s.y0 + STUD.t;
            for (sq = 0; sq < sidesF.length; sq++) {
              var dirF = (sq === 0) ? -1 : 1;
              var sdF = planF[sidesF[sq]], bitsF = [], pairOk = true, roles = ['jack', 'king'], rq;
              for (rq = 0; rq < roles.length; rq++) {
                var role = roles[rq], mb = sdF[role];
                if (!mb) { bitsF.push(role + ' none'); pairOk = false; continue; }
                if (mb.out) { bitsF.push(role + ' past the wall end'); pairOk = false; continue; }
                if (!mb.isNew) {
                  _vTag(mb.x, role);
                  tally.dblOld = (tally.dblOld || 0) + 1;
                  bitsF.push(role + '=stud ' + mmF(mb.x) + (mb.gap > 0.0005 ? ' gap ' + mmF(mb.gap) : ''));
                  continue;
                }
                /* both run to the plate; the header is fitted between the jacks */
                var topF = headAt(mb.x) - STUD.t * 2;
                /* garageapt-v26: on a portal frame's panel side both stop
                   under the header, which runs over them */
                if (fF.pf && fF.pf[sidesF[sq]]) topF = Math.min(topF, fF.pf.yHB);
                var hF = topF - baseF;
                if (hF < 0.05) { bitsF.push(role + ' no height'); pairOk = false; continue; }
                /* A CLAMPED MEMBER IS BUILT EVEN IF SOMETHING ELSE IS NEAR IT.
                   It is the post that closes the frame at the end of a wall, and
                   the thing it reads as a clash with is a module stud inside the
                   glazing, which is cut away anyway (Marek: ram kolem okna musi
                   byt uzavreny). Everywhere else a clash still drops it. */
                if (!mb.clamped && !_vFree(mb.x, baseF, topF, role)) {
                  bitsF.push(role + ' ' + mmF(mb.x) + ' CLASH, dropped');
                  pairOk = false;
                  continue;
                }
                if (mb.clamped) bitsF.push(role + ' clamped to the wall end');
                _FM.role(role);
                var pcsJK = _otherHolePcs(mb.x, baseF, topF, fF), pj9, nJK = 0;
                if (pcsJK.length !== 1 || pcsJK[0][1] - pcsJK[0][0] < hF - 1e-6) {
                  tally.jkCut = (tally.jkCut || 0) + 1;
                  bitsF.push(role + ' cut by the opening over it (' + pcsJK.map(function (pp) { return mmF(pp[0] - s.y0) + '-' + mmF(pp[1] - s.y0); }).join(', ') + ')');
                }
                for (pj9 = 0; pj9 < pcsJK.length; pj9++) {
                  var y09 = pcsJK[pj9][0], hP9 = pcsJK[pj9][1] - y09;
                  if (hP9 < 0.05) continue;
                  if (role === 'jack') {
                    placeCut(STUD.d, hP9, STUD.t, mb.x, y09 + hP9 / 2, woodMat, tilt);
                  } else {
                    /* the king is the outer member: short by the seam on the jack
                       side, eased off both faces */
                    var seamK = (sdF.jack && !sdF.jack.out) ? DBL_SEAM : 0;
                    placeCut(STUD.d - CHAMFER * 2, hP9, STUD.t - seamK, mb.x + dirF * seamK / 2,
                             y09 + hP9 / 2, woodMat, tilt);
                  }
                  if (nJK++) tally.studs++;
                  tally.studLen += hP9;
                }
                tally.studs++;
                if (role === 'jack') tally.jacks++; else tally.kings++;
                tally.dblNew = (tally.dblNew || 0) + 1;
                bitsF.push(role + ' NEW ' + mmF(mb.x));
              }
              /* stitched only where the two are face to face; an existing king
                 standing off the jack by a few centimetres keeps its gap */
              if (pairOk && sdF.jack && sdF.king) {
                var gapJK = sdF.king.isNew ? 0 : sdF.king.gap;
                if (gapJK < 0.0005) {
                  var hS = ((fF.pf && fF.pf[sidesF[sq]]) ? Math.min(headAt(sdF.jack.x) - STUD.t * 2, fF.pf.yHB) : (headAt(sdF.jack.x) - STUD.t * 2)) - baseF;
                  /* core-v801: only the pair's length that stands (an opening over it cuts it) */
                  var pcsS = _otherHolePcs(sdF.jack.x, baseF, baseF + hS, fF), hS9 = 0, ps9;
                  for (ps9 = 0; ps9 < pcsS.length; ps9++) hS9 += Math.max(0, pcsS[ps9][1] - pcsS[ps9][0]);
                  if (!(pcsS.length === 1 && pcsS[0][0] === baseF && pcsS[0][1] === baseF + hS)) hS = hS9;
                  tally.stitch = (tally.stitch || 0) + Math.floor(hS / (GA_LP ? GA_LP.bwp : DBL_STITCH)) + 1;   /* garageapt-v28: 12 in. (was DBL_STITCH) */
                  if (GA_LP) {
                    tally.stitchWas = (tally.stitchWas || 0) + Math.floor(hS / DBL_STITCH) + 1;
                    tally.stitchNow = (tally.stitchNow || 0) + Math.floor(hS / GA_LP.bwp) + 1;
                  }
                  tally.pairs = (tally.pairs || 0) + 1;
                } else {
                  tally.apart = (tally.apart || 0) + 1;
                }
              }
              txtF.push(sidesF[sq] + ' ' + bitsF.join(', '));
            }

            /* THE HEADER: two plies laid flat, BETWEEN the jacks, nailed through
               them into its ends -- the same span as the sill */
            var hdLen = fF.xh1 - fF.xh0, hdTxt;
            /* garageapt-v3: the beam the check wants and the wall has no room
               for over the unit (core-v606 keeps the old header then) */
            if (fF.beamShort > 0 && PORTAL) {
              hdTxt = 'PORTAL BEAM wanted ' + mmF(fF.beamWant || PORTAL.d) + ', the wall has ' +
                      mmF(Math.max(0, (fF.beamWant || PORTAL.d) - fF.beamShort)) + ' over the hole -- flat header kept';
            } else if (fF.beamShort > 0 && HDR_BEAM) {
              tally.noRoom = tally.noRoom || [];
              tally.noRoom.push((s.id || ('W' + i)) + ' ' + (fF.o.code || fF.o.kind) + ' ' + mmF(fF.o.w) + ': LVL ' + mmF(fF.beamWant || HDR_BEAM.d) +
                                ' wanted, ' + mmF(Math.max(0, (fF.beamWant || HDR_BEAM.d) - fF.beamShort)) + ' over the unit');
            }
            if (fF.pf && GA) {
              /* garageapt-v26 (unit 16b): THE PORTAL FRAME (CS-PF, GA_PF) --
                 the header's 3 plies flush with the sheathed face, over the
                 door and its panel(s); the posts at its ends doubled; a
                 tension strap each side, inside; the 3 in. nailing */
              var PFh = fF.pf, hxa = PFh.xa, hxb = PFh.xb, hL = hxb - hxa, wP = GA_PF.plies * GA_PF.ply, kP, sdP;
              _FM.role('header');
              for (kP = 0; kP < GA_PF.plies; kP++) {
                placeAcross(GA_PF.ply - DBL_SEAM, fF.yHT - fF.yHB, hL, (hxa + hxb) / 2, (fF.yHB + fF.yHT) / 2,
                            STUD.d / 2 - wP + GA_PF.ply * (kP + 0.5), woodMat);
              }
              tally.pfLen = (tally.pfLen || 0) + hL * GA_PF.plies;
              tally.pfBeams = (tally.pfBeams || 0) + 1;
              tally.heads = (tally.heads || 0) + 1;
              tally.stitch = (tally.stitch || 0) + (GA_PF.plies - 1) * 2 * (Math.floor(hL / HDR_BEAM.nailStep) + 1) + 2 * GA_PF.plies * 2;
              var pony = (headAt((hxa + hxb) / 2) - STUD.t * 2) - fF.yHT, posts = 0, straps = 0, pfTxt = [];
              /* the posts */
              for (sdP = 0; sdP < 2; sdP++) {
                var SP = PFh[sdP ? 'R' : 'L'];
                if (!SP || SP.post == null) continue;
                var hP = PFh.yHB - baseF;
                if (hP < 0.05 || !_vFree(SP.post, baseF, PFh.yHB, 'post')) { pfTxt.push((sdP ? 'R' : 'L') + ' post ' + mmF(SP.post) + ' CLASH, dropped'); continue; }
                _FM.role('post');
                placeCut(STUD.d, hP, STUD.t, SP.post, baseF + hP / 2, woodMat, 0);
                tally.studs++; tally.studLen += hP;
                tally.stitch = (tally.stitch || 0) + Math.floor(hP / GA_LP.bwp) + 1;
                posts++;
              }
              /* the straps: on the inside face, half on the jack, half on the
                 header's inner face (not drawn through the wall's marker) */
              var mStrap = new T.MeshStandardMaterial({ color: isNew(LAYER + '.portal') ? MAGENTA : 0x9AA3AB, roughness: 0.4, metalness: 0.7 });
              var pfBox = function (w, h, d, along, y, off) {
                var bx = new T.Mesh(new T.BoxGeometry(w, h, d), mStrap);
                bx.position.set(s.x0 + ux * along - s.nx * (STUD.d / 2) + s.nx * off, y, s.z0 + uz * along - s.nz * (STUD.d / 2) + s.nz * off);
                bx.rotation.y = ang;
                bx.name = 'ga-portal-strap';
                g.add(bx);
              };
              for (sdP = 0; sdP < 2; sdP++) {
                var jkP = planF[sdP ? 'R' : 'L'].jack;
                if (!jkP || jkP.out) continue;
                var hs2 = GA_PF.strapL / 2;
                pfBox(GA_PF.strapT, hs2, GA_PF.strapW, jkP.x, PFh.yHB - hs2 / 2, -STUD.d / 2 - GA_PF.strapT / 2);
                pfBox(GA_PF.strapT, hs2, GA_PF.strapW, jkP.x, PFh.yHB + hs2 / 2, STUD.d / 2 - wP - GA_PF.strapT / 2);
                straps++;
              }
              tally.pfStraps = (tally.pfStraps || 0) + straps;
              tally.pfPosts = (tally.pfPosts || 0) + posts;
              /* THE NAILING: 3 in. apart on every member of the panels and
                 along their plate, a 3 in. grid over the header; the wall's
                 own 22 a square metre already counted there taken off */
              var gP = GA_PF.grid, rowsV = Math.floor((PFh.yHB - baseF) / gP) + 1, nNail = 0, aStd = 0, posL = _studsOf(s), zq, pq3;
              var zones = [PFh.zL, PFh.zR];
              for (zq = 0; zq < 2; zq++) {
                var ZZ = zones[zq];
                if (!ZZ) continue;
                var nMem = 0;
                for (pq3 = 0; pq3 < posL.length; pq3++) if (posL[pq3] - STUD.t / 2 >= ZZ[0] - 0.0005 && posL[pq3] + STUD.t / 2 <= ZZ[1] + 0.0005) nMem++;
                var sdZ = planF[zq ? 'R' : 'L'];
                if (sdZ.jack && sdZ.jack.isNew && !sdZ.jack.out) nMem++;
                if (sdZ.king && sdZ.king.isNew && !sdZ.king.out) nMem++;
                var SPz = PFh[zq ? 'R' : 'L'];
                if (SPz && SPz.post != null) nMem++;
                nNail += nMem * rowsV + Math.floor((ZZ[1] - ZZ[0]) / gP) + 1;
                aStd += (ZZ[1] - ZZ[0]) * (PFh.yHB - baseF);
              }
              nNail += (Math.floor(GA_PF.d / gP) + 1) * (Math.floor(hL / gP) + 1);
              aStd += hL * GA_PF.d;
              var pfN = Math.max(0, Math.round(nNail - aStd * SHEATH_NAIL.perM2));
              tally.pfNails = (tally.pfNails || 0) + pfN;
              hdTxt = 'head CS-PF LVL ' + GA_PF.plies + ' x ' + mmF(GA_PF.ply) + ' x ' + mmF(fF.yHT - fF.yHB) + ' ' + GA_PF.code + ' ' + mmF(hL) + ' from ' + mmF(hxa) + ' to ' + mmF(hxb) + ' on ' + mmF(fF.yHB - s.y0);
              tally.pfSaid = tally.pfSaid || [];
              tally.pfSaid.push((s.id || ('W' + i)) + ' ' + (oF.code || 'door') + ' ' + mmF(oF.w) + (PFh.P.warn ? (' (' + PFh.P.warn + ')') : '') + ': header ' + GA_PF.plies + ' x 45 x 302 ' + GA_PF.code + ' ' + mmF(hL) + ' (' + mmF(hxa) + '..' + mmF(hxb) + ', flush with the sheathing) on ' + mmF(fF.yHB - s.y0) +
                                ', pony wall ' + mmF(Math.max(0, pony)) + ' over it; panels ' +
                                ['L', 'R'].map(function (sd) { var S2 = PFh[sd]; return S2 ? (sd + ' ' + mmF(S2.a1 - S2.a0) + (S2.atEnd ? ' to the corner' : '') + ' (' + Math.round(PFh.P.need / IN) + ' in. asked)') : (sd + ' none'); }).join(' / ') +
                                ', their studs under the header; ' + posts + ' post stud(s) added at its ends' + (pfTxt.length ? (' (' + pfTxt.join(', ') + ')') : '') + '; ' + straps + ' x ' + GA_PF.strap + ' header to jack inside, ' + (straps * GA_PF.strapNails) + ' x ' + GA_PF.strapNail +
                                ' (rated per Table R602.10.6.4 -- TO CHECK at purchase); 3 in. nailing +' + pfN + ' x ' + SHEATH_NAIL.code);
            } else if (hdLen > 0.05 && fF.beam > 0 && PORTAL) {
              /* TAURUS: ONE 100 x 200 ON EDGE over the portal, filling the 2x4
                 wall's depth, on the jacks (taurus-v1) */
              _FM.role('header');
              place(STUD.d, fF.yHT - fF.yHB, hdLen, (fF.xh0 + fF.xh1) / 2, (fF.yHB + fF.yHT) / 2, woodMat);
              tally.portalLen = (tally.portalLen || 0) + hdLen;
              tally.heads = (tally.heads || 0) + 1;
              tally.stitch = (tally.stitch || 0) + 4;
              hdTxt = 'head PORTAL BEAM ' + PORTAL.beam + ' ' + mmF(STUD.d) + ' x ' + mmF(fF.yHT - fF.yHB) + ' ' + mmF(hdLen) +
                      ' on ' + mmF(fF.yHB - s.y0) + (fF.headShort > 0.0005 ? (' SHORT OF ROOM BY ' + mmF(fF.headShort)) : '');
            } else if (hdLen > 0.05 && fF.beam > 0 && HDR_BEAM && HDR_BEAM2) {
              /* ranch-v26: an LVL beam on edge, its plies side by side
                 across the wall, nailed together; ranch-v38: the 302 where
                 the check took it */
              var bCode = ((fF.yHT - fF.yHB) > (HDR_BEAM.d + HDR_BEAM2.d) / 2) ? HDR_BEAM2.code : HDR_BEAM.code;
              var nB = Math.max(2, Math.floor((STUD.d + 0.001) / HDR_BEAM.ply)), wB = nB * HDR_BEAM.ply, kB;
              (tally.lvlBy = tally.lvlBy || {})[bCode] = (tally.lvlBy[bCode] || 0) + hdLen * nB;
              _FM.role('header');
              for (kB = 0; kB < nB; kB++) {
                placeAcross(HDR_BEAM.ply - DBL_SEAM, fF.yHT - fF.yHB, hdLen, (fF.xh0 + fF.xh1) / 2, (fF.yHB + fF.yHT) / 2,
                            -wB / 2 + HDR_BEAM.ply * (kB + 0.5), woodMat);
              }
              tally.lvlLen = (tally.lvlLen || 0) + hdLen * nB;
              tally.beams = (tally.beams || 0) + 1;
              tally.heads = (tally.heads || 0) + 1;
              /* two rows of nails every 300 through each joint between plies, and each ply into the jacks */
              tally.stitch = (tally.stitch || 0) + (nB - 1) * 2 * (Math.floor(hdLen / HDR_BEAM.nailStep) + 1) + 2 * nB * 2;
              hdTxt = 'head LVL ' + nB + ' x ' + mmF(HDR_BEAM.ply) + ' x ' + mmF(fF.yHT - fF.yHB) + ' ' + mmF(hdLen) + ' on ' + mmF(fF.yHB - s.y0) +
                      (fF.headShort > 0.0005 ? (' SHORT OF ROOM BY ' + mmF(fF.headShort) + ', into the unit') : '');
            } else if (hdLen > 0.05 && fF.beam > 0 && HDR_BEAM) {
              /* garageapt-v3: an LVL beam on edge, its plies side by side
                 across the wall, nailed together (ranch-v26) */
              var nB = Math.max(2, Math.floor((STUD.d + 0.001) / HDR_BEAM.ply)), wB = nB * HDR_BEAM.ply, kB;
              /* garageapt-v26: a portal frame's header that found no panel to
                 reach over is the 302 still -- billed as what it is */
              var is302 = fF.beam > HDR_BEAM.d + 0.001;
              _FM.role('header');
              for (kB = 0; kB < nB; kB++) {
                placeAcross(HDR_BEAM.ply - DBL_SEAM, fF.yHT - fF.yHB, hdLen, (fF.xh0 + fF.xh1) / 2, (fF.yHB + fF.yHT) / 2,
                            -wB / 2 + HDR_BEAM.ply * (kB + 0.5), woodMat);
              }
              if (is302) { tally.pfLen = (tally.pfLen || 0) + hdLen * nB; tally.pfBeams = (tally.pfBeams || 0) + 1; }
              else { tally.lvlLen = (tally.lvlLen || 0) + hdLen * nB; tally.beams = (tally.beams || 0) + 1; }
              tally.heads = (tally.heads || 0) + 1;
              tally.stitch = (tally.stitch || 0) + (nB - 1) * 2 * (Math.floor(hdLen / HDR_BEAM.nailStep) + 1) + 2 * nB * 2;
              hdTxt = 'head LVL ' + nB + ' x ' + mmF(HDR_BEAM.ply) + ' x ' + mmF(fF.yHT - fF.yHB) + (is302 ? (' ' + GA_PF.code + ' (no portal)') : '') + ' ' + mmF(hdLen) + ' on ' + mmF(fF.yHB - s.y0) +
                      (fF.headShort > 0.0005 ? (' SHORT OF ROOM BY ' + mmF(fF.headShort) + ', into the unit') : '');
            } else if (hdLen > 0.05) {
              var plyH = HEADER.ply;
              _FM.role('header');
              flat(fF.xh0, fF.xh1, fF.yHB, fF.yHB + plyH, 0);
              flat(fF.xh0, fF.xh1, fF.yHB + plyH, fF.yHT, 1);
              tally.headerLen += hdLen * HEADER.plies;
              tally.heads = (tally.heads || 0) + 1;
              /* stitched along, and each ply nailed through the jack at each end */
              if (GA_LP) {
                tally.stitch = (tally.stitch || 0) + 2 * (Math.floor(hdLen / GA_LP.hdrEdge) + 1) + 2 * HEADER.plies * 2;   /* garageapt-v28: along each edge at 16 in. */
                tally.stitchWas = (tally.stitchWas || 0) + Math.floor(hdLen / DBL_STITCH) + 1 + 2 * HEADER.plies * 2;
                tally.stitchNow = (tally.stitchNow || 0) + 2 * (Math.floor(hdLen / GA_LP.hdrEdge) + 1) + 2 * HEADER.plies * 2;
              } else {
                tally.stitch = (tally.stitch || 0) + Math.floor(hdLen / DBL_STITCH) + 1 + 2 * HEADER.plies * 2;
              }
              hdTxt = 'head ' + mmF(hdLen) + ' on ' + mmF(fF.yHB - s.y0) +
                      (fF.headShort > 0.0005 ? (' SHORT OF ROOM BY ' + mmF(fF.headShort) + ', into the unit') : '');
            } else {
              hdTxt = 'head none';
            }

            /* THE SILL: under a window, between the jacks, as many plies as the
               height under it takes (two normally) */
            var slTxt;
            if (fF.floor) {
              slTxt = 'door, plate runs through';
            } else if (fF.sillPlies > 0 && fF.jiR - fF.jiL > 0.05) {
              var plyS = HEADER.ply, slLen = fF.jiR - fF.jiL;
              _FM.role('sill');
              flat(fF.jiL, fF.jiR, fF.yFB - plyS, fF.yFB, 0);
              if (fF.sillPlies > 1) flat(fF.jiL, fF.jiR, fF.ySB, fF.yFB - plyS, -1);
              tally.sillLen += slLen * fF.sillPlies;
              tally.sills = (tally.sills || 0) + 1;
              tally.stitch = (tally.stitch || 0) + (fF.sillPlies > 1 ? Math.floor(slLen / DBL_STITCH) + 1 : 0) +
                             2 * fF.sillPlies * 2;
              slTxt = 'sill ' + mmF(slLen) + ' x' + fF.sillPlies + ' top ' + mmF(fF.yFB - s.y0);
            } else {
              slTxt = 'sill NONE, ' + mmF(fF.yFB - (s.y0 + STUD.t)) + ' mm over the plate';
            }
            frL.push(oF.kind + ' ' + mmF(wfF) + ' at ' + mmF(oF.at) + ': ' + txtF.join(' | ') +
                     ' | ' + hdTxt + ' | ' + slTxt);
          }
          if (frL.length) {
            ctx.log('frame pair W' + i + ' ' + frL.join('  ||  ') +
                    (cutL.length ? ('  || module cut ' + cutL.join(', ')) : ''));
          }
          _vReport();
          _FM.role(null);
        })();

        /* ONLY THE UPPER PLATE RUNS OUT.

           The lower one belongs to the panel: it is nailed on in the shop, it
           ends where the panel ends, and a piece added past that would be a
           separate board pretending to be part of it. What carries on over the
           top is the cap -- which is the plate that laps the panel joints in the
           first place, so carrying the overhang is the same job it already does.

           I had been adding the lower one too, which put a splice at the wall
           face on the member that is supposed to be continuous. */
        tally.plateLen += capLen;
        /* garageapt-v27 (unit 16c): the double top plate face nailed 10d at
           12 in., 3 x 10d at each lap over a corner (Table R602.3(1)) */
        if (GA_LP) {
        tally.tpLen = (tally.tpLen || 0) + Math.max(0, to - from);
        tally.tpN = (tally.tpN || 0) + Math.floor(Math.max(0, to - from) / GA_LP.tp) + 1 + (through ? 2 * GA_LP.lapNails : 0);
        /* garageapt-v28: the double top plate's end joints -- the lower
           plate's at the frames' joints, the cap's at each stock length */
        var tpJ = (jointsF ? jointsF.length : 0) + Math.max(0, Math.ceil(capLen / GA_LP.stock - 1e-6) - 1);
        tally.tpSplices = (tally.tpSplices || 0) + tpJ;
        }
        tally.rake = rakeExt;
        if (parRise > 0) tally.parapet = parRise + STUD.t;

        /* the joint stud is gone too: a panel edge that does not sit on a
           module line is a panel problem, not something to patch with a member */

        tally.segments++;
        tally.area += s.area;
      }

      if (H.setTally) H.setTally(tally);
      /* garageapt-v2: the bottom plates on the slab are the treated row */
      H.add(STUD.code, tally.studLen + tally.plateLen - (tally.soleLen || 0));
      if (tally.soleLen) {
        H.add(SLAB.plate, tally.soleLen);
        ctx.log('STW  bottom plates on the slab ' + (Math.round(tally.soleLen * 10) / 10) + ' m pressure treated ' + SLAB.plate +
                ' (IRC R317.1), the rest of the frame ' + STUD.code);
      }
      if (tally.sealLen) {
        H.add(SILL_SEAL.code, tally.sealLen);
        ctx.log('STW  sill seal ' + (Math.round(tally.sealLen * 10) / 10) + ' m of ' + SILL_SEAL.code +
                ' (' + Math.round(SILL_SEAL.w * 1000) + ' x ' + Math.round(SILL_SEAL.t * 1000) +
                ') under every sole plate, between the slab and the plate -- air seal and ' +
                'damp break, drawn 5 mm proud below the plate');
      }
      H.add(LEDGER.code, tally.ledger || 0);
      H.add(HEADER.code, tally.headerLen + tally.sillLen);
      /* the LVL beams over the wide openings (ranch-v26, garageapt-v3), by
         code: RANCH's 302 beside the 240 (ranch-v38) */
      if (tally.lvlLen > 0) {
        var lbK, LB = tally.lvlBy || {};
        if (!Object.keys(LB).length) LB[HDR_BEAM.code] = tally.lvlLen;
        for (lbK in LB) if (LB.hasOwnProperty(lbK) && LB[lbK] > 0) H.add(lbK, Math.round(LB[lbK] * 100) / 100);
      }
      /* TAURUS: the portal's beam, by the metre (taurus-v1) */
      if (tally.portalLen > 0) {
        H.add(PORTAL.beam, tally.portalLen * (PORTAL.plies || 1));
        ctx.log((H.portal.tag || 'TAU') + '  portal beams ' + (PORTAL.label || PORTAL.beam) + ' ' + PORTAL.beam + ' ' + Math.round(tally.portalLen * 1000) + ' mm' + ((PORTAL.plies || 1) > 1 ? (' x ' + PORTAL.plies + ' plies') : '') + ' over the front portal');
      }
      /* garageapt-v26 (unit 16b): the portal frames' headers and straps */
      if (tally.pfLen > 0) H.add(GA_PF.code, Math.round(tally.pfLen * 100) / 100);
      if (tally.pfStraps) {
        H.add(GA_PF.strap, tally.pfStraps);
        H.add(GA_PF.strapNail, tally.pfStraps * GA_PF.strapNails);
      }
      if (tally.pfSaid && tally.pfSaid.length) {
        ctx.log('GA portal 16b the overhead door on continuously sheathed portal frames (garageapt-v26, IRC R602.10.6.4, method CS-PF; the sheathing\'s plate washers on the slab, 2d): ' + tally.pfSaid.join(' | ') +
                (tally.pfLen > 0 ? ('; billed ' + GA_PF.code + ' ' + (Math.round(tally.pfLen * 100) / 100) + ' m, ' + (tally.pfStraps || 0) + ' x ' + GA_PF.strap + ', ' + ((tally.pfStraps || 0) * GA_PF.strapNails) + ' x ' + GA_PF.strapNail + ', +' + (tally.pfNails || 0) + ' x ' + SHEATH_NAIL.code) : ''));
      }
      var _hdrSaid = H.hdrSaid ? (H.hdrSaid.get() || []) : [];
      for (var hs = 0; hs < _hdrSaid.length; hs++) ctx.log(_hdrSaid[hs]);
      if (tally.beams && tally.lvlBy) ctx.log('HDR  ' + tally.beams + ' LVL header(s) ' + Object.keys(tally.lvlBy || {}).map(function (c) { return c + ' ' + (Math.round(tally.lvlBy[c] * 100) / 100) + ' m'; }).join(', ') + ' (ranch-v26, the 302 ranch-v38)');
      else if (tally.beams) ctx.log('HDR  ' + tally.beams + ' LVL header(s) ' + HDR_BEAM.code + ' ' + (Math.round(tally.lvlLen * 100) / 100) + ' m');
      if (tally.pfBeams) ctx.log('HDR  ' + tally.pfBeams + ' portal frame header(s) ' + GA_PF.code + ' ' + (Math.round(tally.pfLen * 100) / 100) + ' m');
      if (tally.noRoom && tally.noRoom.length) ctx.log('HDR  NO ROOM for the beam over ' + tally.noRoom.length + ' opening(s) -- the unit stands right under the plate, so it keeps the header it had (flat / the plate), NOT ENOUGH by the check: ' + tally.noRoom.join(' | '));
      if (tally.doorCut > 0) ctx.log('STW  bottom plate cut out in ' + tally.doorCuts + ' doorway(s) on the slab, ' + (Math.round(tally.doorCut * 100) / 100) + ' m (the plate runs through while the wall is stood, then is sawn out; billed whole)');
      if (H.hdrSaid) H.hdrSaid.reset();
      /* THE PANEL REPLACES THE SHEATHING IN THE BILL TOO, because it replaces
         it on the wall -- one row, never both (Marek). Same area, same cutting
         allowance (the shed family's panel siding); Matt's T&G is nailed to
         the studs -- no sheet at all, the boards and their nails are the wall
         layer's (taurus-v12). */
      var cladPanel = !!(PANEL && PANEL.on()), cladTG = !cladPanel && !!(TG && TG.on());
      if (cladPanel) H.add(PANEL.code, tally.area * PANEL.waste);
      else if (!cladTG) H.add('sheet.osb18', tally.area * (PANEL ? PANEL.waste : 1.05));

      /* AND WHAT HOLDS THE WALL TOGETHER, WHICH WAS NOT BILLED.

         Two rows, because they are two different counts. The sheathing is an
         area rate on the same rule the roof deck uses. The frame is a junction
         count: every stud is nailed through the plate at both ends, two nails
         each, and that is what a panel is -- there is no glue and no bracket in
         this wall, so if these are missing the frame is bought as loose timber. */
      var wSheath = cladTG ? 0 : (Math.round(tally.area * SHEATH_NAIL.perM2) + (tally.pfNails || 0));
      var wFrame = tally.studs * FRAME_NAIL.perStudEnd * FRAME_NAIL.ends + (tally.stitch || 0);
      /* EVERY FRAME HAS BOTH ITS PLATES, or the line says which does not */
      try {
        var fmC = _FM.check();
        /* core-v804: gable studs on the cap below have no bottom plate by design */
        var nNoSole = 0;
        fmC.bad = fmC.bad.filter(function (b) { var tg = String(b).split(' ')[0]; if (_tierNoSole[tg] && / has no bottom plate$/.test(b)) { nNoSole++; return false; } return true; });
        ctx.log('frame check ' + fmC.frames + ' frame(s)' + (nNoSole ? (' (' + nNoSole + ' stacked on the cap below, no bottom plate of its own)') : '') + ': ' +
                (fmC.bad.length ? ('MISSING -- ' + fmC.bad.join(' | ')) : 'every one has its bottom and its top plate') +
                (_FM.missing ? ' -- ERROR the frame marker is missing, configurator-ui.js is older than core-v253' : ''));
      } catch (ePC) {}
      ctx.log('frame planner ' + (_wlHas('framePlan') ? ('configurator-ui WALL_LAYOUT (take ' +
              Math.round(API.wallLayout.frame.take * 1000) + ', seam ' + Math.round(API.wallLayout.frame.seam * 1000) +
              ', frame ' + Math.round(API.wallLayout.frame.len * 1000) + ' +' + Math.round(API.wallLayout.frame.tol * 1000) +
              ' mm)') : 'MISSING'));
      ctx.log('frame joints ' + tally.panels + ' frame(s) in all, ' + (tally.jointStuds || 0) +
              ' joint stud(s) added, ' + (tally.pairJoints || 0) + ' joint(s) at an opening\'s jack/king' +
              ' (' + STUD.code + ', nailed through the pair at ' +
              Math.round(DBL_STITCH * 1000) + ' mm); the cap plate runs whole and belongs to no frame');
      if (tally.openings) {
        ctx.log('frame total ' + tally.openings + ' opening(s): ' + (tally.dblNew || 0) +
                ' new jack/king, ' + (tally.dblOld || 0) + ' module stud(s) taken as jack/king, ' +
                (tally.pairs || 0) + ' pair(s) face to face; ' + (tally.stitch || 0) + ' x ' +
                FRAME_NAIL.code + ' stitching pairs, headers and sills at ' + Math.round(DBL_STITCH * 1000) +
                ' mm and through the ends' +
                ((tally.apart || 0) ? (', ' + tally.apart + ' pair(s) with a gap, not stitched') : '') +
                '; ' + (tally.heads || 0) + ' header(s) ' + tally.headerLen.toFixed(2) + ' m, ' +
                (tally.sills || 0) + ' sill(s) ' + tally.sillLen.toFixed(2) + ' m, ' +
                tally.cripples + ' cripple(s)' +
                ((tally.gaveWay || 0) ? (', ' + tally.gaveWay + ' module stud(s) gave way to a jack') : '') +
                '; all timber ' + STUD.code);
      }
      if (wSheath) H.add(SHEATH_NAIL.code, wSheath);
      if (wFrame) H.add(FRAME_NAIL.code, wFrame);
      /* garageapt-v27 (unit 16c): Table R602.3(1), the load path's nailing */
      if (GA_LP) {
      if (tally.tpN) H.add(GA_LP.tpNail, tally.tpN);
      if (tally.upSoleN) H.add(GA_LP.soleNail, tally.upSoleN);
      /* garageapt-v28 (unit 16d): the plates' end joints, 8 x 16d each side */
      if (tally.tpSplices) H.add(FRAME_NAIL.code, tally.tpSplices * GA_LP.splice * 2);
      ctx.log('STW  16d the rest of Table R602.3(1) in the walls (garageapt-v28): the doubled studs (jack + king, the frames\' joints) 16d at 12 in. -- every exterior wall a braced wall panel here (was 400 mm), the flat headers 16d at 16 in. along each edge (was one row at 400): ' +
              (tally.stitchWas || 0) + ' -> ' + (tally.stitchNow || 0) + ' x ' + FRAME_NAIL.code + ' (in FIX  wall frame); the double top plates\' end joints ' + (tally.tpSplices || 0) + ' (the frames\' joints + the cap at every 16 ft of stock), 8 x 16d each side -> ' + ((tally.tpSplices || 0) * GA_LP.splice * 2) + ' x ' + FRAME_NAIL.code + ' (20d; 16d asked, no row)');
      ctx.log('STW  16c the load path\'s nailing (garageapt-v27, IRC Table R602.3(1)): the double top plates ' + (Math.round((tally.tpLen || 0) * 10) / 10) + ' m face nailed 10d at 12 in. + 3 a corner lap -> ' + (tally.tpN || 0) + ' x ' + GA_LP.tpNail +
              (tally.upSoleN ? ('; the upper storey\'s sole plates ' + (Math.round(tally.upSoleLen * 10) / 10) + ' m to the rim / end joists through the subfloor, 3 per 16 in. (braced wall panels) -> ' + tally.upSoleN + ' x ' + GA_LP.soleNail + ' (20d; 16d asked, no row)') : '') +
              '; the rim to the plates and the joists to them: GA floor 5a; the slab: anchor bolts 2d');
      }
      if (cladTG) ctx.log('FIX  wall NO SHEATHING -- Matt\'s T&G ' + TG.code + ' vertical on rails on the studs (' + Math.round(tally.area * 10) / 10 + ' m2 of wall)');
      else if (PANEL) ctx.log('FIX  wall ' + (cladPanel ? 'PANEL SIDING ' + PANEL.code : 'sheathing sheet.osb18') +
              ' ' + Math.round(tally.area * 10) / 10 + ' m2 net, ' +
              Math.round(tally.area * PANEL.waste * 10) / 10 + ' m2 billed with cuts -> ' +
              wSheath + ' x ' + SHEATH_NAIL.code + ' at ' + SHEATH_NAIL.perM2 +
              '/m2, same rule as the roof deck' +
              (cladPanel ? ' -- and NO OSB row, the panel is the sheathing' : ''));
      else ctx.log('FIX  wall sheathing ' + Math.round(tally.area * 10) / 10 + ' m2 -> ' +
              wSheath + ' x ' + SHEATH_NAIL.code + ' at ' + SHEATH_NAIL.perM2 +
              '/m2, same rule as the roof deck' + (tally.pfNails ? (' (+' + tally.pfNails + ' the portal frames\' 3 in. nailing, garageapt-v26)') : ''));
      ctx.log('FIX  wall frame ' + tally.studs + ' stud(s) -> ' + wFrame + ' x ' +
              FRAME_NAIL.code + ', ' + FRAME_NAIL.perStudEnd +
              ' through the plate at each of ' + FRAME_NAIL.ends + ' ends' +
              ((tally.stitch || 0) ? (' + ' + tally.stitch + ' stitching the opening frames') : ''));

      var wmin = Math.min.apply(null, tally.widths), wmax = Math.max.apply(null, tally.widths);
      if (tally.hangers) {
        ctx.log('wall     ' + tally.hangers + ' header hanger(s) -- both studs of every pair ' +
                'now run full height, so the header hangs between them instead of bearing on ' +
                'a jack that stops under it');
      }
      /* the marks, so a panel sheet can be photographed the way a truss is --
         and so the count is checkable before anything tries to */
      var PS = _FM.stats();
      ctx.log('truss panel marks ' + PS.bodies + ' bodies in ' + tally.panels +
              ' panel(s); the upper plate is deliberately outside them');
      try {
        var pkK = Object.keys(PS.stat).sort(), pk, pOut = [];
        for (pk = 0; pk < pkK.length && pk < 4; pk++) {
          var stq = PS.stat[pkK[pk]];
          pOut.push(pkK[pk] + ': ' + stq.n + ' pcs, pieces ' +
                    Math.round(stq.lo * 1000) + '..' + Math.round(stq.hi * 1000) +
                    ', span ' + Math.round(stq.span[0] * 1000) + '..' + Math.round(stq.span[1] * 1000));
        }
        ctx.log('truss panel spread ' + pkK.length + ' mark(s) -- ' + pOut.join(' | ') +
                (pkK.length > 4 ? ' ...' : ''));
        ctx.log('truss panel misses ' + PS.seen + ' piece(s) placed, ' + PS.bodies +
                ' marked, ' + PS.off + ' while marking was off, ' + PS.noHit +
                ' at a position in no span' +
                (PS.missAt.length ? (' (at ' + PS.missAt.join(', ') + ' mm)') : '') +
                (PS.offWhat.length ? (' -- the unmarked were ' + PS.offWhat.join(', ')) : ''));
      } catch (ePS2) {}
      ctx.log('wall     ' + tally.segments + ' segment(s) -> ' + tally.panels + ' panels, ' +
              tally.through + ' run through the corner, ' + tally.butted + ' butt into them');
      ctx.log('wall     panel widths ' + API.fmtLen(wmin) + ' to ' + API.fmtLen(wmax) +
              ' (max ' + API.fmtLen(PANEL_MAX) + ', whole bays at ' + API.fmtLen(API.grid()) + ')');
      ctx.log('wall     ' + tally.studs + ' studs, frame ' + Math.round(STUD.d * 1000) + ' mm deep');
      if (SEG.length) {
        ctx.log('wall     sole ' + SEG[0].y0.toFixed(3) + ', studs to ' +
                (SEG[0].y1 - STUD.t).toFixed(3) + ', lower plate to ' + SEG[0].y1.toFixed(3) +
                ', cap plate to ' + SEG[0].head.toFixed(3) + ' -- no gap, roof bears on the cap');
      }
      ctx.log('wall     timber: studs ' + tally.studLen.toFixed(1) + ' m, ' +
              'plates ' + tally.plateLen.toFixed(1) + ' m, wall area ' + API.fmtArea(tally.area));
      if (SEG.length) {
        var shT = { n: 0, waste: 0 };
        for (i = 0; i < SEG.length; i++) {
          var q = API.sheetsFor(SEG[i].len, SEG[i].h);
          shT.n += q.n; shT.waste += q.waste;
        }
        ctx.log('wall     sheathing ' + shT.n + ' boards, offcut ' + API.fmtArea(shT.waste) +
                ' -- every board edge lands on a stud');
      }
      if (tally.parapet) {
        ctx.log('wall     PARAPET IS IN THE PANEL: studs run ' +
                Math.round(tally.parapet * 1000) + ' mm past the bearing line in one piece, ' +
                Math.round(PARAPET_UPSTAND * 1000) + ' mm of it above the finished deck');
        _why(ctx, 'wall     bearer only on the two walls the rafters cross -- the other ' +
                'two carried nothing and are gone');
        ctx.log('wall     ledger ' + Math.round(LEDGER.t * 1000) + ' x ' +
                Math.round(RAFTER.d * 1000) + ' mm, ' + (tally.ledger || 0).toFixed(1) +
                ' m on the inside face -- the joists hang off this, not off the top plate');
      } else {
        _why(ctx, 'wall     no parapet -- plain eave, which is the cheaper and less ' +
                'leak-prone edge; turn it on in ROOF');
      }
      /* THE EAVE PLATE UNDER AN EXTENDED SIDE WITHOUT POSTS. Past a plain eave
         the roof's edge -- and on a hip the ends of the California jacks --
         has to bear on something along the whole extended eave. With posts it
         is their header; below the post trigger nothing was there, so a plate
         of the frame's own section runs along the extended eave line from one
         end wall's run-out cap to the other, at cap height (Marek). */
      try {
        var OWe = (!(API.STATE.roof && API.STATE.roof.type === 'flat') && API.roofOver) ? API.roofOver() : null;
        if (OWe) {
          var B9 = _buildRect(), alongL9 = (API.STATE.roof.axis === 'l');
          var sides9 = alongL9 ? [['x0', OWe.x0, -1], ['x1', OWe.x1, 1]] : [['z0', OWe.z0, -1], ['z1', OWe.z1, 1]];
          var q9;
          for (q9 = 0; q9 < sides9.length; q9++) {
            var ov9 = sides9[q9][1];
            if (!(ov9 > 0.80 && ov9 < EAVE_POST.trigger)) continue;
            var reach9 = ov9 + API.facadeThk() - PLATE.t;   /* the roof edge, the same line the end walls' caps reach */
            /* AT THE HEIGHT OF THE ROOF OVER IT, not at the head of the low
               wall: on a mono the two sides differ by the whole rise, and the
               plate hung 1634 mm under the timber it was meant to tie
               (shed-v9, Marek). bearingY clamps at the plane's edge, and these
               run about 150 mm past it, so the plane is read without a clamp. */
            var eaveM = new T.MeshStandardMaterial({ color: H.wood, roughness: 0.85, metalness: 0.0 });
            _asmCode(ctx, STUD.code, 'side');
            var xE = 0, zE = 0, yCap9;
            if (alongL9) {
              xE = (sides9[q9][2] < 0) ? (B9.x0 - reach9 + STUD.d / 2) : (B9.x1 + reach9 - STUD.d / 2);
              zE = (B9.z0 + B9.z1) / 2;
            } else {
              zE = (sides9[q9][2] < 0) ? (B9.z0 - reach9 + STUD.d / 2) : (B9.z1 + reach9 - STUD.d / 2);
              xE = (B9.x0 + B9.x1) / 2;
            }
            yCap9 = _planeYAt(xE, zE) - STUD.t / 2;
            if (alongL9) {
              var mE = new T.Mesh(new T.BoxGeometry(STUD.d, STUD.t, B9.z1 - B9.z0), eaveM);
              mE.position.set(xE, yCap9, zE); g.add(mE);
              tally.plateLen += (B9.z1 - B9.z0);
            } else {
              var mE2 = new T.Mesh(new T.BoxGeometry(B9.x1 - B9.x0, STUD.t, STUD.d), eaveM);
              mE2.position.set(xE, yCap9, zE); g.add(mE2);
              tally.plateLen += (B9.x1 - B9.x0);
            }
            ctx.log('wall     eave plate on side ' + sides9[q9][0] + ': the roof goes ' + Math.round(ov9 * 1000) + ' past the wall with no posts -- a ' + Math.round(STUD.d * 1000) + 'x' + Math.round(STUD.t * 1000) + ' plate along the eave line, ' + Math.round(reach9 * 1000) + ' out, on the end walls\' run-out caps');
          }
        }
      } catch (eEP) { ctx.log('wall     eave plate THREW ' + (eEP && eEP.message)); }
      if (tally.rake) {
        ctx.log('wall     BOTH plates run ' + Math.round(tally.rake * 1000) +
                ' mm past each end wall, to the far face of the last rafter -- and ' +
                'the lower one is the END PANEL\'S OWN top plate made longer, not a ' +
                'stub added past it, so there is no splice where the cantilever starts');
      } else {
        _why(ctx, 'wall     plates stop at the wall -- nothing of the roof hangs past it');
      }
      ctx.log('wall     header ' + HEADER.plies + ' plies of ' + Math.round(HEADER.ply * 1000) +
              ' mm laid FLAT, ' + Math.round(HEADER.d * 1000) + ' mm deep, filling the ' +
              Math.round(STUD.d * 1000) + ' mm wall -- upper ply chamfered ' +
              Math.round(CHAMFER * 1000) + ' mm so the joint between them reads' +
              (isNew(LAYER) ? '   <-- MAGENTA this version' : ''));
      ctx.log('wall     upper plate chamfered ' + Math.round(CHAMFER * 1000) +
              ' mm so a doubled plate can be counted on screen');
      if (byPanel) _why(ctx, 'wall     PANEL VIEW: one colour per panel, everything else hidden');

      if (tally.openings) {
        ctx.log('wall     ' + tally.openings + ' opening(s): ' + tally.jacks + ' jacks, ' +
                tally.kings + ' kings, ' + tally.cripples + ' cripples, header ' +
                tally.headerLen.toFixed(1) + ' m' +
                (tally.sillLen ? (', sill ' + tally.sillLen.toFixed(1) + ' m') : ''));
        _why(ctx, 'wall     doubled both ways: jack beside king each side, and the ' +
                'header and sill are PAIRS across the wall depth, not single members');
        ctx.log('wall     openings step on the ' + fmtLen(GRID()) + ' stud module, so a jack ' +
                'takes the place of a stud that was going to be there anyway');
      } else {
        _why(ctx, 'wall     no openings yet -- pick one in DOORS & WINDOWS and tap a wall');
      }
    }

    /* =========================================================================
       THE TRUSS MODULE (core-v765, the fourth shared module of the house line
       -- status/modules.md). The common trusses of a trussed roof written
       once: the set-out on the frame's one station list, the girders of a hip
       end, every member and plate drawn through the product's truss kit, the
       ties and the bill. The ADU's text; the truss kit and recorder were the
       core's already (trussKit / trussRecorder) and come in as H.TK / H.TR.

         API.trusses(ctx, planes, {
           api, CHORD, PLATE, STEEL, WEB, TRUSS_PANELS, TRUSS_TIE, wood,
           TK, TR, fink, inTruss, hipGroup, trussCollect, trussSig, stations,
           planes:    the product's own (roofPlanes)
           lastBody:  function () -- the body the last inTruss drew
           setTop, setMemberPts, setTally: the product's _trussTop /
                      _memberPts / _trussTally
           add:       function (code, qty)
           check:     { get, log, boxes } -- RANCH's truss check: girder plies,
                      the heel's big plate, plates baked one body a code
           runRidge, girderL: { plies, tailCut, cutTail } -- ANY HOUSE's L
           ladder:    function (...) -- GARAGE APT / ANY HOUSE gable ladder
           noTies:    function () -- BARNDO's post frame bolts the heels
         })
         -> { eaveY, ridgeY }

       Carried to every product: the ridge ends read off the roof's planes
       (adu-v1419, the half-hip's gable trusses -- SHED, SAUNA, TAURUS, GARDEN
       BAR lacked it) and a ridge along x turning the truss the right way
       (taurus-v16 / anyhouse-lib-v11 -- the rest drew it mirrored).
       ========================================================================= */
    function trusses(ctx, planes, H) {
      /* the product's own pieces, under the names the text has always used */
      var API = H.api, CHORD = H.CHORD, PLATE = H.PLATE, STEEL = H.STEEL, WEB = H.WEB,
          TRUSS_PANELS = H.TRUSS_PANELS, TRUSS_TIE = H.TRUSS_TIE;
      var _TK = H.TK, _TR = H.TR, _fink = H.fink, _inTruss = H.inTruss, _hipGroup = H.hipGroup,
          _trussCollect = H.trussCollect, _trussSig = H.trussSig, _trussStations = H.stations, roofPlanes = H.planes;
      /* RANCH's truss check (ranch-v7 / v59 / v60): the girder's plies, the big
         plate at a heel where the check asks for it, and one body a truss and
         plate code instead of one a plate */
      var CHK = H.check || null, _trussCheck = CHK ? CHK.get() : null;
      var gabMarkLo = 0, gabMarkHi = 0, _memberPts = null;
      /* THE PALETTE IS DECLARED BEFORE ANYTHING USES IT.

         I put it just above _trussAt, which reads well and runs wrong: the
         call sits sixty lines higher up. var hoists the name and not the
         value, so when the first truss was drawn TRUSS_TINT was undefined --
         and TRUSS_TINT['chord'] on undefined is exactly the message the log
         gave, with the whole roof missing behind it (Marek). */
      /* EACH SET OF MEMBERS IN ITS OWN COLOUR, WHILE WE JUDGE THE JOINTS.

         Four sets meet at these nodes and in one colour they are impossible to
         talk about: which piece is short, which one still overlaps, which end
         is cut wrong. So each kind takes a colour of its own for as long as
         this work lasts -- and it comes off the moment the joints are right,
         the way the magenta always did (Marek: kazdy jinou barvou at vim jak
         tu je popsat). */
      var TRUSS_TINT = {
        chord: 0x00E5FF,          /* cyan    -- the top chords */
        tie:   0xFF2BD1,          /* magenta -- the bottom chord */
        post:  0xAEEA00,          /* acid green -- the king post */
        web:   0xFF8A00           /* orange  -- the diagonals */
      };

      function _trussTintMat(T, kind) {
        return new T.MeshStandardMaterial({
          color: TRUSS_TINT[kind] || 0xCCCCCC,
          roughness: 0.7, metalness: 0.0
        });
      }


      _TR.reset();
      _TK.reset();
      var T = ctx.THREE, g = ctx.group, i, j;
      if ((API.STATE.roof || {}).type !== 'hip') {
        ctx.log('ROO  trusses set out between the two GABLE WALL FACES, not between ' +
                'the rake edges: both gables land on a station because both gables ' +
                'are stations, every bay between them is equal, and the overhang is ' +
                'carried on at the same pitch');
      }
      var mat = new T.MeshStandardMaterial({ color: H.wood, roughness: 0.85, metalness: 0.0 });
      var steel = new T.MeshStandardMaterial({ color: STEEL, roughness: 0.4, metalness: 0.8 });
      var R = API.STATE.roof, sp = ctx.grid;

      var P = ctx.poly, x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9;
      for (i = 0; i < P.length; i++) {
        x0 = Math.min(x0, P[i][0]); x1 = Math.max(x1, P[i][0]);
        z0 = Math.min(z0, P[i][1]); z1 = Math.max(z1, P[i][1]);
      }
      var alongL = (R.axis === 'l');
      /* across the span, and along the ridge */
      var hWall = (alongL ? (x1 - x0) : (z1 - z0)) / 2;

      /* THE TRUSSES COVER THE ROOF, NOT THE BUILDING.

         This ran from wall to wall, so a rake overhang pulled out to two metres
         had a roof over it and nothing under it -- the plane grew and the number
         of trusses did not. A truss every 600 mm along the ROOF is what holds it
         up, so the run is the roof's run.

         And a truss is a triangle: it does not stretch, it gets BIGGER. Pull the
         eave out and the tail grows, which raises the ridge and lengthens every
         chord and web -- the same shape at a larger scale. That is why the tail
         is read here rather than the plane being scaled afterwards. */
      var OT = _roofOver();
      var rakeA = alongL ? OT.z0 : OT.x0, rakeB = alongL ? OT.z1 : OT.x1;
      var runFrom = (alongL ? z0 : x0) - rakeA, runTo = (alongL ? z1 : x1) + rakeB;
      var cAcross = alongL ? (x0 + x1) / 2 : (z0 + z1) / 2;
      /* A RIDGE ALONG X TURNS THE TRUSS THE OTHER WAY (taurus-v16, Marek: gable
         roof je nesmyslne otoceny vazniky nad strechu; anyhouse-lib-v11). The
         member is built along its local +x and turned by yaw; world() puts the
         truss's own p on +z when the ridge runs along x, and +PI/2 about y
         turns +x onto -z -- every sloping member came out mirrored, the top
         chords rising to the eaves and out through the sheet. -PI/2 turns +x
         onto +z, the way world() reads p. Carried to every product here
         (core-v765): the rest still had +PI/2. */
      var yaw = alongL ? 0 : (-Math.PI / 2);

      /* THE TRUSS SITS ON THE PLATE, IT DOES NOT SINK INTO IT.

         wallTop() is the TOP of the wall's cap plate -- the surface a truss lands
         on. Putting the bottom chord's centreline there buried half of it, 70 mm,
         inside the plate below: two pieces of timber in the same place, and a
         takeoff that pays for both.

         The chord's UNDERSIDE goes on that line, so its centreline is half a
         depth above it. */
      var eaveY = API.wallTop() + CHORD.d / 2;
      /* the overhang the customer set, plus the facade it has to clear */
      /* the eave sides are the two the slope falls to. They can differ, and a
         truss has one tail -- so the deeper of the two sets the triangle and the
         log says which, rather than the two silently averaging into a shape that
         is neither. */
      var eaveA = alongL ? OT.x0 : OT.z0, eaveB = alongL ? OT.x1 : OT.z1;
      var tailA = eaveA + API.facadeThk(), tailBB = eaveB + API.facadeThk();
      /* the mean is what sets the ridge HEIGHT; the difference moves it sideways */
      var tail = (tailA + tailBB) / 2;
      /* the ridge is measured from the TIP, because that is where the top chords
         start now -- so the raised heel is in the height rather than bolted on */
      var ridgeY = eaveY + (hWall + tail) * Math.tan(R.pitch * Math.PI / 180);
      var tr = _fink(hWall, eaveY, ridgeY, TRUSS_PANELS, tailA, tailBB);
      /* PUBLISHED, BECAUSE OTHER THINGS HAVE TO MEET IT. */
      if (H.setTop) H.setTop({ ridgeY: ridgeY, eaveY: eaveY, wallTop: API.wallTop() });
      var hWallOf = hWall, eaveYOf = eaveY, ridgeYOf = ridgeY;

      /* THE TRUSSES STOP AT THE WALLS. What hangs past the gable is the barge
         rafter on the extended plates, not a truss with nothing under it. */
      /* THE END TRUSSES STEP IN BY A PLATE AS WELL. A plate sits on the FACE of
         the timber, so a truss flush with the gable wall puts 12 mm of steel past
         it. Nothing in the frame passes the frame. */
      var inset = CHORD.t / 2 + PLATE.t;
      /* EVENLY, ACROSS THE WHOLE RUN.

         This stepped from one end at the module and then dropped a last truss on
         the far end wherever it landed -- so making the roof longer added one
         tight against the second-to-last and left the rest at the old spacing.
         A pair 80 mm apart is not a bay, it is two trusses in the same place.

         The run is divided instead: as many bays as fit at about the module, then
         every one of them the same. The spacing comes out near 600 and is never
         a stub. */
      var runSpan = (runTo - inset) - (runFrom + inset);
      var nBays = Math.max(1, Math.round(runSpan / sp));
      var stepT = runSpan / nBays;
      /* ONE SET-OUT. THIS LIST WAS A SECOND ONE, AND THAT IS THE WHOLE STORY.

         The trusses were drawn from a division worked out HERE -- rake to rake,
         runSpan over nBays -- while _trussStations() answers the same question
         for everybody else: the battens, the blown insulation, the gable
         sheathing, and the markers I painted to find this. On a hip the branch
         below already threw this list away and used _trussStations(); on a gable
         nobody ever did, so the timber stood on one list and everything measured
         against it on another.

         That is why the insulation bays fall where the members ought to be and
         the members are elsewhere; why the gable board had nothing behind it on
         one end; and why the two marked trusses came back painted ZERO -- the
         stations I painted are not the stations the loop walks.

         The local division is gone. There is one set-out and this is not it. */
      var stations = _trussStations() || [], sq0;
      if (!stations.length) {
        stations = [];
        for (sq0 = 0; sq0 <= nBays; sq0++) stations.push(runFrom + inset + stepT * sq0);
      }

      /* THE PUBLICATION USED TO STAND HERE, AND THAT WAS THE FAULT.

         What this list holds is the WHOLE-RUN division -- rake to rake, at
         runSpan / nBays. On a hip the branch forty lines below throws it away
         and rebuilds the stations from _trussStations() instead: anchored on the
         RIDGE END, dividing the RIDGE RUN. Published up here, the battens were
         set out on a list the frame never built.

         It is published below that branch now, off the array the truss loop
         actually walks. */

      function world(p, y, u) {
        return alongL ? [cAcross + p, y, u] : [u, y, cAcross + p];
      }

      var tally = { trusses: stations.length, chord: 0, web: 0, plates: 0, platesBig: 0 };

      /* ON A HIP, THE RUN STOPS AT THE GIRDER. */
      /* THE RIDGE ENDS GET A TRUSS, AND THE REST IS DIVIDED BETWEEN THEM. */
      var isHip = (API.STATE.roof.type === 'hip');
      /* THE FOLD IS READ OFF THE PLANES, NOT WORKED OUT AGAIN. */
      var halfSpan = hWall + tail;
      var rEnds = null;
      /* OFF THE ROOF'S PLANES, NOT THE FLOOR'S OUTLINE (adu-v1419, Marek: u
         polovalby chybi cele vazniky nad stranou se stitem). P here is the
         plan's polygon, whose corners carry no ridge, so the ridge ends were
         always guessed from the footprint as a both-ends hip -- a half width
         in from each end. On a long plan the station list then replaced the
         guess and nothing showed; on a square one the guess closed on itself
         (75 mm past the middle from either end), the hip branch was skipped
         and every truss of the half-hip's gable end was left out as past the
         fold. */
      var PLr = [];
      try { PLr = roofPlanes() || []; } catch (ePLr) { PLr = []; }
      for (i = 0; i < PLr.length; i++) {
        var RG = PLr[i] && PLr[i].ridge;
        if (!RG || RG.length < 2) continue;
        /* THE AXIS IS THE ONE THE RIDGE ACTUALLY RUNS ALONG. */
        var dxR = Math.abs(RG[1][0] - RG[0][0]), dzR = Math.abs(RG[1][2] - RG[0][2]);
        if (Math.max(dxR, dzR) < 0.05) continue;
        var useZ = dzR >= dxR;
        var a1 = useZ ? RG[0][2] : RG[0][0], b1 = useZ ? RG[1][2] : RG[1][0];
        rEnds = [Math.min(a1, b1), Math.max(a1, b1)];
        break;
      }
      if (H.runRidge) { var WRg2 = H.runRidge(); if (WRg2) rEnds = WRg2.slice(); }   /* anyhouse-lib-v13: the L's wing, its own ridge */
      if (rEnds) halfSpan = rEnds[0] - runFrom;
      var gA = rEnds ? rEnds[0] : (runFrom + halfSpan);
      var gB = rEnds ? rEnds[1] : (runTo - halfSpan);
      /* both are replaced below from the station list when there is one */
      var girders = 0, skipped = 0;

      /* ONE SPACING, EVERYWHERE. I divided the ridge at one pitch and then
         stepped outward at another, so the run changed rhythm at the fold --
         two... */
      if (isHip && gB - gA > 0.2) {
        var ridgeRun = gB - gA;
        var nR = Math.max(1, Math.round(ridgeRun / sp));
        /* FULL TRUSSES BETWEEN THE RIDGE ENDS, AND NOTHING BEYOND THEM. */
        /* THE FOLD IS WHERE THE STATIONS END, not a second calculation. */
        var ST = _trussStations();
        if (ST.length > 1) {
          stations = ST;
          gA = ST[0];
          gB = ST[ST.length - 1];
          stepT = (gB - gA) / Math.max(1, ST.length - 1);
        } else {
          stepT = ridgeRun / nR;
          stations = [];
          for (sq0 = 0; sq0 <= nR; sq0++) stations.push(gA + stepT * sq0);
        }
      }

      /* AND HERE THE FRAME PUBLISHES WHAT IT BUILT.

         Two set-outs answered one question: the trusses stood at the ridge run
         divided, and the battens were handed the whole run divided. About half a
         bay apart at every station, closing nowhere -- which is the divergence,
         and it was never in the batten code.

         The skipped test below is the truss loop's own, so a station the frame
         does not build cannot be published either. */
      _memberPts = [];
      if (H.setMemberPts) H.setMemberPts(_memberPts);
      for (sq0 = 0; sq0 < stations.length; sq0++) {
        if (isHip && (stations[sq0] < gA - 0.01 || stations[sq0] > gB + 0.01)) continue;
        /* [x, z, dirX, dirZ]: where it stands and which way it runs -- a common
           runs across the ridge axis */
        /* a common spans the whole house across (eave to eave): its ends are put well past both walls */
        _memberPts.push(alongL ? [cAcross, stations[sq0], 1, 0, cAcross - 50, stations[sq0], cAcross + 50, stations[sq0]]
                               : [stations[sq0], cAcross, 0, 1, stations[sq0], cAcross - 50, stations[sq0], cAcross + 50]);
      }
      var mRun = function (p) { return alongL ? p[1] : p[0]; };
      ctx.log('BAT  frame built ' + _memberPts.length + ' member(s), pitch ' +
              Math.round(stepT * 1000) + ' mm, ' +
              (_memberPts.length
                ? (Math.round(mRun(_memberPts[0]) * 1000) + ' to ' +
                   Math.round(mRun(_memberPts[_memberPts.length - 1]) * 1000))
                : 'none') +
              ' -- and the battens set out on THIS');

      /* TWO TRUSSES PAINTED SO HE CAN NAME THE WRONG ONE.

         I have been arguing from a log and he has been arguing from a screen,
         and neither of us can see the other's. So the two trusses I believe the
         gable sheathing lands on are coloured: the LOW one magenta, the HIGH one
         cyan. One word back -- which of the two is off -- and the guessing ends.

         Low and high in the truss axis, so it does not depend on which wall he
         calls first. Comes out again the moment it has done its job.

         AND IT SITS ABOVE THE LOOP, WHICH IS WHY NOTHING WAS PAINTED LAST
         TIME. I put the whole block down beside _trussAt, and _trussAt is
         DECLARED after the loop that calls it -- a function declaration is
         hoisted, so the call still worked, but the var next to it was still
         undefined while every truss was being drawn. The test read undefined,
         returned the plain material and painted nothing. Declarations hoist;
         assignments do not. */
      var GAB_MARK = false;
      gabMarkLo = 0; gabMarkHi = 0;
      var gabLo = null, gabHi = null;
      if (GAB_MARK && !isHip) {
        var gq2;
        for (gq2 = 0; gq2 < stations.length; gq2++) {
          if (gabLo === null || stations[gq2] < gabLo) gabLo = stations[gq2];
          if (gabHi === null || stations[gq2] > gabHi) gabHi = stations[gq2];
        }
        /* the gable stations are the wall faces, not the rake ends -- the ones
           the wall segments put there */
        var SGm = [], q2m, loW = null, hiW = null;
        try { SGm = segments() || []; } catch (eM) { SGm = []; }
        for (q2m = 0; q2m < SGm.length; q2m++) {
          var cm1 = alongL ? SGm[q2m].z0 : SGm[q2m].x0;
          var cm2 = alongL ? SGm[q2m].z1 : SGm[q2m].x1;
          if (loW === null || cm1 < loW) loW = cm1;
          if (loW === null || cm2 < loW) loW = cm2;
          if (hiW === null || cm1 > hiW) hiW = cm1;
          if (hiW === null || cm2 > hiW) hiW = cm2;
        }
        if (loW !== null) {
          gabLo = loW + CHORD.t / 2;
          gabHi = hiW - CHORD.t / 2;
          ctx.log('ROO  gable MARK magenta at ' + Math.round(gabLo * 1000) +
                  ' (low end), cyan at ' + Math.round(gabHi * 1000) +
                  ' (high end) -- these are the two the sheathing is drawn ' +
                  'against; say which one is off');
        }
      }
      /* lit from inside, so neither of them can be lost in shadow */
      var matMag = new T.MeshStandardMaterial({
        color: 0xFF00FF, roughness: 0.85, metalness: 0.0,
        emissive: 0xFF00FF, emissiveIntensity: 0.55 });
      var matCya = new T.MeshStandardMaterial({
        color: 0x00E5FF, roughness: 0.85, metalness: 0.0,
        emissive: 0x00E5FF, emissiveIntensity: 0.55 });

      function _trussMat(u) {
        if (!GAB_MARK || gabLo === null) return mat;
        if (Math.abs(u - gabLo) < 0.02) { gabMarkLo++; return matMag; }
        if (Math.abs(u - gabHi) < 0.02) { gabMarkHi++; return matCya; }
        return mat;
      }

      for (i = 0; i < stations.length; i++) {
        var u = stations[i];

        /* PAST THE FOLD: NOTHING YET.

           The step-downs are gone -- they were the wrong answer, and the right
           one is the California fill: a hip member from the ridge end to the
           corner, a half truss on the centre line, and shorter trusses dying on
           the hip. Each is its own step and each lands on the one before, so an
           empty hip end now is honest rather than half-built. */
        if (isHip && (u < gA - 0.01 || u > gB + 0.01)) { skipped++; continue; }

        /* a girder carries a hip end: only the hipped ends get one, a gable end
           of a half-hip keeps a plain end truss (Marek) */
        var isGirder = isHip && ((API.hipAt('lo') && Math.abs(u - gA) < stepT * 0.55) ||
                                 (API.hipAt('hi') && Math.abs(u - gB) < stepT * 0.55));
        /* the girder's plies by the check (RANCH ranch-v7): 2, or 3 on a long span */
        var plies = isGirder ? ((_trussCheck && _trussCheck.plies) || 2) : 1;
        /* ANY HOUSE anyhouse-lib-v11, THE L: the wing's first truss on the seam
           is a girder (its plies by the bar's span); the bar's trusses over the
           opening have no tail on the wing's side -- their heels sit in the
           girder's hangers */
        var trU = tr;
        if (H.girderL) {
          var ahGp = H.girderL.plies(u, stations), ahCut = H.girderL.tailCut(u);
          if (ahGp) { isGirder = true; plies = ahGp; }
          if (ahCut) trU = H.girderL.cutTail(tr, ahCut, hWallOf, cAcross);
        }
        var ply;
        for (ply = 0; ply < plies; ply++) {
          var uPly = u + (isGirder ? (ply - (plies - 1) / 2) * (CHORD.t + 0.004) : 0);
          _trussAt(uPly, trU, isGirder);
        }
        if (isGirder) girders++;

      }
      /* garageapt-v6: THE GABLE LADDER (the product's GA_LADDER) */
      if (H.ladder) H.ladder(ctx, T, g, mat, tr, stations, world, alongL, isHip);

      /* AND IT SAYS WHAT IT COLLECTED, before anything is drawn. Step one of
         the truss sheet is the data; the drawing is worth nothing if the shapes
         and the counts are wrong, and this is how we see them (Marek). */
      try {
        var tq, tl = [];
        var TRS = _TR.shapes();
        for (tq = 0; tq < TRS.length; tq++) {
          var TS = TRS[tq];
          tl.push((TS.big ? 'girder' : 'truss') + ' x' + TS.n +
                  ' span ' + Math.round(TS.span * 1000) +
                  ' rise ' + Math.round(TS.rise * 1000) +
                  ' | ' + TS.count + ' member(s), ' + (Math.round(TS.run * 10) / 10) + ' m');
        }
        try { _hipGroup(); } catch (eHG) {}
        ctx.log('truss list  ' + _TR.shapes().length + ' distinct shape(s): ' +
                (tl.length ? tl.join('  ||  ') : 'none'));

        /* AND WHAT THE BODIES MEASURE, grouped by the section they were asked
           for. Two numbers matter here: the axis a reader gets (which must be
           the piece, not its plan) and the section (which must be the profile,
           not two of its sides swapped by a rotation). */
        var GG = {}, gq2, G1, gkey, glines = [], gbad = [];
        var TKG = _TK.geom();
        for (gq2 = 0; gq2 < TKG.length; gq2++) {
          G1 = TKG[gq2];
          gkey = Math.round(G1.askD * 1000) + 'x' + Math.round(G1.askT * 1000);
          if (!GG[gkey]) GG[gkey] = { n: 0, lo: 1e9, hi: 0, alo: 1e9, ahi: 0, off: 0, sec: {} };
          var GR = GG[gkey];
          GR.n++;
          if (G1.got < GR.lo) GR.lo = G1.got;
          if (G1.got > GR.hi) GR.hi = G1.got;
          if (G1.ask < GR.alo) GR.alo = G1.ask;
          if (G1.ask > GR.ahi) GR.ahi = G1.ask;
          if (Math.abs(G1.got - G1.ask) > 0.001) GR.off++;
          var gs = Math.round(G1.d * 1000) + 'x' + Math.round(G1.t * 1000);
          GR.sec[gs] = (GR.sec[gs] || 0) + 1;
          if (Math.abs(G1.d - G1.askD) > 0.001 || Math.abs(G1.t - G1.askT) > 0.001) {
            if (gbad.length < 6) gbad.push('asked ' + gkey + ', body ' + gs +
                                           ' on a ' + Math.round(G1.ask * 1000) + ' mm member');
          }
        }
        for (gkey in GG) {
          if (!GG.hasOwnProperty(gkey)) continue;
          var GR2 = GG[gkey], gsl = [], gsk;
          for (gsk in GR2.sec) { if (GR2.sec.hasOwnProperty(gsk)) gsl.push(gsk + ' x' + GR2.sec[gsk]); }
          glines.push(gkey + ': ' + GR2.n + ' member(s), axis ' +
                      Math.round(GR2.lo * 1000) + '-' + Math.round(GR2.hi * 1000) +
                      ' mm (asked ' + Math.round(GR2.alo * 1000) + '-' + Math.round(GR2.ahi * 1000) +
                      '), ' + GR2.off + ' sheared past the axis, section ' + gsl.join(' + '));
        }
        ctx.log('truss geom  ' + (glines.length ? glines.join('  ||  ') : 'no members measured'));
        var WC = (_TK.webClip ? _TK.webClip() : null);
        if (WC) {
          ctx.log('truss webs  ' + WC.n + ' web(s), ' + WC.cut + ' built from the straight body cut by the tie, ' +
                  'the chord and the uprights\' cheeks; left past a face ' + Math.round(WC.after * 1000) + ' mm' +
                  ', left and right halves differ by ' + Math.round((WC.asym || 0) * 1e6) + ' mm2' +
                  (WC.noRoom ? (', ' + WC.noRoom + ' with NO ROOM between the chords') : ''));
        }
        var OR = (_TK.outerRule ? _TK.outerRule() : null);
        if (OR && OR.n) {
          ctx.log('truss rule  outer upright + outer diagonal need ' + Math.round(OR.min * 1000) +
                  ' mm clear between the chords: ' + (OR.n - OR.dropped) + ' of ' + OR.n + ' truss(es) have it (clear ' +
                  Math.round(OR.clearLo * 1000) + (OR.clearHi > OR.clearLo + 0.0005 ? ('-' + Math.round(OR.clearHi * 1000)) : '') +
                  ' mm)' + (OR.dropped ? (', ' + OR.dropped + ' built without the outer pair') : ''));
        }
        if (gbad.length) ctx.log('truss geom  SECTION WRONG on ' + gbad.length +
                                 ' body(s): ' + gbad.join(' | '));
        /* AND THE REPORT WAITS FOR THE HIP END. The jack trusses are recorded
           by the hip framing, which runs inside the roof skin -- long after
           this function. Grouping here caught the main trusses and nothing
           else, which is why the sheet had two shapes and no corners
           (Marek: musi tam byt vsechny vazniky strechy). */
      } catch (eTL) {}

      if (isHip) {
        ctx.log('HIP  trusses: ' + (stations.length - skipped) + ' of ' + stations.length +
                ', girders at ' + Math.round(gA * 1000) + ' and ' + Math.round(gB * 1000) +
                ', ridge ends ' + Math.round(gA * 1000) + ' and ' + Math.round(gB * 1000) +
                ', spacing ' + Math.round(stepT * 1000) +
                ' -- hip ends empty, waiting for the California fill');
      }

      /* THE CENTRE TRUSS OF EACH HIP END, ACROSS THE OTHERS. */

      function _trussAt(u, T2, big) {
        /* A TRUSS IS A DRAWING THE MODEL ALREADY HAS. Every member is stored
           as p1,y1 to p2,y2 -- two points in the truss's own vertical plane,
           which is an elevation. Length, slope and the angle where two members
           meet all come out of those four numbers (Marek). */
        try { _trussCollect(T2, big); } catch (eTC) {}
        /* EVERY BODY SAYS WHICH TRUSS IT IS.

           The sheets are one drawing per distinct SHAPE, and the shape's name
           is the signature the collector already computes. Stamping it on each
           body is what lets one truss be shown on its own: hide everything
           whose signature is not this sheet's, point the camera square at what
           is left, and the picture cannot disagree with the model, because it
           IS the model (Marek: nemuzeme udelat jen printscreeny tech tvaru). */
        var sigAt = '';
        try { sigAt = _trussSig(T2, big); } catch (eSG) {}
        var mat = _trussMat(u);
        for (j = 0; j < T2.members.length; j++) {
          var m = T2.members[j];
          var dp = m.p2 - m.p1, dy = m.y2 - m.y1;
          var len = Math.hypot(dp, dy);
          if (len < 0.02) continue;
          var sect = (m.kind === 'web') ? WEB : CHORD;
          /* A MEMBER CARRIES THE CODE IT IS BILLED UNDER.

             This builder set no mark of its own, so every chord and web
             inherited the roof's 'roof_framing' mark -- which names the
             RAFTER section, 60x240. They are billed as CHORD 60x160 and WEB
             60x120, so the model showed 786 m of a code the bill had 176 m of,
             and 277 m of one the bill had 462 m of. Two audit findings, one
             cause: the bodies were wearing the wrong name (Marek: vsechno
             vykazovat z modelu, ale spravne). */
          _asmMark(ctx, (m.kind === 'web' || m.kind === 'post') ? 'truss_web' : 'truss_chord',
                   'top', (m.kind === 'web' || m.kind === 'post') ? 'Truss web' : 'Truss chord',
                   sect.code);
          var c = world((m.p1 + m.p2) / 2, (m.y1 + m.y2) / 2, u);
          /* the tint comes off, as it always does once the judging is over --
             the palette stays above for whenever this is picked up again */
          /* THE TOP CHORD IS CUT ON THE BOTTOM CHORD'S TOP FACE, and only it:
             the tie is level and is the piece that face belongs to, and a web
             already dies on that face at its end (Marek: horni pasnice
             seriznout na spodni strane podle horni plochy spodni pasnice). */
          _inTruss(T, g, mat, len, sect,
                   Math.atan2(dy, dp), yaw, c[0], c[1], c[2],
                   m.cutA || 0, m.cutB || 0,
                   (m.kind === 'web' && Math.abs(dp) < 0.001) ? 'wvert' : m.kind,
                   (m.kind === 'chord') ? (eaveY + CHORD.d / 2) : null,
                   (m.kind === 'post') ? (T2.tanP || 0) : 0,
                   m.endHi || null, m.endLo || null,
                   /* and a web lies between the tie's top and the chords'
                      underside over its whole length (core-v258) */
                   (m.kind === 'web' && T2.clip) ? { base: T2.clip, pc: (m.p1 + m.p2) / 2 } : null);
          /* AND THE PIECE'S OWN SHAPE TRAVELS WITH IT.

             The sheet was drawn from p1..p2 and a section, which is the member
             as designed. The outline is the member as CUT, so it is carried on
             the member itself, turned from the body's own frame into the
             truss's: along its length and across its depth. */
          try {
            if (H.lastBody() && H.lastBody().userData) {
              H.lastBody().userData.trussSig = sigAt;
              _TR.tag();
            }
          } catch (eST) {}
          try {
            var pol = H.lastBody() && H.lastBody().userData && H.lastBody().userData.poly2;
            if (pol && pol.length > 2) {
              var caA = Math.cos(Math.atan2(dy, dp)), saA = Math.sin(Math.atan2(dy, dp));
              var cpP = (m.p1 + m.p2) / 2, cpY = (m.y1 + m.y2) / 2, zq;
              var outP = [];
              for (zq = 0; zq < pol.length; zq++) {
                outP.push([cpP + pol[zq][0] * caA - pol[zq][1] * saA,
                           cpY + pol[zq][0] * saA + pol[zq][1] * caA]);
              }
              m.poly = outP;
              if (m.pub) m.pub.poly = outP;
            }
          } catch (ePOL) {}
          /* BILLED AS BUILT (core-v264 / this lib's version). The king post is
             built in the CHORD profile but was billed under the WEB code, and
             every piece at the length between its axis points -- while the
             body is cut to its faces. So the bill had 5.5 m of 60x160 too
             little on the default shed and the cut list did not add up to it.
             Now: the code of the section the body is built in, and the length
             of the body itself, read off its geometry along its own length --
             the same number the cut list reads. */
          var lenB = len;
          try {
            var gB = H.lastBody() && H.lastBody().geometry;
            if (gB) {
              if (!gB.boundingBox) gB.computeBoundingBox();
              var bbB = gB.boundingBox;
              var dB = Math.max(bbB.max.x - bbB.min.x, bbB.max.y - bbB.min.y, bbB.max.z - bbB.min.z);
              if (dB > 0.05) lenB = dB;
            }
          } catch (eLB) {}
          if (sect === WEB) tally.web += lenB; else tally.chord += lenB;
        }
        /* ranch-v60: ONE BODY PER TRUSS AND PLATE SIZE, not one per plate. 696
           plate bodies (24 a truss) were a tenth of the house's bodies, each a
           mesh to build and to merge for drawing; they are baked into one per
           truss and code (userData.qtyN says how many, core-v663) -- RANCH */
        var plB = {}, plO = null;
        /* a plate on each face at every node, turned to the member it lies on */
        for (j = 0; j < T2.nodes.length; j++) {
          var nd = T2.nodes[j];
          var pl, pw, ang, pcp, pcy;

          if (nd.apex) {
            /* SYMMETRIC, ON THE AXIS, AND SHAPED BY THE CHORD.

               A rectangle half as wide as the plate, dropped by the fall over
               that half width, has both its top corners exactly under the chords'
               upper faces. Its height is the chord depth measured PLUMB -- which
               is deeper than the timber, because the chord is on the slope. */
            var tanP = (ridgeYOf - eaveYOf) / hWallOf;
            var cosP = 1 / Math.sqrt(1 + tanP * tanP);
            pl = PLATE.l;
            pw = Math.max(0.04, (CHORD.d / cosP) - 0.006);
            ang = 0;
            /* ON THE TRUSS'S AXIS, WHICH IS NOT THE HOUSE'S.

               This was 0 -- the middle of the building -- written back when the
               apex was always there. Since the truss got two tails the ridge
               moves between them, so the chords meet at nd.p and the plate stayed
               behind on the house centre line, hanging in the air beside the
               joint it is supposed to be holding.

               The node already carries the shift. Ask it. */
            pcp = nd.p;
            pcy = ridgeYOf - (pl / 2) * tanP;
          } else {
            /* never wider than the timber it sits on, so it cannot show at the
               edges; and pushed inboard at a member end so it finishes flush */
            /* THE SQUARE IS FOR A THREE-PIECE JOINT ONLY.

               Where the chord takes an upright AND a diagonal, the plate has to
               lap all three, so it is square and sits on the chord's FACE --
               half over the chord, half over the webs. Where only one web
               arrives, the narrow plate the rest of the truss uses is right and
               the square would just be steel (Marek). */
            var both = !!(nd.vert && nd.diag);
            pl = both ? PLATE.sq : PLATE.l;
            pw = both ? PLATE.sq : Math.min(PLATE.w, CHORD.d - 0.004);
            ang = Math.atan2(nd.dy, nd.dp);
            var shift = nd.end ? (pl / 2) : 0;
            pcp = nd.p + nd.dp * shift;
            pcy = nd.y + nd.dy * shift;
            if (both) {
              var tanF = (ridgeYOf - eaveYOf) / hWallOf;
              pcy += (Math.abs(nd.y - eaveYOf) < 0.05)
                ? (CHORD.d / 2)
                : (-(CHORD.d / 2) * Math.sqrt(1 + tanF * tanF));
            }
          }

          /* ranch-v59: THE PLATE WEARS ITS OWN CODE (RANCH) */
          var heelM = !!nd.end && !nd.apex && Math.abs(nd.y - eaveYOf) < 0.001;
          var codeP = (big || (heelM && _trussCheck && _trussCheck.heelBig)) ? PLATE.codeBig : PLATE.code;
          var pc = world(pcp, pcy, u);
          for (var f = -1; f <= 1; f += 2) {
            /* the plate is turned the same way the member under it is: on the
               mesh, so a plate reports its own 150 x 100 and not the box its
               slope happens to fill */
            var pm;
            if (CHK) pm = plO || (plO = new T.Object3D());
            else {
              var pg = new T.BoxGeometry(pl, pw, PLATE.t);
              pm = new T.Mesh(pg, steel);
              pm.userData.trussPart = 'plate';
            }
            pm.rotation.set(0, yaw, ang);
            pm.position.set(pc[0] + (alongL ? 0 : f * (CHORD.t / 2 + PLATE.t / 2)),
                            pc[1],
                            pc[2] + (alongL ? f * (CHORD.t / 2 + PLATE.t / 2) : 0));
            if (CHK) {
              pm.updateMatrix();
              (plB[codeP] = plB[codeP] || CHK.boxes()).add(pl, pw, PLATE.t, 0, 0, 0, pm.matrix.elements);
              /* the heel takes the big plate where the check says the small pair is short (ranch-v7) */
              var heelN = !!nd.end && !nd.apex && Math.abs(nd.y - eaveYOf) < 0.001;
              if (big || (heelN && _trussCheck && _trussCheck.heelBig)) { tally.platesBig++; if (heelN && !big) tally.heelBig = (tally.heelBig || 0) + 1; } else tally.plates++;
            } else {
              g.add(pm);
              if (big) tally.platesBig++; else tally.plates++;
            }
          }
        }
        /* the truss's plates, one body a code, under the plate's own mark (v59) */
        for (var kP in plB) {
          if (!plB.hasOwnProperty(kP) || !plB[kP].n()) continue;
          _asmMark(ctx, 'truss_plate', 'top', 'Truss connector plate', kP);
          var nP = plB[kP].n(), mP = plB[kP].mesh(T, steel);
          mP.userData.trussPart = 'plate';
          mP.userData.qtyN = nP;
          g.add(mP);
        }
      }

      if (H.setTally) H.setTally(tally);
      /* THE PLATES ARE TRUSS PLATES. They were billed as FAT00001, a 5 mm
         carpentry bracket -- a different product at a different price -- while
         the two rows this file has always named sat unused: ROT00005 at an
         ordinary node and ROT00006 at a girder's (Marek). */
      if (tally.plates) H.add(PLATE.code, tally.plates);
      if (tally.platesBig) H.add(PLATE.codeBig, tally.platesBig);
      H.add(CHORD.code, tally.chord);
      H.add(WEB.code, tally.web);

      /* AND THE TIE THAT HOLDS THE TRUSS DOWN, WHICH IS NOT BILLED AND CANNOT BE.

         Every truss bears on the wall plate at two ends and nothing in this bill
         holds it there. Gravity is not a fixing: the load case that matters is
         wind lifting the roof off, and the piece that answers it is a hurricane
         tie at each bearing, nailed into the chord and into the plate.

         A tie is not a connector plate: the plate is inside the truss and is
         already counted twice a node, so billing ties against it would put a
         real quantity out by hundreds of pieces. ROT00007 is the sheet's own
         hurricane tie and FAS38038 the 3.8x38 connector nail that fills it.

         Both items exist now: FAT00012 is the strap and FAS38038 the 3.8x38
         connector nail that fills its holes. Ten a strap, five each side of the
         joint -- a strap nailed with fewer is a strap rated for less. */
      var tiePlies = (stations.length - skipped) + girders * (((_trussCheck && _trussCheck.plies) || 2) - 1);
      var ties = tiePlies * 2;
      /* barndo-v7: the post frame bolts every heel into its post (PF truss heel) */
      if (ties && H.noTies && H.noTies()) { ctx.log('FIX  truss ties none -- the post frame bolts each truss into its post'); ties = 0; }
      if (ties) {
        H.add(TRUSS_TIE.code, ties);
        H.add(TRUSS_TIE.nail, ties * TRUSS_TIE.nailsPerTie);
        ctx.log('FIX  truss ties ' + ties + ' x ' + TRUSS_TIE.code + ' (' + tiePlies +
                ' ply x 2 bearings) + ' + (ties * TRUSS_TIE.nailsPerTie) + ' x ' +
                TRUSS_TIE.nail + ' at ' + TRUSS_TIE.nailsPerTie + ' a strap');
      }
      ctx.log('roof     ' + tally.trusses + ' Fink trusses at ' + API.fmtLen(sp) + ' oc, ' +
              'span ' + API.fmtLen(hWall * 2) + ', ' + TRUSS_PANELS + ' panels a side');
      if (CHK && CHK.log) { try { CHK.log(ctx); } catch (eTCL) { ctx.log('TRUSS CHECK log threw: ' + ((eTCL && eTCL.message) || eTCL)); } }
      /* THE TWO ROOFS, SIDE BY SIDE. The frame and the published plane have to be
         the same building. When they were not, what showed it was the gutter
         hanging where no rafter ended -- so the numbers now sit next to each
         other in the log instead of being inferred from a screenshot. */
      (function () {
        var PL = [];
        try { PL = roofPlanes() || []; } catch (ePl) { return; }
        var tipA = (alongL ? x0 : z0) - tailA, tipB = (alongL ? x1 : z1) + tailBB;
        var ridgeAt = (tipA + tipB) / 2;
        var eA2 = null, eB2 = null, pq;
        for (pq = 0; pq < PL.length; pq++) {
          if (!PL[pq].eave || !PL[pq].eave.length) continue;
          var ev = PL[pq].eave[0][alongL ? 0 : 2];
          if (eA2 === null || ev < eA2) eA2 = ev;
          if (eB2 === null || ev > eB2) eB2 = ev;
        }
        ctx.log('roof     frame tips ' + Math.round(tipA * 1000) + ' / ' + Math.round(tipB * 1000) +
                ', ridge at ' + Math.round(ridgeAt * 1000) +
                ' | plane eaves ' + (eA2 == null ? '?' : Math.round(eA2 * 1000)) + ' / ' +
                (eB2 == null ? '?' : Math.round(eB2 * 1000)) +
                ((eA2 != null && Math.abs(eA2 - tipA) < 0.02 && Math.abs(eB2 - tipB) < 0.02)
                  ? '   they agree'
                  : '   <-- FRAME AND PLANE DISAGREE, so anything holding the eave line hangs free'));
      })();

      ctx.log('roof     spacing ' + API.fmtLen(stepT) + ' -- ' + nBays + ' equal bay(s) over ' +
              API.fmtLen(runSpan) + ', not the module with a stub at the end');
      ctx.log('roof     they run ' + API.fmtLen(runTo - runFrom) + ' -- the ROOF, not the ' +
              'building: rake ' + API.fmtLen(rakeA) + ' + ' + API.fmtLen(runTo - runFrom - rakeA - rakeB) +
              ' + ' + API.fmtLen(rakeB));
      ctx.log('roof     tails ' + API.fmtLen(tailA) + ' / ' + API.fmtLen(tailBB) +
              ' -- the triangle is SCALED to them, not stretched' +
              (tailA !== tailBB
                ? ('; unequal, so the RIDGE moves ' + API.fmtLen(Math.abs(tailBB - tailA) / 2) +
                   ' toward the deeper side and each tail is its own length')
                : ''));
      ctx.log('roof     timber: chords ' + tally.chord.toFixed(1) + ' m of ' +
              Math.round(CHORD.t * 1000) + 'x' + Math.round(CHORD.d * 1000) +
              ', webs ' + tally.web.toFixed(1) + ' m of ' +
              Math.round(WEB.t * 1000) + 'x' + Math.round(WEB.d * 1000));
      if (CHK) ctx.log('roof     connector plates: ' + tally.plates + ' x ' + PLATE.code + ' + ' + (tally.platesBig || 0) + ' x ' + PLATE.codeBig +
              ' (' + tr.nodes.length + ' nodes x 2 faces x ' + tally.trusses + ' trusses' +
              (tally.heelBig ? ('; ' + tally.heelBig + ' of the big ones at the heels, by the check') : '') + ')');
      else ctx.log('roof     ' + tally.plates + ' connector plates (' + tr.nodes.length +
              ' nodes x 2 faces x ' + tally.trusses + ' trusses) -- ' +
              CATALOG['steel.trussPlate'].code);
      ctx.log('roof     every plate turned to its member and kept inside the timber, ' +
              'width ' + Math.round(Math.min(PLATE.w, CHORD.d - 0.004) * 1000) +
              ' mm in a ' + Math.round(CHORD.d * 1000) + ' mm chord');
      _why(ctx, 'roof     apex: chords carried to the axis and butted, plate upright, ' +
              'centred and symmetric about the ridge');
      ctx.log('roof     bottom chord bears at ' + API.wallTop().toFixed(3) +
              ', which is the top of the wall plate -- on it, not in it');
      _why(ctx, 'roof     the bottom chord is the tie, so the walls are no longer spread');
      var heelRise = tail * Math.tan(R.pitch * Math.PI / 180);
      ctx.log('roof     eave: the WHOLE truss runs ' + API.fmtLen(tail) +
              ' past the finished face (' + API.fmtLen(API.STATE.roof.eave) +
              ' overhang + ' + API.fmtLen(API.facadeThk()) + ' facade)');
      ctx.log('roof     raised heel ' + Math.round(heelRise * 1000) +
              ' mm -- what it costs to bring the bottom chord out, and what leaves ' +
              'full insulation depth over the wall plate');
      _why(ctx, 'roof     gable: nothing of the frame passes the wall -- ' +
              'the rake comes from the sheathing, and the end trusses are set in by a plate');
      return { eaveY: eaveY, ridgeY: ridgeY };
    }

    /* =========================================================================
       THE FOUNDATION MODULE (core-v764, the third shared module of the house
       line -- status/modules.md). The building's base written once: the slab,
       the crawl space (strip footings, stem walls, bars and cage, sill and
       anchor bolts, vents, the access door, the ground film), the point
       footings (screws, pads, piers) with their saddles, the entrance steps,
       the bill. The ADU's text, with every product's own behind a hook:

         API.foundation(ctx, {
           api, FOOT, JOIST, SADDLE, FND_LAYERS, STUD, CONCRETE, STEEL_G,
           MAGENTA:   the product's constants
           isNew:     function () -- the base under review (magenta)
           footDepth, stoneMat, screwPile: the product's own
           billReset, add, setTally: the product's bill and _footTally
           slab:      function (ctx) -> true when the product built its own
                      slab (GARAGE APT, ANY HOUSE, RANCH on grade)
           dig:       { mat(T) } -- RANCH: trenches and holes drawn and billed
                      as drawn, a pier as tall as a raised floor needs
           skip:      function (side) -- the L's second rectangle (RANCH, ANY
                      HOUSE); skipX: ANY HOUSE's shortened z walls
           seams:     function (pts, ctx) -> pts (ANY HOUSE, the L's seam row)
           terraceSideAt: function (x, z) -- a product's own terraces (SHED)
           steps:     function () -> false for no steps (TAURUS, GARDEN BAR),
                      stepsNone / stepsTag their log; skipStep(op, seg)
         })

       Carried to every product (SHED's fixes, Marek): the crawl space's access
       door moves off a side a terrace covers, and a vent that falls on the
       door slides past it; the sill follows the product's stud.
       ========================================================================= */
    function foundation(ctx, H) {
      /* the product's own pieces, under the names the text has always used */
      var API = H.api, FOOT = H.FOOT, JOIST = H.JOIST, SADDLE = H.SADDLE, FND_LAYERS = H.FND_LAYERS,
          STUD = H.STUD, CONCRETE = H.CONCRETE, STEEL_G = H.STEEL_G, MAGENTA = H.MAGENTA;
      var _footDepth = H.footDepth, _stoneMat = H.stoneMat, _screwPile = H.screwPile;
      var isNewB = function () { try { return !!H.isNew(); } catch (eN) { return false; } };
      /* which terrace side a point at the wall line is under (300 either way):
         the product's own probe where it has its own terraces (SHED), else the
         core's terrace */
      var _fdTerrSide = function (px, pz) {
        if (H.terraceSideAt) return H.terraceSideAt(px, pz);
        var Tt = _terrace(), r = Tt && Tt.on ? _terraceRect() : null;
        if (!r) return null;
        return (px >= r.x0 - 0.30 && px <= r.x1 + 0.30 && pz >= r.z0 - 0.30 && pz <= r.z1 + 0.30) ? Tt.side : null;
      };
      /* RANCH's ground as dug (ranch-v80/v81/v2): the trenches and holes drawn
         and billed as drawn, the stone and the sheet under their own codes and
         always handed to _box, a pier as tall as a raised floor needs */
      var DIG = H.dig || null;
      /* the L's second rectangle (RANCH ranch-v5, ANY HOUSE anyhouse-lib-v11):
         no stem wall on the side it shares with the first; H.skipX -- ANY
         HOUSE's own, the z walls shortened where an x side is skipped */
      var SKIP = H.skip || null, skX = !!H.skipX;
      /* a product with its own slab (GARAGE APT, ANY HOUSE, RANCH's on grade) */
      if (H.slab && H.slab(ctx)) return;
      try { _asmMark(ctx, ({ screws: 'base_screws', pads: 'base_pads', piers: 'base_piers', strips: 'base_strips' })[STATE.foundation] || 'base', 'top', STATE.foundation,
                      ({ screws: 'steel.screwPile', pads: 'grd.padBlock', piers: 'grd.concrete', strips: 'grd.concrete' })[STATE.foundation]); } catch (eM0) {}
      var T = ctx.THREE, g = ctx.group, i;
      var conc = new T.MeshStandardMaterial({
        color: isNewB() ? MAGENTA : CONCRETE, roughness: 0.95, metalness: 0.0 });
      var steel = new T.MeshStandardMaterial({
        color: isNewB() ? MAGENTA : STEEL_G, roughness: 0.5, metalness: 0.7 });
      var mDigB = DIG ? DIG.mat(T) : null;   /* ranch-v81: the holes and trenches as dug */
      var kind = API.STATE.foundation;
      var deckTop = ctx.stack.slab;
      var depth = _footDepth();

      var P = ctx.poly, x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9;
      for (i = 0; i < P.length; i++) {
        x0 = Math.min(x0, P[i][0]); x1 = Math.max(x1, P[i][0]);
        z0 = Math.min(z0, P[i][1]); z1 = Math.max(z1, P[i][1]);
      }
      var w = x1 - x0, l = z1 - z0, cx = (x0 + x1) / 2, cz = (z0 + z1) / 2;

      /* WHERE THE TIMBER STARTS. Everything below is measured DOWN from it, so a
         support can never end up inside the floor it carries. */
      var timberY = deckTop - JOIST.deck - JOIST.d;
      var headY = timberY - SADDLE.plate;

      H.billReset();
      var tally = { kind: kind, depth: depth, points: 0, concrete: 0, excav: 0,
                    gravel: 0, screws: 0, pads: 0, saddles: 0, geo: 0 };

      function saddle(px, pz, ang) {
        if (kind === 'slab' || kind === 'strips') return;
        var half = JOIST.t / 2 + SADDLE.t / 2;
        var len = 0.16;
        /* ranch-v80: under its own code (it stood under the pier's concrete), the mark put back after */
        var prevI = ctx.item; if (DIG) _asmCode(ctx, 'steel.saddle', 'top');

        var geo = new T.BoxGeometry(len, SADDLE.plate, len);
        geo.rotateY(ang);
        geo.translate(px, timberY - SADDLE.plate / 2, pz);
        g.add(new T.Mesh(geo, steel));

        /* THE CHEEKS GO EITHER SIDE OF THE TIMBER, WHICH MEANS ACROSS IT.

           They were spaced along the member instead -- every bracket a quarter
           turn out, so the U opened along the beam it was meant to grip and the
           beam ran straight over the top of it. The local convention here is that
           +Z is the member direction, so the pair are offset in X and their
           length runs in Z. */
        for (var f = -1; f <= 1; f += 2) {
          var ch = new T.BoxGeometry(SADDLE.t, SADDLE.cheek, len);
          ch.translate(f * half, 0, 0);
          ch.rotateY(ang);
          ch.translate(px, timberY + SADDLE.cheek / 2, pz);
          g.add(new T.Mesh(ch, steel));
        }
        tally.saddles++;
        if (DIG) ctx.item = prevI;
      }

      if (kind === 'slab') {
        /* a raft with a thickened edge, and the building stands on it */
        _box(T, g, conc, w, FOOT.slabH, l, cx, deckTop - FOOT.slabH / 2, cz);
        var eW = FOOT.stripW;
        /* the thickened edge is a footing: as deep as the footing depth says,
           never the fixed 500 alone -- a raft in a cold zone goes below frost */
        var eH = Math.max(FOOT.edgeH, depth);
        _box(T, g, conc, w, eH, eW, cx, deckTop - eH / 2, z0 + eW / 2);
        _box(T, g, conc, w, eH, eW, cx, deckTop - eH / 2, z1 - eW / 2);
        _box(T, g, conc, eW, eH, l - eW * 2, x0 + eW / 2, deckTop - eH / 2, cz);
        _box(T, g, conc, eW, eH, l - eW * 2, x1 - eW / 2, deckTop - eH / 2, cz);
        tally.concrete = w * l * FOOT.slabH + 2 * (w + l - eW * 2) * eW * (eH - FOOT.slabH);
        tally.gravel = w * l * FOOT.gravel;
        tally.excav = w * l * (FOOT.gravel + 0.10);

      } else if (kind === 'strips') {
        _asmMark(ctx, 'grd_concrete', 'top', 'Concrete footing and stem wall', 'grd.concrete');
        /* A CRAWL SPACE. A strip footing at frost depth under every wall and
           one down the middle; on it a concrete stem wall up to the sill,
           which sits under the joists; anchor bolts through the sill at 1.8 m,
           a sill seal under it, vents through the wall every 3 m, and a vapour
           film over the ground inside. The deck stands 650 above grade here
           (the floor height follows the footing). Ported from nothing: this is
           how a US crawl space is built (Marek). */
        /* THE SILL PLATE IS AS WIDE AS THE WALL ON IT, so it follows the stud
           rather than carrying the 160 it was born with (2x4, Marek; SHED) */
        var sW = FOOT.stripW, sillT = 0.045, sillW = STUD.d, mag = isNewB();
        var joistBot = deckTop - JOIST.deck - JOIST.d;
        var wallTop = joistBot - sillT;             /* the stem wall's top */
        var footH = 0.30, footW = sW + 0.30;       /* the footing: wider, 300 deep, its bottom at frost depth */
        var footBot = -depth, footTop = -depth + footH;
        var stemH = wallTop - footTop;
        var mBar = new T.MeshStandardMaterial({ color: mag ? MAGENTA : 0x6B4F3A, roughness: 0.6, metalness: 0.5 });
        function strip(bw, bd, px, pz) {
          if (DIG) {
            /* ranch-v81: its trench, the footing's width, 50 under it -- billed as drawn */
            var runD = Math.max(bw, bd), hD = depth + 0.05;
            _asmCode(ctx, 'grd.excavation', 'top');
            if (bw >= bd) _box(T, g, mDigB, runD, hD, footW, px, -hD / 2, pz); else _box(T, g, mDigB, footW, hD, runD, px, -hD / 2, pz);
            tally.digDrawn = (tally.digDrawn || 0) + runD * footW * hD;
          }
          _asmMark(ctx, 'grd_concrete', 'top', 'Concrete footing and stem wall', 'grd.concrete');
          /* THE FOOTING SITS AT FROST DEPTH: from footBot up to footTop, the
             stem wall on it. It was drawn a footing higher -- its bottom 300 mm
             above frost depth and its body inside the stem wall's foot -- so the
             bars, put where the footing should be, showed under it (Marek). */
          _box(T, g, conc, bw + 0.30, footH, bd + 0.30, px, footTop - footH / 2, pz);   /* footing, spread */
          _box(T, g, conc, bw, stemH, bd, px, footTop + stemH / 2, pz);                          /* stem wall */
          tally.concrete += (bw + 0.30) * (bd + 0.30) * footH + bw * bd * stemH;
          tally.stripLen = (tally.stripLen || 0) + Math.max(bw, bd);   /* the strip's run, for its bars */
          /* THE BARS, DRAWN: four 14 mm bars the length of the footing at its
             corners, 50 mm of cover -- what the concrete row's reinforcement
             actually is, seen when the base is shown whole (Marek) */
          _asmCode(ctx, 'FCO00003', 'top');
          var alongX = bw >= bd, run = Math.max(bw, bd), cov = 0.05, dB = 0.014;
          var wOut = (alongX ? bd : bw) + 0.30, yLo = footTop - footH + cov, yHi = footTop - cov, q9;
          for (q9 = 0; q9 < 4; q9++) {
            var off = ((q9 % 2) ? 1 : -1) * (wOut / 2 - cov), yb = (q9 < 2) ? yLo : yHi;
            if (alongX) _box(T, g, mBar, run + 0.30 - 2 * cov, dB, dB, px, yb, pz + off);
            else _box(T, g, mBar, dB, dB, run + 0.30 - 2 * cov, px + off, yb, pz);
          }
          /* THE CAGE: a stirrup round the four bars every 300 mm (drawn as its
             four sides), a spacer under the cage every metre, and the tying
             wire that holds it -- billed by weight, not drawn (Marek) */
          _asmCode(ctx, 'WWR00061', 'top');
          var sLen = run + 0.30 - 2 * cov, nSt = Math.max(2, Math.floor(sLen / 0.30) + 1), dS = 0.006, q8;
          var wIn = wOut - 2 * cov, hIn = yHi - yLo;
          for (q8 = 0; q8 < nSt; q8++) {
            var at = -sLen / 2 + cov + (sLen - 2 * cov) * (nSt === 1 ? 0.5 : q8 / (nSt - 1));
            var sx = alongX ? px + at : px, sz = alongX ? pz : pz + at;
            if (alongX) {
              _box(T, g, mBar, dS, hIn, dS, sx, (yLo + yHi) / 2, sz - wIn / 2); _box(T, g, mBar, dS, hIn, dS, sx, (yLo + yHi) / 2, sz + wIn / 2);
              _box(T, g, mBar, dS, dS, wIn, sx, yLo, sz); _box(T, g, mBar, dS, dS, wIn, sx, yHi, sz);
            } else {
              _box(T, g, mBar, dS, hIn, dS, sx - wIn / 2, (yLo + yHi) / 2, sz); _box(T, g, mBar, dS, hIn, dS, sx + wIn / 2, (yLo + yHi) / 2, sz);
              _box(T, g, mBar, wIn, dS, dS, sx, yLo, sz); _box(T, g, mBar, wIn, dS, dS, sx, yHi, sz);
            }
          }
          H.add('WWR00061', nSt);
          H.add('FFM00004', Math.max(2, Math.ceil(sLen / 1.0)) * 2);
          H.add('FEC00026', Math.round(sLen * 4 * 0.012 * 100) / 100 + nSt * 0.02);   /* ~12 g/m of bar + 20 g per stirrup */
          _asmCode(ctx, 'grd.concrete', 'top');
        }
        /* ON THE L (ranch-v5, anyhouse-lib-v11): the second rectangle has no
           stem wall where it meets the first -- the first one's wall runs
           there -- and its long walls run on to it. Without H.skip every
           figure is the plain rectangle's, to the last digit. */
        var skS = function (sd) { return !!(SKIP && SKIP(sd)); };
        var zA = z0 + (skS('z0') ? 0 : sW), zB = z1 - (skS('z1') ? 0 : sW);
        var lZ = SKIP ? (zB - zA) : (l - sW * 2), cZ = SKIP ? (zA + zB) / 2 : cz;
        var skXs = function (sd) { return skX && skS(sd); };
        var xA = x0 + (skXs('x0') ? 0 : sW), xB = x1 - (skXs('x1') ? 0 : sW), wX = xB - xA, cXq = (xA + xB) / 2;
        var wZ = skX ? (wX + (skXs('x0') ? 0 : sW) + (skXs('x1') ? 0 : sW)) : w;
        var cXz = skX ? (cXq + ((skXs('x1') ? 0 : sW) - (skXs('x0') ? 0 : sW)) / 2) : cx;
        if (!skS('z0')) strip(wZ, sW, cXz, z0 + sW / 2);
        if (!skS('z1')) strip(wZ, sW, cXz, z1 - sW / 2);
        if (!skS('x0')) strip(sW, lZ, x0 + sW / 2, cZ);
        if (!skS('x1')) strip(sW, lZ, x1 - sW / 2, cZ);
        strip(sW, lZ, cx, cZ);
        /* the sill plate on the perimeter and the middle wall, with its seal */
        _asmMark(ctx, 'sill_plate', 'top', 'Sill plate and anchor bolts', STUD.code);
        var sillM = new T.MeshStandardMaterial({ color: mag ? MAGENTA : 0xA8845A, roughness: 0.9 });
        var runs = [[w, sillW, cx, z0 + sW / 2, 'z0'], [w, sillW, cx, z1 - sW / 2, 'z1'], [sillW, lZ, x0 + sW / 2, cZ, 'x0'], [sillW, lZ, x1 - sW / 2, cZ, 'x1'], [sillW, lZ, cx, cZ, '']].filter(function (r9) { return !r9[4] || !skS(r9[4]); }), rq, sillLen = 0, bolts = 0;
        for (rq = 0; rq < runs.length; rq++) {
          var rr = runs[rq];
          _box(T, g, sillM, rr[0], sillT, rr[1], rr[2], wallTop + sillT / 2, rr[3]);
          var len = Math.max(rr[0], rr[1]); sillLen += len;
          /* anchor bolts every 1.8 m, one within 300 of each end */
          var nA = Math.max(2, Math.ceil(len / 1.8) + 1), a;
          for (a = 0; a < nA; a++) {
            var u = 0.3 + (len - 0.6) * a / (nA - 1);
            var bx = rr[0] > rr[1] ? (rr[2] - rr[0] / 2 + u) : rr[2], bz = rr[0] > rr[1] ? rr[3] : (rr[3] - rr[1] / 2 + u);
            /* THE BOLT AS IT IS: the shank cast into the stem wall and up
               through the sill, a washer and a hex nut on the sill, 30 mm of
               thread proud (Marek: they looked like spikes) */
            var bolt = new T.Mesh(new T.CylinderGeometry(0.006, 0.006, 0.30, 8), steel);
            bolt.position.set(bx, wallTop + sillT + 0.03 - 0.15, bz); g.add(bolt);
            var washer = new T.Mesh(new T.CylinderGeometry(0.022, 0.022, 0.003, 12), steel);
            washer.position.set(bx, wallTop + sillT + 0.0015, bz); g.add(washer);
            var nut = new T.Mesh(new T.CylinderGeometry(0.011, 0.011, 0.010, 6), steel);
            nut.position.set(bx, wallTop + sillT + 0.003 + 0.005, bz); g.add(nut);
            bolts++;
          }
        }
        /* vents through the perimeter wall every 3 m, 400 x 200 (the US 16 x 8), at the wall's top -- FCO00011 */
        _asmMark(ctx, 'foundation_vents', 'side', 'Foundation vents', 'FCO00011');
        /* THE POSITIONS ARE WORKED OUT NOW AND DRAWN LATER.

           The access door is set into this same stem wall and its position is
           decided further down -- so a vent every 3 m and a 600 mm door chosen
           independently sooner or later land on the same piece of wall, and they
           did (Marek: the access opening collides with a vent). The vents are
           the ones with room to move: they are spaced for air, not for a
           position, so whichever one falls on the door is dropped and the run
           re-divided over what is left. */
        var ventM = new T.MeshStandardMaterial({ color: mag ? MAGENTA : 0x3A3D40, roughness: 0.6 }), vents = 0, per = [[w, cx, z0 + sW / 2, 0, 'z0'], [w, cx, z1 - sW / 2, 0, 'z1'], [l, x0 + sW / 2, cz, 1, 'x0'], [l, x1 - sW / 2, cz, 1, 'x1']].filter(function (v9) { return !skS(v9[4]); }), vq;
        var ventAt = [];
        for (vq = 0; vq < per.length; vq++) {
          var pr = per[vq], nV = Math.max(1, Math.round(pr[0] / 3.0)), v;
          for (v = 0; v < nV; v++) {
            var uv = pr[0] * (v + 0.5) / nV;
            if (pr[3] === 0) ventAt.push([pr[1] - pr[0] / 2 + uv, pr[2], 0]);
            else ventAt.push([pr[1], pr[2] - pr[0] / 2 + uv, 1]);
          }
        }
        /* THE WAY IN. A crawl space has to be reachable (IRC R408.4): an
           opening in the stem wall beside the entrance door, 600 x 450, with a
           galvanized frame and a hinged panel -- the way it is actually done
           here, rather than a hatch in the floor (Marek). */
        try {
          var doorW9 = 0.60, doorH9 = Math.min(0.45, Math.max(0.25, wallTop - (-0.05) - 0.10));
          var entX = null, entZ = null, sq9, oq9, SEG9 = ctx.seg || [];
          for (sq9 = 0; sq9 < SEG9.length && entX === null; sq9++) {
            var sg9 = SEG9[sq9]; if (!sg9 || !sg9.openings) continue;
            for (oq9 = 0; oq9 < sg9.openings.length; oq9++) {
              var op9 = sg9.openings[oq9];
              if ((op9.sill || 0) > 0.05) continue;   /* a door: its sill is on the floor */
              var ux9 = (sg9.x1 - sg9.x0) / sg9.len, uz9 = (sg9.z1 - sg9.z0) / sg9.len;
              /* BESIDE THE DOOR, ON THE WALL THAT IS THERE. The offset ran off
                 the end of short walls and the door hung in the air between the
                 strips; it is clamped to the wall's own run and snapped onto
                 the strip line (Marek). */
              var uAt = op9.at + op9.w / 2 + 0.80;
              if (uAt > sg9.len - 0.45) uAt = op9.at - op9.w / 2 - 0.80;
              uAt = Math.max(0.45, Math.min(sg9.len - 0.45, uAt));
              entX = sg9.x0 + ux9 * uAt; entZ = sg9.z0 + uz9 * uAt;
              if (Math.abs(ux9) > Math.abs(uz9)) entZ = (Math.abs(entZ - z0) < Math.abs(entZ - z1)) ? z0 + sW / 2 : z1 - sW / 2;
              else entX = (Math.abs(entX - x0) < Math.abs(entX - x1)) ? x0 + sW / 2 : x1 - sW / 2;
              break;
            }
          }
          /* NOT UNDER THE DECK -- ROUND THE CORNER INSTEAD.

             The door is set into the stem wall beside the entrance, and with a
             terrace on that side it would open into the underside of the deck,
             where nobody can reach it. Skipping it was the first answer and it
             left a crawl space with no way in at all, which is worse than a
             door in an odd place (Marek). So it MOVES: the first side the
             terrace does not cover, at the middle of that wall, away from the
             corners. Only if the terrace has gone all the way round is there
             nowhere left, and then the log says so rather than saying nothing. */
          var tHere = (entX !== null) ? _fdTerrSide(entX, entZ) : null;
          if (tHere) {
            var cxB = (x0 + x1) / 2, czB = (z0 + z1) / 2, movedTo = null;
            var cand = [
              ['z0', cxB, z0 + sW / 2], ['z1', cxB, z1 - sW / 2],
              ['x0', x0 + sW / 2, czB], ['x1', x1 - sW / 2, czB]
            ], cq9;
            for (cq9 = 0; cq9 < cand.length; cq9++) {
              if (_fdTerrSide(cand[cq9][1], cand[cq9][2])) continue;
              entX = cand[cq9][1]; entZ = cand[cq9][2]; movedTo = cand[cq9][0];
              break;
            }
            if (movedTo) {
              ctx.log('base     crawl space access door MOVED off ' + tHere + ' to ' +
                      movedTo + ' at ' + Math.round(entX * 1000) + ',' + Math.round(entZ * 1000) +
                      ' -- the terrace covers the entrance side and the door would ' +
                      'have opened under the deck');
            } else {
              entX = null;
              ctx.log('base     crawl space access door: NOWHERE TO PUT IT -- the terrace ' +
                      'covers every side, so the crawl space needs a hatch in the floor ' +
                      'instead and there is no catalogue row for one yet');
            }
          }
          if (entX !== null) {
            var yDoor = wallTop - 0.12 - doorH9 / 2;
            var mFrame9 = new T.MeshStandardMaterial({ color: mag ? MAGENTA : 0x9EA2A0, roughness: 0.5, metalness: 0.6 });
            var mPan9 = new T.MeshStandardMaterial({ color: mag ? MAGENTA : 0xB8BCBA, roughness: 0.6, metalness: 0.4 });
            var alongX9 = Math.abs(entZ - (z0 + sW / 2)) < 0.02 || Math.abs(entZ - (z1 - sW / 2)) < 0.02;   /* it sits on a strip running in x */
            /* SET INTO THE WALL, NOT ON IT. The frame is a reveal lining inside
               the opening and the panel sits back in it, 20 mm proud of the
               concrete at most -- it stood out like a box before (Marek). */
            _asmCode(ctx, 'FCO00013', 'side');
            var frT9 = 0.04, panT9 = 0.02, outF9 = sW / 2 - frT9 / 2, outP9 = sW / 2 - panT9 / 2 - 0.01;
            var oN9 = (Math.abs(entX - (x0 + x1) / 2) > Math.abs(entZ - (z0 + z1) / 2))
                      ? { x: (entX > (x0 + x1) / 2) ? 1 : -1, z: 0 }
                      : { x: 0, z: (entZ > (z0 + z1) / 2) ? 1 : -1 };
            if (alongX9) {
              /* the lining: head, sill and two jambs round the opening */
              _box(T, g, mFrame9, doorW9 + 0.08, 0.04, frT9, entX, yDoor + doorH9 / 2 + 0.02, entZ + oN9.z * outF9);
              _box(T, g, mFrame9, doorW9 + 0.08, 0.04, frT9, entX, yDoor - doorH9 / 2 - 0.02, entZ + oN9.z * outF9);
              _box(T, g, mFrame9, 0.04, doorH9, frT9, entX - doorW9 / 2 - 0.02, yDoor, entZ + oN9.z * outF9);
              _box(T, g, mFrame9, 0.04, doorH9, frT9, entX + doorW9 / 2 + 0.02, yDoor, entZ + oN9.z * outF9);
              _box(T, g, mPan9, doorW9, doorH9, panT9, entX, yDoor, entZ + oN9.z * outP9);
            } else {
              _box(T, g, mFrame9, frT9, 0.04, doorW9 + 0.08, entX + oN9.x * outF9, yDoor + doorH9 / 2 + 0.02, entZ);
              _box(T, g, mFrame9, frT9, 0.04, doorW9 + 0.08, entX + oN9.x * outF9, yDoor - doorH9 / 2 - 0.02, entZ);
              _box(T, g, mFrame9, frT9, doorH9, 0.04, entX + oN9.x * outF9, yDoor, entZ - doorW9 / 2 - 0.02);
              _box(T, g, mFrame9, frT9, doorH9, 0.04, entX + oN9.x * outF9, yDoor, entZ + doorW9 / 2 + 0.02);
              _box(T, g, mPan9, panT9, doorH9, doorW9, entX + oN9.x * outP9, yDoor, entZ);
            }
            H.add('FCO00013', 1);
            ctx.log('base     crawl space access door FCO00013 600x' + Math.round(doorH9 * 1000) + ' beside the entrance at ' + Math.round(entX * 1000) + ',' + Math.round(entZ * 1000));
          } else ctx.log('base     crawl space access door: no entrance door yet, none placed');
        } catch (eAD) { ctx.log('base     access door THREW ' + (eAD && eAD.message)); }
        /* NOW THE VENTS, ROUND WHATEVER THE DOOR TOOK. Half the door plus half
           the vent plus 100 mm of wall between them is the clear the mason
           needs; anything closer and the two openings are one hole. */
        (function () {
          var clearV = 0.60 / 2 + 0.40 / 2 + 0.10, vq2, moved = 0, dropped = 0;
          var okX = (typeof entX === 'number'), inset = 0.30;
          for (vq2 = 0; vq2 < ventAt.length; vq2++) {
            var va = ventAt[vq2];
            if (okX && Math.abs(va[0] - entX) < clearV && Math.abs(va[1] - entZ) < clearV) {
              /* SLIDE IT PAST, DO NOT LOSE IT. A crawl space is vented by area,
                 so a vent taken out is rot later; it moves along its own run to
                 the far side of the door, and only if that runs off the wall is
                 it given up. */
              var ax = (va[2] === 0) ? 0 : 1;   /* 0 = the run goes in x, 1 = in z */
              var cur = ax ? va[1] : va[0], mid = ax ? entZ : entX;
              var lo = ax ? (z0 + inset) : (x0 + inset), hi = ax ? (z1 - inset) : (x1 - inset);
              var tryA = mid + clearV, tryB = mid - clearV;
              var put = null;
              if (tryA <= hi && Math.abs(tryA - cur) <= 3.0) put = tryA;
              else if (tryB >= lo && Math.abs(tryB - cur) <= 3.0) put = tryB;
              if (put === null) { dropped++; continue; }
              if (ax) va[1] = put; else va[0] = put;
              moved++;
            }
            if (va[2] === 0) _box(T, g, ventM, 0.40, 0.20, sW + 0.02, va[0], wallTop - 0.15, va[1]);
            else _box(T, g, ventM, sW + 0.02, 0.20, 0.40, va[0], wallTop - 0.15, va[1]);
            vents++;
          }
          if (moved || dropped) {
            ctx.log('base     access door vs vents: ' + moved + ' vent(s) slid past it, ' +
                    dropped + ' dropped -- a 600 door and a 400 vent need ' +
                    Math.round(clearV * 1000) + ' mm between centres');
          }
        })();

        /* the film over the ground inside */
        _asmMark(ctx, 'layer_vapour_barrier', 'top', 'Vapour barrier', 'ROV00001');
        var filmM = new T.MeshStandardMaterial({ color: mag ? MAGENTA : 0xD9D9D9, roughness: 0.4, transparent: true, opacity: 0.7 });
        var wF = skX ? wX : (w - 2 * sW), cXf = skX ? cXq : cx, lF = SKIP ? lZ : (l - 2 * sW);
        _box(T, g, filmM, wF, 0.004, lF, cXf, 0.004, cZ);
        var film = wF * lF;
        /* the runs there are (on the L's seam the first rectangle's) */
        var runE = (2 - (skS('z0') ? 1 : 0) - (skS('z1') ? 1 : 0)) * w + (3 - (skS('x0') ? 1 : 0) - (skS('x1') ? 1 : 0)) * l;
        tally.excav = (tally.digDrawn > 0) ? tally.digDrawn : (runE * footW * (depth + 0.05));   /* ranch-v81: as drawn */
        tally.gravel = runE * footW * 0.10;
        H.add(STUD.code, sillLen);
        H.add('INS01003', sillLen);
        H.add('FEC00039', bolts);
        H.add('ROV00001', film * 1.10);
        H.add('FCO00011', vents);
        ctx.log('BASE crawl space: stem wall ' + Math.round(stemH * 1000) + ' tall on a ' + Math.round(footW * 1000) + ' x ' + Math.round(footH * 1000) +
                ' footing at ' + Math.round(depth * 1000) + ' below grade; sill ' + Math.round(sillW * 1000) + 'x45 ' + Math.round(sillLen * 10) / 10 + ' m ' + STUD.code + ' on INS01003 seal, ' + bolts +
                ' anchor bolts FEC00039, ' + vents + ' vents 400x200 FCO00011, ground film ' + API.fmtArea(film) + ' ROV00001 (+10%), deck at ' + Math.round(deckTop * 1000));

      } else {
        /* the four point-founded ones share their layout */
        var pts = API.bearingPoints(FOOT.spacing);
        /* the L's seam (ANY HOUSE anyhouse-lib-v11): the second rectangle's rim
           stands on the first one's supports -- the product drops the row */
        if (H.seams) pts = H.seams(pts, ctx);
        tally.points = pts.length;
        for (i = 0; i < pts.length; i++) {
          var px = pts[i][0], pz = pts[i][1];

          var pang = pts[i][2] || 0;

          if (kind === 'screws') {
            /* ONE DRAWING OF A GROUND SCREW, CALLED TWICE.

               The eave posts had a second one written out beside this: a plain
               cylinder, the wrong length, with no taper, no flight and no collar
               -- so the same product appeared as two different objects in one
               model. It is one function now and the posts call it. */
            _screwPile(T, g, steel, px, pz, headY);
            tally.screws++;

          } else if (kind === 'pads') {
            if (DIG) {
              /* ranch-v81: its hole, as billed: the bed + 200, 50 under it */
              _asmCode(ctx, 'grd.excavation', 'top');
              _box(T, g, mDigB, FOOT.padW + 0.20, FOOT.gravel + 0.05, FOOT.padW + 0.20, px, -(FOOT.gravel + 0.05) / 2, pz);
              _asmCode(ctx, 'grd.padBlock', 'top');
            }
            /* A PRECAST FOOTING IS A TRUNCATED PYRAMID -- the shape the block is
               cast in -- so it reads as a different thing from a poured pier
               (Marek). Square base at padW, top half that, padH tall. */
            var pyr = new T.Mesh(
              new T.CylinderGeometry(FOOT.padW * 0.5 / Math.SQRT2, FOOT.padW / Math.SQRT2, FOOT.padH, 4),
              conc);
            pyr.rotation.y = Math.PI / 4;
            pyr.position.set(px, headY - FOOT.padH / 2, pz);
            g.add(pyr);
            /* the bed under the block: stone, textured (ranch-v80: under its own code) */
            if (DIG) _asmCode(ctx, 'grd.gravel', 'top');
            _box(T, g, _stoneMat(T),
                 FOOT.padW + 0.20, FOOT.gravel, FOOT.padW + 0.20,
                 px, headY - FOOT.padH - FOOT.gravel / 2, pz);
            if (DIG) _asmCode(ctx, 'grd.padBlock', 'top');
            tally.pads++;
            tally.gravel += (FOOT.padW + 0.20) * (FOOT.padW + 0.20) * FOOT.gravel;
            tally.excav += (FOOT.padW + 0.20) * (FOOT.padW + 0.20) * (FOOT.gravel + 0.05);

          } else {
            /* A POURED PIER IS ONE COLUMN, ONE FOOTPRINT, IN LAYERS: 400 x 400
               of concrete from the saddle down to the footing depth (600 at
               least, deeper below frost), a 150 bed of stone under it, and a
               geotextile under the bed -- all the same 400 x 400, as the
               Foundations product builds it. The spread footing and the 900
               bed read as three blocks of three sizes and a base a metre wide,
               which is not what gets poured (Marek). Stone and sheet drawn while
               the BASE step is open; all three billed. */
            var pw = FOOT.pierW;
            if (DIG) {
              /* ranch-v81: its hole, as billed: the pier + 200, to the bed's bottom */
              _asmCode(ctx, 'grd.excavation', 'top');
              _box(T, g, mDigB, pw + 0.20, depth + FOOT.gravel, pw + 0.20, px, -(depth + FOOT.gravel) / 2, pz);
              _asmCode(ctx, 'grd.concrete', 'top');
              /* A HIGHER FLOOR, A TALLER PIER (ranch-v2): the floor level is a
                 slider now, and the pier was 600 from its head whatever the
                 floor -- at +900 its foot stood above the ground. It keeps its
                 depth under grade and takes the rise above the ADU's +350. */
              var pLen = depth + Math.max(0, deckTop - 0.35);
              var pierBot = headY - pLen;
              _box(T, g, conc, pw, pLen, pw, px, headY - pLen / 2, pz);
              /* ranch-v80: the stone and the sheet under their own codes (they
                 stood under the pier's concrete), and _box holds them for the
                 qty audit when they are not drawn (all under grade, core-v682) */
              _asmCode(ctx, 'grd.gravel', 'top');
              _box(T, g, _stoneMat(T), pw, FOOT.gravel, pw, px, pierBot - FOOT.gravel / 2, pz);
              _asmCode(ctx, 'grd.geotextile', 'top');
              _box(T, g, new T.MeshStandardMaterial({ color: FND_LAYERS.geo.colour, roughness: 1.0 }),
                   pw, FND_LAYERS.geo.t, pw, px, pierBot - FOOT.gravel - FND_LAYERS.geo.t / 2, pz);
              _asmCode(ctx, 'grd.concrete', 'top');
              tally.concrete += pw * pw * pLen;
              tally.pierH = (tally.pierH || 0) + pLen;   /* for its four bars */
            } else {
              var pierBot = headY - depth;
              _box(T, g, conc, pw, depth, pw, px, headY - depth / 2, pz);
              if (_footFull()) {
                _box(T, g, _stoneMat(T), pw, FOOT.gravel, pw, px, pierBot - FOOT.gravel / 2, pz);
                _box(T, g, new T.MeshStandardMaterial({ color: FND_LAYERS.geo.colour, roughness: 1.0 }),
                     pw, FND_LAYERS.geo.t, pw, px, pierBot - FOOT.gravel - FND_LAYERS.geo.t / 2, pz);
              }
              tally.concrete += pw * pw * depth;
              tally.pierH = (tally.pierH || 0) + depth;   /* for its four bars */
            }
            /* the four vertical bars of the pier, 50 mm of cover, drawn */
            (function () {
              var mBarP = new T.MeshStandardMaterial({ color: isNewB() ? MAGENTA : 0x6B4F3A, roughness: 0.6, metalness: 0.5 });
              var cov = 0.05, dB = 0.014, q9;
              _asmCode(ctx, 'FCO00003', 'top');
              for (q9 = 0; q9 < 4; q9++) {
                var ox9 = ((q9 % 2) ? 1 : -1) * (pw / 2 - cov), oz9 = ((q9 < 2) ? 1 : -1) * (pw / 2 - cov);
                _box(T, g, mBarP, dB, depth - 2 * cov, dB, px + ox9, headY - depth / 2, pz + oz9);
              }
              _asmCode(ctx, 'grd.concrete', 'top');
            })();
            tally.gravel += pw * pw * FOOT.gravel;
            tally.geo += pw * pw;
            tally.excav += (pw + 0.20) * (pw + 0.20) * (depth + FOOT.gravel);
          }
          saddle(px, pz, pang);
        }
      }

      if (H.setTally) H.setTally(tally);
      H.add('grd.excavation', tally.excav);
      H.add(kind === 'slab' ? 'grd.gravelSlab' : 'grd.gravel', tally.gravel);
      /* THE WAY IN OFF THE GROUND. The floor stands 350 mm (screws, pads,
         piers) to 650 mm (a crawl space) above grade, so every exterior door
         needs steps -- a flight at each, risers no more than 190 and treads
         280, the whole flight one catalogue piece (SES28011). Drawn as its
         steps so the model shows how you get in (Marek). */
      try {
        var SEGs = ctx.seg || [], sq7, oq7, flights = 0;
        var riseTop = deckTop, mStep = new T.MeshStandardMaterial({ color: isNewB() ? MAGENTA : 0x9A9A96, roughness: 0.95 });
        /* TAURUS / GARDEN BAR: no steps at all, his project has none (taurus-v5,
           Marek: schody tam nedelej) -- H.steps() false */
        var stepsOn = H.steps ? !!H.steps() : true;
        if (riseTop > 0.20 && stepsOn) {
          for (sq7 = 0; sq7 < SEGs.length; sq7++) {
            var sg7 = SEGs[sq7]; if (!sg7 || !sg7.openings) continue;
            var ux7 = (sg7.x1 - sg7.x0) / sg7.len, uz7 = (sg7.z1 - sg7.z0) / sg7.len;
            for (oq7 = 0; oq7 < sg7.openings.length; oq7++) {
              var op7 = sg7.openings[oq7];
              if ((op7.sill || 0) > 0.05) continue;   /* a window, not a door */
              /* TAURUS: no flight up to a slatted porch side (taurus-v2 / v4) */
              if (H.skipStep && H.skipStep(op7, sg7)) continue;
              var dx7 = sg7.x0 + ux7 * op7.at, dz7 = sg7.z0 + uz7 * op7.at;
              var nx7 = -uz7, nz7 = ux7;              /* outward from the wall */
              var B7 = _buildRect(), cx7 = (B7.x0 + B7.x1) / 2, cz7 = (B7.z0 + B7.z1) / 2;
              if ((dx7 - cx7) * nx7 + (dz7 - cz7) * nz7 < 0) { nx7 = -nx7; nz7 = -nz7; }
              /* NO STEPS WHERE THE TERRACE IS.

                 The deck is laid a step under the floor, so a door onto it --
                 a French window or an HS portal above all -- is a threshold,
                 not a drop. A flight here would stand ON the deck and climb
                 to a level it already reaches. The question is the core's, so
                 every product asks it the same way (Marek). */
              /* and a product with its own terraces (SHED) asks its own probe
                 when the core's answers nothing */
              var tA7 = null;
              try { tA7 = API.terraceAt(dx7 + nx7 * 0.60, dz7 + nz7 * 0.60); } catch (eT7) {}
              var tSide7 = tA7 ? tA7.side : (H.terraceSideAt ? H.terraceSideAt(dx7 + nx7 * 0.60, dz7 + nz7 * 0.60) : null);
              if (tSide7) {
                ctx.log('base     entrance steps: none on ' + tSide7 +
                        ' -- the terrace is there and the deck is one step below the floor');
                continue;
              }
              var nStep = Math.max(1, Math.ceil(riseTop / 0.190)), rise7 = riseTop / nStep, tread7 = 0.280;
              var wStep = Math.max(0.90, (op7.w || 0.9) + 0.20), q7;
              _asmCode(ctx, 'SES28011', 'top');
              for (q7 = 0; q7 < nStep; q7++) {
                var outAt = API.facadeThk() + 0.02 + (nStep - 1 - q7) * tread7 + tread7 / 2;
                var yTop7 = rise7 * (q7 + 1), cxS = dx7 + nx7 * outAt, czS = dz7 + nz7 * outAt;
                if (Math.abs(ux7) > Math.abs(uz7)) _box(T, g, mStep, wStep, yTop7, tread7, cxS, yTop7 / 2, czS);
                else _box(T, g, mStep, tread7, yTop7, wStep, cxS, yTop7 / 2, czS);
              }
              H.add('SES28011', 1);
              flights++;
            }
          }
        }
        ctx.log('base     entrance steps: ' + (flights ? (flights + ' flight(s) SES28011, floor ' + Math.round(riseTop * 1000) + ' above grade') : (!stepsOn ? ('none -- ' + (H.stepsNone || 'this product has no steps')) : (riseTop > 0.20 ? 'none -- no exterior door' : 'none -- the floor is within a step of the ground'))));
        if (!stepsOn && H.stepsTag) ctx.log(H.stepsTag + '  no entrance steps -- floor ' + Math.round(riseTop * 1000) + ' above grade, as his project');
      } catch (eST) { ctx.log('base     steps THREW ' + (eST && eST.message)); }
      H.add(kind === 'slab' ? 'grd.concreteSlab' : 'grd.concrete', tally.concrete);
      /* reinforcement: 4 bars the length of every strip, 4 the height of every pier */
      if (kind === 'strips' && tally.stripLen > 0) H.add('FCO00003', tally.stripLen * 4);
      if (kind === 'piers' && tally.pierH > 0) H.add('FCO00003', tally.pierH * 4);
      /* the concrete is poured into formwork: one lump sum for the strips or the piers */
      if ((kind === 'strips' && tally.stripLen > 0) || (kind === 'piers' && tally.pierH > 0)) H.add('FCO00007', 1);
      /* 6. the frame on pads and piers is fixed to them: a U post base on every one */
      if (kind === 'pads' && tally.pads > 0) H.add('FCO00004', tally.pads);
      if (kind === 'piers' && tally.points > 0) H.add('FCO00004', tally.points);
      H.add('grd.padBlock', tally.pads);
      H.add('steel.screwPile', tally.screws);
      H.add('steel.saddle', tally.saddles);

      /* AND WHAT BOLTS THE SADDLE TO THE TIMBER, WHICH WAS NOT BILLED.

         The saddle was counted and the fixing through its cheeks was not, so the
         rim was sitting in a bracket that nothing closed. Four a saddle, two per
         cheek, which is what stops the frame rotating on its support.

         Stainless, and not for neatness: this is the lowest fixing in the
         building, splashed off the ground and inside a bracket that holds water
         in its corners. FAS45070 is the stainless the parapet coping already
         uses. FAS45070 is a 4.5x70 and reaches past the timber it holds;
         FAS55050 is the 5.5x50, the connector length, in the same A2. */
      if (tally.saddles) {
        H.add('FAS55050', tally.saddles * 4);
        ctx.log('FIX  base ' + tally.saddles + ' saddle(s) -> ' + (tally.saddles * 4) +
                ' x FAS55050, 2 per cheek -- A2 stainless because it is the ' +
                'lowest fixing on the building and the bracket holds water');
      }
      /* no geotextile sheet and no perimeter drain under point footings; both
         rows stay in the catalogue for the strip and slab kinds */
      if (tally.geo) H.add('grd.geotextile', tally.geo);
      if (tally.drain) H.add('grd.drain', tally.drain);
      ctx.log('base     ' + (_footFull()
              ? 'FULL DEPTH -- the BASE step is open, so everything under the ground is drawn'
              : 'above grade only -- open the BASE step to see how deep it goes'));
      if (kind === 'screws') {
        ctx.log('base     screws, ' + Math.round(FOOT.screwLen * 1000) + ' mm piles -- ' +
                'a pile holds by friction down its whole length, so the frost line ' +
                'does not set its depth the way it sets a footing\'s');
      } else {
        ctx.log('base     ' + kind + ', ' + Math.round(depth * 1000) + ' mm deep' +
                ' (frost ' + Math.round((API.STATE.climateZone || 0) * 1000) + ' mm from the address)');
      }
      if (tally.points) {
        ctx.log('base     ' + tally.points + ' supports at no more than ' +
                API.fmtLen(FOOT.spacing) + ' apart, round the outline and down the centre line');
      }
      if (tally.saddles) {
        ctx.log('base     ' + tally.saddles + ' saddles, top of support at ' + headY.toFixed(3) +
                ' -- one plate under the timber, which starts at ' + timberY.toFixed(3) +
                '. The rim drops between the cheeks.');
      }
      if (tally.concrete) ctx.log('base     concrete ' + tally.concrete.toFixed(2) + ' m3');
      if (tally.gravel)   ctx.log('base     gravel ' + tally.gravel.toFixed(2) + ' m3, excavation ' + tally.excav.toFixed(2) + ' m3');
      if (tally.drain)    ctx.log('base     perimeter drain ' + tally.drain.toFixed(1) +
                                  ' m in the stone at the bottom of the dig -- a hole full of ' +
                                  'water in a frost zone lifts the footing it is under');
      if (kind === 'screws' && _footFull()) {
        _why(ctx, 'base     screw flights drawn -- the shape is the argument for this ' +
                'foundation: it holds by being wound in, with no concrete and no spoil');
      }
      if (kind === 'slab') _why(ctx, 'base     THE SLAB IS THE FLOOR -- the timber floor is suppressed');
      if (isNewB()) _why(ctx, 'base     drawn MAGENTA -- that is what changed this version');
    }

    /* =========================================================================
       THE EAVE POSTS MODULE (core-v762, the second shared module of the house
       line -- status/modules.md). The posts under a deep overhang, their
       footings, knee braces, the eave beam and the gable's run-out plate,
       written once: the ADU's text, with the shed family's framing behind
       H.shed and the THOW porch behind H.porch.

         API.eavePosts(ctx, {
           api:       the product's API (STATE, fmtLen, facadeThk, wallTop,
                      wallThickness, plateZone)
           EAVE_POST, WEB, CHORD, PLATE: the product's constants
           over, rect: _roofOver, _buildRect
           screwPile: the product's _screwPile (a footing on screws)
           plateEndReach, rafterSoffitAt, plateSoffitAt: the product's own
           timber:    the posts' colour (magenta while under review)
           shed:      true -- SHED, TAURUS, GARDEN BAR: cross braces only where
                      a tie runs across, posts under a long gable run-out, ties
                      across at the eave rows' stations, EAVE_POST.span
           porch:     { frame, PORCH, ridgeBeam } -- THOW, the porch's posts
           setPts:    function (array) -- the posts published for the gutters
           add:       function (code, qty) -- the product's own bill line
         })

       Every product's bill and drawing are the same as with its own copy
       (headless, every roof stage, deep overhangs, mono, flat).
       ========================================================================= */
    function eavePosts(ctx, H) {
      var T = ctx.THREE, g = ctx.group;
      /* the product's own pieces, under the names the text has always used */
      var API = H.api, EAVE_POST = H.EAVE_POST, WEB = H.WEB, CHORD = H.CHORD, PLATE = H.PLATE;
      var _roofOver = H.over, _buildRect = H.rect, _screwPile = H.screwPile, quad = _rsQuad;
      var _plateEndReach = H.plateEndReach, _rafterSoffitAt = H.rafterSoffitAt, _plateSoffitAt = H.plateSoffitAt;
      /* the shed family's framing (SHED, TAURUS, GARDEN BAR): an open box that
         may reach far on opposite sides -- cross braces only where a tie runs
         across, posts under a long gable run-out, ties across at the eave rows'
         intermediate stations, EAVE_POST.span between posts */
      var SHED = !!H.shed;
      var R = API.STATE.roof || {};
      var tally = { posts: 0, len: 0, hdr: 0, sockets: 0, screws: 0 };
      var _postAt = [];   /* where each one really stands, for the PIPE check */
      var _postPts = [];  /* and for the downspout, which must not guess */
      if (H.setPts) H.setPts(_postPts);
      if (R.type === 'flat') {
        _why(ctx, 'posts    flat roof with a parapet -- nothing projects, so nothing to carry');
        return tally;
      }

      var O = _roofOver();
      var B = _buildRect();
      /* EAVE_POST.inset is gone from here. It is a setback for standing a post
         back from an edge, and every place in this function wanted the opposite
         question -- where the post ENDS -- which is half its own width. Using the
         setback for that left 74 mm of plate hanging past every post. */
      var TR = EAVE_POST.trigger, PW = EAVE_POST.w;
      var deep = { x0: O.x0 > TR, x1: O.x1 > TR, z0: O.z0 > TR, z1: O.z1 > TR };
      /* THE PORCH'S END ALWAYS HAS ITS POSTS, ON THE TRAILER (thow-v19) --
         H.porch: frame() -> { side, zEnd, footY } or null, PORCH, ridgeBeam() */
      var PZ = null, PORCH = H.porch ? H.porch.PORCH : null;
      if (H.porch) { try { PZ = H.porch.frame(); } catch (ePZ) { PZ = null; } }
      if (PZ) deep[PZ.side > 0 ? 'z1' : 'z0'] = true;
      var _porchFoot = null;
      if (!deep.x0 && !deep.x1 && !deep.z0 && !deep.z1) {
        ctx.log('posts    deepest reach ' + API.fmtLen(Math.max(O.x0, O.x1, O.z0, O.z1)) +
                ' -- carries itself; a post appears past ' + API.fmtLen(TR));
        return tally;
      }

      var timber = new T.MeshStandardMaterial({
        color: H.timber, roughness: 0.85, metalness: 0.02 });
      var galv = new T.MeshStandardMaterial({ color: 0x9A9EA3, roughness: 0.5, metalness: 0.55 });
      var steel = new T.MeshStandardMaterial({ color: 0x8C9196, roughness: 0.45, metalness: 0.7 });
      var groundY = H.deckFoot ? H.deckFoot.y : 0, plateTop = API.wallTop();


      /* THE SAME FOOTING AS THE BUILDING, DRAWN BY THE SAME FUNCTION. */
      /* THE FOOTING OF A POST (adu-v1363). What the building stands on, the post
         stands on too -- a precast pad on pads, a cast pier on piers and strips,
         a ground screw on screws -- and on top of it, always, the black steel
         post base FAT00017, the same as under the pergola. On a screw it sits on
         the screw's head plate: no U holder under a post (Marek). The post
         starts 30 mm up in the base's sleeve, clear of the wet. */
      var PIER = { w: 0.30, d: 0.90, proud: 0.10 };
      function _footTop() {
        if(H.deckFoot)return H.deckFoot.y;
        if (_porchFoot != null) return _porchFoot;
        var F = API.STATE.foundation;
        if (F === 'pads') return groundY + 0.05;
        if (F === 'piers' || F === 'strips') return groundY + PIER.proud;
        return groundY + 0.09;
      }
      var baseMat = new T.MeshStandardMaterial({ color: 0x1D1E20, roughness: 0.55, metalness: 0.45 });
      function footing(lx, lz) {
        var F = API.STATE.foundation, top = _footTop();
        if(H.deckFoot){H.deckFoot.draw(ctx,lx,lz,PW);return;}
        /* on the porch: the post base on the decking, bolted through to the
           trailer's rim -- no footing in the ground (thow-v19) */
        if (_porchFoot != null) {
          var bpP = new T.Mesh(new T.BoxGeometry(0.150, 0.006, 0.150), baseMat);
          bpP.position.set(lx, _porchFoot + 0.003, lz); g.add(bpP);
          var slP = new T.Mesh(new T.BoxGeometry(PW + 0.006, 0.180, PW + 0.006), baseMat);
          slP.position.set(lx, _porchFoot + 0.006 + 0.090, lz); g.add(slP);
          tally.bases = (tally.bases || 0) + 1;
          tally.steel = (tally.steel || 0) + 1;
          return;
        }
        if (F === 'pads') {
          var pad = new T.Mesh(new T.BoxGeometry(0.40, 0.15, 0.40), galv);
          pad.position.set(lx, top - 0.075, lz); g.add(pad);
          tally.pads = (tally.pads || 0) + 1;
        } else if (F === 'piers' || F === 'strips') {
          var pier = new T.Mesh(new T.BoxGeometry(PIER.w, PIER.d + PIER.proud, PIER.w), galv);
          pier.position.set(lx, top - (PIER.d + PIER.proud) / 2, lz); g.add(pier);
          tally.piers = (tally.piers || 0) + 1;
        } else {
          _screwPile(T, g, steel, lx, lz, 0.086);
          tally.screws++;
        }
        var bp = new T.Mesh(new T.BoxGeometry(0.150, 0.006, 0.150), baseMat);
        bp.position.set(lx, top + 0.003, lz); g.add(bp);
        var sl = new T.Mesh(new T.BoxGeometry(PW + 0.006, 0.180, PW + 0.006), baseMat);
        sl.position.set(lx, top + 0.006 + 0.090, lz); g.add(sl);
        tally.bases = (tally.bases || 0) + 1;
      }

      /* topY is the TOP the post must reach. Under a header it stops a post width
         below it; bearing straight on a plate it reaches topY exactly, or a gap
         of one section opens under the plate. Tiny's rule, and its wording. */
      /* KNEE BRACES, ONE EACH SIDE, CUT TO THE ANGLE THEY MEET AT. */
      function brace(lx, lz, topFace, along, inward, headRise) {
        var arm = 0.6;
        /* core-v798: under a RAKED beam the head lands where the beam is, a
           rise higher (or lower) than at the post; 0 everywhere else */
        var hR = (along && headRise) ? headRise : 0;
        var t = WEB.t, dep = WEB.d;
        /* right up to the plate's underside -- the 20 mm gap was mine and it is
           the other half of the notch */
        var yTop = topFace, yBot = yTop - arm;
        if (yBot < groundY + 0.3) return;
        /* INWARD ONLY, TOWARD THE HOUSE. */
        var sideB = inward;
        {
          var aX = along ? lx : (lx + PW / 2 * sideB);
          var aZ = along ? (lz + PW / 2 * sideB) : lz;
          var bX = along ? lx : (aX + arm * sideB);
          var bZ = along ? (aZ + arm * sideB) : lz;

          /* A SOLID, NOT TWO SHEETS.

             I emitted the near face and the far face and nothing else -- no sides,
             no ends. From any angle but straight on it was a sheet of paper, and
             from inside you looked through it. That is what he is seeing as "cuts
             on all sides".

             Six faces from the same four corners: the two faces, the two long
             edges, and the two mitred ends. The ends are the whole point of the
             piece, so they are the last thing that should be missing. */
          var nx = along ? 1 : 0, nz = along ? 0 : 1;
          /* THE SECTION IS THE SAME ALL THE WAY ALONG.

             My four corners took their depth in different directions at the two
             ends -- up the post at the foot, back along the plate at the head --
             so the face between them was warped rather than flat. That is the
             twist he can see.

             The depth is measured PERPENDICULAR to the brace, the same vector at
             both ends, which is what a sawn member does. The ends are then cut
             square to the piece; the mitre against the post and the plate comes
             from the 45 degrees the whole member sits at. */
          var ux = along ? 0 : sideB, uz = along ? sideB : 0;

          /* THE HEAD IS CUT LEVEL AND RUNS UP TO THE PLATE. */
          var p1 = [aX, yBot, aZ];
          var p2 = [aX, yBot + (dep / Math.SQRT1_2), aZ];
          /* THE CUT IS A FACE, NOT A POINT. */
          var cut = dep / Math.SQRT1_2;
          var p3 = [bX - ux * cut, yTop + hR, bZ - uz * cut];
          var p4 = [bX, yTop + hR, bZ];

          function off3d(pt, q) {
            return [pt[0] + nx * t / 2 * q, pt[1], pt[2] + nz * t / 2 * q];
          }
          var nearP = [off3d(p1, -1), off3d(p2, -1), off3d(p3, -1), off3d(p4, -1)];
          var farP = [off3d(p1, 1), off3d(p2, 1), off3d(p3, 1), off3d(p4, 1)];

          var vv = [], e;
          quad(vv, nearP[0], nearP[1], nearP[2], nearP[3]);
          quad(vv, farP[3], farP[2], farP[1], farP[0]);
          for (e = 0; e < 4; e++) {
            var f = (e + 1) % 4;
            quad(vv, nearP[e], farP[e], farP[f], nearP[f]);
          }

          var bg = new T.BufferGeometry();
          bg.setAttribute('position', new T.Float32BufferAttribute(vv, 3));
          bg.computeVertexNormals();
          g.add(new T.Mesh(bg, timber));

          /* AND IT REPORTS ITSELF. Four rounds on this one member, so the copy log
             carries what a snapshot would otherwise have to be read for: where it
             runs, which way, and how long. */
          ctx.log('PLT brace ' + (along ? 'z' : 'x') + ' side' + sideB +
                  ' from ' + Math.round(aX * 1000) + ',' + Math.round(yBot * 1000) +
                  ',' + Math.round(aZ * 1000) +
                  ' to ' + Math.round(bX * 1000) + ',' + Math.round((yTop + hR) * 1000) +
                  ',' + Math.round(bZ * 1000) +
                  ' len ' + Math.round(Math.hypot(arm, arm + hR) * 1000));

          tally.hdr += Math.hypot(arm, arm + hR);
        }
      }

      /* THE BEAM UNDER AN EXTENDED ROOF.

         Both sides were drawing a double wall plate and nothing else. A plate is
         what a rafter bears ON; what carries the overhang back to the posts is a
         BEAM, and it was missing on every roof type -- which is why he sees the
         posts holding up a pair of plates on the eave sides and, on a mono, that
         same pair standing in for the beam that should be there.

         One rule for all three roofs: the beam spans post to post, sits under the
         plates rather than beside them, and takes the section EAVE_POST already
         names for this work. Its ends stop on the post faces (Marek). */
      function beam(x0b, z0b, x1b, z1b, topFace) {
        var dxB = x1b - x0b, dzB = z1b - z0b;
        var lenB = Math.hypot(dxB, dzB);
        if (!(lenB > 0.4)) return 0;
        var wB = CHORD.t, dB = CHORD.d;
        var along = Math.abs(dzB) >= Math.abs(dxB);
        var bm = new T.Mesh(
          along ? new T.BoxGeometry(dB, dB, lenB + PW)
                : new T.BoxGeometry(lenB + PW, dB, dB), timber);
        bm.position.set((x0b + x1b) / 2, topFace - dB / 2, (z0b + z1b) / 2);
        g.add(bm);
        tally.hdr += lenB + PW;
        ctx.log('BEAM ' + (along ? 'z' : 'x') + ' ' +
                Math.round(x0b * 1000) + ',' + Math.round(z0b * 1000) + ' -> ' +
                Math.round(x1b * 1000) + ',' + Math.round(z1b * 1000) +
                '  len ' + Math.round((lenB + PW) * 1000) +
                '  top ' + Math.round(topFace * 1000));
        return lenB;
      }

      function post(lx, lz, topY, noHeader) {
        var topFace = noHeader ? topY : (topY - PW);
        var yFoot = _footTop() + 0.036;
        var h = topFace - yFoot;
        if (h < 0.2) return;
        var pm = new T.Mesh(new T.BoxGeometry(PW, h, PW), timber);
        pm.position.set(lx, yFoot + h / 2, lz);
        g.add(pm);
        tally.posts++;
        tally.len += h;
        _postAt.push(Math.round(lx * 1000) + ',' + Math.round(lz * 1000) +
                     (noHeader ? 'p' : 'h'));
        _postPts.push({ x: lx, z: lz, what: noHeader ? 'post-plate' : 'post' });
        footing(lx, lz);
      }

      /* ------------------------------------------------------------------
         CASE A -- A DEEP EAVE. The roof reaches out sideways, so a header runs
         ALONG the eave on a pair of posts and the projecting roof lands on it.
         ------------------------------------------------------------------ */
      function eaveSide(sgx, ovv) {
        /* THE EAVE IS NOT THE RAKE. Here the truss TAILS reach the roof edge
           -- overhang plus facade -- and the header on its posts carries them
           at their ends, so this reach keeps the facade in it. The rake reach
           (_plateEndReach) lost it, because the last truss there sits inside
           the facade and the plates stop on that truss. */
        var tailReach = ovv + API.facadeThk() - PLATE.t;
        var xEdge = (sgx < 0 ? B.x0 - tailReach : B.x1 + tailReach);
        /* same here: the post ends where the plate ends */
        var xPost = xEdge - sgx * (PW / 2);
        /* THE HEADER RUNS AS FAR AS THE PLATE DOES. It stopped an inset short at
           each end while the plate carried on past it, so the two members that
           are supposed to be the same line ended in different places and the
           corner of the roof had nothing under it. */
        var zNeg = B.z0 - _plateEndReach(O.z0), zPos = B.z1 + _plateEndReach(O.z1);
        /* UNDER THE RAFTERS, NOT UNDER THE WALL HEAD.

           A header out at a deep eave carries the ROOF, and the roof out there is
           a rafter depth below the plane -- so the header's top belongs at the
           rafter soffit and the post reaches that, not the wall plate. Set at the
           plate the pair sat a rafter depth low with daylight above them, which
           on a shallow mono is most of what you see.

           The plane falls, so the soffit at the post line is worked out from the
           reach and the pitch rather than assumed level. */
        /* the roof's own underside at the post line -- read from the plane, so a
           mono answers with its low edge on one side and its high edge on the
           other, and a gable answers the same on both */
        /* and the two ends of this run: same question, same answer -- their faces
           land on the plate's end, so they step in by half a post */
        var pNeg = zNeg + PW / 2, pPos = zPos - PW / 2;
        var tA = _rafterSoffitAt(xPost, pNeg), tB = _rafterSoffitAt(xPost, pPos);
        var topY = Math.min(tA == null ? plateTop : tA, tB == null ? plateTop : tB);
        /* A RAKED RUN (core-v798, RUSSWORX SHED thread, Marek 2026-11-02: bugs
           u podpurnych sloupu). Where the roof does not stay level along this
           run -- the rake side of a mono that falls along z, a gable with its
           ridge turned -- the beam was laid LEVEL at the lower end's soffit: the
           post at the high end stopped there with the roof a whole rise above
           it, the posts between ran on up past the beam to their own soffit and
           braced into air. So the stations are asked first: when their soffits
           differ, every post reaches its own, the beam follows the roof from
           post to post and the braces along it land on it. A level run -- every
           eave of every product until now -- is built exactly as before. */
        var _stZ = [pNeg], _stQ, _stN = SHED ? Math.ceil((pPos - pNeg) / EAVE_POST.span)
                                          : (Math.max(0, Math.ceil((pPos - pNeg) / 5.0) - 1) + 1);
        for (_stQ = 1; _stQ < _stN; _stQ++) _stZ.push(pNeg + (pPos - pNeg) * (_stQ / _stN));
        _stZ.push(pPos);
        var _stY = _stZ.map(function (zz, qq) {
          var tS = (qq === 0) ? tA : (qq === _stZ.length - 1) ? tB : _rafterSoffitAt(xPost, zz);
          return (tS == null) ? topY : tS;
        });
        var RAKED = _stY.some(function (yy) { return Math.abs(yy - topY) > 0.005; });
        function yRun(zz) {
          var qq;
          if (zz <= _stZ[0]) return _stY[0] + (_stZ.length > 1 ? (_stY[1] - _stY[0]) * (zz - _stZ[0]) / (_stZ[1] - _stZ[0]) : 0);
          for (qq = 1; qq < _stZ.length; qq++) {
            if (zz <= _stZ[qq]) return _stY[qq - 1] + (_stY[qq] - _stY[qq - 1]) * (zz - _stZ[qq - 1]) / (_stZ[qq] - _stZ[qq - 1]);
          }
          var nL = _stZ.length;
          return _stY[nL - 1] + (_stY[nL - 1] - _stY[nL - 2]) * (zz - _stZ[nL - 1]) / (_stZ[nL - 1] - _stZ[nL - 2]);
        }
        /* how much higher the beam is where an along brace from the post at z
           meets it, going the way 'way' says */
        function riseAt(zz, way) { return RAKED ? (yRun(zz + way * (PW / 2 + 0.6)) - yRun(zz)) : 0; }
        var topA = RAKED ? _stY[0] : topY, topB = RAKED ? _stY[_stY.length - 1] : topY;
        post(xPost, pNeg, topA);
        post(xPost, pPos, topB);
        /* the two ends of this run brace toward each other, i.e. inward */
        brace(xPost, pNeg, topA - PW, true, 1, riseAt(pNeg, 1));
        brace(xPost, pPos, topB - PW, true, -1, riseAt(pPos, -1));
        if (SHED) {
          /* A CORNER POST BRACES ON EACH SIDE -- 90 DEGREES -- BUT ONLY WHERE
             THERE IS SOMETHING TO BRACE AGAINST (Marek).

             The brace ACROSS the eave beam needs a member running that way at the
             same corner, and that member is the gable tie, which exists only when
             the gable end is deep. With both EAVE sides pulled out and the gables
             shallow -- which is exactly what the axis rule produces -- there is
             nothing across, and the brace stood there pointing at the far side of
             the building with its top against air. Along the beam there is always
             something, so that one is unconditional. */
          if (deep.z0) brace(xPost, pNeg, topA - PW, false, -sgx);
          if (deep.z1) brace(xPost, pPos, topB - PW, false, -sgx);
          if (!deep.z0 && !deep.z1) {
            ctx.log('POST eave x' + (sgx < 0 ? '0' : '1') +
                    ': no cross brace at either end -- the gable ends are shallow, so ' +
                    'there is no tie across for one to bear on');
          }
          /* AND THE ONES IN BETWEEN. This run is the length of the building plus
             both gable overhangs, so at full reach it is well over 10 m on two
             posts. Evenly divided, never more than EAVE_POST.span apart -- even
             spacing rather than 3-3-3-rest, because a short last bay beside three
             long ones reads as a mistake. */
          var runE = pPos - pNeg, nE = Math.ceil(runE / EAVE_POST.span), qE;
          for (qE = 1; qE < nE; qE++) {
            var zMid = pNeg + runE * (qE / nE);
            var tM = _rafterSoffitAt(xPost, zMid);
            post(xPost, zMid, tM == null ? topY : tM);
            /* A POST UNDER A THROUGH BEAM BRACES BOTH WAYS ALONG IT (Marek): the
               beam carries on past this post in both directions, so a single
               brace props one half and leaves the other. The third goes across,
               back toward the house. */
            var yMb = (tM == null ? topY : tM) - PW;
            brace(xPost, zMid, yMb, true, 1, riseAt(zMid, 1));
            brace(xPost, zMid, yMb, true, -1, riseAt(zMid, -1));
            /* and across only where something runs across -- same reason as at
               the corners: a brace with air at its top is a stick. When BOTH eave
               rows are up, the tie that runs across is built after them, by
               eaveCrossTies, and it braces its own stations. */
            if (deep.z0 || deep.z1) brace(xPost, zMid, yMb, false, -sgx);
          }
          if (nE > 1) ctx.log('POST eave x' + (sgx < 0 ? '0' : '1') + ': run ' +
                  Math.round(runE * 1000) + ' mm -> ' + (nE + 1) + ' post(s) at ' +
                  Math.round(runE / nE * 1000) + ' mm, max span ' +
                  Math.round(EAVE_POST.span * 1000));
        } else {
          /* AND A POST EVERY 5 M ALONG THE RUN. Two posts at the ends of a long
             wall leave the header spanning the whole building -- on a long house
             that is eight metres of beam carrying the roof's edge on nothing in
             between (Marek). An intermediate post is added so no bay is over 5 m,
             evenly spaced, and it braces BOTH ways, because the header it holds
             runs on past it in both directions. */
          var runP = pPos - pNeg, POST_MAX = 5.0;
          var nMid = Math.max(0, Math.ceil(runP / POST_MAX) - 1), mq;
          for (mq = 1; mq <= nMid; mq++) {
            var zMid = pNeg + runP * mq / (nMid + 1);
            var tM = _rafterSoffitAt(xPost, zMid);
            post(xPost, zMid, tM == null ? topY : tM);
            brace(xPost, zMid, (tM == null ? topY : tM) - PW, true, 1, riseAt(zMid, 1));
            brace(xPost, zMid, (tM == null ? topY : tM) - PW, true, -1, riseAt(zMid, -1));
          }
          if (nMid) ctx.log('posts    ' + nMid + ' intermediate post(s) on the ' + (sgx < 0 ? 'x0' : 'x1') +
                            ' eave: the run is ' + Math.round(runP * 1000) + ' mm, no bay over ' + Math.round(POST_MAX * 1000) + ' mm, each braced both ways');
        }
        /* the two are TIED AT THE TOP by a header running along the eave, which
           is what actually carries the projecting roof -- a pair of free posts
           carries two points and nothing between them */
        /* A DOUBLE PLATE, LAPPED AT THE CORNER. */

        /* the lower course stops at the face; the upper runs past it by a plate
           width at each end, so the two overlap where they turn the corner */
        /* the eave side takes the opposite half of the bond: its LOWER course
           butts and its UPPER runs through, so the two directions interlock */
        /* MEASURED FROM THE POSTS, NOT FROM THE PLATE'S REACH. */

        /* the same pair on this side: on the post's line, one course either
           side of the plate soffit */
        var xFar = xPost;


        /* THE BEAM GOES UNDER THE RAFTER, AND THE PLATES GO AWAY.

           A rafter bears on ONE thing. Where the roof is carried out to posts
           that thing is the beam, so a double plate on top of it stacks a second
           bearing over the first -- the pair he can see above the beam on a mono.

           The beam runs right up under the rafter, at the height the plates used
           to occupy. On the gable end the plates stay: there they ARE the
           bearing and no beam replaces them (Marek). */
        if (!RAKED) beam(xFar, pNeg, xFar, pPos, topY);
        else {
          /* core-v798: the beam under a raked run, post to post along the
             soffit, its top on the rafters' underside; the two ends run on half
             a post past the end posts as the level beam does */
          var qB, segL = 0, nB = _stZ.length;
          for (qB = 0; qB < nB - 1; qB++) {
            var za = _stZ[qB] - (qB === 0 ? PW / 2 : 0), zb = _stZ[qB + 1] + (qB === nB - 2 ? PW / 2 : 0);
            var ya = yRun(za), yb = yRun(zb), dzS = zb - za, dyS = yb - ya, lS = Math.hypot(dzS, dyS);
            if (!(lS > 0.05)) continue;
            var dBr = CHORD.d, cosS = dzS / lS;
            var bmR = new T.Mesh(new T.BoxGeometry(dBr, dBr, lS), timber);
            bmR.rotation.x = -Math.atan2(dyS, dzS);
            bmR.position.set(xFar, (ya + yb) / 2 - dBr / (2 * Math.max(0.3, cosS)), (za + zb) / 2);
            g.add(bmR);
            segL += lS;
          }
          tally.hdr += segL;
          ctx.log('BEAM z RAKED ' + Math.round(xFar * 1000) + ': ' + (nB - 1) + ' piece(s) post to post along the roof, ' +
                  _stZ.map(function (zz, qq) { return Math.round(zz * 1000) + '@' + Math.round(_stY[qq] * 1000); }).join(' ') +
                  ', ' + Math.round(segL * 1000) + ' mm -- every post up to its own soffit, the braces along it landing on it (core-v798)');
        }

        /* the two courses are gone: the beam above is the bearing now */

        /* the tally and the old plate log went with the plates -- both counted
           and reported members this side no longer has */
      }

      /* ------------------------------------------------------------------
         CASE B -- A DEEP GABLE END. Here it is the WALL PLATE that runs out to
         meet the roof, not an eave. So the post stands UNDER THE PLATE LINE and
         bears straight on it -- no header, and no second profile invented at the
         end of the rafters.
         ------------------------------------------------------------------ */
      function gableEnd(sgz, ovv) {
        /* THE POST STANDS WHERE THE PLATE ENDS. */
        var zEdge = (sgz < 0 ? B.z0 - _plateEndReach(ovv) : B.z1 + _plateEndReach(ovv));
        /* INSET BY THE POST'S OWN HALF WIDTH, NOT BY EAVE_POST.inset. */
        var zPost = zEdge - sgz * (PW / 2);
        /* the porch's posts stand at the trailer's front, on its deck; the
           plates run on past them to the roof's edge (thow-v19) */
        var onPorch = (!!PZ && sgz === PZ.side);
        if (onPorch) { zPost = PZ.zEnd - sgz * PW / 2; _porchFoot = PZ.footY; }
        var wt2 = API.wallThickness();
        var xa = B.x0 + wt2 / 2, xb = B.x1 - wt2 / 2;   /* the plate axis, both walls */
        /* WHERE A DEEP EAVE ALREADY STANDS IN THAT CORNER, DO NOT ADD A SECOND.
           The eave post is out at the edge and this one would be beside it. */
        /* AND THESE ARE SHORT ON A MONO, for the same reason. They bear on the
           wall plate, and on a mono the plate is RAKED -- it climbs with the
           wall. plateTop is wallTop(), which is the LOW head, so the post at the
           high wall stopped a whole rise short of the timber it is holding.
           The plane answers per point here too. */
        var ya = _plateSoffitAt(xa, zPost), yb = _plateSoffitAt(xb, zPost);
        if (!deep.x0) post(xa, zPost, ya == null ? plateTop : ya, true);
        if (!deep.x1) post(xb, zPost, yb == null ? plateTop : yb, true);
        if (!SHED) {
          /* TWO PER POST, IN ONE DIRECTION -- AND THE ONE ACROSS HOLDS THE
             PROFILE ACROSS. On a mono the two plates are at different heights and
             the double profile between the posts sits at the LOWER of them; the
             sideways brace from the high post went to its own plate, a whole rise
             above the profile it is there to carry, and the profile lay under it
             held by nothing (Marek). Both sideways braces head for the profile's
             underside. The braces along the plates stay at their own plate. */
          /* WHERE THE POST AT THIS CORNER REALLY STANDS. With the eave side
             extended its post is out at the roof's edge, not on the wall axis --
             the brace was drawn at the axis and ended in mid air against the
             panels' front face (Marek). */
          function _cornerX(sgx, take, axis) {
            if (!take) return axis;
            var ovvC = (sgx < 0 ? O.x0 : O.x1);
            var tailC = ovvC + API.facadeThk() - PLATE.t;
            var xEdgeC = (sgx < 0 ? B.x0 - tailC : B.x1 + tailC);
            return xEdgeC - sgx * (PW / 2);
          }
          var xAp = _cornerX(-1, deep.x0, xa), xBp = _cornerX(1, deep.x1, xb);
          var yG0 = Math.min(ya == null ? plateTop : ya, yb == null ? plateTop : yb);
          /* AND FROM BOTH ENDS OF IT, whichever side owns the corner post. The
             !deep guard belongs to the POST -- when the eave side is extended it
             plants the post at that corner instead. The brace under the profile
             was guarded with it, so on a mono with the eave side out the profile
             was braced from the low end only and the high end sat on nothing
             (Marek). The post is there either way; the brace goes to it. */
          brace(xAp, zPost, yG0 - 0.06, false, 1);
          brace(xBp, zPost, yG0 - 0.06, false, -1);
          /* AND ON THE WALL'S OWN PLATE: from the same post back toward the
             house, which is the pair he asked for and the direction the roof
             actually pushes. */
          if (!deep.x0) brace(xa, zPost, (ya == null ? plateTop : ya) - 0.06, true, -sgz);
          if (!deep.x1) brace(xb, zPost, (yb == null ? plateTop : yb) - 0.06, true, -sgz);

          /* THE PLATE IS ALREADY OUT THERE. adu-stud-wall runs BOTH of its plates
             past the end wall by this same reach -- I had been drawing a third
             member here to meet the post, which is a new profile pretending to be
             a plate. The post reaches the real one. */
          /* AND A DOUBLE PLATE BETWEEN THE TWO POSTS -- ALWAYS. The test below
             belongs to the POSTS (so one corner does not get a post from this end
             and another from the eave side); the plate was wrapped in it too and
             vanished the moment the eave side grew posts, with nothing to replace
             it -- the eave header runs the other way (shed-v12, Marek). */
          if (true) {
            var ptG = CHORD.t, pdG = CHORD.d;
            var yG = Math.min(ya == null ? plateTop : ya, yb == null ? plateTop : yb);
            /* and when a corner is taken by the eave side, its post stands out at
               the roof's edge -- the plate has to reach THAT, not the wall axis */
            var xA = xAp, xB = xBp;
            var lenG = xB - xA;

            /* AT THE PLATE'S OWN HEIGHT, NOT A COURSE BELOW IT. */
            /* THE OFFSET WAS 3.8 mm, WHICH IS NOTHING.

               PW/2 - pd/2 is 76.2 - 80, so "aligned to the far face" moved the
               member by less than the width of a pencil line and it stayed centred
               on the post. To sit ON the far face the shift is half the post plus
               half the plate. */
            /* BACK TO THE POST'S OWN LINE, AND ONE COURSE UP. */
            var zFar = zPost;

            /* A GAP SHOWS THE JOINT, AND THE ENDS STOP ON THE POSTS. */
            /* THE TWO COURSES IN TWO COLOURS, TO END THE GUESSING. */
            var loMat = timber;   /* settled, so it is timber like its partner */
            var gapG = 0.004;
            var runOut = PW / 2;

            /* BOTH COURSES RUN TO THE POST FACES. */
            var fullG = lenG + runOut * 2;

            /* NO BEAM HERE. TWO PROFILES, AND THEY ARE THE PLATE.

               The beam belongs to the EAVE side, where the roof cantilevers out
               past the wall and something has to carry it back to the posts. It
               was added there and then extended along to this end as well, and
               this end does not work that way: the wall's own plate already runs
               out to the post, the post bears on it, and what spans between the
               two posts is that plate carried across -- two courses, tied into the
               plates of the walls square to them.

               So a third member under them is a beam holding up a beam. It came in
               with the eave fix and it is taken back out of here; eaveSide keeps
               its own call, which is the one that was actually missing (Marek). */

            var loG = new T.Mesh(new T.BoxGeometry(fullG, ptG - gapG, pdG), loMat);
            loG.position.set((xA + xB) / 2, yG + ptG / 2, zFar);
            g.add(loG);

            var upG = new T.Mesh(new T.BoxGeometry(fullG, ptG - gapG, pdG), timber);
            upG.position.set((xA + xB) / 2, yG - ptG / 2, zFar);
            g.add(upG);

            tally.hdr += fullG * 2;
            ctx.log('BUG  gable end: 2 profiles between the posts, no beam under them -- ' +
                    'the beam is the eave side\'s piece and was reaching in here');
            ctx.log('PLT gable plate ' + Math.round(((xa + xb) / 2 - fullG / 2) * 1000) +
                    '..' + Math.round(((xa + xb) / 2 + fullG / 2) * 1000) +
                    '  post faces ' + Math.round((xa - PW / 2) * 1000) +
                    '..' + Math.round((xb + PW / 2) * 1000) +
                    '  posts ' + Math.round(xa * 1000) + ',' + Math.round(xb * 1000));
            /* the number that has been in dispute for five rounds: where the post
               stands along the run, and where the wall's plate actually ends */
            ctx.log('PLT gable post z ' + Math.round(zPost * 1000) +
                    '  face ' + Math.round((zPost + sgz * PW / 2) * 1000) +
                    '  plate ends ' + Math.round((sgz < 0 ? B.z0 - _plateEndReach(ovv)
                                                         : B.z1 + _plateEndReach(ovv)) * 1000) +
                    '  overhang ' + Math.round(ovv * 1000));

            /* WHERE THE TWO COURSES ACTUALLY ARE. */
            ctx.log('PLT plate lower ' + Math.round((yG - ptG) * 1000) + '..' +
                    Math.round(yG * 1000) +
                    '  upper ' + Math.round(yG * 1000) + '..' +
                    Math.round((yG + ptG) * 1000) +
                    '  post top ' + Math.round(yG * 1000) +
                    '  z ' + Math.round(zFar * 1000) + ' (post ' + Math.round(zPost * 1000) + ')');
          }
        } else {
          /* TWO PER POST, IN ONE DIRECTION -- AND THE ONE ACROSS HOLDS THE
             PROFILE ACROSS. On a mono the two plates are at different heights and
             the double profile between the posts sits at the LOWER of them; the
             sideways brace from the high post went to its own plate, a whole rise
             above the profile it is there to carry, and the profile lay under it
             held by nothing (Marek). Both sideways braces head for the profile's
             underside. The braces along the plates stay at their own plate. */
          var yG0 = Math.min(ya == null ? plateTop : ya, yb == null ? plateTop : yb);
          if (!deep.x0) brace(xa, zPost, yG0 - 0.06, false, 1);
          if (!deep.x1) brace(xb, zPost, yG0 - 0.06, false, -1);
          /* AND ON THE WALL'S OWN PLATE: from the same post back toward the
             house, which is the pair he asked for and the direction the roof
             actually pushes. */
          if (!deep.x0) brace(xa, zPost, (ya == null ? plateTop : ya) - 0.06, true, -sgz);
          if (!deep.x1) brace(xb, zPost, (yb == null ? plateTop : yb) - 0.06, true, -sgz);
          /* AND ON A MONO THE TWO ARE AT DIFFERENT HEIGHTS, WHICH IS THE POINT.
             The corner post at the high end carries two different members: the
             RAKED plate, which climbs away from it along the wall line, and the
             LEVEL profile across the gable, which sits at the lower of the two
             plates. One brace under each, so neither is propped by something a
             whole rise above it (Marek). */
          ctx.log('PLT gable corner braces: x0 ' + (deep.x0 ? 'taken by the eave side'
                    : ('raked plate at ' + Math.round((ya == null ? plateTop : ya) * 1000) +
                       ' + level tie at ' + Math.round(yG0 * 1000))) +
                  ' | x1 ' + (deep.x1 ? 'taken by the eave side'
                    : ('raked plate at ' + Math.round((yb == null ? plateTop : yb) * 1000) +
                       ' + level tie at ' + Math.round(yG0 * 1000))) +
                  ' -- two per post, at 90 degrees');

          /* WHERE THE TIE LANDS -- computed once, because it is needed at every
             station now and not only at the far end. */
          function eaveXPost(sgx, ovv) {
            var tailR = ovv + API.facadeThk() - PLATE.t;
            var xEdg = (sgx < 0 ? B.x0 - tailR : B.x1 + tailR);
            return xEdg - sgx * (PW / 2);
          }
          var xA = deep.x0 ? eaveXPost(-1, O.x0) : xa;
          var xB = deep.x1 ? eaveXPost(1, O.x1) : xb;
          var fullG = (xB - xA) + PW;          /* both courses run to the post faces */

          /* THE SAME PAIR OF COURSES, AT WHATEVER STATION IS ASKED FOR.

             It was written once, inline, at the far end of the run -- so the
             intermediate posts got their braces and stood there with nothing
             across them (Marek: copy what is at the end and use it in the middle
             too). Now it is a function and the end is just its first caller. */
          function tiePair(zAt, yLeft, yRight) {
            var ptT = CHORD.t, gapT = 0.004;
            var yT = Math.min(yLeft == null ? plateTop : yLeft, yRight == null ? plateTop : yRight);
            var lo = new T.Mesh(new T.BoxGeometry(fullG, ptT - gapT, CHORD.d), timber);
            lo.position.set((xA + xB) / 2, yT + ptT / 2, zAt);
            g.add(lo);
            var up = new T.Mesh(new T.BoxGeometry(fullG, ptT - gapT, CHORD.d), timber);
            up.position.set((xA + xB) / 2, yT - ptT / 2, zAt);
            g.add(up);
            tally.hdr += fullG * 2;
            return yT;
          }

          /* THE RUN-OUT PLATE IS A BEAM TOO, AND IT IS THE LONG ONE.

             Each wall carries its own two plates out to this post -- 6 m at full
             reach -- and until now the only thing under that was the post at the
             very end. A post goes in wherever the run is longer than the span,
             evenly divided, on BOTH wall lines, and each one braces along the
             plate and across it (Marek). */
          var zWall = (sgz < 0) ? B.z0 : B.z1;
          var runG = Math.abs(zPost - zWall);
          var nG = Math.ceil(runG / EAVE_POST.span), qG, mids = 0;
          for (qG = 1; qG < nG; qG++) {
            var zMid2 = zWall + (zPost - zWall) * (qG / nG);
            var ym0 = _plateSoffitAt(xa, zMid2), ym1 = _plateSoffitAt(xb, zMid2);
            post(xa, zMid2, ym0 == null ? plateTop : ym0, true);
            post(xb, zMid2, ym1 == null ? plateTop : ym1, true);
            /* the run-out plate is a through beam here too: both ways along it,
               and one across toward the middle of the bay */
            var y0b = (ym0 == null ? plateTop : ym0) - 0.06;
            var y1b = (ym1 == null ? plateTop : ym1) - 0.06;
            brace(xa, zMid2, y0b, true, 1);  brace(xa, zMid2, y0b, true, -1);
            brace(xb, zMid2, y1b, true, 1);  brace(xb, zMid2, y1b, true, -1);
            /* AND THE SAME PAIR OF COURSES ACROSS, exactly as at the far end */
            var yTieM = tiePair(zMid2, ym0, ym1);
            /* UNDER THE TIE, NOT UNDER ITS OWN PLATE (Marek). These two prop the
               piece that runs ACROSS, and that piece sits at the LOWER of the two
               soffits -- so on the high side its own plate is a whole rise above
               the thing it is supposed to hold, and the brace came out lying on
               top of it. The far end has always taken the lower figure; this is
               the same number. The braces ALONG the wall plate keep their own
               side's height, because there they really do bear on their own
               plate. */
            brace(xa, zMid2, yTieM - 0.06, false, 1);
            brace(xb, zMid2, yTieM - 0.06, false, -1);
            mids += 2;
          }
          if (mids) ctx.log('POST gable z' + (sgz < 0 ? '0' : '1') + ': run-out ' +
                  Math.round(runG * 1000) + ' mm from the wall -> ' + mids +
                  ' intermediate post(s) on the two wall lines, each pair tied ' +
                  'across by the same two courses as the far end, at ' +
                  Math.round(runG / nG * 1000) + ' mm, max span ' +
                  Math.round(EAVE_POST.span * 1000));

          /* THE PLATE IS ALREADY OUT THERE. adu-stud-wall runs BOTH of its plates
             past the end wall by this same reach -- I had been drawing a third
             member here to meet the post, which is a new profile pretending to be
             a plate. The post reaches the real one. */
          /* AND A DOUBLE PLATE BETWEEN THE TWO POSTS -- ALWAYS, NOT ONLY WHILE
             THE OTHER SIDE IS SHALLOW.

             This was gated on !deep.x0 && !deep.x1, which is the same test the
             POSTS use, and that is the confusion: the guard exists so a gable post
             is not stood beside an eave post in the same corner, and it had been
             put round the plate as well. So the moment the side with the
             HORIZONTAL plates was pulled out far enough to grow its own posts and
             beam, this profile vanished from the raked end -- where nothing
             replaced it, because the eave beam runs the other way (Marek: it
             should stay there the whole time).

             It lands on a bearing either way. Where this end has its own posts it
             sits on them; where the eave side took the corner instead, it sits on
             that wall's own top plate, which is cantilevered out to exactly this
             line -- the same timber it lands on when there are no deep eaves
             anywhere. The BRACES stay with the posts, because a brace with no post
             under it is a strut into thin air. */
          if (true) {
            /* THE FAR END IS NOW JUST THE FIRST CALLER OF tiePair, with the same
               reach rule: out on the eave post where that side took the corner,
               on this end's own post where it did not. */
            var yG = tiePair(zPost, ya, yb);

            ctx.log('PLT gable tie x0 end at ' + Math.round(xA * 1000) +
                    (deep.x0 ? ' (out on the eave post, wall line ' + Math.round(xa * 1000) + ')' : ' (its own post)') +
                    ', x1 end at ' + Math.round(xB * 1000) +
                    (deep.x1 ? ' (out on the eave post, wall line ' + Math.round(xb * 1000) + ')' : ' (its own post)') +
                    ' -- the tie is built either way and reaches the post at each end');
            ctx.log('PLT gable plate ' + Math.round(((xA + xB) / 2 - fullG / 2) * 1000) +
                    '..' + Math.round(((xA + xB) / 2 + fullG / 2) * 1000) +
                    '  post faces ' + Math.round((xa - PW / 2) * 1000) +
                    '..' + Math.round((xb + PW / 2) * 1000) +
                    '  posts ' + Math.round(xa * 1000) + ',' + Math.round(xb * 1000));
            /* the number that has been in dispute for five rounds: where the post
               stands along the run, and where the wall's plate actually ends */
            ctx.log('PLT gable post z ' + Math.round(zPost * 1000) +
                    '  face ' + Math.round((zPost + sgz * PW / 2) * 1000) +
                    '  plate ends ' + Math.round((sgz < 0 ? B.z0 - _plateEndReach(ovv)
                                                         : B.z1 + _plateEndReach(ovv)) * 1000) +
                    '  overhang ' + Math.round(ovv * 1000));

            /* WHERE THE TWO COURSES ACTUALLY ARE. */
            ctx.log('PLT plate lower ' + Math.round((yG - CHORD.t) * 1000) + '..' +
                    Math.round(yG * 1000) + '  upper ' + Math.round(yG * 1000) + '..' +
                    Math.round((yG + CHORD.t) * 1000) +
                    '  post top ' + Math.round(yG * 1000) +
                    '  z ' + Math.round(zPost * 1000) + ' (post ' + Math.round(zPost * 1000) + ')');
          }
        }

        var n2 = Math.max(2, Math.round((ovv + wt2 / 2) / 0.6)) * 2;
        tally.plateScrews = (tally.plateScrews || 0) + n2;
        /* AND ON THE PORCH A KING POST from the gable plate up to the ridge
           beam, which runs on over the porch to the roof's edge (thow-v19) */
        if (onPorch) {
          var rbP = H.porch.ridgeBeam ? H.porch.ridgeBeam() : null;
          var kBot = yG + CHORD.t, kTop = (rbP && rbP.bot) ? rbP.bot : null;
          if (kTop !== null && kTop - kBot > 0.10) {
            var km = new T.Mesh(new T.BoxGeometry(PORCH.king.w, kTop - kBot, PORCH.king.w), timber);
            km.position.set(rbP.x, (kTop + kBot) / 2, zPost);
            g.add(km);
            tally.len += kTop - kBot;
            tally.king = kTop - kBot;
          }
          tally.porch = { z: zPost, foot: PZ.footY, top: Math.min(ya == null ? plateTop : ya, yb == null ? plateTop : yb) };
          _porchFoot = null;
        }
      }

      /* ------------------------------------------------------------------
         ACROSS, AT EVERY STATION -- NOT ONLY AT THE TWO ENDS.

         With both eave sides out there are two rows of posts, and something has
         to tie a low post to the high one opposite it. At the END stations that
         tie already exists and always did: it is the END WALL'S OWN CAP, run out
         past the corner to the post line. The stations in BETWEEN have no wall
         behind them, so they had the braces and no profile -- the braces on the
         high side then climbed all the way to the rafters, because there was
         nothing lower to stop against (Marek).

         So the same pair as at the ends: a profile across at each intermediate
         station, at the LOWER of the two soffits, and both braces bearing under
         IT rather than under whatever happens to be above them. On a mono that
         lower soffit is the low eave, which is why the two ends of the tie sit
         at different heights on their posts.
         ------------------------------------------------------------------ */
      function eaveCrossTies() {
        if (!(deep.x0 && deep.x1)) return;
        var rL = O.x0 + API.facadeThk() - PLATE.t, rR = O.x1 + API.facadeThk() - PLATE.t;
        var xL = (B.x0 - rL) + PW / 2, xR = (B.x1 + rR) - PW / 2;
        var zNeg = B.z0 - _plateEndReach(O.z0), zPos = B.z1 + _plateEndReach(O.z1);
        var pNeg = zNeg + PW / 2, pPos = zPos - PW / 2;
        var runT = pPos - pNeg;
        var nT = Math.max(1, Math.ceil(runT / EAVE_POST.span)), qT, made = 0;
        for (qT = 1; qT < nT; qT++) {
          var zT = pNeg + runT * (qT / nT);
          var sL = _rafterSoffitAt(xL, zT), sR = _rafterSoffitAt(xR, zT);
          var yT = Math.min(sL == null ? plateTop : sL, sR == null ? plateTop : sR);
          beam(xL, zT, xR, zT, yT);
          /* each post braces down onto the tie it now has, from its own side */
          brace(xL, zT, yT - CHORD.d, false, 1);
          brace(xR, zT, yT - CHORD.d, false, -1);
          made++;
        }
        if (made) {
          ctx.log('BEAM cross ties ' + made + ' at the intermediate station(s), ' +
                  Math.round(xL * 1000) + ' to ' + Math.round(xR * 1000) +
                  ' -- the end stations are tied by the end walls\' own run-out caps, ' +
                  'these are the ones that had braces and no profile');
        }
      }

      if (deep.x0) eaveSide(-1, O.x0);
      if (deep.x1) eaveSide(1, O.x1);
      if (SHED) eaveCrossTies();
      if (deep.z0) gableEnd(-1, O.z0);
      if (deep.z1) gableEnd(1, O.z1);

      if (tally.posts) {
        if(H.deckFoot)H.add(H.deckFoot.code,tally.posts);
        H.add(EAVE_POST.postCode, tally.len);
        if (tally.hdr > 0) H.add(EAVE_POST.code, tally.hdr);
        if (tally.bases) {
          H.add(EAVE_POST.base, tally.bases);
          /* what holds the post down: two bolts through the post and its sleeve,
             and four fixings under the plate -- anchors into concrete, bolts into
             the ground screw's flange (adu-v1368) */
          H.add('FAT10002', tally.bases * 2);
          /* on the porch bolted through the trailer's steel (thow-v19) */
          var gBase = tally.bases - (tally.steel || 0);
          if (tally.steel) H.add(PORCH.fix, tally.steel * 4);
          if (gBase > 0) {
            if (tally.screws) H.add('FAT10001', gBase * 4);
            else H.add('FAS10075', gBase * 4);
          }
        }
        if (tally.screws) H.add(EAVE_POST.screw, tally.screws);
        if (tally.pads) H.add('grd.padBlock', tally.pads);
        if (tally.piers) H.add('grd.concrete', Math.round(tally.piers * PIER.w * PIER.w * (PIER.d + PIER.proud) * 1000) / 1000);
        if (tally.plateScrews) H.add('FAS60100', tally.plateScrews);
        if (tally.porch) {
          ctx.log('porch    roof over it: 2 posts ' + EAVE_POST.postCode + ' at the trailer\'s corners at the porch end (z ' + Math.round(tally.porch.z * 1000) + '), on the deck at +' +
                  Math.round(tally.porch.foot * 1000) + ' in a post base ' + EAVE_POST.base + ' bolted to the steel (' + PORCH.fix + ' x 4 each), up to the wall plates at +' + Math.round(tally.porch.top * 1000) +
                  ' that run on to the roof\'s edge | the gable plate across them, knee braces both ways' +
                  (tally.king ? (' | a king post ' + Math.round(tally.king * 1000) + ' up to the ridge beam') : ' | NO king post -- no ridge beam over it'));
        }
        ctx.log('POST ' + tally.posts + ' deep=' +
                (deep.x0 ? 'x0 ' : '') + (deep.x1 ? 'x1 ' : '') +
                (deep.z0 ? 'z0 ' : '') + (deep.z1 ? 'z1' : '') +
                ' at ' + (_postAt.length ? _postAt.join(' ') : '-'));
        _why(ctx, 'posts    a deep EAVE gets a header along it on two posts; a deep ' +
                'GABLE runs the WALL PLATE out and the post bears straight on it -- ' +
                'no second member at the rafter ends');
        ctx.log('posts    heights read off the roof plane, which IS the rafter soffit ' +
                'and the plate top -- header tops flush under the rafters, plate posts ' +
                'one plate (' + API.fmtLen(API.plateZone()) + ') below that line');
        ctx.log('posts    ' + Math.round(tally.len * 10) / 10 + ' m of post, ' +
                Math.round(tally.hdr * 10) / 10 + ' m of header and plate, ' +
                (tally.bases || 0) + ' x ' + EAVE_POST.base + ' post base (4x4 posts ' + EAVE_POST.postCode + ', headers ' + EAVE_POST.code + '), ' +
                ((tally.bases || 0) * 2) + ' x FAT10002 bolt through the posts, ' + ((tally.bases || 0) * 4) + ' x ' +
                (tally.screws ? 'FAT10001 bolt into the screw flanges' : 'FAS10075 anchor into the concrete') +
                (tally.screws ? (', ' + tally.screws + ' x ' + EAVE_POST.screw + ' ground screw, no U holder') : '') +
                (tally.pads ? (', ' + tally.pads + ' x precast pad FCO00009') : '') +
                (tally.piers ? (', ' + tally.piers + ' cast pier(s) FCO00002') : '') +
                (tally.plateScrews ? (', ' + tally.plateScrews + ' x FAS60100 into the plate') : ''));
      }
      return tally;
    }

    /* =========================================================================
       THE GUTTERS MODULE (core-v759, Marek 2026-10-27: zacnes ze stavajicich
       konfiguratoru tvorit centralni asset, at se nemusi stejne veci opakovat
       a abychom mohli rychleji stavet produkty?). THE FIRST SHARED MODULE.

       Twelve house-line libs carried their own _gutters -- one text copied
       from the ADU and then drifted into nine versions: the half-hip stop
       ends (adu-v1357) never reached SHED, TAURUS or GARDEN BAR, the L's
       inside corners and loop downspouts lived only in ANY HOUSE, the
       splash blocks only in GARAGE APT, a bare roof's gutter line only in
       TAURUS, the light downspout section only in RANCH. This is that one
       text, once: the ANY HOUSE version (the ADU's own plus the L, every L
       line behind H.L), with the outlet planner and the downspout target
       beside it. The K section, the stop end, the corner fitting and the
       pipe sweep are the K7 drawer's (_rsgShell / _rsgPlate / _rsgCorner /
       _rsgRound / _rsgPath) -- the same shapes, written once already.

       A product hands in only what is its own:

         API.gutters(ctx, {
           planes:    function () -> the roof's planes (roofPlanes)
           deckPlane: function (plane) -> the deck plane (its eave corners)
           deckT:     function () -> the deck's thickness on top of the eave
           dripFace:  m, the drip edge's face height
           color:     the trough's colour (magenta while it is under review)
           metal:     the fittings' colour
           grid, facadeThk: the product's API.grid / API.facadeThk
           EAVE_POST: the product's post constants (w, trigger)
           posts:     the posts _eavePosts published (_postPts), or []
           taken:     the outlets taken this build (_outletTaken), mutated
           add:       function (code, qty) -- the product's own bill line
           dspSeg:    points round a downspout's corner (RANCH 3, else 24)
           onShoe:    function ({ x, z, ox, oz }) -- each pipe's foot (GARAGE APT)
           L:         the L's own runs and corners (ANY HOUSE): on, prep,
                      pipes, runs, meets, inner, innerMesh, said, cornerCands
         })

       Every product's bill and drawing are the same as with its own copy,
       code by code (headless, every roof type), except where its copy had
       missed a fix -- those are said in status/_shared.md.
       ========================================================================= */
    var GK = { h: 0.127, w: 0.127, drop: 0.09, board: 0.025, bracket: 0.60, standoff: 0.05,
               gutter: 'ROG01251', brk: 'ROA01251', cap: 'ROG00001', hopper: 'ROG00005', pipe: 'ROG01252',
               elbow: 'ROG00006', shoe: 'ROG00009', clip: 'ROG00011', corner: 'ROG00003', icorner: 'ROG00002' };
    var GK_DSP = { w: 0.060, d: 0.087, r: 0.010, wall: 0.0012 };
    var _gkCornerIn = 0;
    function _gkL(H) { return !!(H.L && H.L.on && H.L.on()); }
    /* the downspout's hollow section; seg -- points round each inner corner
       (24 unless the product asks fewer: RANCH 3, a lighter mesh) */
    function _gkDspShell(T, seg) {
      var out = _rsgRound(T, GK_DSP.w, GK_DSP.d, GK_DSP.r), wall = GK_DSP.wall;
      var inner = _rsgRound(T, GK_DSP.w - wall * 2, GK_DSP.d - wall * 2, Math.max(0.001, GK_DSP.r - wall));
      out.holes.push(new T.Path(inner.getPoints(seg || 24)));
      return out;
    }
    /* one swept pipe with arcs at the bends; its length */
    function _gkSweep(T, g, mat, pts, seg) {
      var path = _rsgPath(T, pts, 0.075);
      if (!path) return 0;
      var geo = new T.ExtrudeGeometry(_gkDspShell(T, seg), {
        extrudePath: path, steps: Math.max(24, pts.length * 14), bevelEnabled: false, curveSegments: seg
      });
      g.add(new T.Mesh(geo, mat));
      return path.getLength();
    }
    function _gkOutletPlan(H, bx, bz, nx, nz, ux, uz, run, prefer) {
      var B = _buildRect();
      var half = run / 2 - 0.12;
      /* prefer: a corner {x, z} the outlet has to be at (a hip's two pipes on
         opposite corners) -- only candidates within a metre of it are looked
         at, a post there still wins over the bare corner */
      /* i IS DECLARED HERE, and it was not. The loop that used it lived in the
         block I deleted, so the assignment landed on the module-level i that the
         layers share -- clobbering whatever was mid-loop out there. The gutter
         threw, and the message that would have said so had just been silenced
         along with the rest of the gutter prose. */
      var cand = [], k, i;

      /* THE POSTS AS BUILT, ASKED FOR RATHER THAN WORKED OUT AGAIN.

         This rebuilt them from the same rule _eavePosts uses -- and got a
         different answer, because the rule has two branches and this only had
         one. The log caught it in the end:

           POST 2 deep=z1 at -2910,5100p  4130,5100p     what was built
           PIPE post raw=-4150,5100 posts=4              what this aimed at

         Four candidates where two posts exist, and the chosen one at -4150 is
         neither of them. The pipe was heading for a post that is not there, which
         is why it never looked attached to the one that is.

         Third time a rule written twice has drifted: the batten stations, the
         member positions, and now this. The posts publish; nothing here guesses.
         If none were published there are none, and the corners take over. */
      if (H.posts && H.posts.length) {
        for (i = 0; i < H.posts.length; i++) cand.push(H.posts[i]);
      }

      if (_gkL(H)) H.L.cornerCands().forEach(function (c) { cand.push(c); });   /* the L's own corners */
      else [[B.x0, B.z0], [B.x1, B.z0], [B.x1, B.z1], [B.x0, B.z1]].forEach(function (c) {
        cand.push({ x: c[0], z: c[1], what: 'corner' });
      });

      /* A POST ON THIS EAVE WINS OUTRIGHT. */
      function reach(C) {
        var t = (C.x - bx) * ux + (C.z - bz) * uz;
        t = Math.max(-half, Math.min(half, t));
        var hx = bx + nx * (GK.w / 2) + ux * t;
        var hz = bz + nz * (GK.w / 2) + uz * t;
        return { t: t, d: Math.hypot(C.x - hx, C.z - hz) };
      }
      /* perpendicular distance from the gutter's own line */

      if (prefer) {
        /* everything on that corner's quadrant of the building -- the frame
           corner itself and any post standing out there; a post there wins
           below, so the pipe goes down it (Marek) */
        var near = [], kq, mx = (B.x0 + B.x1) / 2, mz = (B.z0 + B.z1) / 2;
        var sxP = (prefer.x < mx) ? -1 : 1, szP = (prefer.z < mz) ? -1 : 1;
        for (kq = 0; kq < cand.length; kq++) {
          var cq = cand[kq];
          /* the L (anyhouse-lib-v13): that corner itself and a post by it -- its
             box's quadrants hold corners of both rectangles */
          if (_gkL(H)) { if (Math.hypot(cq.x - prefer.x, cq.z - prefer.z) < 1.0) near.push(cq); continue; }
          if (((cq.x - mx) * sxP > -0.05) && ((cq.z - mz) * szP > -0.05)) near.push(cq);
        }
        if (near.length) cand = near;
      }
      var mine = [], others = [];
      for (k = 0; k < cand.length; k++) {
        /* EVERY POST OF THIS ROOF IS A CANDIDATE. NO DISTANCE TEST.

           Three filters tried to say which posts belonged to this gutter and each
           excluded the case it was not written for. The shortest bend already
           rules out the far side, so no filter is needed -- a post is a post. */
        var C0 = cand[k];
        if (C0.what === 'post' || C0.what === 'post-plate') mine.push(C0);
        else others.push(C0);
      }

      /* BUT PREFERRING A POST IS NOT WORTH CROSSING THE BUILDING. */
      /* A TARGET IS NOT USED TWICE.

         Each run picks the nearest post on its own, and where the overhang is on
         ONE side the posts are all on that side -- so both outlets chose the same
         post and both pipes ended up at one corner. Down the gable it never
         showed, because there the posts stand at opposite ends anyway.

         Remembering what has been taken keeps the second outlet off the first
         one's post, which is what puts them on opposite corners without forcing
         either of them anywhere (Marek). */
      function bestOf(list) {
        var b = null, bd = 1e9, q;
        for (q = 0; q < list.length; q++) {
          if (H.taken.indexOf(list[q]) >= 0) continue;
          var d = reach(list[q]).d;
          if (d < bd) { bd = d; b = list[q]; }
        }
        if (!b) {
          /* everything taken -- fall back rather than leave a run undrained */
          for (q = 0; q < list.length; q++) {
            var d2 = reach(list[q]).d;
            if (d2 < bd) { bd = d2; b = list[q]; }
          }
        }
        return b ? { c: b, d: bd } : null;
      }
      var bPost = bestOf(mine), bWall = bestOf(others);
      var pool;
      if (bPost && (!bWall || bPost.d <= bWall.d + 0.5)) pool = mine;
      else pool = others;

      var best = null, bestOff = 1e9, bestT = 0;
      /* THE FILTER IS GONE. Restricting the candidate pool to one half of the
         run changed which POST the pipe aims at, not just where along the
         gutter... */
      for (k = 0; k < pool.length; k++) {
        var r5 = reach(pool[k]);
        if (r5.d < bestOff) { bestOff = r5.d; best = pool[k]; bestT = r5.t; }
      }
      if (best) H.taken.push(best);
      return { t: bestT, target: best, bend: bestOff,
               forced: (pool === mine) ? mine.length : 0 };
    }
    /* THE ANCHOR IS ALREADY CHOSEN -- this only dresses it: which face it sits
       on, how far in from the arris, how far it stands off. Searching again here
       is what let the two decisions disagree. */
    function _gkDspTarget(H, best, nx, nz) {
      var B = _buildRect();
      if (!best) return { x: 0, z: 0, ox: nx, oz: nz, face: 0, what: 'nothing', standOut: 0 };

      var cx = (B.x0 + B.x1) / 2, cz = (B.z0 + B.z1) / 2;
      var ox, oz, L;

      if (best.what === 'corner' && best.faces) {
        /* the L's corner knows its two faces: the one this eave looks down,
           and 300 mm along it, away from the arris */
        var fb = best.faces[0], fd = -2, fi;
        for (fi = 0; fi < best.faces.length; fi++) {
          var fdot = best.faces[fi].n[0] * nx + best.faces[fi].n[1] * nz;
          if (fdot > fd) { fd = fdot; fb = best.faces[fi]; }
        }
        ox = fb.n[0]; oz = fb.n[1];
        best = { x: best.x + fb.along[0] * 0.30, z: best.z + fb.along[1] * 0.30, what: 'corner' };
        _gkCornerIn = 0.30;
      } else if (best.what === 'corner') {
        /* A PIPE COMES DOWN A WALL, NOT DOWN A CORNER.

           The target was the corner itself and "out" was the diagonal from the
           centre -- so the pipe hung at 45 degrees off the arris with nothing
           square behind it, which is why it looked jammed into the corner and
           why a bracket had nowhere flat to land.

           It belongs on ONE of the two faces, a little way in. Which face: the
           one this eave already looks along, so the pipe stays on the side of
           the building the water is coming off. And in from the arris far enough
           to clear the corner trim and give the bracket solid wall -- 300 mm,
           which is where anyone hanging one would put it. */
        var faceX = (Math.abs(nx) > Math.abs(nz));
        ox = faceX ? (nx > 0 ? 1 : -1) : 0;
        oz = faceX ? 0 : (nz > 0 ? 1 : -1);

        /* step in from the arris ALONG that face, towards the middle */
        var inAmt = 0.30;
        if (faceX) {
          best = { x: best.x, z: best.z + (best.z > cz ? -inAmt : inAmt), what: 'corner' };
        } else {
          best = { x: best.x + (best.x > cx ? -inAmt : inAmt), z: best.z, what: 'corner' };
        }
        _gkCornerIn = inAmt;
      } else {
        /* A POST IS APPROACHED FROM THE SIDE THE GUTTER IS ON.

           Out was the direction from the middle of the BUILDING, so a post near a
           corner got a diagonal -- and the pipe stepped 147 mm along the eave as
           well as 85 mm out, which is what put it beside the post instead of
           against it. Same fault the corners had, left behind when those were
           fixed.

           The eave's own outward normal is the answer: square to the wall, no
           travel along it. Snapped to the dominant axis so a segment a degree or
           two off square does not reintroduce the drift. */
        ox = nx; oz = nz;
        if (Math.abs(ox) >= Math.abs(oz)) { ox = ox > 0 ? 1 : -1; oz = 0; }
        else { oz = oz > 0 ? 1 : -1; ox = 0; }
      }

      /* AND IT STANDS OFF FAR ENOUGH TO STILL BE STANDING OFF LATER. */
      /* A PLATE POST STANDS PAST THE GABLE, WHERE THERE IS NO WALL TO CLEAR. */
      var clear5 = (best.what === 'post' || best.what === 'post-plate')
        ? (H.EAVE_POST.w / 2 + GK.standoff + GK_DSP.d / 2)
        : (H.facadeThk() + GK.standoff + GK_DSP.d / 2);

      return { x: best.x + ox * clear5, z: best.z + oz * clear5,
               ox: ox, oz: oz, face: Math.atan2(ox, oz),
               what: best.what, standOut: clear5, cornerIn: _gkCornerIn };
    }
    /* core-v760: the eave drip the edges drawer drew this build along the
       same eave line as a gutter run (parallel, on its height, its middle
       within the drip's length), nearest first; null when there is none */
    function _gkDrip(ctx, a, b, mx, mz, nx, nz) {
      var L = (ctx && ctx._rsDrip) || [], best = null, bd = 1e9, q;
      var y = Math.min(a[1], b[1]);
      var ux = b[0] - a[0], uz = b[2] - a[2], ul = Math.hypot(ux, uz) || 1;
      ux /= ul; uz /= ul;
      for (q = 0; q < L.length; q++) {
        var r = L[q], rx = r.b[0] - r.a[0], rz = r.b[2] - r.a[2], rl = Math.hypot(rx, rz);
        if (!(rl > 0.05)) continue;
        rx /= rl; rz /= rl;
        if (Math.abs(rx * uz - rz * ux) > 0.02) continue;
        if (Math.abs(Math.min(r.a[1], r.b[1]) - y) > 0.06) continue;
        var perp = Math.abs((mx - r.a[0]) * rz - (mz - r.a[2]) * rx);
        if (perp > 0.06) continue;
        var t = (mx - r.a[0]) * rx + (mz - r.a[2]) * rz;
        if (t < -0.05 || t > rl + 0.05) continue;
        if (perp < bd) { bd = perp; best = r; }
      }
      return best;
    }
    function gutters(ctx, H) {
      var T = ctx.THREE, g = ctx.group;
      var _gutCorners = [], _gkTopById = {};
      var R = STATE.roof || {};
      if (R.type === 'flat') {
        _why(ctx, 'gutter   parapet -- the roof turns up against the wall and drains ' +
                'inward to an outlet; there is no eave to hang a gutter on');
        return;
      }

      var planes = [];
      try { planes = H.planes() || []; } catch (e) { return; }
      if (!planes.length) return;

      var metal = new T.MeshStandardMaterial({
        color: H.color,
        roughness: 0.45, metalness: 0.6 });

      var tot = { run: 0, brk: 0, caps: 0, hop: 0, pipe: 0, elb: 0, shoe: 0, clip: 0 };

      /* box3 is gone -- nothing calls it any more. It made rectangular blocks,
         which is what every version of the corner fitting came out as; once the
         fitting is extruded from the gutter's own profile there is no box left to
         draw. */

      /* THE CORNER LIST IS GONE. It existed so each end could measure its own
         distance to its own corner, and that per-end measurement is exactly what
         made the extensions unequal. A symmetric stretch needs no list. */

      var pi, ahL = _gkL(H);
      if (ahL) H.L.prep(planes);
      var ahPipe = (ahL && R.type === 'hip') ? H.L.pipes() : null;
      for (pi = 0; pi < planes.length; pi++) {
        var E = planes[pi].eave;
        if (!E || E.length < 2) continue;
        if (ahL && !H.L.runs()[pi]) continue;   /* a piece with no eave edge of its own */
        var a = E[0], b = E[1];
        var run = Math.hypot(b[0] - a[0], b[2] - a[2]);
        if (!(run > 0.6)) continue;

        /* the eave line as published: direction along it, and outward from it */
        var ux = (b[0] - a[0]) / run, uz = (b[2] - a[2]) / run;
        var nx = -uz, nz = ux;
        /* outward is away from the building centre */
        var cx = 0, cz = 0, P = planes[pi].pts, q;
        for (q = 0; q < P.length; q++) { cx += P[q][0]; cz += P[q][2]; }
        cx /= P.length; cz /= P.length;
        var mx = (a[0] + b[0]) / 2, mz2 = (a[2] + b[2]) / 2;
        if ((mx - cx) * nx + (mz2 - cz) * nz < 0) { nx = -nx; nz = -nz; }

        /* THE GUTTER HANGS OFF THE DECK PLANE'S EAVE, LIKE EVERYTHING ELSE.

           It had its own arithmetic for the tail reach and its own lift, so it
           moved by numbers that were near the flashing's and not equal to them.
           At a shallow pitch near enough is invisible; as the pitch grew the
           trough rose without travelling out, and the drip edge left it behind.

           One source now: the same offset plane the deck and both flashings are
           drawn from. Its eave corners already carry the horizontal AND the
           vertical part of the move, so there is nothing left here to compute --
           only the board thickness and the drop under the drip edge. */
        var DPg = H.deckPlane(planes[pi]);
        if (DPg && DPg.eave) {
          a = DPg.eave[0]; b = DPg.eave[1];
          mx = (a[0] + b[0]) / 2; mz2 = (a[2] + b[2]) / 2;
        }
        /* on the L: the plane's own edge, stopped at an inside corner's fitting */
        var ahRunOwn = 0, ahCapA = true, ahCapB = true;
        if (ahL) {
          var RL = H.L.runs()[pi], s0 = 0, s1 = RL.run;
          if (RL.inA != null) { s0 = RL.inA; ahCapA = false; }
          if (RL.inB != null) { s1 = RL.inB; ahCapB = false; }
          ahRunOwn = RL.run;
          a = [RL.a[0] + RL.ux * s0, RL.a[1], RL.a[2] + RL.uz * s0];
          b = [RL.a[0] + RL.ux * s1, RL.b[1], RL.a[2] + RL.uz * s1];
          run = Math.max(0.05, s1 - s0); ux = RL.ux; uz = RL.uz; nx = RL.nx; nz = RL.nz;
          mx = (a[0] + b[0]) / 2; mz2 = (a[2] + b[2]) / 2;
        }
        var out = GK.board + GK.w / 2 + 0.010;
        var gx = mx + nx * out, gz = mz2 + nz * out;

        var deckLine = Math.min(a[1], b[1]) + H.deckT();
        var yTop = deckLine - H.dripFace - 0.012;
        /* THE TROUGH HANGS FROM THE DRIP EDGE AS IT IS DRAWN (core-v760, Marek
           2026-10-27: ADU, okap by to chtelo posunout vys = bliz k profilu
           okapnice). The rule above read the drip's face down from the DECK's
           top, but the drawer lays the drip on the batten grid -- on a metal
           sheet or seam roof that is the counter-battens and battens higher,
           on a hip the underlay plus its lift -- so the trough hung 36 to 40
           mm under the drip with the fascia showing between. Now the drip's
           own foot, recorded by the edges drawer this build:
             - its face stands out over the trough (a gable's drip, its leg
               running out): the face laps 12 mm into the trough, its kick
               clear of the hangers 20 mm down;
             - its face is at the fascia, behind the trough's back (a hip's,
               its leg running in under the covering): the trough's top 5 mm
               under the kick, which reaches over the back lip.
           No record (a product with its own edges, a hidden edges layer): the
           old rule, unchanged. */
        var DRg = _gkDrip(ctx, a, b, mx, mz2, nx, nz), dripHow = 'deck rule';
        if (DRg) {
          var dFace = (DRg.fx - mx) * nx + (DRg.fz - mz2) * nz, backOff = GK.board + 0.010;
          if (dFace > backOff + 0.003) { yTop = DRg.yFace + 0.012; dripHow = 'lapped 12 into the trough (face ' + Math.round((dFace - backOff) * 1000) + ' out from its back)'; }
          else { yTop = DRg.yLow - 0.005; dripHow = '5 under the kick (face ' + Math.round((backOff - dFace) * 1000) + ' behind its back)'; }
          ctx.log('GUT  ' + String(planes[pi].id || ('p' + pi)) + ' top ' + Math.round(yTop * 1000) + ' from the drip as drawn: ' + dripHow +
                  ' | the deck rule gave ' + Math.round((deckLine - H.dripFace - 0.012) * 1000));
        }
        _gkTopById[String(planes[pi].id)] = yTop;
        var yEave = yTop;

        /* THE K SECTION AS A SHEET, NOT AS A LUMP -- tiny's words and tiny's
           numbers. A gutter is folded from something under a millimetre thick;
           drawn as a filled outline it comes out solid, with no trough to catch
           anything, and nothing hung inside it can ever be seen. The section is
           the outer face and the inner face joined into one thin band. */
        var shp = _rsgShell(T);
        /* AT AN OUTLET CORNER THE RUN REACHES THE NEXT GUTTER'S OUTER FACE.

           The two runs that meet there stop on their own fascia lines, a gutter's
           width short of each other. Carrying each one that width further -- at
           the outlet end only -- makes them overlap, and the corner is closed by
           the gutters themselves.

           Which end that is comes from the outlet, so it is worked out below and
           applied here; the other three ends of every run are untouched. */
        /* A CORNER IS A FITTING, NOT AN OVERLAP. On a hip each run stops at the
           corner, a fascia board past its own line so it meets the back of the
           next run, and the corner itself is built below from the K section --
           two mitred wedges -- and billed as the external corner (Marek: the
           runs crossed each other by 200 mm). */
        /* AN END RUNS ON ONLY WHERE ANOTHER EAVE MEETS IT (adu-v1357). A hip roof
           used to run every gutter past both ends to meet its neighbour round
           the corner -- right for a full hip, wrong for a half-hip, where the
           side gutters end at the gable and were left open with no stop end
           (Marek: okapy nemaji koncove prvky). Each end asks whether another
           eave starts or stops there: yes, it runs on to the corner; no, it
           stops and gets its end cap. */
        var ext = (STATE.roof.type === 'hip') ? GK.board : 0;
        function _eaveMeets(pt) {
          var qe;
          for (qe = 0; qe < planes.length; qe++) {
            if (qe === pi) continue;
            var Eo = planes[qe].eave;
            if (!Eo || Eo.length < 2) continue;
            if (Math.hypot(Eo[0][0] - pt[0], Eo[0][2] - pt[2]) < 0.05 || Math.hypot(Eo[1][0] - pt[0], Eo[1][2] - pt[2]) < 0.05) return true;
          }
          return false;
        }
        var extA = (ext && _eaveMeets(E[0])) ? ext : 0, extB = (ext && _eaveMeets(E[1])) ? ext : 0;
        /* the L (anyhouse-lib-v13): its own run's ends -- on to the corner where
           another run's own end is, never at the inside corner's fitting
           (the eave record is the whole uncut eave there) */
        if (ahL) { extA = (ext && ahCapA && H.L.meets(pi, 0)) ? ext : 0; extB = (ext && ahCapB && H.L.meets(pi, 1)) ? ext : 0; }
        if (extA || extB) _gutCorners.push({ a: [a[0], a[2]], b: [b[0], b[2]], n: [nx, nz], yBase: yTop - GK.h });

        var geo = new T.ExtrudeGeometry(shp, { depth: run + extA + extB,
                                               bevelEnabled: false });
        geo.translate(0, 0, -run / 2);

        /* THE SECTION GROWS OUT FROM THE FASCIA, AND IT WAS GROWING IN.

           rotateY(t) sends local +x to (cos t, -sin t). With t = atan2(nx,nz) +
           PI/2 that comes out as MINUS the outward normal -- so the whole profile
           was built backwards from its anchor and the trough sat tucked under the
           roof instead of hanging off the edge of it.

             +PI/2  ->  local +x maps to [-1, 0]   inward
             -PI/2  ->  local +x maps to [ 1, 0]   outward

           Minus. And the anchor moves with it: local x = 0 is the BACK of the
           trough, so it belongs on the fascia line, not half a trough inboard of
           where the trough was wanted. */
        var rotOut = Math.atan2(nx, nz) - Math.PI / 2;
        geo.rotateY(rotOut);
        var bx4 = mx + nx * GK.board, bz4 = mz2 + nz * GK.board;
        /* the corners are settled, so the gutter is metal again */
        var mesh = new T.Mesh(geo, metal);
        /* THE SHIFT COMES OUT OF THE ROTATION, NOT OUT OF u. */
        var hdX = Math.sin(rotOut), hdZ = Math.cos(rotOut);
        mesh.position.set(bx4 - hdX * extA, yTop - GK.h, bz4 - hdZ * extA);

        ctx.log('GUT run' + pi + ' head ' + hdX.toFixed(2) + ',' + hdZ.toFixed(2) +
                '  u ' + ux.toFixed(2) + ',' + uz.toFixed(2) +
                '  anchor ' + Math.round(bx4 * 1000) + ',' + Math.round(bz4 * 1000) +
                ' -> ' + Math.round(mesh.position.x * 1000) + ',' +
                Math.round(mesh.position.z * 1000));
        g.add(mesh);
        tot.run += ahL ? ahRunOwn : (run + extA + extB);

        /* WHAT EACH RUN DID WITH ITS ENDS. */
        /* NO STOP ENDS ON A HIP. A closed loop has no end to cap -- every run
           hands over to the next at a corner, and a plate there would sit across
           the... */

        ctx.log('GUT run' + pi +
                ' a ' + Math.round(a[0] * 1000) + ',' + Math.round(a[2] * 1000) +
                (extA ? ' EXT' : ' --') +
                '  b ' + Math.round(b[0] * 1000) + ',' + Math.round(b[2] * 1000) +
                (extB ? ' EXT' : ' --') +
                '  ext ' + Math.round(extA * 1000) + '/' + Math.round(extB * 1000));

        /* A HIDDEN HANGER, WHICH IS WHAT A K-STYLE GUTTER HANGS ON.

           Tiny's, and its reasoning: what was there before was a European long
           hook -- a strap up the slope with a nose bent down into the trough.
           Two things wrong with that. A long hook's nose is a CRADLE shaped like
           the gutter, not a spike into it; and a long hook belongs under a
           half-round, not under a K.

           So it is the American way: a flat bar spanning INSIDE the trough from
           the back to the front lip, with a tab down the back taking a screw
           into the fascia. It only reads at all because the trough is a sheet
           now -- inside a solid lump there was nothing to see, which is why this
           had to wait for step 1.

           IT STAYS ON THE RAFTER SPACING. That is what makes the two systems one
           item: the row is the same GUTTER BRACKET whichever profile the buyer
           fits, and the spacing is one layout rather than two. */
        var sp3 = H.grid();
        var nB = Math.max(2, Math.round(run / sp3));
        for (q = 0; q <= nB; q++) {
          var t2 = -run / 2 + run * (q / nB);
          /* the trough now runs from the fascia OUTWARD, so its centre is half a
             width out from the back -- the hangers follow it rather than staying
             where the old anchor used to be */
          var hx3 = bx4 + nx * (GK.w / 2) + ux * t2;
          var hz3 = bz4 + nz * (GK.w / 2) + uz * t2;
          var yBar = yTop - 0.020;

          /* the bar across the trough, back lip to front lip */
          var bg = new T.BoxGeometry(GK.w - 0.008, 0.004, 0.022);
          bg.rotateY(rotOut);
          var bm2 = new T.Mesh(bg, metal);
          bm2.position.set(hx3, yBar, hz3);
          g.add(bm2);

          /* and the tab down the back of the trough, which takes the screw */
          var tg = new T.BoxGeometry(0.0015, 0.045, 0.022);
          tg.rotateY(rotOut);
          var tm2 = new T.Mesh(tg, metal);
          tm2.position.set(bx4 + ux * t2, yBar - 0.022, bz4 + uz * t2);
          g.add(tm2);
          tot.brk++;
        }
        /* AN END CAP IS A SOLID PLATE, so it keeps the filled outline -- the
           one piece of a gutter that really is a lump. */
        for (q = -1; q <= 1; q += 2) {
          if ((q < 0 ? extA : extB) !== 0) continue;   /* that end runs on round a corner */
          if (ahL && !(q < 0 ? ahCapA : ahCapB)) continue;   /* the L's inside corner fitting */
          var cg = new T.ExtrudeGeometry(_rsgPlate(T), { depth: 0.0015, bevelEnabled: false });
          cg.rotateY(rotOut);
          var cm = new T.Mesh(cg, metal);
          cm.position.set(bx4 + ux * (run / 2) * q, yTop - GK.h,
                          bz4 + uz * (run / 2) * q);
          g.add(cm);
          tot.caps++;
        }

        /* the hopper at one end, and the pipe down to the ground */
        /* WHICH END THE OUTLET GOES TO.

           It always went to the '+' end, which is fine on a symmetric roof and
           silly on a dragged one: put the outlet at the end the roof was pulled
           out from and the pipe has to travel the whole overhang back to the
           building before it can go down.

           His two rules, in order:

             a post on this eave  -> the outlet goes to that end and the pipe
                                     comes down the post, because the post is
                                     already carrying that corner;

             no post              -> the end with the SMALLER rake overhang, so
                                     the offset is the small one rather than the
                                     big one. There is no reason to generate a
                                     two-metre swan neck when the other end of
                                     the same gutter needs 300 mm.

           Everything else -- what it lands on, how far it stands off, how far in
           from the arris -- is already decided by _dspTarget, which does not
           need to know any of this. */
        /* HALF THE PIPES ON A HIP. The corners join the runs in pairs, so two
           outlets drain all four; they go on opposite corners rather than
           adjacent ones, and the other two planes get gutter but no pipe. */
        /* THE SKIP TOOK THE GUTTER WITH IT. */
        /* ON A HIP, TWO OF THE FOUR EAVES GET A PIPE. */
        /* TWO PIPES, ON OPPOSITE EAVES, AND THE CORNER GOES WITH THEM. */
        var hipG = (STATE.roof.type === 'hip');
        /* THE TWO PIPES ON OPPOSITE CORNERS (Marek): the two side planes
           (0 and 1) each take one, plane 0 at its low end, plane 1 at its high
           end -- diagonally across the building, so each drains half the eave
           round its corner. The end planes drain round the corners into them. */
        var pipeCorner = null;
        if (ahPipe) { pipeCorner = ahPipe[pi] ? ahPipe[pi].corner : null; }
        else if (hipG && (pi === 0 || pi === 1)) {
          var E0 = planes[pi].eave, ends = [E0[0], E0[1]].sort(function (p1, p2) { return (p1[0] + p1[2]) - (p2[0] + p2[2]); });
          var Ec = (pi === 0) ? ends[0] : ends[1], Bc = _buildRect();
          /* the frame corner on that side (the candidates are frame corners and posts) */
          pipeCorner = { x: (Ec[0] < (Bc.x0 + Bc.x1) / 2) ? Bc.x0 : Bc.x1, z: (Ec[2] < (Bc.z0 + Bc.z1) / 2) ? Bc.z0 : Bc.z1 };
        }
        /* PLANES 1 AND 2 KEEP THEIR PIPE -- HE NAMED THEM.

           Green was plane 1 and blue was plane 2, and those two are the pair on
           opposite corners. I guessed three times and put both on the same side
           every time; the colours took one round and settled it.

           The other two eaves keep their gutter and drain round the corner into
           these, which is what the corner fitting is for. */
        var keepPipe = ahPipe ? !!ahPipe[pi] : (!hipG || pi === 0 || pi === 1);
        if (!keepPipe) {
          /* gutter only -- its water reaches the outlet round the corner */
        } else {
        var plan = _gkOutletPlan(H, bx4, bz4, nx, nz, ux, uz, run, pipeCorner);
        var hz = plan.t;
          /* AND THE CORNER FITTING, DRAWN WHERE THE PIPE IS. */

        var hx2 = bx4 + nx * (GK.w / 2) + ux * hz;
        var hz2 = bz4 + nz * (GK.w / 2) + uz * hz;

          /* NO FITTING. Seven versions of a separate corner piece -- boxes, two
             extrusions, a swept path, mirrored legs -- and every one of them was
             an... */
          /* the corners are billed once for the roof, below, not per run */
        /* THE ONE LINE THIS QUESTION NEEDS. Where the pipe went, how far it had
           to travel to get there, and how many posts it had to choose from --
           which between them say whether the plate post was a candidate at all. */
        /* ALL THREE POSITIONS ON ONE LINE.

           Four goes at this now, each one reasoning from a picture. The three
           numbers that decide it are the hopper, the raw post, and the point the
           pipe is finally put at -- and the gap between the last two is the thing
           he is describing. Logged together they settle it in one paste. */
        /* AND THE REACH THAT DECIDES WHETHER A POST EXISTS AT ALL.

           Five versions went into choosing between a post and a corner on a roof
           that had no post: the deepest overhang was 1360 mm and a post appears
           past 1500, so there was never anything to choose. The one number that
           would have said so in the first round was the reach against its
           trigger, and it was in a different line of a log too big to send.

           It goes here, next to the answer it explains. */
        var O9 = _roofOver();
        ctx.log('PIPE ' + (plan.target ? plan.target.what : 'none') +
                ' hop=' + Math.round(hx2 * 1000) + ',' + Math.round(hz2 * 1000) +
                ' raw=' + (plan.target ? Math.round(plan.target.x * 1000) + ',' +
                            Math.round(plan.target.z * 1000) : '-') +
                ' bend=' + Math.round(plan.bend * 1000) +
                ' posts=' + (plan.forced || 0) +
                ' reach=' + Math.round(Math.max(O9.x0, O9.x1, O9.z0, O9.z1) * 1000) +
                '/' + Math.round(H.EAVE_POST.trigger * 1000));
        tot.endWhy = plan.forced
          ? ('a post stands on this eave, so the pipe goes down it -- ' +
             Math.round(plan.bend * 1000) + ' mm of offset. Where the roof is held up, the ' +
             'pipe goes down the same timber')
          : ('no post on this eave, so the shortest bend wins -- ' +
             Math.round(plan.bend * 1000) + ' mm to the ' +
             (plan.target ? plan.target.what : '?'));
        /* THE OUTLET IS A COLLAR IN THE TROUGH FLOOR, NOT A BOX UNDER IT.

           What was here was a 130 mm cube slung beneath the gutter, wider than
           the trough it hangs off and square where everything around it is
           folded -- so it read as a lump of something else parked under the
           eave.

           A drop outlet is a hole in the bottom of the gutter with a short
           pressed sleeve that tapers from the trough floor down to the pipe
           section. It is smaller than the gutter, it sits INSIDE the profile at
           the top, and it is open. Drawn as a shallow taper: as wide as the
           trough floor where it starts, as wide as the downspout where it ends.

           The catalogue row does not change -- it is still the hopper, 125/80,
           one per outlet. What changes is that it now looks like the fitting it
           has always been billed as. */
        var rTop = 0.086 / 2;                  /* the flat of the K floor */
        var rBot = Math.max(GK_DSP.w, GK_DSP.d) / 2;
        var col = new T.Mesh(
          new T.CylinderGeometry(rTop, rBot, 0.10, 8, 1, true), metal);
        col.position.set(hx2, yTop - GK.h - 0.05 + 0.004, hz2);
        col.rotation.y = Math.PI / 8;
        g.add(col);
        tot.hop++;

        /* STEP 4 WILL PUT THE PIPE WHERE IT BELONGS, NOT STRAIGHT DOWN. */
        var drop = (yTop - GK.h - 0.10);
        if (drop > 0.4) {
          /* WHERE THE PIPE HAS TO END UP. */
          var yHop = yTop - GK.h - 0.10;
          var tgt = _gkDspTarget(H, plan.target, nx, nz);
          ctx.log('PIPE put=' + Math.round(tgt.x * 1000) + ',' + Math.round(tgt.z * 1000) +
                  ' off=' + Math.round(tgt.standOut * 1000) +
                  ' neck=' + Math.round(Math.hypot(tgt.x - hx2, tgt.z - hz2) * 1000));

          var offX = tgt.x - hx2, offZ = tgt.z - hz2;
          var off = Math.hypot(offX, offZ);

          var yStub = yHop - 0.08;
          var yBendTop = yStub;
          var route = [[hx2, yHop, hz2], [hx2, yStub, hz2]];

          if (off > 0.04) {
            /* the neck: down and across at once. A short offset is two bends and
               nothing between them; a long one is a bend, a cut length and
               another bend -- the same shape, but the middle is pipe you pay for
               rather than a fitting. Either way it is ONE swept run now. */
            var neck = Math.max(0.12, off * 0.8);
            var yNeck = yStub - neck;
            route.push([tgt.x, yNeck, tgt.z]);
            tot.elb += 2;
            yBendTop = yNeck;
            tot.neckOff = Math.max(tot.neckOff || 0, off);
            tot.neckLong = (tot.neckLong || 0) + (off > 0.30 ? 1 : 0);
          }

          route.push([tgt.x, 0.16, tgt.z]);
          /* the shoe is the last bend of the same pipe */
          route.push([tgt.x + tgt.ox * 0.13, 0.085, tgt.z + tgt.oz * 0.13]);
          tot.pipe += _gkSweep(T, g, metal, route, H.dspSeg);
          tot.shoe += 1;
          tot.elb += 1;
          if (H.onShoe) H.onShoe({ x: tgt.x + tgt.ox * 0.13, z: tgt.z + tgt.oz * 0.13, ox: tgt.ox, oz: tgt.oz });   /* garageapt-v34: its splash block */

          /* THE CLIPS, ON THE PIPE THAT IS ACTUALLY THERE.

             They were evenly spaced boxes floating at the pipe's centre line.
             A bracket is a strap AROUND the section with a leg back to whatever
             it is screwed to, and the top one belongs just under the lower bend
             -- that is where the weight of a full pipe is trying to pull the
             neck open, and it is the fixing that a downspout comes off without.

             Spacing at 2 m, which is what the fitting is rated for, and never
             fewer than one under the bend and one near the bottom. */
          var yLow = 0.45, ySpan = yBendTop - yLow;
          var nC = Math.max(1, Math.ceil(ySpan / 2.0));
          for (q = 0; q <= nC; q++) {
            var yc = yBendTop - 0.14 - (ySpan - 0.14) * (q / Math.max(1, nC));
            if (yc < yLow - 0.2) continue;
            /* the strap round the pipe */
            var st = new T.Mesh(new T.BoxGeometry(GK_DSP.w + 0.016, 0.016, GK_DSP.d + 0.016), metal);
            st.position.set(tgt.x, yc, tgt.z);
            st.rotation.y = tgt.face;
            g.add(st);
            /* and the leg back to whatever it hangs on -- as long as the gap it
               has to cross, which is the standoff and no more */
            var lg = new T.Mesh(new T.BoxGeometry(0.014, 0.012, GK.standoff), metal);
            lg.position.set(tgt.x - tgt.ox * (GK_DSP.d / 2 + GK.standoff / 2), yc,
                            tgt.z - tgt.oz * (GK_DSP.d / 2 + GK.standoff / 2));
            lg.rotation.y = tgt.face;
            g.add(lg);
            tot.clip++;
          }

          /* THE OUTLET ELBOW IS STILL A FITTING IN THE BILL even though it is now
             part of one swept run in the model. It is the piece that gets kicked,
             so it is a separate item everywhere -- ROG00009, not swallowed into
             the metres. The drawing being continuous does not change what is
             bought. */
          tot.tgt = tgt.what;
          tot.stand = tgt.standOut;
          tot.cornerIn = tgt.cornerIn;
        }
        }   /* end of the outlet branch -- the gutter above it is always built */
      }

      if (!tot.run) return;
      H.add(GK.gutter, tot.run);
      H.add(GK.brk, tot.brk);
      H.add(GK.cap, tot.caps);
      if (ahL && H.L.inner().length) {
        var mIn = new T.MeshStandardMaterial({ color: H.metal, roughness: 0.4, metalness: 0.7, side: T.DoubleSide }), iIn;
        for (iIn = 0; iIn < H.L.inner().length; iIn++) {
          var CI = H.L.inner()[iIn];
          /* at the height of the two runs it joins (core-v760), the deck rule
             when neither has one */
          var yCI = CI.y + H.deckT() - H.dripFace - 0.012, idCI = String(CI.ids || '').split('/'), tCI = null, kCI;
          for (kCI = 0; kCI < idCI.length; kCI++) {
            var tc = _gkTopById[idCI[kCI]];
            if (tc != null && (tCI == null || tc < tCI)) tCI = tc;
          }
          if (tCI != null) yCI = tCI;
          g.add(H.L.innerMesh(T, CI, yCI - GK.h, mIn));
        }
        H.add(GK.icorner, H.L.inner().length);
        ctx.log('GUT  L: ' + H.L.inner().length + ' x ' + GK.icorner + ' internal corner(s) (' + H.L.inner().map(function (c) { return c.ids; }).join(', ') +
                ') -- each run stops on the other\'s front line, the K section mitred round the square between; runs ' +
                H.L.runs().map(function (r9, i9) { return r9 ? (r9.id + ' ' + Math.round(r9.run * 1000)) : null; }).filter(Boolean).join(', ') +
                ' (each plane\'s own eave edge; a piece with none has no gutter)');
      }
      if (ahPipe && H.L.said()) ctx.log(H.L.said());
      ctx.log('GUT  ' + tot.caps + ' x ' + GK.cap + ' stop end(s) billed -- one wherever a gutter run ends without meeting another');
      H.add(GK.hopper, tot.hop);
      if (tot.pipe) {
        H.add(GK.pipe, tot.pipe);
        H.add(GK.elbow, tot.elb);
        H.add(GK.shoe, tot.shoe);
        H.add(GK.clip, tot.clip);
        if (STATE.roof.type === 'hip') {
          /* the corner pieces where two runs share an eave point */
          var nC = 0, i9, j9, mCorner = new T.MeshStandardMaterial({ color: H.metal, roughness: 0.4, metalness: 0.7, side: T.DoubleSide });
          for (i9 = 0; i9 < _gutCorners.length; i9++) for (j9 = i9 + 1; j9 < _gutCorners.length; j9++) {
            var A = _gutCorners[i9], Bq = _gutCorners[j9], pairs = [[A.b, Bq.a], [A.a, Bq.b], [A.a, Bq.a], [A.b, Bq.b]], pq;
            for (pq = 0; pq < pairs.length; pq++) {
              if (Math.hypot(pairs[pq][0][0] - pairs[pq][1][0], pairs[pq][0][1] - pairs[pq][1][1]) < 0.03) {
                g.add(_rsgCorner(T, pairs[pq][0], A.n, Bq.n, A.yBase, mCorner));
                nC++;
                break;
              }
            }
          }
          H.add(GK.corner, nC || 4);
          ctx.log('GUT  hip: ' + (nC || 4) + ' x ' + GK.corner + ' external corner(s) made from the K section (two mitred wedges each), runs stop on the corner; ' + tot.caps + ' x ' + GK.cap + ' stop end(s) where a run ends at a gable');
        }
      }

      _why(ctx, 'gutter   outlet is a tapered collar in the trough FLOOR -- ' +
              Math.round(86) + ' mm at the top down to ' + Math.round(Math.max(GK_DSP.w, GK_DSP.d) * 1000) +
              ' at the pipe, open, and inside the profile rather than a box slung under it');
      if (tot.endWhy) {
        _why(ctx, 'gutter   outlet put at that end because ' + tot.endWhy +
                ' -- no point generating a long swan neck when the other end of the same ' +
                'gutter needs a short one');
      }
      if (tot.cornerIn) {
        _why(ctx, 'gutter   it comes down a WALL, not a corner -- set ' +
                fmtLen(tot.cornerIn) + ' in from the arris along the face this eave ' +
                'looks down, so the bracket has flat wall behind it and the corner trim ' +
                'is clear');
      }
      _why(ctx, 'gutter   pipe stands ' + fmtLen(tot.stand || 0) + ' off the frame line -- ' +
              'facade ' + fmtLen(H.facadeThk()) + ' + standoff ' + fmtLen(GK.standoff) +
              ' + half the section, so it still clears once the cladding is on rather ' +
              'than being swallowed by it');
      _why(ctx, 'gutter   the pipe is ONE swept run with arcs at the bends, not prisms ' +
              'butted end to end -- two prisms meeting at an angle never close, which is ' +
              'the daylight that was showing on the outside of every elbow');
      _why(ctx, 'gutter   ' + tot.clip + ' bracket(s) at 2 m max, the top one just under the ' +
              'lower bend -- a strap round the section with a leg back to what it hangs on, ' +
              'not a box at the centre line. That top fixing is what a downspout comes off ' +
              'without');
      _why(ctx, 'gutter   pipe comes down the ' + (tot.tgt || '?') +
              (tot.neckOff ? (' -- swan neck offsetting ' + fmtLen(tot.neckOff) +
                              ', in TWO axes where the target is a corner rather than square ' +
                              'to the eave' +
                              (tot.neckLong ? '; long enough for a bend, a cut length and ' +
                                              'another bend' : '; short, so two bends and ' +
                                              'nothing between'))
                           : ' -- straight down, no offset needed'));
      _why(ctx, 'gutter   downspout ' + Math.round(GK_DSP.w * 1000) + 'x' + Math.round(GK_DSP.d * 1000) +
              ' with ' + Math.round(GK_DSP.r * 1000) + ' mm corners, hollow at ' +
              Math.round(GK_DSP.wall * 10000) / 10 + ' mm wall -- the American rectangular ' +
              'section, holding what the catalogue dia 80 holds, on the same row');
      _why(ctx, 'gutter   hung on HIDDEN HANGERS at ' + fmtLen(H.grid()) + ' -- a flat bar ' +
              'inside the trough from back lip to front, screwed through a tab at the back, ' +
              'on the rafter spacing. Same catalogue row whichever profile the buyer fits');
      _why(ctx, 'gutter   hangs OUTBOARD of the fascia -- the section grows out from it, ' +
              'which it was doing backwards, so the trough sat tucked under the roof');
      _why(ctx, 'gutter   it hangs off the DECK PLANE eave -- the same corners the deck and the ' +
              'flashings use. With its own arithmetic it rose with the pitch without ' +
              'travelling out, and near enough is invisible at 2 degrees');
      _why(ctx, 'gutter   its top is set off the drip edge AS DRAWN, which is what feeds it -- ' +
              'the drip\'s face laps into the trough where it stands over it, the trough tucks ' +
              'under the drip\'s kick where the face is at the fascia (core-v760). Off the ' +
              'soffit it hung a rafter too low; lifted by the whole deck it stood proud of ' +
              'the roof and the water would have had to climb in');
      _why(ctx, 'gutter   K section drawn as a SHEET 1.5 mm thick, not a filled lump -- ' +
              'so the trough is open and anything hung inside it can be seen. Billed as ' +
              'the generic gutter row: K and half-round are one product to a bill');
      _why(ctx, 'gutter   ' + Math.round(tot.run * 10) / 10 + ' m on ' + tot.brk +
              ' bracket(s) at ' + fmtLen(GK.bracket) + ', ' + tot.caps + ' stop end(s), ' +
              tot.hop + ' hopper(s)');
      _why(ctx, 'gutter   ' + Math.round(tot.pipe * 10) / 10 + ' m of downspout, ' + tot.elb +
              ' elbow(s), ' + tot.shoe + ' shoe(s), ' + tot.clip + ' clip(s)');
      _why(ctx, 'gutter   it HOLDS the published eave line -- ' + planes.length +
              ' plane(s) asked, never re-derived from pitch and overhang, so dragging ' +
              'the roof moves the trough with it and the two cannot drift apart');
    }
    function roofSkin(ctx, spec) {
      spec = spec || {};
      var out = { built: [], area: 0, pending: [] }, planes = spec.planes || [];
      var order = ['ceiling', 'deck', 'insulation', 'underlay', 'battens', 'edges', 'covering', 'ridge', 'gutters'], q;
      var only = spec.only || null;
      var wants = function (k) { return !!spec[k] && (!only || only.indexOf(k) >= 0); };
      if (!spec.layer || typeof spec.lift !== 'function') {
        if (ctx && ctx.log) ctx.log('skin     drawer ROOF SKIN: nothing built -- the spec needs a layer and a lift(plane) function');
        return out;
      }
      for (q = 0; q < order.length; q++) {
        var k = order[q];
        if (!wants(k)) continue;
        try {
          if (k === 'deck') { var rD = _rsDeck(ctx, spec, planes); out.built.push('deck'); out.area = Math.max(out.area, rD.area || 0); }
          else if (k === 'ceiling') { _rsCeiling(ctx, spec, planes); out.built.push('ceiling'); }
          else if (k === 'insulation') { var rI = _rsInsulation(ctx, spec, planes); out.built.push('insulation'); }
          else if (k === 'underlay') { var rU = _rsUnderlay(ctx, spec, planes); out.built.push('underlay'); out.area = Math.max(out.area, rU.area || 0); }
          else if (k === 'battens') { _rsBattens(ctx, spec, planes); out.built.push('battens'); }
          else if (k === 'covering') { var rC = _rsCovering(ctx, spec, planes); out.built.push('covering'); out.area = Math.max(out.area, rC.area || 0); }
          else if (k === 'edges') { _rsEdges(ctx, spec, planes); out.built.push('edges'); }
          else if (k === 'ridge') { _rsRidge(ctx, spec, planes); out.built.push('ridge'); }
          else if (k === 'gutters') { _rsGutters(ctx, spec, planes); out.built.push('gutters'); }
          else { out.pending.push(k); }
        } catch (e) { if (ctx && ctx.log) ctx.log('skin     drawer ' + k + ' THREW ' + (e && e.message)); }
      }
      if (out.pending.length && ctx && ctx.log) ctx.log('skin     drawer ROOF SKIN: not in the drawer yet: ' + out.pending.join(', ') + ' (K7)');
      return out;
    }
    /* -------------------------------------------------------------------------
       THE STAIRS DRAWER (core-v465, A2 -- the A-frame is its first user, the
       T house calls the same thing). One straight flight from one floor to
       the next, in the floor opening the product keeps. The drawer works the
       flight out itself: the risers from the rise, the going from the
       2R + G = 630 rule held between the limits of the kind, and the top step
       lands on the edge of the opening the flight climbs towards. It then
       says whether the opening is long enough for the headroom, and whether
       the foot of the flight has its landing before the room ends.

       THE FLIGHT IS BILLED AS A KIT (core-v466, Marek: schodiste budeme
       vykazovat jako komplet, ne jako material). One catalogue item per
       flight, picked by the rise; the stringers, treads, the flight's rail
       and the tread screws are in it and are drawn but never billed apart,
       or they would be paid twice. The guard round the opening upstairs is
       NOT part of the kit and is billed on its own by the metre: the
       opening's perimeter minus the side the flight arrives at. A rise over
       the last band is an ERROR in the log and nothing is billed -- no item
       is made up for it.

         straight   rise <= 2200 SIS22060 | <= 2400 SIS24070 | <= 2800 SIS28090 | <= 3000 SIS30090 | <= 3200 SIS32090
         (the flight is drawn at the kit's own width: 600 / 700 / 900)
         loft       rise <= 2800 SIS28070
         guard      SIS00001 by the metre

       THE TWO KINDS. 'straight' is the ordinary stair: risers 150 to 190,
       goings 254 to 300, headroom 2032 (6 ft 8 in). 'loft' is the steeper
       loft stair a cabin uses where an ordinary flight would need an opening
       longer than the loft: risers up to 220, goings 200 to 240, headroom
       1900 -- a shorter run and a shorter opening. The log says which values
       were used.

         API.stairPlan({ rise, kind?, riser?, going?, rule?, headroom?, floorZone? })
         -> { n, R, G, run, needHole, headroom, kind }   the arithmetic alone, for
                                           a product that has to place the opening first
         API.stairKit(kind, rise) -> { code, band } | { error }

         API.stairs(ctx, {
           layer:     'aframe-stairs'
           kind:      'straight' | 'loft'
           hole:      { x0, z0, x1, z1 }     the floor opening
           y0, y1:    the floor the flight stands on, the floor it arrives at
           up:        '+z' | '-z' | '+x' | '-x'   the way the flight climbs; the
                                             top step is on that edge of the hole
           width:     m                      over the stringers
           floorZone: m                      the upper floor's structure, for headroom
           room:      { x0, z0, x1, z1 } | null   where the foot must stay inside
           landing:   m                      free floor wanted in front of the foot (0.90)
           riser, going, headroom            override the kind's own values
           guard:     { h } | false          the guard round the opening (900 high)
           color, railColor
           mark:      function (key, dir, label, code) | null
           tag:       'flight 1' -- how the log names this flight
         })
         -> { built: [...], n, R, G, run, needHole, holeLen, headroomOk, footOk, kit, guardLen }
       ------------------------------------------------------------------------- */
    /* THE SHAPES (core-v470, K4): 'straight' is one flight; 'L' is two
       flights at a right angle round a corner square -- a landing (lLanding)
       or two winder treads (lWinder); 'spiral' is treads round a post in a
       square of the kit's diameter. The L kinds keep the ordinary stair's
       risers and goings. */
    var STAIR_KINDS = {
      straight: { riser: { min: 0.15, max: 0.19 }, going: { min: 0.254, max: 0.30 }, headroom: 2.032, rule: 0.63, shape: 'straight' },
      loft:     { riser: { min: 0.15, max: 0.22 }, going: { min: 0.20,  max: 0.24 }, headroom: 1.90,  rule: 0.63, shape: 'straight' },
      lLanding: { riser: { min: 0.15, max: 0.19 }, going: { min: 0.254, max: 0.30 }, headroom: 2.032, rule: 0.63, shape: 'L', landing: true },
      lWinder:  { riser: { min: 0.15, max: 0.19 }, going: { min: 0.254, max: 0.30 }, headroom: 2.032, rule: 0.63, shape: 'L', landing: false },
      spiral:   { riser: { min: 0.17, max: 0.24 }, going: { min: 0.20,  max: 0.30 }, headroom: 1.98,  rule: 0.63, shape: 'spiral' }
    };
    /* THE KITS AS THE CATALOGUE HAS THEM (core-v467, from the T house
       request): the band is the storey height the kit reaches, the width is
       the kit's own and the flight is drawn at it. 2450 was 2400 in the
       catalogue. L-shaped and spiral kits are in the table for billing; the
       drawer draws the straight and loft flight only, until the stairs
       become a fitout piece. */
    /* A KIT SERVES ITS CATALOGUE HEIGHT PLUS OR MINUS 100 (core-v469, Marek:
       schodiste 2800 pro vysku patra 2700 - 2900, 3000 pro 2900 - 3100 atd.):
       the nearest kit within STAIR_TOL of the rise is the one billed; a rise
       with no kit that close is a BILL ERROR, nothing made up. max stays the
       name of the catalogue height. */
    var STAIR_TOL = 0.10;
    var STAIR_KITS = {
      straight: [ { max: 2.20, code: 'SIS22060', w: 0.60 }, { max: 2.40, code: 'SIS24070', w: 0.70 }, { max: 2.80, code: 'SIS28090', w: 0.90 },
                  { max: 3.00, code: 'SIS30090', w: 0.90 }, { max: 3.20, code: 'SIS32090', w: 0.90 } ],
      /* core-v771 (T house thread, Marek 2026-10-29: zapsat oba): the 3200
         spiral and loft kits, rows SIS32016 / SIS32070 added to the catalogue
         as copies of their 2800 siblings */
      loft:     [ { max: 2.80, code: 'SIS28070', w: 0.70 }, { max: 3.20, code: 'SIS32070', w: 0.70 } ],
      lLanding: [ { max: 2.80, code: 'SIS28091', w: 0.90 } ],
      lWinder:  [ { max: 2.80, code: 'SIS28092', w: 0.90 } ],
      spiral:   [ { max: 2.80, code: 'SIS28016', d: 1.60 }, { max: 3.20, code: 'SIS32016', d: 1.60 } ],
      guard:    'SIS00001'
    };
    function stairKit(kind, rise) {
      var B = STAIR_KITS[kind] || STAIR_KITS.straight, i, best = null;
      for (i = 0; i < B.length; i++) {
        var d = Math.abs(rise - B[i].max);
        if (d <= STAIR_TOL + 1e-6 && (!best || d < Math.abs(rise - best.max))) best = B[i];
      }
      if (best) return { code: best.code, band: best.max, w: best.w || 0, d: best.d || 0, tol: STAIR_TOL };
      var have = [], j;
      for (j = 0; j < B.length; j++) have.push(Math.round(B[j].max * 1000));
      return { error: 'rise ' + Math.round(rise * 1000) + ' has no ' + (STAIR_KITS[kind] ? kind : 'straight') + ' kit within ' + Math.round(STAIR_TOL * 1000) + ' (kits ' + have.join(', ') + ')' };
    }
    function stairPlan(o) {
      o = o || {};
      var K = STAIR_KINDS[o.kind] || STAIR_KINDS.straight;
      var H = o.rise || 0, RL = o.riser || K.riser, GL = o.going || K.going;
      var rMin = RL.min, rMax = RL.max, gMin = GL.min, gMax = GL.max;
      var rule = o.rule || K.rule, head = o.headroom || K.headroom, fz = o.floorZone || 0;
      var kind = STAIR_KINDS[o.kind] ? o.kind : 'straight';
      if (!(H > 0.2)) return { n: 0, R: 0, G: 0, run: 0, needHole: 0, headroom: head, kind: kind };
      var n = Math.max(2, Math.ceil(H / rMax - 1e-9)), R = H / n;
      if (R < rMin && n > 2) { n = Math.max(2, Math.floor(H / rMin)); R = H / n; }
      var G = Math.max(gMin, Math.min(gMax, rule - 2 * R));
      var run = (n - 1) * G;
      /* the opening has to reach back from the top edge until the nosing line
         is a headroom plus the floor structure below the upper floor's top */
      var needHole = Math.min(run + G, (head + fz) * G / R);
      /* THE FOOTPRINT (core-v470): fw along the climb, fd across it, measured
         from the top edge back; axisOff is where the arriving flight's axis
         sits across the footprint, from its middle, for turn +1 */
      var KT = stairKit(kind, H), W = (KT && KT.w > 0) ? KT.w : (o.width || 0.90), shape = K.shape || 'straight';
      var outP = { n: n, R: R, G: G, run: run, needHole: needHole, headroom: head, kind: kind, shape: shape, W: W, fw: n * G, fd: W, axisOff: 0 };
      if (shape === 'L') {
        var m2 = K.landing ? Math.ceil(n / 2) : Math.ceil((n - 1) / 2), m1 = K.landing ? (n - m2) : (n - 1 - m2);
        outP.m1 = m1; outP.m2 = m2;
        outP.fw = (m2 - 1) * G + W;
        outP.fd = W + Math.max(0, m1 - 1) * G;
        outP.axisOff = -(outP.fd - W) / 2;
        outP.run = (m2 - 1) * G + (m1 - 1) * G;
        outP.needHole = Math.min(outP.fw, needHole);
      } else if (shape === 'spiral') {
        var D = (KT && KT.d > 0) ? KT.d : 1.60;
        outP.D = D; outP.fw = D; outP.fd = D; outP.W = D / 2;
        outP.step = Math.min(Math.PI / 6, (Math.PI * 11 / 6) / Math.max(1, n - 1));
        outP.needHole = D; outP.run = 0;
      }
      return outP;
    }
    /* which world direction a flight's local across (+v) points for each
       way it climbs -- a product needs it to put an L's lower flight on the
       side it wants (turn +1 is +v) */
    function stairAxes(up) {
      var U = { '+x': [1, 0], '-x': [-1, 0], '+z': [0, 1], '-z': [0, -1] }[up] || [0, 1];
      var V = { '+x': [0, 1], '-x': [0, -1], '+z': [-1, 0], '-z': [1, 0] }[up] || [-1, 0];
      return { u: U, v: V };
    }
    /* L AND SPIRAL (core-v470, K4). Built in a frame at the top edge of the
       opening: local +x is the climb, the top riser on x = 0, everything at
       x < 0; local +v (z) across, the L's lower flight on the +v side for
       turn +1. Stringers under every flight, treads, a landing on four posts
       or two winder treads on two, handrails on both sides of each flight;
       the spiral a post, pie treads, a baluster on each and a helical rail. */
    function _stairsShaped(ctx, spec, P, o) {
      var T = ctx.THREE, H = (spec.y1 || 0) - (spec.y0 || 0), n = P.n, R = P.R, G = P.G, W = o.W;
      var t = (spec.turn === -1) ? -1 : 1, mW = o.mW, mR = o.mR;
      var FL = new T.Group();
      var ax = o.alongX ? o.sgn : 0, az = o.alongX ? 0 : o.sgn;
      FL.rotation.y = Math.atan2(-az, ax);
      var bx = function (m, w, h, d, x, y, z, rx, rz) {
        var b = new T.Mesh(new T.BoxGeometry(w, h, d), m);
        b.position.set(x, y, z); if (rx) b.rotation.x = rx; if (rz) b.rotation.z = rz;
        FL.add(b); return b;
      };
      var ST = { t: 0.060, d: 0.240 }, TR = { t: 0.040, nose: 0.025 }, tw = Math.max(0.3, W - 2 * ST.t), rh = 0.90, q, i;
      if (P.shape === 'L') {
        if (o.alongX) FL.position.set(o.e1, spec.y0, o.mid); else FL.position.set(o.mid, spec.y0, o.e1);
        var landing = !!(STAIR_KINDS[P.kind] || {}).landing, m1 = P.m1, m2 = P.m2;
        var hL = landing ? m1 * R : (m1 + 1) * R, xs = -(m2 - 1) * G, al = Math.atan2(R, G);
        /* the upper flight, along x, into the opening */
        for (i = 1; i <= m2 - 1; i++) {
          var a = xs + (i - 1) * G - TR.nose, b = xs + i * G;
          bx(mW, b - a, TR.t, tw, (a + b) / 2, hL + i * R - TR.t / 2, 0);
        }
        var uL = Math.hypot(-xs, H - hL);
        for (q = -1; q <= 1; q += 2) {
          bx(mW, uL, ST.d, ST.t, xs / 2, (hL + H) / 2 - ST.d / 2 + 0.03, q * (W / 2 - ST.t / 2), 0, al);
          bx(mR, uL, 0.045, 0.045, xs / 2, (hL + H) / 2 + rh, q * (W / 2 + 0.03), 0, al);
        }
        /* the corner: a landing on four posts, or two winders on two */
        var cx0 = xs - W, cx1 = xs, v0 = -W / 2 * t, v1 = W / 2 * t;
        if (landing) {
          bx(mW, W, 0.04, W, xs - W / 2, hL - 0.02, 0);
          var px = [cx0 + 0.05, cx1 - 0.05], pv = [-W / 2 + 0.05, W / 2 - 0.05], a1, b1;
          for (a1 = 0; a1 < 2; a1++) for (b1 = 0; b1 < 2; b1++) bx(mW, 0.09, hL - 0.04, 0.09, px[a1], (hL - 0.04) / 2, pv[b1]);
        } else {
          var mW2 = mW.clone(); mW2.side = T.DoubleSide;
          var tri = function (pts, lvl) {
            var sh = new T.Shape(), k2;
            sh.moveTo(pts[0][0], pts[0][1]);
            for (k2 = 1; k2 < pts.length; k2++) sh.lineTo(pts[k2][0], pts[k2][1]);
            var geo = new T.ExtrudeGeometry(sh, { depth: TR.t, bevelEnabled: false });
            geo.rotateX(Math.PI / 2);
            var me = new T.Mesh(geo, mW2); me.position.y = lvl; FL.add(me); return me;
          };
          tri([[cx1, v1], [cx0, v1], [cx0, v0]], m1 * R);
          tri([[cx1, v1], [cx0, v0], [cx1, v0]], (m1 + 1) * R);
          bx(mW, 0.09, m1 * R - 0.04, 0.09, cx0 + 0.05, (m1 * R - 0.04) / 2, v1 - 0.05 * t);
          bx(mW, 0.09, (m1 + 1) * R - 0.04, 0.09, cx1 - 0.05, ((m1 + 1) * R - 0.04) / 2, v0 + 0.05 * t);
        }
        /* the lower flight, across, climbing toward the corner */
        var xm = xs - W / 2;
        for (i = 1; i <= m1 - 1; i++) {
          var va = W / 2 + (m1 - i - 1) * G, vb = W / 2 + (m1 - i) * G + TR.nose;
          bx(mW, tw, TR.t, vb - va, xm, i * R - TR.t / 2, t * (va + vb) / 2);
        }
        if (m1 >= 1) {
          var lL = Math.hypot(m1 * G, m1 * R), vmid = t * (W / 2 + m1 * G / 2);
          for (q = -1; q <= 1; q += 2) {
            bx(mW, ST.t, ST.d, lL, xm + q * (W / 2 - ST.t / 2), m1 * R / 2 - ST.d / 2 + 0.03, vmid, t * al);
            bx(mR, 0.045, 0.045, lL, xm + q * (W / 2 + 0.03), m1 * R / 2 + rh, vmid, t * al);
          }
        }
        o.out.built.push('upper flight', landing ? 'landing' : 'winders', 'lower flight', 'rails');
      } else {
        /* the spiral: its centre half a diameter back from the top edge */
        var D = P.D, rO = D / 2, step = P.step, cA = o.e1 - o.sgn * rO;
        if (o.alongX) FL.position.set(cA, spec.y0, o.mid); else FL.position.set(o.mid, spec.y0, cA);
        var post = new T.Mesh(new T.CylinderGeometry(0.05, 0.05, H + rh, 16), mR);
        post.position.y = (H + rh) / 2; FL.add(post);
        var pts = [];
        for (i = 1; i <= n - 1; i++) {
          /* the last tread ends facing the top edge (local +x), the others
             wind back from it */
          var th1 = Math.PI / 2 - (n - 1 - i) * step * t, th0 = th1 - step * 1.08 * t;
          var ts = Math.min(th0, th1), tl = Math.abs(th1 - th0);
          var tr = new T.Mesh(new T.CylinderGeometry(rO, rO, TR.t, 24, 1, false, ts, tl), mW);
          tr.position.y = i * R - TR.t / 2; FL.add(tr);
          var tm = (th0 + th1) / 2, bxp = Math.sin(tm) * (rO - 0.04), bzp = Math.cos(tm) * (rO - 0.04);
          var bal = new T.Mesh(new T.CylinderGeometry(0.012, 0.012, rh, 8), mR);
          bal.position.set(bxp, i * R + rh / 2, bzp); FL.add(bal);
          pts.push(new T.Vector3(bxp, i * R + rh, bzp));
        }
        if (pts.length > 1 && T.CatmullRomCurve3 && T.TubeGeometry) {
          var rail = new T.Mesh(new T.TubeGeometry(new T.CatmullRomCurve3(pts), pts.length * 6, 0.022, 8, false), mR);
          FL.add(rail);
        }
        o.out.built.push('post', 'treads', 'balusters', 'rail');
      }
      o.g.add(FL);
      return FL;
    }
    function stairs(ctx, spec) {
      spec = spec || {};
      var out = { built: [], n: 0, R: 0, G: 0, run: 0, needHole: 0, holeLen: 0, headroomOk: false, footOk: false, kit: null, guardLen: 0 };
      var tag = spec.tag || 'flight';
      var L = function (s) { try { if (ctx && ctx.log) ctx.log('stairs   ' + tag + ': ' + s); } catch (e) {} };
      var hole = spec.hole, layer = spec.layer;
      if (!ctx || !ctx.THREE || !ctx.group || !layer) { L('nothing built -- the spec needs a layer, and ctx a THREE and a group'); return out; }
      if (!hole) { L('nothing built -- no opening given'); return out; }
      var H = (spec.y1 || 0) - (spec.y0 || 0);
      if (!(H > 0.2)) { L('nothing built -- the rise ' + Math.round(H * 1000) + ' is not a storey'); return out; }
      var up = spec.up || '+z', alongX = (up === '+x' || up === '-x'), sgn = (up.charAt(0) === '-') ? -1 : 1;
      var P = stairPlan({ rise: H, kind: spec.kind, riser: spec.riser, going: spec.going, rule: spec.rule, headroom: spec.headroom, floorZone: spec.floorZone });
      var KW = stairKit(P.kind, H), n = P.n, R = P.R, G = P.G, W = (KW.w > 0) ? KW.w : (spec.width || 0.90);
      out.n = n; out.R = R; out.G = G; out.run = P.run; out.needHole = P.needHole;
      /* the frame of the flight: u along the climb, v across it, from the
         point where the nosing line meets the lower floor */
      var e1 = alongX ? (sgn > 0 ? hole.x1 : hole.x0) : (sgn > 0 ? hole.z1 : hole.z0);
      var mid = alongX ? (hole.z0 + hole.z1) / 2 : (hole.x0 + hole.x1) / 2;
      var holeLen = alongX ? (hole.x1 - hole.x0) : (hole.z1 - hole.z0);
      var holeW = alongX ? (hole.z1 - hole.z0) : (hole.x1 - hole.x0);
      out.holeLen = holeLen;
      var u0 = e1 - sgn * n * G;                                /* nosing line at the lower floor */
      var foot = e1 - sgn * (n - 1) * G;                        /* the first riser */
      var T = ctx.THREE, g = ctx.group;
      var F = new T.Group();
      if (alongX) F.position.set(u0, spec.y0, mid); else F.position.set(mid, spec.y0, u0);
      /* local +x is the climb: turned about y so it points along 'up' */
      var ax = alongX ? sgn : 0, az = alongX ? 0 : sgn;
      F.rotation.y = Math.atan2(-az, ax);
      g.add(F);
      var colW = (spec.color != null) ? spec.color : 0xC9A87A, colR = (spec.railColor != null) ? spec.railColor : 0x6B5A45;
      var mW = new T.MeshStandardMaterial({ color: colW, roughness: 0.8, metalness: 0 });
      var mR = new T.MeshStandardMaterial({ color: colR, roughness: 0.6, metalness: 0.1 });
      var boxIn = function (grp, m, w, h, d, x, y, z, rz) {
        var b = new T.Mesh(new T.BoxGeometry(w, h, d), m);
        b.position.set(x, y, z);
        if (rz) b.rotation.z = rz;
        grp.add(b);
        return b;
      };
      var box = function (m, w, h, d, x, y, z, rz) { return boxIn(F, m, w, h, d, x, y, z, rz); };
      var ST = { t: 0.060, d: 0.240 }, TR = { t: 0.040, nose: 0.025 };
      var th = Math.atan2(R, G), sn = Math.sin(th), cs = Math.cos(th);
      var mark = function (k, d, l, c) { try { if (spec.mark) spec.mark(k, d, l, c); } catch (e) {} };
      var KIT = stairKit(P.kind, H);
      out.kit = KIT;
      mark('stairs', 'side', 'Stairs', KIT.code || '');
      var nT = n - 1, k, i;
      if (P.shape === 'L' || P.shape === 'spiral') {
        _stairsShaped(ctx, spec, P, { e1: e1, mid: mid, alongX: alongX, sgn: sgn, W: W, mW: mW, mR: mR, g: g, out: out, L: L });
      } else {
      /* the two stringers, their top edge 30 over the nosing line */
      /* its top end is cut back so it stops at the opening's edge and does
         not run into the trimmer of the floor above */
      var Lf0 = Math.hypot(n * G, n * R), cut = ST.d * sn, Lf = Lf0 - cut, off = 0.030 - ST.d / 2;
      var vS = W / 2 - ST.t / 2, su = n * G / 2 - cs * cut / 2, sy = n * R / 2 - sn * cut / 2;
      for (k = -1; k <= 1; k += 2) box(mW, Lf, ST.d, ST.t, su - sn * off, sy + cs * off, k * vS, th);
      /* the treads between them, each from its nosing back to the next riser */
      var tw = Math.max(0.3, W - 2 * ST.t);
      for (i = 1; i <= nT; i++) {
        /* a least tread depth (core-v513): a steep, ladder-like flight's
           treads overlap instead of being thin strips */
        var b = (i + 1) * G, a = b - Math.max(G + TR.nose, spec.treadMin || 0);
        box(mW, b - a, TR.t, tw, (a + b) / 2, i * R - TR.t / 2, 0, 0);
      }
      /* the flight's own handrail, both sides, 900 over the nosing line */
      var railLen = Math.hypot((n - 1) * G, (n - 1) * R), rh = 0.90;
      for (k = -1; k <= 1; k += 2) box(mR, railLen, 0.045, 0.045, G + (n - 1) * G / 2, R + (n - 1) * R / 2 + rh, k * (W / 2 + 0.03), th);
      out.built.push('stringers', 'treads', 'rail');
      }
      /* THE GUARD ROUND THE OPENING UPSTAIRS, on every side but the one the
         flight arrives at: posts at the corners and at most 1200 apart, a top
         rail at 900 and a bottom rail at 100, on the upper floor */
      var guardLen = 0;
      if (spec.guard !== false) {
        var gh = (spec.guard && spec.guard.h) || 0.90, yF = spec.y1, pz = 0.07, GR = new T.Group();
        g.add(GR);
        var edges = [], x0 = hole.x0, x1 = hole.x1, z0 = hole.z0, z1 = hole.z1;
        if (alongX) {
          edges.push([[x0, z0], [x1, z0]], [[x0, z1], [x1, z1]]);
          edges.push(sgn > 0 ? [[x0, z0], [x0, z1]] : [[x1, z0], [x1, z1]]);
        } else {
          edges.push([[x0, z0], [x0, z1]], [[x1, z0], [x1, z1]]);
          edges.push(sgn > 0 ? [[x0, z0], [x1, z0]] : [[x0, z1], [x1, z1]]);
        }
        var ei;
        for (ei = 0; ei < edges.length; ei++) {
          var A = edges[ei][0], Bq = edges[ei][1], len = Math.hypot(Bq[0] - A[0], Bq[1] - A[1]);
          if (len < 0.05) continue;
          guardLen += len;
          var ry = Math.atan2(-(Bq[1] - A[1]), Bq[0] - A[0]), cxE = (A[0] + Bq[0]) / 2, czE = (A[1] + Bq[1]) / 2;
          var r1 = boxIn(GR, mR, len, 0.045, 0.045, cxE, yF + gh, czE, 0); r1.rotation.y = ry;
          var r2 = boxIn(GR, mR, len, 0.030, 0.030, cxE, yF + 0.10, czE, 0); r2.rotation.y = ry;
          var np = Math.max(1, Math.ceil(len / 1.20)), pi;
          for (pi = 0; pi <= np; pi++) {
            var t = pi / np;
            boxIn(GR, mR, pz, gh, pz, A[0] + (Bq[0] - A[0]) * t, yF + gh / 2, A[1] + (Bq[1] - A[1]) * t, 0);
          }
        }
        out.built.push('guard');
      }
      out.guardLen = guardLen;
      /* THE BILL: one kit per flight, the guard by the metre, nothing else */
      var mm = function (v) { return Math.round(v * 1000); };
      if (KIT.error) {
        _log('BILL  ERROR stairs ' + tag + ': ' + KIT.error + ' -- no kit item for it, nothing billed');
      } else {
        billAdd(layer, KIT.code, 1);
        _log('BILL  stairs ' + KIT.code + ' pcs 1 (' + tag + ', ' + P.kind + ', rise ' + mm(H) + ' -> kit ' + mm(KIT.band) + ' +-' + mm(STAIR_TOL) + ')' + (CATALOG[KIT.code] ? '' : ' -- NO CATALOGUE ROW in this product'));
      }
      if (guardLen > 0) {
        billAdd(layer, STAIR_KITS.guard, guardLen);
        _log('BILL  railing ' + STAIR_KITS.guard + ' m ' + (Math.round(guardLen * 10) / 10) + ' (' + tag + ', opening perimeter ' + (Math.round(2 * (holeLen + holeW) * 10) / 10) + ' minus flight side ' + (Math.round(holeW * 10) / 10) + ')' + (CATALOG[STAIR_KITS.guard] ? '' : ' -- NO CATALOGUE ROW in this product'));
      }
      /* THE CHECKS, said every build */
      out.headroomOk = holeLen >= P.needHole - 0.005;
      var foot0 = foot - sgn * (spec.landing || 0.90), footOk = true, roomEdge = null;
      if (P.shape !== 'straight') { foot = e1 - sgn * P.fw; foot0 = foot; }
      if (spec.room && P.shape !== 'spiral') {
        roomEdge = alongX ? (sgn > 0 ? spec.room.x0 : spec.room.x1) : (sgn > 0 ? spec.room.z0 : spec.room.z1);
        footOk = (sgn > 0) ? (foot0 >= roomEdge - 0.005) : (foot0 <= roomEdge + 0.005);
      }
      out.footOk = footOk;
      if (P.shape === 'L') L(P.kind + ': lower flight ' + P.m1 + ' risers, ' + (P.landing === false ? '' : '') + ((STAIR_KINDS[P.kind] || {}).landing ? 'a landing ' + mm(W) + ' square' : 'two winder treads in the corner') + ', upper flight ' + P.m2 + ' risers; footprint ' + mm(P.fw) + ' along x ' + mm(P.fd) + ' across, lower flight on the ' + ((spec.turn === -1) ? '-v' : '+v') + ' side');
      if (P.shape === 'spiral') L('spiral: diameter ' + mm(P.D) + ', ' + nT + ' treads of ' + Math.round(P.step * 180 / Math.PI) + ' deg round a post, arriving at the opening edge');
      L(P.kind + ', rise ' + mm(H) + ' (' + mm(spec.y0) + ' -> ' + mm(spec.y1) + ') in ' + n + ' risers of ' + mm(R) + ', going ' + mm(G) +
        ' (2R+G ' + mm(2 * R + G) + ', pitch ' + Math.round(th * 180 / Math.PI) + ' deg), ' + nT + ' treads, run ' + mm(P.run) + ', width ' + mm(W) + ', climbs ' + up + ' to the opening edge at ' + mm(e1));
      L('headroom ' + mm(P.headroom) + ' needs the opening ' + mm(P.needHole) + ' long along the flight -- it is ' + mm(holeLen) + (out.headroomOk ? ' OK' : (' SHORT by ' + mm(P.needHole - holeLen))));
      L('foot (first riser) at ' + mm(foot) + ', landing ' + mm(spec.landing || 0.90) + (roomEdge == null ? ' -- no room edge given' : (' to the room edge at ' + mm(roomEdge) + (footOk ? ' OK' : (' SHORT by ' + mm(Math.abs(foot0 - roomEdge)) + ' -- the flight runs into the wall')))));
      return out;
    }
    /* -------------------------------------------------------------------------
       POINT FOOTINGS WITH THE TIMBER FLOOR ON THEM (core-v467, A3 -- the
       A-frame is the first product to call it). COPIED FROM THE ADU
       (adu-v1396, _buildFoundation's point branch and the structural part of
       _buildFloor), not designed again: the ground screw with its taper,
       flight and collar; the precast footing as a truncated pyramid on a
       stone bed; the poured pier as ONE 400 x 400 column 600 deep at least
       (frost + 150), four bars, stone and geotextile under it; a galvanised
       saddle on every support with the rim between its cheeks; the supports
       from bearingPoints() at 1830 at most. On them the rim all round, a
       doubled centre beam down the long axis, joists at the grid across it,
       full-depth blocking about every 1.6 m, and the 18 mm OSB deck in
       staggered sheets. Adapted: the codes are the product's catalogue keys
       as the ADU names them, the frame has no build-up layers under the deck,
       and the frame colour is the product's. Everything below grade is drawn
       only on the Roof & Base step, as in the ADU.

         API.pointBase(ctx, {
           layer:    'aframe-base'
           kind:     'screws' | 'pads' | 'piers'
           deckTop:  m                the top of the deck (STACK.slab)
           joist:    { t, d, code }   60 x 240 KVH60240 by default
           deckT:    0.018
           spacing:  1.83             the most between supports
           wood:     colour
         }) -> { points, screws, pads, piers, saddles, concrete, gravel, excav, joists }
       ------------------------------------------------------------------------- */
    var PB_FOOT = { padW: 0.40, padH: 0.20, pierW: 0.40, screwR: 0.051, screwLen: 1.50, gravel: 0.15 };
    var PB_SADDLE = { plate: 0.006, cheek: 0.15, t: 0.005 };
    var PB_LAYERS = { geo: { t: 0.002, colour: 0x6E7B6B }, stone: { colour: 0x8C8377 } };
    var _pbStoneTex = null;
    function _pbStoneMat(T) {
      if (!_pbStoneTex) {
        try {
          var n = 256, cv = global.document.createElement('canvas');
          cv.width = n; cv.height = n;
          var c = cv.getContext('2d');
          c.fillStyle = '#6F6A62'; c.fillRect(0, 0, n, n);
          var cols = ['#9C958A', '#8A8378', '#B0A99C', '#7D776E'], k;
          var seed = 12345;
          var rnd = function () { seed = (seed * 1103515245 + 12345) & 0x7fffffff; return seed / 0x7fffffff; };
          for (k = 0; k < 900; k++) {
            var rx = rnd() * n, ry = rnd() * n, rr = 3 + rnd() * 7;
            c.fillStyle = cols[k % cols.length];
            c.beginPath();
            c.ellipse(rx, ry, rr, rr * (0.6 + rnd() * 0.5), rnd() * Math.PI, 0, Math.PI * 2);
            c.fill();
          }
          _pbStoneTex = new T.CanvasTexture(cv);
          _pbStoneTex.wrapS = _pbStoneTex.wrapT = T.RepeatWrapping;
          _pbStoneTex.repeat.set(3, 3);
        } catch (eS) { _pbStoneTex = null; }
      }
      return _pbStoneTex
        ? new T.MeshStandardMaterial({ map: _pbStoneTex, roughness: 1.0, metalness: 0.0 })
        : new T.MeshStandardMaterial({ color: PB_LAYERS.stone.colour, roughness: 1.0 });
    }
    function _pbHelix(T, rTop, rBot, flight, yTop, yBot, turns, mat) {
      var steps = Math.max(24, Math.round(turns * 26));
      var pos = [], i;
      var pt = function (k, out) {
        var t = k / steps;
        var ang = t * turns * Math.PI * 2;
        var rIn = rTop + (rBot - rTop) * t;
        var r = out ? rIn + flight : rIn - 0.002;
        var y = yTop + (yBot - yTop) * t + (out ? -0.008 : 0);
        return [Math.cos(ang) * r, y, Math.sin(ang) * r];
      };
      for (i = 0; i < steps; i++) {
        var a = pt(i, false), b = pt(i, true), c = pt(i + 1, false), d = pt(i + 1, true);
        pos.push(a[0], a[1], a[2], b[0], b[1], b[2], d[0], d[1], d[2]);
        pos.push(a[0], a[1], a[2], d[0], d[1], d[2], c[0], c[1], c[2]);
      }
      var geo = new T.BufferGeometry();
      geo.setAttribute('position', new T.Float32BufferAttribute(pos, 3));
      geo.computeVertexNormals();
      return new T.Mesh(geo, mat);
    }
    /* a box cut at grade unless the base is shown whole (the ADU's _box) */
    function _pbBox(T, g, mat, w, h, d, x, y, z) {
      var lo = y - h / 2, hi = y + h / 2;
      if (!_footFull()) { if (hi <= 0.001) return null; if (lo < 0) { h = hi; y = hi / 2; } }
      var m = new T.Mesh(new T.BoxGeometry(w, h, d), mat);
      m.position.set(x, y, z);
      g.add(m);
      return m;
    }
    function _pbCyl(T, g, mat, rTop, rBot, h, x, y, z, seg) {
      var lo = y - h / 2, hi = y + h / 2;
      if (!_footFull()) {
        if (hi <= 0.001) return;
        if (lo < 0) { var f = (0 - lo) / h; rBot = rBot + (rTop - rBot) * f; h = hi; y = hi / 2; }
      }
      var m = new T.Mesh(new T.CylinderGeometry(rTop, rBot, h, seg || 14), mat);
      m.position.set(x, y, z);
      g.add(m);
    }
    function _pbScrewPile(T, g, steel, px, pz, headY) {
      var rad = PB_FOOT.screwR, totalLen = PB_FOOT.screwLen;
      var aboveLen = headY, belowLen = totalLen - aboveLen;
      var cylLen = aboveLen + belowLen * 0.42, taperLen = belowLen * 0.58 - 0.06, tipRad = rad * 0.22;
      _pbCyl(T, g, steel, rad, rad, cylLen, px, headY - cylLen / 2, pz);
      var taperTopY = headY - cylLen;
      _pbCyl(T, g, steel, rad, tipRad, taperLen, px, taperTopY - taperLen / 2, pz);
      _pbCyl(T, g, steel, tipRad, 0.002, 0.06, px, taperTopY - taperLen - 0.03, pz, 10);
      if (_footFull()) {
        var hx = _pbHelix(T, rad, tipRad + 0.004, rad * 1.15, taperTopY + belowLen * 0.06, taperTopY - taperLen + 0.02, 3, steel);
        hx.position.set(px, 0, pz);
        g.add(hx);
      }
      _pbCyl(T, g, steel, rad + 0.014, rad + 0.03, 0.10, px, 0.05, pz, 12);
    }
    /* ---------------------------------------------------------------------
       A TERRACE DECK ON GROUND SCREWS (core-v512, moved from the A-frame
       porch, aframe-v60 / v71 / v73). The ADU terrace's build-up in one
       call, for any product: bearers 60 x 160 across x every 1800 along z,
       joists 60 x 160 along z every 500 on them, decking TST00002 25 x 145
       across the joists; under the bearers a ground screw on a 1800 grid, a
       U head on it and a 100 x 100 post only where the bearer stands higher
       than a screw's head reaches (300).

         API.deck(ctx, { layer, x0, x1, z0, z1, top, hangEnds, anchorCode,
                         review, col, colD })
           top       the decking's top face
           hangEnds  the bearers' two ends hang on concrete (a stem, a wall):
                     a hanger FAT00011 each, 4 x anchorCode into the concrete,
                     6 x FAS38038 into the bearer, and no screw at the ends
         -> { bear, joist, sup, post, area, boardScrews, joistScrews, hang,
              nBx, nBz, nJz }  (lengths in m, area in m2)

       Everything it draws it bills into spec.layer.
       --------------------------------------------------------------------- */
    function deck(ctx, spec) {
      spec = spec || {};
      var T = ctx.THREE || global.THREE, g = ctx.group, out = { bear: 0, joist: 0, sup: 0, post: 0, area: 0, boardScrews: 0, joistScrews: 0, hang: 0, nBx: 0, nBz: 0, nJz: 0 };
      if (!T || !g || !spec.layer) { if (ctx.log) ctx.log('deck     nothing built -- the spec needs a layer'); return out; }
      var dx0 = spec.x0, dx1 = spec.x1, dz0 = spec.z0, dz1 = spec.z1, DW = dx1 - dx0, DL = dz1 - dz0;
      if (!(DW > 0.3 && DL > 0.3)) { ctx.log('deck     nothing built -- ' + DW.toFixed(2) + ' x ' + DL.toFixed(2) + ' m is too small'); return out; }
      var MAG = 0xFF00FF, col = spec.review ? MAG : ((spec.col != null) ? spec.col : 0xB98B57), colD = spec.review ? MAG : ((spec.colD != null) ? spec.colD : 0xB68A5A);
      var mW = new T.MeshStandardMaterial({ color: col, roughness: 0.85 }), mD = new T.MeshStandardMaterial({ color: colD, roughness: 0.8 }), mS = new T.MeshStandardMaterial({ color: 0x6E7275, roughness: 0.5, metalness: 0.6 });
      var bx = function (w, h, d, x, y, z, m) { var ms = new T.Mesh(new T.BoxGeometry(w, h, d), m); ms.position.set(x, y, z); g.add(ms); };
      var yDk = spec.top, yJc = yDk - 0.025 - 0.080, yBc = yJc - 0.160, yBb = yBc - 0.080;
      var nBx = Math.max(2, Math.ceil(DW / 1.80) + 1), nBz = Math.max(2, Math.ceil(DL / 1.80) + 1), nJz = Math.max(2, Math.ceil(DW / 0.50) + 1), bi, bj;
      out.nBx = nBx; out.nBz = nBz; out.nJz = nJz;
      for (bj = 0; bj < nBz; bj++) {
        var zB = dz0 + 0.10 + (DL - 0.20) * bj / (nBz - 1);
        bx(DW, 0.160, 0.060, (dx0 + dx1) / 2, yBc, zB, mW);
        out.bear += DW;
        if (spec.hangEnds) { bx(0.004, 0.160, 0.064, dx0 + 0.002, yBc, zB, mS); bx(0.004, 0.160, 0.064, dx1 - 0.002, yBc, zB, mS); out.hang += 2; }
        for (bi = 0; bi < nBx; bi++) {
          if (spec.hangEnds && (bi === 0 || bi === nBx - 1)) continue;
          var xS = dx0 + 0.10 + (DW - 0.20) * bi / (nBx - 1), yHead = Math.min(0.30, yBb - 0.008);
          screwPile(ctx, xS, zB, yHead, { review: spec.review });
          bx(0.14, 0.008, 0.14, xS, yHead + 0.004, zB, mS);
          if (yBb - yHead > 0.05) { bx(0.100, yBb - yHead - 0.008, 0.100, xS, (yBb + yHead + 0.008) / 2, zB, mW); out.post += yBb - yHead; }
          out.sup++;
        }
      }
      for (bi = 0; bi < nJz; bi++) { var xJ = dx0 + 0.03 + (DW - 0.06) * bi / (nJz - 1); bx(0.060, 0.160, DL, xJ, yJc, (dz0 + dz1) / 2, mW); out.joist += DL; }
      var nBd = Math.floor(DL / 0.151), bd;
      for (bd = 0; bd < nBd; bd++) bx(DW, 0.025, 0.145, (dx0 + dx1) / 2, yDk - 0.0125, dz0 + 0.0725 + bd * 0.151, mD);
      out.area = DW * DL; out.boardScrews = nBd * nJz * 2; out.joistScrews = nJz * nBz * 2;
      var L = spec.layer;
      billAdd(L, 'KVH60160', (out.bear + out.joist) * 1.05);
      billAdd(L, 'TST00002', out.area * 1.05);
      billAdd(L, 'FAS55050', out.boardScrews);
      billAdd(L, 'FAS60100', out.joistScrews + out.sup * 4);
      if (out.post > 0) billAdd(L, 'KVH10100', out.post * 1.05);
      billAdd(L, 'steel.screwPile', out.sup);
      billAdd(L, 'steel.saddle', out.sup);
      if (out.hang) {
        billAdd(L, 'FAT00011', out.hang);
        if (spec.anchorCode) billAdd(L, spec.anchorCode, out.hang * 4);
        billAdd(L, 'FAS38038', out.hang * 6);
      }
      ctx.log('deck     ' + DW.toFixed(2) + ' x ' + DL.toFixed(2) + ' m, top ' + Math.round(yDk * 1000) + ' (core-v512): ' + nBz + ' bearer(s), ' + nJz + ' joist(s), ' + out.sup + ' ground screw(s)' + (out.hang ? (', ' + out.hang + ' bearer end(s) hung on the concrete') : '') + '; BILL KVH60160 ' + ((out.bear + out.joist) * 1.05).toFixed(1) + ' m, TST00002 ' + (out.area * 1.05).toFixed(2) + ' m2, ' + out.boardScrews + ' x FAS55050, ' + (out.joistScrews + out.sup * 4) + ' x FAS60100, ' + out.sup + ' x screw pile + U head' + (out.post > 0 ? (', posts KVH10100 ' + (out.post * 1.05).toFixed(2) + ' m') : '') + (out.hang ? (', ' + out.hang + ' x FAT00011, ' + (out.hang * 4) + ' x ' + (spec.anchorCode || '(no anchor code)') + ', ' + (out.hang * 6) + ' x FAS38038') : '') + (spec.review ? ' -- MAGENTA, under review' : ''));
      return out;
    }
    /* ONE GROUND SCREW, DRAWN AS THE BASE DRAWS THEM (core-v511, Marek: vruty
       jako u ADU, s peknou sroubovici): shaft, taper, tip, the flight wound
       round it and the collar at the ground; headY is the top of the screw
       over the ground. For a product's terrace, porch or balcony posts. */
    function screwPile(ctx, px, pz, headY, opt) {
      opt = opt || {};
      var T = ctx.THREE || global.THREE;
      if (!T || !ctx.group) return;
      var steel = new T.MeshStandardMaterial({ color: opt.review ? 0xFF00FF : 0x7C838A, roughness: 0.5, metalness: 0.7 });
      _pbScrewPile(T, ctx.group, steel, px, pz, Math.max(0.05, headY));
    }
    function pointBase(ctx, spec) {
      spec = spec || {};
      var T = ctx.THREE, g = ctx.group, layer = spec.layer, i;
      var kind = spec.kind || 'screws';
      var out = { points: 0, screws: 0, pads: 0, piers: 0, saddles: 0, concrete: 0, gravel: 0, excav: 0, geo: 0, joists: 0 };
      if (!T || !g || !layer) { if (ctx && ctx.log) ctx.log('base     drawer POINT BASE: nothing built -- the spec needs a layer'); return out; }
      if (['screws', 'pads', 'piers'].indexOf(kind) < 0) { ctx.log('base     drawer POINT BASE: kind ' + kind + ' is not a point footing -- nothing built'); return out; }
      var J = spec.joist || { t: 0.060, d: 0.240, code: 'KVH60240' }, deckT = spec.deckT || 0.018;
      var mark = function (k, d, l, c) { try { _asmMark(ctx, k, d, l, c); } catch (e) {} };
      var conc = new T.MeshStandardMaterial({ color: 0x9E9B93, roughness: 0.95, metalness: 0.0 });
      var steel = new T.MeshStandardMaterial({ color: 0x7C838A, roughness: 0.5, metalness: 0.7 });
      var wood = new T.MeshStandardMaterial({ color: (spec.wood != null) ? spec.wood : 0xB98B57, roughness: 0.85, metalness: 0.0 });
      var depth = Math.max(0.60, (STATE.climateZone || 0) + 0.15);
      var deckTop = (typeof spec.deckTop === 'number') ? spec.deckTop : STACK.slab;
      var timberY = deckTop - deckT - J.d, headY = timberY - PB_SADDLE.plate;
      /* A RECT OF THE PRODUCT'S OWN (core-v474, A-frame: zaklad pod celou
         strechou): spec.rect { x0, z0, x1, z1 } replaces the building's
         outline -- the frame, the supports and the deck cover it instead */
      var RQ = (spec.rect && spec.rect.x1 > spec.rect.x0 && spec.rect.z1 > spec.rect.z0) ? spec.rect : null;
      var B = RQ || _buildRect(), x0 = B.x0, x1 = B.x1, z0 = B.z0, z1 = B.z1;
      var w = x1 - x0, l = z1 - z0, cx = (x0 + x1) / 2, cz = (z0 + z1) / 2;
      if (RQ) ctx.log('base     point base on the product rect ' + Math.round(w * 1000) + ' x ' + Math.round(l * 1000) + ' (not the outline)');
      /* THE FOOTINGS, ONE PER BEARING POINT */
      mark(({ screws: 'base_screws', pads: 'base_pads', piers: 'base_piers' })[kind], 'top', kind,
           ({ screws: 'steel.screwPile', pads: 'grd.padBlock', piers: 'grd.concrete' })[kind]);
      var pts = bearingPoints(spec.spacing || 1.83, RQ ? [[x0, z0], [x1, z0], [x1, z1], [x0, z1]] : null);
      out.points = pts.length;
      var saddle = function (px, pz, ang) {
        var half = J.t / 2 + PB_SADDLE.t / 2, len = 0.16, f;
        var geo = new T.BoxGeometry(len, PB_SADDLE.plate, len);
        geo.rotateY(ang);
        geo.translate(px, timberY - PB_SADDLE.plate / 2, pz);
        g.add(new T.Mesh(geo, steel));
        for (f = -1; f <= 1; f += 2) {
          var ch = new T.BoxGeometry(PB_SADDLE.t, PB_SADDLE.cheek, len);
          ch.translate(f * half, 0, 0);
          ch.rotateY(ang);
          ch.translate(px, timberY + PB_SADDLE.cheek / 2, pz);
          g.add(new T.Mesh(ch, steel));
        }
        out.saddles++;
      };
      var pierH = 0;
      for (i = 0; i < pts.length; i++) {
        var px = pts[i][0], pz = pts[i][1], pang = pts[i][2] || 0;
        if (kind === 'screws') {
          _pbScrewPile(T, g, steel, px, pz, headY);
          out.screws++;
        } else if (kind === 'pads') {
          var pyr = new T.Mesh(new T.CylinderGeometry(PB_FOOT.padW * 0.5 / Math.SQRT2, PB_FOOT.padW / Math.SQRT2, PB_FOOT.padH, 4), conc);
          pyr.rotation.y = Math.PI / 4;
          pyr.position.set(px, headY - PB_FOOT.padH / 2, pz);
          g.add(pyr);
          _pbBox(T, g, _pbStoneMat(T), PB_FOOT.padW + 0.20, PB_FOOT.gravel, PB_FOOT.padW + 0.20, px, headY - PB_FOOT.padH - PB_FOOT.gravel / 2, pz);
          out.pads++;
          out.gravel += (PB_FOOT.padW + 0.20) * (PB_FOOT.padW + 0.20) * PB_FOOT.gravel;
          out.excav += (PB_FOOT.padW + 0.20) * (PB_FOOT.padW + 0.20) * (PB_FOOT.gravel + 0.05);
        } else {
          var pw = PB_FOOT.pierW, pierBot = headY - depth;
          _pbBox(T, g, conc, pw, depth, pw, px, headY - depth / 2, pz);
          if (_footFull()) {
            _pbBox(T, g, _pbStoneMat(T), pw, PB_FOOT.gravel, pw, px, pierBot - PB_FOOT.gravel / 2, pz);
            _pbBox(T, g, new T.MeshStandardMaterial({ color: PB_LAYERS.geo.colour, roughness: 1.0 }), pw, PB_LAYERS.geo.t, pw, px, pierBot - PB_FOOT.gravel - PB_LAYERS.geo.t / 2, pz);
          }
          out.concrete += pw * pw * depth;
          pierH += depth;
          var mBar = new T.MeshStandardMaterial({ color: 0x6B4F3A, roughness: 0.6, metalness: 0.5 }), cov = 0.05, dB = 0.014, q9;
          for (q9 = 0; q9 < 4; q9++) {
            var ox9 = ((q9 % 2) ? 1 : -1) * (pw / 2 - cov), oz9 = ((q9 < 2) ? 1 : -1) * (pw / 2 - cov);
            _pbBox(T, g, mBar, dB, depth - 2 * cov, dB, px + ox9, headY - depth / 2, pz + oz9);
          }
          out.piers++;
          out.gravel += pw * pw * PB_FOOT.gravel;
          out.geo += pw * pw;
          out.excav += (pw + 0.20) * (pw + 0.20) * (depth + PB_FOOT.gravel);
        }
        saddle(px, pz, pang);
      }
      if (out.excav) billAdd(layer, 'grd.excavation', out.excav);
      if (out.gravel) billAdd(layer, 'grd.gravel', out.gravel);
      if (out.concrete) billAdd(layer, 'grd.concrete', out.concrete);
      if (kind === 'piers' && pierH > 0) { billAdd(layer, 'FCO00003', pierH * 4); billAdd(layer, 'FCO00007', 1); }
      if (kind === 'pads' && out.pads) billAdd(layer, 'FCO00004', out.pads);
      if (kind === 'piers' && out.points) billAdd(layer, 'FCO00004', out.points);
      if (out.pads) billAdd(layer, 'grd.padBlock', out.pads);
      if (out.screws) billAdd(layer, 'steel.screwPile', out.screws);
      if (out.saddles) { billAdd(layer, 'steel.saddle', out.saddles); billAdd(layer, 'FAS55050', out.saddles * 4); }
      if (out.geo) billAdd(layer, 'grd.geotextile', out.geo);
      /* THE FLOOR FRAME ON THEM: rim, doubled centre beam, joists, blocking, deck */
      var joistY = deckTop - deckT - J.d / 2, deckY = deckTop - deckT / 2;
      var box = function (bw, bh, bd, bx, by, bz, m) {
        var mesh = new T.Mesh(new T.BoxGeometry(bw, bh, bd), m || wood);
        mesh.position.set(bx, by, bz); g.add(mesh); return mesh;
      };
      mark('layer_floor_joists', 'top', 'Floor joists', J.code);
      box(J.t, J.d, l, x0 + J.t / 2, joistY, cz);
      box(J.t, J.d, l, x1 - J.t / 2, joistY, cz);
      box(w - J.t * 2, J.d, J.t, cx, joistY, z0 + J.t / 2);
      box(w - J.t * 2, J.d, J.t, cx, joistY, z1 - J.t / 2);
      var rim = l * 2 + (w - J.t * 2) * 2, k;
      for (k = 0; k < 2; k++) box(J.t, J.d, l, cx + (k ? J.t / 2 : -J.t / 2), joistY, cz);
      var beam = l * 2;
      var sp = ctx.grid || 0.60, run = (w - J.t * 2) / 2 - J.t;
      var jpos = [], zz = z0 + J.t, joistLen = 0;
      while (zz < z1 - J.t - J.t) { jpos.push(zz); zz += sp; }
      jpos.push(z1 - J.t - J.t / 2);
      for (i = 0; i < jpos.length; i++) {
        box(run, J.d, J.t, x0 + J.t + run / 2, joistY, jpos[i]);
        box(run, J.d, J.t, x1 - J.t - run / 2, joistY, jpos[i]);
        out.joists += 2; joistLen += run * 2;
      }
      var blockLen = 0, nBlk = 0;
      if (jpos.length >= 2) {
        var rows = Math.max(1, Math.round(run / 1.6)), halves = [x0 + J.t, x1 - J.t - run], stepB = J.t * 1.2, r, q, j;
        for (r = 0; r < halves.length; r++) for (q = 1; q <= rows; q++) {
          var atX = halves[r] + run * (q / (rows + 1));
          for (j = 1; j < jpos.length; j++) {
            var a = jpos[j - 1], b = jpos[j], gapZ = b - a - J.t;
            if (gapZ < 0.15) continue;
            box(J.t, J.d, gapZ, atX + ((j % 2) ? stepB : -stepB), joistY, (a + b) / 2);
            blockLen += gapZ; nBlk++;
          }
        }
      }
      mark('layer_floor_subfloor', 'top', 'Subfloor OSB', 'OSB25002');
      var mOsb = new T.MeshStandardMaterial({ color: 0xB9A88C, roughness: 0.95, metalness: 0.0 });
      var SHF = sheet(), jt = 0.006, sL = SHF.l, sW = SHF.w, nSh = 0, cI, sX;
      /* OPEN ZONES (core-v528, A-frame: ram jednotny i pro terasu): spec.open
         [{ z0, z1, keep }] are stretches along z where the frame carries a
         terrace instead of a floor -- no OSB there but a strip 'keep' wide along
         each rim (under a plate), and decking TST00002 laid along z across the
         joists, its top at the joists' top + 25 */
      var OPN = Array.isArray(spec.open) ? spec.open.filter(function (o) { return o && o.z1 - o.z0 > 0.05; }) : [], osbOff = 0, dkA = 0, dkScr = 0;
      var inOpen = function (za, zb) { for (var oq = 0; oq < OPN.length; oq++) if (za >= OPN[oq].z0 - 1e-6 && zb <= OPN[oq].z1 + 1e-6) return OPN[oq]; return null; };
      var courses = Math.ceil(l / sW), zCuts = [z0], oc;
      for (cI = 1; cI < courses; cI++) zCuts.push(z0 + cI * sW);
      for (oc = 0; oc < OPN.length; oc++) { zCuts.push(Math.max(z0, OPN[oc].z0)); zCuts.push(Math.min(z1, OPN[oc].z1)); }
      zCuts.push(z1); zCuts.sort(function (a, b) { return a - b; });
      for (cI = 0; cI + 1 < zCuts.length; cI++) {
        var zA = zCuts[cI], zB = zCuts[cI + 1], rowStart = (Math.floor((zA - z0) / sW + 1e-6) % 2) ? -sL / 2 : 0, OZ = inOpen(zA, zB);
        if (zB - zA < 0.02) continue;
        if (OZ) {
          var kp = Math.max(0, OZ.keep || 0);
          if (kp > 0.01) { box(kp - jt, deckT, zB - zA - jt, x0 + kp / 2, deckY, (zA + zB) / 2, mOsb); box(kp - jt, deckT, zB - zA - jt, x1 - kp / 2, deckY, (zA + zB) / 2, mOsb); }
          osbOff += (w - 2 * kp) * (zB - zA);
          continue;
        }
        for (sX = x0 + rowStart; sX < x1; sX += sL) {
          var xA = Math.max(x0, sX), xB = Math.min(x1, sX + sL);
          if (xB - xA < 0.05 || zB - zA < 0.05) continue;
          box(xB - xA - jt, deckT, zB - zA - jt, (xA + xB) / 2, deckY, (zA + zB) / 2, mOsb);
          nSh++;
        }
      }
      if (OPN.length) {
        var mDk = new T.MeshStandardMaterial({ color: (spec.colD != null) ? spec.colD : 0xB68A5A, roughness: 0.8 }), yDkB = deckTop - deckT + 0.0125;
        OPN.forEach(function (OZ2) {
          var kp2 = Math.max(0, OZ2.keep || 0), xa2 = x0 + kp2 + 0.005, xb2 = x1 - kp2 - 0.005, za2 = Math.max(z0, OZ2.z0), zb2 = Math.min(z1, OZ2.z1), nB2 = Math.floor((xb2 - xa2) / 0.151), b2;
          for (b2 = 0; b2 < nB2; b2++) box(0.145, 0.025, zb2 - za2 - 0.004, xa2 + 0.0725 + b2 * 0.151, yDkB, (za2 + zb2) / 2, mDk);
          dkA += (xb2 - xa2) * (zb2 - za2); dkScr += nB2 * Math.ceil((zb2 - za2) / sp + 1) * 2;
        });
        billAdd(layer, 'TST00002', dkA * 1.05); billAdd(layer, 'FAS55050', dkScr);
        ctx.log('base     open zone(s) ' + OPN.map(function (o) { return Math.round(o.z0 * 1000) + '..' + Math.round(o.z1 * 1000); }).join(', ') + ': no OSB (a ' + Math.round((OPN[0].keep || 0) * 1000) + ' strip kept along each rim), decking TST00002 ' + (dkA * 1.05).toFixed(2) + ' m2 along z across the joists, top ' + Math.round((deckTop - deckT + 0.025) * 1000) + ', ' + dkScr + ' x FAS55050 (core-v528)');
      }
      billAdd(layer, J.code, joistLen + rim + beam + blockLen);
      billAdd(layer, 'sheet.osb18', Math.max(0, w * l - osbOff) * 1.05);
      var nDeck = Math.round(w * l * 22), nNail = out.joists * 3 * 2;
      billAdd(layer, 'FAS50060', nDeck);
      billAdd(layer, 'FAS45145', nNail);
      /* THE LOG */
      var mm = function (v) { return Math.round(v * 1000); };
      ctx.log('base     ' + kind + ': ' + out.points + ' supports at no more than ' + mm(spec.spacing || 1.83) + ' apart, round the outline and down the centre line' +
              (kind === 'screws' ? (' -- ground screw ' + mm(PB_FOOT.screwLen) + ' x 102 steel.screwPile, held by its length, not by the frost line')
                                 : kind === 'pads' ? (' -- precast footing 400x400 grd.padBlock on a ' + mm(PB_FOOT.gravel) + ' stone bed, U post base FCO00004')
                                 : (' -- pier 400x400 x ' + mm(depth) + ' deep (frost ' + mm(STATE.climateZone || 0) + '), 4 bars FCO00003, stone and geotextile under it, formwork FCO00007, U post base FCO00004')));
      ctx.log('base     ' + out.saddles + ' saddles steel.saddle, 4 x FAS55050 each; top of support at ' + mm(headY) + ', timber from ' + mm(timberY) +
              (out.concrete ? ('; concrete ' + out.concrete.toFixed(2) + ' m3') : '') + (out.gravel ? ('; gravel ' + out.gravel.toFixed(2) + ' m3, excavation ' + out.excav.toFixed(2) + ' m3') : '') +
              ' -- ' + (_footFull() ? 'FULL DEPTH (Roof & Base open)' : 'above grade only, open Roof & Base to see it whole'));
      ctx.log('floor    frame on the supports: rim ' + rim.toFixed(1) + ' m, centre beam 2 x ' + l.toFixed(1) + ' m, ' + out.joists + ' joists at ' + mm(sp) + ' span ' + mm(run) +
              ', ' + nBlk + ' blocks ' + blockLen.toFixed(1) + ' m -- all ' + J.code + ' ' + (joistLen + rim + beam + blockLen).toFixed(1) + ' m; deck ' + nSh + ' OSB sheets, ' + (w * l).toFixed(1) + ' m2 (+5%) at ' + mm(deckTop));
      ctx.log('FIX  floor deck ' + nDeck + ' x FAS50060 (22/m2), frame ' + nNail + ' x FAS45145 (3 through the rim at each joist end)');
      return out;
    }
    function buildStripSlab(ctx, o) {
      o = o || {};
      /* o.rect { x0, z0, x1, z1 } (core-v474, A-frame): the strips run round
         this rectangle and the slab covers it, instead of the outline -- a
         roof longer than the house, a covered terrace, stands on its own base */
      var RQ = (o.rect && o.rect.x1 > o.rect.x0 && o.rect.z1 > o.rect.z0) ? o.rect : null;
      var T = ctx.THREE, g = ctx.group, P = RQ ? [[RQ.x0, RQ.z0], [RQ.x1, RQ.z0], [RQ.x1, RQ.z1], [RQ.x0, RQ.z1]] : poly(), i, layer = o.layer || 'base';
      var stripW = o.stripW || 0.50, footH = o.footH || 0.30, slabT = o.slabT || 0.15, gravelT = o.gravelT || 0.15;
      var depth = Math.max(o.minDepth || 0.80, STATE.climateZone || 0);
      /* how far the slab reaches past the wall line: a product with a masonry
         wall gives half its wall, so the slab runs to the wall's outer face and
         the wall stands on it whole (core-v335) */
      var out = (o.out > 0) ? o.out : 0;
      /* what lies ON the slab up to the finished floor -- insulation, screed,
         the finish -- when the product counts its floor from the finished
         surface and not from the concrete; none by default (core-v335) */
      var floorT = (o.floorT > 0) ? o.floorT : 0;
      var full = _footFull();
      var CONC = 0x9A9A96, GRAV = 0xB8B0A2;
      var stripLen = 0, footVol = 0, stemVol = 0, nBody = 0;
      /* THE SLAB'S TOP IS THE GROUND FLOOR (core-v335). It was drawn with its
         top at grade whatever the storey stack said, so the floor the walls and
         the roof are measured from and the concrete under it were two different
         heights. The slab now stands where storey 0 starts -- STACK.slab, the
         zone from grade to the top of the ground floor -- and the stems of the
         strips come up to its underside. Whatever of them stands above grade is
         the plinth and is always drawn; what is below grade shows only on the
         base step, as before. A floor build-up given as floorT lies between the
         two: the slab's top is then that much under the floor. */
      var slabTop = STACK.slab - floorT, slabBot = slabTop - slabT, bedBot = slabBot - gravelT;
      var footTop = -depth + footH;
      function sg(v) { var n = Math.round(v * 1000); return (n > 0 ? '+' : '') + n; }
      /* ITS OWN BOX (core-v335). This called _box, and there are two of them
         in this file: the one that takes a colour and a turn, and a later one
         that takes a MATERIAL as its third argument. The later one wins, so
         every strip and the slab were made with a width where the material goes
         -- a mesh with no material, which the renderer never draws. The model
         said the base was built; nothing of it was ever on screen (Marek: neni
         videt zaklad). The same trap the product fell into at thouse-v23. */
      function box(w, h, d, x, y, z, ry, col) {
        if (!(w > 0.001) || !(h > 0.001) || !(d > 0.001)) return null;
        var m = new T.Mesh(new T.BoxGeometry(w, h, d), _mat(T, col, 1));
        m.position.set(x, y, z);
        if (ry) m.rotation.y = ry;
        g.add(m);
        nBody++;
        return m;
      }
      /* WHAT IS UNDER GRADE SHOWS ON THE BASE STEP ONLY -- for every body of
         the base, the slab and its bed included (core-v338). Only the stems
         were split at grade; the slab was drawn whole wherever it lay, so with
         the floor under +300 a slab under the terrain stood in the picture as a
         grey band below the walls. On the base step a body is drawn whole;
         everywhere else only what of it stands above grade. */
      function part(w, yLo, yHi, d, x, z, ry, col) {
        if (!(yHi - yLo > 0.001)) return null;
        if (full) return box(w, yHi - yLo, d, x, (yLo + yHi) / 2, z, ry, col);
        var lo = Math.max(0, yLo);
        if (!(yHi - lo > 0.001)) { _qtyHold(w, yHi - yLo, d); return null; }   /* core-v682 */
        var mP = box(w, yHi - lo, d, x, (lo + yHi) / 2, z, ry, col);
        if (mP && lo > yLo + 0.0005 && _qtyFullOn()) mP.userData.qtyBox = [w, yHi - yLo, d];
        return mP;
      }
      /* core-v682 (RANCH thread, opt-in o.marks { strip, slab, bed }): each
         part under the code it is billed as -- the slab and its bed were
         drawn under the mark the product set before the call (the strips'),
         billed as the slab's concrete and gravel. Without it nothing changes. */
      var MK = (o.marks && typeof o.marks === 'object') ? o.marks : null;
      function mark(k, def) { if (MK && MK[k] && ctx.marks) _asmMark(ctx, 'base_' + k, 'top', def, MK[k]); }
      /* core-v684 (RANCH thread, opt-in o.marks.dig): THE DIG DRAWN AS BILLED
         -- each strip's trench (its run x the footing's width x the depth,
         from grade) and under each slab box what of its bed lies under grade,
         translucent earth; under grade, so drawn on the base step and held
         for the qty audit elsewhere. Without it nothing changes. */
      var mDig = null;
      function digPart(w, yLo, yHi, d, x, z, ry) {
        if (!(yHi - yLo > 0.001)) return;
        var lo = full ? yLo : Math.max(0, yLo);
        if (!(yHi - lo > 0.001)) { _qtyHold(w, yHi - yLo, d); return; }
        if (!mDig) mDig = new T.MeshStandardMaterial({ color: 0x7A6248, roughness: 1.0, transparent: true, opacity: 0.30, depthWrite: false });
        var mD = new T.Mesh(new T.BoxGeometry(w, yHi - lo, d), mDig);
        mD.position.set(x, (lo + yHi) / 2, z); if (ry) mD.rotation.y = ry;
        if (lo > yLo + 0.0005 && _qtyFullOn()) mD.userData.qtyBox = [w, yHi - yLo, d];
        g.add(mD); nBody++;
      }
      if (MK && MK.dig) {
        mark('dig', 'Excavation');
        for (i = 0; i < P.length; i++) {
          var aD = P[i], bD = P[(i + 1) % P.length], dxD = bD[0] - aD[0], dzD = bD[1] - aD[1], LD = Math.sqrt(dxD * dxD + dzD * dzD);
          if (!(LD > 0.05)) continue;
          digPart(LD, -depth, 0, stripW + 0.30, (aD[0] + bD[0]) / 2, (aD[1] + bD[1]) / 2, Math.atan2(dxD, dzD) + Math.PI / 2);
        }
      }
      /* the strips: a spread footing at the bottom, the stem up to the slab */
      mark('strip', 'Strip footing');
      for (i = 0; i < P.length; i++) {
        var a = P[i], b = P[(i + 1) % P.length];
        var dx = b[0] - a[0], dz = b[1] - a[1], L = Math.sqrt(dx * dx + dz * dz);
        if (!(L > 0.05)) continue;
        var ry = Math.atan2(dx, dz) + Math.PI / 2;
        var cx = (a[0] + b[0]) / 2, cz = (a[1] + b[1]) / 2;
        var stemH = Math.max(0, slabBot - footTop);
        stripLen += L;
        footVol += (L + stripW + 0.30) * (stripW + 0.30) * footH;
        stemVol += (L + stripW) * stripW * stemH;
        part(L + stripW + 0.30, footTop - footH, footTop, stripW + 0.30, cx, cz, ry, CONC);
        part(L + stripW, footTop, slabBot, stripW, cx, cz, ry, CONC);
      }
      /* the slab and its bed, over the bar and the wing. The wing's piece starts
         where the bar's ends, so the two touch and never overlap -- two slabs
         lying in one plane flicker where they cover each other */
      var B0 = RQ || _barRect(), WR = RQ ? null : _wingRect();
      var boxes = [{ x0: B0.x0 - out, x1: B0.x1 + out, z0: B0.z0 - out, z1: B0.z1 + out }];
      if (WR) {
        var sd = (STATE.wing && STATE.wing.side) || 'z1';
        var W2 = { x0: WR.x0 - out, x1: WR.x1 + out, z0: WR.z0 - out, z1: WR.z1 + out };
        if (sd === 'z1') W2.z0 = boxes[0].z1;
        else if (sd === 'z0') W2.z1 = boxes[0].z0;
        else if (sd === 'x1') W2.x0 = boxes[0].x1;
        else W2.x1 = boxes[0].x0;
        if (W2.x1 - W2.x0 > 0.01 && W2.z1 - W2.z0 > 0.01) boxes.push(W2);
      }
      var area = 0;
      for (i = 0; i < boxes.length; i++) {
        var bx = boxes[i], w = bx.x1 - bx.x0, l = bx.z1 - bx.z0, mx = (bx.x0 + bx.x1) / 2, mz = (bx.z0 + bx.z1) / 2;
        area += w * l;
        if (MK && MK.dig && bedBot < 0) { mark('dig', 'Excavation'); digPart(w, bedBot, 0, l, mx, mz, 0); }   /* core-v684 */
        mark('slab', 'Concrete slab');
        part(w, slabBot, slabTop, l, mx, mz, 0, CONC);
        mark('bed', 'Gravel base');
        part(w, bedBot, slabBot, l, mx, mz, 0, GRAV);
      }
      /* the dig under the slab is only what of its bed lies below grade */
      var slabVol = area * slabT, gravVol = area * gravelT, digSlab = area * Math.max(0, -bedBot);
      /* THE REINFORCEMENT, DRAWN AND BILLED (core-v474, A-frame, copied from
         the ADU's strips, adu-v1396): in every strip four 14 mm bars at the
         footing's corners, 50 of cover, a stirrup round them every 300 (drawn
         as its four sides), a spacer under the cage every metre each side,
         and the tying wire by weight -- 12 g a metre of bar and 20 g a
         stirrup. Adapted from the T house: starter bars from the cage up into
         the slab every 600, so strip and slab are tied, and one layer of
         welded mesh at the slab's mid depth, 150 squares, sheets 3 x 2 m with
         a 12 per cent lap, 2 spacers and 30 g of wire a square metre. Every
         bar carries userData.rebar, which the reinforcement stage keeps solid.
         Only where o.rebar asks for it, so the T house, which draws its own,
         is untouched. */
      var nStT = 0, nSpT = 0, wireT = 0, starterLen = 0, meshSheets = 0, rbDrawn = 0, nStarters = 0, topBarLen = 0;
      var cxP = 0, czP = 0, ip;
      if (o.rebar) mark('strip', 'Strip footing');   /* core-v682: the cage back under the strips' mark */
      for (ip = 0; ip < P.length; ip++) { cxP += P[ip][0]; czP += P[ip][1]; }
      cxP /= Math.max(1, P.length); czP /= Math.max(1, P.length);
      if (o.rebar) {
        var RB = new T.MeshStandardMaterial({ color: 0x6B4F3A, roughness: 0.6, metalness: 0.5 });
        var rb = function (w2, h2, d2, x2, y2, z2, ry2) {
          if (!full) return null;
          var m2 = new T.Mesh(new T.BoxGeometry(w2, h2, d2), RB);
          m2.position.set(x2, y2, z2); if (ry2) m2.rotation.y = ry2;
          m2.userData.rebar = true;
          g.add(m2); rbDrawn++;
          return m2;
        };
        var cov = 0.05, dB = 0.014, dS = 0.006, fW = stripW + 0.30, yLo = footTop - footH + cov, yHi = footTop - cov, yMesh = slabBot + slabT / 2;
        for (i = 0; i < P.length; i++) {
          var a2 = P[i], b2 = P[(i + 1) % P.length];
          var ddx = b2[0] - a2[0], ddz = b2[1] - a2[1], L2 = Math.sqrt(ddx * ddx + ddz * ddz);
          if (!(L2 > 0.3)) continue;
          var ux = ddx / L2, uz = ddz / L2, nx = -uz, nz = ux, ry2 = Math.atan2(ddx, ddz) + Math.PI / 2;
          var mx2 = (a2[0] + b2[0]) / 2, mz2 = (a2[1] + b2[1]) / 2, sLen = L2 + fW - 2 * cov, q9, s9;
          for (q9 = 0; q9 < 4; q9++) {
            var offA = ((q9 % 2) ? 1 : -1) * (fW / 2 - cov), yb = (q9 < 2) ? yLo : yHi;
            rb(sLen, dB, dB, mx2 + nx * offA, yb, mz2 + nz * offA, ry2);
          }
          var nSt = Math.max(2, Math.floor(sLen / 0.30) + 1), wIn = fW - 2 * cov, hIn = yHi - yLo;
          for (s9 = 0; s9 < nSt; s9++) {
            var at = -sLen / 2 + cov + (sLen - 2 * cov) * (nSt === 1 ? 0.5 : s9 / (nSt - 1));
            var sx = mx2 + ux * at, sz = mz2 + uz * at;
            rb(dS, hIn, dS, sx - nx * wIn / 2, (yLo + yHi) / 2, sz - nz * wIn / 2, 0);
            rb(dS, hIn, dS, sx + nx * wIn / 2, (yLo + yHi) / 2, sz + nz * wIn / 2, 0);
            rb(dS, dS, wIn, sx, yLo, sz, ry2);
            rb(dS, dS, wIn, sx, yHi, sz, ry2);
          }
          /* THE STARTERS (core-v477, A-frame, Marek: neni malo prutu z vence
             do podlahove desky?): L-shaped, up from the footing cage to just
             under the slab's mesh and then 400 into the slab, in two rows
             at the stem's faces every 400, the rows staggered by 200 -- one
             row every 600 down the middle was the least that is done, and a
             straight bar does not hold the slab. And two bars along the top
             of the stem under the slab, tied to the starters, so the stem
             and the slab's edge work together. */
          var hStr = (yMesh - dB) - yLo, legL = 0.40, sgIn = ((cxP - mx2) * nx + (czP - mz2) * nz) >= 0 ? 1 : -1, rowOff = stripW / 2 - cov, rw, s10;
          if (hStr > 0.05) for (rw = -1; rw <= 1; rw += 2) {
            var sh0 = (rw > 0) ? 0.20 : 0, runS = L2 - stripW, nS10 = Math.max(1, Math.floor((runS - sh0) / 0.40));
            for (s10 = 0; s10 <= nS10; s10++) {
              var us = -runS / 2 + sh0 + s10 * 0.40;
              if (us > runS / 2 + 1e-6) break;
              var bxS = mx2 + ux * us + nx * rw * rowOff, bzS = mz2 + uz * us + nz * rw * rowOff;
              rb(dB, hStr, dB, bxS, yLo + hStr / 2, bzS, 0);
              rb(legL, dB, dB, bxS + nx * sgIn * legL / 2, yMesh - dB, bzS + nz * sgIn * legL / 2, Math.atan2(nx * sgIn, nz * sgIn) + Math.PI / 2);
              starterLen += hStr + legL; nStarters++;
            }
          }
          var yTopBar = slabBot - cov, tLen = L2 + stripW - 2 * cov;
          if (yTopBar > yHi + 0.05) for (rw = -1; rw <= 1; rw += 2) {
            rb(tLen, dB, dB, mx2 + nx * rw * rowOff, yTopBar, mz2 + nz * rw * rowOff, ry2);
            topBarLen += tLen;
          }
          nStT += nSt;
          nSpT += Math.max(2, Math.ceil(sLen / 1.0)) * 2;
          wireT += sLen * 4 * 0.012 + nSt * 0.02;
        }
        /* the slab's mesh, box by box */
        var st4 = 0.15, k4;
        for (i = 0; i < boxes.length; i++) {
          var bm = boxes[i], ci = 0.05;
          for (k4 = 0; (bm.x0 + ci + k4 * st4) <= bm.x1 - ci + 1e-9; k4++) rb(dS, dS, bm.z1 - bm.z0 - 2 * ci, bm.x0 + ci + k4 * st4, yMesh, (bm.z0 + bm.z1) / 2, 0);
          for (k4 = 0; (bm.z0 + ci + k4 * st4) <= bm.z1 - ci + 1e-9; k4++) rb(bm.x1 - bm.x0 - 2 * ci, dS, dS, (bm.x0 + bm.x1) / 2, yMesh, bm.z0 + ci + k4 * st4, 0);
        }
        meshSheets = Math.ceil(area * 1.12 / 6);
        billAdd(layer, 'FFR30002', meshSheets);
        billAdd(layer, 'FFM00004', nSpT + Math.ceil(area * 2));
        wireT += nStarters * 0.02;
        billAdd(layer, 'FEC00026', Math.round((wireT + area * 0.03) * 100) / 100);
      }
      billAdd(layer, 'grd.excavation', stripLen * (stripW + 0.30) * depth + digSlab);
      billAdd(layer, 'grd.gravel', stripLen * (stripW + 0.30) * 0.10);
      billAdd(layer, 'grd.gravelSlab', gravVol);
      billAdd(layer, 'grd.geotextile', area * 1.10);
      billAdd(layer, 'grd.concrete', footVol + stemVol);
      billAdd(layer, 'grd.concreteSlab', slabVol);
      billAdd(layer, 'FCO00003', stripLen * 4 * 1.05 + starterLen * 1.05 + topBarLen * 1.05);
      billAdd(layer, 'WWR00061', o.rebar ? nStT : Math.ceil(stripLen / 0.30));
      billAdd(layer, 'FCO00007', 1);
      if (o.rebar) ctx.log('base     reinforcement: 4 bars FCO00003 in every strip + ' + nStarters + ' L-starters (' + Math.round(starterLen * 10) / 10 + ' m, 400 into the slab) in two rows at the stem faces every 400 staggered 200, ' + Math.round(topBarLen * 10) / 10 + ' m of 2 bars along the stem top under the slab, ' +
                           nStT + ' stirrups WWR00061 every 300, ' + (nSpT + Math.ceil(area * 2)) + ' spacers FFM00004, slab mesh ' + meshSheets + ' sheet(s) FFR30002 (' +
                           Math.round(area * 10) / 10 + ' m2 + 12 per cent lap), tying wire FEC00026 ' + Math.round((wireT + area * 0.03) * 100) / 100 + ' kg; ' +
                           (full ? (rbDrawn + ' bars drawn') : 'not drawn -- below grade, shown on Roof & Base or the reinforcement stage'));
      if (RQ) ctx.log('base     strips and slab on the product rect ' + Math.round((RQ.x1 - RQ.x0) * 1000) + ' x ' + Math.round((RQ.z1 - RQ.z0) * 1000) + ' (not the outline)');
      ctx.log('base     strips: ' + P.length + ' run(s), ' + Math.round(stripLen * 10) / 10 + ' m, ' +
              Math.round(stripW * 1000) + ' wide to ' + Math.round(depth * 1000) + ' below grade (footing ' +
              Math.round((stripW + 0.30) * 1000) + ' x ' + Math.round(footH * 1000) + '), stems up to ' + sg(slabBot) +
              (slabBot > 0.001 ? (' -- ' + Math.round(slabBot * 1000) + ' of them above grade') : '') +
              '; concrete ' + Math.round((footVol + stemVol) * 100) / 100 + ' m3; rebar 4 x 14 mm, stirrups every 300' +
              (full ? '' : ' -- below grade hidden outside the base step'));
      ctx.log('base     slab: top at ' + sg(slabTop) +
              (floorT > 0 ? (', ' + Math.round(floorT * 1000) + ' of floor build-up on it to the ground floor at ' + sg(STACK.slab)) : ' = the ground floor') + ' (' +
              (_slabFrom === 'product' ? 'the level this product gives' : 'the footing default, a crawl space -- this product has not said where its floor is') +
              '), ' + Math.round(slabT * 1000) + ' thick, ' + Math.round(slabVol * 100) / 100 + ' m3 over ' + Math.round(area * 10) / 10 +
              ' m2 on ' + Math.round(gravelT * 1000) + ' of gravel, ' +
              (out > 0 ? ('reaching ' + Math.round(out * 1000) + ' past the wall line') : 'its edge on the wall line') +
              '; excavation ' + Math.round((stripLen * (stripW + 0.30) * depth + digSlab) * 10) / 10 + ' m3; ' + nBody + ' bodies drawn');
      var FR = _floorRange();
      ctx.log('base     floor level ' + sg(STACK.slab) + (FR ? (' -- slider ' + sg(FR.min) + ' to ' + sg(FR.max) +
              (typeof STATE.floorLevel === 'number' ? (', set to ' + sg(STATE.floorLevel) + (Math.abs(STATE.floorLevel - STACK.slab) > 0.001 ? (' -- OUT OF RANGE, held at ' + sg(STACK.slab)) : '') + ' and saved with the project') : ', not moved yet (the product default)') +
              '; ' + (slabTop > 0.001 ? ('plinth ' + Math.round(slabTop * 1000) + ' above grade') :
                      (slabTop < -0.001 ? ('slab top ' + Math.round(-slabTop * 1000) + ' under grade -- no plinth' + (full ? '' : ', the slab shows on the base step only'))
                                        : 'slab top at grade -- no plinth'))) : ' -- no slider, this product gives no range'));
      return { stripLen: stripLen, concrete: footVol + stemVol + slabVol, area: area, depth: depth, slabTop: slabTop };
    }

    function _pdfSetOut() {
      try {
        var st = _stage(), T = global.THREE, pts = [], seen = {}, i;
        var KINDS = { base_screws: 'screw', base_pads: 'pad', base_piers: 'pier', base_strips: 'strip' };
        /* ONE FOOTING IS ONE POINT, however many pieces it is drawn from. A
           screw is a shaft, a collar and a head -- three bodies at the same
           place -- so points closer than a quarter of a metre are the same
           footing and are merged. */
        function put(kind, x, z) {
          var q;
          for (q = 0; q < pts.length; q++) {
            if (pts[q].kind !== kind) continue;
            if (Math.abs(pts[q].x - x) < 0.25 && Math.abs(pts[q].z - z) < 0.25) return;
          }
          pts.push({ kind: kind, x: Math.round(x * 1000) / 1000, z: Math.round(z * 1000) / 1000 });
        }
        if (st && st.group && T) {
          st.group.traverse(function (o) {
            if (!o.isMesh || !o.userData || !o.userData.asm) return;
            var mk = o.userData.asm, key = (typeof mk === 'string') ? mk : (mk.key || '');
            var kind = KINDS[key];
            if (!kind) return;
            var b = new T.Box3().setFromObject(o), c = b.getCenter(new T.Vector3()), s = b.getSize(new T.Vector3());
            if (kind !== 'strip' && (s.x > 1.2 || s.z > 1.2)) return;    /* a slab is not a point */
            put(kind, c.x, c.z);
          });
        }
        for (i = 0; i < _setOut.length; i++) put(_setOut[i].kind, _setOut[i].x, _setOut[i].z);
        if (!pts.length) return null;
        var by = {};
        for (i = 0; i < pts.length; i++) by[pts[i].kind] = (by[pts[i].kind] || 0) + 1;
        _log('pdf set-out: ' + pts.length + ' point(s) -- ' +
             Object.keys(by).map(function (k2) { return by[k2] + ' x ' + k2; }).join(', '));
        return { points: pts, counts: by };
      } catch (eSO) { _log('pdf set-out: ' + (eSO && eSO.message)); return null; }
    }

    /* WHAT IS WIRED, AND WHERE (core-v305). The services are drawn in 3D and
       nowhere else; an electrician cannot work from that. Every body in the
       product's electrical layer is read back off the model, sorted by the
       catalogue prefix its code carries -- socket, light, switch, board,
       junction box -- and handed over as points on the plan. */
    /* WHERE EVERY TRUSS STANDS (core-v306). The sheets say what a truss is
       made of; nothing said which of them goes where, and on a hip end that is
       eight different jacks. Every body wearing a truss's mark is found in the
       model, the bodies standing over one line are one truss, and each comes
       back as a line in plan with the label of its own sheet -- so a sheet can
       be matched to a place on the roof (Marek: kladecsky plan strechy). */
    function _pdfRoofPlan() {
      try {
        var st = _stage(), T = global.THREE;
        if (!st || !st.group || !T) return null;
        var sheets = [];
        try { sheets = _pdfTrusses() || []; } catch (eT) { sheets = []; }
        if (!sheets.length) return null;
        var out = [], si, ti;
        for (si = 0; si < sheets.length; si++) {
          var sh = sheets[si], tags = sh.tags || [], tq, nLine = 0;
          /* ONE TAG AT A TIME, AND WITHIN IT ONE DIRECTION AT A TIME. Four hip
             rafters share one drawing, so one sheet's bodies point four ways;
             taken together they average into a line that is on the roof nowhere.
             Bodies are bucketed by the way they lie first, then by which line
             they stand over (Marek: nevidim tam sikme vazniky / narozni). */
          for (tq = 0; tq < tags.length; tq++) {
            var tag = tags[tq], bodies = [];
            st.group.traverse(function (o) {
              if (!o.isMesh || !o.userData) return;
              var u = o.userData;
              if (u.trussSig === tag || u.trussGrp === tag) bodies.push(o);
            });
            if (!bodies.length) continue;
            var bi, q;
            function planDir(o2) {
              try {
                if (!o2.geometry.boundingBox) o2.geometry.computeBoundingBox();
                var bb = o2.geometry.boundingBox, sz = new T.Vector3().subVectors(bb.max, bb.min);
                o2.updateWorldMatrix(true, false);
                var e = o2.matrixWorld.elements, best = null, bl = 0, k2;
                var ax = [[e[0], e[1], e[2], sz.x], [e[4], e[5], e[6], sz.y], [e[8], e[9], e[10], sz.z]];
                for (k2 = 0; k2 < 3; k2++) {
                  var vx = ax[k2][0] * ax[k2][3], vz = ax[k2][2] * ax[k2][3], L2 = Math.sqrt(vx * vx + vz * vz);
                  if (L2 > bl) { bl = L2; best = [vx / (L2 || 1), vz / (L2 || 1), L2]; }
                }
                return (bl > 0.3) ? best : null;
              } catch (eD) { return null; }
            }
            /* bucket by the way a member lies, taking a direction and its
               opposite as the same way */
            var buckets = [];
            for (bi = 0; bi < bodies.length; bi++) {
              var d2 = planDir(bodies[bi]);
              if (!d2) continue;
              var b3 = new T.Box3().setFromObject(bodies[bi]), c3 = b3.getCenter(new T.Vector3());
              var got = null;
              for (q = 0; q < buckets.length; q++) {
                var dot = Math.abs(buckets[q].ux * d2[0] + buckets[q].uz * d2[1]);
                if (dot > 0.985) { got = buckets[q]; break; }
              }
              if (!got) { got = { ux: d2[0], uz: d2[1], pts: [], len: 0 }; buckets.push(got); }
              got.pts.push({ c: [c3.x, c3.z], m: bodies[bi] });
              got.len = Math.max(got.len, d2[2]);
            }
            for (q = 0; q < buckets.length; q++) {
              var BK = buckets[q];
              /* a bucket of short pieces is the webs of a truss already drawn
                 by its own chord: it adds no line of its own */
              if (BK.len < 0.8 || BK.pts.length < 2) continue;
              var ux2 = BK.ux, uz2 = BK.uz, nx2 = -uz2, nz2 = ux2;
              var mx = 0, mz = 0, bj;
              for (bj = 0; bj < BK.pts.length; bj++) { mx += BK.pts[bj].c[0]; mz += BK.pts[bj].c[1]; }
              mx /= BK.pts.length; mz /= BK.pts.length;
              /* HOW FAR A BODY REACHES, not where its middle is. A bottom chord
                 is one body across the whole truss, so its centre says nothing
                 about the span and the line came out a third too short. The
                 corners of the body are projected instead. */
              function reach(o3, ax2, az2) {
                var lo3 = 1e9, hi3 = -1e9, sgx, sgy, sgz;
                try {
                  if (!o3.geometry.boundingBox) o3.geometry.computeBoundingBox();
                  var bb3 = o3.geometry.boundingBox;
                  o3.updateWorldMatrix(true, false);
                  for (sgx = 0; sgx < 2; sgx++) for (sgy = 0; sgy < 2; sgy++) for (sgz = 0; sgz < 2; sgz++) {
                    var v3 = new T.Vector3(sgx ? bb3.max.x : bb3.min.x, sgy ? bb3.max.y : bb3.min.y, sgz ? bb3.max.z : bb3.min.z);
                    v3.applyMatrix4(o3.matrixWorld);
                    var pj = (v3.x - mx) * ax2 + (v3.z - mz) * az2;
                    lo3 = Math.min(lo3, pj); hi3 = Math.max(hi3, pj);
                  }
                } catch (eR) {}
                return [lo3, hi3];
              }
              var lines2 = [], g2;
              for (bj = 0; bj < BK.pts.length; bj++) {
                var dx2 = BK.pts[bj].c[0] - mx, dz2 = BK.pts[bj].c[1] - mz;
                var across = dx2 * nx2 + dz2 * nz2, k3;
                var ex2 = reach(BK.pts[bj].m, ux2, uz2);
                g2 = null;
                for (k3 = 0; k3 < lines2.length; k3++) if (Math.abs(lines2[k3].at - across) < 0.35) { g2 = lines2[k3]; break; }
                if (!g2) { g2 = { at: across, lo: 1e9, hi: -1e9, n: 0 }; lines2.push(g2); }
                g2.lo = Math.min(g2.lo, ex2[0]); g2.hi = Math.max(g2.hi, ex2[1]);
                g2.n++; g2.at = (g2.at * (g2.n - 1) + across) / g2.n;
              }
              lines2.sort(function (p2, q2) { return p2.at - q2.at; });
              for (ti = 0; ti < lines2.length; ti++) {
                var G = lines2[ti];
                if (!(G.hi - G.lo > 0.3)) continue;
                /* the line runs through the CENTRES of the bodies, so it stops
                   half a section short at each end: put that back */
                var pad2 = 0;
                nLine++;
                out.push({
                  label: 'T' + (si + 1) + '.' + nLine,
                  sheet: sh.label || '',
                  a: [mx + ux2 * (G.lo - pad2) + nx2 * G.at, mz + uz2 * (G.lo - pad2) + nz2 * G.at],
                  b: [mx + ux2 * (G.hi + pad2) + nx2 * G.at, mz + uz2 * (G.hi + pad2) + nz2 * G.at]
                });
              }
            }
          }
        }
        if (!out.length) return null;
        _log('pdf roof plan: ' + out.length + ' truss line(s) from ' + sheets.length + ' sheet(s)');
        return { trusses: out };
      } catch (eRP) { _log('pdf roof plan: ' + (eRP && eRP.message)); return null; }
    }

    function _pdfMep() {
      try {
        var st = _stage(), T = global.THREE, pts = [], i;
        if (!st || !st.group || !T) return null;
        var PRE = { EES: 'socket', EEC: 'light', EEL: 'switch', EED: 'board', EEJ: 'box' };
        function put(kind, code, x, z, y) {
          var q;
          for (q = 0; q < pts.length; q++) {
            if (pts[q].kind === kind && Math.abs(pts[q].x - x) < 0.15 && Math.abs(pts[q].z - z) < 0.15) return;
          }
          pts.push({ kind: kind, code: code, x: Math.round(x * 1000) / 1000, z: Math.round(z * 1000) / 1000,
                     y: Math.round((y || 0) * 1000) / 1000 });
        }
        st.group.traverse(function (o) {
          if (!o.isMesh || !o.userData) return;
          var lay = '';
          try { lay = _layerOf(o) || ''; } catch (eL) { lay = ''; }
          if (!/-elec$/.test(lay)) return;
          var mk = o.userData.asm, code = mk ? (typeof mk === 'string' ? '' : (mk.code || '')) : '';
          var kind = PRE[String(code).slice(0, 3)];
          if (!kind) return;
          var b = new T.Box3().setFromObject(o), c = b.getCenter(new T.Vector3());
          put(kind, code, c.x, c.z, c.y);
        });
        if (!pts.length) return null;
        var by = {};
        for (i = 0; i < pts.length; i++) by[pts[i].kind] = (by[pts[i].kind] || 0) + 1;
        _log('pdf services: ' + pts.length + ' point(s) -- ' +
             Object.keys(by).map(function (k) { return by[k] + ' x ' + k; }).join(', '));
        return { points: pts, counts: by };
      } catch (eMe) { _log('pdf services: ' + (eMe && eMe.message)); return null; }
    }

    function _pdfPlanSheet() {
      try {
        var P = poly(), B = _buildRect(), W = wallThickness();
        if (!P || P.length < 3) return null;
        var segs = _namedWallSegs(), out = { outline: [], walls: [], parts: [], rooms: [],
                                             wall: W, w: B.x1 - B.x0, l: B.z1 - B.z0 };
        var i, j;
        for (i = 0; i < P.length; i++) out.outline.push([P[i][0], P[i][1]]);
        for (i = 0; i < segs.length; i++) {
          var s = segs[i], ops = [];
          var list = [];
          try { list = _opsOn(s.id) || []; } catch (eO) { list = []; }
          for (j = 0; j < list.length; j++) {
            var o = list[j];
            ops.push({ at: o.at, w: o.w, h: o.h, sill: o.sill || 0,
                       code: o.code || '', kind: o.kind || '', hand: o.hand || '' });
          }
          ops.sort(function (a, b) { return a.at - b.at; });
          /* the wall's own height too, for the elevations (core-v303) */
          var sgH = 0;
          try { var sgR = segment(s.id); sgH = (sgR && sgR.h) || 0; } catch (eH) { sgH = 0; }
          out.walls.push({ id: s.id, a: [s.x0, s.z0], b: [s.x1, s.z1], len: s.len, h: sgH, openings: ops });
        }
        var pr = STATE.parts || [], pt = (typeof partThickness === 'function') ? partThickness() : 0.1;
        for (i = 0; i < pr.length; i++) {
          var p = pr[i], sp = null;
          try { sp = (typeof _partSpanH === 'function') ? _partSpanH(p) : null; } catch (eS) { sp = null; }
          var a0 = sp ? sp[0] : p.a, a1 = sp ? sp[1] : p.b;
          /* the openings of this wall, so the plan and the sheet show them
             where they are (core-v352) */
          /* a load-bearing wall is as thick as the shell (core-v347) */
          var ptt = p.bear ? wallThickness() : pt;
          /* core-v815: a wall of its own thickness (DUPLEX's party wall) */
          if (typeof p.t === 'number' && p.t > 0) ptt = p.t;
          /* A ROOM LINE IS A LINE (core-v536): two rooms with no wall between
             them, so it has no thickness and the sheet draws it dashed */
          if (p.sep) ptt = 0;
          if (p.dir === 'v') out.parts.push({ a: [p.at, a0], b: [p.at, a1], t: ptt, bear: !!p.bear, sep: !!p.sep });
          else out.parts.push({ a: [a0, p.at], b: [a1, p.at], t: ptt, bear: !!p.bear, sep: !!p.sep });
        }
        var PL = _P(), R = (PL && PL.rooms) ? (PL.rooms() || []) : [];
        for (i = 0; i < R.length; i++) {
          var rc = R[i].rects || [], ar = 0, box = null;
          for (j = 0; j < rc.length; j++) {
            ar += Math.max(0, rc[j].x1 - rc[j].x0) * Math.max(0, rc[j].z1 - rc[j].z0);
            box = box ? { x0: Math.min(box.x0, rc[j].x0), x1: Math.max(box.x1, rc[j].x1),
                          z0: Math.min(box.z0, rc[j].z0), z1: Math.max(box.z1, rc[j].z1) }
                      : { x0: rc[j].x0, x1: rc[j].x1, z0: rc[j].z0, z1: rc[j].z1 };
          }
          if (!box) continue;
          /* THE NAME COMES FROM THE ASSET, not from a field guessed off the
             record: roomName(room, index) is what the plan and the fit buttons
             already call, and it answers in the page's language. Guessing left
             every name blank and the sheet showed bare areas (Marek: pridat do
             pudorysu room names). */
          var nm = '';
          try { nm = (PL && PL.roomName) ? (PL.roomName(R[i], i) || '') : ''; } catch (eN) { nm = ''; }
          if (!nm) { try { nm = _planT(R[i].i18n || R[i].key || '', R[i].def || R[i].name || '') || (R[i].name || ''); } catch (eN2) { nm = R[i].name || ''; } }
          out.rooms.push({ name: nm, area: ar, box: box, rects: rc.slice() });
        }
        _log('pdf plan: ' + out.walls.length + ' wall(s), ' +
             out.walls.reduce(function (n, w2) { return n + w2.openings.length; }, 0) + ' opening(s), ' +
             out.parts.length + ' partition(s), ' + out.rooms.length + ' room(s) -- dimensioned plan sheet');
        return out;
      } catch (ePS) { _log('pdf plan: ' + (ePS && ePS.message)); return null; }
    }

    function _pdfBoqParts() {
      var defs = [
        { key: 'main', title: _t('structure', 'STRUCTURE') },
        { key: 'terrace', title: _t('rooms.terrace', 'Terrace') },
        { key: 'pergola', title: _t('pergola', 'PERGOLA') }
      ];
      var out = [], i, rows;
      for (i = 0; i < defs.length; i++) {
        rows = _pdfBoq(defs[i].key);
        if (rows.length) out.push({ key: defs[i].key, title: defs[i].title, rows: rows });
      }
      _log('pdf bill: ' + out.map(function (p) { return p.title + ' ' + p.rows.length; }).join(', ') +
           ' -- one bill per part, the building first');
      return out;
    }

    /* =========================================================================
       THE PAINT, COUNTED OFF THE MODEL (core-v121).

       The shed billed 21 sqft of coating for a whole building. It was collecting
       the area by hand in two places -- the cladding pass and the corner trims --
       so on a panel-sided shed, where the cladding pass does not run at all,
       only the trims were left. Everything else the building is made of was
       simply never asked about: the posts, the terrace, the frame under the
       floor, the frame of the walls, the rafters (Marek).

       So it is read off the model instead. Every timber body has a size; the
       surface of a box is arithmetic, and a body that is not drawn cannot be
       forgotten. What a painter actually coats:

         MFP00007  impregnation, ONE coat, every piece of structural timber,
                   brushed before the frame is closed in
         MFP00001  penetrating sealer, the exterior wood only
         MFP00002  top coat -- the row already carries TWO coats, so an
                   exposed surface that wants three is billed at 1.5x

       Exposed is decided by the layer the body is in, because that is what
       decides it on site: a stud inside a wall is impregnated and never seen
       again; a post, a deck board, a rafter tail over an overhang is weathered.
       ========================================================================= */
    var COAT_ENCLOSED = /floor|stud-wall|partition|ceiling/i;
    var COAT_WEATHERED = /terrace|base|deck/i;     /* three coats: rain sits on these */



    /* WHAT GETS PAINTED IS NOT THE SAME AS WHAT IS A PROFILE.

       The coating pass asked _isTimber, which answers "is this a structural
       profile" -- and so the facade cladding and the corner trims, the two most
       weathered wooden surfaces on the building, were never coated at all. They
       are not profiles, so that test was right to say no; the pass was wrong to
       be asking it.

       Paint follows wood, whatever shape it is cut into. Boards, trims and
       decking are painted and the structure is painted; sheet goods behind a
       skin are not, and neither is anything that is not wood (Marek: vse
       precizne vykazovat). */
    function _isCoated(code) {
      if (_isTimber(code)) return true;
      var row = null;
      try { row = _catRow(code); } catch (e) {}
      var cat = String((row && row[1]) || '').toUpperCase();
      if (/FURNITURE|SANITARY|APPLIANCE|KITCHEN|LIGHTING|ELECTRIC/.test(cat)) return false;
      var nm = String((row && row[3]) || '').toUpperCase();
      if (!nm) return false;
      /* wood by name, in the shapes that are not profiles */
      return /WOOD (FACADE )?CLADDING|WOOD CORNER TRIM|DECKING|SHIPLAP|SIDING|WOOD PANEL/.test(nm);
    }

    /* a dimension cell, in metres, whatever unit it is written in */
    function _dimM(v) {
      var s = String(v == null ? '' : v).trim();
      var m = s.match(/^([\d.]+)\s*(mm|cm|m)?$/i);
      if (!m) return 0;
      var n = parseFloat(m[1]);
      var u = (m[2] || 'mm').toLowerCase();
      return n * (u === 'm' ? 1 : (u === 'cm' ? 0.01 : 0.001));
    }

    /* WHICH LAYER CARRIES THIS ID, IF ANY.

       This existed and I deleted it by accident: it sat between a comment and
       the orphan helper I was removing, and the cut took the comment, the
       orphan and this with it. The coating pass then threw on every build and
       the log said so -- "coating threw: _layerById is not defined" -- which is
       the only reason it did not disappear silently. */
    function _layerById(id) {
      var i;
      for (i = 0; i < _layers.length; i++) if (_layers[i] && _layers[i].id === id) return _layers[i];
      return null;
    }

    function _coatingBill(P) {
      /* the registration and the first attempt; the layer recomputes on demand */
      _coatRegister(P);
      _coatCompute(P);
    }

    function _coatRegister(P) {
      /* PAINTED SURFACE COMES OFF THE BILL, NOT OFF THE BODIES.

         This measured the bounding box of every drawn body and added up the
         six faces. Two things went wrong with that, and together they made the
         figure two and a half times too large: a member drawn as a run of
         segments counts an end face at every joint that does not exist in
         timber, and the roof alone is 855 bodies; and a cladding board counted
         its back as well as its face.

         The bill already knows the honest quantity: metres of each profile and
         square metres of each board. Metres times the perimeter of its own
         section is the surface of that profile -- the section is in the
         catalogue, so nothing is assumed. A board is painted on the face it
         shows. (Marek: vse precizne vykazovat.) */
      var L = PRODUCT + '-coating';

      if (!_layerById(L)) {
        try {
          registerLayer({
            id: L,
            version: CORE_VERSION,
            role: 'finishes',
            claims: ['coating'],
            /* ASKED WHEN THE BILL IS READ, NOT WHEN THE MODEL IS BUILT.

               The surface comes off the catalogue -- the unit of each code and
               the section of each profile -- and the catalogue arrives after the
               first build. So computing it in the post-build pass found no rows,
               skipped every code, and reported "nothing coatable"; the paint
               vanished from a 149-row bill entirely.

               A layer is asked for its rows when the budget is assembled, which
               is the moment the catalogue certainly exists. So it counts then.
               (Marek: vse precizne vykazovat.) */
            boq: function () {
              try { _coatCompute(null); } catch (eCC) {}
              return _rows(L);
            }
          });
        } catch (eRL) { if (P) P('coating  could not register its layer: ' + ((eRL && eRL.message) || eRL)); }
      }

    }

    function _coatCompute(P) {
      /* AND IT REPORTS THE GEOMETRY BESIDE THE BILL, so the two can be argued
         with instead of trusted.

         The areas from geometry and the lengths in the bill disagree on four
         codes by factors of five and nineteen, and I cannot yet say which side
         is wrong -- my own area sum was two and a half times out a day ago, so
         it is as likely a suspect as the bill. What settles it is the shape of
         the disagreement: how many bodies there are, how long the longest one
         is, and what they add up to. Bodies of the wrong SIZE and bodies of the
         wrong NUMBER are different faults with different causes.

         Nothing here changes a quantity. It measures and prints (Marek: musime
         presne vykazovat). */
      var L = PRODUCT + '-coating';
      var COATS = {
        MFP00007: 'Wood preservative impregnation',
        MFP00001: 'Penetrating wood sealer',
        MFP00002: 'Exposed wood coating',
        WIS00001: 'Interior paint'
      };
      var ck;
      for (ck in COATS) {
        if (COATS.hasOwnProperty(ck) && !CATALOG[ck]) {
          CATALOG[ck] = { code: ck, unit: 'm2', label: COATS[ck] };
        }
      }

      var st = _stage();
      if (!st || !st.group) { if (P) P('coating  no model to measure'); return; }

      /* what every other layer has billed, so a finish can be worked out from it */
      var billedNow = {}, blay, bb2, bk2;
      for (blay in _bill) {
        if (!_bill.hasOwnProperty(blay) || blay === L) continue;
        bb2 = _bill[blay];
        for (bk2 in bb2) {
          if (!bb2.hasOwnProperty(bk2)) continue;
          var bc2 = (CATALOG[bk2] && CATALOG[bk2].code) ? CATALOG[bk2].code : bk2;
          billedNow[bc2] = (billedNow[bc2] || 0) + bb2[bk2];
        }
      }

      /* WOOD IS DECIDED WITHOUT THE CATALOGUE. _isCoated asks _catRow, which
         reads productData -- and productData is built FROM what we publish, so
         at this moment it is empty and every board and trim fell out. The
         registered label is here at build time and says the same thing. */
      function _woodHere(code) {
        var cat = CATALOG[code] || null;
        var nm = String((cat && cat.label) || '').toUpperCase();
        if (/GYPSUM|OSB|CEMENT|MEMBRANE|FOIL|WOOL|INSULAT|TILE|GLASS|STEEL|METAL|SCREW|NAIL|FLASHING|GUTTER/.test(nm)) return false;
        if (/LUMBER|TIMBER|BATTEN|CLADDING|DECKING|TRIM|SHIPLAP|SIDING|PLYWOOD|WOOD/.test(nm)) return true;
        try { if (_isCoated(code)) return true; } catch (eW) {}
        return false;
      }

      var area = {}, where = {}, count = {}, longest = {}, run = {}, bodies = 0, skipped = 0;
      /* BY THE LAYER IT STANDS IN, NOT BY THE CODE (core-v661, RANCH thread,
         opt-in __PROD._coatByLayer). One profile in two places -- the RANCH's
         60x160 is both the studs inside the wall and the truss chords, its
         40x60 the service battens and the battens behind the cladding -- was
         sealed and top-coated as a whole the moment one place of it was not
         enclosed: 972 m2 of exterior coating on a house with 200 m2 of
         exterior wood. With the switch each code's area is kept per layer and
         each part goes where its layer says; _coatEnclosed.test is handed
         (layer, code), so a product can enclose one code in a layer and not
         another. Without it, as before. */
      var byL = false, areaCL = {};
      try { byL = !!(__PROD && __PROD._coatByLayer); } catch (eBL) { byL = false; }
      st.group.traverse(function (o) {
        if (!o.isMesh || !o.geometry) return;
        var m = o.userData && o.userData.asm;
        var tag = m ? (typeof m === 'string' ? m : (m.code || m.key || '')) : '';
        if (!tag) return;
        var code = (CATALOG[tag] && CATALOG[tag].code) ? CATALOG[tag].code : tag;
        if (!_woodHere(code)) { skipped++; return; }

        var d;
        try {
          var g = o.geometry;
          if (!g.boundingBox) g.computeBoundingBox();
          var bb = g.boundingBox, sc = o.scale || { x: 1, y: 1, z: 1 };
          d = [Math.abs(bb.max.x - bb.min.x) * (sc.x || 1),
               Math.abs(bb.max.y - bb.min.y) * (sc.y || 1),
               Math.abs(bb.max.z - bb.min.z) * (sc.z || 1)];
        } catch (eG) { return; }
        d.sort(function (a, b) { return b - a; });
        var len = d[0], wid = d[1], thk = d[2];
        if (!(len > 0.001 && wid > 0.0001)) return;

        var a;
        if (thk < 0.0005) a = len * wid;
        else if (thk < wid / 6) a = len * wid + 2 * (len + wid) * thk;
        else a = 2 * (wid + thk) * len + 2 * wid * thk;

        /* core-v611 (barndo thread): A BODY POURED FROM MANY PIECES -- the
           barndo merges a wall's girts, a post's plies, into one mesh -- was
           measured as one box the size of the wall: 1755 m2 of one profile off
           three bodies, and a window on the gable added 112 m2 of paint. Such a body
           hands its own pieces' surface, worked out by the rule above
           (userData.coatA m2, coatRun / coatMax m, coatN pieces); a body
           without it is measured as before (Marek: vse precizne vykazovat). */
        var u9 = o.userData, nPc = 1, lMax = len;
        if (u9 && typeof u9.coatA === 'number' && isFinite(u9.coatA) && u9.coatA >= 0) {
          a = u9.coatA;
          if (u9.coatRun > 0) len = u9.coatRun;
          lMax = (u9.coatMax > 0) ? u9.coatMax : len;
          if (u9.coatN > 0) nPc = u9.coatN;
        }
        area[code] = (area[code] || 0) + a;
        count[code] = (count[code] || 0) + nPc;
        run[code] = (run[code] || 0) + len;
        if (!(longest[code] > lMax)) longest[code] = lMax;
        var lay = _layerOf(o) || '';
        if (!where[code]) where[code] = [];
        if (where[code].indexOf(lay) < 0) where[code].push(lay);
        if (byL) { if (!areaCL[code]) areaCL[code] = {}; areaCL[code][lay] = (areaCL[code][lay] || 0) + a; }
        bodies++;
      });

      /* what the bill says for the same codes, from our own rows */
      var billed = {}, lay2, b2, kk;
      for (lay2 in _bill) {
        if (!_bill.hasOwnProperty(lay2) || lay2 === L) continue;
        b2 = _bill[lay2];
        for (kk in b2) {
          if (!b2.hasOwnProperty(kk)) continue;
          var c2 = (CATALOG[kk] && CATALOG[kk].code) ? CATALOG[kk].code : kk;
          billed[c2] = (billed[c2] || 0) + b2[kk];
        }
      }

      /* THE PRODUCT SAYS WHAT IS INSIDE (core-v516, from the T house thread,
         Marek: uctovat i naterove materialy). The layer names decided what
         stands outdoors, and a masonry house's roof frame and its T&G lining
         -- inside the walls, under the deck -- were coated as fences. A
         product may hand over its own pattern of enclosed layers
         (__PROD._coatEnclosed), the outdoor area it knows of that the layers
         hide (__PROD._coatOutside: the rafter tails past the wall) and what
         its indoor timber gets (__PROD._coatInterior: a code, or none and a
         note) -- the impregnation stays on everything. */
      var encRe = COAT_ENCLOSED, outExtra = 0, outNote = '', inside = 0, IH = null;
      try { if (__PROD && __PROD._coatEnclosed && __PROD._coatEnclosed.test) encRe = __PROD._coatEnclosed; } catch (eE1) {}
      try { if (__PROD && typeof __PROD._coatOutside === 'function') { var oe = __PROD._coatOutside(); if (oe && oe.area > 0) { outExtra = +oe.area; outNote = oe.note || ''; } } } catch (eE2) {}
      try { if (__PROD && __PROD._coatInterior) IH = __PROD._coatInterior; } catch (eE3) {}
      var impregnate = 0, sealer = 0, topcoat = 0, lines = [], cmp = [], c, q;
      for (c in area) {
        if (!area.hasOwnProperty(c)) continue;
        var a2 = area[c];
        if (!(a2 > 0.01)) continue;
        var outside = false, weathered = false;
        if (byL && areaCL[c]) {
          var lk, aIn = 0, aOut = 0, aWx = 0;
          for (lk in areaCL[c]) {
            if (!areaCL[c].hasOwnProperty(lk)) continue;
            var al = areaCL[c][lk];
            if (encRe.test(lk, c)) aIn += al;
            else if (COAT_WEATHERED.test(lk)) aWx += al;
            else aOut += al;
          }
          impregnate += a2;
          sealer += aOut + aWx; topcoat += aOut + aWx * 1.5; inside += aIn;
          lines.push(c + ' ' + (Math.round(a2 * 10) / 10) + ' (i ' + (Math.round(aIn * 10) / 10) + ' X ' + (Math.round(aOut * 10) / 10) + ' W ' + (Math.round(aWx * 10) / 10) + ')');
        } else {
          for (q = 0; q < where[c].length; q++) {
            if (!encRe.test(where[c][q])) outside = true;
            if (COAT_WEATHERED.test(where[c][q])) weathered = true;
          }
          impregnate += a2;
          if (outside) { sealer += a2; topcoat += weathered ? a2 * 1.5 : a2; } else inside += a2;
          lines.push(c + ' ' + (Math.round(a2 * 10) / 10) + (outside ? (weathered ? ' W' : ' X') : ' i'));
        }

        cmp.push(c + ': ' + count[c] + ' bod, longest ' + (Math.round(longest[c] * 100) / 100) +
                 ' m, model run ' + Math.round(run[c]) + ' m, billed ' +
                 (billed[c] == null ? '(not in the bill)' : (Math.round(billed[c]) + ' ' +
                   String((CATALOG[c] && CATALOG[c].unit) || '').toLowerCase())));
      }

      if (!lines.length) {
        if (P) P('coating  nothing coatable -- ' + bodies + ' bod(ies), ' + skipped + ' not wood');
        return;
      }

      /* AND THE INTERIOR PAINT, WHICH THE CEILING WAS ADDING UP FOR EVERYBODY.

         The painted area was summed by the ceiling builder from its own boards
         and the partitions', and billed there -- so the inside face of every
         external wall went unpainted, and whether the partitions made it in at
         all depended on which layer ran first. Two rooms' worth of paint
         resting on build order (Marek: musime resit i vymalbu interierovych
         sten).

         Board is board: the bill knows how many square metres of plasterboard
         the house has, in every layer, and that is what gets painted. Counted
         here, where the order cannot matter. Tiled walls are not painted, so
         the tile area comes off. */
      /* WHICH TILE COVERS THE BOARD IS THE PRODUCT'S TO SAY (core-v661, RANCH
         thread). /TILE/ took every m2 row with the word in its label off the
         board -- the floor tiles, a roof covering named 'Roof tiles /
         covering', the GEOTEXTILE -- and the RANCH's 450 m2 of board came to
         158 m2 of paint. A product may hand over the pattern of the tiles
         that lie on its boards (__PROD._paintTileRe, read on the upper-cased
         label); without it, as before. */
      var tileRe = /TILE/;
      try { if (__PROD && __PROD._paintTileRe && typeof __PROD._paintTileRe.test === 'function') tileRe = __PROD._paintTileRe; } catch (eTR) {}
      var paintA = 0, tiledA = 0, pk;
      for (pk in billedNow) {
        if (!billedNow.hasOwnProperty(pk)) continue;
        var pr = CATALOG[pk] || null;
        var pn = String((pr && pr.label) || '').toUpperCase();
        if (/GYPSUM|PLASTERBOARD/.test(pn)) paintA += billedNow[pk];
        if (tileRe.test(pn) && String((pr && pr.unit) || '') === 'm2') tiledA += billedNow[pk];
      }
      paintA -= tiledA;
      if (paintA < 0) paintA = 0;

      /* the outdoor timber the product knows of inside an enclosed layer */
      if (outExtra > 0) { sealer += outExtra; topcoat += outExtra; }
      _bill[L] = {};
      var put = function (code, v) { if (v > 0.005) _bill[L][code] = Math.round(v * 100) / 100; };
      put('MFP00007', impregnate);
      put('MFP00001', sealer);
      put('MFP00002', topcoat);
      put('WIS00001', paintA);
      var intNote = '';
      if (IH && inside > 0.01) {
        var fIn = (typeof IH.factor === 'number') ? IH.factor : 1;
        if (IH.code) { put(IH.code, inside * fIn); intNote = ' | ' + IH.code + ' ' + Math.round(inside * fIn) + ' m2 interior wood finish'; }
        else intNote = ' | interior wood finish ' + Math.round(inside) + ' m2 NOT BILLED -- ' + (IH.note || 'no catalogue row');
      }

      /* ONE LINE. He copies the whole log and cannot select part of it, so every
         line I add costs him -- and I added a per-code list and a per-code
         comparison, fifteen lines between them. The three areas are the answer;
         the working out goes to the console, where it costs nothing (Marek). */
      if (P) {
        P('coating  ' + (byL ? 'by layer (core-v661) ' : '') + 'MFP00007 ' + Math.round(impregnate) + ' m2 | MFP00001 ' +
          Math.round(sealer) + ' m2 | MFP00002 ' + Math.round(topcoat) +
          ' m2 | WIS00001 ' + Math.round(paintA) + ' m2 interior paint' +
          (tiledA > 0.5 ? (' (' + Math.round(tiledA) + ' m2 tiled, not painted)') : '') +
          ' | from ' + bodies + ' wooden bod(ies), ' + Math.round(inside) + ' m2 of them inside' +
          (outExtra > 0 ? (' + ' + Math.round(outExtra) + ' m2 outside by the product (' + outNote + ')') : '') + intNote);
        try {
          _log('coating detail  ' + lines.sort().join(' '));
          cmp.sort();
          for (q = 0; q < cmp.length; q++) _log('coating  ' + cmp[q]);
        } catch (eCL) {}
      }
    }

    /* WHAT BELONGS ON A CUT LIST, AND WHAT DOES NOT.

       A cut list is for the pieces somebody stands at a saw and cuts: the
       structural profiles of the frame. Cable is pulled, sill seal is rolled
       out, battens and trims are bought long and trimmed on site by eye, and a
       window sill is a fitting. Listing those turned twenty pages of the
       document into a catalogue of everything with a length, and buried the
       one list that is actually worked from (Marek: jen hlavni profily nosne
       konstrukce, battens se take neresi na ks).

       So the test is the catalogue's own name: DIMENSIONAL LUMBER is a
       structural profile and nothing else is. Everything with a length still
       appears in the SHOPPING list, where knowing how many bars to buy is the
       whole point. */
    function _isCutStock(code, row) {
      var u = String((row && (row[12] || row[13])) || '').toUpperCase().replace(/\s/g, '');
      if (u !== 'M' && u !== 'FT') return false;         /* linear only */
      var nm = String((row && row[3]) || '').toUpperCase();
      if (!nm) return /^KVH/.test(String(code || ''));   /* no sheet: the framing prefix */
      if (!/LUMBER|TIMBER BEAM|GLULAM|RAFTER|JOIST|STUD|POST|TRUSS CHORD/.test(nm)) return false;
      /* named like timber but not cut like it */
      return !/BATTEN|FURRING|TRIM|PROFILE|SEAL|CABLE|GUTTER|FLASHING|SILL|TAPE|STRIP|CONDUCTOR|DECKING/.test(nm);
    }

    /* WHAT TO CUT, READ OFF THE MODEL.

       Only the linear items -- a sheet or a bag of screws has no length worth
       cutting -- and each body by its longest side, which for a stud, a rafter
       or a batten IS the cut. Grouped by profile, sorted long to short, which
       is the order somebody actually cuts in. */
    /* =========================================================================
       THE TRUSS SHEET (core-v204, step 2).

       A truss is already an elevation: every member is stored as two points in
       the truss's own vertical plane. So the drawing needs no new geometry, only
       a scale and a page -- the length of a member, its slope, and the angle at
       a joint all fall out of those four numbers.

       Shapes come from the product, which knows when it built them; the core
       only measures and lays out. Identical trusses are one drawing with a
       count, the way the cut list already treats identical lengths (Marek). */
    var _trussPub = [];

    function reportTrusses(list) {
      _trussPub = (list && list.length) ? list : [];
      return _trussPub.length;
    }

    /* ======================================================================
       THE TRUSS RECORDER (core-v254) -- what the truss sheets are made of,
       moved here from the ADU lib so the shed and the sauna record their
       trusses the same way. A product still builds its own roof; while it does,
       it writes each truss (or each member of a hip end) into a recorder, and
       at the end hands the recorder's shapes to reportTrusses().

       What it does, unchanged from the ADU:
         faceOutline   one side face of a finished body, as the drawing's outline
         collect/sig   a standard truss as a shape; the same shape is one sheet
                       with a count, and every body carries the shape's mark
         hipRec/hipPush/hipGroup
                       the members of a hip end, grouped into the short trusses
                       they are and flattened square on each one's own plane
         add           the merge both of the above share

       The product passes its sections: opt.sections() returns { web, chord }
       ({ d, t, code } each) and is read at call time, so it may name constants
       the product defines further down its file. opt.hipCodes { web, chord }
       names the codes a hip end's members carry on the sheet, where a product
       records them under codes of their own. */
    /* lenAxis says which way the member lies inside its own box and which face
       is the one to read: 2 = length along Z, 0 = length along X, and, added in
       core-v301 for the uprights, 1 = length along Y with its thickness on X,
       3 = length along Y with its thickness on Z. An upright is built as a box
       standing on end, so before this it had no outline at all and its sheet
       drew it as a plain rectangle -- the bevelled head it wears in the model
       was nowhere on the drawing (Marek). */
    function faceOutline(geo, lenAxis, thickHalf) {
      try {
        var pos = geo.attributes.position, i, pts = [], seen = {};
        for (i = 0; i < pos.count; i++) {
          var tv = (lenAxis === 2 || lenAxis === 1) ? pos.getX(i) : pos.getZ(i);
          if (Math.abs(tv - thickHalf) > 1e-6) continue;       /* one face only */
          var a = (lenAxis === 2) ? pos.getZ(i) : ((lenAxis === 0) ? pos.getX(i) : pos.getY(i));
          var b = (lenAxis === 2 || lenAxis === 0) ? pos.getY(i)
                : ((lenAxis === 1) ? pos.getZ(i) : pos.getX(i));
          var k = Math.round(a * 10000) + ':' + Math.round(b * 10000);
          if (seen[k]) continue;
          seen[k] = 1;
          pts.push([a, b]);
        }
        if (pts.length < 3) return null;
        var cx = 0, cy = 0, q;
        for (q = 0; q < pts.length; q++) { cx += pts[q][0]; cy += pts[q][1]; }
        cx /= pts.length; cy /= pts.length;
        pts.sort(function (u, v) {
          return Math.atan2(u[1] - cy, u[0] - cx) - Math.atan2(v[1] - cy, v[0] - cx);
        });
        return pts;
      } catch (eFO) { return null; }
    }

    /* THE TRUSS KIT (core-v257) -- THE SHAPE OF A TRUSS AND THE CUT OF EVERY
       MEMBER, SHARED.

       _fink lays out a standard truss in its own plane (chords, tie, king post,
       the uprights and diagonals, the nodes that take plates) and _inTruss makes
       one member as a body with its ends cut -- plumb at the apex, level on the
       tie at the heel, square or bevelled where a web dies on a chord or a cheek.
       Both lived in the ADU lib and had been copied into the shed at an older
       stage; they are moved here verbatim so the ADU, the shed and the sauna
       build the same truss from one copy -- and so the fixes to come (the webs
       cut by both chords along their whole length, the outer pair dropped where
       there is no room for it) land once (Marek: vsechno idealne v ui asset -
       pro sdileni).

       The product passes its sections: opt.sections() returns { chord, web,
       plate }, read at every call. The state the two keep -- the last body made,
       the geometry list for the log, the floor-cut counters, the fit report --
       lives inside the kit, and the product reads it through the getters. */
    function trussKit(opt) {
      opt = opt || {};
      var _lastBody = null, _trussGeom = [], _trussFloorCut = { n: 0, max: 0 }, _trussFit = {};
      var _webClip = { n: 0, cut: 0, worst: 0, after: 0, noRoom: 0, asym: 0, areas: {} };
      var _outerRule = { n: 0, dropped: 0, clearLo: 1e9, clearHi: -1e9, min: 0.200 };

      /* A WEB LIES BETWEEN THE TWO CHORDS, OVER ITS WHOLE LENGTH (core-v258).

         A web's ends were shaped where it meets a chord or a cheek, and nothing
         else held it: where there is little room -- the outer panel of a truss
         at a low pitch has 12 mm between the chords on a 3.9 m span at 15
         degrees -- the end shaping gave up and the web ran on into the tie, 80
         mm deep, and a flat diagonal sliced through the tie along its length
         (Marek: male zelene a male violet se generuji blbe). So after everything
         else, a web's side profile is clipped by the tie's top face and by the
         top chord's underside, as the half-planes they are, and the body is
         rebuilt as that profile with the web's thickness. Nothing is moved that
         was already between them; what reached past is cut off, exactly on the
         face. Counted for the log: how many were cut, the deepest overrun that
         was taken off, what is left after (0), and any web left with no room. */
      function _clipPoly(poly, f) {
        var out = [], n = poly.length, i, a, b, fa, fb, tt;
        for (i = 0; i < n; i++) {
          a = poly[i]; b = poly[(i + 1) % n]; fa = f(a); fb = f(b);
          if (fa >= 0) out.push(a);
          if ((fa >= 0) !== (fb >= 0)) {
            tt = fa / (fa - fb);
            out.push([a[0] + tt * (b[0] - a[0]), a[1] + tt * (b[1] - a[1])]);
          }
        }
        return out;
      }
      function _prism(T, pts, h) {
        var P = [], i, n = pts.length;
        function tri(a, b, c) { P.push(a[0], a[1], a[2], b[0], b[1], b[2], c[0], c[1], c[2]); }
        for (i = 1; i < n - 1; i++) {
          tri([pts[0][0], pts[0][1], h], [pts[i][0], pts[i][1], h], [pts[i + 1][0], pts[i + 1][1], h]);
          tri([pts[0][0], pts[0][1], -h], [pts[i + 1][0], pts[i + 1][1], -h], [pts[i][0], pts[i][1], -h]);
        }
        for (i = 0; i < n; i++) {
          var a = pts[i], b = pts[(i + 1) % n];
          tri([a[0], a[1], h], [a[0], a[1], -h], [b[0], b[1], -h]);
          tri([a[0], a[1], h], [b[0], b[1], -h], [b[0], b[1], h]);
        }
        var g2 = new T.BufferGeometry();
        g2.setAttribute('position', new T.Float32BufferAttribute(P, 3));
        g2.computeVertexNormals();
        return g2;
      }
      /* A WEB IS ITS STRAIGHT BODY, CUT BY THE FACES IT MEETS (core-v259).

         The first cut (v258) worked on the outline the end shaping had already
         made, read back off the body -- and that shaping was not symmetric: the
         two halves of a truss are drawn in opposite directions and the end
         offsets came out differently on each side, so the right-hand diagonals
         differed from the left (Marek: asymetrie, prava vzpera spatne, leva
         dobra). Reading the outline back also lost its order where it was not
         convex. So a web is no longer shaped and then trimmed: it is made from
         its own straight body, run on a little past both ends, and cut by every
         face that bounds it -- the tie's top, the top chord's underside, and,
         for a sloping web, the cheeks of the two uprights it runs between. The
         two halves are cut by mirror-image faces, so they are mirror images;
         every bearing is exactly on its face; and the shape is convex, so the
         outline cannot be read back wrong. The axis is still the one the layout
         chose, so the bearing balance on the cheek is kept. */
      function _clipWeb(T, geo, sect, ang, cy, clip, len, part) {
        if (!clip || !clip.base) return geo;
        /* WORKED IN THE TRUSS'S OWN FRAME, CENTRED ON ITS RIDGE (core-v260).

           A roof with a longer overhang on one side builds its trusses about
           their own middle and then slides them by half the difference. The
           members were slid; the faces they were cut by were not -- the ridge,
           the chords' underside and every upright stood where a symmetric truss
           would have had them, so every web was cut against faces that were
           not there (Marek: ted jsou krome stredove modre rozbite vsechny). The
           member's position is taken back into the truss's own frame first. */
        var B = clip.base, pc = clip.pc - (B.dx || 0), cA = Math.cos(ang), sA = Math.sin(ang);
        var side = (pc >= 0) ? 1 : -1, k = (B.hOut > 0) ? (B.rise / B.hOut) : 0;
        var c0 = B.eaveY + B.rise - B.chordHalfV;
        var fs = [];
        fs.push(function (q) { return q[1] - B.floor; });                       /* on or above the tie */
        fs.push(function (q) { return (c0 - side * q[0] * k) - q[1]; });        /* under the chord */
        if (part === 'web' && B.ups && B.ups.length) {
          /* between the two uprights either side of it: the nearest cheek on each side */
          var lo = -1e9, hi = 1e9, iu;
          for (iu = 0; iu < B.ups.length; iu++) {
            var U = B.ups[iu];
            if (U.p < pc && U.p + U.h > lo) lo = U.p + U.h;
            if (U.p > pc && U.p - U.h < hi) hi = U.p - U.h;
          }
          if (lo > -1e8) fs.push(function (q) { return q[0] - lo; });
          if (hi < 1e8) fs.push(function (q) { return hi - q[0]; });
        }
        /* the plain body, run on by a member depth at each end so every bounding
           face is reached before the body ends */
        var hL = len / 2 + sect.d, hD = sect.d / 2;
        var rect = [[-hL, -hD], [hL, -hD], [hL, hD], [-hL, hD]], P = [], i, j;
        for (i = 0; i < rect.length; i++) {
          P.push([pc + rect[i][0] * cA - rect[i][1] * sA, cy + rect[i][0] * sA + rect[i][1] * cA]);
        }
        _webClip.n++;
        var out = P;
        for (j = 0; j < fs.length; j++) { out = _clipPoly(out, fs[j]); if (out.length < 3) break; }
        var area = 0;
        for (i = 0; i < out.length; i++) {
          var o1 = out[i], o2 = out[(i + 1) % out.length];
          area += o1[0] * o2[1] - o2[0] * o1[1];
        }
        area = Math.abs(area) / 2;
        if (out.length < 3 || area < 1e-5) { _webClip.noRoom++; return geo; }
        var after = 0, L = [];
        for (i = 0; i < out.length; i++) {
          for (j = 0; j < fs.length; j++) after = Math.max(after, -fs[j](out[i]));
          var dp = out[i][0] - pc, dy = out[i][1] - cy;
          L.push([dp * cA + dy * sA, -dp * sA + dy * cA]);
        }
        _webClip.cut++;
        if (after > _webClip.after) _webClip.after = after;
        /* the two halves must come out the same: kept by where they stand */
        var key = part + ':' + Math.round(Math.abs(pc) * 1000);
        if (_webClip.areas[key] == null) _webClip.areas[key] = area;
        else _webClip.asym = Math.max(_webClip.asym, Math.abs(_webClip.areas[key] - area));
        return _prism(T, L, sect.t / 2);
      }
      function _faceOutline(geo, lenAxis, thickHalf) { return faceOutline(geo, lenAxis, thickHalf); }
      function _sec() {
        var s = (typeof opt.sections === 'function') ? opt.sections() : (opt.sections || {});
        return {
          chord: s.chord || { t: 0.060, d: 0.160, code: '' },
          web:   s.web   || { t: 0.060, d: 0.120, code: '' },
          plate: s.plate || { l: 0.250, w: 0.150, t: 0.012, sq: 0.254, code: '', codeBig: '' }
        };
      }

      function _fink(hWall, eaveY, ridgeY, nPanel, tail, tailB) {
        var SEC_ = _sec(), CHORD = SEC_.chord, WEB = SEC_.web, PLATE = SEC_.plate;
        var members = [], nodes = [], i, sgn;
        tail = tail || 0;
        if (typeof tailB !== 'number') tailB = tail;
        var _dx = (tailB - tail) / 2;
        tail = (tail + tailB) / 2;
        /* hOut is the truss's own half span -- out to the tip of the tail. hWall is
           still where it BEARS, which is what the heel plates and the wall care
           about. */
        var hOut = hWall + tail;
        var rise = ridgeY - eaveY;
        function topY(p) { return eaveY + (1 - Math.abs(p) / hOut) * rise; }
        function M(p1, y1, p2, y2, kind) { members.push({ p1: p1, y1: y1, p2: p2, y2: y2, kind: kind }); }

        /* THE TOP CHORDS RUN THROUGH TO THE AXIS AND MEET THERE.

           Drawn to the apex and cut square -- at right angles to their own length --
           two sloping members leave a wedge of daylight between them: the higher
           arris of one touches while the lower one is still short. A real chord is
           cut PLUMB, so the two faces are vertical and butt flat on the centre
           line.

           Rather than a shaped profile, each chord is carried past the apex by the
           run a plumb cut needs -- half its depth times the fall -- so the square
           ends overlap on the axis and the joint closes. What is left over is
           buried inside the other chord. */
        var mitre = (rise > 0) ? (CHORD.d / 2) * (rise / hOut) : 0;
        var mdy = (rise > 0) ? mitre * (rise / hOut) : 0;

        /* THE TAIL. The eave overhang is the chord itself carrying on past the
           wall -- that is what a truss tail IS, and it is why a trussed roof needs
           no rafter out there. It runs the full overhang, measured past the
           FINISHED face, so the facade's own thickness is in it too.

           THE GABLE IS THE OTHER CASE ENTIRELY. Nothing of the frame goes past a
           gable wall: no truss stands out there to carry it, and the rake overhang
           comes from the sheathing being run wide. Eave yes, gable no. */
        /* AND THE TAIL IS CUT PLUMB TOO, so the overhang is the number asked for
           and not that number plus a corner. A square end on a slope throws its
           lower outer corner out by half the depth times the sine of the pitch --
           31 mm here -- so the end is drawn back by exactly that. The fascia goes
           on the vertical face this leaves. */
        /* THE TRIANGLE IS SIMPLY BIGGER. Both chords end at the SAME point, out at
           the tip, and the tie is the base between those two points -- so the truss
           keeps the shape it had and nothing crosses anything.

           I had left the top chord's tip below the tie, which is where the two
           started cutting through each other: the chord was still falling to the
           old eave line while the tie had been carried out level. A tail is not a
           chord poking below a tie, it is the whole triangle grown by the overhang.

           The tip is still drawn back along the slope by the plumb cut, so the
           corner -- not the centreline -- is what lands on the overhang line. */
        var slopeU = Math.hypot(hOut, rise) || 1;
        var plumbRun = (CHORD.d / 2) * (rise / slopeU);
        var tipX = hOut - plumbRun;
        var tipY = eaveY + plumbRun * (rise / hOut);
        var tieX = hOut;
        M(-tipX, tipY, mitre, ridgeY + mdy, 'chord');
        M(tipX, tipY, -mitre, ridgeY + mdy, 'chord');
        /* THE TIE GOES OUT WITH THEM. A truss tail is not a rafter poking out of a
           roof -- the whole truss is longer, bottom chord included, and the two
           chords close on each other at the tip. That is what makes the overhang a
           triangle you can nail a soffit to rather than a lone board in the air.

           Its ends stop where the chords do, so the tail reads as one piece. */
        M(-tieX, eaveY, tieX, eaveY, 'tie');
        M(0, eaveY, 0, ridgeY, 'post');

        /* A SLOPING WEB DIES ON THE UPRIGHT, AND IT IS LAID DOWN ENOUGH TO BED
           ON IT.

           Drawn to the node itself -- the point where the upright meets the
           chord -- it arrives in the corner between the two and ends in a
           feather point, touching the upright along almost nothing. It is aimed
           at the upright's CHEEK instead, a web depth below where the chord's
           underside crosses it, so it lies down a little and beds on the upright
           over a face instead of a point. Where it then still reaches the chord,
           the chord cuts it -- both profiles do (Marek: chtelo by ji trosku
           polozit, aby se vetsi plochou dotykala cyan vzpery).

           The one at the ridge is the same rule with the king post in place of
           an upright: headP is the cheek it dies on, whichever piece that is. */
        /* every upright in the truss -- the king post and each vertical web -- as
           its axis and half its width across the truss; a sloping web lies
           between the two it runs from and to */
        function _upsOf() {
          var U = [{ p: 0, h: CHORD.d / 2 }], iu, su;
          for (su = -1; su <= 1; su += 2) {
            for (iu = 1; iu < nPanel; iu++) {
              if (dropOuter && iu === outerI) continue;
              U.push({ p: su * (iu / nPanel) * hOut, h: WEB.d / 2 });
            }
          }
          return U;
        }
        var cosPL = Math.cos(Math.atan2(rise, hOut));
        if (!(cosPL > 0.2)) cosPL = 1;
        var sinPL = Math.sin(Math.atan2(rise, hOut));
        var tanPL = (hOut > 0) ? (rise / hOut) : 0;
        var faceVL = (CHORD.d / 2) / cosPL;

        /* AND IT IS LAID DOWN EXACTLY AS FAR AS EVENS THE TWO BEARINGS.

           Drawn at the node it ends in a point on the upright; laid down by a
           fixed amount it beds well on the upright and barely on the chord. What
           settles it is not a number but a balance: the head is cut by both, and
           how much lands on each depends on where their corner falls ACROSS the
           member's depth. Put that corner where the two bearings come out the
           same length and there is nothing left to choose (Marek: idealne aby
           stycna plocha s magenta byla stejna jako se cyan).

           For a member of depth d meeting the chord at alpha and the cheek at
           beta, the bearings are (d/2 - q) / sin alpha and (d/2 + q) / sin beta,
           where q is the corner's offset from the axis. Equal gives

             q = (d/2) * (sin beta - sin alpha) / (sin alpha + sin beta)

           and since alpha and beta move as the member turns, the aim is settled
           by repeating it a few times -- it converges in two or three. */
        function _aimEven(fp, fy, cheekP, sgnL) {
          /* ONE SIDE IS WORKED OUT, THE OTHER IS ITS MIRROR (core-v259).

             The offset to the balancing corner is taken along the member's own
             left-hand normal, and a mirrored member's left hand is the other
             physical side -- so the right half of every truss aimed its sloping
             webs at a different height than the left, and the two halves came
             out different (Marek: asymetrie, prava vzpera spatne, leva dobra).
             The left half is the one that is right; the right half is now the
             left half's answer, mirrored -- a height does not change in a
             mirror. */
          if (sgnL > 0) return _aimEven(-fp, fy, -cheekP, -1);
          var hd = WEB.d / 2;
          var starP = cheekP;
          var starY = (ridgeY - faceVL) - Math.abs(cheekP) * tanPL;
          var hy = starY - WEB.d, it, dxv, dyv, L, ux, uy, sA, sB, qs, qx, qy, t;
          for (it = 0; it < 6; it++) {
            dxv = cheekP - fp; dyv = hy - fy; L = Math.sqrt(dxv * dxv + dyv * dyv);
            if (!(L > 0.05)) break;
            ux = dxv / L; uy = dyv / L;
            sA = Math.abs(ux * sinPL - uy * (-sgnL * cosPL));   /* against the chord */
            sB = Math.abs(ux);                                  /* against the cheek */
            if (!(sA > 0.02) || !(sB > 0.02)) break;
            qs = hd * (sB - sA) / (sA + sB);
            /* the axis must pass through the corner offset by that much */
            qx = starP - qs * (-uy); qy = starY - qs * ux;
            dxv = qx - fp; dyv = qy - fy; L = Math.sqrt(dxv * dxv + dyv * dyv);
            if (!(L > 0.05) || Math.abs(dxv) < 1e-6) break;
            t = (cheekP - fp) / (dxv / L);
            hy = fy + (dyv / L) * t;
            if (hy > starY - 0.005) hy = starY - 0.005;
            if (hy < fy + 0.10) hy = fy + 0.10;
          }
          return hy;
        }
        /* THE OUTER PAIR ONLY WHERE THERE IS ROOM FOR IT (core-v261).

           The outer upright stands two thirds of the way out, where the truss has
           a third of its height left; on a low pitch or a short span that is a
           few centimetres between the chords, and an upright and a diagonal
           squeezed into it carry nothing -- they were generated anyway and badly
           (Marek: u maleho sklonu se generuji navic a blbe; krajni vzpery
           nedavaji konstrukcne smysl). The rule is one number for the whole line:
           the outer upright and the outer diagonal are built only where the clear
           height between the tie's top and the top chord's underside, at the
           outer upright, is at least 200 mm (his choice). Otherwise the pair is
           left out -- and with it its plates and its rows in the bill, which are
           counted from the members that exist. The inner pair and the king post
           always stay. */
        var outerI = nPanel - 1, dropOuter = false, clearOuter = null;
        if (nPanel >= 3) {
          var pO = (outerI / nPanel) * hOut;
          clearOuter = (topY(pO) - faceVL) - (eaveY + CHORD.d / 2);
          dropOuter = clearOuter < _outerRule.min;
          _outerRule.n++;
          if (dropOuter) _outerRule.dropped++;
          if (clearOuter < _outerRule.clearLo) _outerRule.clearLo = clearOuter;
          if (clearOuter > _outerRule.clearHi) _outerRule.clearHi = clearOuter;
        }
        for (sgn = -1; sgn <= 1; sgn += 2) {
          for (i = 1; i < nPanel; i++) {
            if (dropOuter && i === outerI) continue;
            var p = sgn * (i / nPanel) * hOut;
            var prev = (i === 1) ? 0 : sgn * ((i - 1) / nPanel) * hOut;
            M(p, eaveY, p, topY(p), 'web');
            if (i === 1) {
              M(p, eaveY, prev, ridgeY, 'web');
              members[members.length - 1].headP = sgn * (CHORD.d / 2);
            } else {
              var cheekP = prev + sgn * (WEB.d / 2);
              M(p, eaveY, cheekP, _aimEven(p, eaveY, cheekP, sgn), 'web');
              members[members.length - 1].headP = cheekP;
            }
          }
        }

        /* every distinct point, and how many members reach it -- a point with two
           or more is a node and gets a plate on each face */
        var seen = {};
        function key(p, y) { return Math.round(p * 1000) + ':' + Math.round(y * 1000); }
        for (i = 0; i < members.length; i++) {
          var m = members[i];
          [[m.p1, m.y1], [m.p2, m.y2]].forEach(function (pt) {
            /* THE APEX IS ONE JOINT, however many member ends arrive there. Once the
               chords were carried past the centre line to close the mitre, their
               ends stopped landing on the same point as the king post's -- and three
               plates got stacked on top of each other at the ridge. Anything at or
               above ridge level is the apex, and the apex is on the axis. */
            var ap = (pt[1] >= ridgeY - 0.001);
            /* AND THE HEEL IS ONE JOINT TOO. Cutting the chord back so it stops at
               the wall moved its end off the tie's end by 34 mm -- so the heel
               scored as two joints, and the extra one carried a plate hanging 130 mm
               out past the wall. Anything at the eave within a plate's reach of the
               wall line IS the heel, and the heel is where the tie ends. */
            /* THE HEEL IS WHERE THE TIE ENDS, WHICH IS THE WALL -- not where the
               chord ends, which is now out at the end of the tail. A joint out
               there would be a plate hanging in the air. */
            /* THE TIP IS A JOINT AGAIN, because the two chords meet there now --
               and a point where a top chord lands on a bottom chord is exactly what
               a plate is for. */
            var hl = (!ap) && (Math.abs(pt[1] - eaveY) < 0.05) &&
                     (Math.abs(Math.abs(pt[0]) - hOut) < 0.10);
            /* AND IT SNAPS TO THE TIP, NOT TO THE WALL. I widened the test to the
               tip when the truss grew but left the snap on the old wall line, so
               the heel plate was parked 745 mm inboard -- on the bottom chord, at
               no joint at all. That is the loose plate. */
            var pp = ap ? 0 : (hl ? (pt[0] < 0 ? -hOut : hOut) : pt[0]);
            var py = ap ? ridgeY : (hl ? eaveY : pt[1]);
            var k = key(pp, py);
            if (!seen[k]) { seen[k] = { p: pp, y: py, n: 0, vert: 0, diag: 0 }; }
            seen[k].n++;
            /* WHICH KINDS OF WEB ARRIVE HERE, because the plate depends on it: a
               point where the chord takes both an upright AND a diagonal is a
               three-piece joint and wants the square; a point that takes only one
               of them is held by the narrow plate the rest of the truss uses
               (Marek: ty vetsi jen tam, kde se potkava pasnice se svislym i
               sikmou vzperou). */
            if (m.kind === 'web' || m.kind === 'post') {
              if (Math.abs(m.p2 - m.p1) < 0.001) seen[k].vert++;
              else seen[k].diag++;
            }
          });
        }

        /* A PLATE LIES ON A MEMBER, IT DOES NOT FLOAT AT A POINT.

           Every node is told which member it belongs to and which way that member
           runs, so the plate can be turned to match. Square plates dropped on
           sloping joints is why the corners were hanging out in the air.

           A node at the END of its member -- the apex, the two heels -- also gets
           pushed back INBOARD by half a plate, so the plate finishes flush with the
           timber instead of half of it reaching past the end. */
        var slope = Math.hypot(hWall, rise) || 1;
        /* A JOINT IS ANYWHERE A MEMBER ENDS, not only where two of them meet.

           Counting only points with two or more member ENDS missed three joints in
           every truss: the foot of the king post and the head of each outer
           vertical. A web ends there and lands on a chord that runs straight past,
           so the chord contributes no endpoint and the joint scored one. It still
           has to be fixed to something -- that is exactly what the plate is for. */
        for (var k2 in seen) {
          if (!seen.hasOwnProperty(k2)) continue;
          var nd = seen[k2];
          if (nd.y > ridgeY - 1e-6) {
            /* THE APEX PLATE IS ON THE AXIS AND SYMMETRIC. It used to be shoved
               down one chord, which put a plate on the right-hand rafter and
               nothing answering it on the left. It straddles the joint instead:
               upright, centred on the centre line, and sized from the chord depth
               so both its top corners stay under the timber. */
            nd.apex = true; nd.dp = 1; nd.dy = 0; nd.end = false;
          } else if (Math.abs(nd.y - eaveY) < 1e-6) {
            /* anything on the tie runs with the tie */
            nd.dp = (nd.p > 0) ? -1 : 1; nd.dy = 0;
            /* THE HEEL IS AT THE TIP, and this line was still measuring against the
               wall. The plate therefore never got its inboard shift and sat centred
               on the end of the bottom chord -- half of it hanging 150 mm past the
               timber, which is both the loose plate and the 745 mm the overhang was
               measuring instead of 595. */
            nd.end = Math.abs(Math.abs(nd.p) - hOut) < 1e-6;
          } else {
            /* a top-chord node: along that chord, downhill */
            var sg = (nd.p > 0) ? 1 : -1;
            nd.dp = sg * hOut / slope; nd.dy = -rise / slope; nd.end = false;
          }
          nodes.push(nd);
        }

        /* ONE JOINT, ONE PLATE.

           A panel point used to be a single spot: the upright's head and the
           diagonal's head met the chord at the same place. Since the diagonal is
           aimed at the upright's CHEEK and laid down until its two bearings even
           out, its head sits a little below and inboard of the upright's -- 137
           to 166 mm away on this truss. Both were kept as nodes, so both were
           plated, and the two plates lay across each other on the same post.

           They are one joint and they get one plate. Points closer together than
           a plate's own length are merged to their middle; the nearest points
           that are genuinely separate joints are a whole panel apart, so nothing
           real is swallowed (Marek: u jednoho sloupku dve stycnikove desky pres
           sebe). */
        (function mergeNodes() {
          var lim = PLATE.l, a2, b2, i2, j2;
          for (i2 = 0; i2 < nodes.length; i2++) {
            for (j2 = i2 + 1; j2 < nodes.length; j2++) {
              a2 = nodes[i2]; b2 = nodes[j2];
              if (!a2 || !b2) continue;
              if (a2.apex || b2.apex) continue;          /* the apex is its own case */
              var dd2 = Math.hypot(a2.p - b2.p, a2.y - b2.y);
              if (dd2 > lim) continue;
              a2.p = (a2.p + b2.p) / 2;
              a2.y = (a2.y + b2.y) / 2;
              a2.end = a2.end && b2.end;
              a2.vert = (a2.vert || 0) + (b2.vert || 0);
              a2.diag = (a2.diag || 0) + (b2.diag || 0);
              nodes.splice(j2, 1);
              j2--;
            }
          }
        })();

        /* =====================================================================
           THE WEBS ARE CUT BETWEEN THE FACES, NOT DRAWN THROUGH THE AXES.

           Every member here runs centreline to centreline, which is how a truss
           is DESIGNED and not how one is BUILT. A web drawn from the tie's axis
           to the top chord's axis buries 80 mm in one and 89 mm in the other:
           on screen the pieces interpenetrate, and the length that goes to the
           bill and the cut list is 169 mm too long on every web in the roof.

           A real truss has its chords running whole and its webs cut to fit
           between them. So each web is shortened to where its own axis crosses
           the FACE of the member it lands on -- the top of the tie, the
           underside of the top chord. The chords are left alone: they are the
           pieces that run through (Marek).

           This is the first of the three steps we agreed: the cut, then the
           lengths that follow from it, then the drawing. */
        (function trimWebs() {
          /* A MEMBER DIES ON THE FACE IT MEETS, AND THE FACE DECIDES BOTH THINGS.

             There are two ways an end can be wrong and this had both.

             WHERE it stops: the end was dropped by the VERTICAL distance to the
             chord's underside, taken at the p it had before it moved. Sliding
             back along its own axis changes p, and on a sloping chord a
             different p is a different height -- so the axis end came to rest
             44 mm inside the chord on a shallow pitch and further on a steep
             one. The axis now meets the face where it really crosses it: two
             lines, one intersection, no distance measured twice.

             HOW it is cut: the saw angle was computed as a deviation from
             square, which is a MAGNITUDE. Half the members lean the other way
             and needed the same angle with the other sign, so on those the end
             face tilted the wrong way and drove the far corner straight through
             the chord instead of pulling it clear. Measured on this build at 26
             degrees: 152 mm into a chord 178 mm deep, and 123 mm below the
             tie's top face -- which is why the orange crosses the magenta and
             comes out above it (Marek: oranzove vzpery koliduji s magenta).

             The angle is signed now: the end face lies along the member it
             lands on, so the cut is that face's own slope less this member's,
             less a right angle. A square joint still comes out at nought, and a
             mirrored member comes out mirrored instead of identical.

             AND THE ORDER IS HIS: the top chords are primary and run whole; the
             webs are cut to them and to the tie; the piece at the apex is cut to
             the king post as well, which is the third face and the one that used
             to be ignored. */
          var dC = 0.160, dT = 0.160;                 /* chord and tie depth */
          try { if (CHORD && CHORD.d) { dC = CHORD.d; dT = CHORD.d; } } catch (eC) {}
          var cosP = Math.cos(Math.atan2(rise, hOut));
          if (!(cosP > 0.2)) cosP = 1;
          /* the chord's underside, as a drop measured plumb, and the king post's
             own half width -- the post is drawn in the chord's section */
          var faceV = (dC / 2) / cosP, tanP = (hOut > 0) ? (rise / hOut) : 0;
          var postHalf = dC / 2;
          var LIM = (80 * Math.PI) / 180;
          function wrapC(a) {
            while (a > Math.PI / 2) a -= Math.PI;
            while (a <= -Math.PI / 2) a += Math.PI;
            return a;
          }
          function safeCut(v) {
            if (!isFinite(v)) return 0;
            if (v > LIM) return LIM;
            if (v < -LIM) return -LIM;
            return v;
          }
          var q;
          for (q = 0; q < members.length; q++) {
            var m = members[q];
            if (m.kind !== 'web' && m.kind !== 'post') continue;
            var dp = m.p2 - m.p1, dy = m.y2 - m.y1;
            var L = Math.sqrt(dp * dp + dy * dy);
            if (!(L > 0.02)) continue;
            var ux = dp / L, uy = dy / L;
            /* A NEARLY FLAT WEB CANNOT BE TRIMMED THIS WAY, and must not be: a
               flat member does not land on a chord face from below. */
            if (Math.abs(uy) < 0.1) continue;

            var loFirst = (m.y1 <= m.y2);
            var loP = loFirst ? m.p1 : m.p2, loY = loFirst ? m.y1 : m.y2;
            var hiP = loFirst ? m.p2 : m.p1, hiY = loFirst ? m.y2 : m.y1;
            /* up the member, from its low end -- every distance below is along
               this, so nothing is measured plumb and then used along the slope */
            var gl = Math.hypot(hiP - loP, hiY - loY);
            if (!(gl > 0.02)) continue;
            var gx = (hiP - loP) / gl, gy = (hiY - loY) / gl;
            var pMid = (m.p1 + m.p2) / 2;
            var side = (pMid > 0.0005) ? 1 : ((pMid < -0.0005) ? -1 : ((hiP >= 0) ? 1 : -1));

            /* the face at the top: the chord's underside on this member's own
               side. The king post stands under the apex, where the two chords
               meet in a peak, so it is cut flat at the lower of them across its
               own width rather than to one of the slopes. */
            /* THE KING POST GOES UP TO THE CHORDS, NOT TO THE LOWER OF THEM.

               It was cut flat at the level its own outer corner could reach, so
               it stood a whole plumb drop short and carried nothing at the one
               place it exists to carry: the apex. It runs to the underside of
               the chords now and is notched to a peak between them -- the notch
               is cut in the body itself, further down (Marek: musi byt o neco
               delsi a serizly podle spodni hrany pasnic). */
            var faceHi = (m.kind === 'post')
              ? { p: 0, y: ridgeY - faceV, a: 0 }
              : { p: 0, y: ridgeY - faceV, a: -side * Math.atan(tanP) };
            var faceLo = { p: 0, y: eaveY + dT / 2, a: 0 };

            function meetAt(F) {
              var vx = Math.cos(F.a), vy = Math.sin(F.a);
              var den = gx * vy - gy * vx;
              if (Math.abs(den) < 1e-9) return null;
              var t = ((F.p - loP) * vy - (F.y - loY) * vx) / den;
              return { t: t, p: loP + gx * t, y: loY + gy * t, a: F.a };
            }

            var maxCut = L / 3;
            /* the top end, but only if it was standing on the chord line or at
               the apex to begin with */
            var hasHead = (m.kind === 'web' && loFirst && m.headP != null);
            var onChord = (Math.abs(hiY - topY(hiP)) < 0.02) ||
                          (Math.abs(hiY - ridgeY) < 0.02) || hasHead;
            if (onChord) {
              var hit = meetAt(faceHi);
              if (hasHead) {
                /* the cheek it dies on, taken first if it comes first */
                var hitC = meetAt({ p: m.headP, y: 0, a: Math.PI / 2 });
                if (hitC && hitC.t > 0 && (!hit || hit.t <= 0 || hitC.t < hit.t)) hit = hitC;
              }
              if (hit && hit.t > 0 && Math.abs(gl - hit.t) < maxCut) {
                if (loFirst) { m.p2 = hit.p; m.y2 = hit.y; } else { m.p1 = hit.p; m.y1 = hit.y; }
                m.faceHi = hit.a;
                /* AT THE APEX THE END MEETS TWO FACES, NOT ONE.

                   A sloping web that dies beside the king post has the chord's
                   underside above it and the post's cheek in front of it, and
                   one saw angle can only answer one of them. Cut to the chord
                   alone, its long corner drives into the post; drawn back far
                   enough to clear the post -- which is what it used to do -- it
                   touches neither and hangs in the air (Marek: visi ve vzduchu,
                   musi je orezavat magenta a tmave modry profil).

                   So this end is not one angle at all. Each point across the
                   member's depth is carried up its own line until it meets the
                   FIRST of the two faces, and stops there. The result is a
                   corner: the lower part beds on the post's cheek, the upper
                   part under the chord, and the arris between them is where the
                   two really cross. */
                if (hasHead) m.apexEnd = true;
              }
            }
            /* and the bottom end on the tie's top face */
            if (Math.abs(loY - eaveY) < 0.02) {
              var hitL = meetAt(faceLo);
              if (hitL && Math.abs(hitL.t) < maxCut) {
                if (loFirst) { m.p1 = hitL.p; m.y1 = hitL.y; } else { m.p2 = hitL.p; m.y2 = hitL.y; }
                m.faceLo = hitL.a;
              }
            }

            /* THE SAW ANGLE, SIGNED. The end face lies along the member it lands
               on; a square end is perpendicular to this one. So the shear is the
               face's own angle, less this member's, less ninety degrees -- which
               is nought at a right-angle joint and changes sign when either
               member leans the other way. Clamped, because tan of ninety is what
               once turned 1745 bodies into 927 and no roof on the screen. */
            var angF = Math.atan2(m.y2 - m.y1, m.p2 - m.p1);
            var cutLo = safeCut(wrapC((m.faceLo != null ? m.faceLo : 0) - angF - Math.PI / 2));
            var cutHi = safeCut(wrapC((m.faceHi != null ? m.faceHi : 0) - angF - Math.PI / 2));
            if (loFirst) { m.cutA = cutLo; m.cutB = cutHi; }
            else { m.cutB = cutLo; m.cutA = cutHi; }

            /* and the apex end, point by point, against whichever face it meets
               first -- the angle above is replaced by these three offsets, so
               the shear must not also be applied to that end */
            if (m.apexEnd) {
              var dpF = m.p2 - m.p1, dyF = m.y2 - m.y1, lF = Math.hypot(dpF, dyF);
              if (lF > 0.05 && Math.abs(dpF) > 1e-6) {
                var uxF = dpF / lF, uyF = dyF / lF;
                var cpF = (m.p1 + m.p2) / 2, cyF = (m.y1 + m.y2) / 2;
                var fpF = m.headP;                         /* the cheek it dies on */
                var aH = -side * tanP, cH = ridgeY - faceV;   /* the chord's underside */
                var hdF = WEB.d / 2, kF, qF, x1F, x2F, xF, denF;
                function upTo(qq) {
                  var xa = (fpF - cpF + qq * uyF) / uxF;
                  var dn = uyF - aH * uxF;
                  var xb = (Math.abs(dn) > 1e-9)
                    ? ((aH * cpF + cH - cyF - qq * (uxF + aH * uyF)) / dn) : xa;
                  /* the axis end was set where the AXIS meets the chord, so a
                     point off the axis may legitimately reach a little past it --
                     clamping that away is what left the wedge */
                  var xv = ((xa < xb) ? xa : xb) - lF / 2;
                  if (xv > lF * 0.15) xv = lF * 0.15;
                  if (xv < -lF * 0.45) xv = -lF * 0.45;
                  return xv;
                }
                /* AND THE BREAK BETWEEN THE TWO FACES IS WHERE THEY REALLY CROSS.

                   The corner was being drawn at the middle of the member's depth
                   because that is where the middle row of points happens to sit,
                   while the two faces cross somewhere else -- so between the two
                   the end ran straight instead of following either face, and left
                   a thin wedge opening upwards. The crossing point is one line
                   meeting another; the middle row is put THERE (Marek: klinovity
                   otvor otevirajici se nahoru). */
                var pcX = fpF, pcY = aH * fpF + cH;        /* cheek meets underside */
                var qStar = (pcX - cpF) * (-uyF) + (pcY - cyF) * uxF;
                var xStar = (pcX - cpF) * uxF + (pcY - cyF) * uyF - lF / 2;
                if (!(Math.abs(qStar) < hdF - 0.001)) { qStar = 0; xStar = upTo(0); }
                else {
                  if (xStar > lF * 0.15) xStar = lF * 0.15;
                  if (xStar < -lF * 0.45) xStar = -lF * 0.45;
                }
                m.endHi = [upTo(hdF), xStar, upTo(-hdF), qStar];
                m.cutB = 0;
              }
            }

            /* AND THE FOOT OF A SLOPING WEB IS CUT BY THE UPRIGHT BESIDE IT.

               Both members leave the same point on the tie, and the upright is
               120 mm wide there -- so a sloping web drawn from that point starts
               INSIDE it. The upright is the piece that runs on to the chord, so
               it is the one that wins: the sloping web dies on its cheek, and on
               the tie's top face wherever it reaches that first.

               Same rule as at the apex, the other way up: each point across the
               member's depth comes down its own line and stops at the first face
               it meets (Marek: u jejich paty dole oriznout podle cyan profilu). */
            if (m.kind === 'web' && loFirst && Math.abs(m.p2 - m.p1) > 1e-6) {
              var dpL = m.p2 - m.p1, dyL = m.y2 - m.y1, lL = Math.hypot(dpL, dyL);
              if (lL > 0.05) {
                var uxL = dpL / lL, uyL = dyL / lL;
                var cpL = (m.p1 + m.p2) / 2, cyL = (m.y1 + m.y2) / 2;
                var fpL = loP - side * (WEB.d / 2);        /* the upright's cheek */
                var cL = eaveY + dT / 2;                   /* the tie's top face */
                var hdL = WEB.d / 2;
                function downTo(qq) {
                  var xa = (Math.abs(uxL) > 1e-6) ? ((fpL - cpL + qq * uyL) / uxL) : -lL;
                  var xb = (Math.abs(uyL) > 1e-6) ? ((cL - cyL - qq * uxL) / uyL) : -lL;
                  var xv = ((xa > xb) ? xa : xb) + lL / 2;  /* coming down, the first met */
                  if (xv < -lL * 0.15) xv = -lL * 0.15;
                  if (xv > lL * 0.45) xv = lL * 0.45;
                  return xv;
                }
                /* and the same corner at the foot: the upright's cheek meets the
                   tie's top face at one point, and that is where the break goes */
                var fcX = fpL, fcY = cL;
                var qStarL = (fcX - cpL) * (-uyL) + (fcY - cyL) * uxL;
                var xStarL = (fcX - cpL) * uxL + (fcY - cyL) * uyL + lL / 2;
                if (!(Math.abs(qStarL) < hdL - 0.001)) { qStarL = 0; xStarL = downTo(0); }
                else {
                  if (xStarL < -lL * 0.15) xStarL = -lL * 0.15;
                  if (xStarL > lL * 0.45) xStarL = lL * 0.45;
                }
                m.endLo = [downTo(hdL), xStarL, downTo(-hdL), qStarL];
                m.cutA = 0;
              }
            }
          }

          /* AND THEN IT IS MEASURED, ON THE PIECES THAT WILL BE DRAWN.

             I have now twice answered a collision from arithmetic while he was
             looking at the model, and once been right and once been looking at
             the wrong member. So the four corners of every web -- shear and all,
             exactly as the body is built -- are put back against the faces they
             were cut to, and the worst of each goes in the snapshot. A number
             here ends the argument either way: nought means the member is clean
             and what he can see is something else standing behind it. */
          (function fitCheck() {
            var faceD = (dC / 2) / cosP, tieTop = eaveY + dT / 2, q2;
            _trussFit = {};
            for (q2 = 0; q2 < members.length; q2++) {
              var mm = members[q2];
              if (mm.kind !== 'web' && mm.kind !== 'post') continue;
              var ddp = mm.p2 - mm.p1, ddy = mm.y2 - mm.y1;
              var ln = Math.hypot(ddp, ddy);
              if (!(ln > 0.02)) continue;
              var an = Math.atan2(ddy, ddp), ca = Math.cos(an), sa = Math.sin(an);
              var mp = (mm.p1 + mm.p2) / 2, my = (mm.y1 + mm.y2) / 2;
              var hxx = ln / 2, hdd = ((mm.kind === 'web') ? WEB.d : dC) / 2;
              var key2 = (mm.kind === 'post') ? 'post'
                       : ((Math.abs(ddp) < 0.001) ? 'wvert' : 'web');
              if (!_trussFit[key2]) _trussFit[key2] = { n: 0, chord: -9, tie: -9, post: -9, at: 0 };
              var R2 = _trussFit[key2];
              R2.n++;
              var cs = [[hxx, hdd], [hxx, 0], [hxx, -hdd],
                        [-hxx, hdd], [-hxx, 0], [-hxx, -hdd]], k3;
              for (k3 = 0; k3 < cs.length; k3++) {
                var xx = cs[k3][0], yy = cs[k3][1];
                /* the post's top is a peak, the apex end is its own three
                   offsets, every other end is a shear */
                if (mm.kind === 'post' && xx > 0) xx = hxx - Math.abs(yy) * tanP;
                else if (mm.endHi && xx > 0) {
                  var kH = (yy > 0.0001) ? 0 : ((yy < -0.0001) ? 2 : 1);
                  xx = hxx + mm.endHi[kH];
                  if (kH === 1 && mm.endHi.length > 3) yy = mm.endHi[3];
                } else if (mm.endLo && xx < 0) {
                  var kL2 = (yy > 0.0001) ? 0 : ((yy < -0.0001) ? 2 : 1);
                  xx = -hxx + mm.endLo[kL2];
                  if (kL2 === 1 && mm.endLo.length > 3) yy = mm.endLo[3];
                } else xx += -yy * Math.tan(xx > 0 ? (mm.cutB || 0) : (mm.cutA || 0));
                var wp = mp + xx * ca - yy * sa;
                var wy = my + xx * sa + yy * ca;
                if (Math.abs(wp) > hOut) continue;
                var intoC = wy - (topY(wp) - faceD);        /* inside the top chord */
                var belowT = tieTop - wy;                   /* through the tie */
                if (intoC > R2.chord) { R2.chord = intoC; R2.at = wp; }
                if (belowT > R2.tie) R2.tie = belowT;
                if (mm.kind !== 'post' && wy > tieTop + 0.05) {
                  var intoP = (dC / 2) - Math.abs(wp);      /* inside the king post */
                  if (intoP > R2.post) R2.post = intoP;
                }
              }
            }
          })();
        })();

        /* AND ONLY NOW IS THE TRUSS SLID ONTO THE SHIFTED RIDGE LINE.

           THE CUTS WERE MEASURED AGAINST A CHORD THAT HAD ALREADY MOVED.

           Two unequal eave overhangs put the ridge off centre, and the whole
           truss is slid by half the difference to follow it. That slide used to
           happen HERE, before the webs were cut -- but every cut is measured
           against topY(), which describes the chord line of the truss as it was
           LAID OUT, not as it now stands. So the webs were being fitted to a
           chord that was no longer there, by exactly the slide:

             tails equal              webs touch the chord underside      0 mm
             tails 600 and 900        webs driven into the chord        187 mm
             tails 600 and 1200       webs driven into the chord        252 mm

           A chord is 178 mm deep on the plumb, so on an unequal roof the webs
           went clean through it -- which is what he can see, on every upright at
           once, while the arithmetic on an equal roof said nought (Marek: tyka
           se vsech cyan).

           The slide is a rigid move of the finished truss: every member and
           every node by the same amount. Done last, it cannot pull a joint apart
           -- and the cuts are measured where the pieces really are. */
        if (_dx) {
          for (i = 0; i < members.length; i++) {
            members[i].p1 += _dx; members[i].p2 += _dx;
            /* the cheek a member dies on moves with it, or anything reading the
               finished truss is told where that piece used to be */
            if (members[i].headP != null) members[i].headP += _dx;
          }
          for (i = 0; i < nodes.length; i++) { nodes[i].p += _dx; }
        }

        /* the chord's own fall, published with the truss: the body that gets
           notched at the apex needs the same number the joints were cut with */
        return { members: members, nodes: nodes, tanP: (hOut > 0) ? (rise / hOut) : 0,
                 /* the two faces every web lies between: the tie's top and the top
                    chords' underside, as lines in the truss's own plane */
                 clip: { floor: eaveY + CHORD.d / 2, eaveY: eaveY, rise: rise, hOut: hOut,
                         chordHalfV: faceVL, ups: _upsOf(),
                         /* and where the truss's own centre is: with two different
                            tails the whole truss is built about its middle and then
                            moved by this much, so every face above is measured from
                            here, not from nought */
                         dx: _dx || 0 } };
      }

      function _inTruss(T, g, mat, len, sect, ang, yaw, cx, cy, cz, cutA, cutB, part, floorY, peak, endHi, endLo, clip) {
        /* THE TOP CHORD IS PLANED OFF WHERE IT PASSES UNDER THE BOTTOM CHORD.

           Both chords run out to the tip of the tail, one level and one falling,
           so over the last stretch the top chord's underside is BELOW the tie's
           top face and the two solids occupy the same timber -- 152 mm of it at
           26 degrees, in a member 160 mm deep. On screen the magenta runs
           straight through the lime (Marek).

           The cut is the tie's own top face: the chord keeps its full depth up
           to the point where its underside meets that face, and from there its
           underside IS that face. So the box is made with a break across it, the
           break is slid to where the two really cross, and every vertex in the
           lower half is lifted to the face wherever it would otherwise fall
           through it. Above the face nothing moves, which is why the chord still
           measures its own length and section. */
        var segX = (floorY != null) ? 2 : 1;
        var segY = (peak > 0 || (endHi && endHi.length >= 3) ||
                    (endLo && endLo.length >= 3)) ? 2 : 1;
        var geo = new T.BoxGeometry(len, sect.d, sect.t, segX, segY, 1);
        /* and the drawing refuses anything it cannot use, whatever reaches it */
        if (!isFinite(cutA)) cutA = 0;
        if (!isFinite(cutB)) cutB = 0;
        if ((cutA && Math.abs(cutA) > 0.02) || (cutB && Math.abs(cutB) > 0.02)) {
          var pos = geo.attributes.position, i, x, y;
          var halfL = len / 2, halfD = sect.d / 2;
          for (i = 0; i < pos.count; i++) {
            x = pos.getX(i); y = pos.getY(i);
            /* how far this vertex sits from the member's own axis, and which
               end it belongs to */
            var slide = -y * Math.tan((x > 0 ? (cutB || 0) : (cutA || 0)));
            if (Math.abs(Math.abs(x) - halfL) < 1e-6 && halfD > 0) {
              pos.setX(i, x + slide);
            }
          }
          pos.needsUpdate = true;
          geo.computeVertexNormals();
        }

        /* and the underside laid on the face below it, where there is one */
        if (floorY != null) {
          var sinA = Math.sin(ang), cosA = Math.cos(ang);
          if (Math.abs(sinA) > 1e-4 && Math.abs(cosA) > 1e-4) {
            var pos2 = geo.attributes.position, i2, x2, y2;
            var hL2 = len / 2, hD2 = sect.d / 2;
            /* where the member's own underside crosses that face, along its
               length -- the break in the box is slid there */
            /* WHICH LOCAL SIDE IS THE UNDERSIDE DEPENDS ON WHICH WAY THE MEMBER
               RUNS. A chord drawn from the tail up to the apex points left, so
               its own +y is world DOWN, and a rule written for one of them lifts
               the wrong face on the other. The member says which: a vertex is on
               the underside when y times cos of its slope is negative. */
            var xBreak = (floorY - cy + hD2 * Math.abs(cosA)) / sinA;
            var takenHere = 0;
            if (xBreak > -hL2 + 0.005 && xBreak < hL2 - 0.005) {
              for (i2 = 0; i2 < pos2.count; i2++) {
                if (Math.abs(pos2.getX(i2)) < 1e-6) pos2.setX(i2, xBreak);
              }
            }
            for (i2 = 0; i2 < pos2.count; i2++) {
              x2 = pos2.getX(i2); y2 = pos2.getY(i2);
              if (y2 * cosA > -1e-9) continue;          /* the top half never moves */
              var want = (floorY - cy - x2 * sinA) / cosA;
              if (want > hD2) want = hD2;
              if (want < -hD2) want = -hD2;
              if ((want - y2) * cosA > 1e-9) {          /* it would fall through */
                if (Math.abs(want - y2) > takenHere) takenHere = Math.abs(want - y2);
                pos2.setY(i2, want);
              }
            }
            pos2.needsUpdate = true;
            geo.computeVertexNormals();
            if (takenHere > 0.001) {
              _trussFloorCut.n++;
              if (takenHere > _trussFloorCut.max) _trussFloorCut.max = takenHere;
            }
          }
        }
        /* and the apex end of a sloping web, which is a corner of two faces:
           three offsets across the member's depth, given by the caller because
           it is the only one that knows where the chord and the post really are */
        if ((endHi && endHi.length >= 3) || (endLo && endLo.length >= 3)) {
          var posE = geo.attributes.position, iE, xE, yE, hLE = len / 2, hDE = sect.d / 2;
          for (iE = 0; iE < posE.count; iE++) {
            xE = posE.getX(iE); yE = posE.getY(iE);
            var kE = (yE > hDE * 0.5) ? 0 : ((yE < -hDE * 0.5) ? 2 : 1);
            if (endHi && Math.abs(xE - hLE) < 1e-6) {
              posE.setX(iE, hLE + endHi[kE]);
              if (kE === 1 && endHi.length > 3) posE.setY(iE, endHi[3]);
            } else if (endLo && Math.abs(xE + hLE) < 1e-6) {
              posE.setX(iE, -hLE + endLo[kE]);
              if (kE === 1 && endLo.length > 3) posE.setY(iE, endLo[3]);
            }
          }
          posE.needsUpdate = true;
          geo.computeVertexNormals();
        }

        /* AND THE KING POST IS NOTCHED TO THE TWO CHORDS IT HOLDS UP.

           Its head sits where the two chords meet, so the timber above it is not
           a face but a peak: the underside falls away to both sides at the roof's
           own pitch. A flat head either stands short of the apex or buries its
           corners in the chords -- it used to do the first, by its own half width
           times the fall. The head is cut to the peak instead: the middle reaches
           the apex, each edge drops by its distance out times the fall, so the
           post beds against both chords along their whole width. */
        if (peak > 0) {
          var posP = geo.attributes.position, iP, xP, yP;
          var hLP = len / 2;
          for (iP = 0; iP < posP.count; iP++) {
            xP = posP.getX(iP); yP = posP.getY(iP);
            if (Math.abs(xP - hLP) > 1e-6) continue;       /* the head only */
            posP.setX(iP, hLP - Math.abs(yP) * peak);
          }
          posP.needsUpdate = true;
          geo.computeVertexNormals();
        }

        /* THE PIECE IS TURNED, ITS GEOMETRY IS NOT.

           Rotating the geometry left every member with its own box drawn round
           the PLAN of a sloping piece: a 1999 x 160 x 60 web at 44 degrees came
           out of its own bounding box as 1549 x 1504 x 60. Nothing about the
           picture was wrong -- but everything that READS a body reads that box,
           so the cut list, the audit, the section colours, the drawer and the
           truss sheet were all measuring the shadow instead of the piece.

           The hip rafters further down this file have always turned the MESH
           and left the geometry square, and they measure right. This is that,
           copied: the box stays 1999 x 160 x 60 and the mesh carries the slope.
           Euler order XYZ applies Z and then Y, which is the order the geometry
           used, so the piece stands exactly where it stood. */
        if (clip && (part === 'web' || part === 'wvert')) {
          try { geo = _clipWeb(T, geo, sect, ang, cy, clip, len, part); } catch (eCW) {}
        }

        var mesh = new T.Mesh(geo, mat);
        mesh.userData.poly2 = _faceOutline(geo, 0, sect.t / 2);
        mesh.userData.trussPart = part || '';
        mesh.rotation.set(0, yaw, ang);
        mesh.position.set(cx, cy, cz);
        g.add(mesh);
        _lastBody = mesh;

        /* and it says what it measures, against what it was asked for. A member
           sheared at its ends measures LONGER than its centreline: the long
           point of the cut is the piece a saw has to start from. */
        try {
          if (!geo.boundingBox) geo.computeBoundingBox();
          var bbG = geo.boundingBox;
          var dG = [Math.abs(bbG.max.x - bbG.min.x), Math.abs(bbG.max.y - bbG.min.y),
                    Math.abs(bbG.max.z - bbG.min.z)];
          dG.sort(function (a, b) { return b - a; });
          _trussGeom.push({ ask: len, got: dG[0], d: dG[1], t: dG[2],
                            askD: sect.d, askT: sect.t });
        } catch (eTG0) {}
      }

      return {
        fink: _fink,
        inTruss: _inTruss,
        /* a new roof: the geometry list and the floor-cut counters start over */
        reset: function () {
          _trussGeom = []; _trussFloorCut = { n: 0, max: 0 };
          _webClip = { n: 0, cut: 0, worst: 0, after: 0, noRoom: 0, asym: 0, areas: {} };
          _outerRule = { n: 0, dropped: 0, clearLo: 1e9, clearHi: -1e9, min: 0.200 };
        },
        webClip: function () { return _webClip; },
        outerRule: function () { return _outerRule; },
        geom: function () { return _trussGeom; },
        floorCut: function () { return _trussFloorCut; },
        fit: function () { return _trussFit; },
        lastBody: function () { return _lastBody; }
      };
    }

    function trussRecorder(opt) {
      opt = opt || {};
      function SEC() {
        var s = (typeof opt.sections === 'function') ? opt.sections() : (opt.sections || {});
        return { web: s.web || { d: 0.12, t: 0.06, code: '' }, chord: s.chord || { d: 0.16, t: 0.06, code: '' } };
      }
      var shapes = [], hipMembers = [], hipSheet = [], tagged = 0, grpSig = {};

      /* the same merge the standard trusses use, so a shape that repeats is one
         drawing with a count */
      function add(mem, big, prefix, tag) {
        if (!mem || !mem.length) return '';
        var sig = (big ? 'G' : (prefix || 'S')), i, m;
        for (i = 0; i < mem.length; i++) {
          m = mem[i];
          sig += '|' + Math.round(m.p1 * 1000) + ',' + Math.round(m.y1 * 1000) +
                 ':' + Math.round(m.p2 * 1000) + ',' + Math.round(m.y2 * 1000);
        }
        for (i = 0; i < shapes.length; i++) {
          if (shapes[i].sig === sig) {
            shapes[i].n++;
            if (tag) shapes[i].tags.push(tag);
            return sig;
          }
        }
        var pMin = 1e9, pMax = -1e9, yMin = 1e9, yMax = -1e9, run = 0;
        for (i = 0; i < mem.length; i++) {
          m = mem[i];
          pMin = Math.min(pMin, m.p1, m.p2); pMax = Math.max(pMax, m.p1, m.p2);
          yMin = Math.min(yMin, m.y1, m.y2); yMax = Math.max(yMax, m.y1, m.y2);
          run += Math.sqrt((m.p2 - m.p1) * (m.p2 - m.p1) + (m.y2 - m.y1) * (m.y2 - m.y1));
        }
        shapes.push({
          sig: sig, n: 1, big: !!big, prefix: prefix || '',
          members: mem, nodes: null,
          span: pMax - pMin, rise: yMax - yMin, run: run, count: mem.length,
          /* which bodies in the model are this drawing: every instance's mark */
          tags: tag ? [tag] : []
        });
        return sig;
      }

      function sig(T2, big) {
        var s = (big ? 'G' : 'S'), i, m;
        if (!T2 || !T2.members) return s;
        for (i = 0; i < T2.members.length; i++) {
          m = T2.members[i];
          s += '|' + Math.round(m.p1 * 1000) + ',' + Math.round(m.y1 * 1000) +
               ':' + Math.round(m.p2 * 1000) + ',' + Math.round(m.y2 * 1000) +
               (m.kind ? (':' + m.kind) : '');
        }
        return s;
      }

      function collect(T2, big) {
        if (!T2 || !T2.members || !T2.members.length) return;
        var sg = sig(T2, big), i, m, S2 = SEC();
        for (i = 0; i < shapes.length; i++) {
          if (shapes[i].sig === sg) { shapes[i].n++; return; }
        }
        var pMin = 1e9, pMax = -1e9, yMin = 1e9, yMax = -1e9, run = 0;
        for (i = 0; i < T2.members.length; i++) {
          m = T2.members[i];
          pMin = Math.min(pMin, m.p1, m.p2); pMax = Math.max(pMax, m.p1, m.p2);
          yMin = Math.min(yMin, m.y1, m.y2); yMax = Math.max(yMax, m.y1, m.y2);
          run += Math.sqrt((m.p2 - m.p1) * (m.p2 - m.p1) + (m.y2 - m.y1) * (m.y2 - m.y1));
        }
        /* the section travels with the member; and the copy keeps a way back to
           the member it came from, so an outline written later reaches both */
        var mem2 = [];
        for (i = 0; i < T2.members.length; i++) {
          m = T2.members[i];
          /* the post is built in the chord's profile -- the table says what the
             body is (core-v264) */
          var sect = (m.kind === 'web') ? S2.web : S2.chord;
          mem2.push({ p1: m.p1, y1: m.y1, p2: m.p2, y2: m.y2, kind: m.kind,
                      d: sect.d, t: sect.t, code: sect.code });
          m.pub = mem2[mem2.length - 1];
        }
        shapes.push({
          sig: sg, n: 1, big: !!big,
          members: mem2, nodes: T2.nodes || null,
          span: pMax - pMin, rise: yMax - yMin, run: run,
          count: T2.members.length, tags: [sg]
        });
      }

      /* a hip end's member, written down as it is made; the body carries the
         mark of its truss, and its outline unless noPoly says its shape is not
         to be read (the uprights are built standing up) */
      function hipRec(a, b, dep, wid, tag, mesh, noPoly) {
        try {
          var S2 = SEC();
          /* noPoly: true takes no outline at all, 'plumb' and 'plumbZ' take the
             outline of a member standing on end -- its thickness on X or on Z */
          var polR = null;
          if (mesh && mesh.geometry) {
            if (noPoly === 'plumb') polR = faceOutline(mesh.geometry, 1, wid / 2);
            else if (noPoly === 'plumbZ') polR = faceOutline(mesh.geometry, 3, wid / 2);
            else if (!noPoly) polR = faceOutline(mesh.geometry, 2, wid / 2);
          }
          if (mesh && mesh.userData && tag) { mesh.userData.trussGrp = tag; tagged++; }
          hipMembers.push({
            poly: polR,
            x1: a[0], y1: a[1], z1: a[2],
            x2: b[0], y2: b[1], z2: b[2],
            d: dep, t: wid,
            kind: (dep === S2.web.d) ? 'web' : 'chord',
            part: (mesh && mesh.userData && mesh.userData.trussPart) || null,
            code: (dep === S2.web.d) ? ((opt.hipCodes && opt.hipCodes.web) || S2.web.code)
                                     : ((opt.hipCodes && opt.hipCodes.chord) || S2.chord.code),
            grp: tag || null
          });
        } catch (eHR) {}
      }

      /* A JACK TRUSS IS THE MEMBERS THAT STAND OVER ONE POINT: grouped by the
         truss's own tag (or the station along the hip when a member has none),
         and flattened along the line its own longest member takes */
      function hipGroup() {
        hipSheet = [];
        if (!hipMembers.length) return;
        var byKey = {}, i, m, key, alongL = null;
        var sx = 0, sz = 0;
        for (i = 0; i < hipMembers.length; i++) {
          m = hipMembers[i];
          sx += Math.abs(m.x2 - m.x1); sz += Math.abs(m.z2 - m.z1);
        }
        alongL = sx >= sz;
        for (i = 0; i < hipMembers.length; i++) {
          m = hipMembers[i];
          var st = alongL ? ((m.z1 + m.z2) / 2) : ((m.x1 + m.x2) / 2);
          key = m.grp ? m.grp : (Math.round(st * 20) / 20);
          if (!byKey[key]) byKey[key] = [];
          byKey[key].push(m);
        }
        var k2, grp, mem, j, base;
        for (k2 in byKey) {
          if (!byKey.hasOwnProperty(k2)) continue;
          grp = byKey[k2];
          if (grp.length < 2) continue;              /* a lone piece is not a truss */
          var exG = 0, ezG = 0, bestG = -1;
          for (j = 0; j < grp.length; j++) {
            var dxG = grp[j].x2 - grp[j].x1, dzG = grp[j].z2 - grp[j].z1;
            var rG = Math.sqrt(dxG * dxG + dzG * dzG);
            if (rG > bestG) { bestG = rG; exG = dxG / (rG || 1); ezG = dzG / (rG || 1); }
          }
          if (!(bestG > 0.05)) continue;
          /* ONE MARK CAN HOLD TWO TRUSSES, and each is its own drawing (core-v262).

             The hip builder marks the pair either side of the hip end with one
             tag, so both were flattened into one plane and drawn as one truss:
             two half-jacks back to back read as a whole truss with two top
             chords, x2 -- where the roof really holds four of ONE half-jack
             (Marek: u valby symetricke tvary rozdelujeme na dva a dva, pritom
             muze byt jeden tvar 4 ks). So a mark is split into the trusses it
             holds -- by where each stands across its own run, then by gaps
             along it -- and each is recorded on its own; the mirror merge on the
             way to the sheet then counts them together. */
          var nxG = -ezG, nzG = exG, inst = [], ci;
          var ordG = grp.slice().sort(function (u, v) {
            return ((u.x1 + u.x2) * nxG + (u.z1 + u.z2) * nzG) - ((v.x1 + v.x2) * nxG + (v.z1 + v.z2) * nzG);
          });
          for (j = 0; j < ordG.length; j++) {
            var stJ = ((ordG[j].x1 + ordG[j].x2) / 2) * nxG + ((ordG[j].z1 + ordG[j].z2) / 2) * nzG;
            var lastI = inst.length ? inst[inst.length - 1] : null;
            if (lastI && Math.abs(stJ - lastI.st) < 0.15) lastI.m.push(ordG[j]);
            else inst.push({ st: stJ, m: [ordG[j]] });
          }
          var parts = [];
          for (ci = 0; ci < inst.length; ci++) {
            var mi = inst[ci].m.slice().sort(function (u, v) {
              return Math.min(u.x1 * exG + u.z1 * ezG, u.x2 * exG + u.z2 * ezG) -
                     Math.min(v.x1 * exG + v.z1 * ezG, v.x2 * exG + v.z2 * ezG);
            });
            var cur = null, curHi = -1e9;
            for (j = 0; j < mi.length; j++) {
              var e1 = Math.min(mi[j].x1 * exG + mi[j].z1 * ezG, mi[j].x2 * exG + mi[j].z2 * ezG);
              var e2 = Math.max(mi[j].x1 * exG + mi[j].z1 * ezG, mi[j].x2 * exG + mi[j].z2 * ezG);
              if (!cur || e1 > curHi + 0.05) { cur = []; parts.push(cur); curHi = e2; }
              else if (e2 > curHi) curHi = e2;
              cur.push(mi[j]);
            }
          }
          var pk, whole = grp;
          for (pk = 0; pk < parts.length; pk++) {
          grp = parts[pk];
          if (grp.length < 2) continue;              /* a lone piece is not a truss */
          base = 1e9;
          for (j = 0; j < grp.length; j++) {
            base = Math.min(base, grp[j].x1 * exG + grp[j].z1 * ezG,
                                  grp[j].x2 * exG + grp[j].z2 * ezG);
          }
          mem = [];
          for (j = 0; j < grp.length; j++) {
            m = grp[j];
            var q1 = m.x1 * exG + m.z1 * ezG - base;
            var q2 = m.x2 * exG + m.z2 * ezG - base;
            var polM = null;
            if (m.poly && m.poly.length > 2) {
              var dq = q2 - q1, dyq = m.y2 - m.y1, lq = Math.sqrt(dq * dq + dyq * dyq);
              if (lq > 0.001) {
                var uxq = dq / lq, uyq = dyq / lq;
                var cq = (q1 + q2) / 2, cyq = (m.y1 + m.y2) / 2, zp;
                polM = [];
                for (zp = 0; zp < m.poly.length; zp++) {
                  polM.push([cq + m.poly[zp][0] * uxq - m.poly[zp][1] * uyq,
                             cyq + m.poly[zp][0] * uyq + m.poly[zp][1] * uxq]);
                }
              }
            }
            mem.push({
              p1: q1, y1: m.y1, p2: q2, y2: m.y2,
              kind: m.kind, part: m.part || null, d: m.d, t: m.t, code: m.code, poly: polM
            });
          }
          grpSig[k2] = add(mem, false, 'Jack', k2) || '';
          hipSheet.push((k2 + '').replace(/[^A-Za-z0-9]/g, '') + (parts.length > 1 ? ('.' + (pk + 1)) : '') + ':' + grp.length);
          }
          grp = whole;
        }
        hipMembers = [];
      }

      return {
        faceOutline: faceOutline,
        /* a new roof: the shapes and the hip members start over; the count of
           marked bodies is kept across builds, as it always has been */
        reset: function () { shapes = []; hipMembers = []; },
        add: add, sig: sig, collect: collect,
        hipRec: hipRec,
        hipPush: function (rec) { hipMembers.push(rec); },
        hipGroup: hipGroup,
        tag: function () { tagged++; },
        shapes: function () { return shapes; },
        hipSheet: function () { return hipSheet; },
        tagged: function () { return tagged; },
        grpSig: function () { return grpSig; }
      };
    }

    /* THE HIP TRUSSES, READ OFF THE MODEL.

       A hip end is drawn in fourteen separate places and keeps no truss object,
       so recording members as they are made meant fourteen edits with fourteen
       sets of local variables -- and I have spent today learning how that ends.

       The bodies are already there and already named: hip_chord and hip_web,
       from the marks that fixed the bill. So the sheet reads the model, the way
       the audit does. A body's own geometry gives its length and section; its
       world position gives where it sits; and members standing over the same
       point along the hip are one jack truss (Marek: tak urcite z modelu). */
    function _hipTrussesFromModel() {
      var st = _stage();
      if (!st || !st.group || !global.THREE) return [];
      var found = [];
      st.group.traverse(function (o) {
        if (!o.isMesh || !o.geometry) return;
        var m = o.userData && o.userData.asm;
        var key = m ? (typeof m === 'string' ? m : (m.key || '')) : '';
        if (key !== 'hip_chord' && key !== 'hip_web') return;
        var g = o.geometry, d;
        try {
          if (!g.boundingBox) g.computeBoundingBox();
          var bb = g.boundingBox, sc = o.scale || { x: 1, y: 1, z: 1 };
          d = [Math.abs(bb.max.x - bb.min.x) * (sc.x || 1),
               Math.abs(bb.max.y - bb.min.y) * (sc.y || 1),
               Math.abs(bb.max.z - bb.min.z) * (sc.z || 1)];
        } catch (eG) { return; }
        /* the member's own axis is its longest side; the section is the rest */
        var ax = (d[0] >= d[1] && d[0] >= d[2]) ? 0 : ((d[1] >= d[2]) ? 1 : 2);
        var len = d[ax];
        if (!(len > 0.05)) return;
        var sect = [d[0], d[1], d[2]];
        sect.splice(ax, 1);
        sect.sort(function (a, b) { return b - a; });

        var c = new global.THREE.Vector3();
        var e = new global.THREE.Vector3();
        try {
          if (o.updateWorldMatrix) o.updateWorldMatrix(true, false);
          o.getWorldPosition(c);
          e.set(ax === 0 ? 1 : 0, ax === 1 ? 1 : 0, ax === 2 ? 1 : 0);
          e.applyQuaternion(o.getWorldQuaternion(new global.THREE.Quaternion()));
        } catch (eW) { return; }
        found.push({
          kind: (key === 'hip_web') ? 'web' : 'chord',
          d: sect[0], t: sect[1],
          code: (key === 'hip_web') ? 'ROT00002' : 'ROT00001',
          x1: c.x - e.x * len / 2, y1: c.y - e.y * len / 2, z1: c.z - e.z * len / 2,
          x2: c.x + e.x * len / 2, y2: c.y + e.y * len / 2, z2: c.z + e.z * len / 2
        });
      });
      if (found.length < 2) return [];

      /* A TRUSS IS THE MEMBERS THAT SHARE A VERTICAL PLANE.

         I flattened the whole hip end along one axis, and a hip has jack
         trusses on both sides at right angles to each other -- so half of them
         came out with a span of zero and were drawn edge-on, which is what you
         saw (Marek: maji byt videt z boku).

         The plane belongs to the truss, not to the hip end. Each member lies in
         a vertical plane; on the ground that plane is a line, and members of one
         truss share it. So the grouping is by that line -- its direction and how
         far it stands from the origin -- and each group is then flattened along
         its OWN direction. Both sides come out in elevation, and so would a
         truss at any other angle. */
      var groups = [], i2, m2;
      for (i2 = 0; i2 < found.length; i2++) {
        m2 = found[i2];
        var gx = m2.x2 - m2.x1, gz = m2.z2 - m2.z1;
        var gl = Math.sqrt(gx * gx + gz * gz);
        var ux, uz;
        if (gl < 0.02) { ux = 1; uz = 0; }            /* a post: it takes the plane it stands in */
        else { ux = gx / gl; uz = gz / gl; }
        if (ux < 0 || (Math.abs(ux) < 1e-6 && uz < 0)) { ux = -ux; uz = -uz; }
        /* how far the line stands from the origin, square to itself */
        var off = -uz * m2.x1 + ux * m2.z1;
        var ang = Math.round(Math.atan2(uz, ux) * 180 / Math.PI);
        var key3 = ang + '@' + (Math.round(off * 20) / 20);
        var g2 = null, q7;
        for (q7 = 0; q7 < groups.length; q7++) if (groups[q7].key === key3) { g2 = groups[q7]; break; }
        if (!g2) { g2 = { key: key3, ux: ux, uz: uz, list: [] }; groups.push(g2); }
        g2.list.push(m2);
      }

      /* a post standing alone belongs to whichever truss passes through it */
      var out = [], gi, j2, mem, base;
      for (gi = 0; gi < groups.length; gi++) {
        var G = groups[gi];
        if (G.list.length < 2) continue;
        base = 1e9;
        for (j2 = 0; j2 < G.list.length; j2++) {
          m2 = G.list[j2];
          base = Math.min(base, m2.x1 * G.ux + m2.z1 * G.uz, m2.x2 * G.ux + m2.z2 * G.uz);
        }
        mem = [];
        for (j2 = 0; j2 < G.list.length; j2++) {
          m2 = G.list[j2];
          mem.push({
            p1: m2.x1 * G.ux + m2.z1 * G.uz - base, y1: m2.y1,
            p2: m2.x2 * G.ux + m2.z2 * G.uz - base, y2: m2.y2,
            kind: m2.kind, d: m2.d, t: m2.t, code: m2.code
          });
        }
        var pMin = 1e9, pMax = -1e9, yMin = 1e9, yMax = -1e9, run = 0;
        for (j2 = 0; j2 < mem.length; j2++) {
          m2 = mem[j2];
          pMin = Math.min(pMin, m2.p1, m2.p2); pMax = Math.max(pMax, m2.p1, m2.p2);
          yMin = Math.min(yMin, m2.y1, m2.y2); yMax = Math.max(yMax, m2.y1, m2.y2);
          run += Math.sqrt((m2.p2 - m2.p1) * (m2.p2 - m2.p1) + (m2.y2 - m2.y1) * (m2.y2 - m2.y1));
        }
        if (!(pMax - pMin > 0.3)) continue;           /* edge-on or a stray pair */
        out.push({
          n: 1, big: false, prefix: 'Jack', members: mem,
          span: pMax - pMin, rise: yMax - yMin, run: run, count: mem.length
        });
      }
      return out;
    }

    /* a truss's members as numbers, each from its left end, in a steady order;
       mirrored about the span when asked */
    function _memList(S0, mirror) {
      var span = S0.span || 0, out = [], j;
      for (j = 0; j < S0.members.length; j++) {
        var m = S0.members[j];
        var a1 = mirror ? (span - m.p1) : m.p1, a2 = mirror ? (span - m.p2) : m.p2;
        out.push(a1 <= a2 ? [a1, m.y1, a2, m.y2] : [a2, m.y2, a1, m.y1]);
      }
      out.sort(function (u, v) {
        var k;
        for (k = 0; k < 4; k++) {
          var d = Math.round(u[k] * 100) - Math.round(v[k] * 100);
          if (d) return d;
        }
        return 0;
      });
      return out;
    }
    function _memClose(A, B) {
      if (!A || !B || A.length !== B.length) return Infinity;
      var mx = 0, i, k;
      for (i = 0; i < A.length; i++) for (k = 0; k < 4; k++) mx = Math.max(mx, Math.abs(A[i][k] - B[i][k]));
      return mx;
    }

    function _pdfTrusses() {
      /* THE SHEETS COME FROM WHAT THE PRODUCT PUBLISHES, AND ONLY FROM THAT.

         A hip end used to have no truss object at all, so this read the bodies
         back out of the model and grouped them by where they stood. That guess
         is what put seven sheets of loose uprights into the last export -- three
         verticals with no chord between them, over a span of 3933 mm, which is
         not a truss but a row of webs picked off three different jacks. Two more
         sheets carried two separate trusses side by side for the same reason.

         The product names them itself now: every member of the hip end carries
         the tag of the truss it belongs to, and the shapes arrive here already
         assembled. Reading the model a second time can only disagree with that
         (Marek: mohly by nakresy odpovidat presne tomu co jsme postavili). */
      var src = _trussPub.slice();
      if (!src.length) return [];
      /* AND THE SAME SHAPE IS ONE SHEET.

         A girder is the ordinary truss doubled: same span, same rise, same
         members. Two pages with an identical drawing help nobody, so shapes
         that match are merged and the sheet says how many and how many plies
         (Marek: proc by u sedlove mely byt dva vazniky). */
      var merged = [], si, sj, sig2, hit;
      for (si = 0; si < src.length; si++) {
        var S0 = src[si];
        if (!S0 || !S0.members || !S0.members.length) continue;
        /* A MIRRORED TRUSS IS THE SAME TRUSS. The two sides of a hip are each
           other's reflection, so the same shape appears twice -- once read left
           to right and once right to left. Both readings are made and the
           smaller one used, which makes a mirror pair one sheet with a count of
           two (Marek: stejne / zrcadlove vazniky jen jednou). */
        var sigA = [], sigB = [], spanS = Math.round((S0.span || 0) * 1000);
        for (sj = 0; sj < S0.members.length; sj++) {
          var mm0 = S0.members[sj];
          var a1 = Math.round(mm0.p1 * 1000), a2 = Math.round(mm0.p2 * 1000);
          var b1 = spanS - a1, b2 = spanS - a2;
          var y1s = Math.round(mm0.y1 * 1000), y2s = Math.round(mm0.y2 * 1000);
          sigA.push(Math.min(a1, a2) + ',' + (a1 <= a2 ? y1s : y2s) + ':' +
                    Math.max(a1, a2) + ',' + (a1 <= a2 ? y2s : y1s));
          sigB.push(Math.min(b1, b2) + ',' + (b1 <= b2 ? y1s : y2s) + ':' +
                    Math.max(b1, b2) + ',' + (b1 <= b2 ? y2s : y1s));
        }
        sigA.sort(); sigB.sort();
        var sA = sigA.join('|'), sB = sigB.join('|');
        sig2 = (sA < sB) ? sA : sB;
        hit = null;
        for (sj = 0; sj < merged.length; sj++) if (merged[sj].sig2 === sig2) { hit = merged[sj]; break; }
        /* AND A MIRROR THAT MISSES BY A ROUNDING IS STILL THE MIRROR (core-v262).
           The string above rounds every end to the millimetre, and the mirror of
           a point at 1234.5 lands on the other side of the rounding -- so a left
           and a right half-jack of the same size stayed two sheets of two
           (Marek: jeden tvar 4 ks). The members are compared as numbers too,
           directly and mirrored, and within 3 mm they are the same truss. */
        var LA = _memList(S0, false), LB = _memList(S0, true);
        if (!hit) {
          for (sj = 0; sj < merged.length; sj++) {
            if (!merged[sj].list) continue;
            if (_memClose(LA, merged[sj].list) <= 0.003 || _memClose(LB, merged[sj].list) <= 0.003) { hit = merged[sj]; break; }
          }
        }
        if (hit) {
          hit.n += (S0.n || 1);
          if (S0.big) hit.plies = 2;
          /* the shape it merged into keeps its marks as well, so a sheet whose
             only instance arrived as the second of a mirrored pair still has a
             truss to photograph */
          if (S0.tags && S0.tags.length) hit.tags = (hit.tags || []).concat(S0.tags);
          continue;
        }
        merged.push({
          sig2: sig2, list: LA, n: S0.n || 1, big: !!S0.big, plies: S0.big ? 2 : 1,
          prefix: S0.prefix || '', members: S0.members,
          span: S0.span, rise: S0.rise, run: S0.run, count: S0.count,
          /* AND THE MARKS ITS BODIES WEAR.

             Same trap as the outline, one merge further along: this rebuilds
             each shape field by field, a field nobody listed is gone, and the
             photographer then had eleven sheets and no idea which bodies were
             any of them. Mirrored shapes merge into one sheet, so their marks
             merge too -- the first one is the truss that gets photographed. */
          tags: (S0.tags || []).slice()
        });
      }

      var out = [], i, j;
      for (i = 0; i < merged.length; i++) {
        var TS = merged[i];
        if (!TS || !TS.members || !TS.members.length) continue;

        /* the members, each with what a shop needs: length and slope */
        var mem = [], m, L, ang;
        for (j = 0; j < TS.members.length; j++) {
          m = TS.members[j];
          L = Math.sqrt((m.p2 - m.p1) * (m.p2 - m.p1) + (m.y2 - m.y1) * (m.y2 - m.y1));
          if (!(L > 0.01)) continue;
          ang = Math.atan2(m.y2 - m.y1, m.p2 - m.p1) * 180 / Math.PI;
          if (ang < -90) ang += 180;
          if (ang > 90) ang -= 180;
          mem.push({
            kind: m.kind || 'chord',
            length: L,
            angle: Math.round(ang * 10) / 10,
            p1: m.p1, y1: m.y1, p2: m.p2, y2: m.y2,
            d: m.d || 0.16, t: m.t || 0.06, code: m.code || '',
            role: _trussRole(m.part || null, m.kind || 'chord', Math.round(ang * 10) / 10),
            /* AND THE PIECE'S OWN OUTLINE, WHICH THIS WAS DROPPING.

               The sheet is rebuilt here field by field, and a field nobody listed
               does not survive the copy. The product started sending each member's
               real cut shape and it died on this line -- so the drawing went on
               showing the rectangle it draws when no outline arrives, and the
               sheets looked exactly as they had before (Marek: nakresy porad
               neodpovidaji tomu jak vazniky mame v modelu). */
            poly: (m.poly && m.poly.length > 2) ? m.poly : null
          });
        }
        if (!mem.length) continue;

        /* the same member twice is one line with a count, as on the cut list */
        var rows = [], k, key, found;
        for (j = 0; j < mem.length; j++) {
          key = mem[j].role + '|' + Math.round(mem[j].length * 1000) + '|' + mem[j].angle;
          found = null;
          for (k = 0; k < rows.length; k++) if (rows[k].key === key) { found = rows[k]; break; }
          if (found) { found.qty++; continue; }
          rows.push({ key: key, kind: mem[j].role, length: mem[j].length,
                      angle: mem[j].angle, qty: 1,
                      section: mem[j].d ? (Math.round(mem[j].t * 1000) + 'x' + Math.round(mem[j].d * 1000)) : '',
                      code: mem[j].code || '' });
        }
        rows.sort(function (a, b) {
          var d = TRUSS_ROLE_ORDER.indexOf(a.kind) - TRUSS_ROLE_ORDER.indexOf(b.kind);
          return d || (b.length - a.length);
        });

        out.push({
          label: (TS.prefix === 'Jack' ? 'Jack truss (hip end)' : 'Truss') + ' T' + (i + 1) +
                 (TS.plies > 1 ? '  (2 of them doubled as a girder)' : ''),
          qty: TS.n || 1,
          span: TS.span,
          rise: TS.rise,
          totalRun: TS.run,
          members: mem,          /* for the drawing */
          rows: rows,            /* for the table beside it */
          /* and the marks its bodies wear, so one of them can be photographed */
          tags: TS.tags || []
        });
      }
      /* WHAT WENT OUT, SO A SHEET THAT LOOKS WRONG CAN BE TRACED IN ONE LINE.

         A drawing that does not match the model is either a shape the product
         never sent or a shape this file dropped on the way. The count says
         which. */
      try {
        var nM = 0, nP = 0, oi, oj;
        for (oi = 0; oi < out.length; oi++) {
          for (oj = 0; oj < (out[oi].members || []).length; oj++) {
            nM++;
            if (out[oi].members[oj].poly) nP++;
          }
        }
        _log('truss PDF sheets ' + out.length + ', members ' + nM +
             ', of which with a cut outline ' + nP);
      } catch (ePT) {}
      return out;
    }

    function _pdfCutList() {
      var st = _stage();
      if (!st || !st.group || !global.THREE) return [];
      var cutN = 0, cutMax = 0, cutTot = 0;
      var T2 = global.THREE, bx = new T2.Box3(), by = {};
      st.group.traverse(function (o) {
        if (!o.isMesh) return;
        var m = o.userData && o.userData.asm;
        var tag = m ? (typeof m === 'string' ? m : (m.code || '')) : '';
        if (!tag) return;
        var code = (CATALOG[tag] && CATALOG[tag].code) ? CATALOG[tag].code : tag;
        var row = null;
        try { row = _catRow(code); } catch (e) {}
        if (!_isCutStock(code, row)) return;
        /* A CONNECTOR PLATE IS STEEL (core-v264). The plates wore the mark of
           the member they lie on, so 144 of them were listed as timber pieces of
           250 and 254 mm under the web's profile and packed into its lengths on
           the shopping list. A plate is billed by its own code; it is not cut. */
        if (o.userData && o.userData.trussPart === 'plate') return;
        /* A PIECE IS AS LONG AS IT IS, NOT AS LONG AS ITS SHADOW.

           setFromObject gives the axis-aligned box in world space, and a member
           lying at an angle fills a box shorter than itself: a 4 m rafter at 26
           degrees measures 3.60, at 38 degrees 3.15, and a hip rafter turned
           45 degrees measures 2.54 -- a third short. Every sloping piece has
           been cut short on this list, and unlike the bill this one goes to
           somebody with a saw (Marek).

           The geometry knows its own size. Reading the box BEFORE the rotation
           is applied gives the piece, whichever way it is later turned, and the
           scale is the only thing that has to be carried over. */
        try {
          var g = o.geometry, len = 0;
          if (g) {
            if (!g.boundingBox) g.computeBoundingBox();
            var gb = g.boundingBox, sc = o.scale || { x: 1, y: 1, z: 1 };
            var d = [Math.abs(gb.max.x - gb.min.x) * (sc.x || 1),
                     Math.abs(gb.max.y - gb.min.y) * (sc.y || 1),
                     Math.abs(gb.max.z - gb.min.z) * (sc.z || 1)];
            len = Math.max(d[0], d[1], d[2]);
          } else {
            bx.setFromObject(o);
            len = Math.max(bx.max.x - bx.min.x, bx.max.y - bx.min.y, bx.max.z - bx.min.z);
          }
          if (!(len > 0.05)) return;                      /* an offcut, not a piece */
          if (!by[code]) by[code] = [];
          by[code].push(len);
          cutN++;
          if (len > cutMax) cutMax = len;
          cutTot += len;
        } catch (e) {}
      });

      /* AND IT SAYS SO IN THE LOG. This list goes to somebody with a saw and
         has been cutting every sloping piece short; a line in the snapshot is
         how we see that it stopped (Marek: pridat do logu at se da kopirovat). */
      try {
        _say('cut list  ' + cutN + ' piece(s), longest ' + (Math.round(cutMax * 1000)) +
             ' mm, total ' + (Math.round(cutTot)) + ' m');
      } catch (eCL2) {}
      /* THE SAME LENGTH IS ONE LINE, WITH A COUNT.

         A shed drew twenty pages of "1 x 117.70 in", one under the next,
         because every stud in the building had its own row. Nobody cuts from a
         list like that -- they cut 21 pieces at 117.70 and move on. Lengths are
         rounded to the millimetre (or a tenth of an inch) first, so two pieces
         that differ by a rounding error count as one cut rather than two nearly
         identical rows (Marek: mas tam vycet vsech polozek i kdyz jsou
         vicekrat, uved na zacatku pocet). */
      var out = [], c;
      for (c in by) {
        if (!by.hasOwnProperty(c)) continue;
        var met = true; try { met = _pickMetric(); } catch (e) {}
        var seen = {}, order = [], q;
        for (q = 0; q < by[c].length; q++) {
          var L = met ? Math.round(by[c][q] * 1000)
                      : Math.round(by[c][q] * 39.3700787 * 10) / 10;
          if (!seen[L]) { seen[L] = 0; order.push(L); }
          seen[L]++;
        }
        order.sort(function (a, b2) { return b2 - a; });   /* longest first */
        var nm = c;
        try { nm = _asmName(c, '', c) || c; } catch (e) {}
        out.push({
          profile: _pdfSafe(nm), profileCode: c, unit: met ? 'mm' : 'in',
          pieces: order.map(function (L2, i) {
            return { id: String(i + 1), length: L2, qty: seen[L2], label: '' };
          })
        });
      }
      out.sort(function (a, b2) { return b2.pieces.length - a.pieces.length; });
      return out;
    }

    /* =========================================================================
       THE SHOPPING LIST IS A BUYING LIST, NOT A SECOND BILL.

       The bill says the building needs 698 ft of 2x4. Nobody sells 698 ft of
       2x4. Somebody standing in the aisle needs to know how many EIGHT FOOT
       sticks and how many TWELVE -- and that is a different calculation: the
       cut pieces packed into the lengths the yard actually stocks.

       So each entry is the item's own name, exactly as the bill prints it, and
       under it a short table: size, how many, and what it leaves over.

       Only what is SOLD IN SIZES is here. A door, a sink, a box of screws is
       bought by the piece and the bill already says how many; putting them here
       would bury the lines that need thinking about (Marek: neni treba resit ty
       polozky, ktere jsou na pcs).

       Two shapes of goods, two calculations:

         length   timber, battens, trims, gutters, flashings -- the CUT pieces
                  are packed into stock bars, longest first, each new bar the
                  shortest stock length that takes the piece. That is how
                  somebody cuts, so it is how somebody should buy.
         sheet    boards, membranes, coverings -- area over the area of one
                  sheet, rounded up.
       ========================================================================= */
    var SALE_LEN_M  = [3.0, 4.0, 5.0, 6.0];
    var SALE_LEN_FT = [8, 10, 12, 16];
    var SHEET_M2    = 3.125;      /* 2500 x 1250 */
    var SHEET_FT2   = 32;         /* 4 ft x 8 ft */

    function _shopKind(row) {
      var u = String((row && (row[12] || row[13])) || '').toUpperCase().replace(/\s/g, '');
      if (u === 'M' || u === 'FT') return 'length';
      if (u === 'M2' || u === 'FT2') return 'sheet';
      return '';                  /* pcs, ls, kg -- bought as the bill says */
    }

    /* longest first into the shortest bar that takes it: the way a cut list is
       worked through, so the counts match what actually happens */
    function _shopPack(pieces, stock) {
      var bars = [], i, j2, p, placed, k;
      var sorted = pieces.slice().sort(function (a, b) { return b - a; });
      for (i = 0; i < sorted.length; i++) {
        p = sorted[i];
        placed = false;
        for (j2 = 0; j2 < bars.length; j2++) {
          if (bars[j2].left >= p - 1e-6) { bars[j2].left -= p; placed = true; break; }
        }
        if (placed) continue;
        var fit = null;
        for (k = 0; k < stock.length; k++) if (stock[k] >= p - 1e-6) { fit = stock[k]; break; }
        if (fit == null) fit = stock[stock.length - 1];   /* longer than anything sold */
        bars.push({ len: fit, left: fit - p });
      }
      var by = {}, out = [];
      for (i = 0; i < bars.length; i++) {
        var key = String(bars[i].len);
        if (!by[key]) { by[key] = { size: bars[i].len, count: 0, waste: 0 }; out.push(by[key]); }
        by[key].count++;
        by[key].waste += bars[i].left;
      }
      out.sort(function (a, b) { return a.size - b.size; });
      return out;
    }

    function _pdfShopping() {
      var met = true; try { met = _pickMetric(); } catch (e) {}
      var codes = _pdfCodes(), rows = [], c, k2;
      /* the cut pieces, back in metres, so they can be packed */
      var cut = {};
      try {
        var cl = _pdfCutList();
        for (k2 = 0; k2 < cl.length; k2++) {
          var g = cl[k2], arr = [], q;
          for (q = 0; q < g.pieces.length; q++) {
            var L = met ? (g.pieces[q].length / 1000) : (g.pieces[q].length / 39.3700787);
            var n = g.pieces[q].qty || 1;
            while (n-- > 0) arr.push(L);
          }
          cut[g.profileCode] = arr;
        }
      } catch (eC) {}

      for (c in codes) {
        if (!codes.hasOwnProperty(c)) continue;
        var row = null; try { row = _catRow(c); } catch (e) {}
        var kind = _shopKind(row);
        if (!kind) continue;                       /* bought by the piece */
        var qtyM = Math.round(codes[c] * 100) / 100;
        var nm = c; try { nm = _asmName(c, '', c) || c; } catch (e) {}
        var unit = ''; try { unit = _pickUnit(row, null); } catch (e) {}
        var shown = qtyM;
        try { if (!met && row) shown = Math.round(qtyM * _pickConv(row[12], row[13]) * 100) / 100; } catch (e) {}
        var buy = [], note = '';

        if (kind === 'length') {
          var pieces = cut[c] || null;
          if (pieces && pieces.length) {
            var stock = met ? SALE_LEN_M : SALE_LEN_FT.map(function (f) { return f * 0.3048; });
            var packed = _shopPack(pieces, stock);
            for (k2 = 0; k2 < packed.length; k2++) {
              buy.push({
                size:  met ? (Math.round(packed[k2].size * 100) / 100 + ' m')
                           : (Math.round(packed[k2].size / 0.3048) + ' ft'),
                count: packed[k2].count,
                waste: met ? (Math.round(packed[k2].waste * 100) / 100 + ' m')
                           : (Math.round(packed[k2].waste / 0.3048 * 10) / 10 + ' ft')
              });
            }
            note = pieces.length + ' cut(s)';
          } else {
            /* nothing drawn to cut -- sold by the length, so bought in whole
               bars of the commonest size */
            var one = met ? 6.0 : (16 * 0.3048);
            buy.push({ size: met ? '6 m' : '16 ft', count: Math.ceil(qtyM / one), waste: '' });
            note = 'from the quantity, not from cuts';
          }
        } else {
          var per = met ? SHEET_M2 : (SHEET_FT2 * 0.09290304);
          buy.push({ size: met ? '2500 x 1250' : '4 x 8 ft',
                     count: Math.ceil(qtyM / per), waste: '' });
          note = met ? (SHEET_M2 + ' m2 a sheet') : (SHEET_FT2 + ' ft2 a sheet');
        }

        rows.push({ code: c, name: _pdfSafe(nm), qty: shown, unit: _pdfSafe(unit),
                    kind: kind, buy: buy, note: _pdfSafe(note) });
      }
      rows.sort(function (a, b) {
        if (a.kind !== b.kind) return a.kind === 'length' ? -1 : 1;
        return a.name < b.name ? -1 : 1;
      });
      _log('pdf shopping: ' + rows.length + ' item(s) sold in sizes (' +
           rows.filter(function (r) { return r.kind === 'length'; }).length + ' by length, ' +
           rows.filter(function (r) { return r.kind === 'sheet'; }).length +
           ' in sheets) -- everything bought by the piece is left to the bill');
      return rows;
    }

    /* =======================================================================
       ONE TRUSS, PHOTOGRAPHED SQUARE ON.

       Drawing a truss from numbers meant re-deriving the frame it is built in,
       and a piece whose body is turned round comes out mirrored -- which is why
       three rounds of vector drawing produced shapes that looked plausible and
       matched nothing. A picture of the model cannot disagree with the model.

       Everything else in the scene is hidden, the bodies of ONE truss are left,
       the camera is put on the normal of their own plane and told to look at
       them, and the canvas is read off. Each member is painted by the job it
       does, so the sheet can be read at a glance (Marek: pro pdf bych je klidne
       udelal barevne -- horni pasnice jedna barva, spodni jina). */
    /* A TRUSS MEMBER BY WHAT IT DOES, AND THE COLOUR THAT PRINTS.

       The photograph was lit and painted in timber shades, so on paper every
       member came out a dark brown and the sheet had no legend. The members
       are now grouped the way a truss is read -- top chord, bottom chord,
       post, vertical web, diagonal web, connector plate -- each in a distinct
       saturated colour, photographed unlit, with the same colours in a legend
       beside the table; the top and bottom chords wear the frame sheets' top
       and bottom plate colours (Marek: podobnou legendu i pro vazniky, stejne
       barevne cleneni). */
    var TRUSS_PART_ROLE = {
      chord: 'tchord', jack_rafter: 'tchord', hip_rafter: 'tchord', cal_rafter: 'tchord',
      tie: 'bchord', jack_bottom: 'bchord', hip_bottom: 'bchord', cal_bottom: 'bchord',
      post: 'tpost', jack_post: 'tpost', cal_post: 'tpost',
      wvert: 'vweb', jack_vert1: 'vweb', jack_vert2: 'vweb', hip_vert: 'vweb', cal_vert: 'vweb',
      web: 'dweb', jack_diag1: 'dweb', jack_diag2: 'dweb', hip_diag: 'dweb', cal_diag: 'dweb',
      plate: 'cplate'
    };
    var TRUSS_ROLE_COLOR = {
      tchord: 0xD35400,   /* top chord       -- orange      */
      bchord: 0x6B3E1A,   /* bottom chord    -- dark brown  */
      tpost:  0x1F5FA8,   /* post            -- blue        */
      vweb:   0x7D3C98,   /* vertical web    -- purple      */
      dweb:   0x4E7D2A,   /* diagonal web    -- green       */
      cplate: 0x5D6D7E    /* connector plate -- slate grey  */
    };
    var TRUSS_ROLE_ORDER = ['tchord', 'bchord', 'tpost', 'vweb', 'dweb', 'cplate'];
    /* the role of a published member: its body's part when the product sends
       one, else what its kind and slope say */
    function _trussRole(part, kind, angle) {
      if (part && TRUSS_PART_ROLE[part]) return TRUSS_PART_ROLE[part];
      if (kind === 'chord') return 'tchord';
      if (kind === 'tie') return 'bchord';
      if (kind === 'post') return 'tpost';
      return (Math.abs(angle || 0) >= 89.5) ? 'vweb' : 'dweb';
    }

    /* =======================================================================
       A WALL PANEL IS A SHEET TOO.

       The same three moves as a truss: the product marks the bodies of each
       panel, this reads them straight out of the scene, and the photographer
       takes one panel at a time. What the mark deliberately leaves out is the
       upper top plate -- it runs the whole wall over every panel joint and ties
       them together, so it belongs to no panel and appears on no panel's sheet.

       The table is built from the bodies themselves: a piece's longest dimension
       is its length, the other two are its section, and a piece taller than it is
       long is an upright. Nothing is derived twice (Marek: udelejme jeste stejnym
       zpusobem stenove panely, kazdy zvlast). */
    /* THE FRAME SHEETS SAY WHAT EACH PIECE IS.

       The product writes a role on every body of a frame (sole, top, stud,
       jack, king, cripple, joint, header, sill); the table groups by it and the
       photograph is coloured by it. Where a product writes none the old reading
       stands: taller than long is a stud, anything else a plate.

       AND TWO FRAMES THAT ARE THE SAME FRAME ARE ONE SHEET. Same pieces at the
       same places -- or at the mirrored places, which is the same frame turned
       round -- and the sheet carries both marks and x2 (Marek: pokud jsou dva
       stejne ramy, muzou se sloucit). The foam seal under the sole plate is not
       timber and is not on the sheet. */
    /* COLOURS THAT PRINT. The first set was pale timber shades lit by the
       scene, so on paper every piece came out the same muddy brown and only
       the legend was in colour. Each role is now a distinct, saturated colour,
       none of them light, and the frame is photographed unlit (flat colour) so
       the page shows exactly the colour in the legend (Marek: barvy nesmi byt
       svetle, musi byt na papire videt). */
    var FRAME_SHOT_COLOR = {
      sole:    0x6B3E1A,   /* bottom plate   -- dark brown   */
      top:     0xD35400,   /* top plate      -- orange       */
      ledger:  0xB7950B,   /* ledger         -- dark yellow  */
      stud:    0x8E6B3E,   /* studs          -- timber brown */
      joint:   0x4E7D2A,   /* joint stud     -- green        */
      cripple: 0x5D6D7E,   /* cripples       -- slate grey   */
      jack:    0x1F5FA8,   /* jack           -- blue         */
      king:    0x7D3C98,   /* king           -- purple       */
      header:  0xC0392B,   /* header         -- red          */
      sill:    0x138D75,   /* sill           -- teal         */
      other:   0x8E6B3E
    };
    var FRAME_ROLE_ORDER = ['sole', 'top', 'stud', 'joint', 'jack', 'king', 'header', 'sill', 'cripple', 'ledger', 'other'];

    function _panelSheets() {
      var T = global.THREE, st = _stage();
      if (!T || !st || !st.scene) return [];
      var byTag = {};
      st.scene.traverse(function (o) {
        if (!o || !o.isMesh || !o.userData || !o.userData.panelTag) return;
        if (o.userData.frameRole === 'seal') return;
        var t = o.userData.panelTag;
        if (!byTag[t]) byTag[t] = [];
        byTag[t].push(o);
      });
      var keys = Object.keys(byTag).sort(function (u, v) {
        var mu = /^W(\d+)P(\d+)$/.exec(u), mv = /^W(\d+)P(\d+)$/.exec(v);
        if (mu && mv) return (+mu[2]) - (+mv[2]);
        return u < v ? -1 : (u > v ? 1 : 0);
      });
      var out = [], bySig = {}, ki, frames = 0;
      function r5(v) { return Math.round(v * 200) / 200; }   /* 5 mm */
      for (ki = 0; ki < keys.length; ki++) {
        var k = keys[ki], bodies = byTag[k];
        if (bodies.length < 2) continue;

        var pbox = new T.Box3(), bi;
        for (bi = 0; bi < bodies.length; bi++) pbox.expandByObject(bodies[bi]);
        if (pbox.isEmpty()) continue;
        frames++;
        var psz = pbox.getSize(new T.Vector3());
        var alongX = psz.x >= psz.z;
        var base = alongX ? pbox.min.x : pbox.min.z;
        var runLen = alongX ? psz.x : psz.z, bottom = pbox.min.y;

        var mem = [], rows = [], run = 0, sigA = [], sigB = [], roles = {};
        for (bi = 0; bi < bodies.length; bi++) {
          var bb = new T.Box3().setFromObject(bodies[bi]);
          var bs = bb.getSize(new T.Vector3());
          var runExt = alongX ? bs.x : bs.z;
          var a1 = (alongX ? bb.min.x : bb.min.z) - base;
          var a2 = (alongX ? bb.max.x : bb.max.z) - base;
          var upright = bs.y > runExt;
          var uB = bodies[bi].userData || {};
          /* the piece as it is CUT: the product says so where the drawing is
             eased or stops short for a seam; the box is the fallback */
          var L = (uB.cutLen > 0) ? uB.cutLen : (upright ? bs.y : runExt);
          var role = uB.frameRole || (upright ? 'stud' : 'other');
          roles[role] = 1;
          run += L;
          if (upright) {
            mem.push({ kind: 'post', p1: (a1 + a2) / 2, y1: bb.min.y, p2: (a1 + a2) / 2, y2: bb.max.y,
                       d: Math.max(bs.x, bs.z), t: Math.min(bs.x, bs.z) });
          } else {
            mem.push({ kind: 'chord', p1: a1, y1: (bb.min.y + bb.max.y) / 2, p2: a2, y2: (bb.min.y + bb.max.y) / 2,
                       d: bs.y, t: Math.min(bs.x, bs.z) });
          }
          /* the piece, where it stands, and the same read from the other end */
          var y1r = r5(bb.min.y - bottom), y2r = r5(bb.max.y - bottom);
          sigA.push(role + ':' + r5(a1) + ':' + r5(a2) + ':' + y1r + ':' + y2r);
          sigB.push(role + ':' + r5(runLen - a2) + ':' + r5(runLen - a1) + ':' + y1r + ':' + y2r);
          /* the section, in the order a merchant writes it */
          var dims = [bs.x, bs.y, bs.z].sort(function (u, v) { return u - v; });
          var sect = uB.cutSect || (Math.round(dims[0] * 1000) + 'x' + Math.round(dims[1] * 1000));
          var Lb = Math.round(L * 1000);
          var key = role + '|' + sect + '|' + Lb;
          var found = null, ri;
          for (ri = 0; ri < rows.length; ri++) if (rows[ri].key === key) { found = rows[ri]; break; }
          if (found) { found.qty++; continue; }
          rows.push({ key: key, qty: 1, kind: role, section: sect, length: Lb / 1000,
                      angle: upright ? 90 : 0 });
        }
        rows.sort(function (u, v) {
          var d = FRAME_ROLE_ORDER.indexOf(u.kind) - FRAME_ROLE_ORDER.indexOf(v.kind);
          return d || (v.length - u.length);
        });
        sigA.sort(); sigB.sort();
        var sA = r5(runLen) + '|' + r5(psz.y) + '|' + sigA.join(';');
        var sB = r5(runLen) + '|' + r5(psz.y) + '|' + sigB.join(';');
        var sig = (sA < sB) ? sA : sB;

        if (bySig[sig]) {
          bySig[sig].qty++;
          bySig[sig].tags.push(k);
          bySig[sig].label = bySig[sig].tags.join(' = ');
          continue;
        }
        /* a frame is as long as its bottom plate -- the drawing stops each plate
           a seam short at a joint, the plate itself does not */
        var soleLen = 0, sr;
        for (sr = 0; sr < rows.length; sr++) if (rows[sr].kind === 'sole') soleLen = Math.max(soleLen, rows[sr].length);
        var legend = [], rk;
        for (rk = 0; rk < FRAME_ROLE_ORDER.length; rk++) {
          var rn = FRAME_ROLE_ORDER[rk];
          if (roles[rn]) legend.push({ role: rn, color: FRAME_SHOT_COLOR[rn] });
        }
        var sheetP = {
          kind: 'panel',
          label: k,
          qty: 1,
          span: soleLen || runLen,
          rise: psz.y,
          totalRun: run,
          members: mem,
          rows: rows,
          legend: legend,
          tags: [k],
          /* photographed from outside the building */
          face: 'out'
        };
        bySig[sig] = sheetP;
        out.push(sheetP);
      }
      _say('frame sheets ' + frames + ' frame(s) -> ' + out.length + ' sheet(s)' +
           (out.length ? (': ' + out.map(function (S) {
             return S.label + (S.qty > 1 ? (' x' + S.qty) : '') + ' ' + Math.round(S.span * 1000) + 'x' +
                    Math.round(S.rise * 1000) + ' ' + S.rows.reduce(function (n, r) { return n + r.qty; }, 0) + 'pcs';
           }).join(' | ')) : ''));
      return out;
    }

    /* the sheets, each with its own photograph where one could be taken --
       panels first, because a wall is stood up before a roof goes on it (Marek:
       dej je pred vazniky) */
    function _pdfTrussSheets() {
      var sh = [];
      try { sh = sh.concat(_panelSheets()); } catch (ePS) { _say('panel sheets threw: ' + ((ePS && ePS.message) || ePS)); }
      try { sh = sh.concat(_pdfTrusses()); } catch (ePT2) { _say('truss sheets threw: ' + ((ePT2 && ePT2.message) || ePT2)); }
      try { _trussShots(sh); } catch (eTS2) { _say('truss shots threw: ' + ((eTS2 && eTS2.message) || eTS2)); }
      return sh;
    }

    function _trussShots(sheets) {
      var T = global.THREE, st = _stage();
      if (!T || !st || !st.renderer || !st.scene || !sheets || !sheets.length) return 0;

      /* every body that knows which truss it belongs to */
      var all = [], marked = [];
      st.scene.traverse(function (o) {
        if (!o || !o.isMesh) return;
        all.push(o);
        var u = o.userData || {};
        if (u.trussSig || u.trussGrp || u.panelTag) marked.push(o);
      });
      if (!marked.length) { _say('truss shots: no marked bodies in the scene'); return 0; }

      /* ON WHITE, NOT ON WHATEVER THE VIEWPORT HAPPENS TO CLEAR TO.

         The canvas clears to nothing and the shot is saved as a JPEG, which has
         no transparency -- so every truss arrived on a black field and the sheet
         read as a photograph taken at night. The page is white, so the picture
         is taken on white (Marek: maji cerne pozadi, muzeme bez pozadi). */
      var oldBG = st.scene.background, oldClear = null, oldAlpha = 1;
      try {
        oldClear = new T.Color();
        st.renderer.getClearColor(oldClear);
        oldAlpha = st.renderer.getClearAlpha();
      } catch (eCC) { oldClear = null; }
      try {
        st.scene.background = new T.Color(0xFFFFFF);
        st.renderer.setClearColor(0xFFFFFF, 1);
      } catch (eBG) {}

      var savedVis = all.map(function (o) { return o.visible; });
      var savedMat = marked.map(function (o) { return o.material; });
      var mats = {};
      function matFor(part) {
        var role = TRUSS_PART_ROLE[part || 'web'] || 'dweb';
        var key = 'truss:' + role;
        if (!mats[key]) {
          /* unlit, like the frames: the colour on the page is the legend's */
          mats[key] = new T.MeshBasicMaterial({ color: TRUSS_ROLE_COLOR[role] });
        }
        return mats[key];
      }
      function frameMat(role) {
        var key = 'frame:' + (role || 'other');
        if (!mats[key]) {
          /* unlit: a square-on photograph of a flat frame needs no shading,
             and a lit face read far darker than its colour */
          mats[key] = new T.MeshBasicMaterial({
            color: (FRAME_SHOT_COLOR[role] != null) ? FRAME_SHOT_COLOR[role] : FRAME_SHOT_COLOR.other
          });
        }
        return mats[key];
      }

      /* WHERE THE BUILDING IS, so a panel can be photographed from OUTSIDE it.

         The camera used to sit on whichever side of the plane came first, which
         for two facing walls means one is shot from the outside and the other
         from the inside -- the same panel read backwards. The middle of every
         marked body is a good enough centre of the building: the outward side is
         simply the one further from it (Marek: zvenku). */
      var wholeBox = new T.Box3(), wq;
      for (wq = 0; wq < marked.length; wq++) wholeBox.expandByObject(marked[wq]);
      var allCtr = wholeBox.isEmpty() ? new T.Vector3() : wholeBox.getCenter(new T.Vector3());

      var cam = new T.OrthographicCamera(-1, 1, 1, -1, 0.01, 400);
      var el = st.renderer.domElement;
      var aspect = (el.width && el.height) ? (el.width / el.height) : 1.6;
      var made = 0, si, q;

      for (si = 0; si < sheets.length; si++) {
        var S = sheets[si];
        var tags = S.tags || [];
        if (!tags.length) continue;
        var tag = tags[0];
        var pick = marked.filter(function (o) {
          var u = o.userData || {};
          if (u.panelTag === tag && u.frameRole === 'seal') return false;
          return (u.trussSig === tag) || (u.trussGrp === tag) || (u.panelTag === tag);
        });
        if (pick.length < 2) continue;

        /* ONE TRUSS, NOT THE WHOLE ROW, AND ITS OWN PLANE FROM ITS LONGEST PIECE.

           Every instance of a standard truss wears the same mark, so the pick was
           the whole row. The plane was then read off the box round ALL of them,
           and on a building longer than its span the row is the longer side --
           the camera turned to face the row and photographed thirteen trusses
           edge on, a line of bars (Marek: vazniky jsi natocil o 90 stupnu, jsou z
           profilu). It never showed on the ADU because its trusses span further
           than the row they stand in runs.

           The longest piece is a chord, and a chord runs along the span: that
           gives the plane. Of the row, only the pieces standing at that chord's
           own station are kept -- both plies of a girder included, since they
           stand 64 mm apart and the next truss is a module away. */
        /* A HIP TRUSS STANDS AT AN ANGLE, SO THE PLANE IS A DIRECTION, NOT AN AXIS.
           The chord's own run in plan is read off its vertices (the main axis of
           its footprint), the station across it is measured along the normal, and
           the camera looks down that normal -- square on to a truss whichever way
           it faces. */
        var spanE = null, spanN = null;
        function _pts2(o) {
          var out = [], pa = o.geometry && o.geometry.attributes && o.geometry.attributes.position, vv = new T.Vector3(), iv;
          if (!pa) return out;
          o.updateWorldMatrix(true, false);
          for (iv = 0; iv < pa.count; iv++) {
            vv.fromBufferAttribute(pa, iv).applyMatrix4(o.matrixWorld);
            out.push([vv.x, vv.y, vv.z]);
          }
          return out;
        }
        if (S.kind !== 'panel') {
          var lgO = null, lgL = -1, bq1;
          for (q = 0; q < pick.length; q++) {
            bq1 = new T.Box3().setFromObject(pick[q]);
            var hq = Math.hypot(bq1.max.x - bq1.min.x, bq1.max.z - bq1.min.z);
            if (hq > lgL) { lgL = hq; lgO = pick[q]; }
          }
          var P2 = lgO ? _pts2(lgO) : [];
          if (P2.length > 2) {
            var mx2 = 0, mz2 = 0, sxx = 0, szz = 0, sxz = 0, ip;
            for (ip = 0; ip < P2.length; ip++) { mx2 += P2[ip][0]; mz2 += P2[ip][2]; }
            mx2 /= P2.length; mz2 /= P2.length;
            for (ip = 0; ip < P2.length; ip++) {
              var dxp = P2[ip][0] - mx2, dzp = P2[ip][2] - mz2;
              sxx += dxp * dxp; szz += dzp * dzp; sxz += dxp * dzp;
            }
            var thE = 0.5 * Math.atan2(2 * sxz, sxx - szz);
            spanE = { x: Math.cos(thE), z: Math.sin(thE) };
            spanN = { x: -spanE.z, z: spanE.x };
            /* the camera on the +x or +z side, as it always was, so every sheet
               reads the same way round */
            if ((Math.abs(spanN.x) > Math.abs(spanN.z)) ? (spanN.x < 0) : (spanN.z < 0)) { spanN.x = -spanN.x; spanN.z = -spanN.z; }
            /* and along the run, within the chord's own reach -- two instances
               that share a mark can also stand in LINE, one at each end of the
               building, and the picture would then hold both, each a speck */
            var ref1 = mx2 * spanN.x + mz2 * spanN.z, one1 = [], e0 = 1e9, e1 = -1e9;
            for (ip = 0; ip < P2.length; ip++) {
              var pe = P2[ip][0] * spanE.x + P2[ip][2] * spanE.z;
              if (pe < e0) e0 = pe;
              if (pe > e1) e1 = pe;
            }
            for (q = 0; q < pick.length; q++) {
              bq1 = new T.Box3().setFromObject(pick[q]);
              var cq = bq1.getCenter(new T.Vector3());
              var cE = cq.x * spanE.x + cq.z * spanE.z;
              if (Math.abs(cq.x * spanN.x + cq.z * spanN.z - ref1) < 0.12 &&
                  cE > e0 - 0.2 && cE < e1 + 0.2) one1.push(pick[q]);
            }
            _say('truss shot ' + (S.label || tag) + ': ' + one1.length + ' of ' + pick.length +
                 ' marked bodies are one truss, its span runs at ' + Math.round(thE * 180 / Math.PI) +
                 ' deg in plan, photographed square on');
            if (one1.length >= 2) pick = one1;
          }
        }

        /* a truss sheet's legend: the roles its photographed bodies have */
        if (S.kind !== 'panel') {
          var seenR = {}, lgT = [], rr;
          for (q = 0; q < pick.length; q++) {
            var uR = pick[q].userData || {};
            if (uR.trussPart != null) seenR[TRUSS_PART_ROLE[uR.trussPart || 'web'] || 'dweb'] = 1;
          }
          for (rr = 0; rr < TRUSS_ROLE_ORDER.length; rr++) {
            if (seenR[TRUSS_ROLE_ORDER[rr]]) lgT.push({ role: TRUSS_ROLE_ORDER[rr], color: TRUSS_ROLE_COLOR[TRUSS_ROLE_ORDER[rr]] });
          }
          if (lgT.length) S.legend = lgT;
        }
        /* nothing but this truss */
        for (q = 0; q < all.length; q++) all[q].visible = false;
        for (q = 0; q < pick.length; q++) {
          pick[q].visible = true;
          var uP = pick[q].userData || {};
          pick[q].material = (uP.panelTag && !uP.trussPart) ? frameMat(uP.frameRole) : matFor(uP.trussPart);
        }

        /* THE TRUSS'S OWN PLANE, TAKEN FROM THE BODIES THEMSELVES.

           The way a truss runs is simply the horizontal direction its pieces
           spread along the most. No frame to reconstruct, no sign to guess. */
        var box = new T.Box3();
        for (q = 0; q < pick.length; q++) box.expandByObject(pick[q]);
        if (box.isEmpty()) continue;
        var ctr = box.getCenter(new T.Vector3());
        var siz = box.getSize(new T.Vector3());
        var ex = 1, ez = 0;
        if (siz.z > siz.x) { ex = 0; ez = 1; }
        var halfRun = (ex ? siz.x : siz.z) / 2;
        if (spanE) {
          /* along the truss's own run, measured on its own vertices */
          var hr = 0, pq2, P3;
          for (q = 0; q < pick.length; q++) {
            P3 = _pts2(pick[q]);
            for (pq2 = 0; pq2 < P3.length; pq2++) {
              var dr = Math.abs((P3[pq2][0] - ctr.x) * spanE.x + (P3[pq2][2] - ctr.z) * spanE.z);
              if (dr > hr) hr = dr;
            }
          }
          if (hr > 0) halfRun = hr;
        }
        var halfUp = siz.y / 2;
        var pad = 1.10;
        var hw = Math.max(halfRun * pad, 0.3);
        var hh = Math.max(halfUp * pad, 0.3);
        if (hw / hh < aspect) hw = hh * aspect; else hh = hw / aspect;
        cam.left = -hw; cam.right = hw; cam.top = hh; cam.bottom = -hh;
        /* WHERE THE PIECE LIES IN THE PICTURE, for its two dimensions (core-v262).

           The sheet draws the overall length and height beside the photograph
           (Marek: koty, staci 2 -- delka a vyska), and the photograph is a
           picture, so the page has to be told where in it the piece starts and
           ends: as fractions of the image, left to right and top to bottom,
           with the lengths themselves measured off the same bodies. */
        try {
          var dimL = 2 * halfRun, fx0 = 0.5 - halfRun / (2 * hw), fx1 = 0.5 + halfRun / (2 * hw);
          if (spanE && spanN) {
            var eLo = 1e9, eHi = -1e9, pq3, P4;
            for (q = 0; q < pick.length; q++) {
              P4 = _pts2(pick[q]);
              for (pq3 = 0; pq3 < P4.length; pq3++) {
                var de = (P4[pq3][0] - ctr.x) * spanE.x + (P4[pq3][2] - ctr.z) * spanE.z;
                if (de < eLo) eLo = de;
                if (de > eHi) eHi = de;
              }
            }
            var sR = ((spanN.z * spanE.x - spanN.x * spanE.z) >= 0) ? 1 : -1;
            var xa = 0.5 + sR * eLo / (2 * hw), xb = 0.5 + sR * eHi / (2 * hw);
            fx0 = Math.min(xa, xb); fx1 = Math.max(xa, xb); dimL = eHi - eLo;
          }
          S.dim = { x0: fx0, x1: fx1, yT: 0.5 - halfUp / (2 * hh), yB: 0.5 + halfUp / (2 * hh),
                    L: dimL, H: 2 * halfUp };
        } catch (eDM) {}
        cam.near = 0.01; cam.far = 400;
        cam.up.set(0, 1, 0);
        /* a panel faces out; a truss has no outside, so it keeps the fixed side
           and every truss sheet is read the same way round */
        var sgn = 1;
        if (S.face === 'out') {
          var off = ex ? (ctr.z - allCtr.z) : (ctr.x - allCtr.x);
          sgn = (off >= 0) ? 1 : -1;
        }
        if (spanN) cam.position.set(ctr.x + 40 * spanN.x, ctr.y, ctr.z + 40 * spanN.z);
        else cam.position.set(ctr.x + (ex ? 0 : 40 * sgn), ctr.y, ctr.z + (ex ? 40 * sgn : 0));
        cam.lookAt(ctr);
        cam.updateProjectionMatrix();

        try {
          st.renderer.render(st.scene, cam);
          S.shot = el.toDataURL('image/jpeg', 0.92);
          made++;
        } catch (eSH) {}
      }

      /* the scene goes back exactly as it was */
      for (q = 0; q < all.length; q++) all[q].visible = savedVis[q];
      for (q = 0; q < marked.length; q++) marked[q].material = savedMat[q];
      try {
        st.scene.background = oldBG;
        if (oldClear) st.renderer.setClearColor(oldClear, oldAlpha);
      } catch (eRB) {}
      try { st.renderer.render(st.scene, st.camera); } catch (eRR) {}

      /* _say, not _log: the snapshot is built from _buildLog and _log goes to a
         console he has no way to open. Two rounds of diagnostics have already
         been lost to this exact mistake. */
      _say('truss shots ' + made + ' of ' + sheets.length + ' sheet(s) photographed');
      return made;
    }

    function _pdfShot() {
      try {
        var st = _stage();
        if (!st || !st.renderer) return null;
        var prW = _shotPrUp(st), url = null;
        try { st.renderer.render(st.scene, st.camera); } catch (e) {}
        url = st.renderer.domElement.toDataURL('image/jpeg', 0.92);
        _shotPrDown(st, prW);
        return url;
      } catch (e) { return null; }
    }

    function _pdfMetric() {
      var met = !!STATE.isMetric;
      try { met = _pickMetric(); } catch (eM) {}
      _say('frame pdf units ' + (met ? 'METRIC (mm, m)' : 'IMPERIAL (ft-in to 1/16, ft)'));
      return met;
    }

    /* THE DOCUMENT'S DATA, built once (core-v731): the PDF made here and the
       package the drawings page makes (the blueprints in between) read the
       same arguments */
    function _pdfArgs() {
      var boq = _pdfBoq(), cut = _pdfCutList(), shop = _pdfShopping();
      /* THE COVER HAD AN EMPTY BOX because it was handed a total of nought and
         no currency. Both are here: the material out of the rows, the labour
         out of the same costOf the budget uses, and the currency off the page
         (Marek: maji byt uvedene castky total / material / execution). */
      var mat = 0, exe = 0, i, c2 = null;
      for (i = 0; i < boq.length; i++) {
        mat += Number(boq[i].total) || 0;
        exe += Number(boq[i].execution) || 0;
      }
      try { c2 = (global.Shopify && global.Shopify.currency && global.Shopify.currency.active) || ''; } catch (eC) {}
      var shot = _pdfShot();
      /* the strings win when the strip is on the page; the sums stay as the
         fallback for a page that has no strip (the drawings page, a headless
         export) so the card is never empty */
      var texts = _pdfTexts();
      var cover = _pdfCoverImage();
      var args = {
        productTitle: _pdfTitle(),
        coverImage: cover || undefined,
        totals: { material: mat, execution: exe, total: mat + exe, currency: c2 || 'USD',
                  texts: texts || undefined },
        boq: boq,
        cutList: cut,
        boqParts: (function () { try { return _pdfBoqParts(); } catch (eBp) { return null; } })(),
        planSheet: _pdfPlanSheet(),
        setOut: _pdfSetOut(),
        mep: _pdfMep(),
        roofPlan: _pdfRoofPlan(),
        planTitle: _t('grp_plan', 'Plan'),
        trussSheets: _pdfTrussSheets(),
        shoppingList: shop,
        /* A PRODUCT'S BUILD-UPS (core-v292): layer by layer, with thickness,
           code and quantity -- the sauna room's walls, partitions, ceiling and
           floor, and what stands in it; the PDF draws them as a sheet */
        buildUps: (function () {
          if (typeof PDF_BUILDUPS !== 'function') return null;
          try { return PDF_BUILDUPS(); } catch (eBu) { _log('pdf build-ups: ' + (eBu && eBu.message)); return null; }
        })(),
        /* THE UNITS THE HOUSE IS BUILT IN. Every page that writes a length --
           the frame and truss sheets, the cut list -- writes it in these, the
           same switch the budget already follows (Marek: pdf musi reflektovat
           jednotky) */
        meta: { date: new Date().toISOString().slice(0, 10), isMetric: _pdfMetric() }
      };
      /* WHAT SOMEBODY CHOSE GOES IN ITS OWN FIELD, so the exporter can put it
         at the front as full-bleed pages instead of folding it into the one
         automatic "3D visualisation" sheet. The automatic frame is only used
         when they chose nothing at all (Marek). */
      /* ONLY THE VIEWS SOMEBODY CHOSE.

         The automatic 3D frame was the fallback for when nobody had captured
         anything -- a generic angle of the model, put in so the export would not
         open on a bill of quantities. Now that the + PDF button exists, it is
         just an extra page nobody asked for, and the first thing they see
         (Marek: ten prvni obrazek 3d model se nema generovat). */
      if (_pdfShots.length) {
        args.userViews = _pdfShots.map(function (u) { return { imageDataUrl: u }; });
      }
      if (args.buildUps && args.buildUps.blocks) {
        args.buildUpTitle = args.buildUps.title || '';
        args.buildUps = args.buildUps.blocks;
        _log('pdf: ' + args.buildUps.length + ' build-up block(s) for the sheet "' + args.buildUpTitle + '"');
      }
      _log('pdf cover: ' + (cover ? ('image ' + cover.slice(0, 60)) : 'NO PRODUCT IMAGE -- og:image not in the page') +
           ' | figures ' + (texts ? (texts.total + ' / ' + texts.material + ' / ' + texts.execution +
                                     (texts.time ? (' / ' + texts.time) : '')) + ' (off the strip)'
                                  : 'summed here, the strip is not on the page'));
      _log('pdf: ' + _pdfShots.length + ' chosen view(s), ' +
           boq.length + ' bill row(s), ' + cut.length + ' profile(s) to cut, ' +
           shop.length + ' shopping row(s), ' +
           shop.filter(function (r) { return !r.product; }).length + ' of them with no product yet' +
           (shot ? '' : ' | no model snapshot -- the 3D page is skipped'));
      return args;
    }
    function _pdfDefaultExport() {
      var X = global.ConfiguratorPdfExporter;
      if (!X || typeof X.export !== 'function') {
        _log('pdf: no exporter on the page');
        return;
      }
      var args = _pdfArgs();
      /* THE LINK BACK TO THIS PROJECT.

         The QR has to open THIS configuration, not the product page, so the
         short id has to exist before the document is made -- _shareEnsure makes
         one if there is not one yet. The export waits for it, and goes ahead
         without it if the worker is unreachable: a document with no QR beats no
         document (Marek: qr kod, ktery by usera nasmeroval primo na tento
         projekt). */
      /* THE PROJECT'S OWN DOCUMENTATION IN FRONT (core-v728, TAURUS, Marek:
         kupujici stahne PDF puvodni rozsirene o to, co dnes u konfiguratoru
         dodavame -- shopping list, boq, cut list). A product may answer
         __PROD._pdfOriginal() with a promise of { url, label, why } -- the
         original drawings the buyer is entitled to -- and the exporter puts
         that PDF's pages first and ours after them, one download. No answer,
         or url null, and the document is ours alone, as before. */
      var pre = null;
      try { if (typeof __PROD._pdfOriginal === 'function') pre = __PROD._pdfOriginal(); }
      catch (eO) { _log('pdf: the original THREW ' + ((eO && eO.message) || eO)); pre = null; }
      var go = function (shareUrl) {
        if (shareUrl) args.shareUrl = shareUrl;
        _log('pdf: share link ' + (shareUrl || 'NONE -- the QR is left off'));
        return Promise.resolve(pre).then(function (P) {
          if (P && P.url) { args.prependPdf = { url: P.url, label: P.label || '' }; _log('pdf: the original in front -- ' + (P.label || '') + (P.why ? (' (' + P.why + ')') : '')); }
          else if (pre) _log('pdf: no original in front' + ((P && P.why) ? (' -- ' + P.why) : ''));
        }, function (eP) { _log('pdf: the original was not found -- ' + ((eP && eP.message) || eP)); }).then(function () {
          return Promise.resolve(X.export(args)).then(function (res) {
            if (res && res.note) _log('pdf: ' + res.note);
            return res;
          });
        });
      };
      try {
        return _shareEnsure().then(function (id) {
          var u = '';
          try {
            u = global.location.origin + global.location.pathname + '?p=' + id;
          } catch (e) {}
          return go(u);
        }, function (err) {
          _log('pdf: share id unavailable -- ' + ((err && err.message) || err));
          return go('');
        });
      } catch (eS) { return go(''); }
    }

    /* -------------------------------------------------------------------------
       THE PRICE CARD IS NOT THIS FILE'S BUSINESS. There was a corrector here that
       re-centred it, hid arrows and drove the panel open from JavaScript, and it
       was a mistake from the first line: it left the liquid's own card in place
       and built a second opinion on top of it, so the two disagreed on every
       frame -- the narrow card and the ghost jumping about during load were both
       that argument happening on screen.
       The card is the liquid's, written the way the tiny house writes it. Nothing
       here touches it.
       ------------------------------------------------------------------------- */

    /* -------------------------------------------------------------------------
       BILL OF QUANTITIES. The core produces NO rows and knows NO catalog codes;
       it only collects what the layers hand back.
       ------------------------------------------------------------------------- */
    /* --------------------------------------------------------------------------
       PUBLISHING THE BILL.

       THE BUDGET WAS EMPTY BECAUSE NOTHING WAS EVER SENT TO IT. The layers
       counted, the rows were assembled, the snapshot printed them -- and the page
       was never told. The only place this lib touched the bridge was to decide
       whether to grey out the PDF button, which is a read.

       The shape is the tiny lib's, because the page on the other side is the same
       page: a map of code to quantity for the bridge, and the same figures as a
       CSV in both systems for anything reading ConfiguratorBoQ directly. Then
       loadVariantCSV is nudged, debounced, because a rebuild fires on every drag
       and re-pricing on each frame of a drag is work nobody sees.
       -------------------------------------------------------------------------- */
    var _pubT = null;

    function publishBoq() {
      _mark('publish');
      var b = boq();
      var byMaterial = {}, unitOf = {}, i;
      for (i = 0; i < b.rows.length; i++) {
        var r = b.rows[i];
        byMaterial[r.code] = Math.round(((byMaterial[r.code] || 0) + r.qty) * 1000) / 1000;
        unitOf[r.code] = String(r.unit || '').toUpperCase();
      }

      var M_FT = 3.2808, M2_FT2 = 10.7639, M3_FT3 = 35.3147;
      function r2(n) { return Math.round(n * 100) / 100; }
      var csv = [[], []];
      Object.keys(byMaterial).forEach(function (code) {
        var q = byMaterial[code], u = unitOf[code], met, imp;
        if (u === 'M')       { met = r2(q); imp = r2(q * M_FT); }
        else if (u === 'M2') { met = r2(q); imp = r2(q * M2_FT2); }
        else if (u === 'M3') { met = r2(q); imp = r2(q * M3_FT3); }
        else                 { met = Math.round(q); imp = Math.round(q); }
        csv.push([code, met, imp]);
      });

      var bridge = global.ConfiguratorBudgetBridge;
      if (bridge && typeof bridge.publishVariantData === 'function') {
        bridge.publishVariantData(byMaterial);
        global.ConfiguratorBoQ = global.ConfiguratorBoQ || {};
        global.ConfiguratorBoQ.variantData = csv;
        global.ConfiguratorBoQ.source = BOOTDATA().slug || 'house';
      } else {
        global.ConfiguratorBoQ = {
          source: BOOTDATA().slug || 'house', variantData: csv, byMaterial: byMaterial
        };
      }
      _pubNote = b.rows.length + ' row(s) -> ' +
        (bridge && bridge.publishVariantData ? 'bridge' : 'ConfiguratorBoQ only (no bridge on the page)');

      global.clearTimeout(_pubT);
      _pubT = global.setTimeout(function () { _nudgeBudget(0); }, 150);
    }

    var _pubNote = 'not published yet';

    /* THE BUDGET IS ASKED AGAIN WHEN IT DOES NOT ANSWER (core-v256).

       The bill goes to the page and the page is nudged once to price it
       (loadVariantCSV), which fetches the catalogue rows back as productData.
       Once was all it got: if the page's function was not there yet -- the
       page's scripts still loading when the first model was done -- or its
       fetch never came back, nothing asked again, and the budget sat on its
       spinner until the next change happened to publish once more (Marek:
       sem tam zamrzne nacitani budgetu). And the one line that said which of
       the two it was went to the console only.

       Now: a page that has no loadVariantCSV yet is asked again every half
       second for ten seconds; a page that was asked and has handed nothing
       back within four seconds is asked again, three times at most. Every one
       of those is counted, and the count is printed on the load line of the
       snapshot, which is printed whatever the focus. */
    var _pubCalls = 0, _pubMiss = 0, _pubRetries = 0, _pubThrew = '', _pubRetryT = null, _pubFirstAt = null;
    function _sinceLoadMs() {
      try { return Math.round(((global.performance && global.performance.now) ? global.performance.now() : Date.now()) - _T0); }
      catch (e) { return 0; }
    }
    function _catRows() {
      try { return (global.productData && global.productData.length) || 0; } catch (e) { return 0; }
    }
    function _nudgeBudget(tries) {
      tries = tries || 0;
      global.clearTimeout(_pubRetryT);
      if (typeof global.loadVariantCSV === 'function') {
        try {
          global.loadVariantCSV();
          _pubCalls++;
          if (_pubFirstAt == null) _pubFirstAt = _sinceLoadMs();
        } catch (e) {
          _pubThrew = (e && e.message) ? e.message : String(e);
          _log('budget: loadVariantCSV threw ' + _pubThrew);
        }
        /* and if the catalogue has not come back, ask once more */
        _pubRetryT = global.setTimeout(function () {
          if (!_catRows() && tries < 3) { _pubRetries++; _nudgeBudget(tries + 1); }
        }, 4000);
      } else {
        _pubMiss++;
        if (_pubMiss <= 20) _pubRetryT = global.setTimeout(function () { _nudgeBudget(tries); }, 500);
      }
    }

    function boq() {
      /* THE COATING LAYER IS REGISTERED HERE, BEFORE THE WALK. It used to be
         registered in the pass that runs AFTER a build, by which time this
         function had already collected the rows -- and the budget is assembled
         from exactly this list. */
      try { _coatRegister(null); } catch (eCR) {}

      var rows = [], i, r, ctx = {
        STATE: STATE, storeys: storeys(), seg: segments(), roof: roofPlanes(),
        poly: poly(), stack: STACK, grid: GRID()
      };
      for (i = 0; i < _layers.length; i++) {
        if (typeof _layers[i].boq !== 'function') continue;
        try {
          r = _layers[i].boq(ctx) || [];
          for (var j = 0; j < r.length; j++) { r[j].__layer = _layers[i].id; rows.push(r[j]); }
        } catch (e) { _log('boq threw in ' + _layers[i].id + ': ' + (e && e.message)); }
      }
      _say('coating  boq walked ' + _layers.length + ' layer(s), ' + rows.length + ' row(s)');
      return { rows: rows };
    }

    /* -------------------------------------------------------------------------
       STAGE
       ------------------------------------------------------------------------- */
    function _initStage() {
      var st = _stage();
      if (!st) { _log('ConfiguratorStage missing -- configurator-ui.js not loaded?'); return false; }
      var ok = st.init({
        rootId: 'cf-root', canvasId: 'cf-canvas',
        cameraPos: [14, 9, 16], target: [0, 1.5, 0],
        minDistance: 3, maxDistance: 90,

        /* COMING BACK TO THE TAB MUST NOT REFRAME THE PLAN, AND heldFor=0 IS
           WHY THREE VERSIONS OF THIS FAILED TO STOP IT.

           The hold ticked every 75 ms and was read 3300 ms later. On a page that
           is RUNNING that number cannot be zero -- it has to be 40. Zero means
           no timer of mine ran at all between the restore and the reading: the
           tab was not slow, it was STOPPED. Leaving the app suspends the page,
           and on return every pending timer fires in one burst, in an order that
           has nothing to do with the delays that were asked for.

           So the old log was never a sequence. before= was read on the way out,
           settledAt= on the way back in, and the reframe that ruined the framing
           happened in between, while nothing of mine was alive to see it. The
           same explains the rootsize/resize contradiction: rootsize is written
           the moment the observer fires, resize only after a 3.3 s timer the
           freeze never ran. Neither of the two explanations on the list was
           right; the third one was.

           Three consequences, and they are this version.

           1. ONE RESTORE PER BURST, AT THE END OF IT. Each resize used to start
              its own chain. On resume they all arrive together and overwrite one
              another. A resize now only pushes a single trailing timer further
              out, so the restore happens once, after the last of them.

           2. THE GATE IS A DEADLINE, NOT A FLAG. _resizing was one boolean
              shared by every chain in flight, so the first chain to end released
              it for all of them and the sampler went straight back to recording
              the REFITTED framing over the good one. A deadline can only be
              pushed further away, never released early by somebody else.

           3. THE FRAMING IS REMEMBERED IN METRES, NOT AS A ZOOM NUMBER. zoom
              means nothing on its own: setView('2d') rebuilds left/right/top/
              bottom from the group's bounding box and the canvas aspect on every
              single resize, so the same zoom is a different picture afterwards.
              What is worth putting back is how many metres are across the
              screen, and that is what is now sampled and restored.

           AND THE MEASUREMENT NAMED THE WRITER OF 0.270, WHICH WAS NEVER IN
           THIS FILE.

           frustum 1.76 m is the whole story. The building is 7.0 x 5.2 m, so a
           frustum of 1.76 m is not a framing of anything -- it is what
           setView('2d') produces from an EMPTY bounding box: reach 0, span
           0 * 1.15 + 0.6, times twice the aspect. The stage refits the plan by
           measuring a group that has nothing in it.

           From there MapManager does the rest. A 1.76 m frustum at zoom 1 is
           1.76 m across 1067 px, which is 1.6 mm per pixel, which is map zoom
           25.9 -- past the 24 the map can hold. So _computeZoomForLat pulls the
           CAMERA back to what the ground can match: 1.76 / (0.0061 * 1067) =
           0.270, every time, for any building, at this latitude, on this screen.
           That is the constant that looked like somebody setting a value,
           because it is exactly that -- just not from here.

           0.270 against a 1.76 m frustum is 6.51 m across, and the framing it
           replaced was 24.02 m. That is the zoom IN. This version stops it being
           drawn; the empty group behind it is the next thing to open. */
        /* core-v662: no automatic framing while the voice tour drives the
           camera or the sample view has its own */
        autoFitOk: function () {
          try { if (typeof _T !== 'undefined' && _T && _T.on) return false; } catch (eT) {}
          return !_sampleShown && STATE.view === '3d';
        },
        /* core-v804: the product's own first view and drawings' isometric
           (__PROD._viewDir / _isoDir -> { x, z, eye } or null; RUSSWORX SHED) */
        viewDir: function () { return (typeof __PROD._viewDir === 'function') ? __PROD._viewDir() : null; },
        isoDir: function () { return (typeof __PROD._isoDir === 'function') ? __PROD._isoDir() : ((typeof __PROD._viewDir === 'function') ? __PROD._viewDir() : null); },
        onResize: function () {
          try { if (typeof _heroShiftApply === 'function') _heroShiftApply('a resize'); } catch (eHs) {}
          try {
            var rootEl = $('cf-root');
            var nowT = (global.Date && Date.now) ? Date.now() : 0;
            var wNow = rootEl ? (rootEl.clientWidth | 0) : -1;
            var hNow = rootEl ? (rootEl.clientHeight | 0) : -1;
            var gap = _lastResizeAt ? (nowT - _lastResizeAt) : -1;
            var changed = (wNow !== _lastRootW || hNow !== _lastRootH);
            _sizeLog(wNow + 'x' + hNow +
                     (changed ? ('  CHANGED from ' + _lastRootW + 'x' + _lastRootH) : '  same size') +
                     '  +' + gap + 'ms' +
                     '  drawer=' + (function () {
                       var d = $('cfg-ui-drawer');
                       return d ? (d.offsetWidth + 'px') : 'none';
                     })());
            _lastRootW = wNow; _lastRootH = hNow; _lastResizeAt = nowT;
          } catch (eSz) {}

          /* OPENING THE DRAWER RE-CENTRES THE MODEL TOO. Closing it did (the
             exit refits the camera to the footprint); opening it narrowed the
             canvas and left the model where it was, off to one side of what was
             left. The drawer opens with an animation, so the canvas reaches its
             final size a little after the step is shown -- which is why this is
             done from the resize, once the size has actually changed, and only
             in the 3D view with the drawer up (Marek). */
          try {
            if (STATE.view === '3d' && changed && wizardOpen()) {
              if (_fitOpenTimer) { try { global.clearTimeout(_fitOpenTimer); } catch (eT) {} }
              _fitOpenTimer = global.setTimeout(function () {
                _fitOpenTimer = 0;
                try {
                  var st3 = _stage();
                  if (st3 && STATE.view === '3d' && wizardOpen()) {
                    st3.fitCameraToFootprint();
                    _log('drawer resized the canvas -> model re-centred in what is left');
                  }
                } catch (eF3) {}
              }, 180);
            }
          } catch (eO) {}
          try {
            if (STATE.view !== '2d') return;
            _planBlock(RESTORE_GATE_MS);
            /* IN THIS FRAME, NOT IN THREE HUNDRED MILLISECONDS. The stage has
               already refitted by the time this callback runs and the renderer
               has not drawn yet, so putting the framing back HERE means the
               wrong one is never on screen for even one frame. The trailing one
               stays for whatever arrives after the burst. */
            _planRestore(true);
            _planRestoreSoon();
          } catch (eRz) {}
        },
        log: _log
      });
      if (ok) st.rebuildGrid({ size: 60, step: STATE.isMetric ? 1.0 : FT });
      return ok;
    }

    /* and the view and the step decide whether it shows at all */
    /* THE 2D FRAME IS THE BUILDING'S, NOT THE GROUP'S. The kit measures the
       whole group when it frames the plan, and the group holds more than the
       house -- the plan came up on a frustum 150 m wide with a 4 m house in the
       middle of it (Marek: the pool is right, ours is tiny). The building's
       rectangle, with the terrace when there is one, is what the plan is
       about; it goes to the kit as the focus. */
    function _planFocus() {
      try {
        if (typeof __PROD._planFocus === 'function') return __PROD._planFocus();
        /* THE PLAN FRAMES THE BUILDING, NOTHING ELSE (core-v296). The terrace
           was taken into the frame as well, and with it a pergola or a pool
           standing off in the garden -- so the plan of the house came out small
           in the corner of the screen (Marek: prizpusobit jen pudorysu stavby).
           The frame is the building's own rectangle now; everything else is
           still drawn, it just does not decide the zoom. */
        var B = _buildRect(), x0 = B.x0, x1 = B.x1, z0 = B.z0, z1 = B.z1;
        return { cx: (x0 + x1) / 2, cz: (z0 + z1) / 2, cy: 1.2, spanX: (x1 - x0) * 1.35 + 2, spanZ: (z1 - z0) * 1.35 + 2 };
      } catch (e) { return null; }
    }
    /* THE MODEL WAITS FOR ITS PLAN (core-v545, Marek: ten tmavy obdelnik je
       strecha pri pohledu z vrchu -- blikne tam 3D model pred objevenim
       floorplanu). Entering a plan step swaps to the top camera at once, but
       the build that leaves the roof out runs with the step's own work, a
       frame or two later -- and in between the whole house, roof and eaves,
       was drawn from above. From the moment the camera swaps until a build
       has run and the plan is drawn, the model is not drawn at all; a safety
       timer shows it again if nothing comes. A top view with no plan to wait
       for (2D outside the plan steps) is shown at once. */
    var _planRb = 0, _veil = { on: false, rb: 0, t: 0 };
    function _planVeil(on, why) {
      var st = _stage();
      if (!st || !st.group) return;
      if (_veil.t) { global.clearTimeout(_veil.t); _veil.t = 0; }
      if (on) {
        _veil.on = true; _veil.rb = _planRb;
        st.group.visible = false;
        _veil.t = global.setTimeout(function () { _veil.t = 0; _planVeil(false, 'nothing drew the plan in 800 ms'); }, 800);
      } else if (_veil.on) {
        _veil.on = false;
        st.group.visible = true;
        _log('plan     model shown under the plan -- ' + (why || ''));
      }
    }
    function setView(v) {
      var st = _stage();
      var was = STATE.view;
      STATE.view = (v === '2d') ? '2d' : '3d';
      if (was !== '2d' && STATE.view === '2d') { try { _planVeil(true); } catch (eV1) {} }
      else if (STATE.view === '3d') { try { _planVeil(false, 'back in 3D'); } catch (eV2) {} }
      _controls.view(STATE.view, STATE.view === '2d' ? _planFocus() : undefined);
      if (STATE.view === '2d') { try { _planFitB = _planFitSig(); } catch (eFb) {} }   /* core-v706: what the plan was framed on */
      if (STATE.view === '2d') { try { _apply2DControls(); } catch (e2c) {} }
      if (STATE.view === '2d') { try { if (typeof _cutOff === 'function') _cutOff('the plan'); } catch (eCo) {} }   /* the product's section tool, if it has one */
      try { if (typeof _mapUnderTrees === 'function') _mapUnderTrees(); } catch (eMt) {}
      /* a mesh flagged hideInPlan (the fitout blocks) is drawn only in 3D:
         in the plan the asset draws the piece as a symbol and the block under
         it would show through as a coloured slab */
      try {
        if (st && st.group) {
          var plan = (STATE.view === '2d');
          st.group.traverse(function (o) { if (o.userData && o.userData.hideInPlan) o.visible = !plan; });
        }
      } catch (eHp) {}
      var r = $('cf-root');
      if (r) {
        r.classList.toggle('cf-view-2d', STATE.view === '2d');
        r.classList.toggle('cf-view-3d', STATE.view === '3d');
      }
      _syncMapView(true);
      /* switching to 2D is exactly when the drawing has to appear */
      try { drawPlan(); } catch (eV) {}
      savePref();
    }

    /* -------------------------------------------------------------------------
       THE MAP. Lifted from the tiny lib, which had already worked this out, and
       generalised only where it named that product.

       MapManager comes from configurator-ui.js and is loaded on every page by the
       router. It is NOT initialised until here, and it injects its own markup
       into cf-root -- the address modal, the mapbox canvas, the controls -- so
       nothing about the map has to be in the shared liquid.

       There is deliberately no #cf-btn-map in the rail. MapManager binds its
       modal opener to that id, so leaving the element out means nothing binds and
       the PLACE step owns the address instead, which is where it belongs.
       ------------------------------------------------------------------------- */
    var _MM = null;
    var _mapNote = 'not started';

    function _boot() { return global.HOUSE_BOOT || {}; }

    function _injectMapCSS() {
      if ($('cf-house-map-css')) return;
      var st = document.createElement('style');
      st.id = 'cf-house-map-css';
      st.appendChild(document.createTextNode([
        /* ABOVE THE DRAWER, AND OUT OF IT.

           9999 was never the problem: the backdrop is built inside the
           configurator's own root, and an ancestor there establishes a stacking
           context -- so the number was being compared against its siblings and
           never against the drawer at all. A dialog that opens behind the panel
           that opened it is worse than no dialog.

           It is moved to the body when it opens, which takes it out of every
           context but the page's, and then the number means what it says. */
        '.cf-map-modal-backdrop {',
        '  position: fixed; inset: 0; z-index: 100001;',
        '  background: rgba(0,0,0,0.45); display: none;',
        '  align-items: center; justify-content: center;',
        '}',
        '.cf-map-modal-backdrop.open { display: flex; }',
        '.cf-map-modal-sugg-item {',
        '  padding: 9px 12px; font: 12px/1.35 Montserrat, sans-serif; color: #333;',
        '  cursor: pointer; border-bottom: 1px solid #f0f0f0;',
        '}',
        '.cf-map-modal-sugg-item:last-child { border-bottom: none; }',
        '.cf-map-modal-sugg-item:hover { background: #f5f5f0; }'
      ].join('\n')));
      document.head.appendChild(st);
    }

    /* The SDK is fetched once per page and the promise is parked on the window,
       so two configurators on one page cannot start two downloads. */
    function _loadMapboxSDK() {
      if (global.mapboxgl) return Promise.resolve();
      if (global.__cfBaseMapSDKPromise) return global.__cfBaseMapSDKPromise;
      global.__cfBaseMapSDKPromise = new Promise(function (resolve, reject) {
        if (!$('cf-mapbox-gl-css')) {
          var css = document.createElement('link');
          css.id = 'cf-mapbox-gl-css';
          css.rel = 'stylesheet';
          css.href = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.css';
          document.head.appendChild(css);
        }
        var existing = $('cf-mapbox-gl-js');
        if (existing) {
          existing.addEventListener('load', function () { resolve(); });
          existing.addEventListener('error', function () { reject(new Error('Mapbox SDK load failed')); });
          return;
        }
        var s = document.createElement('script');
        s.id = 'cf-mapbox-gl-js';
        s.src = 'https://api.mapbox.com/mapbox-gl-js/v3.0.1/mapbox-gl.js';
        s.onload = function () { resolve(); };
        s.onerror = function () { reject(new Error('Mapbox SDK load failed')); };
        document.head.appendChild(s);
      });
      return global.__cfBaseMapSDKPromise;
    }

    /* THE GROUND HOLDS STILL WHEN IT IS ASKED TO. The canvas sits over Mapbox, so
       panning the map slides the background out from under whatever is being
       lined up on it. */
    function _applyMapLock() {
      if (!_MM || !_MM.map) return;
      STATE.mapLocked = false;
      var m = _MM.map, locked = false;
      var fns = ['dragPan', 'scrollZoom', 'boxZoom', 'dragRotate', 'doubleClickZoom', 'touchZoomRotate'];
      for (var i = 0; i < fns.length; i++) {
        try { if (locked) m[fns[i]].disable(); else m[fns[i]].enable(); } catch (e) {}
      }
      /* the rotate slider stays live whatever the padlock says -- it is a
         deliberate move, like the one-finger pan, and the lock only ever meant
         "no accidental drift" (Marek: rotation went dead with the lock on) */
      var rot = $('cf-map-rotate');
      if (rot) {
        rot.disabled = false;
        rot.style.opacity = '';
        rot.style.pointerEvents = '';
      }
    }

    /* The map only lines up with the scene because the camera behaves
       predictably, so every view change has to tell it. */
    function _syncMapView(animate) {
      try { _controls.syncMap(animate); }
      catch (e) { _log('map view sync: ' + (e && e.message ? e.message : e)); }
    }

    /* CLIMATE ZONE RIDES IN THE ADDRESS DIALOG, because it is the same question:
       where is this being built. Frost depth sets how deep the footings go, so it
       belongs to the envelope and therefore to this block rather than to any one
       product. Somebody with no address can still pick a band directly. */
    var FROST_ZONES = [
      { d: 0.0, i18n: 'base_frost_zone_subtropical', def: 'Subtropical' },
      { d: 0.6, i18n: 'base_frost_zone_warm',        def: 'Warm temperate' },
      { d: 0.8, i18n: 'base_frost_zone_temperate',   def: 'Temperate' },
      { d: 1.0, i18n: 'base_frost_zone_cool',        def: 'Cool temperate' },
      { d: 1.2, i18n: 'base_frost_zone_cold',        def: 'Cold / boreal' },
      { d: 1.5, i18n: 'base_frost_zone_arctic',      def: 'Arctic' }
    ];

    function _esc(s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;')
        .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    function _frostHTML() {
      var out = ['<div class="cf-map-modal-zone" style="margin:12px 0 4px;display:flex;flex-direction:column;gap:5px;">',
        '<label for="cf-map-frost-zone" style="font-size:12px;color:#555;">' +
        _esc(_t('base_frost_zone_label', 'Climate zone (or pick directly, no address):')) + '</label>',
        '<select id="cf-map-frost-zone" style="padding:8px;border:1px solid #ccc;border-radius:6px;font-size:14px;width:100%;background:#fff;">'];
      var i;
      for (i = 0; i < FROST_ZONES.length; i++) {
        out.push('<option value="' + FROST_ZONES[i].d + '">' +
                 _esc(_t(FROST_ZONES[i].i18n, FROST_ZONES[i].def)) +
                 (FROST_ZONES[i].d > 0 ? (' (' + FROST_ZONES[i].d.toFixed(1) + ' m)') : '') +
                 '</option>');
      }
      out.push('</select></div>');
      return out.join('');
    }

    function _wireFrostZone() {
      var sel = $('cf-map-frost-zone');
      if (!sel || sel.getAttribute('data-cf-bound')) return;
      sel.setAttribute('data-cf-bound', '1');
      sel.value = String(STATE.climateZone || 0);
      sel.addEventListener('change', function () {
        STATE.climateZone = parseFloat(sel.value) || 0;
        savePref();
        _log('climate zone -> ' + STATE.climateZone + ' m');
      });
    }

    function initMap() {
      var B = _boot();
      var token = B.mapboxToken || global.MAPBOX_TOKEN;
      if (!token) { _mapNote = 'NO TOKEN in HOUSE_BOOT -- map disabled'; _log('map: ' + _mapNote); return; }
      _injectMapCSS();
      /* core-v798 (perf thread): the SDK and the map wait for the first model
         on screen (_whenModelReady) -- they ran between the first build and
         its first frame */
      _mapNote = 'waiting for the first model';
      _whenModelReady(function () {
      _mapNote = 'loading SDK';
      _loadMapboxSDK().then(function () {
        _MM = global.MapManager;
        if (!_MM) { _mapNote = 'MapManager MISSING -- configurator-ui.js not loaded'; _log('map: ' + _mapNote); return; }
        var st = _stage();
        if (!st || !st.camera) {
          _mapNote = 'STAGE NOT READY -- initMap() ran before the stage was built';
          _log('map: ' + _mapNote);
          return;
        }
        _MM.init({
          token: token,
          storageKey: 'levaMap:p' + (B.productId || '0') + ':c' + (B.customerId || 'anon'),
          i18n: B.i18n || {},
          /* NO PADLOCK. It only ever blocked the one-finger pan, which the
             pool never blocked; the map moves by the finger and turns by the
             slider, and that is the whole of it (Marek). mapLocked stays in
             the state for old payloads and is always false. */
          lock: false,
          getLocked: function () { return false; },
          onLockChange: function () {},
          modalExtraHTML: _frostHTML(),
          onMarkupReady: function () { _wireFrostZone(); },
          engine: {
            camera:        st.camera,
            controls:      st.controls,
            orthoCamera:   st.orthoCam,
            orthoControls: st.orthoControls
          },
          getView: function () { return STATE.view === '2d' ? '2d-top' : '3d'; },
          viewOffset: function () {
            if (STATE.view !== '2d') return null;
            var s9 = _stage(), oc9 = s9 && s9.orthoControls;
            return (oc9 && oc9.target) ? { x: oc9.target.x, z: oc9.target.z } : null;
          },
          getRootSize: function () {
            var r = $('cf-root');
            return { w: r ? r.clientWidth : 0, h: r ? r.clientHeight : 0 };
          },
          getPadStep: function () { return 2; },
          onChange: function () { _savePrefSoon(); },
          requestRender: function () {
            var s2 = _stage();
            if (s2 && s2.renderer && s2.camera) s2.renderer.render(s2.scene, s2.camera);
          }
        });
        try { _MM.loadFromStorage(); } catch (e) {}
        _applyMapLock();
        _syncMapView(false);
        _mapNote = 'ready';
        _log('map ready');
      }).catch(function (err) {
        _mapNote = 'SDK FAILED: ' + (err && err.message ? err.message : err);
        _log('map: ' + _mapNote);
      });
      });
    }

    function mapOpen() {
      if (!_MM || !_MM._openModal) { _log('map: not ready (' + _mapNote + ')'); return false; }
      /* OUT OF THE CONFIGURATOR AND ONTO THE PAGE. Reparenting is what actually
         lifts it clear of the drawer; the z-index above only decides the order
         once it is somewhere the drawer can be compared with. Moving a live node
         keeps every listener already on it, so the geocoder and the suggestion
         list carry on working untouched. */
      try {
        var bd = $('cf-map-modal-backdrop');
        if (bd && bd.parentNode !== document.body) document.body.appendChild(bd);
      } catch (e) { _log('map modal: could not reparent -- ' + (e && e.message)); }
      _MM._openModal();
      return true;
    }
    function mapReady() { return !!(_MM && _MM.map); }
    function mapToggleLock() {
      STATE.mapLocked = false;
      _applyMapLock();
      return false;
    }

    /* -------------------------------------------------------------------------
       DRAGGING THE GROUND IN PLAN. One finger in 2D moves the MAP under the
       model, which is what placing a building on a lot means: the model stays in
       the middle of the drawing and the plot slides beneath it.

       The zoom that used to live here has gone into the shared navigation above,
       because it was doing the wrong thing in both views for the same reason --
       it recentred on every notch, so the edge could never be reached.
       ------------------------------------------------------------------------- */
    var _2dBound = false;

    /* THE 2D CAMERA: zoom only, about the centre, square to the ground. The
       kit switches the ortho controls OFF on every entry into 2D, so this runs
       after each switch as well as at boot. */
    function _apply2DControls() {
      /* THE KIT PINS THE ORTHO CONTROLS OFF (a non-configurable enabled=false),
         so no OrbitControls in 2D at all -- exactly the pool's arrangement: the
         pinch and the wheel are ours (_init2DZoom, copied from the pool), the
         one-finger drag of the map is ours (_init2D), and the camera just has
         to be square over its centre. */
      var st = _stage(), oc = st && st.orthoControls;
      if (!st || !st.orthoCam) return;
      try {
        var cam = st.orthoCam, tg = (oc && oc.target) ? oc.target : { x: 0, z: 0 };
        cam.position.set(tg.x, cam.position.y, tg.z + 0.0001);
        cam.up.set(0, 0, -1);
        cam.lookAt(tg.x, 0, tg.z);
        cam.updateProjectionMatrix();
      } catch (e) {}
    }
    /* PINCH AND WHEEL IN 2D, COPIED FROM THE POOL (_init2DZoom): the ortho
       camera's zoom about the centre, nothing else moves; the kit's map sync
       listens on the ortho controls' 'change', so one is dispatched by hand */
    var _zoom2DBound = false;
    /* the plan's own grab is let go by this when a second finger comes down
       (set by _initOpeningPick); the finger that switched the fitout mode on */
    var _planTakeover = null, _fitEditBy = null;
    function _init2DZoom() {
      if (_zoom2DBound) return;
      var st = _stage(), canvas = st && st.renderer && st.renderer.domElement;
      if (!canvas) { global.setTimeout(_init2DZoom, 100); return; }
      _zoom2DBound = true;
      /* AND THE PLACE STEP ZOOMS TOO.

         All three handlers began by refusing anything that was not the 2D view,
         and the step where you place the building on the photograph runs in 3D.
         So there was no zoom there at all -- not in, not out -- and nothing else
         was listening (Marek: ne, neda se zoom in a out).

         In 3D the camera is the perspective one and zooming it means moving it
         along its own sight line, between the limits the fit already set. In 2D
         nothing changes: the ortho camera's own zoom, about the centre. */
      /* ZOOM WHERE THE FINGERS ARE, AND TWO FINGERS MOVE THE VIEW (core-v538,
         Marek: neda se moc zoomovat a nehybeme pudorysem mimo stred zobrazeni).
         In 2D the zoom was about the middle of the screen and nothing moved
         the view, so a corner of the plan could be enlarged only off the edge.
         Now the point under the fingers (or the mouse) stays put while the
         plan grows or shrinks round it, and the two fingers carry the view
         with them. The camera stays square over the ground -- it is only slid,
         never turned, which is what the 3D navigation once got wrong here --
         and it may slide no further than the frame the fit chose, so the house
         cannot be lost off the screen. Zoomed back out to the fit, the view is
         centred again. One finger is still the map's, as before. */
      /* THE LIMITS ARE THE PLAN'S, NOT THE FRUSTUM'S (core-v564, Marek: zoom
         in dvema prsty, pisu ve vedlejsim okne, pak chci znovu zoom in -- plan
         se skokove zvetsi na velky zoom in a uz ho nejde zmensit; musim pres
         edit nebo 2D / 3D). Coming back from the other window is a resize: the
         stage rebuilds the camera's frustum from whatever it finds then, and
         the restore puts the remembered framing back as a zoom against that
         new frustum -- 0.07 against a frustum that came back a metre wide. The
         pinch clamped the zoom to 0.2 .. 12 of the frustum, so its first move
         jumped the zoom up to 0.2 -- a plan four times larger -- and 0.2 was
         as far out as it would then go; a zoom at or under 1 also pinned the
         view to the middle, so it would not slide either. 2D / 3D refits the
         frustum, which is why that got him out. The limits are now how much
         of the plan is on the screen: from a metre (or a fifteenth of the
         house) to four times the house and its terrace, whatever the frustum
         is; a limit only stops the zoom going further, it never moves it; and
         the view slides as long as some of the house stays in it, the house
         centred once it all fits. One move of the fingers zooms by a quarter
         at most. */
      function _view2DBox() {
        var B = _buildRect(), TR = null;
        try { TR = _terraceRect(); } catch (eT) { TR = null; }
        if (TR) B = { x0: Math.min(B.x0, TR.x0), x1: Math.max(B.x1, TR.x1), z0: Math.min(B.z0, TR.z0), z1: Math.max(B.z1, TR.z1) };
        return B;
      }
      function _view2DNavigationBox() {
        var B = _view2DBox(), N = null;
        try { if (typeof __PROD._planNavigationBounds === 'function') N = __PROD._planNavigationBounds(); } catch (eN) {}
        if (N && ['x0','x1','z0','z1'].every(function (k) { return Number.isFinite(N[k]); }) && N.x1 > N.x0 && N.z1 > N.z0) {
          B = { x0: Math.min(B.x0, N.x0), x1: Math.max(B.x1, N.x1), z0: Math.min(B.z0, N.z0), z1: Math.max(B.z1, N.z1) };
        }
        return B;
      }
      function _view2DLimits(cam) {
        var B = _view2DBox(), N = _view2DNavigationBox(), span = cam.right - cam.left, size = Math.max(B.x1 - B.x0, B.z1 - B.z0, 3);
        var navSize = Math.max(N.x1 - N.x0, N.z1 - N.z0, size);
        var visMin = Math.max(1.0, size / 15), visMax = Math.max(navSize * 4, 30);
        return { zLo: span / visMax, zHi: span / visMin, visMin: visMin, visMax: visMax };
      }
      function _view2DAt(cam, oc, tx, tz) {
        var z = cam.zoom || 1, vw = (cam.right - cam.left) / z / 2, vh = (cam.top - cam.bottom) / z / 2, B = _view2DNavigationBox(), m = 1.0;
        var bcx = (B.x0 + B.x1) / 2, bcz = (B.z0 + B.z1) / 2;
        var lx = (isFinite(vw) && vw > 0) ? Math.max(0, (B.x1 - B.x0) / 2 + m - vw) : 0, lz = (isFinite(vh) && vh > 0) ? Math.max(0, (B.z1 - B.z0) / 2 + m - vh) : 0;
        tx = Math.max(bcx - lx, Math.min(bcx + lx, tx)); tz = Math.max(bcz - lz, Math.min(bcz + lz, tz));
        if (oc && oc.target) oc.target.set(tx, 0, tz);
        cam.position.set(tx, cam.position.y, tz + 0.0001);
        cam.up.set(0, 0, -1);
        cam.lookAt(tx, 0, tz);
        cam.updateProjectionMatrix();
      }
      /* ONE PLAN PAINT A FRAME, NOT ONE A FINGER EVENT (core-v589, Marek: na
         zoom in / out uz pudorys reaguje jen s velkymi obtizemi). A pinch sends
         a touchmove for every few pixels -- faster than a tablet paints a big
         plan -- and each one repainted the whole plan, so the events queued
         behind the paints and the plan lagged further behind the fingers the
         longer they moved. The camera still follows every event at once; the
         plan is painted once on the next frame with wherever the camera is by
         then, and the framing is remembered with it. */
      var _v2dPaint = 0;
      function _view2DDone(s) {
        try { if (s.orthoControls && s.orthoControls.dispatchEvent) s.orthoControls.dispatchEvent({ type: 'change' }); } catch (e) {}
        _pfP.gest++;
        if (_v2dPaint) { _pfP.gestSkipped++; return; }
        _v2dPaint = (global.requestAnimationFrame || function (fn) { return global.setTimeout(fn, 16); })(function () {
          _v2dPaint = 0;
          try { drawPlan(); } catch (e2) {}
          /* what the hand left is the framing to keep (core-v546) */
          try { _planRemember(); } catch (e3) {}
        });
      }
      function zoom2DAt(factor, cx, cy) {
        var s = _stage(), cam = s && s.orthoCam, oc = s && s.orthoControls;
        if (!cam) return;
        try { _planUserMoved(); } catch (eU) {}
        var tg = (oc && oc.target) ? oc.target : { x: 0, z: 0 };
        /* core-v564: by the plan's limits, a quarter at most a move, never a jump */
        factor = Math.max(0.8, Math.min(1.25, factor));
        var z0 = cam.zoom || 1, z1 = z0 * factor, VL = _view2DLimits(cam);
        if (factor > 1 && z1 > VL.zHi) z1 = Math.max(z0, VL.zHi);
        if (factor < 1 && z1 < VL.zLo) z1 = Math.min(z0, VL.zLo);
        if (!(z1 > 0) || !isFinite(z1)) z1 = z0;
        var r = canvas.getBoundingClientRect(), u = 0, v = 0;
        if (cx != null && r.width && r.height) { u = (cx - r.left) / r.width - 0.5; v = (cy - r.top) / r.height - 0.5; }
        var W0 = (cam.right - cam.left) / z0, H0 = (cam.top - cam.bottom) / z0;
        var wx = tg.x + u * W0, wz = tg.z + v * H0;
        cam.zoom = z1;
        _view2DAt(cam, oc, wx - u * W0 * z0 / z1, wz - v * H0 * z0 / z1);
        _view2DDone(s);
      }
      function pan2DBy(dxPx, dyPx) {
        var s = _stage(), cam = s && s.orthoCam, oc = s && s.orthoControls;
        if (!cam || !oc || !oc.target) return;
        try { _planUserMoved(); } catch (eU) {}
        var r = canvas.getBoundingClientRect();
        if (!r.width || !r.height) return;
        var z = cam.zoom || 1, mx = (cam.right - cam.left) / z / r.width, mz = (cam.top - cam.bottom) / z / r.height;
        _view2DAt(cam, oc, oc.target.x - dxPx * mx, oc.target.z - dyPx * mz);
        _view2DDone(s);
      }
      function applyZoom(factor, cx, cy) {
        var s = _stage();
        if (STATE.view !== '2d') {
          var pc = s && s.persCam, pctl = s && s.persControls;
          if (!pc || !pctl || !pctl.target) return;
          var T3 = global.THREE;
          var off = new T3.Vector3().subVectors(pc.position, pctl.target);
          var len = off.length();
          if (!(len > 0)) return;
          var want = len / factor;                  /* pinch out = come closer */
          var lo = pctl.minDistance || 0.5, hi = pctl.maxDistance || (len * 8);
          want = Math.max(lo, Math.min(hi, want));
          off.setLength(want);
          pc.position.copy(pctl.target).add(off);
          pc.updateProjectionMatrix();
          try { pctl.update(); } catch (eU) {}
          return;
        }
        zoom2DAt(factor, cx, cy);
      }
      canvas.addEventListener('wheel', function (e) {
        e.preventDefault();
        applyZoom(e.deltaY > 0 ? (1 / 1.1) : 1.1, e.clientX, e.clientY);
      }, { passive: false });
      var pinch = { active: false, lastDist: 0, mx: 0, my: 0 };
      canvas.addEventListener('touchstart', function (e) {
        /* in 2D the pinch is the pointer gesture below (core-v540) */
        if (STATE.view === '2d') { pinch.active = false; return; }
        if (e.touches.length === 2) {
          pinch.active = true;
          pinch.lastDist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
          pinch.mx = (e.touches[0].clientX + e.touches[1].clientX) / 2; pinch.my = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        }
      }, { passive: true });
      canvas.addEventListener('touchmove', function (e) {
        if (STATE.view === '2d') { if (e.touches.length >= 2) e.preventDefault(); return; }
        if (!pinch.active || e.touches.length !== 2) return;
        e.preventDefault();
        var dist = Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
        var mx = (e.touches[0].clientX + e.touches[1].clientX) / 2, my = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        if (pinch.lastDist > 0) applyZoom(dist / pinch.lastDist, mx, my);
        /* and the middle of the two fingers carries the view (2D only) */
        if (STATE.view === '2d' && (mx !== pinch.mx || my !== pinch.my)) pan2DBy(mx - pinch.mx, my - pinch.my);
        pinch.lastDist = dist; pinch.mx = mx; pinch.my = my;
      }, { passive: false });
      function endPinch(e) { if (!e.touches || e.touches.length < 2) pinch.active = false; }
      canvas.addEventListener('touchend', endPinch);
      canvas.addEventListener('touchcancel', endPinch);
      /* THE TWO-FINGER GESTURE IN 2D (core-v540, Marek: nekdy kdyz se dotknu
         dvema prsty, skoci velky zoom in a uz se z nej nemuzu dostat). The
         pinch listened on the canvas for touches, and a finger that came down
         on anything lying over the plan -- a label, the plus, a handle -- was
         never seen starting: the pinch kept the distance of an earlier
         gesture and divided the next move by it, a jump to full zoom; and
         zoomed in, where nearly every touch lands on something, the view could
         not be got out again. Now the fingers are followed from the top of the
         page, before anything on the plan hears them, each by its own id, and
         the pair is measured afresh whenever a finger comes or goes. A finger
         of the pair is the view's until it is lifted -- the one left down after
         the other is lifted does not start moving the building. */
      var G2 = { pts: {}, spent: {}, on: false, busy: false, dist: 0, mx: 0, my: 0 };
      /* FINGERS WHOSE LIFTING WAS NEVER HEARD (core-v564). A finger the page
         did not see lifted -- the window lost its focus, the page was hidden
         -- stayed in the list: the next touch was not the first one, so the
         list was not cleared, and the two fingers measured were a ghost and a
         real one. Whatever is held is let go when the window loses its focus
         or the page is hidden; at every touch the list is held against the
         fingers the screen really has (the touch list), the oldest that are
         not there forgotten; and when the screen has no finger left, none is
         kept. */
      function g2Reset(why) {
        var n = Object.keys(G2.pts).length, was = G2.on;
        G2.pts = {}; G2.spent = {}; G2.on = false; G2.busy = false;
        if (was) G2.endAt = Date.now();
        if (n) _log('2d two fingers -- ' + n + ' finger(s) let go: ' + why + ' (core-v564)');
      }
      try {
        global.addEventListener('blur', function () { g2Reset('the window lost its focus'); });
        global.document.addEventListener('visibilitychange', function () { if (global.document.hidden) g2Reset('the page was hidden'); });
        global.addEventListener('pagehide', function () { g2Reset('the page was left'); });
        global.document.addEventListener('touchstart', function (e) {
          var live = (e.touches && e.touches.length) || 0, ids = Object.keys(G2.pts), k;
          if (ids.length <= live) return;
          ids.sort(function (a, b) { return (G2.pts[a].t || 0) - (G2.pts[b].t || 0); });
          for (k = 0; k < ids.length - live; k++) { delete G2.pts[ids[k]]; delete G2.spent[ids[k]]; }
          _log('2d two fingers -- ' + (ids.length - live) + ' finger(s) whose lifting was never heard forgotten, ' + live + ' on the screen (core-v564)');
          if (Object.keys(G2.pts).length >= 2) g2Base(); else { G2.on = false; G2.busy = false; }
        }, { capture: true, passive: true });
        var g2TouchEnd = function (e) { if ((!e.touches || !e.touches.length) && Object.keys(G2.pts).length) g2Reset('no finger is on the screen'); };
        global.document.addEventListener('touchend', g2TouchEnd, { capture: true, passive: true });
        global.document.addEventListener('touchcancel', g2TouchEnd, { capture: true, passive: true });
      } catch (eG2) {}
      function g2Inside(e) {
        var r = canvas.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) return false;
        var tg = e.target;
        try { if (tg && tg.closest && tg.closest('.cfg-ui-drawer, #cfg-ui-wiz, input, select, textarea, [data-cf-nopinch]')) return false; } catch (eC) {}
        return true;
      }
      function g2Pair() {
        var ids = Object.keys(G2.pts);
        return (ids.length >= 2) ? [G2.pts[ids[0]], G2.pts[ids[1]]] : null;
      }
      function g2Base() {
        var P = g2Pair(); if (!P) return;
        G2.dist = Math.hypot(P[0].x - P[1].x, P[0].y - P[1].y);
        G2.mx = (P[0].x + P[1].x) / 2; G2.my = (P[0].y + P[1].y) / 2;
      }
      global.document.addEventListener('pointerdown', function (e) {
        if (e.pointerType !== 'touch') return;
        /* the first finger of a fresh touch: whatever was left over from a
           finger whose lifting was never heard is forgotten */
        if (e.isPrimary) { G2.pts = {}; G2.spent = {}; G2.on = false; G2.busy = false; }
        if (STATE.view !== '2d' || !g2Inside(e)) return;
        G2.pts[e.pointerId] = { x: e.clientX, y: e.clientY, t: Date.now() };
        if (Object.keys(G2.pts).length < 2) return;
        /* the second finger: nothing on the plan hears it */
        e.stopPropagation();
        if (!G2.on) {
          var tk = null;
          try { tk = _planTakeover ? _planTakeover() : null; } catch (eT) { tk = { said: 'takeover threw: ' + (eT && eT.message), busy: false }; }
          G2.on = true; G2.busy = !!(tk && tk.busy);
          var cv9 = '';
          try { var cm9 = _stage() && _stage().orthoCam; if (cm9) cv9 = ' | zoom ' + (+cm9.zoom).toFixed(3) + ' of a frustum ' + (cm9.right - cm9.left).toFixed(2) + ' m, ' + ((cm9.right - cm9.left) / cm9.zoom).toFixed(1) + ' m across'; } catch (eC9) { cv9 = ''; }
          _log('2d two fingers -- ' + (G2.busy ? 'a drag is under way, the view is left alone' : 'zoom and slide') + cv9 + (tk ? (' | ' + tk.said) : ''));
        }
        var k; for (k in G2.pts) if (G2.pts.hasOwnProperty(k)) G2.spent[k] = true;
        g2Base();
      }, true);
      global.document.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'touch' || !G2.pts[e.pointerId]) return;
        G2.pts[e.pointerId].x = e.clientX; G2.pts[e.pointerId].y = e.clientY; G2.pts[e.pointerId].t = Date.now();
        if (!G2.spent[e.pointerId] || G2.busy) return;
        /* a finger of the pair: nothing on the plan hears it */
        e.stopPropagation();
        if (!G2.on || STATE.view !== '2d') return;
        var P = g2Pair(); if (!P) return;
        var dist = Math.hypot(P[0].x - P[1].x, P[0].y - P[1].y), mx = (P[0].x + P[1].x) / 2, my = (P[0].y + P[1].y) / 2;
        if (G2.dist > 8 && dist > 8) {
          var fz = dist / G2.dist;
          if (Math.abs(fz - 1) > 0.001) zoom2DAt(fz, mx, my);
        }
        if (mx !== G2.mx || my !== G2.my) pan2DBy(mx - G2.mx, my - G2.my);
        G2.dist = dist; G2.mx = mx; G2.my = my;
      }, true);
      function g2Up(e) {
        if (e.pointerType !== 'touch' || !G2.pts[e.pointerId]) return;
        delete G2.pts[e.pointerId];
        delete G2.spent[e.pointerId];
        if (e.pointerId === _fitEditBy) _fitEditBy = null;
        var n = Object.keys(G2.pts).length;
        if (n >= 2) { g2Base(); return; }
        if (G2.on && n === 0) {
          G2.on = false; G2.busy = false; G2.endAt = Date.now();
          try { var cm = _stage() && _stage().orthoCam; _log('2d two fingers off -- zoom ' + (cm ? (+cm.zoom).toFixed(2) + ', ' + ((cm.right - cm.left) / cm.zoom).toFixed(1) + ' m across' : '?')); } catch (eL) {}
          /* and where the plus stands once the fingers are off (core-v546) */
          try { if (_plusDiag) _log('fitout plus at rest: ' + _plusDiag); } catch (eP) {}
        }
      }
      global.document.addEventListener('pointerup', g2Up, true);
      global.document.addEventListener('pointercancel', g2Up, true);
      /* and a finger of the pair lifted over a button does not press it */
      global.document.addEventListener('click', function (e) {
        if (!G2.on && (!G2.endAt || Date.now() - G2.endAt > 400)) return;
        var r = canvas.getBoundingClientRect();
        if (e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom) return;
        e.stopPropagation(); e.preventDefault();
      }, true);
      _log('2d zoom bound -- wheel about the cursor, two fingers zoom and slide, taken from the plan whatever the first one touched (core-v540); limits by the plan, fingers checked against the screen (core-v564)');
    }
    var _pan2D = { downs: 0, taken: 0, moves: 0, why: 'never asked' };
    /* the kit re-enters 2D on a resize (and turns the controls off again) */
    try { global.addEventListener('resize', function () { if (STATE.view === '2d') global.setTimeout(function () { try { _apply2DControls(); } catch (e) {} }, 120); }); } catch (eRz) {}
    /* BACK ON THE PAGE (core-v574, Marek: kdyz jsem presel do vedlejsiho
       okna, tak se na 3D modelu domku objevil pudorys; casto byva problem,
       kdyz utecu do vedlejsiho okna). A phone stops drawing a page it does not
       show, and what happened meanwhile -- a plan written, the view switched
       -- is put on the canvas only when it comes back. On the way back the
       view is set again as the project has it: the stage's camera with it,
       the plan's drawing shown in 2D and taken off in 3D, the model unveiled
       in 3D. The line says what was found and what was set, so a snapshot
       shows it the next time. */
    var _tab = { away: 0, t: 0 };
    function _tabOvOn() {
      try { var F = _FL(), OV = (F && F.plan && F.plan.overlay) ? F.plan.overlay : null; return !!(OV && OV.el && OV.el.style.display !== 'none'); } catch (e) { return false; }
    }
    function _tabBack(why) {
      if (_tab.t) { global.clearTimeout(_tab.t); _tab.t = 0; }
      _tab.t = global.setTimeout(function () {
        _tab.t = 0;
        try {
          var st = _stage(), cam = (st && st.camera) ? (st.camera.isOrthographicCamera ? 'top' : '3D') : 'none', ov0 = _tabOvOn(), fix = [];
          var away = _tab.away ? (((Date.now() - _tab.away) / 1000).toFixed(0) + ' s') : 'a while';
          if (st && typeof st.setView === 'function' && ((STATE.view === '2d') !== (cam === 'top'))) {
            st.setView(STATE.view, STATE.view === '2d' ? _planFocus() : undefined);
            if (STATE.view === '2d') { try { _apply2DControls(); } catch (e2c) {} }
            fix.push('the stage camera was ' + cam + ', set to ' + (STATE.view === '2d' ? 'the top view' : '3D'));
          }
          if (STATE.view !== '2d' && _veil.on) { _planVeil(false, 'back on the page'); fix.push('the model unveiled'); }
          drawPlan();
          var ov1 = _tabOvOn();
          if (ov1 !== ov0) fix.push('the plan drawing ' + (ov1 ? 'shown' : 'taken off'));
          var tl = 'back on the page after ' + away + ' (' + why + ') -- view ' + STATE.view + ', camera ' + cam + ', mode ' + STATE.mode + ', the plan drawing ' + (ov0 ? 'on' : 'off') +
                   ', the bar ' + (_barPlan ? 'holds the plan' : 'free') + ' -- ' + (fix.length ? fix.join('; ') : 'all as it was') + ' (core-v574)';
          _log('tab      ' + tl);
          /* and into the floorplan's COPY box, where it is read (floorplan v158) */
          try { var FT = _FL(); if (FT && FT.plan && FT.plan.importer && typeof FT.plan.importer.tabSay === 'function') FT.plan.importer.tabSay(tl); } catch (eTs) {}
        } catch (eTb) { _log('tab      back on the page THREW ' + ((eTb && eTb.message) || eTb)); }
      }, 350);
    }
    try {
      global.document.addEventListener('visibilitychange', function () {
        if (global.document.hidden) {
          _tab.away = Date.now();
          _log('tab      the page was left -- view ' + STATE.view + ', mode ' + STATE.mode + ', the plan drawing ' + (_tabOvOn() ? 'on' : 'off') + ' (core-v574)');
        } else _tabBack('the page shown again');
      });
      global.addEventListener('pageshow', function (e) { if (e && e.persisted) _tabBack('the page came back from the browser cache'); });
    } catch (eTab) {}
    function _init2D() {
      if (_2dBound) return;
      var canvas = _canvasEl();
      if (!canvas) { global.setTimeout(_init2D, 120); return; }
      _2dBound = true;

      var pointers = 0;
      canvas.addEventListener('pointerup', function () { pointers = Math.max(0, pointers - 1); }, true);
      canvas.addEventListener('pointercancel', function () { pointers = Math.max(0, pointers - 1); }, true);

      _canPan2D = canPan;
      function canPan() {
        /* BACK IN SERVICE (v1027). The kit keeps the 2D camera still and its
           controls off, so the finger cannot pan the camera; what one finger
           moves in 2D is the MAP under the building, exactly as the pool's
           dragPan does and as this did before v1025. Every refusal is written
           down for the snapshot. */
        if (STATE.view !== '2d') { _pan2D.why = 'not 2d'; return false; }
        if (!_MM || !_MM.map) { _pan2D.why = 'no map'; return false; }
        /* THE PADLOCK DOES NOT STOP THIS. The pool's one-finger pan ignores the
           lock (it governs the map's own handlers and the rotate slider), and
           a locked map is what most saved projects carry -- so the plan's
           finger was dead for the wrong reason (Marek). */
        var d = $('cf-mapbox');
        if (!d || !d.classList.contains('cf-map-visible')) { _pan2D.why = 'map div not visible'; return false; }
        /* NOTHING HERE BOWS OUT ANY MORE.

           First this refused the whole FLOORPLAN step; then I narrowed it to the
           module's stage being past 0 -- and the module sits at stage 1 from the
           moment it initialises, so that was the same refusal wearing a
           different test. Twice I "fixed" it and twice the map stayed frozen on
           the one step where you place the building on the photograph.

           The module binds its own pointer handlers. If a tap belongs to the
           plan, it takes it; whatever reaches here is the map's. There is nothing
           for this function to decide. */
        /* AND THAT STOPPED BEING TRUE THE MOMENT THE MODULE WENT.

           It was the module that took a tap on the plan before this ever saw it,
           with its own handlers and its own capture. The module is unwired now
           and the plan is read HERE, in this file, from a listener on this same
           canvas -- and two listeners on one element both run. So one finger was
           starting a partition drag AND panning the map underneath it at the
           same time. The partition went one way, the ground went the other, and
           from the outside that is a partition that will not drag.

           On the FLOORPLAN step the plan owns the finger. The photograph is a
           backdrop there; it is placed on the PLACE step, which is what that
           gesture is for. This is tiny's rule as well -- while the plan is what
           is being edited, nothing else takes the pointer.

           BUT THE PLAN IS DRAWN IN EVERY 2D VIEW NOW, not only on that step, and
           refusing whenever it is drawn froze the map on the Place step and with
           the drawer shut (Marek: one finger used to pan the map). The finger
           is the plan's only while the plan is being EDITED -- the layout step,
           or the fitouts in hand. */
        /* the pool pans everywhere in 2D; here only a piece in hand or the door
           tool keep the finger -- a partition or an opening claim theirs before
           this runs */
        if (_fitEdit || STATE.doorTool) { _pan2D.why = _fitEdit ? 'a piece in hand' : 'door tool'; return false; }
        _pan2D.why = 'ok';
        return true;
      }

      /* THE ONE-FINGER DRAG OF THE MAP, AS THE POOL HAS IT (_init2DMapPan):
         pointerdown takes the finger, pointermove pans the map by the pixels
         moved, pointerup lets go. A second finger ends it (the pinch takes
         over). What the plan wants for itself it takes before this sees the
         event, with stopImmediatePropagation. */
      var drag = { active: false, x: 0, y: 0, id: null };
      canvas.addEventListener('pointerdown', function (e) {
        _pan2D.downs++;
        if (!canPan()) return;
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        _pan2D.taken++;
        if (drag.active) { drag.active = false; return; }   /* a second finger: the pinch's */
        drag.active = true;
        drag.x = e.clientX; drag.y = e.clientY; drag.id = e.pointerId;
        try { canvas.setPointerCapture(e.pointerId); } catch (x) {}
      });
      canvas.addEventListener('pointermove', function (e) {
        if (!drag.active || e.pointerId !== drag.id) return;
        if (!canPan()) { drag.active = false; return; }
        var dx = e.clientX - drag.x, dy = e.clientY - drag.y;
        drag.x = e.clientX; drag.y = e.clientY;
        if (dx !== 0 || dy !== 0) {
          _pan2D.moves++;
          try { _MM.map.panBy([-dx, -dy], { animate: false }); } catch (x) { _pan2D.why = 'panBy threw ' + (x && x.message); }
        }
      });
      function endDrag(e) {
        if (!drag.active) return;
        if (e && e.pointerId !== drag.id) return;
        drag.active = false;
        try { if (e) canvas.releasePointerCapture(e.pointerId); } catch (x) {}
        savePref();
      }
      canvas.addEventListener('pointerup', endDrag);
      canvas.addEventListener('pointercancel', endDrag);
      canvas.addEventListener('pointerleave', endDrag);
      canvas.addEventListener('touchstart', function (e) { if (e.touches && e.touches.length >= 2) drag.active = false; }, { passive: true });

      _log('2D map drag bound');
    }

    /* -------------------------------------------------------------------------
       FOOTPRINT HANDLES
       ------------------------------------------------------------------------- */
    var _handles = null;

    /* THE BUILDING'S RECTANGLE, WHICH IS NOT THE SAME THING AS THE HANDLE LIST.

       _rects() is what the finger can grab. _buildRect() is what the building
       IS. They were one function because for a long time there was only ever one
       rect to grab -- and the moment a second one appeared, everything that had
       been reading _rects()[0] to mean "the building" started reading the roof
       instead. The floorplan decomposed the roof rectangle into rooms: one room,
       8.1 x 8.1, and the vinyl was laid over the eaves.

       A UI list is not a source of geometry. Anything that wants the building
       asks for the building. */
    /* THE BAR, NOT THE BOX (core-v325). _buildRect() is the box round the whole
       outline, wing included, and everything that shaped the T read it as if it
       were the bar: the wing was put a wing's length past where the bar ends,
       the bar's roof was laid over the whole box, and the slab was 170 m2 for a
       122 m2 house. The bar is the footprint rectangle itself, and everything
       that means the bar reads this instead. */
    function _barRect() {
      var hw = STATE.footprint.w / 2, hl = STATE.footprint.l / 2, ox = STATE.origin.x, oz = STATE.origin.z;
      return { x0: ox - hw, x1: ox + hw, z0: oz - hl, z1: oz + hl };
    }
    function _buildRect() {
      var P = poly(), x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9, i;
      for (i = 0; i < P.length; i++) {
        x0 = Math.min(x0, P[i][0]); x1 = Math.max(x1, P[i][0]);
        z0 = Math.min(z0, P[i][1]); z1 = Math.max(z1, P[i][1]);
      }
      return { x0: x0, x1: x1, z0: z0, z1: z1 };
    }

    /* WHERE THE WING STANDS, AS A RECTANGLE (core-v309). poly() knows the shape
       the wing makes; the handles need it as a rect they can put grips on, and
       _applyRect needs the same arithmetic backwards. Both are here, so the two
       directions cannot drift apart. */
    function _wingRect() {
      var WG = STATE.wing;
      if (!WG || !WG.on) return null;
      var B = _barRect();
      var along = (WG.side === 'x0' || WG.side === 'x1') ? (B.z1 - B.z0) : (B.x1 - B.x0);
      var w = Math.min(WG.w, along - 0.6), l = WG.l;
      var c = (WG.side === 'x0' || WG.side === 'x1') ? ((B.z0 + B.z1) / 2) : ((B.x0 + B.x1) / 2);
      var mid = c + (WG.at || 0);
      var lo = (WG.side === 'x0' || WG.side === 'x1') ? B.z0 : B.x0;
      var hi = (WG.side === 'x0' || WG.side === 'x1') ? B.z1 : B.x1;
      mid = Math.max(lo + w / 2 + 0.3, Math.min(hi - w / 2 - 0.3, mid));
      if (WG.flush === 'lo') mid = lo + w / 2;        /* core-v753: an L's wing, flush with the bar's end */
      else if (WG.flush === 'hi') mid = hi - w / 2;
      if (WG.side === 'z1') return { x0: mid - w / 2, x1: mid + w / 2, z0: B.z1, z1: B.z1 + l };
      if (WG.side === 'z0') return { x0: mid - w / 2, x1: mid + w / 2, z0: B.z0 - l, z1: B.z0 };
      if (WG.side === 'x1') return { x0: B.x1, x1: B.x1 + l, z0: mid - w / 2, z1: mid + w / 2 };
      return { x0: B.x0 - l, x1: B.x0, z0: mid - w / 2, z1: mid + w / 2 };
    }

    /* THE DRAG POINTS SAY WHAT THEY DID (core-v508, Marek: neni videt zadny
       ghost footprint s drag pointy). A refresh that threw was swallowed
       without a word; it is kept now, with what the last refresh drew, for
       the snapshot's handles line. */
    var _hNote = { n: 0, err: '', rects: '', active: '?' };
    function _hRefresh() {
      if (!_handles) { _hNote.err = 'no handles object (ConfiguratorUI.handles missing?)'; return; }
      _hNote.n++;
      try { _handles.refresh(); _hNote.err = ''; } catch (eR0) { _hNote.err = (eR0 && eR0.message) || String(eR0); _log('handles  refresh THREW: ' + _hNote.err); }
      try {
        var act = false;
        try { act = !!(_handles._active && _handles._active()); } catch (eA) { act = 'threw ' + (eA && eA.message); }
        _hNote.active = act;
        if (act === true) {
          var rs = _rects() || [];
          /* the grip count worked out here: _grips lives in the UI kit, out of
             reach, and asking it threw and took the refresh with it (v508) */
          _hNote.rects = rs.map(function (r) { var ng = (r.noGrip || []).length, nG = r.edgeOnly ? (4 - ng) : (8 - ng); return r.key + (r.noGrip ? (' noGrip ' + r.noGrip.join('/')) : '') + (r.edgeOnly ? ' edges' : '') + ' ' + nG + ' grip(s) ' + Math.round((r.x1 - r.x0) * 1000) + 'x' + Math.round((r.z1 - r.z0) * 1000); }).join(', ') || 'none';
          if (!rs.length && editing('roof')) _hNote.rects = 'none -- roof type ' + STATE.roof.type + (STATE.roof.parapet ? ' WITH A PARAPET (no overhang to drag)' : '');
        } else _hNote.rects = '-';
        try { _hNote.kids = _handles._grp ? _handles._grp.children.length : -1; } catch (eK) {}
      } catch (eR) { _hNote.err += (_hNote.err ? ' | ' : '') + 'note: ' + ((eR && eR.message) || String(eR)); }
    }
    var _ghostSaid = '';
    /* THE GHOST A LITTLE OUTSIDE WHAT IT DRAGS (core-v599, Marek: vyraznejsi
       rohove drag pointy / footprint u terasy, bazenu, pergoly -- mirny
       offset, ekvidistanta, at se neprekryvaji s hranami objektu, ve stylu
       roof footprint). The terrace's, the pool's and the pergola's outlines
       and grips lay on the deck's, the coping's and the posts' own edges and
       were lost in them; like the roof's, which stands out by the overhang,
       they now stand 150 mm out (the pool's past its coping). A drag is
       taken back by the same amount in _applyRect, so the sizes and places
       are the objects' own, as before. 150 is three snap steps of 50. */
    var GHOST_OFF = 0.15;
    function _ghostOff(key) {
      if (key === 'pool') return POOL.cope + GHOST_OFF;
      return (key === 'terrace' || key === 'pergola') ? GHOST_OFF : 0;
    }
    function _ghostGrow(key, R) {
      var g = _ghostOff(key);
      if (g) { R.x0 -= g; R.x1 += g; R.z0 -= g; R.z1 += g; }
      return R;
    }
    function _ghostBack(key, r) {
      var g = _ghostOff(key);
      if (!g || !r) return r;
      return { x0: (r.x0 != null) ? r.x0 + g : r.x0, x1: (r.x1 != null) ? r.x1 - g : r.x1,
               z0: (r.z0 != null) ? r.z0 + g : r.z0, z1: (r.z1 != null) ? r.z1 - g : r.z1 };
    }
    function _rects() {
      var B0 = _barRect(), x0 = B0.x0, x1 = B0.x1, z0 = B0.z0, z1 = B0.z1;
      /* THE DRAG STEPS IN THE SAME UNIT THE PLAN SNAPS TO.

         The rect was dragged in half-bays while the plan snapped to whole ones,
         so every second step of the pointer produced no change at all -- the
         building held still, and so did the number, for half of every drag. It
         reads as a frozen dimension because for half the time it IS one.

         One step, one change. */
      var build = { key: 'house', x0: x0, x1: x1, z0: z0, z1: z1,
                    snap: GRID() / 2, movable: false, style: 'primary' };
      /* core-v580: where the plan draws the wall past the outline (planOut),
         the building's frame and its grips go to that outer face -- on the
         outline they sat under the drawn wall, out of sight and out of reach;
         a drag is taken back to the outline in _applyRect */
      var pOb = _planOut() + (+__PROD._handleOffset || 0);
      if (pOb) { build.x0 -= pOb; build.x1 += pOb; build.z0 -= pOb; build.z1 += pOb; }

      /* AND ON THE ROOF STEP, A SECOND RECTANGLE: THE ROOF ITSELF.

         Same eight grips, but each one drags an OVERHANG rather than a
         dimension -- so the roof can be pulled out over one side and left tight
         on another, which is the whole point of posts. Copied from the shed,
         including the reason its rect is not concentric with the building once
         the four sides differ.

         A parapet has nothing to drag: the covering turns up against the wall
         and there is no overhang to speak of. Gable and mono get the rect. */
      /* THE ROOF'S GHOST ONLY WHILE ITS SHAPE IS EDITED (core-v597, Marek:
         footprint strechy muze byt videt jen pri editaci tvaru strechy). On the
         icon strip the roof step also carries the covering and the base, and
         stays the step while the strip is put away: the ghost and its grips
         come with the roof icon (or the roof's own panel) only. The drawer's
         Roof & Base step keeps them as before. */
      var roofShape = true;
      try { if (_stripMode()) roofShape = (_SX.sel === 'roof' || _SX.sel === 'more:roof'); } catch (eRS) { roofShape = true; }
      if (editing('roof') && !roofShape) {
        var ghN3 = 'roof     no ghost -- the strip has ' + (_SX.sel || 'nothing') + ' picked, not the roof\'s shape (core-v597)';
        if (ghN3 !== _ghostSaid) { _ghostSaid = ghN3; _log(ghN3); }
        return [];
      }
      if (editing('roof') && STATE.roof.type !== 'flat' && !STATE.roof.parapet) {
        var O = _roofOver();
        /* the building stays FIRST in the list. Order should not carry meaning,
           and after today it does not -- but leaving it first costs nothing and
           anything still reading [0] gets the building rather than the roof. */
        /* ON THE ROOF STEP, ONLY THE ROOF IS GRABBABLE. Both rects on screen at
           once means the building can be resized from a 3D view by accident --
           the very thing turning the handles off was protecting against. The
           building is drawn as a plain outline for reference and has no grips
           (its own step still has them). */
        var outR = [{ key: 'roofover', style: 'secondary', snap: 0.05, movable: false,
                      x0: x0 - O.x0, x1: x1 + O.x1, z0: z0 - O.z0, z1: z1 + O.z1 }];
        /* ONLY THE GABLE ENDS DRAG (core-v507, Marek: kryta terasa jen smerem
           kde je stit): a product whose roof comes down to the ground on its
           eaves -- the A-frame -- says __PROD._eaveFixed; the eave sides and
           the corners lose their grips and only the two rake edges pull out */
        var eaveFx = false;
        try { eaveFx = !!(__PROD && __PROD._eaveFixed); } catch (eEF) { eaveFx = false; }
        if (eaveFx) {
          var rkF = _rakeKeys(_barAxisRead()), allK = ['x0', 'x1', 'z0', 'z1'];
          outR[0].noGrip = allK.filter(function (k) { return rkF.indexOf(k) < 0; });
          outR[0].edgeOnly = true;
        }
        var ghNote = 'roof     ghost on Roof & Base: reach x0 ' + Math.round(O.x0 * 1000) + ' x1 ' + Math.round(O.x1 * 1000) + ' z0 ' + Math.round(O.z0 * 1000) + ' z1 ' + Math.round(O.z1 * 1000) +
                     (eaveFx ? ('; grips on the gable ends ' + _rakeKeys(_barAxisRead()).join('/') + ' only, the eaves fixed') : '; eight grips');
        if (ghNote !== _ghostSaid) { _ghostSaid = ghNote; _log(ghNote); }
        /* the bar's grip on the wing's side goes into the longer free stretch
           beside the wing, so it is not under the wing's own grip (core-v448) */
        /* THE BAR'S EAVE ON THE WING'S SIDE IS NOT DRAGGED (core-v453, Marek:
           konstrukce je moc komplikovana, strecha na eave strane main runu na
           vnitrni strane nebude moci byt prodlouzena). That side has no grip
           and its reach stays the eave; the wing's own sides and the bar's
           other three are dragged as before. */
        try {
          if (STATE.wing && STATE.wing.on) {
            outR[0].noGrip = [STATE.wing.side];
            var Oi = _roofOver(), eI = _tailsEave();
            if (__PROD && __PROD._roofTails && Oi[STATE.wing.side] !== eI) { Oi[STATE.wing.side] = eI; delete Oi[STATE.wing.side + 'b']; }
          }
        } catch (eGA) {}
        /* THE WING'S ROOF HAS A GHOST OF ITS OWN (core-v427, Marek: ma to byt
           overlay ghost footprint s drag pointy, mame to tak vsude): the same
           eight grips round the wing, each dragging that side's reach; the
           side on the bar has no reach to drag and is put back by the apply. */
        if (STATE.wing && STATE.wing.on) {
          var WRr = _wingRect();
          if (WRr) {
            var WO = STATE.wing.over || { x0: STATE.roof.eave, x1: STATE.roof.eave, z0: STATE.roof.eave, z1: STATE.roof.eave };
            outR.push({ key: 'wingover', style: 'secondary', snap: 0.05, movable: false, noGrip: [({ x0: 'x1', x1: 'x0', z0: 'z1', z1: 'z0' })[STATE.wing.side]],
                        x0: WRr.x0 - (WO.x0 || 0), x1: WRr.x1 + (WO.x1 || 0), z0: WRr.z0 - (WO.z0 || 0), z1: WRr.z1 + (WO.z1 || 0) });
          }
        }
        /* A PRODUCT'S OWN RECTANGLES (core-v523, A-frame: the side wings'
           ghosts with drag points on Roof & Base). __PROD._rects(step) hands
           rects in the same shape, their keys starting 'p:'; a drag goes back
           through __PROD._rectApply and their koty through __PROD._rectDims */
        return outR.concat(_prodRects('roof'));
      }
      if (editing('roof')) {
        var ghN2 = 'roof     no ghost on Roof & Base: roof type ' + STATE.roof.type + (STATE.roof.parapet ? ', parapet' : '') + ' has no overhang to drag';
        if (ghN2 !== _ghostSaid) { _ghostSaid = ghN2; _log(ghN2); }
        return [];      /* a parapet has no overhang to drag */
      }
      /* ON THE FLOORPLAN STEP, THE PRODUCT'S OWN RECTANGLES BESIDE THE HOUSE
         (core-v530, Marek: kridlo -- jeho pudorys -- upravovat ve floorplanu).
         When the product hands rects for 'layout' (the A-frame's side wings, on
         the ground floor's plan) they are grabbable there together with the
         house; their drags go through __PROD._rectApply as on any step. */
      if (editing('layout')) {
        var pLy = _prodRects('layout');
        if (pLy.length) {
          /* ONLY THE PRODUCT'S OWN (core-v596, barndo loft, Marek: na druhem
             podlazi tahat jen hrany galerie): a product rect marked solo is
             the only thing grabbable on the plan -- the house is not resized
             from it */
          var soloL = pLy.some(function (q) { return !!q.solo; });
          var ghL = 'rects    Floorplan: ' + (soloL ? 'only' : 'the house and') + ' the product\'s own ' + pLy.map(function (q) { return q.key; }).join(', ');
          if (ghL !== _ghostSaid) { _ghostSaid = ghL; _log(ghL); }
          return soloL ? pLy : [build].concat(pLy);
        }
      }
      /* AND THE WING IS A RECTANGLE OF ITS OWN, on the step where the plan is
         edited. Its size was fixed in the product, which made the one thing
         that distinguishes a T from a box the one thing that could not be
         changed. Three of its edges drag -- the outer one is its length, the
         two sides are its width, and sliding it along the wall moves it; the
         edge ON the building is held there by the apply. */
      if (editing('layout') && STATE.wing && STATE.wing.on) {
        var WR = _wingRect();
        if (WR) return [build, { key: 'wing', style: 'secondary', snap: GRID() / 2,
                                 movable: false, x0: WR.x0, x1: WR.x1, z0: WR.z0, z1: WR.z1 }];
      }
      /* ON THE STRUCTURE STEP, A PRODUCT'S OWN RECTANGLES ONLY (core-v526,
         Marek: ghost pro posouvani a zmenu rozmeru kridel ve STRUCTURE, v Roof
         & Base zustane natahovani strechy). When the product hands rects for
         'structure' (the A-frame's side wings) they are the only grabbable
         things there; the building is not resized from this step. */
      if (editing('structure')) {
        var pS = _prodRects('structure');
        if (pS.length) {
          var ghS = 'rects    Structure: the product\'s own ' + pS.map(function (q) { return q.key; }).join(', ');
          if (ghS !== _ghostSaid) { _ghostSaid = ghS; _log(ghS); }
          return pS;
        }
      }
      /* ON THE EXTERIOR STEP, THE TERRACE: a second rectangle glued to one side
         of the house; its far edge and its two ends drag, the side on the house
         cannot (the apply keeps it there). Nothing else on that step is
         grabbable, the house stays a plain outline. */
      if (editing('exterior')) {
        var TR = _terraceRect(), outE = [];
        /* each ghost stands _ghostOff out from its own edges (core-v599) */
        /* a paved terrace drags by whole tiles */
        if (TR) outE.push(_ghostGrow('terrace', { key: 'terrace', style: 'secondary', snap: (_terrace().surface === 'pavers') ? _paverSize() : 0.05, movable: true,
                            x0: TR.x0, x1: TR.x1, z0: TR.z0, z1: TR.z1 }));
        /* the pool: free on the site, moved and sized like the terrace */
        var PR = _poolRect();
        if (PR) outE.push(_ghostGrow('pool', { key: 'pool', style: 'secondary', snap: 0.05, movable: true,
                            x0: PR.x0, x1: PR.x1, z0: PR.z0, z1: PR.z1 }));
        /* core-v597: the pergola, moved and sized like the pool */
        var PG = _pgFree() ? _pergolaRect() : null;
        if (PG) outE.push(_ghostGrow('pergola', { key: 'pergola', style: 'secondary', snap: 0.05, movable: true,
                            x0: PG.x0, x1: PG.x1, z0: PG.z0, z1: PG.z1 }));
        /* core-v646 (THOW: the porch's depth by its ghost on Exterior): the
           product's own rects for this step, as on Floorplan and Roof & Base */
        return outE.concat(_prodRects('exterior'));
      }
      return [build];
    }

    /* ---------- THE TERRACE, ported from the shed (terrace attached to a side)
       STATE.product.terrace = { on, side: 'x0'|'x1'|'z0'|'z1', w: along the
       wall, d: out from it, off: along the wall from the house's low corner,
       surface: 'deck'|'pavers' }. Its rect in world, from the house's outline. */
    function _terrace() {
      var P = STATE.product = STATE.product || {};
      if (!P.terrace) P.terrace = { on: false, side: 'z1', w: 4.0, d: 3.0, off: 0, surface: 'deck', pergola: 'none' };
      if (!P.terrace.pergola || P.terrace.pergola === 'wood_dense') P.terrace.pergola = P.terrace.pergola === 'wood_dense' ? 'wood' : 'none';
      return P.terrace;
    }
    /* THE TILE IS THE UNIT: 500 in metric, 20 in (508) in imperial, and a
       paved terrace is always whole tiles -- its size snaps to the tile when
       the surface, the units or a drag change it */
    function _paverSize() { return STATE.isMetric ? 0.500 : 0.508; }
    function _terraceSnapToTiles() {
      var T = _terrace(); if (!T.on || T.surface !== 'pavers') return;
      _terraceRect();
      var t = _paverSize();
      T.sx = Math.round(Math.max(2 * t, Math.round(T.sx / t) * t) * 1000) / 1000;
      T.sz = Math.round(Math.max(2 * t, Math.round(T.sz / t) * t) * 1000) / 1000;
    }
    /* UNDER A SHELTERING ROOF THERE IS NO PERGOLA: a roof pulled out over the
       terrace's side by a metre or more (the porch kind, the one that gets its
       own posts) is the shelter, and a pergola would stand into it; the plain
       600 eave is not that */
    /* THE PERGOLA ON ITS OWN (core-v597, Marek: pergola ma byt on / off jako
       bazen, stejna funkce, presun prstenem). On, it stands over the terrace
       as before -- until it is moved or sized: from then it keeps a rectangle
       of its own (T.pg), anywhere on the site, with the same gold ghost, grips
       and move as the pool on the exterior step. With no terrace, or with the
       terrace under the roof's reach, it comes up beside the house. The kind
       (wooden slats / fabric) is still T.pergola. */
    var PERG_LIM = { min: 1.5, max: 8.0, def: 3.0 };
    /* a place of its own: set by the first move, by a switch-on with no
       terrace to stand over, or kept when the terrace under it goes */
    function _pgOwn(T) { if (!_pgFree()) return false; var G = T && T.pg; return !!(G && G.x0 != null && G.z0 != null && G.sx > 0 && G.sz > 0); }
    /* a product whose lib draws a pergola anywhere (__PROD._pergolaFree, ADU);
       the others keep it over the terrace only, as before core-v597 */
    function _pgFree() { try { return !!(__PROD && __PROD._pergolaFree); } catch (eF) { return false; } }
    /* ON: a kind picked AND something to stand on -- the terrace or a place of
       its own. A project saved with the terrace off and a kind left over
       (before core-v597 that drew nothing) stays without one. */
    function pergolaOn() { var T = _terrace(), k = T.pergola; return !!(k && k !== 'none' && (T.on || _pgOwn(T))); }
    function _pergolaRect() {
      if (!pergolaOn()) return null;
      var T = _terrace(), G = T.pg;
      if (_pgOwn(T)) return { x0: G.x0, x1: G.x0 + G.sx, z0: G.z0, z1: G.z0 + G.sz, own: true };
      var TR = _terraceRect();
      if (TR) return { x0: TR.x0, x1: TR.x1, z0: TR.z0, z1: TR.z1, own: false };
      if (!_pgFree()) return null;
      var B = _buildRect(), d = PERG_LIM.def, cz = (B.z0 + B.z1) / 2;
      return { x0: B.x0 - 1.0 - d, x1: B.x0 - 1.0, z0: cz - d / 2, z1: cz + d / 2, own: true };
    }
    /* the roof shelters it only where it stands over the terrace */
    function _pergolaSheltered() { var PR = _pergolaRect(); return (PR && !PR.own) ? _terraceSheltered() : 0; }
    function togglePergola() {
      var T = _terrace();
      if (pergolaOn()) { T.pgKind = T.pergola; T.pergola = 'none'; }
      else if (!_pgFree() && !T.on) { _log('pergola: turn the terrace on first -- it stands over it'); return false; }
      else if (!_pgFree() && _terraceSheltered()) { _log('pergola: the terrace is under the roof overhang -- the roof is the shelter'); return false; }
      else {
        T.pergola = (T.pgKind === 'fabric') ? 'fabric' : 'wood';
        /* nothing to stand over, or a roof over it: a place of its own at once */
        var spot = '';
        if (!_pgOwn(T) && (!T.on || _terraceSheltered())) {
          var S0 = _pgSpot(T);
          T.pg = { x0: S0.x0, z0: S0.z0, sx: S0.sx, sz: S0.sz };
          spot = ', beside wall ' + S0.side + ' -- ' + S0.why;
        }
      }
      savePref(); rebuild();
      _log('pergola ' + (pergolaOn() ? ('on (' + T.pergola + ', ' + (T.pg ? 'on a place of its own' : 'over the terrace') + (spot || '') + ')') : 'off'));
      return pergolaOn();
    }
    /* WHERE A PERGOLA OF ITS OWN COMES UP: IN SIGHT (core-v599, Marek:
       pergola se nestavi). It was built -- 39 pieces, billed -- 1 m off the
       house's x0 wall, which the opening camera looks at from the other side
       of the house: the whole pergola stood behind the building. It now comes
       up 1 m off the wall that faces the camera most (the terrace's wall
       left to the terrace), centred on it; the place is kept from then on, a
       camera that turns later does not move it. */
    function _pgSpot(T) {
      var B = _buildRect(), d = PERG_LIM.def, gap = 1.0, side = 'x0', why = 'no camera to look from';
      var cx = (B.x0 + B.x1) / 2, cz = (B.z0 + B.z1) / 2;
      try {
        var st = _stage(), cam = st && st.camera;
        if (cam && cam.position) {
          var p = cam.position.clone();
          try { if (st.group && st.group.worldToLocal) p = st.group.worldToLocal(p); } catch (eW) {}
          var vx = p.x - cx, vz = p.z - cz;
          var S = [['x0', -vx], ['x1', vx], ['z0', -vz], ['z1', vz]].sort(function (a, b) { return b[1] - a[1]; });
          var skip = (T && T.on && T.side) ? T.side : '';
          side = (S[0][0] === skip) ? S[1][0] : S[0][0];
          why = 'the wall the camera looks at' + (skip && S[0][0] === skip ? ' after the terrace\'s' : '');
        }
      } catch (eC) {}
      var x0, z0;
      if (side === 'x0') { x0 = B.x0 - gap - d; z0 = cz - d / 2; }
      else if (side === 'x1') { x0 = B.x1 + gap; z0 = cz - d / 2; }
      else if (side === 'z0') { x0 = cx - d / 2; z0 = B.z0 - gap - d; }
      else { x0 = cx - d / 2; z0 = B.z1 + gap; }
      return { x0: Math.round(x0 * 100) / 100, z0: Math.round(z0 * 100) / 100, sx: d, sz: d, side: side, why: why };
    }
    function _terraceSheltered() {
      var T = _terrace(), R = STATE.roof || {}, ov = R.over || {};
      /* A PARAPET HAS NO OVERHANG ANYWHERE, so nothing shelters a terrace on a
         flat roof and the pergola is always on offer there (Marek). */
      if (R.type === 'flat') return 0;
      var o = Number(ov[T.side]) || 0;
      return o >= 1.0 ? o : 0;
    }
    /* THE TERRACE STANDS WHERE IT IS PUT (core-v280). It used to hang off one
       wall of the house, as wide as that wall at most. Now it is a rectangle of
       its own anywhere on the site, any size up to TERR_LIM, with one rule and
       one courtesy: it may not stand under the house, and when an edge comes
       within 500 mm of a wall it snaps to that wall -- attached, with the side
       it is attached on, which is what the threshold, the shelter under a roof
       overhang and the shared foundation go by (Marek: terasa muze byt kdekoliv;
       pod 50 cm od domu se prichyti ke stene). Detached, it has no side.

       A saved project from before is turned into the same rectangle it drew. */
    var TERR_LIM = { minW: 1.2, minD: 1.0, max: 20.0, snap: 0.50 };
    function _terraceRect() {
      var T = _terrace(); if (!T.on) return null;
      if (T.x0 == null || T.z0 == null || !(T.sx > 0) || !(T.sz > 0)) {
        var B = _buildRect(), s = T.side || 'z1', w = Math.max(1.2, T.w || 4.0), d = Math.max(1.0, T.d || 3.0);
        var along = (s === 'z0' || s === 'z1') ? (B.x1 - B.x0) : (B.z1 - B.z0);
        var off = Math.max(0, Math.min(along - w, T.off || 0)), R0;
        if (s === 'z1') R0 = { x0: B.x0 + off, x1: B.x0 + off + w, z0: B.z1, z1: B.z1 + d };
        else if (s === 'z0') R0 = { x0: B.x0 + off, x1: B.x0 + off + w, z0: B.z0 - d, z1: B.z0 };
        else if (s === 'x1') R0 = { x0: B.x1, x1: B.x1 + d, z0: B.z0 + off, z1: B.z0 + off + w };
        else R0 = { x0: B.x0 - d, x1: B.x0, z0: B.z0 + off, z1: B.z0 + off + w };
        T.x0 = R0.x0; T.z0 = R0.z0; T.sx = R0.x1 - R0.x0; T.sz = R0.z1 - R0.z0; T.side = s;
      }
      return { x0: T.x0, x1: T.x0 + T.sx, z0: T.z0, z1: T.z0 + T.sz };
    }
    /* keep it off the house and snap it to a wall it comes close to; says what
       it did so the log can */
    var _terrSettleNote = '';
    function _terraceSettle() {
      var T = _terrace(); if (!T.on) return;
      var R = _terraceRect(), B = _buildRect(), g = TERR_LIM.snap, note = [];
      /* not under the house: out by the shortest way */
      var ox = Math.min(R.x1, B.x1) - Math.max(R.x0, B.x0), oz = Math.min(R.z1, B.z1) - Math.max(R.z0, B.z0);
      if (ox > 0.001 && oz > 0.001) {
        var moves = [
          { d: R.x1 - B.x0, dx: -(R.x1 - B.x0), dz: 0 }, { d: B.x1 - R.x0, dx: B.x1 - R.x0, dz: 0 },
          { d: R.z1 - B.z0, dx: 0, dz: -(R.z1 - B.z0) }, { d: B.z1 - R.z0, dx: 0, dz: B.z1 - R.z0 }
        ].sort(function (a, b) { return a.d - b.d; });
        T.x0 += moves[0].dx; T.z0 += moves[0].dz;
        note.push('moved out from under the house');
        R = _terraceRect();
      }
      /* the wall it faces, if an edge is within the snap distance and the two overlap along it */
      var alongX = Math.min(R.x1, B.x1) - Math.max(R.x0, B.x0) > 0.30, alongZ = Math.min(R.z1, B.z1) - Math.max(R.z0, B.z0) > 0.30;
      var cand = [];
      if (alongX && R.z0 >= B.z1 - 0.001 && R.z0 - B.z1 <= g) cand.push({ side: 'z1', gap: R.z0 - B.z1, dx: 0, dz: B.z1 - R.z0 });
      if (alongX && R.z1 <= B.z0 + 0.001 && B.z0 - R.z1 <= g) cand.push({ side: 'z0', gap: B.z0 - R.z1, dx: 0, dz: B.z0 - R.z1 });
      if (alongZ && R.x0 >= B.x1 - 0.001 && R.x0 - B.x1 <= g) cand.push({ side: 'x1', gap: R.x0 - B.x1, dx: B.x1 - R.x0, dz: 0 });
      if (alongZ && R.x1 <= B.x0 + 0.001 && B.x0 - R.x1 <= g) cand.push({ side: 'x0', gap: B.x0 - R.x1, dx: B.x0 - R.x1, dz: 0 });
      cand.sort(function (a, b) { return a.gap - b.gap; });
      if (cand.length) {
        T.x0 += cand[0].dx; T.z0 += cand[0].dz; T.side = cand[0].side;
        note.push('attached to wall ' + cand[0].side + (cand[0].gap > 0.001 ? (' (snapped ' + Math.round(cand[0].gap * 1000) + ' mm)') : ''));
      } else {
        T.side = null;
        note.push('free-standing');
      }
      T.x0 = Math.round(T.x0 * 1000) / 1000; T.z0 = Math.round(T.z0 * 1000) / 1000;
      _terrSettleNote = note.join(', ');
    }
    /* put it against a named wall, centred on it, keeping its size -- the
       Walls tile's jump */
    function terraceToSide(side) {
      var T = _terrace(); if (!T.on) return;
      var R = _terraceRect(), B = _buildRect(), sx = R.x1 - R.x0, sz = R.z1 - R.z0;
      if (side === 'z1') { T.x0 = (B.x0 + B.x1) / 2 - sx / 2; T.z0 = B.z1; }
      else if (side === 'z0') { T.x0 = (B.x0 + B.x1) / 2 - sx / 2; T.z0 = B.z0 - sz; }
      else if (side === 'x1') { T.x0 = B.x1; T.z0 = (B.z0 + B.z1) / 2 - sz / 2; }
      else { T.x0 = B.x0 - sx; T.z0 = (B.z0 + B.z1) / 2 - sz / 2; }
      _terraceSettle();
      savePref();
      rebuild();
    }

    /* =========================================================================
       NO STEPS ONTO A TERRACE (core-v111).

       A door onto the deck is a threshold, not a drop: the deck is laid a step
       under the floor, so a flight of concrete steps there would STAND on the
       deck and climb to a level it already reaches. The shed worked this out
       for itself and the other two did not, which is how a French window onto
       the terrace ended up with stairs in front of it.

       The terrace rectangle is this file's, so the question belongs here and
       gets answered once for every product (Marek: plati pro vsechny projekty).

       The margin is generous on purpose -- 300 mm past the edge -- because the
       probe is taken a little way out from the door and the deck rarely stops
       exactly where the drawing says. */
    /* =========================================================================
       A WALL BETWEEN THE POSTS (core-v123, step 1 of 4).

       Where a roof reaches far enough to need posts, the space between them is
       a bay -- and a bay can be left open or filled in. The shelter has had
       this for a while: boards or slats, level or upright, with a gap you set.
       The same thing belongs to the shed, the ADU and the sauna, so it is kept
       here and not written three times (Marek).

       THIS STEP IS THE STATE AND THE SIDES ONLY. Nothing is drawn yet. It is
       first because everything after it depends on it: the wall cannot be built
       for a side nobody has named, the plus cannot be offered on a side that
       has no posts, and a choice that is not saved is not a choice.

       Which sides HAVE posts is the product's own business -- it knows its
       overhangs and its trigger -- so a product hands that in and this file
       keeps it. A product that hands in nothing has no bays and the whole
       feature stays invisible, which is the right behaviour for a product with
       no posts at all. */
    var POST_WALL_KINDS = ['clad_h', 'clad_v', 'slat_h', 'slat_v'];
    /* A PRODUCT'S OWN INFILL KIND (core-v721, TAURUS, Marek: prepinac vyplni
       ma zahrnovat i krok vinyl; bez vyplne je pak cast empty). setPostWallKinds
       ([{ key, label(t), draw(ctx, runs, S) }]) adds kinds after the core's four:
       the Slats tile and the strip's arrows walk them too, and for such a kind
       buildPostWalls draws nothing itself -- it hands the product the filled
       runs, merged as for boards ({ side, onX, lo, hi, u0, u1, plane, nOut, yB,
       yT, topAt(u) }), and the product draws and bills them. No call, nothing
       changes. */
    var _pwExtra = [];
    function _pwKinds() {
      var out = (__PROD._postWallOnly || POST_WALL_KINDS).slice();
      _pwExtra.forEach(function (k) { if (out.indexOf(k.key) < 0) out.push(k.key); });
      return out;
    }
    function _pwExtraOf(k) { var i; for (i = 0; i < _pwExtra.length; i++) if (_pwExtra[i].key === k) return _pwExtra[i]; return null; }
    /* core-v723: an entry with one of the core's own keys and a draw is an
       OVERRIDE -- the product draws that kind itself (TAURUS: the boards in
       the facade's own profile, plane and courses); its label stays the
       core's unless it gives one */
    function setPostWallKinds(list) {
      _pwExtra = (list || []).filter(function (k) { return k && k.key && (POST_WALL_KINDS.indexOf(k.key) < 0 || typeof k.draw === 'function'); });
      return _pwKinds();
    }
    /* A KIND PER BAY, AND THE ORIENTATION FROM THE FACADE (core-v723, TAURUS,
       Marek: musime udelat prece jen ruzne vyplne kazdeho pole -- klikem na
       pole se vybere, zvyraznene magenta, a pruvodcem dole se vybere
       samostatna vypln; orientace vyplne stejne jako fasada -- je-li fasada
       horizontal, cyklovat jen horizontal planks / slats).

         setPostWallMode({ perBay: true, orient: function () { return 'h' | 'v' | null; } })

       perBay  a tap on a filled bay's infill picks that bay (drawn magenta
               while picked); the Slats tile and the strip's arrows then change
               that bay alone (STATE.postWall.kinds[bay]); with no bay picked
               they change every bay, as before. A Plus fills its bay with the
               kind on show and picks it; the minus opens it.
       orient  the core's four kinds walked only in that direction, and a
               bay's stored kind turned to it (clad_v on a horizontal facade
               is drawn clad_h); the product's own kinds are always offered.
       No call, nothing changes. */
    var _pwMode = { perBay: false, orient: null };
    var _pwSel = null;
    function setPostWallMode(o) {
      o = o || {};
      _pwMode.perBay = !!o.perBay;
      _pwMode.orient = (typeof o.orient === 'function') ? o.orient : null;
      return { perBay: _pwMode.perBay, orient: !!_pwMode.orient };
    }
    function _pwOrient() {
      if (!_pwMode.orient) return null;
      try { var o = _pwMode.orient(); return (o === 'h' || o === 'v') ? o : null; } catch (e) { return null; }
    }
    function _pwFit(k) {
      var o = _pwOrient();
      if (!o || POST_WALL_KINDS.indexOf(k) < 0) return k;
      return k.replace(/_[hv]$/, '_' + o);
    }
    function _pwCycle() {
      var o = _pwOrient();
      return _pwKinds().filter(function (k) { return !o || POST_WALL_KINDS.indexOf(k) < 0 || k.charAt(k.length - 1) === o; });
    }
    function postWallKindOf(key) {
      var S = _postWallState(), k = (_pwMode.perBay && S.kinds && S.kinds[key]) || S.kind;
      if (_pwKinds().indexOf(k) < 0) k = S.kind;
      return _pwFit(k);
    }
    function postWallSel() { return (_pwMode.perBay && _pwSel && _postWallState().sides[_pwSel]) ? _pwSel : null; }
    /* the kind set from the tile or the arrows: the picked bay's own, or
       every bay's when none is picked */
    function _pwSetKind(k) {
      var S = _postWallState(), sel = postWallSel();
      if (sel) { S.kinds = S.kinds || {}; S.kinds[sel] = k; return 'bay ' + sel; }
      S.kind = k;
      var had = S.kinds ? Object.keys(S.kinds).length : 0;
      S.kinds = {};
      return had ? ('every bay (' + had + ' bay(s) of their own followed -- no bay picked)') : 'every bay';
    }
    var POST_WALL_GAP = { min: 0.01, max: 0.20, def: 0.04 };
    var _postBaySupplier = null;
    /* how far a roof has to reach before it needs standing on. The shed used
       1.25 and the ADU treats 1.0 as shelter; one number for all of them, and a
       product with a different idea passes its own. */
    var POST_TRIGGER = 1.0;

    function setPostBays(fn) { _postBaySupplier = (typeof fn === 'function') ? fn : null; }
    function setPostTrigger(m) { POST_TRIGGER = Math.max(0.3, Number(m) || 1.0); return POST_TRIGGER; }

    /* THIS FILE WORKS OUT WHERE THE POSTS ARE.

       It was going to be three functions, one per product, each reading the
       same overhangs out of the same state -- and three copies of one rule is
       three chances for them to disagree. Everything it needs is already here:
       the outline, the four reaches, the terrace and whether a roof shelters
       it. So it is answered once, centrally, and a product only passes its own
       supplier if it has posts this rule cannot see (Marek: toto je funkce,
       ktera by se hodila do centralniho souboru).

       Two ways a bay comes about, and they are the same two on every product:

         a roof that reaches past the trigger on a side stands on posts there,
         and the bay runs the length of that side;
         a terrace with a pergola has posts of its own round its edge.

       A side is reported once even when both are true of it. */
    function _postBaysDefault() {
      var out = [], seen = {}, B, O, Td, R;

      /* A PARAPET HAS NOTHING TO FILL IN.

         These bays exist because a roof reaches out past the wall and leaves an
         open space under it, carried on posts. A parapet does the opposite: the
         covering turns up against the wall and there is no overhang at all -- so
         a slatted screen under it is filling a gap that is not there, and the
         dialogue that drags the overhang already refuses a parapet for the same
         reason (Marek: s atikou zmizet uplne).

         It is read fresh here, on every build, which is also what makes the bays
         follow a change from gable to hip: the overhang they come from is
         re-read, so the screen changes with the roof rather than keeping the
         shape it had when it was first switched on. */
      try {
        if ((STATE.roof || {}).parapet) return out;
      } catch (eP) {}

      try { B = _buildRect(); O = _roofOver(); } catch (e) { return out; }
      if (!B || !O) return out;

      var deepX0 = (O.x0 || 0) > POST_TRIGGER, deepX1 = (O.x1 || 0) > POST_TRIGGER;
      var deepZ0 = (O.z0 || 0) > POST_TRIGGER, deepZ1 = (O.z1 || 0) > POST_TRIGGER;
      /* the gable sides take the full width, the eave sides the building's own
         length -- so the corner under the roof belongs to one of them, not both */
      var xL = B.x0 - (deepX0 ? O.x0 : 0), xR = B.x1 + (deepX1 ? O.x1 : 0);
      /* THE WALL RUNS POST TO POST, WHICH IS NOT THE SAME AS THE FLOOR.

         The rectangle above is the covered GROUND, and it is deliberately
         asymmetric: the corner is floored by one side so no square metre is
         counted twice. A wall cannot be shared like that -- it runs from the
         outer face of the post at one end to the outer face of the post at the
         other, and the corner posts stand at the outer corners of the roof. Use
         the floor rectangle for the run and one side stops at the building line
         while its posts carry on past it, which is a wall ending in mid air
         (Marek: konci v prazdnu, maji byt od vnejsich ploch sloupu).

         So each bay carries BOTH: the rectangle for the ground and u0/u1 for
         the run of the wall, taken symmetrically. */
      var zL = B.z0 - (deepZ0 ? O.z0 : 0), zR = B.z1 + (deepZ1 ? O.z1 : 0);
      var push = function (side, r) { if (!seen[side]) { seen[side] = 1; out.push(r); } };
      if (deepZ0) push('z0', { side: 'z0', x0: xL, x1: xR, z0: B.z0 - O.z0, z1: B.z0,
                               u0: xL, u1: xR, from: 'roof' });
      if (deepZ1) push('z1', { side: 'z1', x0: xL, x1: xR, z0: B.z1, z1: B.z1 + O.z1,
                               u0: xL, u1: xR, from: 'roof' });
      if (deepX0) push('x0', { side: 'x0', x0: B.x0 - O.x0, x1: B.x0, z0: B.z0, z1: B.z1,
                               u0: zL, u1: zR, from: 'roof' });
      if (deepX1) push('x1', { side: 'x1', x0: B.x1, x1: B.x1 + O.x1, z0: B.z0, z1: B.z1,
                               u0: zL, u1: zR, from: 'roof' });

      /* and a pergola, which carries its own posts wherever it stands */
      try {
        Td = _terrace();
        if (Td && Td.on && Td.pergola && Td.pergola !== 'none' && !Td.pg) {
          R = _terraceRect();
          if (R && Td.side) push(Td.side, { side: Td.side, x0: R.x0, x1: R.x1, z0: R.z0, z1: R.z1, from: 'pergola' });
        }
      } catch (e2) {}
      return out;
    }

    /* the sides that have posts: the product's answer if it gave one, this
       file's otherwise */
    function postBaySides() {
      var r = null;
      if (_postBaySupplier) {
        try { r = _postBaySupplier(); }
        catch (e) { _log('post bays: supplier threw ' + ((e && e.message) || e)); }
      }
      if (!r || !r.length) r = _postBaysDefault();
      if (!r || !r.length) return [];
      var out = [], i;
      for (i = 0; i < r.length; i++) {
        var s = r[i];
        if (!s || !s.side) continue;
        var onXs = (s.side === 'x0' || s.side === 'x1');
        out.push({ side: s.side, x0: s.x0, x1: s.x1, z0: s.z0, z1: s.z1,
                   /* a product that supplies its own bays gets the rectangle's
                      own extent as the run, which is what it meant by it */
                   u0: (s.u0 != null) ? s.u0 : (onXs ? s.z0 : s.x0),
                   u1: (s.u1 != null) ? s.u1 : (onXs ? s.z1 : s.x1),
                   from: s.from || 'product' });
      }
      return out;
    }

    function _postWallState() {
      if (!STATE.postWall) {
        STATE.postWall = { sides: {}, kind: 'clad_v', gap: POST_WALL_GAP.def, color: '' };
      }
      var P = STATE.postWall;
      if (!P.sides) P.sides = {};
      if (_pwKinds().indexOf(P.kind) < 0) P.kind = 'clad_v';
      if (_pwMode.perBay && (!P.kinds || typeof P.kinds !== 'object')) P.kinds = {};
      P.gap = Math.max(POST_WALL_GAP.min, Math.min(POST_WALL_GAP.max,
              (P.gap == null ? POST_WALL_GAP.def : P.gap)));
      if (__PROD._postWallDeck) P.gap = Math.min(P.gap, 0.09);
      return P;
    }

    /* is there a wall on this side, and what is it made of */
    /* KEYED BY BAY, NOT BY SIDE. A side of three bays is three decisions, and
       one flag for the lot could only ever fill all of them (Marek). */
    function postWallOn(key) { return !!_postWallState().sides[key]; }

    function setPostWall(key, on) {
      var P = _postWallState();
      var side = String(key).split(':')[0];
      var live = postBaySides(), ok = false, i;
      for (i = 0; i < live.length; i++) if (live[i].side === side) { ok = true; break; }
      if (!ok) { _log('post wall: no posts on ' + side + ' -- nothing to fill'); return false; }
      if (on) {
        /* core-v723: a bay filled with the kind on show, and picked */
        if (_pwMode.perBay) { var kOn = postWallKind(); P.kinds = P.kinds || {}; if (kOn !== _pwFit(P.kind)) P.kinds[key] = kOn; _pwSel = key; }
        P.sides[key] = true;
      } else {
        delete P.sides[key];
        if (P.kinds) delete P.kinds[key];
        if (_pwSel === key) _pwSel = null;
      }
      savePref(); rebuild();
      _log('post wall: bay ' + key + ' ' + (on ? ('filled' + (_pwMode.perBay ? (' with ' + postWallKindOf(key) + ', picked') : '')) : 'opened'));
      return true;
    }

    function postWallKind() { var sel = postWallSel(); return sel ? postWallKindOf(sel) : _pwFit(_postWallState().kind); }

    /* one tile, cycled -- boards level, boards upright, slats level, slats
       upright -- because four tiles for one decision is three too many */
    function nextPostWallKind() {
      var KK = _pwCycle(), cur = postWallKind();
      var nx = KK[(KK.indexOf(cur) + 1) % KK.length];
      var who = _pwSetKind(nx);
      savePref(); rebuild();
      _log('post wall: ' + nx + ' -- ' + who);
      return nx;
    }

    function postWallIsSlats() {
      if (postWallKind().indexOf('slat') === 0) return true;
      if (!_pwMode.perBay) return false;
      var S = _postWallState(), k;
      for (k in S.sides) if (S.sides.hasOwnProperty(k) && S.sides[k] && postWallKindOf(k).indexOf('slat') === 0) return true;
      return false;
    }
    function postWallGap() { return _postWallState().gap; }

    function setPostWallGap(m) {
      var P = _postWallState();
      P.gap = Math.max(POST_WALL_GAP.min, Math.min(POST_WALL_GAP.max, Number(m) || POST_WALL_GAP.def));
      savePref(); rebuild();
      return P.gap;
    }

    function postWallColor() { return _postWallState().color || ''; }
    function setPostWallColor(c) {
      _postWallState().color = c || '';
      savePref(); rebuild();
      return c;
    }

    /* said once per build, so a side that quietly lost its posts is visible */
    function _postWallReport(P2) {
      var live = postBaySides();
      if (!live.length) { if (P2) P2('post bays none -- no side has posts'); return; }
      var S = _postWallState(), names = [], orphan = [], k, i, has;
      for (i = 0; i < live.length; i++) {
        var nb = postBayCount(live[i]), fl = 0, q;
        for (q = 0; q < nb; q++) if (S.sides[_bayKey(live[i].side, q)]) fl++;
        names.push(live[i].side + ' ' + fl + '/' + nb + ' bay(s) filled');
      }
      for (k in S.sides) {
        if (!S.sides.hasOwnProperty(k)) continue;
        var sd = String(k).split(':')[0];
        has = false;
        for (i = 0; i < live.length; i++) if (live[i].side === sd) { has = true; break; }
        if (!has) orphan.push(k);
      }
      if (P2) {
        P2('post bays ' + live.length + ' side(s) with posts: ' + names.join(', ') +
           ' | from ' + live.map(function (b) { return b.side + ':' + b.from; }).join(' ') +
           ' | ' + S.kind + (_pwMode.perBay ? (' (per bay' + (S.kinds && Object.keys(S.kinds).length ? (': ' + Object.keys(S.kinds).map(function (k) { return k + '=' + S.kinds[k]; }).join(' ')) : '') +
                                              (_pwOrient() ? (', ' + _pwOrient() + ' with the facade') : '') + (postWallSel() ? (', picked ' + postWallSel()) : '') + ')') : '') +
           (postWallIsSlats() ? (' gap ' + Math.round(S.gap * 1000) + ' mm') : '') +
           (S.color ? (' colour ' + S.color) : ' colour as the facade'));
        if (orphan.length) {
          P2('post bays ' + orphan.join(', ') + ' HELD A WALL BUT HAS NO POSTS NOW -- ' +
             'the choice is kept in case the roof comes back, and nothing is drawn there');
        }
      }
    }

    /* =========================================================================
       THE WALL ITSELF (core-v125, step 2 of 4).

       Copied from the shelter rather than designed again, because it works
       there: boards are the facade board at a near-closed 3 mm joint, slats are
       a 60 mm batten at whatever gap is set, and both are 20 mm thick standing
       just outside the post line. What is adapted is the shape of the opening:
       the shelter fills a module between its own frames, this fills a bay
       between posts, which is a plain rectangle (Marek: opisuj z tiny/shelteru,
       kde to funguje).

       It is billed as facade cladding by the square metre, and slats are billed
       on the wood that is actually there -- a 60 mm batten at a 40 mm gap is
       six tenths of the elevation, and charging for the gaps would be charging
       for air. */
    /* A SLAT IS A BATTEN, NOT CLADDING.

       Both were billed as FVW01351, wood facade cladding by the square metre --
       right for boards laid closed, wrong for a slatted screen. A slat is a
       length of batten and is bought, cut and priced by the metre; calling a
       screen "cladding" also charges it as a closed surface, which it is not
       (Marek: ja myslel ze tam mame nejake battens).

       The catalogue stocks a vertical batten at exactly the section a screen
       uses -- FVF00401, 40 x 60 -- so the slat takes that section rather than
       a 20 x 60 nobody sells. */
    var POST_WALL = {
      code:  'FVW01351',          /* boards: wood facade cladding, by the m2 */
      slat:  'FVF00401',          /* slats: vertical timber batten 40 x 60, by the m */
      screw: 'FAS55050',          /* stainless, the same fixing the facade uses */
      thk:   0.020,
      boardW: 0.145, boardGap: 0.003,
      slatW:  0.060, slatT: 0.040,
      /* THE RAILS BETWEEN THE POSTS.

         The shelter has them and this needed them for the same two reasons: a
         bay four metres wide with nothing across it is a flag, not a wall, and
         upright boards have nothing to fix to without them. A 2x4 laid flat,
         one at the head, one at the foot and one every 1.2 m between -- which
         is the span a 20 mm board holds without waving (Marek: musime tam mit i
         rails pro zpevneni mezi sloupy, jako u shelters). */
      /* KVH10060, which is the 60 x 100 the partitions already use -- KVH60100
         is not a code the catalogue has and a rail is not worth inventing one */
      rail: 'KVH10060', railW: 0.100, railT: 0.060, railStep: 1.20
    };

    /* the gable triangle above the wall head, for the sides the ridge runs INTO.

       An upright board or slat can carry straight on up into it -- that is what
       makes a gable end read as one surface. A level board cannot: every course
       would need cutting to a different length at both ends, which is a
       different job and a different price, so those stay rectangular (Marek:
       vertikalni obklad a slats mohou vyplnovat cely stit, horizontalni nech
       jak jsou). Returns the height to build to at a point along the wall. */
    /* THE ROOF OVER A POINT -- ANY POINT, ANY SIDE.

       This asked "is this a gable end?" and answered the plate for everything
       else. On an eave side that is wrong in the worst direction: the wall
       stands out at the post line, where the roof has already fallen BELOW the
       plate, so a wall built to plate height comes straight through the roof.
       That is the four shapes overflowing (Marek: vsechny 4 tvary pretekaji
       strechu, maji byt orizle podle horni plochy vazniku).

       There is no gable question to ask. A roof plane knows its own fall: from
       its eave edge to its ridge edge. Interpolating a POINT between those two
       edges answers it for a rake, an eave, a hip and a mono alike -- and the
       chord depth on top, which is the face the boarding meets.

       The point is (x, z), not a distance along the wall, because on an eave
       side the fall runs across the wall and along it nothing changes. */
    /* how far the boarding runs up INTO the rafter, above its soffit. Named
       for what it is -- it has nothing to do with the shutters. */
    var POST_WALL_RAKE_UP = 0.150;

    function _postWallTopAt(px, pz, yPlate, planes) {
      var R = STATE.roof || {};
      var P = planes || [];
      var best = null, near = null, p;
      for (p = 0; p < P.length; p++) {
        var pl = P[p];
        if (!pl || !pl.eave || !pl.ridge) continue;
        var e0 = pl.eave[0], r0 = pl.ridge[0];
        if (!e0 || !r0) continue;
        /* THE FALL IS SQUARE TO THE EAVE, NOT ALONG THE LINE TO THE RIDGE.

           I read the fall off the vector from the eave's first point to the
           ridge's first point. On a long slope those are square to each other
           and it works. On a hip end the ridge point is pulled in ALONG the
           ridge as well, so that vector comes out diagonal -- 4.00 across
           against 3.00 of fall -- and the axis is read as the wrong one. Every
           measurement after it is then taken the wrong way round, which is why
           three attempts at this screen changed nothing at all.

           A roof plane falls square to its own eave. The eave's own direction
           is what decides it (Marek: porad !!!!!!). */
        var eL = pl.eave[pl.eave.length - 1] || e0;
        var onX = Math.abs(eL[2] - e0[2]) >= Math.abs(eL[0] - e0[0]);
        var ev = onX ? e0[0] : e0[2], rv = onX ? r0[0] : r0[2];
        if (Math.abs(rv - ev) < 1e-6) continue;
        var f = ((onX ? px : pz) - ev) / (rv - ev);
        var over = (f >= -0.02 && f <= 1.02);

        /* AND THE POINT HAS TO BE WITHIN THE PLANE'S WIDTH, NOT JUST ITS FALL.

           I asked each plane only whether the point lay between its eave and its
           ridge -- along the slope. A long slope answers yes for a point far past
           the end of its own ridge, out where the hip has taken over, so the
           screen at a hipped end went on being given the gable's height. The log
           said it outright: a roof reported as hip, and a side running 3803 to
           5573 mm, which is a triangle.

           A plane also has a width: the length of its eave, from one end to the
           other. A point outside that is not under this plane at all, whatever
           the fall axis says (Marek: umis detekovat valbu, zarovnat to po eave
           hranu). */
        if (over) {
          var eA = pl.eave[0], eB = pl.eave[pl.eave.length - 1];
          var wLo = Math.min(onX ? eA[2] : eA[0], onX ? eB[2] : eB[0]);
          var wHi = Math.max(onX ? eA[2] : eA[0], onX ? eB[2] : eB[0]);
          if (wHi - wLo > 0.05) {
            var w = onX ? pz : px;
            if (w < wLo - 0.02 || w > wHi + 0.02) over = false;
          }
        }
        if (f < 0) f = 0; if (f > 1) f = 1;
        var q = e0[1] + (r0[1] - e0[1]) * f;
        /* THE LOWEST PLANE THAT COVERS THE POINT IS THE ROOF OVER IT.

           I took the highest. At a hipped end two planes cover the same point --
           the long slope, which if extended would still be climbing to a gable,
           and the hip end, which is the roof that is actually there. The highest
           is the gable that was hipped away, so the screen went on following a
           shape the roof no longer has (Marek: slats kopiruji tvar stitu i kdyz
           je tam valba).

           On a plain gable only one slope ever covers a point, so the lowest and
           the highest are the same number and nothing changes there. */
        if (over) { if (best === null || q < best) best = q; }
        else if (near === null || q < near) near = q;
      }

      /* AND A HIP END IS STILL A ROOF OVER THAT POINT.

         A plane was used only where the point fell between its eave and its
         ridge, and at a hip the end planes run the other way -- so a point out
         at the corner fell outside every one of them and the answer was the
         plate. The screen then stayed at full height and stood proud of a roof
         that had just been hipped, which is what you saw when switching from a
         gable (Marek: pri zmene na valbu slats trci nad strechu).

         When nothing covers the point, the lowest plane edge nearby is the roof
         over it -- the lowest, because that is the one it would poke through.
         The plate is the answer only when there is no sloping roof at all. */
      if (best === null && near !== null) best = near;
      if (best === null) return yPlate;               /* flat roof, or nothing over it */

      /* AND THE BOARDING DIES INTO THE RAFTER, NOT UNDER IT.

         The plane is the rafter SOFFIT -- this file's roof code says so itself:
         _rafterSoffitAt returns the plane and nothing else. I have now had this
         wrong in both directions. Taking the whole chord depth on top put the
         boards through the roof, because the sheathing, underlay, battens and
         covering all sit above that face. Stopping at the soffit put them under
         the sloping timber, which is the fault we had already been through.

         The boarding runs UP INTO the rafter and stops inside it. The figure is
         Marek's, measured off the model -- 150 mm above the soffit, which on a
         240 rafter leaves it a little over half way up. It is his number, not
         one derived here: if it needs to change it is this one constant
         (Marek: 100 mm, pak jeste tak 5 cm vys). */
      return best + POST_WALL_RAKE_UP;
    }


    /* AND A LEVEL COURSE IS CUT AT ITS ENDS, NOT SAWN OFF SQUARE.

       The courses were boxes: the right length, and both ends vertical. Under a
       rake that leaves a sawtooth all the way up, which is what a square cut
       looks like against a slope (Marek: horizontalni jsou porad zarizle na
       kolmo = rovne).

       The cut is the rake, so the top edge of a course is shorter than its
       bottom edge -- by however much the roof closes in over the width of that
       one board. The box is built to the BOTTOM length and its top vertices are
       pulled in, which mitres both ends at once and needs no angle. */
    function _mitreEnds(T, w, h, d, inLo, inHi, onX) {
      var geo = new T.BoxGeometry(w, h, d);
      var pos = geo.attributes.position, i, y, along, half = h / 2;
      var halfLen = (onX ? d : w) / 2;
      for (i = 0; i < pos.count; i++) {
        y = pos.getY(i);
        if (y < half - 1e-6) continue;                  /* the bottom edge stays */
        along = onX ? pos.getZ(i) : pos.getX(i);
        if (along < 0) {
          if (onX) pos.setZ(i, along + inLo); else pos.setX(i, along + inLo);
        } else {
          if (onX) pos.setZ(i, along - inHi); else pos.setX(i, along - inHi);
        }
      }
      pos.needsUpdate = true;
      geo.computeVertexNormals();
      return geo;
    }

    /* A BOARD CUT ALONG THE SLOPE, NOT STEPPED TO IT.

       Every board was a box with a level top, so a gable of them climbed in
       stairs -- and on 145 mm cladding those steps are the height of a hand.
       The rake is a cut, so the board is cut: the top two edges of the box are
       dropped to the roof line over each side of the board, which mitres it.

       It is one geometry per board and they are not shared, which is the price
       of the cut; a gable of sixty boards is sixty small geometries and that is
       what the model already does for every stud in the building. */
    function _mitreTop(T, w, hi, d, dropA, dropB, onX) {
      var geo = new T.BoxGeometry(w, hi, d);
      var pos = geo.attributes.position, i, y, along;
      var half = hi / 2;
      for (i = 0; i < pos.count; i++) {
        y = pos.getY(i);
        if (y < half - 1e-6) continue;                 /* not a top vertex */
        along = onX ? pos.getZ(i) : pos.getX(i);       /* which side of the board */
        pos.setY(i, half - ((along < 0) ? dropA : dropB));
      }
      pos.needsUpdate = true;
      geo.computeVertexNormals();
      return geo;
    }



    /* THE POSTS, AND THEREFORE THE BAYS.

       A side is not one opening, it is a row of them: posts stand at both ends
       and every span or less between, evenly divided. Everything below counts in
       BAYS -- the dot, the state, the rails and the boards -- because that is
       what somebody points at (Marek: aby se vyplnovalo kazde pole mezi sloupy
       zvlast). The rule is the same one the products use to stand the posts, so
       the two cannot disagree. */
    var POST_SPAN = 4.0;
    function setPostSpan(m) { POST_SPAN = Math.max(1.0, Number(m) || 4.0); return POST_SPAN; }

    /* WHERE THE POSTS REALLY STAND, FROM THE THING THAT STOOD THEM.

       I was dividing the run by a span and calling the result the bays. It gave
       three bays where the building has two and two where it has one, because
       the product's own rule runs over a slightly different length and rounds
       its own way -- and a dot that does not sit in a real bay is worse than no
       dot (Marek: mame 3 sloupy = dve pole a ty tam mas 3 puntiky).

       The products already keep every post's position: the shed for its
       downspout, and the others for the same kind of reason. So they hand the
       list over and this file stops guessing. The span rule stays as the
       fallback for a product that reports nothing. */
    var _postPtsIn = [];

    function reportPosts(pts) {
      _postPtsIn = [];
      if (!pts || !pts.length) return 0;
      var i;
      for (i = 0; i < pts.length; i++) {
        var p = pts[i];
        if (!p || p.x == null || p.z == null) continue;
        _postPtsIn.push({ x: Number(p.x), z: Number(p.z) });
      }
      return _postPtsIn.length;
    }

    /* the posts standing on this side's line, in order along it */
    function _postsOnSide(b) {
      if (!_postPtsIn.length) return null;
      var onX = (b.side === 'x0' || b.side === 'x1');
      var face = onX ? ((b.side === 'x0') ? b.x0 : b.x1)
                     : ((b.side === 'z0') ? b.z0 : b.z1);
      var tol = 0.45, out = [], i;
      for (i = 0; i < _postPtsIn.length; i++) {
        var p = _postPtsIn[i];
        var across = onX ? p.x : p.z;
        if (Math.abs(across - face) > tol) continue;
        out.push(onX ? p.z : p.x);
      }
      out.sort(function (a, c) { return a - c; });
      /* two posts a hair apart are one post seen twice (a post and its plate) */
      var tidy = [], j;
      for (j = 0; j < out.length; j++) {
        if (!tidy.length || Math.abs(out[j] - tidy[tidy.length - 1]) > 0.25) tidy.push(out[j]);
      }
      return (tidy.length >= 2) ? tidy : null;
    }

    /* the u of every post along a side, ends included */
    function _postLine(b) {
      if (b.from === 'deck' && __PROD._postWallDeck) return [b.u0,b.u1];
      var real = _postsOnSide(b);
      if (real) return real;
      var u0 = b.u0, run = b.u1 - b.u0;
      var n = Math.max(1, Math.ceil(run / POST_SPAN)), out = [], q;
      for (q = 0; q <= n; q++) out.push(u0 + run * (q / n));
      return out;
    }

    function postBayCount(b) { return Math.max(1, _postLine(b).length - 1); }

    function _bayKey(side, i) { return side + ':' + i; }

    function buildPostWalls(ctx, colour) {
      var T = ctx.THREE, g = ctx.group;
      var bays = postBaySides(), S = _postWallState();
      var L = PRODUCT + '-postwall';
      _bill[L] = {};
      if (!bays.length) { ctx.log('PWL  none -- no side has posts'); return; }

      var st0 = (ctx.storeys && ctx.storeys[0]) || { y0: 0, y1: 2.5 };
      var yB = st0.y0, yT = st0.y1;
      _postWallY = yB;
      var H = Math.max(0.3, yT - yB);
      var upright = S.kind.charAt(S.kind.length - 1) === 'v';
      var slats = postWallIsSlats();
      var PW = slats ? POST_WALL.slatW : POST_WALL.boardW;
      var PT = slats ? POST_WALL.slatT : POST_WALL.thk;
      var GAP = slats ? S.gap : POST_WALL.boardGap;
      var col = (colour != null) ? colour : (POST_WALL_TONES[S.color] || POST_WALL_TONES.larch);
      /* ONE MATERIAL PER ITEM, NOT ONE PER WALL.

         The rails and the boarding shared a single material. Highlighting a
         picked body tints the MATERIAL, so tapping a rail turned the cladding
         magenta with it -- one object as far as three.js was concerned, two
         items as far as anybody looking at it was concerned (Marek: chci aby
         asset fungoval na kazdy item zvlast).

         They are two catalogue codes, two assembly steps and two prices, so
         they are two materials. Same colour, separate objects. */
      var matBoard = new T.MeshStandardMaterial({ color: col, roughness: 0.88, metalness: 0.0 });
      var matRail  = new T.MeshStandardMaterial({ color: col, roughness: 0.88, metalness: 0.0 });
      var mat = matBoard;

      function put(w, h, d, x, y, z, useMat) {
        var m = new T.Mesh(new T.BoxGeometry(w, h, d), useMat || matBoard);
        m.position.set(x, y, z);
        m.castShadow = true; m.receiveShadow = true;
        g.add(m);
        return m;
      }

      var Bx = null; try { Bx = _buildRect(); } catch (eBR) {}
      if (!Bx) Bx = { x0: -1, x1: 1, z0: -1, z1: 1 };

      var faceA = 0, wood = 0, planks = 0, rails = 0, gableExtra = 0, slatRun = 0, done = [], i;
      /* core-v721: a product's own kind; core-v723: a kind per run, so the
         product's runs are gathered by kind and every drawn body is tagged
         with its bay (a tap picks it) -- the picked bay drawn magenta */
      var xBy = {}, xOrder = [], tagC = [], cladM2 = 0, slatM = 0, kindsUsed = {};
      var _pwMagMats = [];
      var _pwTagFrom = function (n0, cands) {
        var j;
        for (j = n0; j < g.children.length; j++) {
          var ch = g.children[j], bb = null;
          try { bb = new T.Box3().setFromObject(ch); } catch (eBB) { bb = null; }
          if (!bb || bb.isEmpty()) continue;
          var cx = (bb.min.x + bb.max.x) / 2, cz = (bb.min.z + bb.max.z) / 2, best = null, bd = 1e9, c;
          for (c = 0; c < cands.length; c++) {
            var R = cands[c], dd = Math.abs((R.onX ? cx : cz) - R.plane), uu = R.onX ? cz : cx;
            if (uu < R.u0 - 0.08 || uu > R.u1 + 0.08) continue;
            if (dd < bd) { bd = dd; best = R; }
          }
          if (!best || bd > 0.8) continue;
          var uB = best.onX ? cz : cx, LN = best.line, qq, bi = best.lo;
          for (qq = best.lo; qq < best.hi; qq++) if (uB >= LN[qq] - 1e-6) bi = qq;
          ch.userData.postBay = _bayKey(best.side, bi);
          if (best.sel) {
            /* core-v726 (Marek: magenta mene vyrazne, treba 40 %): its own
               colour with 40 % magenta over it */
            ch.traverse(function (o) {
              if (!o.isMesh || !o.material || Array.isArray(o.material)) return;
              var mm = o.material.clone();
              if (mm.color) mm.color.lerp(new T.Color(0xFF00FF), 0.40);
              if (mm.transparent && mm.opacity < 0.4) mm.opacity = 0.4;
              o.material = mm; _pwMagMats.push(mm);
            });
          }
        }
      };

      for (i = 0; i < bays.length; i++) {
        var b = bays[i];
        var onX = (b.side === 'x0' || b.side === 'x1');
        /* A SIDE WITHOUT POSTS IS NOT CLAD (core-v337). A bay is opened on every
           side whose overhang is deep enough, the rake sides included; the
           product then puts its posts where it will -- the sauna only on the
           eaves. The rake side kept its bay, found no posts of its own, fell
           back to a line of marks where no timber stands, and boarded between
           them: slats on a gable end that had no posts, and only over the half
           the fallback reached (Marek: na stitove strane se generuji slats bez
           sloupu, jen na polovine). Where a product reports its posts, a side
           they do not stand on has nothing to fix a wall to and is skipped,
           said in the log. */
        if (!(b.from === 'deck' && __PROD._postWallDeck) && _postPtsIn.length && !_postsOnSide(b)) {
          ctx.log('PWL  ' + b.side + ': no posts stand on this side -- nothing clad there');
          continue;
        }
        var line = _postLine(b);
        var nB = line.length - 1;

        /* THE RUNS OF FILLED BAYS, MERGED.

           Two bays filled side by side are one wall, and a board that stops at
           the post between them is a joint nobody asked for -- so adjacent
           filled bays are joined into one run and the boards cross both. A run
           that has an open bay next to it stops at the outer face of the post it
           touches, which is where the rail ends too (Marek). */
        var runs = [], open = 0, q;
        for (q = 0; q < nB; q++) {
          var bk = _bayKey(b.side, q);
          if (!S.sides[bk]) { open++; continue; }
          /* core-v723: a run is bays of one kind; the picked bay stands alone */
          var bkind = postWallKindOf(bk), bsel = !!(_pwMode.perBay && _pwSel === bk);
          var last = runs.length ? runs[runs.length - 1] : null;
          if (last && last.hi === q && last.kind === bkind && !last.sel && !bsel) { last.hi = q + 1; last.u1 = line[q + 1]; }
          else runs.push({ lo: q, hi: q + 1, u0: line[q], u1: line[q + 1], kind: bkind, sel: bsel });
        }
        if (!runs.length) continue;

        /* the outward normal of this side, and the planes things stand in:
           the posts, then the rails ON them from outside, then the boards
           outside the rails -- nothing sits BETWEEN the posts (Marek) */
        var nOut = (b.side === 'x0' || b.side === 'z0') ? -1 : 1;
        var postPlane = onX ? ((b.side === 'x0') ? b.x0 : b.x1)
                            : ((b.side === 'z0') ? b.z0 : b.z1);
        var railMid  = postPlane + nOut * (POST_WALL.railT / 2);
        var boardMid = postPlane + nOut * (POST_WALL.railT + PT / 2);   /* per run below (core-v723) */

        /* a position along the run, as a point on the wall line -- which is
           what the roof has to be asked about */
        var topAtU = function (u) {
          return _postWallTopAt(onX ? postPlane : u, onX ? u : postPlane, yT, ctx.roof);
        };
        for (q = 0; q < runs.length; q++) {
          var r = runs[q], run = r.u1 - r.u0, mid = (r.u0 + r.u1) / 2, t2, n = 0;
          var tc = { side: b.side, onX: onX, plane: postPlane, u0: r.u0, u1: r.u1, lo: r.lo, hi: r.hi, line: line, sel: r.sel, kind: r.kind };
          tagC.push(tc);
          kindsUsed[r.kind] = 1;
          if (_pwExtraOf(r.kind)) {
            if (!xBy[r.kind]) { xBy[r.kind] = []; xOrder.push(r.kind); }
            xBy[r.kind].push({ side: b.side, onX: onX, lo: r.lo, hi: r.hi, u0: r.u0, u1: r.u1,
                               plane: postPlane, nOut: nOut, yB: yB, yT: yT, topAt: topAtU, sel: r.sel });
            done.push(b.side + '[' + r.lo + (r.hi - r.lo > 1 ? ('-' + (r.hi - 1)) : '') + '] ' + r.kind + ' (the product\'s own)' + (r.sel ? ' PICKED' : ''));
            continue;
          }
          if (!(run > 0.2)) continue;
          /* this run's own kind (core-v723) */
          upright = r.kind.charAt(r.kind.length - 1) === 'v';
          slats = r.kind.indexOf('slat') === 0;
          PW = slats ? POST_WALL.slatW : POST_WALL.boardW;
          PT = slats ? POST_WALL.slatT : POST_WALL.thk;
          GAP = slats ? S.gap : POST_WALL.boardGap;
          boardMid = postPlane + nOut * (POST_WALL.railT + PT / 2);
          var nG0 = g.children.length, w0 = wood, ge0 = gableExtra, sr0 = slatRun;
          /* EVERYTHING DRAWN IS NAMED. Without a mark these bodies are not a
             step in the assembly, cannot be tapped open, and the audit reports
             them as drawn-but-unbilled -- which is exactly the hole the audit
             exists to find. The rails and the boards are two items, so they are
             two marks (Marek: vsechno co je v modelu musi presne do vykazu). */
          _asmMark(ctx, 'postwall_rail', 'side', 'Rail between posts', POST_WALL.rail);

          /* THE RAILS CROSS THE BOARDS, so their direction follows the boards':
             upright boards land on level rails, level boards land on upright
             ones (Marek: u horizontalniho cladding musi byt rails kolme). */
          if (upright) {
            /* LEVEL RAILS STOP WHERE THE ROOF DOES, TOO.

               They ran the whole width at every height, so the ones up in the
               gable carried on past the rake -- the same overflow as the boards
               and for the same reason. A rail is cut to the run where the roof
               is still above it, exactly like a course. */
            var topMost = yT, pr;
            for (pr = 0; pr <= 40; pr++) { var yv = topAtU(r.u0 + run * (pr / 40)); if (yv > topMost) topMost = yv; }
            var railH = Math.max(0.2, topMost - yB);
            var nRail = Math.max(2, Math.ceil(railH / POST_WALL.railStep) + 1);
            var gapR = railH / (nRail - 1), rq;
            for (rq = 0; rq < nRail; rq++) {
              var yR = yB + Math.min(railH - POST_WALL.railW / 2,
                                     Math.max(POST_WALL.railW / 2, rq * gapR));
              var yE2 = yR + POST_WALL.railW / 2;
              var rL = null, rR = null, sr;
              for (sr = 0; sr <= 120; sr++) {
                var ur = r.u0 + run * (sr / 120);
                if (topAtU(ur) + 1e-6 >= yE2) { if (rL === null) rL = ur; rR = ur; }
              }
              if (rL === null) continue;
              var rLen = rR - rL, rMid = (rL + rR) / 2;
              if (!(rLen > 0.05)) continue;
              if (onX) put(POST_WALL.railT, POST_WALL.railW, rLen, railMid, yR, rMid, matRail);
              else put(rLen, POST_WALL.railW, POST_WALL.railT, rMid, yR, railMid, matRail);
              rails += rLen;
            }
          } else {
            var nUp = Math.max(2, Math.ceil(run / POST_WALL.railStep) + 1);
            var gapU = run / (nUp - 1), uq;
            for (uq = 0; uq < nUp; uq++) {
              var uR = r.u0 + Math.min(run - POST_WALL.railW / 2,
                                       Math.max(POST_WALL.railW / 2, uq * gapU));
              /* an upright rail reaches whatever the roof does over its own
                 position, so the courses above the plate have something to land
                 on -- a level board filling a gable with nothing behind it is
                 a board nailed to air (Marek: taky s rails) */
              var hR = Math.max(0.2, topAtU(uR) - yB);
              if (onX) put(POST_WALL.railT, hR, POST_WALL.railW, railMid, yB + hR / 2, uR, matRail);
              else put(POST_WALL.railW, hR, POST_WALL.railT, uR, yB + hR / 2, railMid, matRail);
              rails += hR;
            }
          }

          _asmMark(ctx, slats ? 'postwall_slats' : 'postwall_clad', 'side',
                   slats ? 'Slats between posts' : 'Cladding between posts',
                   slats ? POST_WALL.slat : POST_WALL.code);
          if (upright) {
            var stepU = PW + GAP;
            var countU = Math.max(1, Math.floor((run + GAP) / stepU));
            var padU = (run - (countU * stepU - GAP)) / 2;
            for (t2 = 0; t2 < countU; t2++) {
              var uAt = r.u0 + padU + t2 * stepU + PW / 2;
              /* the roof over each EDGE of the board, so the cut follows it */
              var yA = topAtU(uAt - PW / 2);
              var yBt = topAtU(uAt + PW / 2);
              var yHi = Math.max(yA, yBt);
              var hU = Math.max(0.2, yHi - yB);
              gableExtra += Math.max(0, hU - H) * PW;
              var dA = yHi - yA, dB = yHi - yBt;
              var mesh;
              if (dA > 0.002 || dB > 0.002) {
                mesh = new T.Mesh(_mitreTop(T, onX ? PT : PW, hU,
                                            onX ? PW : PT, dA, dB, onX), mat);
                mesh.position.set(onX ? boardMid : uAt, yB + hU / 2, onX ? uAt : boardMid);
                mesh.castShadow = true; mesh.receiveShadow = true;
                g.add(mesh);
              } else if (onX) put(PT, hU, PW, boardMid, yB + hU / 2, uAt);
              else put(PW, hU, PT, uAt, yB + hU / 2, boardMid);
              slatRun += hU;
              n++;
            }
          } else {
            /* LEVEL COURSES GO UP INTO THE GABLE TOO, EACH ONE SHORTER.

               I left these rectangular because every course in a gable needs
               cutting at both ends and I called that a different job. It is the
               same job -- the saw is already out for the rake -- and stopping
               at the plate leaves a triangle of nothing over a wall that is
               otherwise finished (Marek: horizontalni obklad az do vrchu).

               A course runs between the two points where the roof is still
               above its TOP edge. That is found by walking the run rather than
               solving the roof, so it holds for any shape the planes describe,
               a hip end included. */
            var stepL = PW + GAP;
            var yTopMost = yT;
            var probe, uProbe;
            for (probe = 0; probe <= 40; probe++) {
              uProbe = r.u0 + run * (probe / 40);
              var yq = topAtU(uProbe);
              if (yq > yTopMost) yTopMost = yq;
            }
            var totalH = Math.max(0.2, yTopMost - yB);
            var countL = Math.max(1, Math.floor((totalH + GAP) / stepL));
            for (t2 = 0; t2 < countL; t2++) {
              var yAt = yB + t2 * stepL + PW / 2;
              var yEdge = yAt + PW / 2;
              /* how far this course reaches, left and right */
              var uL = null, uR2 = null, sN = 120, sq;
              for (sq = 0; sq <= sN; sq++) {
                var uu = r.u0 + run * (sq / sN);
                if (topAtU(uu) + 1e-6 >= yEdge) {
                  if (uL === null) uL = uu;
                  uR2 = uu;
                }
              }
              if (uL === null || uR2 === null) continue;      /* above the ridge */

              /* the same reach at the BOTTOM edge, which is the longer one */
              var yLow = yAt - PW / 2;
              var bL = null, bR = null;
              for (sq = 0; sq <= sN; sq++) {
                var ub = r.u0 + run * (sq / sN);
                if (topAtU(ub) + 1e-6 >= yLow) { if (bL === null) bL = ub; bR = ub; }
              }
              if (bL === null) { bL = uL; bR = uR2; }
              var cLen = bR - bL;
              if (!(cLen > 0.05)) continue;
              var cMid = (bL + bR) / 2;
              var inLo = Math.max(0, uL - bL), inHi = Math.max(0, bR - uR2);
              if (inLo > 0.002 || inHi > 0.002) {
                var cm = new T.Mesh(_mitreEnds(T, onX ? PT : cLen, PW,
                                               onX ? cLen : PT, inLo, inHi, onX), mat);
                cm.position.set(onX ? boardMid : cMid, yAt, onX ? cMid : boardMid);
                cm.castShadow = true; cm.receiveShadow = true;
                g.add(cm);
              } else if (onX) put(PT, PW, cLen, boardMid, yAt, cMid);
              else put(cLen, PW, PT, cMid, yAt, boardMid);
              if (yEdge > yT) gableExtra += PW * cLen;
              slatRun += cLen;
              n++;
            }
          }
          planks += n;
          faceA += run * H;
          wood += n * PW * (upright ? H : run);
          if (slats) slatM += slatRun - sr0; else cladM2 += (wood - w0) + (gableExtra - ge0);
          _pwTagFrom(nG0, [tc]);
          done.push(b.side + '[' + r.lo + (r.hi - r.lo > 1 ? ('-' + (r.hi - 1)) : '') + '] ' +
                    n + ' ' + (slats ? 'slat(s)' : 'board(s)') + (r.sel ? ' PICKED' : ''));
        }
        if (open) done.push(b.side + ' ' + open + ' bay(s) left open');
      }

      /* the product's own kinds, each handed its runs (core-v721 / v723) */
      var xi;
      for (xi = 0; xi < xOrder.length; xi++) {
        var xk = xOrder[xi], XD = _pwExtraOf(xk), nX0 = g.children.length;
        ctx.log('PWL  ' + xk + ' is the product\'s own kind -- ' + xBy[xk].length + ' filled run(s) handed to it');
        try { if (XD && typeof XD.draw === 'function') XD.draw(ctx, xBy[xk], S); } catch (eXD) { ctx.log('PWL  ' + xk + ' draw THREW ' + ((eXD && eXD.message) || eXD)); }
        _pwTagFrom(nX0, tagC.filter(function (c9) { return c9.kind === xk; }));
      }
      if (!planks) {
        if (xOrder.length) ctx.log('PWL  ' + done.join(' | '));
        else ctx.log('PWL  nothing built -- ' + bays.length + ' side(s) with posts, no bay filled');
        return;
      }
      /* boards by the surface they close, slats by the metre of batten --
         added to what the product's own kinds billed into the same rows */
      if (slatM > 0.001) {
        if (!CATALOG[POST_WALL.slat]) {
          CATALOG[POST_WALL.slat] = { code: POST_WALL.slat, unit: 'm', label: 'Vertical timber batten' };
        }
        _bill[L][POST_WALL.slat] = Math.round(((_bill[L][POST_WALL.slat] || 0) + slatM) * 100) / 100;
      }
      if (cladM2 > 0.001) _bill[L][POST_WALL.code] = Math.round(((_bill[L][POST_WALL.code] || 0) + cladM2) * 100) / 100;
      _bill[L][POST_WALL.screw] = (_bill[L][POST_WALL.screw] || 0) + Math.max(8, Math.round(planks * 4));
      if (rails > 0.05) {
        if (!CATALOG[POST_WALL.rail]) {
          CATALOG[POST_WALL.rail] = { code: POST_WALL.rail, unit: 'm', label: 'Rail between posts' };
        }
        _bill[L][POST_WALL.rail] = Math.round(((_bill[L][POST_WALL.rail] || 0) + rails) * 100) / 100;
      }
      ctx.log('PWL  rails ' + (Math.round(rails * 10) / 10) + ' m of ' + POST_WALL.rail +
              ', ' + (upright ? 'level (upright boards land on them)' : 'upright (level boards land on them)') +
              ', fixed to the OUTSIDE face of the posts; the boards go outside the rails' +
              (gableExtra > 0.05 ? (' | gable fill ' + (Math.round(gableExtra * 10) / 10) + ' m2') : ''));
      ctx.log('PWL  ' + done.join(' | ') + ' -- ' + Object.keys(kindsUsed).join(' + ') +
              (slatM > 0.001 ? (' at ' + Math.round(S.gap * 1000) + ' mm') : '') +
              ' | elevation ' + (Math.round(faceA * 10) / 10) + ' m2, wood ' +
              (Math.round((wood + gableExtra) * 10) / 10) + ' m2' +
              (slatM > 0.001 ? ' (the gaps are not billed)' : ''));
    }

    /* =========================================================================
       THE PLUS ON THE FLOOR (core-v126, step 3 of 4).

       A bay with posts and no wall gets a disc lying on the ground with a plus
       cut into it. Tapping it fills the bay; tapping a filled bay's disc opens
       it again. The discs are built with the model and picked with the model,
       using the aim helper every other pick already uses -- a sixth copy of the
       raycast was exactly what that helper exists to prevent.

       They live in their own group on the stage rather than in a layer, for the
       same reason the handles do: they are not the building, they must not be
       billed, they must not turn up in the exploded view, and they have to
       disappear the moment the step changes (Marek: tlacitko plus na podlaze). */
    var _plusGrp = null;
    var _postWallY = null;
    /* THE AIM HELPER, BORROWED RATHER THAN COPIED.

       _aim lives a scope deeper than this, and the comment above it says
       plainly why it exists: five copies of the same seven lines, five chances
       to drift. Writing a sixth here would undo that. The deeper scope hands
       its own function up instead, so there is still exactly one raycast. */
    var _aimRef = null;

    function _plusClear() {
      var st = _stage();
      if (_plusGrp && st && st.group) { try { st.group.remove(_plusGrp); } catch (e) {} }
      _plusGrp = null;
    }

    /* THE SAME DOT THE SHELTER USES, COPIED RATHER THAN INVENTED.

       Mine was a red disc with two white bars laid over it as separate meshes.
       It read as a red blob -- the bars were the wrong way round for a minus,
       the colour said nothing, and on the ground plane the two planes fought
       each other for depth. The shelter draws its dot ONCE onto a canvas: a
       near-black circle, a white ring, a white plus, and that canvas becomes
       the texture of one flat disc. One mesh, no z-fighting, and it looks the
       same as the thing next to it (Marek: puntik ma vypadat stejne jako v
       shelters). A filled bay gets a minus, which is the same drawing with one
       stroke left out. */
    var _plusTex = {};

    function _plusTexture(T, filled) {
      var key = filled ? 'minus' : 'plus';
      if (_plusTex[key]) return _plusTex[key];
      var c = document.createElement('canvas');
      c.width = 256; c.height = 256;
      var x = c.getContext('2d');
      x.clearRect(0, 0, 256, 256);
      x.beginPath();
      x.arc(128, 128, 118, 0, Math.PI * 2);
      x.fillStyle = 'rgba(20,21,24,0.92)';
      x.fill();
      x.lineWidth = 12;
      x.strokeStyle = '#ffffff';
      x.stroke();
      x.strokeStyle = '#ffffff';
      x.lineWidth = 16;
      x.lineCap = 'round';
      x.beginPath(); x.moveTo(66, 128); x.lineTo(190, 128); x.stroke();
      if (!filled) { x.beginPath(); x.moveTo(128, 66); x.lineTo(128, 190); x.stroke(); }
      var tex = new T.CanvasTexture(c);
      try { tex.colorSpace = T.SRGBColorSpace; } catch (e) {}
      tex.anisotropy = 4;
      _plusTex[key] = tex;
      return tex;
    }

    /* THE DOT'S SIZE (core-v729, Marek: ty terciky pro on / off vyplni udelej
       mensi, jsou moc velke): 360 across (was 600), and 400 out from the post
       line (was 550) -- one number for every product */
    var PW_DOT = { r: 0.18, out: 0.40 };
    function _plusMesh(T, filled, deckDot) {
      var disc = new T.Mesh(new T.CircleGeometry(PW_DOT.r, 28),
        new T.MeshBasicMaterial({ map: _plusTexture(T, filled), transparent: true,
                                  depthWrite: false }));
      disc.rotation.x = -Math.PI / 2;
      disc.renderOrder = 6;
      // Elevated decks opt into a screen-sized, camera-facing touch target.
      // Other products keep the original horizontal world-sized disc.
      if (deckDot && deckDot.pixels) {
        disc.material.depthTest = false; disc.renderOrder = 30;
        disc.frustumCulled = false;
        disc.onBeforeRender = function(renderer, scene, camera) {
          var rect = renderer.domElement.getBoundingClientRect();
          var v = this.getWorldPosition(new T.Vector3()).applyMatrix4(camera.matrixWorldInverse);
          var depth = camera.isOrthographicCamera ? 1 : Math.abs(v.z);
          var proj = camera.projectionMatrix.elements;
          // Use the real CSS viewport and projection, including view offsets.
          // Separate axes also preserve round targets during responsive resize.
          this.scale.set(deckDot.pixels*depth/(PW_DOT.r*proj[0]*(rect.width||1)),
                         deckDot.pixels*depth/(PW_DOT.r*proj[5]*(rect.height||1)),1);
          this.quaternion.copy(camera.quaternion);
          this.updateMatrixWorld(true);
        };
      }
      return disc;
    }

    function _plusBuild() {
      _plusClear();
      var st = _stage(), T = global.THREE;
      if (!st || !st.group || !T) return 0;
      if (!_postWallPlusWanted()) return 0;
      var bays = postBaySides();
      if (!bays.length) return 0;
      var S = _postWallState();
      /* the floor level is remembered by the wall builder, which is the only
         thing here that gets a context -- storeys() itself is a scope deeper */
      var y = (_postWallY != null ? _postWallY : 0) + 0.03, i;
      _plusGrp = new T.Group();
      _plusGrp.name = 'postwall-plus';
      var B = null; try { B = _buildRect(); } catch (eB) {}
      for (i = 0; i < bays.length; i++) {
        var b = bays[i];
        /* ONLY UNDER A ROOF THAT STANDS ON POSTS.

           A pergola over a terrace has posts too, and I offered a wall there --
           but a pergola is open by definition and a wall under it is a
           different product decision, not this one. Only the bays the ROOF made
           get a dot (Marek: mas ho stavet jen tam, kde jsou podpurne sloupy pod
           previslou strechou, udelal jsi ho i na terase). */
        if (b.from !== 'roof' && !(b.from === 'deck' && __PROD._postWallDeck)) continue;
        var deckDot = b.from === 'deck' ? __PROD._postWallDeck() : null;
        var dotOut = deckDot ? (Number(deckDot.out)||0) : PW_DOT.out;
        var dotY = deckDot ? deckDot.y + 0.03 : y;
        /* ONE DOT PER BAY, at the middle of that bay and just outside the post
           line -- which is also where the wall it switches on will stand */
        var line = _postLine(b);
        var onX2 = (b.side === 'x0' || b.side === 'x1');
        var nOut2 = (b.side === 'x0' || b.side === 'z0') ? -1 : 1;
        var face = onX2 ? ((b.side === 'x0') ? b.x0 : b.x1)
                        : ((b.side === 'z0') ? b.z0 : b.z1);
        var q2;
        for (q2 = 0; q2 < line.length - 1; q2++) {
          var uMid = (line[q2] + line[q2 + 1]) / 2;
          var key2 = _bayKey(b.side, q2);
          var md = _plusMesh(T, !!S.sides[key2], deckDot);
          if (onX2) md.position.set(face + nOut2 * dotOut, dotY, uMid);
          else md.position.set(uMid, dotY, face + nOut2 * dotOut);
          md.userData.postBay = key2;
          _plusGrp.add(md);
        }
      }
      if (!_plusGrp.children.length) {
        _plusClear();
        _log('post wall: no bay is under a roof on posts -- no dot offered');
        return 0;
      }
      st.group.add(_plusGrp);
      return bays.length;
    }

    /* only where somebody is deciding this: the exterior step, and only in 3D */
    var _plusWanted = false;
    function showPostWallPlus(on) {
      _plusWanted = !!on;
      if (_plusWanted) _plusBuild(); else _plusClear();
      /* core-v723: leaving the step lets the picked bay go (its magenta too) */
      if (!_plusWanted && _pwSel) { _pwSel = null; try { rebuild(); } catch (eRb) {} }
      return _plusWanted;
    }
    function _postWallPlusWanted() { return _plusWanted; }

    /* the tap. Answers true when it took the tap, so the caller knows to stop */
    function postWallTap(px, py) {
      if (!_plusWanted || !_plusGrp) return false;
      if (!_aimRef) return false;          /* nothing has aimed yet this session */
      var rc = _aimRef(px, py);
      if (!rc) return false;
      var hits = null;
      try { hits = rc.intersectObjects(_plusGrp.children, true); } catch (e) { return false; }
      if (!hits || !hits.length) return _pwPickTap(rc);
      var o = hits[0].object, side = '';
      while (o && !side) { side = (o.userData && o.userData.postBay) || ''; o = o.parent; }
      if (!side) return _pwPickTap(rc);
      setPostWall(side, !postWallOn(side));
      return true;
    }
    /* core-v723: a tap on a filled bay's infill picks it (perBay products
       only); a second tap lets it go. The nearest SHOWN body under the finger
       decides -- a wall in front of the bay keeps the tap */
    function _pwPickTap(rc) {
      if (!_pwMode.perBay) return false;
      var st = _stage();
      if (!st || !st.group) return false;
      var hs = null;
      try { hs = rc.intersectObjects(st.group.children, true); } catch (e) { return false; }
      var i, key = '';
      for (i = 0; hs && i < hs.length; i++) {
        var o = hs[i].object, shown = true, k = '';
        for (var a = o; a && a !== st.group; a = a.parent) {
          if (a.visible === false) { shown = false; break; }
          if (!k && a.userData && a.userData.postBay) k = a.userData.postBay;
        }
        if (!shown) continue;
        key = k;
        break;
      }
      if (!key || !postWallOn(key)) return false;
      _pwSel = (_pwSel === key) ? null : key;
      _log('post wall: bay ' + key + (_pwSel ? (' picked -- ' + postWallKindOf(key) + '; the arrows change it alone') : ' let go -- the arrows change every bay'));
      rebuild();
      try { _sxRefresh(); } catch (eR) {}
      return true;
    }

    /* =========================================================================
       THE CONTROLS (core-v127, step 4 of 4).

       Three pieces of interface, and all three are made HERE and handed to the
       product ready-built, because the alternative is the same tile written
       three times and drifting three ways -- which is what the door mark and
       the terrace steps both taught us this week.

         postWallTile()     the one cycling tile for the Exterior step: boards
                            level, boards upright, slats level, slats upright
         seatPostWallGap()  the spacing slider, seated into the drawer pane and
                            only when slats are chosen -- a slider that does
                            nothing is worse than no slider
       The colour is NOT made here: it is a row in the product's own palette
       list, so the slats can be a different timber from the facade and still go
       through the one colour system (Marek: asi slats, muze byt vizualne
       atraktivni to odlisit). buildPostWalls takes what that row resolved.

       A product splices these into its own panel; nothing about them is
       product-specific. */
    /* SHORT ENOUGH FOR A TILE.

       "Cladding / Horizontal" is two thirds of a narrow tile spent on a word
       the group heading already said, and the web renders these uppercase, so
       it wrapped or clipped. One noun and a letter: SLATS V, CLAD H. The letter
       needs no translating -- it is the same letter in every locale the store
       runs in -- so only the two nouns go through the translator (Marek: na
       dlaznicich pak stejne nejsou videt, muzes udelat SLATS V / SLATS H). */
    function _postWallLabel(t) {
      var k = postWallKind();
      var XK = _pwExtraOf(k);
      if (XK && XK.label) { try { return (typeof XK.label === 'function') ? XK.label(t) : String(XK.label || k); } catch (eXL) { return k; } }
      var slats = k.indexOf('slat') === 0;
      var up = k.charAt(k.length - 1) === 'v';
      /* core-v726: post_wall_clad reads Planks now; with the facade setting
         the direction (setPostWallMode orient) the letter says nothing */
      return (slats ? t('post_wall_slats', 'Slats') : t('post_wall_clad', 'Planks')) +
             (_pwOrient() ? '' : (' ' + (up ? 'V' : 'H')));
    }

    /* the tile for the Exterior step; dim when no side has posts, because then
       there is nothing for it to be about */
    function postWallTile(t, ICO) {
      return {
        label: _postWallLabel(t),
        svg: (ICO && (ICO.cladding || ICO.wall)) || '',
        on: function () { return postBaySides().length > 0; },
        tap: function () {
          if (!postBaySides().length) {
            _log('post wall: no side has posts -- widen a roof overhang or add a pergola first');
            return;
          }
          nextPostWallKind();
        }
      };
    }

    /* THE SPACING SLIDER, BUILT LIKE THE PITCH ONE.

       Mine came out half off the panel: the kit's slider is made to FLOAT over
       the model -- absolutely placed, centred with a transform, a fixed width --
       and dropping that into a drawer pane leaves it hanging off the left edge.
       The roof sliders are seated by stripping all of that and letting the pane
       decide the width, which is what this does now.

       And it reads like the pitch does: the name small and quiet at the top
       left, the figure riding ON the thumb rather than parked in the middle of
       the track, because that is where the eye already is while dragging
       (Marek: stejny jako u sklonu strechy). */
    var _pwGapSl = null;

    function _pwGapLabel() {
      var el = $('cf-postwall-gap-val');
      if (!el || !_pwGapSl) return;
      var inp = _pwGapSl.input;
      if (!inp) return;
      el.textContent = Math.round(postWallGap() * 1000) + ' mm';
      try {
        var lo = parseFloat(inp.min) || 0, hi = parseFloat(inp.max) || 0;
        var v = parseFloat(inp.value);
        if (isNaN(v)) v = Math.round(postWallGap() * 1000);
        var f = (hi > lo) ? ((v - lo) / (hi - lo)) : 0;
        var w = inp.clientWidth || _pwGapSl.el.clientWidth || 0;
        var thumb = 14;
        el.style.left = (thumb / 2 + f * Math.max(0, w - thumb)) + 'px';
      } catch (e) {}
    }

    function seatPostWallGap(pane, t) {
      if (!pane) return null;
      if (!postWallIsSlats() || !postBaySides().length) return null;
      var U = global.ConfiguratorUI || null;
      if (!U || !U.slider) return null;

      if (!_pwGapSl) {
        /* A STEP SOMEBODY CAN ASK FOR AT A COUNTER. 5 mm gave forty stops
           nobody wants and figures like 85 mm; a slat gap is chosen in whole
           units -- an inch at a time in imperial, 2 cm in metric (Marek:
           krokovani po inch, 2 cm). */
        var stepMm = STATE.isMetric ? 20 : 25;
        /* THE FIRST STOP IS ONE STEP, NOT THE RAW MINIMUM. Starting at 10 mm
           with a 25 mm step gave 10, 35, 60 -- every stop a quarter inch off a
           whole one. Anchored on the step it reads 25, 50, 75: one inch, two,
           three. */
        var loMm = stepMm;
        var hiMm = Math.round((__PROD._postWallDeck ? 0.09 : POST_WALL_GAP.max) * 1000);
        hiMm = loMm + Math.floor((hiMm - loMm) / stepMm) * stepMm;
        _pwGapSl = U.slider({
          id: 'cf-postwall-gap', bottom: 44, width: 190,
          min: loMm, max: hiMm,
          step: stepMm, value: Math.round(postWallGap() * 1000),
          onInput: function (v) { setPostWallGap((Number(v) || 40) / 1000); _pwGapLabel(); },
          onChange: function () { _log('post wall gap -> ' + Math.round(postWallGap() * 1000) + ' mm'); }
        });
        try {
          var lab = document.createElement('div');
          lab.id = 'cf-postwall-gap-val';
          lab.style.cssText =
            'position:absolute;top:100%;transform:translateX(-50%);' +
            'margin-top:4px;padding:1px 7px;border-radius:6px;white-space:nowrap;' +
            'background:rgba(255,255,255,0.96);box-shadow:0 1px 4px rgba(0,0,0,0.12);' +
            'font:700 12px/1.35 Montserrat,sans-serif;color:#1A1C1D;pointer-events:none;';
          _pwGapSl.el.appendChild(lab);
        } catch (e) {}
      }
      if (!_pwGapSl || !_pwGapSl.el) return null;

      var wrap = document.createElement('div');
      wrap.style.cssText = 'position:relative;padding:2px 14px 30px;';
      var tt = document.createElement('div');
      /* LEFT, like the pitch and wall-height sliders. I moved it right on one
         reading and it was wrong: the rule is that it matches the sliders that
         already exist, not that it matches anything else in the drawer
         (Marek: nalevo jako u sklonu strechy). */
      tt.style.cssText = 'font:700 10px Montserrat,sans-serif;letter-spacing:.08em;' +
                         'text-transform:uppercase;opacity:.62;margin-bottom:8px;' +
                         'text-align:left;';
      tt.textContent = t('post_wall_gap', 'Slat spacing');
      wrap.appendChild(tt);

      /* EVERY PIECE OF THE FLOATING LAYOUT HAS TO GO, not most of it: position,
         both insets, the bottom the kit set, the transform that centred it, the
         fixed width and the max width -- and the input inside it as well, which
         carries its own width. Leaving any one of them is what put it half off
         the panel. */
      var el = _pwGapSl.el;
      el.style.position = 'relative';
      el.style.left = 'auto'; el.style.right = 'auto';
      el.style.top = 'auto'; el.style.bottom = 'auto';
      el.style.transform = 'none';
      el.style.margin = '0';
      el.style.width = '100%'; el.style.maxWidth = 'none'; el.style.minWidth = '0';
      try {
        var inp2 = _pwGapSl.input || el.querySelector('input');
        if (inp2) { inp2.style.width = '100%'; inp2.style.maxWidth = 'none'; inp2.style.margin = '0'; }
      } catch (e2) {}
      wrap.appendChild(el);
      pane.appendChild(wrap);
      try { _pwGapSl.show(); } catch (e3) {}

      /* the bubble is placed from the track's width, which is zero until the
         drawer has laid out -- so it is placed again once it has */
      _pwGapLabel();
      try { global.requestAnimationFrame(_pwGapLabel); } catch (e4) {}
      try { global.setTimeout(_pwGapLabel, 350); global.setTimeout(_pwGapLabel, 900); } catch (e5) {}
      return _pwGapSl;
    }

    /* THE COLOUR IS A PALETTE ROW, NOT A LIST OF SWATCHES.

       My first attempt handed back hex swatches of its own. That is not how
       colour works here: a product keeps a list of rows -- key, group, label
       key, a palette of named tones, and a 'when' that decides whether the row
       is offered at all -- and resolves them through its own colorOf. A second
       colour system beside it would look the same and behave differently on
       the first locale or the first palette change.

       So the core asks for the colour instead of choosing it: a product passes
       what its own palette resolved, and adds one row to its list. The tone
       table here is only the fallback for a product that passes nothing. */
    var POST_WALL_TONES = { larch: 0xB9855A, spruce: 0xD8BC8E, oak: 0xA9793F,
                            shou: 0x242428, grey: 0x8C8C88, ivory: 0xE7E0CF };

    /* =========================================================================
       EXTERNAL ROLLER SHUTTERS (core-v140, step 1 of 3).

       A box over the opening, guide rails down both reveals, and the slats
       rolled up inside it -- shading the glass before the heat reaches it.

       STEP 1 IS THE CHOICE AND THE CODE. Nothing is drawn yet. Which shutter an
       opening takes is the whole of the problem: get it wrong and the model is
       a picture of something nobody can order. Everything else is boxes.

       WHICH ONE FITS. The catalogue has two families and they are not
       interchangeable:

         five fixed sizes, made for a particular opening
         a grid of 42, seven widths by six heights

       The rule falls out of the fixed ones once you line them up with the
       openings they name: OEB09001 is 900 x 1575 and the window is 900 x 1350.
       1575 - 1350 = 225, and the catalogue's own note says "height of the
       raised blinds 225". So a shutter's height is the OPENING plus its box,
       and that is the number to match on -- not the opening height, which is
       what I would have guessed and which picks a shutter a box too short.

       A fixed size wins when it matches; otherwise the smallest grid item that
       covers the opening and the box. If neither does, the opening says so in
       the log instead of getting a shutter that does not exist. */
    /* THE BOX IS NOT A CONSTANT. IT GROWS WITH THE DROP.

       I read 1575 - 1350 = 225 off one row and made it a constant. It is a
       table: a longer curtain is a fatter roll and needs a deeper case, and the
       catalogue steps it 205 / 225 / 240 / 275 / 475. With a flat 225 every
       2590 portal asked for a shutter 2815 tall, none exists, and I reported
       seven holes in a range that has none -- the answer was written in the
       rows I had, in NOTE 1 (Marek: schranka neni konstanta, roste se
       spustenim). Checked against every row that names its window: no
       mismatches. */
    function _blindBox(h) {
      var mm = h * 1000;
      if (mm <= 1250) return 0.205;
      if (mm <= 1600) return 0.225;
      if (mm <= 2100) return 0.240;
      if (mm <= 2700) return 0.275;
      return 0.475;
    }

    /* AND THERE ARE SIXTEEN FIXED SIZES, NOT FIVE. I built the list from the
       47-row file and the catalogue carries 60 -- every portal size is there
       under exactly the id you would expect. Taken from the catalogue now. */
    var BLIND_FIXED = [
      { code: 'OEB06001', w: 0.600, h: 2.865 },
      { code: 'OEB07001', w: 0.700, h: 1.355 },
      { code: 'OEB08001', w: 0.800, h: 1.355 },
      { code: 'OEB09001', w: 0.900, h: 1.575 },
      { code: 'OEB10001', w: 1.000, h: 2.865 },
      { code: 'OEB12001', w: 1.200, h: 1.725 },
      { code: 'OEB12002', w: 1.200, h: 2.240 },
      { code: 'OEB15001', w: 1.500, h: 1.875 },
      { code: 'OEB15002', w: 1.500, h: 2.865 },
      { code: 'OEB17001', w: 1.700, h: 2.865 },
      { code: 'OEB20001', w: 2.000, h: 2.865 },
      { code: 'OEB25001', w: 2.500, h: 2.865 },
      { code: 'OEB26001', w: 2.590, h: 1.940 },
      { code: 'OEB30001', w: 3.000, h: 2.865 },
      { code: 'OEB40001', w: 4.000, h: 2.865 },
      { code: 'OEB50001', w: 5.000, h: 3.865 }
    ];

    /* AND THE RANGE ITSELF SAYS WHERE IT STOPS.

       I put a floor of 750 x 1000 in on my own reading of "a shutter is not
       worth it on a small window" -- and it threw out the 600 and 700 wide
       sizes, which the catalogue has, because a 600 x 2590 french door is
       narrow but not small. The catalogue already draws that line by what it
       does and does not stock: if something in it covers the opening, it is
       fitted; if nothing does, there is none. One authority, not two. */
    var BLIND_W = [0.75, 1.00, 1.25, 1.50, 1.75, 2.00, 2.25];
    var BLIND_H = [1.205, 1.455, 1.725, 1.990, 2.240, 2.525];

    /* the grid code is built from the position in each list, which is what the
       catalogue's own numbering is: OEBG + width index + height index */
    function _blindGridCode(wi, hi) {
      return 'OEBG0' + (wi + 2) + '0' + (hi + 3);
    }

    function blindFor(w, h) {
      var need = h + _blindBox(h), i, j;

      /* AN EXACT SIZE FIRST. The catalogue's fixed rows are made for particular
         openings, and when one names this one it is the answer. */
      for (i = 0; i < BLIND_FIXED.length; i++) {
        var fx = BLIND_FIXED[i];
        if (Math.abs(fx.w - w) < 0.005 && Math.abs(fx.h - need) < 0.030) {
          return { code: fx.code, w: fx.w, h: fx.h, exact: true };
        }
      }

      /* OTHERWISE THE SMALLEST THING THAT COVERS IT -- and smallest means the
         whole shutter, not the first row that happens to be wide enough.

         Taking the first fit put a 600 x 2865 over a 400 x 600 window: narrow
         enough to pass the width test and nearly five times too tall, because
         nothing was comparing candidates against each other. Every fitting size
         is gathered and the least oversized one wins, on height first, since
         that is the dimension that runs away. */
      var WIDE = 0.30, TALL = 0.60;
      var best = null, all = BLIND_FIXED.slice();
      for (i = 0; i < BLIND_W.length; i++) {
        for (j = 0; j < BLIND_H.length; j++) {
          all.push({ code: _blindGridCode(i, j), w: BLIND_W[i], h: BLIND_H[j] });
        }
      }
      for (i = 0; i < all.length; i++) {
        var c = all[i];
        if (c.w + 1e-6 < w || c.w > w + WIDE) continue;
        if (c.h + 1e-6 < need || c.h > need + TALL) continue;
        if (!best || c.h < best.h - 1e-6 || (Math.abs(c.h - best.h) < 1e-6 && c.w < best.w)) best = c;
      }
      if (best) return { code: best.code, w: best.w, h: best.h, exact: false };
      return null;
    }

    /* ON OR OFF, FOR THE WHOLE HOUSE. One tile, not one per window: somebody
       fitting shutters fits them, and a per-opening choice is a second editing
       mode for a decision almost nobody splits (Marek: dlazdice zapnout
       zaluzie). */
    function blindsOn() { return !!STATE.blinds; }
    function setBlinds(on) {
      STATE.blinds = !!on;
      savePref(); rebuild();
      _log('blinds ' + (STATE.blinds ? 'on' : 'off'));
      return STATE.blinds;
    }
    function toggleBlinds() { return setBlinds(!blindsOn()); }

    function blindsTile(t, ICO) {
      return {
        label: t('blinds', 'Blinds'),
        svg: (ICO && (ICO.blinds || ICO.window)) || '',
        on: function () { return blindsOn(); },
        tap: function () { toggleBlinds(); }
      };
    }

    /* =========================================================================
       THE SHUTTERS THEMSELVES (core-v141, step 2 of 3).

       A segment already carries everything this needs -- where it runs, which
       way it faces, and the openings on it with their clear size and sill. So
       the box goes above each opening, a guide rail down each reveal, and a
       hand of slats showing under the box, all outside the facade.

       Only WINDOWS and french doors. An entry door with a shutter over it is a
       shop at night, not a house, and a garage door has its own. Anything the
       catalogue has no shutter for is named in the log rather than silently
       skipped -- an opening that quietly has no shutter is the one somebody
       finds on site. */
    var BLIND = {
      /* THE BOX GOES INTO THE WALL, NOT ONTO IT.

         A 205 mm case hung on the facade is a lump over every window. Built in,
         only its front panel shows -- which is how these are actually fitted and
         is the reason the box has a removable front at all (Marek: neda se ta
         krabice zapustit vice do fasady, aby byl videt jen celni panel). The
         case is drawn behind the facade line and a thin panel closes it flush. */
      boxD:   0.205,         /* the case, which is buried */
      faceT:  0.022,         /* the front panel, which is all you see */
      proud:  0.008,         /* and it stands a touch proud, so it reads as a lid */
      railW:  0.055,
      /* LOWER, AND MADE OF SLATS. A hand of curtain says shutter; a solid strip
         says lintel. It is drawn slat by slat, at the real pitch, and further
         down so the thing reads as a shutter that is partly closed. */
      drop:   0.34,          /* of the opening height */
      slatH:  0.050, slatGap: 0.002, slatT: 0.014,
      boxCol: 0xE8E6E1, slatCol: 0xD8D5CE
    };

    function buildBlinds(ctx, colour) {
      var T = ctx.THREE, g = ctx.group;
      var L = PRODUCT + '-blinds';
      _bill[L] = {};
      if (!blindsOn()) { ctx.log('BLD  off'); return; }
      var segs = ctx.seg || [];
      if (!segs.length) { ctx.log('BLD  no wall segments'); return; }

      /* the colour the product's own palette resolved; the pale default only
         when it passed none */
      var cBox = (colour != null) ? colour : BLIND.boxCol;

      /* A MATERIAL PER SHUTTER, NOT PER WALL.

         The audit caught my own builder twice: every shutter on the house drew
         from the same two materials, so tapping one tinted all of them -- the
         same fault as the rails and the cladding, made by the same hand a day
         later. Each shutter is its own catalogue code and its own price, so
         each gets its own pair (Marek's audit, adu-blinds twice). */
      var matFor = function () {
        return {
          box:  new T.MeshStandardMaterial({ color: cBox, roughness: 0.55, metalness: 0.25 }),
          slat: new T.MeshStandardMaterial({ color: cBox, roughness: 0.62, metalness: 0.20 })
        };
      };
      var mats = matFor(), matBox = mats.box, matSlat = mats.slat;

      function put(mat, w, h, d, x, y, z, ry) {
        var m = new T.Mesh(new T.BoxGeometry(w, h, d), mat);
        m.position.set(x, y, z);
        if (ry) m.rotation.y = ry;
        m.castShadow = true; m.receiveShadow = true;
        g.add(m);
        return m;
      }

      var fitted = 0, skipped = [], noneR = [], byCode = {}, i, j;
      var FT = 0; try { FT = Number(STATE.facadeThk) || 0; } catch (e) {}

      for (i = 0; i < segs.length; i++) {
        var sg = segs[i];
        var ops = sg.openings || [];
        if (!ops.length) continue;
        /* the wall's own direction, and its outward normal */
        var dx = (sg.x1 - sg.x0) / sg.len, dz = (sg.z1 - sg.z0) / sg.len;
        /* THE BOX'S WIDTH RUNS ALONG THE WALL, WHICH THIS GETS WRONG BY A
           QUARTER TURN. Rotating by atan2(dx, dz) sends the box's local X to
           (0, -1) for a wall running along x -- square across it, so every
           shutter stood at right angles and half of it was inside the wall.
           The angle that maps local X onto the wall's own direction is
           atan2(-dz, dx): checked on all four walls, not reasoned about once
           (Marek: blinds jsou postavene kolmo, trci ze zdi pulkou). */
        var ry = Math.atan2(-dz, dx);
        for (j = 0; j < ops.length; j++) {
          var o = ops[j];
          if (!o) continue;
          if (o.kind !== 'window' && o.kind !== 'french') continue;
          /* core-v791 (TWO STORY thread, Rohatec R5): NOT OVER A ROUND LIGHT --
             a shutter cannot close a circle (the offer: "all the windows but
             the round one") -- and not over an opening the project marked
             blind: false (the reference house has them on the study and the
             upper floor only). Named in the log, as a skipped one is. */
          if (o.round || /^OWC/.test(o.code || '')) { noneR.push((o.code || 'round') + ' (round)'); continue; }
          if (o.blind === false) { noneR.push((o.code || o.kind) + ' (marked without)'); continue; }
          var b = blindFor(o.w, o.h);
          if (!b) { skipped.push((o.code || o.kind) + ' ' + Math.round(o.w * 1000) + 'x' + Math.round(o.h * 1000)); continue; }

          /* the centre of the opening, out on the face of the facade */
          var cx = sg.x0 + dx * o.at + sg.nx * (FT + BLIND.boxD / 2);
          var cz = sg.z0 + dz * o.at + sg.nz * (FT + BLIND.boxD / 2);
          var yHead = sg.y0 + o.sill + o.h;

          /* one mark per shutter, carrying the code that was chosen for this
             opening -- so the exploded view steps through them and a tap opens
             the right catalogue row rather than the one next door */
          _asmMark(ctx, 'blind', 'side', 'Exterior blinds', b.code);
          mats = matFor(); matBox = mats.box; matSlat = mats.slat;

          /* the case, buried in the facade, and the panel that closes it. Its
             height is the box for THIS drop, not a fixed number. */
          var bx = _blindBox(o.h);
          var nx = sg.nx, nz = sg.nz;
          var caseOff = FT - BLIND.boxD / 2 + BLIND.faceT;
          put(matBox, b.w, bx, BLIND.boxD,
              sg.x0 + dx * o.at + nx * caseOff, yHead + bx / 2,
              sg.z0 + dz * o.at + nz * caseOff, ry);
          var faceOff = FT + BLIND.proud + BLIND.faceT / 2;
          put(matBox, b.w, bx, BLIND.faceT,
              sg.x0 + dx * o.at + nx * faceOff, yHead + bx / 2,
              sg.z0 + dz * o.at + nz * faceOff, ry);

          /* a guide down each reveal, to the sill */
          var half = b.w / 2 - BLIND.railW / 2;
          var gx = sg.x0 + dx * o.at + sg.nx * (FT + BLIND.railW / 2);
          var gz = sg.z0 + dz * o.at + sg.nz * (FT + BLIND.railW / 2);
          var k;
          for (k = -1; k <= 1; k += 2) {
            put(matBox, BLIND.railW, o.h, BLIND.railW,
                gx + dx * (half * k), sg.y0 + o.sill + o.h / 2, gz + dz * (half * k), ry);
          }

          /* the curtain: real slats, at the real pitch, a third of the way down */
          var curtain = Math.max(BLIND.slatH, o.h * BLIND.drop);
          var pitchS = BLIND.slatH + BLIND.slatGap;
          var nS = Math.max(1, Math.floor(curtain / pitchS));
          var slatW = b.w - BLIND.railW * 2;
          var sOff = FT + BLIND.proud + BLIND.slatT / 2;
          var sx = sg.x0 + dx * o.at + nx * sOff;
          var sz = sg.z0 + dz * o.at + nz * sOff;
          var sq;
          /* THE CURTAIN ROLLS UP IN THE WALK-THROUGH (core-v630, Marek: at se
             vytahuji i zaluzie pri prochazeni): every slat of one shutter
             shares one arLeaf, so the AR export keeps the curtain as a node
             hung from the head; the walk page draws it up into the case as
             someone comes near. Nothing changes on screen or in AR. */
          var curL = { type: 'lift', hinge: [sx, yHead, sz], min: 0.06, ext: true, out: [nx, 0, nz] };
          for (sq = 0; sq < nS; sq++) {
            var slat = put(matSlat, slatW, BLIND.slatH, BLIND.slatT,
                sx, yHead - BLIND.slatH / 2 - sq * pitchS, sz, ry);
            slat.userData.arLeaf = curL;
          }

          byCode[b.code] = (byCode[b.code] || 0) + 1;
          fitted++;
        }
      }

      if (!fitted) {
        ctx.log('BLD  on, but no window or french door takes one' +
                (skipped.length ? (' | nothing fits: ' + skipped.join(', ')) : '') +
                (noneR.length ? (' | none on ' + noneR.join(', ')) : ''));
        return;
      }
      var c, parts = [];
      for (c in byCode) {
        if (!byCode.hasOwnProperty(c)) continue;
        if (!CATALOG[c]) CATALOG[c] = { code: c, unit: 'pcs', label: 'Exterior blinds' };
        _bill[L][c] = byCode[c];
        parts.push(c + ' x' + byCode[c]);
      }

      /* WHAT HOLDS THEM ON. A shutter arrived as one line and nothing else --
         no anchors into the reveal, no foam behind the case. They are not drawn
         and that is exactly why they have to be billed, or the quote is short
         by every fixing in the building (Marek: vse precizne vykazovat).

         The same fixings the joinery already uses for a window in the same
         reveal: the full thread screw at its own spacing, and foam bedding the
         case. Two anchors per guide, four per case. */
      var BLIND_FIX = { anchor: 'FAS75132', perGuide: 2, perCase: 4, foam: 'ODP00001', foamPer: 6 };
      var anch = fitted * (BLIND_FIX.perGuide * 2 + BLIND_FIX.perCase);
      var foam = Math.ceil(fitted / BLIND_FIX.foamPer);
      if (CATALOG[BLIND_FIX.anchor]) _bill[L][BLIND_FIX.anchor] = anch;
      if (CATALOG[BLIND_FIX.foam]) _bill[L][BLIND_FIX.foam] = foam;
      parts.push(BLIND_FIX.anchor + ' x' + anch);
      parts.push(BLIND_FIX.foam + ' x' + foam);
      ctx.log('BLD  ' + fitted + ' curtain(s) roll up in the AR walk-through (arLeaf lift, core-v630)');
      ctx.log('BLD  ' + fitted + ' shutter(s): ' + parts.sort().join(', ') +
              (skipped.length ? (' | NO SHUTTER FITS: ' + skipped.join(', ')) : '') +
              (noneR.length ? (' | none on ' + noneR.join(', ')) : ''));
    }

    function terraceAt(px, pz, margin) {
      var R = _terraceRect();
      if (!R) return null;
      var m = (margin == null) ? 0.30 : margin;
      if (px >= R.x0 - m && px <= R.x1 + m && pz >= R.z0 - m && pz <= R.z1 + m) {
        var t = _terrace();
        return { side: (t && t.side) || 'free', rect: R };
      }
      return null;
    }

    /* the four reaches, kept in range and filled in from the old single eave for
       a saved project that has not got them */
    /* the house line's overhang limits, as they were before core-v426 changed
       them for every product (core-v457, K1): the T house has its own through
       __PROD._roofTails and never read these */
    var OVER_LIM = { min: 0.10, max: 3.00 };
    /* the widest the roof may finish, living space plus both overhangs */
    var ROOF_MAX_W = 9.0;

    /* THE REACH THE PLANES ARE BUILT WITH (core-v443, R1, Marek: natahuji se
       u eave hrany jen krokve, nic jineho se se strechou nedeje). A product
       that sets __PROD._roofTails keeps its planes on the roof's own eave on
       the two EAVE sides, whatever the person dragged there -- the product
       lengthens the rafter tails itself from STATE.roof.over. On the two
       RIDGE-END sides the dragged reach goes into the planes as it always
       did: a gable just gets longer, a hip's end moves out with its shape. */
    /* the datum is the product's own minimum eave, a constant, never the
       project's stored eave (core-v444, Marek: to co je bez tazeni je
       minimalni presah, nemenit umisteni krokvi, jen jejich delku) */
    function _tailsEave() {
      var e = 0.5;
      try { if (__PROD && typeof __PROD._eaveMin === 'number') e = __PROD._eaveMin; } catch (eE) {}
      return e;
    }
    /* THE RAKE MAY COME IN UNDER THE EAVE (core-v496, Marek: strechu ve
       stitech nastavit tak, aby netrcela zadna krokev, schovana ve fasade;
       porad nejde strecha zatahnout az pod obklad). A tails product's floor
       was the eave on all four sides, so a gable end could never be pulled
       in past 500 and its end rafter always stood out of the facade. The
       ridge-end sides now floor at the product's __PROD._rakeMin -- a number
       or a function of the wall, the end rafter's outer face on the finished
       face -- and the eave sides keep the eave. A hipped end is an eave
       (core-v333) and reaches at least as far as the eave sides whatever its
       key holds, so the lower floor cannot pull a hip in. */
    function _tailsRake() {
      var e = _tailsEave();
      try {
        var v = __PROD ? __PROD._rakeMin : null;
        if (typeof v === 'function') v = v();
        if (typeof v === 'number' && isFinite(v) && v >= 0) return Math.min(e, v);
      } catch (eR) {}
      return e;
    }
    /* the bar's axis as the builder reads it: with a wing on, the ridge runs
       along the wing's side of the bar (roofPlanes); else the stored axis */
    function _barAxisRead() {
      var R = STATE.roof, WG = STATE.wing;
      try { if (WG && WG.on && R.type !== 'flat' && R.type !== 'mono') return (WG.side === 'z0' || WG.side === 'z1') ? 'w' : 'l'; } catch (eA) {}
      return R.axis;
    }
    function _rakeKeys(axis) { return (axis === 'l') ? ['z0', 'z1'] : ['x0', 'x1']; }
    function _roofOverForPlanes(over, axis) {
      var R = STATE.roof, tails = false;
      try { tails = !!(__PROD && __PROD._roofTails); } catch (eT) { tails = false; }
      if (!tails || !over) return over;
      /* the builder's own reading of the axis (core-v445): with 'l' the
         EAVES are the x0 / x1 sides and the rakes are z0 / z1 -- v444 had
         them the other way round, so the eave planes still moved */
      var e = _tailsEave(), rk = _tailsRake(), alongL = ((axis || R.axis) === 'l');
      var mx = function (v) { return (typeof v === 'number' && v > rk) ? v : rk; };
      return alongL ? { x0: e, x1: e, z0: mx(over.z0), z1: mx(over.z1) } : { x0: mx(over.x0), x1: mx(over.x1), z0: e, z1: e };
    }
    /* -------------------------------------------------------------------------
       THE ROOF DRAGGED (core-v502, step 6 of the T house hand-over, Marek: i
       tazeni strechy). A tails product keeps its planes on the eave and runs
       only the timber out; what that takes is here now, next to the reach
       the planes are built with:
         roofTails.extraOf(pn, PLr)   how far past the eave a plane was
                                      dragged, read for its side (the bar's
                                      side under the wing never reaches; a
                                      bar piece past the wing reads the
                                      side's second reach)
         roofTails.extend(pn, reach)  the plane with its eave pushed out by
                                      that reach -- or, with a valley on the
                                      eave, the plane as it was and a strip
         roofTails.hipRule()          a hip end only where its eaves are not
                                      dragged; returns the note for the log
       Moved out of configurator-thouse-lib.js word for word (thouse-v223,
       v229, v231, v235, v244). A product with its own roof frame calls them;
       the rest of the line has no tails and never reaches them.
       ------------------------------------------------------------------------- */
    /* HOW FAR PAST THE CORE'S EAVE A PLANE WAS DRAGGED (thouse-v223, from
       _buildRoofFrame, unchanged): the frame runs its rafter tails by it and
       the skin now extends its planes by it. */
    function tailsExtraOf(pn, PLr) {
      try {
        var e = _tailsEave(), OV = pn.wing ? ((STATE.wing || {}).over) : ((STATE.roof || {}).over);
        if (!OV || !pn.eave || !pn.pts) return 0;
        var cx = 0, cz = 0, q;
        for (q = 0; q < pn.pts.length; q++) { cx += pn.pts[q][0]; cz += pn.pts[q][2]; }
        cx /= pn.pts.length; cz /= pn.pts.length;
        var e0 = pn.eave[0], e1 = pn.eave[1], alongX = Math.abs(e1[0] - e0[0]) >= Math.abs(e1[2] - e0[2]), side;
        if (alongX) side = (e0[2] < cz) ? 'z0' : 'z1'; else side = (e0[0] < cx) ? 'x0' : 'x1';
        /* THE BAR'S EAVE ON THE WING'S SIDE NEVER REACHES (thouse-v197, Marek:
           na vnitrni strane nebude moci byt prodlouzena): its pieces all
           carry the whole eave, so a reach there built four beams over
           each other; that side stays the plain eave */
        if (!pn.wing && STATE.wing && STATE.wing.on && STATE.wing.side === side) return 0;
        /* a bar piece beyond the wing reads the side's second reach
           (thouse-v190): the pieces on the wing's side are told apart by
           where their eave lies against the wing's planes */
        if (!pn.wing && pn.piece && STATE.wing && STATE.wing.side === side && typeof OV[side + 'b'] === 'number') {
          var wx0 = 1e9, wx1 = -1e9, wz0 = 1e9, wz1 = -1e9, q4, k4;
          for (q4 = 0; q4 < PLr.length; q4++) { if (!PLr[q4] || !PLr[q4].wing || !PLr[q4].pts) continue; for (k4 = 0; k4 < PLr[q4].pts.length; k4++) { wx0 = Math.min(wx0, PLr[q4].pts[k4][0]); wx1 = Math.max(wx1, PLr[q4].pts[k4][0]); wz0 = Math.min(wz0, PLr[q4].pts[k4][2]); wz1 = Math.max(wz1, PLr[q4].pts[k4][2]); } }
          var em = [(e0[0] + e1[0]) / 2, (e0[2] + e1[2]) / 2], after = alongX ? (em[0] > (wx0 + wx1) / 2) : (em[1] > (wz0 + wz1) / 2);
          if (after) side = side + 'b';
        }
        var v = OV[side];
        return (typeof v === 'number' && v > e) ? (v - e) : 0;
      } catch (er) { return 0; }
    }

    /* a plane copied with its eave pushed out by a plan reach (thouse-v223).
       The eave vertices are the lowest ones; each moves along its side edge
       (to the neighbour that is not on the eave) until it lies the reach
       further out, measured square to the eave in plan. A vertex between two
       eave vertices -- a clipped piece -- moves straight down the slope. */
    function tailsExtend(pn, reach) {
      var cp = {}, k;
      for (k in pn) if (pn.hasOwnProperty(k)) cp[k] = pn[k];
      if (!(reach > 0.005) || !pn.pts || !pn.eave || !pn.n) return { pl: cp, by: 0 };
      var n = pn.n, hl = Math.hypot(n[0], n[2]);
      if (!(hl > 1e-4) || !(Math.abs(n[1]) > 1e-4)) return { pl: cp, by: 0 };
      var hx = n[0] / hl, hz = n[2] / hl, tan = hl / Math.abs(n[1]);
      var ey = pn.eave[0][1], P = pn.pts, m = P.length, i, out = [];
      var onE = function (v) { return Math.abs(v[1] - ey) < 0.005; };
      /* the middle of the eave, along it: a corner that would slide TOWARDS
         it is on a valley, not a hip (thouse-v229, Marek: na prodlouzene eave
         hrane side run neni okap az k main run, chybi asi metr). Down a hip
         the corner moves away from the middle and the eave grows; down the
         wing's valley it moved a reach back along the eave and the run
         stopped short of the bar. Such a corner goes straight down the slope,
         so the eave keeps its end at the bar's eave line. */
      var ux9 = -hz, uz9 = hx, tMid = 0, nE = 0, iE;
      for (iE = 0; iE < pn.pts.length; iE++) if (Math.abs(pn.pts[iE][1] - pn.eave[0][1]) < 0.005) { tMid += pn.pts[iE][0] * ux9 + pn.pts[iE][2] * uz9; nE++; }
      tMid = nE ? tMid / nE : 0;
      var valleyAt = -1, isValley = false;
      var push = function (v, nb) {
        isValley = false;
        if (nb) {
          var dx = v[0] - nb[0], dy = v[1] - nb[1], dz = v[2] - nb[2], along = dx * hx + dz * hz;
          if (along > 1e-4) {
            var t = reach / along, mvU = (dx * ux9 + dz * uz9) * t, inward = (tMid - (v[0] * ux9 + v[2] * uz9));
            if (!(mvU * inward > 0.01 * Math.abs(inward || 1))) return [v[0] + dx * t, v[1] + dy * t, v[2] + dz * t];
            isValley = true;
          }
        }
        return [v[0] + hx * reach, v[1] - tan * reach, v[2] + hz * reach];
      };
      for (i = 0; i < m; i++) {
        var v = P[i];
        if (!onE(v)) { out.push(v.slice()); continue; }
        var a = P[(i - 1 + m) % m], b = P[(i + 1) % m], nb = null;
        if (!onE(a) && onE(b)) nb = a; else if (onE(a) && !onE(b)) nb = b;
        out.push(push(v, nb));
        if (isValley) valleyAt = i;
      }
      /* A PLANE WITH A VALLEY AT ITS EAVE IS NOT STRETCHED BUT GIVEN A STRIP
         (thouse-v235, Marek: protazena strecha na eave hrane side run
         generuje vrstvy strechy vevnitr domu). Stretched as one plane, its
         side ran from the new eave corner straight up to the valley's top,
         across the bar's roof and down into the house -- the covering is
         laid between an eave line and a ridge line, so it cannot follow a
         side that bends. The plane now stays as the core built it, and the
         dragged reach is a strip of its own in the same plane: from the old
         eave down to the new one, its outer end on the rake, its inner end
         square to the eave at the valley's foot, which is the bar's eave
         line. The strip's top is the old eave, so its covering laps up under
         the plane's first course; the old eave gets no drip edge (edgePl). */
      if (valleyAt >= 0) {
        var oi = -1;
        for (i = 0; i < m; i++) if (i !== valleyAt && onE(P[i])) { oi = i; }
        if (oi >= 0) {
          var main = {}, st = {}, ed = {}, kk;
          for (kk in pn) if (pn.hasOwnProperty(kk)) { main[kk] = pn[kk]; st[kk] = pn[kk]; ed[kk] = pn[kk]; }
          main._split = true;
          var vO = P[oi], vV = P[valleyAt], nO = out[oi], nV = out[valleyAt];
          st.id = String(pn.id) + 's'; st._strip = true; st.kind = 'slope';
          st.pts = [vO.slice(), vV.slice(), nV.slice(), nO.slice()];
          st.eave = [nO.slice(), nV.slice()];
          st.ridge = [vO.slice(), vV.slice()];
          var sL = Math.hypot(vV[0] - vO[0], vV[2] - vO[2]), sW = Math.hypot(nO[0] - vO[0], nO[1] - vO[1], nO[2] - vO[2]);
          st.area = sL * sW;
          ed.eave = [vV.slice(), vV.slice()]; ed._split = true;
          return { pl: main, by: reach, strip: st, edgePl: ed };
        }
      }
      cp.pts = out;
      /* the eave line: its ends move with the vertices they sit on */
      var eOut = [], j;
      for (j = 0; j < 2; j++) {
        var ev = pn.eave[j], hit = -1;
        for (i = 0; i < m; i++) if (Math.abs(P[i][0] - ev[0]) < 0.002 && Math.abs(P[i][1] - ev[1]) < 0.002 && Math.abs(P[i][2] - ev[2]) < 0.002) { hit = i; break; }
        eOut.push(hit >= 0 ? out[hit].slice() : push(ev, null));
      }
      cp.eave = eOut;
      var ax = 0, ay = 0, az = 0;
      for (i = 0; i < m; i++) {
        var p1 = out[i], p2 = out[(i + 1) % m];
        ax += p1[1] * p2[2] - p1[2] * p2[1]; ay += p1[2] * p2[0] - p1[0] * p2[2]; az += p1[0] * p2[1] - p1[1] * p2[0];
      }
      cp.area = Math.hypot(ax, ay, az) / 2;
      return { pl: cp, by: reach };
    }
    /* A HIP END ONLY WHERE ITS EAVES ARE NOT DRAGGED (thouse-v231, Marek:
       valbu budeme aplikovat na dany stit jen jestli nebude vytazena eave
       hrana). A dragged eave lengthens its rafters and so comes down lower
       than the core's eave; the hip end next to it stays on the core's eave,
       and the two could never meet at the corner. So while either eave side
       of a roof is dragged, that roof's hip ends are turned into gables, and
       they come back as hips when the eaves are back on the minimum. What the
       person chose is remembered in roof._hipForced, so only the ends this
       rule turned are turned back. */
    function tailsHipRule() {
      var _hipNote = '';
      /* a drag under 50 mm is not a drag (thouse-v244): a stored reach a hair
         over the minimum no longer turns a hip into a gable */
      var R = STATE.roof, WG = STATE.wing, e = _tailsEave() + 0.05, said = [];
      if (!R || R.type !== 'hip' || !R.hipEnds) { return ''; }
      R._hipForced = R._hipForced || {};
      var dragged = function (O, keys) { var q, k; if (!O) return false; for (q = 0; q < keys.length; q++) { k = keys[q]; if ((typeof O[k] === 'number' && O[k] > e) || (typeof O[k + 'b'] === 'number' && O[k + 'b'] > e)) return true; } return false; };
      /* the bar's eaves read the way the core builds the bar (thouse-v233):
         with the wing on a z side the bar's ridge runs along x and its eaves
         are z0 / z1 whatever the stored roof.axis says -- read from roof.axis
         they came out as the ENDS, so dragging a hip end turned it into a
         gable (Marek: valby ted nefunguji) */
      var wingOnH = !!(WG && WG.on), sideZH = wingOnH && (WG.side === 'z0' || WG.side === 'z1');
      var barEaves = wingOnH ? (sideZH ? ['z0', 'z1'] : ['x0', 'x1']) : ((R.axis === 'l') ? ['x0', 'x1'] : ['z0', 'z1']);
      var wingEaves = (WG && (WG.side === 'z0' || WG.side === 'z1')) ? ['x0', 'x1'] : ['z0', 'z1'];
      var barD = dragged(R.over, barEaves), wingD = !!(WG && WG.on) && dragged(WG.over, wingEaves);
      var ends = [['lo', barD], ['hi', barD], ['wing', wingD]], q;
      for (q = 0; q < ends.length; q++) {
        var k = ends[q][0], drag = ends[q][1];
        if (drag && R.hipEnds[k]) { R.hipEnds[k] = false; R._hipForced[k] = true; said.push(k + ' hip -> gable'); }
        else if (!drag && R._hipForced[k]) { R.hipEnds[k] = true; delete R._hipForced[k]; said.push(k + ' gable -> hip again'); }
      }
      var held = [];
      for (q in R._hipForced) if (R._hipForced.hasOwnProperty(q)) held.push(q);
      var ovs = function (O, ks) { return ks.map(function (k) { return k + ' ' + Math.round(((O || {})[k] || 0) * 1000); }).join(' '); };
      _hipNote = 'type ' + R.type + ', chosen lo ' + !!R.hipEnds.lo + ' hi ' + !!R.hipEnds.hi + ' wing ' + !!R.hipEnds.wing + ' | bar eaves ' + ovs(R.over, barEaves) + ', wing eaves ' + ovs(WG && WG.over, wingEaves) + ' | hip ends: ' + (held.length ? (held.join(', ') + ' held as gable while ' + (barD ? 'the bar' : '') + (barD && wingD ? ' and ' : '') + (wingD ? 'the wing' : '') + ' eave is dragged') : 'as chosen') + (said.length ? (' (' + said.join(', ') + ')') : '');
      return _hipNote;
    }
    var roofTails = { extraOf: tailsExtraOf, extend: tailsExtend, hipRule: tailsHipRule };

    function _roofOver() {
      var R = STATE.roof;
      /* a tails product: the second-stretch keys and the bar's side under the
         wing are held on the eave (core-v453) */
      try { if (__PROD && __PROD._roofTails && R.over) { var kb; for (kb in R.over) if (R.over.hasOwnProperty(kb) && /b$/.test(kb)) R.over[kb] = clamp(R.over[kb], _tailsEave(), 5.0); } } catch (eKB) {}
      try { if (__PROD && __PROD._roofTails && STATE.wing && STATE.wing.on && R.over) { var sdW = STATE.wing.side; R.over[sdW] = _tailsEave(); delete R.over[sdW + 'b']; } } catch (eWS2) {}
      /* a tails product never reaches under its eave (core-v443) */
      try { if (__PROD && __PROD._roofTails) { var eT2 = _tailsEave(), rkT2 = _tailsRake(), rkK2 = _rakeKeys(_barAxisRead()); R.eave = eT2; if (!R.over) R.over = { x0: eT2, x1: eT2, z0: eT2, z1: eT2 }; var kT; for (kT in R.over) if (R.over.hasOwnProperty(kT)) { var flT = (rkK2.indexOf(kT) >= 0) ? rkT2 : eT2; if (!(R.over[kT] >= flT)) R.over[kT] = flT; } } } catch (eG2) {}
      if (!R.over) R.over = { x0: R.eave, x1: R.eave, z0: R.eave, z1: R.eave };
      var k = ['x0', 'x1', 'z0', 'z1'], i;
      /* core-v579: OVER A TERRACE THE ROOF MAY COVER THE TERRACE (Marek: u
         uzkych domu to pravidlo zbytecne limituje -- the 263 sqft house could
         not roof its terrace; posts come by themselves past 1.5 m). On the side
         a terrace is attached to, the reach may go as far as the terrace's
         outer edge (+ 5 cm), up to OVER_LIM.max; half the side and the 9 m
         roof stay the rule everywhere else. A flat roof has no overhang. */
      var terCover = {};
      try {
        var Tc = _terrace();
        if (Tc && Tc.on && Tc.side && R.type !== 'flat') {
          var RTc = _terraceRect(), BTc = _buildRect(), sTc = Tc.side, reachTc = 0;
          if (RTc && BTc) {
            if (sTc === 'z1') reachTc = RTc.z1 - BTc.z1; else if (sTc === 'z0') reachTc = BTc.z0 - RTc.z0;
            else if (sTc === 'x1') reachTc = RTc.x1 - BTc.x1; else if (sTc === 'x0') reachTc = BTc.x0 - RTc.x0;
            if (reachTc > 0) terCover[sTc] = Math.min(OVER_LIM.max, Math.round((reachTc + 0.05) * 1000) / 1000);
          }
        }
      } catch (eTc) {}
      for (i = 0; i < 4; i++) {
        /* AND NO SIDE CARRIES MORE THAN HALF ITS OWN LENGTH.

           OVER_LIM was a flat 3 m whatever the building was, so a 4 x 4 m annexe
           could take 3 m on all four sides -- a 10 x 10 roof over 16 square
           metres of house, six times the footprint. Nobody would build that and
           the configurator should not offer it.

           Tied to the side it belongs to: an x-side overhang is bounded by half
           the width, a z-side by half the length. A covered terrace is then never
           deeper than half the house it is attached to, which is the proportion
           that reads as a porch rather than a canopy with a shed under it.

           Normal sizes are untouched -- at 6 x 8 m the half is 3 m and 4 m, so
           the old ceiling still governs (Marek). */
        /* AND THE ROOF ITSELF STOPS AT 9 m ACROSS.

           The living space caps at 8 m wide; what the truss has to span is that
           plus both overhangs, and 9 m total is where an ordinary nail plate
           truss still is one. Ten metres was the other candidate and it tempts a
           metre of eave all the way round, which is expensive roof and no more
           terrace: at 9, a full-width house gets a 500 mm eave and the deep side
           of the terrace goes down the LENGTH, which is where there is room for
           it anyway.

           So each side is bounded three ways -- the flat maximum, half its own
           side, and whatever is left of the 9 m once the house is taken out
           (Marek). */
        var side = (k[i][0] === 'x') ? STATE.footprint.w : STATE.footprint.l;
        var cap = Math.min(OVER_LIM.max, side / 2);
        if (k[i][0] === 'x') {
          cap = Math.min(cap, Math.max(OVER_LIM.min, (ROOF_MAX_W - STATE.footprint.w) / 2));
        }
        if (terCover[k[i]] > cap) cap = terCover[k[i]];
        /* core-v78: AND A PRODUCT MAY SET ITS OWN CAP PER EDGE. The three rules
           above are the house line's; a fork can want one edge to reach much
           further than half its own side -- a roof pulled out over a terrace
           along the ridge does not lengthen a single truss. Asked the same way
           the fitout and cladding hooks are asked, so a product that says
           nothing behaves exactly as before. */
        if (typeof __PROD._productOverCap === 'function') {
          var pcap = __PROD._productOverCap(k[i], side, cap);
          if (pcap != null && pcap > 0) cap = pcap;
        }
        if (typeof R.over[k[i]] !== 'number') R.over[k[i]] = R.eave;
        /* a tails product reaches five metres on any side; the house line's
           half-the-side rule is for roofs that grow with the reach (core-v450,
           Marek: stit se ma natahovat az 5 metru, jde to 3) */
        var loC = OVER_LIM.min;
        try { if (__PROD && __PROD._roofTails) { cap = 5.0; loC = Math.min(OVER_LIM.min, _tailsRake()); } } catch (eC5) {}
        R.over[k[i]] = clamp(R.over[k[i]], loC, cap);
      }
      return R.over;
    }

    /* NOTHING LEAVES THE OUTLINE WHEN THE PLAN IS RESIZED. A partition keeps
       600 mm of room from the shell, an opening keeps 200 mm from each corner,
       and the wall pushes whatever is in its way ahead of it. An opening on a
       wall that has become too short is narrowed rather than dropped. */
    var PART_KEEP = 0.60;
    var OP_KEEP   = 0.20;

    function _confinePlan() {
      var moved = 0, shrunk = 0, i, k;
      var b = _planBounds(), wt = wallThickness();
      var ps = _normParts(), p, lo, hi, c0, e0, e1;
      for (i = 0; i < ps.length; i++) {
        p = ps[i];
        if (p.o === 'v') { lo = b.ax0 + wt + PART_KEEP; hi = b.ax1 - wt - PART_KEEP; e0 = b.az0; e1 = b.az1; }
        else             { lo = b.az0 + wt + PART_KEEP; hi = b.az1 - wt - PART_KEEP; e0 = b.ax0; e1 = b.ax1; }
        if (hi < lo) { lo = hi = (lo + hi) / 2; }
        c0 = p.c;
        p.c = p.at = clamp(p.c, lo, hi);
        if (Math.abs(p.c - c0) > 1e-4) moved++;
        if (typeof p.a === 'number') p.a = clamp(p.a, e0, e1);
        if (typeof p.b === 'number') p.b = clamp(p.b, e0, e1);
        if (typeof p.s === 'number') p.s = clamp(p.s, e0, e1);
        if (typeof p.e === 'number') p.e = clamp(p.e, e0, e1);
      }

      var ops = STATE.openings || [], o, sg, room, at0;
      for (k = 0; k < ops.length; k++) {
        o = ops[k];
        if (!o) continue;
        sg = segment(o.seg);
        if (!sg) continue;
        /* A FULL-WALL UNIT FOLLOWS ITS WALL (core-v531, Marek: full wall
           window se mumifikovalo pri ruznych editacnich krocich). It was
           sized once, at placement, and every resize of the plan only ever
           cut it down -- narrower on a shorter wall, never wider again on a
           longer one. It is sized to the wall it stands on afresh here, the
           same way it was placed. */
        if (o.fullW || o.halfW) {
          var w0f = o.w, h0f = o.h;
          o.full = o.halfW ? 'half' : true;
          _sizeToWall(sg, o);
          if (Math.abs(w0f - o.w) > 1e-4 || Math.abs(h0f - o.h) > 1e-4) moved++;
          continue;
        }
        room = sg.len - OP_KEEP * 2;
        if (o.w > room) { o.w = Math.max(0.30, room); shrunk++; }
        at0 = o.at;
        o.at = clamp(o.at, OP_KEEP + o.w / 2, Math.max(OP_KEEP + o.w / 2, sg.len - OP_KEEP - o.w / 2));
        if (Math.abs(o.at - at0) > 1e-4) moved++;
        /* AND THE HEAD IS HELD UNDER THE LINE THE WALL ACTUALLY HAS (core-v531,
           Marek: kulate okno vyrazne zmenselo svuj prumer). This cut every
           unit whose head stood over the storey's level height -- which on a
           gable wall is every unit in the gable, since thouse-v295 -- down to
           300 mm. The window-under-the-plate rule knows the product's line
           (the rafters on a gable) and moves a unit down before it ever
           shortens it. */
        var h0c = o.h, chC = _snapUnderPlate(o);
        if (chC && Math.abs(h0c - o.h) > 0.0005) shrunk++;
      }
      /* AND THE ROOM NAMES COME IN WITH THE WALLS.

         v222 taught a PARTITION to push the pins ahead of it. The shell was still
         shrinking straight over them, and a pin left outside the outline is
         dropped on the next paint -- so pulling the building in by a metre threw
         away the names of every room along that edge, and the anchoring then
         handed those rooms whatever the list order gave them. Resizing the plan
         is the biggest change there is, which is why this is the one that kept
         showing.

         A pin outside is brought back to just inside the lining it went through.
         It keeps its room in every case that has a room left to keep. */
      var pins = STATE.roomNames, pulled = 0;
      if (Array.isArray(pins)) {
        var px0 = b.ax0 + wt + 0.10, px1 = b.ax1 - wt - 0.10;
        var pz0 = b.az0 + wt + 0.10, pz1 = b.az1 - wt - 0.10;
        if (px1 > px0 && pz1 > pz0) {
          for (i = 0; i < pins.length; i++) {
            var pn = pins[i];
            if (!pn || typeof pn.x !== 'number' || typeof pn.z !== 'number') continue;
            var nx = clamp(pn.x, px0, px1), nz = clamp(pn.z, pz0, pz1);
            if (Math.abs(nx - pn.x) > 1e-4 || Math.abs(nz - pn.z) > 1e-4) {
              pn.x = nx; pn.z = nz; pulled++;
            }
          }
        }
      }

      if (moved || shrunk || pulled) {
        _log('plan resized: ' + moved + ' pushed back inside, ' + shrunk +
             ' narrowed to fit, ' + pulled + ' room name(s) pulled in with the wall');
      }
      return moved + shrunk + pulled;
    }

    function _prodRects(step) {
      var o = [];
      try { if (__PROD && typeof __PROD._rects === 'function') o = __PROD._rects(step) || []; } catch (eP) { o = []; _log('rects    product rects threw: ' + (eP && eP.message)); }
      return o.filter(function (q) { return q && typeof q.key === 'string' && q.key.indexOf('p:') === 0; });
    }
    var _rectLastKey = '';
    /* a grip moved (core-v617): on the plan the plan only, in 3D the roof's
       grips build the roof set, any other a light whole house */
    function _rectRebuild(key) {
      var set = (key === 'roofover' || key === 'wingover') ? 'roof' : null;
      /* core-v623 (RANCH: a cross gable dragged built the whole house light
         on every move -- under 1 fps on the tablet): a product rect's drag
         builds only the layers __PROD._rectDragSet(key) names (suffixes,
         e.g. ['xgable']); the whole house on release as before */
      if (!set && typeof key === 'string' && key.indexOf('p:') === 0 && typeof __PROD._rectDragSet === 'function') {
        try { var ps = __PROD._rectDragSet(key); if (ps && ps.length) set = ps; } catch (eDS) {}
      }
      _dragMove('the ' + key + ' grip', set);
    }
    function _applyRect(key, r, k) {
      _rectLastKey = key;
      if (typeof key === 'string' && key.indexOf('p:') === 0) {
        try { if (__PROD && typeof __PROD._rectApply === 'function') __PROD._rectApply(key, r, k); } catch (eA) { _log('rects    product apply threw: ' + (eA && eA.message)); }
        _rectRebuild(key);
        return;
      }
      /* the ghost stood _ghostOff out: the object's own edges (core-v599) */
      if (key === 'pool' || key === 'pergola' || key === 'terrace') r = _ghostBack(key, r);
      if (key === 'pool') {
        var Q = _pool(), cur2 = _poolRect() || { x0: 0, x1: 0, z0: 0, z1: 0 };
        var px0 = (r.x0 != null) ? r.x0 : cur2.x0, px1 = (r.x1 != null) ? r.x1 : cur2.x1;
        var pz0 = (r.z0 != null) ? r.z0 : cur2.z0, pz1 = (r.z1 != null) ? r.z1 : cur2.z1;
        Q.w = Math.round(clamp(px1 - px0, POOL.minW, POOL.maxW) * 100) / 100;
        Q.l = Math.round(clamp(pz1 - pz0, POOL.minL, POOL.maxL) * 100) / 100;
        /* the edge that is not being pulled stays where it is: pulling the near
           edge keeps the far one, a move carries both */
        Q.x0 = Math.round(((r.x0 != null && r.x1 == null) ? (px1 - Q.w) : px0) * 100) / 100;
        Q.z0 = Math.round(((r.z0 != null && r.z1 == null) ? (pz1 - Q.l) : pz0) * 100) / 100;
        _rectRebuild(key);
        return;
      }
      /* core-v597: the pergola keeps a rectangle of its own from its first move */
      if (key === 'pergola') {
        var Tg = _terrace(), cg = _pergolaRect() || { x0: 0, x1: 0, z0: 0, z1: 0 };
        var gx0 = (r.x0 != null) ? r.x0 : cg.x0, gx1 = (r.x1 != null) ? r.x1 : cg.x1;
        var gz0 = (r.z0 != null) ? r.z0 : cg.z0, gz1 = (r.z1 != null) ? r.z1 : cg.z1;
        var gsx = Math.round(clamp(gx1 - gx0, PERG_LIM.min, PERG_LIM.max) * 100) / 100;
        var gsz = Math.round(clamp(gz1 - gz0, PERG_LIM.min, PERG_LIM.max) * 100) / 100;
        Tg.pg = { x0: Math.round(((r.x0 != null && r.x1 == null) ? (gx1 - gsx) : gx0) * 100) / 100,
                  z0: Math.round(((r.z0 != null && r.z1 == null) ? (gz1 - gsz) : gz0) * 100) / 100, sx: gsx, sz: gsz };
        _rectRebuild(key);
        return;
      }
      if (key === 'terrace') {
        /* the side on the house stays on the house; the other three edges and
           a move set w, d and the offset along the wall */
        /* free on the site: the pulled edges move, the others stay; then it is
           kept off the house and snapped to a wall it comes close to */
        var T = _terrace();
        var cur = _terraceRect() || { x0: 0, x1: 0, z0: 0, z1: 0 };
        var x0 = (r.x0 != null) ? r.x0 : cur.x0, x1 = (r.x1 != null) ? r.x1 : cur.x1;
        var z0 = (r.z0 != null) ? r.z0 : cur.z0, z1 = (r.z1 != null) ? r.z1 : cur.z1;
        T.sx = Math.round(clamp(x1 - x0, TERR_LIM.minW, TERR_LIM.max) * 100) / 100;
        T.sz = Math.round(clamp(z1 - z0, TERR_LIM.minD, TERR_LIM.max) * 100) / 100;
        T.x0 = Math.round(((r.x0 != null && r.x1 == null) ? (x1 - T.sx) : x0) * 100) / 100;
        T.z0 = Math.round(((r.z0 != null && r.z1 == null) ? (z1 - T.sz) : z0) * 100) / 100;
        _terraceSnapToTiles();
        _terraceSettle();
        _rectRebuild(key);
        return;
      }
      if (key === 'roofover') {
        /* AN OVERHANG IS THE GAP BETWEEN A ROOF EDGE AND THE WALL IT COVERS, so
           the dragged rect is turned back into four gaps rather than stored as a
           rectangle of its own. The building is the datum; the roof reaches. */
        /* THE DATUM IS THE BAR, THE SAME RECT THE GHOST IS DRAWN FROM
           (core-v451, Marek: porad nejde natahnout eave hrana hlavniho runu na
           strane k side runu). The apply measured the reach from the whole
           building's bounds, which on the wing's side lie a wing's length
           further out, so the gap came out negative and was clamped back to
           the eave. On every other side the two rects are the same. */
        var B3 = _barRect(), O = _roofOver(), tailsP = false;
        try { tailsP = !!(__PROD && __PROD._roofTails); } catch (eTP) { tailsP = false; }
        /* on a tails product the roof only reaches further out, never in:
           the floor of every side is the roof's own eave (core-v443) */
        /* five metres on a tails product (core-v447, T1, Marek: stitova strana
           ma jit vytahnout aspon 5 metru na kazdem runu) */
        var loLim = tailsP ? _tailsEave() : OVER_LIM.min, hiLim = tailsP ? 5.0 : OVER_LIM.max;
        /* the ridge-end sides floor at the rake (core-v496) */
        var rkKd = tailsP ? _rakeKeys(_barAxisRead()) : [], rkLo = tailsP ? _tailsRake() : loLim;
        var loOf = function (k) { return (rkKd.indexOf(k) >= 0) ? rkLo : loLim; };
        if (r.x0 != null) O.x0 = clamp(B3.x0 - r.x0, loOf('x0'), hiLim);
        if (r.x1 != null) O.x1 = clamp(r.x1 - B3.x1, loOf('x1'), hiLim);
        if (r.z0 != null) O.z0 = clamp(B3.z0 - r.z0, loOf('z0'), hiLim);
        if (r.z1 != null) O.z1 = clamp(r.z1 - B3.z1, loOf('z1'), hiLim);
        /* the single eave follows the smallest of them, so everything still
           reading STATE.roof.eave sees the tightest edge rather than a stale one
           -- except on a tails product, where the eave is the datum */
        /* A DRAGGED HIP END STANDS ON POSTS, SO IT STARTS AT 800 (core-v446,
           S3, Marek: valba se musi opirat o nosny profil, tazeni valby
           minimalne 800 od steny): on a hip roof a ridge-end side that is
           dragged at all goes straight to 800; untouched it stays the eave */
        if (tailsP && STATE.roof.type === 'hip') {
          var eH = _tailsEave(), rakeKeys = (STATE.roof.axis === 'l') ? ['z0', 'z1'] : ['x0', 'x1'], qh;
          for (qh = 0; qh < rakeKeys.length; qh++) if (O[rakeKeys[qh]] > eH + 0.001 && O[rakeKeys[qh]] < 0.8) O[rakeKeys[qh]] = 0.8;
        }
        if (!tailsP) STATE.roof.eave = Math.min(O.x0, O.x1, O.z0, O.z1);
        else _log('roof     reach -> x0 ' + Math.round(O.x0 * 1000) + ', x1 ' + Math.round(O.x1 * 1000) + ', z0 ' + Math.round(O.z0 * 1000) + ', z1 ' + Math.round(O.z1 * 1000) + ' (eave ' + Math.round(_tailsEave() * 1000) + ' is the datum, planes fixed on the eave sides; the rakes ' + rkKd.join('/') + ' may come in to ' + Math.round(_tailsRake() * 1000) + ')');
        _rectRebuild(key);
        return;
      }
      if (key === 'wingover') {
        /* THE WING'S OWN FOUR REACHES, ON THE TAILS RULES (core-v447, T1,
           Marek: kazdy stit a eave se tahne samostatne): the wing's ghost
           writes the wing's reaches and nothing of the bar's; floor at the
           eave, five metres out, a dragged hip end from 800. */
        var WRa = _wingRect(), WGa = STATE.wing, tailsW = false;
        try { tailsW = !!(__PROD && __PROD._roofTails); } catch (eTW) { tailsW = false; }
        if (!WRa || !WGa) return;
        var eW = tailsW ? _tailsEave() : STATE.roof.eave, loW = tailsW ? eW : OVER_LIM.min, hiW = tailsW ? 5.0 : OVER_LIM.max;
        /* the wing's far end is its rake and floors at the rake (core-v496) */
        var farK = WGa.side, loWk = function (k) { return (tailsW && k === farK) ? _tailsRake() : loW; };
        WGa.over = WGa.over || { x0: eW, x1: eW, z0: eW, z1: eW };
        if (r.x0 != null) WGa.over.x0 = clamp(WRa.x0 - r.x0, loWk('x0'), hiW);
        if (r.x1 != null) WGa.over.x1 = clamp(r.x1 - WRa.x1, loWk('x1'), hiW);
        if (r.z0 != null) WGa.over.z0 = clamp(WRa.z0 - r.z0, loWk('z0'), hiW);
        if (r.z1 != null) WGa.over.z1 = clamp(r.z1 - WRa.z1, loWk('z1'), hiW);
        /* WHICH SIDE OF THE WING TOUCHES THE BAR (core-v450, Marek: stit side
           run se nenatahuje). STATE.wing.side names the BAR's side the wing
           sits on; the wing rect's own side that touches the bar is the
           opposite key -- a wing on the bar's z1 touches with its z0 -- and
           its far end, the gable or hip, is the key of the same name. v447
           had the two swapped: it wiped the far end on every drop. */
        var touchW = ({ x0: 'x1', x1: 'x0', z0: 'z1', z1: 'z0' })[WGa.side], farW = WGa.side;
        if (tailsW && STATE.roof.type === 'hip') {
          if (farW && WGa.over[farW] > eW + 0.001 && WGa.over[farW] < 0.8) WGa.over[farW] = 0.8;
        }
        /* the side that sits on the bar carries no reach at all */
        try { if (touchW) WGa.over[touchW] = tailsW ? eW : 0; } catch (eWS) {}
        _log('roof     wing overhang -> ' + ['x0', 'x1', 'z0', 'z1'].map(function (k) { return k + ' ' + Math.round((WGa.over[k] || 0) * 1000); }).join(', '));
        _rectRebuild(key);
        return;
      }
      if (key === 'wing') {
        var WG = STATE.wing, BW = _barRect(), cur = _wingRect();
        if (!WG || !WG.on || !cur) return;
        var wx0 = (r.x0 != null) ? r.x0 : cur.x0, wx1 = (r.x1 != null) ? r.x1 : cur.x1;
        var wz0 = (r.z0 != null) ? r.z0 : cur.z0, wz1 = (r.z1 != null) ? r.z1 : cur.z1;
        var sideZ = (WG.side === 'z0' || WG.side === 'z1');
        /* the width runs ALONG the wall the wing stands on, the length OUT of
           it, and the edge sitting on the building is never what was dragged --
           whatever the pointer did to it, it goes back on the wall */
        var along = sideZ ? (BW.x1 - BW.x0) : (BW.z1 - BW.z0);
        var wNew = sideZ ? (wx1 - wx0) : (wz1 - wz0);
        var lNew = (WG.side === 'z1') ? (wz1 - BW.z1)
                 : (WG.side === 'z0') ? (BW.z0 - wz0)
                 : (WG.side === 'x1') ? (wx1 - BW.x1)
                 : (BW.x0 - wx0);
        WG.w = Math.round(clamp(wNew, 2.0, along - 0.6) * 100) / 100;
        /* THE DEPTH LIMIT IS THE WHOLE T's (core-v330): from the bar's outer wall
           to the wing's end face, LIMITS.maxL altogether, so the bar and the wing
           share it -- what one takes the other cannot (Marek: 18 m od vnejsi
           zdi main run az po celo side run) */
        WG.l = Math.round(clamp(lNew, 1.5, Math.max(1.5, LIMITS.maxL - STATE.footprint.l)) * 100) / 100;
        var midNew = sideZ ? ((wx0 + wx1) / 2) : ((wz0 + wz1) / 2);
        var cc = sideZ ? ((BW.x0 + BW.x1) / 2) : ((BW.z0 + BW.z1) / 2);
        WG.at = Math.round((midNew - cc) * 100) / 100;
        /* the product's own lattice for the wing too (core-v341): its width and
           length inside the same limits, and its offset so the bar's wall on
           either side of it comes out whole */
        if (__PROD && typeof __PROD._planSnap === 'function') {
          var wSn = function (v, kind, lim) {
            var r2 = null;
            try { r2 = __PROD._planSnap(v, kind, lim); } catch (eW) { r2 = null; }
            return (typeof r2 === 'number' && isFinite(r2)) ? r2 : v;
          };
          WG.w = wSn(WG.w, 'wing-w', { min: 2.0, max: along - 0.6 });
          WG.l = wSn(WG.l, 'wing-l', { min: 1.5, max: Math.max(1.5, LIMITS.maxL - STATE.footprint.l) });
          /* THE SIDE THAT WAS NOT DRAGGED STAYS (core-v342). The width is
             snapped after the pointer, so a centre taken from the pointer's
             rect moved the other side too (Marek: tazeni jedne steny nema mit
             vliv na jine steny). The grip says which side is held: a grip on
             the low edge holds the high one, and the other way round; a grip
             on the wing's end moves neither side. */
          var gW = (k == null) ? -1 : (k | 0);
          var sideLo = sideZ ? cur.x0 : cur.z0, sideHi = sideZ ? cur.x1 : cur.z1;
          var holdHi = (gW === 0 || gW === 3 || gW === 7), holdLo = (gW === 1 || gW === 2 || gW === 5);
          if (!sideZ) { holdHi = (gW === 0 || gW === 1 || gW === 4); holdLo = (gW === 2 || gW === 3 || gW === 6); }
          if (holdHi) WG.at = (sideHi - WG.w / 2) - cc;
          else if (holdLo) WG.at = (sideLo + WG.w / 2) - cc;
          WG.at = wSn(WG.at, 'wing-at', { along: along, w: WG.w });
        }
        /* core-v753: an L's wing stays flush with the bar's end -- its offset
           follows its width, the drag only sizes it */
        if (WG.flush === 'lo' || WG.flush === 'hi') {
          var ccF = sideZ ? ((BW.x0 + BW.x1) / 2) : ((BW.z0 + BW.z1) / 2), loF = sideZ ? BW.x0 : BW.z0, hiF = sideZ ? BW.x1 : BW.z1;
          WG.at = Math.round((((WG.flush === 'lo') ? loF + WG.w / 2 : hiF - WG.w / 2) - ccF) * 10000) / 10000;
        }
        _rectRebuild(key);
        return;
      }
      if (key !== 'house') return;
      /* core-v580: the frame stands planOut past the outline (see _rects) */
      var pOa = _planOut() + (+__PROD._handleOffset || 0);
      if (pOa) {
        r = { x0: (r.x0 != null) ? r.x0 + pOa : null, x1: (r.x1 != null) ? r.x1 - pOa : null,
              z0: (r.z0 != null) ? r.z0 + pOa : null, z1: (r.z1 != null) ? r.z1 - pOa : null };
      }
      /* THE CENTRE IS SNAPPED TOO. The size was rounded to the module and the
         centre was not, so a drag that changed nothing still moved the building
         half a step -- and the label, pinned to the outline, twitched while the
         number it carried stood still. Nothing about the plan should move on a
         step that does not change the plan. */
      /* AT THE LIMIT THE BUILDING STOPS, IT DOES NOT SLIDE.

         The intent was right and the test was not. The handles asset sends a
         NORMALISED rect -- prop.x0 is Math.min of the pinned edge and the moving
         one, prop.x1 the Math.max -- so r.x1 > r.x0 is true on every drag there
         has ever been, and the held edge came out as r.x0 every time. Drag the
         west corner outward past 8 m and the pinned edge is the EAST one, but
         the code held the west: the size stopped and the whole plan set off with
         the finger, which is the thing that must not happen.

         r cannot say which edge was pinned, because the normalising threw that
         away. The grip number can: it is the third argument, it is the same
         numbering the asset uses to build the pucks, and each grip already
         names its own pinned side there.

           0 (x0,z0)  1 (x1,z0)  2 (x1,z1)  3 (x0,z1)
           4 mid z0   5 mid x1   6 mid z1   7 mid x0

         Hold a grip that sits on the LOW x edge and the high one is pinned, so
         the centre is measured back from r.x1. Same in z. Nothing else moves. */
      STATE.limitHit = '';
      var gk = (k == null) ? -1 : (k | 0);
      var pinHiX = (gk === 0 || gk === 3 || gk === 7);
      var pinHiZ = (gk === 0 || gk === 1 || gk === 4);

      /* THE PINNED EDGE STAYS WHERE IT IS. ALWAYS.

         The centre was snapped to the grid step, but a size that changes by one
         step moves the centre by HALF a step -- so the rounding threw the centre
         a half step one way or the other, and with it the whole building: drag
         one corner and the other three jumped (Marek: they must not move at
         all). The centre is not a thing to snap. The size snaps; the pinned
         edge is already on the grid from the last drag; the centre follows from
         those two and nothing else -- and when the size does not change, neither
         does the position. Core block, to be carried to the other house libs. */
      /* THE WING STAYS WHERE IT IS WHEN THE BAR IS DRAGGED (core-v342). Its
         offset is counted from the bar's centre, and dragging one end of the
         bar moves the centre -- so the wing slid by half the drag, a wall
         nobody touched (Marek: tazeni jedne steny nema mit vliv na jine
         steny). The offset is corrected by what the centre moved along the
         wall the wing stands on. */
      var oxWas = STATE.origin.x, ozWas = STATE.origin.z;
      if (r.x0 != null && r.x1 != null) {
        var wantW = snapDim(Math.abs(r.x1 - r.x0), 'w', { min: LIMITS.minW, max: LIMITS.maxW });
        var gotW = clamp(wantW, LIMITS.minW, LIMITS.maxW);
        STATE.footprint.w = gotW;
        STATE.origin.x = pinHiX ? (r.x1 - gotW / 2) : (r.x0 + gotW / 2);
        if (Math.abs(gotW - wantW) > 1e-6) STATE.limitHit = 'w';
      }
      if (r.z0 != null && r.z1 != null) {
        var maxLBar = (STATE.wing && STATE.wing.on) ? Math.max(LIMITS.minL, LIMITS.maxL - STATE.wing.l) : LIMITS.maxL;
        var wantL = snapDim(Math.abs(r.z1 - r.z0), 'l', { min: LIMITS.minL, max: maxLBar });
        var gotL = clamp(wantL, LIMITS.minL, maxLBar);
        STATE.footprint.l = gotL;
        STATE.origin.z = pinHiZ ? (r.z1 - gotL / 2) : (r.z0 + gotL / 2);
        if (Math.abs(gotL - wantL) > 1e-6) STATE.limitHit = (STATE.limitHit === 'w') ? 'wl' : 'l';
      }
      if (STATE.limitHit) {
        _log('LIM  grip ' + gk + ' pinned ' + (pinHiX ? 'x1' : 'x0') + '/' +
             (pinHiZ ? 'z1' : 'z0') + ' -> ' + STATE.limitHit +
             ' held at ' + Math.round(STATE.footprint.w * 1000) + ' x ' +
             Math.round(STATE.footprint.l * 1000) +
             ', origin ' + Math.round(STATE.origin.x * 1000) + ',' +
             Math.round(STATE.origin.z * 1000));
      }
      if (STATE.wing && STATE.wing.on) {
        var sZ = (STATE.wing.side === 'z0' || STATE.wing.side === 'z1');
        var dC = sZ ? (STATE.origin.x - oxWas) : (STATE.origin.z - ozWas);
        if (Math.abs(dC) > 1e-6) {
          STATE.wing.at = Math.round(((STATE.wing.at || 0) - dC) * 10000) / 10000;
          _log('wing     held in place while the bar was dragged: offset now ' + Math.round(STATE.wing.at * 1000));
        }
      }
      STATE.polygon = null;
      clampSpan();
      _confinePlan();
      _rectRebuild(key);
    }

    /* OUTSIDE THE PLAN, ON THE LINE BEING MEASURED.

       Both labels used to sit ON the outline -- one on the south edge, one on the
       east -- so the number covered the very wall whose length it was giving, and
       on a small plan it covered the grip as well. A dimension belongs off the
       drawing with the measurement standing clear of it, which is where it goes
       on any drawn plan.

       Each one keeps to the middle of its own edge, so it still reads as
       belonging to that line rather than to the building in general. */
    var DIM_OUT = 1.0;

    /* TWO SETS OF KOTY, AND ONLY ONE OF THEM IS A DRAWING.

       The asset dimensions EVERY edge of the outline, every paint, in the plan's
       own hand -- that is the drawn set and it is the one to keep. These two
       labels belong to the drag grips: they ride the handles, they give the
       overall width and length only, and on the plan they land on top of the
       asset's koty saying the same thing twice.

       So they stand down while the plan is drawn, and stay in 3D -- where the
       handles are the only thing there is to read a size from. */
    function _dims(key) {
      /* IN THE PLAN THE DRAWING HAS ITS OWN DIMENSIONS, so these stay away --
         except the one that stands on its limit, which the plan cannot show as
         red with MAX under it and this can. So in the plan only the maxed
         labels are returned; they land on top of the plan's own (Marek). */
      /* NOT EVEN THE MAXED ONE: a sprite cannot turn with the plan's vertical
         dimensions and it doubled the label instead of recolouring it. The red
         and the MAX belong to the plan's own dimension, in the floorplan asset
         (its maxDims hook, to come); here nothing in the plan. */
      var inPlan = _planWanted();
      if (inPlan) return [];
      if (typeof key === 'string' && key.indexOf('p:') === 0) {
        try { return (__PROD && typeof __PROD._rectDims === 'function') ? (__PROD._rectDims(key) || []) : []; } catch (eD) { return []; }
      }
      if (key === 'pool') {
        var PR2 = _poolRect();
        if (!PR2) return [];
        return [{ text: fmtLen(PR2.x1 - PR2.x0), x: (PR2.x0 + PR2.x1) / 2, z: PR2.z0 - POOL.cope - DIM_OUT },
                { text: fmtLen(PR2.z1 - PR2.z0), x: PR2.x1 + POOL.cope + DIM_OUT, z: (PR2.z0 + PR2.z1) / 2 }];
      }
      if (key === 'pergola') {
        var PG2 = _pergolaRect();
        if (!PG2) return [];
        return [{ text: fmtLen(PG2.x1 - PG2.x0), x: (PG2.x0 + PG2.x1) / 2, z: PG2.z0 - DIM_OUT },
                { text: fmtLen(PG2.z1 - PG2.z0), x: PG2.x1 + DIM_OUT, z: (PG2.z0 + PG2.z1) / 2 }];
      }
      /* THE TERRACE MEASURED LIKE THE POOL (core-v281): its two sides, off the
         near and the right edge (Marek) */
      if (key === 'terrace') {
        var TR2 = _terraceRect();
        if (!TR2) return [];
        return [{ text: fmtLen(TR2.x1 - TR2.x0), x: (TR2.x0 + TR2.x1) / 2, z: TR2.z0 - DIM_OUT },
                { text: fmtLen(TR2.z1 - TR2.z0), x: TR2.x1 + DIM_OUT, z: (TR2.z0 + TR2.z1) / 2 }];
      }
      if (key === 'roofover') {
        var O2 = _roofOver(), B2 = _buildRect();
        var RR2 = { x0: B2.x0 - O2.x0, x1: B2.x1 + O2.x1,
                    z0: B2.z0 - O2.z0, z1: B2.z1 + O2.z1 };
        var mx = (RR2.x0 + RR2.x1) / 2, mz = (RR2.z0 + RR2.z1) / 2;
        /* ONLY WHERE THE REACH IS DIFFERENT. Four numbers all reading the same
           thing is four things to check and nothing learned; the one that has
           been pulled out is the one worth a label. All four equal means the
           roof is even, and one number says that. */
        var base2 = Math.min(O2.x0, O2.x1, O2.z0, O2.z1);
        var even = (O2.x0 === O2.x1 && O2.x1 === O2.z0 && O2.z0 === O2.z1);
        if (even) return [{ text: fmtLen(base2), x: mx, z: RR2.z0 - DIM_OUT }];
        var out2 = [];
        if (O2.z0 > base2) out2.push({ text: fmtLen(O2.z0), x: mx, z: RR2.z0 - DIM_OUT });
        if (O2.z1 > base2) out2.push({ text: fmtLen(O2.z1), x: mx, z: RR2.z1 + DIM_OUT });
        if (O2.x0 > base2) out2.push({ text: fmtLen(O2.x0), x: RR2.x0 - DIM_OUT, z: mz });
        if (O2.x1 > base2) out2.push({ text: fmtLen(O2.x1), x: RR2.x1 + DIM_OUT, z: mz });
        return out2;
      }
      if (key === 'wing') {
        var WRd = _wingRect();
        if (!WRd) return [];
        var wcx = (WRd.x0 + WRd.x1) / 2, wcz = (WRd.z0 + WRd.z1) / 2;
        return [
          { text: fmtLen(WRd.x1 - WRd.x0), x: wcx, z: WRd.z0 - DIM_OUT },
          { text: fmtLen(WRd.z1 - WRd.z0), x: WRd.x1 + DIM_OUT, z: wcz }
        ];
      }
      var r = _barRect();
      var cx = (r.x0 + r.x1) / 2, cz = (r.z0 + r.z1) / 2;
      /* THE DIMENSION THAT HIT ITS LIMIT SAYS SO.

         Stopping the drag is half the answer -- the handle keeps moving under the
         finger and nothing explains why the building does not. The dimension that
         is holding turns red and carries MAX under it, in the same face, so the
         reason is written where the person is already looking (Marek). */
      /* AT THE LIMIT, NOT ONLY PUSHING PAST IT. The flag came from the drag
         asking for more than the maximum, so a drag that stopped exactly on
         8000 showed a plain number and nothing said it was the end. The
         number is red with MAX under it whenever the size stands on its limit,
         drag or no drag (Marek). */
      var hit = STATE.limitHit || '';
      var atW = hit.indexOf('w') >= 0 || STATE.footprint.w >= LIMITS.maxW - 1e-6;
      var atL = hit.indexOf('l') >= 0 || STATE.footprint.l >= LIMITS.maxL - 1e-6;

      /* AND THE WORD COMES OUT OF THE STRING.

         MAX was glued onto the number because the asset dropped the flag and the
         message had to arrive somehow. It draws it now -- red number, MAX hung
         underneath in the same face -- so leaving it in the text as well would
         print it twice, once inside the pill and once below it. The flag is the
         whole message again. */
      var labs = [
        { text: fmtLen(STATE.footprint.w),
          x: cx, z: Math.min(r.z0, r.z1) - DIM_OUT, max: atW },
        { text: fmtLen(STATE.footprint.l),
          x: Math.max(r.x0, r.x1) + DIM_OUT, z: cz, max: atL }
      ];
      return labs;
    }

    function _initHandles(activeFn) {
      var U = _UI();
      if (!U || !U.handles) { _log('ConfiguratorUI.handles missing'); return; }
      _handles = U.handles({
        id: 'house', y: typeof __PROD._handleY==='function'?__PROD._handleY():0.04,
        planOverlay: __PROD._handleOverlay ? function(){return _planWanted()&&!_stripPlanLocked();} : null,
        /* NO FILL: the tinted plane under the pucks lay over the rooms and
           changed their colours; ui-v105 draws the outline and the pucks only
           when this is false (older UI ignores it) */
        fill: false,
        /* no held labels: the plan draws its own red MAX (floorplan v58), and a
           label held between drags showed up in the 3D view as well (Marek) */
        active: activeFn || function () { return true; },
        rects: _rects,
        apply: _applyRect,
        dims: _dims,
        /* THE DIMENSION IS THE THING BEING DRAGGED, so it has to stay readable
           wherever the camera is. As a fraction of the viewport height: at 0.05
           it is about 30 px tall on a phone and the same on a desktop, which is
           a label rather than the speck a world-sized one becomes on a building
           this size. Needs ui-v63 or newer; older ones ignore it and behave as
           they always did. */
        /* THE SHEET'S OWN SIZE: a pill 22 px tall, which on a 600 px canvas is
           this fraction. I had it at 26, which is not much on its own and is
           enough to stop it looking like the drawing it came from. */
        /* THE SAME SIZE AS THE ONE ON THE WINDOW. That kot is 22 px tall, and
           these were 27 -- near enough to look like a mistake rather than a
           choice, and big enough to sit on the building. One number for both. */
        dimScreen: 22 / 728,

        /* A TAP ON A PRODUCT'S RECT (core-v583, RANCH: the cross gables'
           on/off icon). The press that never moved goes to __PROD._rectTap(key,
           grip, point) -- point is where it landed, world x z at the rect's
           height and the screen cx cy; an answer of true is a change: saved,
           built, one Undo step. Nothing else hears taps, as before. */
        onTap: function (key, k, pt) {
          if (typeof key !== 'string' || key.indexOf('p:') !== 0 || typeof __PROD._rectTap !== 'function') return;
          var ch = false;
          try { ch = !!__PROD._rectTap(key, k, pt || null); } catch (eRT) { _log('rects    product tap threw: ' + (eRT && eRT.message)); }
          if (ch) { savePref(); rebuild(); undoPush(); }
        },
        onCommit: function () {
          if (_dragStop('the grip let go')) { rebuild(); if (_planWanted()) drawPlan(); }
          savePref();
          /* AND THE PRODUCT HEARS ABOUT IT (core-v311). A drag is a change like a
             tap on a tile, so a product that keeps an undo history has to be told
             when one has finished -- otherwise the one change made with the
             finger is the one change that cannot be taken back. */
          try { if (typeof __PROD.onCommit === 'function') __PROD.onCommit(_rectLastKey); } catch (eOC) {}
          /* and it goes into the history, in every product (core-v316) */
          undoPush();
          if (_rectLastKey === 'pool') { _poolLinkFor(5000); return; }
          if (_rectLastKey === 'pergola') { var PGc = _pergolaRect(); if (PGc) _log('pergola -> ' + fmtLen(PGc.x1 - PGc.x0) + ' x ' + fmtLen(PGc.z1 - PGc.z0) + ' at ' + PGc.x0.toFixed(2) + ', ' + PGc.z0.toFixed(2) + ' (core-v597)'); return; }
          if (_rectLastKey === 'wing' && STATE.wing) {
            _log('wing -> ' + fmtLen(STATE.wing.w) + ' x ' + fmtLen(STATE.wing.l) + ' on ' + STATE.wing.side +
                 ', ' + fmtLen(STATE.wing.at || 0) + ' off centre');
            return;
          }
          _log('footprint -> ' + fmtLen(STATE.footprint.w) + ' x ' + fmtLen(STATE.footprint.l));
        }
      });
      if (_handles) _handles.bind();
    }

    /* -------------------------------------------------------------------------
       PITCH SLIDER
       ------------------------------------------------------------------------- */
    var _pitchSl = null;
    function _initPitch() {
      var U = _UI();
      if (!U || !U.slider || _pitchSl) return _pitchSl;
      _pitchSl = U.slider({
        id: 'cf-house-pitch', bottom: 44, width: 190,
        min: pitchMin(), max: pitchMax(), step: 1, value: STATE.roof.pitch,
        onInput: function (v) {
          STATE.roof.pitch = clamp(v, pitchMin(), pitchMax());
          _pitchLabel();
          _dragMove('the pitch slider', 'roof');
        },
        onChange: function () { if (_dragStop('the pitch slider let go')) rebuild(); savePref(); _log('pitch -> ' + STATE.roof.pitch + ' deg'); }
      });

      /* THE NUMBER RIDES ON THE THUMB. A readout parked in the middle of the
         track makes you look away from what you are dragging to read what you
         dragged it to; over the thumb it is already where your eye is.

         Just the figure, no word in front of it: the slider is only ever shown
         for the pitch, so a label saying so is a word to read every time for
         nothing. */
      try {
        var lab = document.createElement('div');
        lab.id = 'cf-house-pitch-val';
        lab.style.cssText =
          'position:absolute;top:100%;transform:translateX(-50%);' +
          'margin-top:4px;padding:1px 7px;border-radius:6px;white-space:nowrap;' +
          'background:rgba(255,255,255,0.96);box-shadow:0 1px 4px rgba(0,0,0,0.12);' +
          'font:700 12px/1.35 Montserrat,sans-serif;color:#1A1C1D;pointer-events:none;';
        if (_pitchSl.el.style.position !== 'absolute') _pitchSl.el.style.position = 'absolute';
        _pitchSl.el.appendChild(lab);
      } catch (e) {}
      _pitchLabel();
      return _pitchSl;
    }

    function _pitchLabel() {
      var el = $('cf-house-pitch-val');
      if (!el) return;
      el.textContent = Math.round(STATE.roof.pitch) + '\u00B0';

      /* WHERE THE THUMB ACTUALLY IS. A range input puts its thumb at the fraction
         of the track, but inset by half a thumb at each end -- so a readout placed
         at the raw percentage drifts off it towards the extremes. The thumb's own
         width is taken out of the travel, which is the same arithmetic the
         browser does. */
      try {
        var inp = _pitchSl.input;
        if (!inp) return;
        var lo = parseFloat(inp.min) || 0, hi = parseFloat(inp.max) || 0;
        var v = parseFloat(inp.value);
        if (isNaN(v)) v = STATE.roof.pitch;
        var f = (hi > lo) ? ((v - lo) / (hi - lo)) : 0;
        var w = inp.clientWidth || _pitchSl.el.clientWidth || 0;
        var thumb = 14;
        el.style.left = (thumb / 2 + f * Math.max(0, w - thumb)) + 'px';
      } catch (e2) {}
    }

    /* THE EXPLODE SLIDER, on the Assembly step: 0 assembled, 1 the default
       spread, 2 twice; the same kit slider the roof pitch uses */
    var _explodeSl = null, _explodeGuard = false;
    function _explodeSliderShow(v) {
      /* the slider follows the animation: set with the guard up so its own
         input handler does not snap the model to the value */
      if (!_explodeSl) return;
      _explodeGuard = true;
      try { _explodeSl.set({ value: Math.round(v * 20) / 20 }); } catch (e) {}
      _explodeGuard = false;
    }
    function _initExplode() {
      var U = _UI(); if (!U || !U.slider || _explodeSl) return;
      _explodeSl = U.slider({
        id: 'cf-house-explode', bottom: 216, width: 190,
        min: 0, max: 4, step: 0.05, value: 2,
        onInput: function (v) {
          if (_explodeGuard) return;   /* our own set(), not the user's hand */
          API.setExplode(v);
          /* the section cut is for the assembled building: off while exploded */
          try { if (_cutTool && _cutTool.on() && API.explode() > 0.02) _cutTool.set({ on: false }); } catch (e) {}
        },
        onChange: function () {
          _log('explode -> ' + API.explode());
          try { if (API.explode() <= 0.02 && _asm.on && _cutTool && !_cutTool.on()) { _cutTool.set({ axis: 'x', t: 0.5, on: true }); _cutTool.set({ plane: false }); } } catch (e) {}
        }
      });
      _explodeSl.hide();
    }
    function showExplode(on) {
      if (!_explodeSl) _initExplode();
      if (!_explodeSl) return;
      if (on) {
        _explodeGuard = true;
        try { _explodeSl.set({ min: 0, max: 4, value: API.explode() || 0 }); } catch (e) {}
        _explodeGuard = false;
        _explodeSl.show();
      } else {
        _explodeSl.hide();
      }
    }
    /* THE WALL HEIGHT: the clear height of the ground floor, on the Roof and
       Base step above the pitch slider -- 2.40 to 3.20 m in 50 mm steps (a
       sheet is cut to length either way); the label is the catalogue's */
    var _heightSl = null;
    function _initHeight() {
      var U = _UI();
      if (!U || !U.slider || _heightSl) return _heightSl;
      _heightSl = U.slider({
        id: 'cf-house-height', bottom: 100, width: 190,
        min: CLEAR_LIM.min, max: CLEAR_LIM.max, step: _heightStep(), value: STATE.clearH[0],
        onInput: function (v) {
          /* THE WALL GROWS BY WHOLE COURSES (core-v409, Marek: vyska steny se
             ma menit po krocich definovanych vyskou bloku, vyska vence
             konstantni). A product that lays courses gives their height; the
             slider walks in those, so the ring beam is always a course and
             nothing is ever cut on its height. Everyone else keeps 50 mm. */
          var st = _heightStep(), vv = clamp(v, CLEAR_LIM.min, CLEAR_LIM.max);
          if (st > 0.02) vv = Math.round(vv / st) * st;
          /* the wall total: the ground floor stays, storey two is the rest --
             unless the product is one storey, where the slider IS the wall and
             only stretches it (core-v481) */
          var h1 = STATE.clearH[0] || 2.6;
          if (_oneStorey()) { STATE.clearH[0] = Math.round(vv * 10000) / 10000; STATE.clearH[1] = 0; }
          else if (vv <= h1 + 0.001) { STATE.clearH[0] = Math.round(vv * 10000) / 10000; STATE.clearH[1] = 0; }
          else STATE.clearH[1] = Math.round((vv - h1) * 10000) / 10000;
          _storeysFromHeights();
          /* THE WALL IS FITTED TO ITS COURSES BEFORE ANYTHING IS BUILT
             (core-v441, Marek: strecha pri tazeni odskakuje a zase priskakuje).
             The masonry product used to find the leftover above its last
             course during the build and take it off the top storey with a
             second build, so the roof went up and came back on every step.
             Now the product is asked first, the storey is trimmed, and the
             one build that follows is the right one. */
          _heightLabel();
          try { _stLabel(); } catch (eSL) {}
          _dragMove('the wall height slider', null);
        },
        onChange: function () { if (_dragStop('the wall height slider let go')) rebuild(); savePref(); _log('wall height -> ' + fmtLen(STATE.clearH[0])); }
      });
      try {
        var lab = document.createElement('div');
        lab.id = 'cf-house-height-val';
        lab.style.cssText =
          'position:absolute;top:100%;transform:translateX(-50%);' +
          'margin-top:4px;padding:1px 7px;border-radius:6px;white-space:nowrap;' +
          'background:rgba(255,255,255,0.96);box-shadow:0 1px 4px rgba(0,0,0,0.12);' +
          'font:700 12px/1.35 Montserrat,sans-serif;color:#1A1C1D;pointer-events:none;';
        if (_heightSl.el.style.position !== 'absolute') _heightSl.el.style.position = 'absolute';
        _heightSl.el.appendChild(lab);
      } catch (e) {}
      _heightSl.hide();
      return _heightSl;
    }
    function _heightLabel() {
      var el = $('cf-house-height-val');
      if (!el || !_heightSl) return;
      /* on the plan the label is the whole wall, the sum of the storeys */
      var vTot = _wallTotal();
      el.textContent = fmtLen(vTot);
      try {
        var inp = _heightSl.input, lo = CLEAR_LIM.min, hi = CLEAR_LIM.max, v = vTot;
        var f = (hi > lo) ? ((v - lo) / (hi - lo)) : 0;
        var w = inp.clientWidth || _heightSl.el.clientWidth || 0, thumb = 14;
        el.style.left = (thumb / 2 + f * Math.max(0, w - thumb)) + 'px';
      } catch (e2) {}
    }
    function _noWallH() { try { return !!(__PROD && __PROD._noWallHeight); } catch (e) { return false; } }
    function showHeight(on) {
      if (on && _noWallH()) on = false;
      if (!_heightSl) _initHeight();
      if (!_heightSl) return;
      if (_heightSl.el && _heightSl.el.parentNode && _heightSl.el.parentNode.id !== 'cf-root') { if (on) { _heightSl.set({ min: CLEAR_LIM.min, max: CLEAR_LIM.max, value: STATE.clearH[0] }); _heightLabel(); } return; }
      _slidersCentre();
      if (on) { _heightSl.set({ min: CLEAR_LIM.min, max: CLEAR_LIM.max, step: _heightStep(), value: _wallTotal() }); _heightLabel(); _heightSl.show(); }
      else _heightSl.hide();
    }
    /* THE FLOOR LEVEL: the finished floor above grade, on the Roof and Base
       step with the wall height and the pitch (core-v336). Only a product that
       gives a range has it -- __PROD.FLOOR_LEVEL = { min, max, step } in metres
       -- because only a product whose floor stands on the ground can move it:
       the T house, +300 to +500 (Marek: dalsi posuvnik, vyska podlahy nad
       terenem, rozmezi tak 200 mm). The slider writes STATE.floorLevel, the
       product answers the core's _productSlab with it, and the storeys, the
       walls, the slab, the plinth and the roof all follow on the rebuild. The
       title is the site's own word for the floor (floor_label, already
       translated); the figure on the thumb says the rest: +300 mm. */
    var _floorSl = null;
    function _floorRange() {
      var F = __PROD.FLOOR_LEVEL;
      if (!F || !(F.max > F.min)) return null;
      return { min: F.min, max: F.max, step: (F.step > 0) ? F.step : 0.01 };
    }
    function _floorNow() {
      var R = _floorRange();
      return R ? clamp(STACK.slab, R.min, R.max) : STACK.slab;
    }
    function _initFloor() {
      var U = _UI(), R = _floorRange();
      if (!U || !U.slider || _floorSl || !R) return _floorSl;
      _floorSl = U.slider({
        /* the floor slider moves up a row so the storey slider can sit
           right on the wall slider (core-v431, Marek: ma byt u wall, pod) */
        id: 'cf-house-floor', bottom: 212, width: 190,
        min: R.min, max: R.max, step: R.step, value: _floorNow(),
        onInput: function (v) {
          var R2 = _floorRange(); if (!R2) return;
          STATE.floorLevel = Math.round(clamp(v, R2.min, R2.max) * 1000) / 1000;
          _stackForFooting();
          _floorLabel();
          _dragMove('the floor slider', null);
        },
        onChange: function () {
          if (_dragStop('the floor slider let go')) rebuild();
          savePref();
          _say('base     floor level -> +' + Math.round(STACK.slab * 1000) + ' by the slider (' + _slabFrom + ')');
        }
      });
      try {
        var lab = document.createElement('div');
        lab.id = 'cf-house-floor-val';
        lab.style.cssText =
          'position:absolute;top:100%;transform:translateX(-50%);' +
          'margin-top:4px;padding:1px 7px;border-radius:6px;white-space:nowrap;' +
          'background:rgba(255,255,255,0.96);box-shadow:0 1px 4px rgba(0,0,0,0.12);' +
          'font:700 12px/1.35 Montserrat,sans-serif;color:#1A1C1D;pointer-events:none;';
        if (_floorSl.el.style.position !== 'absolute') _floorSl.el.style.position = 'absolute';
        _floorSl.el.appendChild(lab);
      } catch (e) {}
      _floorSl.hide();
      return _floorSl;
    }
    function _floorLabel() {
      var el = $('cf-house-floor-val'), R = _floorRange();
      if (!el || !_floorSl || !R) return;
      var v = _floorNow();
      el.textContent = '+' + (STATE.isMetric ? (Math.round(v * 1000) + ' mm') : fmtLen(v));
      try {
        var inp = _floorSl.input, f = (R.max > R.min) ? ((v - R.min) / (R.max - R.min)) : 0;
        var w = inp.clientWidth || _floorSl.el.clientWidth || 0, thumb = 14;
        el.style.left = (thumb / 2 + f * Math.max(0, w - thumb)) + 'px';
      } catch (e2) {}
    }
    /* THE STOREY'S CLEAR HEIGHT, ON THE FLOORPLAN STEP (core-v406, Marek:
       dat vysku patra pri editaci floorplanu, posuvnik svetla vyska podlazi a
       dat tam default bezny rozmer). One slider over the plan, for the storey
       the cards have lit; it writes STATE.clearH for that storey and steps by
       a course when the product lays courses, so a ceiling always lands on a
       bed joint. 2.60 is the everyday default. Copied from the floor slider. */
    var _stSl = null;
    function _stRange() {
      var crs = 0;
      try { if (typeof __PROD._courseH === 'function') crs = +__PROD._courseH() || 0; } catch (e) { crs = 0; }
      /* A PRODUCT MAY CAP THE GROUND FLOOR (core-v471, Marek: maximalni vyska
         patra pro schodiste 3400): __PROD._storeyMax(STACK) gives the highest
         clear height it allows; kept on a whole course */
      var mx = 3.60;
      try { if (typeof __PROD._storeyMax === 'function') { var pm = +__PROD._storeyMax(STACK); if (pm > 2.40 && pm < mx) mx = pm; } } catch (eM) {}
      /* A PRODUCT MAY SET ITS OWN STEP (core-v472, Marek: plynule po 50 mm,
         ne jen 2500 a 2750): __PROD._storeyStep() -> m; the value is then
         not held to a course */
      var ps = 0;
      try { if (typeof __PROD._storeyStep === 'function') ps = +__PROD._storeyStep() || 0; } catch (eP) { ps = 0; }
      if (ps > 0.005) {
        mx = Math.floor(mx / ps + 1e-6) * ps;
        return { min: 2.40, max: Math.round(mx * 10000) / 10000, step: ps, crs: 0 };
      }
      if (crs > 0.02) mx = Math.floor(mx / crs + 1e-6) * crs;
      return { min: 2.40, max: Math.round(mx * 10000) / 10000, step: (crs > 0.02) ? crs : 0.05, crs: crs };
    }
    function _stNow() {
      var k = 0, R = _stRange();
      var v = (STATE.clearH && typeof STATE.clearH[k] === 'number') ? STATE.clearH[k] : 2.60;
      if (R.crs > 0.02) v = Math.round(v / R.crs) * R.crs;
      return clamp(v, R.min, R.max);
    }
    function _initStorey() {
      var U = _UI(), R = _stRange();
      if (!U || !U.slider || _stSl) return _stSl;
      _stSl = U.slider({
        id: 'cf-house-storey', bottom: 156, width: 190,
        min: R.min, max: R.max, step: R.step, value: _stNow(),
        onInput: function (v) {
          var R2 = _stRange(), k = 0;
          if (R2.crs > 0.02) v = Math.round(v / R2.crs) * R2.crs;
          STATE.clearH = STATE.clearH || [];
          /* the wall total is held: the ground floor takes from storey two */
          var totW = _wallTotal();
          STATE.clearH[k] = Math.round(clamp(v, R2.min, R2.max) * 1000) / 1000;
          STATE.clearH[1] = _oneStorey() ? 0 : Math.max(0, Math.round((totW - STATE.clearH[0]) * 10000) / 10000);
          _storeysFromHeights();
          _stLabel();
          try { _heightLabel(); } catch (eHL) {}
          _dragMove('the storey slider', null);
        },
        onChange: function () {
          if (_dragStop('the storey slider let go')) rebuild();
          savePref();
          _say('base     ground floor -> ' + Math.round((STATE.clearH[0] || 0) * 1000) + ', storey 2 ' + Math.round((STATE.clearH[1] || 0) * 1000) + ' from the wall height' + (_stRange().crs > 0.02 ? (', on courses of ' + Math.round(_stRange().crs * 1000)) : ''));
        }
      });
      try {
        var lab = document.createElement('div');
        lab.id = 'cf-house-storey-val';
        lab.style.cssText =
          'position:absolute;top:100%;transform:translateX(-50%);' +
          'margin-top:4px;padding:1px 7px;border-radius:6px;white-space:nowrap;' +
          'background:rgba(255,255,255,0.96);box-shadow:0 1px 4px rgba(0,0,0,0.12);' +
          'font:700 12px/1.35 Montserrat,sans-serif;color:#1A1C1D;pointer-events:none;';
        if (_stSl.el.style.position !== 'absolute') _stSl.el.style.position = 'absolute';
        _stSl.el.appendChild(lab);
      } catch (e) {}
      _stSl.hide();
      return _stSl;
    }
    function _stLabel() {
      var el = $('cf-house-storey-val'), R = _stRange();
      if (!el || !_stSl) return;
      var v = _stNow();
      /* the figure alone on the thumb (core-v440, Marek: bez popisu storey 1,
         jen cislo); the title over the slider says what it is */
      el.textContent = STATE.isMetric ? (Math.round(v * 1000) + ' mm') : fmtLen(v);
      try {
        var inp = _stSl.input, f = (R.max > R.min) ? ((v - R.min) / (R.max - R.min)) : 0;
        var w = inp.clientWidth || _stSl.el.clientWidth || 0, thumb = 14;
        el.style.left = (thumb / 2 + f * Math.max(0, w - thumb)) + 'px';
      } catch (e2) {}
    }
    function showStorey(on) {
      var wants = false;
      try { wants = !!(__PROD && __PROD._storeySlider); } catch (eW) { wants = false; }
      if (!wants) { if (_stSl) _stSl.hide(); return; }
      if (!_stSl) _initStorey();
      if (!_stSl) return;
      var R = _stRange();
      if (_stSl.el && _stSl.el.parentNode && _stSl.el.parentNode.id !== 'cf-root') { if (on) { _stSl.set({ min: R.min, max: R.max, step: R.step, value: _stNow() }); _stLabel(); _stSl.show(); } return; }
      _slidersCentre();
      if (on) { _stSl.set({ min: R.min, max: R.max, step: R.step, value: _stNow() }); _stLabel(); _stSl.show(); }
      else _stSl.hide();
    }
    function showFloor(on) {
      if (!_floorRange()) { if (_floorSl) _floorSl.hide(); return; }
      if (!_floorSl) _initFloor();
      if (!_floorSl) return;
      var R = _floorRange();
      if (_floorSl.el && _floorSl.el.parentNode && _floorSl.el.parentNode.id !== 'cf-root') { if (on) { _floorSl.set({ min: R.min, max: R.max, value: _floorNow() }); _floorLabel(); } return; }
      _slidersCentre();
      if (on) { _floorSl.set({ min: R.min, max: R.max, value: _floorNow() }); _floorLabel(); _floorSl.show(); }
      else _floorSl.hide();
    }
    /* THE SLIDERS SIT OVER THE MODEL, CENTRED ON THE VISIBLE CANVAS -- the part
       left of the open drawer -- one above the other (Marek: the drawer covered
       them at the right). Re-centred whenever they show and on resize. */
    function _slidersCentre() {
      try {
        var root = $('cf-root'); if (!root) return;
        var rw = root.clientWidth || 0, dr = $('cfg-ui-drawer'), dw = 0;
        if (dr && dr.classList.contains('open') && !dr.classList.contains('cfg-ui-drawer-bottom')) dw = dr.getBoundingClientRect().width || 0;
        var cx = Math.round((rw - dw) / 2), q, S = [_pitchSl, _heightSl, _floorSl];
        for (q = 0; q < S.length; q++) if (S[q] && S[q].el) { S[q].el.style.left = cx + 'px'; S[q].el.style.transform = 'translateX(-50%)'; }
      } catch (e) {}
    }
    try { global.addEventListener('resize', function () { _slidersCentre(); }); } catch (eRS) {}
    function showPitch(on) {
      if (!_pitchSl) _initPitch();
      if (!_pitchSl) return;
      /* seated in the drawer: the wrap shows or hides with the roof type */
      if (_pitchSl.el && _pitchSl.el.parentNode && _pitchSl.el.parentNode.id !== 'cf-root') { _pitchSl.el.parentNode.style.display = on ? '' : 'none'; if (on) { clampPitch(); _pitchSl.set({ min: pitchMin(), max: pitchMax(), value: STATE.roof.pitch }); _pitchLabel(); } return; }
      _slidersCentre();
      /* THE MONO-PITCH HAS A PITCH TOO, so it gets the slider. It only did not
         before because its geometry did not tilt. */
      if (on) {
        /* THE RANGE AND THE VALUE BOTH BELONG TO THE ROOF TYPE, so both are set
           every time it is shown. Setting only the range let a pitch left over
           from another roof sit outside its own limits -- the track said 15 to 35
           while the value was 45. */
        clampPitch();
        _pitchSl.set({ min: pitchMin(), max: pitchMax(), value: STATE.roof.pitch });
        _pitchLabel();
        _pitchSl.show();
      } else _pitchSl.hide();
    }

    /* -------------------------------------------------------------------------
       FLOORPLAN. Two contracts: init() for the library itself, and plan.init()
       for the drawing, which is the big one.
       ------------------------------------------------------------------------- */
    /* --------------------------------------------------------------------------
       POINT IN POLYGON, EDGE INCLUDED -- WHICH IS THE ONE THE PLAN NEEDS.

       The core's inPoly is a strict crossing test and it throws away the polygon
       it is handed: it always answers about the building's outline. The asset
       asks with points two centimetres off a wall, and sometimes about a ROOM's
       polygon rather than the building's, so both of those matter. Tiny answers
       the whole contract with this one function; so does this now.
       -------------------------------------------------------------------------- */
    function _inPolyEdge(x, z, P) {
      P = P || poly();
      var n = P.length, i, j, a, b, dx, dz, lenSq, t, tc, px, pz, xi, zi, xj, zj;
      var EPS = 0.001;
      for (i = 0; i < n; i++) {
        a = P[i]; b = P[(i + 1) % n];
        dx = b[0] - a[0]; dz = b[1] - a[1];
        lenSq = dx * dx + dz * dz;
        if (lenSq < 1e-9) continue;
        t = ((x - a[0]) * dx + (z - a[1]) * dz) / lenSq;
        tc = t < 0 ? 0 : (t > 1 ? 1 : t);
        px = a[0] + tc * dx; pz = a[1] + tc * dz;
        if (Math.sqrt((x - px) * (x - px) + (z - pz) * (z - pz)) < EPS) return true;
      }
      var inside = false;
      for (i = 0, j = n - 1; i < n; j = i++) {
        xi = P[i][0]; zi = P[i][1]; xj = P[j][0]; zj = P[j][1];
        if (((zi > z) !== (zj > z)) && (x < (xj - xi) * (z - zi) / (zj - zi) + xi)) inside = !inside;
      }
      return inside;
    }

    /* --------------------------------------------------------------------------
       WHERE A LINE LIES INSIDE THE BUILDING, IN STRETCHES -- AND ITS ABSENCE IS
       WHY EVERY TAP ON A PARTITION THREW.

           partAt(): H.clipRuns is not a function
           doorTapAt(): H.clipRuns is not a function
           movePartTo(): H.pushDoors is not a function

       The asset works out how far a partition reaches by striking its line across
       the outline and keeping the stretches that fall inside. partSpan, meetsWall,
       footObstacles and the drag clamp all go through it, so with no clipRuns the
       very first of them threw -- inside the pointer handler, which is why
       selecting a wall, cutting one and placing a door were dead all at once.

       Copied from tiny. Crossings are paired off rather than taken first-to-last,
       so a line that leaves the plan and comes back -- across the notch of an L --
       gives two stretches instead of one bridging the gap.
       -------------------------------------------------------------------------- */
    function _clipRuns(P, ox, oz, gx, gz) {
      var runs = [], hits = [], e, p, q;
      P = P || poly();
      for (e = 0; e < P.length; e++) {
        p = P[e]; q = P[(e + 1) % P.length];
        var ex = q[0] - p[0], ez = q[1] - p[1];
        var den = gz * ex - gx * ez;
        if (Math.abs(den) < 1e-12) continue;
        var rx = p[0] - ox, rz = p[1] - oz;
        var t = (ex * rz - ez * rx) / den;
        var w = (gx * rz - gz * rx) / den;
        if (w >= -1e-6 && w <= 1 + 1e-6) hits.push({ t: t, e: e });
      }
      if (hits.length < 2) return runs;
      hits.sort(function (m, n) { return m.t - n.t; });
      for (e = 0; e + 1 < hits.length; e++) {
        var h0 = hits[e], h1 = hits[e + 1];
        if (h1.t - h0.t < 0.05) continue;
        var mt = (h0.t + h1.t) / 2;
        if (!_inPolyEdge(ox + gx * mt, oz + gz * mt, P)) continue;
        runs.push({ t0: h0.t, t1: h1.t, e0: h0.e, e1: h1.e });
      }
      return runs;
    }

    /* THE DOORS TRAVEL WITH THE WALL THEY SIT IN. The asset calls this the moment
       a partition lands somewhere new, and the function is the ASSET'S own -- the
       host only has to point at it, exactly as tiny does. Missing, movePartTo
       threw and a partition could not be dragged at all. */
    function _pushDoors() {
      var F = _FL();
      if (!F || !F.plan || !F.plan.pushDoors) return false;
      try { F.plan.pushDoors(); return true; }
      catch (e) { _log('pushDoors: ' + (e && e.message)); return false; }
    }

    /* --------------------------------------------------------------------------
       THE WALL SEGMENTS IN THE ASSET'S OWN FRAME -- AND THIS IS WHAT TRUNCATED
       THE DRAWING, NOT opsOn.

       The asset's openings pass is two lines, once per wall:

           var sg = segs[si], here = H.opsOn(sg.i);
           var cxw = sg.a[0] + sg.ux * op.off, czw = sg.a[1] + sg.uz * op.off;

       This function answered { x0, z0, x1, z1 }. No i, no a, no ux, no uz. So
       opsOn was called with undefined -- harmless in itself, it answers every
       opening when it is told nothing -- and then sg.a[0] threw on the very
       first opening there was. drawFloor is one long pass and the asset swallows
       the throw, so partitions, doors, room names and koty were never reached.
       That was engine NOT HELD.

       Which means the boolean was never a fix. It only hid the bad segment: with
       nothing to iterate, the loop could not reach the line that throws.

       THE FRAME IS THIS PRODUCT'S, NOT TINY'S. Tiny trims its segments back by a
       wall thickness at every corner and swaps a and b until the normal points
       inward -- correct there, because tiny's offsets are measured against that
       trimmed frame. Here an opening's at comes from segments(): the raw polygon
       corner and the full edge length. Copying tiny's arithmetic would shift
       every opening by a wall thickness and mirror the ones on the flipped
       walls. The SHAPE is copied from tiny; the geometry stays the core's.

       Nothing is skipped, so the position in this array is the polygon edge
       index and S0-W<i> is out[i] -- which is what _opsOn relies on when the
       asset asks by number.
       -------------------------------------------------------------------------- */
    function _wallSegs(Pin) {
      var P = (Pin && Pin.length >= 3) ? Pin : poly(), out = [], i, a, b, dx, dz, len;
      for (i = 0; i < P.length; i++) {
        a = P[i]; b = P[(i + 1) % P.length];
        dx = b[0] - a[0]; dz = b[1] - a[1];
        len = Math.sqrt(dx * dx + dz * dz) || 1e-6;
        out.push({
          id: 'S0-W' + i, i: i,
          a: [a[0], a[1]], b: [b[0], b[1]],
          x0: a[0], z0: a[1], x1: b[0], z1: b[1],
          len: len,
          ux: dx / len, uz: dz / len,
          /* the outward normal of a clockwise ring seen from above -- the same
             one segments() carries, so the two can never disagree */
          nx: dz / len, nz: -dx / len,
          uLo: 0, uHi: len
        });
      }
      return out;
    }

    /* the same four walls WITH their names, for anything on this side that needs
       to tie an opening to a wall. It is deliberately not what wallSegs answers:
       see the note on opsOn below. */
    function _namedWallSegs() {
      var P = poly(), out = [], i, a, b, dx, dz;
      for (i = 0; i < P.length; i++) {
        a = P[i]; b = P[(i + 1) % P.length];
        dx = b[0] - a[0]; dz = b[1] - a[1];
        out.push({ id: 'S0-W' + i, i: i, x0: a[0], z0: a[1], x1: b[0], z1: b[1],
                   len: Math.sqrt(dx * dx + dz * dz) });
      }
      return out;
    }

    /* --------------------------------------------------------------------------
       THE OPENINGS ON A WALL, WHICH IS WHAT THIS WAS ALWAYS ASKING FOR.

       opsOn answered editing('openings') -- a yes/no about which wizard step is
       open. The asset wants the openings ON a wall: the list it breaks the poche
       for and draws a symbol over. Handed a boolean it found nothing to iterate
       and drew nothing, without complaint, which is why the windows and the door
       have never appeared in the plan.

       WHICH WALL IS THE LAST ARGUMENT. The asset may name it as an index, as an
       id, or as the segment object, and it may pass a module ahead of it the way
       footObstacles does. The wall is the thing being asked about, so it is the
       last thing said; with nothing said at all the honest answer is every
       opening there is.
       -------------------------------------------------------------------------- */
    /* A PANE THAT DOES NOT OPEN DRAWS AS ONE (core-v282). The catalogue says
       which units are fixed -- the full-wall and half-wall glazing, the
       full-height lights, the fixed OWR -- and a product may add its own rule
       (the sauna: every window of the sauna room). Such a unit goes to the plan
       as FIXED GLAZING, and never with the full-wall flags that draw a slider
       (Marek: fix okna nesmi byt v pudorysu videt jako oteviratelna). */
    var OPENING_FIXED = null;
    function _openingFixed(o) {
      if (!o) return false;
      if (o.kind === 'fixed' || o.kind === 'fixf' || o.kind === 'fixh') return true;
      var k, i, G, CAT = (_UI() && _UI().OPENING_CATALOG) || {};
      for (k in CAT) {
        if (!CAT.hasOwnProperty(k)) continue;
        G = CAT[k];
        for (i = 0; G && G.items && i < G.items.length; i++) if (G.items[i].code === o.code && G.items[i].fixed) return true;
      }
      if (typeof OPENING_FIXED === 'function') { try { return !!OPENING_FIXED(o); } catch (e) { return false; } }
      return false;
    }
    function _opsOn() {
      var segs = _namedWallSegs(), key = arguments.length ? arguments[arguments.length - 1] : null;
      var want = null, i, o, out = [];

      if (key && typeof key === 'object') want = key.id || (typeof key.i === 'number' && segs[key.i] ? segs[key.i].id : null);
      else if (typeof key === 'string') want = key;
      else if (typeof key === 'number' && segs[key]) want = segs[key].id;

      var all = STATE.openings || [];
      for (i = 0; i < all.length; i++) {
        o = all[i];
        if (!o) continue;
        if (o.host === 'roof') continue;   /* a roof window is not in the plan's walls (core-v491, R5) */
        if (want && o.seg !== want) continue;
        var fixO = _openingFixed(o);
        out.push({
          seg: o.seg, at: o.at, c: o.at, off: o.at,
          w: o.w, width: o.w, h: o.h, sill: o.sill,
          kind: o.kind, code: o.code, hand: o.hand, leaves: o.leaves,
          /* an entry door that opens out of the building (core-v567): the plan
             draws its leaves outside, the label inside (floorplan v149) */
          out: o.out ? true : null,
          /* a unit fitted to the whole wall draws as a slider, not as a window --
             unless it is fixed glazing, which draws as glazing */
          fullW: fixO ? null : (o.fullW || null), halfW: fixO ? null : (o.halfW || null),
          /* a light that does not open draws as FIXED GLAZING whatever else it
             is called -- the asset checks this before its own auto-fit rule */
          fix: fixO
        });
      }
      return out;
    }

    function _decomposeToRects(Pin) {
      /* THE BUILDING, ASKED FOR BY NAME. This read _rects()[0] -- the first thing
         the finger could grab -- so on the ROOF step the plan was handed the roof
         rectangle and cut IT into rooms.
         AND A T IS NOT ONE RECTANGLE (core-v393, Marek: vidim jeden room pres
         cely opsany obdelnik). Handing back the bounding box meant the plan cut
         rooms out of the corners the wing leaves open, and everything built on
         this -- rooms, their areas, the koty between them -- inherited the lie.
         The outline is sliced instead: a cut at every corner's z, and on each
         band the spans the outline actually covers. A plain rectangle still
         comes back as one rectangle, so nothing else changes. */
      var P = (Pin && Pin.length >= 3) ? Pin : poly();
      if (!P || P.length < 4) return [_buildRect()];
      var zs = [], i, q;
      for (i = 0; i < P.length; i++) zs.push(P[i][1]);
      zs.sort(function (a, b) { return a - b; });
      var uz = [];
      for (i = 0; i < zs.length; i++) if (!i || zs[i] - zs[i - 1] > 0.01) uz.push(zs[i]);
      if (uz.length < 2) return [_buildRect()];
      var out = [];
      for (i = 0; i + 1 < uz.length; i++) {
        var zm = (uz[i] + uz[i + 1]) / 2, hits = [];
        for (q = 0; q < P.length; q++) {
          var a = P[q], b = P[(q + 1) % P.length];
          if (Math.abs(b[1] - a[1]) < 1e-9) continue;
          var t = (zm - a[1]) / (b[1] - a[1]);
          if (t < -1e-9 || t > 1 + 1e-9) continue;
          hits.push(a[0] + (b[0] - a[0]) * t);
        }
        hits.sort(function (a2, b2) { return a2 - b2; });
        for (q = 0; q + 1 < hits.length; q += 2) {
          if (hits[q + 1] - hits[q] < 0.05) continue;
          out.push({ x0: hits[q], x1: hits[q + 1], z0: uz[i], z1: uz[i + 1] });
        }
      }
      return out.length ? out : [_buildRect()];
    }

    /* --------------------------------------------------------------------------
       A PARTITION, IN BOTH DIALECTS -- AND THIS IS WHY THE PLAN HAD ONE ROOM.

       Seven partitions were in STATE and the asset reported a single room. It
       was not the flood fill: it was the shape. This product writes a partition
       as { dir, at }, the asset reads one as { o, c } -- orientation and
       coordinate. Neither side is wrong and neither side threw; the asset simply
       found no orientation on any of them and cut nothing, so the whole floor
       stayed one room. Room fills, room names and every koty between partitions
       were downstream of that.

       Rather than rename the field on one side and break whatever is saved in
       everybody's browser, a partition now carries BOTH names for the same two
       numbers, kept in step. It is written after every edit the asset makes,
       because movePartTo writes the asset's field and this product's own drawing
       reads the other one.
       -------------------------------------------------------------------------- */
    function _planBounds() {
      var P2 = poly(), ax0 = 1e9, ax1 = -1e9, az0 = 1e9, az1 = -1e9, q;
      for (q = 0; q < P2.length; q++) {
        ax0 = Math.min(ax0, P2[q][0]); ax1 = Math.max(ax1, P2[q][0]);
        az0 = Math.min(az0, P2[q][1]); az1 = Math.max(az1, P2[q][1]);
      }
      return { ax0: ax0, ax1: ax1, az0: az0, az1: az1 };
    }

    /* EVERY PARTITION NEEDS A NAME OF ITS OWN, and none of them had one.

       A door is stored as { pid, u, w } -- which wall it is in, and how far along.
       With no id on the wall, pid came out undefined, and doorsLive then matched
       undefined against undefined and tied every door to the first wall it looked
       at. Cutting a wall in two re-points the doors in the far half by id as well,
       so that was equally blind. */
    var _partSeq = 0;

    function _normParts() {
      var ps = (STATE.parts = STATE.parts || []), b = _planBounds(), i, p, o, c;
      for (i = 0; i < ps.length; i++) if (ps[i] && (ps[i].id | 0) > _partSeq) _partSeq = ps[i].id | 0;
      for (i = 0; i < ps.length; i++) {
        p = ps[i];
        if (!p || typeof p !== 'object') continue;
        if (typeof p.id !== 'number' || !p.id) p.id = ++_partSeq;
        o = p.o || p.dir || 'v';
        c = (typeof p.c === 'number') ? p.c : p.at;
        if (typeof c !== 'number' || !isFinite(c)) {
          c = (o === 'v') ? (b.ax0 + b.ax1) / 2 : (b.az0 + b.az1) / 2;
        }
        p.o = o; p.dir = o;
        p.c = c; p.at = c;
        if (typeof p.a !== 'number') p.a = (o === 'v') ? b.az0 : b.ax0;
        if (typeof p.b !== 'number') p.b = (o === 'v') ? b.az1 : b.ax1;
        /* A CUT HAS TO REACH THE TIMBER, NOT ONLY THE PICTURE. The asset records
           a trim as s and e; adu-partitions builds from a and b and the bill
           counts from the same pair, so a wall cut in the plan was still framed
           and still paid for at its full length. */
        if (typeof p.s === 'number' && p.s > p.a) p.a = p.s;
        if (typeof p.e === 'number' && p.e < p.b) p.b = p.e;
        if (typeof p.st !== 'number') p.st = 0;
      }
      /* THE STOREY THE PLAN SHOWS (core-v402, Marek: nosna zed by mela jit
         pres obe patra, ale pokud ji odstranim cut ve vyssich patrech, zmizi
         jen v danem patru). A partition belongs to the storey it was drawn on.
         A load-bearing wall belongs to that storey and every one above it,
         unless it has been cut away on a storey -- then it is gone there and
         there only, and still stands below. p.st is the storey a wall was
         drawn on, p.cutOn the storeys it has been removed from. */
      var sv = STATE.planStorey | 0, out = [], q;
      for (q = 0; q < ps.length; q++) {
        var pq = ps[q];
        if (!pq || typeof pq !== 'object') continue;
        var here = (pq.st === sv) || (pq.bear && pq.st < sv);
        if (here && pq.cutOn && pq.cutOn.indexOf(sv) >= 0) here = false;
        if (here) out.push(pq);
      }
      return out;
    }

    /* the same courtesy for a door: the asset writes its own fields when one is
       cycled or dragged, and nothing here should have to know which */
    /* --------------------------------------------------------------------------
       PICKING AND MOVING A PARTITION, ON THIS PRODUCT'S OWN DATA.

       Thirteen taps, thirteen room names, not one partition. The point is right
       -- the room under it was found every time -- so the tap is landing where
       it should and partAt is simply not answering. It may want a tolerance this
       product cannot give it, it may want the point in a frame of its own; from
       here there is no way to tell and no more rounds to spend finding out.

       The partitions belong to THIS file. Their line is two numbers and their
       span is two more. Asking somebody else which one is under a finger, and
       then not being able to drag anything when the answer is no, is a
       dependency with nothing to show for it.

       So the asset is asked first and keeps the job wherever it answers; when it
       does not, this takes over. The grab is 350 mm of BUILDING, not of screen,
       so it is the same size at every zoom -- which is the rule already written
       into this file for openings, and it is the reason a partition can be
       caught on a tablet at all.
       -------------------------------------------------------------------------- */
    var PART_GRAB = 0.35;

    function _partAtLocal(x, z) {
      var ps = _normParts(), best = -1, bd = PART_GRAB, i, p, d, lo, hi;
      for (i = 0; i < ps.length; i++) {
        p = ps[i];
        if (!p) continue;
        lo = Math.min(p.a, p.b) - 0.2; hi = Math.max(p.a, p.b) + 0.2;
        if ((p.o || p.dir) === 'v') {
          if (z < lo || z > hi) continue;
          d = Math.abs(x - p.c);
        } else {
          if (x < lo || x > hi) continue;
          d = Math.abs(z - p.c);
        }
        if (d < bd) { bd = d; best = i; }
      }
      return best;
    }

    function _movePartLocal(i, c) {
      var ps = _normParts(), p = ps[i];
      if (!p || typeof c !== 'number' || !isFinite(c)) return false;
      var b = _planBounds(), wt = wallThickness() + 0.3;
      c = ((p.o || p.dir) === 'v') ? clamp(c, b.ax0 + wt, b.ax1 - wt)
                                   : clamp(c, b.az0 + wt, b.az1 - wt);
      if (Math.abs(c - p.c) < 1e-6) return false;
      p.c = c; p.at = c;
      return true;
    }

    function _cutPartLocal(i) {
      var ps = _normParts();
      if (i < 0 || i >= ps.length) return false;
      ps.splice(i, 1);
      _dropOrphanDoors();
      return true;
    }

    /* THE DOORS WERE STORED, CYCLED AND NEVER DRAWN. The drawing asks the HOST
       for them -- H.doorsLive() for the symbols, H.doorGaps() to interrupt the
       partition where a doorway is -- and both answered an empty list, so
       thirteen doors existed and the walls came out solid. The asset has the real
       answers; the host only has to point at them, as tiny does. */
    function _fpPlan() { var F = _FL(); return (F && F.plan) ? F.plan : null; }

    /* NO RECURSION HERE: the asset's partSpan asks back for partitions(), which
       is _normParts, and _normParts must therefore never ask for a span. It
       clamps a/b by s/e itself instead. */
    function _partSpanH(p) {
      var P = _fpPlan();
      if (P && P.partSpan) {
        try { var sp = P.partSpan(p); if (sp) return sp; } catch (e) {}
      }
      var b = _planBounds(), wt = wallThickness();
      return (p && (p.o || p.dir) === 'v') ? [b.az0 + wt, b.az1 - wt] : [b.ax0 + wt, b.ax1 - wt];
    }
    /* A WALL THAT GOES TAKES ITS DOORS WITH IT.

       cutPartSeg removes the partition, or trims a stretch off it, and says
       nothing about the doors that were standing in what it removed. They stayed
       in STATE.doors pointing at a wall that is gone or no longer reaches them --
       drawn as a door beside its own hole, and still counted in the bill.

       The centre decides, not the edge: a door that merely pokes past the end is
       nudged back by pushDoors, and only one whose middle is off the wall
       entirely has nowhere left to be. That also makes this safe to run on every
       setParts, including the ones a drag fires frame after frame. */
    /* WHY A NAME JUMPS ROOMS WHEN A WALL IS DRAGGED, AND IT IS NOT THE PINS.

       The asset already anchors a name to a PIN -- the point that was tapped when
       the room was named -- so a named room keeps its name when a wall moves past
       it. That part works. What moves is everything NOBODY has named: with no pin
       to find, roomNameIdx falls back to  i % table.length,  which is the room's
       POSITION IN THE LIST. Drag a wall and the rooms come back in a different
       order, so every unnamed room is handed a different name. Nine pins for two
       rooms in the snapshot is the same story from the other end -- pins left
       behind outside the plan, waiting to claim a room again.

       So every room gets a pin as soon as it exists, carrying the name it is
       already showing, and pins that have fallen outside the outline are dropped.
       Nothing then reaches the positional fallback and nothing renames itself. */
    var _anchorTimer = 0, _pinNote = '';

    function _anchorSoon() {
      try {
        if (_anchorTimer) { global.clearTimeout(_anchorTimer); _anchorTimer = 0; }
        _anchorTimer = global.setTimeout(function () {
          _anchorTimer = 0;
          try {
            var n = _anchorRoomNames();
            if (n) {
              savePref();
              _pinNote = n + ' anchored once the plan settled';
              _log('room pins: ' + n + ' anchored');
            }
          } catch (e) { _log('room pins: ' + (e && e.message)); }
        }, 400);
      } catch (e2) {}
    }

    function _anchorRoomNames() {
      var F = _FL();
      if (!F || !F.plan || !F.plan.rooms) return 0;
      var pins = (STATE.roomNames = STATE.roomNames || []);
      if (!Array.isArray(pins)) { pins = STATE.roomNames = []; }
      var changed = 0, i, k;

      /* a pin outside the building cannot belong to any room in it */
      var keep = [];
      for (i = 0; i < pins.length; i++) {
        var pn = pins[i];
        if (!pn || typeof pn.x !== 'number' || typeof pn.z !== 'number') { changed++; continue; }
        if (!_inPolyEdge(pn.x, pn.z, poly())) { changed++; continue; }
        var dup = false;
        for (k = 0; k < keep.length; k++) {
          if (String(keep[k].st) === String(pn.st) && Math.abs(keep[k].x - pn.x) < 0.05 && Math.abs(keep[k].z - pn.z) < 0.05) { dup = true; break; }
        }
        if (dup) { changed++; continue; }
        keep.push(pn);
      }
      if (changed) { pins.length = 0; for (i = 0; i < keep.length; i++) pins.push(keep[i]); }

      var R, TB;
      try {
        R = F.plan.rooms() || [];
        TB = (F.draw && F.draw.roomTable) ? (F.draw.roomTable() || []) : [];
      } catch (e) { return changed; }

      function holds(r, x, z) {
        var j, rc, rs = r && r.rects;
        for (j = 0; rs && j < rs.length; j++) {
          rc = rs[j];
          if (x >= rc.x0 && x <= rc.x1 && z >= rc.z0 && z <= rc.z1) return true;
        }
        return false;
      }

      for (i = 0; i < R.length; i++) {
        var r = R[i], pinned = false;
        for (k = 0; k < pins.length; k++) {
          /* a pin with st names a room on that storey only; one without it,
             as before, on every storey (core-v596) */
          if (pins[k].st != null && (pins[k].st | 0) !== (STATE.planStorey | 0)) continue;
          if (holds(r, pins[k].x, pins[k].z)) { pinned = true; break; }
        }
        if (pinned) continue;
        /* the name it is showing right now, kept rather than reset -- pinning must
           not be visible on the screen, only in what happens next */
        var idx = 0;
        try { idx = F.plan.roomNameIdx(r, i) | 0; } catch (e2) { idx = i % Math.max(1, TB.length); }
        var pt = _roomAnchor(r);
        if (!pt) continue;
        var npn = { x: pt.x, z: pt.z, n: idx, k: (TB[idx] || {}).i18n || '' };
        if ((STATE.planStorey | 0) > 0) npn.st = STATE.planStorey | 0;
        pins.push(npn);
        changed++;
      }
      return changed;
    }

    /* A WALL PUSHES THE NAMES AHEAD OF IT.

       Anchoring a name to a pin stops the list order renaming rooms, but the pin
       itself does not move -- so a wall dragged far enough to cross one carries
       that room's name over to the room on the other side, and the room it came
       from is left with none. A short drag never reaches a pin, which is exactly
       why this only appears on the bigger changes.

       A pin that the wall has overtaken is set down just clear of its new face,
       on the side it started. Where there is no room left on that side -- the
       wall has been pushed up against the next one -- the pin is left where it is
       and the prune drops it on the next paint. */
    function _pushRoomPins(o, from, to) {
      var pins = STATE.roomNames;
      if (!Array.isArray(pins) || !(Math.abs(to - from) > 1e-6)) return 0;
      var vert = (o === 'v');
      var gap = partThickness() / 2 + 0.12;
      var moved = 0, i, pn, v, at, ob, want;
      for (i = 0; i < pins.length; i++) {
        pn = pins[i];
        if (!pn) continue;
        v = vert ? pn.x : pn.z;
        at = vert ? pn.z : pn.x;
        if (typeof v !== 'number' || typeof at !== 'number') continue;
        if (v < from) {
          if (v <= to - gap) continue;
          want = to - gap;
        } else if (v > from) {
          if (v >= to + gap) continue;
          want = to + gap;
        } else { continue; }
        ob = _footObstaclesH({ o: o, dir: o, c: to }, at);
        if (!ob || want < ob.lo + 0.05 || want > ob.hi - 0.05) continue;
        if (vert) pn.x = want; else pn.z = want;
        moved++;
      }
      if (moved) _log('room pins: ' + moved + ' pushed ahead of the wall');
      return moved;
    }

    /* the middle of the room's biggest cell, which is inside it however the cells
       are arranged -- a bounding-box centre is not, on an L */
    function _roomAnchor(r) {
      var rs = r && r.rects, best = null, i, rc, a;
      for (i = 0; rs && i < rs.length; i++) {
        rc = rs[i];
        a = (rc.x1 - rc.x0) * (rc.z1 - rc.z0);
        if (!best || a > best.a) best = { a: a, x: (rc.x0 + rc.x1) / 2, z: (rc.z0 + rc.z1) / 2 };
      }
      return best;
    }

    function _dropOrphanDoors() {
      var ds = (STATE.doors = STATE.doors || []), ps = STATE.parts || [];
      var kept = [], gone = 0, i, k, wall, sp;
      for (i = 0; i < ds.length; i++) {
        wall = null;
        for (k = 0; k < ps.length; k++) if (ps[k] && ps[k].id === ds[i].pid) { wall = ps[k]; break; }
        if (!wall) { gone++; continue; }
        sp = _partSpanH(wall);
        if (sp && (ds[i].u < sp[0] - 0.01 || ds[i].u > sp[1] + 0.01)) { gone++; continue; }
        kept.push(ds[i]);
      }
      if (gone) {
        STATE.doors = kept;
        _log('partition gone -- ' + gone + ' door(s) went with it');
      }
      return gone;
    }

    function _doorsLive() { var P = _fpPlan(); return (P && P.doorsLive) ? (P.doorsLive() || []) : []; }
    function _footObstaclesH(p, at) {
      var P = _fpPlan();
      if (P && P.footObstacles) {
        try { var r = P.footObstacles(p, at); if (r && typeof r.lo === 'number') return r; } catch (e) {}
      }
      var b = _planBounds(), wt = wallThickness();
      return (p && (p.o || p.dir) === 'v') ? { lo: b.ax0 + wt, hi: b.ax1 - wt }
                                           : { lo: b.az0 + wt, hi: b.az1 - wt };
    }
    function _doorGaps(pw) { var P = _fpPlan(); return (P && P.doorGaps) ? (P.doorGaps(pw) || []) : []; }
    function _doorSeg(pid, u) { var P = _fpPlan(); return (P && P.doorSeg) ? P.doorSeg(pid, u) : null; }
    function _doorFree(pid, seg, ex) { var P = _fpPlan(); return (P && P.doorFree) ? (P.doorFree(pid, seg, ex) || []) : []; }

    function _normDoors() {
      var ds = (STATE.doors = STATE.doors || []), i, d;
      for (i = 0; i < ds.length; i++) {
        d = ds[i];
        if (!d || typeof d !== 'object') continue;
        if (typeof d.at !== 'number' && typeof d.c === 'number') d.at = d.c;
        if (typeof d.c !== 'number' && typeof d.at === 'number') d.c = d.at;
      }
      return ds;
    }

    /* --------------------------------------------------------------------------
       WHICH ROOMS AN ADU MAY HAVE -- AND WHY roomKeys ALONE WAS NOT ENOUGH.

       v189 put the asset's own keys in roomKeys and the snapshot still read
       "offered 12, resolved 16". The keys were right; the asset does not take
       the list in that form, or not in v56. Rather than spend another version
       guessing at the form, the product hands over the TABLE.

       AND IT HANDS OVER THE ASSET'S OWN ROWS. roomTable normally means a product
       brings its own vocabulary, colours and all -- which is how a bathroom ends
       up the same blue in every product only until somebody edits one of the
       copies. This does not copy anything: it picks the rows it wants OUT of
       DRAW.ROOMS and hands those very objects back. Change a colour in the asset
       and it changes here, because it is the same row.

       TWELVE, and the four that went are worth being able to argue about.
       DINING, WC, GARAGE and STAIRS are gone because the asset has no such row.
       SAUNA, TERRACE and WORKSHOP belong to other products, and a terrace is
       drawn as an AREA rather than as a room. UTILITY is TECHNICAL, the same
       room under the name the whole platform already uses.

       The ORDER here is the order the tap walks through them, so the rooms
       somebody actually names first come first.
       -------------------------------------------------------------------------- */
    /* THE ASSET'S ROOM WORDS MOVED, SO THIS FILTER HAS TO MOVE WITH THEM. */
    /* WHICH ROOMS THIS PRODUCT MAY HAVE -- filled by the product below.
       The core only ever asks how many there are and whether a key is in the
       list; WHICH keys is the product's business. */
    var ROOM_KEYS = [];

    /* the rows themselves, taken live out of the asset's default list. Never
       DRAW.roomTable(), which is the call that CONSULTS this one -- asking it
       here would be a product and an asset each waiting for the other. */
    var _roomsFrozen = null;
    /* ROWS THE PRODUCT BRINGS ITSELF (core-v267). A key the shared asset does
       not carry used to throw the whole vocabulary away -- the sauna asked for
       SHOWER, CHANGING, RELAX, WC and PORCH, the asset has none of them, and the
       plan fell back to LIVING ROOM (Marek: nesmyslne nazvy mistnosti). The old
       sauna handed the asset its own table, {i18n, def, col}, and the asset drew
       from it; this does the same for exactly the keys the asset lacks. A key
       the asset has keeps the asset's own row. */
    var ROOM_ROWS = [];

    function _roomTableOnce() {
      if (_roomsFrozen) return _roomsFrozen;
      var F = _FL();
      var all = (F && F.draw && F.draw.ROOMS) ? F.draw.ROOMS : null;
      /* the pristine list or nothing -- a shorter one is this product's own
         answer coming back round */
      if (!all || all.length < 16) return null;
      var out = [], i, j, r, k, hit;
      for (i = 0; i < ROOM_KEYS.length; i++) {
        hit = null;
        for (j = 0; j < all.length; j++) {
          r = all[j]; k = r && (r.i18n || r.key);
          if (k === ROOM_KEYS[i]) { hit = r; break; }
        }
        if (!hit && ROOM_ROWS && ROOM_ROWS.length) {
          for (j = 0; j < ROOM_ROWS.length; j++) {
            if (ROOM_ROWS[j] && ROOM_ROWS[j].i18n === ROOM_KEYS[i]) {
              hit = { i18n: ROOM_ROWS[j].i18n, def: ROOM_ROWS[j].def, col: ROOM_ROWS[j].col };
              break;
            }
          }
        }
        if (hit) out.push(hit);
      }
      if (out.length !== ROOM_KEYS.length) return null;
      _roomsFrozen = out;
      return out;
    }

    /* =========================================================================
       specExport -- EVERYTHING A DRAWING NEEDS, AND NOTHING THAT CAN ONLY LIVE
       HERE.

       The documentation page must not load this file. Twelve products are coming
       out of this one and a page that imports a product lib has to import twelve;
       a page that reads a schema reads one. So this hands over plain JSON --
       survives stringify and parse, no THREE objects, no functions, no live
       references into STATE.

       WHAT IT DOES NOT DO IS GUESS. Anything the drawing will want and this
       product cannot answer goes in gaps[] by name. A schema that fills a hole
       with a plausible number produces a drawing that is wrong and looks right,
       and there is no way to tell afterwards which numbers were real.

       v1: the plan is complete -- outline, walls, partitions, openings, room
       pins, storeys. Elevations are not: the roof is here but the material roles
       are thin and sections are not in this round at all.
       ========================================================================= */
    var SPEC_SCHEMA = 1;
    var _specNote = 'not exported yet';

    function _specR(v) { return Math.round(v * 10000) / 10000; }

    function specExport() {
      var gaps = [];
      var P = poly(), i;

      var outline = [];
      for (i = 0; i < P.length; i++) outline.push([_specR(P[i][0]), _specR(P[i][1])]);

      var wt = wallThickness(), pt = partThickness();
      /* every opening on the plan, by the wall it sits in (core-v352) */
      (function () {
        var HS = STATE.holes || [], i, G;
        out.holes = [];
        for (i = 0; i < HS.length; i++) {
          G = _holeGeom(HS[i]);
          if (!G) continue;
          out.holes.push({ id: HS[i].id, kind: HS[i].kind,
                           a: [_specR(G.p0[0]), _specR(G.p0[2])], b: [_specR(G.p1[0]), _specR(G.p1[2])],
                           w: _specR(HS[i].w), sill: _specR(HS[i].sill || 0), h: _specR(HS[i].h || 0) });
        }
      })();
      var S = storeys(), wallH = S.length ? S[0].h : 0;

      var walls = [], sg = _wallSegs();
      for (i = 0; i < sg.length; i++) {
        walls.push({
          id: sg[i].id, a: [_specR(sg[i].a[0]), _specR(sg[i].a[1])],
          ux: _specR(sg[i].ux), uz: _specR(sg[i].uz),
          len: _specR(sg[i].len), thick: _specR(wt), h: _specR(wallH)
        });
      }

      /* WALLS THE PRODUCT BUILDS BESIDE THE OUTLINE (core-v529): the A-frame's
         side wings -- the same shape, flagged extra, with their storey and foot,
         so an opening recorded on them (wall 'WG-L-outer') finds its wall */
      var XS = _prodSegs();
      for (i = 0; i < XS.length; i++) {
        var xs = XS[i];
        walls.push({ id: xs.id, a: [_specR(xs.x0), _specR(xs.z0)], ux: _specR((xs.x1 - xs.x0) / xs.len), uz: _specR((xs.z1 - xs.z0) / xs.len),
                     len: _specR(xs.len), thick: _specR(wt), h: _specR(xs.h || 0), y0: _specR(xs.y0 || 0), storey: xs.storey | 0, extra: true });
      }
      /* THE PLAN OF EVERY STOREY AS THE PRODUCT DRAWS IT (core-v529): where a
         product answers __PROD._planPoly / _planOps (the A-frame narrows floor
         by floor, its ground floor takes the side wings in, floor 2 the sunk
         dormer's slots), each storey's own outline and the openings on its
         edges (seg = S0-W<edge of THAT outline>), so the drawings show what the
         plan step shows */
      var plans = [];
      if (typeof __PROD._planPoly === 'function') {
        var nSt = storeys().length, stq;
        for (stq = 0; stq < nSt; stq++) {
          var PPs = null, OPs = null;
          try { PPs = __PROD._planPoly(stq); } catch (ePP) { PPs = null; }
          if (!PPs || PPs.length < 3) continue;
          try { OPs = (typeof __PROD._planOps === 'function') ? __PROD._planOps(stq, null) : null; } catch (ePO) { OPs = null; }
          plans.push({ storey: stq, outline: PPs.map(function (q) { return [_specR(q[0]), _specR(q[1])]; }),
                       openings: (OPs || []).map(function (o) { return { wall: o.seg, off: _specR(+o.at || 0), w: _specR(+o.w || 0), h: _specR(+o.h || 0), sill: _specR(+o.sill || 0), kind: o.kind || 'window', code: o.code || null, fix: !!o.fix }; }) });
        }
      }

      /* a partition is stored as an axis and a coordinate; the drawing wants the
         same shape as a wall, so it is given one */
      var parts = _normParts(), partitions = [];
      for (i = 0; i < parts.length; i++) {
        var pw = parts[i], sp = _partSpanH(pw), vert = (pw.o || pw.dir) === 'v';
        if (!sp) continue;
        partitions.push({
          id: 'P' + (pw.id | 0),
          a: vert ? [_specR(pw.c), _specR(sp[0])] : [_specR(sp[0]), _specR(pw.c)],
          ux: vert ? 0 : 1, uz: vert ? 1 : 0,
          len: _specR(sp[1] - sp[0]), thick: pw.sep ? 0 : _specR((typeof pw.t === 'number' && pw.t > 0) ? pw.t : (pw.bear ? wallThickness() : pt)), h: _specR(wallH), bear: !!pw.bear, sep: !!pw.sep
        });
      }

      var ops = STATE.openings || [], openings = [];
      for (i = 0; i < ops.length; i++) {
        var o = ops[i];
        if (!o || o.host === 'roof') continue;
        openings.push({
          wall: o.seg, off: _specR(o.at), w: _specR(o.w), h: _specR(o.h),
          sill: _specR(o.sill || 0), kind: o.kind || 'window', fix: !!o.fix
        });
      }

      /* interior doors ride a partition, not a wall, and the drawing needs both */
      var ds = STATE.doors || [], doors = [];
      for (i = 0; i < ds.length; i++) {
        if (!ds[i]) continue;
        doors.push({
          part: 'P' + (ds[i].pid | 0), off: _specR(ds[i].u), w: _specR(ds[i].w || 0.80),
          sw: (ds[i].sw | 0), slide: (ds[i].slide | 0), pass: !!ds[i].pass,
          /* core-v537: a closet door -- 'pair', 'bifold' or 'bypass', '' for any other */
          kind: ds[i].kind || ''
        });
      }

      var pins = STATE.roomNames || [], rooms = [];
      for (i = 0; i < pins.length; i++) {
        if (!pins[i]) continue;
        rooms.push({ x: _specR(pins[i].x), z: _specR(pins[i].z), k: pins[i].k || '' });
      }

      var levels = [];
      for (i = 0; i < S.length; i++) {
        levels.push({ i: S[i].i, floor: _specR(S[i].y0), clear: _specR(S[i].h), head: _specR(S[i].head) });
      }

      var rp = [], planes = [];
      try { rp = roofPlanes() || []; } catch (eR) { gaps.push('roof: roofPlanes threw -- ' + (eR && eR.message)); }
      for (i = 0; i < rp.length; i++) {
        var pl = rp[i], pts = [], q;
        for (q = 0; q < (pl.pts || []).length; q++) {
          pts.push([_specR(pl.pts[q][0]), _specR(pl.pts[q][1]), _specR(pl.pts[q][2])]);
        }
        planes.push({ id: pl.id, kind: pl.kind, pitch: _specR(pl.pitch || 0), pts: pts,
                      ridge: !!pl.ridge, area: _specR(pl.area || 0) });
      }

      var layers = [];
      for (i = 0; i < _layers.length; i++) {
        var L = _layers[i];
        layers.push({ id: L.id, role: L.role || null, claims: (L.claims || []).slice() });
        if (!L.role) gaps.push('layer ' + L.id + ': no role -- a saved sheet cannot key on it');
        else if (!isRole(L.role)) gaps.push('layer ' + L.id + ': role "' + L.role + '" is not one of the seven');
      }

      /* WHAT IS NOT HERE, NAMED. */
      if (!STATE.roof || STATE.roof.type == null) gaps.push('roof: no type');
      if (!planes.length) gaps.push('roof: no planes -- elevations cannot be drawn');
      if (!walls.length) gaps.push('walls: none');
      if (!levels.length) gaps.push('storeys: none -- no floor level, no wall height');
      gaps.push('materials: not carried yet -- a role has no material behind it, so an elevation has no hatch or finish');
      gaps.push('sections: out of scope in v1, as agreed');
      if (rooms.length < 1) gaps.push('rooms: no pins -- rooms will draw unnamed');

      var spec = {
        schema: SPEC_SCHEMA,
        slug: PRODUCT,
        version: __PROD.VERSION,
        core: CORE_VERSION,
        units: STATE.isMetric ? 'm' : 'ft',
        projectId: (function () { try { return STATE.projectId || null; } catch (e) { return null; } })(),
        outline: outline,
        walls: walls,
        plans: plans,
        partitions: partitions,
        openings: openings,
        doors: doors,
        rooms: rooms,
        storeys: levels,
        roof: { type: (STATE.roof && STATE.roof.type) || null,
                pitch: _specR((STATE.roof && STATE.roof.pitch) || 0),
                planes: planes },
        layers: layers,
        materials: [],
        gaps: gaps
      };

      var kb = 0;
      try { kb = Math.round(JSON.stringify(spec).length / 102.4) / 10; } catch (eS) { kb = -1; }
      _specNote = 'v' + SPEC_SCHEMA + ' | ' + walls.length + ' wall(s)' + (XS.length ? (' (' + XS.length + ' the product\'s own)') : '') + ', ' + plans.length + ' storey plan(s), ' + partitions.length +
                  ' partition(s), ' + openings.length + ' opening(s), ' + doors.length + ' door(s), ' +
                  rooms.length + ' room pin(s), ' + planes.length + ' roof plane(s), ' +
                  layers.length + ' layer(s) | ' + kb + ' kB' +
                  (gaps.length ? ('  <-- ' + gaps.length + ' gap(s)') : '  no gaps');
      if (_specNote !== _specSaid) { _specSaid = _specNote; _log('spec     export ' + _specNote); }
      return spec;
    }
    var _specSaid = '';

    /* THE PLAN OF THE STOREY ON SHOW (core-v513, Marek: pudorysy maji mit
       sirku skutecneho patra, ne zakladny; jen okna a dvere toho patra). A
       product whose storeys differ -- the A-frame narrows with every floor --
       answers __PROD._planPoly(storey) with that storey's outline (the same
       corners in the same order, so the walls keep their ids) and
       __PROD._planOps(storey, wallId) with the openings drawn there. Only the
       plan asks them; the 3D build, the footings and the roof keep poly(). */
    var _planHookNote = '';
    /* THE PLAN'S WALL REACHES PAST THE OUTLINE (core-v580, RANCH block house:
       the outline is the wall AXIS -- the masonry is laid centred on it -- and
       the build's wall is the half block from the axis in, so the plan drew
       half the block: the inner face right, a wall half as thick as it is).
       A product answers __PROD._planOut() with how far the drawn wall reaches
       past the outline (half the block) and the plan host hands it on as
       planOut(): the floorplan asset (v168) draws the poche, the openings'
       breaks and the outline's koty that much further out. Only the DRAWING
       moves -- the outline, the wall the rooms are measured from, the openings'
       places, the plan reader and the build are what they were. No answer,
       nothing changes. */
    function _planOut() {
      var h = 0;
      try { if (typeof __PROD._planOut === 'function') h = +__PROD._planOut() || 0; } catch (e) { h = 0; }
      return (h > 0.001 && h < 1) ? h : 0;
    }
    function _planPolyNow() {
      var P = null;
      try { if (_planWanted() && typeof __PROD._planPoly === 'function') P = __PROD._planPoly(STATE.planStorey | 0); } catch (e) { P = null; }
      return (P && P.length >= 3) ? P : poly();
    }
    function _planOpsOn() {
      if (_planWanted() && typeof __PROD._planOps === 'function') {
        var key = arguments.length ? arguments[arguments.length - 1] : null, want = null;
        if (key && typeof key === 'object') want = key.id || (typeof key.i === 'number' ? ('S0-W' + key.i) : null);
        else if (typeof key === 'string') want = key;
        else if (typeof key === 'number') want = 'S0-W' + key;
        var r = null;
        try { r = __PROD._planOps(STATE.planStorey | 0, want); } catch (e) { r = null; }
        if (r) return r;
      }
      return _opsOn.apply(null, arguments);
    }
    function planHost() {
      return {
        poly:        function () { return _planPolyNow(); },
        wallSegs:    function () { return _wallSegs(_planPolyNow()); },
        partitions:  _normParts,
        setParts:    function (ps) {
          /* THE PLAN ONLY EVER SEES ONE STOREY, SO IT ONLY EVER HANDS ONE BACK
             (core-v413, Marek: kdyz oriznu nosnou zed v druhem patre, zmizi i z
             prvniho). It used to replace the whole list with its copies -- the
             walls of the other storeys went with them, and st and cutOn with
             every wall. Now: the other storeys' walls are kept as they are, a
             wall the plan returns takes its old st, cutOn and bear back by id,
             and a load-bearing wall the plan dropped on a storey above its own
             is cut away on that storey only, not removed. */
          var old = STATE.parts || [], byId = {}, qi, sv = STATE.planStorey | 0;
          for (qi = 0; qi < old.length; qi++) if (old[qi] && old[qi].id != null) byId[old[qi].id] = old[qi];
          var shown = _normParts(), shownIds = {};
          for (qi = 0; qi < shown.length; qi++) shownIds[shown[qi].id] = true;
          var back = ps || [], backIds = {}, out = [];
          for (qi = 0; qi < back.length; qi++) if (back[qi] && back[qi].id != null) backIds[back[qi].id] = true;
          /* walls the plan could not see stay untouched */
          for (qi = 0; qi < old.length; qi++) {
            var o = old[qi];
            if (!o) continue;
            if (!shownIds[o.id]) { out.push(o); continue; }
            if (backIds[o.id]) continue;
            /* shown and not returned: binned on this storey */
            if (o.bear && (o.st | 0) < sv) {
              o.cutOn = (o.cutOn || []).slice();
              if (o.cutOn.indexOf(sv) < 0) o.cutOn.push(sv);
              out.push(o);
              _log('partition load-bearing #' + o.id + ' cut away on storey ' + (sv + 1) + ' only -- it still stands on storey ' + ((o.st | 0) + 1));
            }
          }
          /* walls the plan returned: its geometry, our storey fields */
          for (qi = 0; qi < back.length; qi++) {
            var b = back[qi];
            if (!b) continue;
            var was = byId[b.id];
            if (was) { if (was.bear) b.bear = true; b.st = (typeof was.st === 'number') ? was.st : sv; if (was.cutOn) b.cutOn = was.cutOn.slice(); }
            /* the room line (core-v536): the asset says sep itself on every copy
               it hands back; an older asset does not, and the flag is kept */
            if (was && was.sep && !b.hasOwnProperty('sep')) b.sep = true;
            else if (typeof b.st !== 'number') b.st = sv;
            out.push(b);
          }
          STATE.parts = out;
          _normParts(); _dropOrphanDoors();
        },
        rects:       function () { return _decomposeToRects(_planPolyNow()); },
        doors:       _normDoors,
        doorsRef:    _normDoors,
        /* the fitout pieces, kept by the product in its bag; the asset draws
           them and moves them, the product builds and bills them */
        fixturesRef: function () {
          var P = STATE.product = STATE.product || {};
          return P.fixtures = P.fixtures || [];
        },
        setDoors:    function (d) { STATE.doors = d || []; _normDoors(); },
        roomNames:   function () { return (STATE.roomNames = STATE.roomNames || []); },
        /* THE STOREY ON SHOW, AND THE ONE UNDER IT (core-v596, barndo loft,
           Marek: kontext prvniho podlazi 30% opacity). storey() lets the asset
           keep each storey's room names to that storey (a pin carries st on
           an upper storey); underlay() hands the drawing of the storey below
           -- { alpha, poly, wall, lines } from __PROD._planUnder(storey) --
           which the asset (v200) paints faint behind the plan on show */
        storey:      function () { return STATE.planStorey | 0; },
        underlay:    function () {
          var ps = STATE.planStorey | 0;
          if (!ps || !_planWanted()) return null;
          try { return (__PROD && typeof __PROD._planUnder === 'function') ? (__PROD._planUnder(ps) || null) : null; } catch (eU) { return null; }
        },
        rooms:       function () { var F = _FL(); return (F && F.plan && F.plan.rooms) ? F.plan.rooms() : []; },
        /* THE HANDLES ARE ON WHENEVER THE PLAN IS. This answered STATE.cutsOn --
           the cut-handles toggle, a different control for a different thing --
           so the asset's own grips, its room naming and its door editing were
           switched off the whole time the plan was up. The snapshot said
           handles=off and I read past it twice.

           A plan you cannot touch is a picture. On this step the handles ARE the
           plan. */
        handlesOn:   function () { return STATE.view === '2d' && editing('layout') && !_stripPlanLocked(); },
        setHandles:  function () { },
        passDoors:   true,
        /* THE EDGE-INCLUSIVE ONE, AND IT KEEPS THE POLYGON IT IS GIVEN. The
           asset asks about room polygons as well as the outline, and about
           points two centimetres off a wall. */
        inPoly:      _inPolyEdge,
        /* THE TWO THAT WERE NOT THERE, AND WHICH KILLED EVERY TAP ON A
           PARTITION. The log named them three times over -- partAt,
           doorTapAt, movePartTo -- and the contract probe said 23/23 the
           whole time, because it was counting its own list. */
        clipRuns:    _clipRuns,
        pushDoors:   _pushDoors,
        isMetric:    function () { return !!STATE.isMetric; },
        /* which outline sizes stand on their limit: the plan draws those koty
           red with MAX under them (floorplan asset, maxDims hook) */
        maxDims:     function () {
          return { w: STATE.footprint.w >= LIMITS.maxW - 1e-6,
                   l: STATE.footprint.l >= LIMITS.maxL - 1e-6 };
        },
        wallThk:     wallThickness,
        /* core-v580: how far the drawn wall reaches past the outline */
        planOut:     _planOut,
        /* THE PLAN DRAWS THE STUD THE LAYER ACTUALLY FRAMES. 0.12 was typed in
           here while adu-partitions builds a 100 mm stud, so the drawing and the
           model disagreed by 20 mm and neither knew. Tiny hands over its PART_THK
           for exactly this reason.

           wallThk was already right: it answers wallThickness(), the wall this
           product really has. It reads 160 against tiny's number because the
           walls differ, not because the drawing does. */
        partThk:     partThickness,
        doorW:       function () { return STATE.isMetric ? 0.80 : 32 * IN; },
        clamp:       clamp,
        fmtArea:     fmtArea,
        t:           _planT,
        log:         _log,

        /* THE KEY THAT WAS NOT THERE, AND IT IS WHY NOTHING WORKED.

           "H.toast is not a function", thrown out of cycleRoomNameAt. The asset
           tells the person what it just did, or why it refused, by calling
           toast on its host -- and this product never answered it. So the call
           did its work and then died on the way out, and before the guards went
           in it took the whole pointerdown handler with it. Every verb that
           reports anything -- naming a room, placing a door, cutting a
           partition, refusing a move -- goes through this one function.

           One missing line in the contract, and from the outside it read as a
           plan that does not respond at all. There is nothing here to design:
           the shared UI has no toast of its own, so this puts the message in the
           snapshot log, where a tablet can hand it back. */
        toast:       function (msg) { _log('plan: ' + String(msg == null ? '' : msg)); },
        /* the asset rebuilds after its own moves (movePartTo); while a finger
           drags on the plan that is a repaint, the house waits for the
           release (core-v617) */
        rebuild:     function () { if (_drag.on && !_dragOff() && _planWanted()) { drawPlan(); return; } rebuild(); },
        save:        savePref,
        /* WHICH ROOMS A HOUSE MAY HAVE. Names and colours live in the asset so a
           bathroom is the same blue in every product; this says only which of
           them this line offers. */
        /* --------------------------------------------------------------------
           WHAT THE DRAWING ASKS FOR, ANSWERED.

           drawFloor wants 23 things from its host and this product answered 15.
           The missing eight are not new capability -- each is something this file
           already knows and had simply never been asked for, because until now
           nothing ever called the drawing.

           Where the product genuinely has no such thing -- no modules to stack,
           no doorway standing open -- the honest answer is an empty list rather
           than a guess. The asset then draws nothing for it instead of drawing
           something wrong.
           -------------------------------------------------------------------- */

        /* ONE BUILDING, AT THE ORIGIN. The asset offsets everything by the first
           module's position so a container yard can hold several; a house is one
           and sits where its polygon says. */
        modules:     function () { return [{ x: 0, z: 0 }]; },

        getFloors:   function () { return clamp(STATE.storeys | 0, 1, 2); },
        getView:     function () { return STATE.view; },
        getVariant:  function () { return STATE.roof.type; },

        /* A TERRACE IS AN AREA, and this product has none yet. Empty is the right
           answer: when one arrives it is rects in the module's own frame, the
           same shape every other product hands over. */
        /* THE TERRACE ON THE PLAN (core-v564, Marek: pokud existuje v projektu
           terasa, mela by byt videt i v pudoryse -- v nahledu pudorysu ji
           mame). It is handed over the way the plan draws an outdoor area: its
           rectangle, in its own colour from the room table, with its name and
           its area, under the building */
        areas:       function () {
          var TR = null;
          try { TR = _terraceRect(); } catch (eTR) { TR = null; }
          if (!TR || !(TR.x1 > TR.x0) || !(TR.z1 > TR.z0)) return [];
          return [{ name: _t('rooms.terrace', 'Terrace'), col: '#C6CBB9', area: (TR.x1 - TR.x0) * (TR.z1 - TR.z0),
                    rects: [{ x0: TR.x0, z0: TR.z0, x1: TR.x1, z1: TR.z1 }] }];
        },

        doorsLive:   _doorsLive,
        doorGaps:    _doorGaps,
        doorFree:    _doorFree,
        doorSeg:     _doorSeg,

        /* WHAT IS UNDER THE FINGER RIGHT NOW, said in the type the asset reads.
           It tests dragPart() >= 0 and treats dragDoor() as a door object; false
           answered both questions by accident rather than on purpose.

           And the answer matters: while a door is held, the drawing puts a kot on
           the gap left and right of it, so the distance to its neighbours can be
           read WHILE it is being slid. Answering null meant sliding a door blind. */
        dragPart:    function () { return _partDrag ? _partDrag.i : -1; },
        dragDoor:    function () { return _doorDrag ? _doorDrag.d : null; },

        /* NOT FOOTINGS. I read this as foundations -- piers, stacked modules --
           and answered an empty list, which is why dragging a partition showed no
           numbers at all.

           It is what stands in the way AT THE FOOT OF A PARTITION, along the wall
           that foot slides on: the lining of the shell on one side, the nearest
           parallel wall on the other. The drawing already puts a kot from each of
           those to the wall in your hand, stepped into the room it describes --
           which is the measurement asked for. It got { lo: undefined,
           hi: undefined } from a list, every comparison came out NaN, and every
           kot was skipped without a word. */
        footObstacles: _footObstaclesH,

        /* AND NOW IT ANSWERS THE OPENINGS, because wallSegs finally speaks the
           asset's frame.

           v193 read this right and relied on something that was not there. The
           asset reads sg.a[0] and sg.ux the line after it calls this, and
           wallSegs was handing over { x0, z0, x1, z1 } -- so the first opening
           on any wall threw inside drawFloor, the asset swallowed it, and
           everything after the openings pass was never reached. The boolean did
           not fix that; it hid it, by leaving nothing to iterate.

           The lesson stands even though the reading was correct: this drawing is
           one long pass, so a wrong answer anywhere does not spoil one thing, it
           TRUNCATES the picture from that point on. */
        opsOn:       _planOpsOn,

        /* WHY THE SCISSORS TOOK THE ICON AND LEFT THE WALL.

           The cut worked. cutPartSeg trimmed the partition, wrote s/e on it and
           the icon went, because the icons are built from the ASSET'S idea of how
           far a wall reaches. The slab is drawn from the HOST'S -- H.partSpan --
           and this answered the full inner span of the building every time,
           reading neither s nor e nor which wall an end was trimmed to. So the
           drawing was told the wall still ran the whole way, and drew it.

           It is the asset's answer now, as in tiny. Everything about how far an
           interior wall reaches -- the outline, the trims, the corner it makes
           with the wall it was cut back to -- is the asset's rule. */
        partSpan:    _partSpanH,

        /* the asset owns the room vocabulary and the colours; these pass through
           so a bathroom is the same blue in every product */
        roomName:    function (r, q) {
          var F = _FL();
          return (F && F.plan && F.plan.roomName) ? F.plan.roomName(r, q) : '';
        },
        roomNameIdx: function (r, q) {
          var F = _FL();
          return (F && F.plan && F.plan.roomNameIdx) ? F.plan.roomNameIdx(r, q) : q;
        },

        /* SEE ROOM_KEYS ABOVE. The keys are handed over both ways because the
           asset reads one of them and I do not yet know which. */
        /* THE TABLE IS NOT OFFERED, AND THAT EXPERIMENT IS OVER.

           v192 handed the asset a table filtered out of its own DRAW.ROOMS, to
           get the twelve rooms an ADU may have without copying anybody's
           colours. It went 16 rows, then 12, then TWO -- and two names cycling
           is what he saw on screen. The asset builds its table from what the
           host returns and keeps it, so the filter was reading its own output
           and narrowing every time. Caching the widest list did not save it
           either.

           A vocabulary that shrinks is far worse than one that is too long. The
           keys stay, the table goes, and the asset draws with its own sixteen
           until the list is offered in the form it actually reads. */
        roomKeys: ROOM_KEYS.slice(),

        /* THE TWELVE, FROZEN ONCE. v192 filtered DRAW.ROOMS on every paint and
           the list ate itself -- 16, then 12, then two names cycling. The asset
           builds its table from what the host returns, so a filter that reads
           the live list is reading its own output.

           This reads it ONCE, and only when it is unmistakably the pristine
           sixteen, and only if every one of the twelve is found. Anything less
           and no table is offered at all, which leaves the asset drawing with
           its own list -- long, but never wrong. It cannot narrow twice because
           after the first success it never looks again. */
        roomTable: (function () {
          var rows = _roomTableOnce();
          return rows ? function () { return rows; } : undefined;
        })()
      };
    }

    /* --------------------------------------------------------------------------
       THE PLAN IS GEOMETRY IN THE SCENE, NOT A PICTURE OVER IT.

       I built this as a canvas painted by the asset's drawFloor, and it was the
       wrong architecture for what a plan has to do here. A painted plan is a
       photograph: it has one scale, fixed when it was drawn, so a pinch either
       scales a bitmap or does nothing; nothing in it can be tapped, because there
       is nothing there to tap; and every interaction -- drop a partition, cut
       one, cycle a room name -- has to be rebuilt as hit-testing against numbers
       that the drawing does not keep.

       Tiny does not do that. Its plan is the ORTHO CAMERA looking down at plan
       geometry in the same scene as everything else. Zoom is the camera, so scale
       holds for free. Picking is the same raycast the rest of the product uses.
       A partition is an object, so it can be dragged and cut.

       So this draws flat plan-standard geometry into the group: the poche band on
       the outline, the partitions, the openings as gaps in the band. It is a
       layer like any other, which means it is built by the same rebuild and hides
       with the same viz stage.
       -------------------------------------------------------------------------- */
    /* --------------------------------------------------------------------------
       THE DRAWING IS THE ASSET'S, AND IT ALWAYS WAS.

       I built this twice and both times I built the wrong half.

       First as a canvas painted by drawFloor -- right drawing, no picking. Then I
       tore that out for geometry in the scene, which picks and zooms and draws
       four grey slabs: no room fills, no door swings, no window symbols, no
       dimensions. I had decided tiny drew its own plan. It does not. Tiny calls
       FloorplanLib.plan.drawFloor, and the asset owns the whole vocabulary -- the
       room colours from the UX page, the opening symbols, the poche weight.
       Everything he listed as missing is in that one call.

       So the drawing comes back, and the picking stays: the pointer is converted
       through the SAME ppm/ox/oy the drawing used, so a tap lands where the ink
       is. One transform, stated once, used by both.
       -------------------------------------------------------------------------- */
    /* --------------------------------------------------------------------------
       NO CANVAS. THE PLAN IS THE SCENE, SEEN FROM ABOVE.

       I painted the plan onto a canvas of my own and hid #cf-canvas underneath
       it -- and #cf-canvas is where the 3D scene renders. The floorplan module
       draws into BaseEngine.sceneGroup, which is that scene. So every switch was
       right, every asset loaded, every probe green, and the one surface the
       module was drawing on was the one I had switched off. Ten rounds went into
       that, and every one of them looked from the outside like "the plan does
       not work".

       Tiny never had a second canvas. Its plan is the ortho camera pointed down
       at plan geometry the module puts in the scene, which is why zoom, picking
       and cutting all work there without anything extra: they are the scene's.
       -------------------------------------------------------------------------- */
    /* AND THIS IS WHY AN OPENING COULD NOT BE MOVED ON THE PLAN.

       Not the reach, not the grab, not the drag -- all three were right. The plan
       was simply NOT THERE. It was drawn on the FLOORPLAN step only, and moving a
       window is something you do on DOORS & WINDOWS. Switch that step to the top
       view and _planWanted() went false: no drawing, no asset canvas, and the tap
       fell through to the 3D handler, which is behind the opening-edit toggle. So
       every branch I added since v223 was live code on a step nobody moving a
       window is ever standing on.

       Two versions were spent measuring the grab. The grab was fine.

       A top view of a house with its openings on it IS a plan, so the plan is
       drawn on that step too. Nothing else changes: the step still opens in 3D,
       and 2D is still something you ask for. */
    function _planWanted() {
      /* AND WHILE THE BAR HOLDS IT (core-v553): the plan the Floorplan step
         shows, with no drawer open -- see _cmdPlanView */
      return STATE.view === '2d' && (editing('layout') || editing('openings') || (_barPlan && !wizardOpen()));
    }
    /* THE FITOUTS ARE WORKED ON THE BAR'S PLAN TOO (core-v554, Marek: v
       pudorysu s bannerem floorplan by mely jit delat zakladni operace --
       premistit okna a dvere, ale i fitouts, plus pri zoom in; pricky ani nic
       jineho tady needitovat). The pieces, their plus and their buttons answer
       on the Floorplan step and on the plan the bar holds; the partitions, the
       outline's handles and the room names stay the Floorplan step's. */
    function _fitPlanOn() {
      return (editing('layout') && !_stripPlanLocked()) || (_barPlan && !wizardOpen() && STATE.view === '2d' && !_plainPlan());
    }

    /* what the last paint did, so the snapshot can say it */
    var _planNote = 'not painted yet';

    /* --------------------------------------------------------------------------
       THE ASSET PAINTS THE PLAN, ON ITS OWN CANVAS.

       The signature settled it: drawFloor(ctx, lvl, ppm, ox, oy) takes a 2D
       context, so the drawing was never going to be geometry in the scene -- and
       plan.overlay is the asset's OWN canvas, with ensure, show, hide, paint and
       repaint. Every argument the drawing needs is worked out in there from the
       engine it is handed, which is why paint takes one thing and repaint takes
       none.

       So this stops being a product that draws a plan and becomes a product that
       asks for one. Room fills in the platform's colours, the six opening
       symbols, the poche weight, the koty, the room names -- all of it is behind
       that one call, and none of it can drift from the sauna or the tiny house
       because there is only one copy.

       The old module is left exactly where it is for this step. Its drawing is
       BEHIND this canvas, so nothing doubles up on screen, and it still holds the
       pointer -- moving that is the next step, and moving it in the same breath
       as the drawing would leave nothing to tell the two apart if it went wrong.
       -------------------------------------------------------------------------- */
    /* THE SCISSORS LIVE IN THEIR OWN LAYER, not loose in cf-root, so taking them
       down cannot fail: empty the box. Copied from tiny, which learned it the
       hard way -- buttons left standing over the 3D model. */
    function _iconHost() {
      var root = $('cf-root');
      if (!root) return null;
      var el = $('cf-plan-icons');
      if (!el) {
        el = global.document.createElement('div');
        el.id = 'cf-plan-icons';
        el.style.cssText = 'position:absolute;left:0;top:0;right:0;bottom:0;pointer-events:none;z-index:6;';
        root.appendChild(el);
      }
      return el;
    }

    function _iconsOff() {
      try {
        var F = _FL();
        if (F && F.plan && F.plan.icons) F.plan.icons(null, null, 0, 0);
      } catch (e) {}
      try {
        var el = $('cf-plan-icons');
        if (el) { el.innerHTML = ''; el.style.display = 'none'; }
      } catch (e2) {}
    }

    /* THE DRAWING FOLLOWS THE CAMERA, WHOEVER ELSE IS OR IS NOT HOLDING THE
       LOOP. The asset repaints the plan from the stage's render loop when it
       holds it, and on the second opening of the plan step it did not -- so a
       pan, a zoom or the framing put back by a resize moved the pucks (scene
       objects) and left the drawing where it was painted (Marek: the plan keeps
       coming apart from the overlay). One listener on the ortho controls'
       'change', bound once per controls instance, repaints on every camera
       move: user or programmatic, OrbitControls.update dispatches it. */
    /* THE ROOM'S OWN PLUS: a red round button with a white plus in the lower
       third of every room, on the plan only; a tap opens the drawer filtered
       for that room and puts the plan into fitout mode (the bar with rotate,
       mirror, cross and bin comes up); the black cross ends it (Marek). Placed
       from the overlay's projection, re-placed on every camera move. */
    /* WHAT A PRODUCT DRAWS ON THE PLAN ITSELF (core-v285): a host over the plan,
       the plan's own world-to-screen map, and the product's function to fill it
       -- the sauna's benches and the badge that cycles them. Taps reach what the
       product puts there and nothing else. */
    var PLAN_EXTRA = null, FIT_PLUS_SKIP = null, PDF_BUILDUPS = null, _planExtraQ = 0;
    function _planExtraLater(n) {
      var left = (n == null) ? 2 : n;
      if (left <= 0 || _planExtraQ > 2) return;
      _planExtraQ++;
      var raf = global.requestAnimationFrame || function (fn) { return global.setTimeout(fn, 16); };
      raf(function () {
        _planExtraQ--;
        try {
          var F2 = _FL(), OV2 = F2 && F2.plan && F2.plan.overlay;
          if (OV2) { _planExtraPlace(OV2); _planExtraLater(left - 1); }
        } catch (eL) {}
      });
    }
    function _planExtraPlace(OV) {
      var root = $('cf-root'); if (!root) return;
      var box = $('cf-plan-extra-host');
      if (!box) {
        box = global.document.createElement('div');
        box.id = 'cf-plan-extra-host';
        box.style.cssText = 'position:absolute;left:0;top:0;right:0;bottom:0;pointer-events:none;z-index:7;';
        root.appendChild(box);
      }
      /* THE FIRST FRAME'S MAP IS NOT THE ONE THE PLAN ENDS UP WITH (core-v295).
         The overlay fits the drawing to the view after it paints, so anything
         placed from the map read at that moment lands beside the plan and only
         falls into place at the next zoom (Marek: pri prepnuti do pudorysu je
         lavice mimo sauna room). The placing is repeated on the next two frames,
         by which time the fit has happened. */
      var m = (_planWanted() && OV && OV.map) ? OV.map(_stage()) : null;
      if (!m || typeof PLAN_EXTRA !== 'function') { box.style.display = 'none'; return; }
      box.style.display = '';
      try { PLAN_EXTRA(box, m, { layout: editing('layout') }); if(_handles&&_handles.overlayRefresh)_handles.overlayRefresh(); } catch (eX) { _log('plan extra: ' + (eX && eX.message)); }
    }
    /* THE PLUS ONLY WHERE YOU TAPPED (core-v538, Marek: cervena plus v kazde
       mistnosti -- plus po klepnuti). A plus stood in every room the whole
       time the Floorplan step was open, six of them on a small house, over
       the very spots a finger wants for zooming and dragging. Now the plan
       has none until a room is tapped: that room is tinted and its plus
       appears where the finger was. Tapping the same room again, or outside
       the rooms, puts it away. A room that is furnished by the product or is
       a closet gets neither, as before. */
    /* ---------- SUG: THE SUGGESTED FURNITURE (core-v541) ------------------
       The plan reader furnishes a room the plan draws empty and marks each such
       piece sug (floorplan v118). The SUG tile in the Floorplan drawer puts
       them aside -- off the plan, out of the model and out of the budget, kept
       in the project under product.sugPark -- and puts them back where they
       were. A suggested piece moved, turned or mirrored by hand is the buyer's
       from then on and the tile leaves it alone. */
    var _SUG_SVG = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round">' +
      '<path d="M11 3l1.9 4.9L18 9.8l-5.1 1.9L11 16.6l-1.9-4.9L4 9.8l5.1-1.9z"/><path d="M18.5 14.5l.8 2 2 .8-2 .8-.8 2-.8-2-2-.8 2-.8z"/></svg>';
    function _sugCount() {
      var P = STATE.product || {}, n = 0, L = P.fixtures || [], i;
      for (i = 0; i < L.length; i++) if (L[i] && L[i].sug) n++;
      return n + ((P.sugPark && P.sugPark.length) || 0);
    }
    function sugToggle() {
      var P = STATE.product = STATE.product || {};
      var L = P.fixtures = P.fixtures || [], i, n = 0;
      if (!P.sugOff) {
        var keep = [], park = [];
        for (i = 0; i < L.length; i++) (L[i] && L[i].sug ? park : keep).push(L[i]);
        /* the list is changed in place: the asset holds this very array */
        L.length = 0;
        for (i = 0; i < keep.length; i++) L.push(keep[i]);
        P.sugPark = (P.sugPark || []).concat(park);
        P.sugOff = true; n = park.length;
      } else {
        var back = P.sugPark || [];
        for (i = 0; i < back.length; i++) L.push(back[i]);
        n = back.length;
        P.sugPark = []; P.sugOff = false;
      }
      _log('fitout SUG ' + (P.sugOff ? 'off -- ' + n + ' suggested piece(s) put aside' : 'on -- ' + n + ' suggested piece(s) back on the plan'));
      savePref(); rebuild(); drawPlan();
    }
    function _sugOwn(f, how) {
      if (!f || !f.sug) return;
      delete f.sug;
      _log('fitout ' + f.kind + ' was suggested -- ' + how + ' by hand, it is the buyer\'s now');
    }

    /* ---------- THE BAR ON THE CANVAS (core-v541) ---------------------------
       Marek: lista na canvas -- nahrat / hlas / psat / oddelovac / edit; 50 px
       od spodniho okraje sekce; na ADU pro vsechny, jinde jen pro admina; edit
       je nove soucasti listy. The pill the floating icons wear, at the foot of
       the canvas: upload a floor plan, speak, type, send, and the edit pencil,
       which used to stand alone above the page's own bar. Upload runs the
       buyer's road through the plan import (floorplan v119): the Floorplan step
       opens so the violet plan can be seen, a dark bubble over the bar counts
       the seconds, then offers APPLY and CANCEL -- or says in words that the
       buyer must sign in, or that today's free reading is used and the pack is
       short, with the button that does something about it. The microphone
       writes what is said into the field (the browser's own speech); SEND waits
       for the worker to read a described plan, the next step.
       core-v542 (Marek: kdyz vyjedou drawers edit, listu schovat; divider mezi
       send a edit neni skoro videt; lista jeste niz, polovicni px od spodniho
       okraje): the bar steps aside whenever the edit drawer is out, on every
       width -- the drawer has its own close, and the bar only got in the way
       there; the bubble then moves to the top of the canvas so a reading in
       progress stays in sight. The divider is a 2 px mid-grey rule instead of
       a hairline the white swallowed, and the bar sits 25 px over the foot. */
    var _CMD_FOOT = 25;
    /* core-v543 (Marek: po nahrani pudorysu / zadani se lista zmeni na dve
       ikony a mezi nimi divider -- vlevo ikona floorplan; pri kliknuti na ni
       listu rozbalit na puvodni velikost a muze se floorplan znovu zadavat;
       i ve dvou ikonach vystredene na stred). SEND sends the field to the
       designer (floorplan v120 describe) on the picture's road; once a plan
       is applied -- read or designed -- the bar folds to the plan icon, the
       divider and the pencil, centred as before. The plan icon opens it out
       again; a tap anywhere else folds it back while the field is empty and
       nothing is running. With the drawer out on a tablet the bubble sits
       where the bar was (the foot is free there); on a phone the wizard's own
       bar has the foot, so the bubble stays at the top. */
    /* core-v544 (Marek: nemusi tam byt reading the plan, spise nejakou progress
       animaci a pak rovnou aplikovat -- tlacitko apply je krok navic; nemusime
       pak resit zadne preklady; a rovnou otevrit drawer na step floorplan, at
       je to videt). The reading and the designing show without words (a bar
       in a bubble then, the spinner in the bar since core-v547); the plan is
       written the moment it is ready and the
       model on the screen is the answer (core-v545: no drawer, no tick).
       Words are left only where something went wrong: sign in (the keys
       the trial already has), the free plan used up, or a plan that could not
       be made. */
    /* core-v547 (Marek: pri nacitani floorplanu se hned na zacatku zobrazi
       pill se svymi ikonami floorplan a edit, ale misto floorplan bude
       klasicka loading animace; po nacteni se prepne do stavu po nacteni;
       nemusi tam byt zadny tmavy loading banner, a po nahrani nebo voice
       zadani nemusi byt videt cely banner). The moment a picture is chosen or
       a description sent, the bar folds to its two icons and the plan icon is
       the spinner -- twelve grey spokes going round; when the plan is in, the
       spinner is the plan icon again. No dark banner while it reads; one
       still comes when something went wrong, because that needs words. */
    /* core-v548: A PROJECT WITH ITS PLAN COMES BACK FOLDED, AND THE FIELD
       ASKS WHAT TO CHANGE (Marek: pole meni vyznam -- po nacteni planu je pole
       "Co zmenit..."; po refreshi zustanou ty dve ikony). The asset says
       whether the project's plan came by the bar (floorplan v122, planned);
       once it does, the bar folds -- once per page, so a bar opened by hand
       stays open. The field becomes "Describe a change..." as soon as there
       is a plan AND the asset can change one (user.change, the next asset
       step); until then it keeps asking for a plan, so it never promises
       what it cannot do. */
    /* core-v550 (Marek: pri editaci by banner mel vypadat jinak -- misto
       tlacitka upload po nacteni pudorysu nebo vytvoreni podle slovniho textu
       tlacitko reset; do pole "Popiste zmeny"). Once the field changes the
       plan there is, the bar says so: the upload gives way to a reset, the
       field is tinted sand. Reset asks first (the keys the configurator has:
       reset_changes, master_confirm, cancel), then puts the project back as
       it was before the plan came, when this device kept that copy -- or,
       when it did not, keeps the house and only ends the plan's mode. Either
       way the bar is back where it began: upload, and "describe your plan". */
    var _cmd = { el: null, inp: null, bub: null, mic: null, send: null, edit: null, rec: null, listening: false, lastSt: '', timer: 0, hideT: 0, noteT: 0, noteFadeT: 0, noteArm: false, note: null, wasOff: null, bwArm: false, bwShown: '',
                 plan: null, mini: false, canMini: false, busy: false, loading: false, applyT: 0,
                 plannedSeen: false, mode: '', hadPlan: false, asked: false,
                 idea: null, ideaTxt: null, ideaShown: '', fld: null, clr: null,
                 hintIdea: '', hintOff: false, hintSaid: '', ideaSeen: {}, ideaT: 0,
                 autoDone: false, autoVis: 0, autoLast: 0, touched: false };
    /* OPEN FOR FIVE SECONDS, THEN OUT OF THE WAY (core-v555, Marek: listu
       ukazat celou na 5 vterin, pak ji sbalit, pokud neni zadna akce; pri
       kliknuti uz zustane rozbalena; s nahranym nebo zadanym pudorysem
       defaultne sbalena). Counted while the bar is actually on screen --
       not behind the edit drawer or the edit tools, not in a hidden tab. */
    var _CMD_AUTO_MS = 5000;
    var _CMD_SVG = global.ConfiguratorUI.planIcons;
    /* THE BAR ON EVERY PRODUCT, FOR EVERYONE (core-v571, Marek: vzhledem k
       tomu, ze mame nastaveni pres admin na shopify, muzes to v ui otevrit
       pro vsechny). It was the ADU's for everyone and the other products'
       for an admin only. Which product the homepage banner sends a buyer to
       is set in the theme editor (the plan banner's blocks) and decided by
       the worker; every page with the floorplan asset takes a plan. */
    function _cmdAllowed() {
      var F = global.FloorplanLib;
      return !!(F && F.plan);
    }
    /* THE PLAIN BAR (core-v721, TAURUS, Marek: banner pro nahravani pudorysu
       tady nedava smysl; budou jen dve tlacitka -- prepinani pudorys a Edit).
       __PROD._cmdPlain true: the bar stays folded to its two buttons, the plan
       icon opens the floorplan (the plan strip, as before) and closes it again
       -- no field, no upload, no voice, no ideas. */
    function _cmdPlainOn() { try { return !!(__PROD && __PROD._cmdPlain); } catch (e) { return false; } }
    function _cmdUser() {
      var F = global.FloorplanLib;
      return (F && F.plan && F.plan.importer && F.plan.importer.user) ? F.plan.importer.user : null;
    }
    function _cmdCss() { global.ConfiguratorUI.planBarCss(_CMD_FOOT); }
    function _cmdBtn(cls, svg, label) {
      var b = global.document.createElement('button');
      b.type = 'button';
      b.className = 'cf-cmd-b ' + cls;
      b.innerHTML = svg;
      b.setAttribute('aria-label', label);
      return b;
    }
    function _cmdBarInit() {
      if (_cmd.el) return;
      try { _laneMount(); } catch (eLn) { _log('lane     mount THREW ' + ((eLn && eLn.message) || eLn)); }
      if (!_cmdAllowed()) {
        _log('cmdbar   not on this page -- no floorplan asset (core-v571: on every product that has it, for everyone)');
        return;
      }
      var root = $('cf-root');
      if (!root) return;
      _cmdCss();
      root.classList.add('cf-cmdbar-on');
      var controls = global.ConfiguratorUI.planBar(_t), bar = controls.el, pl = controls.plan, up = controls.upload,
          mic = controls.mic, fld = controls.field, inp = controls.input, send = controls.send,
          dv = controls.divider, edit = controls.edit, clr = controls.clear;
      /* THE TOUR'S PLAY / STOP (core-v632): its own button in the canvas's
         top right corner, the bar as it was -- only for a product that opts
         in with __PROD._tour, on an English page */
      try { _tourMount(); } catch (eTm) { _log('tour     mount THREW ' + ((eTm && eTm.message) || eTm)); }
      var bub = global.document.createElement('div');
      bub.id = 'cf-cmd-bub';
      bub.setAttribute('data-cf-nopinch', '1');
      /* the idea over the plan icon (core-v552): one black button, as the
         AI bubble is */
      var idea = global.document.createElement('button');
      idea.type = 'button';
      idea.id = 'cf-cmd-idea';
      idea.setAttribute('data-cf-nopinch', '1');
      var ideaT = global.document.createElement('span');
      ideaT.className = 'cf-idea-t';
      idea.appendChild(ideaT);
      /* the rights notice under the bar (core-v559) */
      var note = global.document.createElement('div');
      note.id = 'cf-cmd-note';
      note.className = 'cf-gone';
      note.setAttribute('role', 'status');
      note.appendChild(global.document.createElement('span'));
      root.appendChild(bar);
      root.appendChild(bub);
      root.appendChild(idea);
      root.appendChild(note);
      _cmd.note = note;
      /* the picture's slider (core-v562): 0 to 100 %, in steps of 5 */
      var pic = global.document.createElement('input');
      pic.type = 'range'; pic.id = 'cf-cmd-pic'; pic.min = '0'; pic.max = '100'; pic.step = '5';
      pic.setAttribute('data-cf-nopinch', '1');
      pic.setAttribute('aria-label', _t('floorplan_opacity', 'Floorplan opacity'));
      root.appendChild(pic);
      _cmd.pic = pic;
      pic.addEventListener('pointerdown', function (e) { e.stopPropagation(); _cmd.picDrag = true; _cmdTouched('the picture slider'); });
      pic.addEventListener('input', function () {
        _cmdPicPaint();
        var T3 = _trace(); if (!T3) return;
        T3.opacity = Math.max(0, Math.min(1, (+pic.value || 0) / 100));
        _traceRepaint();
      });
      pic.addEventListener('change', function () {
        _cmd.picDrag = false;
        var T4 = _trace();
        _log('cmdbar   the picture under the plan set to ' + Math.round(((T4 && +T4.opacity) || 0) * 100) + ' % on the slider under the bar');
      });
      _cmd.el = bar; _cmd.inp = inp; _cmd.bub = bub; _cmd.mic = mic; _cmd.send = send; _cmd.edit = edit; _cmd.plan = pl;
      _cmd.idea = idea; _cmd.ideaTxt = ideaT; _cmd.fld = fld; _cmd.clr = clr;
      idea.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _cmdIdeaTake(); });
      /* THE SUGGESTION IN THE FIELD (core-v552, Marek: v poli uz nejaky navrh
         s tremi teckami, v jazyce stranky; kdyz kliknu do pole, text se smaze
         a muzu psat; na konci pole krizek pro smazani; odeslat sipkou) */
      clr.addEventListener('pointerdown', function (e) { e.preventDefault(); });
      clr.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _cmdClear(); });
      bar.addEventListener('pointerdown', function () { _cmdTouched('a press on the bar'); }, true);
      inp.addEventListener('focus', function () {
        _cmdTouched('the field took the focus');
        if (_cmd.hintIdea) { _cmd.hintOff = true; _log('cmdbar   a tap into the field -- the suggestion cleared, the field is the buyer\'s'); }
        _cmdHint();
      });
      inp.addEventListener('blur', function () { _cmdHint(); });
      pl.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation(); if (_cmd.loading) return;
        /* the plain bar: the plan icon opens the floorplan (the plan strip, or
           the bar's plan without the strip) and a second tap closes it -- the
           bar itself never opens out */
        if (_cmdPlainOn()) {
          if (_sxPlanOpen()) _sxPlan(false, 'the plan icon (plain bar)');
          else if (_barPlan && STATE.view === '2d') _cmdPlanView(false, 'the plan icon (plain bar)', true);
          else _cmdPlanView(true, 'the plan icon (plain bar)');
          _cmdSync(); return;
        }
        _cmdMini(false, 'the plan icon'); _cmdPlanView(true, 'the plan icon');
      });
      send.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _cmdSend(); });
      inp.addEventListener('input', function () { _cmdHint(); _cmdSendState(); });
      /* a tap away from the bar folds it back -- only once a plan has been
         applied, with the field empty and nothing running. A TAP, NOT A
         TOUCH (core-v553): with the plan up, a pinch or a slide of the plan
         is how it is looked at, so only a short still single-finger tap
         folds the bar and takes the canvas back to 3D */
      var tap0 = null;
      global.document.addEventListener('pointerdown', function (e) {
        tap0 = (e.isPrimary === false) ? null : { x: e.clientX, y: e.clientY, t: Date.now(), id: e.pointerId, tg: e.target };
        if (e.isPrimary === false) _cmd.tapMulti = Date.now();
      }, true);
      global.document.addEventListener('pointermove', function (e) {
        if (tap0 && e.pointerId === tap0.id && Math.abs(e.clientX - tap0.x) + Math.abs(e.clientY - tap0.y) > 12) tap0 = null;
      }, true);
      global.document.addEventListener('pointerup', function (e) {
        var T = tap0; tap0 = null;
        if (!T || e.pointerId !== T.id || Date.now() - T.t > 450 || (_cmd.tapMulti && Date.now() - _cmd.tapMulti < 600)) return;
        if (!_cmd.canMini || _cmd.mini || _cmd.busy || _cmd.listening) return;
        if (String(_cmd.inp.value || '').trim()) return;
        /* the plan strip is being worked in: the bar stays as it is (core-v593) */
        try { if (_sxPlanOpen()) return; } catch (eSp) {}
        var t = T.tg;
        if (t && ((_cmd.el && _cmd.el.contains(t)) || (_cmd.bub && _cmd.bub.contains(t)) || (_cmd.idea && _cmd.idea.contains(t)) || (_cmd.pic && _cmd.pic === t))) return;
        /* THE ADMIN'S OWN BUTTONS ARE NOT A TAP AWAY (core-v581, Marek: kdyz
           zmacknu send log / raw, tak se stranka automaticky nacita). SEND
           LOG, SEND RAW, REF and Fresh assets hang on the page, outside the
           configurator, so a tap on them folded the bar and took the plan
           back to 3D -- the whole model built again, the view reset, which
           looked like a reload. They leave the canvas as it is. */
        try {
          if (t && t.closest && t.closest('#cf-house-dbg-row, #cf-cache-bust-btn')) {
            _log('cmdbar   a tap on the admin buttons (' + String((t.closest('button') || t).id || t.tagName || '?') + ') -- the bar and the view stay as they are');
            return;
          }
        } catch (eAd) {}
        /* THE PLAN LIBRARY'S DRAWER IS THE BAR'S OWN (core-v563, Marek: kdyz
           otevru drawer pres upload, 2d floorplan na canvas se prepne do 3d,
           stejne tak kdyz drawer zavru -- drawer nema ovlivnit canvas). The
           drawer hangs on the page, not in the configurator, so its cross, its
           chips and its tiles were taps off the configurator: the bar folded
           and the plan went back to 3D. A tap in it, or any tap while it is
           out or just went, is the drawer's. */
        if (typeof _plib !== 'undefined' && _plib && _plib.el && (_plib.open || (Date.now() - (_plib.shut || 0) < 500) || _plib.el.contains(t))) return;
        /* ON THE BAR'S PLAN A TAP ON THE CANVAS IS WORK (core-v554): a piece
           picked, a spot for the next one, a window moved -- the plan stays;
           the 3D button on the rail, a change sent or a tap off the
           configurator take it back to 3D */
        var rootT = $('cf-root');
        if (_barPlan && rootT && t && rootT.contains(t)) return;
        _cmdMini(true, 'a tap away from the bar');
        _cmdPlanView(false, 'a tap away from the bar');
      }, true);

      up.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _cmdUpload(); });
      edit.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        var w = _wiz;
        if (!w) { _log('cmdbar   edit: the wizard is not mounted'); return; }
        /* the pencil beside the plan strip opens the edit strip instead
           (core-v593) */
        if (_stripMode()) { if (_sxPlanOpen() || !w.isOpen()) _sxEditOpen(); else w.close(); _cmdSync(); return; }
        if (w.isOpen()) { if (w.dismiss) w.dismiss(); else w.close(); }
        else w.open();
        _cmdSync();
      });
      inp.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); _cmdSend(); } });
      _cmdMicInit();
      /* THE PAGE OPENS FOLDED (ui-v217, Marek: pri refresh stranky nema byt
         rozbaleny spodni banner vubec, jen uzsi varianta pri kliku na ikonu
         fp): the plan icon and the pencil on the dark pill; the field opens
         with the plan strip from the plan icon only. No fold on a timer then
         -- there is nothing open to fold. */
      _cmd.autoDone = true;
      _cmdMini(true, 'the page opens folded -- the plan icon opens the field with the plan strip (ui-v217)');
      if (_cmd.timer) global.clearInterval(_cmd.timer);
      _cmd.timer = global.setInterval(_cmdSync, 400);
      _cmdSync();
      /* AND A PAGE THAT OPENS IN 2D OPENS ON THE PLAN (core-v556): the view is
         remembered, the bar's plan is not -- so a reload in 2D showed a veiled
         canvas. Taken up on the first tick, once boot has built the model. */
      _cmd.bootPlan = true;
      /* core-v570: what the homepage handed over, if anything */
      try { _cmdIntake(); } catch (eIn) { _log('cmdbar   from the homepage THREW ' + ((eIn && eIn.message) || eIn)); }
      _log('cmdbar   on the canvas, ' + _CMD_FOOT + ' px over its foot, aside while the edit drawer is out -- upload, voice, text and SEND, edit; folds after APPLY, and without a plan after ' + (_CMD_AUTO_MS / 1000) + ' s on screen unused (' + (PRODUCT === 'adu' ? 'the ADU, for everyone' : 'an admin') + ')' +
           (_cmdUser() ? '' : ' | the floorplan asset has no import for the bar yet (floorplan v119 needed)'));
    }
    /* the bar steps aside while the edit drawer is out, on a phone and on a
       desk alike (core-v542), and the bubble rides over the bar -- or at the
       top of the canvas while the bar is aside. A microphone still listening
       when the drawer comes out is stopped: nobody can see the field it
       writes into. */
    function _cmdSync() {
      if (!_cmd.el) return;
      var open = false;
      try { open = !!(_wiz && _wiz.isOpen && _wiz.isOpen()); } catch (e) { open = false; }
      /* THE PLAN STRIP IS THE BAR'S OWN (core-v593): the floorplan is edited
         beside the bar that uploads and describes it, so the bar stays */
      var planS = false;
      try { planS = _sxPlanOpen(); } catch (ePs) { planS = false; }
      var wasOpen = open;
      if (planS) open = false;
      /* core-v628 (Marek: kdyz odemknu editaci floorplan, ma zmizet hlavni
         floorplan banner dole a bude se tam objevovat stejny banner jako pri
         edit mode domu): unlocked, the plan's functions own the bottom */
      var planEd = false;
      try { planEd = planS && !_SX.lock; } catch (ePe) { planEd = false; }
      /* AND WHILE THE EDIT TOOLS ARE UP (core-v554): the cross, the bin and
         the fitout buttons stand where the bar stands, so the bar steps aside
         until the cross puts them away */
      var tools = false;
      try { tools = !open && (typeof _opBarEl !== 'undefined') && !!_opBarEl; } catch (eT) { tools = false; }
      var off = open || tools || planEd;
      /* the rail's 3D button ends the bar's plan */
      if (_barPlan && !open && STATE.view !== '2d') {
        _barPlan = false;
        STATE.mode = (_barPlanMode != null) ? _barPlanMode : 5; _barPlanMode = null;
        try { if (typeof _fitEdit !== 'undefined' && _fitEdit) fitEdit(false, 'the plan view closed'); } catch (eF) {}
        try { rebuild(); var stR = _stage(); if (stR && stR.fitCameraToFootprint) stR.fitCameraToFootprint(); drawPlan(); } catch (eRb) { _log('cmdbar   rebuilding for 3D THREW ' + ((eRb && eRb.message) || eRb)); }
        _log('cmdbar   back in 3D -- the plan view let go, the model rebuilt for 3D');
        if (_cmd.canMini && !_cmd.mini && !_cmd.busy && !String((_cmd.inp && _cmd.inp.value) || '').trim()) _cmdMini(true, 'back in 3D');
      }
      if (off !== _cmd.wasOff) {
        _cmd.wasOff = off;
        _log('cmdbar   ' + (off ? ('aside -- ' + (open ? 'the edit drawer is out' : 'the edit tools are up (the cross puts them away)')) : 'back on the canvas'));
        /* THE DRAWER TAKES THE VIEW OVER (core-v553): its steps decide 2D or
           3D; the bar folds, so it comes back as the drawer leaves the model */
        if (off && open) {
          if (_barPlan) { _barPlan = false; _log('cmdbar   plan view handed to the edit drawer'); }
          if (_cmd.canMini && !_cmd.mini && !_cmd.busy && !String((_cmd.inp && _cmd.inp.value) || '').trim()) _cmdMini(true, 'the edit drawer came out');
        }
        if (off && _cmd.listening && _cmd.rec) { try { _cmd.rec.stop(); } catch (eS) {} }
      }
      _cmd.el.classList.toggle('cf-cmd-off', off);
      if (_cmd.note) _cmd.note.classList.toggle('cf-aside', off);
      _cmdPicSync(off);
      _cmd.edit.classList.toggle('cf-on', open);
      _cmdPlannedSync();
      _cmdAutoFold(off);
      if (_cmd.bootPlan) {
        _cmd.bootPlan = false;
        if (STATE.view === '2d' && !wasOpen && !_barPlan) _cmdPlanView(true, 'the page opened in 2D', true);
      }
      var root = _cmd.el.parentNode;
      if (root && root.classList) root.classList.toggle('cf-cmdbar-vis', !off);
      if (_cmd.bub) {
        var desk = (global.innerWidth || 0) >= 600;
        if (off && desk) { _cmd.bub.style.top = 'auto'; _cmd.bub.style.bottom = _CMD_FOOT + 'px'; _cmd.bub.classList.remove('cf-tail'); }
        else if (off) { _cmd.bub.style.top = '12px'; _cmd.bub.style.bottom = 'auto'; _cmd.bub.classList.remove('cf-tail'); }
        else { _cmd.bub.style.top = 'auto'; _cmd.bub.style.bottom = (_CMD_FOOT + (_cmd.el.offsetHeight || 70) + 12) + 'px'; _cmd.bub.classList.add('cf-tail'); }
      }
      _cmdIdeaSync();
      _cmdHint();
    }
    /* ---------- THE IDEAS (core-v551) ---------------------------------------
       Marek: v ramci tokenu floorplanu by se mohla vygenerovat i nejaka otazka
       -- "Pridat presah strechy a pod nim terasu?" -- bublina nad ikonou
       floorplanu, user rozklikne a muze editovat. The designer writes one or
       two with every plan it reads or designs (worker w1.93), in the page's
       language, and the asset keeps them with the project (floorplan v124).
       The first one stands over the plan icon while the bar is folded and
       nothing else is said there. A tap opens the bar with the idea in the
       field, to be edited and sent as a change; once sent it is used up. The
       cross puts it away for good and the next one comes. */
    function _cmdIdeas() {
      var IU = _cmdUser(), L = [];
      try { L = (IU && typeof IU.ideas === 'function') ? (IU.ideas() || []) : []; } catch (e) { L = []; }
      return L;
    }
    /* FOUR SECONDS AT THE START (core-v552; core-v553, Marek: bublinu
       zobrazit dele, 4 vteriny, zarovnanou na stred ikony): each idea stands
       over the plan icon once per page, then it is in the field only */
    var IDEA_MS = 4000;
    function _cmdIdeaSync() {
      var B = _cmd.idea;
      if (!B || !_cmd.el || !_cmd.plan) return;
      if (_cmdPlainOn()) { if (B.classList.contains('cf-on')) B.classList.remove('cf-on'); return; }
      var L = _cmdIdeas();
      var bubOn = !!(_cmd.bub && _cmd.bub.classList.contains('cf-on'));
      var txt = L.length ? String(L[0]) : '';
      var can = !!(txt && _cmd.mini && !_cmd.busy && !_cmd.loading && !_cmd.wasOff && !bubOn && _cmd.plan.offsetWidth);
      var t0 = txt ? _cmd.ideaSeen[txt] : 0, now = Date.now();
      if (can && !t0) { t0 = _cmd.ideaSeen[txt] = now; }
      var show = can && (now - t0 < IDEA_MS);
      if (!show) {
        if (B.classList.contains('cf-on')) { B.classList.remove('cf-on'); _log('cmdbar   the idea over the plan icon went away -- it waits in the field'); }
        _cmd.ideaShown = '';
        return;
      }
      if (txt !== _cmd.ideaShown) {
        _cmd.ideaTxt.textContent = txt;
        _cmd.ideaShown = txt;
        _log('cmdbar   an idea over the plan icon for ' + (IDEA_MS / 1000) + ' s (' + L.length + ' in all): "' + txt + '"');
        if (_cmd.ideaT) global.clearTimeout(_cmd.ideaT);
        _cmd.ideaT = global.setTimeout(function () { _cmd.ideaT = 0; _cmdIdeaSync(); }, IDEA_MS - (now - t0) + 40);
      }
      B.classList.add('cf-on');
      /* over the plan icon, inside the canvas; the tail under the icon */
      var root = _cmd.el.parentNode;
      if (!root || !root.getBoundingClientRect) return;
      var rr = root.getBoundingClientRect(), pr = _cmd.plan.getBoundingClientRect(), br = _cmd.el.getBoundingClientRect();
      var cx = pr.left + pr.width / 2 - rr.left, bw = B.offsetWidth || 220, W = rr.width || root.clientWidth || 0;
      /* centred on the icon, kept inside the canvas */
      var left = Math.max(12, Math.min(W - bw - 12, cx - bw / 2));
      B.style.left = Math.round(left) + 'px';
      B.style.bottom = Math.round(rr.bottom - Math.min(br.top, pr.top) + 10) + 'px';
      B.style.setProperty('--cf-tail', Math.round(Math.max(16, Math.min(bw - 16, cx - left))) + 'px');
    }
    /* a tap on the bubble opens the bar, where the idea waits in the field */
    function _cmdIdeaTake() {
      var t = _cmd.ideaShown;
      if (_cmd.idea) _cmd.idea.classList.remove('cf-on');
      _cmd.ideaShown = '';
      if (t) _cmd.ideaSeen[t] = 1;
      _cmd.hintOff = false;
      _cmdMini(false, 'a tap on the idea');
      _cmdPlanView(true, 'a tap on the idea');
      _cmdHint();
      _log('cmdbar   the idea bubble was tapped -- the bar opened with it in the field: "' + t + '"');
    }
    /* ---------- THE PLAN WITHOUT THE DRAWER (core-v553) ---------------------
       Marek: kdyz spustim nahrani / vytvoreni pudorysu, canvas se musi
       prepnout do pudorysu view (stejne jako v step floorplan, ale bez
       drawer), a stejne tak kdyz ho pak edituji klikem na floorplan; po
       nacteni muze byt videt cely 3D model. The bar holds the plan view while
       a plan is read, designed or changed and while it is open for a change:
       the top camera and the plan drawing of the Floorplan step, with no
       drawer -- to look at, not to drag (the pencil's Floorplan step is for
       that). It lets go once the plan is written, when nothing came, on a
       tap away, on reset, and to the drawer when that comes out. */
    var _barPlan = false, _barPlanMode = null;
    function _cmdPlanView(on, why, plain) {
      on = !!on;
      /* WITH THE ICON STRIP THE PLAN VIEW IS THE PLAN STRIP (core-v593): the
         plan in 2D with the whole floorplan menu beside the bar, its padlock
         first; only its cross ends it, so a reading that comes to nothing or
         a tap away leaves it where the buyer put it */
      /* BUT NOT FROM THE RAIL'S 2D / 3D BUTTON (core-v600, Marek: tlacitko
         prepinani 2d / 3d nema spoustet fp edit, pak koliduje banner fp edit
         se zakladnim bannerem; fp edit zapina az dole tlacitko fp). plain =
         the plan to look at, as the bar showed it before the strip: the top
         camera and the plan drawing, the rail stays on screen for 3D. The
         floorplan editing opens from the canvas bar's plan icon only. */
      if (!plain && _stripOn() && _wiz && _wiz.strip) {
        if (on) _sxPlan(true, why);
        else if (_sxPlanOpen()) _log('cmdbar   the plan strip stays open -- ' + (why || '') + ' (its cross ends it)');
        return;
      }
      var drawer = false;
      try { drawer = !!(_wiz && _wiz.isOpen && _wiz.isOpen()); } catch (eW) { drawer = false; }
      if (drawer) { if (_barPlan) { _barPlan = false; _log('cmdbar   plan view handed to the edit drawer'); } return; }
      if (on === _barPlan) return;
      _barPlan = on;
      try {
        if (on) {
          _barPlanMode = STATE.mode;
          STATE.mode = 3;
          if (STATE.view !== '2d') setView('2d');
          rebuild(); drawPlan();
        } else {
          STATE.mode = (_barPlanMode != null) ? _barPlanMode : 5;
          _barPlanMode = null;
          /* the fitout tools belong to the plan (core-v554) */
          try { if (typeof _fitEdit !== 'undefined' && _fitEdit) fitEdit(false, 'the plan view closed'); } catch (eFe) {}
          try { if (typeof _plusSel !== 'undefined') _plusSel = null; } catch (ePs) {}
          if (STATE.view === '2d') setView('3d');
          rebuild();
          var stP = _stage();
          if (stP && stP.fitCameraToFootprint) stP.fitCameraToFootprint();
          drawPlan();
        }
      } catch (eP) { _log('cmdbar   the plan view THREW ' + ((eP && eP.message) || eP)); }
      _log('cmdbar   ' + (on ? 'the canvas shows the plan without the drawer -- windows, doors and fitouts can be worked here, the plus comes with the zoom; partitions stay the Floorplan step\'s' : 'the canvas back in 3D, the whole building in view') + (why ? (' -- ' + why) : ''));
    }
    /* the suggestion as it reads in the field: the designer's question with
       three dots instead of its question mark */
    function _cmdHintText(t) {
      var x = String(t || '').replace(/\s+/g, ' ').trim().replace(/[\s?!.\u2026]+$/, '');
      return x ? (x + '...') : '';
    }
    /* THE FIELD'S OWN TEXT (core-v552): with a plan and an idea, the idea
       stands in the field as its suggestion -- the arrow sends it as it is; a
       tap into the field clears it for the buyer's own words; the cross puts
       it away. Without one, the field asks what it always asked. */
    function _cmdHint() {
      var inp = _cmd.inp;
      if (!inp) return;
      var focused = (global.document.activeElement === inp), val = String(inp.value || '');
      var L = (_cmd.mode === 'change') ? _cmdIdeas() : [];
      var idea = (L.length && !_cmd.hintOff && !val && !focused && !_cmd.mini) ? String(L[0]) : '';
      var ph;
      if (idea) ph = _cmdHintText(idea);
      else if (focused && _cmd.hintOff) ph = '';
      else ph = (_cmd.mode === 'change') ? _t('cmd_change', 'Describe a change...') : _t('cmd_placeholder', 'Describe your floor plan...');
      if (inp.placeholder !== ph) inp.placeholder = ph;
      inp.classList.toggle('cf-hint', !!idea);
      _cmd.hintIdea = idea;
      if (_cmd.fld) {
        _cmd.fld.classList.toggle('cf-hinted', !!idea);
        _cmd.fld.classList.toggle('cf-x', !!(idea || val));
      }
      if (idea && idea !== _cmd.hintSaid) {
        _cmd.hintSaid = idea;
        _log('cmdbar   the field suggests the idea: "' + _cmdHintText(idea) + '" -- the arrow sends it, a tap into the field clears it, the cross puts it away');
      }
      if (!idea) _cmd.hintSaid = '';
      _cmdSendState();
    }
    /* the cross: the buyer's words cleared, or the suggestion put away for
       good (the next one comes when the bar opens again) */
    function _cmdClear() {
      var inp = _cmd.inp, IU = _cmdUser();
      if (!inp) return;
      if (String(inp.value || '')) {
        inp.value = '';
        _cmd.hintOff = true;
        _log('cmdbar   the cross cleared the field');
        try { inp.focus(); } catch (eF) {}
      } else if (_cmd.hintIdea) {
        var t = _cmd.hintIdea, ok = false;
        try { ok = !!(IU && typeof IU.ideaDone === 'function' && IU.ideaDone(t, 'put away by the cross in the field')); } catch (eD) { ok = false; }
        _cmd.hintOff = true;
        _log('cmdbar   the suggestion was put away: "' + t + '"' + (ok ? '' : ' -- the asset could not take it off (floorplan v124 needed)'));
      }
      _cmdHint();
    }
    /* the project's plan, as the asset tells it (floorplan v122) */
    function _cmdPlanned() {
      var IU = _cmdUser(), st = null;
      try { st = (IU && IU.state) ? IU.state() : null; } catch (e) { st = null; }
      return (st && st.planned) ? st.planned : { from: '', pic: '', picLaid: false };
    }
    function _cmdMode() {
      var IU = _cmdUser(), pl = _cmdPlanned();
      /* a plan this project has from the bar, or one written in this page --
         not merely a bar folded for a reading under way (core-v549) */
      return (IU && typeof IU.change === 'function' && (pl.from || _cmd.hadPlan)) ? 'change' : 'new';
    }
    function _cmdPlannedSync() {
      if (!_cmd.el || !_cmd.inp) return;
      var pl = _cmdPlanned();
      if (pl.from && !_cmd.plannedSeen) {
        _cmd.plannedSeen = true;
        _cmd.canMini = true;
        if (!_cmd.busy) _cmdMini(true, 'the project already has a plan from the bar (' + pl.from + (pl.pic ? ', picture ' + pl.pic : '') + ')');
      }
      var m = _cmdMode();
      if (m !== _cmd.mode) {
        _cmd.mode = m;
        _cmd.el.classList.toggle('cf-cmd-change', m === 'change');
        _cmdHint();
        _log('cmdbar   the field ' + (m === 'change' ? 'changes the plan there is' : 'makes a new plan') +
             ((m === 'new' && pl.from) ? ' -- this asset cannot change a plan yet (user.change)' : ''));
      }
    }
    function _cmdBubble(text, btns) {
      var B = _cmd.bub;
      if (!B) return;
      if (_cmd.hideT) { global.clearTimeout(_cmd.hideT); _cmd.hideT = 0; }
      B.classList.remove('cf-q');
      if (text == null) { B.classList.remove('cf-on'); B.innerHTML = ''; return; }
      B.innerHTML = '';
      var sp = global.document.createElement('span');
      sp.textContent = text;
      B.appendChild(sp);
      (btns || []).forEach(function (bd) {
        var b = global.document.createElement('button');
        b.type = 'button';
        b.textContent = bd.t;
        if (bd.pri) b.className = 'cf-pri';
        b.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); try { bd.fn(); } catch (eF) { _log('cmdbar   bubble button threw: ' + (eF && eF.message)); } });
        B.appendChild(b);
      });
      B.classList.add('cf-on');
      _cmdSync();
    }
    function _cmdUpload() {
      /* THE EDIT DRAWER STAYS AS IT IS (core-v545, Marek: edit drawer ma
         otevrit jen tlacitko edit, nic jineho) -- the upload opens the plan
         library's own drawer (core-v560), and the plan is written into the
         model on the screen */
      _plibOpen();
    }
    /* THE PLAN LIBRARY DRAWER (core-v560, Marek: tlacitko upload by mohlo
       otevrit nejdriv drawer s nasimi pudorysy -- idealne zase nas drawer --
       a v draweru tlacitka vyfotit / nahrat z pocitace / odkaz, ktery ve
       vedlejsim okne otevre hledani "adu floorplan black & white", omezene
       tim, cim je omezeny nas projekt; nahore filtr podle poctu pokoju, vzdy
       jen cisla, kde je aspon jeden pudorys -- pocita se podle loznic, jak se
       ADU prodavaji v USA). The upload button opens it: our drawer's shape --
       in from the right where there is width, up from the bottom on a phone,
       white, the title and the cross of the theme -- and in it, first the
       three ways to bring a plan (the camera, a picture from the device, a
       search on the web in a new window: images, black and white, line
       drawings, the house's size in feet, a rectangle), then the bedroom
       filter with only the numbers some plan has, then the plans this product
       can build, each drawn small from its walls. A tap on one builds it, on
       the road of a plan from words -- no ticket, no quota. A plan of one's
       own (any, for an admin) has a cross: two taps take it out. Half the
       screen on a phone, not a third: while a plan is being picked, the plan
       is what is looked at. */
    var _plib = { el: null, body: null, items: null, counts: null, sel: null, err: '', inCam: null, inDev: null, delArm: '', delT: 0, open: false, gone: {}, mir: {} };
    var _PLIB_SVG = global.ConfiguratorUI.planLibraryIcons;
    function _plibCss() { global.ConfiguratorUI.planLibraryCss(); }
    function _plibDesk() { try { return (global.innerWidth || 0) >= 600; } catch (e) { return false; } }
    function _plibBuild() {
      if (_plib.el) return;
      _plibCss();
      var shell = global.ConfiguratorUI.planLibraryShell({ t: _t,
        noUpload: !!(__PROD && typeof __PROD._libraryItems === 'function' && (__PROD._cmdPlain || __PROD._libraryNoUpload)),
        close: function () { _plibClose('the cross'); },
        camera: function () { _plibClose('the camera'); _cmdPickFile(true); },
        upload: function () { _plibClose('a picture from the device'); _cmdPickFile(false); }
      });
      var d = global.document, el = shell.el, bd = shell.body;
      var af = d.createElement('div'); af.className = 'cf-plib-ar';
      var flt = d.createElement('div'); flt.className = 'cf-plib-flt';
      var flt2 = d.createElement('div'); flt2.className = 'cf-plib-flt cf-baths';
      var grid = d.createElement('div'); grid.className = 'cf-plib-grid';
      var msg = d.createElement('div'); msg.className = 'cf-plib-msg';
      bd.appendChild(af); bd.appendChild(flt); bd.appendChild(flt2); bd.appendChild(grid); bd.appendChild(msg);
      /* a list that did not come is asked for again with a tap (core-v569) */
      msg.addEventListener('click', function (e) { if (!msg.classList.contains('cf-err')) return; e.preventDefault(); e.stopPropagation(); _plib.items = null; _plibFetch('a tap on the message'); });
      d.body.appendChild(el);
      _plib.el = el; _plib.af = af; _plib.flt = flt; _plib.flt2 = flt2; _plib.grid = grid; _plib.msg = msg;
      /* the area's numbers stand in pixels: laid out again when the screen turns */
      global.addEventListener('resize', function () { if (_plib.open && _plib.apaint) { try { _plib.apaint(); } catch (eR) { /* the next render lays them out */ } } });
      d.addEventListener('keydown', function (e) { if (_plib.open && e.key === 'Escape') _plibClose('Escape'); });
    }
    function _plibOpen() {
      var IU = _cmdUser(), ownLib = !!(__PROD && typeof __PROD._libraryItems === 'function');   /* core-v726 */
      if (!IU && !ownLib) { _log('cmdbar   upload: the floorplan asset has no import for the bar (floorplan v119 or later)'); return; }
      if (!ownLib && typeof IU.library !== 'function') {
        _log('cmdbar   upload: the floorplan asset has no plan library (floorplan v136 or later) -- straight to picking a picture');
        _cmdPickFile(false);
        return;
      }
      _plibBuild();
      var el = _plib.el, desk = _plibDesk();
      el.classList.remove('cf-bottom');
      _plib.err = ''; _plib.delArm = '';
      /* THE LIST AT ONCE, IF IT CAME IN THE LAST FIVE MINUTES (core-v569):
         shown as it was and asked again behind it, so the drawer never opens
         on a spinner it already had the answer to */
      var fresh = _plib.cache && (Date.now() - _plib.cache.at) < 300000;
      _plib.items = fresh ? _plib.cache.items : null;
      if (fresh) _plibCount();
      _plibRender();
      global.requestAnimationFrame(function () { el.classList.add('cf-open'); });
      _plib.open = true;
      var tlA = el.querySelector('.cf-plib-note a');
      _log('cmdbar   upload: the plan library drawer -- in from the right, ' + (desk ? '46 % of the screen, 336 to 704 px' : '95 % of the width') + ', the camera and the upload round in the header (core-v569); the terms at ' + (tlA ? tlA.getAttribute('href') : 'none') + '; the canvas stays in ' + (STATE.view === '2d' ? '2D' : '3D') +
           (fresh ? ('; the list of ' + Math.round((Date.now() - _plib.cache.at) / 1000) + ' s ago shown, asked again behind it') : ''));
      _plibFetch(fresh ? 'the drawer opened, the list kept' : 'the drawer opened');
    }
    /* ASK FOR THE LIST, AND DO NOT WAIT FOR EVER (core-v569, Marek: v jednu
       chvili se pudorysy nenacitaly, musel jsem refresh stranku). The ask
       had no end: an answer that never came left the drawer on its spinner
       until the page was reloaded, and a failed one said the library was
       empty. Now: an answer that has not come in 12 s is asked for once
       more; one that fails is asked again after 2 s, once; and when the
       second does not come either, the drawer says so and a tap asks again.
       An answer to an ask that has since been asked again is set aside; a
       late answer to the last ask is still taken. When the last ask fails
       and an older list is kept, that list is shown rather than the error. */
    var _PLIB_WAIT_MS = 12000;
    function _plibFetch(why, again) {
      /* A PRODUCT'S OWN DESIGNS (core-v726, TAURUS, Marek: pridat aspon 4
         designy do vyberu floorplanu). __PROD._libraryItems() returns the
         drawer's tiles itself -- { id, w, d, floor, name, cap, thumb } with the
         picture in the library's own shape -- and __PROD._libraryPick(it,
         mirror) builds the one tapped; the worker's house plans are not asked
         for. No hook, nothing changes. */
      if (__PROD && typeof __PROD._libraryItems === 'function') {
        var own = [];
        try { own = __PROD._libraryItems() || []; } catch (eOw) { own = []; _log('cmdbar   library: the product\'s designs threw ' + ((eOw && eOw.message) || eOw)); }
        _plib.err = ''; _plib.items = own; _plib.cache = { items: own, at: Date.now() };
        _plibCount(); _plibPrune(); _plibRender();
        _log('cmdbar   library: ' + own.length + ' design(s) of the product\'s own (' + why + ', core-v726)');
        return;
      }
      var IU = _cmdUser();
      if (!IU || typeof IU.library !== 'function') return;
      var seq = _plib.seq = (_plib.seq || 0) + 1, done = false, t0 = Date.now();
      if (_plib.items == null && _plib.msg) { _plib.msg.classList.remove('cf-err'); }
      if (_plib.items == null) _plibRender();
      if (_plib.waitT) { global.clearTimeout(_plib.waitT); _plib.waitT = 0; }
      function fail(txt) {
        _log('cmdbar   library: the list did not come (' + txt + ', ' + ((Date.now() - t0) / 1000).toFixed(1) + ' s, ' + why + ')' + (again ? ' -- the second time' : ' -- asked once more'));
        if (!again) { global.setTimeout(function () { if (_plib.seq === seq) _plibFetch(why + ', asked again', true); }, txt === 'no answer' ? 0 : 2000); return; }
        _plib.err = txt;
        if (_plib.items != null && _plib.items.length) return;
        if (_plib.cache && _plib.cache.items.length) {
          _plib.items = _plib.cache.items; _plibCount(); _plibRender();
          _log('cmdbar   library: the list of ' + Math.round((Date.now() - _plib.cache.at) / 1000) + ' s ago shown in its place');
          return;
        }
        _plib.items = []; _plib.counts = {}; _plibRender();
        if (_plib.msg) {
          _plib.msg.textContent = _t('floorplan_library_error', 'The floor plans did not load. Tap here to try again.');
          _plib.msg.classList.add('cf-err');
        }
      }
      _plib.waitT = global.setTimeout(function () {
        _plib.waitT = 0;
        if (done || _plib.seq !== seq) return;
        done = true;
        fail('no answer');
      }, _PLIB_WAIT_MS);
      IU.library(function (err, j) {
        if (_plib.seq !== seq) { _log('cmdbar   library: an older answer came after a newer ask -- set aside'); return; }
        if (_plib.waitT) { global.clearTimeout(_plib.waitT); _plib.waitT = 0; }
        if (done && !(j && !err)) return;
        done = true;
        if (err || !j) { fail(String(err || 'no answer')); return; }
        _plib.err = '';
        if (_plib.msg) _plib.msg.classList.remove('cf-err');
        var nu = (j.items || []).slice(), sig = function (L) { return (L || []).map(function (x) { return x.id + ':' + (x.v || x.at || '') + ':' + (x.g || '') + (x.gr || ''); }).join(','); };
        /* the same list as the one on the screen: left as it is, so a slider
           in the middle of a drag is not built again under the finger */
        if (_plib.items && _plib.items.length && sig(nu) === sig(_plib.items)) {
          _plib.cache = { items: _plib.items, at: Date.now() };
          _log('cmdbar   library: ' + nu.length + ' plan(s) in ' + ((Date.now() - t0) / 1000).toFixed(1) + ' s (' + why + ') -- the same as shown, left as it is');
          return;
        }
        _plib.items = nu;
        _plib.cache = { items: _plib.items, at: Date.now() };
        _plibCount();
        _plibPrune();
        _plibRender();
        _log('cmdbar   library: ' + _plib.items.length + ' plan(s) for this product in ' + ((Date.now() - t0) / 1000).toFixed(1) + ' s (' + why + '), bedrooms ' +
             (Object.keys(_plib.counts).map(function (k) { return (k === '0' ? 'studio' : k) + ': ' + _plib.counts[k]; }).join(', ') || 'none') +
             ', bathrooms ' + (Object.keys(_plib.bcounts || {}).map(function (k) { return (k === '2' ? '2+' : k) + ': ' + _plib.bcounts[k]; }).join(', ') || 'none') +
             ' | very like each other: ' + (j.sim ? (j.sim.groups + ' group(s), ' + j.sim.hidden + ' plan(s) folded under their leader') : 'not said -- a worker before w2.09') + ' (core-v572)');
      });
    }
    function _plibClose(why) {
      if (!_plib.el || !_plib.open) return;
      _plib.open = false;
      _plib.shut = Date.now();
      _plib.el.classList.remove('cf-open');
      if (_plib.delT) { global.clearTimeout(_plib.delT); _plib.delT = 0; }
      _log('cmdbar   library: the drawer closed (' + why + ') -- the canvas stays in ' + (STATE.view === '2d' ? '2D' : '3D') + ', the bar ' + (_cmd.mini ? 'folded' : 'open'));
    }
    /* how many plans there are for each number of bedrooms, from the list as it is */
    /* core-v572: and of bathrooms -- 1, 1.5, and 2 and over as one chip */
    /* core-v573 (Marek: vzdy jen cela cisla): whole bathrooms -- a WC beside
       a bathroom is under the bathroom's number, a WC alone under none */
    var _plibView = null;
    function _plibUI() {
      if (!_plibView) _plibView = global.ConfiguratorUI.planLibraryView({ state: _plib, units: STATE, t: _t, fmtArea: fmtArea, log: _log, pick: _plibPick, roomFilters: __PROD._libraryRoomFilters });
      return _plibView;
    }
    function _plibCount() { return _plibUI().count(); }
    function _plibPrune() { return _plibUI().prune(); }
    function _plibRender() { return _plibUI().render(); }
    function _plibGrid() { return _plibUI().grid(); }
    function _plibPick(it, mirror) {
      /* core-v726: a product's own design is built by the product */
      if (__PROD && typeof __PROD._libraryPick === 'function' && typeof __PROD._libraryItems === 'function') {
        _plibClose('a design picked');
        var okP = false;
        try { okP = !!__PROD._libraryPick(it, !!mirror); } catch (ePk) { _log('cmdbar   library: design ' + it.id + ' THREW ' + ((ePk && ePk.message) || ePk)); }
        _log('cmdbar   library: design ' + it.id + (mirror ? ' MIRRORED' : '') + ' picked -- ' + (it.name || '') + (okP ? ' -- built' : ' -- NOT built') + ' (core-v726)');
        if (okP) { try { global.setTimeout(function () { try { undoPush(); } catch (eU) {} }, 0); } catch (eT) {} }
        return;
      }
      var IU = _cmdUser();
      if (!IU || typeof IU.fromLibrary !== 'function' || _plib.gone[it.id]) return;
      _plibClose('a plan picked');
      _cmd.lastSt = '';
      _cmd.asked = true;
      _cmd.bwArm = false;
      var ok = IU.fromLibrary(it.id, _cmdOnPlan, mirror ? { mirror: true } : undefined);
      _log('cmdbar   library: plan ' + it.id + (mirror ? ' MIRRORED (core-v595)' : '') + ' picked -- ' + ((it.beds | 0) ? (it.beds + ' bedroom(s)') : 'a studio') + ', ' + (it.w || 0).toFixed(2) + ' x ' + (it.d || 0).toFixed(2) + ' m' +
           (it.leva ? ', LEVA' : '') + (it.mine ? ', one of yours' : '') + (ok ? ' -- being built' : ' -- NOT started'));
    }
    /* two taps on the cross take a plan out: the first marks it for three
       seconds, the second asks the worker */
    function _plibDel(it, tile) {
      var IU = _cmdUser();
      if (!IU || typeof IU.libraryDrop !== 'function') return;
      if (_plib.delArm !== it.id) {
        var prev = _plib.grid && _plib.grid.querySelector('.cf-plib-t.cf-del');
        if (prev) prev.classList.remove('cf-del');
        _plib.delArm = it.id;
        tile.classList.add('cf-del');
        if (_plib.delT) global.clearTimeout(_plib.delT);
        _plib.delT = global.setTimeout(function () { _plib.delT = 0; _plib.delArm = ''; tile.classList.remove('cf-del'); }, 3000);
        _log('cmdbar   library: plan ' + it.id + ' marked -- a second tap on its cross takes it out of the library');
        return;
      }
      _plib.delArm = '';
      if (_plib.delT) { global.clearTimeout(_plib.delT); _plib.delT = 0; }
      /* gone from under the finger at once: no tap builds it while the
         worker takes it out */
      tile.style.pointerEvents = 'none'; tile.style.opacity = '0.35';
      _plib.gone[it.id] = 1;
      IU.libraryDrop(it.id, function (err) {
        if (err) { delete _plib.gone[it.id]; tile.classList.remove('cf-del'); tile.style.pointerEvents = ''; tile.style.opacity = ''; _log('cmdbar   library: plan ' + it.id + ' NOT taken out -- ' + err); return; }
        var L = _plib.items || [], k;
        for (k = 0; k < L.length; k++) if (L[k].id === it.id) { L.splice(k, 1); break; }
        _plibCount();
        _plibPrune();
        _plibRender();
        _log('cmdbar   library: plan ' + it.id + ' taken out of the library -- ' + L.length + ' left here');
      });
    }
    /* THE WEB SEARCH, in a new window: images, black and white, line
       drawings -- an ADU, the bedrooms picked in the filter, the house's own
       size in feet, a rectangle: what this configurator reads best */
    /* the camera or a picture from the device: the importer reads the file
       the buyer chose, and the rights notice waits for it (core-v559) */
    function _cmdPickFile(cam) {
      var IU = _cmdUser(), d = global.document;
      if (!IU || !d || !d.body) return;
      var inp = cam ? _plib.inCam : _plib.inDev;
      if (!inp) {
        inp = d.createElement('input');
        inp.type = 'file'; inp.accept = 'image/*';
        if (cam) inp.setAttribute('capture', 'environment');
        inp.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;opacity:0';
        inp.addEventListener('change', function () {
          var fl = inp.files && inp.files[0];
          try { inp.value = ''; } catch (eV) {}
          if (!fl) return;
          _cmd.lastSt = '';
          _cmd.asked = true;
          _cmd.bwArm = true;
          var ok = IU.readFile(fl, _cmdOnPlan);
          _log('cmdbar   upload: ' + (cam ? 'a photo from the camera' : 'a picture from the device') + ', ' + Math.round((fl.size || 0) / 1024) + ' kB -- ' + (ok ? 'being read' : 'NOT read'));
        });
        d.body.appendChild(inp);
        if (cam) _plib.inCam = inp; else _plib.inDev = inp;
      }
      _log('cmdbar   upload: ' + (cam ? 'the camera' : 'picking a picture of the plan') + ' -- the rights stand under the drawer\'s buttons, none on the canvas (core-v566)');
      inp.click();
    }
    /* THE RIGHTS NOTICE (core-v559, Marek: az po vyberu obrazku, jinak ji
       prekryje vyber a zmizi drive nez se obrazek nahraje; 8 vterin behem
       nahravani a pak fade out; ne bublina, ale notice v nasi cervene, pod
       bannerem, mensi font, aby se vesly a byla mezi nimi mezera). One red
       line under the bar, in the foot below it, from the moment a picture is
       chosen: eight seconds, then it fades. The bar does not move -- the
       font steps down until the line fits the canvas. */
    var _CMD_NOTE_MS = 8000, _CMD_NOTE_FADE = 600;
    /* v560: the same line says what it is given -- the rights, or the hint
       to bring a black and white drawing when the reader saw a render or a
       photo */
    /* THE PICTURE'S SLIDER (core-v562, Marek: slider s picture fp opacity dej
       nakonec pod otevreny banner pudorysu, na misto, kde je note o
       vlastnictvi obrazku, po nacteni floorplanu; nemusi byt s popisem;
       nebude v draweru). It shows while a picture lies under the plan, the
       plan is on screen in 2D and the bar is open and on the canvas -- and
       not while the red line under the bar is saying something: the line
       comes first, the slider when it has faded. */
    /* the line filled to the value (core-v563) */
    function _cmdPicPaint() {
      var P = _cmd.pic;
      if (!P) return;
      var v = Math.max(0, Math.min(100, +P.value || 0));
      P.style.backgroundImage = 'linear-gradient(to right,#D2A878 0%,#D2A878 ' + v + '%,rgba(0,0,0,0.13) ' + v + '%,rgba(0,0,0,0.13) 100%)';
    }
    function _cmdPicSync(off) {
      var P = _cmd.pic;
      if (!P) return;
      var T = _trace(), has = false;
      try { has = !!(T && T.has && T.has()); } catch (eH) { has = false; }
      var N = _cmd.note, noteBusy = !!(N && !N.classList.contains('cf-gone'));
      var show = has && !off && !_cmd.mini && STATE.view === '2d' && !noteBusy;
      if (has) _traceSliderHide();
      if (show && !_cmd.picDrag) { var v = String(Math.round(((T && +T.opacity) || 0) * 100)); if (P.value !== v) { P.value = v; _cmdPicPaint(); } }
      if (show && !_cmd.picOn) _cmdPicPaint();
      if (show !== !!_cmd.picOn) {
        _cmd.picOn = show;
        P.classList.toggle('cf-on', show);
        _log('cmdbar   the picture\'s slider ' + (show ? ('under the bar, at ' + P.value + ' %') : ('away -- ' + (!has ? 'no picture under the plan' : (off ? 'the bar is aside' : (_cmd.mini ? 'the bar is folded' : (STATE.view !== '2d' ? 'the 3D view' : 'the line under the bar is speaking')))))));
      }
    }
    function _cmdNoteShow(txt0, what) {
      var N = _cmd.note;
      if (!N) return;
      var txt = txt0 || _t('floorplan_rights', 'Upload only floor plans you own or have the right to use.');
      if (_cmd.noteT) { global.clearTimeout(_cmd.noteT); _cmd.noteT = 0; }
      if (_cmd.noteFadeT) { global.clearTimeout(_cmd.noteFadeT); _cmd.noteFadeT = 0; }
      N.firstChild.textContent = txt;
      N.classList.remove('cf-gone');
      var fs = _cmdNoteFit();
      N.classList.add('cf-on');
      _log('cmdbar   ' + (what || 'upload: a picture chosen -- the rights notice') + ' under the bar for ' + (_CMD_NOTE_MS / 1000) + ' s, red, ' + fs + ' px' +
           (_cmd.wasOff ? ' (the bar is aside, so it waits hidden until the bar is back)' : ''));
      _cmd.noteT = global.setTimeout(function () {
        _cmd.noteT = 0;
        N.classList.remove('cf-on');
        _cmd.noteFadeT = global.setTimeout(function () { _cmd.noteFadeT = 0; N.classList.add('cf-gone'); }, _CMD_NOTE_FADE + 50);
        _log('cmdbar   the ' + (what ? 'hint' : 'rights notice') + ' faded out after ' + (_CMD_NOTE_MS / 1000) + ' s');
      }, _CMD_NOTE_MS);
    }
    /* the font that fits: 12 px on a desk, 11 on a tablet or phone, down by
       half a pixel to 8 until the line is inside the canvas with 12 px a side */
    function _cmdNoteFit() {
      var N = _cmd.note, sp = N && N.firstChild;
      if (!sp) return 0;
      var root = N.parentNode, W = (root && root.clientWidth) || global.innerWidth || 360;
      var fs = ((global.innerWidth || 0) >= 641) ? 12 : 11;
      N.style.fontSize = fs + 'px';
      var k = 0;
      while (fs > 8 && sp.getBoundingClientRect().width > W - 24 && k < 12) { fs -= 0.5; N.style.fontSize = fs + 'px'; k++; }
      return fs;
    }
    function _cmdGo(url) {
      return function () {
        if (!url) return;
        var u = String(url);
        if (/\/account\/login/.test(u) && u.indexOf('return_url') < 0) {
          try {
            /* core-v570: with the word the homepage handed over, until it came */
            var q = String(global.location.search || '');
            if (_cmd.intakeQ && q.indexOf(_cmd.intakeQ.split('=')[0] + '=') < 0) q += (q ? '&' : '?') + _cmd.intakeQ;
            u += (u.indexOf('?') >= 0 ? '&' : '?') + 'return_url=' + encodeURIComponent(global.location.pathname + q);
          } catch (e) {}
        }
        global.location.href = u;
      };
    }
    /* LOADING (core-v547): the bar folded, the spinner where the plan icon
       is; off again, the plan icon comes back */
    function _cmdLoading(on, why) {
      on = !!on;
      if (on === _cmd.loading || !_cmd.plan) return;
      _cmd.loading = on;
      /* A PLAN BEING READ, DESIGNED OR CHANGED IS WATCHED ON THE PLAN
         (core-v553, Marek: kdyz spustim nahrani / vytvoreni pudorysu, canvas
         se musi prepnout do pudorysu -- jako step floorplan, ale bez drawer) */
      if (on) _cmdPlanView(true, why || 'a plan is being read');
      if (on) {
        _cmdBubble(null);
        _cmd.canMini = true;
        _cmdMini(true, why || 'a plan is being read');
        _cmd.plan.innerHTML = _CMD_SVG.spin;
        _cmd.plan.classList.add('cf-load');
      } else {
        _cmd.plan.innerHTML = _CMD_SVG.plan;
        _cmd.plan.classList.remove('cf-load');
      }
      _log('cmdbar   ' + (on ? 'loading -- the spinner in the plan icon' : 'loaded -- the plan icon back') + (why ? (' (' + why + ')') : ''));
    }
    /* A PLAN'S SIZE IN A BUBBLE (core-v565, lifted out of the too-big
       bubble of core-v564): metres as the plan writes them, or feet and whole
       inches -- a sixteenth says nothing here */
    function _cmdFz(m) {
      if (STATE.isMetric !== false) return fmtLen(m);
      var inch = Math.round(m / 0.0254), ft = Math.floor(inch / 12);
      return ft + '\u2032-' + (inch - ft * 12) + '\u2033';
    }
    /* WHICH OTHER PRODUCT BUILDS IT (core-v582): the house line's products
       the page hands over (HOUSE_BOOT.planOthers -- key, url, label, sizes;
       the admin's only for the admin), not this one, the first whose sizes
       take the plan either way round */
    /* core-v585: who 'admin' -- the button only for the admin (the product
       unlisted); the rule of where a plan is published takes every product */
    function _cmdPlanElsewhere(pa, pb, any) {
      var B = global.HOUSE_BOOT || {}, L = Array.isArray(B.planOthers) ? B.planOthers : [], me = String(B.slug || ''), i, adm = false;
      try { var LPa = global.LEVA_PRODUCT || {}; adm = (LPa.isAdmin === true || LPa.isAdmin === 'true'); } catch (eAd) { adm = false; }
      for (i = 0; i < L.length; i++) {
        var o = L[i];
        if (!o || !o.key || !o.url || o.key === me) continue;
        if (!any && o.who === 'admin' && !adm) continue;
        var w0 = Number(o.minW) || 0, w1 = Number(o.maxW) || 0, l0 = Number(o.minL) || 0, l1 = Number(o.maxL) || 0;
        if (!(w1 > 0 && l1 > 0)) continue;
        var a = Math.max(pa, pb), b = Math.min(pa, pb);
        if ((b >= w0 - 0.01 && b <= w1 + 0.01 && a >= l0 - 0.01 && a <= l1 + 0.01) || (a >= w0 - 0.01 && a <= w1 + 0.01 && b >= l0 - 0.01 && b <= l1 + 0.01)) return o;
      }
      return null;
    }
    function _cmdOnPlan(s) {
      if (!s) return;
      /* the picture is chosen: the rights notice (core-v559) */
      if (_cmd.noteArm && s.from === 'picture') { _cmd.noteArm = false; _cmdNoteShow(); }
      var IU = _cmdUser(), cancel = { t: _t('cancel', 'Cancel'), fn: function () { if (IU) IU.cancel(); _cmdBubble(null); _log('cmdbar   plan cancelled'); } };
      var key = s.st + '|' + (s.code || '') + '|' + (s.ready ? 1 : 0), fresh = (key !== _cmd.lastSt);
      if (fresh) {
        _cmd.lastSt = key;
        _log('cmdbar   plan ' + s.st + (s.from ? (' from a ' + s.from) : '') + (s.code ? (' (' + s.code + ')') : '') + (s.ready ? ' -- ready, written straight away' : '') +
             (s.err ? (' -- ' + s.err) : '') + (s.why ? (' -- ' + s.why) : '') +
             (s.size ? (' | ' + s.size.w.toFixed(2) + ' x ' + s.size.d.toFixed(2) + ' m, ' + s.size.rooms + ' rooms, ' + s.size.doors + ' doors') : ''));
      }
      _cmd.busy = (s.st === 'shrinking' || s.st === 'ticket' || s.st === 'reading');
      _cmdSendState();
      if (_cmd.busy) { _cmdLoading(true, s.from === 'text' ? 'a description' : (s.from === 'library' ? 'a plan from the library' : 'a picture')); return; }
      /* A RENDER OR A PHOTO (core-v560, worker w1.98): once it is written or
         has failed, the line under the bar says a black and white drawing
         reads best -- once for each picture */
      if (fresh && _cmd.bwArm && s.from === 'picture' && (s.st === 'applied' || s.st === 'failed' || (s.st === 'done' && !s.ready)) && (s.style === 'render' || s.style === 'photo')) {
        _cmd.bwArm = false;
        _cmdNoteShow(_t('floorplan_bw_hint', 'A black and white line drawing reads best.'), 'the reader saw a ' + s.style + ' -- the black and white hint');
      }
      /* the rest happens once for each new state: a repeat of the same state
         must not apply twice, rebuild the buttons under a finger, or fold the
         bar again after it was opened by hand */
      if (!fresh) return;
      if (s.st === 'done' && s.ready && !_cmd.asked) {
        /* a reading nobody asked for here (a restore brought an old one back
           into view): it is not written (core-v550) */
        _log('cmdbar   a plan is ready that the bar did not ask for -- not written');
      } else if (s.st === 'done' && s.ready) {
        _cmd.asked = false;
        /* NO APPLY BUTTON (core-v544): the plan is written as soon as it fits,
           out of this call (core-v547: no bar to run to the end any more) */
        _cmd.busy = true; _cmdSendState();
        if (_cmd.applyT) global.clearTimeout(_cmd.applyT);
        _cmd.applyT = global.setTimeout(function () {
          _cmd.applyT = 0;
          var U2 = _cmdUser(), st2 = U2 ? U2.state() : null;
          if (!st2 || !st2.ready) { _cmd.busy = false; _cmdSendState(); _cmdLoading(false, 'the plan went before it could be written'); return; }
          /* A THROW MUST NOT LEAVE THE SPINNER ON (core-v590, Marek: obrazovka
             zamrzla po "postavit mensi"): the bar is freed and the log says it */
          var ok = false;
          try { ok = U2.apply(); } catch (eAp) { ok = false; _log('cmdbar   writing the plan THREW ' + ((eAp && eAp.message) || eAp) + ' (core-v590)'); }
          /* a new plan: what an earlier one put aside does not belong to it */
          if (ok) { var P = STATE.product = STATE.product || {}; P.sugPark = []; P.sugOff = false; savePref(); }
          else { _cmd.busy = false; _cmdSendState(); _cmdLoading(false, 'the plan could not be written'); _cmdBubble(_t('plan_failed', 'Could not create the floor plan. Try again.'), [cancel]); }
          _log('cmdbar   written straight away -- ' + (ok ? 'done' : 'FAILED'));
        }, 0);
      } else if (s.st === 'done' && s.question) {
        /* THE DESIGNER ASKS (core-v549): its own words, in the buyer's own
           language -- nothing to translate -- and a field for the answer */
        _cmdLoading(false, 'the designer asks');
        _cmdAsk(s.question);
      } else if (s.st === 'failed' || (s.st === 'done' && !s.ready)) {
        _cmdLoading(false, 'it did not come');
        _cmdPlanView(false, 'nothing was written');
        if (s.code === 'sign_in') {
          /* the trial's own words and button -- nothing new to translate */
          _cmdBubble(_t('trial_login_label', 'Sign in for the full experience'), [{ t: _t('trial_login_btn', 'Sign in'), pri: true, fn: _cmdGo(s.loginUrl || '/account/login') }, cancel]);
        } else if (s.code === 'plan_tokens') {
          /* core-v655: a floor plan out of tokens gets the same card as the PDF */
          if (_lowTokens(_t('plan_tokens', 'Your free floor plan for today is used. Another one takes [n] tokens.').replace('[n]', String(s.need || 7)))) { _log('cmdbar   plan: not enough tokens -- the Not enough tokens card'); return; }
          _cmdBubble(_t('plan_tokens', 'Your free floor plan for today is used. Another one takes [n] tokens.').replace('[n]', String(s.need || 7)),
                     [{ t: _t('buy_tokens', 'Buy tokens'), pri: true, fn: _cmdGo(s.buyUrl || _pdfCreditsUrl()) }, cancel]);
        } else if (s.why === 'size' && s.size && s.lim && s.size.w > 0 && s.lim.w > 0) {
          /* TOO BIG FOR THIS PRODUCT (core-v564, Marek: tento pudorys se
             nenacetl, proc vlastne? Pritom vypada pouzitelne, pokud tedy neni
             moc velky). Plan 1000073678 is 13.10 m long and the ADU goes to
             12.00 m: the bubble says both, the plan's longer side first */
          var pa = Math.max(s.size.w, s.size.d), pb = Math.min(s.size.w, s.size.d), la = Math.max(s.lim.w, s.lim.d), lb = Math.min(s.lim.w, s.lim.d);
          var fz = _cmdFz;
          /* TOO SMALL FOR IT (core-v575, Marek: proc nejde postavit? Tvari se,
             ze pudorys je mensi nez max rozmery stavby). A sauna 5.36 x 2.54 m
             in the ADU, which builds from 4.00 x 4.00 m, was told the largest
             size: the plan under the smallest is told the smallest. */
          var mA = Math.max(Number(s.lim.minW) || 0, Number(s.lim.minL) || 0), mB = Math.min(Number(s.lim.minW) || 0, Number(s.lim.minL) || 0);
          if (mB > 0 && (pa < mA - 0.005 || pb < mB - 0.005)) {
            _cmdBubble(_t('plan_too_small', 'The plan is [size]. This configurator builds from [min].')
                         .replace('[size]', fz(pa) + ' x ' + fz(pb)).replace('[min]', fz(mA) + ' x ' + fz(mB)), [cancel]);
            _log('cmdbar   the plan is too small: ' + pa.toFixed(2) + ' x ' + pb.toFixed(2) + ' m, this product builds from ' + mA.toFixed(2) + ' x ' + mB.toFixed(2) + ' m (core-v575)');
          } else {
            /* AND WHAT CAN BE DONE WITH IT (core-v582, floorplan v171, Marek:
               aby uzivatel dostal odpoved na svuj upload -- oboji). Another
               product of the house line that builds it as drawn opens with the
               reading (nothing read or charged again), and a plan up to 35 %
               over is built here smaller on the buyer's word. */
            var btT = [], OT = _cmdPlanElsewhere(pa, pb), SQ = s.squeeze;
            if (OT && IU && typeof IU.handOff === 'function') {
              btT.push({ t: _t('plan_open_in', 'Open in [product]').replace('[product]', OT.label), pri: true, fn: function () {
                var hid = IU.handOff(OT.key);
                if (!hid) { _log('cmdbar   open in ' + OT.key + ': the reading could not be kept on this device -- nothing opened'); _cmdBubble(_t('plan_failed', 'Could not create the floor plan. Try again.'), [cancel]); return; }
                var u2 = OT.url + (OT.url.indexOf('?') >= 0 ? '&' : '?') + 'read=' + hid;
                _log('cmdbar   open in ' + OT.key + ' (core-v582): ' + u2 + ' -- the plan goes there with its reading');
                global.location.href = u2;
              } });
            }
            if (SQ && SQ.w > 0 && SQ.l > 0 && IU && typeof IU.squeeze === 'function') {
              btT.push({ t: _t('plan_build_at', 'Build it at [size]').replace('[size]', fz(Math.max(SQ.w, SQ.l)) + ' x ' + fz(Math.min(SQ.w, SQ.l))), pri: !OT, fn: function () {
                _cmdBubble(null);
                _cmd.asked = true;
                _cmdLoading(true, 'the plan built smaller on the buyer\'s word');
                /* core-v585 (Marek: zmensene projekty publikovat cele, podle
                   pravidla -- je-li vhodnejsi produkt, zverejnit tam): the
                   plan as drawn goes to the product that builds it, to be
                   published there; with none, this smaller one is published */
                var OTa = _cmdPlanElsewhere(pa, pb, true);
                var okS = false;
                try { okS = IU.squeeze({ lib: !OTa }); } catch (eSq) { okS = false; _log('cmdbar   build it smaller THREW ' + ((eSq && eSq.message) || eSq) + ' (core-v590)'); }
                _log('cmdbar   build it smaller (core-v582): ' + SQ.pct + ' % over, at ' + SQ.w.toFixed(2) + ' x ' + SQ.l.toFixed(2) + ' m -- ' + (okS ? 'it fits, written as any plan' : 'it does NOT fit even so') +
                     ' | published (core-v585): ' + (OTa ? ('as drawn in ' + OTa.key + ', not this smaller one') : 'this smaller one -- no product builds it as drawn'));
                if (!okS) { _cmdLoading(false, 'it did not fit smaller either'); _cmdBubble(_t('plan_failed', 'Could not create the floor plan. Try again.'), [cancel]); }
                else if (OTa && typeof IU.queueFor === 'function') {
                  var LPp = null;
                  try { LPp = (typeof global.levaAnonLocation === 'function') ? global.levaAnonLocation() : null; } catch (eLp) { LPp = null; }
                  global.Promise.resolve(LPp)['catch'](function () { return null; }).then(function (loc) {
                    var place = loc ? { city: loc.city || '', lat: Number(loc.latitude), lng: Number(loc.longitude), cc: loc.countryCode || '' } : null;
                    return IU.queueFor(OTa.key, place);
                  });
                }
              } });
            }
            btT.push(cancel);
            _cmdBubble(_t('plan_too_big', 'The plan is [size]. This configurator builds up to [max].')
                         .replace('[size]', fz(pa) + ' x ' + fz(pb)).replace('[max]', fz(la) + ' x ' + fz(lb)), btT);
            _log('cmdbar   the plan is too big: ' + pa.toFixed(2) + ' x ' + pb.toFixed(2) + ' m, this product builds up to ' + la.toFixed(2) + ' x ' + lb.toFixed(2) + ' m' +
                 ' | offered: ' + (OT ? ('open in ' + OT.key + ' (' + OT.minW + '..' + OT.maxW + ' x ' + OT.minL + '..' + OT.maxL + ' m)') : 'no other product builds it') +
                 (SQ ? (', build it here at ' + SQ.w.toFixed(2) + ' x ' + SQ.l.toFixed(2) + ' m (' + SQ.pct + ' % over)') : ', not here smaller (over 35 %)'));
          }
        } else {
          /* not read, not a house, or it does not fit this product -- the log
             says which */
          _cmdBubble(_t('plan_failed', 'Could not create the floor plan. Try again.'), [cancel]);
        }
      } else if (s.st === 'applied') {
        /* THE MODEL IS THE ANSWER (core-v545, Marek: nemusi se objevovat ani
           check -- proste se zobrazi model a zustanou ty dve ikony): the
           bubble goes, the bar folds, nothing opens */
        _cmd.busy = false;
        _cmdBubble(null);
        _cmdLoading(false, 'the plan was applied');
        /* WRITTEN, AND THE PLAN STAYS IN VIEW (core-v556, Marek: po nacteni
           floorplanu ma zustat videt floorplan; 2D / 3D je tlacitko na railu).
           It went to the 3D model before. The plan is framed afresh, as the
           house may have changed its size with it. */
        if (_barPlan && STATE.view === '2d') { setView('2d'); try { drawPlan(); } catch (eDp) {} _log('cmdbar   the plan was written -- the plan stays in view; 3D is the rail\'s 2D / 3D button'); }
        else _cmdPlanView(true, 'the plan was written -- 3D is the rail\'s 2D / 3D button');
        /* the field empties after a change too (core-v552) */
        if (_cmd.inp && (s.from === 'text' || s.from === 'change')) { _cmd.inp.value = ''; }
        _cmdSendState();
        _cmd.canMini = true;
        _cmd.hadPlan = true;
        _cmdMini(true, 'the plan was applied');
        /* BUILT SMALLER THAN DRAWN (core-v565, floorplan v145, Marek: pokud je
           pudorys do 10 % rozdil, vygenerovat o neco zmenseny, s upozornenim, ze
           tento konfigurator ma omezeni). Plan 1000073678 is 13.05 m long and
           the ADU goes to 12.00 m: it is built at 12.00, the rooms that can give
           it giving it, and the bubble says the three sizes -- as drawn, the
           most this product builds, as built -- each its longer side first.
           It stays until Close; the idea over the plan icon waits for it. */
        var SC = s.scaled;
        /* AND BUILT BIGGER THAN DRAWN (core-v575, floorplan v160): a plan a
           little under the smallest size, built at it */
        if (SC && SC.up && !SC.down && SC.w0 > 0 && SC.l0 > 0 && SC.minW > 0 && SC.w > 0) {
          var ga = Math.max(SC.w0, SC.l0), gb = Math.min(SC.w0, SC.l0), na = Math.max(SC.minW, SC.minL), nb = Math.min(SC.minW, SC.minL);
          var gba = Math.max(SC.w, SC.l), gbb = Math.min(SC.w, SC.l);
          _cmdBubble(_t('plan_grown', 'The plan is [size]. This configurator builds from [min], so it was built at [built].')
                       .replace('[size]', _cmdFz(ga) + ' x ' + _cmdFz(gb)).replace('[min]', _cmdFz(na) + ' x ' + _cmdFz(nb))
                       .replace('[built]', _cmdFz(gba) + ' x ' + _cmdFz(gbb)),
                     [{ t: _t('close', 'Close'), fn: function () { _cmdBubble(null); _log('cmdbar   the built-bigger bubble closed'); } }]);
          _log('cmdbar   the plan was built bigger than drawn: ' + ga.toFixed(2) + ' x ' + gb.toFixed(2) + ' m drawn, ' + na.toFixed(2) + ' x ' + nb.toFixed(2) +
               ' m at the least, built at ' + gba.toFixed(2) + ' x ' + gbb.toFixed(2) + ' m (' + (SC.pct != null ? SC.pct : '?') + ' % under) -- the bubble says so (core-v575)');
        } else if (SC && SC.w0 > 0 && SC.l0 > 0 && SC.maxW > 0 && SC.w > 0) {
          var sa = Math.max(SC.w0, SC.l0), sb = Math.min(SC.w0, SC.l0), ma = Math.max(SC.maxW, SC.maxL), mb = Math.min(SC.maxW, SC.maxL);
          var ba = Math.max(SC.w, SC.l), bb = Math.min(SC.w, SC.l);
          _cmdBubble(_t('plan_scaled', 'The plan is [size]. This configurator builds up to [max], so it was built at [built].')
                       .replace('[size]', _cmdFz(sa) + ' x ' + _cmdFz(sb)).replace('[max]', _cmdFz(ma) + ' x ' + _cmdFz(mb))
                       .replace('[built]', _cmdFz(ba) + ' x ' + _cmdFz(bb)),
                     [{ t: _t('close', 'Close'), fn: function () { _cmdBubble(null); _log('cmdbar   the built-smaller bubble closed'); } }]);
          _log('cmdbar   the plan was built smaller than drawn: ' + sa.toFixed(2) + ' x ' + sb.toFixed(2) + ' m drawn, ' + ma.toFixed(2) + ' x ' + mb.toFixed(2) +
               ' m at most, built at ' + ba.toFixed(2) + ' x ' + bb.toFixed(2) + ' m (' + (SC.pct != null ? SC.pct : '?') + ' % over) -- the bubble says so');
        }
      } else if (s.st === 'idle') {
        _cmdBubble(null);
        _cmdLoading(false, 'nothing is being read');
        _cmdPlanView(false, 'nothing is being read');
      }
    }
    /* RESET LEFT THE BAR (core-v557, Marek: tlacitko restore misto upload je
       blbost). The house before the plan comes back with Undo: the import
       is one step of it. */
    /* THE QUESTION AND ITS ANSWER (core-v549). The bubble carries the
       designer's question as written, a field and a send; the answer goes as
       the next change, and the designer has the question's instruction among
       the earlier ones, so "the south one" is understood. Cancel lets it be. */
    function _cmdAsk(q) {
      var B = _cmd.bub, doc = global.document, IU = _cmdUser();
      if (!B) return;
      _cmdBubble(String(q || ''), []);
      B.classList.add('cf-q');
      var row = doc.createElement('div'); row.className = 'cf-ask';
      var inp = doc.createElement('input'); inp.type = 'text'; inp.className = 'cf-ask-in'; inp.setAttribute('enterkeyhint', 'send');
      var go = doc.createElement('button'); go.type = 'button'; go.className = 'cf-ask-go'; go.innerHTML = _CMD_SVG.send; go.setAttribute('aria-label', 'Send');
      var no = doc.createElement('button'); no.type = 'button'; no.className = 'cf-ask-no'; no.textContent = _t('cancel', 'Cancel');
      function send() {
        var a = String(inp.value || '').trim();
        if (!a || !IU || typeof IU.change !== 'function') return;
        try { inp.blur(); } catch (eB) {}
        _cmdBubble(null);
        _cmd.lastSt = '';
        _cmd.asked = true;
        _cmdLoading(true, 'an answer to the designer');
        _log('cmdbar   answer to the designer: "' + (a.length > 80 ? (a.slice(0, 80) + '...') : a) + '"');
        if (!IU.change(a, _cmdOnPlan, { product: PRODUCT })) _cmdLoading(false, 'the asset refused the answer');
      }
      go.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); send(); });
      inp.addEventListener('keydown', function (e) { if (e.key === 'Enter') { e.preventDefault(); send(); } });
      no.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _cmdBubble(null); _log('cmdbar   the question was let be'); _cmdPlanView(false, 'the question was let be'); });
      row.appendChild(inp); row.appendChild(go); row.appendChild(no);
      B.appendChild(row);
      _cmdSync();
      _log('cmdbar   the designer asks: "' + String(q || '').slice(0, 120) + '"');
    }
    /* SEND (core-v543): the field to the designer, on the road the picture
       takes; the drawer is left as it is (core-v545) */
    function _cmdSendState() {
      if (!_cmd.send || !_cmd.inp) return;
      /* the suggestion in the field can be sent as it is (core-v552) */
      _cmd.send.disabled = _cmd.busy || !(String(_cmd.inp.value || '').trim() || _cmd.hintIdea);
    }
    /* forceNew (core-v570): words from the homepage are a new plan, never a
       change of the one this project has; onPlan: who hears the reading */
    function _cmdSend(forceNew, onPlan) {
      var text = String((_cmd.inp && _cmd.inp.value) || '').trim(), fromIdea = '';
      /* nothing typed: the suggestion goes, in the designer's own words */
      if (!text && _cmd.hintIdea && !forceNew) { text = fromIdea = _cmd.hintIdea; }
      if (!text || _cmd.busy) return false;
      var IU = _cmdUser();
      if (!IU || typeof IU.describe !== 'function') { _log('cmdbar   send: the floorplan asset cannot design from words (floorplan v120 or later)'); return false; }
      if (_cmd.listening && _cmd.rec) { try { _cmd.rec.stop(); } catch (eS) {} }
      try { _cmd.inp.blur(); } catch (eB) {}
      /* the drawer stays as it is (core-v545) */
      _cmd.lastSt = '';
      _cmd.asked = true;
      _cmd.noteArm = false;
      var chg = !forceNew && (_cmdMode() === 'change');
      _cmdLoading(true, chg ? 'a change' : 'a description');
      _log('cmdbar   send: ' + text.length + ' character(s) -- ' + (chg ? 'a change to the plan there is' : 'a new plan') + ' -- "' + (text.length > 80 ? (text.slice(0, 80) + '...') : text) + '"');
      /* the suggestion sent is used up (core-v552) */
      if (fromIdea) {
        try { if (chg && typeof IU.ideaDone === 'function') IU.ideaDone(fromIdea, 'sent from the field'); } catch (eI) {}
        _cmd.hintIdea = ''; _cmd.hintOff = true;
        _log('cmdbar   the suggestion was sent as it stood: "' + fromIdea + '"');
      }
      var cbS = onPlan || _cmdOnPlan;
      var ok = chg ? IU.change(text, cbS, { product: PRODUCT }) : IU.describe(text, cbS, { product: PRODUCT });
      if (!ok) { _cmdLoading(false, 'the asset refused it'); _log('cmdbar   send: the asset refused it'); }
      return !!ok;
    }
    /* ---------- ANY HOUSE HANDS OVER A RECIPE (core-v752) --------------------
       Marek 2026-10-22 (ANY HOUSE thread): OPEN on the any-house page opens
       the product already set to the visitor's recipe. ?recipe=<base64url
       JSON> { shape, storeys, structure, roof, foundation, w, l } -- the
       recipe of assets/configurator-recipe.js. The page starts from the
       product's DEFAULT project, not from what this browser kept (_loadPref0),
       and once that has settled the recipe goes in through the product's own
       setters: the wall system (setStructure), the size (into LIMITS,
       snapped), the L corner (importNotch), the roof stage and the footing.
       What a product cannot take is logged, never forced -- its own rules
       (beforeBuild) still have the last word. ?recipe= then leaves the
       address, so a reload does not apply it again. Nothing here runs
       without ?recipe=. */
    function _recipeRead() {
      var s = '';
      try { s = new URL(global.location.href).searchParams.get('recipe') || ''; } catch (e) { return null; }
      if (!s) return null;
      try { var r = JSON.parse(_b64uDec(s)); return (r && typeof r === 'object') ? r : null; } catch (e2) { return null; }
    }
    function _recipeStrip() {
      try {
        var u = new URL(global.location.href);
        u.searchParams['delete']('recipe');
        if (global.history && global.history.replaceState) global.history.replaceState(null, '', u.pathname + (u.search || '') + (u.hash || ''));
      } catch (e) {}
    }
    function _recipeIntake() {
      var R = _recipeRead();
      if (!R) return;
      var t0 = Date.now();
      _log('recipe   from ANY HOUSE (core-v752): ' + JSON.stringify(R) + ' -- applied once the project has settled');
      function go() {
        var C = global.HouseLib;
        var ready = !!(STATE && STATE.footprint && C && typeof C.rebuild === 'function') && !_initLoads && (Date.now() - t0) >= 600;
        if (!ready && (Date.now() - t0) < 15000) { global.setTimeout(go, 400); return; }
        _recipeStrip();
        try { _recipeApply(R, C || {}); } catch (e) { _log('recipe   THREW ' + ((e && e.message) || e)); }
      }
      global.setTimeout(go, 400);
    }
    function _recipeApply(R, C) {
      var did = [], skip = [], k, ST, off, f = R.foundation, rt = R.roof, st = R.structure;
      function cl(v, a, b) { return Math.max(a, Math.min(b, v)); }
      /* THE WALLS first: a block wall snaps the plan to its module */
      if (st) {
        if (typeof C.setStructure === 'function') {
          k = st === 'timber' ? 'timber' : (st === 'masonry' ? 'clay' : (st === 'icf' ? 'eps' : ''));
          if (k) { try { C.setStructure(k); did.push('structure ' + k); } catch (eS) { skip.push('structure ' + k + ' threw'); } }
          else skip.push('structure ' + st + ' (not offered)');
        } else if (__PROD && typeof __PROD.structureSample === 'function' && (st === 'masonry' || st === 'icf')) {
          STATE.product = STATE.product || {};
          if (st === 'icf') STATE.product.masonry = 'eps';
          else if (!STATE.product.masonry || STATE.product.masonry === 'eps') STATE.product.masonry = 'clay';
          did.push('masonry ' + STATE.product.masonry);
        } else did.push('structure ' + st + ' (the product\'s own)');
      }
      /* THE SIZE: w across, l along the ridge; a T's bar runs along its
         ridge and its depth is what is left of w past the wing */
      if (+R.w > 0 && +R.l > 0) {
        var wing = !!(STATE.wing && STATE.wing.on), fw, fl;
        if (wing) { fw = Math.max(+R.w, +R.l); fl = Math.min(+R.w, +R.l) - (+STATE.wing.l || 0); }
        else { fw = Math.min(+R.w, +R.l); fl = Math.max(+R.w, +R.l); }
        fw = cl(snapDim(cl(fw, LIMITS.minW, LIMITS.maxW), 'w'), LIMITS.minW, LIMITS.maxW);
        fl = cl(snapDim(cl(fl, LIMITS.minL, LIMITS.maxL), 'l'), LIMITS.minL, LIMITS.maxL);
        STATE.footprint.w = fw; STATE.footprint.l = fl;
        did.push('size ' + fw.toFixed(2) + ' x ' + fl.toFixed(2) + ' m');
      }
      /* THE L: the product's own corner (RANCH importNotch), 40 % each way */
      if (R.shape === 'L' && typeof C.importNotch === 'function') {
        var ox = (STATE.origin && STATE.origin.x) || 0, oz = (STATE.origin && STATE.origin.z) || 0;
        var hw = STATE.footprint.w / 2, hl = STATE.footprint.l / 2;
        var N = null;
        try { N = C.importNotch({ x0: ox + hw - STATE.footprint.w * 0.4, x1: ox + hw, z0: oz + hl - STATE.footprint.l * 0.4, z1: oz + hl }); } catch (eN) { N = null; }
        if (N) did.push('L corner ' + N.c + ' ' + N.nx.toFixed(2) + ' x ' + N.nz.toFixed(2)); else skip.push('L (refused)');
      } else if (R.shape && R.shape !== 'I' && !(R.shape === 'T' && STATE.wing && STATE.wing.on)) skip.push('shape ' + R.shape + ' (not offered)');
      /* THE ROOF: the stage of the line's pitched tile, or mono / flat */
      if (rt) {
        off = roofOffer();
        k = (rt === 'halfhip') ? 'hip' : rt;
        if (off && off.indexOf(k) < 0) skip.push('roof ' + rt + ' (not offered)');
        else if (rt === 'gable' || rt === 'hip' || rt === 'halfhip') {
          ST = _roofStages();
          var n = rt === 'gable' ? 0 : (rt === 'halfhip' ? 1 : ST.length - 1);
          if (!n) { STATE.roof.type = 'gable'; STATE.roof.hipEnds = { lo: false, hi: false, wing: false }; }
          else { STATE.roof.type = 'hip'; STATE.roof.hipEnds = { lo: ST[n].lo, hi: ST[n].hi, wing: ST[n].wing }; }
          did.push('roof ' + rt + ' (stage ' + n + ')');
        } else if (rt === 'mono' || rt === 'flat') { STATE.roof.type = rt; did.push('roof ' + rt); }
        else skip.push('roof ' + rt + ' (not offered)');
        if (typeof _spanRule === 'function') setMaxSpan(_spanRule(STATE.roof.type));
        clampPitch();
      }
      /* THE FOOTING: the product's own setter where it has one (RANCH's slab) */
      if (f) {
        if (typeof C.setFoundationRanch === 'function') {
          try { C.setFoundationRanch(f); did.push('foundation ' + f); } catch (eF) { skip.push('foundation ' + f + ' threw'); }
        } else if (FOUNDATIONS.indexOf(f) >= 0) {
          STATE.foundation = f; _stackForFooting(); did.push('foundation ' + f);
        } else skip.push('foundation ' + f + ' (not offered)');
      }
      /* THE STOREYS are the product's own (one, two, a loft): logged only */
      if (R.storeys) {
        var have = STATE.storeys || 1;
        if ((R.storeys === '2' && have !== 2) || (R.storeys === '1' && have !== 1) || R.storeys === '1L' || R.storeys === '1A')
          skip.push('storeys ' + R.storeys + ' (the product has ' + have + ')');
      }
      savePref();
      rebuild();
      try { drawPlan(); } catch (eP) {}
      global.__LEVA_RECIPE_DONE = { did: did, skip: skip };
      _log('recipe   applied: ' + (did.join(', ') || 'nothing') + (skip.length ? ' | NOT TAKEN: ' + skip.join(', ') : ''));
    }

    /* ---------- WHAT THE HOMEPAGE HANDS OVER (core-v570) --------------------
       Marek: po nacteni pudorysu / zadani promptu agent vyhodnoti co je to za
       projekt a rovnou otevre relevantni projekt uz s nahranym pudorysem; z
       draweru se vsemi pudorysy se vybrany otevre ve vedlejsim okne. The
       homepage banner opens this product in a new window with one of three:
         ?read=id  a plan read on the homepage, kept by the worker (w2.06) --
                   taken without reading or charging again (floorplan v152);
         ?plib=id  a plan picked in the banner's library -- as a tile here;
         ?ask=...  the words typed into the banner -- designed as a new plan.
       Once the project has settled (the buyer's own, the shared one or the
       product's default, whichever comes), the bar takes it as if it had
       been asked here, and the plan is written as soon as it fits -- the bar
       does that with every plan. The word goes out of the address at once,
       so a reload does not read or design it again; a sign-in on the way
       comes back to it (the login's return address keeps it). */
    var _CMD_INTAKE_KEYS = ['read', 'plib', 'ask'], _CMD_INTAKE_WAIT_MS = 15000;
    function _cmdIntakeRead() {
      var u = null;
      try { u = new URL(global.location.href); } catch (e) { return null; }
      var r = u.searchParams.get('read'), l = u.searchParams.get('plib'), a = u.searchParams.get('ask');
      if (r && /^[a-f0-9]{16,40}$/.test(r)) return { k: 'read', v: r };
      if (l && /^[a-f0-9]{16,40}$/.test(l)) return { k: 'plib', v: l };
      if (a && String(a).trim()) return { k: 'ask', v: String(a).replace(/\s+/g, ' ').trim().slice(0, 1500) };
      return null;
    }
    function _cmdIntakeStrip() {
      try {
        var u = new URL(global.location.href), k;
        for (k = 0; k < _CMD_INTAKE_KEYS.length; k++) u.searchParams['delete'](_CMD_INTAKE_KEYS[k]);
        if (global.history && global.history.replaceState) global.history.replaceState(null, '', u.pathname + (u.search || '') + (u.hash || ''));
      } catch (e) {}
    }
    function _cmdIntake() {
      var I = _cmdIntakeRead();
      if (!I) return;
      _cmd.intakeQ = I.k + '=' + encodeURIComponent(I.v);
      _cmdIntakeStrip();
      _cmdTouched('a plan from the homepage');
      _log('cmdbar   from the homepage (core-v570): ' + (I.k === 'read' ? ('the plan read there, ' + I.v) : (I.k === 'plib' ? ('library plan ' + I.v) : ('the words "' + I.v.slice(0, 80) + '"'))) +
           ' -- out of the address, taken once the project has settled');
      var t0 = Date.now();
      function go() {
        var IU = _cmdUser(), C = global.HouseLib;
        var ready = !!(IU && STATE && STATE.footprint && C && typeof C.rebuild === 'function') && !_initLoads && !_cmd.busy && (Date.now() - t0) >= 600;
        if (!ready && (Date.now() - t0) < _CMD_INTAKE_WAIT_MS) { global.setTimeout(go, 400); return; }
        if (!IU) { _log('cmdbar   from the homepage: NOT taken -- the floorplan asset has no import here'); _cmd.intakeQ = ''; return; }
        /* who hears it: the bar, and the address word kept for a sign-in on
           the way until the plan has come (or failed for another reason) */
        var cb = function (s) {
          if (s && (s.st === 'done' || s.st === 'applied' || (s.st === 'failed' && s.code !== 'sign_in'))) _cmd.intakeQ = '';
          _cmdOnPlan(s);
        };
        _cmd.lastSt = ''; _cmd.asked = true; _cmd.bwArm = false; _cmd.noteArm = false;
        var ok = false, why = '';
        if (I.k === 'read') {
          if (typeof IU.fromHand === 'function') ok = IU.fromHand(I.v, cb); else why = 'the floorplan asset is older than v152';
        } else if (I.k === 'plib') {
          ok = (typeof IU.fromLibrary === 'function') ? IU.fromLibrary(I.v, cb) : false;
        } else {
          if (_cmd.inp) { _cmd.inp.value = I.v; _cmdSendState(); }
          ok = _cmdSend(true, cb);
        }
        if (!ok) _cmd.intakeQ = '';
        _log('cmdbar   from the homepage: ' + I.k + (ok ? ' -- started' : (' -- NOT started' + (why ? (' (' + why + ')') : ''))) + ' after ' + ((Date.now() - t0) / 1000).toFixed(1) + ' s' +
             ((Date.now() - t0) >= _CMD_INTAKE_WAIT_MS ? ' (the project had not settled -- taken anyway)' : ''));
      }
      global.setTimeout(go, 0);
    }
    /* the bar was used: no fold on a timer from here on */
    function _cmdTouched(why) {
      if (_cmd.touched) return;
      _cmd.touched = true;
      if (!_cmd.autoDone) {
        _cmd.autoDone = true;
        _log('cmdbar   used within ' + (_CMD_AUTO_MS / 1000) + ' s (' + why + ') -- it stays open');
      }
    }
    /* five seconds on screen and nobody used it: fold to the plan icon and
       the pencil. Once settled -- folded, used, or a plan there to fold it by
       its own rule -- it is not asked again on this page, so a bar opened by
       hand from the plan icon stays open. */
    function _cmdAutoFold(off) {
      if (_cmd.autoDone || !_cmd.el) return;
      var why = '';
      if (_cmd.canMini) why = 'the project has a plan -- folded by that rule';
      else if (_cmd.mini) why = 'already folded';
      else if (_cmd.busy || _cmd.loading) why = 'a plan is being read';
      else if (_cmd.listening) why = 'the microphone is listening';
      else if (String((_cmd.inp && _cmd.inp.value) || '').trim()) why = 'there is text in the field';
      if (why) { _cmd.autoDone = true; _log('cmdbar   no fold on a timer -- ' + why); return; }
      var now = Date.now(), hidden = false;
      try { hidden = !!global.document.hidden; } catch (eH) { hidden = false; }
      if (off || hidden) { _cmd.autoLast = 0; return; }
      if (_cmd.autoLast) _cmd.autoVis += Math.min(2500, now - _cmd.autoLast);
      _cmd.autoLast = now;
      if (_cmd.autoVis < _CMD_AUTO_MS) return;
      _cmd.autoDone = true;
      _cmdMini(true, 'nobody used it in ' + (_CMD_AUTO_MS / 1000) + ' s on screen -- the plan icon opens it again, and then it stays open');
    }
    /* folded or open; the reason goes to the log */
    /* ===== THE GUIDED TOUR (core-v631, core-v632) ===========================
       Marek: hlasovy pruvodce. A product opts in with __PROD._tour = true
       (the ADU first). core-v632 (Marek: ikony zpet jak byly, play do
       praveho horniho rohu, at stop nekoliduje s prvky, ktere musi byt videt
       behem prezentace; na jinych jazykovych mutacich zatim ikonu
       nezobrazovat; ikony, kterych se tyka voiceover, muzou pulsovat): the
       play / stop button stands alone in the canvas's top right corner,
       beside the + PDF chip and over the rail, and only on an English page
       (core-v668: or a Spanish one).
       Each step plays its MP3 from Shopify Files (the names of the
       voice-tour sheet) while the icon it talks about pulses, runs its
       animation in cycles, and goes on once the voice has ended and the
       running cycle is done. A voice that is refused or fails makes the
       steps run on their own time with a bubble naming the icon. A touch in
       the configurator or the plan library, or the stop button, ends it;
       the project, the view, the camera, the bar and the Undo history go
       back to where they were. */
    var _TOUR_CDN = 'https://cdn.shopify.com/s/files/1/0803/9815/6119/files/';
    /* THE INTRO IS THE PRODUCT'S OWN (core-v651, Marek: prvni voice over pro
       kazdy projekt specificky, zazni jmeno projektu): tour-intro-<slug>
       first, then the shared tour-intro. The clips were uploaded as
       tour-intro-<slug>.mp3.mp3 (the extension twice in Shopify Files), so
       that name is tried first and the plain one after it; a clip that is
       not there goes to the next name with no gap in the tour. Every other
       step has its one clip. */
    /* THE LANGUAGES WITH A VOICE (core-v668, Marek: druhy jazyk spanelstina,
       vse nahrano): each clip's twin <name>-es, uploaded as <name>-es.mp3.mp3.
       A Spanish page tries the Spanish names first (the product's intro, then
       the shared one) and the English clip after them, so a missing Spanish
       clip still speaks. A page in another language shows no tour. */
    var _TOUR_VOICES = { en: '', es: '-es', de: '', fr: '', cs: '', pt: '', zh: '', ar: '', ja: '', tr: '', ko: '', it: '', pl: '', ro: '', nl: '', el: '', hu: '', sv: '', he: '', fi: '', no: '' };   /* core-v672: German, worker clips only (no Shopify -de set) */
    function _tourLang() {
      var L = '';
      try { L = String((global.HOUSE_BOOT && global.HOUSE_BOOT.locale) || global.document.documentElement.lang || ''); } catch (e) { L = ''; }
      var m = /^([a-z]{2})/i.exec(L);
      return m ? m[1].toLowerCase() : 'en';
    }
    /* THE GENERATED VOICE (core-v670, Marek: Roger z ElevenLabs, nahrej ted):
       a language listed here is spoken from the leva-voice worker first --
       its clips are made by the voice workflow of the leva-workers repo
       (ElevenLabs) as <lang>/<name>.mp3 -- and the Shopify Files clips below
       stay as the fallback, name by name. A name 'voice:<lang>/<file>' is the
       worker's, any other one Shopify's (_tourUrl). */
    var _TOUR_WORKER = 'https://leva-voice.long-cloud-d006.workers.dev/v/';
    var _TOUR_GEN = { en: 1, es: 1, de: 1, fr: 1, cs: 1, pt: 1, zh: 1, ar: 1, ja: 1, tr: 1, ko: 1, it: 1, pl: 1, ro: 1, nl: 1, el: 1, hu: 1, sv: 1, he: 1, fi: 1, no: 1 };   /* core-v671: Spanish too (Roger, all 73 clips) */
    function _tourUrl(n) { n = String(n || ''); var p = _TOUR_PRE[n]; if (p && p !== 1) return p; return n.indexOf('voice:') === 0 ? _TOUR_WORKER + n.slice(6) : _TOUR_CDN + n; }
    /* CLIPS FETCHED AHEAD (core-v681, Marek: po zmacknuti play byva nekdy
       dlouho ticho): the worker's clips sit in R2 in North America (ENAM) and
       nothing caches them nearer, so a clip asked for only when its step
       begins can take a second or more over the ocean before it sounds. A
       clip fetched ahead is kept as a blob URL that _tourUrl hands to the
       audio element instead of the address -- it sounds at once. A clip that
       could not be fetched ahead plays from the network as before; the names
       after it (the shared intro, Shopify's clips) stay the fallback. */
    var _TOUR_PRE = {}, _TOUR_PRE_N = [], _TOUR_PRE_B = {};
    function _tourPreTrim() {
      var cur = '';
      try { cur = String((_T.audio && _T.audio.src) || ''); } catch (e) { cur = ''; }
      for (var k = 0; _TOUR_PRE_N.length > 6 && k < _TOUR_PRE_N.length;) {
        var o = _TOUR_PRE_N[k];
        if (_TOUR_PRE[o] === cur) { k++; continue; }
        try { global.URL.revokeObjectURL(_TOUR_PRE[o]); } catch (eR) {}
        delete _TOUR_PRE[o];
        delete _TOUR_PRE_B[o];
        _TOUR_PRE_N.splice(k, 1);
      }
    }
    function _tourPreName(L, why) {
      var n = L.shift();
      if (!n || _TOUR_PRE[n]) return;
      if (!global.fetch || !global.URL || !global.URL.createObjectURL) return;
      _TOUR_PRE[n] = 1;
      var t0 = _perfNow();
      global.fetch(_TOUR_WORKER + n.slice(6), { mode: 'cors', credentials: 'omit' }).then(function (r) {
        if (!r.ok) { var eH = new Error('HTTP ' + r.status); eH.st = r.status; throw eH; }
        return r.blob();
      }).then(function (b) {
        if (_TOUR_PRE[n] !== 1) return;
        _TOUR_PRE[n] = global.URL.createObjectURL(b);
        _TOUR_PRE_B[n] = b;
        _TOUR_PRE_N.push(n);
        _tourPreTrim();
        _log('tour     voice ' + n + ' fetched ahead in ' + ((_perfNow() - t0) / 1000).toFixed(1) + ' s (' + Math.round(b.size / 1024) + ' kB, ' + why + ')');
      }).catch(function (e) {
        delete _TOUR_PRE[n];
        if (e && e.st === 404 && L.length) { _tourPreName(L, why); return; }
        _log('tour     voice ' + n + ' not fetched ahead (' + ((e && e.message) || e) + ') -- it plays from the network');
      });
    }
    function _tourPreStep(S, why) {
      if (!S || !_TOUR_GEN[_tourLang()]) return;
      if (_tourOut(S)) return;   /* core-v729: a step this product leaves out is not fetched */
      var L = [];
      try { L = _tourSrcs(S).filter(function (x) { return String(x).indexOf('voice:') === 0; }); } catch (e) { L = []; }
      _tourPreName(L, why);
    }
    /* ONE STREAM, NOT A CLIP EACH (core-v695, Marek: tim ze jsou to
       samostatne stopy, tak se to vzdy po dojeti stopy zastavi a ze scroll
       oznameni to zmizi): a clip that ended let the phone end the playing --
       the panel in the notifications went and a page in the background was put
       to sleep before the next clip. Now one player plays the whole tour: the
       audio element plays a MediaSource, each step's clip is appended to it
       (sequence mode) and a step's voice has ended when the play head passes
       its clip's end. The player never runs dry: a 0.5 s silence
       (xx/silence-half) is appended whenever less than 0.5 s is left (1.5 s on
       screen, where a rebuild can hold the page up) -- with
       the page in the background and, since core-v699, on screen too (Marek:
       pri nabehu nove stopy vzdy slysim cinknuti a ztlumeni zvuku, i na
       mobilu): a stream that ran out between two clips stalled, the player
       held its last frames back and let them out with the next clip, and a
       phone restarts its audio output after a stall -- the click and the dip
       at every new clip. A browser without MediaSource for MP3 plays one
       clip at a time as before. */
    var _TS = null;
    function _tourStreamOk() {
      try { return !!(global.MediaSource && global.MediaSource.isTypeSupported('audio/mpeg') && global.fetch && global.URL && global.URL.createObjectURL); } catch (e) { return false; }
    }
    function _tourNet(n) { n = String(n || ''); return n.indexOf('voice:') === 0 ? _TOUR_WORKER + n.slice(6) : _TOUR_CDN + n; }
    function _tourStreamOpen(a) {
      if (_TS && _TS.url) { try { global.URL.revokeObjectURL(_TS.url); } catch (eRv) {} }
      var S = { ms: null, sb: null, q: [], busy: false, url: '', dead: false };
      try { S.ms = new global.MediaSource(); S.url = global.URL.createObjectURL(S.ms); } catch (e) { return null; }
      S.ms.addEventListener('sourceopen', function () {
        if (_TS !== S || S.sb) return;
        try { S.sb = S.ms.addSourceBuffer('audio/mpeg'); S.sb.mode = 'sequence'; }
        catch (eSb) { S.dead = true; _log('tour     stream: no source buffer (' + ((eSb && eSb.message) || eSb) + ') -- the clips play one at a time'); _tourStreamFail(S); return; }
        S.sb.addEventListener('updateend', function () { var j = S.job; S.job = null; S.busy = false; if (j) { try { j.ok(_tourStreamEnd(S)); } catch (eJ) {} } _tourStreamPump(S); });
        S.sb.addEventListener('error', function () { var j = S.job; S.job = null; S.busy = false; if (j) j.no(new Error('append failed')); _tourStreamPump(S); });
        _tourStreamPump(S);
      });
      a.src = S.url;
      if (!_T.silence && !_T.silenceAsked) {
        _T.silenceAsked = true;
        global.fetch(_TOUR_WORKER + 'xx/silence-half.mp3', { mode: 'cors', credentials: 'omit' }).then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.arrayBuffer(); })
          .then(function (b) { _T.silence = b; }).catch(function (e) { _log('tour     stream: no silence clip (' + ((e && e.message) || e) + ') -- the player waits between clips instead'); });
      }
      return S;
    }
    function _tourStreamEnd(S) { try { var B = S.sb.buffered; return B.length ? B.end(B.length - 1) : 0; } catch (e) { return 0; } }
    function _tourStreamPump(S) {
      if (_TS !== S || !S.sb || S.busy || !S.q.length) return;
      var j = S.q.shift();
      /* a mark: runs once all that was queued before it is in (core-v697) */
      if (j.fn) { try { j.fn(); } catch (eF) { _log('tour     stream mark THREW ' + ((eF && eF.message) || eF)); } _tourStreamPump(S); return; }
      /* the queue after a time taken out -- nothing there, nothing to do */
      if (j.tail != null && j.tail >= _tourStreamEnd(S) - 0.02) { try { j.ok(_tourStreamEnd(S)); } catch (eT) {} _tourStreamPump(S); return; }
      S.busy = true;
      S.job = j;
      try {
        if (j.cut) S.sb.remove(0, j.cut);
        else if (j.tail != null) S.sb.remove(j.tail, Infinity);
        else { j.from = _tourStreamEnd(S); S.sb.appendBuffer(j.buf); }
      } catch (e) { S.busy = false; S.job = null; j.no(e); _tourStreamPump(S); }
    }
    function _tourStreamAdd(buf) {
      var S = _TS;
      return new Promise(function (ok, no) {
        if (!S || S.dead) { no(new Error('no stream')); return; }
        var j = { buf: buf };
        j.ok = function (to) { ok({ from: j.from || 0, to: to }); };
        j.no = no;
        S.q.push(j);
        _tourStreamPump(S);
      });
    }
    function _tourStreamFail(S) {
      if (_TS !== S) return;
      _TS = null;
      var j;
      while ((j = S.q.shift())) { try { j.no(new Error('no stream')); } catch (e) {} }
    }
    /* fn once the stream has taken all that waits before it (core-v697) */
    function _tourStreamMark(fn) {
      var S = _TS;
      if (!S || S.dead) { fn(); return; }
      S.q.push({ fn: fn, ok: function () {}, no: function () { try { fn(); } catch (e) {} } });
      _tourStreamPump(S);
    }
    /* everything queued from time 'at' on taken out, and the next clip set to
       follow right there (core-v697: the page came back, the voice queued for
       the later steps goes, the shows add it again step by step) */
    function _tourStreamTail(at) {
      var S = _TS;
      if (!S || S.dead) return;
      S.q.push({ tail: at,
        ok: function (end) {
          try { S.sb.abort(); } catch (eA) {}
          try { S.sb.timestampOffset = end; } catch (eT) { _log('tour     stream: the next clip could not be set at ' + end.toFixed(1) + ' s (' + ((eT && eT.message) || eT) + ')'); }
          _log('tour     stream: the voice queued after ' + at.toFixed(1) + ' s taken out -- the next clip follows at ' + end.toFixed(1) + ' s');
        },
        no: function (e) { _log('tour     stream: the queued voice not taken out (' + ((e && e.message) || e) + ')'); } });
      _tourStreamPump(S);
    }
    /* a clip and its short silence into the stream, kept as the step's part
       of it -- _T.segs: { i, key, name, tip, from, to } (core-v697) */
    function _tourSegAdd(i, S, c) {
      var G = (_T.segs = _T.segs || []);
      var g = { i: i, key: S.key, name: c.name, tip: '', from: null, to: null };
      try { g.tip = S.tip ? _t(S.tip[0], S.tip[1]) : ''; } catch (eT) { g.tip = ''; }
      G.push(g);
      g.p = _tourStreamAdd(c.buf).then(function (r) { g.from = r.from; g.to = r.to; return g; });
      if (_T.silence) _tourStreamAdd(_T.silence.slice(0)).catch(function () {});
      g.p.catch(function () { var k = (_T.segs || []).indexOf(g); if (k >= 0) _T.segs.splice(k, 1); });
      return g;
    }
    function _tourSegOf(i) {
      var G = _T.segs || [], k;
      for (k = G.length - 1; k >= 0; k--) if (G[k].i === i) return G[k];
      return null;
    }
    /* the play head against the clip's end; the silence while the page is in
       the background; what was played long ago taken out of the buffer */
    function _tourStreamTick() {
      var S = _TS, a = _T.audio;
      if (!S || !a || !S.sb) return;
      var t = a.currentTime || 0, W = _T.segWatch;
      /* the clip's end passed -- or the play head stands still just short of
         it (the player holds the last frames back when nothing follows) */
      if (W) {
        var fire = t >= W.to - 0.06;
        if (!fire && W.to - t < 0.5) { if (W.lt === t) fire = (Date.now() - W.lts) > 400; else { W.lt = t; W.lts = Date.now(); } }
        if (fire) { _T.segWatch = null; try { W.fn(); } catch (eW) {} }
      }
      /* the voice queued ahead: which step plays, the end of the tour (core-v697) */
      if (_T.ahead) _tourAheadTick(t);
      if (!_T.on || _TS !== S) return;
      if (_T.silence && !S.busy && !S.q.length && _tourStreamEnd(S) - t < (_tourHidden() ? 0.5 : 1.5)) {
        _tourStreamAdd(_T.silence.slice(0)).catch(function () {});
      }
      try { var B = S.sb.buffered; if (!S.busy && !S.q.length && B.length && t - B.start(0) > 90) { S.q.push({ cut: t - 30, ok: function () {}, no: function () {} }); _tourStreamPump(S); } } catch (eC) {}
    }
    /* a step's clip as bytes: fetched ahead, else from the network, the names
       in their order (the product's intro, the shared one, Shopify's) */
    function _tourClipBytes(L) {
      var k = 0;
      var next = function () {
        if (k >= L.length) return Promise.reject(new Error('none of ' + L.length + ' name(s) came'));
        var n = L[k++], b = _TOUR_PRE_B[n];
        if (b && b.arrayBuffer) return b.arrayBuffer().then(function (x) { return { name: n, buf: x, ahead: true }; });
        return global.fetch(_tourNet(n), { mode: 'cors', credentials: 'omit' }).then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.arrayBuffer();
        }).then(function (x) { return { name: n, buf: x, ahead: false }; }, function () { return next(); });
      };
      return next();
    }

    /* a ?ig page -- the link made for social posts (core-v713) */
    function _tourIg() {
      try { if (global.LEVA_IG) return true; return new URL(global.location.href).searchParams.has('ig'); } catch (e) { return false; }
    }
    /* THE CREATOR PITCH (core-v754, marketing thread, Marek 2026-10-23 15:27:
       "nabidnout 70% royalty provizi s tim, ze mu posles ukazku Taurus I --
       novy s profilem autora v draweru. Udelej i voice over, ktery to vsechno
       vysvetli po zmacknuti tlacitka ... Mozna udelej uplne novy produkt,
       unlisted (Taurus III)"; 15:28: "V voice over nezminuj zadne presne
       provize"; 15:30: "tlacitko play velke na stredu obrazovky, po kliknuti
       play se minimalizuje"): a page whose router set window.LEVA_PITCH
       (a product with custom.creator_pitch = true -- TAURUS III, the demo
       author page sent to creators) plays the onboarding: a big PLAY in the
       middle of the canvas that shrinks into its corner on the first tap,
       then the pitch cut (_TOUR_PITCH) with its own clips voice:<lang>/pitch-<key>
       (English where the page's language has none), the author drawer
       (window.LevaAuthorDrawer, sections/IG-profile.liquid) as a step of its
       own, the subtitles in the page's language where the worker has them.
       Never on a ?ig page; every other page as before. */
    function _tourPitch() {
      try { return !!global.LEVA_PITCH && !_tourIg(); } catch (e) { return false; }
    }
    function _tourSrcs(S) {
      var L = [], slug = '', lg = _tourLang(), sx = _TOUR_VOICES[lg] || '', base = (S && S.mp3) || 'tour-intro';
      try { slug = String(BOOTDATA().slug || ''); } catch (e) { slug = ''; }
      var intro = !!(S && S.key === 'intro' && /^[a-z0-9]+$/.test(slug));
      /* core-v754: the creator pitch speaks only its own clips -- the page's
         language, else English (the tour's clips say other things) */
      if (S && S.pitch && _tourPitch()) {
        L.push('voice:' + lg + '/' + S.pitch + '.mp3');
        if (lg !== 'en') L.push('voice:en/' + S.pitch + '.mp3');
        return L;
      }
      if (_TOUR_GEN[lg]) {
        /* core-v735: the homepage hero's general intro, every language */
        if (S && S.key === 'intro' && _tourAuto()) L.push('voice:' + lg + '/hero-intro.mp3');
        /* the product's hook, English: the reel's and every English tour's
           first clip (core-v713, core-v720) */
        if (intro && lg === 'en') L.push('voice:en/ig-intro-' + slug + '.mp3');
        /* core-v714: a highlight's own short line for the reel, English only;
           core-v726: the product's own version of a clip first, where
           __PROD._tourClips names it (TAURUS: ig-library-taurus) */
        var own = function (n) { try { return !!(slug && __PROD && __PROD._tourClips && __PROD._tourClips.indexOf(n) >= 0); } catch (eO) { return false; } };
        if (S && S.ig && lg === 'en' && _tourIg()) { if (own(S.ig)) L.push('voice:en/' + S.ig + '-' + slug + '.mp3'); L.push('voice:en/' + S.ig + '.mp3'); }
        if (intro) L.push('voice:' + lg + '/tour-intro-' + slug + '.mp3');
        /* core-v729: a product's own clip in the page's language too (TAURUS:
           tour-library-taurus in all 21 -- no upload on that product); a
           language without it falls through to the general clip */
        if (own(base)) L.push('voice:' + lg + '/' + base + '-' + slug + '.mp3');
        L.push('voice:' + lg + '/' + base + '.mp3');
      }
      if (sx) {
        if (intro) { L.push('tour-intro-' + slug + sx + '.mp3.mp3'); L.push('tour-intro-' + slug + sx + '.mp3'); }
        L.push(base + sx + '.mp3.mp3');
        L.push(base + sx + '.mp3');
      }
      if (intro) {
        L.push('tour-intro-' + slug + '.mp3.mp3');
        L.push('tour-intro-' + slug + '.mp3');
      }
      L.push(base + '.mp3');
      return L;
    }
    var _TOUR_SVG = {
      play: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8.5 5.5v13l10.5-6.5z" fill="currentColor"/></svg>',
      pause: '<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="6.5" y="5.5" width="4" height="13" rx="1.2" fill="currentColor"/><rect x="13.5" y="5.5" width="4" height="13" rx="1.2" fill="currentColor"/></svg>'
    };
    /* the sentence the command step types into the field, word for word
       what tour-command.mp3 (tour-command-es.mp3) says */
    var _TOUR_TYPE = { en: 'Two bedrooms and a bigger kitchen', es: 'Dos dormitorios y una cocina m\u00e1s grande', de: 'Zwei Schlafzimmer und eine gr\u00f6\u00dfere K\u00fcche', fr: 'Deux chambres et une cuisine plus grande', cs: 'Dv\u011b lo\u017enice a v\u011bt\u0161\u00ed kuchyn\u011b', pt: 'Dois quartos e uma cozinha maior', zh: '\u4e24\u95f4\u5367\u5ba4\u548c\u4e00\u4e2a\u66f4\u5927\u7684\u53a8\u623f', ar: '\u063a\u0631\u0641\u062a\u0627 \u0646\u0648\u0645 \u0648\u0645\u0637\u0628\u062e \u0623\u0643\u0628\u0631', ja: '\u5bdd\u5ba42\u3064\u3068\u5e83\u3044\u30ad\u30c3\u30c1\u30f3', tr: '\u0130ki yatak odas\u0131 ve daha b\u00fcy\u00fck bir mutfak', ko: '\uce68\uc2e4 \ub450 \uac1c\uc640 \ub354 \ub113\uc740 \uc8fc\ubc29', it: 'Due camere da letto e una cucina pi\u00f9 grande', pl: 'Dwie sypialnie i wi\u0119ksza kuchnia', ro: 'Dou\u0103 dormitoare \u0219i o buc\u0103t\u0103rie mai mare', nl: 'Twee slaapkamers en een grotere keuken', el: '\u0394\u03cd\u03bf \u03c5\u03c0\u03bd\u03bf\u03b4\u03c9\u03bc\u03ac\u03c4\u03b9\u03b1 \u03ba\u03b1\u03b9 \u03bc\u03b5\u03b3\u03b1\u03bb\u03cd\u03c4\u03b5\u03c1\u03b7 \u03ba\u03bf\u03c5\u03b6\u03af\u03bd\u03b1', hu: 'K\u00e9t h\u00e1l\u00f3szoba \u00e9s nagyobb konyha', sv: 'Tv\u00e5 sovrum och ett st\u00f6rre k\u00f6k', he: '\u05e9\u05e0\u05d9 \u05d7\u05d3\u05e8\u05d9 \u05e9\u05d9\u05e0\u05d4 \u05d5\u05de\u05d8\u05d1\u05d7 \u05d2\u05d3\u05d5\u05dc \u05d9\u05d5\u05ea\u05e8', fi: 'Kaksi makuuhuonetta ja isompi keitti\u00f6', no: 'To soverom og et st\u00f8rre kj\u00f8kken' };
    function _tourType() { return _TOUR_TYPE[_tourLang()] || _TOUR_TYPE.en; }
    var _T = { btn: null, on: false, run: 0, i: -1, audio: null, voice: false, saved: null, cam: null, view: '3d', bub: null, hl: null, pl: [], tm: [], w0: null, pass: 0, undoN: null };
    /* core-v732: the ?ig&autoplay reel's state (sound, run, armed) */
    var _TA = { sound: false, run: true, armed: false, tries: 0 };
    /* EVERY PRODUCT OF THE LINE (core-v643, Marek: muzes to poslat na vsechny
       projekty): on unless the product says __PROD._tour = false */
    function _tourOn() { try { return !!(__PROD && __PROD._tour !== false); } catch (e) { return false; } }
    function _perfNow() { try { return global.performance.now(); } catch (e) { return Date.now(); } }
    function _tourVoiceLang() { return _TOUR_VOICES.hasOwnProperty(_tourLang()); }
    function _tourHidden() { try { return !!global.document.hidden; } catch (e) { return false; } }
    /* THE TOUR IN THE BACKGROUND (core-v691, Marek: slo by to spustit i na
       pozadi?): it goes on when the page is left -- the voice keeps playing
       and each step ends with its voice; the log says when the page went and
       came back. The phone's media controls pause and play it. */
    try {
      global.document.addEventListener('visibilitychange', function () {
        if (!_T.on) return;
        var at = ((_T.i >= 0 ? _T.i : _T.nextI) || 0) + 1;
        if (_tourHidden()) {
          /* core-v697: the voice of the steps left queued at once */
          if (_TS && _T.voice) _tourAheadGo('the page went to the background at step ' + at);
          else _log('tour     the page went to the background -- the voice goes on, each step ends with its voice');
        } else if (_T.ahead) _tourCatchUp();
        else _log('tour     the page is on screen again -- the shows go on from step ' + at);
      });
    } catch (eVis) {}
    /* THE VOICE AHEAD IN THE BACKGROUND (core-v697, Marek: kdyz odejdu z
       okna, tak se nikdy nespusti dalsi stopa a je ticho; stopy se pak musi
       prehravat dal a dal, pri otevreni se vizual musi prizpusobit aktualni
       stope): a phone gives a page in the background no timers for long
       stretches -- the step after never came, the stream ran dry and the
       silent page was put to sleep. Now, the moment the page goes, its shows
       stop and the steps left are walked at once: each one's start only
       asks whether this product has it (nothing is scheduled, nothing is
       shown) and the voice of every step that has something is fetched and
       queued in the stream, one after the other -- the player plays the rest
       of the tour on its own. The panel follows the step whose voice plays;
       after the last one the tour ends as always. */
    function _tourTickLoop() {
      if (!_TS) return;
      var r = _T.run;
      var tk = function () { if (r !== _T.run) return; _tourStreamTick(); _tourLater(tk, 250, r); };
      _tourLater(tk, 250, r);
    }
    function _tourAheadGo(why) {
      if (!_T.on || !_TS || !_T.voice || _T.ahead) return;
      var cur = _T.i, from = (cur >= 0) ? cur : (_T.nextI || 0), S0 = (cur >= 0) ? _TOUR_STEPS[cur] : null;
      /* the shows stop: their timers go with a new run */
      _T.run++;
      _T.tm.forEach(function (id) { global.clearTimeout(id); });
      _T.tm = [];
      var run = _T.run;
      _T.ahead = true; _T.aheadDone = false; _T.aheadNext = -1; _T.segWatch = null; _T.segNow = null;
      if (S0) {
        _tourHl(null); _tourBub(null); _tourPulse([]);
        try { S0.end(false); } catch (eE) { _log('tour     ' + S0.key + ' end THREW ' + ((eE && eE.message) || eE)); }
      }
      _T.i = -1;
      var list = [], passed = [], j, S;
      /* the step that was on screen: its voice is in the stream already (or
         on its way), else it is queued first */
      if (S0) { if (!_tourSegOf(cur)) list.push(cur); from = cur + 1; }
      _T.probe = true;
      for (j = from; j < _TOUR_STEPS.length; j++) {
        S = _TOUR_STEPS[j];
        _T.skip = '';
        if (S.has) { var okH = false; try { okH = !!S.has(); } catch (eH) { okH = false; } if (!okH) _T.skip = 'nothing for it'; }
        if (!_T.skip && _tourOut(S)) _T.skip = 'not on this product';   /* core-v729 */
        if (!_T.skip) { try { S.start(run); } catch (eS) { _log('tour     ' + S.key + ' start THREW ' + ((eS && eS.message) || eS) + ' (walked in the background)'); } }
        if (_T.skip) passed.push(S.key); else list.push(j);
        try { S.end(false); } catch (eE2) {}
      }
      _T.probe = false;
      _T.skip = '';
      _tourHl(null); _tourBub(null); _tourPulse([]);
      _T.nextI = list.length ? list[0] : _TOUR_STEPS.length;
      _log('tour     ' + (why || 'the page went to the background') + ' -- the shows stop; the voice of the ' + list.length + ' step(s) left goes into the stream at once: ' +
        list.map(function (x) { return _TOUR_STEPS[x].key; }).join(', ') + (passed.length ? ' (nothing on this product for: ' + passed.join(', ') + ')' : ''));
      _tourTickLoop();
      _tourAheadQueue(list, run);
    }
    function _tourAheadQueue(list, run) {
      /* four clips fetched at a time, put into the stream in their order */
      var k = 0, P = [], asked = 0;
      var pull = function () {
        while (asked < list.length && asked < k + 4) { P[asked] = _tourClipBytes(_tourSrcs(_TOUR_STEPS[list[asked]])); P[asked].catch(function () {}); asked++; }
      };
      var next = function () {
        if (run !== _T.run || !_T.ahead) return;
        if (k >= list.length) { _T.aheadDone = true; _T.aheadNext = -1; _log('tour     (background) the rest of the tour is in the stream'); return; }
        pull();
        var pk = P[k], j = list[k++], S = _TOUR_STEPS[j];
        _T.aheadNext = j;
        pk.then(function (c) {
          if (run !== _T.run || !_T.ahead) return;
          var g = _tourSegAdd(j, S, c);
          g.p.then(function () {
            if (run !== _T.run) return;
            _log('tour     (background) ' + S.key + ' voice ' + c.name + ' queued at ' + g.from.toFixed(1) + ' s, ' + (g.to - g.from).toFixed(1) + ' s long' + (c.ahead ? ' (fetched ahead)' : ' (from the network)'));
            try { if (_T.audio && _T.audio.paused) { var pr = _T.audio.play(); if (pr && pr.catch) pr.catch(function (eA) { if (run === _T.run) _tourPlayFail(eA, run); }); } } catch (eP) {}
            next();
          }, function () { next(); });
        }, function (e) {
          if (run !== _T.run) return;
          _log('tour     (background) no clip for ' + S.key + ' (' + ((e && e.message) || e) + ') -- left out');
          next();
        });
      };
      next();
    }
    /* the step whose voice plays: the panel says it, pause and previous go
       by it; after the last voice the tour ends */
    function _tourAheadTick(t) {
      var G = _T.segs || [], k, g = null;
      for (k = 0; k < G.length; k++) if (G[k].to != null && t < G[k].to) { g = G[k]; break; }
      if (g && g.from != null && t >= g.from - 0.05 && _T.segNow !== g) {
        _T.segNow = g;
        _T.played = _T.played || [];
        if (_T.played.indexOf(g.i) < 0) _T.played.push(g.i);
        _T.nextI = g.i;
        try { _tourMediaMeta(g.tip); } catch (eM) {}
        _log('tour     (background) step ' + (g.i + 1) + '/' + _TOUR_STEPS.length + ' ' + g.key + ' -- its voice plays');
      }
      if (!g && _T.aheadDone && G.length && t > G[G.length - 1].to + 0.3) {
        _log('tour     (background) the last voice has played');
        _tourStop('the end of the tour');
      }
    }
    /* THE PAGE IS BACK (core-v697): the voice queued after the one now
       playing is taken out, and the shows start again at that step -- the
       project put back as at play, that step's show from its beginning while
       its voice goes on; the steps after it add their voice as they come */
    function _tourCatchUp() {
      if (!_T.on || !_T.ahead) return;
      _T.ahead = false;
      var S = _TS, run = _T.run;
      if (S) {
        var keep = [];
        S.q.forEach(function (j) { if (j.buf) { try { j.no(new Error('the page came back')); } catch (eN) {} } else keep.push(j); });
        S.q = keep;
      }
      _tourStreamMark(function () {
        if (run !== _T.run || !_T.on) return;
        var a = _T.audio, t = (a && a.currentTime) || 0, G = (_T.segs || []).filter(function (g) { return g.to != null; }), k, at = -1;
        /* the step whose voice plays, if 2 s of it are left -- else the next
           one, whose voice comes within moments (a show started again for a
           voice about to end would run on in silence) */
        for (k = 0; k < G.length; k++) if (G[k].to > t + 0.6 && (G[k].from > t || G[k].to - t > 2)) { at = k; break; }
        if (at < 0) {
          if (_T.aheadDone) { _T.segs = G; _log('tour     the page is back after the last voice'); _tourStop('the end of the tour'); return; }
          var nx = G.length ? G[G.length - 1].i + 1 : (_T.aheadNext >= 0 ? _T.aheadNext : (_T.nextI || 0));
          _T.segs = G;
          _tourResync(nx, 'the page is back at ' + t.toFixed(1) + ' s, no voice queued past it -- the tour goes on at step ' + (nx + 1));
          return;
        }
        var cut = (at + 1 < G.length) ? G[at + 1].from : null;
        _T.segs = G.slice(0, at + 1);
        if (cut != null) _tourStreamTail(cut);
        _tourResync(G[at].i, 'the page is back at ' + t.toFixed(1) + ' s -- the voice plays step ' + (G[at].i + 1) + ' ' + G[at].key + ' (until ' + G[at].to.toFixed(1) + ' s), its show starts again there');
      });
    }
    function _tourResync(k, why) {
      _log('tour     ' + why);
      _T.keepAudio = true;
      try {
        _tourStop(why, true);
        _T.resumeAt = k;
        _tourStart();
      } finally { _T.keepAudio = false; }
    }
    function _tourMedia() {
      try {
        var ms = global.navigator && global.navigator.mediaSession;
        if (!ms) return;
        if (!_T.mediaSet) {
          _T.mediaSet = true;
          ms.setActionHandler('pause', function () { if (_T.on) _tourPause('the media controls'); });
          ms.setActionHandler('play', function () { if (!_T.on) _tourStart(); });
          try { ms.setActionHandler('nexttrack', function () { _tourSkip(1); }); } catch (eN) {}
          try { ms.setActionHandler('previoustrack', function () { _tourSkip(-1); }); } catch (eP) {}
          _log('tour     media controls on -- the notification / lock screen: the product picture and name, play, pause, next, previous; a tap on it brings the page back');
        }
        _tourMediaMeta('');
        try { ms.playbackState = 'playing'; } catch (eS) {}
      } catch (eMs) {}
    }
    /* the panel in the phone's notifications like a music app's (core-v694,
       Marek: v scroll mobil oznaceni banner s ovladanim prehravani jako
       Spotify, klikem se vratim na stranku): the product's picture (og:image)
       and name, LEVA and the step's own label under it */
    function _tourMediaArt() {
      if (typeof _T.mediaArt === 'string') return _T.mediaArt;
      var u = '';
      try { var m = global.document.querySelector('meta[property="og:image"]'); u = m ? String(m.getAttribute('content') || '') : ''; } catch (e) { u = ''; }
      if (u.indexOf('//') === 0) u = 'https:' + u;
      _T.mediaArt = u.replace(/^http:/, 'https:');
      return _T.mediaArt;
    }
    function _tourMediaMeta(step) {
      try {
        var ms = global.navigator && global.navigator.mediaSession;
        if (!ms || !global.MediaMetadata) return;
        var art = _tourMediaArt(), title = String(global.document.title || 'LEVA').split(/\s+[\u2013\u2014|-]\s+/)[0];
        ms.metadata = new global.MediaMetadata({ title: title, artist: 'LEVA' + (step ? ' \u00b7 ' + step : ''), album: 'LEVA', artwork: art ? [{ src: art, sizes: '512x512' }] : [] });
      } catch (e) {}
    }
    /* next / previous from the notification: the tour pauses and goes on from
       that step; previous goes to the last step that was shown, not to one
       this product has nothing for */
    function _tourSkip(d) {
      var n = _TOUR_STEPS.length, cur = _T.on ? ((_T.i >= 0) ? _T.i : (_T.nextI || 0)) : (_T.resumeAt || 0), to = cur + d;
      if (d < 0) { var P = (_T.played || []).filter(function (x) { return x < cur; }); to = P.length ? P[P.length - 1] : 0; }
      to = Math.max(0, Math.min(n - 1, to));
      _log('tour     media controls: ' + (d > 0 ? 'next' : 'previous') + ' -- on from step ' + (to + 1));
      if (_T.on) _tourPause('the media controls (' + (d > 0 ? 'next' : 'previous') + ')');
      _T.resumeAt = to;
      _tourStart();
    }
    function _tourCss() {
      if ($('cf-tour-css')) return;
      var st = global.document.createElement('style');
      st.id = 'cf-tour-css';
      var M = "'Montserrat',sans-serif";
      st.textContent =
        /* core-v639 (Marek: ikonu play / pause opravdu nakonec doprava nahoru
           do rohu, at nekoliduje s pdf): the top right corner, left of the
           + PDF chip -- _tourPlace() reads the chip and keeps 8 px from it */
        '#cf-tour-btn{position:absolute;top:12px;right:20px;z-index:14;width:38px;height:38px;border-radius:50%;border:0;padding:0;margin:0;' +
        'display:inline-flex;align-items:center;justify-content:center;background:#141516;color:#ffffff;cursor:pointer;' +
        'box-shadow:0 4px 14px rgba(0,0,0,0.20);-webkit-tap-highlight-color:transparent;touch-action:manipulation;}' +
        '#cf-tour-btn svg{width:18px;height:18px;display:block;}' +
        '#cf-tour-btn.cf-ig-gone{opacity:0 !important;pointer-events:none !important;transition:opacity .2s;}' +   /* core-v763 */
        '#cf-tour-btn.cf-on{background:#D8232A;}' +
        /* core-v754 THE CREATOR PITCH's big PLAY in the middle of the canvas
           (over the inline top / right that _tourPlace writes); a soft dark
           halo that swells and fades, no red */
        '#cf-tour-btn.cf-pitch-big{top:50% !important;left:50% !important;right:auto !important;width:112px;height:112px;transform:translate(-50%,-50%);z-index:20;' +
        'animation:cf-pitch-halo 2.2s ease-in-out infinite;}' +
        '#cf-tour-btn.cf-pitch-big svg{width:50px;height:50px;margin-left:5px;}' +
        '@keyframes cf-pitch-halo{0%,100%{box-shadow:0 10px 32px rgba(0,0,0,0.28),0 0 0 0 rgba(20,21,22,0.20);}60%{box-shadow:0 10px 32px rgba(0,0,0,0.28),0 0 0 22px rgba(20,21,22,0);}}' +
        '@media (max-width:640px){#cf-tour-btn.cf-pitch-big{width:92px;height:92px;}#cf-tour-btn.cf-pitch-big svg{width:42px;height:42px;}}' +
        '@media (prefers-reduced-motion:reduce){#cf-tour-btn.cf-pitch-big{animation:none;box-shadow:0 10px 32px rgba(0,0,0,0.28);}}' +
        /* core-v645: the rail and the edit strip start under the PLAY -- their
           middle 23 px lower, the strip 70 px from the top at least (a
           phone's strip already keeps 60 px) */
        '#cf-root.cf-tour-top .cf-sidebar{top:calc(50% + 23px);}' +
        '#cf-root.cf-tour-top #cf-sx-strip{top:calc(50% + 23px);max-height:calc(100% - 94px);max-height:min(470px,calc(100% - 94px));}' +
        '@media (max-width:640px){#cf-root.cf-tour-top #cf-sx-strip{top:calc(50% - 16px);max-height:calc(100% - 152px);max-height:min(470px,calc(100% - 152px));}}' +
        /* core-v641 (Marek: play tlacitko na zacatku pulsovat, staci fade in
           a fade off na 50 %): until the first PLAY on this page */
        '#cf-tour-btn.cf-idle{animation:cf-tour-fade 1.6s ease-in-out infinite;}' +
        '@keyframes cf-tour-fade{0%,100%{opacity:1;}50%{opacity:0.5;}}' +
        /* core-v688 (Marek: nedelat puls do cervene barvy, prvni zobrazeni
           200 % jeho finalniho zobrazeni): the entrance, after the soft pulse
           in the sheet so it wins while it runs; it scales from its top
           edge, so nothing goes over the canvas's top. core-v698 (Marek:
           200 % pri plne opacite nevypada dobre): one move, 200 % at 10 %
           opacity to its own size at full opacity (1.8 s), no hold */
        '#cf-tour-btn.cf-intro-wait{transform:scale(0);opacity:0;}' +
        '#cf-tour-btn.cf-intro{transform-origin:50% 0;animation:cf-tour-in 1.8s cubic-bezier(0.25,0.8,0.3,1) both;}' +
        '@keyframes cf-tour-in{0%{transform:scale(2);opacity:0.1;}100%{transform:scale(1);opacity:1;}}' +
        '@media (prefers-reduced-motion:reduce){#cf-tour-btn.cf-idle,#cf-tour-btn.cf-intro{animation:none;}#cf-tour-btn.cf-intro-wait{transform:none;opacity:1;}}' +
        /* core-v724 (Marek 2026-10-20: vsude jen jemne zvyrazneni; background
           f5f5f5, barva o neco tmavsi, jemny puls; nikde cerveny nadech):
           one soft halo that swells and settles, a shade darker than the
           page around it; on the dark strip and the dark folded bar a shade
           lighter than them. No red ring anywhere (it was on the right-hand
           icons and the strip since core-v706). */
        '.cf-tour-hl,.cf-tour-pulse{animation:cf-tour-soft 1.6s ease-in-out infinite !important;}' +
        '@keyframes cf-tour-soft{0%,100%{box-shadow:0 0 0 0 rgba(0,0,0,0.10);}50%{box-shadow:0 0 0 8px rgba(0,0,0,0.10);}}' +
        '#cf-sx-strip .cf-tour-hl,#cf-sx-strip .cf-tour-pulse,.cf-cmd-mini .cf-tour-hl,.cf-cmd-mini .cf-tour-pulse{animation:cf-tour-soft-d 1.6s ease-in-out infinite !important;}' +
        '@keyframes cf-tour-soft-d{0%,100%{box-shadow:0 0 0 0 rgba(255,255,255,0.18);}50%{box-shadow:0 0 0 6px rgba(255,255,255,0.18);}}' +
        '#cf-tour-bub{position:absolute;z-index:17;transform:translate(-50%,-100%);margin-top:-12px;background:#141516;color:#ffffff;' +
        'font:700 11px/1 ' + M + ';letter-spacing:0.06em;text-transform:uppercase;padding:8px 12px;border-radius:999px;white-space:nowrap;' +
        'pointer-events:none;display:none;box-shadow:0 6px 20px rgba(0,0,0,0.22);}' +
        '#cf-tour-bub.cf-on{display:block;}' +
        '#cf-tour-bub.cf-side{transform:translate(-100%,-50%);margin-top:0;}' +
        /* typing on a phone: the field takes the room of upload and the pencil
           for the few seconds of the command step, so the sentence reads */
        '#cf-cmdbar.cf-tour-type .cf-cmd-in{text-overflow:clip;}' +
        '@media (max-width:640px){#cf-cmdbar.cf-tour-type .cf-cmd-up,#cf-cmdbar.cf-tour-type .cf-cmd-div,#cf-cmdbar.cf-tour-type .cf-cmd-edit{display:none !important;}' +
        '#cf-cmdbar.cf-tour-type .cf-cmd-fld{width:100%;min-width:150px;}}';
      global.document.head.appendChild(st);
    }
    /* THE SCROLL LANE (core-v690, Marek: mam problem se skrolovanim stranky
       na section configurator -- 3D plocha bere dotyky a cerne banery napravo
       taky; nalevo pruhledny pas se sipkami nahoru dolu, mezi nimi tecka --
       Marek's glyph, v sede, jemne pulsujici opacity 20 / 50, vlevo na stredu
       zobrazeni, posouva se). Touch screens only: a 44 px band along the
       canvas's left edge where a swipe up or down moves the page (touch-action
       pan-y -- the model never hears it, nor the two-finger plan zoom:
       data-cf-nopinch). Under every panel and bar (z-index 4).
       core-v693 (Marek: impuls slaby, 20 / 70; kousicek dal od kraje, 5 px,
       at nekoliduje s posuvnikem mapy; pri skrolovani za pravy banner se
       animace trha -- nech sipky napevno na stredu section): the glyph sits
       in the middle of the section and moves with it (no script on scroll,
       the opacity pulse on the compositor), 17 px from the edge -- the map
       step's rotation slider starts at 46 px. */
    var _LANE = { el: null };
    var _LANE_SVG = '<svg viewBox="80 10 96 236" aria-hidden="true">' +
      '<path d="M128 20 L166 58 Q171 64 163 66 L128 48 L93 66 Q85 64 90 58 Z"/>' +
      '<circle cx="128" cy="128" r="34"/>' +
      '<path d="M128 236 L166 198 Q171 192 163 190 L128 208 L93 190 Q85 192 90 198 Z"/></svg>';
    function _laneMount() {
      if (_LANE.el) return;
      var root = $('cf-root'), doc = global.document;
      if (!root) return;
      if (!doc.getElementById('cf-lane-css')) {
        var st = doc.createElement('style');
        st.id = 'cf-lane-css';
        st.textContent =
          '#cf-scroll-lane{display:none;position:absolute;left:0;top:0;bottom:64px;width:44px;z-index:4;touch-action:pan-y;pointer-events:auto;' +
          '-webkit-tap-highlight-color:transparent;}' +
          '@media (hover:none) and (pointer:coarse){#cf-scroll-lane{display:block;}}' +
          '#cf-scroll-lane .cf-ln-g{position:absolute;left:17px;top:calc(50% + 32px);width:20px;height:50px;transform:translateY(-50%);will-change:opacity;animation:cfLnP 2.4s ease-in-out infinite;}' +
          '#cf-scroll-lane svg{width:100%;height:100%;display:block;}' +
          '#cf-scroll-lane path,#cf-scroll-lane circle{fill:#6B7075;stroke:#6B7075;stroke-width:6;stroke-linejoin:round;}' +
          '@keyframes cfLnP{0%,100%{opacity:0.2}50%{opacity:0.7}}' +
          '@media (prefers-reduced-motion:reduce){#cf-scroll-lane .cf-ln-g{animation:none;opacity:0.45;}}';
        doc.head.appendChild(st);
      }
      var el = doc.createElement('div');
      el.id = 'cf-scroll-lane';
      el.setAttribute('data-cf-nopinch', '1');
      var g = doc.createElement('div');
      g.className = 'cf-ln-g';
      g.innerHTML = _LANE_SVG;
      el.appendChild(g);
      root.appendChild(el);
      _LANE.el = el;
      var shown = '';
      try { shown = global.getComputedStyle(el).display; } catch (eD) { shown = '?'; }
      _log('lane     the scroll lane on the left edge -- ' + (shown === 'none' ? 'hidden (not a touch screen)' : 'shown (a touch screen)') + ' (core-v690)');
    }
    /* ===== THE TOUR'S SUBTITLES (core-v700) ====================================
       Marek 2026-10-14: psane titulky v EN ve spodni tretine, pro video a
       Instagram; then (Ano -- vsude; odkaz s ?ig zobrazi titulky a zaroven
       vypne vsechny admin buttons / logs): the container's subtitles
       (container-v535 / v537) on every product of the core, the same look
       and the same switches. The words are the clips' own: the leva-voice
       worker hands out the text each clip was made from (GET /texts/en), so
       a remade clip brings its subtitle with it and nothing is typed here.
       One line of the sentence at a time in the lower third of the canvas,
       timed by the voice: by the stream's segment the play head is in (the
       clip's name tells which text), by the clip's own time on the one-clip
       path, by the step's own time with no voice. Nothing in the runner is
       changed: a watcher reads where the voice is.
       Switches: shown by default (TOUR_SUBS); the small cross at the band's
       top right closes them for this visitor (localStorage leva-tour-subs,
       one key for every product); ?ig (or ?subs=1) shows them on a page
       whatever was closed -- the ?ig page has no admin UI either
       (layout/theme.liquid ig-v1); ?subs=0 hides them on a page. */
    var TOUR_SUBS = true;
    var TOUR_SUBS_LANG = 'en';
    var TOUR_SUBS_KEY = 'leva-tour-subs';
    var _TSUB = { el: null, tx: null, txt: null, asked: false, failAt: 0, iv: 0, key: '', chunks: null, shown: '', stepI: -2, stepT0: 0, said: '', closed: false };
    function _tourSubsOn() {
      var q = null, ig = false, kept = null;
      if (_TSUB.closed) return false;   /* the cross, on this page view (a ?ig page too) */
      try { var U = new URL(global.location.href); q = U.searchParams.get('subs'); ig = U.searchParams.has('ig'); } catch (e) { q = null; }
      if (q === '1' || ig || global.LEVA_IG) return true;
      if (q === '0') return false;
      try { kept = global.localStorage.getItem(TOUR_SUBS_KEY); } catch (eK) { kept = null; }
      if (kept === '0') return false;
      return TOUR_SUBS;
    }
    /* the cross: closed for this visitor, from now on and on the next visit */
    function _tourSubsClose(e) {
      if (e) { e.preventDefault(); e.stopPropagation(); }
      try { global.localStorage.setItem(TOUR_SUBS_KEY, '0'); } catch (eS) {}
      _TSUB.closed = true;
      _tourSubShow('');
      _log('tour     subtitles closed by the cross -- off for this visitor (a ?ig page shows them again when it is opened again)');
    }
    function _tourSubsCss() {
      if ($('cf-tour-sub-css')) return;
      var st = global.document.createElement('style');
      st.id = 'cf-tour-sub-css';
      st.textContent =
        '#cf-tour-sub{position:absolute;left:50%;bottom:20%;transform:translateX(-50%);z-index:16;max-width:min(680px,calc(100% - 150px));' +
        'box-sizing:border-box;padding:9px 16px;border-radius:12px;background:rgba(20,21,22,0.80);color:#ffffff;text-align:center;' +
        "font:600 17px/1.35 'Montserrat',sans-serif;letter-spacing:0.01em;pointer-events:none;display:none;box-shadow:0 6px 20px rgba(0,0,0,0.20);}" +
        '#cf-tour-sub.cf-on{display:block;}' +
        '#cf-tour-sub.cf-over{position:fixed;z-index:2147483000;}' +   /* core-v747 over the drawers; core-v803 over the item drawer too, which leva-profile raises to 100006 */
        '#cf-tour-sub .cf-tour-sub-x{position:absolute;top:-9px;right:-9px;width:22px;height:22px;border-radius:50%;border:0;padding:0;margin:0;' +
        'display:flex;align-items:center;justify-content:center;background:#ffffff;color:#141516;cursor:pointer;pointer-events:auto;' +
        'box-shadow:0 2px 8px rgba(0,0,0,0.25);-webkit-tap-highlight-color:transparent;touch-action:manipulation;}' +
        '#cf-tour-sub .cf-tour-sub-x svg{width:10px;height:10px;display:block;}' +
        '#cf-tour-sub .cf-tour-sub-t{display:block;}' +
        '@media (max-width:640px){#cf-tour-sub{font-size:14px;max-width:calc(100% - 96px);padding:7px 12px;bottom:22%;}}' +
        /* core-v730 (Marek 2026-10-21: zvetsit titulky u pruvodcu s ig, na ig nejsou skoro videt; bezny pruvodce nechat): on a ?ig page (html.leva-ig) the line is about 4 % of the page's width -- 46 px on a 1100 px stage, 24 px at the least, 56 px at the most; as wide as its words up to the page less 160 px, clear of the right-hand rail (left 50 % alone would cap it at half the page); 22 px on a phone; every other page as before */
        'html.leva-ig #cf-tour-sub{font-size:clamp(24px,4.2vw,56px);line-height:1.22;font-weight:700;padding:0.32em 0.7em;border-radius:0.38em;width:max-content;max-width:calc(100% - 160px);}' +
        '@media (max-width:640px){html.leva-ig #cf-tour-sub{font-size:22px;max-width:calc(100% - 128px);}}';
      global.document.head.appendChild(st);
    }
    function _tourSubsText() {
      if (_TSUB.txt || _TSUB.asked || !global.fetch) return;
      if (_TSUB.failAt && Date.now() - _TSUB.failAt < 20000) return;   /* not again within 20 s */
      _TSUB.asked = true;
      var url = _TOUR_WORKER.replace(/\/v\/$/, '/texts/') + TOUR_SUBS_LANG;
      global.fetch(url, { mode: 'cors', credentials: 'omit' }).then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      }).then(function (j) {
        _TSUB.txt = j || {};
        _log('tour     subtitles: ' + Object.keys(_TSUB.txt).length + ' ' + TOUR_SUBS_LANG + ' text(s) from the voice worker');
        /* core-v754: the creator pitch's lines in the page's language where
           the worker has them (Polish so far), English otherwise */
        var lgP = _tourLang();
        if (_tourPitch() && lgP !== TOUR_SUBS_LANG) {
          global.fetch(_TOUR_WORKER.replace(/\/v\/$/, '/texts/') + lgP, { mode: 'cors', credentials: 'omit' }).then(function (r) { return r.ok ? r.json() : {}; }).then(function (k) {
            var n = 0;
            Object.keys(k || {}).forEach(function (x) { if (x.indexOf('pitch-') === 0 && k[x]) { _TSUB.txt[x] = k[x]; n++; } });
            _TSUB.key = '';
            _log('tour     subtitles: ' + n + ' pitch line(s) in ' + lgP + ' (core-v754)');
          }).catch(function () {});
        }
      }).catch(function (e) {
        _TSUB.asked = false;
        _TSUB.failAt = Date.now();
        _log('tour     subtitles: the texts did not come (' + ((e && e.message) || e) + ') -- asked again in 20 s');
      });
    }
    /* the text a clip says: by its file name (the worker's or Shopify's),
       the language's suffix taken off */
    function _tourSubKey(name) {
      var n = String(name || '').replace(/^voice:[a-z]{2}(-[a-z]{2})?\//, '').replace(/^.*\//, '').replace(/(\.mp3)+$/, '');
      var T = _TSUB.txt || {};
      if (T[n]) return n;
      var m = /^(.*)-[a-z]{2}$/.exec(n);
      if (m && T[m[1]]) return m[1];
      return '';
    }
    /* a step's own clip name, for the paths without a stream */
    function _tourSubStepKey(S) {
      var T = _TSUB.txt || {}, slug = '';
      if (!S) return '';
      try { slug = String(BOOTDATA().slug || ''); } catch (e) { slug = ''; }
      if (S.pitch && _tourPitch() && T[S.pitch]) return S.pitch;   /* core-v754 */
      if (S.key === 'intro' && slug && _tourLang() === 'en' && T['ig-intro-' + slug]) return 'ig-intro-' + slug;
      if (S.ig && _tourIg() && slug && T[S.ig + '-' + slug]) return S.ig + '-' + slug;   /* core-v726 */
      if (S.ig && _tourIg() && T[S.ig]) return S.ig;
      if (slug && T[S.mp3 + '-' + slug]) return S.mp3 + '-' + slug;   /* core-v726; core-v729 any language */
      if (S.key === 'intro' && slug && T['tour-intro-' + slug]) return 'tour-intro-' + slug;
      return T[S.mp3] ? S.mp3 : '';
    }
    /* the sentence by sentence, a long one split at its comma or colon
       nearest the middle; each piece weighed by its letters */
    function _tourSubChunks(text) {
      var out = [];
      String(text || '').replace(/([.!?])\s+/g, '$1\n').split('\n').forEach(function (s) {
        s = s.trim();
        if (!s) return;
        var parts = [s];
        while (parts.some(function (x) { return x.length > 78; })) {
          var q = [], cut = false;
          parts.forEach(function (x) {
            if (x.length <= 78 || cut) { q.push(x); return; }
            var mid = x.length / 2, best = -1, re = /[,:;]\s/g, mm;
            while ((mm = re.exec(x))) if (best < 0 || Math.abs(mm.index - mid) < Math.abs(best - mid)) best = mm.index;
            if (best < 0) { var sp = x.lastIndexOf(' ', Math.round(mid)); best = (sp > 0) ? sp - 1 : -1; }
            if (best < 0) { q.push(x); return; }
            q.push(x.slice(0, best + 1).trim()); q.push(x.slice(best + 1).trim());
            cut = true;
          });
          if (!cut) break;
          parts = q;
        }
        out = out.concat(parts);
      });
      var tot = 0, acc = 0;
      out.forEach(function (x) { tot += x.length; });
      return out.map(function (x) { acc += x.length; return { t: x, end: tot ? acc / tot : 1 }; });
    }
    function _tourSubShow(text) {
      var el = _TSUB.el;
      if (!el && !text) return;
      if (!el) {
        var root = $('cf-root');
        if (!root) return;
        _tourSubsCss();
        el = _TSUB.el = global.document.createElement('div');
        el.id = 'cf-tour-sub';
        var tx = global.document.createElement('span');
        tx.className = 'cf-tour-sub-t';
        el.appendChild(tx);
        var x = global.document.createElement('button');
        x.type = 'button';
        x.className = 'cf-tour-sub-x';
        x.setAttribute('aria-label', _t('close', 'Close'));
        x.innerHTML = '<svg viewBox="0 0 10 10" aria-hidden="true"><path d="M1.5 1.5l7 7M8.5 1.5l-7 7" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" fill="none"/></svg>';
        x.addEventListener('click', _tourSubsClose);
        el.appendChild(x);
        _TSUB.tx = tx;
        root.appendChild(el);
      }
      if (text) _tourSubPlace();
      if (text === _TSUB.shown) return;
      _TSUB.shown = text;
      if (text) { _TSUB.tx.textContent = text; el.classList.add('cf-on'); }
      else el.classList.remove('cf-on');
    }
    /* 15 PX OVER THE BOTTOM BAR (core-v706, Marek: titulky dej 15 px nad
       spodni banner floorplan / edit): the band stands over whatever is at
       the canvas's foot -- the command bar, the edit bar and its slider(s) --
       its own lower third only when none is up */
    /* core-v747 (Marek 2026-10-21: titulky jsou schovane za drawers -- budgets
       atd.): is a drawer open over the page -- a side panel (the budget, an
       item's card, the shared budgets ...) or the assistant */
    function _tourDrawerUp() {
      try {
        if ($('cf-dash')) return true;   /* core-v802 the pitch's sample dashboard, fixed over the page */
        var d = global.document, L = d.querySelectorAll('.side-panel.active'), i, w = global.innerWidth || 0;
        for (i = 0; i < L.length; i++) { var r = L[i].getBoundingClientRect(); if (r.width > 0 && r.height > 0 && r.left < w - 4 && r.right > 4) return true; }
        var ai = d.getElementById('leva-ai-drawer');
        if (ai && !ai.hidden && ai.getAttribute('aria-hidden') !== 'true') { var ra = ai.getBoundingClientRect(); if (ra.width > 0 && ra.height > 0) return true; }
      } catch (e) {}
      return false;
    }
    function _tourSubPlace() {
      var el = _TSUB.el, root = $('cf-root');
      if (!el || !root) return;
      /* core-v747 OVER THE DRAWERS: while one is open the band lives in the
         page's body, fixed over it (2147483000, core-v803; the assistant is 99000, the item drawer 100006), at the
         window's foot -- inside the canvas no z-index lifts it over them */
      var dw = _tourDrawerUp(), host = dw ? global.document.body : root;
      if (el.parentNode !== host) host.appendChild(el);
      el.classList.toggle('cf-over', dw);
      if (dw) {
        var vl = Math.round(global.innerWidth / 2) + 'px', vb = Math.round(global.innerHeight * 0.07) + 'px';
        if (el.style.left !== vl) el.style.left = vl;
        if (el.style.bottom !== vb) el.style.bottom = vb;
        return;
      }
      if (el.style.left) el.style.left = '';
      var rr = root.getBoundingClientRect(), top = rr.bottom, ids = ['cf-cmdbar', 'cf-sx-bar', 'cf-sx-sl'], k;
      for (k = 0; k < ids.length; k++) {
        var b = $(ids[k]);
        if (!b || b.classList.contains('cf-cmd-off')) continue;
        var cs = null;
        try { cs = global.getComputedStyle(b); } catch (eC) { cs = null; }
        if (cs && (cs.display === 'none' || cs.visibility === 'hidden' || Number(cs.opacity) === 0)) continue;
        var r = b.getBoundingClientRect();
        if (r.height > 0 && r.top > rr.top && r.top < top) top = r.top;
      }
      var v = (top < rr.bottom) ? (Math.round(rr.bottom - top + 15) + 'px') : '';
      if (el.style.bottom !== v) el.style.bottom = v;
    }
    /* where the voice is: { key, frac } or null */
    function _tourSubWhere() {
      var a = _T.audio, i = _T.i, S = (i >= 0) ? _TOUR_STEPS[i] : null;
      if (i !== _TSUB.stepI) { _TSUB.stepI = i; _TSUB.stepT0 = Date.now(); }
      if (_T.voice && a && _TS && _T.segs) {
        var t = a.currentTime || 0, G = _T.segs, k;
        for (k = G.length - 1; k >= 0; k--) {
          var g = G[k];
          if (g.from != null && g.to != null && t >= g.from && t < g.to) return { key: _tourSubKey(g.name), frac: (t - g.from) / Math.max(0.1, g.to - g.from) };
        }
        /* a step whose clip did not come runs on its own time: so does its
           subtitle */
        if (S && _T.clipLost === i) return { key: _tourSubStepKey(S), frac: (Date.now() - _TSUB.stepT0) / Math.max(1000, S.sec * 1000) };
        return null;
      }
      if (!S) return null;
      if (_T.voice && a && !_TS) {
        if (!(a.duration > 0) || a.paused || a.ended) return null;
        return { key: _tourSubStepKey(S), frac: a.currentTime / a.duration };
      }
      return { key: _tourSubStepKey(S), frac: (Date.now() - _TSUB.stepT0) / Math.max(1000, S.sec * 1000) };
    }
    function _tourSubTick() {
      if (!_T.on || !_tourSubsOn()) { _tourSubShow(''); return; }
      if (!_TSUB.txt) { _tourSubsText(); _tourSubShow(''); return; }
      var w = _tourSubWhere();
      if (!w || !w.key || !(w.frac >= 0) || w.frac >= 1) { _tourSubShow(''); return; }
      if (w.key !== _TSUB.key) {
        _TSUB.key = w.key;
        _TSUB.chunks = _tourSubChunks(_TSUB.txt[w.key]);
        var said = 'tour     subtitle ' + w.key + ': ' + _TSUB.chunks.length + ' line(s)';
        if (said !== _TSUB.said) { _TSUB.said = said; _log(said); }
      }
      var C = _TSUB.chunks || [], j;
      for (j = 0; j < C.length; j++) if (w.frac < C[j].end) break;
      _tourSubShow(C[Math.min(j, C.length - 1)] ? C[Math.min(j, C.length - 1)].t : '');
    }
    function _tourSubInit() {
      if (_TSUB.iv) return;
      if (_tourSubsOn()) _tourSubsText();
      _TSUB.iv = global.setInterval(_tourSubTick, 150);
      _log('tour     subtitles ' + (_tourSubsOn() ? ('ON, ' + TOUR_SUBS_LANG + ', the lower third, a cross to close them') : 'off') + ' (core-v700; TOUR_SUBS ' + TOUR_SUBS + '; ?ig or ?subs=1 shows them on a page, ?subs=0 hides them, the cross closes them for this visitor)');
    }
    /* core-v754: the creator pitch's big PLAY shrinks into its place over the
       rail on the first tap (Marek: po kliknuti play se minimalizuje) --
       measured big and small, then animated from the one to the other */
    function _tourPitchShrink() {
      var b = _T.btn;
      if (!b || !b.classList.contains('cf-pitch-big')) return;
      var r0 = b.getBoundingClientRect();
      b.classList.remove('cf-pitch-big');
      try { _tourPlace(); } catch (eP) {}
      var r1 = b.getBoundingClientRect();
      try {
        if (b.animate && r0.width > 0 && r1.width > 0) {
          var dx = Math.round((r0.left + r0.width / 2) - (r1.left + r1.width / 2)), dy = Math.round((r0.top + r0.height / 2) - (r1.top + r1.height / 2)), k = r0.width / r1.width;
          b.animate([{ transform: 'translate(' + dx + 'px,' + dy + 'px) scale(' + k.toFixed(3) + ')' }, { transform: 'none' }], { duration: 650, easing: 'cubic-bezier(0.3,0.7,0.2,1)' });
        }
      } catch (eA) {}
      _log('tour     creator pitch: the big PLAY shrinks into its corner (core-v754)');
    }
    function _tourMount() {
      if (!_tourOn() || _T.btn) return;
      if (!_tourVoiceLang()) { _log('tour     not shown -- no voice for the language ' + _tourLang() + ' (core-v668: English and Spanish)'); return; }
      var root = $('cf-root');
      if (!root) return;
      _tourCss();
      var b = global.document.createElement('button');
      b.type = 'button';
      b.id = 'cf-tour-btn';
      b.className = _tourPitch() ? 'cf-pitch-big' : 'cf-idle cf-intro-wait';   /* core-v754 the creator pitch: big in the middle, no entrance */
      if (_tourPitch()) _T.introDone = true;
      b.innerHTML = _TOUR_SVG.play;
      b.setAttribute('aria-label', 'Tour');
      b.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        b.classList.remove('cf-intro');
        if (_T.on) _tourPause('the pause button');
        else if (_tourIgPlay()) _tourIgGo(function () { _tourPitchShrink(); _tourStart(); });
        else { _tourPitchShrink(); _tourStart(); }
      });
      root.appendChild(b);
      _T.btn = b;
      try { _tourSubInit(); } catch (eTs) { _log('tour     subtitles THREW ' + ((eTs && eTs.message) || eTs)); }
      /* the entrance once the button is on screen (core-v685, core-v688): it
         shows at 200 % and settles to its size, then the soft pulse of
         cf-idle. It waits unseen
         (cf-intro-wait) so it does not show, vanish and come back; 6 s at
         the latest whatever the observer says */
      var intro = function () {
        if (_T.introDone) return;
        _T.introDone = true;
        b.classList.remove('cf-intro-wait');
        if (_T.on) return;
        b.classList.add('cf-intro');
        _log('tour     play button: the entrance -- from 200 % at 10 % opacity to its size at full opacity, then the soft pulse (core-v698)');
        global.setTimeout(function () { b.classList.remove('cf-intro'); }, 2000);
      };
      try {
        var io = new global.IntersectionObserver(function (E) {
          if (E.some(function (x) { return x.isIntersecting && x.intersectionRatio > 0.6; })) { io.disconnect(); global.setTimeout(intro, 300); }
        }, { threshold: [0, 0.6, 1] });
        io.observe(b);
      } catch (eIo) { global.setTimeout(intro, 1200); }
      global.setTimeout(intro, 6000);
      /* the first step's clip fetched ahead, once the page has settled
         (core-v681) */
      global.setTimeout(function () { if (!_T.on) _tourPreStep(_TOUR_STEPS[0], 'the first step, before play'); }, 3000);
      /* ONLY THE PAUSE BUTTON PAUSES (core-v639, Marek: pauza pruvodce jen
         kdyz kliknu na ikonu pause, jinak to ridi a ovlada pruvodce): a
         touch, the wheel or the mouse no longer stop it */
      _tourPlace();
      global.addEventListener('resize', _tourPlace);
      _T.placeT = global.setInterval(_tourPlace, 1000);
      /* a page left mid-tour keeps the visitor's project, not the tour's
         (core-v636): the stop writes it back before the page goes */
      global.addEventListener('pagehide', function () { if (_T.on) _tourStop('the page was left'); });
      try { _tourAutoArm(); } catch (eAu) { _log('tour     autoplay THREW ' + ((eAu && eAu.message) || eAu)); }
      _log('tour     play button over the rail, on its axis, 12 px under the top edge -- ' + _TOUR_STEPS.length + ' step(s), paused only by its own button (core-v645)' + (_tourPitch() ? '; the creator pitch: big in the middle until the first tap (core-v754)' : ''));
    }
    /* core-v732 (homepage hero, Marek 2026-10-21: na homepage primo
       configurator ADU a jeho IG prezentace, spustit i bez mackani, zvuk az
       klikem na ikonu sound on): a page opened with ?ig&autoplay (the hero's
       frame, templates/product.embed.json) starts the reel by itself, muted,
       once the page has loaded and the stage is up, and plays it again from
       the top 1.5 s after its end. window.LEVA_TOUR_SOUND(on) turns the
       voice on / off -- the hero's sound icon calls it inside its own click
       (same origin), so the browser lets the sound out; with no argument it
       says whether the voice is on. window.LEVA_TOUR_RUN(on) pauses the reel
       / goes on (the hero off / back on screen). window.LEVA_TOUR_ON is true
       once the reel has started. Such a frame never writes a short link
       (_shareRelease). A page without autoplay is as before. */
    function _tourAuto() {
      try { return _tourIg() && new URL(global.location.href).searchParams.has('autoplay'); } catch (e) { return false; }
    }
    function _tourAutoGo(why) {
      if (_T.on || !_T.btn || !_TA.run) return;
      var st = _stage();
      if (!(st && st.persCam) && _TA.tries++ < 30) { global.setTimeout(function () { _tourAutoGo(why); }, 700); return; }
      /* the player is a <video> here: a browser starts a muted video by
         itself (playsinline), a muted <audio> it does not; the voice plays
         from it all the same */
      try {
        if (!_T.audio) {
          var v = global.document.createElement('video');
          v.muted = true; v.playsInline = true; v.preload = 'auto';
          v.setAttribute('muted', ''); v.setAttribute('playsinline', ''); v.setAttribute('webkit-playsinline', '');
          v.addEventListener('timeupdate', function () { _tourStreamTick(); });
          _T.audio = v;
        }
        _T.audio.muted = !_TA.sound;
      } catch (eA) {}
      _log('tour     autoplay -- ' + why + (_TA.sound ? ', with the voice' : ', muted') + ' (core-v732)');
      _tourStart();
      global.LEVA_TOUR_ON = true;
    }
    function _tourAutoAgain() {
      if (!_TA.armed) return;
      global.setTimeout(function () { _tourAutoGo('the reel again from the top'); }, 1500);
    }
    function _tourAutoArm() {
      if (_TA.armed || !_tourAuto()) return;
      _TA.armed = true;
      global.LEVA_TOUR_SOUND = function (on) {
        if (on === undefined) return _TA.sound;
        _TA.sound = !!on;
        var a = _T.audio;
        if (a) {
          try { a.muted = !_TA.sound; } catch (eM) {}
          /* inside the caller's click: the element is let out with its sound */
          if (_TA.sound && _T.on && _T.voice) { try { var pr = a.play(); if (pr && pr.catch) pr.catch(function () {}); } catch (eP) {} }
        }
        /* a voice the browser had refused: the step again, now with it */
        if (_TA.sound && _T.on && !_T.voice) { _tourPause('the sound was turned on'); _tourAutoGo('again with the voice'); }
        /* core-v734: off again -- the step goes on fast, without the voice */
        else if (!_TA.sound && _T.on && _T.voice) { _tourPause('the sound was turned off'); _tourAutoGo('again without the voice'); }
        _log('tour     autoplay: the voice ' + (_TA.sound ? 'on' : 'off') + ' (core-v732)');
        return _TA.sound;
      };
      global.LEVA_TOUR_RUN = function (on) {
        _TA.run = !!on;
        if (!_TA.run) { if (_T.on) _tourPause('the stage left the screen'); }
        else if (!_T.on) _tourAutoGo('the stage is back on screen');
        return _TA.run;
      };
      /* core-v735 (Marek 2026-10-21 12:09: "bez spodniho grid. Maximalne
         udelat v okoli nejake faded jakoby dalsi objekty - stromy"): the
         page's LEVA_SCENERY_FADE (the hero's frame) puts the spruces of the
         scenery round the house, see-through at that opacity */
      try {
        var fadeO = +global.LEVA_SCENERY_FADE;
        if (fadeO > 0 && fadeO < 1) {
          var sT = _treesState();
          sT.on = { a: false, b: true, c: false };
          _buildScenery();
          var fadeN = 0;
          global.setInterval(function () {
            var stF = _stage(); if (!stF || !stF.scene) return;
            var hit = 0;
            stF.scene.traverse(function (ob) {
              if (!ob || !/^trees-/.test(String(ob.name || ''))) return;
              ob.traverse(function (m) {
                /* core-v736: half the trees */
                if (m.isInstancedMesh && !(m.userData && m.userData.heroHalf)) { m.userData = m.userData || {}; m.userData.heroHalf = 1; m.count = Math.max(1, Math.ceil(m.count / 2)); }
                if (!m.material) return;
                [].concat(m.material).forEach(function (mt) {
                  if (!mt || (mt.userData && mt.userData.heroFade)) return;
                  mt.userData = mt.userData || {}; mt.userData.heroFade = 1;
                  mt.transparent = true; mt.opacity = fadeO; mt.depthWrite = false; mt.needsUpdate = true; hit++;
                });
              });
            });
            if (hit && fadeN++ < 5) { _log('tour     the hero: ' + hit + ' tree material(s) faded to ' + fadeO + ' (core-v735)'); try { if (stF.wake) stF.wake(300); } catch (eW) {} }
          }, 1000);
        }
      } catch (eSc) { _log('tour     the hero scenery THREW ' + ((eSc && eSc.message) || eSc)); }
      /* core-v736: a slow turn the whole time the reel runs, about 4 degrees
         a second whatever the screen's frame rate (the controls turn by a
         step per frame: the speed is set from the frames drawn in the last
         second, 60 fps = 0.7) */
      /* core-v745 (Marek 21:01: "Pohyb animace je docela trhany ... exploded view
         je pak animovany dobre - je rychlejsi ale za me idealni tempo"): the
         turn is the reel's own now, by the clock -- each frame turns the camera
         by the time since the last one (8 degrees a second, the exploded
         view's pace; a frame later than 120 ms counts as 120 ms, so a long
         build slows the turn for a moment instead of jumping it). The
         controls' autoRotate turned a fixed step per frame with the speed
         guessed once a second from the frames drawn, so every uneven frame
         showed as a stutter */
      var HERO_TURN = 8 * Math.PI / 180, rotLast = 0;
      var rotTick = function (now) {
        global.requestAnimationFrame(rotTick);
        try {
          var cR = _tourControls();
          if (cR && cR.autoRotate) cR.autoRotate = false;
          var t = (typeof now === 'number') ? now : _perfNow();
          if (!_TA.run || STATE.view !== '3d') { rotLast = 0; return; }
          var dt = rotLast ? Math.min(0.12, Math.max(0, (t - rotLast) / 1000)) : 0;
          rotLast = t;
          var stR = _stage(), cam = stR && stR.persCam;
          if (!dt || !cam || !cR || !cR.target) return;
          /* core-v746: window.LEVA_HERO_TURN (degrees a second) -- the hero's
             video is recorded with the turn that makes one loop of the reel
             exactly one full turn, so the video loops without a jump */
          var gT = +global.LEVA_HERO_TURN, wT = (gT > 0 && gT < 90) ? gT * Math.PI / 180 : HERO_TURN;
          var a = wT * dt, cs = Math.cos(a), sn = Math.sin(a), dx = cam.position.x - cR.target.x, dz = cam.position.z - cR.target.z;
          cam.position.x = cR.target.x + dx * cs - dz * sn;
          cam.position.z = cR.target.z + dx * sn + dz * cs;
          cam.lookAt(cR.target);
        } catch (eRt) {}
      };
      global.requestAnimationFrame(rotTick);
      var go = function () { global.setTimeout(function () { _tourAutoGo('the page has loaded'); }, 1500); };
      if (global.document.readyState === 'complete') go(); else global.addEventListener('load', go);
      _log('tour     ?ig&autoplay: the reel starts by itself, muted, and loops (core-v732)');
    }
    /* ONE PLACE ON EVERY PRODUCT (core-v645, Marek: tlacitko ma byt porad
       na kazdem projektu na stejnem miste -- na ose praveho banneru, nad
       nim, pod hornim okrajem; vsude): 12 px under the canvas's top edge,
       its middle on the rail's axis, whatever else is on the canvas. The
       rail and the edit strip start under it (cf-tour-top), and a rail too
       tall for what is left is drawn smaller about its own middle, so the
       axis stays where it is. Runs every second and on a resize. */
    var TOUR_TOP = 12, TOUR_D = 38;
    /* EVERY ?ig POST (core-v763, Marek 2026-10-27 10:38: "Tlacitko play dej na stred dolu, ale po zmacknuti, dve vteriny nic, pak spustit. Tlacitko mezitim schovat - plati pro vsechny prispevky pro ig, ktere budeme delat"): the PLAY in the middle of
       the canvas's bottom edge, above the command bar; a tap hides it and the
       reel starts 2 s later, so the recording begins on a clean screen; it
       comes back when the reel ends (_tourStop). Not the hero's muted
       autoplay, not the creator pitch's big PLAY. */
    function _tourIgPlay() { return _tourIg() && !_tourAuto() && !_tourPitch(); }
    function _tourIgGo(fn) {
      if (_T.igWait || !_T.btn) return;
      _T.igWait = true;
      _T.btn.classList.add('cf-ig-gone');
      _log('tour     ?ig: PLAY hidden, the reel starts in 2 s (core-v763)');
      global.setTimeout(function () { _T.igWait = false; fn(); }, 2000);
    }
    function _tourPlace() {
      var b = _T.btn, root = $('cf-root');
      if (!b || !root) return;
      if (_tourIgPlay()) {
        var bt = 25;
        try {
          var rr = root.getBoundingClientRect(), cb = $('cf-cmdbar'), cr = cb ? cb.getBoundingClientRect() : null;
          if (cr && cr.height > 0) bt = Math.max(12, Math.round(rr.bottom - cr.top + 12));
        } catch (eB) {}
        var want = { top: 'auto', right: 'auto', left: 'calc(50% - ' + (TOUR_D / 2) + 'px)', bottom: bt + 'px' };
        for (var kw in want) if (want.hasOwnProperty(kw) && b.style[kw] !== want[kw]) b.style[kw] = want[kw];
        return;
      }
      if (!root.classList.contains('cf-tour-top')) root.classList.add('cf-tour-top');
      var rail = global.document.querySelector('#cf-root .cf-sidebar'), right = 21;
      try {
        var R = root.getBoundingClientRect(), a = rail ? rail.getBoundingClientRect() : null;
        if (!(a && a.width > 0)) { rail = null; a = null; var sx = $('cf-sx-strip'); if (sx) a = sx.getBoundingClientRect(); }
        if (a && a.width > 0) right = Math.round(R.right - (a.left + a.width / 2) - TOUR_D / 2);
        if (rail) {
          /* the rail's own height, not its drawn one */
          var h = rail.offsetHeight, H = root.clientHeight, k = 1;
          if (h > 0 && H > 0 && h > H - 70) k = Math.max(0.6, (H - 70) / h);
          var tf = (k < 0.999) ? ('translateY(-50%) scale(' + (Math.round(k * 1000) / 1000) + ')') : '';
          if (rail.style.transform !== tf) {
            rail.style.transform = tf;
            if (_T.railK !== k) { _T.railK = k; _log('tour     the rail drawn at ' + Math.round(k * 100) + ' % under the PLAY (' + h + ' px tall, ' + H + ' px of canvas)'); }
          }
        }
      } catch (e) {}
      if (b.style.top !== TOUR_TOP + 'px') b.style.top = TOUR_TOP + 'px';
      if (b.style.right !== right + 'px') b.style.right = right + 'px';
    }
    /* the partition the plan step drags: the first on the ground floor, or
       one drawn for the purpose when the plan has none (put back after) */
    function _tourPart() {
      var L = STATE.parts || [], i;
      for (i = 0; i < L.length; i++) if (L[i] && !L[i].bear && (L[i].st | 0) === 0) return L[i];
      if (_T.partTried) return null;
      _T.partTried = true;
      try { addPartition('v'); } catch (eA) { return null; }
      L = STATE.parts || [];
      for (i = 0; i < L.length; i++) if (L[i] && !L[i].bear && (L[i].st | 0) === 0) return L[i];
      return null;
    }
    function _tourPartById(id) {
      var L = STATE.parts || [], i;
      for (i = 0; i < L.length; i++) if (L[i] && L[i].id === id) return L[i];
      return null;
    }
    /* ---- small helpers ----------------------------------------------------- */
    function _tourLater(fn, ms, run) {
      /* the steps walked in the background only ask, they schedule nothing (core-v697) */
      if (_T.probe) return 0;
      var id = global.setTimeout(function () { if (run === _T.run) { try { fn(); } catch (e) { _log('tour     step THREW ' + ((e && e.message) || e)); _tourStop('an error in the step'); } } }, ms);
      _T.tm.push(id);
      return id;
    }
    /* AN ICON BELOW THE STRIP'S FOLD IS BROUGHT UP FIRST (core-v639, Marek:
       musis posunout i carousel ikon, pokud nejsou videt -- jsou schovane
       dole v banneru, musela by se scrollovat sipka): the strip's list is
       scrolled, smoothly, until the icon stands whole in it; the fades and
       the arrow follow by the list's own scroll handler */
    function _tourSee(el) {
      try {
        var L = el && el.closest ? el.closest('.cf-sx-list') : null;
        if (!L) return;
        var top = el.offsetTop - L.offsetTop, h = el.offsetHeight || 40, st = L.scrollTop, vis = L.clientHeight;
        if (top >= st + 2 && top + h <= st + vis - 2) return;
        var to = Math.max(0, Math.min(L.scrollHeight - vis, Math.round(top - (vis - h) / 2)));
        try { L.scrollTo({ top: to, behavior: 'smooth' }); } catch (eS) { L.scrollTop = to; }
      } catch (e) {}
    }
    /* the icon a passage walks over, one at a time */
    function _tourHl(el) {
      if (_T.hl && _T.hl !== el) { try { _T.hl.classList.remove('cf-tour-hl'); } catch (e) {} }
      _T.hl = el || null;
      if (el) { _tourSee(el); try { el.classList.add('cf-tour-hl'); } catch (e2) {} }
    }
    /* the icons the voice talks about, pulsing for as long as it does */
    function _tourPulse(list) {
      _T.pl.forEach(function (el) { try { el.classList.remove('cf-tour-pulse'); } catch (e) {} });
      _T.pl = (list || []).filter(function (el) { return !!el; });
      _T.pl.forEach(function (el) { _tourSee(el); try { el.classList.add('cf-tour-pulse'); } catch (e) {} });
    }
    function _tourBub(el, text) {
      var root = $('cf-root');
      if (!root) return;
      if (!_T.bub) {
        _T.bub = global.document.createElement('div');
        _T.bub.id = 'cf-tour-bub';
        root.appendChild(_T.bub);
      }
      var B = _T.bub;
      if (!el || !text || _T.voice) { B.classList.remove('cf-on'); return; }
      var r = el.getBoundingClientRect(), R = root.getBoundingClientRect();
      if (!r.width && !r.height) { B.classList.remove('cf-on'); return; }
      B.textContent = text;
      var side = !!(el.closest && el.closest('#cf-sx-strip'));
      B.classList.toggle('cf-side', side);
      B.style.left = Math.round(side ? (r.left - R.left - 10) : (r.left - R.left + r.width / 2)) + 'px';
      B.style.top = Math.round(side ? (r.top - R.top + r.height / 2) : (r.top - R.top)) + 'px';
      B.classList.add('cf-on');
      try {
        var b = B.getBoundingClientRect();
        if (b.right > R.right - 6) B.style.left = Math.round(parseFloat(B.style.left) - (b.right - R.right + 6)) + 'px';
        if (b.left < R.left + 6) B.style.left = Math.round(parseFloat(B.style.left) + (R.left + 6 - b.left)) + 'px';
      } catch (eB) {}
    }
    function _tourLabel(el) {
      if (!el) return '';
      return String(el.getAttribute('aria-label') || el.getAttribute('title') || '').trim();
    }
    function _tourStrip() { return $('cf-sx-strip'); }
    function _tourStripIcon(k) { var s = _tourStrip(); return s ? s.querySelector('.cf-sx-b[data-k="' + k + '"]') : null; }
    function _tourStripClose() {
      var s = _tourStrip(), x = s ? s.querySelector('.cf-sx-x') : null;
      if (x) x.click();
      else { try { if (_wiz && _wiz.isOpen && _wiz.isOpen()) _wiz.close(); } catch (e) {} }
    }
    /* the plan strip, opened by its own icon when a step finds it shut */
    function _tourPlanOpen(keepLock) {
      var on = false;
      try { on = _sxPlanOpen(); } catch (e) { on = false; }
      if (!on && _cmd.plan) _cmd.plan.click();
      /* core-v648: opened unlocked, so a tile's tap does not open the
         padlock with a whole build -- the plan step shows the padlock itself */
      if (!keepLock) _tourUnlock();
    }
    /* the plan strip's Trace tile, found by what it does, not by its word */
    function _tourTraceIcon() {
      var I = null;
      try { (_SX.icons || []).forEach(function (x) { if (!I && x && x.item && x.item.tap === planTraceTap) I = x; }); } catch (e) { I = null; }
      return I ? _tourStripIcon(I.key) : null;
    }
    function _tourControls() {
      var st = _stage();
      return (st && (st.persControls || st.controls)) || null;
    }
    function _tourField(v) {
      var inp = _cmd.inp;
      if (!inp) return;
      inp.value = v;
      try { inp.scrollLeft = inp.scrollWidth; } catch (eS) {}
      try { _cmdHint(); } catch (eH) {}
    }

    /* the project as it was when play was pressed, put back without a word */
    /* THE CANVAS FOLLOWS THE HOUSE, IN THE SAME FRAME (core-v638, Marek:
       zoom in / out behem zmeny modelu nevypada dobre -- fit canvas behem
       kazde prestavby, nebo radeji vetsi zoom out, at to nepodskakuje).
       Called right after the rebuild, so the next frame shows the new house
       already framed: no easing, nothing zooms on screen. The whole house
       fits with room to spare (the sphere round it, not 0.85 of it). With
       out: true the camera only ever steps back -- a smaller house that
       follows a bigger one is shown from the same distance, so a run of
       changes never pumps in and out. k widens the margin further. */
    /* core-v736: the hero -- the roof's four overhangs out by 1.2 m and back */
    /* core-v737: the exploded view in the hero's reel, setExplode without
       its own animation, driven frame by frame; the camera backs off with it
       along its own line of sight (1.4 x when spread, harness: the spread
       house then stays in the frame) and comes back to the same distance.
       core-v740 (Marek 2026-10-21 17:09: "Exploded view nech delsi dobu,
       muzes ho i chvili rotovat, pak exploded off a pokracovat"): out in
       1.2 s, held 3.4 s while the camera turns faster round it (about 100
       degrees, eased in and out), back together in 1.2 s */
    function _tourXpPulse(run, cb) {
      /* core-v743 (Marek 18:50: mirne zoom out, pri vraceni zpet zase zoom in do
         stejneho mista; pri exploded fazi muze byt rotace mirne rychlejsi): the
         camera 1.2 x out and back to the very same distance, no framing after;
         the extra turn 22 degrees (core-v745: 10, on top of the faster
         8 degree a second turn) over the whole pulse, eased (on top of the
         slow turn).
         core-v744 (Marek 19:47: "pri exploded musi zaroven byt zoom out (prvky
         utikaji mimo zobrazeni), a pri opetovnem slozeni zase zoom in"): 1.2 x
         was too little -- the zoom is now measured on the exploded house
         itself: its box at the peak, seen from every angle the turn passes
         (-60 to +60 degrees), must stay inside the picture (the right edge
         and the top and bottom with a margin; the left may go under the
         text); the camera's target moves with the box's centre and back */
      var OUT = 1200, HOLD = 3400, BACK = 1200, PEAK = 1.8, SPIN = 10 * Math.PI / 180, TOT = OUT + HOLD + BACK, tA = _perfNow(), st = _stage(), n = 0;
      /* the spread is worked out here, before the clock starts */
      try { setExplode(0, false); }
      catch (eX0) { _log('tour     the hero: no exploded view (' + ((eX0 && eX0.message) || eX0) + ')'); cb(); return; }
      var cam = st && st.persCam, ctl = _tourControls(), d0 = 0, spun = 0, K = 1.2, dv = null, tg0 = null, why = 'no camera';
      try { if (cam && ctl && ctl.target) { d0 = cam.position.distanceTo(ctl.target); tg0 = ctl.target.clone(); } } catch (eD) { d0 = 0; }
      try {
        var T3 = global.THREE, b0 = new T3.Box3().setFromObject(st.group);
        setExplode(PEAK, false);
        var b1 = new T3.Box3().setFromObject(st.group), C = [], nP = 0;
        /* the corners of every flying piece's own box at the peak (and of
           the house at rest for what stays) -- one box round the whole spread
           has empty corners and zoomed out three times over */
        var addBox = function (bx) {
          if (!bx || bx.isEmpty() || !isFinite(bx.min.x)) return;
          var P2 = [bx.min, bx.max], a2, b2, q2;
          for (a2 = 0; a2 < 2; a2++) for (b2 = 0; b2 < 2; b2++) for (q2 = 0; q2 < 2; q2++) C.push(new T3.Vector3(P2[a2].x, P2[b2].y, P2[q2].z));
        };
        var spN = -1, spWhy = '';
        try {
          var SP = _asmSpread(); spN = SP ? SP.length : -1;
          if (SP && SP.length && SP.length <= 8000) {
            var bxP = new T3.Box3();
            SP.forEach(function (e) { if (e && e.m) { bxP.setFromObject(e.m); addBox(bxP); nP++; } });
            addBox(b0);
            /* thousands of pieces: only the outline counts -- the outermost
               corner in each of 64 directions round a sphere */
            if (C.length > 512) {
              var D = [], KD = 64, g = Math.PI * (3 - Math.sqrt(5)), cB = b1.getCenter(new T3.Vector3()), keep = {}, out2 = [], kd, ci;
              for (kd = 0; kd < KD; kd++) { var yy = 1 - (kd + 0.5) * 2 / KD, rr = Math.sqrt(1 - yy * yy), th = g * kd; D.push([Math.cos(th) * rr, yy, Math.sin(th) * rr]); }
              for (kd = 0; kd < KD; kd++) {
                var bi = -1, bv = -Infinity, dx = D[kd][0], dy = D[kd][1], dz = D[kd][2];
                for (ci = 0; ci < C.length; ci++) { var pc = C[ci], dd = (pc.x - cB.x) * dx + (pc.y - cB.y) * dy + (pc.z - cB.z) * dz; if (dd > bv) { bv = dd; bi = ci; } }
                if (bi >= 0 && !keep[bi]) { keep[bi] = 1; out2.push(C[bi]); }
              }
              C = out2;
            }
          }
        } catch (eSp) { C = []; nP = 0; spWhy = ((eSp && eSp.message) || String(eSp)).slice(0, 60); }
        if (!C.length) addBox(b1);
        setExplode(0, false);
        if (d0 > 0 && !b1.isEmpty() && isFinite(b1.min.x)) {
          dv = b1.getCenter(new T3.Vector3()).sub(b0.getCenter(new T3.Vector3()));
          var tg1 = tg0.clone().add(dv);
          var probe = cam.clone(), dir0 = cam.position.clone().sub(tg0).normalize(), v = new T3.Vector3();
          var fits = function (dir, d) {
            probe.position.copy(tg1).addScaledVector(dir, d); probe.lookAt(tg1); probe.updateMatrixWorld(true);
            for (var i = 0; i < C.length; i++) { v.copy(C[i]).project(probe); if (v.z > 1 || v.x > 0.96 || v.x < -0.98 || v.y > 0.94 || v.y < -0.94) return false; }
            return true;
          };
          var need = d0 * 1.12, ang;
          for (ang = -60; ang <= 60; ang += 15) {
            var r = ang * Math.PI / 180, cs = Math.cos(r), sn = Math.sin(r);
            var dir = new T3.Vector3(dir0.x * cs - dir0.z * sn, dir0.y, dir0.x * sn + dir0.z * cs).normalize();
            if (fits(dir, need)) continue;
            var lo = need, hi = d0 * 4, it;
            if (!fits(dir, hi)) { need = hi; continue; }
            for (it = 0; it < 14; it++) { var mid = (lo + hi) / 2; if (fits(dir, mid)) hi = mid; else lo = mid; }
            need = hi;
          }
          K = Math.min(4, need / d0);
          why = (nP ? nP + ' pieces measured (' + C.length + ' outline points), ' : 'one box (' + spN + ' pieces' + (spWhy ? ', ' + spWhy : '') + '), ') + 'the exploded box ' + (b1.max.x - b1.min.x).toFixed(1) + ' x ' + (b1.max.y - b1.min.y).toFixed(1) + ' x ' + (b1.max.z - b1.min.z).toFixed(1) + ' m, its centre ' + dv.length().toFixed(2) + ' m off';
        }
      } catch (eM) { K = 1.2; dv = null; why = 'measuring threw ' + ((eM && eM.message) || eM); }
      var prep = Math.round(_perfNow() - tA), t0 = _perfNow(), tgP = tg0 ? tg0.clone() : null;
      var ease = function (x) { x = Math.max(0, Math.min(1, x)); return (1 - Math.cos(Math.PI * x)) / 2; };
      /* the camera at f x its first distance from the target moved c of the
         way to the exploded centre, turned by da round the vertical axis --
         the direction read against where the target was the frame before,
         so the slow turn in between is kept */
      var camAt = function (f, da, c) {
        if (!(d0 > 0)) return;
        try {
          var dir = cam.position.clone().sub(tgP || ctl.target); if (dir.lengthSq() < 1e-9) return;
          if (da) { var cs = Math.cos(da), sn = Math.sin(da), x = dir.x * cs - dir.z * sn, z = dir.x * sn + dir.z * cs; dir.x = x; dir.z = z; }
          if (tg0) { ctl.target.copy(tg0); if (dv && c) ctl.target.addScaledVector(dv, c); tgP = ctl.target.clone(); }
          dir.normalize(); cam.position.copy(ctl.target).addScaledVector(dir, d0 * f);
        } catch (eC) {}
      };
      var tick = function () {
        if (run !== _T.run) { try { setExplode(0, false); } catch (eS) {} camAt(1, 0, 0); return; }
        var t = _perfNow() - t0, c, sp;
        if (t < OUT) c = ease(t / OUT);
        else if (t < OUT + HOLD) c = 1;
        else c = 1 - ease((t - OUT - HOLD) / BACK);
        /* the extra turn: its share of SPIN so far, eased over the whole pulse */
        sp = SPIN * ease(t / TOT);
        var da = sp - spun; spun = sp;
        try { setExplode(PEAK * c, false); } catch (eT) {}
        camAt(1 + (K - 1) * c, da, c);
        n++;
        try { if (st && st.wake) st.wake(250); } catch (eW) {}
        if (t < OUT + HOLD + BACK) { global.requestAnimationFrame(tick); return; }
        try { setExplode(0, false); } catch (eE) {}
        camAt(1, 0, 0);
        _log('tour     the hero: the exploded view out to ' + PEAK + ' with the camera ' + K.toFixed(2) + ' x out (' + why + '), held and turned ' + Math.round(spun * 180 / Math.PI) + ' degrees, back in ' + Math.round(_perfNow() - t0) + ' ms, ' + n + ' frames, worked out in ' + prep + ' ms (core-v744)');
        cb();
      };
      global.requestAnimationFrame(tick);
    }
    /* core-v744: THE HERO'S PICTURE OFF CENTRE (Marek 19:47: "muzes ho nechat
       animovat pod celym section (i pod sloupcem vlevo)"). The homepage's frame
       spans the whole section now; its data-shift (home-hero-v11, 0.2 on the
       desktop's two columns) moves the camera's centre that share of the width
       to the right -- a view offset, as the desktop drawer does it the other
       way (_deskShell) -- so the house stands where it stood beside the text
       while what flies out of it is drawn under the text, not cut off */
    var _heroShiftNow = null;
    function _heroShiftApply(why) {
      if (!_tourAuto()) return;
      var sh = 0;
      try { var fe = global.frameElement; sh = fe ? (+fe.getAttribute('data-shift') || 0) : 0; } catch (eF) { sh = 0; }
      if (!(sh >= -0.45 && sh <= 0.45)) sh = 0;
      var st = _stage(), cam = st && st.persCam, el = st && st.renderer && st.renderer.domElement;
      if (!cam || !el || typeof cam.setViewOffset !== 'function') return;
      var w = el.clientWidth || el.width, h = el.clientHeight || el.height;
      if (!(w > 0 && h > 0)) return;
      var sig = sh + '|' + w + 'x' + h;
      var onNow = !!(cam.view && cam.view.enabled);
      if (sig === _heroShiftNow && onNow === !!sh) return;
      if (sh) cam.setViewOffset(w, h, -Math.round(sh * w), 0, w, h);
      else if (onNow) cam.clearViewOffset();
      cam.updateProjectionMatrix();
      if (_heroShiftNow === null || _heroShiftNow.split('|')[0] !== String(sh)) _log('tour     the hero: the picture ' + (sh ? Math.round(sh * 100) + ' % of the width right of centre' : 'centred') + ' (' + w + 'x' + h + ', ' + (why || '?') + ', core-v744)');
      _heroShiftNow = sig;
      try { if (st.wake) st.wake(300); } catch (eW) {}
    }
    if (_tourAuto()) global.LEVA_HERO_SHIFT = function () { try { _heroShiftApply('the page asked'); } catch (e) {} };
    function _tourEaves(run, cb) {
      var R = STATE.roof;
      if (!R || R.type === 'flat' || R.parapet) { _tourLater(cb, 200, run); return; }
      try { if (__PROD && __PROD._roofTails) { _tourLater(cb, 200, run); return; } } catch (eT) {}
      if (!R.over) R.over = { x0: R.eave || 0.4, x1: R.eave || 0.4, z0: R.eave || 0.4, z1: R.eave || 0.4 };
      var O = R.over, o0 = { x0: O.x0, x1: O.x1, z0: O.z0, z1: O.z1 }, D = [0.3, 0.6, 0.9, 1.2, 1.2, 0.9, 0.6, 0.3, 0], k = 0;
      _log('tour     the hero: the overhangs stretched and back (core-v736)');
      var stepE = function () {
        if (run !== _T.run) return;
        if (k >= D.length) { _tourLater(cb, 300, run); return; }
        var d = D[k++];
        ['x0', 'x1', 'z0', 'z1'].forEach(function (sd) { O[sd] = clamp((+o0[sd] || 0.4) + d, OVER_LIM.min, OVER_LIM.max); });
        R.eave = Math.min(O.x0, O.x1, O.z0, O.z1);
        _T.dirty = true;
        try { rebuild(); _sxRefresh(); } catch (eR) {}
        _tourLater(stepE, 230, run);
      };
      stepE();
    }
    /* core-v743: THE HERO'S PORCH. The overhangs eased (by the clock, so a
       slow phone takes as long as a fast one) to f of HERO_EAVES metres more
       than the house's own -- f 1 out, f 0 exactly back; the posts come
       under every side deeper than POST_TRIGGER */
    var HERO_EAVES = 1.3;
    function _tourEavesTo(f, ms, run, cb) {
      var R = STATE.roof;
      if (!R || R.type === 'flat' || R.parapet) { _tourLater(cb, 100, run); return; }
      try { if (__PROD && __PROD._roofTails) { _tourLater(cb, 100, run); return; } } catch (eT) {}
      if (!R.over) R.over = { x0: R.eave || 0.4, x1: R.eave || 0.4, z0: R.eave || 0.4, z1: R.eave || 0.4 };
      if (!_T.eO0) { var Q = R.over; _T.eO0 = { x0: Q.x0, x1: Q.x1, z0: Q.z0, z1: Q.z1, e: R.eave }; _T.eF = 0; }
      var O = R.over, o0 = _T.eO0, fA = _T.eF || 0, t0 = _perfNow(), n = 0;
      var ease = function (x) { x = Math.max(0, Math.min(1, x)); return (1 - Math.cos(Math.PI * x)) / 2; };
      var stepE = function () {
        if (run !== _T.run) return;
        var x = Math.min(1, (_perfNow() - t0) / ms), fN = fA + (f - fA) * ease(x);
        ['x0', 'x1', 'z0', 'z1'].forEach(function (sd) { O[sd] = (fN > 0) ? clamp((+o0[sd] || 0.4) + HERO_EAVES * fN, OVER_LIM.min, OVER_LIM.max) : o0[sd]; });
        R.eave = (fN > 0) ? Math.min(O.x0, O.x1, O.z0, O.z1) : o0.e;
        _T.eF = fN; _T.dirty = true; n++;
        try { rebuild(); } catch (eR) {}
        /* core-v745: 90 ms of frames between two builds, so the turn goes on
           smoothly while the eaves grow (builds back to back starved them) */
        if (x < 1) { _tourLater(stepE, 90, run); return; }
        _log('tour     the hero: the overhangs ' + (f > 0 ? 'out' : 'back in') + ' in ' + Math.round(_perfNow() - t0) + ' ms, ' + n + ' builds, posts on ' + postBaySides().length + ' side(s) (core-v743)');
        cb();
      };
      stepE();
    }
    /* the infill between the porch posts: the bays of the first side with
       posts filled one after another with slats, then the other slat kinds
       (the direction) and back -- the cladding kinds would close the porch
       into a wall, so the hero leaves them out */
    /* core-v744 (Marek 19:52: "idealne vypnout vypln ve chvili, kdy je behem
       rotace blizko ke kamere"): how squarely a side with posts faces the
       camera, -1 (away) .. 1 (straight at it), on the ground plane */
    function _heroFacing(b) {
      try {
        var st = _stage(), cam = st && st.persCam, ctl = _tourControls(), t = ctl && ctl.target;
        if (!cam || !t || !b) return 0;
        var sx = (b.x0 + b.x1) / 2 - t.x, sz = (b.z0 + b.z1) / 2 - t.z, cx = cam.position.x - t.x, cz = cam.position.z - t.z;
        var ls = Math.sqrt(sx * sx + sz * sz), lc = Math.sqrt(cx * cx + cz * cz);
        return (ls > 1e-6 && lc > 1e-6) ? (sx * cx + sz * cz) / (ls * lc) : 0;
      } catch (e) { return 0; }
    }
    function _tourHeroInfill(run, cb) {
      /* the side that faces the camera most is filled -- the one seen best now
         and while the slats change */
      var L = postBaySides(), keys = [];
      L.sort(function (a, b) { return _heroFacing(b) - _heroFacing(a); });
      var side = L[0] || null;
      L.slice(0, 1).forEach(function (b) { for (var q = 0; q < postBayCount(b); q++) keys.push(_bayKey(b.side, q)); });
      if (!keys.length) { _log('tour     the hero: no posts under the eaves -- no infill'); _tourLater(cb, 200, run); return; }
      var KK = _pwCycle(), KS = KK.filter(function (k) { return k.indexOf('slat') === 0; }), k = 0;
      if (KS.length) KK = KS;
      var slat = KK[0];
      _T.pwKind0 = _postWallState().kind;
      _T.pwOn = [];
      _pwSetKind(slat);
      _log('tour     the hero: ' + keys.length + ' bay(s) filled with ' + slat + ', then ' + KK.join(' / '));
      var nextB = function () {
        if (run !== _T.run) return;
        if (k < keys.length) {
          var key = keys[k++];
          if (!postWallOn(key) && setPostWall(key, true) !== false) _T.pwOn.push(key);
          if (_pwMode.perBay) _pwSel = null;
          _T.dirty = true;
          _tourLater(nextB, 260, run);
          return;
        }
        var i0 = Math.max(0, KK.indexOf(slat)), j = 0;
        /* core-v744 (Marek 19:47: "alespon jednou vypni a pak zapni vypln mezi
           sloupy"): the infill off for a moment and on again */
        /* off when the filled side is nearest the camera: it waits (up to
           6 s) while the turn is still bringing the side round, so long as
           it is not straight on already */
        var waitN = 0, fBest = -2;
        var offOn = function () {
          if (run !== _T.run) return;
          var L2 = (_T.pwOn || []).slice();
          if (!L2.length) { _tourLater(cb, 600, run); return; }
          var f = _heroFacing(side);
          if (f < 0.9 && waitN < 24 && f >= fBest - 0.002) { fBest = Math.max(fBest, f); waitN++; _tourLater(offOn, 250, run); return; }
          L2.forEach(function (key) { setPostWall(key, false); });
          _log('tour     the hero: the infill off and on again, its side ' + Math.round(Math.acos(Math.max(-1, Math.min(1, f))) * 180 / Math.PI) + ' degrees from the camera after ' + (waitN * 0.25).toFixed(2) + ' s (core-v744)');
          _tourLater(function () {
            L2.forEach(function (key) { setPostWall(key, true); });
            if (_pwMode.perBay) _pwSel = null;
            _tourLater(cb, 1000, run);
          }, 1100, run);
        };
        var nextK = function () {
          if (run !== _T.run) return;
          if (j >= KK.length) { _tourLater(offOn, 500, run); return; }
          j++;
          _pwSetKind(KK[(i0 + j) % KK.length]);
          try { rebuild(); } catch (eR) {}
          _tourLater(nextK, (j >= KK.length) ? 900 : 750, run);
        };
        _tourLater(nextK, 1000, run);
      };
      nextB();
    }
    function _tourHeroInfillOff(run, cb) {
      var L = (_T.pwOn || []).slice().reverse(), k = 0;
      var next = function () {
        if (run !== _T.run) return;
        if (k < L.length) { setPostWall(L[k++], false); _tourLater(next, 140, run); return; }
        _T.pwOn = [];
        try { if (_T.pwKind0) _postWallState().kind = _T.pwKind0; } catch (eK) {}
        cb();
      };
      next();
    }
    /* the colours the colour step changed, put back one by one */
    function _tourHeroColorsBack(run, cb) {
      var L = (_T.colBack || []).slice().reverse(), k = 0;
      var next = function () {
        if (run !== _T.run) return;
        if (k >= L.length) { _T.colBack = []; cb(); return; }
        var c = L[k++];
        try { colorSlotSet(c.s, c.k); } catch (eC) {}
        _tourLater(next, 550, run);
      };
      next();
    }
    /* core-v736: the hero -- faded buildings round the house: plain boxes of
       several sizes with windows and a door drawn on, see-through */
    function _tourFit(o) {
      o = o || {};
      var st = _stage(), T = global.THREE;
      if (!st || !T || !st.persCam || !st.group || STATE.view === '2d') return;
      var cam = st.persCam, ctl = st.persControls, box, sph;
      try { box = new T.Box3().setFromObject(st.group); } catch (eB) { return; }
      if (box.isEmpty() || !isFinite(box.min.x)) return;
      sph = box.getBoundingSphere(new T.Sphere());
      if (!(sph.radius > 0)) return;
      var vF = cam.fov * Math.PI / 180, hF = 2 * Math.atan(Math.tan(vF / 2) * (cam.aspect || 1.4));
      var d1 = (sph.radius / Math.sin(Math.min(vF, hF) / 2)) * (o.k || 1.0) * ((+global.LEVA_FIT_K > 0) ? Math.min(3, Math.max(0.5, +global.LEVA_FIT_K)) : 1);   /* core-v735 */
      var tg0 = ctl ? ctl.target : sph.center;
      if (o.out) d1 = Math.max(d1, cam.position.distanceTo(tg0));
      var dir = cam.position.clone().sub(tg0);
      if (dir.lengthSq() < 1e-9) dir.set(1, 0.45, 1);
      dir.normalize();
      if (ctl) {
        ctl.minDistance = Math.min(ctl.minDistance || d1 * 0.3, d1 * 0.3);
        ctl.maxDistance = Math.max(ctl.maxDistance || d1 * 3, d1 * 3);
        ctl.target.copy(sph.center);
      }
      cam.near = Math.max(0.05, d1 / 200); cam.far = Math.max(cam.far || 0, d1 * 6); cam.updateProjectionMatrix();
      cam.position.copy(sph.center.clone().add(dir.multiplyScalar(d1)));
      try { if (ctl) ctl.update(); } catch (eU) {}
      if (st.wake) st.wake(400);
    }
    /* the camera as it was at play, put back at once */
    function _tourCamPlay() {
      if (_tourAuto()) return;   /* core-v736: the hero's camera keeps turning */
      try {
        var st = _stage(), c = st && st.persCam, ctl = _tourControls();
        if (!_T.cam || !c || !ctl) return;
        c.position.copy(_T.cam.p); ctl.target.copy(_T.cam.t);
        ctl.minDistance = _T.cam.mn; ctl.maxDistance = _T.cam.mx;
        c.near = _T.cam.n; c.far = _T.cam.f; c.updateProjectionMatrix();
        ctl.update();
        if (st.wake) st.wake(600);
      } catch (e) {}
    }
    /* (no savePref here: the end of the tour writes the project once) */
    function _tourBack() {
      var src = _T.base || _T.saved;
      if (!src || !_T.dirty) return;
      _T.dirty = false;
      /* core-v745: the hero's walks end on the house's own choice -- the same
         project is not built a second time (a whole build is a stutter) */
      if (_tourAuto()) { try { if (JSON.stringify(payload()) === src) { _tourLightOff(); return; } } catch (eSame) {} }
      _tourLightOff();
      try {
        _applyPayload(JSON.parse(src), 'the tour');
        /* core-v648: on the plan only the plan, the house when 3D shows */
        _tourPaint();
      } catch (eB) { _log('tour     putting the project back THREW ' + ((eB && eB.message) || eB)); }
    }
    /* ---- core-v636: the floor plan tools ------------------------------------
       Every step shows its tool on the visitor's own project and puts the
       project back when its voice ends (_tourBack); what the payload does not
       carry (the door tool, the cut handles, the furniture mode, a plan on its
       way) is ended by hand. While the tour runs no short link is written
       (_shareHeld), so the address never points at a half-changed house. */
    /* THE PLAN'S CAMERA EASED (core-v707): to a point and a zoom in n frames
       of 50 ms, the plan repainted each frame, then cb */
    function _tourPlanEase(tx, tz, zEnd, n, run, cb) {
      var st = _stage(), cam = st && st.orthoCam, oc = st && st.orthoControls;
      if (!cam || !oc || !oc.target || !(zEnd > 0)) { if (cb) cb(); return; }
      var x0 = oc.target.x, z0 = oc.target.z, k0 = cam.zoom || 1, i = 0;
      n = Math.max(1, n | 0);
      var step = function () {
        i++;
        var e = 0.5 - 0.5 * Math.cos(Math.PI * i / n), x = x0 + (tx - x0) * e, z = z0 + (tz - z0) * e;
        cam.zoom = k0 * Math.pow(zEnd / k0, e);
        oc.target.set(x, 0, z);
        cam.position.set(x, cam.position.y, z + 0.0001);
        cam.up.set(0, 0, -1);
        cam.lookAt(x, 0, z);
        _planRepaint(cam);
        try { if (st.wake) st.wake(400); } catch (eW) {}
        if (i < n) _tourLater(step, 50, run); else if (cb) cb();
      };
      step();
    }
    function _tourPlanTool(test) {
      var I = null;
      try { (_SX.icons || []).forEach(function (x) { if (!I && x && x.item && test(x.item)) I = x; }); } catch (e) { I = null; }
      return I ? _tourStripIcon(I.key) : null;
    }
    /* A TOOL PUT AWAY, THE PLAN LEFT OPEN (core-v648, Marek: sekvence
       predstaveni fp u velkeho projektu trva dlouho): the padlock's tap
       built the whole house each time -- 2 s a step on a RANCH -- so the
       plan stays unlocked while the tour is on it; only the tools are off */
    function _tourRelock() {
      STATE.doorTool = false;
      STATE.cutsOn = false;
      try { if (fitEdit()) fitEdit(false, 'the tour'); } catch (eF) {}
      try { _sxFnAllOff(null, 'the tour'); } catch (eA) {}
      _tourPaint();
    }
    /* the padlock opened as its tap opens it, without the build */
    function _tourUnlock() {
      if (!_SX.lock) return;
      if (!_planWanted()) { var lk = _tourStripIcon('lock'); if (lk) lk.click(); return; }
      _SX.lock = false;
      _tourPaint();
      try { _sxAfter('the plan UNLOCKED by the tour -- nothing built (core-v648)'); } catch (eS) {}
      try { _cmdSync(); } catch (eC) {}
    }
    /* THE PADLOCK CLOSED FOR THE BOTTOM BAR (core-v653, Marek: behem
       voiceoveru, kdyz zminoval ze je mozne psat i text, nebyl zobrazeny
       spodni floorplan banner -- fp banner napravo byl odemceny). Unlocked,
       the plan's own bar owns the bottom (core-v628) and the command bar
       steps aside, so the steps that talk about that bar (the library's
       upload, the command) close the padlock first -- as a visitor sees the
       bar -- without the build; the next plan step opens it again */
    function _tourLock() {
      if (!_SX.lock) {
        _SX.lock = true;
        _tourRelock();
        try { _sxAfter('the plan LOCKED by the tour -- the bottom bar shown, nothing built (core-v653)'); } catch (eS) {}
      }
      try { _cmdSync(); } catch (eC) {}
    }
    /* ON THE PLAN NOTHING IS BUILT WHILE THE TOUR MOVES THINGS (core-v648):
       the drag path (core-v617) -- the plan repainted from the state, the
       floorplan asset's own rebuild calls only repaint it too -- and the
       house built once, when 3D shows again (_tourFresh). In 3D a frame
       builds as before. */
    function _tourLightOn() {
      if (!_planWanted()) return;
      try { _dragBegin('the tour'); } catch (e) {}
    }
    function _tourLightOff() {
      try { if (_drag.on && _drag.kind === 'the tour') _dragStop('the tour moved on'); } catch (e) {}
    }
    function _tourPaint() {
      if (_planWanted()) {
        _T.stale3d = true;
        try { drawPlan(); if (typeof _hRefresh === 'function') _hRefresh(); } catch (eP) {}
        return;
      }
      try { rebuild(); drawPlan(); } catch (eR) { _log('tour     build THREW ' + ((eR && eR.message) || eR)); }
    }
    function _tourFresh() {
      if (!_T.stale3d || _planWanted()) return;
      _T.stale3d = false;
      _tourLightOff();
      var t0 = _perfNow();
      try { rebuild(); } catch (eR) {}
      _log('tour     the house built once, back from the plan (' + Math.round(_perfNow() - t0) + ' ms)');
    }
    /* a partition moved one way and the other, as a finger would */
    function _tourPartFrames(id, dd) {
      var P0 = _tourPartById(id), out = [];
      if (!P0) return out;
      var c0 = P0.c;
      [0.5, 1, 0, -1, -0.5, 0].forEach(function (f) {
        out.push(function () { _tourPartTo(id, Math.round((c0 + dd * f) * 10000) / 10000); });
      });
      return out;
    }
    /* a partition moved the way a finger moves it: through the asset's
       movePartTo, which pushes the doors it meets ahead of it and stops at
       one that cannot go further (floorplan-lib v209); the direct write only
       where the asset has none */
    function _tourPartTo(id, c) {
      var PL = _P(), ps = [], i;
      try { ps = _normParts(); } catch (eN) { ps = []; }
      for (i = 0; i < ps.length; i++) {
        if (ps[i] && ps[i].id === id) {
          if (PL && typeof PL.movePartTo === 'function') { try { PL.movePartTo(i, c); return; } catch (eM) {} }
          ps[i].c = ps[i].at = c;
          try { _normParts(); } catch (eN2) {}
          return;
        }
      }
    }
    /* AN INTERIOR DOOR OUT, IN AGAIN, SLID (core-v648): the first door of a
       partition taken out, a beat with the wall closed, a door put in on the
       same wall in the longer stretch beside where it was (the door tool's
       addDoorAt), then slid along it -- frames for _tourFrames */
    function _tourDoorSwap() {
      var out = [], S = { pid: null, u: null, d: null, sl: null };
      out.push(function () {
        var D = STATE.doors || [], i, d = null;
        for (i = 0; i < D.length && !d; i++) if (D[i] && D[i].pid != null && !D[i].pass && !D[i].kind) d = D[i];
        if (!d) return;
        S.pid = d.pid; S.u = d.u;
        D.splice(D.indexOf(d), 1);
        _T.dirty = true;
        _log('tour     plan: the door on wall ' + d.pid + ' taken out');
      });
      out.push(function () {});
      out.push(function () {
        var PL = _P(), ps = [], q = null, i;
        if (!PL || !PL.addDoorAt) return;
        try { ps = _normParts(); } catch (eN) { ps = []; }
        for (i = 0; i < ps.length && !q; i++) if (ps[i] && S.pid != null && ps[i].id === S.pid) q = ps[i];
        if (!q) for (i = 0; i < ps.length && !q; i++) if (ps[i] && !ps[i].sep && !ps[i].bear && (ps[i].st | 0) === (STATE.planStorey | 0)) q = ps[i];
        if (!q) { _log('tour     plan: no partition to put a door in'); return; }
        var sp = null;
        try { sp = _partSpanH(q); } catch (eS) { sp = null; }
        if (!sp) return;
        var u = (S.u == null) ? (sp[0] + sp[1]) / 2 : ((S.u - sp[0] > sp[1] - S.u) ? (sp[0] + S.u) / 2 : (S.u + sp[1]) / 2);
        var v = ((q.dir || q.o) === 'v'), got = null;
        try { got = PL.addDoorAt(v ? q.at : u, v ? u : q.at); } catch (eD) { got = null; }
        if (!got && S.u != null) { try { got = PL.addDoorAt(v ? q.at : S.u, v ? S.u : q.at); } catch (eD2) { got = null; } }
        if (got) { S.d = got; _T.dirty = true; try { _normDoors(); } catch (eND) {} }
        _log('tour     plan: ' + (got ? ('a door put in on wall ' + q.id) : 'NO door went back in'));
      });
      [0, 1, 2, 3, 4, 5].forEach(function (k) {
        out.push(function () {
          if (!S.d) return;
          if (!S.sl) S.sl = _tourDoorFrames(S.d);
          if (S.sl[k]) S.sl[k]();
        });
      });
      return out;
    }
    function _tourDoorFrames(dr) {
      var out = [];
      if (dr) {
        var pw = null, ps = [], sp = null, k;
        try { ps = _normParts(); } catch (eP) { ps = []; }
        for (k = 0; k < ps.length; k++) if (ps[k] && ps[k].id === dr.pid) pw = ps[k];
        try { sp = pw ? _partSpanH(pw) : null; } catch (eSp) { sp = null; }
        if (sp) {
          /* where the door is when its turn comes -- a partition dragged
             before it may have pushed it along */
          var w = dr.w || 0.8, lo = sp[0] + w / 2, hi = sp[1] - w / 2, g = null;
          [0.33, 0.66, 1, 0.66, 0.33, 0].forEach(function (f) {
            out.push(function () {
              if (!g) {
                /* inside its own stretch: never through a wall that crosses it */
                var u0 = dr.u, half = w / 2 + 0.16, j;
                for (j = 0; j < ps.length; j++) {
                  var x = ps[j];
                  if (!x || x === pw || x.sep || (x.o || x.dir) === (pw.o || pw.dir)) continue;
                  var xs = null;
                  try { xs = _partSpanH(x); } catch (eX) { xs = null; }
                  if (!xs || pw.c < xs[0] - 0.05 || pw.c > xs[1] + 0.05) continue;
                  if (x.c >= u0) hi = Math.min(hi, x.c - half); else lo = Math.max(lo, x.c + half);
                }
                var du = Math.min(0.6, Math.max(0, Math.max(hi - u0, u0 - lo)));
                g = { u0: u0, du: du, s: (hi - u0 >= u0 - lo) ? 1 : -1 };
              }
              if (g.du > 0.1) { dr.u = clamp(g.u0 + g.s * g.du * f, lo, hi); _T.dirty = true; }
            });
          });
        }
      }
      return out;
    }
    /* done (the step's: the voice has ended) cuts a pass short -- the
       step's end puts the project back anyway (core-v648) */
    function _tourFrames(frames, ms, cb, run, done) {
      var k = 0;
      var next = function () {
        var over = false;
        try { over = !!(done && k > 0 && done()); } catch (eD) { over = false; }
        if (k >= frames.length || over) { _tourLightOff(); cb(); return; }
        _tourLightOn();
        try { frames[k++](); } catch (eR) { _log('tour     frame THREW ' + ((eR && eR.message) || eR)); }
        _tourPaint();
        _tourLater(next, ms, run);
      };
      next();
    }
    /* a partition to show a tool on: the visitor's first, or one drawn for it */
    function _tourHasPart() {
      var sv = STATE.planStorey | 0, ps = [];
      try { ps = _normParts(); } catch (e) { ps = []; }
      if (ps.some(function (q) { return q && !q.sep && !q.bear && (q.st | 0) === sv; })) return true;
      try { addPartition('v'); _T.dirty = true; } catch (eA) { return false; }
      return true;
    }
    /* one interior door, put in the way the door tool puts it */
    function _tourDoorIn() {
      var PL = _P();
      if (!PL || !PL.addDoorAt) { _log('tour     doors: the plan asset has no addDoorAt'); return false; }
      _tourHasPart();
      var ps = [], sv = STATE.planStorey | 0, FR = [0.5, 0.3, 0.7], i, f;
      try { ps = _normParts(); } catch (e) { ps = []; }
      for (i = 0; i < ps.length; i++) {
        var q = ps[i], sp = null;
        if (!q || q.sep || q.bear || (q.st | 0) !== sv) continue;
        try { sp = _partSpanH(q); } catch (eS) { sp = null; }
        if (!sp || sp[1] - sp[0] < 1.0) continue;
        var v = ((q.dir || q.o) === 'v');
        for (f = 0; f < FR.length; f++) {
          var u = sp[0] + (sp[1] - sp[0]) * FR[f], got = null;
          try { got = PL.addDoorAt(v ? q.at : u, v ? u : q.at); } catch (eD) { got = null; }
          if (got) {
            _T.dirty = true;
            _T.doorNew = got;
            try { _normDoors(); } catch (eR) {}
            _tourPaint();
            _log('tour     doors: a door in on the wall ' + (q.id || i));
            return true;
          }
        }
      }
      _log('tour     doors: no partition took a door');
      return false;
    }
    /* the piece the fitout step puts in: a bed or a sofa reads at a glance
       (the tour speaks English and Spanish, so their names -- core-v668) */
    function _tourFitTile() {
      var pk = global.document.getElementById('cfg-ui-picker');
      if (!pk) return null;
      var L = [].slice.call(pk.querySelectorAll('.cfg-pk-tile')), i, j,
        R = [/^\s*(double bed|cama de matrimonio|cama doble|doppelbett|lit double|man\u017eelsk\u00e1 postel|dvoul\u016f\u017eko|cama de casal|\u53cc\u4eba\u5e8a|\u0633\u0631\u064a\u0631 \u0645\u0632\u062f\u0648\u062c|\u30c0\u30d6\u30eb\u30d9\u30c3\u30c9|\u00e7ift ki\u015filik yatak|\ub354\ube14 \uce68\ub300|letto matrimoniale|\u0142\u00f3\u017cko podw\u00f3jne|pat dublu|tweepersoonsbed|\u03b4\u03b9\u03c0\u03bb\u03cc \u03ba\u03c1\u03b5\u03b2\u03ac\u03c4\u03b9|francia\u00e1gy|dubbels\u00e4ng|\u05de\u05d9\u05d8\u05d4 \u05d6\u05d5\u05d2\u05d9\u05ea|paris\u00e4nky|dobbeltseng)/i, /^\s*(sof(a|\u00e1)|canap(e|\u00e9)|pohovka|sed\u00e1\u010dka|\u6c99\u53d1|\u0643\u0646\u0628\u0629|\u30bd\u30d5\u30a1|kanepe|\uc18c\ud30c|divano|canapea) 3/i, /^\s*(sof(a|\u00e1)|canap(e|\u00e9)|pohovka|sed\u00e1\u010dka|\u6c99\u53d1|\u0643\u0646\u0628\u0629|\u30bd\u30d5\u30a1|kanepe|\uc18c\ud30c|divano|canapea|bank|\u03ba\u03b1\u03bd\u03b1\u03c0|kanap\u00e9|soffa|\u05e1\u05e4\u05d4|sohva)/i, /^\s*(armchair|sill\u00f3n|sillon|sessel|fauteuil|k\u0159eslo|poltrona|fotel|fotoliu|\u6276\u624b\u6905|\u0643\u0631\u0633\u064a \u0628\u0630\u0631\u0627\u0639\u064a\u0646|\u30a2\u30fc\u30e0\u30c1\u30a7\u30a2|koltuk|\uc548\ub77d\uc758\uc790|\u03c0\u03bf\u03bb\u03c5\u03b8\u03c1\u03cc\u03bd\u03b1|fotel|f\u00e5t\u00f6lj|\u05db\u05d5\u05e8\u05e1\u05d4|nojatuoli|lenestol)/i];
      for (j = 0; j < R.length; j++) for (i = 0; i < L.length; i++) if (R[j].test(L[i].textContent || '')) return L[i];
      return L[0] || null;
    }
    /* something mirror visibly turns over: on a plan with no inner wall, one
       drawn off the middle with a door in it (gone again after the step) */
    function _tourAsym() {
      var sv = STATE.planStorey | 0, ps = [], F = STATE.footprint;
      try { ps = _normParts(); } catch (e) { ps = []; }
      if (ps.some(function (q) { return q && !q.sep && (q.st | 0) === sv; })) return;
      try { addPartition('v'); } catch (eA) { return; }
      _T.dirty = true;
      var L = STATE.parts || [], P = L[L.length - 1];
      if (P && (P.o || P.dir) === 'v' && F) {
        P.c = P.at = Math.round((P.c - (F.w || 6) * 0.2) * 1000) / 1000;
        try { _normParts(); } catch (eN) {}
      }
      try { rebuild(); drawPlan(); } catch (eR) {}
      _tourDoorIn();
      _log('tour     mirror: a wall off the middle with a door, to be turned over');
    }

    /* ---- core-v637: the edit strip, the rail and the close ------------------
       The edit strip's icons are tapped as a finger taps them (the tooltip
       names each), their bar's arrows walk the choices, a slider sweeps; the
       rail's buttons that change only the view are pressed and pressed back;
       AR, Undo, Share, PDF and + PDF only pulse -- the tour never opens a
       sheet, downloads, uploads or takes back a change. */
    function _tourEditOpen() {
      try { if (!(_wiz && _wiz.isOpen && _wiz.isOpen() && _SX.mode === 'edit')) _sxEditOpen(); } catch (e) {}
      _tourFresh();
    }
    function _tourStripShut() {
      try { if (_wiz && _wiz.isOpen && _wiz.isOpen()) _tourStripClose(); } catch (e) {}
      _tourFresh();
    }
    function _tourUnpick() {
      if (!_SX.sel) return;
      _SX.sel = null;
      try { _sxRefresh(); } catch (e) {}
    }
    /* the next (or previous) choice on the bar's arrows */
    function _tourArrow(dir) {
      var a = _SX.bar ? _SX.bar.querySelectorAll('.cf-sx-arw') : [], el = a.length ? a[dir > 0 ? a.length - 1 : 0] : null;
      if (!el) return false;
      _tourPulse([el]);
      el.click();
      _T.dirty = true;
      return true;
    }
    /* core-v708 (Marek: strechu i zaklady vzdy ukazat vsechny varianty):
       every choice of a row on the arrows, one after another from the one on
       screen -- n-1 taps, the visitor's own comes back at the step's end.
       mid(go) runs on each choice before the next tap (the roof's pitch);
       the tour waits for the walk, so a short voice cannot cut it */
    function _tourWalkAll(key, ms, run, cb, mid) {
      var I = _sxIconBy(key), n = I ? _sxCycle(I).length : 0, k = 0;
      /* the reel: two taps at most (three choices), 0.7 s each (core-v720) */
      var taps = _tourIg() ? Math.min(n - 1, 2) : n - 1;
      if (_tourIg()) ms = Math.min(ms, 700);
      /* core-v743: the hero walks every choice and on round to the house's
         own, so the step ends where it began -- nothing snaps back */
      if (_tourAuto()) { taps = n; ms = Math.max(900, Math.min(ms, 1200)); }
      _log('tour     ' + key + ': ' + Math.min(taps + 1, n) + ' of ' + n + ' choices on the arrows' + (taps >= n ? ', and round to the first' : ''));
      if (n < 2) { _tourLater(cb, 800, run); return; }
      var next = function () {
        if (k >= taps) { cb(); return; }
        k++;
        _tourArrow(1);
        _tourLater(function () { if (mid) mid(next); else next(); }, ms, run);
      };
      next();
    }
    /* the roof choice on screen ({ t } from _sxRoofList), null when none */
    function _tourRoofNow() {
      var L = _sxCycle(_sxIconBy('roof')), i;
      for (i = 0; i < L.length; i++) { try { if (L[i].on && L[i].on()) return L[i]; } catch (eO) {} }
      return null;
    }
    /* an edit-strip icon pulsed, then tapped */
    function _tourIconStart(key, run, ms) {
      _tourEditOpen();
      var b = _tourStripIcon(key);
      if (!b) { _T.skip = 'no ' + key + ' icon on this product'; return false; }
      _tourPulse([b]);
      _tourLater(function () {
        _tourPulse([]);
        var b2 = _tourStripIcon(key);
        if (b2 && _SX.sel !== key) b2.click();
      }, ms || (_tourIg() ? 700 : 1100), run);
      return true;
    }
    /* a step that walks a row's choices on the arrows */
    function _tourWalkStep(key, mp3, sec) {
      return { key: key, mp3: mp3, sec: sec, lead: 2100,
        start: function (run) { _T.wkN = 0; _tourIconStart(key, run); },
        cycle: function (cb, run) {
          /* core-v744: the hero walks every choice once and round to the
             house's own (the facade: boards, the other boards, render) */
          if (_tourAuto()) {
            if (_T.wkN) { _tourLater(cb, 250, run); return; }
            _T.wkN = 1; _tourWalkAll(key, 1100, run, cb); return;
          }
          _tourArrow(1); _tourLater(cb, 1500, run);
        },
        end: function () { _tourUnpick(); _tourBack(); } };
    }
    /* an on / off of the edit strip switched on, shown, and back */
    function _tourSwitchStep(key, mp3, sec, isOn, fit) {
      return { key: key, mp3: mp3, sec: sec, lead: 2400,
        start: function (run) {
          _tourEditOpen();
          var b = _tourStripIcon(key);
          if (!b) { _T.skip = 'no ' + key + ' switch on this product'; return; }
          _T.sw0 = false;
          try { _T.sw0 = !!isOn(); } catch (eO) {}
          if (fit) _tourCamKeep();
          _tourPulse([b]);
          _tourLater(function () {
            _tourPulse([]);
            var b2 = _tourStripIcon(key);
            if (!_T.sw0 && b2) { b2.click(); _T.dirty = true; }
            if (fit) _tourFit({ out: true });
          }, 1200, run);
        },
        cycle: function (cb, run) { _tourLater(cb, 800, run); },
        end: function () { _tourUnpick(); _tourBack(); if (fit) _tourCamBack(); } };
    }
    /* ---- the pergola and the pool in the yard (core-v642, Marek: pergolu a
       pool umistit v nejake minimalni vzdalenosti od steny / hranice domu,
       behem voiceoveru s nimi pohybovat, menit rozmery) -------------------
       Switched on by their strip icons, then put close by the wall that faces
       the camera (the pool 1.0 m off it, the pergola 0.6 m -- since core-v709
       off the roof's edge, not the wall's), long side along the wall; while
       the voice runs they slide once along the wall, then grow once (v709;
       they slid there and back and grew and shrank). The camera steps back once, before the first
       move, with room for all of it, and comes back at the end. A pergola
       that only stands over the terrace (a product without _pergolaFree) is
       switched on and shown, not moved. */
    function _tourCamSide() {
      var B = _buildRect(), cx = (B.x0 + B.x1) / 2, cz = (B.z0 + B.z1) / 2, side = 'x1', T = _terrace();
      try {
        var st = _stage(), cam = st && st.camera, p = cam.position.clone();
        try { if (st.group && st.group.worldToLocal) p = st.group.worldToLocal(p); } catch (eW) {}
        var vx = p.x - cx, vz = p.z - cz;
        var S = [['x0', -vx], ['x1', vx], ['z0', -vz], ['z1', vz]].sort(function (a, b) { return b[1] - a[1]; });
        side = (T && T.on && T.side === S[0][0]) ? S[1][0] : S[0][0];
      } catch (e) {}
      return { B: B, cx: cx, cz: cz, side: side };
    }
    /* core-v709 (Marek: pergola a pool musi byt mimo roof footprint -- ted
       koliduje se slats u previsu strechy): what the yard keeps out of is the
       roof as built -- the box of the roof's layer (eaves, fascia, gutters, a
       wing's or a cross gable's roof) in the plan's coordinates -- with the
       walls as the floor of it; the walls alone when there is no roof layer */
    function _tourKeepOut() {
      var B = _buildRect(), K = { x0: B.x0, x1: B.x1, z0: B.z0, z1: B.z1 }, R = null;
      try {
        var st = _stage(), g = st && st.group, T3 = global.THREE;
        if (g && T3) {
          g.children.forEach(function (c) { if (/^layer:.*-roof$/.test(String(c.name || ''))) R = c; });
          if (R) {
            g.updateMatrixWorld(true);
            var b = new T3.Box3().setFromObject(R);
            if (!b.isEmpty()) {
              b.applyMatrix4(new T3.Matrix4().copy(g.matrixWorld).invert());
              K.x0 = Math.min(K.x0, b.min.x); K.x1 = Math.max(K.x1, b.max.x);
              K.z0 = Math.min(K.z0, b.min.z); K.z1 = Math.max(K.z1, b.max.z);
            }
          }
        }
      } catch (e) {}
      K.roof = !!R;
      return K;
    }
    /* a rectangle by a wall: along it, out from it, a gap, slid by off --
       the gap from the roof's edge when W.K is there (core-v709) */
    function _tourByWall(W, along, depth, gap, off) {
      var B = W.K || W.B, s2 = W.side;
      if (s2 === 'x0') return { x0: B.x0 - gap - depth, z0: W.cz - along / 2 + off, sx: depth, sz: along };
      if (s2 === 'x1') return { x0: B.x1 + gap, z0: W.cz - along / 2 + off, sx: depth, sz: along };
      if (s2 === 'z0') return { x0: W.cx - along / 2 + off, z0: B.z0 - gap - depth, sx: along, sz: depth };
      return { x0: W.cx - along / 2 + off, z0: B.z1 + gap, sx: along, sz: depth };
    }
    function _tourYardSet(key, R) {
      if (key === 'pool') {
        var Q = _pool();
        Q.x0 = Math.round(R.x0 * 100) / 100; Q.z0 = Math.round(R.z0 * 100) / 100;
        Q.w = Math.round(R.sx * 100) / 100; Q.l = Math.round(R.sz * 100) / 100;
      } else {
        var T = _terrace();
        T.pg = { x0: Math.round(R.x0 * 100) / 100, z0: Math.round(R.z0 * 100) / 100, sx: Math.round(R.sx * 100) / 100, sz: Math.round(R.sz * 100) / 100 };
      }
      _T.dirty = true;
    }
    function _tourYardStep(key, mp3, sec) {
      var isOn = (key === 'pool') ? function () { return poolOn(); } : function () { return pergolaOn(); };
      var A = (key === 'pool') ? { along: 6.0, depth: 3.0, gap: 1.0, slide: 1.5, grow: 2.0, deep: 1.0 }
                               : { along: 3.0, depth: 3.0, gap: 0.6, slide: 1.2, grow: 1.5, deep: 0.6 };
      return { key: key, mp3: mp3, sec: sec, lead: 2300,
        start: function (run) {
          _tourEditOpen();
          var b = _tourStripIcon(key);
          if (!b) { _T.skip = 'no ' + key + ' switch on this product'; return; }
          _T.yardMoved = false; _T.yardWalk = false;
          _T.yardFree = (key === 'pool') || _pgFree();
          if (!_T.yardFree && !(_terrace() && _terrace().on)) { _T.skip = 'the pergola stands over the terrace and there is none'; return; }
          _tourCamKeep();
          _tourPulse([b]);
          _tourLater(function () {
            _tourPulse([]);
            var b2 = _tourStripIcon(key), on0 = false;
            try { on0 = !!isOn(); } catch (eO) {}
            /* core-v643: a pergola that stands only over the terrace (no
               _pergolaFree -- the T house) is a choice on the arrows, not a
               switch: picked here, walked in the cycle */
            if (!_T.yardFree) {
              if (!(_terrace() && _terrace().on)) { _log('tour     ' + key + ': no terrace to stand over -- shown only'); return; }
              if (b2 && _SX.sel !== key) b2.click();
              _T.yardWalk = true;
              _log('tour     ' + key + ': over the terrace, its kinds walked on the arrows');
              return;
            }
            if (!on0 && b2) { b2.click(); _T.dirty = true; }
            if (!isOn()) { _log('tour     ' + key + ': did not come on'); return; }
            if (_T.yardFree) {
              _T.yardW = _tourCamSide();
              _T.yardW.K = _tourKeepOut();
              var KO = _T.yardW.K, BW = _T.yardW.B, sdK = _T.yardW.side;
              var ovK = Math.abs(KO[sdK] - BW[sdK]);
              _tourYardSet(key, _tourByWall(_T.yardW, A.along, A.depth, A.gap, 0));
              try { rebuild(); } catch (eR) {}
              _log('tour     ' + key + ': by wall ' + sdK + ', ' + A.gap + ' m off the ' + (KO.roof ? ('roof\'s edge (' + Math.round(ovK * 1000) + ' mm past the wall)') : 'wall (no roof layer)'));
            }
            /* framed once with room for the slide and the growth */
            _tourFit({ out: true, k: 1.35 });
          }, 1200, run);
        },
        cycle: function (cb, run) {
          if (_T.yardWalk) { _tourArrow(1); _tourLater(cb, 1500, run); return; }
          if (_T.yardMoved || !_T.yardFree || !isOn() || !_T.yardW) { _tourLater(cb, 800, run); return; }
          _T.yardMoved = true;
          /* core-v709 (Marek: pergola se ma ukazat kratsi dobu -- staci
             umistit, jednou premistit, zvetsit rozmer. Stejne pool): placed
             (start), slid once along the wall, a pause, then bigger once --
             three frames each, six builds where there were eighteen; it stays
             so until the voice ends and _tourBack puts the project back */
          var F = [], f;
          [0.33, 0.66, 1].forEach(function (g) { F.push({ a: 0, d: 0, o: g * A.slide }); });
          F.push(null);
          [0.33, 0.66, 1].forEach(function (g) { F.push({ a: g * A.grow, d: g * A.deep, o: A.slide }); });
          var k = 0;
          var next = function () {
            if (k >= F.length) { _log('tour     ' + key + ': placed, moved once, made bigger'); _tourLater(cb, 300, run); return; }
            f = F[k++];
            if (!f) { _tourLater(next, 700, run); return; }
            _tourYardSet(key, _tourByWall(_T.yardW, A.along + f.a, A.depth + f.d, A.gap, f.o));
            try { rebuild(); } catch (eR) {}
            _tourLater(next, 170, run);
          };
          next();
        },
        end: function () { _tourUnpick(); _tourBack(); _tourCamBack(); _T.yardW = null; _T.yardWalk = false; } };
    }
    /* a rail button found and, while on screen, pulsed */
    function _tourRail(id) {
      var el = $(id);
      return (el && el.offsetParent !== null) ? el : null;
    }
    function _tourRailStep(key, mp3, sec, id) {
      return { key: key, mp3: mp3, sec: sec, lead: 600,
        start: function () {
          _tourStripShut();
          var el = _tourRail(id);
          if (!el) { _T.skip = 'no ' + id + ' on this page'; return; }
          _tourPulse([el]);
        },
        cycle: function (cb, run) { _tourLater(cb, 800, run); },
        end: function () {} };
    }
    function _tourCamKeep() {
      try {
        var st = _stage(), c = st && st.persCam, ctl = _tourControls();
        _T.camS = (c && ctl) ? { p: c.position.clone(), t: ctl.target.clone() } : null;
      } catch (e) { _T.camS = null; }
    }
    function _tourCamBack() {
      try {
        var st = _stage(), c = st && st.persCam, ctl = _tourControls();
        if (_T.camS && c && ctl) { c.position.copy(_T.camS.p); ctl.target.copy(_T.camS.t); ctl.update(); if (st.wake) st.wake(800); }
      } catch (e) {}
      _T.camS = null;
    }
    /* the assembly walk ended where it began */
    function _tourAsmOff() {
      try {
        if (!_asm2 || !_asm2.k) return;
        try { _asmLand(); } catch (eL) {}
        _asm2.k = 0;
        try { _asmApply(false); } catch (eA) {}
        try { rebuild(); } catch (eR) {}
        try { if (typeof _pickEnable === 'function') _pickEnable(false); } catch (eP) {}
        try { if (typeof _cyanOff === 'function') _cyanOff(); } catch (eC) {}
        try { _asmCounter(); _asmAnnounce(); } catch (eN) {}
        _log('tour     the assembly walk put back to 0');
      } catch (e) {}
    }
    function _tourCutOff() {
      try {
        if (_cutTool && _cutTool.state && _cutTool.state().on) _cutTool.set({ axis: 'x', t: 0.5, flip: false, plane: true, handle: true, on: false });
      } catch (e) {}
    }
    function _tourVizBack() {
      if (_T.viz0 == null || (STATE.vizStage | 0) === _T.viz0) return;
      try {
        var v = setViz(_T.viz0);
        railCounters();
        try { _asmCounter(); } catch (eC) {}
        var el = $('cf-btn-viz');
        if (el) { el.setAttribute('aria-pressed', STATE.vizStage ? 'true' : 'false'); if (v && v.label) el.title = _t('viz_prefix', 'Visualisation:') + ' ' + v.label; }
      } catch (e) {}
    }
    /* ---- the AR window, the budget drawer, the assistant (core-v639) ---- */
    function _tourArShown() { var m = $('cf-ar-modal'); return !!(m && !m.hidden); }
    function _tourArReady() { var q = $('cf-ar-modal-qr'); return !!(q && q.querySelector('img')); }
    function _tourArOpen(el) {
      var sig = '';
      try { sig = JSON.stringify(_slim(payload())); } catch (e) { sig = ''; }
      var m = $('cf-ar-modal');
      if (m && sig && _T.arSig === sig && _tourArReady()) {
        m.hidden = false;
        _log('tour     ar: the window with the QR this project got before -- no new upload');
      } else {
        _T.arPend = sig;
        el.click();
        _log('tour     ar: the window opened, the model on its way to the AR viewer');
      }
      _T.arOpened = true;
    }
    function _tourArClose() {
      if (!_T.arOpened) return;
      _T.arOpened = false;
      var x = global.document.querySelector('#cf-ar-modal [data-cf-ar-close]');
      if (x && _tourArShown()) { try { x.click(); } catch (e) {} }
      var m = $('cf-ar-modal');
      if (m && !m.hidden) m.hidden = true;
    }
    function _tourBudScroller() {
      var el = global.document.querySelector('#Budget-Drawer [data-budget-sections]') || global.document.querySelector('#Budget-Drawer .budget-drawer__body');
      while (el && el !== global.document.body) {
        var cs = global.getComputedStyle(el);
        if (/(auto|scroll)/.test(cs.overflowY) && el.scrollHeight > el.clientHeight + 2) return el;
        el = el.parentElement;
      }
      return null;
    }
    function _tourBudOpen() {
      try {
        var d = global.document.getElementById('Budget-Drawer');
        if (d && d.classList.contains('active')) return;
        global.openBudgetMainDrawer();
        _T.budOpen = true;
        var sc = _tourBudScroller();
        _T.budY0 = sc ? sc.scrollTop : 0;
        _log('tour     budget: the drawer opened');
      } catch (e) { _log('tour     budget: the drawer did not open (' + ((e && e.message) || e) + ')'); }
    }
    function _tourBudClose() {
      if (!_T.budOpen) return;
      _T.budOpen = false;
      try { var sc = _tourBudScroller(); if (sc) sc.scrollTop = _T.budY0 || 0; } catch (eS) {}
      try { if (typeof global.closeBudgetMainDrawer === 'function') global.closeBudgetMainDrawer(); } catch (e) {}
    }
    /* =====================================================================
       THE BUDGET SHOWN AS THE CONTAINER SHOWS IT (core-v748, opt-in
       __PROD._tourBudget2; ported from configurator-container-lib.js
       container-v559..v567 as it stands -- Marek 2026-10-20 / 21: no rings
       in the budget, the price bar's three stores + cheapest instead; the
       item card held until it has loaded and run down to its offers; always
       the budget's SECOND item). Copied: the row pick, the offers asked
       ahead, the card's state, its offers' scroll, the price bar's plan /
       taps / undo, the card's close. Adapted: _perfNow for the clock.
       ===================================================================== */
    function _tourBud2() { try { return !!(__PROD && __PROD._tourBudget2); } catch (e) { return false; } }
    function _tourBudRow() {
      var L = [].slice.call(global.document.querySelectorAll('#Budget-Drawer .budget-item[data-item-id]')).filter(function (el) {
        return el.offsetParent !== null && String(el.getAttribute('data-mat-status') || '') !== 'empty';
      });
      return L[1] || L[0] || null;
    }
    function _tourBudWarm(id) {
      if (!id || _T.budWarm === id) return;
      _T.budWarm = id;
      try {
        if (typeof global.fetchVendorsCsv === 'function' && !(Array.isArray(global.vendorList) && global.vendorList.length)) {
          var tV = _perfNow();
          global.fetchVendorsCsv().then(function (L) { _log('tour     budget: the stores table asked ahead -- ' + ((L && L.length) || 0) + ' store(s) in ' + ((_perfNow() - tV) / 1000).toFixed(1) + ' s'); })['catch'](function () {});
        }
      } catch (eV) {}
      var cty = 'US';
      try { if (global.Shopify && global.Shopify.country && /^[A-Z]{2}$/.test(global.Shopify.country)) cty = global.Shopify.country; } catch (eC) {}
      try {
        global.fetch('https://leva-scrape.long-cloud-d006.workers.dev/price/offers?item=' + encodeURIComponent(id) + '&country=' + cty)
          .then(function (r) { return r.json(); })
          .then(function (d) {
            var L = (d && d.offers) || [], n = 0, i;
            for (i = 0; i < L.length && n < 12; i++) if (L[i] && L[i].image) { var im = new global.Image(); im.src = L[i].image; n++; }
            _log('tour     budget: ' + id + ' -- ' + L.length + ' offer(s) asked ahead, ' + n + ' picture(s) loading');
          })['catch'](function () {});
      } catch (eW) {}
    }
    function _tourBudCardState() {
      var dr = global.document.getElementById('Custom-Drawer');
      if (!dr || !dr.classList.contains('active')) return { open: false, ready: false, say: 'card not open' };
      var vis = function (el) { return !!(el && el.offsetParent !== null); };
      var loader = dr.querySelector('.affiliate-immersive-loader');
      var detail = dr.querySelector('.leva-detail, .affiliate-product-card, .affiliate-empty-card');
      var panel = dr.querySelector('[data-search-panel][data-input-id]:not([data-input-id=""])');
      var res = panel ? panel.querySelector('[data-search-results]') : null, hint = res ? res.querySelector('.leva-search-hint') : null;
      var tiles = res ? res.children.length - (hint ? 1 : 0) : 0;
      var searching = !!(hint && /\u2026|\.\.\./.test(hint.textContent || ''));
      var imgs = [].slice.call(dr.querySelectorAll('img')), pend = imgs.filter(function (im) { return vis(im) && !im.complete; }).length;
      var ready = !vis(loader) && !!detail && !searching && pend === 0;
      return { open: true, ready: ready, tiles: tiles, panel: panel,
               say: (vis(loader) ? 'loader up, ' : '') + (detail ? '' : 'no detail yet, ') + tiles + ' offer tile(s), ' + pend + ' picture(s) pending' +
                    (hint ? (', hint "' + String(hint.textContent || '').slice(0, 24) + '"') : '') + (panel ? (panel.hidden ? ', offers panel shut' : ', offers panel open') : ', no offers panel') };
    }
    function _tourBudCardOffers() {
      var dr = global.document.getElementById('Custom-Drawer');
      if (!dr || !dr.classList.contains('active')) return false;
      var vis = function (el) { return !!(el && el.offsetParent !== null); };
      var panel = dr.querySelector('[data-search-panel][data-input-id]:not([data-input-id=""])');
      if (!vis(panel)) return false;
      var vend = panel.querySelector('[data-search-vendors]');
      var tgt = vis(vend) && vend.children.length ? vend : panel;
      try { tgt.scrollIntoView({ block: 'start', behavior: 'smooth' }); } catch (e) { try { tgt.scrollIntoView(); } catch (e2) {} }
      return true;
    }
    function _tourBudBar() {
      var b = global.document.querySelector('#Budget-Drawer [data-leva-pricebar]') || global.document.querySelector('[data-leva-pricebar]');
      return (b && !b.hidden && b.offsetParent !== null) ? b : null;
    }
    function _tourBudFilterPlan() {
      _T.budFilt = null;
      var b = _tourBudBar();
      if (!b) { _log('tour     budget: no price bar on screen -- the filter is not shown'); return null; }
      if (b.querySelector('.leva-pricebar__vend.is-on, [data-leva-key].is-on, [data-leva-src].is-on')) {
        _log('tour     budget: the price bar is already set by the visitor -- left alone');
        return null;
      }
      var tiles = [].slice.call(b.querySelectorAll('[data-leva-vend]')).filter(function (el) {
        var k = String(el.getAttribute('data-leva-vend') || '');
        return el.offsetParent !== null && !el.querySelector('.leva-pricebar__tag') && !/^\d+$/.test(k);
      }).map(function (el) { return String(el.getAttribute('data-leva-vend') || ''); });
      var want = [/homedepot|home ?depot/i, /lowes/i, /walmart/i], out = [], i, k;
      for (i = 0; i < want.length; i++) for (k = 0; k < tiles.length; k++) if (want[i].test(tiles[k]) && out.indexOf(tiles[k]) < 0) { out.push(tiles[k]); break; }
      for (k = 0; k < tiles.length && out.length < 3; k++) if (out.indexOf(tiles[k]) < 0) out.push(tiles[k]);
      if (!out.length || !b.querySelector('[data-leva-key="cheap"]')) { _log('tour     budget: the price bar has no stores / no cheapest -- the filter is not shown'); return null; }
      _T.budFilt = { vend: out, on: [], key: false };
      _log('tour     budget: the price bar -- stores ' + out.join(', ') + ' then cheapest (a preview; APPLY not pressed)');
      return out;
    }
    function _tourBudBarShow(sc) {
      var b = _tourBudBar();
      if (!b || !sc) return;
      try {
        var y = sc.scrollTop + (b.getBoundingClientRect().top - sc.getBoundingClientRect().top) - 16;
        sc.scrollTo({ top: Math.max(0, y), behavior: 'smooth' });
      } catch (e) {}
    }
    function _tourBudTap(sel, val) {
      var F = _T.budFilt, b = _tourBudBar();
      if (!F || !b) return false;
      var attr = (sel === '[data-leva-vend]') ? 'data-leva-vend' : 'data-leva-key';
      var el = [].slice.call(b.querySelectorAll(sel)).filter(function (x) { return x.getAttribute(attr) === val; })[0];
      if (!el) { _log('tour     budget: ' + val + ' not on the price bar any more'); return false; }
      try { el.click(); } catch (e) { return false; }
      if (attr === 'data-leva-vend') { if (F.on.indexOf(val) < 0) F.on.push(val); else F.on.splice(F.on.indexOf(val), 1); }
      else F.key = !F.key;
      return true;
    }
    function _tourBudFilterOff(why) {
      var F = _T.budFilt;
      if (!F) return;
      var vend = F.on.slice().reverse(), i;
      for (i = 0; i < vend.length; i++) _tourBudTap('[data-leva-vend]', vend[i]);
      var b = _tourBudBar(), chip = b ? b.querySelector('[data-leva-key="cheap"]') : null;
      if (chip && chip.classList.contains('is-on')) { try { chip.click(); } catch (e) {} }
      _T.budFilt = null;
      _log('tour     budget: the price bar put back (' + why + ')' + ((b && b.querySelector('.leva-pricebar__vend.is-on, [data-leva-key].is-on')) ? ' -- SOMETHING IS STILL ON' : ''));
    }
    function _tourBudItemClose() {
      if (!_T.budItem) return;
      _T.budItem = false;
      try {
        var dr = global.document.getElementById('Custom-Drawer');
        if (!dr || !dr.classList.contains('active')) return;
        var x = dr.querySelector('side-panel-close') || dr.querySelector('.side-panel-close');
        if (x) x.click();
      } catch (e) {}
    }
    /* the container's budget step's show (container-v563..v567), one pass:
       1 the dashboard held, the item's offers asked ahead; 1b the price bar
       brought up, three stores on, then cheapest; 2 down the items to the
       SECOND one; 3 its card opened, held until loaded (10 s at most, asked
       again after 1.5 s), run down to its offers, held, shut; the bar put
       back. The step's voice may end first -- the core's loop waits for
       this pass (cb) before it moves on, so nothing is cut short. */
    function _tourBudShow(cb, run) {
      var sc = _tourBudScroller();
      if (!sc) { _tourLater(cb, 600, run); return; }
      var ig = _tourIg();
      sc.scrollTop = 0;
      _log('tour     budget: the dashboard held (core-v748, the container\'s show)');
      var rowW = _tourBudRow();
      if (rowW) _tourBudWarm(rowW.getAttribute('data-item-id'));
      /* adapted: the reel's rhythm tighter than the container's (the house
         line's reel holds the hook, the designs, the infill and the build
         before it, and stays under 60 s) */
      var tF = ig ? 1600 : 3400, STEP = ig ? 500 : 900, IN = ig ? 500 : 700, plan = _tourBudFilterPlan();
      if (plan) {
        _tourLater(function () { _tourBudBarShow(sc); }, tF, run);
        plan.forEach(function (k, ix) {
          _tourLater(function () { _tourBudTap('[data-leva-vend]', k); }, tF + IN + ix * STEP, run);
        });
        _tourLater(function () { _tourBudTap('[data-leva-key]', 'cheap'); }, tF + IN + plan.length * STEP, run);
        tF += IN + (plan.length + 1) * STEP + (ig ? 1000 : 2400);
      }
      _tourLater(function () {
        var row = _tourBudRow(), sr = sc.getBoundingClientRect(), y0 = sc.scrollTop, y1;
        if (row) y1 = Math.max(0, Math.min(sc.scrollHeight - sc.clientHeight, y0 + (row.getBoundingClientRect().top - sr.top) - sc.clientHeight * 0.4));
        else y1 = Math.max(0, Math.min(sc.scrollHeight - sc.clientHeight, Math.round((sc.scrollHeight - sc.clientHeight) * 0.6)));
        var MS = ig ? 1500 : 3200, t0 = _perfNow();
        var run2 = function () {
          if (run !== _T.run) return;
          var f = Math.min(1, (_perfNow() - t0) / MS), e = f < 0.5 ? 2 * f * f : 1 - Math.pow(-2 * f + 2, 2) / 2;
          sc.scrollTop = Math.round(y0 + (y1 - y0) * e);
          if (f < 1) { global.requestAnimationFrame(run2); return; }
          if (!row) { _tourBudFilterOff('no item to open'); _tourLater(cb, 400, run); return; }
          var rid = row.getAttribute('data-item-id');
          _log('tour     budget: item ' + rid + ' (' + (row.getAttribute('data-mat-status') || '?') + ') opened -- the budget\'s second');
          _tourLater(function () {
            try { row.click(); _T.budItem = true; } catch (eC) {}
            var tOpen = _perfNow(), retried = false;
            var wait = function () {
              if (run !== _T.run) return;
              var st = _tourBudCardState(), waited = _perfNow() - tOpen;
              if (!st.open && !retried && waited > 1500) {
                retried = true;
                var okF = false;
                try { okF = (typeof global.openItemDrawerFast === 'function') && !!global.openItemDrawerFast(rid); } catch (eF) { okF = false; }
                _log('tour     budget: the card had not opened after ' + (waited / 1000).toFixed(1) + ' s -- asked again ' + (okF ? '(openItemDrawerFast)' : '(nothing to ask: no opener)'));
              }
              if (!st.ready && waited < 10000) { _tourLater(wait, 200, run); return; }
              _log('tour     budget: the item card ' + (st.ready ? 'loaded' : 'still loading') + ' after ' + (waited / 1000).toFixed(1) + ' s (' + st.say + ')');
              var tShown = _perfNow(), lost = false;
              var guard = function () {
                if (run !== _T.run || !_T.budItem || lost) return;
                var dr2 = global.document.getElementById('Custom-Drawer');
                if (!dr2 || !dr2.classList.contains('active')) { lost = true; _log('tour     budget: the item card was shut by something else ' + ((_perfNow() - tShown) / 1000).toFixed(1) + ' s after it loaded'); return; }
                _tourLater(guard, 250, run);
              };
              guard();
              _tourLater(function () {
                var offs = _tourBudCardOffers();
                _log(offs ? 'tour     budget: the card run down to its offers (the stores and their products)' : 'tour     budget: no offers on the card to show');
                _tourLater(function () {
                  _log('tour     budget: the item card shown ' + ((_perfNow() - tShown) / 1000).toFixed(1) + ' s in all, shut now');
                  _tourBudItemClose();
                  _tourLater(function () {
                    _tourBudFilterOff('the step is over');
                    _tourLater(cb, 600, run);
                  }, 500, run);
                }, offs ? (ig ? 2500 : 4500) : (ig ? 1200 : 2000), run);
              }, ig ? 1500 : 2600, run);
            };
            _tourLater(wait, 500, run);
          }, ig ? 400 : 900, run);
        };
        global.requestAnimationFrame(run2);
      }, tF, run);
    }
    function _tourAiClose() {
      if (!_T.aiOpen) return;
      _T.aiOpen = false;
      try { if (global.LevaAI && typeof global.LevaAI.close === 'function') global.LevaAI.close(); } catch (e) {}
    }
    /* the place panel's first trees tile ('Scenery 1'), found by its word */
    function _tourTreeTile() {
      var w = String(_uiStr('scenery', 'Scenery') || 'Scenery').toLowerCase(), L, i;
      L = [].slice.call(global.document.querySelectorAll('#cf-sx-modal .cfg-ui-wiz-ico, #cf-sx-modal button'));
      for (i = 0; i < L.length; i++) {
        var t = String(L[i].getAttribute('aria-label') || L[i].title || L[i].textContent || '').toLowerCase().replace(/\s+/g, ' ').trim();
        if (t.indexOf(w) >= 0 && /1\b/.test(t)) return L[i];
      }
      return null;
    }
    function _tourTreesA() { try { var K = _trees(); return !!(K && K.isOn('a')); } catch (e) { return false; } }
    /* a window put in on the longest wall facing the camera, as the picker's
       pick and a tap on the wall would put it */
    function _tourWindowIn() {
      var G = null, it = null, U = _UI(), j;
      try { G = (U && U.openingGroup) ? U.openingGroup('window') : null; } catch (eG) { G = null; }
      /* an ordinary window: one the catalogue gives a size, not a full wall */
      for (j = 0; G && G.items && j < G.items.length && !it; j++) {
        var c = G.items[j];
        if (c && !c.full && !c.fixed && c.w >= 0.8 && c.h >= 1.0) it = c;
      }
      if (!it) { _log('tour     openings: no window in the catalogue'); return null; }
      var st = _stage(), cam = st && st.persCam, L = [], best = null, i;
      try { L = segments().filter(function (q) { return (q.storey | 0) === 0 && q.len > (it.w || 1) + 1.2; }); } catch (eS) { L = []; }
      for (i = 0; i < L.length; i++) {
        var q = L[i], mx = (q.x0 + q.x1) / 2, mz = (q.z0 + q.z1) / 2;
        var face = cam ? ((cam.position.x - mx) * q.nx + (cam.position.z - mz) * q.nz) : 1;
        if (face <= 0) continue;
        if (!best || q.len > best.len) best = q;
      }
      if (!best && L.length) best = L[0];
      if (!best) { _log('tour     openings: no wall long enough'); return null; }
      var rec = null;
      try {
        rec = addOpening(_sizeToWall(best, { seg: best.id, at: best.len * 0.4, w: it.w, h: it.h, sill: it.sill, kind: 'window',
          code: it.code, hand: it.hand, full: it.full, leaves: it.leaves }));
      } catch (eA) { rec = null; _log('tour     openings: adding THREW ' + ((eA && eA.message) || eA)); }
      if (rec) { _T.dirty = true; _T.winSeg = best; }
      _log('tour     openings: ' + (rec ? ('a ' + (rec.code || 'window') + ' in on ' + best.id) : 'NO window went in'));
      return rec;
    }
    /* THE PLAN FROM THE LIBRARY STAYS (core-v640, Marek: kdyz vyberu fp z
       databaze, tak ten fp uz nech nasazeny a zobraz cely 3d model, nevracet
       zpet). Once it has landed, the library closes, the plan strip closes,
       the whole house is shown in 3D, and the plan becomes the tour's base:
       every later step shows its tool on it and puts IT back. The stop still
       gives the visitor's own project back. */
    /* A PLAN THAT IS NOT THE ONE ON SCREEN (core-v675, Marek: kdyz se nahrava /
       ukazuje ulozene floorplans, mel by se nahrat vzdy jiny nez je aktualne
       na webu a s nim pokracovat): the first tile took the first plan, often
       the very house on screen, so nothing changed and the step waited 9 s
       for a plan that never came. Now the tiles are walked in their order,
       past a plan of the house's own size (within 25 cm both ways -- the one
       on screen, as near as the library can tell) and past the plans this
       page's tour already took; the first of the rest is taken. When every
       plan has been taken once, the round starts again; when none differs,
       the first tile as before. */
    function _tourLibTile(el) {
      var T = [].slice.call(el.querySelectorAll('.cf-plib-t')), items = {}, F = STATE.footprint || {};
      var a0 = Math.min(+F.w || 0, +F.l || 0), b0 = Math.max(+F.w || 0, +F.l || 0);
      (_plib.items || []).forEach(function (it) { if (it && it.id != null) items[String(it.id)] = it; });
      _T.libUsed = _T.libUsed || [];
      var pick = function () {
        for (var i = 0; i < T.length; i++) {
          var id = T[i].getAttribute('data-plib') || '', it = items[id];
          if (!it || (_plib.gone && _plib.gone[id]) || _T.libUsed.indexOf(id) >= 0) continue;
          var a = Math.min(it.w || 0, it.d || 0), b = Math.max(it.w || 0, it.d || 0);
          if (Math.abs(a - a0) < 0.25 && Math.abs(b - b0) < 0.25) continue;
          return T[i];
        }
        return null;
      };
      var t = pick();
      if (!t && _T.libUsed.length) { _T.libUsed = []; t = pick(); }
      if (!t) { _log('tour     library: no plan of another size than the house on screen -- the first tile'); return T[0] || null; }
      return t;
    }
    function _tourPlanSig() {
      try { return JSON.stringify([STATE.footprint, STATE.polygon, STATE.parts, STATE.doors, STATE.openings]); } catch (e) { return ''; }
    }
    function _tourLibWait(cb, run, n) {
      var landed = _tourPlanSig() !== _T.libSig && !_cmd.applyT && !_cmd.busy;
      if (!landed && n < 30) { _tourLater(function () { _tourLibWait(cb, run, n + 1); }, 300, run); return; }
      if (!landed) { _log('tour     library: the plan did not land in 9 s -- called off'); _tourLater(cb, 300, run); return; }
      _T.libPicked = false;
      try { _plibClose('the plan landed'); } catch (eC) {}
      _tourStripShut();
      try { if (STATE.view === '2d') setView('3d'); } catch (eV) {}
      /* the plan that landed was built whole by its own apply */
      _T.stale3d = false;
      _tourFit();
      _T.base = JSON.stringify(payload());
      _T.dirty = false;
      _log('tour     library: the plan landed and stays -- shown in 3D, the base of the steps that follow');
      _tourLater(cb, 2200, run);
    }
    /* a plan on its way (the library, mirror's rebuilt plan) called off, so
       it cannot land on the project once it has been put back */
    function _tourCancelPlan() {
      if (!_T.libPicked && !_T.planAsked) return;
      _T.libPicked = false; _T.planAsked = false;
      try { var IUc = _cmdUser(); if (IUc && typeof IUc.cancel === 'function') IUc.cancel(); } catch (eCa) {}
      try { if (_cmd.applyT) { global.clearTimeout(_cmd.applyT); _cmd.applyT = 0; } } catch (eAt) {}
      _cmd.asked = false; _cmd.busy = false;
      try { _cmdLoading(false, 'the tour'); } catch (eLd) {}
      try { _cmdBubble(null); } catch (eBb) {}
      try { _cmdSendState(); } catch (eSs) {}
    }
    /* ---- the intro (core-v637, Marek: zacatek nejdriv prepinanim 5
       referencnich projektu a pak jen rotace posledniho domu o 360 stupnu;
       rotovat a zaroven menit je spis prusvih) ------------------------------
       Five of the product's reference designs from the table at the bottom
       of the page (rows sorted by floor area, so taken evenly from small to
       large), fetched at play from leva-share and shown one after another,
       the canvas framing each; then the last one turns once, a full 360,
       and nothing changes while it turns. Opened the tour's own way: no
       Undo entry, no ?p= in the address, no copy in localStorage, and the
       visitor's units, place and figure kept -- the project goes back at
       the end of the step. */
    var _TOUR_REFS = 5;
    function _tourRefIds() {
      var seen = {}, L = [], out = [], i;
      try {
        [].slice.call(global.document.querySelectorAll('[data-lrd] a[data-lrd-p]')).forEach(function (a) {
          var id = String(a.getAttribute('data-lrd-p') || '');
          if (/^[a-z0-9]{6,12}$/.test(id) && !seen[id]) { seen[id] = 1; L.push(id); }
        });
      } catch (e) { L = []; }
      /* core-v714: a ?ig page shows three -- the smallest, one in the middle
         and the biggest, so the price row moves the most */
      var N = _tourIg() ? 4 : _TOUR_REFS;   /* core-v720: four on the reel */
      if (L.length <= N) return L;
      for (i = 0; i < N; i++) out.push(L[Math.round(i * (L.length - 1) / (N - 1))]);
      return out;
    }
    function _tourRefLoad() {
      var ids = _tourRefIds(), run = _T.run;
      _T.refs = ids.map(function () { return null; });
      _T.refN = ids.length;
      _T.refAt = 0;
      _T.refWait = 0;
      ids.forEach(function (id, k) {
        try {
          global.fetch(_shareWorker() + '/s/' + id)
            .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
            .then(function (p) { if (run === _T.run && p && p.footprint) _T.refs[k] = p; else if (run === _T.run) _T.refs[k] = false; })
            ['catch'](function (err) { if (run === _T.run) { _T.refs[k] = false; _log('tour     intro: reference ' + id + ' not fetched (' + ((err && err.message) || err) + ')'); } });
        } catch (eF) { _T.refs[k] = false; }
      });
      _log('tour     intro: ' + ids.length + ' reference design(s) asked for' + (ids.length ? (' (' + ids.join(', ') + ')') : ' -- no reference table on this page, the turn only'));
    }
    function _tourRefShow(p) {
      var q = JSON.parse(JSON.stringify(p)), k;
      /* the visitor's own: units, place, map, the figure */
      ['isMetric', 'origin', 'climateZone', 'mapLocked', 'bodyVisible', 'bodyX', 'bodyZ'].concat(_tourAuto() ? ['scenery', 'trees'] : []).forEach(function (k2) {   /* core-v735: the hero keeps its trees */
        if (STATE.hasOwnProperty(k2)) q[k2] = JSON.parse(JSON.stringify(STATE[k2] === undefined ? null : STATE[k2]));
      });
      for (k in q) { if (q[k] === null && (k === 'origin' || k === 'bodyX' || k === 'bodyZ')) delete q[k]; }
      _applyPayload(q, 'the tour (a reference design)');
      _T.dirty = true;
      rebuild();
      /* core-v741 (Marek 2026-10-21 18:08: "kazdy dum prizpusobit podle jeho
         velikosti"): the hero frames every design to its own size -- in on a
         small one, out on a big one; elsewhere the camera only ever backs off */
      _tourFit(_tourAuto() ? {} : { out: true });
    }
    /* one full turn round the house, eased in and out, the camera's height
       and distance kept */
    function _tourTurn(ms, run, cb) {
      var st = _stage(), cam = st && st.persCam, ctl = _tourControls();
      if (!cam || !ctl || STATE.view === '2d') { cb(); return; }
      var tg = ctl.target.clone(), off = cam.position.clone().sub(tg);
      var r = Math.sqrt(off.x * off.x + off.z * off.z), a0 = Math.atan2(off.x, off.z), y = off.y, t0 = _perfNow();
      var step = function () {
        if (run !== _T.run) return;
        var f = Math.min(1, (_perfNow() - t0) / ms), e = f < 0.5 ? 2 * f * f : 1 - Math.pow(-2 * f + 2, 2) / 2;
        var a = a0 + 2 * Math.PI * e;
        cam.position.set(tg.x + r * Math.sin(a), tg.y + y, tg.z + r * Math.cos(a));
        cam.lookAt(tg);
        try { ctl.update(); } catch (eU) {}
        if (st.wake) st.wake(400);
        if (f < 1) global.requestAnimationFrame(step); else cb();
      };
      global.requestAnimationFrame(step);
    }

    /* ---- THE PRODUCT'S OWN ICONS (core-v643, Marek: posli to na vsechny
       projekty) ------------------------------------------------------------
       The tour runs on every product of the line now, and the steps below
       play only where their icon is: the storey cards (BARNDO's loft, GARAGE
       APT, A-FRAME), RANCH's footprint shape and cross gables, the T house's
       load-bearing walls, wall openings, ridge and roof insulation, A-FRAME's
       stairs, the THOW's trailer and the sauna's hot tub. Each is found by
       what it is -- its row on the strip, its glyph, its tap -- and a product
       without it skips the step, its voice unplayed. */
    /* the plan strip's tiles of the Floorplan row with this label, in order:
       { b: the button, it: the tile } (the keys are t<row>.<tile>:...) */
    function _tourPlanRowI(label) {
      var rows = [], out = [];
      try { rows = _sxRows('layout') || []; } catch (e) { rows = []; }
      (_SX.icons || []).forEach(function (I) {
        var m = /^t(\d+)\.(\d+):/.exec(String((I && I.key) || ''));
        if (!m) return;
        var r = rows[+m[1]];
        if (!r || String(r.label || '') !== String(label || '')) return;
        var b = _tourStripIcon(I.key);
        if (b) out.push({ b: b, it: (r.items || [])[+m[2]] || I.item || null });
      });
      return out;
    }
    function _tourPlanRow(label) { return _tourPlanRowI(label).map(function (x) { return x.b; }); }
    /* the plan strip's icon that walks choices on the bar (RANCH's footprint) */
    function _tourPlanCycleKey() {
      var k = '';
      /* core-v647: a product's cycle that is not a footprint shape says so
         (item.noTour -- THOW's Loft tile) and is passed over */
      try { (_SX.icons || []).forEach(function (I) { if (!k && I && I.kind === 'cycle' && /^cyc:/.test(String(I.key || '')) && !(I.item && I.item.noTour)) k = I.key; }); } catch (e) { k = ''; }
      return k;
    }
    /* an edit-strip icon the strip made from a product's own row (auto:...) */
    function _tourAutoKey(step, test) {
      var k = '';
      try {
        (_SX.icons || []).forEach(function (I) {
          if (k || !I || !I.auto || I.step !== step) return;
          var r = _sxAutoRow(I);
          if (r && r.items && r.items.length && test(r)) k = I.key;
        });
      } catch (e) { k = ''; }
      return k;
    }
    /* a choice of the product's own walked on the arrows */
    function _tourAutoWalk(key, mp3, sec, step, test, o) {
      o = o || {};
      return { key: key, mp3: mp3, sec: sec, lead: 2100,
        start: function (run) {
          _tourEditOpen();
          var k = _tourAutoKey(step, test);
          if (!k) { _T.skip = 'no ' + key + ' icon on this product'; return; }
          _T.autoK = k;
          if (o.start) o.start();
          _tourIconStart(k, run);
        },
        cycle: function (cb, run) { _tourArrow(1); _tourLater(cb, 1500, run); },
        end: function () { _tourUnpick(); _tourBack(); if (o.end) o.end(); } };
    }
    /* an on / off of the product's own: on, and back and forth while the
       voice runs -- or, with o.cycle, on once and shown its own way */
    function _tourAutoFlip(key, mp3, sec, step, test, o) {
      o = o || {};
      return { key: key, mp3: mp3, sec: sec, lead: 2400,
        start: function (run) {
          _tourEditOpen();
          var k = _tourAutoKey(step, test);
          if (!k) { _T.skip = 'no ' + key + ' switch on this product'; return; }
          _T.autoK = k;
          _T.flipMoved = false;
          if (o.fit) _tourCamKeep();
          _tourPulse([_tourStripIcon(k)]);
          _tourLater(function () {
            _tourPulse([]);
            var I = _sxIconBy(_T.autoK), on0 = false;
            try { on0 = !!(I && I.on && I.on()); } catch (eO) { on0 = false; }
            if (!(o.cycle && on0)) _tourFlip();
            if (o.fit) _tourFit({ out: true, k: 1.3 });
          }, 1200, run);
        },
        cycle: function (cb, run) {
          if (o.cycle) { o.cycle(cb, run); return; }
          _tourLater(function () { _tourFlip(); _tourLater(cb, 200, run); }, 2000, run);
        },
        end: function () { _tourUnpick(); _tourBack(); if (o.fit) _tourCamBack(); } };
    }
    function _tourFlip() {
      var b = _tourStripIcon(_T.autoK);
      if (!b) return;
      b.click();
      _T.dirty = true;
    }
    /* the next storey card, pulsed and then tapped */
    function _tourStoreyNext(run, cb) {
      var lab = _t('storeys', 'Storeys'), C = _tourPlanRow(lab);
      if (C.length < 2) { if (cb) _tourLater(cb, 600, run); return; }
      var k = ((STATE.planStorey | 0) + 1) % C.length;
      _tourPulse([C[k]]);
      _tourLater(function () {
        var C2 = _tourPlanRow(lab);
        _tourPulse([]);
        if (C2[k]) { C2[k].click(); _T.dirty = true; _log('tour     storeys: the plan of storey ' + (k + 1)); }
        if (cb) _tourLater(cb, 1300, run);
      }, 600, run);
    }
    /* A-FRAME's stairs: the other kind, then Rotate, Rotate -- and round */
    function _tourStairTap(run) {
      var R = _tourPlanRowI(_t('stairs', 'Stairs')), n = (_T.stK++) % 3, pick = null, i;
      if (R.length < 2) return;
      if (n === 0) {
        for (i = 0; i < Math.min(2, R.length) && !pick; i++) { var lit = false; try { lit = !!(R[i].it && R[i].it.on && R[i].it.on()); } catch (eL) {} if (!lit) pick = i; }
        if (pick == null) pick = 1;
      } else pick = Math.min(2, R.length - 1);
      _tourPulse([R[pick].b]);
      _tourLater(function () {
        var R2 = _tourPlanRowI(_t('stairs', 'Stairs'));
        _tourPulse([]);
        if (R2[pick]) { R2[pick].b.click(); _T.dirty = true; _log('tour     stairs: ' + _tourLabel(R2[pick].b)); }
      }, 500, run);
    }
    /* a wall added by its tile and dragged one way and the other: the
       partitions, and a masonry house's load-bearing walls */
    function _tourWallStep(key, mp3, sec, icoV, icoH, what) {
      return { key: key, mp3: mp3, sec: sec, lead: 2700,
        start: function (run) {
          /* the glyphs by name: ICO is not there yet when the list is made */
          var svgV = ICO[icoV], svgH = ICO[icoH];
          _tourPlanOpen();
          var bv = svgV ? _tourPlanTool(function (it) { return it.svg === svgV; }) : null;
          var bh = svgH ? _tourPlanTool(function (it) { return it.svg === svgH; }) : null;
          if (!bv) { _T.skip = 'no ' + what + ' tiles on the plan strip'; return; }
          _T.wallId = null;
          _tourPulse([bv, bh]);
          _tourLater(function () {
            var ids = (STATE.parts || []).map(function (q) { return q && q.id; });
            var b = _tourPlanTool(function (it) { return it.svg === svgV; });
            _tourPulse([]);
            if (b) b.click();
            var L = STATE.parts || [], i;
            for (i = L.length - 1; i >= 0; i--) if (L[i] && ids.indexOf(L[i].id) < 0) { _T.wallId = L[i].id; break; }
            if (_T.wallId) _T.dirty = true;
            _log('tour     ' + key + ': ' + (_T.wallId ? ('a vertical ' + what + ' in (' + _T.wallId + ')') : ('NO ' + what + ' went in (no room wide enough)')));
          }, 1500, run);
        },
        cycle: function (cb, run, done) {
          if (!_T.wallId) { _tourLater(cb, 700, run); return; }
          _tourFrames(_tourPartFrames(_T.wallId, 0.45), 150, cb, run, done);
        },
        end: function () { _tourBack(); _tourRelock(); } };
    }
    /* the T house's opening in a wall: the tile, an opening dropped into the
       longest wall, made wider and back */
    function _tourHoleTile(it) { return !!(it && it.svg === ICO.door && it.tap !== planDoorTap && String(it.label || '') === String(_uiStr('op_opening', 'Opening'))); }
    function _tourHoleOff() {
      try { if (_holeMode) holeMode(false); } catch (eM) {}
      try { if (_hh.on) { editHole(null); _opBinLinger(); } } catch (eH) {}
    }
    /* the sauna's hot tub moved along the wall that faces the camera */
    function _tourTubCycle(cb, run) {
      var on = false;
      try { on = !!propOn('tub'); } catch (eO) { on = false; }
      if (_T.flipMoved || !on) { _tourLater(cb, 800, run); return; }
      _T.flipMoved = true;
      var W = _tourCamSide(), p = _prop('tub'), F = [0, 0.4, 0.8, 1.2, 0.8, 0.4, 0, -0.4, -0.8, -1.2, -0.8, -0.4, 0], k = 0;
      _log('tour     hot tub: by wall ' + W.side + ', moved along it');
      var next = function () {
        if (k >= F.length) { _tourLater(cb, 300, run); return; }
        var R = _tourByWall(W, 2.2, 2.2, 1.0, F[k++]);
        p.x = Math.round((R.x0 + R.sx / 2) * 100) / 100;
        p.z = Math.round((R.z0 + R.sz / 2) * 100) / 100;
        _T.dirty = true;
        try { _propXZ('tub'); rebuild(); _propHandles(); } catch (eR) {}
        _tourLater(next, 170, run);
      };
      next();
    }

    /* ---- the steps, in the order of the voice-tour sheet --------------------
       start(run) opens the function, lead is how long that takes, pulse()
       names the icons to pulse, cycle(cb, run, done) shows the function once
       and calls cb, end(stopped) puts it away. sec is the step's own time
       when there is no voice (about the length of its MP3). */
    var _TOUR_STEPS = [
      { key: 'intro', mp3: 'tour-intro', sec: 13, lead: 500,
        start: function () {
          if (STATE.view === '2d') setView('3d');
          var c = _tourControls();
          if (c && !_tourAuto()) { _T.rot = { on: c.autoRotate, sp: c.autoRotateSpeed }; c.autoRotate = false; }
          _T.turned = false;
          _T.xpDone = false;
          _T.introShown = false;
          /* core-v743: the hero keeps the page's own house -- no designs */
          if (_tourAuto()) { _T.refs = []; _T.refN = 0; _T.refAt = 0; _T.refWait = 0; _T.introShown = true; _T.eO0 = null; _T.eF = 0; _T.pwOn = []; _T.colBack = []; }
          else _tourRefLoad();
          if (!_T.pitch0) { try { showPitch(false); } catch (eSp) {} }
        },
        /* the next reference design every 1.4 s; when the five are shown (or
           the ones that came), one turn of the last; then it only waits */
        cycle: function (cb, run) {
          if (!_T.pitch0) { try { showPitch(false); } catch (eSp) {} }
          if (_T.refAt < _T.refN) {
            var p = _T.refs[_T.refAt];
            if (p === null && _T.refWait < 12) { _T.refWait++; _tourLater(cb, 250, run); return; }
            _T.refWait = 0;
            var k = _T.refAt++;
            if (p) {
              try { _tourRefShow(p); _log('tour     intro: reference ' + (k + 1) + '/' + _T.refN + ' on screen'); }
              catch (eR) { _log('tour     intro: reference ' + (k + 1) + ' THREW ' + ((eR && eR.message) || eR)); }
              /* core-v714: on ?ig 2 s a design, time to read the price row;
                 core-v720: 1.5 s, four of them */
              _tourLater(cb, _tourQuiet() ? 1100 : (_tourIg() ? 1500 : 1400), run);   /* core-v734: the hero 1.1 s */
            } else _tourLater(cb, 50, run);
            return;
          }
          /* core-v737 (Marek 2026-10-21 15:12: "v animaci by bylo dobre take
             udelat exploded view - roztahnout a stahnout behem dvou vterin ...
             zaradit do prvni casti prezentace projektu"): the hero only, once
             the designs have been shown, the house comes apart and back */
          /* the reel: no turn, the next highlight comes once the hook ends (core-v720);
             core-v723: a page without reference designs turns the house once
             under the hook instead of standing still (TAURUS) */
          if (!_T.turned && (!_tourIg() || !_T.refN) && !_tourQuiet() && !_tourAuto()) {   /* core-v743: the hero turns on its own, slowly */   /* core-v734: never the slow turn in the hero */
            _T.turned = true;
            _log('tour     intro: one full turn of the last house');
            _tourTurn(5600, run, function () { _tourLater(cb, 200, run); });
            return;
          }
          _tourLater(cb, _tourIg() ? 250 : 600, run);
        },
        end: function () {
          var c = _tourControls();
          if (c && _T.rot) { c.autoRotate = !!_T.rot.on; if (_T.rot.sp != null) c.autoRotateSpeed = _T.rot.sp; }
          _T.rot = null;
          if (_T.dirty) { _tourBack(); _tourCamPlay(); }
          /* core-v742 (live runner on core-v741: after the intro the house of
             the page came back with the camera still at the last, biggest
             design's distance -- 53 % of the frame instead of 72 %): the hero
             frames the house it goes on with */
        } },
      { key: 'edit', mp3: 'tour-edit', sec: 6, lead: 1600, tip: ['edit', 'Edit'],
        target: function () { return _cmd.edit; },
        start: function (run) {
          _tourPulse([_cmd.edit]);
          _tourLater(function () { _cmd.edit.click(); }, 1100, run);
        },
        /* the pencil's function is the strip: every icon in it lit in turn.
           After one whole pass a voice that has ended cuts the next pass
           short, so the wait for the next step stays short */
        cycle: function (cb, run, done) {
          var s = _tourStrip(), L = s ? [].slice.call(s.querySelectorAll('.cf-sx-b')) : [], k = 0;
          if (!L.length) { _tourLater(cb, 600, run); return; }
          var next = function () {
            if (k >= L.length || (_T.pass > 0 && done())) { _T.pass++; _tourHl(null); _tourBub(null); cb(); return; }
            var el = L[k++];
            _tourHl(el);
            _tourBub(el, _tourLabel(el));
            _tourLater(next, 480, run);
          };
          next();
        },
        end: function () { _tourStripClose(); } },
      { key: 'plan', mp3: 'tour-plan', sec: 7, lead: 3800, tip: ['view_plan', 'Floor plan'],
        target: function () { return _cmd.plan; },
        start: function (run) {
          _T.w0 = STATE.footprint ? STATE.footprint.w : null;
          _T.parts0 = JSON.stringify(STATE.parts || []);
          _T.partTried = false;
          _T.planDoor = false;
          _T.planPass = 0;
          _tourPulse([_cmd.plan]);
          _tourLater(function () { _tourPulse([]); _tourPlanOpen(true); }, 800, run);
          /* the padlock first in the plan strip: pulsing, then opened */
          _tourLater(function () { _tourPulse([_tourStripIcon('lock')]); }, 1400, run);
          _tourLater(function () {
            _tourUnlock();
            _tourLater(function () { _tourPulse([_tourStripIcon('lock')]); }, 150, run);
          }, 2200, run);
        },
        /* an outer wall dragged out and back, a partition moved one way and
           the other (core-v633), then an interior door taken out, put in
           again elsewhere on its wall and slid along it (core-v648, Marek:
           rychle zmenit vnejsi rozmer, posunout pricky, smazat a vlozit
           interierove dvere, posunout s nimi) -- the windows no longer: on
           the plan their slide could not be seen. Nothing is built while it
           runs (_tourFrames on the plan), 140 ms a frame; the door once a
           step, the walls again while the voice still runs */
        cycle: function (cb, run, done) {
          var F = STATE.footprint, frames = [];
          if (F && _T.w0 != null) {
            var room = (LIMITS.maxW || (_T.w0 + 1.2)) - _T.w0 - 0.05, d = Math.min(1.2, room);
            if (d < 0.3) d = -Math.min(1.2, _T.w0 - (LIMITS.minW || 2) - 0.05);
            [0.5, 1, 0.5, 0].forEach(function (f) { frames.push(function () { F.w = _T.w0 + d * f; _T.dirty = true; }); });
          }
          var P = null;
          try { P = _tourPart(); } catch (eP) { P = null; }
          if (P) frames = frames.concat(_tourPartFrames(P.id, 0.45));
          if (!_T.planDoor) { _T.planDoor = true; frames = frames.concat(_tourDoorSwap()); }
          if (!frames.length) { _tourLater(cb, 600, run); return; }
          /* the first pass whole, a later one cut short when the voice ends */
          _tourFrames(frames, 140, cb, run, (_T.planPass++ > 0) ? done : null);
        },
        /* the plan stays open and unlocked for the steps that follow
           (core-v648: the padlock's tap built the whole house) */
        end: function () {
          _T.dirty = true;
          _tourBack();
          _tourRelock();
        } },
      /* core-v643: the storey cards walked -- BARNDO switches its loft on
         first, the loft being its storey 2 */
      { key: 'storeys', mp3: 'tour-storeys', sec: 6, lead: 2600,
        start: function (run) {
          _tourPlanOpen();
          var loftL = _t('rooms.loft', 'Loft'), stL = _t('storeys', 'Storeys');
          var loft = _tourPlanRow(loftL), cards = _tourPlanRow(stL);
          if (!loft.length && cards.length < 2) { _T.skip = 'one storey and no loft on this product'; return; }
          _tourPulse(loft.length ? loft : cards);
          _tourLater(function () {
            var L2 = _tourPlanRow(loftL), C2 = _tourPlanRow(stL);
            _tourPulse([]);
            if (L2.length && C2.length < 2) { L2[0].click(); _T.dirty = true; _log('tour     storeys: the loft on -- its plan is storey 2'); return; }
            _tourStoreyNext(run);
          }, 1200, run);
        },
        cycle: function (cb, run) { _tourLater(function () { _tourStoreyNext(run, cb); }, 700, run); },
        end: function () { _tourBack(); _tourRelock(); } },
      { key: 'library', mp3: 'tour-library', sec: 9, lead: 1900,
        start: function (run) {
          _T.libDone = false;
          try { if (_T.mir0 == null) _T.mir0 = JSON.stringify((_plib && _plib.mir) || {}); } catch (eM) {}
          _tourPlanOpen(true);
          _tourLock();
          _tourLater(function () { _tourPulse([_cmd.el && _cmd.el.querySelector('.cf-cmd-up')]); }, 300, run);
          _tourLater(function () { _tourPulse([]); try { _plibOpen(); } catch (eO) { _log('tour     library THREW ' + ((eO && eO.message) || eO)); } }, 1400, run);
        },
        /* our plans scrolled through, then the first one turned over by its
           mirror button and taken into the model (core-v635, Marek: ukazka
           mirroring a jeho aplikace primo do modelu; pri zastaveni zpet k
           ulozenemu modelu) -- once; a voice still going waits after it */
        cycle: function (cb, run) {
          if (_T.libDone) { _tourLater(cb, 600, run); return; }
          var el = _plib && _plib.el, tries = 0;
          /* core-v726: the drawer opens 1.4 s into the step and the reel's
             lead is shorter -- wait for it rather than give the plan up */
          if (!el || !_plib.open) { _tourLater(cb, 300, run); return; }
          _T.libDone = true;
          var max = Math.max(0, el.scrollHeight - el.clientHeight), to = Math.min(max, Math.round(el.clientHeight * 0.5)), k = 0, N = 8;
          var take = function () {
            var t = el.querySelector('.cf-plib-t') ? _tourLibTile(el) : null;
            if (!t && tries++ < 15) { _tourLater(take, 200, run); return; }
            if (!t || !_plib.open) { _log('tour     library: no plan to take -- the list did not come'); cb(); return; }
            /* the reel takes the plan as it is, quicker (core-v720) */
            var reel = _tourIg(), mb = reel ? null : t.querySelector('.cf-plib-mi');
            _tourPulse([mb || t]);
            _tourLater(function () {
              if (mb) mb.click();
              _tourLater(function () {
                _tourPulse([t]);
                _tourLater(function () {
                  _tourPulse([]);
                  _T.libPicked = true;
                  _T.libSig = _tourPlanSig();
                  var pid = t.getAttribute('data-plib') || '';
                  _T.libUsed = _T.libUsed || [];
                  if (pid && _T.libUsed.indexOf(pid) < 0) _T.libUsed.push(pid);
                  t.click();
                  _log('tour     library: plan ' + pid + ' taken into the model' + (mb ? ', mirrored' : '') + ' -- not the one on screen (core-v675)');
                  _tourLibWait(cb, run, 0);
                }, reel ? 700 : 1100, run);
              }, reel ? 0 : 1100, run);
            }, reel ? 600 : 1200, run);
          };
          var scroll = function () {
            if (k > 2 * N) { el.scrollTop = 0; take(); return; }
            var f = (k <= N) ? (k / N) : ((2 * N - k) / N);
            el.scrollTop = Math.round(to * (0.5 - 0.5 * Math.cos(Math.PI * f)));
            k++;
            _tourLater(scroll, 90, run);
          };
          _tourLater(_tourIg() ? take : scroll, 300, run);
        },
        end: function () {
          try { if (_plib && _plib.el) _plib.el.scrollTop = 0; _plibClose('the tour moved on'); } catch (e) {}
          /* a plan still on its way is called off; one that has come stays
             (core-v640) */
          if (_T.libPicked) { _tourCancelPlan(); _T.dirty = true; _tourBack(); }
        } },
      { key: 'command', mp3: 'tour-command', sec: 8, lead: 800,
        start: function () {
          _tourPlanOpen(true);
          _tourLock();
          if (_cmd.el) _cmd.el.classList.add('cf-tour-type');
          _tourPulse([_cmd.mic, _cmd.inp]);
        },
        /* the sentence typed into the field letter by letter, held, cleared;
           never sent */
        cycle: function (cb, run) {
          var t = _tourType(), k = 0;
          var next = function () {
            if (k <= t.length) { _tourField(t.slice(0, k++)); _tourLater(next, 70, run); return; }
            _tourLater(function () { _tourField(''); _tourLater(cb, 400, run); }, 1600, run);
          };
          next();
        },
        end: function () { _tourField(''); if (_cmd.el) _cmd.el.classList.remove('cf-tour-type'); } },
      { key: 'trace', mp3: 'tour-trace', sec: 6, lead: 600,
        start: function () {
          _tourPlanOpen();
          _tourPulse([_tourTraceIcon()]);
        },
        cycle: function (cb, run) { _tourLater(cb, 800, run); },
        end: function () {} },
      /* core-v643: RANCH's footprint walked on the bar's arrows -- the
         rectangle, the L on each corner */
      { key: 'footprint', mp3: 'tour-footprint', sec: 7, lead: 2000,
        start: function (run) {
          _tourPlanOpen();
          var k = _tourPlanCycleKey();
          if (!k) { _T.skip = 'no footprint shape on this product'; return; }
          _T.fpK = k;
          _tourPulse([_tourStripIcon(k)]);
          _tourLater(function () {
            var b2 = _tourStripIcon(_T.fpK);
            _tourPulse([]);
            if (b2 && _SX.sel !== _T.fpK) b2.click();
          }, 1100, run);
        },
        cycle: function (cb, run) { _tourArrow(1); _tourLater(cb, 1400, run); },
        end: function () { _tourUnpick(); _tourBack(); _tourRelock(); } },
      /* core-v636: a wall added with one tap, then dragged one way and the other */
      _tourWallStep('walls', 'tour-walls', 7, 'wallV', 'wallH', 'wall'),
      /* core-v643: the same with the T house's load-bearing masonry */
      _tourWallStep('bearing', 'tour-bearing', 8, 'bearV', 'bearH', 'load-bearing wall'),
      /* core-v643: the Opening tile, an opening in the longest wall, wider
         and back */
      { key: 'wall-opening', mp3: 'tour-wall-opening', sec: 7, lead: 2600,
        start: function (run) {
          _tourPlanOpen();
          var b = _tourPlanTool(_tourHoleTile);
          if (!b) { _T.skip = 'no wall openings on this product'; return; }
          _T.hole = null; _T.holeW = false;
          _tourPulse([b]);
          _tourLater(function () {
            var b2 = _tourPlanTool(_tourHoleTile);
            _tourPulse([]);
            if (b2 && !_holeMode) b2.click();
          }, 1200, run);
          _tourLater(function () {
            var h = null;
            try { h = addHole(); } catch (eH) { h = null; _log('tour     wall opening: adding THREW ' + ((eH && eH.message) || eH)); }
            try { if (_holeMode) holeMode(false); } catch (eM) {}
            if (h) { _T.hole = h; _T.dirty = true; }
            try { drawPlan(); if (_wiz && _wiz.refresh) _wiz.refresh(); } catch (eR) {}
            _log('tour     wall opening: ' + (h ? ('one in ' + h.kind + ' ' + h.ref + ', ' + Math.round(h.w * 1000) + ' wide') : 'NONE went in'));
          }, 2400, run);
        },
        cycle: function (cb, run) {
          var h = _T.hole;
          if (!h || _T.holeW) { _tourLater(cb, 800, run); return; }
          _T.holeW = true;
          var st = 0;
          try { if (typeof __PROD._planStep === 'function') st = +__PROD._planStep() || 0; } catch (eS) { st = 0; }
          if (!(st > 0.05)) st = 0.25;
          var w0 = h.w, u0 = h.u, F = [1, 2, 1, 0], k = 0;
          var next = function () {
            if (k >= F.length) { _tourLater(cb, 300, run); return; }
            var f = F[k++];
            h.w = Math.round((w0 + 2 * st * f) * 1000) / 1000;
            h.u = Math.max(0, Math.round((u0 - st * f) * 1000) / 1000);
            try { snapHole(h); rebuild(); drawPlan(); } catch (eR) {}
            _tourLater(next, 450, run);
          };
          next();
        },
        end: function () { _tourHoleOff(); _T.hole = null; _tourBack(); _tourRelock(); } },
      /* the door tool on, a door in a partition */
      { key: 'doors', mp3: 'tour-doors', sec: 6, lead: 2500,
        start: function (run) {
          _tourPlanOpen();
          var b = _tourPlanTool(function (it) { return it.tap === planDoorTap; });
          if (!b) { _T.skip = 'no door tool on the plan strip'; return; }
          _T.doorNew = null; _T.doorSlid = false;
          _tourPulse([b]);
          _tourLater(function () {
            var b2 = _tourPlanTool(function (it) { return it.tap === planDoorTap; });
            _tourPulse([]);
            if (b2 && !STATE.doorTool) b2.click();
            _tourDoorIn();
          }, 1300, run);
        },
        /* core-v640: the new door slid along its wall and back */
        cycle: function (cb, run) {
          var d = _T.doorNew;
          if (!d || _T.doorSlid) { _tourLater(cb, 800, run); return; }
          _T.doorSlid = true;
          var D = STATE.doors || [], i, dr = null;
          for (i = 0; i < D.length; i++) if (D[i] === d || (D[i] && D[i].pid === d.pid && Math.abs(D[i].u - d.u) < 1e-6)) dr = D[i];
          if (!dr) { _tourLater(cb, 800, run); return; }
          _tourFrames(_tourDoorFrames(dr), 150, cb, run);
        },
        end: function () { _tourBack(); _tourRelock(); } },
      /* the scissors on, the red handles show, one piece of wall cut away */
      { key: 'scissors', mp3: 'tour-scissors', sec: 7, lead: 3700,
        start: function (run) {
          _tourPlanOpen();
          var b = _tourPlanTool(function (it) { return it.tap === planCutTap; });
          if (!b) { _T.skip = 'no cut tool on the plan strip'; return; }
          _tourPulse([b]);
          _tourLater(function () {
            _tourHasPart();
            var b2 = _tourPlanTool(function (it) { return it.tap === planCutTap; });
            if (b2 && !STATE.cutsOn) b2.click();
            _tourLater(function () {
              var h = $('cf-plan-icons'), hb = h ? h.querySelector('button') : null;
              _tourPulse(hb ? [hb] : []);
            }, 300, run);
          }, 1200, run);
          /* core-v707 (Marek: pri scissors udelej zoom in na pricku, cut, a
             pak zoom out, a pricku muzes vratit): the plan eases in on the
             piece about to go, it is cut once the plan is there, the plan
             eases back out to where it was, and the piece comes back. Each
             move waits for the one before (a tablet paints the plan slower
             than a timer runs); with no plan camera, the cut alone at 3.4 s */
          var cut = function () {
            var PL = _P(), S = null;
            _tourPulse([]);
            try { S = (PL && PL.partSegs) ? PL.partSegs() : null; } catch (e) { S = null; }
            if (!S || !S.length || !PL.cutPartSeg) { _log('tour     scissors: no piece of wall to cut'); return false; }
            /* the walls and doors as they stand before the cut, to put the piece back */
            try { _T.scPre = { parts: JSON.stringify(STATE.parts || []), doors: JSON.stringify(STATE.doors || []) }; } catch (eS) { _T.scPre = null; }
            try { _tourLightOn(); PL.cutPartSeg(0); _T.dirty = true; _tourPaint(); _tourLightOff(); } catch (eC) { _log('tour     scissors: cutting THREW ' + ((eC && eC.message) || eC)); return false; }
            _log('tour     scissors: one piece of ' + S.length + ' cut away');
            return true;
          };
          var back = function () {
            var pre = _T.scPre;
            _T.scPre = null;
            if (!pre) return;
            try { STATE.parts = JSON.parse(pre.parts); STATE.doors = JSON.parse(pre.doors); _tourPaint(); } catch (eP) { _log('tour     scissors: putting the piece back THREW ' + ((eP && eP.message) || eP)); return; }
            _log('tour     scissors: out again, the cut piece put back');
          };
          _tourLater(function () {
            var PL = _P(), S = null, st = _stage(), cam = st && st.orthoCam, oc = st && st.orthoControls;
            try { S = (PL && PL.partSegs) ? PL.partSegs() : null; } catch (e) { S = null; }
            if (!S || !S.length || !cam || !oc || !oc.target || STATE.view !== '2d') { _tourLater(cut, 1400, run); return; }
            var g = S[0], len = Math.abs(g.e - g.s), W = (cam.right - cam.left);
            _T.scZ = { x: oc.target.x, z: oc.target.z, k: cam.zoom || 1 };
            var z1 = Math.max(_T.scZ.k, W / Math.max(3, len * 2.4));
            _log('tour     scissors: in on the piece at ' + g.mx.toFixed(2) + ', ' + g.mz.toFixed(2) + ' -- zoom ' + _T.scZ.k.toFixed(2) + ' -> ' + z1.toFixed(2));
            _tourPlanEase(g.mx, g.mz, z1, 12, run, function () {
              _tourLater(function () {
                cut();
                _tourLater(function () {
                  var Z = _T.scZ;
                  if (!Z) return;
                  _T.scZ = null;
                  _tourPlanEase(Z.x, Z.z, Z.k, 12, run, function () { _tourLater(back, 500, run); });
                }, 1200, run);
              }, 500, run);
            });
          }, 2000, run);
        },
        cycle: function (cb, run) { _tourLater(cb, 800, run); },
        end: function () {
          if (_T.scZ) { var Z = _T.scZ; _T.scZ = null; _tourPlanEase(Z.x, Z.z, Z.k, 1, _T.run); }
          _T.scPre = null;
          _tourBack(); _tourRelock();
        } },
      /* core-v643: A-FRAME's stairs -- the other kind, then turned */
      { key: 'stairs', mp3: 'tour-stairs', sec: 7, lead: 2000,
        start: function (run) {
          _tourPlanOpen();
          var R = _tourPlanRow(_t('stairs', 'Stairs'));
          if (R.length < 2) { _T.skip = 'no stairs on this product'; return; }
          _T.stK = 0;
          _tourPulse(R);
          _tourLater(function () { _tourPulse([]); _tourStairTap(run); }, 1300, run);
        },
        cycle: function (cb, run) { _tourLater(function () { _tourStairTap(run); _tourLater(cb, 700, run); }, 1400, run); },
        end: function () { _tourBack(); _tourRelock(); } },
      /* the furniture catalogue opened, a piece put in and dragged across
         the room and back */
      { key: 'fitout', mp3: 'tour-fitout', sec: 7, lead: 3600,
        start: function (run) {
          _tourPlanOpen();
          var b = _tourStripIcon('fitout');
          if (!b) { _T.skip = 'no fitout on this product'; return; }
          _T.fitPiece = null;
          _tourPulse([b]);
          _tourLater(function () { var b2 = _tourStripIcon('fitout'); _tourPulse([]); if (b2) b2.click(); }, 1100, run);
          _tourLater(function () {
            var t = _tourFitTile();
            if (t) { try { t.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (eS) {} _tourPulse([t]); }
          }, 2000, run);
          _tourLater(function () {
            var t = _tourFitTile();
            var L0 = ((STATE.product && STATE.product.fixtures) || []).slice();
            _tourPulse([]);
            if (t) t.click();
            var L = (STATE.product && STATE.product.fixtures) || [], i;
            for (i = L.length - 1; i >= 0; i--) if (L0.indexOf(L[i]) < 0) { _T.fitPiece = L[i]; break; }
            if (_T.fitPiece) _T.dirty = true;
            _log('tour     fitout: ' + (_T.fitPiece ? ('a ' + (_T.fitPiece.kind || 'piece') + ' put in') : ('NO piece came in (' + (t ? 'the tile did nothing' : 'no catalogue tile') + ')')));
          }, 3200, run);
        },
        cycle: function (cb, run) {
          var f = _T.fitPiece, PL = _P(), FX = PL && PL.fixtures;
          if (!f || !FX || !FX.moveTo) { _tourLater(cb, 700, run); return; }
          /* toward the middle of the room and back, never into a wall */
          var x0 = f.x, z0 = f.z, k = 0, G = [0.25, 0.5, 0.75, 1, 1, 0.75, 0.5, 0.25, 0];
          var dx = (x0 < 0 ? 1 : -1) * 1.4, dz = (z0 < 0 ? 1 : -1) * 0.9;
          var next = function () {
            if (k >= G.length) { _tourPaint(); cb(); return; }
            try { FX.moveTo(f, x0 + dx * G[k], z0 + dz * G[k]); k++; drawPlan(); } catch (eM) { k = G.length; }
            _tourLater(next, 200, run);
          };
          next();
        },
        end: function () {
          var pk = global.document.getElementById('cfg-ui-picker'), x = pk ? pk.querySelector('[data-pk-close]') : null;
          if (x) { try { x.click(); } catch (eX) {} }
          try { if (fitEdit()) fitEdit(false, 'the tour'); } catch (e) {}
          _fitRoom = null;
          _T.fitPiece = null;
          _tourBack(); _tourRelock();
        } },
      /* only where a plan read from a picture left suggested furniture */
      { key: 'suggested', mp3: 'tour-suggested', sec: 7, lead: 600,
        has: function () { try { return _sugCount() > 0; } catch (e) { return false; } },
        start: function () {
          _tourPlanOpen();
          var b = _tourPlanTool(function (it) { return it.svg === _SUG_SVG; });
          if (!b) { _T.skip = 'no suggested furniture in this project'; return; }
          _tourPulse([b]);
        },
        cycle: function (cb, run) { _tourLater(cb, 800, run); },
        end: function () {} },
      /* the whole layout turned over left to right, held, put back */
      { key: 'mirror', mp3: 'tour-mirror', sec: 8, lead: 3600,
        start: function (run) {
          _tourPlanOpen();
          var b = _tourPlanTool(function (it) { return it.tap === planMirrorTap; });
          if (!b) { _T.skip = 'no mirror on the plan strip'; return; }
          _tourAsym();
          _tourLater(function () { _tourPulse([_tourPlanTool(function (it) { return it.tap === planMirrorTap; })]); }, 600, run);
          _tourLater(function () {
            var b2 = _tourPlanTool(function (it) { return it.tap === planMirrorTap; });
            _tourPulse([]);
            _T.planAsked = true;
            _T.dirty = true;
            if (b2) b2.click();
          }, 2200, run);
        },
        cycle: function (cb, run) { _tourLater(cb, 800, run); },
        end: function () { _tourCancelPlan(); _tourBack(); _tourRelock(); } },
      /* ==== the edit strip (core-v637) ==== */
      /* the pitch swept on the slider, then the shapes walked on the arrows */
      /* core-v708 (Marek: strechu i zaklady vzdy ukazat vsechny varianty):
         every roof on the arrows (gable, Hip 1, Hip 2, Hip, mono, flat -- a
         wing's hips too), the pitch up and down once on the full hip (six
         frames -- on a tablet each is a whole build), then a roof a cycle
         while the voice goes on */
      { key: 'roof', mp3: 'tour-roof', sec: 6, lead: 2100,
        start: function (run) { _T.roofN = 0; _T.roofSw = _tourIg(); _tourIconStart('roof', run); },   /* core-v720: the reel shows the shapes, no pitch sweep */
        cycle: function (cb, run) {
          var sweep = function (done) {
            var R = STATE.roof;
            if (!R || !(+R.pitch > 0) || R.type === 'flat' || R.parapet) { done(); return; }
            var sl = _SX.sl ? _SX.sl.querySelector('input') : null, lo = 15, hi = 35;
            try { lo = pitchMin(); hi = pitchMax(); } catch (eL) {}
            _tourPulse(sl ? [sl] : []);
            var p0 = +R.pitch || 26, D = [5, 10, 5, 0, -5, 0], k = 0;
            var stepP = function () {
              if (k >= D.length) { _tourPulse([]); _tourLater(done, 300, run); return; }
              R.pitch = clamp(p0 + D[k++], lo, hi);
              _T.dirty = true;
              try { rebuild(); _sxRefresh(); } catch (eR) {}
              _tourLater(stepP, 170, run);
            };
            stepP();
          };
          if (!_T.roofN) {
            _T.roofN = 1;
            _tourWalkAll('roof', 900, run, cb, function (go) {
              var c = _tourRoofNow(), R = STATE.roof;
              /* the full hip; a product's own list (no t): its first pitched roof */
              var here = c && (c.t ? c.t === 'hip' : (R.type !== 'flat' && !R.parapet && +R.pitch > 0));
              if (_T.roofSw || !here) { go(); return; }
              _T.roofSw = true;
              sweep(go);
            });
            return;
          }
          /* the reel: the three shapes were enough, it waits for the voice (core-v720) */
          if (_tourIg()) { _tourLater(cb, 250, run); return; }
          _tourArrow(1);
          _tourLater(cb, 1300, run);
        },
        end: function () { _tourUnpick(); _tourBack(); } },
      /* core-v643: RANCH's cross gables -- picked (one on each eave comes on),
         then one of them made wider, narrower and back, as its grips on the
         Floorplan do (ranch-v49: no width slider) */
      { key: 'cross-gable', mp3: 'tour-cross-gable', sec: 7, lead: 2100,
        start: function (run) { _T.xgDone = false; if (_tourIconStart('step:xgable', run)) _T.dirty = true; },
        cycle: function (cb, run) {
          var X = STATE.product && STATE.product.xg, key = null, k;
          if (X && X.slots) for (k in X.slots) if (!key && X.slots.hasOwnProperty(k) && X.slots[k] && X.slots[k].on) key = k;
          if (_T.xgDone || !key) { _tourLater(cb, 800, run); return; }
          _T.xgDone = true;
          var S = X.slots[key], w0 = (S.w > 0) ? S.w : (((key.charAt(0) === 'b') ? X.wb : X.wa) || X.w || 2.4);
          var D = [0.3, 0.6, 0.9, 0.6, 0.3, 0, -0.3, -0.6, -0.3, 0], i = 0;
          _log('tour     cross gable: ' + key + ' wider, narrower and back');
          var next = function () {
            if (i >= D.length) { _tourLater(cb, 300, run); return; }
            S.w = Math.round((w0 + D[i++]) * 100) / 100;
            _T.dirty = true;
            try { rebuild(); } catch (eR) {}
            _tourLater(next, 220, run);
          };
          next();
        },
        end: function () { _tourUnpick(); _tourBack(); } },
      /* core-v643: the T house's ridges -- level, the wing lower, and back */
      _tourAutoFlip('ridge', 'tour-ridge', 7, 'roof', function (r) { return r.items.length === 1 && r.items[0].key === 'ridge'; }),
      _tourWalkStep('covering', 'tour-covering', 7),
      /* core-v643: the T house's roof insulation, above the rafters or between
         them, walked with the section on so the layers show */
      _tourAutoWalk('roof-insulation', 'tour-roof-insulation', 6, 'roof', function (r) {
        return r.items.length === 2 && (String(r.label || '') === String(_t('envelope_toggle', 'Insulation type')) ||
          String(r.items[0].label || '') === String(_t('roof_ins_above', 'Above rafters')));
      }, { start: function () { try { if (_cutTool && _cutTool.set) _cutTool.set({ axis: 'x', t: 0.5, flip: false, plane: true, handle: true, on: true }); } catch (eC) {} },
           end: function () { _tourCutOff(); } }),
      /* the wall height up and down on the structure panel's slider */
      { key: 'structure', mp3: 'tour-structure', sec: 5, lead: 1900,
        start: function (run) { _T.h0 = null; _tourIconStart('structure', run); },
        cycle: function (cb, run) {
          var H = STATE.clearH;
          if (!H || !(H[0] > 0)) { _tourLater(cb, 700, run); return; }
          if (_T.h0 == null) _T.h0 = H[0];
          var inp = global.document.querySelector('#cf-sx-modal input.cf-sx-range'), D = [0.15, 0.3, 0.45, 0.6, 0.45, 0.3, 0.15, 0], k = 0;
          _tourPulse(inp ? [inp] : []);
          var stepH = function () {
            if (k >= D.length) { _tourPulse([]); _tourLater(cb, 300, run); return; }
            H[0] = Math.round((_T.h0 + D[k++]) * 1000) / 1000;
            _T.dirty = true;
            try { _storeysFromHeights(); rebuild(); _sxRefresh(); } catch (eR) {}
            _tourLater(stepH, 180, run);
          };
          stepH();
        },
        end: function () { _T.h0 = null; _tourUnpick(); _tourBack(); } },
      /* core-v708 (Marek: strechu i zaklady vzdy ukazat vsechny varianty):
         every foundation on the arrows, one after another, then one a cycle
         while the voice goes on */
      { key: 'base', mp3: 'tour-base', sec: 7, lead: 2100,
        start: function (run) { _T.baseN = 0; _tourIconStart('base', run); },
        cycle: function (cb, run) {
          if (!_T.baseN) {
            _T.baseN = 1;
            _tourWalkAll('base', 1000, run, cb);
            return;
          }
          if (_tourIg()) { _tourLater(cb, 250, run); return; }   /* core-v720 */
          _tourArrow(1);
          _tourLater(cb, 1300, run);
        },
        end: function () { _tourUnpick(); _tourBack(); } },
      /* core-v768 (GARDEN BAR thread, Marek 2026-10-29: voice over ve 21 jazycich): THE PRODUCT'S OWN ROWS -- a
         product names the rows that make it what it is (__PROD._tourRow, GARDEN BAR: the counter's shape, its
         cladding, its worktop); the step opens each in turn and walks its choices on the arrows (the reel: the
         first row only), the visitor's own back at the end. The clip is the product's own (tour-product-<slug>
         through __PROD._tourClips); a product without the opt-in passes the step by */
      { key: 'product', mp3: 'tour-product', sec: 9, lead: 1200,
        has: function () { return _tourRows().length > 0; },
        start: function (run) { _T.prN = 0; _T.prK = 0; _tourEditOpen(); _tourIconStart(_tourRowKey(_tourRows()[0]), run); },
        cycle: function (cb, run) {
          var R = _tourRows().map(_tourRowKey);
          if (_T.prN || !R.length) { _tourLater(cb, 250, run); return; }
          var walk = function () {
            _tourWalkAll(R[_T.prK], 900, run, function () {
              _T.prK++;
              if (_T.prK >= R.length || _tourIg()) { _T.prN = 1; cb(); return; }
              _tourIconStart(R[_T.prK], run, 500);
              _tourLater(walk, 900, run);
            });
          };
          walk();
        },
        end: function () { _tourUnpick(); _tourBack(); } },
      /* core-v643: the THOW's trailer -- bumper-pull, gooseneck, deck-over --
         framed once with room for the gooseneck's neck */
      _tourAutoWalk('trailer', 'tour-trailer', 6, 'roof', function (r) {
        return r.items.length >= 2 && String(r.label || '') === String(_t('base', 'Base'));
      }, { start: function () { _tourCamKeep(); _tourFit({ out: true, k: 1.25 }); }, end: function () { _tourCamBack(); } }),
      /* the picker opened and its tabs walked, a window put on the wall,
         slid along it, and taken off by the bin beside it */
      { key: 'openings', mp3: 'tour-openings', sec: 8, lead: 4600,
        start: function (run) {
          if (!_tourIconStart('openings', run)) return;
          _T.win = null;
          _tourLater(function () {
            var ch = [].slice.call(global.document.querySelectorAll('#cfg-ui-picker .cfg-pk-chip')), k = 0;
            var walk = function () {
              if (k >= ch.length) { if (ch[0]) ch[0].click(); return; }
              _tourPulse([ch[k]]);
              ch[k++].click();
              _tourLater(walk, 550, run);
            };
            walk();
          }, 1900, run);
          _tourLater(function () {
            _tourPulse([]);
            var x = global.document.querySelector('#cfg-ui-picker [data-pk-close]');
            if (x) x.click();
            _T.win = _tourWindowIn();
          }, 4300, run);
        },
        cycle: function (cb, run) {
          var rec = _T.win, sg = _T.winSeg;
          if (!rec || !sg) { _tourLater(cb, 700, run); return; }
          if (_T.winSlid) {
            /* the bin beside it, pulsed and pressed: the window goes */
            var P = { x: sg.x0 + (sg.x1 - sg.x0) * rec.at / sg.len, y: (sg.y0 || 0) + (rec.sill || 0) + (rec.h || 1) / 2, z: sg.z0 + (sg.z1 - sg.z0) * rec.at / sg.len };
            try { _nearBinShow(rec, P); } catch (eB) {}
            var nb = $('cf-op-nearbin');
            _tourPulse(nb ? [nb] : []);
            _tourLater(function () {
              _tourPulse([]);
              if (nb && nb.classList.contains('cf-on')) nb.click(); else { try { removeOpening(rec); } catch (eR) {} }
              _T.win = null;
              _tourLater(cb, 600, run);
            }, 1300, run);
            return;
          }
          _T.winSlid = true;
          var a0 = rec.at, span = Math.max(0.6, Math.min(1.6, sg.len * 0.3)), D = [0.25, 0.5, 0.75, 1, 0.75, 0.5, 0.25, 0], k = 0;
          var stepW = function () {
            if (k >= D.length) { try { _dragStop('the tour'); rebuild(); } catch (eS) {} _tourLater(cb, 300, run); return; }
            try { moveOpening(rec, a0 + span * D[k++]); _dragMove('a window or door', 'opening'); } catch (eM) { k = D.length; }
            _tourLater(stepW, 170, run);
          };
          stepW();
        },
        end: function () {
          _T.winSlid = false;
          try { _nearBinHide('the tour'); } catch (eH) {}
          var x = global.document.querySelector('#cfg-ui-picker [data-pk-close]');
          if (x) { try { x.click(); } catch (eX) {} }
          try { armOpening(null); } catch (eA) {}
          try { if (opEdit()) opEdit(false); } catch (eO) {}
          _T.win = null; _T.winSeg = null;
          _tourUnpick(); _tourBack();
        } },
      /* three colour slots, one dot each */
      /* the roof, the facade, the windows & doors and the accessories, one
         colour each (core-v639, Marek: zmen i barvu fasady, oken a doplnku,
         nejen strechy) -- each slot brought up on the bar, then a dot from
         the far side of its palette, so the change is seen */
      { key: 'colors', mp3: 'tour-colors', sec: 7, lead: 1700,
        start: function (run) { _T.colN = 0; _T.colBack = []; _tourIconStart('colors', run); },
        /* the four in one go, so a voice that ends early does not cut the
           last ones off; 1.1 s each */
        cycle: function (cb, run) {
          if (_T.colN) { _tourLater(cb, 800, run); return; }
          var reelC = _tourIg(), want = reelC ? ['roof', 'facade', 'joinery'] : ['roof', 'facade', 'joinery', 'metal'], n = 0;   /* core-v720: three on the reel */
          /* core-v744 (Marek 19:47: "cladding zmenit barvu"): the facade slot is
             the render's colour only -- on boards the hero changes the timber
             (the cladding with its trims) instead */
          if (_tourAuto()) { var ckH = 'horizontal'; try { ckH = cladKind(); } catch (eCk) {} want = ['roof', (ckH === 'off') ? 'facade' : 'timber', 'joinery']; }
          var next = function () {
            if (n >= want.length) {
              _T.colN = n; _tourPulse([]);
              _tourLater(cb, 300, run); return;
            }
            var key = want[n++], CS = [], ix = -1, i;
            try { CS = _sxColorSlots(); } catch (eC) { CS = []; }
            for (i = 0; i < CS.length; i++) if (CS[i] && CS[i].key === key) ix = i;
            if (ix < 0) { next(); return; }
            _SX.colorIdx = ix;
            try { _sxRefresh(); } catch (eR) {}
            _tourLater(function () {
              var dots = _SX.bar ? [].slice.call(_SX.bar.querySelectorAll('.cf-sx-dot')) : [], at = 0, j;
              for (j = 0; j < dots.length; j++) if (dots[j].classList.contains('cf-on')) at = j;
              if (dots.length > 1) {
                var d = dots[(at + Math.max(1, Math.floor(dots.length / 2))) % dots.length];
                /* core-v743: the hero keeps the colour on to the end and puts it back then */
                if (_tourAuto()) { try { _T.colBack.push({ s: key, k: colorSlotKey(key) }); } catch (eK) {} }
                _tourPulse([d]);
                d.click();
                _T.dirty = true;
                _log('tour     colors: ' + key + ' changed');
              }
              _tourLater(next, reelC ? 650 : 800, run);
            }, reelC ? 250 : 300, run);
          };
          next();
        },
        end: function () { _SX.colorIdx = 0; _tourUnpick(); if (!_tourAuto()) _tourBack(); } },
      _tourWalkStep('facade', 'tour-facade', 5),
      /* the terrace on, grown and back, decking then pavers */
      { key: 'terrace', mp3: 'tour-terrace', sec: 7, lead: 2300,
        start: function (run) {
          _tourEditOpen();
          var b = _tourStripIcon('terrace');
          if (!b) { _T.skip = 'no terrace on this product'; return; }
          _T.tGrown = false;
          _tourCamKeep();
          _tourPulse([b]);
          _tourLater(function () {
            _tourPulse([]);
            var T0 = _terrace(), b2 = _tourStripIcon('terrace');
            if (!T0.on && b2) { b2.click(); _T.dirty = true; }
            else if (b2 && _SX.sel !== 'terrace') { _SX.sel = 'terrace'; _sxRefresh(); }
            _tourFit({ out: true, k: 1.2 });
          }, 1200, run);
        },
        cycle: function (cb, run) {
          var T = _terrace();
          if (!T.on) { _tourLater(cb, 700, run); return; }
          if (!_T.tGrown) {
            _T.tGrown = true;
            var w0 = +T.w || 4, d0 = +T.d || 3, D = [0.2, 0.4, 0.6, 0.4, 0.2, 0], k = 0;
            var stepT = function () {
              if (k >= D.length) { _tourLater(cb, 300, run); return; }
              var f = D[k++];
              T.w = Math.round((w0 * (1 + f * 0.5)) * 100) / 100; T.d = Math.round((d0 * (1 + f * 0.5)) * 100) / 100;
              _T.dirty = true;
              try { rebuild(); if (typeof _hRefresh === 'function') _hRefresh(); } catch (eR) {}
              _tourLater(stepT, 200, run);
            };
            stepT();
            return;
          }
          _tourArrow(1);
          _tourLater(cb, 1500, run);
        },
        end: function () { _tourUnpick(); _tourBack(); _tourCamBack(); } },
      _tourYardStep('pergola', 'tour-pergola', 6),
      _tourYardStep('pool', 'tour-pool', 7),
      /* core-v643: the sauna's hot tub on, moved along the wall facing the
         camera, off */
      _tourAutoFlip('hot-tub', 'tour-hot-tub', 5, 'exterior', function (r) {
        return r.items.length === 1 && String(r.items[0].label || '') === String(_t('hot_tub', 'Hot tub'));
      }, { fit: true, cycle: _tourTubCycle }),
      _tourSwitchStep('blinds', 'tour-blinds', 5, function () { return blindsOn(); }, false),
      /* core-v726 (TAURUS, Marek: udelej i ukazku zapinani a vypinani /
         cyklovani vyplni -- v ig i v pruvodci): a product with a kind per bay
         fills an empty bay (picked), walks its kinds on the arrows, fills a
         second, opens the first; the rest walk the kinds as before. On the
         reel only where the product picks bays. */
      { key: 'slats', mp3: 'tour-slats', sec: 9, lead: 1800,
        start: function (run) {
          /* core-v743: the hero -- the eaves out into a porch first, the infill in its cycle */
          if (_tourAuto()) { _T.slN = 0; return; }
          if (_tourIg() && !_pwMode.perBay) { _T.skip = 'the reel shows the infill only where a bay is picked'; return; }
          if (!_tourIconStart('slats', run)) return;
          _T.pwK = 0; _T.pwA = ''; _T.pwB = ''; _T.slN = 0;
        },
        cycle: function (cb, run) {
          if (_tourAuto()) {
            if (_T.slN) { _tourLater(cb, 250, run); return; }
            _T.slN = 1;
            _tourEavesTo(1, 1500, run, function () { _tourHeroInfill(run, cb); });
            return;
          }
          if (!_pwMode.perBay) {
            _tourArrow(1); _tourLater(cb, 1500, run); return;
          }
          var k = _T.pwK++, ig = _tourIg();
          var empty = function (not) {
            var L = postBaySides(), order = ['z1', 'x1', 'z0', 'x0'], i2, q2;
            L.sort(function (a, b) { return order.indexOf(a.side) - order.indexOf(b.side); });
            for (i2 = 0; i2 < L.length; i2++) {
              var n2 = postBayCount(L[i2]);
              for (q2 = 0; q2 < n2; q2++) { var key2 = _bayKey(L[i2].side, q2); if (!postWallOn(key2) && key2 !== not) return key2; }
            }
            return '';
          };
          if (k === 0) {
            _T.pwA = empty(''); _T.pwAf = !!_T.pwA;
            if (_T.pwA) { setPostWall(_T.pwA, true); _T.dirty = true; try { _sxRefresh(); } catch (eR0) {} }
            else {
              /* every bay filled: one picked as it is, its kinds walked */
              var Lf = postBaySides(), i4, q4;
              for (i4 = 0; i4 < Lf.length && !_T.pwA; i4++) for (q4 = 0; q4 < postBayCount(Lf[i4]) && !_T.pwA; q4++) if (postWallOn(_bayKey(Lf[i4].side, q4))) _T.pwA = _bayKey(Lf[i4].side, q4);
              if (_T.pwA) { _pwSel = _T.pwA; try { rebuild(); _sxRefresh(); } catch (eR4) {} }
            }
            _log('tour     infill: bay ' + (_T.pwA || 'none') + (_T.pwAf ? ' filled and picked' : ' picked as it is'));
            _tourLater(cb, ig ? 900 : 1300, run); return;
          }
          if (k === 1 || k === 2) { if (_T.pwA) _tourArrow(1); _tourLater(cb, ig ? 800 : 1200, run); return; }
          if (k === 3) {
            _T.pwB = empty(_T.pwA);
            if (_T.pwB) { setPostWall(_T.pwB, true); _T.dirty = true; try { _sxRefresh(); } catch (eR1) {} }
            _log('tour     infill: bay ' + (_T.pwB || 'none free') + ' filled');
            _tourLater(cb, ig ? 800 : 1200, run); return;
          }
          if (k === 4 && _T.pwA && _T.pwAf) {
            setPostWall(_T.pwA, false); _T.dirty = true;
            _log('tour     infill: bay ' + _T.pwA + ' opened again');
            _tourLater(cb, ig ? 700 : 1000, run); return;
          }
          if (_pwSel) { _pwSel = null; try { rebuild(); _sxRefresh(); } catch (eR2) {} }
          _tourLater(cb, ig ? 250 : 600, run);
        },
        end: function () {
          if (_tourAuto()) { _tourUnpick(); return; }   /* core-v743: the porch stays for the exploded view */
          if (_pwSel) { _pwSel = null; try { rebuild(); } catch (eR3) {} } _tourUnpick(); _tourBack();
        } },
      /* the address, the climate zone, the trees on and off -- nothing typed */
      { key: 'place', mp3: 'tour-place', sec: 13, lead: 1600,
        start: function (run) {
          if (!_tourIconStart('place', run)) return;
          _T.trees0 = JSON.stringify(STATE.trees || {});
          _T.placeK = 0;
        },
        cycle: function (cb, run) {
          var k = _T.placeK++;
          if (k === 0) { _tourPulse([$('cf-map-address-input')].filter(Boolean)); _tourLater(cb, 2600, run); return; }
          if (k === 1) { _tourPulse([$('cf-map-frost-zone')].filter(Boolean)); _tourLater(cb, 2600, run); return; }
          if (k === 2) {
            /* the first trees tile pulsed and tapped (core-v639, Marek: na
               ukazku zapnout i prvni ikonu stromu) */
            var tb = _tourTreeTile();
            _tourPulse(tb ? [tb] : []);
            _tourLater(function () {
              var tb2 = _tourTreeTile(), on0 = _tourTreesA();
              if (tb2) tb2.click(); else { try { toggleTrees('a'); } catch (eT) {} }
              if (_tourTreesA() && !on0) { _T.dirty = true; _T.treesOn = true; }
              _log('tour     place: the first trees tile ' + (tb2 ? 'tapped' : 'not found -- the trees switched directly') + ', trees ' + (_tourTreesA() ? 'ON' : 'off'));
            }, 900, run);
            _tourLater(cb, 4200, run);
            return;
          }
          if (k === 3 && _T.treesOn) {
            _T.treesOn = false;
            var tb3 = _tourTreeTile();
            if (tb3 && _tourTreesA()) tb3.click(); else if (_tourTreesA()) { try { toggleTrees('a'); } catch (eT2) {} }
            _tourPulse([]);
            _tourLater(cb, 1200, run);
            return;
          }
          _tourLater(cb, 900, run);
        },
        end: function () {
          if (_T.treesOn) { _T.treesOn = false; if (_tourTreesA()) { try { toggleTrees('a'); } catch (eT) {} } }
          _tourUnpick();
          _tourBack();
          try { if (_T.trees0 != null && JSON.stringify(STATE.trees || {}) === _T.trees0) _buildScenery(); } catch (eB) {}
          try { var fz = $('cf-map-frost-zone'); if (fz) fz.value = String(STATE.climateZone); } catch (eZ) {}
          _T.trees0 = null;
        } },
      /* ==== the rail (core-v637) ==== */
      { key: 'view', mp3: 'tour-view', sec: 6, lead: 1500,
        start: function (run) {
          _tourStripShut();
          var el = _tourRail('cf-btn-view');
          if (!el) { _T.skip = 'no 2D / 3D button'; return; }
          _tourCamKeep();
          _T.view2 = STATE.view;
          _tourPulse([el]);
          _tourLater(function () { el.click(); }, 900, run);
        },
        cycle: function (cb, run) { _tourLater(cb, 900, run); },
        end: function () {
          var el = _tourRail('cf-btn-view');
          if (el && STATE.view !== _T.view2) el.click();
          _tourCamBack();
        } },
      { key: 'viz', mp3: 'tour-viz', sec: 9, lead: 900,
        start: function () {
          _tourStripShut();
          var el = _tourRail('cf-btn-viz');
          if (!el) { _T.skip = 'no visualisation button'; return; }
          _T.vizN = 0;
          try { _T.vizL = vizStages().length; } catch (eV) { _T.vizL = 1; }
          if (!(_T.vizL > 1)) { _T.skip = 'only one visualisation stage'; return; }
          _tourPulse([el]);
          var c = _tourControls();
          if (c) { _T.rot = { on: c.autoRotate, sp: c.autoRotateSpeed }; c.autoRotate = true; c.autoRotateSpeed = 0.6; }
        },
        cycle: function (cb, run) {
          var el = _tourRail('cf-btn-viz');
          if (el && _T.vizN < _T.vizL - 1) { el.click(); _T.vizN++; }
          _tourLater(cb, 2000, run);
        },
        end: function () {
          var c = _tourControls();
          if (c && _T.rot) { c.autoRotate = !!_T.rot.on; if (_T.rot.sp != null) c.autoRotateSpeed = _T.rot.sp; }
          _T.rot = null;
          _tourVizBack();
        } },
      { key: 'section', mp3: 'tour-section', sec: 9, lead: 1500,
        start: function (run) {
          _tourStripShut();
          var el = _tourRail('cf-btn-colors');
          if (!el) { _T.skip = 'no section button'; return; }
          _T.cutK = 0;
          _tourPulse([el]);
          _tourLater(function () { if (!(_cutTool && _cutTool.state && _cutTool.state().on)) el.click(); }, 900, run);
        },
        cycle: function (cb, run) {
          if (!_cutTool || !_cutTool.set) { _tourLater(cb, 700, run); return; }
          var axis = (_T.cutK++ % 2) ? 'z' : 'x', T = [0.5, 0.42, 0.34, 0.26, 0.34, 0.42, 0.5, 0.58, 0.66, 0.58, 0.5], k = 0;
          try { _cutTool.set({ axis: axis, t: 0.5 }); } catch (eA) {}
          var stepC = function () {
            if (k >= T.length) { _tourLater(cb, 200, run); return; }
            try { _cutTool.set({ t: T[k++] }); } catch (eS) {}
            _tourLater(stepC, 140, run);
          };
          stepC();
        },
        end: function () { _tourCutOff(); } },
      { key: 'assembly', mp3: 'tour-assembly', sec: 11, lead: 800,
        start: function () {
          _tourStripShut();
          var el = _tourRail('cf-btn-assembly');
          if (!el) { _T.skip = 'no assembly button'; return; }
          _tourCamKeep();
          _T.asmK = 0;
          _tourPulse([el]);
        },
        /* core-v749 (Marek 2026-10-21: kdyz prezentujeme exploded a pak kroky,
           prehraje se jen prvni krok -- at je videt aspon 4 kroky, mohou byt v
           rychlem sledu): the walk in ONE pass -- a tap a cycle let the voice's
           end close the step after the first part; now the exploded view, back
           together and four assembly steps in quick succession, then the step
           ends (the reel and the whole tour alike, a little quicker on the
           reel) */
        cycle: function (cb, run) {
          var el = _tourRail('cf-btn-assembly');
          var ig = _tourIg();
          if (!el || _T.asmK > 0) { _tourLater(cb, ig ? 500 : 800, run); return; }
          var HOLD = ig ? [2200, 900, 650, 650, 650, 800] : [2800, 1600, 900, 900, 900, 1200], k = 0;
          var next = function () {
            if (run !== _T.run) return;
            var e2 = _tourRail('cf-btn-assembly');
            if (!e2 || k >= HOLD.length) { _log('tour     assembly: exploded, back together and ' + Math.max(0, _T.asmK - 2) + ' step(s) shown (core-v749)'); cb(); return; }
            e2.click();
            _T.asmK++;
            _tourLater(next, HOLD[k++], run);
          };
          next();
        },
        end: function () { _tourAsmOff(); _tourCamBack(); } },
      /* the figure on, walked along the wall and out and back, then off
         (core-v639, Marek: behem prezentace zapnuti vypnuti s nim muzes i
         pohybovat) */
      { key: 'figure', mp3: 'tour-figure', sec: 5, lead: 1500,
        start: function (run) {
          _tourStripShut();
          var el = _tourRail('cf-btn-body');
          if (!el) { _T.skip = 'no scale figure'; return; }
          _T.body2 = !!STATE.bodyVisible;
          _T.figMoved = false;
          _T.figWait = 0;
          _tourPulse([el]);
          _tourLater(function () { if (!STATE.bodyVisible) el.click(); }, 900, run);
        },
        cycle: function (cb, run) {
          if (_T.figMoved) { _tourLater(cb, 600, run); return; }
          var pr = _findPerson();
          if ((!pr || !STATE.bodyVisible) && _T.figWait++ < 10) { _tourLater(cb, 250, run); return; }
          _T.figMoved = true;
          if (!pr) { _tourLater(cb, 300, run); return; }
          var x0 = _bodyX(), z0 = _bodyZ(), P = [[0, 0], [0, -2.4], [1.2, -2.4], [1.2, 0], [0, 0]], LEG = 650, t0 = _perfNow();
          _T.dirty = true;
          var walk = function () {
            if (run !== _T.run) return;
            var t = (_perfNow() - t0) / LEG, k = Math.min(P.length - 2, Math.floor(t)), f = t - k;
            if (t >= P.length - 1) { k = P.length - 2; f = 1; }
            var x = x0 + P[k][0] + (P[k + 1][0] - P[k][0]) * f, z = z0 + P[k][1] + (P[k + 1][1] - P[k][1]) * f;
            STATE.bodyX = x; STATE.bodyZ = z;
            var pp = _findPerson();
            if (pp) pp.position.set(x, 0, z);
            var st = _stage();
            if (st && st.wake) st.wake(300);
            if (t < P.length - 1) global.requestAnimationFrame(walk); else cb();
          };
          global.requestAnimationFrame(walk);
        },
        end: function () {
          var el = _tourRail('cf-btn-body');
          if (el && !!STATE.bodyVisible !== _T.body2) el.click();
          _tourBack();
        } },
      { key: 'units', mp3: 'tour-units', sec: 7, lead: 1500,
        start: function (run) {
          _tourStripShut();
          var el = _tourRail('cf-btn-unit');
          if (!el) { _T.skip = 'no units button'; return; }
          _T.met2 = !!STATE.isMetric;
          _tourPulse([el]);
          _tourLater(function () { el.click(); _T.dirty = true; }, 900, run);
        },
        cycle: function (cb, run) { _tourLater(cb, 800, run); },
        end: function () {
          var el = _tourRail('cf-btn-unit');
          if (el && !!STATE.isMetric !== _T.met2) el.click();
          _tourBack();
        } },
      /* the AR window opened so the QR is seen, then closed by its cross
         (core-v639, Marek: jde spustit okno ar a pak ho zavrit krizkem, at
         uzivatel vidi qr code). The model is uploaded as a tap on AR would
         -- once: a second play on the same project shows the QR it got */
      { key: 'ar', mp3: 'tour-ar', sec: 7, lead: 1400,
        start: function (run) {
          _tourStripShut();
          var el = _tourRail('cf-btn-ar');
          if (!el) { _T.skip = 'no AR button'; return; }
          _tourPulse([el]);
          _tourLater(function () { _tourPulse([]); _tourArOpen(el); }, 1000, run);
        },
        cycle: function (cb, run, done) {
          if (_tourArReady() && _T.arPend != null) { _T.arSig = _T.arPend; _T.arPend = null; }
          if (!done()) { _tourLater(cb, 500, run); return; }
          var t0 = _perfNow();
          var close = function () {
            if (_tourArShown() && !_tourArReady() && _perfNow() - t0 < 6000) { _tourLater(close, 300, run); return; }
            if (_tourArReady() && _T.arPend != null) { _T.arSig = _T.arPend; _T.arPend = null; }
            var x = global.document.querySelector('#cf-ar-modal [data-cf-ar-close]');
            if (!x || !_tourArShown()) { cb(); return; }
            _tourPulse([x]);
            _tourLater(function () { _tourPulse([]); _tourArClose(); _tourLater(cb, 300, run); }, 1200, run);
          };
          close();
        },
        end: function () { _tourArClose(); } },
      _tourRailStep('undo', 'tour-undo', 5, 'cf-btn-undo'),
      _tourRailStep('share', 'tour-share', 6, 'cf-btn-share'),
      _tourRailStep('pdf', 'tour-pdf', 17, 'cf-btn-pdf'),
      /* the model turned to another angle while + PDF pulses -- nothing is taken */
      /* core-v645: + PDF is hidden until the PDF icon's first tap -- the tour
         taps it (pulsed first), + PDF comes up and pulses; hidden again at
         the end of the tour if it was hidden at play */
      { key: 'pdf-add', mp3: 'tour-pdf-add', sec: 15, lead: 2200,
        start: function (run) {
          _tourStripShut();
          var el = $('cf-shot-chip'), pdf = _tourRail('cf-btn-pdf');
          if (!el) { _T.skip = 'no + PDF chip'; return; }
          if (_shotHidden() && !pdf) { _T.skip = 'no PDF icon to bring + PDF up'; return; }
          _tourCamKeep();
          var c = _tourControls();
          if (c) { _T.rot = { on: c.autoRotate, sp: c.autoRotateSpeed }; c.autoRotate = true; c.autoRotateSpeed = 1.2; }
          if (!_shotHidden()) { _tourPulse([el]); return; }
          _tourPulse([pdf]);
          _tourLater(function () {
            _tourPulse([]);
            if (_shotHidden()) {
              _T.chipShown = true;
              var p2 = _tourRail('cf-btn-pdf');
              if (p2) p2.click();
              _log('tour     pdf-add: the PDF icon tapped once -- + PDF ' + (_shotHidden() ? 'did NOT come up' : 'up'));
            }
            _tourPulse([$('cf-shot-chip')]);
          }, 1300, run);
        },
        cycle: function (cb, run) { _tourLater(cb, 800, run); },
        end: function () {
          var c = _tourControls();
          if (c && _T.rot) { c.autoRotate = !!_T.rot.on; if (_T.rot.sp != null) c.autoRotateSpeed = _T.rot.sp; }
          _T.rot = null;
          _tourCamBack();
        } },
      /* the total and the budget button, then the drawer opened, run down
         to its end and closed (core-v639, Marek: pri step budget otevrit i
         drawer, projet ho dolu a pak ho zavrit) */
      { key: 'budget', mp3: 'tour-budget', sec: 9, lead: 1500,
        start: function (run) {
          _tourStripShut();
          var L = [global.document.querySelector('.leva-float-group .leva-act--budget'), global.document.querySelector('[data-bstrip-mine-total]')]
            .filter(function (el) { return el && el.offsetParent !== null; });
          var can = (typeof global.openBudgetMainDrawer === 'function');
          if (!L.length && !can) { _T.skip = 'no budget on the page'; return; }
          _T.budRan = false;
          _tourPulse(L);
          if (can) _tourLater(function () { _tourPulse([]); _tourBudOpen(); }, _tourIg() ? 700 : 1200, run);
        },
        cycle: function (cb, run) {
          if (_T.budRan || !_T.budOpen) { _tourLater(cb, 600, run); return; }
          _T.budRan = true;
          /* core-v748: the container's show where the product asks for it */
          if (_tourBud2()) { _tourBudShow(cb, run); return; }
          var sc = _tourBudScroller();
          if (!sc) { _tourLater(cb, 600, run); return; }
          var y0 = sc.scrollTop, y1 = Math.max(0, sc.scrollHeight - sc.clientHeight), MS = _tourIg() ? 2500 : 4200, t0 = _perfNow();   /* core-v720: quicker on the reel */
          var run2 = function () {
            if (run !== _T.run) return;
            var f = Math.min(1, (_perfNow() - t0) / MS), e = f < 0.5 ? 2 * f * f : 1 - Math.pow(-2 * f + 2, 2) / 2;
            sc.scrollTop = Math.round(y0 + (y1 - y0) * e);
            if (f < 1) global.requestAnimationFrame(run2); else _tourLater(cb, 400, run);
          };
          global.requestAnimationFrame(run2);
        },
        end: function () { _tourBudItemClose(); _tourBudFilterOff('the step ended'); _tourBudClose(); } },
      /* the assistant's icon pulsed, its drawer opened and closed again */
      { key: 'ai', mp3: 'tour-ai', sec: 17, lead: 1500,
        start: function (run) {
          var el = global.document.querySelector('.leva-float-group .leva-act--ai');
          var can = !!(global.LevaAI && typeof global.LevaAI.open === 'function');
          if ((!el || el.offsetParent === null) && !can) { _T.skip = 'no assistant on the page'; return; }
          if (el && el.offsetParent !== null) _tourPulse([el]);
          if (can) _tourLater(function () { _tourPulse([]); try { global.LevaAI.open(); _T.aiOpen = true; } catch (eO) {} }, 1200, run);
        },
        cycle: function (cb, run) { _tourLater(cb, 800, run); },
        end: function () { _tourAiClose(); } },
      /* the pencil, where the visitor's own work starts */
      { key: 'outro', mp3: 'tour-outro', sec: 5, lead: 600,
        start: function () {
          _tourStripShut();
          if (!_cmd.edit) { _T.skip = 'no pencil'; return; }
          _tourPulse([_cmd.edit]);
        },
        cycle: function (cb, run) { _tourLater(cb, 800, run); },
        end: function () {} }
    ];
    /* THE ?ig PAGE IS THE REEL (core-v714, Marek: predelat ?ig, nemusi byt 2
       verze, cely pruvodce jen na strankach; highlights: 3 designs, strecha /
       zaklady s menici se cenou, floorplans, exploded, budget, AI asistent).
       A page opened with ?ig plays only these steps, in this order, each with
       its own short English line (voice:en/ig-<key>, the usual clip as the
       fallback); every other page keeps the whole tour. Steps a product has
       nothing for are passed by as before (has() / skip). */
    /* core-v720 (Marek 2026-10-19: highlights do 60 vterin -- designs i
       barevne varianty, floorplan z databaze nebo slovni popis, strecha /
       zaklady, rozpocet, AI agent): the reel's second cut */
    var _TOUR_IG = [['intro', ''], ['colors', 'ig-colors'], ['library', 'ig-library'], ['product', 'ig-product'], ['roof', 'ig-roof'], ['base', 'ig-base'],
                    ['trailer', 'ig-trailer'], ['slats', 'ig-infill'], ['assembly', 'ig-assembly'], ['budget', 'ig-budget'], ['ai', 'ig-ai'], ['outro', 'ig-outro']];
    /* core-v748 (Marek 2026-10-21: do IG pred budgetem exploded + par kroku
       assembly): the assembly step sits on the reel before the budget, but
       plays there only for a product that asks for it (__PROD._reelAsm) --
       the others' reels stay as they were timed */
    var _TOUR_FULL = _TOUR_STEPS.slice();   /* core-v744: the hero picks from every step (the facade is not one of the ?ig highlights) */
    if (_tourIg()) {
      (function () {
        var by = {}, L = [];
        _TOUR_STEPS.forEach(function (S) { by[S.key] = S; });
        _TOUR_IG.forEach(function (x) { var S = by[x[0]]; if (S) { if (x[1]) S.ig = x[1]; L.push(S); } });
        if (L.length >= 3) {
          _log('tour     ?ig page: the highlights only -- ' + L.map(function (S) { return S.key; }).join(', ') + ' (' + L.length + ' of ' + _TOUR_STEPS.length + ' steps, core-v714)');
          _TOUR_STEPS = L;
        }
      })();
    }
    /* core-v754 THE CREATOR PITCH's cut (see _tourPitch): the steps, their
       clips and a time of their own for a step whose clip does not come
       (about the clip's length) */
    var _TOUR_PITCH = [['intro', 'pitch-intro', 10], ['profile', 'pitch-profile', 9], ['colors', 'pitch-colors', 6], ['roof', 'pitch-roof', 6],
                       ['base', 'pitch-base', 6], ['slats', 'pitch-slats', 6], ['assembly', 'pitch-assembly', 7], ['budget', 'pitch-budget', 13], ['login', 'pitch-login', 13], ['dashboard', 'pitch-dashboard', 17], ['editlist', 'pitch-editlist', 20], ['outro', 'pitch-outro', 16]];
    /* the author drawer of the page (IG-profile ig-profile-v2.8) and the
       element in it that scrolls */
    function _tourAuthorPanel() { return global.document.getElementById('Leva-Author-Drawer'); }
    function _tourAuthorScroller() {
      var p = _tourAuthorPanel(), L, i;
      if (!p) return null;
      L = [p, p.querySelector('.side-panel-inner'), p.querySelector('.side-panel-content')];
      for (i = 0; i < L.length; i++) if (L[i] && L[i].scrollHeight > L[i].clientHeight + 4) return L[i];
      return p;
    }
    /* core-v802 THE PITCH'S SAMPLE DASHBOARD (marketing thread, 2026-11-02):
       the real royalty dashboard lives on another domain behind a sign-in,
       so this step draws a copy of it. It stands fixed over the page:
       _tourDrawerUp counts it as a drawer, so the subtitles go to the window's
       foot over it and the card keeps above them (_tourDashFit) -- never
       behind it.
       core-v803 (Marek: ten dashboard bude nas, ktery uz pouzivame, ne?): the
       copy IS that page -- the layout, the colours, the sizes, the sections in
       their order and the labels of leva-invoices /royalties (its English),
       every number invented, a SAMPLE pill in the header; a change there has
       to be made here too. Only the percentages are left out: his name line
       and the commission heading carry a deal, and no two deals are alike.
       The sums agree (earned = paid + ready + hold, orders and payouts add
       up). Divs throughout: no h2, table or input for the theme to restyle. */
    function _tourDashCss() {
      if ($('cf-dash-css')) return;
      var st = global.document.createElement('style');
      st.id = 'cf-dash-css';
      st.textContent =
        "#cf-dash{position:fixed;left:50%;top:12px;transform:translateX(-50%);z-index:100000;box-sizing:border-box;width:min(760px,calc(100vw - 24px));" +
        "overflow:auto;-webkit-overflow-scrolling:touch;border-radius:14px;background:#faf9f7;color:#1a1a1a;" +
        "font:400 15px/1.3 -apple-system,system-ui,sans-serif;letter-spacing:0;text-align:left;text-transform:none;box-shadow:0 0 0 100vmax rgba(20,21,22,0.40),0 14px 40px rgba(0,0,0,0.30);opacity:0;transition:opacity .28s ease;}" +
        '#cf-dash.cf-on{opacity:1;}' +
        '#cf-dash *{box-sizing:border-box;}' +
        '.cf-dh{position:sticky;top:0;z-index:2;display:flex;align-items:center;gap:10px;padding:13px 18px;background:#faf9f7;border-bottom:1px solid rgba(128,128,128,.25);}' +
        '.cf-dh b{font-weight:800;color:#c9342f;}' +
        '.cf-dh span{opacity:.7;}' +
        '.cf-dh i{font-style:normal;font-size:11px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#fff;background:#c9342f;border-radius:20px;padding:2px 10px;}' +
        '.cf-dh u{margin-left:auto;text-decoration:none;font-size:14px;font-weight:700;color:#c9342f;white-space:nowrap;word-spacing:6px;}' +
        '.cf-dm{padding:16px 18px 20px;}' +
        '.cf-dsec{font-size:18px;font-weight:700;margin:6px 0 4px;}' +
        '.cf-dn{font-size:13px;opacity:.7;margin:4px 0 12px;line-height:1.45;}' +
        '.cf-dk3,.cf-dk4{display:grid;gap:10px;}' +
        '.cf-dk3{grid-template-columns:repeat(3,1fr);margin-bottom:18px;}' +
        '.cf-dk4{grid-template-columns:repeat(4,1fr);margin-bottom:14px;}' +
        '.cf-dk3>div,.cf-dk4>div,.cf-dp{border:1px solid rgba(128,128,128,.25);border-radius:12px;padding:14px;}' +
        '.cf-dk3>div,.cf-dk4>div{min-width:0;}' +
        '.cf-dk3 b,.cf-dk4 b{display:block;font-size:20px;font-weight:700;margin-top:4px;}' +
        '@media (max-width:640px){.cf-dk4{grid-template-columns:repeat(2,1fr);}.cf-dk3{gap:6px;}.cf-dk3>div{padding:10px 8px;font-size:13px;}.cf-dk3 b{font-size:15px;}}' +
        '.cf-dk3 small,.cf-dk4 small{display:block;font-size:12px;opacity:.65;margin-top:2px;}' +
        '.cf-dp{margin-bottom:14px;}' +
        '.cf-dl{font-size:11px;text-transform:uppercase;letter-spacing:.06em;opacity:.6;margin-bottom:6px;}' +
        '.cf-du{font-family:ui-monospace,monospace;font-size:13px;word-break:break-all;}' +
        '.cf-drow{display:flex;gap:10px;align-items:center;flex-wrap:wrap;}' +
        '.cf-dbs{flex:0 0 auto;display:inline-block;color:#c9342f;border:1px solid #c9342f;border-radius:10px;padding:6px 12px;font-size:13px;font-weight:700;}' +
        '.cf-db{display:inline-block;background:#c9342f;color:#fff;border-radius:10px;padding:10px 16px;font-size:14px;font-weight:700;}' +
        '.cf-di{padding:11px;border:1px solid rgba(128,128,128,.35);border-radius:10px;margin:0 0 10px;color:#757575;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}' +
        '.cf-ds{font-size:12px;opacity:.6;}' +
        '.cf-dh3{font-size:15px;font-weight:700;margin:22px 0 8px;}' +
        '.cf-dt{font-size:14px;}' +
        '.cf-dtr{display:grid;gap:10px;align-items:center;padding:8px 10px;border-bottom:1px solid rgba(128,128,128,.18);}' +
        '.cf-dth{font-size:11px;text-transform:uppercase;letter-spacing:.04em;}' +
        '.cf-dth>div{opacity:.6;}' +
        '.cf-dtr>div{min-width:0;}' +
        '.cf-dtr b{font-weight:700;}' +
        '.cf-dtr .cf-dr{text-align:right;white-space:nowrap;}' +
        '.cf-dt1 .cf-dtr{grid-template-columns:minmax(0,1fr) auto auto;}' +
        '.cf-dt2 .cf-dtr{grid-template-columns:minmax(0,1fr) 72px 72px;}' +
        '.cf-dt3 .cf-dtr{grid-template-columns:minmax(0,1fr) 60px 80px;}' +
        '.cf-dt4 .cf-dtr{grid-template-columns:96px minmax(0,1fr) auto;}' +
        '.cf-dhr{border-top:1px solid rgba(128,128,128,.25);margin:26px 0 18px;}';
      global.document.head.appendChild(st);
    }
    function _tourDashHtml() {
      var now = new Date(), Y = now.getFullYear(), M = now.getMonth(), LOC = 'en-US';
      var FD = new Intl.DateTimeFormat(LOC, { year: 'numeric', month: 'short', day: 'numeric' }), FM = new Intl.DateTimeFormat(LOC, { year: 'numeric', month: 'short' }), FN = new Intl.DateTimeFormat(LOC, { month: 'short', day: 'numeric' });
      var ago = function (d) { return FD.format(new Date(now.getTime() - d * 86400000)); };
      var pay = function (m) { return FD.format(new Date(Y, M - m, 15)); };
      var usd = function (n) { return '$' + n.toLocaleString(LOC, { minimumFractionDigits: 2, maximumFractionDigits: 2 }); };
      var next = FN.format(new Date(Y, M + 1, 15)), B = 'https://www.theleva.com/?via=yourname';
      var kpi = function (x) { return '<div>' + x[0] + '<b' + (x[3] ? ' style="color:#c9342f"' : '') + '>' + usd(x[1]) + '</b>' + (x[2] ? '<small>' + x[2] + '</small>' : '') + '</div>'; };
      var head = function (L) { return '<div class="cf-dtr cf-dth">' + L.join('') + '</div>'; };
      var link = function (x) {
        return '<div class="cf-dtr"><div><b>' + x[0] + '</b>' + (x[1] ? ' <span class="cf-ds">' + x[1] + '</span>' : '') + '<br><span class="cf-du cf-ds">' + x[2] + '</span></div><div class="cf-dr">' + x[3] + '</div><div class="cf-dr"><span class="cf-dbs">Copy</span></div></div>';
      };
      var order = function (x) {
        return '<div class="cf-dtr"><div>' + x[0] + '<br><span class="cf-ds">' + x[1] + '</span></div><div class="cf-dr">' + usd(x[2]) + '</div><div class="cf-dr"><b>' + usd(x[3]) + '</b></div></div>';
      };
      var month = function (x) {
        return '<div class="cf-dtr"><div>' + FM.format(new Date(Y, M - x[0], 1)) + '</div><div class="cf-dr">' + x[1] + '</div><div class="cf-dr">' + usd(x[2]) + '</div></div>';
      };
      var line = function (x) {
        return '<div class="cf-dtr"><div>' + x[0] + '</div><div>' + x[1] + '</div><div class="cf-dr">' + usd(x[2]) + '</div></div>';
      };
      return '<div class="cf-dh"><b>LEVA</b><span>Your name</span><i>Sample</i><u>EN DE FR</u></div><div class="cf-dm">' +
        '<div data-a="top"><div class="cf-dsec">Your affiliate links</div>' +
        '<div class="cf-dn">Your commission on every order from customers who came through your links, on everything sold on LEVA except collaboration projects (those pay their authors through the royalties below). Products after discounts, without tax and shipping. A commission is approved 30 days after the order is paid and paid out by the 15th of the next month once it reaches $50.00. Refunds are deducted.</div>' +
        '<div class="cf-dk4">' + [['Earned', 320], ['Paid out', 180], ['Ready for payout', 90, 'paid by ' + next], ['In the hold', 50, '30 days after payment']].map(kpi).join('') + '</div></div>' +
        '<div class="cf-dp" data-a="links"><div class="cf-dl">Your referral link</div><div class="cf-drow"><span class="cf-du">' + B + '</span><span class="cf-dbs">Copy</span></div>' +
        '<div class="cf-ds" style="margin-top:8px">Visitors through all your links: 2500 - LEVA accounts made: 40 - last visit ' + ago(1) + '</div></div>' +
        '<div class="cf-dp"><div class="cf-dl">Make a link for a post</div><div class="cf-di">LEVA page the post is about (empty = homepage)</div><div class="cf-di">Name of the post, e.g. Garden swing reel</div><div class="cf-di">Where you post it (optional)</div><span class="cf-db">Create link</span></div>' +
        '<div class="cf-dh3" data-a="list">Your links</div><div class="cf-dt cf-dt1">' + head(['<div>Link</div>', '<div class="cf-dr">Visitors</div>', '<div></div>']) +
        [['Pergola reel', 'Instagram', B + '&amp;l=pergola-reel&amp;utm_source=instagram', 1200], ['Workshop video', 'YouTube', B + '&amp;l=workshop-video&amp;utm_source=youtube', 800], ['Referral link', '', B, 500]].map(link).join('') + '</div>' +
        '<div class="cf-dh3">Orders through your links</div><div class="cf-dt cf-dt2">' + head(['<div>Order</div>', '<div class="cf-dr">Sale</div>', '<div class="cf-dr">Commission</div>']) +
        [['Pergola plans', '#2041 - ' + ago(5) + ' - In the hold until ' + ago(-25), 250, 50], ['Garden shed plans', '#1987 - ' + ago(42) + ' - Approved - paid by ' + next, 450, 90], ['Workshop plans', '#1903 - ' + ago(75) + ' - Paid ' + pay(1), 900, 180]].map(order).join('') + '</div>' +
        '<div class="cf-dhr"></div><div class="cf-dsec" data-a="roy">Royalties from your author projects</div>' +
        '<div class="cf-dk3" style="margin-top:12px">' + [['Earned total', 1200], ['Paid out', 900], ['Balance', 300, '', 1]].map(kpi).join('') + '</div>' +
        '<div class="cf-dh3">Payouts</div><div class="cf-dt cf-dt4">' + [[pay(1), 'PayPal', 400], [pay(2), 'PayPal', 300], [pay(3), 'PayPal', 200]].map(line).join('') + '</div>' +
        '<div class="cf-dh3">Monthly</div><div class="cf-dt cf-dt3">' + head(['<div>Month</div>', '<div class="cf-dr">Sales</div>', '<div class="cf-dr">Royalty</div>']) +
        [[1, 12, 300], [2, 16, 400], [3, 12, 300], [4, 8, 200]].map(month).join('') + '</div>' +
        '<div class="cf-dh3">Recent sales</div><div class="cf-dt cf-dt4">' + [[ago(3), 'Your project plans<br><span class="cf-ds">#2055</span>', 25], [ago(9), 'Your project plans<br><span class="cf-ds">#2038</span>', 25], [ago(14), 'Your project plans<br><span class="cf-ds">#2017</span>', 25]].map(line).join('') + '</div>' +
        '</div>';
    }
    /* core-v803: the page rolls with the voice -- the money at the top, the
       links, the orders, the royalties at the foot */
    var _TOUR_DASH_AT = [[0, 'top'], [0.42, 'links'], [0.58, 'list'], [0.76, 'roy']];
    function _tourDashRoll(sec) {
      var c = $('cf-dash');
      if (!c) return;
      var w = null, k = 0, i;
      try { w = _tourSubWhere(); } catch (eW) {}
      var f = (w && w.frac >= 0) ? w.frac : (Date.now() - (_T.dashT0 || 0)) / (sec * 1000);
      for (i = 0; i < _TOUR_DASH_AT.length; i++) if (f >= _TOUR_DASH_AT[i][0]) k = i;
      if (_T.dashAt === k) return;
      _T.dashAt = k;
      var el = c.querySelector('[data-a="' + _TOUR_DASH_AT[k][1] + '"]'), hd = c.querySelector('.cf-dh');
      if (!el) return;
      var y = Math.max(0, el.getBoundingClientRect().top - c.getBoundingClientRect().top + c.scrollTop - (hd ? hd.offsetHeight : 0) - 12);
      try { c.scrollTo({ top: y, behavior: 'smooth' }); } catch (eS) { c.scrollTop = y; }
      _log('tour     dashboard: rolled to ' + _TOUR_DASH_AT[k][1] + ' at ' + Math.round(f * 100) + ' % of the clip (core-v803)');
    }
    /* the band's room at the window's foot: 7 % of the height (_tourSubPlace)
       and three lines of it at the most; the card takes what is left over it,
       in the middle of that space, and scrolls only on a very low window */
    function _tourDashFit() {
      var c = $('cf-dash');
      if (!c) return;
      var H = global.innerHeight || 0, foot = Math.round(H * 0.07) + 94;
      c.style.maxHeight = Math.max(160, H - foot - 12) + 'px';
      c.style.top = Math.max(12, Math.round((H - foot - c.offsetHeight) / 2)) + 'px';
    }
    function _tourDashShow() {
      if ($('cf-dash')) return;
      _tourDashCss();
      var c = global.document.createElement('div');
      c.id = 'cf-dash';
      c.innerHTML = _tourDashHtml();
      global.document.body.appendChild(c);
      _tourDashFit();
      global.requestAnimationFrame(function () { c.classList.add('cf-on'); });
      _log('tour     dashboard: the sample card up (core-v802)');
    }
    function _tourDashOff() {
      var c = $('cf-dash');
      if (c && c.parentNode) c.parentNode.removeChild(c);
    }
    /* core-v802 EDITING THE LIST: the budget drawer, the framing item the
       budget step opens (_tourBudRow), its chain icon, a sample link typed
       into the field. Only .value is set: no input event, no focus, Sync never
       pressed; the panel takes no taps meanwhile and the field is read-only,
       so nothing can be sent or saved. The step's end empties the field. */
    var _TOUR_EDIT_URL = 'https://www.amazon.com/dp/B0SAMPLE00';
    function _tourEditOff() {
      var L = _T.editLink;
      _T.editLink = null;
      if (!L) return;
      try {
        L.inp.value = '';
        L.inp.readOnly = false;
        L.panel.style.pointerEvents = '';
        if (!L.panel.hidden) L.tog.click();
      } catch (e) {}
    }
    function _tourEditType(cb, run) {
      var dr = global.document.getElementById('Custom-Drawer');
      var tog = dr ? dr.querySelector('[data-link-toggle]') : null;
      var panel = dr ? dr.querySelector('[data-link-panel]') : null;
      var inp = panel ? panel.querySelector('[data-link-input]') : null;
      if (!tog || !inp) { _log('tour     editlist: no link field on the card -- nothing typed'); _tourLater(cb, 800, run); return; }
      inp.readOnly = true;
      panel.style.pointerEvents = 'none';
      _T.editLink = { tog: tog, panel: panel, inp: inp };
      try { tog.click(); } catch (eC) {}
      _tourLater(function () {
        try { inp.blur(); } catch (eB) {}   /* the drawer's openL focuses it: no keyboard on a phone */
        try { panel.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (eS) {}
        var t = _TOUR_EDIT_URL, k = 0;
        var next = function () {
          if (!_T.editLink) return;
          if (k <= t.length) {
            inp.value = t.slice(0, k++);
            try { inp.scrollLeft = inp.scrollWidth; } catch (eL) {}
            _tourLater(next, 60, run);
            return;
          }
          _log('tour     editlist: the sample link typed, Sync not pressed (core-v802)');
          _tourLater(cb, 600, run);
        };
        next();
      }, 500, run);
    }
    function _tourEditShow(cb, run) {
      var sc = _tourBudScroller(), row = _tourBudRow();
      if (!row) { _log('tour     editlist: no item to open'); _tourLater(cb, 600, run); return; }
      var rid = row.getAttribute('data-item-id');
      var tap = function () {
        _log('tour     editlist: item ' + rid + ' opened');
        try { row.click(); _T.budItem = true; } catch (eC) {}
        var tOpen = _perfNow(), retried = false;
        var wait = function () {
          var st = _tourBudCardState(), waited = _perfNow() - tOpen;
          if (!st.open && !retried && waited > 1500) {
            retried = true;
            try { if (typeof global.openItemDrawerFast === 'function') global.openItemDrawerFast(rid); } catch (eF) {}
          }
          if (!st.ready && waited < 8000) { _tourLater(wait, 200, run); return; }
          _log('tour     editlist: the card ' + (st.ready ? 'loaded' : 'still loading') + ' after ' + (waited / 1000).toFixed(1) + ' s (' + st.say + ')');
          _tourLater(function () { _tourEditType(cb, run); }, 500, run);
        };
        _tourLater(wait, 400, run);
      };
      if (!sc) { tap(); return; }
      var sr = sc.getBoundingClientRect(), y0 = sc.scrollTop, y1 = Math.max(0, Math.min(sc.scrollHeight - sc.clientHeight, y0 + (row.getBoundingClientRect().top - sr.top) - sc.clientHeight * 0.4));
      var MS = 1200, t0 = _perfNow();
      var run2 = function () {
        if (run !== _T.run) return;
        var f = Math.min(1, (_perfNow() - t0) / MS), e = f < 0.5 ? 2 * f * f : 1 - Math.pow(-2 * f + 2, 2) / 2;
        sc.scrollTop = Math.round(y0 + (y1 - y0) * e);
        if (f < 1) { global.requestAnimationFrame(run2); return; }
        _tourLater(tap, 300, run);
      };
      global.requestAnimationFrame(run2);
    }
    if (_tourPitch()) {
      /* the author's profile: the drawer slid in, scrolled down to the posts
         and the photos of the original build, shut at the step's end */
      _TOUR_STEPS.push({ key: 'profile', mp3: 'pitch-profile', sec: 9, lead: 1400,
        start: function () {
          _tourStripShut();
          var D = global.LevaAuthorDrawer;
          if (!D || typeof D.open !== 'function' || !_tourAuthorPanel()) { _T.skip = 'no author drawer on the page'; return; }
          try { D.open(); _T.authOpen = true; } catch (eO) { _T.skip = 'the author drawer threw'; }
        },
        cycle: function (cb, run) {
          var p = _tourAuthorPanel(), sc = _tourAuthorScroller();
          if (!p || !p.classList.contains('active') || !sc) { _tourLater(cb, 500, run); return; }
          var y1 = Math.max(0, sc.scrollHeight - sc.clientHeight);
          if (sc.scrollTop < y1 - 2) { try { sc.scrollBy({ top: Math.round(sc.clientHeight * 0.4), behavior: 'smooth' }); } catch (eS) { sc.scrollTop += 200; } }
          _tourLater(cb, 1700, run);
        },
        end: function () { _T.authOpen = false; try { if (global.LevaAuthorDrawer) global.LevaAuthorDrawer.close(); } catch (eC) {} } });
      /* core-v755: after logging in -- the budget drawer opened at its top,
         where the theme (aff-v1.3) draws the list's stats panel (a sample on
         this page); scrolled up only, no pulse inside the drawer (Marek
         2026-10-20: no highlights in the budget) */
      _TOUR_STEPS.push({ key: 'login', mp3: 'pitch-login', sec: 13, lead: 1500,
        start: function (run) {
          _tourStripShut();
          if (typeof global.openBudgetMainDrawer !== 'function') { _T.skip = 'no budget on the page'; return; }
          _tourLater(function () { _tourBudOpen(); }, 300, run);
        },
        cycle: function (cb, run) {
          try { if (typeof global.LEVA_AFF_PANEL === 'function' && !_T.statsOpen) { _T.statsOpen = !!global.LEVA_AFF_PANEL(true); } } catch (eP) {}   /* core-v756 opened, shut at the end */
          var sc = _tourBudScroller();
          try { if (sc && sc.scrollTop > 0) sc.scrollTo({ top: 0, behavior: 'smooth' }); } catch (eS) { if (sc) sc.scrollTop = 0; }
          _tourLater(cb, 1200, run);
        },
        end: function () { try { if (_T.statsOpen && typeof global.LEVA_AFF_PANEL === 'function') global.LEVA_AFF_PANEL(false); } catch (eP) {} _T.statsOpen = false; _tourBudClose(); } });
      /* core-v802: the sample royalty dashboard (above _tourDashCss) */
      _TOUR_STEPS.push({ key: 'dashboard', mp3: 'pitch-dashboard', sec: 17, lead: 900,
        start: function () { _tourStripShut(); _T.dashT0 = Date.now(); _T.dashAt = 0; _tourDashShow(); },
        cycle: function (cb, run) {
          _tourDashFit();
          _tourDashRoll(17);
          _tourLater(cb, 400, run);
        },
        end: function () { _tourDashOff(); } });
      /* core-v802: the list edited -- the drawer held while the voice says it
         is yours to edit, then the item, its chain icon and the typed link */
      _TOUR_STEPS.push({ key: 'editlist', mp3: 'pitch-editlist', sec: 20, lead: 3000,
        start: function (run) {
          _tourStripShut();
          if (typeof global.openBudgetMainDrawer !== 'function') { _T.skip = 'no budget on the page'; return; }
          _T.editRan = false;
          _tourLater(function () { _tourBudOpen(); }, 300, run);
        },
        cycle: function (cb, run) {
          if (_T.editRan || !_T.budOpen) { _tourLater(cb, 600, run); return; }
          _T.editRan = true;
          _tourEditShow(cb, run);
        },
        end: function () { _tourEditOff(); _tourBudItemClose(); _tourBudClose(); } });
      (function () {
        var by = {}, L = [];
        _TOUR_STEPS.forEach(function (S) { by[S.key] = S; });
        _TOUR_PITCH.forEach(function (x) { var S = by[x[0]]; if (S) { S.pitch = x[1]; if (x[2]) S.sec = x[2]; L.push(S); } });
        /* the outro: the author's page again under the closing words (send us
           photos), left open when the tour ends -- never skipped for want of
           a pencil */
        if (by.outro) {
          by.outro.start = function () {
            _tourStripShut();
            try { var p = _tourAuthorPanel(); if (global.LevaAuthorDrawer && p && !p.classList.contains('active')) { global.LevaAuthorDrawer.open(); var sc = _tourAuthorScroller(); if (sc) sc.scrollTop = 0; } } catch (eO) {}
          };
          by.outro.cycle = function (cb, run) { _tourLater(cb, 800, run); };
          by.outro.end = function () {};
        }
        if (L.length >= 3) {
          _log('tour     creator pitch: ' + L.map(function (S) { return S.key; }).join(', ') + ' (' + L.length + ' steps, pitch-* clips, core-v754)');
          _TOUR_STEPS = L;
        }
      })();
    }
    /* core-v734: the homepage hero's cut (?ig&autoplay) -- the types fast,
       nothing that lives in a drawer */
    /* core-v743 (Marek 2026-10-21 18:50: "jeden dum = pudorys a behem animace
       se budou menit: zaklady, typ strechy, barvy, natahovani strechy, slats
       vyplne mezi sloupy. Exploded view - mirne zoom out, pri vraceni zpet
       zase zoom in do stejneho mista ... plynula, bez preskakovani"): one
       house -- no reference designs --, base, roof, colours + the eaves,
       the infill, then the exploded view as its own step */
    var _TOUR_HERO = ['intro', 'base', 'roof', 'facade', 'colors', 'slats', 'hero-explode'];   /* core-v744: + the facade (boards -> render and back) */
    if (_tourAuto()) {
      _TOUR_STEPS.push({ key: 'hero-explode', mp3: 'tour-assembly', sec: 8, lead: 400,
        start: function () { _T.xpDone = false; },
        cycle: function (cb, run) {
          if (_T.xpDone) { _tourLater(cb, 250, run); return; }
          _T.xpDone = true;
          /* then back to the house it began with, step by step: the infill
             off, the eaves in, the colours back -- the loop starts again
             on the very same house */
          _tourXpPulse(run, function () {
            _tourHeroInfillOff(run, function () {
              _tourEavesTo(0, 1300, run, function () {
                _tourHeroColorsBack(run, function () { _tourLater(cb, 300, run); });
              });
            });
          });
        },
        end: function () { _tourBack(); _T.eO0 = null; _T.eF = 0; _T.pwOn = []; _T.colBack = []; } });
      (function () {
        var L = _TOUR_FULL.concat(_TOUR_STEPS.filter(function (S) { return S.key === 'hero-explode'; })).filter(function (S) { return _TOUR_HERO.indexOf(S.key) >= 0; });
        L.sort(function (a, b) { return _TOUR_HERO.indexOf(a.key) - _TOUR_HERO.indexOf(b.key); });
        if (L.length >= 2) {
          _log('tour     ?ig&autoplay: the hero cut -- ' + L.map(function (S) { return S.key; }).join(', ') + ' (core-v734)');
          _TOUR_STEPS = L;
        }
      })();
    }
    /* core-v734: the hero's reel while muted -- no voice, each step as long
       as its show */
    function _tourQuiet() { return _tourAuto() && !_TA.sound; }

    /* ---- the runner ---------------------------------------------------------- */
    function _tourStart() {
      if (_T.on || !_T.btn) return;
      _T.btn.classList.remove('cf-idle');
      var from = (_T.resumeAt > 0 && _T.resumeAt < _TOUR_STEPS.length) ? _T.resumeAt : 0;
      _T.on = true; _T.run++; _T.i = -1; _T.tm = []; _T.t0 = Date.now(); _T.t0p = _perfNow(); _T.mx = null;
      _T.voice = true;
      _T.clipLost = -2;
      /* core-v734: the hero's muted reel runs without the voice */
      var quiet = _tourQuiet();
      try { _heroShiftApply('the reel starts'); } catch (eHs) {}
      if (quiet) { _T.voice = false; _log('tour     the hero reel, muted: no voice, the steps as long as their shows (core-v734)'); }
      try { _T.saved = JSON.stringify(payload()); } catch (eP) { _T.saved = null; }
      _T.undoN = _undoStack.length;
      /* the whole history, not a count: thirty pushes would shift the
         visitor's oldest entries out (core-v636) */
      _T.undo0 = _undoStack.slice();
      /* no short link of a half-changed house while the tour runs */
      _T.held0 = _shareHeld;
      _shareHeld = true;
      try { if (_shareTimer) { global.clearTimeout(_shareTimer); _shareTimer = 0; } } catch (eSt) {}
      try { _T.kept0 = global.localStorage.getItem('leva-plan-import-kept'); } catch (eK) { _T.kept0 = null; }
      _T.dirty = false;
      _T.base = null;
      _T.viz0 = STATE.vizStage | 0;
      _T.view = STATE.view;
      _T.mode = STATE.mode;
      try { _T.pitch0 = !!(_pitchSl && _pitchSl.el && _pitchSl.el.offsetParent !== null); } catch (ePv) { _T.pitch0 = false; }
      try {
        var st = _stage(), c = st && st.persCam, ctl = _tourControls();
        _T.cam = (c && ctl) ? { p: c.position.clone(), t: ctl.target.clone(), mn: ctl.minDistance, mx: ctl.maxDistance, n: c.near, f: c.far } : null;
      } catch (eC) { _T.cam = null; }
      /* from a quiet start: no strip, no drawer, the bar folded */
      try { if (_wiz && _wiz.isOpen && _wiz.isOpen()) _wiz.close(); } catch (eW) {}
      try { _plibClose('the tour started'); } catch (eL) {}
      try { _cmdMini(true, 'the tour started'); } catch (eM) {}
      /* the voice is unlocked inside the tap, on ONE audio element that every
         step reuses -- a phone lets the page go on playing it after that */
      if (!quiet) try {
        if (!_T.audio) {
          _T.audio = new global.Audio(); _T.audio.preload = 'auto';
          _T.audio.addEventListener('timeupdate', function () { _tourStreamTick(); });
        }
        _T.segWatch = null; _T.clipPending = false; _T.ahead = false;
        /* the page came back (core-v697): the voice goes on, only the shows start again */
        if (_T.keepAudio && _TS && _T.audio) throw { keep: true };
        /* one stream for the whole tour where the browser has it (core-v695) */
        _T.segs = []; _T.segNow = null;
        _TS = _tourStreamOk() ? _tourStreamOpen(_T.audio) : null;
        if (_TS) _log('tour     one stream for the whole tour (MediaSource, audio/mpeg) -- the player does not stop between the clips');
        else _T.audio.src = _tourUrl(_tourSrcs(_TOUR_STEPS[from])[0]);
        /* how long the first sound took, and whether its clip was there
           ahead (core-v681) */
        var tTap = _perfNow(), runTap = _T.run, aheadTap = _TS ? !!_TOUR_PRE_B[_tourSrcs(_TOUR_STEPS[from])[0]] : String(_T.audio.src).indexOf('blob:') === 0;
        var firstSound = function () {
          _T.audio.removeEventListener('playing', firstSound);
          if (runTap !== _T.run) return;
          _log('tour     first sound ' + ((_perfNow() - tTap) / 1000).toFixed(1) + ' s after play (' + (aheadTap ? 'the clip was fetched ahead' : 'the clip came from the network') + ')');
        };
        _T.audio.addEventListener('playing', firstSound);
        var pr = _T.audio.play();
        if (pr && pr.catch) pr.catch(function (eA) { _tourPlayFail(eA, _T.run); });
      } catch (eA2) {
        if (eA2 && eA2.keep) _log('tour     the voice goes on -- the shows start again at step ' + (from + 1));
        else _tourNoVoice('could not start (' + ((eA2 && eA2.message) || eA2) + ')');
      }
      _T.btn.innerHTML = _TOUR_SVG.pause;
      _T.btn.classList.add('cf-on');
      _tourMedia();
      _tourTickLoop();
      _log('tour     started -- the project kept to be put back');
      if (from) _log('tour     resumed at step ' + (from + 1) + ' ' + _TOUR_STEPS[from].key);
      _tourStep(from, _T.run);
    }
    function _tourNoVoice(why) {
      if (!_T.voice) return;
      _T.voice = false;
      _log('tour     the voice ' + why + ' -- the steps run on their own time, with the bubbles');
      try { if (_T.audio) _T.audio.pause(); } catch (e) {}
      if (_T.on && _T.stepVoiceLost) _T.stepVoiceLost();
    }
    /* ONE CLIP THAT DOES NOT COME silences only its own step (core-v643):
       a missing or broken MP3 lets that step run on its own time and the
       next step tries its voice again; only a refusal to play at all (the
       browser's autoplay rule) turns the voice off for the rest */
    function _tourClipLost(why, run) {
      if (run !== _T.run || !_T.voice) return;
      if (_T.clipLost === _T.i) return;
      _T.clipLost = _T.i;
      _log('tour     the voice ' + why + ' -- this step runs on its own time, the next one tries its voice again');
      if (_T.on && _T.stepVoiceLost) _T.stepVoiceLost();
    }
    function _tourPlayFail(eA, run) {
      var nm = String((eA && eA.name) || eA || '');
      /* the clip was replaced before it started (a skipped step): not a
         failure; a clip that could not load is the element's onerror's to
         answer -- it tries the next name (core-v651) */
      if (/Abort|NotSupported/i.test(nm)) return;
      if (/NotAllowed/i.test(nm)) _tourNoVoice('refused (' + nm + ')');
      else _tourClipLost('did not play (' + nm + ')', run);
    }
    /* core-v729: a step this product leaves out of every tour (__PROD._tourOut,
       TAURUS: the plan read from a picture -- no upload there) */
    function _tourOut(S) {
      try { return !!(S && __PROD && __PROD._tourOut && __PROD._tourOut.indexOf(S.key) >= 0); } catch (e) { return false; }
    }
    /* core-v768: the rows the product step walks (__PROD._tourRow, a key or a list of keys) */
    function _tourRows() {
      var R = [];
      try { R = [].concat((__PROD && __PROD._tourRow) || []); } catch (e) { R = []; }
      return R.filter(function (k) { return !!k; }).map(String);
    }
    /* a row's icon on the strip: its own key, or the auto icon made from the row (auto:<step>:<row key>) */
    function _tourRowKey(k) {
      try {
        if (_sxIconBy(k)) return k;
        for (var i = 0; i < (_SX.icons || []).length; i++) { var I = _SX.icons[i]; if (I && I.rowKey === k) return I.key; }
      } catch (e) {}
      return k;
    }
    function _tourStep(i, run) {
      if (run !== _T.run) return;
      if (i >= _TOUR_STEPS.length) { _tourStop('the end of the tour'); _tourAutoAgain(); return; }
      /* a page in the background: the voice of the steps left goes ahead (core-v697) */
      if (_TS && _T.voice && !_T.ahead && _tourHidden()) { _T.i = -1; _T.nextI = i; _tourAheadGo('the page is in the background at step ' + (i + 1)); return; }
      var S = _TOUR_STEPS[i];
      _T.i = i;
      _T.nextI = i;
      var spoken = false, t0 = Date.now();
      _T.pass = 0;
      var done = function () { return spoken; };
      _log('tour     step ' + (i + 1) + '/' + _TOUR_STEPS.length + ' ' + S.key + (_T.voice ? (' -- ' + _tourSrcs(S)[0]) : (' -- ' + S.sec + ' s, no voice')));
      /* A STEP THIS PRODUCT HAS NOTHING FOR IS PASSED BY (core-v636): its
         has() says no, or its start finds no icon -- the voice is not played */
      _T.skip = '';
      if (S.has) { var okH = false; try { okH = !!S.has(); } catch (eH) { okH = false; } if (!okH) _T.skip = 'nothing for it on this product'; }
      /* core-v726: a product leaves steps out of its reel (time) */
      try { if (!_T.skip && _tourIg() && __PROD && __PROD._reelSkip && __PROD._reelSkip.indexOf(S.key) >= 0) _T.skip = 'left out of this product\'s reel'; } catch (eRs) {}
      /* core-v729 (TAURUS, Marek: u taurus neni mozne nahravat vlastni floorplan
         -- upravit UI i voice guider): steps a product leaves out of its tour
         altogether, the reel and the full tour alike (__PROD._tourOut) */
      if (!_T.skip && _tourOut(S)) _T.skip = 'not on this product';
      try { if (!_T.skip && _tourIg() && S.key === 'assembly' && !(__PROD && __PROD._reelAsm)) _T.skip = 'not on this product\'s reel (opt-in __PROD._reelAsm, core-v748)'; } catch (eRa) {}
      if (!_T.skip) { try { S.start(run); } catch (eS) { _log('tour     ' + S.key + ' start THREW ' + ((eS && eS.message) || eS)); } _tourFresh(); }
      if (_T.skip) {
        _log('tour     ' + S.key + ' skipped -- ' + _T.skip);
        _tourHl(null); _tourBub(null); _tourPulse([]);
        try { S.end(false); } catch (eSk) {}
        _T.i = -1;
        _T.nextI = i + 1;
        _tourLater(function () { _tourStep(i + 1, run); }, 40, run);
        return;
      }
      /* the steps shown, for previous; the step's label in the notification (core-v694) */
      _T.played = _T.played || [];
      if (_T.played.indexOf(i) < 0) _T.played.push(i);
      try { _tourMediaMeta(S.tip ? _t(S.tip[0], S.tip[1]) : ''); } catch (eMm) {}
      var tgt = null;
      try { tgt = S.target ? S.target() : null; } catch (eT) { tgt = null; }
      if (S.tip && tgt) _tourBub(tgt, _t(S.tip[0], S.tip[1]));
      /* core-v734: the hero's muted reel -- a step ends once its show has run
         (the intro: 1.1 s a reference design) */
      var heroQ = _tourQuiet() && !_T.voice, cycled = 0;
      var secS = heroQ ? (S.key === 'intro' ? (_T.refN ? 0.6 + 1.1 * _T.refN : 0.4) : 0.4) : S.sec;
      var own = function () { _tourLater(function () { spoken = true; }, secS * 1000, run); };
      _T.stepVoiceLost = function () { own(); };
      if (_T.voice && _T.audio && _TS) {
        /* ONE STREAM (core-v695): the clip appended, its end watched */
        _tourPreStep(_TOUR_STEPS[i + 1], 'the next step');
        _tourPreStep(_TOUR_STEPS[i + 2], 'the step after');
        var have = _tourSegOf(i);
        if (have && have.to != null) {
          /* its voice plays already -- the page came back in this step (core-v697) */
          _log('tour     ' + S.key + ' voice ' + have.name + ' already in the stream (until ' + have.to.toFixed(1) + ' s) -- the show catches up with it');
          _T.segWatch = { to: have.to, fn: function () { if (run === _T.run) { spoken = true; _log('tour     ' + S.key + ' voice ' + have.name + ' ended after ' + ((Date.now() - t0) / 1000).toFixed(1) + ' s'); } } };
          _tourStreamTick();
        } else {
          var LS = _tourSrcs(S);
          _T.clipPending = true;
          _tourClipBytes(LS).then(function (c) {
            if (run !== _T.run || _T.i !== i) return null;
            /* the clip and a short silence after it, so the play head runs
               past the clip's end (and the player keeps sounding until the
               next) -- kept as this step's part of the stream (core-v697) */
            var g = _tourSegAdd(i, S, c);
            return g.p.then(function (seg) {
              _T.clipPending = false;
              if (run !== _T.run) return;
              _log('tour     ' + S.key + ' voice ' + c.name + ' in the stream at ' + seg.from.toFixed(1) + ' s, ' + (seg.to - seg.from).toFixed(1) + ' s long' + (c.ahead ? ' (fetched ahead)' : ' (from the network)'));
              try { if (_T.audio.paused) { var pr2 = _T.audio.play(); if (pr2 && pr2.catch) pr2.catch(function (eA) { if (run === _T.run) _tourPlayFail(eA, run); }); } } catch (eP2) {}
              _T.segWatch = { to: seg.to, fn: function () { if (run === _T.run) { spoken = true; _log('tour     ' + S.key + ' voice ' + c.name + ' ended after ' + ((Date.now() - t0) / 1000).toFixed(1) + ' s'); } } };
              _tourStreamTick();
            });
          }).catch(function (e) { _T.clipPending = false; if (run === _T.run && _T.i === i) _tourClipLost('no clip for ' + S.key + ' (' + ((e && e.message) || e) + ')', run); });
        }
        _tourLater(function () { if (!spoken) { _log('tour     ' + S.key + ' voice still going after ' + (S.sec + 20) + ' s -- moving on'); spoken = true; } }, (S.sec + 20) * 1000, run);
      } else if (_T.voice && _T.audio) {
        /* the next two steps' clips fetched while this one plays (core-v681) */
        _tourPreStep(_TOUR_STEPS[i + 1], 'the next step');
        _tourPreStep(_TOUR_STEPS[i + 2], 'the step after');
        var a = _T.audio, L = _tourSrcs(S), li = 0, src = _tourUrl(L[0]);
        var playIt = function () {
          try { var pr = a.play(); if (pr && pr.catch) pr.catch(function (eA) { if (run === _T.run) _tourPlayFail(eA, run); }); } catch (eP) { _tourClipLost('could not play', run); }
        };
        a.onended = function () { if (run === _T.run) { spoken = true; _log('tour     ' + S.key + ' voice ' + L[li] + ' ended after ' + ((Date.now() - t0) / 1000).toFixed(1) + ' s'); } };
        a.onerror = function () {
          if (run !== _T.run || _T.i !== i) return;
          if (li + 1 < L.length) {
            li++;
            _log('tour     ' + S.key + ': ' + L[li - 1] + ' not there -- ' + L[li]);
            a.src = _tourUrl(L[li]);
            playIt();
            return;
          }
          _tourClipLost('MP3 failed for ' + L[li], run);
        };
        if (String(a.src).indexOf(src) < 0) {
          a.src = src;
          playIt();
        }
        /* a voice that never ends (a stalled download) does not hold the tour */
        _tourLater(function () { if (!spoken) { _log('tour     ' + S.key + ' voice still going after ' + (S.sec + 20) + ' s -- moving on'); spoken = true; } }, (S.sec + 20) * 1000, run);
      } else own();
      /* the step's end, once: from its show, or from the watch below when
         the page is in the background (core-v691) */
      var finished = false;
      var finish = function () {
        if (finished || run !== _T.run) return;
        finished = true;
        _tourHl(null); _tourBub(null); _tourPulse([]);
        try { S.end(false); } catch (eE) { _log('tour     ' + S.key + ' end THREW ' + ((eE && eE.message) || eE)); }
        _T.i = -1;
        _T.nextI = i + 1;
        _log('tour     ' + S.key + ' done in ' + ((Date.now() - t0) / 1000).toFixed(1) + ' s' + (_tourHidden() ? ' (the page in the background)' : ''));
        _tourLater(function () { _tourStep(i + 1, run); }, _tourIg() ? 250 : 600, run);   /* core-v720: the reel's cuts */
      };
      var loop = function () {
        if (run !== _T.run || finished) return;
        /* core-v738: the hero's intro ends once its show has run -- every
           design and the exploded view (live runner: a slow device cut it
           after 2 or 3 designs, the exploded view never came) -- 20 s at most */
        var showOn = heroQ && S.key === 'intro' && !_T.introShown && (Date.now() - t0) < 20000;
        if (spoken && (!heroQ || cycled) && !showOn) { finish(); return; }
        cycled++;
        /* a page in the background draws nothing: no show, the voice leads */
        if (_tourHidden()) { _tourLater(loop, 500, run); return; }
        try { S.cycle(loop, run, done); } catch (eCy) { _log('tour     ' + S.key + ' cycle THREW ' + ((eCy && eCy.message) || eCy)); _tourLater(loop, 600, run); }
      };
      _tourLater(loop, _tourIg() ? Math.min(S.lead || 600, 1200) : (S.lead || 600), run);   /* core-v720: the reel's shorter leads */
      /* a show that waits on frames (the intro's turn) does not call back
         while the page is hidden -- the voice's end moves the tour on */
      var watch = function () {
        if (run !== _T.run || finished) return;
        if (spoken && _tourHidden()) { finish(); return; }
        _tourLater(watch, 700, run);
      };
      _tourLater(watch, 700, run);
    }
    /* a pause is a stop that remembers the step it broke into: the project
       goes back as it was (the visitor may change it meanwhile), and the next
       play starts that step again from its beginning */
    function _tourPause(why) {
      if (!_T.on) return;
      var at = (_T.nextI > 0) ? _T.nextI : 0;
      _tourStop(why, true);
      _T.resumeAt = at;
      _log('tour     paused at step ' + (at + 1) + ' ' + ((_TOUR_STEPS[at] || {}).key || '') + ' -- play goes on from there');
    }
    function _tourStop(why, paused) {
      if (!_T.on) return;
      if (_T.btn && !paused) _T.btn.classList.remove('cf-ig-gone');   /* core-v763 */
      if (_tourIg() && !paused) _log('tour     ?ig reel: ' + ((Date.now() - (_T.t0 || Date.now())) / 1000).toFixed(1) + ' s from play to ' + (why || 'the stop') + ' (core-v720, the aim: under 60 s)');
      var keepA = !!(_T.keepAudio && paused);
      if (!keepA) { try { if (global.navigator.mediaSession) global.navigator.mediaSession.playbackState = 'paused'; } catch (eMp) {} }
      _T.ahead = false; _T.probe = false;
      if (!paused) _T.resumeAt = 0;
      var S = _TOUR_STEPS[_T.i];
      _T.on = false; _T.run++;
      _T.tm.forEach(function (id) { global.clearTimeout(id); });
      _T.tm = [];
      if (_T.audio) { try { _T.audio.onended = null; _T.audio.onerror = null; if (!keepA) _T.audio.pause(); } catch (eA) {} }
      _tourHl(null); _tourPulse([]);
      if (_T.bub) _T.bub.classList.remove('cf-on');
      try { if (S && S.end) S.end(true); } catch (eE) { _log('tour     ' + (S && S.key) + ' end THREW ' + ((eE && eE.message) || eE)); }
      _tourField('');
      if (_cmd.el) _cmd.el.classList.remove('cf-tour-type');
      try { _plibClose('the tour ended'); } catch (eL) {}
      /* a plan the tour took from the library and that is still on its way
         is called off, so it cannot land on the visitor's own project after
         it has been put back */
      _tourCancelPlan();
      /* what the tour opened over the page (core-v639) */
      _tourArClose();
      _tourBudClose();
      _tourAiClose();
      _tourDashOff();
      /* the rail's own state (core-v637): the walk, the cut, the stage, the
         figure's button, the units with the budget's selector */
      _tourAsmOff();
      _tourCutOff();
      _tourVizBack();
      try { var cR = _tourControls(); if (cR && _T.rot) { cR.autoRotate = !!_T.rot.on; if (_T.rot.sp != null) cR.autoRotateSpeed = _T.rot.sp; } } catch (eRo) {}
      _T.rot = null;
      if (_T.treesOn) { _T.treesOn = false; try { toggleTrees('a'); } catch (eTr) {} }
      try { if (_T.saved) { var p0 = JSON.parse(_T.saved); if (!!STATE.isMetric !== !!p0.isMetric) toggleUnits(); if (!!STATE.bodyVisible !== !!p0.bodyVisible) toggleBody(); } } catch (eUb) {}
      try { _nearBinHide('the tour ended'); } catch (eNb) {}
      try { armOpening(null); if (opEdit()) opEdit(false); } catch (eOp) {}
      _tourHoleOff();
      if (_T.chipShown) { _T.chipShown = false; if (!_pdfShots.length) _shotShow(false, 'the tour ended, as it was at play'); }
      /* what the payload does not carry (core-v636) */
      STATE.doorTool = false;
      STATE.cutsOn = false;
      try { if (fitEdit()) fitEdit(false, 'the tour ended'); } catch (eFe) {}
      _fitRoom = null;
      try { var pkX = global.document.querySelector('#cfg-ui-picker [data-pk-close]'); if (pkX) pkX.click(); } catch (ePk) {}
      try { var kNow = global.localStorage.getItem('leva-plan-import-kept'); if (kNow !== _T.kept0) { if (_T.kept0 == null) global.localStorage.removeItem('leva-plan-import-kept'); else global.localStorage.setItem('leva-plan-import-kept', _T.kept0); } } catch (eKp) {}
      if (_T.mir0 != null) { try { _plib.mir = JSON.parse(_T.mir0); } catch (eMr) {} _T.mir0 = null; }
      if (!_T.pitch0) { try { showPitch(false); } catch (eSp2) {} }
      try { if (_wiz && _wiz.isOpen && _wiz.isOpen()) _wiz.close(); } catch (eW) {}
      /* the project as it was before play -- every key the payload carries */
      /* the short link may be written again -- the project about to be saved
         is the visitor's, so it matches what was written before play */
      if (_T.held0 != null) { _shareHeld = _T.held0; _T.held0 = null; }
      if (_T.saved) {
        try {
          _applyPayload(JSON.parse(_T.saved), 'the tour');
          savePref();
        } catch (eP) { _log('tour     putting the project back THREW ' + ((eP && eP.message) || eP)); }
      }
      _T.dirty = false;
      _T.base = null;
      /* core-v648: a light drag the tour left on is ended; the build below covers the plan's */
      _tourLightOff();
      _T.stale3d = false;
      try { _buildScenery(); } catch (eSc) {}
      try { if (_T.view !== STATE.view) setView(_T.view); } catch (eV) {}
      /* the strips leave the stage mode they worked in; the page goes back
         to the one it had (the assembly counter reads by it) */
      if (_T.mode != null) STATE.mode = _T.mode;
      try { rebuild(); drawPlan(); railCounters(); } catch (eR) {}
      try {
        var st = _stage(), c = st && st.persCam, ctl = _tourControls();
        if (_T.cam && c && ctl && !_tourAuto()) {   /* core-v736: not in the hero */
          c.position.copy(_T.cam.p); ctl.target.copy(_T.cam.t);
          ctl.minDistance = _T.cam.mn; ctl.maxDistance = _T.cam.mx;
          c.near = _T.cam.n; c.far = _T.cam.f; c.updateProjectionMatrix();
          ctl.update();
        }
        if (st && st.wake) st.wake(1500);
      } catch (eC) {}
      /* THE TOUR IS NOT AN EDIT: whatever it pushed on the Undo history (the
         strips and the plan push their own states) is taken off again, now
         and once more after the strips' own late pushes */
      var trim = function () {
        try {
          if (_T.undo0) { _undoStack.length = 0; Array.prototype.push.apply(_undoStack, _T.undo0); }
          else if (_T.undoN != null && _undoStack.length > _T.undoN) _undoStack.length = _T.undoN;
          if (_undoBtn) _undoBtn.refresh();
        } catch (eU) {}
      };
      trim(); global.setTimeout(trim, 80); global.setTimeout(trim, 600);
      try { _cmdMini(true, 'the tour ended'); } catch (eM) {}
      if (_T.btn) { _T.btn.innerHTML = _TOUR_SVG.play; _T.btn.classList.remove('cf-on'); }
      try { _cmdSync(); } catch (eSy) {}
      _log('tour     stopped -- ' + (why || '') + (S ? (' (at ' + S.key + ')') : '') + '; project, view and camera put back');
    }
    function tourState() {
      var a = _T.audio, G = (_T.segs || []).map(function (g) { return g.key + '@' + (g.from == null ? '?' : g.from.toFixed(1)) + '-' + (g.to == null ? '?' : g.to.toFixed(1)); });
      return { on: _T.on, step: _T.i, key: (_TOUR_STEPS[_T.i] || {}).key || '', voice: _T.voice, steps: _TOUR_STEPS.length, keys: _TOUR_STEPS.map(function (x) { return x.key; }), mounted: !!_T.btn,
        ahead: !!_T.ahead, vt: a ? Math.round((a.currentTime || 0) * 100) / 100 : null, paused: a ? !!a.paused : null, buf: (_TS && _TS.sb) ? Math.round(_tourStreamEnd(_TS) * 100) / 100 : null, segs: G };
    }

    function _cmdMini(on, why) {
      if (!_cmd.el) return;
      on = !!on;
      if (!on && _cmdPlainOn()) { if (!_cmd.mini) { on = true; } else { _log('cmdbar   stays folded -- the plain bar has no field (' + (why || '') + ')'); return; } }
      if (on === _cmd.mini) return;
      _cmd.mini = on;
      /* folded, the field's suggestion is offered afresh next time (core-v552) */
      if (on) _cmd.hintOff = false;
      _cmd.el.classList.toggle('cf-cmd-mini', on);
      _log('cmdbar   ' + (on ? 'folded to the plan icon and the pencil' : 'opened out') + ' -- ' + (why || ''));
      _cmdSync();
    }
    /* THE MICROPHONE: the browser's own speech, in the page's language, into
       the field. A browser without it shows no microphone. */
    function _cmdMicInit() {
      var SR = global.SpeechRecognition || global.webkitSpeechRecognition, mic = _cmd.mic;
      if (!SR) { mic.style.display = 'none'; _log('cmdbar   voice: this browser has no speech recognition -- the microphone is hidden'); return; }
      mic.addEventListener('click', function (e) {
        e.preventDefault(); e.stopPropagation();
        if (_cmd.listening && _cmd.rec) { try { _cmd.rec.stop(); } catch (eS) {} return; }
        var rec;
        try { rec = new SR(); } catch (eN) { _log('cmdbar   voice: could not start (' + (eN && eN.message) + ')'); return; }
        var lang = '';
        try { lang = global.document.documentElement.lang || global.navigator.language || 'en'; } catch (eL) { lang = 'en'; }
        rec.lang = lang;
        rec.interimResults = true;
        rec.continuous = false;
        var base = _cmd.inp.value ? (_cmd.inp.value.replace(/\s+$/, '') + ' ') : '';
        rec.onresult = function (ev) {
          var t = '', i;
          for (i = 0; i < ev.results.length; i++) t += ev.results[i][0].transcript;
          _cmd.inp.value = base + t;
          _cmdSendState();
        };
        rec.onerror = function (ev) { _log('cmdbar   voice error: ' + (ev && ev.error)); };
        rec.onend = function () { _cmd.listening = false; mic.classList.remove('cf-on'); _log('cmdbar   voice: stopped, ' + _cmd.inp.value.length + ' character(s) in the field'); };
        try { rec.start(); } catch (eSt) { _log('cmdbar   voice: start threw ' + (eSt && eSt.message)); return; }
        _cmd.rec = rec; _cmd.listening = true;
        mic.classList.add('cf-on');
        _log('cmdbar   voice: listening in ' + lang);
      });
    }

    var _plusSel = null, _plusAuto = {}, _plusAutoLast = '', _plusDiag = '', _plusThrew = '';
    function _plusAutoSay(list, why) {
      var key = list + '|' + (why || '');
      if (key === _plusAutoLast) return;
      _plusAutoLast = key;
      _log('fitout plus: ' + (list ? ('zoomed in -- up by itself in room(s) ' + list) : (why ? ('none -- ' + why) : 'no room big enough on screen for its own plus')) +
           (_plusDiag && !why ? (' | ' + _plusDiag) : ''));
    }
    function _roomHasPt(R, x, z) {
      for (var j = 0; R && j < (R.rects || []).length; j++) {
        var q = R.rects[j];
        if (x >= q.x0 && x <= q.x1 && z >= q.z0 && z <= q.z1) return true;
      }
      return false;
    }
    function _fitPlusTap(wx, wz) {
      if (STATE.view !== '2d' || !_planWanted() || !_fitPlanOn()) return 'plus: not the plan step';
      if (STATE.doorTool || STATE.cutsOn || _fitEdit) return 'plus: a tool is on';
      if (!_fitHasCatalog()) return 'plus: no fitout catalogue';
      var PL = _P();
      if (!PL || !PL.rooms) return 'plus: no rooms';
      var rooms = PL.rooms() || [], hit = -1, was = -1, i;
      for (i = 0; i < rooms.length; i++) {
        if (hit < 0 && _roomHasPt(rooms[i], wx, wz)) hit = i;
        if (was < 0 && _plusSel && _roomHasPt(rooms[i], _plusSel.x, _plusSel.z)) was = i;
      }
      /* a closet or a room the product furnishes itself has no plus: the tap
         puts away the one that was up and shows nothing */
      var skip = false;
      if (hit >= 0) {
        if (rooms[hit].closet) skip = true;
        if (typeof FIT_PLUS_SKIP === 'function') { try { skip = skip || !!FIT_PLUS_SKIP(rooms[hit], hit); } catch (eSk) {} }
      }
      if (hit >= 0 && !skip && _plusAuto && _plusAuto[hit]) {
        var saidA = 'plus: tap in room ' + hit + ' -- zoomed in, its plus is already up';
        _log('fitout ' + saidA);
        return saidA;
      }
      _plusSel = (hit < 0 || hit === was || skip) ? null : { x: wx, z: wz };
      var said = 'plus: tap ' + (hit < 0 ? 'outside the rooms' : ('in room ' + hit + (skip ? ' (closet or furnished, no plus)' : ''))) +
                 (_plusSel ? ' -- shown' : ' -- put away');
      _log('fitout ' + said);
      _fitPlusPlace();
      return said;
    }
    function _fitPlusPlace() {
      var root = $('cf-root'); if (!root) return;
      var i, j;
      var box = $('cf-fit-plus-host');
      if (!box) {
        /* its own layer under cf-root: the icon host is hidden whenever the
           cut tool is off, and the plus must not go with it */
        box = global.document.createElement('div');
        box.id = 'cf-fit-plus-host';
        box.style.cssText = 'position:absolute;left:0;top:0;right:0;bottom:0;pointer-events:none;z-index:7;';
        root.appendChild(box);
      }
      /* core-v76: no catalogue, no plus. A product that does not build fitouts
         had the button drawn in every room and nothing behind it (Marek). */
      var show = _fitHasCatalog() && _planWanted() && _fitPlanOn() && !STATE.doorTool && !STATE.cutsOn;
      if (!_fitPlanOn()) _plusSel = null;
      var F = _FL(), PL = _P(), st = _stage();
      var OV = F && F.plan && F.plan.overlay, m = (show && OV && OV.map) ? OV.map(st) : null;
      var rooms = (show && PL && PL.rooms) ? (PL.rooms() || []) : [];
      if (!m || !rooms.length) {
        box.style.display = 'none'; _plusAuto = {};
        /* WHY THERE IS NO PLUS, in words (core-v546, Marek: porad se
           neobjevuji plus pri zoom in) */
        var whyNo = !_fitHasCatalog() ? 'this product has no fitout catalogue' : (!_planWanted() ? 'the plan is not up' : (!_fitPlanOn() ? 'not the Floorplan step nor the bar\'s plan' :
                    (STATE.doorTool ? 'the door tool is on' : (STATE.cutsOn ? 'the scissors are on' : (!m ? 'no projection of the plan' : 'no rooms')))));
        _plusAutoSay('', whyNo);
        return;
      }
      /* THE PLUS COMES BY ITSELF ONCE A ROOM IS ZOOMED INTO (core-v540, Marek:
         plus bez klepnuti, ale az pri zoom in na danou mistnost, kde uz nebudou
         tlacitka pres sebe a nebude tam velky sum). A room whose biggest box
         shows on screen at least PLUS_AUTO of the plan's shorter side (and
         never under 220 px) both ways has its plus, in the middle of the part
         of it on screen, a little under the name. A tap still brings one up
         in a room too small for that. */
      /* SOONER, AND NEVER ON TOP OF ANYTHING (core-v549, Marek: plus se muze
         objevovat drive, jen jsem nechtel aby byly pres sebe na malem
         floorplanu -- objevuji se zbytecne pozde). Not a share of the screen
         any more: a room has its plus as soon as the plus fits in it beside
         its name -- under the name when the room is tall enough, to its right
         when it is wide enough -- and stays clear of every other plus, of the
         bar at the foot and of the area figure at the top. Bigger rooms are
         served first, so where two would crowd, the bigger room keeps it. */
      var hw = root.clientWidth || 0, hh = root.clientHeight || 0;
      /* AND A LITTLE LATER (core-v550, Marek: plus muzou byt trosku pri mirne
         vetsim zoom in, ale jen trosku): the room's shorter side on screen is
         at least PLUS_MIN as well -- the whole house on a tablet shows none,
         a first step of zoom shows them in the main rooms */
      var PLUS_R = 17, PLUS_GAP = 46, NAME_DY = 38, NAME_DX = 80, PLUS_MIN = 180;
      var barTop = hh;
      try { if (_cmd && _cmd.el && !_cmd.el.classList.contains('cf-cmd-off')) barTop = hh - _CMD_FOOT - (_cmd.el.offsetHeight || 70) - 10; } catch (eBt) {}
      var AUTO = {}, nAuto = 0, autoList = [], diag = [], cand = [], taken = [];
      var pinsA = STATE.roomNames || [];
      for (i = 0; i < rooms.length; i++) {
        var Ra = rooms[i], rca = null, bAa = -1, ja, nmA = '';
        try { nmA = PL.roomName ? String(PL.roomName(Ra, i) || '') : ''; } catch (eN) { nmA = ''; }
        nmA = (nmA || ('room ' + i)).slice(0, 18);
        if (Ra.closet) { diag.push(nmA + ' closet'); continue; }
        /* a room that never has a plus is not counted as one that has it up
           (core-v546): the list said "up" while its button stayed hidden */
        var skA = false;
        if (typeof FIT_PLUS_SKIP === 'function') { try { skA = !!FIT_PLUS_SKIP(Ra, i); } catch (eSA) {} }
        if (skA) { diag.push(nmA + ' no fitouts'); continue; }
        for (ja = 0; ja < (Ra.rects || []).length; ja++) {
          var ra = Ra.rects[ja], aa = (ra.x1 - ra.x0) * (ra.z1 - ra.z0);
          if (aa > bAa) { bAa = aa; rca = ra; }
        }
        if (!rca) continue;
        var vx0 = Math.max(0, m.ox + rca.x0 * m.ppm), vx1 = Math.min(hw || 1e9, m.ox + rca.x1 * m.ppm);
        var vy0 = Math.max(60, m.oy + rca.z0 * m.ppm), vy1 = Math.min(barTop, m.oy + rca.z1 * m.ppm);
        /* where the name is: its pin when one stands in the room, else the middle */
        var lx = (vx0 + vx1) / 2, ly = (vy0 + vy1) / 2, pq;
        for (pq = 0; pq < pinsA.length; pq++) {
          var pnA = pinsA[pq];
          if (pnA && pnA.x >= rca.x0 && pnA.x <= rca.x1 && pnA.z >= rca.z0 && pnA.z <= rca.z1) { lx = m.ox + pnA.x * m.ppm; ly = m.oy + pnA.z * m.ppm; break; }
        }
        cand.push({ i: i, nm: nmA, vx0: vx0, vx1: vx1, vy0: vy0, vy1: vy1, lx: lx, ly: ly, a: Math.max(0, vx1 - vx0) * Math.max(0, vy1 - vy0) });
      }
      cand.sort(function (a, b) { return b.a - a.a; });
      for (i = 0; i < cand.length; i++) {
        var cq = cand[i], spots = [], sp, k2, free, pick = null, vw = cq.vx1 - cq.vx0, vh = cq.vy1 - cq.vy0;
        if (Math.min(vw, vh) < PLUS_MIN) { diag.push(cq.nm + ' ' + Math.max(0, Math.round(vw)) + 'x' + Math.max(0, Math.round(vh)) + ' small'); continue; }
        /* under the name, then right of it */
        spots.push({ x: Math.max(cq.vx0 + PLUS_R + 4, Math.min(cq.vx1 - PLUS_R - 4, cq.lx)), y: cq.ly + NAME_DY });
        spots.push({ x: cq.lx + NAME_DX, y: Math.max(cq.vy0 + PLUS_R + 4, Math.min(cq.vy1 - PLUS_R - 4, cq.ly)) });
        for (sp = 0; sp < spots.length && !pick; sp++) {
          var S1 = spots[sp];
          if (S1.x - PLUS_R < cq.vx0 + 2 || S1.x + PLUS_R > cq.vx1 - 2 || S1.y - PLUS_R < cq.vy0 + 2 || S1.y + PLUS_R > cq.vy1 - 2) continue;
          free = true;
          for (k2 = 0; k2 < taken.length; k2++) if (Math.hypot(taken[k2].x - S1.x, taken[k2].y - S1.y) < PLUS_GAP) { free = false; break; }
          if (free) pick = S1;
        }
        diag.push(cq.nm + ' ' + Math.max(0, Math.round(vw)) + 'x' + Math.max(0, Math.round(vh)) + (pick ? ' UP' : ((vw > 0 && vh > 0) ? ' small' : ' off screen')));
        if (pick) { taken.push(pick); AUTO[cq.i] = { sx: pick.x, sy: pick.y }; nAuto++; autoList.push(cq.i); }
      }
      _plusAuto = AUTO;
      _plusDiag = 'a plus needs ' + PLUS_MIN + ' px of room on its shorter side, its ' + (2 * PLUS_R) + ' px under or beside the name, ' + PLUS_GAP + ' px from another, on a ' + hw + 'x' + hh + ' canvas: ' + diag.join(', ');
      _plusAutoSay(autoList.join(','));
      if (!_plusSel && !nAuto) { box.style.display = 'none'; return; }
      box.style.display = '';
      /* the tint of the tapped room, a layer of its own under the plus */
      var tint = $('cf-fit-sel');
      if (!tint) {
        tint = global.document.createElement('div');
        tint.id = 'cf-fit-sel';
        tint.style.cssText = 'position:absolute;left:0;top:0;right:0;bottom:0;pointer-events:none;';
        box.insertBefore(tint, box.firstChild);
      }
      tint.innerHTML = '';
      /* each plus knows its room's index (__idx), so a room with no box can
         not shift the others onto the wrong button, and none is made twice */
      var kids = [], plusBtns = [];
      for (i = 0; i < box.children.length; i++) {
        var kc = box.children[i];
        if (kc === tint) continue;
        plusBtns.push(kc);
        if (kc.__idx != null && !kids[kc.__idx]) kids[kc.__idx] = kc;
      }
      var usedIdx = {};
      for (i = 0; i < rooms.length; i++) {
        var R = rooms[i], rc = null, bA = -1;
        /* a room the product furnishes itself gets no plus (the sauna room) --
           its button is kept, hidden, so the others keep their places */
        var skipR = false;
        if (typeof FIT_PLUS_SKIP === 'function') { try { skipR = !!FIT_PLUS_SKIP(R, i); } catch (eSk) {} }
        /* A CLOSET GETS NO PLUS (core-v536, Marek: vestavene skrine, mensi nez
           3 m2 -- jen pricky, ne mistnost). The asset marks it on the room;
           nothing is furnished in a built-in closet */
        if (R.closet) skipR = true;
        for (j = 0; j < (R.rects || []).length; j++) {
          var r = R.rects[j], a = (r.x1 - r.x0) * (r.z1 - r.z0);
          if (a > bA) { bA = a; rc = r; }
        }
        if (!rc) continue;
        var cx = (rc.x0 + rc.x1) / 2, cz = rc.z0 + (rc.z1 - rc.z0) * 0.78;
        var selHere = !!(_plusSel && _roomHasPt(R, _plusSel.x, _plusSel.z));
        var autoHere = !!AUTO[i] && !selHere;
        if (selHere) { cx = _plusSel.x; cz = _plusSel.z; }
        else if (autoHere) { cx = (AUTO[i].sx - m.ox) / m.ppm; cz = (AUTO[i].sy - m.oy) / m.ppm; }
        var sx = m.ox + cx * m.ppm, sy = m.oy + cz * m.ppm;
        if (selHere && !skipR) {
          for (j = 0; j < (R.rects || []).length; j++) {
            var tr = R.rects[j], td = global.document.createElement('div');
            td.style.cssText = 'position:absolute;background:rgba(232,68,42,0.10);left:' + (m.ox + tr.x0 * m.ppm) + 'px;top:' + (m.oy + tr.z0 * m.ppm) + 'px;width:' +
              ((tr.x1 - tr.x0) * m.ppm) + 'px;height:' + ((tr.z1 - tr.z0) * m.ppm) + 'px;';
            tint.appendChild(td);
          }
        }
        var b = kids[i];
        if (!b) {
          b = global.document.createElement('button');
          b.__idx = i;
          b.type = 'button';
          b.style.cssText = 'position:absolute;width:34px;height:34px;border-radius:50%;border:0;' +
            'background:#E8442A;color:#fff;box-shadow:0 2px 8px rgba(0,0,0,0.22);cursor:pointer;' +
            'pointer-events:auto;display:flex;align-items:center;justify-content:center;padding:0;' +
            'transform:translate(-50%,-50%);touch-action:none;';
          b.innerHTML = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>';
          b.addEventListener('pointerdown', function (e) { e.stopPropagation(); });
          b.addEventListener('click', function (e) {
            e.preventDefault(); e.stopPropagation();
            var rm = this.__room;
            if (!rm) return;
            _fitRoom = rm; _fitAt = null;
            if (!fitEdit()) fitEdit(true);
            _fitOpen(_fitGroups(rm.key || rm.name));
            _log('fitout plus: ' + (rm.name || '?') + ' (' + (rm.key || 'no key') + ') at ' + rm.x.toFixed(2) + ',' + rm.z.toFixed(2));
          });
          box.appendChild(b);
          kids[i] = b;
          plusBtns.push(b);
        }
        usedIdx[i] = true;
        /* THE GROUPS ARE PICKED IN ENGLISH (core-v473, Marek: fitouts se
           prirazuji mistnosti v en, jiny jazyk do toho nezasahuje): name is
           what the buyer reads, key is the room row's own key and English
           name, and the product's _fitGroupOf is asked with the key */
        var enKey = '';
        try {
          var FLr = global.FloorplanLib, TB = (FLr && FLr.draw && FLr.draw.roomTable) ? FLr.draw.roomTable() : [];
          var ix = PL.roomNameIdx ? PL.roomNameIdx(R, i) : i, rw = TB.length ? TB[(((ix | 0) % TB.length) + TB.length) % TB.length] : null;
          if (rw) enKey = String(rw.i18n || '') + ' ' + String(rw.def || '');
        } catch (eK) { enKey = ''; }
        b.__room = { x: cx, z: (rc.z0 + rc.z1) / 2, name: (PL.roomName ? PL.roomName(R, i) : ''), key: enKey };
        b.style.left = sx + 'px'; b.style.top = sy + 'px'; b.style.display = (skipR || !(selHere || autoHere)) ? 'none' : '';
      }
      for (j = 0; j < plusBtns.length; j++) if (!usedIdx[plusBtns[j].__idx]) plusBtns[j].style.display = 'none';
    }
    /* THE FITOUT ICON ON THE PLAN STRIP (core-v626, Marek: jak ted pridavame
       nove fitouts? nevidim v banneru s floorplan edit ikonami ikonu fitouts
       -- barndominium). The red plus comes only in a room zoomed into (180 px
       on screen), and the tap that brought one up in a smaller room lost its
       caller with the strip -- with the whole house on a tablet there was no
       way to a fitout. The plan strip has a FITOUT icon now: the whole
       catalogue, and the piece picked goes into the room in the middle of
       the plan on screen (the nearest room that takes fitouts -- not a
       closet, not one the product furnishes itself); with no such room the
       product places it where a new piece goes. The plus of a room zoomed
       into stays, with that room's own pieces. */
    function _fitStripRoom() {
      var F = _FL(), PL = _P(), st = _stage(), root = $('cf-root');
      var OV = F && F.plan && F.plan.overlay, m = (OV && OV.map) ? OV.map(st) : null;
      var rooms = (PL && PL.rooms) ? (PL.rooms() || []) : [];
      if (!m || !(m.ppm > 0) || !rooms.length || !root) return null;
      var wx = ((root.clientWidth || 0) / 2 - m.ox) / m.ppm, wz = ((root.clientHeight || 0) / 2 - m.oy) / m.ppm;
      var best = null, bd = 1e18, i, j;
      for (i = 0; i < rooms.length; i++) {
        var R = rooms[i], sk = false;
        if (!R || R.closet) continue;
        if (typeof FIT_PLUS_SKIP === 'function') { try { sk = !!FIT_PLUS_SKIP(R, i); } catch (eSk) { sk = false; } }
        if (sk) continue;
        for (j = 0; j < (R.rects || []).length; j++) {
          var q = R.rects[j];
          var dx = Math.max(q.x0 - wx, 0, wx - q.x1), dz = Math.max(q.z0 - wz, 0, wz - q.z1), d = dx * dx + dz * dz;
          if (d < bd) { bd = d; best = { R: R, i: i, q: q }; }
        }
      }
      if (!best) return null;
      var enKey = '';
      try {
        var FLr = global.FloorplanLib, TB = (FLr && FLr.draw && FLr.draw.roomTable) ? FLr.draw.roomTable() : [];
        var ix = PL.roomNameIdx ? PL.roomNameIdx(best.R, best.i) : best.i, rw = TB.length ? TB[(((ix | 0) % TB.length) + TB.length) % TB.length] : null;
        if (rw) enKey = String(rw.i18n || '') + ' ' + String(rw.def || '');
      } catch (eK) { enKey = ''; }
      var nm = '';
      try { nm = PL.roomName ? String(PL.roomName(best.R, best.i) || '') : ''; } catch (eN) { nm = ''; }
      return { x: (best.q.x0 + best.q.x1) / 2, z: (best.q.z0 + best.q.z1) / 2, name: nm, key: enKey, inside: bd === 0 };
    }
    function _fitStripTap() {
      if (!_fitHasCatalog()) { _log('fitout icon: this product has no fitout catalogue'); return; }
      var rm = null;
      try { rm = _fitStripRoom(); } catch (eR) { rm = null; _log('fitout icon: finding the room THREW ' + ((eR && eR.message) || eR)); }
      _fitRoom = rm; _fitAt = null;
      if (!fitEdit()) fitEdit(true, 'the plan strip\'s fitout icon');
      _fitOpen(null);
      _log('fitout icon: the whole catalogue -- the piece goes ' + (rm ? ('into ' + (rm.name || '?') + (rm.inside ? ' (in the middle of the screen)' : ' (the nearest room to the middle of the screen)') + ' at ' + rm.x.toFixed(2) + ',' + rm.z.toFixed(2)) : 'where the product puts a new piece (no room on screen)') + ' (core-v626)');
    }
    function _plusThrow(e) {
      var msg = String((e && e.message) || e);
      if (msg === _plusThrew) return;
      _plusThrew = msg;
      _log('fitout plus THREW ' + msg);
    }
    var _iconsFollowRaf = 0;
    function _planIconsFollow() {
      if (_iconsFollowRaf) return;
      _iconsFollowRaf = global.requestAnimationFrame(function () {
        _iconsFollowRaf = 0;
        try { _fitPlusPlace(); } catch (ePl2) { _plusThrow(ePl2); }
        /* AND WHAT THE PRODUCT DRAWS ON THE PLAN (core-v624, RANCH: the cross
           gables' marks and icons stayed where they were through a pan or a
           pinch -- PLAN_EXTRA was placed on a paint only, and a camera move
           repaints the overlay without it) */
        try { var Fx = _FL(), OVx = Fx && Fx.plan && Fx.plan.overlay; if (OVx && typeof PLAN_EXTRA === 'function') _planExtraPlace(OVx); } catch (ePx2) {}
        try {
          var F = _FL(), st = _stage();
          if (!F || !F.plan || !F.plan.icons || !STATE.cutsOn) return;
          var OV2 = F.plan.overlay, host = _iconHost();
          var mIco = (OV2 && OV2.map) ? OV2.map(st) : null;
          if (host && mIco) F.plan.icons(host, mIco, 0, 0);
        } catch (e) {}
      });
    }
    function _planFollow(OV) {
      try { var elA = $('cf-plan-area'); if (elA && elA.style.display !== 'none') _planAreaPlace(elA); } catch (eA) {}
      try {
        var st = _stage(), ctl = st && st.orthoControls;
        if (!ctl || !ctl.addEventListener || !OV || !OV.paint) return;
        if (ctl.__aduPlanFollow) return;
        ctl.__aduPlanFollow = true;
        ctl.addEventListener('change', function () {
          try {
            if (!_planWanted()) return;
            /* the scissor icons are DOM, anchored at paint: put them back on
               the partitions after the camera moved (Marek: they stayed put
               through a pinch) */
            _planIconsFollow();
            /* SQUARE TO THE PLAN, EVERY TIME. The ortho camera stands a hair
               off plumb (0.0001 in z, so lookAt has an up to work with) and a
               damped pinch walks that hair into a visible tilt: the pucks, which
               are objects under the camera, came out as a parallelogram while
               the drawing, painted flat, did not (Marek). So on every move the
               camera is put back exactly over its target, up set, looking
               straight down, before the drawing is repainted. */
            var st2 = _stage(), cam2 = st2 && st2.orthoCam;
            if (cam2 && ctl.target) {
              cam2.position.set(ctl.target.x, cam2.position.y, ctl.target.z + 0.0001);
              cam2.up.set(0, 0, -1);
              cam2.lookAt(ctl.target.x, ctl.target.y, ctl.target.z);
              cam2.updateMatrixWorld(true);
              if (cam2.matrixWorldInverse && cam2.matrixWorldInverse.copy) {
                cam2.matrixWorldInverse.copy(cam2.matrixWorld);
                if (cam2.matrixWorldInverse.invert) cam2.matrixWorldInverse.invert();
              }
            }
            OV.paint(_stage());
          } catch (eF) {}
        });
        _log('plan follows the ortho camera (change listener bound)');
      } catch (eB) {}
    }

    /* THE USABLE AREA BY THE PLAN: the rooms' inner area summed, as a big
       figure with a small title, top left over the canvas, shown while the
       plan is on screen (Marek) */
    function _planAreaShow(on) {
      var root = $('cf-root'); if (!root) return;
      var el = $('cf-plan-area');
      if (!on) { if (el) el.style.display = 'none'; return; }
      if (!el) {
        el = document.createElement('div');
        el.id = 'cf-plan-area';
        el.style.cssText = 'position:absolute;left:50%;top:14px;transform:translateX(-50%);z-index:8;pointer-events:none;color:#1A1C1D;text-align:center;text-shadow:0 1px 3px rgba(255,255,255,0.9);white-space:nowrap;';
        el.innerHTML = '<div id="cf-plan-area-t" style="font:700 11px Montserrat,sans-serif;letter-spacing:.08em;text-transform:uppercase;color:#9CA3AF"></div>' +
                       '<div id="cf-plan-area-v" style="font:800 22px Montserrat,sans-serif;line-height:1.15"></div>';
        root.appendChild(el);
      }
      var PL = _P(), R = (PL && PL.rooms) ? (PL.rooms() || []) : [], a = 0, i, j;
      for (i = 0; i < R.length; i++) {
        var rc = R[i].rects || [];
        for (j = 0; j < rc.length; j++) a += Math.max(0, rc[j].x1 - rc[j].x0) * Math.max(0, rc[j].z1 - rc[j].z0);
      }
      /* the cells are the outline split by the partition lines: take the walls
         and the partitions out by the ratio of net to gross the rooms give */
      var W = wallThickness(), P = poly(), gross = polyArea(P), inner = 0;
      try { inner = Math.max(0, (STATE.footprint.w - 2 * W) * (STATE.footprint.l - 2 * W)); } catch (e) { inner = a; }
      var parts = (STATE.parts || []), pt = partThickness ? partThickness() : 0.1, k, partArea = 0;
      for (k = 0; k < parts.length; k++) { try { var sp = _partSpanH ? _partSpanH(parts[k]) : [parts[k].a, parts[k].b]; partArea += Math.abs(sp[1] - sp[0]) * ((typeof parts[k].t === 'number' && parts[k].t > 0) ? parts[k].t : pt); } catch (e2) {} }
      var net = Math.max(0, (a > 0 ? Math.min(a, inner) : inner) - partArea);
      /* THE ROOMS ADD UP TO IT (core-v564, floorplan v143, Marek: vymera nad
         floorplanem musi sedet za vsechny mistnosti): the plan's own sum of
         each room's floor, as each is written in it */
      var NA = null;
      try { NA = (PL && PL.netAreas) ? PL.netAreas() : null; } catch (eNA) { NA = null; }
      if (NA && NA.total > 0) net = NA.total;
      $('cf-plan-area-t').textContent = _t('inner_floor_area', 'Inner Floor Area');
      $('cf-plan-area-v').textContent = fmtArea(net);
      el.style.display = (STATE.view === '2d') ? '' : 'none';
      _planAreaPlace(el);
    }
    /* WHERE IT SITS: by the plan itself. The building's rectangle is projected
       to the screen; a plan taller than wide gets the figure to its LEFT at the
       top third, a plan wider than tall gets it ABOVE at the left third
       (Marek). Re-placed on every paint and every camera move. */
    function _planAreaPlace(el) {
      var st = _stage(), T = global.THREE; if (!st || !T || !st.camera || !st.renderer) return;
      try {
        var B = _buildRect(), y = API.storeys()[0].y0, cv = st.renderer.domElement, root = $('cf-root');
        var cr = cv.getBoundingClientRect(), rr = root ? root.getBoundingClientRect() : cr;
        var pts = [[B.x0, B.z0], [B.x1, B.z0], [B.x1, B.z1], [B.x0, B.z1]], q, sx0 = 1e9, sx1 = -1e9, sy0 = 1e9, sy1 = -1e9;
        for (q = 0; q < 4; q++) {
          var v = new T.Vector3(pts[q][0], y, pts[q][1]).project(st.camera);
          var px = (v.x + 1) / 2 * cr.width + (cr.left - rr.left), py = (1 - v.y) / 2 * cr.height + (cr.top - rr.top);
          sx0 = Math.min(sx0, px); sx1 = Math.max(sx1, px); sy0 = Math.min(sy0, py); sy1 = Math.max(sy1, py);
        }
        /* TWO METRES ABOVE THE PLAN, in the building's own scale: the point
           (mid x, z0 - 2 m) projected to the screen, the text centred on it and
           standing on it; it moves with the zoom, the font does not (Marek) */
        var pA = new T.Vector3((B.x0 + B.x1) / 2, y, B.z0 - 2.0).project(st.camera);
        var pB = new T.Vector3((B.x0 + B.x1) / 2, y, B.z1 + 2.0).project(st.camera);
        if (pB.y > pA.y) pA = pB;   /* whichever side is UP on the screen */
        var ax = (pA.x + 1) / 2 * cr.width + (cr.left - rr.left), ay = (1 - pA.y) / 2 * cr.height + (cr.top - rr.top);
        el.style.textAlign = 'center';
        el.style.right = 'auto';
        el.style.left = Math.round(ax) + 'px';
        el.style.top = Math.round(ay) + 'px';
        el.style.transform = 'translate(-50%, -100%)';
      } catch (e) {}
    }
    /* WHAT A PLAN PAINT COSTS (core-v589): calls, last / average / slowest, and
       how many a gesture asked for -- the perf lines read them. The paint itself
       is _drawPlanNow, unchanged. */
    var _pfP = { n: 0, last: 0, sum: 0, max: 0, gest: 0, gestSkipped: 0 };
    function drawPlan() {
      var a = 0;
      try { a = (global.performance && global.performance.now) ? global.performance.now() : Date.now(); } catch (e0) {}
      try { return _drawPlanNow(); }
      finally {
        try {
          var ms = ((global.performance && global.performance.now) ? global.performance.now() : Date.now()) - a;
          _pfP.n++; _pfP.last = ms; _pfP.sum += ms; if (ms > _pfP.max) _pfP.max = ms;
        } catch (e1) {}
      }
    }
    function _drawPlanNow() {
      var stageCv = $('cf-canvas');
      try { if (!_planWanted()) { var fb = $('cf-fit-plus-host'); if (fb) fb.style.display = 'none'; } } catch (eFb) {}
      /* the blocks flagged hideInPlan stay hidden as long as the plan is up,
         whatever rebuilt in the meantime */
      try {
        var stH = _stage();
        if (stH && stH.group) {
          var planH = _planWanted();
          stH.group.traverse(function (o) { if (o.userData && o.userData.hideInPlan) o.visible = !planH; });
        }
      } catch (eHp2) {}
      if (stageCv) stageCv.style.visibility = '';

      var F = _FL();
      var OV = (F && F.plan && F.plan.overlay) ? F.plan.overlay : null;
      try { _picWatch(F); } catch (ePw) {}
      if (!OV) {
        _planNote = 'NO plan.overlay on ' + (F ? F.VERSION : 'a missing asset');
        if (_veil.on) _planVeil(false, 'no plan drawing in this page');
        return;
      }

      try {
        if (!_planWanted()) {
          /* a top view with no plan to wait for */
          if (_veil.on) _planVeil(false, 'a top view, no plan in it');
          if (OV.hide) OV.hide();
          _iconsOff();
          try { var pxh = $('cf-plan-extra-host'); if (pxh) pxh.style.display = 'none'; } catch (ePxh) {}
          _planNote = 'hidden -- view=' + STATE.view;
          try { _planAreaShow(false); } catch (eA2) {}
          return;
        }
        /* NEVER THROUGH THE 3D CAMERA (core-v574, Marek: kdyz jsem presel do
           vedlejsiho okna, tak se na 3D modelu domku objevil pudorys). The
           drawing is laid over the canvas by the stage's own camera: drawn
           while that camera is still the 3D one, the plan lands on the model
           in perspective. The plan is wanted, so the stage is set to the top
           view first. */
        try {
          var stG = _stage();
          if (stG && stG.camera && !stG.camera.isOrthographicCamera && typeof stG.setView === 'function') {
            stG.setView('2d', _planFocus());
            try { _apply2DControls(); } catch (e2g) {}
            _log('plan     the plan was about to be drawn through the 3D camera -- the stage set to the top view first (core-v574)');
          }
        } catch (eGd) { _log('plan     setting the top view THREW ' + ((eGd && eGd.message) || eGd)); }
        /* the host is handed over again because the plan it describes has
           changed since the last paint -- walls moved, a door was placed */
        if (F.plan.init) F.plan.init(planHost());
        if (OV.ensure) OV.ensure();
        if (OV.show) OV.show();

        /* THE DRAWING MUST NOT CATCH THE FINGER. This canvas lies over the one
           the renderer draws on, and that is also the element OrbitControls
           listens to -- so a canvas that answered pointer events would take
           every tap AND kill the pinch that zooms the plan. It is ink, nothing
           more; the taps go through it to the surface underneath, where this
           product has always read them. */
        if (OV.el && OV.el.style) {
          OV.el.style.pointerEvents = 'none';
          OV.el.style.touchAction = 'none';
        }

        if (OV.paint) OV.paint(_stage());
        /* the plan's own build has run and the plan is on: the model may show */
        if (_veil.on && _planRb > _veil.rb) _planVeil(false, 'the plan build ran and the plan is drawn');
        try { _planAreaShow(true); } catch (eA) {}
        _planFollow(OV);
        try { _fitPlusPlace(); } catch (ePl) { _plusThrow(ePl); }
        try { _planExtraPlace(OV); _planExtraLater(); } catch (ePx) {}

        /* THE SCISSORS ARE THE ASSET'S, buttons and glyph and placement. One
           call per paint, because they stand at the ends of partitions that
           have just moved. */
        /* THE ICONS WERE NEVER BUILT, AND THE LOG SAID THEY WERE. planIcons
           starts with  if (!hostEl || !doc || !m || !_handles()) return  -- and
           m was 0. It returned an empty list every time and the note read
           "icons built" because nothing threw. It reports the COUNT now.
           m is the world-to-screen projection, and the asset already makes one:
           overlay.map(stage) is what the drawing itself is placed with. */
        var icoN = -1, icoWhy = '';
        try {
          if (!F.plan.icons) { icoWhy = 'no plan.icons'; }
          else if (!STATE.cutsOn) { _iconsOff(); icoWhy = 'cut tool off'; }
          else if (F.plan.handles && !F.plan.handles.on()) { _iconsOff(); icoWhy = 'handles down'; }
          else {
            var host = _iconHost();
            var mIco = OV.map ? OV.map(_stage()) : null;
            if (!host) icoWhy = 'NO HOST';
            else if (!mIco) icoWhy = 'NO PROJECTION -- overlay.map gave null';
            else {
              host.style.display = '';
              icoN = (F.plan.icons(host, mIco, 0, 0) || []).length;
              icoWhy = icoN + ' scissors';
            }
          }
        } catch (eI) { icoWhy = 'THREW ' + (eI && eI.message); _log('plan icons: ' + (eI && eI.message)); }

        /* AND ONLY WHEN THE PLAN IS STANDING STILL.

           This used to anchor on every paint, which is every frame of a drag --
           and a drag is full of rooms that exist for three frames while a wall
           crosses another. Each of those got a pin of its own, kept for good, and
           those pins then claimed real rooms later. That is why the names kept
           moving on SMALL drags too, and why it depended on where the wall was
           when it was picked up: it depended on which transient rooms the drag
           happened to make.

           A trailing timer waits for quiet, so nothing that only existed mid-drag
           is ever pinned. It also catches the outline's handles, whose drag state
           this file cannot see at all. */
        _anchorSoon();

        var el = OV.el || null;
        _planNote = 'painted by ' + (F.VERSION || '?') +
                    ' | pins ' + ((STATE.roomNames || []).length) +
                    (_pinNote ? (' -- ' + _pinNote) : '') +
                    ' | canvas ' + (el ? ((el.width | 0) + 'x' + (el.height | 0)) : 'NONE BUILT') +
                    ' | engine ' + (OV._eng ? 'held' : 'NOT HELD') +
                    ' | rooms ' + ((F.plan.rooms ? (F.plan.rooms() || []).length : '?')) +
                    ' | parts ' + (STATE.parts || []).length +
                    ' | doors ' + (STATE.doors || []).length +
                    ' | icons ' + icoWhy +
                    ' | openings ' + (function () {
                      try {
                        var w = _namedWallSegs(), k = 0, q;
                        for (q = 0; q < w.length; q++) k += _opsOn(w[q].id).length;
                        return k + ' on ' + w.length + ' wall(s)';
                      } catch (eO) { return '?'; }
                    })();
      } catch (e) {
        _planNote = 'paint THREW ' + (e && e.message ? e.message : e);
        _log('plan: ' + _planNote);
      }
    }

    /* the drawing has to follow the camera, or a pinch moves the building and
       leaves the plan behind. repaint reuses the engine paint kept hold of. */
    function repaintPlan() {
      try {
        var F = _FL();
        if (!_planWanted() || !F || !F.plan || !F.plan.overlay) return;
        if (F.plan.overlay.repaint) F.plan.overlay.repaint();
        else drawPlan();
      } catch (e) {}
    }

    /* --------------------------------------------------------------------------
       WORKING ON THE PLAN: pick a partition, move it, drop it on the bin.

       This is the same shape as editing an opening, and deliberately so -- one
       way of handling a thing is worth more than two good ones. Grab it, it
       follows the pointer, the button at the bottom becomes a bin while it is
       moving, letting go over the bin removes it.

       IT USES THE GROUND PLANE, NOT THE OBJECTS. Ray-casting at the meshes would
       mean picking the exact 4 mm slab a partition is drawn as, which is a hard
       target on a phone and gets harder as you zoom out. The pointer is projected
       onto the floor and the nearest partition within reach is taken -- so the
       tolerance is in metres of building rather than pixels of screen, and it is
       the same at every zoom.
       -------------------------------------------------------------------------- */
    var _partDrag = null;

    /* which end of which doorway is being dragged, while one is */
    var _doorEnd = null;
    var _doorDrag = null;

    /* where a tap landed with nothing under it, until the finger lets go */
    var _roomTap = null;

    /* ON THE SHELL, WHICH IS NOT A ROOM. An exterior wall is not a partition, so
       grabbing one falls through every probe the plan has -- and the outline's
       own handles live on this same canvas. Anything within a wall's thickness of
       an outside face is a wall grab, whatever else it might also be. */
    function _nearShell(x, z) {
      var segs = _wallSegs(), reach = wallThickness() + 0.15, i, sg, dx, dz, t, px, pz;
      for (i = 0; i < segs.length; i++) {
        sg = segs[i];
        dx = sg.b[0] - sg.a[0]; dz = sg.b[1] - sg.a[1];
        if (sg.len < 1e-6) continue;
        t = ((x - sg.a[0]) * dx + (z - sg.a[1]) * dz) / (sg.len * sg.len);
        t = t < 0 ? 0 : (t > 1 ? 1 : t);
        px = sg.a[0] + t * dx; pz = sg.a[1] + t * dz;
        if (Math.sqrt((x - px) * (x - px) + (z - pz) * (z - pz)) <= reach) return true;
      }
      return false;
    }

    /* STILL A TAP UNTIL IT TRAVELS. Tiny's far(): a few pixels of slop, so a
       finger that only rests on a door cycles it instead of nudging it. The
       origin is taken when the door is grabbed, not on the first move -- taken
       later it would measure the second drag from where the first one started. */
    var _dragFrom = null;
    function _dragFromSet(e) { _dragFrom = e ? { x: e.clientX, y: e.clientY } : null; }
    function _farEnough(e) {
      if (!e || !_dragFrom) return true;
      var dx = e.clientX - _dragFrom.x, dy = e.clientY - _dragFrom.y;
      return (dx * dx + dy * dy) > 36;
    }

    /* the map's own test, so the snapshot can ask IT rather than re-state it */
    var _canPan2D = null;

    /* WHAT HAPPENS WHEN A FINGER LANDS ON THE PLAN, counted. "It does not drag"
       can mean the tap never arrived, or that it arrived and the asset found
       nothing under it. Those two want opposite fixes and look identical. */
    var _planTaps = { taps: 0, part: 0, door: 0, end: 0, room: 0, none: 0, last: '-', threw: '-' };

    /* ONE QUESTION, ONE GUARD. A verb that throws must cost its own answer and
       nothing else -- and it must say which verb it was, in the snapshot, where
       it can be read off a tablet. */
    function _ask(PL, fn, a, b, c) {
      try {
        if (!PL || typeof PL[fn] !== 'function') return null;
        var r = (c === undefined) ? PL[fn](a, b) : PL[fn](a, b, c);
        return (r === null || r === undefined || r === false) ? null : r;
      } catch (e) {
        /* EVERY VERB KEEPS ITS OWN, because one field held only the LAST throw
           and the verbs run in order -- so a partAt that fails was overwritten
           by the room name that fails after it, and only the second one was
           ever visible. */
        var msg = fn + '(): ' + (e && e.message ? e.message : e);
        if (_planTaps.threw.indexOf(msg) < 0) {
          _planTaps.threw = (_planTaps.threw === '-' ? '' : _planTaps.threw + ' | ') + msg;
        }
        _log('plan ' + msg);
        return null;
      }
    }

    /* --------------------------------------------------------------------------
       THE EDITOR IS THE ASSET'S. ALL OF IT.

       I wrote partitionNear, movePartition, removePartition, armPartition and
       placePartition, and every one of them already existed:

         partAt(x, z)            which partition is under the point
         movePartTo(i, c)        move it, with the asset's own clamping
         cutPartSeg(i)           cut it -- the thing I had not built at all
         cycleRoomNameAt(x, z)   name the room under the point
         addDoorAt(x, z)         put a door in the wall under the point
         doorAt(x, z)            which door is under it -- ASKS ONLY
         doorTapAt(x, z)         cycles one, and ADDS one when there is none
         moveDoorTo, resizeDoorTo, stepDoor

       Every product using this asset gets the same behaviour, and mine was a
       second implementation that was worse and could only drift. What belongs
       here is the pointer -- turning a tap on this product's canvas into a point
       in this product's building -- and nothing after that.
       -------------------------------------------------------------------------- */
    var _rbSoonT = null;
    function _rebuildSoon(ms, why) {
      if (_rbSoonT) clearTimeout(_rbSoonT);
      _rbSoonT = setTimeout(function () { _rbSoonT = null; _log('rebuild after ' + (why || 'a pause')); rebuild(); drawPlan(); }, ms || 1200);
    }
    var _armedPart = null;

    function armPartition(dir) {
      _armedPart = (dir === _armedPart) ? null : dir;
      var w = API.wizard();
      if (w && w.refresh) w.refresh();
      _log(_armedPart ? ('partition armed (' + _armedPart + ') -- tap the plan')
                      : 'partition disarmed');
      return _armedPart;
    }
    function armedPartition() { return _armedPart; }

    function _P() { var F = _FL(); return (F && F.plan) ? F.plan : null; }

    /* placing is the one thing the asset does not do for us: it edits what is
       there, and the list itself is this product's state */
    /* THE WALL GOES IN WHEN THE BUTTON IS PRESSED, INTO THE BIGGEST ROOM THAT
       WILL TAKE IT. Copied from tiny's addPartition.

       Arming the button and waiting for a tap on the drawing was my invention and
       it is wrong twice over: the button showed no state, and nothing appeared
       until the plan was touched. Tiny has no arming at all -- the icon divides
       the largest room there is and the wall is on screen before the finger
       leaves the button. */
    function _partId() {
      var ps = STATE.parts || [], i, mx = 0;
      for (i = 0; i < ps.length; i++) if (ps[i] && (ps[i].id | 0) > mx) mx = ps[i].id | 0;
      return mx + 1;
    }

    /* the opening itself: into the longest wall that can take it, a module
       wide where the product steps by one (core-v348) */
    /* PUT IT WHERE THE FINGER SAYS (core-v350, Marek: dlazdice Hole neotevira
       drawer ale rovnou aktivuje vlozeni na misto ve 3d modelu). The tile arms
       the mode; the next tap on the model drops an opening into the wall
       nearest the point touched -- outer wall, load-bearing wall or partition
       -- at that spot, sized on the blocks (core-v349), and the mode goes off
       again. Tapping the tile again, or anywhere off a wall, disarms it. */
    var _holeMode = false, _holeTap = null;
    function holeMode(on) {
      var st = _stage();
      _holeMode = !!on;
      if (_holeMode && st && st.renderer && !_holeTap) {
        _holeTap = function (e) {
          var hit = _holeHit(e);
          holeMode(false);
          if (hit) addHole(hit);
          else _log('hole     tap off the walls -- nothing placed');
          try { if (_wiz && _wiz.refresh) _wiz.refresh(); } catch (eR) {}
        };
        st.renderer.domElement.addEventListener('pointerdown', _holeTap, true);
      } else if (!_holeMode && _holeTap && st && st.renderer) {
        st.renderer.domElement.removeEventListener('pointerdown', _holeTap, true);
        _holeTap = null;
      }
      _log('hole     placing ' + (_holeMode ? 'armed -- tap the wall' : 'off'));
      return _holeMode;
    }
    /* which wall the tap landed on, and how far along it */
    function _holeHit(e) {
      var st = _stage(), T = global.THREE;
      if (!st || !st.camera || !st.renderer || !T) return null;
      var r = st.renderer.domElement.getBoundingClientRect();
      var ndc = new T.Vector2(((e.clientX - r.left) / r.width) * 2 - 1, -((e.clientY - r.top) / r.height) * 2 + 1);
      var rc = new T.Raycaster();
      rc.setFromCamera(ndc, st.camera);
      var hits = rc.intersectObject(st.group, true), pt = null, i;
      for (i = 0; i < hits.length; i++) if (hits[i] && hits[i].point) { pt = hits[i].point; break; }
      if (!pt) return null;
      var best = null, PO = poly(), q, ps = _normParts();
      function near(kind, ref, ax, az, bx, bz) {
        var dx = bx - ax, dz = bz - az, L = Math.sqrt(dx * dx + dz * dz);
        if (!(L > 0.5)) return;
        var t = ((pt.x - ax) * dx + (pt.z - az) * dz) / (L * L);
        var tc = Math.max(0, Math.min(1, t));
        var px = ax + dx * tc, pz = az + dz * tc, d = Math.sqrt((pt.x - px) * (pt.x - px) + (pt.z - pz) * (pt.z - pz));
        if (!best || d < best.d) best = { kind: kind, ref: ref, u: tc * L, len: L, d: d };
      }
      for (q = 0; q < PO.length; q++) near('wall', q, PO[q][0], PO[q][1], PO[(q + 1) % PO.length][0], PO[(q + 1) % PO.length][1]);
      for (q = 0; q < ps.length; q++) {
        var sp = _partSpanH(ps[q]);
        if (!sp) continue;
        if (ps[q].sep) continue;          /* a room line has nothing to make a hole in (core-v536) */
        if ((ps[q].dir || ps[q].o) === 'v') near(ps[q].bear ? 'bearing' : 'partition', ps[q].id, ps[q].at, sp[0], ps[q].at, sp[1]);
        else near(ps[q].bear ? 'bearing' : 'partition', ps[q].id, sp[0], ps[q].at, sp[1], ps[q].at);
      }
      if (!best || best.d > 1.5) return null;
      return best;
    }
    /* EDITED ON THE WALL ITSELF (core-v351, Marek: edituje se stejne jako
       vlozene okno). Four points on the opening's edges: the two upright ones
       take the width along the wall, on the block's head joints, the two flat
       ones the sill and the head, by whole courses. A fifth, the bin, takes
       the opening away. They ride the model like the block sample's points. */
    var _hh = { on: null, els: {}, drag: null, tracking: false };
    function _holeGeom(h) {
      if (!h) return null;
      var ax, az, bx, bz, PO = poly(), ps = _normParts(), i, sp;
      if (h.kind === 'wall') {
        if (!PO[h.ref]) return null;
        ax = PO[h.ref][0]; az = PO[h.ref][1];
        bx = PO[(h.ref + 1) % PO.length][0]; bz = PO[(h.ref + 1) % PO.length][1];
      } else {
        for (i = 0; i < ps.length; i++) if (ps[i].id === h.ref) { sp = _partSpanH(ps[i]); break; }
        if (!sp) return null;
        if ((ps[i].dir || ps[i].o) === 'v') { ax = ps[i].at; az = sp[0]; bx = ps[i].at; bz = sp[1]; }
        else { ax = sp[0]; az = ps[i].at; bx = sp[1]; bz = ps[i].at; }
      }
      var dx = bx - ax, dz = bz - az, L = Math.sqrt(dx * dx + dz * dz);
      if (!(L > 0.2)) return null;
      /* from the foot of the wall itself, not the finished floor, or the
         opening hangs in the air over the masonry (core-v360) */
      var base = storeys()[0].y0;
      try { if (typeof __PROD._wallBaseY === 'function') { var bY = +__PROD._wallBaseY(); if (isFinite(bY)) base = bY; } } catch (eB) {}
      var ux = dx / L, uz = dz / L, y0 = base + (h.sill || 0), y1 = y0 + (h.h || 1);
      return { ax: ax, az: az, ux: ux, uz: uz, L: L, y0: y0, y1: y1,
               p0: [ax + ux * h.u, y0, az + uz * h.u], p1: [ax + ux * (h.u + h.w), y0, az + uz * (h.u + h.w)] };
    }
    /* ---------------------------------------------------------------------
       THE PRODUCT'S OWN OUTLINE AND CYCLER FOR A UNIT (core-v503).

       __PROD._opOutline(sg, rec) -> [[u, v], ...] | null
           the unit's shape in the wall: u along the wall from sg.x0 (as
           rec.at), v up from sg.y0. Null keeps the rectangle.
       __PROD._opCycle(rec) -> { label, next() } | null
           the unit's variants; next() steps the record and answers the new
           key. Null means no icon.

       Any product may answer them -- a gable glazing in the A-frame today, a
       raked or arched window anywhere tomorrow. A product without them is
       exactly as before.
       --------------------------------------------------------------------- */
    function _opOutlineOf(rec) {
      if (!rec || rec.host === 'roof' || typeof __PROD._opOutline !== 'function') return null;
      var sg = segment(rec.seg), pts = null;
      if (!sg || !(sg.len > 0)) return null;
      try { pts = __PROD._opOutline(sg, rec); } catch (e) { pts = null; }
      if (!pts || !pts.length) return null;
      /* ONE OUTLINE OR SEVERAL (core-v505): a glazing in two parts -- floors
         1 and 3 with the wall of floor 2 between them -- answers a list of
         polygons; a single polygon is a list of points */
      var polys = (pts[0] && typeof pts[0][0] === 'number') ? [pts] : pts, ok = [], i;
      for (i = 0; i < polys.length; i++) if (polys[i] && polys[i].length >= 3) ok.push(polys[i]);
      if (!ok.length) return null;
      return { sg: sg, pts: ok[0], polys: ok };
    }
    function _opOutlineWorld(sg, u, v) {
      var ux = (sg.x1 - sg.x0) / sg.len, uz = (sg.z1 - sg.z0) / sg.len;
      return [sg.x0 + ux * u, (sg.y0 || 0) + v, sg.z0 + uz * u];
    }
    function _opOutlineCentroid(pts) {
      var a = 0, cx = 0, cy = 0, i;
      for (i = 0; i < pts.length; i++) {
        var p = pts[i], q = pts[(i + 1) % pts.length], c = p[0] * q[1] - q[0] * p[1];
        a += c; cx += (p[0] + q[0]) * c; cy += (p[1] + q[1]) * c;
      }
      if (Math.abs(a) < 1e-9) {
        cx = 0; cy = 0;
        for (i = 0; i < pts.length; i++) { cx += pts[i][0]; cy += pts[i][1]; }
        return [cx / pts.length, cy / pts.length];
      }
      return [cx / (3 * a), cy / (3 * a)];
    }
    function _opPolyArea(P) {
      var a = 0, i;
      for (i = 0; i < P.length; i++) { var p = P[i], q = P[(i + 1) % P.length]; a += p[0] * q[1] - q[0] * p[1]; }
      return Math.abs(a) / 2;
    }
    function _opGhostDepth() {
      var t = 0;
      try { t = +wallThickness() || 0; } catch (e) { t = 0; }
      return (t > 0.02 ? t : 0.30) + 0.04;
    }
    /* the outline pushed through the wall, centred on the wall line */
    function _opOutlineMesh(T, sg, pts, depth, mat) {
      try {
        var sh = new T.Shape(), i;
        sh.moveTo(pts[0][0], pts[0][1]);
        for (i = 1; i < pts.length; i++) sh.lineTo(pts[i][0], pts[i][1]);
        sh.closePath();
        var geo = new T.ExtrudeGeometry(sh, { depth: depth, bevelEnabled: false });
        geo.translate(0, 0, -depth / 2);
        var ux = (sg.x1 - sg.x0) / sg.len, uz = (sg.z1 - sg.z0) / sg.len;
        var M = new T.Matrix4().makeBasis(new T.Vector3(ux, 0, uz), new T.Vector3(0, 1, 0), new T.Vector3(-uz, 0, ux));
        M.setPosition(sg.x0, sg.y0 || 0, sg.z0);
        geo.applyMatrix4(M);
        return new T.Mesh(geo, mat);
      } catch (e) { return null; }
    }
    var _opOutlineSaid = {};
    function _opOutlineSay(rec, n) {
      var k = rec.seg + '@' + Math.round((rec.at || 0) * 1000) + ':' + n;
      if (_opOutlineSaid[k]) return;
      _opOutlineSaid[k] = 1;
      _log('opening  overlay from the product outline (' + n + ' points) on ' + rec.seg + ' -- no sill shown');
    }

    /* THE CYCLER ICON (core-v503, Marek: cykler v edit mode uprostred stitu):
       with the opening edit on, every unit whose product answers _opCycle
       gets a round black button with a white turn glyph at the middle of its
       outline. A tap steps the variant and the model is rebuilt. The target
       stays 44px and visible throughout opening editing, including orbit. */
    var _opcEls = [];
    var _OPC_GLYPH = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M4 12a8 8 0 0 1 13.66-5.66L20 8.5"/><path d="M20 3.5v5h-5"/><path d="M20 12a8 8 0 0 1-13.66 5.66L4 15.5"/><path d="M4 20.5v-5h5"/></svg>';
    function _opcEl(n) {
      var host = _smpHost();
      if (_opcEls[n] && _opcEls[n].parentNode) return _opcEls[n];
      if (!host) return null;
      var el = document.createElement('div');
      el.id = 'cf-opc-' + n;
      el.style.cssText = 'position:absolute;transform:translate(-50%,-50%);display:none;flex-direction:column;align-items:center;' +
        'pointer-events:none;z-index:13;';
      var b = document.createElement('div');
      b.style.cssText = 'width:44px;height:44px;flex:none;border-radius:50%;background:#1A1C1D;display:flex;align-items:center;justify-content:center;' +
        'box-shadow:0 2px 8px rgba(0,0,0,0.30);cursor:pointer;touch-action:none;pointer-events:auto;';
      b.innerHTML = _OPC_GLYPH;
      /* NO NAME UNDER IT (core-v505, Marek: znacka bez tooltip / vysvetleni):
         the model changing under the tap is the explanation */
      el.appendChild(b);
      el.__btn = b;
      b.addEventListener('pointerdown', function (e) {
        e.preventDefault(); e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        var rec = el.__rec, C = null, key = '?';
        if (!rec) return;
        C = _opCycleOf(rec);
        if (!C || typeof C.next !== 'function') { _log('opening  cycle: the product gave no cycler for this unit'); return; }
        try { key = C.next(); } catch (eN) { _log('opening  cycle threw: ' + ((eN && eN.message) || eN)); return; }
        _opLast = rec;
        savePref();
        rebuild();
        _log('opening  cycle -> ' + key + ' on ' + rec.seg);
      });
      host.appendChild(el);
      _opcEls[n] = el;
      return el;
    }
    /* THE CYCLER FOR A UNIT: a corner window's own (core-v751), else the
       product's -- one icon per corner window, on its first unit */
    function _opCycleOf(rec) {
      if (!rec) return null;
      if (rec.cw) return _cwCycle(rec);
      if (typeof __PROD._opCycle !== 'function') return null;
      try { return __PROD._opCycle(rec); } catch (eC) { return null; }
    }
    function _opcOn() {
      if (STATE.view !== '3d' || (typeof __PROD._opCycle !== 'function' && !_cwAny())) return false;
      try { return !!opEdit() && !!editing('openings'); } catch (e) { return false; }
    }
    function _opcTick() {
      var n = 0, OP = STATE.openings || [], i;
      if (_opcOn()) {
        for (i = 0; i < OP.length; i++) {
          var rec = OP[i], C = null;
          if (!rec || rec.host === 'roof') continue;
          C = _opCycleOf(rec);
          if (!C) continue;
          var sg = segment(rec.seg);
          if (!sg || !(sg.len > 0)) continue;
          var OL = rec.cw ? null : _opOutlineOf(rec), c;
          if (rec.cw) {
            /* the corner window's icon stands on the corner, halfway up the glass */
            var pB = _cwPeer(rec), hd = rec.sill + rec.h;
            if (pB) hd = Math.max(hd, pB.sill + pB.h);
            c = [rec.cwEnd ? sg.len : 0, Math.max(0.3, hd / 2)];
          }
          else if (OL) {
            /* the middle of all the glazed parts, weighted by their areas */
            var aS = 0, cxS = 0, cyS = 0, pq2;
            for (pq2 = 0; pq2 < OL.polys.length; pq2++) {
              var cP = _opOutlineCentroid(OL.polys[pq2]), aP = _opPolyArea(OL.polys[pq2]);
              aS += aP; cxS += cP[0] * aP; cyS += cP[1] * aP;
            }
            c = (aS > 1e-9) ? [cxS / aS, cyS / aS] : _opOutlineCentroid(OL.pts);
            /* a centre that falls in the solid wall between two parts goes to the biggest part */
            if (OL.polys.length > 1) {
              var big = OL.polys[0], ab = 0;
              for (pq2 = 0; pq2 < OL.polys.length; pq2++) { var aq = _opPolyArea(OL.polys[pq2]); if (aq > ab) { ab = aq; big = OL.polys[pq2]; } }
              c = _opOutlineCentroid(big);
            }
          }
          else {
            var u0 = (rec.at || 0) - (rec.w || 0) / 2, u1 = u0 + (rec.w || 0), v0 = rec.sill || 0, v1 = v0 + (rec.h || 0);
            c = [(u0 + u1) / 2, (v0 + v1) / 2];
          }
          var sc = _smpScreen(_opOutlineWorld(sg, c[0], c[1]));
          if (!sc) continue;
          /* An editing control has a fixed touch target. Orbiting the house
             must not shrink it or hide it behind an opaque wall. */
          var el = _opcEl(n);
          if (!el) break;
          n++;
          el.__rec = rec;
          var par = el.offsetParent || el.parentNode, pr = par ? par.getBoundingClientRect() : { left: 0, top: 0 };
          el.style.display = 'flex';
          el.style.transform = 'translate(-50%,-50%)';
          var st = _stage(), cr = st.renderer.domElement.getBoundingClientRect();
          var left = Math.max(cr.left, 0) + 28, right = Math.min(cr.right, global.innerWidth) - 80;
          var top = Math.max(cr.top, 0) + 28, bottom = Math.min(cr.bottom, global.innerHeight) - 76;
          var x = Math.max(left, Math.min(right, sc.x)), y = Math.max(top, Math.min(bottom, sc.y));
          /* Keep a control reachable when its unit projects behind the eye. */
          if (!isFinite(x) || !isFinite(y) || sc.z > 1) { x = (left + right) / 2; y = bottom; }
          for (var j = 0; j < n - 1; j++) {
            var other = _opcEls[j];
            if (other && Math.abs(x - other.__x) < 46 && Math.abs(y - other.__y) < 46) y = other.__y > top + 48 ? other.__y - 48 : other.__y + 48;
          }
          el.__x = x; el.__y = y;
          el.style.left = (x - pr.left) + 'px';
          el.style.top = (y - pr.top) + 'px';
        }
      }
      for (i = n; i < _opcEls.length; i++) if (_opcEls[i]) { _opcEls[i].style.display = 'none'; _opcEls[i].__rec = null; }
    }

    /* ---------------------------------------------------------------------
       THE CORNER WINDOW (core-v751, corner window thread).

       Marek 2026-10-16: "Na druhou pozici (kde to dava smysl) udelame corner
       window. Bude to sestava dvou katalogovych oken, opet s ikonou
       prepinani (ruzne sestavy) ... Dulezite je aby to bylo konstrukcne
       spravne." -- and on the switch: "krokovat uzsi hs portal s pevnym
       oknem, pak sirsi hs portal se stejnym oknem, pak dva stejne hs
       portaly atd., pak se sestava ozrcadli atd. Max 12 kroku."

       WHAT IT IS. Two ORDINARY catalogue openings, one on each wall of an
       outside corner, each pinned to the corner at the same keep any unit has
       from a corner (the wall that meets it, a jack and a king). So the
       framing, the joinery, the bill, the plan and the AR walk-through see
       two plain units and need to know nothing new: every product of the
       core builds and bills them as it builds and bills any window.

       THE CORNER POST STAYS. A light frame keeps its corner studs, and each
       unit gets its own header bearing on the jack beside the corner; a post
       frame keeps its corner post, a masonry wall its pier. A glass corner
       with no post would need a header cantilevered over the corner (LVL or
       steel) and is not offered.

       WHAT IS CHECKED, PER WALL, FOR EVERY SET (a set that fails is skipped
       by the cycler, and a tap where none fits places nothing):
         - the unit fits along the wall, clear of the other units on it
           (a king and a jack, 120 mm, between two holes);
         - the head fits under the header (a door's 120 mm header under the
           plate; a window may use the plate itself, as everywhere);
         - an HS portal only on the ground storey (a door needs somewhere to
           step out to);
         - BRACING: a full-height panel at least half the wall's height long
           (IRC wall bracing, WSP: 48 in on an 8 ft wall) must start within
           3.05 m (10 ft) of the corner. A corner window takes the panel
           every corner otherwise has; this keeps the next one close enough.
           A hold-down at the corner is not modelled.

       THE SETS, the walls being A (the one tapped) and B (round the corner):
       first every combination, then the mirrors of the unequal ones. Heads
       line up across the corner: on the HS portal's head where a portal is
       in the set, else at 0.90 + the taller window. */
    var CW_SETS = [
      { k: 'hs1500+fixed',   a: 'OWR15002', b: 'OWR25917' },
      { k: 'hs2500+fixed',   a: 'OWR25001', b: 'OWR25917' },
      { k: 'hs2500+hs1500',  a: 'OWR25001', b: 'OWR15002' },
      { k: 'hs1500+hs1500',  a: 'OWR15002', b: 'OWR15002' },
      { k: 'hs2500+hs2500',  a: 'OWR25001', b: 'OWR25001' },
      { k: 'fixed+fixed',    a: 'OWR25917', b: 'OWR25917' },
      { k: 'window+window',  a: 'OWR15001', b: 'OWR15001' },
      { k: 'window+fixed',   a: 'OWR15001', b: 'OWR25917' },
      { k: 'fixed+hs1500',   a: 'OWR25917', b: 'OWR15002' },
      { k: 'fixed+hs2500',   a: 'OWR25917', b: 'OWR25001' },
      { k: 'hs1500+hs2500',  a: 'OWR15002', b: 'OWR25001' },
      { k: 'fixed+window',   a: 'OWR25917', b: 'OWR15001' }
    ];
    var CW_OFF = { aframe: 1, container: 1 };   /* Marek: ne A-frame, ne container house */
    var CW_BRACE_REACH = 3.05;                  /* 10 ft from the corner */
    var CW_BRACE_RATIO = 0.5;                   /* panel length / wall height */
    var CW_GAP = 0.12;                          /* a king and a jack between two holes */
    var _cwKinds = null, _cwSeq = 0;
    function _cwOffered() {
      try {
        if (__PROD && __PROD._cornerWin === false) return false;
        if (__PROD && __PROD._cornerWin === true) return true;
      } catch (e0) {}
      var sl = '';
      try { sl = String((BOOTDATA() || {}).slug || PRODUCT || ''); } catch (e1) { sl = ''; }
      return !CW_OFF[sl];
    }
    function _cwAny() {
      var OP = STATE.openings || [], i;
      for (i = 0; i < OP.length; i++) if (OP[i] && OP[i].cw) return true;
      return false;
    }
    /* a catalogue unit by its code, with the group it belongs to */
    function _cwUnit(code) {
      var CAT = (typeof OPENING_CATALOG !== 'undefined') ? OPENING_CATALOG : ((_UI() && _UI().OPENING_CATALOG) || {});
      var K = ['window', 'french'], q, i;
      for (q = 0; q < K.length; q++) {
        var G = CAT[K[q]];
        if (!G || !G.items) continue;
        for (i = 0; i < G.items.length; i++) {
          var it = G.items[i];
          if (it && it.code === code) return { code: code, w: +it.w, h: +it.h, leaves: it.leaves || 1, kind: K[q], hs: K[q] === 'french', fixed: !!it.fixed, name: it.name };
        }
      }
      return null;
    }
    function _cwSegIdx(id) {
      var m = /^S(\d+)-W(\d+)$/.exec(String(id || ''));
      return m ? { s: +m[1], i: +m[2] } : null;
    }
    /* the wall round an OUTSIDE corner at one end of sg (end 0: its start,
       1: its end), and which of its own ends the corner is */
    function _cwPartner(sg, end) {
      var I = _cwSegIdx(sg && sg.id);
      if (!I) return null;
      var N = poly().length;
      if (N < 3) return null;
      var j = end ? (I.i + 1) % N : (I.i - 1 + N) % N;
      var pg = segment('S' + I.s + '-W' + j);
      if (!pg || !(pg.len > 0)) return null;
      var e1 = end ? sg : pg, e2 = end ? pg : sg;
      var d2x = (e2.x1 - e2.x0) / e2.len, d2z = (e2.z1 - e2.z0) / e2.len;
      /* outside corner: the next wall turns in from the first one's outward
         normal; at most 120 degrees inside, so a near-straight joint is none */
      if (!(e1.nx * d2x + e1.nz * d2z < -0.5)) return null;
      return { sg: pg, end: end ? 0 : 1 };
    }
    function _cwAt(sg, w, end) {
      var k = _openingKeep();
      return end ? (sg.len - k - w / 2) : (k + w / 2);
    }
    function _cwPeer(rec) {
      var OP = STATE.openings || [], i;
      if (!rec || !rec.cw) return null;
      for (i = 0; i < OP.length; i++) if (OP[i] && OP[i] !== rec && OP[i].cw === rec.cw) return OP[i];
      return null;
    }
    /* the highest a unit's head may stand at the corner of this wall: under
       the plate (a window may use the plate as its header, as everywhere),
       a door 120 mm lower for its own header; under a rake, the product's line */
    function _cwTop(sg, end, U) {
      var tmp = { seg: sg.id, at: _cwAt(sg, U.w, end), w: U.w, h: U.h, sill: U.hs ? 0 : 0.9, kind: U.kind };
      var hook = _opUnderHook(sg, tmp), top = _plateUnder(sg, tmp) + WALL_LAYOUT.frame.fit;
      return top - ((U.hs && hook == null) ? SILL_HEAD_RESERVE : 0);
    }
    /* one wall of the corner with unit U at sill: { ok, why, panel } */
    function _cwWallOk(sg, end, U, sill, skip) {
      if (!sg || !U) return { ok: false, why: 'no wall' };
      if (U.hs && (sg.storey || 0) > 0) return { ok: false, why: 'HS only on the ground floor' };
      if (U.hs && _cwKinds && _cwKinds.indexOf('french') < 0) return { ok: false, why: 'the product sells no HS' };
      var k = _openingKeep(), L = sg.len;
      if (k + U.w + k > L + 1e-6) return { ok: false, why: 'wall ' + Math.round(L * 1000) + ' too short' };
      var top = _cwTop(sg, end, U);
      if (sill < -1e-6) return { ok: false, why: Math.round(U.h * 1000) + ' high, the wall gives ' + Math.round(top * 1000) };
      if (sill + U.h > top + 0.0005) return { ok: false, why: 'head ' + Math.round((sill + U.h) * 1000) + ' over ' + Math.round(top * 1000) };
      /* the other units on this wall, measured from the corner */
      var occ = [[k, k + U.w]], OP = STATE.openings || [], i;
      for (i = 0; i < OP.length; i++) {
        var o = OP[i];
        if (!o || o.host === 'roof' || o.seg !== sg.id || (skip && skip.indexOf(o) >= 0)) continue;
        var a0 = o.at - o.w / 2, a1 = o.at + o.w / 2, c0 = end ? (L - a1) : a0, c1 = end ? (L - a0) : a1;
        if (c0 < k + U.w + CW_GAP - 1e-6) return { ok: false, why: 'a ' + (o.code || o.kind) + ' in the way' };
        occ.push([c0, c1]);
      }
      occ.sort(function (p, q) { return p[0] - q[0]; });
      var x = 0, need2 = CW_BRACE_RATIO * sg.h, best = null;
      for (i = 0; i <= occ.length; i++) {
        var nx = (i < occ.length) ? occ[i][0] : L, run = nx - x;
        if (run >= need2 - 1e-6 && x <= CW_BRACE_REACH + 1e-6) { best = { s: x, len: run }; break; }
        if (i < occ.length) x = Math.max(x, occ[i][1]);
      }
      if (!best) return { ok: false, why: 'no braced panel ' + Math.round(need2 * 1000) + ' within ' + Math.round(CW_BRACE_REACH * 1000) + ' of the corner' };
      return { ok: true, panel: best, sill: sill };
    }
    /* a whole set on the two walls */
    function _cwFit(i, A, B, skip) {
      var S = CW_SETS[i];
      if (!S) return { ok: false, why: 'no set' };
      var UA = _cwUnit(S.a), UB = _cwUnit(S.b);
      if (!UA || !UB) return { ok: false, why: 'unit not in the catalogue' };
      /* THE HEADS LINE UP ACROSS THE CORNER: on the portal's head where a
         portal is in the set; else at 0.90 + the taller window, brought down
         as far as the lower of the two walls needs (a window may come down,
         a door stays on the floor) */
      var H;
      if (UA.hs || UB.hs) H = Math.max(UA.hs ? UA.h : 0, UB.hs ? UB.h : 0);
      else H = Math.min(0.90 + Math.max(UA.h, UB.h), _cwTop(A.sg, A.end, UA), _cwTop(B.sg, B.end, UB));
      var sA = UA.hs ? 0 : Math.round((H - UA.h) * 1000) / 1000, sB = UB.hs ? 0 : Math.round((H - UB.h) * 1000) / 1000;
      var fa = _cwWallOk(A.sg, A.end, UA, sA, skip);
      if (!fa.ok) return { ok: false, why: 'A ' + fa.why };
      var fb = _cwWallOk(B.sg, B.end, UB, sB, skip);
      if (!fb.ok) return { ok: false, why: 'B ' + fb.why };
      return { ok: true, UA: UA, UB: UB, H: H, fa: fa, fb: fb };
    }
    function _cwSetUnit(rec, sg, end, U, sill) {
      rec.code = U.code; rec.w = U.w; rec.h = U.h; rec.sill = sill; rec.kind = U.kind;
      rec.leaves = U.leaves; rec.hand = null; rec.out = null; rec.fullW = null; rec.halfW = null; rec.rake = null; rec.full = null;
      rec.at = _cwAt(sg, U.w, end);
    }
    function _cwApply(rA, rB, A, B, i, F) {
      _cwSetUnit(rA, A.sg, A.end, F.UA, F.fa.sill);
      _cwSetUnit(rB, B.sg, B.end, F.UB, F.fb.sill);
      rA.cwVar = i; rB.cwVar = i;
    }
    function _cwSayFit(tag, rA, rB, i, F) {
      _say('corner   ' + tag + ' ' + rA.seg + ' / ' + rB.seg + ' set ' + (i + 1) + '/' + CW_SETS.length + ' ' + CW_SETS[i].k +
           ' (' + F.UA.code + ' ' + Math.round(F.UA.w * 1000) + ' x ' + Math.round(F.UA.h * 1000) + ' + ' + F.UB.code + ' ' + Math.round(F.UB.w * 1000) + ' x ' + Math.round(F.UB.h * 1000) +
           ', heads at ' + Math.round(F.H * 1000) + ') | corner post kept, keep ' + Math.round(_openingKeep() * 1000) +
           ' | brace A: panel ' + Math.round(F.fa.panel.len * 1000) + ' from ' + Math.round(F.fa.panel.s * 1000) +
           ', B: panel ' + Math.round(F.fb.panel.len * 1000) + ' from ' + Math.round(F.fb.panel.s * 1000) +
           ' (need ' + Math.round(CW_BRACE_RATIO * 100) + ' % of the wall height within ' + Math.round(CW_BRACE_REACH * 1000) + ')');
    }
    /* the first set from i on (i included when self) that fits, or -1, with the reasons */
    function _cwFirst(i0, A, B, skip, self, why) {
      var n = CW_SETS.length, k;
      for (k = self ? 0 : 1; k < n; k++) {
        var i = (i0 + k) % n, F = _cwFit(i, A, B, skip);
        if (F.ok) return { i: i, F: F };
        if (why) why.push((i + 1) + ' ' + F.why);
      }
      return null;
    }
    function _cwPlace(seg, at) {
      if (!seg || !_cwSegIdx(seg.id)) { _log('corner window: tap an outside wall of the house'); _say('corner   refused: ' + (seg ? seg.id : 'no wall') + ' is not an outline wall'); return null; }
      var end = (at < seg.len / 2) ? 0 : 1, P = _cwPartner(seg, end);
      if (!P) { end = 1 - end; P = _cwPartner(seg, end); }
      if (!P) { _log('corner window: no outside corner at either end of this wall'); _say('corner   refused: no outside corner at either end of ' + seg.id); return null; }
      var A = { sg: seg, end: end }, B = P;
      /* a corner window already in this corner gives way to the new one --
         once the new one is known to fit; till then it stays where it is */
      var OP = STATE.openings, old = {}, oldR = [], q;
      for (q = 0; q < OP.length; q++) {
        var o = OP[q];
        if (o && o.cw && ((o.seg === A.sg.id && o.cwEnd === A.end) || (o.seg === B.sg.id && o.cwEnd === B.end))) old[o.cw] = 1;
      }
      for (q = 0; q < OP.length; q++) if (OP[q] && OP[q].cw && old[OP[q].cw]) oldR.push(OP[q]);
      var why = [], R = _cwFirst(0, A, B, oldR, true, why);
      if (!R) {
        _log('corner window: no set fits this corner -- a shorter wall, a window by the corner or too little wall left for bracing');
        _say('corner   refused at ' + A.sg.id + ' / ' + B.sg.id + ': ' + why.join('; '));
        return null;
      }
      for (q = OP.length - 1; q >= 0; q--) if (OP[q] && OP[q].cw && old[OP[q].cw]) OP.splice(q, 1);
      if (oldR.length) _say('corner   the corner window that stood here gave way (' + oldR.length + ' units)');
      _cwSeq++;
      var gid = 'cw' + Date.now().toString(36) + _cwSeq;
      var rA = { seg: A.sg.id, kind: 'window', cw: gid, cwSide: 'a', cwEnd: A.end, cwVar: 0 };
      var rB = { seg: B.sg.id, kind: 'window', cw: gid, cwSide: 'b', cwEnd: B.end, cwVar: 0 };
      _cwApply(rA, rB, A, B, R.i, R.F);
      OP.push(rA, rB);
      _cwSayFit('placed', rA, rB, R.i, R.F);
      if (why.length) _say('corner   skipped before it: ' + why.join('; '));
      savePref();
      opEdit(true);
      _opLast = rA;
      _log('corner window ' + CW_SETS[R.i].k + ' on ' + rA.seg + ' / ' + rB.seg);
      return rA;
    }
    /* the pair and its two walls, or null when it no longer stands at a corner */
    function _cwPair(rec) {
      var rB = _cwPeer(rec);
      if (!rB) return null;
      var rA = (rec.cwSide === 'b') ? rB : rec;
      rB = (rA === rec) ? rB : rec;
      var sgA = segment(rA.seg), sgB = segment(rB.seg);
      if (!sgA || !sgB) return null;
      var P = _cwPartner(sgA, rA.cwEnd);
      if (!P || P.sg.id !== rB.seg || P.end !== rB.cwEnd) return null;
      return { rA: rA, rB: rB, A: { sg: sgA, end: rA.cwEnd }, B: P };
    }
    function _cwCycle(rec) {
      if (!rec || !rec.cw || rec.cwSide === 'b') return null;
      var Q = _cwPair(rec);
      if (!Q) return null;
      var i0 = (+rec.cwVar || 0) % CW_SETS.length;
      return {
        label: CW_SETS[i0].k,
        next: function () {
          var why = [], R = _cwFirst(i0, Q.A, Q.B, [Q.rA, Q.rB], false, why);
          if (!R) { _say('corner   cycler: no other set fits ' + Q.rA.seg + ' / ' + Q.rB.seg + ' -- ' + why.join('; ')); return CW_SETS[i0].k; }
          _cwApply(Q.rA, Q.rB, Q.A, Q.B, R.i, R.F);
          _cwSayFit('cycled', Q.rA, Q.rB, R.i, R.F);
          if (why.length) _say('corner   skipped: ' + why.join('; '));
          return CW_SETS[R.i].k;
        }
      };
    }
    /* every build: each pair pinned to its corner; on a full build the set
       is checked against the wall as it is now and stepped on, or the pair
       let go as two ordinary units, when it no longer fits */
    function _cwUnlink(r, why) {
      var p = _cwPeer(r), L = [r].concat(p ? [p] : []), q;
      for (q = 0; q < L.length; q++) { delete L[q].cw; delete L[q].cwSide; delete L[q].cwEnd; delete L[q].cwVar; }
      _say('corner   let go as two ordinary units (' + L.map(function (x) { return x.seg; }).join(' / ') + '): ' + why);
    }
    function _cwSync(light) {
      var OP = STATE.openings || [], done = {}, i, n = 0;
      for (i = 0; i < OP.length; i++) {
        var r = OP[i];
        if (!r || !r.cw || done[r.cw]) continue;
        done[r.cw] = 1;
        var Q = _cwPair(r);
        if (!Q) { _cwUnlink(r, 'the two walls no longer meet at an outside corner'); continue; }
        n++;
        Q.rA.at = _cwAt(Q.A.sg, Q.rA.w, Q.A.end);
        Q.rB.at = _cwAt(Q.B.sg, Q.rB.w, Q.B.end);
        if (light) continue;
        var iv = (+Q.rA.cwVar || 0) % CW_SETS.length, F = _cwFit(iv, Q.A, Q.B, [Q.rA, Q.rB]);
        if (F.ok) {
          if (Q.rA.code !== F.UA.code || Q.rB.code !== F.UB.code) _cwApply(Q.rA, Q.rB, Q.A, Q.B, iv, F);
          _cwSayFit('ok', Q.rA, Q.rB, iv, F);
          continue;
        }
        var why = [F.why], R = _cwFirst(iv, Q.A, Q.B, [Q.rA, Q.rB], false, why);
        if (R) { _cwApply(Q.rA, Q.rB, Q.A, Q.B, R.i, R.F); _cwSayFit('refit', Q.rA, Q.rB, R.i, R.F); _say('corner   set ' + (iv + 1) + ' no longer fits: ' + why.join('; ')); }
        else _cwUnlink(Q.rA, 'no set fits any more: ' + why.join('; '));
      }
      if (n && light) _say('corner   ' + n + ' corner window(s) kept on their corners during the drag');
    }

    /* A GRIP YOU CAN HIT (core-v513, Marek: drag pointy schodu nejdou moc
       dobre chytnout): the gold dot stays 14 px, the element round it is a
       44 px transparent touch target with the dot in its middle */
    var _gripSaid = 0;
    function _gripTarget(el) {
      el.style.width = '44px'; el.style.height = '44px'; el.style.background = 'transparent';
      el.style.border = 'none'; el.style.boxShadow = 'none'; el.style.borderRadius = '50%';
      var dot = document.createElement('div');
      dot.style.cssText = 'position:absolute;left:50%;top:50%;transform:translate(-50%,-50%);width:14px;height:14px;border-radius:50%;' +
        'background:#C49A4A;border:2px solid #FFFFFF;box-shadow:0 1px 4px rgba(0,0,0,0.35);pointer-events:none;box-sizing:content-box;';
      el.appendChild(dot);
      if (!_gripSaid) { _gripSaid = 1; _log('grips  hit area 44 px (dot 14 px)'); }
    }
    function _holeEl(k) {
      var id = 'cf-hole-' + k, el = document.getElementById(id), host = _smpHost();
      if (el || !host) return el;
      el = document.createElement('div');
      el.id = id;
      /* OUR GOLD DOTS, as on the base and the plan (core-v359): small, solid,
         on the corners; the fill itself is what the opening is dragged by */
      el.className = '';
      el.style.cssText = 'position:absolute;transform:translate(-50%,-50%);touch-action:none;pointer-events:auto;display:none;' +
        'width:14px;height:14px;border-radius:50%;background:#C49A4A;border:2px solid #FFFFFF;' +
        'box-shadow:0 1px 4px rgba(0,0,0,0.35);z-index:12;';
      _gripTarget(el);

      host.appendChild(el);
      el.addEventListener('pointerdown', function (e) {
        var h = _hh.on, G = h && _holeGeom(h);
        if (!G) return;
        e.preventDefault(); e.stopPropagation();
        _hh.drag = { k: k, h: h, G: G, base: G.y0 - (h.sill || 0) };
        try { el.setPointerCapture(e.pointerId); } catch (x) {}
        var st = _stage(); if (st && st.controls) st.controls.enabled = false;
      });
      el.addEventListener('pointermove', function (e) {
        var D = _hh.drag;
        if (!D || D.k !== k) return;
        var G = D.G, h = D.h, step = 0, crs = 0;
        try { if (typeof __PROD._planStep === 'function') step = +__PROD._planStep() || 0; } catch (e1) {}
        try { if (typeof __PROD._courseH === 'function') crs = +__PROD._courseH() || 0; } catch (e2) {}
        /* the middle grip slides the whole opening along its wall */
        if (k === 'mid') {
          var tM = _smpAxisT(e, { c: [G.ax, G.y0, G.az], ax: [G.ux, 0, G.uz] });
          if (tM == null) return;
          var uM = tM - h.w / 2;
          if (step > 0.05) uM = Math.round(uM / step) * step;
          h.u = Math.max(0, Math.min(G.L - h.w, uM));
          snapHole(h);
          if (!_hh.raf) _hh.raf = (global.requestAnimationFrame || global.setTimeout)(function () { _hh.raf = 0; rebuild(); }, 16);
          return;
        }
        /* a corner moves both of its edges at once (core-v354) */
        var lo = (k === 'c00' || k === 'c01'), up = (k === 'c01' || k === 'c11');
        var tA = _smpAxisT(e, { c: [G.ax, G.y0, G.az], ax: [G.ux, 0, G.uz] });
        var tB = _smpAxisT(e, { c: [G.p0[0], _hh.drag.base, G.p0[2]], ax: [0, 1, 0] });
        if (tA != null) {
          /* the rounding belongs to the snap, which knows the wall's own grid;
             rounding here first only fought it and the width stopped moving */
          if (lo) { var e1p = h.u + h.w; h.u = Math.max(0, Math.min(e1p - 0.05, tA)); h.w = e1p - h.u; }
          else { h.w = Math.max(0.05, Math.min(G.L - h.u, tA - h.u)); }
        }
        if (tB != null) {
          if (crs > 0.02) tB = Math.round(tB / crs) * crs;
          if (!up) { var top = (h.sill || 0) + h.h; h.sill = Math.max(0, Math.min(top - (crs || 0.2), tB)); h.h = top - h.sill; }
          else { h.h = Math.max(crs || 0.2, tB - (h.sill || 0)); }
        }
        snapHole(h);
        if (!_hh.raf) _hh.raf = (global.requestAnimationFrame || global.setTimeout)(function () { _hh.raf = 0; rebuild(); }, 16);
      });
      function up(e) {
        if (!_hh.drag || _hh.drag.k !== k) return;
        _hh.drag = null;
        try { el.releasePointerCapture(e.pointerId); } catch (x) {}
        var st = _stage(); if (st && st.controls) st.controls.enabled = true;
        savePref();
        rebuild();
      }
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', up);
      return el;
    }
    /* in edit mode a tap picks the opening under the finger (core-v357) */
    /* WHICHEVER CORNER IS NEAREST THE FINGER (core-v374): the dots were the
       only thing that could start a resize, and a dot is a small target that
       gets smaller as the model is turned or zoomed -- so a resize kept coming
       out as a move. The canvas now measures the distance to the four corners
       itself and takes the touch as a resize whenever one is close. */
    function _holeCornerAt(e) {
      var h = _hh.on, G = h ? _holeGeom(h) : null, keys = ['c00', 'c10', 'c11', 'c01'], i, best = null;
      if (!G) return null;
      var P = { c00: [G.p0[0], G.y0, G.p0[2]], c10: [G.p1[0], G.y0, G.p1[2]],
                c11: [G.p1[0], G.y1, G.p1[2]], c01: [G.p0[0], G.y1, G.p0[2]] };
      for (i = 0; i < keys.length; i++) {
        var sc = _smpScreen(P[keys[i]]);
        if (!sc) continue;
        var d = Math.hypot(e.clientX - sc.x, e.clientY - sc.y);
        if (d < 34 && (!best || d < best.d)) best = { k: keys[i], d: d, G: G, h: h };
      }
      return best;
    }
    function _fhTapInside(e) {
      /* THE HOLE IS DRAGGED BY ITS WHOLE FOOTPRINT (core-v424, W7): a touch
         anywhere inside it on the plan starts a move, the corner dots stay
         for sizing. */
      if (!_fhOn() || _fhDrag) return false;
      return false;
      var H = _fhHook(), R = H ? H.get() : null; if (!R) return false;
      var y = storeys()[STATE.planStorey | 0] ? storeys()[STATE.planStorey | 0].y0 : 0;
      var tx = _smpAxisT(e, { c: [0, y, 0], ax: [1, 0, 0] }), tz = _smpAxisT(e, { c: [0, y, 0], ax: [0, 0, 1] });
      if (tx == null || tz == null) return false;
      var inX = tx > R.x0 + 0.05 && tx < R.x1 - 0.05, inZ = tz > R.z0 + 0.05 && tz < R.z1 - 0.05;
      if (!inX || !inZ) return false;
      e.preventDefault(); e.stopPropagation();
      _fhDrag = { k: 'mid', R0: { x0: R.x0, z0: R.z0, x1: R.x1, z1: R.z1 }, start: [tx, tz], canvas: true };
      var st = _stage(); if (st && st.controls) st.controls.enabled = false;
      return true;
    }
    function _fhCanvasMove(e) {
      var D = _fhDrag, H = _fhHook();
      if (!D || !D.canvas || !H) return;
      var y = storeys()[STATE.planStorey | 0] ? storeys()[STATE.planStorey | 0].y0 : 0;
      var tx = _smpAxisT(e, { c: [0, y, 0], ax: [1, 0, 0] }), tz = _smpAxisT(e, { c: [0, y, 0], ax: [0, 0, 1] });
      if (tx == null || tz == null) return;
      var dx = tx - D.start[0], dz = tz - D.start[1];
      _fhArm();
      H.set({ x0: D.R0.x0 + dx, z0: D.R0.z0 + dz, x1: D.R0.x1 + dx, z1: D.R0.z1 + dz });
    }
    function _fhCanvasUp() {
      var D = _fhDrag;
      if (!D || !D.canvas) return;
      _fhDrag = null; _fhArm();
      var st = _stage(); if (st && st.controls) st.controls.enabled = true;
      savePref(); rebuild(); drawPlan();
      var H = _fhHook(), R = H && H.get ? H.get() : null;
      if (R) _log('plan     stairwell moved -> at ' + Math.round((R.x0 + R.x1) * 500) + ', ' + Math.round((R.z0 + R.z1) * 500));
    }
    function _holeEditTap(e) {
      if (_fhTapInside(e)) return;
      if (_hh.drag) return;
      var near = _holeCornerAt(e);
      if (near) {
        e.preventDefault();
        e.stopPropagation();
        _hh.drag = { k: near.k, h: near.h, G: near.G, base: near.G.y0 - (near.h.sill || 0) };
        var stC = _stage();
        if (stC && stC.controls) stC.controls.enabled = false;
        return;
      }
      var ed = false;
      try { ed = !!opEdit(); } catch (e0) { ed = false; }
      if (!ed || _holeMode) return;
      var hit = _holeHit(e), HS = STATE.holes || [], i, best = null;
      if (!hit) return;
      /* THE MOVE STARTS IN THE MIDDLE 80 PER CENT (core-v400, Marek: posun
         bude treba 80 procent plochy, ne cela a ne az k drag points). A touch
         near a jamb belongs to the corner dots and must not slide the whole
         opening off under the finger; the fill drag is taken only from the
         inner 80 per cent of the width, and the outer strips are left to the
         corners. */
      for (i = 0; i < HS.length; i++) {
        if (!HS[i] || HS[i].kind !== hit.kind || HS[i].ref !== hit.ref) continue;
        var d = Math.abs((HS[i].u + HS[i].w / 2) - hit.u);
        if (d < HS[i].w * 0.40 && (!best || d < best.d)) best = { h: HS[i], d: d };
      }
      if (best) {
        editHole(best.h);
        try { _opBarMode = 'bin'; _opBinShow(); } catch (eB2) {}
        /* grabbed anywhere on the fill, so it slides along its wall from here */
        var G0 = _holeGeom(best.h);
        if (G0) {
          _hh.drag = { k: 'fill', h: best.h, G: G0, grab: hit.u - best.h.u };
          var st0 = _stage();
          if (st0 && st0.controls) st0.controls.enabled = false;
        }
        rebuild();
      }
    }
    function _holeCornerMove(e, D) {
      var h = D.h, G = D.G, k = D.k, step = 0, crs = 0;
      try { if (typeof __PROD._planStep === 'function') step = +__PROD._planStep() || 0; } catch (e1) {}
      try { if (typeof __PROD._courseH === 'function') crs = +__PROD._courseH() || 0; } catch (e2) {}
      var lo = (k === 'c00' || k === 'c01'), up = (k === 'c01' || k === 'c11');
      var tA = _smpAxisT(e, { c: [G.ax, G.y0, G.az], ax: [G.ux, 0, G.uz] });
      var tB = _smpAxisT(e, { c: [G.p0[0], D.base, G.p0[2]], ax: [0, 1, 0] });
      if (tA != null) {
        if (lo) { var e1p = h.u + h.w; h.u = Math.max(0, Math.min(e1p - 0.05, tA)); h.w = e1p - h.u; }
        else { h.w = Math.max(0.05, Math.min(G.L - h.u, tA - h.u)); }
      }
      if (tB != null) {
        if (crs > 0.02) tB = Math.round(tB / crs) * crs;
        if (!up) { var top = (h.sill || 0) + h.h; h.sill = Math.max(0, Math.min(top - (crs || 0.2), tB)); h.h = top - h.sill; }
        else { h.h = Math.max(crs || 0.2, tB - (h.sill || 0)); }
      }
      snapHole(h);
      if (!_hh.raf) _hh.raf = (global.requestAnimationFrame || global.setTimeout)(function () { _hh.raf = 0; rebuild(); }, 16);
    }
    function _holeFillMove(e) {
      if (_fhDrag && _fhDrag.canvas) { _fhCanvasMove(e); return; }
      var D = _hh.drag;
      if (D && D.k !== 'fill') { _holeCornerMove(e, D); return; }
      if (!D || D.k !== 'fill') return;
      var step = 0;
      try { if (typeof __PROD._planStep === 'function') step = +__PROD._planStep() || 0; } catch (e1) {}
      var G = D.G, h = D.h;
      var t = _smpAxisT(e, { c: [G.ax, G.y0, G.az], ax: [G.ux, 0, G.uz] });
      if (t == null) return;
      var u = t - D.grab;
      if (step > 0.05) u = Math.round(u / step) * step;
      h.u = Math.max(0, Math.min(G.L - h.w, u));
      snapHole(h);
      if (!_hh.raf) _hh.raf = (global.requestAnimationFrame || global.setTimeout)(function () { _hh.raf = 0; rebuild(); }, 16);
    }
    function _holeFillUp() {
      if (_fhDrag && _fhDrag.canvas) { _fhCanvasUp(); return; }
      if (!_hh.drag) return;
      _hh.drag = null;
      var st1 = _stage();
      if (st1 && st1.controls) st1.controls.enabled = true;
      savePref();
      rebuild();
    }
    function editHole(h) {
      _hh.on = h || null;
      if (_hh.on) { try { _opBarMode = 'bin'; _opBinShow(); } catch (eBB) {} }
      _log('hole     ' + (h ? ('#' + h.id + ' on the wall: drag its edges, the bin takes it away') : 'nothing selected'));
      _holeTrack();
    }
    function delHole(id) {
      var HS = STATE.holes || [], i;
      for (i = 0; i < HS.length; i++) if (HS[i] && HS[i].id === id) { HS.splice(i, 1); break; }
      _hh.on = null;
      _log('hole     #' + id + ' removed');
      savePref();
      rebuild();
    }
    /* the fill is touched on the canvas, so the canvas has to be listening
       from the start, not only after the Hole tile was pressed (core-v363) */
    function _holeWire() {
      var st = _stage();
      if (!st || !st.renderer || !st.renderer.domElement || st.renderer.domElement.__cfHoleEdit) return;
      st.renderer.domElement.__cfHoleEdit = 1;
      st.renderer.domElement.addEventListener('pointerdown', _holeEditTap, true);
      st.renderer.domElement.addEventListener('pointermove', _holeFillMove, true);
      st.renderer.domElement.addEventListener('pointerup', _holeFillUp, true);
      st.renderer.domElement.addEventListener('pointercancel', _holeFillUp, true);
      _log('hole     the wall is listening for taps on an opening');
    }
    /* THE TWO NUMBERS THAT MATTER WHILE DRAGGING (core-v365): the width under
       the opening and the height beside it, in the units the project is in */
    function _holeDim(k, txt, pos) {
      var id = 'cf-hole-dim-' + k, el = document.getElementById(id), host = _smpHost();
      if (!el) {
        if (!host) return;
        el = document.createElement('div');
        el.id = id;
        el.style.cssText = 'position:absolute;transform:translate(-50%,-50%);pointer-events:none;display:none;' +
          'padding:2px 7px;border-radius:9px;background:rgba(20,20,20,0.82);color:#F4E3C1;' +
          'font:600 12px/1.2 system-ui,-apple-system,sans-serif;white-space:nowrap;z-index:10;';
        host.appendChild(el);
      }
      if (!pos) { el.style.display = 'none'; return; }
      var sc = _smpScreen(pos), par = el.offsetParent || el.parentNode, pr = par ? par.getBoundingClientRect() : { left: 0, top: 0 };
      if (!sc) { el.style.display = 'none'; return; }
      el.textContent = txt;
      el.style.display = 'block';
      el.style.left = (sc.x - pr.left) + 'px';
      el.style.top = (sc.y - pr.top) + 'px';
    }
    /* A RECTANGLE IN THE FLOOR, EDITED ON THE PLAN (core-v418, X7b, Marek:
       user muze v pudoryse menit misto, posouvat, hybat drag pointy, ty se
       maji chytat i sten). A product hands the core a rectangle through
       __PROD._floorHole -- get() and set(rect) -- and on the plan of a storey
       above the ground floor the core shows four gold corner dots and one in
       the middle: a corner moves its two edges, the middle moves the whole
       hole. Edges snap to the outer walls and the walls inside within 300. */
    var _fhEls = {}, _fhDrag = null, _fhRaf = 0;
    /* WHAT THE STAIRWELL GRIPS LAST DID, FOR THE SNAPSHOT (core-v495, Marek:
       drag pointy nereaguji): which grip was pressed, how many moves reached
       it, the rectangle it asked for and the one the product kept */
    var _fhNote = 'no grip touched yet', _fhMoves = 0;
    function _fhR(R) { return R ? (Math.round((R.x1 - R.x0) * 1000) + ' x ' + Math.round((R.z1 - R.z0) * 1000) + ' at ' + Math.round((R.x0 + R.x1) * 500) + ', ' + Math.round((R.z0 + R.z1) * 500)) : 'none'; }
    /* THE HOLE IS ARMED BY ITS CORNERS (core-v472, Marek: overlay se aktivuje
       drag pointy v rozich a deaktivuje po 5 s necinnosti, pak je aktivni
       room name, fitouts): only the corner dots are live at first; touching
       one arms the hole -- the fill goes solid, the middle dot comes up and a
       touch inside it moves it; five seconds with no touch disarms it and a
       touch inside goes to the room under it again */
    var _fhArmedAt = 0, FH_ARM_MS = 5000;
    /* core-v476 (Marek: na otvoru drag point jako smerova sipka, porad
       zapnuty, drag jen za tento bod; mistnosti pak prepinat jednoduseji):
       the hole is always live but only by its handles -- the four-way arrow
       in the middle moves it, the corners size it; a touch anywhere else
       inside it goes to the room under it. Arming is gone. */
    function _fhArmed() { return true; }
    function _fhArm() { var was = _fhArmed(); _fhArmedAt = Date.now(); if (!was) _log('plan     stairwell armed -- drag it or its corners; 5 s idle disarms it'); }
    function _fhHook() { try { return (__PROD && __PROD._floorHole) ? __PROD._floorHole() : null; } catch (e) { return null; } }
    /* ON WHICH STOREY THE HOLE IS EDITED (core-v468, from the T house: the
       opening is edited on the floor where the stairs stand). A product that
       returns a number as storey from __PROD._floorHole() gets the grips on
       that storey's plan only -- the T house returns 0, the ground floor.
       Without it, as before: on any storey above the ground floor. */
    function _fhStoreyOk(H) {
      var ps = STATE.planStorey | 0;
      if (H && typeof H.storey === 'number' && isFinite(H.storey)) return ps === (H.storey | 0);
      return ps > 0;
    }
    function _fhOn() {
      var H = _fhHook();
      return !!(H && H.get && H.set && STATE.view === '2d' && _fhStoreyOk(H) && editing('layout'));
    }
    function _fhSnap(v, axisX) {
      /* THE INNER FACE, NOT THE WALL LINE (core-v424, W7, Marek: otvor se ma
         chytat vnitrni plochy obvodovych sten): an outer wall's line is its
         middle; the hole ends at the face the room sees, half a wall in. */
      var P = poly(), best = v, bd = 0.30, i, ps = _normParts(), tw = 0, cxp = 0, czp = 0;
      try { tw = wallThickness() || 0; } catch (e) { tw = 0; }
      for (i = 0; i < P.length; i++) { cxp += P[i][0]; czp += P[i][1]; }
      cxp /= Math.max(1, P.length); czp /= Math.max(1, P.length);
      for (i = 0; i < P.length; i++) {
        var c = axisX ? P[i][0] : P[i][1], cen = axisX ? cxp : czp;
        var face = c + (c < cen ? tw / 2 : -tw / 2);
        if (Math.abs(face - v) < bd) { bd = Math.abs(face - v); best = face; }
      }
      for (i = 0; i < ps.length; i++) {
        var isV = ((ps[i].o || ps[i].dir) === 'v');
        if (isV !== axisX) continue;
        var c2 = (typeof ps[i].c === 'number') ? ps[i].c : ps[i].at;
        if (typeof c2 === 'number' && Math.abs(c2 - v) < bd) { bd = Math.abs(c2 - v); best = c2; }
      }
      return best;
    }
    /* SEVERAL OPENINGS (core-v771, T house thread, Marek 2026-10-27: druhy
       otvor -- the apartment open to the roof with its own stair). A product
       whose _floorHole() hands list() (every rectangle) and setAt(i, R) gets
       the corners and the move handle on each of them; index 0 is get() /
       set() as before, and a product without list() has the one hole it
       always had. The turn handle stays on the first. */
    function _fhAll(H) {
      if (!H) return [];
      try { if (typeof H.list === 'function') { var L = H.list() || [], out = [], i; for (i = 0; i < L.length; i++) if (L[i]) out.push(L[i]); return out; } } catch (eL) {}
      var R0 = H.get ? H.get() : null;
      return R0 ? [R0] : [];
    }
    function _fhGetI(H, idx) { var L = _fhAll(H); return L[idx | 0] || null; }
    function _fhSetI(H, R, idx) {
      if ((idx | 0) > 0 && typeof H.setAt === 'function') { H.setAt(idx | 0, R); return; }
      H.set(R);
    }
    var _fhMaxIdx = 0;
    function _fhEl(k, idx) {
      idx = idx | 0;
      var id = 'cf-fh-' + k + (idx ? ('-' + idx) : ''), el = document.getElementById(id), host = _smpHost();
      if (idx > _fhMaxIdx) _fhMaxIdx = idx;
      if (el || !host) return el;
      el = document.createElement('div');
      el.id = id;
      el.style.cssText = 'position:absolute;transform:translate(-50%,-50%);touch-action:none;pointer-events:auto;display:none;' +
        'width:14px;height:14px;border-radius:50%;background:#C49A4A;border:2px solid #FFFFFF;box-shadow:0 1px 4px rgba(0,0,0,0.35);z-index:12;';
      if (k !== 'mid' && k !== 'rot') _gripTarget(el);
      if (k === 'mid') {
        el.style.width = '34px'; el.style.height = '34px'; el.style.borderRadius = '50%';
        el.style.display = 'none'; el.style.alignItems = 'center'; el.style.justifyContent = 'center';
        el.innerHTML = '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">' +
          '<path d="M12 3v18M3 12h18"/><path d="M9 6l3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3"/></svg>';
      }
      /* THE TURN HANDLE (core-v492, Marek: ikona otaceni schodu v
         pudorysu): a product whose _floorHole() hands a rotate() gets a
         round button beside the hole's far corner; a tap turns the flight a
         quarter and the hole with it */
      if (k === 'rot') {
        /* on the flight's axis like the move handle, black with a white glyph
           (core-v493, Marek: tlacitko rotace na osu schodiste jako move,
           cerne pozadi, bila ikona) */
        el.style.width = '34px'; el.style.height = '34px'; el.style.borderRadius = '50%';
        el.style.background = '#1A1C1D';
        el.style.display = 'none'; el.style.alignItems = 'center'; el.style.justifyContent = 'center';
        el.innerHTML = '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="#FFFFFF" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">' +
          '<path d="M20 12a8 8 0 1 1-2.34-5.66"/><path d="M20 4v5h-5"/></svg>';
      }
      host.appendChild(el);
      el.addEventListener('pointerdown', function (e) {
        var H = _fhHook(); if (!H) return;
        if (k === 'rot') {
          e.preventDefault(); e.stopPropagation();
          if (typeof H.rotate === 'function') {
            H.rotate();
            savePref(); rebuild(); drawPlan();
            _log('plan     stairwell turned -> ' + (typeof H.dir === 'function' ? H.dir() : '?'));
          }
          return;
        }
        e.preventDefault(); e.stopPropagation();
        var R = _fhGetI(H, idx);
        if (!R) return;
        _fhArm();
        _fhDrag = { k: k, idx: idx, R0: { x0: R.x0, z0: R.z0, x1: R.x1, z1: R.z1 }, start: null };
        _fhMoves = 0; _fhNote = 'grip ' + k + (idx ? (' of opening ' + (idx + 1)) : '') + ' down on ' + _fhR(R);
        try { el.setPointerCapture(e.pointerId); } catch (x) {}
        var st = _stage(); if (st && st.controls) st.controls.enabled = false;
      });
      el.addEventListener('pointermove', function (e) {
        var D = _fhDrag, H = _fhHook();
        if (!D || D.k !== k || (D.idx | 0) !== idx || !H) return;
        var y = storeys()[STATE.planStorey | 0] ? storeys()[STATE.planStorey | 0].y0 : 0;
        var tx = _smpAxisT(e, { c: [0, y, 0], ax: [1, 0, 0] }), tz = _smpAxisT(e, { c: [0, y, 0], ax: [0, 0, 1] });
        if (tx == null || tz == null) { _fhNote = 'grip ' + k + ': a move gave no point on the plan (axis hit null)'; return; }
        if (D.start == null) { D.start = [tx, tz]; return; }
        _fhMoves++;
        _fhArm();
        var dx = tx - D.start[0], dz = tz - D.start[1], R = { x0: D.R0.x0, z0: D.R0.z0, x1: D.R0.x1, z1: D.R0.z1 };
        if (k === 'mid') { R.x0 += dx; R.x1 += dx; R.z0 += dz; R.z1 += dz; }
        else {
          if (k === 'c00' || k === 'c01') R.x0 = Math.min(D.R0.x1 - 0.6, _fhSnap(D.R0.x0 + dx, true)); else R.x1 = Math.max(D.R0.x0 + 0.6, _fhSnap(D.R0.x1 + dx, true));
          if (k === 'c00' || k === 'c10') R.z0 = Math.min(D.R0.z1 - 0.6, _fhSnap(D.R0.z0 + dz, false)); else R.z1 = Math.max(D.R0.z0 + 0.6, _fhSnap(D.R0.z1 + dz, false));
        }
        _fhSetI(H, R, idx);
        try { _fhNote = 'grip ' + k + (idx ? (' of opening ' + (idx + 1)) : '') + ' moving (' + _fhMoves + ' moves): asked ' + _fhR(R) + ', product keeps ' + _fhR(_fhGetI(H, idx)); } catch (eN) {}
        /* NO REBUILD WHILE THE FINGER IS DOWN (core-v420, Marek: nemusis to
           porad v pudoryse prepocitavat, premodeluj az pri prepnuti do 3d).
           The dots follow the finger from the tracking loop; the floor is
           rebuilt once, when the finger lifts. A half-updated rectangle was
           what left a joist running through the hole. */
      });
      function up(e) {
        if (!_fhDrag || _fhDrag.k !== k || (_fhDrag.idx | 0) !== idx) return;
        _fhDrag = null; _fhArm();
        try { el.releasePointerCapture(e.pointerId); } catch (x) {}
        var st = _stage(); if (st && st.controls) st.controls.enabled = true;
        savePref(); rebuild(); drawPlan();
        var H = _fhHook(), R = H ? _fhGetI(H, idx) : null;
        if (R) _log('plan     stairwell' + (idx ? (' ' + (idx + 1)) : '') + ' -> ' + Math.round((R.x1 - R.x0) * 1000) + ' x ' + Math.round((R.z1 - R.z0) * 1000) + ' at ' + Math.round((R.x0 + R.x1) * 500) + ', ' + Math.round((R.z0 + R.z1) * 500));
        _fhNote += ' | up after ' + _fhMoves + ' move(s), kept ' + _fhR(R);
      }
      el.addEventListener('pointerup', up);
      el.addEventListener('pointercancel', up);
      return el;
    }
    /* THE HOLE'S FILL FOLLOWS THE FINGER (core-v471, Marek: magenta overlay
       se neposouva plynule s drag points): a magenta 50 per cent polygon on
       the four corners, drawn by the same tracking loop as the dots, so it
       moves with them every frame instead of waiting for the rebuild on
       release */
    function _fhFill(pts, armed, idx) {
      var host = _smpHost(); if (!host) return;
      var fid = 'cf-fh-fill' + ((idx | 0) ? ('-' + (idx | 0)) : '');
      var sv = document.getElementById(fid);
      if (!sv && !pts) return;
      if (!sv) {
        sv = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        sv.id = fid;
        sv.setAttribute('style', 'position:absolute;left:0;top:0;width:100%;height:100%;pointer-events:none;z-index:11;overflow:visible;display:none;');
        var pg = document.createElementNS('http://www.w3.org/2000/svg', 'polygon');
        pg.setAttribute('fill', 'rgba(255,0,255,0.5)');
        pg.setAttribute('stroke', '#2B2B2B'); pg.setAttribute('stroke-width', '1.5');
        sv.appendChild(pg);
        host.appendChild(sv);
      }
      if (!pts) { sv.style.display = 'none'; return; }
      var pr = host.getBoundingClientRect(), q, str = [];
      for (q = 0; q < pts.length; q++) str.push((pts[q].x - pr.left).toFixed(1) + ',' + (pts[q].y - pr.top).toFixed(1));
      sv.firstChild.setAttribute('points', str.join(' '));
      sv.firstChild.setAttribute('fill', 'rgba(255,0,255,0.35)');
      sv.firstChild.setAttribute('stroke-dasharray', '');
      sv.style.display = 'block';
    }
    function _fhTick() {
      var on = _fhOn(), H = on ? _fhHook() : null, HL = H ? _fhAll(H) : [], hi;
      for (hi = 0; hi < Math.max(HL.length, 1); hi++) _fhTick1(H, HL[hi] || null, hi);
      /* an opening taken away leaves no grips behind */
      for (hi = Math.max(HL.length, 1); hi <= _fhMaxIdx; hi++) _fhTick1(null, null, hi);
    }
    function _fhTick1(H, R, idx) {
      var keys = ['c00', 'c10', 'c11', 'c01', 'mid', 'rot'], i;
      var y = storeys()[STATE.planStorey | 0] ? storeys()[STATE.planStorey | 0].y0 : 0;
      var fillPts = [];
      /* THE MOVE AND THE TURN HANDLE SHARE THE FLIGHT'S AXIS, EVENLY
         (core-v494, Marek: stejnomerne rozmistene ve stairs footprint, move
         ne na stredu; ikony se zmensuji / zvetsuji podle footprintu): with a
         turn handle the two stand at a third and two thirds of the opening's
         length along the climb, move nearer the foot; both are sized from the
         opening's width on screen. Without one, move stays in the middle. */
      var hasRot = !!(H && typeof H.rotate === 'function') && !idx, posMid = null, posRot = null, szH = 34;
      if (R) {
        var cxR = (R.x0 + R.x1) / 2, czR = (R.z0 + R.z1) / 2;
        var dR = (H && typeof H.dir === 'function') ? String(H.dir() || '+z') : '+z', axX = (dR === '+x' || dR === '-x'), sgR = (dR.charAt(0) === '-') ? -1 : 1;
        var LR = axX ? (R.x1 - R.x0) : (R.z1 - R.z0), WR = axX ? (R.z1 - R.z0) : (R.x1 - R.x0), qR = LR / 6;
        if (hasRot) {
          posMid = axX ? [cxR - sgR * qR, y, czR] : [cxR, y, czR - sgR * qR];
          posRot = axX ? [cxR + sgR * qR, y, czR] : [cxR, y, czR + sgR * qR];
        } else posMid = [cxR, y, czR];
        try {
          var sA = _smpScreen(axX ? [cxR, y, czR - WR / 2] : [cxR - WR / 2, y, czR]), sB = _smpScreen(axX ? [cxR, y, czR + WR / 2] : [cxR + WR / 2, y, czR]);
          var sL0 = _smpScreen(axX ? [cxR - LR / 2, y, czR] : [cxR, y, czR - LR / 2]), sL1 = _smpScreen(axX ? [cxR + LR / 2, y, czR] : [cxR, y, czR + LR / 2]);
          if (sA && sB) {
            var wPx = Math.hypot(sB.x - sA.x, sB.y - sA.y), lPx = (sL0 && sL1) ? Math.hypot(sL1.x - sL0.x, sL1.y - sL0.y) : 1e9;
            szH = Math.round(Math.max(20, Math.min(52, Math.min(wPx * 0.55, lPx / 4))));
          }
        } catch (eSz) { szH = 34; }
      }
      for (i = 0; i < keys.length; i++) {
        if (!R && !document.getElementById('cf-fh-' + keys[i] + (idx ? ('-' + idx) : ''))) continue;
        var el = _fhEl(keys[i], idx);
        if (!el) continue;
        if (!R || (keys[i] === 'mid' && !_fhArmed())) { el.style.display = 'none'; continue; }
        if (keys[i] === 'rot' && !hasRot) { el.style.display = 'none'; continue; }
        var pos;
        if (keys[i] === 'c00') pos = [R.x0, y, R.z0]; else if (keys[i] === 'c10') pos = [R.x1, y, R.z0];
        else if (keys[i] === 'c11') pos = [R.x1, y, R.z1]; else if (keys[i] === 'c01') pos = [R.x0, y, R.z1];
        else if (keys[i] === 'rot') pos = posRot;
        else pos = posMid;
        var big = (keys[i] === 'mid' || keys[i] === 'rot');
        if (big) {
          var szS = szH + 'px';
          if (el.style.width !== szS) {
            el.style.width = szS; el.style.height = szS;
            var svgE = el.querySelector && el.querySelector('svg');
            if (svgE) { var gS = String(Math.round(szH * 0.64)); svgE.setAttribute('width', gS); svgE.setAttribute('height', gS); }
          }
        }
        var sc = _smpScreen(pos), par = el.offsetParent || el.parentNode, pr = par ? par.getBoundingClientRect() : { left: 0, top: 0 };
        if (sc) { el.style.display = big ? 'flex' : 'block'; el.style.left = (sc.x - pr.left) + 'px'; el.style.top = (sc.y - pr.top) + 'px'; if (!big) fillPts.push(sc); }
      }
      try { _fhFill((R && fillPts.length === 4) ? fillPts : null, _fhArmed(), idx); } catch (eF) {}
    }
    function _holeTrack() {
      _holeWire();
      if (_hh.tracking) return;
      _hh.tracking = true;
      function tick() {
        try { _fhTick(); } catch (eFh) {}
        try { _opcTick(); } catch (eOc) {}
        var h = _hh.on, G = h ? _holeGeom(h) : null, keys = ['c00', 'c10', 'c11', 'c01'], i;
        for (i = 0; i < keys.length; i++) {
          var el = _holeEl(keys[i]);
          if (!el) continue;
          var stepOn = true;
          try { stepOn = !!editing('openings'); } catch (eS1) { stepOn = true; }
          if (!G || !stepOn || STATE.view !== '3d') { el.style.display = 'none'; continue; }
          /* on the corners, the same grips the plan and the terrace use */
          var pos;
          if (keys[i] === 'c00') pos = [G.p0[0], G.y0, G.p0[2]];
          else if (keys[i] === 'c10') pos = [G.p1[0], G.y0, G.p1[2]];
          else if (keys[i] === 'c11') pos = [G.p1[0], G.y1, G.p1[2]];
          else pos = [G.p0[0], G.y1, G.p0[2]];
          var sc = _smpScreen(pos), par = el.offsetParent || el.parentNode, pr = par ? par.getBoundingClientRect() : { left: 0, top: 0 };
          if (sc) {
            el.style.display = 'block';
            el.style.left = (sc.x - pr.left) + 'px';
            el.style.top = (sc.y - pr.top) + 'px';
          }
        }
        var dimOn = true;
        try { dimOn = !!editing('openings'); } catch (eS2) { dimOn = true; }
        if (G && dimOn && STATE.view === '3d') {
          _holeDim('w', fmtLen(h.w), [(G.p0[0] + G.p1[0]) / 2, G.y0 - 0.18, (G.p0[2] + G.p1[2]) / 2]);
          _holeDim('h', fmtLen(h.h), [G.p0[0] - G.ux * 0.22, (G.y0 + G.y1) / 2, G.p0[2] - G.uz * 0.22]);
        } else {
          _holeDim('w', '', null);
          _holeDim('h', '', null);
        }
        _rafQuiet(tick);
      }
      _rafQuiet(tick);
    }
    function addHole(at) {
      var b = _planBounds(), ps = _normParts(), step = 0, best = null, i, L;
      try { if (typeof __PROD._planStep === 'function') step = +__PROD._planStep() || 0; } catch (eS) { step = 0; }
      var cand = [];
      var PO = poly(), q;
      for (q = 0; q < PO.length; q++) {
        var e0 = PO[q], e1 = PO[(q + 1) % PO.length];
        L = Math.abs(e1[0] - e0[0]) + Math.abs(e1[1] - e0[1]);
        if (L > 1.2) cand.push({ kind: 'wall', ref: q, len: L });
      }
      for (i = 0; i < ps.length; i++) {
        var sp = _partSpanH(ps[i]);
        if (!sp) continue;
        L = sp[1] - sp[0];
        if (ps[i].sep) continue;          /* nor this (core-v536) */
        if (L > 1.2) cand.push({ kind: ps[i].bear ? 'bearing' : 'partition', ref: ps[i].id, len: L });
      }
      for (i = 0; i < cand.length; i++) {
        var used = 0, q2, HS = STATE.holes || [];
        for (q2 = 0; q2 < HS.length; q2++) if (HS[q2] && HS[q2].kind === cand[i].kind && HS[q2].ref === cand[i].ref) used++;
        cand[i].score = cand[i].len - used * 2.5;
        if (!best || cand[i].score > best.score) best = cand[i];
      }
      if (at && at.len > 1.2) best = { kind: at.kind, ref: at.ref, len: at.len, at: at.u };
      if (!best) { _log('opening + none: no wall long enough yet'); return null; }
      var w = step > 0.05 ? Math.max(step, Math.round(1.0 / step) * step) : 1.0;
      if (w > best.len - 0.6) w = Math.max(0.6, best.len - 0.6);
      var u = (typeof best.at === 'number') ? Math.max(0, Math.min(best.len - w, best.at - w / 2)) : ((best.len - w) / 2);
      if (step > 0.05) u = Math.max(step, Math.round(u / step) * step);
      STATE.holes = STATE.holes || [];
      var stH = STATE.planStorey | 0, S2 = storeys(), liftH = (S2[stH] && S2[0]) ? (S2[stH].y0 - S2[0].y0) : 0;
      var h = { id: (STATE.holes.length ? (STATE.holes[STATE.holes.length - 1].id + 1) : 1),
                kind: best.kind, ref: best.ref, u: Math.round(u * 1000) / 1000, w: Math.round(w * 1000) / 1000,
                sill: Math.round((0.9 + liftH) * 10000) / 10000, h: 1.4, st: stH };
      snapHole(h);
      STATE.holes.push(h);
      editHole(h);
      _log('opening + ' + best.kind + ' ' + best.ref + ' at ' + Math.round(u * 1000) + ' wide ' + Math.round(w * 1000) +
           (step > 0.05 ? (' (stepped by ' + Math.round(step * 1000) + ')') : ''));
      savePref();
      rebuild();
      return h;
    }
    /* A HOLE IS SIZED BY THE BLOCKS (core-v349, Marek: na vysku odstranovat
       cele rady, na sirku se chytat koncu bloku). Its width and its distance
       along the wall go on the block's module, so the jambs fall on head
       joints and only every other course needs a cut; its sill and its head go
       on whole courses, so no course is ever cut through lengthwise. A product
       that gives neither module nor course keeps whatever was set. */
    /* EVERY OPENING IN ONE SHAPE (core-v364), so a product that lays masonry
       cuts a window and a door out of the wall exactly as it cuts a plain
       hole: the wall it is in, where it starts along that wall, how wide, how
       high and off what sill. Windows and doors keep their own record; this
       is only the view of them the wall needs. */
    var _wopSaid = 0;
    function wallOpenings() {
      var out = [], OP = STATE.openings || [], i, sg;
      /* THE FLOOR UNIT'S SILL IS SETTLED HERE, WHERE THE WALL READS IT
         (core-v398): the pass that wrote it onto the record was not reaching
         the record, so the sill stayed on the slab and nothing moved. Whatever
         the record says, a unit that stands at the floor is handed to the wall
         with its sill at the floor build-up, and the log says so every build. */
      var fsR = 0;
      try { var wbR = (typeof __PROD._wallBaseY === 'function') ? +__PROD._wallBaseY() : storeys()[0].y0; fsR = Math.max(0, storeys()[0].y0 - wbR); } catch (eR) { fsR = 0; }
      var said = [];
      /* AN OPENING ON AN UPPER STOREY IS MEASURED FROM ITS OWN FLOOR
         (core-v405): the wall's sill is counted from the foot of the wall, so
         a unit on storey two carries the height of the storeys below it, and a
         floor unit there stands on that storey's finished floor. */
      var S = storeys();
      for (i = 0; i < OP.length; i++) {
        sg = segment(OP[i].seg);
        if (!sg) continue;
        var stI = sg.storey | 0, lift = (S[stI] && S[0]) ? (S[stI].y0 - S[0].y0) : 0;
        var sl = OP[i].sill || 0;
        var floorU = (OP[i].kind === 'door' || OP[i].kind === 'french' || OP[i].kind === 'garage' || OP[i].kind === 'portal' || sl <= fsR + 0.02);
        if (floorU) sl = fsR;
        /* A UNIT THE PRODUCT STANDS ON THE WALL'S FOOT (core-v616, RANCH
           ranch-v36): a garage door on the garage's bare slab, under the
           house's floor build-up -- the hole, the unit and the reveals all
           read it from here */
        var onFoot = false;
        if (floorU && stI === 0 && typeof __PROD._unitOnFoot === 'function') { try { onFoot = !!__PROD._unitOnFoot(OP[i]); } catch (eUF) { onFoot = false; } }
        if (onFoot) sl = 0;
        sl += lift;
        said.push((OP[i].code || OP[i].kind) + ' storey ' + (stI + 1) + ' ' + (onFoot ? 'on the wall\'s foot' : (floorU ? 'on the floor' : 'in the wall')) + ' sill ' + Math.round(sl * 1000) + ' head ' + Math.round((sl + (OP[i].h || 0)) * 1000));
        out.push({ id: 'op' + i, kind: 'wall', ref: sg.edge, unit: OP[i].kind, code: OP[i].code || null, rake: !!OP[i].rake, rec: OP[i],
                   u: Math.max(0, OP[i].at - OP[i].w / 2), w: OP[i].w,
                   sill: sl, h: OP[i].h || 0 });
      }
      if (said.length && !_wopSaid) { _wopSaid = 1; _say('hole     units for the wall, floor build-up ' + Math.round(fsR * 1000) + ': ' + said.join('; ')); }
      return out;
    }
    var _holeSnapSay = 0, _holeRepass = 0;
    /* where the walls inside meet the outer run an opening sits in, as
       distances along that run: both faces of each load-bearing wall */
    function _innerFacesOn(h) {
      var out = [], P = poly(), ps = _normParts(), i, tw = 0;
      if (!h || h.kind !== 'wall' || !P[h.ref]) return out;
      try { tw = wallThickness() || 0; } catch (e) { tw = 0; }
      var a = P[h.ref], b = P[(h.ref + 1) % P.length], dx = b[0] - a[0], dz = b[1] - a[1], L = Math.hypot(dx, dz) || 1, ux = dx / L, uz = dz / L;
      var along = Math.abs(dx) > Math.abs(dz);
      for (i = 0; i < ps.length; i++) {
        var p = ps[i];
        if (!p || !p.bear) continue;
        var isV = ((p.o || p.dir) === 'v'), c = (typeof p.c === 'number') ? p.c : p.at;
        if (typeof c !== 'number') continue;
        if (along !== isV) continue;
        var u = along ? (c - a[0]) * (ux < 0 ? -1 : 1) : (c - a[1]) * (uz < 0 ? -1 : 1);
        if (u < -0.01 || u > L + 0.01) continue;
        out.push(u - tw / 2); out.push(u + tw / 2);
      }
      return out;
    }
    /* WHAT A MASONRY WALL HAS TO TAKE OUT OF A COURSE (core-v376). Every
       product that lays blocks needs the same three answers for a course: what
       to leave out of it, where a lintel goes, and how many of its openings it
       met. Having it here means a product supplies its block and its wall, and
       the openings behave the same in all of them. Bands come back in the
       wall's own coordinates, measured from the corner the wall starts at. */
    function openingBands(ref, kind, yb, yt, crs, lintelBear) {
      var HS = (STATE.holes || []).concat(kind === 'wall' ? wallOpenings() : []), i, out = { bands: [], lintels: [], n: 0 };
      var bear = (lintelBear == null) ? 0.125 : lintelBear;
      for (i = 0; i < HS.length; i++) {
        var h = HS[i];
        if (!h || h.kind !== kind || h.ref !== ref) continue;
        var y0 = (h.sill || 0), y1 = y0 + (h.h || 0), y2 = y1 + (crs || 0);
        if (yt <= y0 + 0.001 || yb >= y2 - 0.001) continue;
        var over = (yb >= y1 - 0.001);
        var b0 = over ? (h.u - bear) : h.u, b1 = over ? (h.u + h.w + bear) : (h.u + h.w);
        out.bands.push([b0, b1]);
        if (over) out.lintels.push({ id: h.id, u0: b0, u1: b1, unit: h.unit || 'hole' });
        else out.n++;
      }
      return out;
    }
    /* THE UNIT ITSELF, ONCE, FOR EVERY PRODUCT (core-v377, Marek: okna jsou
       vsude, jen se muze v konkretnim projektu upravit nabidka). A window is a
       frame, its glass, and a sill on each face; a door is the same without
       the glass reaching the floor. The catalogue of what can be chosen stays
       with the product -- this only builds what was chosen, in the wall the
       product describes: how thick it is, and how far in the unit sits. */
    /* the catalogue row a placed unit was picked from, by its code (core-v518) */
    /* core-v809: a round light (OWC, OPENING_CATALOG round) */
    function _roundOp(o) { if (!o || !o.code) return false; if (o.round) return true; var it = _openingItem(o.code); return !!(it && it.round); }
    function _openingItem(code) {
      if (!code) return null;
      var CAT = (typeof OPENING_CATALOG !== 'undefined') ? OPENING_CATALOG : ((_UI() && _UI().OPENING_CATALOG) || {}), k, i, G;
      for (k in CAT) {
        if (!CAT.hasOwnProperty(k)) continue;
        G = CAT[k];
        for (i = 0; G && G.items && i < G.items.length; i++) if (G.items[i].code === code) return G.items[i];
      }
      return null;
    }
    function buildOpenings(T, g, opt) {
      opt = opt || {};
      var t = +opt.t || 0.3, inset = (opt.inset == null) ? 0.08 : +opt.inset;
      var fr = (opt.frame == null) ? 0.06 : +opt.frame, sillOut = (opt.sillOut == null) ? 0.03 : +opt.sillOut;
      var L = wallOpenings(), i, n = 0, tally = { kinds: {}, codes: {}, noCode: 0, sill: 0, sillIn: 0, thresh: 0, perim: 0 };
      var mk = function (w, h, d, x, y, z, rot, col, op) {
        var m = new T.Mesh(new T.BoxGeometry(w, h, d), _mat(T, col, op == null ? 1 : op));
        m.position.set(x, y, z);
        if (rot) m.rotation.y = rot;
        g.add(m);
      };
      var nSkip = 0;
      for (i = 0; i < L.length; i++) {
        /* A UNIT THE PRODUCT DRAWS AND BILLS ITSELF (core-v491, R4): a glazed
           gable triangle is not a rectangle in a wall */
        if (L[i].rake || (typeof opt.skip === 'function' && opt.skip(L[i]))) { nSkip++; continue; }
        var G = _holeGeom(L[i]);
        if (!G) continue;
        /* THE FRAME REACHES ITS LINTEL (core-v389): the opening in the wall is
           the structural one -- its head was taken up to the bed joint the
           lintel bears on -- and the unit was drawn at the size the product
           offers, which left daylight over the frame. The unit is drawn to the
           opening itself instead, less a joint all round, so what shows over
           the frame is the joint and nothing else. */
        var JNT2 = (opt.joint == null) ? 0.010 : +opt.joint;
        var w = Math.max(0.1, L[i].w - 2 * JNT2), hh = Math.max(0.1, L[i].h - 2 * JNT2);
        var cx = (G.p0[0] + G.p1[0]) / 2, cz = (G.p0[2] + G.p1[2]) / 2, cy = (G.y0 + G.y1) / 2;
        var rot = Math.atan2(G.ux, G.uz) + Math.PI / 2;
        var nx = -G.uz, nz = G.ux;       /* across the wall */
        var dIn = t / 2 - inset;          /* the unit sits this far from the middle */
        /* A ROUND LIGHT IN A SQUARE HOLE (core-v518, Marek: doplnit do
           moznosti vkladani oken i tyto dve kulata okna). The wall knows
           only rectangles -- the blocks are cut square, the lintel bears as
           for any hole, the facade and the plaster return square -- so the
           rough opening stays the rectangle the catalogue row asked for
           (its head taken to the course like every other). In it the unit is
           drawn round: a solid ring 60 deep for the frame, one disc of glass,
           and the four corners between the square and the circle closed by
           the wall itself, from the inner face of the masonry to the
           finished face of the facade, in the reveal's shade through the
           masonry and the facade's own shade past it. No sill boards -- a
           porthole has no sill -- and the joint round it is the circle's
           length. The unit is billed by the piece on its own row. */
        var ITr = _openingItem(L[i].code);
        if (ITr && ITr.round) {
          var dia = ITr.w || Math.min(L[i].w, L[i].h);
          var Rr = Math.min(dia / 2, (Math.min(L[i].w, L[i].h) - 2 * JNT2) / 2), rIn = Math.max(0.05, Rr - fr);
          var cyR = (G.y0 + G.y1) / 2, ox = cx - nx * dIn, oz = cz - nz * dIn;
          var qn = new T.Quaternion().setFromUnitVectors(new T.Vector3(0, 1, 0), new T.Vector3(nx, 0, nz));
          var prof = [new T.Vector2(rIn, -0.03), new T.Vector2(Rr, -0.03), new T.Vector2(Rr, 0.03), new T.Vector2(rIn, 0.03), new T.Vector2(rIn, -0.03)];
          var ring = new T.Mesh(new T.LatheGeometry(prof, 48), _mat(T, 0xF2F0EA, 1));
          ring.position.set(ox, cyR, oz); ring.quaternion.copy(qn); g.add(ring);
          var pane = new T.Mesh(new T.CylinderGeometry(rIn + 0.005, rIn + 0.005, 0.012, 48), _mat(T, 0x9FC4D6, 0.45));
          pane.position.set(ox, cyR, oz); pane.quaternion.copy(qn); g.add(pane);
          var FTr = 0; try { FTr = facadeThk() || 0; } catch (eFT) { FTr = 0; }
          var colF = 0xD9D6CC; try { colF = (cladKind() === 'off') ? colorSlotOf('facade') : colorSlotOf('timber'); } catch (eCF) {}
          var W2 = L[i].w / 2, qd, k2, NSEG = 16;
          var prism = function (pts, o0, o1, col) {
            var sh = new T.Shape(), ar = 0, k3;
            for (k3 = 0; k3 < pts.length; k3++) { var p1 = pts[k3], p2 = pts[(k3 + 1) % pts.length]; ar += p1[0] * p2[1] - p2[0] * p1[1]; }
            var P2 = (ar < 0) ? pts.slice().reverse() : pts;
            for (k3 = 0; k3 < P2.length; k3++) { if (k3 === 0) sh.moveTo(P2[k3][0], P2[k3][1]); else sh.lineTo(P2[k3][0], P2[k3][1]); }
            var geo = new T.ExtrudeGeometry(sh, { depth: o1 - o0, bevelEnabled: false });
            var M = new T.Matrix4().makeBasis(new T.Vector3(G.ux, 0, G.uz), new T.Vector3(0, 1, 0), new T.Vector3(nx, 0, nz));
            M.setPosition(cx + nx * o0, 0, cz + nz * o0);
            geo.applyMatrix4(M);
            g.add(new T.Mesh(geo, _mat(T, col, 1)));
          };
          for (qd = 0; qd < 4; qd++) {
            var sx = (qd === 0 || qd === 3) ? 1 : -1, sy = (qd < 2) ? 1 : -1, yE = (sy > 0) ? G.y1 : G.y0;
            var pts = [[sx * Rr, cyR], [sx * W2, cyR], [sx * W2, yE], [0, yE], [0, cyR + sy * Rr]];
            for (k2 = 1; k2 < NSEG; k2++) { var ang = (Math.PI / 2) * (1 - k2 / NSEG); pts.push([sx * Rr * Math.cos(ang), cyR + sy * Rr * Math.sin(ang)]); }
            prism(pts, -t / 2, t / 2, 0xE9E4DA);
            /* a product that cuts its own round holes in the facade (T house,
               thouse-v296) gets the corners through the masonry only (core-v531) */
            if (FTr > 0.001 && !(__PROD && __PROD._ownRoundFacade)) prism(pts, -(t / 2 + FTr), -t / 2, colF);
          }
          n++;
          tally.kinds.window = (tally.kinds.window || 0) + 1;
          tally.round = (tally.round || 0) + 1;
          if (L[i].code) tally.codes[L[i].code] = (tally.codes[L[i].code] || 0) + 1; else tally.noCode++;
          tally.perim += Math.PI * 2 * Rr;
          continue;
        }
        /* the frame, as four boards round the opening */
        mk(w, fr, 0.06, cx - nx * dIn, G.y0 + fr / 2, cz - nz * dIn, rot, 0xF2F0EA);
        mk(w, fr, 0.06, cx - nx * dIn, G.y1 - fr / 2, cz - nz * dIn, rot, 0xF2F0EA);
        mk(fr, hh - 2 * fr, 0.06, cx - nx * dIn + G.ux * (w / 2 - fr / 2), cy, cz - nz * dIn + G.uz * (w / 2 - fr / 2), rot, 0xF2F0EA);
        mk(fr, hh - 2 * fr, 0.06, cx - nx * dIn - G.ux * (w / 2 - fr / 2), cy, cz - nz * dIn - G.uz * (w / 2 - fr / 2), rot, 0xF2F0EA);
        /* WHAT GOES IN THE FRAME DEPENDS ON WHAT WAS CHOSEN (core-v378): a
           window and a french door are glazed and divided into leaves, because
           a four metre opening is not one sheet; an entry door is a leaf with
           no glass; and a door of any kind stands on the floor, so it gets no
           sill on either face -- a threshold instead. */
        /* A DOOR HAS NO SILL BOARD ANYWHERE (core-v387, S3, Marek: parapet
           nad dvermi nesedi, je jakoby zdvojeny). Anything standing on the
           floor -- an entry door, a french door, a portal, a garage -- gets a
           threshold and nothing else; only a window is given the two boards. */
        var kind = L[i].unit || 'window', glazed = (kind !== 'door' && kind !== 'garage');
        var onFloorU = (kind !== 'window') || ((L[i].sill || 0) <= (opt.floorUp == null ? 0.02 : (+opt.floorUp + 0.02)));
        var leaves = Math.max(1, Math.round((w - 2 * fr) / 0.9)), q, gw = (w - 2 * fr) / leaves;
        for (q = 0; q < leaves; q++) {
          var off = -(w - 2 * fr) / 2 + gw * (q + 0.5);
          if (glazed) mk(gw - 0.03, hh - 2 * fr - 0.03, 0.012, cx - nx * dIn + G.ux * off, cy, cz - nz * dIn + G.uz * off, rot, 0x9FC4D6, 0.45);
          else mk(gw - 0.02, hh - 2 * fr - 0.02, 0.045, cx - nx * dIn + G.ux * off, cy, cz - nz * dIn + G.uz * off, rot, 0xD8D3C8);
          if (q) mk(0.05, hh - 2 * fr, 0.06, cx - nx * dIn + G.ux * (off - gw / 2), cy, cz - nz * dIn + G.uz * (off - gw / 2), rot, 0xF2F0EA);
        }
        if (!onFloorU) {
          mk(w + 0.08, 0.03, t / 2 + sillOut, cx - nx * (t / 4 + sillOut / 2), G.y0 - 0.015, cz - nz * (t / 4 + sillOut / 2), rot, 0xE6E3DC);
          mk(w + 0.10, 0.03, t / 2 + 0.05, cx + nx * (t / 4 + 0.025), G.y0 - 0.015, cz + nz * (t / 4 + 0.025), rot, 0xEFEDE7);
        } else {
          mk(w + 0.04, 0.02, t, cx, G.y0 + 0.01, cz, rot, 0xBDB7A9);
        }
        /* THE REVEAL THAT CLOSES THE JOINT (core-v380, V5): between the frame
           and the blocks there is a finger of gap on every side, and it is
           what the foam fills and the plaster covers. Drawing it makes the
           unit sit in the wall instead of floating in the hole, and it shows
           at once when a frame has been left standing proud of its jamb. */
        var rv = 0.02, band = t - 0.04;
        mk(rv, hh + 2 * rv, band, cx - G.ux * (w / 2 + rv / 2), cy, cz - G.uz * (w / 2 + rv / 2), rot, 0xE9E4DA);
        mk(rv, hh + 2 * rv, band, cx + G.ux * (w / 2 + rv / 2), cy, cz + G.uz * (w / 2 + rv / 2), rot, 0xE9E4DA);
        mk(w, rv, band, cx, G.y1 + rv / 2, cz, rot, 0xE9E4DA);
        n++;
        tally.kinds[kind] = (tally.kinds[kind] || 0) + 1;
        /* THE UNIT ITSELF, ON THE ROW IT WAS PICKED FROM (core-v382): the
           chooser already stores the catalogue code of the size taken from the
           product's own offer, so the bill takes that code by the piece and
           nothing has to be matched up by its dimensions afterwards. */
        /* THE FULL- AND HALF-WALL GLAZING IS SOLD BY THE SQUARE METRE (core-v489,
           from the ADU/shed/sauna thread, Marek): FIXF0001 / FIXH0001 are M2 / FT2
           rows, so they take the glass's own area, every other unit the piece.
           FULL0001 was a duplicate row of FIXF0001 and is being deleted from the
           catalogue; an opening saved with it is billed as FIXF0001. */
        var uCode = (L[i].code === 'FULL0001') ? 'FIXF0001' : L[i].code;
        if (uCode && (uCode === 'FIXF0001' || uCode === 'FIXH0001')) {
          tally.codes[uCode] = (tally.codes[uCode] || 0) + w * hh;
          tally.m2 = tally.m2 || {}; tally.m2[uCode] = 1;
        } else if (uCode) { tally.codes[uCode] = (tally.codes[uCode] || 0) + 1; }
        else tally.noCode++;
        /* THE ENTRY DOOR'S HANDLE SET (core-v567, Marek: vsechno, co
           postavime, se musi vykazovat): the unit does not carry it, so the ADU
           bills ODD00002 with every ODE door and the walls built here did not.
           One set to a door, a pair included -- its second leaf is bolted. */
        if (uCode && /^ODE/.test(String(uCode))) tally.handles = (tally.handles || 0) + 1;
        if (!onFloorU) { tally.sill += (w + 0.08); tally.sillIn += (w + 0.10); }
        else tally.thresh += w + 0.04;
        tally.perim += 2 * (w + hh);
      }
      /* WHAT THE UNITS COST, SAID OUT LOUD (core-v379): the joint round every
         frame is foam and the frame is held by anchors, and neither is drawn,
         so neither gets billed by accident. The codes belong to the catalogue
         and none exists yet, so this reports the quantities and says so. */
      if (n) {
        /* THE SILL BOARDS ON THE ROWS THE CATALOGUE ALREADY HAS (core-v381):
           the outer board and the inner board are each sold by the metre, so
           the length of every window's sill goes straight onto them. The units
           themselves are billed by the product, which knows which size was
           picked from its own offer. */
        try {
          if (opt.layer && typeof billAdd === 'function') {
            var cc;
            for (cc in tally.codes) if (tally.codes.hasOwnProperty(cc)) {
              /* A UNIT'S CODE COMES FROM THE OFFER, NOT FROM THE PRODUCT'S OWN
                 LIST (core-v390): the chooser picks a row straight out of the
                 catalogue, so a product can never have declared it in advance --
                 and every window was being dropped as "no catalogue entry"
                 while the log said it had been billed. The code is registered
                 here, by the piece, the moment it is used. */
              if (!CATALOG[cc]) CATALOG[cc] = { code: cc, unit: (tally.m2 && tally.m2[cc]) ? 'm2' : 'pcs', label: cc };
              billAdd(opt.layer, cc, tally.codes[cc]);
            }
            /* THE JOINT ROUND EVERY FRAME (core-v383): it is taped on both
               faces, inside and out, and the catalogue sells that tape by the
               metre. The foam and the anchors have no row of their own yet, so
               they stay out of the bill and the log says so rather than being
               put on a row they do not belong to. */
            if (tally.handles) {
              if (!CATALOG.ODD00002) CATALOG.ODD00002 = { code: 'ODD00002', unit: 'pcs', label: 'ODD00002' };
              billAdd(opt.layer, 'ODD00002', tally.handles);
            }
            if (tally.perim > 0.01) billAdd(opt.layer, 'OWP00002', tally.perim * 2 * 1.05);
            /* THE FOAM IN THAT JOINT, ON THE ROW THE CATALOGUE ALREADY HAS
               (core-v384): ODP00001 is sold by the can and one can does about
               thirty metres of joint, so the cans come from the length round
               the frames. A door's threshold takes the transition strip by the
               metre. The frame anchors still have no row anywhere, so they are
               counted and reported, never billed onto something else. */
            if (tally.perim > 0.01) billAdd(opt.layer, 'ODP00001', Math.ceil(tally.perim / 30));
            if (tally.thresh > 0.01) billAdd(opt.layer, 'ODD00003', tally.thresh * 1.05);
            /* AND THE ANCHORS THAT HOLD THE FRAME (core-v385): OWP00003 now
               exists, so the last thing this drew without billing is billed --
               one anchor every 700 mm along the jambs and the head, which is
               how the makers ask for them to be set. */
            if (tally.perim > 0.01) billAdd(opt.layer, 'OWP00003', Math.ceil(tally.perim / 0.7));
            if (tally.sill > 0.01) billAdd(opt.layer, 'OWW00002', tally.sill * 1.05);
            if (tally.sillIn > 0.01) billAdd(opt.layer, 'OWW00001', tally.sillIn * 1.05);
          }
        } catch (eB1) {}
        var ks = [], kk;
        for (kk in tally.kinds) if (tally.kinds.hasOwnProperty(kk)) ks.push(tally.kinds[kk] + ' ' + kk + '(s)');
        if (tally.round) ks.push(tally.round + ' of the windows round -- drawn in a square rough opening, its corners closed by the wall from the inner face to the finished face, no sill boards, the joint the circle round');
        var cl = [], c2;
        for (c2 in tally.codes) if (tally.codes.hasOwnProperty(c2)) cl.push((tally.m2 && tally.m2[c2]) ? (Math.round(tally.codes[c2] * 100) / 100 + ' m2 ' + c2) : (tally.codes[c2] + ' x ' + c2));
        _say('BILL  units billed by the piece: ' + (cl.length ? cl.join(', ') : 'none') +
             (tally.noCode ? ('; ' + tally.noCode + ' unit(s) carry no catalogue code and are NOT BILLED') : ''));
        _say('BILL  units: ' + ks.join(', ') + '; sill boards outer OWW00002 ' + Math.round(tally.sill * 100) / 100 +
             ' m and inner OWW00001 ' + Math.round(tally.sillIn * 100) / 100 +
             ' m, thresholds ODD00003 ' + Math.round(tally.thresh * 100) / 100 + ' m; perimeter joint ' +
             Math.round(tally.perim * 100) / 100 + ' m taped on both faces as OWP00002 and foamed with ' +
             Math.ceil(tally.perim / 30) + ' can(s) of ODP00001 at 30 m each; ' + Math.ceil(tally.perim / 0.7) +
             ' frame anchor(s) OWP00003 at 700 apart' + (tally.handles ? ('; ' + tally.handles + ' handle set(s) ODD00002, one to each entry door (core-v567)') : '') +
             ' -- everything drawn here is billed');
      }
      if (nSkip) _say('opening  ' + nSkip + ' unit(s) left to the product (rake / skip): drawn and billed there, not here');
      return n;
    }
    function snapHole(h) {
      if (!h) return h;
      /* A DOOR STANDS ON THE FLOOR (core-v386, Marek: dvere a hs portaly maji
         sedet na podlaze, nemaji se dat posouvat do vrchu). Its sill is not a
         choice, so it is put back on the floor here rather than being left to
         whatever the drag or the stored project last said. */
      if (h.unit === 'door' || h.unit === 'french' || h.unit === 'garage' || h.unit === 'portal') {
        var fs0 = 0;
        try { var wb0 = (typeof __PROD._wallBaseY === 'function') ? +__PROD._wallBaseY() : storeys()[0].y0; fs0 = Math.max(0, storeys()[0].y0 - wb0); } catch (eF0) { fs0 = 0; }
        h.sill = Math.round(fs0 * 10000) / 10000;
      }
      var step = 0, crs = 0;
      try { if (typeof __PROD._planStep === 'function') step = +__PROD._planStep() || 0; } catch (e1) { step = 0; }
      try { if (typeof __PROD._courseH === 'function') crs = +__PROD._courseH() || 0; } catch (e2) { crs = 0; }
      if (step > 0.05) {
        /* BOTH JAMBS ON A HEAD JOINT (core-v368, Marek): the masonry is laid
           from its own starting point, so the opening is measured from there
           too -- then one course ends on a whole block at each jamb and only
           the course between them is cut. The block's size therefore sets
           both where an opening can sit and how wide it can be. */
        var ph = 0;
        try { if (typeof __PROD._wallPhase === 'function') ph = +__PROD._wallPhase(h.ref, h.kind) || 0; } catch (eP) { ph = 0; }
        /* HALF A BLOCK IS A JOINT TOO (core-v370, Marek): the courses shift by
           half a block, so a jamb can sit on the joints of courses 1, 3, 5 or
           on those of 2, 4, 6. Snapping on half the module lets the width grow
           in half blocks and still leaves whole blocks in every other course
           -- which course that is simply swaps over. */
        /* THE TWO LATTICES THE BOND ACTUALLY MAKES (core-v371): the odd
           courses have their head joints on the wall's own grid, the even ones
           on that grid moved by the bond's shift -- which is the wall's
           thickness folded into a module, not half a block. A jamb goes to the
           nearer of the two, so one set of courses ends whole there. */
        var sh = 0;
        try { if (typeof __PROD._courseShift === 'function') sh = +__PROD._courseShift() || 0; } catch (eSh) { sh = 0; }
        /* ONE LATTICE FOR THE START, TWO WIDTHS FROM IT (core-v373, Marek: ty
           mas u bloku 250 x 350 steps otvoru 150 / 250 / 400 -- vzdyt to
           generuje prorezy). Taking the nearer joint at each jamb let the far
           one land a shift BEHIND the near one, and a width of 150 or 400 cuts
           into the course instead of ending it. The start goes on the wall's
           own grid; the width is then a run of whole blocks, or that run plus
           the one shift the courses are laid with -- 250, 350, 500, 600, 750,
           850 on a 250 block in a 350 wall, and 250, 380, 500, 630 on a 380. */
        /* THE SHIFT COMES OFF THE RUN, IT IS NOT ADDED (core-v375, Marek:
           otvory maji vznikat pri bloku 250 / 350 na 150 / 250 / 400 / 500).
           The far jamb ends a course a shift SHORT of the near one's, so the
           widths are a run of whole blocks, or that run less the one shift the
           bond carries: 150, 250, 400, 500 on a 250 block in a 350 wall. Both
           numbers come out of the block set in the Structure step, so changing
           its length or its width changes the whole ladder with it. */
        var u0s = Math.round((h.u - ph) / step) * step + ph;
        var wRun = Math.max(step, Math.round(h.w / step) * step);
        var wAlt = Math.round((h.w + sh) / step) * step - sh;
        if (wAlt < 0.05) wAlt = step - sh > 0.05 ? (step - sh) : step;
        var u1s = u0s + ((Math.abs(h.w - wRun) <= Math.abs(h.w - wAlt)) ? wRun : wAlt);
        /* A JAMB AGAINST A WALL INSIDE LANDS ON THAT WALL (core-v423, Marek:
           na druhe strane presahuje obvodova zed pri tazeni otvoru o 100 za
           nosnou). The block grid does not know where a load-bearing wall
           meets this run; within 150 of its face the jamb takes the face, so
           an opening can be drawn wall to wall with nothing left over. */
        try {
          var faces = _innerFacesOn(h), fi, snapD = 0.15;
          for (fi = 0; fi < faces.length; fi++) {
            if (Math.abs(h.u - faces[fi]) < snapD && Math.abs(h.u - faces[fi]) < Math.abs(h.u - u0s) + 0.001) u0s = faces[fi];
            if (Math.abs(h.u + h.w - faces[fi]) < snapD && Math.abs(h.u + h.w - faces[fi]) < Math.abs(h.u + h.w - u1s) + 0.001) u1s = faces[fi];
          }
        } catch (eIF) {}
        h.u = Math.max(0, u0s);
        h.w = u1s - h.u;
      }
      if (crs > 0.02) {
        /* THE HOLE AND THE UNIT ARE THE SAME HOLE (core-v394): the wall raised
           a window's head to the bed joint its lintel bears on, but it did that
           while laying, so the opening record kept the smaller number and the
           frame was drawn to it -- which is the gap over and under the glass.
           The head is raised here instead, once, on the record both the wall
           and the unit read. */
        h.sill = Math.max(0, Math.round((h.sill || 0) / crs) * crs);
        h.h = Math.max(crs, Math.round((h.h || crs) / crs) * crs);
      }
      /* the snapshot has to show the module the jambs were put on, because a
         missing one looks exactly like no snapping at all (core-v369) */
      if (_holeSnapSay) {
        _holeSnapSay = 0;
        _say('hole     snap on module ' + (step > 0.05 ? Math.round(step * 1000) : 'NONE -- the product gave no block module') +
             ', bond shift ' + Math.round(sh * 1000) + ', wall phase ' + Math.round(ph * 1000) +
             ', course ' + (crs > 0.02 ? Math.round(crs * 1000) : 'NONE') +
             ' -> starts ' + Math.round(h.u * 1000) + ', wide ' + Math.round(h.w * 1000) +
             ', ends ' + Math.round((h.u + h.w) * 1000));
      }
      h.w = Math.round(h.w * 10000) / 10000; h.u = Math.round(h.u * 10000) / 10000;
      h.sill = Math.round((h.sill || 0) * 10000) / 10000; h.h = Math.round(h.h * 10000) / 10000;
      return h;
    }
    function snapHoles() {
      var HS = STATE.holes || [], i, out = [];
      _holeSnapSay = 1;
      /* A WINDOW MOVES UP TO ITS LINTEL (core-v395, Marek: okno se melo
         primknout / posunout nahoru). Its height is the maker's and stays; its
         head has to be a bed joint for the lintel to bear on; so the whole
         unit rises until it is, and the course under the sill is made up in
         cut blocks by the wall. A door stands on the floor and keeps its head
         where the leaf puts it. */
      try {
        var crs2 = 0;
        if (typeof __PROD._courseH === 'function') crs2 = +__PROD._courseH() || 0;
        var OP = STATE.openings || [], q;
        if (crs2 > 0.02) for (q = 0; q < OP.length; q++) {
          var o = OP[q];
          if (!o) continue;
          /* a round light stays where it was dropped: no lintel, so no bed
             joint to reach (core-v522) */
          var rdQ = false;
          try { var itQ = _openingItem(o.code); rdQ = !!(itQ && itQ.round); } catch (eQ) { rdQ = false; }
          if (rdQ) continue;
          /* WHAT STANDS ON THE FLOOR IS DECIDED BY WHERE IT STANDS (core-v397,
             Marek: porad mas preklad fixovany na row). The unit in question was
             a full-height fixed window -- a window by kind, so it was raised to
             a bed joint like any window and its lintel went into the row. A
             unit whose sill is at the floor is a floor unit, whatever it is
             called: a portal, a fixed light to the floor, a door. */
          var fsK = 0;
          try { var wbK = (typeof __PROD._wallBaseY === 'function') ? +__PROD._wallBaseY() : storeys()[0].y0; fsK = Math.max(0, storeys()[0].y0 - wbK); } catch (eK) { fsK = 0; }
          var isFloor = (o.kind === 'door' || o.kind === 'french' || o.kind === 'garage' || o.kind === 'portal' || (o.sill || 0) <= fsK + 0.02);
          /* A DOOR STANDS ON THE FINISHED FLOOR (core-v396, Marek: dat spodni
             prah dveri do urovne podlahy a zapustit s tim i preklad). The wall
             starts on the slab; the floor is a build-up above it; the door's
             threshold belongs to the floor, so its sill is the build-up, and
             its lintel comes down with it -- straight on the leaf, as before. */
          if (isFloor) {
            var fs = 0;
            try {
              var wb = (typeof __PROD._wallBaseY === 'function') ? +__PROD._wallBaseY() : storeys()[0].y0;
              fs = Math.max(0, storeys()[0].y0 - wb);
            } catch (eFs) { fs = 0; }
            fs = Math.round(fs * 10000) / 10000;
            if (Math.abs((o.sill || 0) - fs) > 0.0005) { o.sill = fs; out.push(o.kind + ' put on the finished floor at +' + Math.round(fs * 1000)); }
            continue;
          }
          var head = (o.sill || 0) + (o.h || 0);
          var headUp = Math.ceil((head - 0.0005) / crs2) * crs2;
          if (headUp - head > 0.0005) { o.sill = Math.round((headUp - (o.h || 0)) * 10000) / 10000; out.push('window raised ' + Math.round((headUp - head) * 1000) + ' to a bed joint'); }
        }
      } catch (eW) {}
      for (i = 0; i < HS.length; i++) {
        if (!HS[i]) continue;
        var w0 = HS[i].w, s0 = HS[i].sill, h0 = HS[i].h;
        snapHole(HS[i]);
        if (Math.abs(w0 - HS[i].w) > 0.0005 || Math.abs((s0 || 0) - HS[i].sill) > 0.0005 || Math.abs((h0 || 0) - HS[i].h) > 0.0005) {
          out.push('#' + HS[i].id + ' ' + Math.round(HS[i].w * 1000) + ' wide, sill ' + Math.round(HS[i].sill * 1000) + ', ' + Math.round(HS[i].h * 1000) + ' high');
        }
      }
      return out.length ? ('openings put on the blocks: ' + out.join(', ')) : '';
    }
    function addPartition(o, bear) {
      var stNew = STATE.planStorey | 0;
      var F = _FL();
      var RM = (F && F.plan && F.plan.rooms) ? (F.plan.rooms() || []) : [];
      var R = [], bi, bj, best = null, i;
      for (bi = 0; bi < RM.length; bi++) {
        for (bj = 0; bj < (RM[bi].rects || []).length; bj++) R.push(RM[bi].rects[bj]);
      }
      for (i = 0; i < R.length; i++) {
        var span = (o === 'v') ? (R[i].x1 - R[i].x0) : (R[i].z1 - R[i].z0);
        var A = (R[i].x1 - R[i].x0) * (R[i].z1 - R[i].z0);
        if (span < 1.60) continue;
        if (!best || A > best.A) best = { A: A, r: R[i], span: span };
      }
      if (!best) { _log('partition ' + o + ' refused: no room wide enough'); return; }
      var c = (o === 'v') ? (best.r.x0 + best.r.x1) / 2 : (best.r.z0 + best.r.z1) / 2;
      STATE.parts = (STATE.parts || []).slice();
      var np = { id: _partId(), o: o, dir: o, c: c, at: c, st: stNew };
      if (bear) np.bear = true;
      STATE.parts.push(np);
      _log('partition ' + (bear ? 'load-bearing ' : '') + o + ' drawn on storey ' + (stNew + 1) + (bear ? ', it stands on every storey above too' : ''));
      _normParts();
      savePref();
      rebuild();
      drawPlan();
      var w = API.wizard();
      if (w && w.refresh) w.refresh();
      _log((bear ? 'bearing wall + ' : 'partition + ') + o + ' at ' + c.toFixed(3) + ' -> ' + STATE.parts.length + ' total');
    }

    function placePartition(dir, x, z) {
      var P2 = poly(), ax0 = 1e9, ax1 = -1e9, az0 = 1e9, az1 = -1e9, q;
      for (q = 0; q < P2.length; q++) {
        ax0 = Math.min(ax0, P2[q][0]); ax1 = Math.max(ax1, P2[q][0]);
        az0 = Math.min(az0, P2[q][1]); az1 = Math.max(az1, P2[q][1]);
      }
      STATE.parts = STATE.parts || [];
      STATE.parts.push((dir === 'v') ? { dir: 'v', o: 'v', at: x, c: x, a: az0, b: az1 }
                                     : { dir: 'h', o: 'h', at: z, c: z, a: ax0, b: ax1 });
      _normParts();
      savePref();
      rebuild();
      drawPlan();
      _log('partition placed, ' + dir);
    }

    /* _buildPlan is gone with the geometry it drew. The plan is the asset's
       drawing again -- see drawPlan above -- and every layer stands down while it
       is up, which is what the wrapper at registration does. */

    /* WHERE THE PLAN WAS, kept between frames so a resize can put it back. It is
       sampled as you move rather than on the resize itself, because by the time
       the resize fires the stage has already refitted and the old values are
       gone. */
    var _planKeep = null;

    /* the last few resizes, kept for the snapshot */
    var _resizeLines = [];

    /* the root's size at each resize, so a mover shows itself */
    var _lastRootW = -1, _lastRootH = -1, _lastResizeAt = 0;
    var _sizeLines = [];
    function _sizeLog(msg) {
      _sizeLines.push(msg);
      if (_sizeLines.length > 12) _sizeLines.shift();
    }
    function _resizeLog(msg) {
      _resizeLines.push(msg);
      if (_resizeLines.length > 12) _resizeLines.shift();
    }

    /* THE SAMPLER STANDS DOWN WHILE A RESIZE IS BEING HANDLED, AND IT STANDS
       DOWN AGAINST A CLOCK RATHER THAN A FLAG.

       It runs every 400 ms and a resize takes longer than that to settle, so
       between reading the old framing and putting it back the sampler used to
       record the REFITTED framing over the top of it -- and the restore then
       faithfully restored the value it was meant to be undoing.

       _resizing was a single boolean shared by every resize in flight. Two of
       them overlap on any real burst, and the first to finish cleared the flag
       for both, which is the pair that has already cost two rounds. A deadline
       cannot be cleared by somebody else; it can only be pushed further out. */
    var RESTORE_GATE_MS = 4000;   /* the sampler stays down this long after a resize */
    var RESTORE_WAIT_MS = 300;    /* trailing wait, so one burst gets one restore */
    var RESTORE_HOLD_MS = 2500;   /* and the framing is re-asserted for this long */

    var _planBlockUntil = 0;
    var _restoreTimer = 0;
    var _holdTimer = 0;

    function _nowMs() { try { return Date.now(); } catch (e) { return 0; } }

    function _planBlock(ms) {
      var until = _nowMs() + (ms || RESTORE_GATE_MS);
      if (until > _planBlockUntil) _planBlockUntil = until;
    }

    /* HOW MANY METRES ARE ACROSS THE SCREEN. This is the framing; zoom is only a
       ratio against a frustum that the refit rebuilds from the bounding box and
       the aspect every time it runs. */
    function _planVis(cam) {
      if (!cam || !(cam.zoom > 0)) return 0;
      var w = (cam.right - cam.left) / cam.zoom;
      return (isFinite(w) && w > 0) ? w : 0;
    }

    /* REPAINT WITH THE CAMERA WHERE IT IS NOW. The controls set the camera's
       position and target, but its world matrix -- and the inverse the
       projection uses -- are only rebuilt at the next render. Painting the plan
       straight after a restore therefore projected with the camera one frame
       behind, and the drawing landed a whole framing away from the pucks, which
       are scene objects and render with the new one. Bring the matrices up to
       date first, then paint. */
    function _planRepaint(cam) {
      try {
        if (cam && cam.updateMatrixWorld) cam.updateMatrixWorld(true);
        if (cam && cam.matrixWorldInverse && cam.matrixWorld) {
          cam.matrixWorldInverse.copy(cam.matrixWorld);
          if (cam.matrixWorldInverse.invert) cam.matrixWorldInverse.invert();
          else if (cam.matrixWorldInverse.getInverse) cam.matrixWorldInverse.getInverse(cam.matrixWorld);
        }
        if (cam && cam.updateProjectionMatrix) cam.updateProjectionMatrix();
      } catch (eCm) {}
      try { drawPlan(); } catch (eDp) {}
      _hRefresh();
    }

    function _planApplyVis(cam, vis) {
      if (!cam || !(vis > 0)) return 0;
      var span = cam.right - cam.left;
      if (!(span > 0)) return 0;
      var z = span / vis;
      if (!isFinite(z) || !(z > 0)) return 0;
      z = Math.max(0.02, Math.min(40, z));
      cam.zoom = z;
      if (cam.updateProjectionMatrix) cam.updateProjectionMatrix();
      return z;
    }

    function _planWiden(cam, ctl) {
      if (ctl) { ctl.minZoom = 0.02; ctl.maxZoom = 40; }
      if (cam && cam.minZoom !== undefined) cam.minZoom = 0.02;
      if (cam && cam.maxZoom !== undefined) cam.maxZoom = 40;
    }

    function _planRemember() {
      try {
        if (_nowMs() < _planBlockUntil) return;
        if (STATE.view !== '2d') return;
        var st = _stage(), cam = st && st.orthoCam, ctl = st && st.orthoControls;
        if (!cam || !ctl || !ctl.target) return;
        /* the limits are widened here too, because the refit narrows them every
           time it runs and not only on a resize */
        _planWiden(cam, ctl);
        var vis = _planVis(cam);
        if (!vis) return;
        _planKeep = { zoom: cam.zoom, vis: vis,
                      t: { x: ctl.target.x, y: ctl.target.y, z: ctl.target.z } };
      } catch (e) {}
    }

    /* ONE RESTORE PER BURST. Every resize pushes the same timer further out, so
       the storm that coming back to the app produces ends in a single restore
       instead of a dozen overwriting each other. */
    /* THE HAND WINS (core-v546, Marek: zoom opet skace, kdyz prejdu do
       vedlejsiho okna a pak se vratim -- predtim se chova docela dobre, pri
       navratu hapruje). Coming back to the tab is a resize, and a resize puts
       the remembered framing back and then HOLDS it for 2.5 s, re-asserting it
       ten times a second, while the sampler that remembers the framing stays
       down for 4 s. A pinch in that window was fought every 100 ms -- the
       stutter -- and what it reached was snapped back to the framing from
       before. A zoom or a slide by hand now stands the pending restore and the
       hold down at once, and the framing it leaves is remembered on the spot. */
    function _planUserMoved() {
      var busy = !!(_holdTimer || _restoreTimer) || _nowMs() < _planBlockUntil;
      if (!busy) return;
      if (_holdTimer) { try { global.clearInterval(_holdTimer); } catch (e1) {} _holdTimer = 0; }
      if (_restoreTimer) { try { global.clearTimeout(_restoreTimer); } catch (e2) {} _restoreTimer = 0; }
      _planBlockUntil = 0;
      _resizeLog('hand     a pinch or a slide took the view -- the restore and the hold after the resize stood down');
      _log('2d       the hand took the view during the restore after a resize (back from another tab) -- the restore stood down');
    }
    function _planRestoreSoon() {
      try {
        if (_restoreTimer) { global.clearTimeout(_restoreTimer); _restoreTimer = 0; }
        _restoreTimer = global.setTimeout(function () { _planRestore(false); }, RESTORE_WAIT_MS);
      } catch (e) {}
    }

    function _planRestore(immediate) {
      if (!immediate) _restoreTimer = 0;
      try {
        var st = _stage(), cam = st && st.orthoCam, ctl = st && st.orthoControls;
        if (!cam) { if (!immediate) { _resizeLog('restore  SKIPPED -- no ortho camera'); _planBlockUntil = 0; } return; }
        if (STATE.view !== '2d') { if (!immediate) { _resizeLog('restore  SKIPPED -- view is ' + STATE.view); _planBlockUntil = 0; } return; }
        var keep = _planKeep;
        if (!keep || !(keep.vis > 0)) { if (!immediate) { _resizeLog('restore  SKIPPED -- nothing remembered yet'); _planBlockUntil = 0; } return; }

        _planWiden(cam, ctl);
        var visRefit = _planVis(cam), zRefit = cam.zoom;

        /* THE ORDER IS NOT NEGOTIABLE. OrbitControls.update() writes the camera
           from its own state, so the target goes first, update() runs, and the
           framing is set LAST -- after the controls have had their say. It also
           has to be last for a second reason: update() dispatches change, the
           map listens to change, and the map's answer to a collapsed frustum is
           to pull this camera back to 0.270. Setting the framing after that call
           means nothing gets a chance to overrule it. */
        if (ctl && ctl.target && keep.t) ctl.target.set(keep.t.x, keep.t.y, keep.t.z);
        if (ctl && ctl.update) ctl.update();
        _planApplyVis(cam, keep.vis);

        /* and the ground is told, so the photo is at the scale of the framing
           that won rather than the one that lost */
        try { _syncMapView(false); } catch (eM) {}
        /* AND THE DRAWING IS REPAINTED. The handles are objects in the scene and
           follow the camera by themselves; the plan is a 2D painting made from
           the camera's projection, and nothing repainted it after the framing
           was put back -- so every time the plan step opened and the drawer
           resized the canvas, the pucks sat on the new framing and the drawing
           stayed on the old one, until the first drag rebuilt it (Marek). */
        _planRepaint(cam);

        var frus = cam.right - cam.left;
        var emptyNote = (frus < 3 ? '   <-- THE STAGE REFIT AN EMPTY GROUP' : '');

        if (immediate) {
          _resizeLog('snap     ' + (visRefit ? visRefit.toFixed(2) : '?') + ' -> ' +
                     keep.vis.toFixed(2) + ' m across, same frame' +
                     ' | zoom ' + zRefit.toFixed(3) + ' -> ' + cam.zoom.toFixed(3) +
                     ' | frustum ' + frus.toFixed(2) + ' m' + emptyNote);
          return;
        }

        _resizeLog('restore  wanted ' + keep.vis.toFixed(2) + ' m across, found ' +
                   (visRefit ? visRefit.toFixed(2) : '?') + ' m' +
                   ' | zoom ' + zRefit.toFixed(3) + ' -> ' + cam.zoom.toFixed(3) +
                   ' | frustum ' + frus.toFixed(2) + ' m' +
                   ' | target ' + keep.t.x.toFixed(2) + ',' + keep.t.z.toFixed(2) +
                   ' | limits ' + (ctl ? (ctl.minZoom + '..' + ctl.maxZoom) : 'n/a') + emptyNote);

        /* AND IT IS HELD, because the freeze guarantees stragglers: a reframe
           can still land after this. Every correction is counted, so a hold that
           reports zero corrections is the proof that nothing else is touching
           the camera and that this hold can be deleted in a later version. */
        var tries = 0, fixes = 0;
        if (_holdTimer) { try { global.clearInterval(_holdTimer); } catch (e1) {} _holdTimer = 0; }
        _holdTimer = global.setInterval(function () {
          tries++;
          try {
            var s2 = _stage(), c2 = s2 && s2.orthoCam, t2 = s2 && s2.orthoControls;
            if (c2 && STATE.view === '2d') {
              var v2 = _planVis(c2);
              if (v2 && Math.abs(v2 - keep.vis) / keep.vis > 0.005) {
                fixes++;
                _planWiden(c2, t2);
                if (t2 && t2.target && keep.t) t2.target.set(keep.t.x, keep.t.y, keep.t.z);
                _planApplyVis(c2, keep.vis);
                _planRepaint(c2);
              }
            }
          } catch (eH) {}
          if (tries * 100 >= RESTORE_HOLD_MS) {
            try { global.clearInterval(_holdTimer); } catch (e3) {}
            _holdTimer = 0;
            _resizeLog('hold     ' + tries + ' tick(s), corrected ' + fixes + 'x' +
                       (fixes ? '   <-- SOMETHING IS STILL REFRAMING AFTER THE RESTORE' : '   ok'));
            if (!_restoreTimer) _planBlockUntil = 0;
          }
        }, 100);
      } catch (e) {
        _resizeLog('restore  THREW ' + (e && e.message));
        _planBlockUntil = 0;
      }
    }

    var _fpInit = false;

    function _initFloorplan() {
      var F = _FL();
      if (!F) { _log('FloorplanLib missing -- configurator-floorplan-lib.js not loaded'); return; }
      try {
        F.init({
          getFloors:  function () { return clamp(STATE.storeys | 0, 1, 2); },
          getView:    function () { return STATE.view; },
          isMetric:   function () { return !!STATE.isMetric; },
          getVariant: function () { return STATE.roof.type; },
          t:          _t,
          flush3D:    function () { try { rebuild(); } catch (e) {} },
          stage:      _stage()
        });
      } catch (e) { _log('FloorplanLib.init threw: ' + (e && e.message)); }
      try { if (F.plan && F.plan.init) F.plan.init(planHost()); }
      catch (e2) { _log('FloorplanLib.plan.init threw: ' + (e2 && e2.message)); }

      /* THE MODULE HAS ITS OWN init AND NOBODY WAS CALLING IT.

         configurator-floorplan.js is now on the page -- fpassets says module=yes
         -- but loading a script only defines it. ConfiguratorFloorplan.init is
         what binds the pointer, the handles and the stage, and it takes the same
         host contract the lib does. Tiny calls it; this product never did. */
      /* --------------------------------------------------------------------
         ONE FINGER PANS THE PLAN.

         OrbitControls maps one touch to ROTATE and two to dolly-and-pan. On a
         perspective view that is right; on a plan it is nonsense -- there is
         nothing to orbit when you are looking straight down, so a one-finger
         drag did nothing and the only way to move the drawing was to pinch it,
         which zooms at the same time. You could not slide the plan without
         resizing it.

         In 2D one finger pans, two pinch to zoom, and rotation is off entirely.
         -------------------------------------------------------------------- */
      try {
        var st2 = _stage(), T2 = global.THREE;
        var oc = st2 && st2.orthoControls;
        if (oc && T2 && T2.TOUCH) {
          oc.enableRotate = false;

          /* ONE FINGER BELONGS TO THE MAP, NOT TO THE CAMERA.

             I gave the ortho camera ONE:PAN a version ago, which is right when
             the plan is what you are dragging -- and wrong here, because the
             thing under one finger in 2D is the aerial photograph. Tiny binds
             that itself: pointerdown, pointermove, map.panBy, and it bows out
             the moment a second finger lands. Handing the same gesture to the
             camera made the two fight, and the map lost.

             So the camera keeps only the pinch. One finger is the map's, exactly
             as canPan() below has always assumed. */
          /* AND NOW ONE FINGER IS THE CAMERA'S AGAIN, the way the pool has it:
             the camera pans, and the kit's ortho sync moves the map under it by
             the same metres, so ground and plan travel together. Two fingers
             pinch. What the plan wants (a partition, an opening, a piece) takes
             the finger before the camera sees it, with stopImmediatePropagation
             and the controls switched off for the drag. The lib's own
             map.panBy drag is gone -- two hands on one wheel (Marek). */
          /* v1027: the camera only ZOOMS in 2D -- pinch and wheel. No pan (the
             map's one-finger drag does that), no rotate (a plan has no side),
             and the controls are ON, which the kit leaves off by default */
          _apply2DControls();
          _log('2d controls: one finger pans the map under the building, two fingers pinch the camera');
        } else {
          _log('2d controls: orthoControls or THREE.TOUCH missing -- one-finger pan not set');
        }
      } catch (eTC) { _log('2d controls: ' + (eTC && eTC.message)); }

      /* --------------------------------------------------------------------
         THE OLD MODULE IS NOT WIRED, AND THAT IS THE POINT.

         configurator-floorplan.js used to draw this plan into the scene and bind
         the pointer to it. The asset draws it now, on its own canvas, and the
         asset binds nothing -- it answers questions about a point. So the module
         has no half left to own, and a module that is wired and does nothing is
         worse than one that is absent: it answers questions about a plan it is
         not drawing, and it races the handlers that are.

         Two things went with it. global.BaseEngine and the sceneGroup alias were
         put there so its render() would find a scene; without the alias it draws
         nothing, which is now what is wanted. And setStage, which decided which
         of its five modes was live -- the asset has no modes, the wizard step is
         the mode.
         -------------------------------------------------------------------- */
      _fpInit = false;
      /* AND THE SCRIPT TAG IS GOING TOO. The module was never wired here; the tag
         only kept a file on the page for nobody. It is being taken off this
         template, so its absence is the normal state and there is nothing to
         report -- if it turns up, that is the thing worth a line. */
      if (global.ConfiguratorFloorplan) {
        _log('configurator-floorplan.js is STILL on this page -- the tag can come out, nothing here uses it');
      }

    }

    /* -------------------------------------------------------------------------
       WIZARD. The core owns the wiring, the product owns the steps.
       ------------------------------------------------------------------------- */
    var _wiz = null;
    var _wizRefit = false;   /* a 2D step was left for a 3D one: frame the camera once the model is back */

    function initWizard(steps, opts) {
      var U = _UI();
      if (!U || !U.wizard) { _log('ConfiguratorUI.wizard missing'); return null; }
      if (_wiz) return _wiz;
      opts = opts || {};
      var defs = [], i;
      for (i = 0; i < steps.length; i++) {
        (function (w) {
          defs.push({
            key:   w.key,
            label: w.label,
            hint:  w.hint,
            skip:  w.skip,
            panel: w.panel,
            slider: w.slider,
            icons: (typeof w.icons === 'function') ? w.icons : (w.icons || []),
            exit:  function () { if (w.exit) w.exit(); },
            enter: function () {
              /* THE CAMERA IS FRAMED AFTER THE MODEL IS BACK (core-v318). Leaving
                 the plan for a 3D step swapped the camera first and rebuilt the
                 model second, so the framing measured whatever the plan step had
                 left in the group -- sometimes a few plan pieces, and the camera
                 dived onto a corner (Marek: je videt jen roh stavby). The switch
                 is noted here and the framing done in onStep, once rebuild() has
                 the whole building standing again. */
              if (w.view === '2d') { if (STATE.view !== '2d') setView('2d'); }
              else if (STATE.view === '2d') { _wizRefit = true; setView('3d'); }
              STATE.mode = w.mode || 1;
              /* NO PITCH SLIDER ON A LEVEL ROOF. The parapet variant is dead flat by
             definition -- its fall is made in the covering -- so a track that
             cannot move is a control that lies. */
          showPitch(w.key === 'roof' && STATE.roof.type !== 'flat');
          try { showHeight(w.key === 'roof'); } catch (eH) {}
          try { showFloor(w.key === 'roof'); } catch (eFl) {}
          /* THE GROUND-FLOOR SLIDER LIVES ON ROOF AND BASE WITH THE WALL
             HEIGHT (core-v415, Marek: vysku prvniho patra a wall height nakonec
             nechej ve strecha a zaklad). Storey two is the difference; it has
             no slider of its own. */
          try { showStorey(w.key === 'roof'); } catch (eSt) {}
          /* THE WALL HEIGHT IS ALWAYS THERE ON THE PLAN (core-v410, Marek:
             posuvnik vysky zdi je videt porad, jsou to spojite nadoby). On the
             plan it shows the whole wall and moves the storey whose card is
             lit; the storey slider moves that storey and the wall follows. */

              if (w.enter) w.enter();
            }
          });
        })(steps[i]);
      }
      _wiz = U.wizard({
        steps: defs,
        hideBar: true,
        /* the icon strip instead of the drawer (core-v591) */
        strip: _stripOn,
        renderStrip: _sxRender,
        stripStart: _sxStart,
        title: opts.title || _tCap('edit', 'Edit'),
        onStep: function (key) {
          if (key !== 'assembly') _cutOff('editing (' + key + ')');
          /* THE ASSEMBLY STEP IS A READABLE SECTION: the house cut across, the
             wall build-up named beside it (ported from the shed) */
          try { if (key === 'assembly') _asmEnter(); else _asmLeave(); } catch (eA) { _log('assembly: ' + (eA && eA.message)); }
          try { showExplode(key === 'assembly'); } catch (eX) { _log('explode: ' + (eX && eX.message)); }
          /* CHANGING TAB LEAVES THE MODE TOO. Edit mode belongs to one step, and
             its cross stayed on the model over every other one -- eating taps on
             a step that has nothing to do with openings. Walking away is leaving,
             the same as shutting the drawer. */
          if (_opEdit && key !== 'openings') {
            opEdit(false);
            _log('opening edit closed -- moved to ' + key);
          }
          /* AND SO DOES THE OPENING ITSELF (core-v367): its fill, its corner
             dots and its numbers belong to this step, not to the model. */
          if (key !== 'openings' && (_hh.on || _holeMode)) {
            _hh.on = null;
            if (_holeMode) holeMode(false);
            _log('hole     let go -- moved to ' + key);
            rebuild();
          }
          /* AND THE TRACE IS A MODE TOO, so it leaves with the step that owns it.
             Armed, it holds the pointer and puts its own handles and its opacity
             slider on the screen. Walking to another tab and leaving all that up
             is the same fault the opening cross had. */
          if (key !== 'layout') _traceOff('moved to ' + key);
          _log('wizard -> ' + key);
          /* the plan belongs to one step, so a step change shows or hides it */
          /* AND THE HANDLES ARE LAID OUT LAST. rebuild() refreshes them and then
             drawPlan() here fitted the plan again, so on every re-opening of the
             plan step the pucks sat where the plan had been before the fit and
             the plan under them had moved -- until the first drag, whose rebuild
             put them back together. Refresh after the last thing that can move
             the plan (Marek). */
          try { rebuild(); drawPlan(); } catch (eS) {}
          _hRefresh();
          if (opts.onStep) opts.onStep(key);
          if (_wizRefit) {
            _wizRefit = false;
            try { var stF = _stage(); if (stF && STATE.view === '3d') stF.fitCameraToFootprint(); } catch (eF) {}
            _log('wizard  camera framed on the whole building after the plan');
          }
        },
        onExit: function () {
          /* CLOSING THE DRAWER CLOSES THE EDITING WITH IT.

             Edit mode put a cross on the model, and the cross was the only way
             out of it. Shut the drawer and the cross stayed there, over a
             finished view, still taking every tap on a window -- a mode with its
             own button left running after the panel it belongs to has gone.

             Leaving is leaving. */
          if (_opEdit) { opEdit(false); _log('opening edit closed with the drawer'); }
          /* THE TRACE GOES WITH IT, and this is the one he is looking at: the
             opacity slider stayed on screen after the drawer was shut, and the
             next time it opened the plan and the footprint were out of step --
             because the image was still armed, still holding the pointer and
             still being drawn by a panel that was no longer there.

             Leaving is leaving, and it has to be said once per way out. */
          _traceOff('the drawer closed');
          STATE.mode = 5;
          showPitch(false);
          try { showHeight(false); } catch (eH2) {}
          try { showFloor(false); } catch (eFl2) {}
          try { showStorey(false); } catch (eSt2) {}
          if (STATE.view === '2d') setView('3d');
          rebuild();
          /* core-v750 (the hero's video, recorded 2026-10-21: the camera jumped
             back to the framing angle at the end of every loop): the homepage
             hero's reel shuts the drawer when it ends -- its camera keeps its
             own turn and distance there, no framing */
          var st = _stage(), heroNoFit = false;
          try { heroNoFit = (typeof _tourAuto === 'function') && _tourAuto(); } catch (eHa) { heroNoFit = false; }
          if (st && !heroNoFit) st.fitCameraToFootprint();
          if (opts.onExit) opts.onExit();
          _log('wizard closed -> finished view' + (heroNoFit ? ' (the hero: camera kept, core-v750)' : ''));
        }
      });
      return _wiz;
    }

    function wizardOpen() { return !!(_wiz && _wiz.isOpen()); }
    var _fitOpenTimer = 0;
    function editing(key) {
      if (!_wiz || !_wiz.isOpen()) return false;
      var c = _wiz.current();
      return !!(c && c.key === key);
    }

    /* =========================================================================
       THE ICON STRIP (core-v591, Marek 2026-10-10: misto sirokeho draweru
       uzky drawer, misto sections jen ikony -- square s rounded corners, bez
       popisu, tooltip jedno slovo; u nekterych v canvas dole sipky doleva
       doprava a cyklovani, pod tim posuvnik; nekde plovouci modal, nekde
       drawer, nekde jen on / off).

       core-v593 (Marek, after the first test: smer je spravny): the strip
       is INVERSE -- dark, white glyphs, a switch that is ON in LEVA red, the
       picked section white; the arrows' banner sits on the canvas axis with
       BLACK arrows and the option's own word over it (Gable / Hip 1 / Mono-
       pitch / Parapet ...); the FLOORPLAN is no longer an icon here -- it is
       the plan icon of the canvas bar, which opens a strip of its own with
       the whole floorplan menu, the padlock first, and its cross ends the
       plan editing. Every product on the core gets the strip
       (__PROD._iconUi = false keeps the old drawer).

       The wizard's steps are kept as they are -- enter, exit, onStep and
       editing(key) mean what they meant -- and the wizard draws no drawer: it
       calls _sxRender, which draws a narrow strip of icons down the right of
       the canvas, over the rail, and puts each icon's controls on the canvas:

         cycle    the left / right arrows where the canvas bar stands, the
                  section's one word between them, the option's word over
                  them, a slider under them where the section has one
         toggle   on / off on the icon itself (terrace, pool, blinds)
         modal    a floating panel over the model, moved by its handle
         picker   the openings drawer, a tab per kind
         colors   arrows walking the outside parts, their colour dots over them

       THE PLAN STRIP (_SX.mode 'plan'): the wizard on its Floorplan step, the
       padlock first and then every tile of the product's Floorplan rows as an
       icon; locked, the plan is only looked at.

       The icons are found from the product's own rows (GROUPS), by the keys
       the line's row builders put on them, so a product that has no terrace
       has no terrace icon; the order is fixed and the same in every product.
       A row the strip does not know is never lost: it lands in a panel of
       its step's own. ?ui=icons and ?ui=drawer on the address force either.
       ========================================================================= */
    var _SX = { el: null, list: null, tip: null, tipT: 0, bar: null, sl: null, modal: null,
                sel: null, lock: true, sig: '', barSig: '', icons: [], colorIdx: 0, drag: null,
                mPos: null, on: null, said: '', mode: 'edit', tools: [] };
    var _sxVideoId = '';
    var _sxRoofOpts = null;

    function _stripOn() {
      if (_SX.on !== null) return _SX.on;
      var q = '', v = null;
      try { q = String((global.location && global.location.search) || ''); } catch (e) { q = ''; }
      if (/[?&]ui=drawer(&|$)/.test(q)) v = false;
      else if (/[?&]ui=icons(&|$)/.test(q)) v = true;
      /* EVERY PRODUCT ON THE CORE (core-v593, Marek: dat to na vsechny
         projekty vazane na UI asset); a product may still say false */
      else { try { v = !(__PROD && __PROD._iconUi === false); } catch (e2) { v = true; } }
      try { if (__PROD && __PROD.VERSION) _SX.on = v; } catch (e3) {}
      return v;
    }
    function _stripMode() { return !!(_wiz && _wiz.strip && _wiz.strip()); }
    function _sxPlanOpen() { try { return !!(_stripMode() && _wiz.isOpen() && _SX.mode === 'plan'); } catch (e) { return false; } }
    /* THE RAIL'S PLAN IS LOOKED AT (core-v600): with the icon strip the
       plan the rail's 2D button shows answers nothing -- editing it is the
       plan strip's, opened from the canvas bar's plan icon */
    function _plainPlan() { try { return !!(_stripMode() && _barPlan && !wizardOpen() && STATE.view === '2d'); } catch (e) { return false; } }
    function _stripPlanLocked() { return !!((_SX.lock && editing('layout') && _stripMode()) || _plainPlan()); }

    function _sxWord(t) { return (t && t.join) ? _t(t[0], t[1]) : String(t || ''); }
    function _sxG(name) { return ICO[name] || ICO.layers || ''; }
    function _sxRefresh() { try { if (_wiz && _wiz.refresh) _wiz.refresh(); } catch (e) {} }
    function _sxAfter(why) {
      _sxRefresh();
      /* core-v597: what the strip has picked decides the handles (the roof's
         ghost only with the roof's shape picked) -- a pick inside the same
         step changed nothing, so the handles are redrawn on every pick */
      try { _hRefresh(); } catch (eH) {}
      global.setTimeout(function () { try { undoPush(); } catch (e) {} }, 0);
      if (why) _log('strip    ' + why);
    }
    function _sxGoto(step) {
      if (!step || !_wiz || !_wiz.gotoKey) return;
      var c = _wiz.current();
      if (c && c.key === step) return;
      _wiz.gotoKey(step);
    }
    function _sxSteps() {
      var o = {}, L = (_wiz && _wiz.steps) ? _wiz.steps() : [], i;
      for (i = 0; i < L.length; i++) if (L[i]) o[L[i].key] = L[i];
      return { has: o, list: L };
    }
    function _sxRows(step) {
      if (typeof _kitGroups !== 'function') return [];
      try { return _kitGroups(step) || []; }
      catch (e) { _log('strip    the ' + step + ' rows threw ' + ((e && e.message) || e)); return []; }
    }
    function _sxRow(rows, k) {
      for (var i = 0; i < (rows || []).length; i++) if (rows[i] && rows[i].key === k) return rows[i];
      return null;
    }
    function _sxItem(row, k) {
      if (!row || !row.items) return null;
      for (var i = 0; i < row.items.length; i++) if (row.items[i] && row.items[i].key === k) return row.items[i];
      return null;
    }
    function _sxFind(step, rk, ik) { return _sxItem(_sxRow(_sxRows(step), rk), ik); }
    /* what is left of a step's rows once the strip's own icons have taken
       theirs: claim is { rowKey: '*' | [itemKeys] | fn(item) } */
    function _sxLeft(rows, claim) {
      var out = [];
      (rows || []).forEach(function (r) {
        if (!r || r.range) { if (r && r.range && !(r.key && claim[r.key])) out.push(r); return; }
        var c = r.key ? claim[r.key] : null;
        if (c === '*') return;
        if (!c) { if (r.items && r.items.length) out.push(r); return; }
        var rest = (r.items || []).filter(function (it) {
          if (!it) return false;
          if (typeof c === 'function') return !c(it);
          return c.indexOf(it.key) < 0;
        });
        if (rest.length) out.push({ label: r.label, items: rest });
      });
      return out;
    }
    var _SX_CLAIM = {
      roof: { roof: ['pitched', 'gable', 'mono', 'flat'], covering: '*', base: '*' },
      openings: { openings: function (it) { return !!it.kind; }, opedit: '*' },
      exterior: { facade: '*', terrace: '*', elements: ['pergola', 'pool'], blinds: '*', slats: '*' }
    };
    function _sxColorSlots() {
      return (_colorSlotList || []).filter(function (s) {
        if (!s || s.grp === 'interior') return false;
        try { return !s.when || !!s.when(); } catch (e) { return false; }
      });
    }
    /* core-v624: not when the product rides them on its own strip icons
       (__PROD._stripOwnSliders -- RANCH's cross gable width on its icon) */
    function _sxRoofProdSliders() { try { return typeof __PROD._roofSliders === 'function' && !__PROD._stripOwnSliders; } catch (e) { return false; } }

    /* THE LEFTOVER ROWS SPLIT TOO (core-v598, Marek: rozepsat na sipky a
       on/off u vsech config). A row the strip has no icon of its own for is
       read by its shape: ONE tile that answers on() is an on/off icon (the
       sauna's hot tub, the T house's ridge, a hole); two or more tiles of
       which exactly one is lit are a CHOICE and get the arrows with the lit
       tile's caption over them (the T house's insulation, the THOW's chassis);
       anything else -- actions, pictures, drag sources, sliders -- stays in
       the step's + panel. */
    function _sxAutoRows(step, claim) {
      var left = _sxLeft(_sxRows(step), claim || {}), out = [], rest = [];
      left.forEach(function (r, n) {
        if (!r || r.range || !r.items || !r.items.length) { if (r) rest.push(r); return; }
        var its = r.items;
        /* core-v628: an edit step of its own (RANCH's cross gables): picked, not switched */
        if (r.step && r.key) { out.push({ id: 'step:' + r.key, kind: 'step', row: r }); return; }
        var plain = its.every(function (it) { return it && typeof it.on === 'function' && typeof it.tap === 'function' && !it.act && !it.down && !it.img && !it.small; });
        if (!plain) { rest.push(r); return; }
        var lit = its.filter(function (it) { try { return !!it.on(); } catch (e) { return false; } }).length;
        var id = 'auto:' + step + ':' + String(r.key || r.label || n).slice(0, 32) + (its.length === 1 ? (':' + String(its[0].key || its[0].label || '').slice(0, 24)) : '');
        if (its.length === 1) out.push({ id: id, kind: 'toggle', row: r });
        else if (lit === 1) out.push({ id: id, kind: 'cycle', row: r });
        else rest.push(r);
      });
      return { icons: out, rest: rest };
    }
    function _sxAutoRow(I) {
      var A = _sxAutoRows(I.step, _SX_CLAIM[I.step] || {}), i;
      for (i = 0; i < A.icons.length; i++) if (A.icons[i].id === I.key) return A.icons[i].row;
      return null;
    }
    function _sxAutoIcons(step, add) {
      var A = _sxAutoRows(step, _SX_CLAIM[step] || {});
      A.icons.forEach(function (a) {
        var it0 = a.row.items[0];
        var I = { key: a.id, step: step, kind: a.kind, auto: true, rowKey: a.row.key || '',
                  tip: (a.kind === 'toggle') ? (it0.label || it0.title || a.row.label || '') : (a.row.label || it0.label || '') };
        if (a.kind === 'step') { I.tip = a.row.label || it0.label || ''; I.dual = a.row.slider2 || null; I.onPick = (typeof a.row.onPick === 'function') ? a.row.onPick : null; }
        if (a.kind === 'toggle') {
          I.on = function () { var r = _sxAutoRow(I); try { return !!(r && r.items[0].on()); } catch (e) { return false; } };
          I.item = function () { var r = _sxAutoRow(I); return r ? r.items[0] : null; };
          /* AN ON / OFF ICON WITH ITS OWN SLIDER (core-v624, RANCH, Marek:
             vikyre -- samostatna ikona, musi mit svuj posuvnik). A row whose
             one tile is an on / off may hand slider = { title: [key, def],
             core() -> a ConfiguratorUI.slider, sync(S), on(), read() } -- the
             shape of the strip's own sliders. Switched on, the icon's bar
             comes up with that slider and no arrows (one tile has nothing to
             walk); the slider drives the product's own, as the pitch does. */
          var rs = a.row.slider;
          if (rs && typeof rs.core === 'function' && typeof rs.read === 'function') {
            var sk = 'prod:' + a.id;
            _SX_SL[sk] = { title: rs.title || [a.row.label || '', a.row.label || ''], core: rs.core,
                           sync: (typeof rs.sync === 'function') ? rs.sync : function () {},
                           on: (typeof rs.on === 'function') ? rs.on : function () { return true; }, read: rs.read };
            I.slider = sk; I.bar = true; I.own = true;
          }
        }
        add(I);
      });
      return A.rest.length;
    }
    /* the glyph of an auto icon: its tile's, the lit one's for a choice */
    function _sxAutoSvg(I) {
      var r = _sxAutoRow(I), i;
      if (!r || !r.items) return _sxG('layers');
      for (i = 0; i < r.items.length; i++) { try { if (I.kind === 'cycle' && r.items[i].on()) return _sxWrapSvg(r.items[i].svg) || _sxG('layers'); } catch (e) {} }
      return _sxWrapSvg(r.items[0].svg) || _sxG('layers');
    }
    /* A TILE'S GLYPH MAY BE BARE PATHS (core-v599: the T house's insulation
       tiles hand '<path .../>' with no svg round it, and the strip showed an
       empty tile): wrapped as the kit's own glyphs are */
    function _sxWrapSvg(s) {
      s = String(s || '');
      if (!s || /^\s*<svg/i.test(s)) return s;
      return '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + s + '</svg>';
    }

    /* ---- the edit strip's icons, in the one order every product shares ---- */
    function _sxIcons() {
      var SS = _sxSteps(), has = SS.has, out = [], k;
      function add(o) { out.push(o); }
      if (has.place) add({ key: 'place', step: 'place', kind: 'modal', g: 'pin', tip: ['sauna_wiz_1', 'Place'], panel: 'place' });
      /* the floorplan is the canvas bar's plan icon (core-v593); only a page
         without the bar keeps an icon here, and it opens the plan strip */
      if (has.layout && !(_cmd && _cmd.el)) add({ key: 'layout', step: null, kind: 'goplan', g: 'plan', tip: ['view_plan', 'Floor plan'] });
      var stS = false;
      try { stS = !!(__PROD && __PROD._storeySlider); } catch (eS) { stS = false; }
      if (has.structure || !_noWallH() || _floorRange() || stS) {
        add({ key: 'structure', step: has.structure ? 'structure' : (has.place ? 'place' : null), kind: 'modal', g: 'layers',
              tip: ['structure', 'Structure'], panel: has.structure ? 'structure' : null, sliders: ['height', 'floor', 'storey'] });
      }
      if (has.roof) {
        var rr = _sxRows('roof');
        if (_sxRoofList(_sxRow(rr, 'roof')).length) add({ key: 'roof', step: 'roof', kind: 'cycle', g: 'gable', tip: ['roof', 'Roof'], slider: 'pitch' });
        var rC = _sxRow(rr, 'covering');
        if (rC && rC.items && rC.items.length > 1) add({ key: 'covering', step: 'roof', kind: 'cycle', g: 'raked', tip: ['mc_cov_group', 'Roof covering'] });
        var rB = _sxRow(rr, 'base');
        if (rB && rB.items && rB.items.length > 1) add({ key: 'base', step: 'roof', kind: 'cycle', g: 'footing', tip: ['base', 'Base'] });
        if (_sxAutoIcons('roof', add) || _sxRoofProdSliders()) add({ key: 'more:roof', step: 'roof', kind: 'modal', g: 'plus', tip: has.roof.label, left: 'roof', prodRoof: true });
      }
      if (has.openings) {
        var ro = _sxRows('openings'), rK = _sxRow(ro, 'openings');
        if (rK && (rK.items || []).some(function (it) { return it && it.kind; })) add({ key: 'openings', step: 'openings', kind: 'picker', g: 'window', tip: ['wiz_openings', 'Doors & windows'] });
        if (_sxAutoIcons('openings', add)) add({ key: 'more:openings', step: 'openings', kind: 'modal', g: 'plus', tip: has.openings.label, left: 'openings' });
      }
      if (has.colors && _sxColorSlots().length) add({ key: 'colors', step: 'colors', kind: 'colors', g: 'color', tip: ['toggle_colors', 'Color'] });
      if (has.exterior) {
        var re = _sxRows('exterior'), rEl = _sxRow(re, 'elements');
        if (_sxRow(re, 'facade')) add({ key: 'facade', step: 'exterior', kind: 'cycle', g: 'facade', tip: ['grp_facade', 'Facade'] });
        if (_sxItem(_sxRow(re, 'terrace'), 'terrace')) add({ key: 'terrace', step: 'exterior', kind: 'toggle', g: 'terrace', tip: ['rooms.terrace', 'Terrace'], bar: true,
          on: function () { return !!_terrace().on; }, item: function () { return _sxFind('exterior', 'terrace', 'terrace'); } });
        /* core-v597 (Marek: pergola on / off jako bazen): a switch, moved on the exterior step's ghost */
        if (_sxItem(rEl, 'pergola') && _pgFree()) add({ key: 'pergola', step: 'exterior', kind: 'toggle', g: 'pergola', tip: ['pergola', 'Pergola'],
          on: function () { return pergolaOn(); }, item: function () { return { tap: function () { togglePergola(); } }; } });
        else if (_sxItem(rEl, 'pergola') && _terrace().on) add({ key: 'pergola', step: 'exterior', kind: 'cycle', g: 'pergola', tip: ['pergola', 'Pergola'] });
        if (_sxItem(rEl, 'pool')) add({ key: 'pool', step: 'exterior', kind: 'toggle', g: 'pool', tip: ['rooms.pool', 'Pool'],
          on: function () { return poolOn(); }, item: function () { return _sxFind('exterior', 'elements', 'pool'); } });
        if (_sxRow(re, 'blinds')) add({ key: 'blinds', step: 'exterior', kind: 'toggle', g: 'blinds', tip: ['blinds', 'Blinds'],
          on: function () { return blindsOn(); }, item: function () { var r = _sxRow(_sxRows('exterior'), 'blinds'); return (r && r.items) ? r.items[0] : null; } });
        if (_sxRow(re, 'slats') && postBaySides().length) add({ key: 'slats', step: 'exterior', kind: 'cycle', g: 'cladding', tip: ['post_wall_infill', 'Infill'] });
        if (_sxAutoIcons('exterior', add)) add({ key: 'more:exterior', step: 'exterior', kind: 'modal', g: 'plus', tip: has.exterior.label, left: 'exterior' });
      }
      /* a step of the product's own: its panel, whole, in a floating panel */
      var KNOWN = { place: 1, layout: 1, structure: 1, roof: 1, openings: 1, colors: 1, exterior: 1, video: 1, assembly: 1 };
      for (k = 0; k < SS.list.length; k++) {
        var st = SS.list[k];
        if (!st || KNOWN[st.key]) continue;
        add({ key: 'step:' + st.key, step: st.key, kind: 'modal', g: 'layers', tip: st.label, panel: st.key });
      }
      if (has.video && _sxVideoId) add({ key: 'video', step: null, kind: 'video', g: 'play', tip: ['wiz_video', 'Video'] });
      return out;
    }

    /* ---- the plan strip's icons: the padlock, then the Floorplan rows ------- */
    function _sxPlanIcons() {
      var out = [{ key: 'lock', kind: 'lock', tip: ['edit', 'Edit'] }], tools = [];
      _sxRows('layout').forEach(function (r, ri) {
        if (!r || r.range || !r.items) return;
        /* core-v628: a row that is an edit step of its own (r.step -- RANCH's
           cross gables): one icon that picks it, its slider(s) on the bar */
        if (r.step && r.key && r.items[0]) {
          tools.push(r.items[0]);
          out.push({ key: 'step:' + r.key, rowKey: r.key, kind: 'step', row: r, item: r.items[0], tip: r.label || r.items[0].label || '',
                     dual: r.slider2 || null, onPick: (typeof r.onPick === 'function') ? r.onPick : null });
          return;
        }
        r.items.forEach(function (it, ii) {
          if (!it) return;
          var k = 't' + ri + '.' + ii + ':' + String(it.label || it.title || '').slice(0, 24);
          tools.push(it);
          /* core-v628: a tile with choices is walked on the bar's arrows, its
             own word over them (RANCH's L shape: Rectangle, L shape 1..4) */
          if (it.choices && it.choices.length) {
            out.push({ key: 'cyc:' + (r.key || k), rowKey: r.key || '', kind: 'cycle', item: it, choices: it.choices, tip: r.label || it.label || '' });
            return;
          }
          out.push({ key: k, kind: 'tool', item: it, tip: it.title || it.label || (r.label || '') });
        });
        /* core-v626: the fitout icon after the Plan row's tools */
        if (r.label === _t('grp_plan', 'Plan') && _fitHasCatalog()) {
          var fitL = _cap(_t('container_int_fitout', 'FITOUT'));
          var fitIt = { label: fitL, title: fitL, svg: _sxG('sofa'), act: true, on: function () { return false; }, tap: _fitStripTap };
          tools.push(fitIt);
          out.push({ key: 'fitout', kind: 'tool', item: fitIt, tip: fitL });
        }
      });
      if (_fitHasCatalog() && !tools.some(function (x) { return x && x.tap === _fitStripTap; })) {
        var fitL2 = _cap(_t('container_int_fitout', 'FITOUT'));
        var fitIt2 = { label: fitL2, title: fitL2, svg: _sxG('sofa'), act: true, on: function () { return false; }, tap: _fitStripTap };
        tools.push(fitIt2);
        out.push({ key: 'fitout', kind: 'tool', item: fitIt2, tip: fitL2 });
      }
      _SX.tools = tools;
      return out;
    }
    function _sxIconBy(key) {
      for (var i = 0; i < _SX.icons.length; i++) if (_SX.icons[i].key === key) return _SX.icons[i];
      return null;
    }

    /* ---- what each pair of arrows walks ----------------------------------- */
    function _sxRoofList(row) {
      var out = [];
      ((row && row.items) || []).forEach(function (it) {
        if (!it) return;
        if (it.key === 'pitched') {
          /* core-v707: each entry says its kind (t), so the tour can step to
             another TYPE past the half-hips; the last stage is the full hip */
          var n = roofStageCount(), q;
          for (q = 0; q < n; q++) (function (kk) {
            out.push({ t: kk === 0 ? 'gable' : (kk === n - 1 ? 'hip' : 'hip-part'),
                       on: function () { var t = STATE.roof.type; return (t === 'gable' || t === 'hip') && pitchedStage() === kk; },
                       go: function () { _sxPitched(kk); } });
          })(q);
        } else if (it.key === 'gable' || it.key === 'mono' || it.key === 'flat') {
          out.push({ t: it.key, on: it.on, go: it.tap });
        }
      });
      return out;
    }
    /* the pitched tile's own preparation, then straight to the stage asked
       for -- the tile walks one stage a tap, the arrows go either way */
    function _sxPitched(k) {
      var S = STATE, o = _sxRoofOpts || {};
      S.roof.parapet = false;
      S.roof.eave = (o.eave != null) ? o.eave : (S.roof.eave || 0.4);
      if (S.roof.pitch < 15 || S.roof.pitch > 35) S.roof.pitch = o.pitch || 26;
      setPitchedStage(k);
    }
    function _sxChoice(row) {
      return ((row && row.items) || []).filter(function (it) { return it && !it.dim; })
        .map(function (it) { return { on: it.on, go: it.tap }; });
    }
    function _sxFacade() {
      var k0 = cladKind();
      if (CLADDINGS.indexOf(k0) < 0) {
        var it = _sxFind('exterior', 'facade', 'facade');
        /* core-v619: a product's own facade kinds may come as the tile's
           choices ({ on, go } each), so the bar's arrows walk both ways */
        if (it && it.choices && it.choices.length) return it.choices;
        return it ? [{ on: function () { return true; }, go: it.tap }] : [];
      }
      return CLADDINGS.map(function (k) {
        return { on: function () { return cladKind() === k; }, go: function () { setCladding(k); } };
      });
    }
    var _SX_PERG = ['none', 'wood', 'fabric'];
    function _sxPergola() {
      return _SX_PERG.map(function (k) {
        return {
          on: function () { var p = _terrace().pergola || 'none'; if (p === 'alu') p = 'wood'; return p === k; },
          go: function () {
            var T2 = _terrace();
            if (!T2.on) { _log('pergola: turn the terrace on first -- it stands over it'); return; }
            if (k !== 'none' && _terraceSheltered()) { _log('pergola: the terrace is under the roof overhang -- the roof is the shelter'); return; }
            T2.pergola = k; savePref(); rebuild();
          }
        };
      });
    }
    function _sxSlats() {
      /* core-v723: the facade's direction only, and the picked bay alone */
      return _pwCycle().map(function (k) {
        return { on: function () { return postWallKind() === k; },
                 go: function () { var who = _pwSetKind(k); savePref(); rebuild(); _log('post wall: ' + k + ' -- ' + who); } };
      });
    }
    function _sxSurface() {
      var it = _sxFind('exterior', 'terrace', 'terraceSurface');
      if (!it) return [];
      return [{ on: function () { return _terrace().surface !== 'pavers'; }, go: it.tap },
              { on: function () { return _terrace().surface === 'pavers'; }, go: it.tap }];
    }
    function _sxCycle(I) {
      if (!I) return [];
      if (I.choices) return I.choices;
      if (I.auto) { var rA = _sxAutoRow(I); return rA ? rA.items.map(function (it) { return { on: it.on, go: it.tap }; }) : []; }
      switch (I.key) {
        case 'roof':     return _sxRoofList(_sxRow(_sxRows('roof'), 'roof'));
        case 'covering': return _sxChoice(_sxRow(_sxRows('roof'), 'covering'));
        case 'base':     return _sxChoice(_sxRow(_sxRows('roof'), 'base'));
        case 'facade':   return _sxFacade();
        case 'pergola':  return _sxPergola();
        case 'slats':    return _sxSlats();
        case 'terrace':  return _sxSurface();
      }
      return [];
    }
    /* THE OPTION'S OWN WORD, OVER THE ARROWS (core-v593, Marek: drivejsi
       popisy dlazdic, ktere se menily s typem, dej nad banner): the caption
       the tile used to wear -- Gable, Hip 1, Hip 2, Hip, Mono-pitch, Parapet;
       the covering, the footing, the facade variant, the slats -- read off the
       same rows, so it is the words the drawer had, already translated */
    function _sxOnLabel(row, keys) {
      var L = (row && row.items) || [], i;
      for (i = 0; i < L.length; i++) {
        var it = L[i];
        if (!it || (keys && keys.indexOf(it.key) < 0)) continue;
        var on = false;
        try { on = !!(it.on && it.on()); } catch (e) { on = false; }
        if (on) return it.label || '';
      }
      return '';
    }
    function _sxValue(I) {
      try {
        if (I.choices) { var J = _sxIconBy(I.key) || I; return (J.item && J.item.label) || ''; }
        if (I.auto) return _sxOnLabel(_sxAutoRow(I));
        switch (I.key) {
          case 'roof':     return _sxOnLabel(_sxRow(_sxRows('roof'), 'roof'), ['pitched', 'gable', 'mono', 'flat']);
          case 'covering': return _sxOnLabel(_sxRow(_sxRows('roof'), 'covering'));
          case 'base':     return _sxOnLabel(_sxRow(_sxRows('roof'), 'base'));
          case 'facade':   var itF = _sxFind('exterior', 'facade', 'facade'); return itF ? itF.label : '';
          case 'pergola':
            if ((_terrace().pergola || 'none') === 'none') return _t('sauna_ui_off', 'OFF');
            var itP = _sxFind('exterior', 'elements', 'pergola'); return itP ? itP.label : '';
          case 'slats':    var rS = _sxRow(_sxRows('exterior'), 'slats'); return (rS && rS.items && rS.items[0]) ? rS.items[0].label : '';
          case 'terrace':  return (_terrace().surface === 'pavers') ? _t('pool_surface_tiles', 'Tiles') : _t('decking', 'Decking');
        }
      } catch (e) {}
      return '';
    }
    function _sxStep(key, dir) {
      var I = _sxIconBy(key);
      if (!I) return;
      if (I.kind === 'colors') { _SX.colorIdx += dir; _sxRefresh(); return; }
      var L = _sxCycle(I), n = L.length, cur = -1, i;
      if (!n) { _log('strip    ' + key + ': nothing to walk'); return; }
      for (i = 0; i < n; i++) { try { if (L[i].on && L[i].on()) { cur = i; break; } } catch (eO) {} }
      var nx = (cur < 0) ? (dir > 0 ? 0 : n - 1) : ((cur + dir + n) % n);
      try { L[nx].go(); } catch (eG) { _log('strip    ' + key + ' THREW ' + ((eG && eG.message) || eG)); }
      _sxAfter(key + ' ' + (dir > 0 ? 'next' : 'back') + ' -> ' + (nx + 1) + ' of ' + n + ' "' + _sxValue(I) + '"');
    }

    /* ---- the sliders: the canvas ones stay the engine, these drive them ---- */
    function _sxFmtMm(v) { return STATE.isMetric ? (Math.round(v * 1000) + ' mm') : fmtLen(v); }
    /* A PRODUCT'S LIMIT AT THE TOP OF A SLIDER (core-v647, THOW A2: MAX on the
       height and the pitch sliders, as on the width): __PROD._sliderAtMax(key)
       true puts MAX after the figure, in the width's red. Without the hook
       nothing changes. */
    function _sxAtMax(key) { try { return !!(__PROD && typeof __PROD._sliderAtMax === 'function' && __PROD._sliderAtMax(key)); } catch (e) { return false; } }
    var _SX_SL = {
      pitch: { title: ['roof_pitch', 'Roof pitch'],
        core: function () { if (!_pitchSl) _initPitch(); return _pitchSl; },
        sync: function (S) { if (STATE.roof.type !== 'flat') { clampPitch(); S.set({ min: pitchMin(), max: pitchMax(), value: STATE.roof.pitch }); try { _pitchLabel(); } catch (e) {} } },
        on: function () { return STATE.roof.type !== 'flat' && !STATE.roof.parapet; },
        read: function () { return Math.round(STATE.roof.pitch || 0) + '\u00B0' + (_sxAtMax('pitch') ? ' MAX' : ''); },
        max: function () { return _sxAtMax('pitch'); } },
      height: { title: ['mc_wall_height', 'Wall height'],
        want: function () { return !_noWallH(); },
        core: function () { if (!_heightSl) _initHeight(); return _heightSl; },
        sync: function (S) { S.set({ min: CLEAR_LIM.min, max: CLEAR_LIM.max, step: _heightStep(), value: _wallTotal() }); try { _heightLabel(); } catch (e) {} },
        on: function () { return true; },
        read: function () { return fmtLen(_wallTotal()) + (_sxAtMax('height') ? ' MAX' : ''); },
        max: function () { return _sxAtMax('height'); } },
      floor: { title: ['floor_label', 'Floor'],
        want: function () { return !!_floorRange(); },
        core: function () { if (!_floorSl) _initFloor(); return _floorSl; },
        sync: function (S) { var R = _floorRange(); if (R) S.set({ min: R.min, max: R.max, value: _floorNow() }); },
        on: function () { return true; },
        read: function () { return '+' + _sxFmtMm(_floorNow()); } },
      storey: { title: ['storeys', 'Storey'],
        want: function () { try { return !!(__PROD && __PROD._storeySlider); } catch (e) { return false; } },
        core: function () { if (!_stSl) _initStorey(); return _stSl; },
        sync: function (S) { var R = _stRange(); S.set({ min: R.min, max: R.max, step: R.step, value: _stNow() }); },
        on: function () { return true; },
        read: function () { return _sxFmtMm(_stNow()); } },
      gap: { title: ['post_wall_gap', 'Slat spacing'],
        core: function () { if (!_pwGapSl) { try { seatPostWallGap(global.document.createElement('div'), _t); } catch (e) {} } return _pwGapSl; },
        sync: function (S) { S.set({ value: Math.round(postWallGap() * 1000) }); },
        on: function () { return postWallIsSlats(); },
        read: function () { return Math.round(postWallGap() * 1000) + ' mm'; } }
    };
    function _sxPaint(inp) {
      var lo = parseFloat(inp.min) || 0, hi = parseFloat(inp.max) || 0, v = parseFloat(inp.value) || 0;
      var pct = (hi > lo) ? Math.max(0, Math.min(100, (v - lo) / (hi - lo) * 100)) : 0;
      inp.style.backgroundImage = 'linear-gradient(to right,#D2A878 0%,#D2A878 ' + pct + '%,rgba(0,0,0,0.13) ' + pct + '%,rgba(0,0,0,0.13) 100%)';
    }
    /* one of ours, which hands every move to the core's own slider so the
       rules behind it (courses, storeys, clamps) are the ones that run */
    function _sxRange(key, withTitle) {
      var D = _SX_SL[key], doc = global.document;
      var row = doc.createElement('div');
      row.className = 'cf-sx-srow';
      if (withTitle) {
        var tt = doc.createElement('div');
        tt.className = 'cf-sx-stt';
        tt.textContent = _sxWord(D.title);
        row.appendChild(tt);
      }
      var line = doc.createElement('div');
      line.className = 'cf-sx-sline';
      var inp = doc.createElement('input');
      inp.type = 'range';
      inp.className = 'cf-sx-range';
      inp.setAttribute('data-cf-nopinch', '1');
      inp.setAttribute('aria-label', _sxWord(D.title));
      var val = doc.createElement('span');
      val.className = 'cf-sx-val';
      line.appendChild(inp);
      line.appendChild(val);
      row.appendChild(line);
      var done = function () { global.setTimeout(function () { if (_SX.drag === key) _SX.drag = null; }, 60); };
      inp.addEventListener('pointerdown', function (e) { e.stopPropagation(); _SX.drag = key; });
      inp.addEventListener('pointerup', done);
      inp.addEventListener('pointercancel', done);
      inp.addEventListener('input', function () {
        _SX.drag = key;
        var S = D.core();
        if (!S || !S.input) return;
        S.input.value = inp.value;
        try { S.input.dispatchEvent(new global.Event('input')); } catch (eI) {}
        _sxPaint(inp);
        val.textContent = D.read();
        val.style.color = (D.max && D.max()) ? '#C8102E' : '';
      });
      inp.addEventListener('change', function () {
        var S = D.core();
        if (S && S.input) { try { S.input.dispatchEvent(new global.Event('change')); } catch (eC) {} }
        _SX.drag = null;
        global.setTimeout(function () { try { undoPush(); } catch (e) {} }, 0);
        _log('strip    ' + key + ' -> ' + D.read() + ' on the strip\'s slider');
      });
      row.__upd = function () {
        var S = D.core();
        if (!S || !S.input) { row.style.display = 'none'; return; }
        row.style.display = '';
        var on = !!D.on();
        if (_SX.drag !== key) {
          try { D.sync(S); } catch (eS) {}
          inp.min = S.input.min; inp.max = S.input.max; inp.step = S.input.step; inp.value = S.input.value;
        }
        inp.disabled = !on;
        row.classList.toggle('cf-off', !on);
        _sxPaint(inp);
        val.textContent = D.read();
        val.style.color = (D.max && D.max()) ? '#C8102E' : '';
      };
      return row;
    }

    /* TWO THUMBS ON ONE TRACK (core-v628, Marek: dva puntiky, jeden ovlada
       sirku vikyru na jedne strane, druhy -- jina barva -- na druhe; rozmer
       pod danym puntikem). D from the product: title, min(), max(), step(),
       get(i) / set(i, v, done) in its own units, read(i) the words under the
       thumb, on(i), color[i], name[i]. Two range inputs over one track, only
       their thumbs take a finger; the one nearer the finger comes on top, so
       two thumbs on one spot can both be taken. While a thumb moves the
       product builds light (its set(i, v, false)); let go, set(i, v, true)
       and one Undo step. */
    function _sxRange2(D, key) {
      var doc = global.document, row = doc.createElement('div');
      row.className = 'cf-sx-srow';
      var box = doc.createElement('div');
      box.className = 'cf-sx-dual';
      var tr = doc.createElement('div');
      tr.className = 'cf-sx-dtr';
      box.appendChild(tr);
      var ins = [], vals = [], i;
      for (i = 0; i < 2; i++) (function (k) {
        var inp = doc.createElement('input');
        inp.type = 'range';
        inp.className = 'cf-sx-range cf-sx-d' + k;
        inp.setAttribute('data-cf-nopinch', '1');
        inp.setAttribute('aria-label', String((D.name && D.name[k]) || ('' + (k + 1))));
        var cl = (D.color && D.color[k]) || (k ? '#3E7CB1' : '#D2A878');
        inp.style.setProperty('--cf-th', cl);
        var val = doc.createElement('span');
        val.className = 'cf-sx-dv';
        val.style.borderBottomColor = cl;
        inp.addEventListener('pointerdown', function (e) { e.stopPropagation(); _SX.drag = key; });
        var done = function () { global.setTimeout(function () { if (_SX.drag === key) _SX.drag = null; }, 60); };
        inp.addEventListener('pointerup', done);
        inp.addEventListener('pointercancel', done);
        inp.addEventListener('input', function () {
          _SX.drag = key;
          try { D.set(k, Number(inp.value), false); } catch (eS) { _log('strip    ' + key + ' thumb ' + k + ' THREW ' + ((eS && eS.message) || eS)); }
          place();
        });
        inp.addEventListener('change', function () {
          try { D.set(k, Number(inp.value), true); } catch (eS2) {}
          _SX.drag = null;
          global.setTimeout(function () { try { undoPush(); } catch (e2) {} }, 0);
          _log('strip    ' + key + ' thumb ' + k + ' -> ' + (D.read ? D.read(k) : inp.value) + ' on the strip\'s two-thumb slider');
          place();
        });
        box.appendChild(inp); box.appendChild(val);
        ins.push(inp); vals.push(val);
      })(i);
      /* the thumb nearer the finger on top */
      box.addEventListener('pointerdown', function (e) {
        var R = box.getBoundingClientRect(), lo = Number(ins[0].min) || 0, hi = Number(ins[0].max) || 1;
        var f = R.width > 16 ? Math.max(0, Math.min(1, (e.clientX - R.left - 8) / (R.width - 16))) : 0.5, v = lo + f * (hi - lo);
        var d0 = Math.abs(Number(ins[0].value) - v), d1 = Math.abs(Number(ins[1].value) - v), top = (d1 < d0) ? 1 : 0;
        ins[top].style.zIndex = '3'; ins[1 - top].style.zIndex = '2';
      }, true);
      function place() {
        var k, lo = Number(ins[0].min) || 0, hi = Number(ins[0].max) || 1, W = box.clientWidth || 0;
        for (k = 0; k < 2; k++) {
          var f = (hi > lo) ? (Number(ins[k].value) - lo) / (hi - lo) : 0;
          vals[k].style.left = (8 + f * Math.max(0, W - 16)) + 'px';
          vals[k].textContent = D.read ? D.read(k) : ins[k].value;
        }
        /* two labels on one spot: the second steps down a line */
        var a = parseFloat(vals[0].style.left) || 0, b = parseFloat(vals[1].style.left) || 0;
        vals[1].style.top = (Math.abs(a - b) < 58) ? '42px' : '';
      }
      row.appendChild(box);
      row.__upd = function () {
        var on0 = true, k;
        if (_SX.drag !== key) {
          for (k = 0; k < 2; k++) {
            ins[k].min = String(D.min()); ins[k].max = String(D.max()); ins[k].step = String(D.step ? D.step() : 1);
            ins[k].value = String(D.get(k));
          }
        }
        for (k = 0; k < 2; k++) {
          var on = true;
          try { on = !D.on || !!D.on(k); } catch (eO) { on = true; }
          ins[k].classList.toggle('cf-off', !on);
          on0 = on0 && !on;
        }
        row.classList.toggle('cf-off', on0);
        place();
      };
      return row;
    }

    /* ---- the look, once ---------------------------------------------------- */
    function _sxCss() {
      var U = _UI();
      if (U && U.stripCss) U.stripCss(_CMD_FOOT);
    }
    var _SX_SVG = {
      x: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M6 6l12 12"/><path d="M18 6L6 18"/></svg>',
      l: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>',
      r: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M9 5l7 7-7 7"/></svg>',
      shut: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="4.5" y="10.5" width="15" height="10" rx="2"/><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3"/></svg>',
      open: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><rect x="4.5" y="10.5" width="15" height="10" rx="2"/><path d="M8 10.5V7.5a4 4 0 0 1 7.6-1.8"/></svg>',
      /* the handle: four arrows out of the middle */
      move: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3v18M3 12h18"/><path d="M9 6l3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3"/></svg>'
    };
    function _sxBtn(cls, svg, label) {
      var b = global.document.createElement('button');
      b.type = 'button';
      b.className = cls;
      b.innerHTML = svg;
      if (label) b.setAttribute('aria-label', label);
      return b;
    }

    /* ---- the tooltip: hover with a mouse, five seconds after a tap --------- */
    function _sxTipShow(btn, I, ms) {
      var tip = _SX.tip, root = $('cf-root');
      if (!tip || !root || !btn || !I) return;
      var w = _sxWord(I.tip);
      if (!w) return;
      tip.textContent = w;
      var rr = root.getBoundingClientRect(), br = btn.getBoundingClientRect(), sr = _SX.el ? _SX.el.getBoundingClientRect() : br;
      tip.style.right = Math.round(rr.right - sr.left + 10) + 'px';
      tip.style.top = Math.round(br.top + br.height / 2 - rr.top) + 'px';
      tip.classList.add('cf-on');
      if (_SX.tipT) { global.clearTimeout(_SX.tipT); _SX.tipT = 0; }
      if (ms) _SX.tipT = global.setTimeout(function () { _SX.tipT = 0; tip.classList.remove('cf-on'); }, ms);
    }
    function _sxTipHide() {
      if (_SX.tipT) { global.clearTimeout(_SX.tipT); _SX.tipT = 0; }
      if (_SX.tip) _SX.tip.classList.remove('cf-on');
    }

    /* ---- opening the one strip or the other -------------------------------- */
    function _sxEditOpen() {
      if (!_wiz) return;
      if (_wiz.isOpen() && _SX.mode === 'edit') return;
      if (_wiz.isOpen()) _wiz.close();
      _SX.mode = 'edit';
      _SX.sel = null;
      _wiz.open();
    }
    /* THE PLAN STRIP (core-v593, Marek: floorplan cely presunout do kroku s
       nahranim pudorysu -- otevre samostatny banner napravo s celym menu pro
       editaci pudorysu, prvni ikona zamku; krizkem se zavre editace fp) */
    function _sxPlan(on, why) {
      if (!_wiz) return;
      if (on) {
        if (_sxPlanOpen()) return;
        if (_wiz.isOpen()) _wiz.close();
        _SX.mode = 'plan';
        _SX.sel = null;
        _SX.lock = true;
        STATE.doorTool = false;
        STATE.cutsOn = false;
        try { if (fitEdit()) fitEdit(false, 'the plan strip opened locked'); } catch (eF) {}
        _wiz.open();
        _log('strip    the PLAN strip opened, locked' + (why ? (' -- ' + why) : ''));
      } else if (_sxPlanOpen()) {
        _wiz.close();
        _log('strip    the plan strip closed' + (why ? (' -- ' + why) : ''));
      }
    }
    /* which step the wizard opens on in strip mode */
    function _sxStart() { return (_SX.mode === 'plan') ? 'layout' : null; }

    /* ---- a tap on an icon ---------------------------------------------------- */
    /* ONE EDIT FUNCTION AT A TIME ON THE PLAN STRIP (core-v628, Marek: v
       pravem banneru u floorplan maji ikony klepnutim danou funkci editace
       spustit, klikem na jinou ikonu se vypnou; neni tam zadna funkce, ktera
       ma prvek on / off -- i u jinych projektu). A tap on an icon starts its
       function and ends the one at work: the door tool, the cut handles, the
       openings tool, the furniture, a product's step (RANCH's cross gables,
       the L shape's arrows). A tap on the function at work ends it. An action
       (a wall, mirror, a plan upload) runs once and ends the one at work. A
       storey card and a setting (Suggested) are not functions: they keep what
       they are and end nothing. The functions are the tools marked fn (the
       core's Doors, Cut handles, Opening) and the furniture; a product's own
       tile with an on() is a SETTING -- BARNDO's Loft is a loft, not a mode,
       and ending it would take the loft away. */
    function _sxSetting(I) {
      var it = I && I.item;
      if (!it || I.kind !== 'tool') return false;
      if (it.small || it.setting || (typeof _SUG_SVG !== 'undefined' && it.svg === _SUG_SVG)) return true;
      if (it.fn || it.act || it.img) return false;
      try { if (it.tap === _fitStripTap) return false; } catch (eF) { return false; }
      return typeof it.on === 'function';
    }
    function _sxFnOn(I) {
      if (!I) return false;
      if (I.kind === 'step' || I.kind === 'cycle') return _SX.sel === I.key;
      if (I.kind !== 'tool' || !I.item) return false;
      var it = I.item;
      try { if (it.tap === _fitStripTap) return !!fitEdit(); } catch (eF) { return false; }
      if (!it.fn || typeof it.on !== 'function') return false;
      try { return !!it.on(); } catch (eO) { return false; }
    }
    function _sxFnOff(I, why) {
      if (!I) return;
      if (_SX.sel === I.key) _SX.sel = null;
      if (I.kind !== 'tool' || !I.item || !_sxFnOn(I)) return;
      try {
        if (I.item.tap === _fitStripTap) fitEdit(false, why);
        else I.item.tap();
      } catch (eT) { _log('strip    ending ' + I.key + ' THREW ' + ((eT && eT.message) || eT)); }
      _log('strip    ' + String(_sxWord(I.tip) || I.key) + ' ended -- ' + why);
    }
    function _sxFnAllOff(exceptKey, why) {
      _controls.endTools(_SX.icons || [], exceptKey, function (J) { return J.kind !== 'lock' && _sxFnOn(J); }, function (J) { _sxFnOff(J, why); });
      if (_SX.sel && _SX.sel !== exceptKey) _SX.sel = null;
    }
    function _sxPlanPick(I) {
      var it = I.item || null, key = I.key;
      if (it && it.down) { _sxFnAllOff(key, 'another icon (' + key + ')'); return; }   /* a drag source: its own pointerdown runs it */
      if (_SX.lock) { _SX.lock = false; try { rebuild(); drawPlan(); _hRefresh(); } catch (eL) {} _log('strip    the plan unlocked by ' + key); try { _cmdSync(); } catch (eCs) {} }
      if (_sxSetting(I)) {
        try { if (it && it.tap) it.tap(); } catch (eT) { _log('strip    ' + key + ' THREW ' + ((eT && eT.message) || eT)); }
        _sxAfter('plan setting "' + String(_sxWord(I.tip) || key) + '"');
        return;
      }
      var was = _sxFnOn(I);
      _sxFnAllOff(key, 'another icon (' + key + ')');
      if (was) { _sxFnOff(I, 'tapped again'); _sxAfter('plan function "' + String(_sxWord(I.tip) || key) + '" ended'); try { if (_planWanted()) drawPlan(); } catch (eD0) {} return; }
      if (I.kind === 'tool') {
        try { if (it && it.tap) it.tap(); } catch (eT2) { _log('strip    plan tool "' + (it.label || '?') + '" THREW ' + ((eT2 && eT2.message) || eT2)); }
        _SX.sel = _sxFnOn(I) ? key : null;
      } else {
        _SX.sel = key;
        if (I.onPick) { try { I.onPick('plan'); } catch (eP) { _log('strip    ' + key + ' pick THREW ' + ((eP && eP.message) || eP)); } }
      }
      try { if (_planWanted()) drawPlan(); } catch (eD) {}
      _sxAfter('plan function "' + String(_sxWord(I.tip) || key) + '"' + (_SX.sel ? ' at work' : ' run'));
    }
    /* what the strip has picked, by the product's row key (core-v628) --
       RANCH asks whether its cross gables' step is open */
    function _sxPicked(rowKey) {
      try {
        if (!_stripMode() || !_wiz || !_wiz.isOpen() || !_SX.sel) return false;
        var I = _sxIconBy(_SX.sel);
        return !!(I && I.rowKey && I.rowKey === rowKey);
      } catch (e) { return false; }
    }
    function _sxTap(key, btn, e) {
      var I = _sxIconBy(key);
      if (!I) return;
      var mouse = !!(e && e.pointerType === 'mouse');
      if (!mouse) _sxTipShow(btn, I, 5000);
      if (I.kind === 'lock') { _sxLock(); return; }
      if (_SX.mode === 'plan' && (I.kind === 'tool' || I.kind === 'step' || I.kind === 'cycle')) { _sxPlanPick(I); return; }
      if (I.kind === 'tool') { _sxTool(I); return; }
      /* core-v628: a product's edit step on the edit strip: picked, put away
         by a second tap or by any other pick */
      if (I.kind === 'step') {
        if (_SX.sel === key) { _SX.sel = null; _sxAfter(key + ' put away'); return; }
        _SX.sel = key;
        _sxGoto(I.step);
        if (I.onPick) { try { I.onPick('edit'); } catch (eP) { _log('strip    ' + key + ' pick THREW ' + ((eP && eP.message) || eP)); } }
        _sxAfter(key + ' picked -- its step open');
        return;
      }
      if (I.kind === 'goplan') { _sxPlan(true, 'the floorplan icon'); return; }
      if (I.kind === 'video') { if (_sxVideoId) videoOpen(_sxVideoId); return; }
      if (I.kind === 'picker') {
        _SX.sel = null;
        _sxGoto(I.step);
        try { if (!opEdit()) opEdit(true); } catch (eO) {}
        _sxPicker();
        _sxAfter('openings: the drawer opened, the units on the model can be moved');
        return;
      }
      if (I.kind === 'toggle') {
        _sxGoto(I.step);
        /* core-v624: an on / off with its own slider, lit but put away, comes
           up with its slider on the first tap; the next tap switches it off */
        var lit0 = false;
        try { lit0 = !!(I.own && I.on && I.on()); } catch (eL0) { lit0 = false; }
        if (lit0 && _SX.sel !== key) { _SX.sel = key; _sxAfter(key + ' picked -- its slider up'); return; }
        var it = I.item ? I.item() : null;
        if (!it || !it.tap) { _log('strip    ' + key + ': no tile to switch'); return; }
        try { it.tap(); } catch (eT) { _log('strip    ' + key + ' THREW ' + ((eT && eT.message) || eT)); }
        var now = false;
        try { now = !!(I.on && I.on()); } catch (eN) { now = false; }
        _SX.sel = (now && I.bar) ? key : null;
        _sxAfter(key + ' switched ' + (now ? 'ON' : 'off'));
        return;
      }
      if (_SX.sel === key) { _SX.sel = null; _sxAfter(key + ' put away'); return; }
      _SX.sel = key;
      _sxGoto(I.step);
      _sxAfter(key + ' picked');
    }
    /* a tile of the Floorplan rows: a tap while locked opens the lock first,
       so a tool is never a dead button */
    function _sxTool(I) {
      var it = I.item;
      if (!it) return;
      if (_SX.lock) { _SX.lock = false; try { rebuild(); drawPlan(); _hRefresh(); } catch (eL) {} _log('strip    the plan unlocked by a tool'); }
      if (it.down) return;     /* a drag source: its own pointerdown runs it */
      try { if (it.tap) it.tap(); } catch (eT) { _log('strip    plan tool "' + (it.label || '?') + '" THREW ' + ((eT && eT.message) || eT)); }
      _sxAfter('plan tool "' + String(it.label || it.title || '?') + '"');
    }
    function _sxPicker() {
      var r = _sxRow(_sxRows('openings'), 'openings');
      var kinds = ((r && r.items) || []).filter(function (it) { return it && it.kind; })
        .map(function (it) { return { kind: it.kind, label: it.label }; });
      if (!kinds.length) { _log('strip    openings: the product offers no kind'); return; }
      var K = (typeof __PROD.KIT === 'function') ? __PROD.KIT() : null;
      if (!K || !K.openPicker) { _log('strip    openings: the kit has no picker'); return; }
      var a = armedOpening(), ti = 0, q;
      for (q = 0; q < kinds.length; q++) if (a && a.kind === kinds[q].kind) ti = q;
      /* ONE ROW OF TABS AND NOTHING ELSE (core-v593, Marek: je tam zdvojene
         menu -- muzou byt jen zalozky): the title is the step's, the tabs
         name the kinds, no hand and no width chips */
      var ttl = String(_t('wiz_openings', 'Doors & windows') || '');
      if (ttl && ttl === ttl.toUpperCase()) ttl = ttl.charAt(0) + ttl.slice(1).toLowerCase();
      K.openPicker(kinds[ti].kind, kinds[ti].label, { tabs: kinds, tab: ti, title: ttl });
    }
    function _sxLock() {
      _SX.lock = !_SX.lock;
      if (_SX.lock) {
        try { _sxFnAllOff(null, 'the padlock'); } catch (eFo) {}
        STATE.doorTool = false;
        STATE.cutsOn = false;
        try { if (fitEdit()) fitEdit(false, 'the padlock'); } catch (e) {}
        try { if (_armedPart) _armedPart = null; } catch (eA) {}
      }
      try { rebuild(); drawPlan(); } catch (e2) {}
      try { _hRefresh(); } catch (e3) {}
      _sxAfter('the plan ' + (_SX.lock ? 'LOCKED -- looked at, not edited' : 'UNLOCKED -- the whole floorplan menu works'));
      try { _cmdSync(); } catch (eCs) {}
    }

    /* ---- the controls on the canvas ------------------------------------------ */
    function _sxBarSpec(I) {
      if (!I) return null;
      /* core-v628: a product's step -- its title, no arrows, its slider(s) */
      if (I.kind === 'step') {
        var sl2 = I.slider && _SX_SL[I.slider] ? I.slider : null;
        if (!I.dual && !sl2) return { label: _sxWord(I.tip), cur: '', noArrows: true, sig: 'p' };
        return { label: _sxWord(I.tip), cur: '', slider: sl2, dual: I.dual || null, noArrows: true, sig: 'p' + (sl2 || '') + (I.dual ? 'd' : '') };
      }
      if (I.kind === 'cycle' || (I.kind === 'toggle' && I.bar)) {
        if (I.kind === 'toggle' && !(I.on && I.on())) return null;
        if (!_sxCycle(I).length) return null;
        var sl = I.slider || ((I.key === 'slats' && postWallIsSlats()) ? 'gap' : null);
        /* core-v624: an on / off with its own slider has no arrows and no caption over them */
        if (I.own) return { label: _sxWord(I.tip), cur: '', slider: sl, noArrows: true, sig: 'o' + (sl || '') };
        return { label: _sxWord(I.tip), cur: _sxValue(I), slider: sl, sig: 'c' + (sl || '') };
      }
      if (I.kind === 'colors') {
        var CS = _sxColorSlots();
        if (!CS.length) return null;
        var ci = ((_SX.colorIdx % CS.length) + CS.length) % CS.length, s = CS[ci];
        return { label: _sxWord(I.tip), cur: _t(s.labelKey, s.def) + (s.label2Key ? (' / ' + _t(s.label2Key, s.def2)) : ''), dots: s, sig: 'k' + s.key };
      }
      return null;
    }
    function _sxBarDrop() {
      if (_SX.bar && _SX.bar.parentNode) _SX.bar.parentNode.removeChild(_SX.bar);
      if (_SX.sl && _SX.sl.parentNode) _SX.sl.parentNode.removeChild(_SX.sl);
      _SX.bar = null; _SX.sl = null; _SX.barSig = '';
    }
    function _sxBarBuild(I, spec) {
      var root = $('cf-root'), doc = global.document;
      if (!root) return;
      var bar = doc.createElement('div');
      bar.id = 'cf-sx-bar';
      bar.setAttribute('data-cf-nopinch', '1');
      if (spec.dots) {
        var dw = doc.createElement('div');
        dw.className = 'cf-sx-dots';
        bar.appendChild(dw);
        bar.__dots = dw;
      }
      var cur = doc.createElement('div');
      cur.className = 'cf-sx-cur';
      bar.appendChild(cur);
      bar.__cur = cur;
      var pill = doc.createElement('div');
      pill.className = 'cf-sx-pill';
      var lb = doc.createElement('span');
      lb.className = 'cf-sx-lbl';
      var key = I.key;
      var pv = _sxBtn('cf-sx-arw', _SX_SVG.l, _t('back', 'Back'));
      var nx = _sxBtn('cf-sx-arw', _SX_SVG.r, _t('next', 'Next'));
      pv.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _sxStep(key, -1); });
      nx.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _sxStep(key, 1); });
      if (!spec.noArrows) pill.appendChild(pv);
      pill.appendChild(lb);
      if (!spec.noArrows) pill.appendChild(nx);
      bar.__lbl = lb;
      bar.appendChild(pill);
      root.appendChild(bar);
      _SX.bar = bar;
      if (spec.dual) {
        var sw2 = doc.createElement('div');
        sw2.id = 'cf-sx-sl';
        sw2.className = 'cf-sx-sl2';
        sw2.setAttribute('data-cf-nopinch', '1');
        var rr2 = _sxRange2(spec.dual, key);
        sw2.appendChild(rr2);
        sw2.__row = rr2;
        root.appendChild(sw2);
        _SX.sl = sw2;
      } else if (spec.slider && _SX_SL[spec.slider]) {
        var sw = doc.createElement('div');
        sw.id = 'cf-sx-sl';
        sw.setAttribute('data-cf-nopinch', '1');
        var rr = _sxRange(spec.slider, false);
        sw.appendChild(rr);
        sw.__row = rr;
        root.appendChild(sw);
        _SX.sl = sw;
      }
    }
    function _sxBarUpdate(I, spec) {
      var bar = _SX.bar;
      if (!bar) return;
      if (bar.__lbl && bar.__lbl.textContent !== spec.label) bar.__lbl.textContent = spec.label;
      if (bar.__cur && bar.__cur.textContent !== (spec.cur || '')) bar.__cur.textContent = spec.cur || '';
      if (bar.__dots && spec.dots) {
        var s = spec.dots, curK = colorSlotKey(s.key), pal = _colorPalOf(s), dw = bar.__dots, i;
        dw.innerHTML = '';
        for (i = 0; i < pal.length; i++) (function (k) {
          var b = global.document.createElement('button');
          b.type = 'button';
          b.className = 'cf-sx-dot' + (k === curK ? ' cf-on' : '');
          b.style.background = colorHex(COLOR_PALETTE[k] != null ? COLOR_PALETTE[k] : 0x888888);
          b.setAttribute('aria-label', k);
          b.addEventListener('click', function (e) {
            e.preventDefault(); e.stopPropagation();
            colorSlotSet(s.key, k);
            _sxAfter('colour ' + s.key + ' -> ' + k);
          });
          dw.appendChild(b);
        })(pal[i]);
      }
      if (_SX.sl && _SX.sl.__row && _SX.sl.__row.__upd) _SX.sl.__row.__upd();
    }
    function _sxBarSync() {
      /* core-v628: and on the plan strip while the plan is unlocked (Marek:
         kdyz odemknu editaci floorplan ... stejny banner jako pri edit mode) */
      var I = ((_SX.mode === 'edit' || (_SX.mode === 'plan' && !_SX.lock)) && _SX.sel) ? _sxIconBy(_SX.sel) : null;
      /* the edit tools (the cross, the bin) stand where this bar stands */
      var tools = false;
      try { tools = !!_opBarEl; } catch (eT) { tools = false; }
      var spec = (I && !tools) ? _sxBarSpec(I) : null;
      if (!spec) { _sxBarDrop(); return; }
      var sig = I.key + '|' + spec.sig;
      if (!_SX.bar || _SX.barSig !== sig) { _sxBarDrop(); _sxBarBuild(I, spec); _SX.barSig = sig; }
      _sxBarUpdate(I, spec);
    }

    /* ---- the floating panel ---------------------------------------------------- */
    /* the map's address dialog borrowed into the place panel (ui-v214) */
    function _sxAddrIn(m) {
      var bd = $('cf-map-modal-backdrop');
      if (!bd) { _log('strip    place: no address dialog on the page yet (the map is not up) -- the Address tile stays'); return; }
      var box = global.document.createElement('div');
      box.className = 'cf-sx-addr';
      bd.classList.remove('open');
      box.appendChild(bd);
      m.appendChild(box);
      m.__addr = bd;
      var inp = $('cf-map-address-input'), ok = $('cf-map-modal-ok'), sg = $('cf-map-suggestions');
      try { if (inp && !inp.value && _MM && _MM.address) inp.value = _MM.address; } catch (eV) {}
      /* ONE TAP: a suggestion loads the place at once, Enter the first one */
      if (sg && !sg.__cfSxOne) {
        sg.__cfSxOne = true;
        sg.addEventListener('click', function (e) {
          if (!bd.parentNode || !bd.parentNode.classList || !bd.parentNode.classList.contains('cf-sx-addr')) return;
          var it = e.target && e.target.closest ? e.target.closest('.cf-map-modal-sugg-item') : null;
          if (!it) return;
          global.setTimeout(function () { try { if (ok && !ok.disabled) ok.click(); if (inp) inp.blur(); } catch (eO) {} }, 0);
        });
      }
      if (inp && !inp.__cfSxEnter) {
        inp.__cfSxEnter = true;
        inp.addEventListener('keydown', function (e) {
          if (e.key !== 'Enter') return;
          if (!bd.parentNode || !bd.parentNode.classList || !bd.parentNode.classList.contains('cf-sx-addr')) return;
          e.preventDefault();
          try { if (ok && !ok.disabled) ok.click(); inp.blur(); } catch (eO) {}
        });
      }
      _log('strip    place: the address field in the panel, the scenery under it (ui-v214)');
    }
    function _sxModalDrop() {
      var m = _SX.modal;
      if (m && m.__addr) {
        try { m.__addr.classList.remove('open'); global.document.body.appendChild(m.__addr); } catch (eAh) {}
        try { var sg0 = $('cf-map-suggestions'); if (sg0) sg0.classList.remove('open'); } catch (eS0) {}
        m.__addr = null;
      }
      if (m && m.__borrowed) {
        /* a product's own slider goes back to the canvas, hidden, so the next
           panel can take it again */
        m.__borrowed.forEach(function (sl) {
          try { sl.hide(); var root = $('cf-root'); if (root && sl.el) root.appendChild(sl.el); } catch (eB) {}
        });
      }
      if (m && m.parentNode) m.parentNode.removeChild(m);
      _SX.modal = null;
    }
    function _sxModalSeat(m, sl, titleKey, def) {
      if (!sl || !sl.el) return;
      var wrap = global.document.createElement('div');
      wrap.className = 'cf-sx-srow';
      var tt = global.document.createElement('div');
      tt.className = 'cf-sx-stt';
      tt.textContent = _t(titleKey, def);
      wrap.appendChild(tt);
      var el = sl.el;
      el.style.position = 'relative'; el.style.left = 'auto'; el.style.right = 'auto'; el.style.bottom = 'auto'; el.style.top = 'auto';
      el.style.transform = 'none'; el.style.width = '100%'; el.style.maxWidth = 'none'; el.style.margin = '0 0 22px'; el.style.boxSizing = 'border-box';
      try { if (sl.input) { sl.input.style.width = '100%'; sl.input.style.maxWidth = 'none'; } } catch (eI) {}
      wrap.appendChild(el);
      m.__sls.appendChild(wrap);
      try { sl.show(); } catch (eS) {}
      m.__borrowed.push(sl);
    }
    function _sxModalBuild(I) {
      var root = $('cf-root'), doc = global.document;
      if (!root) return;
      var m = doc.createElement('div');
      m.id = 'cf-sx-modal';
      m.setAttribute('data-cf-nopinch', '1');
      var hd = doc.createElement('div');
      hd.className = 'cf-sx-mh';
      var grip = _sxBtn('cf-sx-grip', _SX_SVG.move, 'Move');
      var tt = doc.createElement('span');
      tt.className = 'cf-sx-mt';
      var mx = _sxBtn('cf-sx-mx', _SX_SVG.x, _t('close', 'Close'));
      hd.appendChild(grip); hd.appendChild(tt); hd.appendChild(mx);
      var body = doc.createElement('div');
      body.className = 'cf-sx-mb';
      var rows = doc.createElement('div');
      var sls = doc.createElement('div');
      sls.className = 'cf-sx-msl';
      body.appendChild(rows); body.appendChild(sls);
      m.appendChild(hd);
      if (I.panel === 'place') { try { _sxAddrIn(m); } catch (eAd) { _log('strip    the address field THREW ' + ((eAd && eAd.message) || eAd)); } }
      m.appendChild(body);
      mx.addEventListener('click', function (e) { e.preventDefault(); e.stopPropagation(); _SX.sel = null; _sxAfter('the panel closed by its cross'); });
      /* MOVED BY ITS HEADER, the handle showing where to take it (Marek:
         viditelne tlacitko na presun) */
      hd.addEventListener('pointerdown', function (e) {
        if (mx.contains(e.target)) return;
        e.preventDefault(); e.stopPropagation();
        var mr = m.getBoundingClientRect(), off = { x: e.clientX - mr.left, y: e.clientY - mr.top, id: e.pointerId };
        try { hd.setPointerCapture(e.pointerId); } catch (eC) {}
        function mv(ev) {
          if (ev.pointerId !== off.id) return;
          var R = root.getBoundingClientRect();
          var x = Math.max(4, Math.min(R.width - m.offsetWidth - 4, ev.clientX - R.left - off.x));
          var y = Math.max(4, Math.min(R.height - 48, ev.clientY - R.top - off.y));
          m.style.left = Math.round(x) + 'px'; m.style.top = Math.round(y) + 'px'; m.style.bottom = 'auto'; m.style.transform = 'none';
          _SX.mPos = { x: Math.round(x), y: Math.round(y) };
        }
        function up() {
          hd.removeEventListener('pointermove', mv);
          hd.removeEventListener('pointerup', up);
          hd.removeEventListener('pointercancel', up);
        }
        hd.addEventListener('pointermove', mv);
        hd.addEventListener('pointerup', up);
        hd.addEventListener('pointercancel', up);
      });
      m.addEventListener('pointerdown', function (e) { e.stopPropagation(); });
      if (_SX.mPos) { m.style.left = _SX.mPos.x + 'px'; m.style.top = _SX.mPos.y + 'px'; m.style.bottom = 'auto'; m.style.transform = 'none'; }
      m.__rows = rows; m.__sls = sls; m.__tt = tt; m.__key = I.key; m.__borrowed = [];
      (I.sliders || []).forEach(function (k) {
        var D = _SX_SL[k];
        if (!D || (D.want && !D.want())) return;
        sls.appendChild(_sxRange(k, true));
      });
      /* A PRODUCT'S OWN ROOF SLIDERS (RANCH: one width for every cross
         gable) are seated here, the way the drawer seated them */
      if (I.prodRoof && _sxRoofProdSliders()) {
        try { __PROD._roofSliders(function (sl, titleKey, def) { _sxModalSeat(m, sl, titleKey, def); }, _UI()); }
        catch (ePS) { _log('strip    product roof sliders THREW ' + ((ePS && ePS.message) || ePS)); }
      }
      root.appendChild(m);
      _SX.modal = m;
    }
    function _sxModalSync() {
      var I = (_SX.mode === 'edit' && _SX.sel) ? _sxIconBy(_SX.sel) : null;
      if (!I || I.kind !== 'modal') { _sxModalDrop(); return; }
      if (!_SX.modal || _SX.modal.__key !== I.key) { _sxModalDrop(); _sxModalBuild(I); }
      var m = _SX.modal;
      if (!m) return;
      m.__tt.textContent = _sxWord(I.tip);
      var pane = m.__rows;
      try {
        if (I.left) _wiz.groupRows(pane, _sxAutoRows(I.left, _SX_CLAIM[I.left] || {}).rest);
        else if (I.panel) {
          var st = _sxSteps().has[I.panel];
          pane.innerHTML = '';
          if (st && typeof st.panel === 'function') st.panel(pane, _wiz);
        } else pane.innerHTML = '';
      } catch (eP) { _log('strip    the ' + I.key + ' panel THREW ' + ((eP && eP.message) || eP)); }
      var ch = m.__sls.children, q;
      for (q = 0; q < ch.length; q++) if (ch[q].__upd) ch[q].__upd();
    }

    /* ---- the strip itself ---------------------------------------------------------- */
    function _sxTeardown() {
      _sxBarDrop();
      _sxModalDrop();
      _sxTipHide();
      if (_SX.el && _SX.el.parentNode) _SX.el.parentNode.removeChild(_SX.el);
      _SX.el = null; _SX.list = null; _SX.sig = ''; _SX.sel = null; _SX.icons = []; _SX.tools = [];
      _SX.mode = 'edit';
    }
    function _sxIconHtml(I) {
      if (I.kind === 'lock') return _SX.lock ? _SX_SVG.shut : _SX_SVG.open;
      if (I.auto) return _sxAutoSvg(I);
      if ((I.kind === 'step' || I.kind === 'cycle') && I.item) return _sxWrapSvg(I.item.svg) || _sxG('layers');
      if (I.kind === 'tool') {
        var it = I.item || {};
        if (it.img) return '<img src="' + it.img + '" alt="" draggable="false">';
        if (it.small) return '<span class="cf-sx-num">' + String(it.label || '') + '</span>';
        return it.svg || _sxG('layers');
      }
      return _sxG(I.g);
    }
    /* a plan tile that is a switch (doors, cut handles, suggested): on / off
       like the terrace; an action (a wall, mirror, upload) or a storey card is not */
    function _sxIsSwitch(I) {
      if (I.kind === 'toggle') return true;
      /* core-v628: the plan strip has functions, not switches (Marek) */
      if (_SX.mode === 'plan') return false;
      if (I.kind !== 'tool' || !I.item) return false;
      var it = I.item;
      return !!(typeof it.on === 'function' && !it.act && !it.small && !it.img);
    }
    function _sxRender(open) {
      var root = $('cf-root');
      if (!root) return;
      _sxCss();
      _controls.frame(open, _SX.mode === 'plan');
      if (!open) { if (_SX.el) _log('strip    closed -- the canvas bar is back'); _sxTeardown(); return; }
      var doc = global.document;
      if (!_SX.el) {
        var sharedStrip = _controls.strip(function () {
          _sxTipHide();
          var wasPlan = (_SX.mode === 'plan');
          if (_wiz) _wiz.close();
          /* the plan strip's cross ends the plan editing, and the canvas bar
             folds back to its two icons once there is a plan */
          /* core-v597 (Marek: kdyz odejdu z pudorys mode, musi se zobrazit jen
             menu s dvema tlacitky -- floorplan a edit; banner s nahranim
             floorplanu se zavira zaroven s cernym pravym): the plan strip's
             cross folds the bar to its two icons with or without a plan --
             only a plan being read keeps it open, so its progress shows */
          if (wasPlan) {
            try {
              if (_cmd && _cmd.listening && _cmd.rec) { try { _cmd.rec.stop(); } catch (eRs) {} }
              if (_cmd && !_cmd.mini && !_cmd.busy) _cmdMini(true, 'the plan strip closed (core-v597)');
            } catch (eM) {}
          }
        }, function () { _sxTipHide(); _sxMore(); }, _t('close', 'Close'), _SX_SVG.x);
        _SX.el = sharedStrip.el; _SX.list = sharedStrip.list; _SX.tip = sharedStrip.tip;
        _log('strip    open (' + _SX.mode + ')');
      }
      var plan = (_SX.mode === 'plan');
      var icons = plan ? _sxPlanIcons() : _sxIcons();
      _SX.icons = icons;
      if (_SX.sel && !_sxIconBy(_SX.sel)) _SX.sel = null;
      _controls.tools(_SX.list, icons.map(function (I) {
        var on = false;
        try {
          if (I.kind === 'lock') on = !!_SX.lock;
          else if (I.kind === 'tool') on = !!(I.item && typeof I.item.on === 'function' && I.item.on());
          else on = !!(I.on && I.on());
        } catch (eO) {}
        var selected = _SX.sel === I.key;
        if (plan && I.kind !== 'lock') {
          selected = selected || (I.kind === 'tool' && (_sxFnOn(I) || (on && _sxSetting(I))));
          on = false;
        }
        return { key: I.key, html: _sxIconHtml(I), label: _sxWord(I.tip),
          toggle: _sxIsSwitch(I), separator: I.kind === 'lock', on: on, selected: selected,
          dim: plan && I.kind === 'tool' && _SX.lock,
          pressed: (_sxIsSwitch(I) || I.kind === 'lock') ? on : selected,
          tap: function (e, b) { _sxTap(I.key, b, e); },
          enter: function (b) { _sxTipShow(b, I, 0); }, leave: _sxTipHide,
          down: I.kind === 'tool' && I.item && typeof I.item.down === 'function' ? function (e, b) {
            if (_SX.lock) { _SX.lock = false; try { rebuild(); drawPlan(); _hRefresh(); } catch (eL) {} }
            _sxFnAllOff(I.key, 'another tool');
            I.item.down(e, b);
          } : null
        };
      }));
      /* the slats' plus signs only while the slats are what is being decided */
      try {
        var cur = _wiz.current();
        if (!plan && cur && cur.key === 'exterior' && _sxIconBy('slats')) {
          var wantP = (_SX.sel === 'slats');
          if (_postWallPlusWanted() !== wantP) showPostWallPlus(wantP);
        }
      } catch (ePl) {}
      _sxFit();
      _sxBarSync();
      _sxModalSync();
    }
    function _sxMore() {
      var L = _SX.list, el = _SX.el, U = _UI();
      if (!L || !el) return;
      if (U && U.stripMore) { U.stripMore(el, L); return; }
      el.classList.toggle('cf-sx-more', L.scrollHeight - L.scrollTop - L.clientHeight > 4);
      el.classList.toggle('cf-sx-less', L.scrollTop > 4);
    }
    /* the last icon half shown and the arrow (ui-v212) */
    function _sxFit() {
      var U = _UI();
      if (U && U.stripFit && _SX.el && _SX.list) U.stripFit(_SX.el, _SX.list);
      else _sxMore();
    }

    /* ---- THE BIN BESIDE THE ONE TAPPED (core-v593, Marek: klikem na vyplen
       se objevi v blizkosti vyplne ikona bin pro odstraneni daneho openings
       -- v pudorysu i ve 3D). A tap on a window or a door (a press that does
       not move it) puts a red bin next to it, held to the spot it was tapped
       as the view turns; a tap on the bin takes the unit out, one Undo. It
       goes after six seconds, at the next press anywhere else, or when the
       unit is dragged. ---------------------------------------------------- */
    var _NB = { el: null, rec: null, P: null, until: 0, raf: 0, last: 'never shown' };
    /* THE UNIT BY WHERE IT IS, NOT BY THE OBJECT: a rebuild may hand back the
       openings as new records, so the one tapped is found again by its wall
       and its place on it (as the swing button finds it) */
    function _nbFind() {
      var r = _NB.rec, L = STATE.openings || [], i;
      if (!r) return null;
      if (L.indexOf(r) >= 0) return r;
      for (i = 0; i < L.length; i++) {
        var q = L[i];
        if (!q) continue;
        if (r.host === 'roof') { if (q.host === 'roof' && q.plane === r.plane && Math.abs((q.along || 0) - (r.along || 0)) < 0.01 && Math.abs((q.up || 0) - (r.up || 0)) < 0.01) return q; }
        else if (q.seg === r.seg && Math.abs((q.at || 0) - (r.at || 0)) < 0.01) return q;
      }
      return null;
    }
    function _nearBinShow(rec, P) {
      var root = $('cf-root');
      if (!root || !rec || !P) return;
      _sxCss();
      if (!_NB.el) {
        var b = _sxBtn('', _OP_BIN, _t('delete', 'Delete'));
        b.id = 'cf-op-nearbin';
        b.setAttribute('data-cf-nopinch', '1');
        b.addEventListener('pointerdown', function (e) { e.stopPropagation(); });
        b.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          var r = _nbFind();
          _nearBinHide('tapped');
          if (!r) return;
          _log('opening  ' + (r.code || r.kind) + ' on ' + (r.seg || r.host || '?') + ' removed by the bin beside it (core-v593)');
          if (_opLast === r) _opLast = null;
          _opSel = null;
          removeOpening(r);
          try { if (_planWanted()) drawPlan(); } catch (eD) {}
          _sxRefresh();
          global.setTimeout(function () { try { undoPush(); } catch (eU) {} }, 0);
        });
        root.appendChild(b);
        _NB.el = b;
        global.document.addEventListener('pointerdown', function (e) {
          if (_NB.el && _NB.el.classList.contains('cf-on') && e.target !== _NB.el && !_NB.el.contains(e.target)) _nearBinHide('a press elsewhere');
        }, true);
      }
      _NB.rec = { seg: rec.seg, at: rec.at, host: rec.host, plane: rec.plane, along: rec.along, up: rec.up, code: rec.code, kind: rec.kind };
      _NB.P = P; _NB.until = Date.now() + 6000;
      _NB.el.classList.add('cf-on');
      if (!_NB.raf) _NB.raf = global.requestAnimationFrame(_nearBinTick);
      _log('opening  the bin beside ' + (rec.code || rec.kind) + ' on ' + (rec.seg || rec.host || '?') + ' (core-v593)');
    }
    function _nearBinHide(why) {
      if (_NB.el && _NB.el.classList.contains('cf-on')) _NB.last = 'went: ' + (why || '?');
      if (_NB.el) _NB.el.classList.remove('cf-on');
      _NB.rec = null; _NB.P = null;
      if (_NB.raf) { try { global.cancelAnimationFrame(_NB.raf); } catch (e) {} _NB.raf = 0; }
    }
    function _nearBinTick() {
      _NB.raf = 0;
      var el = _NB.el, st = _stage(), T = global.THREE;
      if (!el || !_NB.rec) { _nearBinHide('nothing held'); return; }
      if (Date.now() > _NB.until) { _nearBinHide('six seconds'); return; }
      if (!_nbFind()) { _nearBinHide('the unit is not there any more'); return; }
      if (st && st.camera && st.renderer && T) {
        var v = new T.Vector3(_NB.P.x, _NB.P.y || 0, _NB.P.z).project(st.camera);
        var cr = st.renderer.domElement.getBoundingClientRect(), par = el.offsetParent;
        var pr = par ? par.getBoundingClientRect() : { left: 0, top: 0, width: cr.width, height: cr.height };
        var x = cr.left - pr.left + (v.x + 1) * 0.5 * cr.width + 34;
        var y = cr.top - pr.top + (1 - (v.y + 1) * 0.5) * cr.height - 34;
        x = Math.max(28, Math.min((pr.width || cr.width) - 28, x));
        y = Math.max(28, Math.min((pr.height || cr.height) - 28, y));
        el.style.left = Math.round(x) + 'px';
        el.style.top = Math.round(y) + 'px';
        el.style.visibility = (v.z > -1 && v.z < 1) ? '' : 'hidden';
      }
      _NB.raf = global.requestAnimationFrame(_nearBinTick);
    }

    function _sxNote() {
      if (!_stripOn()) return 'strip    off -- the wide drawer (?ui=drawer, or the product says __PROD._iconUi = false; ?ui=icons forces the strip)';
      var c = null;
      try { c = (_wiz && _wiz.isOpen()) ? _wiz.current() : null; } catch (e) { c = null; }
      return 'strip    ' + (_SX.el ? ('OPEN ' + _SX.mode) : 'closed') + ' | ' + _SX.icons.length + ' icon(s) | picked ' + (_SX.sel || 'none') +
             ' | step ' + (c ? c.key : '-') + ' | plan ' + (_SX.lock ? 'locked' : 'UNLOCKED') +
             ' | bar ' + (_SX.bar ? (_SX.barSig || 'up') : 'none') + ' | panel ' + (_SX.modal ? _SX.modal.__key : 'none') +
             ' | bin ' + (_NB.rec ? ('beside ' + (_NB.rec.code || _NB.rec.kind)) : ('none, ' + _NB.last));
    }

    /* -------------------------------------------------------------------------
       PREFS
       ------------------------------------------------------------------------- */
    var _prefKey = 'leva:house-core';

    /* ---------- THE ASSEMBLY WALK (core-v74) ---------------------------------
       The rail's assembly button steps through the building the way it is
       built. A layer's builder marks the start of every material phase with
       _asmMark(ctx, key, dir) -- key is the locale key of the item's name, dir
       'top' or 'side' -- and a step is what that layer put in its group between
       two marks; a layer that marks nothing is one step named after itself.
       Steps run in construction order (ASM_ORDER, a product list). Walking
       forward shows one more step, flown in from above or from the side, with
       its name in a pill and k/N on the button; past the last step the whole
       building is back and the button reads 0/N. No rebuild, only visibility
       and a short animation. */
    var ASM_ORDER;   /* the product sets it (hoisted var, assigned up top): layer ids in build order */
    var _asm2 = { steps: [], k: 0, anim: null, pill: null, camHome: null, touched: false };
    /* THE STEPS BEFORE THE FIRST ITEM (core-v479): 1 the exploded view, 2 the
       whole building back together in the view it was opened in, and only
       then the items from 3 (Marek: po exploded view jeste krok zpet na
       zakladni pohled, pak az prvni kroky) */
    var ASM_HEAD = 2;
    function _asmMark(ctx, key, dir, def, code, nameFromKey) {
      if (!ctx || !ctx.marks) return;
      var mk = { at: ctx.group.children.length, key: key, def: def || key, dir: dir || 'top', code: code || '', byKey: !!nameFromKey };
      ctx.marks.push(mk);
      ctx.item = mk;
    }
    /* the short form for a builder that only needs to say which item the next
       bodies are: the code alone, the name comes from the BoQ */
    function _asmCode(ctx, code, dir) { _asmMark(ctx, '', dir || 'top', code, code); }
    /* THE NAME IS THE CENTRAL BoQ's: the bridge resolves a code to the same
       localised item name (with its dimensions) the buyer sees in the budget;
       the locale key is the fallback for a phase that has no code */
    var _asmNameFrom = '', _asmItemKeysLogged = false;
    function _asmName(code, key, def) {
      _asmNameFrom = 'fallback';
      try {
        var br = global.ConfiguratorBudgetBridge, c = code;
        if (c && CATALOG[c] && CATALOG[c].code) c = CATALOG[c].code;   /* a catalogue alias (grd.padBlock) -> its code */
        if (br && br.getBoqItem && c) {
          var it = br.getBoqItem(c);
          if (it && it.name) {
            _asmNameFrom = 'boq ' + c;
            if (!_asmItemKeysLogged) { _asmItemKeysLogged = true; try { _log('assembly: bridge item keys ' + Object.keys(it).join(',')); } catch (e0) {} }
            /* the dimension: a plain string only (an object is the bridge's own
               structure, not a note), and not appended when the name already
               ends with it -- '60 x 240 60 x 240' was that (Marek) */
            var raw = STATE.isMetric ? (it.noteMetric || it.note_metric || it.dimMetric || it.dimensionsMetric || '') : (it.noteImperial || it.note_imperial || it.dimImperial || it.dimensionsImperial || '');
            var dim = (typeof raw === 'string') ? raw.trim() : '';
            var nm = String(it.name).trim();
            if (dim) {
              var norm = function (s) { return s.replace(/\s+/g, '').replace(/[x\u00D7]/g, 'x').toLowerCase(); };
              if (norm(nm).indexOf(norm(dim)) >= 0) dim = '';
            }
            return nm + (dim ? (' ' + dim) : '');
          }
        }
        /* NO BRIDGE, THE CATALOGUE ROW (core-v479). The page stopped carrying
           ConfiguratorBudgetBridge (budget -> ConfiguratorBoQ only), and every
           step fell back to its baked English label -- the names were there
           before only because the bridge was. The catalogue itself is on the
           page as productData all the same, so the name is read from the row:
           the NAME column of the page's language when the row carries the
           locale blocks, the English NAME when it does not, and the NOTE
           column for the dimension exactly as the bridge path adds it (Marek:
           text u steps maji byt polozky z boq dle jazykove mutace). */
        var row = c ? _catRow(c) : null;
        if (row) {
          var li = _asmLangCol(row), nmR = String((li >= 0 ? row[li] : '') || '').trim();
          _asmNameFrom = 'catalogue ' + (nmR ? _pageLang() : 'EN') + ' ' + c;
          if (!nmR) nmR = String(row[3] || '').trim();
          if (nmR) {
            var dimR = String((STATE.isMetric ? row[17] : row[18]) || '').trim();
            if (dimR) {
              var normR = function (s) { return s.replace(/\s+/g, '').replace(/[x\u00D7]/g, 'x').toLowerCase(); };
              if (normR(nmR).indexOf(normR(dimR)) >= 0) dimR = '';
            }
            return nmR + (dimR ? (' ' + dimR) : '');
          }
          _asmNameFrom = 'catalogue row without a name for ' + c;
        } else if (c && _asmNameFrom.indexOf('boq has no') !== 0) {
          _asmNameFrom = 'no bridge, no catalogue row for ' + c;
        }
      } catch (e) { _asmNameFrom = 'threw ' + (e && e.message); }
      return key ? _t(key, def) : def;
    }
    /* THE NAME COLUMN OF THE PAGE'S LANGUAGE in a central catalogue row: 28
       fixed columns, then CATEGORY, SUBCATEGORY, NAME and DESCRIPTION, each
       in 20 languages in the sheet's own order. English is column 3 itself;
       -1 when the page is English or the row is too short to hold the
       blocks (a page that trims its rows gets the English name). */
    var ASM_LANGS = { ar: 0, cs: 1, cz: 1, de: 2, el: 3, es: 4, fi: 5, fr: 6, he: 7, iw: 7, hu: 8, it: 9, ja: 10, ko: 11,
                      nl: 12, nb: 13, no: 13, nn: 13, pl: 14, pt: 15, ro: 16, sv: 17, tr: 18, zh: 19 };
    function _asmLangCol(row) {
      var lg = _pageLang(), ix = ASM_LANGS[lg];
      if (ix === undefined || !row) return -1;
      var col = 28 + 2 * 20 + ix;
      return (row.length > col) ? col : -1;
    }
    function _asmStepLabel(s) { if (!s) return ''; if (s.byKey && s.key) { _asmNameFrom = 'key'; return _t(s.key, s.def); } return _asmName(s.code, s.key, s.def); }
    var ASM_LABELS;   /* the product's map: layer id -> [locale key, default] */
    var ASM_STEP_ORDER;   /* the product's map: layer id -> [code or key, or {id, dir}] in walk order */
    var ASM_GLOBAL_ORDER;   /* the product's list of 'layer|key-or-code' ('*' = the rest of the layer), the walk across layers */
    function _asmLayerLabel(id) {
      var e = ASM_LABELS && ASM_LABELS[id];
      if (e) return _cap(_t(e[0], e[1]));
      var s = String(id).replace(/^[a-z]+-/, '').replace(/-/g, ' ');
      return s.charAt(0).toUpperCase() + s.slice(1);
    }
    var ASM_TRUSS_KEYS = { truss_chord: 1, truss_web: 1, truss_plate: 1, hip_chord: 1, hip_web: 1, hip_plate: 1 };
    var _asmUnlisted = [];   /* where the unlisted groups went, for the snapshot */
    function _asmCollect() {
      var st = _stage(); if (!st || !st.group) return [];
      _asmUnlisted = [];
      var byId = {}, i, j, out = [];
      for (i = 0; i < st.group.children.length; i++) { var c = st.group.children[i]; if (c.userData && c.userData.layer) byId[c.userData.layer] = c; }
      var order = (ASM_ORDER || []).slice(), k;
      for (k = 0; k < _layers.length; k++) if (order.indexOf(_layers[k].id) < 0) order.push(_layers[k].id);
      for (i = 0; i < order.length; i++) {
        var lg = byId[order[i]]; if (!lg || !lg.children.length) continue;
        var groups = [], byKey = {};
        for (j = 0; j < lg.children.length; j++) {
          var ch = lg.children[j], tag = ch.userData && ch.userData.asm;
          if (__PROD._asmModeled && (!tag || ch.userData.asmSkip)) continue;
          /* A TRUSS COMES WHOLE (core-v278). Its chords, webs and plates were
             three steps, and the exploded view moves every step by its own
             distance -- so each truss flew apart into sticks, where the yard
             delivers it made up (Marek: vazniky se dodavaji vcelku; plati pro
             vsechny projekty). Every truss part, common and hip, is one step. */
          var isTruss = !!(tag && ASM_TRUSS_KEYS[tag.key]);
          var id = isTruss ? '__trusses' : (tag ? ((tag.key || '') + '|' + (tag.code || '') + '|' + (tag.def || '')) : '__layer');
          var gph = byKey[id];
          if (!gph) {
            gph = isTruss
              ? { layer: order[i], key: 'viz_trusses', def: 'Trusses', dir: tag.dir || 'top', code: '', byKey: true, near: false, meshes: [] }
              : { layer: order[i], key: tag ? tag.key : '', def: tag ? tag.def : _asmLayerLabel(order[i]), dir: tag ? tag.dir : 'top', code: tag ? tag.code : '', byKey: !!(tag && tag.byKey), near: !!(tag && tag.near), meshes: [] };
            byKey[id] = gph; groups.push(gph);
          }
          gph.meshes.push(ch);
        }
        /* THE PRODUCT'S ORDER within the layer, by code or key; a listed entry
           may also set the direction.

           AN UNLISTED GROUP FOLLOWS THE ONE BUILT JUST BEFORE IT (core-v479).
           It used to go after ALL the listed ones -- and the roof drawer
           marks its counter-battens itself, under a key no product lists, so
           they came in after the covering, the ridge and the gutters; the
           drawer's fascia and its later drip edges did the same (Marek: mame
           battens az po stresni krytine). Now whatever is not listed takes
           the place right behind its predecessor in build order, which is
           where the builder put it. Only a group with no listed group built
           before it still goes to the end, as before. */
        var ord = (ASM_STEP_ORDER && ASM_STEP_ORDER[order[i]]) || null;
        if (ord) {
          var rank = {}, dirs = {}, r;
          var xdirs = {};
          for (r = 0; r < ord.length; r++) { var e = (typeof ord[r] === 'string') ? { id: ord[r] } : ord[r]; rank[e.id] = r; if (e.dir) dirs[e.id] = e.dir; if (e.xdir) xdirs[e.id] = e.xdir; }
          var prevRank = null, prevName = '', unl = [];
          for (j = 0; j < groups.length; j++) {
            var gk = groups[j].key || '', gc = groups[j].code || '';
            var gid = (rank[gk] !== undefined) ? gk : gc;   /* the key names the phase; the code is the fallback */
            if (rank[gid] !== undefined) {
              groups[j]._rank = rank[gid];
              prevRank = groups[j]._rank; prevName = gid;
            } else if (prevRank !== null) {
              prevRank = prevRank + 0.001;
              groups[j]._rank = prevRank;
              unl.push((gk || gc || '?') + ' after ' + prevName);
              prevName = gk || gc || '?';
            } else {
              groups[j]._rank = ord.length + j;
              unl.push((gk || gc || '?') + ' at the end');
            }
            if (dirs[gid]) groups[j].dir = dirs[gid];
            if (xdirs[gid]) groups[j].xdir = xdirs[gid];   /* a direction for the exploded view alone */
          }
          if (unl.length) _asmUnlisted.push(order[i].replace(/^[a-z]+-/, '') + ': ' + unl.join(', '));
          groups.sort(function (p1, p2) { return p1._rank - p2._rank; });
        }
        for (j = 0; j < groups.length; j++) out.push(groups[j]);
      }
      /* THE GLOBAL ORDER: the product's list of 'layer|id' (id = key or code,
         '*' = the rest of that layer) decides the walk across layers -- so the
         floor's finish can come after the frame, the interiors before the
         roof. Whatever is not listed follows, in its layer order. */
      if (ASM_GLOBAL_ORDER && ASM_GLOBAL_ORDER.length) {
        var picked = [], used = {}, e, q, m;
        function idsOf(g) { return [g.key || '', g.code || '']; }
        for (e = 0; e < ASM_GLOBAL_ORDER.length; e++) {
          var ent = ASM_GLOBAL_ORDER[e], parts = ent.split('|'), lay = parts[0], want = parts[1] || '*';
          for (q = 0; q < out.length; q++) {
            if (used[q] || out[q].layer !== lay) continue;
            var ids = idsOf(out[q]);
            /* EVERY GROUP OF THAT KEY, NOT THE FIRST (core-v482): a floor finish
               is one step per material -- boards in the rooms, tiles in the
               bathroom -- all under one key, and only the first came at the
               place the product asked for; the rest fell to the layer's '*' */
            if (want === '*' || ids[0] === want || ids[1] === want) { picked.push(out[q]); used[q] = true; }
          }
        }
        for (q = 0; q < out.length; q++) if (!used[q]) picked.push(out[q]);
        out = picked;
      }
      return out;
    }
    function _asmRebuilt() {
      _asm2.steps = _asmCollect();
      _explodeShown = 0;   /* new bodies stand assembled */
      try {
        var S = _asm2.steps, withCode = 0, noCode = {}, unknown = [], i, br = global.ConfiguratorBudgetBridge;
        for (i = 0; i < S.length; i++) {
          if (S[i].code) {
            withCode++;
            var c = S[i].code; if (CATALOG[c] && CATALOG[c].code) c = CATALOG[c].code;
            var it = (br && br.getBoqItem) ? br.getBoqItem(c) : null;
            if (!it || !it.name) unknown.push(c + ' (' + S[i].layer + ')');
          } else noCode[S[i].layer] = (noCode[S[i].layer] || 0) + 1;
        }
        _log('assembly steps ' + S.length + ' | with a code ' + withCode + ' | no code: ' + (Object.keys(noCode).map(function (k) { return k + ' x' + noCode[k]; }).join(', ') || 'none') +
             ' | bridge ' + (br && br.getBoqItem ? 'present' : 'MISSING') + (unknown.length ? (' | codes the bridge does not know: ' + unknown.join(', ')) : ''));
      } catch (eL) {}
      if (_asm2.k > 0) _asmApply(false);
      /* the first step's own animation must not be overwritten by a snap */
      if (_explodeK > 0 && _asm2.k !== 1) setExplode(_explodeK);
      _asmCounter();
    }
    /* k counts the exploded view as step 1, the whole building as step 2, the
       items from 3; N = items + ASM_HEAD */
    function _asmCounter() {
      try { var el = $('cf-stat-assembly'); if (el) el.textContent = (_asm2.k > 0 ? _asm2.k : 0) + '/' + (_asm2.steps.length + ASM_HEAD); } catch (e) {}
    }
    /* THE NAME SITS AT THE BUTTON, as the bench does it: the button's title is
       the step's name, and the tooltip flashes for a moment after the tap */
    var _asmTipTimer = 0;
    function _asmPillShow(text) {
      var b = $('cf-btn-assembly'); if (!b) return;
      b.title = text || _t('assembly', 'Assembly');
      if (_asmTipTimer) { try { global.clearTimeout(_asmTipTimer); } catch (e) {} _asmTipTimer = 0; }
      if (text) {
        b.classList.add('show-tip');
        _asmTipTimer = global.setTimeout(function () { try { b.classList.remove('show-tip'); } catch (e2) {} }, 2500);
      } else b.classList.remove('show-tip');
    }
    function _asmLand() {
      /* whatever was still flying lands where it belongs, so a fast tap never
         leaves a body short of its place */
      if (_asm2.anim) { try { global.cancelAnimationFrame(_asm2.anim); } catch (e) {} _asm2.anim = null; }
      var q, S = _asm2.flying || [];
      for (q = 0; q < S.length; q++) { var s = S[q]; s.m.position.set(s.x, s.y, s.z); }
      _asm2.flying = null;
    }
    function _asmApply(animate) {
      var T = global.THREE, st = _stage(), S = _asm2.steps, k = _asm2.k, i, j;
      if (!st) return;
      _asmLand();
      if (k <= 0) {
        for (i = 0; i < S.length; i++) for (j = 0; j < S[i].meshes.length; j++) { S[i].meshes[j].visible = true; }
        /* and the stage says which of those may actually be seen */
        try { _vizAfter(null); } catch (eV0) {}
        if (_explodeK > 0) setExplode(0);
        try { if (typeof showExplode === 'function') showExplode(false); } catch (eS0) {}
        _asmPillShow('');
        return;
      }
      if (k === 1) {
        /* step 1: the exploded view of the whole building, with its slider */
        for (i = 0; i < S.length; i++) for (j = 0; j < S[i].meshes.length; j++) S[i].meshes[j].visible = true;
        try { if (typeof showExplode === 'function') showExplode(true); } catch (eS) {}
        setExplode(_explodeK > 0 ? _explodeK : 2, true);
        _asmPillShow(_t('exploded_view', 'Exploded view'));
        return;
      }
      if (k === 2) {
        /* step 2: the building comes back together, and the camera goes back
           to where it stood before the explosion backed it off */
        for (i = 0; i < S.length; i++) for (j = 0; j < S[i].meshes.length; j++) S[i].meshes[j].visible = true;
        try { if (typeof showExplode === 'function') showExplode(false); } catch (eS3) {}
        setExplode(0, !!animate, _asm2.camHome);
        _asmPillShow(_t('viz_full', 'Full build'));
        return;
      }
      /* a tap during step 2's animation leaves the bodies part way: snap them */
      if (_explodeK > 0 || _explodeShown > 0) setExplode(0);
      try { if (typeof showExplode === 'function') showExplode(false); } catch (eS2) {}
      var n = k - ASM_HEAD;   /* the item index, 1-based */
      for (i = 0; i < S.length; i++) for (j = 0; j < S[i].meshes.length; j++) S[i].meshes[j].visible = (i < n);
      var step = S[n - 1]; if (!step) return;
      _asmPillShow(_asmStepLabel(step));
      if (!animate || !T) return;
      /* fly in: from above, or from the side away from the building's centre */
      var B = _buildRect(), starts = [], bx = new T.Box3(), cc = new T.Vector3();
      var SIDEV = { x0: { x: -1, z: 0 }, x1: { x: 1, z: 0 }, z0: { x: 0, z: -1 }, z1: { x: 0, z: 1 } };
      for (j = 0; j < step.meshes.length; j++) {
        var m = step.meshes[j], p = m.position, off = { x: 0, y: 0, z: 0 };
        var flyDir = (__PROD._asmModeled && m.userData.assemblyDir) || step.dir;
        if (flyDir === 'side' || flyDir === 'in') {
          /* side: from outside the wall in; in: from inside the building out to the wall */
          var sv = SIDEV[_asmSideOf(_asmBodyCtr(T, m, bx, cc), B)], sg = (flyDir === 'in') ? -1 : 1;
          off.x = sv.x * 2.5 * sg; off.z = sv.z * 2.5 * sg;
        }
        else if (flyDir === 'bottom') { off.y = -3.0; }
        else { off.y = 3.0; }
        starts.push({ m: m, x: p.x, y: p.y, z: p.z, ox: off.x, oy: off.y, oz: off.z });
        m.position.set(p.x + off.x, p.y + off.y, p.z + off.z);
      }
      _asm2.flying = starts;
      var t0 = (global.performance && global.performance.now) ? global.performance.now() : Date.now(), DUR = 700;
      function tick() {
        var now = (global.performance && global.performance.now) ? global.performance.now() : Date.now();
        var u = Math.min(1, (now - t0) / DUR), e = 1 - Math.pow(1 - u, 3), q;
        for (q = 0; q < starts.length; q++) { var s = starts[q]; s.m.position.set(s.x + s.ox * (1 - e), s.y + s.oy * (1 - e), s.z + s.oz * (1 - e)); }
        if (u < 1) _asm2.anim = global.requestAnimationFrame(tick); else { _asm2.anim = null; _asm2.flying = null; }
      }
      _asm2.anim = global.requestAnimationFrame(tick);
    }
    /* THE EXPLODED VIEW: every step moved out along its own direction, later
       layers further, by a factor k (0 = assembled, 1 = the default spread,
       2 = twice). The walk and the explosion share the steps; positions are
       remembered once per build and restored at k = 0. */
    var _explodeK = 0, EXPLODE_D = 1.5;   /* metres out at k = 1; the default k is 2, the slider runs to 4 */
    /* AN ITEM MOVES AS ONE THING. A membrane laid in strips or a hip's corner
       rafters are one item and explode together, not strip by strip (Marek).
       A 'top' item goes up as one block; a 'side' item is split by the side
       of the building its bodies stand on, and each side's block goes out
       square to its wall -- so cladding peels off each face as a sheet. */
    function _asmBodyCtr(T, m, bx, c) {
      var p = m.userData.__asmPos || (m.userData.__asmPos = { x: m.position.x, y: m.position.y, z: m.position.z });
      if (!m.userData.__asmCtr) {
        try { bx.setFromObject(m); bx.getCenter(c); m.userData.__asmCtr = { x: c.x - (m.position.x - p.x), y: c.y - (m.position.y - p.y), z: c.z - (m.position.z - p.z) }; }
        catch (e2) { m.userData.__asmCtr = { x: p.x, y: p.y, z: p.z }; }
      }
      return m.userData.__asmCtr;
    }
    function _asmSideOf(q, B) {
      /* which face of the building a body belongs to: the one it is nearest, in plan */
      var dx0 = q.x - B.x0, dx1 = B.x1 - q.x, dz0 = q.z - B.z0, dz1 = B.z1 - q.z;
      var mn = Math.min(dx0, dx1, dz0, dz1);
      return mn === dx0 ? 'x0' : mn === dx1 ? 'x1' : mn === dz0 ? 'z0' : 'z1';
    }
    /* =====================================================================
       THE EXPLODED VIEW, core-v658 (Marek 2026-10-12: Stred od ktereho se
       vaze explode je stred podlahy / teziste podlahove plochy domu. Cim dal
       jsou prvky od tohoto bodu, tim dal se v rozkladu vzdali -- aby se
       nestalo, ze izolace strechy bude v rozkladu vyse nez strecha. Stenove
       items odjizdi vsechny do strany, stropni a vyse pak nahoru ... Prvky se
       nemaji pri pohybu v exploded fazi prolinat. Plati pro vsechny projekty).

       What it replaced, and why: every step was given ONE direction from the
       middle of its whole box and ONE number from its leading edge, and the
       steps were then chained in that order 200 mm apart. A sloped layer's
       leading edge is its eave, which is lower than the eave of the layer
       under it once the upper one overhangs further -- so the roof steel
       came out BELOW its underlay, the vapour barrier below the gravel, and a
       body that spans several walls (a wrap, the shop insulation) went off
       to one side as a block. Partitions, fitouts and the electrics in them
       went to the nearest face with the facade and crossed each other.

       Now, per BODY, by the rules he gave:
         - THE CENTRE is the floor's centroid (the area of the ground floor's
           outline), at the floor level.
         - WHICH WAY: from the floor covering down -> down; a wall layer (the
           wall layers' bodies, the gable triangles with them) -> out; the
           roof and the ceiling layers, and anything else from the wall plate
           up -> up; whatever rests on something going up rides up with it
           (a loft, its walls, guards and furniture; an upper storey).
         - OUT means square to the wall it belongs to (the ground floor's
           outline segment it lies along, or both at a corner); a body deep
           inside the plan -- a partition, a fitout, the stair, a water heater
           -- stays where it is: it is next to the centre, and the walls and
           the roof go away from it, and a partition's door stays in it. A
           window or a door in an outer wall goes out between the layers it is
           built between: past the girts, the liner and the board round it,
           inside the steel and the trim put on over its flange.
         - HOW FAR: a share of its distance from the centre (out: from the
           centre to the face, up: its height over the floor, down: its depth
           under it), and AT LEAST 200 mm further than anything it lies on --
           decided from the geometry itself: the bodies are drawn into a grid
           (plan for up / down, each wall's face for out) and wherever one
           body covers another, the outer one must travel further. So a
           layer can never pass through the one beneath it.
         - IN WHAT ORDER: everything going up or down starts at once and has
           cleared the walls' tops (a soffit, a truss heel, a deck a post
           goes through) before anything moves out -- the walls slide out
           under a roof that has already lifted. What has a wall's body OVER
           it (an A-frame's loft under its slopes) waits instead until the
           walls are well out (k = XP.kL2), and so does all under it.
         - WALLS: one face per wall LINE (a wall cut in two is one face), the
           wall a body lies along (nearest, longer along it than across), a
           second one only at a corner -- each with its own distance and its
           own order (two walls' orders never mixed), and dropped where
           another wall's piece outward of it does not go that way. An
           upper storey's walls ride up with it as one and go out.
         - AN A-FRAME (storey 10 mm high): its roof layers are its walls --
           out square to the wall under their plane's eave; only what lies on
           no slope (the ridge, the loft) goes up.
         - AN ITEM taken apart (a cable over the eave and down the wall) keeps
           its touching pieces together where nothing above them is passed.
       Checked headless on every house-line product by drawing the scene at
       13 values of k and looking for any two bodies that come to occupy the
       same place they did not at rest (the harness's exm.js).
       ===================================================================== */
    var EXPLODE_GAIN = 0.35;   /* the share of its distance a body travels at k = 1 */
    var XP = { gap: 0.20, min0: 0.25, band: 0.45, deep: 0.80, near: 0.25, kL: 0.8, kL2: 1.2, cell: 0.15, tol: 0.004, rawTol: 0.008, clear: 0.08, rest: 0.30 };
    var _xpCache = null, _xpInfo = '';
    function _xpCentroid() {
      var P = poly(), A = 0, cx = 0, cz = 0, i;
      for (i = 0; i < P.length; i++) {
        var a = P[i], b = P[(i + 1) % P.length], cr = a[0] * b[1] - b[0] * a[1];
        A += cr; cx += (a[0] + b[0]) * cr; cz += (a[1] + b[1]) * cr;
      }
      if (Math.abs(A) < 1e-9) { var B = _buildRect(); return { x: (B.x0 + B.x1) / 2, z: (B.z0 + B.z1) / 2 }; }
      return { x: cx / (3 * A), z: cz / (3 * A) };
    }
    function _xpInside(P, x, z) {
      var ins = false, i, j;
      for (i = 0, j = P.length - 1; i < P.length; j = i++) {
        var xi = P[i][0], zi = P[i][1], xj = P[j][0], zj = P[j][1];
        if (((zi > z) !== (zj > z)) && (x < (xj - xi) * (z - zi) / ((zj - zi) || 1e-12) + xi)) ins = !ins;
      }
      return ins;
    }
    /* the layer's kind, from what it claims (and its id when it claims nothing) */
    function _xpKind(layerId) {
      var L = null, i, cl = [];
      for (i = 0; i < _layers.length; i++) if (_layers[i].id === layerId) { L = _layers[i]; break; }
      cl = (L && L.claims) || [];
      function has(c) { return cl.indexOf(c) >= 0; }
      var id = String(layerId || '');
      function pre(re) { for (var q = 0; q < cl.length; q++) if (re.test(String(cl[q]))) return true; return false; }
      return {
        /* a facade on masonry, a post wall, are walls; a roof's frame and a
           ground floor's ceiling are the roof and the ceiling (the T house
           claims roof-frame and ceiling-ground: its rafters stayed put) */
        wall: has('wall') || has('parapet') || has('facade') || has('postwall') || /(^|-)(stud-)?wall$/.test(id),
        roof: pre(/^(roof|ceiling)/),
        part: has('partitions'),
        fit: has('fitout'),
        join: has('joinery')
      };
    }
    /* a body standing upright: no sloped face of any size (a rafter's top
       and underside slope; a wool batt in a gable is all square faces) */
    function _xpStanding(tris) {
      var tot = 0, slo = 0, t, q;
      for (t = 0; t < tris.length; t++) {
        var A = tris[t];
        for (q = 0; q + 8 < A.length; q += 9) {
          var ax = A[q + 3] - A[q], ay = A[q + 4] - A[q + 1], az = A[q + 5] - A[q + 2], bx = A[q + 6] - A[q], by = A[q + 7] - A[q + 1], bz = A[q + 8] - A[q + 2];
          var nx = ay * bz - az * by, ny = az * bx - ax * bz, nz = ax * by - ay * bx, ar = Math.sqrt(nx * nx + ny * ny + nz * nz);
          if (!(ar > 1e-12)) continue;
          tot += ar; var c = Math.abs(ny) / ar;
          if (c > 0.15 && c < 0.985) slo += ar;
        }
      }
      return tot > 0 && slo < 0.05 * tot;
    }
    function _xpNearKey(st, kind) {
      if (kind.join || st.near) return true;
      return /(^|_)(doors?|windows?)$|handle|hinge/i.test(st.key || '');
    }
    /* A GRID THAT HOLDS, PER CELL, EACH BODY'S RANGE ALONG ONE AXIS. */
    function _xpGrid() { return { m: new Map() }; }
    /* while it is drawn a cell holds [body, centre lo, centre hi, raw lo, raw hi]
       per body; _xpFinal leaves [body, lo, hi] -- the centre's reading where
       the body has one, its raw points only where it has nothing else */
    function _xpPut(G, ku, kv, bi, w, raw, cover) {
      var key = (ku + 40000) * 80000 + (kv + 40000), c = G.m.get(key), o = raw ? 3 : 1;
      if (!c) { c = []; G.m.set(key, c); }
      var n = c.length, q = -1;
      if (n && c[n - 6] === bi) q = n - 6;
      else for (var t = 0; t < n; t += 6) if (c[t] === bi) { q = t; break; }
      if (q < 0) { c.push(bi, Infinity, -Infinity, Infinity, -Infinity, 0); q = n; }
      if (w < c[q + o]) c[q + o] = w; if (w > c[q + o + 1]) c[q + o + 1] = w;
      if (cover) c[q + 5] = 1;
    }
    /* ...and the final cell is [body, lo, hi, raw] per body (raw: 1 when only
       its own points were read there, a stud's or a pipe's side -- such a
       body is compared with a clear 15 mm, not the 4 mm of two readings at
       the same centre) */
    function _xpFinal(G) {
      G.m.forEach(function (c, key) {
        var d = [], q;
        /* raw 0: read at the centre, the body there; 2: its level face read
           at a centre it does not reach (a batten's front between two
           centres, a sheet's bottom edge beside one); 1: its own points */
        for (q = 0; q < c.length; q += 6) {
          if (isFinite(c[q + 1])) d.push(c[q], c[q + 1], c[q + 2], c[q + 5] ? 0 : 2); else d.push(c[q], c[q + 3], c[q + 4], 1);
        }
        G.m.set(key, d);
      });
    }
    /* q wholly above (outward of) r in this cell, and its middle higher */
    function _xpOver(c, q, r) {
      var t = (c[q + 3] === 1 || c[r + 3] === 1) ? XP.rawTol : -XP.tol;
      return c[q + 1] >= c[r + 2] + t && c[q + 1] + c[q + 2] > c[r + 1] + c[r + 2];
    }
    /* triangles (flat world xyz) drawn into G: u, v the grid's coordinates and
       w the depth, each given as a function of a point. EVERY BODY IS READ AT
       THE CELL'S CENTRE: two sheets 3 mm apart on a slope differ by more than
       that across a cell, so a corner or an edge read where it lies would put
       either one above the other. A triangle too thin for any centre to fall
       in (a pipe's or a stud's side) is still drawn: its corners and points
       along its edges name the cells, and its plane is read at their centres
       (kept within its own depths); a triangle seen edge on gives its depths
       as they are. */
    function _xpRaster(G, tris, bi, U, V, W, H) {
      var q, A = tris;
      for (q = 0; q + 8 < A.length; q += 9) {
        var u0 = U(A, q), v0 = V(A, q), w0 = W(A, q), u1 = U(A, q + 3), v1 = V(A, q + 3), w1 = W(A, q + 3), u2 = U(A, q + 6), v2 = V(A, q + 6), w2 = W(A, q + 6);
        var den = (v1 - v2) * (u0 - u2) + (u2 - u1) * (v0 - v2), flat = Math.abs(den) < 1e-10, steep = flat, level = false;
        if (!flat) {
          var gu = ((v1 - v2) * w0 + (v2 - v0) * w1 + (v0 - v1) * w2) / den, gv = ((u2 - u1) * w0 + (u0 - u2) * w1 + (u1 - u0) * w2) / den;
          steep = Math.abs(gu) + Math.abs(gv) > 3;
          /* a face square to the view (a batten's front, 60 wide, between two
             cell centres) reads the same anywhere in the cell: its plane is
             read at the centre however far off it lies -- within 2 mm -- and
             counts as a centre's reading (it needed a clear 8 mm as a raw one,
             and the battens on the OSB behind the 2.5 mm wrap had no order) */
          level = (Math.abs(gu) + Math.abs(gv)) * H * 0.5 <= 0.002;
        }
        var pts = [[u0, v0, w0], [u1, v1, w1], [u2, v2, w2]], e, t;
        for (e = 0; e < 3; e++) {
          var P = pts[e], Q = pts[(e + 1) % 3], L2 = Math.max(Math.abs(Q[0] - P[0]), Math.abs(Q[1] - P[1])), n = Math.floor(L2 / (H * 0.5));
          if (n > 4000) n = 4000;
          for (t = 0; t < Math.max(1, n); t++) {
            var f = t / Math.max(1, n), pu = P[0] + (Q[0] - P[0]) * f, pv = P[1] + (Q[1] - P[1]) * f, pw = P[2] + (Q[2] - P[2]) * f;
            var cu = Math.round(pu / H), cv = Math.round(pv / H);
            var rawP = steep;
            if (!steep) {
              /* a plane no steeper than 3:1 is read at the centre -- when the
                 centre is near it; a steeper one (a sliver seen almost edge on),
                 or one the centre is well off, where its point lies */
              var cu2 = cu * H, cv2 = cv * H;
              var m0 = ((v1 - v2) * (cu2 - u2) + (u2 - u1) * (cv2 - v2)) / den, m1 = ((v2 - v0) * (cu2 - u2) + (u0 - u2) * (cv2 - v2)) / den;
              var bMin = Math.min(m0, m1, 1 - m0 - m1);
              if (level || bMin >= -0.3) pw = m0 * w0 + m1 * w1 + (1 - m0 - m1) * w2; else rawP = true;
            }
            _xpPut(G, cu, cv, bi, pw, rawP, !rawP && bMin >= -0.05);   /* the body is there only if the centre is on it */
          }
        }
        if (flat) continue;
        var cu0 = Math.ceil(Math.min(u0, u1, u2) / H), cu1 = Math.floor(Math.max(u0, u1, u2) / H);
        var cv0 = Math.ceil(Math.min(v0, v1, v2) / H), cv1 = Math.floor(Math.max(v0, v1, v2) / H);
        if (cu1 < cu0 || cv1 < cv0 || (cu1 - cu0 + 1) * (cv1 - cv0 + 1) > 250000) continue;
        for (var cuu = cu0; cuu <= cu1; cuu++) for (var cvv = cv0; cvv <= cv1; cvv++) {
          var qu = cuu * H, qv = cvv * H;
          var l0 = ((v1 - v2) * (qu - u2) + (u2 - u1) * (qv - v2)) / den, l1 = ((v2 - v0) * (qu - u2) + (u0 - u2) * (qv - v2)) / den, l2 = 1 - l0 - l1;
          if (l0 < -1e-6 || l1 < -1e-6 || l2 < -1e-6) continue;
          _xpPut(G, cuu, cvv, bi, l0 * w0 + l1 * w1 + l2 * w2, false, true);   /* inside the triangle: its true depth at the centre */
        }
      }
    }
    /* the order a direction's groups must keep: votes 'a below b' from the
       cells, the stronger direction of each pair, then the strongest first,
       an edge left out where it would close a loop */
    function _xpDag(votes, nodes, small) {
      /* each pair: the stronger direction is an order; a pair with many cells
         EACH way is interleaved (a truss and the bracing on its webs) and may
         be tied into one group -- but never two that something else already
         puts in order, so a tie cannot fold a roof's layers into one */
      var par = {}, k, i, ord = [], inter = [], seen = {};
      for (i = 0; i < nodes.length; i++) par[nodes[i]] = nodes[i];
      function rt(x) { if (par[x] === undefined) par[x] = x; while (par[x] !== x) { par[x] = par[par[x]]; x = par[x]; } return x; }
      for (k in votes) {
        if (!votes.hasOwnProperty(k)) continue;
        var ab = k.split('>'), a = ab[0], b = ab[1];
        if (seen[b + '>' + a] || seen[k]) continue;
        seen[k] = true;
        var va = votes[k], vb = votes[b + '>' + a] || 0;
        /* ...and so is a pair where the fewer cells are still a floor's worth
           (40 and a tenth of the other way): blown insulation over the chords
           it lies between -- the truss went up through it */
        var vMin = Math.min(va, vb), vMax = Math.max(va, vb);
        if (va >= 3 && vb >= 3 && (vMin >= 0.25 * vMax || (vMin >= 40 && vMin >= 0.1 * vMax))) { inter.push({ a: a, b: b, w: vMin }); continue; }
        /* a small piece -- a cable's bend, a clip -- covers a cell or two of
           anything: one cell with nothing the other way is its whole say */
        var sm = small && (small[a] || small[b]);
        if (va > vb && (va - vb >= 2 || va >= 3 || (sm && !vb))) ord.push({ a: a, b: b, w: va - vb });
        else if (vb > va && (vb - va >= 2 || vb >= 3 || (sm && !va))) ord.push({ a: b, b: a, w: vb - va });
      }
      ord.sort(function (p, q) { return q.w - p.w; });
      inter.sort(function (p, q) { return q.w - p.w; });
      var out = {}, kept = 0, dropped = 0;
      function reach(from, to) {
        var st = [from], vis = {}, x, j;
        while (st.length) { x = st.pop(); if (x === to) return true; if (vis[x]) continue; vis[x] = true; var o = out[x] || []; for (j = 0; j < o.length; j++) st.push(o[j]); }
        return false;
      }
      /* the orders strongest first, an order left out where it would close a
         loop -- looked for only inside the knots (strongly connected parts):
         an order between two knots can close none, and the search over the
         whole house for every order took a second on the T house */
      var comp = {};
      function reachIn(from, to, cc) {
        var st = [from], vis = {}, x, j;
        while (st.length) { x = st.pop(); if (x === to) return true; if (vis[x]) continue; vis[x] = true; var o = out[x] || []; for (j = 0; j < o.length; j++) if (comp[o[j]] === cc) st.push(o[j]); }
        return false;
      }
      function build() {
        out = {}; kept = 0; dropped = 0; comp = {};
        var all = {}, E = [], seenE = {}, v;
        for (i = 0; i < nodes.length; i++) all[rt(nodes[i])] = all[rt(nodes[i])] || [];
        for (i = 0; i < ord.length; i++) {
          var ra = rt(ord[i].a), rb = rt(ord[i].b), ke = ra + '|' + rb;
          if (ra === rb || seenE[ke]) continue;
          seenE[ke] = 1;
          if (!all[ra]) all[ra] = []; if (!all[rb]) all[rb] = [];
          all[ra].push(rb); E.push([ra, rb]);
        }
        /* Tarjan, without recursion */
        var idx = {}, low = {}, onS = {}, S9 = [], cnt = 0, nc = 0;
        for (v in all) {
          if (!all.hasOwnProperty(v) || idx[v] !== undefined) continue;
          var stk = [[v, 0]]; idx[v] = low[v] = cnt++; S9.push(v); onS[v] = true;
          while (stk.length) {
            var tp = stk[stk.length - 1], x = tp[0], ch = all[x];
            if (tp[1] < ch.length) {
              var w = ch[tp[1]++];
              if (idx[w] === undefined) { idx[w] = low[w] = cnt++; S9.push(w); onS[w] = true; stk.push([w, 0]); }
              else if (onS[w] && idx[w] < low[x]) low[x] = idx[w];
            } else {
              stk.pop();
              if (stk.length) { var pr = stk[stk.length - 1][0]; if (low[x] < low[pr]) low[pr] = low[x]; }
              if (low[x] === idx[x]) { var wq; do { wq = S9.pop(); onS[wq] = false; comp[wq] = nc; } while (wq !== x); nc++; }
            }
          }
        }
        for (v in all) if (all.hasOwnProperty(v)) out[v] = [];
        for (i = 0; i < E.length; i++) {
          var a9 = E[i][0], b9 = E[i][1];
          if (comp[a9] !== comp[b9]) { out[a9].push(b9); kept++; continue; }
          if (reachIn(b9, a9, comp[a9])) { dropped++; continue; }
          out[a9].push(b9); kept++;
        }
      }
      build();
      var tied = 0, late = [];
      for (i = 0; i < inter.length; i++) {
        var ta = rt(inter[i].a), tb = rt(inter[i].b);
        if (ta === tb) continue;
        /* already in order through others: that order stands, and this pair
           keeps it (its stronger way, if that agrees) */
        if (reach(ta, tb) || reach(tb, ta)) {
          var vA = votes[inter[i].a + '>' + inter[i].b] || 0, vB = votes[inter[i].b + '>' + inter[i].a] || 0;
          late.push(vA >= vB ? { a: inter[i].a, b: inter[i].b, w: 0 } : { a: inter[i].b, b: inter[i].a, w: 0 });
          continue;
        }
        par[ta] = tb; tied++;
        build();
      }
      for (i = 0; i < late.length; i++) {
        var la = rt(late[i].a), lb = rt(late[i].b);
        if (la === lb || out[la].indexOf(lb) >= 0 || reach(lb, la)) continue;
        out[la].push(lb); kept++;
      }
      return { out: out, kept: kept, dropped: dropped, root: rt, tied: tied };
    }
    /* the distances along a dag: each at least its base, and gap past every
       group it lies on */
    function _xpMags(D, base0, gap) {
      var indeg = {}, k, j, mag = {}, Q = [], done = 0, base = {};
      for (k in base0) if (base0.hasOwnProperty(k)) { var r0 = D.root(k); base[r0] = Math.max(base[r0] || 0, base0[k]); }
      for (k in D.out) if (D.out.hasOwnProperty(k)) { indeg[k] = indeg[k] || 0; for (j = 0; j < D.out[k].length; j++) indeg[D.out[k][j]] = (indeg[D.out[k][j]] || 0) + 1; }
      for (k in indeg) if (indeg.hasOwnProperty(k)) { mag[k] = base[k] || 0; if (!indeg[k]) Q.push(k); }
      while (Q.length) {
        var x = Q.shift(); done++;
        var o = D.out[x] || [];
        for (j = 0; j < o.length; j++) {
          if (mag[o[j]] < mag[x] + gap) mag[o[j]] = mag[x] + gap;
          if (--indeg[o[j]] === 0) Q.push(o[j]);
        }
      }
      /* every node by its own name, through the group it was tied into */
      for (k in base0) if (base0.hasOwnProperty(k)) mag[k] = mag[D.root(k)] !== undefined ? mag[D.root(k)] : base0[k];
      return mag;
    }
    function _asmSpread() {
      var T = global.THREE, S = _asm2.steps, i, j, out = [];
      if (!T || !S.length) return out;
      if (_xpCache && _xpCache.steps === S) return _xpCache.out;
      /* Product-defined rigid assemblies preserve the native animation,
         camera fit, picking and catalogue walk. Other products keep their path. */
      if (typeof __PROD._assemblySpread === 'function') {
        out = __PROD._assemblySpread(S, T);
        if (Array.isArray(out) && out.length) {
          _xpInfo = 'rigid assemblies: ' + out.length + ' modeled bodies';
          _xpCache = {steps:S, out:out}; return out;
        }
        out = [];
      }
      if (__PROD._asmRadial) {
        var centre = _xpCentroid(), baseY = API.storeys()[0].y0, boxR = new T.Box3(), centreR = new T.Vector3();
        for (i=0;i<S.length;i++) for(j=0;j<S[i].meshes.length;j++) {
          var body=S[i].meshes[j], rest=body.userData.__asmPos || (body.userData.__asmPos={x:body.position.x,y:body.position.y,z:body.position.z});
          var current=body.position.clone();body.position.set(rest.x,rest.y,rest.z);body.updateMatrixWorld(true);
          boxR.setFromObject(body);boxR.getCenter(centreR);body.position.copy(current);body.updateMatrixWorld(true);
          var fixed=!!body.userData.bunkerContext, gain=EXPLODE_GAIN;
          out.push({m:body,p:rest,v:{x:fixed?0:(centreR.x-centre.x)*gain,y:fixed?0:(centreR.y-baseY)*gain,z:fixed?0:(centreR.z-centre.z)*gain},l:{x:0,y:0,z:0}});
        }
        _xpInfo='radial: '+out.length+' modeled bodies, gain '+EXPLODE_GAIN+' from floor centroid';
        _xpCache={steps:S,out:out};return out;
      }

      var t0 = (global.performance && global.performance.now) ? global.performance.now() : Date.now();
      var C = _xpCentroid(), P0 = poly(), yFloor = 0.35, yPlate = 2.75;
      var yUpper = Infinity;   /* the next storey's floor: only a wall standing on it rides up */
      try { var S0 = API.storeys()[0]; yFloor = S0.y0; yPlate = (S0.head != null) ? S0.head : (S0.y1 != null ? S0.y1 : S0.y0 + 2.4); var S1 = API.storeys()[1]; if (S1 && isFinite(S1.y0)) yUpper = S1.y0; } catch (eS0) {}
      /* an A-frame has no wall and a storey 10 mm high: its 'plate' at 450
         sent the heat pump outside and the stair up through the roof */
      yPlate = Math.max(yPlate, yFloor + 2.0);
      /* AN A-FRAME'S ROOF IS ITS WALLS (its storey is 10 mm high): a roof
         layer's piece goes out with the slope it lies in, and only what lies
         on no slope -- the ridge, the loft -- goes up */
      var roofWalls = false;
      try { roofWalls = API.storeys()[0].h < 1.0; } catch (eRW) {}
      var SG = [];
      try { SG = segments().filter(function (s) { return (s.storey | 0) === 0 && s.len > 0.05; }); } catch (eSg) { SG = []; }
      /* WALLS ON ONE LINE ARE ONE FACE: a wall cut in two (a T's bar end
         beside its stem) gave the masonry one half and its insulation the
         other, two faces with no order between them */
      (function () {
        var M = [], i2, j2;
        for (i2 = 0; i2 < SG.length; i2++) {
          var s2 = SG[i2], off = s2.x0 * s2.nx + s2.z0 * s2.nz, hit = null;
          for (j2 = 0; j2 < M.length; j2++) if (M[j2].nx * s2.nx + M[j2].nz * s2.nz > 0.99 && Math.abs(M[j2].off - off) < 0.03) { hit = M[j2]; break; }
          if (!hit) { M.push({ x0: s2.x0, z0: s2.z0, x1: s2.x1, z1: s2.z1, len: s2.len, nx: s2.nx, nz: s2.nz, off: off, id: s2.id, storey: 0 }); continue; }
          var ux2 = (hit.x1 - hit.x0) / hit.len, uz2 = (hit.z1 - hit.z0) / hit.len;
          var tA = (s2.x0 - hit.x0) * ux2 + (s2.z0 - hit.z0) * uz2, tB = (s2.x1 - hit.x0) * ux2 + (s2.z1 - hit.z0) * uz2;
          var t0 = Math.min(0, tA, tB), t1 = Math.max(hit.len, tA, tB), bx0 = hit.x0, bz0 = hit.z0;
          hit.x0 = bx0 + ux2 * t0; hit.z0 = bz0 + uz2 * t0; hit.x1 = bx0 + ux2 * t1; hit.z1 = bz0 + uz2 * t1; hit.len = t1 - t0;
        }
        SG = M;
      })();
      var H = XP.cell, EPS = 0.02;
      /* ---- 1. every body: its place at rest, its triangles, its kind ---- */
      var B = [], bx = new T.Box3(), v3 = new T.Vector3(), nTri = 0;
      for (i = 0; i < S.length; i++) {
        var st = S[i], kind = _xpKind(st.layer), near = _xpNearKey(st, kind);
        for (j = 0; j < st.meshes.length; j++) {
          var m = st.meshes[j];
          var p = m.userData.__asmPos || (m.userData.__asmPos = { x: m.position.x, y: m.position.y, z: m.position.z });
          var cur = { x: m.position.x, y: m.position.y, z: m.position.z };
          m.position.set(p.x, p.y, p.z); m.updateMatrixWorld(true);
          var tris = [], lo = Infinity, hi = -Infinity, mnx = Infinity, mxx = -Infinity, mnz = Infinity, mxz = -Infinity;
          m.traverse(function (o) {
            if (!o.isMesh || o.isInstancedMesh || !o.geometry || !o.geometry.attributes || !o.geometry.attributes.position) return;
            var pa = o.geometry.attributes.position, ix = o.geometry.index, n = ix ? ix.count : pa.count, mw = o.matrixWorld, q;
            if (n > 180000) n = 180000 - (180000 % 3);
            var arr = new Float32Array(n * 3);
            for (q = 0; q < n; q++) {
              v3.fromBufferAttribute(pa, ix ? ix.getX(q) : q).applyMatrix4(mw);
              arr[q * 3] = v3.x; arr[q * 3 + 1] = v3.y; arr[q * 3 + 2] = v3.z;
              if (v3.y < lo) lo = v3.y; if (v3.y > hi) hi = v3.y;
              if (v3.x < mnx) mnx = v3.x; if (v3.x > mxx) mxx = v3.x; if (v3.z < mnz) mnz = v3.z; if (v3.z > mxz) mxz = v3.z;
            }
            tris.push(arr); nTri += n / 3;
          });
          m.position.set(cur.x, cur.y, cur.z); m.updateMatrixWorld(true);
          if (!tris.length || !isFinite(lo)) { out.push({ m: m, p: p, v: { x: 0, y: 0, z: 0 }, l: { x: 0, y: 0, z: 0 } }); continue; }
          var b = { m: m, p: p, st: i, kind: kind, near: near, tris: tris, lo: lo, hi: hi, x0: mnx, x1: mxx, z0: mnz, z1: mxz,
                    cx: (mnx + mxx) / 2, cz: (mnz + mxz) / 2, cls: '', faces: [] };
          /* which way */
          /* a wall's piece goes out with its wall even under the floor line
             (a crawl space's skirt went down through the piers) */
          if (kind.wall) b.cls = 'lat';
          else if (hi <= yFloor + EPS) b.cls = 'down';
          else if ((kind.roof || lo >= yPlate - EPS) && roofWalls && !kind.part && !kind.fit) { b.cls = 'lat'; b.roofLat = true; }
          else if (kind.roof && lo >= yFloor + 1.0) b.cls = 'up';
          else if (lo >= yPlate - EPS) b.cls = 'up';
          else b.cls = 'lat';
          /* a piece standing up in a wall -- the wool between an A-frame's
             gable studs is the lining's -- may be the wall's; a truss never is */
          if (b.cls === 'up' && !(st.key === 'viz_trusses' || ASM_TRUSS_KEYS[st.key]) && Math.min(mxx - mnx, mxz - mnz) <= 0.25 && hi - lo >= 0.25 && _xpStanding(tris)) b.vertRoof = true;
          B.push(b);
        }
      }
      /* the faces a lateral body belongs to: the ground floor's outline
         segments it lies along -- within reach of the outline and not deep
         inside the plan -- or outside the outline altogether */
      /* an A-frame's piece high on a slope, far from the sill: the wall under
         the eave of the roof plane it lies on (the ridge's own pieces, on no
         one plane, go up) */
      var RPs = null;
      function slopeFace(b) {
        if (RPs === null) { try { RPs = roofPlanes() || []; } catch (eRP) { RPs = []; } }
        var cx = (b.x0 + b.x1) / 2, cz = (b.z0 + b.z1) / 2, best = [], bs = -1, p3, s3;
        for (p3 = 0; p3 < RPs.length; p3++) {
          var E = RPs[p3].eave, R3 = RPs[p3].ridge;
          if (!E || !R3 || E.length < 2 || R3.length < 2) continue;
          var emx = (E[0][0] + E[1][0]) / 2, emz = (E[0][2] + E[1][2]) / 2, rmx = (R3[0][0] + R3[1][0]) / 2, rmz = (R3[0][2] + R3[1][2]) / 2;
          var hx = emx - rmx, hz = emz - rmz, hL = Math.hypot(hx, hz);
          if (!(hL > 0.2)) continue;
          hx /= hL; hz /= hL;
          var sd = (cx - rmx) * hx + (cz - rmz) * hz;
          if (sd < 0.15 || sd > hL + 0.6) continue;   /* on this plane's side of the ridge, not past its eave */
          var ex = E[1][0] - E[0][0], ez = E[1][2] - E[0][2], eL = Math.hypot(ex, ez) || 1, al = ((cx - E[0][0]) * ex + (cz - E[0][2]) * ez) / eL;
          if (al < -0.6 || al > eL + 0.6) continue;
          for (s3 = 0; s3 < SG.length; s3++) {
            var dot3 = SG[s3].nx * hx + SG[s3].nz * hz;
            if (dot3 > 0.9 && dot3 > bs) { bs = dot3; best = [s3]; }
          }
        }
        return best;
      }
      function riseFrom(b, g, dOut, dIn) {
        var span = dOut - dIn, yS = 0, nS = 0, yD = 0, nD = 0, t3, q3;
        if (b.hi - b.lo < 0.5 * span) return false;
        for (t3 = 0; t3 < b.tris.length; t3++) {
          var A3 = b.tris[t3];
          for (q3 = 0; q3 + 2 < A3.length; q3 += 3) {
            var d3 = (A3[q3] - g.x0) * g.nx + (A3[q3 + 2] - g.z0) * g.nz;
            if (d3 > dOut - 0.25 * span) { yS += A3[q3 + 1]; nS++; } else if (d3 < dIn + 0.25 * span) { yD += A3[q3 + 1]; nD++; }
          }
        }
        return nS > 0 && nD > 0 && (yD / nD - yS / nS) > 0.5 * span;
      }
      function facesOf(b) {
        var cand = [], s, k2;
        var corners = [[b.x0, b.z0], [b.x1, b.z0], [b.x0, b.z1], [b.x1, b.z1]];
        for (s = 0; s < SG.length; s++) {
          var g = SG[s], ux = (g.x1 - g.x0) / g.len, uz = (g.z1 - g.z0) / g.len, dOut = -Infinity, dIn = Infinity, a0 = Infinity, a1 = -Infinity;
          for (k2 = 0; k2 < 4; k2++) {
            var dx = corners[k2][0] - g.x0, dz = corners[k2][1] - g.z0, d = dx * g.nx + dz * g.nz, al = dx * ux + dz * uz;
            if (d > dOut) dOut = d; if (d < dIn) dIn = d; if (al < a0) a0 = al; if (al > a1) a1 = al;
          }
          if (a1 < -XP.band || a0 > g.len + XP.band) continue;
          if (dOut < -XP.band) continue;
          /* deep inside the plan -- unless it is a slope rising from this wall
             (an A-frame's rafters, wool and sheathing run from the sill to the
             ridge: its roof IS its walls, and they stayed where they were) */
          if (dIn < -XP.deep && !riseFrom(b, g, dOut, dIn)) continue;
          cand.push({ s: s, dOut: dOut, asp: (a1 - a0) / Math.max(0.01, dOut - dIn) });
        }
        /* the wall it lies along first -- nearest, and longer along it than
           across: the end band of a T's bar lies along the bar's end, not
           3.7 m out of line with the stem's side facing the same way, and a
           strip of insulation ending at a corner along its own wall */
        cand.sort(function (p1, p2) { return Math.abs(p1.dOut) / Math.min(10, Math.max(0.1, p1.asp)) - Math.abs(p2.dOut) / Math.min(10, Math.max(0.1, p2.asp)); });
        /* two faces only at a corner: two walls facing the same way (a T's
           bar end and its stem, one wall cut in two) are one direction, and
           summed they sent the masonry out twice as far as its insulation */
        var keep = [];
        for (k2 = 0; k2 < cand.length && keep.length < 2; k2++) {
          var gk = SG[cand[k2].s], par2 = false;
          for (var k3 = 0; k3 < keep.length; k3++) if (gk.nx * SG[keep[k3]].nx + gk.nz * SG[keep[k3]].nz > 0.5) par2 = true;
          /* no second wall for a strip clearly along its own wall (the
             insulation of a T's bar running into the corner with the stem) */
          if (keep.length && cand[0].asp >= 3) continue;
          if (!par2) keep.push(cand[k2].s);
        }
        return keep;
      }
      for (i = 0; i < B.length; i++) {
        var bb = B[i];
        if (bb.vertRoof) { var fv0 = facesOf(bb); if (fv0.length === 1) { bb.cls = 'lat'; bb.faces = fv0; } continue; }
        if (bb.roofLat) { bb.faces = facesOf(bb); if (!bb.faces.length) bb.faces = slopeFace(bb); if (!bb.faces.length) bb.cls = 'up'; continue; }
        if (bb.cls !== 'lat') continue;
        /* a wall layer's body wholly above the plate goes out with its wall
           only if it lies along one (a gable triangle); the insulation the
           shop lays under its roof is in the wall layer, and goes up */
        if (bb.kind.wall && bb.lo >= yPlate - EPS && !facesOf(bb).length) { bb.cls = 'up'; continue; }
        var outside = !_xpInside(P0, bb.cx, bb.cz);
        if ((bb.kind.part || bb.kind.fit) && !outside) { bb.cls = 'in'; continue; }
        bb.faces = facesOf(bb);
        if (!bb.faces.length) bb.cls = 'in';
      }
      /* A PIECE HELD BY ANOTHER OF ITS OWN ITEM GOES WITH IT: a downpipe's
         strap at mid height is the roof's (the gutters item) and rose through
         the eave and the soffit -- it goes out with its downpipe */
      for (i = 0; i < B.length; i++) {
        var bs = B[i];
        if (bs.cls !== 'up' || bs.hi > yPlate - EPS) continue;
        for (j = 0; j < B.length; j++) {
          var bh = B[j];
          if (bh.st !== bs.st || bh.cls !== 'lat' || !bh.faces.length) continue;
          if (bs.x0 < bh.x0 - 0.06 || bs.x1 > bh.x1 + 0.06 || bs.z0 < bh.z0 - 0.06 || bs.z1 > bh.z1 + 0.06 || bs.lo < bh.lo - 0.06 || bs.hi > bh.hi + 0.06) continue;
          bs.cls = 'lat'; bs.faces = bh.faces.slice(); break;
        }
      }
      var now = function () { return (global.performance && global.performance.now) ? global.performance.now() : Date.now(); }, tm = [now()];
      /* ---- 2. the plan grid: up and down groups, and what rests on what ---- */
      var GP = _xpGrid(), Ux = function (A, q) { return A[q]; }, Vz = function (A, q) { return A[q + 2]; }, Wy = function (A, q) { return A[q + 1]; };
      for (i = 0; i < B.length; i++) for (j = 0; j < B[i].tris.length; j++) _xpRaster(GP, B[i].tris[j], i, Ux, Vz, Wy, H);
      _xpFinal(GP);
      /* riders: a body that is not going up but rests on one that is, rides
         up with it (iterated: a loft's furniture on its floor finish) */
      var riders = 0, pass;
      /* AN UPPER STOREY'S WALLS RIDE UP WITH IT, all of them, at once: found
         one by one resting on what goes up, a wall's bands were picked up a
         pass at a time and the rest left standing for the floor to go
         through (the garage apartment) */
      for (i = 0; i < B.length; i++) {
        var bw = B[i];
        if (!bw.kind.wall || bw.cls === 'down' || !(bw.lo >= yUpper - 0.3)) continue;
        bw.rider = true; bw.wallRider = true; bw.latCls = bw.cls; bw.cls = 'up'; riders++;
        if (!bw.faces.length) bw.faces = facesOf(bw);
      }
      for (pass = 0; pass < 4; pass++) {
        var on = {}, tot = {}, changed = false;
        GP.m.forEach(function (c) {
          var q, r;
          for (q = 0; q < c.length; q += 4) {
            var bq = B[c[q]];
            if (bq.cls === 'up' || bq.cls === 'down') continue;
            tot[c[q]] = (tot[c[q]] || 0) + 1;
            for (r = 0; r < c.length; r += 4) {
              var br = B[c[r]];
              if (br.cls !== 'up') continue;
              if (c[q + 1] >= c[r + 2] - 0.02 && c[q + 1] + c[q + 2] > c[r + 1] + c[r + 2] && c[q + 1] - c[r + 2] <= XP.rest) { on[c[q]] = (on[c[q]] || 0) + 1; break; }
            }
          }
        });
        for (i = 0; i < B.length; i++) {
          if (B[i].cls === 'up' || B[i].cls === 'down' || !tot[i] || B[i].vertRoof) continue;   /* a piece standing in a wall goes with the wall */
          /* a wall's own piece rides only above the ground storey (an upper
             storey's walls on its floor); at the plate it is the wall's -- a
             gable triangle, a heel bolt -- not the roof's */
          if (B[i].kind.wall && !(B[i].lo >= yUpper - 0.3)) continue;   /* a gable's board on a ceiling's rim is the gable's (ranch) */
          if ((on[i] || 0) >= 0.5 * tot[i] && B[i].lo > yFloor + 0.5) {
            B[i].rider = true; B[i].latCls = B[i].cls; B[i].cls = 'up'; riders++; changed = true;
            /* only a wall rides up AND goes out (an upper storey's walls on
               their floor); a cable's bend or a loft's chair just rides */
            if (!B[i].kind.wall) B[i].faces = [];
          }
        }
        if (!changed) break;
      }
      /* the votes: in every cell, a group wholly above (up) or below (down)
         another must travel further */
      /* body against body: 'a>b' = a is wholly under b in a cell (b travels further) */
      var bvU = {}, bvD = {}, bvL = {}, bvLf = {};
      function vote(bv, a, b) { var kv2 = a + '>' + b; bv[kv2] = (bv[kv2] || 0) + 1; }
      /* ONE THIN THING ROUND ANOTHER IN A CELL -- a rake trim folded over the
         steel's edge, a down conductor lying in the steel's ribs: the SMALLER
         of the two (in plan) is the one that sits on the other, and goes
         further. Up and down only. */
      GP.m.forEach(function (c) {
        var q, r;
        for (q = 0; q < c.length; q += 4) {
          var bq = B[c[q]], top = -1, encl = [];
          if (bq.cls !== 'up' && bq.cls !== 'down') continue;
          for (r = 0; r < c.length; r += 4) {
            if (q === r) continue;
            var br = B[c[r]];
            if (bq.st === br.st || bq.cls !== br.cls) continue;
            if (c[q + 2] - c[q + 1] <= 0.12 && c[q + 1] < c[r + 1] - XP.tol && c[q + 2] > c[r + 2] + XP.tol) {
              encl.push(r);
              if (top < 0 || (bq.cls === 'up' ? c[r + 2] > c[top + 2] : c[r + 1] < c[top + 1])) top = r;
              continue;
            }
            if (!_xpOver(c, q, r)) continue;   /* q wholly above r in this cell */
            if (bq.cls === 'up') vote(bvU, c[r], c[q]); else vote(bvD, c[q], c[r]);
          }
          for (r = 0; r < encl.length; r++) {
            var e5 = encl[r], bv5 = (bq.cls === 'up') ? bvU : bvD, be5 = B[c[e5]];
            var qSmall = (bq.x1 - bq.x0) * (bq.z1 - bq.z0) <= (be5.x1 - be5.x0) * (be5.z1 - be5.z0);
            /* up: the smaller goes above; down: the smaller goes below */
            if (bq.cls === 'up') { if (qSmall) vote(bv5, c[e5], c[q]); else vote(bv5, c[q], c[e5]); }
            else { if (qSmall) vote(bv5, c[q], c[e5]); else vote(bv5, c[e5], c[q]); }
          }
        }
      });
      var k;
      tm.push(now());
      /* ---- 3. the faces: out groups along each wall, and what an up or down
         group must clear before the walls move ---- */
      var GF = [], colX = {}, crossM = {}, blockF = {};
      for (k = 0; k < SG.length; k++) GF.push(_xpGrid());
      for (k = 0; k < SG.length; k++) {
        (function (g, G, fI) {
          var ux = (g.x1 - g.x0) / g.len, uz = (g.z1 - g.z0) / g.len;
          var U = function (A, q) { return (A[q] - g.x0) * ux + (A[q + 2] - g.z0) * uz; };
          var V = function (A, q) { return A[q + 1]; };
          var W = function (A, q) { return (A[q] - g.x0) * g.nx + (A[q + 2] - g.z0) * g.nz; };
          /* the corner pieces going this way: another wall's body is drawn here
             only where it could stand in front of one of them */
          var cornerBx = [];
          for (i = 0; i < B.length; i++) { var bc = B[i]; if (bc.faces.length > 1 && bc.faces.indexOf(fI) >= 0) cornerBx.push(bc); }
          var nearCorner = function (b) {
            for (var t = 0; t < cornerBx.length; t++) { var cb = cornerBx[t]; if (b.x0 <= cb.x1 + 0.5 && b.x1 >= cb.x0 - 0.5 && b.z0 <= cb.z1 + 0.5 && b.z1 >= cb.z0 - 0.5 && b.lo <= cb.hi && b.hi >= cb.lo) return true; }
            return false;
          };
          for (i = 0; i < B.length; i++) {
            var b4 = B[i], mine = b4.faces.indexOf(SG.indexOf(g)) >= 0;
            if (!mine) {
              /* an up or down body near this face: what the walls must clear;
                 another wall's body near it: what a corner piece going this
                 way would run into */
              if (b4.cls !== 'up' && b4.cls !== 'down' && !(b4.cls === 'lat' && b4.faces.length && nearCorner(b4))) continue;
              var dO = Math.max((b4.x0 - g.x0) * g.nx + (b4.z0 - g.z0) * g.nz, (b4.x1 - g.x0) * g.nx + (b4.z1 - g.z0) * g.nz,
                                (b4.x0 - g.x0) * g.nx + (b4.z1 - g.z0) * g.nz, (b4.x1 - g.x0) * g.nx + (b4.z0 - g.z0) * g.nz);
              if (dO < -XP.deep) continue;
            }
            for (j = 0; j < b4.tris.length; j++) _xpRaster(G, b4.tris[j], i, U, V, W, H);
          }
          _xpFinal(G);
          /* per column (along the wall) every body's lowest and highest cell */
          G.m.forEach(function (c, key) {
            var ku = Math.floor(key / 80000) - 40000, kv = (key % 80000) - 40000, q, r;
            for (q = 0; q < c.length; q += 4) {
              var ck = fI + ':' + c[q] + ':' + ku, e = colX[ck];   /* per face: two walls' columns are not one */
              if (!e) colX[ck] = [kv, kv]; else { if (kv < e[0]) e[0] = kv; if (kv > e[1]) e[1] = kv; }
            }
            for (q = 0; q < c.length; q += 4) {
             for (r = 0; r < c.length; r += 4) {
              if (q === r) continue;
              var bq = B[c[q]], br = B[c[r]];
              if (bq.st === br.st) continue;
              var qLat = bq.faces.indexOf(fI) >= 0 && (bq.cls === 'lat' || bq.rider), rLat = br.faces.indexOf(fI) >= 0 && (br.cls === 'lat' || br.rider);
              if (!_xpOver(c, q, r)) continue;   /* q wholly outward of r in this cell */
              /* a corner piece with something of another wall outward of it,
                 that does not go this way: it cannot go this way either */
              if (rLat && br.faces.length > 1 && !br.near && bq.cls === 'lat' && bq.faces.length && bq.faces.indexOf(fI) < 0 && !bq.near) { var bk = blockF[c[r]] || (blockF[c[r]] = {}); bk[fI] = (bk[fI] || 0) + 1; continue; }
              if (qLat && rLat && !bq.near && !br.near) { vote(bvL, c[r], c[q]); var kf = c[r] + '>' + c[q] + '@' + fI; bvLf[kf] = (bvLf[kf] || 0) + 1; }
              /* an up / down body outward of a wall body at the same height:
                 it has to be out of the wall's way before the wall moves */
              else if (rLat && !br.near && (bq.cls === 'up' || bq.cls === 'down') && !bq.rider) crossM[c[r] + ',' + c[q] + ',' + fI + ':' + ku] = 1;
             }
            }
          });
        })(SG[k], GF[k], k);
      }
      tm.push(now());
      var unblocked = 0, bf;
      for (bf in blockF) {
        if (!blockF.hasOwnProperty(bf)) continue;
        var bB = B[+bf], keepF = bB.faces.filter(function (f2) { return !((blockF[bf][f2] || 0) >= 2); });
        if (keepF.length < bB.faces.length) { bB.faces = keepF.length ? keepF : [bB.faces[0]]; unblocked++; }
      }
      /* ---- 4. the groups that move as one: a step (an item moves as one
         thing), unless its bodies lie on both sides of another item -- the
         eave drip goes under the steel and the rake trim over it, a cable
         runs in the cavity and out on the room side to its box: such a
         step's bodies keep their own places in the order. A truss never
         comes apart. ---- */
      var split = {}, nSplit = 0;
      function conflicts(bv) {
        var side = {}, kk3;
        for (kk3 in bv) {
          if (!bv.hasOwnProperty(kk3) || bv[kk3] < 3) continue;
          var ab3 = kk3.split('>'), a3 = +ab3[0], b3 = +ab3[1];
          var ka = a3 + ':' + B[b3].st, kb = b3 + ':' + B[a3].st;
          side[ka] = (side[ka] || 0) + bv[kk3];     /* a under step(b) */
          side[kb] = (side[kb] || 0) - bv[kk3];     /* b over step(a) */
        }
        var seen = {}, kk4;
        for (kk4 in side) {
          if (!side.hasOwnProperty(kk4) || Math.abs(side[kk4]) < 3) continue;
          var p4 = kk4.split(':'), st4 = B[+p4[0]].st, key4 = st4 + ':' + p4[1], sg4 = side[kk4] > 0 ? 1 : 2;
          seen[key4] = (seen[key4] || 0) | sg4;
          if (seen[key4] === 3 && !split[st4]) {
            var S4 = S[st4];
            if (!(S4 && (S4.key === 'viz_trusses' || ASM_TRUSS_KEYS[S4.key]))) { split[st4] = true; nSplit++; }
          }
        }
      }
      conflicts(bvU); conflicts(bvD); conflicts(bvL);
      var vU = {}, vD = {}, vL = {}, baseU = {}, baseD = {}, baseL = {}, nodesU = [], nodesD = [], nodesL = [];
      for (i = 0; i < B.length; i++) {
        var bn2 = B[i], tag2 = split[bn2.st] ? ('b' + i) : ('s' + bn2.st);
        bn2.nU = 'u' + tag2; bn2.nD = 'd' + tag2;
        /* out: a group per item AND WALL -- the west wall's pipe and the east
           wall's go opposite ways and have nothing to keep in order; as one
           group, every cell where the one lay outward of anything was the
           other's too, and the orders closed in loops across the house */
        /* a corner piece (two walls) is a group of its own: the end stud of
           the one wall and the first wool bay of the other are both 'corner',
           on opposite sides of each other, and as one group they had no order */
        bn2.nL = 'l' + ((split[bn2.st] || bn2.faces.length > 1) ? ('b' + i) : (tag2 + ':' + bn2.faces.join('/')));
        /* AND A DISTANCE PER WALL IT GOES OUT FROM: a corner piece goes out
           from both walls, each by the order of that wall's own layers -- one
           distance for both mixed the two walls' orders, and the end stud of
           the one and the wool of the other went through each other */
        bn2.nLf = {};
        if (bn2.cls === 'up') { if (baseU[bn2.nU] === undefined) { baseU[bn2.nU] = Infinity; nodesU.push(bn2.nU); } baseU[bn2.nU] = Math.min(baseU[bn2.nU], bn2.lo); }
        if (bn2.cls === 'down') { if (baseD[bn2.nD] === undefined) { baseD[bn2.nD] = -Infinity; nodesD.push(bn2.nD); } baseD[bn2.nD] = Math.max(baseD[bn2.nD], bn2.hi); }
        if (bn2.cls === 'lat' || (bn2.rider && bn2.faces.length)) {
          for (j = 0; j < bn2.faces.length; j++) {
            var nf2 = bn2.nL + '@' + bn2.faces[j];
            bn2.nLf[bn2.faces[j]] = nf2;
            if (baseL[nf2] === undefined) { baseL[nf2] = Infinity; nodesL.push(nf2); }
            var g2 = SG[bn2.faces[j]], dIn3 = Math.min(
              (bn2.x0 - C.x) * g2.nx + (bn2.z0 - C.z) * g2.nz, (bn2.x1 - C.x) * g2.nx + (bn2.z0 - C.z) * g2.nz,
              (bn2.x0 - C.x) * g2.nx + (bn2.z1 - C.z) * g2.nz, (bn2.x1 - C.x) * g2.nx + (bn2.z1 - C.z) * g2.nz);
            baseL[nf2] = Math.min(baseL[nf2], Math.max(0, dIn3));
          }
        }
      }
      for (k = 0; k < nodesU.length; k++) baseU[nodesU[k]] = XP.min0 + EXPLODE_GAIN * Math.max(0, baseU[nodesU[k]] - yFloor);
      for (k = 0; k < nodesD.length; k++) baseD[nodesD[k]] = XP.min0 + EXPLODE_GAIN * Math.max(0, yFloor - baseD[nodesD[k]]);
      for (k = 0; k < nodesL.length; k++) baseL[nodesL[k]] = XP.min0 + EXPLODE_GAIN * (isFinite(baseL[nodesL[k]]) ? baseL[nodesL[k]] : 0);
      function nodeVotes(bv, V, f) {
        var kk5;
        for (kk5 in bv) {
          if (!bv.hasOwnProperty(kk5)) continue;
          var ab5 = kk5.split('>'), na = B[+ab5[0]][f], nb = B[+ab5[1]][f];
          if (na === nb) continue;
          V[na + '>' + nb] = (V[na + '>' + nb] || 0) + bv[kk5];
        }
      }
      nodeVotes(bvU, vU, 'nU'); nodeVotes(bvD, vD, 'nD');
      var kk9;
      for (kk9 in bvLf) {
        if (!bvLf.hasOwnProperty(kk9)) continue;
        var m8 = kk9.split(/[>@]/), na8 = B[+m8[0]].nLf[m8[2]], nb8 = B[+m8[1]].nLf[m8[2]];
        if (!na8 || !nb8 || na8 === nb8) continue;
        vL[na8 + '>' + nb8] = (vL[na8 + '>' + nb8] || 0) + bvLf[kk9];
      }
      /* ---- 5. the distances ---- */
      /* the small groups: no more than 4 cells in their own grid */
      var cellsU = {}, cellsL = {}, smallU = {}, smallL = {};
      GP.m.forEach(function (c) { for (var q = 0; q < c.length; q += 4) { var bq = B[c[q]]; cellsU[bq.nU] = (cellsU[bq.nU] || 0) + 1; cellsU[bq.nD] = (cellsU[bq.nD] || 0) + 1; } });
      for (k = 0; k < SG.length; k++) GF[k].m.forEach(function (c) { for (var q = 0; q < c.length; q += 4) { var nq = B[c[q]].nLf && B[c[q]].nLf[k]; if (nq) cellsL[nq] = (cellsL[nq] || 0) + 1; } });
      for (k in cellsU) if (cellsU.hasOwnProperty(k) && cellsU[k] <= 4) smallU[k] = true;
      for (k in cellsL) if (cellsL.hasOwnProperty(k) && cellsL[k] <= 4) smallL[k] = true;
      var DU = _xpDag(vU, nodesU, smallU), DD = _xpDag(vD, nodesD, smallU), DL = _xpDag(vL, nodesL, smallL);
      var magU = _xpMags(DU, baseU, XP.gap), magD = _xpMags(DD, baseD, XP.gap), magL = _xpMags(DL, baseL, XP.gap);
      /* WHAT WAITS FOR THE WALLS: a body going up with a wall's body over it
         (an A-frame's loft floor under its roof slopes, an upper floor through
         balloon studs) would go up through it. It waits until the walls are
         out (k = XP.kL2), and so does everything under it in the order, which
         would otherwise rise into it; the wall body over it goes out far
         enough by then to be off it. Down the same way, under a wall body. */
      var lateU = {}, lateD = {}, needLate = {}, nLate = 0, fL2 = _xpLatF(XP.kL2), lateC = {}, lateP = {}, needy = {}, cr0;
      /* what has to clear the walls FIRST (a roof's eave over them) never
         waits for them, nor does anything that carries it */
      for (cr0 in crossM) if (crossM.hasOwnProperty(cr0)) { var vb0 = B[+cr0.split(',')[1]]; needy[vb0.cls === 'up' ? DU.root(vb0.nU) : DD.root(vb0.nD)] = true; }
      GP.m.forEach(function (c) {
        var q, r;
        for (q = 0; q < c.length; q += 4) {
          var bq = B[c[q]];
          if (bq.cls !== 'up' && bq.cls !== 'down') continue;
          for (r = 0; r < c.length; r += 4) {
            var br = B[c[r]];
            /* a wall body's own face over it, read at the centre -- a slope, a
               sill, a plate's top; a sheet seen edge on says nothing */
            if (br.cls !== 'lat' || br.near || !br.faces.length || br.st === bq.st || c[r + 3] || c[q + 3]) continue;
            if (br.x1 - br.x0 < 0.03 || br.z1 - br.z0 < 0.03) continue;
            if (bq.cls === 'up' ? !_xpOver(c, r, q) : !_xpOver(c, q, r)) continue;
            var nq9 = bq.cls === 'up' ? bq.nU : bq.nD;
            if (needy[bq.cls === 'up' ? DU.root(nq9) : DD.root(nq9)]) continue;
            lateC[nq9] = (lateC[nq9] || 0) + 1;
            (lateP[nq9] = lateP[nq9] || {})[c[r]] = c[q];
          }
        }
      });
      var nq8, rb8, f9;
      for (nq8 in lateC) {
        if (!lateC.hasOwnProperty(nq8) || lateC[nq8] < 3) continue;
        for (rb8 in lateP[nq8]) {
          if (!lateP[nq8].hasOwnProperty(rb8)) continue;
          var br8 = B[+rb8], bq8 = B[lateP[nq8][rb8]];
          if (nq8.charAt(0) === 'u') { if (!lateU[nq8]) lateU[nq8] = rb8 + ' over ' + lateP[nq8][rb8]; } else if (!lateD[nq8]) lateD[nq8] = rb8 + ' under ' + lateP[nq8][rb8];
          /* how far the wall body goes along each of its normals to be off it */
          for (f9 = 0; f9 < br8.faces.length; f9++) {
            var g9 = SG[br8.faces[f9]], nm9 = br8.nLf[br8.faces[f9]];
            var dq9 = function (x, z) { return (x - g9.x0) * g9.nx + (z - g9.z0) * g9.nz; };
            var qMax = Math.max(dq9(bq8.x0, bq8.z0), dq9(bq8.x1, bq8.z0), dq9(bq8.x0, bq8.z1), dq9(bq8.x1, bq8.z1));
            var rMin = Math.min(dq9(br8.x0, br8.z0), dq9(br8.x1, br8.z0), dq9(br8.x0, br8.z1), dq9(br8.x1, br8.z1));
            var nl = (qMax - rMin + XP.clear) / fL2;
            if (nl > (needLate[nm9] || 0)) needLate[nm9] = nl;
          }
        }
      }
      /* ...and everything under a late one in the order */
      function lateDown(D, late) {
        var preds = {}, a9, Q9 = [], seen9 = {};
        for (a9 in D.out) if (D.out.hasOwnProperty(a9)) D.out[a9].forEach(function (b9) { (preds[b9] = preds[b9] || []).push(a9); });
        for (a9 in late) if (late.hasOwnProperty(a9)) { var r9 = D.root(a9); if (!seen9[r9]) { seen9[r9] = true; Q9.push(r9); } }
        while (Q9.length) { var x9 = Q9.pop(); (preds[x9] || []).forEach(function (p9) { if (!seen9[p9] && !needy[p9]) { seen9[p9] = true; Q9.push(p9); } }); }
        return seen9;
      }
      var lateRU = lateDown(DU, lateU), lateRD = lateDown(DD, lateD);
      for (k in needLate) if (needLate.hasOwnProperty(k) && needLate[k] > (baseL[k] || 0)) baseL[k] = needLate[k];
      magL = _xpMags(DL, baseL, XP.gap);
      for (i = 0; i < B.length; i++) {
        var bL = B[i];
        bL.late = (bL.cls === 'up' && !!lateRU[DU.root(bL.nU)]) || (bL.cls === 'down' && !!lateRD[DD.root(bL.nD)]);
        if (bL.late) nLate++;
      }
      /* what has to be cleared before the walls move (at k = XP.kL) */
      var raised = 0, cr, needU = {}, needD = {};
      for (cr in crossM) {
        if (!crossM.hasOwnProperty(cr)) continue;
        var cx3 = cr.split(','), sB = B[+cx3[0]], vB = B[+cx3[1]], col = cx3[2].split(':');
        var es = colX[col[0] + ':' + cx3[0] + ':' + col[1]], ev = colX[col[0] + ':' + cx3[1] + ':' + col[1]];
        if (!es || !ev) continue;
        if (sB.rider || vB.late) continue;   /* a rider goes up with what carries it; a late one waits for the walls */
        if (vB.cls === 'up') {
          var nu = (es[1] * H - ev[0] * H + XP.clear) / XP.kL;
          if (nu > (needU[vB.nU] || 0)) needU[vB.nU] = nu;
        } else {
          var nd = (ev[1] * H - es[0] * H + XP.clear) / XP.kL;
          if (nd > (needD[vB.nD] || 0)) needD[vB.nD] = nd;
        }
      }
      for (k in needU) if (needU.hasOwnProperty(k) && needU[k] > (baseU[k] || 0)) { baseU[k] = needU[k]; raised++; }
      for (k in needD) if (needD.hasOwnProperty(k) && needD[k] > (baseD[k] || 0)) { baseD[k] = needD[k]; raised++; }
      if (raised) { magU = _xpMags(DU, baseU, XP.gap); magD = _xpMags(DD, baseD, XP.gap); }
      /* the upper storey's walls go up as one, by the furthest of them */
      var wrM = 0, wrN = {};
      for (i = 0; i < B.length; i++) if (B[i].wallRider) { wrN[B[i].nU] = true; if ((magU[B[i].nU] || 0) > wrM) wrM = magU[B[i].nU]; }
      if (wrM > 0) { for (k in wrN) if (wrN.hasOwnProperty(k)) baseU[k] = Math.max(baseU[k] || 0, wrM); magU = _xpMags(DU, baseU, XP.gap); }
      /* ---- the doors and windows of the outer walls ----
         A WINDOW GOES OUT BETWEEN THE LAYERS IT IS BUILT BETWEEN: a hand's
         width past everything round it that is not wholly outside it (the
         girts, the liner, the wool, the board), and what lies wholly outside
         it -- the steel over its flange, the trim round the opening -- goes
         further still. It went past the whole wall, through the trim and the
         steel that are put on after it. The pieces of one window (frame, sash,
         glass, handle) go as one. */
      var nearMag = {}, nCl = [], nPar = {}, nIds = [];
      for (i = 0; i < B.length; i++) if (B[i].near && B[i].faces.length) { nPar[i] = i; nIds.push(i); }
      var nRoot = function (x) { while (nPar[x] !== x) { nPar[x] = nPar[nPar[x]]; x = nPar[x]; } return x; };
      for (i = 0; i < nIds.length; i++) for (j = i + 1; j < nIds.length; j++) {
        var w1 = B[nIds[i]], w2 = B[nIds[j]];
        if (w1.st !== w2.st || w1.faces.join() !== w2.faces.join()) continue;
        if (w1.x0 > w2.x1 + 0.03 || w2.x0 > w1.x1 + 0.03 || w1.z0 > w2.z1 + 0.03 || w2.z0 > w1.z1 + 0.03 || w1.lo > w2.hi + 0.03 || w2.lo > w1.hi + 0.03) continue;
        nPar[nRoot(nIds[i])] = nRoot(nIds[j]);
      }
      var nearPass = function () {
        /* per window and wall body: the cells it lies wholly outside the
           window in, and the cells it does not -- the more of the two says
           which side of the window it is on */
        var cOut = {}, cIn = {};
        for (k = 0; k < SG.length; k++) {
          GF[k].m.forEach(function (c) {
            var q, r;
            for (q = 0; q < c.length; q += 4) {
              var bq = B[c[q]];
              if (!bq.near || !bq.faces.length) continue;
              var wr = nRoot(c[q]);
              for (r = 0; r < c.length; r += 4) {
                var br = B[c[r]];
                if (br.near || br.cls !== 'lat' || !br.faces.length) continue;
                if (c[r + 2] < c[q + 1] - 0.35 || c[r + 1] > c[q + 2] + 0.35) continue;
                var tgt = _xpOver(c, r, q) ? cOut : cIn, kw = wr + '|' + br.nLf[k];
                tgt[kw] = (tgt[kw] || 0) + 1;
              }
            }
          });
        }
        var inner = {}, up = 0, kw2, w, n7;
        for (kw2 in cIn) {
          if (!cIn.hasOwnProperty(kw2) || cIn[kw2] < (cOut[kw2] || 0)) continue;
          w = kw2.split('|'); n7 = w[1]; w = w[0];
          if ((magL[n7] || 0) > (inner[w] || 0)) inner[w] = magL[n7];
        }
        for (i = 0; i < nIds.length; i++) { w = nRoot(nIds[i]); nearMag[w] = Math.max(nearMag[w] || 0, (inner[w] || baseL[B[nIds[i]].nLf[B[nIds[i]].faces[0]]] || XP.min0) + XP.near); }
        for (kw2 in cOut) {
          if (!cOut.hasOwnProperty(kw2) || cOut[kw2] <= (cIn[kw2] || 0)) continue;
          w = kw2.split('|'); n7 = w[1]; w = w[0];
          if ((magL[n7] || 0) < nearMag[w] + XP.gap - 1e-6 && (baseL[n7] || 0) < nearMag[w] + XP.gap - 1e-6) { baseL[n7] = nearMag[w] + XP.gap; up++; }
        }
        if (up) magL = _xpMags(DL, baseL, XP.gap);
        return up;
      };
      var nearUp = nearPass();
      if (nearUp) nearUp += nearPass();
      /* A GROUP WITH NO ORDER OF ITS OWN GOES WITH WHAT IT IS BUILT INTO: a
         pipe or a cable in a wall's cavity is neither wholly inside nor wholly
         outside the layers round it, so it has no place in the order and
         travelled by its distance alone -- the board inside it went faster and
         through it. It takes the furthest of the wall's layers it shares its
         depth with. */
      var linked = {}, kk2;
      for (kk2 in vL) if (vL.hasOwnProperty(kk2)) { var ab2 = kk2.split('>'); linked[ab2[0]] = linked[ab2[1]] = true; }
      var hostL = {};
      for (k = 0; k < SG.length; k++) {
        GF[k].m.forEach(function (c) {
          var q, r;
          for (q = 0; q < c.length; q += 4) {
            var bq = B[c[q]], nq = bq.nLf && bq.nLf[k];
            if (!nq || linked[nq] || bq.near || bq.cls !== 'lat' || !bq.faces.length) continue;
            for (r = 0; r < c.length; r += 4) {
              var br = B[c[r]], nr = br.nLf && br.nLf[k];
              if (!nr || r === q || br.st === bq.st || br.near || br.cls !== 'lat' || !br.faces.length || !linked[nr]) continue;
              if (c[r + 2] < c[q + 1] - XP.tol || c[r + 1] > c[q + 2] + XP.tol) continue;   /* the depths overlap */
              if ((magL[nr] || 0) > (hostL[nq] || 0)) hostL[nq] = magL[nr];
            }
          }
        });
      }
      var hosted = 0;
      for (kk2 in hostL) if (hostL.hasOwnProperty(kk2) && hostL[kk2] > (magL[kk2] || 0) - 1e-6) { magL[kk2] = hostL[kk2]; hosted++; }
      /* AN ITEM TAKEN APART STAYS TOGETHER WHERE IT CAN: the pieces of a step
         placed one by one (a lightning cable over the roof's edge and down the
         wall) each went its own distance -- the run along the ridge, the bend
         over the eave and the drop beside it at three heights. Touching pieces
         going the same way go as far as the furthest of them -- each only as
         far as nothing it lies under is passed. */
      var joined = 0;
      function together(cls, D, mag, nodeOf) {
        var st6, ids, pr6, top6;
        var rt6 = function (x) { while (pr6[x] !== x) { pr6[x] = pr6[pr6[x]]; x = pr6[x]; } return x; };
        for (st6 in split) {
          if (!split.hasOwnProperty(st6)) continue;
          ids = []; pr6 = {}; top6 = {};
          for (i = 0; i < B.length; i++) if (B[i].st === +st6 && B[i].cls === cls && !B[i].near && nodeOf(B[i])) { ids.push(i); pr6[i] = i; }
          if (ids.length < 2 || ids.length > 400) continue;
          for (i = 0; i < ids.length; i++) for (j = i + 1; j < ids.length; j++) {
            var p6 = B[ids[i]], q6 = B[ids[j]], t6 = 0.02;
            if (p6.x0 > q6.x1 + t6 || q6.x0 > p6.x1 + t6 || p6.z0 > q6.z1 + t6 || q6.z0 > p6.z1 + t6 || p6.lo > q6.hi + t6 || q6.lo > p6.hi + t6) continue;
            if (cls === 'lat' && p6.faces.join() !== q6.faces.join()) continue;
            pr6[rt6(ids[i])] = rt6(ids[j]);
          }
          for (i = 0; i < ids.length; i++) { var r6 = rt6(ids[i]), mv = mag[nodeOf(B[ids[i]])] || 0; if (!(top6[r6] >= mv)) top6[r6] = mv; }
          for (i = 0; i < ids.length; i++) {
            var n6 = nodeOf(B[ids[i]]), want = top6[rt6(ids[i])], cur = mag[n6] || 0;
            if (want <= cur + 1e-6 || D.root(n6) !== n6) continue;
            var o6 = D.out[n6] || [], lim = want;
            for (j = 0; j < o6.length; j++) lim = Math.min(lim, (mag[o6[j]] || 0) - XP.gap);
            if (lim > cur + 1e-6) { mag[n6] = lim; joined++; }
          }
        }
      }
      together('up', DU, magU, function (b) { return b.nU; }); together('down', DD, magD, function (b) { return b.nD; });
      for (k = 0; k < SG.length; k++) together('lat', DL, magL, function (b) { return (b.nLf && b.nLf[k]) || null; });
      /* A PARTITION'S DOOR STAYS IN ITS PARTITION, with the partition: it
         went 120 mm out of it to either side and ran into the water heater
         beside it, the partition at its jamb and the next door at a corner */
      /* ---- 6. the vectors ---- */
      var nUp = 0, nDown = 0, nLat = 0, nIn = 0, nNear = 0;
      for (i = 0; i < B.length; i++) {
        var b5 = B[i], v = { x: 0, y: 0, z: 0 }, l = { x: 0, y: 0, z: 0 }, f;
        if (b5.cls === 'up') { v.y = magU[b5.nU] || XP.min0; nUp++; }
        else if (b5.cls === 'down') { v.y = -(magD[b5.nD] || XP.min0); nDown++; }
        if (b5.near && b5.faces.length) {
          var mN = nearMag[nRoot(i)] || ((baseL[b5.nLf[b5.faces[0]]] || XP.min0) + XP.near);
          for (f = 0; f < b5.faces.length; f++) { l.x += SG[b5.faces[f]].nx * mN; l.z += SG[b5.faces[f]].nz * mN; }
          nNear++;
        } else if ((b5.cls === 'lat' || b5.rider) && b5.faces.length) {
          for (f = 0; f < b5.faces.length; f++) { var mL = magL[b5.nLf[b5.faces[f]]] || XP.min0; l.x += SG[b5.faces[f]].nx * mL; l.z += SG[b5.faces[f]].nz * mL; }
          nLat++;
        } else if (b5.cls === 'in') nIn++;
        out.push({ m: b5.m, p: b5.p, v: v, l: l, late: !!b5.late });
      }
      var t1 = (global.performance && global.performance.now) ? global.performance.now() : Date.now();
      _xpInfo = 'explode  ' + B.length + ' bodies (' + Math.round(nTri / 1000) + 'k triangles) from the floor centroid ' + C.x.toFixed(2) + ',' + C.z.toFixed(2) +
                ' at +' + yFloor.toFixed(2) + ': ' + nUp + ' up (' + riders + ' riding on what goes up, ' + nLate + ' waiting for the walls), ' + nDown + ' down, ' + nLat + ' out square to their wall, ' + nNear + ' window / door pieces between their wall\'s layers (' + nearUp + ' layer(s) sent past them), ' + nIn + ' inside stay; ' +
                nSplit + ' item(s) placed piece by piece (' + joined + ' piece(s) kept with the rest of the item), order kept up ' + DU.kept + ' / down ' + DD.kept + ' / out ' + DL.kept + ' (left out to break loops ' + (DU.dropped + DD.dropped + DL.dropped) + ', tied together ' + (DU.tied + DD.tied + DL.tied) + ', ' + hosted + ' with the wall they are built into), ' + raised + ' lifted to clear the walls first; ' +
                Math.round(t1 - t0) + ' ms (bodies ' + Math.round(tm[0] - t0) + ', plan ' + Math.round(tm[1] - tm[0]) + ', walls ' + Math.round(tm[2] - tm[1]) + ', order ' + Math.round(t1 - tm[2]) + ') (' + CORE_VERSION + ')';
      try { _log(_xpInfo); } catch (eL) {}
      _xpCache = { steps: S, out: out };
      /* the harness asks for the working (?xpdbg / global.__XPDBG): every body's class and distance */
      if (global.__XPDBG) global.__XPDBG = { DU: DU, DL: DL, DD: DD, baseU: baseU, needU: needU, GP: GP, GF: GF, B: B, SG: SG, magU: magU, magD: magD, magL: magL, nearMag: nearMag, vU: vU, vD: vD, vL: vL, crossM: crossM, colX: colX, lateU: lateU, lateD: lateD, steps: S };
      return out;
    }
    /* the walls wait until what goes up or down has cleared them: k up to
       XP.kL moves only those; by the default k = 2 everything is out */
    function _xpLatF(k) { return (k <= XP.kL) ? 0 : (k <= 2 ? (k - XP.kL) * 2 / (2 - XP.kL) : k); }
    /* ...and what waits for the walls moves once they are well out */
    function _xpLateF(k) { return (k <= XP.kL2) ? 0 : (k <= 2 ? (k - XP.kL2) * 2 / (2 - XP.kL2) : k); }
    var _explodeShown = 0;   /* what is on screen right now, which an animation starts from */
    function setExplode(k, animate, camHome) {
      var T = global.THREE;
      _asmLand();
      /* FROM WHERE IT IS SHOWN, not from the target set a moment ago: the walk
         calls this twice on its first step (once from the rebuild it asks for,
         once after), and the second call found from === to and jumped (Marek) */
      var from = _explodeShown;
      _explodeK = Math.max(0, Math.min(4, Number(k) || 0));
      if (!_asm2.steps.length) _asm2.steps = _asmCollect();
      var sp = _asmSpread(), i;
      function put(kk) {
        _explodeShown = kk;
        var fl = _xpLatF(kk), fw = _xpLateF(kk);   /* core-v658: the walls move once what goes up or down has cleared them; what waits for them, after */
        for (i = 0; i < sp.length; i++) { var e = sp[i], fv = e.late ? fw : kk; e.m.position.set(e.p.x + e.v.x * fv + e.l.x * fl, e.p.y + e.v.y * fv + e.l.y * fl, e.p.z + e.v.z * fv + e.l.z * fl); }
      }
      if (!animate) { put(_explodeK); return _explodeK; }
      /* smooth: from where it was to where it goes, eased -- and THE CAMERA
         BACKS OFF WITH IT, so the whole exploded building is in the frame at
         the end (Marek): the distance it needs is measured on the exploded
         state before the animation starts, and the camera slides out along
         its own line of sight */
      var to = _explodeK, camA = null, camB = null, st0 = _stage();
      /* BACK TO A KNOWN VIEW: the camera and its target slide to the pose
         handed in (core-v479, the walk's step 2) instead of backing off */
      try {
        var camH = st0 && st0.camera, ctlH = st0 && (st0.persControls || st0.controls);
        if (camHome && camH && ctlH && ctlH.target && camH.isPerspectiveCamera && camHome.pos && camHome.tg) {
          camB = { cam: camH, ctl: ctlH, p0: camH.position.clone(), p1: camHome.pos.clone(), t0: ctlH.target.clone(), t1: camHome.tg.clone() };
        }
      } catch (eH) { camB = null; }
      try {
        var cam = st0 && st0.camera, ctl = st0 && (st0.persControls || st0.controls);
        if (!camB && cam && ctl && ctl.target && cam.isPerspectiveCamera) {
          var tg = ctl.target, d0 = cam.position.distanceTo(tg);
          put(to);
          var bx = new T.Box3().setFromObject(st0.group), sph = new T.Sphere(); bx.getBoundingSphere(sph);
          put(from);
          /* a modest step back: the sphere overstates a spread-out building
             (its corners), so 70 percent of the fit -- the exploded house fills
             the frame rather than sitting small in it (Marek: too much) */
          var fov = (cam.fov || 45) * Math.PI / 180, need = sph.radius / Math.sin(fov / 2) * 0.70;
          var dirC = new T.Vector3().subVectors(cam.position, tg).normalize();
          camA = { dir: dirC, d0: d0, d1: Math.max(d0, need), tg: tg.clone(), cam: cam };
        }
      } catch (eC) { camA = null; }
      var t0 = (global.performance && global.performance.now) ? global.performance.now() : Date.now(), DUR = 2600;
      function tick() {
        var now = (global.performance && global.performance.now) ? global.performance.now() : Date.now();
        var u = Math.min(1, (now - t0) / DUR), e2 = (u < 0.5) ? 2 * u * u : 1 - Math.pow(-2 * u + 2, 2) / 2;   /* ease in and out */
        var kk = from + (to - from) * e2;
        put(kk);
        if (camA) { var dd = camA.d0 + (camA.d1 - camA.d0) * e2; camA.cam.position.copy(camA.tg).addScaledVector(camA.dir, dd); camA.cam.lookAt(camA.tg); }
        if (camB) { camB.cam.position.lerpVectors(camB.p0, camB.p1, e2); camB.ctl.target.lerpVectors(camB.t0, camB.t1, e2); camB.cam.lookAt(camB.ctl.target); }
        try { if (typeof _explodeSliderShow === 'function') _explodeSliderShow(kk); } catch (eS) {}
        if (u < 1) _asm2.anim = global.requestAnimationFrame(tick); else _asm2.anim = null;
      }
      _asm2.anim = global.requestAnimationFrame(tick);
      return _explodeK;
    }
    function explode() { return _explodeK; }
    function asmWalking() { return _asm2.k > 0; }
    /* THE WALK'S BILL. Every step is one row of its layer's bill (the same
       code in two layers is two rows already); a step with no code (the
       fitouts) takes its whole layer. Quantities are the lib's; prices come
       from the budget core when it offers costOf(code, metricQty) -- the same
       unit price and duration the budget uses -- so the strip can show what
       each step costs and what has been built so far (Marek). */
    function asmBill() {
      var S = _asm2.steps.length ? _asm2.steps : _asmCollect(), i, j, out = [], byLayer = {};
      function rowsOf(layer) {
        if (byLayer[layer]) return byLayer[layer];
        var R = [];
        try { R = _rows(layer) || []; } catch (e) { R = []; }
        byLayer[layer] = R; return R;
      }
      var assigned=null;
      if (__PROD._asmModeled) {
        assigned=S.map(function(){return [];});
        var productRows=typeof __PROD._assemblyRows==='function'?__PROD._assemblyRows():null;
        if(Array.isArray(productRows)) {
          // A product can expose its final component BOQ, including custom
          // registered layers and expanded kits that are absent from _bill.
          productRows.forEach(function(row){
            var lay=row.__layer||row.layer,target=-1,last=-1,any=-1;
            for(var si=0;si<S.length;si++) {
              var sc=S[si].code;if(CATALOG[sc]&&CATALOG[sc].code)sc=CATALOG[sc].code;
              if(sc===row.code&&any<0)any=si;
              if(S[si].layer===lay){last=si;if(sc===row.code&&target<0)target=si;}
            }
            if(target<0)target=last>=0?last:any>=0?any:S.length-1;
            if(target>=0)assigned[target].push(row);
          });
        } else {
        var lays=Object.keys(_bill || {});
        lays.forEach(function(lay){rowsOf(lay).forEach(function(row){
          var target=-1,last=-1;
          for(var si=0;si<S.length;si++) {if(S[si].layer===lay){last=si;if(S[si].code===row.code&&target<0)target=si;}}
          if(target<0)target=last;
          if(target<0&&S.length)target=S.length-1;
          if(target>=0)assigned[target].push(row);
        });});
        }
      }
      var BC = global.LevaBudgetCore, cum = { material: 0, execution: 0, minutes: 0 };
      for (i = 0; i < S.length; i++) {
        var st = S[i], R = rowsOf(st.layer), lines = [], c = st.code;
        if (c && CATALOG[c] && CATALOG[c].code) c = CATALOG[c].code;
        if(assigned) lines=assigned[i];
        else for (j = 0; j < R.length; j++) if (!c || R[j].code === c) lines.push(R[j]);
        var cost = { material: 0, execution: 0, minutes: 0 }, q;
        for (q = 0; q < lines.length; q++) {
          var ln = lines[q], one = null;
          try { if (BC && BC.costOf) one = BC.costOf(ln.code, ln.qty); } catch (eC) { one = null; }
          if (one) { cost.material += one.material || 0; cost.execution += one.execution || 0; cost.minutes += one.minutes || 0; }
        }
        cum.material += cost.material; cum.execution += cost.execution; cum.minutes += cost.minutes;
        out.push({ k: i + ASM_HEAD + 1, layer: st.layer, code: c || '', label: _asmStepLabel(st), lines: lines.map(function (r) { return { code: r.code, qty: r.qty, unit: r.unit }; }),
                   cost: cost, cumulative: { material: cum.material, execution: cum.execution, minutes: cum.minutes } });
      }
      return out;
    }
    /* what the strip listens for: which step, and the money so far */
    function _asmAnnounce() {
      try {
        var N = _asm2.steps.length + ASM_HEAD, k = _asm2.k, bill = (k > ASM_HEAD) ? asmBill() : null, row = bill ? bill[k - ASM_HEAD - 1] : null;
        global.dispatchEvent(new CustomEvent('configurator:assembly-step', { detail: {
          k: k, n: N, walking: k > 0, exploded: k === 1,
          label: k === 0 ? '' : (k === 1 ? _t('exploded_view', 'Exploded view') : (k === 2 ? _t('viz_full', 'Full build') : (row ? row.label : ''))),
          step: row || null,
          priced: !!(global.LevaBudgetCore && global.LevaBudgetCore.costOf)
        } }));
      } catch (e) {}
    }
    function asmNext() {
      if (!_asm2.steps.length) _asm2.steps = _asmCollect();
      var N = _asm2.steps.length + ASM_HEAD;
      var wasWalking = _asm2.k > 0;
      _asm2.k = (_asm2.k >= N) ? 0 : _asm2.k + 1;
      _asm2.touched = true;
      /* entering or leaving the walk changes what the base draws (whole
         footings), so the model is rebuilt at those two moments */
      if ((_asm2.k > 0) !== wasWalking) { try { rebuild(); } catch (eRb) {} }
      /* the view the walk was opened in, which step 2 returns to */
      if (_asm2.k === 1) {
        _asm2.camHome = null;
        try {
          var stH = _stage(), cH = stH && stH.camera, kH = stH && (stH.persControls || stH.controls);
          if (cH && kH && kH.target && cH.isPerspectiveCamera) _asm2.camHome = { pos: cH.position.clone(), tg: kH.target.clone() };
        } catch (eCH) { _asm2.camHome = null; }
      }
      _asmApply(_asm2.k > 1);
      /* step 1 IS the exploded view: that is where a part can be picked, and
         where what has no supplier shows itself */
      try { _pickEnable(_asm2.k === 1); } catch (ePk) {}
      try { if (_asm2.k === 1) _cyanOn(); else _cyanOff(); } catch (eCy) {}
      _asmCounter();
      _asmAnnounce();
      var s = _asm2.k > ASM_HEAD ? _asm2.steps[_asm2.k - ASM_HEAD - 1] : null, lb = s ? _asmStepLabel(s) : '';
      _log('assembly ' + (_asm2.k === 0 ? ('0/' + N + ' -- the whole building') : _asm2.k === 1 ? ('1/' + N + ' exploded view') :
           _asm2.k === 2 ? ('2/' + N + ' back together, camera back to the opening view' + (_asm2.camHome ? '' : ' (no 3D camera kept, the view stays)')) :
           (_asm2.k + '/' + N + ' ' + lb + ' [' + _asmNameFrom + '] (' + s.meshes.length + ' bodies, ' + s.dir + ', ' + s.layer + ')')));
      return _asm2.k;
    }

    /* ---------- FOR THE BLUEPRINTS PAGE (core-v73) ----------------------------
       A layer can be shown, dimmed or hidden by name; the whole project can be
       handed over in one object; and the dimension anchors -- the numbers a
       drawing wants to write -- are listed so the page draws them without
       knowing the geometry. */
    var _layerModes = {};
    function setLayerMode(id, mode) {
      _layerModes[id] = mode || 'on';
      _applyLayerModes();
    }
    function layerModes() { return _layerModes; }
    function _applyLayerModes() {
      var st = _stage(); if (!st || !st.group) return;
      st.group.traverse(function (o) {
        if (!o.userData || !o.userData.layer) return;
        var mode = _layerModes[o.userData.layer] || 'on';
        o.visible = (mode !== 'off');
        o.traverse(function (m) {
          if (!m.isMesh || !m.material) return;
          var mats = Array.isArray(m.material) ? m.material : [m.material], k;
          for (k = 0; k < mats.length; k++) {
            var mt = mats[k]; if (!mt) continue;
            if (mode === 'dim') {
              if (mt.userData.__dim === undefined) mt.userData.__dim = { t: mt.transparent, o: mt.opacity, c: mt.color ? mt.color.getHex() : null };
              mt.transparent = true; mt.opacity = 0.18;
              if (mt.color) mt.color.setHex(0x9AA0A6);
              mt.needsUpdate = true;
            } else if (mt.userData.__dim !== undefined) {
              mt.transparent = mt.userData.__dim.t; mt.opacity = mt.userData.__dim.o;
              if (mt.color && mt.userData.__dim.c != null) mt.color.setHex(mt.userData.__dim.c);
              delete mt.userData.__dim; mt.needsUpdate = true;
            }
          }
        });
      });
    }
    function blueprintPackage() {
      var i, layers = [];
      for (i = 0; i < _layers.length; i++) {
        /* core-v725: the layer's name in the page's language (the assembly's
           own map, ASM_LABELS), so the drawings page lists layers by name */
        var lbl = ''; try { lbl = _asmLayerLabel(_layers[i].id); } catch (eLb) { lbl = ''; }
        layers.push({ id: _layers[i].id, role: _layers[i].role || '', version: _layers[i].version || '', label: lbl });
      }
      return { product: PRODUCT, version: __PROD.VERSION, core: CORE_VERSION, units: STATE.isMetric ? 'metric' : 'imperial',
               payload: payload(), layers: layers };
    }
    function dimAnchors() {
      var B = _buildRect(), SEG = segments(), S = storeys()[0], i, j;
      var out = { rect: { x0: B.x0, x1: B.x1, z0: B.z0, z1: B.z1 }, wallThk: wallThickness(),
                  floor: S.y0, wallTop: wallTop(), openings: [], partitions: [], roof: {} };
      for (i = 0; i < SEG.length; i++) {
        var sg = SEG[i], ux = (sg.x1 - sg.x0) / sg.len, uz = (sg.z1 - sg.z0) / sg.len;
        for (j = 0; j < (sg.openings || []).length; j++) {
          var o = sg.openings[j];
          out.openings.push({ seg: i, x: sg.x0 + ux * o.at, z: sg.z0 + uz * o.at, at: o.at, w: o.w, h: o.h, sill: o.sill || 0,
                              kind: (o.sill || 0) > 0.05 ? 'window' : 'door', along: Math.abs(ux) > 0.5 ? 'x' : 'z' });
        }
      }
      var parts = STATE.parts || [];
      for (i = 0; i < parts.length; i++) {
        var p = parts[i], spn = _partSpanH ? _partSpanH(p) : null;
        out.partitions.push({ dir: p.dir, at: p.at, a: spn ? spn[0] : Math.min(p.a, p.b), b: spn ? spn[1] : Math.max(p.a, p.b) });
      }
      try {
        var R = roofPlanes(), top = -1e9, eave = 1e9;
        for (i = 0; i < R.length; i++) {
          var P = R[i].pts || [];
          for (j = 0; j < P.length; j++) { if (P[j][1] > top) top = P[j][1]; if (P[j][1] < eave) eave = P[j][1]; }
        }
        out.roof = { type: STATE.roof.type, pitch: STATE.roof.pitch, top: top, eave: eave };
      } catch (eR) {}
      return out;
    }

    function payload() {
      return {
        v: __PROD.VERSION,
        origin: STATE.origin, footprint: STATE.footprint, polygon: STATE.polygon,
        /* the wing goes with the project, or a shared T comes back as a box
           with the default wing stuck on it (core-v311) */
        wing: STATE.wing,
        storeys: STATE.storeys, clearH: STATE.clearH, roof: STATE.roof,
        holes: STATE.holes,
        planStorey: STATE.planStorey,
        /* the floor-level slider's value goes with the project, or a shared
           house comes back on its default floor (core-v336) */
        floorLevel: STATE.floorLevel,
        openings: STATE.openings, segH: STATE.segH, parts: STATE.parts, doors: STATE.doors,
        roomNames: STATE.roomNames, isMetric: STATE.isMetric,
        /* THE VIZ STAGE IS NOT SAVED. It is a way of looking at the model for a
           moment, not a setting -- coming back to a half-hidden building days
           later reads as a broken page, and it was how a reload landed straight
           in the panel view. Every load starts on the whole building. */
        climateZone: STATE.climateZone, mapLocked: STATE.mapLocked,
        foundation: STATE.foundation, facadeThk: STATE.facadeThk, scenery: STATE.scenery, trees: STATE.trees || {},
        colorView: STATE.colorView,
        flatCeiling: STATE.flatCeiling,

        /* THE COVERING. payload() is a LIST, not a copy of the state, so a field
           that is not named here is saved by nobody -- and loadPref only restores
           keys it finds, so every rebuild handed the reader the default.

           The picker wrote the right value into the right object and the next
           frame threw it away. Nothing logged, nothing threw, and the battens
           simply were not there, which is why this took so long to find.

           Declaring it in STATE was necessary and not sufficient: a field has to
           be in the state AND in the payload, and I only did the first. */
        covering: STATE.covering,
        /* AND THE CLADDING, for the same reason spelled out above it: in the
           state and NOT in the payload is a setting that is thrown away on the
           next frame, which is the fault the covering already taught us once. */
        cladding: STATE.cladding,
        /* the walls between the posts, named here for exactly that reason --
           the lesson above was paid for twice already */
        postWall: STATE.postWall,
        blinds: STATE.blinds,
        product: STATE.product || {},
        /* the product's NAME, for the page that has to pick the lib from a
           stored project (product above is the settings bag) */
        productName: PRODUCT,
        bodyVisible: STATE.bodyVisible, bodyX: STATE.bodyX, bodyZ: STATE.bodyZ
      };
    }

    /* THE PROJECT TRAVELS WHOLE. The prefs lived in localStorage only, so a
       shared link opened on another device came up with the defaults -- the
       floorplan travelled (the asset writes it to the project store), the
       house itself did not. The same payload now goes to the project store
       under 'house-config' whenever it is saved, and on boot the store's copy
       wins over localStorage when it is there and newer; the store is what the
       share link and the user's Firestore project are made of (Marek: colours,
       fitouts, everything). The store initialises after boot, so a late arrival
       is picked up by a short watch and applied with a rebuild. */
    var STORE_KEY = 'house-config';
    function _store() {
      var S = global._ProjectStore;
      return (S && S._initialized && typeof S.writeKey === 'function' && typeof S.read === 'function') ? S : null;
    }
    function _storeWrite() {
      var S = _store(); if (!S) return false;
      try {
        S.writeKey(STORE_KEY, { v: 1, product: PRODUCT, updatedAt: new Date().toISOString(), payload: payload() });
        return true;
      } catch (e) { _log('project store write failed: ' + (e && e.message)); return false; }
    }
    function _applyPayload(p, from) {
      var k;
      for (k in p) { if (k !== 'v' && p.hasOwnProperty(k) && STATE.hasOwnProperty(k)) STATE[k] = p[k]; }
      if (STATE.foundation === 'slab' && (typeof FOUNDATIONS === 'undefined' || FOUNDATIONS.indexOf('slab') < 0)) STATE.foundation = 'strips';
      try { if (typeof _stackForFooting === 'function') _stackForFooting(); } catch (eSF) {}
      /* a loaded configuration is taken as it is: the next build must not read
         its new outline as "the walls moved" and push the fitouts about (Marek:
         the shared link came up with the pieces elsewhere) */
      _fitGeomSig = '';
      _log('prefs restored from ' + from + ' (' + (p.v || '?') + ')');
    }
    /* THE LINK IS THE PROJECT. The share sheet and the browser's own copy-link
       both take location.href, and the store the floorplan trace writes to is
       not on this page -- so a link carried the product and nothing else. The
       whole payload now rides in the URL hash (#cfg=, base64 of the JSON, a
       few KB) and is rewritten on every save; a page opened on that link reads
       it first, before localStorage, and keeps it (Marek: shape, openings,
       partitions, fitouts, colours -- everything the user has). */
    function _b64u(s) {
      try { return global.btoa(unescape(encodeURIComponent(s))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, ''); }
      catch (e) { return ''; }
    }
    function _b64uDec(s) {
      try {
        s = String(s || '').replace(/-/g, '+').replace(/_/g, '/');
        while (s.length % 4) s += '=';
        return decodeURIComponent(escape(global.atob(s)));
      } catch (e) { return ''; }
    }
    /* SHORTER: the JSON is rounded to millimetres, stripped of what the loader
       ignores, and deflated (CompressionStream, which every current Chrome has)
       before the base64 -- #c= is five to eight times shorter than the plain
       #cfg= it replaces; the plain form is still read, and written only where
       the browser cannot deflate (Marek: shorten the link, a lot). */
    function _slim(p) {
      function r3(v) { return (typeof v === 'number') ? Math.round(v * 1000) / 1000 : v; }
      function walk(o) {
        if (Array.isArray(o)) return o.map(walk);
        if (o && typeof o === 'object') { var out = {}, k; for (k in o) if (o.hasOwnProperty(k)) out[k] = walk(o[k]); return out; }
        return r3(o);
      }
      var q = walk(p);
      delete q.v;
      return q;
    }
    function _bytesToB64u(u8) {
      var s = '', i;
      for (i = 0; i < u8.length; i++) s += String.fromCharCode(u8[i]);
      return global.btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
    }
    function _b64uToBytes(s) {
      s = String(s || '').replace(/-/g, '+').replace(/_/g, '/');
      while (s.length % 4) s += '=';
      var bin = global.atob(s), u8 = new Uint8Array(bin.length), i;
      for (i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
      return u8;
    }
    function _deflate(txt) {
      if (typeof global.CompressionStream !== 'function') return Promise.resolve(null);
      try {
        var cs = new global.CompressionStream('deflate-raw');
        var w = cs.writable.getWriter();
        w.write(new global.TextEncoder().encode(txt)); w.close();
        return new global.Response(cs.readable).arrayBuffer().then(function (ab) { return new Uint8Array(ab); });
      } catch (e) { return Promise.resolve(null); }
    }
    function _inflate(u8) {
      if (typeof global.DecompressionStream !== 'function') return Promise.resolve(null);
      try {
        var ds = new global.DecompressionStream('deflate-raw');
        var w = ds.writable.getWriter();
        w.write(u8); w.close();
        return new global.Response(ds.readable).text();
      } catch (e) { return Promise.resolve(null); }
    }
    /* THE SHORT LINK. The configuration is put on leva-share (a KV worker) and
       the link carries only ?p=<id>: eight characters instead of a hash of
       hundreds. Written on every save, but only when the configuration has
       actually changed since the last write and not more than once a second
       and a half; the deflated hash stays as the fallback when the worker is
       out of reach. On boot ?p= is fetched first. */
    var SHARE_WORKER_FALLBACK = 'https://leva-share.long-cloud-d006.workers.dev';
    function _shareWorker() {
      var B = null;
      try { B = BOOTDATA(); } catch (e) { B = null; }
      return (B && B.shareWorkerUrl) || SHARE_WORKER_FALLBACK;
    }
    var _shareTimer = 0, _shareLast = '', _shareId = '';
    /* NOTHING IS WRITTEN WHILE THE PAGE OPENS (core-v608, Marek: proc nam
       tolik hori KV -- the account has 1 000 writes a day on the free plan).
       Every save during boot asked for a short link, and the first one went
       out before the ?p= of the address had come back (the boot builds on
       the local copy meanwhile), so every page opened from a link wrote a
       new one without a single change; a first visit on the product's
       default project wrote one too. Now the writes wait until the boot is
       done and the project's own loads (?p=, the default project, #c=) are
       in; then a page opened on ?p= or on the default project takes what is
       on screen as already written and writes only after a real change. A
       page on its own copy (local, store, #c=) writes once, as before, so
       its address has a ?p= to share. */
    var _shareHeld = true, _shareDef = '', _hashWait = false, _shareBooted = false, _shareN = 0;
    function _shareRelease(why) {
      if (!_shareHeld) return;
      /* core-v732: the homepage's frame (?ig&autoplay) writes no short link
         and keeps its address -- it is a presentation, not a project */
      if (_tourAuto()) { _log('share    ?ig&autoplay: no short link from this frame (core-v732)'); return; }
      _shareHeld = false;
      var txt = '';
      try { txt = JSON.stringify(_slim(payload())); } catch (e) { txt = ''; }
      if (_shareId) {
        _shareLast = txt;
        _log('share    opened on ?p=' + _shareId + ' -- no new short link until something changes (core-v608, ' + why + ')');
        return;
      }
      if (_shareDef) {
        _shareId = _shareDef; _shareLast = txt;
        _setQueryP(_shareDef);
        _log('share    opened on the default project ' + _shareDef + ' -- the address points at it, no new short link until something changes (core-v608, ' + why + ')');
        return;
      }
      _log('share    opened on its own copy (local / store / #c) -- one short link written, as before (core-v608, ' + why + ')');
      _shareWrite();
    }
    function _shareHoldWatch() {
      var t0 = Date.now();
      (function tick() {
        if (!_shareHeld) return;
        if (_shareBooted && !_initLoads && !_hashWait) { _shareRelease('boot done, the loads are in'); return; }
        /* never held for good: a boot that never says done still writes */
        if (Date.now() - t0 > 20000) { _shareRelease('20 s -- ' + (_shareBooted ? '' : 'no boot done, ') + (_initLoads ? (_initLoads + ' load(s) still out') : 'loads in')); return; }
        global.setTimeout(tick, 250);
      })();
    }
    function _setQueryP(id) {
      try {
        var u = new URL(global.location.href);
        if (u.searchParams.get('p') === id) return;
        u.searchParams.set('p', id);
        u.hash = '';
        if (global.history && global.history.replaceState) global.history.replaceState(null, '', u.toString());
      } catch (e) {}
    }
    function _shareWrite() {
      if (_shareHeld) return;
      if (_shareTimer) { try { global.clearTimeout(_shareTimer); } catch (e0) {} }
      _shareTimer = global.setTimeout(function () {
        _shareTimer = 0;
        var txt;
        try { txt = JSON.stringify(_slim(payload())); } catch (e) { return; }
        if (txt === _shareLast) return;
        var mine = txt;
        global.fetch(_shareWorker() + '/s', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: txt })
          .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
          .then(function (j) {
            if (!j || !j.id) throw new Error('no id');
            _shareLast = mine; _shareId = j.id;
            _setQueryP(j.id);
            _shareN++;
            _log('share    short link ?p=' + j.id + ' written -- ' + _shareN + ' this page (core-v608)');
            /* the diary of projects: the newest id of this product first, so
               the drawings page can pick up the latest state on Update */
            try {
              var d = JSON.parse(global.localStorage.getItem('leva-projects') || '[]');
              d = d.filter(function (e) { return e.id !== j.id; });
              d.unshift({ id: j.id, product: PRODUCT, label: PRODUCT.toUpperCase() + ' ' + STATE.footprint.w + ' x ' + STATE.footprint.l, at: new Date().toISOString() });
              global.localStorage.setItem('leva-projects', JSON.stringify(d.slice(0, 30)));
            } catch (eD) {}
          })
          .catch(function (err) {
            _log('share worker unavailable (' + (err && err.message) + ') -- the link keeps the deflated hash');
            _hashWrite();
          });
      }, 1500);
    }
    /* the project's short id, made now when it does not exist yet */
    function _shareEnsure() {
      if (_shareId) return Promise.resolve(_shareId);
      var txt;
      try { txt = JSON.stringify(_slim(payload())); } catch (e) { return Promise.reject(e); }
      return global.fetch(_shareWorker() + '/s', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: txt })
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (j) { if (!j || !j.id) throw new Error('no id'); _shareLast = txt; _shareId = j.id; _setQueryP(j.id); return j.id; });
    }
    /* A SHARED PROJECT OPENED IN PLACE (core-v578, Marek: kdyz kliknu na
       znacku v mape, tak se v konfiguratoru ma nacist dany projekt -- plati
       napric vsemi konfiguratory). The map strip finds the page's loader by
       an object with applyConfigId; the house line had none, so a pin of a
       house-line project could only send the visitor to a new page. The
       project comes from leva-share by its id and is applied exactly as
       ?p= is on boot: one Undo step, the address and the local copy follow
       it, the next change saves under a new id as always. */
    function applyConfigId(id) {
      id = String(id || '');
      if (!/^[a-z0-9]{6,12}$/.test(id)) return Promise.reject(new Error('not a project id: ' + id));
      return global.fetch(_shareWorker() + '/s/' + id)
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (p) {
          if (!p || !p.footprint) throw new Error('the answer holds no project');
          try { undoPush(); } catch (eU) {}
          _applyPayload(p, 'a shared project opened in place (' + id + ')');
          _shareId = id; _shareLast = JSON.stringify(_slim(p));
          _setQueryP(id);
          try { global.localStorage.setItem(_prefKey, JSON.stringify(payload())); } catch (eL) {}
          try { rebuild(); drawPlan(); } catch (eR) { _log('shared project ' + id + ': the rebuild THREW ' + ((eR && eR.message) || eR)); }
          _log('shared project ' + id + ' opened in place (core-v578)');
          return id;
        })['catch'](function (err) { _log('shared project ' + id + ' NOT opened: ' + ((err && err.message) || err)); throw err; });
    }
    /* the drawings page of a project, in the page's language */
    function _bpUrl(id) {
      var B = null; try { B = BOOTDATA(); } catch (e) {}
      /* core-v568: with no address from the page, the drawings page in the
         page's language too */
      var bRoot = '/';
      try { bRoot = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/'; } catch (eBR) { bRoot = '/'; }
      if (bRoot.charAt(bRoot.length - 1) !== '/') bRoot += '/';
      var base = (B && B.blueprintsUrl) || (bRoot + 'pages/blueprints');
      return base + (base.indexOf('?') >= 0 ? '&' : '?') + 'p=' + id;
    }
    function openBlueprints() {
      _shareEnsure().then(function (id) {
        var url = _bpUrl(id);
        _log('blueprints -> ' + url);
        try { global.open(url, '_blank'); } catch (e2) { global.location.href = url; }
      }).catch(function (err) { _log('blueprints: could not save the project first (' + (err && err.message) + ')'); });
    }
    /* =========================================================================
       THE WHOLE PACKAGE ON THE DRAWINGS PAGE (core-v731, blueprints thread,
       Marek: blueprints se bude automaticky otevirat po druhem zmacknuti pdf
       tlacitka a vygeneruje se na strance komplet to, co je ted v pdf, prave
       s blueprints; prvni strana zustane product image, na druhou stranu
       seznam stran).

       The paid second tap no longer saves the PDF here for a product the
       drawings page can draw. The document's arguments -- the bill priced by
       the budget on this page, the cut list, the shopping list, the cover,
       the chosen views -- are kept in this browser (indexedDB leva-pkg, by
       the project's short id, a month at most) and the drawings page of the
       project opens; it makes the document from them with its sheets in
       between and offers the one PDF. Anything in the way and the PDF is
       made here as before, so a paid tap never ends with nothing.
       HOUSE_BOOT.pdfPackage = false keeps a page on the old way.
       ========================================================================= */
    /* core-v761 (Marek 2026-10-27: PDF u vsech aktivnich projektu stejne):
       BUNKER and HOUSEBOAT too (the drawings page draws them) */
    var PKG_SLUGS = { adu: 1, shed: 1, sauna: 1, ranch: 1, aframe: 1, barndo: 1, garageapt: 1, thow: 1, thouse: 1, bunker: 1, houseboat: 1, taurus: 1, gardenbar: 1, cabin: 1, twostory: 1, ahengine: 1 };   /* core-v769: + TAURUS, GARDEN BAR; core-v774: + CABIN, TWO STORY, ANY HOUSE ENGINE */
    /* '' when the package goes to the drawings page, else why not */
    function _pdfToBlueprints() {
      var B = null; try { B = BOOTDATA() || {}; } catch (e) { B = {}; }
      if (B.pdfPackage === false) return 'HOUSE_BOOT.pdfPackage = false';
      var slug = String(PRODUCT || B.slug || '');
      if (!PKG_SLUGS[slug]) return 'no drawings page for ' + (slug || 'this product');
      if (typeof _pdfExport === 'function' && _pdfExport !== _pdfDefaultExport) return 'the product makes its own PDF';
      /* core-v769: the original documentation goes with the package (prependPdf) */
      if (!global.indexedDB) return 'no indexedDB in this browser';
      return '';
    }
    function _pkgPut(id, rec) {
      return new Promise(function (ok, no) {
        var rq;
        try { rq = global.indexedDB.open('leva-pkg', 1); } catch (e) { no(e); return; }
        rq.onupgradeneeded = function () { try { rq.result.createObjectStore('pkg'); } catch (eU) {} };
        rq.onerror = function () { no(rq.error || new Error('indexedDB did not open')); };
        rq.onsuccess = function () {
          var db = rq.result, tx;
          try { tx = db.transaction('pkg', 'readwrite'); } catch (eT) { db.close(); no(eT); return; }
          var st = tx.objectStore('pkg'), old = Date.now() - 30 * 86400000;
          st.put(rec, id);
          /* a package older than a month goes */
          try {
            var cr = st.openCursor();
            cr.onsuccess = function () { var c = cr.result; if (!c) return; if (c.value && c.value.at < old) c['delete'](); c['continue'](); };
          } catch (eC) {}
          tx.oncomplete = function () { db.close(); ok(); };
          tx.onerror = tx.onabort = function () { db.close(); no(tx.error || new Error('indexedDB write failed')); };
        };
      });
    }
    /* the PDF made here, paid first -- the way back when the package fails */
    function _pdfPaidRun() {
      if (_pdfRoyalty()) { _log('pdf: royalty product -- made here, the purchase gate decides (core-v758)'); _pdfRun(); return; }
      _pdfPay(function () {
        try { if (global.ConfiguratorPdfExporter) global.ConfiguratorPdfExporter.__levaPaidAt = Date.now(); } catch (eM) {}
        _pdfRun();
      });
    }
    /* core-v769: an author's royalty product not bought here -- the drawings
       page offers its purchase (the firewood rack's words, rack-v10-docs) */
    function _pkgBuy() {
      if (!_pdfRoyalty() || _pdfOwner()) return null;
      var B = null; try { B = BOOTDATA() || {}; } catch (e) { B = {}; }
      var vid = ''; try { vid = new URL(global.location.href).searchParams.get('variant') || ''; } catch (eV) { vid = ''; }
      if (!vid) { try { vid = String(((B.variants || [])[0] || {}).id || ''); } catch (eV2) { vid = ''; } }
      var root = '/'; try { root = (global.Shopify && global.Shopify.routes && global.Shopify.routes.root) || '/'; } catch (eR) { root = '/'; }
      if (root.charAt(root.length - 1) !== '/') root += '/';
      return { text: _t('pdfPaywallMsg', 'To download the PDF documentation, please purchase this product first.'), btn: _t('addToCart', 'Add to Cart'),
               url: global.location.origin + root + (vid ? ('cart/add?id=' + vid + '&quantity=1') : ('products/' + String(B.productHandle || ''))) };
    }
    /* the package kept for the drawings page under the project's id -- the
       PDF tap and the drawings page's own request (core-v767) alike */
    function _pkgKeep(args) {
      var B = null; try { B = BOOTDATA() || {}; } catch (e) { B = {}; }
      args.meta = args.meta || {};
      /* the exporter reads the product and its variant off the address; the
         drawings page has neither */
      try { var mH = /\/products\/([^\/?#]+)/.exec(global.location.pathname); if (mH && !args.meta.productHandle) args.meta.productHandle = mH[1]; } catch (eH) {}
      try { var vid = new URL(global.location.href).searchParams.get('variant'); if (vid) args.meta.variantId = vid; } catch (eV) {}
      var t0 = Date.now();
      /* core-v769: the product's own documentation in front (TAURUS: Matt's
         TAURUS II drawings for its buyers and the admin) -- the drawings page
         puts its pages first, as the PDF made here did */
      var pre = null;
      try { if (typeof __PROD._pdfOriginal === 'function') pre = __PROD._pdfOriginal(); } catch (eO) { pre = null; }
      return Promise.resolve(pre).then(function (P) {
        if (P && P.url) { args.prependPdf = { url: P.url, label: P.label || '' }; _log('pdf package: the original in front -- ' + (P.label || '') + (P.why ? (' (' + P.why + ')') : '')); }
        else if (pre) _log('pdf package: no original in front' + ((P && P.why) ? (' -- ' + P.why) : ''));
      }, function (eP) { _log('pdf package: the original was not found -- ' + ((eP && eP.message) || eP)); }).then(function () {
        return _shareEnsure();
      }).then(function (id) {
        args.shareUrl = global.location.origin + global.location.pathname + '?p=' + id;
        var txt = JSON.stringify(args), clean = JSON.parse(txt);
        /* core-v733: the design's token key (the same configuration again is
           free) and whether this buyer owns the project (no tokens) go with it */
        var key = ''; try { key = _pdfKey(); } catch (eK) { key = ''; }
        return _pkgPut(id, { v: 2, at: Date.now(), id: id, slug: String(PRODUCT || B.slug || ''), core: CORE_VERSION, key: key, owner: _pdfOwner(), buy: _pkgBuy(), args: clean }).then(function () {
          _log('pdf package: ' + Math.round(txt.length / 1024) + ' kB kept for the drawings page under ' + id + ' (' + (Date.now() - t0) + ' ms)');
          return id;
        });
      });
    }
    function _pdfPackage() {
      var args;
      try { args = _pdfArgs(); } catch (eA) { _log('pdf package: the arguments THREW ' + ((eA && eA.message) || eA) + ' -- the PDF made here instead (paid first)'); _pdfPaidRun(); return; }
      _pkgKeep(args).then(function (id) {
        var url = _bpUrl(id), w = null;
        try { w = global.open(url, '_blank'); } catch (eO) { w = null; }
        if (w) { _log('pdf package: the drawings page opened -- ' + url); return; }
        /* the browser held the new window back (the payment came between the
           tap and the window): one tap more, on the button in the bubble */
        var lab = ''; try { lab = ($('cf-btn-pdf') && $('cf-btn-pdf').getAttribute('title')) || ''; } catch (eT) {}
        var go = function () {
          var w2 = null; try { w2 = global.open(url, '_blank'); } catch (e2) { w2 = null; }
          if (!w2) global.location.href = url;
          _log('pdf package: the drawings page ' + (w2 ? 'opened from the bubble' : 'opened in this tab') + ' -- ' + url);
        };
        if (_cmd && _cmd.bub) {
          _log('pdf package: the new window was blocked -- one more tap in the bubble');
          _cmdBubble('', [{ t: lab || 'PDF', pri: true, fn: function () { _cmdBubble(null); go(); } }, _pdfCancelBtn()]);
        } else { _log('pdf package: the new window was blocked and no bubble -- this tab goes'); global.location.href = url; }
      }).catch(function (err) {
        _log('pdf package FAILED ' + ((err && err.message) || err) + ' -- the PDF made here instead (paid first)');
        _pdfPaidRun();
      });
    }
    /* THE DRAWINGS PAGE ASKS FOR ITS PACKAGE (core-v767, blueprints thread,
       Marek 2026-10-29: chci vizualizaci kompletniho pdf na blueprints - i to
       co jsme meli v puvodnim PDF - main page s product picture, cut lists,
       shopping lists, BOQ; blueprints jsou pak rozsireni). The package is made
       on this page -- its catalogue, its prices, its product image -- so a
       link to the drawings opened in a browser that never tapped PDF had the
       drawings alone. The drawings page (blueprints-v37) sends that visitor
       here with ?p=<id>&bppkg=1: the page covers itself at once ("Making the
       PDF..."), and once the project is in, the catalogue is loaded and the
       bill has prices (or 20 s after the catalogue; 60 s at most) it keeps
       the very package the PDF tap keeps (_pkgKeep -- no tokens, the lock
       on the drawings page decides) and goes back to the drawings with
       &bpback=1, so a failure never sends it round again. */
    function _bpAskId() {
      try { var u = new URL(global.location.href); return u.searchParams.get('bppkg') === '1' ? String(u.searchParams.get('p') || '') : ''; } catch (e) { return ''; }
    }
    function _bpCover() {
      if (!_bpAskId()) return;
      try {
        var d = global.document, o = d.getElementById('cf-bp-cover');
        if (o || !d.body) return;
        var I = (global.HOUSE_BOOT && global.HOUSE_BOOT.i18n) || {};
        o = d.createElement('div'); o.id = 'cf-bp-cover';
        o.style.cssText = 'position:fixed;inset:0;z-index:2147483646;display:flex;align-items:center;justify-content:center;background:#f5f5f5;font:700 18px Montserrat,sans-serif;color:#1A1C1D;text-align:center;padding:20px;';
        o.textContent = String(I.bpMakingPdf || 'Making the PDF...');
        d.body.appendChild(o);
      } catch (e) {}
    }
    function _bpAutoRun() {
      var id = _bpAskId();
      if (!id) return;
      _bpCover();
      var t0 = Date.now(), tCat = 0, tSet = 0, done = false;
      var back = function (why, nid) {
        if (done) return; done = true;
        _log('pdf package for the drawings page: ' + why + ' -- back to the drawings (core-v767)');
        try { global.location.replace(_bpUrl(nid || id) + '&bpback=1'); } catch (eR) {}
      };
      var no = ''; try { no = _pdfToBlueprints(); } catch (eN) { no = 'THREW ' + ((eN && eN.message) || eN); }
      if (!/^[a-z0-9]{6,12}$/.test(id) || no) { back('no package from here (' + (no || 'no project id') + ')'); return; }
      (function tick() {
        if (done) return;
        var now = Date.now(), why = '';
        try {
          if (_shareHeld) why = 'the project not in yet';
          else if (!_catRows() && now - t0 < 30000) why = 'the catalogue not in yet';
          else {
            if (!tCat) tCat = now;
            var rows = _pdfBoq('main'), pr = 0, i;
            for (i = 0; i < rows.length; i++) if (rows[i].total > 0) pr++;
            if (!pr && now - tCat < 20000) why = 'no prices yet (' + rows.length + ' bill rows)';
            else if (!tSet) { tSet = now; why = 'settling (' + pr + ' of ' + rows.length + ' priced)'; }
            else if (now - tSet < 1500) why = 'settling';
          }
        } catch (eT) { why = ''; _log('pdf package for the drawings page: the check THREW ' + ((eT && eT.message) || eT)); }
        if (why && now - t0 < 60000) { global.setTimeout(tick, 700); return; }
        if (why) _log('pdf package for the drawings page: 60 s -- ' + why + ' -- made as it stands');
        var args;
        try { args = _pdfArgs(); } catch (eA) { back('the arguments THREW ' + ((eA && eA.message) || eA)); return; }
        _pkgKeep(args).then(function (nid) { back('kept in ' + Math.round((Date.now() - t0) / 1000) + ' s', nid); },
                            function (err) { back('NOT kept -- ' + ((err && err.message) || err)); });
      })();
    }
    try { _bpCover(); } catch (eBc) {}
    /* THE MODEL A PRODUCT OPENS ON.

       A bare product page used to come up on whatever the defaults happen to
       be, which is a box. A product can name a saved project instead and the
       page opens on that -- the same short id the share link carries, so it is
       set by configuring the model, sharing it, and pasting the id (Marek).

       It only applies when the URL names nothing: a link with ?p= or a hash is
       somebody's own project and always wins. And if the worker cannot be
       reached the page still comes up, on the plain defaults, because a
       configurator that will not load without the network is worse than one
       that opens on a box. */
    var _defaultShare = '';
    function setDefaultModel(id) {
      _defaultShare = (/^[a-z0-9]{6,12}$/.test(String(id || ''))) ? String(id) : '';
      return _defaultShare;
    }

    function _shareRead() {
      var id = null, own = false;
      try { id = new URL(global.location.href).searchParams.get('p'); } catch (e) { id = null; }
      own = !!id;
      if (!id) {
        /* a hash is a project too, and it is read elsewhere -- do not step on it */
        var h = '';
        try { h = String(global.location.hash || ''); } catch (eH) {}
        if (h.indexOf('#c=') === 0 || h.indexOf('#cfg=') === 0) return Promise.resolve(null);
        id = _defaultShare;
        if (id) _log('opening on the product default model ' + id);
      }
      if (!id || !/^[a-z0-9]{6,12}$/.test(id)) return Promise.resolve(null);
      return global.fetch(_shareWorker() + '/s/' + id)
        .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
        .then(function (p) { if (p && p.footprint) { _shareId = id; _shareLast = JSON.stringify(_slim(p)); return p; } return null; })
        .catch(function (err) { _log('shared project ' + id + ' not found: ' + (err && err.message)); return null; });
    }
    var _hashTimer = 0;
    function _setHash(h) {
      if (global.location.hash === h) return;
      if (global.history && global.history.replaceState) {
        global.history.replaceState(null, '', global.location.pathname + global.location.search + h);
      } else global.location.hash = h;
    }
    function _hashWrite() {
      if (_hashTimer) { try { global.clearTimeout(_hashTimer); } catch (e0) {} }
      _hashTimer = global.setTimeout(function () {
        _hashTimer = 0;
        try {
          var txt = JSON.stringify(_slim(payload()));
          _deflate(txt).then(function (u8) {
            if (u8 && u8.length) _setHash('#c=' + _bytesToB64u(u8));
            else { var enc = _b64u(txt); if (enc) _setHash('#cfg=' + enc); }
          });
        } catch (e) {}
      }, 300);
    }
    /* the plain form reads at once; the deflated one comes back through a
       promise and is applied with a rebuild when it lands */
    function _hashRead() {
      try {
        var m = /[#&]cfg=([^&]+)/.exec(global.location.hash || '');
        if (!m) return null;
        var txt = _b64uDec(m[1]);
        if (!txt) return null;
        var p = JSON.parse(txt);
        return (p && p.footprint) ? p : null;
      } catch (e) { _log('link config unreadable: ' + (e && e.message)); return null; }
    }
    function _hashHasDeflated() { return /[#&]c=([^&]+)/.test(global.location.hash || ''); }
    function _hashReadDeflated() {
      var m = /[#&]c=([^&]+)/.exec(global.location.hash || '');
      if (!m) return Promise.resolve(null);
      var u8;
      try { u8 = _b64uToBytes(m[1]); } catch (e) { return Promise.resolve(null); }
      return _inflate(u8).then(function (txt) {
        try { var p = JSON.parse(txt || ''); return (p && p.footprint) ? p : null; } catch (e2) { return null; }
      });
    }
    /* WHAT A SAVE COSTS (core-v589): count, last, slowest -- for the perf lines */
    var _pfS = { n: 0, last: 0, max: 0, soon: 0 };
    function savePref() {
      var a = 0;
      try { a = (global.performance && global.performance.now) ? global.performance.now() : Date.now(); } catch (e0) {}
      try { global.localStorage.setItem(_prefKey, JSON.stringify(payload())); } catch (e) {}
      _storeWrite();
      _shareWrite();
      try {
        var ms = ((global.performance && global.performance.now) ? global.performance.now() : Date.now()) - a;
        _pfS.n++; _pfS.last = ms; if (ms > _pfS.max) _pfS.max = ms;
      } catch (e1) {}
    }
    /* A SAVE AFTER THE MOVE, NOT ONE A MOVE (core-v589). The map reports every
       step of a pan or a pinch as a change, and each one wrote the whole
       project twice into the browser's storage while the fingers were still
       moving. Half a second after the last one it is written once. */
    var _spSoon = 0;
    function _savePrefSoon() {
      _pfS.soon++;
      if (_spSoon) { try { global.clearTimeout(_spSoon); } catch (e) {} }
      _spSoon = global.setTimeout(function () { _spSoon = 0; savePref(); }, 500);
    }

    /* THE PERF LINES (core-v589, Marek: chtelo by to zamakat na optimalizaci,
       rychlem nacitani a plynule praci). Numbers from the device the snapshot
       is sent from, so every step of the optimisation is judged on the tablet
       and not guessed: what the page loaded and when, what a build and each
       layer cost, how long frames take standing still and under a finger, how
       much the GPU is asked to draw, what a plan paint and a save cost, and how
       often the map under the model repaints. */
    function _perfLines() {
      var out = [], st = _stage(), rn = st && st.renderer, i, k;
      function r0(x) { return Math.round(x || 0); }
      function kb(b) { return Math.round((b || 0) / 1024); }
      function stats(L, j) {
        if (!L || !L.length) return null;
        var a = L.map(function (x) { return x[j]; }).sort(function (p, q) { return p - q; }), s = 0;
        for (k = 0; k < a.length; k++) s += a[k];
        return { n: a.length, avg: s / a.length, p95: a[Math.min(a.length - 1, Math.floor(a.length * 0.95))], max: a[a.length - 1] };
      }
      /* the device */
      try {
        var cv = rn && rn.domElement, pr = rn && rn.getPixelRatio ? rn.getPixelRatio() : 0;
        out.push('perf   device dpr ' + (global.devicePixelRatio || 1) + ', rendered at x' + pr +
                 (cv ? (' | canvas ' + cv.clientWidth + ' x ' + cv.clientHeight + ' css = ' + cv.width + ' x ' + cv.height + ' px') : '') +
                 ' | cores ' + ((global.navigator && global.navigator.hardwareConcurrency) || '?') +
                 ' | memory ' + ((global.navigator && global.navigator.deviceMemory) || '?') + ' GB' +
                 ' | antialias on, preserveDrawingBuffer on');
      } catch (eD) { out.push('perf   device threw ' + eD.message); }
      /* the load: the page, our scripts, three and the map from their CDNs */
      try {
        var PE = global.performance, nav = (PE && PE.getEntriesByType) ? (PE.getEntriesByType('navigation')[0] || null) : null;
        var res = (PE && PE.getEntriesByType) ? PE.getEntriesByType('resource') : [], mine = [], esm = { n: 0, t: 0, d: 0, end: 0 }, tot = 0;
        for (i = 0; i < res.length; i++) {
          var e = res[i], nm = String(e.name || ''), base = nm.split('?')[0].split('/').pop();
          if (/esm\.sh/.test(nm)) { esm.n++; esm.t += e.transferSize || 0; esm.d += e.decodedBodySize || 0; esm.end = Math.max(esm.end, e.responseEnd || 0); tot += e.transferSize || 0; continue; }
          if (/^configurator-.*\.js$/.test(base) || /mapbox-gl\.js$/.test(base) || /^budget-core\.js$/.test(base)) {
            mine.push(base.replace(/^configurator-/, '').replace(/\.js$/, '') + ' ' + (e.transferSize ? (kb(e.transferSize) + ' kB') : 'cache') + ' / ' + kb(e.decodedBodySize) + ' kB unpacked, done at ' + r0(e.responseEnd) + ' ms');
            tot += e.transferSize || 0;
          }
        }
        out.push('perf   load page ' + (nav ? (kb(nav.transferSize) + ' kB, html done ' + r0(nav.responseEnd) + ' ms, DOMContentLoaded ' + r0(nav.domContentLoadedEventEnd) + ' ms, load ' + r0(nav.loadEventEnd) + ' ms') : 'no navigation timing') +
                 ' | core started ' + r0(_T0) + ' ms, first model done ' + (_pfB.firstEnd != null ? (r0(_pfB.firstEnd) + ' ms') : 'NOT YET') + ' (from the tap on the link)');
        out.push('perf   scripts ' + (mine.length ? mine.join(' | ') : 'none seen') + ' | three (esm.sh) ' + esm.n + ' file(s) ' + kb(esm.t) + ' kB / ' + kb(esm.d) + ' kB, done at ' + r0(esm.end) + ' ms | over the wire ' + kb(tot) + ' kB in all');
      } catch (eL) { out.push('perf   load threw ' + eL.message); }
      /* the builds */
      try {
        var H = _pfB.hist.slice(), sH = 0, mH = 0;
        for (i = 0; i < H.length; i++) { sH += H[i]; mH = Math.max(mH, H[i]); }
        var LY = _pfB.layers.slice().sort(function (p, q) { return q.ms - p.ms; }), lyT = 0;
        for (i = 0; i < LY.length; i++) lyT += LY[i].ms;
        out.push('perf   build ' + _pfB.n + ' build(s), last ' + r0(H[H.length - 1]) + ' ms, average ' + r0(H.length ? sH / H.length : 0) + ' ms, slowest ' + r0(mH) + ' ms (last ' + H.length + ')' +
                 ' | layers ' + r0(lyT) + ' ms of the last: ' + LY.slice(0, 8).map(function (x) { return x.id + ' ' + r0(x.ms) + (x.hidden ? ' (hidden, counted)' : ''); }).join(', '));
      } catch (eB) { out.push('perf   build threw ' + eB.message); }
      try { out.push(_pfGeoLine()); } catch (eGL) { out.push('perf   geometry threw ' + eGL.message); }
      /* the frames */
      try {
        var PF = st && st._perf;
        if (!PF) out.push('perf   frames not measured (no stage)');
        else {
          /* core-v603: the render call averaged over the frames actually drawn */
          var dI = PF.idle.filter(function (x) { return x[2] !== 0; }), dB = PF.busy.filter(function (x) { return x[2] !== 0; });
          var fI = stats(PF.idle, 0), rI = stats(dI, 1) || { avg: 0 }, fB = stats(PF.busy, 0), rB = stats(dB, 1) || { avg: 0 };
          var line = function (f, r) { return f ? (r0(f.avg) + ' ms a frame = ' + r0(1000 / Math.max(1, f.avg)) + ' fps, p95 ' + r0(f.p95) + ', worst ' + r0(f.max) + ', the render call ' + r0(r.avg) + ' ms (' + f.n + ' frames)') : 'none yet'; };
          var RDp = st && st._rd;
          out.push('perf   frames still: ' + line(fI, rI) + ' | under a finger: ' + line(fB, rB) +
                   (RDp ? (' | drawn ' + RDp.drawn + ', skipped ' + RDp.skipped + ' (nothing changed)' + (RDp.always ? ' -- ?render=always' : ' -- a frame only on a change, core-v603')) : ''));
        }
      } catch (eF) { out.push('perf   frames threw ' + eF.message); }
      /* what the GPU is asked to draw */
      try {
        var nM = 0, nV = 0, byM = {}, kM, topM = [];
        if (st && st.scene) st.scene.traverse(function (o) {
          if (!o.isMesh && !o.isLine && !o.isSprite && !o.isPoints) return;
          if (o.userData && o.userData.mergeProxy) return;   /* core-v621: the merged stand-ins are in 'perf   merged' */
          nM++;
          var vis = o.visible, p = o.parent;
          while (vis && p) { if (p.visible === false) vis = false; p = p.parent; }
          if (vis) nV++;
          var lay = '';
          try { lay = _layerOf(o) || ''; } catch (eLo) { lay = ''; }
          if (!lay) { var q = o; while (q && q.parent && q.parent !== st.scene && q.parent !== st.group) q = q.parent; lay = (q && q.name) || '(scene)'; }
          byM[lay] = (byM[lay] || 0) + 1;
        });
        for (kM in byM) if (byM.hasOwnProperty(kM)) topM.push([kM, byM[kM]]);
        topM.sort(function (p, q) { return q[1] - p[1]; });
        var inf = rn && rn.info, mem = inf && inf.memory;
        out.push('perf   scene ' + nV + ' of ' + nM + ' bodies visible, last frame ' + ((st && st._perf) ? st._perf.calls : '?') + ' draw call(s), ' + ((st && st._perf) ? st._perf.tris : '?') + ' triangles' +
                 ' | GPU memory: ' + (mem ? (mem.geometries + ' geometries, ' + mem.textures + ' textures') : '?') +
                 ' | most bodies: ' + topM.slice(0, 8).map(function (x) { return x[0] + ' ' + x[1]; }).join(', '));
      } catch (eS) { out.push('perf   scene threw ' + eS.message); }
      /* the plan, the saves, the map */
      try {
        out.push('perf   plan ' + _pfP.n + ' paint(s), last ' + r0(_pfP.last) + ' ms, average ' + r0(_pfP.n ? _pfP.sum / _pfP.n : 0) + ' ms, slowest ' + r0(_pfP.max) + ' ms' +
                 ' | 2D zoom / pan events ' + _pfP.gest + ', ' + _pfP.gestSkipped + ' of them waited for the next frame instead of painting' +
                 ' | saves ' + _pfS.n + ', last ' + r0(_pfS.last) + ' ms, slowest ' + r0(_pfS.max) + ' ms, map moves held back ' + _pfS.soon);
        /* core-v602: the map manager is _MM here ('MM is not defined' in every log) */
        var MM = _MM, mp = MM && MM.map, nowM = (global.performance && global.performance.now) ? global.performance.now() : Date.now();
        out.push('perf   map ' + (mp ? (MM.currentStyle + ', ' + (MM._pfRenders || 0) + ' repaint(s) in ' + r0((nowM - (MM._pfSince || nowM)) / 1000) + ' s' +
                 ((function () { try { var el = global.document.getElementById('cf-mapbox'); return el ? (', ' + (el.offsetParent === null ? 'hidden' : 'on screen')) : ''; } catch (eE) { return ''; } })())) : 'not loaded'));
      } catch (eP) { out.push('perf   plan / map threw ' + eP.message); }
      /* WHAT A MERGE WOULD SAVE (core-v602): every body is a draw call of its
         own; bodies of one layer that share a material could be one. Per
         layer: bodies, materials, triangles -- and the draw calls a merge per
         layer and material would leave. */
      try {
        var byL = {}, nB = 0, pairs = 0, kL2;
        if (st && st.group) st.group.traverse(function (o) {
          if (!o.isMesh) return;
          var vis = o.visible, p = o.parent;
          while (vis && p) { if (p.visible === false) vis = false; p = p.parent; }
          if (!vis) return;
          var lay = '';
          try { lay = _layerOf(o) || ''; } catch (eLo) { lay = ''; }
          if (!lay) lay = '(other)';
          var L = byL[lay] || (byL[lay] = { n: 0, mats: {}, nm: 0, tris: 0, inst: 0 });
          L.n++; nB++;
          var ms = Array.isArray(o.material) ? o.material : [o.material];
          for (var q = 0; q < ms.length; q++) { var u = ms[q] && ms[q].uuid; if (u && !L.mats[u]) { L.mats[u] = 1; L.nm++; } }
          var g = o.geometry, tc = 0;
          try { tc = g ? ((g.index ? g.index.count : (g.attributes.position ? g.attributes.position.count : 0)) / 3) : 0; } catch (eG) { tc = 0; }
          if (o.isInstancedMesh) { L.inst++; tc *= (o.count || 1); }
          L.tris += tc;
        });
        var topL = [];
        for (kL2 in byL) if (byL.hasOwnProperty(kL2)) { topL.push([kL2, byL[kL2]]); pairs += byL[kL2].nm; }
        topL.sort(function (a, b) { return b[1].n - a[1].n; });
        out.push('perf   merge ' + nB + ' visible bodies in ' + pairs + ' layer-material pair(s) -- merged, about ' + pairs + ' draw calls instead of ' + nB +
                 ' | ' + topL.slice(0, 8).map(function (x) { return x[0] + ' ' + x[1].n + ' bodies / ' + x[1].nm + ' mat / ' + Math.round(x[1].tris / 1000) + 'k tris' + (x[1].inst ? (' (' + x[1].inst + ' instanced)') : ''); }).join(', '));
      } catch (eMg) { out.push('perf   merge threw ' + eMg.message); }
      try { out.push(_mgLine()); } catch (eMl) { out.push('perf   merged threw ' + eMl.message); }
      /* the main thread: long tasks, slow taps, the heap */
      try {
        var hp = global.performance && global.performance.memory;
        out.push('perf   tasks ' + _pfT.n + ' long task(s) since the page opened, ' + r0(_pfT.sum) + ' ms blocked in all, worst ' + r0(_pfT.max) + ' ms' +
                 ' | taps: ' + _pfE.n + ' event(s) over 40 ms, ' + _pfE.slow + ' over 200 ms, worst ' + r0(_pfE.max) + ' ms' + (_pfE.maxName ? (' (' + _pfE.maxName + ')') : '') +
                 ' | heap ' + (hp ? (Math.round(hp.usedJSHeapSize / 1048576) + ' of ' + Math.round(hp.jsHeapSizeLimit / 1048576) + ' MB') : '?'));
      } catch (eTk) { out.push('perf   tasks threw ' + eTk.message); }
      /* core-v604: why the stage drew, who asked for animation frames, and
         which scripts the long frames belong to */
      try {
        var RDw = st && st._rd, Wy = (RDw && RDw.why) || null, who = global.__levaRafWho || {}, wl = [], kw;
        for (kw in who) if (who.hasOwnProperty(kw)) wl.push([kw, who[kw]]);
        wl.sort(function (a, b) { return b[1] - a[1]; });
        out.push('perf   wake ' + (Wy ? ('controls ' + Wy.controls + ', a page animation frame ' + Wy.frame + ', input ' + Wy.input + ', camera ' + Wy.camera + ', heartbeat ' + Wy.beat + (Wy.always ? (', always ' + Wy.always) : '') + ' | the controls said moved with the camera still ' + (Wy.noise || 0) + 'x (not drawn)') : 'no tally') +
                 ' | frames asked ' + (global.__levaRafExt | 0) + ', sampled callers: ' + (wl.length ? wl.slice(0, 6).map(function (x) { return x[0] + ' x' + (x[1] * 20); }).join(' ; ') : 'none'));
      } catch (eWk) { out.push('perf   wake threw ' + eWk.message); }
      try {
        if (!_pfLF.on) out.push('perf   loaf not supported by this browser');
        else {
          var lb = [], kb2;
          for (kb2 in _pfLF.by) if (_pfLF.by.hasOwnProperty(kb2)) lb.push([kb2, _pfLF.by[kb2]]);
          lb.sort(function (a, b) { return b[1].ms - a[1].ms; });
          out.push('perf   loaf ' + _pfLF.n + ' long frame(s), ' + r0(_pfLF.ms) + ' ms in all, of it style and layout ' + r0(_pfLF.layout) + ' ms | top scripts: ' +
                   (lb.length ? lb.slice(0, 8).map(function (x) { return x[0] + ' ' + r0(x[1].ms) + ' ms' + (x[1].lay > 5 ? (' (forced layout ' + r0(x[1].lay) + ')') : '') + ' x' + x[1].n; }).join(' | ') : 'none'));
        }
      } catch (eLf) { out.push('perf   loaf threw ' + eLf.message); }
      /* the bench, when ?bench=1 ran it */
      try { _benchLines().forEach(function (l) { out.push(l); }); } catch (eBl) { out.push('bench  threw ' + eBl.message); }
      return out;
    }

    /* THE BENCH (core-v602, Marek: na zacatku udelat nejaky benchmark,
       poslal bych ti log). ?bench=1 in the address: four seconds after the
       first model the page runs the same walk on every product and every
       device, no finger needed -- the model standing still, turned round,
       zoomed in and out, built again three times, the plan in 2D panned --
       measuring each part apart (frame time, the render call, draw calls,
       triangles, long tasks, plan paints), then puts the camera back and
       sends the log itself (SEND LOG, where the page has it). A chip in the
       corner says which part runs; the finger stays off the screen. */
    var _BN = { state: 'off', mode: 0, res: [], rebuild: [], t0: 0, ms: 0, sent: '', at: '',
                work: [], wstate: '', wt0: 0, wms: 0, wcur: null, fresh: false, wback: '' };
    try { global.__levaBench = _BN; } catch (eGB) {}
    /* ?bench=1 the view walk, ?bench=2 the work walk (core-v607), ?bench=3 both */
    function _benchMode() {
      try {
        var m = /[?&]bench=(\d)(&|$)/.exec(String((global.location && global.location.search) || ''));
        return m ? ((+m[1]) & 3) : 0;
      } catch (e) { return 0; }
    }
    function _benchWanted() { return _benchMode() > 0; }
    function _benchChip(txt) {
      try {
        var el = global.document.getElementById('cf-bench-chip');
        if (!txt) { if (el && el.parentNode) el.parentNode.removeChild(el); return; }
        if (!el) {
          el = global.document.createElement('div');
          el.id = 'cf-bench-chip';
          el.style.cssText = 'position:absolute;left:12px;top:12px;z-index:60;padding:7px 12px;border-radius:999px;background:#1A1C1D;color:#ffffff;' +
            'font:700 11px/1 Montserrat,sans-serif;letter-spacing:0.06em;pointer-events:none;box-shadow:0 4px 14px rgba(0,0,0,0.25);';
          var root = global.document.getElementById('cf-root') || global.document.body;
          root.appendChild(el);
        }
        el.textContent = txt;
      } catch (e) {}
    }
    function _benchMaybe() {
      if (_BN.state !== 'off' || !_benchWanted()) return;
      _BN.state = 'waiting';
      _BN.mode = _benchMode();
      _benchChip('BENCH starts in 4 s -- do not touch');
      global.setTimeout(function () {
        var m = _BN.mode;
        var work = function () { if (m & 2) _benchWork(_benchFinish); else _benchFinish(); };
        if (m & 1) _benchRun(work); else work();
      }, 4000);
    }
    /* the walk(s) done: the log goes out by itself (SEND LOG, where the page has it) */
    function _benchFinish() {
      _BN.state = 'done';
      _log('bench    done' + (_BN.ms ? (' -- view walk ' + Math.round(_BN.ms / 1000) + ' s') : '') + (_BN.wms ? (' -- work walk ' + Math.round(_BN.wms / 1000) + ' s') : ''));
      var btn = global.document.getElementById('cf-house-send-log');
      if (btn) {
        _benchChip('BENCH done -- sending the log');
        global.setTimeout(function () { try { btn.click(); _BN.sent = 'SEND LOG pressed by the bench'; } catch (eS) {} global.setTimeout(function () { _benchChip('BENCH done -- log sent (' + String(btn.textContent || '').trim() + ')'); global.setTimeout(function () { _benchChip(''); }, 6000); }, 2500); }, 800);
      } else {
        _benchChip('BENCH done -- no SEND LOG on this page');
        global.setTimeout(function () { _benchChip(''); }, 6000);
      }
    }
    function _benchRun(next) {
      var st = _stage();
      if (!st || !st.renderer || !st.camera || !st.controls) { _BN.state = 'no stage'; _benchChip(''); if (next) next(); return; }
      _BN.state = 'running'; _BN.res = []; _BN.rebuild = []; _BN.t0 = _pfNow();
      _log('bench    started (core-v602): still, orbit, zoom, rebuild x3, plan 2D');
      var R = st.renderer, orig = R.render, cur = null;
      R.render = function (sc, ca) {
        var a = _pfNow();
        orig.call(R, sc, ca);
        if (cur) { cur.rend.push(_pfNow() - a); try { cur.calls = R.info.render.calls; cur.tris = R.info.render.triangles; } catch (eI) {} }
      };
      var cam0 = st.camera.position.clone(), tg0 = st.controls.target.clone();
      function changed() { try { st.controls.dispatchEvent({ type: 'change' }); } catch (eC) {} }
      function phase(name, ms, step, next) {
        cur = { name: name, dt: [], rend: [], calls: 0, tris: 0, lt0: _pfT.n, lts0: _pfT.sum, pp0: _pfP.n, pps0: _pfP.sum, t0: _pfNow() };
        _BN.at = name;
        _benchChip('BENCH ' + name + ' -- do not touch');
        _log('bench    ' + name + ' for ' + (ms / 1000) + ' s');
        var last = 0;
        function f() {
          var now = _pfNow();
          if (last) cur.dt.push(now - last);
          last = now;
          var t = now - cur.t0;
          if (step) { try { step(t / 1000); } catch (eS) {} }
          if (t < ms) global.requestAnimationFrame(f);
          else {
            cur.lt = _pfT.n - cur.lt0; cur.lts = _pfT.sum - cur.lts0; cur.pp = _pfP.n - cur.pp0; cur.pps = _pfP.sum - cur.pps0; cur.ms = now - cur.t0;
            _BN.res.push(cur); cur = null;
            global.setTimeout(next, 300);
          }
        }
        global.requestAnimationFrame(f);
      }
      var off = cam0.clone().sub(tg0), rr = Math.sqrt(off.x * off.x + off.z * off.z), a0 = Math.atan2(off.z, off.x), d0 = off.length();
      function orbit(t) {
        var a = a0 + t * 0.9;
        st.camera.position.set(tg0.x + rr * Math.cos(a), cam0.y, tg0.z + rr * Math.sin(a));
        st.camera.lookAt(tg0); changed();
      }
      function zoom(t) {
        var k = 1 + 0.4 * Math.sin(2 * Math.PI * t / 3), dir = off.clone().normalize();
        st.camera.position.set(tg0.x + dir.x * d0 * k, tg0.y + dir.y * d0 * k, tg0.z + dir.z * d0 * k);
        st.camera.lookAt(tg0); changed();
      }
      function back3d() { try { st.camera.position.copy(cam0); st.controls.target.copy(tg0); st.camera.lookAt(tg0); changed(); } catch (eB) {} }
      function rebuilds(k, next) {
        (function one(i) {
          if (i >= k) { next(); return; }
          _benchChip('BENCH rebuild ' + (i + 1) + ' of ' + k + ' -- do not touch');
          global.setTimeout(function () {
            var a = _pfNow();
            try { rebuild(); } catch (eR) {}
            _BN.rebuild.push(_pfNow() - a);
            one(i + 1);
          }, 500);
        })(0);
      }
      function plan(next) {
        var bar = (typeof _cmd !== 'undefined' && _cmd && _cmd.el);
        try { if (bar) _cmdPlanView(true, 'the bench', true); else setView('2d'); } catch (eP) {}
        global.setTimeout(function () {
          var s2 = _stage(), c2 = s2 && s2.camera, o2 = s2 && s2.controls;
          if (!c2 || !o2) { next(); return; }
          var p0 = c2.position.clone(), q0 = o2.target.clone();
          phase('plan 2D pan', 4000, function (t) {
            var dx = 1.5 * Math.sin(2 * Math.PI * t / 4);
            c2.position.set(p0.x + dx, p0.y, p0.z); o2.target.set(q0.x + dx, q0.y, q0.z);
            try { o2.dispatchEvent({ type: 'change' }); } catch (eD) {}
            try { drawPlan(); } catch (eDp) {}
          }, function () {
            try { c2.position.copy(p0); o2.target.copy(q0); } catch (eBk) {}
            try { if (bar) _cmdPlanView(false, 'the bench', true); else setView('3d'); } catch (eQ) {}
            global.setTimeout(next, 1200);
          });
        }, 1500);
      }
      function done() {
        R.render = orig;
        back3d();
        _BN.ms = _pfNow() - _BN.t0;
        _log('bench    view walk done in ' + Math.round(_BN.ms / 1000) + ' s');
        global.setTimeout(function () { if (next) next(); }, 800);
      }
      phase('still 3D', 4000, null, function () {
        phase('orbit', 5000, orbit, function () {
          back3d();
          phase('zoom', 3000, zoom, function () {
            back3d();
            rebuilds(3, function () {
              phase('still after rebuilds', 2000, null, function () {
                plan(done);
              });
            });
          });
        });
      });
    }

    /* EVERY BUILD WHILE A WORK PART RUNS (core-v607): how long, and where the
       time went -- the layers (each by name), the bill, the plan paint, the
       rest of rebuild(). The next frame drawn after it is marked, so the cost
       of putting the new model on the GPU shows apart from a plain frame. */
    function _benchBuilt(ms) {
      _BN.fresh = true;
      var c = _BN.wcur;
      if (!c) return;
      var b = c.b, L = _pfB.layers || [], lay = 0, i;
      b.n++; b.ms += ms; if (ms > b.max) b.max = ms;
      for (i = 0; i < L.length; i++) { lay += L[i].ms || 0; b.L[L[i].id] = (b.L[L[i].id] || 0) + (L[i].ms || 0); }
      b.lay += lay; b.boq += (_pfB.boq || 0); b.plan += (_pfB.plan || 0);
      var A = _pfB.after || {}, k;
      for (k in A) if (A.hasOwnProperty(k) && k.charAt(0) !== '(') b.R[k] = (b.R[k] || 0) + A[k];
    }

    /* THE WORK WALK (core-v607, Marek 2026-10-11: chtelo by to vyzkouset vice
       funkci -- praci s floorplan (editace), zmena strechy, sklon, zakladu,
       tazeni strechy, okna a dvere atd.; staci na ADU). ?bench=2 runs it,
       ?bench=3 runs the view walk first. Each part goes in the way a finger
       does: the strip's own arrows for the roof, the covering and the base;
       the pitch slider's input events, one a frame; pointer events on the
       canvas for every drag -- the roof's ghost, the plan's outer wall, a
       partition, a door, a window -- and for the tap that puts a window in.
       A part says how long a tap or a move held the page, the frames under
       the finger, how many builds ran and where their time went, how long
       the first frame after a build took to draw, and when a press did not
       take hold, why. The project is put back as it was at the end. */
    function _benchWork(fin) {
      var st = _stage(), T = global.THREE;
      _BN.work = []; _BN.wstate = 'running'; _BN.wt0 = _pfNow(); _BN.state = 'running';
      if (!st || !st.renderer || !st.camera || !T) { _BN.wstate = 'no stage'; if (fin) fin(); return; }
      var snap = null;
      try { snap = JSON.stringify(payload()); } catch (eSn) { snap = null; }
      var cam0 = st.camera.position.clone(), tg0 = (st.controls && st.controls.target) ? st.controls.target.clone() : null;
      var R = st.renderer, orig = R.render, cv = R.domElement, PID = 4242, finished = false;
      _log('bench    work walk started (core-v607)');
      R.render = function (sc, ca) {
        var a = _pfNow();
        orig.call(R, sc, ca);
        var c = _BN.wcur, e = _pfNow();
        if (c) {
          c.rend.push(e - a);
          if (_BN.fresh) c.fresh.push(e - a);
          if (c.tapAt) { c.nf.push(e - c.tapAt); c.tapAt = 0; }
        }
        _BN.fresh = false;
      };
      function now() { return _pfNow(); }
      function msg(e) { return String((e && e.message) || e).slice(0, 120); }
      function part(name, kind) {
        var c = { name: name, kind: kind || 'tap', t0: now(), dt: [], rend: [], fresh: [], ev: [], nf: [], tapAt: 0, dn: null, up: null,
                  b: { n: 0, ms: 0, max: 0, lay: 0, boq: 0, plan: 0, L: {}, R: {} },
                  lt0: _pfT.n, lts0: _pfT.sum, pp0: _pfP.n, pps0: _pfP.sum, note: '' };
        _BN.wcur = c; _BN.at = name;
        _benchChip('BENCH ' + name + ' -- do not touch');
        _log('bench    ' + name);
        var last = 0;
        (function f() {
          if (_BN.wcur !== c || finished) return;
          var n = now();
          if (last) c.dt.push(n - last);
          last = n;
          _rafQuiet(f);
        })();
        return c;
      }
      function end(c) {
        if (!c || c.ms != null) return;
        c.ms = now() - c.t0; c.lt = _pfT.n - c.lt0; c.lts = _pfT.sum - c.lts0; c.pp = _pfP.n - c.pp0; c.pps = _pfP.sum - c.pps0;
        if (_BN.wcur === c) _BN.wcur = null;
        _BN.work.push(c);
      }
      function skip(name, why) { _BN.work.push({ name: name, skip: why }); _log('bench    ' + name + ' skipped -- ' + why); }
      function note(c, s) { c.note = c.note ? (c.note + '; ' + s) : s; }

      /* the steps run one after the other; a step that throws ends its part
         with the message and the walk goes on */
      var steps = [], si = 0, cont = null;
      function step(name, fn) { steps.push({ name: name, fn: fn }); }
      function run() {
        if (finished) return;
        if (si >= steps.length) { wrapUp('all parts done'); return; }
        var s = steps[si++], called = false;
        cont = function () { if (called) return; called = true; global.setTimeout(run, 300); };
        try { s.fn(cont); } catch (e) { fail(s.name, e); }
      }
      function fail(where, e) {
        var c = _BN.wcur;
        if (c) { note(c, 'THREW ' + msg(e)); end(c); }
        else skip(where || 'a step', 'threw ' + msg(e));
        if (cont) cont();
      }
      function later(ms, fn) { global.setTimeout(function () { if (finished) return; try { fn(); } catch (e) { fail('', e); } }, ms); }
      function nextFrame(fn) { _rafQuiet(function () { if (finished) return; try { fn(); } catch (e) { fail('', e); } }); }

      /* a finger: one pointer, touch, on the 3D canvas; the window's own
         listeners hear it through the capture path. Answers how long the
         page was held by the event. */
      function fire(type, x, y) {
        var ev;
        try {
          ev = new global.PointerEvent(type, { bubbles: true, cancelable: true, composed: true, clientX: x, clientY: y,
            pointerId: PID, pointerType: 'touch', isPrimary: true, button: 0, buttons: (type === 'pointerup') ? 0 : 1, width: 10, height: 10, pressure: (type === 'pointerup') ? 0 : 0.5 });
        } catch (eP) { return -1; }
        var a = now();
        try { cv.dispatchEvent(ev); } catch (eD) { _log('bench    ' + type + ' threw ' + msg(eD)); }
        return now() - a;
      }
      function cam() { return _navCam() || st.camera; }
      function scr(x, y, z) {
        var C = cam();
        try { C.updateMatrixWorld(); } catch (eM) {}
        var v = new T.Vector3(x, y, z).project(C), b = cv.getBoundingClientRect();
        return [b.left + (v.x + 1) / 2 * b.width, b.top + (1 - v.y) / 2 * b.height, v.z];
      }
      function onCanvas(p) {
        if (!p || !(p[2] < 1)) return false;
        var b = cv.getBoundingClientRect();
        return p[0] > b.left + 12 && p[0] < b.right - 12 && p[1] > b.top + 12 && p[1] < b.bottom - 12;
      }

      /* taps: fn run n times, gap ms apart; each says how long it held the
         page and when the next picture was drawn after it */
      function taps(name, n, fn, gap, next) {
        var c = part(name, 'tap'), i = 0;
        (function one() {
          if (i >= n) { later(gap, function () { end(c); next(); }); return; }
          i++;
          var a = now();
          fn(i, c);
          c.ev.push(now() - a);
          c.tapAt = a;
          later(gap, one);
        })();
      }
      /* a drag: down at from, a move a frame along at(t) for ms, up at the end;
         took() says whether the press took hold */
      function drag(name, from, at, ms, next, opt) {
        opt = opt || {};
        var c = part(name, 'drag');
        c.dn = fire('pointerdown', from[0], from[1]);
        var ok = true;
        try { if (opt.took) ok = !!opt.took(); } catch (eT) { ok = false; }
        if (!ok) {
          fire('pointerup', from[0], from[1]);
          note(c, 'the press did not take hold' + (opt.miss ? (' -- ' + opt.miss()) : ''));
          later(700, function () { end(c); next(); });
          return;
        }
        var t0 = now();
        (function mv() {
          var t = Math.min(1, (now() - t0) / ms), p = at(t);
          if (t >= 1) {
            c.up = fire('pointerup', p[0], p[1]);
            c.tapAt = now();
            later(1200, function () { end(c); next(); });
            return;
          }
          c.ev.push(fire('pointermove', p[0], p[1]));
          nextFrame(mv);
        })();
      }
      function icon(key) { try { return _sxIconBy(key); } catch (e) { return null; } }
      function pick(key) { if (_SX.sel !== key) _sxTap(key, null, { pointerType: 'mouse' }); }
      function cycle(name, key, next) {
        var I = icon(key);
        if (!I) { skip(name, 'no ' + key + ' icon on the strip'); next(); return; }
        pick(key);
        var n = 0;
        try { n = _sxCycle(I).length; } catch (eC) { n = 0; }
        if (!n) { skip(name, 'nothing to walk on the ' + key + ' icon'); next(); return; }
        later(700, function () { taps(name + ' (' + n + ' taps, round to the start)', n, function () { _sxStep(key, 1); }, 1100, next); });
      }
      /* the grips of a ghost rect, by side, where the handles will take them */
      function gripOf(r, y, prefer) {
        var mx = (r.x0 + r.x1) / 2, mz = (r.z0 + r.z1) / 2, out = null;
        [{ k: 'z0', x: mx, z: r.z0, dx: 0, dz: -1 }, { k: 'z1', x: mx, z: r.z1, dx: 0, dz: 1 },
         { k: 'x0', x: r.x0, z: mz, dx: -1, dz: 0 }, { k: 'x1', x: r.x1, z: mz, dx: 1, dz: 0 }].forEach(function (g) {
          if (r.noGrip && r.noGrip.indexOf(g.k) >= 0) return;
          var p = scr(g.x, y, g.z);
          if (!onCanvas(p)) return;
          var h = null;
          try { h = _handles ? _handles._hitAt(p[0], p[1]) : null; } catch (eH) { h = null; }
          if (!h || !h.rect || h.rect.key !== r.key) return;
          g.p = p;
          if (!out || (prefer === 'low' ? p[1] > out.p[1] : Math.abs(p[1] - (cv.getBoundingClientRect().top + cv.clientHeight / 2)) < Math.abs(out.p[1] - (cv.getBoundingClientRect().top + cv.clientHeight / 2)))) out = g;
        });
        return out;
      }
      function nearGrips(p) {
        var rs = [], i, best = 1e9;
        try { rs = _rects() || []; } catch (eR) { rs = []; }
        for (i = 0; i < rs.length; i++) {
          var r = rs[i], mx = (r.x0 + r.x1) / 2, mz = (r.z0 + r.z1) / 2, y = (r.y != null) ? r.y : 0.04;
          [[mx, r.z0], [mx, r.z1], [r.x0, mz], [r.x1, mz], [r.x0, r.z0], [r.x1, r.z0], [r.x0, r.z1], [r.x1, r.z1]].forEach(function (q) {
            var s = scr(q[0], y, q[1]);
            var d = Math.hypot(s[0] - p[0], s[1] - p[1]);
            if (d < best) best = d;
          });
        }
        return best;
      }
      function segPoint(sg, a, out, y) {
        var ux = (sg.x1 - sg.x0) / sg.len, uz = (sg.z1 - sg.z0) / sg.len;
        return [sg.x0 + ux * a + (sg.nx || 0) * out, y, sg.z0 + uz * a + (sg.nz || 0) * out];
      }
      /* a drag is at least px on the screen whatever the zoom (core-v609:
         on RANCH 0.4 m was under the 8 px a partition needs to start) */
      function amp(m, px, x, y, z, dx, dz) {
        var a = scr(x, y, z), b = scr(x + dx, y, z + dz), k = Math.hypot(b[0] - a[0], b[1] - a[1]);
        return (k > 0.5) ? Math.max(m, px / k) : m;
      }
      function rayWhat(p) {
        var hs = null;
        try { hs = _hitGroup(p[0], p[1]); } catch (eH) { hs = null; }
        if (!hs || !hs.length) return { pt: null, what: 'nothing' };
        var o = hs[0].object, lay = '';
        for (var q = o; q && !lay; q = q.parent) if (q.userData && q.userData.layer) lay = q.userData.layer;
        return { pt: hs[0].point, what: (lay || (o && o.name) || 'a body') };
      }
      var tpl = null, added = null, partPt = null;

      /* ---- 3D, the icon strip ---- */
      step('open the strip', function (next) {
        if (!_stripOn() || !_wiz) { skip('open the strip', 'this product has no icon strip'); next(); return; }
        taps('open the strip', 1, function () { _sxEditOpen(); }, 1200, next);
      });
      step('roof type', function (next) { cycle('roof type', 'roof', next); });
      step('covering', function (next) { cycle('covering', 'covering', next); });
      step('pitch drag', function (next) {
        var D = _SX_SL.pitch;
        if (!D.on()) { skip('pitch drag', 'the roof is flat'); next(); return; }
        var S = D.core();
        if (!S || !S.input) { skip('pitch drag', 'no pitch slider'); next(); return; }
        var p0 = +STATE.roof.pitch || 0, lo = pitchMin(), hi = pitchMax(), A = Math.min(8, Math.max(1, (hi - lo) / 2));
        var c = part('pitch drag', 'drag'), t0 = now(), last = null;
        c.dn = 0;
        (function mv() {
          var t = Math.min(1, (now() - t0) / 3000);
          var v = (t >= 1) ? p0 : Math.round(Math.max(lo, Math.min(hi, p0 + A * Math.sin(2 * Math.PI * t))));
          if (v !== last) {
            last = v;
            S.input.value = String(v);
            var a = now();
            try { S.input.dispatchEvent(new global.Event('input')); } catch (eI) {}
            c.ev.push(now() - a);
          }
          if (t >= 1) {
            var a2 = now();
            try { S.input.dispatchEvent(new global.Event('change')); } catch (eC) {}
            c.up = now() - a2; c.tapAt = now();
            note(c, 'swept ' + Math.round(p0 - A) + '..' + Math.round(p0 + A) + ' deg, back to ' + Math.round(p0));
            later(1200, function () { end(c); next(); });
            return;
          }
          nextFrame(mv);
        })();
      });
      step('roof drag', function (next) {
        if (!icon('roof')) { skip('roof drag', 'no roof icon on the strip'); next(); return; }
        pick('roof');
        try { _hRefresh(); } catch (eH) {}
        later(700, function () {
          var rs = _rects() || [], r = null, i;
          for (i = 0; i < rs.length; i++) if (rs[i].key === 'roofover') r = rs[i];
          if (!r) { skip('roof drag', 'no roof ghost (roof ' + STATE.roof.type + (STATE.roof.parapet ? ', parapet' : '') + ')'); next(); return; }
          var y = (r.y != null) ? r.y : ((_handles && _handles._y) || 0.04), g = gripOf(r, y, 'low');
          if (!g) { skip('roof drag', 'no grip of the roof ghost on the screen'); next(); return; }
          var Ar = amp(0.8, 60, g.x, y, g.z, g.dx, g.dz);
          drag('roof drag', g.p, function (t) { var d = Ar * Math.sin(Math.PI * t); return scr(g.x + g.dx * d, y, g.z + g.dz * d); }, 3000, next,
               { took: function () { return !!(_handles && _handles._drag); }, miss: function () { return 'grip ' + g.k + ' at ' + Math.round(g.p[0]) + ',' + Math.round(g.p[1]); } });
        });
      });
      /* ---- the RANCH cross gables (core-v625, Marek: zapinani / vypinani
         kolmych vikyru -- zaradit do benche). Since ranch-v42 / v43 they are
         switched by their own strip icon (all on / off, the width slider) and
         place by place by the triangles on the Floorplan. Both are tapped
         like a finger and each tap must change the product's state; the
         bodies of the gable layer are counted before / after. */
      function prodSig() { try { return JSON.stringify(STATE.product || {}); } catch (e) { return ''; } }
      function layBodies(re) {
        var n = 0;
        try { st.group.children.forEach(function (c) { if (c.userData && re.test(c.userData.layer || '')) c.traverse(function (o) { if (o.isMesh) n++; }); }); } catch (e) {}
        return n;
      }
      /* a finger on any element: down, a 2 px wobble, up (the element's own
         listeners and the window's capture both hear it) */
      function fireOn(el, type, x, y) {
        var ev;
        try {
          ev = new global.PointerEvent(type, { bubbles: true, cancelable: true, composed: true, clientX: x, clientY: y,
            pointerId: PID, pointerType: 'touch', isPrimary: true, button: 0, buttons: (type === 'pointerup') ? 0 : 1, width: 10, height: 10, pressure: (type === 'pointerup') ? 0 : 0.5 });
        } catch (eP) { return -1; }
        var a = now();
        try { el.dispatchEvent(ev); } catch (eD) { _log('bench    ' + type + ' threw ' + msg(eD)); }
        return now() - a;
      }
      /* the strip's own icon, by its key or as the auto icon of the row
         ('auto:<step>:xgable:xgable') */
      function xgKey() {
        var L = _SX.icons || [], i;
        for (i = 0; i < L.length; i++) if (L[i] && (L[i].key === 'xgable' || /(^|:)xgable$/.test(String(L[i].key || '')))) return L[i].key;
        return '';
      }
      /* the places that are on, as the product keeps them (RANCH) */
      function xgOnList() {
        try {
          var X = STATE.product && STATE.product.xg, out = [], k;
          if (!X || !X.slots) return '';
          for (k in X.slots) if (X.slots.hasOwnProperty(k) && X.slots[k] && X.slots[k].on) out.push(k);
          var L = out.sort().join(' ') || 'none';
          /* ranch-v44: the switch for them all, the places kept while it is off */
          return (X.on === false) ? ('switched off (' + L + ' kept)') : L;
        } catch (e) { return '?'; }
      }
      step('cross gables icon', function (next) {
        var xk = xgKey();
        if (!xk) { skip('cross gables icon', 'no cross gable icon on the strip (roof ' + STATE.roof.type + ', ' + (_SX.icons || []).length + ' icon(s))'); next(); return; }
        /* core-v628 / ranch-v45: the icon is the cross gables' STEP -- a tap
           picks it (none on: the last ones, or a0 b0, come on) with the two-
           thumb slider, a second tap puts it away; they are switched place by
           place on the Floorplan */
        taps('cross gables icon (its step picked and put away)', 2, function (k, c) {
          var s0 = prodSig(), b0 = layBodies(/xgable$/), on0 = xgOnList();
          _sxTap(xk, null, { pointerType: 'mouse' });
          var picked = (_SX.sel === xk), two = 0;
          try { two = global.document.querySelectorAll('#cf-sx-sl.cf-sx-sl2 input[type=range]').length; } catch (eQ) { two = 0; }
          note(c, 'tap ' + k + ': ' + (picked ? ('step picked, ' + (two === 2 ? 'the two-thumb slider up' : ('NO two-thumb slider (' + two + ' thumb(s))'))) : 'step put away') +
                  (prodSig() !== s0 ? (', on ' + on0 + ' -> ' + xgOnList() + ', gable bodies ' + b0 + ' -> ' + layBodies(/xgable$/)) : (', on ' + on0)));
        }, 1600, next);
      });
      step('base', function (next) { cycle('base', 'base', next); });
      /* ---- 3D, a window put in, moved and taken out ---- */
      step('window add', function (next) {
        if (_SX.sel) { _SX.sel = null; try { _sxAfter('the bench put the strip\'s pick away'); } catch (eA) {} }
        var L = STATE.openings || [], i;
        for (i = 0; i < L.length; i++) if (L[i] && L[i].kind === 'window' && L[i].seg && !L[i].host && segment(L[i].seg)) { tpl = L[i]; break; }
        if (!tpl) { skip('window add', 'no window in the house to copy'); next(); return; }
        var cp = cam().position, SG = segments(), best = null;
        for (i = 0; i < SG.length; i++) {
          var s = SG[i];
          if (!s || !(s.len > 2) || (s.storey | 0) !== 0) continue;
          var mx = (s.x0 + s.x1) / 2, mz = (s.z0 + s.z1) / 2;
          if (((s.nx || 0) * (cp.x - mx) + (s.nz || 0) * (cp.z - mz)) <= 0) continue;
          var occ = L.filter(function (o) { return o && o.seg === s.id; }).map(function (o) { return [o.at - o.w / 2 - 0.35, o.at + o.w / 2 + 0.35]; })
                     .sort(function (a, b) { return a[0] - b[0]; });
          var lo = 0.6, gap = null, q;
          for (q = 0; q <= occ.length; q++) {
            var hi = (q < occ.length) ? occ[q][0] : (s.len - 0.6);
            if (!gap || hi - lo > gap[1] - gap[0]) gap = [lo, hi];
            if (q < occ.length) lo = Math.max(lo, occ[q][1]);
          }
          if (!gap || gap[1] - gap[0] < tpl.w + 0.3) continue;
          /* the ray from the camera has to meet THIS wall first -- an eave,
             a porch or a gable in front of it takes the tap (core-v609) */
          var mid = (gap[0] + gap[1]) / 2, y0w = (s.y0 || 0) + (tpl.sill || 0), hw = (tpl.h || 1), tries = [[mid, 0.5], [mid, 0.25], [mid - 0.6, 0.5], [mid + 0.6, 0.5]], tq;
          for (tq = 0; tq < tries.length; tq++) {
            var at = tries[tq][0], y = y0w + hw * tries[tq][1];
            if (at < gap[0] || at > gap[1]) continue;
            var w3 = segPoint(s, at, 0.25, y), p = scr(w3[0], w3[1], w3[2]);
            if (!onCanvas(p)) continue;
            var rw = rayWhat(p), nn = rw.pt ? nearestSegment(rw.pt.x, rw.pt.z, 'window') : null;
            var ok = !!(nn && nn.seg && nn.seg.id === s.id && nn.dist < 0.8);
            var cand = { s: s, at: at, p: p, room: gap[1] - gap[0], ok: ok, what: rw.what + (nn ? (' ' + nn.dist.toFixed(2) + ' m from ' + nn.seg.id) : '') };
            if (!best || (ok && !best.ok) || (ok === best.ok && cand.room > best.room)) best = cand;
            if (ok) break;
          }
        }
        if (!best) { skip('window add', 'no wall facing the camera with room for a ' + Math.round(tpl.w * 1000) + ' window'); next(); return; }
        var n0 = (STATE.openings || []).length;
        taps('window add', 1, function (k, c) {
          if (!best.ok) note(c, 'no clear view of a wall -- tapped anyway, the ray meets ' + best.what);
          armOpening({ kind: tpl.kind, w: tpl.w, h: tpl.h, sill: tpl.sill, code: tpl.code, hand: tpl.hand, leaves: tpl.leaves });
          fire('pointerdown', best.p[0], best.p[1]);
          fire('pointerup', best.p[0], best.p[1]);
          if ((STATE.openings || []).length > n0) { added = STATE.openings[STATE.openings.length - 1]; note(c, 'on ' + added.seg + ' at ' + Math.round(added.at * 1000)); }
          else { note(c, 'the tap placed nothing -- aimed at ' + best.s.id + ' at ' + Math.round(best.at * 1000) + ', the ray meets ' + best.what + (_armed ? ', still armed' : ', disarmed')); armOpening(null); }
        }, 1400, next);
      });
      step('window drag', function (next) {
        if (!added) { skip('window drag', 'no window was put in'); next(); return; }
        var sg = segment(added.seg);
        if (!sg) { skip('window drag', 'its wall is gone'); next(); return; }
        try { if (!opEdit()) opEdit(true); } catch (eO) {}
        var y = (sg.y0 || 0) + (added.sill || 0) + (added.h || 1) / 2, a0 = added.at;
        var w0 = segPoint(sg, a0, 0.2, y), p0 = scr(w0[0], w0[1], w0[2]);
        var Aw = amp(0.5, 48, w0[0], y, w0[2], (sg.x1 - sg.x0) / sg.len, (sg.z1 - sg.z0) / sg.len);
        _opLast = null;
        drag('window drag', p0, function (t) { var w = segPoint(sg, a0 + Aw * Math.sin(2 * Math.PI * t), 0.2, y); return scr(w[0], w[1], w[2]); }, 3000, next,
             { took: function () { return _opLast === added; }, miss: function () { return _sillNote; } });
      });
      step('window remove', function (next) {
        if (!added) { skip('window remove', 'no window was put in'); next(); return; }
        taps('window remove', 1, function () { removeOpening(added); added = null; }, 1200, function () {
          try { if (opEdit()) opEdit(false); } catch (eO) {}
          later(600, next);
        });
      });
      /* ---- the plan strip, 2D ---- */
      step('open the plan', function (next) {
        if (_SX.sel) { _SX.sel = null; try { _sxAfter('the bench'); } catch (eA) {} }
        taps('open the plan', 1, function (k, c) {
          _cmdPlanView(true, 'the bench');
          if (STATE.view !== '2d') note(c, 'the view stayed ' + STATE.view);
        }, 1600, next);
      });
      step('plan unlock', function (next) {
        if (!_sxPlanOpen()) { skip('plan unlock', 'the plan strip is not open'); next(); return; }
        taps('plan unlock', 1, function () { if (_SX.lock) _sxLock(); }, 1000, next);
      });
      step('plan wall drag', function (next) {
        var rs = _rects() || [], r = null, i;
        for (i = 0; i < rs.length; i++) if (rs[i].key === 'house') r = rs[i];
        if (!r) { skip('plan wall drag', 'no house outline to grab (' + rs.map(function (q) { return q.key; }).join(', ') + ')'); next(); return; }
        var y = (r.y != null) ? r.y : ((_handles && _handles._y) || 0.04), g = gripOf(r, y, 'mid');
        if (!g) { skip('plan wall drag', 'no grip of the outline on the screen'); next(); return; }
        var Aq = amp(0.6, 48, g.x, y, g.z, g.dx, g.dz);
        drag('plan wall drag', g.p, function (t) { var d = Aq * Math.sin(2 * Math.PI * t); return scr(g.x + g.dx * d, y, g.z + g.dz * d); }, 3000, next,
             { took: function () { return !!(_handles && _handles._drag); }, miss: function () { return 'grip ' + g.k; } });
      });
      step('plan partition drag', function (next) {
        var PL = _P(), ps = [], i, f, sv = STATE.planStorey | 0, FR = [0.5, 0.35, 0.65, 0.25, 0.75];
        try { ps = _normParts(); } catch (eN) { ps = []; }
        var grab = 0.2;
        for (i = 0; i < ps.length && !partPt; i++) {
          var p = ps[i], sp = null;
          if (!p || p.sep || ((p.st | 0) !== sv && !p.bear)) continue;
          try { sp = _partSpanH(p); } catch (eS) { sp = null; }
          if (!sp || sp[1] - sp[0] < 1.4) continue;
          var vert = ((p.dir || p.o) === 'v');
          for (f = 0; f < FR.length && !partPt; f++) {
            var u = sp[0] + (sp[1] - sp[0]) * FR[f], x = vert ? p.at : u, z = vert ? u : p.at;
            var hitP = PL ? _ask(PL, 'partAt', x, z, grab) : null;
            if (hitP === null || hitP < 0) continue;
            if (PL && PL.fixtures && _ask(PL, 'fixtureAt', x, z)) continue;
            var s = scr(x, 0, z);
            if (!onCanvas(s) || nearGrips(s) < 40) continue;
            partPt = { x: x, z: z, vert: vert, p: s, sp: sp, at: p.at, f: FR[f] };
          }
        }
        if (!partPt) { skip('plan partition drag', ps.length ? 'no partition free to grab on the screen' : 'no partitions'); next(); return; }
        var Ap = amp(0.4, 48, partPt.x, 0, partPt.z, partPt.vert ? 1 : 0, partPt.vert ? 0 : 1);
        drag('plan partition drag', partPt.p, function (t) {
          var d = Ap * Math.sin(2 * Math.PI * t);
          return partPt.vert ? scr(partPt.x + d, 0, partPt.z) : scr(partPt.x, 0, partPt.z + d);
        }, 3000, next, { took: function () { return !!_partDrag; }, miss: function () { return _planTaps.last; } });
      });
      step('plan door add', function (next) {
        var PL = _P();
        if (!PL || !PL.addDoorAt) { skip('plan door add', 'the plan asset has no addDoorAt'); next(); return; }
        /* A SPOT THAT TAKES A DOOR (core-v609): every partition on the plan,
           five points along each, asked of the asset dry -- addDoorAt, then
           the doors put back as they were -- so the tap lands where a door
           fits; a door already there or a grip of the outline is passed by */
        var ps = [], FR = [0.5, 0.3, 0.7, 0.2, 0.8], i, f, q = null, sv = STATE.planStorey | 0, keep = JSON.stringify(STATE.doors || []), tried = 0;
        try { ps = _normParts(); } catch (eN) { ps = []; }
        for (i = 0; i < ps.length && !q; i++) {
          var pr = ps[i], sp = null;
          if (!pr || pr.sep || ((pr.st | 0) !== sv && !pr.bear)) continue;
          try { sp = _partSpanH(pr); } catch (eS) { sp = null; }
          if (!sp || sp[1] - sp[0] < 1.0) continue;
          var vert = ((pr.dir || pr.o) === 'v');
          for (f = 0; f < FR.length && !q; f++) {
            var u = sp[0] + (sp[1] - sp[0]) * FR[f], x = vert ? pr.at : u, z = vert ? u : pr.at;
            if (_ask(PL, 'doorAt', x, z)) continue;
            var s = scr(x, 0, z);
            if (!onCanvas(s) || nearGrips(s) < 40) continue;
            var got = null;
            tried++;
            try { got = PL.addDoorAt(x, z); } catch (eD) { got = null; }
            try { STATE.doors = JSON.parse(keep); _normDoors(); } catch (eK) {}
            if (got) q = { x: x, z: z, p: s, vert: vert };
          }
        }
        if (!q) { skip('plan door add', tried ? ('no partition takes a door (' + tried + ' spot(s) asked)') : 'no partition free on the screen'); next(); return; }
        var n0 = (STATE.doors || []).length;
        taps('plan door add', 1, function (k, c) {
          STATE.doorTool = true;
          fire('pointerdown', q.p[0], q.p[1]);
          fire('pointerup', q.p[0], q.p[1]);
          var n1 = (STATE.doors || []).length;
          note(c, (n1 > n0) ? 'a door in, ' + n1 + ' now' : ('no door placed (' + _planTaps.last + ')'));
          if (n1 > n0) { partPt = partPt || {}; partPt.door = q; }
        }, 1400, next);
      });
      step('plan door drag', function (next) {
        var q = partPt && partPt.door;
        if (!q) { STATE.doorTool = false; skip('plan door drag', 'no door was put in'); next(); return; }
        var Ad = amp(0.4, 48, q.x, 0, q.z, q.vert ? 0 : 1, q.vert ? 1 : 0);
        drag('plan door drag', q.p, function (t) {
          var d = Ad * Math.sin(2 * Math.PI * t);
          return q.vert ? scr(q.x, 0, q.z + d) : scr(q.x + d, 0, q.z);
        }, 3000, function () { STATE.doorTool = false; try { _sxRefresh(); } catch (eR) {} next(); },
             { took: function () { return !!_doorDrag || !!_doorEnd; }, miss: function () { return _planTaps.last; } });
      });
      step('plan window drag', function (next) {
        /* the window on the plan farthest from the outline's grips */
        var L = STATE.openings || [], i, best = null;
        for (i = 0; i < L.length; i++) {
          var o = L[i], sgo = (o && o.seg && !o.host && o.kind === 'window') ? segment(o.seg) : null;
          if (!sgo) continue;
          [0, -0.3, 0.3].forEach(function (k) {
            var off = k * (o.w || 1), wo = segPoint(sgo, o.at + off, 0, 0), po = scr(wo[0], 0, wo[2]);
            if (!onCanvas(po)) return;
            var dg = nearGrips(po);
            if (!best || dg > best.dg) best = { o: o, sg: sgo, p: po, dg: dg, off: off };
          });
        }
        if (!best) { skip('plan window drag', 'no window on the screen'); next(); return; }
        if (best.dg < 36) { skip('plan window drag', 'every window sits under a grip of the outline (' + Math.round(best.dg) + ' px)'); next(); return; }
        var rec = best.o, sg = best.sg, a0 = rec.at + best.off;
        _opLast = null;
        var Aww = amp(0.5, 48, best.p ? segPoint(sg, a0, 0, 0)[0] : 0, 0, segPoint(sg, a0, 0, 0)[2], (sg.x1 - sg.x0) / sg.len, (sg.z1 - sg.z0) / sg.len);
        drag('plan window drag', best.p, function (t) { var w = segPoint(sg, a0 + Aww * Math.sin(2 * Math.PI * t), 0, 0); return scr(w[0], 0, w[2]); }, 3000, next,
             { took: function () { return _opLast === rec; }, miss: function () { return _planTaps.last; } });
      });
      step('plan cross gables', function (next) {
        if (!_sxPlanOpen()) { skip('plan cross gables', 'the plan strip is not open'); next(); return; }
        if (_SX.lock) { try { _sxLock(); } catch (eL) {} }
        /* core-v628: the places' icons are up while the cross gables' step is picked */
        try {
          var xs = xgKey();
          if (xs && _SX.sel !== xs) _sxTap(xs, null, { pointerType: 'mouse' });
        } catch (eXs) {}
        later(500, function () {
          /* ranch-v44: each place's round on / off icon (div[data-xg]); the
             v43 triangles (svg g[data-xg]) when the product still has them */
          var all = [], seen = {}, tg = [];
          try { all = Array.prototype.slice.call(global.document.querySelectorAll('[data-xg]')); } catch (eQ) { all = []; }
          all.forEach(function (el) {
            var key = el.getAttribute('data-xg'), R = el.getBoundingClientRect(), pe = '';
            try { pe = global.getComputedStyle(el).pointerEvents; } catch (eC) { pe = ''; }
            if (!key || seen[key] || !(R.width > 0 && R.height > 0) || pe === 'none') return;
            seen[key] = 1; tg.push(key);
          });
          if (!tg.length) { skip('plan cross gables', all.length ? (all.length + ' mark(s), none taking a tap') : ('no on / off icons on the plan (cross gables ' + (xgOnList() || 'not in this product') + ')')); next(); return; }
          tg = tg.slice(0, 2);
          function elOf(key) {
            var L = [], i;
            try { L = global.document.querySelectorAll('[data-xg="' + key + '"]'); } catch (eS) { L = []; }
            for (i = 0; i < L.length; i++) {
              var R = L[i].getBoundingClientRect(), pe = '';
              try { pe = global.getComputedStyle(L[i]).pointerEvents; } catch (eC) { pe = ''; }
              if (R.width > 0 && R.height > 0 && pe !== 'none') return L[i];
            }
            return null;
          }
          taps('plan cross gables (' + tg.length + ' place(s), switched and back)', tg.length * 2, function (k, c) {
            var key = tg[(k - 1) >> 1], el = elOf(key), lab = key + ' tap ' + (((k - 1) % 2) + 1);
            if (!el) { note(c, lab + ': its icon is gone'); return; }
            var R = el.getBoundingClientRect(), x = R.left + R.width / 2, y = R.top + R.height / 2;
            /* what a finger there really meets: the icon, or whatever lies over it */
            var top = null;
            try { top = global.document.elementFromPoint(x, y); } catch (eF) { top = null; }
            var onIt = !!(top && (top === el || el.contains(top)));
            if (!onIt) {
              /* what lies over it, named with its parents (id, class, z-index) */
              var dsc = function (e2) {
                if (!e2) return 'nothing';
                var z = ''; try { z = global.getComputedStyle(e2).zIndex; } catch (eZ) { z = ''; }
                return e2.tagName.toLowerCase() + (e2.id ? ('#' + e2.id) : '') + ((e2.className && typeof e2.className === 'string') ? ('.' + e2.className.trim().split(/\s+/).slice(0, 2).join('.')) : '') +
                       (e2.getAttribute && e2.getAttribute('data-ranch-xg') ? '[data-ranch-xg]' : '') + (z && z !== 'auto' ? (' z' + z) : '');
              };
              note(c, lab + ': NOT tapped -- at ' + Math.round(x) + ',' + Math.round(y) + ' a finger meets ' + dsc(top) + (top ? (' in ' + dsc(top.parentElement) + ' in ' + dsc(top.parentElement && top.parentElement.parentElement)) : '') +
                   ', the icon is ' + dsc(el) + ' in ' + dsc(el.parentElement) + ' in ' + dsc(el.parentElement && el.parentElement.parentElement));
              return;
            }
            var s0 = prodSig(), on0 = xgOnList();
            fireOn(top, 'pointerdown', x, y);
            fireOn(top, 'pointermove', x + 2, y + 1);
            fireOn(top, 'pointerup', x + 2, y + 1);
            note(c, lab + (prodSig() !== s0 ? (': switched, on ' + on0 + ' -> ' + xgOnList()) : ': NOT switched (the icon had the tap)'));
          }, 1600, next);
        });
      });
      step('close the plan', function (next) {
        if (!_sxPlanOpen()) { skip('close the plan', 'the plan strip is not open'); next(); return; }
        taps('close the plan', 1, function (k, c) { _sxPlan(false, 'the bench'); if (STATE.view === '2d') note(c, 'the view stayed 2d'); }, 1600, next);
      });

      function wrapUp(why) {
        if (finished) return;
        finished = true;
        if (_BN.wcur) end(_BN.wcur);
        R.render = orig;
        try { STATE.doorTool = false; armOpening(null); } catch (e1) {}
        try { if (opEdit()) opEdit(false); } catch (e2) {}
        try { if (_sxPlanOpen()) _sxPlan(false, 'the bench ended'); } catch (e3) {}
        try { _SX.lock = true; _SX.sel = null; } catch (e3b) {}
        try { if (_wiz && _wiz.isOpen()) _wiz.close(); } catch (e4) {}
        try { if (STATE.view === '2d') setView('3d'); } catch (e5) {}
        try { if (snap) _applyPayload(JSON.parse(snap), 'the bench (the project as it was before the walk)'); } catch (e6) { _log('bench    putting the project back threw ' + msg(e6)); }
        try { rebuild(); drawPlan(); savePref(); undoPush(); } catch (e7) {}
        /* and says whether it is the same project again, key by key */
        var back = 'the project put back';
        try {
          if (snap) {
            var A = JSON.parse(snap), B = JSON.parse(JSON.stringify(payload())), dk = [], kk;
            for (kk in A) if (A.hasOwnProperty(kk) && JSON.stringify(A[kk]) !== JSON.stringify(B[kk])) dk.push(kk);
            back = dk.length ? ('the project put back, but it differs in ' + dk.join(', ')) : 'the project put back, identical';
          } else back = 'the project could not be copied at the start -- NOT put back';
        } catch (e9) { back = 'the project check threw ' + msg(e9); }
        _BN.wback = back;
        try { st.camera.position.copy(cam0); if (tg0 && st.controls) { st.controls.target.copy(tg0); st.camera.lookAt(tg0); st.controls.dispatchEvent({ type: 'change' }); } } catch (e8) {}
        _BN.wms = _pfNow() - _BN.wt0;
        _BN.wstate = 'done';
        _log('bench    work walk done in ' + Math.round(_BN.wms / 1000) + ' s -- ' + why + '; ' + _BN.wback);
        global.setTimeout(function () { if (fin) fin(); }, 1200);
      }
      global.setTimeout(function () { if (!finished) wrapUp('stopped after 4 min'); }, 240000);
      run();
    }

    function _benchLines() {
      if (_BN.state === 'off') return _benchWanted() ? ['bench  waiting for the first model'] : [];
      var out = [];
      if (_BN.state !== 'done') out.push('bench  ' + _BN.state + ' (' + _BN.res.length + ' view part(s), ' + _BN.work.length + ' work part(s) measured so far' + (_BN.at ? (', now: ' + _BN.at) : '') + ')');
      function r0(x) { return Math.round(x || 0); }
      function st2(a) {
        if (!a || !a.length) return null;
        var b = a.slice().sort(function (p, q) { return p - q; }), s = 0, k;
        for (k = 0; k < b.length; k++) s += b[k];
        return { n: b.length, avg: s / b.length, p95: b[Math.min(b.length - 1, Math.floor(b.length * 0.95))], max: b[b.length - 1] };
      }
      if (_BN.res.length && _BN.ms) {
        out.push('bench  ran ' + r0(_BN.ms / 1000) + ' s from +' + r0(_BN.t0 / 1000) + ' s (core-v602) -- one line a part: frame time, the render call, the GPU load, the main thread');
        _BN.res.forEach(function (c) {
          var f = st2(c.dt), rd = st2(c.rend);
          out.push('bench  ' + c.name + ' ' + (c.ms / 1000).toFixed(1) + ' s: ' + (f ? (r0(f.avg) + ' ms a frame = ' + r0(1000 / Math.max(1, f.avg)) + ' fps, p95 ' + r0(f.p95) + ', worst ' + r0(f.max)) : 'no frames') +
                   ' | render ' + (rd ? (r0(rd.avg) + ' ms (' + rd.n + ' renders)') : 'none') + ' | ' + c.calls + ' draw calls, ' + Math.round(c.tris / 1000) + 'k triangles' +
                   ' | long tasks ' + c.lt + ' = ' + r0(c.lts) + ' ms' + (c.pp ? (' | plan paints ' + c.pp + ' x ' + r0(c.pps / c.pp) + ' ms') : ''));
        });
        out.push('bench  rebuild ' + _BN.rebuild.length + ' x: ' + _BN.rebuild.map(function (x) { return r0(x) + ' ms'; }).join(', '));
      }
      if (_BN.work.length || _BN.wstate) {
        out.push('bench  WORK ' + (_BN.wstate || '?') + ' in ' + r0((_BN.wms || (_pfNow() - _BN.wt0)) / 1000) + ' s' + (_BN.wback ? (', ' + _BN.wback) : '') + ' (' + CORE_VERSION + ') -- a line a part: how long a tap / a move held the page, frames under the finger, the builds and where their time went (layers, bill, plan, rest), the first frame after a build');
        _BN.work.forEach(function (c) {
          if (c.skip) { out.push('bench  W ' + c.name + ' -- skipped: ' + c.skip); return; }
          var f = st2(c.dt), e = st2(c.ev), nf = st2(c.nf), fr = st2(c.fresh), b = c.b, s;
          s = 'bench  W ' + c.name + ' ' + ((c.ms || 0) / 1000).toFixed(1) + ' s: ';
          if (c.kind === 'tap') s += (e ? ('a tap held the page ' + r0(e.avg) + ' ms' + (e.n > 1 ? (' (worst ' + r0(e.max) + ')') : '')) : 'no tap') + (nf ? (', the new picture ' + r0(nf.avg) + ' ms after the tap') : '');
          else s += (e ? (e.n + ' move(s), a move held the page ' + r0(e.avg) + ' ms (worst ' + r0(e.max) + ')') : 'no moves') +
                    (f ? (', ' + r0(1000 / Math.max(1, f.avg)) + ' fps under the finger (p95 ' + r0(f.p95) + ' ms)') : '') +
                    ((c.dn != null || c.up != null) ? (' | press ' + r0(c.dn) + ' ms, let go ' + r0(c.up) + ' ms' + (nf ? (', picture ' + r0(nf.avg) + ' ms after') : '')) : '');
          if (b.n) {
            var ls = [], k;
            for (k in b.L) if (b.L.hasOwnProperty(k)) ls.push([k, b.L[k]]);
            ls.sort(function (p, q) { return q[1] - p[1]; });
            s += ' | ' + b.n + ' build(s) x ' + r0(b.ms / b.n) + ' ms (worst ' + r0(b.max) + '): layers ' + r0(b.lay / b.n) +
                 (ls.length ? (' (' + ls.slice(0, 3).map(function (x) { return String(x[0]).replace(/^[a-z]+-/, '') + ' ' + r0(x[1] / b.n); }).join(', ') + ')') : '') +
                 ', bill ' + r0(b.boq / b.n) + ', plan ' + r0(b.plan / b.n) + ', rest ' + r0((b.ms - b.lay - b.boq - b.plan) / b.n);
            var rs2 = [], k2;
            for (k2 in b.R) if (b.R.hasOwnProperty(k2) && b.R[k2] / b.n >= 3) rs2.push([k2, b.R[k2]]);
            rs2.sort(function (p, q) { return q[1] - p[1]; });
            if (rs2.length) s += ' (' + rs2.slice(0, 4).map(function (x) { return x[0] + ' ' + r0(x[1] / b.n); }).join(', ') + ')';
          } else s += ' | no build';
          if (fr) s += ' | first frame after a build ' + r0(fr.avg) + ' ms';
          if (c.pp) s += ' | plan paints ' + c.pp + ' x ' + r0(c.pps / c.pp) + ' ms';
          s += ' | long tasks ' + c.lt + ' = ' + r0(c.lts) + ' ms';
          if (c.note) s += ' | ' + c.note;
          out.push(s);
        });
      }
      if (_BN.sent) out.push('bench  ' + _BN.sent);
      return out;
    }

    var _storeSeen = '';
    /* core-v570: the project's loads still on the way (the short link, the
       default project) -- the homepage's plan waits for them */
    var _initLoads = 0;
    function loadPref() {
      try { _loadPref0(); } finally { _shareHoldWatch(); _bootCoverWatch(); }
    }
    /* core-v809 (TWO STORY thread, Marek 2026-11-03: "a to nacitani opravit
       vsude"): A PROJECT THAT COMES OVER THE NETWORK -- the short link ?p=, the
       deflated #c=, the product's default project -- showed the plain defaults
       first and then jumped to it. While one is on its way the stage is covered
       (the page's own surface, a small ring turning) and uncovered two frames
       after it is applied and rebuilt, or after 4 s whatever happens; nothing on
       the way, nothing is covered. Every product, no hook. */
    function _bootCoverWatch() {
      if (!(_initLoads > 0 || _hashWait)) return;
      /* the brand's own values (UX.bg, UX.line, UX.sand -- UX lives in the
         brand block, outside this scope) */
      var BC = { bg: '#F5F5F5', line: '#DADCE0', sand: '#D2A878' };
      /* 4 s of the page's FREE time, not of the clock: the first build holds
         the thread for seconds on a phone, and a clock deadline ran out inside
         it and uncovered the plain model while the project was still coming */
      var t0 = Date.now(), tLast = t0, free = 0, el = null;
      function put() {
        if (el) return;
        var cv = $('cf-canvas'), host = cv && cv.parentNode;
        if (!host || !global.document) return;
        try {
          if (!$('cf-boot-cover-kf')) {
            var st = document.createElement('style');
            st.id = 'cf-boot-cover-kf';
            st.textContent = '@keyframes cfBootSpin{to{transform:rotate(360deg)}}';
            (document.head || document.body).appendChild(st);
          }
          if (global.getComputedStyle && global.getComputedStyle(host).position === 'static') host.style.position = 'relative';
          el = document.createElement('div');
          el.id = 'cf-boot-cover';
          el.style.cssText = 'position:absolute;left:0;top:0;right:0;bottom:0;z-index:4;background:' + BC.bg + ';display:flex;align-items:center;justify-content:center;pointer-events:none;transition:opacity .25s ease;opacity:1';
          el.innerHTML = '<div style="width:34px;height:34px;border-radius:50%;border:3px solid ' + BC.line + ';border-top-color:' + BC.sand + ';animation:cfBootSpin .9s linear infinite"></div>';
          host.appendChild(el);
        } catch (eC) { el = null; if (!put.said) { put.said = 1; _log('boot cover not put: ' + ((eC && eC.message) || eC)); } }
      }
      function off(why) {
        _log('boot cover off -- ' + why + ' (' + (Date.now() - t0) + ' ms)');
        var raf = global.requestAnimationFrame || function (f) { return global.setTimeout(f, 16); };
        raf(function () { raf(function () {
          if (!el) return;
          el.style.opacity = '0';
          global.setTimeout(function () { if (el && el.parentNode) el.parentNode.removeChild(el); el = null; }, 280);
        }); });
      }
      (function tick() {
        put();
        if (!(_initLoads > 0 || _hashWait)) { off('the project is in'); return; }
        var tNow = Date.now(); free += Math.min(200, tNow - tLast); tLast = tNow;
        if (free > 4000) { off('4 s -- shown as it stands'); return; }
        global.setTimeout(tick, 60);
      })();
    }
    function _loadPref0() {
      /* the short link first: fetched, applied with a rebuild when it lands */
      var hasP = false;
      try { hasP = !!new URL(global.location.href).searchParams.get('p'); } catch (eP) { hasP = false; }
      if (hasP) {
        _storeSeen = 'link';
        _initLoads++;
        _shareRead().then(function (p) {
          _initLoads = Math.max(0, _initLoads - 1);
          if (!p) return;
          _applyPayload(p, 'the short link (?p)');
          try { global.localStorage.setItem(_prefKey, JSON.stringify(payload())); } catch (eL3) {}
          try { rebuild(); drawPlan(); } catch (eR3) {}
        });
      }
      /* then the hash forms: what was shared is what is wanted */
      var fromLink = _hashRead();
      if (fromLink) {
        _applyPayload(fromLink, 'the link (#cfg)');
        try { global.localStorage.setItem(_prefKey, JSON.stringify(payload())); } catch (eL) {}
        _storeSeen = 'link';
        return;
      }
      var hashC = false;
      if (_hashHasDeflated()) {
        /* applied as soon as it inflates; the first build runs on the local
           copy and is replaced a moment later */
        hashC = true;
        _storeSeen = 'link';
        _hashWait = true;
        _hashReadDeflated().then(function (p) {
          _hashWait = false;
          if (!p) { _log('link config (#c) unreadable'); return; }
          _applyPayload(p, 'the link (#c, deflated)');
          try { global.localStorage.setItem(_prefKey, JSON.stringify(payload())); } catch (eL2) {}
          try { rebuild(); drawPlan(); } catch (eR) {}
        });
      }
      /* core-v752: a recipe from ANY HOUSE starts from the product's default
         project, not from what this browser kept (_recipeIntake) */
      var rcp = !!_recipeRead();
      if (rcp) { _storeSeen = 'link'; _log('recipe   link: the default project, this browser\'s own project left alone'); }
      var local = null;
      try {
        var raw = rcp ? null : global.localStorage.getItem(_prefKey);
        if (raw) local = JSON.parse(raw);
      } catch (e) { _log('prefs restore failed: ' + (e && e.message)); }
      var S = rcp ? null : _store(), rec = null;
      try { rec = S ? S.read(STORE_KEY) : null; } catch (e2) { rec = null; }
      if (rec && rec.payload && rec.product === PRODUCT) {
        _applyPayload(rec.payload, 'project store');
        _storeSeen = rec.updatedAt || '';
        return;
      }
      if (local) { _applyPayload(local, 'localStorage'); return; }
      /* NOTHING OF THEIR OWN YET: the product's default project, a fixed
         snapshot on leva-share (an id never changes; what a visitor then does
         is saved under new ids), so the first thing on screen is a house with
         its windows and not the bare defaults (Marek). */
      var dp = (typeof DEFAULT_PROJECT === 'string') ? DEFAULT_PROJECT : '';
      /* core-v809: nor under a #c= link -- the default came in after the
         link had inflated and replaced it (a fresh browser opening a link) */
      if (dp && !hasP && !hashC) {
        _storeSeen = rcp ? 'link' : 'default';
        _initLoads++;
        global.fetch(_shareWorker() + '/s/' + dp)
          .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json(); })
          .then(function (p) {
            _initLoads = Math.max(0, _initLoads - 1);
            if (!p || !p.footprint) return;
            _applyPayload(p, 'the default project (' + dp + ')');
            _shareDef = dp;
            try { rebuild(); drawPlan(); } catch (eR4) {}
            _log('default project ' + dp + ' applied -- the visitor had nothing of their own');
          })
          .catch(function (err) { _initLoads = Math.max(0, _initLoads - 1); _log('default project ' + dp + ' not read: ' + (err && err.message)); });
      }
    }
    /* the store arrives after the lib on most loads: watch for it a while,
       take its config once, then keep our own writes flowing to it */
    function _storeWatch() {
      var tries = 0;
      var t = global.setInterval(function () {
        tries++;
        var S = _store();
        if (!S) { if (tries > 40) global.clearInterval(t); return; }
        global.clearInterval(t);
        var rec = null;
        try { rec = S.read(STORE_KEY); } catch (e) { rec = null; }
        if (rec && rec.payload && rec.product === PRODUCT && _storeSeen !== 'link' && (rec.updatedAt || '') !== _storeSeen) {
          _applyPayload(rec.payload, 'project store (late)');
          _storeSeen = rec.updatedAt || '';
          try { rebuild(); drawPlan(); } catch (eR) {}
        } else {
          /* nothing stored yet for this project: put ours there so the link has it */
          _storeWrite();
        }
      }, 500);
    }

    var _bootFresh = null;
    function reset() {
      var met = !!STATE.isMetric, fresh = JSON.parse(_bootFresh || JSON.stringify(DEFAULTS)), k;
      for (k in fresh) if (fresh.hasOwnProperty(k)) STATE[k] = fresh[k];
      /* core-v797: the visitor's units stay (the switch also moves the budget and rebuilds) */
      if (!!STATE.isMetric !== met) toggleUnits();
      else { savePref(); rebuild(); }
      try { if (typeof _wiz !== 'undefined' && _wiz && _wiz.refresh) _wiz.refresh(); } catch (eW) {}
      _log('reset -> the product\'s own design' + (_bootFresh ? '' : ' (no boot state: the core\'s defaults)'));
    }

    /* THE BUDGET'S SWITCH IS THE SAME SWITCH.

       Flipping here changed the model and left the budget on whatever it had
       -- two switches for one decision, and the drawer, which reads the
       budget's, then disagreed with the model. The budget keeps its choice on
       #unitSelector[data-current-unit] and repaints on a 'unitChange' event,
       so both are set here. Written only when it actually differs, so this
       cannot ping-pong with the budget's own toggle (Marek: neovlivni
       prepinaci tlacitko v budget draweru). */
    function _syncBudgetUnits() {
      try {
        var el = global.document.getElementById('unitSelector');
        if (!el) return;
        var want = STATE.isMetric ? 'metric' : 'imperial';
        if ((el.getAttribute('data-current-unit') || '') === want) return;
        el.setAttribute('data-current-unit', want);
        global.dispatchEvent(new global.CustomEvent('unitChange', { detail: { unit: want } }));
        try { global.document.dispatchEvent(new global.CustomEvent('budget-unit-change')); } catch (e2) {}
        _log('units: budget switched to ' + want + ' with the model');
      } catch (e) { _log('units: could not switch the budget -- ' + ((e && e.message) || e)); }
    }

    function toggleUnits() {
      STATE.isMetric = !STATE.isMetric;
      _syncBudgetUnits();
      /* a paved terrace is whole tiles of the unit's size: re-snap on the flip */
      try { if (typeof _terraceSnapToTiles === 'function') _terraceSnapToTiles(); } catch (eTS) {}
      STATE.footprint.w = clamp(snapDim(STATE.footprint.w, 'w', { min: LIMITS.minW, max: LIMITS.maxW }), LIMITS.minW, LIMITS.maxW);
      STATE.footprint.l = clamp(snapDim(STATE.footprint.l, 'l', { min: LIMITS.minL, max: LIMITS.maxL }), LIMITS.minL, LIMITS.maxL);
      STATE.clearH[0] = snapClear(STATE.clearH[0]);
      STATE.clearH[1] = snapClear(STATE.clearH[1]);
      var st = _stage();
      if (st) st.rebuildGrid({ size: 60, step: STATE.isMetric ? 1.0 : FT });
      savePref();
      rebuild();
    }

    /* NO SLAB ON THIS PRODUCT. Its floor is a timber deck 350 above grade on
       every footing, and a slab with a deck on top is not a thing anyone
       builds; the slab comes back with a concrete floor build-up (the masonry
       house). The strips are a crawl space: a stem wall carries the deck. */
    var FOUNDATIONS = ['screws', 'pads', 'piers', 'strips'];

    /* THE FLOOR STANDS HIGHER ON A CRAWL SPACE. Screws, pads and piers carry
       the deck 350 above grade; strips carry a stem wall, and a crawl space
       wants 450 clear under the joists and 150 of wall above grade -- so on
       strips the deck goes to 650. Set with the footing, and again when a
       project loads. */
    /* A PRODUCT CAN SAY WHERE ITS GROUND FLOOR IS (core-v335). The two
       figures above are a timber floor's -- a deck on screws, pads or piers, or
       over a crawl space on strips. A masonry house on strips has no crawl
       space: its floor is a slab a little above grade, and nothing in the stack
       could say so, so the walls and the roof stood half a metre higher than
       that house ever will. __PROD._productSlab(foundation) answers with the
       height from grade to the top of the ground floor, in metres; anything
       that is not a number leaves the footing's own figure. No product in the
       line answers it today except the one that needs it, so nothing else
       moves. */
    var _slabFrom = 'footing';
    function _stackForFooting() {
      STACK.slab = (STATE.foundation === 'strips') ? 0.65 : 0.35;
      _slabFrom = 'footing';
      if (typeof __PROD._productSlab === 'function') {
        var v = null;
        try { v = __PROD._productSlab(STATE.foundation); } catch (eS) { v = null; }
        if (typeof v === 'number' && isFinite(v) && v >= 0 && v < 3) { STACK.slab = v; _slabFrom = 'product'; }
      }
    }
    function setFoundation(kind) {
      if (kind === 'slab') kind = 'strips';
      if (FOUNDATIONS.indexOf(kind) < 0) return STATE.foundation;
      STATE.foundation = kind;
      _stackForFooting();
      savePref();
      rebuild();
      _log('foundation -> ' + kind + (kind === 'strips' ? (' (ground floor at +' + Math.round(STACK.slab * 1000) +
           (_slabFrom === 'product' ? ', the level this product gives)' : ', a crawl space on a stem wall)')) : ''));
      return STATE.foundation;
    }
    /* THE CLADDING DIRECTION.

       facadeThk is moved with it rather than left alone: it is what the roof
       overhang, the eave and the gutter are all measured from, so a wall that
       grows 40 mm and a facadeThk that does not means an eave that stops 40 mm
       short of the wall it shelters. The base figure is kept so the two
       directions can be switched back and forth without the number drifting. */
    /* THREE STATES ON ONE TILE: OFF, ACROSS, UP.

       Off is not a way of looking at the building, it is a building without
       cladding -- nothing drawn, nothing billed, and the facade is thinner by
       exactly the board. That is why facadeThk moves for it as well as for the
       counter-batten: the roof overhang, the eave and the gutter are all
       measured from that number, so a facade that loses 15 mm and an eave that
       does not is an eave hanging 15 mm proud of the wall it shelters.

       CLAD_T is here rather than in the wall zone because both ends need it --
       the facade thickness up here and the board down there -- and a thickness
       written in two places is the fault this file has paid for more than any
       other. */
    var CLADDINGS = ['off', 'horizontal', 'vertical'];
    var CLAD_T = 0.015;

    /* THE FACADE IS AS THICK AS WHAT IS DRAWN. ONE NUMBER, ONE PLACE.

       This returned a base of 145 with a board taken off or a counter-batten
       put on -- a figure from a build-up that no longer exists. The wall draws
       75 for boards across, 115 for boards up and about 171 for the render,
       so the roof measured its overhang from a face that was 70 mm outside the
       boards and 26 mm inside the render: an overhang of 100 was 170 on one
       wall and 74 on the other, and the last truss came through the boards
       while it hid in the render.

       So the thickness is the sum of the layers the wall zone actually draws,
       read from that zone's own constants, and _facadeFace down there asks
       this rather than adding them up a second time. Read at build and switch
       time, never while the file loads, so the constants are all there. */
    function _cladThk(kind) {
      /* core-v77: A PRODUCT MAY OWN A CLADDING THE CORE HAS NEVER HEARD OF.
         The arithmetic below is the house line's two answers -- boards on
         battens, or a contact facade -- and a fork can have a third. Asked the
         same way the fitout hooks are asked, so a product that says nothing
         behaves exactly as before. */
      if (typeof __PROD._productCladThk === 'function') {
        var pv = __PROD._productCladThk(kind);
        if (pv != null) return pv;
      }
      var osb = 0.018 + 0.001;
      if (kind === 'off') {
        return osb + __PROD.WALL_EPS.t + __PROD.WALL_COAT.gap + __PROD.WALL_COAT.t +
               __PROD.WALL_REND.pt + __PROD.WALL_REND.t;
      }
      return osb + __PROD.WALL_MEMB.t + __PROD.WALL_FBATT.d +
             (kind === 'vertical' ? __PROD.WALL_FBATT.d : 0) + __PROD.WALL_CLAD.t;
    }

    /* ONE FACADE TILE, ONE STATE, EVERY PRODUCT (core-v497, Marek: at UI v
       edit draweru je stejne v kazdem projektu, ne jednou dlazdice a jinde
       cyklovani). The T house kept its own facade word in product.facade and
       its own tile because the core's state started horizontal and a
       masonry house starts rendered. Now the state is unset until chosen
       and cladKind() answers with the product's own default, so the core's
       tile serves the masonry house too and the word is saved in one place
       for every product. The product's note on the log line is its own
       (__PROD._cladNote), the house line's stays the fallback. */
    function _cladDefault() {
      var d = null;
      try { d = (__PROD && typeof __PROD._claddingDefault === 'string') ? __PROD._claddingDefault : null; } catch (eD) { d = null; }
      return (d && CLADDINGS.indexOf(d) >= 0) ? d : 'horizontal';
    }
    function cladKind() {
      /* a product's own kind (the shed's panels) passes through as it did */
      var k = STATE.cladding;
      return (typeof k === 'string' && k) ? k : _cladDefault();
    }
    function setCladding(kind) {
      if (CLADDINGS.indexOf(kind) < 0) return cladKind();
      STATE.cladding = kind;
      STATE.facadeThk = _cladThk(kind);
      savePref();
      rebuild();
      var note = null;
      try { if (__PROD && typeof __PROD._cladNote === 'function') note = __PROD._cladNote(kind); } catch (eN) { note = null; }
      if (note == null) note = (kind === 'off' ? ' (battens and cavity stay, no boards)' : kind === 'vertical' ? ' (counter-batten under it)' : ' (boards cross the battens)');
      _log('cladding -> ' + kind + ', facade ' + Math.round(STATE.facadeThk * 1000) + ' mm' + note + ', saved with the project');
      return STATE.cladding;
    }

    function nextCladding() {
      var i = CLADDINGS.indexOf(cladKind());
      return setCladding(CLADDINGS[(i < 0 ? 0 : i + 1) % CLADDINGS.length]);
    }

    function nextFoundation() {
      return setFoundation(FOUNDATIONS[(FOUNDATIONS.indexOf(STATE.foundation) + 1) % FOUNDATIONS.length]);
    }

    /* WHERE THE POINT SUPPORTS GO -- round the outline and down the centre line,
       no further apart than a rim joist will carry. Every point-founded variant
       asks the same question, so it is answered once. */
    /* THE PLAN CANNOT BE WIDER THAN THE ROOF CAN CROSS.

       Clamping here rather than refusing the drag means the building stays
       buildable whatever is done to it, and the number that changed says so in
       the log rather than the wall quietly being wrong. */
    function setMaxSpan(m) {
      STATE.maxSpan = m || 0;
      return clampSpan();
    }
    function clampSpan() {
      if (!STATE.maxSpan) return false;
      var alongL = (STATE.roof.axis === 'l');
      var key = alongL ? 'w' : 'l';
      if (STATE.footprint[key] <= STATE.maxSpan + 1e-6) return false;
      var was = STATE.footprint[key];
      STATE.footprint[key] = STATE.maxSpan;
      _log('span limit: ' + key + ' ' + fmtLen(was) + ' -> ' + fmtLen(STATE.maxSpan) +
           ' (this roof cannot cross more)');
      return true;
    }

    /* AND IT FOLLOWS THE ROOF TYPE BY ITSELF. The limit used to be set only where
       the tiles called it, so changing the roof any other way left the old one in
       force -- a mono-pitch could be as wide as a trussed gable. The product
       registers the rule once and setRoofType asks it. */
    var _spanRule = null;
    function setSpanRule(fn) {
      _spanRule = fn;
      if (typeof _spanRule === 'function') setMaxSpan(_spanRule(STATE.roof.type));
    }

    function bearingPoints(maxSpacing, Pover) {
      var P = (Pover && Pover.length >= 3) ? Pover : poly(), i, j;

      /* WHERE A SUPPORT MAY STAND, AND WHERE IT MAY NOT.

         Two things were wrong and they had one cause: the points were laid out
         along each member without ever asking what ELSE was at that spot.

         NOT ON A CROSSING. A bracket takes one piece of timber between its
         cheeks. Put it where two members meet -- a corner, or where the centre
         beam lands on the rim -- and it straddles a joint and grips neither. So
         every candidate is checked against every member line, and one that sits
         on more than one steps aside along its own member until it is under a
         single piece.

         AND NOT NEXT TO ANOTHER. Insetting the ends from the corner, and running
         the centre beam out to the rim, both produced pairs a hand's width apart:
         two footings, two holes, one job. Nothing is accepted within half a bay
         of something already placed.

         The spacing is still the sheet, so what survives all that is regular. */
      var sw = sheet().w;
      var perSpan = Math.max(1, Math.floor((maxSpacing + 1e-6) / sw));
      var step = perSpan * sw;

      var x0 = 1e9, x1 = -1e9, z0 = 1e9, z1 = -1e9;
      for (i = 0; i < P.length; i++) {
        x0 = Math.min(x0, P[i][0]); x1 = Math.max(x1, P[i][0]);
        z0 = Math.min(z0, P[i][1]); z1 = Math.max(z1, P[i][1]);
      }
      var cx = (x0 + x1) / 2;

      /* every line a member runs along: the four rims and the centre beam */
      var lines = [
        { axis: 'x', at: x0 }, { axis: 'x', at: x1 }, { axis: 'x', at: cx },
        { axis: 'z', at: z0 }, { axis: 'z', at: z1 }
      ];
      var TOL = 0.15;

      /* AND THE JOISTS, which is what I had missed. They cross the centre beam
         every 600 mm, so a support under the beam at 1200 centres landed under a
         joist EVERY time -- a bracket over a crossing down the whole middle of
         the building, which is exactly the thing the rule was written to stop. I
         checked against the rims and the beam and never against the members that
         actually sit on them.

         They are not a handful of lines but a family, so the test is modular:
         how far is this point from the nearest joist. */
      var jStep = GRID();
      function onJoist(pz) {
        /* THE END JOIST IS THE RIM. The joist family starts at the same line the
           rim runs along, so testing it blindly made every support on the two
           rims parallel to the joists look like a crossing with itself -- and a
           third of them were thrown away for it. Those two lines are one member,
           not two. */
        if (Math.abs(pz - z0) < TOL || Math.abs(pz - z1) < TOL) return false;
        var m = ((pz - z0) % jStep + jStep) % jStep;
        return Math.min(m, jStep - m) < TOL;
      }

      function onLines(px, pz) {
        var n = 0, k;
        for (k = 0; k < lines.length; k++) {
          var d = (lines[k].axis === 'x') ? Math.abs(px - lines[k].at) : Math.abs(pz - lines[k].at);
          if (d < TOL) n++;
        }
        return n;
      }
      function onCount(px, pz) {
        return onLines(px, pz) + (onJoist(pz) ? 1 : 0);
      }

      var out = [];
      function accept(px, pz, ang) {
        for (var k = 0; k < out.length; k++) {
          if (Math.hypot(out[k][0] - px, out[k][1] - pz) < step * 0.5) return false;
        }
        out.push([px, pz, ang]);
        return true;
      }

      /* EQUAL SPACING, AND ONE RULE FOR WHAT IT AVOIDS.

         The supports were struck evenly and then each one that landed on a
         crossing was walked along its member by half a joist bay -- so along a
         rim square to the joists every second support moved and the run came
         out with bays of 900 and 1500 side by side, the thing he saw as
         supports "some closer, some further apart". A rim is the member the
         joists SIT ON; a support under it at a joist is not a crossing, it is
         the ordinary place for one. What must not carry a support is where two
         lines of timber meet -- a corner, or the centre beam's end on the rim --
         and under the centre beam, between the joists that cross it.

         So: from the inset at each end, the run is divided into equal bays no
         longer than the spacing. On a rim nothing moves but a support within
         reach of the centre beam's line, which steps half a bay along. The
         centre beam is set out once so that its supports fall between joists --
         one shift for all of them, not a nudge for each. */
      function run(ax, az, bx, bz, ends) {
        var ang = Math.atan2(bx - ax, bz - az);
        var len = Math.hypot(bx - ax, bz - az);
        if (len < 0.05) return;
        var ux = (bx - ax) / len, uz = (bz - az) / len;
        var inset = (ends && len > 0.9) ? 0.30 : 0;
        var usable = len - inset * 2;
        /* THE EPSILON IS NOT COSMETIC. An edge six sheets long divided by one
           sheet comes out 6.000000000000001, and ceil turns that into seven
           spans -- so a wall that divides exactly got an extra support and two
           half-length bays in the middle. */
        var n = Math.max(1, Math.ceil(usable / step - 1e-6));
        /* a rim keeps its bays EQUAL: if one of them would put a support on the
           centre beam's line, the run takes one more bay instead of moving that
           one support -- three tries, and the spacing stays one number */
        if (ends) {
          var nTry, ok9;
          for (nTry = n; nTry <= n + 2; nTry++) {
            ok9 = true;
            for (var jt = 0; jt <= nTry; jt++) {
              var tt = inset + (usable / nTry) * jt;
              if (onLines(ax + ux * tt, az + uz * tt) > 1) { ok9 = false; break; }
            }
            if (ok9) { n = nTry; break; }
          }
        }
        var sp = usable / n;
        var from = ends ? 0 : 1, to = ends ? n : n - 1;

        /* THE CENTRE BEAM STEPS IN JOIST BAYS. Its supports have to fall
           between the joists that cross it, and a spacing that is not a
           multiple of the joist bay lands on a joist somewhere along the run --
           on the imperial grid (406) half the points did, and the ones that
           could not be shifted clear were left out, which is the double gap he
           saw. So the beam's step is the largest multiple of the joist bay that
           is not over the spacing (3 x 610, 4 x 406), it starts in the middle of
           the first clear bay past the rim, and every support is between two
           joists by construction. Equal bays, none missing (Marek). */
        if (!ends) {
          var bays = Math.max(1, Math.floor((maxSpacing + 1e-6) / jStep));
          var stepB = bays * jStep;
          var first = jStep / 2;
          while (first < 0.30) first += jStep;
          for (var atB = first; atB <= len - 0.30 + 1e-6; atB += stepB) {
            accept(ax + ux * atB, az + uz * atB, ang);
          }
          return;
        }

        for (j = from; j <= to; j++) {
          var at = inset + sp * j;
          at = Math.max(Math.min(inset || 0.30, len / 2), Math.min(len - Math.min(inset || 0.30, len / 2), at));
          var px = ax + ux * at, pz = az + uz * at;
          /* a rim: only the centre beam's line is a crossing to step off */
          if (onLines(px, pz) > 1) {
            var at2 = at + ((at < len / 2) ? (jStep / 2) : (-jStep / 2));
            at2 = Math.max(0.30, Math.min(len - 0.30, at2));
            px = ax + ux * at2; pz = az + uz * at2;
            if (onLines(px, pz) > 1) continue;
          }
          accept(px, pz, ang);
        }
      }

      /* A RECTANGLE IS SET OUT AS A GRID (core-v528, Marek: u 4,6 x 6,2 mas
         zakladove prvky tesne u sebe, dlouhe strany a hned vedle nich smerem ke
         stredu; musi byt logicky rozmistene). Laid member by member, the gable
         rims took supports 300 in from the long rims and the long rims lost
         their corner ones to the half-bay rule -- uneven runs and pairs a
         forearm apart. The frame is three members along z (the two rims and
         the centre beam, the joists spanning across between them), so the
         supports are ROWS across it: equal bays along z no longer than the
         spacing, the first and last row 200 in from the ends (off the corner
         joint), one support under each of the three members in every row; the
         gable rims span between the rows' end supports like every joist. Under
         the centre beam a support that would sit on a crossing joist steps to
         the middle of the nearest joist bay. */
      var isRect = (P.length === 4) && P.every(function (q, k) { var r = P[(k + 1) % 4]; return Math.abs(q[0] - r[0]) < 1e-6 || Math.abs(q[1] - r[1]) < 1e-6; });
      if (isRect) {
        /* the rows stand in the middle of joist bays, so the centre beam's support is in line with the rims' and on no joist */
        var jT0 = 0.060, firstR = z0 + jT0 + jStep / 2, lastR;
        while (firstR < z0 + 0.20) firstR += jStep;
        lastR = firstR; while (lastR + jStep <= z1 - 0.20 + 1e-6) lastR += jStep;
        var Bt = Math.round((lastR - firstR) / jStep), maxB = Math.max(1, Math.floor((maxSpacing + 1e-6) / jStep)), nR = Math.max(1, Math.ceil(Bt / maxB - 1e-9));
        var baseB = Math.floor(Bt / nR), extra = Bt - baseB * nR, zr = firstR, rq, cq, segs = [];
        for (rq = 0; rq < nR; rq++) segs.push(baseB);
        for (rq = 0; rq < extra; rq++) segs[(rq % 2) ? (nR - 1 - Math.floor(rq / 2)) : Math.floor(rq / 2)]++;
        var rows = [firstR]; for (rq = 0; rq < nR; rq++) { zr += segs[rq] * jStep; rows.push(zr); }
        if (Bt === 0) rows = [firstR];
        for (rq = 0; rq < rows.length; rq++) for (cq = 0; cq < 3; cq++) out.push([[x0, cx, x1][cq], rows[rq], 0]);
        return out;
      }
      for (i = 0; i < P.length; i++) {
        run(P[i][0], P[i][1], P[(i + 1) % P.length][0], P[(i + 1) % P.length][1], true);
      }
      /* THE CENTRE BEAM WITHOUT ITS ENDS. Its ends are where it meets the rim,
         which is the crossing this whole rule exists to avoid -- and the rim
         already has a support either side of that point. */
      run(cx, z0, cx, z1, false);

      return out;
    }

    function setStoreys(n) {
      STATE.storeys = clamp(n | 0, 1, 2);
      savePref();
      rebuild();
      _log('storeys -> ' + STATE.storeys + ', apex ' + fmtLen(apexY()));
    }

    /* GABLE OR HIP: both are built from trusses, and half a dozen places asked
       for 'gable' by name to mean that. A second trussed roof turns every one of
       them into a place a hip falls through -- no crown, no ceiling, the skin
       sitting a rafter deep instead of a chord. One question, asked once. */
    function _isTrussed() {
      var t = STATE.roof.type;
      return t === 'gable' || t === 'hip';
    }
    /* WHICH ENDS ARE HIPPED. A hip roof may be hipped at one end only (a
       half-hip: gable at the other). roof.hipEnds = { lo, hi } names the ends
       along the ridge; a plain 'hip' with nothing set is hipped at both, a
       'gable' at neither. hipAt(end) is the one question everything asks. */
    function hipEnds() {
      var R = STATE.roof;
      if (!R.hipEnds || typeof R.hipEnds !== 'object') R.hipEnds = { lo: true, hi: true, wing: true };
      if (R.hipEnds.lo === undefined) R.hipEnds.lo = true;
      if (R.hipEnds.hi === undefined) R.hipEnds.hi = true;
      /* the wing's outer end, the third end of a T (core-v324); a project saved
         before it takes the wing hipped whenever the roof is */
      if (R.hipEnds.wing === undefined) R.hipEnds.wing = true;
      return R.hipEnds;
    }
    /* THE ENDS A ROOF HAS, AND THE ORDER THE ONE TILE WALKS THEM (core-v324).
       Two on a bar; on a T a third, the wing's. Every combination of hipped ends
       is a stage, in the order Marek set: gable, then one end at a time, then
       two at a time, then all -- 4 stages on a bar, 8 on a T. */
    function _roofStages() {
      if (STATE.wing && STATE.wing.on) {
        return [{ lo: false, hi: false, wing: false }, { lo: true, hi: false, wing: false },
                { lo: false, hi: true, wing: false }, { lo: false, hi: false, wing: true },
                { lo: true, hi: true, wing: false }, { lo: true, hi: false, wing: true },
                { lo: false, hi: true, wing: true }, { lo: true, hi: true, wing: true }];
      }
      return [{ lo: false, hi: false, wing: false }, { lo: true, hi: false, wing: false },
              { lo: false, hi: true, wing: false }, { lo: true, hi: true, wing: false }];
    }
    function roofStageLabel() {
      var k = pitchedStage(), ST = _roofStages()[k];
      if (!k) return _uiStr('roof_gable', 'Gable');
      var ends = [];
      if (ST.lo) ends.push('1');
      if (ST.hi) ends.push('2');
      if (ST.wing) ends.push('3');
      var all = ST.lo && ST.hi && (!(STATE.wing && STATE.wing.on) || ST.wing);
      return _t('roof_v2', 'Hip') + (all ? '' : (' ' + ends.join('/')));
    }
    function hipAt(end) {
      if (STATE.roof.type !== 'hip') return false;
      var H = hipEnds();
      return !!H[end];
    }
    /* WHICH END OF THE RIDGE A WALL STANDS AT: 'lo' or 'hi' along the ridge
       axis for an end wall, null for a wall along the ridge -- so a half-hip
       can ask hipAt() for the wall in front of it */
    function ridgeEndOf(seg) {
      var P = roofPlanes() || [], q, useZ = null;
      for (q = 0; q < P.length; q++) {
        var RG = P[q] && P[q].ridge; if (!RG || RG.length < 2) continue;
        var dx = Math.abs(RG[1][0] - RG[0][0]), dz = Math.abs(RG[1][2] - RG[0][2]);
        if (Math.max(dx, dz) < 0.05) continue;
        useZ = dz >= dx; break;
      }
      if (useZ === null) return null;
      var alongZ = Math.abs(seg.z1 - seg.z0) > Math.abs(seg.x1 - seg.x0);
      if (alongZ === useZ) return null;   /* runs along the ridge: a side wall */
      var B = _barRect(), mid = useZ ? (B.z0 + B.z1) / 2 : (B.x0 + B.x1) / 2;
      var at = useZ ? (seg.z0 + seg.z1) / 2 : (seg.x0 + seg.x1) / 2;
      return at < mid ? 'lo' : 'hi';
    }
    /* the four stages of the pitched roof: gable, hip low end, hip high end, hip both */
    function pitchedStage() {
      if (STATE.roof.type !== 'hip') return 0;
      var H = hipEnds(), ST = _roofStages(), i, wingOn = !!(STATE.wing && STATE.wing.on);
      for (i = 1; i < ST.length; i++) {
        if (!!ST[i].lo === !!H.lo && !!ST[i].hi === !!H.hi && (!wingOn || !!ST[i].wing === !!H.wing)) return i;
      }
      return 0;
    }
    function roofStageCount() { return _roofStages().length; }
    function setPitchedStage(k) {
      var ST = _roofStages(), n = ST.length;
      k = ((k % n) + n) % n;
      if (k === 0) { STATE.roof.type = 'gable'; STATE.roof.hipEnds = { lo: false, hi: false, wing: false }; }
      else { STATE.roof.type = 'hip'; STATE.roof.hipEnds = { lo: ST[k].lo, hi: ST[k].hi, wing: ST[k].wing }; }
      if (typeof _spanRule === 'function') setMaxSpan(_spanRule(STATE.roof.type));
      clampPitch();
      savePref();
      rebuild();
      return k;
    }

    /* WHICH ROOFS THIS PRODUCT SELLS, AND HOW FAR EACH CROSSES (core-v551,
       Marek: proc by nesla zmenit strecha na atiku, kdyz ji mame ve variantach
       ADU?). The floorplan asset hands the roof to the designer and writes
       the type it answers; it asks here which types the product's Roof & Base
       row offers -- the row is asked for once, without being drawn, when it
       has not been drawn yet -- and how far a type may span, so a roof that
       cannot cross the house is refused instead of shrinking the house under
       the plan. null: the product has no Roof & Base row of the line's; 0: no
       limit. */
    var _roofOfferSeen = null;
    function roofOffer() {
      if (!_roofOfferSeen && typeof _kitGroups === 'function') {
        try { _kitGroups('roof'); } catch (eG) { _log('roof     offer: asking the product\'s Roof & Base row threw ' + ((eG && eG.message) || eG)); }
      }
      return _roofOfferSeen ? _roofOfferSeen.slice() : null;
    }
    function spanFor(t) {
      if (typeof _spanRule !== 'function') return 0;
      var v = 0;
      try { v = Number(_spanRule(t)) || 0; } catch (eR) { v = 0; }
      return v > 0 ? v : 0;
    }
    function setRoofType(t) {
      /* HIP JOINS THE LIST HERE FIRST. */
      STATE.roof.type = (t === 'flat' || t === 'mono' || t === 'hip') ? t : 'gable';
      if (t === 'hip' && !(hipEnds().lo || hipEnds().hi)) STATE.roof.hipEnds = { lo: true, hi: true, wing: true };
      if (typeof _spanRule === 'function') setMaxSpan(_spanRule(STATE.roof.type));
      clampPitch();
      savePref();
      rebuild();
      _log('roof -> ' + STATE.roof.type);
    }

    /* -------------------------------------------------------------------------
       SNAPSHOT. Everything relevant goes here, because the on-screen snapshot is
       what gets pasted back. A part that built nothing has to say so and say why.
       ------------------------------------------------------------------------- */
    /* -------------------------------------------------------------------------
       THE ASSET SURFACE, READ OFF THE LIVE OBJECT.

       The floorplan is moving onto the shared asset, which means calling
       functions that live in another file. Guessing their names or their
       arguments is exactly how a feature ends up wired and silent: the asset
       falls back, nothing throws, and nothing happens -- which has cost this
       project rounds before, on the shed and on the container both.

       So the snapshot prints what is ACTUALLY THERE. Every verb on the asset,
       on plan, on draw and on trace, each with the number of arguments it
       takes, so a signature is read rather than assumed. And the room table
       the asset resolved from the keys this product offers, with the colour
       and the translated name beside each row.

       One screenshot then answers what the file would answer, and it answers
       it about the version that is really loaded rather than the one on my
       desk. That is the whole of this step: nothing is changed, everything
       that the next step has to call is named.
       ------------------------------------------------------------------------- */
    function _fpSurface(o) {
      if (!o) return 'MISSING';
      var out = [], k;
      for (k in o) {
        try {
          if (typeof o[k] === 'function') out.push(k + '/' + o[k].length);
          else if (o[k] && typeof o[k] === 'object') out.push(k + '{}');
          else out.push(k);
        } catch (eS) {}
      }
      out.sort();
      return out.length ? out.join(' ') : '(empty)';
    }

    function _fpSig(fn) {
      if (typeof fn !== 'function') return 'MISSING';
      try {
        var src = String(fn).replace(/\s+/g, ' ');
        var i = src.indexOf('{');
        return (i > 0 ? src.slice(0, i) : src.slice(0, 160)).slice(0, 160);
      } catch (e) { return 'unreadable'; }
    }

    function _fpApi(P, F, H) {
      try {
        if (!F) { P('fpapi      FloorplanLib MISSING -- nothing to read'); return; }
        P('fpapi      root   ' + _fpSurface(F));
        P('fpapi      plan   ' + _fpSurface(F.plan));
        P('fpapi      draw   ' + _fpSurface(F.draw));
        P('fpapi      trace  ' + _fpSurface(F.trace));
        /* ONE LEVEL DEEPER, because the drawing lives down there. The first
           reading printed overlay, handles, ui and underlay as bare braces and
           those are exactly the four the next step has to call. */
        P('fpapi      p.overlay ' + _fpSurface(F.plan && F.plan.overlay));
        P('fpapi      p.handles ' + _fpSurface(F.plan && F.plan.handles));
        P('fpapi      ui     ' + _fpSurface(F.ui));
        P('fpapi      underlay ' + _fpSurface(F.underlay));
        P('fpapi      d.SYM  ' + _fpSurface(F.draw && F.draw.SYM));
        /* AND THE ARGUMENT NAMES, read off the functions themselves. An arity
           says how many; only the source says which, and calling drawFloor with
           the arguments in the wrong order is a whole round wasted. */
        P('fpsig      drawFloor ' + _fpSig(F.plan && F.plan.drawFloor));
        P('fpsig      icons     ' + _fpSig(F.plan && F.plan.icons));
        P('fpsig      init      ' + _fpSig(F.plan && F.plan.init));
        P('fpsig      opening   ' + _fpSig(F.draw && F.draw.opening));
        P('fpsig      kot       ' + _fpSig(F.draw && F.draw.kot));
      } catch (eA) { P('fpapi      threw ' + (eA && eA.message)); }

      try {
        var offered = H ? H.roomKeys : undefined;
        var isArr = (Object.prototype.toString.call(offered) === '[object Array]');
        P('fprooms    this product offers roomKeys as ' + (typeof offered) +
          ' -- ' + (isArr ? (offered.length + ': ' + offered.join(', ')) : String(offered)));

        var tab = null, src = 'none';
        if (F && F.plan && typeof F.plan.roomTable === 'function') { tab = F.plan.roomTable(); src = 'plan.roomTable'; }
        else if (F && F.draw && typeof F.draw.roomTable === 'function') { tab = F.draw.roomTable(); src = 'draw.roomTable'; }
        if (!tab || !tab.length) {
          P('fprooms    the asset resolved NO table (' + src + ') -- the plan is being');
          P('fprooms    drawn from whatever default it holds, not from those keys');
          return;
        }
        P('fprooms    this product hands over ' +
          (_roomsFrozen ? (_roomsFrozen.length + ' row(s), frozen once from the pristine sixteen')
                        : 'NO table -- the pristine sixteen were never seen'));
        P('fprooms    ' + src + ' resolved ' + tab.length + ' room(s) -- default / colour / key / translated:');
        for (var i = 0; i < tab.length; i++) {
          var r = tab[i] || {};
          var key = r.i18n || r.key || '';
          P('  fproom   ' + (r.def || r.name || '?') +
            '  |  ' + (r.col || r.color || '?') +
            '  |  ' + (key || '?') +
            '  |  ' + (key ? _t(key, r.def || '') : '-'));
        }
      } catch (eR) { P('fprooms    threw ' + (eR && eR.message)); }
    }

    /* THE SNAPSHOT IS ONLY WHAT IS BEING CHASED.

       398 lines of assets, parts, i18n, scheme, layers and the whole bill were
       printed every time, and the two lines that mattered were somewhere inside
       them -- so it had to go through Word and a PDF to reach me, and twice it
       did not arrive at all.

       FOCUS names the prefixes. Nothing else is printed, which makes the result
       short enough to paste straight into a message. Set FOCUS to '' when the
       whole log is wanted again (Marek). */
    function snapshot() {
      var out = [];
      /* renamed off P, which is now the product bag */
      function P2(x) { out.push(x); _log(x); }

      P2('=== ' + __PROD.VERSION + ' ===');

      /* A FAILURE IS NEVER FILTERED OUT.

         FOCUS decides what the snapshot prints, and I have twice narrowed it in
         a way that dropped THREW and SKIPPED with everything else. A layer then
         fell over in silence: the roof vanished from the model and the log had
         nothing to say about it, so I spent two versions guessing at the cause
         from body counts.

         Whatever FOCUS says, a line that reports a throw or a skipped layer goes
         through. That was the rule from the start and the filter should never
         have been able to break it (Marek: kdyz tak rozsirit log). */
      var keep = FOCUS ? FOCUS.split(' ') : null;
      var i, shown = 0;
      for (i = 0; i < _buildLog.length; i++) {
        var always = /THREW|SKIPPED|threw|failed|ERROR/.test(_buildLog[i]);
        if (keep && !always) {
          var hit = false, q;
          for (q = 0; q < keep.length; q++) {
            if (_buildLog[i].indexOf(keep[q]) === 0) { hit = true; break; }
          }
          if (!hit) continue;
        }
        P2(_buildLog[i]);
        shown++;
      }
      /* THE ASSEMBLY WALK IN THREE LINES (core-v479): printed once the walk
         has been tapped, or when FOCUS asks for asm -- how the steps are
         numbered, where every step's name came from, and the roof's own
         order, which is what was wrong (Marek: battens az po krytine) */
      try {
        if (_asm2.touched || !FOCUS || FOCUS.indexOf('asm') >= 0) {
          var SW = _asm2.steps.length ? _asm2.steps : _asmCollect(), NW = SW.length + ASM_HEAD, qw, src = {}, rowW = null;
          var roofW = [];
          for (qw = 0; qw < SW.length; qw++) {
            var lbW = _asmStepLabel(SW[qw]);
            var fw = String(_asmNameFrom || '').replace(/ [A-Z]{2,4}[0-9A-Z]*$/, '').replace(/ for .*$/, '');
            if (/^boq /.test(_asmNameFrom)) fw = 'bridge';
            src[fw] = (src[fw] || 0) + 1;
            if (/-roof$/.test(SW[qw].layer)) roofW.push((qw + ASM_HEAD + 1) + ' ' + lbW);
            if (!rowW && SW[qw].code) { try { rowW = _catRow(CATALOG[SW[qw].code] && CATALOG[SW[qw].code].code ? CATALOG[SW[qw].code].code : SW[qw].code); } catch (eRw) {} }
          }
          P2('asm   walk ' + NW + ' step(s): 1 exploded view, 2 whole building back in the opening view, 3..' + NW + ' the items | now at ' + _asm2.k + '/' + NW);
          P2('asm   names (page ' + _pageLang() + '): ' + Object.keys(src).map(function (kw) { return src[kw] + ' ' + kw; }).join(', ') +
             ' | bridge ' + ((global.ConfiguratorBudgetBridge && global.ConfiguratorBudgetBridge.getBoqItem) ? 'present' : 'MISSING') +
             ' | catalogue row width ' + (rowW ? rowW.length : 'no row') + (rowW ? (', name column ' + (_asmLangCol(rowW) >= 0 ? _asmLangCol(rowW) : '3 (EN)')) : ''));
          P2('asm   roof ' + (roofW.length ? roofW.join(' > ') : 'no roof steps'));
          if (_asmUnlisted.length) P2('asm   unlisted, placed behind what was built before them: ' + _asmUnlisted.join(' | '));
        }
      } catch (eAW) { P2('asm   summary threw ' + (eAW && eAW.message)); }
      P2(_loadLine());
      /* core-v662: how the 3D camera was framed, the last few times */
      try { var stFr = _stage(); P2('frame ' + ((stFr && stFr._frameNotes && stFr._frameNotes.length) ? stFr._frameNotes.join(' || ') : 'no framing yet') + (stFr && stFr._camTouched ? ' | camera touched' : ' | camera untouched')); } catch (eFr) { P2('frame ' + (eFr && eFr.message)); }
      /* THE LAST TAP ON A WINDOW, said on every snapshot (core-v268): the grab
         was never printed, so a window that would not pick up could not be told
         apart from one that picked up in the wrong place (Marek: chytani oken je
         mimo okna, dole pod nimi) */
      P2('grab  ' + _sillNote + (_grabDiag ? (' || ' + _grabDiag) : ''));
      P2('object ' + _objNote);
      try {
        var hasPP = (typeof __PROD._planPoly === 'function'), hasPO = (typeof __PROD._planOps === 'function'), PPn = _planPolyNow(), xs9 = PPn.map(function (p) { return p[0]; });
        P2('plan  storey ' + ((STATE.planStorey | 0) + 1) + (_planWanted() ? ' on show' : ' (plan not up)') + ': outline ' + Math.round((Math.max.apply(null, xs9) - Math.min.apply(null, xs9)) * 1000) + ' wide ' + (hasPP && _planWanted() ? 'from the product' : 'from poly()') +
           ', openings ' + (hasPO && _planWanted() ? ('from the product (' + (_planOpsOn() || []).length + ')') : 'from the project'));
      } catch (ePl) { P2('plan  ' + (ePl && ePl.message)); }
      try {
        var cK = null; try { cK = (_wiz && _wiz.isOpen && _wiz.isOpen()) ? (_wiz.current() || {}).key : null; } catch (eWz) {}
        P2('handles step ' + (cK || 'drawer closed') + ' | active ' + _hNote.active + ' | ' + _hNote.n + ' refresh(es), ' + (_hNote.kids == null ? '?' : _hNote.kids) + ' piece(s) drawn | rects: ' + (_hNote.rects || '-') + (_hNote.err ? (' | LAST REFRESH THREW: ' + _hNote.err) : ''));
      } catch (eHn) { P2('handles ' + (eHn && eHn.message)); }
      try { P2('wizard ' + (_wiz && _wiz.tapNote ? _wiz.tapNote() : 'no wizard')); } catch (eWn) { P2('wizard ' + (eWn && eWn.message)); }
      try { P2(_sxNote()); } catch (eSx) { P2('strip    ' + (eSx && eSx.message)); }
      P2('stairwell ' + _fhNote + ' | grips ' + (_fhOn() ? 'ON' : 'off (' + (!_fhHook() ? 'no product hole' : (STATE.view !== '2d' ? 'not the plan' : (!editing('layout') ? 'not the Floorplan step' : 'storey ' + (STATE.planStorey | 0) + ' has none'))) + ')'));
      /* WHAT IS IN THE SCENE, BY LAYER, AND WHETHER IT CAN BE SEEN (core-v328):
         a layer that built its bodies and shows nothing is told apart from one
         that built none -- the two look the same on a screenshot (Marek: neni
         videt stena, zaklad, jen strecha) */
      /* AND A BODY WITH NO MATERIAL IS NOT VISIBLE, whatever its flag says
         (core-v335). The base was counted here as built and visible while the
         renderer drew none of it: made with a number where the material goes.
         Such a body is now counted apart and named, so the same trap in any
         layer says so on the first snapshot. */
      try {
        var stL = _stage(), byL = {}, kL, outL = [];
        if (stL && stL.group) stL.group.traverse(function (o) {
          if (!o.isMesh) return;
          var lay = _layerOf(o) || '(guider)';
          if (!byL[lay]) byL[lay] = { n: 0, vis: 0, bad: 0 };
          byL[lay].n++;
          var okMat = !!(o.material && (o.material.isMaterial || Array.isArray(o.material)));
          if (!okMat) byL[lay].bad++;
          var vis = o.visible && okMat, p = o.parent;
          while (vis && p) { if (p.visible === false) vis = false; p = p.parent; }
          if (vis) byL[lay].vis++;
        });
        for (kL in byL) if (byL.hasOwnProperty(kL)) outL.push(kL + ' ' + byL[kL].vis + '/' + byL[kL].n +
          (byL[kL].bad ? (' (' + byL[kL].bad + ' WITHOUT A MATERIAL -- never drawn)') : ''));
        P2('scene  ' + (outL.length ? outL.join(' | ') : 'EMPTY -- no meshes in the stage group') + ' (visible/built)');
      } catch (eSc) { P2('scene  ' + (eSc && eSc.message)); }
      /* PERF (core-v589): what loading, building, painting and every frame
         cost on THIS device -- always printed, whatever FOCUS says */
      try { var pfL = _perfLines(), qPf; for (qPf = 0; qPf < pfL.length; qPf++) P2(pfL[qPf]); }
      catch (ePf) { P2('perf   threw ' + ((ePf && ePf.message) || ePf)); }
      P2('--- ' + shown + '/' + _buildLog.length + ' FOCUS="' + FOCUS + '"');

      return out.join('\n');
    }

    /* -------------------------------------------------------------------------
       BOOT
       ------------------------------------------------------------------------- */
    function boot(opts) {
      opts = opts || {};
      if (!global.THREE) { _log('boot deferred -- THREE not on window yet'); return false; }
      if (opts.t) _i18n = opts.t;
      if (opts.prefKey) _prefKey = opts.prefKey;

      /* core-v797: WHAT RESET GOES BACK TO -- the product's own fresh page, taken before a saved project overwrites it */
      try { _bootFresh = JSON.stringify(STATE); } catch (eBF) { _bootFresh = null; }
      loadPref();
      /* the review stage is for looking, not for keeping: a reload opens the
         model whole again (core-v419) */
      try { if (viz().key === 'focus') STATE.vizStage = 0; } catch (eVz0) {}
      try { _stackForFooting(); } catch (eSF0) {}
      _storeWatch();
      STATE.mode = 1;
      clampPitch();

      if (!_initStage()) return false;

      var U = _UI();
      _fillIcons();
      if (U && U.injectBrand) U.injectBrand();

      /* HEADLESS FOR THE BLUEPRINTS: the stage, the layers and the plan, and
         none of the page furniture -- no map, no handles, no scenery, no body,
         no pdf button. The project arrives by ?p= like a shared link. */
      var headless = !!opts.blueprint;
      /* the padlock on the strip's plan holds the building's grips too (core-v591) */
      var hAct = opts.handlesActive;
      _initHandles(headless ? function () { return false; }
                            : (typeof hAct === 'function' ? function () { return !_stripPlanLocked() && hAct(); } : hAct));
      _initFloorplan();
      if (!headless) {
        try { initMap(); } catch (eM) { _log('map init threw: ' + (eM && eM.message ? eM.message : eM)); }
      }
      if (!headless) {
        /* the page's own gestures stay off on the drawings page, so the page
           can scroll over the model */
        try { _init2D(); } catch (e2D) { _log('2D controls: ' + (e2D && e2D.message ? e2D.message : e2D)); }
        try { _init2DZoom(); } catch (e2Z) { _log('2D zoom: ' + (e2Z && e2Z.message ? e2Z.message : e2Z)); }
        try { _initNav(); } catch (eNV) { _log('navigation: ' + (eNV && eNV.message ? eNV.message : eNV)); }
      }
      if (!headless) {
        try { _initOpeningPick(); } catch (eOP) { _log('opening pick: ' + (eOP && eOP.message ? eOP.message : eOP)); }
        try { _buildScenery(); } catch (eSC) { _log('scenery: ' + (eSC && eSC.message ? eSC.message : eSC)); }
        try { _initBodyHandle(); _startBodyTracker(); } catch (eB) { _log('body handle: ' + (eB && eB.message ? eB.message : eB)); }
        try { _propHandles(); } catch (ePH) { _log('prop handles: ' + (ePH && ePH.message ? ePH.message : ePH)); }
        if (opts.exportPdf) setPdfExport(opts.exportPdf);
        try { _initPdfButton(); } catch (eP2) { _log('pdf button: ' + (eP2 && eP2.message ? eP2.message : eP2)); }
        try { _cmdBarInit(); } catch (eCB) { _log('cmdbar   threw: ' + (eCB && eCB.message ? eCB.message : eCB)); }
      }
      STATE.bodyVisible = headless ? false : STATE.bodyVisible;
        /* THE FIRST BUILD, REPORTED. boot() used to call rebuild() bare, so an
         exception in any layer took the rest of boot with it -- no "boot
         complete", no model, an empty group and a build log of nothing,
         which is exactly the state that says least about itself. Now it is
         caught, named, and painted where it can be read off the screen. */
      try {
        rebuild();
      } catch (eR) {
        var rm = 'REBUILD THREW -- ' + ((eR && eR.message) || eR);
        var rw = (eR && eR.stack) ? String(eR.stack).split('\n').slice(0, 5).join('\n') : '';
        try { _log(rm); if (rw) _log(rw); } catch (r1) {}
        try { _buildLog.push(rm); if (rw) _buildLog.push(rw); } catch (r2) {}
        try { global.__levaLogs = global.__levaLogs || []; global.__levaLogs.push(rm); if (rw) global.__levaLogs.push(rw); } catch (r3) {}
        try { if (global.console) (global.console.error || global.console.log)(rm, eR); } catch (r4) {}
        try {
          var rd = global.document.createElement('div');
          rd.setAttribute('style', 'position:fixed;left:0;right:0;top:0;z-index:2147483647;' +
            'background:#B3261E;color:#fff;font:12px/1.45 ui-monospace,Menlo,monospace;' +
            'padding:10px 12px;white-space:pre-wrap;max-height:60vh;overflow:auto');
          rd.textContent = rm + (rw ? ('\n' + rw) : '');
          (global.document.body || global.document.documentElement).appendChild(rd);
        } catch (r5) {}
      }

      if (STATE.bodyVisible) {
        var bb = $('cf-btn-body');
        if (bb) { bb.setAttribute('aria-pressed', 'true'); }
        var bh = $('cf-body-handle');
        if (bh) bh.classList.add('active');
      }

      var st = _stage();
      if (st) st.fitCameraToFootprint();
      setView(STATE.view);
      /* core-v662: for the first 20 s, until the first tap or key anywhere on
         the page, a model that changes is framed again (a plan or a project
         that lands after this first framing) */
      _afUntil = Date.now() + 20000;
      try {
        var _afStop = function () { _afUntil = 0; };
        global.document.addEventListener('pointerdown', _afStop, { once: true, capture: true, passive: true });
        global.document.addEventListener('keydown', _afStop, { once: true, capture: true, passive: true });
      } catch (eAfL) {}

      /* core-v752: what ANY HOUSE handed over, if anything */
      try { _recipeIntake(); } catch (eRc) { _log('recipe   from ANY HOUSE THREW ' + ((eRc && eRc.message) || eRc)); }
      _log('boot complete -- ' + __PROD.VERSION);
      _shareBooted = true;
      /* core-v767: the drawings page asked for its package */
      try { _bpAutoRun(); } catch (eBp) { _log('pdf package for the drawings page THREW ' + ((eBp && eBp.message) || eBp)); }
      return true;
    }

    /* Everything the core block offers a product. A lib publishes this and then
       adds its own keys on top, so the shared liquid and the shared assets always
       find the same names whichever product is on the page. */
    function coreExport() {
      return {
        CORE_VERSION: CORE_VERSION,
        LAYER_ROLES: LAYER_ROLES.slice(),
        specExport: specExport,
        STATE: STATE,
        DEFAULTS: DEFAULTS,
        STACK: STACK,
        LIMITS: LIMITS,

        rebuild: rebuild,
        hipAt: hipAt,
        ridgeEndOf: ridgeEndOf,
        pitchedStage: pitchedStage,
        setPitchedStage: setPitchedStage,
        setLayerMode: setLayerMode,
        /* core-v628: whether the strip has a product's row (its step) picked */
        stripPicked: _sxPicked,
        afterBuild: [],
        asmNext: asmNext,
        asmWalking: asmWalking,
        asmBill: asmBill,
        asmMark: _asmMark,
        setExplode: setExplode,
        explode: explode,
        /* core-v658: the last exploded view's summary (the snapshot, the harness) */
        explodeInfo: function () { return _xpInfo; },
        openBlueprints: openBlueprints,
        /* core-v731: the PDF's arguments as the package hands them over (the harness) */
        pdfArgs: function () { return _pdfArgs(); },
        layerModes: layerModes,
        blueprintPackage: blueprintPackage,
        dimAnchors: dimAnchors,
        reset: reset,
        savePref: savePref,
        payload: payload,
        noteError: _log,

        registerLayer: registerLayer,
        finiteGuard: finiteGuard,
        layers: layers,
        stageViews: stageViews,
        setOutPoint: setOutPoint,

        poly: poly,
        polyOf: polyOf,   /* core-v785 */
        polyArea: polyArea,
        addOpening: addOpening,
        opEdit: opEdit,
        armPartition: armPartition,
        addPartition: addPartition,
        armedPartition: armedPartition,
        placePartition: placePartition,
        scheme: scheme,
        nextScheme: function () { var S = SCHEME(); if (S) S.next(); return scheme(); },
        sizeToWall: _sizeToWall,
        moveOpening: moveOpening,
        snapOpeningAt: snapOpeningAt,
        /* the whole layout, so a product never works any of it out again */
        wallLayout: WALL_LAYOUT,
        openingRange: openingRange,
        openingNear: openingNear,
        armOpening: armOpening,
        armedOpening: armedOpening,
        nearestSegment: nearestSegment,
        removeOpening: removeOpening,
        openingsOn: openingsOn,
        inPoly: inPoly,
        storeys: storeys,
        segments: segments,
        segment: segment,
        roofPlanes: roofPlanes,
        apexY: apexY,
        wallTop: wallTop,
        bearingY: bearingY,

        setVizStages: setVizStages,
        terraceAt:    terraceAt,
        buildPostWalls:      buildPostWalls,
        postWallTile:        postWallTile,
        reportTrusses:       reportTrusses,
        trussRecorder:       trussRecorder,
        trussKit:            trussKit,
        faceOutline:         faceOutline,
        blindFor:            blindFor,
        blindsOn:            blindsOn,
        setBlinds:           setBlinds,
        toggleBlinds:        toggleBlinds,
        blindsTile:          blindsTile,
        bodyInstance:        _bodyInstance,
        buildBlinds:         buildBlinds,
        seatPostWallGap:     seatPostWallGap,
        showPostWallPlus:    showPostWallPlus,
        setPostWallKinds:    setPostWallKinds,
        setPostWallMode:     setPostWallMode,
        postWallKindOf:      postWallKindOf,
        postWallSel:         postWallSel,
        postWallTap:         postWallTap,
        setPostBays:      setPostBays,
        setPostTrigger:   setPostTrigger,
        setDefaultModel:  setDefaultModel,
        setPostSpan:      setPostSpan,
        reportPosts:      reportPosts,
        postBayCount:     postBayCount,
        postBaySides:     postBaySides,
        postWallOn:       postWallOn,
        setPostWall:      setPostWall,
        postWallKind:     postWallKind,
        nextPostWallKind: nextPostWallKind,
        postWallIsSlats:  postWallIsSlats,
        postWallGap:      postWallGap,
        setPostWallGap:   setPostWallGap,
        postWallColor:    postWallColor,
        setPostWallColor: setPostWallColor,
        vizStages: vizStages,
        viz: viz,
        setViz: setViz,
        nextViz: nextViz,

        panScene: panScene,
        zoomAt: zoomAt,

        toggleBody: toggleBody,
        arOpen: arOpen,
        arExport: arExport,
        wallThickness: wallThickness,
        propOn: propOn,
        toggleProp: toggleProp,
        poolOn: poolOn,
        togglePool: togglePool,
        pergolaOn: pergolaOn,
        togglePergola: togglePergola,
        pergolaRect: _pergolaRect,
        pergolaSheltered: _pergolaSheltered,
        defineProp: defineProp,
        propPos: propPos,
        terraceToSide: terraceToSide,
        buildPergola: buildPergola,
        facadeThk: facadeThk,
        setWallThickness: setWallThickness,
        partThickness: partThickness,
        partSpan: _partSpanH,
        roofOver: _roofOver,
        setPartThickness: setPartThickness,
        setPlateZone: setPlateZone,
        plateZone: function () { return STACK.plate; },
        setPdfExport: setPdfExport,

        initMap: initMap,
        mapOpen: mapOpen,
        mapReady: mapReady,
        placeStepDef: placeStepDef,
        placeGroups: placeGroups,
        colorSlots: colorSlots,
        texSheet: texSheet, texMesh: texMesh, texGrain: texGrain, texStucco: texStucco, texLog: texLog,
        masonry: masonry,
        roofTails: roofTails,
        colorSlotKey: colorSlotKey,
        colorSlotOf: colorSlotOf,
        colorSlotNext: colorSlotNext,
        colorSlotSet: colorSlotSet,
        colorHex: colorHex,
        colorsStepDef: colorsStepDef,
        colorsPanel: colorsPanel,
        openingsStepDef: openingsStepDef,
        openingsGroups: openingsGroups,
        cornerPlace: function (segId, at) { var sgP = segment(segId); return sgP ? _cwPlace(sgP, (at == null) ? 0 : +at) : null; },
        cornerCycle: function (rec) { var Cc = _opCycleOf(rec); if (!Cc) return null; var kc = Cc.next(); savePref(); rebuild(); return kc; },
        layoutStepDef: layoutStepDef,
        wallOpenings: wallOpenings,
      openingBands: openingBands,
      /* the plan as rectangles, for a floor laid across them (core-v408) */
      rects: _decomposeToRects,
      buildOpenings: buildOpenings,
      layoutGroups: layoutGroups,
        undoPush: undoPush,
        undoPop: undoPop,
        undoState: undoState,
        /* the guided tour, for the snapshot and a test that starts at step i */
        tour: { state: function () { return tourState(); }, at: function (i) { if (!_T.on) _T.resumeAt = Math.max(0, i | 0); return tourState(); } },
        railBind: railBind,
        railCounters: railCounters,
        stepDef: stepDef,
        stepPanel: stepPanel,
        wizardMount: wizardMount,
        wizardSteps: wizardSteps,
        kitDefault: kitDefault,
        videoGroups: videoGroups,
        ROOF_COVERINGS: ROOF_COVERINGS,
        covKey: covKey,
        covAllowed: covAllowed,
        covFallback: covFallback,
        covDef: covDef,
        roofGroups: roofGroups,
        exteriorGroups: exteriorGroups,
        roofStageLabel: roofStageLabel,
        roofStageCount: roofStageCount,
        billAdd: billAdd,
        buildStripSlab: buildStripSlab,
        roofSkin: roofSkin,
        gutters: gutters,
        eavePosts: eavePosts,
        foundation: foundation,
        trusses: trusses,
        studWall: studWall,
        wallKit: wallKit,
        houseFloor: houseFloor,
        houseCeiling: houseCeiling,
        houseParts: houseParts,
        houseJoinery: houseJoinery,
        houseDoorSet: houseDoorSet,
        houseRoof: houseRoof,
        houseRoofSkin: houseRoofSkin,
        houseFlatRoof: houseFlatRoof,
        houseFitout: houseFitout,
        houseWebPlane: houseWebPlane,
        houseFootOnChord: houseFootOnChord,
        houseSeatOnHip: houseSeatOnHip,
        houseRafterSteps: houseRafterSteps,
        houseRoofPlaneYAt: houseRoofPlaneYAt,
        houseRoofYAtXZ: houseRoofYAtXZ,
        houseUnderRoof: houseUnderRoof,
        houseMembTex: houseMembTex,
        houseSpaceBuild: houseSpaceBuild,
        houseDiagWalls: houseDiagWalls,
        houseFinTex: houseFinTex,
        houseStudPositions: houseStudPositions,
        houseStudPositionsWorld: houseStudPositionsWorld,
        houseGableFaces: houseGableFaces,
        houseTrussStations: houseTrussStations,
        houseTerraceDeck: houseTerraceDeck,
        houseLps: houseLps,
        houseOpenFitouts: houseOpenFitouts,
        houseSnapshot: houseSnapshot,
        houseSkirtCuts: houseSkirtCuts,
        roofOpenings: roofOpenings,
        screwPile: screwPile,
        deck: deck,
        stairs: stairs,
        stairPlan: stairPlan,
        stairKit: stairKit,
        stairAxes: stairAxes,
        pointBase: pointBase,
        roofEdges: roofEdges,
        mapToggleLock: mapToggleLock,

        setMaxSpan: setMaxSpan,
        setSpanRule: setSpanRule,
        FOUNDATIONS: FOUNDATIONS,
        setFoundation: setFoundation,
        setCladding: setCladding,
        nextCladding: nextCladding,
        cladding: cladKind,
        SCENERY: SCENERY,
        cycleScenery: cycleScenery,
        buildScenery: _buildScenery,
        nextFoundation: nextFoundation,
        bearingPoints: bearingPoints,

        setStoreys: setStoreys,
        setRoofType: setRoofType,
        applyConfigId: applyConfigId,
        roofOffer: roofOffer,
        spanFor: spanFor,
        toggleUnits: toggleUnits,
        setView: setView,
        showPitch: showPitch,

        initWizard: initWizard,
        wizard: function () { return _wiz; },
        wizardOpen: wizardOpen,
        editing: editing,

        planHost: planHost,
        boq: boq,
        publishBoq: publishBoq,

        grid: GRID,
        sheet: sheet,
        sheetsFor: sheetsFor,
        snapDim: snapDim,
        snapClear: snapClear,
        fmtLen: fmtLen,
        fmtArea: fmtArea,
        clamp: clamp,

        _coreBoot: boot,
        _coreSnapshot: snapshot,
        /* THE SNAPSHOT'S FILTER, FROM THE PAGE (core-v576, Marek: SEND RAW
           posle cely log bez filtru FOCUS, vsechny radky). FOCUS lives in here
           and the page could not reach it, so a snapshot could only ever be
           the narrowed one. focus() reads it, focus(v) sets it: SEND RAW
           lifts it for one snapshot and puts it back. */
        focus: function (v) { if (arguments.length) FOCUS = String(v == null ? '' : v); return FOCUS; },
        _stage: _stage,
        _handles: function () { return _handles; }
      };
    }

    /* -------------------------------------------------------------------------
       THE PLUMBING THAT WAS SITTING UNDER THE LINE.

       None of this is product code -- t() is the translator, CATALOG is the
       bill's registry, _rows builds its lines, _box draws a box, _trace* is the
       outlining, _cut* the section cuts, _asm* the assembly, API the export
       itself. It had drifted below END CORE BLOCK, most likely because the line
       was drawn later than the code was written, and it meant the core reached
       DOWN into the product for twenty-three names. A shared core cannot do
       that: the product it would reach into does not exist yet.

       So it moves up, in one block, byte for byte the same in every product --
       which is what makes the two cores hash the same. The only two that stay
       below are CATALOG and FOCUS, because those really are product data; the
       core keeps an empty one of each and the product fills it, exactly as it
       does with ROOM_KEYS and the assembly tables.
       ------------------------------------------------------------------------- */
    /* the bill's registry and the snapshot's filter are the product's own */
    var CATALOG = {};
    var FOCUS = '';

    /* -------------------------------------------------------------------------
       AND THE LAST TWELVE, THROUGH ONE BAG.

       These are the only names the core still needs FROM the product: who it is
       (VERSION), the kit config (KIT), the wall section beside a section cut
       (_asmLayers), the four optional hooks, and the layer constants _cladThk
       measures the facade with. Twenty-six sites in nine thousand lines.

       They go through P rather than through scope, because scope is exactly
       what a shared core does not have: the product is a different file and is
       not there when the core is read. The product fills P at the end of its
       own block -- which is why every one of these reads happens at boot or
       later, never while the file is loading.
       ------------------------------------------------------------------------- */
    /* __PROD, NOT P. The core uses P as a local name in twenty-six places --
       var P = poly(), var P = _fpPlan(), function P(line) -- and roofPlanes() is
       one of them: there P is the footprint polygon, so __PROD.RAFTER was undefined
       and .t on it threw. The bag the product hands back needs a name nothing
       else wants. */
    var __PROD = {};

    function BOOTDATA() { return global.HOUSE_BOOT || {}; }

    /* THE LOG HAS TO FIT ON A TABLET.

       207 lines, and most of them explain rather than report -- useful while a
       layer is being built, noise once it works, and too much to paste back from
       a phone. TERSE keeps the lines that carry a NUMBER and drops the ones that
       carry a reason; the reasons are all in the comments here anyway, where they
       do not have to be copied to be read.

       Set it false when a layer misbehaves and the reasoning is wanted again. */
    var TERSE = true;

    var _notBilled = [];   /* every key that was billed into nothing this build -- printed in the snapshot always */

    var _cutTool = null;

    /* =========================================================================
       THE ASSEMBLY STEP -- a section you can read. The house is cut across its
       middle (the kit's cut planes, the grey plane hidden) and the exterior
       wall's build-up is named in a column beside the model: one row per
       layer, outside in, with its thickness, each joined to its layer at the
       cut by a leader. The model turns; the column does not, and the leaders
       are redrawn from the camera on every move -- ported from the shed's
       assembly panel. Leaving the step takes it all away.
       ========================================================================= */
    var _asm = { on: false, panel: null, rows: [], lines: null, grp: null, bound: false };

    function SCHEME() {
      if (_schemes) return _schemes;
      var U = _UI();
      if (!U || !U.schemes) return null;
      _schemes = U.schemes({
        index: STATE.colorView | 0,
        /* the rail button is the section tool's now; the cycler must not light
           it (ui-v107 honours this) */
        button: false,
        title: function (sc, i, n) {
          return _t('toggle_colors', 'Colour') + ' ' + i + '/' + n + ' -- ' + sc.key;
        },
        onChange: function (sc, i) {
          STATE.colorView = i;
          savePref();
          rebuild();
          _log('scheme -> ' + sc.key + ' (' + (i + 1) + ')');
        }
      });
      return _schemes;
    }

    /* the current scheme, or the first one, so a layer can always ask */
    function scheme() {
      var S = SCHEME();
      var U = _UI();
      if (S) return S.current();
      return (U && U.SCHEMES_DEFAULT) ? U.SCHEMES_DEFAULT[0] : { deck: 0xB9A88C };
    }

    function t(key, fallback) {
      try {
        var d = BOOTDATA().i18n || {};
        var v = d[key];
        _i18nAsked[key] = 1;
        if (!v || String(v).indexOf('Translation missing') === 0) _i18nMissed[key] = fallback;
        else delete _i18nMissed[key];
        if (v && fallback && String(v) === String(fallback) && _pageLang() !== 'en') {
          _i18nSameAsEn[key] = fallback;
        } else {
          delete _i18nSameAsEn[key];
        }
        /* A KEY THE THEME HAS NOT GOT COMES BACK AS PROSE, NOT AS NOTHING.
           Shopify answers a missing key with "Translation missing: en.x.y", which
           is a perfectly truthy string -- so it would be shown on the button, and
           the fallback written right here would never be reached. */
        if (v && String(v).indexOf('Translation missing') !== 0) return v;
      } catch (e) {}
      return fallback;
    }

    function _trace() {
      var F = global.FloorplanLib;
      return (F && F.trace && F.trace.has) ? F.trace : null;
    }

    /* the image changed, so the plan is repainted and the button repainted with
       it: red with no picture, black while it is awake, grey once it is locked */
    var _traceDrag = null;

    function _traceRepaint() {
      var F = global.FloorplanLib;
      var OV = (F && F.plan && F.plan.overlay) ? F.plan.overlay : null;
      if (!OV || !OV.repaint) { try { if (API.drawPlan) API.drawPlan(); } catch (e0) {} return; }
      if (_traceRaf) return;
      _traceRaf = (global.requestAnimationFrame || global.setTimeout)(function () {
        _traceRaf = 0;
        try { OV.repaint(); } catch (e1) {}
      }, 16);
    }

    /* the asset draws its own opacity slider under the picture; the drawer has
       it now, so that one is put out of sight by a rule, whatever the asset
       sets inline (core-v539) */
    function _traceSliderHide() {
      try {
        if (global.document.getElementById('cf-trace-slider-off')) return;
        var stl = global.document.createElement('style');
        stl.id = 'cf-trace-slider-off';
        stl.textContent = '#cf-trace-opacity-row,#cf-trace-opacity-bubble{display:none !important;}';
        global.document.head.appendChild(stl);
        _log('trace    opacity slider in the drawer -- the one under the picture is hidden (core-v539)');
      } catch (e) {}
    }
    /* A PICTURE ARRIVING OR LEAVING REDRAWS THE DRAWER, so the slider row comes
       and goes with it without a tap anywhere (an import lays the picture down
       while the drawer is open) */
    var _picHad = false;
    function _picWatch(F) {
      var has = false;
      try { has = !!(F && F.trace && F.trace.has && F.trace.has()); } catch (e) { has = false; }
      if (has === _picHad) return;
      _picHad = has;
      if (!editing('layout')) return;
      global.setTimeout(function () { try { if (_wiz && _wiz.refresh) _wiz.refresh(); } catch (e2) {} }, 0);
    }
    function _traceIcon() {
      _traceIconPaint();
      try {
        global.setTimeout(_traceIconPaint, 0);
        global.setTimeout(_traceIconPaint, 120);
      } catch (e) {}
    }

    function _traceOff(why) {
      var F = global.FloorplanLib;
      if (!F || !F.trace || !F.trace.armed) return;
      F.trace.armed = false;
      _traceDrag = null;
      _log('trace disarmed -- ' + why + '; the image stays, locked');
      /* NO REBUILD. The picture lives on the 2D plan and in nothing else, so
         putting it to sleep has no business rebuilding the model. */
      _traceRepaint();
      _traceIcon();
    }

    function _fillIcons() {
      if (ICO.__filled) return ICO;
      var K = __PROD.KIT();
      if (!K) return ICO;                     /* try again next time */
      var k, n = 0;
      for (k in K.icons) if (K.icons.hasOwnProperty(k)) { ICO[k] = K.icons[k]; n++; }
      ICO.__filled = true;
      _log('icons: shared set loaded, ' + n + ' glyphs');
      return ICO;
    }

    function _box(T, g, mat, w, h, d, x, y, z) {
      var lo = y - h / 2, hi = y + h / 2, h0 = h;
      if (!_footFull()) {
        if (hi <= 0.001) { _qtyHold(w, h, d); return; }
        if (lo < 0) { h = hi; y = hi / 2; }
      }
      var m = new T.Mesh(new T.BoxGeometry(w, h, d), mat);
      m.position.set(x, y, z);
      if (h !== h0 && _qtyFullOn()) m.userData.qtyBox = [w, h0, d];   /* core-v682: measured whole */
      g.add(m);
    }
    /* core-v682 (RANCH thread, opt-in __PROD._qtyFull): WHAT IS UNDER GRADE
       IS MEASURED WHOLE. Outside the Roof & Base step a body is cut at grade
       (drawn as what of it stands above) or not drawn at all (all of it
       under) -- and the qty audit measured what was left: a 150 gravel bed
       read as a 2 mm sheet, a slab's edge as its 152 above grade, a pier as
       its 86 (Marek: kreslena telesa bez mereni). A cut body keeps its whole
       box (userData.qtyBox), a body left out entirely is held on its layer's
       group with the item current then, and the audit measures both whole.
       Nothing drawn or billed changes. Without the flag nothing changes. */
    var _qtyLayer = null;
    function _qtyFullOn() { try { return !!(__PROD && __PROD._qtyFull); } catch (eQF) { return false; } }
    function _qtyHold(w, h, d) {
      if (!_qtyFullOn() || !_qtyLayer || !_qtyLayer.g || !_qtyLayer.g._qtyHeld) return;
      _qtyLayer.g._qtyHeld.push({ asm: _qtyLayer.ctx ? _qtyLayer.ctx.item : null, d: [w, h, d] });
    }

    /* Rows for the budget: only what can actually be bought. */
      /* =========================================================================
         THE TAG AUDIT (core-v88).

         A mark is set once and stays current until something replaces it, so a
         builder that draws without setting its own inherits whoever went before.
         That is how an interior door came out as OSB sheathing: the lining is
         drawn after the boarding and never says what it is. The model was wrong
         about what it contains, quietly, and anything reading the model -- the
         drawer, the pricing, the asset record -- inherits that (Marek: pokud
         model nevi, ze jsou to dvere, muze nam to rozbit cely budgeting).

         Chasing those one at a time is what produced the door patch. This lists
         them instead. Three questions, per layer, on every rebuild:

           DRAWN BUT NOT BILLED  a tag carries bodies and no row bills it --
                                 something is modelled and nobody pays for it
           BILLED BUT NOT DRAWN  a row has a quantity and no body carries its tag
                                 -- something is charged and cannot be seen
           UNTAGGED              bodies with no mark at all -- the model does not
                                 know what they are

         The third is the one that matters most here: an untagged body is
         harmless on its own, but a MIS-tagged one is not, and the two are told
         apart only by knowing which bodies a builder meant to mark. So the audit
         also prints, per tag, how many bodies it holds -- a door lining sitting
         under OSB shows up as OSB holding far more than sheets.
         ========================================================================= */
      /* THE AUDIT ASKS ONE QUESTION, ACROSS THE WHOLE BUILDING.

         It used to ask it layer by layer, and that produced alarms where
         nothing was wrong: a screw is billed and never drawn, and a panel is
         drawn in the wall and billed in the frame. Both came out as faults.
         Dozens of those and the one real fault is invisible -- which defeats
         the audit, because it exists to find exactly that one (Marek).

         So drawn and billed are each summed over every layer first, and only
         then compared. A code that is drawn somewhere and billed somewhere is
         fine wherever those somewheres are. What is left is real: something
         drawn that nobody bills, or billed that nobody draws.

         UNTAGGED stays per layer, because there the layer IS the answer -- it
         says which builder forgot to name its bodies.

         Nothing about billing changes. This is the check, not the bill. */
      /* =========================================================================
       WHICH ITEMS SHARE A MATERIAL (core-v162).

       Two bugs this week came from one cause: a builder made ONE material and
       drew two different billed items with it. Highlighting a picked body tints
       the material, so tapping a rail turned the cladding magenta too, and no
       amount of looking at the picker would have found it.

       I tried to find the rest by reading the source and the scanner lumped a
       whole product library into a single function -- 1.2 MB of it -- and
       reported seventy-seven findings I could not stand behind. So it is asked
       of the running model instead, where there is nothing to parse: walk the
       bodies, group them by the material OBJECT they carry, and report any
       material worn by more than one assembly mark.

       A shared material is not always wrong -- a single item drawn in several
       passes is fine -- but two different CODES on one material always is,
       because they are two prices and two things to point at. That is what this
       prints (Marek: udelej audit techto chyb v celem projektu). */
    /* AND THE FIX BELONGS HERE, NOT IN FIVE BUILDERS.

       The audit found seven materials worn by two codes: my own shutters twice,
       and then the partitions, the joinery and the lightning protection. I could
       patch each builder -- and would be back here the next time somebody writes
       a builder that makes one material and draws two items with it, which is
       the natural way to write one.

       So it is mended where it is measured. After every build, any material
       carrying more than one assembly code is CLONED, once per code, and the
       bodies are moved onto their own copy. Same colour, same everything; what
       changes is that tinting one item can no longer reach another.

       The builders keep their one material and stay simple. Nobody has to
       remember this rule (Marek: chci aby asset fungoval na kazdy item zvlast). */
    function _matSplit(P) {
      var st = _stage();
      if (!st || !st.group) return;

      function codeOf(o) {
        var m = o.userData && o.userData.asm;
        var tag = m ? (typeof m === 'string' ? m : (m.code || m.key || '')) : '';
        if (!tag) return '';
        return (CATALOG[tag] && CATALOG[tag].code) ? CATALOG[tag].code : tag;
      }

      /* which codes each material is currently worn by -- found through a
         Map (core-v621: the list was searched body by body, on the RANCH
         10 000 bodies x 1 800 materials = the 60 ms 'split' of every build) */
      var recs = [], recM = (typeof Map === 'function') ? new Map() : null;
      function recOf(mat) {
        if (recM) return recM.get(mat) || null;
        for (var i = 0; i < recs.length; i++) if (recs[i].mat === mat) return recs[i];
        return null;
      }
      function recFor(mat) {
        var r = recOf(mat);
        if (r) return r;
        r = { mat: mat, codes: {}, clones: {} };
        recs.push(r);
        if (recM) recM.set(mat, r);
        return r;
      }
      st.group.traverse(function (o) {
        if (!o.isMesh || !o.material || o.material.length) return;   /* leave multi-material alone */
        var c = codeOf(o);
        if (!c) return;
        recFor(o.material).codes[c] = 1;
      });

      var shared = 0, made = 0, i, k;
      for (i = 0; i < recs.length; i++) {
        var n = 0;
        for (k in recs[i].codes) if (recs[i].codes.hasOwnProperty(k)) n++;
        if (n > 1) { recs[i].split = true; shared++; }
      }
      if (!shared) { if (P) P('material audit  nothing shared, no splitting needed'); return; }

      /* the first code keeps the original; every other code gets a clone */
      st.group.traverse(function (o) {
        if (!o.isMesh || !o.material || o.material.length) return;
        var c = codeOf(o);
        if (!c) return;
        var rec = recOf(o.material);
        if (!rec || !rec.split) return;
        if (!rec.keep) { rec.keep = c; return; }      /* this code keeps the original */
        if (c === rec.keep) return;
        if (!rec.clones[c]) {
          try { rec.clones[c] = rec.mat.clone(); made++; }
          catch (eC) { return; }
        }
        o.material = rec.clones[c];
      });

      if (P) P('material audit  split ' + shared + ' shared material(s) into ' + (shared + made) +
               ' -- each item now tints on its own');
    }

    /* =========================================================================
       WHAT THE MODEL HOLDS, AGAINST WHAT THE BILL SAYS (core-v181).

       The rule is that everything is billed FROM the model (Marek: vsechno
       mame vykazovat z modelu, ale spravne). This does not change a single
       quantity -- it measures the model the way the bill measures, code by
       code, and prints every place the two disagree by more than a fifth.

       Measuring has to match the unit or the comparison is noise: a profile is
       metres of run, a board or sheet is square metres of face, and anything by
       the piece is a count. The unit comes from CATALOG, which the products
       fill when they register, so it is there at build time.

       Bodies of one member drawn as several segments still add up to the right
       run, so splitting is not a fault here. What shows up is a real difference
       in how much there is. */
    function _qtyAudit(P) {
      var st = _stage();
      if (!st || !st.group || typeof P !== 'function') return;

      var model = {}, count = {}, longest = {}, pieces = {}, byLayer = {}, sample = {}, runOf = {};
      var shortOf = {}, dims = {}, bakedRun = {}, nWhole = 0, nHeld = 0;
      /* core-v687 (RANCH thread, opt-in __PROD._qtyPieces): WHAT IS BILLED BY
         THE PIECE, COUNTED. The audit set those codes aside ("by the piece,
         not comparable") because a door is a frame, a leaf and a handle --
         so the pieces are counted, not the bodies: a body's qtyPiece (the
         product names its piece), else bodies touching (within 20 mm) are
         one piece, a baked body says its qtyN, one held under grade is one.
         A body may say it is billed as another code (qtyCode -- a fitout
         piece drawn under the fitout's one step) and what comes with it
         (qtyWith -- a faucet with its sink, chairs with their table). */
      var PCQ = (function () { try { return !!(__PROD && __PROD._qtyPieces); } catch (ePQ) { return false; } })(), PCS = {};
      function pcAdd(code, o, qN) {
        var u = o.userData || {}, P = PCS[code] || (PCS[code] = { ids: {}, boxes: [], baked: 0, with: 0, lay: {} });
        P.lay[(_layerOf(o) || '(none)').replace(/^[a-z]+-/, '')] = 1;
        if (u.qtyPiece != null) P.ids[String(u.qtyPiece)] = 1;
        else if (o.__held) P.baked += 1;
        else if (qN > 1) P.baked += qN;
        else { try { var Tq = global.THREE, bq = new Tq.Box3().setFromObject(o); bq.expandByScalar(0.02); P.boxes.push(bq); } catch (eBq) { P.baked += 1; } }
      }
      /* core-v682 (opt-in __PROD._qtyFull): a body cut at grade is measured as
         its whole box, one left out under grade from what its layer held */
      function one(o) {
        if (!o.isMesh || !o.geometry) return;
        var m = o.userData && o.userData.asm;
        var tag = m ? (typeof m === 'string' ? m : (m.code || m.key || '')) : '';
        if (PCQ && o.userData && o.userData.qtyCode) tag = String(o.userData.qtyCode);   /* core-v687 */
        if (PCQ && o.userData && o.userData.qtyWith && o.userData.qtyWith.length) {
          for (var qw = 0; qw < o.userData.qtyWith.length; qw++) {
            var cw = String(o.userData.qtyWith[qw]); (PCS[cw] || (PCS[cw] = { ids: {}, boxes: [], baked: 0, with: 0, lay: {} })).with++;
          }
        }
        if (!tag) return;
        var cat = CATALOG[tag] || null;
        var code = (cat && cat.code) ? cat.code : tag;
        var unit = String((CATALOG[code] && CATALOG[code].unit) || (cat && cat.unit) || '').toLowerCase();

        var d;
        try {
          var g = o.geometry;
          if (!g.boundingBox) g.computeBoundingBox();
          var bb = g.boundingBox, sc = o.scale || { x: 1, y: 1, z: 1 };
          d = [Math.abs(bb.max.x - bb.min.x) * (sc.x || 1),
               Math.abs(bb.max.y - bb.min.y) * (sc.y || 1),
               Math.abs(bb.max.z - bb.min.z) * (sc.z || 1)];
        } catch (eG) { return; }
        var qBx = o.userData && o.userData.qtyBox;
        if (qBx && qBx.length === 3 && _qtyFullOn()) { d = [qBx[0] * (sc.x || 1), qBx[1] * (sc.y || 1), qBx[2] * (sc.z || 1)]; if (o.__held) nHeld++; else nWhole++; }
        d.sort(function (a, b) { return b - a; });

        /* ONLY MEASURED UNITS ARE COMPARABLE. Counting bodies for a piece is
           meaningless: a door is drawn as a frame, a leaf, glazing and a
           handle, and the bill rightly says one door. My first run reported
           twelve such "disagreements" -- every one of them my error, and
           between them they buried the eight that are real. Pieces are counted
           and listed apart, without a verdict (Marek). */
        /* THE RUN OF A PROFILE IS THE SIDE THAT IS NOT ITS SECTION.

           I took the longest side. Right for a 2.4 m stud, wrong for an 80 mm
           packer: its longest side is its own 120 mm depth, so it was billed at
           its section instead of its length. 336 of 448 truss webs are under
           300 mm, and that alone made the code read 1.31x.

           A profile's section is the pair of sizes that repeats on every piece
           of that code, so the bodies themselves say what it is: collect the
           three sides now, find the two that recur, and the run is whatever is
           left (Marek). */
        /* A BODY BAKED FROM MANY PIECES SAYS WHAT THEY WERE (core-v663, RANCH
           thread): userData.qtyN pieces, and for an area or a volume qtyV, the
           sum the pieces would have measured one by one -- its own box is the
           whole tray, not its sheets. A body without them as before. */
        var uQ = o.userData || {}, qN = (uQ.qtyN > 0) ? uQ.qtyN : 1;
        var qV = (typeof uQ.qtyV === 'number' && isFinite(uQ.qtyV) && uQ.qtyV >= 0) ? uQ.qtyV : null;
        var v = 0;
        /* AND A BAKED PROFILE SAYS ITS RUN (core-v664, RANCH thread):
           userData.qtyRun, the metres its pieces would have measured one by
           one -- its own box is the whole batch. Kept out of the section
           finding below and added back after it. A body without it as before. */
        var qR = (unit === 'm' && typeof uQ.qtyRun === 'number' && isFinite(uQ.qtyRun) && uQ.qtyRun >= 0) ? uQ.qtyRun : null;
        if (qR != null) {
          v = qR;
          bakedRun[code] = (bakedRun[code] || 0) + qR;
        }
        else if (unit === 'm') {
          v = d[0];                                       /* provisional */
          if (!dims[code]) dims[code] = [];
          dims[code].push([d[0], d[1], d[2], o]);
        }
        else if (unit === 'm2') v = (qV != null) ? qV : d[0] * d[1];          /* the face of a board */
        else if (unit === 'm3') v = (qV != null) ? qV : d[0] * d[1] * d[2];
        else { pieces[code] = (pieces[code] || 0) + qN; if (PCQ && /^(pcs|ks|pc|ea|set|)$/.test(unit) && CATALOG[code]) pcAdd(code, o, qN); return; }
        model[code] = (model[code] || 0) + v;
        count[code] = (count[code] || 0) + ((unit === 'm' && qR == null) ? 1 : qN);
        if (!(longest[code] > d[0])) longest[code] = d[0];
        /* the biggest body of this code, kept so a ratio can be read without
           opening the source -- half of it went in and the other half did not,
           because the script that added it never wrote the file */
        runOf[code] = (runOf[code] || 0) + d[0];
        /* A PIECE SHORTER THAN ITS OWN SECTION IS MEASURED WRONG HERE.

           The run of a profile is taken as its longest side. That is right for a
           2.4 m stud and wrong for an 80 mm packer, whose longest side is its own
           120 mm depth -- so every short piece is counted at its section instead
           of its length. 313 hip webs averaging 390 mm against a bill averaging
           210 says that is exactly what is happening, and it is my arithmetic,
           not the product's (Marek). Counted, so it can be seen rather than
           argued about. */
        if (d[0] < 0.30) shortOf[code] = (shortOf[code] || 0) + 1;
        if (!sample[code] || d[0] * d[1] > sample[code][0] * sample[code][1]) {
          sample[code] = [d[0], d[1], d[2]];
        }
        /* WHICH LAYER THE RUN IS IN. Three faults so far have been one layer
           drawing under another layer's code, and each time I read source to
           find which. The model knows -- so it says. */
        var lyr = _layerOf(o) || '(none)';
        if (!byLayer[code]) byLayer[code] = {};
        byLayer[code][lyr] = (byLayer[code][lyr] || 0) + v;
      }
      st.group.traverse(one);
      if (_qtyFullOn()) {
        var GB0 = { boundingBox: { min: { x: 0, y: 0, z: 0 }, max: { x: 0, y: 0, z: 0 } } }, hc;
        st.group.children.forEach(function (lgH) {
          var HL = lgH && lgH._qtyHeld; if (!HL || !HL.length) return;
          for (hc = 0; hc < HL.length; hc++) {
            if (!HL[hc] || !HL[hc].asm) continue;
            one({ isMesh: true, geometry: GB0, scale: { x: 1, y: 1, z: 1 }, parent: lgH, __held: true, userData: { asm: HL[hc].asm, qtyBox: HL[hc].d } });
          }
        });
      }

      /* THE MERGE IS GONE, AND SO IS THE GUESSING.

         I tried to have the audit spot a profile drawn as several bodies -- an L
         trim is two legs and one length. Geometry cannot tell that from slats at
         75 mm centres, which are separate pieces that look identical to the test.
         Two attempts: the first merged 448 truss webs into three, the second
         halved the post-wall slats that had matched their bill exactly.

         So the audit measures what it can measure and says so. Four codes are
         drawn as multi-leg profiles and will always read high here -- they are
         named below rather than silently excluded, because a number nobody
         explains is how this started (Marek: hlavne dobre).

         What that leaves is a plain question: does the model hold as much as the
         bill says. For everything except those four, it does or it does not. */
      var LEGGED = { FVW02660: 2, ROM02401: 2, ROV00101: 3, ROG01251: 2 };

      /* AND SOME ITEMS ARE NEVER DRAWN WHOLE, ON PURPOSE.

         Reinforcing bar runs inside concrete: the model shows where it is, not
         all of it -- eighty stubs of 36 mm against 48 m in the bill. Concrete is
         the same the other way: the pier is drawn, what is cast below ground is
         not. Neither is a fault, and reporting them as one every build is the
         noise this audit exists to cut (Marek).

         Named rather than hidden, as with the multi-leg profiles: a number
         somebody can explain beats a number that quietly vanishes. */
      var PARTIAL = /^(FCO0000[23]|FEC00002|grd\.)/;

      /* NOW THE SECTION IS KNOWN, SO THE RUN CAN BE. */
      var dk;
      for (dk in dims) {
        if (!dims[dk].hasOwnProperty) continue;
        if (!dims.hasOwnProperty(dk)) continue;
        var D = dims[dk], f = {}, q3, z3, key3;
        for (q3 = 0; q3 < D.length; q3++) {
          for (z3 = 0; z3 < 3; z3++) {
            key3 = Math.round(D[q3][z3] * 1000);
            f[key3] = (f[key3] || 0) + 1;
          }
        }
        /* THE SECTION IS ON EVERY PIECE, WHICH IS WHAT PICKS IT OUT.

           Taking the two commonest sizes was not enough: when two hundred posts
           share a height, that height is as common as the section and gets taken
           for one. A section size is on ALMOST EVERY body of the code, so the
           test is presence, not popularity -- and where more than two qualify,
           the two smallest are the section, because a profile is longer than it
           is thick (Marek). */
        var cand = [], kk3;
        for (kk3 in f) {
          if (!f.hasOwnProperty(kk3)) continue;
          if (f[kk3] >= D.length * 0.9) cand.push(Number(kk3));
        }
        cand.sort(function (p, q) { return p - q; });
        var best1 = -1, best2 = -1;
        /* A SQUARE SECTION (core-v665, RANCH thread): a 4x4's one size is on
           every piece twice, so it is the only candidate and the fallback took
           the commonest length for the other side -- 6 posts of a pergola read
           as 100 mm each. Twice on (nearly) every piece is both sides.
           core-v677: and it is tested FIRST -- four condensate drops of one
           length made that length a candidate too, the pair (16, 778) was
           taken for the section and each drop measured 16 mm. The smallest
           size twice on every piece is the section, whatever else recurs. */
        if (cand.length >= 1 && f[cand[0]] >= 2 * D.length * 0.9) { best1 = cand[0]; best2 = cand[0]; }
        else if (cand.length >= 2) { best1 = cand[0]; best2 = cand[1]; }
        else {
          /* nothing is shared by nearly all of them: fall back to the commonest
             pair, which is what it did before */
          var n1 = -1, n2 = -1;
          for (kk3 in f) {
            if (!f.hasOwnProperty(kk3)) continue;
            if (f[kk3] > n1) { best2 = best1; n2 = n1; best1 = Number(kk3); n1 = f[kk3]; }
            else if (f[kk3] > n2) { best2 = Number(kk3); n2 = f[kk3]; }
          }
        }
        var tot3 = 0;
        for (q3 = 0; q3 < D.length; q3++) {
          var sides = [Math.round(D[q3][0] * 1000), Math.round(D[q3][1] * 1000),
                       Math.round(D[q3][2] * 1000)];
          var used1 = false, used2 = false, run3 = null, z4;
          for (z4 = 0; z4 < 3; z4++) {
            if (!used1 && Math.abs(sides[z4] - best1) <= 2) { used1 = true; continue; }
            if (!used2 && Math.abs(sides[z4] - best2) <= 2) { used2 = true; continue; }
            if (run3 === null) run3 = sides[z4];
          }
          /* if both section sides were not found, the piece is not that profile
             -- fall back to its longest side, which is what it was before */
          tot3 += ((run3 === null) ? sides[0] : run3) / 1000;
        }
        model[dk] = tot3 + (bakedRun[dk] || 0);   /* core-v664 */
      }

      /* THE BILL PER LAYER, NOT JUST ITS TOTAL.

         The model side has said which layers a code is drawn in for a while, and
         the bill side has only ever given one number -- so a mismatch could be in
         any of three layers and I have guessed wrong twice. Both sides, layer by
         layer, put the finger on one builder (Marek: chci to mit presne). */
      var billedBy = {};
      var billed = {}, lay, b, k;
      for (lay in _bill) {
        if (!_bill.hasOwnProperty(lay)) continue;
        if (lay.indexOf('-coating') >= 0) continue;       /* paint has no bodies */
        b = _bill[lay];
        for (k in b) {
          if (!b.hasOwnProperty(k)) continue;
          var c2 = (CATALOG[k] && CATALOG[k].code) ? CATALOG[k].code : k;
          billed[c2] = (billed[c2] || 0) + b[k];
          if (!billedBy[c2]) billedBy[c2] = {};
          billedBy[c2][lay] = (billedBy[c2][lay] || 0) + b[k];
        }
      }

      /* fasteners, chemistry and overheads are billed and never drawn, by
         design; they are not a disagreement */
      /* AND THE SERVICES ARE BILLED WITHOUT BODIES TOO. Sockets, cable, boxes,
         breakers and the rest have catalogue rows and no geometry, and 68 of
         them were being reported as missing on every build -- noise of exactly
         the kind this audit exists to cut through. */
      var NO_BODY = /^(FAS|FAT|FEC|ODP|SBG|WDS|WDF|MFP|OVH|EE[A-Z]|LPS|SFF|FFC)/;

      var off = [], onlyModel = [], onlyBill = [], legged = [], partial = [], c, rows = 0;
      for (c in model) {
        if (!model.hasOwnProperty(c)) continue;
        rows++;
        var u = String((CATALOG[c] && CATALOG[c].unit) || '').toLowerCase();
        if (billed[c] == null) { onlyModel.push(c + ' ' + (Math.round(model[c] * 10) / 10) + ' ' + (u || 'pcs')); continue; }
        var mv = model[c], bv = billed[c];
        if (!(mv > 0) || !(bv > 0)) continue;
        var ratio = mv / bv;
        if (ratio > 1.2 || ratio < 0.8) {
          /* one entry per layer: what the model holds there against what that
             same layer bills */
          var spread = [], lk2, seenL = {};
          for (lk2 in (byLayer[c] || {})) if (byLayer[c].hasOwnProperty(lk2)) seenL[lk2] = 1;
          for (lk2 in (billedBy[c] || {})) if (billedBy[c].hasOwnProperty(lk2)) seenL[lk2] = 1;
          for (lk2 in seenL) {
            if (!seenL.hasOwnProperty(lk2)) continue;
            var mL = Math.round((byLayer[c] && byLayer[c][lk2]) || 0);
            var bL = Math.round((billedBy[c] && billedBy[c][lk2]) || 0);
            spread.push(lk2.replace(/^[a-z]+-/, '') + ' ' + mL + '/' + bL);
          }
          if (PARTIAL.test(c)) {
            partial.push(c + ' ' + (Math.round(mv * 10) / 10) + '/' + (Math.round(bv * 10) / 10) +
                         ' (' + (u || 'pcs') + ', not drawn in full)');
            continue;
          }
          if (LEGGED[c]) {
            legged.push(c + ' ' + (Math.round(mv * 10) / 10) + '/' + (Math.round(bv * 10) / 10) +
                        ' (drawn as ' + LEGGED[c] + ' legs per length)');
            continue;
          }
          /* AND ONE WORKED EXAMPLE. A ratio alone has cost four rounds of
             guessing; the biggest body with its three sides says whether the
             pieces are too big or simply too many. */
          /* AND FOR A SHEET CODE, THE RUN AS WELL AS THE AREA.

             The cement board holds twice what it bills, from the same loop over
             the same segments -- so the pieces are right and there are too many
             of them, or they are counted twice. Area alone cannot separate
             those; the total length can, because it is the number the builder
             itself adds up (Marek). */
          var ex = sample[c] || null;
          var runTot = (u === 'm2' && runOf[c]) ? (', run ' + Math.round(runOf[c]) + ' m') : '';
          var shortN = shortOf[c] ? (', ' + shortOf[c] + ' under 300 mm') : '';
          off.push(c + ': model ' + (Math.round(mv * 10) / 10) + ' vs bill ' +
                   (Math.round(bv * 10) / 10) + ' ' + (u || 'pcs') +
                   ' = ' + (Math.round(ratio * 100) / 100) + 'x, ' + count[c] + ' bod' +
                   (ex ? (', biggest ' + Math.round(ex[0] * 1000) + 'x' +
                          Math.round(ex[1] * 1000) + 'x' + Math.round(ex[2] * 1000) + ' mm') : '') +
                   runTot + shortN +
                   ' | ' + spread.sort().join(', '));
        }
      }
      for (c in billed) {
        if (!billed.hasOwnProperty(c)) continue;
        if (model[c] != null) continue;
        /* AND A PIECE HAS A BODY -- it is just not measured here. I put every
           piece aside so its bodies would not be miscounted, and then reported
           the same codes as having no body at all. Fifty-two of them, all
           drawn, every build (Marek). */
        if (pieces[c] != null) continue;
        if (NO_BODY.test(c)) continue;
        onlyBill.push(c + ' ' + (Math.round(billed[c] * 10) / 10));
      }

      /* THE LONGEST PIECE IN THE BUILDING, MEASURED THE WAY THE CUT LIST NOW
         MEASURES. The cut list itself only runs when a PDF is made, and the log
         is cleared by the next rebuild, so its line is easy to miss. This runs
         on every build and tests the same thing: a sloping member used to come
         out short because its world box is shorter than it is (Marek). */
      var longAll = 0, longCode = '', lk4;
      for (lk4 in longest) {
        if (!longest.hasOwnProperty(lk4)) continue;
        if (longest[lk4] > longAll) { longAll = longest[lk4]; longCode = lk4; }
      }

      var pc = 0, pk;
      for (pk in pieces) if (pieces.hasOwnProperty(pk)) pc++;
      P('qty audit  ' + rows + ' measured code(s) | ' + off.length +
        ' disagree with the bill by more than a fifth | ' + onlyModel.length +
        ' drawn but not billed | ' + onlyBill.length + ' billed with no body | ' +
        pc + ' code(s) by the piece, not comparable' +
        ((nWhole || nHeld) ? (' | under grade measured whole (core-v682): ' + nWhole + ' cut at grade, ' + nHeld + ' held under it') : ''));
      /* AND IT IS NOT THE CUT LIST. I labelled this line "cut list" and then
         read its answer as a fault in the cut list -- a drip edge flashing is
         the longest body in the model, which is true and means nothing, because
         the cut list excludes flashings by name. A line that names itself wrong
         invents work (Marek). */
      P('qty audit  longest body anywhere ' + Math.round(longAll * 1000) + ' mm (' +
        longCode + ') -- own geometry, not the world box; the cut list is separate');
      off.sort();
      var i;
      for (i = 0; i < off.length && i < 20; i++) P('qty audit  ' + off[i]);
      if (off.length > 20) P('qty audit  ... and ' + (off.length - 20) + ' more');
      if (legged.length) P('qty audit  multi-leg profiles, read high by design: ' + legged.sort().join(' | '));
      if (partial.length) P('qty audit  never drawn in full, by design: ' + partial.sort().join(' | '));
      if (onlyModel.length) P('qty audit  drawn, not billed: ' + onlyModel.sort().join(' | '));
      /* core-v687 (opt-in __PROD._qtyPieces): the pieces, counted against the
         bill. Consumables and overheads (screws, glue, foam, labour) are
         bought and never drawn: not listed among the bodiless. */
      if (PCQ) {
        var NOPC = /^(FAS|FAT|FEC|ODP|SBG|WDS|WDF|MFP|OVH|SFF|FFC)/, pcCodes = {}, pck, nPc = 0, pcAgree = 0, pcDiff = [], pcOnly = [], pcNone = [];
        for (pck in billed) if (billed.hasOwnProperty(pck)) { var uPc = String((CATALOG[pck] && CATALOG[pck].unit) || '').toLowerCase(); if (/^(pcs|ks|pc|ea|set)$/.test(uPc)) pcCodes[pck] = 1; }
        for (pck in PCS) if (PCS.hasOwnProperty(pck)) pcCodes[pck] = 1;
        for (pck in pcCodes) {
          if (!pcCodes.hasOwnProperty(pck)) continue;
          var Pq = PCS[pck], dn = 0;
          if (Pq) {
            var bxq = Pq.boxes, par = [], iq, jq;
            for (iq = 0; iq < bxq.length; iq++) par.push(iq);
            var fq = function (x) { while (par[x] !== x) { par[x] = par[par[x]]; x = par[x]; } return x; };
            for (iq = 0; iq < bxq.length; iq++) for (jq = iq + 1; jq < bxq.length; jq++) if (bxq[iq].intersectsBox(bxq[jq])) { var ra = fq(iq), rb = fq(jq); if (ra !== rb) par[ra] = rb; }
            var rts = {}; for (iq = 0; iq < bxq.length; iq++) rts[fq(iq)] = 1;
            dn = Object.keys(Pq.ids).length + Object.keys(rts).length + Pq.baked + Pq.with;
          }
          var bnq = Math.round((billed[pck] || 0) * 100) / 100;
          if (!(dn > 0)) { if (bnq > 0 && !NOPC.test(pck)) pcNone.push(pck + ' ' + bnq); continue; }
          nPc++;
          if (!(bnq > 0)) { pcOnly.push(pck + ' ' + dn); continue; }
          if (Math.abs(dn - bnq) < 0.01) { pcAgree++; continue; }
          var bl2 = [], lq;
          for (lq in (billedBy[pck] || {})) if (billedBy[pck].hasOwnProperty(lq)) bl2.push(lq.replace(/^[a-z]+-/, '') + ' ' + (Math.round(billedBy[pck][lq] * 100) / 100));
          pcDiff.push(pck + ' ' + dn + '/' + bnq + ' (drawn in ' + Object.keys(Pq.lay).join('/') + '; billed in ' + bl2.join(', ') + ')');
        }
        P('qty audit  pieces (core-v687): ' + nPc + ' code(s) drawn by the piece -- ' + pcAgree + ' agree with the bill, ' + pcDiff.length + ' differ (drawn/billed), ' +
          pcOnly.length + ' drawn, not billed | ' + pcNone.length + ' billed, none drawn (screws, glue, foam and overheads left out)');
        if (pcDiff.length) P('qty audit  pieces differ: ' + pcDiff.sort().join('; '));
        if (pcOnly.length) P('qty audit  pieces drawn, not billed: ' + pcOnly.sort().join('; '));
        if (pcNone.length) P('qty audit  pieces billed, none drawn: ' + pcNone.sort().join(', '));
      }
      /* THE BODILESS LIST WAS THE LONGEST LINE IN THE SNAPSHOT and it never
         changes: furniture, fittings and fixings that are bought and not drawn.
         A count says the same thing (Marek: zmensi log na to co potrebujes). */
      if (onlyBill.length) P('qty audit  billed with no body: ' + onlyBill.length +
                             ' code(s), all bought-not-drawn');
    }

    function _matAudit(P) {
      var st = _stage();
      if (!st || !st.group || typeof P !== 'function') return;
      var byMat = [], bodies = 0, byMatM = (typeof Map === 'function') ? new Map() : null;   /* core-v621: a Map, not a search */
      st.group.traverse(function (o) {
        if (!o.isMesh || !o.material) return;
        var m = o.userData && o.userData.asm;
        var tag = m ? (typeof m === 'string' ? m : (m.code || m.key || '')) : '';
        if (!tag) return;
        var code = (CATALOG[tag] && CATALOG[tag].code) ? CATALOG[tag].code : tag;
        bodies++;
        var mats = o.material.length ? o.material : [o.material];
        var q;
        for (q = 0; q < mats.length; q++) {
          var rec = null, r;
          if (byMatM) rec = byMatM.get(mats[q]) || null;
          else for (r = 0; r < byMat.length; r++) if (byMat[r].mat === mats[q]) { rec = byMat[r]; break; }
          if (!rec) { rec = { mat: mats[q], codes: {}, n: 0, layer: _layerOf(o) }; byMat.push(rec); if (byMatM) byMatM.set(mats[q], rec); }
          rec.codes[code] = (rec.codes[code] || 0) + 1;
          rec.n++;
        }
      });
      var bad = [], i, k;
      for (i = 0; i < byMat.length; i++) {
        var cs = [];
        for (k in byMat[i].codes) if (byMat[i].codes.hasOwnProperty(k)) cs.push(k + ':' + byMat[i].codes[k]);
        if (cs.length > 1) bad.push({ layer: byMat[i].layer || '(no layer)', n: byMat[i].n, cs: cs.sort() });
      }
      P('material audit  ' + bodies + ' tagged bod(ies) on ' + byMat.length + ' material(s) | ' +
        bad.length + ' material(s) worn by more than one code' +
        (bad.length ? ' -- picking one of these tints the others' : ''));
      /* EVERY LINE CARRIES THE PREFIX, or the snapshot filter eats it.

         The detail lines began with two spaces and FOCUS matches on the first
         word, so the count came through and the findings did not -- a report
         that says "7 problems" and lists none (Marek). */
      bad.sort(function (a, b) { return b.cs.length - a.cs.length; });
      for (i = 0; i < bad.length && i < 14; i++) {
        P('material audit  ' + bad[i].layer + ': ' + bad[i].n +
          ' bod(ies) on one material -- ' + bad[i].cs.join(' '));
      }
      if (bad.length > 14) P('material audit  ... and ' + (bad.length - 14) + ' more');
    }

    function _tagAudit(P) {
        var st = _stage();
        if (!st || !st.group || typeof P !== 'function') return;
        var lines = [], totalUntagged = 0, i, j, k;
        var drawnAll = {}, billedAll = {}, drawnWhere = {}, billedWhere = {};

        for (i = 0; i < _layers.length; i++) {
          var id = _layers[i].id;
          var lg = null;
          for (j = 0; j < st.group.children.length; j++) {
            if (st.group.children[j].userData && st.group.children[j].userData.layer === id) { lg = st.group.children[j]; break; }
          }
          if (!lg) continue;
          var byTag = {}, untagged = 0, meshes = 0;
          lg.traverse(function (o) {
            if (!o.isMesh) return;
            meshes++;
            var m = o.userData && o.userData.asm;
            var tag = m ? (typeof m === 'string' ? m : (m.code || m.key || '')) : '';
            if (!tag) { untagged++; return; }
            byTag[tag] = (byTag[tag] || 0) + 1;
          });
          totalUntagged += untagged;
          if (untagged) lines.push('  ' + id + ': ' + meshes + ' mesh(es) | UNTAGGED ' + untagged);

          for (k in byTag) {
            if (!byTag.hasOwnProperty(k)) continue;
            var c = (CATALOG[k] && CATALOG[k].code) ? CATALOG[k].code : k;
            drawnAll[c] = (drawnAll[c] || 0) + byTag[k];
            if (!drawnWhere[c]) drawnWhere[c] = [];
            if (drawnWhere[c].indexOf(id) < 0) drawnWhere[c].push(id);
          }
          var rows = [];
          try { rows = _rows(id) || []; } catch (eR) { rows = []; }
          for (j = 0; j < rows.length; j++) {
            var rc = rows[j].code;
            billedAll[rc] = (billedAll[rc] || 0) + (rows[j].qty || 0);
            if (!billedWhere[rc]) billedWhere[rc] = [];
            if (billedWhere[rc].indexOf(id) < 0) billedWhere[rc].push(id);
          }
          var shape = [];
          for (k in byTag) if (byTag.hasOwnProperty(k)) shape.push(k + ':' + byTag[k]);
          if (shape.length) lines.push('    ' + id + ' tags -> ' + shape.sort().join(' '));
        }
        /* A BILL OF ITS OWN WITH NO LAYER (core-v665, RANCH thread): a product
           may bill a part into a bucket of its own so the PDF can print it apart
           -- the pergola, drawn with the terrace -- and those rows were never
           read here, so everything drawn for them came out DRAWN NOT BILLED.
           Every bucket that is not a layer is read too; coating stays out. */
        (function () {
          var ids = {}, q, bk, rr;
          for (q = 0; q < _layers.length; q++) ids[_layers[q].id] = 1;
          for (bk in _bill) {
            if (!_bill.hasOwnProperty(bk) || ids[bk] || bk.indexOf('-coating') >= 0) continue;
            try { rr = _rows(bk) || []; } catch (eB) { rr = []; }
            for (q = 0; q < rr.length; q++) {
              billedAll[rr[q].code] = (billedAll[rr[q].code] || 0) + (rr[q].qty || 0);
              if (!billedWhere[rr[q].code]) billedWhere[rr[q].code] = [];
              if (billedWhere[rr[q].code].indexOf(bk) < 0) billedWhere[rr[q].code].push(bk);
            }
          }
        })();

        /* THE FASTENERS AND THE CHEMISTRY ARE BILLED AND NEVER DRAWN, ON
           PURPOSE. Screws, nails, tape, foam, adhesive and paint have catalogue
           rows and no geometry; reporting them as missing every single build is
           the loudest false alarm there is. */
        var NOT_DRAWN_OK = /^(FAS|WDS|WDF|FEC|MFP|OVH|ODP|SBG|FAT)/;

        var noBill = [], noDraw = [];
        for (k in drawnAll) {
          if (!drawnAll.hasOwnProperty(k)) continue;
          if (!(k in billedAll)) noBill.push(k + ' x' + drawnAll[k] + ' (in ' + drawnWhere[k].join(', ') + ')');
        }
        for (k in billedAll) {
          if (!billedAll.hasOwnProperty(k)) continue;
          if (billedAll[k] <= 0) continue;
          if (k in drawnAll) continue;
          if (NOT_DRAWN_OK.test(k)) continue;
          noDraw.push(k + ' (billed in ' + billedWhere[k].join(', ') + ')');
        }
        if (noBill.length) lines.push('  DRAWN NOT BILLED: ' + noBill.sort().join(' | '));
        if (noDraw.length) lines.push('  BILLED NOT DRAWN: ' + noDraw.sort().join(' | '));

        P('tag audit  ' + (totalUntagged ? (totalUntagged + ' untagged') : 'nothing untagged') +
          ' | ' + (noBill.length + noDraw.length) + ' mismatch(es) across the whole building' +
          ' | ' + Object.keys(drawnAll).length + ' code(s) drawn, ' +
          Object.keys(billedAll).length + ' billed');
        for (i = 0; i < lines.length; i++) P(lines[i]);
      }



    function _rows(layer) {
      var out = [], k, b = _bill[layer] || {};
      for (k in b) {
        if (!b.hasOwnProperty(k)) continue;
        var c = CATALOG[k];
        if (!c || !c.code) continue;
        out.push({ code: c.code, qty: Math.round(b[k] * 100) / 100, unit: c.unit, label: c.label });
      }
      return out;
    }

    /* a caption in the drawer is sentence case; a key that comes back in
       capitals (FITOUT) is folded to one capital */
    function _cap(s) {
      s = String(s || '');
      return (s === s.toUpperCase()) ? (s.charAt(0) + s.slice(1).toLowerCase()) : s;
    }

    /* THE SECTION IS OFF WHILE EDITING. Its caps and stencil clones are built
       once per model and do not follow a piece that is dragged, so in the plan
       they read as coloured shapes left behind (Marek). Opening the drawer or
       switching to the plan turns the cut off; the rail button turns it back on. */
    function _cutOff(why) {
      try {
        if (_cutTool && _cutTool.on()) { _cutTool.toggle(); _log('cut planes off -- ' + why); }
      } catch (e) {}
    }

    function _asmEnter() {
      var st = _stage(), T = global.THREE; if (!st || !T) return;
      if (_asm.on) { _asmLayout(); return; }
      _asm.on = true;
      /* the cut across the middle, plane hidden, handle kept (it can be moved) */
      var U = _UI();
      if (!_cutTool && U && U.cutPlanes) {
        _cutTool = U.cutPlanes({ stage: _stage, rootId: 'cf-root', buttonId: 'cf-btn-colors', unitBtnId: 'cf-btn-unit',
                                 fmtLen: fmtLen, log: _log, controls: _navControls, view: function () { return STATE.view; } });
      }
      if (_cutTool && !(API.explode && API.explode() > 0.02)) { _cutTool.set({ axis: 'x', t: 0.5, flip: false, on: true }); try { _cutTool.set({ plane: false }); } catch (e) {} }
      /* the panel */
      var root = $('cf-root'); if (!root) return;
      if (!$('cf-asm-css')) {
        var css = document.createElement('style');
        css.id = 'cf-asm-css';
        css.textContent =
          '#cf-asm-panel{position:absolute;left:calc(50% + 150px);top:14%;z-index:9;display:flex;flex-direction:column;gap:12px;pointer-events:none;max-width:min(40%,300px);}' +
          '@media (max-width: 900px){#cf-asm-panel{left:calc(50% + 110px);}}' +
          '.cf-asm-row{display:flex;flex-direction:column;gap:1px;padding:2px 0;}' +
          '.cf-asm-name{font:800 14px Montserrat,sans-serif;color:#1A1C1D;white-space:nowrap;line-height:1.15;text-shadow:0 1px 3px rgba(255,255,255,0.9);}' +
          '.cf-asm-mm{font:600 11px Montserrat,sans-serif;color:#9CA3AF;line-height:1;text-shadow:0 1px 3px rgba(255,255,255,0.9);}' +
          '.cf-asm-head{font:800 11px Montserrat,sans-serif;letter-spacing:.09em;color:#6B7280;padding-bottom:2px;border-bottom:1px solid #E5E7EB;text-shadow:0 1px 3px rgba(255,255,255,0.9);}';
        document.head.appendChild(css);
      }
      var el = document.createElement('div');
      el.id = 'cf-asm-panel';
      root.appendChild(el);
      var head = document.createElement('div'); head.className = 'cf-asm-head'; head.textContent = t('exterior_wall', 'EXTERIOR WALL'); el.appendChild(head);
      var L = __PROD._asmLayers(), i;
      _asm.rows = [];
      for (i = 0; i < L.length; i++) {
        var row = document.createElement('div'); row.className = 'cf-asm-row';
        var nm = document.createElement('span'); nm.className = 'cf-asm-name'; nm.textContent = L[i].name;
        var mm = document.createElement('span'); mm.className = 'cf-asm-mm'; mm.textContent = fmtLen(L[i].t);
        row.appendChild(nm); row.appendChild(mm); el.appendChild(row);
        _asm.rows.push({ row: row, lay: L[i] });
      }
      _asm.panel = el;
      /* the leaders: one line segment per row, in the scene */
      var g = new T.Group(); g.name = 'asm-leaders'; g.userData.arSkip = true;
      var pos = new Float32Array(L.length * 6);
      var geo = new T.BufferGeometry(); geo.setAttribute('position', new T.BufferAttribute(pos, 3));
      var ln = new T.LineSegments(geo, new T.LineBasicMaterial({ color: 0x1A1C1D, transparent: true, opacity: 0.8, depthTest: false }));
      ln.renderOrder = 70; g.add(ln); st.scene.add(g);
      _asm.grp = g; _asm.lines = ln;
      if (!_asm.bound) {
        _asm.bound = true;
        try { var ctl = st.persControls || st.controls; if (ctl && ctl.addEventListener) ctl.addEventListener('change', function () { if (_asm.on) _asmLayout(); }); } catch (e2) {}
        try { global.addEventListener('resize', function () { if (_asm.on) _asmLayout(); }); } catch (e3) {}
      }
      _asmLayout();
      _log('assembly: section across the middle, ' + L.length + ' wall layer(s) named');
    }

      /* =========================================================================
         PICK A PART IN THE EXPLODED VIEW, GET ITS DRAWER (core-v85).

         Every mesh already knows what it is: the build tags whatever a layer adds
         with the catalogue item that was current at that moment
         (userData.asm), and the raycast that finds what is under a finger is the
         same one the rest of the file uses. The asset drawer already wants
         exactly that -- a code, a row and a quantity -- and resolves the NAME
         itself from the raw catalogue row, localised, so nothing here has to
         translate anything.

         So this is only the wiring: a tap on the canvas while the walk is at the
         exploded step reads the tag under the finger and opens the drawer on it.
         Nothing is drawn, hidden or replaced -- which is the whole point, after
         an evening spent failing to swap the model for a sample (Marek).

         ONLY IN THE EXPLODED VIEW, on purpose. There every part stands on its
         own and what you touch is what you meant; on the finished building the
         nearest surface answers for everything behind it, so a wall would only
         ever offer its cladding. An untagged body is ignored and named in the
         log, so the gaps in the tagging show themselves. */
      var _pickOn = false, _pickEl = null, _pickDown = null, _pickMissing = {};

      /* THE TAG IS A CATALOGUE KEY, NOT ALWAYS A CODE.

         A builder marks with whatever it has: sometimes the code itself
         (FCP00009), more often the internal key (steel.screwPile). The drawer
         was handed the key, so the row lookup found nothing and CODE, CATEGORY,
         UNIT, PRICE and QUANTITY all came up blank while the name fell back to
         the baked English label -- exactly what the screenshot shows. _asmName
         already resolves this one way; everything here has to resolve it the
         same way, once, at the door (Marek: jediny zdroj pravdy). */
      function _pickCode(tag) {
        if (!tag) return null;
        var c = String(tag);
        if (CATALOG[c] && CATALOG[c].code) return String(CATALOG[c].code);
        return c;
      }
      function _pickRow(code) {
        var pd = global.productData;
        if (!pd || !pd.length || !code) return null;
        for (var i = 0; i < pd.length; i++) if (pd[i] && pd[i][0] === code) return pd[i];
        return null;
      }
      /* what the project has of this item, across every layer -- matched on the
         resolved code, so a key and its code count as the same thing */
      function _pickQty(code) {
        var q = 0, lay, b, k;
        for (lay in _bill) {
          if (!_bill.hasOwnProperty(lay)) continue;
          b = _bill[lay];
          for (k in b) {
            if (!b.hasOwnProperty(k)) continue;
            if (_pickCode(k) === code) q += b[k];
          }
        }
        return Math.round(q * 100) / 100;
      }
      /* THE SAME NUMBERS THE BUDGET SHOWS, from the same place the budget gets
         them: the unit price out of LevaBudgetCore, the totals multiplied by
         what this project actually has. One source (Marek). */
      function _pickMoney(code, qty) {
        try {
          var bc = global.LevaBudgetCore;
          if (bc && bc.costOf) {
            var c = bc.costOf(code, qty);
            if (c && (c.material > 0 || c.execution > 0)) {
              return { unit: qty > 0 ? (c.material / qty) : 0, total: c.material,
                       execution: c.execution, minutes: c.minutes, from: 'budget' };
            }
          }
        } catch (e) {}
        /* AND A SECOND PLACE TO ASK.

           costOf goes through the budget snippet's own price lookup, which
           reads window.user_prices -- and on a page where the budget has not
           opened, that is empty, so every row came out at nought while the
           card on screen showed real money. The bridge answers from the same
           store but knows the stored figure is per METRIC unit and converts
           it; it is the one the budget itself uses to draw a row (Marek). */
        try {
          var br = global.ConfiguratorBudgetBridge;
          if (br && br.getUserPrice) {
            var up = br.getUserPrice(code);
            var p = up && (up.displayPrice != null ? up.displayPrice : up.price);
            if (p != null && isFinite(p) && p > 0) {
              return { unit: p, total: p * qty, execution: 0, minutes: 0, from: 'bridge' };
            }
          }
        } catch (e2) {}
        return null;
      }
      /* AND THE QUANTITY HAS TO MATCH THE UNIT IT IS WRITTEN IN.

         The label now comes from the sheet's imperial column, but the number
         beside it is still what the model counted -- and the model counts in
         metres. "33.31 FT" for 33.31 m is worse than the metric label was,
         because it is confidently wrong. The conversion is by what the unit
         measures, not by a column: a length, an area, a volume or a count
         (Marek: cely system je na imperial, v asset jsou metric). */
      function _pickConv(uMet, uImp) {
        var a = String(uMet || '').toUpperCase().replace(/\s/g, '');
        var b = String(uImp || '').toUpperCase().replace(/\s/g, '');
        if (a === b) return 1;                       /* PCS, SET, and the like */
        if (a === 'M'  && b === 'FT')  return 3.280839895;
        if (a === 'M2' && (b === 'FT2' || b === 'SQFT')) return 10.7639104;
        if (a === 'M3' && (b === 'FT3' || b === 'CUFT')) return 35.3146667;
        if (a === 'M3' && (b === 'YD3' || b === 'CUYD')) return 1.30795062;
        if (a === 'KG' && b === 'LB')  return 2.20462262;
        if (a === 'L'  && (b === 'GAL' || b === 'GALLON')) return 0.264172052;
        return 1;                                    /* unknown pair: leave it alone */
      }
      function _pickQtyShown(code, row) {
        var q = _pickQty(code);
        if (_pickMetric() || !row) return q;
        var f = _pickConv(row[12], row[13]);
        return Math.round(q * f * 100) / 100;
      }
      /* WHICH SYSTEM IS ON SCREEN -- ASKED OF THE BUDGET, NOT OF US.

         The drawer showed M2 on a page set to imperial because it asked
         STATE.isMetric, the CONFIGURATOR's own toggle, and the two are
         separate switches. The budget reads its from one place --

             #unitSelector[data-current-unit]

         -- and everything it prints follows that. The drawer sits beside the
         budget and quotes its numbers, so it has to follow the same switch or
         the two disagree on the same row (Marek: cely system je na imperial, v
         asset jsou metric). The configurator's own toggle is the fallback for
         a page with no budget on it. */
      function _pickMetric() {
        try {
          var el = global.document.getElementById('unitSelector');
          var u = el && el.getAttribute('data-current-unit');
          if (u) return String(u).toLowerCase() === 'metric';
        } catch (e) {}
        return !!STATE.isMetric;
      }
      function _pickUnit(row, cat) {
        var met = _pickMetric();
        var u = row ? String((met ? row[12] : row[13]) || '').trim() : '';
        if (!u && row) u = String((met ? row[13] : row[12]) || '').trim();   /* only one filled */
        if (!u && cat && cat.unit) u = String(cat.unit);
        return u;
      }
      /* ONE PIECE, NOT THE WHOLE SET.

         The drawer already has the fields -- position, dimensions, length --
         and fills them from whatever this registry returns. It was returning
         the CODE's record every time, so every batten in the house opened the
         same panel with an empty length. The drawer was never the problem; this
         function was (Marek: chtel bych aby kazda batten byla zvlast).

         So the id may now carry a piece: "FVF00401#12". Everything about the
         code stays as it was -- catalogue row, project quantity, the linked
         product -- and the three per-piece fields are filled from the body that
         was tapped. An id with no "#" behaves exactly as before, which is what
         the asset list sends. */
      function _pickInstance(tag) {
        if (!tag) return null;
        var _piece = null, _hash = String(tag).indexOf('#');
        if (_hash > 0) {
          try { _piece = _pickPieces[String(tag)] || null; } catch (ePc) {}
          tag = String(tag).slice(0, _hash);
        }
        var code = _pickCode(tag);
        var row = _pickRow(code), cat = CATALOG[tag] || null, k;
        if (!cat) for (k in CATALOG) if (CATALOG.hasOwnProperty(k) && CATALOG[k] && CATALOG[k].code === code) { cat = CATALOG[k]; break; }
        /* THE NAME IS THE CENTRAL BoQ's, RESOLVED HERE.

           The drawer can do this itself from the raw row -- but only through
           window.decoratedTitle, and that helper is not on this page, so it
           fell back every time to the baked English label and none of the
           central names showed. _asmName is the same lookup the assembly walk
           uses: the bridge's item, in the page's language, with its dimensions
           appended once (Marek: neobjevuji se nazvy z central BOQ). */
        /* the money is worked out on the METRIC quantity, because that is what
           prices are stored per; only what is shown is converted */
        var qtyM = _pickQty(code), money = _pickMoney(code, qtyM);
        var qty = _pickQtyShown(code, row);
        var nm = '';
        try { nm = _asmName(tag, '', (cat && cat.label) || code) || ''; } catch (e) {}
        /* what the budget has linked to this code, if anything -- the drawer
           shows it in its Product tab rather than inventing an interface */
        /* the same assignment the budget shows, from the same place -- so the
           drawer and the budget can never disagree about whether an item has a
           supplier (Marek: jediny zdroj pravdy) */
        var prod = null;
        try {
          var p = _cyanProductOf(code);
          if (p) {
            prod = {
              /* the card shows the vendor and the unit price the budget shows,
                 in the budget's own shape (Marek) */
              source: p.vendor || p.source || '',
              title: p.title || p.name || p.vendor || code,
              handle: p.handle || '',
              url: p.url || p.link || '',
              image: p.image || p.img || '',
              /* per the unit ON SCREEN: the stored figure is per metric unit,
                 so an imperial page divides it by the same factor the quantity
                 was multiplied by */
              price: (function () {
                var v = (p.price != null) ? p.price : (p.unitPrice != null ? p.unitPrice : (money ? money.unit : null));
                if (v == null || _pickMetric() || !row) return v;
                var f = _pickConv(row[12], row[13]);
                return f ? (v / f) : v;
              })(),
              currency: p.currency || '',
              unit: p.unit || _pickUnit(row, cat),
              availableForSale: p.availableForSale
            };
          }
        } catch (e2) {}
        return {
          id: _piece ? _piece.instanceId : code,
          name: nm || (cat && cat.label) || code,
          type: (row && row[2]) || '',
          boqCode: code,
          category: (row && row[1]) || '',
          /* THE UNIT COMES FROM THE CATALOGUE, IN THE SYSTEM ON SCREEN.

             The baked CATALOG label carries one unit and it is always the
             metric one, so an imperial visitor was told "/ m" for a board sold
             by the foot -- a made-up unit next to a real price. The central
             sheet has both, in their own columns (UNIT METRIC, UNIT IMPERIAL),
             and which one to show is the switch the visitor already set
             (Marek: ma vychazet z central boq, tam jsou relevantni jednotky). */
          unit: _pickUnit(row, cat),
          projectQuantity: qty,
          /* the unit price follows the unit on screen, so price x quantity is
             still the total */
          projectUnitPrice: (money && qty > 0) ? (money.total / qty) : (money ? money.unit : null),
          projectTotalPrice: money ? money.total : null,
          boqRow: row || null,
          product: prod,

          /* the piece itself: where it sits and how big it is. The drawer shows
             dimensions when it has them and a plain length otherwise, so both
             are handed over and it picks. */
          position: _piece ? _piece.position : null,
          dimensions: _piece ? { length: _piece.length, width: _piece.width,
                                 thickness: _piece.thickness } : null,
          length: _piece ? _piece.length : null,
          pieceIndex: _piece ? _piece.index : 0,
          pieceOf: _piece ? _piece.of : 0
        };
      }
      /* the pieces the drawer may be asked about, kept only between the tap
         and the drawer reading it back -- never a second copy of the model */
      var _pickPieces = {};

      function _pickInit() {
        if (!global.ConfiguratorAssetDrawer || _pickInit.done) return;
        try {
          global.ConfiguratorAssetDrawer.init({
            registry: function (id) { return _pickInstance(id); },
            onClose: function () { try { _pickUnlight(); } catch (e) {} },
            drawerScopeClass: 'house-asset-drawer'
          });
          _pickInit.done = true;
        } catch (e) { _log('asset drawer init: ' + ((e && e.message) || e)); }
      }
      /* ITS OWN RAYCAST. _hitGroup and _canvasEl sit in another closure and
         are not visible from here; they are four lines, so the module carries
         them rather than the file being rearranged around it. */
      var _pickRc = null, _pickNdc = null;
      function _pickCanvas() {
        var st = _stage();
        return (st && st.renderer && st.renderer.domElement) || null;
      }
      function _pickHits(px, py) {
        var st = _stage(), T = global.THREE, el = _pickCanvas();
        if (!st || !T || !el || !st.group) return null;
        var cam = (STATE.view === '2d' && st.orthoCam) ? st.orthoCam : st.camera;
        if (!cam) return null;
        var r = el.getBoundingClientRect();
        if (!r.width || !r.height) return null;
        if (!_pickRc) _pickRc = new T.Raycaster();
        if (!_pickNdc) _pickNdc = new T.Vector2();
        _pickNdc.x = ((px - r.left) / r.width) * 2 - 1;
        _pickNdc.y = -((py - r.top) / r.height) * 2 + 1;
        _pickRc.setFromCamera(_pickNdc, cam);
        try { return _pickRc.intersectObjects(st.group.children, true) || null; } catch (x) { return null; }
      }
      /* THE TAG IS A MARK, NOT A CODE. userData.asm holds the whole mark the
         builder set -- { at, key, def, dir, code } -- so passing it to the
         drawer as an id printed [object Object] in every field. The code is
         one property down, and a mark can legitimately have none (a key-only
         mark names a step without buying anything), in which case there is
         nothing to open (Marek). */
      function _pickCodeOf(o) {
        var m = o && o.userData && o.userData.asm;
        if (!m) return null;
        if (typeof m === 'string') return m;
        return (m.code && String(m.code)) || null;
      }
      function _pickAt(px, py) {
        var hits = _pickHits(px, py), i, o;
        if (!hits || !hits.length) return null;
        for (i = 0; i < hits.length; i++) {
          o = hits[i].object;
          while (o) {
            var cd = _pickCodeOf(o);
            if (cd) return cd;
            if (o.userData && o.userData.asm) {
              var mk = o.userData.asm;
              _log('pick: "' + (mk.key || mk.def || '?') + '" is a step mark with no code -- nothing to show');
              return null;
            }
            o = o.parent;
          }
          var nm = (hits[i].object && hits[i].object.name) || 'unnamed';
          if (!_pickMissing[nm]) { _pickMissing[nm] = 1; _log('pick: "' + nm + '" has no catalogue tag -- ignored'); }
        }
        return null;
      }
      function _pickEnable(on) {
        if (!on) { try { _pickUnlight(); } catch (eU) {} }
        var el = _pickCanvas();
        if (!el) return;
        if (on && !_pickOn) {
          _pickInit();
          _pickDown = null;
          el.addEventListener('pointerdown', _pickPointerDown, true);
          el.addEventListener('pointerup', _pickPointerUp, true);
          _pickOn = true;
          _pickEl = el;
          _log('pick: on -- tap a part to open its drawer');
        } else if (!on && _pickOn) {
          try { _pickEl.removeEventListener('pointerdown', _pickPointerDown, true); } catch (e) {}
          try { _pickEl.removeEventListener('pointerup', _pickPointerUp, true); } catch (e2) {}
          _pickOn = false; _pickEl = null; _pickDown = null;
        }
      }
      function _pickPointerDown(e) { _pickDown = { x: e.clientX, y: e.clientY, t: Date.now() }; }
      /* THE PICKED BODY IS TINTED. Without it the drawer answers about
         something and you are not certain what, especially where two layers
         meet. A light magenta over the real colour, the same signal the model
         has always used for "this is the thing under discussion", and it comes
         off when the drawer closes or another body is picked (Marek). */
      /* =====================================================================
         WHAT HAS NO SUPPLIER YET, IN CYAN.

         Unpriced means one thing and only one: the catalogue code has no shop
         product assigned to it (Marek). Not "no price in user_prices" -- that
         is empty on a product page with the budget closed, and hanging the
         colour on it would paint the whole building on load and be a lie.

         Only in the exploded view, where every part stands on its own; on the
         finished building the nearest surface would answer for everything
         behind it. It goes on with the explode and comes off with it, and the
         picked body's magenta always wins over it. */
      var CYAN_MIX = 0.45;
      var _cyanLit = [];
      /* WHERE THE BUDGET ACTUALLY KEEPS AN ASSIGNMENT.

         I asked the bridge for item.product and it is not there -- so every
         code came back unassigned and the whole building went cyan, while the
         budget plainly shows vendors and links (Marek: v budgetu jich spoustu
         nacenenych mame). The budget keeps them in window.user_prices under
         the code with a suffix:

             <code>_affiliate_product_url
             <code>_affiliate_product_title
             <code>_affiliate_product_source
             <code>_affiliate_product_image_url

         That IS the single source of truth -- it is what renderItem reads to
         draw the vendor line. The bridge is asked first in case it ever grows
         the field, then this. */
      function _cyanProductOf(tag) {
        var code = _pickCode(tag);
        try {
          var br = global.ConfiguratorBudgetBridge;
          var it = (br && br.getBoqItem) ? br.getBoqItem(code) : null;
          var p = it && (it.product || it.affiliate || it.userProduct);
          if (p && (p.title || p.handle || p.url)) return p;
        } catch (e) {}
        try {
          var up = global.user_prices || {};
          var url = up[code + '_affiliate_product_url'] || '';
          var ttl = up[code + '_affiliate_product_title'] || '';
          var src = up[code + '_affiliate_product_source'] || '';
          var img = up[code + '_affiliate_product_image_url'] || '';
          /* AND THE PRICE, which is NOT part of the assignment record: the
             budget keeps it under the bare code, one key over. Without it the
             card had a vendor and a picture and no number (Marek: neni videt
             unit price). It is stored per METRIC unit, so it is converted for
             display like every other figure here. */
          var raw = up[code];
          var pr = (raw === '' || raw == null) ? null : (parseFloat(String(raw).replace(',', '.')) || null);
          if (url || ttl) return { title: ttl || src || code, url: url, image: img, vendor: src, price: pr };
        } catch (e2) {}
        return null;
      }
      function _cyanHasProduct(tag) {
        /* WITHOUT THE BUDGET ON THE PAGE, NOTHING IS CLAIMED. user_prices is
           filled by the budget; before it loads, every code would look
           unassigned and the building would flash cyan on every visit. */
        try { if (!global.user_prices || !Object.keys(global.user_prices).length) return true; } catch (e0) { return true; }
        return !!_cyanProductOf(tag);
      }
      function _cyanOff() {
        var i;
        for (i = 0; i < _cyanLit.length; i++) {
          try {
            var r = _cyanLit[i], m = r.obj && r.obj.material;
            if (m && m.color && m.color.setHex) { m.color.setHex(r.hex); m.needsUpdate = true; }
          } catch (e) {}
        }
        _cyanLit = [];
      }
      function _cyanOn() {
        _cyanOff();
        var T = global.THREE;
        if (!T || !_asm2.steps || !_asm2.steps.length) return;
        var i, j, n = 0, codes = [], noCode = 0;
        for (i = 0; i < _asm2.steps.length; i++) {
          var st = _asm2.steps[i];
          /* A STEP WITH NO CODE IS NOT AN UNPRICED ITEM.

             The key was used when the code was missing -- 'door_lining',
             'container_int_fitout' -- and a key is not in any catalogue, so it
             came back unassigned and went cyan. It looked like a separate item
             nobody had priced, which is exactly what a door lining is NOT: it
             is part of the door and always was. Nothing to buy means nothing
             to colour (Marek: zarubne neresime, vzdy jsou soucasti dveri). */
          var tag = st.code;
          if (!tag) { noCode++; continue; }
          if (_cyanHasProduct(tag)) continue;
          codes.push(_pickCode(tag));
          for (j = 0; j < st.meshes.length; j++) {
            var o = st.meshes[j], m = o && o.material;
            if (!m || !m.color || !m.color.getHex) continue;
            _cyanLit.push({ obj: o, hex: m.color.getHex() });
            m.color.lerp(new T.Color(0x18D8E0), CYAN_MIX);
            m.needsUpdate = true;
            n++;
          }
        }
        _log('unpriced: ' + codes.length + ' code(s) with no shop product, ' + n +
             ' bod(ies) in cyan' +
             (noCode ? (' | ' + noCode + ' step(s) carry no code and are left alone') : '') +
             (codes.length ? (' -- ' + codes.join(', ')) : ''));
      }

      var _pickLit = null;
      /* THE PIECE, AND THE REST OF ITS KIND BEHIND IT.

         Tinting the tapped body tinted every body of its code, because they
         share one material -- which is right for the bill and wrong for the eye.
         The magenta is MIXED INTO the colour the body already has, not laid over
         it as transparency: a white assembly gives a nearly magenta piece and
         faintly pink kin, and a cedar one gives the same two steps in its own
         hue. I made them see-through first and that was wrong -- you could look
         straight through the wall (Marek: ma se implementovat na stavajici
         barvu, nemaji byt pruhledne).

         Sixty per cent of the way to magenta for the piece, twenty-five for the
         rest of its kind.

         Nothing is edited in place: every body that changes gets a CLONE and
         the original is put back on release, so a rebuild or a repaint is never
         looking at a tinted material. */
      function _pickLight(obj) {
        _pickUnlight();
        if (!obj || !obj.material || obj.material.length) return;
        try {
          var T = global.THREE, st = _stage();
          if (!T || !st || !st.group) return;

          var code = '';
          try {
            var mm = obj.userData && obj.userData.asm;
            var tg = mm ? (typeof mm === 'string' ? mm : (mm.code || mm.key || '')) : '';
            code = tg ? (((CATALOG[tg] && CATALOG[tg].code) ? CATALOG[tg].code : tg)) : '';
          } catch (eC) {}

          _pickLit = { touched: [] };

          /* the piece itself */
          var MAG = new T.Color(0xFF3CC8);
          var own = obj.material.clone();
          own.color.lerp(MAG, 0.60);
          if (own.emissive) { own.emissive.setHex(0xFF3CC8); own.emissiveIntensity = 0.22; }
          own.needsUpdate = true;
          _pickLit.touched.push({ o: obj, was: obj.material });
          obj.material = own;

          if (!code) return;

          /* and the rest of its kind, faded back. One clone per material, not
             per body, so a hundred studs cost one object. */
          var faded = [];
          st.group.traverse(function (q) {
            if (!q.isMesh || q === obj || !q.material || q.material.length) return;
            var m2 = q.userData && q.userData.asm;
            var t2 = m2 ? (typeof m2 === 'string' ? m2 : (m2.code || m2.key || '')) : '';
            if (!t2) return;
            var c2 = (CATALOG[t2] && CATALOG[t2].code) ? CATALOG[t2].code : t2;
            if (c2 !== code) return;
            var rec = null, r;
            for (r = 0; r < faded.length; r++) if (faded[r].was === q.material) { rec = faded[r]; break; }
            if (!rec) {
              var f = q.material.clone();
              f.color.lerp(MAG, 0.25);
              f.needsUpdate = true;
              rec = { was: q.material, mat: f };
              faded.push(rec);
            }
            _pickLit.touched.push({ o: q, was: q.material });
            q.material = rec.mat;
          });
        } catch (e) { _pickLit = null; }
      }

      function _pickUnlight() {
        if (!_pickLit) return;
        try {
          var t = _pickLit.touched || [], i;
          for (i = 0; i < t.length; i++) {
            try {
              if (t[i].o && t[i].was) {
                var cur = t[i].o.material;
                t[i].o.material = t[i].was;
                if (cur && cur.dispose && cur !== t[i].was) cur.dispose();
              }
            } catch (eR) {}
          }
        } catch (e) {}
        _pickLit = null;
      }

      function _pickPointerUp(e) {
        /* a tap, not the end of an orbit: the finger has to have stayed put */
        var d = _pickDown; _pickDown = null;
        if (!d) return;
        if (Math.abs(e.clientX - d.x) > 6 || Math.abs(e.clientY - d.y) > 6) return;
        if (Date.now() - d.t > 600) return;
        var hit = _pickHits(e.clientX, e.clientY);
        var code = _pickAt(e.clientX, e.clientY);
        if (!code) { _pickUnlight(); return; }
        try { if (hit && hit.length) _pickLight(hit[0].object); } catch (eL) {}
        try {
          _pickInit();
          /* the body that was actually tapped, with its own size and place. It
             is published before the drawer opens so the drawer can read it, and
             passed as a second argument as well -- a drawer that ignores it
             behaves exactly as before. */
          var inst = null;
          try { inst = _bodyInstance(hit && hit.length ? hit[0].object : null); } catch (eBI) {}
          try { global.__levaPickedInstance = inst; } catch (eGI) {}
          /* the drawer is opened ON THE PIECE; the registry above resolves it
             back to the code plus this body's own size and place */
          var openId = code;
          if (inst && inst.instanceId && inst.index) {
            _pickPieces = {};
            _pickPieces[inst.instanceId] = inst;
            openId = inst.instanceId;
          }
          /* WHAT HAPPENED ON THIS TAP, IN THE CONFIGURATOR'S OWN LOG.

             The drawer keeps a diagnostic of its own and it is excellent -- and
             unreachable, because you read it inside the drawer that will not
             open. Four attempts at this fault have been made on guesses for
             exactly that reason (Marek: nemuzu se dostat k logu z asset drawer
             kdyz se neotevre).

             So the same three facts are recorded here: whether the drawer
             object is even present, what its open() returned, and whether the
             panel is on the page and open a moment later. */
          var _dOK = !!global.ConfiguratorAssetDrawer;
          var _ret = '(not called)';
          if (_dOK) {
            try { _ret = String(global.ConfiguratorAssetDrawer.open(openId, inst)); }
            catch (eOD) { _ret = 'THREW ' + ((eOD && eOD.message) || eOD); }
          }
          var _wv = '?';
          try { _wv = (global.LevaBudgetWiring && global.LevaBudgetWiring.VERSION) || 'none'; } catch (eWV) {}
          _say('drawer  tap ' + openId + ' | ConfiguratorAssetDrawer ' +
               (_dOK ? 'present' : 'MISSING') + ' | open() returned ' + _ret +
               ' | wiring ' + _wv);
          try {
            global.setTimeout(function () {
              var el = null, cls = '(no element)';
              try { el = document.getElementById('Custom-Drawer'); } catch (eEl) {}
              if (el) cls = el.classList && el.classList.contains('active') ? 'OPEN' : 'on the page but CLOSED';
              _say('drawer  300 ms later: ' + cls +
                   ' | catalogue rows ' + ((global.productData && global.productData.length) || 0));
            }, 300);
          } catch (eT3) {}
          _log('pick: tag "' + code + '" -> code ' + _pickCode(code) +
               ' (' + _pickQty(_pickCode(code)) + ' in the project)');
          if (inst) _log('pick: instance ' + _instText(inst));
        } catch (e3) { _log('pick: drawer open threw ' + ((e3 && e3.message) || e3)); }
      }


    /* =========================================================================
       ONE BODY, ONE INSTANCE (core-v167).

       The pick handed the drawer a CODE and nothing else, so every stud in the
       building opened the same panel: the right catalogue row, an empty LENGTH
       and an empty POSITION. What somebody wants when they tap a particular
       piece is that piece -- how long it is, what section, where it sits
       (Marek: kazde teleso klikatelne zvlast a u instance i delka daneho kusu).

       The model already knows. A body is a box: its own geometry gives the
       three dimensions, and sorting them tells you which is the length and
       which is the section. So the record is read off the body at the moment it
       is tapped -- nothing to store, nothing to keep in step with a rebuild. */
    function _bodyInstance(o) {
      if (!o || !o.isMesh) return null;
      var m = o.userData && o.userData.asm;
      var tag = m ? (typeof m === 'string' ? m : (m.code || m.key || '')) : '';
      if (!tag) return null;
      var code = (CATALOG[tag] && CATALOG[tag].code) ? CATALOG[tag].code : tag;

      /* the three sides of this body, in metres, largest first */
      var d = [0, 0, 0];
      try {
        var g = o.geometry;
        if (g) {
          if (!g.boundingBox) g.computeBoundingBox();
          var bb = g.boundingBox;
          var s = o.scale || { x: 1, y: 1, z: 1 };
          d = [Math.abs(bb.max.x - bb.min.x) * (s.x || 1),
               Math.abs(bb.max.y - bb.min.y) * (s.y || 1),
               Math.abs(bb.max.z - bb.min.z) * (s.z || 1)];
        }
      } catch (eG) {}
      var sorted = d.slice().sort(function (a, b) { return b - a; });

      /* where it sits, in the building's own coordinates */
      var p = { x: 0, y: 0, z: 0 };
      try {
        var w = new global.THREE.Vector3();
        o.getWorldPosition(w);
        p = { x: w.x, y: w.y, z: w.z };
      } catch (eP) { try { p = { x: o.position.x, y: o.position.y, z: o.position.z }; } catch (eP2) {} }

      /* which one of its kind this is -- counted now, so a rebuild cannot make
         the numbering lie */
      var idx = 0, total = 0, seen = false;
      try {
        var st = _stage();
        if (st && st.group) {
          st.group.traverse(function (q) {
            if (!q.isMesh) return;
            var mq = q.userData && q.userData.asm;
            var tq = mq ? (typeof mq === 'string' ? mq : (mq.code || mq.key || '')) : '';
            if (!tq) return;
            var cq = (CATALOG[tq] && CATALOG[tq].code) ? CATALOG[tq].code : tq;
            if (cq !== code) return;
            total++;
            if (q === o) { idx = total; seen = true; }
          });
        }
      } catch (eI) {}

      var cat = CATALOG[tag] || CATALOG[code] || {};
      return {
        code: code,
        tag: tag,
        layer: _layerOf(o) || '',
        name: (m && m.def) || cat.label || code,
        index: seen ? idx : 0,
        of: total,
        instanceId: code + (seen ? ('#' + idx) : ''),
        length: sorted[0] || 0,
        width: sorted[1] || 0,
        thickness: sorted[2] || 0,
        size: [d[0], d[1], d[2]],
        position: p,
        unit: cat.unit || ''
      };
    }

    function _instText(it) {
      if (!it) return '';
      var mm = function (v) { return Math.round(v * 1000); };
      return it.instanceId + ' of ' + it.of + ' | ' + mm(it.length) + ' x ' + mm(it.width) +
             ' x ' + mm(it.thickness) + ' mm | at ' +
             (Math.round(it.position.x * 100) / 100) + ', ' +
             (Math.round(it.position.y * 100) / 100) + ', ' +
             (Math.round(it.position.z * 100) / 100) + ' | layer ' + it.layer;
    }

    function _asmLeave() {
      try { _pickEnable(false); } catch (ePk2) {}
      try { _cyanOff(); } catch (eCy2) {}
      if (!_asm.on) return;
      _asm.on = false;
      var st = _stage();
      try { if (_asm.panel && _asm.panel.parentNode) _asm.panel.parentNode.removeChild(_asm.panel); } catch (e) {}
      try { if (st && _asm.grp) st.scene.remove(_asm.grp); } catch (e2) {}
      _asm.panel = null; _asm.grp = null; _asm.lines = null; _asm.rows = [];
      try { if (_cutTool && _cutTool.on()) { _cutTool.set({ plane: true }); _cutTool.toggle(); } } catch (e3) {}
      _log('assembly: section closed');
    }

    /* --------------------------------------------------------------------------
       EXPORT. Core keys first, product keys on top. The name is HouseLib for
       every product in the line.
       -------------------------------------------------------------------------- */
    var API = coreExport();

    /* AND THE SECOND WAVE: what the first one turned out to need. The kit's
       icon bag, the language, the colour schemes, the outline's repaint timer,
       the bill accumulator, the footing helper and the assembly's layout are
       all the same in every product -- they came out with their callers. KIT()
       stays below, because what it hands the kit IS the product: its groups,
       its openings, its wizard. */

    /* WHICH KEYS THE THEME ACTUALLY HAS.

       Five rounds have gone into reading screenshots to work out why one label is
       Czech and the next is English. The answer is always the same shape -- the
       key is missing somewhere along a four-link chain -- and it is invisible
       from the outside because a fallback looks exactly like a translation that
       happens to be in English.

       Every lookup is recorded: asked for, and whether it landed. The snapshot
       then names the ones that did not, which turns "some are translated" into a
       list somebody can act on. */
    var _i18nAsked = {}, _i18nMissed = {}, _i18nSameAsEn = {};

    /* THE THEME ANSWERING IS NOT THE SAME AS THE PAGE BEING TRANSLATED, and the
       Czech page shows it: Mono-pitch, Ground screws and Concrete slab standing
       among Sedlova, Patkove and Pasove. Every one of those keys EXISTS and every
       one was answered, so the missing list has been empty the whole time and the
       probe has been reporting a clean sheet over an English tile.

       A key the Czech file has not got falls back to the English one, which comes
       back word for word as the fallback written here. On a page that is not in
       English that is the tell, and it is worth as much as the missing list:
       these are keys to TRANSLATE, not keys to add. */
    function _pageLang() {
      try {
        var d = global.document, h = d && d.documentElement;
        return String((h && h.getAttribute('lang')) || 'en').slice(0, 2).toLowerCase();
      } catch (e) { return 'en'; }
    }

    /* --------------------------------------------------------------------------
       COLOUR SCHEMES. The cycler is the shared one; what it paints is ours.

       The rail has carried cf-btn-colors since the first version and the snapshot
       has been reporting it as NOT WIRED ever since -- a button that lights up
       and changes nothing. It steps the shared scheme list now.

       WHAT IT CAN COLOUR TODAY IS THE DECK, and I would rather say that than
       wire a control to a surface that does not exist yet. This product has no
       cladding layer: the EXTERIOR step's two tiles are stubs, the roof is
       reported as "not covered yet", and there is no terrace. The scheme's
       facade, roof and roofPat fields are read and applied the moment those
       layers arrive; until then the deck is the one surface a scheme touches,
       and the log says so rather than letting a silent control look broken. */
    var _schemes = null;

    var ICO = {};

    /* THE CHEAP REPAINT, AND ONE PER FRAME.

       drawPlan re-inits the host contract, ensures and shows the overlay, paints
       it and then rebuilds the scissor icons through the DOM. Running that on
       every pointermove is what made the picture crawl -- tiny does not do it,
       it repaints the overlay and nothing else.

       The asset already has that call: overlay.repaint() paints with the engine
       it last painted with. And it is coalesced onto an animation frame, so a
       finger that fires forty moves in a frame still paints once. */
    var _traceRaf = 0;

    /* AND THE GLYPH IS SWAPPED, NOT THE BUTTON.

       The panel builds a tile as a glyph span PLUS a caption span. Writing the
       button's whole innerHTML -- which is what the rail-based version did, and
       what tiny can get away with because its rail buttons are glyph only --
       would have thrown the caption away. Only the glyph span is touched. */
    function _traceIconPaint() {
      try {
        var TR = _trace(), doc = global.document;
        if (!doc) return;
        var has = !!(TR && TR.has());
        var armed = has && !!TR.armed;
        var want = has ? ICO_FREEZE_G : (ICO.upload || '');
        var bg = armed ? '#1A1C1D' : '';
        var fg = armed ? '#ffffff' : '';
        var bs = doc.querySelectorAll('button[title="' + _traceTitle() + '"]'), i, gl;
        for (i = 0; i < bs.length; i++) {
          gl = bs[i].querySelector('.cfg-ui-wiz-ico-g');
          if (gl) { if (gl.innerHTML !== want) gl.innerHTML = want; }
          else if (bs[i].innerHTML !== want) { bs[i].innerHTML = want; }
          if (bs[i].style.background !== bg) bs[i].style.background = bg;
          if (bs[i].style.color !== fg) bs[i].style.color = fg;
        }
      } catch (e) {}
    }

    var _bill = {};

    /* the footings are drawn whole on the Roof & Base step and in the assembly
       walk (Marek: show the base elements entire, below grade too) */
    /* THE FOUNDATION SHOWS WHEN A PRODUCT ASKS FOR IT (core-v429, Marek: v
       default mode neni videt zaklad domu, jen kari sit). Below grade used to
       be drawn on the base step only; a product that sets __PROD._footAlways
       gets its slab and strips on every step, so a house whose slab lies just
       under grade does not stand on nothing. */
    function _footFull() {
      var always = false;
      try { always = !!(__PROD && __PROD._footAlways); } catch (e) { always = false; }
      var rbS = false;
      try { rbS = (viz().key === 'rebar'); } catch (eRs) { rbS = false; }
      return always || rbS || API.editing('roof') || (API.asmWalking && API.asmWalking());
    }

    function _asmLayout() {
      var st = _stage(), T = global.THREE; if (!st || !T || !_asm.on || !_asm.lines) return;
      var cam = st.camera, canvas = st.renderer && st.renderer.domElement; if (!cam || !canvas) return;
      var cr = canvas.getBoundingClientRect(); if (!cr.width || !cr.height) return;
      var B = _buildRect(), xCut = (B.x0 + B.x1) / 2, zOut = B.z1 + facadeThk(), y = API.storeys()[0].y0 + 1.40;
      var ctl = _navControls(), camDist = (ctl && ctl.target) ? cam.position.distanceTo(ctl.target) : 10;
      var pos = _asm.lines.geometry.attributes.position, i;
      for (i = 0; i < _asm.rows.length; i++) {
        var R = _asm.rows[i], rr = R.row.getBoundingClientRect();
        var px = rr.left - 8, py = rr.top + rr.height / 2;
        var ndc = new T.Vector3(((px - cr.left) / cr.width) * 2 - 1, -((py - cr.top) / cr.height) * 2 + 1, 0.5);
        ndc.unproject(cam);
        var dir = ndc.sub(cam.position).normalize();
        var end = cam.position.clone().add(dir.multiplyScalar(camDist));
        /* the anchor: on the cut face, at the layer's depth in the z1 wall */
        pos.setXYZ(i * 2, xCut + 0.01, y, zOut - R.lay.at);
        pos.setXYZ(i * 2 + 1, end.x, end.y, end.z);
      }
      pos.needsUpdate = true;
      _asm.lines.geometry.computeBoundingSphere();
    }

    /* AND THE LAST TWO THE MOVE ITSELF PULLED IN: the outline's title and the
       icon freeze flag. Both are read by code that has just gone up, and both
       are the same in every product. */

    /* the snowflake, as a bare glyph body so it drops into the same wrapper the
       shared icon set uses */
    var ICO_FREEZE_G =
      '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
      '<path d="M12 3v18M4.2 7.5l15.6 9M4.2 16.5l15.6-9"/>' +
      '<path d="M9 5l3 3 3-3M9 19l3-3 3 3"/>' +
      '<path d="M6.4 9.4l.4-3.4 3.4.4M17.6 14.6l-.4 3.4-3.4-.4"/>' +
      '<path d="M17.6 9.4l-.4-3.4-3.4.4M6.4 14.6l.4 3.4 3.4-.4"/></svg>';

    /* ONE PLACE THE BUTTON IS NAMED, and it is why the icon never changed.

       The tile is built with t('plan_underlay','Trace') and the paint went
       looking for button[title=KS('plan_underlay')]. Those are two different
       translators with two different fallbacks, so the selector matched nothing
       and the paint ran happily over an empty list -- no error, no icon.

       Tiny has exactly this helper for exactly this reason. */
    function _traceTitle() { return t('plan_underlay', 'Trace'); }

    /* ===== END CORE BLOCK core-v72 ============================================
       Below this line is the PRODUCT. Nothing here is copied into another one.
       ========================================================================== */

    /* ---------------------------------------------------------------------
       WHAT THE PRODUCT GETS. Everything it reads from here, by name.

       The plain ones are handed over once: functions, and objects that are
       only ever filled in place. The eight at the bottom are different --
       the core REASSIGNS them while it runs, so a copy taken now would go
       stale. Those are properties with a getter and a setter, so the
       product reads and writes the core's own variable and not a snapshot
       of it.
       --------------------------------------------------------------------- */
    var __CORE = {
      $: $,
      API: API,
      BOOTDATA: BOOTDATA,
      CLADDINGS: CLADDINGS,
      CLAD_T: CLAD_T,
      CLEAR_LIM: CLEAR_LIM,
      CORE_VERSION: CORE_VERSION,
      FOUNDATIONS: FOUNDATIONS,
      FT: FT,
      GRID: GRID,
      ICO: ICO,
      ICO_FREEZE_G: ICO_FREEZE_G,
      IN: IN,
      LIMITS: LIMITS,
      OVER_LIM: OVER_LIM,
      SHEET: SHEET,
      STATE: STATE,
      STORE_KEY: STORE_KEY,
      _FL: _FL,
      _P: _P,
      _UI: _UI,
      _asm2: _asm2,
      _asmCode: _asmCode,
      _asmCollect: _asmCollect,
      _asmLeave: _asmLeave,
      _asmMark: _asmMark,
      _asmStepLabel: _asmStepLabel,
      _bill: _bill,
      _box: _box,
      _buildRect: _buildRect,
      _cap: _cap,
      _cladThk: _cladThk,
      /* core-v624: a product's own slider builds only its layers while it moves */
      _dragMove: _dragMove,
      _sxPicked: _sxPicked,
      _footFull: _footFull,
      _heightLabel: _heightLabel,
      _initHeight: _initHeight,
      _initPitch: _initPitch,
      _isTrussed: _isTrussed,
      _log: _log,
      _navControls: _navControls,
      _pan2D: _pan2D,
      _partFramed: _partFramed,
      _paverSize: _paverSize,
      _pitchLabel: _pitchLabel,
      _planRemember: _planRemember,
      _planTaps: _planTaps,
      _planWanted: _planWanted,
      _roofOver: _roofOver,
      _rows: _rows,
      _shareWorker: _shareWorker,
      _stage: _stage,
      _store: _store,
      _t: _t,
      _tCap: _tCap,
      _terrace: _terrace,
      _terraceRect: _terraceRect,
      _terraceSheltered: _terraceSheltered,
      _terraceSnapToTiles: _terraceSnapToTiles,
      _trace: _trace,
      _traceIcon: _traceIcon,
      _traceRepaint: _traceRepaint,
      _traceTitle: _traceTitle,
      _trees: _trees,
      _why: _why,
      apexY: apexY,
      boq: boq,
      clamp: clamp,
      drawPlan: drawPlan,
      editing: editing,
      fitEdit: fitEdit,
      fmtLen: fmtLen,
      hipAt: hipAt,
      hipEnds: hipEnds,
      inPoly: inPoly,
      opEdit: opEdit,
      poly: poly,
      polyOf: polyOf,   /* core-v785 */
      polyArea: polyArea,
      roofPlanes: roofPlanes,
      savePref: savePref,
      scheme: scheme,
      segments: segments,
      sheet: sheet,
      showExplode: showExplode,
      storeys: storeys,
      t: t,
      toggleTrees: toggleTrees,
      placeStepDef: placeStepDef,
      placeGroups: placeGroups,
      colorSlots: colorSlots,
      colorSlotKey: colorSlotKey,
      colorSlotOf: colorSlotOf,
      colorSlotNext: colorSlotNext,
      colorSlotSet: colorSlotSet,
      colorHex: colorHex,
      colorsStepDef: colorsStepDef,
      colorsPanel: colorsPanel,
      openingsStepDef: openingsStepDef,
      openingsGroups: openingsGroups,
      cornerPlace: function (segId, at) { var sgP = segment(segId); return sgP ? _cwPlace(sgP, (at == null) ? 0 : +at) : null; },
      cornerCycle: function (rec) { var Cc = _opCycleOf(rec); if (!Cc) return null; var kc = Cc.next(); savePref(); rebuild(); return kc; },
      layoutStepDef: layoutStepDef,
      layoutGroups: layoutGroups,
      undoPush: undoPush,
      undoPop: undoPop,
      undoState: undoState,
      tour: { state: function () { return tourState(); }, at: function (i) { if (!_T.on) _T.resumeAt = Math.max(0, i | 0); return tourState(); } },
      railBind: railBind,
      railCounters: railCounters,
      stepDef: stepDef,
      stepPanel: stepPanel,
      wizardMount: wizardMount,
      wizardSteps: wizardSteps,
      kitDefault: kitDefault,
      videoGroups: videoGroups,
      ROOF_COVERINGS: ROOF_COVERINGS,
      covKey: covKey,
      covAllowed: covAllowed,
      covFallback: covFallback,
      covDef: covDef,
      roofGroups: roofGroups,
      exteriorGroups: exteriorGroups,
      roofStageLabel: roofStageLabel,
      roofStageCount: roofStageCount,
      billAdd: billAdd,
      buildStripSlab: buildStripSlab,
      roofSkin: roofSkin,
      gutters: gutters,
      eavePosts: eavePosts,
      foundation: foundation,
      trusses: trusses,
      studWall: studWall,
      wallKit: wallKit,
      houseFloor: houseFloor,
      houseCeiling: houseCeiling,
      houseParts: houseParts,
      houseJoinery: houseJoinery,
      houseDoorSet: houseDoorSet,
      houseRoof: houseRoof,
      houseRoofSkin: houseRoofSkin,
      houseFlatRoof: houseFlatRoof,
      houseFitout: houseFitout,
      houseWebPlane: houseWebPlane,
      houseFootOnChord: houseFootOnChord,
      houseSeatOnHip: houseSeatOnHip,
      houseRafterSteps: houseRafterSteps,
      houseRoofPlaneYAt: houseRoofPlaneYAt,
      houseRoofYAtXZ: houseRoofYAtXZ,
      houseUnderRoof: houseUnderRoof,
      houseMembTex: houseMembTex,
      houseSpaceBuild: houseSpaceBuild,
      houseDiagWalls: houseDiagWalls,
      houseFinTex: houseFinTex,
      houseStudPositions: houseStudPositions,
      houseStudPositionsWorld: houseStudPositionsWorld,
      houseGableFaces: houseGableFaces,
      houseTrussStations: houseTrussStations,
      houseTerraceDeck: houseTerraceDeck,
      houseLps: houseLps,
      houseOpenFitouts: houseOpenFitouts,
      houseSnapshot: houseSnapshot,
      houseSkirtCuts: houseSkirtCuts,
      roofOpenings: roofOpenings,
      screwPile: screwPile,
      deck: deck,
      stairs: stairs,
      stairPlan: stairPlan,
      stairKit: stairKit,
      stairAxes: stairAxes,
      pointBase: pointBase,
      roofEdges: roofEdges,
      viz: viz,
      wallThickness: wallThickness,
      wallTop: wallTop,
      __PROD: __PROD
    };
    (function () {
      function live(k, get, set) { Object.defineProperty(__CORE, k, { get: get, set: set }); }
      live('ASM_GLOBAL_ORDER', function () { return ASM_GLOBAL_ORDER; }, function (v) { ASM_GLOBAL_ORDER = v; });
      live('ASM_LABELS', function () { return ASM_LABELS; }, function (v) { ASM_LABELS = v; });
      live('ASM_ORDER', function () { return ASM_ORDER; }, function (v) { ASM_ORDER = v; });
      live('ASM_STEP_ORDER', function () { return ASM_STEP_ORDER; }, function (v) { ASM_STEP_ORDER = v; });
      live('CATALOG', function () { return CATALOG; }, function (v) { CATALOG = v; });
      live('DEFAULT_PROJECT', function () { return DEFAULT_PROJECT; }, function (v) { DEFAULT_PROJECT = v; });
      live('FOCUS', function () { return FOCUS; }, function (v) { FOCUS = v; });
      live('LOG_TAG', function () { return LOG_TAG; }, function (v) { LOG_TAG = v; });
      live('PRODUCT', function () { return PRODUCT; }, function (v) { PRODUCT = v; });
      live('ROOM_KEYS', function () { return ROOM_KEYS; }, function (v) { ROOM_KEYS = v; });
      live('ROOM_ROWS', function () { return ROOM_ROWS; }, function (v) { ROOM_ROWS = v; });
      live('OPENING_FIXED', function () { return OPENING_FIXED; }, function (v) { OPENING_FIXED = v; });
      live('PLAN_EXTRA', function () { return PLAN_EXTRA; }, function (v) { PLAN_EXTRA = v; });
      live('FIT_PLUS_SKIP', function () { return FIT_PLUS_SKIP; }, function (v) { FIT_PLUS_SKIP = v; });
      live('PDF_BUILDUPS', function () { return PDF_BUILDUPS; }, function (v) { PDF_BUILDUPS = v; });
      live('_MM', function () { return _MM; }, function (v) { _MM = v; });
      live('_cutTool', function () { return _cutTool; }, function (v) { _cutTool = v; });
      live('_fitAt', function () { return _fitAt; }, function (v) { _fitAt = v; });
      live('_fitDiag', function () { return _fitDiag; }, function (v) { _fitDiag = v; });
      live('_fitEdit', function () { return _fitEdit; }, function (v) { _fitEdit = v; });
      live('_fitLast', function () { return _fitLast; }, function (v) { _fitLast = v; });
      live('_fitRoom', function () { return _fitRoom; }, function (v) { _fitRoom = v; });
      live('_heightSl', function () { return _heightSl; }, function (v) { _heightSl = v; });
      live('_notBilled', function () { return _notBilled; }, function (v) { _notBilled = v; });
      live('_opSel', function () { return _opSel; }, function (v) { _opSel = v; });
      live('_pitchSl', function () { return _pitchSl; }, function (v) { _pitchSl = v; });
      live('_shareId', function () { return _shareId; }, function (v) { _shareId = v; });
      live('_storeSeen', function () { return _storeSeen; }, function (v) { _storeSeen = v; });
    })();

    /* ---------------------------------------------------------------------
       AND IF THE PRODUCT THROWS, SAY SO -- DO NOT VANISH.

       build() runs the whole product at load. An exception anywhere in it
       used to take the assignment with it, so global.HouseLib was never
       set and the page came up blank with nothing in the log to copy:
       the one state in which there is no way to ask the page what went
       wrong. Now the error is caught, written where the LOG button can
       reach it, and the core is handed back anyway -- degraded, but
       answerable.
       --------------------------------------------------------------------- */
    try {
      build(__CORE);
    } catch (eB) {
      var msg = 'BUILD THREW -- ' + ((eB && eB.message) || eB);
      var where = (eB && eB.stack) ? String(eB.stack).split('\n').slice(0, 4).join(' | ') : '';
      try { _log(msg); if (where) _log('   at ' + where); } catch (e1) {}
      try {
        global.__levaLogs = global.__levaLogs || [];
        global.__levaLogs.push(msg); if (where) global.__levaLogs.push('   at ' + where);
      } catch (e2) {}
      try { global.__coreBuildError = eB; } catch (e3) {}
      try { if (global.console && global.console.error) global.console.error('[core] ' + msg, eB); } catch (e4) {}
      /* AND ON THE SCREEN. A console message is no use when the page is
         reached from a phone, and a log is no use when the thing that holds
         the log is what failed. This writes the error where it cannot be
         missed and can simply be read out. */
      try {
        var d = global.document.createElement('div');
        d.setAttribute('style', 'position:fixed;left:0;right:0;top:0;z-index:2147483647;' +
          'background:#B3261E;color:#fff;font:12px/1.45 ui-monospace,Menlo,monospace;' +
          'padding:10px 12px;white-space:pre-wrap;word-break:break-word;max-height:60vh;overflow:auto');
        d.textContent = msg + (where ? ('\n' + where) : '');
        var host = global.document.body || global.document.documentElement;
        if (host) host.appendChild(d);
      } catch (e5) {}
    }
    return API;
  }

  global.ConfiguratorCore = { create: createCore, controls: createControls, VERSION: 'core-v81-controls' };

  try {
    if (global.Configurator && global.Configurator.log) {
      global.Configurator.log.log('[core] ConfiguratorCore core-v80 ready');
    }
  } catch (e) {}

})(window);
